import me$3, { isTree } from "math-expressions";
class ParameterStack {
  constructor(initialParameters) {
    this.stack = [{}];
    if (initialParameters !== void 0) {
      Object.assign(this.parameters, initialParameters);
    }
  }
  get parameters() {
    return this.stack[this.stack.length - 1];
  }
  push(additionalParameters, mergePrevious = true) {
    let newParameters = {};
    if (mergePrevious) {
      Object.assign(newParameters, this.parameters);
      if (additionalParameters !== void 0) {
        Object.assign(newParameters, additionalParameters);
      }
    } else {
      newParameters = additionalParameters ?? {};
    }
    this.stack.push(newParameters);
  }
  pop() {
    let lastParams = this.stack.pop();
    if (this.stack.length === 0) {
      this.stack = [{}];
    }
    return lastParams;
  }
}
class Numerics {
  constructor({
    maxIterationsRoot = 80,
    maxIterationsMinimize = 500,
    eps = 1e-6
  } = {}) {
    this.maxIterationsRoot = maxIterationsRoot;
    this.maxIterationsMinimize = maxIterationsMinimize;
    this.eps = eps;
  }
  /**
   *
   * Find zero of an univariate function f.
   * @param {function} f Function, whose root is to be found
   * @param {Array,Number} x0  Start value or start interval enclosing the root
   * @param {Object} object Parent object in case f is method of it
   * @returns {Number} the approximation of the root
   * Algorithm:
   *  G.Forsythe, M.Malcolm, C.Moler, Computer methods for mathematical
   *  computations. M., Mir, 1980, p.180 of the Russian edition
   *
   * If x0 is an array containing lower and upper bound for the zero
   * algorithm 748 is applied. Otherwise, if x0 is a number,
   * the algorithm tries to bracket a zero of f starting from x0.
   * If this fails, we fall back to Newton's method.
   */
  fzero(f2, x0, object) {
    var a2, b2, c2, fa2, fb, fc, aa2, blist, i2, len, u2, fu, prev_step, t1, cb, t2, tol_act, p2, q2, new_step, eps = this.eps, maxiter = this.maxIterationsRoot, niter = 0;
    if (Array.isArray(x0)) {
      if (x0.length < 2) {
        throw new Error(
          "fzero: length of array x0 has to be at least two."
        );
      }
      a2 = x0[0];
      fa2 = f2.call(object, a2);
      b2 = x0[1];
      fb = f2.call(object, b2);
    } else {
      a2 = x0;
      fa2 = f2.call(object, a2);
      if (a2 === 0) {
        aa2 = 1;
      } else {
        aa2 = a2;
      }
      blist = [
        0.9 * aa2,
        1.1 * aa2,
        aa2 - 1,
        aa2 + 1,
        0.5 * aa2,
        1.5 * aa2,
        -aa2,
        2 * aa2,
        -10 * aa2,
        10 * aa2
      ];
      len = blist.length;
      for (i2 = 0; i2 < len; i2++) {
        b2 = blist[i2];
        fb = f2.call(object, b2);
        if (fa2 * fb <= 0) {
          break;
        }
      }
      if (b2 < a2) {
        u2 = a2;
        a2 = b2;
        b2 = u2;
        fu = fa2;
        fa2 = fb;
        fb = fu;
      }
    }
    if (fa2 * fb > 0) {
      if (Array.isArray(x0)) {
        return this.fminbr(f2, [a2, b2], object).x;
      }
      return this.Newton(f2, a2, object);
    }
    c2 = a2;
    fc = fa2;
    while (niter < maxiter) {
      prev_step = b2 - a2;
      if (Math.abs(fc) < Math.abs(fb)) {
        a2 = b2;
        b2 = c2;
        c2 = a2;
        fa2 = fb;
        fb = fc;
        fc = fa2;
      }
      tol_act = 0.5 * eps * (Math.abs(b2) + 1);
      new_step = (c2 - b2) * 0.5;
      if (Math.abs(new_step) <= tol_act && Math.abs(fb) <= eps) {
        return b2;
      }
      if (Math.abs(prev_step) >= tol_act && Math.abs(fa2) > Math.abs(fb)) {
        cb = c2 - b2;
        if (a2 === c2) {
          t1 = fb / fa2;
          p2 = cb * t1;
          q2 = 1 - t1;
        } else {
          q2 = fa2 / fc;
          t1 = fb / fc;
          t2 = fb / fa2;
          p2 = t2 * (cb * q2 * (q2 - t1) - (b2 - a2) * (t1 - 1));
          q2 = (q2 - 1) * (t1 - 1) * (t2 - 1);
        }
        if (p2 > 0) {
          q2 = -q2;
        } else {
          p2 = -p2;
        }
        if (p2 < 0.75 * cb * q2 - Math.abs(tol_act * q2) * 0.5 && p2 < Math.abs(prev_step * q2 * 0.5)) {
          new_step = p2 / q2;
        }
      }
      if (Math.abs(new_step) < tol_act) {
        if (new_step > 0) {
          new_step = tol_act;
        } else {
          new_step = -tol_act;
        }
      }
      a2 = b2;
      fa2 = fb;
      b2 += new_step;
      fb = f2.call(object, b2);
      if (fb > 0 && fc > 0 || fb < 0 && fc < 0) {
        c2 = a2;
        fc = fa2;
      }
      niter++;
    }
    return b2;
  }
  /**
   *
   * Find minimum of an univariate function f.
   * <p>
   * Algorithm:
   *  G.Forsythe, M.Malcolm, C.Moler, Computer methods for mathematical
   *  computations. M., Mir, 1980, p.180 of the Russian edition
   *
   * @param {function} f Function, whose minimum is to be found
   * @param {Array} x0  Start interval enclosing the minimum
   * @param {Object} context Parent object in case f is method of it
   *
   * Return object with attributes:
   * - success: true if reached minimum before max number of iterations
   * - x: the approximation of the minimum value position
   * - fx: the value of f at x
   * - tol: the tolerance used in computing the minimum
   **/
  fminbr(f2, x0, context, eps_override) {
    let eps = eps_override !== void 0 ? eps_override : this.eps;
    var a2, b2, x2, v2, w2, fx, fv, fw, range, middle_range, tol_act, new_step, p2, q2, t2, ft2, r2 = (3 - Math.sqrt(5)) * 0.5, tol = eps, sqrteps = eps, maxiter = this.maxIterationsMinimize, niter = 0;
    if (!Array.isArray(x0) || x0.length < 2) {
      throw new Error(
        "Numerics.fminbr: length of array x0 has to be at least two."
      );
    }
    a2 = x0[0];
    b2 = x0[1];
    v2 = a2 + r2 * (b2 - a2);
    fv = f2.call(context, v2);
    if (Number.isNaN(fv)) {
      return { success: false };
    }
    x2 = v2;
    w2 = v2;
    fx = fv;
    fw = fv;
    while (niter < maxiter) {
      range = b2 - a2;
      middle_range = (a2 + b2) * 0.5;
      tol_act = sqrteps * Math.abs(x2) + tol / 3;
      if (Math.abs(x2 - middle_range) + range * 0.5 <= 2 * tol_act) {
        return { success: true, x: x2, fx, tol: tol_act };
      }
      new_step = r2 * (x2 < middle_range ? b2 - x2 : a2 - x2);
      if (Math.abs(x2 - w2) >= tol_act) {
        t2 = (x2 - w2) * (fx - fv);
        q2 = (x2 - v2) * (fx - fw);
        p2 = (x2 - v2) * q2 - (x2 - w2) * t2;
        q2 = 2 * (q2 - t2);
        if (q2 > 0) {
          p2 = -p2;
        } else {
          q2 = -q2;
        }
        if (Math.abs(p2) < Math.abs(new_step * q2) && // If x+p/q falls in [a,b]
        p2 > q2 * (a2 - x2 + 2 * tol_act) && //  not too close to a and
        p2 < q2 * (b2 - x2 - 2 * tol_act)) {
          new_step = p2 / q2;
        }
      }
      if (Math.abs(new_step) < tol_act) {
        if (new_step > 0) {
          new_step = tol_act;
        } else {
          new_step = -tol_act;
        }
      }
      t2 = x2 + new_step;
      ft2 = f2.call(context, t2);
      if (Number.isNaN(ft2)) {
        return { success: false };
      }
      if (ft2 <= fx) {
        if (t2 < x2) {
          b2 = x2;
        } else {
          a2 = x2;
        }
        v2 = w2;
        w2 = x2;
        x2 = t2;
        fv = fw;
        fw = fx;
        fx = ft2;
      } else {
        if (t2 < x2) {
          a2 = t2;
        } else {
          b2 = t2;
        }
        if (ft2 <= fw || w2 === x2) {
          v2 = w2;
          w2 = t2;
          fv = fw;
          fw = ft2;
        } else if (ft2 <= fv || v2 === x2 || v2 === w2) {
          v2 = t2;
          fv = ft2;
        }
      }
      niter += 1;
    }
    return { success: false, x: x2, fx };
  }
  /**
   * Newton's method to find roots of a funtion in one variable.
   * @param {function} f We search for a solution of f(x)=0.
   * @param {Number} x initial guess for the root, i.e. start value.
   * @param {Object} context optional object that is treated as "this" in the function body. This is useful if
   * the function is a method of an object and contains a reference to its parent object via "this".
   * @returns {Number} A root of the function f.
   */
  Newton(f2, x2, context) {
    var df, i2 = 0, h2 = this.eps, newf = f2.apply(context, [x2]);
    if (Array.isArray(x2)) {
      x2 = x2[0];
    }
    while (i2 < 50 && Math.abs(newf) > h2) {
      df = this.D(f2, context)(x2);
      if (Math.abs(df) > h2) {
        x2 -= newf / df;
      } else {
        x2 += Math.random() * 0.2 - 1;
      }
      newf = f2.apply(context, [x2]);
      i2 += 1;
    }
    return x2;
  }
  /**
   * Numerical (symmetric) approximation of derivative.
   * @param {function} f Function in one variable to be differentiated.
   * @param {object} [obj] Optional object that is treated as "this" in the function body. This is useful, if the function is a
   * method of an object and contains a reference to its parent object via "this".
   * @returns {function} Derivative function of a given function f.
   */
  D(f2, obj) {
    if (!(obj === void 0 || obj === null)) {
      return function(x2) {
        var h2 = 1e-5, h22 = h2 * 2;
        return (f2(x2 + h2) - f2(x2 - h2)) / h22;
      };
    }
    return function(x2) {
      var h2 = 1e-5, h22 = h2 * 2;
      return (f2.apply(obj, [x2 + h2]) - f2.apply(obj, [x2 - h2])) / h22;
    };
  }
}
var commonjsGlobal$1 = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
function getDefaultExportFromCjs$1(x2) {
  return x2 && x2.__esModule && Object.prototype.hasOwnProperty.call(x2, "default") ? x2["default"] : x2;
}
function getAugmentedNamespace(n2) {
  if (Object.prototype.hasOwnProperty.call(n2, "__esModule")) return n2;
  var f2 = n2.default;
  if (typeof f2 == "function") {
    var a2 = function a3() {
      var isInstance = false;
      try {
        isInstance = this instanceof a3;
      } catch {
      }
      if (isInstance) {
        return Reflect.construct(f2, arguments, this.constructor);
      }
      return f2.apply(this, arguments);
    };
    a2.prototype = f2.prototype;
  } else a2 = {};
  Object.defineProperty(a2, "__esModule", { value: true });
  Object.keys(n2).forEach(function(k2) {
    var d2 = Object.getOwnPropertyDescriptor(n2, k2);
    Object.defineProperty(a2, k2, d2.get ? d2 : {
      enumerable: true,
      get: function() {
        return n2[k2];
      }
    });
  });
  return a2;
}
var alea$1 = { exports: {} };
var alea = alea$1.exports;
var hasRequiredAlea;
function requireAlea() {
  if (hasRequiredAlea) return alea$1.exports;
  hasRequiredAlea = 1;
  (function(module) {
    (function(global2, module2, define) {
      function Alea(seed) {
        var me2 = this, mash = Mash();
        me2.next = function() {
          var t2 = 2091639 * me2.s0 + me2.c * 23283064365386963e-26;
          me2.s0 = me2.s1;
          me2.s1 = me2.s2;
          return me2.s2 = t2 - (me2.c = t2 | 0);
        };
        me2.c = 1;
        me2.s0 = mash(" ");
        me2.s1 = mash(" ");
        me2.s2 = mash(" ");
        me2.s0 -= mash(seed);
        if (me2.s0 < 0) {
          me2.s0 += 1;
        }
        me2.s1 -= mash(seed);
        if (me2.s1 < 0) {
          me2.s1 += 1;
        }
        me2.s2 -= mash(seed);
        if (me2.s2 < 0) {
          me2.s2 += 1;
        }
        mash = null;
      }
      function copy(f2, t2) {
        t2.c = f2.c;
        t2.s0 = f2.s0;
        t2.s1 = f2.s1;
        t2.s2 = f2.s2;
        return t2;
      }
      function impl(seed, opts) {
        var xg = new Alea(seed), state = opts && opts.state, prng = xg.next;
        prng.int32 = function() {
          return xg.next() * 4294967296 | 0;
        };
        prng.double = function() {
          return prng() + (prng() * 2097152 | 0) * 11102230246251565e-32;
        };
        prng.quick = prng;
        if (state) {
          if (typeof state == "object") copy(state, xg);
          prng.state = function() {
            return copy(xg, {});
          };
        }
        return prng;
      }
      function Mash() {
        var n2 = 4022871197;
        var mash = function(data) {
          data = String(data);
          for (var i2 = 0; i2 < data.length; i2++) {
            n2 += data.charCodeAt(i2);
            var h2 = 0.02519603282416938 * n2;
            n2 = h2 >>> 0;
            h2 -= n2;
            h2 *= n2;
            n2 = h2 >>> 0;
            h2 -= n2;
            n2 += h2 * 4294967296;
          }
          return (n2 >>> 0) * 23283064365386963e-26;
        };
        return mash;
      }
      if (module2 && module2.exports) {
        module2.exports = impl;
      } else {
        this.alea = impl;
      }
    })(
      alea,
      module
    );
  })(alea$1);
  return alea$1.exports;
}
var xor128$1 = { exports: {} };
var xor128 = xor128$1.exports;
var hasRequiredXor128;
function requireXor128() {
  if (hasRequiredXor128) return xor128$1.exports;
  hasRequiredXor128 = 1;
  (function(module) {
    (function(global2, module2, define) {
      function XorGen(seed) {
        var me2 = this, strseed = "";
        me2.x = 0;
        me2.y = 0;
        me2.z = 0;
        me2.w = 0;
        me2.next = function() {
          var t2 = me2.x ^ me2.x << 11;
          me2.x = me2.y;
          me2.y = me2.z;
          me2.z = me2.w;
          return me2.w ^= me2.w >>> 19 ^ t2 ^ t2 >>> 8;
        };
        if (seed === (seed | 0)) {
          me2.x = seed;
        } else {
          strseed += seed;
        }
        for (var k2 = 0; k2 < strseed.length + 64; k2++) {
          me2.x ^= strseed.charCodeAt(k2) | 0;
          me2.next();
        }
      }
      function copy(f2, t2) {
        t2.x = f2.x;
        t2.y = f2.y;
        t2.z = f2.z;
        t2.w = f2.w;
        return t2;
      }
      function impl(seed, opts) {
        var xg = new XorGen(seed), state = opts && opts.state, prng = function() {
          return (xg.next() >>> 0) / 4294967296;
        };
        prng.double = function() {
          do {
            var top = xg.next() >>> 11, bot = (xg.next() >>> 0) / 4294967296, result = (top + bot) / (1 << 21);
          } while (result === 0);
          return result;
        };
        prng.int32 = xg.next;
        prng.quick = prng;
        if (state) {
          if (typeof state == "object") copy(state, xg);
          prng.state = function() {
            return copy(xg, {});
          };
        }
        return prng;
      }
      if (module2 && module2.exports) {
        module2.exports = impl;
      } else {
        this.xor128 = impl;
      }
    })(
      xor128,
      module
    );
  })(xor128$1);
  return xor128$1.exports;
}
var xorwow$1 = { exports: {} };
var xorwow = xorwow$1.exports;
var hasRequiredXorwow;
function requireXorwow() {
  if (hasRequiredXorwow) return xorwow$1.exports;
  hasRequiredXorwow = 1;
  (function(module) {
    (function(global2, module2, define) {
      function XorGen(seed) {
        var me2 = this, strseed = "";
        me2.next = function() {
          var t2 = me2.x ^ me2.x >>> 2;
          me2.x = me2.y;
          me2.y = me2.z;
          me2.z = me2.w;
          me2.w = me2.v;
          return (me2.d = me2.d + 362437 | 0) + (me2.v = me2.v ^ me2.v << 4 ^ (t2 ^ t2 << 1)) | 0;
        };
        me2.x = 0;
        me2.y = 0;
        me2.z = 0;
        me2.w = 0;
        me2.v = 0;
        if (seed === (seed | 0)) {
          me2.x = seed;
        } else {
          strseed += seed;
        }
        for (var k2 = 0; k2 < strseed.length + 64; k2++) {
          me2.x ^= strseed.charCodeAt(k2) | 0;
          if (k2 == strseed.length) {
            me2.d = me2.x << 10 ^ me2.x >>> 4;
          }
          me2.next();
        }
      }
      function copy(f2, t2) {
        t2.x = f2.x;
        t2.y = f2.y;
        t2.z = f2.z;
        t2.w = f2.w;
        t2.v = f2.v;
        t2.d = f2.d;
        return t2;
      }
      function impl(seed, opts) {
        var xg = new XorGen(seed), state = opts && opts.state, prng = function() {
          return (xg.next() >>> 0) / 4294967296;
        };
        prng.double = function() {
          do {
            var top = xg.next() >>> 11, bot = (xg.next() >>> 0) / 4294967296, result = (top + bot) / (1 << 21);
          } while (result === 0);
          return result;
        };
        prng.int32 = xg.next;
        prng.quick = prng;
        if (state) {
          if (typeof state == "object") copy(state, xg);
          prng.state = function() {
            return copy(xg, {});
          };
        }
        return prng;
      }
      if (module2 && module2.exports) {
        module2.exports = impl;
      } else {
        this.xorwow = impl;
      }
    })(
      xorwow,
      module
    );
  })(xorwow$1);
  return xorwow$1.exports;
}
var xorshift7$1 = { exports: {} };
var xorshift7 = xorshift7$1.exports;
var hasRequiredXorshift7;
function requireXorshift7() {
  if (hasRequiredXorshift7) return xorshift7$1.exports;
  hasRequiredXorshift7 = 1;
  (function(module) {
    (function(global2, module2, define) {
      function XorGen(seed) {
        var me2 = this;
        me2.next = function() {
          var X2 = me2.x, i2 = me2.i, t2, v2;
          t2 = X2[i2];
          t2 ^= t2 >>> 7;
          v2 = t2 ^ t2 << 24;
          t2 = X2[i2 + 1 & 7];
          v2 ^= t2 ^ t2 >>> 10;
          t2 = X2[i2 + 3 & 7];
          v2 ^= t2 ^ t2 >>> 3;
          t2 = X2[i2 + 4 & 7];
          v2 ^= t2 ^ t2 << 7;
          t2 = X2[i2 + 7 & 7];
          t2 = t2 ^ t2 << 13;
          v2 ^= t2 ^ t2 << 9;
          X2[i2] = v2;
          me2.i = i2 + 1 & 7;
          return v2;
        };
        function init(me3, seed2) {
          var j2, X2 = [];
          if (seed2 === (seed2 | 0)) {
            X2[0] = seed2;
          } else {
            seed2 = "" + seed2;
            for (j2 = 0; j2 < seed2.length; ++j2) {
              X2[j2 & 7] = X2[j2 & 7] << 15 ^ seed2.charCodeAt(j2) + X2[j2 + 1 & 7] << 13;
            }
          }
          while (X2.length < 8) X2.push(0);
          for (j2 = 0; j2 < 8 && X2[j2] === 0; ++j2) ;
          if (j2 == 8) X2[7] = -1;
          else X2[j2];
          me3.x = X2;
          me3.i = 0;
          for (j2 = 256; j2 > 0; --j2) {
            me3.next();
          }
        }
        init(me2, seed);
      }
      function copy(f2, t2) {
        t2.x = f2.x.slice();
        t2.i = f2.i;
        return t2;
      }
      function impl(seed, opts) {
        if (seed == null) seed = +/* @__PURE__ */ new Date();
        var xg = new XorGen(seed), state = opts && opts.state, prng = function() {
          return (xg.next() >>> 0) / 4294967296;
        };
        prng.double = function() {
          do {
            var top = xg.next() >>> 11, bot = (xg.next() >>> 0) / 4294967296, result = (top + bot) / (1 << 21);
          } while (result === 0);
          return result;
        };
        prng.int32 = xg.next;
        prng.quick = prng;
        if (state) {
          if (state.x) copy(state, xg);
          prng.state = function() {
            return copy(xg, {});
          };
        }
        return prng;
      }
      if (module2 && module2.exports) {
        module2.exports = impl;
      } else {
        this.xorshift7 = impl;
      }
    })(
      xorshift7,
      module
    );
  })(xorshift7$1);
  return xorshift7$1.exports;
}
var xor4096$1 = { exports: {} };
var xor4096 = xor4096$1.exports;
var hasRequiredXor4096;
function requireXor4096() {
  if (hasRequiredXor4096) return xor4096$1.exports;
  hasRequiredXor4096 = 1;
  (function(module) {
    (function(global2, module2, define) {
      function XorGen(seed) {
        var me2 = this;
        me2.next = function() {
          var w2 = me2.w, X2 = me2.X, i2 = me2.i, t2, v2;
          me2.w = w2 = w2 + 1640531527 | 0;
          v2 = X2[i2 + 34 & 127];
          t2 = X2[i2 = i2 + 1 & 127];
          v2 ^= v2 << 13;
          t2 ^= t2 << 17;
          v2 ^= v2 >>> 15;
          t2 ^= t2 >>> 12;
          v2 = X2[i2] = v2 ^ t2;
          me2.i = i2;
          return v2 + (w2 ^ w2 >>> 16) | 0;
        };
        function init(me3, seed2) {
          var t2, v2, i2, j2, w2, X2 = [], limit = 128;
          if (seed2 === (seed2 | 0)) {
            v2 = seed2;
            seed2 = null;
          } else {
            seed2 = seed2 + "\0";
            v2 = 0;
            limit = Math.max(limit, seed2.length);
          }
          for (i2 = 0, j2 = -32; j2 < limit; ++j2) {
            if (seed2) v2 ^= seed2.charCodeAt((j2 + 32) % seed2.length);
            if (j2 === 0) w2 = v2;
            v2 ^= v2 << 10;
            v2 ^= v2 >>> 15;
            v2 ^= v2 << 4;
            v2 ^= v2 >>> 13;
            if (j2 >= 0) {
              w2 = w2 + 1640531527 | 0;
              t2 = X2[j2 & 127] ^= v2 + w2;
              i2 = 0 == t2 ? i2 + 1 : 0;
            }
          }
          if (i2 >= 128) {
            X2[(seed2 && seed2.length || 0) & 127] = -1;
          }
          i2 = 127;
          for (j2 = 4 * 128; j2 > 0; --j2) {
            v2 = X2[i2 + 34 & 127];
            t2 = X2[i2 = i2 + 1 & 127];
            v2 ^= v2 << 13;
            t2 ^= t2 << 17;
            v2 ^= v2 >>> 15;
            t2 ^= t2 >>> 12;
            X2[i2] = v2 ^ t2;
          }
          me3.w = w2;
          me3.X = X2;
          me3.i = i2;
        }
        init(me2, seed);
      }
      function copy(f2, t2) {
        t2.i = f2.i;
        t2.w = f2.w;
        t2.X = f2.X.slice();
        return t2;
      }
      function impl(seed, opts) {
        if (seed == null) seed = +/* @__PURE__ */ new Date();
        var xg = new XorGen(seed), state = opts && opts.state, prng = function() {
          return (xg.next() >>> 0) / 4294967296;
        };
        prng.double = function() {
          do {
            var top = xg.next() >>> 11, bot = (xg.next() >>> 0) / 4294967296, result = (top + bot) / (1 << 21);
          } while (result === 0);
          return result;
        };
        prng.int32 = xg.next;
        prng.quick = prng;
        if (state) {
          if (state.X) copy(state, xg);
          prng.state = function() {
            return copy(xg, {});
          };
        }
        return prng;
      }
      if (module2 && module2.exports) {
        module2.exports = impl;
      } else {
        this.xor4096 = impl;
      }
    })(
      xor4096,
      // window object or global
      module
    );
  })(xor4096$1);
  return xor4096$1.exports;
}
var tychei$1 = { exports: {} };
var tychei = tychei$1.exports;
var hasRequiredTychei;
function requireTychei() {
  if (hasRequiredTychei) return tychei$1.exports;
  hasRequiredTychei = 1;
  (function(module) {
    (function(global2, module2, define) {
      function XorGen(seed) {
        var me2 = this, strseed = "";
        me2.next = function() {
          var b2 = me2.b, c2 = me2.c, d2 = me2.d, a2 = me2.a;
          b2 = b2 << 25 ^ b2 >>> 7 ^ c2;
          c2 = c2 - d2 | 0;
          d2 = d2 << 24 ^ d2 >>> 8 ^ a2;
          a2 = a2 - b2 | 0;
          me2.b = b2 = b2 << 20 ^ b2 >>> 12 ^ c2;
          me2.c = c2 = c2 - d2 | 0;
          me2.d = d2 << 16 ^ c2 >>> 16 ^ a2;
          return me2.a = a2 - b2 | 0;
        };
        me2.a = 0;
        me2.b = 0;
        me2.c = 2654435769 | 0;
        me2.d = 1367130551;
        if (seed === Math.floor(seed)) {
          me2.a = seed / 4294967296 | 0;
          me2.b = seed | 0;
        } else {
          strseed += seed;
        }
        for (var k2 = 0; k2 < strseed.length + 20; k2++) {
          me2.b ^= strseed.charCodeAt(k2) | 0;
          me2.next();
        }
      }
      function copy(f2, t2) {
        t2.a = f2.a;
        t2.b = f2.b;
        t2.c = f2.c;
        t2.d = f2.d;
        return t2;
      }
      function impl(seed, opts) {
        var xg = new XorGen(seed), state = opts && opts.state, prng = function() {
          return (xg.next() >>> 0) / 4294967296;
        };
        prng.double = function() {
          do {
            var top = xg.next() >>> 11, bot = (xg.next() >>> 0) / 4294967296, result = (top + bot) / (1 << 21);
          } while (result === 0);
          return result;
        };
        prng.int32 = xg.next;
        prng.quick = prng;
        if (state) {
          if (typeof state == "object") copy(state, xg);
          prng.state = function() {
            return copy(xg, {});
          };
        }
        return prng;
      }
      if (module2 && module2.exports) {
        module2.exports = impl;
      } else {
        this.tychei = impl;
      }
    })(
      tychei,
      module
    );
  })(tychei$1);
  return tychei$1.exports;
}
var seedrandom$3 = { exports: {} };
const __viteBrowserExternal = {};
const __viteBrowserExternal$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  default: __viteBrowserExternal
}, Symbol.toStringTag, { value: "Module" }));
const require$$0 = /* @__PURE__ */ getAugmentedNamespace(__viteBrowserExternal$1);
var seedrandom$2 = seedrandom$3.exports;
var hasRequiredSeedrandom$1;
function requireSeedrandom$1() {
  if (hasRequiredSeedrandom$1) return seedrandom$3.exports;
  hasRequiredSeedrandom$1 = 1;
  (function(module) {
    (function(global2, pool, math) {
      var width = 256, chunks = 6, digits = 52, rngname = "random", startdenom = math.pow(width, chunks), significance = math.pow(2, digits), overflow = significance * 2, mask = width - 1, nodecrypto;
      function seedrandom2(seed, options, callback) {
        var key = [];
        options = options == true ? { entropy: true } : options || {};
        var shortseed = mixkey(flatten2(
          options.entropy ? [seed, tostring(pool)] : seed == null ? autoseed() : seed,
          3
        ), key);
        var arc4 = new ARC4(key);
        var prng = function() {
          var n2 = arc4.g(chunks), d2 = startdenom, x2 = 0;
          while (n2 < significance) {
            n2 = (n2 + x2) * width;
            d2 *= width;
            x2 = arc4.g(1);
          }
          while (n2 >= overflow) {
            n2 /= 2;
            d2 /= 2;
            x2 >>>= 1;
          }
          return (n2 + x2) / d2;
        };
        prng.int32 = function() {
          return arc4.g(4) | 0;
        };
        prng.quick = function() {
          return arc4.g(4) / 4294967296;
        };
        prng.double = prng;
        mixkey(tostring(arc4.S), pool);
        return (options.pass || callback || function(prng2, seed2, is_math_call, state) {
          if (state) {
            if (state.S) {
              copy(state, arc4);
            }
            prng2.state = function() {
              return copy(arc4, {});
            };
          }
          if (is_math_call) {
            math[rngname] = prng2;
            return seed2;
          } else return prng2;
        })(
          prng,
          shortseed,
          "global" in options ? options.global : this == math,
          options.state
        );
      }
      function ARC4(key) {
        var t2, keylen = key.length, me2 = this, i2 = 0, j2 = me2.i = me2.j = 0, s2 = me2.S = [];
        if (!keylen) {
          key = [keylen++];
        }
        while (i2 < width) {
          s2[i2] = i2++;
        }
        for (i2 = 0; i2 < width; i2++) {
          s2[i2] = s2[j2 = mask & j2 + key[i2 % keylen] + (t2 = s2[i2])];
          s2[j2] = t2;
        }
        (me2.g = function(count) {
          var t3, r2 = 0, i3 = me2.i, j3 = me2.j, s3 = me2.S;
          while (count--) {
            t3 = s3[i3 = mask & i3 + 1];
            r2 = r2 * width + s3[mask & (s3[i3] = s3[j3 = mask & j3 + t3]) + (s3[j3] = t3)];
          }
          me2.i = i3;
          me2.j = j3;
          return r2;
        })(width);
      }
      function copy(f2, t2) {
        t2.i = f2.i;
        t2.j = f2.j;
        t2.S = f2.S.slice();
        return t2;
      }
      function flatten2(obj, depth) {
        var result = [], typ = typeof obj, prop;
        if (depth && typ == "object") {
          for (prop in obj) {
            try {
              result.push(flatten2(obj[prop], depth - 1));
            } catch (e3) {
            }
          }
        }
        return result.length ? result : typ == "string" ? obj : obj + "\0";
      }
      function mixkey(seed, key) {
        var stringseed = seed + "", smear, j2 = 0;
        while (j2 < stringseed.length) {
          key[mask & j2] = mask & (smear ^= key[mask & j2] * 19) + stringseed.charCodeAt(j2++);
        }
        return tostring(key);
      }
      function autoseed() {
        try {
          var out;
          if (nodecrypto && (out = nodecrypto.randomBytes)) {
            out = out(width);
          } else {
            out = new Uint8Array(width);
            (global2.crypto || global2.msCrypto).getRandomValues(out);
          }
          return tostring(out);
        } catch (e3) {
          var browser = global2.navigator, plugins2 = browser && browser.plugins;
          return [+/* @__PURE__ */ new Date(), global2, plugins2, global2.screen, tostring(pool)];
        }
      }
      function tostring(a2) {
        return String.fromCharCode.apply(0, a2);
      }
      mixkey(math.random(), pool);
      if (module.exports) {
        module.exports = seedrandom2;
        try {
          nodecrypto = require$$0;
        } catch (ex) {
        }
      } else {
        math["seed" + rngname] = seedrandom2;
      }
    })(
      // global: `self` in browsers (including strict mode and web workers),
      // otherwise `this` in Node and other environments
      typeof self !== "undefined" ? self : seedrandom$2,
      [],
      // pool: entropy pool starts empty
      Math
      // math: package containing random, pow, and seedrandom
    );
  })(seedrandom$3);
  return seedrandom$3.exports;
}
var seedrandom$1;
var hasRequiredSeedrandom;
function requireSeedrandom() {
  if (hasRequiredSeedrandom) return seedrandom$1;
  hasRequiredSeedrandom = 1;
  var alea2 = requireAlea();
  var xor1282 = requireXor128();
  var xorwow2 = requireXorwow();
  var xorshift72 = requireXorshift7();
  var xor40962 = requireXor4096();
  var tychei2 = requireTychei();
  var sr2 = requireSeedrandom$1();
  sr2.alea = alea2;
  sr2.xor128 = xor1282;
  sr2.xorwow = xorwow2;
  sr2.xorshift7 = xorshift72;
  sr2.xor4096 = xor40962;
  sr2.tychei = tychei2;
  seedrandom$1 = sr2;
  return seedrandom$1;
}
var seedrandomExports = requireSeedrandom();
const seedrandom = /* @__PURE__ */ getDefaultExportFromCjs$1(seedrandomExports);
var commonjsGlobal = typeof globalThis !== "undefined" ? globalThis : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : typeof self !== "undefined" ? self : {};
function getDefaultExportFromCjs(x2) {
  return x2 && x2.__esModule && Object.prototype.hasOwnProperty.call(x2, "default") ? x2["default"] : x2;
}
var colorName$1;
var hasRequiredColorName;
function requireColorName() {
  if (hasRequiredColorName) return colorName$1;
  hasRequiredColorName = 1;
  colorName$1 = {
    "aliceblue": [240, 248, 255],
    "antiquewhite": [250, 235, 215],
    "aqua": [0, 255, 255],
    "aquamarine": [127, 255, 212],
    "azure": [240, 255, 255],
    "beige": [245, 245, 220],
    "bisque": [255, 228, 196],
    "black": [0, 0, 0],
    "blanchedalmond": [255, 235, 205],
    "blue": [0, 0, 255],
    "blueviolet": [138, 43, 226],
    "brown": [165, 42, 42],
    "burlywood": [222, 184, 135],
    "cadetblue": [95, 158, 160],
    "chartreuse": [127, 255, 0],
    "chocolate": [210, 105, 30],
    "coral": [255, 127, 80],
    "cornflowerblue": [100, 149, 237],
    "cornsilk": [255, 248, 220],
    "crimson": [220, 20, 60],
    "cyan": [0, 255, 255],
    "darkblue": [0, 0, 139],
    "darkcyan": [0, 139, 139],
    "darkgoldenrod": [184, 134, 11],
    "darkgray": [169, 169, 169],
    "darkgreen": [0, 100, 0],
    "darkgrey": [169, 169, 169],
    "darkkhaki": [189, 183, 107],
    "darkmagenta": [139, 0, 139],
    "darkolivegreen": [85, 107, 47],
    "darkorange": [255, 140, 0],
    "darkorchid": [153, 50, 204],
    "darkred": [139, 0, 0],
    "darksalmon": [233, 150, 122],
    "darkseagreen": [143, 188, 143],
    "darkslateblue": [72, 61, 139],
    "darkslategray": [47, 79, 79],
    "darkslategrey": [47, 79, 79],
    "darkturquoise": [0, 206, 209],
    "darkviolet": [148, 0, 211],
    "deeppink": [255, 20, 147],
    "deepskyblue": [0, 191, 255],
    "dimgray": [105, 105, 105],
    "dimgrey": [105, 105, 105],
    "dodgerblue": [30, 144, 255],
    "firebrick": [178, 34, 34],
    "floralwhite": [255, 250, 240],
    "forestgreen": [34, 139, 34],
    "fuchsia": [255, 0, 255],
    "gainsboro": [220, 220, 220],
    "ghostwhite": [248, 248, 255],
    "gold": [255, 215, 0],
    "goldenrod": [218, 165, 32],
    "gray": [128, 128, 128],
    "green": [0, 128, 0],
    "greenyellow": [173, 255, 47],
    "grey": [128, 128, 128],
    "honeydew": [240, 255, 240],
    "hotpink": [255, 105, 180],
    "indianred": [205, 92, 92],
    "indigo": [75, 0, 130],
    "ivory": [255, 255, 240],
    "khaki": [240, 230, 140],
    "lavender": [230, 230, 250],
    "lavenderblush": [255, 240, 245],
    "lawngreen": [124, 252, 0],
    "lemonchiffon": [255, 250, 205],
    "lightblue": [173, 216, 230],
    "lightcoral": [240, 128, 128],
    "lightcyan": [224, 255, 255],
    "lightgoldenrodyellow": [250, 250, 210],
    "lightgray": [211, 211, 211],
    "lightgreen": [144, 238, 144],
    "lightgrey": [211, 211, 211],
    "lightpink": [255, 182, 193],
    "lightsalmon": [255, 160, 122],
    "lightseagreen": [32, 178, 170],
    "lightskyblue": [135, 206, 250],
    "lightslategray": [119, 136, 153],
    "lightslategrey": [119, 136, 153],
    "lightsteelblue": [176, 196, 222],
    "lightyellow": [255, 255, 224],
    "lime": [0, 255, 0],
    "limegreen": [50, 205, 50],
    "linen": [250, 240, 230],
    "magenta": [255, 0, 255],
    "maroon": [128, 0, 0],
    "mediumaquamarine": [102, 205, 170],
    "mediumblue": [0, 0, 205],
    "mediumorchid": [186, 85, 211],
    "mediumpurple": [147, 112, 219],
    "mediumseagreen": [60, 179, 113],
    "mediumslateblue": [123, 104, 238],
    "mediumspringgreen": [0, 250, 154],
    "mediumturquoise": [72, 209, 204],
    "mediumvioletred": [199, 21, 133],
    "midnightblue": [25, 25, 112],
    "mintcream": [245, 255, 250],
    "mistyrose": [255, 228, 225],
    "moccasin": [255, 228, 181],
    "navajowhite": [255, 222, 173],
    "navy": [0, 0, 128],
    "oldlace": [253, 245, 230],
    "olive": [128, 128, 0],
    "olivedrab": [107, 142, 35],
    "orange": [255, 165, 0],
    "orangered": [255, 69, 0],
    "orchid": [218, 112, 214],
    "palegoldenrod": [238, 232, 170],
    "palegreen": [152, 251, 152],
    "paleturquoise": [175, 238, 238],
    "palevioletred": [219, 112, 147],
    "papayawhip": [255, 239, 213],
    "peachpuff": [255, 218, 185],
    "peru": [205, 133, 63],
    "pink": [255, 192, 203],
    "plum": [221, 160, 221],
    "powderblue": [176, 224, 230],
    "purple": [128, 0, 128],
    "rebeccapurple": [102, 51, 153],
    "red": [255, 0, 0],
    "rosybrown": [188, 143, 143],
    "royalblue": [65, 105, 225],
    "saddlebrown": [139, 69, 19],
    "salmon": [250, 128, 114],
    "sandybrown": [244, 164, 96],
    "seagreen": [46, 139, 87],
    "seashell": [255, 245, 238],
    "sienna": [160, 82, 45],
    "silver": [192, 192, 192],
    "skyblue": [135, 206, 235],
    "slateblue": [106, 90, 205],
    "slategray": [112, 128, 144],
    "slategrey": [112, 128, 144],
    "snow": [255, 250, 250],
    "springgreen": [0, 255, 127],
    "steelblue": [70, 130, 180],
    "tan": [210, 180, 140],
    "teal": [0, 128, 128],
    "thistle": [216, 191, 216],
    "tomato": [255, 99, 71],
    "turquoise": [64, 224, 208],
    "violet": [238, 130, 238],
    "wheat": [245, 222, 179],
    "white": [255, 255, 255],
    "whitesmoke": [245, 245, 245],
    "yellow": [255, 255, 0],
    "yellowgreen": [154, 205, 50]
  };
  return colorName$1;
}
var colorNameExports = requireColorName();
const colorName = /* @__PURE__ */ getDefaultExportFromCjs(colorNameExports);
var nearestColor$2 = { exports: {} };
var nearestColor$1 = nearestColor$2.exports;
var hasRequiredNearestColor;
function requireNearestColor() {
  if (hasRequiredNearestColor) return nearestColor$2.exports;
  hasRequiredNearestColor = 1;
  (function(module) {
    (function(context) {
      function nearestColor2(needle, colors) {
        needle = parseColor(needle);
        if (!needle) {
          return null;
        }
        var distanceSq, minDistanceSq = Infinity, rgb, value;
        colors || (colors = nearestColor2.DEFAULT_COLORS);
        for (var i2 = 0; i2 < colors.length; ++i2) {
          rgb = colors[i2].rgb;
          distanceSq = Math.pow(needle.r - rgb.r, 2) + Math.pow(needle.g - rgb.g, 2) + Math.pow(needle.b - rgb.b, 2);
          if (distanceSq < minDistanceSq) {
            minDistanceSq = distanceSq;
            value = colors[i2];
          }
        }
        if (value.name) {
          return {
            name: value.name,
            value: value.source,
            rgb: value.rgb,
            distance: Math.sqrt(minDistanceSq)
          };
        }
        return value.source;
      }
      nearestColor2.from = function from(availableColors) {
        var colors = mapColors(availableColors), nearestColorBase = nearestColor2;
        var matcher = function nearestColor3(hex) {
          return nearestColorBase(hex, colors);
        };
        matcher.from = from;
        matcher.or = function or2(alternateColors) {
          var extendedColors = colors.concat(mapColors(alternateColors));
          return nearestColor2.from(extendedColors);
        };
        return matcher;
      };
      function mapColors(colors) {
        if (colors instanceof Array) {
          return colors.map(function(color) {
            return createColorSpec(color);
          });
        }
        return Object.keys(colors).map(function(name) {
          return createColorSpec(colors[name], name);
        });
      }
      function parseColor(source) {
        var red, green, blue;
        if (typeof source === "object") {
          return source;
        }
        if (source in nearestColor2.STANDARD_COLORS) {
          return parseColor(nearestColor2.STANDARD_COLORS[source]);
        }
        var hexMatch = source.match(/^#?((?:[0-9a-f]{3}){1,2})$/i);
        if (hexMatch) {
          hexMatch = hexMatch[1];
          if (hexMatch.length === 3) {
            hexMatch = [
              hexMatch.charAt(0) + hexMatch.charAt(0),
              hexMatch.charAt(1) + hexMatch.charAt(1),
              hexMatch.charAt(2) + hexMatch.charAt(2)
            ];
          } else {
            hexMatch = [
              hexMatch.substring(0, 2),
              hexMatch.substring(2, 4),
              hexMatch.substring(4, 6)
            ];
          }
          red = parseInt(hexMatch[0], 16);
          green = parseInt(hexMatch[1], 16);
          blue = parseInt(hexMatch[2], 16);
          return { r: red, g: green, b: blue };
        }
        var rgbMatch = source.match(/^rgb\(\s*(\d{1,3}%?),\s*(\d{1,3}%?),\s*(\d{1,3}%?)\s*\)$/i);
        if (rgbMatch) {
          red = parseComponentValue(rgbMatch[1]);
          green = parseComponentValue(rgbMatch[2]);
          blue = parseComponentValue(rgbMatch[3]);
          return { r: red, g: green, b: blue };
        }
        throw Error('"' + source + '" is not a valid color');
      }
      function createColorSpec(input, name) {
        var color = {};
        if (name) {
          color.name = name;
        }
        if (typeof input === "string") {
          color.source = input;
          color.rgb = parseColor(input);
        } else if (typeof input === "object") {
          if (input.source) {
            return createColorSpec(input.source, input.name);
          }
          color.rgb = input;
          color.source = rgbToHex(input);
        }
        return color;
      }
      function parseComponentValue(string) {
        if (string.charAt(string.length - 1) === "%") {
          return Math.round(parseInt(string, 10) * 255 / 100);
        }
        return Number(string);
      }
      function rgbToHex(rgb) {
        return "#" + leadingZero(rgb.r.toString(16)) + leadingZero(rgb.g.toString(16)) + leadingZero(rgb.b.toString(16));
      }
      function leadingZero(value) {
        if (value.length === 1) {
          value = "0" + value;
        }
        return value;
      }
      nearestColor2.STANDARD_COLORS = {
        aqua: "#0ff",
        black: "#000",
        blue: "#00f",
        fuchsia: "#f0f",
        gray: "#808080",
        green: "#008000",
        lime: "#0f0",
        maroon: "#800000",
        navy: "#000080",
        olive: "#808000",
        orange: "#ffa500",
        purple: "#800080",
        red: "#f00",
        silver: "#c0c0c0",
        teal: "#008080",
        white: "#fff",
        yellow: "#ff0"
      };
      nearestColor2.DEFAULT_COLORS = mapColors([
        "#f00",
        // r
        "#f80",
        // o
        "#ff0",
        // y
        "#0f0",
        // g
        "#00f",
        // b
        "#008",
        // i
        "#808"
        // v
      ]);
      nearestColor2.VERSION = "0.4.4";
      if (module && module.exports) {
        module.exports = nearestColor2;
      } else {
        context.nearestColor = nearestColor2;
      }
    })(nearestColor$1);
  })(nearestColor$2);
  return nearestColor$2.exports;
}
var nearestColorExports = requireNearestColor();
const nearestColor = /* @__PURE__ */ getDefaultExportFromCjs(nearestColorExports);
const canonicalColorHexByKey = {
  black: "#000000",
  white: "#ffffff",
  gray: "#808080",
  red: "#ff0000",
  orange: "#ffa500",
  yellow: "#ffff00",
  green: "#008000",
  cyan: "#00ffff",
  blue: "#0000ff",
  purple: "#800080",
  pink: "#ffc0cb",
  brown: "#a52a2a"
};
const canonicalAnchorDefinitions = Object.fromEntries(
  Object.entries(canonicalColorHexByKey).map(([canonical, hex]) => [
    canonical,
    { hex, canonical }
  ])
);
const extraAnchorDefinitions = {
  silver: { hex: "#c0c0c0", canonical: "gray" },
  grayDark: { hex: "#4d4d4d", canonical: "gray" },
  grayCustom: { hex: "#757575", canonical: "gray" },
  grayLight: { hex: "#c8c8c8", canonical: "gray" },
  grayLighter: { hex: "#dcdcdc", canonical: "gray" },
  maroon: { hex: "#800000", canonical: "red" },
  redLight: { hex: "#f08080", canonical: "red" },
  redCustom: { hex: "#D4042D", canonical: "red" },
  redDark: { hex: "#8b0000", canonical: "red" },
  orangeLight: { hex: "#ffa07a", canonical: "orange" },
  orangeCustom: { hex: "#F19143", canonical: "orange" },
  orangePresetAccessible: { hex: "#a6510c", canonical: "orange" },
  orangeDark: { hex: "#ff8c00", canonical: "orange" },
  yellowLight: { hex: "#ffffe0", canonical: "yellow" },
  yellowDark: { hex: "#bdb76b", canonical: "yellow" },
  yellowgreen: { hex: "#9acd32", canonical: "green" },
  olive: { hex: "#808000", canonical: "green" },
  lime: { hex: "#00ff00", canonical: "green" },
  greenLight: { hex: "#90ee90", canonical: "green" },
  greenCustom: { hex: "#2ca02c", canonical: "green" },
  greenDark: { hex: "#006400", canonical: "green" },
  teal: { hex: "#008080", canonical: "green" },
  aqua: { hex: "#00ffff", canonical: "cyan" },
  cyanLight: { hex: "#e0ffff", canonical: "cyan" },
  cyanDark: { hex: "#008b8b", canonical: "cyan" },
  navy: { hex: "#000080", canonical: "blue" },
  blueCustom: { hex: "#648FFF", canonical: "blue" },
  bluePresetAccessible: { hex: "#1f5dff", canonical: "blue" },
  blueLight: { hex: "#add8e6", canonical: "blue" },
  blueDark: { hex: "#00008b", canonical: "blue" },
  fuchsia: { hex: "#ff00ff", canonical: "purple" },
  purpleCustom: { hex: "#644CD6", canonical: "purple" },
  purpleLight: { hex: "#dda0dd", canonical: "purple" },
  purpleDark: { hex: "#8b008b", canonical: "purple" },
  hotpink: { hex: "#ff69b4", canonical: "pink" },
  pinkDark: { hex: "#ff1493", canonical: "pink" },
  brownLight: { hex: "#deb887", canonical: "brown" },
  brownDark: { hex: "#8b4513", canonical: "brown" }
};
const anchorDefinitions = {
  ...canonicalAnchorDefinitions,
  ...extraAnchorDefinitions
};
const matchColorHexByName = Object.fromEntries(
  Object.entries(anchorDefinitions).map(([name, definition]) => [
    name,
    definition.hex
  ])
);
const canonicalKeyByMatchName = Object.fromEntries(
  Object.entries(anchorDefinitions).map(([name, definition]) => [
    name,
    definition.canonical
  ])
);
const nearestCanonicalColor = nearestColor.from(matchColorHexByName);
const cssNamedColorSet = /* @__PURE__ */ new Set([
  ...Object.keys(colorName),
  "transparent",
  "currentcolor"
]);
function isCssNamedColorKeyword(colorValue) {
  return cssNamedColorSet.has(colorValue.trim().toLowerCase());
}
function resolveColorWord(colorValue, options = {}) {
  const preserveCssNamedColor = options.preserveCssNamedColor ?? true;
  const invalidFallback = options.invalidFallback ?? "original";
  const trimmedColorValue = colorValue.trim();
  if (trimmedColorValue.length === 0) {
    return {
      englishWord: "",
      localizedWord: "",
      source: "invalid"
    };
  }
  if (preserveCssNamedColor && isCssNamedColorKeyword(trimmedColorValue)) {
    const lowerName = trimmedColorValue.toLowerCase();
    const key = `cssColor.${lowerName}`;
    const englishWord = trimmedColorValue;
    const localizedWord = options.translate ? options.translate(key, englishWord) : englishWord;
    return {
      key,
      englishWord,
      localizedWord,
      source: "named-css"
    };
  }
  try {
    const nearest = nearestCanonicalColor(trimmedColorValue);
    const canonicalName = nearest ? canonicalKeyByMatchName[nearest.name] : void 0;
    if (!canonicalName) {
      throw Error("No nearest canonical color resolved");
    }
    const key = `color.${canonicalName}`;
    const englishWord = canonicalName;
    const localizedWord = options.translate ? options.translate(key, englishWord) : englishWord;
    return {
      key,
      englishWord,
      localizedWord,
      source: "nearest-palette"
    };
  } catch (e22) {
    const englishWord = invalidFallback === "empty" ? "" : trimmedColorValue;
    return {
      englishWord,
      localizedWord: englishWord,
      source: "invalid"
    };
  }
}
function colorValueToWord(colorValue, options = {}) {
  return resolveColorWord(colorValue, options).localizedWord;
}
function isStyleValueWithPosition(value) {
  return typeof value === "object" && value !== null && "style" in value;
}
function getStyleValueString(styleDef, key) {
  const value = styleDef[key]?.style;
  return typeof value === "string" ? value : void 0;
}
function getStyleValueNumber(styleDef, key) {
  const value = styleDef[key]?.style;
  return typeof value === "number" ? value : void 0;
}
function setStyleValue(styleDef, key, style, position) {
  styleDef[key] = {
    style,
    position
  };
}
function normalizeStyleDefinitionValues(styleDef) {
  const normalizedStyleDefinition = {};
  for (const key in styleDef) {
    const typedKey = key;
    const value = styleDef[typedKey];
    if (value === void 0) {
      continue;
    }
    if (isStyleValueWithPosition(value)) {
      setStyleValue(
        normalizedStyleDefinition,
        typedKey,
        value.style,
        value.position
      );
    } else {
      setStyleValue(normalizedStyleDefinition, typedKey, value);
    }
  }
  return normalizedStyleDefinition;
}
function normalizeStyleDefinitionsValues(styleDefinitions) {
  const normalizedStyleDefinitions = {};
  for (const styleNumber in styleDefinitions) {
    normalizedStyleDefinitions[styleNumber] = normalizeStyleDefinitionValues(styleDefinitions[styleNumber]);
  }
  return normalizedStyleDefinitions;
}
function unwrapStyleDefinition(styleDef) {
  const unwrappedStyleDefinition = {};
  for (const key in styleDef) {
    const typedKey = key;
    const value = styleDef[typedKey]?.style;
    if (value !== void 0) {
      unwrappedStyleDefinition[typedKey] = value;
    }
  }
  return unwrappedStyleDefinition;
}
const DEFAULT_STYLE_VALUES = {
  lineOpacity: 0.7,
  lineWidth: 4,
  lineWidthWord: "thick",
  lineStyle: "solid",
  lineStyleWord: "",
  markerOpacity: 0.7,
  markerStyle: "circle",
  markerStyleWord: "point",
  markerSize: 5,
  markerFilled: true,
  fillOpacity: 0.3,
  fillPatternOpacity: 1,
  fillStyle: "solid",
  fillStyleWord: "",
  lineColor: "#1f5dff",
  lineColorDarkMode: "#648FFF",
  markerColor: "#1f5dff",
  markerColorDarkMode: "#648FFF",
  fillColor: "#1f5dff",
  fillColorDarkMode: "#648FFF",
  textColor: "black",
  textColorDarkMode: "white",
  highContrastColor: "#2963FF",
  highContrastColorDarkMode: "#92B4FF"
};
const RESOLVED_STYLE_FALLBACKS = {
  ...DEFAULT_STYLE_VALUES,
  lineColorWord: "",
  lineColorWordDarkMode: "",
  markerColorWord: "",
  markerColorWordDarkMode: "",
  fillColorWord: "",
  fillColorWordDarkMode: "",
  textColorWord: "",
  textColorWordDarkMode: "",
  highContrastColorWord: "",
  highContrastColorWordDarkMode: "",
  backgroundColor: "",
  backgroundColorWord: "",
  backgroundColorDarkMode: "",
  backgroundColorWordDarkMode: ""
};
function resolveStyleDefinition(styleDef) {
  const resolved = { ...RESOLVED_STYLE_FALLBACKS };
  const writable = resolved;
  for (const key of Object.keys(
    RESOLVED_STYLE_FALLBACKS
  )) {
    const value = styleDef[key];
    if (value !== void 0) {
      writable[key] = value;
    }
  }
  return resolved;
}
function positionSortValue(position) {
  if (typeof position.start?.offset === "number") {
    return position.start.offset;
  }
  const line = position.start?.line ?? 0;
  const column = position.start?.column ?? 0;
  return line * 1e6 + column;
}
function latestPosition(...positions) {
  let latest;
  for (const position of positions) {
    if (!position) {
      continue;
    }
    if (!latest || positionSortValue(position) >= positionSortValue(latest)) {
      latest = position;
    }
  }
  return latest;
}
var r$2 = { grad: 0.9, turn: 360, rad: 360 / (2 * Math.PI) }, t$1$1 = function(r2) {
  return "string" == typeof r2 ? r2.length > 0 : "number" == typeof r2;
}, n$2 = function(r2, t2, n2) {
  return void 0 === t2 && (t2 = 0), void 0 === n2 && (n2 = Math.pow(10, t2)), Math.round(n2 * r2) / n2 + 0;
}, e$2 = function(r2, t2, n2) {
  return void 0 === t2 && (t2 = 0), void 0 === n2 && (n2 = 1), r2 > n2 ? n2 : r2 > t2 ? r2 : t2;
}, u$2 = function(r2) {
  return (r2 = isFinite(r2) ? r2 % 360 : 0) > 0 ? r2 : r2 + 360;
}, a$2 = function(r2) {
  return { r: e$2(r2.r, 0, 255), g: e$2(r2.g, 0, 255), b: e$2(r2.b, 0, 255), a: e$2(r2.a) };
}, o$1$1 = function(r2) {
  return { r: n$2(r2.r), g: n$2(r2.g), b: n$2(r2.b), a: n$2(r2.a, 3) };
}, i$2 = /^#([0-9a-f]{3,8})$/i, s$2 = function(r2) {
  var t2 = r2.toString(16);
  return t2.length < 2 ? "0" + t2 : t2;
}, h$2 = function(r2) {
  var t2 = r2.r, n2 = r2.g, e22 = r2.b, u2 = r2.a, a2 = Math.max(t2, n2, e22), o2 = a2 - Math.min(t2, n2, e22), i2 = o2 ? a2 === t2 ? (n2 - e22) / o2 : a2 === n2 ? 2 + (e22 - t2) / o2 : 4 + (t2 - n2) / o2 : 0;
  return { h: 60 * (i2 < 0 ? i2 + 6 : i2), s: a2 ? o2 / a2 * 100 : 0, v: a2 / 255 * 100, a: u2 };
}, b$2 = function(r2) {
  var t2 = r2.h, n2 = r2.s, e22 = r2.v, u2 = r2.a;
  t2 = t2 / 360 * 6, n2 /= 100, e22 /= 100;
  var a2 = Math.floor(t2), o2 = e22 * (1 - n2), i2 = e22 * (1 - (t2 - a2) * n2), s2 = e22 * (1 - (1 - t2 + a2) * n2), h2 = a2 % 6;
  return { r: 255 * [e22, i2, o2, o2, s2, e22][h2], g: 255 * [s2, e22, e22, i2, o2, o2][h2], b: 255 * [o2, o2, s2, e22, e22, i2][h2], a: u2 };
}, g$2 = function(r2) {
  return { h: u$2(r2.h), s: e$2(r2.s, 0, 100), l: e$2(r2.l, 0, 100), a: e$2(r2.a) };
}, d$2 = function(r2) {
  return { h: n$2(r2.h), s: n$2(r2.s), l: n$2(r2.l), a: n$2(r2.a, 3) };
}, f$2 = function(r2) {
  return b$2((n2 = (t2 = r2).s, { h: t2.h, s: (n2 *= ((e22 = t2.l) < 50 ? e22 : 100 - e22) / 100) > 0 ? 2 * n2 / (e22 + n2) * 100 : 0, v: e22 + n2, a: t2.a }));
  var t2, n2, e22;
}, c$2 = function(r2) {
  return { h: (t2 = h$2(r2)).h, s: (u2 = (200 - (n2 = t2.s)) * (e22 = t2.v) / 100) > 0 && u2 < 200 ? n2 * e22 / 100 / (u2 <= 100 ? u2 : 200 - u2) * 100 : 0, l: u2 / 2, a: t2.a };
  var t2, n2, e22, u2;
}, l$2 = /^hsla?\(\s*([+-]?\d*\.?\d+)(deg|rad|grad|turn)?\s*,\s*([+-]?\d*\.?\d+)%\s*,\s*([+-]?\d*\.?\d+)%\s*(?:,\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, p$2 = /^hsla?\(\s*([+-]?\d*\.?\d+)(deg|rad|grad|turn)?\s+([+-]?\d*\.?\d+)%\s+([+-]?\d*\.?\d+)%\s*(?:\/\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, v$2 = /^rgba?\(\s*([+-]?\d*\.?\d+)(%)?\s*,\s*([+-]?\d*\.?\d+)(%)?\s*,\s*([+-]?\d*\.?\d+)(%)?\s*(?:,\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, m$2 = /^rgba?\(\s*([+-]?\d*\.?\d+)(%)?\s+([+-]?\d*\.?\d+)(%)?\s+([+-]?\d*\.?\d+)(%)?\s*(?:\/\s*([+-]?\d*\.?\d+)(%)?\s*)?\)$/i, y$2 = { string: [[function(r2) {
  var t2 = i$2.exec(r2);
  return t2 ? (r2 = t2[1]).length <= 4 ? { r: parseInt(r2[0] + r2[0], 16), g: parseInt(r2[1] + r2[1], 16), b: parseInt(r2[2] + r2[2], 16), a: 4 === r2.length ? n$2(parseInt(r2[3] + r2[3], 16) / 255, 2) : 1 } : 6 === r2.length || 8 === r2.length ? { r: parseInt(r2.substr(0, 2), 16), g: parseInt(r2.substr(2, 2), 16), b: parseInt(r2.substr(4, 2), 16), a: 8 === r2.length ? n$2(parseInt(r2.substr(6, 2), 16) / 255, 2) : 1 } : null : null;
}, "hex"], [function(r2) {
  var t2 = v$2.exec(r2) || m$2.exec(r2);
  return t2 ? t2[2] !== t2[4] || t2[4] !== t2[6] ? null : a$2({ r: Number(t2[1]) / (t2[2] ? 100 / 255 : 1), g: Number(t2[3]) / (t2[4] ? 100 / 255 : 1), b: Number(t2[5]) / (t2[6] ? 100 / 255 : 1), a: void 0 === t2[7] ? 1 : Number(t2[7]) / (t2[8] ? 100 : 1) }) : null;
}, "rgb"], [function(t2) {
  var n2 = l$2.exec(t2) || p$2.exec(t2);
  if (!n2) return null;
  var e22, u2, a2 = g$2({ h: (e22 = n2[1], u2 = n2[2], void 0 === u2 && (u2 = "deg"), Number(e22) * (r$2[u2] || 1)), s: Number(n2[3]), l: Number(n2[4]), a: void 0 === n2[5] ? 1 : Number(n2[5]) / (n2[6] ? 100 : 1) });
  return f$2(a2);
}, "hsl"]], object: [[function(r2) {
  var n2 = r2.r, e22 = r2.g, u2 = r2.b, o2 = r2.a, i2 = void 0 === o2 ? 1 : o2;
  return t$1$1(n2) && t$1$1(e22) && t$1$1(u2) ? a$2({ r: Number(n2), g: Number(e22), b: Number(u2), a: Number(i2) }) : null;
}, "rgb"], [function(r2) {
  var n2 = r2.h, e22 = r2.s, u2 = r2.l, a2 = r2.a, o2 = void 0 === a2 ? 1 : a2;
  if (!t$1$1(n2) || !t$1$1(e22) || !t$1$1(u2)) return null;
  var i2 = g$2({ h: Number(n2), s: Number(e22), l: Number(u2), a: Number(o2) });
  return f$2(i2);
}, "hsl"], [function(r2) {
  var n2 = r2.h, a2 = r2.s, o2 = r2.v, i2 = r2.a, s2 = void 0 === i2 ? 1 : i2;
  if (!t$1$1(n2) || !t$1$1(a2) || !t$1$1(o2)) return null;
  var h2 = (function(r3) {
    return { h: u$2(r3.h), s: e$2(r3.s, 0, 100), v: e$2(r3.v, 0, 100), a: e$2(r3.a) };
  })({ h: Number(n2), s: Number(a2), v: Number(o2), a: Number(s2) });
  return b$2(h2);
}, "hsv"]] }, N$2 = function(r2, t2) {
  for (var n2 = 0; n2 < t2.length; n2++) {
    var e22 = t2[n2][0](r2);
    if (e22) return [e22, t2[n2][1]];
  }
  return [null, void 0];
}, x$2 = function(r2) {
  return "string" == typeof r2 ? N$2(r2.trim(), y$2.string) : "object" == typeof r2 && null !== r2 ? N$2(r2, y$2.object) : [null, void 0];
}, M$4 = function(r2, t2) {
  var n2 = c$2(r2);
  return { h: n2.h, s: e$2(n2.s + 100 * t2, 0, 100), l: n2.l, a: n2.a };
}, H$3 = function(r2) {
  return (299 * r2.r + 587 * r2.g + 114 * r2.b) / 1e3 / 255;
}, $$2 = function(r2, t2) {
  var n2 = c$2(r2);
  return { h: n2.h, s: n2.s, l: e$2(n2.l + 100 * t2, 0, 100), a: n2.a };
}, j$2 = (function() {
  function r2(r3) {
    this.parsed = x$2(r3)[0], this.rgba = this.parsed || { r: 0, g: 0, b: 0, a: 1 };
  }
  return r2.prototype.isValid = function() {
    return null !== this.parsed;
  }, r2.prototype.brightness = function() {
    return n$2(H$3(this.rgba), 2);
  }, r2.prototype.isDark = function() {
    return H$3(this.rgba) < 0.5;
  }, r2.prototype.isLight = function() {
    return H$3(this.rgba) >= 0.5;
  }, r2.prototype.toHex = function() {
    return r3 = o$1$1(this.rgba), t2 = r3.r, e22 = r3.g, u2 = r3.b, i2 = (a2 = r3.a) < 1 ? s$2(n$2(255 * a2)) : "", "#" + s$2(t2) + s$2(e22) + s$2(u2) + i2;
    var r3, t2, e22, u2, a2, i2;
  }, r2.prototype.toRgb = function() {
    return o$1$1(this.rgba);
  }, r2.prototype.toRgbString = function() {
    return r3 = o$1$1(this.rgba), t2 = r3.r, n2 = r3.g, e22 = r3.b, (u2 = r3.a) < 1 ? "rgba(" + t2 + ", " + n2 + ", " + e22 + ", " + u2 + ")" : "rgb(" + t2 + ", " + n2 + ", " + e22 + ")";
    var r3, t2, n2, e22, u2;
  }, r2.prototype.toHsl = function() {
    return d$2(c$2(this.rgba));
  }, r2.prototype.toHslString = function() {
    return r3 = d$2(c$2(this.rgba)), t2 = r3.h, n2 = r3.s, e22 = r3.l, (u2 = r3.a) < 1 ? "hsla(" + t2 + ", " + n2 + "%, " + e22 + "%, " + u2 + ")" : "hsl(" + t2 + ", " + n2 + "%, " + e22 + "%)";
    var r3, t2, n2, e22, u2;
  }, r2.prototype.toHsv = function() {
    return r3 = h$2(this.rgba), { h: n$2(r3.h), s: n$2(r3.s), v: n$2(r3.v), a: n$2(r3.a, 3) };
    var r3;
  }, r2.prototype.invert = function() {
    return w$2({ r: 255 - (r3 = this.rgba).r, g: 255 - r3.g, b: 255 - r3.b, a: r3.a });
    var r3;
  }, r2.prototype.saturate = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$2(M$4(this.rgba, r3));
  }, r2.prototype.desaturate = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$2(M$4(this.rgba, -r3));
  }, r2.prototype.grayscale = function() {
    return w$2(M$4(this.rgba, -1));
  }, r2.prototype.lighten = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$2($$2(this.rgba, r3));
  }, r2.prototype.darken = function(r3) {
    return void 0 === r3 && (r3 = 0.1), w$2($$2(this.rgba, -r3));
  }, r2.prototype.rotate = function(r3) {
    return void 0 === r3 && (r3 = 15), this.hue(this.hue() + r3);
  }, r2.prototype.alpha = function(r3) {
    return "number" == typeof r3 ? w$2({ r: (t2 = this.rgba).r, g: t2.g, b: t2.b, a: r3 }) : n$2(this.rgba.a, 3);
    var t2;
  }, r2.prototype.hue = function(r3) {
    var t2 = c$2(this.rgba);
    return "number" == typeof r3 ? w$2({ h: r3, s: t2.s, l: t2.l, a: t2.a }) : n$2(t2.h);
  }, r2.prototype.isEqual = function(r3) {
    return this.toHex() === w$2(r3).toHex();
  }, r2;
})(), w$2 = function(r2) {
  return r2 instanceof j$2 ? r2 : new j$2(r2);
}, S$2 = [], k$2 = function(r2) {
  r2.forEach(function(r3) {
    S$2.indexOf(r3) < 0 && (r3(j$2, y$2), S$2.push(r3));
  });
};
var o$2 = function(o2) {
  var t2 = o2 / 255;
  return t2 < 0.04045 ? t2 / 12.92 : Math.pow((t2 + 0.055) / 1.055, 2.4);
}, t$2 = function(t2) {
  return 0.2126 * o$2(t2.r) + 0.7152 * o$2(t2.g) + 0.0722 * o$2(t2.b);
};
function a11yPlugin(o2) {
  o2.prototype.luminance = function() {
    return o3 = t$2(this.rgba), void 0 === (r2 = 2) && (r2 = 0), void 0 === n2 && (n2 = Math.pow(10, r2)), Math.round(n2 * o3) / n2 + 0;
    var o3, r2, n2;
  }, o2.prototype.contrast = function(r2) {
    void 0 === r2 && (r2 = "#FFF");
    var n2, a2, i2, e22, v2, u2, d2, c2 = r2 instanceof o2 ? r2 : new o2(r2);
    return e22 = this.rgba, v2 = c2.toRgb(), u2 = t$2(e22), d2 = t$2(v2), n2 = u2 > d2 ? (u2 + 0.05) / (d2 + 0.05) : (d2 + 0.05) / (u2 + 0.05), void 0 === (a2 = 2) && (a2 = 0), void 0 === i2 && (i2 = Math.pow(10, a2)), Math.floor(i2 * n2) / i2 + 0;
  }, o2.prototype.isReadable = function(o3, t2) {
    return void 0 === o3 && (o3 = "#FFF"), void 0 === t2 && (t2 = {}), this.contrast(o3) >= (e22 = void 0 === (i2 = (r2 = t2).size) ? "normal" : i2, "AAA" === (a2 = void 0 === (n2 = r2.level) ? "AA" : n2) && "normal" === e22 ? 7 : "AA" === a2 && "large" === e22 ? 3 : 4.5);
    var r2, n2, a2, i2, e22;
  };
}
function namesPlugin(e22, f2) {
  var a2 = { white: "#ffffff", bisque: "#ffe4c4", blue: "#0000ff", cadetblue: "#5f9ea0", chartreuse: "#7fff00", chocolate: "#d2691e", coral: "#ff7f50", antiquewhite: "#faebd7", aqua: "#00ffff", azure: "#f0ffff", whitesmoke: "#f5f5f5", papayawhip: "#ffefd5", plum: "#dda0dd", blanchedalmond: "#ffebcd", black: "#000000", gold: "#ffd700", goldenrod: "#daa520", gainsboro: "#dcdcdc", cornsilk: "#fff8dc", cornflowerblue: "#6495ed", burlywood: "#deb887", aquamarine: "#7fffd4", beige: "#f5f5dc", crimson: "#dc143c", cyan: "#00ffff", darkblue: "#00008b", darkcyan: "#008b8b", darkgoldenrod: "#b8860b", darkkhaki: "#bdb76b", darkgray: "#a9a9a9", darkgreen: "#006400", darkgrey: "#a9a9a9", peachpuff: "#ffdab9", darkmagenta: "#8b008b", darkred: "#8b0000", darkorchid: "#9932cc", darkorange: "#ff8c00", darkslateblue: "#483d8b", gray: "#808080", darkslategray: "#2f4f4f", darkslategrey: "#2f4f4f", deeppink: "#ff1493", deepskyblue: "#00bfff", wheat: "#f5deb3", firebrick: "#b22222", floralwhite: "#fffaf0", ghostwhite: "#f8f8ff", darkviolet: "#9400d3", magenta: "#ff00ff", green: "#008000", dodgerblue: "#1e90ff", grey: "#808080", honeydew: "#f0fff0", hotpink: "#ff69b4", blueviolet: "#8a2be2", forestgreen: "#228b22", lawngreen: "#7cfc00", indianred: "#cd5c5c", indigo: "#4b0082", fuchsia: "#ff00ff", brown: "#a52a2a", maroon: "#800000", mediumblue: "#0000cd", lightcoral: "#f08080", darkturquoise: "#00ced1", lightcyan: "#e0ffff", ivory: "#fffff0", lightyellow: "#ffffe0", lightsalmon: "#ffa07a", lightseagreen: "#20b2aa", linen: "#faf0e6", mediumaquamarine: "#66cdaa", lemonchiffon: "#fffacd", lime: "#00ff00", khaki: "#f0e68c", mediumseagreen: "#3cb371", limegreen: "#32cd32", mediumspringgreen: "#00fa9a", lightskyblue: "#87cefa", lightblue: "#add8e6", midnightblue: "#191970", lightpink: "#ffb6c1", mistyrose: "#ffe4e1", moccasin: "#ffe4b5", mintcream: "#f5fffa", lightslategray: "#778899", lightslategrey: "#778899", navajowhite: "#ffdead", navy: "#000080", mediumvioletred: "#c71585", powderblue: "#b0e0e6", palegoldenrod: "#eee8aa", oldlace: "#fdf5e6", paleturquoise: "#afeeee", mediumturquoise: "#48d1cc", mediumorchid: "#ba55d3", rebeccapurple: "#663399", lightsteelblue: "#b0c4de", mediumslateblue: "#7b68ee", thistle: "#d8bfd8", tan: "#d2b48c", orchid: "#da70d6", mediumpurple: "#9370db", purple: "#800080", pink: "#ffc0cb", skyblue: "#87ceeb", springgreen: "#00ff7f", palegreen: "#98fb98", red: "#ff0000", yellow: "#ffff00", slateblue: "#6a5acd", lavenderblush: "#fff0f5", peru: "#cd853f", palevioletred: "#db7093", violet: "#ee82ee", teal: "#008080", slategray: "#708090", slategrey: "#708090", aliceblue: "#f0f8ff", darkseagreen: "#8fbc8f", darkolivegreen: "#556b2f", greenyellow: "#adff2f", seagreen: "#2e8b57", seashell: "#fff5ee", tomato: "#ff6347", silver: "#c0c0c0", sienna: "#a0522d", lavender: "#e6e6fa", lightgreen: "#90ee90", orange: "#ffa500", orangered: "#ff4500", steelblue: "#4682b4", royalblue: "#4169e1", turquoise: "#40e0d0", yellowgreen: "#9acd32", salmon: "#fa8072", saddlebrown: "#8b4513", sandybrown: "#f4a460", rosybrown: "#bc8f8f", darksalmon: "#e9967a", lightgoldenrodyellow: "#fafad2", snow: "#fffafa", lightgrey: "#d3d3d3", lightgray: "#d3d3d3", dimgray: "#696969", dimgrey: "#696969", olivedrab: "#6b8e23", olive: "#808000" }, r2 = {};
  for (var d2 in a2) r2[a2[d2]] = d2;
  var l2 = {};
  e22.prototype.toName = function(f3) {
    if (!(this.rgba.a || this.rgba.r || this.rgba.g || this.rgba.b)) return "transparent";
    var d3, i2, n2 = r2[this.toHex()];
    if (n2) return n2;
    if (null == f3 ? void 0 : f3.closest) {
      var o2 = this.toRgb(), t2 = 1 / 0, b2 = "black";
      if (!l2.length) for (var c2 in a2) l2[c2] = new e22(a2[c2]).toRgb();
      for (var g2 in a2) {
        var u2 = (d3 = o2, i2 = l2[g2], Math.pow(d3.r - i2.r, 2) + Math.pow(d3.g - i2.g, 2) + Math.pow(d3.b - i2.b, 2));
        u2 < t2 && (t2 = u2, b2 = g2);
      }
      return b2;
    }
  };
  f2.string.push([function(f3) {
    var r3 = f3.toLowerCase(), d3 = "transparent" === r3 ? "#0000" : a2[r3];
    return d3 ? new e22(d3).toRgb() : null;
  }, "name"]);
}
k$2([a11yPlugin, namesPlugin]);
const CANVAS_LIGHT_MODE_COLOR = "#ffffff";
const CANVAS_DARK_MODE_COLOR = "#121212";
const GRAPHIC_CONTRAST_THRESHOLD = 3;
const TEXT_CONTRAST_THRESHOLD = 4.5;
function clamp01$1(value) {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
function compositeOver(foreground, background) {
  const a2 = clamp01$1(foreground.a);
  return {
    r: foreground.r * a2 + background.r * (1 - a2),
    g: foreground.g * a2 + background.g * (1 - a2),
    b: foreground.b * a2 + background.b * (1 - a2)
  };
}
function hexWithLightness(color, lightness) {
  return w$2({ ...color, l: lightness }).toHex();
}
function compositedContrastRatio({
  foreground,
  canvas,
  background,
  opacityMultiplier = 1
}) {
  const fg = w$2(foreground);
  const canvasColor = w$2(canvas);
  if (!fg.isValid() || !canvasColor.isValid()) {
    return null;
  }
  const canvasRgb = canvasColor.toRgb();
  const canvasOpaque = {
    r: canvasRgb.r,
    g: canvasRgb.g,
    b: canvasRgb.b
  };
  let backgroundOpaque = canvasOpaque;
  if (background !== void 0) {
    const bg = w$2(background);
    if (!bg.isValid()) {
      return null;
    }
    backgroundOpaque = compositeOver(bg.toRgb(), canvasOpaque);
  }
  const fgRgba = fg.toRgb();
  const effectiveForeground = compositeOver(
    { ...fgRgba, a: clamp01$1(fgRgba.a * opacityMultiplier) },
    backgroundOpaque
  );
  return w$2(effectiveForeground).contrast(w$2(backgroundOpaque));
}
function deriveAccessibleDarkModeColor({
  lightColor,
  threshold,
  opacityMultiplier = 1
}) {
  const base = w$2(lightColor);
  if (!base.isValid()) {
    return lightColor;
  }
  const currentRatio = compositedContrastRatio({
    foreground: lightColor,
    canvas: CANVAS_DARK_MODE_COLOR,
    opacityMultiplier
  });
  if (currentRatio !== null && currentRatio >= threshold) {
    return lightColor;
  }
  const { h: h2, s: s2, l: startL, a: a2 } = base.toHsl();
  for (let l2 = Math.ceil(startL); l2 <= 100; l2++) {
    const candidate = hexWithLightness({ h: h2, s: s2, a: a2 }, l2);
    const ratio = compositedContrastRatio({
      foreground: candidate,
      canvas: CANVAS_DARK_MODE_COLOR,
      opacityMultiplier
    });
    if (ratio !== null && ratio >= threshold) {
      return candidate;
    }
  }
  return hexWithLightness({ h: h2, s: s2, a: a2 }, 100);
}
function invertLightness(color) {
  const c2 = w$2(color);
  if (!c2.isValid()) {
    return color;
  }
  const { h: h2, s: s2, l: l2, a: a2 } = c2.toHsl();
  return w$2({ h: h2, s: s2, l: 100 - l2, a: a2 }).toHex();
}
function deriveAccessibleDarkModeBackground(lightColor) {
  return invertLightness(lightColor);
}
function suggestAccessibleDarkModeColorAgainst({
  startColor,
  partnerColor,
  channelRole,
  threshold
}) {
  const base = w$2(startColor);
  if (!base.isValid()) {
    return null;
  }
  const { h: h2, s: s2, l: startL, a: a2 } = base.toHsl();
  const ratioAtLightness = (candidateL) => {
    const candidate = hexWithLightness({ h: h2, s: s2, a: a2 }, candidateL);
    return channelRole === "text" ? compositedContrastRatio({
      foreground: candidate,
      canvas: CANVAS_DARK_MODE_COLOR,
      background: partnerColor
    }) : compositedContrastRatio({
      foreground: partnerColor,
      canvas: CANVAS_DARK_MODE_COLOR,
      background: candidate
    });
  };
  const roundedStart = Math.round(startL);
  for (let delta = 0; delta <= 100; delta++) {
    const candidates = delta === 0 ? [roundedStart] : [roundedStart + delta, roundedStart - delta];
    for (const candidateL of candidates) {
      if (candidateL < 0 || candidateL > 100) {
        continue;
      }
      const ratio = ratioAtLightness(candidateL);
      if (ratio !== null && ratio >= threshold) {
        return hexWithLightness({ h: h2, s: s2, a: a2 }, candidateL);
      }
    }
  }
  return null;
}
const CANVAS_FOR_MODE = {
  light: CANVAS_LIGHT_MODE_COLOR,
  dark: CANVAS_DARK_MODE_COLOR
};
const MODE_SUFFIX = {
  light: "",
  dark: " (dark mode)"
};
function formatRatio(value) {
  return value.toFixed(2);
}
function createContrastAccessibilityDiagnostic({
  styleNumber,
  context,
  ratio,
  threshold,
  position
}) {
  return {
    type: "accessibility",
    level: 1,
    message: `Style definition ${styleNumber} has insufficient contrast for ${context} (${formatRatio(ratio)}:1; requires at least ${threshold}:1).`,
    position
  };
}
function appendContrastAccessibilityDiagnosticIfNeeded({
  diagnostics,
  styleNumber,
  context,
  ratio,
  threshold,
  position
}) {
  if (ratio === null || ratio >= threshold || !position) {
    return;
  }
  diagnostics.push(
    createContrastAccessibilityDiagnostic({
      styleNumber,
      context,
      ratio,
      threshold,
      position
    })
  );
}
function colorKey(item, mode) {
  return mode === "dark" ? `${item}ColorDarkMode` : `${item}Color`;
}
function contrastDiagnosticsForMode(styleNumber, styleDef, mode) {
  const diagnostics = [];
  const canvas = CANVAS_FOR_MODE[mode];
  const suffix = MODE_SUFFIX[mode];
  const textColor = getStyleValueString(styleDef, colorKey("text", mode));
  const backgroundColor = getStyleValueString(
    styleDef,
    colorKey("background", mode)
  );
  if (textColor) {
    const textPosition = styleDef[colorKey("text", mode)]?.position;
    const diagnosticPosition = latestPosition(
      textPosition,
      styleDef[colorKey("background", mode)]?.position
    );
    const ratio = compositedContrastRatio({
      foreground: textColor,
      canvas,
      background: backgroundColor
    });
    appendContrastAccessibilityDiagnosticIfNeeded({
      diagnostics,
      styleNumber,
      context: (backgroundColor ? "text color against background color" : "text color against the canvas") + suffix,
      ratio,
      threshold: TEXT_CONTRAST_THRESHOLD,
      position: diagnosticPosition
    });
  }
  const highContrastColor = getStyleValueString(
    styleDef,
    colorKey("highContrast", mode)
  );
  if (highContrastColor) {
    const highContrastPosition = styleDef[colorKey("highContrast", mode)]?.position;
    const ratio = compositedContrastRatio({
      foreground: highContrastColor,
      canvas
    });
    appendContrastAccessibilityDiagnosticIfNeeded({
      diagnostics,
      styleNumber,
      context: "high-contrast color against the canvas" + suffix,
      ratio,
      threshold: TEXT_CONTRAST_THRESHOLD,
      position: highContrastPosition
    });
  }
  const lineColor = getStyleValueString(styleDef, colorKey("line", mode));
  if (lineColor) {
    const linePosition = styleDef[colorKey("line", mode)]?.position;
    const diagnosticPosition = latestPosition(
      linePosition,
      styleDef.lineOpacity?.position
    );
    const lineOpacity = getStyleValueNumber(styleDef, "lineOpacity") ?? DEFAULT_STYLE_VALUES.lineOpacity;
    const ratio = compositedContrastRatio({
      foreground: lineColor,
      canvas,
      opacityMultiplier: lineOpacity
    });
    appendContrastAccessibilityDiagnosticIfNeeded({
      diagnostics,
      styleNumber,
      context: "line color against the canvas" + suffix,
      ratio,
      threshold: GRAPHIC_CONTRAST_THRESHOLD,
      position: diagnosticPosition
    });
  }
  const markerColor = getStyleValueString(styleDef, colorKey("marker", mode));
  if (markerColor) {
    const markerPosition = styleDef[colorKey("marker", mode)]?.position;
    const diagnosticPosition = latestPosition(
      markerPosition,
      styleDef.markerOpacity?.position
    );
    const markerOpacity = getStyleValueNumber(styleDef, "markerOpacity") ?? DEFAULT_STYLE_VALUES.markerOpacity;
    const ratio = compositedContrastRatio({
      foreground: markerColor,
      canvas,
      opacityMultiplier: markerOpacity
    });
    appendContrastAccessibilityDiagnosticIfNeeded({
      diagnostics,
      styleNumber,
      context: "marker color against the canvas" + suffix,
      ratio,
      threshold: GRAPHIC_CONTRAST_THRESHOLD,
      position: diagnosticPosition
    });
  }
  return diagnostics;
}
function derivedDarkModeCombinationDiagnostics(styleNumber, styleDef) {
  const textLight = getStyleValueString(styleDef, "textColor");
  const backgroundLight = getStyleValueString(styleDef, "backgroundColor");
  const textDark = getStyleValueString(styleDef, "textColorDarkMode");
  const backgroundDark = getStyleValueString(
    styleDef,
    "backgroundColorDarkMode"
  );
  if (!textLight || !backgroundLight || !textDark || !backgroundDark) {
    return [];
  }
  if (styleDef.textColorDarkMode?.position || styleDef.backgroundColorDarkMode?.position) {
    return [];
  }
  const lightRatio = compositedContrastRatio({
    foreground: textLight,
    canvas: CANVAS_LIGHT_MODE_COLOR,
    background: backgroundLight
  });
  const darkRatio = compositedContrastRatio({
    foreground: textDark,
    canvas: CANVAS_DARK_MODE_COLOR,
    background: backgroundDark
  });
  if (lightRatio === null || darkRatio === null || lightRatio < TEXT_CONTRAST_THRESHOLD || darkRatio >= TEXT_CONTRAST_THRESHOLD) {
    return [];
  }
  const textPosition = styleDef.textColor?.position;
  const backgroundPosition = styleDef.backgroundColor?.position;
  const position = latestPosition(textPosition, backgroundPosition);
  if (!position) {
    return [];
  }
  const squiggledChannel = backgroundPosition && position === backgroundPosition ? "background" : "text";
  const channelsToTry = squiggledChannel === "background" ? ["background", "text"] : ["text", "background"];
  let suggestion;
  for (const channel of channelsToTry) {
    const color = suggestAccessibleDarkModeColorAgainst({
      startColor: channel === "text" ? textDark : backgroundDark,
      partnerColor: channel === "text" ? backgroundDark : textDark,
      channelRole: channel,
      threshold: TEXT_CONTRAST_THRESHOLD
    });
    if (color) {
      suggestion = {
        lightAttribute: channel === "text" ? "textColor" : "backgroundColor",
        darkAttribute: channel === "text" ? "textColorDarkMode" : "backgroundColorDarkMode",
        darkColor: color
      };
      break;
    }
  }
  const baseMessage = `Although style definition ${styleNumber} has specified colors that provide sufficient contrast for light mode, the dark-mode colors derived from these values have insufficient contrast for the text color against the background color (${formatRatio(darkRatio)}:1; requires at least ${TEXT_CONTRAST_THRESHOLD}:1).`;
  let fixMessage;
  if (suggestion) {
    const lightColor = invertLightness(suggestion.darkColor);
    fixMessage = ` To ensure sufficient contrast in dark mode, either increase the light-mode contrast (e.g., set ${suggestion.lightAttribute}="${lightColor}") or override the dark-mode color (e.g., set ${suggestion.darkAttribute}="${suggestion.darkColor}").`;
  } else {
    fixMessage = ` To ensure sufficient contrast in dark mode, increase the light-mode contrast or override the derived colors with textColorDarkMode and/or backgroundColorDarkMode.`;
  }
  return [
    {
      type: "accessibility",
      level: 1,
      message: baseMessage + fixMessage,
      position
    }
  ];
}
function derivedDarkModeTextCanvasDiagnostics(styleNumber, styleDef) {
  const textLight = getStyleValueString(styleDef, "textColor");
  const textDark = getStyleValueString(styleDef, "textColorDarkMode");
  if (!textLight || !textDark || getStyleValueString(styleDef, "backgroundColor") || getStyleValueString(styleDef, "backgroundColorDarkMode") || styleDef.textColorDarkMode?.position) {
    return [];
  }
  const position = styleDef.textColor?.position;
  if (!position) {
    return [];
  }
  const lightRatio = compositedContrastRatio({
    foreground: textLight,
    canvas: CANVAS_LIGHT_MODE_COLOR
  });
  const darkRatio = compositedContrastRatio({
    foreground: textDark,
    canvas: CANVAS_DARK_MODE_COLOR
  });
  if (lightRatio === null || darkRatio === null || lightRatio < TEXT_CONTRAST_THRESHOLD || darkRatio >= TEXT_CONTRAST_THRESHOLD) {
    return [];
  }
  const suggestedDark = deriveAccessibleDarkModeColor({
    lightColor: textDark,
    threshold: TEXT_CONTRAST_THRESHOLD
  });
  const suggestedDarkRatio = compositedContrastRatio({
    foreground: suggestedDark,
    canvas: CANVAS_DARK_MODE_COLOR
  });
  const fixMessage = suggestedDarkRatio !== null && suggestedDarkRatio >= TEXT_CONTRAST_THRESHOLD ? ` To ensure sufficient contrast in dark mode, either increase the light-mode contrast (e.g., set textColor="${invertLightness(suggestedDark)}") or override the dark-mode color (e.g., set textColorDarkMode="${suggestedDark}").` : ` To ensure sufficient contrast in dark mode, increase the light-mode contrast or override the derived color with textColorDarkMode.`;
  return [
    {
      type: "accessibility",
      level: 1,
      message: `Although style definition ${styleNumber} has a specified text color that provides sufficient contrast for light mode, the dark-mode text color derived from this value has insufficient contrast against the canvas (${formatRatio(darkRatio)}:1; requires at least ${TEXT_CONTRAST_THRESHOLD}:1).${fixMessage}`,
      position
    }
  ];
}
function contrastAccessibilityDiagnosticsForStyleDefinition(styleNumber, styleDef) {
  return [
    ...contrastDiagnosticsForMode(styleNumber, styleDef, "light"),
    ...contrastDiagnosticsForMode(styleNumber, styleDef, "dark"),
    ...derivedDarkModeCombinationDiagnostics(styleNumber, styleDef),
    ...derivedDarkModeTextCanvasDiagnostics(styleNumber, styleDef)
  ];
}
function contrastAccessibilityDiagnosticsForStyleDefinitions(styleDefinitions) {
  const diagnostics = [];
  for (const styleNumber in styleDefinitions) {
    diagnostics.push(
      ...contrastAccessibilityDiagnosticsForStyleDefinition(
        styleNumber,
        styleDefinitions[styleNumber]
      )
    );
  }
  return diagnostics;
}
let styleAttributes$1 = {
  lineColor: {
    componentType: "text",
    description: "Color used for line strokes (light mode)."
  },
  lineColorWord: {
    componentType: "text",
    description: "Human-readable name of the line color (light mode)."
  },
  lineColorDarkMode: {
    componentType: "text",
    description: "Color used for line strokes (dark mode)."
  },
  lineColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the line color (dark mode)."
  },
  lineOpacity: {
    componentType: "number",
    description: "Opacity of line strokes, 0 to 1."
  },
  lineWidth: {
    componentType: "number",
    description: "Stroke width for lines, in pixels."
  },
  lineWidthWord: {
    componentType: "text",
    description: "Human-readable name of the line width."
  },
  lineStyle: {
    componentType: "text",
    description: "Stroke style for lines.",
    toLowerCase: true,
    validValues: [
      { value: "solid", description: "Continuous, unbroken stroke." },
      {
        value: "dashed",
        description: "Stroke composed of evenly-spaced dashes."
      },
      {
        value: "dotted",
        description: "Stroke composed of evenly-spaced dots."
      }
    ]
  },
  lineStyleWord: {
    componentType: "text",
    description: "Human-readable name of the line style."
  },
  markerColor: {
    componentType: "text",
    description: "Color used for markers/points (light mode)."
  },
  markerColorWord: {
    componentType: "text",
    description: "Human-readable name of the marker color (light mode)."
  },
  markerColorDarkMode: {
    componentType: "text",
    description: "Color used for markers/points (dark mode)."
  },
  markerColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the marker color (dark mode)."
  },
  markerOpacity: {
    componentType: "number",
    description: "Opacity of markers/points, 0 to 1."
  },
  markerStyle: {
    componentType: "text",
    description: "Marker shape.",
    toLowerCase: true,
    validValues: [
      { value: "circle", description: "Circular marker." },
      { value: "square", description: "Square marker." },
      {
        value: "triangle",
        description: 'Triangular marker pointing up (alias for "triangleUp").'
      },
      {
        value: "triangleUp",
        description: "Triangular marker pointing up."
      },
      {
        value: "triangleDown",
        description: "Triangular marker pointing down."
      },
      {
        value: "triangleLeft",
        description: "Triangular marker pointing left."
      },
      {
        value: "triangleRight",
        description: "Triangular marker pointing right."
      },
      { value: "diamond", description: "Diamond-shaped marker." },
      { value: "cross", description: "Cross-shaped marker (×)." },
      { value: "plus", description: "Plus-shaped marker (+)." }
    ]
  },
  markerStyleWord: {
    componentType: "text",
    description: "Human-readable name of the marker style."
  },
  markerSize: {
    componentType: "number",
    description: "Marker size in pixels."
  },
  markerFilled: {
    componentType: "boolean",
    description: "Whether the marker is rendered filled (true) or open (false). Has no effect when markerStyle is cross or plus, which have no interior."
  },
  fillColor: {
    componentType: "text",
    description: "Fill color used inside closed shapes (light mode)."
  },
  fillColorWord: {
    componentType: "text",
    description: "Human-readable name of the fill color (light mode)."
  },
  fillColorDarkMode: {
    componentType: "text",
    description: "Fill color used inside closed shapes (dark mode)."
  },
  fillColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the fill color (dark mode)."
  },
  fillOpacity: {
    componentType: "number",
    description: "Opacity of solid fills, 0 to 1."
  },
  fillPatternOpacity: {
    componentType: "number",
    description: "Opacity of pattern fills (non-solid fillStyle), 0 to 1."
  },
  fillStyle: {
    componentType: "text",
    description: "Fill pattern used inside closed shapes.",
    toLowerCase: true,
    validValues: [
      { value: "solid", description: "Solid fill." },
      {
        value: "horizontal",
        description: "Horizontal line pattern."
      },
      {
        value: "vertical",
        description: "Vertical line pattern."
      },
      {
        value: "diagonal",
        description: "Diagonal line pattern (/)."
      },
      {
        value: "backDiagonal",
        description: "Back-diagonal line pattern (\\)."
      },
      {
        value: "dots",
        description: "Dots pattern."
      },
      {
        value: "diamonds",
        description: "Filled diamonds pattern."
      }
    ]
  },
  fillStyleWord: {
    componentType: "text",
    description: "Human-readable name of the fill style."
  },
  textColor: {
    componentType: "text",
    description: "Text color (light mode)."
  },
  textColorWord: {
    componentType: "text",
    description: "Human-readable name of the text color (light mode)."
  },
  textColorDarkMode: {
    componentType: "text",
    description: "Text color (dark mode)."
  },
  textColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the text color (dark mode)."
  },
  highContrastColor: {
    componentType: "text",
    description: "High-contrast accent color (light mode)."
  },
  highContrastColorWord: {
    componentType: "text",
    description: "Human-readable name of the high-contrast accent color (light mode)."
  },
  highContrastColorDarkMode: {
    componentType: "text",
    description: "High-contrast accent color (dark mode)."
  },
  highContrastColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the high-contrast accent color (dark mode)."
  },
  backgroundColor: {
    componentType: "text",
    description: "Background color (light mode)."
  },
  backgroundColorWord: {
    componentType: "text",
    description: "Human-readable name of the background color (light mode)."
  },
  backgroundColorDarkMode: {
    componentType: "text",
    description: "Background color (dark mode)."
  },
  backgroundColorWordDarkMode: {
    componentType: "text",
    description: "Human-readable name of the background color (dark mode)."
  }
};
const markerStyleValuesWithFillVariants = styleAttributes$1.markerStyle.validValues.filter(
  (entry) => entry.value !== "cross" && entry.value !== "plus"
);
const MARKER_OVERRIDE_KEYS = [
  "markerStyle",
  "markerSize",
  "markerFilled"
];
const LINE_OVERRIDE_KEYS = [
  "lineStyle",
  "lineWidth"
];
const FILL_OVERRIDE_KEYS = [
  "fillOpacity",
  "fillPatternOpacity",
  "fillStyle"
];
const markerOverrideAttributes = Object.fromEntries(
  MARKER_OVERRIDE_KEYS.map((key) => [key, styleAttributes$1[key]])
);
const lineOverrideAttributes = Object.fromEntries(
  LINE_OVERRIDE_KEYS.map((key) => [key, styleAttributes$1[key]])
);
const fillOverrideAttributes = Object.fromEntries(
  FILL_OVERRIDE_KEYS.map((key) => [key, styleAttributes$1[key]])
);
({
  ...markerOverrideAttributes,
  ...lineOverrideAttributes,
  ...fillOverrideAttributes
});
const STYLE_OVERRIDE_CATEGORIES = {
  marker: markerOverrideAttributes,
  line: lineOverrideAttributes,
  fill: fillOverrideAttributes
};
for (const [category, group] of Object.entries(STYLE_OVERRIDE_CATEGORIES)) {
  for (const [key, spec] of Object.entries(group)) {
    if (key.toLowerCase().includes("color")) {
      throw new Error(
        `Style override category "${category}" contains color-related key "${key}"; colors stay <styleDefinition>-only so per-styleNumber WCAG contrast diagnostics remain authoritative. If this is intentional, also reconcile the lowercase asymmetry in returnSelectedStyleStateVariableDefinition.`
      );
    }
    if (key.endsWith("Opacity") && key !== "fillOpacity" && key !== "fillPatternOpacity") {
      throw new Error(
        `Style override category "${category}" contains contrast-feeding opacity key "${key}"; lineOpacity and markerOpacity feed the WCAG contrast diagnostic as an opacityMultiplier on the foreground alpha, so they stay <styleDefinition>-only alongside colors. Only fillOpacity and fillPatternOpacity are contrast-irrelevant and overridable.`
      );
    }
    if (spec.componentType === "text" && !spec.toLowerCase) {
      throw new Error(
        `Style override key "${key}" in category "${category}" is text-typed but missing toLowerCase: true. Add toLowerCase: true to its styleAttributes entry, or move it out of the override surface.`
      );
    }
  }
}
function attributeSpecFromStyleAttribute(spec) {
  const attr = {
    createComponentOfType: spec.componentType,
    description: spec.description
  };
  if (spec.validValues) {
    attr.validValues = spec.validValues;
  }
  if (spec.toLowerCase) {
    attr.toLowerCase = spec.toLowerCase;
  }
  return attr;
}
let defaultStyle = { ...DEFAULT_STYLE_VALUES };
const coloredItemsForWords = [
  "line",
  "marker",
  "fill",
  "text",
  "highContrast",
  "background"
];
function addMissingColorWordsToStyleDefinition(styleDef) {
  for (const item of coloredItemsForWords) {
    const colorKey2 = `${item}Color`;
    const colorWordKey = `${colorKey2}Word`;
    const darkKey = `${colorKey2}DarkMode`;
    const darkWordKey = `${colorWordKey}DarkMode`;
    const colorValue = getStyleValueString(styleDef, colorKey2);
    if (!(colorWordKey in styleDef) && colorValue) {
      setStyleValue(styleDef, colorWordKey, colorValueToWord(colorValue));
    }
    const darkColorValue = getStyleValueString(styleDef, darkKey);
    if (!(darkWordKey in styleDef) && darkColorValue) {
      setStyleValue(
        styleDef,
        darkWordKey,
        colorValueToWord(darkColorValue)
      );
    }
  }
  return styleDef;
}
function addMissingColorWordsToStyleDefinitions(styleDefinitions) {
  for (const styleDef of Object.values(styleDefinitions)) {
    addMissingColorWordsToStyleDefinition(styleDef);
  }
  return styleDefinitions;
}
function cloneDefaultStyleWithMissingColorWords() {
  return addMissingColorWordsToStyleDefinition(
    normalizeStyleDefinitionValues(Object.assign({}, defaultStyle))
  );
}
function deriveDarkModeColorForItem(item, lightColor, styleDef) {
  if (item === "background") {
    return deriveAccessibleDarkModeBackground(lightColor);
  }
  if (item === "text") {
    return invertLightness(lightColor);
  }
  if (item === "highContrast") {
    return deriveAccessibleDarkModeColor({
      lightColor,
      threshold: TEXT_CONTRAST_THRESHOLD
    });
  }
  if (item === "line" || item === "marker") {
    const opacityKey = `${item}Opacity`;
    const opacityDefault = DEFAULT_STYLE_VALUES[opacityKey];
    const opacity = getStyleValueNumber(styleDef, opacityKey) ?? opacityDefault ?? 1;
    return deriveAccessibleDarkModeColor({
      lightColor,
      threshold: GRAPHIC_CONTRAST_THRESHOLD,
      opacityMultiplier: opacity
    });
  }
  return deriveAccessibleDarkModeColor({
    lightColor,
    threshold: GRAPHIC_CONTRAST_THRESHOLD
  });
}
function addMissingChildStyleColorFields(styleDef) {
  for (const item of coloredItemsForWords) {
    deriveMissingDarkModeColor(styleDef, item);
  }
  return addMissingColorWordsToStyleDefinition(styleDef);
}
function deriveMissingDarkModeColor(styleDef, item) {
  const colorKey2 = `${item}Color`;
  const darkKey = `${colorKey2}DarkMode`;
  if (!(colorKey2 in styleDef) || darkKey in styleDef) {
    return;
  }
  const colorValue = getStyleValueString(styleDef, colorKey2);
  if (colorValue === void 0) {
    return;
  }
  const darkColor = deriveDarkModeColorForItem(item, colorValue, styleDef);
  setStyleValue(styleDef, darkKey, darkColor);
}
function deriveMissingStyleWords(styleDef) {
  if ("lineWidth" in styleDef && !("lineWidthWord" in styleDef)) {
    const widthValue = getStyleValueNumber(styleDef, "lineWidth");
    if (widthValue !== void 0) {
      const widthPosition = styleDef.lineWidth?.position;
      const word = widthValue >= 4 ? "thick" : widthValue <= 1 ? "thin" : "";
      setStyleValue(styleDef, "lineWidthWord", word, widthPosition);
    }
  }
  if ("lineStyle" in styleDef && !("lineStyleWord" in styleDef)) {
    const lineStyle = getStyleValueString(styleDef, "lineStyle");
    if (lineStyle) {
      const lineStylePosition = styleDef.lineStyle?.position;
      const word = lineStyle === "dashed" ? "dashed" : lineStyle === "dotted" ? "dotted" : "";
      setStyleValue(styleDef, "lineStyleWord", word, lineStylePosition);
    }
  }
  if ("markerStyle" in styleDef && !("markerStyleWord" in styleDef)) {
    const markerStyle = getStyleValueString(styleDef, "markerStyle");
    const markerStylePosition = styleDef.markerStyle?.position;
    if (markerStyle) {
      setStyleValue(
        styleDef,
        "markerStyleWord",
        markerStyle,
        markerStylePosition
      );
    }
    const markerStyleWord = getStyleValueString(
      styleDef,
      "markerStyleWord"
    );
    if (markerStyleWord === "circle") {
      setStyleValue(
        styleDef,
        "markerStyleWord",
        "point",
        markerStylePosition
      );
    } else if (markerStyleWord && markerStyleWord.slice(0, 8) === "triangle") {
      setStyleValue(
        styleDef,
        "markerStyleWord",
        "triangle",
        markerStylePosition
      );
    }
  }
  if ("fillStyle" in styleDef && !("fillStyleWord" in styleDef)) {
    const fillStyle = getStyleValueString(styleDef, "fillStyle");
    if (fillStyle) {
      const fillStylePosition = styleDef.fillStyle?.position;
      const fillStyleWordMap = {
        solid: "",
        horizontal: "horizontal lines",
        vertical: "vertical lines",
        diagonal: "diagonal lines",
        backdiagonal: "reverse diagonal lines",
        dots: "dots",
        diamonds: "diamonds"
      };
      const word = fillStyleWordMap[fillStyle] ?? "";
      setStyleValue(styleDef, "fillStyleWord", word, fillStylePosition);
    }
  }
}
function returnDefaultStyleDefinitions() {
  return addMissingColorWordsToStyleDefinitions(
    normalizeStyleDefinitionsValues({
      1: { ...DEFAULT_STYLE_VALUES },
      2: {
        lineColor: "#D4042D",
        lineColorDarkMode: "#F1466A",
        lineOpacity: 0.7,
        lineWidth: 2,
        lineWidthWord: "",
        lineStyle: "solid",
        lineStyleWord: "",
        markerColor: "#D4042D",
        markerColorDarkMode: "#F1466A",
        markerOpacity: 0.7,
        markerStyle: "square",
        markerStyleWord: "square",
        markerSize: 5,
        fillColor: "#D4042D",
        fillColorDarkMode: "#F1466A",
        fillOpacity: 0.3,
        textColor: "#D4042D",
        textColorDarkMode: "#FF7A7A",
        highContrastColor: "#D4042D",
        highContrastColorDarkMode: "#FF7A7A"
      },
      3: {
        lineColor: "#a6510c",
        lineColorDarkMode: "#F19143",
        lineOpacity: 0.7,
        lineWidth: 3,
        lineWidthWord: "",
        lineStyle: "solid",
        lineStyleWord: "",
        markerColor: "#a6510c",
        markerColorDarkMode: "#F19143",
        markerOpacity: 0.7,
        markerStyle: "triangle",
        markerStyleWord: "triangle",
        markerSize: 5,
        fillColor: "#a6510c",
        fillColorDarkMode: "#F19143",
        fillOpacity: 0.3,
        textColor: "#BE5A0E",
        textColorDarkMode: "#FFA94D",
        highContrastColor: "#BE5A0E",
        highContrastColorDarkMode: "#FFA94D"
      },
      4: {
        lineColor: "#644CD6",
        lineColorDarkMode: "#9F8FE8",
        lineOpacity: 0.7,
        lineWidth: 2,
        lineWidthWord: "",
        lineStyle: "solid",
        lineStyleWord: "",
        markerColor: "#644CD6",
        markerColorDarkMode: "#9F8FE8",
        markerOpacity: 0.7,
        markerStyle: "diamond",
        markerStyleWord: "diamond",
        markerSize: 5,
        fillColor: "#644CD6",
        fillColorDarkMode: "#9F8FE8",
        fillOpacity: 0.3,
        textColor: "#644CD6",
        textColorDarkMode: "#B0A4EE",
        highContrastColor: "#644CD6",
        highContrastColorDarkMode: "#B0A4EE"
      },
      5: {
        lineColor: "black",
        lineColorDarkMode: "white",
        lineOpacity: 1,
        lineWidth: 1,
        lineWidthWord: "thin",
        lineStyle: "solid",
        lineStyleWord: "",
        markerColor: "black",
        markerColorDarkMode: "white",
        markerOpacity: 1,
        markerStyle: "circle",
        markerStyleWord: "point",
        markerSize: 5,
        fillColor: "black",
        fillColorDarkMode: "white",
        fillOpacity: 0.7,
        textColor: "black",
        textColorDarkMode: "white",
        highContrastColor: "black",
        highContrastColorDarkMode: "white"
      },
      6: {
        lineColor: "#636363",
        lineColorDarkMode: "#CCCCCC",
        lineOpacity: 0.7,
        lineWidth: 1,
        lineWidthWord: "thin",
        lineStyle: "dotted",
        lineStyleWord: "dotted",
        markerColor: "#636363",
        markerColorDarkMode: "#CCCCCC",
        markerOpacity: 0.7,
        markerStyle: "circle",
        markerStyleWord: "point",
        markerSize: 5,
        fillColor: "#636363",
        fillColorDarkMode: "#CCCCCC",
        fillOpacity: 0.3,
        textColor: "#757575",
        textColorDarkMode: "#B0B0B0",
        highContrastColor: "#757575",
        highContrastColorDarkMode: "#B0B0B0"
      }
    })
  );
}
function returnStyleDefinitionStateVariables() {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.setupChildren = {
    returnDependencies: () => ({
      setupChildren: {
        dependencyType: "child",
        childGroups: ["setups"],
        proceedIfAllChildrenNotMatched: true
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: { setupChildren: dependencyValues.setupChildren }
      };
    }
  };
  stateVariableDefinitions.styleDefinitions = {
    mustEvaluate: true,
    stateVariablesDeterminingDependencies: ["setupChildren"],
    returnDependencies({ stateValues }) {
      let dependencies = {
        ancestorWithStyle: {
          dependencyType: "ancestor",
          variableNames: ["styleDefinitions"]
        },
        styleDefinitionSetupChildren: {
          dependencyType: "child",
          childGroups: ["styleDefinitions", "setups"],
          variableNames: ["styleNumber", "styleDefinition"],
          variablesOptional: true,
          proceedIfAllChildrenNotMatched: true
        }
      };
      for (let setupChild of stateValues.setupChildren) {
        dependencies[`styleDefinitionsOf${setupChild.componentIdx}`] = {
          dependencyType: "child",
          parentIdx: setupChild.componentIdx,
          childGroups: ["styleDefinitions"],
          variableNames: ["styleNumber", "styleDefinition"]
        };
      }
      return dependencies;
    },
    definition({ dependencyValues }) {
      const styleDefinitions = {};
      let startingStateVariableDefinitions;
      if (dependencyValues.ancestorWithStyle) {
        startingStateVariableDefinitions = dependencyValues.ancestorWithStyle.stateValues.styleDefinitions;
      }
      if (!startingStateVariableDefinitions) {
        startingStateVariableDefinitions = returnDefaultStyleDefinitions();
      }
      for (const styleNumber in startingStateVariableDefinitions) {
        styleDefinitions[styleNumber] = normalizeStyleDefinitionValues(
          Object.assign(
            {},
            startingStateVariableDefinitions[styleNumber]
          )
        );
      }
      const styleDefinitionChildren = [];
      for (let child of dependencyValues.styleDefinitionSetupChildren) {
        if (child.componentType === "setup") {
          styleDefinitionChildren.push(
            ...dependencyValues[`styleDefinitionsOf${child.componentIdx}`]
          );
        } else {
          styleDefinitionChildren.push(child);
        }
      }
      for (const child of styleDefinitionChildren) {
        const styleNumber = child.stateValues.styleNumber;
        let styleDef = styleDefinitions[styleNumber];
        if (!styleDef) {
          styleDef = styleDefinitions[styleNumber] = cloneDefaultStyleWithMissingColorWords();
        }
        const theNewDef = normalizeStyleDefinitionValues(
          Object.assign({}, child.stateValues.styleDefinition)
        );
        if (child.position) {
          for (const key in theNewDef) {
            const typedKey = key;
            const value = theNewDef[typedKey];
            if (!value) {
              continue;
            }
            if (value.position === void 0) {
              value.position = child.position;
            }
          }
        }
        addMissingChildStyleColorFields(theNewDef);
        deriveMissingStyleWords(theNewDef);
        Object.assign(styleDef, theNewDef);
      }
      const diagnostics = contrastAccessibilityDiagnosticsForStyleDefinitions(
        styleDefinitions
      );
      return {
        setValue: { styleDefinitions },
        sendDiagnostics: diagnostics
      };
    }
  };
  return stateVariableDefinitions;
}
function returnSelectedStyleStateVariableDefinition(options = {}) {
  const overrideAttributeNames = options.overrideAttributeNames ?? [];
  return {
    selectedStyle: {
      forRenderer: true,
      willNeverBeEssential: true,
      returnDependencies: () => {
        const dependencies = {
          styleNumber: {
            dependencyType: "stateVariable",
            variableName: "styleNumber"
          },
          ancestorWithStyle: {
            dependencyType: "ancestor",
            variableNames: ["styleDefinitions"]
          }
        };
        for (const name of overrideAttributeNames) {
          dependencies[name] = {
            dependencyType: "attributeComponent",
            attributeName: name,
            variableNames: ["value"]
          };
        }
        return dependencies;
      },
      definition: function({
        dependencyValues
      }) {
        let styleDefinitions = dependencyValues.ancestorWithStyle.stateValues.styleDefinitions;
        if (!styleDefinitions) {
          styleDefinitions = returnDefaultStyleDefinitions();
        }
        let selectedStyle = styleDefinitions[dependencyValues.styleNumber];
        if (selectedStyle === void 0) {
          selectedStyle = cloneDefaultStyleWithMissingColorWords();
        }
        if (overrideAttributeNames.length > 0) {
          const overrideStyleDef = {};
          for (const name of overrideAttributeNames) {
            const dep = dependencyValues[name];
            if (dep == null) {
              continue;
            }
            let value = dep.stateValues.value;
            if (value === void 0 || value === null) {
              continue;
            }
            if (typeof value === "string" && styleAttributes$1[name]?.toLowerCase) {
              value = value.toLowerCase();
            }
            setStyleValue(
              overrideStyleDef,
              name,
              value,
              dep.position
            );
          }
          if (Object.keys(overrideStyleDef).length > 0) {
            deriveMissingStyleWords(overrideStyleDef);
            selectedStyle = Object.assign(
              {},
              selectedStyle,
              overrideStyleDef
            );
          }
        }
        return {
          setValue: {
            selectedStyle: resolveStyleDefinition(
              unwrapStyleDefinition(selectedStyle)
            )
          }
        };
      }
    }
  };
}
function returnTextStyleDescriptionDefinitions() {
  return {
    textColor: {
      description: "Human-readable name for this component's text color, derived from the active style and theme.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({
        dependencyValues
      }) {
        let selectedStyle = dependencyValues.selectedStyle;
        let textColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          textColorWord = selectedStyle.textColorWordDarkMode;
        } else {
          textColorWord = selectedStyle.textColorWord;
        }
        return { setValue: { textColor: textColorWord } };
      }
    },
    backgroundColor: {
      description: "Human-readable name for this component's background color, derived from the active style and theme.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({
        dependencyValues
      }) {
        let selectedStyle = dependencyValues.selectedStyle;
        let backgroundColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          backgroundColorWord = selectedStyle.backgroundColorWordDarkMode;
        } else {
          backgroundColorWord = selectedStyle.backgroundColorWord;
        }
        if (!backgroundColorWord) {
          backgroundColorWord = "none";
        }
        return { setValue: { backgroundColor: backgroundColorWord } };
      }
    },
    textStyleDescription: {
      description: "Human-readable description of this component's text styling (color and any background color).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        textColor: {
          dependencyType: "stateVariable",
          variableName: "textColor"
        },
        backgroundColor: {
          dependencyType: "stateVariable",
          variableName: "backgroundColor"
        }
      }),
      definition: function({
        dependencyValues
      }) {
        let textStyleDescription = dependencyValues.textColor;
        if (dependencyValues.backgroundColor !== "none") {
          textStyleDescription += ` with a ${dependencyValues.backgroundColor} background`;
        }
        return { setValue: { textStyleDescription } };
      }
    }
  };
}
function printDoenetMLrange(position) {
  if (position.start.line === position.end.line) {
    return `line ${position.start.line}`;
  } else {
    return `lines ${position.start.line}–${position.end.line}`;
  }
}
function getLineCharRange(position, allNewlines) {
  let { begin, end } = getBeginEndFromDoenetMLRange(position);
  if (begin === void 0 || end === void 0) {
    return {};
  }
  let { line: lineBegin, character: charBegin } = findLineCharInfo(
    begin,
    allNewlines
  );
  let { line: lineEnd, character: charEnd } = findLineCharInfo(
    end,
    allNewlines
  );
  return { lineBegin, charBegin, lineEnd, charEnd };
}
function getBeginEndFromDoenetMLRange(position) {
  let begin, end;
  if (position) {
    if (position.begin !== void 0) {
      begin = position.begin;
      end = position.end;
    } else if (position.selfCloseBegin !== void 0) {
      begin = position.selfCloseBegin;
      end = position.selfCloseEnd;
    } else if (position.openBegin !== void 0) {
      begin = position.openBegin;
      end = position.closeEnd;
    }
    position.begin = begin;
    position.end = end;
  }
  return { begin, end };
}
function findAllNewlines(inText) {
  let allNewlines = [];
  for (let i3 = 0; i3 < inText.length; i3++) {
    if (inText[i3] == "\n") {
      allNewlines.push(i3 + 1);
    }
  }
  return allNewlines;
}
function findLineCharInfo(pos, allNewlines) {
  for (let i3 = 0; i3 < allNewlines.length; i3++) {
    if (pos <= allNewlines[i3]) {
      if (i3 === 0) {
        return { line: 1, character: pos };
      } else {
        return { line: i3 + 1, character: pos - allNewlines[i3 - 1] };
      }
    }
  }
  return {
    line: allNewlines.length + 1,
    character: pos - (allNewlines[allNewlines.length - 1] || 0)
  };
}
function assignDoenetMLRange(components, position, sourceDoc, init = true) {
  for (let comp of components) {
    if (typeof comp === "object") {
      if (!comp.position || init) {
        comp.position = position;
        comp.sourceDoc = sourceDoc;
      }
      if (comp.children) {
        assignDoenetMLRange(comp.children, position, sourceDoc, false);
      }
      if (comp.attributes) {
        for (let attrName in comp.attributes) {
          let attrComp = comp.attributes[attrName].component;
          if (attrComp) {
            assignDoenetMLRange(
              [attrComp],
              position,
              sourceDoc,
              false
            );
          }
        }
      }
    }
  }
}
var base32$1 = { exports: {} };
var hasRequiredBase32;
function requireBase32() {
  if (hasRequiredBase32) return base32$1.exports;
  hasRequiredBase32 = 1;
  (function(module) {
    (function() {
      var root = typeof window === "object" ? window : {};
      var NODE_JS = !root.HI_BASE32_NO_NODE_JS && typeof process === "object" && process.versions && process.versions.node;
      if (NODE_JS) {
        root = commonjsGlobal;
      }
      var COMMON_JS = !root.HI_BASE32_NO_COMMON_JS && true && module.exports;
      var BASE32_ENCODE_CHAR = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567".split("");
      var BASE32_DECODE_CHAR = {
        "A": 0,
        "B": 1,
        "C": 2,
        "D": 3,
        "E": 4,
        "F": 5,
        "G": 6,
        "H": 7,
        "I": 8,
        "J": 9,
        "K": 10,
        "L": 11,
        "M": 12,
        "N": 13,
        "O": 14,
        "P": 15,
        "Q": 16,
        "R": 17,
        "S": 18,
        "T": 19,
        "U": 20,
        "V": 21,
        "W": 22,
        "X": 23,
        "Y": 24,
        "Z": 25,
        "2": 26,
        "3": 27,
        "4": 28,
        "5": 29,
        "6": 30,
        "7": 31
      };
      var blocks = [0, 0, 0, 0, 0, 0, 0, 0];
      var throwInvalidUtf8 = function(position, partial) {
        if (partial.length > 10) {
          partial = "..." + partial.substr(-10);
        }
        var err = new Error("Decoded data is not valid UTF-8. Maybe try base32.decode.asBytes()? Partial data after reading " + position + " bytes: " + partial + " <-");
        err.position = position;
        throw err;
      };
      var toUtf8String = function(bytes) {
        var str = "", length = bytes.length, i3 = 0, followingChars = 0, b2, c2;
        while (i3 < length) {
          b2 = bytes[i3++];
          if (b2 <= 127) {
            str += String.fromCharCode(b2);
            continue;
          } else if (b2 > 191 && b2 <= 223) {
            c2 = b2 & 31;
            followingChars = 1;
          } else if (b2 <= 239) {
            c2 = b2 & 15;
            followingChars = 2;
          } else if (b2 <= 247) {
            c2 = b2 & 7;
            followingChars = 3;
          } else {
            throwInvalidUtf8(i3, str);
          }
          for (var j2 = 0; j2 < followingChars; ++j2) {
            b2 = bytes[i3++];
            if (b2 < 128 || b2 > 191) {
              throwInvalidUtf8(i3, str);
            }
            c2 <<= 6;
            c2 += b2 & 63;
          }
          if (c2 >= 55296 && c2 <= 57343) {
            throwInvalidUtf8(i3, str);
          }
          if (c2 > 1114111) {
            throwInvalidUtf8(i3, str);
          }
          if (c2 <= 65535) {
            str += String.fromCharCode(c2);
          } else {
            c2 -= 65536;
            str += String.fromCharCode((c2 >> 10) + 55296);
            str += String.fromCharCode((c2 & 1023) + 56320);
          }
        }
        return str;
      };
      var decodeAsBytes = function(base32Str) {
        if (base32Str === "") {
          return [];
        } else if (!/^[A-Z2-7=]+$/.test(base32Str)) {
          throw new Error("Invalid base32 characters");
        }
        base32Str = base32Str.replace(/=/g, "");
        var v1, v2, v3, v4, v5, v6, v7, v8, bytes = [], index2 = 0, length = base32Str.length;
        for (var i3 = 0, count = length >> 3 << 3; i3 < count; ) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v8 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index2++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index2++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
          bytes[index2++] = (v4 << 4 | v5 >>> 1) & 255;
          bytes[index2++] = (v5 << 7 | v6 << 2 | v7 >>> 3) & 255;
          bytes[index2++] = (v7 << 5 | v8) & 255;
        }
        var remain = length - count;
        if (remain === 2) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index2++] = (v1 << 3 | v2 >>> 2) & 255;
        } else if (remain === 4) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index2++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index2++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
        } else if (remain === 5) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index2++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index2++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
          bytes[index2++] = (v4 << 4 | v5 >>> 1) & 255;
        } else if (remain === 7) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          bytes[index2++] = (v1 << 3 | v2 >>> 2) & 255;
          bytes[index2++] = (v2 << 6 | v3 << 1 | v4 >>> 4) & 255;
          bytes[index2++] = (v4 << 4 | v5 >>> 1) & 255;
          bytes[index2++] = (v5 << 7 | v6 << 2 | v7 >>> 3) & 255;
        }
        return bytes;
      };
      var encodeAscii = function(str) {
        var v1, v2, v3, v4, v5, base32Str = "", length = str.length;
        for (var i3 = 0, count = parseInt(length / 5) * 5; i3 < count; ) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3++);
          v3 = str.charCodeAt(i3++);
          v4 = str.charCodeAt(i3++);
          v5 = str.charCodeAt(i3++);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[(v4 << 3 | v5 >>> 5) & 31] + BASE32_ENCODE_CHAR[v5 & 31];
        }
        var remain = length - count;
        if (remain === 1) {
          v1 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[v1 << 2 & 31] + "======";
        } else if (remain === 2) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[v2 << 4 & 31] + "====";
        } else if (remain === 3) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3++);
          v3 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[v3 << 1 & 31] + "===";
        } else if (remain === 4) {
          v1 = str.charCodeAt(i3++);
          v2 = str.charCodeAt(i3++);
          v3 = str.charCodeAt(i3++);
          v4 = str.charCodeAt(i3);
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[v4 << 3 & 31] + "=";
        }
        return base32Str;
      };
      var encodeUtf8 = function(str) {
        var v1, v2, v3, v4, v5, code, end = false, base32Str = "", index2 = 0, i3, start = 0, length = str.length;
        if (str === "") {
          return base32Str;
        }
        do {
          blocks[0] = blocks[5];
          blocks[1] = blocks[6];
          blocks[2] = blocks[7];
          for (i3 = start; index2 < length && i3 < 5; ++index2) {
            code = str.charCodeAt(index2);
            if (code < 128) {
              blocks[i3++] = code;
            } else if (code < 2048) {
              blocks[i3++] = 192 | code >> 6;
              blocks[i3++] = 128 | code & 63;
            } else if (code < 55296 || code >= 57344) {
              blocks[i3++] = 224 | code >> 12;
              blocks[i3++] = 128 | code >> 6 & 63;
              blocks[i3++] = 128 | code & 63;
            } else {
              code = 65536 + ((code & 1023) << 10 | str.charCodeAt(++index2) & 1023);
              blocks[i3++] = 240 | code >> 18;
              blocks[i3++] = 128 | code >> 12 & 63;
              blocks[i3++] = 128 | code >> 6 & 63;
              blocks[i3++] = 128 | code & 63;
            }
          }
          start = i3 - 5;
          if (index2 === length) {
            ++index2;
          }
          if (index2 > length && i3 < 6) {
            end = true;
          }
          v1 = blocks[0];
          if (i3 > 4) {
            v2 = blocks[1];
            v3 = blocks[2];
            v4 = blocks[3];
            v5 = blocks[4];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[(v4 << 3 | v5 >>> 5) & 31] + BASE32_ENCODE_CHAR[v5 & 31];
          } else if (i3 === 1) {
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[v1 << 2 & 31] + "======";
          } else if (i3 === 2) {
            v2 = blocks[1];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[v2 << 4 & 31] + "====";
          } else if (i3 === 3) {
            v2 = blocks[1];
            v3 = blocks[2];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[v3 << 1 & 31] + "===";
          } else {
            v2 = blocks[1];
            v3 = blocks[2];
            v4 = blocks[3];
            base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[v4 << 3 & 31] + "=";
          }
        } while (!end);
        return base32Str;
      };
      var encodeBytes = function(bytes) {
        var v1, v2, v3, v4, v5, base32Str = "", length = bytes.length;
        for (var i3 = 0, count = parseInt(length / 5) * 5; i3 < count; ) {
          v1 = bytes[i3++];
          v2 = bytes[i3++];
          v3 = bytes[i3++];
          v4 = bytes[i3++];
          v5 = bytes[i3++];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[(v4 << 3 | v5 >>> 5) & 31] + BASE32_ENCODE_CHAR[v5 & 31];
        }
        var remain = length - count;
        if (remain === 1) {
          v1 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[v1 << 2 & 31] + "======";
        } else if (remain === 2) {
          v1 = bytes[i3++];
          v2 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[v2 << 4 & 31] + "====";
        } else if (remain === 3) {
          v1 = bytes[i3++];
          v2 = bytes[i3++];
          v3 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[v3 << 1 & 31] + "===";
        } else if (remain === 4) {
          v1 = bytes[i3++];
          v2 = bytes[i3++];
          v3 = bytes[i3++];
          v4 = bytes[i3];
          base32Str += BASE32_ENCODE_CHAR[v1 >>> 3] + BASE32_ENCODE_CHAR[(v1 << 2 | v2 >>> 6) & 31] + BASE32_ENCODE_CHAR[v2 >>> 1 & 31] + BASE32_ENCODE_CHAR[(v2 << 4 | v3 >>> 4) & 31] + BASE32_ENCODE_CHAR[(v3 << 1 | v4 >>> 7) & 31] + BASE32_ENCODE_CHAR[v4 >>> 2 & 31] + BASE32_ENCODE_CHAR[v4 << 3 & 31] + "=";
        }
        return base32Str;
      };
      var encode = function(input, asciiOnly) {
        var notString = typeof input !== "string";
        if (notString && input.constructor === ArrayBuffer) {
          input = new Uint8Array(input);
        }
        if (notString) {
          return encodeBytes(input);
        } else if (asciiOnly) {
          return encodeAscii(input);
        } else {
          return encodeUtf8(input);
        }
      };
      var decode = function(base32Str, asciiOnly) {
        if (!asciiOnly) {
          return toUtf8String(decodeAsBytes(base32Str));
        }
        if (base32Str === "") {
          return "";
        } else if (!/^[A-Z2-7=]+$/.test(base32Str)) {
          throw new Error("Invalid base32 characters");
        }
        var v1, v2, v3, v4, v5, v6, v7, v8, str = "", length = base32Str.indexOf("=");
        if (length === -1) {
          length = base32Str.length;
        }
        for (var i3 = 0, count = length >> 3 << 3; i3 < count; ) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v8 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255) + String.fromCharCode((v4 << 4 | v5 >>> 1) & 255) + String.fromCharCode((v5 << 7 | v6 << 2 | v7 >>> 3) & 255) + String.fromCharCode((v7 << 5 | v8) & 255);
        }
        var remain = length - count;
        if (remain === 2) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255);
        } else if (remain === 4) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255);
        } else if (remain === 5) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255) + String.fromCharCode((v4 << 4 | v5 >>> 1) & 255);
        } else if (remain === 7) {
          v1 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v2 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v3 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v4 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v5 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v6 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          v7 = BASE32_DECODE_CHAR[base32Str.charAt(i3++)];
          str += String.fromCharCode((v1 << 3 | v2 >>> 2) & 255) + String.fromCharCode((v2 << 6 | v3 << 1 | v4 >>> 4) & 255) + String.fromCharCode((v4 << 4 | v5 >>> 1) & 255) + String.fromCharCode((v5 << 7 | v6 << 2 | v7 >>> 3) & 255);
        }
        return str;
      };
      var exports = {
        encode,
        decode
      };
      decode.asBytes = decodeAsBytes;
      if (COMMON_JS) {
        module.exports = exports;
      } else {
        root.base32 = exports;
      }
    })();
  })(base32$1);
  return base32$1.exports;
}
var base32Exports = requireBase32();
const base32 = /* @__PURE__ */ getDefaultExportFromCjs(base32Exports);
async function cidFromText(text) {
  let encoder = new TextEncoder();
  let data = encoder.encode(text);
  return await cidFromArrayBuffer(data);
}
let digest = null;
async function cidFromArrayBuffer(data) {
  if (!digest) {
    try {
      await crypto.subtle.digest("SHA-256", new Uint8Array());
      digest = (data2) => crypto.subtle.digest("SHA-256", data2);
    } catch (e22) {
      const sha256 = (await import("./sha256-C0vj3HDn-CHRxYe9s.js").then((n2) => n2.s)).default;
      digest = (data2) => sha256(data2, {
        asBytes: true
      });
    }
  }
  let hashBuffer = await digest(data);
  let cidArray = new Uint8Array(36);
  cidArray.set([1, 85, 18, 32]);
  cidArray.set(new Uint8Array(hashBuffer), 4);
  let cid = "b" + base32.encode(cidArray).toLowerCase().replace(/=+/, "");
  return cid;
}
const textByCid = {};
function retrieveTextFileForCid(cid, ext = "doenet") {
  if (textByCid[cid] !== void 0) {
    return Promise.resolve(textByCid[cid]);
  }
  let resultServer = retrieveTextFileFromServer(cid, ext);
  return resultServer.promise.then((res) => {
    textByCid[cid] = res;
    return res;
  });
}
function retrieveTextFileFromServer(cid, ext) {
  let controller = new AbortController();
  let signal = controller.signal;
  let retrieveFromServer = async function() {
    try {
      let response = await fetch(`/media/${cid}.${ext}`, { signal });
      if (response.ok) {
        let doenetML = await response.text();
        let CidRetrieved = await cidFromText(doenetML);
        if (CidRetrieved === cid) {
          return doenetML;
        } else {
          console.warn(`cid mismatch, ${cid}, ${CidRetrieved}`);
          return Promise.reject(new Error("cid mismatch"));
        }
      } else {
        return Promise.reject(new Error(`cid not found: ${cid}`));
      }
    } catch (e22) {
      return Promise.reject(new Error(`cid not found: ${cid}`));
    }
  };
  let promise = retrieveFromServer();
  return { promise, controller };
}
function isSubset(obj) {
  return obj instanceof Constructors.InvalidSet || obj instanceof Constructors.EmptySet || obj instanceof Constructors.RealLine || obj instanceof Constructors.Singleton || obj instanceof Constructors.OpenInterval || obj instanceof Constructors.Union;
}
const genericMethods = {
  union: function(A2, B2) {
    return A2.complement().intersect(B2.complement()).complement();
  },
  intersectWithOpenInterval: function(A2, B2) {
    return A2.intersect(B2);
  },
  setMinus: function(A2, B2) {
    return A2.intersect(B2.complement());
  },
  symmetricDifference: function(A2, B2) {
    return A2.setMinus(B2).union(B2.setMinus(A2));
  },
  containsSubset: function(A2, B2) {
    return A2.intersect(B2).equals(B2);
  },
  isSubsetOf: function(A2, B2) {
    return genericMethods.containsSubset(B2, A2);
  },
  equals: function(A2, B2) {
    return A2.symmetricDifference(B2).isEmpty();
  }
};
function subsetReviver(_key, value) {
  if (typeof value === "object" && value !== null && "objectType" in value && value.objectType === "subset" && "data" in value && typeof value.data === "object" && value.data !== null && "type" in value.data && value.data.type !== void 0) {
    if (value.data.type === "empty") {
      return EmptySet();
    } else if (value.data.type === "realLine") {
      return RealLine();
    } else if (value.data.type === "singleton") {
      if ("element" in value.data && typeof value.data.element === "number") {
        return Singleton(value.data.element);
      }
    } else if (value.data.type === "union") {
      if ("subsets" in value.data && Array.isArray(value.data.subsets) && value.data.subsets.every(
        (s2) => s2 instanceof Constructors.Singleton || s2 instanceof Constructors.OpenInterval
      )) {
        return Union(value.data.subsets);
      }
    } else if (value.data.type === "openInterval") {
      if ("left" in value.data && typeof value.data.left === "number" && "right" in value.data && typeof value.data.right === "number") {
        return OpenInterval(value.data.left, value.data.right);
      }
    }
  }
  return value;
}
const Constructors = {
  EmptySet: class {
    constructor() {
      this.type = "empty";
    }
    union(that) {
      return that;
    }
    intersectWithOpenInterval(that) {
      return genericMethods.intersectWithOpenInterval(this, that);
    }
    setMinus(that) {
      return genericMethods.setMinus(this, that);
    }
    symmetricDifference(that) {
      return genericMethods.symmetricDifference(this, that);
    }
    containsSubset(that) {
      return genericMethods.containsSubset(this, that);
    }
    isSubsetOf(that) {
      return genericMethods.isSubsetOf(this, that);
    }
    equals(that) {
      return genericMethods.equals(this, that);
    }
    intersect() {
      return EmptySet();
    }
    containsElement() {
      return false;
    }
    isEmpty() {
      return true;
    }
    complement() {
      return RealLine();
    }
    isValid() {
      return true;
    }
    toString() {
      return "∅";
    }
    toMathExpression() {
      return me$3.fromAst("emptyset");
    }
    toJSON() {
      return {
        objectType: "subset",
        data: { type: "empty" }
      };
    }
    copy() {
      return subsetReviver(null, this.toJSON());
    }
  },
  InvalidSet: class {
    constructor() {
      this.type = "invalid";
    }
    intersectWithOpenInterval(that) {
      return genericMethods.intersectWithOpenInterval(this, that);
    }
    setMinus(that) {
      return genericMethods.setMinus(this, that);
    }
    symmetricDifference(that) {
      return genericMethods.symmetricDifference(this, that);
    }
    containsSubset(that) {
      return genericMethods.containsSubset(this, that);
    }
    isSubsetOf(that) {
      return genericMethods.isSubsetOf(this, that);
    }
    equals(that) {
      return genericMethods.equals(this, that);
    }
    union() {
      return InvalidSet();
    }
    intersect() {
      return InvalidSet();
    }
    containsElement() {
      return false;
    }
    isEmpty() {
      return true;
    }
    complement() {
      return InvalidSet();
    }
    isValid() {
      return false;
    }
    toString() {
      return "＿";
    }
    toMathExpression() {
      return me$3.fromAst("＿");
    }
    toJSON() {
      return {
        objectType: "subset",
        data: { type: "invalid" }
      };
    }
    copy() {
      return subsetReviver(null, this.toJSON());
    }
  },
  RealLine: class {
    constructor() {
      this.type = "realLine";
    }
    intersectWithOpenInterval(that) {
      return genericMethods.intersectWithOpenInterval(this, that);
    }
    setMinus(that) {
      return genericMethods.setMinus(this, that);
    }
    symmetricDifference(that) {
      return genericMethods.symmetricDifference(this, that);
    }
    containsSubset(that) {
      return genericMethods.containsSubset(this, that);
    }
    isSubsetOf(that) {
      return genericMethods.isSubsetOf(this, that);
    }
    equals(that) {
      return genericMethods.equals(this, that);
    }
    isValid() {
      return true;
    }
    union() {
      return RealLine();
    }
    intersect(that) {
      return that;
    }
    containsElement() {
      return true;
    }
    complement() {
      return EmptySet();
    }
    isEmpty() {
      return false;
    }
    toString() {
      return "ℝ";
    }
    toMathExpression() {
      return me$3.fromAst("R");
    }
    toJSON() {
      return {
        objectType: "subset",
        data: { type: "realLine" }
      };
    }
    copy() {
      return subsetReviver(null, this.toJSON());
    }
  },
  Singleton: class {
    constructor(element) {
      this.type = "singleton";
      this.element = element;
    }
    intersectWithOpenInterval(that) {
      return genericMethods.intersectWithOpenInterval(this, that);
    }
    setMinus(that) {
      return genericMethods.setMinus(this, that);
    }
    symmetricDifference(that) {
      return genericMethods.symmetricDifference(this, that);
    }
    containsSubset(that) {
      return genericMethods.containsSubset(this, that);
    }
    isSubsetOf(that) {
      return genericMethods.isSubsetOf(this, that);
    }
    equals(that) {
      return genericMethods.equals(this, that);
    }
    isValid() {
      return true;
    }
    union(that) {
      if (that.containsElement(this.element)) {
        return that;
      } else {
        return Union([that, this]);
      }
    }
    intersect(subset2) {
      if (subset2.containsElement(this.element)) {
        return Singleton(this.element);
      } else {
        return EmptySet();
      }
    }
    isEmpty() {
      return false;
    }
    containsElement(element) {
      return element === this.element;
    }
    complement() {
      return Union([
        OpenInterval(-Infinity, this.element),
        OpenInterval(this.element, Infinity)
      ]);
    }
    toString() {
      return `{${this.element}}`;
    }
    toMathExpression() {
      return me$3.fromAst(["set", this.element]);
    }
    toJSON() {
      return {
        objectType: "subset",
        data: { type: "singleton", element: this.element }
      };
    }
    copy() {
      return subsetReviver(null, this.toJSON());
    }
  },
  OpenInterval: class {
    constructor(left, right) {
      this.type = "openInterval";
      this.left = left;
      this.right = right;
    }
    union(that) {
      return genericMethods.union(this, that);
    }
    setMinus(that) {
      return genericMethods.setMinus(this, that);
    }
    symmetricDifference(that) {
      return genericMethods.symmetricDifference(this, that);
    }
    containsSubset(that) {
      return genericMethods.containsSubset(this, that);
    }
    isSubsetOf(that) {
      return genericMethods.isSubsetOf(this, that);
    }
    equals(that) {
      return genericMethods.equals(this, that);
    }
    isValid() {
      return true;
    }
    intersect(subset2) {
      return subset2.intersectWithOpenInterval(this);
    }
    intersectWithOpenInterval(that) {
      return OpenInterval(
        Math.max(this.left, that.left),
        Math.min(this.right, that.right)
      );
    }
    complement() {
      return Union([
        OpenClosedInterval(-Infinity, this.left),
        ClosedOpenInterval(this.right, Infinity)
      ]);
    }
    isEmpty() {
      return this.left >= this.right;
    }
    containsElement(element) {
      return element > this.left && element < this.right;
    }
    toString() {
      return `(${this.left.toString()},${this.right.toString()})`;
    }
    toMathExpression() {
      return me$3.fromAst([
        "interval",
        ["tuple", this.left, this.right],
        ["tuple", false, false]
      ]);
    }
    toJSON() {
      return {
        objectType: "subset",
        data: {
          type: "openInterval",
          left: this.left,
          right: this.right
        }
      };
    }
    copy() {
      return subsetReviver(null, this.toJSON());
    }
  },
  Union: class {
    constructor(subsets) {
      this.type = "union";
      this.subsets = subsets;
    }
    union(that) {
      return genericMethods.union(this, that);
    }
    setMinus(that) {
      return genericMethods.setMinus(this, that);
    }
    symmetricDifference(that) {
      return genericMethods.symmetricDifference(this, that);
    }
    containsSubset(that) {
      return genericMethods.containsSubset(this, that);
    }
    isSubsetOf(that) {
      return genericMethods.isSubsetOf(this, that);
    }
    equals(that) {
      return genericMethods.equals(this, that);
    }
    isValid() {
      return true;
    }
    intersectWithOpenInterval(that) {
      return this.intersect(that);
    }
    containsElement(element) {
      return this.subsets.some((s2) => s2.containsElement(element));
    }
    isEmpty() {
      return this.subsets.every((s2) => s2.isEmpty());
    }
    complement() {
      return this.subsets.map((s2) => s2.complement()).reduce((a2, b2) => a2.intersect(b2));
    }
    intersect(subset2) {
      return Union(this.subsets.map((s2) => subset2.intersect(s2)));
    }
    toString() {
      return this.subsets.map((s2) => s2.toString()).join(" U ");
    }
    toMathExpression() {
      return me$3.fromAst([
        "union",
        ...this.subsets.map((s2) => s2.toMathExpression().tree)
      ]);
    }
    toJSON() {
      return {
        objectType: "subset",
        data: {
          type: "union",
          subsets: this.subsets.map((s2) => s2.toJSON())
        }
      };
    }
    copy() {
      return subsetReviver(null, this.toJSON());
    }
  }
};
function EmptySet() {
  return new Constructors.EmptySet();
}
function InvalidSet() {
  return new Constructors.InvalidSet();
}
function RealLine() {
  return new Constructors.RealLine();
}
function Singleton(element) {
  if (!Number.isFinite(element)) {
    return new Constructors.EmptySet();
  }
  return new Constructors.Singleton(element);
}
function OpenInterval(left, right) {
  if (!(left < right)) {
    return new Constructors.EmptySet();
  } else if (left === -Infinity && right === Infinity) {
    return new Constructors.RealLine();
  } else {
    return new Constructors.OpenInterval(left, right);
  }
}
function Union(subsets) {
  if (subsets.some((s2) => s2.type === "invalid")) {
    return new Constructors.InvalidSet();
  }
  const flattenedSubsets = subsets.reduce(
    (acc, val) => val.type === "union" ? [...acc, ...val.subsets] : [...acc, val],
    []
  );
  const filtered = [];
  for (const sub2 of flattenedSubsets) {
    if (sub2.type === "realLine") {
      return new Constructors.RealLine();
    } else if (sub2.type !== "empty" && !sub2.isEmpty()) {
      filtered.push(sub2);
    }
  }
  if (filtered.length === 0) {
    return new Constructors.EmptySet();
  }
  const newSubsets = [];
  for (let ind1 = 0; ind1 < filtered.length; ind1++) {
    let sub1 = filtered[ind1];
    let addSub1 = true;
    if (sub1.type === "openInterval") {
      let left = sub1.left;
      let right = sub1.right;
      for (let ind2 = ind1 + 1; ind2 < filtered.length; ind2++) {
        let sub2 = filtered[ind2];
        if (sub2.type === "openInterval") {
          if (left < sub2.right && sub2.left < right) {
            left = Math.min(left, sub2.left);
            right = Math.max(right, sub2.right);
            filtered.splice(ind2, 1);
            ind2--;
            addSub1 = false;
            ind1--;
            break;
          } else if (left === sub2.right || right === sub2.left) {
            let gap = left === sub2.right ? left : right;
            let foundSingleton = false;
            for (let ind3 = 0; ind3 < newSubsets.length; ind3++) {
              let sub3 = newSubsets[ind3];
              if (sub3.type === "singleton" && sub3.element === gap) {
                newSubsets.splice(ind3, 1);
                foundSingleton = true;
                break;
              }
            }
            if (!foundSingleton) {
              for (let ind3 = ind1 + 1; ind3 < filtered.length; ind3++) {
                let sub3 = filtered[ind3];
                if (sub3.type === "singleton" && sub3.element === gap) {
                  filtered.splice(ind3, 1);
                  foundSingleton = true;
                  if (ind3 < ind2) {
                    ind2--;
                  }
                  break;
                }
              }
            }
            if (foundSingleton) {
              left = Math.min(left, sub2.left);
              right = Math.max(right, sub2.right);
              filtered.splice(ind2, 1);
              ind2--;
              addSub1 = false;
              ind1--;
              break;
            }
          }
        } else {
          if (sub2.element > left && sub2.element < right) {
            filtered.splice(ind2, 1);
            ind2--;
          }
        }
      }
      sub1.left = left;
      sub1.right = right;
      if (sub1.left === -Infinity && sub1.right === Infinity) {
        return new Constructors.RealLine();
      }
    } else {
      let val = sub1.element;
      for (let ind2 = ind1 + 1; ind2 < filtered.length; ind2++) {
        let sub2 = filtered[ind2];
        if (sub2.type === "openInterval") {
          if (val > sub2.left && val < sub2.right) {
            filtered.splice(ind1, 1);
            ind1--;
            addSub1 = false;
            break;
          }
        } else if (sub2.element === val) {
          filtered.splice(ind2, 1);
          ind2--;
        }
      }
    }
    if (addSub1) {
      newSubsets.push(sub1);
    }
  }
  if (newSubsets.length === 1) {
    return newSubsets[0];
  }
  return new Constructors.Union(newSubsets);
}
function ClosedInterval(left, right) {
  if (!(left <= right)) {
    return new Constructors.EmptySet();
  } else if (left === -Infinity && right === Infinity) {
    return new Constructors.RealLine();
  } else {
    const pieces = [
      new Constructors.OpenInterval(left, right)
    ];
    if (Number.isFinite(left)) {
      pieces.push(new Constructors.Singleton(left));
    }
    if (Number.isFinite(right)) {
      pieces.push(new Constructors.Singleton(right));
    }
    return Union(pieces);
  }
}
function OpenClosedInterval(left, right) {
  if (!(left < right)) {
    return new Constructors.EmptySet();
  } else if (left === -Infinity && right === Infinity) {
    return new Constructors.RealLine();
  } else {
    const pieces = [
      new Constructors.OpenInterval(left, right)
    ];
    if (Number.isFinite(right)) {
      pieces.push(new Constructors.Singleton(right));
    }
    return Union(pieces);
  }
}
function ClosedOpenInterval(left, right) {
  if (!(left < right)) {
    return new Constructors.EmptySet();
  } else if (left === -Infinity && right === Infinity) {
    return new Constructors.RealLine();
  } else {
    const pieces = [
      new Constructors.OpenInterval(left, right)
    ];
    if (Number.isFinite(left)) {
      pieces.push(new Constructors.Singleton(left));
    }
    return Union(pieces);
  }
}
const subsetOfReals = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ClosedInterval,
  ClosedOpenInterval,
  EmptySet,
  InvalidSet,
  OpenClosedInterval,
  OpenInterval,
  RealLine,
  Singleton,
  Union,
  isSubset,
  subsetReviver
}, Symbol.toStringTag, { value: "Module" }));
function deepClone(obj, BaseComponent2, hash) {
  if (Object(obj) !== obj || obj instanceof Function) return obj;
  if (obj instanceof me$3.class || isSubset(obj)) {
    return obj.copy();
  }
  if (hash === void 0) {
    hash = /* @__PURE__ */ new WeakMap();
  }
  if (Array.isArray(obj)) {
    return [...obj.map((x2) => deepClone(x2, BaseComponent2, hash))];
  }
  if (obj.constructor === Object && Object.entries(obj).length === 0) {
    return {};
  }
  if (hash.has(obj)) return hash.get(obj);
  let result;
  try {
    result = new obj.constructor();
  } catch (e22) {
    result = Object.create(Object.getPrototypeOf(obj));
  }
  if (obj instanceof Map) {
    Array.from(
      obj,
      ([key, val]) => result.set(
        deepClone(key, BaseComponent2, hash),
        deepClone(val, BaseComponent2, hash)
      )
    );
  } else if (obj instanceof Set) {
    Array.from(
      obj,
      (key) => result.add(deepClone(key, BaseComponent2, hash))
    );
  }
  hash.set(obj, result);
  return Object.assign(
    result,
    ...Object.keys(obj).map((key) => ({
      [key]: deepClone(obj[key], BaseComponent2, hash)
    }))
  );
}
function deepCompare(a2, b2, BaseComponent2) {
  let leftChain = [];
  let rightChain = [];
  function compare2Objects(x2, y2) {
    let p2;
    if (Number.isNaN(x2) && Number.isNaN(y2)) {
      return true;
    }
    if (x2 === y2) {
      return true;
    }
    if (x2 instanceof me$3.class && y2 instanceof me$3.class) {
      return compare2Objects(x2.tree, y2.tree);
    }
    if (typeof x2 === "function" && typeof y2 === "function" || x2 instanceof Date && y2 instanceof Date || x2 instanceof RegExp && y2 instanceof RegExp || x2 instanceof String && y2 instanceof String || x2 instanceof Number && y2 instanceof Number) {
      return x2.toString() === y2.toString();
    }
    if (!(x2 instanceof Object && y2 instanceof Object)) {
      return false;
    }
    if (x2.isPrototypeOf(y2) || y2.isPrototypeOf(x2)) {
      return false;
    }
    if (x2.constructor !== y2.constructor) {
      return false;
    }
    if (x2.prototype !== y2.prototype) {
      return false;
    }
    if (leftChain.indexOf(x2) > -1 || rightChain.indexOf(y2) > -1) {
      return false;
    }
    for (p2 in y2) {
      if (y2.hasOwnProperty(p2) !== x2.hasOwnProperty(p2)) {
        return false;
      } else if (typeof y2[p2] !== typeof x2[p2]) {
        return false;
      }
    }
    for (p2 in x2) {
      if (y2.hasOwnProperty(p2) !== x2.hasOwnProperty(p2)) {
        return false;
      } else if (typeof y2[p2] !== typeof x2[p2]) {
        return false;
      }
      switch (typeof x2[p2]) {
        case "object":
        case "function":
          leftChain.push(x2);
          rightChain.push(y2);
          if (!compare2Objects(x2[p2], y2[p2])) {
            return false;
          }
          leftChain.pop();
          rightChain.pop();
          break;
        default:
          if (x2[p2] !== y2[p2] && !(Number.isNaN(x2[p2]) && Number.isNaN(y2[p2]))) {
            return false;
          }
          break;
      }
    }
    return true;
  }
  return compare2Objects(a2, b2);
}
function serializedComponentsReplacer(key, value) {
  if (value !== value) {
    return { objectType: "special-numeric", stringValue: "NaN" };
  } else if (value === Infinity) {
    return { objectType: "special-numeric", stringValue: "Infinity" };
  } else if (value === -Infinity) {
    return { objectType: "special-numeric", stringValue: "-Infinity" };
  }
  return value;
}
function nanInfinityReviver(key, value) {
  if (value && value.objectType === "special-numeric") {
    if (value.stringValue === "NaN") {
      return NaN;
    } else if (value.stringValue === "Infinity") {
      return Infinity;
    } else if (value.stringValue === "-Infinity") {
      return -Infinity;
    }
  }
  return value;
}
function serializedComponentsReviver(key, value) {
  return me$3.reviver(key, subsetReviver(key, nanInfinityReviver(key, value)));
}
function buildSubsetFromIntervals(tree, variable) {
  if (!Array.isArray(tree)) {
    if (Number.isFinite(tree)) {
      return Singleton(tree);
    } else if (tree === "R") {
      return RealLine();
    } else if (tree === "emptyset") {
      return EmptySet();
    } else {
      return InvalidSet();
    }
  }
  let operator = tree[0];
  if (operator === "interval") {
    let endpoints = tree[1];
    let closed = tree[2];
    let left = endpoints[1];
    if (!Number.isFinite(left)) {
      left = me$3.fromAst(left).evaluate_to_constant();
      if (!(Number.isFinite(left) || left === Infinity || left === -Infinity)) {
        return InvalidSet();
      }
    }
    let right = endpoints[2];
    if (!Number.isFinite(right)) {
      right = me$3.fromAst(right).evaluate_to_constant();
      if (!(Number.isFinite(right) || right === Infinity || right === -Infinity)) {
        return InvalidSet();
      }
    }
    if (closed[1]) {
      if (closed[2]) {
        return ClosedInterval(left, right);
      } else {
        return ClosedOpenInterval(left, right);
      }
    } else {
      if (closed[2]) {
        return OpenClosedInterval(left, right);
      } else {
        return OpenInterval(left, right);
      }
    }
  } else if (operator === "union" || operator === "or" || operator === "set" || operator === "list") {
    let pieces = tree.slice(1).map((x2) => buildSubsetFromIntervals(x2, variable)).filter((x2) => x2);
    if (pieces.length === 0) {
      return EmptySet();
    } else if (pieces.length === 1) {
      return pieces[0];
    } else {
      return Union(pieces);
    }
  } else if (operator === "intersect" || operator === "and") {
    let pieces = tree.slice(1).map((x2) => buildSubsetFromIntervals(x2, variable)).filter((x2) => x2);
    if (pieces.length === 0) {
      return RealLine();
    } else {
      return pieces.reduce((a2, c2) => a2.intersect(c2));
    }
  } else if (["<", "le", ">", "ge", "=", "ne"].includes(operator)) {
    let left = tree[1];
    let varAtLeft = false;
    if (!Number.isFinite(left)) {
      if (deepCompare(left, variable)) {
        varAtLeft = true;
      } else {
        left = me$3.fromAst(left).evaluate_to_constant();
        if (!(Number.isFinite(left) || left === Infinity || left === -Infinity)) {
          return InvalidSet();
        }
      }
    }
    let right = tree[2];
    let varAtRight = false;
    if (!Number.isFinite(right)) {
      if (deepCompare(right, variable)) {
        varAtRight = true;
      } else {
        right = me$3.fromAst(right).evaluate_to_constant();
        if (!(Number.isFinite(right) || right === Infinity || right === -Infinity)) {
          return InvalidSet();
        }
      }
    }
    if (varAtLeft) {
      if (varAtRight) {
        return InvalidSet();
      } else {
        if (operator === "<") {
          return OpenInterval(-Infinity, right);
        } else if (operator === "le") {
          return OpenClosedInterval(-Infinity, right);
        } else if (operator === ">") {
          return OpenInterval(right, Infinity);
        } else if (operator === "ge") {
          return ClosedOpenInterval(right, Infinity);
        } else if (operator === "=") {
          if (Number.isFinite(right)) {
            return Singleton(right);
          } else {
            return EmptySet();
          }
        } else {
          if (Number.isFinite(right)) {
            return Union([
              OpenInterval(-Infinity, right),
              OpenInterval(right, Infinity)
            ]);
          } else {
            return RealLine();
          }
        }
      }
    } else {
      if (varAtRight) {
        if (operator === "<") {
          return OpenInterval(left, Infinity);
        } else if (operator === "le") {
          return ClosedOpenInterval(left, Infinity);
        } else if (operator === ">") {
          return OpenInterval(-Infinity, left);
        } else if (operator === "ge") {
          return OpenClosedInterval(-Infinity, left);
        } else if (operator === "=") {
          if (Number.isFinite(left)) {
            return Singleton(left);
          } else {
            return EmptySet();
          }
        } else {
          if (Number.isFinite(left)) {
            return Union([
              OpenInterval(-Infinity, left),
              OpenInterval(left, Infinity)
            ]);
          } else {
            return RealLine();
          }
        }
      } else {
        return InvalidSet();
      }
    }
  } else if (["lts", "gts"].includes(operator)) {
    let vals = tree[1].slice(1);
    let strict = tree[2].slice(1);
    if (vals.length !== 3 || !deepCompare(vals[1], variable)) {
      return InvalidSet();
    }
    if (operator === "gts") {
      vals.reverse();
      strict.reverse();
    }
    let left = vals[0];
    if (!Number.isFinite(left)) {
      left = me$3.fromAst(left).evaluate_to_constant();
      if (!(Number.isFinite(left) || left === Infinity || left === -Infinity)) {
        return InvalidSet();
      }
    }
    let right = vals[2];
    if (!Number.isFinite(right)) {
      right = me$3.fromAst(right).evaluate_to_constant();
      if (!(Number.isFinite(right) || right === Infinity || right === -Infinity)) {
        return InvalidSet();
      }
    }
    if (strict[0]) {
      if (strict[1]) {
        return OpenInterval(left, right);
      } else {
        return OpenClosedInterval(left, right);
      }
    } else {
      if (strict[1]) {
        return ClosedOpenInterval(left, right);
      } else {
        return ClosedInterval(left, right);
      }
    }
  } else if (operator === "|") {
    let x2 = tree[1];
    return buildSubsetFromIntervals(tree[2], x2);
  } else if (operator === "^" && (tree[2] === "C" || tree[2] === "c")) {
    let orig = buildSubsetFromIntervals(tree[1], variable);
    return orig.complement();
  } else if (operator === "in") {
    if (deepCompare(tree[1], variable)) {
      return buildSubsetFromIntervals(tree[2], variable);
    } else {
      return InvalidSet();
    }
  } else if (operator === "ni") {
    if (deepCompare(tree[2], variable)) {
      return buildSubsetFromIntervals(tree[1], variable);
    } else {
      return InvalidSet();
    }
  } else if (operator === "notin") {
    if (deepCompare(tree[1], variable)) {
      let orig = buildSubsetFromIntervals(tree[2], variable);
      return orig.complement();
    }
    return InvalidSet();
  } else if (operator === "notni") {
    if (deepCompare(tree[2], variable)) {
      let orig = buildSubsetFromIntervals(tree[1], variable);
      return orig.complement();
    }
    return InvalidSet();
  } else {
    let num = me$3.fromAst(tree).evaluate_to_constant();
    if (typeof num === "number" && Number.isFinite(num)) {
      return Singleton(num);
    } else {
      return InvalidSet();
    }
  }
}
function buildSubsetFromMathExpression(expr, variable) {
  return buildSubsetFromIntervals(expr.to_intervals().tree, variable?.tree);
}
function mathExpressionFromSubsetValue({
  subsetValue,
  variable,
  displayMode = "intervals"
}) {
  function subsetToMath(subset2) {
    if (subset2 === null) {
      return "＿";
    }
    if (displayMode === "intervals") {
      if (subset2.type === "closedInterval") {
        return [
          "interval",
          ["tuple", subset2.left, subset2.right],
          ["tuple", true, true]
        ];
      } else if (subset2.type === "openClosedInterval") {
        return [
          "interval",
          ["tuple", subset2.left, subset2.right],
          ["tuple", false, true]
        ];
      } else if (subset2.type === "closedOpenInterval") {
        return [
          "interval",
          ["tuple", subset2.left, subset2.right],
          ["tuple", true, false]
        ];
      } else {
        return subset2.toMathExpression().tree;
      }
    } else {
      if (subset2.type === "closedInterval") {
        return [
          "lts",
          ["tuple", subset2.left, variable, subset2.right],
          ["tuple", false, false]
        ];
      } else if (subset2.type === "openClosedInterval") {
        if (subset2.left === -Infinity) {
          return ["le", variable, subset2.right];
        } else {
          return [
            "lts",
            ["tuple", subset2.left, variable, subset2.right],
            ["tuple", true, false]
          ];
        }
      } else if (subset2.type === "closedOpenInterval") {
        if (subset2.right === Infinity) {
          return ["ge", variable, subset2.left];
        } else {
          return [
            "lts",
            ["tuple", subset2.left, variable, subset2.right],
            ["tuple", false, true]
          ];
        }
      } else if (subset2.type === "openInterval") {
        if (subset2.left === -Infinity) {
          return ["<", variable, subset2.right];
        } else if (subset2.right === Infinity) {
          return [">", variable, subset2.left];
        } else {
          return [
            "lts",
            ["tuple", subset2.left, variable, subset2.right],
            ["tuple", true, true]
          ];
        }
      } else if (subset2.type === "singleton") {
        return ["=", variable, subset2.element];
      } else if (!subset2.isValid()) {
        return "＿";
      } else if (subset2.isEmpty()) {
        return ["in", variable, "emptyset"];
      } else if (subset2.type === "realLine") {
        return ["in", variable, "R"];
      } else {
        return "＿";
      }
    }
  }
  let expression;
  let mathSubsets = mergeIntervals(subsetValue).map((x2) => subsetToMath(x2));
  if (mathSubsets.length > 1) {
    if (displayMode === "intervals") {
      expression = me$3.fromAst(["union", ...mathSubsets]);
    } else {
      expression = me$3.fromAst(["or", ...mathSubsets]);
    }
  } else {
    expression = me$3.fromAst(mathSubsets[0]);
  }
  return expression;
}
function mergeIntervals(subsetValue) {
  if (subsetValue.type === "union") {
    let singletons = subsetValue.subsets.filter(
      (x2) => x2.type === "singleton"
    );
    let intervals = subsetValue.subsets.filter(
      (x2) => x2.type === "openInterval"
    );
    for (let ind1 = 0; ind1 < singletons.length; ind1++) {
      let x2 = singletons[ind1].element;
      for (let ind2 = 0; ind2 < intervals.length; ind2++) {
        let interval = intervals[ind2];
        if (x2 === interval.left) {
          if (interval.type === "openClosedInterval") {
            interval = {
              type: "closedInterval",
              left: interval.left,
              right: interval.right
            };
          } else {
            interval = {
              type: "closedOpenInterval",
              left: interval.left,
              right: interval.right
            };
          }
          intervals.splice(ind2, 1, interval);
          singletons.splice(ind1, 1);
          ind1--;
        } else if (x2 === interval.right) {
          if (interval.type === "closedOpenInterval") {
            interval = {
              type: "closedInterval",
              left: interval.left,
              right: interval.right
            };
          } else {
            interval = {
              type: "openClosedInterval",
              left: interval.left,
              right: interval.right
            };
          }
          intervals.splice(ind2, 1, interval);
          singletons.splice(ind1, 1);
          ind1--;
        }
      }
    }
    return [...intervals, ...singletons].sort(
      (a2, b2) => (a2.type === "singleton" ? a2.element : a2.left) - (b2.type === "singleton" ? b2.element : b2.left)
    );
  } else {
    return [subsetValue];
  }
}
function find_effective_domain({
  domain,
  truncateToFiniteDomain = false,
  xscale = 1
}) {
  let minx = -Infinity;
  let maxx = Infinity;
  let openMin = true, openMax = true;
  if (domain !== null) {
    let domain1 = domain[0];
    if (domain1 !== void 0) {
      minx = me$3.fromAst(domain1.tree[1][1]).evaluate_to_constant();
      if (!Number.isFinite(minx)) {
        minx = -Infinity;
      } else {
        openMin = !domain1.tree[2][1];
      }
      maxx = me$3.fromAst(domain1.tree[1][2]).evaluate_to_constant();
      if (!Number.isFinite(maxx)) {
        maxx = Infinity;
      } else {
        openMax = !domain1.tree[2][2];
      }
    }
  }
  if (truncateToFiniteDomain) {
    if (minx === -Infinity) {
      if (maxx === Infinity) {
        minx = -100 * xscale;
        maxx = 100 * xscale;
      } else {
        minx = maxx - 200 * xscale;
      }
    } else if (maxx === Infinity) {
      maxx = minx + 200 * xscale;
    }
  } else {
    if (minx === -Infinity) {
      openMin = false;
    }
    if (maxx === Infinity) {
      openMax = false;
    }
  }
  return { minx, maxx, openMin, openMax };
}
function find_effective_domains_piecewise_children({
  domain,
  numericalDomainsOfChildren
}) {
  let domainUnused;
  if (domain) {
    let domainMin = me$3.fromAst(domain[0].tree[1][1]).evaluate_to_constant();
    let domainMax = me$3.fromAst(domain[0].tree[1][2]).evaluate_to_constant();
    if (Number.isNaN(domainMin) || Number.isNaN(domainMax)) {
      domainUnused = RealLine();
    } else {
      domainUnused = buildSubsetFromMathExpression(domain[0]);
    }
  } else {
    domainUnused = RealLine();
  }
  let effectiveChildDomains = [];
  for (let childDomain of numericalDomainsOfChildren) {
    if (!childDomain) {
      effectiveChildDomains.push(EmptySet());
    } else {
      let childDomainMathExpr = me$3.fromAst([
        "interval",
        ["tuple", ...childDomain[0]],
        ["tuple", ...childDomain[1]]
      ]);
      let childDomainSubset = buildSubsetFromMathExpression(childDomainMathExpr);
      let childDomainToConsider = childDomainSubset.intersect(domainUnused);
      domainUnused = domainUnused.setMinus(childDomainSubset);
      effectiveChildDomains.push(childDomainToConsider);
    }
  }
  return effectiveChildDomains;
}
const { gcd: gcd$3, lcm: lcm$1 } = me$3.math;
function enumerateSelectionCombinations({
  numberOfIndices,
  numberOfOptions,
  maxNumber = Infinity,
  withReplacement = false,
  skipZero = false
  // for case when generating offsets but without replacement
}) {
  if (numberOfIndices === 1) {
    let options = [...Array(numberOfOptions).keys()];
    options = options.slice(1);
    if (!skipZero) {
      options = [...options, 0];
    }
    options = options.slice(0, maxNumber);
    let options2 = options.map((x2) => [x2]);
    return options2;
  }
  let allOffsetsCombinations = enumerateSelectionCombinations({
    numberOfIndices: numberOfIndices - 1,
    numberOfOptions,
    maxNumber,
    withReplacement,
    skipZero: !withReplacement
  });
  let results = [];
  let numberSoFar = 0;
  for (let offsets of allOffsetsCombinations) {
    for (let ind0 = 1; ind0 <= numberOfOptions; ind0++) {
      let combination2 = [
        ind0 % numberOfOptions,
        ...offsets.map((x2) => (ind0 + x2) % numberOfOptions)
      ];
      if (skipZero && combination2.includes(0)) {
        continue;
      }
      results.push(combination2);
      numberSoFar++;
      if (numberSoFar >= maxNumber) {
        return results;
      }
    }
  }
  return results;
}
function enumerateCombinations({
  numberOfOptionsByIndex,
  maxNumber = Infinity
}) {
  let numberOfIndices = numberOfOptionsByIndex.length;
  if (numberOfIndices === 0) {
    return [];
  }
  let gcds = [];
  for (let ind1 = 0; ind1 < numberOfIndices; ind1++) {
    let g2 = [];
    for (let ind2 = 0; ind2 < ind1; ind2++) {
      g2.push(
        gcd$3(numberOfOptionsByIndex[ind1], numberOfOptionsByIndex[ind2])
      );
    }
    gcds.push(g2);
  }
  let maxGCD = gcds.reduce((a2, c2) => [...c2, ...a2], []).reduce((a2, c2) => Math.max(a2, c2), 1);
  if (maxGCD === 1) {
    let totalCombinations = numberOfOptionsByIndex.reduce((a2, c2) => a2 * c2);
    totalCombinations = Math.min(totalCombinations, maxNumber);
    let results = [];
    for (let ind = 0; ind < totalCombinations; ind++) {
      let r2 = numberOfOptionsByIndex.map((x2) => ind % x2);
      results.push(r2);
    }
    return results;
  }
  if (numberOfIndices === 2) {
    const gcdValue = maxGCD;
    let computedLcm = lcm$1(...numberOfOptionsByIndex);
    let results = [];
    let numberSoFar = 0;
    for (let offset = 0; offset < gcdValue; offset++) {
      for (let ind = 0; ind < computedLcm; ind++) {
        let r2 = [
          ind % numberOfOptionsByIndex[0],
          (ind + offset) % numberOfOptionsByIndex[1]
        ];
        results.push(r2);
        numberSoFar++;
        if (numberSoFar >= maxNumber) {
          return results;
        }
      }
    }
    return results;
  }
  let lcms = [];
  for (let ind1 = 0; ind1 < numberOfIndices; ind1++) {
    let m2 = [];
    for (let ind2 = 0; ind2 < ind1; ind2++) {
      if (gcds[ind1][ind2] > 1) {
        m2.push(
          lcm$1(
            numberOfOptionsByIndex[ind1],
            numberOfOptionsByIndex[ind2]
          )
        );
      } else {
        m2.push(null);
      }
    }
    lcms.push(m2);
  }
  let minLCM = lcms.reduce((a2, c2) => [...c2, ...a2], []).filter((x2) => x2 !== null).reduce((a2, c2) => Math.min(a2, c2), Infinity);
  let matchInds = [];
  for (let ind1 = 0; ind1 < numberOfIndices; ind1++) {
    for (let ind2 = 0; ind2 < ind1; ind2++) {
      if (lcms[ind1][ind2] === minLCM) {
        matchInds = [ind2, ind1];
        break;
      }
    }
    if (matchInds.length > 0) {
      break;
    }
  }
  let n0 = numberOfOptionsByIndex[matchInds[0]];
  let n1 = numberOfOptionsByIndex[matchInds[1]];
  let innerOptions = [n0, n1];
  let outerOptions = [...numberOfOptionsByIndex];
  outerOptions[matchInds[0]] = n0 * n1;
  outerOptions.splice(matchInds[1], 1);
  let outerResult = enumerateCombinations({
    numberOfOptionsByIndex: outerOptions,
    maxNumber
  });
  let innerResult = enumerateCombinations({
    numberOfOptionsByIndex: innerOptions,
    maxNumber
  });
  for (let r2 of outerResult) {
    let ires = innerResult[r2[matchInds[0]]];
    r2.splice(matchInds[0], 1, ires[0]);
    r2.splice(matchInds[1], 0, ires[1]);
  }
  return outerResult;
}
function enumeratePermutations({
  values: values2,
  maxNumber
}) {
  let result = [];
  const permute = (arr, m2 = []) => {
    if (arr.length === 0) {
      result.push(m2);
    } else {
      for (let i3 = 0; i3 < arr.length; i3++) {
        let curr = arr.slice();
        let next = curr.splice(i3, 1);
        permute(curr.slice(), m2.concat(next));
        if (result.length >= maxNumber) {
          return;
        }
      }
    }
  };
  permute(values2);
  return result;
}
function normalizeMathExpression({
  value,
  simplify,
  expand = false,
  createVectors = false,
  createIntervals = false,
  assumptions = null
}) {
  if (assumptions && assumptions.tree !== "＿") {
    const assumptionsToAdd = [];
    if (Array.isArray(assumptions.tree) && assumptions.tree[0] === "list") {
      for (let i3 = 1; i3 < assumptions.tree.length; i3++) {
        assumptionsToAdd.push(me$3.fromAst(assumptions.tree[i3]));
      }
    } else {
      assumptionsToAdd.push(assumptions);
    }
    for (const assumption of assumptionsToAdd) {
      me$3.add_assumption(assumption);
    }
  }
  if (createVectors) {
    value = value.tuples_to_vectors();
  }
  if (createIntervals) {
    value = value.to_intervals();
  }
  if (expand) {
    value = value.expand();
  }
  if (simplify === "full") {
    value = value.simplify();
  } else if (simplify === "numbers") {
    value = value.evaluate_numbers();
  } else if (simplify === "numberspreserveorder") {
    value = value.evaluate_numbers({ skip_ordering: true });
  } else if (simplify === "normalizeorder") {
    value = value.default_order();
  }
  me$3.clear_assumptions();
  return value;
}
function convertValueToMathExpression(value) {
  if (value instanceof me$3.class) {
    return value;
  } else if (typeof value === "number" || typeof value === "string") {
    return me$3.fromAst(value);
  } else if (isTree(value)) {
    return me$3.fromAst(value);
  } else {
    return me$3.fromAst("＿");
  }
}
const vectorOperators = ["vector", "altvector", "tuple"];
const { mod: mod$5 } = me$3.math;
const { dopri: dopri$1 } = me$3.math;
function returnNumericalFunctionFromFormula({
  formula,
  numInputs,
  variables,
  domain = null,
  component = 0
}) {
  component = Number(component);
  let formulaIsVectorValued = Array.isArray(formula.tree) && vectorOperators.includes(formula.tree[0]);
  if (formulaIsVectorValued) {
    try {
      formula = formula.get_component(component);
    } catch (e22) {
      return () => NaN;
    }
  } else if (component !== 0) {
    return () => NaN;
  }
  let formula_f;
  try {
    formula_f = formula.subscripts_to_strings().f();
  } catch (e22) {
    return () => NaN;
  }
  if (numInputs === 1) {
    let varString = variables[0].subscripts_to_strings().tree;
    let minx = -Infinity, maxx = Infinity;
    let openMin = false, openMax = false;
    if (domain) {
      let domain0 = domain[0];
      if (domain0 !== void 0) {
        minx = me$3.fromAst(domain0.tree[1][1]).evaluate_to_constant();
        if (typeof minx !== "number" || Number.isNaN(minx)) {
          minx = -Infinity;
        } else {
          openMin = !domain0.tree[2][1];
        }
        maxx = me$3.fromAst(domain0.tree[1][2]).evaluate_to_constant();
        if (typeof maxx !== "number" || Number.isNaN(maxx)) {
          maxx = Infinity;
        } else {
          openMax = !domain0.tree[2][2];
        }
      }
      if (minx === -Infinity) {
        openMin = false;
      }
      if (maxx === Infinity) {
        openMax = false;
      }
    }
    return function(x2, overrideDomain = false) {
      if (overrideDomain) {
        if (isNaN(x2)) {
          return NaN;
        }
      } else if (!(x2 >= minx) || !(x2 <= maxx) || openMin && x2 === minx || openMax && x2 === maxx) {
        return NaN;
      }
      try {
        return formula_f({ [varString]: x2 });
      } catch (e22) {
        return NaN;
      }
    };
  }
  let varStrings = [];
  for (let i3 = 0; i3 < numInputs; i3++) {
    varStrings.push(variables[i3].subscripts_to_strings().tree);
  }
  let haveDomain = false;
  let domainIntervals = [];
  let domainOpens = [];
  if (domain !== null) {
    haveDomain = true;
    for (let i3 = 0; i3 < numInputs; i3++) {
      let thisDomain = domain[i3];
      if (!thisDomain) {
        haveDomain = false;
        break;
      }
      let minx = -Infinity, maxx = Infinity;
      let openMin = false, openMax = false;
      minx = me$3.fromAst(thisDomain.tree[1][1]).evaluate_to_constant();
      if (typeof minx !== "number" || Number.isNaN(minx)) {
        minx = -Infinity;
      } else {
        openMin = !thisDomain.tree[2][1];
      }
      maxx = me$3.fromAst(thisDomain.tree[1][2]).evaluate_to_constant();
      if (typeof maxx !== "number" || Number.isNaN(maxx)) {
        maxx = Infinity;
      } else {
        openMax = !thisDomain.tree[2][2];
      }
      if (minx === -Infinity) {
        openMin = false;
      }
      if (maxx === Infinity) {
        openMax = false;
      }
      domainIntervals.push([minx, maxx]);
      domainOpens.push([openMin, openMax]);
    }
  }
  return function(...xs) {
    let fArgs = {};
    for (let i3 = 0; i3 < numInputs; i3++) {
      let x2 = xs[i3];
      if (isNaN(x2)) {
        return NaN;
      } else if (haveDomain) {
        let [minx, maxx] = domainIntervals[i3];
        let [openMin, openMax] = domainOpens[i3];
        if (!(x2 >= minx) || !(x2 <= maxx) || openMin && x2 === minx || openMax && x2 === maxx) {
          return NaN;
        }
      }
      fArgs[varStrings[i3]] = x2;
    }
    try {
      return formula_f(fArgs);
    } catch (e22) {
      return NaN;
    }
  };
}
function returnNumericalFunctionFromReevaluatedFormula({
  formulaExpressionWithCodes,
  evaluateChildrenToReevaluate,
  numInputs,
  variables,
  domain = null,
  component = 0
}) {
  component = Number(component);
  let formulaIsVectorValued = Array.isArray(formulaExpressionWithCodes.tree) && vectorOperators.includes(formulaExpressionWithCodes.tree[0]);
  if (formulaIsVectorValued) {
    try {
      formulaExpressionWithCodes = formulaExpressionWithCodes.get_component(Number(component));
    } catch (e22) {
      return () => NaN;
    }
  } else if (component !== 0) {
    return () => NaN;
  }
  let formula_f;
  try {
    formula_f = formulaExpressionWithCodes.subscripts_to_strings().f();
  } catch (e22) {
    return () => NaN;
  }
  if (numInputs === 1) {
    let varString = variables[0].subscripts_to_strings().tree;
    let minx = -Infinity, maxx = Infinity;
    let openMin = false, openMax = false;
    if (domain !== null) {
      let domain0 = domain[0];
      if (domain0 !== void 0) {
        minx = me$3.fromAst(domain0.tree[1][1]).evaluate_to_constant();
        if (typeof minx !== "number" || Number.isNaN(minx)) {
          minx = -Infinity;
        } else {
          openMin = !domain0.tree[2][1];
        }
        maxx = me$3.fromAst(domain0.tree[1][2]).evaluate_to_constant();
        if (typeof maxx !== "number" || Number.isNaN(maxx)) {
          maxx = Infinity;
        } else {
          openMax = !domain0.tree[2][2];
        }
      }
    }
    if (minx === -Infinity) {
      openMin = false;
    }
    if (maxx === Infinity) {
      openMax = false;
    }
    return function(x2, overrideDomain = false) {
      if (overrideDomain) {
        if (isNaN(x2)) {
          return NaN;
        }
      } else if (!(x2 >= minx) || !(x2 <= maxx) || openMin && x2 === minx || openMax && x2 === maxx) {
        return NaN;
      }
      let argsForInputs = { [varString]: x2 };
      let fArgs = { [varString]: x2 };
      for (let code in evaluateChildrenToReevaluate) {
        let childF = evaluateChildrenToReevaluate[code].fReevaluate;
        let inputFs = evaluateChildrenToReevaluate[code].inputMathFs;
        try {
          let input = inputFs.map(
            (f2) => me$3.fromAst(f2(argsForInputs))
          );
          fArgs[code] = childF(input).evaluate_to_constant();
        } catch (e22) {
          return NaN;
        }
      }
      try {
        return formula_f(fArgs);
      } catch (e22) {
        return NaN;
      }
    };
  }
  let varStrings = [];
  for (let i3 = 0; i3 < numInputs; i3++) {
    varStrings.push(variables[i3].subscripts_to_strings().tree);
  }
  let haveDomain = false;
  let domainIntervals = [];
  let domainOpens = [];
  if (domain !== null) {
    haveDomain = true;
    for (let i3 = 0; i3 < numInputs; i3++) {
      let thisDomain = domain[i3];
      if (!thisDomain) {
        haveDomain = false;
        break;
      }
      let minx = -Infinity, maxx = Infinity;
      let openMin = false, openMax = false;
      minx = me$3.fromAst(thisDomain.tree[1][1]).evaluate_to_constant();
      if (typeof minx !== "number" || Number.isNaN(minx)) {
        minx = -Infinity;
      } else {
        openMin = !thisDomain.tree[2][1];
      }
      maxx = me$3.fromAst(thisDomain.tree[1][2]).evaluate_to_constant();
      if (typeof maxx !== "number" || Number.isNaN(maxx)) {
        maxx = Infinity;
      } else {
        openMax = !thisDomain.tree[2][2];
      }
      if (minx === -Infinity) {
        openMin = false;
      }
      if (maxx === Infinity) {
        openMax = false;
      }
      domainIntervals.push([minx, maxx]);
      domainOpens.push([openMin, openMax]);
    }
  }
  return function(...xs) {
    let fArgs = {};
    for (let i3 = 0; i3 < numInputs; i3++) {
      let x2 = xs[i3];
      if (typeof x2 !== "number" || isNaN(x2)) {
        return NaN;
      } else if (haveDomain) {
        let [minx, maxx] = domainIntervals[i3];
        let [openMin, openMax] = domainOpens[i3];
        if (!(x2 >= minx) || !(x2 <= maxx) || openMin && x2 === minx || openMax && x2 === maxx) {
          return NaN;
        }
      }
      fArgs[varStrings[i3]] = x2;
    }
    let argsForInputs = { ...fArgs };
    for (let code in evaluateChildrenToReevaluate) {
      let childF = evaluateChildrenToReevaluate[code].fReevaluate;
      let inputFs = evaluateChildrenToReevaluate[code].inputMathFs;
      try {
        let input = inputFs.map(
          (f2) => me$3.fromAst(f2(argsForInputs))
        );
        fArgs[code] = childF(input).evaluate_to_constant();
      } catch (e22) {
        return NaN;
      }
    }
    try {
      return formula_f(fArgs);
    } catch (e22) {
      return NaN;
    }
  };
}
function returnPiecewiseNumericalFunctionFromChildren({
  numericalFsOfChildren,
  numericalDomainsOfChildren,
  domain = null,
  component = 0
}) {
  component = Number(component);
  if (component !== 0) {
    return () => NaN;
  }
  let { minx, maxx, openMin, openMax } = find_effective_domain({
    domain: domain ?? null
  });
  return function(x2, overrideDomain = false) {
    if (overrideDomain) {
      if (isNaN(x2)) {
        return NaN;
      }
    } else if (!(x2 >= minx) || !(x2 <= maxx) || openMin && x2 === minx || openMax && x2 === maxx) {
      return NaN;
    }
    for (let [ind, childDomain] of numericalDomainsOfChildren.entries()) {
      let childMinX = childDomain[0][0];
      let childMaxX = childDomain[0][1];
      let childMinXClosed = childDomain[1][0];
      let childMaxXClosed = childDomain[1][1];
      if ((x2 > childMinX || childMinXClosed && x2 === childMinX) && (x2 < childMaxX || childMaxXClosed && x2 === childMaxX)) {
        return numericalFsOfChildren[ind](x2);
      }
    }
    return NaN;
  };
}
function returnSymbolicFunctionFromFormula({
  formula,
  simplify,
  expand,
  domain = null,
  numInputs,
  variables,
  component = 0
}) {
  component = Number(component);
  let formulaIsVectorValued = Array.isArray(formula.tree) && vectorOperators.includes(formula.tree[0]);
  if (formulaIsVectorValued) {
    try {
      formula = formula.get_component(component);
    } catch (e22) {
      return () => me$3.fromAst("＿");
    }
  } else if (component !== 0) {
    return () => me$3.fromAst("＿");
  }
  let formula_transformed = formula.subscripts_to_strings();
  if (numInputs === 1) {
    let varString = variables[0].subscripts_to_strings().tree;
    let minx = -Infinity, maxx = Infinity;
    let openMin = false, openMax = false;
    if (domain !== null) {
      let domain0 = domain[0];
      if (domain0 !== void 0) {
        minx = me$3.fromAst(domain0.tree[1][1]).evaluate_to_constant();
        if (typeof minx !== "number" || Number.isNaN(minx)) {
          minx = -Infinity;
        } else {
          openMin = !domain0.tree[2][1];
        }
        maxx = me$3.fromAst(domain0.tree[1][2]).evaluate_to_constant();
        if (typeof maxx !== "number" || Number.isNaN(maxx)) {
          maxx = Infinity;
        } else {
          openMax = !domain0.tree[2][2];
        }
      }
      if (minx === -Infinity) {
        openMin = false;
      }
      if (maxx === Infinity) {
        openMax = false;
      }
    }
    return function(x2, overrideDomain = false) {
      if (!overrideDomain) {
        let xNum = x2.evaluate_to_constant();
        if (!Number.isNaN(xNum) && (!(xNum >= minx) || !(xNum <= maxx) || openMin && xNum === minx || openMax && xNum === maxx)) {
          return me$3.fromAst("＿");
        }
      }
      return normalizeMathExpression({
        value: formula_transformed.substitute({ [varString]: x2 }).strings_to_subscripts(),
        simplify,
        expand
      });
    };
  }
  let varStrings = [];
  for (let i3 = 0; i3 < numInputs; i3++) {
    varStrings.push(variables[i3].subscripts_to_strings().tree);
  }
  let haveDomain = false;
  let domainIntervals = [];
  let domainOpens = [];
  if (domain !== null) {
    haveDomain = true;
    for (let i3 = 0; i3 < numInputs; i3++) {
      let thisDomain = domain[i3];
      if (!thisDomain) {
        haveDomain = false;
        break;
      }
      let minx = -Infinity, maxx = Infinity;
      let openMin = false, openMax = false;
      minx = me$3.fromAst(thisDomain.tree[1][1]).evaluate_to_constant();
      if (typeof minx !== "number" || Number.isNaN(minx)) {
        minx = -Infinity;
      } else {
        openMin = !thisDomain.tree[2][1];
      }
      maxx = me$3.fromAst(thisDomain.tree[1][2]).evaluate_to_constant();
      if (typeof maxx !== "number" || Number.isNaN(maxx)) {
        maxx = Infinity;
      } else {
        openMax = !thisDomain.tree[2][2];
      }
      if (minx === -Infinity) {
        openMin = false;
      }
      if (maxx === Infinity) {
        openMax = false;
      }
      domainIntervals.push([minx, maxx]);
      domainOpens.push([openMin, openMax]);
    }
    if (haveDomain && domainIntervals.every(
      (v2) => v2[0] === -Infinity && v2[1] === Infinity
    )) {
      haveDomain = false;
    }
  }
  return function(...xs) {
    let subArgs = {};
    let foundOutsideDomain = false;
    let allNumeric = true;
    for (let i3 = 0; i3 < numInputs; i3++) {
      let x2 = xs[i3];
      if (haveDomain && allNumeric) {
        let xNum = x2.evaluate_to_constant();
        if (Number.isNaN(xNum)) {
          allNumeric = false;
        } else {
          let [minx, maxx] = domainIntervals[i3];
          let [openMin, openMax] = domainOpens[i3];
          if (!(xNum >= minx) || !(xNum <= maxx) || openMin && xNum === minx || openMax && xNum === maxx) {
            foundOutsideDomain = true;
          }
        }
      }
      subArgs[varStrings[i3]] = x2;
    }
    if (allNumeric && foundOutsideDomain) {
      return me$3.fromAst("＿");
    }
    return normalizeMathExpression({
      value: formula_transformed.substitute(subArgs).strings_to_subscripts(),
      simplify,
      expand
    });
  };
}
function returnSymbolicFunctionFromReevaluatedFormula({
  formulaExpressionWithCodes,
  evaluateChildrenToReevaluate,
  simplify,
  expand,
  numInputs,
  variables,
  domain = null,
  component = 0
}) {
  component = Number(component);
  let formulaIsVectorValued = Array.isArray(formulaExpressionWithCodes.tree) && vectorOperators.includes(formulaExpressionWithCodes.tree[0]);
  if (formulaIsVectorValued) {
    try {
      formulaExpressionWithCodes = formulaExpressionWithCodes.get_component(component);
    } catch (e22) {
      return () => me$3.fromAst("＿");
    }
  } else if (component !== 0) {
    return () => me$3.fromAst("＿");
  }
  let formula_transformed = formulaExpressionWithCodes.subscripts_to_strings();
  if (numInputs === 1) {
    let varString = variables[0].subscripts_to_strings().tree;
    let minx = -Infinity, maxx = Infinity;
    let openMin = false, openMax = false;
    if (domain !== null) {
      let domain0 = domain[0];
      if (domain0 !== void 0) {
        minx = me$3.fromAst(domain0.tree[1][1]).evaluate_to_constant();
        if (typeof minx !== "number" || Number.isNaN(minx)) {
          minx = -Infinity;
        } else {
          openMin = !domain0.tree[2][1];
        }
        maxx = me$3.fromAst(domain0.tree[1][2]).evaluate_to_constant();
        if (typeof maxx !== "number" || Number.isNaN(maxx)) {
          maxx = Infinity;
        } else {
          openMax = !domain0.tree[2][2];
        }
        if (minx === -Infinity) {
          openMin = false;
        }
        if (maxx === Infinity) {
          openMax = false;
        }
      }
    }
    return function(x2, overrideDomain = false) {
      if (!overrideDomain) {
        let xNum = x2.evaluate_to_constant();
        if (!Number.isNaN(xNum) && (!(xNum >= minx) || !(xNum <= maxx) || openMin && xNum === minx || openMax && xNum === maxx)) {
          return me$3.fromAst("＿");
        }
      }
      let argsForInputs = { [varString]: x2.tree };
      let fArgs = { [varString]: x2 };
      for (let code in evaluateChildrenToReevaluate) {
        let childF = evaluateChildrenToReevaluate[code].fReevaluate;
        let inputFs = evaluateChildrenToReevaluate[code].inputMathFs;
        try {
          let input = inputFs.map(
            (f2) => me$3.fromAst(f2(argsForInputs))
          );
          fArgs[code] = childF(input);
        } catch (e22) {
          return me$3.fromAst("＿");
        }
      }
      return normalizeMathExpression({
        value: formula_transformed.substitute(fArgs).strings_to_subscripts(),
        simplify,
        expand
      });
    };
  }
  let varStrings = [];
  for (let i3 = 0; i3 < numInputs; i3++) {
    varStrings.push(variables[i3].subscripts_to_strings().tree);
  }
  let haveDomain = false;
  let domainIntervals = [];
  let domainOpens = [];
  if (domain !== null) {
    haveDomain = true;
    for (let i3 = 0; i3 < numInputs; i3++) {
      let thisDomain = domain[i3];
      if (!thisDomain) {
        haveDomain = false;
        break;
      }
      let minx = -Infinity, maxx = Infinity;
      let openMin = false, openMax = false;
      minx = me$3.fromAst(thisDomain.tree[1][1]).evaluate_to_constant();
      if (typeof minx !== "number" || Number.isNaN(minx)) {
        minx = -Infinity;
      } else {
        openMin = !thisDomain.tree[2][1];
      }
      maxx = me$3.fromAst(thisDomain.tree[1][2]).evaluate_to_constant();
      if (typeof maxx !== "number" || Number.isNaN(maxx)) {
        maxx = Infinity;
      } else {
        openMax = !thisDomain.tree[2][2];
      }
      if (minx === -Infinity) {
        openMin = false;
      }
      if (maxx === Infinity) {
        openMax = false;
      }
      domainIntervals.push([minx, maxx]);
      domainOpens.push([openMin, openMax]);
    }
  }
  return function(...xs) {
    let subArgs = {};
    let argsForInputs = {};
    let foundOutsideDomain = false;
    let allNumeric = true;
    for (let i3 = 0; i3 < numInputs; i3++) {
      let x2 = xs[i3];
      if (haveDomain && allNumeric) {
        let xNum = x2.evaluate_to_constant();
        if (Number.isNaN(xNum)) {
          allNumeric = false;
        } else {
          let [minx, maxx] = domainIntervals[i3];
          let [openMin, openMax] = domainOpens[i3];
          if (!(xNum >= minx) || !(xNum <= maxx) || openMin && xNum === minx || openMax && xNum === maxx) {
            foundOutsideDomain = true;
          }
        }
      }
      subArgs[varStrings[i3]] = x2;
      argsForInputs[varStrings[i3]] = x2.tree;
    }
    if (allNumeric && foundOutsideDomain) {
      return me$3.fromAst("＿");
    }
    for (let code in evaluateChildrenToReevaluate) {
      let childF = evaluateChildrenToReevaluate[code].fReevaluate;
      let inputFs = evaluateChildrenToReevaluate[code].inputMathFs;
      try {
        let input = inputFs.map(
          (f2) => me$3.fromAst(f2(argsForInputs))
        );
        subArgs[code] = childF(input);
      } catch (e22) {
        return NaN;
      }
    }
    return normalizeMathExpression({
      value: formula_transformed.substitute(subArgs).strings_to_subscripts(),
      simplify,
      expand
    });
  };
}
function returnSymbolicFunctionForEvaluate({
  symbolicfs,
  numInputs
}) {
  return function(input) {
    if (input.length === 1) {
      let inputTree = input[0].tree;
      if (Array.isArray(inputTree) && vectorOperators.includes(inputTree[0])) {
        input = inputTree.slice(1).map((x2) => me$3.fromAst(x2));
      }
    }
    if (input.length !== numInputs) {
      return me$3.fromAst("＿");
    }
    let components = symbolicfs.map((f2) => f2(...input).tree);
    let value;
    if (components.length === 1) {
      value = me$3.fromAst(components[0]);
    } else {
      value = me$3.fromAst(["vector", ...components]);
    }
    return value;
  };
}
function returnNumericFunctionForEvaluate({
  numericalfs,
  numInputs
}) {
  return function(input) {
    if (input.length === 1) {
      let inputTree = input[0].tree;
      if (Array.isArray(inputTree) && vectorOperators.includes(inputTree[0])) {
        input = inputTree.slice(1).map((x2) => me$3.fromAst(x2));
      }
    }
    if (input.length !== numInputs) {
      return me$3.fromAst("＿");
    }
    let numericInput = input.map((x2) => x2.evaluate_to_constant());
    let components = numericalfs.map((f2) => f2(...numericInput));
    let value;
    if (components.length === 1) {
      value = me$3.fromAst(components[0]);
    } else {
      value = me$3.fromAst(["vector", ...components]);
    }
    return value;
  };
}
function returnBezierFunctions({
  numThroughPoints,
  numericalThroughPoints,
  splineCoeffs,
  extrapolateForward,
  extrapolateForwardCoeffs,
  extrapolateBackward,
  extrapolateBackwardCoeffs,
  component
}) {
  if (numThroughPoints < 1) {
    return () => NaN;
  }
  let len = numThroughPoints - 1;
  let lastPointX = numericalThroughPoints[len][component];
  let cs = splineCoeffs.map((x2) => x2[component]);
  let cB;
  if (extrapolateBackward) {
    cB = extrapolateBackwardCoeffs[component];
  }
  let cF;
  if (extrapolateForward) {
    cF = extrapolateForwardCoeffs[component];
  }
  return function(t2) {
    if (isNaN(t2)) {
      return NaN;
    }
    if (t2 < 0) {
      if (extrapolateBackward) {
        return (cB[2] * t2 + cB[1]) * t2 + cB[0];
      } else {
        return NaN;
      }
    }
    if (t2 >= len) {
      if (extrapolateForward) {
        t2 -= len;
        return (cF[2] * t2 + cF[1]) * t2 + cF[0];
      } else if (t2 === len) {
        return lastPointX;
      } else {
        return NaN;
      }
    }
    let z2 = Math.floor(t2);
    t2 -= z2;
    let c2 = cs[z2];
    return ((c2[3] * t2 + c2[2]) * t2 + c2[1]) * t2 + c2[0];
  };
}
function returnInterpolatedFunction({
  xs,
  coeffs,
  interpolationPoints,
  domain = null
}) {
  let interpolationPointYs = [];
  if (interpolationPoints) {
    interpolationPointYs = interpolationPoints.map((x2) => x2.y);
  }
  if (xs === null) {
    return () => NaN;
  }
  let minx = -Infinity, maxx = Infinity;
  let openMin = false, openMax = false;
  if (domain !== null) {
    let domain0 = domain[0];
    if (domain0 !== void 0) {
      minx = me$3.fromAst(domain0.tree[1][1]).evaluate_to_constant();
      if (typeof minx !== "number" || Number.isNaN(minx)) {
        minx = -Infinity;
      } else {
        openMin = !domain0.tree[2][1];
      }
      maxx = me$3.fromAst(domain0.tree[1][2]).evaluate_to_constant();
      if (typeof maxx !== "number" || Number.isNaN(maxx)) {
        maxx = Infinity;
      } else {
        openMax = !domain0.tree[2][2];
      }
      if (minx === -Infinity) {
        openMin = false;
      }
      if (maxx === Infinity) {
        openMax = false;
      }
    }
  }
  let x0 = xs[0], xL = xs[xs.length - 1];
  return function(x2, overrideDomain = false) {
    if (overrideDomain) {
      if (isNaN(x2)) {
        return NaN;
      }
    } else if (!(x2 >= minx) || !(x2 <= maxx) || openMin && x2 === minx || openMax && x2 === maxx) {
      return NaN;
    }
    if (x2 <= x0) {
      x2 -= x0;
      let c22 = coeffs[0];
      if (x2 === -Infinity) {
        if (c22[3] === 0) {
          if (c22[2] === 0) {
            if (c22[1] === 0) {
              return c22[0];
            } else {
              return -Math.sign(c22[1]) * Infinity;
            }
          } else {
            return Math.sign(c22[2]) * Infinity;
          }
        } else {
          return -Math.sign(c22[3]) * Infinity;
        }
      }
      return ((c22[3] * x2 + c22[2]) * x2 + c22[1]) * x2 + c22[0];
    }
    if (x2 >= xL) {
      let i22 = xs.length - 2;
      x2 -= xs[i22];
      let c22 = coeffs[i22];
      if (x2 === Infinity) {
        if (c22[3] === 0) {
          if (c22[2] === 0) {
            if (c22[1] === 0) {
              return c22[0];
            } else {
              return Math.sign(c22[1]) * Infinity;
            }
          } else {
            return Math.sign(c22[2]) * Infinity;
          }
        } else {
          return Math.sign(c22[3]) * Infinity;
        }
      }
      if (x2 === Infinity && c22[1] === 0 && c22[2] === 0 && c22[3] === 0) {
        return c22[0];
      }
      return ((c22[3] * x2 + c22[2]) * x2 + c22[1]) * x2 + c22[0];
    }
    let low = 0, mid, high = xs.length - 1;
    while (low <= high) {
      mid = Math.floor(0.5 * (low + high));
      let xHere = xs[mid];
      if (xHere < x2) {
        low = mid + 1;
      } else if (xHere > x2) {
        high = mid - 1;
      } else {
        return interpolationPointYs[mid];
      }
    }
    let i3 = Math.max(0, high);
    x2 -= xs[i3];
    let c2 = coeffs[i3];
    return ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0];
  };
}
function returnReturnDerivativesOfInterpolatedFunction({
  xs,
  coeffs,
  variables
}) {
  if (!xs) {
    return () => () => NaN;
  }
  let variable1Trans = variables[0].subscripts_to_strings().tree;
  let x0 = xs[0], xL = xs[xs.length - 1];
  return function(derivVariables) {
    let derivVariablesTrans = derivVariables.map(
      (x2) => x2.subscripts_to_strings().tree
    );
    let order = derivVariablesTrans.length;
    if (order > 3 || !derivVariablesTrans.every((x2) => x2 === variable1Trans) || derivVariablesTrans.includes("＿")) {
      return () => 0;
    }
    if (order === 0 || xs === null) {
      return () => NaN;
    }
    return function(x2) {
      if (isNaN(x2)) {
        return NaN;
      }
      if (x2 <= x0) {
        x2 -= x0;
        let c22 = coeffs[0];
        if (order === 1) {
          return (3 * c22[3] * x2 + 2 * c22[2]) * x2 + c22[1];
        } else if (order === 2) {
          return 6 * c22[3] * x2 + 2 * c22[2];
        } else {
          return 6 * c22[3];
        }
      }
      if (x2 >= xL) {
        let i22 = xs.length - 2;
        x2 -= xs[i22];
        let c22 = coeffs[i22];
        if (order === 1) {
          return (3 * c22[3] * x2 + 2 * c22[2]) * x2 + c22[1];
        } else if (order === 2) {
          return 6 * c22[3] * x2 + 2 * c22[2];
        } else {
          return 6 * c22[3];
        }
      }
      let low = 0, mid, high = xs.length - 1;
      while (low <= high) {
        mid = Math.floor(0.5 * (low + high));
        let xHere = xs[mid];
        if (xHere < x2) {
          low = mid + 1;
        } else if (xHere > x2) {
          high = mid - 1;
        } else {
          if (order === 1) {
            return coeffs[mid][1];
          } else if (order === 2) {
            return 2 * coeffs[mid][2];
          } else {
            return 6 * coeffs[mid][3];
          }
        }
      }
      let i3 = Math.max(0, high);
      x2 -= xs[i3];
      let c2 = coeffs[i3];
      if (order === 1) {
        return (3 * c2[3] * x2 + 2 * c2[2]) * x2 + c2[1];
      } else if (order === 2) {
        return 6 * c2[3] * x2 + 2 * c2[2];
      } else {
        return 6 * c2[3];
      }
    };
  };
}
const functionOperatorDefinitions = {
  clampFunction: function(lowerValue, upperValue) {
    return function(x2) {
      if (!Number.isFinite(x2)) {
        return NaN;
      }
      return Math.max(lowerValue, Math.min(upperValue, x2));
    };
  },
  wrapFunctionPeriodic: function(lowerValue, upperValue) {
    return function(x2) {
      if (!Number.isFinite(x2)) {
        return NaN;
      }
      let lower = lowerValue;
      let upper = upperValue;
      if (lower === upper) {
        return lower;
      }
      if (lower > upper) {
        [upper, lower] = [lower, upper];
      }
      return lower + mod$5(x2 - lower, upper - lower);
    };
  },
  derivative: function(derivDefinition, derivVariables) {
    if (derivDefinition.derivativeType === "interpolatedFunction") {
      let derivGenerator = returnReturnDerivativesOfInterpolatedFunction({
        xs: derivDefinition.xs,
        coeffs: derivDefinition.coeffs,
        variables: derivDefinition.variables.map(
          convertValueToMathExpression
        )
      });
      let vars = derivVariables.map(convertValueToMathExpression);
      if (derivDefinition.additionalDerivVariables) {
        let additionalVars = derivDefinition.additionalDerivVariables.map(
          convertValueToMathExpression
        );
        vars = [...additionalVars, ...vars];
      }
      if (derivDefinition.variableMappings) {
        for (let variableMapping of derivDefinition.variableMappings) {
          let mappedDerivVariables = [];
          for (let dVar of vars) {
            let mapped = variableMapping[dVar.subscripts_to_strings().tree];
            if (mapped) {
              mappedDerivVariables.push(
                convertValueToMathExpression(mapped)
              );
            } else {
              mappedDerivVariables.push(me$3.fromAst("＿"));
            }
          }
          vars = mappedDerivVariables;
        }
      }
      let deriv = derivGenerator(vars);
      return deriv;
    } else {
      return () => NaN;
    }
  }
};
const DEFAULT_MATH_INPUT_FUNCTION_NAMES = [
  "arg",
  "deg",
  "det",
  "dim",
  "exp",
  "gcd",
  "hom",
  "ker",
  "lg",
  "lim",
  "ln",
  "log",
  "max",
  "min",
  "Pr",
  "cos",
  "cosh",
  "acos",
  "acosh",
  "arccos",
  "arccosh",
  "cot",
  "coth",
  "acot",
  "acoth",
  "arccot",
  "arccoth",
  "csc",
  "csch",
  "acsc",
  "acsch",
  "arccsc",
  "arccsch",
  "sec",
  "sech",
  "asec",
  "asech",
  "arcsec",
  "arcsech",
  "sin",
  "sinh",
  "asin",
  "asinh",
  "arcsin",
  "arcsinh",
  "tan",
  "tanh",
  "atan",
  "atanh",
  "arctan",
  "arctanh",
  "nPr",
  "nCr"
];
function isValidMathQuillFunctionName(token) {
  if (!/^[a-z|-]+$/i.test(token)) return false;
  const parts = token.split("|");
  if (parts.length > 2) return false;
  if (parts[0].length < 2) return false;
  return true;
}
function buildEffectiveMathInputFunctionNames({
  additional = [],
  removed = [],
  reset = null
}) {
  if (reset !== null) {
    const seen2 = /* @__PURE__ */ new Set();
    const out2 = [];
    const droppedFromReset = [];
    for (const name of reset) {
      if (seen2.has(name)) continue;
      seen2.add(name);
      if (!isValidMathQuillFunctionName(name)) {
        droppedFromReset.push(name);
        continue;
      }
      out2.push(name);
    }
    return { names: out2, droppedFromAdditional: [], droppedFromReset };
  }
  const removedSet = new Set(removed);
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const name of DEFAULT_MATH_INPUT_FUNCTION_NAMES) {
    if (removedSet.has(name) || seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  const droppedFromAdditional = [];
  for (const name of additional) {
    if (removedSet.has(name) || seen.has(name)) continue;
    seen.add(name);
    if (!isValidMathQuillFunctionName(name)) {
      droppedFromAdditional.push(name);
      continue;
    }
    out.push(name);
  }
  return { names: out, droppedFromAdditional, droppedFromReset: [] };
}
const creativeCommonsVersions = [
  "1.0",
  "2.0",
  "2.5",
  "3.0",
  "4.0"
];
const defaultCreativeCommonsVersion = "4.0";
function creativeCommonsLicense(code, name, urlSlug, description) {
  return {
    code,
    name: `Creative Commons ${name}`,
    description,
    kind: "creative-commons",
    url: (version = defaultCreativeCommonsVersion) => `https://creativecommons.org/licenses/${urlSlug}/${version}/`
  };
}
function fixedUrlLicense(code, name, url, description, kind) {
  return {
    code,
    name,
    description,
    kind,
    url: () => url
  };
}
const mediaLicenses = [
  creativeCommonsLicense(
    "CC-BY",
    "Attribution",
    "by",
    "Creative Commons Attribution: reuse with credit, including commercially."
  ),
  creativeCommonsLicense(
    "CC-BY-SA",
    "Attribution-ShareAlike",
    "by-sa",
    "Creative Commons Attribution-ShareAlike: reuse with credit; adaptations keep the same license."
  ),
  creativeCommonsLicense(
    "CC-BY-ND",
    "Attribution-NoDerivatives",
    "by-nd",
    "Creative Commons Attribution-NoDerivatives: reuse unchanged, with credit."
  ),
  creativeCommonsLicense(
    "CC-BY-NC",
    "Attribution-NonCommercial",
    "by-nc",
    "Creative Commons Attribution-NonCommercial: reuse with credit for noncommercial purposes."
  ),
  creativeCommonsLicense(
    "CC-BY-NC-SA",
    "Attribution-NonCommercial-ShareAlike",
    "by-nc-sa",
    "Creative Commons Attribution-NonCommercial-ShareAlike: noncommercial reuse with credit; adaptations keep the same license."
  ),
  creativeCommonsLicense(
    "CC-BY-NC-ND",
    "Attribution-NonCommercial-NoDerivatives",
    "by-nc-nd",
    "Creative Commons Attribution-NonCommercial-NoDerivatives: reuse unchanged for noncommercial purposes, with credit."
  ),
  fixedUrlLicense(
    "CC0",
    "CC0 1.0 Public Domain Dedication",
    "https://creativecommons.org/publicdomain/zero/1.0/",
    "Creative Commons Zero: the creator has dedicated the work to the public domain.",
    "public-domain"
  ),
  fixedUrlLicense(
    "PDM",
    "Public Domain Mark 1.0",
    "https://creativecommons.org/publicdomain/mark/1.0/",
    "Public Domain Mark: the work is free of known copyright restrictions.",
    "public-domain"
  ),
  fixedUrlLicense(
    "GFDL",
    "GNU Free Documentation License",
    "https://www.gnu.org/licenses/fdl-1.3.html",
    "GNU Free Documentation License: copyleft license common on Wikimedia.",
    "license"
  ),
  fixedUrlLicense(
    "FAL",
    "Free Art License 1.3",
    "https://artlibre.org/licence/lal/en/",
    "Free Art License: copyleft license for artistic works.",
    "license"
  ),
  fixedUrlLicense(
    "OGL",
    "Open Government Licence v3.0 (UK)",
    "https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/",
    "UK Open Government Licence: reuse government material with attribution.",
    "license"
  ),
  fixedUrlLicense(
    "MIT",
    "MIT License",
    "https://opensource.org/license/mit",
    "MIT License: permissive license common for icon and illustration sets.",
    "license"
  ),
  fixedUrlLicense(
    "APACHE-2.0",
    "Apache License 2.0",
    "https://www.apache.org/licenses/LICENSE-2.0",
    "Apache License 2.0: permissive license common for icon and illustration sets.",
    "license"
  )
];
const mediaLicensesByLowerCode = Object.fromEntries(
  mediaLicenses.map((info) => [info.code.toLowerCase(), info])
);
function getMediaLicenseInfo(code) {
  return mediaLicensesByLowerCode[code.toLowerCase()];
}
function lettersToNumber(letters) {
  try {
    letters = letters.toUpperCase();
  } catch (e22) {
    console.warn("Cannot convert " + letters + " to a number");
    return void 0;
  }
  let number = 0, len = letters.length, pos = len;
  while ((pos -= 1) > -1) {
    let numForLetter = letters.charCodeAt(pos) - 64;
    if (numForLetter < 1 || numForLetter > 26) {
      console.warn("Cannot convert " + letters + " to a number");
      return void 0;
    }
    number += numForLetter * Math.pow(26, len - 1 - pos);
  }
  return number;
}
function numberToLetters(number, lowercase) {
  number--;
  let offset = 65;
  if (lowercase) {
    offset = 97;
  }
  let letters = "";
  while (true) {
    let nextNum = number % 26;
    letters = String.fromCharCode(offset + nextNum) + letters;
    if (number < 26) {
      break;
    }
    number = Math.floor(number / 26) - 1;
  }
  return letters;
}
const maxWidth = 850;
const sizePossibilities = ["tiny", "small", "medium", "large", "full"];
const widthFractions = [1 / 12, 0.3, 0.5, 0.7, 1];
const fractionMidpoints = widthFractions.map(
  (x2, i3) => (x2 + (widthFractions[i3 + 1] || Infinity)) / 2
);
const orderedWidthMidpoints = fractionMidpoints.map((x2) => maxWidth * x2);
const orderedPercentWidthMidpoints = fractionMidpoints.map(
  (x2) => x2 * 100
);
const widthsBySize = {};
sizePossibilities.forEach(
  (x2, i3) => widthsBySize[x2] = Math.floor(widthFractions[i3] * maxWidth)
);
const percentWidthsBySize = {};
sizePossibilities.forEach(
  (x2, i3) => percentWidthsBySize[x2] = Math.round(widthFractions[i3] * 100)
);
function flattenDeep(arr1) {
  return arr1.reduce(
    (acc, val) => Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val),
    []
  );
}
function flattenLevels(arr1, levels) {
  return arr1.reduce(
    (acc, val) => Array.isArray(val) && levels > 1 ? acc.concat(flattenLevels(val, levels - 1)) : acc.concat(val),
    []
  );
}
function roundForDisplay({
  value,
  dependencyValues
}) {
  let rounded = me$3.round_numbers_to_precision_plus_decimals(
    value,
    dependencyValues.displayDigits,
    dependencyValues.displayDecimals
  );
  if (dependencyValues.displayDigits > 0 && dependencyValues.displaySmallAsZero > 0) {
    rounded = me$3.set_small_zero(
      rounded,
      dependencyValues.displaySmallAsZero
    );
  }
  return rounded;
}
var cssesc_1;
var hasRequiredCssesc;
function requireCssesc() {
  if (hasRequiredCssesc) return cssesc_1;
  hasRequiredCssesc = 1;
  var object = {};
  var hasOwnProperty = object.hasOwnProperty;
  var merge2 = function merge22(options, defaults2) {
    if (!options) {
      return defaults2;
    }
    var result = {};
    for (var key in defaults2) {
      result[key] = hasOwnProperty.call(options, key) ? options[key] : defaults2[key];
    }
    return result;
  };
  var regexAnySingleEscape = /[ -,\.\/:-@\[-\^`\{-~]/;
  var regexSingleEscape = /[ -,\.\/:-@\[\]\^`\{-~]/;
  var regexExcessiveSpaces = /(^|\\+)?(\\[A-F0-9]{1,6})\x20(?![a-fA-F0-9\x20])/g;
  var cssesc2 = function cssesc3(string, options) {
    options = merge2(options, cssesc3.options);
    if (options.quotes != "single" && options.quotes != "double") {
      options.quotes = "single";
    }
    var quote = options.quotes == "double" ? '"' : "'";
    var isIdentifier = options.isIdentifier;
    var firstChar = string.charAt(0);
    var output = "";
    var counter = 0;
    var length = string.length;
    while (counter < length) {
      var character = string.charAt(counter++);
      var codePoint = character.charCodeAt();
      var value = void 0;
      if (codePoint < 32 || codePoint > 126) {
        if (codePoint >= 55296 && codePoint <= 56319 && counter < length) {
          var extra = string.charCodeAt(counter++);
          if ((extra & 64512) == 56320) {
            codePoint = ((codePoint & 1023) << 10) + (extra & 1023) + 65536;
          } else {
            counter--;
          }
        }
        value = "\\" + codePoint.toString(16).toUpperCase() + " ";
      } else {
        if (options.escapeEverything) {
          if (regexAnySingleEscape.test(character)) {
            value = "\\" + character;
          } else {
            value = "\\" + codePoint.toString(16).toUpperCase() + " ";
          }
        } else if (/[\t\n\f\r\x0B]/.test(character)) {
          value = "\\" + codePoint.toString(16).toUpperCase() + " ";
        } else if (character == "\\" || !isIdentifier && (character == '"' && quote == character || character == "'" && quote == character) || isIdentifier && regexSingleEscape.test(character)) {
          value = "\\" + character;
        } else {
          value = character;
        }
      }
      output += value;
    }
    if (isIdentifier) {
      if (/^-[-\d]/.test(output)) {
        output = "\\-" + output.slice(1);
      } else if (/\d/.test(firstChar)) {
        output = "\\3" + firstChar + " " + output.slice(1);
      }
    }
    output = output.replace(regexExcessiveSpaces, function($0, $1, $2) {
      if ($1 && $1.length % 2) {
        return $0;
      }
      return ($1 || "") + $2;
    });
    if (!isIdentifier && options.wrap) {
      return quote + output + quote;
    }
    return output;
  };
  cssesc2.options = {
    "escapeEverything": false,
    "isIdentifier": false,
    "quotes": "single",
    "wrap": false
  };
  cssesc2.version = "3.0.0";
  cssesc_1 = cssesc2;
  return cssesc_1;
}
requireCssesc();
const data_format_version = "0.7.0";
function getVariantsForDescendantsForUniqueVariants({
  variantIndex,
  serializedComponent,
  componentInfoObjects
}) {
  let descendantVariantComponents = serializedComponent.variants?.descendantVariantComponents;
  if (descendantVariantComponents === void 0) {
    return { success: false };
  }
  let numVariantsByDescendant = descendantVariantComponents.map(
    (x2) => x2.variants.numVariants
  );
  let indices = enumerateCombinations({
    numberOfOptionsByIndex: numVariantsByDescendant,
    maxNumber: variantIndex
  })[variantIndex - 1];
  let desiredVariants = [];
  for (let [ind, comp] of descendantVariantComponents.entries()) {
    let compClass = componentInfoObjects.allComponentClasses[comp.componentType];
    let r2 = compClass.getUniqueVariant({
      serializedComponent: comp,
      variantIndex: indices[ind] + 1,
      componentInfoObjects
    });
    if (r2.success) {
      desiredVariants.push(r2.desiredVariant);
    } else {
      return { success: false };
    }
  }
  return {
    success: true,
    desiredVariants
  };
}
function setUpVariantSeedAndRng({
  serializedComponent,
  sharedParameters,
  descendantVariantComponents,
  useSubpartVariantRng = false
}) {
  let variantSeed;
  let desiredVariant;
  if (serializedComponent.variants) {
    desiredVariant = serializedComponent.variants.desiredVariant;
  }
  if (desiredVariant?.seed !== void 0) {
    variantSeed = desiredVariant.seed.toString();
  } else {
    if (useSubpartVariantRng) {
      variantSeed = sharedParameters.subpartVariantRng().toString().slice(2);
    } else {
      variantSeed = sharedParameters.variantRng().toString().slice(2);
    }
  }
  sharedParameters.variantSeed = variantSeed;
  sharedParameters.variantRng = sharedParameters.rngClass(
    sharedParameters.variantSeed
  );
  sharedParameters.subpartVariantRng = sharedParameters.rngClass(
    sharedParameters.variantSeed + "s"
  );
  if (desiredVariant?.subvariants && descendantVariantComponents) {
    for (let ind in desiredVariant.subvariants) {
      let subvariant = desiredVariant.subvariants[ind];
      let variantComponent = descendantVariantComponents[ind];
      if (variantComponent === void 0) {
        break;
      }
      variantComponent.variants.desiredVariant = subvariant;
    }
  }
}
function gatherVariantComponents({
  serializedComponents,
  componentInfoObjects
}) {
  let variantComponents = [];
  for (let serializedComponent of serializedComponents) {
    if (typeof serializedComponent !== "object") {
      continue;
    }
    if (!serializedComponent.variants) {
      serializedComponent.variants = {};
    }
    if (serializedComponent.variants.isVariantComponent) {
      variantComponents.push(serializedComponent);
      continue;
    }
    let componentType = serializedComponent.componentType;
    if (componentType in componentInfoObjects.componentTypesCreatingVariants) {
      serializedComponent.variants.isVariantComponent = true;
      variantComponents.push(serializedComponent);
      continue;
    }
    let compClass = componentInfoObjects.allComponentClasses[serializedComponent.componentType];
    if (compClass?.ignoreVariantsFromChildren || !serializedComponent.children) {
      continue;
    }
    if (serializedComponent.children.some(
      (x2) => x2.componentType === "variantControl"
    )) {
      serializedComponent.variants.isVariantComponent = true;
      variantComponents.push(serializedComponent);
      continue;
    }
    let descendantVariantComponents = gatherVariantComponents({
      serializedComponents: serializedComponent.children,
      componentInfoObjects
    });
    if (descendantVariantComponents.length > 0) {
      serializedComponent.variants.descendantVariantComponents = descendantVariantComponents;
      variantComponents.push(...descendantVariantComponents);
    }
  }
  return variantComponents;
}
function getNumVariants({
  serializedComponent,
  componentInfoObjects,
  infoDiagnostics
}) {
  if (!serializedComponent.variants) {
    serializedComponent.variants = {};
  }
  let variantControlChild;
  for (let child of serializedComponent.children) {
    if (child.componentType === "variantControl") {
      variantControlChild = child;
      break;
    }
  }
  let isDocument = serializedComponent.componentType === "document";
  if (!variantControlChild) {
    if (!isDocument) {
      return { success: false };
    }
    let nonBlankChildren = serializedComponent.children.filter(
      (x2) => x2.componentType || x2.trim() !== ""
    );
    if (nonBlankChildren.length === 1 && componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: nonBlankChildren[0].componentType,
      baseComponentType: "_sectioningComponent"
    })) {
      let sectionChild = nonBlankChildren[0];
      let results = getNumVariants({
        serializedComponent: sectionChild,
        componentInfoObjects,
        infoDiagnostics
      });
      if (results.success) {
        serializedComponent.variants.descendantVariantComponents = gatherVariantComponents({
          serializedComponents: serializedComponent.children,
          componentInfoObjects
        });
        serializedComponent.variants.uniqueVariants = true;
        serializedComponent.variants.numVariants = sectionChild.variants.numVariants;
        serializedComponent.variants.allPossibleVariants = sectionChild.variants.allPossibleVariants;
        serializedComponent.variants.allVariantNames = sectionChild.variants.allVariantNames;
        serializedComponent.variants.allPossibleVariantUniqueIndices = [
          ...sectionChild.variants.allPossibleVariants.keys()
        ].map((x2) => x2 + 1);
        serializedComponent.variants.allPossibleVariantSeeds = [
          ...sectionChild.variants.allPossibleVariants.keys()
        ].map((x2) => (x2 + 1).toString());
        return results;
      }
    }
  }
  return determineVariantsForSection({
    serializedComponent,
    componentInfoObjects,
    isDocument,
    infoDiagnostics
  });
}
function determineVariantsForSection({
  serializedComponent,
  componentInfoObjects,
  isDocument = false,
  infoDiagnostics
}) {
  if (serializedComponent.variants === void 0) {
    serializedComponent.variants = {};
  }
  if (serializedComponent.variants.allPossibleVariants) {
    return {
      success: true,
      numVariants: serializedComponent.variants.numVariants
    };
  }
  let variantControlChild;
  for (let child of serializedComponent.children) {
    if (child.componentType === "variantControl") {
      variantControlChild = child;
      break;
    }
  }
  if (!variantControlChild && !isDocument) {
    let BaseComponent2 = componentInfoObjects.allComponentClasses._base;
    return BaseComponent2.determineNumberOfUniqueVariants({
      serializedComponent,
      componentInfoObjects,
      infoDiagnostics
    });
  }
  let specifiedVariantNames = [];
  if (variantControlChild?.attributes.variantNames) {
    specifiedVariantNames = variantControlChild.attributes.variantNames.component.children.map(
      (x2) => x2.toLowerCase()
    );
  }
  if (specifiedVariantNames.length !== [...new Set(specifiedVariantNames)].length) {
    throw Error("Duplicate variant names specified");
  }
  let numVariantsSpecified = variantControlChild?.attributes.numVariants?.primitive.value;
  if (!Number.isFinite(numVariantsSpecified)) {
    numVariantsSpecified = 100;
  }
  numVariantsSpecified = Math.min(Math.max(numVariantsSpecified, 1), 1e3);
  let variantNames = [...specifiedVariantNames];
  if (variantNames.length < numVariantsSpecified) {
    let variantNumber = variantNames.length;
    let variantValue = variantNumber;
    let variantString;
    while (variantNumber < numVariantsSpecified) {
      variantNumber++;
      variantValue++;
      variantString = indexToLowercaseLetters$1(variantValue);
      while (specifiedVariantNames.includes(variantString)) {
        variantValue++;
        variantString = indexToLowercaseLetters$1(variantValue);
      }
      variantNames.push(variantString);
    }
  } else {
    variantNames = variantNames.slice(0, numVariantsSpecified);
  }
  let variantsToInclude = variantControlChild?.attributes.variantsToInclude?.component.children;
  if (variantsToInclude) {
    if (variantsToInclude.length === 0) {
      throw Error(
        "Cannot specify a blank variantsToInclude attribute of a variantControl"
      );
    }
    variantsToInclude = variantsToInclude.map((x2) => x2.toLowerCase());
    variantsToInclude = [...new Set(variantsToInclude)];
    for (let variant of variantsToInclude) {
      if (!variantNames.includes(variant)) {
        throw Error(
          `Cannot include variant ${variant} as ${variant} is a not variant name`
        );
      }
    }
  }
  let variantsToExclude = variantControlChild?.attributes.variantsToExclude?.component.children || [];
  variantsToExclude = variantsToExclude.map((x2) => x2.toLowerCase());
  for (let variant of variantsToExclude) {
    if (!variantNames.includes(variant)) {
      throw Error(
        `Cannot exclude variant ${variant} as ${variant} is not a variant name`
      );
    }
  }
  if (variantsToInclude) {
    variantsToInclude = variantsToInclude.filter(
      (x2) => !variantsToExclude.includes(x2)
    );
  } else {
    variantsToInclude = [...variantNames].filter(
      (x2) => !variantsToExclude.includes(x2)
    );
  }
  let specifiedSeeds = [];
  if (variantControlChild?.attributes.seeds) {
    specifiedSeeds = variantControlChild.attributes.seeds.component.children;
  }
  let variantSeeds = [...specifiedSeeds];
  if (variantSeeds.length < numVariantsSpecified) {
    variantSeeds = [...Array(numVariantsSpecified).keys()].map(
      (i2) => (i2 + 1).toString()
    );
  } else {
    variantSeeds = variantSeeds.slice(0, numVariantsSpecified);
  }
  let variantsIndicesSeedsToInclude = variantsToInclude.map((variant) => {
    const index2 = variantNames.indexOf(variant) + 1;
    return { variant, index: index2, seed: variantSeeds[index2 - 1] };
  });
  let uniqueVariantsIsSpecified = variantControlChild?.attributes.uniqueVariants !== void 0;
  let uniqueVariants = Boolean(
    variantControlChild?.attributes.uniqueVariants?.primitive.value
  );
  let uniqueResult;
  if (uniqueVariants || !uniqueVariantsIsSpecified) {
    let BaseComponent2 = componentInfoObjects.allComponentClasses._base;
    uniqueResult = BaseComponent2.determineNumberOfUniqueVariants({
      serializedComponent,
      componentInfoObjects,
      infoDiagnostics
    });
    if (!uniqueResult.success || !(uniqueResult.numVariants > 0 && // If there are more than 10000000 variants,
    // there is no more than about a 0.05% chance of getting a duplicate in 100 tries
    // and no more than about a 5% chance of getting a duplicate in 1000 tries.
    // Since creating arrays that large to enumerate the unique results is getting to be expensive,
    // we'll just randomly and independently select variants.
    uniqueResult.numVariants <= 1e7)) {
      uniqueVariants = false;
    } else {
      uniqueVariants = true;
    }
  }
  let allPossibleVariants = [];
  let allPossibleVariantUniqueIndices = [];
  let allPossibleVariantSeeds = [];
  if (uniqueVariants && uniqueResult) {
    variantsIndicesSeedsToInclude = variantsIndicesSeedsToInclude.filter(
      ({ index: index2 }) => index2 <= uniqueResult.numVariants
    );
    const maxIndex = variantsIndicesSeedsToInclude.reduce(
      (a2, c2) => Math.max(a2, c2.index),
      0
    );
    const rng = seedrandom.alea("0");
    const N2 = uniqueResult.numVariants;
    const uniqueIndices = [...Array(N2).keys()].map((i2) => i2 + 1);
    for (let i2 = 0; i2 < maxIndex; i2++) {
      const rand = rng();
      const j2 = i2 + Math.floor(rand * (N2 - i2));
      [uniqueIndices[i2], uniqueIndices[j2]] = [
        uniqueIndices[j2],
        uniqueIndices[i2]
      ];
    }
    const uniqueIndicesToChooseFrom = uniqueIndices.slice(0, maxIndex);
    for (const { variant, index: index2, seed } of variantsIndicesSeedsToInclude) {
      allPossibleVariantUniqueIndices.push(
        uniqueIndicesToChooseFrom[index2 - 1]
      );
      allPossibleVariants.push(variant);
      allPossibleVariantSeeds.push(seed);
    }
  } else {
    allPossibleVariants = variantsToInclude;
    allPossibleVariantUniqueIndices = variantsIndicesSeedsToInclude.map(
      ({ index: index2 }) => index2
    );
    allPossibleVariantSeeds = variantsIndicesSeedsToInclude.map(
      ({ seed }) => seed
    );
  }
  let numVariants = allPossibleVariants.length;
  if (numVariants === 0) {
    throw Error(
      "No variants selected based on variantsToInclude, variantsToExclude, and the number of variants available"
    );
  }
  serializedComponent.variants.uniqueVariants = uniqueVariants;
  serializedComponent.variants.numVariants = numVariants;
  serializedComponent.variants.allPossibleVariants = allPossibleVariants;
  serializedComponent.variants.allVariantNames = variantNames;
  serializedComponent.variants.allPossibleVariantUniqueIndices = allPossibleVariantUniqueIndices;
  serializedComponent.variants.allPossibleVariantSeeds = allPossibleVariantSeeds;
  return {
    success: true,
    numVariants
  };
}
function indexToLowercaseLetters$1(index2) {
  return numberToLetters(index2, true);
}
function extractConstantSortAttribute(serializedComponent, componentName, numToSelect, infoDiagnostics) {
  let sort;
  let sortComponent = serializedComponent.attributes.sort?.component;
  if (sortComponent) {
    if (sortComponent.children?.length === 1 && typeof sortComponent.children[0] === "string") {
      sort = sortComponent.children[0].trim().toLowerCase();
    } else if ((!sortComponent.children || sortComponent.children?.length === 0) && typeof sortComponent.state?.value === "string") {
      sort = sortComponent.state.value.trim().toLowerCase();
    } else {
      pushVariantInfo(
        infoDiagnostics,
        `cannot determine unique variants of ${componentName} as sort isn't a constant.`,
        serializedComponent
      );
      return { success: false };
    }
  } else {
    sort = "unsorted";
  }
  if (sort !== "unsorted" && numToSelect > 1) {
    pushVariantInfo(
      infoDiagnostics,
      `have not implemented unique variants of a ${componentName} with sort`,
      serializedComponent
    );
    return { success: false };
  }
  return { success: true, sort };
}
function pushVariantInfo(infoDiagnostics, message, serializedComponent) {
  if (!infoDiagnostics) {
    return;
  }
  infoDiagnostics.push({
    type: "info",
    message,
    position: serializedComponent?.position,
    sourceDoc: serializedComponent?.sourceDoc
  });
}
var appliedFunctionSymbolsDefault = [
  "abs",
  "exp",
  "log",
  "ln",
  "log10",
  "sign",
  "sqrt",
  "cbrt",
  "nthroot",
  "erf",
  "cos",
  "cosh",
  "acos",
  "acosh",
  "arccos",
  "arccosh",
  "cot",
  "coth",
  "acot",
  "acoth",
  "arccot",
  "arccoth",
  "csc",
  "csch",
  "acsc",
  "acsch",
  "arccsc",
  "arccsch",
  "sec",
  "sech",
  "asec",
  "asech",
  "arcsec",
  "arcsech",
  "sin",
  "sinh",
  "asin",
  "asinh",
  "arcsin",
  "arcsinh",
  "tan",
  "tanh",
  "atan",
  "atan2",
  "atanh",
  "arctan",
  "arctanh",
  "arg",
  "min",
  "max",
  "mean",
  "median",
  "floor",
  "ceil",
  "round",
  "sum",
  "prod",
  "variance",
  "std",
  "count",
  "mod",
  "re",
  "im",
  "det",
  "trace",
  "nPr",
  "nCr"
];
var appliedFunctionSymbolsDefaultLatex = [
  "abs",
  "exp",
  "log",
  "ln",
  "log10",
  "sign",
  "sqrt",
  "erf",
  "cos",
  "cosh",
  "acos",
  "acosh",
  "arccos",
  "arccosh",
  "cot",
  "coth",
  "acot",
  "acoth",
  "arccot",
  "arccoth",
  "csc",
  "csch",
  "acsc",
  "acsch",
  "arccsc",
  "arccsch",
  "sec",
  "sech",
  "asec",
  "asech",
  "arcsec",
  "arcsech",
  "sin",
  "sinh",
  "asin",
  "asinh",
  "arcsin",
  "arcsinh",
  "tan",
  "tanh",
  "atan",
  "atan2",
  "atanh",
  "arctan",
  "arctanh",
  "arg",
  "min",
  "max",
  "mean",
  "median",
  "floor",
  "ceil",
  "round",
  "sum",
  "prod",
  "variance",
  "std",
  "count",
  "mod",
  "Re",
  "Im",
  "det",
  "trace",
  "nPr",
  "nCr"
];
let allowedLatexSymbols = [
  "alpha",
  "beta",
  "gamma",
  "Gamma",
  "delta",
  "Delta",
  "epsilon",
  "zeta",
  "eta",
  "theta",
  "Theta",
  "iota",
  "kappa",
  "lambda",
  "Lambda",
  "mu",
  "nu",
  "xi",
  "Xi",
  "pi",
  "Pi",
  "rho",
  "sigma",
  "Sigma",
  "tau",
  "Tau",
  "upsilon",
  "Upsilon",
  "phi",
  "Phi",
  "chi",
  "psi",
  "Psi",
  "omega",
  "Omega",
  "partial",
  "varnothing",
  "emptyset",
  "angle",
  "circ",
  "$",
  "%"
];
var textToAst = new me$3.converters.textToAstObj({
  appliedFunctionSymbols: appliedFunctionSymbolsDefault
});
function textToMathFactory({
  functionSymbols,
  appliedFunctionSymbols = appliedFunctionSymbolsDefault,
  splitSymbols = true,
  parseScientificNotation = false
} = {}) {
  return (x2) => me$3.fromAst(
    new me$3.converters.textToAstObj({
      appliedFunctionSymbols,
      functionSymbols,
      splitSymbols,
      parseScientificNotation
    }).convert(x2)
  );
}
var latexToAst = new me$3.converters.latexToAstObj({
  appliedFunctionSymbols: appliedFunctionSymbolsDefaultLatex,
  allowedLatexSymbols
});
function latexToMathFactory({
  functionSymbols,
  appliedFunctionSymbols = appliedFunctionSymbolsDefaultLatex,
  splitSymbols = true,
  parseScientificNotation = false
} = {}) {
  if (splitSymbols) {
    return (x2) => me$3.fromAst(
      new me$3.converters.latexToAstObj({
        appliedFunctionSymbols,
        functionSymbols,
        allowedLatexSymbols,
        parseScientificNotation
      }).convert(
        wrapWordIncludingNumberWithVar(x2, parseScientificNotation)
      )
    );
  } else {
    return (x2) => me$3.fromAst(
      new me$3.converters.latexToAstObj({
        appliedFunctionSymbols,
        functionSymbols,
        allowedLatexSymbols,
        parseScientificNotation
      }).convert(wrapWordWithVar(x2, parseScientificNotation))
    );
  }
}
function findFiniteNumericalValue(value) {
  if (value === void 0) {
    return void 0;
  }
  if (Number.isFinite(value)) {
    return value;
  }
  if (value.evaluate_to_constant !== void 0) {
    value = value.evaluate_to_constant();
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}
function returnNVariables(n2, variablesSpecified) {
  if (!Number.isInteger(n2) || n2 < 1) {
    return [];
  }
  let nVariablesSpecified = variablesSpecified.length;
  if (nVariablesSpecified === 0) {
    if (n2 === 1) {
      return [me$3.fromAst("x")];
    } else if (n2 === 2) {
      return [me$3.fromAst("x"), me$3.fromAst("y")];
    } else if (n2 === 3) {
      return [me$3.fromAst("x"), me$3.fromAst("y"), me$3.fromAst("z")];
    } else {
      let variables2 = [];
      for (let i2 = 1; i2 <= n2; i2++) {
        variables2.push(me$3.fromAst(textToAst.convert(`x_${i2}`)));
      }
      return variables2;
    }
  }
  if (new Set(variablesSpecified.map((x2) => x2.toString())).size < nVariablesSpecified) {
    console.warn("Duplicate variables specified");
  }
  if (n2 <= nVariablesSpecified) {
    return variablesSpecified.slice(0, n2);
  }
  let variablesUsed = [...variablesSpecified.map((x2) => x2.toString())];
  let variables = [...variablesSpecified];
  for (let i2 = nVariablesSpecified + 1; i2 <= n2; i2++) {
    let preferredVariables;
    if (i2 == 1) {
      if (n2 > 3) {
        preferredVariables = ["x_1"];
      } else {
        preferredVariables = ["x"];
      }
    } else if (i2 == 2) {
      if (n2 > 3) {
        preferredVariables = ["x_2", "y_2"];
      } else {
        preferredVariables = ["y", "x_2"];
      }
    } else if (i2 == 3) {
      if (n2 > 3) {
        preferredVariables = ["x_3", "y_3", "z_3"];
      } else {
        preferredVariables = ["z", "x_3", "z_3"];
      }
    } else {
      preferredVariables = [
        "x",
        "y",
        "z",
        "u",
        "v",
        "w",
        "X",
        "Y",
        "Z"
      ].map((x2) => `${x2}_${i2}`);
    }
    let addedVariable = false;
    for (let v2 of preferredVariables) {
      if (!variablesUsed.includes(v2)) {
        variables.push(me$3.fromAst(textToAst.convert(v2)));
        variablesUsed.push(v2);
        addedVariable = true;
        break;
      }
    }
    if (!addedVariable) {
      let v2 = preferredVariables[0];
      variables.push(me$3.fromAst(textToAst.convert(v2)));
      variablesUsed.push(v2);
      console.warn(`Variables added were not unique`);
    }
  }
  return variables;
}
function normalizeLatexString(latexString, { unionFromU = false } = {}) {
  let substitutions = [
    ["α", "\\alpha "],
    // 'α'
    ["β", "\\beta "],
    // 'β'
    ["ϐ", "\\beta "],
    // 'ϐ'
    ["Γ", "\\Gamma "],
    // 'Γ'
    ["γ", "\\gamma "],
    // 'γ'
    ["Δ", "\\Delta "],
    // 'Δ'
    ["δ", "\\delta "],
    // 'δ'
    ["ε", "\\epsilon "],
    // 'ε' should this be varepsilon?
    ["ϵ", "\\epsilon "],
    // 'ϵ'
    ["ζ", "\\zeta "],
    // 'ζ'
    ["η", "\\eta "],
    // 'η'
    ["Θ", "\\Theta "],
    // 'Θ'
    ["ϴ", "\\Theta "],
    // 'ϴ'
    ["θ", "\\theta "],
    // 'θ'
    ["ᶿ", "\\theta "],
    // 'ᶿ'
    ["ϑ", "\\theta "],
    // 'ϑ'
    ["ι", "\\iota "],
    // 'ι'
    ["κ", "\\kappa "],
    // 'κ'
    ["Λ", "\\Lambda "],
    // 'Λ'
    ["λ", "\\lambda "],
    // 'λ'
    ["μ", "\\mu "],
    // 'μ'
    ["µ", "\\mu "],
    // 'µ' should this be micro?
    ["ν", "\\nu "],
    // 'ν'
    ["Ξ", "\\Xi "],
    // 'Ξ'
    ["ξ", "\\xi "],
    // 'ξ'
    ["Π", "\\Pi "],
    // 'Π'
    ["π", "\\pi "],
    // 'π'
    ["ϖ", "\\pi "],
    // 'ϖ' should this be varpi?
    ["ρ", "\\rho "],
    // 'ρ'
    ["ϱ", "\\rho "],
    // 'ϱ' should this be varrho?
    ["Σ", "\\Sigma "],
    // 'Σ'
    ["σ", "\\sigma "],
    // 'σ'
    ["ς", "\\sigma "],
    // 'ς' should this be varsigma?
    ["τ", "\\tau "],
    // 'τ'
    ["Υ", "\\Upsilon "],
    // 'Υ'
    ["υ", "\\upsilon "],
    // 'υ'
    ["Φ", "\\Phi "],
    // 'Φ'
    ["φ", "\\phi "],
    // 'φ' should this be varphi?
    ["ϕ", "\\phi "],
    // 'ϕ'
    ["Ψ", "\\Psi "],
    // 'Ψ'
    ["ψ", "\\psi "],
    // 'ψ'
    ["Ω", "\\Omega "],
    // 'Ω'
    ["ω", "\\omega "],
    // 'ω'
    ["−", "-"],
    // minus sign
    ["⋅", " \\cdot "],
    // dot operator
    ["·", " \\cdot "],
    // middle dot
    ["×", " \\times "],
    // times operator
    ["∪", " \\cup "],
    // ∪
    ["∩", " \\cap "],
    // ∩
    ["∞", " \\infty "],
    // ∞
    ["∅", " \\emptyset "],
    // ∅
    ["′", "'"],
    // ′
    ["⁡", " "],
    // function application
    ["⁢", " "],
    // invisible times
    ["\\\\text(?=\\{)", "\\operatorname"]
  ];
  for (let sub2 of substitutions) {
    latexString = latexString.replace(new RegExp(sub2[0], "g"), sub2[1]);
  }
  let startLdotsMatch = latexString.match(
    /^(\\ )*(\\ldots|\.(\\ )*\.(\\ )*\.)(\\ )*(.*)$/
  );
  if (startLdotsMatch) {
    let afterLdots = startLdotsMatch[6];
    if (afterLdots[0] !== ",") {
      latexString = "\\ldots," + afterLdots;
    } else {
      latexString = "\\ldots" + afterLdots;
    }
  }
  let endLdotsMatch = latexString.match(
    /^(.*?)(\\ )*(\\ldots|\.(\\ )*\.(\\ )*\.)(\\ )*$/
  );
  if (endLdotsMatch) {
    let beforeLdots = endLdotsMatch[1];
    if (beforeLdots[beforeLdots.length - 1] !== ",") {
      latexString = beforeLdots + ",\\ldots";
    } else {
      latexString = beforeLdots + "\\ldots";
    }
  }
  latexString = latexString.replace(/(\b|\\ )or(\b|\\ )/g, "$1\\lor$2");
  latexString = latexString.replace(/(\b|\\ )and(\b|\\ )/g, "$1\\land$2");
  if (unionFromU) {
    latexString = latexString.replace(/(\b|\\ )U(\b|\\ )/g, "$1\\cup$2");
  }
  latexString = removeRepeatedSuperSubScripts(latexString);
  return latexString;
}
function isValidVariable(expression) {
  let tree = expression.tree;
  let validVariable = true;
  if (typeof tree === "string") {
    if (tree === "＿") {
      validVariable = false;
    }
  } else if (!Array.isArray(tree) || tree[0] !== "_" || typeof tree[1] !== "string" || typeof tree[2] !== "string" && typeof tree[2] !== "number") {
    validVariable = false;
  }
  return validVariable;
}
function mathStateVariableFromNumberStateVariable({
  numberVariableName = "number",
  mathVariableName = "math",
  isPublic = false
} = {}) {
  let mathDef = {
    returnDependencies: () => ({
      number: {
        dependencyType: "stateVariable",
        variableName: numberVariableName
      }
    }),
    definition: function({ dependencyValues }) {
      return {
        setValue: {
          [mathVariableName]: numberToMathExpression(
            dependencyValues.number
          )
        }
      };
    },
    inverseDefinition: function({ desiredStateVariableValues }) {
      let desiredMath = desiredStateVariableValues[mathVariableName];
      let desiredNumber;
      if (desiredMath instanceof me$3.class) {
        desiredNumber = desiredMath.evaluate_to_constant();
      } else if (typeof desiredMath === "number") {
        desiredNumber = desiredMath;
      } else {
        desiredNumber = NaN;
      }
      return {
        success: true,
        instructions: [
          {
            setDependency: "number",
            desiredValue: desiredNumber
          }
        ]
      };
    }
  };
  if (isPublic) {
    mathDef.public = true;
    mathDef.shadowingInstructions = { createComponentOfType: "math" };
  }
  return mathDef;
}
function numberToMathExpression(number) {
  let mathTree;
  if (typeof number === "number") {
    mathTree = number;
  } else if (number === null) {
    mathTree = "＿";
  } else {
    if (number.im === 0) {
      mathTree = number.re;
    } else {
      let imPart;
      if (number.im === 1) {
        imPart = "i";
      } else if (number.im === -1) {
        imPart = ["-", "i"];
      } else {
        imPart = ["*", number.im, "i"];
      }
      if (number.re === 0) {
        mathTree = imPart;
      } else {
        mathTree = ["+", number.re, imPart];
      }
    }
  }
  return me$3.fromAst(mathTree);
}
function mergeListsWithOtherContainers(tree) {
  if (!Array.isArray(tree)) {
    return tree;
  }
  let operator = tree[0];
  let operands = tree.slice(1);
  if ([...vectorOperators, "list", "set"].includes(operator)) {
    operands = operands.reduce(
      (a2, c2) => Array.isArray(c2) && c2[0] === "list" ? [...a2, ...c2.slice(1)] : [...a2, c2],
      []
    );
  }
  operands = operands.map((x2) => mergeListsWithOtherContainers(x2));
  return [operator, ...operands];
}
function wrapWordWithVar(string, parseScientificNotation) {
  let newString = "";
  let regex = /\\operatorname\s*{[^{}]*}/;
  let match = string.match(regex);
  while (match && match.index !== void 0) {
    let beginMatch = match.index;
    let endMatch = beginMatch + match[0].length;
    newString += wrapWordWithVarSub(
      string.substring(0, beginMatch),
      parseScientificNotation
    );
    newString += string.substring(beginMatch, endMatch);
    string = string.substring(endMatch);
    match = string.match(regex);
  }
  newString += wrapWordWithVarSub(string, parseScientificNotation);
  return newString;
}
function wrapWordWithVarSub(string, parseScientificNotation) {
  let newString = "";
  const regex = /([^a-zA-Z0-9]?)([a-zA-Z][a-zA-Z0-9]+)([^a-zA-Z0-9]?)/;
  let regexSN;
  if (parseScientificNotation) {
    const sci_notat_exp_regex = "(E[+\\-]?[0-9]+\\s*($|(?=\\,|&|\\||\\\\\\||\\)|\\}|\\\\}|\\]|\\\\\\\\|\\\\end)))";
    regexSN = new RegExp(
      "([0-9]+(\\.[0-9]*)?" + sci_notat_exp_regex + ")|(\\.[0-9]+" + sci_notat_exp_regex + ")"
    );
  }
  let match = string.match(regex);
  while (match && match.index !== void 0) {
    let beginMatch = match.index;
    let endMatch = beginMatch + match[0].length - match[3].length;
    if (regexSN) {
      let matchSN = string.match(regexSN);
      if (matchSN && matchSN.index !== void 0 && matchSN.index < endMatch && matchSN.index + matchSN[0].length > beginMatch) {
        newString += string.substring(0, endMatch);
        string = string.substring(endMatch);
        match = string.match(regex);
        continue;
      }
    }
    if (match[1] === "\\") {
      newString += string.substring(0, endMatch);
      string = string.substring(endMatch);
    } else {
      let beginWord = beginMatch + match[1].length;
      newString += string.substring(0, beginWord);
      newString += `\\operatorname{${match[2]}}`;
      string = string.substring(endMatch);
    }
    match = string.match(regex);
  }
  newString += string;
  return newString;
}
function wrapWordIncludingNumberWithVar(string, parseScientificNotation) {
  let newString = "";
  let regex = /\\operatorname\s*{[^{}]*}/;
  let match = string.match(regex);
  while (match && match.index !== void 0) {
    let beginMatch = match.index;
    let endMatch = beginMatch + match[0].length;
    newString += wrapWordIncludingNumberWithVarSub(
      string.substring(0, beginMatch),
      parseScientificNotation
    );
    newString += string.substring(beginMatch, endMatch);
    string = string.substring(endMatch);
    match = string.match(regex);
  }
  newString += wrapWordIncludingNumberWithVarSub(
    string,
    parseScientificNotation
  );
  return newString;
}
function wrapWordIncludingNumberWithVarSub(string, parseScientificNotation) {
  let newString = "";
  const regex = /([^a-zA-Z0-9\s]?\s*)([a-zA-Z][a-zA-Z0-9]*[0-9][a-zA-Z0-9]*)([^a-zA-Z0-9]?)/;
  let regexSN;
  if (parseScientificNotation) {
    const sci_notat_exp_regex = "(E[+\\-]?[0-9]+\\s*($|(?=\\,|&|\\||\\\\\\||\\)|\\}|\\\\}|\\]|\\\\\\\\|\\\\end)))";
    regexSN = new RegExp(
      "([0-9]+(\\.[0-9]*)?" + sci_notat_exp_regex + ")|(\\.[0-9]+" + sci_notat_exp_regex + ")"
    );
  }
  let match = string.match(regex);
  while (match && match.index !== void 0) {
    let beginMatch = match.index;
    let endMatch = beginMatch + match[0].length - match[3].length;
    if (regexSN) {
      let matchSN = string.match(regexSN);
      if (matchSN && matchSN.index !== void 0 && matchSN.index < endMatch && matchSN.index + matchSN[0].length > beginMatch) {
        newString += string.substring(0, endMatch);
        string = string.substring(endMatch);
        match = string.match(regex);
        continue;
      }
    }
    if (match[1] === "\\" || match[1][0] === "^" || match[1][0] === "_") {
      newString += string.substring(0, endMatch);
      string = string.substring(endMatch);
    } else {
      let beginWord = beginMatch + match[1].length;
      newString += string.substring(0, beginWord);
      newString += `\\operatorname{${match[2]}}`;
      string = string.substring(endMatch);
    }
    match = string.match(regex);
  }
  newString += string;
  return newString;
}
function stripLatex(latex) {
  return latex.replace(/\\,/g, "").replace(/\\operatorname{([^{}]*)}/g, "$1");
}
function superSubscriptsToUnicode(text) {
  let charToSubscriptUnicode = {
    0: "₀",
    1: "₁",
    2: "₂",
    3: "₃",
    4: "₄",
    5: "₅",
    6: "₆",
    7: "₇",
    8: "₈",
    9: "₉",
    "+": "₊",
    "-": "₋",
    " ": ""
  };
  let charToSuperscriptUnicode = {
    0: "⁰",
    1: "¹",
    2: "²",
    3: "³",
    4: "⁴",
    5: "⁵",
    6: "⁶",
    7: "⁷",
    8: "⁸",
    9: "⁹",
    "+": "⁺",
    "-": "⁻",
    " ": ""
  };
  function replaceSubscripts(match, p1) {
    let newVal = "";
    for (let char of p1) {
      newVal += charToSubscriptUnicode[char];
    }
    return newVal;
  }
  function replaceSuperscripts(match, p1) {
    let newVal = "";
    for (let char of p1) {
      newVal += charToSuperscriptUnicode[char];
    }
    return newVal;
  }
  text = text.replace(/_(\d+)(?!\.)/g, replaceSubscripts);
  text = text.replace(/_\(([\d +-]+)\)/g, replaceSubscripts);
  text = text.replace(/\^(\d+)(?!\.)/g, replaceSuperscripts);
  text = text.replace(/\^\(([\d +-]+)\)/g, replaceSuperscripts);
  return text;
}
function unicodeToSuperSubscripts(text) {
  let subscriptUnicodeToChar = {
    "₀": "0",
    "₁": "1",
    "₂": "2",
    "₃": "3",
    "₄": "4",
    "₅": "5",
    "₆": "6",
    "₇": "7",
    "₈": "8",
    "₉": "9",
    "₊": "+",
    "₋": "-"
  };
  let superscriptUnicodeToChar = {
    "⁰": "0",
    "¹": "1",
    "²": "2",
    "³": "3",
    "⁴": "4",
    "⁵": "5",
    "⁶": "6",
    "⁷": "7",
    "⁸": "8",
    "⁹": "9",
    "⁺": "+",
    "⁻": "-"
  };
  function replaceSubscripts(match, p1) {
    let newVal = "";
    for (let char of p1) {
      newVal += subscriptUnicodeToChar[char];
    }
    return "_(" + newVal + ")";
  }
  function replaceSuperscripts(match, p1) {
    let newVal = "";
    for (let char of p1) {
      newVal += superscriptUnicodeToChar[char];
    }
    return "^(" + newVal + ")";
  }
  text = text.replace(
    /([\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089\u208A\u208B]+)/g,
    replaceSubscripts
  );
  text = text.replace(
    /([\u2070\u00B9\u00B2\u00B3\u2074\u2075\u2076\u2077\u2078\u2079\u207A\u207B]+)/g,
    replaceSuperscripts
  );
  return text;
}
function removeFunctionsMathExpressionClass(value) {
  if (value instanceof me$3.class) {
    value = value.tree;
  } else if (typeof value === "function") {
    value = void 0;
  } else if (Array.isArray(value)) {
    value = value.map((x2) => removeFunctionsMathExpressionClass(x2));
  } else if (typeof value === "object" && value !== null && !subsetOfReals.isSubset(value)) {
    let valueCopy = {};
    for (let key in value) {
      valueCopy[key] = removeFunctionsMathExpressionClass(value[key]);
    }
    value = valueCopy;
  }
  return value;
}
async function preprocessMathInverseDefinition({
  desiredValue,
  stateValues,
  variableName = "value",
  arrayKey,
  workspace,
  listIndex = []
}) {
  if (desiredValue.tree[0] === "list") {
    let newAst = ["list"];
    for (let [ind, entry] of desiredValue.tree.slice(1).entries()) {
      let newEntryResult = await preprocessMathInverseDefinition({
        desiredValue: me$3.fromAst(entry),
        stateValues,
        variableName,
        arrayKey,
        workspace,
        listIndex: [...listIndex, ind]
      });
      newAst.push(newEntryResult.desiredValue.tree);
    }
    return { desiredValue: me$3.fromAst(newAst) };
  }
  if (!vectorOperators.includes(desiredValue.tree[0]) || !desiredValue.tree.includes()) {
    return { desiredValue };
  }
  let valueAst;
  let workspaceKey = variableName + "Ast";
  if (arrayKey !== void 0) {
    workspaceKey += `_${arrayKey}`;
  }
  if (listIndex.length > 0) {
    workspaceKey += `_list_` + listIndex.join("_");
  }
  if (workspace[workspaceKey]) {
    valueAst = workspace[workspaceKey].slice(0, desiredValue.tree.length);
  } else {
    let currentValue = await stateValues[variableName];
    if (currentValue) {
      currentValue = currentValue.expand().simplify();
      if (arrayKey !== void 0 && currentValue[arrayKey]) {
        currentValue = currentValue[arrayKey];
      }
      if (listIndex.length > 0) {
        for (let ind of listIndex) {
          if (currentValue.tree[0] === "list" && currentValue.tree[ind + 1]) {
            currentValue = me$3.fromAst(currentValue.tree[ind + 1]);
          }
        }
      }
      if (vectorOperators.includes(currentValue.tree[0])) {
        valueAst = currentValue.tree.slice(0, desiredValue.tree.length);
      }
    }
  }
  if (valueAst) {
    let vectorComponentsNotAffected = [];
    let foundNotAffected = false;
    for (let [ind, value] of desiredValue.tree.entries()) {
      if (value === void 0) {
        foundNotAffected = true;
        vectorComponentsNotAffected.push(ind);
      } else {
        valueAst[ind] = value;
      }
    }
    desiredValue = me$3.fromAst(valueAst);
    workspace[workspaceKey] = valueAst;
    if (foundNotAffected) {
      return {
        desiredValue,
        vectorComponentsNotAffected
      };
    } else {
      return { desiredValue };
    }
  } else {
    let desiredOperands = [];
    for (let val of desiredValue.tree.slice(1)) {
      if (val === void 0) {
        desiredOperands.push("＿");
      } else {
        desiredOperands.push(val);
      }
    }
    desiredValue = me$3.fromAst([desiredValue.tree[0], ...desiredOperands]);
    return { desiredValue };
  }
}
function latexToText(latex) {
  let expression;
  try {
    expression = me$3.fromAst(latexToAst.convert(latex));
  } catch (e3) {
    return latex;
  }
  return superSubscriptsToUnicode(expression.toString());
}
function textToLatex(text) {
  let expression;
  try {
    expression = me$3.fromAst(textToAst.convert(text));
  } catch (e3) {
    return text;
  }
  return expression.toLatex();
}
function removeRepeatedSuperSubScripts(latex) {
  let newLatex = latex;
  const isWhitespace = (char) => /\s/.test(char);
  const findMatchingBrace = (text, openIndex) => {
    if (text[openIndex] !== "{") {
      return -1;
    }
    let depth = 0;
    for (let i2 = openIndex; i2 < text.length; i2++) {
      if (text[i2] === "{") {
        depth++;
      } else if (text[i2] === "}") {
        depth--;
        if (depth === 0) {
          return i2;
        }
      }
    }
    return -1;
  };
  const removeRepScript = (scriptChar) => {
    let changed = true;
    while (changed) {
      changed = false;
      let i2 = 0;
      while (i2 < newLatex.length) {
        if (newLatex[i2] !== scriptChar) {
          i2++;
          continue;
        }
        let j2 = i2 + 1;
        while (j2 < newLatex.length && isWhitespace(newLatex[j2])) {
          j2++;
        }
        if (newLatex[j2] !== "{") {
          i2++;
          continue;
        }
        let outerOpen = j2;
        let outerClose = findMatchingBrace(newLatex, outerOpen);
        if (outerClose === -1) {
          i2++;
          continue;
        }
        let k2 = outerOpen + 1;
        while (k2 < outerClose && isWhitespace(newLatex[k2])) {
          k2++;
        }
        if (newLatex[k2] !== scriptChar) {
          i2 = outerClose + 1;
          continue;
        }
        let l2 = k2 + 1;
        while (l2 < outerClose && isWhitespace(newLatex[l2])) {
          l2++;
        }
        if (newLatex[l2] !== "{") {
          i2 = outerClose + 1;
          continue;
        }
        let innerOpen = l2;
        let innerClose = findMatchingBrace(newLatex, innerOpen);
        if (innerClose === -1 || innerClose > outerClose) {
          i2 = outerClose + 1;
          continue;
        }
        let trailing = innerClose + 1;
        while (trailing < outerClose && isWhitespace(newLatex[trailing])) {
          trailing++;
        }
        if (trailing !== outerClose) {
          i2 = outerClose + 1;
          continue;
        }
        let innerScript = newLatex.substring(innerOpen + 1, innerClose);
        newLatex = newLatex.substring(0, i2) + `${scriptChar}{${innerScript}}` + newLatex.substring(outerClose + 1);
        changed = true;
        i2 = i2 + 1 + innerScript.length + 2;
      }
    }
  };
  removeRepScript("^");
  removeRepScript("_");
  return newLatex;
}
const TimerLabels = {
  autoSubmit: "auto-submit answers",
  scheduledSaveState: "scheduled saveState",
  throttledSaveChanges: "throttled saveChangesToDatabase",
  submissionSaveState: "submission saveState",
  visibilityPeriodicSend: "visibility periodic send",
  visibilityResumeSend: "visibility resume send",
  visibilityAutoSuspend: "visibility auto-suspend",
  firstVisibleSend: "first-visible visibility send",
  generateDastSaveState: "saveState (generateDast epilogue)",
  navigateToComponent: "navigate to component"
};
function reportTimerError(label) {
  return (error) => {
    console.error(`Error in ${label}:`, error);
  };
}
function getSourceLocationForComponent(component, components) {
  let position = component.position;
  let sourceDoc = component.sourceDoc;
  let comp = component;
  while (position === void 0) {
    if (!(comp.parentIdx > 0)) {
      break;
    }
    comp = components[comp.parentIdx];
    position = comp.position;
    sourceDoc = comp.sourceDoc;
  }
  return { position, sourceDoc };
}
const TAG_NAME_REGEX = /^[A-Za-z0-9_:-]+/;
function narrowPositionToOpeningTag(position, source) {
  if (!position || !source) {
    return position;
  }
  const startOffset = position.start.offset;
  if (typeof startOffset !== "number" || source[startOffset] !== "<") {
    return position;
  }
  const endOffsetLimit = typeof position.end.offset === "number" ? position.end.offset : source.length;
  const searchWindow = source.slice(startOffset + 1, endOffsetLimit);
  const match = TAG_NAME_REGEX.exec(searchWindow);
  if (!match) {
    return position;
  }
  const tagNameLength = 1 + match[0].length;
  return {
    start: position.start,
    end: {
      line: position.start.line,
      column: position.start.column + tagNameLength,
      offset: startOffset + tagNameLength
    }
  };
}
function convertToErrorComponent(component, eOrMessage) {
  const message = typeof eOrMessage === "string" ? eOrMessage : typeof eOrMessage === "object" && eOrMessage !== null && "message" in eOrMessage && typeof eOrMessage.message === "string" ? eOrMessage.message : "An error occurred";
  const newComponent = {
    type: "serialized",
    componentType: "_error",
    componentIdx: component.componentIdx,
    state: { message },
    children: [],
    attributes: {},
    doenetAttributes: {},
    position: component.position,
    sourceDoc: component.sourceDoc
  };
  if (component.attributes.name) {
    let nameAttribute;
    if (component.type === "serialized") {
      nameAttribute = component.attributes.name;
    } else {
      if (component.attributes.name.children?.length === 1 && typeof component.attributes.name.children[0] === "string") {
        nameAttribute = {
          type: "primitive",
          name: "name",
          primitive: {
            type: "string",
            value: component.attributes.name.children[0]
          }
        };
      }
    }
    if (nameAttribute) {
      newComponent.attributes.name = nameAttribute;
    }
  }
  return { component: newComponent, message };
}
function removeBlankStringChildren(serializedComponents, componentInfoObjects) {
  const newComponents = [];
  for (let component of serializedComponents) {
    if (typeof component === "string") {
      newComponents.push(component);
    } else {
      const newComponent = { ...component };
      let componentClass = componentInfoObjects.allComponentClasses[component.componentType];
      if (componentClass && !componentClass.includeBlankStringChildren) {
        newComponent.children = component.children.filter(
          (x2) => typeof x2 !== "string" || x2.trim() !== ""
        );
      }
      newComponent.children = removeBlankStringChildren(
        newComponent.children,
        componentInfoObjects
      );
      newComponent.attributes = { ...newComponent.attributes };
      for (let attrName in newComponent.attributes) {
        const attribute = { ...newComponent.attributes[attrName] };
        if (attribute.type === "component") {
          attribute.component = removeBlankStringChildren(
            [attribute.component],
            componentInfoObjects
          )[0];
          newComponent.attributes[attrName] = attribute;
        }
      }
      newComponents.push(newComponent);
    }
  }
  return newComponents;
}
function decodeXMLEntities(serializedComponents) {
  function replaceEntities(s2) {
    return s2.replace(/&apos;/g, "'").replace(/&quot;/g, '"').replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/&dollar;/g, "$").replace(/&amp;/g, "&");
  }
  const newComponents = [];
  for (let component of serializedComponents) {
    if (typeof component === "string") {
      newComponents.push(replaceEntities(component));
    } else {
      const newComponent = { ...component };
      newComponent.children = decodeXMLEntities(component.children);
      const attributes = { ...component.attributes };
      for (let attrName in attributes) {
        let attribute = { ...attributes[attrName] };
        if (attribute.type === "component") {
          attribute.component = decodeXMLEntities([
            attribute.component
          ])[0];
        } else if (attribute.type === "primitive") {
          if (attribute.primitive.type === "string") {
            attribute.primitive.value = replaceEntities(
              attribute.primitive.value
            );
          }
        } else ;
        attributes[attrName] = attribute;
      }
      newComponent.attributes = attributes;
      newComponents.push(newComponent);
    }
  }
  return newComponents;
}
function isUnflattenedComponent(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && typedObj.type === "unflattened" && typeof typedObj.componentType === "string" && typeof typedObj.componentIdx === "number" && Array.isArray(typedObj.children) && typedObj.children.every(
    (child) => typeof child === "string" || isUnflattenedComponent(child)
  ) && typeof typedObj.attributes === "object" && typedObj.attributes !== null && Object.values(typedObj.attributes).every(
    (attr) => isUnflattenedAttribute(attr)
  ) && typeof typedObj.state === "object" && typedObj.state !== null;
}
function isUnflattenedAttribute(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && typeof typedObj.name === "string" && Array.isArray(typedObj.children) && typedObj.children.every(
    (child) => typeof child === "string" || isUnflattenedComponent(child)
  );
}
function isSerializedComponent(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && typedObj.type === "serialized" && typeof typedObj.componentType === "string" && typeof typedObj.componentIdx === "number" && Array.isArray(typedObj.children) && typedObj.children.every(
    (child) => typeof child === "string" || isSerializedComponent(child)
  ) && typeof typedObj.attributes === "object" && typedObj.attributes !== null && Object.values(typedObj.attributes).every(
    (attr) => isSerializedAttribute(attr)
  ) && typeof typedObj.state === "object" && typedObj.state !== null && typeof typedObj.doenetAttributes === "object" && typedObj.doenetAttributes !== null;
}
function isPrimitiveAttributeValue(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && (typedObj.type === "string" && typeof typedObj.value === "string" || typedObj.type === "boolean" && typeof typedObj.value === "boolean" || typedObj.type === "number" && typeof typedObj.value === "number" || typedObj.type === "numberArray" && Array.isArray(typedObj.value) && typedObj.value.every((val) => typeof val === "number") || typedObj.type === "stringArray" && Array.isArray(typedObj.value) && typedObj.value.every((val) => typeof val === "string"));
}
function isComponentAttribute(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && typedObj.type === "component" && typeof typedObj.name === "string" && isSerializedComponent(typedObj.component) && (typedObj.ignoreFixed === void 0 || typeof typedObj.ignoreFixed === "boolean") && (typedObj.sourceDoc === void 0 || typeof typedObj.sourceDoc === "number");
}
function isPrimitiveAttribute(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && typedObj.type === "primitive" && typeof typedObj.name === "string" && isPrimitiveAttributeValue(typedObj.primitive) && (typedObj.ignoreFixed === void 0 || typeof typedObj.ignoreFixed === "boolean") && (typedObj.sourceDoc === void 0 || typeof typedObj.sourceDoc === "number");
}
function isReferencesAttribute(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && typedObj.type === "references" && typeof typedObj.name === "string" && Array.isArray(typedObj.references) && typedObj.references.every(
    (reference) => isSerializedComponent(reference)
  ) && (typedObj.ignoreFixed === void 0 || typeof typedObj.ignoreFixed === "boolean") && (typedObj.sourceDoc === void 0 || typeof typedObj.sourceDoc === "number");
}
function isUnresolvedAttribute(obj) {
  const typedObj = obj;
  return typeof typedObj === "object" && typedObj !== null && typedObj.type === "unresolved" && typeof typedObj.name === "string" && Array.isArray(typedObj.children) && typedObj.children.every(
    (child) => typeof child === "string" || isUnflattenedComponent(child)
  ) && (typedObj.ignoreFixed === void 0 || typeof typedObj.ignoreFixed === "boolean") && (typedObj.sourceDoc === void 0 || typeof typedObj.sourceDoc === "number");
}
function isSerializedAttribute(obj) {
  return isComponentAttribute(obj) || isPrimitiveAttribute(obj) || isReferencesAttribute(obj) || isUnresolvedAttribute(obj);
}
function applySugar({
  serializedComponents,
  parentParametersFromSugar = {},
  parentAttributes = {},
  componentInfoObjects,
  isAttributeComponent = false,
  nComponents,
  stateIdInfo
}) {
  let diagnostics = [];
  const newComponents = [];
  for (let component of serializedComponents) {
    if (typeof component === "string") {
      newComponents.push(component);
      continue;
    }
    try {
      let componentType = component.componentType;
      let componentClass = componentInfoObjects.allComponentClasses[componentType];
      if (!componentClass) {
        throw Error(`Unrecognized component type ${componentType}.`);
      }
      let componentAttributes = {};
      for (let attrName in component.attributes) {
        let attribute = component.attributes[attrName];
        if (attribute.type === "primitive") {
          componentAttributes[attrName] = attribute.primitive;
        }
      }
      let newParentParametersFromSugar = {};
      let newComponent = component;
      if (!component.skipSugar) {
        for (let sugarInstruction of componentClass.returnSugarInstructions()) {
          let childTypes = component.children.map((x2) => typeof x2 === "string" ? "s" : "n").join("");
          if (sugarInstruction.childrenRegex) {
            let match = childTypes.match(
              sugarInstruction.childrenRegex
            );
            if (!match || match[0].length !== component.children.length) {
              continue;
            }
          }
          let matchedChildren = deepClone(component.children, void 0, void 0);
          let nNonStrings = 0;
          for (let child of matchedChildren) {
            if (typeof child !== "string") {
              child.preSugarInd = nNonStrings;
              nNonStrings++;
            }
          }
          let sugarResults = sugarInstruction.replacementFunction({
            matchedChildren,
            parentParametersFromSugar,
            parentAttributes,
            componentAttributes,
            componentInfoObjects,
            isAttributeComponent,
            nComponents,
            stateIdInfo
          });
          if (sugarResults.diagnostics) {
            for (const d2 of sugarResults.diagnostics) {
              diagnostics.push({
                ...d2,
                position: component.position,
                sourceDoc: component.sourceDoc
              });
            }
          }
          if (sugarResults.success) {
            if (!Number.isInteger(sugarResults.nComponents)) {
              console.error(
                `Invalid sugar from ${componentClass.componentType}: did not return integer nComponents`
              );
              throw Error(
                `Invalid sugar from ${componentClass.componentType}: did not return integer nComponents`
              );
            }
            nComponents = sugarResults.nComponents;
            let newChildren = sugarResults.newChildren;
            let newAttributes = sugarResults.newAttributes;
            let preSugarIndsFoundInChildren = [];
            let preSugarIndsFoundInAttributes = [];
            if (newChildren) {
              preSugarIndsFoundInChildren = findPreSugarIndsAndMarkFromSugar(newChildren);
            }
            if (newAttributes) {
              for (let attrName in newAttributes) {
                let comp = newAttributes[attrName].component;
                if (comp) {
                  preSugarIndsFoundInAttributes.push(
                    ...findPreSugarIndsAndMarkFromSugar(
                      comp.children
                    )
                  );
                }
              }
            }
            let preSugarIndsFound = [
              ...preSugarIndsFoundInChildren,
              ...preSugarIndsFoundInAttributes
            ];
            if (preSugarIndsFound.length !== nNonStrings || !preSugarIndsFound.sort((a2, b2) => a2 - b2).every((v2, i2) => v2 === i2)) {
              throw Error(
                `Invalid sugar for ${componentType} as didn't return set of original components`
              );
            }
            if (sugarResults.parametersForChildrenSugar) {
              Object.assign(
                newParentParametersFromSugar,
                sugarResults.parametersForChildrenSugar
              );
            }
            newComponent = { ...newComponent };
            if (newChildren) {
              if (newChildren.every(
                (child) => isSerializedComponent(child) || typeof child === "string"
              )) {
                newComponent.children = newChildren;
              } else if (newChildren.every(
                (child) => isUnflattenedComponent(child) || typeof child === "string"
              )) {
                const expandResult = expandUnflattenedToSerializedComponents({
                  serializedComponents: newChildren,
                  componentInfoObjects,
                  nComponents,
                  stateIdInfo
                });
                newComponent.children = expandResult.components;
                diagnostics.push(...expandResult.diagnostics);
                nComponents = expandResult.nComponents;
              } else {
                console.log(
                  `Invalid newChildren returned from sugar by ${componentClass.componentType}`,
                  newChildren
                );
                throw Error(
                  `Invalid newChildren returned from sugar by ${componentClass.componentType}`
                );
              }
            } else {
              newComponent.children = [];
            }
            if (newAttributes) {
              newComponent.attributes = {
                ...newComponent.attributes
              };
              if (Object.values(newAttributes).every(
                (attr) => isSerializedAttribute(attr)
              )) {
                Object.assign(
                  newComponent.attributes,
                  newAttributes
                );
              } else if (Object.values(newAttributes).every(
                (attr) => isUnflattenedAttribute(attr)
              )) {
                const expandResult = expandAllUnflattenedAttributes({
                  unflattenedAttributes: newAttributes,
                  componentClass,
                  componentInfoObjects,
                  nComponents,
                  stateIdInfo
                });
                Object.assign(
                  newComponent.attributes,
                  expandResult.attributes
                );
                diagnostics.push(...expandResult.diagnostics);
                nComponents = expandResult.nComponents;
              } else {
                console.log(
                  `Invalid newAttributes returned from sugar by ${componentClass.componentType}`,
                  newAttributes
                );
                throw Error(
                  `Invalid newAttributes returned from sugar by ${componentClass.componentType}`
                );
              }
            }
          }
        }
      }
      if (componentClass.removeBlankStringChildrenPostSugar) {
        newComponent.children = newComponent.children.filter(
          (x2) => typeof x2 !== "string" || /\S/.test(x2)
        );
      }
      let res = applySugar({
        serializedComponents: newComponent.children,
        parentParametersFromSugar: newParentParametersFromSugar,
        parentAttributes: componentAttributes,
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      newComponent.children = res.components;
      diagnostics.push(...res.diagnostics);
      nComponents = res.nComponents;
      for (const attrName in newComponent.attributes) {
        const attribute = newComponent.attributes[attrName];
        if (attribute.type === "component") {
          const res2 = applySugar({
            serializedComponents: [attribute.component],
            parentAttributes: componentAttributes,
            componentInfoObjects,
            isAttributeComponent: true,
            nComponents,
            stateIdInfo
          });
          attribute.component = res2.components[0];
          diagnostics.push(...res2.diagnostics);
          nComponents = res2.nComponents;
        }
      }
      newComponents.push(newComponent);
    } catch (e3) {
      console.error(e3);
      const convertResult = convertToErrorComponent(component, e3);
      newComponents.push(convertResult.component);
      diagnostics.push({
        type: "error",
        message: convertResult.message,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
    }
  }
  return { diagnostics, components: newComponents, nComponents };
}
function findPreSugarIndsAndMarkFromSugar(components) {
  const preSugarIndsFound = [];
  for (const component of components) {
    if (typeof component !== "object") {
      continue;
    }
    if (component.preSugarInd !== void 0) {
      preSugarIndsFound.push(component.preSugarInd);
    } else {
      if (!component.doenetAttributes) {
        component.doenetAttributes = {};
      }
      component.doenetAttributes.createdFromSugar = true;
      if (component.children) {
        const inds = findPreSugarIndsAndMarkFromSugar(
          component.children
        );
        preSugarIndsFound.push(...inds);
      }
    }
  }
  return preSugarIndsFound;
}
function convertRefsToCopies({
  serializedComponents,
  nComponents,
  componentInfoObjects,
  allNodes,
  addNodesToResolver
}) {
  const newComponents = [];
  for (const component of serializedComponents) {
    if (typeof component === "string") {
      newComponents.push(component);
      continue;
    }
    let newComponent = { ...component };
    let res = convertRefsToCopies({
      serializedComponents: newComponent.children,
      nComponents,
      componentInfoObjects,
      allNodes,
      addNodesToResolver
    });
    newComponent.children = res.components;
    nComponents = res.nComponents;
    const newAttributes = {};
    for (const attrName in newComponent.attributes) {
      const attribute = { ...newComponent.attributes[attrName] };
      res = convertRefsToCopies({
        serializedComponents: attribute.children,
        nComponents,
        componentInfoObjects,
        allNodes,
        addNodesToResolver
      });
      attribute.children = res.components;
      nComponents = res.nComponents;
      newAttributes[attrName] = attribute;
    }
    newComponent.attributes = newAttributes;
    if (!newComponent.extending) {
      newComponents.push(newComponent);
      continue;
    }
    const extending = newComponent.extending;
    const refResolution = unwrapSource(extending);
    if (newComponent.componentType === "evaluate" && newComponent.children.length === 1 && newComponent.children[0].componentType === "ol") {
      const evalResult = convertEvaluate({
        evaluateComponent: newComponent,
        refResolution,
        nComponents,
        componentInfoObjects,
        allNodes,
        addNodesToResolver
      });
      nComponents = evalResult.nComponents;
      newComponents.push(evalResult.newComponent);
      continue;
    }
    const outerAttributes = { ...newComponent.attributes };
    newComponent.attributes = {};
    let wrappingComponent = null;
    if ("ExtendAttribute" in extending || "CopyAttribute" in extending) {
      if (newComponent.componentType.endsWith("List") && !["asList", "convertSetToList"].includes(
        newComponent.componentType
      )) {
        wrappingComponent = newComponent;
        wrappingComponent.attributes = outerAttributes;
        newComponent = {
          type: "unflattened",
          componentType: "_copy",
          componentIdx: nComponents++,
          attributes: {},
          doenetAttributes: {
            forList: wrappingComponent.componentIdx
          },
          state: {},
          children: [],
          extending: wrappingComponent.extending
        };
        if (!wrappingComponent.doenetAttributes) {
          wrappingComponent.doenetAttributes = {};
        }
        if (addNodesToResolver) {
          const flatFragment = {
            type: "flatFragment",
            children: [newComponent.componentIdx],
            nodes: [
              {
                type: "element",
                name: newComponent.componentType,
                parent: wrappingComponent.componentIdx,
                children: [],
                attributes: [],
                idx: newComponent.componentIdx,
                sourceDoc: wrappingComponent.sourceDoc
              }
            ],
            parentIdx: wrappingComponent.componentIdx,
            parentSourceSequence: null,
            idxMap: { [newComponent.componentIdx]: 0 }
          };
          addNodesToResolver(flatFragment, "None");
        }
        if ("CopyAttribute" in extending) {
          newComponent.attributes.link = {
            name: "link",
            children: ["false"]
          };
          wrappingComponent.doenetAttributes.copyListViaComposite = newComponent.componentIdx;
        } else {
          wrappingComponent.doenetAttributes.extendListViaComposite = newComponent.componentIdx;
        }
        if ("removeEmptyArrayEntries" in wrappingComponent.attributes) {
          newComponent.attributes.removeEmptyArrayEntries = wrappingComponent.attributes.removeEmptyArrayEntries;
          delete wrappingComponent.attributes.removeEmptyArrayEntries;
        }
        delete wrappingComponent.extending;
      } else {
        outerAttributes.createComponentOfType = {
          name: "createComponentOfType",
          children: [newComponent.componentType]
        };
        outerAttributes.createComponentIdx = {
          name: "createComponentIdx",
          children: [newComponent.componentIdx.toString()]
        };
        const originalIdx = newComponent.componentIdx;
        newComponent.componentIdx = nComponents++;
        if (addNodesToResolver) {
          const flatFragment = {
            type: "flatFragment",
            children: [newComponent.componentIdx],
            nodes: [
              {
                type: "element",
                name: newComponent.componentType,
                parent: originalIdx,
                children: [],
                attributes: [],
                idx: newComponent.componentIdx,
                sourceDoc: newComponent.sourceDoc
              }
            ],
            parentIdx: originalIdx,
            parentSourceSequence: null,
            idxMap: { [newComponent.componentIdx]: 0 }
          };
          addNodesToResolver(flatFragment, "None");
        }
        if (outerAttributes.name) {
          outerAttributes.createComponentName = {
            name: "createComponentName",
            children: outerAttributes.name.children
          };
          delete outerAttributes.name;
        }
        outerAttributes.copyInChildren = {
          name: "copyInChildren",
          children: ["true"]
        };
        if ("CopyAttribute" in extending) {
          outerAttributes.link = {
            name: "link",
            children: ["false"]
          };
        }
      }
    }
    newComponent.componentType = "_copy";
    const convertPath = (path_part) => {
      const new_index = path_part.index.map(
        (index2) => {
          const res2 = convertRefsToCopies({
            serializedComponents: index2.value,
            nComponents,
            componentInfoObjects,
            allNodes,
            addNodesToResolver
          });
          nComponents = res2.nComponents;
          if (res2.components.length === 1) {
            const comp = res2.components[0];
            if (typeof comp === "string") {
              return {
                value: [comp],
                position: index2.position,
                sourceDoc: index2.sourceDoc
              };
            } else if (comp.componentType === "number" || comp.componentType === "integer") {
              comp.componentType = "integer";
              return {
                value: [comp],
                position: index2.position,
                sourceDoc: index2.sourceDoc
              };
            } else if (comp.componentType === "_copy" && !comp.attributes.createComponentOfType) {
              comp.attributes.createComponentOfType = {
                name: "createComponentOfType",
                children: ["integer"]
              };
              return {
                value: [comp],
                position: index2.position,
                sourceDoc: index2.sourceDoc
              };
            }
          }
          return {
            value: [
              {
                type: "unflattened",
                componentType: "integer",
                componentIdx: nComponents++,
                attributes: {},
                doenetAttributes: {},
                children: res2.components,
                state: {},
                position: index2.position,
                sourceDoc: index2.sourceDoc
              }
            ],
            position: index2.position,
            sourceDoc: index2.sourceDoc
          };
        }
      );
      return {
        index: new_index,
        name: path_part.name,
        position: path_part.position,
        sourceDoc: path_part.sourceDoc
      };
    };
    const converted_originalPath = refResolution.originalPath.map(convertPath);
    refResolution.originalPath = converted_originalPath;
    if (refResolution.unresolvedPath) {
      const converted_unresolvedPath = refResolution.unresolvedPath.map(convertPath);
      refResolution.unresolvedPath = converted_unresolvedPath;
    }
    if (wrappingComponent) {
      wrappingComponent.children.unshift(newComponent);
      newComponents.push(wrappingComponent);
    } else {
      newComponent.attributes = {
        ...newComponent.attributes,
        ...outerAttributes
      };
      newComponents.push(newComponent);
    }
  }
  return { components: newComponents, nComponents };
}
function convertEvaluate({
  evaluateComponent,
  refResolution,
  nComponents,
  componentInfoObjects,
  allNodes,
  addNodesToResolver
}) {
  const functionComponent = {
    type: "unflattened",
    componentType: "function",
    componentIdx: nComponents++,
    children: [],
    attributes: {},
    doenetAttributes: {},
    state: {},
    extending: evaluateComponent.extending
  };
  let res = convertRefsToCopies({
    serializedComponents: [functionComponent],
    nComponents,
    componentInfoObjects,
    allNodes,
    addNodesToResolver
  });
  nComponents = res.nComponents;
  if (addNodesToResolver) {
    const flatFragment = {
      type: "flatFragment",
      children: [functionComponent.componentIdx],
      nodes: [
        {
          type: "element",
          name: functionComponent.componentType,
          parent: evaluateComponent.componentIdx,
          children: [],
          attributes: [],
          idx: functionComponent.componentIdx,
          sourceDoc: evaluateComponent.sourceDoc
        }
      ],
      parentIdx: evaluateComponent.componentIdx,
      parentSourceSequence: null,
      idxMap: { [functionComponent.componentIdx]: 0 }
    };
    addNodesToResolver(flatFragment, "None");
  }
  evaluateComponent.attributes.function = {
    name: "function",
    children: res.components
  };
  let listChildren = [];
  if (evaluateComponent.children.length > 0) {
    listChildren = {
      ...evaluateComponent.children[0]
    }.children.map((child) => {
      const mathChild = child;
      mathChild.componentType = "math";
      return mathChild;
    });
  }
  evaluateComponent.attributes.input = {
    name: "input",
    children: listChildren
  };
  evaluateComponent.children = [];
  return { newComponent: evaluateComponent, nComponents };
}
async function normalizedDastToSerializedComponents(normalized_root, componentInfoObjects, addNodesToResolver) {
  function unflattenDastNodes(indicesOrStrings, diagnostics2) {
    const unflattenedNodes = [];
    for (const idxOrString of indicesOrStrings) {
      if (typeof idxOrString === "string") {
        unflattenedNodes.push(idxOrString);
        continue;
      } else {
        const node = normalized_root.nodes[idxOrString];
        if (node.type === "element") {
          unflattenedNodes.push({
            type: "unflattened",
            componentType: getComponentType(node),
            componentIdx: idxOrString,
            attributes: Object.fromEntries(
              node.attributes.filter(
                (attribute) => attribute.name !== "type" || node.name !== "division"
              ).map((attribute) => [
                attribute.name,
                {
                  name: attribute.name,
                  children: unflattenDastNodes(
                    attribute.children,
                    diagnostics2
                  ),
                  position: attribute.position,
                  sourceDoc: attribute.sourceDoc
                }
              ])
            ),
            position: node.position,
            sourceDoc: node.sourceDoc,
            childrenPosition: node.childrenPosition,
            extending: node.extending ? unFlattenExtending(node.extending, diagnostics2) : void 0,
            children: unflattenDastNodes(
              node.children,
              diagnostics2
            ),
            state: {}
          });
        } else {
          if (node.errorType === "warning" || node.errorType === "info") {
            diagnostics2.push({
              type: node.errorType,
              message: node.message,
              position: node.position,
              sourceDoc: node.sourceDoc
            });
            if (node.message.includes("No referent") || node.message.includes("Multiple referents")) {
              unflattenedNodes.push({
                type: "unflattened",
                componentType: "_copy",
                componentIdx: idxOrString,
                attributes: {},
                position: node.position,
                sourceDoc: node.sourceDoc,
                extending: {
                  Ref: {
                    nodeIdx: -1,
                    unresolvedPath: null,
                    originalPath: [],
                    nodesInResolvedPath: []
                  }
                },
                state: {},
                children: []
              });
            }
          } else {
            unflattenedNodes.push({
              type: "unflattened",
              componentType: "_error",
              componentIdx: idxOrString,
              attributes: {},
              position: node.position,
              sourceDoc: node.sourceDoc,
              state: {
                message: node.message,
                unresolvedPath: node.unresolvedPath
              },
              children: []
            });
          }
        }
      }
    }
    return unflattenedNodes;
  }
  function unFlattenExtending(extending, diagnostics2) {
    const refResolution = unwrapSource(extending);
    let unresolvedPath = null;
    if (refResolution.unresolvedPath) {
      unresolvedPath = unflattenPath(
        refResolution.unresolvedPath,
        diagnostics2
      );
    }
    const originalPath = unflattenPath(
      refResolution.originalPath,
      diagnostics2
    );
    const unflattenedRefResolution = {
      nodeIdx: refResolution.nodeIdx,
      unresolvedPath,
      originalPath,
      nodesInResolvedPath: [...refResolution.nodesInResolvedPath]
    };
    return addSource(unflattenedRefResolution, extending);
  }
  function unflattenPath(path, diagnostics2) {
    return path.map((path_part) => {
      let index2 = path_part.index.map((flat_index) => {
        let value = unflattenDastNodes(flat_index.value, diagnostics2);
        return {
          value,
          position: flat_index.position,
          sourceDoc: flat_index.sourceDoc
        };
      });
      return {
        index: index2,
        name: path_part.name,
        position: path_part.position,
        sourceDoc: path_part.sourceDoc
      };
    });
  }
  const documentIdx = normalized_root.children[0];
  let nComponents = normalized_root.nodes.length;
  if (typeof documentIdx === "string" || normalized_root.children.length !== 1) {
    throw Error("Root of dast should be a single document");
  }
  const diagnostics = [];
  const unflattenedDocument = unflattenDastNodes(
    [documentIdx],
    diagnostics
  )[0];
  if (unflattenedDocument.componentType !== "document") {
    throw Error("Root of dast should be a single document");
  }
  const convertToCopyResult = convertRefsToCopies({
    serializedComponents: [unflattenedDocument],
    nComponents,
    componentInfoObjects,
    allNodes: normalized_root.nodes,
    addNodesToResolver
  });
  nComponents = convertToCopyResult.nComponents;
  let expandResult = expandUnflattenedToSerializedComponents({
    serializedComponents: convertToCopyResult.components,
    componentInfoObjects,
    nComponents
  });
  let expandedRoot = expandResult.components;
  nComponents = expandResult.nComponents;
  diagnostics.push(...expandResult.diagnostics);
  expandedRoot = removeBlankStringChildren(
    expandedRoot,
    componentInfoObjects
  );
  const sugarResult = applySugar({
    serializedComponents: expandedRoot,
    componentInfoObjects,
    nComponents
  });
  diagnostics.push(...sugarResult.diagnostics);
  nComponents = sugarResult.nComponents;
  return {
    document: sugarResult.components[0],
    nComponents,
    diagnostics,
    sources: [...normalized_root.sources]
  };
}
function expandUnflattenedToSerializedComponents({
  serializedComponents,
  componentInfoObjects,
  nComponents,
  ignoreErrors = false,
  init = true,
  stateIdInfo
}) {
  const diagnostics = [];
  let newComponents = [];
  for (let component of serializedComponents) {
    if (typeof component === "string") {
      newComponents.push(component);
      continue;
    }
    let componentClass = componentInfoObjects.allComponentClasses[component.componentType];
    let newComponent;
    try {
      if (componentClass === void 0) {
        throw Error(
          `Invalid component type: \`<${component.componentType}>\``
        );
      }
      const expandResult = expandAllUnflattenedAttributes({
        unflattenedAttributes: component.attributes,
        componentClass,
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      let attributes = expandResult.attributes;
      diagnostics.push(...expandResult.diagnostics);
      nComponents = expandResult.nComponents;
      const defaultPrimitiveResult = addDefaultPrimitiveAttributes({
        unflattenedAttributes: component.attributes,
        currentAttributes: attributes,
        componentClass,
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      attributes = defaultPrimitiveResult.attributes;
      diagnostics.push(...defaultPrimitiveResult.diagnostics);
      nComponents = defaultPrimitiveResult.nComponents;
      let extending = void 0;
      if (component.extending) {
        const unflattenedRefResolution = unwrapSource(
          component.extending
        );
        const refResolutionResult = expandUnflattenedRefResolution({
          unflattenedRefResolution,
          componentInfoObjects,
          nComponents,
          ignoreErrors,
          stateIdInfo
        });
        const refResolution = refResolutionResult.refResolution;
        extending = addSource(refResolution, component.extending);
        diagnostics.push(...refResolutionResult.diagnostics);
        nComponents = refResolutionResult.nComponents;
      }
      newComponent = {
        ...component,
        type: "serialized",
        children: [],
        attributes,
        extending,
        doenetAttributes: component.doenetAttributes ?? {}
      };
    } catch (e3) {
      const convertResult = convertToErrorComponent(component, e3);
      newComponent = convertResult.component;
      if (!ignoreErrors) {
        diagnostics.push({
          type: "error",
          message: convertResult.message,
          position: component.position,
          sourceDoc: component.sourceDoc
        });
      }
    }
    let ignoreErrorsInChildren = ignoreErrors || componentClass?.ignoreErrorsFromChildren;
    let res = expandUnflattenedToSerializedComponents({
      serializedComponents: component.children,
      componentInfoObjects,
      nComponents,
      ignoreErrors: ignoreErrorsInChildren,
      init: false,
      stateIdInfo
    });
    newComponent.children = res.components;
    diagnostics.push(...res.diagnostics);
    nComponents = res.nComponents;
    newComponents.push(newComponent);
  }
  if (init) {
    newComponents = decodeXMLEntities(newComponents);
  }
  return { diagnostics, components: newComponents, nComponents };
}
function expandUnflattenedRefResolution({
  unflattenedRefResolution,
  componentInfoObjects,
  nComponents,
  ignoreErrors,
  stateIdInfo
}) {
  let unresolvedPath = null;
  const diagnostics = [];
  if (unflattenedRefResolution.unresolvedPath) {
    const res2 = expandUnflattenedPath({
      unflattenedPath: unflattenedRefResolution.unresolvedPath,
      componentInfoObjects,
      nComponents,
      ignoreErrors,
      stateIdInfo
    });
    unresolvedPath = res2.expandedPath;
    nComponents = res2.nComponents;
    diagnostics.push(...res2.diagnostics);
  }
  const res = expandUnflattenedPath({
    unflattenedPath: unflattenedRefResolution.originalPath,
    componentInfoObjects,
    nComponents,
    ignoreErrors,
    stateIdInfo
  });
  const originalPath = res.expandedPath;
  nComponents = res.nComponents;
  diagnostics.push(...res.diagnostics);
  const refResolution = {
    nodeIdx: unflattenedRefResolution.nodeIdx,
    unresolvedPath,
    originalPath,
    nodesInResolvedPath: [...unflattenedRefResolution.nodesInResolvedPath]
  };
  return {
    refResolution,
    diagnostics,
    nComponents
  };
}
function expandUnflattenedPath({
  unflattenedPath,
  componentInfoObjects,
  nComponents,
  ignoreErrors,
  stateIdInfo
}) {
  const diagnostics = [];
  const expandedPath = unflattenedPath.map((path_part) => {
    let index2 = path_part.index.map((flat_index) => {
      let res = expandUnflattenedToSerializedComponents({
        serializedComponents: flat_index.value,
        componentInfoObjects,
        nComponents,
        ignoreErrors,
        stateIdInfo
      });
      let valueComponents = res.components;
      diagnostics.push(...res.diagnostics);
      nComponents = res.nComponents;
      if (valueComponents.length !== 1) {
        throw Error("Unresolved index should be a single component");
      }
      return {
        value: valueComponents,
        position: flat_index.position,
        sourceDoc: flat_index.sourceDoc
      };
    });
    return {
      index: index2,
      name: path_part.name,
      position: path_part.position,
      sourceDoc: path_part.sourceDoc
    };
  });
  return { expandedPath, nComponents, diagnostics };
}
function expandAllUnflattenedAttributes({
  unflattenedAttributes,
  componentClass,
  componentInfoObjects,
  nComponents,
  stateIdInfo
}) {
  let classAttributes = componentClass.createAttributesObject();
  const diagnostics = [];
  let attributeLowerCaseMapping = {};
  for (let attrName in classAttributes) {
    attributeLowerCaseMapping[attrName.toLowerCase()] = attrName;
  }
  let attributes = {};
  for (let attr in unflattenedAttributes) {
    let attrName = attributeLowerCaseMapping[attr.toLowerCase()];
    let attrDef = classAttributes[attrName];
    if (attrDef) {
      if (attrName in attributes) {
        throw Error(`Cannot repeat attribute ${attrName}.`);
      }
      let res = expandAttribute({
        attrDef,
        attribute: unflattenedAttributes[attr],
        allUnflattenedAttributes: unflattenedAttributes,
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      attributes[attrName] = res.attribute;
      diagnostics.push(...res.diagnostics);
      nComponents = res.nComponents;
    } else if (componentClass.acceptAnyAttribute || attr.includes(":")) {
      let res = expandAttribute({
        attribute: unflattenedAttributes[attr],
        allUnflattenedAttributes: unflattenedAttributes,
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      attributes[attr] = res.attribute;
      diagnostics.push(...res.diagnostics);
      nComponents = res.nComponents;
    } else {
      throw Error(
        `Invalid attribute "${attr}" for a component of type \`<${componentClass.componentType}>\`.`
      );
    }
  }
  return { attributes, diagnostics, nComponents };
}
function addDefaultPrimitiveAttributes({
  unflattenedAttributes,
  currentAttributes,
  componentClass,
  componentInfoObjects,
  nComponents,
  stateIdInfo
}) {
  const diagnostics = [];
  let classAttributes = componentClass.createAttributesObject();
  const newAttributes = { ...currentAttributes };
  for (let attrName in classAttributes) {
    let attrDef = classAttributes[attrName];
    if (attrDef.createPrimitiveOfType && "defaultPrimitiveValue" in attrDef && !(attrName in currentAttributes)) {
      let res = expandAttribute({
        attrDef,
        allUnflattenedAttributes: unflattenedAttributes,
        attribute: {
          name: attrName,
          children: [attrDef.defaultPrimitiveValue.toString()]
        },
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      newAttributes[attrName] = res.attribute;
      diagnostics.push(...res.diagnostics);
      nComponents = res.nComponents;
    }
  }
  return { attributes: newAttributes, diagnostics, nComponents };
}
function expandAttribute({
  attrDef,
  attribute,
  allUnflattenedAttributes = {},
  componentInfoObjects,
  nComponents,
  stateIdInfo
}) {
  const diagnostics = [];
  if (attrDef?.createComponentOfType) {
    const initialResult = createInitialComponentFromAttribute({
      attrDef,
      attribute,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    });
    const unflattenedComponent = initialResult.component;
    nComponents = initialResult.nComponents;
    if (attribute.position) {
      unflattenedComponent.position = attribute.position;
    }
    if (attribute.sourceDoc !== void 0) {
      unflattenedComponent.sourceDoc = attribute.sourceDoc;
    }
    if (!unflattenedComponent.doenetAttributes) {
      unflattenedComponent.doenetAttributes = {};
    }
    unflattenedComponent.doenetAttributes.isAttributeChildFor = attribute.name;
    if (attrDef.attributesForCreatedComponent || attrDef.copyComponentAttributesForCreatedComponent) {
      const unflattenedComponentAttributes = {};
      if (attrDef.attributesForCreatedComponent) {
        for (const attrName in attrDef.attributesForCreatedComponent) {
          unflattenedComponentAttributes[attrName] = {
            name: attrName,
            children: [
              attrDef.attributesForCreatedComponent[attrName]
            ]
          };
        }
      }
      if (attrDef.copyComponentAttributesForCreatedComponent) {
        for (let attrName of attrDef.copyComponentAttributesForCreatedComponent) {
          if (allUnflattenedAttributes[attrName]) {
            unflattenedComponentAttributes[attrName] = JSON.parse(
              JSON.stringify(allUnflattenedAttributes[attrName])
            );
          }
        }
      }
      unflattenedComponent.attributes = unflattenedComponentAttributes;
    }
    let res = expandUnflattenedToSerializedComponents({
      serializedComponents: [unflattenedComponent],
      componentInfoObjects,
      nComponents,
      stateIdInfo
    });
    diagnostics.push(...res.diagnostics);
    nComponents = res.nComponents;
    let attr = {
      type: "component",
      name: attribute.name,
      component: removeBlankStringChildren(
        res.components,
        componentInfoObjects
      )[0],
      sourceDoc: attribute.sourceDoc
    };
    if (attrDef.ignoreFixed) {
      attr.component.doenetAttributes.ignoreParentFixed = true;
    }
    return { attribute: attr, diagnostics, nComponents };
  } else if (attrDef?.createPrimitiveOfType) {
    let primitiveValue = createPrimitiveFromAttribute({ attrDef, attribute });
    if (attrDef.validatePrimitives) {
      primitiveValue = attrDef.validatePrimitives(primitiveValue);
    }
    return {
      attribute: {
        type: "primitive",
        name: attribute.name,
        primitive: primitiveValue,
        sourceDoc: attribute.sourceDoc
      },
      diagnostics,
      nComponents
    };
  } else if (attrDef?.createReferences) {
    let res = expandUnflattenedToSerializedComponents({
      serializedComponents: attribute.children,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    });
    diagnostics.push(...res.diagnostics);
    nComponents = res.nComponents;
    const references = [];
    const stringChildren = [];
    for (const child of res.components) {
      if (typeof child === "object") {
        references.push(child);
      } else if (typeof child === "string") {
        if (child.trim() !== "") {
          stringChildren.push(child);
          if (!attrDef.allowStrings) {
            diagnostics.push({
              type: "warning",
              message: `Invalid value \`${child.trim()}\` for attribute \`${attribute.name}\`. Attribute must be composed of references that begin with a \`$\`.`,
              position: attribute.position,
              sourceDoc: attribute.sourceDoc
            });
          }
        }
      }
    }
    return {
      attribute: {
        type: "references",
        name: attribute.name,
        references,
        stringChildren,
        position: attribute.position,
        sourceDoc: attribute.sourceDoc
      },
      diagnostics,
      nComponents
    };
  } else {
    return {
      attribute: {
        type: "unresolved",
        name: attribute.name,
        children: attribute.children,
        position: attribute.position,
        sourceDoc: attribute.sourceDoc
      },
      diagnostics,
      nComponents
    };
  }
}
function createInitialComponentFromAttribute({
  attrDef,
  attribute,
  componentInfoObjects,
  nComponents,
  stateIdInfo
}) {
  if (attrDef.createComponentOfType === void 0) {
    throw Error(
      "Cannot call initialComponentFromAttribute unless have createComponentOfType"
    );
  }
  if (attribute.children.length === 0) {
    if (attrDef.valueForTrue !== void 0) {
      return {
        component: {
          type: "unflattened",
          componentType: attrDef.createComponentOfType,
          componentIdx: nComponents++,
          stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
          attributes: {},
          children: [],
          state: { value: attrDef.valueForTrue },
          sourceDoc: attribute.sourceDoc
        },
        nComponents
      };
    } else if (componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: attrDef.createComponentOfType,
      baseComponentType: "boolean"
    })) {
      return {
        component: {
          type: "unflattened",
          componentType: attrDef.createComponentOfType,
          componentIdx: nComponents++,
          stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
          attributes: {},
          children: [],
          state: { value: true },
          sourceDoc: attribute.sourceDoc
        },
        nComponents
      };
    } else {
      throw Error(`Invalid empty attribute ${attribute.name}`);
    }
  } else if (attribute.children.length === 1) {
    const firstChild = attribute.children[0];
    if (typeof firstChild === "string") {
      let valueTrimLower = firstChild.trim().toLowerCase();
      if (valueTrimLower === "true" && attrDef.valueForTrue !== void 0) {
        return {
          component: {
            type: "unflattened",
            componentType: attrDef.createComponentOfType,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            attributes: {},
            children: [],
            state: { value: attrDef.valueForTrue },
            sourceDoc: attribute.sourceDoc
          },
          nComponents
        };
      } else if (valueTrimLower === "false" && attrDef.valueForFalse !== void 0) {
        return {
          component: {
            type: "unflattened",
            componentType: attrDef.createComponentOfType,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            attributes: {},
            children: [],
            state: { value: attrDef.valueForFalse },
            sourceDoc: attribute.sourceDoc
          },
          nComponents
        };
      } else if (componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: attrDef.createComponentOfType,
        baseComponentType: "boolean"
      }) && ["true", "false"].includes(valueTrimLower)) {
        return {
          component: {
            type: "unflattened",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            componentType: attrDef.createComponentOfType,
            attributes: {},
            children: [],
            state: { value: valueTrimLower === "true" },
            sourceDoc: attribute.sourceDoc
          },
          nComponents
        };
      }
    }
  }
  const component = {
    type: "unflattened",
    componentType: attrDef.createComponentOfType,
    componentIdx: nComponents++,
    stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
    attributes: {},
    children: [],
    state: {},
    sourceDoc: attribute.sourceDoc
  };
  component.children = [...attribute.children];
  return { component, nComponents };
}
function createPrimitiveFromAttribute({
  attrDef,
  attribute
}) {
  if (attrDef.createPrimitiveOfType === void 0) {
    throw Error(
      "Cannot call initialPrimitiveFromAttribute unless have createPrimitiveOfType"
    );
  }
  if (attribute.children.length === 0) {
    if (attrDef.valueForTrue !== void 0) {
      return attrDef.valueForTrue;
    } else if (attrDef.createPrimitiveOfType === "boolean") {
      return { type: "boolean", value: true };
    } else {
      throw Error(`Invalid empty attribute ${attribute.name}`);
    }
  } else if (attribute.children.length === 1) {
    const firstChild = attribute.children[0];
    if (typeof firstChild === "string") {
      let valueTrimLower = firstChild.trim().toLowerCase();
      if (valueTrimLower === "true" && attrDef.valueForTrue !== void 0) {
        return attrDef.valueForTrue;
      } else if (valueTrimLower === "false" && attrDef.valueForFalse !== void 0) {
        return attrDef.valueForFalse;
      } else if (attrDef.createPrimitiveOfType === "boolean") {
        return { type: "boolean", value: valueTrimLower === "true" };
      } else if (attrDef.createPrimitiveOfType === "number") {
        return { type: "number", value: Number(firstChild) };
      } else if (attrDef.createPrimitiveOfType === "integer") {
        return {
          type: "number",
          value: Math.round(Number(firstChild))
        };
      } else if (attrDef.createPrimitiveOfType === "numberArray") {
        return {
          type: "numberArray",
          value: firstChild.trim().split(/\s+/).map(Number)
        };
      } else if (attrDef.createPrimitiveOfType === "stringArray") {
        return {
          type: "stringArray",
          value: firstChild.trim().split(/\s+/)
        };
      } else {
        return { type: "string", value: firstChild };
      }
    }
  }
  throw Error(
    `Invalid reference in a primitive attribute: ${attribute.name}`
  );
}
function convertUnresolvedAttributesForComponentType({
  attributes,
  componentType,
  componentInfoObjects,
  compositeAttributesObj = {},
  dontSkipAttributes = [],
  nComponents,
  stateIdInfo
}) {
  const diagnostics = [];
  const newClass = componentInfoObjects.allComponentClasses[componentType];
  const newAttributesObj = newClass.createAttributesObject();
  let attributeLowerCaseMapping = {};
  for (let attrName in newAttributesObj) {
    attributeLowerCaseMapping[attrName.toLowerCase()] = attrName;
  }
  const newAttributes = {};
  for (const attrNameOrig in attributes) {
    const attribute = attributes[attrNameOrig];
    const attrName = attributeLowerCaseMapping[attrNameOrig.toLocaleLowerCase()];
    const attrDef = newAttributesObj[attrName];
    if (attrName in compositeAttributesObj && !compositeAttributesObj[attrName].leaveRaw && !dontSkipAttributes.includes(attrName)) {
      continue;
    }
    if (attrDef) {
      if (attribute.type === "unresolved") {
        let res = expandAttribute({
          attrDef,
          attribute,
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        newAttributes[attrName] = res.attribute;
        diagnostics.push(...res.diagnostics);
        nComponents = res.nComponents;
        if (newAttributes[attrName].type === "component") {
          for (const child of newAttributes[attrName].component.children) {
            if (typeof child === "object") {
              child.componentIdx = nComponents++;
              child.stateId = stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0;
            }
          }
          let serializedComponents = [
            newAttributes[attrName].component
          ];
          removeBlankStringChildren(
            serializedComponents,
            componentInfoObjects
          );
          const sugarResult = applySugar({
            serializedComponents,
            componentInfoObjects,
            nComponents,
            stateIdInfo
          });
          diagnostics.push(...sugarResult.diagnostics);
          nComponents = sugarResult.nComponents;
          newAttributes[attrName].component = sugarResult.components[0];
        }
      } else {
        newAttributes[attrName] = attribute;
      }
    } else if (newClass.acceptAnyAttribute) {
      newAttributes[attrNameOrig] = attribute;
    }
  }
  return { attributes: newAttributes, nComponents };
}
function convertAttributeValuesForComponentType({
  attributeValues,
  componentType,
  componentInfoObjects,
  nComponents,
  stateIdInfo
}) {
  const newClass = componentInfoObjects.allComponentClasses[componentType];
  const newAttributesObj = newClass.createAttributesObject();
  let attributeLowerCaseMapping = {};
  for (let attrName in newAttributesObj) {
    attributeLowerCaseMapping[attrName.toLowerCase()] = attrName;
  }
  const newAttributes = {};
  for (const attrNameOrig in attributeValues) {
    const attributeVal = attributeValues[attrNameOrig];
    const attrName = attributeLowerCaseMapping[attrNameOrig.toLocaleLowerCase()];
    const attrDef = newAttributesObj[attrName];
    if (attrDef?.createComponentOfType) {
      newAttributes[attrName] = {
        type: "component",
        name: attrName,
        component: {
          type: "serialized",
          componentIdx: nComponents++,
          stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
          componentType: attrDef.createComponentOfType,
          attributes: {},
          doenetAttributes: {},
          state: { value: attributeVal },
          children: []
        }
      };
    } else if (attrDef?.createPrimitiveOfType) {
      const cType = attrDef.createPrimitiveOfType;
      if (cType === "string" || cType === "boolean" || cType === "number") {
        if (typeof attributeVal === cType) {
          newAttributes[attrName] = {
            type: "primitive",
            name: attrName,
            primitive: { type: cType, value: attributeVal }
          };
        }
      } else if (cType === "numberArray") {
        if (Array.isArray(attributeVal) && attributeVal.every((v2) => typeof v2 === "number")) {
          newAttributes[attrName] = {
            type: "primitive",
            name: attrName,
            primitive: { type: cType, value: attributeVal }
          };
        }
      } else if (cType === "stringArray") {
        if (Array.isArray(attributeVal) && attributeVal.every((v2) => typeof v2 === "string")) {
          newAttributes[attrName] = {
            type: "primitive",
            name: attrName,
            primitive: { type: cType, value: attributeVal }
          };
        }
      }
    }
  }
  return { attributes: newAttributes, nComponents };
}
function unwrapSource(extending) {
  if ("Ref" in extending) {
    return extending.Ref;
  } else if ("ExtendAttribute" in extending) {
    return extending.ExtendAttribute;
  } else {
    return extending.CopyAttribute;
  }
}
function addSource(refResolution, extending) {
  if ("Ref" in extending) {
    return { Ref: refResolution };
  } else if ("ExtendAttribute" in extending) {
    return { ExtendAttribute: refResolution };
  } else {
    return { CopyAttribute: refResolution };
  }
}
function getComponentType(element) {
  if (element.name === "division") {
    const typeAttr = element.attributes.find(
      (attr) => attr.name === "type"
    );
    if (typeAttr?.children.length == 1 && typeof typeAttr.children[0] === "string") {
      return typeAttr.children[0];
    }
  }
  return element.name;
}
function postProcessCopy({
  serializedComponents,
  componentIdx,
  addShadowDependencies = true,
  markAsPrimaryShadow = false,
  identifierPrefix = "",
  unlinkExternalCopies = false,
  copiesByRefIdx = {},
  componentIndicesFound = [],
  init = true
}) {
  for (let ind in serializedComponents) {
    let component = serializedComponents[ind];
    if (typeof component !== "object") {
      continue;
    }
    if (unlinkExternalCopies) {
      componentIndicesFound.push(component.componentIdx);
    }
    if (component.originalIdx != void 0) {
      if (!component.dontShadowOriginalIndex) {
        if (addShadowDependencies) {
          let downDep = {
            [component.originalIdx]: [
              {
                dependencyType: "referenceShadow",
                compositeIdx: componentIdx
              }
            ]
          };
          if (init) {
            downDep[component.originalIdx][0].firstLevelReplacement = true;
          }
          if (markAsPrimaryShadow) {
            downDep[component.originalIdx][0].isPrimaryShadow = true;
          }
          component.downstreamDependencies = downDep;
        } else {
          component.unlinkedCopySource = component.originalIdx;
        }
      }
    }
    if (component.componentType === "_copy" && unlinkExternalCopies) {
      const refResolution = unwrapSource(component.extending);
      const nodeIdx = refResolution.nodeIdx;
      if (nodeIdx == null) {
        if (!component.attributes.uri) {
          throw Error("we need to create a nodeIdx here, then.");
        }
      } else {
        if (copiesByRefIdx[nodeIdx] === void 0) {
          copiesByRefIdx[nodeIdx] = [];
        }
        copiesByRefIdx[nodeIdx].push(component);
      }
    }
  }
  for (let ind in serializedComponents) {
    let component = serializedComponents[ind];
    if (typeof component !== "object") {
      continue;
    }
    postProcessCopy({
      serializedComponents: component.children,
      componentIdx,
      addShadowDependencies,
      markAsPrimaryShadow,
      identifierPrefix,
      unlinkExternalCopies,
      copiesByRefIdx,
      componentIndicesFound,
      init: false
    });
    for (let attrName in component.attributes) {
      let attribute = component.attributes[attrName];
      if (attribute.component) {
        attribute.component = postProcessCopy({
          serializedComponents: [attribute.component],
          componentIdx,
          addShadowDependencies,
          markAsPrimaryShadow,
          identifierPrefix,
          unlinkExternalCopies,
          copiesByRefIdx,
          componentIndicesFound,
          init: false
        })[0];
      }
    }
    if (component.replacements) {
      postProcessCopy({
        serializedComponents: component.replacements,
        componentIdx,
        addShadowDependencies,
        markAsPrimaryShadow,
        identifierPrefix,
        unlinkExternalCopies,
        copiesByRefIdx,
        componentIndicesFound,
        init: false
      });
    }
  }
  if (init && unlinkExternalCopies) {
    for (let extendIdxStr in copiesByRefIdx) {
      if (!componentIndicesFound.includes(Number(extendIdxStr))) {
        for (let copyComponent of copiesByRefIdx[extendIdxStr]) {
          if (!copyComponent.attributes) {
            copyComponent.attributes = {};
          }
          copyComponent.attributes.link = {
            type: "primitive",
            primitive: { type: "boolean", value: false }
          };
        }
      }
    }
  }
  return serializedComponents;
}
async function verifyReplacementsMatchSpecifiedType({
  component,
  replacements,
  replacementChanges,
  workspace = {},
  componentInfoObjects,
  compositeAttributesObj,
  components,
  nComponents,
  stateIdInfo,
  publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
}) {
  let diagnostics = [];
  if (!component.attributes.createComponentOfType?.primitive && !component.sharedParameters.compositesMustHaveAReplacement) {
    return {
      replacements,
      replacementChanges,
      diagnostics,
      nComponents
    };
  }
  let replacementsToWithhold = component.replacementsToWithhold;
  let replacementTypes;
  const originalReplacements = replacements;
  if (!replacementChanges) {
    if (component.componentType === "group") {
      replacements = replacements.filter(
        (x2) => x2.componentType || x2.trim().length > 0
      );
    }
    replacementTypes = replacements.map((x2) => x2.componentType);
    if (replacementTypes.length === 1 && replacementTypes[0] === "externalContent") {
      replacementTypes = replacements[0].children.filter((x2) => x2.componentType || x2.trim().length > 0).map((x2) => x2.componentType);
    }
    if (replacementsToWithhold > 0) {
      replacementTypes = replacementTypes.slice(
        0,
        replacementTypes.length - replacementsToWithhold
      );
    }
  } else {
    const res = calculateReplacementTypesFromChanges(
      component,
      replacementChanges
    );
    replacementTypes = res.replacementTypes;
    replacementsToWithhold = res.replacementsToWithhold;
  }
  if (!component.attributes.createComponentOfType?.primitive && component.sharedParameters.compositesMustHaveAReplacement && replacementTypes.length > 0) {
    return {
      // Note: use `originalReplacements` as may have modified `replacements`, above.
      replacements: originalReplacements,
      replacementChanges,
      diagnostics,
      nComponents
    };
  }
  let requiredComponentType = component.attributes.createComponentOfType?.primitive.value;
  let requiredLength = await component.stateValues.numComponentsSpecified;
  if (!requiredComponentType) {
    requiredComponentType = component.sharedParameters.compositesDefaultReplacementType;
    if (!requiredComponentType) {
      throw Error(
        `A component class specified descendantCompositesMustHaveAReplacement but didn't specify descendantCompositesDefaultReplacementType`
      );
    }
    requiredLength = 1;
  }
  requiredComponentType = componentInfoObjects.componentTypeLowerCaseMapping[requiredComponentType.toLowerCase()];
  if (replacementTypes.length !== requiredLength || !replacementTypes.every((x2) => x2 === requiredComponentType)) {
    if (replacements?.length === 1 && requiredLength === 1 && (replacements[0].componentType === "group" || replacements[0].componentType === "_copy")) {
      if (!replacements[0].attributes) {
        replacements[0].attributes = {};
      }
      replacements[0].attributes.createComponentOfType = {
        type: "primitive",
        primitive: { type: "string", value: requiredComponentType }
      };
      replacements[0].attributes.numComponents = {
        type: "primitive",
        primitive: { type: "string", value: requiredLength }
      };
      if (component.attributes.createComponentName?.primitive) {
        replacements[0].attributes.createComponentName = JSON.parse(
          JSON.stringify(component.attributes.createComponentName)
        );
        delete replacements[0].attributes.name;
      }
      if (component.attributes.createComponentIdx?.primitive) {
        replacements[0].attributes.createComponentIdx = JSON.parse(
          JSON.stringify(component.attributes.createComponentIdx)
        );
        replacements[0].componentIdx = nComponents++;
        replacements[0].stateId = `${stateIdInfo.prefix}${stateIdInfo.num++}`;
      }
      return {
        replacements,
        replacementChanges,
        diagnostics,
        nComponents
      };
    } else if (replacementChanges?.length === 1 && replacementChanges[0].changeType === "add" && replacementChanges[0].changeTopLevelReplacements && replacementChanges[0].serializedReplacements.length === 1 && (replacementChanges[0].serializedReplacements[0].componentType == "group" || replacementChanges[0].serializedReplacements[0].componentType === "_copy") && requiredLength === 1 && replacementChanges[0].numberReplacementsToReplace >= component.replacements.length) {
      const theReplacement = replacementChanges[0].serializedReplacements[0];
      if (!theReplacement.attributes) {
        theReplacement.attributes = {};
      }
      theReplacement.attributes.createComponentOfType = {
        type: "primitive",
        primitive: { type: "string", value: requiredComponentType }
      };
      theReplacement.attributes.numComponents = {
        type: "primitive",
        primitive: { type: "string", value: requiredLength }
      };
      if (component.attributes.createComponentName?.primitive) {
        theReplacement.attributes.createComponentName = JSON.parse(
          JSON.stringify(component.attributes.createComponentName)
        );
        delete theReplacement.attributes.name;
      }
      if (component.attributes.createComponentIdx?.primitive) {
        theReplacement.attributes.createComponentIdx = JSON.parse(
          JSON.stringify(component.attributes.createComponentIdx)
        );
        theReplacement.componentIdx = nComponents++;
        theReplacement.stateId = `${stateIdInfo.prefix}${stateIdInfo.num++}`;
      }
      return {
        replacements,
        replacementChanges,
        diagnostics,
        nComponents
      };
    }
    let wrapExistingReplacements = replacementTypes.length === requiredLength && !(replacementsToWithhold > 0) && workspace.sourceIndices?.length === requiredLength;
    let originalReplacements2;
    if (wrapExistingReplacements && replacementChanges) {
      if (replacementChanges.length === 1 && replacementChanges[0].numberReplacementsToReplace === component.replacements.length) {
        originalReplacements2 = replacementChanges[0].serializedReplacements;
      } else {
        wrapExistingReplacements = false;
      }
    }
    if (wrapExistingReplacements) {
      if (!originalReplacements2) {
        originalReplacements2 = replacements;
      }
    } else {
      workspace.numReplacementsBySource = [];
      workspace.numNonStringReplacementsBySource = [];
      workspace.propVariablesCopiedBySource = [];
      workspace.sourceIndices = [];
    }
    replacements = [];
    for (let i2 = 0; i2 < requiredLength; i2++) {
      const res = convertUnresolvedAttributesForComponentType({
        attributes: component.attributes,
        componentType: requiredComponentType,
        componentInfoObjects,
        compositeAttributesObj,
        nComponents,
        stateIdInfo
      });
      const attributesFromComposite = res.attributes;
      nComponents = res.nComponents;
      replacements.push({
        componentType: requiredComponentType,
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: attributesFromComposite,
        doenetAttributes: {},
        state: {},
        children: []
      });
    }
    if (replacementTypes.length === 0 && requiredLength === 1) {
      let targetInactive = await component.stateValues.targetInactive;
      let propName = (await component.stateValues.effectivePropNameBySource)?.[0];
      if (propName && !targetInactive) {
        let replacementSources = await component.stateValues.replacementSourceIdentities;
        let replacementSource = replacementSources[0];
        let target = components[replacementSource.componentIdx];
        if (target) {
          let propVariable = publicCaseInsensitiveAliasSubstitutions2({
            stateVariables: [propName],
            componentClass: target.constructor
          })[0];
          let stateVarObj = target.state[propVariable];
          if (stateVarObj?.isArray || stateVarObj?.isArrayEntry) {
            let arrayStateVarObj, arrayKeys;
            if (stateVarObj.isArray) {
              arrayStateVarObj = stateVarObj;
              let arraySize = await stateVarObj.arraySize;
              arrayKeys = stateVarObj.getAllArrayKeys(arraySize);
            } else {
              arrayStateVarObj = target.state[stateVarObj.arrayStateVariable];
              arrayKeys = arrayStateVarObj.getArrayKeysFromVarName({
                arrayEntryPrefix: stateVarObj.entryPrefix,
                varEnding: stateVarObj.varEnding,
                numDimensions: arrayStateVarObj.numDimensions
              });
            }
            propVariable = arrayStateVarObj.arrayVarNameFromArrayKey(
              arrayKeys[0] || Array(arrayStateVarObj.numDimensions).fill("0").join(",")
            );
          } else {
            stateVarObj = void 0;
          }
          if (stateVarObj) {
            replacements[0].downstreamDependencies = {
              [replacementSource.componentIdx]: [
                {
                  dependencyType: "referenceShadow",
                  compositeIdx: component.componentIdx,
                  propVariable,
                  additionalStateVariableShadowing: stateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables
                }
              ]
            };
          }
        }
      }
    }
    if (wrapExistingReplacements) {
      for (let [ind, repl] of replacements.entries()) {
        repl.children = [originalReplacements2[ind]];
      }
    }
    workspace.wrapExistingReplacements = wrapExistingReplacements;
    if (!wrapExistingReplacements) {
      workspace.numReplacementsBySource.push(replacements.length);
      workspace.numNonStringReplacementsBySource.push(
        replacements.filter((x2) => typeof x2 !== "string").length
      );
    }
    if (replacementChanges) {
      replacementChanges = [];
      if (component.replacementsToWithhold > 0) {
        replacementChanges.push({
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: 0
        });
      }
      let numberReplacementsToReplace = 0;
      if (component.replacements) {
        numberReplacementsToReplace = component.replacements.length;
      }
      replacementChanges.push({
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace,
        serializedReplacements: replacements
      });
    }
  }
  return { replacements, replacementChanges, diagnostics, nComponents };
}
function calculateReplacementTypesFromChanges(component, replacementChanges) {
  let replacementsToWithhold = component.replacementsToWithhold;
  let replacementTypes = component.replacements.map((x2) => x2.componentType);
  for (const change of replacementChanges) {
    if (change.changeType === "add") {
      if (change.replacementsToWithhold !== void 0) {
        replacementsToWithhold = change.replacementsToWithhold;
      }
      if (!change.changeTopLevelReplacements) {
        continue;
      }
      if (change.serializedReplacements) {
        let numberToDelete = change.numberReplacementsToReplace;
        if (!(numberToDelete > 0)) {
          numberToDelete = 0;
        }
        const firstIndex = change.firstReplacementInd;
        const newTypes = change.serializedReplacements.map(
          (x2) => x2.componentType
        );
        replacementTypes.splice(
          firstIndex,
          numberToDelete,
          ...newTypes
        );
      }
    } else if (change.changeType === "delete") {
      if (change.replacementsToWithhold !== void 0) {
        replacementsToWithhold = change.replacementsToWithhold;
      }
      if (change.changeTopLevelReplacements) {
        const firstIndex = change.firstReplacementInd;
        const numberToDelete = change.numberReplacementsToDelete;
        replacementTypes.splice(firstIndex, numberToDelete);
      }
    } else if (change.changeType === "changeReplacementsToWithhold") {
      if (change.replacementsToWithhold !== void 0) {
        replacementsToWithhold = change.replacementsToWithhold;
      }
    }
  }
  if (replacementsToWithhold > 0) {
    replacementTypes = replacementTypes.slice(
      0,
      replacementTypes.length - replacementsToWithhold
    );
  }
  return { replacementTypes, replacementsToWithhold };
}
function addAttributesToSingleReplacement(replacements, component, componentInfoObjects) {
  if (replacements.length === 1 && typeof replacements[0] === "object") {
    if (replacements[0].componentType === "group" || replacements[0].componentType === "_copy") {
      if (replacements[0].attributes?.createComponentIdx != null) {
        return;
      }
    }
    if (component.attributes.createComponentName?.primitive.value != void 0) {
      replacements[0].attributes.name = {
        type: "primitive",
        name: "name",
        primitive: {
          type: "string",
          value: component.attributes.createComponentName.primitive.value
        }
      };
    }
    if (component.attributes.createComponentIdx?.primitive.value != void 0) {
      const prevComponentIdx = replacements[0].componentIdx;
      const newComponentIdx = component.attributes.createComponentIdx.primitive.value;
      substituteComponentIdx(
        replacements,
        prevComponentIdx,
        newComponentIdx
      );
    }
  }
}
function substituteComponentIdx(components, prevComponentIdx, newComponentIdx) {
  for (const component of components) {
    if (typeof component === "string") {
      continue;
    }
    if (component.componentIdx === prevComponentIdx) {
      component.componentIdx = newComponentIdx;
    }
    if (component.extending) {
      const refResolution = unwrapSource(component.extending);
      if (refResolution.nodeIdx === prevComponentIdx) {
        refResolution.nodeIdx = newComponentIdx;
      }
    }
    substituteComponentIdx(
      component.children,
      prevComponentIdx,
      newComponentIdx
    );
    for (const attrName in component.attributes) {
      const attribute = component.attributes[attrName];
      if (attribute.type === "component") {
        substituteComponentIdx(
          [attribute.component],
          prevComponentIdx,
          newComponentIdx
        );
      }
    }
  }
}
function addAttributesToSingleReplacementChange(component, replacementChanges, componentInfoObjects) {
  const { replacementTypes, replacementsToWithhold } = calculateReplacementTypesFromChanges(component, replacementChanges);
  if (replacementTypes.length === 1 && !(replacementsToWithhold > 0)) {
    if (replacementChanges.length === 1 && replacementChanges[0].changeType === "add" && replacementChanges[0].serializedReplacements.length === 1 && typeof replacementChanges[0].serializedReplacements[0] === "object") {
      const theNewReplacement = replacementChanges[0].serializedReplacements[0];
      if (theNewReplacement.componentType === "group" || theNewReplacement.componentType === "_copy") {
        if (theNewReplacement.attributes?.createComponentIdx != null) {
          return;
        }
      }
      if (component.attributes.createComponentName?.primitive.value != void 0) {
        theNewReplacement.attributes.name = {
          type: "primitive",
          name: "name",
          primitive: {
            type: "string",
            value: component.attributes.createComponentName.primitive.value
          }
        };
      }
      if (component.attributes.createComponentIdx?.primitive.value != void 0) {
        theNewReplacement.componentIdx = component.attributes.createComponentIdx.primitive.value;
      }
    }
  }
}
function getSelfReferentialFallbackPropName({
  componentAncestors,
  componentInfoObjects,
  replacementSourceComponentType
}) {
  let inRecognizedContext = false;
  for (const ancestor of componentAncestors) {
    const ancestorClass = ancestor.componentClass;
    const ancestorType = ancestorClass.componentType;
    if ([
      "label",
      "text",
      "math",
      "m",
      "md",
      "boolean",
      "number",
      "mathList",
      "numberList",
      "textList",
      "booleanList"
    ].some(
      (base) => componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: ancestorType,
        baseComponentType: base
      })
    )) {
      inRecognizedContext = true;
      break;
    }
    if (!ancestorClass.treatAsComponentForRecursiveReplacements) {
      break;
    }
  }
  if (!inRecognizedContext) {
    return void 0;
  }
  const publicVarInfo = componentInfoObjects.publicStateVariableInfo[replacementSourceComponentType];
  if (!publicVarInfo) {
    return void 0;
  }
  let valueType = publicVarInfo.stateVariableDescriptions?.value?.createComponentOfType;
  if (valueType === void 0 && publicVarInfo.aliases?.value) {
    const aliasTarget = publicVarInfo.aliases.value.target;
    valueType = publicVarInfo.stateVariableDescriptions?.[aliasTarget]?.createComponentOfType;
  }
  if (!valueType) {
    return void 0;
  }
  if (componentInfoObjects.isInheritedComponentType({
    inheritedComponentType: valueType,
    baseComponentType: "math"
  }) || componentInfoObjects.isInheritedComponentType({
    inheritedComponentType: valueType,
    baseComponentType: "text"
  })) {
    return "value";
  }
  return void 0;
}
function createNewComponentIndices(serializedComponents, nComponents, stateIdInfo, idxMapOverride) {
  const result = createNewComponentIndicesSub(
    serializedComponents,
    nComponents,
    stateIdInfo,
    idxMapOverride
  );
  const newComponents = result.components;
  const idxMap = result.idxMap;
  nComponents = result.nComponents;
  remapRefResolutions(newComponents, idxMap);
  return {
    components: newComponents,
    nComponents
  };
}
function createNewComponentIndicesSub(serializedComponents, nComponents, stateIdInfo, idxMapOverride) {
  const newComponents = [];
  const idxMap = {};
  for (const component of serializedComponents) {
    if (typeof component === "string") {
      newComponents.push(component);
      continue;
    }
    const newComponent = { ...component };
    newComponent.originalIdx = component.componentIdx;
    const override = idxMapOverride?.[newComponent.originalIdx];
    newComponent.componentIdx = override ?? nComponents++;
    idxMap[newComponent.originalIdx] = newComponent.componentIdx;
    if (stateIdInfo) {
      newComponent.stateId = `${stateIdInfo.prefix}${stateIdInfo.num++}`;
    } else {
      newComponent.stateId = void 0;
    }
    const childResult = createNewComponentIndicesSub(
      component.children,
      nComponents,
      stateIdInfo,
      idxMapOverride
    );
    newComponent.children = childResult.components;
    Object.assign(idxMap, childResult.idxMap);
    nComponents = childResult.nComponents;
    const attrResult = newComponentIndicesForAttributes(
      component.attributes,
      nComponents,
      idxMap,
      idxMapOverride,
      stateIdInfo
    );
    newComponent.attributes = attrResult.attributes;
    nComponents = attrResult.nComponents;
    if (newComponent.extending) {
      const extendResult = newComponentIndicesForExtending(
        newComponent.extending,
        nComponents,
        idxMap,
        idxMapOverride,
        stateIdInfo
      );
      newComponent.extending = extendResult.extending;
      nComponents = extendResult.nComponents;
    }
    newComponents.push(newComponent);
  }
  return { components: newComponents, idxMap, nComponents };
}
function newComponentIndicesForExtending(extending, nComponents, idxMap, idxMapOverride, stateIdInfo) {
  let newExtending = extending;
  const refResolution = unwrapSource(extending);
  let originalPath = [];
  for (const pathPath of refResolution.originalPath) {
    const newPathPart = { ...pathPath };
    const index2 = [];
    for (const indexPart of newPathPart.index) {
      const newIndexPart = { ...indexPart };
      const indexResult = createNewComponentIndicesSub(
        newIndexPart.value,
        nComponents,
        stateIdInfo,
        idxMapOverride
      );
      newIndexPart.value = indexResult.components;
      Object.assign(idxMap, indexResult.idxMap);
      nComponents = indexResult.nComponents;
      index2.push(newIndexPart);
    }
    newPathPart.index = index2;
    originalPath.push(newPathPart);
    const newRefResolution = { ...refResolution };
    newRefResolution.originalPath = originalPath;
    newExtending = addSource(newRefResolution, extending);
  }
  return { extending: newExtending, nComponents };
}
function newComponentIndicesForAttributes(attributes, nComponents, idxMap, idxMapOverride, stateIdInfo) {
  const newAttributes = {};
  for (const attrName in attributes) {
    const attribute = { ...attributes[attrName] };
    newAttributes[attrName] = attribute;
    if (attribute.type === "component") {
      const attrResult = createNewComponentIndicesSub(
        [attribute.component],
        nComponents,
        stateIdInfo,
        idxMapOverride
      );
      attribute.component = attrResult.components[0];
      Object.assign(idxMap, attrResult.idxMap);
      nComponents = attrResult.nComponents;
    } else if (attribute.type === "references") {
      const attrResult = createNewComponentIndicesSub(
        attribute.references,
        nComponents,
        stateIdInfo,
        idxMapOverride
      );
      attribute.references = attrResult.components;
      Object.assign(idxMap, attrResult.idxMap);
      nComponents = attrResult.nComponents;
    } else if (attribute.type === "unresolved") {
      const attrResult = createNewComponentIndicesUnflattened(
        attribute.children,
        nComponents,
        stateIdInfo
      );
      attribute.children = attrResult.components;
      Object.assign(idxMap, attrResult.idxMap);
      nComponents = attrResult.nComponents;
    } else if (attribute.type === "primitive") {
      if (attrName === "createComponentIdx" && attribute.primitive.type === "number") {
        const originalIdx = attribute.primitive.value;
        const override = idxMapOverride?.[originalIdx];
        const newIdx = override ?? nComponents++;
        attribute.primitive = { type: "number", value: newIdx };
        idxMap[originalIdx] = newIdx;
      }
    } else {
      console.error("Found invalid attribute", attribute);
      throw Error("Found invalid attribute");
    }
  }
  return { attributes: newAttributes, nComponents };
}
function createNewComponentIndicesUnflattened(serializedComponents, nComponents, stateIdInfo) {
  const newComponents = [];
  const idxMap = {};
  for (const component of serializedComponents) {
    if (typeof component === "string") {
      newComponents.push(component);
      continue;
    }
    const newComponent = { ...component };
    newComponent.originalIdx = component.componentIdx;
    newComponent.componentIdx = nComponents++;
    idxMap[newComponent.originalIdx] = newComponent.componentIdx;
    newComponent.stateId = stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0;
    const childResult = createNewComponentIndicesUnflattened(
      component.children,
      nComponents,
      stateIdInfo
    );
    newComponent.children = childResult.components;
    Object.assign(idxMap, childResult.idxMap);
    nComponents = childResult.nComponents;
    newComponent.attributes = { ...component.attributes };
    for (const attrName in newComponent.attributes) {
      const attribute = { ...newComponent.attributes[attrName] };
      newComponent.attributes[attrName] = attribute;
      const attrResult = createNewComponentIndicesUnflattened(
        attribute.children,
        nComponents,
        stateIdInfo
      );
      attribute.children = attrResult.components;
      Object.assign(idxMap, attrResult.idxMap);
      nComponents = attrResult.nComponents;
      if (attrName === "createComponentIdx" && attribute.children.length === 1 && typeof attribute.children[0] === "string") {
        const originalIdx = Number(attribute.children[0]);
        if (Number.isInteger(originalIdx)) {
          const newIdx = nComponents++;
          attribute.children[0] = newIdx.toString();
          idxMap[originalIdx] = newIdx;
        }
      }
    }
    const extending = newComponent.extending;
    if (extending) {
      const refResolution = unwrapSource(extending);
      let unresolvedPath = null;
      if (refResolution.unresolvedPath) {
        unresolvedPath = [];
        for (const pathPath of refResolution.unresolvedPath) {
          const newPathPart = { ...pathPath };
          const index2 = [];
          for (const indexPart of newPathPart.index) {
            const newIndexPart = { ...indexPart };
            const indexResult = createNewComponentIndicesUnflattened(
              newIndexPart.value,
              nComponents,
              stateIdInfo
            );
            newIndexPart.value = indexResult.components;
            Object.assign(idxMap, indexResult.idxMap);
            nComponents = indexResult.nComponents;
            index2.push(newIndexPart);
          }
          newPathPart.index = index2;
          unresolvedPath.push(newPathPart);
        }
        const newRefResolution = { ...refResolution };
        newRefResolution.unresolvedPath = unresolvedPath;
        newComponent.extending = addSource(newRefResolution, extending);
      }
    }
    newComponents.push(newComponent);
  }
  return { components: newComponents, idxMap, nComponents };
}
function remapRefResolutions(serializedComponents, idxMap) {
  for (const component of serializedComponents) {
    if (typeof component === "string") {
      continue;
    }
    const extending = component.extending;
    if (extending) {
      const refResolution = unwrapSource(extending);
      const newNodeIdx = idxMap[refResolution.nodeIdx];
      if (newNodeIdx != void 0) {
        refResolution.nodeIdx = newNodeIdx;
      }
      if (refResolution.unresolvedPath) {
        for (const pathPath of refResolution.unresolvedPath) {
          for (const indexPart of pathPath.index) {
            remapRefResolutions(indexPart.value, idxMap);
          }
        }
      }
      for (const pathPath of refResolution.originalPath) {
        for (const indexPart of pathPath.index) {
          remapRefResolutions(indexPart.value, idxMap);
        }
      }
      refResolution.nodesInResolvedPath = refResolution.nodesInResolvedPath.map((idx) => {
        const newIdx = idxMap[idx];
        if (newIdx != void 0) {
          return newIdx;
        } else {
          return idx;
        }
      });
    }
    if (component.doenetAttributes.extendListViaComposite) {
      const newNodeIdx = idxMap[component.doenetAttributes.extendListViaComposite];
      if (newNodeIdx != void 0) {
        component.doenetAttributes.extendListViaComposite = newNodeIdx;
      }
    }
    if (component.doenetAttributes.copyListViaComposite) {
      const newNodeIdx = idxMap[component.doenetAttributes.copyListViaComposite];
      if (newNodeIdx != void 0) {
        component.doenetAttributes.copyListViaComposite = newNodeIdx;
      }
    }
    remapRefResolutions(component.children, idxMap);
    for (const attrName in component.attributes) {
      const attribute = component.attributes[attrName];
      if (attribute.type === "component") {
        remapRefResolutions([attribute.component], idxMap);
      } else if (attribute.type === "references") {
        remapRefResolutions(attribute.references, idxMap);
      } else if (attribute.type === "unresolved") {
        remapUnflattenedRefResolutions(attribute.children, idxMap);
      }
    }
  }
}
function remapUnflattenedRefResolutions(serializedComponents, idxMap) {
  for (const component of serializedComponents) {
    if (typeof component === "string") {
      continue;
    }
    const extending = component.extending;
    if (extending) {
      const refResolution = unwrapSource(extending);
      const newNodeIdx = idxMap[refResolution.nodeIdx];
      if (newNodeIdx != void 0) {
        refResolution.nodeIdx = newNodeIdx;
      }
      if (refResolution.unresolvedPath) {
        for (const pathPath of refResolution.unresolvedPath) {
          for (const indexPart of pathPath.index) {
            remapUnflattenedRefResolutions(
              indexPart.value,
              idxMap
            );
          }
        }
      }
      for (const pathPath of refResolution.originalPath) {
        for (const indexPart of pathPath.index) {
          remapUnflattenedRefResolutions(
            indexPart.value,
            idxMap
          );
        }
      }
      refResolution.nodesInResolvedPath = refResolution.nodesInResolvedPath.map((idx) => {
        const newIdx = idxMap[idx];
        if (newIdx != void 0) {
          return newIdx;
        } else {
          return idx;
        }
      });
    }
    remapUnflattenedRefResolutions(
      component.children,
      idxMap
    );
    for (const attrName in component.attributes) {
      const attribute = component.attributes[attrName];
      remapUnflattenedRefResolutions(
        attribute.children,
        idxMap
      );
    }
  }
}
function createComponentIndicesFromSerializedChildren(serializedComponents, componentIdxSources, nComponents, stateIdInfo) {
  const result = createComponentIndicesFromSerializedChildrenSub(
    serializedComponents,
    componentIdxSources,
    nComponents,
    stateIdInfo
  );
  const newComponents = result.components;
  const idxMap = result.idxMap;
  nComponents = result.nComponents;
  remapRefResolutions(newComponents, idxMap);
  return { components: newComponents, nComponents, idxMap };
}
function createComponentIndicesFromSerializedChildrenSub(serializedComponents, componentIdxSources, nComponents, stateIdInfo) {
  const newComponents = [];
  const idxMap = {};
  for (const [idx, component] of serializedComponents.entries()) {
    if (typeof component === "string") {
      newComponents.push(component);
      continue;
    }
    const newComponent = { ...component };
    const idxSource = componentIdxSources[idx];
    if (typeof idxSource === "string") {
      throw Error("Serialized children don't correspond");
    }
    newComponent.originalIdx = component.componentIdx;
    newComponent.componentIdx = idxSource.componentIdx;
    idxMap[newComponent.originalIdx] = newComponent.componentIdx;
    newComponent.stateId = idxSource.stateId;
    const childResult = createComponentIndicesFromSerializedChildrenSub(
      component.children,
      idxSource.children,
      nComponents,
      stateIdInfo
    );
    newComponent.children = childResult.components;
    Object.assign(idxMap, childResult.idxMap);
    nComponents = childResult.nComponents;
    const attrResult = newComponentIndicesForAttributesFromSerialized(
      component.attributes,
      idxSource.attributes,
      nComponents,
      idxMap,
      stateIdInfo
    );
    newComponent.attributes = attrResult.attributes;
    nComponents = attrResult.nComponents;
    const extending = newComponent.extending;
    if (extending) {
      if (newComponent.extending) {
        const extendResult = newComponentIndicesForExtending(
          newComponent.extending,
          nComponents,
          idxMap,
          stateIdInfo
        );
        newComponent.extending = extendResult.extending;
        nComponents = extendResult.nComponents;
      }
    }
    newComponents.push(newComponent);
  }
  return { components: newComponents, idxMap, nComponents };
}
function newComponentIndicesForAttributesFromSerialized(attributes, attributeIdxSources, nComponents, idxMap, stateIdInfo) {
  const newAttributes = {};
  for (const attrName in attributes) {
    const attribute = { ...attributes[attrName] };
    newAttributes[attrName] = attribute;
    const idxSource = attributeIdxSources[attrName];
    if (!idxSource) {
      const attrResult = newComponentIndicesForAttributes(
        { [attrName]: attribute },
        nComponents,
        idxMap,
        stateIdInfo
      );
      newAttributes[attrName] = attrResult.attributes[attrName];
      nComponents = attrResult.nComponents;
      continue;
    }
    if (attribute.type === "component") {
      if (idxSource.type !== "component") {
        throw Error("Attributes don't correspond");
      }
      const attrResult = createComponentIndicesFromSerializedChildrenSub(
        [attribute.component],
        [idxSource.component],
        nComponents,
        stateIdInfo
      );
      attribute.component = attrResult.components[0];
      Object.assign(idxMap, attrResult.idxMap);
      nComponents = attrResult.nComponents;
    } else if (attribute.type === "references") {
      if (idxSource.type !== "references") {
        throw Error("Attributes don't correspond");
      }
      const attrResult = createComponentIndicesFromSerializedChildrenSub(
        attribute.references,
        idxSource.references,
        nComponents,
        stateIdInfo
      );
      attribute.references = attrResult.components;
      Object.assign(idxMap, attrResult.idxMap);
      nComponents = attrResult.nComponents;
    } else if (attribute.type === "unresolved") {
      const attrResult = createNewComponentIndicesUnflattened(
        attribute.children,
        nComponents,
        stateIdInfo
      );
      attribute.children = attrResult.components;
      Object.assign(idxMap, attrResult.idxMap);
      nComponents = attrResult.nComponents;
    } else if (attribute.type === "primitive") {
      if (attrName === "createComponentIdx" && attribute.primitive.type === "number") {
        if (idxSource.type !== "primitive" || idxSource.primitive.type !== "number") {
          throw Error("Attributes don't correspond");
        }
        const originalIdx = attribute.primitive.value;
        const newIdx = idxSource.primitive.value;
        attribute.primitive.value = newIdx;
        idxMap[originalIdx] = newIdx;
      }
    } else {
      console.error("Found invalid attribute", attribute);
      throw Error("Found invalid attribute");
    }
  }
  return { attributes: newAttributes, nComponents };
}
function extractCreateComponentIdxMapping(serializedComponents) {
  const createComponentIdxMapping = {};
  for (const component of serializedComponents) {
    if (typeof component === "string") {
      continue;
    }
    if (component.attributes?.createComponentIdx?.type === "primitive") {
      const primitive = component.attributes.createComponentIdx.primitive;
      if (primitive.type === "number") {
        const createComponentIdx = primitive.value;
        createComponentIdxMapping[createComponentIdx] = component.componentIdx;
      }
    }
  }
  return { createComponentIdxMapping };
}
function registerComponent({
  core: core2,
  component
}) {
  if (core2._components[component.componentIdx] !== void 0) {
    throw Error(`Duplicate component index: ${component.componentIdx}`);
  }
  core2._components[component.componentIdx] = component;
}
function deregisterComponent({
  core: core2,
  component,
  recursive = true
}) {
  if (recursive) {
    for (let childIdxStr in component.allChildren) {
      deregisterComponent({
        core: core2,
        component: component.allChildren[childIdxStr].component
      });
    }
  }
  delete core2._components[component.componentIdx];
}
function setAncestors({
  core: core2,
  component,
  ancestors = []
}) {
  component.ancestors = ancestors;
  let ancestorsForChildren = [
    {
      componentIdx: component.componentIdx,
      componentClass: component.constructor
    },
    ...component.ancestors
  ];
  for (const childIdxStr in component.allChildren) {
    let unproxiedChild = core2._components[Number(childIdxStr)];
    if (unproxiedChild) {
      setAncestors({
        core: core2,
        component: unproxiedChild,
        ancestors: ancestorsForChildren
      });
    }
  }
  for (let attrName in component.attributes) {
    let comp = component.attributes[attrName].component;
    if (comp) {
      setAncestors({
        core: core2,
        component: comp,
        ancestors: ancestorsForChildren
      });
    }
  }
}
async function addChildrenAndRecurseToShadows({
  core: core2,
  parent,
  indexOfDefiningChildren,
  newChildren
}) {
  spliceChildren({ parent, indexOfDefiningChildren, newChildren });
  let newChildrenResult = await processNewDefiningChildren({
    core: core2,
    parent
  });
  let addedComponents = {};
  let deletedComponents = {};
  if (!newChildrenResult.success) {
    newChildrenResult = await processNewDefiningChildren({
      core: core2,
      parent,
      forceExpandComposites: true
    });
    if (!newChildrenResult.success) {
      return newChildrenResult;
    }
  }
  for (let child of newChildren) {
    if (typeof child === "object") {
      addedComponents[child.componentIdx] = child;
    }
  }
  if (parent.shadowedBy) {
    for (let shadowingParent of parent.shadowedBy) {
      if (shadowingParent.shadows.propVariable || shadowingParent.constructor.doNotExpandAsShadowed) {
        continue;
      }
      let shadowingSerializeChildren = [];
      let nComponents = core2._components.length;
      for (let child of newChildren) {
        if (typeof child === "object") {
          const serializedComponent = await child.serialize();
          const res = createNewComponentIndices(
            [serializedComponent],
            nComponents
          );
          nComponents = res.nComponents;
          shadowingSerializeChildren.push(...res.components);
        } else {
          shadowingSerializeChildren.push(child);
        }
      }
      if (nComponents > core2._components.length) {
        core2._components[nComponents - 1] = void 0;
      }
      shadowingSerializeChildren = postProcessCopy({
        serializedComponents: shadowingSerializeChildren,
        componentIdx: shadowingParent.shadows.compositeIdx
      });
      let unproxiedShadowingParent = core2._components[shadowingParent.componentIdx];
      core2.parameterStack.push(
        unproxiedShadowingParent.sharedParameters,
        false
      );
      let createResult = await createIsolatedComponents({
        core: core2,
        serializedComponents: shadowingSerializeChildren,
        ancestors: shadowingParent.ancestors
      });
      core2.parameterStack.pop();
      let shadowResult = await addChildrenAndRecurseToShadows({
        core: core2,
        parent: unproxiedShadowingParent,
        indexOfDefiningChildren,
        newChildren: createResult.components
      });
      if (!shadowResult.success) {
        throw Error(
          `was able to add components to parent but not shadows!`
        );
      }
      Object.assign(addedComponents, shadowResult.addedComponents);
    }
  }
  return {
    success: true,
    deletedComponents,
    addedComponents
  };
}
async function processNewDefiningChildren({
  core: core2,
  parent,
  expandComposites = true,
  forceExpandComposites = false
}) {
  core2.parameterStack.push(parent.sharedParameters, false);
  let childResult = await deriveChildResultsFromDefiningChildren({
    core: core2,
    parent,
    expandComposites,
    forceExpandComposites
  });
  core2.parameterStack.pop();
  let ancestorsForChildren = [
    {
      componentIdx: parent.componentIdx,
      componentClass: parent.constructor
    },
    ...parent.ancestors
  ];
  for (const childIdxStr in parent.allChildren) {
    let unproxiedChild = core2._components[Number(childIdxStr)];
    setAncestors({
      core: core2,
      component: unproxiedChild,
      ancestors: ancestorsForChildren
    });
  }
  return childResult;
}
function spliceChildren({
  parent,
  indexOfDefiningChildren,
  newChildren
}) {
  let numDefiningChildren = parent.definingChildren.length;
  if (!Number.isInteger(indexOfDefiningChildren) || indexOfDefiningChildren > numDefiningChildren || indexOfDefiningChildren < 0) {
    throw Error(
      "Can't add children at index " + indexOfDefiningChildren + ". Invalid index."
    );
  }
  parent.definingChildren.splice(indexOfDefiningChildren, 0, ...newChildren);
}
function addNodesToFlatFragment({
  flatFragment,
  serializedComponents,
  parentIdx
}) {
  for (const comp of serializedComponents) {
    if (typeof comp === "string") {
      continue;
    }
    let componentIdx = getEffectiveComponentIdx(comp);
    const flatElement = {
      type: "element",
      name: comp.componentType,
      parent: parentIdx,
      children: [],
      attributes: [],
      idx: componentIdx,
      sourceDoc: comp.sourceDoc
    };
    if (comp.children) {
      flatElement.children = comp.children.map((child) => {
        if (typeof child === "string") {
          return child;
        } else {
          return getEffectiveComponentIdx(child);
        }
      });
      addNodesToFlatFragment({
        flatFragment,
        serializedComponents: comp.children,
        parentIdx: componentIdx
      });
    }
    const name = getEffectiveComponentName(comp);
    if (name) {
      flatElement.attributes.push({
        type: "attribute",
        name: "name",
        parent: componentIdx,
        children: [name],
        sourceDoc: comp.sourceDoc
      });
    }
    if (comp.type === "unflattened") {
      for (const attrName in comp.attributes) {
        const attribute = comp.attributes[attrName];
        if (attribute.children) {
          addNodesToFlatFragment({
            flatFragment,
            serializedComponents: attribute.children,
            parentIdx: componentIdx
          });
        }
      }
    } else {
      for (const attrName in comp.attributes) {
        const attribute = comp.attributes[attrName];
        switch (attribute.type) {
          case "primitive":
            if (attrName === "name" || attrName === "createComponentName") {
              break;
            }
            const attributeChildren = Array.isArray(
              attribute.primitive.value
            ) ? attribute.primitive.value.map((v2) => v2.toString()) : [attribute.primitive.value.toString()];
            flatElement.attributes.push({
              type: "attribute",
              name: attrName,
              parent: componentIdx,
              children: attributeChildren,
              sourceDoc: attribute.sourceDoc
            });
            break;
          case "component":
            flatElement.attributes.push({
              type: "attribute",
              name: attrName,
              parent: componentIdx,
              children: [
                getEffectiveComponentIdx(attribute.component)
              ],
              sourceDoc: attribute.sourceDoc
            });
            addNodesToFlatFragment({
              flatFragment,
              serializedComponents: [attribute.component],
              parentIdx: componentIdx
            });
            break;
          case "references":
            flatElement.attributes.push({
              type: "attribute",
              name: attrName,
              parent: componentIdx,
              children: attribute.references.map(
                getEffectiveComponentIdx
              ),
              sourceDoc: attribute.sourceDoc
            });
            addNodesToFlatFragment({
              flatFragment,
              serializedComponents: attribute.references,
              parentIdx: componentIdx
            });
            break;
          case "unresolved":
            flatElement.attributes.push({
              type: "attribute",
              name: attrName,
              parent: componentIdx,
              children: attribute.children.map(
                (child) => typeof child === "string" ? child : getEffectiveComponentIdx(child)
              ),
              sourceDoc: attribute.sourceDoc
            });
            addNodesToFlatFragment({
              flatFragment,
              serializedComponents: attribute.children,
              parentIdx: componentIdx
            });
            break;
        }
      }
    }
    if (comp.extending) {
      if (comp.type === "unflattened") {
        const refResolution = unwrapSource(comp.extending);
        for (const part of refResolution.originalPath) {
          for (const index2 of part.index) {
            addNodesToFlatFragment({
              flatFragment,
              serializedComponents: index2.value,
              parentIdx: componentIdx
            });
          }
        }
      } else {
        const refResolution = unwrapSource(comp.extending);
        for (const part of refResolution.originalPath) {
          for (const index2 of part.index) {
            addNodesToFlatFragment({
              flatFragment,
              serializedComponents: index2.value,
              parentIdx: componentIdx
            });
          }
        }
      }
    }
    if (comp.position) {
      flatElement.position = JSON.parse(JSON.stringify(comp.position));
    }
    const idxInNodes = flatFragment.nodes.length;
    flatFragment.nodes[idxInNodes] = flatElement;
    flatFragment.idxMap[componentIdx] = idxInNodes;
  }
}
function getEffectiveComponentIdx(component) {
  let componentIdx = component.componentIdx;
  if (component.type === "unflattened") {
    if (component.attributes.createComponentIdx?.children.length === 1) {
      const child = component.attributes.createComponentIdx.children[0];
      if (typeof child === "string") {
        const idx = Number(child);
        if (Number.isInteger(idx)) {
          componentIdx = idx;
        }
      }
    }
  } else {
    if (component.attributes.createComponentIdx?.type === "primitive") {
      if (component.attributes.createComponentIdx.primitive.type === "number") {
        componentIdx = component.attributes.createComponentIdx.primitive.value;
      }
    }
  }
  return componentIdx;
}
function getEffectiveComponentName(component) {
  if (component.type === "unflattened") {
    if (component.attributes.createComponentName?.children.length === 1) {
      const child = component.attributes.createComponentName.children[0];
      if (typeof child === "string") {
        return child;
      }
    }
    if (component.attributes.name?.children.length === 1) {
      const child = component.attributes.name.children[0];
      if (typeof child === "string") {
        return child;
      }
    }
    return null;
  } else {
    if (component.attributes.createComponentName?.type === "primitive" && component.attributes.createComponentName.primitive.type === "string") {
      return component.attributes.createComponentName.primitive.value;
    } else if (component.attributes.name?.type === "primitive" && component.attributes.name.primitive.type === "string") {
      return component.attributes.name.primitive.value;
    } else {
      return null;
    }
  }
}
async function calcStartEndIdx({
  replacements,
  copyComponentIdx,
  updateStart,
  updateEnd
}) {
  const activeReplacements = [];
  for (const repl of replacements) {
    if (typeof repl === "string") {
      if (repl.trim() !== "") {
        activeReplacements.push(repl);
      }
    } else if (!await repl.stateValues.isInactiveCompositeReplacement) {
      activeReplacements.push(repl);
    }
  }
  const flattenedReplacements = [];
  let startIdx;
  let endIdx;
  let i2 = 0;
  for (const repl of activeReplacements) {
    if (typeof repl === "string" || repl.componentType !== "_copy") {
      flattenedReplacements.push(repl);
      i2++;
      continue;
    }
    if (!repl.isExpanded) {
      if (repl.componentIdx === copyComponentIdx) {
        startIdx = i2;
        endIdx = i2 + 1;
      }
      flattenedReplacements.push(repl);
      i2++;
      continue;
    }
    let replReplacements = repl.replacements ?? [];
    if (repl.replacementsToWithhold) {
      replReplacements = replReplacements.slice(
        0,
        replReplacements.length - repl.replacementsToWithhold
      );
    }
    const recursionResult = await calcStartEndIdx({
      replacements: replReplacements,
      copyComponentIdx,
      updateStart,
      updateEnd
    });
    const recurFlattened = recursionResult.flattenedReplacements;
    const n2 = recurFlattened.length;
    if (recursionResult.startIdx !== void 0) {
      startIdx = recursionResult.startIdx;
      endIdx = recursionResult.endIdx;
    }
    if (repl.componentIdx === copyComponentIdx) {
      if (updateStart !== void 0 && updateEnd !== void 0) {
        startIdx = i2 + updateStart;
        endIdx = i2 + updateEnd;
      } else {
        startIdx = i2;
        endIdx = i2 + n2;
      }
    }
    flattenedReplacements.push(...recurFlattened);
    i2 += n2;
  }
  return { flattenedReplacements, startIdx, endIdx };
}
async function addReplacementsToResolver({
  core: core2,
  serializedReplacements,
  component,
  updateOldReplacementsStart,
  updateOldReplacementsEnd,
  blankStringReplacements
}) {
  if (component.constructor.replacementsAlreadyInResolver) {
    return;
  }
  const { parentIdx, indexResolution } = await determineParentAndIndexResolutionForResolver({
    core: core2,
    component,
    updateOldReplacementsStart,
    updateOldReplacementsEnd,
    blankStringReplacements
  });
  const fragmentChildren = [];
  let parentSourceSequence = null;
  if (component.attributes.createComponentIdx != null) {
    if (serializedReplacements[0]?.children) {
      fragmentChildren.push(...serializedReplacements[0].children);
    }
    for (const attrName in serializedReplacements[0]?.attributes) {
      const attribute = serializedReplacements[0].attributes[attrName];
      if (attribute.type === "component") {
        fragmentChildren.push(attribute.component);
      } else if (attribute.type === "references") {
        fragmentChildren.push(...attribute.references);
      }
    }
    let sourceSequence = serializedReplacements[0]?.attributes["source:sequence"];
    const createComponentIdxPrimitive = component.attributes.createComponentIdx?.primitive;
    if (sourceSequence && createComponentIdxPrimitive?.type === "number") {
      parentSourceSequence = {
        type: "attribute",
        name: "source:sequence",
        parent: createComponentIdxPrimitive.value,
        children: sourceSequence.children.filter(
          (child) => typeof child === "string"
        ),
        sourceDoc: sourceSequence.sourceDoc
      };
    }
  } else {
    fragmentChildren.push(...serializedReplacements);
  }
  const flatFragment = {
    type: "flatFragment",
    children: fragmentChildren.map(
      (child) => typeof child === "string" ? child : getEffectiveComponentIdx(child)
    ),
    nodes: [],
    parentIdx,
    parentSourceSequence,
    idxMap: {}
  };
  addNodesToFlatFragment({
    flatFragment,
    serializedComponents: fragmentChildren,
    parentIdx
  });
  if ((flatFragment.nodes.length > 0 || indexResolution !== "None") && core2.addNodesToResolver) {
    core2.addNodesToResolver(flatFragment, indexResolution);
    core2.rootNames = core2.calculateRootNames?.().names;
    let indexParent = indexResolution.ReplaceAll?.parent ?? indexResolution.ReplaceRange?.parent ?? null;
    if (indexParent !== null && indexParent !== component.componentIdx) {
      const indexParentComposite = core2._components[indexParent];
      if (indexParentComposite) {
        await core2.dependencies.addBlockersFromChangedReplacements(
          indexParentComposite
        );
      }
    }
  }
}
async function determineParentAndIndexResolutionForResolver({
  core: core2,
  component,
  updateOldReplacementsStart,
  updateOldReplacementsEnd,
  blankStringReplacements
}) {
  let update_start = updateOldReplacementsStart;
  let update_end = updateOldReplacementsEnd;
  if (updateOldReplacementsStart !== void 0 && updateOldReplacementsEnd !== void 0) {
    for (const [i2, isBlankString] of (blankStringReplacements ?? []).entries()) {
      if (i2 >= updateOldReplacementsEnd) {
        break;
      }
      if (isBlankString) {
        update_end--;
        if (i2 < updateOldReplacementsStart) {
          update_start--;
        }
      }
    }
  }
  let parentIdx;
  let indexResolution = "None";
  if (component.doenetAttributes.forList) {
    parentIdx = component.parentIdx;
  } else if (component.attributes.createComponentIdx?.primitive) {
    parentIdx = component.attributes.createComponentIdx?.primitive.value;
    if (component.attributes.createComponentOfType?.primitive && core2.componentInfoObjects.isCompositeComponent({
      componentType: component.attributes.createComponentOfType.primitive.value,
      includeNonStandard: true
    })) {
      indexResolution = { ReplaceAll: { parent: parentIdx } };
      if (update_start !== void 0 && update_end !== void 0) {
        indexResolution = {
          ReplaceRange: {
            parent: parentIdx,
            range: { start: update_start, end: update_end }
          }
        };
      }
    }
  } else if (component.componentType === "_copy") {
    let copyComponent = component;
    parentIdx = component.componentIdx;
    while (copyComponent.replacementOf) {
      if (copyComponent.replacementOf.componentType === "_copy") {
        copyComponent = copyComponent.replacementOf;
        continue;
      } else {
        break;
      }
    }
    if (copyComponent.replacementOf) {
      const indexParent = copyComponent.replacementOf;
      const { startIdx, endIdx } = await calcStartEndIdx({
        replacements: indexParent.replacements,
        copyComponentIdx: copyComponent.componentIdx,
        updateStart: update_start,
        updateEnd: update_end
      });
      if (startIdx !== void 0 && endIdx !== void 0) {
        indexResolution = {
          ReplaceRange: {
            parent: indexParent.componentIdx,
            range: { start: startIdx, end: endIdx }
          }
        };
      } else {
        indexResolution = "None";
      }
    } else {
      parentIdx = copyComponent.componentIdx;
      indexResolution = { ReplaceAll: { parent: parentIdx } };
    }
  } else {
    parentIdx = component.componentIdx;
    if (core2.componentInfoObjects.isCompositeComponent({
      componentType: component.componentType,
      includeNonStandard: true
    })) {
      if (update_start !== void 0 && update_end !== void 0) {
        indexResolution = {
          ReplaceRange: {
            parent: parentIdx,
            range: { start: update_start, end: update_end }
          }
        };
      } else {
        indexResolution = { ReplaceAll: { parent: parentIdx } };
      }
    }
  }
  return { parentIdx, indexResolution };
}
function addComponentsToResolver({
  core: core2,
  components,
  parentIdx
}) {
  const flatFragment = {
    type: "flatFragment",
    children: components.map(
      (child) => typeof child === "string" ? child : getEffectiveComponentIdx(child)
    ),
    nodes: [],
    parentIdx,
    parentSourceSequence: null,
    idxMap: {}
  };
  addNodesToFlatFragment({
    flatFragment,
    serializedComponents: components,
    parentIdx
  });
  if (core2.addNodesToResolver) {
    core2.addNodesToResolver(flatFragment, "None");
    core2.rootNames = core2.calculateRootNames?.().names;
  }
}
function gatherDiagnosticsAndAssignDoenetMLRange({
  core: core2,
  components,
  diagnostics,
  position,
  sourceDoc,
  overwriteDoenetMLRange = false
}) {
  assignDoenetMLRange(
    components,
    position,
    sourceDoc,
    overwriteDoenetMLRange
  );
  assignDoenetMLRange(diagnostics, position, sourceDoc);
  for (const diagnostic of diagnostics) {
    core2.addDiagnostic(diagnostic);
  }
}
function removeComponentsFromResolver({
  core: core2,
  componentsToRemove
}) {
  if (componentsToRemove.length === 0) {
    return;
  }
  const flatElements = componentsToRemove.map((comp) => {
    let flatElement = {
      type: "element",
      name: comp.componentType,
      parent: comp.parentIdx,
      children: [],
      attributes: [],
      idx: comp.componentIdx
    };
    if (comp.attributes.createComponentName && !comp.isExpanded) {
      flatElement.attributes.push({
        type: "attribute",
        name: "name",
        parent: comp.parentIdx,
        children: [comp.attributes.createComponentName.primitive.value]
      });
    } else if (comp.attributes.name) {
      flatElement.attributes.push({
        type: "attribute",
        name: "name",
        parent: comp.parentIdx,
        children: [comp.attributes.name.primitive.value]
      });
    }
    return flatElement;
  });
  if (core2.deleteNodesFromResolver) {
    core2.deleteNodesFromResolver({
      nodes: flatElements
    });
    core2.rootNames = core2.calculateRootNames?.().names;
  }
}
function preprocessAttributesObject(attributesObject) {
  for (const attrName in attributesObject) {
    const attrSpec = attributesObject[attrName];
    if (Array.isArray(attrSpec.validValues)) {
      for (const entry of attrSpec.validValues) {
        if (typeof entry !== "object" || entry === null || typeof entry.value !== "string") {
          throw new Error(
            `Invalid validValues entry for attribute \`${attrName}\`: every entry must be a {value, description} object. Got: ${JSON.stringify(entry)}`
          );
        }
      }
    }
    if (attrSpec.toLowerCase === true) {
      if (attrSpec.defaultValue !== void 0 && attrSpec.defaultValue !== null) {
        attrSpec.defaultValue = String(
          attrSpec.defaultValue
        ).toLowerCase();
      }
      if (Array.isArray(attrSpec.validValues)) {
        attrSpec.validValues = attrSpec.validValues.map((entry) => ({
          ...entry,
          value: entry.value.toLowerCase()
        }));
      }
      if (attrSpec.valueForTrue !== void 0 && attrSpec.valueForTrue !== null) {
        attrSpec.valueForTrue = String(
          attrSpec.valueForTrue
        ).toLowerCase();
      }
      if (attrSpec.valueForFalse !== void 0 && attrSpec.valueForFalse !== null) {
        attrSpec.valueForFalse = String(
          attrSpec.valueForFalse
        ).toLowerCase();
      }
    }
  }
  return attributesObject;
}
function validateListItemsAgainstValidValues({
  items,
  validValues,
  toLowerCase,
  attribute
}) {
  const allowed = validValues.map((v2) => v2.value);
  const allowedSet = new Set(allowed);
  const validItems = [];
  const invalidItems = [];
  for (const item of items) {
    const normalized = typeof item === "string" ? (toLowerCase ? item.toLowerCase() : item).trim() : item;
    if (typeof normalized === "string" && allowedSet.has(normalized)) {
      validItems.push(normalized);
    } else {
      invalidItems.push(item);
    }
  }
  const diagnostics = [];
  if (invalidItems.length > 0) {
    const invalidList = invalidItems.map((v2) => `\`${v2}\``).join(", ");
    diagnostics.push({
      message: `Invalid value${invalidItems.length > 1 ? "s" : ""} ${invalidList} for attribute \`${attribute}\`; ignoring.`,
      type: "info"
    });
  }
  return { value: validItems, diagnostics };
}
async function expandAllComposites({
  core: core2,
  component,
  force = false
}) {
  let parentsWithCompositesNotReady = await expandCompositesOfDescendants({
    core: core2,
    component,
    forceExpandComposites: force
  });
  let expandedAnother = true;
  while (expandedAnother) {
    expandedAnother = false;
    for (let parentIdx of parentsWithCompositesNotReady) {
      let parent = core2._components[parentIdx];
      let foundReady = false;
      for (let compositeIdx of parent.unexpandedCompositesNotReady) {
        let composite = core2._components[compositeIdx];
        if (composite.state.readyToExpandWhenResolved.isResolved) {
          foundReady = true;
          break;
        } else {
          let resolveResult = await core2.dependencies.resolveItem({
            componentIdx: composite.componentIdx,
            type: "stateVariable",
            stateVariable: "readyToExpandWhenResolved",
            force,
            recurseUpstream: true
          });
          if (resolveResult.success) {
            foundReady = true;
            break;
          }
        }
      }
      if (foundReady) {
        await deriveChildResultsFromDefiningChildren({
          core: core2,
          parent,
          expandComposites: true,
          forceExpandComposites: force
        });
        expandedAnother = true;
      }
    }
  }
}
async function expandCompositesOfDescendants({
  core: core2,
  component,
  forceExpandComposites = false
}) {
  let parentsWithCompositesNotReady = [];
  if (!component.matchedCompositeChildren) {
    await deriveChildResultsFromDefiningChildren({
      core: core2,
      parent: component,
      expandComposites: true,
      forceExpandComposites
    });
    if (component.unexpandedCompositesNotReady.length > 0) {
      parentsWithCompositesNotReady.push(component.componentIdx);
    } else {
      await core2.dependencies.resolveBlockersFromChangedActiveChildren(
        component,
        forceExpandComposites
      );
    }
  }
  for (let attrName in component.attributes) {
    let attrComp = component.attributes[attrName].component;
    if (attrComp) {
      let additionalParentsWithNotReady = await expandCompositesOfDescendants({
        core: core2,
        component: attrComp,
        forceExpandComposites
      });
      parentsWithCompositesNotReady.push(
        ...additionalParentsWithNotReady
      );
    }
  }
  for (let childIdxStr in component.allChildren) {
    let child = component.allChildren[childIdxStr].component;
    if (typeof child !== "object") {
      continue;
    }
    let additionalParentsWithNotReady = await expandCompositesOfDescendants(
      {
        core: core2,
        component: child,
        forceExpandComposites
      }
    );
    parentsWithCompositesNotReady.push(...additionalParentsWithNotReady);
  }
  return parentsWithCompositesNotReady;
}
async function componentAndRenderedDescendants({
  core: core2,
  component
}) {
  if (component?.componentIdx == void 0) {
    return [];
  }
  let componentIndices = [component.componentIdx];
  if (component.constructor.renderChildren) {
    if (!component.matchedCompositeChildren) {
      await deriveChildResultsFromDefiningChildren({
        core: core2,
        parent: component,
        expandComposites: true
      });
    }
    for (let child of component.activeChildren) {
      componentIndices.push(
        ...await componentAndRenderedDescendants({
          core: core2,
          component: child
        })
      );
    }
  }
  return componentIndices;
}
async function expandCompositeOfDefiningChildren({
  core: core2,
  parent,
  children,
  expandComposites,
  forceExpandComposites
}) {
  let unexpandedCompositesReady = [];
  let unexpandedCompositesNotReady = [];
  for (let childInd = 0; childInd < children.length; childInd++) {
    let child = children[childInd];
    if (child instanceof core2.componentInfoObjects.allComponentClasses._composite) {
      if (findChildGroup({
        core: core2,
        childType: child.componentType,
        parentClass: parent.constructor
      }).success) {
        continue;
      }
      if (!child.isExpanded) {
        if (!child.state.readyToExpandWhenResolved.isResolved) {
          if (expandComposites) {
            let resolveResult = await core2.dependencies.resolveItem(
              {
                componentIdx: child.componentIdx,
                type: "stateVariable",
                stateVariable: "readyToExpandWhenResolved",
                expandComposites,
                force: forceExpandComposites
              }
            );
            if (!resolveResult.success) {
              unexpandedCompositesNotReady.push(
                child.componentIdx
              );
              core2.updateInfo.compositesToExpand.add(
                child.componentIdx
              );
              continue;
            }
          } else {
            unexpandedCompositesNotReady.push(child.componentIdx);
            core2.updateInfo.compositesToExpand.add(
              child.componentIdx
            );
            continue;
          }
        } else if (!expandComposites) {
          unexpandedCompositesReady.push(child.componentIdx);
          core2.updateInfo.compositesToExpand.add(child.componentIdx);
          continue;
        }
        await expandCompositeComponent({ core: core2, component: child });
      }
      let result = await expandCompositeOfDefiningChildren({
        core: core2,
        parent,
        children: child.replacements,
        expandComposites,
        forceExpandComposites
      });
      unexpandedCompositesReady.push(...result.unexpandedCompositesReady);
      unexpandedCompositesNotReady.push(
        ...result.unexpandedCompositesNotReady
      );
    }
  }
  return { unexpandedCompositesReady, unexpandedCompositesNotReady };
}
async function expandCompositeComponent({
  core: core2,
  component
}) {
  if (!("readyToExpandWhenResolved" in component.state)) {
    throw Error(
      `Could not find state variable readyToExpandWhenResolved of composite ${component.componentIdx}`
    );
  }
  if (!component.state.readyToExpandWhenResolved.isResolved) {
    core2.updateInfo.compositesToExpand.add(component.componentIdx);
    return { success: false };
  }
  core2.updateInfo.compositesToExpand.delete(component.componentIdx);
  core2.updateInfo.compositesBeingExpanded.push(component.componentIdx);
  if (component.parent) {
    for (const list of [
      component.parent.unexpandedCompositesReady,
      component.parent.unexpandedCompositesNotReady
    ]) {
      if (!list) {
        continue;
      }
      const ind = list.indexOf(component.componentIdx);
      if (ind !== -1) {
        list.splice(ind, 1);
      }
    }
  }
  if (component.shadows && !component.shadows.propVariable && !component.constructor.doNotExpandAsShadowed) {
    return await expandShadowingComposite({ core: core2, component });
  }
  let initialNComponents;
  let result;
  const originalWorkspace = { ...component.replacementsWorkspace };
  do {
    initialNComponents = core2._components.length;
    component.replacementsWorkspace = { ...originalWorkspace };
    result = await component.constructor.createSerializedReplacements({
      component: core2._components[component.componentIdx],
      // to create proxy
      components: core2._components,
      nComponents: core2._components.length,
      workspace: component.replacementsWorkspace,
      componentInfoObjects: core2.componentInfoObjects,
      allDoenetMLs: core2.allDoenetMLs,
      flags: core2.flags,
      resolveItem: core2.dependencies.resolveItem.bind(core2.dependencies),
      publicCaseInsensitiveAliasSubstitutions: core2.publicCaseInsensitiveAliasSubstitutions.bind(core2)
    });
  } while (core2._components.length !== initialNComponents);
  const newNComponents = result.nComponents;
  if (!(Number.isInteger(newNComponents) && newNComponents >= core2._components.length)) {
    throw Error(
      `Invalid nComponents returned by createSerializedReplacements for ${component.componentType}: `,
      newNComponents
    );
  }
  let position = core2._components[component.componentIdx].position;
  let sourceDoc = core2._components[component.componentIdx].sourceDoc;
  let overwriteDoenetMLRange = component.componentType === "_copy";
  gatherDiagnosticsAndAssignDoenetMLRange({
    core: core2,
    components: result.replacements,
    diagnostics: result.diagnostics,
    position,
    sourceDoc,
    overwriteDoenetMLRange
  });
  if (component.constructor.stateVariableToEvaluateAfterReplacements) {
    await component.stateValues[component.constructor.stateVariableToEvaluateAfterReplacements];
  }
  if (result.replacements) {
    let serializedReplacements = result.replacements;
    await addReplacementsToResolver({
      core: core2,
      serializedReplacements,
      component
    });
    reserveComponentIndices(core2, newNComponents);
    await createAndSetReplacements({
      core: core2,
      component,
      serializedReplacements
    });
  } else {
    throw Error(
      `Invalid createSerializedReplacements of ${component.componentIdx}`
    );
  }
  _finishExpanding({ core: core2, componentIdx: component.componentIdx });
  return { success: true, compositesExpanded: [component.componentIdx] };
}
async function expandShadowingComposite({
  core: core2,
  component
}) {
  if (core2.updateInfo.compositesBeingExpanded.includes(
    component.shadows.componentIdx
  )) {
    let compositeInvolved = core2._components[component.shadows.componentIdx];
    while (compositeInvolved.shadows) {
      compositeInvolved = core2._components[compositeInvolved.shadows.componentIdx];
    }
    throw Error(
      `Circular dependency involving ${compositeInvolved.componentIdx}.`
    );
  }
  let shadowedComposite = core2._components[component.shadows.componentIdx];
  let compositesExpanded = [];
  if (!shadowedComposite.isExpanded) {
    let result = await expandCompositeComponent({
      core: core2,
      component: shadowedComposite
    });
    if (!result.success) {
      throw Error(
        `expand result of ${component.componentIdx} was not a success even though ready to expand.`
      );
    }
    compositesExpanded.push(...result.compositesExpanded);
  }
  let serializedReplacements = [];
  if (component.replacementsWorkspace.replacementsCreated === void 0) {
    component.replacementsWorkspace.replacementsCreated = 0;
  }
  const stateIdInfo = {
    prefix: `${component.stateId}|`,
    num: component.replacementsWorkspace.replacementsCreated
  };
  let newNComponents = core2._components.length;
  const idxMapOverride = {};
  const copyInChildren = shadowedComposite.attributes.copyInChildren?.primitive.value;
  if (copyInChildren) {
    const componentChildren = JSON.parse(
      JSON.stringify(component.serializedChildren)
    );
    const shadowedChildren = JSON.parse(
      JSON.stringify(shadowedComposite.serializedChildren)
    );
    const res = createComponentIndicesFromSerializedChildren(
      shadowedChildren,
      componentChildren,
      newNComponents
    );
    for (const idx in res.idxMap) {
      if (res.idxMap[idx] < newNComponents) {
        idxMapOverride[idx] = res.idxMap[idx];
      }
    }
  }
  for (let [idx, repl] of shadowedComposite.replacements.entries()) {
    if (typeof repl === "object") {
      const serializedComponent = await repl.serialize();
      if (component.constructor.useSerializedChildrenComponentIndices) {
        const res = createComponentIndicesFromSerializedChildren(
          [serializedComponent],
          [component.serializedChildren[idx]],
          newNComponents,
          stateIdInfo
        );
        newNComponents = res.nComponents;
        serializedReplacements.push(...res.components);
      } else {
        const res = createNewComponentIndices(
          [serializedComponent],
          newNComponents,
          stateIdInfo,
          idxMapOverride
        );
        newNComponents = res.nComponents;
        serializedReplacements.push(...res.components);
      }
    } else {
      serializedReplacements.push(repl);
    }
  }
  if (component.constructor.addExtraSerializedChildrenWhenShadowing) {
    let rendered = true;
    if ("rendered" in component.state) {
      rendered = await component.state.rendered.value;
    }
    if (rendered) {
      serializedReplacements.push(
        ...deepClone(
          component.serializedChildren.slice(
            serializedReplacements.length
          )
        )
      );
    }
  }
  adjustForCreateComponentIdxName({
    serializedReplacements,
    composite: component
  });
  let nameOfCompositeMediatingTheShadow = component.shadows.compositeIdx;
  let compositeMediatingTheShadow = core2._components[nameOfCompositeMediatingTheShadow];
  serializedReplacements = postProcessCopy({
    serializedComponents: serializedReplacements,
    componentIdx: nameOfCompositeMediatingTheShadow
  });
  let foundCircular = false;
  let shadowedByShadowed = shadowedComposite.mediatesShadows?.filter((v2) => v2.propVariable === void 0).map((v2) => v2.shadowed);
  while (shadowedByShadowed?.length > 0) {
    if (shadowedByShadowed.includes(nameOfCompositeMediatingTheShadow)) {
      foundCircular = true;
      let message = "Circular dependency detected";
      if (component.attributes.createComponentOfType?.primitive) {
        message += ` involving \`<${component.attributes.createComponentOfType.primitive.value}>\` component`;
      }
      message += ".";
      serializedReplacements = [
        {
          type: "serialized",
          componentType: "_error",
          componentIdx: newNComponents++,
          attributes: {},
          doenetAttributes: {},
          children: [],
          state: { message },
          position: compositeMediatingTheShadow.position,
          sourceDoc: compositeMediatingTheShadow.sourceDoc
        }
      ];
      core2.addDiagnostic({
        type: "error",
        message,
        position: compositeMediatingTheShadow.position,
        sourceDoc: compositeMediatingTheShadow.sourceDoc
      });
      break;
    }
    shadowedByShadowed = shadowedByShadowed.reduce(
      (acc, cIdx) => {
        let comp = core2._components[cIdx];
        if (comp?.mediatesShadows) {
          return [
            ...acc,
            ...comp.mediatesShadows.filter((v2) => v2.propVariable === void 0).map((v2) => v2.shadowed)
          ];
        } else {
          return acc;
        }
      },
      []
    );
  }
  if (!foundCircular) {
    let verificationResult = await verifyReplacementsMatchSpecifiedType({
      component,
      replacements: serializedReplacements,
      componentInfoObjects: core2.componentInfoObjects,
      compositeAttributesObj: preprocessAttributesObject(
        component.constructor.createAttributesObject()
      ),
      components: core2._components,
      nComponents: newNComponents,
      stateIdInfo,
      publicCaseInsensitiveAliasSubstitutions: core2.publicCaseInsensitiveAliasSubstitutions.bind(core2)
    });
    for (const diagnostic of verificationResult.diagnostics) {
      core2.addDiagnostic(diagnostic);
    }
    newNComponents = verificationResult.nComponents;
    serializedReplacements = verificationResult.replacements;
    addAttributesToSingleReplacement(
      serializedReplacements,
      component,
      core2.componentInfoObjects
    );
  }
  component.replacementsWorkspace.replacementsCreated = stateIdInfo.num;
  await addReplacementsToResolver({
    core: core2,
    serializedReplacements,
    component
  });
  reserveComponentIndices(core2, newNComponents);
  await createAndSetReplacements({
    core: core2,
    component,
    serializedReplacements
  });
  if (shadowedComposite.replacementsToWithhold > 0) {
    component.replacementsToWithhold = shadowedComposite.replacementsToWithhold;
  }
  _finishExpanding({ core: core2, componentIdx: component.componentIdx });
  compositesExpanded.push(component.componentIdx);
  return { success: true, compositesExpanded };
}
function _finishExpanding({
  core: core2,
  componentIdx
}) {
  const targetInd = core2.updateInfo.compositesBeingExpanded.indexOf(componentIdx);
  if (targetInd === -1) {
    throw Error(
      `Something is wrong as we lost track that we were expanding ${componentIdx}`
    );
  }
  core2.updateInfo.compositesBeingExpanded.splice(targetInd, 1);
}
function reserveComponentIndices(core2, newNComponents) {
  if (newNComponents > core2._components.length) {
    core2._components[newNComponents - 1] = void 0;
  }
}
function adjustForCreateComponentIdxName({
  serializedReplacements,
  composite
}) {
  if (serializedReplacements.length === 1) {
    if (composite.attributes.createComponentIdx?.primitive.value != void 0) {
      serializedReplacements[0].componentIdx = composite.attributes.createComponentIdx.primitive.value;
    }
    if (composite.attributes.createComponentName?.primitive.value != void 0) {
      serializedReplacements[0].attributes.name = {
        type: "primitive",
        name: "name",
        primitive: {
          type: "string",
          value: composite.attributes.createComponentName.primitive.value
        }
      };
    }
  }
}
async function createAndSetReplacements({
  core: core2,
  component,
  serializedReplacements
}) {
  core2.parameterStack.push(component.sharedParameters, false);
  try {
    let replacementResult = await createIsolatedComponents({
      core: core2,
      serializedComponents: serializedReplacements,
      ancestors: component.ancestors,
      shadow: true,
      componentsReplacementOf: component
    });
    component.replacements = replacementResult.components;
  } catch (e3) {
    console.error(e3);
    component.replacements = await core2.setErrorReplacements({
      composite: component,
      message: e3.message
    });
  }
  core2.parameterStack.pop();
  await core2.dependencies.addBlockersFromChangedReplacements(component);
  component.isExpanded = true;
}
async function replaceCompositeChildren({
  core: core2,
  parent
}) {
  delete parent.placeholderActiveChildrenIndices;
  delete parent.placeholderActiveChildrenIndicesByComposite;
  delete parent.compositeReplacementActiveRange;
  let undisplayableErrorChildren;
  let nPlaceholdersAdded = 0;
  for (let childInd = 0; childInd < parent.activeChildren.length; childInd++) {
    let child = parent.activeChildren[childInd];
    if (child instanceof core2.componentInfoObjects.allComponentClasses._composite) {
      if (findChildGroup({
        core: core2,
        childType: child.componentType,
        parentClass: parent.constructor
      }).success) {
        continue;
      }
      let replaceWithPlaceholders = false;
      if (!child.isExpanded) {
        if (child.attributes.createComponentOfType?.primitive) {
          replaceWithPlaceholders = true;
        } else {
          continue;
        }
      }
      let replacements;
      if (replaceWithPlaceholders) {
        let numComponents;
        if (child.attributes.numComponents) {
          numComponents = child.attributes.numComponents.primitive.value;
        } else {
          numComponents = 1;
        }
        let componentType = core2.componentInfoObjects.componentTypeLowerCaseMapping[child.attributes.createComponentOfType.primitive.value.toLowerCase()];
        replacements = [];
        for (let i2 = 0; i2 < numComponents; i2++) {
          replacements.push({
            componentType,
            fromComposite: child.componentIdx,
            placeholderInd: nPlaceholdersAdded
          });
          nPlaceholdersAdded++;
        }
        parent.hasPlaceholderActiveChildren = true;
        let placeholdInds = [...Array(numComponents).keys()].map(
          (x2) => x2 + childInd
        );
        if (!parent.placeholderActiveChildrenIndices) {
          parent.placeholderActiveChildrenIndices = [];
        }
        parent.placeholderActiveChildrenIndices.push(...placeholdInds);
        if (!parent.placeholderActiveChildrenIndicesByComposite) {
          parent.placeholderActiveChildrenIndicesByComposite = {};
        }
        parent.placeholderActiveChildrenIndicesByComposite[child.componentIdx] = placeholdInds;
      } else {
        await markWithheldReplacementsInactive({
          core: core2,
          composite: child
        });
        replacements = child.replacements;
        if (child.replacementsToWithhold > 0) {
          replacements = replacements.slice(
            0,
            -child.replacementsToWithhold
          );
        }
        if (!parent.constructor.includeBlankStringChildren || parent.constructor.removeBlankStringChildrenPostSugar) {
          replacements = replacements.filter(
            (x2) => typeof x2 !== "string" || /\S/.test(x2)
          );
        }
      }
      if (!parent.compositeReplacementActiveRange) {
        parent.compositeReplacementActiveRange = [];
      }
      let replacementsCanBeInList = replacements.map(
        (repl) => Boolean(
          typeof repl !== "object" || core2.componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: repl.componentType,
            baseComponentType: "_inline"
          }) && repl.constructor.canBeInList !== false || repl.constructor.canBeInList
        )
      );
      for (let otherCompositeObject of parent.compositeReplacementActiveRange) {
        if (otherCompositeObject.lastInd >= childInd) {
          otherCompositeObject.lastInd += replacements.length - 1;
          otherCompositeObject.potentialListComponents.splice(
            childInd,
            1,
            ...replacementsCanBeInList
          );
        }
      }
      if (replacements.some(
        (repl) => repl.componentType === "_error"
      ) && !parent.constructor.canDisplayChildErrors) {
        if (!undisplayableErrorChildren) {
          undisplayableErrorChildren = [];
        }
        let errorReplacements = replacements.filter(
          (repl) => repl.componentType === "_error"
        );
        replacements = replacements.filter(
          (repl) => repl.componentType !== "_error"
        );
        undisplayableErrorChildren.push(...errorReplacements);
      }
      if (child.isExpanded) {
        parent.compositeReplacementActiveRange.push({
          compositeIdx: child.componentIdx,
          compositeName: core2.rootNames?.[child.componentIdx] ?? "_id_" + child.componentIdx.toString(),
          extendIdx: await child.stateValues.extendIdx,
          unresolvedPath: await child.stateValues.unresolvedPath,
          firstInd: childInd,
          lastInd: childInd + replacements.length - 1,
          asList: await child.stateValues.asList,
          potentialListComponents: replacementsCanBeInList
        });
      }
      parent.activeChildren.splice(childInd, 1, ...replacements);
      let allChildrenObj = parent.allChildren[child.componentIdx];
      delete allChildrenObj.activeChildrenIndex;
      for (let ind22 = 0; ind22 < replacements.length; ind22++) {
        let replacement = replacements[ind22];
        if (replacement.componentIdx != void 0) {
          parent.allChildren[replacement.componentIdx] = {
            activeChildrenIndex: childInd + ind22,
            component: replacement
          };
        }
      }
      let ind2 = parent.allChildrenOrdered.indexOf(child.componentIdx);
      parent.allChildrenOrdered.splice(
        ind2 + 1,
        0,
        ...replacements.filter((x2) => typeof x2 === "object").map(
          (x2) => x2.componentIdx ? x2.componentIdx : x2.placeholderInd
        )
      );
      if (replacements.length !== 1) {
        let nShift = replacements.length - 1;
        for (let ind22 = childInd + replacements.length; ind22 < parent.activeChildren.length; ind22++) {
          let child2 = parent.activeChildren[ind22];
          if (child2.componentIdx != void 0) {
            parent.allChildren[child2.componentIdx].activeChildrenIndex += nShift;
          }
        }
      }
      childInd--;
    }
  }
  if (undisplayableErrorChildren) {
    await addUndisplayableErrorChildrenToAncestor({
      core: core2,
      parent,
      undisplayableErrorChildren
    });
  }
}
async function addUndisplayableErrorChildrenToAncestor({
  core: core2,
  parent,
  undisplayableErrorChildren
}) {
  let ancestorToDisplayErrors = parent;
  let definingChildIndToAddError;
  while (!ancestorToDisplayErrors.constructor.canDisplayChildErrors) {
    let nextAncestor = core2._components[ancestorToDisplayErrors.parentIdx];
    definingChildIndToAddError = nextAncestor.definingChildren.indexOf(
      ancestorToDisplayErrors
    );
    ancestorToDisplayErrors = nextAncestor;
  }
  if (definingChildIndToAddError === -1) {
    definingChildIndToAddError = 0;
  }
  ancestorToDisplayErrors.definingChildren.splice(
    definingChildIndToAddError,
    0,
    ...undisplayableErrorChildren
  );
  await processNewDefiningChildren({
    core: core2,
    parent: ancestorToDisplayErrors,
    expandComposites: false
  });
}
async function markWithheldReplacementsInactive({
  core: core2,
  composite
}) {
  let numActive = composite.replacements.length;
  if (await composite.stateValues.isInactiveCompositeReplacement) {
    numActive = 0;
  } else if (composite.replacementsToWithhold > 0) {
    numActive -= composite.replacementsToWithhold;
  }
  for (let repl of composite.replacements.slice(0, numActive)) {
    await changeInactiveComponentAndDescendants({
      core: core2,
      component: repl,
      inactive: false
    });
  }
  for (let repl of composite.replacements.slice(numActive)) {
    await changeInactiveComponentAndDescendants({
      core: core2,
      component: repl,
      inactive: true
    });
  }
  if (!await composite.stateValues.isInactiveCompositeReplacement) {
    let cIdx = composite.componentIdx;
    if (core2.updateInfo.inactiveCompositesToUpdateReplacements.has(cIdx)) {
      core2.updateInfo.inactiveCompositesToUpdateReplacements.delete(cIdx);
      core2.updateInfo.compositesToUpdateReplacements.add(cIdx);
    }
  }
}
async function changeInactiveComponentAndDescendants({
  core: core2,
  component,
  inactive
}) {
  if (typeof component !== "object") {
    return;
  }
  if (await component.stateValues.isInactiveCompositeReplacement !== inactive) {
    component.state.isInactiveCompositeReplacement.value = inactive;
    await core2.markUpstreamDependentsStale({
      component,
      varName: "isInactiveCompositeReplacement"
    });
    core2.dependencies.recordActualChangeInUpstreamDependencies({
      component,
      varName: "isInactiveCompositeReplacement"
    });
    for (const childIdxStr in component.allChildren) {
      await changeInactiveComponentAndDescendants({
        core: core2,
        component: core2._components[childIdxStr],
        inactive
      });
    }
    for (let attrName in component.attributes) {
      let attrComp = component.attributes[attrName].component;
      if (attrComp) {
        await changeInactiveComponentAndDescendants({
          core: core2,
          component: core2._components[attrComp.componentIdx],
          inactive
        });
      }
    }
    if (component.replacements) {
      await markWithheldReplacementsInactive({
        core: core2,
        composite: component
      });
    }
  }
}
function recursivelyReplaceCompositesWithReplacements({
  core: core2,
  replacements,
  recurseNonStandardComposites = false,
  includeWithheldReplacements = false,
  stopIfHaveProp
}) {
  let compositesFound = [];
  let newReplacements = [];
  let unexpandedCompositesReady = [];
  let unexpandedCompositesNotReady = [];
  for (let replacement of replacements) {
    const isComposite = core2.componentInfoObjects.isCompositeComponent({
      componentType: replacement.componentType,
      includeNonStandard: recurseNonStandardComposites
    });
    if (!isComposite) {
      newReplacements.push(replacement);
      continue;
    }
    if (stopIfHaveProp) {
      const checkForPublic = core2.matchPublicStateVariables({
        stateVariables: [stopIfHaveProp],
        componentClass: replacement.constructor
      })[0];
      if (!checkForPublic.startsWith("__not_public_")) {
        newReplacements.push(replacement);
        continue;
      }
    }
    compositesFound.push(replacement.componentIdx);
    if (!replacement.isExpanded) {
      if (replacement.state.readyToExpandWhenResolved.isResolved) {
        unexpandedCompositesReady.push(replacement.componentIdx);
      } else {
        unexpandedCompositesNotReady.push(replacement.componentIdx);
      }
      newReplacements.push(replacement);
      continue;
    }
    let replacementReplacements = replacement.replacements;
    if (!includeWithheldReplacements && replacement.replacementsToWithhold > 0) {
      replacementReplacements = replacementReplacements.slice(
        0,
        -replacement.replacementsToWithhold
      );
    }
    const recursionResult = recursivelyReplaceCompositesWithReplacements({
      core: core2,
      replacements: replacementReplacements,
      recurseNonStandardComposites,
      includeWithheldReplacements,
      stopIfHaveProp
    });
    compositesFound.push(...recursionResult.compositesFound);
    newReplacements.push(...recursionResult.newReplacements);
    unexpandedCompositesReady.push(
      ...recursionResult.unexpandedCompositesReady
    );
    unexpandedCompositesNotReady.push(
      ...recursionResult.unexpandedCompositesNotReady
    );
  }
  return {
    compositesFound,
    newReplacements,
    unexpandedCompositesReady,
    unexpandedCompositesNotReady
  };
}
function renameStateVariable({
  stateVariableDefinitions,
  oldName,
  newName
}) {
  let stateVarDef = stateVariableDefinitions[newName] = stateVariableDefinitions[oldName];
  delete stateVariableDefinitions[oldName];
  stateVarDef.excludeFromSchema = true;
  if (stateVarDef.additionalStateVariablesDefined) {
    let ind = stateVarDef.additionalStateVariablesDefined.indexOf(oldName);
    if (ind !== -1) {
      stateVarDef.additionalStateVariablesDefined[ind] = newName;
    }
  }
  let originalDefinition = stateVarDef.definition;
  let keysInObjects = [
    "setValue",
    "useEssentialOrDefaultValue",
    "setEssentialValue"
  ];
  let entriesInArrays = ["noChanges"];
  stateVarDef.definition = function(args) {
    let result = originalDefinition(args);
    for (let key of keysInObjects) {
      if (result[key] && oldName in result[key]) {
        result[key][newName] = result[key][oldName];
        delete result[key][oldName];
      }
    }
    for (let key of entriesInArrays) {
      if (result[key]) {
        let ind = result[key].indexOf(oldName);
        if (ind !== -1) {
          result[key][ind] = newName;
        }
      }
    }
    return result;
  };
  let originalInverseDefinition = stateVarDef.inverseDefinition;
  if (originalInverseDefinition) {
    stateVarDef.inverseDefinition = async function(args) {
      let desiredStateVariableValues = args.desiredStateVariableValues;
      desiredStateVariableValues[oldName] = desiredStateVariableValues[newName];
      delete desiredStateVariableValues[newName];
      let results = await originalInverseDefinition(args);
      if (results.success) {
        for (let instruction of results.instructions) {
          if (instruction.setEssentialValue === oldName) {
            instruction.setEssentialValue = newName;
          }
        }
      }
      return results;
    };
  }
}
function defaultGetArrayKeysFromVarNameMultiDim({
  arrayEntryPrefix,
  varEnding,
  arraySize,
  numDimensions
}) {
  let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
  if (indices.length === numDimensions && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
    if (arraySize) {
      if (indices.every((x2, i2) => x2 < arraySize[i2])) {
        return [String(indices)];
      } else {
        return [];
      }
    } else {
      return [String(indices)];
    }
  } else {
    return [];
  }
}
function defaultGetArrayKeysFromVarNameOneDim({
  arrayEntryPrefix,
  varEnding,
  arraySize
}) {
  let index2 = Number(varEnding) - 1;
  if (Number.isInteger(index2) && index2 >= 0) {
    if (arraySize) {
      if (index2 < arraySize[0]) {
        return [String(index2)];
      } else {
        return [];
      }
    } else {
      return [String(index2)];
    }
  } else {
    return [];
  }
}
function returnDefaultGetArrayKeysFromVarName(numDim) {
  if (numDim > 1) {
    return defaultGetArrayKeysFromVarNameMultiDim;
  } else {
    return defaultGetArrayKeysFromVarNameOneDim;
  }
}
function returnDefaultArrayVarNameFromPropIndex(numDim, arrayEntryPrefix) {
  if (numDim > 1) {
    return function(propIndex, varName) {
      return arrayEntryPrefix + [
        ...propIndex.map((x2) => Math.round(Number(x2))),
        ...Array(numDim - propIndex.length).fill(1)
      ].join("_");
    };
  } else {
    return function(propIndex, varName) {
      return arrayEntryPrefix + propIndex[0];
    };
  }
}
function getStaleStateVariableValue() {
  return this.svComponent.coreFunctions.getStateVariableValue({
    component: this.svComponent,
    stateVariable: this.svVarName
  });
}
const STALE_VALUE_DESCRIPTOR = Object.freeze({
  get: getStaleStateVariableValue,
  configurable: true
});
function installStaleValueGetter(stateVarObj) {
  delete stateVarObj.value;
  Object.defineProperty(stateVarObj, "value", STALE_VALUE_DESCRIPTOR);
}
async function entryGetValueViaGetEntryValues() {
  const arrayStateVarObj = this.svComponent.state[this.arrayStateVariable];
  return await arrayStateVarObj.getEntryValues({
    varName: this.svVarName
  });
}
async function entryGetValueFromArrayValues() {
  const arrayStateVarObj = this.svComponent.state[this.arrayStateVariable];
  const arrayKeys = await this.arrayKeys;
  if (arrayKeys.length === 0) {
    return;
  }
  const value = [];
  for (const arrayKey of arrayKeys) {
    value.push(arrayStateVarObj.getArrayValue({ arrayKey }));
  }
  if (value.length === 1) {
    return value[0];
  } else {
    return value;
  }
}
function getEntryArrayKeys() {
  return (async () => {
    await this.svComponent.state[this.arrayStateVariable].arraySize;
    return this._arrayKeys;
  })();
}
const ENTRY_ARRAY_KEYS_DESCRIPTOR = Object.freeze({
  get: getEntryArrayKeys
});
function getEntryUnflattenedArrayKeys() {
  return (async () => {
    await this.svComponent.state[this.arrayStateVariable].arraySize;
    return this._unflattenedArrayKeys;
  })();
}
const ENTRY_UNFLATTENED_ARRAY_KEYS_DESCRIPTOR = Object.freeze({ get: getEntryUnflattenedArrayKeys });
function getEntryArraySize() {
  return this.svComponent.state[this.arrayStateVariable].arraySize;
}
const ENTRY_ARRAY_SIZE_DESCRIPTOR = Object.freeze({
  get: getEntryArraySize
});
function getEntryArrayEntrySize() {
  return (async () => {
    let unflattenedArrayKeys = await this.unflattenedArrayKeys;
    let arrayEntrySize = [];
    let subArray = [unflattenedArrayKeys];
    for (let i2 = 0; i2 < this.numDimensions; i2++) {
      subArray = subArray[0];
      arrayEntrySize.push(subArray.length);
    }
    arrayEntrySize.reverse();
    return arrayEntrySize;
  })();
}
const ENTRY_ARRAY_ENTRY_SIZE_DESCRIPTOR = Object.freeze({
  get: getEntryArrayEntrySize
});
function multiDimKeyToIndex(key) {
  return key.split(",").map((x2) => Number(x2));
}
function oneDimKeyToIndex(key) {
  return Number(key);
}
function arrayIndexToKey(index2) {
  return String(index2);
}
function defaultReturnEntryDimensions() {
  return 0;
}
function multiDimSetArrayValue({ value, arrayKey, arraySize, arrayValues = this.arrayValues }) {
  const component = this.svComponent;
  const addDiagnostic = component.coreFunctions.addDiagnostic;
  const numDimensions = this.numDimensions;
  let index2 = this.keyToIndex(arrayKey);
  let numDimensionsInArrayKey = index2.length;
  if (numDimensionsInArrayKey > numDimensions) {
    addDiagnostic({
      type: "info",
      message: "Cannot set array value.  Number of dimensions is too large.",
      position: component.position,
      sourceDoc: component.sourceDoc
    });
    return { nFailures: 1 };
  }
  let arrayValuesDrillDown = arrayValues;
  let arraySizeDrillDown = arraySize;
  for (let indComponent of index2.slice(0, index2.length - 1)) {
    if (indComponent >= 0 && indComponent < arraySizeDrillDown[0]) {
      if (!arrayValuesDrillDown[indComponent]) {
        arrayValuesDrillDown[indComponent] = [];
      }
      arrayValuesDrillDown = arrayValuesDrillDown[indComponent];
      arraySizeDrillDown = arraySizeDrillDown.slice(1);
    } else {
      addDiagnostic({
        type: "info",
        message: "ignore setting array value out of bounds",
        position: component.position,
        sourceDoc: component.sourceDoc
      });
      return { nFailures: 1 };
    }
  }
  let nFailures = 0;
  if (numDimensionsInArrayKey < numDimensions) {
    let setArrayValuesPiece = function(desiredValue, arrayValuesPiece, arraySizePiece) {
      if (!Array.isArray(desiredValue)) {
        addDiagnostic({
          type: "info",
          message: "ignoring array values with insufficient dimensions",
          position: component.position,
          sourceDoc: component.sourceDoc
        });
        return { nFailures: 1 };
      }
      let nFailuresSub = 0;
      let currentSize = arraySizePiece[0];
      if (desiredValue.length > currentSize) {
        addDiagnostic({
          type: "info",
          message: "ignoring array values out of bounds",
          position: component.position,
          sourceDoc: component.sourceDoc
        });
        nFailuresSub += desiredValue.length - currentSize;
        desiredValue = desiredValue.slice(0, currentSize);
      }
      if (arraySizePiece.length === 1) {
        for (let [ind, val] of desiredValue.entries()) {
          arrayValuesPiece[ind] = val;
        }
      } else {
        for (let [ind, val] of desiredValue.entries()) {
          if (!arrayValuesPiece[ind]) {
            arrayValuesPiece[ind] = [];
          }
          let result2 = setArrayValuesPiece(
            val,
            arrayValuesPiece[ind],
            arraySizePiece.slice(1)
          );
          nFailuresSub += result2.nFailures;
        }
      }
      return { nFailures: nFailuresSub };
    };
    let result = setArrayValuesPiece(
      value,
      arrayValuesDrillDown,
      arraySizeDrillDown
    );
    nFailures += result.nFailures;
  } else {
    arrayValuesDrillDown[index2[index2.length - 1]] = value;
  }
  return { nFailures };
}
function oneDimSetArrayValue({ value, arrayKey, arraySize, arrayValues = this.arrayValues }) {
  let ind = this.keyToIndex(arrayKey);
  if (ind >= 0 && ind < arraySize[0]) {
    arrayValues[ind] = value;
    return { nFailures: 0 };
  } else {
    const component = this.svComponent;
    component.coreFunctions.addDiagnostic({
      type: "info",
      message: `Ignoring setting array values out of bounds: ${arrayKey} of ${this.svVarName}`,
      position: component.position,
      sourceDoc: component.sourceDoc
    });
    return { nFailures: 1 };
  }
}
function multiDimGetArrayValue({ arrayKey, arrayValues = this.arrayValues }) {
  let index2 = this.keyToIndex(arrayKey);
  let aVals = arrayValues;
  for (let indComponent of index2.slice(0, index2.length - 1)) {
    aVals = aVals[indComponent];
    if (!aVals) {
      return void 0;
    }
  }
  return aVals[index2[index2.length - 1]];
}
function oneDimGetArrayValue({ arrayKey, arrayValues = this.arrayValues }) {
  return arrayValues[arrayKey];
}
function multiDimGetAllArrayKeys(arraySize, flatten2 = true, desiredSize) {
  function prependToAllKeys(keys2, newStuff) {
    for (let [ind, key] of keys2.entries()) {
      if (Array.isArray(key)) {
        prependToAllKeys(key, newStuff);
      } else {
        keys2[ind] = newStuff + "," + key;
      }
    }
  }
  function getAllArrayKeysSub(subArraySize) {
    if (subArraySize.length === 1) {
      return Array.from(Array(subArraySize[0]), (_2, i2) => String(i2));
    } else {
      let currentSize = subArraySize[0];
      let subSubKeys = getAllArrayKeysSub(subArraySize.slice(1));
      let subKeys = [];
      for (let ind = 0; ind < currentSize; ind++) {
        if (flatten2) {
          subKeys.push(...subSubKeys.map((x2) => ind + "," + x2));
        } else {
          let newSubSubKeys = deepClone(subSubKeys);
          prependToAllKeys(newSubSubKeys, ind);
          subKeys.push(newSubSubKeys);
        }
      }
      return subKeys;
    }
  }
  if (desiredSize) {
    if (desiredSize.length === 0) {
      return [];
    } else {
      return getAllArrayKeysSub(desiredSize);
    }
  } else if (!arraySize || arraySize.length === 0) {
    return [];
  } else {
    return getAllArrayKeysSub(arraySize);
  }
}
function oneDimGetAllArrayKeys(arraySize, flatten2, desiredSize) {
  if (desiredSize) {
    if (desiredSize.length === 0) {
      return [];
    } else {
      return Array.from(Array(desiredSize[0]), (_2, i2) => String(i2));
    }
  } else if (!arraySize || arraySize.length === 0) {
    return [];
  } else {
    return Array.from(Array(arraySize[0]), (_2, i2) => String(i2));
  }
}
function defaultMultiDimArrayVarNameFromArrayKey(arrayKey) {
  return this.entryPrefixes[0] + arrayKey.split(",").map((x2) => Number(x2) + 1).join("_");
}
function defaultOneDimArrayVarNameFromArrayKey(arrayKey) {
  return this.entryPrefixes[0] + String(Number(arrayKey) + 1);
}
async function multiDimAdjustArrayToNewArraySize() {
  function resizeSubArray(subArray, subArraySize) {
    subArray.length = subArraySize[0];
    if (subArraySize.length > 1) {
      let subSubArraySize = subArraySize.slice(1);
      for (let [ind, subSubArray] of subArray.entries()) {
        if (!subSubArray) {
          subSubArray = subArray[ind] = [];
        }
        resizeSubArray(subSubArray, subSubArraySize);
      }
    }
  }
  let arraySize = await this.arraySize;
  resizeSubArray(this.arrayValues, arraySize);
}
async function oneDimAdjustArrayToNewArraySize() {
  let arraySize = await this.arraySize;
  this.arrayValues.length = arraySize[0];
}
function resolveArrayStateVarObj(svObj) {
  return svObj.isArrayEntry ? svObj.svComponent.state[svObj.arrayStateVariable] : svObj;
}
function defaultReturnWrappingComponents(prefix) {
  return [];
}
async function arrayReturnDependencies(args) {
  const arrayStateVarObj = resolveArrayStateVarObj(this);
  const component = arrayStateVarObj.svComponent;
  const arrayVarName = arrayStateVarObj.svVarName;
  args.arraySize = await arrayStateVarObj.arraySize;
  for (let key in args.stateValues) {
    if (key.slice(0, 8) === "__array_") {
      delete args.stateValues[key];
    }
  }
  if (args.arrayKeys === void 0) {
    args.arrayKeys = arrayStateVarObj.getAllArrayKeys(args.arraySize);
  }
  if (!arrayStateVarObj.dependencyNames) {
    arrayStateVarObj.dependencyNames = {
      namesByKey: {},
      keysByName: {},
      global: []
    };
    if (arrayStateVarObj.additionalStateVariablesDefined) {
      for (let vName of arrayStateVarObj.additionalStateVariablesDefined) {
        component.state[vName].dependencyNames = arrayStateVarObj.dependencyNames;
      }
    }
  }
  let dependencies = {};
  if (arrayStateVarObj.basedOnArrayKeyStateVariables && args.arrayKeys.length > 1) {
    const allStateVariablesAffected = [
      arrayVarName,
      ...arrayStateVarObj.additionalStateVariablesDefined ?? []
    ];
    for (let arrayKey of args.arrayKeys) {
      for (let vName of allStateVariablesAffected) {
        let sObj = component.state[vName];
        dependencies[vName + "_" + arrayKey] = {
          dependencyType: "stateVariable",
          variableName: sObj.arrayVarNameFromArrayKey(arrayKey)
        };
      }
    }
  } else {
    let arrayDependencies = arrayStateVarObj.returnArrayDependenciesByKey(args);
    if (arrayDependencies.globalDependencies) {
      arrayStateVarObj.dependencyNames.global = Object.keys(
        arrayDependencies.globalDependencies
      );
      Object.assign(dependencies, arrayDependencies.globalDependencies);
    }
    if (!arrayDependencies.dependenciesByKey) {
      arrayDependencies.dependenciesByKey = {};
    }
    for (let arrayKey of args.arrayKeys) {
      if (!(arrayKey in arrayStateVarObj.dependencyNames.namesByKey)) {
        args.changedDependency = true;
      }
      arrayStateVarObj.dependencyNames.namesByKey[arrayKey] = {};
      for (let depName in arrayDependencies.dependenciesByKey[arrayKey]) {
        let extendedDepName = "__" + arrayKey + "_" + depName;
        dependencies[extendedDepName] = arrayDependencies.dependenciesByKey[arrayKey][depName];
        arrayStateVarObj.dependencyNames.namesByKey[arrayKey][depName] = extendedDepName;
        if (!arrayStateVarObj.dependencyNames.keysByName[extendedDepName]) {
          arrayStateVarObj.dependencyNames.keysByName[extendedDepName] = [];
        }
        if (!arrayStateVarObj.dependencyNames.keysByName[extendedDepName].includes(arrayKey)) {
          arrayStateVarObj.dependencyNames.keysByName[extendedDepName].push(arrayKey);
        }
      }
    }
    arrayStateVarObj.dependencyNames.global.push("__array_size");
  }
  dependencies.__array_size = {
    dependencyType: "stateVariable",
    variableName: arrayStateVarObj.arraySizeStateVariable
  };
  return dependencies;
}
function arrayGetCurrentFreshness({ freshnessInfo, arrayKeys, arraySize }) {
  const arrayStateVarObj = resolveArrayStateVarObj(this);
  const arrayVarName = arrayStateVarObj.svVarName;
  if (arrayKeys === void 0) {
    arrayKeys = arrayStateVarObj.getAllArrayKeys(arraySize);
  }
  let freshByKey = freshnessInfo.freshByKey;
  let numberFresh = freshnessInfo.freshArraySize ? 1 : 0;
  for (let arrayKey of arrayKeys) {
    if (freshByKey[arrayKey]) {
      numberFresh += 1;
    }
  }
  if (numberFresh > 0) {
    if (numberFresh === arrayKeys.length + 1) {
      return { fresh: { [arrayVarName]: true } };
    } else {
      return { partiallyFresh: { [arrayVarName]: numberFresh } };
    }
  } else {
    return { fresh: { [arrayVarName]: false } };
  }
}
function arrayMarkStale({ freshnessInfo, changes, arrayKeys, arraySize }) {
  const arrayStateVarObj = resolveArrayStateVarObj(this);
  const arrayVarName = arrayStateVarObj.svVarName;
  let result = {};
  if (arrayKeys === void 0) {
    arrayKeys = arrayStateVarObj.getAllArrayKeys(arraySize);
  }
  if (arrayStateVarObj.markStaleByKey) {
    result = arrayStateVarObj.markStaleByKey({ arrayKeys, changes });
  }
  let freshByKey = freshnessInfo.freshByKey;
  if (changes.__array_size) {
    freshnessInfo.freshArraySize = false;
    freshnessInfo.freshByKey = {};
    result.fresh = { [arrayVarName]: false };
    return result;
  }
  if (Object.keys(freshByKey).length === 0) {
    freshnessInfo.freshByKey = {};
    if (freshnessInfo.freshArraySize) {
      result.partiallyFresh = { [arrayVarName]: 1 };
      return result;
    } else {
      result.fresh = { [arrayVarName]: false };
      return result;
    }
  }
  for (let changeName in changes) {
    if (arrayStateVarObj.dependencyNames.global.includes(changeName)) {
      freshnessInfo.freshByKey = {};
      if (freshnessInfo.freshArraySize) {
        result.partiallyFresh = { [arrayVarName]: 1 };
        return result;
      } else {
        result.fresh = { [arrayVarName]: false };
        return result;
      }
    }
    if (arrayStateVarObj.basedOnArrayKeyStateVariables && arrayKeys.length > 1) {
      delete freshByKey[changeName];
    } else {
      for (let key of arrayStateVarObj.dependencyNames.keysByName[changeName]) {
        delete freshByKey[key];
      }
    }
  }
  let numberFresh = freshnessInfo.freshArraySize ? 1 : 0;
  for (let arrayKey of arrayKeys) {
    if (freshByKey[arrayKey]) {
      numberFresh += 1;
    }
  }
  if (numberFresh > 0) {
    if (numberFresh === arrayKeys.length + 1) {
      result.fresh = { [arrayVarName]: true };
      return result;
    } else {
      result.partiallyFresh = { [arrayVarName]: numberFresh };
      return result;
    }
  } else {
    result.fresh = { [arrayVarName]: false };
    return result;
  }
}
function arrayFreshenOnNoChanges({ arrayKeys, freshnessInfo, arraySize }) {
  const arrayStateVarObj = resolveArrayStateVarObj(this);
  let freshByKey = freshnessInfo.freshByKey;
  if (arrayKeys === void 0) {
    arrayKeys = arrayStateVarObj.getAllArrayKeys(arraySize);
  }
  for (let arrayKey of arrayKeys) {
    freshByKey[arrayKey] = true;
  }
}
function extractArrayDependencies(arrayStateVarObj, dependencyValues, arrayKeys, usedDefault) {
  let globalDependencyValues = {};
  let globalUsedDefault = {};
  for (let dependencyName of arrayStateVarObj.dependencyNames.global) {
    globalDependencyValues[dependencyName] = dependencyValues[dependencyName];
    globalUsedDefault[dependencyName] = usedDefault[dependencyName];
  }
  let dependencyValuesByKey = {};
  let usedDefaultByKey = {};
  let foundAllDependencyValuesForKey = {};
  for (let arrayKey of arrayKeys) {
    dependencyValuesByKey[arrayKey] = {};
    usedDefaultByKey[arrayKey] = {};
    if (arrayKey in arrayStateVarObj.dependencyNames.namesByKey) {
      foundAllDependencyValuesForKey[arrayKey] = true;
      for (let dependencyName in arrayStateVarObj.dependencyNames.namesByKey[arrayKey]) {
        let extendedDepName = arrayStateVarObj.dependencyNames.namesByKey[arrayKey][dependencyName];
        if (extendedDepName in dependencyValues) {
          dependencyValuesByKey[arrayKey][dependencyName] = dependencyValues[extendedDepName];
          usedDefaultByKey[arrayKey][dependencyName] = usedDefault[extendedDepName];
        } else {
          foundAllDependencyValuesForKey[arrayKey] = false;
        }
      }
    }
  }
  return {
    globalDependencyValues,
    globalUsedDefault,
    dependencyValuesByKey,
    usedDefaultByKey,
    foundAllDependencyValuesForKey
  };
}
function arrayDefinition(args) {
  const arrayStateVarObj = resolveArrayStateVarObj(this);
  const arrayVarName = arrayStateVarObj.svVarName;
  const component = arrayStateVarObj.svComponent;
  if (args.arrayKeys === void 0) {
    args.arrayKeys = arrayStateVarObj.getAllArrayKeys(args.arraySize);
  }
  if (arrayStateVarObj.basedOnArrayKeyStateVariables && args.arrayKeys.length > 1) {
    return {};
  } else {
    let extractedDeps = extractArrayDependencies(
      arrayStateVarObj,
      args.dependencyValues,
      args.arrayKeys,
      args.usedDefault
    );
    let globalDependencyValues = extractedDeps.globalDependencyValues;
    let globalUsedDefault = extractedDeps.globalUsedDefault;
    let dependencyValuesByKey = extractedDeps.dependencyValuesByKey;
    let usedDefaultByKey = extractedDeps.usedDefaultByKey;
    let foundAllDependencyValuesForKey = extractedDeps.foundAllDependencyValuesForKey;
    delete args.dependencyValues;
    args.globalDependencyValues = globalDependencyValues;
    args.globalUsedDefault = globalUsedDefault;
    args.dependencyValuesByKey = dependencyValuesByKey;
    args.usedDefaultByKey = usedDefaultByKey;
    let arrayKeysToRecalculate = [];
    let freshByKey = args.freshnessInfo.freshByKey;
    for (let arrayKey of args.arrayKeys) {
      if (!freshByKey[arrayKey] && foundAllDependencyValuesForKey[arrayKey]) {
        freshByKey[arrayKey] = true;
        arrayKeysToRecalculate.push(arrayKey);
      }
    }
    let result;
    if (arrayKeysToRecalculate.length === 0) {
      result = {};
    } else {
      args.arrayKeys = arrayKeysToRecalculate;
      if (!arrayStateVarObj.arrayDefinitionByKey) {
        throw Error(
          `For ${arrayVarName} of ${component.componentType}, arrayDefinitionByKey must be a function`
        );
      }
      result = arrayStateVarObj.arrayDefinitionByKey(args);
      if (result.setValue && result.setValue[arrayVarName]) {
        for (let arrayKey in result.setValue[arrayVarName]) {
          freshByKey[arrayKey] = true;
        }
      }
      if (result.useEssentialOrDefaultValue && result.useEssentialOrDefaultValue[arrayVarName]) {
        for (let arrayKey in result.useEssentialOrDefaultValue[arrayVarName]) {
          freshByKey[arrayKey] = true;
        }
      }
    }
    if (!args.freshnessInfo.freshArraySize) {
      if (args.changes.__array_size) {
        result.arraySizeChanged = [arrayVarName];
        if (arrayStateVarObj.additionalStateVariablesDefined) {
          for (let varName of arrayStateVarObj.additionalStateVariablesDefined) {
            if (component.state[varName].isArray) {
              result.arraySizeChanged.push(varName);
            }
          }
        }
      }
      args.freshnessInfo.freshArraySize = true;
    }
    return result;
  }
}
function arrayInverseDefinition(args) {
  const arrayStateVarObj = resolveArrayStateVarObj(this);
  const arrayVarName = arrayStateVarObj.svVarName;
  if (!arrayStateVarObj.inverseArrayDefinitionByKey) {
    return { success: false };
  }
  if (args.arrayKeys === void 0) {
    args.arrayKeys = arrayStateVarObj.getAllArrayKeys(args.arraySize);
  }
  if (arrayStateVarObj.basedOnArrayKeyStateVariables && args.arrayKeys.length > 1) {
    const allStateVariablesAffected = [
      arrayVarName,
      ...arrayStateVarObj.additionalStateVariablesDefined ?? []
    ];
    let instructions = [];
    for (let vName of allStateVariablesAffected) {
      for (let key in args.desiredStateVariableValues[vName]) {
        let depName = vName + "_" + key;
        if (depName in args.dependencyValues) {
          instructions.push({
            setDependency: depName,
            desiredValue: args.desiredStateVariableValues[vName][key],
            treatAsInitialChange: args.initialChange
          });
        }
      }
    }
    return {
      success: true,
      instructions
    };
  } else {
    let extractedDeps = extractArrayDependencies(
      arrayStateVarObj,
      args.dependencyValues,
      args.arrayKeys,
      args.usedDefault
    );
    let globalDependencyValues = extractedDeps.globalDependencyValues;
    let globalUsedDefault = extractedDeps.globalUsedDefault;
    let dependencyValuesByKey = extractedDeps.dependencyValuesByKey;
    let usedDefaultByKey = extractedDeps.usedDefaultByKey;
    delete args.dependencyValues;
    args.globalDependencyValues = globalDependencyValues;
    args.globalUsedDefault = globalUsedDefault;
    args.dependencyValuesByKey = dependencyValuesByKey;
    args.usedDefaultByKey = usedDefaultByKey;
    args.dependencyNamesByKey = arrayStateVarObj.dependencyNames.namesByKey;
    if (!arrayStateVarObj.allowExtraArrayKeysInInverse) {
      let newDesiredStateVariableValues = {};
      for (let vName in args.desiredStateVariableValues) {
        newDesiredStateVariableValues[vName] = {};
        for (let key in args.desiredStateVariableValues[vName]) {
          if (args.arrayKeys.includes(key)) {
            newDesiredStateVariableValues[vName][key] = args.desiredStateVariableValues[vName][key];
          }
        }
      }
      args.desiredStateVariableValues = newDesiredStateVariableValues;
    }
    let result = arrayStateVarObj.inverseArrayDefinitionByKey(args);
    return result;
  }
}
function getArrayArraySize() {
  return (async () => {
    if (!this.svComponent.state[this.arraySizeStateVariable].initiallyResolved) {
      return [];
    }
    if (this.arraySizeStale) {
      await this.recalculateArraySizeDependentQuantities();
    }
    return await this.svComponent.stateValues[this.arraySizeStateVariable];
  })();
}
const ARRAY_ARRAY_SIZE_DESCRIPTOR = Object.freeze({
  get: getArrayArraySize
});
function normalizeArrayStateVariableDefaults(def, varName) {
  if (!def.isArray) {
    return;
  }
  if (def.numDimensions === void 0) {
    def.numDimensions = 1;
  }
  if (!def.entryPrefixes) {
    def.entryPrefixes = [varName];
  }
  const multiDim = def.numDimensions > 1;
  if (!def.keyToIndex) {
    def.keyToIndex = multiDim ? multiDimKeyToIndex : oneDimKeyToIndex;
  }
  if (!def.indexToKey) {
    def.indexToKey = arrayIndexToKey;
  }
  if (!def.getAllArrayKeys) {
    def.getAllArrayKeys = multiDim ? multiDimGetAllArrayKeys : oneDimGetAllArrayKeys;
  }
  if (!def.getArrayKeysFromVarName) {
    def.getArrayKeysFromVarName = returnDefaultGetArrayKeysFromVarName(
      def.numDimensions
    );
  }
  if (!def.arrayVarNameFromArrayKey) {
    def.arrayVarNameFromArrayKey = multiDim ? defaultMultiDimArrayVarNameFromArrayKey : defaultOneDimArrayVarNameFromArrayKey;
  }
  if (!def.arrayVarNameFromPropIndex) {
    def.arrayVarNameFromPropIndex = returnDefaultArrayVarNameFromPropIndex(
      def.numDimensions,
      def.entryPrefixes[0]
    );
  }
  if (!def.returnEntryDimensions) {
    def.returnEntryDimensions = defaultReturnEntryDimensions;
  }
  if (def.shadowingInstructions) {
    if (!def.shadowingInstructions.returnWrappingComponents) {
      def.shadowingInstructions.returnWrappingComponents = defaultReturnWrappingComponents;
    }
    def.wrappingComponents = def.shadowingInstructions.returnWrappingComponents();
  }
}
function arraySizeDefinition({ dependencyValues }) {
  let arraySize = this.svComponent.state[this.arraySizeSourceVarName].returnArraySize({
    dependencyValues
  });
  for (let [ind, value] of arraySize.entries()) {
    if (!(Number.isInteger(value) && value >= 0)) {
      arraySize[ind] = 0;
    }
  }
  return { setValue: { [this.svVarName]: arraySize } };
}
function arraySizeMarkStale() {
  for (let varName of this.arraySizeVarNamesAffected) {
    this.svComponent.state[varName].arraySizeStale = true;
  }
  return {};
}
async function arrayRecalculateArraySizeDependentQuantities() {
  const component = this.svComponent;
  let newArraySize = await component.stateValues[this.arraySizeStateVariable];
  if (this.previousArraySize.length !== newArraySize.length || this.previousArraySize.some((v2, i2) => v2 != newArraySize[i2])) {
    this.previousArraySize = [...newArraySize];
    let varNamesIncluding = this.varNamesIncludingArrayKeys = {};
    for (let entryName of this.arrayEntryNames) {
      let entryStateVarObj = component.state[entryName];
      let arrayKeys = this.getArrayKeysFromVarName({
        arrayEntryPrefix: entryStateVarObj.entryPrefix,
        varEnding: entryStateVarObj.varEnding,
        arraySize: newArraySize,
        numDimensions: this.numDimensions
      });
      entryStateVarObj._unflattenedArrayKeys = arrayKeys;
      entryStateVarObj._arrayKeys = flattenDeep(arrayKeys);
      for (let arrayKey of entryStateVarObj._arrayKeys) {
        if (!varNamesIncluding[arrayKey]) {
          varNamesIncluding[arrayKey] = [];
        }
        varNamesIncluding[arrayKey].push(entryName);
      }
    }
  }
  this.arraySizeStale = false;
}
async function entryReturnDependencies(args) {
  const arrayStateVarObj = this.svComponent.state[this.arrayStateVariable];
  args.arraySize = await this.arraySize;
  args.arrayKeys = await this.arrayKeys;
  let dependencies = await arrayStateVarObj.returnDependencies(args);
  let numNames = Object.keys(
    arrayStateVarObj.dependencyNames.namesByKey
  ).length;
  if (this.numberNamesInPreviousReturnDep !== numNames) {
    args.changedDependency = true;
  }
  this.numberNamesInPreviousReturnDep = numNames;
  return dependencies;
}
async function initializeComponentStateVariables({
  core: core2,
  component
}) {
  for (let stateVariable in component.state) {
    if (component.state[stateVariable].isAlias) {
      if (!component.stateVarAliases) {
        component.stateVarAliases = {};
      }
      component.stateVarAliases[stateVariable] = component.state[stateVariable].targetVariableName;
      delete component.state[stateVariable];
    } else {
      initializeStateVariablePlaceholder({
        core: core2,
        component,
        stateVariable
      });
    }
  }
}
function initializeStateVariablePlaceholder({
  core: core2,
  component,
  stateVariable
}) {
  const stateVarObj = component.state[stateVariable];
  stateVarObj.svComponent = component;
  stateVarObj.svVarName = stateVariable;
  stateVarObj.isResolved = false;
  installStaleValueGetter(stateVarObj);
  if (stateVarObj.isArray) {
    if (!component.arrayEntryPrefixes) {
      component.arrayEntryPrefixes = {};
    }
    const entryPrefixes = stateVarObj.entryPrefixes ?? [stateVariable];
    for (const prefix of entryPrefixes) {
      component.arrayEntryPrefixes[prefix] = stateVariable;
    }
  }
  if (stateVarObj.triggerActionOnChange) {
    let componentTriggers = core2.stateVariableChangeTriggers[component.componentIdx];
    if (!componentTriggers) {
      componentTriggers = core2.stateVariableChangeTriggers[component.componentIdx] = {};
    }
    componentTriggers[stateVariable] = {
      action: stateVarObj.triggerActionOnChange
    };
  }
}
async function ensureStateVariableMaterialized({
  core: core2,
  component,
  stateVariable
}) {
  const stateVarObj = component.state[stateVariable];
  if (!stateVarObj || stateVarObj.svMaterialized || stateVarObj.isArrayEntry) {
    return;
  }
  materializeNestedDefinitionFields(stateVarObj);
  applyPendingShadowModification(stateVarObj);
  const allStateVariablesAffected = [stateVariable];
  if (stateVarObj.additionalStateVariablesDefined) {
    allStateVariablesAffected.push(
      ...stateVarObj.additionalStateVariablesDefined
    );
  }
  for (const varName of allStateVariablesAffected) {
    const obj = component.state[varName];
    if (obj) {
      obj.svMaterialized = true;
    }
  }
  core2.dependencies.numMaterializedStateVariables += allStateVariablesAffected.length;
  let workspace;
  for (const varName of allStateVariablesAffected) {
    const obj = component.state[varName];
    if (!obj) {
      continue;
    }
    if (varName !== stateVariable) {
      materializeNestedDefinitionFields(obj);
      applyPendingShadowModification(obj);
    }
    if (obj.createWorkspace) {
      if (!workspace) {
        workspace = {};
      }
      obj.workspace = workspace;
    }
    await initializeStateVariable({
      core: core2,
      component,
      stateVariable: varName
    });
  }
}
function materializeNestedDefinitionFields(stateVarObj) {
  if (stateVarObj.shadowingInstructions && !Object.prototype.hasOwnProperty.call(
    stateVarObj,
    "shadowingInstructions"
  )) {
    stateVarObj.shadowingInstructions = Object.assign(
      {},
      stateVarObj.shadowingInstructions
    );
  }
  if (stateVarObj.additionalStateVariablesDefined && !Object.prototype.hasOwnProperty.call(
    stateVarObj,
    "additionalStateVariablesDefined"
  )) {
    stateVarObj.additionalStateVariablesDefined = [
      ...stateVarObj.additionalStateVariablesDefined
    ];
  }
  if (stateVarObj.stateVariablesDeterminingDependencies && !Object.prototype.hasOwnProperty.call(
    stateVarObj,
    "stateVariablesDeterminingDependencies"
  )) {
    stateVarObj.stateVariablesDeterminingDependencies = [
      ...stateVarObj.stateVariablesDeterminingDependencies
    ];
  }
}
async function initializeStateVariable({
  core: core2,
  component,
  stateVariable,
  arrayStateVariable,
  arrayEntryPrefix
}) {
  if (!component.state[stateVariable]) {
    component.state[stateVariable] = {};
  }
  let stateVarObj = component.state[stateVariable];
  stateVarObj.svComponent = component;
  stateVarObj.svVarName = stateVariable;
  stateVarObj.isResolved = false;
  stateVarObj.svMaterialized = true;
  installStaleValueGetter(stateVarObj);
  if (arrayEntryPrefix !== void 0) {
    await initializeArrayEntryStateVariable({
      stateVarObj,
      arrayStateVariable,
      arrayEntryPrefix,
      component,
      stateVariable
    });
  } else if (stateVarObj.isArray) {
    await initializeArrayStateVariable({
      core: core2,
      stateVarObj,
      component,
      stateVariable
    });
  }
  if (stateVarObj.triggerActionOnChange) {
    let componentTriggers = core2.stateVariableChangeTriggers[component.componentIdx];
    if (!componentTriggers) {
      componentTriggers = core2.stateVariableChangeTriggers[component.componentIdx] = {};
    }
    if (!componentTriggers[stateVariable]) {
      componentTriggers[stateVariable] = {
        action: stateVarObj.triggerActionOnChange
      };
    }
  }
}
async function initializeArrayEntryStateVariable({
  stateVarObj,
  arrayStateVariable,
  arrayEntryPrefix,
  component,
  stateVariable
}) {
  stateVarObj.isArrayEntry = true;
  stateVarObj.arrayStateVariable = arrayStateVariable;
  let arrayStateVarObj = component.state[arrayStateVariable];
  stateVarObj.definition = arrayStateVarObj.definition;
  stateVarObj.inverseDefinition = arrayStateVarObj.inverseDefinition;
  stateVarObj.markStale = arrayStateVarObj.markStale;
  stateVarObj.freshnessInfo = arrayStateVarObj.freshnessInfo;
  stateVarObj.getPreviousDependencyValuesForMarkStale = arrayStateVarObj.getPreviousDependencyValuesForMarkStale;
  stateVarObj.provideEssentialValuesInDefinition = arrayStateVarObj.provideEssentialValuesInDefinition;
  stateVarObj.providePreviousValuesInDefinition = arrayStateVarObj.providePreviousValuesInDefinition;
  stateVarObj.isLocation = arrayStateVarObj.isLocation;
  stateVarObj.numDimensions = arrayStateVarObj.returnEntryDimensions(arrayEntryPrefix);
  stateVarObj.entryPrefix = arrayEntryPrefix;
  stateVarObj.varEnding = stateVariable.slice(arrayEntryPrefix.length);
  if (arrayStateVarObj.createWorkspace) {
    stateVarObj.createWorkspace = true;
    stateVarObj.workspace = arrayStateVarObj.workspace;
  }
  if (arrayStateVarObj.basedOnArrayKeyStateVariables) {
    stateVarObj.basedOnArrayKeyStateVariables = true;
  }
  if (arrayStateVarObj.additionalStateVariablesDefined) {
    stateVarObj.additionalStateVariablesDefined = [];
    let entryPrefixInd = arrayStateVarObj.entryPrefixes.indexOf(arrayEntryPrefix);
    for (let varName of arrayStateVarObj.additionalStateVariablesDefined) {
      let sObj = component.state[varName];
      if (sObj.isArray) {
        let newArrayEntryPrefix = sObj.entryPrefixes[entryPrefixInd];
        let arrayEntryVarName = newArrayEntryPrefix + stateVarObj.varEnding;
        stateVarObj.additionalStateVariablesDefined.push(
          arrayEntryVarName
        );
      } else {
        stateVarObj.additionalStateVariablesDefined.push(varName);
      }
    }
  }
  if (arrayStateVarObj.shadowingInstructions) {
    stateVarObj.shadowingInstructions = {};
    stateVarObj.wrappingComponents = arrayStateVarObj.shadowingInstructions.returnWrappingComponents(
      arrayEntryPrefix
    );
    if (arrayStateVarObj.shadowingInstructions.attributesToShadow) {
      stateVarObj.shadowingInstructions.attributesToShadow = arrayStateVarObj.shadowingInstructions.attributesToShadow;
    }
    if (arrayStateVarObj.shadowingInstructions.createComponentOfType) {
      let entryPrefixInd = arrayStateVarObj.entryPrefixes.indexOf(arrayEntryPrefix);
      if (arrayStateVarObj.shadowingInstructions.createComponentOfType[entryPrefixInd]) {
        stateVarObj.shadowingInstructions.createComponentOfType = [
          arrayStateVarObj.shadowingInstructions.createComponentOfType[entryPrefixInd]
        ];
      }
    }
  }
  if (arrayStateVarObj.getEntryValues) {
    stateVarObj.getValueFromArrayValues = entryGetValueViaGetEntryValues;
  } else {
    stateVarObj.getValueFromArrayValues = entryGetValueFromArrayValues;
  }
  stateVarObj.arraySizeStateVariable = arrayStateVarObj.arraySizeStateVariable;
  stateVarObj._arrayKeys = [];
  stateVarObj._unflattenedArrayKeys = [];
  Object.defineProperty(
    stateVarObj,
    "arrayKeys",
    ENTRY_ARRAY_KEYS_DESCRIPTOR
  );
  Object.defineProperty(
    stateVarObj,
    "unflattenedArrayKeys",
    ENTRY_UNFLATTENED_ARRAY_KEYS_DESCRIPTOR
  );
  if (component.state[stateVarObj.arraySizeStateVariable].initiallyResolved) {
    let arraySize = await arrayStateVarObj.arraySize;
    let arrayKeys = arrayStateVarObj.getArrayKeysFromVarName({
      arrayEntryPrefix: stateVarObj.entryPrefix,
      varEnding: stateVarObj.varEnding,
      arraySize,
      numDimensions: arrayStateVarObj.numDimensions
    });
    stateVarObj._unflattenedArrayKeys = arrayKeys;
    stateVarObj._arrayKeys = flattenDeep(arrayKeys);
    let varNamesIncluding = arrayStateVarObj.varNamesIncludingArrayKeys;
    for (let arrayKey of stateVarObj._arrayKeys) {
      if (!varNamesIncluding[arrayKey]) {
        varNamesIncluding[arrayKey] = [];
      }
      varNamesIncluding[arrayKey].push(stateVariable);
    }
  }
  arrayStateVarObj.arrayEntryNames.push(stateVariable);
  Object.defineProperty(
    stateVarObj,
    "arraySize",
    ENTRY_ARRAY_SIZE_DESCRIPTOR
  );
  Object.defineProperty(
    stateVarObj,
    "arrayEntrySize",
    ENTRY_ARRAY_ENTRY_SIZE_DESCRIPTOR
  );
  if (arrayStateVarObj.stateVariablesDeterminingDependencies) {
    if (!stateVarObj.stateVariablesDeterminingDependencies) {
      stateVarObj.stateVariablesDeterminingDependencies = [];
    }
    for (let varName of arrayStateVarObj.stateVariablesDeterminingDependencies) {
      if (!stateVarObj.stateVariablesDeterminingDependencies.includes(
        varName
      )) {
        stateVarObj.stateVariablesDeterminingDependencies.push(varName);
      }
    }
  }
  stateVarObj.returnDependencies = entryReturnDependencies;
}
async function initializeArrayStateVariable({
  core: core2,
  stateVarObj,
  component,
  stateVariable
}) {
  stateVarObj.arrayValues = [];
  if (stateVarObj.numDimensions === void 0) {
    stateVarObj.numDimensions = 1;
  }
  let entryPrefixes = stateVarObj.entryPrefixes;
  if (!entryPrefixes) {
    entryPrefixes = stateVarObj.entryPrefixes = [stateVariable];
  }
  if (!component.arrayEntryPrefixes) {
    component.arrayEntryPrefixes = {};
  }
  for (let prefix of entryPrefixes) {
    component.arrayEntryPrefixes[prefix] = stateVariable;
  }
  if (stateVarObj.numDimensions > 1) {
    if (!stateVarObj.keyToIndex) {
      stateVarObj.keyToIndex = multiDimKeyToIndex;
    }
    stateVarObj.setArrayValue = multiDimSetArrayValue;
    stateVarObj.getArrayValue = multiDimGetArrayValue;
    if (!stateVarObj.getAllArrayKeys) {
      stateVarObj.getAllArrayKeys = multiDimGetAllArrayKeys;
    }
    if (!stateVarObj.arrayVarNameFromArrayKey) {
      stateVarObj.arrayVarNameFromArrayKey = defaultMultiDimArrayVarNameFromArrayKey;
    }
    if (!stateVarObj.arrayVarNameFromPropIndex) {
      stateVarObj.arrayVarNameFromPropIndex = returnDefaultArrayVarNameFromPropIndex(
        stateVarObj.numDimensions,
        entryPrefixes[0]
      );
    }
    stateVarObj.adjustArrayToNewArraySize = multiDimAdjustArrayToNewArraySize;
  } else {
    if (!stateVarObj.keyToIndex) {
      stateVarObj.keyToIndex = oneDimKeyToIndex;
    }
    stateVarObj.setArrayValue = oneDimSetArrayValue;
    stateVarObj.getArrayValue = oneDimGetArrayValue;
    if (!stateVarObj.getAllArrayKeys) {
      stateVarObj.getAllArrayKeys = oneDimGetAllArrayKeys;
    }
    if (!stateVarObj.arrayVarNameFromArrayKey) {
      stateVarObj.arrayVarNameFromArrayKey = defaultOneDimArrayVarNameFromArrayKey;
    }
    if (!stateVarObj.arrayVarNameFromPropIndex) {
      stateVarObj.arrayVarNameFromPropIndex = returnDefaultArrayVarNameFromPropIndex(1, entryPrefixes[0]);
    }
    stateVarObj.adjustArrayToNewArraySize = oneDimAdjustArrayToNewArraySize;
  }
  if (!stateVarObj.getArrayKeysFromVarName) {
    stateVarObj.getArrayKeysFromVarName = returnDefaultGetArrayKeysFromVarName(stateVarObj.numDimensions);
  }
  if (!stateVarObj.indexToKey) {
    stateVarObj.indexToKey = arrayIndexToKey;
  }
  if (!stateVarObj.returnEntryDimensions) {
    stateVarObj.returnEntryDimensions = defaultReturnEntryDimensions;
  }
  if (stateVarObj.shadowingInstructions) ;
  stateVarObj.usedDefaultByArrayKey = {};
  stateVarObj.arrayEntryNames = [];
  stateVarObj.varNamesIncludingArrayKeys = {};
  stateVarObj.returnDependencies = arrayReturnDependencies;
  stateVarObj.getCurrentFreshness = arrayGetCurrentFreshness;
  stateVarObj.markStale = arrayMarkStale;
  stateVarObj.freshenOnNoChanges = arrayFreshenOnNoChanges;
  stateVarObj.definition = arrayDefinition;
  stateVarObj.inverseDefinition = arrayInverseDefinition;
  await createArraySizeStateVariable({
    core: core2,
    stateVarObj,
    component,
    stateVariable
  });
  stateVarObj.arraySizeStale = true;
  stateVarObj.previousArraySize = [];
  Object.defineProperty(
    stateVarObj,
    "arraySize",
    ARRAY_ARRAY_SIZE_DESCRIPTOR
  );
  stateVarObj.recalculateArraySizeDependentQuantities = arrayRecalculateArraySizeDependentQuantities;
  if (!stateVarObj.freshnessInfo) {
    stateVarObj.freshnessInfo = { freshByKey: {} };
    if (stateVarObj.additionalStateVariablesDefined) {
      for (let vName of stateVarObj.additionalStateVariablesDefined) {
        if (!component.state[vName]) {
          component.state[vName] = {};
        }
        component.state[vName].freshnessInfo = stateVarObj.freshnessInfo;
      }
    }
  }
}
async function createArraySizeStateVariable({
  core: core2,
  stateVarObj,
  component,
  stateVariable
}) {
  let allStateVariablesAffected = [stateVariable];
  if (stateVarObj.additionalStateVariablesDefined) {
    allStateVariablesAffected.push(
      ...stateVarObj.additionalStateVariablesDefined
    );
  }
  allStateVariablesAffected.sort();
  let arraySizeStateVar = `__array_size_` + allStateVariablesAffected.join("_");
  stateVarObj.arraySizeStateVariable = arraySizeStateVar;
  if (stateVarObj.stateVariablesDeterminingDependencies) {
    stateVarObj.stateVariablesDeterminingDependencies.push(
      arraySizeStateVar
    );
  } else {
    stateVarObj.stateVariablesDeterminingDependencies = [arraySizeStateVar];
  }
  if (component.state[arraySizeStateVar]) {
    if (component.state[arraySizeStateVar].isShadow) {
      let arraySizeStateVarObj = component.state[arraySizeStateVar];
      arraySizeStateVarObj.arraySizeVarNamesAffected = allStateVariablesAffected;
      arraySizeStateVarObj.markStale = arraySizeMarkStale;
    }
    return;
  }
  component.state[arraySizeStateVar] = {
    returnDependencies: stateVarObj.returnArraySizeDependencies,
    definition: arraySizeDefinition,
    markStale: arraySizeMarkStale,
    arraySizeVarNamesAffected: allStateVariablesAffected,
    arraySizeSourceVarName: stateVariable
  };
  if (stateVarObj.stateVariablesDeterminingArraySizeDependencies) {
    component.state[arraySizeStateVar].stateVariablesDeterminingDependencies = stateVarObj.stateVariablesDeterminingArraySizeDependencies;
  }
  await initializeStateVariable({
    core: core2,
    component,
    stateVariable: arraySizeStateVar
  });
}
async function arrayEntryNamesFromPropIndex({
  core: core2,
  stateVariables,
  component,
  propIndex
}) {
  let newVarNames = [];
  for (let varName of stateVariables) {
    let stateVarObj = component.state[varName];
    if (!stateVarObj) {
      if (!core2.checkIfArrayEntry({
        stateVariable: varName,
        component
      }).isArrayEntry) {
        newVarNames.push(varName);
        continue;
      }
      await core2.createFromArrayEntry({
        stateVariable: varName,
        component
      });
      stateVarObj = component.state[varName];
    }
    let newName;
    if (stateVarObj.isArray) {
      newName = stateVarObj.arrayVarNameFromPropIndex(propIndex, varName);
    } else if (stateVarObj.isArrayEntry) {
      let arrayStateVarObj = component.state[stateVarObj.arrayStateVariable];
      newName = arrayStateVarObj.arrayVarNameFromPropIndex(
        propIndex,
        varName
      );
    } else {
      core2.addDiagnostic({
        type: "warning",
        message: `Cannot get propIndex from ${varName} of ${component.componentIdx} as it is not an array or array entry state variable`,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
      newName = varName;
    }
    if (newName) {
      newVarNames.push(newName);
    } else {
      core2.addDiagnostic({
        type: "warning",
        message: `Cannot get propIndex from ${varName} of ${component.componentIdx}`,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
      newVarNames.push(varName);
    }
  }
  return newVarNames;
}
const PRIMITIVE_TO_COMPONENT_TYPE = {
  string: "text",
  stringArray: "textList",
  numberArray: "numberList"
};
const SHARED_STATE_VARIABLE_DEFINITIONS = /* @__PURE__ */ Symbol(
  "sharedStateVariableDefinitions"
);
const classStateVariableDefinitionsCache = /* @__PURE__ */ new WeakMap();
function getClassStateVariableDefinitions(core2, componentClass) {
  let perCore = classStateVariableDefinitionsCache.get(core2);
  if (!perCore) {
    perCore = /* @__PURE__ */ new Map();
    classStateVariableDefinitionsCache.set(core2, perCore);
  }
  let entry = perCore.get(componentClass);
  if (!entry) {
    const combined = {};
    createAttributeStateVariableDefinitions({
      core: core2,
      stateVariableDefinitions: combined,
      componentClass
    });
    const normalized = componentClass.returnNormalizedStateVariableDefinitions(
      core2.numerics
    );
    Object.assign(combined, normalized);
    for (const varName in combined) {
      normalizeArrayStateVariableDefaults(combined[varName], varName);
    }
    combined[SHARED_STATE_VARIABLE_DEFINITIONS] = true;
    entry = { combined, normalized };
    perCore.set(componentClass, entry);
  }
  return entry;
}
function wrapStateVariableDefinition(def) {
  return Object.create(def);
}
async function createStateVariableDefinitions({
  core: core2,
  componentClass,
  prescribedDependencies,
  componentIdx
}) {
  let redefineDependencies;
  if (prescribedDependencies) {
    for (const idxStr in prescribedDependencies) {
      const idx = Number(idxStr);
      let depArray = prescribedDependencies[idx];
      for (let dep of depArray) {
        if (dep.dependencyType === "referenceShadow") {
          if (idx === componentIdx) {
            throw Error(
              `Circular dependency involving ${componentIdx}.`
            );
          }
          redefineDependencies = {
            linkSource: "referenceShadow",
            targetIdx: idx,
            compositeIdx: dep.compositeIdx,
            propVariable: dep.propVariable,
            fromImplicitProp: dep.fromImplicitProp,
            arrayStateVariable: dep.arrayStateVariable,
            arrayKey: dep.arrayKey,
            ignorePrimaryStateVariable: dep.ignorePrimaryStateVariable,
            substituteForPrimaryStateVariable: dep.substituteForPrimaryStateVariable,
            firstLevelReplacement: dep.firstLevelReplacement,
            additionalStateVariableShadowing: dep.additionalStateVariableShadowing
          };
        } else if (dep.dependencyType === "adapter") {
          redefineDependencies = {
            linkSource: "adapter",
            adapterTargetIdentity: dep.adapterTargetIdentity,
            adapterVariable: dep.adapterVariable,
            substituteForPrimaryStateVariable: dep.substituteForPrimaryStateVariable,
            stateVariablesToShadow: dep.stateVariablesToShadow
          };
        }
      }
    }
  }
  const classDefinitions = getClassStateVariableDefinitions(
    core2,
    componentClass
  );
  if (!redefineDependencies) {
    return classDefinitions.combined;
  }
  let stateVariableDefinitions = {};
  for (const varName in classDefinitions.normalized) {
    stateVariableDefinitions[varName] = wrapStateVariableDefinition(
      classDefinitions.normalized[varName]
    );
  }
  if (redefineDependencies.linkSource === "adapter") {
    createAdapterStateVariableDefinitions({
      core: core2,
      redefineDependencies,
      stateVariableDefinitions,
      componentClass
    });
  } else {
    await createReferenceShadowStateVariableDefinitions({
      core: core2,
      redefineDependencies,
      stateVariableDefinitions,
      componentClass
    });
  }
  return stateVariableDefinitions;
}
function createAttributeStateVariableDefinitions({
  core: core2,
  componentClass,
  stateVariableDefinitions
}) {
  let attributes = preprocessAttributesObject(
    componentClass.createAttributesObject()
  );
  for (let attrName in attributes) {
    let attributeSpecification = attributes[attrName];
    if (!attributeSpecification.createStateVariable) {
      continue;
    }
    let varName = attributeSpecification.createStateVariable;
    let stateVarDef = stateVariableDefinitions[varName] = {
      isAttribute: true,
      hasEssential: true,
      provideEssentialValuesInDefinition: true
    };
    if (attributeSpecification.public) {
      _setShadowingInstructionsFromAttribute(
        stateVarDef,
        attributeSpecification
      );
    }
    let stateVariableForAttributeValue = _resolveAttributeValueVariable(
      core2,
      attributeSpecification,
      attrName,
      componentClass
    );
    stateVarDef.returnDependencies = function() {
      return {
        ..._buildAttributeFallbackDependencies(attributeSpecification),
        ..._buildAttributeValueDependency(
          attributeSpecification,
          attrName,
          stateVariableForAttributeValue
        )
      };
    };
    const { definition, inverseDefinition } = _buildAttributeDerivedDefinitions({
      varName,
      stateVariableForAttributeValue,
      attributeSpecification,
      attrName
    });
    stateVarDef.definition = definition;
    if (!attributeSpecification.noInverse) {
      stateVarDef.inverseDefinition = inverseDefinition;
    }
    _copyPassthroughAttributes(stateVarDef, attributeSpecification, {
      includeTriggerActionOnChange: true
    });
  }
}
function createAdapterStateVariableDefinitions({
  core: core2,
  redefineDependencies,
  stateVariableDefinitions,
  componentClass
}) {
  let adapterTargetComponent = core2._components[redefineDependencies.adapterTargetIdentity.componentIdx];
  let attributes = preprocessAttributesObject(
    componentClass.createAttributesObject()
  );
  for (let attrName in attributes) {
    let attributeSpecification = attributes[attrName];
    if (!attributeSpecification.createStateVariable) {
      continue;
    }
    let varName = attributeSpecification.createStateVariable;
    let stateVarDef = stateVariableDefinitions[varName] = {
      isAttribute: true,
      hasEssential: true
    };
    if (attributeSpecification.public) {
      _setShadowingInstructionsFromAttribute(
        stateVarDef,
        attributeSpecification
      );
    }
    if (varName in adapterTargetComponent.state) {
      stateVarDef.returnDependencies = () => ({
        adapterTargetVariable: {
          dependencyType: "stateVariable",
          componentIdx: redefineDependencies.adapterTargetIdentity.componentIdx,
          variableName: varName
        }
      });
    } else {
      stateVarDef.returnDependencies = () => ({});
    }
    stateVarDef.definition = function({ dependencyValues, usedDefault }) {
      if (dependencyValues.adapterTargetVariable === void 0 || usedDefault.adapterTargetVariable) {
        return {
          useEssentialOrDefaultValue: {
            [varName]: true
          },
          checkForActualChange: { [varName]: true }
        };
      } else {
        return {
          setValue: {
            [varName]: dependencyValues.adapterTargetVariable
          },
          checkForActualChange: { [varName]: true }
        };
      }
    };
    if (!attributeSpecification.noInverse) {
      stateVarDef.inverseDefinition = async function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.adapterTargetVariable === void 0) {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: varName,
                value: desiredStateVariableValues[varName]
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setDependency: "adapterTargetVariable",
                desiredValue: desiredStateVariableValues[varName]
              }
            ]
          };
        }
      };
    }
    _copyPassthroughAttributes(stateVarDef, attributeSpecification);
  }
  const { name: primaryStateVariableForDefinition, stateDef } = _resolvePrimaryStateVariableForDefinition({
    redefineDependencies,
    componentClass,
    stateVariableDefinitions,
    throwIfMissing: false
  });
  stateDef.isShadow = true;
  stateDef.returnDependencies = () => ({
    adapterTargetVariable: {
      dependencyType: "stateVariable",
      componentIdx: redefineDependencies.adapterTargetIdentity.componentIdx,
      variableName: redefineDependencies.adapterVariable
    }
  });
  if (stateDef.set) {
    stateDef.definition = function({ dependencyValues }) {
      return {
        setValue: {
          [primaryStateVariableForDefinition]: stateDef.set(
            dependencyValues.adapterTargetVariable
          )
        }
      };
    };
  } else {
    stateDef.definition = function({ dependencyValues }) {
      return {
        setValue: {
          [primaryStateVariableForDefinition]: dependencyValues.adapterTargetVariable
        }
      };
    };
  }
  stateDef.inverseDefinition = function({ desiredStateVariableValues }) {
    return {
      success: true,
      instructions: [
        {
          setDependency: "adapterTargetVariable",
          desiredValue: desiredStateVariableValues[primaryStateVariableForDefinition]
        }
      ]
    };
  };
  if (redefineDependencies.stateVariablesToShadow) {
    modifyStateDefsToBeShadows({
      stateVariablesToShadow: redefineDependencies.stateVariablesToShadow,
      stateVariableDefinitions,
      targetComponent: adapterTargetComponent
    });
  }
}
async function createReferenceShadowStateVariableDefinitions({
  core: core2,
  redefineDependencies,
  stateVariableDefinitions,
  componentClass
}) {
  let targetComponent = core2._components[redefineDependencies.targetIdx];
  if (redefineDependencies.propVariable) {
    if (!targetComponent.state[redefineDependencies.propVariable] && core2.checkIfArrayEntry({
      stateVariable: redefineDependencies.propVariable,
      component: targetComponent
    }).isArrayEntry) {
      await core2.createFromArrayEntry({
        stateVariable: redefineDependencies.propVariable,
        component: targetComponent
      });
    }
  }
  let attributes = preprocessAttributesObject(
    componentClass.createAttributesObject()
  );
  for (let attrName in attributes) {
    let attributeSpecification = attributes[attrName];
    let varName = attributeSpecification.createStateVariable;
    if (!varName) {
      continue;
    }
    let stateVarDef = stateVariableDefinitions[varName] = {
      isAttribute: true,
      hasEssential: true,
      provideEssentialValuesInDefinition: true
    };
    !attributeSpecification.createComponentOfType;
    if (attributeSpecification.public) {
      _setShadowingInstructionsFromAttribute(
        stateVarDef,
        attributeSpecification
      );
    }
    let stateVariableForAttributeValue = _resolveAttributeValueVariable(
      core2,
      attributeSpecification,
      attrName,
      componentClass
    );
    const thisDependencies = {
      ..._buildAttributeValueDependency(
        attributeSpecification,
        attrName,
        stateVariableForAttributeValue
      ),
      ..._buildAttributeFallbackDependencies(attributeSpecification)
    };
    stateVarDef.returnDependencies = () => thisDependencies;
    const { definition, inverseDefinition } = _buildAttributeDerivedDefinitions({
      varName,
      stateVariableForAttributeValue,
      attributeSpecification,
      attrName
    });
    stateVarDef.definition = definition;
    if (!attributeSpecification.noInverse) {
      stateVarDef.inverseDefinition = inverseDefinition;
    }
    _copyPassthroughAttributes(stateVarDef, attributeSpecification);
  }
  if (redefineDependencies.propVariable) {
    if (!redefineDependencies.ignorePrimaryStateVariable) {
      const { name: primaryStateVariableForDefinition, stateDef } = _resolvePrimaryStateVariableForDefinition({
        redefineDependencies,
        componentClass,
        stateVariableDefinitions,
        throwIfMissing: true
      });
      stateDef.isShadow = true;
      stateDef.returnDependencies = () => ({
        targetVariable: {
          dependencyType: "stateVariable",
          componentIdx: targetComponent.componentIdx,
          variableName: redefineDependencies.propVariable
        }
      });
      let setDefault = false;
      if (targetComponent.state[redefineDependencies.propVariable].defaultValue !== void 0) {
        stateDef.defaultValue = targetComponent.state[redefineDependencies.propVariable].defaultValue;
        if (stateDef.set) {
          stateDef.defaultValue = stateDef.set(stateDef.defaultValue);
        }
        stateDef.hasEssential = true;
        setDefault = true;
      }
      let targetVariableIsArray = targetComponent.state[redefineDependencies.propVariable].isArray;
      if (stateDef.set) {
        stateDef.definition = function({
          dependencyValues,
          usedDefault
        }) {
          let targetVariable = dependencyValues.targetVariable;
          if (targetVariable === void 0 || targetVariableIsArray && targetVariable.length === 0) {
            return {
              useEssentialOrDefaultValue: {
                [primaryStateVariableForDefinition]: true
              }
            };
          }
          let valueFromTarget = stateDef.set(targetVariable);
          if (setDefault && usedDefault.targetVariable) {
            return {
              useEssentialOrDefaultValue: {
                [primaryStateVariableForDefinition]: {
                  defaultValue: valueFromTarget
                }
              }
            };
          }
          return {
            setValue: {
              [primaryStateVariableForDefinition]: valueFromTarget
            }
          };
        };
      } else {
        stateDef.definition = function({
          dependencyValues,
          usedDefault
        }) {
          let targetVariable = dependencyValues.targetVariable;
          if (targetVariable === void 0 || targetVariableIsArray && targetVariable.length === 0) {
            return {
              useEssentialOrDefaultValue: {
                [primaryStateVariableForDefinition]: true
              }
            };
          }
          if (setDefault && usedDefault.targetVariable) {
            return {
              useEssentialOrDefaultValue: {
                [primaryStateVariableForDefinition]: {
                  defaultValue: targetVariable
                }
              }
            };
          }
          return {
            setValue: {
              [primaryStateVariableForDefinition]: targetVariable
            }
          };
        };
      }
      stateDef.inverseDefinition = function({
        desiredStateVariableValues
      }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "targetVariable",
              desiredValue: desiredStateVariableValues[primaryStateVariableForDefinition]
            }
          ]
        };
      };
    }
    let shadowStandardVariables = false;
    let stateVariablesToShadow2 = [];
    if (targetComponent.constructor.implicitPropReturnsSameType) {
      if (redefineDependencies.fromImplicitProp) {
        shadowStandardVariables = true;
      }
      for (let varName in targetComponent.state) {
        let stateObj = targetComponent.state[varName];
        if (stateObj.shadowVariable || stateObj.isShadow) {
          stateVariablesToShadow2.push(varName);
        }
      }
    }
    if (redefineDependencies.additionalStateVariableShadowing) {
      let differentStateVariablesInTarget = Array(
        stateVariablesToShadow2.length
      );
      for (let varName in redefineDependencies.additionalStateVariableShadowing) {
        if (!stateVariablesToShadow2.includes(varName)) {
          stateVariablesToShadow2.push(varName);
          differentStateVariablesInTarget.push(
            redefineDependencies.additionalStateVariableShadowing[varName].stateVariableToShadow
          );
        }
      }
      modifyStateDefsToBeShadows({
        stateVariablesToShadow: stateVariablesToShadow2,
        stateVariableDefinitions,
        targetComponent,
        differentStateVariablesInTarget
      });
    } else if (shadowStandardVariables) {
      modifyStateDefsToBeShadows({
        stateVariablesToShadow: stateVariablesToShadow2,
        stateVariableDefinitions,
        targetComponent
      });
    }
    return;
  }
  let foundReadyToExpandWhenResolved = false;
  if ("readyToExpandWhenResolved" in stateVariableDefinitions) {
    foundReadyToExpandWhenResolved = true;
    let stateDef = stateVariableDefinitions.readyToExpandWhenResolved;
    let originalReturnDependencies = stateDef.returnDependencies.bind(stateDef);
    let originalDefinition = stateDef.definition;
    stateDef.returnDependencies = function(args) {
      let dependencies = originalReturnDependencies(args);
      dependencies.targetReadyToExpandWhenResolved = {
        dependencyType: "stateVariable",
        componentIdx: targetComponent.componentIdx,
        variableName: "readyToExpandWhenResolved"
      };
      return dependencies;
    };
    stateDef.definition = function(args) {
      let result = originalDefinition(args);
      if (result.setValue && result.setValue.readyToExpandWhenResolved) {
        if (!args.dependencyValues.targetReadyToExpandWhenResolved) {
          result.setValue.readyToExpandWhenResolved = false;
        }
      }
      return result;
    };
  }
  let stateVariablesToShadow = [];
  for (let varName in targetComponent.state) {
    let stateObj = targetComponent.state[varName];
    if (stateObj.shadowVariable || stateObj.isShadow) {
      stateVariablesToShadow.push(varName);
    }
  }
  modifyStateDefsToBeShadows({
    stateVariablesToShadow,
    stateVariableDefinitions,
    foundReadyToExpandWhenResolved,
    targetComponent
  });
}
function shadowReturnDependencies(args) {
  let dependencies = Object.assign(
    {},
    this.shadowOriginalReturnDependencies?.(args)
  );
  dependencies.targetVariable = {
    dependencyType: "stateVariable",
    componentIdx: this.shadowOfComponentIdx,
    variableName: this.shadowVarNameInTarget
  };
  if (this.shadowCopyComponentType) {
    dependencies.targetVariableComponentType = {
      dependencyType: "stateVariableComponentType",
      componentIdx: this.shadowOfComponentIdx,
      variableName: this.shadowVarNameInTarget
    };
  }
  return dependencies;
}
function shadowDefinition({ dependencyValues, usedDefault }) {
  const varName = this.shadowVarName;
  let result = {};
  if ("targetVariableComponentType" in dependencyValues) {
    result.setCreateComponentOfType = {
      [varName]: dependencyValues.targetVariableComponentType
    };
  }
  if (usedDefault.targetVariable && "defaultValue" in this && this.hasEssential) {
    result.useEssentialOrDefaultValue = {
      [varName]: {
        defaultValue: dependencyValues.targetVariable
      }
    };
  } else {
    result.setValue = {
      [varName]: dependencyValues.targetVariable
    };
  }
  return result;
}
function shadowInverseDefinition({ desiredStateVariableValues }) {
  return {
    success: true,
    instructions: [
      {
        setDependency: "targetVariable",
        desiredValue: desiredStateVariableValues[this.shadowVarName],
        shadowedVariable: true
      }
    ]
  };
}
function shadowReturnArrayDependenciesByKey({ arrayKeys }) {
  let dependenciesByKey = {};
  for (let key of arrayKeys) {
    dependenciesByKey[key] = {
      targetVariable: {
        dependencyType: "stateVariable",
        componentIdx: this.shadowOfComponentIdx,
        variableName: this.shadowOverrideVarName || this.arrayVarNameFromArrayKey(key)
      }
    };
  }
  let globalDependencies = {};
  if (this.shadowCopyComponentType) {
    globalDependencies.targetVariableComponentType = {
      dependencyType: "stateVariableComponentType",
      componentIdx: this.shadowOfComponentIdx,
      variableName: this.shadowVarName
    };
  }
  if (this.inverseShadowToSetEntireArray) {
    globalDependencies.targetArray = {
      dependencyType: "stateVariable",
      componentIdx: this.shadowOfComponentIdx,
      variableName: this.shadowVarName
    };
  }
  return { globalDependencies, dependenciesByKey };
}
function shadowArrayDefinitionByKey({ globalDependencyValues, dependencyValuesByKey, arrayKeys }) {
  const varName = this.shadowVarName;
  let newEntries = {};
  for (let arrayKey of arrayKeys) {
    if ("targetVariable" in dependencyValuesByKey[arrayKey]) {
      newEntries[arrayKey] = dependencyValuesByKey[arrayKey].targetVariable;
    } else {
      newEntries[arrayKey] = this.defaultValueByArrayKey?.(arrayKey);
    }
  }
  let result = {
    setValue: { [varName]: newEntries }
  };
  if ("targetVariableComponentType" in globalDependencyValues) {
    result.setCreateComponentOfType = {
      [varName]: globalDependencyValues.targetVariableComponentType
    };
  }
  return result;
}
function shadowInverseArrayDefinitionByKey({
  desiredStateVariableValues,
  dependencyValuesByKey,
  dependencyNamesByKey,
  arraySize,
  initialChange
}) {
  const varName = this.shadowVarName;
  if (this.inverseShadowToSetEntireArray) {
    return {
      success: true,
      instructions: [
        {
          setDependency: "targetArray",
          desiredValue: desiredStateVariableValues[varName],
          treatAsInitialChange: initialChange
        }
      ]
    };
  }
  let instructions = [];
  for (let key in desiredStateVariableValues[varName]) {
    if (!dependencyValuesByKey[key]) {
      continue;
    }
    instructions.push({
      setDependency: dependencyNamesByKey[key].targetVariable,
      desiredValue: desiredStateVariableValues[varName][key],
      shadowedVariable: true
    });
  }
  return {
    success: true,
    instructions
  };
}
function modifyStateDefsToBeShadows({
  stateVariablesToShadow,
  stateVariableDefinitions,
  foundReadyToExpandWhenResolved,
  targetComponent,
  differentStateVariablesInTarget = []
}) {
  let deleteStateVariablesFromDefinition = {};
  for (let [varInd, varName] of stateVariablesToShadow.entries()) {
    let stateDef = stateVariableDefinitions[varName];
    if (stateDef === void 0) {
      if (varName.slice(0, 8) === "__array_") {
        stateDef = stateVariableDefinitions[varName] = {};
      } else {
        continue;
      }
    }
    stateDef.isShadow = true;
    if (stateDef.additionalStateVariablesDefined) {
      for (let varName2 of stateDef.additionalStateVariablesDefined) {
        if (!stateVariablesToShadow.includes(varName2)) {
          if (!deleteStateVariablesFromDefinition[varName2]) {
            deleteStateVariablesFromDefinition[varName2] = [];
          }
          deleteStateVariablesFromDefinition[varName2].push(varName);
        }
      }
    }
    stateDef.svShadowParams = {
      targetComponentIdx: targetComponent.componentIdx,
      overrideVarName: differentStateVariablesInTarget[varInd],
      keepOriginalDependencies: Boolean(foundReadyToExpandWhenResolved)
    };
  }
  for (let varName in deleteStateVariablesFromDefinition) {
    stateVariableDefinitions[varName].svShadowDeleteVars = deleteStateVariablesFromDefinition[varName];
  }
}
function applyPendingShadowModification(stateVarObj) {
  const params = stateVarObj.svShadowParams;
  if (params) {
    delete stateVarObj.svShadowParams;
    const varName = stateVarObj.svVarName;
    const originalReturnDependencies = stateVarObj.returnDependencies;
    stateVarObj.additionalStateVariablesDefined = void 0;
    if (!params.keepOriginalDependencies) {
      stateVarObj.stateVariablesDeterminingDependencies = void 0;
    }
    let copyComponentType = stateVarObj.public && stateVarObj.shadowingInstructions.hasVariableComponentType;
    stateVarObj.shadowOfComponentIdx = params.targetComponentIdx;
    stateVarObj.shadowVarName = varName;
    stateVarObj.shadowCopyComponentType = copyComponentType;
    if (stateVarObj.isArray) {
      stateVarObj.shadowOverrideVarName = params.overrideVarName;
      stateVarObj.returnArrayDependenciesByKey = shadowReturnArrayDependenciesByKey;
      stateVarObj.arrayDefinitionByKey = shadowArrayDefinitionByKey;
      stateVarObj.inverseArrayDefinitionByKey = shadowInverseArrayDefinitionByKey;
    } else {
      if (params.keepOriginalDependencies) {
        stateVarObj.shadowOriginalReturnDependencies = originalReturnDependencies.bind(stateVarObj);
      }
      stateVarObj.shadowVarNameInTarget = params.overrideVarName || varName;
      stateVarObj.returnDependencies = shadowReturnDependencies;
      stateVarObj.definition = shadowDefinition;
      stateVarObj.excludeDependencyValuesInInverseDefinition = true;
      stateVarObj.inverseDefinition = shadowInverseDefinition;
    }
  }
  const deleteVars = stateVarObj.svShadowDeleteVars;
  if (deleteVars) {
    delete stateVarObj.svShadowDeleteVars;
    modifyStateDefToDeleteVariableReferences({
      varNamesToDelete: deleteVars,
      stateDef: stateVarObj
    });
  }
}
function modifyStateDefToDeleteVariableReferences({
  varNamesToDelete,
  stateDef
}) {
  for (let varName2 of varNamesToDelete) {
    let ind = stateDef.additionalStateVariablesDefined.indexOf(varName2);
    stateDef.additionalStateVariablesDefined.splice(ind, 1);
  }
  let originalDefinition = stateDef.definition;
  stateDef.definition = function(args) {
    let results = originalDefinition(args);
    for (let key in results) {
      if (Array.isArray(results[key])) {
        for (let varName2 of varNamesToDelete) {
          let ind = results[key].indexOf(varName2);
          if (ind !== -1) {
            results[key].splice(ind, 1);
          }
        }
      } else {
        for (let varName2 of varNamesToDelete) {
          delete results[key][varName2];
        }
      }
    }
    return results;
  };
}
function _buildAttributeValueDependency(attributeSpecification, attrName, stateVariableForAttributeValue) {
  if (attributeSpecification.createPrimitiveOfType) {
    return {
      attributePrimitive: {
        dependencyType: "attributePrimitive",
        attributeName: attrName
      }
    };
  } else if (attributeSpecification.createReferences) {
    return {
      attributeRefResolutions: {
        dependencyType: "attributeRefResolutions",
        attributeName: attrName
      }
    };
  } else {
    return {
      attributeComponent: {
        dependencyType: "attributeComponent",
        attributeName: attrName,
        variableNames: [stateVariableForAttributeValue]
      }
    };
  }
}
function _buildAttributeFallbackDependencies(attributeSpecification) {
  const deps = {};
  if (attributeSpecification.fallBackToParentStateVariable) {
    deps.parentValue = {
      dependencyType: "parentStateVariable",
      variableName: attributeSpecification.fallBackToParentStateVariable
    };
  }
  if (attributeSpecification.fallBackToSourceCompositeStateVariable) {
    deps.sourceCompositeValue = {
      dependencyType: "sourceCompositeStateVariable",
      variableName: attributeSpecification.fallBackToSourceCompositeStateVariable
    };
  }
  return deps;
}
function _resolvePrimaryStateVariableForDefinition({
  redefineDependencies,
  componentClass,
  stateVariableDefinitions,
  throwIfMissing
}) {
  let name = "value";
  if (redefineDependencies.substituteForPrimaryStateVariable) {
    name = redefineDependencies.substituteForPrimaryStateVariable;
  } else if (componentClass.primaryStateVariableForDefinition) {
    name = componentClass.primaryStateVariableForDefinition;
  }
  const stateDef = stateVariableDefinitions[name];
  if (throwIfMissing && !stateDef) {
    if (redefineDependencies.substituteForPrimaryStateVariable) {
      throw Error(
        `Invalid public state variable of componentType ${componentClass.componentType}: substituteForPrimaryStateVariable ${redefineDependencies.substituteForPrimaryStateVariable} does not exist`
      );
    } else {
      throw Error(
        `Cannot have a public state variable with componentType ${componentClass.componentType} as the class doesn't have a primary state variable for definition`
      );
    }
  }
  return { name, stateDef };
}
function _setShadowingInstructionsFromAttribute(stateVarDef, attributeSpecification) {
  stateVarDef.public = true;
  stateVarDef.shadowingInstructions = {};
  if (attributeSpecification.createPrimitiveOfType) {
    const primitive = attributeSpecification.createPrimitiveOfType;
    stateVarDef.shadowingInstructions.createComponentOfType = PRIMITIVE_TO_COMPONENT_TYPE[primitive] ?? primitive;
  } else if (attributeSpecification.createReferences) {
    throw Error(
      "Cannot make a public state variable from an attribute with createReferences"
    );
  } else {
    stateVarDef.shadowingInstructions.createComponentOfType = attributeSpecification.createComponentOfType;
  }
}
function _resolveAttributeValueVariable(core2, attributeSpecification, attrName, componentClass) {
  if (!attributeSpecification.createComponentOfType) {
    return void 0;
  }
  const attributeClass = core2.componentInfoObjects.allComponentClasses[attributeSpecification.createComponentOfType];
  if (!attributeClass) {
    throw Error(
      `Component type ${attributeSpecification.createComponentOfType} does not exist so cannot create state variable for attribute ${attrName} of componentType ${componentClass.componentType}.`
    );
  }
  let stateVariableForAttributeValue = attributeSpecification.componentStateVariableForAttributeValue;
  if (stateVariableForAttributeValue === void 0) {
    stateVariableForAttributeValue = attributeClass.stateVariableToBeShadowed;
    if (stateVariableForAttributeValue === void 0) {
      stateVariableForAttributeValue = "value";
    }
  }
  return stateVariableForAttributeValue;
}
function _copyPassthroughAttributes(stateVarDef, attributeSpecification, { includeTriggerActionOnChange = false } = {}) {
  const attributesToCopy = [
    "forRenderer",
    "defaultValue",
    "propagateToProps",
    ...includeTriggerActionOnChange ? ["triggerActionOnChange"] : [],
    "ignoreFixed",
    "isLocation",
    "essentialVarName"
  ];
  for (const attrName2 of attributesToCopy) {
    if (attrName2 in attributeSpecification) {
      stateVarDef[attrName2] = attributeSpecification[attrName2];
    }
  }
}
function _buildAttributeDerivedDefinitions({
  varName,
  stateVariableForAttributeValue,
  attributeSpecification,
  attrName
}) {
  const definition = function({
    dependencyValues,
    usedDefault,
    essentialValues
  }) {
    let attributeValue;
    if (dependencyValues.attributeComponent) {
      attributeValue = dependencyValues.attributeComponent.stateValues[stateVariableForAttributeValue];
    } else if (dependencyValues.attributePrimitive != null) {
      attributeValue = dependencyValues.attributePrimitive;
    } else if (dependencyValues.attributeRefResolutions != null && !usedDefault.attributeRefResolutions) {
      attributeValue = dependencyValues.attributeRefResolutions;
    } else {
      let haveSourceCompositeValue = dependencyValues.sourceCompositeValue != null;
      if (haveSourceCompositeValue && !usedDefault.sourceCompositeValue && essentialValues[varName] === void 0) {
        return {
          setValue: {
            [varName]: dependencyValues.sourceCompositeValue
          },
          checkForActualChange: { [varName]: true }
        };
      } else {
        let haveParentValue = dependencyValues.parentValue != null;
        if (haveParentValue && !usedDefault.parentValue && essentialValues[varName] === void 0) {
          return {
            setValue: {
              [varName]: dependencyValues.parentValue
            },
            checkForActualChange: { [varName]: true }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              [varName]: true
            },
            checkForActualChange: { [varName]: true }
          };
        }
      }
    }
    const res = validateAttributeValue({
      value: attributeValue,
      attributeSpecification,
      attribute: attrName
    });
    return {
      setValue: { [varName]: res.value },
      checkForActualChange: { [varName]: true },
      sendDiagnostics: res.diagnostics
    };
  };
  const inverseDefinition = async function({
    desiredStateVariableValues,
    dependencyValues,
    usedDefault,
    essentialValues
  }) {
    if (!dependencyValues.attributeComponent) {
      if (dependencyValues.attributePrimitive != null) {
        return { success: false };
      }
      if (dependencyValues.attributeRefResolutions != null) {
        return { success: false };
      }
      let haveSourceCompositeValue = dependencyValues.sourceCompositeValue != null;
      if (haveSourceCompositeValue && !usedDefault.sourceCompositeValue && essentialValues[varName] === void 0) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "sourceCompositeValue",
              desiredValue: desiredStateVariableValues[varName]
            }
          ]
        };
      } else {
        let haveParentValue = dependencyValues.parentValue != null;
        if (haveParentValue && !usedDefault.parentValue && essentialValues[varName] === void 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "parentValue",
                desiredValue: desiredStateVariableValues[varName]
              }
            ]
          };
        } else {
          const res = validateAttributeValue({
            value: desiredStateVariableValues[varName],
            attributeSpecification,
            attribute: attrName
          });
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: varName,
                value: res.value
              }
            ],
            sendDiagnostics: res.diagnostics
          };
        }
      }
    }
    return {
      success: true,
      instructions: [
        {
          setDependency: "attributeComponent",
          desiredValue: desiredStateVariableValues[varName],
          variableIndex: 0
        }
      ]
    };
  };
  return { definition, inverseDefinition };
}
function validateAttributeValue({
  value,
  attributeSpecification,
  attribute
}) {
  let diagnostics = [];
  const valueOrig = value;
  if (attributeSpecification.transformNonFiniteTo !== void 0 && !Number.isFinite(value)) {
    value = attributeSpecification.transformNonFiniteTo;
  }
  if (attributeSpecification.validValues && Array.isArray(value)) {
    const res = validateListItemsAgainstValidValues({
      items: value,
      validValues: attributeSpecification.validValues,
      toLowerCase: attributeSpecification.toLowerCase,
      attribute
    });
    diagnostics.push(...res.diagnostics);
    return { value: res.value, diagnostics };
  }
  if (attributeSpecification.toLowerCase) {
    value = value.toLowerCase();
  }
  if (attributeSpecification.trim || attributeSpecification.validValues) {
    value = value.trim();
  }
  if (attributeSpecification.validValues) {
    const allowed = attributeSpecification.validValues.map((v2) => v2.value);
    if (!allowed.includes(value)) {
      let defaultValue = attributeSpecification.defaultValue;
      if (defaultValue === void 0) {
        if (attributeSpecification.createPrimitiveOfType) {
          defaultValue = attributeSpecification.defaultPrimitiveValue;
        }
        if (defaultValue === void 0) {
          throw Error(
            "Invalid attribute specification: no default value specified"
          );
        }
      }
      diagnostics.push({
        message: `Invalid value \`${valueOrig}\` for attribute \`${attribute}\`, using value \`${defaultValue}\``,
        type: "info"
      });
      value = defaultValue;
    }
  } else if (attributeSpecification.clamp) {
    if (value < attributeSpecification.clamp[0]) {
      value = attributeSpecification.clamp[0];
    } else if (value > attributeSpecification.clamp[1]) {
      value = attributeSpecification.clamp[1];
    } else if (!Number.isFinite(value)) {
      value = attributeSpecification.defaultValue;
    }
  }
  return { value, diagnostics };
}
const EMPTY_SERIALIZED_CHILDREN = Object.freeze([]);
function ancestorsForChild(parent) {
  return [
    {
      componentIdx: parent.componentIdx,
      componentClass: parent.constructor
    },
    ...parent.ancestors
  ];
}
async function addComponents({
  core: core2,
  serializedComponents,
  parentIdx,
  indexOfDefiningChildren,
  initialAdd = false
}) {
  core2.initialAddPhase = initialAdd;
  if (!Array.isArray(serializedComponents)) {
    serializedComponents = [serializedComponents];
  }
  let parent;
  let ancestors = [];
  if (!initialAdd) {
    parent = core2._components[parentIdx];
    if (!parent) {
      core2.addDiagnostic({
        type: "warning",
        message: `Cannot add children to parent ${parentIdx} as ${parentIdx} does not exist`
      });
      return [];
    }
    ancestors = ancestorsForChild(parent);
    core2.parameterStack.push(parent.sharedParameters, false);
    core2.nTimesAddedComponents = (core2.nTimesAddedComponents ?? 0) + 1;
    addComponentsToResolver({
      core: core2,
      components: serializedComponents,
      parentIdx
    });
  }
  let createResult = await createIsolatedComponents({
    core: core2,
    serializedComponents,
    ancestors
  });
  if (!initialAdd) {
    core2.parameterStack.pop();
  }
  const newComponents = createResult.components;
  let deletedComponents = {};
  let addedComponents = {};
  newComponents.forEach((x2) => addedComponents[x2.componentIdx] = x2);
  if (initialAdd) {
    if (newComponents.length !== 1) {
      throw Error(
        "Initial components need to be an array of just one component."
      );
    }
    core2.document = newComponents[0];
    await _expandAllCompositesBothPasses(core2);
    await _drainStateVariablesToEvaluate(core2);
    await addQueuedErrorComponentsFromStateVariables({ core: core2 });
    await core2.replacementChangesFromCompositesToUpdate();
    let results = await core2.initializeRenderedComponentInstruction(
      core2.document
    );
    if (core2.errorComponentsToAdd.length > 0) {
      await addQueuedErrorComponentsFromStateVariables({ core: core2 });
      await core2.replacementChangesFromCompositesToUpdate();
      results = await core2.initializeRenderedComponentInstruction(
        core2.document
      );
    }
    core2.documentRendererInstructions = results.componentToRender;
    let updateInstructions = [
      {
        instructionType: "updateRendererStates",
        rendererStatesToUpdate: results.rendererStatesToUpdate
      }
    ];
    core2.callUpdateRenderers({ updateInstructions }, true);
    if (results.rendererStatesToForceUpdate.length > 0) {
      let updateInstructions2 = [
        {
          instructionType: "updateRendererStates",
          rendererStatesToUpdate: results.rendererStatesToForceUpdate
        }
      ];
      core2.callUpdateRenderers({ updateInstructions: updateInstructions2 });
    }
    await _drainCompositesToUpdateReplacements(core2);
    await core2.processStateVariableTriggers(true);
  } else {
    if (parent === void 0) {
      throw Error("Must specify parent when adding components.");
    }
    if (indexOfDefiningChildren === void 0) {
      indexOfDefiningChildren = parent.definingChildren.length;
    }
    let addResults = await addChildrenAndRecurseToShadows({
      core: core2,
      parent,
      indexOfDefiningChildren,
      newChildren: newComponents
    });
    if (!addResults.success) {
      throw Error(
        "Couldn't satisfy child logic result.  Need informative error message"
      );
    }
    Object.assign(addedComponents, addResults.addedComponents);
    Object.assign(deletedComponents, addResults.deletedComponents);
    await _expandAllCompositesBothPasses(core2);
    await _drainStateVariablesToEvaluate(core2);
    await core2.replacementChangesFromCompositesToUpdate();
    await core2.updateRendererInstructions({
      componentNamesToUpdate: await componentAndRenderedDescendants({
        core: core2,
        component: parent
      })
    });
    await _drainCompositesToUpdateReplacements(core2);
    await core2.processStateVariableTriggers(true);
  }
  core2.initialAddPhase = false;
  return newComponents;
}
async function createIsolatedComponents({
  core: core2,
  serializedComponents,
  ancestors,
  shadow = false,
  componentsReplacementOf
}) {
  const newComponents = [];
  let lastErrorMessage = "";
  const res = extractCreateComponentIdxMapping(serializedComponents);
  Object.assign(
    core2.createComponentIdxMapping,
    res.createComponentIdxMapping
  );
  for (let serializedComponent of serializedComponents) {
    if (typeof serializedComponent !== "object") {
      newComponents.push(serializedComponent);
      continue;
    }
    let componentClass = core2.componentInfoObjects.allComponentClasses[serializedComponent.componentType];
    if (componentClass === void 0) {
      const message = `Invalid component type: \`<${serializedComponent.componentType}>\`.`;
      core2.diagnosticsManager.markPending();
      const convertResult = convertToErrorComponent(
        serializedComponent,
        message
      );
      serializedComponent = convertResult.component;
      lastErrorMessage = message;
      componentClass = core2.componentInfoObjects.allComponentClasses[serializedComponent.componentType];
    }
    if (!serializedComponent.doenetAttributes) {
      serializedComponent.doenetAttributes = {};
    }
    let componentIdx = serializedComponent.componentIdx;
    if (componentIdx === void 0) {
      console.log(serializedComponent);
      throw Error("Found a serialized component without a componentIdx");
    }
    const createResult = await createChildrenThenComponent({
      core: core2,
      serializedComponent,
      componentIdx,
      ancestors,
      componentClass,
      shadow,
      componentsReplacementOf
    });
    const newComponent = createResult.newComponent;
    newComponents.push(newComponent);
    if (createResult.lastErrorMessage) {
      lastErrorMessage = createResult.lastErrorMessage;
    }
  }
  let results = {
    components: newComponents,
    lastErrorMessage
  };
  return results;
}
async function createChildrenThenComponent({
  core: core2,
  serializedComponent,
  componentIdx,
  ancestors,
  componentClass,
  shadow = false,
  componentsReplacementOf
}) {
  let lastErrorMessage = "";
  let lastErrorMessageFromAttribute = "";
  if (!(Number.isInteger(componentIdx) && componentIdx >= 0)) {
    throw Error(`Found an invalid componentIdx: ${componentIdx}`);
  }
  if (core2._components[componentIdx] !== void 0) {
    console.log(core2._components[componentIdx], serializedComponent);
    throw Error(`Found a duplicate componentIdx: ${componentIdx}`);
  }
  const serializedChildren = serializedComponent.children;
  let definingChildren = [];
  const childrenToRemainSerialized = [];
  const ancestorsForChildren = [
    { componentIdx, componentClass },
    ...ancestors
  ];
  const parentSharedParameters = core2.parameterStack.parameters;
  core2.parameterStack.push();
  const sharedParameters = core2.parameterStack.parameters;
  if (componentClass.descendantCompositesMustHaveAReplacement && !shadow) {
    sharedParameters.compositesMustHaveAReplacement = true;
    sharedParameters.compositesDefaultReplacementType = componentClass.descendantCompositesDefaultReplacementType;
  } else if (componentClass.descendantCompositesMustHaveAReplacement === false || shadow) {
    sharedParameters.compositesMustHaveAReplacement = false;
  }
  if (serializedChildren !== void 0) {
    if (componentClass.preprocessSerializedChildren) {
      componentClass.preprocessSerializedChildren({
        serializedChildren,
        attributes: serializedComponent.attributes,
        componentIdx
      });
    }
    if (componentClass.setUpVariant) {
      const descendantVariantComponents = gatherVariantComponents({
        serializedComponents: serializedChildren,
        componentInfoObjects: core2.componentInfoObjects
      });
      componentClass.setUpVariant({
        serializedComponent,
        sharedParameters,
        descendantVariantComponents,
        core: core2
      });
    }
    if (componentClass.keepChildrenSerialized) {
      const childrenAddressed = /* @__PURE__ */ new Set([]);
      const keepSerializedInds = componentClass.keepChildrenSerialized({
        serializedComponent,
        componentInfoObjects: core2.componentInfoObjects
      });
      for (const ind of keepSerializedInds) {
        if (childrenAddressed.has(Number(ind))) {
          throw Error(
            "Invalid instructions to keep children serialized from " + componentClass.componentType + ": child repeated"
          );
        }
        childrenAddressed.add(Number(ind));
        childrenToRemainSerialized.push(serializedChildren[ind]);
      }
      const childrenToCreate = [];
      for (const [ind, child] of serializedChildren.entries()) {
        if (!childrenAddressed.has(ind)) {
          childrenToCreate.push(child);
        }
      }
      if (childrenToCreate.length > 0) {
        const childrenResult = await createIsolatedComponents({
          core: core2,
          serializedComponents: childrenToCreate,
          ancestors: ancestorsForChildren,
          shadow,
          componentsReplacementOf
        });
        definingChildren = childrenResult.components;
        if (childrenResult.lastErrorMessage) {
          lastErrorMessage = childrenResult.lastErrorMessage;
        }
      }
    } else {
      const childrenResult = await createIsolatedComponents({
        core: core2,
        serializedComponents: serializedChildren,
        ancestors: ancestorsForChildren,
        shadow,
        componentsReplacementOf
      });
      definingChildren = childrenResult.components;
      if (childrenResult.lastErrorMessage) {
        lastErrorMessage = childrenResult.lastErrorMessage;
      }
    }
  }
  let attributes = {};
  if (serializedComponent.attributes) {
    for (const attrName in serializedComponent.attributes) {
      const attribute = serializedComponent.attributes[attrName];
      if (attribute.component) {
        if (attrName === componentClass.addAttributeToResolver) {
          addComponentsToResolver({
            core: core2,
            components: [attribute.component],
            parentIdx: serializedComponent.componentIdx
          });
        }
        try {
          const attrResult = await createIsolatedComponents({
            core: core2,
            serializedComponents: [attribute.component],
            ancestors: ancestorsForChildren,
            shadow,
            componentsReplacementOf
          });
          if (attrResult.lastErrorMessage) {
            lastErrorMessage = attrResult.lastErrorMessage;
            lastErrorMessageFromAttribute = attrResult.lastErrorMessage;
          }
          attributes[attrName] = {
            component: attrResult.components[0]
          };
        } catch (e3) {
          console.error(e3);
          if (e3.message.includes("Circular dependency")) {
            throw Error(
              core2.dependencies.getCircularDependencyMessage([
                serializedComponent
              ])
            );
          } else {
            throw e3;
          }
        }
      } else if (attribute.references) {
        const attrResult = await createIsolatedComponents({
          core: core2,
          serializedComponents: attribute.references,
          ancestors: ancestorsForChildren,
          shadow,
          componentsReplacementOf
        });
        if (attrResult.lastErrorMessage) {
          lastErrorMessage = attrResult.lastErrorMessage;
          lastErrorMessageFromAttribute = attrResult.lastErrorMessage;
        }
        attributes[attrName] = {
          references: attrResult.components,
          stringChildren: attribute.stringChildren
        };
      } else {
        attributes[attrName] = serializedComponent.attributes[attrName];
      }
    }
  }
  let refResolution = null;
  if (serializedComponent.extending) {
    refResolution = unwrapSource(serializedComponent.extending);
    const nodeIdx = refResolution.nodeIdx;
    const originalPath = [];
    for (const pathPart of refResolution.originalPath) {
      const index2 = [];
      for (const indexPiece of pathPart.index) {
        const valueResult = await createIsolatedComponents({
          core: core2,
          serializedComponents: indexPiece.value,
          ancestors: ancestorsForChildren,
          shadow,
          componentsReplacementOf
        });
        if (valueResult.lastErrorMessage) {
          lastErrorMessage = valueResult.lastErrorMessage;
        }
        const value = valueResult.components;
        index2.push({
          value,
          position: indexPiece.position,
          sourceDoc: indexPiece.sourceDoc
        });
      }
      originalPath.push({
        name: pathPart.name,
        index: index2,
        position: pathPart.position,
        sourceDoc: pathPart.sourceDoc
      });
    }
    refResolution = {
      nodeIdx,
      unresolvedPath: refResolution.unresolvedPath,
      originalPath,
      nodesInResolvedPath: refResolution.nodesInResolvedPath
    };
  }
  if (serializedComponent.componentType === "_error") {
    lastErrorMessage = serializedComponent.state.message;
    core2.addDiagnostic({
      type: "error",
      message: serializedComponent.state.message,
      position: serializedComponent.position,
      sourceDoc: serializedComponent.sourceDoc
    });
  } else if (lastErrorMessageFromAttribute || lastErrorMessage && !componentClass.canDisplayChildErrors) {
    const convertResult = convertToErrorComponent(
      serializedComponent,
      lastErrorMessageFromAttribute || lastErrorMessage
    );
    serializedComponent = convertResult.component;
    attributes = {};
    componentClass = core2.componentInfoObjects.allComponentClasses[serializedComponent.componentType];
  }
  const prescribedDependencies = {};
  if (serializedComponent.downstreamDependencies) {
    for (const idxStr in serializedComponent.downstreamDependencies) {
      const idx = Number(idxStr);
      if (idx === componentIdx) {
        throw Error(
          core2.dependencies.getCircularDependencyMessage([
            serializedComponent
          ])
        );
      }
      if (core2._components[idx]) {
        prescribedDependencies[idx] = serializedComponent.downstreamDependencies[idx];
      } else {
        throw Error(
          core2.dependencies.getCircularDependencyMessage([
            serializedComponent
          ])
        );
      }
    }
  }
  const stateVariableDefinitions = await createStateVariableDefinitions({
    core: core2,
    componentClass,
    prescribedDependencies,
    componentIdx
  });
  delete core2.updateInfo.deletedComponents[componentIdx];
  delete core2.updateInfo.deletedStateVariables[componentIdx];
  const stateId = serializedComponent.stateId ?? componentIdx.toString();
  core2.componentIdxByStateId[stateId] = componentIdx;
  const newComponent = new componentClass({
    componentIdx,
    stateId,
    rootName: core2.rootNames?.[componentIdx] ?? "_id_" + componentIdx.toString(),
    ancestors,
    definingChildren,
    stateVariableDefinitions,
    serializedChildren: childrenToRemainSerialized.length > 0 ? childrenToRemainSerialized : EMPTY_SERIALIZED_CHILDREN,
    serializedComponent,
    attributes,
    componentInfoObjects: core2.componentInfoObjects,
    coreFunctions: core2.coreFunctions,
    flags: core2.flags,
    shadow,
    numerics: core2.numerics,
    sharedParameters,
    parentSharedParameters,
    refResolution
  });
  registerComponent({ core: core2, component: newComponent });
  if (componentsReplacementOf) {
    newComponent.replacementOf = componentsReplacementOf;
  }
  if (serializedComponent.adaptedFrom) {
    newComponent.adaptedFrom = core2._components[serializedComponent.adaptedFrom];
    newComponent.adaptedFrom.adapterUsed = newComponent;
  }
  for (const idxStr in prescribedDependencies) {
    const idx = Number(idxStr);
    const depArray = prescribedDependencies[idx];
    for (const dep of depArray) {
      if (dep.dependencyType === "referenceShadow") {
        const shadowInfo = {
          componentIdx: idx
        };
        Object.assign(shadowInfo, dep);
        delete shadowInfo.dependencyType;
        newComponent.shadows = shadowInfo;
        if (dep.firstLevelReplacement) {
          newComponent.firstLevelReplacement = true;
        }
        const shadowedComponent = core2._components[idx];
        if (!shadowedComponent.shadowedBy) {
          shadowedComponent.shadowedBy = [];
        }
        shadowedComponent.shadowedBy.push(newComponent);
        const mediatingShadowComposite = core2._components[shadowInfo.compositeIdx];
        if (!mediatingShadowComposite.mediatesShadows) {
          mediatingShadowComposite.mediatesShadows = [];
        }
        mediatingShadowComposite.mediatesShadows.push({
          shadowing: newComponent.componentIdx,
          shadowed: idx,
          propVariable: dep.propVariable
        });
        if (dep.isPrimaryShadow) {
          shadowedComponent.primaryShadow = newComponent.componentIdx;
          if (core2.dependencies.updateTriggers.primaryShadowDependencies[idx]) {
            for (const dep2 of core2.dependencies.updateTriggers.primaryShadowDependencies[idx]) {
              await dep2.recalculateDownstreamComponents();
            }
          }
        }
        break;
      }
    }
  }
  if (serializedComponent.unlinkedCopySource) {
    newComponent.unlinkedCopySource = serializedComponent.unlinkedCopySource;
  }
  await deriveChildResultsFromDefiningChildren({
    core: core2,
    parent: newComponent,
    expandComposites: false
  });
  await initializeComponentStateVariables({ core: core2, component: newComponent });
  core2.dependencies.createComponentDependencySlots(newComponent);
  const variablesChanged = await core2.dependencies.checkForDependenciesOnNewComponent(
    componentIdx
  );
  for (const varDescription of variablesChanged) {
    await core2.markStateVariableAndUpstreamDependentsStale({
      component: core2._components[varDescription.componentIdx],
      varName: varDescription.varName
    });
  }
  await checkForStateVariablesUpdatesForNewComponent({
    core: core2,
    componentIdx
  });
  await core2.dependencies.resolveIfReady({
    componentIdx,
    type: "componentIdentity",
    expandComposites: false
  });
  core2.recordStateVariablesMustEvaluate(componentIdx);
  await core2.checkForActionChaining({ component: newComponent });
  core2.parameterStack.pop();
  const results = { newComponent, lastErrorMessage };
  return results;
}
async function checkForStateVariablesUpdatesForNewComponent({
  core: core2,
  componentIdx
}) {
  let comp = core2._components[componentIdx];
  const stateId = comp.stateId;
  if (stateId in core2.updateInfo.stateVariableUpdatesForMissingComponents) {
    let result = await core2.processNewStateVariableValues(
      {
        [componentIdx]: core2.updateInfo.stateVariableUpdatesForMissingComponents[stateId]
      },
      // This `true` indicates we have a new component
      true
    );
    if (comp.constructor.processWhenJustUpdatedForNewComponent || result.foundIgnore) {
      for (let vName in core2.updateInfo.stateVariableUpdatesForMissingComponents[stateId]) {
        if (comp.state[vName]) {
          core2.updateInfo.stateVariablesToEvaluate.push({
            componentIdx,
            stateVariable: vName
          });
          comp.state[vName].justUpdatedForNewComponent = true;
          if (result.foundIgnore) {
            comp.reprocessAfterEvaluate = core2.updateInfo.stateVariableUpdatesForMissingComponents[stateId];
          }
        }
      }
    }
    delete core2.updateInfo.stateVariableUpdatesForMissingComponents[stateId];
  }
}
async function addQueuedErrorComponentsFromStateVariables({
  core: core2
}) {
  if (!core2.errorComponentsToAdd?.length) {
    return;
  }
  const errorComponentsToAdd = core2.errorComponentsToAdd;
  core2.errorComponentsToAdd = [];
  const numberInsertedAfterSource = {};
  for (let errorInfo of errorComponentsToAdd) {
    let sourceComponent = core2._components[errorInfo.componentIdx];
    let parent;
    while (sourceComponent?.parentIdx > 0) {
      const candidateParent = core2._components[sourceComponent.parentIdx];
      if (!candidateParent) {
        break;
      }
      if (candidateParent.constructor.canDisplayChildErrors) {
        parent = candidateParent;
        break;
      }
      sourceComponent = candidateParent;
    }
    if (!parent) {
      if (core2.document?.constructor.canDisplayChildErrors) {
        parent = core2.document;
      }
    }
    if (!parent) {
      continue;
    }
    let indexOfDefiningChildren = parent.definingChildren.length;
    if (sourceComponent?.parentIdx === parent.componentIdx) {
      const sourceInd = parent.definingChildren.findIndex(
        (child) => typeof child === "object" && child.componentIdx === sourceComponent.componentIdx
      );
      if (sourceInd !== -1) {
        const numberAlreadyInserted = numberInsertedAfterSource[sourceComponent.componentIdx] ?? 0;
        indexOfDefiningChildren = sourceInd + 1 + numberAlreadyInserted;
        numberInsertedAfterSource[sourceComponent.componentIdx] = numberAlreadyInserted + 1;
      }
    }
    let serializedErrorComponents = [
      {
        type: "serialized",
        componentType: "_error",
        componentIdx: core2._components.length,
        state: { message: errorInfo.message },
        position: errorInfo.position,
        sourceDoc: errorInfo.sourceDoc,
        children: [],
        attributes: {},
        doenetAttributes: {}
      }
    ];
    core2._components[core2._components.length] = void 0;
    let ancestors = ancestorsForChild(parent);
    core2.parameterStack.push(parent.sharedParameters, false);
    let createResult;
    try {
      createResult = await createIsolatedComponents({
        core: core2,
        serializedComponents: serializedErrorComponents,
        ancestors
      });
    } finally {
      core2.parameterStack.pop();
    }
    let addResults = await addChildrenAndRecurseToShadows({
      core: core2,
      parent,
      indexOfDefiningChildren,
      newChildren: createResult.components
    });
    if (!addResults.success) {
      throw Error(
        "Couldn't add error component from state variable evaluation."
      );
    }
  }
}
async function _expandAllCompositesBothPasses(core2) {
  await expandAllComposites({ core: core2, component: core2.document });
  await expandAllComposites({ core: core2, component: core2.document, force: true });
}
async function _drainStateVariablesToEvaluate(core2) {
  if (!core2.updateInfo.stateVariablesToEvaluate) {
    return;
  }
  const stateVariablesToEvaluate = core2.updateInfo.stateVariablesToEvaluate;
  core2.updateInfo.stateVariablesToEvaluate = [];
  for (const { componentIdx, stateVariable } of stateVariablesToEvaluate) {
    const comp = core2._components[componentIdx];
    if (comp && comp.state[stateVariable]) {
      await core2.getStateVariableValue({
        component: comp,
        stateVariable
      });
    }
  }
}
async function _drainCompositesToUpdateReplacements(core2) {
  if (core2.updateInfo.compositesToUpdateReplacements.size === 0) {
    return;
  }
  await core2.replacementChangesFromCompositesToUpdate();
  const componentNamesToUpdate = [
    ...core2.updateInfo.componentsToUpdateRenderers
  ];
  core2.updateInfo.componentsToUpdateRenderers.clear();
  await core2.updateRendererInstructions({
    componentNamesToUpdate
  });
}
const derivingChildResultsInProgress = /* @__PURE__ */ new WeakMap();
async function deriveChildResultsFromDefiningChildren({
  core: core2,
  parent,
  expandComposites = true,
  forceExpandComposites = false
}) {
  let inProgress = derivingChildResultsInProgress.get(core2);
  if (!inProgress) {
    inProgress = /* @__PURE__ */ new Set();
    derivingChildResultsInProgress.set(core2, inProgress);
  }
  if (inProgress.has(parent.componentIdx)) {
    return { success: false, skipping: true };
  }
  inProgress.add(parent.componentIdx);
  try {
    let result = await expandCompositeOfDefiningChildren({
      core: core2,
      parent,
      children: parent.definingChildren,
      expandComposites,
      forceExpandComposites
    });
    parent.unexpandedCompositesReady = result.unexpandedCompositesReady;
    parent.unexpandedCompositesNotReady = result.unexpandedCompositesNotReady;
    let previousActiveChildren;
    if (parent.activeChildren) {
      previousActiveChildren = parent.activeChildren.map(
        (child) => child.componentIdx ? child.componentIdx : child
      );
    }
    parent.activeChildren = parent.definingChildren.slice();
    parent.allChildren = {};
    parent.allChildrenOrdered = [];
    for (let ind = 0; ind < parent.activeChildren.length; ind++) {
      let child = parent.activeChildren[ind];
      let childIdx;
      if (typeof child !== "object") {
        continue;
      }
      childIdx = child.componentIdx;
      parent.allChildren[childIdx] = {
        activeChildrenIndex: ind,
        definingChildrenIndex: ind,
        component: child
      };
      parent.allChildrenOrdered.push(childIdx);
    }
    await replaceCompositeChildren({ core: core2, parent });
    const childGroupResults = await matchChildrenToChildGroups({
      core: core2,
      parent
    });
    if (childGroupResults.success) {
      delete core2.unmatchedChildren[parent.componentIdx];
      parent.childrenMatchedWithPlaceholders = true;
      parent.matchedCompositeChildrenWithPlaceholders = true;
    } else {
      parent.childrenMatchedWithPlaceholders = false;
      parent.matchedCompositeChildrenWithPlaceholders = true;
      let unmatchedChildrenTypes = [];
      for (let child of childGroupResults.unmatchedChildren) {
        if (typeof child === "string") {
          unmatchedChildrenTypes.push("string");
        } else {
          unmatchedChildrenTypes.push(
            "`<" + child.componentType + ">`"
          );
          if (core2.componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "_composite"
          })) {
            parent.matchedCompositeChildrenWithPlaceholders = false;
          }
        }
      }
      if (parent.doenetAttributes.isAttributeChildFor) {
        let attributeForComponentType = parent.ancestors[0].componentClass.componentType;
        core2.unmatchedChildren[parent.componentIdx] = {
          message: `Invalid format for attribute ${parent.doenetAttributes.isAttributeChildFor} of \`<${attributeForComponentType}>\`.`
        };
      } else {
        core2.unmatchedChildren[parent.componentIdx] = {
          message: `Invalid children for \`<${parent.componentType}>\`: Found invalid children: ${unmatchedChildrenTypes.join(
            ", "
          )}`
        };
      }
    }
    await core2.dependencies.addBlockersFromChangedActiveChildren({
      parent
    });
    if (parent.constructor.renderChildren) {
      let childrenUnchanged = previousActiveChildren && previousActiveChildren.length == parent.activeChildren.length && parent.activeChildren.every(
        (child, ind) => child.componentIdx ? child.componentIdx === previousActiveChildren[ind] : child === previousActiveChildren[ind]
      );
      if (!childrenUnchanged) {
        core2.componentsWithChangedChildrenToRender.add(
          parent.componentIdx
        );
      }
    }
    return childGroupResults;
  } finally {
    inProgress.delete(parent.componentIdx);
  }
}
async function matchChildrenToChildGroups({
  core: core2,
  parent
}) {
  parent.childMatchesByGroup = {};
  for (let groupName in parent.constructor.childGroupIndsByName) {
    parent.childMatchesByGroup[groupName] = [];
  }
  let success = true;
  let unmatchedChildren = [];
  for (let [ind, child] of parent.activeChildren.entries()) {
    let childType = typeof child !== "object" ? typeof child : child.componentType;
    if (childType === void 0) {
      success = false;
      unmatchedChildren.push(child);
      continue;
    }
    let result = findChildGroup({
      core: core2,
      childType,
      parentClass: parent.constructor
    });
    if (result.success) {
      parent.childMatchesByGroup[result.group].push(ind);
      if (result.adapterIndUsed !== void 0) {
        await substituteAdapter({
          core: core2,
          parent,
          childInd: ind,
          adapterIndUsed: result.adapterIndUsed
        });
      }
    } else {
      success = false;
      unmatchedChildren.push(child);
    }
  }
  return { success, unmatchedChildren };
}
function findChildGroup({
  core: core2,
  childType,
  parentClass
}) {
  let result = findChildGroupNoAdapters({
    core: core2,
    componentType: childType,
    parentClass
  });
  if (result.success) {
    return result;
  } else if (childType === "string") {
    return { success: false };
  }
  let childClass = core2.componentInfoObjects.allComponentClasses[childType];
  let numAdapters = childClass.numAdapters;
  for (let n2 = 0; n2 < numAdapters; n2++) {
    let adapterComponentType = childClass.getAdapterComponentType(
      n2,
      core2.componentInfoObjects.publicStateVariableInfo
    );
    result = findChildGroupNoAdapters({
      core: core2,
      componentType: adapterComponentType,
      parentClass
    });
    if (result.success) {
      result.adapterIndUsed = n2;
      return result;
    }
  }
  return findChildGroupNoAdapters({
    core: core2,
    componentType: childType,
    parentClass,
    afterAdapters: true
  });
}
function findChildGroupNoAdapters({
  core: core2,
  componentType,
  parentClass,
  afterAdapters = false
}) {
  if (parentClass.childGroupOfComponentType[componentType]) {
    return {
      success: true,
      group: parentClass.childGroupOfComponentType[componentType]
    };
  }
  for (let group of parentClass.childGroups) {
    for (let typeFromGroup of group.componentTypes) {
      if (core2.componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: componentType,
        baseComponentType: typeFromGroup
      })) {
        if (group.matchAfterAdapters && !afterAdapters) {
          continue;
        }
        if (!(typeFromGroup === "_base" && core2.componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: componentType,
          baseComponentType: "_composite"
        }))) {
          parentClass.childGroupOfComponentType[componentType] = group.group;
          return {
            success: true,
            group: group.group
          };
        }
      }
    }
  }
  return { success: false };
}
async function returnActiveChildrenIndicesToRender({
  core: core2,
  component
}) {
  let indicesToRender = [];
  let numChildrenToRender = Infinity;
  if ("numChildrenToRender" in component.state) {
    numChildrenToRender = await component.stateValues.numChildrenToRender;
  }
  let childIndicesToRender = null;
  if ("childIndicesToRender" in component.state) {
    childIndicesToRender = await component.stateValues.childIndicesToRender;
  }
  for (let [ind, child] of component.activeChildren.entries()) {
    if (ind >= numChildrenToRender) {
      break;
    }
    if (childIndicesToRender && !childIndicesToRender.includes(ind)) {
      continue;
    }
    if (typeof child === "object") {
      if (child.constructor.sendToRendererEvenIfHidden || !await child.stateValues.hidden) {
        indicesToRender.push(ind);
      }
    } else {
      let hidden = false;
      if (component.compositeReplacementActiveRange) {
        for (let compositeInfo of component.compositeReplacementActiveRange) {
          let composite = core2._components[compositeInfo.compositeIdx];
          if (await composite.stateValues.hidden) {
            if (compositeInfo.firstInd <= ind && compositeInfo.lastInd >= ind) {
              hidden = true;
              break;
            }
          }
        }
      }
      if (!hidden) {
        indicesToRender.push(ind);
      }
    }
  }
  return indicesToRender;
}
async function substituteAdapter({
  core: core2,
  parent,
  childInd,
  adapterIndUsed
}) {
  let originalChild = parent.activeChildren[childInd];
  let newSerializedChild;
  if (originalChild.componentIdx != void 0) {
    newSerializedChild = originalChild.getAdapter(adapterIndUsed);
    newSerializedChild.componentIdx = core2._components.length;
    core2._components[core2._components.length] = void 0;
  } else {
    newSerializedChild = {
      componentType: core2.componentInfoObjects.allComponentClasses[originalChild.componentType].getAdapterComponentType(
        adapterIndUsed,
        core2.componentInfoObjects.publicStateVariableInfo
      ),
      placeholderInd: originalChild.placeholderInd + "adapt"
    };
  }
  let adapter = originalChild.adapterUsed;
  if (adapter === void 0 || adapter.componentType !== newSerializedChild.componentType) {
    if (originalChild.componentIdx != void 0) {
      newSerializedChild.adaptedFrom = originalChild.componentIdx;
      assignDoenetMLRange(
        [newSerializedChild],
        originalChild.position,
        originalChild.sourceDoc
      );
      let newChildrenResult = await createIsolatedComponents({
        core: core2,
        serializedComponents: [newSerializedChild],
        shadow: true,
        ancestors: originalChild.ancestors
      });
      adapter = newChildrenResult.components[0];
    } else {
      newSerializedChild.adaptedFrom = originalChild;
      adapter = newSerializedChild;
    }
  }
  parent.activeChildren.splice(childInd, 1, adapter);
  if (originalChild.componentIdx != void 0) {
    delete parent.allChildren[originalChild.componentIdx].activeChildrenIndex;
    parent.allChildren[adapter.componentIdx] = {
      activeChildrenIndex: childInd,
      component: adapter
    };
  }
  if (originalChild.componentIdx != void 0) {
    let originalInd = parent.allChildrenOrdered.indexOf(
      originalChild.componentIdx
    );
    parent.allChildrenOrdered.splice(
      originalInd + 1,
      0,
      adapter.componentIdx
    );
  } else {
    let originalInd = parent.allChildrenOrdered.indexOf(
      originalChild.placeholderInd
    );
    parent.allChildrenOrdered.splice(
      originalInd + 1,
      0,
      adapter.placeholderInd
    );
  }
}
function gatherDescendants({
  ancestor,
  descendantTypes,
  recurseToMatchedChildren = true,
  useReplacementsForComposites = false,
  includeNonActiveChildren = false,
  skipOverAdapters = false,
  ignoreReplacementsOfMatchedComposites = false,
  ignoreReplacementsOfEncounteredComposites = false,
  init = true,
  componentInfoObjects
}) {
  let matchChildToTypes = (child) => descendantTypes.some(
    (ct2) => componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: child.componentType,
      baseComponentType: ct2
    })
  );
  let childrenToCheck = [];
  if (useReplacementsForComposites && componentInfoObjects.isInheritedComponentType({
    inheritedComponentType: ancestor.componentType,
    baseComponentType: "_composite"
  })) {
    if (init) {
      childrenToCheck.push(
        ...replacementsForComposites({
          composite: ancestor,
          componentInfoObjects,
          includeComposites: includeNonActiveChildren
        }).filter((x2) => typeof x2 === "object")
      );
    }
  } else {
    for (let childIdx of ancestor.allChildrenOrdered) {
      let childObj = ancestor.allChildren[childIdx];
      let child;
      let childIsActive = false;
      let childIsAdapter = false;
      if (childObj) {
        child = childObj.component;
        if (child.adaptedFrom) {
          childIsAdapter = true;
        }
        if (ancestor.activeChildren.includes(child)) {
          childIsActive = true;
        }
      } else {
        for (let aChild of ancestor.activeChildren) {
          if (aChild.placeholderInd === childIdx) {
            child = aChild;
            childIsActive = true;
            if (typeof childIdx === "string" && childIdx.substring(
              childIdx.length - 5,
              childIdx.length
            ) === "adapt") {
              childIsAdapter = true;
            }
            break;
          } else if (aChild.adaptedFrom && aChild.adaptedFrom.placeholderInd === childIdx) {
            child = aChild.adaptedFrom;
            break;
          }
        }
      }
      if (child) {
        if (childIsAdapter && skipOverAdapters) {
          if (!childrenToCheck.includes(child.adaptedFrom)) {
            childrenToCheck.push(child.adaptedFrom);
          }
        } else if (childIsActive || includeNonActiveChildren) {
          childrenToCheck.push(child);
        }
      }
    }
  }
  if (ignoreReplacementsOfMatchedComposites || ignoreReplacementsOfEncounteredComposites) {
    let namesToIgnore = [];
    for (let child of childrenToCheck) {
      let checkChildForReplacements = matchChildToTypes(child);
      if (ignoreReplacementsOfEncounteredComposites && !checkChildForReplacements) {
        checkChildForReplacements = !(child.componentType === "_copy" && child.doenetAttributes.copiedURI);
      }
      if (checkChildForReplacements && componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: child.componentType,
        baseComponentType: "_composite"
      })) {
        namesToIgnore = [
          ...namesToIgnore,
          ...replacementsForComposites({
            composite: child,
            componentInfoObjects,
            includeComposites: true
          }).filter((x2) => typeof x2 === "object").map(
            (x2) => x2.componentIdx ? x2.componentIdx : x2.placeholderInd
          )
        ];
      }
    }
    if (namesToIgnore.length > 0) {
      childrenToCheck = childrenToCheck.filter(
        (x2) => !(namesToIgnore.includes(x2.componentIdx) || namesToIgnore.includes(x2.placeholderInd))
      );
    }
  }
  let descendants = [];
  for (let child of childrenToCheck) {
    let matchedChild = matchChildToTypes(child);
    if (matchedChild) {
      descendants.push({
        componentIdx: child.componentIdx ? child.componentIdx : child.placeholderInd,
        componentType: child.componentType
      });
    }
    if ((!matchedChild || recurseToMatchedChildren) && child.placeholderInd === void 0) {
      let additionalDescendants = gatherDescendants({
        ancestor: child,
        descendantTypes,
        recurseToMatchedChildren,
        useReplacementsForComposites,
        includeNonActiveChildren,
        skipOverAdapters,
        ignoreReplacementsOfMatchedComposites,
        ignoreReplacementsOfEncounteredComposites,
        init: false,
        componentInfoObjects
      });
      descendants.push(...additionalDescendants);
    }
  }
  return descendants;
}
function replacementsForComposites({
  composite,
  includeComposites = false,
  componentInfoObjects
}) {
  let replacements = [];
  if (composite.replacements) {
    let originalReplacements;
    if (composite.replacementsToWithhold) {
      let numReplacements = composite.replacements.length - composite.replacementsToWithhold;
      originalReplacements = composite.replacements.slice(
        0,
        numReplacements
      );
    } else {
      originalReplacements = composite.replacements;
    }
    for (let replacement of originalReplacements) {
      if (componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: replacement.componentType,
        baseComponentType: "_composite"
      })) {
        if (includeComposites) {
          replacements.push(replacement);
        }
        replacements.push(
          ...replacementsForComposites({
            composite: replacement,
            componentInfoObjects,
            includeComposites
          })
        );
      } else {
        replacements.push(replacement);
      }
    }
  }
  return replacements;
}
function ancestorsIncludingComposites(comp, components) {
  if (comp.ancestors === void 0 || comp.ancestors.length === 0) {
    return [];
  }
  let comps = [comp.ancestors[0].componentIdx];
  let parent = components[comp.ancestors[0].componentIdx];
  if (parent) {
    comps.push(...ancestorsIncludingComposites(parent, components));
  }
  if (comp.replacementOf) {
    comps.push(comp.replacementOf.componentIdx);
    let replacementAs = ancestorsIncludingComposites(
      comp.replacementOf,
      components
    );
    for (let a2 of replacementAs) {
      if (!comps.includes(a2)) {
        comps.push(a2);
      }
    }
  }
  return comps;
}
function cloneChangeMetadataIfNeeded({
  changeMetadata,
  consumeChanges
}) {
  return consumeChanges ? changeMetadata : deepClone(changeMetadata);
}
const INITIAL_CHANGE_RECORD = Object.freeze({ changed: true });
const _Dependency = class _Dependency {
  constructor({
    component,
    stateVariable,
    allStateVariablesAffected,
    dependencyName,
    dependencyDefinition,
    dependencyHandler
  }) {
    this.downstreamVariableNameIfNoVariables = "__identity";
    this.dependencyName = dependencyName;
    this.dependencyHandler = dependencyHandler;
    this.upstreamComponentIdx = component.componentIdx;
    this.upstreamVariableNames = allStateVariablesAffected;
    this.definition = dependencyDefinition;
    this.representativeStateVariable = stateVariable;
    if (dependencyDefinition.doNotProxy) {
      this.doNotProxy = true;
    }
    if (dependencyDefinition.variablesOptional) {
      this.variablesOptional = true;
    }
    if (dependencyDefinition.publicStateVariablesOnly) {
      this.publicStateVariablesOnly = true;
    }
    if (dependencyDefinition.caseInsensitiveVariableMatch) {
      this.caseInsensitiveVariableMatch = true;
    }
    if (dependencyDefinition.useMappedVariableNames) {
      this.useMappedVariableNames = true;
    }
    if (dependencyDefinition.propIndex) {
      if (dependencyDefinition.propIndex.every(Number.isFinite)) {
        this.propIndex = dependencyDefinition.propIndex.map(Math.round);
      } else {
        this.propIndex = [];
      }
    }
    this.returnSingleVariableValue = false;
    this.returnSingleComponent = false;
    this.originalDownstreamVariableNames = [];
  }
  get dependencyType() {
    return this.constructor.dependencyType;
  }
  setUpParameters() {
  }
  async determineDownstreamComponents(_args) {
    return {
      success: true,
      downstreamComponentIndices: [],
      downstreamComponentTypes: []
    };
  }
  async initialize() {
    this.setUpParameters();
    let downComponents = await this.determineDownstreamComponents();
    let downstreamComponentIndices = downComponents.downstreamComponentIndices;
    let downstreamComponentTypes = downComponents.downstreamComponentTypes;
    this.upstreamVariableNames = this.dependencyHandler.internVariableNameListByCopy(
      this.upstreamVariableNames
    );
    this.originalDownstreamVariableNames = this.dependencyHandler.internVariableNameListByCopy(
      this.originalDownstreamVariableNames
    );
    this.componentIdentitiesChanged = true;
    let upCompDownDeps = this.dependencyHandler.downstreamDependencies[this.upstreamComponentIdx];
    if (!upCompDownDeps) {
      upCompDownDeps = this.dependencyHandler.downstreamDependencies[this.upstreamComponentIdx] = {};
    }
    for (let varName of this.upstreamVariableNames) {
      if (!upCompDownDeps[varName]) {
        upCompDownDeps[varName] = {};
      }
      upCompDownDeps[varName][this.dependencyName] = this;
    }
    if (this.originalDownstreamVariableNames.length === 0 && !this.originalVariablesByComponent) {
      delete this.mappedDownstreamVariableNamesByComponent;
      delete this.upValuesChanged;
    } else {
      this.mappedDownstreamVariableNamesByComponent = [];
      this.valuesChanged = [];
    }
    this.downstreamComponentIndices = [];
    this.downstreamComponentTypes = [];
    for (let [
      index2,
      downstreamComponentIdx
    ] of downstreamComponentIndices.entries()) {
      await this.addDownstreamComponent({
        downstreamComponentIdx,
        downstreamComponentType: downstreamComponentTypes[index2],
        index: index2
      });
    }
    this.downstreamComponentIndices = this.dependencyHandler.internComponentIndexList(
      this.downstreamComponentIndices
    );
    this.downstreamComponentTypes = this.dependencyHandler.internComponentTypeList(
      this.downstreamComponentTypes
    );
    if (this.mappedDownstreamVariableNamesByComponent) {
      this.mappedDownstreamVariableNamesByComponent = this.dependencyHandler.internNameListArray(
        this.mappedDownstreamVariableNamesByComponent
      );
    }
    if (this.valuesChanged) {
      this.valuesChanged = this.dependencyHandler.internValuesChangedArray(
        this.valuesChanged
      );
    }
  }
  /**
   * Replace the interned (frozen, shared) parallel downstream arrays with
   * mutable copies before changing the downstream set.
   */
  thawDownstreamArrays() {
    if (Object.isFrozen(this.downstreamComponentIndices)) {
      this.downstreamComponentIndices = [
        ...this.downstreamComponentIndices
      ];
    }
    if (Object.isFrozen(this.downstreamComponentTypes)) {
      this.downstreamComponentTypes = [...this.downstreamComponentTypes];
    }
    if (this.mappedDownstreamVariableNamesByComponent && Object.isFrozen(this.mappedDownstreamVariableNamesByComponent)) {
      this.mappedDownstreamVariableNamesByComponent = [
        ...this.mappedDownstreamVariableNamesByComponent
      ];
    }
    if (this.valuesChanged && Object.isFrozen(this.valuesChanged)) {
      this.valuesChanged = [...this.valuesChanged];
    }
  }
  /**
   * Replace the interned (frozen, shared) `valuesChanged` outer array
   * and/or the record at `componentInd` with mutable copies so a caller
   * can record change metadata on them.
   */
  thawValuesChangedRecord(componentInd) {
    if (!this.valuesChanged) {
      return;
    }
    if (Object.isFrozen(this.valuesChanged)) {
      this.valuesChanged = [...this.valuesChanged];
    }
    const record = this.valuesChanged[componentInd];
    if (record && Object.isFrozen(record)) {
      this.valuesChanged[componentInd] = { ...record };
    }
  }
  /**
   * Remove the change record for `varName` of the downstream component at
   * `componentInd` after `getValue` consumed it (records are recreated on
   * demand when marked changed again). Fully-consumed records converge to
   * the shared frozen empty record, and outer arrays of shared records are
   * re-interned, so an unchanging dependency retains no per-dependency
   * change-tracking allocations at all.
   */
  consumeChangeRecord(componentInd, varName) {
    const outer = this.valuesChanged;
    if (!outer) {
      return;
    }
    const record = outer[componentInd];
    if (!record) {
      return;
    }
    let replacement = record;
    if (Object.isFrozen(record)) {
      if (!(varName in record)) {
        return;
      }
      const remaining = Object.keys(record).filter(
        (name) => name !== varName
      );
      if (remaining.length === 0) {
        replacement = this.dependencyHandler.emptyValuesChangedRecord;
      } else {
        replacement = {};
        for (const name of remaining) {
          replacement[name] = record[name];
        }
      }
    } else {
      delete record[varName];
      if (Object.keys(record).length === 0) {
        replacement = this.dependencyHandler.emptyValuesChangedRecord;
      }
    }
    if (replacement !== record) {
      if (Object.isFrozen(this.valuesChanged)) {
        this.valuesChanged = [...this.valuesChanged];
      }
      this.valuesChanged[componentInd] = replacement;
    }
    if (!Object.isFrozen(this.valuesChanged)) {
      this.valuesChanged = this.dependencyHandler.internValuesChangedArray(
        this.valuesChanged
      );
    }
  }
  async addDownstreamComponent({
    downstreamComponentIdx,
    downstreamComponentType,
    index: index2
  }) {
    this.componentIdentitiesChanged = true;
    this.thawDownstreamArrays();
    this.downstreamComponentIndices.splice(
      index2,
      0,
      downstreamComponentIdx
    );
    this.downstreamComponentTypes.splice(index2, 0, downstreamComponentType);
    let downComponent = this.dependencyHandler._components[downstreamComponentIdx];
    if (downComponent) {
      let originalVarNames;
      if (this.originalVariablesByComponent) {
        originalVarNames = this.originalDownstreamVariableNamesByComponent[index2];
      } else {
        originalVarNames = this.originalDownstreamVariableNames;
      }
      if (this.caseInsensitiveVariableMatch) {
        originalVarNames = this.dependencyHandler.core.findCaseInsensitiveMatches({
          stateVariables: originalVarNames,
          componentClass: downComponent.constructor
        });
      }
      if (this.publicStateVariablesOnly) {
        originalVarNames = this.dependencyHandler.core.matchPublicStateVariables({
          stateVariables: originalVarNames,
          componentClass: downComponent.constructor
        });
      }
      let mappedVarNames = this.dependencyHandler.core.substituteAliases({
        stateVariables: originalVarNames,
        componentClass: downComponent.constructor
      });
      if (this.constructor.convertToArraySize) {
        const convertedVarNames = [];
        for (const vName of mappedVarNames) {
          let stateVarObj = downComponent.state[vName];
          if (stateVarObj) {
            if (stateVarObj.isArray) {
              await ensureStateVariableMaterialized({
                core: this.dependencyHandler.core,
                component: downComponent,
                stateVariable: vName
              });
            }
            if (stateVarObj.arraySizeStateVariable) {
              convertedVarNames.push(
                stateVarObj.arraySizeStateVariable
              );
            } else {
              convertedVarNames.push(
                `__${vName}_is_not_an_array`
              );
            }
            continue;
          }
          let converted = `__${vName}_is_not_an_array`;
          if (downComponent.arrayEntryPrefixes) {
            let arrayEntryPrefixesLongestToShortest = Object.keys(
              downComponent.arrayEntryPrefixes
            ).sort((a2, b2) => b2.length - a2.length);
            for (let arrayEntryPrefix of arrayEntryPrefixesLongestToShortest) {
              if (vName.substring(0, arrayEntryPrefix.length) === arrayEntryPrefix) {
                let arrayVariableName = downComponent.arrayEntryPrefixes[arrayEntryPrefix];
                let arrayStateVarObj = downComponent.state[arrayVariableName];
                let arrayKeys = arrayStateVarObj.getArrayKeysFromVarName({
                  arrayEntryPrefix,
                  varEnding: vName.substring(
                    arrayEntryPrefix.length
                  ),
                  numDimensions: arrayStateVarObj.numDimensions
                });
                if (arrayKeys.length > 0) {
                  await ensureStateVariableMaterialized({
                    core: this.dependencyHandler.core,
                    component: downComponent,
                    stateVariable: arrayVariableName
                  });
                  converted = downComponent.state[arrayVariableName].arraySizeStateVariable;
                  break;
                }
              }
            }
          }
          convertedVarNames.push(converted);
        }
        mappedVarNames = convertedVarNames;
      }
      if (this.propIndex !== void 0) {
        mappedVarNames = await arrayEntryNamesFromPropIndex({
          core: this.dependencyHandler.core,
          stateVariables: mappedVarNames,
          component: downComponent,
          propIndex: this.propIndex
        });
      }
      let downVarNames = mappedVarNames;
      if (originalVarNames.length > 0 || this.originalVariablesByComponent) {
        mappedVarNames = this.dependencyHandler.internVariableNameList(
          mappedVarNames
        );
        this.mappedDownstreamVariableNamesByComponent.splice(
          index2,
          0,
          mappedVarNames
        );
        this.valuesChanged.splice(
          index2,
          0,
          this.dependencyHandler.internInitialValuesChangedRecord(
            mappedVarNames,
            INITIAL_CHANGE_RECORD
          )
        );
        if (this.variablesOptional) {
          downVarNames = downVarNames.filter(
            (downVar) => downVar in downComponent.state || this.dependencyHandler.core.checkIfArrayEntry({
              stateVariable: downVar,
              component: downComponent
            }).isArrayEntry
          );
        }
        for (let downVar of downVarNames) {
          if (!downComponent.state[downVar]) {
            await this.dependencyHandler.core.createFromArrayEntry({
              component: downComponent,
              stateVariable: downVar
            });
          }
          if (!downComponent.state[downVar].isResolved) {
            for (let varName of this.upstreamVariableNames) {
              await this.dependencyHandler.addBlocker({
                blockerComponentIdx: downstreamComponentIdx,
                blockerType: "stateVariable",
                blockerStateVariable: downVar,
                componentIdxBlocked: this.upstreamComponentIdx,
                typeBlocked: "stateVariable",
                stateVariableBlocked: varName
              });
              if (this.dependencyType === "determineDependencies") {
                await this.dependencyHandler.addBlocker({
                  blockerComponentIdx: downstreamComponentIdx,
                  blockerType: "stateVariable",
                  blockerStateVariable: downVar,
                  componentIdxBlocked: this.upstreamComponentIdx,
                  typeBlocked: "determineDependencies",
                  stateVariableBlocked: varName,
                  dependencyBlocked: this.dependencyName
                });
              }
            }
          }
        }
      }
      if (downVarNames.length === 0) {
        downVarNames = [this.downstreamVariableNameIfNoVariables];
      }
      let downCompUpDeps = this.dependencyHandler.upstreamDependencies[downstreamComponentIdx];
      if (!downCompUpDeps) {
        downCompUpDeps = this.dependencyHandler.upstreamDependencies[downstreamComponentIdx] = {};
      }
      for (let varName of downVarNames) {
        const existingUpDeps = downCompUpDeps[varName];
        if (existingUpDeps === void 0) {
          downCompUpDeps[varName] = [this];
        } else {
          downCompUpDeps[varName] = existingUpDeps.concat(this);
        }
        if (varName !== this.downstreamVariableNameIfNoVariables) {
          for (let upstreamVarName of this.upstreamVariableNames) {
            this.dependencyHandler.resetCircularCheckPassed(
              this.upstreamComponentIdx,
              upstreamVarName
            );
          }
        }
      }
    }
    for (let upVarName of this.upstreamVariableNames) {
      if (this.dependencyHandler._components[this.upstreamComponentIdx].state[upVarName].initiallyResolved) {
        await this.dependencyHandler.core.markStateVariableAndUpstreamDependentsStale(
          {
            component: this.dependencyHandler.components[this.upstreamComponentIdx],
            varName: upVarName
          }
        );
      }
    }
  }
  async removeDownstreamComponent({
    indexToRemove,
    recordChange = true
  }) {
    if (recordChange) {
      this.componentIdentitiesChanged = true;
    }
    this.thawDownstreamArrays();
    let componentIdx = this.downstreamComponentIndices[indexToRemove];
    this.downstreamComponentIndices.splice(indexToRemove, 1);
    this.downstreamComponentTypes.splice(indexToRemove, 1);
    if (componentIdx in this.dependencyHandler._components) {
      let affectedDownstreamVariableNames;
      if (!this.mappedDownstreamVariableNamesByComponent) {
        affectedDownstreamVariableNames = [
          this.downstreamVariableNameIfNoVariables
        ];
      } else {
        affectedDownstreamVariableNames = this.mappedDownstreamVariableNamesByComponent[indexToRemove];
        this.mappedDownstreamVariableNamesByComponent.splice(
          indexToRemove,
          1
        );
        this.valuesChanged.splice(indexToRemove, 1);
        if (this.variablesOptional) {
          affectedDownstreamVariableNames = [
            ...affectedDownstreamVariableNames,
            this.downstreamVariableNameIfNoVariables
          ];
        }
      }
      for (let vName of affectedDownstreamVariableNames) {
        let downCompUpDeps = this.dependencyHandler.upstreamDependencies[componentIdx][vName];
        if (downCompUpDeps) {
          let ind = downCompUpDeps.indexOf(this);
          if (ind !== -1) {
            if (downCompUpDeps.length === 1) {
              delete this.dependencyHandler.upstreamDependencies[componentIdx][vName];
            } else {
              this.dependencyHandler.upstreamDependencies[componentIdx][vName] = downCompUpDeps.slice(0, ind).concat(downCompUpDeps.slice(ind + 1));
            }
          }
        }
        if (vName !== this.downstreamVariableNameIfNoVariables) {
          for (let upstreamVarName of this.upstreamVariableNames) {
            this.dependencyHandler.resetCircularCheckPassed(
              this.upstreamComponentIdx,
              upstreamVarName
            );
          }
        }
      }
    }
    if (recordChange) {
      for (let upVarName of this.upstreamVariableNames) {
        if (this.dependencyHandler._components[this.upstreamComponentIdx].state[upVarName].initiallyResolved) {
          await this.dependencyHandler.core.markStateVariableAndUpstreamDependentsStale(
            {
              component: this.dependencyHandler.components[this.upstreamComponentIdx],
              varName: upVarName
            }
          );
        }
      }
    }
  }
  async swapDownstreamComponents(index1, index2) {
    this.componentIdentitiesChanged = true;
    this.thawDownstreamArrays();
    [
      this.downstreamComponentIndices[index1],
      this.downstreamComponentIndices[index2]
    ] = [
      this.downstreamComponentIndices[index2],
      this.downstreamComponentIndices[index1]
    ];
    [
      this.downstreamComponentTypes[index1],
      this.downstreamComponentTypes[index2]
    ] = [
      this.downstreamComponentTypes[index2],
      this.downstreamComponentTypes[index1]
    ];
    if (this.originalDownstreamVariableNames.length > 0 || this.originalVariablesByComponent) {
      [
        this.mappedDownstreamVariableNamesByComponent[index1],
        this.mappedDownstreamVariableNamesByComponent[index2]
      ] = [
        this.mappedDownstreamVariableNamesByComponent[index2],
        this.mappedDownstreamVariableNamesByComponent[index1]
      ];
      [this.valuesChanged[index1], this.valuesChanged[index2]] = [
        this.valuesChanged[index2],
        this.valuesChanged[index1]
      ];
    }
    for (let upVarName of this.upstreamVariableNames) {
      if (this.dependencyHandler._components[this.upstreamComponentIdx].state[upVarName].initiallyResolved) {
        await this.dependencyHandler.core.markStateVariableAndUpstreamDependentsStale(
          {
            component: this.dependencyHandler.components[this.upstreamComponentIdx],
            varName: upVarName
          }
        );
      }
    }
  }
  deleteDependency() {
    let affectedDownstreamVariableNamesByUpstreamComponent = [];
    if (!this.mappedDownstreamVariableNamesByComponent) {
      affectedDownstreamVariableNamesByUpstreamComponent = Array(
        this.downstreamComponentIndices.length
      ).fill([this.downstreamVariableNameIfNoVariables]);
    } else {
      affectedDownstreamVariableNamesByUpstreamComponent = this.mappedDownstreamVariableNamesByComponent;
      if (this.variablesOptional) {
        let newVarNames = [];
        for (let [
          ind,
          cIdx
        ] of this.downstreamComponentIndices.entries()) {
          let varNamesForComponent = [];
          for (let vName of affectedDownstreamVariableNamesByUpstreamComponent[ind]) {
            if (this.dependencyHandler.components[cIdx].state[vName]) {
              varNamesForComponent.push(vName);
            }
          }
          if (varNamesForComponent.length > 0) {
            newVarNames.push(varNamesForComponent);
          } else {
            newVarNames.push([
              this.downstreamVariableNameIfNoVariables
            ]);
          }
        }
        affectedDownstreamVariableNamesByUpstreamComponent = newVarNames;
      }
    }
    for (let [
      cInd,
      downCompIdx
    ] of this.downstreamComponentIndices.entries()) {
      for (let vName of affectedDownstreamVariableNamesByUpstreamComponent[cInd]) {
        let downCompUpDeps = this.dependencyHandler.upstreamDependencies[downCompIdx][vName];
        if (downCompUpDeps) {
          let ind = downCompUpDeps.indexOf(this);
          if (ind !== -1) {
            if (downCompUpDeps.length === 1) {
              delete this.dependencyHandler.upstreamDependencies[downCompIdx][vName];
            } else {
              this.dependencyHandler.upstreamDependencies[downCompIdx][vName] = downCompUpDeps.slice(0, ind).concat(downCompUpDeps.slice(ind + 1));
            }
          }
        }
        for (let upVar of this.upstreamVariableNames) {
          this.dependencyHandler.deleteFromNeededToResolve({
            componentIdxBlocked: this.upstreamComponentIdx,
            typeBlocked: "stateVariable",
            stateVariableBlocked: upVar,
            blockerType: "stateVariable",
            blockerCode: downCompIdx + "|" + vName
          });
        }
        if (vName !== this.downstreamVariableNameIfNoVariables) {
          for (let upstreamVarName of this.upstreamVariableNames) {
            this.dependencyHandler.resetCircularCheckPassed(
              this.upstreamComponentIdx,
              upstreamVarName
            );
          }
        }
      }
    }
    this.deleteFromUpdateTriggers();
    let upCompDownDeps = this.dependencyHandler.downstreamDependencies[this.upstreamComponentIdx];
    for (let varName of this.upstreamVariableNames) {
      delete upCompDownDeps[varName][this.dependencyName];
    }
  }
  deleteFromUpdateTriggers() {
  }
  async getValueNoProxy({
    verbose = false,
    consumeChanges = true
  } = {}) {
    return await _Dependency.prototype.getValue.call(this, {
      verbose,
      skipProxy: true,
      consumeChanges
    });
  }
  /**
   * Return current dependency value, change metadata, and used-default metadata.
   * When consumeChanges is false, this method reports current change flags without
   * clearing them from dependency state.
   */
  async getValue({
    verbose = false,
    skipProxy = false,
    consumeChanges = true
  } = {}) {
    let value = [];
    let changes = {};
    let usedDefault = [];
    if (this.componentIdentitiesChanged) {
      changes.componentIdentitiesChanged = true;
      if (consumeChanges) {
        this.componentIdentitiesChanged = false;
      }
    }
    for (let [
      componentInd,
      componentIdx
    ] of this.downstreamComponentIndices.entries()) {
      let depComponent = this.dependencyHandler._components[componentIdx];
      usedDefault[componentInd] = false;
      if (depComponent) {
        let componentObj = {
          componentType: depComponent.componentType
        };
        if (!this.skipComponentIndices) {
          componentObj.componentIdx = componentIdx;
        }
        if (depComponent.position) {
          componentObj.position = JSON.parse(
            JSON.stringify(depComponent.position)
          );
          componentObj.sourceDoc = depComponent.sourceDoc;
        }
        let originalVarNames;
        if (this.originalVariablesByComponent) {
          originalVarNames = this.originalDownstreamVariableNamesByComponent[componentInd];
        } else {
          originalVarNames = this.originalDownstreamVariableNames;
        }
        if (originalVarNames.length > 0) {
          componentObj.stateValues = {};
          let usedDefaultObj = {};
          let foundOneUsedDefault = false;
          for (let [
            varInd,
            originalVarName
          ] of originalVarNames.entries()) {
            let mappedVarName = this.mappedDownstreamVariableNamesByComponent[componentInd][varInd];
            let nameForOutput = this.useMappedVariableNames ? mappedVarName : originalVarName;
            if (!this.variablesOptional || mappedVarName in depComponent.state) {
              let mappedStateVarObj = depComponent.state[mappedVarName];
              if (!mappedStateVarObj.deferred) {
                componentObj.stateValues[nameForOutput] = await mappedStateVarObj.value;
                let valueChanged = this.valuesChanged[componentInd][mappedVarName];
                if (valueChanged?.changed) {
                  if (!changes.valuesChanged) {
                    changes.valuesChanged = {};
                  }
                  if (!changes.valuesChanged[componentInd]) {
                    changes.valuesChanged[componentInd] = {};
                  }
                  changes.valuesChanged[componentInd][nameForOutput] = cloneChangeMetadataIfNeeded({
                    changeMetadata: valueChanged,
                    consumeChanges
                  });
                }
                if (consumeChanges) {
                  this.consumeChangeRecord(
                    componentInd,
                    mappedVarName
                  );
                }
                if (mappedStateVarObj.usedDefault) {
                  usedDefaultObj[nameForOutput] = true;
                  foundOneUsedDefault = true;
                } else if (mappedStateVarObj.isArrayEntry && mappedStateVarObj.arrayKeys.length === 1) {
                  let arrayStateVarObj = depComponent.state[mappedStateVarObj.arrayStateVariable];
                  if (arrayStateVarObj.usedDefaultByArrayKey[mappedStateVarObj.arrayKeys[0]]) {
                    usedDefaultObj[nameForOutput] = true;
                    foundOneUsedDefault = true;
                  }
                }
              }
            }
          }
          if (foundOneUsedDefault) {
            usedDefault[componentInd] = usedDefaultObj;
          }
        }
        value.push(componentObj);
      } else {
        value.push({
          componentType: this.downstreamComponentTypes[componentInd]
        });
      }
    }
    if (!verbose) {
      if (this.returnSingleVariableValue) {
        if (value.length === 1) {
          value = value[0];
          if (changes.valuesChanged && changes.valuesChanged[0]) {
            changes.valuesChanged = changes.valuesChanged[0];
          } else {
            delete changes.valuesChanged;
          }
          usedDefault = usedDefault[0];
          let stateVariables = Object.keys(value.stateValues);
          if (stateVariables.length === 1) {
            let nameForOutput = stateVariables[0];
            value = value.stateValues[nameForOutput];
            if (changes.valuesChanged && changes.valuesChanged[nameForOutput]) {
              changes.valuesChanged = changes.valuesChanged[nameForOutput];
            }
            if (usedDefault) {
              usedDefault = usedDefault[nameForOutput];
            }
          } else {
            value = null;
            changes = {};
            usedDefault = false;
          }
        } else {
          value = null;
          changes = {};
          usedDefault = false;
        }
      } else if (this.returnSingleComponent) {
        if (value.length === 1) {
          value = value[0];
          if (changes.valuesChanged && changes.valuesChanged[0]) {
            changes.valuesChanged = changes.valuesChanged[0];
          } else {
            delete changes.valuesChanged;
          }
          usedDefault = usedDefault[0];
        } else {
          value = null;
          usedDefault = false;
        }
      }
    }
    return { value, changes, usedDefault };
  }
  checkForCircular() {
    for (let varName of this.upstreamVariableNames) {
      this.dependencyHandler.resetCircularCheckPassed(
        this.upstreamComponentIdx,
        varName
      );
    }
    for (let varName of this.upstreamVariableNames) {
      this.dependencyHandler.checkForCircularDependency({
        componentIdx: this.upstreamComponentIdx,
        varName
      });
    }
  }
  async recalculateDownstreamComponents({ force = false } = {}) {
    let newDownComponents = await this.determineDownstreamComponents({
      force
    });
    let newComponentIndices = newDownComponents.downstreamComponentIndices;
    let foundChange = newComponentIndices.length !== this.downstreamComponentIndices.length || this.downstreamComponentIndices.some(
      (v2, i2) => v2 != newComponentIndices[i2]
    );
    if (foundChange) {
      this.componentIdentitiesChanged = true;
      let nRemoved = 0;
      for (let [ind, downCompIdx] of [
        ...this.downstreamComponentIndices
      ].entries()) {
        if (!newComponentIndices.includes(downCompIdx)) {
          await this.removeDownstreamComponent({
            indexToRemove: ind - nRemoved
          });
          nRemoved++;
        }
      }
      for (let [ind, downCompIdx] of newComponentIndices.entries()) {
        let oldInd = this.downstreamComponentIndices.indexOf(downCompIdx);
        if (oldInd !== -1) {
          if (oldInd !== ind) {
            await this.swapDownstreamComponents(oldInd, ind);
          }
        } else {
          await this.addDownstreamComponent({
            downstreamComponentIdx: downCompIdx,
            downstreamComponentType: newDownComponents.downstreamComponentTypes[ind],
            index: ind
          });
        }
      }
    }
    if (this.originalVariablesByComponent) {
      for (let [ind, downCompIdx] of [
        ...this.downstreamComponentIndices
      ].entries()) {
        if (this.mappedDownstreamVariableNamesByComponent[ind].length !== this.originalDownstreamVariableNamesByComponent[ind].length || this.mappedDownstreamVariableNamesByComponent[ind].some(
          (v2, i2) => this.originalDownstreamVariableNamesByComponent[ind][i2] !== v2
        )) {
          await this.removeDownstreamComponent({
            indexToRemove: ind
          });
          await this.addDownstreamComponent({
            downstreamComponentIdx: downCompIdx,
            downstreamComponentType: newDownComponents.downstreamComponentTypes[ind],
            index: ind
          });
        }
      }
    }
    return { success: newDownComponents.success };
  }
  /**
   * Add resolve blockers to this dependency due to the component with `componentIdx`
   * not existing as well as update triggers that will attempt to resolve
   * this dependency when the component is created.
   */
  async addBlockerUpdateTriggerForMissingComponent(componentIdx) {
    this.addUpdateTriggerForMissingComponent(componentIdx);
    for (const varName of this.upstreamVariableNames) {
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: componentIdx,
        blockerType: "componentIdentity",
        componentIdxBlocked: this.upstreamComponentIdx,
        typeBlocked: "recalculateDownstreamComponents",
        stateVariableBlocked: varName,
        dependencyBlocked: this.dependencyName
      });
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: this.upstreamComponentIdx,
        blockerType: "recalculateDownstreamComponents",
        blockerStateVariable: varName,
        blockerDependency: this.dependencyName,
        componentIdxBlocked: this.upstreamComponentIdx,
        typeBlocked: "stateVariable",
        stateVariableBlocked: varName
      });
    }
  }
  /**
   * Add update triggers to the component with `componentIdx`
   * that will update this dependency when the component is created.
   */
  async addUpdateTriggerForMissingComponent(componentIdx) {
    let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx];
    if (!dependenciesMissingComponent) {
      dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx] = [];
    }
    if (!dependenciesMissingComponent.includes(this)) {
      dependenciesMissingComponent.push(this);
    }
  }
  /**
   * Delete any update triggers for this component based on `componentIdx` not yet existing
   */
  async deleteUpdateTriggerForMissingComponent(componentIdx) {
    const dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx];
    if (dependenciesMissingComponent) {
      const ind = dependenciesMissingComponent.indexOf(this);
      if (ind !== -1) {
        dependenciesMissingComponent.splice(ind, 1);
      }
    }
  }
  /**
   * Add a resolve blocker to this dependency based on `varName` of `componentIdx`
   * not yet being resolved.
   */
  async addBlockerForUnresolvedStateVariable(componentIdx, varName) {
    for (const vName of this.upstreamVariableNames) {
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: componentIdx,
        blockerType: "stateVariable",
        blockerStateVariable: varName,
        componentIdxBlocked: this.upstreamComponentIdx,
        typeBlocked: "recalculateDownstreamComponents",
        stateVariableBlocked: vName,
        dependencyBlocked: this.dependencyName
      });
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: this.upstreamComponentIdx,
        blockerType: "recalculateDownstreamComponents",
        blockerStateVariable: vName,
        blockerDependency: this.dependencyName,
        componentIdxBlocked: this.upstreamComponentIdx,
        typeBlocked: "stateVariable",
        stateVariableBlocked: vName
      });
    }
  }
  /**
   * Add a resolve blocker to this dependency based on `composite`
   * not yet being expanded.
   */
  async addBlockerForUnexpandedComposite(composite) {
    for (const varName of this.upstreamVariableNames) {
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: this.upstreamComponentIdx,
        blockerType: "recalculateDownstreamComponents",
        blockerStateVariable: varName,
        blockerDependency: this.dependencyName,
        componentIdxBlocked: this.upstreamComponentIdx,
        typeBlocked: "stateVariable",
        stateVariableBlocked: varName
      });
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: composite.componentIdx,
        blockerType: "expandComposite",
        componentIdxBlocked: this.upstreamComponentIdx,
        typeBlocked: "recalculateDownstreamComponents",
        stateVariableBlocked: varName,
        dependencyBlocked: this.dependencyName
      });
    }
    if (!composite.state.readyToExpandWhenResolved.isResolved) {
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: composite.componentIdx,
        blockerType: "stateVariable",
        blockerStateVariable: "readyToExpandWhenResolved",
        componentIdxBlocked: composite.componentIdx,
        typeBlocked: "expandComposite"
      });
    }
  }
  /** Add an update trigger to this dependency that will update it
   * if the replacements of any composite in `composites` are changed. */
  addUpdateTriggersForCompositeReplacements(composites) {
    for (let cIdx of composites) {
      let replacementDependencies = this.dependencyHandler.updateTriggers.replacementDependenciesByComposite[cIdx];
      if (!replacementDependencies) {
        replacementDependencies = this.dependencyHandler.updateTriggers.replacementDependenciesByComposite[cIdx] = [];
      }
      if (!replacementDependencies.includes(this)) {
        replacementDependencies.push(this);
      }
    }
  }
  /**
   * Delete any update triggers for this dependency based on the replacements of `composites`.
   */
  deleteUpdateTriggersForCompositeReplacements(composites) {
    for (let compositeIdx of composites) {
      let replacementDeps = this.dependencyHandler.updateTriggers.replacementDependenciesByComposite[compositeIdx];
      if (replacementDeps) {
        let ind = replacementDeps.indexOf(this);
        if (ind !== -1) {
          replacementDeps.splice(ind, 1);
        }
      }
    }
  }
};
_Dependency.dependencyType = "_base";
let Dependency = _Dependency;
const _StateVariableDependency = class _StateVariableDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableName == void 0) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableName is not defined`
      );
    }
    this.originalDownstreamVariableNames = [this.definition.variableName];
    if (this.definition.onlyToSetInInverseDefinition) {
      this.onlyToSetInInverseDefinition = true;
    }
    if (this.definition.returnAsComponentObject) {
      this.returnSingleComponent = true;
    } else {
      this.returnSingleVariableValue = true;
    }
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      this.addBlockerUpdateTriggerForMissingComponent(this.componentIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      this.deleteUpdateTriggerForMissingComponent(
        this.specifiedComponentName
      );
    }
  }
};
_StateVariableDependency.dependencyType = "stateVariable";
let StateVariableDependency = _StateVariableDependency;
const _StateVariableFromUnresolvedPathDependency = class _StateVariableFromUnresolvedPathDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (!this.definition.unresolvedPath) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: unresolvedPath is not defined`
      );
    }
    this.originalDownstreamVariableNames = [];
    if (this.definition.returnAsComponentObject) {
      this.returnSingleComponent = true;
    } else {
      this.returnSingleVariableValue = true;
    }
  }
  async determineDownstreamComponents() {
    const component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      this.addBlockerUpdateTriggerForMissingComponent(this.componentIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let unresolvedPath = [...this.definition.unresolvedPath];
    let nextPart = unresolvedPath.shift();
    let variableName = nextPart.name;
    if (variableName === "") {
      variableName = component.constructor.variableForIndexAsProp;
      if (!variableName) {
        return {
          success: true,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
    }
    this.originalDownstreamVariableNames = [variableName];
    let propIndex = [];
    let foundBadIndex = false;
    if (nextPart.index.length > 0) {
      propIndex = nextPart.index.map(
        (index_part) => Math.round(Number(index_part.value[0]))
      );
      if (!propIndex.every(Number.isFinite)) {
        foundBadIndex = true;
      }
    }
    nextPart = unresolvedPath.shift();
    if (foundBadIndex || unresolvedPath.length > 0 || nextPart?.index.length > 0) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (nextPart != void 0) {
      let foundMatchToNextName = false;
      const stateVarInfo = this.dependencyHandler.componentInfoObjects.publicStateVariableInfo[component.componentType].stateVariableDescriptions[variableName];
      if (stateVarInfo?.indexAliases) {
        const dim = propIndex.length;
        const aliases = stateVarInfo.indexAliases[dim];
        const aliasIdx = aliases.indexOf(nextPart.name);
        if (aliasIdx !== -1) {
          propIndex.push(aliasIdx + 1);
          foundMatchToNextName = true;
        }
      } else {
        const arrayEntryCheck = this.dependencyHandler.core.checkIfArrayEntry({
          stateVariable: variableName,
          component
        });
        if (arrayEntryCheck.isArrayEntry) {
          const arrayVariableName = arrayEntryCheck.arrayVariableName;
          const arrayEntryPrefix = arrayEntryCheck.arrayEntryPrefix;
          const arrayStateVarObj = component.state[arrayVariableName];
          if (arrayStateVarObj.indexAliases) {
            const arrayDimensions = arrayStateVarObj.numDimensions;
            const entryDimensions = arrayStateVarObj.returnEntryDimensions(
              arrayEntryPrefix
            );
            const dimensionInArray = arrayDimensions - entryDimensions;
            const dim = propIndex.length;
            const aliases = arrayStateVarObj.indexAliases[dim + dimensionInArray];
            const aliasIdx = aliases.indexOf(nextPart.name);
            if (aliasIdx !== -1) {
              propIndex.push(aliasIdx + 1);
              foundMatchToNextName = true;
            }
          }
        }
      }
      if (!foundMatchToNextName) {
        return {
          success: true,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
    }
    if (propIndex.length > 0) {
      this.propIndex = propIndex;
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      this.deleteUpdateTriggerForMissingComponent(
        this.specifiedComponentName
      );
    }
  }
};
_StateVariableFromUnresolvedPathDependency.dependencyType = "stateVariableFromUnresolvedPath";
let StateVariableFromUnresolvedPathDependency = _StateVariableFromUnresolvedPathDependency;
const _MultipleStateVariablesDependency = class _MultipleStateVariablesDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (!Array.isArray(this.definition.variableNames)) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
      );
    }
    this.originalDownstreamVariableNames = this.definition.variableNames;
    this.returnSingleComponent = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_MultipleStateVariablesDependency.dependencyType = "multipleStateVariables";
let MultipleStateVariablesDependency = _MultipleStateVariablesDependency;
const _StateVariableComponentTypeDependency = class _StateVariableComponentTypeDependency extends StateVariableDependency {
  async getValue({ verbose = false, consumeChanges = true } = {}) {
    let value = [];
    let changes = {};
    if (this.staticValue) {
      value = [this.staticValue];
    } else {
      if (this.componentIdentitiesChanged) {
        changes.componentIdentitiesChanged = true;
        if (consumeChanges) {
          this.componentIdentitiesChanged = false;
        }
      }
      if (this.downstreamComponentIndices.length === 1) {
        let componentIdx = this.downstreamComponentIndices[0];
        let depComponent = this.dependencyHandler.components[componentIdx];
        let componentObj = {
          componentIdx: depComponent.componentIdx,
          componentType: depComponent.componentType
        };
        componentObj.stateValues = {};
        let originalVarName = this.originalDownstreamVariableNames[0];
        let mappedVarName = this.mappedDownstreamVariableNamesByComponent[0][0];
        let nameForOutput = this.useMappedVariableNames ? mappedVarName : originalVarName;
        if (!this.variablesOptional || mappedVarName in depComponent.state) {
          if (!depComponent.state[mappedVarName].deferred) {
            let stateVarObj = depComponent.state[mappedVarName];
            await stateVarObj.value;
            componentObj.stateValues[nameForOutput] = stateVarObj.componentType;
            if (stateVarObj.isArray) {
              if (stateVarObj.wrappingComponents?.length > 0) {
                let wrapCT = stateVarObj.wrappingComponents[stateVarObj.wrappingComponents.length - 1][0];
                if (typeof wrapCT === "object") {
                  wrapCT = wrapCT.componentType;
                }
                componentObj.stateValues[nameForOutput] = wrapCT;
              }
            }
            let valueChanged0 = this.valuesChanged[0][mappedVarName];
            if (valueChanged0?.changed) {
              if (!changes.valuesChanged) {
                changes.valuesChanged = {};
              }
              if (!changes.valuesChanged[0]) {
                changes.valuesChanged[0] = {};
              }
              changes.valuesChanged[0][nameForOutput] = cloneChangeMetadataIfNeeded({
                changeMetadata: valueChanged0,
                consumeChanges
              });
            }
            if (consumeChanges) {
              this.consumeChangeRecord(0, mappedVarName);
            }
            let hasVariableComponentType = stateVarObj.shadowingInstructions?.hasVariableComponentType;
            if (!hasVariableComponentType && stateVarObj.isArrayEntry) {
              let arrayStateVarObj = depComponent.state[stateVarObj.arrayStateVariable];
              hasVariableComponentType = arrayStateVarObj.shadowingInstructions?.hasVariableComponentType;
            }
            if (!hasVariableComponentType) {
              this.staticValue = componentObj;
              await this.removeDownstreamComponent({
                indexToRemove: 0,
                recordChange: false
              });
            }
          }
        }
        value = [componentObj];
      }
    }
    if (!verbose) {
      if (this.returnSingleVariableValue) {
        if (value.length === 1) {
          value = value[0];
          let stateVariables = Object.keys(value.stateValues);
          if (changes.valuesChanged && changes.valuesChanged[0] && changes.valuesChanged[0][0]) {
            changes.valuesChanged = changes.valuesChanged[0][0];
          }
          if (stateVariables.length === 1) {
            value = value.stateValues[stateVariables[0]];
          } else {
            value = null;
          }
        } else {
          value = null;
        }
      } else if (this.returnSingleComponent) {
        if (value.length === 1) {
          value = value[0];
          if (changes.valuesChanged && changes.valuesChanged[0]) {
            changes.valuesChanged = changes.valuesChanged[0];
          }
        } else {
          value = null;
        }
      }
    }
    return { value, changes, usedDefault: false };
  }
};
_StateVariableComponentTypeDependency.dependencyType = "stateVariableComponentType";
let StateVariableComponentTypeDependency = _StateVariableComponentTypeDependency;
const _StateVariableArraySizeDependency = class _StateVariableArraySizeDependency extends StateVariableDependency {
};
_StateVariableArraySizeDependency.dependencyType = "stateVariableArraySize";
_StateVariableArraySizeDependency.convertToArraySize = true;
let StateVariableArraySizeDependency = _StateVariableArraySizeDependency;
const _RecursiveDependencyValuesDependency = class _RecursiveDependencyValuesDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames === void 0) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames is not defined`
      );
    }
    this.startingVariableNames = this.definition.variableNames;
    this.originalVariablesByComponent = true;
    this.includeImmediateValueWithValue = this.definition.includeImmediateValueWithValue;
    this.includeRawValueWithImmediateValue = this.definition.includeRawValueWithImmediateValue;
    this.includeOnlyEssentialValues = this.definition.includeOnlyEssentialValues;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents({ force = false } = {}) {
    this.missingComponents = [];
    this.originalDownstreamVariableNamesByComponent = [];
    let result = await this.getRecursiveDependencyVariables({
      componentIdx: this.componentIdx,
      variableNames: this.startingVariableNames,
      force
    });
    if (!result.success) {
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let downstreamComponentIndices = [];
    let downstreamComponentTypes = [];
    for (const componentIdxStr in result.components) {
      const componentIdx = Number(componentIdxStr);
      if (this.includeOnlyEssentialValues) {
        let essentialVarNames = [];
        let component = this.dependencyHandler._components[componentIdx];
        for (let vName of result.components[componentIdx].variableNames) {
          if (component.state[vName]?.hasEssential) {
            essentialVarNames.push(vName);
          } else if (component.state[vName]?.isArrayEntry) {
            if (component.state[component.state[vName].arrayStateVariable].hasEssential) {
              essentialVarNames.push(vName);
            }
          }
        }
        if (essentialVarNames.length > 0) {
          downstreamComponentIndices.push(componentIdx);
          downstreamComponentTypes.push(
            result.components[componentIdx].componentType
          );
          this.originalDownstreamVariableNamesByComponent.push(
            essentialVarNames
          );
        }
      } else {
        downstreamComponentIndices.push(componentIdx);
        downstreamComponentTypes.push(
          result.components[componentIdx].componentType
        );
        this.originalDownstreamVariableNamesByComponent.push(
          result.components[componentIdx].variableNames
        );
      }
    }
    return {
      success: true,
      downstreamComponentIndices,
      downstreamComponentTypes
    };
  }
  async getRecursiveDependencyVariables({
    componentIdx,
    variableNames,
    force,
    components = {}
  }) {
    let component = this.dependencyHandler._components[componentIdx];
    if (!component) {
      if (!this.missingComponents.includes(componentIdx)) {
        let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx];
        if (!dependenciesMissingComponent) {
          dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx] = [];
        }
        if (!dependenciesMissingComponent.includes(this)) {
          dependenciesMissingComponent.push(this);
        }
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false
      };
    }
    if (this.includeImmediateValueWithValue && variableNames.includes("value") && !variableNames.includes("immediateValue") && "immediateValue" in component.state) {
      variableNames = [...variableNames, "immediateValue"];
    }
    if (this.includeRawValueWithImmediateValue && variableNames.includes("immediateValue") && !variableNames.includes("rawRendererValue") && "rawRendererValue" in component.state) {
      variableNames = [...variableNames, "rawRendererValue"];
    }
    let thisComponentObj = components[componentIdx];
    if (!thisComponentObj) {
      thisComponentObj = components[componentIdx] = {
        componentIdx,
        componentType: component.componentType,
        variableNames: []
      };
    }
    let triggersForComponent = this.dependencyHandler.updateTriggers.dependenciesBasedOnDependenciesOfStateVariables[componentIdx];
    if (!triggersForComponent) {
      triggersForComponent = this.dependencyHandler.updateTriggers.dependenciesBasedOnDependenciesOfStateVariables[componentIdx] = {};
    }
    for (let varName of variableNames) {
      if (!thisComponentObj.variableNames.includes(varName)) {
        thisComponentObj.variableNames.push(varName);
        let triggersForVarName = triggersForComponent[varName];
        if (!triggersForVarName) {
          triggersForVarName = triggersForComponent[varName] = [];
        }
        if (!triggersForVarName.includes(this)) {
          triggersForVarName.push(this);
        }
        let stateVarObj = component.state[varName];
        if (stateVarObj) {
          if (!stateVarObj.isResolved) {
            if (force) {
              await stateVarObj.value;
            } else {
              for (let vName of this.upstreamVariableNames) {
                await this.dependencyHandler.addBlocker({
                  blockerComponentIdx: componentIdx,
                  blockerType: "stateVariable",
                  blockerStateVariable: varName,
                  componentIdxBlocked: this.upstreamComponentIdx,
                  typeBlocked: "recalculateDownstreamComponents",
                  stateVariableBlocked: vName,
                  dependencyBlocked: this.dependencyName
                });
                await this.dependencyHandler.addBlocker({
                  blockerComponentIdx: this.upstreamComponentIdx,
                  blockerType: "recalculateDownstreamComponents",
                  blockerStateVariable: vName,
                  blockerDependency: this.dependencyName,
                  componentIdxBlocked: this.upstreamComponentIdx,
                  typeBlocked: "stateVariable",
                  stateVariableBlocked: vName
                });
              }
              return { success: false };
            }
          }
          let downDeps = this.dependencyHandler.downstreamDependencies[component.componentIdx][varName];
          for (let dependencyName in downDeps) {
            let dep = downDeps[dependencyName];
            if (dep.onlyToSetInInverseDefinition) {
              continue;
            }
            for (let [
              cInd,
              cIdx
            ] of dep.downstreamComponentIndices.entries()) {
              let varNames = [];
              if (dep.originalDownstreamVariableNames.length > 0 || dep.originalVariablesByComponent) {
                varNames = dep.mappedDownstreamVariableNamesByComponent[cInd];
              }
              let result = await this.getRecursiveDependencyVariables({
                componentIdx: cIdx,
                variableNames: varNames,
                force,
                components
              });
              if (!result.success) {
                return { success: false };
              }
            }
          }
        }
      }
    }
    return {
      success: true,
      components
    };
  }
  async getValue({ consumeChanges = true } = {}) {
    this.gettingValue = true;
    this.varsWithUpdatedDeps = {};
    let result;
    let accumulatedVarsWithUpdatedDeps = {};
    let foundNewUpdated = true;
    let changes = {};
    try {
      while (foundNewUpdated) {
        foundNewUpdated = false;
        result = await super.getValue({
          consumeChanges
        });
        if (result.changes.valuesChanged) {
          if (!changes.valuesChanged) {
            changes.valuesChanged = result.changes.valuesChanged;
          } else {
            for (let ind in result.changes.valuesChanged) {
              let changeObj = result.changes.valuesChanged[ind];
              if (!changes.valuesChanged[ind]) {
                changes.valuesChanged[ind] = changeObj;
              } else {
                for (let depName in changeObj) {
                  changes.valuesChanged[ind][depName] = changeObj[depName];
                }
              }
            }
          }
        }
        for (const cIdxStr in this.varsWithUpdatedDeps) {
          let compAccumulated = accumulatedVarsWithUpdatedDeps[cIdxStr];
          if (!compAccumulated) {
            compAccumulated = accumulatedVarsWithUpdatedDeps[cIdxStr] = [];
          }
          for (let vName of this.varsWithUpdatedDeps[cIdxStr]) {
            if (!compAccumulated.includes(vName)) {
              compAccumulated.push(vName);
              foundNewUpdated = true;
            }
          }
        }
        if (foundNewUpdated) {
          this.varsWithUpdatedDeps = {};
          await this.recalculateDownstreamComponents();
        }
      }
    } finally {
      this.gettingValue = false;
    }
    result.changes = changes;
    return result;
  }
  deleteFromUpdateTriggers() {
    for (let componentIdx of this.missingComponents) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_RecursiveDependencyValuesDependency.dependencyType = "recursiveDependencyValues";
let RecursiveDependencyValuesDependency = _RecursiveDependencyValuesDependency;
const _ComponentIdentityDependency = class _ComponentIdentityDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    this.returnSingleComponent = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_ComponentIdentityDependency.dependencyType = "componentIdentity";
let ComponentIdentityDependency = _ComponentIdentityDependency;
const _AttributeComponentDependency = class _AttributeComponentDependency extends Dependency {
  setUpParameters() {
    if (this.definition.parentIdx != void 0) {
      this.parentIdx = this.definition.parentIdx;
      this.specifiedComponentName = this.parentIdx;
    } else {
      this.parentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.attributeName = this.definition.attributeName;
    this.returnSingleComponent = true;
    this.dontRecurseToShadows = this.definition.dontRecurseToShadows;
    this.dontRecurseToShadowsIfHaveAttribute = this.definition.dontRecurseToShadowsIfHaveAttribute;
  }
  async determineDownstreamComponents() {
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.parentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let attribute = parent.attributes[this.attributeName];
    if (attribute?.component) {
      if (attribute.component.shadows) {
        if (this.dontRecurseToShadows) {
          return {
            success: true,
            downstreamComponentIndices: [],
            downstreamComponentTypes: []
          };
        } else if (this.dontRecurseToShadowsIfHaveAttribute) {
          let otherAttribute = parent.attributes[this.dontRecurseToShadowsIfHaveAttribute];
          if (otherAttribute?.component && !otherAttribute.component.shadows) {
            return {
              success: true,
              downstreamComponentIndices: [],
              downstreamComponentTypes: []
            };
          }
        }
      }
      return {
        success: true,
        downstreamComponentIndices: [attribute.component.componentIdx],
        downstreamComponentTypes: [attribute.component.componentType]
      };
    }
    if (this.dontRecurseToShadows) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let comp = parent;
    while (comp.shadows) {
      let shadows = comp.shadows;
      let propVariable = comp.shadows.propVariable;
      let fromImplicitProp = comp.doenetAttributes.fromImplicitProp;
      if (this.dontRecurseToShadowsIfHaveAttribute && comp.attributes[this.dontRecurseToShadowsIfHaveAttribute]) {
        break;
      }
      comp = this.dependencyHandler._components[shadows.componentIdx];
      if (!comp) {
        break;
      }
      if (propVariable && !(fromImplicitProp && comp.constructor.implicitPropReturnsSameType)) {
        if (!(comp.state[propVariable]?.shadowingInstructions?.attributesToShadow?.includes(
          this.attributeName
        ) || comp.constructor.createAttributesObject()[this.attributeName]?.propagateToProps)) {
          break;
        }
      }
      attribute = comp.attributes[this.attributeName];
      if (attribute?.component) {
        return {
          success: true,
          downstreamComponentIndices: [
            attribute.component.componentIdx
          ],
          downstreamComponentTypes: [
            attribute.component.componentType
          ]
        };
      }
    }
    return {
      success: true,
      downstreamComponentIndices: [],
      downstreamComponentTypes: []
    };
  }
  async getValue({ verbose, consumeChanges = true } = {}) {
    let result = await this.getValueNoProxy({
      verbose,
      consumeChanges
    });
    return result;
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_AttributeComponentDependency.dependencyType = "attributeComponent";
let AttributeComponentDependency = _AttributeComponentDependency;
const _ChildDependency = class _ChildDependency extends Dependency {
  setUpParameters() {
    if (this.definition.parentIdx != void 0) {
      this.parentIdx = this.definition.parentIdx;
      this.specifiedComponentName = this.parentIdx;
    } else {
      this.parentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.includeAllChildren = this.definition.includeAllChildren;
    this.childGroups = this.definition.childGroups;
    if (!this.includeAllChildren && !Array.isArray(this.childGroups)) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: childGroups must be an array`
      );
    }
    if (this.definition.childIndices !== void 0) {
      this.childIndices = this.definition.childIndices.map(
        (x2) => Number(x2)
      );
    }
    this.skipComponentIndices = this.definition.skipComponentIndices;
    this.skipPlaceholders = this.definition.skipPlaceholders;
    this.proceedIfAllChildrenNotMatched = this.definition.proceedIfAllChildrenNotMatched;
    this.dontRecurseToShadows = this.definition.dontRecurseToShadows;
  }
  async determineDownstreamComponents() {
    if (this.downstreamPrimitives) {
      this.previousDownstreamPrimitives = [...this.downstreamPrimitives];
    } else {
      this.previousDownstreamPrimitives = [];
    }
    this.downstreamPrimitives = [];
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.parentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let childDependencies = this.dependencyHandler.updateTriggers.childDependenciesByParent[this.parentIdx];
    if (!childDependencies) {
      childDependencies = this.dependencyHandler.updateTriggers.childDependenciesByParent[this.parentIdx] = [];
    }
    if (!childDependencies.includes(this)) {
      childDependencies.push(this);
    }
    let activeChildrenIndices = this.includeAllChildren ? [...parent.activeChildren.keys()] : parent.returnMatchedChildIndices(this.childGroups);
    if (activeChildrenIndices === void 0) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: childGroups ${this.childGroups} does not exist.`
      );
    }
    if (this.childIndices) {
      activeChildrenIndices = activeChildrenIndices.filter(
        (x2, i2) => this.childIndices.includes(i2)
      );
    }
    if (!parent.childrenMatched && !this.proceedIfAllChildrenNotMatched) {
      let canProceedWithPlaceholders = false;
      if (parent.childrenMatchedWithPlaceholders) {
        if (this.skipPlaceholders) {
          activeChildrenIndices = activeChildrenIndices.filter(
            (x2) => !parent.placeholderActiveChildrenIndices.includes(
              x2
            )
          );
        }
        if (this.skipComponentIndices && this.originalDownstreamVariableNames.length === 0) {
          canProceedWithPlaceholders = true;
        } else {
          canProceedWithPlaceholders = activeChildrenIndices.every(
            (x2) => !parent.placeholderActiveChildrenIndices.includes(
              x2
            )
          );
        }
      }
      if (!canProceedWithPlaceholders) {
        let haveCompositesNotReady = parent.unexpandedCompositesNotReady.length > 0;
        if (!haveCompositesNotReady && parent.unexpandedCompositesReady.length > 0) {
          for (let varName of this.upstreamVariableNames) {
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.parentIdx,
              blockerType: "childMatches",
              blockerStateVariable: varName,
              // add so that can have different blockers of child logic
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "recalculateDownstreamComponents",
              stateVariableBlocked: varName,
              dependencyBlocked: this.dependencyName
            });
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.upstreamComponentIdx,
              blockerType: "recalculateDownstreamComponents",
              blockerStateVariable: varName,
              blockerDependency: this.dependencyName,
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName
            });
          }
          return {
            success: false,
            downstreamComponentIndices: [],
            downstreamComponentTypes: []
          };
        }
        if (haveCompositesNotReady) {
          for (let varName of this.upstreamVariableNames) {
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.parentIdx,
              blockerType: "childMatches",
              blockerStateVariable: varName,
              // add so that can have different blockers of child logic
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "recalculateDownstreamComponents",
              stateVariableBlocked: varName,
              dependencyBlocked: this.dependencyName
            });
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.upstreamComponentIdx,
              blockerType: "recalculateDownstreamComponents",
              blockerStateVariable: varName,
              blockerDependency: this.dependencyName,
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName
            });
          }
          let compositesBlockingWithComponentType = [];
          let compositesBlockingWithoutComponentType = [];
          for (let compositeNotReady of parent.unexpandedCompositesNotReady) {
            if (parent.childrenMatchedWithPlaceholders) {
              let inds = parent.placeholderActiveChildrenIndicesByComposite[compositeNotReady];
              if (inds.every(
                (x2) => !activeChildrenIndices.includes(x2)
              )) {
                continue;
              }
            }
            let compositeComp = this.dependencyHandler._components[compositeNotReady];
            if (compositeComp.attributes.createComponentOfType?.primitive) {
              compositesBlockingWithComponentType.push(
                compositeNotReady
              );
            } else {
              compositesBlockingWithoutComponentType.push(
                compositeNotReady
              );
            }
          }
          let compositesToAddBlockers = compositesBlockingWithoutComponentType;
          if (compositesToAddBlockers.length === 0) {
            compositesToAddBlockers = compositesBlockingWithComponentType;
          }
          for (let compositeNotReady of compositesToAddBlockers) {
            for (let varName of this.upstreamVariableNames) {
              await this.dependencyHandler.addBlocker({
                blockerComponentIdx: compositeNotReady,
                blockerType: "stateVariable",
                blockerStateVariable: "readyToExpandWhenResolved",
                componentIdxBlocked: this.upstreamComponentIdx,
                typeBlocked: "childMatches",
                stateVariableBlocked: varName
                // add to just block for this variable
              });
            }
          }
          return {
            success: false,
            downstreamComponentIndices: [],
            downstreamComponentTypes: []
          };
        }
      }
    }
    let activeChildrenMatched = activeChildrenIndices.map(
      (x2) => parent.activeChildren[x2]
    );
    this.compositeReplacementRange = [];
    if (this.dontRecurseToShadows) {
      let allActiveChildrenMatched = activeChildrenMatched;
      let allActiveChildrenIndices = activeChildrenIndices;
      activeChildrenMatched = [];
      activeChildrenIndices = [];
      for (let [ind, child] of allActiveChildrenMatched.entries()) {
        if (!(child.shadows && child.shadows.compositeIdx === parent?.shadows?.compositeIdx)) {
          activeChildrenMatched.push(child);
          activeChildrenIndices.push(allActiveChildrenIndices[ind]);
        }
      }
    } else {
      if (parent.compositeReplacementActiveRange && activeChildrenMatched.length > 0) {
        for (let compositeInfo of parent.compositeReplacementActiveRange) {
          let translatedFirstInd, translatedLastInd;
          let translatedPotentialListComponents = [];
          for (let [
            ind,
            activeInd
          ] of activeChildrenIndices.entries()) {
            if (compositeInfo.firstInd > activeInd) {
              continue;
            }
            if (compositeInfo.lastInd < activeInd) {
              if (compositeInfo.lastInd === compositeInfo.firstInd - 1) {
                translatedFirstInd = ind;
                translatedLastInd = ind - 1;
              }
              break;
            }
            if (translatedFirstInd === void 0) {
              translatedFirstInd = ind;
            }
            translatedLastInd = ind;
            translatedPotentialListComponents.push(
              compositeInfo.potentialListComponents[activeInd - compositeInfo.firstInd]
            );
          }
          if (translatedLastInd !== void 0) {
            this.compositeReplacementRange.push({
              compositeIdx: compositeInfo.compositeIdx,
              extendIdx: compositeInfo.extendIdx,
              unresolvedPath: compositeInfo.unresolvedPath,
              firstInd: translatedFirstInd,
              lastInd: translatedLastInd,
              asList: compositeInfo.asList,
              potentialListComponents: translatedPotentialListComponents
            });
          }
        }
      }
      for (let child of activeChildrenMatched) {
        let childSource = child;
        let parentSource = parent;
        while (childSource?.shadows && childSource.shadows.compositeIdx === parentSource?.shadows?.compositeIdx) {
          parentSource = this.dependencyHandler._components[parentSource.shadows.componentIdx];
          childSource = this.dependencyHandler._components[childSource.shadows.componentIdx];
        }
      }
    }
    this.activeChildrenIndices = activeChildrenIndices;
    let downstreamComponentIndices = [];
    let downstreamComponentTypes = [];
    for (let [ind, child] of activeChildrenMatched.entries()) {
      if (typeof child !== "object") {
        this.downstreamPrimitives.push(child);
        continue;
      }
      this.downstreamPrimitives.push(null);
      downstreamComponentIndices.push(
        child.componentIdx ? child.componentIdx : `__placeholder_${ind}`
      );
      downstreamComponentTypes.push(child.componentType);
    }
    if (this.originalDownstreamVariableNames.includes("hidden") && this.downstreamPrimitives.find((x2) => x2 !== null)) {
      for (let compositeObj of this.compositeReplacementRange) {
        downstreamComponentIndices.push(compositeObj.compositeIdx);
        downstreamComponentTypes.push(
          this.dependencyHandler._components[compositeObj.compositeIdx].componentType
        );
        this.addedCompositeHiddenDependency = true;
      }
      if (this.addedCompositeHiddenDependency && this.originalDownstreamVariableNames.length > 1) {
        this.variablesOptional = true;
      }
    }
    return {
      success: true,
      downstreamComponentIndices,
      downstreamComponentTypes
    };
  }
  async getValue({ verbose, consumeChanges = true } = {}) {
    let result = await this.getValueNoProxy({
      verbose,
      consumeChanges
    });
    let compositeReplacementRange = this.compositeReplacementRange;
    if (this.addedCompositeHiddenDependency) {
      let nDepsAdded = compositeReplacementRange.length;
      let extraDependencies = result.value.splice(
        result.value.length - nDepsAdded,
        nDepsAdded
      );
      compositeReplacementRange = JSON.parse(
        JSON.stringify(compositeReplacementRange)
      );
      for (let [ind, range] of compositeReplacementRange.entries()) {
        range.hidden = extraDependencies[ind].stateValues.hidden;
      }
    }
    let resultValueWithPrimitives = [];
    let resultInd = 0;
    for (let primitiveOrNull of this.downstreamPrimitives) {
      if (primitiveOrNull === null) {
        resultValueWithPrimitives.push(result.value[resultInd]);
        resultInd++;
      } else {
        resultValueWithPrimitives.push(primitiveOrNull);
      }
    }
    resultValueWithPrimitives.compositeReplacementRange = compositeReplacementRange;
    result.value = resultValueWithPrimitives;
    if (this.downstreamPrimitives.length !== this.previousDownstreamPrimitives.length || this.downstreamPrimitives.some(
      (v2, i2) => v2 !== this.previousDownstreamPrimitives[i2]
    )) {
      result.changes.componentIdentitiesChanged = true;
      if (consumeChanges) {
        this.previousDownstreamPrimitives = [
          ...this.downstreamPrimitives
        ];
      }
    }
    return result;
  }
  deleteFromUpdateTriggers() {
    let childDeps = this.dependencyHandler.updateTriggers.childDependenciesByParent[this.parentIdx];
    if (childDeps) {
      let ind = childDeps.indexOf(this);
      if (ind !== -1) {
        childDeps.splice(ind, 1);
      }
    }
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_ChildDependency.dependencyType = "child";
let ChildDependency = _ChildDependency;
const _DescendantDependency = class _DescendantDependency extends Dependency {
  setUpParameters() {
    if (this.definition.ancestorIdx != void 0) {
      this.ancestorIdx = this.definition.ancestorIdx;
      this.specifiedComponentName = this.ancestorIdx;
    } else {
      this.ancestorIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.componentTypes = this.definition.componentTypes;
    this.recurseToMatchedChildren = this.definition.recurseToMatchedChildren;
    this.useReplacementsForComposites = this.definition.useReplacementsForComposites;
    this.includeNonActiveChildren = this.definition.includeNonActiveChildren;
    this.includeAttributeChildren = this.definition.includeAttributeChildren;
    this.skipOverAdapters = this.definition.skipOverAdapters;
    this.ignoreReplacementsOfMatchedComposites = this.definition.ignoreReplacementsOfMatchedComposites;
    this.ignoreReplacementsOfEncounteredComposites = this.definition.ignoreReplacementsOfEncounteredComposites;
    if (this.definition.sourceIndex !== null && this.definition.sourceIndex !== void 0) {
      if (Number.isInteger(this.definition.sourceIndex)) {
        this.sourceIndex = this.definition.sourceIndex;
      } else {
        this.sourceIndex = NaN;
      }
    }
  }
  async determineDownstreamComponents() {
    let ancestor = this.dependencyHandler._components[this.ancestorIdx];
    if (!ancestor) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.ancestorIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.ancestorIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.ancestorIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let descendantDependencies = this.dependencyHandler.updateTriggers.descendantDependenciesByAncestor[this.ancestorIdx];
    if (!descendantDependencies) {
      descendantDependencies = this.dependencyHandler.updateTriggers.descendantDependenciesByAncestor[this.ancestorIdx] = [];
    }
    if (!descendantDependencies.includes(this)) {
      descendantDependencies.push(this);
    }
    let result = this.gatherUnexpandedComposites(ancestor);
    if (result.haveCompositesNotReady || result.haveUnexpandedCompositeReady) {
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
        for (const parentIdxStr in result.unexpandedCompositesReadyByParentName) {
          const parentIdx = Number(parentIdxStr);
          await this.dependencyHandler.addBlocker({
            blockerComponentIdx: parentIdx,
            blockerType: "childMatches",
            blockerStateVariable: varName,
            componentIdxBlocked: this.upstreamComponentIdx,
            typeBlocked: "recalculateDownstreamComponents",
            stateVariableBlocked: varName,
            dependencyBlocked: this.dependencyName
          });
        }
        for (const parentIdxStr in result.unexpandedCompositesNotReadyByParentName) {
          const parentIdx = Number(parentIdxStr);
          await this.dependencyHandler.addBlocker({
            blockerComponentIdx: parentIdx,
            blockerType: "childMatches",
            blockerStateVariable: varName,
            componentIdxBlocked: this.upstreamComponentIdx,
            typeBlocked: "recalculateDownstreamComponents",
            stateVariableBlocked: varName,
            dependencyBlocked: this.dependencyName
          });
        }
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let descendants = gatherDescendants({
      ancestor,
      descendantTypes: this.componentTypes,
      recurseToMatchedChildren: this.recurseToMatchedChildren,
      useReplacementsForComposites: this.useReplacementsForComposites,
      includeNonActiveChildren: this.includeNonActiveChildren,
      skipOverAdapters: this.skipOverAdapters,
      ignoreReplacementsOfMatchedComposites: this.ignoreReplacementsOfMatchedComposites,
      ignoreReplacementsOfEncounteredComposites: this.ignoreReplacementsOfEncounteredComposites,
      componentInfoObjects: this.dependencyHandler.componentInfoObjects
    });
    if (this.sourceIndex !== void 0) {
      let theDescendant = descendants[this.sourceIndex - 1];
      if (theDescendant) {
        descendants = [theDescendant];
      } else {
        descendants = [];
      }
    }
    return {
      success: true,
      downstreamComponentIndices: descendants.map(
        (x2) => x2.componentIdx
      ),
      downstreamComponentTypes: descendants.map(
        (x2) => x2.componentType
      )
    };
  }
  gatherUnexpandedComposites(component) {
    let unexpandedCompositesReadyByParentName = {};
    let unexpandedCompositesNotReadyByParentName = {};
    let haveUnexpandedCompositeReady = false;
    let haveCompositesNotReady = false;
    let placeholdersOKForMatchedDescendants = this.skipComponentIndices && this.originalDownstreamVariableNames.length === 0;
    if (!component.matchedCompositeChildren) {
      if (component.matchedCompositeChildrenWithPlaceholders) {
        if (component.unexpandedCompositesReady.length > 0) {
          let unexpandedReady = this.unexpandedCompositesAdjustedForPlacedholders(
            component.unexpandedCompositesReady,
            placeholdersOKForMatchedDescendants
          );
          if (unexpandedReady.length > 0) {
            unexpandedCompositesReadyByParentName[component.componentIdx] = unexpandedReady;
            haveUnexpandedCompositeReady = true;
          }
        }
        if (component.unexpandedCompositesNotReady.length > 0) {
          let unexpandedNotReady = this.unexpandedCompositesAdjustedForPlacedholders(
            component.unexpandedCompositesNotReady,
            placeholdersOKForMatchedDescendants
          );
          if (unexpandedNotReady.length > 0) {
            unexpandedCompositesNotReadyByParentName[component.componentIdx] = unexpandedNotReady;
            haveCompositesNotReady = true;
          }
        }
      } else {
        if (component.unexpandedCompositesReady.length > 0) {
          unexpandedCompositesReadyByParentName[component.componentIdx] = component.unexpandedCompositesReady;
          haveUnexpandedCompositeReady = true;
        }
        if (component.unexpandedCompositesNotReady.length > 0) {
          unexpandedCompositesNotReadyByParentName[component.componentIdx] = component.unexpandedCompositesNotReady;
          haveCompositesNotReady = true;
        }
      }
    }
    for (const childIdxStr in component.allChildren) {
      let child = component.allChildren[childIdxStr].component;
      if (typeof child === "object") {
        let result = this.gatherUnexpandedComposites(child);
        if (result.haveUnexpandedCompositeReady) {
          Object.assign(
            unexpandedCompositesReadyByParentName,
            result.unexpandedCompositesReadyByParentName
          );
          haveUnexpandedCompositeReady = true;
        }
        if (result.haveCompositesNotReady) {
          Object.assign(
            unexpandedCompositesNotReadyByParentName,
            result.unexpandedCompositesNotReadyByParentName
          );
          haveCompositesNotReady = true;
        }
      }
    }
    return {
      unexpandedCompositesReadyByParentName,
      haveUnexpandedCompositeReady,
      unexpandedCompositesNotReadyByParentName,
      haveCompositesNotReady
    };
  }
  unexpandedCompositesAdjustedForPlacedholders(unexpandedComposites, placeholdersOKForMatchedDescendants) {
    let adjustedUnexpanded = [];
    for (let compositeIdx of unexpandedComposites) {
      let composite = this.dependencyHandler._components[compositeIdx];
      if (composite.attributes.createComponentOfType) {
        let placeholderType = this.dependencyHandler.componentInfoObjects.componentTypeLowerCaseMapping[composite.attributes.createComponentOfType.primitive.value.toLowerCase()];
        let matches = this.componentTypes.some(
          (ct2) => this.dependencyHandler.componentInfoObjects.isInheritedComponentType(
            {
              inheritedComponentType: placeholderType,
              baseComponentType: ct2
            }
          )
        );
        if (matches) {
          if (!placeholdersOKForMatchedDescendants) {
            adjustedUnexpanded.push(compositeIdx);
          }
        } else {
          adjustedUnexpanded.push(compositeIdx);
        }
      } else {
        adjustedUnexpanded.push(compositeIdx);
      }
    }
    return adjustedUnexpanded;
  }
  deleteFromUpdateTriggers() {
    let descendantDeps = this.dependencyHandler.updateTriggers.descendantDependenciesByAncestor[this.ancestorIdx];
    if (descendantDeps) {
      let ind = descendantDeps.indexOf(this);
      if (ind !== -1) {
        descendantDeps.splice(ind, 1);
      }
    }
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_DescendantDependency.dependencyType = "descendant";
let DescendantDependency = _DescendantDependency;
const _ParentDependency = class _ParentDependency extends Dependency {
  setUpParameters() {
    if (this.definition.childIdx != void 0) {
      this.childIdx = this.definition.childIdx;
      this.specifiedComponentName = this.childIdx;
    } else {
      this.childIdx = this.upstreamComponentIdx;
    }
    if (!this.definition.variableName) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: must have a variableName`
      );
    } else {
      this.originalDownstreamVariableNames = [
        this.definition.variableName
      ];
    }
    if (this.definition.parentComponentType) {
      this.parentComponentType = this.definition.parentComponentType;
    }
    this.returnSingleVariableValue = true;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents() {
    let child = this.dependencyHandler._components[this.childIdx];
    if (!child) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.childIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.childIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.childIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (child.parentIdx == void 0) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    this.parentIdx = child.parentIdx;
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.parentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (this.parentComponentType && !this.dependencyHandler.componentInfoObjects.isInheritedComponentType(
      {
        inheritedComponentType: parent.componentType,
        baseComponentType: this.parentComponentType
      }
    )) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let parentDependencies = this.dependencyHandler.updateTriggers.parentDependenciesByParent[this.parentIdx];
    if (!parentDependencies) {
      parentDependencies = this.dependencyHandler.updateTriggers.parentDependenciesByParent[this.parentIdx] = [];
    }
    if (!parentDependencies.includes(this)) {
      parentDependencies.push(this);
    }
    return {
      success: true,
      downstreamComponentIndices: [this.parentIdx],
      downstreamComponentTypes: [parent.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    let parentDeps = this.dependencyHandler.updateTriggers.parentDependenciesByParent[this.parentIdx];
    if (parentDeps) {
      let ind = parentDeps.indexOf(this);
      if (ind !== -1) {
        parentDeps.splice(ind, 1);
      }
    }
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent2 = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent2) {
        let ind = dependenciesMissingComponent2.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent2.splice(ind, 1);
        }
      }
    }
    let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
    if (dependenciesMissingComponent) {
      let ind = dependenciesMissingComponent.indexOf(this);
      if (ind !== -1) {
        dependenciesMissingComponent.splice(ind, 1);
      }
    }
  }
};
_ParentDependency.dependencyType = "parentStateVariable";
let ParentDependency = _ParentDependency;
const _ParentIdentityDependency = class _ParentIdentityDependency extends Dependency {
  setUpParameters() {
    if (this.definition.childIdx != void 0) {
      this.childIdx = this.definition.childIdx;
      this.specifiedComponentName = this.childIdx;
    } else {
      this.childIdx = this.upstreamComponentIdx;
    }
    if (this.definition.parentComponentType) {
      this.parentComponentType = this.definition.parentComponentType;
    }
    this.returnSingleComponent = true;
  }
  async determineDownstreamComponents() {
    let child = this.dependencyHandler._components[this.childIdx];
    if (!child) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.childIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.childIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.childIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (child.parentIdx == void 0) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    this.parentIdx = child.parentIdx;
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.parentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (this.parentComponentType && !this.dependencyHandler.componentInfoObjects.isInheritedComponentType(
      {
        inheritedComponentType: parent.componentType,
        baseComponentType: this.parentComponentType
      }
    )) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let parentDependencies = this.dependencyHandler.updateTriggers.parentDependenciesByParent[this.parentIdx];
    if (!parentDependencies) {
      parentDependencies = this.dependencyHandler.updateTriggers.parentDependenciesByParent[this.parentIdx] = [];
    }
    if (!parentDependencies.includes(this)) {
      parentDependencies.push(this);
    }
    return {
      success: true,
      downstreamComponentIndices: [this.parentIdx],
      downstreamComponentTypes: [parent.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    let parentDeps = this.dependencyHandler.updateTriggers.parentDependenciesByParent[this.parentIdx];
    if (parentDeps) {
      let ind = parentDeps.indexOf(this);
      if (ind !== -1) {
        parentDeps.splice(ind, 1);
      }
    }
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent2 = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent2) {
        let ind = dependenciesMissingComponent2.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent2.splice(ind, 1);
        }
      }
    }
    let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
    if (dependenciesMissingComponent) {
      let ind = dependenciesMissingComponent.indexOf(this);
      if (ind !== -1) {
        dependenciesMissingComponent.splice(ind, 1);
      }
    }
  }
};
_ParentIdentityDependency.dependencyType = "parentIdentity";
let ParentIdentityDependency = _ParentIdentityDependency;
const _AncestorDependency = class _AncestorDependency extends Dependency {
  setUpParameters() {
    if (this.definition.descendantIdx != void 0) {
      this.descendantIdx = this.definition.descendantIdx;
      this.specifiedComponentName = this.descendantIdx;
    } else {
      this.descendantIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.returnSingleComponent = true;
    if (this.definition.componentType) {
      this.componentType = this.definition.componentType;
    }
  }
  async determineDownstreamComponents() {
    let descendant = this.dependencyHandler._components[this.descendantIdx];
    if (!descendant) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.descendantIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.descendantIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.descendantIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (this.dependencyHandler._components[this.dependencyHandler.core.documentIdx] == void 0) {
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.dependencyHandler.core.documentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let ancestorResults = this.findMatchingAncestor(descendant);
    if (ancestorResults.missingComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[ancestorResults.missingComponentName];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[ancestorResults.missingComponentName] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: ancestorResults.missingComponentName,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    for (let ancestorIdx of ancestorResults.ancestorsExamined) {
      let ancestorDependencies = this.dependencyHandler.updateTriggers.ancestorDependenciesByPotentialAncestor[ancestorIdx];
      if (!ancestorDependencies) {
        ancestorDependencies = this.dependencyHandler.updateTriggers.ancestorDependenciesByPotentialAncestor[ancestorIdx] = [];
      }
      if (!ancestorDependencies.includes(this)) {
        ancestorDependencies.push(this);
      }
    }
    this.ancestorResults = ancestorResults;
    if (ancestorResults.ancestorFound) {
      return {
        success: true,
        downstreamComponentIndices: [
          ancestorResults.ancestorFound.componentIdx
        ],
        downstreamComponentTypes: [
          ancestorResults.ancestorFound.componentClass.componentType
        ]
      };
    } else {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
  }
  findMatchingAncestor(descendant) {
    let ancestorsExamined = [];
    if (this.componentType) {
      for (let ancestor of descendant.ancestors) {
        let ancestorComponent = this.dependencyHandler._components[ancestor.componentIdx];
        if (!ancestorComponent) {
          return { missingComponentName: ancestor.componentIdx };
        }
        ancestorsExamined.push(ancestor.componentIdx);
        if (this.dependencyHandler.componentInfoObjects.isInheritedComponentType(
          {
            inheritedComponentType: ancestorComponent.componentType,
            baseComponentType: this.componentType
          }
        )) {
          return {
            ancestorsExamined,
            ancestorFound: ancestor
          };
        }
      }
      return { ancestorsExamined };
    }
    if (this.originalDownstreamVariableNames.length === 0) {
      console.warn(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: must specify componentType or variableNames to find ancestor`
      );
      return { ancestorsExamined };
    }
    let variableNames = this.originalDownstreamVariableNames;
    for (let ancestor of descendant.ancestors) {
      let ancestorComponent = this.dependencyHandler._components[ancestor.componentIdx];
      if (!ancestorComponent) {
        return { missingComponentName: ancestor.componentIdx };
      }
      ancestorsExamined.push(ancestor.componentIdx);
      let foundAllVarNames = true;
      for (let vName of variableNames) {
        if (!(vName in ancestorComponent.state || this.dependencyHandler.core.checkIfArrayEntry({
          stateVariable: vName,
          component: ancestorComponent
        }).isArrayEntry)) {
          foundAllVarNames = false;
          break;
        }
      }
      if (foundAllVarNames) {
        return {
          ancestorsExamined,
          ancestorFound: ancestor
        };
      }
    }
    return { ancestorsExamined };
  }
  deleteFromUpdateTriggers() {
    if (this.ancestorResults) {
      for (let ancestorIdx of this.ancestorResults.ancestorsExamined) {
        let ancestorDeps = this.dependencyHandler.updateTriggers.ancestorDependenciesByPotentialAncestor[ancestorIdx];
        if (ancestorDeps) {
          let ind = ancestorDeps.indexOf(this);
          if (ind !== -1) {
            ancestorDeps.splice(ind, 1);
          }
        }
      }
    }
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
    if (this.ancestorResults?.missingComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.ancestorResults.missingComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_AncestorDependency.dependencyType = "ancestor";
let AncestorDependency = _AncestorDependency;
const _ReplacementDependency = class _ReplacementDependency extends Dependency {
  setUpParameters() {
    if (this.definition.compositeIdx != void 0) {
      this.compositeIdx = this.definition.compositeIdx;
      this.specifiedComponentName = this.compositeIdx;
    } else {
      this.compositeIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.recursive = this.definition.recursive;
    this.recurseNonStandardComposites = this.definition.recurseNonStandardComposites;
    if (this.definition.sourceIndex !== null && this.definition.sourceIndex !== void 0) {
      if (Number.isInteger(this.definition.sourceIndex)) {
        this.sourceIndex = this.definition.sourceIndex;
      } else {
        this.sourceIndex = NaN;
      }
    }
    this.stopIfHaveProp = this.definition.stopIfHaveProp;
    this.includeWithheldReplacements = this.definition.includeWithheldReplacements;
    this.expandReplacements = true;
  }
  async determineDownstreamComponents() {
    if (this.replacementPrimitives) {
      this.previousReplacementPrimitives = [
        ...this.replacementPrimitives
      ];
    } else {
      this.previousReplacementPrimitives = [];
    }
    this.replacementPrimitives = [];
    let composite = this.dependencyHandler._components[this.compositeIdx];
    if (!composite) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.compositeIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.compositeIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.compositeIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (this.stopIfHaveProp) {
      const checkForPublic = this.dependencyHandler.core.matchPublicStateVariables({
        stateVariables: [this.stopIfHaveProp],
        componentClass: composite.constructor
      })[0];
      if (!checkForPublic.startsWith("__not_public_")) {
        this.replacementPrimitives.push(null);
        return {
          success: true,
          downstreamComponentIndices: [composite.componentIdx],
          downstreamComponentTypes: [composite.componentType]
        };
      }
    }
    if (!composite.isExpanded) {
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.compositeIdx,
          blockerType: "expandComposite",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
      }
      if (!composite.state.readyToExpandWhenResolved.isResolved) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.compositeIdx,
          blockerType: "stateVariable",
          blockerStateVariable: "readyToExpandWhenResolved",
          componentIdxBlocked: this.compositeIdx,
          typeBlocked: "expandComposite"
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    this.compositesFound = [this.compositeIdx];
    let replacements = composite.replacements;
    if (!this.includeWithheldReplacements && composite.replacementsToWithhold > 0) {
      replacements = replacements.slice(
        0,
        -composite.replacementsToWithhold
      );
    }
    if (this.recursive) {
      let result = recursivelyReplaceCompositesWithReplacements({
        core: this.dependencyHandler.core,
        replacements,
        recurseNonStandardComposites: this.recurseNonStandardComposites,
        includeWithheldReplacements: this.includeWithheldReplacements,
        stopIfHaveProp: this.stopIfHaveProp
      });
      if (result.unexpandedCompositesNotReady.length > 0 || result.unexpandedCompositesReady.length > 0) {
        for (let varName of this.upstreamVariableNames) {
          await this.dependencyHandler.addBlocker({
            blockerComponentIdx: this.upstreamComponentIdx,
            blockerType: "recalculateDownstreamComponents",
            blockerStateVariable: varName,
            blockerDependency: this.dependencyName,
            componentIdxBlocked: this.upstreamComponentIdx,
            typeBlocked: "stateVariable",
            stateVariableBlocked: varName
          });
          for (let compositeIdx of [
            ...result.unexpandedCompositesReady,
            ...result.unexpandedCompositesNotReady
          ]) {
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: compositeIdx,
              blockerType: "expandComposite",
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "recalculateDownstreamComponents",
              stateVariableBlocked: varName,
              dependencyBlocked: this.dependencyName
            });
          }
        }
        for (let compositeIdx of result.unexpandedCompositesNotReady) {
          await this.dependencyHandler.addBlocker({
            blockerComponentIdx: compositeIdx,
            blockerType: "stateVariable",
            blockerStateVariable: "readyToExpandWhenResolved",
            componentIdxBlocked: compositeIdx,
            typeBlocked: "expandComposite"
          });
        }
        return {
          success: false,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
      replacements = result.newReplacements;
      this.compositesFound.push(...result.compositesFound);
    }
    for (let cIdx of this.compositesFound) {
      let replacementDependencies = this.dependencyHandler.updateTriggers.replacementDependenciesByComposite[cIdx];
      if (!replacementDependencies) {
        replacementDependencies = this.dependencyHandler.updateTriggers.replacementDependenciesByComposite[cIdx] = [];
      }
      if (!replacementDependencies.includes(this)) {
        replacementDependencies.push(this);
      }
    }
    if (this.sourceIndex !== void 0) {
      let nonBlankStringReplacements = replacements.filter(
        (x2) => typeof x2 !== "string" || x2.trim() !== ""
      );
      let theReplacement = nonBlankStringReplacements[this.sourceIndex - 1];
      if (theReplacement && typeof theReplacement !== "string") {
        replacements = [theReplacement];
      } else {
        replacements = [];
      }
    }
    let downstreamComponentIndices = [];
    let downstreamComponentTypes = [];
    for (let repl of replacements) {
      if (typeof repl !== "object") {
        this.replacementPrimitives.push(repl);
        continue;
      }
      this.replacementPrimitives.push(null);
      downstreamComponentIndices.push(repl.componentIdx);
      downstreamComponentTypes.push(repl.componentType);
    }
    return {
      success: true,
      downstreamComponentIndices,
      downstreamComponentTypes
    };
  }
  async getValue({ verbose, consumeChanges = true } = {}) {
    let result = await this.getValueNoProxy({
      verbose,
      consumeChanges
    });
    let resultValueWithPrimitives = [];
    let resultInd = 0;
    for (let primitiveOrNull of this.replacementPrimitives) {
      if (primitiveOrNull === null) {
        resultValueWithPrimitives.push(result.value[resultInd]);
        resultInd++;
      } else {
        resultValueWithPrimitives.push(primitiveOrNull);
      }
    }
    result.value = resultValueWithPrimitives;
    if (this.replacementPrimitives.length !== this.previousReplacementPrimitives.length || this.replacementPrimitives.some(
      (v2, i2) => v2 !== this.previousReplacementPrimitives[i2]
    )) {
      result.changes.componentIdentitiesChanged = true;
      if (consumeChanges) {
        this.previousReplacementPrimitives = [
          ...this.replacementPrimitives
        ];
      }
    }
    return result;
  }
  deleteFromUpdateTriggers() {
    if (this.compositesFound) {
      for (let compositeIdx of this.compositesFound) {
        let replacementDeps = this.dependencyHandler.updateTriggers.replacementDependenciesByComposite[compositeIdx];
        if (replacementDeps) {
          let ind = replacementDeps.indexOf(this);
          if (ind !== -1) {
            replacementDeps.splice(ind, 1);
          }
        }
      }
    }
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_ReplacementDependency.dependencyType = "replacement";
let ReplacementDependency = _ReplacementDependency;
const _RefResolutionIndexDependencies = class _RefResolutionIndexDependencies extends Dependency {
  setUpParameters() {
    if (this.definition.compositeIdx != void 0) {
      this.compositeIdx = this.definition.compositeIdx;
      this.specifiedComponentName = this.compositeIdx;
    } else {
      this.compositeIdx = this.upstreamComponentIdx;
    }
    this.missingComponentBlockers = [];
  }
  async determineDownstreamComponents() {
    this.compositeReplacementDependencies = [];
    let composite = this.dependencyHandler._components[this.compositeIdx];
    if (!composite) {
      this.addBlockerUpdateTriggerForMissingComponent(this.compositeIdx);
      this.missingComponentBlockers.push(this.compositeIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    this.componentList = [];
    const gatherResult = await this.gatherComponentsInPath(
      composite.refResolution.originalPath
    );
    if (gatherResult.success) {
      this.componentList = gatherResult.componentList;
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    } else {
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
  }
  // Iterate through the index of all parts of `originalPath`.
  // If encounter unexpanded composites, set up resolve blockers
  // so that this dependency will be resolved again once the composites are expanded.
  // Otherwise, gather all the component indices of the "integer" components into `componentList`.
  // If encountered unexpanded composites, return
  // - success: false
  // If successfully found all integer components return
  // - success: true,
  // - componentList: a list of the component indices of the "integer" components found in the unresolved path
  // Throw an error if an index of unresolved path does not contain either a string or a single integer component
  async gatherComponentsInPath(originalPath) {
    const componentList = [];
    let foundUnexpanded = false;
    for (const path_part of originalPath) {
      for (const index_part of path_part.index) {
        if (typeof index_part.value[0] !== "string") {
          let indexComponent = index_part.value[0];
          const haveComposite = this.dependencyHandler.componentInfoObjects.isCompositeComponent(
            {
              componentType: indexComponent.componentType,
              includeNonStandard: true
            }
          );
          if (haveComposite) {
            if (!indexComponent.isExpanded) {
              this.addBlockerForUnexpandedComposite(
                indexComponent
              );
              foundUnexpanded = true;
            } else {
              this.addUpdateTriggersForCompositeReplacements([
                indexComponent.componentIdx
              ]);
              this.compositeReplacementDependencies.push(
                indexComponent.componentIdx
              );
              if (indexComponent.replacements.length !== 1) {
                throw Error(
                  "Something went wrong as path index is not an integer"
                );
              }
              indexComponent = indexComponent.replacements[0];
            }
          }
          if (!foundUnexpanded && indexComponent.componentType !== "integer") {
            throw Error(
              "Something went wrong as path index is not an integer"
            );
          }
          componentList.push(indexComponent.componentIdx);
        }
      }
    }
    if (foundUnexpanded) {
      return { success: false };
    } else {
      return {
        success: true,
        componentList
      };
    }
  }
  async getValue({ consumeChanges = true } = {}) {
    const result = await super.getValue({ consumeChanges });
    result.value = this.componentList;
    result.changes.componentIdentitiesChanged = true;
    return result;
  }
  deleteFromUpdateTriggers() {
    for (const componentIdx of this.missingComponentBlockers) {
      this.deleteUpdateTriggerForMissingComponent(componentIdx);
    }
    this.deleteUpdateTriggersForCompositeReplacements(
      this.compositeReplacementDependencies
    );
  }
};
_RefResolutionIndexDependencies.dependencyType = "refResolutionIndexDependencies";
let RefResolutionIndexDependencies = _RefResolutionIndexDependencies;
const _RefResolutionDependency = class _RefResolutionDependency extends Dependency {
  setUpParameters() {
    if (this.definition.compositeIdx != void 0) {
      this.compositeIdx = this.definition.compositeIdx;
      this.specifiedComponentName = this.compositeIdx;
    } else {
      this.compositeIdx = this.upstreamComponentIdx;
    }
    this.indexDependencyValues = this.definition.indexDependencyValues;
    this.missingComponentBlockers = [];
  }
  async determineDownstreamComponents({ force = false } = {}) {
    this.compositeReplacementDependencies = [];
    let composite = this.dependencyHandler._components[this.compositeIdx];
    if (!composite) {
      this.addBlockerUpdateTriggerForMissingComponent(this.compositeIdx);
      this.missingComponentBlockers.push(this.compositeIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let nodeIdx = composite.refResolution.nodeIdx;
    const componentCreatingExtendIdx = this.dependencyHandler.core.createComponentIdxMapping[nodeIdx];
    if (componentCreatingExtendIdx != null) {
      const compositeCreating = this.dependencyHandler._components[componentCreatingExtendIdx];
      if (!compositeCreating) {
        this.addUpdateTriggerForMissingComponent(
          componentCreatingExtendIdx
        );
        this.missingComponentBlockers.push(componentCreatingExtendIdx);
        return {
          success: false,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
      if (!compositeCreating.isExpanded) {
        this.addBlockerForUnexpandedComposite(compositeCreating);
        return {
          success: false,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
      this.compositeReplacementDependencies.push(
        compositeCreating.componentIdx
      );
      this.addUpdateTriggersForCompositeReplacements([
        compositeCreating.componentIdx
      ]);
    }
    const resolveComponentResult = await this.resolveComponentsInPathIndices(
      composite.refResolution.originalPath,
      force
    );
    if (!resolveComponentResult.success) {
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    this.originalPath = resolveComponentResult.path;
    const refComponent = this.dependencyHandler._components[nodeIdx];
    if (!refComponent) {
      this.addUpdateTriggerForMissingComponent(nodeIdx);
      this.missingComponentBlockers.push(nodeIdx);
      this.extendIdx = -1;
      this.unresolvedPath = this.originalPath;
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    const haveComposite = this.dependencyHandler.componentInfoObjects.isCompositeComponent({
      componentType: refComponent.componentType,
      includeNonStandard: true
    });
    if (haveComposite) {
      if (!refComponent.isExpanded) {
        this.addBlockerForUnexpandedComposite(refComponent);
        return {
          success: false,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
      this.compositeReplacementDependencies.push(
        refComponent.componentIdx
      );
      this.addUpdateTriggersForCompositeReplacements([
        refComponent.componentIdx
      ]);
    }
    let refResolution;
    const getDoenetMLStringForReference = () => {
      const originalPath = composite.refResolution.originalPath;
      const startOffset = originalPath[0].position?.start.offset;
      const endOffset = originalPath[originalPath.length - 1].position?.end.offset;
      const sourceDoc = originalPath[0].sourceDoc ?? 0;
      let doenetMLString = "";
      if (startOffset != void 0 && endOffset != void 0) {
        doenetMLString = this.dependencyHandler.core.allDoenetMLs?.[sourceDoc]?.substring(startOffset, endOffset) ?? "";
      }
      return doenetMLString;
    };
    const skip_parent_search = resolveComponentResult.path[0].name === "";
    try {
      refResolution = this.dependencyHandler.core.resolvePath(
        { path: resolveComponentResult.path },
        composite.refResolution.nodesInResolvedPath[0],
        skip_parent_search
      );
    } catch (e3) {
      if (e3 === "NonUniqueReferent" || e3 === "NoReferent") {
        const referenceText = getDoenetMLStringForReference();
        const message = e3 === "NonUniqueReferent" ? `Multiple referents found for reference: \`$${referenceText}\`` : `No referent found for reference: \`$${referenceText}\``;
        this.dependencyHandler.core.addDiagnostic({
          type: "warning",
          message,
          position: composite.position,
          sourceDoc: composite.sourceDoc
        });
        this.extendIdx = -1;
        this.unresolvedPath = this.originalPath;
        return {
          success: true,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      } else {
        throw e3;
      }
    }
    this.extendIdx = refResolution.nodeIdx;
    this.unresolvedPath = refResolution.unresolvedPath;
    for (const idx of refResolution.nodesInResolvedPath) {
      const componentInvolved = this.dependencyHandler._components[idx];
      if (idx !== this.compositeIdx && componentInvolved && this.dependencyHandler.componentInfoObjects.isCompositeComponent(
        {
          componentType: componentInvolved.componentType,
          includeNonStandard: true
        }
      )) {
        this.compositeReplacementDependencies.push(
          componentInvolved.componentIdx
        );
        this.addUpdateTriggersForCompositeReplacements([
          componentInvolved.componentIdx
        ]);
      }
    }
    if (refResolution.unresolvedPath === null) {
      return this.foundExtend();
    }
    if (refResolution.unresolvedPath[0].name !== "") {
      return this.foundExtend();
    }
    nodeIdx = refResolution.nodeIdx;
    const newRefComponent = this.dependencyHandler._components[nodeIdx];
    if (!newRefComponent) {
      this.addUpdateTriggerForMissingComponent(nodeIdx);
      this.missingComponentBlockers.push(nodeIdx);
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (this.dependencyHandler.componentInfoObjects.isCompositeComponent({
      componentType: newRefComponent.componentType,
      includeNonStandard: true
    })) {
      if (!newRefComponent.isExpanded) {
        this.addBlockerForUnexpandedComposite(newRefComponent);
        return {
          success: false,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
      const referenceText = getDoenetMLStringForReference();
      this.dependencyHandler.core.addDiagnostic({
        type: "warning",
        message: `No referent found for reference: \`$${referenceText}\``,
        position: composite.position,
        sourceDoc: composite.sourceDoc
      });
      this.compositeReplacementDependencies.push(
        newRefComponent.componentIdx
      );
      this.addUpdateTriggersForCompositeReplacements([
        newRefComponent.componentIdx
      ]);
    }
    return this.foundExtend();
  }
  /**
   * Given that we've successfully resolved the reference
   * and set `this.extendIdx` to the resulting node,
   * finish the determination of downstream components by
   * - checking if the component `this.extendIdx` exist and setting up a blocker if it doesn't,
   * - setting the downstream component data to that component if it does exist.
   */
  foundExtend() {
    let extendedComponent = this.dependencyHandler._components[this.extendIdx];
    if (!extendedComponent) {
      this.addUpdateTriggerForMissingComponent(this.extendIdx);
      this.missingComponentBlockers.push(this.extendIdx);
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (extendedComponent.constructor.resolveToParent) {
      this.extendIdx = extendedComponent.parentIdx;
      extendedComponent = this.dependencyHandler._components[this.extendIdx];
      if (!extendedComponent) {
        this.addUpdateTriggerForMissingComponent(this.extendIdx);
        this.missingComponentBlockers.push(this.extendIdx);
        return {
          success: true,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      }
    }
    return {
      success: true,
      downstreamComponentIndices: [this.extendIdx],
      downstreamComponentTypes: [extendedComponent.componentType]
    };
  }
  /**
   * Iterate through the index of all parts of `path`.
   * If any component is found, it must be an "integer".
   * Resolve its `value` state variable, which should be an integer,
   * and use its string value instead of the component.
   *
   * Note: we use strings rather than numbers for the literal indices
   * so that the unresolved path follows the `FlatPathPart` assumed by the resolver.
   */
  async resolveComponentsInPathIndices(path, _force) {
    const pathWithResolvedIndexComponents = [];
    for (const path_part of path) {
      let index2 = [];
      for (const index_part of path_part.index) {
        if (typeof index_part.value[0] === "string") {
          index2.push(index_part);
        } else {
          let indexComponent = index_part.value[0];
          const haveComposite = this.dependencyHandler.componentInfoObjects.isCompositeComponent(
            {
              componentType: indexComponent.componentType,
              includeNonStandard: true
            }
          );
          if (haveComposite) {
            if (!indexComponent.isExpanded) {
              this.addBlockerForUnexpandedComposite(
                indexComponent
              );
              return {
                success: false
              };
            }
            this.addUpdateTriggersForCompositeReplacements([
              indexComponent.componentIdx
            ]);
            this.compositeReplacementDependencies.push(
              indexComponent.componentIdx
            );
            if (indexComponent.replacements.length !== 1) {
              throw Error(
                "Something went wrong as path index is not an integer"
              );
            }
            indexComponent = indexComponent.replacements[0];
          }
          if (indexComponent.componentType !== "integer") {
            throw Error(
              "Something went wrong as path index is not an integer"
            );
          }
          index2.push({
            value: [
              this.indexDependencyValues[indexComponent.componentIdx].toString()
            ],
            position: index_part.position,
            sourceDoc: index_part.sourceDoc
          });
        }
      }
      pathWithResolvedIndexComponents.push({
        name: path_part.name,
        index: index2,
        position: path_part.position,
        sourceDoc: path_part.sourceDoc
      });
    }
    return {
      success: true,
      path: pathWithResolvedIndexComponents
    };
  }
  async getValue({ consumeChanges = true } = {}) {
    const result = await super.getValue({ consumeChanges });
    result.value = {
      extendIdx: this.extendIdx ?? -1,
      unresolvedPath: this.unresolvedPath,
      originalPath: this.originalPath
    };
    return result;
  }
  deleteFromUpdateTriggers() {
    for (const componentIdx of this.missingComponentBlockers) {
      this.deleteUpdateTriggerForMissingComponent(componentIdx);
    }
    this.deleteUpdateTriggersForCompositeReplacements(
      this.compositeReplacementDependencies
    );
  }
};
_RefResolutionDependency.dependencyType = "refResolution";
let RefResolutionDependency = _RefResolutionDependency;
const _AttributeRefResolutions = class _AttributeRefResolutions extends Dependency {
  setUpParameters() {
    if (this.definition.parentIdx != void 0) {
      this.parentIdx = this.definition.parentIdx;
      this.specifiedComponentName = this.parentIdx;
    } else {
      this.parentIdx = this.upstreamComponentIdx;
    }
    this.attributeName = this.definition.attributeName;
    this.originalDownstreamVariableNames = [
      "extendIdx",
      "unresolvedPath",
      "originalPath"
    ];
    this.missingComponentBlockers = [];
    this.extendIndicesResolved = [];
  }
  async determineDownstreamComponents() {
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      this.addBlockerUpdateTriggerForMissingComponent(this.parentIdx);
      this.missingComponentBlockers.push(this.parentIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let attribute = parent.attributes[this.attributeName];
    if (attribute?.references) {
      this.foundAttribute = true;
      return {
        success: true,
        downstreamComponentIndices: attribute.references.map(
          (comp) => comp.componentIdx
        ),
        downstreamComponentTypes: attribute.references.map(
          (comp) => comp.componentType
        )
      };
    } else {
      this.foundAttribute = false;
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
  }
  async getValue({ consumeChanges = true } = {}) {
    const result = await super.getValue({ consumeChanges });
    const newValue = [];
    for (const comp of result.value) {
      const extendIdx = comp.stateValues.extendIdx;
      newValue.push({
        componentIdx: extendIdx,
        unresolvedPath: comp.stateValues.unresolvedPath,
        originalPath: comp.stateValues.originalPath,
        position: comp.position,
        sourceDoc: comp.sourceDoc
      });
      if (extendIdx !== -1) {
        if (!this.extendIndicesResolved.includes(extendIdx)) {
          this.extendIndicesResolved.push(extendIdx);
          let attributeRefResolutionDeps = this.dependencyHandler.attributeRefResolutionDependenciesByReferenced[extendIdx];
          if (!attributeRefResolutionDeps) {
            attributeRefResolutionDeps = this.dependencyHandler.attributeRefResolutionDependenciesByReferenced[extendIdx] = [];
          }
          attributeRefResolutionDeps.push({
            dependency: this,
            composite: comp
          });
        }
        if (this.dependencyHandler.updateTriggers.componentsReferencingAttributeByReferenced[extendIdx]) {
          for (let dep of this.dependencyHandler.updateTriggers.componentsReferencingAttributeByReferenced[extendIdx]) {
            for (let varName of dep.upstreamVariableNames) {
              await this.dependencyHandler.addBlocker({
                blockerComponentIdx: dep.upstreamComponentIdx,
                blockerType: "recalculateDownstreamComponents",
                blockerStateVariable: varName,
                blockerDependency: dep.dependencyName,
                componentIdxBlocked: dep.upstreamComponentIdx,
                typeBlocked: "stateVariable",
                stateVariableBlocked: varName
              });
            }
            await this.dependencyHandler.addBlockersFromChangedStateVariableDependencies(
              {
                componentIdx: dep.upstreamComponentIdx,
                stateVariables: dep.upstreamVariableNames
              }
            );
          }
        }
      }
    }
    result.value = newValue;
    result.usedDefault = !this.foundAttribute;
    return result;
  }
  deleteFromUpdateTriggers() {
    for (const componentIdx of this.missingComponentBlockers) {
      this.deleteUpdateTriggerForMissingComponent(componentIdx);
    }
    for (const extendIdx of this.extendIndicesResolved) {
      let attributeRefResolutionDeps = this.dependencyHandler.attributeRefResolutionDependenciesByReferenced[extendIdx];
      if (attributeRefResolutionDeps) {
        let ind = attributeRefResolutionDeps.findIndex(
          (entry) => entry.dependency === this
        );
        if (ind !== -1) {
          attributeRefResolutionDeps.splice(ind, 1);
        }
      }
    }
  }
};
_AttributeRefResolutions.dependencyType = "attributeRefResolutions";
let AttributeRefResolutions = _AttributeRefResolutions;
const _ComponentsReferencingAttributeDependency = class _ComponentsReferencingAttributeDependency extends Dependency {
  setUpParameters() {
    if (this.definition.referencedIdx != void 0) {
      this.referencedIdx = this.definition.referencedIdx;
      this.specifiedComponentName = this.referencedIdx;
    } else {
      this.referencedIdx = this.upstreamComponentIdx;
    }
    this.attributeName = this.definition.attributeName;
    this.allowUnresolvedPath = this.definition.allowUnresolvedPath || false;
    this.missingComponentBlockers = [];
  }
  async determineDownstreamComponents() {
    let referencedComponent = this.dependencyHandler._components[this.referencedIdx];
    if (!referencedComponent) {
      this.addBlockerUpdateTriggerForMissingComponent(this.referencedIdx);
      this.missingComponentBlockers.push(this.referencedIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!this.dependencyHandler.updateTriggers.componentsReferencingAttributeByReferenced[this.referencedIdx]) {
      this.dependencyHandler.updateTriggers.componentsReferencingAttributeByReferenced[this.referencedIdx] = [];
    }
    this.dependencyHandler.updateTriggers.componentsReferencingAttributeByReferenced[this.referencedIdx].push(this);
    let attributeRefResolutionDeps = this.dependencyHandler.attributeRefResolutionDependenciesByReferenced[this.referencedIdx];
    if (attributeRefResolutionDeps) {
      if (!this.allowUnresolvedPath) {
        attributeRefResolutionDeps = attributeRefResolutionDeps.filter(
          (entry) => entry.composite.stateValues.unresolvedPath === null
        );
      }
      if (this.attributeName) {
        attributeRefResolutionDeps = attributeRefResolutionDeps.filter(
          (entry) => entry.dependency.attributeName === this.attributeName
        );
      }
      const downstreamComponentIndices = [];
      const downstreamComponentTypes = [];
      for (const entry of attributeRefResolutionDeps) {
        const parentIdx = entry.dependency.parentIdx;
        const parent = this.dependencyHandler._components[parentIdx];
        if (parent) {
          downstreamComponentIndices.push(parentIdx);
          downstreamComponentTypes.push(parent.componentType);
        }
      }
      return {
        success: true,
        downstreamComponentIndices,
        downstreamComponentTypes
      };
    }
    return {
      success: false,
      downstreamComponentIndices: [],
      downstreamComponentTypes: []
    };
  }
  deleteFromUpdateTriggers() {
    for (const componentIdx of this.missingComponentBlockers) {
      this.deleteUpdateTriggerForMissingComponent(componentIdx);
    }
  }
};
_ComponentsReferencingAttributeDependency.dependencyType = "componentsReferencingAttribute";
let ComponentsReferencingAttributeDependency = _ComponentsReferencingAttributeDependency;
const _StringsFromReferenceAttribute = class _StringsFromReferenceAttribute extends Dependency {
  setUpParameters() {
    if (this.definition.parentIdx != void 0) {
      this.parentIdx = this.definition.parentIdx;
      this.specifiedComponentName = this.parentIdx;
    } else {
      this.parentIdx = this.upstreamComponentIdx;
    }
    this.attributeName = this.definition.attributeName;
    this.missingComponentBlockers = [];
  }
  async determineDownstreamComponents() {
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      this.addBlockerUpdateTriggerForMissingComponent(this.parentIdx);
      this.missingComponentBlockers.push(this.parentIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let attribute = parent.attributes[this.attributeName];
    if (attribute?.references) {
      this.attributeStrings = [...attribute.stringChildren];
    } else {
      this.attributeStrings = null;
    }
    return {
      success: true,
      downstreamComponentIndices: [],
      downstreamComponentTypes: []
    };
  }
  async getValue() {
    return {
      value: this.attributeStrings,
      changes: {}
    };
  }
  deleteFromUpdateTriggers() {
    for (const componentIdx of this.missingComponentBlockers) {
      this.deleteUpdateTriggerForMissingComponent(componentIdx);
    }
  }
};
_StringsFromReferenceAttribute.dependencyType = "stringsFromReferenceAttribute";
let StringsFromReferenceAttribute = _StringsFromReferenceAttribute;
const _RendererId = class _RendererId extends Dependency {
  setUpParameters() {
    this.componentIdx = this.definition.componentIdx;
    this.specifiedComponentName = this.componentIdx;
    this.missingComponentBlockers = [];
  }
  async determineDownstreamComponents() {
    this.component = this.dependencyHandler._components[this.componentIdx];
    if (!this.component) {
      this.addBlockerUpdateTriggerForMissingComponent(this.componentIdx);
      this.missingComponentBlockers.push(this.componentIdx);
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [],
      downstreamComponentTypes: []
    };
  }
  async getValue() {
    if (this.component) {
      return {
        value: this.dependencyHandler.core.getRendererId(
          this.component
        ),
        changes: {}
      };
    } else {
      return {
        value: "",
        changes: {}
      };
    }
  }
  deleteFromUpdateTriggers() {
    for (const componentIdx of this.missingComponentBlockers) {
      this.deleteUpdateTriggerForMissingComponent(componentIdx);
    }
  }
};
_RendererId.dependencyType = "rendererId";
let RendererId = _RendererId;
const _SourceCompositeStateVariableDependency = class _SourceCompositeStateVariableDependency extends Dependency {
  setUpParameters() {
    if (this.definition.replacementIdx != void 0) {
      this.replacementIdx = this.definition.replacementIdx;
      this.specifiedComponentName = this.replacementIdx;
    } else {
      this.replacementIdx = this.upstreamComponentIdx;
    }
    if (!this.definition.variableName) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: must have a variableName`
      );
    } else {
      this.originalDownstreamVariableNames = [
        this.definition.variableName
      ];
    }
    if (this.definition.compositeComponentType) {
      this.compositeComponentType = this.definition.compositeComponentType;
    }
    if (this.definition.skipCopies) {
      this.skipCopies = this.definition.skipCopies;
    }
    this.returnSingleVariableValue = true;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents() {
    let replacement = this.dependencyHandler._components[this.replacementIdx];
    if (!replacement) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.replacementIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.replacementIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.replacementIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!replacement.replacementOf) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let sourceComposite = replacement.replacementOf;
    if (this.compositeComponentType) {
      while (!this.dependencyHandler.componentInfoObjects.isInheritedComponentType(
        {
          inheritedComponentType: sourceComposite.componentType,
          baseComponentType: this.compositeComponentType
        }
      )) {
        if (sourceComposite.replacementOf) {
          sourceComposite = sourceComposite.replacementOf;
        } else {
          return {
            success: true,
            downstreamComponentIndices: [],
            downstreamComponentTypes: []
          };
        }
      }
    } else if (this.skipCopies) {
      while (sourceComposite.componentType === "_copy") {
        const extendedComponent = await sourceComposite.stateValues.extendedComponent;
        if (extendedComponent && this.dependencyHandler.componentInfoObjects.isCompositeComponent(
          { componentType: extendedComponent.componentType }
        )) {
          sourceComposite = this.dependencyHandler.components[extendedComponent.componentIdx];
        } else {
          return {
            success: true,
            downstreamComponentIndices: [],
            downstreamComponentTypes: []
          };
        }
      }
    }
    return {
      success: true,
      downstreamComponentIndices: [sourceComposite.componentIdx],
      downstreamComponentTypes: [sourceComposite.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_SourceCompositeStateVariableDependency.dependencyType = "sourceCompositeStateVariable";
let SourceCompositeStateVariableDependency = _SourceCompositeStateVariableDependency;
const _SourceCompositeIdentityDependency = class _SourceCompositeIdentityDependency extends Dependency {
  setUpParameters() {
    if (this.definition.replacementIdx != void 0) {
      this.replacementIdx = this.definition.replacementIdx;
      this.specifiedComponentName = this.replacementIdx;
    } else {
      this.replacementIdx = this.upstreamComponentIdx;
    }
    this.returnSingleComponent = true;
  }
  async determineDownstreamComponents() {
    let replacement = this.dependencyHandler._components[this.replacementIdx];
    if (!replacement) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.replacementIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.replacementIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.replacementIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!replacement.replacementOf) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let sourceComposite = replacement.replacementOf;
    return {
      success: true,
      downstreamComponentIndices: [sourceComposite.componentIdx],
      downstreamComponentTypes: [sourceComposite.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_SourceCompositeIdentityDependency.dependencyType = "sourceCompositeIdentity";
let SourceCompositeIdentityDependency = _SourceCompositeIdentityDependency;
const _ShadowSourceStateVariableDependency = class _ShadowSourceStateVariableDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (!this.definition.variableName) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: must have a variableName`
      );
    } else {
      this.originalDownstreamVariableNames = [
        this.definition.variableName
      ];
    }
    this.returnSingleVariableValue = true;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!component.shadows) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let shadowSourceComponentIdx = component.shadows.componentIdx;
    let shadowSource = this.dependencyHandler._components[shadowSourceComponentIdx];
    if (!shadowSource) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [shadowSource.componentIdx],
      downstreamComponentTypes: [shadowSource.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_ShadowSourceStateVariableDependency.dependencyType = "shadowSourceStateVariable";
let ShadowSourceStateVariableDependency = _ShadowSourceStateVariableDependency;
const _ShadowSourceDependency = class _ShadowSourceDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.returnSingleComponent = true;
    this.variablesOptional = true;
    this.givePropVariableValue = this.definition.givePropVariableValue;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!component.shadows) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (component.shadows.propVariable && !component.shadows.fromImplicitProp) {
      if (!this.givePropVariableValue) {
        return {
          success: true,
          downstreamComponentIndices: [],
          downstreamComponentTypes: []
        };
      } else {
        if (!this.originalDownstreamVariableNames.includes(
          component.shadows.propVariable
        )) {
          if (Object.isFrozen(this.originalDownstreamVariableNames)) {
            this.originalDownstreamVariableNames = [
              ...this.originalDownstreamVariableNames
            ];
          }
          this.originalDownstreamVariableNames.push(
            component.shadows.propVariable
          );
        }
      }
    }
    let shadowSourceComponentIdx = component.shadows.componentIdx;
    let shadowSource = this.dependencyHandler._components[shadowSourceComponentIdx];
    if (!shadowSource) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [shadowSource.componentIdx],
      downstreamComponentTypes: [shadowSource.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_ShadowSourceDependency.dependencyType = "shadowSource";
let ShadowSourceDependency = _ShadowSourceDependency;
const _UnlinkedCopySourceDependency = class _UnlinkedCopySourceDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.returnSingleComponent = true;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!component.unlinkedCopySource) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let unlinkedCopySourceComponentIdx = component.unlinkedCopySource;
    let unlinkedCopySource = this.dependencyHandler._components[unlinkedCopySourceComponentIdx];
    if (!unlinkedCopySource) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [unlinkedCopySource.componentIdx],
      downstreamComponentTypes: [unlinkedCopySource.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_UnlinkedCopySourceDependency.dependencyType = "unlinkedCopySource";
let UnlinkedCopySourceDependency = _UnlinkedCopySourceDependency;
const _ShadowInfoDependency = class _ShadowInfoDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
  }
  async getValue() {
    const component = this.dependencyHandler._components[this.componentIdx];
    if (!component?.shadows) {
      return { value: null, changes: {} };
    }
    return {
      value: {
        componentIdx: component.shadows.componentIdx,
        propVariable: component.shadows.propVariable ?? null
      },
      changes: {}
    };
  }
};
_ShadowInfoDependency.dependencyType = "shadowInfo";
let ShadowInfoDependency = _ShadowInfoDependency;
const _PrimaryShadowDependency = class _PrimaryShadowDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.returnSingleComponent = true;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let primaryShadowDependencies = this.dependencyHandler.updateTriggers.primaryShadowDependencies[this.componentIdx];
    if (!primaryShadowDependencies) {
      primaryShadowDependencies = this.dependencyHandler.updateTriggers.primaryShadowDependencies[this.componentIdx] = [];
    }
    if (!primaryShadowDependencies.includes(this)) {
      primaryShadowDependencies.push(this);
    }
    if (!component.primaryShadow) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let primaryShadowComponentIdx = component.primaryShadow;
    let primaryShadow = this.dependencyHandler._components[primaryShadowComponentIdx];
    if (!primaryShadow) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [primaryShadow.componentIdx],
      downstreamComponentTypes: [primaryShadow.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_PrimaryShadowDependency.dependencyType = "primaryShadow";
let PrimaryShadowDependency = _PrimaryShadowDependency;
const _AdapterSourceStateVariableDependency = class _AdapterSourceStateVariableDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (!this.definition.variableName) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: must have a variableName`
      );
    } else {
      this.originalDownstreamVariableNames = [
        this.definition.variableName
      ];
    }
    this.returnSingleVariableValue = true;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!component.adaptedFrom) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let sourceComposite = component.adaptedFrom;
    return {
      success: true,
      downstreamComponentIndices: [sourceComposite.componentIdx],
      downstreamComponentTypes: [sourceComposite.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_AdapterSourceStateVariableDependency.dependencyType = "adapterSourceStateVariable";
let AdapterSourceStateVariableDependency = _AdapterSourceStateVariableDependency;
const _AdapterSourceDependency = class _AdapterSourceDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames) {
      if (!Array.isArray(this.definition.variableNames)) {
        throw Error(
          `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames must be an array`
        );
      }
      this.originalDownstreamVariableNames = this.definition.variableNames;
    } else {
      this.originalDownstreamVariableNames = [];
    }
    this.returnSingleComponent = true;
    this.variablesOptional = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (!component.adaptedFrom) {
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let sourceComposite = component.adaptedFrom;
    return {
      success: true,
      downstreamComponentIndices: [sourceComposite.componentIdx],
      downstreamComponentTypes: [sourceComposite.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_AdapterSourceDependency.dependencyType = "adapterSource";
let AdapterSourceDependency = _AdapterSourceDependency;
const _CountAmongSiblingsDependency = class _CountAmongSiblingsDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.componentType) {
      this.componentType = this.definition.componentType;
    } else if (this.definition.sameType) {
      this.sameType = true;
    }
    this.includeInheritedComponentTypes = Boolean(
      this.definition.includeInheritedComponentTypes
    );
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    if (component.parentIdx == void 0) {
      console.warn(
        `component ${this.componentIdx} does not have a parent for state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}.`
      );
      return {
        success: true,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    this.parentIdx = component.parentIdx;
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.parentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    let childDependencies = this.dependencyHandler.updateTriggers.childDependenciesByParent[this.parentIdx];
    if (!childDependencies) {
      childDependencies = this.dependencyHandler.updateTriggers.childDependenciesByParent[this.parentIdx] = [];
    }
    if (!childDependencies.includes(this)) {
      childDependencies.push(this);
    }
    if (!parent.childrenMatched) {
      let canProceedWithPlaceholders = parent.childrenMatchedWithPlaceholders;
      if (!canProceedWithPlaceholders) {
        let haveCompositesNotReady = parent.unexpandedCompositesNotReady.length > 0;
        if (!haveCompositesNotReady && parent.unexpandedCompositesReady.length > 0) {
          for (let varName of this.upstreamVariableNames) {
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.parentIdx,
              blockerType: "childMatches",
              blockerStateVariable: varName,
              // add so that can have different blockers of child logic
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "recalculateDownstreamComponents",
              stateVariableBlocked: varName,
              dependencyBlocked: this.dependencyName
            });
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.upstreamComponentIdx,
              blockerType: "recalculateDownstreamComponents",
              blockerStateVariable: varName,
              blockerDependency: this.dependencyName,
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName
            });
          }
          return {
            success: false,
            downstreamComponentIndices: [],
            downstreamComponentTypes: []
          };
        }
        if (haveCompositesNotReady) {
          for (let varName of this.upstreamVariableNames) {
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.parentIdx,
              blockerType: "childMatches",
              blockerStateVariable: varName,
              // add so that can have different blockers of child logic
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "recalculateDownstreamComponents",
              stateVariableBlocked: varName,
              dependencyBlocked: this.dependencyName
            });
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.upstreamComponentIdx,
              blockerType: "recalculateDownstreamComponents",
              blockerStateVariable: varName,
              blockerDependency: this.dependencyName,
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName
            });
          }
          for (let compositeNotReady of parent.unexpandedCompositesNotReady) {
            for (let varName of this.upstreamVariableNames) {
              await this.dependencyHandler.addBlocker({
                blockerComponentIdx: compositeNotReady,
                blockerType: "stateVariable",
                blockerStateVariable: "readyToExpandWhenResolved",
                componentIdxBlocked: this.upstreamComponentIdx,
                typeBlocked: "childMatches",
                stateVariableBlocked: varName
                // add to just block for this variable
              });
            }
          }
          return {
            success: false,
            downstreamComponentIndices: [],
            downstreamComponentTypes: []
          };
        }
      }
    }
    return {
      success: true,
      // downstreamComponentIndices: parent.activeChildren.map(x => x.componentIdx),
      // downstreamComponentTypes: parent.activeChildren.map(x => x.componentType),
      downstreamComponentIndices: [],
      downstreamComponentTypes: []
    };
  }
  deleteFromUpdateTriggers() {
    let childDeps = this.dependencyHandler.updateTriggers.childDependenciesByParent[this.parentIdx];
    if (childDeps) {
      let ind = childDeps.indexOf(this);
      if (ind !== -1) {
        childDeps.splice(ind, 1);
      }
    }
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent2 = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent2) {
        let ind = dependenciesMissingComponent2.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent2.splice(ind, 1);
        }
      }
    }
    let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
    if (dependenciesMissingComponent) {
      let ind = dependenciesMissingComponent.indexOf(this);
      if (ind !== -1) {
        dependenciesMissingComponent.splice(ind, 1);
      }
    }
  }
  async getValue() {
    let childComponentType;
    if (this.componentType) {
      childComponentType = this.componentType;
    } else if (this.sameType) {
      childComponentType = this.dependencyHandler.components[this.upstreamComponentIdx].componentType;
    }
    let children = this.dependencyHandler.components[this.parentIdx].activeChildren;
    if (childComponentType) {
      if (this.includeInheritedComponentTypes) {
        children = children.filter(
          (x2) => this.dependencyHandler.componentInfoObjects.isInheritedComponentType(
            {
              inheritedComponentType: x2.componentType,
              baseComponentType: childComponentType
            }
          )
        );
      } else {
        children = children.filter(
          (x2) => x2.componentType === childComponentType
        );
      }
    }
    let value = children.map((x2) => x2.componentIdx).indexOf(this.upstreamComponentIdx) + 1;
    if (this.parentIdx === this.dependencyHandler.core.documentIdx) {
      let initializeCounters = this.dependencyHandler.core.initializeCounters;
      if (this.includeInheritedComponentTypes) {
        for (let cType in initializeCounters) {
          if (this.dependencyHandler.componentInfoObjects.isInheritedComponentType(
            {
              inheritedComponentType: cType,
              baseComponentType: childComponentType
            }
          )) {
            value += initializeCounters[cType] - 1;
            break;
          }
        }
      } else {
        let initialCounter = initializeCounters[childComponentType];
        if (initialCounter) {
          value += initialCounter - 1;
        }
      }
    }
    return { value, changes: {} };
  }
};
_CountAmongSiblingsDependency.dependencyType = "countAmongSiblings";
let CountAmongSiblingsDependency = _CountAmongSiblingsDependency;
const _ValueDependency = class _ValueDependency extends Dependency {
  setUpParameters() {
    this.value = this.definition.value;
  }
  async getValue() {
    return {
      value: this.value,
      changes: {}
    };
  }
};
_ValueDependency.dependencyType = "value";
let ValueDependency = _ValueDependency;
const _FlagDependency = class _FlagDependency extends ValueDependency {
  setUpParameters() {
    this.flagName = this.definition.flagName;
    this.value = this.dependencyHandler.core.flags[this.flagName];
  }
};
_FlagDependency.dependencyType = "flag";
let FlagDependency = _FlagDependency;
const _DoenetAttributeDependency = class _DoenetAttributeDependency extends StateVariableDependency {
  setUpParameters() {
    this.attributeName = this.definition.attributeName;
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
  }
  async getValue({ consumeChanges = true } = {}) {
    let value = null;
    let changes = {};
    if (this.componentIdentitiesChanged) {
      changes.componentIdentitiesChanged = true;
      if (consumeChanges) {
        this.componentIdentitiesChanged = false;
      }
    }
    if (this.downstreamComponentIndices.length === 1) {
      let depComponent = this.dependencyHandler.components[this.downstreamComponentIndices[0]];
      value = depComponent.doenetAttributes[this.attributeName];
    }
    return { value, changes };
  }
};
_DoenetAttributeDependency.dependencyType = "doenetAttribute";
let DoenetAttributeDependency = _DoenetAttributeDependency;
const _ExtendingDependency = class _ExtendingDependency extends StateVariableDependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
  }
  async getValue({ consumeChanges = true } = {}) {
    let value = null;
    let changes = {};
    if (this.componentIdentitiesChanged) {
      changes.componentIdentitiesChanged = true;
      if (consumeChanges) {
        this.componentIdentitiesChanged = false;
      }
    }
    if (this.downstreamComponentIndices.length === 1) {
      let depComponent = this.dependencyHandler.components[this.downstreamComponentIndices[0]];
      value = depComponent.extending;
    }
    return { value, changes };
  }
};
_ExtendingDependency.dependencyType = "extending";
let ExtendingDependency = _ExtendingDependency;
const _AttributePrimitiveDependency = class _AttributePrimitiveDependency extends StateVariableDependency {
  setUpParameters() {
    this.attributeName = this.definition.attributeName;
    if (this.definition.parentIdx != void 0) {
      this.componentIdx = this.definition.parentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
  }
  async getValue({ consumeChanges = true } = {}) {
    let value = null;
    let changes = {};
    if (this.componentIdentitiesChanged) {
      changes.componentIdentitiesChanged = true;
      if (consumeChanges) {
        this.componentIdentitiesChanged = false;
      }
    }
    if (this.downstreamComponentIndices.length === 1) {
      let parent = this.dependencyHandler.components[this.componentIdx];
      if (parent) {
        value = parent.attributes[this.attributeName];
        if (value && value.type === "primitive") {
          value = value.primitive.value;
        } else {
          value = null;
        }
      }
    }
    return { value, changes };
  }
};
_AttributePrimitiveDependency.dependencyType = "attributePrimitive";
let AttributePrimitiveDependency = _AttributePrimitiveDependency;
const _SerializedChildrenDependency = class _SerializedChildrenDependency extends Dependency {
  setUpParameters() {
    if (this.definition.parentIdx != void 0) {
      this.parentIdx = this.definition.parentIdx;
      this.specifiedComponentName = this.parentIdx;
    } else {
      this.parentIdx = this.upstreamComponentIdx;
    }
  }
  async determineDownstreamComponents() {
    let parent = this.dependencyHandler._components[this.parentIdx];
    if (!parent) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.parentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.parentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [this.parentIdx],
      downstreamComponentTypes: [parent.componentType]
    };
  }
  async getValue() {
    let parent = this.dependencyHandler._components[this.parentIdx];
    return {
      value: parent.serializedChildren,
      changes: {}
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_SerializedChildrenDependency.dependencyType = "serializedChildren";
let SerializedChildrenDependency = _SerializedChildrenDependency;
const _DoenetMLDependency = class _DoenetMLDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    this.displayOnlyChildren = this.definition.displayOnlyChildren;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  async getValue() {
    let doenetML = this.dependencyHandler.core.requestComponentDoenetML(
      this.componentIdx,
      this.displayOnlyChildren
    );
    return {
      value: doenetML,
      changes: {}
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_DoenetMLDependency.dependencyType = "doenetML";
let DoenetMLDependency = _DoenetMLDependency;
const _DoenetMLRangeDependency = class _DoenetMLRangeDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  async getValue() {
    let component = this.dependencyHandler._components[this.componentIdx];
    return {
      value: component.position ?? null,
      changes: {}
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_DoenetMLRangeDependency.dependencyType = "position";
let DoenetMLRangeDependency = _DoenetMLRangeDependency;
const _VariantsDependency = class _VariantsDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
      this.specifiedComponentName = this.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  async getValue() {
    let component = this.dependencyHandler._components[this.componentIdx];
    return {
      value: component.variants,
      changes: {}
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
};
_VariantsDependency.dependencyType = "variants";
let VariantsDependency = _VariantsDependency;
const _CounterDependency = class _CounterDependency extends Dependency {
  setUpParameters() {
    this.counterName = this.definition.counterName;
    this.componentIdx = this.upstreamComponentIdx;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    let counters = component.counters[this.counterName];
    if (!counters) {
      counters = component.counters[this.counterName] = {
        dependencies: [],
        componentList: [],
        value: null
      };
    }
    if (!counters.dependencies.includes(this)) {
      counters.dependencies.push(this);
    }
    await this.dependencyHandler.collateCountersAndPropagateToAncestors(
      component
    );
    return {
      success: true,
      downstreamComponentIndices: [],
      downstreamComponentTypes: []
    };
  }
  async getValue() {
    let component = this.dependencyHandler._components[this.componentIdx];
    return {
      value: component.counters[this.counterName].value,
      changes: {}
    };
  }
};
_CounterDependency.dependencyType = "counter";
let CounterDependency = _CounterDependency;
const _DetermineDependenciesDependency = class _DetermineDependenciesDependency extends Dependency {
  setUpParameters() {
    if (this.definition.componentIdx != void 0) {
      this.componentIdx = this.definition.componentIdx;
    } else {
      this.componentIdx = this.upstreamComponentIdx;
    }
    if (this.definition.variableNames === void 0) {
      throw Error(
        `Invalid state variable ${this.representativeStateVariable} of ${this.upstreamComponentIdx}, dependency ${this.dependencyName}: variableNames is not defined`
      );
    }
    this.originalDownstreamVariableNames = this.definition.variableNames;
    this.returnSingleComponent = true;
  }
  async determineDownstreamComponents() {
    let component = this.dependencyHandler._components[this.componentIdx];
    if (!component) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx];
      if (!dependenciesMissingComponent) {
        dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.componentIdx] = [];
      }
      if (!dependenciesMissingComponent.includes(this)) {
        dependenciesMissingComponent.push(this);
      }
      for (let varName of this.upstreamVariableNames) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.componentIdx,
          blockerType: "componentIdentity",
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "recalculateDownstreamComponents",
          stateVariableBlocked: varName,
          dependencyBlocked: this.dependencyName
        });
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "recalculateDownstreamComponents",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
      }
      return {
        success: false,
        downstreamComponentIndices: [],
        downstreamComponentTypes: []
      };
    }
    for (let varName of this.upstreamVariableNames) {
      await this.dependencyHandler.addBlocker({
        blockerComponentIdx: this.upstreamComponentIdx,
        blockerType: "determineDependencies",
        blockerStateVariable: varName,
        blockerDependency: this.dependencyName,
        componentIdxBlocked: this.upstreamComponentIdx,
        typeBlocked: "stateVariable",
        stateVariableBlocked: varName
      });
    }
    return {
      success: true,
      downstreamComponentIndices: [this.componentIdx],
      downstreamComponentTypes: [component.componentType]
    };
  }
  deleteFromUpdateTriggers() {
    if (this.specifiedComponentName) {
      let dependenciesMissingComponent = this.dependencyHandler.updateTriggers.dependenciesMissingComponentBySpecifiedName[this.specifiedComponentName];
      if (dependenciesMissingComponent) {
        let ind = dependenciesMissingComponent.indexOf(this);
        if (ind !== -1) {
          dependenciesMissingComponent.splice(ind, 1);
        }
      }
    }
  }
  async markStale() {
    let component = this.dependencyHandler._components[this.upstreamComponentIdx];
    for (let varName of this.upstreamVariableNames) {
      if (!(component && component.state[varName] && component.state[varName].currentlyResolving)) {
        await this.dependencyHandler.addBlocker({
          blockerComponentIdx: this.upstreamComponentIdx,
          blockerType: "determineDependencies",
          blockerStateVariable: varName,
          blockerDependency: this.dependencyName,
          componentIdxBlocked: this.upstreamComponentIdx,
          typeBlocked: "stateVariable",
          stateVariableBlocked: varName
        });
        for (let depName in this.dependencyHandler.downstreamDependencies[this.upstreamComponentIdx][varName]) {
          let dep = this.dependencyHandler.downstreamDependencies[this.upstreamComponentIdx][varName][depName];
          if (dep.dependencyType !== "determineDependencies") {
            await this.dependencyHandler.addBlocker({
              blockerComponentIdx: this.upstreamComponentIdx,
              blockerType: "determineDependencies",
              blockerStateVariable: varName,
              blockerDependency: this.dependencyName,
              componentIdxBlocked: this.upstreamComponentIdx,
              typeBlocked: "recalculateDownstreamComponents",
              stateVariableBlocked: varName,
              dependencyBlocked: depName
            });
          }
        }
      }
    }
  }
};
_DetermineDependenciesDependency.dependencyType = "determineDependencies";
let DetermineDependenciesDependency = _DetermineDependenciesDependency;
const _FileDependency = class _FileDependency extends Dependency {
  setUpParameters() {
    this.cid = this.definition.cid;
    this.uri = this.definition.uri;
    this.fileType = this.definition.fileType;
  }
  async getValue() {
    let extension;
    if (this.cid) {
      if (this.fileType.toLowerCase() === "csv") {
        extension = "csv";
      } else {
        return {
          value: null,
          changes: {}
        };
      }
      let fileContents = await retrieveTextFileForCid(
        this.cid,
        extension
      );
      return {
        value: fileContents,
        changes: {}
      };
    } else {
      let response = await fetch(this.uri);
      if (response.ok) {
        let fileContents = await response.text();
        return {
          value: fileContents,
          changes: {}
        };
      } else {
        return {
          value: null,
          changes: {}
        };
      }
    }
  }
};
_FileDependency.dependencyType = "file";
let FileDependency = _FileDependency;
const dependencyTypeClasses = [
  StateVariableDependency,
  StateVariableFromUnresolvedPathDependency,
  MultipleStateVariablesDependency,
  StateVariableComponentTypeDependency,
  StateVariableArraySizeDependency,
  RecursiveDependencyValuesDependency,
  ComponentIdentityDependency,
  AttributeComponentDependency,
  ChildDependency,
  DescendantDependency,
  ParentDependency,
  ParentIdentityDependency,
  AncestorDependency,
  ReplacementDependency,
  RefResolutionIndexDependencies,
  RefResolutionDependency,
  AttributeRefResolutions,
  ComponentsReferencingAttributeDependency,
  StringsFromReferenceAttribute,
  RendererId,
  SourceCompositeStateVariableDependency,
  SourceCompositeIdentityDependency,
  ShadowSourceStateVariableDependency,
  ShadowSourceDependency,
  UnlinkedCopySourceDependency,
  PrimaryShadowDependency,
  ShadowInfoDependency,
  AdapterSourceStateVariableDependency,
  AdapterSourceDependency,
  CountAmongSiblingsDependency,
  ValueDependency,
  FlagDependency,
  DoenetAttributeDependency,
  ExtendingDependency,
  AttributePrimitiveDependency,
  SerializedChildrenDependency,
  DoenetMLDependency,
  DoenetMLRangeDependency,
  VariantsDependency,
  CounterDependency,
  DetermineDependenciesDependency,
  FileDependency
];
const EMPTY_BLOCKERS = Object.freeze({});
class DependencyHandler {
  constructor({
    _components,
    componentInfoObjects,
    core: core2
  }) {
    this.upstreamDependencies = {};
    this.downstreamDependencies = {};
    this.switchDependencies = {};
    this.circularCheckPassed = {};
    this.circularResolveBlockedCheckPassed = {};
    this.dependencyTypes = {};
    dependencyTypeClasses.forEach(
      (dt2) => this.dependencyTypes[dt2.dependencyType] = dt2
    );
    this.core = core2;
    this._components = _components;
    this.componentInfoObjects = componentInfoObjects;
    this.updateTriggers = {
      descendantDependenciesByAncestor: {},
      ancestorDependenciesByPotentialAncestor: {},
      replacementDependenciesByComposite: {},
      childDependenciesByParent: {},
      parentDependenciesByParent: {},
      dependenciesMissingComponentBySpecifiedName: {},
      dependenciesBasedOnDependenciesOfStateVariables: {},
      primaryShadowDependencies: {},
      componentsReferencingAttributeByReferenced: {}
    };
    this.resolveBlockers = {
      neededToResolve: {},
      resolveBlockedBy: {}
    };
    this.numDependencySetups = 0;
    this.numMaterializedStateVariables = 0;
    this.attributeRefResolutionDependenciesByReferenced = {};
    this._internedVariableNameLists = /* @__PURE__ */ new Map();
    this._internedComponentIndexLists = /* @__PURE__ */ new Map();
    this._internedComponentTypeLists = /* @__PURE__ */ new Map();
    this._internedNameListArrays = /* @__PURE__ */ new Map();
    this._internedListIds = /* @__PURE__ */ new WeakMap();
    this._nextInternedListId = 1;
    this._internedValuesChangedRecords = /* @__PURE__ */ new Map();
    this._internedValuesChangedArrays = /* @__PURE__ */ new Map();
    this.emptyValuesChangedRecord = Object.freeze({});
    this._internedListIds.set(
      this.emptyValuesChangedRecord,
      this._nextInternedListId
    );
    this._nextInternedListId++;
  }
  internComponentIndexList(indices) {
    const key = indices.join(",");
    let interned = this._internedComponentIndexLists.get(key);
    if (!interned) {
      interned = Object.freeze(indices);
      this._internedComponentIndexLists.set(key, interned);
    }
    return interned;
  }
  internComponentTypeList(types) {
    const key = types.join("\n");
    let interned = this._internedComponentTypeLists.get(key);
    if (!interned) {
      interned = Object.freeze(types);
      this._internedComponentTypeLists.set(key, interned);
    }
    return interned;
  }
  /**
   * Intern an array whose elements are already-interned name lists
   * (from `internVariableNameList`), keyed by the identity of those
   * elements. Elements that are not interned lists (no assigned id) make
   * the array non-internable; it is returned unchanged.
   */
  internNameListArray(outer) {
    const ids = [];
    for (const inner of outer) {
      const id = typeof inner === "object" && inner !== null ? this._internedListIds.get(inner) : void 0;
      if (id === void 0) {
        return outer;
      }
      ids.push(id);
    }
    const key = ids.join(",");
    let interned = this._internedNameListArrays.get(key);
    if (!interned) {
      interned = Object.freeze(outer);
      this._internedNameListArrays.set(key, interned);
    }
    return interned;
  }
  internVariableNameList(names) {
    const key = names.join("\n");
    let interned = this._internedVariableNameLists.get(key);
    if (!interned) {
      interned = Object.freeze(names);
      this._internedVariableNameLists.set(key, interned);
      this._internedListIds.set(interned, this._nextInternedListId);
      this._nextInternedListId++;
    }
    return interned;
  }
  /**
   * Like `internVariableNameList`, but never freezes the caller's array:
   * on a cache miss the interned entry is a frozen copy. For dependency
   * fields that alias arrays owned elsewhere (state-variable definitions,
   * constructor arguments), which must stay mutable for their owners.
   */
  internVariableNameListByCopy(names) {
    const key = names.join("\n");
    const interned = this._internedVariableNameLists.get(key);
    if (interned) {
      return interned;
    }
    return this.internVariableNameList([...names]);
  }
  /**
   * Return the shared frozen initial `valuesChanged` record (every
   * variable mapped to the shared initial change record) for the given
   * interned variable-name list. Falls back to a fresh mutable record if
   * `names` was not interned. Writers must thaw/replace before mutating;
   * see `Dependency.thawValuesChangedRecord` and
   * `Dependency.consumeChangeRecord`.
   */
  internInitialValuesChangedRecord(names, initialChangeRecord) {
    const listId = this._internedListIds.get(names);
    if (listId === void 0) {
      const record2 = {};
      for (const name of names) {
        record2[name] = initialChangeRecord;
      }
      return record2;
    }
    let record = this._internedValuesChangedRecords.get(listId);
    if (!record) {
      record = {};
      for (const name of names) {
        record[name] = initialChangeRecord;
      }
      Object.freeze(record);
      this._internedValuesChangedRecords.set(listId, record);
      this._internedListIds.set(record, this._nextInternedListId);
      this._nextInternedListId++;
    }
    return record;
  }
  /**
   * Intern a `valuesChanged` outer array whose entries are all interned
   * records (from `internInitialValuesChangedRecord` or
   * `emptyValuesChangedRecord`), keyed by the identity of those entries.
   * Any non-interned (hence mutable) entry makes the array non-internable;
   * it is returned unchanged.
   */
  internValuesChangedArray(outer) {
    const ids = [];
    for (const record of outer) {
      const id = typeof record === "object" && record !== null ? this._internedListIds.get(record) : void 0;
      if (id === void 0) {
        return outer;
      }
      ids.push(id);
    }
    const key = ids.join(",");
    let interned = this._internedValuesChangedArrays.get(key);
    if (!interned) {
      interned = Object.freeze(outer);
      this._internedValuesChangedArrays.set(key, interned);
    }
    return interned;
  }
  /**
   * Create the per-component entries in the two dependency maps.
   *
   * The maps are indexed unconditionally by component elsewhere
   * (e.g. `markUpstreamDependentsStale`, `resetCircularCheckPassed`),
   * so every component needs its slots at construction even though the
   * per-state-variable dependencies are now built on demand by
   * `ensureStateVariableDependenciesSetUp`.
   */
  createComponentDependencySlots(component) {
    if (this.downstreamDependencies[component.componentIdx]) {
      this.deleteAllDownstreamDependencies({ component });
    }
    this.downstreamDependencies[component.componentIdx] = {};
    if (!this.upstreamDependencies[component.componentIdx]) {
      this.upstreamDependencies[component.componentIdx] = {};
    }
  }
  /**
   * Build the dependencies of `stateVariable` (and the rest of its
   * `additionalStateVariablesDefined` group) if they have not been built
   * yet. Dependencies are created on demand at the first resolution
   * attempt (`resolveItem`/`resolveIfReady`) rather than at component
   * construction, so state variables that are never demanded never pay
   * for their dependency structures.
   *
   * The marker for "already set up" is the existence of the variable's
   * slot in `downstreamDependencies` — slots are deleted only by
   * whole-component teardown.
   */
  async ensureStateVariableDependenciesSetUp(componentIdx, stateVariable) {
    const component = this._components[componentIdx];
    if (!component) {
      return;
    }
    const stateVarObj = component.state[stateVariable];
    if (!stateVarObj) {
      return;
    }
    const downDepsForComponent = this.downstreamDependencies[component.componentIdx];
    if (!downDepsForComponent || downDepsForComponent[stateVariable] !== void 0) {
      return;
    }
    await ensureStateVariableMaterialized({
      core: this.core,
      component,
      stateVariable
    });
    const allStateVariablesAffected = [stateVariable];
    if (stateVarObj.additionalStateVariablesDefined) {
      allStateVariablesAffected.push(
        ...stateVarObj.additionalStateVariablesDefined
      );
    }
    for (const varName of allStateVariablesAffected) {
      if (!downDepsForComponent[varName]) {
        downDepsForComponent[varName] = {};
      }
    }
    this.numDependencySetups++;
    await this.setUpStateVariableDependencies({
      component,
      stateVariable,
      allStateVariablesAffected
    });
  }
  async setUpStateVariableDependencies({
    component,
    stateVariable,
    allStateVariablesAffected
  }) {
    let stateVarObj = component.state[stateVariable];
    let dependencies;
    if (stateVarObj.stateVariablesDeterminingDependencies) {
      dependencies = {};
      if (stateVarObj.stateVariablesDeterminingDependencies) {
        dependencies.__determine_dependencies = {
          dependencyType: "determineDependencies",
          variableNames: stateVarObj.stateVariablesDeterminingDependencies
        };
      }
    } else {
      dependencies = await stateVarObj.returnDependencies({
        componentInfoObjects: this.componentInfoObjects,
        sharedParameters: component.sharedParameters
      });
    }
    for (let dependencyName in dependencies) {
      let dependencyDefinition = dependencies[dependencyName];
      if (!(dependencyDefinition.dependencyType in this.dependencyTypes)) {
        throw Error(
          `Unrecognized dependency type ${dependencyDefinition.dependencyType} for ${dependencyName} of ${stateVariable} of ${component.componentIdx}`
        );
      }
      let dep = new this.dependencyTypes[dependencyDefinition.dependencyType]({
        component,
        stateVariable,
        allStateVariablesAffected,
        dependencyName,
        dependencyDefinition,
        dependencyHandler: this,
        expandComposites: false,
        forceExpandComposites: false
      });
      await dep.initialize();
      dep.checkForCircular();
    }
  }
  deleteAllDownstreamDependencies({
    component,
    stateVariables = "__all__"
  }) {
    let componentIdx = component.componentIdx;
    let stateVariablesToAdddress;
    if (stateVariables === "__all__") {
      stateVariablesToAdddress = Object.keys(
        this.downstreamDependencies[componentIdx]
      );
    } else {
      stateVariablesToAdddress = stateVariables;
    }
    for (let stateVariable of stateVariablesToAdddress) {
      let downDeps = this.downstreamDependencies[componentIdx][stateVariable];
      for (let downDepName in downDeps) {
        downDeps[downDepName].deleteDependency();
      }
      delete this.downstreamDependencies[componentIdx][stateVariable];
    }
    if (Object.keys(this.downstreamDependencies[componentIdx]).length === 0 && !this.components[componentIdx]) {
      delete this.downstreamDependencies[componentIdx];
    }
  }
  async deleteAllUpstreamDependencies({
    component,
    stateVariables = "__all__",
    completelyDelete = false
  }) {
    let componentIdx = component.componentIdx;
    let stateVariablesToAdddress;
    if (stateVariables === "__all__") {
      stateVariablesToAdddress = Object.keys(
        this.upstreamDependencies[componentIdx]
      );
    } else {
      stateVariablesToAdddress = stateVariables;
    }
    for (let stateVariable of stateVariablesToAdddress) {
      if (this.upstreamDependencies[componentIdx][stateVariable]) {
        for (let upDep of [
          ...this.upstreamDependencies[componentIdx][stateVariable]
        ]) {
          if (completelyDelete) {
            for (let upVarName of upDep.upstreamVariableNames) {
              if (this._components[upDep.upstreamComponentIdx].state[upVarName].initiallyResolved) {
                await this.core.markStateVariableAndUpstreamDependentsStale(
                  {
                    component: this.components[upDep.upstreamComponentIdx],
                    varName: upVarName
                  }
                );
              }
            }
            upDep.deleteDependency();
          } else {
            await upDep.removeDownstreamComponent({
              indexToRemove: upDep.downstreamComponentIndices.indexOf(
                componentIdx
              )
            });
          }
        }
      }
      delete this.upstreamDependencies[componentIdx][stateVariable];
    }
    if (Object.keys(this.upstreamDependencies[componentIdx]).length === 0 && !this._components[componentIdx]) {
      delete this.upstreamDependencies[componentIdx];
    }
  }
  async addBlockersFromChangedStateVariableDependencies({
    componentIdx,
    stateVariables
  }) {
    let triggersForComponent = this.updateTriggers.dependenciesBasedOnDependenciesOfStateVariables[componentIdx];
    if (triggersForComponent) {
      for (let varName of stateVariables) {
        let triggersForVarName = triggersForComponent[varName];
        if (triggersForVarName) {
          for (let dep of triggersForVarName) {
            if (dep.gettingValue) {
              let compWithUpdated = dep.varsWithUpdatedDeps[componentIdx];
              if (!compWithUpdated) {
                compWithUpdated = dep.varsWithUpdatedDeps[componentIdx] = [];
              }
              if (!compWithUpdated.includes(varName)) {
                compWithUpdated.push(varName);
              }
            } else {
              for (let vName of dep.upstreamVariableNames) {
                await this.addBlocker({
                  blockerComponentIdx: dep.upstreamComponentIdx,
                  blockerType: "recalculateDownstreamComponents",
                  blockerStateVariable: vName,
                  blockerDependency: dep.dependencyName,
                  componentIdxBlocked: dep.upstreamComponentIdx,
                  typeBlocked: "stateVariable",
                  stateVariableBlocked: vName
                });
              }
            }
          }
        }
      }
    }
  }
  async addBlockersFromChangedActiveChildren({ parent }) {
    await this.collateCountersAndPropagateToAncestors(parent);
    if (this.updateTriggers.childDependenciesByParent[parent.componentIdx]) {
      for (let dep of this.updateTriggers.childDependenciesByParent[parent.componentIdx]) {
        for (let varName of dep.upstreamVariableNames) {
          await this.addBlocker({
            blockerComponentIdx: dep.upstreamComponentIdx,
            blockerType: "recalculateDownstreamComponents",
            blockerStateVariable: varName,
            blockerDependency: dep.dependencyName,
            componentIdxBlocked: dep.upstreamComponentIdx,
            typeBlocked: "stateVariable",
            stateVariableBlocked: varName
          });
        }
        await this.addBlockersFromChangedStateVariableDependencies({
          componentIdx: dep.upstreamComponentIdx,
          stateVariables: dep.upstreamVariableNames
        });
      }
    }
    if (parent.ancestors) {
      if (this.updateTriggers.parentDependenciesByParent[parent.componentIdx]) {
        for (let dep of this.updateTriggers.parentDependenciesByParent[parent.componentIdx]) {
          for (let varName of dep.upstreamVariableNames) {
            await this.addBlocker({
              blockerComponentIdx: dep.upstreamComponentIdx,
              blockerType: "recalculateDownstreamComponents",
              blockerStateVariable: varName,
              blockerDependency: dep.dependencyName,
              componentIdxBlocked: dep.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName
            });
          }
          await this.addBlockersFromChangedStateVariableDependencies({
            componentIdx: dep.upstreamComponentIdx,
            stateVariables: dep.upstreamVariableNames
          });
        }
      }
      for (let ancestorIdx of [
        parent.componentIdx,
        ...ancestorsIncludingComposites(parent, this.components)
      ]) {
        await this.addDescendantBlockersToAncestor(ancestorIdx);
      }
      if (this.updateTriggers.ancestorDependenciesByPotentialAncestor[parent.componentIdx]) {
        for (let dep of this.updateTriggers.ancestorDependenciesByPotentialAncestor[parent.componentIdx]) {
          for (let varName of dep.upstreamVariableNames) {
            await this.addBlocker({
              blockerComponentIdx: dep.upstreamComponentIdx,
              blockerType: "recalculateDownstreamComponents",
              blockerStateVariable: varName,
              blockerDependency: dep.dependencyName,
              componentIdxBlocked: dep.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName
            });
          }
          await this.addBlockersFromChangedStateVariableDependencies({
            componentIdx: dep.upstreamComponentIdx,
            stateVariables: dep.upstreamVariableNames
          });
        }
      }
    }
  }
  async resolveBlockersFromChangedActiveChildren(parent, force = false) {
    await this.collateCountersAndPropagateToAncestors(parent);
    if (this.updateTriggers.childDependenciesByParent[parent.componentIdx]) {
      for (let dep of this.updateTriggers.childDependenciesByParent[parent.componentIdx]) {
        await this.resolveIfReady({
          componentIdx: dep.upstreamComponentIdx,
          type: "recalculateDownstreamComponents",
          stateVariable: dep.representativeStateVariable,
          dependency: dep.dependencyName,
          force
          // recurseUpstream: true,
        });
      }
    }
    if (parent.ancestors) {
      if (this.updateTriggers.parentDependenciesByParent[parent.componentIdx]) {
        for (let dep of this.updateTriggers.parentDependenciesByParent[parent.componentIdx]) {
          await this.resolveIfReady({
            componentIdx: dep.upstreamComponentIdx,
            type: "recalculateDownstreamComponents",
            stateVariable: dep.representativeStateVariable,
            dependency: dep.dependencyName,
            force
            // recurseUpstream: true
          });
        }
      }
      for (let ancestorIdx of [
        parent.componentIdx,
        ...ancestorsIncludingComposites(parent, this.components)
      ]) {
        await this.resolveDescendantBlockersToAncestor(
          ancestorIdx,
          force
        );
      }
      if (this.updateTriggers.ancestorDependenciesByPotentialAncestor[parent.componentIdx]) {
        for (let dep of this.updateTriggers.ancestorDependenciesByPotentialAncestor[parent.componentIdx]) {
          await this.resolveIfReady({
            componentIdx: dep.upstreamComponentIdx,
            type: "recalculateDownstreamComponents",
            stateVariable: dep.representativeStateVariable,
            dependency: dep.dependencyName,
            force
            // recurseUpstream: true
          });
        }
      }
    }
  }
  async addDescendantBlockersToAncestor(ancestorIdx) {
    if (this.updateTriggers.descendantDependenciesByAncestor[ancestorIdx]) {
      for (let dep of this.updateTriggers.descendantDependenciesByAncestor[ancestorIdx]) {
        for (let varName of dep.upstreamVariableNames) {
          await this.addBlocker({
            blockerComponentIdx: dep.upstreamComponentIdx,
            blockerType: "recalculateDownstreamComponents",
            blockerStateVariable: varName,
            blockerDependency: dep.dependencyName,
            componentIdxBlocked: dep.upstreamComponentIdx,
            typeBlocked: "stateVariable",
            stateVariableBlocked: varName
          });
        }
        await this.addBlockersFromChangedStateVariableDependencies({
          componentIdx: dep.upstreamComponentIdx,
          stateVariables: dep.upstreamVariableNames
        });
      }
    }
  }
  async resolveDescendantBlockersToAncestor(ancestorIdx, force = false) {
    if (this.updateTriggers.descendantDependenciesByAncestor[ancestorIdx]) {
      for (let dep of this.updateTriggers.descendantDependenciesByAncestor[ancestorIdx]) {
        await this.resolveIfReady({
          componentIdx: dep.upstreamComponentIdx,
          type: "recalculateDownstreamComponents",
          stateVariable: dep.representativeStateVariable,
          dependency: dep.dependencyName,
          force
          // recurseUpstream: true
        });
      }
    }
  }
  async addBlockersFromChangedReplacements(composite) {
    if (this.updateTriggers.replacementDependenciesByComposite[composite.componentIdx]) {
      for (let dep of this.updateTriggers.replacementDependenciesByComposite[composite.componentIdx]) {
        for (let varName of dep.upstreamVariableNames) {
          await this.addBlocker({
            blockerComponentIdx: dep.upstreamComponentIdx,
            blockerType: "recalculateDownstreamComponents",
            blockerStateVariable: varName,
            blockerDependency: dep.dependencyName,
            componentIdxBlocked: dep.upstreamComponentIdx,
            typeBlocked: "stateVariable",
            stateVariableBlocked: varName
          });
        }
      }
    }
    for (let ancestorIdx of [
      composite.componentIdx,
      ...ancestorsIncludingComposites(composite, this.components)
    ]) {
      await this.addDescendantBlockersToAncestor(ancestorIdx);
    }
  }
  checkForCircularDependency({
    componentIdx,
    varName,
    previouslyVisited = []
  }) {
    let stateVariableIdentifier = componentIdx + ":" + varName;
    if (previouslyVisited.includes(stateVariableIdentifier)) {
      console.log(
        "found circular",
        stateVariableIdentifier,
        previouslyVisited
      );
      let componentNameRe = /^(.*):/;
      let componentsInvolved = previouslyVisited.map(
        (x2) => this.components[x2.match(componentNameRe)[1]]
      );
      let message = this.getCircularDependencyMessage(componentsInvolved);
      throw Error(message);
    } else {
      previouslyVisited = [...previouslyVisited, stateVariableIdentifier];
    }
    if (!this.circularCheckPassed[stateVariableIdentifier]) {
      this.circularCheckPassed[stateVariableIdentifier] = true;
      if (componentIdx in this.downstreamDependencies) {
        let downDeps = this.downstreamDependencies[componentIdx][varName];
        for (let dependencyName in downDeps) {
          let dep = downDeps[dependencyName];
          let downstreamComponentIndices = dep.downstreamComponentIndices;
          if (!downstreamComponentIndices) {
            continue;
          }
          let mappedDownstreamVariableNamesByComponent = dep.mappedDownstreamVariableNamesByComponent;
          if (!mappedDownstreamVariableNamesByComponent) {
            continue;
          }
          for (let [
            ind,
            cIdx
          ] of downstreamComponentIndices.entries()) {
            let varNames = mappedDownstreamVariableNamesByComponent[ind];
            for (let vname of varNames) {
              this.checkForCircularDependency({
                componentIdx: cIdx,
                varName: vname,
                previouslyVisited
              });
            }
          }
        }
      }
    }
  }
  /**
   * Drop the circular-check memo records. They are pure caches ("this
   * state variable already passed the check"), so clearing costs only
   * re-verification when a later dependency change re-triggers a check.
   * Called once initial document construction finishes: at that point the
   * memos cover every state variable created during the load (one record
   * entry per state variable), but they regrow only for the typically few
   * components involved in subsequent updates.
   */
  clearCircularCheckMemos() {
    this.circularCheckPassed = {};
    this.circularResolveBlockedCheckPassed = {};
  }
  resetCircularCheckPassed(componentIdx, varName) {
    let stateVariableIdentifier = componentIdx + ":" + varName;
    if (this.circularCheckPassed[stateVariableIdentifier]) {
      delete this.circularCheckPassed[stateVariableIdentifier];
      let upstream = this.upstreamDependencies[componentIdx][varName];
      if (upstream) {
        for (let upDep of upstream) {
          for (let vName of upDep.upstreamVariableNames) {
            if (vName !== "__identity") {
              this.resetCircularCheckPassed(
                upDep.upstreamComponentIdx,
                vName
              );
            }
          }
        }
      }
    }
  }
  async updateDependencies({ componentIdx, stateVariable, dependency }) {
    let component = this._components[componentIdx];
    let stateVarObj = component.state[stateVariable];
    let allStateVariablesAffected = [stateVariable];
    if (stateVarObj.additionalStateVariablesDefined) {
      allStateVariablesAffected.push(
        ...stateVarObj.additionalStateVariablesDefined
      );
    }
    let determineDeps = this.downstreamDependencies[componentIdx][stateVariable].__determine_dependencies;
    let dependencyResult;
    if (determineDeps) {
      let resolvedAll = true;
      if (determineDeps.originalDownstreamVariableNames.length > 0) {
        for (let [
          ind,
          cIdx
        ] of determineDeps.downstreamComponentIndices.entries()) {
          let comp = this._components[cIdx];
          for (let vName of determineDeps.mappedDownstreamVariableNamesByComponent[ind]) {
            let resolved = comp.state[vName].isResolved;
            if (!resolved) {
              let result = await this.resolveItem({
                componentIdx: cIdx,
                type: "stateVariable",
                stateVariable: vName
              });
              resolved = result.success;
            }
            if (!resolved) {
              resolvedAll = false;
              for (let vName2 of allStateVariablesAffected) {
                await this.addBlocker({
                  blockerComponentIdx: cIdx,
                  blockerType: "stateVariable",
                  blockerStateVariable: vName,
                  componentIdxBlocked: componentIdx,
                  typeBlocked: "determineDependencies",
                  stateVariableBlocked: vName2,
                  dependencyBlocked: dependency
                });
              }
            }
          }
        }
      }
      if (resolvedAll) {
        dependencyResult = await determineDeps.getValue();
      } else {
        return { success: false };
      }
    } else {
      dependencyResult = { changes: {}, value: { stateValues: {} } };
    }
    if (Object.keys(dependencyResult.changes).length === 0 && stateVarObj._previousValue !== void 0) {
      return { success: true };
    }
    let returnDepArgs = {
      stateValues: Object.assign({}, dependencyResult.value.stateValues),
      componentInfoObjects: this.componentInfoObjects,
      sharedParameters: component.sharedParameters
    };
    let newDependencies = await stateVarObj.returnDependencies(returnDepArgs);
    if (stateVarObj.stateVariablesDeterminingDependencies) {
      newDependencies.__determine_dependencies = {
        dependencyType: "determineDependencies",
        variableNames: stateVarObj.stateVariablesDeterminingDependencies
      };
    }
    let changeResult = await this.replaceDependenciesIfChanged({
      component,
      stateVariable,
      newDependencies,
      allStateVariablesAffected
    });
    if (!(changeResult.changedDependency || returnDepArgs.changedDependency)) {
      return { success: true };
    }
    for (let dep of changeResult.newlyCreatedDependencies) {
      dep.checkForCircular();
    }
    for (let varName of allStateVariablesAffected) {
      this.checkForCircularDependency({
        componentIdx: component.componentIdx,
        varName
      });
      component.state[varName].forceRecalculation = true;
    }
    if (stateVarObj.initiallyResolved) {
      await this.core.markStateVariableAndUpstreamDependentsStale({
        component,
        varName: stateVariable
      });
    }
    for (let varName of allStateVariablesAffected) {
      if (component.state[varName].initiallyResolved) {
        this.recordActualChangeInUpstreamDependencies({
          component,
          varName
        });
      }
    }
    await this.addBlockersFromChangedStateVariableDependencies({
      componentIdx,
      stateVariables: allStateVariablesAffected
    });
    return { success: true };
  }
  async replaceDependenciesIfChanged({
    component,
    stateVariable,
    newDependencies,
    allStateVariablesAffected
  }) {
    let currentDeps = this.downstreamDependencies[component.componentIdx][stateVariable];
    let changedDependency = false;
    let newlyCreatedDependencies = [];
    for (let dependencyName in currentDeps) {
      if (!(dependencyName in newDependencies)) {
        changedDependency = true;
        currentDeps[dependencyName].deleteDependency();
      }
    }
    for (let dependencyName in newDependencies) {
      if (dependencyName in currentDeps) {
        let dependencyDefinition = newDependencies[dependencyName];
        let currentDep = currentDeps[dependencyName];
        if (!deepCompare(dependencyDefinition, currentDep.definition)) {
          changedDependency = true;
          currentDeps[dependencyName].deleteDependency();
          let dependencyDefinition2 = newDependencies[dependencyName];
          let dep = new this.dependencyTypes[dependencyDefinition2.dependencyType]({
            component,
            stateVariable,
            allStateVariablesAffected,
            dependencyName,
            dependencyDefinition: dependencyDefinition2,
            dependencyHandler: this
          });
          await dep.initialize();
          newlyCreatedDependencies.push(dep);
        }
      } else {
        changedDependency = true;
        let dependencyDefinition = newDependencies[dependencyName];
        let dep = new this.dependencyTypes[dependencyDefinition.dependencyType]({
          component,
          stateVariable,
          allStateVariablesAffected,
          dependencyName,
          dependencyDefinition,
          dependencyHandler: this
        });
        await dep.initialize();
        newlyCreatedDependencies.push(dep);
      }
    }
    return { changedDependency, newlyCreatedDependencies };
  }
  async checkForDependenciesOnNewComponent(componentIdx) {
    let variablesChanged = [];
    let variablesJustResolved = {};
    if (this.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx]) {
      for (let dep of this.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx]) {
        let upComponent = this._components[dep.upstreamComponentIdx];
        if (!upComponent) {
          continue;
        }
        let upVarsInUpstreamComponent = true;
        for (let upVar of dep.upstreamVariableNames) {
          if (!(upVar in upComponent.state)) {
            upVarsInUpstreamComponent = false;
            break;
          }
        }
        if (!upVarsInUpstreamComponent) {
          continue;
        }
        for (let varName of dep.upstreamVariableNames) {
          let stateVarObj = upComponent.state[varName];
          if (stateVarObj.initiallyResolved) {
            if (!(variablesJustResolved[dep.upstreamComponentIdx] && variablesJustResolved[dep.upstreamComponentIdx][varName])) {
              variablesChanged.push({
                componentIdx: dep.upstreamComponentIdx,
                varName
              });
            }
          }
        }
        for (let varName of dep.upstreamVariableNames) {
          this.deleteFromNeededToResolve({
            componentIdxBlocked: dep.upstreamComponentIdx,
            typeBlocked: "recalculateDownstreamComponents",
            stateVariableBlocked: varName,
            dependencyBlocked: dep.dependencyName,
            blockerComponentIdx: componentIdx,
            blockerType: "componentIdentity"
          });
        }
        let result = await this.resolveIfReady({
          componentIdx: dep.upstreamComponentIdx,
          type: "recalculateDownstreamComponents",
          stateVariable: dep.representativeStateVariable,
          dependency: dep.dependencyName,
          expandComposites: false,
          recurseUpstream: true
        });
        if (result.success) {
          for (let varName of dep.upstreamVariableNames) {
            if (!upComponent.state[varName].initiallyResolved) {
              if (!variablesJustResolved[dep.upstreamComponentIdx]) {
                variablesJustResolved[dep.upstreamComponentIdx] = {};
              }
              variablesJustResolved[dep.upstreamComponentIdx][varName] = true;
            }
          }
        } else {
          for (let varName of dep.upstreamVariableNames) {
            await this.addBlocker({
              blockerComponentIdx: dep.upstreamComponentIdx,
              blockerType: "recalculateDownstreamComponents",
              blockerStateVariable: varName,
              blockerDependency: dep.dependencyName,
              componentIdxBlocked: dep.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName
            });
          }
        }
      }
      delete this.updateTriggers.dependenciesMissingComponentBySpecifiedName[componentIdx];
    }
    return variablesChanged;
  }
  /**
   * Get dependency values for a state variable.
   * By default this consumes dependency change flags; set consumeChanges to false
   * to inspect dependencies without clearing pending change markers.
   */
  async getStateVariableDependencyValues({
    component,
    stateVariable,
    consumeChanges = true
  }) {
    let dependencyValues = {};
    let dependencyChanges = {};
    let dependencyUsedDefault = {};
    let downDeps = this.downstreamDependencies[component.componentIdx][stateVariable];
    for (let dependencyName in downDeps) {
      let dep = downDeps[dependencyName];
      if (dep.onlyToSetInInverseDefinition) {
        continue;
      }
      let { value, changes, usedDefault } = await dep.getValue({
        consumeChanges
      });
      dependencyValues[dependencyName] = value;
      if (Object.keys(changes).length > 0) {
        dependencyChanges[dependencyName] = changes;
      }
      if (usedDefault) {
        dependencyUsedDefault[dependencyName] = usedDefault;
      }
    }
    return {
      dependencyValues,
      changes: dependencyChanges,
      usedDefault: dependencyUsedDefault
    };
  }
  recordActualChangeInUpstreamDependencies({
    component,
    varName,
    changes
  }) {
    let componentIdx = component.componentIdx;
    let upstream = this.upstreamDependencies[componentIdx][varName];
    if (upstream) {
      for (let upDep of upstream) {
        if (upDep.valuesChanged) {
          let ind = upDep.downstreamComponentIndices.indexOf(componentIdx);
          upDep.thawValuesChangedRecord(ind);
          let upValuesChanged = upDep.valuesChanged[ind][varName];
          if (!upValuesChanged) {
            upValuesChanged = upDep.valuesChanged[ind][varName] = {};
          } else if (Object.isFrozen(upValuesChanged)) {
            upValuesChanged = upDep.valuesChanged[ind][varName] = {
              changed: upValuesChanged.changed
            };
          }
          if (component.state[varName] && component.state[varName].isArray) {
            if (upValuesChanged.changed === void 0) {
              upValuesChanged.changed = { arrayKeysChanged: {} };
            } else if (upValuesChanged.changed === true) {
              upValuesChanged.changed = {
                allArrayKeysChanged: true,
                arraySizeChanged: true,
                arrayKeysChanged: {}
              };
            }
            if (changes) {
              if (changes.allArrayKeysChanged) {
                upValuesChanged.changed.allArrayKeysChanged = true;
              }
              if (changes.arraySizeChanged) {
                upValuesChanged.changed.arraySizeChanged = true;
              }
              Object.assign(
                upValuesChanged.changed.arrayKeysChanged,
                changes.arrayKeysChanged
              );
            }
          } else {
            upValuesChanged.changed = true;
          }
        }
      }
    }
  }
  async collateCountersAndPropagateToAncestors(component) {
    let allCounterNames = Object.keys(component.counters);
    for (let childIdx of component.allChildrenOrdered) {
      let child = this._components[childIdx];
      if (child) {
        for (let counterName in child.counters) {
          if (!allCounterNames.includes(counterName)) {
            allCounterNames.push(counterName);
          }
        }
      }
    }
    let foundChange = false;
    for (let counterName of allCounterNames) {
      let counters = component.counters[counterName];
      if (!counters) {
        counters = component.counters[counterName] = {
          dependencies: [],
          componentList: []
        };
      }
      let componentList = [];
      if (counters.dependencies.length > 0) {
        componentList.push(component.componentIdx);
      }
      for (let childIdx of component.allChildrenOrdered) {
        let child = this._components[childIdx];
        if (child) {
          let childCounters = child.counters[counterName];
          if (childCounters) {
            componentList.push(...childCounters.componentList);
          }
        }
      }
      if (componentList.length !== counters.componentList.length || counters.componentList.some(
        (v2, i2) => v2 != componentList[i2]
      )) {
        foundChange = true;
        counters.componentList = componentList;
      }
    }
    if (!foundChange) {
      return { foundChange: false };
    }
    if (!component.ancestors[0]) {
      for (let counterName of allCounterNames) {
        let counters = component.counters[counterName];
        for (let [ind, cIdx] of counters.componentList.entries()) {
          let comp = this._components[cIdx];
          let compCounter = comp.counters[counterName];
          compCounter.value = ind + 1;
          for (let dep of compCounter.dependencies) {
            if (comp.state[dep.representativeStateVariable].initiallyResolved) {
              await this.core.markStateVariableAndUpstreamDependentsStale(
                {
                  component: comp,
                  varName: dep.representativeStateVariable
                }
              );
              for (let varName of dep.upstreamVariableNames) {
                comp.state[varName].forceRecalculation = true;
                this.recordActualChangeInUpstreamDependencies({
                  component: comp,
                  varName
                });
              }
            }
          }
        }
      }
      return { foundChange: true, finishedPropagation: true };
    }
    let parent = this._components[component.ancestors[0].componentIdx];
    if (!(parent && parent.allChildrenOrdered.includes(component.componentIdx))) {
      return { foundChange: true, finishedPropagation: false };
    }
    let parentResult = await this.collateCountersAndPropagateToAncestors(parent);
    if (!parentResult.foundChange) {
      console.error(
        `we found a change in propagating counters for ${component.componentIdx}, but no change for ancestors!`
      );
    }
    return {
      foundChange: true,
      finishedPropagation: parentResult.finishedPropagation
    };
  }
  /**
   * Non-creating variant of `getNeededToResolve`: returns the nested entry
   * if it exists, else `EMPTY_BLOCKERS`, without materializing intermediate
   * levels. `getNeededToResolve` creates the whole nested chain on every
   * lookup, and read-only callers used to leave tens of thousands of empty
   * `{cIdx: {type: {stateVariable: {}}}}` chains behind — the bulk of
   * `resolveBlockers`' retained memory.
   */
  peekNeededToResolve({
    componentIdx,
    type,
    stateVariable,
    dependency
  }) {
    let neededToResolve = this.resolveBlockers.neededToResolve[componentIdx]?.[type];
    if (neededToResolve && stateVariable) {
      neededToResolve = neededToResolve[stateVariable];
      if (neededToResolve && dependency) {
        neededToResolve = neededToResolve[dependency];
      }
    }
    return neededToResolve ?? EMPTY_BLOCKERS;
  }
  /** Non-creating variant of `getResolveBlockedBy`; see `peekNeededToResolve`. */
  peekResolveBlockedBy({
    componentIdx,
    type,
    stateVariable,
    dependency
  }) {
    let resolveBlockedBy = this.resolveBlockers.resolveBlockedBy[componentIdx]?.[type];
    if (resolveBlockedBy && stateVariable) {
      resolveBlockedBy = resolveBlockedBy[stateVariable];
      if (resolveBlockedBy && dependency) {
        resolveBlockedBy = resolveBlockedBy[dependency];
      }
    }
    return resolveBlockedBy ?? EMPTY_BLOCKERS;
  }
  getNeededToResolve({ componentIdx, type, stateVariable, dependency }) {
    let neededToResolveForComponent = this.resolveBlockers.neededToResolve[componentIdx];
    if (!neededToResolveForComponent) {
      neededToResolveForComponent = this.resolveBlockers.neededToResolve[componentIdx] = {};
    }
    let neededToResolve = neededToResolveForComponent[type];
    if (!neededToResolve) {
      neededToResolve = neededToResolveForComponent[type] = {};
    }
    if (stateVariable) {
      let neededToResolveTemp = neededToResolve;
      neededToResolve = neededToResolveTemp[stateVariable];
      if (!neededToResolve) {
        neededToResolve = neededToResolveTemp[stateVariable] = {};
      }
      if (dependency) {
        let neededToResolveTemp2 = neededToResolve;
        neededToResolve = neededToResolveTemp2[dependency];
        if (!neededToResolve) {
          neededToResolve = neededToResolveTemp2[dependency] = {};
        }
      }
    }
    return neededToResolve;
  }
  deleteFromNeededToResolve({
    componentIdxBlocked,
    typeBlocked,
    stateVariableBlocked,
    dependencyBlocked,
    blockerType,
    blockerCode,
    deleteFromReciprocal = true
  }) {
    let codeBlocked = componentIdxBlocked.toString();
    if (stateVariableBlocked) {
      codeBlocked += "|" + stateVariableBlocked;
      if (dependencyBlocked) {
        codeBlocked += "|" + dependencyBlocked;
      }
    }
    let deleteBlockerTypeAndCode = (neededObj) => {
      if (blockerType) {
        if (neededObj[blockerType]) {
          if (blockerCode) {
            let ind = neededObj[blockerType].indexOf(blockerCode);
            if (ind !== -1) {
              neededObj[blockerType].splice(ind, 1);
            }
            if (neededObj[blockerType].length === 0) {
              delete neededObj[blockerType];
            }
            if (deleteFromReciprocal) {
              let [
                blockerComponentIdx,
                blockerStateVariable,
                blockerDependency
              ] = typeof blockerCode === "string" ? blockerCode.split("|") : [blockerCode];
              this.deleteFromResolveBlockedBy({
                blockerComponentIdx,
                blockerType,
                blockerStateVariable,
                blockerDependency,
                typeBlocked,
                codeBlocked,
                deleteFromReciprocal: false
              });
            }
          } else {
            if (deleteFromReciprocal) {
              for (let code of neededObj[blockerType]) {
                let [
                  blockerComponentIdx,
                  blockerStateVariable,
                  blockerDependency
                ] = typeof code === "string" ? code.split("|") : [code];
                this.deleteFromResolveBlockedBy({
                  blockerComponentIdx,
                  blockerType,
                  blockerStateVariable,
                  blockerDependency,
                  typeBlocked,
                  codeBlocked,
                  deleteFromReciprocal: false
                });
              }
            }
            delete neededObj[blockerType];
          }
        }
      } else {
        if (deleteFromReciprocal) {
          for (let type in neededObj) {
            for (let code of neededObj[type]) {
              let [
                blockerComponentIdx,
                blockerStateVariable,
                blockerDependency
              ] = typeof code === "string" ? code.split("|") : [code];
              this.deleteFromResolveBlockedBy({
                blockerComponentIdx,
                blockerType: type,
                blockerStateVariable,
                blockerDependency,
                typeBlocked,
                codeBlocked,
                deleteFromReciprocal: false
              });
            }
          }
        }
      }
    };
    let neededToResolveForComponent = this.resolveBlockers.neededToResolve[componentIdxBlocked];
    if (neededToResolveForComponent) {
      let neededToResolveForType = neededToResolveForComponent[typeBlocked];
      if (neededToResolveForType) {
        if (stateVariableBlocked) {
          let neededToResolveForStateVariable = neededToResolveForType[stateVariableBlocked];
          if (neededToResolveForStateVariable) {
            if (dependencyBlocked) {
              let neededToResolveForDependency = neededToResolveForStateVariable[dependencyBlocked];
              if (neededToResolveForDependency) {
                deleteBlockerTypeAndCode(
                  neededToResolveForDependency
                );
                if (!blockerType || Object.keys(neededToResolveForDependency).length === 0) {
                  delete neededToResolveForStateVariable[dependencyBlocked];
                }
              }
              if (Object.keys(neededToResolveForStateVariable).length === 0) {
                delete neededToResolveForType[stateVariableBlocked];
              }
            } else {
              deleteBlockerTypeAndCode(
                neededToResolveForStateVariable
              );
              if (!blockerType || Object.keys(neededToResolveForStateVariable).length === 0) {
                delete neededToResolveForType[stateVariableBlocked];
              }
            }
          }
          if (Object.keys(neededToResolveForType).length === 0) {
            delete neededToResolveForComponent[typeBlocked];
          }
        } else {
          deleteBlockerTypeAndCode(neededToResolveForType);
          if (!blockerType || Object.keys(neededToResolveForType).length === 0) {
            delete neededToResolveForComponent[typeBlocked];
          }
        }
      }
      if (Object.keys(neededToResolveForComponent).length === 0) {
        delete this.resolveBlockers.neededToResolve[componentIdxBlocked];
      }
    }
  }
  checkIfHaveNeededToResolve({
    componentIdx,
    type,
    stateVariable,
    dependency
  }) {
    let neededToResolveForComponent = this.resolveBlockers.neededToResolve[componentIdx];
    if (!neededToResolveForComponent) {
      return false;
    }
    let neededToResolve = neededToResolveForComponent[type];
    if (!neededToResolve) {
      return false;
    }
    if (stateVariable) {
      let neededToResolveTemp = neededToResolve;
      neededToResolve = neededToResolveTemp[stateVariable];
      if (!neededToResolve) {
        return false;
      }
      if (dependency) {
        let neededToResolveTemp2 = neededToResolve;
        neededToResolve = neededToResolveTemp2[dependency];
        if (!neededToResolve) {
          return false;
        }
      }
    }
    return Object.keys(neededToResolve).length > 0;
  }
  getResolveBlockedBy({
    componentIdx,
    type,
    stateVariable,
    dependency
  }) {
    let resolveBlockedByComponent = this.resolveBlockers.resolveBlockedBy[componentIdx];
    if (!resolveBlockedByComponent) {
      resolveBlockedByComponent = this.resolveBlockers.resolveBlockedBy[componentIdx] = {};
    }
    let resolveBlockedBy = resolveBlockedByComponent[type];
    if (!resolveBlockedBy) {
      resolveBlockedBy = resolveBlockedByComponent[type] = {};
    }
    if (stateVariable) {
      let resolveBlockedByTemp = resolveBlockedBy;
      resolveBlockedBy = resolveBlockedByTemp[stateVariable];
      if (!resolveBlockedBy) {
        resolveBlockedBy = resolveBlockedByTemp[stateVariable] = {};
      }
      if (dependency) {
        let resolveBlockedByTemp2 = resolveBlockedBy;
        resolveBlockedBy = resolveBlockedByTemp2[dependency];
        if (!resolveBlockedBy) {
          resolveBlockedBy = resolveBlockedByTemp2[dependency] = {};
        }
      }
    }
    return resolveBlockedBy;
  }
  deleteFromResolveBlockedBy({
    blockerComponentIdx,
    blockerType,
    blockerStateVariable,
    blockerDependency,
    typeBlocked,
    codeBlocked,
    deleteFromReciprocal = true
  }) {
    let blockerCode = blockerComponentIdx.toString();
    if (blockerStateVariable) {
      blockerCode += "|" + blockerStateVariable;
      if (blockerDependency) {
        blockerCode += "|" + blockerDependency;
      }
    }
    let deleteTypeAndCodeBlocked = (neededObj) => {
      if (typeBlocked) {
        if (neededObj[typeBlocked]) {
          if (codeBlocked) {
            let ind = neededObj[typeBlocked].indexOf(codeBlocked);
            if (ind !== -1) {
              neededObj[typeBlocked].splice(ind, 1);
            }
            if (neededObj[typeBlocked].length === 0) {
              delete neededObj[typeBlocked];
            }
            if (deleteFromReciprocal) {
              let [
                componentIdxBlocked,
                stateVariableBlocked,
                dependencyBlocked
              ] = typeof codeBlocked === "string" ? codeBlocked.split("|") : [codeBlocked];
              this.deleteFromNeededToResolve({
                componentIdxBlocked,
                typeBlocked,
                stateVariableBlocked,
                dependencyBlocked,
                blockerType,
                blockerCode,
                deleteFromReciprocal: false
              });
            }
          } else {
            if (deleteFromReciprocal) {
              for (let code of neededObj[typeBlocked]) {
                let [
                  componentIdxBlocked,
                  stateVariableBlocked,
                  dependencyBlocked
                ] = typeof code === "string" ? code.split("|") : [code];
                this.deleteFromNeededToResolve({
                  componentIdxBlocked,
                  typeBlocked,
                  stateVariableBlocked,
                  dependencyBlocked,
                  blockerType,
                  blockerCode,
                  deleteFromReciprocal: false
                });
              }
            }
            delete neededObj[typeBlocked];
          }
        }
      } else {
        if (deleteFromReciprocal) {
          for (let type in neededObj) {
            for (let code of neededObj[type]) {
              let [
                componentIdxBlocked,
                stateVariableBlocked,
                dependencyBlocked
              ] = typeof code === "string" ? code.split("|") : [code];
              this.deleteFromNeededToResolve({
                componentIdxBlocked,
                typeBlocked: type,
                stateVariableBlocked,
                dependencyBlocked,
                blockerType,
                blockerCode,
                deleteFromReciprocal: false
              });
            }
          }
        }
      }
    };
    let resolveBlockedByForComponent = this.resolveBlockers.resolveBlockedBy[blockerComponentIdx];
    if (resolveBlockedByForComponent) {
      let resolveBlockedByForType = resolveBlockedByForComponent[blockerType];
      if (resolveBlockedByForType) {
        if (blockerStateVariable) {
          let resolveBlockedByForStateVariable = resolveBlockedByForType[blockerStateVariable];
          if (resolveBlockedByForStateVariable) {
            if (blockerDependency) {
              let resolveBlockedByForDependency = resolveBlockedByForStateVariable[blockerDependency];
              if (resolveBlockedByForDependency) {
                deleteTypeAndCodeBlocked(
                  resolveBlockedByForDependency
                );
                if (!typeBlocked || Object.keys(resolveBlockedByForDependency).length === 0) {
                  delete resolveBlockedByForStateVariable[blockerDependency];
                }
              }
              if (Object.keys(resolveBlockedByForStateVariable).length === 0) {
                delete resolveBlockedByForType[blockerStateVariable];
              }
            } else {
              deleteTypeAndCodeBlocked(
                resolveBlockedByForStateVariable
              );
              if (!typeBlocked || Object.keys(resolveBlockedByForStateVariable).length === 0) {
                delete resolveBlockedByForType[blockerStateVariable];
              }
            }
          }
          if (Object.keys(resolveBlockedByForType).length === 0) {
            delete resolveBlockedByForComponent[blockerType];
          }
        } else {
          deleteTypeAndCodeBlocked(resolveBlockedByForType);
          if (!typeBlocked || Object.keys(resolveBlockedByForType).length === 0) {
            delete resolveBlockedByForComponent[blockerType];
          }
        }
      }
      if (Object.keys(resolveBlockedByForComponent).length === 0) {
        delete this.resolveBlockers.resolveBlockedBy[blockerComponentIdx];
      }
    }
  }
  async addBlocker({
    blockerComponentIdx,
    blockerType,
    blockerStateVariable,
    blockerDependency,
    componentIdxBlocked,
    typeBlocked,
    stateVariableBlocked,
    dependencyBlocked
  }) {
    let blockerCode = blockerComponentIdx.toString();
    if (blockerStateVariable) {
      blockerCode += "|" + blockerStateVariable;
      if (blockerDependency) {
        blockerCode += "|" + blockerDependency;
      }
    }
    let codeBlocked = componentIdxBlocked;
    if (stateVariableBlocked) {
      codeBlocked += "|" + stateVariableBlocked;
      if (dependencyBlocked) {
        codeBlocked += "|" + dependencyBlocked;
      }
    }
    let neededForBlocked = this.getNeededToResolve({
      componentIdx: componentIdxBlocked,
      type: typeBlocked,
      stateVariable: stateVariableBlocked,
      dependency: dependencyBlocked
    });
    let neededToResolveBlocked = neededForBlocked[blockerType];
    if (!neededToResolveBlocked) {
      neededForBlocked[blockerType] = [blockerCode];
    } else if (neededToResolveBlocked.includes(blockerCode)) {
      return;
    } else {
      neededToResolveBlocked.push(blockerCode);
    }
    if (typeBlocked === "stateVariable") {
      let component = this._components[componentIdxBlocked];
      if (component) {
        let stateVarObj = component.state[stateVariableBlocked];
        stateVarObj.isResolved = false;
        if (stateVarObj.initiallyResolved) {
          await this.core.markStateVariableAndUpstreamDependentsStale(
            {
              component,
              varName: stateVariableBlocked
            }
          );
          let upDeps = this.upstreamDependencies[componentIdxBlocked][stateVariableBlocked];
          if (upDeps) {
            for (let dep of upDeps) {
              if (this._components[dep.upstreamComponentIdx]) {
                for (let vName of dep.upstreamVariableNames) {
                  await this.addBlocker({
                    blockerComponentIdx: componentIdxBlocked,
                    blockerType: "stateVariable",
                    blockerStateVariable: stateVariableBlocked,
                    componentIdxBlocked: dep.upstreamComponentIdx,
                    typeBlocked: "stateVariable",
                    stateVariableBlocked: vName
                  });
                }
              }
            }
          }
        }
      }
    }
    let resolvedBlockedByBlocker = this.getResolveBlockedBy({
      componentIdx: blockerComponentIdx,
      type: blockerType,
      stateVariable: blockerStateVariable,
      dependency: blockerDependency
    });
    let blockedByBlocker = resolvedBlockedByBlocker[typeBlocked];
    if (!blockedByBlocker) {
      resolvedBlockedByBlocker[typeBlocked] = [codeBlocked];
    } else if (!blockedByBlocker.includes(codeBlocked)) {
      blockedByBlocker.push(codeBlocked);
    }
    this.resetCircularResolveBlockerCheckPassed({
      componentIdx: componentIdxBlocked,
      type: typeBlocked,
      stateVariable: stateVariableBlocked,
      dependency: dependencyBlocked
    });
    this.checkForCircularResolveBlocker({
      componentIdx: componentIdxBlocked,
      type: typeBlocked,
      stateVariable: stateVariableBlocked,
      dependency: dependencyBlocked
    });
  }
  async processNewlyResolved({
    componentIdxNewlyResolved,
    typeNewlyResolved,
    stateVariableNewlyResolved,
    dependencyNewlyResolved,
    expandComposites = true,
    force = false,
    recurseUpstream = false
  }) {
    if (typeNewlyResolved === "stateVariable") {
      let component = this._components[componentIdxNewlyResolved];
      if (component) {
        let stateVarObj = component.state[stateVariableNewlyResolved];
        if (stateVarObj) {
          component.state[stateVariableNewlyResolved].isResolved = true;
          component.state[stateVariableNewlyResolved].initiallyResolved = true;
        }
      }
    } else if (typeNewlyResolved === "componentIdentity") {
      if (!(componentIdxNewlyResolved in this._components || force)) {
        return { success: false };
      }
    } else {
      if (typeNewlyResolved === "recalculateDownstreamComponents") {
        let dep;
        try {
          dep = this.downstreamDependencies[componentIdxNewlyResolved][stateVariableNewlyResolved][dependencyNewlyResolved];
        } catch (e3) {
        }
        if (dep) {
          let result = await dep.recalculateDownstreamComponents({
            force
          });
          if (!(result.success || force)) {
            return result;
          }
          for (let varName of dep.upstreamVariableNames) {
            this.deleteFromNeededToResolve({
              componentIdxBlocked: dep.upstreamComponentIdx,
              typeBlocked: "stateVariable",
              stateVariableBlocked: varName,
              blockerType: "recalculateDownstreamComponents",
              blockerCode: dep.upstreamComponentIdx + "|" + varName + "|" + dependencyNewlyResolved
            });
          }
          for (let varName of dep.upstreamVariableNames) {
            await this.resolveIfReady({
              componentIdx: dep.upstreamComponentIdx,
              type: "stateVariable",
              stateVariable: varName,
              expandComposites,
              force,
              recurseUpstream
            });
          }
          return { success: true };
        }
      } else if (typeNewlyResolved === "determineDependencies") {
        let dep;
        try {
          dep = this.downstreamDependencies[componentIdxNewlyResolved][stateVariableNewlyResolved][dependencyNewlyResolved];
        } catch (e3) {
        }
        if (dep) {
          let neededForItem = this.peekNeededToResolve({
            componentIdx: componentIdxNewlyResolved,
            type: "stateVariable",
            stateVariable: stateVariableNewlyResolved
          });
          let foundDetermineDependenciesNotResolved = false;
          let determineDepsDependencies = [];
          if (neededForItem.determineDependencies) {
            for (let code of neededForItem.determineDependencies) {
              let [
                blockerComponentIdx,
                blockerStateVariable,
                blockerDependency
              ] = typeof code === "string" ? code.split("|") : [code];
              if (this.checkIfHaveNeededToResolve({
                componentIdx: componentIdxNewlyResolved,
                type: "determineDependency",
                stateVariable: stateVariableNewlyResolved,
                dependency: blockerDependency
              })) {
                foundDetermineDependenciesNotResolved = true;
                break;
              }
              determineDepsDependencies.push(blockerDependency);
            }
          }
          if (foundDetermineDependenciesNotResolved) {
            return { success: true };
          }
          let result = await this.updateDependencies({
            componentIdx: componentIdxNewlyResolved,
            stateVariable: stateVariableNewlyResolved,
            dependency: dependencyNewlyResolved
          });
          if (!result.success) {
            return { success: false };
          }
          for (let varName of dep.upstreamVariableNames) {
            for (let dependency of determineDepsDependencies) {
              this.deleteFromResolveBlockedBy({
                blockerComponentIdx: dep.upstreamComponentIdx,
                blockerType: "determineDependencies",
                blockerStateVariable: varName,
                blockerDependency: dependency
              });
            }
            await this.resolveIfReady({
              componentIdx: componentIdxNewlyResolved,
              type: "stateVariable",
              stateVariable: varName,
              expandComposites,
              force,
              recurseUpstream
            });
          }
          return { success: true };
        }
      } else if (typeNewlyResolved === "childMatches") {
        let component = this._components[componentIdxNewlyResolved];
        if (component) {
          if (!component.childrenMatched) {
            let result = await deriveChildResultsFromDefiningChildren({
              core: this.core,
              parent: component,
              expandComposites,
              forceExpandComposites: force
            });
            if (!result.skipping && !(component.childrenMatchedWithPlaceholders || force)) {
              return { success: false };
            }
          }
        }
      } else if (typeNewlyResolved === "expandComposite") {
        let composite = this._components[componentIdxNewlyResolved];
        if (!composite.isExpanded) {
          if (this.core.updateInfo.compositesBeingExpanded.includes(
            componentIdxNewlyResolved
          )) {
            return { success: false };
          }
          await expandCompositeComponent({
            core: this.core,
            component: this._components[componentIdxNewlyResolved]
          });
        }
      } else {
        throw Error(
          `Unrecognized type newly resolved: ${typeNewlyResolved}`
        );
      }
    }
    let resolveBlockedByNewlyResolved = this.peekResolveBlockedBy({
      componentIdx: componentIdxNewlyResolved,
      type: typeNewlyResolved,
      stateVariable: stateVariableNewlyResolved,
      dependency: dependencyNewlyResolved
    });
    resolveBlockedByNewlyResolved = Object.assign(
      {},
      resolveBlockedByNewlyResolved
    );
    for (let type in resolveBlockedByNewlyResolved) {
      for (let code of [...resolveBlockedByNewlyResolved[type]]) {
        this.deleteFromResolveBlockedBy({
          blockerComponentIdx: componentIdxNewlyResolved,
          blockerType: typeNewlyResolved,
          blockerStateVariable: stateVariableNewlyResolved,
          blockerDependency: dependencyNewlyResolved,
          typeBlocked: type,
          codeBlocked: code
        });
        if (recurseUpstream) {
          let [cIdx, vName, depName] = typeof code === "string" ? code.split("|") : [code];
          await this.resolveIfReady({
            componentIdx: cIdx,
            type,
            stateVariable: vName,
            dependency: depName,
            expandComposites,
            force,
            recurseUpstream
          });
        }
      }
    }
    return { success: true };
  }
  async resolveStateVariablesIfReady({ component, stateVariables }) {
    let componentIdx = component.componentIdx;
    for (let varName of stateVariables) {
      await this.resolveIfReady({
        componentIdx,
        type: "stateVariable",
        stateVariable: varName,
        expandComposites: false
        // recurseUpstream: true
      });
    }
  }
  async resolveIfReady({
    componentIdx,
    type,
    stateVariable,
    dependency,
    expandComposites = true,
    force = false,
    recurseUpstream = false
  }) {
    if (type === "stateVariable") {
      await this.ensureStateVariableDependenciesSetUp(
        componentIdx,
        stateVariable
      );
    }
    let haveNeededToResolve = this.checkIfHaveNeededToResolve({
      componentIdx,
      type,
      stateVariable,
      dependency
    });
    if (haveNeededToResolve) {
      return { success: false };
    }
    this.deleteFromNeededToResolve({
      componentIdxBlocked: componentIdx,
      typeBlocked: type,
      stateVariableBlocked: stateVariable,
      dependencyBlocked: dependency
    });
    let result = await this.processNewlyResolved({
      componentIdxNewlyResolved: componentIdx,
      typeNewlyResolved: type,
      stateVariableNewlyResolved: stateVariable,
      dependencyNewlyResolved: dependency,
      expandComposites,
      force,
      recurseUpstream
    });
    return result;
  }
  async resolveItem({
    componentIdx,
    type,
    stateVariable,
    dependency,
    force = false,
    recurseUpstream = false,
    expandComposites = true,
    numPreviouslyNeeded
  }) {
    if (type === "stateVariable") {
      await this.ensureStateVariableDependenciesSetUp(
        componentIdx,
        stateVariable
      );
    }
    let neededForItem = this.peekNeededToResolve({
      componentIdx,
      type,
      stateVariable,
      dependency
    });
    let determineDepsBlockers = neededForItem.determineDependencies;
    if (determineDepsBlockers && determineDepsBlockers.length > 0) {
      for (let blockerCode of [...determineDepsBlockers]) {
        let [
          blockerComponentIdx,
          blockerStateVariable,
          blockerDependency
        ] = typeof blockerCode === "string" ? blockerCode.split("|") : [blockerCode];
        let result = await this.resolveItem({
          componentIdx: blockerComponentIdx,
          type: "determineDependencies",
          stateVariable: blockerStateVariable,
          dependency: blockerDependency,
          force,
          //recurseUpstream
          expandComposites
        });
        if (!result.success) {
          return result;
        }
      }
    }
    let stateVarObj;
    if (type === "stateVariable" && this._components[componentIdx]) {
      stateVarObj = this._components[componentIdx].state[stateVariable];
      if (stateVarObj) {
        stateVarObj.currentlyResolving = true;
      }
    }
    let previousNFailures = Infinity;
    let nFailures = Infinity;
    while (Object.keys(neededForItem).length > 0 || nFailures > 0) {
      if (Number.isFinite(nFailures) && nFailures >= previousNFailures) {
        break;
      }
      if (nFailures > 0) {
        neededForItem = this.peekNeededToResolve({
          componentIdx,
          type,
          stateVariable,
          dependency
        });
      }
      previousNFailures = nFailures;
      nFailures = 0;
      for (let blockerType in neededForItem) {
        if (blockerType === "determineDependencies") {
          throw Error(
            `Shouldn't have determine dependencies blocker after determining dependencies: ${componentIdx}, ${type}, ${stateVariable}, ${dependency}`
          );
        }
        for (let code of [...neededForItem[blockerType]]) {
          let [
            blockerComponentIdx,
            blockerStateVariable,
            blockerDependency
          ] = typeof code === "string" ? code.split("|") : [code];
          let result = await this.resolveItem({
            componentIdx: blockerComponentIdx,
            type: blockerType,
            stateVariable: blockerStateVariable,
            dependency: blockerDependency,
            //force, //recurseUpstream
            expandComposites
          });
          if (!result.success) {
            if (force) {
              nFailures++;
            } else {
              return result;
            }
          }
        }
      }
    }
    if (nFailures > 0) {
      neededForItem = this.peekNeededToResolve({
        componentIdx,
        type,
        stateVariable,
        dependency
      });
      while (Object.keys(neededForItem).length > 0) {
        for (let blockerType in neededForItem) {
          if (blockerType === "determineDependencies") {
            throw Error(
              `Shouldn't have determine dependencies blocker after determining dependencies: ${componentIdx}, ${type}, ${stateVariable}, ${dependency}`
            );
          }
          for (let code of [...neededForItem[blockerType]]) {
            let [
              blockerComponentIdx,
              blockerStateVariable,
              blockerDependency
            ] = typeof code === "string" ? code.split("|") : [code];
            let result = await this.resolveItem({
              componentIdx: blockerComponentIdx,
              type: blockerType,
              stateVariable: blockerStateVariable,
              dependency: blockerDependency,
              force,
              //recurseUpstream
              expandComposites
            });
            if (!result.success) {
              return result;
            }
          }
        }
      }
    }
    if (stateVarObj) {
      stateVarObj.currentlyResolving = false;
    }
    let finalResult = await this.resolveIfReady({
      componentIdx,
      type,
      stateVariable,
      dependency,
      force,
      recurseUpstream,
      expandComposites
    });
    if (!finalResult.success) {
      let stillNeededForItem = this.peekNeededToResolve({
        componentIdx,
        type,
        stateVariable,
        dependency
      });
      let numNeeded = Object.keys(stillNeededForItem).length;
      if (numNeeded > 0) {
        if (numPreviouslyNeeded === void 0 || numNeeded < numPreviouslyNeeded) {
          finalResult = await this.resolveItem({
            componentIdx,
            type,
            stateVariable,
            dependency,
            force,
            recurseUpstream,
            expandComposites,
            numPreviouslyNeeded: numNeeded
          });
        }
      }
    }
    return finalResult;
  }
  checkForCircularResolveBlocker({
    componentIdx,
    type,
    stateVariable,
    dependency,
    previouslyVisited = []
  }) {
    let code = componentIdx.toString();
    if (stateVariable) {
      code += "|" + stateVariable;
      if (dependency) {
        code += "|" + dependency;
      }
    }
    let identifier = code + "|" + type;
    if (previouslyVisited.includes(identifier)) {
      console.log("found circular", identifier, previouslyVisited);
      let componentNameRe = /^([^|]*)\|/;
      let componentsInvolved = previouslyVisited.map(
        (x2) => this.components[x2.match(componentNameRe)[1]]
      );
      let message = this.getCircularDependencyMessage(componentsInvolved);
      throw Error(message);
    } else {
      previouslyVisited = [...previouslyVisited, identifier];
    }
    if (!this.circularResolveBlockedCheckPassed[identifier]) {
      this.circularResolveBlockedCheckPassed[identifier] = true;
      let neededForItem = this.peekNeededToResolve({
        componentIdx,
        type,
        stateVariable,
        dependency
      });
      for (let blockerType in neededForItem) {
        for (let blockerCode of neededForItem[blockerType]) {
          let [
            blockerComponentIdx,
            blockerStateVariable,
            blockerDependency
          ] = typeof blockerCode === "string" ? blockerCode.split("|") : [blockerCode];
          this.checkForCircularResolveBlocker({
            componentIdx: blockerComponentIdx,
            type: blockerType,
            stateVariable: blockerStateVariable,
            dependency: blockerDependency,
            previouslyVisited
          });
        }
      }
    }
  }
  getCircularDependencyMessage(componentsInvolved) {
    let uniqueComponentNames = [];
    let componentTypesForUniqueNames = [];
    let linesForUniqueNames = [];
    for (let comp of componentsInvolved) {
      let name = comp.componentIdx;
      let relativeName = name;
      if (relativeName) ;
      else {
        relativeName = comp.doenetAttributes?.prescribedName;
      }
      if (relativeName) {
        if (!uniqueComponentNames.includes(relativeName)) {
          uniqueComponentNames.push(relativeName);
          componentTypesForUniqueNames.push(comp.componentType);
          let position = comp.position;
          let addedLine = false;
          if (position) {
            if (position.doenetMLId === 0 && position.start.line === void 0) {
              Object.assign(
                position,
                getLineCharRange(
                  position,
                  this.core.doenetMLNewlines
                )
              );
            }
            let lineBegin = position.start.line;
            if (lineBegin) {
              addedLine = true;
              let lineEnd = position.end.line;
              if (lineEnd === lineBegin) {
                linesForUniqueNames.push(`line ${lineBegin}`);
              } else {
                linesForUniqueNames.push(
                  `lines ${lineBegin}–${lineEnd}`
                );
              }
            }
          }
          if (!addedLine) {
            linesForUniqueNames.push(null);
          }
        }
      }
    }
    if (uniqueComponentNames.length === 0) {
      let comp = componentsInvolved[0];
      let name = comp.componentIdx;
      let relativeName = name;
      if (relativeName) ;
      else {
        relativeName = comp.doenetAttributes?.prescribedName;
      }
      uniqueComponentNames = [relativeName];
      componentTypesForUniqueNames = [comp.componentType];
    }
    let message = "";
    for (let [ind, cType] of componentTypesForUniqueNames.entries()) {
      if (message) {
        message += ", ";
      }
      message += `<${cType}>`;
      let lineRange = linesForUniqueNames[ind];
      if (lineRange) {
        message += ` (${lineRange})`;
      } else {
        let name = uniqueComponentNames[ind];
        if (name[0] !== "_") {
          message += ` (named "${name}")`;
        }
      }
    }
    return `Circular dependency involving these components: ${message}.`;
  }
  resetCircularResolveBlockerCheckPassed({
    componentIdx,
    type,
    stateVariable,
    dependency
  }) {
    let code = componentIdx.toString();
    if (stateVariable) {
      code += "|" + stateVariable;
      if (dependency) {
        code += "|" + dependency;
      }
    }
    let identifier = code + "|" + type;
    if (this.circularResolveBlockedCheckPassed[identifier]) {
      delete this.circularResolveBlockedCheckPassed[identifier];
      let resolveBlockedBy = this.peekResolveBlockedBy({
        componentIdx,
        type,
        stateVariable,
        dependency
      });
      for (let typeBlocked in resolveBlockedBy) {
        for (let codeBlocked of resolveBlockedBy[typeBlocked]) {
          let [
            componentIdxBlocked,
            stateVariableBlocked,
            dependencyBlocked
          ] = typeof codeBlocked === "string" ? codeBlocked.split("|") : [codeBlocked];
          this.resetCircularResolveBlockerCheckPassed({
            componentIdx: componentIdxBlocked,
            type: typeBlocked,
            stateVariable: stateVariableBlocked,
            dependency: dependencyBlocked
          });
        }
      }
    }
  }
  get components() {
    return this._components;
  }
  set components(_value) {
  }
}
class ActionTriggerScheduler {
  constructor({ core: core2 }) {
    this.core = core2;
    this.stateVariableChangeTriggers = {};
    this.actionsChangedToActions = {};
    this.originsOfActionsChangedToActions = {};
  }
  /**
   * Clear all per-document trigger and chain registrations. Called from
   * `Core.generateDast` so state from any previous run does not leak in.
   */
  reset() {
    this.stateVariableChangeTriggers = {};
    this.actionsChangedToActions = {};
    this.originsOfActionsChangedToActions = {};
  }
  /**
   * Poll every registered trigger variable for a value change since the
   * last poll, and dispatch the trigger's action if so. Renderer state
   * is normally updated by the caller; pass
   * `updateRenderersIfTriggered: true` to push a renderer update inline
   * after any action fires.
   *
   * TODO: this scans every registered trigger each tick. We could
   * narrow to components that actually changed by intersecting with
   * `componentsToUpdateRenderers`, but that set excludes non-rendered
   * components that may still carry triggers.
   */
  async processStateVariableTriggers(updateRenderersIfTriggered = false) {
    let triggeredAction = false;
    for (const componentIdxStr in this.stateVariableChangeTriggers) {
      const componentIdx = Number(componentIdxStr);
      let component = this.core._components[componentIdx];
      for (let stateVariable in this.stateVariableChangeTriggers[componentIdx]) {
        let triggerInstructions = this.stateVariableChangeTriggers[componentIdx][stateVariable];
        let value = await component.state[stateVariable].value;
        if (value !== triggerInstructions.previousValue) {
          let previousValue = triggerInstructions.previousValue;
          triggerInstructions.previousValue = value;
          let action = component.actions[triggerInstructions.action];
          if (action) {
            await this.core.performAction({
              componentIdx,
              actionName: triggerInstructions.action,
              args: {
                stateValues: { [stateVariable]: value },
                previousValues: {
                  [stateVariable]: previousValue
                },
                skipRendererUpdate: true
              }
            });
            triggeredAction = true;
          }
        }
      }
    }
    if (triggeredAction && updateRenderersIfTriggered) {
      await this.core.updateAllChangedRenderers();
    }
  }
  /**
   * Queue every `mustEvaluate` state variable on `componentIdx` into
   * `updateInfo.stateVariablesToEvaluate`, so that a later flush forces
   * them to compute even if nothing read them.
   */
  recordStateVariablesMustEvaluate(componentIdx) {
    let comp = this.core._components[componentIdx];
    for (let vName in comp.state) {
      if (comp.state[vName].mustEvaluate) {
        this.core.updateInfo.stateVariablesToEvaluate.push({
          componentIdx,
          stateVariable: vName
        });
      }
    }
  }
  /**
   * Re-sync the chained-action graph for `component` after the value of
   * one of its `chainActionOnActionOfStateVariableTargets` state
   * variables changed. Computes the diff between the previously
   * registered targets and the new ones: register newly added targets
   * in `actionsChangedToActions`, drop targets that are no longer
   * present, and update the per-component bookkeeping in
   * `originsOfActionsChangedToActions`.
   */
  async checkForActionChaining({
    component,
    stateVariables
  }) {
    if (!component) {
      return;
    }
    if (!stateVariables) {
      stateVariables = Object.keys(component.state);
    }
    for (let varName of stateVariables) {
      let stateVarObj = component.state[varName];
      if (stateVarObj.chainActionOnActionOfStateVariableTargets) {
        let chainInfo = stateVarObj.chainActionOnActionOfStateVariableTargets;
        let targetIds = await stateVarObj.value;
        let originObj = this.originsOfActionsChangedToActions[component.componentIdx];
        let previousIds;
        if (originObj) {
          previousIds = originObj[varName];
        }
        if (!previousIds) {
          previousIds = [];
        }
        let newTargets = [];
        if (Array.isArray(targetIds)) {
          newTargets = [...targetIds];
          for (let id of newTargets) {
            let indPrev = previousIds.indexOf(id);
            if (indPrev === -1) {
              let componentActionsChained = this.actionsChangedToActions[id];
              if (!componentActionsChained) {
                componentActionsChained = this.actionsChangedToActions[id] = [];
              }
              componentActionsChained.push({
                componentIdx: component.componentIdx,
                actionName: chainInfo.triggeredAction,
                stateVariableDefiningChain: varName,
                args: {}
              });
            } else {
              previousIds.splice(indPrev, 1);
            }
          }
        }
        for (let idToNoLongerChain of previousIds) {
          let componentActionsChained = this.actionsChangedToActions[idToNoLongerChain];
          if (componentActionsChained) {
            let newComponentActionsChained = [];
            for (let chainedInfo of componentActionsChained) {
              if (chainedInfo.componentIdx !== component.componentIdx || chainedInfo.stateVariableDefiningChain !== varName) {
                newComponentActionsChained.push(chainedInfo);
              }
            }
            this.actionsChangedToActions[idToNoLongerChain] = newComponentActionsChained;
          }
        }
        if (newTargets.length > 0) {
          if (!originObj) {
            originObj = this.originsOfActionsChangedToActions[component.componentIdx] = {};
          }
          originObj[varName] = newTargets;
        } else if (originObj) {
          delete originObj[varName];
          if (Object.keys(originObj).length === 0) {
            delete this.originsOfActionsChangedToActions[component.componentIdx];
          }
        }
      }
    }
  }
  /**
   * After an action runs on `componentIdx`, fire any actions chained to
   * it (and to any composite that this component shadows via a bare
   * reference like `$P` — but not via `extend`/`copy`, since those
   * don't propagate authored intent). Re-syncs the chain graph first
   * so `componentsToUpdateActionChaining` doesn't miss any pending
   * registration changes.
   */
  async triggerChainedActions({
    componentIdx,
    triggeringAction,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    for (const cIdxStr in this.core.updateInfo.componentsToUpdateActionChaining) {
      await this.checkForActionChaining({
        component: this.core._components[Number(cIdxStr)],
        stateVariables: this.core.updateInfo.componentsToUpdateActionChaining[cIdxStr]
      });
    }
    this.core.updateInfo.componentsToUpdateActionChaining = {};
    let actionsToChain = [];
    let cIdx = componentIdx;
    while (true) {
      let comp = this.core._components[cIdx];
      let id = cIdx;
      if (triggeringAction) {
        id = id + "|" + triggeringAction;
      }
      if (this.actionsChangedToActions[id]) {
        actionsToChain.push(...this.actionsChangedToActions[id]);
      }
      if (comp?.shadows) {
        let composite = this.core._components[comp.shadows.compositeIdx];
        if (composite.attributes.createComponentOfType != null) {
          break;
        }
        cIdx = comp.shadows.componentIdx;
      } else {
        break;
      }
    }
    for (let chainedActionInstructions of actionsToChain) {
      chainedActionInstructions = { ...chainedActionInstructions };
      if (chainedActionInstructions.args) {
        chainedActionInstructions.args = {
          ...chainedActionInstructions.args
        };
      } else {
        chainedActionInstructions.args = {};
      }
      chainedActionInstructions.args.skipRendererUpdate = true;
      await this.core.performAction(chainedActionInstructions);
    }
    if (!skipRendererUpdate) {
      await this.core.updateAllChangedRenderers(
        sourceInformation,
        actionId
      );
    }
  }
}
class AutoSubmitManager {
  constructor({ core: core2 }) {
    this.core = core2;
    this.answersToSubmit = [];
    this.submitAnswersTimeout = null;
  }
  recordAnswer(componentIdx) {
    if (!this.answersToSubmit.includes(componentIdx)) {
      this.answersToSubmit.push(componentIdx);
    }
    if (this.submitAnswersTimeout !== null) {
      clearTimeout(this.submitAnswersTimeout);
    }
    this.submitAnswersTimeout = setTimeout(() => {
      this.submitAnswersTimeout = null;
      this.submitNow().catch(reportTimerError(TimerLabels.autoSubmit));
    }, 1e3);
  }
  async submitNow() {
    let toSubmit = this.answersToSubmit;
    this.answersToSubmit = [];
    for (let componentIdx of toSubmit) {
      let component = this.core._components[componentIdx];
      if (component.actions.submitAnswer) {
        await this.core.requestAction({
          componentIdx,
          actionName: "submitAnswer"
        });
      }
    }
  }
  /**
   * Cancel any pending debounce and submit immediately. Used during
   * `Core.terminate()` to flush queued answers before shutdown.
   */
  async flush() {
    if (this.submitAnswersTimeout !== null) {
      clearTimeout(this.submitAnswersTimeout);
      this.submitAnswersTimeout = null;
      await this.submitNow();
    }
  }
}
async function deleteComponents({
  core: core2,
  components,
  deleteUpstreamDependencies = true,
  skipProcessingChildrenOfParents = []
}) {
  if (!Array.isArray(components)) {
    components = [components];
  }
  const componentsToDelete = {};
  determineComponentsToDelete({
    core: core2,
    components,
    deleteUpstreamDependencies,
    componentsToDelete
  });
  const parentsOfPotentiallyDeleted = {};
  for (const componentIdxStr in componentsToDelete) {
    const componentIdx = Number(componentIdxStr);
    let component = componentsToDelete[componentIdx];
    let parent = core2._components[component.parentIdx];
    if (parent === void 0 || parent.componentIdx in componentsToDelete) {
      continue;
    }
    let parentObj = parentsOfPotentiallyDeleted[component.parentIdx];
    if (parentObj === void 0) {
      parentObj = {
        parent: core2._components[component.parentIdx],
        childNamesToBeDeleted: /* @__PURE__ */ new Set()
      };
      parentsOfPotentiallyDeleted[component.parentIdx] = parentObj;
    }
    parentObj.childNamesToBeDeleted.add(componentIdx);
  }
  let replacementsDeletedFromComposites = [];
  for (const componentIdxStr in componentsToDelete) {
    const componentIdx = Number(componentIdxStr);
    let component = core2._components[componentIdx];
    if (component.replacementOf) {
      let composite = component.replacementOf;
      let replacementNames = composite.replacements.map(
        (x2) => x2.componentIdx
      );
      let replacementInd = replacementNames.indexOf(componentIdx);
      if (replacementInd !== -1) {
        composite.replacements.splice(replacementInd, 1);
        if (!replacementsDeletedFromComposites.includes(
          composite.componentIdx
        )) {
          replacementsDeletedFromComposites.push(
            composite.componentIdx
          );
        }
      }
    }
  }
  for (const compositeIdx of replacementsDeletedFromComposites) {
    if (!(compositeIdx in componentsToDelete)) {
      await core2.dependencies.addBlockersFromChangedReplacements(
        core2._components[compositeIdx]
      );
    }
  }
  const allParents = [];
  for (const parentIdxStr in parentsOfPotentiallyDeleted) {
    const parentObj = parentsOfPotentiallyDeleted[parentIdxStr];
    const parent = parentObj.parent;
    allParents.push(parent);
    for (let ind = parent.definingChildren.length - 1; ind >= 0; ind--) {
      const child = parent.definingChildren[ind];
      if (parentObj.childNamesToBeDeleted.has(child.componentIdx)) {
        parent.definingChildren.splice(ind, 1);
      }
    }
    if (!skipProcessingChildrenOfParents.includes(parent.componentIdx)) {
      await processNewDefiningChildren({
        core: core2,
        parent,
        expandComposites: false
      });
    }
  }
  for (const componentIdxStr in componentsToDelete) {
    const componentIdx = Number(componentIdxStr);
    const component = core2._components[componentIdx];
    if (component.shadows) {
      const shadowedComponent = core2._components[component.shadows.componentIdx];
      if (shadowedComponent.shadowedBy.length === 1) {
        delete shadowedComponent.shadowedBy;
      } else {
        shadowedComponent.shadowedBy.splice(
          shadowedComponent.shadowedBy.indexOf(component),
          1
        );
      }
    }
    core2.dependencies.deleteAllDownstreamDependencies({
      component
    });
    for (let varName in core2.dependencies.upstreamDependencies[component.componentIdx]) {
      let upDeps = core2.dependencies.upstreamDependencies[component.componentIdx][varName];
      for (let upDep of upDeps) {
        if (upDep.specifiedComponentName && upDep.specifiedComponentName in componentsToDelete) {
          let dependenciesMissingComponent = core2.dependencies.updateTriggers.dependenciesMissingComponentBySpecifiedName[upDep.specifiedComponentName];
          if (!dependenciesMissingComponent) {
            dependenciesMissingComponent = core2.dependencies.updateTriggers.dependenciesMissingComponentBySpecifiedName[upDep.specifiedComponentName] = [];
          }
          if (!dependenciesMissingComponent.includes(upDep)) {
            dependenciesMissingComponent.push(upDep);
          }
        }
      }
    }
    await core2.dependencies.deleteAllUpstreamDependencies({
      component
    });
    if (!core2.updateInfo.deletedStateVariables[component.componentIdx]) {
      core2.updateInfo.deletedStateVariables[component.componentIdx] = [];
    }
    core2.updateInfo.deletedStateVariables[component.componentIdx].push(
      ...Object.keys(component.state)
    );
    core2.updateInfo.deletedComponents[component.componentIdx] = true;
    delete core2.unmatchedChildren[component.componentIdx];
    delete core2.stateVariableChangeTriggers[component.componentIdx];
  }
  const componentsToRemoveFromResolver = [];
  for (const componentIdxStr in componentsToDelete) {
    const componentIdx = Number(componentIdxStr);
    let component = core2._components[componentIdx];
    if (component.replacementOf) {
      const compositeSource = component.replacementOf;
      if (compositeSource.attributes.createComponentIdx?.primitive?.value == component.componentIdx) {
        let compositeCreatingComponentIdx = compositeSource;
        let foundUndeletedSourceOfComponentIdx = false;
        while (true) {
          if (!(compositeCreatingComponentIdx.componentIdx in componentsToDelete)) {
            foundUndeletedSourceOfComponentIdx = true;
            break;
          }
          if (compositeCreatingComponentIdx.replacementOf?.attributes.createComponentIdx?.primitive?.value === component.componentIdx) {
            compositeCreatingComponentIdx = compositeCreatingComponentIdx.replacementOf;
          } else {
            break;
          }
        }
        if (foundUndeletedSourceOfComponentIdx) {
          continue;
        }
      }
      if (!(compositeSource.componentIdx in componentsToDelete) && compositeSource.constructor.replacementsAlreadyInResolver) {
        continue;
      }
    }
    componentsToRemoveFromResolver.push(component);
  }
  removeComponentsFromResolver({
    core: core2,
    componentsToRemove: componentsToRemoveFromResolver
  });
  for (const componentIdxStr in componentsToDelete) {
    const componentIdx = Number(componentIdxStr);
    let component = core2._components[componentIdx];
    delete core2.cumulativeStateVariableChanges[component.stateId];
    deregisterComponent({ core: core2, component, recursive: false });
    core2.updateInfo.componentsToUpdateRenderers.delete(componentIdx);
    core2.updateInfo.compositesToUpdateReplacements.delete(componentIdx);
    core2.updateInfo.inactiveCompositesToUpdateReplacements.delete(
      componentIdx
    );
  }
  return {
    success: true,
    deletedComponents: componentsToDelete,
    parentsOfDeleted: allParents
  };
}
function determineComponentsToDelete({
  core: core2,
  components,
  deleteUpstreamDependencies,
  componentsToDelete
}) {
  for (let component of components) {
    if (typeof component !== "object") {
      continue;
    }
    if (component.componentIdx in componentsToDelete) {
      continue;
    }
    componentsToDelete[component.componentIdx] = core2._components[component.componentIdx];
    let componentsToRecurse = Object.values(
      component.allChildren
    ).map((x2) => x2.component);
    for (let attrName in component.attributes) {
      let comp = component.attributes[attrName].component;
      if (comp) {
        componentsToRecurse.push(comp);
      } else {
        let references = component.attributes[attrName].references;
        if (references) {
          componentsToRecurse.push(...references);
        }
      }
    }
    if (component.adaptedFrom !== void 0) {
      componentsToRecurse.push(component.adaptedFrom);
    }
    if (deleteUpstreamDependencies === true) {
      if (component.shadowedBy) {
        componentsToRecurse.push(...component.shadowedBy);
      }
      if (component.adapterUsed) {
        componentsToRecurse.push(component.adapterUsed);
      }
      if (component.replacements) {
        componentsToRecurse.push(...component.replacements);
      }
    }
    determineComponentsToDelete({
      core: core2,
      components: componentsToRecurse,
      deleteUpstreamDependencies,
      componentsToDelete
    });
  }
}
class CompositeReplacementUpdater {
  constructor({ core: core2 }) {
    this.core = core2;
  }
  /**
   * Public entry point: recompute a composite's replacements after its
   * inputs change.
   *
   * Walks the diff between old and new replacement lists by:
   *   1. Asking the composite's `calculateReplacementChanges` for an
   *      ordered list of structural changes (added/deleted/withheld
   *      replacement spans).
   *   2. Deleting obsolete replacements via
   *      `deleteReplacementsFromShadowsThenComposite`, which recurses
   *      into shadowing composites first so the shadow tree shrinks
   *      from leaves inward.
   *   3. Creating new replacements via the `ComponentBuilder` and
   *      `CompositeExpander` module functions, calling
   *      `createShadowedReplacements` for every shadow so the shadow
   *      tree grows in lockstep.
   *   4. Threading every change through `componentChanges` so callers
   *      can inspect what moved.
   *
   * Returns `{ success, deletedComponents, addedComponents,
   * parentsOfDeleted }`. `parentsOfDeleted` carries every parent whose
   * defining-children list was touched, so the caller knows what to
   * re-render. The `do…while` retry loop inside reruns
   * `calculateReplacementChanges` when `core._components.length` shifted
   * during the call (a sign that another expansion happened mid-resolve
   * and could collide with our assigned indices); it terminates as soon
   * as the length is stable across one pass. There is currently no
   * explicit upper bound — see the `TODO` at the loop's tail.
   *
   * Skips entirely when this composite is itself a shadow — its
   * replacements will be (re)created when the shadowed composite is
   * processed.
   */
  async updateCompositeReplacements({
    component,
    componentChanges,
    sourceOfUpdate
  }) {
    let deletedComponents = {};
    let addedComponents = {};
    let parentsOfDeleted = /* @__PURE__ */ new Set();
    if (component.shadows && !component.shadows.propVariable && !component.constructor.doNotExpandAsShadowed) {
      let results2 = {
        success: true,
        deletedComponents,
        addedComponents,
        parentsOfDeleted
      };
      return results2;
    }
    let proxiedComponent = this.core._components[component.componentIdx];
    if (!component.replacements) {
      component.replacements = [];
    }
    await component.stateValues.readyToExpandWhenResolved;
    let initialNComponents;
    let replacementResults;
    const originalWorkspace = { ...component.replacementsWorkspace };
    do {
      initialNComponents = this.core._components.length;
      component.replacementsWorkspace = { ...originalWorkspace };
      const rawReplacementResults = await component.constructor.calculateReplacementChanges({
        component: proxiedComponent,
        componentChanges,
        components: this.core._components,
        nComponents: this.core._components.length,
        workspace: component.replacementsWorkspace,
        componentInfoObjects: this.core.componentInfoObjects,
        flags: this.core.flags,
        resolveItem: this.core.dependencies.resolveItem.bind(
          this.core.dependencies
        ),
        publicCaseInsensitiveAliasSubstitutions: this.core.publicCaseInsensitiveAliasSubstitutions.bind(
          this.core
        )
      });
      replacementResults = {
        replacementChanges: rawReplacementResults?.replacementChanges ?? [],
        diagnostics: rawReplacementResults?.diagnostics ?? [],
        nComponents: rawReplacementResults?.nComponents ?? this.core._components.length
      };
    } while (this.core._components.length !== initialNComponents);
    if (component.constructor.stateVariableToEvaluateAfterReplacements) {
      await component.stateValues[component.constructor.stateVariableToEvaluateAfterReplacements];
    }
    if (replacementResults.nComponents > this.core._components.length) {
      this.core._components[replacementResults.nComponents - 1] = void 0;
    }
    if (replacementResults.diagnostics.length > 0) {
      const parent = this.core._components[component.componentIdx];
      gatherDiagnosticsAndAssignDoenetMLRange({
        core: this.core,
        components: [],
        diagnostics: replacementResults.diagnostics,
        position: parent.position,
        sourceDoc: parent.sourceDoc
      });
    }
    for (let change of replacementResults.replacementChanges) {
      let originalEffectiveLength = component.replacements.length - (component.replacementsToWithhold ?? 0);
      if (change.changeType === "add") {
        if (change.replacementsToWithhold !== void 0) {
          await this.adjustReplacementsToWithhold({
            component,
            change,
            componentChanges
          });
          originalEffectiveLength = component.replacements.length - (component.replacementsToWithhold ?? 0);
        }
        let unproxiedComponent = this.core._components[component.componentIdx];
        this.core.parameterStack.push(
          unproxiedComponent.sharedParameters,
          false
        );
        let newComponents;
        let currentShadowedBy = {
          [component.componentIdx]: calculateAllComponentsShadowing(component)
        };
        let numberToDelete = change.numberReplacementsToReplace;
        let firstIndex = change.firstReplacementInd;
        const updateOldReplacementsStart = Math.min(
          originalEffectiveLength,
          firstIndex
        );
        const updateOldReplacementsEnd = Math.min(
          originalEffectiveLength,
          firstIndex + (numberToDelete ?? 0)
        );
        const blankStringReplacements = component.replacements.map(
          (repl) => typeof repl === "string" && repl.trim() === ""
        );
        if (numberToDelete > 0 && change.changeTopLevelReplacements) {
          await this.deleteReplacementsFromShadowsThenComposite({
            change,
            composite: component,
            componentChanges,
            sourceOfUpdate,
            parentsOfDeleted,
            deletedComponents,
            addedComponents,
            processNewChildren: false
          });
        }
        if (!change.serializedReplacements) {
          throw Error(`Invalid replacement change.`);
        }
        const serializedReplacements = change.serializedReplacements;
        const position = this.core._components[component.componentIdx].position;
        const sourceDoc = this.core._components[component.componentIdx].sourceDoc;
        const overwriteDoenetMLRange = component.componentType === "_copy";
        gatherDiagnosticsAndAssignDoenetMLRange({
          core: this.core,
          components: serializedReplacements,
          diagnostics: [],
          position,
          sourceDoc,
          overwriteDoenetMLRange
        });
        const newNComponents = change.nComponents;
        await addReplacementsToResolver({
          core: this.core,
          serializedReplacements,
          component,
          updateOldReplacementsStart,
          updateOldReplacementsEnd,
          blankStringReplacements
        });
        if (newNComponents > this.core._components.length) {
          this.core._components[newNComponents - 1] = void 0;
        }
        try {
          const createResult = await createIsolatedComponents({
            core: this.core,
            serializedComponents: serializedReplacements,
            ancestors: component.ancestors,
            componentsReplacementOf: component
          });
          newComponents = createResult.components;
        } catch (e3) {
          console.error(e3);
          newComponents = await this.setErrorReplacements({
            composite: component,
            message: e3.message
          });
        }
        this.core.parameterStack.pop();
        const newReplacementsByComposite = {
          [component.componentIdx]: {
            newComponents,
            parent: change.parent
          }
        };
        if (unproxiedComponent.shadowedBy && currentShadowedBy[unproxiedComponent.componentIdx].length > 0) {
          const newReplacementsForShadows = await this.createShadowedReplacements({
            replacementsToShadow: newComponents,
            componentToShadow: unproxiedComponent,
            parentToShadow: change.parent,
            currentShadowedBy,
            componentChanges,
            sourceOfUpdate,
            parentsOfDeleted,
            deletedComponents,
            addedComponents,
            updateOldReplacementsStart,
            updateOldReplacementsEnd,
            blankStringReplacements
          });
          Object.assign(
            newReplacementsByComposite,
            newReplacementsForShadows
          );
        }
        for (const compositeIdxStr in newReplacementsByComposite) {
          const compositeIdx = Number(compositeIdxStr);
          const composite = this.core._components[compositeIdx];
          if (!composite) {
            continue;
          }
          const newReplacements = newReplacementsByComposite[compositeIdx].newComponents;
          if (!composite.isExpanded) {
            await expandCompositeComponent({
              core: this.core,
              component: composite
            });
            const newChange = {
              changeType: "addedReplacements",
              composite,
              newReplacements: composite.replacements,
              topLevel: true,
              firstIndex: 0,
              numberDeleted: 0
            };
            componentChanges.push(newChange);
            continue;
          }
          for (const comp of newReplacements) {
            if (typeof comp === "object") {
              addedComponents[comp.componentIdx] = comp;
            }
          }
          if (change.changeTopLevelReplacements === true) {
            const parent = this.core._components[composite.parentIdx];
            composite.replacements.splice(
              firstIndex,
              0,
              ...newReplacements
            );
            await this.core.dependencies.addBlockersFromChangedReplacements(
              composite
            );
            const newChange = {
              changeType: "addedReplacements",
              composite,
              newReplacements,
              topLevel: true,
              firstIndex,
              numberDeleted: numberToDelete
            };
            componentChanges.push(newChange);
            await processNewDefiningChildren({
              core: this.core,
              parent,
              expandComposites: false
            });
            const componentsAffected = await componentAndRenderedDescendants({
              core: this.core,
              component: parent
            });
            componentsAffected.forEach(
              (cIdx) => this.core.updateInfo.componentsToUpdateRenderers.add(
                cIdx
              )
            );
          } else {
            const parent = this.core._components[newReplacementsByComposite[compositeIdx].parent.componentIdx];
            spliceChildren({
              parent,
              indexOfDefiningChildren: change.indexOfDefiningChildren,
              newChildren: newReplacements
            });
            await processNewDefiningChildren({
              core: this.core,
              parent
            });
            for (const repl of newReplacements) {
              if (typeof repl === "object") {
                addedComponents[repl.componentIdx] = repl;
              }
            }
            const componentsAffected = await componentAndRenderedDescendants({
              core: this.core,
              component: parent
            });
            componentsAffected.forEach(
              (cIdx) => this.core.updateInfo.componentsToUpdateRenderers.add(
                cIdx
              )
            );
            const newChange = {
              changeType: "addedReplacements",
              composite,
              newReplacements
            };
            componentChanges.push(newChange);
          }
        }
      } else if (change.changeType === "delete") {
        if (change.replacementsToWithhold !== void 0) {
          await this.adjustReplacementsToWithhold({
            component,
            change,
            componentChanges
          });
        }
        await this.deleteReplacementsFromShadowsThenComposite({
          change,
          composite: component,
          componentsToDelete: change.components,
          componentChanges,
          sourceOfUpdate,
          parentsOfDeleted,
          deletedComponents,
          addedComponents
        });
      } else if (change.changeType === "updateStateVariables") {
        const workspace = {};
        const newStateVariableValues = {};
        for (const stateVariable in change.stateChanges) {
          const instruction = {
            componentIdx: change.component.componentIdx,
            stateVariable,
            value: change.stateChanges[stateVariable],
            overrideFixed: true
          };
          await this.core.requestComponentChanges({
            instruction,
            initialChange: false,
            workspace,
            newStateVariableValues
          });
        }
        await this.core.processNewStateVariableValues(
          newStateVariableValues
        );
      } else if (change.changeType === "changeReplacementsToWithhold") {
        if (change.replacementsToWithhold !== void 0) {
          await this.adjustReplacementsToWithhold({
            component,
            change,
            componentChanges,
            adjustResolver: true
          });
        }
        await this.processChildChangesAndRecurseToShadows(component);
      }
    }
    const results = {
      success: true,
      deletedComponents,
      addedComponents,
      parentsOfDeleted
    };
    return results;
  }
  async setErrorReplacements({
    composite,
    message
  }) {
    this.core.addDiagnostic({
      type: "error",
      message,
      position: composite.position,
      sourceDoc: composite.sourceDoc
    });
    let errorReplacements = [
      {
        type: "serialized",
        componentType: "_error",
        componentIdx: this.core._components.length,
        state: { message },
        position: composite.position,
        sourceDoc: composite.sourceDoc,
        children: [],
        attributes: {}
      }
    ];
    this.core._components[this.core._components.length] = void 0;
    composite.isInErrorState = true;
    let createResult = await createIsolatedComponents({
      core: this.core,
      serializedComponents: errorReplacements,
      ancestors: composite.ancestors,
      componentsReplacementOf: composite
    });
    return createResult.components;
  }
  async deleteReplacementsFromShadowsThenComposite({
    change,
    composite,
    componentsToDelete,
    componentChanges,
    sourceOfUpdate,
    parentsOfDeleted,
    deletedComponents,
    addedComponents,
    processNewChildren = true
  }) {
    if (!composite.isExpanded) {
      return;
    }
    for (const shadowingComposite of iterateExpandableShadows(composite)) {
      let shadowingComponentsToDelete;
      if (componentsToDelete) {
        shadowingComponentsToDelete = [];
        for (let compToDelete of componentsToDelete) {
          const shadowingCompToDelete = findExpandableShadowByCompositeIdx(
            compToDelete,
            shadowingComposite.shadows.compositeIdx
          );
          if (!shadowingCompToDelete) {
            console.error(
              `could not find shadowing component of ${compToDelete.componentIdx}`
            );
          } else {
            shadowingComponentsToDelete.push(shadowingCompToDelete);
          }
        }
      }
      await this.deleteReplacementsFromShadowsThenComposite({
        change,
        composite: shadowingComposite,
        componentsToDelete: shadowingComponentsToDelete,
        componentChanges,
        sourceOfUpdate,
        parentsOfDeleted,
        deletedComponents,
        addedComponents,
        processNewChildren
      });
    }
    if (change.changeTopLevelReplacements) {
      let firstIndex = change.firstReplacementInd;
      let numberToDelete = change.numberReplacementsToDelete;
      if (change.changeType === "add") {
        numberToDelete = change.numberReplacementsToReplace;
      }
      let replacementsToDelete = composite.replacements.splice(
        firstIndex,
        numberToDelete
      );
      await this.core.dependencies.addBlockersFromChangedReplacements(
        composite
      );
      let deleteResults = await deleteComponents({
        core: this.core,
        components: replacementsToDelete,
        skipProcessingChildrenOfParents: [composite.parentIdx]
      });
      if (processNewChildren) {
        await processNewDefiningChildren({
          core: this.core,
          parent: this.core._components[composite.parentIdx],
          expandComposites: false
        });
      }
      if (deleteResults.success === false) {
        throw Error("Couldn't delete components on composite update");
      }
      await this._recordDeleteResults({
        deleteResults,
        composite,
        numberDeleted: numberToDelete,
        parentsOfDeleted,
        deletedComponents,
        componentChanges,
        topLevel: true,
        firstIndex
      });
      let parent = this.core._components[composite.parentIdx];
      let componentsAffected = await componentAndRenderedDescendants({
        core: this.core,
        component: parent
      });
      componentsAffected.forEach(
        (cIdx) => this.core.updateInfo.componentsToUpdateRenderers.add(cIdx)
      );
    } else {
      let numberToDelete = componentsToDelete.length;
      let deleteResults = await deleteComponents({
        core: this.core,
        components: componentsToDelete,
        deleteUpstreamDependencies: false
      });
      if (deleteResults.success === false) {
        throw Error(
          "Couldn't delete components prescribed by composite"
        );
      }
      await this._recordDeleteResults({
        deleteResults,
        composite,
        numberDeleted: numberToDelete,
        parentsOfDeleted,
        deletedComponents,
        componentChanges
      });
      Object.assign(addedComponents, deleteResults.addedComponents);
    }
  }
  async processChildChangesAndRecurseToShadows(component) {
    let parent = this.core._components[component.parentIdx];
    await processNewDefiningChildren({
      core: this.core,
      parent,
      expandComposites: false
    });
    let componentsAffected = await componentAndRenderedDescendants({
      core: this.core,
      component: parent
    });
    componentsAffected.forEach(
      (cIdx) => this.core.updateInfo.componentsToUpdateRenderers.add(cIdx)
    );
    for (const shadowingComponent of iterateExpandableShadows(component)) {
      await this.processChildChangesAndRecurseToShadows(
        shadowingComponent
      );
    }
  }
  async createShadowedReplacements({
    replacementsToShadow,
    componentToShadow,
    parentToShadow,
    currentShadowedBy,
    componentChanges,
    sourceOfUpdate,
    parentsOfDeleted,
    deletedComponents,
    addedComponents,
    updateOldReplacementsStart,
    updateOldReplacementsEnd,
    blankStringReplacements
  }) {
    let newShadowedBy = calculateAllComponentsShadowing(componentToShadow);
    if (!currentShadowedBy[componentToShadow.componentIdx] || !newShadowedBy.every(
      (x2) => currentShadowedBy[componentToShadow.componentIdx].includes(x2)
    )) {
      throw Error(
        `Circular dependency involving ${componentToShadow.componentIdx}.`
      );
    }
    this.core.updateInfo.compositesBeingExpanded.push(
      componentToShadow.componentIdx
    );
    let newComponentsForShadows = {};
    for (const shadowingComponent of iterateExpandableShadows(
      componentToShadow
    )) {
      if (this.core.updateInfo.compositesBeingExpanded.includes(
        shadowingComponent.componentIdx
      )) {
        throw Error(
          `Circular dependency involving ${shadowingComponent.componentIdx}.`
        );
      }
      if (shadowingComponent.shadowedBy) {
        currentShadowedBy[shadowingComponent.componentIdx] = calculateAllComponentsShadowing(shadowingComponent);
      }
      if (shadowingComponent.isExpanded) {
        let newSerializedReplacements = [];
        shadowingComponent.replacementsWorkspace.replacementsCreated = 0;
        const stateIdInfo = {
          prefix: `${shadowingComponent.stateId}|`,
          num: shadowingComponent.replacementsWorkspace.replacementsCreated
        };
        let nComponents = this.core._components.length;
        let newNComponents = nComponents;
        for (let [idx, repl] of replacementsToShadow.entries()) {
          if (typeof repl === "object") {
            const serializedComponent = await repl.serialize();
            if (shadowingComponent.constructor.useSerializedChildrenComponentIndices) {
              const res = createComponentIndicesFromSerializedChildren(
                [serializedComponent],
                [
                  shadowingComponent.serializedChildren[idx]
                ],
                newNComponents,
                stateIdInfo
              );
              newNComponents = res.nComponents;
              newSerializedReplacements.push(...res.components);
            } else {
              const res = createNewComponentIndices(
                [serializedComponent],
                newNComponents,
                stateIdInfo
              );
              newNComponents = res.nComponents;
              newSerializedReplacements.push(...res.components);
            }
          } else {
            newSerializedReplacements.push(repl);
          }
        }
        shadowingComponent.replacementsWorkspace.replacementsCreated = stateIdInfo.num;
        adjustForCreateComponentIdxName({
          serializedReplacements: newSerializedReplacements,
          composite: shadowingComponent
        });
        await addReplacementsToResolver({
          core: this.core,
          serializedReplacements: newSerializedReplacements,
          component: shadowingComponent,
          updateOldReplacementsStart,
          updateOldReplacementsEnd,
          blankStringReplacements
        });
        if (newNComponents > this.core._components.length) {
          this.core._components[newNComponents - 1] = void 0;
        }
        newSerializedReplacements = postProcessCopy({
          serializedComponents: newSerializedReplacements,
          componentIdx: shadowingComponent.shadows.compositeIdx
        });
        if (shadowingComponent.attributes.isResponse) {
          let compositeAttributesObj = preprocessAttributesObject(
            shadowingComponent.constructor.createAttributesObject()
          );
          for (let repl of newSerializedReplacements) {
            if (typeof repl !== "object") {
              continue;
            }
            if (!repl.attributes) {
              repl.attributes = {};
            }
            let nComponents2 = this.core._components.length;
            const res = convertUnresolvedAttributesForComponentType(
              {
                attributes: {
                  isResponse: shadowingComponent.attributes.isResponse
                },
                componentType: repl.componentType,
                componentInfoObjects: this.core.componentInfoObjects,
                compositeAttributesObj,
                nComponents: nComponents2
              }
            );
            const attributesFromComposite = res.attributes;
            nComponents2 = res.nComponents;
            if (nComponents2 > this.core._components.length) {
              this.core._components[nComponents2 - 1] = void 0;
            }
            Object.assign(repl.attributes, attributesFromComposite);
          }
        }
        let newComponents;
        let unproxiedShadowingComponent = this.core._components[shadowingComponent.componentIdx];
        this.core.parameterStack.push(
          unproxiedShadowingComponent.sharedParameters,
          false
        );
        try {
          let createResult = await createIsolatedComponents({
            core: this.core,
            serializedComponents: newSerializedReplacements,
            ancestors: shadowingComponent.ancestors,
            componentsReplacementOf: shadowingComponent
          });
          newComponents = createResult.components;
        } catch (e3) {
          console.error(e3);
          newComponents = await this.setErrorReplacements({
            composite: shadowingComponent,
            message: e3.message
          });
        }
        this.core.parameterStack.pop();
        let shadowingParent;
        if (parentToShadow) {
          shadowingParent = findExpandableShadowByCompositeIdx(
            parentToShadow,
            shadowingComponent.shadows.compositeIdx
          );
          if (!shadowingParent) {
            console.error(
              `could not find shadowing parent of ${parentToShadow.componentIdx}`
            );
          }
        }
        newComponentsForShadows[shadowingComponent.componentIdx] = {
          newComponents,
          parent: shadowingParent
        };
        if (shadowingComponent.shadowedBy && currentShadowedBy[shadowingComponent.componentIdx].length > 0) {
          let recursionComponents = await this.createShadowedReplacements({
            replacementsToShadow: newComponents,
            componentToShadow: shadowingComponent,
            parentToShadow: shadowingParent,
            currentShadowedBy,
            componentChanges,
            sourceOfUpdate,
            parentsOfDeleted,
            deletedComponents,
            addedComponents,
            updateOldReplacementsStart,
            updateOldReplacementsEnd,
            blankStringReplacements
          });
          Object.assign(newComponentsForShadows, recursionComponents);
        }
      }
    }
    let targetInd = this.core.updateInfo.compositesBeingExpanded.indexOf(
      componentToShadow.componentIdx
    );
    if (targetInd === -1) {
      throw Error(
        `Something is wrong as we lost track that we were expanding ${componentToShadow.componentIdx}`
      );
    }
    this.core.updateInfo.compositesBeingExpanded.splice(targetInd, 1);
    return newComponentsForShadows;
  }
  async adjustReplacementsToWithhold({
    component,
    change,
    componentChanges,
    adjustResolver = false
  }) {
    let replacementsToWithhold = change.replacementsToWithhold;
    let changeInReplacementsToWithhold;
    if (component.replacementsToWithhold !== void 0) {
      changeInReplacementsToWithhold = replacementsToWithhold - component.replacementsToWithhold;
    } else {
      changeInReplacementsToWithhold = replacementsToWithhold;
    }
    if (changeInReplacementsToWithhold < 0) {
      let lastIndToStopWithholding = component.replacements.length - replacementsToWithhold;
      let firstIndToStopWithholding = component.replacements.length - replacementsToWithhold + changeInReplacementsToWithhold;
      let newReplacements = component.replacements.slice(
        firstIndToStopWithholding,
        lastIndToStopWithholding
      );
      let newChange = {
        changeType: "addedReplacements",
        composite: component,
        topLevel: true,
        newReplacements,
        firstIndex: firstIndToStopWithholding,
        numberDeleted: 0
      };
      componentChanges.push(newChange);
    } else if (changeInReplacementsToWithhold > 0) {
      let firstIndToStartWithholding = component.replacements.length - replacementsToWithhold;
      let lastIndToStartWithholding = firstIndToStartWithholding + changeInReplacementsToWithhold;
      let withheldReplacements = component.replacements.slice(
        firstIndToStartWithholding,
        lastIndToStartWithholding
      );
      let withheldNamesByParent = {};
      for (let comp of withheldReplacements) {
        let par = comp.parentIdx;
        if (withheldNamesByParent[par] === void 0) {
          withheldNamesByParent[par] = [];
        }
        withheldNamesByParent[par].push(comp.componentIdx);
      }
      let newChange = {
        changeType: "deletedReplacements",
        composite: component,
        topLevel: true,
        firstIndex: firstIndToStartWithholding,
        numberDeleted: changeInReplacementsToWithhold,
        deletedNamesByParent: withheldNamesByParent,
        deletedComponents: withheldReplacements
      };
      componentChanges.push(newChange);
    }
    if (adjustResolver) {
      const blankStringReplacements = component.replacements.map(
        (repl) => typeof repl === "string" && repl.trim() === ""
      );
      const { indexResolution } = await determineParentAndIndexResolutionForResolver({
        core: this.core,
        component,
        updateOldReplacementsStart: 0,
        updateOldReplacementsEnd: component.replacements.length - (component.replacementsToWithhold ?? 0),
        blankStringReplacements
      });
      let indexParent = indexResolution.ReplaceAll?.parent ?? indexResolution.ReplaceRange?.parent ?? null;
      if (indexParent !== null && indexParent !== component.componentIdx) {
        const indexParentComposite = this.core._components[indexParent];
        if (indexParentComposite) {
          if (this.core.replaceIndexResolutionsInResolver) {
            const newContentForIndex = component.replacements.slice(
              0,
              component.replacements.length - change.replacementsToWithhold
            ).map((repl) => {
              if (typeof repl === "string") {
                return repl;
              } else {
                return repl.componentIdx;
              }
            });
            this.core.replaceIndexResolutionsInResolver(
              { content: newContentForIndex },
              indexResolution
            );
            this.core.rootNames = this.core.calculateRootNames?.().names;
            await this.core.dependencies.addBlockersFromChangedReplacements(
              indexParentComposite
            );
          }
        }
      }
    }
    component.replacementsToWithhold = replacementsToWithhold;
    await this.core.dependencies.addBlockersFromChangedReplacements(
      component
    );
    for (const shadowingComponent of iterateExpandableShadows(component)) {
      await this.adjustReplacementsToWithhold({
        component: shadowingComponent,
        change,
        componentChanges,
        adjustResolver
      });
    }
  }
  /**
   * Shared post-delete bookkeeping for the two branches of
   * `deleteReplacementsFromShadowsThenComposite`:
   *   1. record every parent-of-deleted on `parentsOfDeleted` and queue
   *      its rendered descendants for renderer update,
   *   2. group `deleteResults.deletedComponents` by `parentIdx` into
   *      `deletedNamesByParent`,
   *   3. push a `deletedReplacements` change record onto
   *      `componentChanges`, and
   *   4. merge `deleteResults.deletedComponents` into `deletedComponents`.
   *
   * Pass `topLevel: true` (with `firstIndex`) for the
   * `change.changeTopLevelReplacements` branch — the change record then
   * carries the position of the splice. The non-top-level branch omits
   * both fields.
   */
  async _recordDeleteResults({
    deleteResults,
    composite,
    numberDeleted,
    parentsOfDeleted,
    deletedComponents,
    componentChanges,
    topLevel,
    firstIndex
  }) {
    for (const parent of deleteResults.parentsOfDeleted) {
      parentsOfDeleted.add(parent.componentIdx);
      const componentsAffected = await componentAndRenderedDescendants({
        core: this.core,
        component: parent
      });
      componentsAffected.forEach(
        (cIdx) => this.core.updateInfo.componentsToUpdateRenderers.add(cIdx)
      );
    }
    const deletedNamesByParent = {};
    for (const compName in deleteResults.deletedComponents) {
      const comp = deleteResults.deletedComponents[compName];
      const par = comp.parentIdx;
      if (deletedNamesByParent[par] === void 0) {
        deletedNamesByParent[par] = [];
      }
      deletedNamesByParent[par].push(compName);
    }
    const newChange = {
      changeType: "deletedReplacements",
      composite,
      numberDeleted,
      deletedNamesByParent,
      deletedComponents: deleteResults.deletedComponents
    };
    if (topLevel) {
      newChange.topLevel = true;
      newChange.firstIndex = firstIndex;
    }
    componentChanges.push(newChange);
    Object.assign(deletedComponents, deleteResults.deletedComponents);
  }
}
function* iterateExpandableShadows(component) {
  if (!component.shadowedBy) {
    return;
  }
  for (const shadow of component.shadowedBy) {
    if (shadow.shadows.propVariable || shadow.constructor.doNotExpandAsShadowed) {
      continue;
    }
    yield shadow;
  }
}
function findExpandableShadowByCompositeIdx(component, compositeIdx) {
  for (const shadow of iterateExpandableShadows(component)) {
    if (shadow.shadows.compositeIdx === compositeIdx) {
      return shadow;
    }
  }
  return void 0;
}
function calculateAllComponentsShadowing(component) {
  const allShadowing = [];
  for (const shadow of iterateExpandableShadows(component)) {
    allShadowing.push(shadow.componentIdx);
    allShadowing.push(...calculateAllComponentsShadowing(shadow));
  }
  if (component.replacementOf) {
    let additionalShadowing = calculateAllComponentsShadowing(
      component.replacementOf
    );
    allShadowing.push(...additionalShadowing);
  }
  return allShadowing;
}
class DiagnosticsManager {
  constructor({
    preliminaryDiagnostics
  }) {
    this.diagnostics = preliminaryDiagnostics.filter(
      (diagnostic) => {
        if (diagnostic.type === "error") {
          return false;
        }
        this.assertDiagnosticIsValid(diagnostic);
        return true;
      }
    );
    this.hasPendingDiagnostics = true;
  }
  /**
   * Mark the queue as having pending diagnostics without adding a record.
   * Used by Core's error-component path, where the diagnostic itself is
   * synthesized later by `convertToErrorComponent`.
   */
  markPending() {
    this.hasPendingDiagnostics = true;
  }
  /**
   * Get pending diagnostics and reset the pending flag.
   *
   * Caps the diagnostics array at the most recent {@link MAX_DIAGNOSTICS}
   * entries so a long session with many warnings can't grow it unboundedly.
   */
  getDiagnostics() {
    this.diagnostics = this.diagnostics.slice(-1e3);
    this.hasPendingDiagnostics = false;
    return { diagnostics: this.diagnostics };
  }
  assertDiagnosticIsValid({
    type,
    level
  }) {
    if (!["error", "warning", "info", "accessibility"].includes(type)) {
      throw Error("Invalid diagnostic type");
    }
    if (type === "accessibility") {
      if (level === void 0) {
        throw Error("Missing accessibility diagnostic level");
      }
      if (![1, 2].includes(level)) {
        throw Error("Invalid accessibility diagnostic level");
      }
    }
  }
  /**
   * Add a diagnostic record to `this.diagnostics`, deduplicating by
   * type + message + source location.
   *
   * Returns `true` if a new entry was added, `false` if it was deduped.
   * Core's initial-add phase inspects this so it can re-throw deduped errors
   * via the existing rethrow path (see `Core.js` near the
   * `result.sendDiagnostics` loop).
   */
  addDiagnostic(diagnostic) {
    const sameLocation = (pointA, pointB) => (pointA?.offset ?? void 0) === (pointB?.offset ?? void 0) && (pointA?.line ?? void 0) === (pointB?.line ?? void 0) && (pointA?.column ?? void 0) === (pointB?.column ?? void 0);
    const haveSamePosition = (warningPosition, newPosition) => {
      if (warningPosition === void 0 || newPosition === void 0) {
        return warningPosition === newPosition;
      }
      return sameLocation(warningPosition.start, newPosition.start) && sameLocation(warningPosition.end, newPosition.end);
    };
    this.assertDiagnosticIsValid(diagnostic);
    const alreadyHaveDiagnostic = this.diagnostics.some((existing) => {
      if (existing.type !== diagnostic.type) {
        return false;
      }
      if (diagnostic.type === "accessibility" && existing.type === "accessibility" && existing.level !== diagnostic.level) {
        return false;
      }
      return existing.message === diagnostic.message && existing.sourceDoc === diagnostic.sourceDoc && haveSamePosition(existing.position, diagnostic.position);
    });
    if (alreadyHaveDiagnostic) {
      return false;
    }
    this.diagnostics.push(diagnostic);
    this.hasPendingDiagnostics = true;
    return true;
  }
}
class EssentialValueWriter {
  constructor({ core: core2 }) {
    this.core = core2;
  }
  /**
   * Public bulk-write entry point. Applies `newStateVariableValues` to the
   * live component tree, then flushes any composite-replacement work the
   * writes triggered:
   *   1. `processNewStateVariableValues` writes the values and records
   *      the changes back to upstream dependencies.
   *   2. `replacementChangesFromCompositesToUpdate` recomputes the
   *      replacements of every composite added to
   *      `updateInfo.compositesToUpdateReplacements` during step 1.
   *   3. If any composite produced new replacements, `expandAllComposites`
   *      runs (twice — once normally, once with the force flag) to
   *      catch composites that became expandable as a side effect, and
   *      any deferred state variables in
   *      `updateInfo.stateVariablesToEvaluate` are evaluated.
   *   4. A second `replacementChangesFromCompositesToUpdate` pass picks
   *      up composites scheduled by step 3.
   *
   * Note: this stops after one expand/flush cycle. If step 3 schedules
   * further composites, they will be picked up on the next call from
   * `UpdateExecutor.performUpdate` rather than looping here.
   */
  async executeUpdateStateVariables(newStateVariableValues) {
    await this.processNewStateVariableValues(newStateVariableValues);
    let replacementResult = await this.replacementChangesFromCompositesToUpdate();
    if (replacementResult.updatedComposites) {
      await expandAllComposites({
        core: this.core,
        component: this.core.document
      });
      await expandAllComposites({
        core: this.core,
        component: this.core.document,
        force: true
      });
      if (this.core.updateInfo.stateVariablesToEvaluate) {
        let stateVariablesToEvaluate = this.core.updateInfo.stateVariablesToEvaluate;
        this.core.updateInfo.stateVariablesToEvaluate = [];
        for (let {
          componentIdx,
          stateVariable
        } of stateVariablesToEvaluate) {
          let comp = this.core._components[componentIdx];
          if (comp && comp.state[stateVariable]) {
            await this.core.getStateVariableValue({
              component: comp,
              stateVariable
            });
          }
        }
      }
    }
    await this.replacementChangesFromCompositesToUpdate();
  }
  async replacementChangesFromCompositesToUpdate() {
    let compositesToUpdateReplacements = [
      ...this.core.updateInfo.compositesToUpdateReplacements
    ];
    this.core.updateInfo.compositesToUpdateReplacements.clear();
    let compositesNotReady = /* @__PURE__ */ new Set();
    let nPasses = 0;
    let updatedComposites = false;
    let componentChanges = [];
    while (compositesToUpdateReplacements.length > 0) {
      for (let cIdx of compositesToUpdateReplacements) {
        let composite = this.core._components[cIdx];
        if (composite instanceof this.core.componentInfoObjects.allComponentClasses._composite && composite.isExpanded && !composite.isInErrorState) {
          if (composite.state.readyToExpandWhenResolved.initiallyResolved) {
            if (await composite.stateValues.isInactiveCompositeReplacement) {
              this.core.updateInfo.inactiveCompositesToUpdateReplacements.add(
                cIdx
              );
            } else {
              let result = await this.core.updateCompositeReplacements({
                component: composite,
                componentChanges
              });
              if (Object.keys(result.addedComponents).length > 0) {
                updatedComposites = true;
              }
              if (Object.keys(result.deletedComponents).length > 0) {
                updatedComposites = true;
              }
            }
          } else {
            compositesNotReady.add(cIdx);
          }
        }
      }
      compositesToUpdateReplacements = [
        ...this.core.updateInfo.compositesToUpdateReplacements
      ];
      this.core.updateInfo.compositesToUpdateReplacements.clear();
      nPasses++;
      if (nPasses > 100) {
        throw Error(
          `Seem to have an infinite loop while calculating replacement changes`
        );
      }
    }
    this.core.updateInfo.compositesToUpdateReplacements = compositesNotReady;
    return { updatedComposites };
  }
  /**
   * Apply a bulk batch of `componentIdx → varName → newValue` updates to
   * the live component tree. Used at boot to re-apply persisted /
   * restored state and after `executeUpdate` instructions to flush
   * intermediate inverse-definition results.
   *
   * For each component:
   *   - Skips and stashes onto `updateInfo.stateVariableUpdatesForMissingComponents`
   *     when the component does not exist yet (it will be re-applied
   *     once the component is created via `checkForDependenciesOnNewComponent`).
   *   - For each variable, calls `processNewStateVariableValueForVariable`,
   *     which dispatches into the array / scalar / essential / primitive-child
   *     branches and may recurse into shadowing variables.
   *   - Records actual changes back to upstream dependencies so the rest
   *     of the staleness machinery sees the write.
   *
   * The optional `newComponent` flag is set when the bulk apply is
   * happening as part of a fresh component initialization, which
   * suppresses some warnings that would otherwise fire for
   * not-yet-resolved variables.
   */
  async processNewStateVariableValues(newStateVariableValues, newComponent = false) {
    let nFailures = 0;
    let foundIgnore = false;
    for (const cIdxStr in newStateVariableValues) {
      const cIdx = Number(cIdxStr);
      let comp = this.core._components[cIdx];
      if (comp === void 0) {
        let updatesForComp = this.core.updateInfo.stateVariableUpdatesForMissingComponents[cIdx];
        if (updatesForComp === void 0) {
          updatesForComp = this.core.updateInfo.stateVariableUpdatesForMissingComponents[cIdx] = {};
        }
        Object.assign(updatesForComp, newStateVariableValues[cIdx]);
        continue;
      }
      let newComponentStateVariables = newStateVariableValues[cIdx];
      for (let vName in newComponentStateVariables) {
        let compStateObj = comp.state[vName];
        if (compStateObj) {
          await ensureStateVariableMaterialized({
            core: this.core,
            component: comp,
            stateVariable: vName
          });
        }
        if (compStateObj === void 0) {
          let match = vName.match(/^__def_primitive_(\d+)$/);
          if (!match && newComponent) {
            match = vName.match(/^__def_primitive_ignore_(\d+)$/);
          }
          if (match) {
            let childInd = Number(match[1]);
            comp.definingChildren[childInd] = newComponentStateVariables[vName];
            await processNewDefiningChildren({
              core: this.core,
              parent: comp,
              expandComposites: false
            });
            continue;
          } else {
            match = vName.match(/^__def_primitive_ignore_(\d+)$/);
            if (match) {
              let childInd = Number(match[1]);
              comp.definingChildren[childInd] = newComponentStateVariables[vName];
              foundIgnore = true;
              continue;
            }
          }
          this.core.addDiagnostic({
            type: "info",
            message: `can't update state variable ${vName} of component ${cIdx}, as it doesn't exist.`,
            position: this.core._components[cIdx].position,
            sourceDoc: this.core._components[cIdx].sourceDoc
          });
          continue;
        }
        if (!compStateObj.hasEssential) {
          this.core.addDiagnostic({
            type: "info",
            message: `can't update state variable ${vName} of component ${cIdx}, as it does not have an essential state variable.`,
            position: this.core._components[cIdx].position,
            sourceDoc: this.core._components[cIdx].sourceDoc
          });
          continue;
        }
        let essentialVarName = vName;
        if (comp.state[vName].essentialVarName) {
          essentialVarName = comp.state[vName].essentialVarName;
        }
        if (vName in this.core.rendererVariablesByComponentType[comp.componentType]) {
          this.core.updateInfo.componentsToUpdateRenderers.add(
            comp.componentIdx
          );
        }
        if (compStateObj.isArray) {
          let essentialArray = comp.essentialState[essentialVarName];
          if (!Array.isArray(essentialArray)) {
            essentialArray = comp.essentialState[essentialVarName] = [];
          }
          let arrayEntryNamesAffected = [];
          if (!comp.state[compStateObj.arraySizeStateVariable].initiallyResolved) {
            await comp.state[compStateObj.arraySizeStateVariable].value;
          }
          let arraySize = await compStateObj.arraySize;
          for (let arrayKey in newComponentStateVariables[vName]) {
            if (arrayKey === "mergeObject") {
              continue;
            }
            let set2 = (x2) => x2;
            if (compStateObj.set) {
              set2 = compStateObj.set;
            }
            let setResult = compStateObj.setArrayValue({
              value: set2(
                newComponentStateVariables[vName][arrayKey]
              ),
              arrayKey,
              arraySize,
              arrayValues: essentialArray
            });
            compStateObj.usedDefaultByArrayKey[arrayKey] = false;
            nFailures += setResult.nFailures;
            let varNamesContainingArrayKey = compStateObj.varNamesIncludingArrayKeys[arrayKey];
            if (varNamesContainingArrayKey) {
              arrayEntryNamesAffected.push(
                ...varNamesContainingArrayKey
              );
            }
          }
          for (let arrayEntryName of arrayEntryNamesAffected) {
            await this.core.recordActualChangeInStateVariable({
              componentIdx: cIdx,
              varName: arrayEntryName
            });
          }
        } else {
          if (compStateObj.set) {
            comp.essentialState[essentialVarName] = compStateObj.set(newComponentStateVariables[vName]);
          } else {
            comp.essentialState[essentialVarName] = newComponentStateVariables[vName];
          }
          delete compStateObj.usedDefault;
        }
        await this.core.recordActualChangeInStateVariable({
          componentIdx: cIdx,
          varName: vName
        });
      }
    }
    return { nFailures, foundIgnore };
  }
  /**
   * Walk one update instruction's inverse-definition chain, threading
   * desired values through dependencies until each lands on an essential
   * or inverse-set target. The instruction object describes a single
   * variable change; the function recursively expands it via the
   * variable's `inverseDefinition` (or `arrayInverseDefinition` for
   * arrays), calling itself on each downstream "set" instruction the
   * inverse returns.
   *
   * Result accumulators:
   *   - `newStateVariableValues[componentIdx][varName]` collects the
   *     final values to write — flushed by the caller via
   *     `executeUpdateStateVariables` / `processNewStateVariableValues`.
   *   - `workspace[componentIdx]` holds per-component scratch state
   *     shared across recursive calls so each variable is processed once.
   *
   * Termination: a chain ends at an essential variable, an inverse
   * definition returning `success: false`, or an instruction whose
   * `value` already matches the current value (per `_isUnchanged` checks
   * inside the inverse definitions). The `initialChange` flag is true
   * only for the top-level call so child recursions can suppress some
   * provenance bookkeeping.
   */
  async requestComponentChanges({
    instruction,
    initialChange = true,
    workspace,
    newStateVariableValues
  }) {
    let component = this.core._components[instruction.componentIdx];
    let stateVariable = this.core.substituteAliases({
      stateVariables: [instruction.stateVariable],
      componentClass: component.constructor
    })[0];
    if (workspace[instruction.componentIdx] === void 0) {
      workspace[instruction.componentIdx] = {};
    }
    let componentWorkspace = workspace[instruction.componentIdx];
    let stateVarObj = component.state[stateVariable];
    await ensureStateVariableMaterialized({
      core: this.core,
      component,
      stateVariable
    });
    let additionalStateVariablesDefined = stateVarObj.additionalStateVariablesDefined;
    let allStateVariablesAffected = [stateVariable];
    if (additionalStateVariablesDefined) {
      allStateVariablesAffected.push(...additionalStateVariablesDefined);
    }
    for (let varName of allStateVariablesAffected) {
      if (!component.state[varName].isResolved) {
        let result = await this.core.dependencies.resolveItem({
          componentIdx: component.componentIdx,
          type: "stateVariable",
          stateVariable: varName,
          force: true
        });
        if (!result.success) {
          throw Error(
            `Can't get value of ${stateVariable} of ${component.componentIdx} as ${varName} couldn't be resolved.`
          );
        }
      }
    }
    let inverseDefinitionArgs = await this.core.getStateVariableDefinitionArguments({
      component,
      stateVariable,
      excludeDependencyValues: stateVarObj.excludeDependencyValuesInInverseDefinition,
      consumeChanges: false
    });
    inverseDefinitionArgs.componentInfoObjects = this.core.componentInfoObjects;
    inverseDefinitionArgs.initialChange = initialChange;
    inverseDefinitionArgs.stateValues = component.stateValues;
    inverseDefinitionArgs.overrideFixed = instruction.overrideFixed;
    inverseDefinitionArgs.shadowedVariable = instruction.shadowedVariable;
    inverseDefinitionArgs.sourceDetails = instruction.sourceDetails;
    if (instruction.overrides) {
      inverseDefinitionArgs.overrides = instruction.overrides;
    }
    let stateVariableForWorkspace = stateVariable;
    if (stateVarObj.isArrayEntry) {
      let arrayStateVariable = stateVarObj.arrayStateVariable;
      stateVariableForWorkspace = arrayStateVariable;
      let desiredValuesForArray = {};
      if (inverseDefinitionArgs.arrayKeys.length === 1) {
        if ("value" in instruction) {
          desiredValuesForArray[inverseDefinitionArgs.arrayKeys[0]] = instruction.value;
        } else if ("valueOfStateVariable" in instruction) {
          desiredValuesForArray[inverseDefinitionArgs.arrayKeys[0]] = await this._resolveValueOfStateVariable(
            instruction,
            component
          );
        }
      } else {
        for (let [
          ind,
          arrayKey
        ] of inverseDefinitionArgs.arrayKeys.entries()) {
          if (Array.isArray(instruction.value)) {
            desiredValuesForArray[arrayKey] = instruction.value[ind];
          } else if (instruction.value instanceof me$3.class) {
            try {
              desiredValuesForArray[arrayKey] = instruction.value.get_component(ind);
            } catch (e3) {
            }
          }
        }
      }
      inverseDefinitionArgs.desiredStateVariableValues = {
        [arrayStateVariable]: desiredValuesForArray
      };
    } else {
      if ("value" in instruction) {
        inverseDefinitionArgs.desiredStateVariableValues = {
          [stateVariable]: instruction.value
        };
      } else if ("valueOfStateVariable" in instruction) {
        inverseDefinitionArgs.desiredStateVariableValues = {
          [stateVariable]: await this._resolveValueOfStateVariable(
            instruction,
            component
          )
        };
      }
    }
    let stateVariableWorkspace = componentWorkspace[stateVariableForWorkspace];
    if (stateVariableWorkspace === void 0) {
      stateVariableWorkspace = componentWorkspace[stateVariableForWorkspace] = {};
    }
    if (stateVarObj.additionalStateVariablesDefined) {
      for (let varName2 of stateVarObj.additionalStateVariablesDefined) {
        let stateVariableForWorkspace2 = varName2;
        let stateVarObj2 = component.state[varName2];
        if (stateVarObj2.isArray) {
          stateVariableForWorkspace2 = stateVarObj.arrayStateVariable;
        }
        let stateVariableWorkspace2 = componentWorkspace[stateVariableForWorkspace2];
        if (stateVariableWorkspace2) {
          Object.assign(
            stateVariableWorkspace,
            stateVariableWorkspace2
          );
          componentWorkspace[stateVariableForWorkspace2] = stateVariableWorkspace;
        }
      }
    }
    inverseDefinitionArgs.workspace = stateVariableWorkspace;
    if (instruction.additionalStateVariableValues) {
      for (let varName2 in instruction.additionalStateVariableValues) {
        if (!stateVarObj.additionalStateVariablesDefined.includes(
          varName2
        )) {
          this.core.addDiagnostic({
            type: "info",
            message: `Can't invert ${varName2} at the same time as ${stateVariable}, as not an additional state variable defined`,
            position: component.position,
            sourceDoc: component.sourceDoc
          });
          continue;
        }
        inverseDefinitionArgs.desiredStateVariableValues[varName2] = instruction.additionalStateVariableValues[varName2];
      }
    }
    if (!stateVarObj.inverseDefinition) {
      this.core.addDiagnostic({
        type: "info",
        message: `Cannot change state variable ${stateVariable} of ${component.componentIdx} as it doesn't have an inverse definition`,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
      return;
    }
    if (!instruction.overrideFixed && !stateVarObj.ignoreFixed && await component.stateValues.fixed) {
      this.core.addDiagnostic({
        type: "info",
        message: `Changing ${stateVariable} of ${component.componentIdx} did not succeed because fixed is true.`,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
      return;
    }
    if (!instruction.overrideFixed && stateVarObj.isLocation && await component.stateValues.fixLocation) {
      this.core.addDiagnostic({
        type: "info",
        message: `Changing ${stateVariable} of ${component.componentIdx} did not succeed because fixLocation is true.`,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
      return;
    }
    if (!(initialChange || await component.stateValues.modifyIndirectly !== false)) {
      this.core.addDiagnostic({
        type: "info",
        message: `Changing ${stateVariable} of ${component.componentIdx} did not succeed because modifyIndirectly is false.`,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
      return;
    }
    let inverseResult = await stateVarObj.inverseDefinition(
      inverseDefinitionArgs
    );
    if (!stateVarObj.excludeDependencyValuesInInverseDefinition) {
      await this.core.dependencies.getStateVariableDependencyValues({
        component,
        stateVariable,
        consumeChanges: true
      });
    }
    if (inverseResult.sendDiagnostics) {
      for (const diagnostic of inverseResult.sendDiagnostics) {
        this.core.addDiagnostic({
          position: component.position,
          sourceDoc: component.sourceDoc,
          ...diagnostic
        });
      }
    }
    if (!inverseResult.success) {
      return;
    }
    let combinedInstructions = [];
    let arrayInstructionInProgress;
    for (let newInstruction of inverseResult.instructions) {
      let foundArrayInstruction = false;
      if (newInstruction.setDependency) {
        let dependencyName = newInstruction.setDependency;
        let dep = this.core.dependencies.downstreamDependencies[component.componentIdx][stateVariable][dependencyName];
        if (["stateVariable", "parentStateVariable"].includes(
          dep.dependencyType
        ) && dep.downstreamComponentIndices.length === 1) {
          let dComponentIdx = dep.downstreamComponentIndices[0];
          let dVarName = dep.mappedDownstreamVariableNamesByComponent[0][0];
          let depStateVarObj = this.core._components[dComponentIdx].state[dVarName];
          if ((depStateVarObj.isArrayEntry || depStateVarObj.isArray) && !depStateVarObj.doNotCombineInverseArrayInstructions) {
            let arrayStateVariable = depStateVarObj.isArrayEntry ? depStateVarObj.arrayStateVariable : dVarName;
            if (arrayInstructionInProgress && !(arrayInstructionInProgress.componentIdx === dComponentIdx && arrayInstructionInProgress.stateVariable === arrayStateVariable && arrayInstructionInProgress.shadowedVariable === newInstruction.shadowedVariable && arrayInstructionInProgress.treatAsInitialChange === newInstruction.treatAsInitialChange)) {
              combinedInstructions.push(
                arrayInstructionInProgress
              );
              arrayInstructionInProgress = void 0;
            }
            if (!(newInstruction.additionalDependencyValues || depStateVarObj.basedOnArrayKeyStateVariables)) {
              foundArrayInstruction = true;
              if (!arrayInstructionInProgress) {
                arrayInstructionInProgress = {
                  combinedArray: true,
                  componentIdx: dComponentIdx,
                  stateVariable: arrayStateVariable,
                  shadowedVariable: newInstruction.shadowedVariable,
                  treatAsInitialChange: newInstruction.treatAsInitialChange,
                  desiredValue: {}
                };
              }
              if (depStateVarObj.isArrayEntry) {
                let arrayKeys = await depStateVarObj.arrayKeys;
                if (arrayKeys.length === 0) {
                  let depArrayStateVarObj = this.core._components[dComponentIdx].state[arrayStateVariable];
                  if (depArrayStateVarObj.allowExtraArrayKeysInInverse) {
                    arrayKeys = depArrayStateVarObj.getArrayKeysFromVarName(
                      {
                        arrayEntryPrefix: depStateVarObj.entryPrefix,
                        varEnding: depStateVarObj.varEnding,
                        numDimensions: depArrayStateVarObj.numDimensions
                      }
                    );
                  }
                }
                if (arrayKeys.length === 1) {
                  arrayInstructionInProgress.desiredValue[arrayKeys[0]] = newInstruction.desiredValue;
                } else {
                  for (let [
                    ind,
                    arrayKey
                  ] of arrayKeys.entries()) {
                    arrayInstructionInProgress.desiredValue[arrayKey] = newInstruction.desiredValue[ind];
                  }
                }
              } else {
                if (depStateVarObj.numDimensions === 1 || !Array.isArray(newInstruction.desiredValue)) {
                  if (typeof newInstruction.desiredValue === "object" && !(newInstruction.desiredValue instanceof me$3.class)) {
                    Object.assign(
                      arrayInstructionInProgress.desiredValue,
                      newInstruction.desiredValue
                    );
                  } else {
                    let firstArrayKey = Array(
                      depStateVarObj.numDimensions
                    ).fill("0").join(",");
                    arrayInstructionInProgress.desiredValue[firstArrayKey] = newInstruction.desiredValue;
                  }
                } else {
                  Object.assign(
                    arrayInstructionInProgress.desiredValue,
                    convertMultidimensionalArrayToKeyedObject(
                      newInstruction.desiredValue,
                      depStateVarObj.numDimensions
                    )
                  );
                }
              }
            }
          }
        }
      }
      if (!foundArrayInstruction) {
        if (arrayInstructionInProgress) {
          combinedInstructions.push(arrayInstructionInProgress);
          arrayInstructionInProgress = void 0;
        }
        combinedInstructions.push(newInstruction);
      }
    }
    if (arrayInstructionInProgress) {
      combinedInstructions.push(arrayInstructionInProgress);
      arrayInstructionInProgress = void 0;
    }
    for (let newInstruction of combinedInstructions) {
      if (newInstruction.setEssentialValue) {
        if (!allStateVariablesAffected.includes(
          newInstruction.setEssentialValue
        )) {
          let foundArrayMatch = false;
          if (stateVarObj.isArrayEntry) {
            let arrayStateVariables = [
              stateVarObj.arrayStateVariable
            ];
            if (stateVarObj.additionalStateVariablesDefined) {
              for (let vName of stateVarObj.additionalStateVariablesDefined) {
                let sObj = component.state[vName];
                if (sObj.isArrayEntry) {
                  arrayStateVariables.push(
                    sObj.arrayStateVariable
                  );
                }
              }
            }
            foundArrayMatch = arrayStateVariables.includes(
              newInstruction.setEssentialValue
            );
          }
          if (!foundArrayMatch) {
            throw Error(
              `Invalid inverse definition of ${stateVariable} of ${component.componentIdx}: specified changing value of ${newInstruction.setEssentialValue}, which is not a state variable defined with ${stateVariable}.`
            );
          }
        }
        if (!component.state[newInstruction.setEssentialValue].hasEssential) {
          throw Error(
            `Invalid inverse definition of ${stateVariable} of ${component.componentIdx}: can't set essential value of ${newInstruction.setEssentialValue} if it is does not have an essential value.`
          );
        }
        if (!("value" in newInstruction)) {
          throw Error(
            `Invalid inverse definition of ${stateVariable} of ${component.componentIdx}: setEssentialValue must specify a value`
          );
        }
        let value = newInstruction.value;
        if (value instanceof me$3.class) {
          let result = await preprocessMathInverseDefinition({
            desiredValue: value,
            stateValues: component.stateValues,
            variableName: newInstruction.setEssentialValue,
            workspace: stateVariableWorkspace
          });
          value = result.desiredValue;
        }
        if (component.state[newInstruction.setEssentialValue].doNotShadowEssential || component.state[newInstruction.setEssentialValue].shadowVariable) {
          this.calculateEssentialVariableChanges({
            component,
            varName: newInstruction.setEssentialValue,
            value,
            newStateVariableValues,
            recurseToShadows: false
          });
        } else {
          let baseComponent = component;
          while (baseComponent.shadows && (baseComponent.shadows.propVariable === void 0 || baseComponent.doenetAttributes.fromImplicitProp && this.core._components[baseComponent.shadows.componentIdx].constructor.implicitPropReturnsSameType)) {
            baseComponent = this.core._components[baseComponent.shadows.componentIdx];
            if (!instruction.overrideFixed && !stateVarObj.ignoreFixed && await baseComponent.stateValues.fixed) {
              this.core.addDiagnostic({
                type: "info",
                message: `Changing ${stateVariable} of ${baseComponent.componentIdx} did not succeed because fixed is true.`,
                position: baseComponent.position,
                sourceDoc: baseComponent.sourceDoc
              });
              return;
            }
            if (!instruction.overrideFixed && !stateVarObj.isLocation && await baseComponent.stateValues.fixLocation) {
              this.core.addDiagnostic({
                type: "info",
                message: `Changing ${stateVariable} of ${baseComponent.componentIdx} did not succeed because fixLocation is true.`,
                position: baseComponent.position,
                sourceDoc: baseComponent.sourceDoc
              });
              return;
            }
          }
          this.calculateEssentialVariableChanges({
            component: baseComponent,
            varName: newInstruction.setEssentialValue,
            value,
            newStateVariableValues
          });
        }
      } else if (newInstruction.setDependency) {
        let dependencyName = newInstruction.setDependency;
        let dep = this.core.dependencies.downstreamDependencies[component.componentIdx][stateVariable][dependencyName];
        if (dep.dependencyType === "child") {
          if (newInstruction.childIndex === void 0) {
            newInstruction.childIndex = 0;
          }
          if (newInstruction.variableIndex === void 0) {
            newInstruction.variableIndex = 0;
          }
          let childInd = newInstruction.childIndex;
          if (dep.downstreamPrimitives[childInd] !== null) {
            if (typeof newInstruction.desiredValue === typeof dep.downstreamPrimitives[childInd]) {
              let parent = this.core._components[dep.parentIdx];
              let activeChildInd = dep.activeChildrenIndices[childInd];
              if (parent.compositeReplacementActiveRange) {
                for (let compositeObj of parent.compositeReplacementActiveRange) {
                  if (compositeObj.firstInd <= activeChildInd && compositeObj.lastInd >= activeChildInd) {
                    throw Error(
                      `Need to implement changing primitive replacements from composite (parent ${parent.componentIdx}, activeChildInd ${activeChildInd})`
                    );
                  }
                }
              }
              let definingInd = activeChildInd;
              if (parent.compositeReplacementActiveRange) {
                for (let compositeObj of parent.compositeReplacementActiveRange) {
                  if (compositeObj.lastInd < definingInd) {
                    definingInd -= compositeObj.lastInd - compositeObj.firstInd;
                  }
                }
              }
              let baseParent = parent;
              while (baseParent.shadows && baseParent.shadows.propVariable === void 0) {
                baseParent = this.core._components[baseParent.shadows.componentIdx];
              }
              let markToIgnoreForParent;
              if (newInstruction.ignoreChildChangeForComponent) {
                markToIgnoreForParent = parent.componentIdx;
              }
              this.calculatePrimitiveChildChanges({
                parent: baseParent,
                definingInd,
                newValue: newInstruction.desiredValue,
                newStateVariableValues,
                markToIgnoreForParent
              });
            }
          } else {
            let downstreamInd = dep.downstreamPrimitives.slice(0, childInd + 1).filter((x2) => !x2).length - 1;
            let cIdx = dep.downstreamComponentIndices[downstreamInd];
            if (cIdx == void 0) {
              throw Error(
                `Invalid inverse definition of ${stateVariable} of ${component.componentIdx}: ${dependencyName} child of index ${newInstruction.childIndex} does not exist.`
              );
            }
            let varName = dep.mappedDownstreamVariableNamesByComponent[newInstruction.childIndex][newInstruction.variableIndex];
            if (!varName) {
              throw Error(
                `Invalid inverse definition of ${stateVariable} of ${component.componentIdx}: ${dependencyName} variable of index ${newInstruction.variableIndex} does not exist.`
              );
            }
            await this._recurseInto({
              inst: {
                componentIdx: cIdx,
                stateVariable: varName,
                value: newInstruction.desiredValue,
                overrideFixed: instruction.overrideFixed,
                arrayKey: newInstruction.arrayKey
              },
              newInstruction,
              workspace,
              newStateVariableValues
            });
          }
        } else if ([
          "attributeComponent",
          "shadowSource",
          "adapterSource",
          "targetComponent"
        ].includes(dep.dependencyType)) {
          let cIdx = dep.downstreamComponentIndices[0];
          let varName = dep.mappedDownstreamVariableNamesByComponent[0][newInstruction.variableIndex];
          if (!varName) {
            throw Error(
              `Invalid inverse definition of ${stateVariable} of ${component.componentIdx}: ${dependencyName} variable of index ${newInstruction.variableIndex} does not exist.`
            );
          }
          await this._recurseInto({
            inst: {
              componentIdx: cIdx,
              stateVariable: varName,
              value: newInstruction.desiredValue,
              overrideFixed: instruction.overrideFixed,
              arrayKey: newInstruction.arrayKey
            },
            newInstruction,
            workspace,
            newStateVariableValues
          });
        } else if ([
          "stateVariable",
          "parentStateVariable",
          "adapterSourceStateVariable",
          "sourceCompositeStateVariable"
        ].includes(dep.dependencyType) && dep.downstreamComponentIndices.length === 1) {
          let dComponentIdx = dep.downstreamComponentIndices[0];
          let dVarName = dep.mappedDownstreamVariableNamesByComponent[0][0];
          let inst = {
            componentIdx: dComponentIdx,
            stateVariable: dVarName,
            value: newInstruction.desiredValue,
            overrideFixed: instruction.overrideFixed,
            shadowedVariable: newInstruction.shadowedVariable
          };
          if (newInstruction.additionalDependencyValues) {
            let stateVarObj2 = this.core._components[dComponentIdx].state[dVarName];
            for (let dependencyName2 in newInstruction.additionalDependencyValues) {
              let dep2 = this.core.dependencies.downstreamDependencies[component.componentIdx][stateVariable][dependencyName2];
              if (!([
                "stateVariable",
                "parentStateVariable"
              ].includes(dep2.dependencyType) && dep2.downstreamComponentIndices.length === 1)) {
                this.core.addDiagnostic({
                  type: "info",
                  message: `Can't simultaneously set additional dependency value ${dependencyName2} if it isn't a state variable`,
                  position: this.core._components[dComponentIdx].position,
                  sourceDoc: this.core._components[dComponentIdx].sourceDoc
                });
                continue;
              }
              let varName2 = dep2.mappedDownstreamVariableNamesByComponent[0][0];
              if (dep2.downstreamComponentIndices[0] !== dComponentIdx || !stateVarObj2.additionalStateVariablesDefined.includes(
                varName2
              )) {
                this.core.addDiagnostic({
                  type: "info",
                  message: `Can't simultaneously set additional dependency value ${dependencyName2} if it doesn't correspond to additional state variable defined of ${dependencyName}'s state variable`,
                  position: this.core._components[dComponentIdx].position,
                  sourceDoc: this.core._components[dComponentIdx].sourceDoc
                });
                continue;
              }
              if (!inst.additionalStateVariableValues) {
                inst.additionalStateVariableValues = {};
              }
              inst.additionalStateVariableValues[varName2] = newInstruction.additionalDependencyValues[dependencyName2];
            }
          }
          await this._recurseInto({
            inst,
            newInstruction,
            workspace,
            newStateVariableValues
          });
        } else {
          throw Error(
            `unimplemented dependency type ${dep.dependencyType} in requestComponentChanges`
          );
        }
      } else if (newInstruction.combinedArray) {
        await this._recurseInto({
          inst: {
            componentIdx: newInstruction.componentIdx,
            stateVariable: newInstruction.stateVariable,
            value: newInstruction.desiredValue,
            overrideFixed: instruction.overrideFixed,
            shadowedVariable: newInstruction.shadowedVariable
          },
          newInstruction,
          workspace,
          newStateVariableValues
        });
      } else {
        throw Error(
          `Unrecognized instruction in inverse definition of ${stateVariable} of ${component.componentIdx}: keys [${Object.keys(newInstruction).join(", ")}]`
        );
      }
    }
    return;
  }
  calculateEssentialVariableChanges({
    component,
    varName,
    value,
    newStateVariableValues,
    recurseToShadows = true
  }) {
    if (!newStateVariableValues[component.componentIdx]) {
      newStateVariableValues[component.componentIdx] = {};
    }
    if (component.state[varName].isArray) {
      if (!newStateVariableValues[component.componentIdx][varName]) {
        newStateVariableValues[component.componentIdx][varName] = {
          mergeObject: true
        };
      }
      Object.assign(
        newStateVariableValues[component.componentIdx][varName],
        value
      );
    } else {
      newStateVariableValues[component.componentIdx][varName] = value;
    }
    if (recurseToShadows && component.shadowedBy) {
      for (let shadow of component.shadowedBy) {
        if (shadow.shadows.propVariable === void 0 || shadow.doenetAttributes.fromImplicitProp && component.constructor.implicitPropReturnsSameType) {
          this.calculateEssentialVariableChanges({
            component: shadow,
            varName,
            value,
            newStateVariableValues
          });
        }
      }
    }
  }
  calculatePrimitiveChildChanges({
    parent,
    definingInd,
    newValue,
    newStateVariableValues,
    markToIgnoreForParent
  }) {
    if (!newStateVariableValues[parent.componentIdx]) {
      newStateVariableValues[parent.componentIdx] = {};
    }
    if (parent.componentIdx === markToIgnoreForParent) {
      newStateVariableValues[parent.componentIdx][`__def_primitive_ignore_${definingInd}`] = newValue;
    } else {
      newStateVariableValues[parent.componentIdx][`__def_primitive_${definingInd}`] = newValue;
    }
    if (parent.shadowedBy) {
      for (let shadow of parent.shadowedBy) {
        if (shadow.shadows.propVariable === void 0) {
          this.calculatePrimitiveChildChanges({
            parent: shadow,
            definingInd,
            newValue,
            newStateVariableValues,
            markToIgnoreForParent
          });
        }
      }
    }
  }
  /**
   * Merge `value` for a state variable into the `cumulativeStateVariableChanges`
   * bag at `[stateId][varName]`. If the existing entry is an object with
   * `mergeObject = true`, attributes are `Object.assign`ed in (rather than
   * replacing the whole entry). Function values inside `value` are stripped
   * via `removeFunctionsMathExpressionClass` before write.
   *
   * Lazily creates `cumulativeStateVariableChanges[stateId]` if absent.
   */
  mergeIntoCumulative(stateId, varName, value) {
    const cumulative = this.core.cumulativeStateVariableChanges;
    if (!cumulative[stateId]) {
      cumulative[stateId] = {};
    }
    const cumValues = cumulative[stateId][varName];
    const incoming = removeFunctionsMathExpressionClass(value);
    if (typeof cumValues === "object" && cumValues !== null && cumValues.mergeObject) {
      Object.assign(cumValues, incoming);
    } else {
      cumulative[stateId][varName] = incoming;
    }
  }
  /**
   * Resolve `instruction.valueOfStateVariable` to its current value on
   * `component`: substitute aliases, look up the resolved state-variable
   * object, and `await` its `.value` getter. Throws a uniform
   * "value of state variable not found" error if the alias resolves to
   * a name that doesn't exist on the component's state.
   */
  async _resolveValueOfStateVariable(instruction, component) {
    const otherStateVariable = this.core.substituteAliases({
      stateVariables: [instruction.valueOfStateVariable],
      componentClass: component.constructor
    })[0];
    const sObj = component.state[otherStateVariable];
    if (!sObj) {
      throw Error(
        `Invalid instruction to change ${instruction.stateVariable} of ${instruction.componentIdx}, value of state variable ${instruction.valueOfStateVariable} not found.`
      );
    }
    return await sObj.value;
  }
  /**
   * Forward an inverse-definition recursion: invoke
   * `requestComponentChanges` with the prepared `inst` instruction,
   * propagating `treatAsInitialChange` from `newInstruction` and threading
   * the same `workspace` / `newStateVariableValues` bags through.
   */
  async _recurseInto({
    inst,
    newInstruction,
    workspace,
    newStateVariableValues
  }) {
    await this.requestComponentChanges({
      instruction: inst,
      initialChange: newInstruction.treatAsInitialChange === true,
      workspace,
      newStateVariableValues
    });
  }
}
function convertMultidimensionalArrayToKeyedObject(array, nDim) {
  if (nDim === 1) {
    return Object.assign({}, array);
  }
  let newObj = {};
  for (let ind in array) {
    let subObj = convertMultidimensionalArrayToKeyedObject(
      array[ind],
      nDim - 1
    );
    for (let key in subObj) {
      newObj[`${ind},${key}`] = subObj[key];
    }
  }
  return newObj;
}
async function handleNavigatingToComponent({
  core: core2,
  componentIdx,
  hash
}) {
  let component = core2._components[componentIdx];
  if (!component) {
    return;
  }
  let componentAndAncestors = [
    componentIdx,
    ...component.ancestors.map((x2) => x2.componentIdx)
  ];
  let openedParent = false;
  for (let cIdx of componentAndAncestors) {
    let comp = core2._components[cIdx];
    if (comp.actions?.revealSection) {
      let isOpen = await comp.stateValues.open;
      if (isOpen === false) {
        await core2.performAction({
          componentIdx: cIdx,
          actionName: "revealSection"
        });
        if (cIdx !== componentIdx) {
          openedParent = true;
        }
      }
    }
  }
  if (openedParent) {
    postMessage({
      messageType: "navigateToHash",
      args: { hash }
    });
  }
}
function navigateToTarget({
  core: core2,
  args
}) {
  postMessage({
    messageType: "navigateToTarget",
    coreId: core2.coreId,
    args
  });
}
class ProcessQueue {
  constructor({ core: core2 }) {
    this.core = core2;
    this.queue = [];
    this.processing = false;
    this.stopProcessingRequests = false;
  }
  /**
   * Clear the queue and processing flags. Called from `Core.generateDast`
   * so state from any previous run does not leak in.
   */
  reset() {
    this.queue = [];
    this.processing = false;
    this.stopProcessingRequests = false;
  }
  /**
   * Start `executeProcesses` if it's not already draining the queue.
   * Called from each request entry point after the new entry is pushed.
   */
  _kickoff() {
    if (!this.processing) {
      this.processing = true;
      this.executeProcesses().catch((e3) => console.error(e3));
    }
  }
  /**
   * Drain `this.queue` serially, dispatching each entry to Core's
   * `performUpdate`/`performAction`/`performRecordEvent`. `skippable`
   * update/action entries are dropped (without resolving) when more work
   * is already queued behind them, so a flood of intermediate values
   * collapses to the latest.
   *
   * Errors from per-entry dispatch are caught and routed to the entry's
   * `reject`; throws outside that path bubble up via the `_kickoff`
   * `.catch(console.error)` so we don't drop them silently.
   */
  async executeProcesses() {
    if (this.stopProcessingRequests) {
      return;
    }
    while (this.queue.length > 0) {
      let nextUpdateInfo = this.queue.splice(0, 1)[0];
      let result;
      try {
        if (nextUpdateInfo.type === "update") {
          if (!nextUpdateInfo.skippable || this.queue.length < 2) {
            result = await this.core.performUpdate(nextUpdateInfo);
          }
        } else if (nextUpdateInfo.type === "action") {
          if (!nextUpdateInfo.skippable || this.queue.length < 2) {
            result = await this.core.performAction(nextUpdateInfo);
          }
        } else if (nextUpdateInfo.type === "recordEvent") {
          result = await this.core.performRecordEvent(nextUpdateInfo);
        } else {
          throw Error(
            `Unrecognized process type: ${nextUpdateInfo.type}`
          );
        }
        nextUpdateInfo.resolve(result);
      } catch (e3) {
        console.error(e3);
        nextUpdateInfo.reject(
          typeof e3 === "object" && e3 && "message" in e3 && typeof e3.message === "string" ? e3.message : "Error in core"
        );
      }
    }
    this.processing = false;
  }
  /**
   * Enqueue a component action (e.g. button click, input commit) and
   * return a Promise that resolves with the action's result once the
   * queue drains to it. `args.skippable === true` lets the queue drop
   * intermediate entries when newer work is queued behind them.
   */
  requestAction({
    componentIdx,
    actionName,
    args
  }) {
    return new Promise((resolve, reject2) => {
      let skippable = args?.skippable;
      this.queue.push({
        type: "action",
        componentIdx,
        actionName,
        args,
        skippable,
        resolve,
        reject: reject2
      });
      this._kickoff();
    });
  }
  /**
   * Enqueue a state-variable update batch and return a Promise that
   * resolves once the queue drains to it. In read-only mode the update
   * is short-circuited to a renderer-only refresh (so the renderer can
   * revert any optimistic UI it showed) and the queue is skipped.
   */
  async requestUpdate({
    updateInstructions,
    transient = false,
    event,
    skippable = false,
    overrideReadOnly = false
  }) {
    if (this.core.flags.readOnly && !overrideReadOnly) {
      let sourceInformation = {};
      for (let instruction of updateInstructions) {
        let componentSourceInformation = sourceInformation[instruction.componentIdx];
        if (!componentSourceInformation) {
          componentSourceInformation = sourceInformation[instruction.componentIdx] = {};
        }
        if (instruction.sourceDetails) {
          Object.assign(
            componentSourceInformation,
            instruction.sourceDetails
          );
        }
      }
      await this.core.updateRendererInstructions({
        componentNamesToUpdate: updateInstructions.map(
          (x2) => x2.componentIdx
        ),
        sourceOfUpdate: { sourceInformation }
      });
      return;
    }
    return new Promise((resolve, reject2) => {
      this.queue.push({
        type: "update",
        updateInstructions,
        transient,
        event,
        skippable,
        resolve,
        reject: reject2
      });
      this._kickoff();
    });
  }
  /**
   * Enqueue an event for `performRecordEvent`, or for visibility-changed
   * events bypass the queue and route directly to the visibility tracker
   * (which manages its own debounced send). Resuming visibility measuring
   * here piggybacks on every recorded event so suspended documents wake
   * back up on the next interaction.
   */
  requestRecordEvent(event) {
    this.core.resumeVisibilityMeasuring();
    if (event.verb === "visibilityChanged") {
      return this.core.processVisibilityChangedEvent(event);
    }
    return this.sendRecordEvent(event);
  }
  /**
   * Push a recordEvent entry directly onto the queue and kick the drain
   * loop. Called by `VisibilityTracker.sendVisibilityChangedEvents` to
   * emit aggregated `isVisible` events. Bypasses `requestRecordEvent`
   * because that path routes inbound `visibilityChanged` events into
   * VisibilityTracker and calls `resumeVisibilityMeasuring` — both
   * inappropriate on the outbound batch path.
   */
  sendRecordEvent(event) {
    return new Promise((resolve, reject2) => {
      this.queue.push({
        type: "recordEvent",
        event,
        resolve,
        reject: reject2
      });
      this._kickoff();
    });
  }
}
class RendererInstructionBuilder {
  constructor({ core: core2 }) {
    this.core = core2;
    this.componentsToRender = {};
    this.componentsWithChangedChildrenToRender = /* @__PURE__ */ new Set();
    this.rendererState = {};
  }
  /**
   * Clear all per-document renderer state. Called from `Core.generateDast`
   * so that state from any previous run does not leak into a fresh document.
   */
  reset() {
    this.componentsToRender = {};
    this.componentsWithChangedChildrenToRender = /* @__PURE__ */ new Set();
    this.rendererState = {};
  }
  /**
   * Forward an update batch to the renderer, attaching the current
   * diagnostics queue (which both passes the data and clears the
   * pending flag). `init: true` is used only for the very first
   * document render.
   */
  callUpdateRenderers(args, init = false) {
    let diagnostics = void 0;
    if (this.core.hasPendingDiagnostics) {
      diagnostics = this.core.getDiagnostics().diagnostics;
    }
    this.core.updateRenderersCallback({ ...args, init, diagnostics });
  }
  /**
   * Build and send the next renderer-update batch.
   *
   * Two passes:
   *  1. For every parent in `componentsWithChangedChildrenToRender`,
   *     compare the rendered-child identifier list against the previous
   *     one and, if it changed, tear down the old child renderers and
   *     re-create the new ones (which can recurse).
   *  2. For every component in `componentNamesToUpdate`, capture the
   *     current `forRenderer` state values into a `rendererStatesToUpdate`
   *     entry and append cached `childrenInstructions` if pass 1
   *     produced new ones.
   *
   * The combined batch is delivered via `callUpdateRenderers`.
   */
  async updateRendererInstructions({
    componentNamesToUpdate,
    sourceOfUpdate = {},
    actionId
  }) {
    let deletedRenderers = [];
    let updateInstructions = [];
    let rendererStatesToUpdate = [];
    let newChildrenInstructions = {};
    let componentsWithChangedChildrenToRenderInProgress = this.componentsWithChangedChildrenToRender;
    this.componentsWithChangedChildrenToRender = /* @__PURE__ */ new Set();
    for (let componentIdx of componentsWithChangedChildrenToRenderInProgress) {
      if (componentIdx in this.componentsToRender) {
        let currentChildIdentifiers = [];
        let unproxiedComponent = this.core._components[componentIdx];
        let indicesToRender = [];
        if (unproxiedComponent && unproxiedComponent.constructor.renderChildren) {
          if (!unproxiedComponent.matchedCompositeChildren) {
            await deriveChildResultsFromDefiningChildren({
              core: this.core,
              parent: unproxiedComponent,
              expandComposites: true,
              forceExpandComposites: true
            });
          }
          indicesToRender = await returnActiveChildrenIndicesToRender(
            {
              core: this.core,
              component: unproxiedComponent
            }
          );
          let renderedInd = 0;
          for (let [
            ind,
            child
          ] of unproxiedComponent.activeChildren.entries()) {
            if (indicesToRender.includes(ind)) {
              if (child.rendererType) {
                currentChildIdentifiers.push(
                  `nameType:${child.componentIdx};${child.componentType}`
                );
                renderedInd++;
              } else if (typeof child === "string") {
                currentChildIdentifiers.push(
                  `string${renderedInd}:${child}`
                );
                renderedInd++;
              } else if (typeof child === "number") {
                currentChildIdentifiers.push(
                  `number${renderedInd}:${child.toString()}`
                );
                renderedInd++;
              } else {
                currentChildIdentifiers.push("");
              }
            } else {
              currentChildIdentifiers.push("");
            }
          }
        }
        let previousChildRenderers = this.componentsToRender[componentIdx].children;
        let previousChildIdentifiers = [];
        for (let [
          ind,
          child
        ] of previousChildRenderers.entries()) {
          if (child === null) {
            previousChildIdentifiers.push("");
          } else if (child.componentIdx != void 0) {
            previousChildIdentifiers.push(
              `nameType:${child.componentIdx};${child.componentType}`
            );
          } else if (typeof child === "string") {
            previousChildIdentifiers.push(`string${ind}:${child}`);
          } else if (typeof child === "number") {
            previousChildIdentifiers.push(
              `number${ind}:${child.toString()}`
            );
          }
        }
        if (currentChildIdentifiers.length !== previousChildIdentifiers.length || currentChildIdentifiers.some(
          (v2, i2) => v2 !== previousChildIdentifiers[i2]
        )) {
          for (let child of previousChildRenderers) {
            if (child?.componentIdx != void 0) {
              let deletedNames = this.deleteFromComponentsToRender({
                componentIdx: child.componentIdx,
                recurseToChildren: true,
                componentsWithChangedChildrenToRenderInProgress
              });
              deletedRenderers.push(...deletedNames);
            }
          }
          let childrenToRender = [];
          if (indicesToRender.length > 0) {
            for (let [
              ind,
              child
            ] of unproxiedComponent.activeChildren.entries()) {
              if (indicesToRender.includes(ind)) {
                if (child.rendererType) {
                  let results = await this.initializeRenderedComponentInstruction(
                    child,
                    componentsWithChangedChildrenToRenderInProgress
                  );
                  childrenToRender.push(
                    results.componentToRender
                  );
                  rendererStatesToUpdate.push(
                    ...results.rendererStatesToUpdate
                  );
                } else if (typeof child === "string") {
                  childrenToRender.push(child);
                } else if (typeof child === "number") {
                  childrenToRender.push(
                    child.toString()
                  );
                } else {
                  childrenToRender.push(null);
                }
              } else {
                childrenToRender.push(null);
              }
            }
          }
          this.componentsToRender[componentIdx].children = childrenToRender;
          newChildrenInstructions[componentIdx] = childrenToRender;
          componentsWithChangedChildrenToRenderInProgress.delete(
            componentIdx
          );
          if (!componentNamesToUpdate.includes(componentIdx)) {
            componentNamesToUpdate.push(componentIdx);
          }
        }
      }
    }
    for (let componentIdx of componentNamesToUpdate) {
      if (componentIdx in this.componentsToRender) {
        let component = this.core._components[componentIdx];
        if (component) {
          let stateValuesForRenderer = {};
          for (let stateVariable in component.state) {
            if (component.state[stateVariable].forRenderer) {
              let value = removeFunctionsMathExpressionClass(
                await component.state[stateVariable].value
              );
              stateValuesForRenderer[stateVariable] = value;
            }
          }
          if (component.compositeReplacementActiveRange) {
            stateValuesForRenderer._compositeReplacementActiveRange = component.compositeReplacementActiveRange;
          }
          let newRendererState = {
            componentIdx,
            stateValues: stateValuesForRenderer,
            rendererType: component.rendererType
            // TODO: need this to ignore baseVariables change: is this right place?
          };
          if (!this.rendererState[componentIdx]) {
            this.rendererState[componentIdx] = {};
          }
          this.rendererState[componentIdx].stateValues = stateValuesForRenderer;
          if (newChildrenInstructions[componentIdx]) {
            newRendererState.childrenInstructions = newChildrenInstructions[componentIdx];
            this.rendererState[componentIdx].childrenInstructions = newChildrenInstructions[componentIdx];
          }
          rendererStatesToUpdate.push(newRendererState);
        }
      }
    }
    if (rendererStatesToUpdate.length > 0) {
      let instruction = {
        instructionType: "updateRendererStates",
        rendererStatesToUpdate,
        sourceOfUpdate
      };
      updateInstructions.splice(0, 0, instruction);
    }
    this.callUpdateRenderers({ updateInstructions, actionId });
  }
  /**
   * Construct the initial renderer instructions for `component` and
   * its rendered descendants: collect `forRenderer` state values, build
   * the children-to-render list (recursing on rendered children), and
   * register the component in `componentsToRender`. Returns
   * `{ componentToRender, rendererStatesToUpdate, rendererStatesToForceUpdate }`
   * — the third bucket holds states marked `alwaysUpdateRenderer`,
   * which are flushed in a second pass without `init: true`.
   *
   * Returns `undefined` if `component.rendererType` is unset (callers
   * only invoke this for components that render).
   */
  async initializeRenderedComponentInstruction(component, componentsWithChangedChildrenToRenderInProgress = /* @__PURE__ */ new Set()) {
    if (component.rendererType === void 0) {
      return;
    }
    if (!component.matchedCompositeChildren) {
      await deriveChildResultsFromDefiningChildren({
        core: this.core,
        parent: component,
        expandComposites: true
        //forceExpandComposites: true,
      });
    }
    let rendererStatesToUpdate = [];
    let rendererStatesToForceUpdate = [];
    let stateValuesForRenderer = {};
    let stateValuesForRendererAlwaysUpdate = {};
    let alwaysUpdate = false;
    for (let stateVariable in component.state) {
      if (component.state[stateVariable].forRenderer) {
        stateValuesForRenderer[stateVariable] = removeFunctionsMathExpressionClass(
          await component.state[stateVariable].value
        );
        if (component.state[stateVariable].alwaysUpdateRenderer) {
          alwaysUpdate = true;
        }
      }
    }
    if (component.compositeReplacementActiveRange) {
      stateValuesForRenderer._compositeReplacementActiveRange = component.compositeReplacementActiveRange;
    }
    if (alwaysUpdate) {
      stateValuesForRendererAlwaysUpdate = stateValuesForRenderer;
    }
    let componentIdx = component.componentIdx;
    let childrenToRender = [];
    if (component.constructor.renderChildren) {
      let indicesToRender = await returnActiveChildrenIndicesToRender({
        core: this.core,
        component
      });
      for (let [
        ind,
        child
      ] of component.activeChildren.entries()) {
        if (indicesToRender.includes(ind)) {
          if (child.rendererType) {
            let results = await this.initializeRenderedComponentInstruction(
              child,
              componentsWithChangedChildrenToRenderInProgress
            );
            childrenToRender.push(results.componentToRender);
            rendererStatesToUpdate.push(
              ...results.rendererStatesToUpdate
            );
            rendererStatesToForceUpdate.push(
              ...results.rendererStatesToForceUpdate
            );
          } else if (typeof child === "string") {
            childrenToRender.push(child);
          } else if (typeof child === "number") {
            childrenToRender.push(child.toString());
          } else {
            childrenToRender.push(null);
          }
        } else {
          childrenToRender.push(null);
        }
      }
    }
    rendererStatesToUpdate.push({
      componentIdx,
      stateValues: stateValuesForRenderer,
      childrenInstructions: childrenToRender
    });
    if (Object.keys(stateValuesForRendererAlwaysUpdate).length > 0) {
      rendererStatesToForceUpdate.push({
        componentIdx,
        stateValues: stateValuesForRendererAlwaysUpdate
      });
    }
    this.rendererState[componentIdx] = {
      stateValues: stateValuesForRenderer,
      childrenInstructions: childrenToRender
    };
    componentsWithChangedChildrenToRenderInProgress.delete(componentIdx);
    let requestActions = {};
    for (let actionName in component.actions) {
      requestActions[actionName] = {
        actionName,
        componentIdx: component.componentIdx
      };
    }
    for (let actionName in component.externalActions) {
      let action = await component.externalActions[actionName];
      if (action) {
        requestActions[actionName] = {
          actionName,
          componentIdx: action.componentIdx
        };
      }
    }
    let rendererInstructions = {
      componentIdx,
      effectiveIdx: component.componentOrAdaptedIdx,
      id: this.getRendererId(component),
      componentType: component.componentType,
      rendererType: component.rendererType,
      actions: requestActions
    };
    this.componentsToRender[componentIdx] = {
      children: childrenToRender
    };
    return {
      componentToRender: rendererInstructions,
      rendererStatesToUpdate,
      rendererStatesToForceUpdate
    };
  }
  /**
   * Get the `rendererId` of `component`,
   * where `rendererId` is the `rootName` of the component, if it exists,
   * else the `componentIdx` as a string.
   *
   * The `rootName` is the simplest unique reference to the component
   * when the document root is the origin. As `rootName` is designed to be
   * a HTML id, indices are represented with `:`. For example,
   * if `$a.b[2][3].c` is the simplest reference to a component from the root,
   * then its root name will be `a.b:2:3.c`.
   *
   * If a component was adapted from another component,
   * then the `renderedId` of the original component is used instead,
   * as that corresponds to the component that was authored.
   */
  getRendererId(component) {
    return this.core.rootNames?.[component.componentOrAdaptedIdx] ?? `_id_${component.componentOrAdaptedIdx.toString()}`;
  }
  /**
   * Drop `componentIdx` (and, by default, every nested rendered child)
   * from `componentsToRender`, and clear it from the in-progress
   * change set. Returns the flat list of component indices that were
   * removed so the caller can include them in the renderer update.
   */
  deleteFromComponentsToRender({
    componentIdx,
    recurseToChildren = true,
    componentsWithChangedChildrenToRenderInProgress
  }) {
    let deletedComponentNames = [componentIdx];
    if (recurseToChildren) {
      let componentInstruction = this.componentsToRender[componentIdx];
      if (componentInstruction) {
        for (let child of componentInstruction.children) {
          if (child) {
            let additionalDeleted = this.deleteFromComponentsToRender({
              componentIdx: child.componentIdx,
              recurseToChildren,
              componentsWithChangedChildrenToRenderInProgress
            });
            deletedComponentNames.push(...additionalDeleted);
          }
        }
      }
    }
    delete this.componentsToRender[componentIdx];
    componentsWithChangedChildrenToRenderInProgress.delete(componentIdx);
    return deletedComponentNames;
  }
  /**
   * Drain `updateInfo.componentsToUpdateRenderers` into a renderer
   * update batch. If the first pass produced new composite replacement
   * work (which can happen when child results get derived during the
   * pass), apply those replacement changes and run a second drain so
   * the renderer sees a fully consistent tree.
   */
  async updateAllChangedRenderers(sourceInformation = {}, actionId) {
    let componentNamesToUpdate = [
      ...this.core.updateInfo.componentsToUpdateRenderers
    ];
    this.core.updateInfo.componentsToUpdateRenderers.clear();
    await this.updateRendererInstructions({
      componentNamesToUpdate,
      sourceOfUpdate: { sourceInformation, local: true },
      actionId
    });
    if (this.core.updateInfo.compositesToUpdateReplacements.size > 0) {
      await this.core.replacementChangesFromCompositesToUpdate();
      let componentNamesToUpdate2 = [
        ...this.core.updateInfo.componentsToUpdateRenderers
      ];
      this.core.updateInfo.componentsToUpdateRenderers.clear();
      await this.updateRendererInstructions({
        componentNamesToUpdate: componentNamesToUpdate2,
        sourceOfUpdate: { sourceInformation, local: true },
        actionId
      });
    }
  }
}
class StalenessPropagator {
  constructor({ core: core2 }) {
    this.core = core2;
  }
  async createFromArrayEntry({
    stateVariable,
    component,
    initializeOnly = false
  }) {
    if (!component.arrayEntryPrefixes) {
      throw Error(
        `Unknown state variable ${stateVariable} of ${component.componentIdx}`
      );
    }
    let arrayEntryPrefixesLongestToShortest = Object.keys(
      component.arrayEntryPrefixes
    ).sort((a2, b2) => b2.length - a2.length);
    for (let arrayEntryPrefix of arrayEntryPrefixesLongestToShortest) {
      if (stateVariable.substring(0, arrayEntryPrefix.length) === arrayEntryPrefix) {
        let arrayVariableName = component.arrayEntryPrefixes[arrayEntryPrefix];
        await ensureStateVariableMaterialized({
          core: this.core,
          component,
          stateVariable: arrayVariableName
        });
        let arrayStateVarObj = component.state[arrayVariableName];
        let arrayKeys = arrayStateVarObj.getArrayKeysFromVarName({
          arrayEntryPrefix,
          varEnding: stateVariable.substring(arrayEntryPrefix.length),
          numDimensions: arrayStateVarObj.numDimensions
        });
        if (arrayKeys.length > 0) {
          await initializeStateVariable({
            core: this.core,
            component,
            stateVariable,
            arrayStateVariable: arrayVariableName,
            arrayEntryPrefix
          });
          if (initializeOnly) {
            return;
          }
          let allStateVariablesAffected = [stateVariable];
          if (component.state[stateVariable].additionalStateVariablesDefined) {
            allStateVariablesAffected.push(
              ...component.state[stateVariable].additionalStateVariablesDefined
            );
            for (let additionalVar of component.state[stateVariable].additionalStateVariablesDefined) {
              if (!component.state[additionalVar]) {
                await this.createFromArrayEntry({
                  stateVariable: additionalVar,
                  component,
                  initializeOnly: true
                });
              }
            }
          }
          await this.core.dependencies.ensureStateVariableDependenciesSetUp(
            component.componentIdx,
            stateVariable
          );
          let newStateVariablesToResolve = [];
          for (let varName of allStateVariablesAffected) {
            this.core.dependencies.checkForCircularDependency({
              componentIdx: component.componentIdx,
              varName
            });
            newStateVariablesToResolve.push(varName);
          }
          await this.core.dependencies.resolveStateVariablesIfReady({
            component,
            stateVariables: newStateVariablesToResolve
          });
          return;
        }
      }
    }
    throw Error(
      `Unknown state variable ${stateVariable} of ${component.componentIdx}`
    );
  }
  async markDescendantsToUpdateRenderers(component) {
    if (component.constructor.renderChildren) {
      let indicesToRender = await returnActiveChildrenIndicesToRender({
        core: this.core,
        component
      });
      for (let ind of indicesToRender) {
        let child = component.activeChildren[ind];
        this.core.updateInfo.componentsToUpdateRenderers.add(
          child.componentIdx
        );
        await this.markDescendantsToUpdateRenderers(child);
      }
    }
  }
  /**
   * Public entry point used after a value has materially changed: invalidate
   * `varName` and every state variable downstream of it.
   *
   *  - Adds `component.componentIdx` to `componentsToUpdateRenderers` if
   *    `varName` is in the component's renderer-variable set.
   *  - Builds the `allStateVariablesAffectedObj` covering `varName` and
   *    every entry in its `additionalStateVariablesDefined` group, then
   *    dispatches into `_processStaleVisit`, which:
   *      - Calls each variable's `markStale` predicate, classifying the
   *        result as `fresh` (no recursion), `partiallyFresh` (mark and
   *        recurse), or fully stale (replace `value` with the lazy getter
   *        and recurse). For array state variables, the array-entry remap
   *        bridges array-level freshness to the per-entry mirrors via
   *        `_remapArrayEntryFreshness`.
   *      - When the variable is fully stale, saves `_previousValue`,
   *        reinstalls the lazy getter via `_replaceWithStaleGetter`, and
   *        calls `markUpstreamDependentsStale` to walk the upstream graph.
   *      - Triggers `actionTriggerScheduler.queueTriggeredActions` and the
   *        auto-submit detector for each visited variable.
   */
  async markStateVariableAndUpstreamDependentsStale({
    component,
    varName
  }) {
    if (varName in this.core.rendererVariablesByComponentType[component.componentType]) {
      this.core.updateInfo.componentsToUpdateRenderers.add(
        component.componentIdx
      );
    }
    const allStateVariablesAffectedObj = {
      [varName]: component.state[varName]
    };
    if (component.state[varName].additionalStateVariablesDefined) {
      component.state[varName].additionalStateVariablesDefined.forEach(
        (x2) => allStateVariablesAffectedObj[x2] = component.state[x2]
      );
    }
    await this._processStaleVisit({
      component,
      varName,
      allStateVariablesAffectedObj
    });
  }
  /**
   * Read-only freshness probe: invoke the variable's `getCurrentFreshness`
   * predicate (if defined) and return the same `{ fresh, partiallyFresh,
   * ... }` verdict shape that `processMarkStale` uses, with
   * array-state-variable readings remapped onto their array-entry mirrors
   * via `_remapArrayEntryFreshness`. Returns `undefined` when the
   * variable has no predicate. Does not mutate state.
   *
   * Called from `_processStaleVisit` to decide whether a variable was
   * "previously effectively fresh" before the new mark-stale pass runs.
   */
  async lookUpCurrentFreshness({
    component,
    varName,
    allStateVariablesAffectedObj
  }) {
    let stateVarObj = component.state[varName];
    if (!stateVarObj.getCurrentFreshness) {
      return;
    }
    let freshnessInfo = stateVarObj.freshnessInfo;
    const { arrayKeys, arraySize } = await this._getArrayKeysAndSize(
      stateVarObj,
      component
    );
    let result = stateVarObj.getCurrentFreshness({
      freshnessInfo,
      arrayKeys,
      arraySize
    });
    this._remapArrayEntryFreshness(result, allStateVariablesAffectedObj);
    return result;
  }
  /**
   * Run the variable's `markStale` predicate (if any) and merge the result
   * back into `allStateVariablesAffectedObj`.
   *
   * Returns a freshness verdict:
   *   - `{ fresh: { [varName]: true, ... } }`: predicate considers the value
   *     completely fresh; the caller should not recurse into upstream deps.
   *   - `{ partiallyFresh: { [varName]: true, ... } }`: predicate sees a
   *     partial change; the caller should recurse but also mark the variable
   *     so a later mark-stale invocation can revisit it.
   *   - other attributes set by `markStale` are passed through to the caller.
   *
   * For array state variables the predicate is invoked with `arrayKeys` and
   * `arraySize`; the caller bridges array-level vs entry-level freshness
   * via `_remapArrayEntryFreshness`. May mutate `freshnessInfo` on the
   * affected variables; does not record changes or queue renderer updates.
   */
  async processMarkStale({
    component,
    varName,
    allStateVariablesAffectedObj
  }) {
    let stateVarObj = component.state[varName];
    if (!stateVarObj.markStale || !stateVarObj.initiallyResolved) {
      let fresh = {};
      Object.keys(allStateVariablesAffectedObj).forEach(
        (x2) => fresh[x2] = false
      );
      return { fresh };
    }
    let changes = {};
    let downDeps = this.core.dependencies.downstreamDependencies[component.componentIdx][varName];
    for (let dependencyName in downDeps) {
      let dep = downDeps[dependencyName];
      let depChanges = {};
      let foundDepChange = false;
      if (dep.componentIdentityChanged) {
        depChanges.componentIdentityChanged = true;
        foundDepChange = true;
      }
      if (dep.componentIdentitiesChanged) {
        depChanges.componentIdentitiesChanged = true;
        foundDepChange = true;
      }
      if (dep.valuesChanged) {
        depChanges.valuesChanged = dep.valuesChanged;
        foundDepChange = true;
      }
      if (foundDepChange) {
        changes[dependencyName] = depChanges;
      }
    }
    let freshnessInfo = stateVarObj.freshnessInfo;
    const { arrayKeys, arraySize } = await this._getArrayKeysAndSize(
      stateVarObj,
      component
    );
    let result = stateVarObj.markStale({
      freshnessInfo,
      changes,
      arrayKeys,
      arraySize
    });
    this._remapArrayEntryFreshness(result, allStateVariablesAffectedObj);
    return result;
  }
  /**
   * Walk one step upward from `component`/`varName` and queue stale-marking
   * on every upstream dependent that consumes it. For each upstream
   * dependency:
   *   - Skip if `onlyToSetInInverseDefinition` (write-only edge).
   *   - Run the dependency's optional `markStale` side effect.
   *   - Find the position of `componentIdx` in
   *     `downstreamComponentIndices` and record `valuesChanged` for that
   *     slot (with our `freshnessInfo` if available) so the upstream
   *     definition sees a concrete change.
   *   - If the upstream variable is in its component's
   *     `rendererVariablesByComponentType` set, queue the upstream
   *     component for a renderer update.
   *   - Dispatch `_processStaleVisit` against the upstream component, which
   *     re-enters this function for its upstream dependents.
   *
   * `_processStaleVisit` already short-circuits when a variable is
   * already stale (has a getter in place), so this walk terminates at
   * any cached-stale frontier.
   */
  async markUpstreamDependentsStale({
    component,
    varName
  }) {
    let componentIdx = component.componentIdx;
    let upstream = this.core.dependencies.upstreamDependencies[componentIdx][varName];
    let freshnessInfo;
    if (component.state[varName]) {
      freshnessInfo = component.state[varName].freshnessInfo;
    }
    if (upstream) {
      for (let upDep of upstream) {
        if (upDep.onlyToSetInInverseDefinition) {
          continue;
        }
        let foundVarChange = false;
        if (upDep.markStale) {
          await upDep.markStale();
        }
        if (upDep.downstreamComponentIndices) {
          let componentInd = upDep.downstreamComponentIndices.indexOf(componentIdx);
          if (componentInd === -1) {
            continue;
          }
          if (upDep.mappedDownstreamVariableNamesByComponent) {
            let varInd = upDep.mappedDownstreamVariableNamesByComponent[componentInd].indexOf(varName);
            if (varInd === -1) {
              throw Error(
                `something went wrong as ${varName} not a downstreamVariable of ${upDep.dependencyName}`
              );
            }
            if (!upDep.valuesChanged) {
              upDep.valuesChanged = [];
            }
            upDep.thawValuesChangedRecord(componentInd);
            if (!upDep.valuesChanged[componentInd]) {
              upDep.valuesChanged[componentInd] = {};
            }
            let changeRecord = upDep.valuesChanged[componentInd][varName];
            if (!changeRecord || Object.isFrozen(changeRecord)) {
              changeRecord = upDep.valuesChanged[componentInd][varName] = changeRecord ? { changed: changeRecord.changed } : {};
            }
            changeRecord.potentialChange = true;
            if (freshnessInfo) {
              changeRecord.freshnessInfo = freshnessInfo;
            }
            foundVarChange = true;
          } else if (varName === upDep.downstreamVariableNameIfNoVariables) {
            if (!upDep.valuesChanged) {
              upDep.valuesChanged = {
                [upDep.downstreamVariableNameIfNoVariables]: {}
              };
            }
            upDep.componentIdentityChanged = true;
            upDep.valuesChanged[upDep.downstreamVariableNameIfNoVariables].potentialChange = true;
            foundVarChange = true;
          }
        }
        if (foundVarChange) {
          const upDepComponent = this.core._components[upDep.upstreamComponentIdx];
          for (const upstreamVar of upDep.upstreamVariableNames) {
            if (upstreamVar in this.core.rendererVariablesByComponentType[upDepComponent.componentType]) {
              this.core.updateInfo.componentsToUpdateRenderers.add(
                upDep.upstreamComponentIdx
              );
              break;
            }
          }
          const upVarName = upDep.upstreamVariableNames[0];
          const allStateVariablesAffectedObj = {};
          upDep.upstreamVariableNames.forEach(
            (x2) => allStateVariablesAffectedObj[x2] = upDepComponent.state[x2]
          );
          await this._processStaleVisit({
            component: upDepComponent,
            varName: upVarName,
            allStateVariablesAffectedObj
          });
        }
      }
    }
  }
  // evaluatedDeferredChildStateVariables(component) {
  //   for (let child of component.activeChildren) {
  //     if (child.componentType === "string") {
  //       for (let varName in child.state) {
  //         if (child.state[varName].deferred) {
  //           let evaluateSoNoLongerDeferred = child.state[varName].value;
  //         }
  //       }
  //     }
  //   }
  // }
  /**
   * Pull the read-only `arrayKeys` and `arraySize` snapshot needed by
   * `getCurrentFreshness` / `markStale`. We can't evaluate state variables
   * mid-mark-stale, so we read the last calculated `_arrayKeys` and the
   * `arraySize` state variable's `_previousValue` (or its current value if
   * that variable was itself fresh).
   */
  async _getArrayKeysAndSize(stateVarObj, component) {
    let arrayKeys;
    let arraySize;
    if (stateVarObj.isArrayEntry) {
      arrayKeys = stateVarObj._arrayKeys;
    }
    if (stateVarObj.isArrayEntry || stateVarObj.isArray) {
      const arraySizeStateVar = component.state[stateVarObj.arraySizeStateVariable];
      arraySize = arraySizeStateVar._previousValue;
      const varWasFresh = !(Object.getOwnPropertyDescriptor(arraySizeStateVar, "value")?.get || arraySizeStateVar.immutable);
      if (varWasFresh) {
        arraySize = await arraySizeStateVar.value;
      }
      if (!Array.isArray(arraySize)) {
        arraySize = [];
      }
    }
    return { arrayKeys, arraySize };
  }
  /**
   * Rewrite the `fresh` / `partiallyFresh` keys returned by
   * `getCurrentFreshness` or `markStale` so that an array-state-variable
   * reading is reinterpreted as the freshness of a specific
   * `arrayEntry` listed in `allStateVariablesAffectedObj`. The array's own
   * key is removed once its value has been moved onto the entry.
   */
  _remapArrayEntryFreshness(result, allStateVariablesAffectedObj) {
    if (result.partiallyFresh) {
      for (const vName in allStateVariablesAffectedObj) {
        if (allStateVariablesAffectedObj[vName].isArrayEntry) {
          const arrayName = allStateVariablesAffectedObj[vName].arrayStateVariable;
          result.partiallyFresh[vName] = result.partiallyFresh[arrayName];
          delete result.partiallyFresh[arrayName];
        }
      }
    }
    if (result.fresh) {
      for (const vName in allStateVariablesAffectedObj) {
        if (allStateVariablesAffectedObj[vName].isArrayEntry) {
          const arrayName = allStateVariablesAffectedObj[vName].arrayStateVariable;
          if (arrayName in result.fresh) {
            result.fresh[vName] = result.fresh[arrayName];
            delete result.fresh[arrayName];
          }
        }
      }
    }
  }
  /**
   * Stash `stateVarObj`'s current value into `_previousValue` (cloning if
   * it's an array) and reinstall the lazy `value` getter that pulls
   * through `core.getStateVariableValue`. Together these mark the variable
   * as stale: the next `await stateVarObj.value` will recompute, while
   * `_previousValue` is preserved for change-detection.
   */
  async _replaceWithStaleGetter(stateVarObj) {
    stateVarObj._previousValue = await stateVarObj.value;
    if (Array.isArray(stateVarObj._previousValue)) {
      stateVarObj._previousValue = [...stateVarObj._previousValue];
    }
    installStaleValueGetter(stateVarObj);
  }
  /**
   * Visit `component` for stale-marking against `varName` plus every
   * other state variable in `allStateVariablesAffectedObj`. Walks the
   * freshness lookup, dispatches the markStale side effects
   * (`updateReplacements` / `updateRenderedChildren` /
   * `updateActionChaining` / `updateDependencies` / auto-submit), reinstalls
   * the lazy stale getter on every previously-fresh variable, and recurses
   * to upstream dependents when freshness has actually decreased.
   *
   * Both call sites pre-build `allStateVariablesAffectedObj` from the
   * variable's `additionalStateVariablesDefined` (standalone) or from
   * `upDep.upstreamVariableNames` (upstream-walk loop) before invoking.
   * The standalone caller additionally fans the renderer-update marker
   * for the single `varName`; the upstream caller iterates its multi-var
   * dependency. Those parts stay at the call sites.
   */
  async _processStaleVisit({
    component,
    varName,
    allStateVariablesAffectedObj
  }) {
    const currentFreshnessInfo = await this.lookUpCurrentFreshness({
      component,
      varName,
      allStateVariablesAffectedObj
    });
    const previouslyFreshVars = [];
    const previouslyEffectivelyFresh = [];
    let sumPreviouslyPartiallyFresh = 0;
    const varsChanged = {};
    for (const vName in allStateVariablesAffectedObj) {
      varsChanged[vName] = true;
      const stateVarObj = allStateVariablesAffectedObj[vName];
      if (!(Object.getOwnPropertyDescriptor(stateVarObj, "value")?.get || stateVarObj.immutable)) {
        previouslyFreshVars.push(vName);
      } else if (currentFreshnessInfo) {
        if (currentFreshnessInfo.fresh && currentFreshnessInfo.fresh[vName]) {
          previouslyEffectivelyFresh.push(vName);
        } else if (currentFreshnessInfo.partiallyFresh && currentFreshnessInfo.partiallyFresh[vName]) {
          sumPreviouslyPartiallyFresh += currentFreshnessInfo.partiallyFresh[vName];
        }
      }
    }
    previouslyEffectivelyFresh.push(...previouslyFreshVars);
    const aVarWasFreshOrPartiallyFresh = previouslyEffectivelyFresh.length > 0 || sumPreviouslyPartiallyFresh > 0;
    let freshnessDecreased = false;
    if (aVarWasFreshOrPartiallyFresh) {
      const result = await this.processMarkStale({
        component,
        varName,
        allStateVariablesAffectedObj
      });
      if (result.fresh) {
        for (const vName in result.fresh) {
          if (result.fresh[vName]) {
            delete varsChanged[vName];
          }
        }
      }
      let sumNewPartiallyFresh = 0;
      for (const vName in allStateVariablesAffectedObj) {
        if (previouslyEffectivelyFresh.includes(vName) && !(result.fresh && result.fresh[vName])) {
          freshnessDecreased = true;
          break;
        }
        if (result.partiallyFresh && result.partiallyFresh[vName]) {
          sumNewPartiallyFresh += result.partiallyFresh[vName];
        }
      }
      if (sumNewPartiallyFresh < sumPreviouslyPartiallyFresh) {
        freshnessDecreased = true;
      }
      if (result.updateReplacements) {
        this.core.updateInfo.compositesToUpdateReplacements.add(
          component.componentIdx
        );
      }
      if (result.updateParentRenderedChildren) {
        for (const ancestorObj of component.ancestors) {
          if (!this.core.componentInfoObjects.allComponentClasses._composite.isPrototypeOf(
            ancestorObj.componentCase
          )) {
            if (ancestorObj.componentClass.renderChildren) {
              this.core.componentsWithChangedChildrenToRender.add(
                ancestorObj.componentIdx
              );
            }
            break;
          }
        }
      }
      if (result.updateRenderedChildren) {
        this.core.componentsWithChangedChildrenToRender.add(
          component.componentIdx
        );
      }
      if (result.updateDescendantRenderers) {
        await this.markDescendantsToUpdateRenderers(component);
      }
      if (result.updateActionChaining) {
        let chainObj = this.core.updateInfo.componentsToUpdateActionChaining[component.componentIdx];
        if (!chainObj) {
          chainObj = this.core.updateInfo.componentsToUpdateActionChaining[component.componentIdx] = [];
        }
        for (const vName in allStateVariablesAffectedObj) {
          if (!chainObj.includes(vName)) {
            chainObj.push(vName);
          }
        }
      }
      if (result.updateDependencies) {
        for (const vName of result.updateDependencies) {
          component.state[vName].needDependenciesUpdated = true;
        }
      }
      if (this.core.flags.autoSubmit && result.answerCreditPotentiallyChanged) {
        this.core.recordAnswerToAutoSubmit(component.componentIdx);
      }
    }
    for (const vName in varsChanged) {
      const stateVarObj = allStateVariablesAffectedObj[vName];
      delete stateVarObj.recursiveDependencyValues;
      if (previouslyFreshVars.includes(vName)) {
        await this._replaceWithStaleGetter(stateVarObj);
      }
    }
    if (freshnessDecreased) {
      for (const vName in varsChanged) {
        await this.markUpstreamDependentsStale({
          component,
          varName: vName
        });
      }
    }
  }
}
function promisifyRequest(request) {
  return new Promise((resolve, reject2) => {
    request.oncomplete = request.onsuccess = () => resolve(request.result);
    request.onabort = request.onerror = () => reject2(request.error);
  });
}
function createStore(dbName, storeName) {
  let dbp;
  const getDB = () => {
    if (dbp)
      return dbp;
    const request = indexedDB.open(dbName);
    request.onupgradeneeded = () => request.result.createObjectStore(storeName);
    dbp = promisifyRequest(request);
    dbp.then((db) => {
      db.onclose = () => dbp = void 0;
    }, () => {
    });
    return dbp;
  };
  return (txMode, callback) => getDB().then((db) => callback(db.transaction(storeName, txMode).objectStore(storeName)));
}
let defaultGetStoreFunc;
function defaultGetStore() {
  if (!defaultGetStoreFunc) {
    defaultGetStoreFunc = createStore("keyval-store", "keyval");
  }
  return defaultGetStoreFunc;
}
function set(key, value, customStore = defaultGetStore()) {
  return customStore("readwrite", (store) => {
    store.put(value, key);
    return promisifyRequest(store.transaction);
  });
}
class StatePersistence {
  constructor({ core: core2 }) {
    this.core = core2;
    this.saveStateToDBTimerId = null;
    this.saveDocStateTimeoutID = null;
    this.docStateToBeSavedToDatabase = null;
    this.changesToBeSaved = false;
  }
  /**
   * Cancel any pending saves and clear the buffered payload. Called from
   * `Core.generateDast` so timers and state from a previous run do not
   * leak into the new document.
   */
  reset() {
    if (this.saveStateToDBTimerId !== null) {
      clearTimeout(this.saveStateToDBTimerId);
      this.saveStateToDBTimerId = null;
    }
    if (this.saveDocStateTimeoutID !== null) {
      clearTimeout(this.saveDocStateTimeoutID);
      this.saveDocStateTimeoutID = null;
    }
    this.docStateToBeSavedToDatabase = null;
    this.changesToBeSaved = false;
  }
  /**
   * Schedule a debounced `saveState` after `delayMs` milliseconds, replacing
   * any previously scheduled save.
   */
  scheduleSave(delayMs) {
    if (this.saveDocStateTimeoutID !== null) {
      clearTimeout(this.saveDocStateTimeoutID);
    }
    this.saveDocStateTimeoutID = setTimeout(() => {
      this.saveState().catch(
        reportTimerError(TimerLabels.scheduledSaveState)
      );
    }, delayMs);
  }
  async saveImmediately() {
    if (this.saveDocStateTimeoutID !== null) {
      clearTimeout(this.saveDocStateTimeoutID);
      this.saveDocStateTimeoutID = null;
      await this.saveState(true);
    } else {
      await this.saveChangesToDatabase(true);
    }
  }
  /**
   * Build the serialized document-state payload — the shape
   * `reportScoreAndState` delivers to hosts and `DocViewer`'s
   * `initialState` accepts back.
   */
  buildDocStatePayload(onSubmission = false) {
    const core2 = this.core;
    const coreStateString = JSON.stringify(
      core2.cumulativeStateVariableChanges,
      serializedComponentsReplacer
    );
    let rendererStateString = null;
    if (core2.flags.saveRendererState) {
      rendererStateString = JSON.stringify(
        core2.rendererState,
        serializedComponentsReplacer
      );
    }
    return {
      payload: {
        cid: core2.cid,
        coreInfo: core2.coreInfoString,
        coreState: coreStateString,
        rendererState: rendererStateString,
        initializeCounters: core2.initializeCounters,
        docId: core2.docId,
        attemptNumber: core2.attemptNumber,
        activityId: core2.activityId,
        onSubmission
      },
      coreStateString,
      rendererStateString
    };
  }
  async saveState(overrideThrottle = false, onSubmission = false) {
    this.saveDocStateTimeoutID = null;
    const core2 = this.core;
    if (!core2.flags.allowSaveState && !core2.flags.allowLocalState) {
      return;
    }
    const { payload, coreStateString, rendererStateString } = this.buildDocStatePayload(onSubmission);
    if (core2.flags.allowLocalState) {
      await set(
        `${core2.activityId}|${core2.docId}|${core2.attemptNumber}|${core2.cid}`,
        {
          data_format_version,
          coreState: coreStateString,
          rendererState: rendererStateString,
          coreInfo: core2.coreInfoString
        }
      );
    }
    if (!core2.flags.allowSaveState) {
      return;
    }
    this.docStateToBeSavedToDatabase = payload;
    this.changesToBeSaved = true;
    await this.saveChangesToDatabase(overrideThrottle);
  }
  /**
   * Flush-state-on-demand (Doenet/DoenetML#1440): push any pending changes
   * through the normal `reportScoreAndState` pipeline (via `saveImmediately`)
   * so a persistence host saves them right away — exactly as it does for a
   * routine autosave, with no knowledge that a flush occurred. Reports honor
   * the `allowSaveState`/`allowLocalState` flags, so nothing is emitted when
   * saving is off (there is no persistence host to receive it).
   *
   * Returns whether this viewer held any state: `false` before document
   * generation has produced `coreInfoString` (the viewer holds nothing
   * beyond what it was initialized with, so tearing it down loses nothing
   * either way).
   */
  async flushState() {
    if (!this.core.coreInfoString) {
      return false;
    }
    await this.saveImmediately();
    return true;
  }
  async saveChangesToDatabase(overrideThrottle = false) {
    if (!this.changesToBeSaved) {
      return;
    }
    if (this.saveStateToDBTimerId !== null) {
      if (overrideThrottle) {
        clearTimeout(this.saveStateToDBTimerId);
      } else {
        return;
      }
    }
    this.changesToBeSaved = false;
    this.saveStateToDBTimerId = setTimeout(() => {
      this.saveStateToDBTimerId = null;
      this.saveChangesToDatabase().catch(
        reportTimerError(TimerLabels.throttledSaveChanges)
      );
    }, 6e4);
    this.core.reportScoreAndStateCallback({
      state: { ...this.docStateToBeSavedToDatabase },
      score: await this.core.document.stateValues.creditAchieved
    });
    return;
  }
}
class StateVariableEvaluator {
  constructor({ core: core2 }) {
    this.core = core2;
  }
  /**
   * Resolve `component.state[stateVariable]` to a fresh value, returning the
   * awaited `value` (or array values for array state variables).
   *
   * Behaviour highlights for callers that need to reason about side effects:
   *  - Lazily resolves any unresolved state variables in the
   *    `additionalStateVariablesDefined` group via
   *    `dependencies.resolveItem`; throws if resolution fails.
   *  - Records a `dependencies.recordActualChangeInUpstreamDependencies`
   *    entry for every variable whose value materially changed (per
   *    `_isUnchanged`), and for the matching array entries when an array
   *    state variable's keys, size, or contents changed.
   *  - Forces a re-evaluation of `expressionWithCodes`-style state via
   *    `component.reprocessAfterEvaluate` (see the kludge note below) when
   *    that flag is set on the component.
   *  - Mutates `component.state[varName].value`,
   *    `_previousValue`, `freshnessInfo`, `arrayValues`, and clears
   *    `forceRecalculation` and `justUpdatedForNewComponent` on the
   *    affected variables. Does not mark anything stale itself —
   *    downstream invalidation is the responsibility of
   *    `markStateVariableAndUpstreamDependentsStale`, called by callers
   *    that record a change.
   */
  async getStateVariableValue({
    component,
    stateVariable
  }) {
    let stateVarObj = component.state[stateVariable];
    if (!stateVarObj) {
      throw Error(
        `Can't get value of ${stateVariable} of ${component.componentIdx} as it doesn't exist.`
      );
    }
    await ensureStateVariableMaterialized({
      core: this.core,
      component,
      stateVariable
    });
    if (component.reprocessAfterEvaluate) {
      let reprocessAfterEvaluate = component.reprocessAfterEvaluate;
      delete component.reprocessAfterEvaluate;
      for (let vName in reprocessAfterEvaluate) {
        if (component.state[vName]) {
          await this.getStateVariableValue({
            component,
            stateVariable: vName
          });
        }
      }
      await this.core.processNewStateVariableValues({
        [component.componentIdx]: reprocessAfterEvaluate
      });
    }
    let additionalStateVariablesDefined = stateVarObj.additionalStateVariablesDefined;
    let allStateVariablesAffected = [stateVariable];
    if (additionalStateVariablesDefined) {
      allStateVariablesAffected.push(...additionalStateVariablesDefined);
    }
    let justUpdatedForNewComponent = false;
    for (let varName of allStateVariablesAffected) {
      if (!component.state[varName].isResolved) {
        let result2 = await this.core.dependencies.resolveItem({
          componentIdx: component.componentIdx,
          type: "stateVariable",
          stateVariable: varName,
          force: true
        });
        if (!result2.success) {
          throw Error(
            `Can't get value of ${stateVariable} of ${component.componentIdx} as ${varName} couldn't be resolved.`
          );
        }
      }
      if (component.state[varName].justUpdatedForNewComponent) {
        delete component.state[varName].justUpdatedForNewComponent;
        justUpdatedForNewComponent = true;
      }
    }
    let definitionArgs = await this.getStateVariableDefinitionArguments({
      component,
      stateVariable
    });
    definitionArgs.componentInfoObjects = this.core.componentInfoObjects;
    definitionArgs.justUpdatedForNewComponent = justUpdatedForNewComponent;
    definitionArgs.initialAddPhase = this.core.initialAddPhase;
    definitionArgs.freshnessInfo = stateVarObj.freshnessInfo;
    let arraySize = definitionArgs.arraySize;
    let result;
    if (Object.keys(definitionArgs.changes).length === 0 && stateVarObj._previousValue !== void 0 && !stateVarObj.forceRecalculation) {
      let noChanges = [stateVariable];
      if (additionalStateVariablesDefined) {
        noChanges.push(...additionalStateVariablesDefined);
      }
      result = { noChanges };
      if (stateVarObj.freshenOnNoChanges) {
        stateVarObj.freshenOnNoChanges(definitionArgs);
      }
    } else {
      delete stateVarObj.forceRecalculation;
      result = stateVarObj.definition(definitionArgs);
    }
    let receivedValue = {
      [stateVariable]: false
    };
    let valuesChanged = {};
    if (additionalStateVariablesDefined) {
      for (let otherVar of additionalStateVariablesDefined) {
        receivedValue[otherVar] = false;
      }
    }
    for (let varName in result.setValue) {
      if (!(varName in component.state)) {
        throw Error(
          `Definition of state variable ${stateVariable} of ${component.componentIdx} returned value of ${varName}, which isn't a state variable.`
        );
      }
      let matchingArrayEntry;
      if (!(varName in receivedValue)) {
        matchingArrayEntry = this._findOrThrowMatchingArrayEntry({
          varName,
          receivedValue,
          component,
          errorMessage: `Attempting to set value of stateVariable ${varName} in definition of ${stateVariable} of ${component.componentIdx}, but it's not listed as an additional state variable defined.`
        });
        receivedValue[matchingArrayEntry] = true;
        valuesChanged[matchingArrayEntry] = true;
      } else {
        receivedValue[varName] = true;
        if (component.state[varName].isArray) {
          if (!valuesChanged[varName]) {
            valuesChanged[varName] = { arrayKeysChanged: {} };
          }
        } else {
          valuesChanged[varName] = true;
        }
      }
      if (!component.state[varName].isResolved) {
        if (!matchingArrayEntry || !component.state[matchingArrayEntry].isResolved) {
          throw Error(
            `Attempting to set value of stateVariable ${varName} of ${component.componentIdx} while it is still unresolved!`
          );
        }
      }
      if (component.state[varName].isArray) {
        if (!valuesChanged[varName]) {
          valuesChanged[varName] = { arrayKeysChanged: {} };
        }
        let checkForActualChange = {};
        if (result.checkForActualChange && result.checkForActualChange[varName]) {
          checkForActualChange = result.checkForActualChange[varName];
        }
        for (let arrayKey in result.setValue[varName]) {
          if (checkForActualChange[arrayKey]) {
            let prevValue = component.state[varName].getArrayValue({
              arrayKey
            });
            let newValue = result.setValue[varName][arrayKey];
            if (prevValue !== newValue) {
              component.state[varName].setArrayValue({
                value: result.setValue[varName][arrayKey],
                arrayKey,
                arraySize
              });
              component.state[varName].usedDefaultByArrayKey[arrayKey] = false;
              valuesChanged[varName].arrayKeysChanged[arrayKey] = true;
            }
          } else {
            component.state[varName].setArrayValue({
              value: result.setValue[varName][arrayKey],
              arrayKey,
              arraySize
            });
            component.state[varName].usedDefaultByArrayKey[arrayKey] = false;
            valuesChanged[varName].arrayKeysChanged[arrayKey] = true;
          }
        }
      } else {
        delete component.state[varName].value;
        component.state[varName].value = result.setValue[varName];
        delete component.state[varName].usedDefault;
        if (result.checkForActualChange?.[varName]) {
          if (this._isUnchanged(
            component.state[varName].value,
            component.state[varName]._previousValue
          )) {
            delete valuesChanged[varName];
          }
        }
      }
    }
    for (let varName in result.useEssentialOrDefaultValue) {
      if (!(varName in component.state)) {
        throw Error(
          `Definition of state variable ${stateVariable} of ${component.componentIdx} requested essential or default value of ${varName}, which isn't a state variable.`
        );
      }
      if (!component.state[varName].hasEssential) {
        throw Error(
          `Definition of state variable ${stateVariable} of ${component.componentIdx} requested essential or default value of ${varName}, but hasEssential is not set.`
        );
      }
      let matchingArrayEntry;
      if (!(varName in receivedValue)) {
        matchingArrayEntry = this._findOrThrowMatchingArrayEntry({
          varName,
          receivedValue,
          component,
          errorMessage: `Attempting to set value of stateVariable ${varName} in definition of ${stateVariable} of ${component.componentIdx}, but it's not listed as an additional state variable defined.`
        });
        receivedValue[matchingArrayEntry] = true;
        valuesChanged[matchingArrayEntry] = true;
      } else {
        receivedValue[varName] = true;
        if (component.state[varName].isArray) {
          if (!valuesChanged[varName]) {
            valuesChanged[varName] = { arrayKeysChanged: {} };
          }
        } else {
          valuesChanged[varName] = true;
        }
      }
      if (!component.state[varName].isResolved) {
        if (!matchingArrayEntry || !component.state[matchingArrayEntry].isResolved) {
          throw Error(
            `Attempting to set value of stateVariable ${varName} of ${component.componentIdx} while it is still unresolved!`
          );
        }
      }
      let essentialVarName = varName;
      if (component.state[varName].essentialVarName) {
        essentialVarName = component.state[varName].essentialVarName;
      }
      let essentialValue = component.essentialState[essentialVarName];
      if (component.state[varName].isArray) {
        if (!valuesChanged[varName]) {
          valuesChanged[varName] = { arrayKeysChanged: {} };
        }
        let checkForActualChange = {};
        if (result.checkForActualChange && result.checkForActualChange[varName]) {
          checkForActualChange = result.checkForActualChange[varName];
        }
        for (let arrayKey in result.useEssentialOrDefaultValue[varName]) {
          let prevValue;
          if (checkForActualChange[arrayKey]) {
            prevValue = component.state[varName].getArrayValue({
              arrayKey
            });
          }
          let essentialValueForArrayKey;
          if (Array.isArray(essentialValue)) {
            essentialValueForArrayKey = component.state[varName].getArrayValue({
              arrayKey,
              arrayValues: essentialValue
            });
          } else {
            essentialValue = component.essentialState[essentialVarName] = [];
          }
          if (essentialValueForArrayKey !== void 0) {
            component.state[varName].setArrayValue({
              value: essentialValueForArrayKey,
              arrayKey,
              arraySize
            });
          } else {
            let defaultValue = result.useEssentialOrDefaultValue[varName][arrayKey].defaultValue;
            if (defaultValue !== void 0) {
              component.state[varName].setArrayValue({
                value: defaultValue,
                arrayKey,
                arraySize
              });
              component.state[varName].usedDefaultByArrayKey[arrayKey] = true;
            } else if (component.state[varName].defaultValueByArrayKey?.(
              arrayKey
            ) !== void 0) {
              component.state[varName].setArrayValue({
                value: component.state[varName].defaultValueByArrayKey(arrayKey),
                arrayKey,
                arraySize
              });
              component.state[varName].usedDefaultByArrayKey[arrayKey] = true;
            } else {
              throw Error(
                `Neither value nor default value specified; state variable: ${varName}, component: ${component.componentIdx}, arrayKey: ${arrayKey}.`
              );
            }
          }
          if (checkForActualChange[arrayKey]) {
            let newValue = component.state[varName].getArrayValue({
              arrayKey
            });
            if (newValue !== prevValue) {
              valuesChanged[varName].arrayKeysChanged[arrayKey] = true;
            }
          } else {
            valuesChanged[varName].arrayKeysChanged[arrayKey] = true;
          }
        }
      } else {
        if (essentialValue !== void 0) {
          delete component.state[varName].value;
          component.state[varName].value = essentialValue;
        } else {
          let defaultValue = result.useEssentialOrDefaultValue[varName].defaultValue;
          if (defaultValue !== void 0) {
            delete component.state[varName].value;
            component.state[varName].value = defaultValue;
            component.state[varName].usedDefault = true;
          } else if (component.state[varName].defaultValue !== void 0) {
            delete component.state[varName].value;
            component.state[varName].value = component.state[varName].defaultValue;
            component.state[varName].usedDefault = true;
          } else {
            throw Error(
              `Neither value nor default value specified; state variable: ${varName}, component: ${component.componentIdx}.`
            );
          }
        }
        if (result.checkForActualChange?.[varName]) {
          if (this._isUnchanged(
            component.state[varName].value,
            component.state[varName]._previousValue
          )) {
            delete valuesChanged[varName];
          }
        }
      }
    }
    for (let varName in result.markAsUsedDefault) {
      if (!component.state[varName].isResolved) {
        throw Error(
          `Marking state variable as used default when it isn't yet resolved: ${varName} of ${component.componentIdx}`
        );
      }
      if (!(varName in receivedValue)) {
        this._findOrThrowMatchingArrayEntry({
          varName,
          receivedValue,
          component,
          errorMessage: `Marking state variable  ${varName} as used default in definition of ${stateVariable} of ${component.componentIdx}, but it's not listed as an additional state variable defined.`
        });
      }
      if (Array.isArray(result.markAsUsedDefault[varName])) {
        for (let arrayKey in result.markAsUsedDefault[varName]) {
          component.state[varName].usedDefaultByArrayKey[arrayKey] = Boolean(result.markAsUsedDefault[varName][arrayKey]);
        }
      } else {
        component.state[varName].usedDefault = Boolean(
          result.markAsUsedDefault[varName]
        );
      }
    }
    if (result.noChanges) {
      for (let varName of result.noChanges) {
        if (!component.state[varName].isResolved) {
          component.state[varName].isResolved = true;
        }
        if (!(varName in receivedValue)) {
          this._findOrThrowMatchingArrayEntry({
            varName,
            receivedValue,
            component,
            errorMessage: `Claiming stateVariable ${varName} is unchanged in definition of ${stateVariable} of ${component.componentIdx}, but it's not listed as an additional state variable defined.`
          });
        }
        receivedValue[varName] = true;
        if (Object.getOwnPropertyDescriptor(
          component.state[varName],
          "value"
        )?.get || component.state[varName].immutable) {
          delete component.state[varName].value;
          component.state[varName].value = component.state[varName]._previousValue;
        }
      }
    }
    for (let varName in result.setEssentialValue) {
      if (!(varName in component.state)) {
        throw Error(
          `Definition of state variable ${stateVariable} of ${component.componentIdx} tried to make ${varName} essential, which isn't a state variable.`
        );
      }
      if (!(varName in receivedValue)) {
        this._findOrThrowMatchingArrayEntry({
          varName,
          receivedValue,
          component,
          errorMessage: `Attempting to set essential value of stateVariable ${varName} in definition of ${stateVariable} of ${component.componentIdx}, but it's not listed as an additional state variable defined.`
        });
      }
      if (!component.state[varName].hasEssential) {
        throw Error(
          `Attempting to set the essential value of stateVariable ${varName} in definition of ${stateVariable} of ${component.componentIdx}, but it does not have an essential value`
        );
      }
      if (!(component.state[varName].shadowVariable || component.state[varName].doNotShadowEssential)) {
        throw Error(
          `Attempting to set the essential value of stateVariable ${varName} in definition of ${stateVariable} of ${component.componentIdx}, but it is not allowed unless the state variable is shadowed or the essential state is not shadowed.`
        );
      }
      if (!this.core.essentialValuesSavedInDefinition[component.stateId]) {
        this.core.essentialValuesSavedInDefinition[component.stateId] = {};
      }
      let essentialVarName = varName;
      if (component.state[varName].essentialVarName) {
        essentialVarName = component.state[varName].essentialVarName;
      }
      if (component.state[varName].isArray) {
        let essentialArray = component.essentialState[essentialVarName];
        if (!Array.isArray(essentialArray)) {
          essentialArray = component.essentialState[essentialVarName] = [];
        }
        if (!this.core.essentialValuesSavedInDefinition[component.stateId][varName]) {
          this.core.essentialValuesSavedInDefinition[component.stateId][varName] = {
            mergeObject: true
          };
        }
        for (let arrayKey in result.setEssentialValue[varName]) {
          component.state[varName].setArrayValue({
            value: result.setEssentialValue[varName][arrayKey],
            arrayKey,
            arraySize,
            arrayValues: essentialArray
          });
          this.core.essentialValuesSavedInDefinition[component.stateId][varName][arrayKey] = result.setEssentialValue[varName][arrayKey];
        }
      } else {
        component.essentialState[essentialVarName] = result.setEssentialValue[varName];
        this.core.essentialValuesSavedInDefinition[component.stateId][varName] = result.setEssentialValue[varName];
      }
    }
    if (result.setCreateComponentOfType) {
      for (let varName in result.setCreateComponentOfType) {
        if (!component.state[varName].shadowingInstructions?.hasVariableComponentType) {
          throw Error(
            `Cannot set type of ${varName} of ${component.componentIdx} as it it does not have the hasVariableComponentType attribute.`
          );
        }
        let changedComponentType = false;
        let shadowingInstructions = component.state[varName].shadowingInstructions;
        if (!shadowingInstructions) {
          shadowingInstructions = component.state[varName].shadowingInstructions = {};
        }
        let originalCreateComponentOfType = shadowingInstructions.createComponentOfType;
        let newCreateComponentOfType = result.setCreateComponentOfType[varName];
        if (Array.isArray(originalCreateComponentOfType)) {
          if (Array.isArray(newCreateComponentOfType)) {
            if (originalCreateComponentOfType.length !== newCreateComponentOfType.length) {
              changedComponentType = true;
            } else if (originalCreateComponentOfType.some(
              (v2, i2) => v2 != newCreateComponentOfType[i2]
            )) {
              changedComponentType = true;
            }
          } else {
            changedComponentType = true;
          }
        } else if (Array.isArray(newCreateComponentOfType)) {
          changedComponentType = true;
        } else {
          changedComponentType = originalCreateComponentOfType !== newCreateComponentOfType;
        }
        if (changedComponentType) {
          valuesChanged[varName] = true;
        }
        shadowingInstructions.createComponentOfType = result.setCreateComponentOfType[varName];
        if (component.state[varName].isArray && component.state[varName].arrayEntryNames) {
          let arrayComponentType = result.setCreateComponentOfType[varName];
          let arrayComponentTypeIsArray = Array.isArray(arrayComponentType);
          for (let arrayEntryName of component.state[varName].arrayEntryNames) {
            if (arrayComponentTypeIsArray) {
              let arrayKeys = await component.state[arrayEntryName].arrayKeys;
              let componentType = [];
              for (let arrayKey of arrayKeys) {
                let ind = component.state[varName].keyToIndex(
                  arrayKey
                );
                componentType.push(arrayComponentType[ind]);
              }
              component.state[arrayEntryName].shadowingInstructions.createComponentOfType = componentType;
            } else {
              component.state[arrayEntryName].shadowingInstructions.createComponentOfType = arrayComponentType;
            }
          }
        }
      }
    }
    if (result.arraySizeChanged) {
      for (let varName of result.arraySizeChanged) {
        await component.state[varName].adjustArrayToNewArraySize();
        if (valuesChanged[varName] === void 0) {
          valuesChanged[varName] = { arrayKeysChanged: {} };
        } else if (valuesChanged[varName] === true) {
          valuesChanged[varName] = {
            allArrayKeysChanged: true,
            arrayKeysChanged: {}
          };
        }
        valuesChanged[varName].arraySizeChanged = true;
      }
    }
    if (result.sendDiagnostics?.length > 0) {
      const { position, sourceDoc } = this.core.getSourceLocationForComponent(component);
      for (const diagnostic of result.sendDiagnostics) {
        const addedDiagnostic = this.core.addDiagnostic({
          position,
          sourceDoc,
          ...diagnostic
        });
        if (addedDiagnostic && diagnostic?.type === "error" && this.core.initialAddPhase) {
          this.core.errorComponentsToAdd.push({
            componentIdx: component.componentIdx,
            position,
            sourceDoc,
            ...diagnostic
          });
        }
      }
    }
    for (let varName in receivedValue) {
      if (!(receivedValue[varName] || component.state[varName].isArrayEntry || component.state[varName].isArray)) {
        throw Error(
          `definition of ${stateVariable} of ${component.componentIdx} didn't return value of ${varName}`
        );
      }
      if (component.state[varName].isArray) {
        delete component.state[varName].value;
        component.state[varName].value = component.state[varName].arrayValues;
      } else if (component.state[varName].isArrayEntry) {
        delete component.state[varName].value;
        component.state[varName].value = await component.state[varName].getValueFromArrayValues();
      }
    }
    for (let varName in valuesChanged) {
      this.core.dependencies.recordActualChangeInUpstreamDependencies({
        component,
        varName,
        changes: valuesChanged[varName]
        // so far, just in case is an array state variable
      });
      if (component.state[varName].isArray) {
        let arrayVarNamesChanged = [];
        if (valuesChanged[varName] === true || valuesChanged[varName].allArrayKeysChanged || valuesChanged[varName].arraySizeChanged) {
          if (component.state[varName].arrayEntryNames) {
            arrayVarNamesChanged = component.state[varName].arrayEntryNames;
          }
        } else {
          let varNamesByArrayKey = component.state[varName].varNamesIncludingArrayKeys;
          for (let arrayKeyChanged in valuesChanged[varName].arrayKeysChanged) {
            let additionalVarNamesChanged = varNamesByArrayKey[arrayKeyChanged];
            if (additionalVarNamesChanged) {
              arrayVarNamesChanged.push(
                ...additionalVarNamesChanged
              );
            }
          }
        }
        arrayVarNamesChanged = [...new Set(arrayVarNamesChanged)];
        for (let arrayVarName of arrayVarNamesChanged) {
          this.core.dependencies.recordActualChangeInUpstreamDependencies(
            {
              component,
              varName: arrayVarName
            }
          );
        }
      }
    }
    return await stateVarObj.value;
  }
  /**
   * Build definition/inverse-definition args for a state variable.
   * When consumeChanges is false, dependency change flags are observed but preserved
   * for a later consuming read.
   */
  async getStateVariableDefinitionArguments({
    component,
    stateVariable,
    excludeDependencyValues = false,
    consumeChanges = true
  }) {
    let args;
    if (excludeDependencyValues) {
      args = {};
    } else {
      args = await this.core.dependencies.getStateVariableDependencyValues({
        component,
        stateVariable,
        consumeChanges
      });
    }
    args.componentIdx = component.componentIdx;
    let stateVarObj = component.state[stateVariable];
    if (stateVarObj.isArrayEntry) {
      args.arrayKeys = await stateVarObj.arrayKeys;
      args.arraySize = await stateVarObj.arraySize;
    } else if (stateVarObj.isArray) {
      args.arraySize = await stateVarObj.arraySize;
    }
    if (stateVarObj.createWorkspace) {
      args.workspace = stateVarObj.workspace;
    }
    if (stateVarObj.providePreviousValuesInDefinition || stateVarObj.provideEssentialValuesInDefinition) {
      let allStateVariablesDefined = [stateVariable];
      if (stateVarObj.additionalStateVariablesDefined) {
        allStateVariablesDefined.push(
          ...stateVarObj.additionalStateVariablesDefined
        );
      }
      if (stateVarObj.providePreviousValuesInDefinition) {
        let previousValues = {};
        for (let varName of allStateVariablesDefined) {
          if (component.state[varName].isArrayEntry) {
            varName = component.state[varName].arrayStateVariable;
          }
          previousValues[varName] = component.state[varName]._previousValue;
        }
        args.previousValues = previousValues;
      }
      if (stateVarObj.provideEssentialValuesInDefinition) {
        let essentialValues = {};
        for (let varName of allStateVariablesDefined) {
          if (component.state[varName].isArrayEntry) {
            varName = component.state[varName].arrayStateVariable;
          }
          let essentialVarName = varName;
          if (component.state[varName].essentialVarName) {
            essentialVarName = component.state[varName].essentialVarName;
          }
          essentialValues[varName] = component.essentialState[essentialVarName];
        }
        args.essentialValues = essentialValues;
      }
    }
    return args;
  }
  /**
   * Record that `componentIdx`/`varName` has actually changed (called
   * after the bulk-write path has updated values in `comp.essentialState`
   * and friends). Three side effects, in order:
   *   1. `markStateVariableAndUpstreamDependentsStale` — invalidate
   *      `varName` and propagate up the dependency graph. The mark-stale
   *      pass already covers `additionalStateVariablesDefined`.
   *   2. Set `forceRecalculation = true` on `varName` and every entry in
   *      its `additionalStateVariablesDefined` group, so the next
   *      `getStateVariableValue` call ignores the "no changes detected"
   *      fast-path and re-runs the definition.
   *   3. `recordActualChangeInUpstreamDependencies` for each variable in
   *      the group, so dependents see a concrete change flag.
   */
  async recordActualChangeInStateVariable({
    componentIdx,
    varName
  }) {
    let component = this.core._components[componentIdx];
    await this.core.markStateVariableAndUpstreamDependentsStale({
      component,
      varName
    });
    let allStateVariables = [varName];
    if (component.state[varName].additionalStateVariablesDefined) {
      allStateVariables.push(
        ...component.state[varName].additionalStateVariablesDefined
      );
    }
    for (let vName of allStateVariables) {
      component.state[vName].forceRecalculation = true;
      this.core.dependencies.recordActualChangeInUpstreamDependencies({
        component,
        varName: vName
      });
    }
  }
  /**
   * Look up `varName` in `receivedValue`'s array-entry alternates: scan
   * the component's `arrayEntryNames` for one already in `receivedValue`,
   * and return it. Throws with `errorMessage` if no array-entry match is
   * found (or if the state variable is not an array). The caller decides
   * whether to additionally mark the entry on `receivedValue`/`valuesChanged`.
   */
  _findOrThrowMatchingArrayEntry({
    varName,
    receivedValue,
    component,
    errorMessage
  }) {
    let matchingArrayEntry;
    if (component.state[varName].isArray && component.state[varName].arrayEntryNames) {
      for (let arrayEntryName of component.state[varName].arrayEntryNames) {
        if (arrayEntryName in receivedValue) {
          matchingArrayEntry = arrayEntryName;
          break;
        }
      }
    }
    if (!matchingArrayEntry) {
      throw Error(errorMessage);
    }
    return matchingArrayEntry;
  }
  /**
   * Decide whether two state-variable values should be treated as
   * unchanged for the purpose of `checkForActualChange`. Triple-equals for
   * scalars; for arrays, a shallow first-dimension comparison.
   *
   * TODO: deeper comparison for arrays? Likely unnecessary — array state
   * variables usually surface deeper changes through their own machinery.
   */
  _isUnchanged(newValue, previousValue) {
    if (newValue === previousValue) {
      return true;
    }
    if (Array.isArray(newValue) && Array.isArray(previousValue)) {
      return newValue.length === previousValue.length && newValue.every((v2, i2) => v2 === previousValue[i2]);
    }
    return false;
  }
  // The five state-variable name-resolution helpers below live as pure
  // functions in StateVariableNameResolver.ts. The wrappers preserve the
  // public surface (`core.findCaseInsensitiveMatches`, etc., plus the
  // by-reference passes used in composite sugar functions) by injecting
  // `componentInfoObjects` and delegating.
}
class UpdateExecutor {
  constructor({ core: core2 }) {
    this.core = core2;
  }
  /**
   * Run a component-defined action method (e.g. `submitAnswer`,
   * `revealSection`).
   *
   * Special-case branches handled before the main dispatch:
   *   - `actionName === "setTheme"` with no `componentIdx`: re-enters via
   *     `performUpdate` to set `document.theme`. The action mechanism is
   *     co-opted here because the document doesn't expose a real action
   *     surface, but theme is a UI-only state variable that the viewer
   *     needs to be able to set. `doNotSave` is always passed since theme
   *     is not user content.
   *   - Component missing + `actionName === "recordVisibilityChange"`
   *     with `args.isVisible === false`: a "component became hidden"
   *     event for a component that has already been deleted. Recorded
   *     directly via `requestRecordEvent` because there is no live
   *     component to dispatch through.
   *
   * Main path: look up `component.actions[actionName]` (with optional
   * case-insensitive fallback when `caseInsensitiveMatch` is set), record
   * the `event` if provided, and `await` the action. Returns
   * `{ actionId }` on success. If the component exists but the action
   * does not, a warning diagnostic is added and `{}` is returned.
   */
  async performAction({
    componentIdx,
    actionName,
    args,
    event,
    caseInsensitiveMatch
  }) {
    if (actionName === "setTheme" && componentIdx === void 0) {
      await this.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.core.documentIdx,
            stateVariable: "theme",
            value: args.theme
          }
        ],
        actionId: args.actionId,
        doNotSave: true
        // this isn't an interaction, so don't save doc state
      });
      return { actionId: args.actionId };
    }
    let component = this.core._components[componentIdx];
    if (component && component.actions) {
      let action = component.actions[actionName];
      if (!action && caseInsensitiveMatch) {
        let actionNameLower = actionName.toLowerCase();
        for (let aName in component.actions) {
          if (aName.toLowerCase() === actionNameLower) {
            action = component.actions[aName];
            actionName = aName;
            break;
          }
        }
      }
      if (action) {
        if (event) {
          this.core.requestRecordEvent(event);
        }
        if (!args) {
          args = {};
        }
        await action(args);
        return { actionId: args.actionId };
      }
    }
    if (!component && actionName === "recordVisibilityChange" && args?.isVisible === false) {
      this.core.requestRecordEvent({
        verb: "visibilityChanged",
        object: {
          componentIdx
        },
        result: { isVisible: false }
      });
      return { actionId: args.actionId };
    }
    if (component) {
      this.core.addDiagnostic({
        type: "warning",
        message: `Cannot run action ${actionName} on component ${componentIdx}`,
        position: component.position,
        sourceDoc: component.sourceDoc
      });
    }
    return {};
  }
  /**
   * Drive a batch of update instructions through the inverse-definition
   * pipeline, then surface the results to renderers and persistence.
   *
   * The instruction array is processed in order. Each instruction
   * dispatches by `updateType`:
   *   - `updateValue`: hand off to `requestComponentChanges`, accumulating
   *     `newStateVariableValues` for the post-loop flush.
   *   - `addComponents` / `deleteComponents`: structural changes that
   *     synchronously call `Core.addComponents` / `Core.deleteComponents`.
   *   - `executeUpdate`: flush the currently-accumulated
   *     `newStateVariableValues` immediately so subsequent inverse
   *     definitions can read the updated values.
   *   - `recordItemSubmission`: queue the instruction for the
   *     post-loop submission-event recording (and triggers an
   *     immediate save).
   *   - `setComponentNeedingUpdateValue` /
   *     `unsetComponentNeedingUpdateValue`: flag bookkeeping for
   *     downstream answer-submission detection.
   *
   * After the loop, `executeUpdateStateVariables` runs once more on
   * any leftover `newStateVariableValues`. Unless
   * `canSkipUpdatingRenderer` is set, each instruction's target component
   * is queued into `componentsToUpdateRenderers` so an optimistic
   * renderer edit can be confirmed or reverted (see that flag). Then
   * `processStateVariableTriggers` runs, and the final
   * `updateAllChangedRenderers` fan-out runs only when
   * `skipRendererUpdate` is false. Essential values saved during
   * definitions are merged into the cumulative changes log so they
   * persist on the next save.
   *
   * Persistence side effects:
   *   - Submission updates fire `saveState(true, true)` immediately
   *     (fire-and-forget; failures are logged but do not block).
   *   - Otherwise `statePersistence.scheduleSave(1000)` debounces a
   *     save unless `doNotSave` is set.
   */
  async performUpdate({
    updateInstructions,
    diagnostics,
    actionId,
    event,
    overrideReadOnly = false,
    doNotSave = false,
    canSkipUpdatingRenderer = false,
    skipRendererUpdate = false,
    sourceInformation = {}
  }) {
    if (diagnostics) {
      for (let diagnostic of diagnostics) {
        this.core.addDiagnostic(diagnostic);
      }
    }
    if (this.core.flags.readOnly && !overrideReadOnly) {
      if (!canSkipUpdatingRenderer) {
        for (let instruction of updateInstructions) {
          this._recordSourceDetails(instruction, sourceInformation);
        }
        await this.core.updateRendererInstructions({
          componentNamesToUpdate: updateInstructions.map((x2) => x2.componentIdx).filter((idx) => idx != void 0),
          sourceOfUpdate: { sourceInformation },
          actionId
        });
      }
      return;
    }
    let newStateVariableValues = {};
    let newStateVariableValuesProcessed = [];
    let workspace = {};
    let recordComponentSubmissions = [];
    for (let instruction of updateInstructions) {
      this._recordSourceDetails(instruction, sourceInformation);
      if (instruction.updateType === "updateValue") {
        await this.core.requestComponentChanges({
          instruction,
          workspace,
          newStateVariableValues
        });
      } else if (instruction.updateType === "addComponents") {
        const res = createNewComponentIndices(
          instruction.serializedComponents,
          this.core._components.length
        );
        if (res.nComponents > this.core._components.length) {
          this.core._components[res.nComponents - 1] = void 0;
        }
        await addComponents({
          core: this.core,
          serializedComponents: res.components,
          parentIdx: instruction.parentIdx
        });
      } else if (instruction.updateType === "deleteComponents") {
        if (instruction.componentIndices.length > 0) {
          let componentsToDelete = [];
          for (let componentIdx of instruction.componentIndices) {
            let component = this.core._components[componentIdx];
            if (component) {
              componentsToDelete.push(component);
            } else {
              this.core.addDiagnostic({
                type: "info",
                message: `Cannot delete ${componentIdx} as it doesn't exist.`
              });
            }
          }
          if (componentsToDelete.length > 0) {
            await deleteComponents({
              core: this.core,
              components: componentsToDelete
            });
          }
        }
      } else if (instruction.updateType === "executeUpdate") {
        await this.core.executeUpdateStateVariables(
          newStateVariableValues
        );
        newStateVariableValuesProcessed.push(newStateVariableValues);
        newStateVariableValues = {};
      } else if (instruction.updateType === "recordItemSubmission") {
        recordComponentSubmissions.push(instruction);
      } else if (instruction.updateType === "setComponentNeedingUpdateValue") {
        this.core.cumulativeStateVariableChanges.__componentNeedingUpdateValue = this.core._components[instruction.componentIdx].stateId;
      } else if (instruction.updateType === "unsetComponentNeedingUpdateValue") {
        delete this.core.cumulativeStateVariableChanges.__componentNeedingUpdateValue;
      }
    }
    await this.core.executeUpdateStateVariables(newStateVariableValues);
    newStateVariableValuesProcessed.push(newStateVariableValues);
    if (!canSkipUpdatingRenderer) {
      updateInstructions.forEach((comp) => {
        if (comp.componentIdx != void 0) {
          this.core.updateInfo.componentsToUpdateRenderers.add(
            comp.componentIdx
          );
        }
      });
    }
    await this.core.processStateVariableTriggers();
    if (!skipRendererUpdate) {
      await this.core.updateAllChangedRenderers(
        sourceInformation,
        actionId
      );
    }
    if (recordComponentSubmissions.length > 0) {
      let componentsSubmitted = [
        ...new Set(
          recordComponentSubmissions.map((x2) => x2.componentNumber)
        )
      ];
      let componentCreditAchieved = await this.core.document.stateValues.componentCreditAchieved;
      if (event) {
        if (!event.context) {
          event.context = {};
        }
        event.context.componentNumber = componentsSubmitted[0];
        event.context.componentCreditAchieved = componentCreditAchieved[componentsSubmitted[0] - 1];
        if (componentsSubmitted.length > 1) {
          event.context.additionalComponentCreditAchieved = {};
          for (let componentNumber of componentsSubmitted) {
            event.context.additionalComponentCreditAchieved[componentNumber] = componentCreditAchieved[componentNumber - 1];
          }
        }
        event.context.docCreditAchieved = await this.core.document.stateValues.creditAchieved;
      }
    }
    if (Object.keys(this.core.essentialValuesSavedInDefinition).length > 0) {
      for (const stateId in this.core.essentialValuesSavedInDefinition) {
        const componentIdx = this.core.componentIdxByStateId[stateId];
        let essentialState = this.core._components[componentIdx]?.essentialState;
        if (essentialState) {
          for (let varName in this.core.essentialValuesSavedInDefinition[stateId]) {
            if (essentialState[varName] !== void 0) {
              this.core.essentialValueWriter.mergeIntoCumulative(
                stateId,
                varName,
                essentialState[varName]
              );
            }
          }
        }
      }
      this.core.essentialValuesSavedInDefinition = {};
    }
    if (!doNotSave) {
      for (let newValuesProcessed of newStateVariableValuesProcessed) {
        for (const componentIdxStr in newValuesProcessed) {
          const stateId = this.core._components[Number(componentIdxStr)].stateId;
          for (let varName in newValuesProcessed[componentIdxStr]) {
            this.core.essentialValueWriter.mergeIntoCumulative(
              stateId,
              varName,
              newValuesProcessed[componentIdxStr][varName]
            );
          }
        }
      }
    }
    if (recordComponentSubmissions.length > 0) {
      this.core.saveState(true, true).catch(reportTimerError(TimerLabels.submissionSaveState));
    } else if (!doNotSave) {
      this.core.statePersistence.scheduleSave(1e3);
    }
    await this.core.document.stateValues.componentCreditAchieved;
    if (event) {
      this.core.requestRecordEvent(event);
    }
  }
  /**
   * Merge `instruction.sourceDetails` into the per-component bag inside
   * `sourceInformation`. No-op when `sourceDetails` is absent or when the
   * instruction has no `componentIdx` (e.g. `recordItemSubmission`
   * entries from `Answer.js`/`Pretzel.js`, which would otherwise create
   * a `sourceInformation["undefined"]` sentinel bucket). Used to forward
   * upstream-action provenance ("which input triggered this change?")
   * through the update pipeline.
   */
  _recordSourceDetails(instruction, sourceInformation) {
    if (instruction.componentIdx == void 0 || !instruction.sourceDetails) {
      return;
    }
    let componentSourceInformation = sourceInformation[instruction.componentIdx];
    if (!componentSourceInformation) {
      componentSourceInformation = sourceInformation[instruction.componentIdx] = {};
    }
    Object.assign(componentSourceInformation, instruction.sourceDetails);
  }
}
class VisibilityTracker {
  constructor({ core: core2 }) {
    this.core = core2;
    this.info = {
      componentsCurrentlyVisible: {},
      infoToSend: {},
      timeLastSent: /* @__PURE__ */ new Date(),
      saveDelay: 6e4,
      saveTimerId: null,
      suspendDelay: 3 * 6e4,
      suspendTimerId: null,
      suspended: false,
      documentHasBeenVisible: false
    };
  }
  processVisibilityChangedEvent(event) {
    let componentIdx = event.object.componentIdx;
    let isVisible = event.result.isVisible;
    if (isVisible) {
      if (!this.info.componentsCurrentlyVisible[componentIdx]) {
        this.info.componentsCurrentlyVisible[componentIdx] = /* @__PURE__ */ new Date();
      }
      if (componentIdx === this.core.documentIdx) {
        if (!this.info.documentHasBeenVisible) {
          this.info.documentHasBeenVisible = true;
          this.core.onDocumentFirstVisible();
        }
      }
    } else {
      let begin = this.info.componentsCurrentlyVisible[componentIdx];
      if (begin) {
        delete this.info.componentsCurrentlyVisible[componentIdx];
        let timeInSeconds = ((/* @__PURE__ */ new Date()).getTime() - Math.max(
          begin.getTime(),
          this.info.timeLastSent.getTime()
        )) / 1e3;
        if (this.info.infoToSend[componentIdx]) {
          this.info.infoToSend[componentIdx] += timeInSeconds;
        } else {
          this.info.infoToSend[componentIdx] = timeInSeconds;
        }
      }
    }
  }
  sendVisibilityChangedEvents() {
    let infoToSend = { ...this.info.infoToSend };
    this.info.infoToSend = {};
    let timeLastSent = this.info.timeLastSent;
    this.info.timeLastSent = /* @__PURE__ */ new Date();
    let currentVisible = {
      ...this.info.componentsCurrentlyVisible
    };
    for (const componentIdxStr in currentVisible) {
      let timeInSeconds = (this.info.timeLastSent.getTime() - Math.max(
        timeLastSent.getTime(),
        currentVisible[componentIdxStr].getTime()
      )) / 1e3;
      if (infoToSend[componentIdxStr]) {
        infoToSend[componentIdxStr] += timeInSeconds;
      } else {
        infoToSend[componentIdxStr] = timeInSeconds;
      }
    }
    for (const componentIdxStr in infoToSend) {
      infoToSend[componentIdxStr] = Math.round(
        infoToSend[componentIdxStr]
      );
      if (!infoToSend[componentIdxStr]) {
        delete infoToSend[componentIdxStr];
      }
    }
    let promise;
    if (Object.keys(infoToSend).length > 0) {
      let event = {
        object: {
          componentIdx: this.core.documentIdx,
          componentType: "document"
        },
        verb: "isVisible",
        result: infoToSend
      };
      promise = this.core.processQueue.sendRecordEvent(event);
    }
    if (!this.info.suspended) {
      if (this.info.saveTimerId !== null) {
        clearTimeout(this.info.saveTimerId);
      }
      this.info.saveTimerId = setTimeout(() => {
        this.sendVisibilityChangedEvents()?.catch(
          reportTimerError(TimerLabels.visibilityPeriodicSend)
        );
      }, this.info.saveDelay);
    }
    return promise;
  }
  async suspendVisibilityMeasuring() {
    if (this.info.saveTimerId !== null) {
      clearTimeout(this.info.saveTimerId);
    }
    if (this.info.suspendTimerId !== null) {
      clearTimeout(this.info.suspendTimerId);
    }
    if (!this.info.suspended) {
      this.info.suspended = true;
      await this.sendVisibilityChangedEvents();
    }
  }
  resumeVisibilityMeasuring() {
    if (this.info.suspended) {
      this.info.suspended = false;
      this.info.timeLastSent = /* @__PURE__ */ new Date();
      if (this.info.saveTimerId !== null) {
        clearTimeout(this.info.saveTimerId);
      }
      this.info.saveTimerId = setTimeout(() => {
        this.sendVisibilityChangedEvents()?.catch(
          reportTimerError(TimerLabels.visibilityResumeSend)
        );
      }, this.info.saveDelay);
    }
    if (this.info.suspendTimerId !== null) {
      clearTimeout(this.info.suspendTimerId);
    }
    this.info.suspendTimerId = setTimeout(() => {
      this.suspendVisibilityMeasuring().catch(
        reportTimerError(TimerLabels.visibilityAutoSuspend)
      );
    }, this.info.suspendDelay);
  }
}
function findCaseInsensitiveMatches({
  stateVariables,
  componentClass,
  componentInfoObjects
}) {
  let stateVarInfo = componentInfoObjects.stateVariableInfo[componentClass.componentType];
  let newVariables = [];
  for (let stateVariable of stateVariables) {
    let foundMatch = false;
    let lowerCaseVarName = stateVariable.toLowerCase();
    for (let varName in stateVarInfo.stateVariableDescriptions) {
      if (lowerCaseVarName === varName.toLowerCase()) {
        foundMatch = true;
        newVariables.push(varName);
        break;
      }
    }
    if (foundMatch) {
      continue;
    }
    let isArraySize = false;
    let lowerCaseNameMinusSize = lowerCaseVarName;
    if (lowerCaseVarName.substring(0, 13) === "__array_size_") {
      isArraySize = true;
      lowerCaseNameMinusSize = lowerCaseVarName.substring(13);
    }
    for (let aliasName in stateVarInfo.aliases) {
      if (lowerCaseNameMinusSize === aliasName.toLowerCase()) {
        if (isArraySize) {
          aliasName = "__array_size_" + aliasName;
        }
        newVariables.push(aliasName);
        foundMatch = true;
        break;
      }
    }
    if (foundMatch) {
      continue;
    }
    let arrayEntryPrefixesLongestToShortest = Object.keys(
      stateVarInfo.arrayEntryPrefixes
    ).sort((a2, b2) => b2.length - a2.length);
    for (let prefix of arrayEntryPrefixesLongestToShortest) {
      if (lowerCaseVarName.substring(0, prefix.length) === prefix.toLowerCase()) {
        let arrayVariableName = stateVarInfo.arrayEntryPrefixes[prefix].arrayVariableName;
        let arrayStateVarDescription = stateVarInfo.stateVariableDescriptions[arrayVariableName];
        let arrayKeys = arrayStateVarDescription.getArrayKeysFromVarName({
          arrayEntryPrefix: prefix,
          varEnding: stateVariable.substring(prefix.length),
          numDimensions: arrayStateVarDescription.numDimensions
        });
        if (arrayKeys.length > 0) {
          let newVarName = prefix + lowerCaseVarName.substring(prefix.length);
          foundMatch = true;
          newVariables.push(newVarName);
          break;
        }
      }
    }
    if (foundMatch) {
      continue;
    }
    newVariables.push(stateVariable);
  }
  return newVariables;
}
function matchPublicStateVariables({
  stateVariables,
  componentClass,
  componentInfoObjects
}) {
  let stateVarInfo = componentInfoObjects.publicStateVariableInfo[componentClass.componentType];
  let newVariables = [];
  for (let stateVariable of stateVariables) {
    if (stateVariable in stateVarInfo.stateVariableDescriptions) {
      newVariables.push(stateVariable);
      continue;
    }
    let varName = stateVariable;
    if (varName in stateVarInfo.aliases) {
      varName = stateVarInfo.aliases[varName].target;
      if (varName in stateVarInfo.stateVariableDescriptions) {
        newVariables.push(varName);
        continue;
      }
    }
    let foundMatch = false;
    let arrayEntryPrefixesLongestToShortest = Object.keys(
      stateVarInfo.arrayEntryPrefixes
    ).sort((a2, b2) => b2.length - a2.length);
    for (let prefix of arrayEntryPrefixesLongestToShortest) {
      if (varName.substring(0, prefix.length) === prefix) {
        let arrayVariableName = stateVarInfo.arrayEntryPrefixes[prefix].arrayVariableName;
        let arrayStateVarDescription = stateVarInfo.stateVariableDescriptions[arrayVariableName];
        let arrayKeys = arrayStateVarDescription.getArrayKeysFromVarName({
          arrayEntryPrefix: prefix,
          varEnding: varName.substring(prefix.length),
          numDimensions: arrayStateVarDescription.numDimensions
        });
        if (arrayKeys.length > 0) {
          foundMatch = true;
          break;
        }
      }
    }
    if (foundMatch) {
      newVariables.push(stateVariable);
    } else {
      newVariables.push("__not_public_" + stateVariable);
    }
  }
  return newVariables;
}
function substituteAliases({
  stateVariables,
  componentClass,
  componentInfoObjects
}) {
  let newVariables = [];
  let stateVarInfo = componentInfoObjects.stateVariableInfo[componentClass.componentType];
  for (let stateVariable of stateVariables) {
    let isArraySize = false;
    if (stateVariable.substring(0, 13) === "__array_size_") {
      isArraySize = true;
      stateVariable = stateVariable.substring(13);
    }
    stateVariable = stateVariable in stateVarInfo.aliases ? stateVarInfo.aliases[stateVariable].target : stateVariable;
    if (isArraySize) {
      stateVariable = "__array_size_" + stateVariable;
    }
    newVariables.push(stateVariable);
  }
  return newVariables;
}
function publicCaseInsensitiveAliasSubstitutions({
  stateVariables,
  componentClass,
  componentInfoObjects
}) {
  let mappedVarNames = findCaseInsensitiveMatches({
    stateVariables,
    componentClass,
    componentInfoObjects
  });
  mappedVarNames = matchPublicStateVariables({
    stateVariables: mappedVarNames,
    componentClass,
    componentInfoObjects
  });
  mappedVarNames = substituteAliases({
    stateVariables: mappedVarNames,
    componentClass,
    componentInfoObjects
  });
  return mappedVarNames;
}
function checkIfArrayEntry({
  stateVariable,
  component
}) {
  for (let arrayEntryPrefix in component.arrayEntryPrefixes) {
    if (stateVariable.substring(0, arrayEntryPrefix.length) === arrayEntryPrefix) {
      let arrayVariableName = component.arrayEntryPrefixes[arrayEntryPrefix];
      let arrayStateVarObj = component.state[arrayVariableName];
      let arrayKeys = arrayStateVarObj.getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding: stateVariable.substring(arrayEntryPrefix.length),
        numDimensions: arrayStateVarObj.numDimensions
      });
      if (arrayKeys.length > 0) {
        return {
          isArrayEntry: true,
          arrayVariableName,
          arrayEntryPrefix
        };
      }
    }
  }
  return { isArrayEntry: false };
}
var sha1$2 = { exports: {} };
function commonjsRequire(path) {
  throw new Error('Could not dynamically require "' + path + '". Please configure the dynamicRequireTargets or/and ignoreDynamicRequires option of @rollup/plugin-commonjs appropriately for this require call to work.');
}
var core$1 = { exports: {} };
var core = core$1.exports;
var hasRequiredCore;
function requireCore() {
  if (hasRequiredCore) return core$1.exports;
  hasRequiredCore = 1;
  (function(module, exports) {
    (function(root, factory) {
      {
        module.exports = factory();
      }
    })(core, function() {
      var CryptoJS = CryptoJS || (function(Math2, undefined$1) {
        var crypto2;
        if (typeof window !== "undefined" && window.crypto) {
          crypto2 = window.crypto;
        }
        if (typeof self !== "undefined" && self.crypto) {
          crypto2 = self.crypto;
        }
        if (typeof globalThis !== "undefined" && globalThis.crypto) {
          crypto2 = globalThis.crypto;
        }
        if (!crypto2 && typeof window !== "undefined" && window.msCrypto) {
          crypto2 = window.msCrypto;
        }
        if (!crypto2 && typeof commonjsGlobal$1 !== "undefined" && commonjsGlobal$1.crypto) {
          crypto2 = commonjsGlobal$1.crypto;
        }
        if (!crypto2 && typeof commonjsRequire === "function") {
          try {
            crypto2 = require$$0;
          } catch (err) {
          }
        }
        var cryptoSecureRandomInt = function() {
          if (crypto2) {
            if (typeof crypto2.getRandomValues === "function") {
              try {
                return crypto2.getRandomValues(new Uint32Array(1))[0];
              } catch (err) {
              }
            }
            if (typeof crypto2.randomBytes === "function") {
              try {
                return crypto2.randomBytes(4).readInt32LE();
              } catch (err) {
              }
            }
          }
          throw new Error("Native crypto module could not be used to get secure random number.");
        };
        var create = Object.create || /* @__PURE__ */ (function() {
          function F2() {
          }
          return function(obj) {
            var subtype;
            F2.prototype = obj;
            subtype = new F2();
            F2.prototype = null;
            return subtype;
          };
        })();
        var C3 = {};
        var C_lib = C3.lib = {};
        var Base = C_lib.Base = /* @__PURE__ */ (function() {
          return {
            /**
             * Creates a new object that inherits from this object.
             *
             * @param {Object} overrides Properties to copy into the new object.
             *
             * @return {Object} The new object.
             *
             * @static
             *
             * @example
             *
             *     var MyType = CryptoJS.lib.Base.extend({
             *         field: 'value',
             *
             *         method: function () {
             *         }
             *     });
             */
            extend: function(overrides) {
              var subtype = create(this);
              if (overrides) {
                subtype.mixIn(overrides);
              }
              if (!subtype.hasOwnProperty("init") || this.init === subtype.init) {
                subtype.init = function() {
                  subtype.$super.init.apply(this, arguments);
                };
              }
              subtype.init.prototype = subtype;
              subtype.$super = this;
              return subtype;
            },
            /**
             * Extends this object and runs the init method.
             * Arguments to create() will be passed to init().
             *
             * @return {Object} The new object.
             *
             * @static
             *
             * @example
             *
             *     var instance = MyType.create();
             */
            create: function() {
              var instance = this.extend();
              instance.init.apply(instance, arguments);
              return instance;
            },
            /**
             * Initializes a newly created object.
             * Override this method to add some logic when your objects are created.
             *
             * @example
             *
             *     var MyType = CryptoJS.lib.Base.extend({
             *         init: function () {
             *             // ...
             *         }
             *     });
             */
            init: function() {
            },
            /**
             * Copies properties into this object.
             *
             * @param {Object} properties The properties to mix in.
             *
             * @example
             *
             *     MyType.mixIn({
             *         field: 'value'
             *     });
             */
            mixIn: function(properties) {
              for (var propertyName in properties) {
                if (properties.hasOwnProperty(propertyName)) {
                  this[propertyName] = properties[propertyName];
                }
              }
              if (properties.hasOwnProperty("toString")) {
                this.toString = properties.toString;
              }
            },
            /**
             * Creates a copy of this object.
             *
             * @return {Object} The clone.
             *
             * @example
             *
             *     var clone = instance.clone();
             */
            clone: function() {
              return this.init.prototype.extend(this);
            }
          };
        })();
        var WordArray = C_lib.WordArray = Base.extend({
          /**
           * Initializes a newly created word array.
           *
           * @param {Array} words (Optional) An array of 32-bit words.
           * @param {number} sigBytes (Optional) The number of significant bytes in the words.
           *
           * @example
           *
           *     var wordArray = CryptoJS.lib.WordArray.create();
           *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607]);
           *     var wordArray = CryptoJS.lib.WordArray.create([0x00010203, 0x04050607], 6);
           */
          init: function(words2, sigBytes) {
            words2 = this.words = words2 || [];
            if (sigBytes != undefined$1) {
              this.sigBytes = sigBytes;
            } else {
              this.sigBytes = words2.length * 4;
            }
          },
          /**
           * Converts this word array to a string.
           *
           * @param {Encoder} encoder (Optional) The encoding strategy to use. Default: CryptoJS.enc.Hex
           *
           * @return {string} The stringified word array.
           *
           * @example
           *
           *     var string = wordArray + '';
           *     var string = wordArray.toString();
           *     var string = wordArray.toString(CryptoJS.enc.Utf8);
           */
          toString: function(encoder) {
            return (encoder || Hex).stringify(this);
          },
          /**
           * Concatenates a word array to this word array.
           *
           * @param {WordArray} wordArray The word array to append.
           *
           * @return {WordArray} This word array.
           *
           * @example
           *
           *     wordArray1.concat(wordArray2);
           */
          concat: function(wordArray) {
            var thisWords = this.words;
            var thatWords = wordArray.words;
            var thisSigBytes = this.sigBytes;
            var thatSigBytes = wordArray.sigBytes;
            this.clamp();
            if (thisSigBytes % 4) {
              for (var i2 = 0; i2 < thatSigBytes; i2++) {
                var thatByte = thatWords[i2 >>> 2] >>> 24 - i2 % 4 * 8 & 255;
                thisWords[thisSigBytes + i2 >>> 2] |= thatByte << 24 - (thisSigBytes + i2) % 4 * 8;
              }
            } else {
              for (var j2 = 0; j2 < thatSigBytes; j2 += 4) {
                thisWords[thisSigBytes + j2 >>> 2] = thatWords[j2 >>> 2];
              }
            }
            this.sigBytes += thatSigBytes;
            return this;
          },
          /**
           * Removes insignificant bits.
           *
           * @example
           *
           *     wordArray.clamp();
           */
          clamp: function() {
            var words2 = this.words;
            var sigBytes = this.sigBytes;
            words2[sigBytes >>> 2] &= 4294967295 << 32 - sigBytes % 4 * 8;
            words2.length = Math2.ceil(sigBytes / 4);
          },
          /**
           * Creates a copy of this word array.
           *
           * @return {WordArray} The clone.
           *
           * @example
           *
           *     var clone = wordArray.clone();
           */
          clone: function() {
            var clone = Base.clone.call(this);
            clone.words = this.words.slice(0);
            return clone;
          },
          /**
           * Creates a word array filled with random bytes.
           *
           * @param {number} nBytes The number of random bytes to generate.
           *
           * @return {WordArray} The random word array.
           *
           * @static
           *
           * @example
           *
           *     var wordArray = CryptoJS.lib.WordArray.random(16);
           */
          random: function(nBytes) {
            var words2 = [];
            for (var i2 = 0; i2 < nBytes; i2 += 4) {
              words2.push(cryptoSecureRandomInt());
            }
            return new WordArray.init(words2, nBytes);
          }
        });
        var C_enc = C3.enc = {};
        var Hex = C_enc.Hex = {
          /**
           * Converts a word array to a hex string.
           *
           * @param {WordArray} wordArray The word array.
           *
           * @return {string} The hex string.
           *
           * @static
           *
           * @example
           *
           *     var hexString = CryptoJS.enc.Hex.stringify(wordArray);
           */
          stringify: function(wordArray) {
            var words2 = wordArray.words;
            var sigBytes = wordArray.sigBytes;
            var hexChars = [];
            for (var i2 = 0; i2 < sigBytes; i2++) {
              var bite = words2[i2 >>> 2] >>> 24 - i2 % 4 * 8 & 255;
              hexChars.push((bite >>> 4).toString(16));
              hexChars.push((bite & 15).toString(16));
            }
            return hexChars.join("");
          },
          /**
           * Converts a hex string to a word array.
           *
           * @param {string} hexStr The hex string.
           *
           * @return {WordArray} The word array.
           *
           * @static
           *
           * @example
           *
           *     var wordArray = CryptoJS.enc.Hex.parse(hexString);
           */
          parse: function(hexStr) {
            var hexStrLength = hexStr.length;
            var words2 = [];
            for (var i2 = 0; i2 < hexStrLength; i2 += 2) {
              words2[i2 >>> 3] |= parseInt(hexStr.substr(i2, 2), 16) << 24 - i2 % 8 * 4;
            }
            return new WordArray.init(words2, hexStrLength / 2);
          }
        };
        var Latin1 = C_enc.Latin1 = {
          /**
           * Converts a word array to a Latin1 string.
           *
           * @param {WordArray} wordArray The word array.
           *
           * @return {string} The Latin1 string.
           *
           * @static
           *
           * @example
           *
           *     var latin1String = CryptoJS.enc.Latin1.stringify(wordArray);
           */
          stringify: function(wordArray) {
            var words2 = wordArray.words;
            var sigBytes = wordArray.sigBytes;
            var latin1Chars = [];
            for (var i2 = 0; i2 < sigBytes; i2++) {
              var bite = words2[i2 >>> 2] >>> 24 - i2 % 4 * 8 & 255;
              latin1Chars.push(String.fromCharCode(bite));
            }
            return latin1Chars.join("");
          },
          /**
           * Converts a Latin1 string to a word array.
           *
           * @param {string} latin1Str The Latin1 string.
           *
           * @return {WordArray} The word array.
           *
           * @static
           *
           * @example
           *
           *     var wordArray = CryptoJS.enc.Latin1.parse(latin1String);
           */
          parse: function(latin1Str) {
            var latin1StrLength = latin1Str.length;
            var words2 = [];
            for (var i2 = 0; i2 < latin1StrLength; i2++) {
              words2[i2 >>> 2] |= (latin1Str.charCodeAt(i2) & 255) << 24 - i2 % 4 * 8;
            }
            return new WordArray.init(words2, latin1StrLength);
          }
        };
        var Utf8 = C_enc.Utf8 = {
          /**
           * Converts a word array to a UTF-8 string.
           *
           * @param {WordArray} wordArray The word array.
           *
           * @return {string} The UTF-8 string.
           *
           * @static
           *
           * @example
           *
           *     var utf8String = CryptoJS.enc.Utf8.stringify(wordArray);
           */
          stringify: function(wordArray) {
            try {
              return decodeURIComponent(escape(Latin1.stringify(wordArray)));
            } catch (e3) {
              throw new Error("Malformed UTF-8 data");
            }
          },
          /**
           * Converts a UTF-8 string to a word array.
           *
           * @param {string} utf8Str The UTF-8 string.
           *
           * @return {WordArray} The word array.
           *
           * @static
           *
           * @example
           *
           *     var wordArray = CryptoJS.enc.Utf8.parse(utf8String);
           */
          parse: function(utf8Str) {
            return Latin1.parse(unescape(encodeURIComponent(utf8Str)));
          }
        };
        var BufferedBlockAlgorithm = C_lib.BufferedBlockAlgorithm = Base.extend({
          /**
           * Resets this block algorithm's data buffer to its initial state.
           *
           * @example
           *
           *     bufferedBlockAlgorithm.reset();
           */
          reset: function() {
            this._data = new WordArray.init();
            this._nDataBytes = 0;
          },
          /**
           * Adds new data to this block algorithm's buffer.
           *
           * @param {WordArray|string} data The data to append. Strings are converted to a WordArray using UTF-8.
           *
           * @example
           *
           *     bufferedBlockAlgorithm._append('data');
           *     bufferedBlockAlgorithm._append(wordArray);
           */
          _append: function(data) {
            if (typeof data == "string") {
              data = Utf8.parse(data);
            }
            this._data.concat(data);
            this._nDataBytes += data.sigBytes;
          },
          /**
           * Processes available data blocks.
           *
           * This method invokes _doProcessBlock(offset), which must be implemented by a concrete subtype.
           *
           * @param {boolean} doFlush Whether all blocks and partial blocks should be processed.
           *
           * @return {WordArray} The processed data.
           *
           * @example
           *
           *     var processedData = bufferedBlockAlgorithm._process();
           *     var processedData = bufferedBlockAlgorithm._process(!!'flush');
           */
          _process: function(doFlush) {
            var processedWords;
            var data = this._data;
            var dataWords = data.words;
            var dataSigBytes = data.sigBytes;
            var blockSize = this.blockSize;
            var blockSizeBytes = blockSize * 4;
            var nBlocksReady = dataSigBytes / blockSizeBytes;
            if (doFlush) {
              nBlocksReady = Math2.ceil(nBlocksReady);
            } else {
              nBlocksReady = Math2.max((nBlocksReady | 0) - this._minBufferSize, 0);
            }
            var nWordsReady = nBlocksReady * blockSize;
            var nBytesReady = Math2.min(nWordsReady * 4, dataSigBytes);
            if (nWordsReady) {
              for (var offset = 0; offset < nWordsReady; offset += blockSize) {
                this._doProcessBlock(dataWords, offset);
              }
              processedWords = dataWords.splice(0, nWordsReady);
              data.sigBytes -= nBytesReady;
            }
            return new WordArray.init(processedWords, nBytesReady);
          },
          /**
           * Creates a copy of this object.
           *
           * @return {Object} The clone.
           *
           * @example
           *
           *     var clone = bufferedBlockAlgorithm.clone();
           */
          clone: function() {
            var clone = Base.clone.call(this);
            clone._data = this._data.clone();
            return clone;
          },
          _minBufferSize: 0
        });
        C_lib.Hasher = BufferedBlockAlgorithm.extend({
          /**
           * Configuration options.
           */
          cfg: Base.extend(),
          /**
           * Initializes a newly created hasher.
           *
           * @param {Object} cfg (Optional) The configuration options to use for this hash computation.
           *
           * @example
           *
           *     var hasher = CryptoJS.algo.SHA256.create();
           */
          init: function(cfg) {
            this.cfg = this.cfg.extend(cfg);
            this.reset();
          },
          /**
           * Resets this hasher to its initial state.
           *
           * @example
           *
           *     hasher.reset();
           */
          reset: function() {
            BufferedBlockAlgorithm.reset.call(this);
            this._doReset();
          },
          /**
           * Updates this hasher with a message.
           *
           * @param {WordArray|string} messageUpdate The message to append.
           *
           * @return {Hasher} This hasher.
           *
           * @example
           *
           *     hasher.update('message');
           *     hasher.update(wordArray);
           */
          update: function(messageUpdate) {
            this._append(messageUpdate);
            this._process();
            return this;
          },
          /**
           * Finalizes the hash computation.
           * Note that the finalize operation is effectively a destructive, read-once operation.
           *
           * @param {WordArray|string} messageUpdate (Optional) A final message update.
           *
           * @return {WordArray} The hash.
           *
           * @example
           *
           *     var hash = hasher.finalize();
           *     var hash = hasher.finalize('message');
           *     var hash = hasher.finalize(wordArray);
           */
          finalize: function(messageUpdate) {
            if (messageUpdate) {
              this._append(messageUpdate);
            }
            var hash = this._doFinalize();
            return hash;
          },
          blockSize: 512 / 32,
          /**
           * Creates a shortcut function to a hasher's object interface.
           *
           * @param {Hasher} hasher The hasher to create a helper for.
           *
           * @return {Function} The shortcut function.
           *
           * @static
           *
           * @example
           *
           *     var SHA256 = CryptoJS.lib.Hasher._createHelper(CryptoJS.algo.SHA256);
           */
          _createHelper: function(hasher) {
            return function(message, cfg) {
              return new hasher.init(cfg).finalize(message);
            };
          },
          /**
           * Creates a shortcut function to the HMAC's object interface.
           *
           * @param {Hasher} hasher The hasher to use in this HMAC helper.
           *
           * @return {Function} The shortcut function.
           *
           * @static
           *
           * @example
           *
           *     var HmacSHA256 = CryptoJS.lib.Hasher._createHmacHelper(CryptoJS.algo.SHA256);
           */
          _createHmacHelper: function(hasher) {
            return function(message, key) {
              return new C_algo.HMAC.init(hasher, key).finalize(message);
            };
          }
        });
        var C_algo = C3.algo = {};
        return C3;
      })(Math);
      return CryptoJS;
    });
  })(core$1);
  return core$1.exports;
}
var sha1$1 = sha1$2.exports;
var hasRequiredSha1;
function requireSha1() {
  if (hasRequiredSha1) return sha1$2.exports;
  hasRequiredSha1 = 1;
  (function(module, exports) {
    (function(root, factory) {
      {
        module.exports = factory(requireCore());
      }
    })(sha1$1, function(CryptoJS) {
      (function() {
        var C3 = CryptoJS;
        var C_lib = C3.lib;
        var WordArray = C_lib.WordArray;
        var Hasher = C_lib.Hasher;
        var C_algo = C3.algo;
        var W2 = [];
        var SHA1 = C_algo.SHA1 = Hasher.extend({
          _doReset: function() {
            this._hash = new WordArray.init([
              1732584193,
              4023233417,
              2562383102,
              271733878,
              3285377520
            ]);
          },
          _doProcessBlock: function(M3, offset) {
            var H3 = this._hash.words;
            var a2 = H3[0];
            var b2 = H3[1];
            var c2 = H3[2];
            var d2 = H3[3];
            var e3 = H3[4];
            for (var i2 = 0; i2 < 80; i2++) {
              if (i2 < 16) {
                W2[i2] = M3[offset + i2] | 0;
              } else {
                var n2 = W2[i2 - 3] ^ W2[i2 - 8] ^ W2[i2 - 14] ^ W2[i2 - 16];
                W2[i2] = n2 << 1 | n2 >>> 31;
              }
              var t2 = (a2 << 5 | a2 >>> 27) + e3 + W2[i2];
              if (i2 < 20) {
                t2 += (b2 & c2 | ~b2 & d2) + 1518500249;
              } else if (i2 < 40) {
                t2 += (b2 ^ c2 ^ d2) + 1859775393;
              } else if (i2 < 60) {
                t2 += (b2 & c2 | b2 & d2 | c2 & d2) - 1894007588;
              } else {
                t2 += (b2 ^ c2 ^ d2) - 899497514;
              }
              e3 = d2;
              d2 = c2;
              c2 = b2 << 30 | b2 >>> 2;
              b2 = a2;
              a2 = t2;
            }
            H3[0] = H3[0] + a2 | 0;
            H3[1] = H3[1] + b2 | 0;
            H3[2] = H3[2] + c2 | 0;
            H3[3] = H3[3] + d2 | 0;
            H3[4] = H3[4] + e3 | 0;
          },
          _doFinalize: function() {
            var data = this._data;
            var dataWords = data.words;
            var nBitsTotal = this._nDataBytes * 8;
            var nBitsLeft = data.sigBytes * 8;
            dataWords[nBitsLeft >>> 5] |= 128 << 24 - nBitsLeft % 32;
            dataWords[(nBitsLeft + 64 >>> 9 << 4) + 14] = Math.floor(nBitsTotal / 4294967296);
            dataWords[(nBitsLeft + 64 >>> 9 << 4) + 15] = nBitsTotal;
            data.sigBytes = dataWords.length * 4;
            this._process();
            return this._hash;
          },
          clone: function() {
            var clone = Hasher.clone.call(this);
            clone._hash = this._hash.clone();
            return clone;
          }
        });
        C3.SHA1 = Hasher._createHelper(SHA1);
        C3.HmacSHA1 = Hasher._createHmacHelper(SHA1);
      })();
      return CryptoJS.SHA1;
    });
  })(sha1$2);
  return sha1$2.exports;
}
var sha1Exports = requireSha1();
const sha1 = /* @__PURE__ */ getDefaultExportFromCjs$1(sha1Exports);
var encBase64$1 = { exports: {} };
var encBase64 = encBase64$1.exports;
var hasRequiredEncBase64;
function requireEncBase64() {
  if (hasRequiredEncBase64) return encBase64$1.exports;
  hasRequiredEncBase64 = 1;
  (function(module, exports) {
    (function(root, factory) {
      {
        module.exports = factory(requireCore());
      }
    })(encBase64, function(CryptoJS) {
      (function() {
        var C3 = CryptoJS;
        var C_lib = C3.lib;
        var WordArray = C_lib.WordArray;
        var C_enc = C3.enc;
        C_enc.Base64 = {
          /**
           * Converts a word array to a Base64 string.
           *
           * @param {WordArray} wordArray The word array.
           *
           * @return {string} The Base64 string.
           *
           * @static
           *
           * @example
           *
           *     var base64String = CryptoJS.enc.Base64.stringify(wordArray);
           */
          stringify: function(wordArray) {
            var words2 = wordArray.words;
            var sigBytes = wordArray.sigBytes;
            var map2 = this._map;
            wordArray.clamp();
            var base64Chars = [];
            for (var i2 = 0; i2 < sigBytes; i2 += 3) {
              var byte1 = words2[i2 >>> 2] >>> 24 - i2 % 4 * 8 & 255;
              var byte2 = words2[i2 + 1 >>> 2] >>> 24 - (i2 + 1) % 4 * 8 & 255;
              var byte3 = words2[i2 + 2 >>> 2] >>> 24 - (i2 + 2) % 4 * 8 & 255;
              var triplet = byte1 << 16 | byte2 << 8 | byte3;
              for (var j2 = 0; j2 < 4 && i2 + j2 * 0.75 < sigBytes; j2++) {
                base64Chars.push(map2.charAt(triplet >>> 6 * (3 - j2) & 63));
              }
            }
            var paddingChar = map2.charAt(64);
            if (paddingChar) {
              while (base64Chars.length % 4) {
                base64Chars.push(paddingChar);
              }
            }
            return base64Chars.join("");
          },
          /**
           * Converts a Base64 string to a word array.
           *
           * @param {string} base64Str The Base64 string.
           *
           * @return {WordArray} The word array.
           *
           * @static
           *
           * @example
           *
           *     var wordArray = CryptoJS.enc.Base64.parse(base64String);
           */
          parse: function(base64Str) {
            var base64StrLength = base64Str.length;
            var map2 = this._map;
            var reverseMap = this._reverseMap;
            if (!reverseMap) {
              reverseMap = this._reverseMap = [];
              for (var j2 = 0; j2 < map2.length; j2++) {
                reverseMap[map2.charCodeAt(j2)] = j2;
              }
            }
            var paddingChar = map2.charAt(64);
            if (paddingChar) {
              var paddingIndex = base64Str.indexOf(paddingChar);
              if (paddingIndex !== -1) {
                base64StrLength = paddingIndex;
              }
            }
            return parseLoop(base64Str, base64StrLength, reverseMap);
          },
          _map: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/="
        };
        function parseLoop(base64Str, base64StrLength, reverseMap) {
          var words2 = [];
          var nBytes = 0;
          for (var i2 = 0; i2 < base64StrLength; i2++) {
            if (i2 % 4) {
              var bits1 = reverseMap[base64Str.charCodeAt(i2 - 1)] << i2 % 4 * 2;
              var bits2 = reverseMap[base64Str.charCodeAt(i2)] >>> 6 - i2 % 4 * 2;
              var bitsCombined = bits1 | bits2;
              words2[nBytes >>> 2] |= bitsCombined << 24 - nBytes % 4 * 8;
              nBytes++;
            }
          }
          return WordArray.create(words2, nBytes);
        }
      })();
      return CryptoJS.enc.Base64;
    });
  })(encBase64$1);
  return encBase64$1.exports;
}
var encBase64Exports = requireEncBase64();
const Base64 = /* @__PURE__ */ getDefaultExportFromCjs$1(encBase64Exports);
var defaults$1;
var hasRequiredDefaults;
function requireDefaults() {
  if (hasRequiredDefaults) return defaults$1;
  hasRequiredDefaults = 1;
  defaults$1 = {
    space: "",
    cycles: false,
    replacer: (k2, v2) => v2,
    stringify: JSON.stringify
  };
  return defaults$1;
}
var util$1;
var hasRequiredUtil$1;
function requireUtil$1() {
  if (hasRequiredUtil$1) return util$1;
  hasRequiredUtil$1 = 1;
  util$1 = {
    isArray: Array.isArray,
    assign: Object.assign,
    isObject: (v2) => typeof v2 === "object",
    isFunction: (v2) => typeof v2 === "function",
    isBoolean: (v2) => typeof v2 === "boolean",
    isRegex: (v2) => v2 instanceof RegExp,
    keys: Object.keys
  };
  return util$1;
}
var lib;
var hasRequiredLib;
function requireLib() {
  if (hasRequiredLib) return lib;
  hasRequiredLib = 1;
  const DEFAULTS = requireDefaults();
  const isFunction2 = requireUtil$1().isFunction;
  const isBoolean2 = requireUtil$1().isBoolean;
  const isObject2 = requireUtil$1().isObject;
  const isArray2 = requireUtil$1().isArray;
  const isRegex = requireUtil$1().isRegex;
  const assign2 = requireUtil$1().assign;
  const keys2 = requireUtil$1().keys;
  function serialize(obj) {
    if (obj === null || obj === void 0) return obj;
    if (isRegex(obj)) return obj.toString();
    return obj.toJSON ? obj.toJSON() : obj;
  }
  function stringifyDeterministic(obj, opts) {
    opts = opts || assign2({}, DEFAULTS);
    if (isFunction2(opts)) opts = { compare: opts };
    const space = opts.space || DEFAULTS.space;
    const cycles = isBoolean2(opts.cycles) ? opts.cycles : DEFAULTS.cycles;
    const replacer2 = opts.replacer || DEFAULTS.replacer;
    const stringify2 = opts.stringify || DEFAULTS.stringify;
    const compare2 = opts.compare && /* @__PURE__ */ (function(f2) {
      return function(node) {
        return function(a2, b2) {
          const aobj = { key: a2, value: node[a2] };
          const bobj = { key: b2, value: node[b2] };
          return f2(aobj, bobj);
        };
      };
    })(opts.compare);
    if (!cycles) stringify2(obj);
    const seen = [];
    return (function _deterministic(parent, key, node, level) {
      const indent = space ? "\n" + new Array(level + 1).join(space) : "";
      const colonSeparator = space ? ": " : ":";
      node = serialize(node);
      node = replacer2.call(parent, key, node);
      if (node === void 0) return;
      if (!isObject2(node) || node === null) return stringify2(node);
      if (isArray2(node)) {
        const out = [];
        for (let i2 = 0; i2 < node.length; i2++) {
          const item = _deterministic(node, i2, node[i2], level + 1) || stringify2(null);
          out.push(indent + space + item);
        }
        return "[" + out.join(",") + indent + "]";
      } else {
        if (cycles) {
          if (seen.indexOf(node) !== -1) {
            return stringify2("[Circular]");
          } else {
            seen.push(node);
          }
        }
        const nodeKeys = keys2(node).sort(compare2 && compare2(node));
        const out = [];
        for (let i2 = 0; i2 < nodeKeys.length; i2++) {
          const key2 = nodeKeys[i2];
          const value = _deterministic(node, key2, node[key2], level + 1);
          if (!value) continue;
          const keyValue = stringify2(key2) + colonSeparator + value;
          out.push(indent + space + keyValue);
        }
        seen.splice(seen.indexOf(node), 1);
        return "{" + out.join(",") + indent + "}";
      }
    })({ "": obj }, "", obj, 0);
  }
  lib = stringifyDeterministic;
  return lib;
}
var libExports = requireLib();
const stringify = /* @__PURE__ */ getDefaultExportFromCjs$1(libExports);
function returnScoredContainerAncestorDependency(...variableNames) {
  return {
    dependencyType: "ancestor",
    variableNames
  };
}
function returnStandardAnswerAttributes() {
  return {
    weight: {
      createComponentOfType: "number",
      createStateVariable: "weight",
      defaultValue: 1,
      public: true,
      groupName: "answer-grading",
      description: "Relative weight of this answer when aggregating credit across multiple answers."
    },
    handGraded: {
      createPrimitiveOfType: "boolean",
      createStateVariable: "handGraded",
      defaultValue: false,
      public: true,
      groupName: "answer-grading",
      description: "Whether this answer is graded by hand rather than automatically."
    },
    matchPartial: {
      createComponentOfType: "boolean",
      createStateVariable: "matchPartial",
      defaultValue: false,
      public: true,
      groupName: "answer-grading",
      description: "Whether to award partial credit when the response is partially correct."
    },
    maxNumAttempts: {
      createComponentOfType: "integer",
      createStateVariable: "maxNumAttempts",
      defaultValue: Infinity,
      public: true,
      groupName: "answer-grading",
      description: "Maximum number of times the response can be submitted."
    },
    showCorrectness: {
      createComponentOfType: "boolean",
      createStateVariable: "showCorrectnessPreliminary",
      defaultValue: true,
      groupName: "answer-grading",
      description: "Whether to display whether the submitted response is correct."
    },
    colorCorrectness: {
      createComponentOfType: "boolean",
      createStateVariable: "colorCorrectnessPreliminary",
      defaultValue: true,
      public: true,
      groupName: "answer-grading",
      // The runtime stores the raw attribute value under
      // `colorCorrectnessPreliminary` so a derived `colorCorrectness`
      // state def can combine it with the ancestor's setting. Authors
      // should see only the derived `colorCorrectness` property, so
      // hide the plumbing-named state var from the schema while
      // keeping the attribute itself author-facing. See #1089.
      stateVarExcludeFromSchema: true,
      description: "Whether to color-code the response based on its correctness."
    },
    disableAfterCorrect: {
      createComponentOfType: "boolean",
      createStateVariable: "disableAfterCorrect",
      defaultValue: false,
      public: true,
      groupName: "answer-grading",
      description: "Whether to disable the answer after a fully correct response has been submitted."
    },
    submitLabel: {
      createComponentOfType: "text",
      createStateVariable: "submitLabel",
      defaultValue: "Check Work",
      public: true,
      forRenderer: true,
      groupName: "answer-grading",
      description: "Label for the submit button when correctness is shown."
    },
    submitLabelNoCorrectness: {
      createComponentOfType: "text",
      createStateVariable: "submitLabelNoCorrectness",
      defaultValue: "Submit Response",
      public: true,
      forRenderer: true,
      groupName: "answer-grading",
      description: "Label for the submit button when correctness is not shown."
    },
    displayDigitsForResponses: {
      createComponentOfType: "integer",
      createStateVariable: "displayDigitsForResponses",
      defaultValue: 10,
      public: true,
      groupName: "answer-grading",
      description: "Number of significant digits to display when rendering numeric responses."
    },
    displayDigitsForCreditAchieved: {
      createComponentOfType: "integer",
      createStateVariable: "displayDigitsForCreditAchieved",
      defaultValue: 3,
      public: true,
      groupName: "answer-grading",
      description: "Number of significant digits to display for the credit achieved value."
    }
  };
}
function returnStandardAnswerStateVariableDefinition() {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.showCorrectness = {
    forRenderer: true,
    returnDependencies: () => ({
      showCorrectnessPreliminary: {
        dependencyType: "stateVariable",
        variableName: "showCorrectnessPreliminary"
      },
      showCorrectnessFlag: {
        dependencyType: "flag",
        flagName: "showCorrectness"
      },
      handGraded: {
        dependencyType: "stateVariable",
        variableName: "handGraded"
      },
      showCorrectnessAncestor: {
        dependencyType: "ancestor",
        variableNames: ["showCorrectness"]
      }
    }),
    definition({ dependencyValues, usedDefault }) {
      let showCorrectness;
      if (!usedDefault.showCorrectnessPreliminary) {
        showCorrectness = dependencyValues.showCorrectnessPreliminary;
      } else if (dependencyValues.handGraded) {
        showCorrectness = false;
      } else if (dependencyValues.showCorrectnessAncestor) {
        showCorrectness = dependencyValues.showCorrectnessAncestor.stateValues.showCorrectness;
      } else {
        showCorrectness = dependencyValues.showCorrectnessFlag !== false;
      }
      return { setValue: { showCorrectness } };
    }
  };
  stateVariableDefinitions.colorCorrectness = {
    forRenderer: true,
    returnDependencies: () => ({
      colorCorrectnessPreliminary: {
        dependencyType: "stateVariable",
        variableName: "colorCorrectnessPreliminary"
      },
      showCorrectness: {
        dependencyType: "stateVariable",
        variableName: "showCorrectness"
      },
      colorCorrectnessAncestor: {
        dependencyType: "ancestor",
        variableNames: ["colorCorrectness"]
      }
    }),
    definition({ dependencyValues, usedDefault }) {
      let colorCorrectness = true;
      if (!dependencyValues.showCorrectness) {
        colorCorrectness = false;
      } else if (!usedDefault.colorCorrectnessPreliminary) {
        colorCorrectness = dependencyValues.colorCorrectnessPreliminary;
      } else if (dependencyValues.colorCorrectnessAncestor) {
        colorCorrectness = dependencyValues.colorCorrectnessAncestor.stateValues.colorCorrectness;
      }
      return { setValue: { colorCorrectness } };
    }
  };
  stateVariableDefinitions.suppressCheckWork = {
    returnDependencies: () => ({
      autoSubmit: {
        dependencyType: "flag",
        flagName: "autoSubmit"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          suppressCheckWork: dependencyValues.autoSubmit
        }
      };
    }
  };
  stateVariableDefinitions.displayDecimalsForCreditAchieved = {
    returnDependencies: () => ({}),
    definition: () => ({
      setValue: { displayDecimalsForCreditAchieved: -Infinity }
    })
  };
  stateVariableDefinitions.creditAchieved = {
    description: "The fraction of credit achieved on the most recent submission (between 0 and 1).",
    defaultValue: 0,
    public: true,
    shadowingInstructions: {
      createComponentOfType: "number",
      addAttributeComponentsShadowingStateVariables: {
        displayDigits: {
          stateVariableToShadow: "displayDigitsForCreditAchieved"
        },
        displayDecimals: {
          stateVariableToShadow: "displayDecimalsForCreditAchieved"
        }
      }
    },
    forRenderer: true,
    hasEssential: true,
    returnDependencies: () => ({}),
    definition: () => ({
      useEssentialOrDefaultValue: {
        creditAchieved: true
      }
    }),
    inverseDefinition: function({ desiredStateVariableValues }) {
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "creditAchieved",
            value: desiredStateVariableValues.creditAchieved
          }
        ]
      };
    }
  };
  stateVariableDefinitions.responseHasBeenSubmitted = {
    description: "Whether a response has ever been submitted for this answer.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    defaultValue: false,
    hasEssential: true,
    returnDependencies: () => ({}),
    definition: () => ({
      useEssentialOrDefaultValue: {
        responseHasBeenSubmitted: true
      }
    }),
    inverseDefinition: function({ desiredStateVariableValues }) {
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "responseHasBeenSubmitted",
            value: desiredStateVariableValues.responseHasBeenSubmitted
          }
        ]
      };
    }
  };
  stateVariableDefinitions.autoSubmit = {
    returnDependencies: () => ({
      autoSubmit: {
        dependencyType: "flag",
        flagName: "autoSubmit"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          autoSubmit: Boolean(dependencyValues.autoSubmit)
        }
      };
    }
  };
  stateVariableDefinitions.creditAchievedDependencies = {
    shadowVariable: true,
    stateVariablesDeterminingDependencies: ["autoSubmit"],
    returnDependencies: ({ stateValues }) => ({
      currentCreditAchievedDependencies: {
        dependencyType: "recursiveDependencyValues",
        variableNames: ["creditAchievedIfSubmit"],
        includeImmediateValueWithValue: !stateValues.autoSubmit,
        includeRawValueWithImmediateValue: !stateValues.autoSubmit,
        includeOnlyEssentialValues: true
      }
    }),
    definition({ dependencyValues, componentIdx }) {
      let diagnostics = [];
      let selfDependencies = dependencyValues.currentCreditAchievedDependencies.find(
        (x2) => x2.componentIdx === componentIdx
      );
      if (selfDependencies) {
        if (Object.keys(selfDependencies.stateValues).find(
          (x2) => x2.substring(0, 17) === "submittedResponse"
        )) {
          diagnostics.push({
            message: "An award for this answer is based on the answer tag's own submitted response, which will lead to unexpected behavior.",
            type: "warning"
          });
        }
      }
      let stringified = stringify(
        dependencyValues.currentCreditAchievedDependencies,
        { replacer: serializedComponentsReplacer }
      );
      return {
        setValue: {
          creditAchievedDependencies: Base64.stringify(
            sha1(stringified)
          )
        },
        sendDiagnostics: diagnostics
      };
    },
    markStale: () => ({ answerCreditPotentiallyChanged: true })
  };
  stateVariableDefinitions.creditAchievedDependenciesAtSubmit = {
    defaultValue: null,
    hasEssential: true,
    returnDependencies: () => ({}),
    definition: () => ({
      useEssentialOrDefaultValue: {
        creditAchievedDependenciesAtSubmit: true
      }
    }),
    inverseDefinition: function({ desiredStateVariableValues }) {
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "creditAchievedDependenciesAtSubmit",
            value: desiredStateVariableValues.creditAchievedDependenciesAtSubmit
          }
        ]
      };
    }
  };
  stateVariableDefinitions.justSubmitted = {
    description: "Whether the most recent submission for this answer has not yet been changed.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    forRenderer: true,
    defaultValue: false,
    hasEssential: true,
    shadowVariable: true,
    returnDependencies: () => ({
      currentCreditAchievedDependencies: {
        dependencyType: "stateVariable",
        variableName: "creditAchievedDependencies"
      },
      creditAchievedDependenciesAtSubmit: {
        dependencyType: "stateVariable",
        variableName: "creditAchievedDependenciesAtSubmit"
      },
      disableAfterCorrect: {
        dependencyType: "stateVariable",
        variableName: "disableAfterCorrect"
      },
      hasBeenCorrect: {
        dependencyType: "stateVariable",
        variableName: "hasBeenCorrect"
      }
    }),
    definition: function({
      dependencyValues,
      justUpdatedForNewComponent,
      initialAddPhase
    }) {
      if (dependencyValues.disableAfterCorrect && dependencyValues.hasBeenCorrect) {
        return {
          setValue: { justSubmitted: true }
        };
      }
      let foundChange = dependencyValues.creditAchievedDependenciesAtSubmit !== dependencyValues.currentCreditAchievedDependencies;
      if (foundChange && !(justUpdatedForNewComponent || initialAddPhase)) {
        return {
          setValue: { justSubmitted: false },
          setEssentialValue: { justSubmitted: false }
        };
      } else {
        return {
          useEssentialOrDefaultValue: { justSubmitted: true }
        };
      }
    },
    inverseDefinition({ desiredStateVariableValues }) {
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "justSubmitted",
            value: desiredStateVariableValues.justSubmitted
          }
        ]
      };
    }
  };
  stateVariableDefinitions.numSubmissions = {
    description: "Total number of times a response has been submitted.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    defaultValue: 0,
    hasEssential: true,
    returnDependencies: () => ({}),
    definition: () => ({
      useEssentialOrDefaultValue: {
        numSubmissions: true
      }
    }),
    inverseDefinition: ({ desiredStateVariableValues }) => ({
      success: true,
      instructions: [
        {
          setEssentialValue: "numSubmissions",
          value: desiredStateVariableValues.numSubmissions
        }
      ]
    })
  };
  stateVariableDefinitions.numIncorrectSubmissions = {
    defaultValue: 0,
    hasEssential: true,
    forRenderer: true,
    returnDependencies: () => ({}),
    definition: () => ({
      useEssentialOrDefaultValue: {
        numIncorrectSubmissions: true
      }
    }),
    inverseDefinition: ({ desiredStateVariableValues }) => ({
      success: true,
      instructions: [
        {
          setEssentialValue: "numIncorrectSubmissions",
          value: desiredStateVariableValues.numIncorrectSubmissions
        }
      ]
    })
  };
  stateVariableDefinitions.numAttemptsLeft = {
    description: "Remaining number of submission attempts before the maximum is reached.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    forRenderer: true,
    returnDependencies: () => ({
      numSubmissions: {
        dependencyType: "stateVariable",
        variableName: "numSubmissions"
      },
      maxNumAttempts: {
        dependencyType: "stateVariable",
        variableName: "maxNumAttempts"
      },
      ancestorSuppressingAnswerSubmitButtons: returnScoredContainerAncestorDependency(
        "suppressAnswerSubmitButtons",
        "numAttemptsLeft"
      ),
      // Used to target the ignored-`maxNumAttempts` warning at the
      // attribute itself rather than the whole `<answer>`.
      maxNumAttemptsAttr: {
        dependencyType: "attributeComponent",
        attributeName: "maxNumAttempts"
      }
    }),
    definition({ dependencyValues, usedDefault }) {
      let sendDiagnostics = [];
      let insideSectionWideCheckWork = dependencyValues.ancestorSuppressingAnswerSubmitButtons?.stateValues.suppressAnswerSubmitButtons;
      if (!usedDefault.maxNumAttempts && insideSectionWideCheckWork) {
        sendDiagnostics.push({
          type: "warning",
          message: "Setting `maxNumAttempts` on an `<answer>` inside a container with `sectionWideCheckWork` has no effect, as the number of attempts is controlled by the container. Set `maxNumAttempts` on the container instead.",
          position: dependencyValues.maxNumAttemptsAttr?.position
        });
      }
      const numAttemptsLeft = insideSectionWideCheckWork ? dependencyValues.ancestorSuppressingAnswerSubmitButtons.stateValues.numAttemptsLeft : Math.max(
        0,
        dependencyValues.maxNumAttempts - dependencyValues.numSubmissions
      );
      return {
        setValue: { numAttemptsLeft },
        sendDiagnostics
      };
    }
  };
  stateVariableDefinitions.hasBeenCorrect = {
    defaultValue: false,
    hasEssential: true,
    shadowVariable: true,
    returnDependencies: () => ({
      creditAchieved: {
        dependencyType: "stateVariable",
        variableName: "creditAchieved"
      },
      nextCreditFactor: {
        dependencyType: "stateVariable",
        variableName: "nextCreditFactor",
        variablesOptional: true
      }
    }),
    definition({ dependencyValues }) {
      const creditAchieved = dependencyValues.creditAchieved;
      if (creditAchieved === 1 || creditAchieved > 0 && creditAchieved === dependencyValues.nextCreditFactor) {
        return {
          setValue: { hasBeenCorrect: true },
          setEssentialValue: { hasBeenCorrect: true }
        };
      }
      return {
        useEssentialOrDefaultValue: {
          hasBeenCorrect: true
        }
      };
    }
  };
  stateVariableDefinitions.disabled = {
    description: "Whether this answer is disabled and is no longer accepting submissions.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    forRenderer: true,
    stateVariablesDeterminingDependencies: ["disableAfterCorrect"],
    returnDependencies({ stateValues }) {
      let dependencies = {
        disabledOriginal: {
          dependencyType: "stateVariable",
          variableName: "disabledOriginal"
        },
        numAttemptsLeft: {
          dependencyType: "stateVariable",
          variableName: "numAttemptsLeft"
        },
        disableAfterCorrect: {
          dependencyType: "stateVariable",
          variableName: "disableAfterCorrect"
        }
      };
      if (stateValues.disableAfterCorrect) {
        dependencies.hasBeenCorrect = {
          dependencyType: "stateVariable",
          variableName: "hasBeenCorrect"
        };
      }
      return dependencies;
    },
    definition({ dependencyValues }) {
      let disabled = dependencyValues.disabledOriginal || dependencyValues.numAttemptsLeft < 1 || dependencyValues.disableAfterCorrect && dependencyValues.hasBeenCorrect;
      return { setValue: { disabled } };
    }
  };
  stateVariableDefinitions.inComponentNumber = {
    returnDependencies: () => ({
      documentAncestor: {
        dependencyType: "ancestor",
        componentType: "document",
        variableNames: ["componentNumberByAnswerName"]
      }
    }),
    definition({ dependencyValues, componentIdx }) {
      return {
        setValue: {
          inComponentNumber: dependencyValues.documentAncestor.stateValues.componentNumberByAnswerName[componentIdx]
        }
      };
    }
  };
  return stateVariableDefinitions;
}
function returnSimplifyExpandOnCompareWarning() {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.simplifyExpandOnCompareWarning = {
    returnDependencies: () => ({
      expandOnCompareAttr: {
        dependencyType: "attributeComponent",
        attributeName: "expandOnCompare"
      },
      simplifyOnCompareAttr: {
        dependencyType: "attributeComponent",
        attributeName: "simplifyOnCompare"
      },
      symbolicEquality: {
        dependencyType: "stateVariable",
        variableName: "symbolicEquality"
      }
    }),
    definition({ dependencyValues }) {
      const sendDiagnostics = [];
      if (!dependencyValues.symbolicEquality) {
        const attributesSpecified = [];
        if (dependencyValues.expandOnCompareAttr !== null) {
          attributesSpecified.push("expandOnCompare");
        }
        if (dependencyValues.simplifyOnCompareAttr !== null) {
          attributesSpecified.push("simplifyOnCompare");
        }
        if (attributesSpecified.length > 0) {
          sendDiagnostics.push({
            message: `The ${attributesSpecified.join(
              " and "
            )} attribute${attributesSpecified.length > 1 ? "s" : ""} will have no effect without symbolicEquality set.`,
            type: "warning"
          });
        }
      }
      return {
        sendDiagnostics,
        setValue: {
          simplifyExpandOnCompareWarning: null
        }
      };
    }
  };
  return stateVariableDefinitions;
}
function numberAnswers(components, componentInfoObjects, numSoFar = 0) {
  let count = numSoFar;
  for (let comp of components) {
    if (comp.componentType === "answer" || componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: comp.componentType,
      baseComponentType: "_blockScoredComponent"
    })) {
      count++;
      comp.answerNumber = count;
    } else if (comp.children) {
      const result = numberAnswers(
        comp.children,
        componentInfoObjects,
        count
      );
      count = result.count;
    }
  }
  return { count };
}
class Core {
  constructor({
    doenetML,
    serializedDocument,
    nComponentsInit,
    componentInfoObjects,
    flags,
    allDoenetMLs,
    preliminaryDiagnostics,
    activityId,
    cid,
    docId,
    attemptNumber = 1,
    requestedVariant,
    requestedVariantIndex,
    initializeCounters = {},
    theme,
    prerender = false,
    stateVariableChanges: stateVariableChangesString,
    coreId,
    addNodesToResolver,
    replaceIndexResolutionsInResolver,
    deleteNodesFromResolver,
    resolvePath,
    calculateRootNames,
    updateRenderersCallback,
    reportScoreAndStateCallback,
    requestAnimationFrame,
    cancelAnimationFrame,
    copyToClipboard,
    sendEvent,
    requestSolutionView
  }) {
    this.coreId = coreId;
    this.activityId = activityId;
    this.docId = docId;
    this.attemptNumber = attemptNumber;
    this.doenetML = doenetML;
    this.allDoenetMLs = allDoenetMLs;
    this.serializedDocument = serializedDocument;
    this.nComponentsInit = nComponentsInit;
    this.createComponentIdxMapping = {};
    this.addNodesToResolver = addNodesToResolver;
    this.replaceIndexResolutionsInResolver = replaceIndexResolutionsInResolver;
    this.deleteNodesFromResolver = deleteNodesFromResolver;
    this.resolvePath = resolvePath;
    this.calculateRootNames = calculateRootNames;
    this.rootNames = this.calculateRootNames?.().names;
    this.updateRenderersCallback = updateRenderersCallback;
    this.reportScoreAndStateCallback = reportScoreAndStateCallback;
    this.requestAnimationFrame = requestAnimationFrame;
    this.cancelAnimationFrame = cancelAnimationFrame;
    this.copyToClipboard = copyToClipboard;
    this.sendEvent = sendEvent;
    this.requestSolutionViewCallback = requestSolutionView;
    this.cid = cid;
    this.diagnosticsManager = new DiagnosticsManager({
      preliminaryDiagnostics
    });
    this.numerics = new Numerics();
    this.flags = flags;
    this.theme = theme;
    this.getStateVariableValue = this.getStateVariableValue.bind(this);
    this.componentInfoObjects = componentInfoObjects;
    this.initializeCounters = initializeCounters;
    const stateVariableChanges = stateVariableChangesString ? JSON.parse(
      stateVariableChangesString,
      serializedComponentsReviver
    ) : {};
    this.receivedStateVariableChanges = Boolean(stateVariableChangesString);
    this.coreFunctions = {
      requestUpdate: this.requestUpdate.bind(this),
      performUpdate: this.performUpdate.bind(this),
      requestAction: this.requestAction.bind(this),
      performAction: this.performAction.bind(this),
      triggerChainedActions: this.triggerChainedActions.bind(this),
      updateRenderers: this.updateRenderers.bind(this),
      requestRecordEvent: this.requestRecordEvent.bind(this),
      requestAnimationFrame: this.requestAnimationFrame.bind(this),
      cancelAnimationFrame: this.cancelAnimationFrame.bind(this),
      requestSolutionView: this.requestSolutionView.bind(this),
      requestComponentDoenetML: this.requestComponentDoenetML.bind(this),
      copyToClipboard: this.copyToClipboard.bind(this),
      navigateToTarget: (args) => navigateToTarget({ core: this, args }),
      // State-variable runtime plumbing, not component-facing API:
      // the shared state-variable functions in StateVariableInitializer
      // reach core through `svComponent.coreFunctions`.
      getStateVariableValue: this.getStateVariableValue,
      // bound above
      addDiagnostic: this.addDiagnostic.bind(this)
    };
    this.updateInfo = {
      componentsToUpdateRenderers: /* @__PURE__ */ new Set(),
      compositesToExpand: /* @__PURE__ */ new Set(),
      compositesToUpdateReplacements: /* @__PURE__ */ new Set(),
      inactiveCompositesToUpdateReplacements: /* @__PURE__ */ new Set(),
      componentsToUpdateActionChaining: {},
      unresolvedDependencies: {},
      unresolvedByDependent: {},
      deletedStateVariables: {},
      deletedComponents: {},
      compositesBeingExpanded: [],
      // `stateVariableUpdatesForMissingComponents` is the set of
      // updates whose target component does not yet exist (e.g. it
      // hasn't been created by a composite). The original
      // `JSON.stringify`/`JSON.parse` round-trip with the
      // replacer/reviver pair is preserved verbatim — `deepClone`
      // would not invoke the replacer.
      stateVariableUpdatesForMissingComponents: JSON.parse(
        JSON.stringify(
          stateVariableChanges,
          serializedComponentsReplacer
        ),
        serializedComponentsReviver
      ),
      stateVariablesToEvaluate: []
    };
    this.cumulativeStateVariableChanges = JSON.parse(
      JSON.stringify(stateVariableChanges, serializedComponentsReplacer),
      serializedComponentsReviver
    );
    this.requestedVariantIndex = requestedVariantIndex;
    this.requestedVariant = requestedVariant;
    this.visibilityTracker = new VisibilityTracker({ core: this });
    this.autoSubmitManager = new AutoSubmitManager({ core: this });
    this.rendererInstructionBuilder = new RendererInstructionBuilder({
      core: this
    });
    this.processQueue = new ProcessQueue({ core: this });
    this.actionTriggerScheduler = new ActionTriggerScheduler({
      core: this
    });
    this.stateVariableEvaluator = new StateVariableEvaluator({
      core: this
    });
    this.stalenessPropagator = new StalenessPropagator({ core: this });
    this.essentialValueWriter = new EssentialValueWriter({ core: this });
    this.compositeReplacementUpdater = new CompositeReplacementUpdater({
      core: this
    });
    this.updateExecutor = new UpdateExecutor({ core: this });
    this.statePersistence = new StatePersistence({ core: this });
    this.parameterStack = new ParameterStack();
    this.parameterStack.parameters.rngClass = seedrandom.alea;
    this.parameterStack.parameters.prerender = prerender;
  }
  /**
   * Construct core from the DoenetML in order to generate the Dast that can be used
   * to render the document.
   *
   * Creates all components and resolves all dependencies necessary to determine
   * state variables that are marked `forRenderer`.
   *
   * The dast is communicated back to the renderer via calls to `updateRenderersCallback`.
   */
  async generateDast() {
    this.doenetMLNewlines = findAllNewlines(this.allDoenetMLs[0]);
    let serializedComponents = [deepClone(this.serializedDocument)];
    this.serializedDocument = void 0;
    numberAnswers(serializedComponents, this.componentInfoObjects);
    this.documentIdx = serializedComponents[0].componentIdx;
    if (!serializedComponents[0].state) {
      serializedComponents[0].state = {};
    }
    serializedComponents[0].state.theme = this.theme;
    serializedComponents[0].doenetAttributes.cid = this.cid;
    this._components = [];
    this.componentIdxByStateId = {};
    this._components[this.nComponentsInit - 1] = void 0;
    this.errorComponentsToAdd = [];
    this.actionTriggerScheduler.reset();
    this.essentialValuesSavedInDefinition = {};
    this.statePersistence.reset();
    this.rendererInstructionBuilder.reset();
    this.rendererVariablesByComponentType = {};
    for (let componentType in this.componentInfoObjects.allComponentClasses) {
      Object.defineProperty(
        this.rendererVariablesByComponentType,
        componentType,
        {
          get: (function() {
            let varDescriptions = this.componentInfoObjects.allComponentClasses[componentType].returnStateVariableInfo({
              onlyForRenderer: true
            }).stateVariableDescriptions;
            delete this.rendererVariablesByComponentType[componentType];
            return this.rendererVariablesByComponentType[componentType] = varDescriptions;
          }).bind(this),
          configurable: true
        }
      );
    }
    this.processQueue.reset();
    this.dependencies = new DependencyHandler({
      _components: this._components,
      componentInfoObjects: this.componentInfoObjects,
      core: this
    });
    this.unmatchedChildren = {};
    let numVariants = getNumVariants({
      serializedComponent: serializedComponents[0],
      componentInfoObjects: this.componentInfoObjects
    }).numVariants;
    if (!this.requestedVariant) {
      this.requestedVariantIndex = ((this.requestedVariantIndex - 1) % numVariants + numVariants) % numVariants + 1;
      if (serializedComponents[0].variants.uniqueVariants) {
        let docClass = this.componentInfoObjects.allComponentClasses[serializedComponents[0].componentType];
        let result = docClass.getUniqueVariant({
          serializedComponent: serializedComponents[0],
          variantIndex: this.requestedVariantIndex,
          componentInfoObjects: this.componentInfoObjects
        });
        if (result.success) {
          this.requestedVariant = result.desiredVariant;
        }
      }
      if (!this.requestedVariant) {
        this.requestedVariant = { index: this.requestedVariantIndex };
      }
    }
    this.parameterStack.parameters.variant = this.requestedVariant;
    serializedComponents[0].variants.desiredVariant = this.parameterStack.parameters.variant;
    await addComponents({
      core: this,
      serializedComponents,
      initialAdd: true
    });
    this.updateInfo.componentsToUpdateRenderers.clear();
    await this.document.stateValues.componentCreditAchieved;
    this.canonicalGeneratedVariantString = JSON.stringify(
      await this.document.stateValues.generatedVariantInfo,
      serializedComponentsReplacer
    );
    this.canonicalDocVariantStrings = (await this.document.stateValues.docVariantInfo).map((x2) => JSON.stringify(x2, serializedComponentsReplacer));
    const coreInfo = {
      generatedVariantString: this.canonicalGeneratedVariantString,
      allPossibleVariants: deepClone(
        await this.document.sharedParameters.allPossibleVariants
      ),
      rendererTypesInDocument: deepClone(this.rendererTypesInDocument),
      documentToRender: this.documentRendererInstructions
    };
    this.coreInfoString = JSON.stringify(
      coreInfo,
      serializedComponentsReplacer
    );
    if (this.cumulativeStateVariableChanges.__componentNeedingUpdateValue) {
      await this.performAction({
        actionName: "updateValue",
        componentIdx: this.componentIdxByStateId[this.cumulativeStateVariableChanges.__componentNeedingUpdateValue],
        args: { doNotIgnore: true }
      });
    }
    if (!this.receivedStateVariableChanges) {
      this.saveState().catch(
        reportTimerError(TimerLabels.generateDastSaveState)
      );
    }
    this.dependencies.clearCircularCheckMemos();
    const returnResult = {
      coreInfo
    };
    if (Object.keys(this.unmatchedChildren).length > 0) {
      for (const componentIdxStr in this.unmatchedChildren) {
        let parent = this._components[Number(componentIdxStr)];
        this.addDiagnostic({
          type: "warning",
          message: this.unmatchedChildren[Number(componentIdxStr)].message,
          position: parent.position,
          sourceDoc: parent.sourceDoc
        });
      }
    }
    let diagnostics = void 0;
    if (this.hasPendingDiagnostics) {
      diagnostics = this.getDiagnostics().diagnostics;
    }
    return { ...returnResult, diagnostics };
  }
  async onDocumentFirstVisible() {
    this.requestRecordEvent({
      verb: "experienced",
      object: {
        componentIdx: this.document.componentIdx,
        componentType: "document"
      }
    });
    await this.document.stateValues.scoredDescendants;
    setTimeout(() => {
      this.sendVisibilityChangedEvents()?.catch(
        reportTimerError(TimerLabels.firstVisibleSend)
      );
    }, this.visibilityInfo.saveDelay);
  }
  // → rendererInstructionBuilder
  get componentsToRender() {
    return this.rendererInstructionBuilder.componentsToRender;
  }
  get componentsWithChangedChildrenToRender() {
    return this.rendererInstructionBuilder.componentsWithChangedChildrenToRender;
  }
  get rendererState() {
    return this.rendererInstructionBuilder.rendererState;
  }
  callUpdateRenderers(args, init = false) {
    return this.rendererInstructionBuilder.callUpdateRenderers(args, init);
  }
  // → diagnosticsManager
  get diagnostics() {
    return this.diagnosticsManager.diagnostics;
  }
  get hasPendingDiagnostics() {
    return this.diagnosticsManager.hasPendingDiagnostics;
  }
  getDiagnostics() {
    return this.diagnosticsManager.getDiagnostics();
  }
  addDiagnostic(diagnostic) {
    if (diagnostic?.type === "accessibility" && diagnostic.position) {
      const source = this.allDoenetMLs[diagnostic.sourceDoc ?? 0];
      const narrowed = narrowPositionToOpeningTag(
        diagnostic.position,
        source
      );
      if (narrowed !== diagnostic.position) {
        diagnostic = { ...diagnostic, position: narrowed };
      }
    }
    return this.diagnosticsManager.addDiagnostic(diagnostic);
  }
  getSourceLocationForComponent(component) {
    return getSourceLocationForComponent(component, this._components);
  }
  async updateRendererInstructions(args) {
    return this.rendererInstructionBuilder.updateRendererInstructions(args);
  }
  async initializeRenderedComponentInstruction(component, componentsWithChangedChildrenToRenderInProgress) {
    return this.rendererInstructionBuilder.initializeRenderedComponentInstruction(
      component,
      componentsWithChangedChildrenToRenderInProgress
    );
  }
  getRendererId(component) {
    return this.rendererInstructionBuilder.getRendererId(component);
  }
  deleteFromComponentsToRender(args) {
    return this.rendererInstructionBuilder.deleteFromComponentsToRender(
      args
    );
  }
  // → actionTriggerScheduler
  get stateVariableChangeTriggers() {
    return this.actionTriggerScheduler.stateVariableChangeTriggers;
  }
  get actionsChangedToActions() {
    return this.actionTriggerScheduler.actionsChangedToActions;
  }
  get originsOfActionsChangedToActions() {
    return this.actionTriggerScheduler.originsOfActionsChangedToActions;
  }
  async processStateVariableTriggers(updateRenderersIfTriggered = false) {
    return this.actionTriggerScheduler.processStateVariableTriggers(
      updateRenderersIfTriggered
    );
  }
  recordStateVariablesMustEvaluate(componentIdx) {
    return this.actionTriggerScheduler.recordStateVariablesMustEvaluate(
      componentIdx
    );
  }
  async checkForActionChaining(args) {
    return this.actionTriggerScheduler.checkForActionChaining(args);
  }
  // → stateVariableEvaluator
  async getStateVariableValue(args) {
    return this.stateVariableEvaluator.getStateVariableValue(args);
  }
  async getStateVariableDefinitionArguments(args) {
    return this.stateVariableEvaluator.getStateVariableDefinitionArguments(
      args
    );
  }
  async recordActualChangeInStateVariable(args) {
    return this.stateVariableEvaluator.recordActualChangeInStateVariable(
      args
    );
  }
  findCaseInsensitiveMatches({
    stateVariables,
    componentClass
  }) {
    return findCaseInsensitiveMatches({
      stateVariables,
      componentClass,
      componentInfoObjects: this.componentInfoObjects
    });
  }
  matchPublicStateVariables({
    stateVariables,
    componentClass
  }) {
    return matchPublicStateVariables({
      stateVariables,
      componentClass,
      componentInfoObjects: this.componentInfoObjects
    });
  }
  substituteAliases({
    stateVariables,
    componentClass
  }) {
    return substituteAliases({
      stateVariables,
      componentClass,
      componentInfoObjects: this.componentInfoObjects
    });
  }
  publicCaseInsensitiveAliasSubstitutions({
    stateVariables,
    componentClass
  }) {
    return publicCaseInsensitiveAliasSubstitutions({
      stateVariables,
      componentClass,
      componentInfoObjects: this.componentInfoObjects
    });
  }
  checkIfArrayEntry({
    stateVariable,
    component
  }) {
    return checkIfArrayEntry({ stateVariable, component });
  }
  // → stalenessPropagator
  async createFromArrayEntry(args) {
    return this.stalenessPropagator.createFromArrayEntry(args);
  }
  async markDescendantsToUpdateRenderers(component) {
    return this.stalenessPropagator.markDescendantsToUpdateRenderers(
      component
    );
  }
  async markStateVariableAndUpstreamDependentsStale(args) {
    return this.stalenessPropagator.markStateVariableAndUpstreamDependentsStale(
      args
    );
  }
  async lookUpCurrentFreshness(args) {
    return this.stalenessPropagator.lookUpCurrentFreshness(args);
  }
  async processMarkStale(args) {
    return this.stalenessPropagator.processMarkStale(args);
  }
  async markUpstreamDependentsStale(args) {
    return this.stalenessPropagator.markUpstreamDependentsStale(args);
  }
  // → compositeReplacementUpdater
  async updateCompositeReplacements(args) {
    return this.compositeReplacementUpdater.updateCompositeReplacements(
      args
    );
  }
  async setErrorReplacements(args) {
    return this.compositeReplacementUpdater.setErrorReplacements(args);
  }
  async deleteReplacementsFromShadowsThenComposite(args) {
    return this.compositeReplacementUpdater.deleteReplacementsFromShadowsThenComposite(
      args
    );
  }
  async createShadowedReplacements(args) {
    return this.compositeReplacementUpdater.createShadowedReplacements(
      args
    );
  }
  async adjustReplacementsToWithhold(args) {
    return this.compositeReplacementUpdater.adjustReplacementsToWithhold(
      args
    );
  }
  get rendererTypesInDocument() {
    return this.document.allPotentialRendererTypes;
  }
  get components() {
    return this._components;
  }
  // → processQueue
  requestAction(args) {
    return this.processQueue.requestAction(args);
  }
  async requestUpdate(args) {
    return this.processQueue.requestUpdate(args);
  }
  requestRecordEvent(event) {
    return this.processQueue.requestRecordEvent(event);
  }
  // → updateExecutor
  async performAction(args) {
    return this.updateExecutor.performAction(args);
  }
  async performUpdate(args) {
    return this.updateExecutor.performUpdate(args);
  }
  // → actionTriggerScheduler (cont.)
  async triggerChainedActions(args) {
    return this.actionTriggerScheduler.triggerChainedActions(args);
  }
  // → rendererInstructionBuilder (cont.) — `updateRenderers` is the
  // public entry point that wraps `updateAllChangedRenderers`.
  async updateRenderers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!skipRendererUpdate) {
      await this.updateAllChangedRenderers(sourceInformation, actionId);
    }
  }
  async updateAllChangedRenderers(sourceInformation = {}, actionId) {
    return this.rendererInstructionBuilder.updateAllChangedRenderers(
      sourceInformation,
      actionId
    );
  }
  // Event emission — assembles the event payload and dispatches it
  // through the host-provided `sendEvent`.
  async performRecordEvent({ event }) {
    if (!this.flags.allowSaveEvents) {
      return;
    }
    if (!event.result) {
      event.result = {};
    }
    if (!event.context) {
      event.context = {};
    }
    const payload = {
      activityId: this.activityId,
      cid: this.cid,
      docId: this.docId,
      attemptNumber: this.attemptNumber,
      variantIndex: this.requestedVariant.index,
      verb: event.verb,
      object: JSON.stringify(event.object, serializedComponentsReplacer),
      result: JSON.stringify(
        removeFunctionsMathExpressionClass(event.result),
        serializedComponentsReplacer
      ),
      context: JSON.stringify(
        event.context,
        serializedComponentsReplacer
      ),
      timestamp: (/* @__PURE__ */ new Date()).toISOString().slice(0, 19).replace("T", " "),
      version: "0.1.1"
    };
    this.sendEvent(payload);
  }
  // → visibilityTracker
  get visibilityInfo() {
    return this.visibilityTracker.info;
  }
  processVisibilityChangedEvent(event) {
    return this.visibilityTracker.processVisibilityChangedEvent(event);
  }
  sendVisibilityChangedEvents() {
    return this.visibilityTracker.sendVisibilityChangedEvents();
  }
  async suspendVisibilityMeasuring() {
    return this.visibilityTracker.suspendVisibilityMeasuring();
  }
  resumeVisibilityMeasuring() {
    return this.visibilityTracker.resumeVisibilityMeasuring();
  }
  async saveImmediately() {
    return this.statePersistence.saveImmediately();
  }
  /**
   * Flush-state-on-demand (Doenet/DoenetML#1440): wait briefly for
   * in-flight action processing to settle (the same bounded wait
   * `terminate` uses), then push any pending state through the normal
   * `reportScoreAndState` pipeline so a persistence host saves it. Returns
   * whether the viewer held any state. Hosts use this to unmount the
   * document losslessly.
   */
  async flushState() {
    if (this.processQueue.processing) {
      const pause100 = () => new Promise((resolve) => setTimeout(resolve, 100));
      for (let i2 = 0; i2 < 10; i2++) {
        await pause100();
        if (!this.processQueue.processing) {
          break;
        }
      }
    }
    return this.statePersistence.flushState();
  }
  async saveState(overrideThrottle = false, onSubmission = false) {
    return this.statePersistence.saveState(overrideThrottle, onSubmission);
  }
  async saveChangesToDatabase(overrideThrottle) {
    return this.statePersistence.saveChangesToDatabase(overrideThrottle);
  }
  // → essentialValueWriter
  async executeUpdateStateVariables(newStateVariableValues) {
    return this.essentialValueWriter.executeUpdateStateVariables(
      newStateVariableValues
    );
  }
  async replacementChangesFromCompositesToUpdate() {
    return this.essentialValueWriter.replacementChangesFromCompositesToUpdate();
  }
  async processNewStateVariableValues(newStateVariableValues, newComponent = false) {
    return this.essentialValueWriter.processNewStateVariableValues(
      newStateVariableValues,
      newComponent
    );
  }
  async requestComponentChanges(args) {
    return this.essentialValueWriter.requestComponentChanges(args);
  }
  /**
   * Poll `requestSolutionViewCallback` to determine whether the user is
   * allowed to view the solution. Only fires when `solutionDisplayMode`
   * is set to "buttonRequirePermission".
   */
  async requestSolutionView(componentIdx) {
    const requestResult = await this.requestSolutionViewCallback(componentIdx);
    return {
      allowView: requestResult.allowView
    };
  }
  get scoredComponentWeights() {
    return (async () => (await this.document.stateValues.scoredDescendants).map(
      (x2) => x2.stateValues.weight
    ))();
  }
  handleVisibilityChange(documentIsVisible) {
    if (documentIsVisible) {
      this.resumeVisibilityMeasuring();
    } else {
      this.suspendVisibilityMeasuring();
    }
  }
  async terminate() {
    let pause100 = function() {
      return new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
    };
    await this.suspendVisibilityMeasuring();
    await this.autoSubmitManager.flush();
    this.processQueue.stopProcessingRequests = true;
    if (this.processQueue.processing) {
      for (let i2 = 0; i2 < 10; i2++) {
        await pause100();
        if (!this.processQueue.processing) {
          break;
        }
      }
    }
    await this.saveImmediately();
  }
  // → autoSubmitManager
  recordAnswerToAutoSubmit(componentIdx) {
    this.autoSubmitManager.recordAnswer(componentIdx);
  }
  async autoSubmitAnswers() {
    return this.autoSubmitManager.submitNow();
  }
  requestComponentDoenetML(componentIdx, displayOnlyChildren) {
    let component = this.components[componentIdx];
    if (!component) {
      return null;
    }
    let position = component.position;
    if (!position) {
      return null;
    }
    let sourceDoc = component.sourceDoc ?? 0;
    let startInd, endInd;
    if (displayOnlyChildren) {
      if (!component.childrenPosition) {
        return "";
      }
      startInd = component.childrenPosition.start.offset;
      endInd = component.childrenPosition.end.offset;
    } else {
      startInd = position.start.offset;
      endInd = position.end.offset;
    }
    let componentDoenetML = this.allDoenetMLs[sourceDoc].slice(
      startInd,
      endInd
    );
    if (displayOnlyChildren) {
      if (componentDoenetML[0] === "\n") {
        componentDoenetML = componentDoenetML.slice(1);
      }
      componentDoenetML = componentDoenetML.replace(
        /\n[ \t]*$(?!\n)/,
        ""
      );
    }
    let lines = componentDoenetML.split("\n");
    let minSpaces = lines.slice(1).reduce(
      (a2, c2) => Math.min(
        a2,
        c2.trim().length > 1 ? c2.search(/\S|$/) : Infinity
      ),
      Infinity
    );
    if (minSpaces === Infinity && lines[0].trim().length > 1) {
      minSpaces = lines[0].search(/\S|$/);
    }
    if (Number.isFinite(minSpaces) && minSpaces > 0) {
      lines = lines.map((s2) => {
        let nStrip = Math.min(minSpaces, s2.search(/\S|$/));
        return s2.slice(nStrip);
      });
      componentDoenetML = lines.join("\n");
    }
    return componentDoenetML;
  }
}
function returnAnchorAttributes() {
  return {
    anchor: {
      createComponentOfType: "point",
      groupName: "positioning",
      description: "Coordinates of the anchor point used to position this component on a graph."
    },
    positionFromAnchor: {
      createComponentOfType: "text",
      createStateVariable: "positionFromAnchor",
      defaultValue: "center",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      isLocation: true,
      groupName: "positioning",
      validValues: [
        {
          value: "upperRight",
          description: "Place the component above and to the right of the anchor point."
        },
        {
          value: "upperLeft",
          description: "Place the component above and to the left of the anchor point."
        },
        {
          value: "lowerRight",
          description: "Place the component below and to the right of the anchor point."
        },
        {
          value: "lowerLeft",
          description: "Place the component below and to the left of the anchor point."
        },
        {
          value: "top",
          description: "Place the component directly above the anchor point."
        },
        {
          value: "bottom",
          description: "Place the component directly below the anchor point."
        },
        {
          value: "left",
          description: "Place the component directly to the left of the anchor point."
        },
        {
          value: "right",
          description: "Place the component directly to the right of the anchor point."
        },
        {
          value: "center",
          description: "Center the component on the anchor point."
        }
      ],
      description: "Where this component sits relative to its anchor point."
    }
  };
}
function returnGraphControlOrderAttribute() {
  return {
    createComponentOfType: "integer",
    createStateVariable: "controlOrder",
    defaultValue: 0,
    clamp: [0, Infinity],
    public: true,
    forRenderer: true,
    description: "Slot order for graph controls (1-indexed; 0 means no explicit slot request)."
  };
}
function returnAnchorStateVariableDefinition() {
  return {
    anchor: {
      description: "The coordinates where this component is anchored on the graph.",
      defaultValue: me$3.fromText("(0,0)").tuples_to_vectors(),
      public: true,
      forRenderer: true,
      hasEssential: true,
      isLocation: true,
      groupName: "positioning",
      shadowingInstructions: {
        createComponentOfType: "point"
      },
      returnDependencies: () => ({
        anchorAttr: {
          dependencyType: "attributeComponent",
          attributeName: "anchor",
          variableNames: ["coords"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.anchorAttr) {
          return {
            setValue: {
              anchor: dependencyValues.anchorAttr.stateValues.coords
            }
          };
        } else {
          return { useEssentialOrDefaultValue: { anchor: true } };
        }
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues,
        initialChange
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        if (dependencyValues.anchorAttr) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "anchorAttr",
                desiredValue: desiredStateVariableValues.anchor,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "anchor",
                value: desiredStateVariableValues.anchor
              }
            ]
          };
        }
      }
    }
  };
}
async function moveGraphicalObjectWithAnchorAction({
  x: x2,
  y: y2,
  z: z2,
  transient,
  actionId,
  sourceInformation = {},
  skipRendererUpdate = false,
  componentIdx,
  componentType,
  coreFunctions
}) {
  let components = ["vector"];
  if (x2 !== void 0) {
    components[1] = x2;
  }
  if (y2 !== void 0) {
    components[2] = y2;
  }
  if (z2 !== void 0) {
    components[3] = z2;
  }
  if (transient) {
    return await coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx,
          stateVariable: "anchor",
          value: me$3.fromAst(components)
        }
      ],
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  } else {
    return await coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx,
          stateVariable: "anchor",
          value: me$3.fromAst(components)
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx,
          componentType
        },
        result: {
          x: x2,
          y: y2,
          z: z2
        }
      }
    });
  }
}
function textFromComponent(component) {
  if (typeof component !== "object") {
    return component.toString();
  } else if (component.stateValues.hidden) {
    return "";
  } else if (typeof component.stateValues.text === "string") {
    return component.stateValues.text;
  } else {
    return " ";
  }
}
function textFromChildren(children, textFromComponentConverter = textFromComponent) {
  let result = textFromChildrenSub({
    compositeReplacementRange: children.compositeReplacementRange,
    children,
    startInd: 0,
    endInd: children.length - 1,
    textFromComponentConverter
  });
  return result.newChildren.map(textFromComponentConverter).join("");
}
function textFromChildrenSub({
  compositeReplacementRange,
  children,
  startInd,
  endInd,
  textFromComponentConverter,
  potentialListComponents
}) {
  let newChildren = [];
  let newPotentialListComponents = [];
  let lastChildInd = startInd - 1;
  let lastChildIndIncludingEmptyComposites = lastChildInd;
  for (let rangeInd = 0; rangeInd < compositeReplacementRange.length; rangeInd++) {
    let range = compositeReplacementRange[rangeInd];
    let rangeFirstInd = range.firstInd;
    let rangeLastInd = range.lastInd;
    if (rangeFirstInd > lastChildInd && rangeLastInd <= endInd) {
      if (lastChildInd + 1 < rangeFirstInd) {
        newChildren.push(
          ...children.slice(lastChildInd + 1, rangeFirstInd)
        );
        if (potentialListComponents) {
          newPotentialListComponents.push(
            ...potentialListComponents.slice(
              lastChildInd - startInd + 1,
              rangeFirstInd - startInd
            )
          );
        }
      }
      if (!range.hidden) {
        let subReplacementRange = compositeReplacementRange.slice(
          rangeInd + 1
        );
        let {
          newChildren: childrenInRange,
          newPotentialListComponents: potentialListComponentsInRange
        } = textFromChildrenSub({
          compositeReplacementRange: subReplacementRange,
          children,
          startInd: rangeFirstInd,
          endInd: rangeLastInd,
          textFromComponentConverter,
          potentialListComponents: range.potentialListComponents
        });
        let allAreListComponents = potentialListComponentsInRange.every(
          (x2) => x2
        );
        if (range.asList && allAreListComponents && childrenInRange.length > 1) {
          newChildren.push(
            childrenInRange.map(textFromComponentConverter).filter((v2) => v2 !== "").map(
              (v2, i2, a2) => i2 === a2.length - 1 ? v2 : v2.trimEnd()
            ).join(", ")
          );
        } else {
          newChildren.push(
            childrenInRange.map(textFromComponentConverter).join("")
          );
        }
        if (potentialListComponents) {
          newPotentialListComponents.push(allAreListComponents);
        }
      }
      lastChildInd = rangeLastInd;
      lastChildIndIncludingEmptyComposites = Math.max(
        rangeFirstInd,
        rangeLastInd
      );
    }
  }
  if (lastChildInd < endInd) {
    newChildren.push(...children.slice(lastChildInd + 1, endInd + 1));
    if (potentialListComponents) {
      newPotentialListComponents.push(
        ...potentialListComponents.slice(
          lastChildIndIncludingEmptyComposites - startInd + 1,
          endInd - startInd + 1
        )
      );
    }
  }
  return { newChildren, newPotentialListComponents };
}
function returnTextPieceStateVariableDefinitions() {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.numWords = {
    description: "The number of words in the text value.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    returnDependencies: () => ({
      value: {
        dependencyType: "stateVariable",
        variableName: "value"
      }
    }),
    definition({
      dependencyValues
    }) {
      return {
        setValue: {
          numWords: dependencyValues.value.trim().split(/\s+/).length
        }
      };
    }
  };
  stateVariableDefinitions.words = {
    description: "The text value split into words.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "text"
    },
    isArray: true,
    entryPrefixes: ["word"],
    returnArraySizeDependencies: () => ({
      numWords: {
        dependencyType: "stateVariable",
        variableName: "numWords"
      }
    }),
    returnArraySize({
      dependencyValues
    }) {
      return [dependencyValues.numWords];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({
      globalDependencyValues
    }) {
      return {
        setValue: {
          words: globalDependencyValues.value.trim().split(/\s+/)
        }
      };
    }
  };
  stateVariableDefinitions.numCharacters = {
    description: "The number of characters (graphemes) in the text value.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    returnDependencies: () => ({
      value: {
        dependencyType: "stateVariable",
        variableName: "value"
      }
    }),
    definition({
      dependencyValues
    }) {
      const itr = new Intl.Segmenter("en", {
        granularity: "grapheme"
      }).segment(dependencyValues.value);
      return {
        setValue: { numCharacters: [...itr].length }
      };
    }
  };
  stateVariableDefinitions.characters = {
    description: "The text value split into characters (graphemes).",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "text"
    },
    isArray: true,
    entryPrefixes: ["character"],
    returnArraySizeDependencies: () => ({
      numCharacters: {
        dependencyType: "stateVariable",
        variableName: "numCharacters"
      }
    }),
    returnArraySize({
      dependencyValues
    }) {
      return [dependencyValues.numCharacters];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({
      globalDependencyValues
    }) {
      const itr = new Intl.Segmenter("en", {
        granularity: "grapheme"
      }).segment(globalDependencyValues.value);
      return {
        setValue: {
          characters: Array.from(itr, ({ segment }) => segment)
        }
      };
    }
  };
  stateVariableDefinitions.numListItems = {
    description: "The number of comma-separated items when the text is interpreted as a list.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    returnDependencies: () => ({
      value: {
        dependencyType: "stateVariable",
        variableName: "value"
      }
    }),
    definition({
      dependencyValues
    }) {
      return {
        setValue: {
          numListItems: dependencyValues.value.trim().split(",").length
        }
      };
    }
  };
  stateVariableDefinitions.list = {
    description: "The text value split into items at commas.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "text"
    },
    isArray: true,
    entryPrefixes: ["listItem"],
    returnArraySizeDependencies: () => ({
      numListItems: {
        dependencyType: "stateVariable",
        variableName: "numListItems"
      }
    }),
    returnArraySize({
      dependencyValues
    }) {
      return [dependencyValues.numListItems];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({
      globalDependencyValues
    }) {
      return {
        setValue: {
          list: globalDependencyValues.value.trim().split(",").map((s2) => s2.trim())
        }
      };
    }
  };
  return stateVariableDefinitions;
}
function createStateProxyHandler() {
  return {
    get: function(obj, prop) {
      let result = obj[prop];
      if (result !== void 0) {
        if (result.isArray && (result.public || result.trackChanges)) {
          result = new Proxy(
            result.value,
            createArrayProxyHandler({
              variable: prop,
              numDimensions: result.numDimensions
            })
          );
        } else {
          result = result.value;
        }
      }
      return result;
    },
    set: function(obj, prop, value) {
      throw Error("Property " + prop + " is read-only");
    },
    deleteProperty: function(obj, prop) {
      throw Error("Property " + prop + " is read-only");
    }
  };
}
function createArrayProxyHandler({
  variable,
  numDimensions,
  indicesSoFar = []
}) {
  return {
    variable,
    numDimensions,
    indicesSoFar,
    get: function(obj, index2) {
      if (index2 === "then" && obj instanceof Promise) {
        return (f2) => obj.then.bind(obj)(
          (x2) => f2(
            x2 !== null && typeof x2 === "object" ? new Proxy(
              x2,
              createArrayProxyHandler({
                variable: this.variable,
                numDimensions: this.numDimensions,
                indicesSoFar: this.indicesSoFar
              })
            ) : x2
          )
        );
      }
      let result = obj[index2];
      if (result !== null && typeof result === "object" && numDimensions > 1) {
        result = new Proxy(
          result,
          createArrayProxyHandler({
            variable: this.variable,
            numDimensions: this.numDimensions - 1,
            indicesSoFar: [...this.indicesSoFar, index2]
          })
        );
      }
      return result;
    },
    set: function(obj, prop, value) {
      throw Error("Property " + prop + " is read-only");
    },
    deleteProperty: function(obj, prop) {
      throw Error("Property " + prop + " is read-only");
    }
  };
}
const SERIALIZE_ENCOUNTERED_COMPONENT_PREFIX = "Encountered ";
class BaseComponent {
  constructor({
    componentIdx,
    stateId,
    rootName,
    ancestors,
    serializedComponent,
    definingChildren,
    serializedChildren,
    attributes,
    stateVariableDefinitions,
    componentInfoObjects,
    coreFunctions,
    flags,
    shadow,
    numerics,
    parentSharedParameters,
    sharedParameters,
    refResolution
  }) {
    this.numerics = numerics;
    this.parentSharedParameters = parentSharedParameters;
    this.sharedParameters = sharedParameters;
    this.componentIdx = componentIdx;
    this.stateId = stateId;
    this.rootName = rootName;
    this.ancestors = ancestors;
    this.counters = {};
    this.componentInfoObjects = componentInfoObjects;
    this.coreFunctions = coreFunctions;
    this.flags = flags;
    if (shadow === true) {
      this.isShadow = true;
    }
    this.definingChildren = definingChildren;
    if (this.definingChildren === void 0) {
      this.definingChildren = [];
    }
    this.serializedChildren = serializedChildren;
    this.attributes = attributes;
    if (refResolution) {
      this.refResolution = refResolution;
    }
    this.state = {};
    if (stateVariableDefinitions[SHARED_STATE_VARIABLE_DEFINITIONS]) {
      for (let stateVariable in stateVariableDefinitions) {
        this.state[stateVariable] = Object.create(
          stateVariableDefinitions[stateVariable]
        );
      }
    } else {
      for (let stateVariable in stateVariableDefinitions) {
        this.state[stateVariable] = stateVariableDefinitions[stateVariable];
      }
    }
    this.stateValues = new Proxy(this.state, createStateProxyHandler());
    this.essentialState = {};
    if (serializedComponent.state) {
      this.essentialState = deepClone(serializedComponent.state);
    }
    this.doenetAttributes = {};
    if (serializedComponent.doenetAttributes !== void 0) {
      Object.assign(
        this.doenetAttributes,
        serializedComponent.doenetAttributes
      );
    }
    if (serializedComponent.variants !== void 0) {
      this.variants = serializedComponent.variants;
    }
    if (serializedComponent.position) {
      this.position = serializedComponent.position;
    }
    if (serializedComponent.sourceDoc !== void 0) {
      this.sourceDoc = serializedComponent.sourceDoc;
    }
    if (serializedComponent.childrenPosition) {
      this.childrenPosition = serializedComponent.childrenPosition;
    }
    if (serializedComponent.answerNumber) {
      this.answerNumber = serializedComponent.answerNumber;
    }
    this.actions = {
      copyDoenetMLToClipboard: this.copyDoenetMLToClipboard.bind(this)
    };
  }
  static componentType = "_base";
  static get rendererType() {
    return this.componentType;
  }
  get componentType() {
    return this.constructor.componentType;
  }
  get componentOrAdaptedIdx() {
    if (this.adaptedFrom) {
      return this.adaptedFrom.componentIdx;
    } else {
      return this.componentIdx;
    }
  }
  get rendererType() {
    return this.constructor.rendererType;
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = ["_error"];
    if (this.rendererType) {
      allPotentialRendererTypes.push(this.rendererType);
    }
    for (let varName in this.state) {
      let stateVarObj = this.state[varName];
      if (stateVarObj.public) {
        let componentTypes = stateVarObj.componentType;
        if (!Array.isArray(componentTypes)) {
          componentTypes = [componentTypes];
        }
        if (stateVarObj.wrappingComponents) {
          componentTypes.push(
            ...flattenDeep(stateVarObj.wrappingComponents).map(
              (x2) => typeof x2 === "object" ? x2.componentType : x2
            )
          );
        }
        for (let componentType of componentTypes) {
          let componentClass = this.componentInfoObjects.allComponentClasses[componentType];
          if (componentClass) {
            let rendererType = componentClass.rendererType;
            if (rendererType && !allPotentialRendererTypes.includes(rendererType)) {
              allPotentialRendererTypes.push(rendererType);
            }
          }
        }
      }
    }
    if (this.constructor.adapters) {
      for (let adapterInfo of this.constructor.adapters) {
        let componentType;
        if (typeof adapterInfo === "string") {
          componentType = adapterInfo;
        } else {
          componentType = adapterInfo.componentType;
        }
        let componentClass = this.componentInfoObjects.allComponentClasses[componentType];
        if (componentClass) {
          let rendererType = componentClass.rendererType;
          if (rendererType && !allPotentialRendererTypes.includes(rendererType)) {
            allPotentialRendererTypes.push(rendererType);
          }
        }
      }
    }
    if (!this.rendererType) {
      return allPotentialRendererTypes;
    }
    for (const childIdxStr in this.allChildren) {
      let child = this.allChildren[childIdxStr].component;
      if (typeof child !== "object") {
        continue;
      } else {
        for (let rendererType of child.allPotentialRendererTypes) {
          if (!allPotentialRendererTypes.includes(rendererType)) {
            allPotentialRendererTypes.push(rendererType);
          }
        }
      }
    }
    return allPotentialRendererTypes;
  }
  potentialRendererTypesFromSerializedComponents(serializedComponents) {
    let potentialRendererTypes = [];
    for (let comp of serializedComponents) {
      let compClass = this.componentInfoObjects.allComponentClasses[comp.componentType];
      if (compClass) {
        let rendererType = compClass.rendererType;
        if (rendererType && !potentialRendererTypes.includes(rendererType)) {
          potentialRendererTypes.push(rendererType);
        }
        let stateVariableDescriptions = compClass.returnStateVariableInfo({
          onlyPublic: true
        }).stateVariableDescriptions;
        for (let varName in stateVariableDescriptions) {
          let stateDescrip = stateVariableDescriptions[varName];
          let componentTypes = stateDescrip.shadowingInstructions?.createComponentOfType;
          if (!Array.isArray(componentTypes)) {
            componentTypes = [componentTypes];
          }
          if (stateDescrip.wrappingComponents) {
            componentTypes.push(
              ...flattenDeep(stateDescrip.wrappingComponents).map(
                (x2) => typeof x2 === "object" ? x2.componentType : x2
              )
            );
          }
          for (let componentType of componentTypes) {
            let componentClass = this.componentInfoObjects.allComponentClasses[componentType];
            if (componentClass) {
              let rendererType2 = componentClass.rendererType;
              if (rendererType2 && !potentialRendererTypes.includes(rendererType2)) {
                potentialRendererTypes.push(rendererType2);
              }
            }
          }
        }
        if (compClass.adapters) {
          for (let adapterInfo of compClass.adapters) {
            let componentType;
            if (typeof adapterInfo === "string") {
              componentType = adapterInfo;
            } else {
              componentType = adapterInfo.componentType;
            }
            let componentClass = this.componentInfoObjects.allComponentClasses[componentType];
            if (componentClass) {
              let rendererType2 = componentClass.rendererType;
              if (rendererType2 && !potentialRendererTypes.includes(rendererType2)) {
                potentialRendererTypes.push(rendererType2);
              }
            }
          }
        }
      }
      if (comp.children) {
        let childRenderTypes = this.potentialRendererTypesFromSerializedComponents(
          comp.children
        );
        for (let rendererType of childRenderTypes) {
          if (!potentialRendererTypes.includes(rendererType)) {
            potentialRendererTypes.push(rendererType);
          }
        }
      }
    }
    return potentialRendererTypes;
  }
  get childrenMatched() {
    return this.childrenMatchedWithPlaceholders && !this.placeholderActiveChildrenIndices;
  }
  get matchedCompositeChildren() {
    return this.matchedCompositeChildrenWithPlaceholders && !this.placeholderActiveChildrenIndices;
  }
  static createAttributesObject() {
    return {
      name: {
        createPrimitiveOfType: "string",
        description: "The name used to reference this component from elsewhere in the document."
      },
      hide: {
        createComponentOfType: "boolean",
        createStateVariable: "hide",
        defaultValue: false,
        public: true,
        description: "Whether to hide this component from the rendered output."
      },
      disabled: {
        createComponentOfType: "boolean",
        createStateVariable: "disabledPreliminary",
        defaultValue: false,
        description: "Whether this component is disabled and cannot be interacted with."
      },
      fixed: {
        createComponentOfType: "boolean",
        createStateVariable: "fixedPreliminary",
        defaultValue: false,
        ignoreFixed: true,
        description: "Whether this component's value is fixed and cannot be modified."
      },
      fixLocation: {
        createComponentOfType: "boolean",
        createStateVariable: "fixLocationPreliminary",
        defaultValue: false,
        description: "Whether this component's location is fixed (preventing it from being moved while still allowing other modifications)."
      },
      modifyIndirectly: {
        createComponentOfType: "boolean",
        createStateVariable: "modifyIndirectly",
        defaultValue: true,
        public: true,
        propagateToProps: true,
        excludeFromSchema: true,
        description: "Whether this component can be modified indirectly through a property reference."
      },
      styleNumber: {
        createComponentOfType: "integer",
        createStateVariable: "styleNumber",
        defaultValue: 1,
        public: true,
        fallBackToParentStateVariable: "styleNumber",
        fallBackToSourceCompositeStateVariable: "styleNumber",
        description: "The style number used to select this component's visual styling from the available style definitions."
      },
      isResponse: {
        createPrimitiveOfType: "boolean",
        createStateVariable: "isResponse",
        defaultValue: false,
        public: true,
        description: "Whether this component is treated as a response for the purposes of assessment."
      },
      isPotentialResponse: {
        createPrimitiveOfType: "boolean",
        createStateVariable: "isPotentialResponse",
        defaultValue: false,
        excludeFromSchema: true,
        description: "Whether this component is a candidate response that could be marked as a response."
      },
      permid: {
        createPrimitiveOfType: "string",
        createStateVariable: "permid",
        defaultValue: "",
        public: true,
        excludeFromSchema: true,
        description: "A persistent identifier for this component, stable across edits (currently unused but present for compatibility)."
      },
      // Adding `extend` and `copy` attributes so they are in the schema for all components.
      // These attributes are unused, as the `extend` and `copy` attributes are removed
      // from the dast when references are expanded.
      extend: {
        createReferences: true,
        description: "Extend another component by reference, inheriting its children and attributes. Enter a reference as `$name`."
      },
      copy: {
        createReferences: true,
        description: "Create an independent copy of another component by reference. Enter a references a `$name`."
      }
    };
  }
  static returnSugarInstructions() {
    return [];
  }
  static returnChildGroups() {
    return [
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static get childGroups() {
    if (this.hasOwnProperty("childGroupsData")) {
      return this.childGroupsData;
    } else {
      this.childGroupsData = this.returnChildGroups();
      return this.childGroupsData;
    }
  }
  static childGroupOfComponentTypeData;
  static get childGroupOfComponentType() {
    if (this.hasOwnProperty("childGroupOfComponentTypeData")) {
      return this.childGroupOfComponentTypeData;
    } else {
      this.childGroupOfComponentTypeData = {};
      return this.childGroupOfComponentTypeData;
    }
  }
  static childGroupIndsByNameData;
  static get childGroupIndsByName() {
    if (this.hasOwnProperty("childGroupIndsByNameData")) {
      return Object.assign({}, this.childGroupIndsByNameData);
    }
    this.childGroupIndsByNameData = {};
    for (let [ind, group] of this.childGroups.entries()) {
      if (group.group in this.childGroupIndsByNameData) {
        throw Error(
          `Invalid childGroups for componentClass ${this.componentType}: ${group} is repeated`
        );
      }
      this.childGroupIndsByNameData[group.group] = ind;
    }
    return Object.assign({}, this.childGroupIndsByNameData);
  }
  returnMatchedChildIndices(childGroups) {
    let matchedIndices = [];
    for (let groupName of childGroups) {
      let matches = this.childMatchesByGroup[groupName];
      if (!matches) {
        return void 0;
      }
      matchedIndices.push(...matches);
    }
    return matchedIndices.sort((a2, b2) => a2 - b2);
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = {};
    stateVariableDefinitions.hidden = {
      description: "Whether this component is hidden from the rendered output.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        hide: {
          dependencyType: "stateVariable",
          variableName: "hide",
          variablesOptional: true
        },
        parentHidden: {
          dependencyType: "parentStateVariable",
          variableName: "hidden"
        },
        sourceCompositeHidden: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "hidden"
        },
        adapterSourceHidden: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "hidden"
        },
        parentChildrenToHide: {
          dependencyType: "parentStateVariable",
          variableName: "childrenToHide"
        }
      }),
      definition: ({ dependencyValues, componentIdx }) => {
        return {
          setValue: {
            hidden: Boolean(
              dependencyValues.parentHidden || dependencyValues.sourceCompositeHidden || dependencyValues.adapterSourceHidden || dependencyValues.hide || Array.isArray(
                dependencyValues.parentChildrenToHide
              ) && dependencyValues.parentChildrenToHide.includes(
                componentIdx
              )
            )
          }
        };
      },
      markStale: () => ({ updateParentRenderedChildren: true }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "hide",
              desiredValue: desiredStateVariableValues.hidden
            }
          ]
        };
      }
    };
    stateVariableDefinitions.hiddenIgnoreParent = {
      returnDependencies: () => ({
        hide: {
          dependencyType: "stateVariable",
          variableName: "hide",
          variablesOptional: true
        },
        sourceCompositeHidden: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "hiddenIgnoreParent"
        },
        adapterSourceHidden: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "hiddenIgnoreParent"
        }
      }),
      definition: ({ dependencyValues }) => {
        return {
          setValue: {
            hiddenIgnoreParent: Boolean(
              dependencyValues.sourceCompositeHidden || dependencyValues.adapterSourceHidden || dependencyValues.hide
            )
          }
        };
      }
    };
    stateVariableDefinitions.disabled = {
      description: "Whether this component is disabled and cannot be interacted with.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      hasEssential: true,
      doNotShadowEssential: true,
      defaultValue: false,
      returnDependencies: () => ({
        disabledPreliminary: {
          dependencyType: "stateVariable",
          variableName: "disabledPreliminary",
          variablesOptional: true
        },
        readOnly: {
          dependencyType: "flag",
          flagName: "readOnly"
        },
        parentDisabled: {
          dependencyType: "parentStateVariable",
          variableName: "disabled"
        },
        sourceCompositeDisabled: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "disabled"
        },
        adapterSourceDisabled: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "disabled"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.readOnly) {
          return { setValue: { disabled: true } };
        }
        if (!usedDefault.disabledPreliminary) {
          return {
            setValue: {
              disabled: Boolean(
                dependencyValues.disabledPreliminary
              )
            }
          };
        }
        let disabled = false;
        let useEssential = true;
        if (dependencyValues.parentDisabled !== null && !usedDefault.parentDisabled) {
          disabled = disabled || dependencyValues.parentDisabled;
          useEssential = false;
        }
        if (dependencyValues.sourceCompositeDisabled !== null && !usedDefault.sourceCompositeDisabled) {
          disabled = disabled || dependencyValues.sourceCompositeDisabled;
          useEssential = false;
        }
        if (dependencyValues.adapterSourceDisabled !== null && !usedDefault.adapterSourceDisabled) {
          disabled = disabled || dependencyValues.adapterSourceDisabled;
          useEssential = false;
        }
        if (useEssential) {
          return {
            useEssentialOrDefaultValue: {
              disabled: true
            }
          };
        } else {
          return { setValue: { disabled } };
        }
      },
      inverseDefinition({
        dependencyValues,
        desiredStateVariableValues
      }) {
        if (dependencyValues.disabledPreliminary !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "disabledPreliminary",
                desiredValue: desiredStateVariableValues.disabled
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "disabled",
                value: desiredStateVariableValues.disabled
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.fixed = {
      description: "Whether this component's value is fixed and cannot be modified.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: false,
      hasEssential: true,
      doNotShadowEssential: true,
      ignoreFixed: true,
      returnDependencies: () => ({
        fixedPreliminary: {
          dependencyType: "stateVariable",
          variableName: "fixedPreliminary",
          variablesOptional: true
        },
        parentFixed: {
          dependencyType: "parentStateVariable",
          variableName: "fixed"
        },
        sourceCompositeFixed: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "fixed"
        },
        adapterSourceFixed: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "fixed"
        },
        shadowSourceFixed: {
          dependencyType: "shadowSourceStateVariable",
          variableName: "fixed"
        },
        shadowSource: {
          dependencyType: "shadowSource",
          givePropVariableValue: true
        },
        ignoreParentFixed: {
          dependencyType: "doenetAttribute",
          attributeName: "ignoreParentFixed"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (!usedDefault.fixedPreliminary) {
          return {
            setValue: {
              fixed: dependencyValues.fixedPreliminary
            }
          };
        }
        let fixed = false;
        let useEssential = true;
        if (dependencyValues.parentFixed !== null && !usedDefault.parentFixed && !dependencyValues.ignoreParentFixed) {
          fixed = fixed || dependencyValues.parentFixed;
          useEssential = false;
        }
        if (dependencyValues.sourceCompositeFixed !== null && !usedDefault.sourceCompositeFixed) {
          fixed = fixed || dependencyValues.sourceCompositeFixed;
          useEssential = false;
        }
        if (dependencyValues.adapterSourceFixed !== null && !usedDefault.adapterSourceFixed) {
          fixed = fixed || dependencyValues.adapterSourceFixed;
          useEssential = false;
        }
        if (dependencyValues.shadowSourceFixed !== null && !usedDefault.shadowSourceFixed) {
          if (dependencyValues.shadowSource.stateValues && !("fixed" in dependencyValues.shadowSource.stateValues)) {
            fixed = fixed || dependencyValues.shadowSourceFixed;
            useEssential = false;
          }
        }
        if (useEssential) {
          return {
            useEssentialOrDefaultValue: {
              fixed: true
            }
          };
        } else {
          return { setValue: { fixed } };
        }
      },
      inverseDefinition({
        dependencyValues,
        desiredStateVariableValues
      }) {
        if (dependencyValues.fixedPreliminary !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "fixedPreliminary",
                desiredValue: desiredStateVariableValues.fixed
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "fixed",
                value: desiredStateVariableValues.fixed
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.fixLocation = {
      description: "Whether this component's location is fixed (preventing it from being moved while still allowing other modifications).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: false,
      hasEssential: true,
      doNotShadowEssential: true,
      returnDependencies: () => ({
        fixLocationPreliminary: {
          dependencyType: "stateVariable",
          variableName: "fixLocationPreliminary",
          variablesOptional: true
        },
        parentFixLocation: {
          dependencyType: "parentStateVariable",
          variableName: "fixLocation"
        },
        sourceCompositeFixLocation: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "fixLocation"
        },
        adapterSourceFixLocation: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "fixLocation"
        },
        shadowSourceFixLocation: {
          dependencyType: "shadowSourceStateVariable",
          variableName: "fixLocation"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (!usedDefault.fixLocationPreliminary) {
          return {
            setValue: {
              fixLocation: dependencyValues.fixLocationPreliminary
            }
          };
        }
        let fixLocation = false;
        let useEssential = true;
        if (dependencyValues.parentFixLocation !== null && !usedDefault.parentFixLocation) {
          fixLocation = fixLocation || dependencyValues.parentFixLocation;
          useEssential = false;
        }
        if (dependencyValues.sourceCompositeFixLocation !== null && !usedDefault.sourceCompositeFixLocation) {
          fixLocation = fixLocation || dependencyValues.sourceCompositeFixLocation;
          useEssential = false;
        }
        if (dependencyValues.adapterSourceFixLocation !== null && !usedDefault.adapterSourceFixLocation) {
          fixLocation = fixLocation || dependencyValues.adapterSourceFixLocation;
          useEssential = false;
        }
        if (dependencyValues.shadowSourceFixLocation !== null && !usedDefault.shadowSourceFixLocation) {
          fixLocation = fixLocation || dependencyValues.shadowSourceFixLocation;
          useEssential = false;
        }
        if (useEssential) {
          return {
            useEssentialOrDefaultValue: {
              fixLocation: true
            }
          };
        } else {
          return { setValue: { fixLocation } };
        }
      },
      inverseDefinition({
        dependencyValues,
        desiredStateVariableValues
      }) {
        if (dependencyValues.fixLocationPreliminary !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "fixLocationPreliminary",
                desiredValue: desiredStateVariableValues.fixLocation
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "fixLocation",
                value: desiredStateVariableValues.fixLocation
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.isInactiveCompositeReplacement = {
      defaultValue: false,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          isInactiveCompositeReplacement: true
        }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: {
                variableName: "isInactiveCompositeReplacement",
                value: desiredStateVariableValues.isInactiveCompositeReplacement
              }
            }
          ]
        };
      }
    };
    stateVariableDefinitions.doenetML = {
      description: "The DoenetML source code that produced this component.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        doenetML: {
          dependencyType: "doenetML"
        }
      }),
      definition({ dependencyValues }) {
        let doenetML = dependencyValues.doenetML;
        if (!doenetML) {
          doenetML = "";
        }
        return { setValue: { doenetML } };
      }
    };
    return stateVariableDefinitions;
  }
  static returnNormalizedStateVariableDefinitions(numerics) {
    let newDefinitions = this.returnStateVariableDefinitions(numerics);
    if (!newDefinitions) {
      throw Error(
        `Error in state variable definitions of ${this.componentType}: returnStateVariableDefinitions did not return anything`
      );
    }
    let cleanAdditionalStateVariableDefined = function(additionalStateVariablesDefined) {
      for (let [
        ind,
        varObj
      ] of additionalStateVariablesDefined.entries()) {
        if (typeof varObj === "object") {
          additionalStateVariablesDefined[ind] = varObj.variableName;
        }
      }
    };
    let defAttributesToCopy = [
      "returnDependencies",
      "definition",
      "inverseDefinition",
      "stateVariablesDeterminingDependencies",
      "stateVariablesDeterminingArraySizeDependencies",
      "isArray",
      "numDimensions",
      "returnArraySizeDependencies",
      "returnArraySize",
      "returnArrayDependenciesByKey",
      "arrayDefinitionByKey",
      "inverseArrayDefinitionByKey",
      "basedOnArrayKeyStateVariables",
      "markStale",
      "getPreviousDependencyValuesForMarkStale",
      "determineDependenciesImmediately",
      "createWorkspace",
      "workspace",
      "provideEssentialValuesInDefinition",
      "providePreviousValuesInDefinition",
      "isLocation"
    ];
    let stateVariableDefinitions = {};
    for (let varName in newDefinitions) {
      let thisDef = newDefinitions[varName];
      stateVariableDefinitions[varName] = thisDef;
      if (thisDef.createWorkspace) {
        thisDef.workspace = {};
      }
      if (thisDef.additionalStateVariablesDefined) {
        for (let [
          ind,
          otherVarObj
        ] of thisDef.additionalStateVariablesDefined.entries()) {
          let defCopy = {};
          for (let attr of defAttributesToCopy) {
            if (attr in thisDef) {
              defCopy[attr] = thisDef[attr];
            }
          }
          defCopy.additionalStateVariablesDefined = [
            ...thisDef.additionalStateVariablesDefined
          ];
          defCopy.additionalStateVariablesDefined[ind] = varName;
          cleanAdditionalStateVariableDefined(
            defCopy.additionalStateVariablesDefined
          );
          let otherVarName = otherVarObj;
          if (typeof otherVarObj === "object") {
            otherVarName = otherVarObj.variableName;
            otherVarObj = Object.assign({}, otherVarObj);
            delete otherVarObj.variableName;
            Object.assign(defCopy, otherVarObj);
          }
          stateVariableDefinitions[otherVarName] = defCopy;
        }
        cleanAdditionalStateVariableDefined(
          thisDef.additionalStateVariablesDefined
        );
      }
    }
    return stateVariableDefinitions;
  }
  static returnStateVariableInfo({
    onlyPublic = false,
    onlyForRenderer = false
  } = {}) {
    let attributeObject = this.createAttributesObject();
    let stateVariableDescriptions = {};
    let arrayEntryPrefixes = {};
    let aliases = {};
    for (let attrName in attributeObject) {
      let attrObj = attributeObject[attrName];
      let varName = attrObj.createStateVariable;
      if (varName) {
        if ((!onlyPublic || attrObj.public) && (!onlyForRenderer || attrObj.forRenderer)) {
          if (attrObj.public) {
            let attributeFromPrimitive = !attrObj.createComponentOfType;
            let createComponentOfType;
            if (attributeFromPrimitive) {
              createComponentOfType = attrObj.createPrimitiveOfType;
              if (createComponentOfType === "string") {
                createComponentOfType = "text";
              }
            } else {
              createComponentOfType = attrObj.createComponentOfType;
            }
            stateVariableDescriptions[varName] = {
              createComponentOfType,
              public: true,
              isArray: false
            };
          } else {
            stateVariableDescriptions[varName] = {
              public: false,
              isArray: false
            };
          }
          if (attrName === varName) {
            stateVariableDescriptions[varName].fromAttribute = true;
          }
          if (attrObj.description !== void 0) {
            stateVariableDescriptions[varName].description = attrObj.description;
          }
          if (attrObj.groupName !== void 0) {
            stateVariableDescriptions[varName].groupName = attrObj.groupName;
          }
          if (attrObj.highlighted !== void 0) {
            stateVariableDescriptions[varName].highlighted = attrObj.highlighted;
          }
        }
      }
    }
    let stateDef = this.returnNormalizedStateVariableDefinitions();
    for (let varName in stateDef) {
      let theStateDef = stateDef[varName];
      if (theStateDef.isAlias) {
        aliases[varName] = {
          target: theStateDef.targetVariableName
        };
        if (theStateDef.description !== void 0) {
          aliases[varName].description = theStateDef.description;
        }
        if (theStateDef.excludeFromSchema) {
          aliases[varName].excludeFromSchema = true;
        }
        continue;
      }
      if ((!onlyPublic || theStateDef.public) && (!onlyForRenderer || theStateDef.forRenderer)) {
        if (theStateDef.public) {
          stateVariableDescriptions[varName] = {
            createComponentOfType: theStateDef.shadowingInstructions.createComponentOfType,
            public: true,
            isArray: Boolean(theStateDef.isArray)
          };
        } else {
          stateVariableDescriptions[varName] = {
            public: false,
            isArray: Boolean(theStateDef.isArray)
          };
        }
        if (theStateDef.description !== void 0) {
          stateVariableDescriptions[varName].description = theStateDef.description;
        }
        if (theStateDef.excludeFromSchema) {
          stateVariableDescriptions[varName].excludeFromSchema = true;
        }
        if (theStateDef.groupName !== void 0) {
          stateVariableDescriptions[varName].groupName = theStateDef.groupName;
        }
        if (theStateDef.highlighted !== void 0) {
          stateVariableDescriptions[varName].highlighted = theStateDef.highlighted;
        }
        if (theStateDef.hasEssential && theStateDef.defaultValue !== void 0) {
          stateVariableDescriptions[varName].defaultValue = theStateDef.defaultValue;
        }
        if (theStateDef.isArray) {
          stateVariableDescriptions[varName].isArray = true;
          stateVariableDescriptions[varName].numDimensions = theStateDef.numDimensions === void 0 ? 1 : theStateDef.numDimensions;
          stateVariableDescriptions[varName].schemaSubarrays = theStateDef.schemaSubarrays;
          stateVariableDescriptions[varName].wrappingComponents = theStateDef.shadowingInstructions?.returnWrappingComponents ? theStateDef.shadowingInstructions.returnWrappingComponents() : [];
          stateVariableDescriptions[varName].indexAliases = theStateDef.indexAliases;
          let entryPrefixes;
          if (theStateDef.entryPrefixes) {
            entryPrefixes = theStateDef.entryPrefixes;
          } else {
            entryPrefixes = [varName];
          }
          for (let prefix of entryPrefixes) {
            arrayEntryPrefixes[prefix] = {
              arrayVariableName: varName,
              numDimensions: theStateDef.returnEntryDimensions ? theStateDef.returnEntryDimensions(prefix) : 0,
              wrappingComponents: theStateDef.shadowingInstructions?.returnWrappingComponents ? theStateDef.shadowingInstructions.returnWrappingComponents(
                prefix
              ) : []
            };
          }
          if (theStateDef.getArrayKeysFromVarName) {
            stateVariableDescriptions[varName].getArrayKeysFromVarName = theStateDef.getArrayKeysFromVarName;
          } else {
            stateVariableDescriptions[varName].getArrayKeysFromVarName = returnDefaultGetArrayKeysFromVarName(
              stateVariableDescriptions[varName].numDimensions
            );
          }
          if (theStateDef.arrayVarNameFromPropIndex) {
            stateVariableDescriptions[varName].arrayVarNameFromPropIndex = theStateDef.arrayVarNameFromPropIndex;
          } else {
            stateVariableDescriptions[varName].arrayVarNameFromPropIndex = returnDefaultArrayVarNameFromPropIndex(
              stateVariableDescriptions[varName].numDimensions,
              entryPrefixes[0]
            );
          }
        }
      }
    }
    return { stateVariableDescriptions, arrayEntryPrefixes, aliases };
  }
  get parentIdx() {
    if (this.ancestors === void 0 || this.ancestors.length === 0) {
      return;
    }
    return this.ancestors[0].componentIdx;
  }
  get allDescendants() {
    let descendants = [];
    for (let name in this.allChildren) {
      let child = this.allChildren[name].component;
      descendants = [...descendants, name, ...child.allDescendants];
    }
    return descendants;
  }
  async serialize(parameters = {}) {
    if (parameters.errorIfEncounterComponent?.includes(this.componentIdx)) {
      throw Error(
        `${SERIALIZE_ENCOUNTERED_COMPONENT_PREFIX}${this.componentIdx}`
      );
    }
    const serializedComponent = {
      type: "serialized",
      componentType: this.componentType,
      componentIdx: this.componentIdx,
      children: [],
      attributes: {},
      doenetAttributes: {},
      state: {},
      sourceDoc: this.sourceDoc
    };
    let serializedChildren = [];
    let parametersForChildren = { ...parameters };
    let primitiveSourceAttributesToIgnore;
    if (parameters.primitiveSourceAttributesToIgnore) {
      primitiveSourceAttributesToIgnore = parameters.primitiveSourceAttributesToIgnore;
    } else {
      primitiveSourceAttributesToIgnore = [];
    }
    let componentSourceAttributesToIgnore;
    if (parameters.componentSourceAttributesToIgnore) {
      componentSourceAttributesToIgnore = parameters.componentSourceAttributesToIgnore;
    } else {
      componentSourceAttributesToIgnore = [];
    }
    {
      if (this.constructor.serializeReplacementsForChildren && this.isExpanded && (!("rendered" in this.state) || await this.state.rendered.value)) {
        for (let repl of this.replacements) {
          if (typeof repl !== "object") {
            serializedChildren.push(repl);
          } else {
            serializedChildren.push(
              await repl.serialize(parametersForChildren)
            );
          }
        }
      } else {
        for (let child of this.definingChildren) {
          if (typeof child !== "object") {
            serializedChildren.push(child);
          } else {
            serializedChildren.push(
              await child.serialize(parametersForChildren)
            );
          }
        }
        if (this.serializedChildren !== void 0) {
          let shadowComponents = this.attributes.copyInChildren?.primitive?.value;
          for (let child of this.serializedChildren) {
            serializedChildren.push(
              this.copySerializedComponent(
                child,
                shadowComponents
              )
            );
          }
        }
      }
      if (serializedChildren.length > 0) {
        serializedComponent.children = serializedChildren;
      }
    }
    for (let attrName in this.attributes) {
      let attribute = this.attributes[attrName];
      if (attribute.component) {
        if (parameters.copyAll && !componentSourceAttributesToIgnore.includes(attrName)) {
          serializedComponent.attributes[attrName] = {
            type: "component",
            component: await attribute.component.serialize(
              parametersForChildren
            ),
            sourceDoc: attribute.component.sourceDoc
          };
        }
      } else if (attribute.references) {
        const references = [];
        for (const refComp of attribute.references) {
          references.push(
            await refComp.serialize(parametersForChildren)
          );
        }
        serializedComponent.attributes[attrName] = {
          type: "references",
          references,
          stringChildren: attribute.stringChildren,
          sourceDoc: attribute.sourceDoc
        };
      } else {
        if (!primitiveSourceAttributesToIgnore.includes(attrName) || parameters.copyAll) {
          serializedComponent.attributes[attrName] = JSON.parse(
            JSON.stringify(attribute)
          );
        }
      }
    }
    if (this.essentialState && Object.keys(this.essentialState).length > 0) {
      serializedComponent.state = deepClone(this.essentialState);
    }
    if (parameters.copyPrimaryEssential || parameters.copyPrimaryEssentialIfShadow && this.shadows) {
      let primaryEssentialStateVariable = "value";
      if (this.constructor.primaryEssentialStateVariable) {
        primaryEssentialStateVariable = this.constructor.primaryEssentialStateVariable;
      } else if (this.constructor.primaryStateVariableForDefinition) {
        primaryEssentialStateVariable = this.constructor.primaryStateVariableForDefinition;
      }
      if (this.state[primaryEssentialStateVariable]) {
        if (this.state[primaryEssentialStateVariable].essentialVarName) {
          primaryEssentialStateVariable = this.state[primaryEssentialStateVariable].essentialVarName;
        }
        let stateVariableToBeShadowed = "value";
        if (this.constructor.stateVariableToBeShadowed) {
          stateVariableToBeShadowed = this.constructor.stateVariableToBeShadowed;
        } else if (this.constructor.primaryStateVariableForDefinition) {
          stateVariableToBeShadowed = this.constructor.primaryStateVariableForDefinition;
        }
        if (this.state[stateVariableToBeShadowed]) {
          if (!serializedComponent.state) {
            serializedComponent.state = {};
          }
          serializedComponent.state[primaryEssentialStateVariable] = await this.stateValues[stateVariableToBeShadowed];
        }
      }
    }
    if (parameters.copyEssentialState || parameters.copyEssentialStateIfShadow && this.shadows) {
      for (let varName in this.state) {
        if (!(varName in serializedComponent.state)) {
          let stateVar = this.state[varName];
          if (stateVar.hasEssential) {
            const value = await this.stateValues[varName];
            if (!stateVar.usedDefault) {
              serializedComponent.state[varName] = value;
            }
          }
        }
      }
    }
    if (this.position) {
      serializedComponent.position = JSON.parse(
        JSON.stringify(this.position)
      );
    }
    if (this.childrenPosition) {
      serializedComponent.childrenPosition = JSON.parse(
        JSON.stringify(this.childrenPosition)
      );
    }
    if (parameters.copyVariants) {
      if (this.state.generatedVariantInfo) {
        serializedComponent.variants = {
          desiredVariant: await this.stateValues.generatedVariantInfo
        };
      }
    }
    serializedComponent.originalIdx = this.componentIdx;
    serializedComponent.originalDoenetAttributes = deepClone(
      this.doenetAttributes
    );
    serializedComponent.doenetAttributes = deepClone(this.doenetAttributes);
    serializedComponent.originalAttributes = deepClone(
      serializedComponent.attributes
    );
    if (this.refResolution) {
      serializedComponent.extending = {
        Ref: {
          nodeIdx: this.refResolution.nodeIdx,
          unresolvedPath: deepClone(
            this.refResolution.unresolvedPath
          ),
          originalPath: await this.serializePath(
            this.refResolution.originalPath,
            parametersForChildren
          ),
          nodesInResolvedPath: deepClone(
            this.refResolution.nodesInResolvedPath
          )
        }
      };
    }
    delete serializedComponent.doenetAttributes.prescribedName;
    return serializedComponent;
  }
  async serializePath(path, parametersForChildren) {
    if (path == null) {
      return path;
    }
    let newPath = [];
    for (const pathPart of path) {
      const newPathPart = {
        name: pathPart.name,
        position: pathPart.position,
        sourceDoc: pathPart.sourceDoc,
        index: []
      };
      for (const index2 of pathPart.index) {
        const newIndex = {
          position: index2.position,
          sourceDoc: index2.sourceDoc,
          value: []
        };
        for (let comp of index2.value) {
          if (typeof comp !== "object") {
            newIndex.value.push(comp);
          } else {
            newIndex.value.push(
              await comp.serialize(parametersForChildren)
            );
          }
        }
        newPathPart.index.push(newIndex);
      }
      newPath.push(newPathPart);
    }
    return newPath;
  }
  copySerializedComponent(serializedComponent, shadowComponents = false) {
    if (typeof serializedComponent !== "object") {
      return serializedComponent;
    }
    let serializedChildren = [];
    if (serializedComponent.children !== void 0) {
      for (let child of serializedComponent.children) {
        serializedChildren.push(this.copySerializedComponent(child));
      }
    }
    let serializedCopy = {
      type: "serialized",
      componentType: serializedComponent.componentType,
      componentIdx: serializedComponent.componentIdx,
      originalIdx: serializedComponent.componentIdx,
      dontShadowOriginalIndex: !shadowComponents,
      children: serializedChildren,
      state: {},
      doenetAttributes: {},
      attributes: {},
      sourceDoc: serializedComponent.sourceDoc
    };
    if (serializedComponent.doenetAttributes != void 0) {
      serializedCopy.originalDoenetAttributes = deepClone(
        serializedComponent.doenetAttributes
      );
      serializedCopy.doenetAttributes = deepClone(
        serializedComponent.doenetAttributes
      );
      delete serializedCopy.doenetAttributes.prescribedName;
    }
    if (serializedComponent.attributes != void 0) {
      serializedCopy.attributes = deepClone(
        serializedComponent.attributes
      );
      serializedCopy.originalAttributes = deepClone(
        serializedComponent.attributes
      );
    }
    if (serializedComponent.extending != void 0) {
      serializedCopy.extending = deepClone(serializedComponent.extending);
    }
    if (serializedComponent.position !== void 0) {
      serializedCopy.position = deepClone(serializedComponent.position);
    }
    if (serializedComponent.state !== void 0) {
      Object.assign(serializedCopy.state, serializedComponent.state);
    }
    return serializedCopy;
  }
  // TODO: this function is not being used (7/13/2025). Delete it?
  copySerializedComponentToDast(serializedComponent) {
    if (typeof serializedComponent !== "object") {
      return { type: "text", value: serializedComponent };
    }
    let dastChildren = [];
    if (serializedComponent.children !== void 0) {
      for (let child of serializedComponent.children) {
        dastChildren.push(this.copySerializedComponentToDast(child));
      }
    }
    let dastCopy = {
      type: "element",
      name: serializedComponent.componentType,
      attributes: {
        originalIdx: {
          type: "attribute",
          name: "originalIdx",
          children: [
            {
              type: "text",
              value: serializedComponent.componentIdx.toString()
            }
          ]
        }
      },
      children: dastChildren
    };
    if (serializedComponent.attributes.name) {
      dastElement.attributes.name = {
        type: "attribute",
        name: "name",
        children: [
          {
            type: "text",
            value: serializedComponent.attributes.name.primitive.value
          }
        ]
      };
    }
    if (serializedComponent.position !== void 0) {
      dastCopy.position = deepClone(serializedComponent.position);
    }
    return dastCopy;
  }
  static adapters = [];
  static get numAdapters() {
    return this.adapters.length;
  }
  getAdapter(ind) {
    if (ind >= this.constructor.adapters.length) {
      return;
    }
    let adapter = this.constructor.adapters[ind];
    let adapterStateVariable;
    let adapterComponentType;
    let substituteForPrimaryStateVariable;
    let stateVariablesToShadow;
    if (typeof adapter === "string") {
      adapterStateVariable = adapter;
    } else {
      adapterStateVariable = adapter.stateVariable;
      adapterComponentType = adapter.componentType;
      substituteForPrimaryStateVariable = adapter.substituteForPrimaryStateVariable;
      stateVariablesToShadow = adapter.stateVariablesToShadow;
    }
    let stateFromAdapter = this.state[adapterStateVariable];
    if (stateFromAdapter === void 0 || !stateFromAdapter.public && !adapterComponentType) {
      throw Error(
        "Invalid adapter " + adapterStateVariable + " in " + this.componentType
      );
    }
    if (adapterComponentType === void 0) {
      adapterComponentType = stateFromAdapter.shadowingInstructions.createComponentOfType;
    }
    return {
      type: "serialized",
      componentType: adapterComponentType,
      attribute: {},
      children: [],
      doenetAttributes: [],
      downstreamDependencies: {
        [this.componentIdx]: [
          {
            dependencyType: "adapter",
            adapterVariable: adapterStateVariable,
            adapterTargetIdentity: {
              componentIdx: this.componentIdx,
              componentType: this.componentType
            },
            substituteForPrimaryStateVariable,
            stateVariablesToShadow
          }
        ]
      }
    };
  }
  static getAdapterComponentType(ind, publicStateVariableInfo) {
    if (ind >= this.adapters.length) {
      return;
    }
    let adapter = this.adapters[ind];
    let adapterStateVariable;
    let adapterComponentType;
    if (typeof adapter === "string") {
      adapterStateVariable = adapter;
    } else {
      adapterStateVariable = adapter.stateVariable;
      adapterComponentType = adapter.componentType;
    }
    if (adapterComponentType === void 0) {
      let stateVarInfo = publicStateVariableInfo[this.componentType];
      let varInfo = stateVarInfo.stateVariableDescriptions[adapterStateVariable];
      if (!varInfo) {
        throw Error(
          "Invalid adapter " + adapterStateVariable + " in " + this.componentType
        );
      }
      adapterComponentType = varInfo.createComponentOfType;
      if (!adapterComponentType) {
        throw Error(
          `Couldn't get adapter component type for ${adapterStateVariable} of componentType ${this.componentType}`
        );
      }
    }
    return adapterComponentType;
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants !== void 0) {
      return { success: true, numVariants };
    }
    let descendantVariantComponents = [];
    if (serializedComponent.children) {
      descendantVariantComponents = gatherVariantComponents({
        serializedComponents: serializedComponent.children,
        componentInfoObjects
      });
    }
    if (serializedComponent.variants === void 0) {
      serializedComponent.variants = {};
    }
    serializedComponent.variants.descendantVariantComponents = descendantVariantComponents;
    numVariants = 1;
    let numVariantsByDescendant = [];
    for (let descendant of descendantVariantComponents) {
      let descendantClass = componentInfoObjects.allComponentClasses[descendant.componentType];
      let result = descendantClass.determineNumberOfUniqueVariants({
        serializedComponent: descendant,
        componentInfoObjects,
        infoDiagnostics
      });
      if (!result.success) {
        return { success: false };
      }
      numVariantsByDescendant.push(result.numVariants);
      numVariants *= result.numVariants;
    }
    if (!(numVariants > 0)) {
      return { success: false };
    }
    serializedComponent.variants.numVariants = numVariants;
    serializedComponent.variants.uniqueVariantData = {
      numVariantsByDescendant
    };
    return { success: true, numVariants };
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let haveNontrivialSubvariants = false;
    let numVariantsByDescendant = serializedComponent.variants.uniqueVariantData.numVariantsByDescendant;
    let descendantVariantComponents = serializedComponent.variants.descendantVariantComponents;
    let subvariants = [];
    if (descendantVariantComponents.length > 0) {
      let indicesForEachDescendant = enumerateCombinations({
        numberOfOptionsByIndex: numVariantsByDescendant,
        maxNumber: variantIndex
      })[variantIndex - 1].map((x2) => x2 + 1);
      for (let descendantNum = 0; descendantNum < numVariantsByDescendant.length; descendantNum++) {
        if (numVariantsByDescendant[descendantNum] > 1) {
          let descendant = descendantVariantComponents[descendantNum];
          let compClass = componentInfoObjects.allComponentClasses[descendant.componentType];
          let result = compClass.getUniqueVariant({
            serializedComponent: descendant,
            variantIndex: indicesForEachDescendant[descendantNum],
            componentInfoObjects
          });
          if (!result.success) {
            return { success: false };
          }
          subvariants.push(result.desiredVariant);
          haveNontrivialSubvariants = true;
        } else {
          subvariants.push({});
        }
      }
    }
    let desiredVariant = { index: variantIndex };
    if (haveNontrivialSubvariants) {
      desiredVariant.subvariants = subvariants;
    }
    return { success: true, desiredVariant };
  }
  // An action that can be called from DoenetML with a `<callAction>`
  // that will copy the DoenetML of the targeted component to the clipboard
  // (assuming the component was in the original source file).
  // TODO: add test
  // TODO: add documentation
  async copyDoenetMLToClipboard({ actionId }) {
    let doenetML = await this.stateValues.doenetML;
    if (doenetML) {
      this.coreFunctions.copyToClipboard({ text: doenetML, actionId });
    }
  }
}
class InlineComponent extends BaseComponent {
  static componentType = "_inline";
}
class Text extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveText: this.moveText.bind(this),
      textClicked: this.textClicked.bind(this),
      textFocused: this.textFocused.bind(this)
    });
  }
  static componentType = "text";
  static componentDocs = {
    summary: "A text string"
  };
  static includeBlankStringChildren = true;
  static variableForImplicitProp = "value";
  static implicitPropReturnsSameType = true;
  static descendantCompositesMustHaveAReplacement = true;
  static descendantCompositesDefaultReplacementType = "text";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the text can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.layer = {
      description: "Z-order layer index when shown on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    attributes.isLatex = {
      description: "Whether the text content is treated as LaTeX.",
      createComponentOfType: "boolean",
      createStateVariable: "isLatex",
      defaultValue: false,
      public: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "textLike",
        componentTypes: [
          "string",
          "text",
          "_singleCharacterInline",
          "_inlineRenderInlineChildren"
        ]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.inUnorderedList = {
      returnDependencies: () => ({
        sourceCompositeUnordered: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "unordered",
          skipCopies: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.sourceCompositeUnordered !== null && !usedDefault.sourceCompositeUnordered) {
          return {
            setValue: {
              inUnorderedList: Boolean(
                dependencyValues.sourceCompositeUnordered
              )
            }
          };
        } else {
          return {
            setValue: {
              inUnorderedList: false
            }
          };
        }
      }
    };
    stateVariableDefinitions.value = {
      description: "The text value.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: this.componentType,
        // the reason we create a attribute component from the state variable,
        // rather than just shadowing the attribute,
        // is that a sequence creates a text where it sets fixed directly in the state
        // TODO: how to deal with this in general?  Should we disallow that way to set state?
        // Or should we always shadow attributes this way?
        addAttributeComponentsShadowingStateVariables: {
          fixed: {
            stateVariableToShadow: "fixed"
          }
        }
      },
      hasEssential: true,
      returnDependencies: () => ({
        textLikeChildren: {
          dependencyType: "child",
          childGroups: ["textLike"],
          variableNames: ["text"]
        }
      }),
      defaultValue: "",
      set: (x2) => x2 === null ? "" : String(x2),
      definition: function({ dependencyValues }) {
        if (dependencyValues.textLikeChildren.length === 0) {
          return {
            useEssentialOrDefaultValue: {
              value: true
            }
          };
        }
        let value = textFromChildren(dependencyValues.textLikeChildren);
        return { setValue: { value } };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let numChildren = dependencyValues.textLikeChildren.length;
        if (numChildren > 1) {
          let foundAllFromListComposite = false;
          for (let range of dependencyValues.textLikeChildren.compositeReplacementRange) {
            if (range.asList && range.firstInd === 0 && range.lastInd === numChildren - 1) {
              foundAllFromListComposite = true;
            }
          }
          if (foundAllFromListComposite) {
            let splitValues = desiredStateVariableValues.value.split(",").map((v2) => v2.trim());
            if (splitValues.length === numChildren) {
              let instructions = splitValues.map((v2, i2) => ({
                setDependency: "textLikeChildren",
                desiredValue: v2,
                childIndex: i2,
                variableIndex: 0
              }));
              return { success: true, instructions };
            }
          }
          return { success: false };
        }
        if (numChildren === 1) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "textLikeChildren",
                desiredValue: desiredStateVariableValues.value,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "value",
              value: desiredStateVariableValues.value === null ? "" : String(desiredStateVariableValues.value)
            }
          ]
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The text rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { text: dependencyValues.value }
      }),
      inverseDefinition: ({ desiredStateVariableValues }) => ({
        success: true,
        instructions: [
          {
            setDependency: "value",
            desiredValue: desiredStateVariableValues.text
          }
        ]
      })
    };
    stateVariableDefinitions.math = {
      description: "The text parsed as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        isLatex: {
          dependencyType: "stateVariable",
          variableName: "isLatex"
        }
      }),
      definition({ dependencyValues }) {
        let parser = dependencyValues.isLatex ? latexToMathFactory() : textToMathFactory();
        let expression;
        try {
          expression = parser(dependencyValues.value);
        } catch (e3) {
          expression = me$3.fromAst("＿");
        }
        return { setValue: { math: expression } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let text;
        if (dependencyValues.isLatex) {
          text = desiredStateVariableValues.math.toLatex();
        } else {
          text = desiredStateVariableValues.math.toString();
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "value",
              desiredValue: text
            }
          ]
        };
      }
    };
    stateVariableDefinitions.number = {
      description: "The text parsed as a number.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({
        math: {
          dependencyType: "stateVariable",
          variableName: "math"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            number: dependencyValues.math.evaluate_to_constant()
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "math",
              desiredValue: me$3.fromAst(
                Number(desiredStateVariableValues.number)
              )
            }
          ]
        };
      }
    };
    let pieceDefs = returnTextPieceStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, pieceDefs);
    return stateVariableDefinitions;
  }
  static adapters = ["math", "number"];
  async moveText({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  async textClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async textFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class TextOrInline extends InlineComponent {
  static componentType = "_textOrInline";
  static renderChildren = true;
  static rendererType = "containerInline";
  static includeBlankStringChildren = true;
  static returnChildGroups() {
    return [
      {
        group: "inlines",
        componentTypes: ["_inline"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.value = {
      description: "The combined content of this component's children.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: this.componentType
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlines"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let value = textFromChildren(dependencyValues.inlineChildren);
        return { setValue: { value } };
      }
    };
    stateVariableDefinitions.text = {
      description: "The combined content of this component's children, as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { text: dependencyValues.value }
      })
    };
    return stateVariableDefinitions;
  }
}
class BlockComponent extends BaseComponent {
  static componentType = "_block";
}
function returnNumberDisplayStateVariableDefinitions({
  childGroupsIfSingleMatch = [],
  childGroupsToStopSingleMatch = [],
  additionalAttributeComponent = null,
  displayDigitsDefault = 3,
  displaySmallAsZeroDefault = 1e-14
} = {}) {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.displayDigits = {
    description: "Number of significant digits to display when rendering this number.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    hasEssential: true,
    defaultValue: displayDigitsDefault,
    returnDependencies: numberDisplayDependencies({
      stateVariable: "displayDigits",
      childGroupsIfSingleMatch,
      childGroupsToStopSingleMatch,
      ignoreShadowsIfHaveAttribute: "displayDecimals",
      additionalAttributeComponent
    }),
    definition: numberDisplayDefinition({
      stateVariable: "displayDigits",
      valueIfIgnore: 0
    })
  };
  stateVariableDefinitions.displayDecimals = {
    description: "Number of decimal places to display when rendering this number.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    hasEssential: true,
    defaultValue: 2,
    returnDependencies: numberDisplayDependencies({
      stateVariable: "displayDecimals",
      childGroupsIfSingleMatch,
      childGroupsToStopSingleMatch,
      ignoreShadowsIfHaveAttribute: "displayDigits",
      additionalAttributeComponent
    }),
    definition: numberDisplayDefinition({
      stateVariable: "displayDecimals",
      valueIfIgnore: -Infinity
    })
  };
  stateVariableDefinitions.displaySmallAsZero = {
    description: "Threshold below which numbers are displayed as zero.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "number"
    },
    hasEssential: true,
    defaultValue: displaySmallAsZeroDefault,
    returnDependencies: numberDisplayDependencies({
      stateVariable: "displaySmallAsZero",
      childGroupsIfSingleMatch,
      childGroupsToStopSingleMatch,
      additionalAttributeComponent
    }),
    definition: numberDisplayDefinition({
      stateVariable: "displaySmallAsZero"
    })
  };
  stateVariableDefinitions.padZeros = {
    description: "Whether to pad displayed numbers with trailing zeros to fill the requested digits/decimals.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    hasEssential: true,
    defaultValue: false,
    returnDependencies: numberDisplayDependencies({
      stateVariable: "padZeros",
      childGroupsIfSingleMatch,
      childGroupsToStopSingleMatch,
      additionalAttributeComponent
    }),
    definition: numberDisplayDefinition({
      stateVariable: "padZeros"
    })
  };
  stateVariableDefinitions.avoidScientificNotation = {
    description: "Whether to render numbers in full decimal form rather than scientific notation.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    hasEssential: true,
    defaultValue: false,
    returnDependencies: numberDisplayDependencies({
      stateVariable: "avoidScientificNotation",
      childGroupsIfSingleMatch,
      childGroupsToStopSingleMatch,
      additionalAttributeComponent
    }),
    definition: numberDisplayDefinition({
      stateVariable: "avoidScientificNotation"
    })
  };
  return stateVariableDefinitions;
}
function numberDisplayDependencies({
  stateVariable,
  childGroupsIfSingleMatch,
  childGroupsToStopSingleMatch,
  ignoreShadowsIfHaveAttribute = null,
  additionalAttributeComponent = null
}) {
  return function() {
    let dependencies = {
      attribute: {
        dependencyType: "attributeComponent",
        attributeName: stateVariable,
        variableNames: ["value"]
      },
      singleMatchChildren: {
        dependencyType: "child",
        childGroups: childGroupsIfSingleMatch,
        variableNames: [stateVariable],
        variablesOptional: true
      },
      stopSingleMatchChildren: {
        dependencyType: "child",
        childGroups: childGroupsToStopSingleMatch
      }
    };
    if (ignoreShadowsIfHaveAttribute) {
      dependencies.attributePromptingIgnore = {
        dependencyType: "attributeComponent",
        attributeName: ignoreShadowsIfHaveAttribute,
        variableNames: ["value"],
        dontRecurseToShadowsIfHaveAttribute: stateVariable
      };
      dependencies.attribute.dontRecurseToShadowsIfHaveAttribute = ignoreShadowsIfHaveAttribute;
    }
    if (additionalAttributeComponent) {
      dependencies.additionalAttribute = {
        dependencyType: "attributeComponent",
        attributeName: additionalAttributeComponent,
        variableNames: [stateVariable]
      };
    }
    return dependencies;
  };
}
function numberDisplayDefinition({ stateVariable, valueIfIgnore = null }) {
  return function({ dependencyValues, usedDefault }) {
    let foundDefaultValue = false;
    let theDefaultValueFound;
    if (dependencyValues.attribute) {
      if (usedDefault.attribute?.value) {
        foundDefaultValue = true;
        theDefaultValueFound = dependencyValues.attribute.stateValues.value;
      } else {
        return {
          setValue: {
            [stateVariable]: dependencyValues.attribute.stateValues.value
          }
        };
      }
    } else if (dependencyValues.attributePromptingIgnore) {
      if (usedDefault.attributePromptingIgnore?.value) {
        foundDefaultValue = true;
        theDefaultValueFound = valueIfIgnore;
      } else {
        return {
          setValue: {
            [stateVariable]: valueIfIgnore
          }
        };
      }
    } else if (dependencyValues.additionalAttribute) {
      if (usedDefault.additionalAttribute?.[stateVariable]) {
        foundDefaultValue = true;
        theDefaultValueFound = dependencyValues.additionalAttribute.stateValues[stateVariable];
      } else {
        return {
          setValue: {
            [stateVariable]: dependencyValues.additionalAttribute.stateValues[stateVariable]
          }
        };
      }
    }
    if (dependencyValues.singleMatchChildren.length === 1 && dependencyValues.stopSingleMatchChildren.length === 0 && dependencyValues.singleMatchChildren[0].stateValues[stateVariable] !== void 0) {
      if (usedDefault.singleMatchChildren[0]?.[stateVariable]) {
        foundDefaultValue = true;
        theDefaultValueFound = dependencyValues.singleMatchChildren[0].stateValues[stateVariable];
      } else {
        return {
          setValue: {
            [stateVariable]: dependencyValues.singleMatchChildren[0].stateValues[stateVariable]
          }
        };
      }
    }
    if (foundDefaultValue) {
      return {
        useEssentialOrDefaultValue: {
          [stateVariable]: { defaultValue: theDefaultValueFound }
        }
      };
    } else {
      return { useEssentialOrDefaultValue: { [stateVariable]: true } };
    }
  };
}
function returnNumberDisplayAttributes() {
  return {
    displayDigits: {
      createComponentOfType: "integer",
      groupName: "number-display",
      description: "Number of significant digits to display when rendering this number."
    },
    displayDecimals: {
      createComponentOfType: "integer",
      groupName: "number-display",
      description: "Number of decimal places to display when rendering this number."
    },
    displaySmallAsZero: {
      createComponentOfType: "number",
      groupName: "number-display",
      valueForTrue: 1e-14,
      valueForFalse: 0,
      description: "Threshold below which numbers are displayed as zero."
    },
    padZeros: {
      createComponentOfType: "boolean",
      groupName: "number-display",
      description: "Whether to pad displayed numbers with trailing zeros to fill the requested digits/decimals."
    },
    avoidScientificNotation: {
      createComponentOfType: "boolean",
      groupName: "number-display",
      description: "Whether to render numbers in full decimal form rather than scientific notation."
    }
  };
}
function returnNumberDisplayAttributeComponentShadowing() {
  let shadowing = {};
  for (let stateVariable in returnNumberDisplayStateVariableDefinitions()) {
    shadowing[stateVariable] = {
      stateVariableToShadow: stateVariable,
      // if used in an array state variable that has wrapping components
      // add the attribute to the outer component
      // rather than the inner components (the default)
      addToOuterIfWrappedArray: true
    };
  }
  return shadowing;
}
function buildNumberDisplayParameters({
  padZeros,
  displayDigits,
  displayDecimals,
  avoidScientificNotation
} = {}) {
  let params = {};
  if (padZeros) {
    if (Number.isFinite(displayDecimals)) {
      params.padToDecimals = displayDecimals;
    }
    if (displayDigits >= 1) {
      params.padToDigits = displayDigits;
    }
  }
  if (avoidScientificNotation) {
    params.avoidScientificNotation = true;
  }
  return params;
}
function gatherRawNumberDisplayFixedResponseAttributes(component, components) {
  const rawAttrNames = [
    "fixed",
    "isResponse",
    ...Object.keys(returnNumberDisplayAttributes())
  ];
  let componentForRawAttributes = component;
  let attributesToConvert = {};
  while (rawAttrNames.length > 0) {
    const attrsFound = [];
    for (const attr of rawAttrNames) {
      if (attr in componentForRawAttributes.attributes) {
        if (componentForRawAttributes.attributes[attr].type === "unresolved") {
          attributesToConvert[attr] = componentForRawAttributes.attributes[attr];
        }
        attrsFound.push(attr);
        if (attr === "displayDigits") {
          attrsFound.push("displayDecimals");
        } else if (attr === "displayDecimals") {
          attrsFound.push("displayDigits");
        }
      }
    }
    if (componentForRawAttributes.doenetAttributes.extendListViaComposite) {
      const composite = components[componentForRawAttributes.doenetAttributes.extendListViaComposite];
      if (typeof composite.stateValues.extendIdx === "number" && composite.stateValues.extendIdx !== -1) {
        componentForRawAttributes = components[composite.stateValues.extendIdx];
      } else {
        break;
      }
    } else if (componentForRawAttributes.doenetAttributes.copyListViaComposite) {
      const composite = components[componentForRawAttributes.doenetAttributes.copyListViaComposite];
      if (typeof composite.stateValues.extendIdx === "number" && composite.stateValues.extendIdx !== -1) {
        componentForRawAttributes = components[composite.stateValues.extendIdx];
      } else {
        break;
      }
    } else {
      break;
    }
    for (const attr of attrsFound) {
      const idx = rawAttrNames.indexOf(attr);
      if (idx !== -1) {
        rawAttrNames.splice(idx, 1);
      }
    }
  }
  return attributesToConvert;
}
function addShadowNumberDisplayAttributes({
  nComponents,
  stateIdInfo,
  source,
  compositeIdx,
  attributes,
  componentInfoObjects
}) {
  const sourceSVs = componentInfoObjects.publicStateVariableInfo[source.componentType]?.stateVariableDescriptions;
  if (!sourceSVs) {
    return nComponents;
  }
  const numberDisplayAttrs = returnNumberDisplayAttributes();
  const origAttrNames = Object.keys(attributes);
  for (const attrName in numberDisplayAttrs) {
    if (origAttrNames.includes(attrName)) {
      continue;
    }
    if (attrName === "displayDigits" && origAttrNames.includes("displayDecimals") || attrName === "displayDecimals" && origAttrNames.includes("displayDigits")) {
      continue;
    }
    if (sourceSVs[attrName]?.createComponentOfType === numberDisplayAttrs[attrName].createComponentOfType) {
      const shadowComponent = {
        type: "serialized",
        componentType: numberDisplayAttrs[attrName].createComponentOfType,
        componentIdx: nComponents++,
        stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
        attributes: {},
        doenetAttributes: {},
        state: {},
        children: [],
        downstreamDependencies: {
          [source.componentIdx]: [
            {
              compositeIdx,
              dependencyType: "referenceShadow",
              propVariable: attrName
            }
          ]
        }
      };
      attributes[attrName] = {
        component: shadowComponent
      };
    }
  }
  return nComponents;
}
function createInputStringFromChildren({
  children,
  codePre,
  format: format2,
  createInternalLists = false,
  parser,
  createDisplayedMathString = false
}) {
  let nonStringInd = 0;
  let nonStringIndByChild = [];
  for (let child of children) {
    if (typeof child === "string") {
      nonStringIndByChild.push(null);
    } else {
      nonStringIndByChild.push(nonStringInd);
      nonStringInd++;
    }
  }
  let result = createInputStringFromChildrenSub({
    compositeReplacementRange: children.compositeReplacementRange,
    children,
    startInd: 0,
    endInd: children.length - 1,
    nonStringIndByChild,
    format: format2,
    codePre,
    createInternalLists,
    nextInternalListInd: nonStringInd,
    parser,
    createDisplayedMathString
  });
  let joinString = createDisplayedMathString ? " " : "";
  return {
    string: result.newChildren.join(joinString),
    internalLists: result.internalLists
  };
}
function createInputStringFromChildrenSub({
  compositeReplacementRange,
  children,
  startInd,
  endInd,
  nonStringIndByChild,
  format: format2,
  codePre,
  potentialListComponents,
  createInternalLists,
  nextInternalListInd,
  parser,
  createDisplayedMathString
}) {
  let newChildren = [];
  let newPotentialListComponents = [];
  let lastChildInd = startInd - 1;
  let internalLists = {};
  let leftDelimiters = ["{", "[", "(", "|", ","];
  let rightDelimiters = ["}", "]", ")", "|", ","];
  if (compositeReplacementRange) {
    for (let rangeInd = 0; rangeInd < compositeReplacementRange.length; rangeInd++) {
      let range = compositeReplacementRange[rangeInd];
      let rangeFirstInd = range.firstInd;
      let rangeLastInd = range.lastInd;
      if (rangeFirstInd > lastChildInd && rangeLastInd <= endInd) {
        if (lastChildInd + 1 < rangeFirstInd) {
          for (let ind = lastChildInd + 1; ind < rangeFirstInd; ind++) {
            newChildren.push(
              baseStringFromChildren({
                children,
                startInd: ind,
                endInd: ind,
                nonStringIndByChild,
                format: format2,
                codePre,
                createDisplayedMathString
              })
            );
          }
          if (potentialListComponents) {
            newPotentialListComponents.push(
              ...potentialListComponents.slice(
                lastChildInd - startInd + 1,
                rangeFirstInd - startInd
              )
            );
          }
        }
        let subReplacementRange = compositeReplacementRange.slice(
          rangeInd + 1
        );
        let {
          newChildren: childrenInRange,
          newPotentialListComponents: potentialListComponentsInRange,
          internalLists: newInternalLists
        } = createInputStringFromChildrenSub({
          compositeReplacementRange: subReplacementRange,
          children,
          startInd: rangeFirstInd,
          endInd: rangeLastInd,
          nonStringIndByChild,
          format: format2,
          codePre,
          potentialListComponents: range.potentialListComponents,
          createInternalLists,
          nextInternalListInd,
          parser,
          createDisplayedMathString
        });
        Object.assign(internalLists, newInternalLists);
        nextInternalListInd += Object.keys(newInternalLists).length;
        let allAreListComponents = potentialListComponentsInRange.every(
          (x2) => x2
        );
        if (range.asList && allAreListComponents && childrenInRange.length > 1) {
          let listString = childrenInRange.filter((v2) => v2.trim() !== "").map(
            (v2, i2, a2) => i2 === a2.length - 1 ? v2 : v2.trimEnd()
          ).join(", ");
          let wrap = false;
          if (rangeFirstInd > 0) {
            let prevInd = rangeFirstInd - 1;
            while (prevInd >= 0) {
              let prevChild = children[prevInd];
              if (typeof prevChild === "string") {
                prevChild = prevChild.trim();
                if (prevChild.length > 0) {
                  if (!leftDelimiters.includes(
                    prevChild[prevChild.length - 1]
                  )) {
                    wrap = true;
                  }
                  break;
                }
              } else {
                wrap = true;
              }
            }
          }
          if (!wrap) {
            if (rangeLastInd < children.length - 1) {
              let nextInd = rangeLastInd + 1;
              while (nextInd <= children.length - 1) {
                let nextChild = children[nextInd];
                if (typeof nextChild === "string") {
                  nextChild = nextChild.trim();
                  if (nextChild.length > 0) {
                    let nextChar = nextChild[0];
                    if (format2 === "latex" && nextChar === "\\" && nextChild.length > 1) {
                      nextChar = nextChild[1];
                    }
                    if (!rightDelimiters.includes(nextChar)) {
                      wrap = true;
                    }
                    break;
                  }
                } else {
                  wrap = true;
                }
              }
            }
          }
          if (wrap) {
            if (createInternalLists) {
              let code = codePre + nextInternalListInd;
              internalLists[code] = parser?.(listString) ?? "";
              nextInternalListInd++;
              listString = returnStringForCode(format2, code);
            } else if (!createDisplayedMathString) {
              listString = "(" + listString + ")";
            }
          }
          newChildren.push(listString);
        } else {
          let joinString = createDisplayedMathString ? " " : "";
          newChildren.push(childrenInRange.join(joinString));
        }
        if (potentialListComponents) {
          newPotentialListComponents.push(allAreListComponents);
        }
        lastChildInd = rangeLastInd;
      }
    }
  }
  if (lastChildInd < endInd) {
    for (let ind = lastChildInd + 1; ind <= endInd; ind++) {
      newChildren.push(
        baseStringFromChildren({
          children,
          startInd: ind,
          endInd: ind,
          nonStringIndByChild,
          format: format2,
          codePre,
          createDisplayedMathString
        })
      );
    }
    if (potentialListComponents) {
      newPotentialListComponents.push(
        ...potentialListComponents.slice(
          lastChildInd - startInd + 1,
          endInd - startInd + 1
        )
      );
    }
  }
  return {
    newChildren,
    newPotentialListComponents,
    internalLists
  };
}
function baseStringFromChildren({
  children,
  startInd,
  endInd,
  nonStringIndByChild,
  format: format2,
  codePre,
  createDisplayedMathString
}) {
  if (createDisplayedMathString) {
    return displayedMathStringFromChildren({ children, startInd, endInd });
  }
  let str = "";
  for (let ind = startInd; ind <= endInd; ind++) {
    const child = children[ind];
    if (typeof child === "string") {
      const leadingSpace = typeof children[ind - 1] === "string" ? "" : " ";
      const trailingSpace = typeof children[ind + 1] === "string" ? "" : " ";
      str += leadingSpace + child + trailingSpace;
    } else {
      const code = codePre + nonStringIndByChild[ind];
      const nextString = returnStringForCode(format2, code);
      str += nextString;
    }
  }
  return str;
}
function returnStringForCode(format2, code) {
  let nextString;
  if (format2 === "latex") {
    nextString = "\\operatorname{" + code + "}";
  } else {
    nextString = " " + code + " ";
  }
  return nextString;
}
function displayedMathStringFromChildren({
  children,
  startInd,
  endInd
}) {
  let pieces = [];
  for (let ind = startInd; ind <= endInd; ind++) {
    let child = children[ind];
    if (typeof child !== "object") {
      let childTrim = String(child).trim();
      if (childTrim) {
        pieces.push(childTrim);
      }
    } else if (typeof child.stateValues.latex === "string") {
      let latex = child.stateValues.latex.trim();
      if (latex) {
        pieces.push(latex);
      }
    } else if (typeof child.stateValues.text === "string") {
      let text = child.stateValues.text.trim();
      if (text) {
        pieces.push(text);
      }
    }
  }
  return pieces.join(" ");
}
const vectorAndListOperators$2 = ["list", ...vectorOperators];
function returnMathVectorMatrixStateVariableDefinitions() {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.numDimensions = {
    description: "The number of dimensions if the math expression is interpreted as a vector or matrix.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    returnDependencies: () => ({
      value: {
        dependencyType: "stateVariable",
        variableName: "value"
      }
    }),
    definition({ dependencyValues }) {
      let numDimensions = 1;
      let tree = dependencyValues.value.tree;
      if (Array.isArray(tree)) {
        if (vectorAndListOperators$2.includes(tree[0])) {
          numDimensions = tree.length - 1;
        } else if (tree[0] === "matrix") {
          let size = tree[1].slice(1);
          if (size[0] === 1) {
            numDimensions = size[1];
          } else if (size[1] === 1) {
            numDimensions = size[0];
          }
        } else if (vectorOperators.includes(tree[1]?.[0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
          numDimensions = tree[1].length - 1;
        }
      }
      return { setValue: { numDimensions } };
    }
  };
  stateVariableDefinitions.vector = {
    description: "The math expression interpreted as a vector (its components).",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "math",
      addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
      returnWrappingComponents(prefix) {
        if (prefix === "x") {
          return [];
        } else {
          return [
            [
              "vector",
              {
                componentType: "mathList",
                isAttributeNamed: "xs"
              }
            ]
          ];
        }
      }
    },
    isArray: true,
    entryPrefixes: ["x"],
    returnArraySizeDependencies: () => ({
      numDimensions: {
        dependencyType: "stateVariable",
        variableName: "numDimensions"
      }
    }),
    returnArraySize({
      dependencyValues
    }) {
      return [dependencyValues.numDimensions];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({
      globalDependencyValues,
      arraySize
    }) {
      let tree = globalDependencyValues.value.tree;
      let createdVector = false;
      let vector = {};
      if (Array.isArray(tree)) {
        if (vectorAndListOperators$2.includes(tree[0])) {
          for (let ind = 0; ind < arraySize[0]; ind++) {
            vector[ind] = me$3.fromAst(tree[ind + 1]);
          }
          createdVector = true;
        } else if (tree[0] === "matrix") {
          let size = tree[1].slice(1);
          if (size[0] === 1) {
            for (let ind = 0; ind < arraySize[0]; ind++) {
              vector[ind] = me$3.fromAst(tree[2][1][ind + 1]);
            }
            createdVector = true;
          } else if (size[1] === 1) {
            for (let ind = 0; ind < arraySize[0]; ind++) {
              vector[ind] = me$3.fromAst(tree[2][ind + 1][1]);
            }
            createdVector = true;
          }
        } else if (vectorOperators.includes(tree[1]?.[0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
          for (let ind = 0; ind < arraySize[0]; ind++) {
            vector[ind] = me$3.fromAst(tree[1][ind + 1]);
          }
          createdVector = true;
        }
      }
      if (!createdVector) {
        vector[0] = globalDependencyValues.value;
      }
      return { setValue: { vector } };
    },
    async inverseArrayDefinitionByKey({
      desiredStateVariableValues,
      globalDependencyValues,
      stateValues,
      workspace,
      arraySize
    }) {
      if (!workspace.desiredVector) {
        workspace.desiredVector = [];
      }
      for (let ind = 0; ind < arraySize[0]; ind++) {
        if (desiredStateVariableValues.vector[ind] !== void 0) {
          workspace.desiredVector[ind] = convertValueToMathExpression(
            desiredStateVariableValues.vector[ind]
          );
        } else if (workspace.desiredVector[ind] === void 0) {
          workspace.desiredVector[ind] = (await stateValues.vector)[ind];
        }
      }
      let desiredValue;
      let tree = globalDependencyValues.value.tree;
      if (Array.isArray(tree)) {
        if (vectorAndListOperators$2.includes(tree[0])) {
          desiredValue = me$3.fromAst([
            tree[0],
            ...workspace.desiredVector.map((x2) => x2.tree)
          ]);
        } else if (tree[0] === "matrix") {
          let size = tree[1].slice(1);
          if (size[0] === 1) {
            let desiredMatrixVals = ["tuple"];
            for (let ind = 0; ind < arraySize[0]; ind++) {
              desiredMatrixVals.push(
                workspace.desiredVector[ind].tree
              );
            }
            desiredMatrixVals = ["tuple", desiredMatrixVals];
            desiredValue = me$3.fromAst([
              "matrix",
              tree[1],
              desiredMatrixVals
            ]);
          } else if (size[1] === 1) {
            let desiredMatrixVals = ["tuple"];
            for (let ind = 0; ind < arraySize[0]; ind++) {
              desiredMatrixVals.push([
                "tuple",
                workspace.desiredVector[ind].tree
              ]);
            }
            desiredValue = me$3.fromAst([
              "matrix",
              tree[1],
              desiredMatrixVals
            ]);
          }
        } else if (vectorOperators.includes(tree[1]?.[0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
          const desiredTree = [
            tree[0],
            [
              tree[1][0],
              ...workspace.desiredVector.map(
                (x2) => x2.tree
              )
            ]
          ];
          if (tree[2]) {
            desiredTree.push(tree[2]);
          }
          desiredValue = me$3.fromAst(desiredTree);
        }
      }
      if (!desiredValue) {
        desiredValue = workspace.desiredVector[0];
      }
      let instructions = [
        {
          setDependency: "value",
          desiredValue
        }
      ];
      return {
        success: true,
        instructions
      };
    }
  };
  stateVariableDefinitions.x = {
    isAlias: true,
    targetVariableName: "x1",
    description: "The first component of the math expression when interpreted as a vector."
  };
  stateVariableDefinitions.y = {
    isAlias: true,
    targetVariableName: "x2",
    description: "The second component of the math expression when interpreted as a vector."
  };
  stateVariableDefinitions.z = {
    isAlias: true,
    targetVariableName: "x3",
    description: "The third component of the math expression when interpreted as a vector."
  };
  stateVariableDefinitions.numListItems = {
    isAlias: true,
    targetVariableName: "numDimensions",
    description: "The number of items when the math expression is interpreted as a list."
  };
  stateVariableDefinitions.list = {
    description: "The math expression interpreted as a list (its elements).",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "math",
      addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
    },
    isArray: true,
    entryPrefixes: ["listItem"],
    returnArraySizeDependencies: () => ({
      numDimensions: {
        dependencyType: "stateVariable",
        variableName: "numDimensions"
      }
    }),
    returnArraySize({
      dependencyValues
    }) {
      return [dependencyValues.numDimensions];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        vector: {
          dependencyType: "stateVariable",
          variableName: "vector"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({
      globalDependencyValues
    }) {
      return { setValue: { list: globalDependencyValues.vector } };
    },
    async inverseArrayDefinitionByKey({
      desiredStateVariableValues
    }) {
      let instructions = [
        {
          setDependency: "vector",
          desiredValue: desiredStateVariableValues.list
        }
      ];
      return {
        success: true,
        instructions
      };
    }
  };
  stateVariableDefinitions.matrixSize = {
    description: "The size of the math expression as a matrix, as a [numRows, numColumns] list.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "numberList"
    },
    returnDependencies: () => ({
      value: {
        dependencyType: "stateVariable",
        variableName: "value"
      }
    }),
    definition({ dependencyValues }) {
      let matrixSize = [1, 1];
      let tree = dependencyValues.value.tree;
      if (Array.isArray(tree)) {
        if (vectorAndListOperators$2.includes(tree[0])) {
          matrixSize = [tree.length - 1, 1];
        } else if (tree[0] === "matrix") {
          matrixSize = tree[1].slice(1);
        } else if (vectorOperators.includes(tree[1]?.[0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
          matrixSize = [1, tree[1].length - 1];
        }
      }
      return { setValue: { matrixSize } };
    }
  };
  stateVariableDefinitions.numRows = {
    description: "The number of rows when the math expression is interpreted as a matrix.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    returnDependencies: () => ({
      matrixSize: {
        dependencyType: "stateVariable",
        variableName: "matrixSize"
      }
    }),
    definition({
      dependencyValues
    }) {
      return {
        setValue: { numRows: dependencyValues.matrixSize[0] }
      };
    }
  };
  stateVariableDefinitions.numColumns = {
    description: "The number of columns when the math expression is interpreted as a matrix.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    returnDependencies: () => ({
      matrixSize: {
        dependencyType: "stateVariable",
        variableName: "matrixSize"
      }
    }),
    definition({
      dependencyValues
    }) {
      return {
        setValue: { numColumns: dependencyValues.matrixSize[1] }
      };
    }
  };
  stateVariableDefinitions.matrix = {
    description: "The math expression interpreted as a matrix (its entries by row and column).",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "math",
      addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
      returnWrappingComponents(prefix) {
        if (prefix === "matrixEntry") {
          return [];
        } else if (prefix === "row") {
          return [["matrix", "matrixRow"]];
        } else if (prefix === "column") {
          return [["matrix", "matrixColumn"]];
        } else {
          return [["matrixRow"], ["matrix"]];
        }
      }
    },
    isArray: true,
    numDimensions: 2,
    entryPrefixes: ["matrixEntry", "row", "column", "rows", "columns"],
    schemaSubarrays: {
      rows: {
        numDimensions: 2,
        description: "The matrix's entries grouped by row."
      },
      columns: {
        numDimensions: 2,
        description: "The matrix's entries grouped by column."
      }
    },
    returnEntryDimensions: (prefix) => {
      if (prefix === "matrixEntry") {
        return 0;
      } else if (prefix === "rows" || prefix === "columns") {
        return 2;
      } else {
        return 1;
      }
    },
    getArrayKeysFromVarName({
      arrayEntryPrefix,
      varEnding,
      arraySize
    }) {
      if (arrayEntryPrefix === "matrixEntry") {
        let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
        if (indices.length === 2 && indices.every((x2) => Number.isInteger(x2) && x2 >= 0)) {
          if (arraySize) {
            if (indices.every((x2, i2) => x2 < arraySize[i2])) {
              return [String(indices)];
            } else {
              return [];
            }
          } else {
            return [String(indices)];
          }
        } else {
          return [];
        }
      } else if (arrayEntryPrefix === "row") {
        let rowInd = Number(varEnding) - 1;
        if (!(Number.isInteger(rowInd) && rowInd >= 0)) {
          return [];
        }
        if (!arraySize) {
          return [rowInd + ",0"];
        }
        if (rowInd < arraySize[0]) {
          return Array.from(
            Array(arraySize[1]),
            (_2, i2) => rowInd + "," + i2
          );
        } else {
          return [];
        }
      } else if (arrayEntryPrefix === "column") {
        let colInd = Number(varEnding) - 1;
        if (!(Number.isInteger(colInd) && colInd >= 0)) {
          return [];
        }
        if (!arraySize) {
          return ["0," + colInd];
        }
        if (colInd < arraySize[1]) {
          return Array.from(
            Array(arraySize[0]),
            (_2, i2) => i2 + "," + colInd
          );
        } else {
          return [];
        }
      } else if (arrayEntryPrefix === "rows" || arrayEntryPrefix === "columns") {
        if (!arraySize) {
          return ["0,0"];
        }
        let keys2 = [];
        for (let rowInd = 0; rowInd < arraySize[0]; rowInd++) {
          keys2.push(
            ...Array.from(
              Array(arraySize[1]),
              (_2, i2) => rowInd + "," + i2
            )
          );
        }
        return keys2;
      }
    },
    arrayVarNameFromPropIndex(propIndex, varName) {
      if (varName === "matrix" || varName === "rows") {
        if (propIndex.length === 1) {
          return "row" + propIndex[0];
        } else {
          return `matrixEntry${propIndex[0]}_${propIndex[1]}`;
        }
      }
      if (varName === "columns") {
        if (propIndex.length === 1) {
          return "column" + propIndex[0];
        } else {
          return `matrixEntry${propIndex[1]}_${propIndex[0]}`;
        }
      }
      if (varName.slice(0, 3) === "row") {
        let rowNum = Number(varName.slice(3));
        if (Number.isInteger(rowNum) && rowNum > 0) {
          return `matrixEntry${rowNum}_${propIndex[0]}`;
        }
      }
      if (varName.slice(0, 6) === "column") {
        let colNum = Number(varName.slice(6));
        if (Number.isInteger(colNum) && colNum > 0) {
          return `matrixEntry${propIndex[0]}_${colNum}`;
        }
      }
      return null;
    },
    returnArraySizeDependencies: () => ({
      matrixSize: {
        dependencyType: "stateVariable",
        variableName: "matrixSize"
      }
    }),
    returnArraySize({
      dependencyValues
    }) {
      return dependencyValues.matrixSize;
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({
      globalDependencyValues,
      arraySize
    }) {
      let tree = globalDependencyValues.value.tree;
      let createdMatrix = false;
      let matrix2 = {};
      if (Array.isArray(tree)) {
        if (vectorAndListOperators$2.includes(tree[0])) {
          for (let ind = 0; ind < arraySize[0]; ind++) {
            matrix2[ind + ",0"] = me$3.fromAst(tree[ind + 1]);
          }
          createdMatrix = true;
        } else if (tree[0] === "matrix") {
          let matVals = tree[2];
          for (let i2 = 0; i2 < arraySize[0]; i2++) {
            for (let j2 = 0; j2 < arraySize[1]; j2++) {
              matrix2[`${i2},${j2}`] = me$3.fromAst(
                matVals[i2 + 1][j2 + 1]
              );
            }
          }
          createdMatrix = true;
        } else if (vectorOperators.includes(tree[1]?.[0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
          for (let ind = 0; ind < arraySize[1]; ind++) {
            matrix2["0," + ind] = me$3.fromAst(tree[1][ind + 1]);
          }
          createdMatrix = true;
        }
      }
      if (!createdMatrix) {
        matrix2["0,0"] = globalDependencyValues.value;
      }
      return { setValue: { matrix: matrix2 } };
    },
    async inverseArrayDefinitionByKey({
      desiredStateVariableValues,
      globalDependencyValues,
      stateValues,
      workspace,
      arraySize
    }) {
      if (!workspace.desiredMatrix) {
        workspace.desiredMatrix = {};
      }
      for (let i2 = 0; i2 < arraySize[0]; i2++) {
        for (let j2 = 0; j2 < arraySize[1]; j2++) {
          let arrayKey = i2 + "," + j2;
          if (desiredStateVariableValues.matrix[arrayKey] !== void 0) {
            workspace.desiredMatrix[arrayKey] = convertValueToMathExpression(
              desiredStateVariableValues.matrix[arrayKey]
            );
          } else if (workspace.desiredMatrix[arrayKey] === void 0) {
            workspace.desiredMatrix[arrayKey] = (await stateValues.matrix)[i2][j2];
          }
        }
      }
      let desiredValue;
      let tree = globalDependencyValues.value.tree;
      if (Array.isArray(tree)) {
        if (vectorAndListOperators$2.includes(tree[0])) {
          desiredValue = me$3.fromAst([
            tree[0],
            ...Array.from(
              { length: arraySize[0] },
              (_2, ind) => workspace.desiredMatrix[`${ind},0`].tree
            )
          ]);
        } else if (tree[0] === "matrix") {
          let desiredMatrixVals = ["tuple"];
          for (let i2 = 0; i2 < arraySize[0]; i2++) {
            let row = ["tuple"];
            for (let j2 = 0; j2 < arraySize[1]; j2++) {
              row.push(
                workspace.desiredMatrix[`${i2},${j2}`].tree
              );
            }
            desiredMatrixVals.push(row);
          }
          desiredValue = me$3.fromAst([
            "matrix",
            tree[1],
            desiredMatrixVals
          ]);
        } else if (vectorOperators.includes(tree[1]?.[0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
          let desiredTree = [tree[0]];
          let desiredVector = [tree[1][0]];
          for (let ind = 0; ind < arraySize[1]; ind++) {
            desiredVector.push(
              workspace.desiredMatrix["0," + ind].tree
            );
          }
          desiredTree = [tree[0], desiredVector];
          if (tree[2]) {
            desiredTree.push(tree[2]);
          }
          desiredValue = me$3.fromAst(desiredTree);
        }
      }
      if (!desiredValue) {
        desiredValue = workspace.desiredMatrix["0,0"];
      }
      let instructions = [
        {
          setDependency: "value",
          desiredValue
        }
      ];
      return {
        success: true,
        instructions
      };
    }
  };
  return stateVariableDefinitions;
}
const vectorAndListOperators$1 = ["list", ...vectorOperators];
class MathComponent extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveMath: this.moveMath.bind(this),
      mathClicked: this.mathClicked.bind(this),
      mathFocused: this.mathFocused.bind(this)
    });
  }
  static componentType = "math";
  static componentDocs = {
    summary: "A math expression"
  };
  // used when creating new component via adapter or copy prop
  static primaryStateVariableForDefinition = "unnormalizedValue";
  // for copying a property with link="false"
  // make sure it doesn't use the essential state variable unnormalizedValue
  static primaryEssentialStateVariable = "value";
  static variableForImplicitProp = "value";
  static implicitPropReturnsSameType = true;
  static variableForIndexAsProp = "vector";
  static descendantCompositesMustHaveAReplacement = true;
  static descendantCompositesDefaultReplacementType = "math";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.format = {
      description: "Input format.",
      createComponentOfType: "text",
      createStateVariable: "format",
      defaultValue: "text",
      public: true,
      highlighted: true,
      toLowerCase: true,
      validValues: [
        {
          value: "text",
          description: "Plain-text math notation (e.g., `x^2 + 1`)."
        },
        {
          value: "latex",
          description: "LaTeX-formatted math (e.g., `x^{2} + 1`)."
        }
      ]
    };
    attributes.simplify = {
      description: "Level of simplification applied to the expression.",
      createComponentOfType: "text",
      createStateVariable: "simplify",
      defaultValue: "none",
      public: true,
      highlighted: true,
      toLowerCase: true,
      valueForTrue: "full",
      valueForFalse: "none",
      validValues: [
        {
          value: "none",
          description: "No simplification is applied."
        },
        {
          value: "full",
          description: "Fully simplify the expression."
        },
        {
          value: "numbers",
          description: "Simplify numeric subexpressions only, leaving symbolic structure intact."
        },
        {
          value: "numbersPreserveOrder",
          description: "Like `numbers`, but does not reorder commutative operands."
        },
        {
          value: "normalizeOrder",
          description: "Reorder commutative operands into a canonical form without simplifying values."
        }
      ]
    };
    attributes.expand = {
      description: "Whether to expand the expression.",
      createComponentOfType: "boolean",
      createStateVariable: "expand",
      defaultValue: false,
      public: true
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.displayDigits.highlighted = true;
    attributes.renderMode = {
      description: 'How the math is rendered (e.g. "inline", "display").',
      createComponentOfType: "text",
      createStateVariable: "renderMode",
      defaultValue: "inline",
      public: true,
      forRenderer: true
    };
    attributes.unordered = {
      createComponentOfType: "boolean",
      description: "Whether tuple- or list-like math expressions should be treated as unordered for comparison."
    };
    attributes.createVectors = {
      description: "Whether tuple-like expressions are interpreted as vectors.",
      createComponentOfType: "boolean",
      createStateVariable: "createVectors",
      defaultValue: false,
      public: true
    };
    attributes.createIntervals = {
      description: "Whether range expressions are interpreted as intervals.",
      createComponentOfType: "boolean",
      createStateVariable: "createIntervals",
      defaultValue: false,
      public: true
    };
    attributes.functionSymbols = {
      description: "Symbols treated as function names when parsing.",
      createComponentOfType: "textList",
      createStateVariable: "functionSymbols",
      defaultValue: ["f", "g"],
      public: true,
      fallBackToParentStateVariable: "functionSymbols",
      fallBackToSourceCompositeStateVariable: "functionSymbols"
    };
    attributes.referencesAreFunctionSymbols = {
      createReferences: true,
      createStateVariable: "referencesAreFunctionSymbols",
      defaultValue: [],
      fallBackToParentStateVariable: "referencesAreFunctionSymbols",
      fallBackToSourceCompositeStateVariable: "referencesAreFunctionSymbols",
      description: "References whose names should be treated as function symbols when parsing."
    };
    attributes.splitSymbols = {
      description: "Whether multi-character symbols are split into a product of variables.",
      createComponentOfType: "boolean",
      createStateVariable: "splitSymbols",
      defaultValue: true,
      public: true,
      fallBackToParentStateVariable: "splitSymbols",
      fallBackToSourceCompositeStateVariable: "splitSymbols"
    };
    attributes.parseScientificNotation = {
      description: "Whether to parse expressions like 1e3 as scientific notation.",
      createComponentOfType: "boolean",
      createStateVariable: "parseScientificNotation",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "parseScientificNotation"
    };
    attributes.displayBlanks = {
      description: "Whether blanks (placeholders) are visibly rendered.",
      createComponentOfType: "boolean",
      createStateVariable: "displayBlanks",
      defaultValue: true,
      public: true
    };
    attributes.assumptions = {
      description: "Assumptions applied when simplifying or comparing.",
      createComponentOfType: "math",
      createStateVariable: "assumptions",
      defaultValue: me$3.fromAst("＿"),
      // long underscore
      public: true
    };
    attributes.draggable = {
      description: "Whether the math component can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.layer = {
      description: "Z-order layer index when shown on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      },
      {
        group: "strings",
        componentTypes: ["string"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({
      childGroupsIfSingleMatch: ["maths"],
      childGroupsToStopSingleMatch: ["strings"]
    });
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.valueShadow = {
      defaultValue: me$3.fromAst("＿"),
      // long underscore
      hasEssential: true,
      essentialVarName: "value",
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          valueShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "valueShadow",
              value: desiredStateVariableValues.valueShadow
            }
          ]
        };
      }
    };
    stateVariableDefinitions.unordered = {
      description: "Whether list-like values are treated as unordered.",
      defaultValue: false,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      hasEssential: true,
      returnDependencies: () => ({
        unorderedAttr: {
          dependencyType: "attributeComponent",
          attributeName: "unordered",
          variableNames: ["value"]
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["unordered"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.unorderedAttr === null) {
          if (dependencyValues.mathChildren.length > 0) {
            let unordered = dependencyValues.mathChildren.every(
              (x2) => x2.stateValues.unordered
            );
            return { setValue: { unordered } };
          } else {
            return {
              useEssentialOrDefaultValue: {
                unordered: true
              }
            };
          }
        } else {
          return {
            setValue: {
              unordered: dependencyValues.unorderedAttr.stateValues.value
            }
          };
        }
      }
    };
    stateVariableDefinitions.inUnorderedList = {
      returnDependencies: () => ({
        sourceCompositeUnordered: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "unordered",
          skipCopies: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.sourceCompositeUnordered !== null && !usedDefault.sourceCompositeUnordered) {
          return {
            setValue: {
              inUnorderedList: Boolean(
                dependencyValues.sourceCompositeUnordered
              )
            }
          };
        } else {
          return {
            setValue: {
              inUnorderedList: false
            }
          };
        }
      }
    };
    stateVariableDefinitions.codePre = {
      // deferCalculation: false,
      returnDependencies: () => ({
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"]
        }
      }),
      definition({ dependencyValues }) {
        let codePre = "math";
        let foundInString = false;
        do {
          foundInString = false;
          for (let child of dependencyValues.stringChildren) {
            if (child.includes(codePre) === true) {
              foundInString = true;
              codePre += "m";
              break;
            }
          }
        } while (foundInString);
        return { setValue: { codePre } };
      }
    };
    stateVariableDefinitions.mathChildrenFunctionSymbols = {
      returnDependencies: () => ({
        referencesAreFunctionSymbols: {
          dependencyType: "stateVariable",
          variableName: "referencesAreFunctionSymbols"
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"]
        }
      }),
      definition({ dependencyValues }) {
        let mathChildrenFunctionSymbols = [];
        if (dependencyValues.mathChildren.compositeReplacementRange) {
          for (let compositeInfo of dependencyValues.mathChildren.compositeReplacementRange) {
            if (dependencyValues.referencesAreFunctionSymbols.some(
              (reference) => reference.componentIdx === compositeInfo.extendIdx && deepCompare(
                reference.unresolvedPath,
                compositeInfo.unresolvedPath
              )
            )) {
              for (let ind = compositeInfo.firstInd; ind <= compositeInfo.lastInd; ind++) {
                mathChildrenFunctionSymbols.push(ind);
              }
            }
          }
        }
        return { setValue: { mathChildrenFunctionSymbols } };
      }
    };
    stateVariableDefinitions.expressionWithCodes = {
      hasEssential: true,
      doNotShadowEssential: true,
      returnDependencies: () => ({
        stringMathChildren: {
          dependencyType: "child",
          childGroups: ["strings", "maths"]
        },
        // have stringChildren and mathChildren just for inverse definition
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"]
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"]
        },
        format: {
          dependencyType: "stateVariable",
          variableName: "format"
        },
        codePre: {
          dependencyType: "stateVariable",
          variableName: "codePre"
        },
        functionSymbols: {
          dependencyType: "stateVariable",
          variableName: "functionSymbols"
        },
        mathChildrenFunctionSymbols: {
          dependencyType: "stateVariable",
          variableName: "mathChildrenFunctionSymbols"
        },
        splitSymbols: {
          dependencyType: "stateVariable",
          variableName: "splitSymbols"
        },
        parseScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "parseScientificNotation"
        }
      }),
      set: (x2) => x2 === null ? null : convertValueToMathExpression(x2),
      definition: calculateExpressionWithCodes,
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        let newExpressionWithCodes = desiredStateVariableValues.expressionWithCodes;
        let instructions = [
          {
            setEssentialValue: "expressionWithCodes",
            value: newExpressionWithCodes
          }
        ];
        let nStringChildren = dependencyValues.stringChildren.length;
        if (nStringChildren === 0) {
          return { success: false };
        }
        if (dependencyValues.mathChildren.length === 0) {
          let stringValue;
          if (await stateValues.format === "latex") {
            stringValue = newExpressionWithCodes.toLatex();
          } else {
            stringValue = newExpressionWithCodes.toString();
          }
          instructions.push({
            setDependency: "stringChildren",
            desiredValue: stringValue,
            childIndex: 0,
            variableIndex: 0,
            ignoreChildChangeForComponent: true
          });
          for (let ind = 1; ind < nStringChildren; ind++) {
            instructions.push({
              setDependency: "stringChildren",
              desiredValue: "",
              childIndex: ind,
              variableIndex: 0,
              ignoreChildChangeForComponent: true
            });
          }
        } else {
          let stringExpr;
          if (await stateValues.format === "latex") {
            stringExpr = newExpressionWithCodes.toLatex();
          } else {
            stringExpr = newExpressionWithCodes.toString();
          }
          for (let [ind, stringCodes] of (await stateValues.codesAdjacentToStrings).entries()) {
            let thisString = stringExpr;
            if (Object.keys(stringCodes).length === 0) {
              instructions.push({
                setDependency: "stringChildren",
                desiredValue: "",
                childIndex: ind,
                variableIndex: 0,
                ignoreChildChangeForComponent: true
              });
            } else {
              if (stringCodes.prevCode) {
                thisString = thisString.split(
                  stringCodes.prevCode
                )[1];
              }
              if (stringCodes.nextCode) {
                thisString = thisString.split(
                  stringCodes.nextCode
                )[0];
              }
              instructions.push({
                setDependency: "stringChildren",
                desiredValue: thisString,
                childIndex: ind,
                variableIndex: 0,
                ignoreChildChangeForComponent: true
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.mathChildrenWithCanBeModified = {
      returnDependencies: () => ({
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value", "canBeModified"]
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          mathChildrenWithCanBeModified: dependencyValues.mathChildren
        }
      })
    };
    stateVariableDefinitions.unnormalizedValue = {
      isLocation: true,
      returnDependencies: () => ({
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        },
        // Note: need stringChildren for inverse definition
        // (even though not in definition)
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"],
          variableNames: ["value"]
        },
        expressionWithCodes: {
          dependencyType: "stateVariable",
          variableName: "expressionWithCodes"
        },
        codePre: {
          dependencyType: "stateVariable",
          variableName: "codePre"
        },
        valueShadow: {
          dependencyType: "stateVariable",
          variableName: "valueShadow"
        }
      }),
      set: convertValueToMathExpression,
      hasEssential: true,
      defaultValue: me$3.fromAst("＿"),
      // long underscore
      definition: calculateMathValue,
      inverseDefinition: invertMath
    };
    stateVariableDefinitions.value = {
      description: "The math expression value.",
      isLocation: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: this.componentType,
        attributesToShadow: ["unordered", "simplify", "expand"],
        // the reason we create a attribute component from the state variable fixed,
        // rather than just shadowing the attribute,
        // is that a sequence creates a math where it sets fixed directly in the state
        addAttributeComponentsShadowingStateVariables: {
          fixed: {
            stateVariableToShadow: "fixed"
          },
          ...returnNumberDisplayAttributeComponentShadowing()
        }
      },
      returnDependencies: () => ({
        unnormalizedValue: {
          dependencyType: "stateVariable",
          variableName: "unnormalizedValue"
        },
        simplify: {
          dependencyType: "stateVariable",
          variableName: "simplify"
        },
        expand: {
          dependencyType: "stateVariable",
          variableName: "expand"
        },
        createVectors: {
          dependencyType: "stateVariable",
          variableName: "createVectors"
        },
        createIntervals: {
          dependencyType: "stateVariable",
          variableName: "createIntervals"
        },
        assumptions: {
          dependencyType: "stateVariable",
          variableName: "assumptions"
        }
      }),
      definition: function({ dependencyValues }) {
        let value = dependencyValues.unnormalizedValue;
        let {
          simplify,
          expand,
          createVectors,
          createIntervals,
          assumptions
        } = dependencyValues;
        value = normalizeMathExpression({
          value,
          simplify,
          expand,
          createVectors,
          createIntervals,
          assumptions
        });
        return { setValue: { value } };
      },
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "unnormalizedValue",
              desiredValue: desiredStateVariableValues.value
            }
          ]
        };
      }
    };
    stateVariableDefinitions.number = {
      description: "The numeric value of the expression (NaN if not a number).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: function({ dependencyValues }) {
        let number = dependencyValues.value.evaluate_to_constant();
        return { setValue: { number } };
      },
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "value",
              desiredValue: me$3.fromAst(
                desiredStateVariableValues.number
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.isNumber = {
      description: "Whether the expression evaluates to a finite number.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            isNumber: Number.isFinite(dependencyValues.value.tree)
          }
        };
      }
    };
    stateVariableDefinitions.isNumeric = {
      description: "Whether the expression evaluates to any number (including infinities).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        number: {
          dependencyType: "stateVariable",
          variableName: "number"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            isNumeric: Number.isFinite(dependencyValues.number)
          }
        };
      }
    };
    stateVariableDefinitions.valueForDisplay = {
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        simplify: {
          dependencyType: "stateVariable",
          variableName: "simplify"
        },
        expand: {
          dependencyType: "stateVariable",
          variableName: "expand"
        }
      }),
      definition: function({ dependencyValues }) {
        let value = dependencyValues.value;
        let rounded = roundForDisplay({
          value,
          dependencyValues
        });
        return {
          setValue: {
            valueForDisplay: normalizeMathExpression({
              value: rounded,
              simplify: dependencyValues.simplify,
              expand: dependencyValues.expand
            })
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "value",
              desiredValue: desiredStateVariableValues.valueForDisplay
            }
          ]
        };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The expression rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displayBlanks: {
          dependencyType: "stateVariable",
          variableName: "displayBlanks"
        }
      }),
      definition: function({ dependencyValues }) {
        let latex;
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        if (!dependencyValues.displayBlanks) {
          params.showBlanks = false;
        }
        try {
          latex = dependencyValues.valueForDisplay.toLatex(params);
        } catch (e3) {
          if (dependencyValues.displayBlanks) {
            latex = "＿";
          } else {
            latex = "";
          }
        }
        return { setValue: { latex } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let value;
        try {
          value = me$3.fromLatex(desiredStateVariableValues.latex);
        } catch (e3) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "valueForDisplay",
              desiredValue: value
            }
          ]
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The expression rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        value: {
          dependencyType: "stateVariable",
          variableName: "value",
          onlyToSetInInverseDefinition: true
        },
        displayBlanks: {
          dependencyType: "stateVariable",
          variableName: "displayBlanks"
        }
      }),
      definition: function({ dependencyValues }) {
        let text;
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        if (!dependencyValues.displayBlanks) {
          params.showBlanks = false;
        }
        try {
          text = dependencyValues.valueForDisplay.toString(params);
        } catch (e3) {
          if (dependencyValues.displayBlanks) {
            text = "＿";
          } else {
            text = "";
          }
        }
        return {
          setValue: {
            text: superSubscriptsToUnicode(text)
          }
        };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        stateValues
      }) {
        let fromText = textToMathFactory({
          functionSymbols: await stateValues.functionSymbols,
          splitSymbols: await stateValues.splitSymbols,
          parseScientificNotation: await stateValues.parseScientificNotation
        });
        let expr;
        try {
          expr = fromText(
            unicodeToSuperSubscripts(
              desiredStateVariableValues.text
            )
          );
        } catch (e3) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "value",
              desiredValue: expr
            }
          ]
        };
      }
    };
    stateVariableDefinitions.codesAdjacentToStrings = {
      returnDependencies: () => ({
        stringMathChildren: {
          dependencyType: "child",
          childGroups: ["strings", "maths"]
        },
        codePre: {
          dependencyType: "stateVariable",
          variableName: "codePre"
        },
        format: {
          dependencyType: "stateVariable",
          variableName: "format"
        }
      }),
      definition: calculateCodesAdjacentToStrings
    };
    stateVariableDefinitions.canBeModified = {
      additionalStateVariablesDefined: [
        "constantChildIndices",
        "codeForExpression",
        "inverseMaps",
        "template",
        "mathChildrenMapped"
      ],
      returnDependencies: () => ({
        mathChildrenModifiable: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["canBeModified"]
        },
        expressionWithCodes: {
          dependencyType: "stateVariable",
          variableName: "expressionWithCodes"
        },
        modifyIndirectly: {
          dependencyType: "stateVariable",
          variableName: "modifyIndirectly"
        },
        fixed: {
          dependencyType: "stateVariable",
          variableName: "fixed"
        },
        fixLocation: {
          dependencyType: "stateVariable",
          variableName: "fixLocation"
        },
        codePre: {
          dependencyType: "stateVariable",
          variableName: "codePre"
        }
      }),
      definition: determineCanBeModified
    };
    stateVariableDefinitions.mathChildrenByVectorComponent = {
      returnDependencies: () => ({
        codePre: {
          dependencyType: "stateVariable",
          variableName: "codePre"
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"]
        },
        expressionWithCodes: {
          dependencyType: "stateVariable",
          variableName: "expressionWithCodes"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.expressionWithCodes === null) {
          return {
            setValue: { mathChildrenByVectorComponent: null }
          };
        }
        let expressionWithCodesTree = dependencyValues.expressionWithCodes.tree;
        let nMathChildren = dependencyValues.mathChildren.length;
        if (nMathChildren === 0 || !Array.isArray(expressionWithCodesTree) || !vectorOperators.includes(expressionWithCodesTree[0])) {
          return {
            setValue: { mathChildrenByVectorComponent: null }
          };
        }
        let mathChildrenByVectorComponent = {};
        let childInd = 0;
        let childCode = dependencyValues.codePre + childInd;
        for (let ind = 1; ind < expressionWithCodesTree.length; ind++) {
          let exprComp = expressionWithCodesTree[ind];
          let mc = mathChildrenByVectorComponent[ind] = [];
          if (Array.isArray(exprComp)) {
            let flattenedComp = flattenDeep(exprComp);
            while (flattenedComp.includes(childCode)) {
              mc.push(childInd);
              childInd++;
              childCode = dependencyValues.codePre + childInd;
            }
          } else {
            if (exprComp === childCode) {
              mc.push(childInd);
              childInd++;
              childCode = dependencyValues.codePre + childInd;
            }
          }
          if (childInd >= nMathChildren) {
            break;
          }
        }
        return { setValue: { mathChildrenByVectorComponent } };
      }
    };
    Object.assign(
      stateVariableDefinitions,
      returnMathVectorMatrixStateVariableDefinitions()
    );
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "number",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    },
    "text",
    { componentType: "point", stateVariable: "value" },
    { componentType: "vector", stateVariable: "value" },
    {
      componentType: "subsetOfReals",
      stateVariable: "value",
      substituteForPrimaryStateVariable: "subsetValue"
    },
    {
      stateVariable: "value",
      componentType: "_directionComponent",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
  async moveMath({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  async mathClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async mathFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function calculateExpressionWithCodes({ dependencyValues, changes }) {
  if (!("stringMathChildren" in changes && changes.stringMathChildren.componentIdentitiesChanged || "format" in changes || "splitSymbols" in changes || "parseScientificNotation" in changes || "functionSymbols" in changes || "mathChildrenFunctionSymbols" in changes)) {
    return { useEssentialOrDefaultValue: { expressionWithCodes: true } };
  }
  if (dependencyValues.stringMathChildren.length === 0) {
    return {
      setValue: { expressionWithCodes: null },
      setEssentialValue: { expressionWithCodes: null }
    };
  }
  let functionSymbols = [...dependencyValues.functionSymbols];
  functionSymbols.push(
    ...dependencyValues.mathChildrenFunctionSymbols.map(
      (x2) => dependencyValues.codePre + x2
    )
  );
  let parser;
  if (dependencyValues.format === "text") {
    parser = textToMathFactory({
      functionSymbols,
      splitSymbols: dependencyValues.splitSymbols,
      parseScientificNotation: dependencyValues.parseScientificNotation
    });
  } else if (dependencyValues.format === "latex") {
    parser = latexToMathFactory({
      functionSymbols,
      splitSymbols: dependencyValues.splitSymbols,
      parseScientificNotation: dependencyValues.parseScientificNotation
    });
  }
  let stringResults = createInputStringFromChildren({
    children: dependencyValues.stringMathChildren,
    codePre: dependencyValues.codePre,
    format: dependencyValues.format,
    parser
  });
  let inputString = stringResults.string;
  let expressionWithCodes = null;
  if (inputString === "") {
    expressionWithCodes = me$3.fromAst("＿");
  } else {
    try {
      expressionWithCodes = parser(inputString);
    } catch (e3) {
      expressionWithCodes = me$3.fromAst("＿");
      console.log(
        `Invalid value for a math of ${dependencyValues.format} format: \`${inputString}\``
      );
    }
  }
  return {
    setValue: { expressionWithCodes },
    setEssentialValue: { expressionWithCodes }
  };
}
function calculateMathValue({ dependencyValues } = {}) {
  if (dependencyValues.expressionWithCodes === null) {
    return {
      setValue: { unnormalizedValue: dependencyValues.valueShadow }
    };
  }
  let subsMapping = {};
  for (let [ind, child] of dependencyValues.mathChildren.entries()) {
    subsMapping[dependencyValues.codePre + ind] = child.stateValues.value;
  }
  let value = dependencyValues.expressionWithCodes;
  if (dependencyValues.mathChildren.length > 0) {
    value = value.substitute(subsMapping);
  }
  value = me$3.fromAst(mergeListsWithOtherContainers(value.tree));
  return {
    setValue: { unnormalizedValue: value }
  };
}
function calculateCodesAdjacentToStrings({ dependencyValues }) {
  let codesAdjacentToStrings = [];
  let mathInd;
  for (let [ind, child] of dependencyValues.stringMathChildren.entries()) {
    if (typeof child === "string") {
      let nextChild = dependencyValues.stringMathChildren[ind + 1];
      if (nextChild !== void 0 && typeof nextChild === "string") {
        continue;
      }
      let subCodes = {};
      if (mathInd !== void 0) {
        if (dependencyValues.format === "latex") {
          subCodes.prevCode = "\\operatorname{" + dependencyValues.codePre + mathInd + "}";
        } else {
          subCodes.prevCode = dependencyValues.codePre + mathInd;
        }
      }
      if (nextChild !== void 0) {
        let nextInd = 0;
        if (mathInd !== void 0) {
          nextInd = mathInd + 1;
        }
        if (dependencyValues.format === "latex") {
          subCodes.nextCode = "\\operatorname{" + dependencyValues.codePre + nextInd + "}";
        } else {
          subCodes.nextCode = dependencyValues.codePre + nextInd;
        }
      }
      codesAdjacentToStrings.push(subCodes);
    } else {
      if (mathInd === void 0) {
        mathInd = 0;
      } else {
        mathInd++;
      }
    }
  }
  return { setValue: { codesAdjacentToStrings } };
}
function determineCanBeModified({ dependencyValues }) {
  if (!dependencyValues.modifyIndirectly || dependencyValues.fixed || dependencyValues.fixLocation) {
    return {
      setValue: {
        canBeModified: false,
        constantChildIndices: null,
        codeForExpression: null,
        inverseMaps: null,
        template: null,
        mathChildrenMapped: null
      }
    };
  }
  if (dependencyValues.mathChildrenModifiable.length === 0) {
    return {
      setValue: {
        canBeModified: true,
        constantChildIndices: null,
        codeForExpression: null,
        inverseMaps: null,
        template: null,
        mathChildrenMapped: null
      }
    };
  }
  let variableInds = [];
  let variables = [];
  let constants = [];
  let constantChildIndices = {};
  for (let [
    ind,
    childModifiable
  ] of dependencyValues.mathChildrenModifiable.entries()) {
    let substitutionCode = dependencyValues.codePre + ind;
    if (childModifiable.stateValues.canBeModified === true) {
      variableInds.push(ind);
      variables.push(substitutionCode);
    } else {
      constants.push(substitutionCode);
      constantChildIndices[substitutionCode] = ind;
    }
  }
  let codeForExpression = dependencyValues.codePre + "expr";
  let tree = me$3.utils.unflattenLeft(
    dependencyValues.expressionWithCodes.tree
  );
  let result = checkForLinearExpression(
    tree,
    variables,
    codeForExpression,
    constants
  );
  if (result.foundLinear) {
    let inverseMaps = {};
    let template = result.template;
    let mathChildrenMapped = /* @__PURE__ */ new Set();
    for (let key in result.mappings) {
      inverseMaps[key] = result.mappings[key];
      let mathChildSub = inverseMaps[key].mathChildSub;
      if (mathChildSub) {
        let mathChildInd = variableInds[variables.indexOf(mathChildSub)];
        inverseMaps[key].mathChildInd = mathChildInd;
        mathChildrenMapped.add(Number(mathChildInd));
      }
    }
    mathChildrenMapped.has = mathChildrenMapped.has.bind(mathChildrenMapped);
    return {
      setValue: {
        canBeModified: true,
        constantChildIndices,
        codeForExpression,
        inverseMaps,
        template,
        mathChildrenMapped
      }
    };
  }
  return {
    setValue: {
      canBeModified: false,
      constantChildIndices: null,
      codeForExpression: null,
      inverseMaps: null,
      template: null,
      mathChildrenMapped: null
    }
  };
}
function checkForLinearExpression(tree, variables, inverseTree, constants = [], components = []) {
  let tree_variables = me$3.variables(tree);
  if (tree_variables.every((v2) => !variables.includes(v2))) {
    if (tree_variables.every((v2) => !constants.includes(v2))) {
      let mappings = {};
      let key = "x" + components.join("_");
      mappings[key] = {
        result: me$3.fromAst(inverseTree).expand().simplify(),
        components
      };
      return { foundLinear: true, mappings, template: key };
    }
  }
  if (!Array.isArray(tree)) {
    return checkForScalarLinearExpression(
      tree,
      variables,
      inverseTree,
      components
    );
  }
  let operator = tree[0];
  let operands = tree.slice(1);
  if (vectorAndListOperators$1.includes(operator)) {
    let result = { mappings: {}, template: [operator] };
    let numLinear = 0;
    for (let ind = 0; ind < operands.length; ind++) {
      let new_components = [...components, ind];
      let res = checkForLinearExpression(
        operands[ind],
        variables,
        inverseTree,
        constants,
        new_components
      );
      if (res.foundLinear) {
        numLinear++;
        result.mappings = Object.assign(result.mappings, res.mappings);
        result.template.push(res.template);
      } else {
        result.template.push("x" + new_components.join("_"));
      }
    }
    if (numLinear === 0) {
      return { foundLinear: false };
    }
    result.foundLinear = true;
    return result;
  } else {
    return checkForScalarLinearExpression(
      tree,
      variables,
      inverseTree,
      components
    );
  }
}
function checkForScalarLinearExpression(tree, variables, inverseTree, components = []) {
  if (typeof tree === "string" && variables.includes(tree)) {
    let mappings = {};
    let template = "x" + components.join("_");
    mappings[template] = {
      result: me$3.fromAst(inverseTree).expand().simplify(),
      components,
      mathChildSub: tree
    };
    return { foundLinear: true, mappings, template };
  }
  if (!Array.isArray(tree)) {
    return { foundLinear: false };
  }
  let operator = tree[0];
  let operands = tree.slice(1);
  if (operator === "-") {
    inverseTree = ["-", inverseTree];
    return checkForScalarLinearExpression(
      operands[0],
      variables,
      inverseTree,
      components
    );
  }
  if (operator === "+") {
    if (me$3.variables(operands[0]).every((v2) => !variables.includes(v2))) {
      inverseTree = ["+", inverseTree, ["-", operands[0]]];
      return checkForScalarLinearExpression(
        operands[1],
        variables,
        inverseTree,
        components
      );
    } else if (me$3.variables(operands[1]).every((v2) => !variables.includes(v2))) {
      inverseTree = ["+", inverseTree, ["-", operands[1]]];
      return checkForScalarLinearExpression(
        operands[0],
        variables,
        inverseTree,
        components
      );
    } else {
      return { foundLinear: false };
    }
  }
  if (operator === "*") {
    if (me$3.variables(operands[0]).every((v2) => !variables.includes(v2)) && !exprContainsVector(operands[0])) {
      inverseTree = ["/", inverseTree, operands[0]];
      return checkForScalarLinearExpression(
        operands[1],
        variables,
        inverseTree,
        components
      );
    } else if (me$3.variables(operands[1]).every((v2) => !variables.includes(v2)) && !exprContainsVector(operands[1])) {
      inverseTree = ["/", inverseTree, operands[1]];
      return checkForScalarLinearExpression(
        operands[0],
        variables,
        inverseTree,
        components
      );
    } else {
      return { foundLinear: false };
    }
  }
  if (operator === "/") {
    if (me$3.variables(operands[1]).every((v2) => !variables.includes(v2))) {
      inverseTree = ["*", inverseTree, operands[1]];
      return checkForScalarLinearExpression(
        operands[0],
        variables,
        inverseTree,
        components
      );
    } else {
      return { foundLinear: false };
    }
  }
  return { foundLinear: false };
}
async function invertMath({
  desiredStateVariableValues,
  dependencyValues,
  stateValues,
  workspace,
  overrideFixed
}) {
  if (!await stateValues.canBeModified && !overrideFixed) {
    return { success: false };
  }
  let mathChildren = dependencyValues.mathChildren;
  let nStringChildren = dependencyValues.stringChildren.length;
  if (mathChildren.length === 1 && nStringChildren === 0) {
    return {
      success: true,
      instructions: [
        {
          setDependency: "mathChildren",
          desiredValue: desiredStateVariableValues.unnormalizedValue,
          childIndex: 0,
          variableIndex: 0
        }
      ]
    };
  }
  let desiredExpression = convertValueToMathExpression(
    desiredStateVariableValues.unnormalizedValue
  );
  let result = await preprocessMathInverseDefinition({
    desiredValue: desiredExpression,
    stateValues,
    variableName: "value",
    workspace
  });
  let vectorComponentsNotAffected = result.vectorComponentsNotAffected;
  desiredExpression = result.desiredValue;
  if (mathChildren.length === 0) {
    let instructions2 = [];
    if (nStringChildren > 0) {
      instructions2.push({
        setDependency: "expressionWithCodes",
        desiredValue: desiredExpression
      });
    } else {
      instructions2.push({
        setDependency: "valueShadow",
        desiredValue: desiredExpression
      });
    }
    return {
      success: true,
      instructions: instructions2
    };
  }
  let expressionPieces = await getExpressionPieces({
    expression: desiredExpression,
    stateValues
  });
  if (!expressionPieces) {
    return { success: false };
  }
  let instructions = [];
  let childrenToSkip = [];
  if (vectorComponentsNotAffected && await stateValues.mathChildrenByVectorComponent) {
    let mathChildrenByVectorComponent = await stateValues.mathChildrenByVectorComponent;
    for (let ind of vectorComponentsNotAffected) {
      if (mathChildrenByVectorComponent[ind]) {
        childrenToSkip.push(...mathChildrenByVectorComponent[ind]);
      }
    }
  }
  let mathChildrenWithCanBeModified = await stateValues.mathChildrenWithCanBeModified;
  for (let [childInd, mathChild] of mathChildren.entries()) {
    if (stateValues.mathChildrenMapped.has(childInd) && mathChildrenWithCanBeModified[childInd].stateValues.canBeModified) {
      if (!childrenToSkip.includes(childInd)) {
        let childValue = expressionPieces[childInd];
        let subsMap = {};
        let foundConst = false;
        let constantChildIndices = await stateValues.constantChildIndices;
        for (let code in constantChildIndices) {
          let constInd = constantChildIndices[code];
          subsMap[code] = mathChildren[constInd].stateValues.value;
          foundConst = true;
        }
        if (foundConst) {
          childValue = childValue.substitute(subsMap);
        }
        childValue = childValue.expand().simplify();
        instructions.push({
          setDependency: "mathChildren",
          desiredValue: childValue,
          childIndex: childInd,
          variableIndex: 0
        });
      }
      delete expressionPieces[childInd];
    }
  }
  if (nStringChildren > 0) {
    let newExpressionWithCodes = dependencyValues.expressionWithCodes;
    let codePre = dependencyValues.codePre;
    let nCP = codePre.length;
    let mathComponentIsCode = (tree) => typeof tree === "string" && tree.substring(0, nCP) === codePre;
    let mathComponentContainsCode = (tree) => {
      if (Array.isArray(tree)) {
        return flattenDeep(tree.slice(1)).some(mathComponentIsCode);
      } else {
        return mathComponentIsCode(tree);
      }
    };
    if (vectorAndListOperators$1.includes(newExpressionWithCodes.tree[0]) && !newExpressionWithCodes.tree.slice(1).every(mathComponentContainsCode)) {
      let inverseMaps = await stateValues.inverseMaps;
      for (let piece in expressionPieces) {
        let inverseMap = inverseMaps[piece];
        if (inverseMap.mathChildInd !== void 0) {
          continue;
        }
        let components = inverseMap.components;
        newExpressionWithCodes = newExpressionWithCodes.substitute_component(
          components,
          expressionPieces[piece]
        );
      }
      instructions.push({
        setDependency: "expressionWithCodes",
        desiredValue: newExpressionWithCodes
      });
    }
  }
  return {
    success: true,
    instructions
  };
}
async function getExpressionPieces({ expression, stateValues }) {
  let template = await stateValues.template;
  let matching = me$3.utils.match(expression.tree, template);
  if (!matching) {
    matching = me$3.utils.match(
      expression.tuples_to_vectors().tree,
      me$3.fromAst(template).tuples_to_vectors().tree
    );
    if (!matching) {
      matching = me$3.utils.match(
        expression.to_intervals().tree,
        me$3.fromAst(template).to_intervals().tree
      );
      if (!matching) {
        matching = me$3.utils.match(
          expression.tuples_to_vectors().to_intervals().tree,
          me$3.fromAst(template).tuples_to_vectors().to_intervals().tree
        );
        if (!matching) {
          return false;
        }
      }
    }
  }
  let pieces = {};
  for (let x2 in matching) {
    let subMap = {};
    subMap[await stateValues.codeForExpression] = matching[x2];
    let inverseMap = (await stateValues.inverseMaps)[x2];
    if (inverseMap !== void 0) {
      let id = x2;
      if (inverseMap.mathChildInd !== void 0) {
        id = inverseMap.mathChildInd;
      }
      pieces[id] = inverseMap.result.substitute(subMap);
      pieces[id] = normalizeMathExpression({
        value: pieces[id],
        simplify: await stateValues.simplify,
        expand: await stateValues.expand,
        createVectors: await stateValues.createVectors,
        createIntervals: await stateValues.createIntervals
      });
    }
  }
  return pieces;
}
function exprContainsVector(tree) {
  if (!Array.isArray(tree)) {
    return false;
  }
  let operator = tree[0];
  let operands = tree.slice(1);
  if (vectorOperators.includes(operator)) {
    return true;
  }
  return operands.some(exprContainsVector);
}
class Label extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveLabel: this.moveLabel.bind(this),
      labelClicked: this.labelClicked.bind(this),
      labelFocused: this.labelFocused.bind(this)
    });
  }
  static componentType = "label";
  static componentDocs = {
    summary: "A label for an input, answer, button, or graphical object"
  };
  static rendererType = "label";
  static includeBlankStringChildren = true;
  // used when creating new component via adapter or copy prop
  static primaryStateVariableForDefinition = "valueShadow";
  static variableForImplicitProp = "value";
  static implicitPropReturnsSameType = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.for = {
      createReferences: true,
      description: "Reference to the component this label is for (e.g., an input)."
    };
    attributes.forObject = {
      createReferences: true,
      description: "Reference to the graphical object this label is attached to."
    };
    attributes.draggable = {
      description: "Whether the label can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.layer = {
      description: "Z-order layer index when shown on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    attributes.maskLabel = {
      description: "Whether to give the label an opaque background so it stays legible when it overlaps an axis, grid line, or another object.",
      createComponentOfType: "boolean",
      createStateVariable: "maskLabel",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "inlines",
        componentTypes: ["_inline"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.valueShadow = {
      hasEssential: true,
      defaultValue: null,
      returnDependencies: () => ({}),
      definition() {
        return {
          useEssentialOrDefaultValue: { valueShadow: true }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "valueShadow",
              value: desiredStateVariableValues.valueShadow
            }
          ]
        };
      }
    };
    stateVariableDefinitions.hasLatex = {
      description: "Whether the label contains LaTeX content.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlines"],
          variableNames: [
            "text",
            "latex",
            "value",
            "hasLatex",
            "renderAsMath"
          ],
          variablesOptional: true
        },
        valueShadow: {
          dependencyType: "stateVariable",
          variableName: "valueShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.inlineChildren.length === 0 && dependencyValues.valueShadow !== null) {
          let value = dependencyValues.valueShadow;
          let hasLatex2 = Boolean(/\\\(.*\\\)/.exec(value));
          return { setValue: { hasLatex: hasLatex2 } };
        }
        let hasLatex = false;
        for (let comp of dependencyValues.inlineChildren) {
          if (typeof comp !== "object") ;
          else if (typeof comp.stateValues.hasLatex === "boolean" && typeof comp.stateValues.value === "string" && typeof comp.stateValues.text === "string") {
            if (comp.stateValues.hasLatex) {
              return { setValue: { hasLatex: true } };
            }
          } else if (typeof comp.stateValues.renderAsMath === "boolean" && typeof comp.stateValues.latex === "string" && typeof comp.stateValues.text === "string") {
            if (comp.stateValues.renderAsMath) {
              return { setValue: { hasLatex: true } };
            }
          } else if (typeof comp.stateValues.latex === "string") {
            return { setValue: { hasLatex: true } };
          }
        }
        return { setValue: { hasLatex } };
      }
    };
    stateVariableDefinitions.text = {
      public: true,
      description: "The label's text content.",
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      additionalStateVariablesDefined: [
        {
          variableName: "latex",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "latex"
          },
          description: "The label's content as a LaTeX string."
        },
        {
          variableName: "value",
          public: true,
          forRenderer: true,
          shadowingInstructions: {
            createComponentOfType: this.componentType,
            addStateVariablesShadowingStateVariables: {
              hasLatex: {
                stateVariableToShadow: "hasLatex"
              }
            }
          },
          description: "The label's content as a label-typed value."
        }
      ],
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlines"],
          variableNames: [
            "text",
            "latex",
            "value",
            "hasLatex",
            "renderAsMath",
            "hidden"
          ],
          variablesOptional: true
        },
        valueShadow: {
          dependencyType: "stateVariable",
          variableName: "valueShadow"
        },
        hasLatex: {
          dependencyType: "stateVariable",
          variableName: "hasLatex"
        }
      }),
      definition: function({ dependencyValues, componentIdx }) {
        if (dependencyValues.inlineChildren.length === 0 && dependencyValues.valueShadow !== null) {
          let value2 = dependencyValues.valueShadow;
          let text2 = value2;
          let latex2 = value2;
          if (dependencyValues.hasLatex) {
            latex2 = latex2.replace(/\\\(/g, "");
            latex2 = latex2.replace(/\\\)/g, "");
            text2 = extractTextFromLabel(text2);
          }
          return { setValue: { text: text2, latex: latex2, value: value2 } };
        }
        let valueFromComponentConverter = function(comp) {
          if (typeof comp !== "object") {
            return comp.toString();
          } else if (comp.stateValues.hidden) {
            return "";
          } else if (typeof comp.stateValues.hasLatex === "boolean" && typeof comp.stateValues.value === "string") {
            return comp.stateValues.value;
          } else if (typeof comp.stateValues.renderAsMath === "boolean" && typeof comp.stateValues.latex === "string" && typeof comp.stateValues.text === "string") {
            if (comp.stateValues.renderAsMath) {
              return "\\(" + comp.stateValues.latex + "\\)";
            } else {
              return comp.stateValues.text;
            }
          } else if (typeof comp.stateValues.latex === "string") {
            return "\\(" + comp.stateValues.latex + "\\)";
          } else if (typeof comp.stateValues.text === "string") {
            return comp.stateValues.text;
          }
        };
        let textFromComponentConverter = function(comp, preferLatex = false) {
          if (typeof comp !== "object") {
            return comp.toString();
          } else if (comp.stateValues.hidden) {
            return "";
          } else if (typeof comp.stateValues.text === "string" && !preferLatex) {
            return comp.stateValues.text;
          } else if (typeof comp.stateValues.latex === "string") {
            return comp.stateValues.latex;
          } else if (typeof comp.stateValues.text === "string") {
            return comp.stateValues.text;
          }
        };
        let value = textFromChildren(
          dependencyValues.inlineChildren,
          valueFromComponentConverter
        );
        let text = textFromChildren(
          dependencyValues.inlineChildren,
          (x2) => textFromComponentConverter(x2, false)
        );
        let latex = textFromChildren(
          dependencyValues.inlineChildren,
          (x2) => textFromComponentConverter(x2, true)
        );
        return { setValue: { text, latex, value } };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredValue;
        if (typeof desiredStateVariableValues.value === "string") {
          desiredValue = desiredStateVariableValues.value;
        } else if (typeof desiredStateVariableValues.text === "string") {
          if (dependencyValues.hasLatex) {
            desiredValue = textToLatex(
              desiredStateVariableValues.text
            );
          } else {
            desiredValue = desiredStateVariableValues.text;
          }
        } else if (typeof desiredStateVariableValues.latex === "string") {
          desiredValue = desiredStateVariableValues.latex;
        } else {
          return { success: false };
        }
        if (dependencyValues.inlineChildren.length === 0 && dependencyValues.valueShadow !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "valueShadow",
                desiredValue
              }
            ]
          };
        } else if (dependencyValues.inlineChildren.length === 1) {
          let comp = dependencyValues.inlineChildren[0];
          if (typeof comp !== "object") {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "inlineChildren",
                  desiredValue,
                  childIndex: 0
                }
              ]
            };
          } else if (typeof comp.stateValues.hasLatex === "boolean" && typeof comp.stateValues.value === "string" && typeof comp.stateValues.text === "string") {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "inlineChildren",
                  desiredValue,
                  childIndex: 0,
                  variableIndex: 2
                  // the variable "value"
                }
              ]
            };
          } else if (typeof comp.stateValues.renderAsMath === "boolean" && typeof comp.stateValues.latex === "string" && typeof comp.stateValues.text === "string") {
            if (comp.stateValues.renderAsMath) {
              let match = desiredValue.match(/^\\\((.*)\\\)/);
              if (match) {
                desiredValue = match[1];
              }
              return {
                success: true,
                instructions: [
                  {
                    setDependency: "inlineChildren",
                    desiredValue,
                    childIndex: 0,
                    variableIndex: 1
                    // the "latex" variable
                  }
                ]
              };
            } else {
              return {
                success: true,
                instructions: [
                  {
                    setDependency: "inlineChildren",
                    desiredValue,
                    childIndex: 0,
                    variableIndex: 0
                    // the "text" variable
                  }
                ]
              };
            }
          } else if (typeof comp.stateValues.latex === "string") {
            let match = desiredValue.match(/^\\\((.*)\\\)/);
            if (match) {
              desiredValue = match[1];
            }
            return {
              success: true,
              instructions: [
                {
                  setDependency: "inlineChildren",
                  desiredValue,
                  childIndex: 0,
                  variableIndex: 1
                  // the "latex" variable
                }
              ]
            };
          } else if (typeof comp.stateValues.text === "string") {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "inlineChildren",
                  desiredValue,
                  childIndex: 0,
                  variableIndex: 0
                  // the "text" variable
                }
              ]
            };
          } else {
            return { success: false };
          }
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.forObjectComponentIdx = {
      returnDependencies: () => ({
        forObject: {
          dependencyType: "attributeRefResolutions",
          attributeName: "forObject"
        }
      }),
      definition({ dependencyValues }) {
        let forObjectComponentIdx;
        if (dependencyValues.forObject?.length === 1) {
          if (dependencyValues.forObject[0].unresolvedPath === null) {
            forObjectComponentIdx = dependencyValues.forObject[0].componentIdx;
          } else {
            forObjectComponentIdx = -1;
          }
        } else {
          forObjectComponentIdx = null;
        }
        return { setValue: { forObjectComponentIdx } };
      }
    };
    stateVariableDefinitions.forAttributeData = {
      returnDependencies: () => ({
        forResolutions: {
          dependencyType: "attributeRefResolutions",
          attributeName: "for"
        },
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph"
        }
      }),
      definition({ dependencyValues }) {
        const diagnostics = [];
        const forResolutions = dependencyValues.forResolutions ?? [];
        const hasForAttribute = forResolutions.length > 0;
        const isGraphicalLabel = Boolean(
          dependencyValues.graphAncestor
        );
        let targetComponentIdx = null;
        if (hasForAttribute) {
          if (isGraphicalLabel) {
            diagnostics.push({
              message: "The `for` attribute on graphical `<label>` is ignored.",
              type: "warning"
            });
          } else if (forResolutions.length !== 1) {
            diagnostics.push({
              message: "The `for` attribute on `<label>` must resolve to exactly one component.",
              type: "warning"
            });
          } else if (forResolutions[0].unresolvedPath !== null) {
            diagnostics.push({
              message: "The `for` attribute on `<label>` could not be resolved to a component.",
              type: "warning"
            });
          } else {
            targetComponentIdx = forResolutions[0].componentIdx;
          }
        }
        return {
          setValue: {
            forAttributeData: {
              hasForAttribute,
              isGraphicalLabel,
              targetComponentIdx
            }
          },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.forTargetComponentIdentity = {
      stateVariablesDeterminingDependencies: ["forAttributeData"],
      returnDependencies({ stateValues }) {
        const targetComponentIdx = stateValues.forAttributeData?.targetComponentIdx;
        if (targetComponentIdx !== null && targetComponentIdx !== void 0) {
          return {
            targetComponentIdentity: {
              dependencyType: "componentIdentity",
              componentIdx: targetComponentIdx
            }
          };
        }
        return {};
      },
      definition({ dependencyValues }) {
        return {
          setValue: {
            forTargetComponentIdentity: dependencyValues.targetComponentIdentity ?? null
          }
        };
      }
    };
    stateVariableDefinitions.forTargetInputComponentIdx = {
      stateVariablesDeterminingDependencies: [
        "forAttributeData",
        "forTargetComponentIdentity"
      ],
      returnDependencies({ stateValues, componentInfoObjects }) {
        const dependencies = {
          forAttributeData: {
            dependencyType: "stateVariable",
            variableName: "forAttributeData"
          },
          forTargetComponentIdentity: {
            dependencyType: "stateVariable",
            variableName: "forTargetComponentIdentity"
          }
        };
        const targetIdentity = stateValues.forTargetComponentIdentity;
        if (targetIdentity && componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: targetIdentity.componentType,
          baseComponentType: "answer"
        })) {
          dependencies.answerInputComponentIdxForLabel = {
            dependencyType: "stateVariable",
            componentIdx: targetIdentity.componentIdx,
            variableName: "inputComponentIdxForLabel"
          };
          dependencies.answerHasAuthoredInputChildrenForLabel = {
            dependencyType: "stateVariable",
            componentIdx: targetIdentity.componentIdx,
            variableName: "hasAuthoredInputChildrenForLabel"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        const diagnostics = [];
        const forAttributeData = dependencyValues.forAttributeData;
        if (!forAttributeData?.hasForAttribute || forAttributeData.isGraphicalLabel) {
          return { setValue: { forTargetInputComponentIdx: null } };
        }
        const targetIdentity = dependencyValues.forTargetComponentIdentity;
        if (!targetIdentity) {
          return { setValue: { forTargetInputComponentIdx: null } };
        }
        if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: targetIdentity.componentType,
          baseComponentType: "_input"
        })) {
          return {
            setValue: {
              forTargetInputComponentIdx: targetIdentity.componentIdx
            }
          };
        }
        if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: targetIdentity.componentType,
          baseComponentType: "answer"
        })) {
          const answerInputComponentIdxForLabel = dependencyValues.answerInputComponentIdxForLabel;
          const answerHasAuthoredInputChildrenForLabel = dependencyValues.answerHasAuthoredInputChildrenForLabel;
          if (answerInputComponentIdxForLabel !== null && answerInputComponentIdxForLabel !== void 0) {
            return {
              setValue: {
                forTargetInputComponentIdx: answerInputComponentIdxForLabel
              },
              sendDiagnostics: diagnostics
            };
          }
          if (answerHasAuthoredInputChildrenForLabel) {
            diagnostics.push({
              message: "The `for` attribute on `<label>` references an `<answer>` with explicitly authored inputs; reference the input directly.",
              type: "warning"
            });
            return {
              setValue: { forTargetInputComponentIdx: null },
              sendDiagnostics: diagnostics
            };
          }
          diagnostics.push({
            message: "The `for` attribute on `<label>` references an `<answer>` without an input to label.",
            type: "warning"
          });
          return {
            setValue: { forTargetInputComponentIdx: null },
            sendDiagnostics: diagnostics
          };
        }
        diagnostics.push({
          message: "The `for` attribute on `<label>` must reference an input or an answer.",
          type: "warning"
        });
        return {
          setValue: { forTargetInputComponentIdx: null },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.forTargetRendererId = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: [
        "forTargetInputComponentIdx"
      ],
      returnDependencies({ stateValues }) {
        const dependencies = {
          forTargetInputComponentIdx: {
            dependencyType: "stateVariable",
            variableName: "forTargetInputComponentIdx"
          }
        };
        if (stateValues.forTargetInputComponentIdx !== null) {
          dependencies.forTargetRendererId = {
            dependencyType: "rendererId",
            componentIdx: stateValues.forTargetInputComponentIdx
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        return {
          setValue: {
            forTargetRendererId: dependencyValues.forTargetRendererId ?? null
          }
        };
      }
    };
    stateVariableDefinitions.forTargetInputComponentIdentity = {
      stateVariablesDeterminingDependencies: [
        "forTargetInputComponentIdx"
      ],
      returnDependencies({ stateValues }) {
        const forTargetInputComponentIdx = stateValues.forTargetInputComponentIdx;
        if (forTargetInputComponentIdx !== null && forTargetInputComponentIdx !== void 0) {
          return {
            forTargetInputComponentIdentity: {
              dependencyType: "componentIdentity",
              componentIdx: forTargetInputComponentIdx
            }
          };
        }
        return {};
      },
      definition({ dependencyValues }) {
        return {
          setValue: {
            forTargetInputComponentIdentity: dependencyValues.forTargetInputComponentIdentity ?? null
          }
        };
      }
    };
    stateVariableDefinitions.canBeAccessibilityLabel = {
      returnDependencies: () => ({
        forTargetInputComponentIdx: {
          dependencyType: "stateVariable",
          variableName: "forTargetInputComponentIdx"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            canBeAccessibilityLabel: dependencyValues.forTargetInputComponentIdx !== null
          }
        };
      }
    };
    stateVariableDefinitions.forTargetIsGroup = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: [
        "forTargetInputComponentIdentity"
      ],
      returnDependencies({ stateValues }) {
        const dependencies = {
          forTargetInputComponentIdentity: {
            dependencyType: "stateVariable",
            variableName: "forTargetInputComponentIdentity"
          }
        };
        const inputIdentity = stateValues.forTargetInputComponentIdentity;
        if (inputIdentity?.componentType === "choiceInput") {
          dependencies.choiceInputInline = {
            dependencyType: "stateVariable",
            componentIdx: inputIdentity.componentIdx,
            variableName: "inline"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        const inputIdentity = dependencyValues.forTargetInputComponentIdentity;
        if (!inputIdentity) {
          return { setValue: { forTargetIsGroup: false } };
        }
        let forTargetIsGroup = false;
        if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: inputIdentity.componentType,
          baseComponentType: "matrixInput"
        }) || componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: inputIdentity.componentType,
          baseComponentType: "fractionInput"
        })) {
          forTargetIsGroup = true;
        } else if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: inputIdentity.componentType,
          baseComponentType: "choiceInput"
        })) {
          forTargetIsGroup = dependencyValues.choiceInputInline === false;
        }
        return { setValue: { forTargetIsGroup } };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = ["text"];
  async moveLabel({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  async labelClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async labelFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function extractTextFromLabel(labelValue) {
  let unprocessedText = labelValue;
  let text = "";
  let match = unprocessedText.match(/\\\((.*?)\\\)/);
  while (match) {
    let preChars = match.index;
    text += unprocessedText.slice(0, preChars);
    text += latexToText(match[1]);
    unprocessedText = unprocessedText.slice(preChars + match[0].length);
    match = unprocessedText.match(/\\\((.*?)\\\)/);
  }
  text += unprocessedText;
  return text;
}
class CompositeComponent extends BaseComponent {
  constructor(args) {
    super(args);
    this.replacementsWorkspace = {};
  }
  static componentType = "_composite";
  static rendererType = void 0;
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.hidden.public = false;
    stateVariableDefinitions.disabled.public = false;
    stateVariableDefinitions.fixed.public = false;
    stateVariableDefinitions.fixLocation.public = false;
    stateVariableDefinitions.replacements = {
      returnDependencies: () => ({
        replacements: {
          dependencyType: "replacement"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { replacements: dependencyValues.replacements }
      })
    };
    stateVariableDefinitions.recursiveReplacements = {
      returnDependencies: () => ({
        recursiveReplacements: {
          dependencyType: "replacement",
          recursive: true
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          recursiveReplacements: dependencyValues.recursiveReplacements
        }
      })
    };
    stateVariableDefinitions.fullRecursiveReplacements = {
      returnDependencies: () => ({
        recursiveReplacements: {
          dependencyType: "replacement",
          recursive: true,
          recurseNonStandardComposites: true
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          fullRecursiveReplacements: dependencyValues.recursiveReplacements
        }
      })
    };
    return stateVariableDefinitions;
  }
  // This function is called by Core.js in expandCompositeComponent
  // See that invocation for documentation
  static createSerializedReplacements({ nComponents }) {
    return { replacements: [], diagnostics: [], nComponents };
  }
  // This function is called by Core.js in updateCompositeReplacements
  // See that invocation for documentation
  static calculateReplacementChanges({ nComponents }) {
    return { replacementChanges: [], nComponents };
  }
  // serialize(parameters = {}) {
  //   let serializedState = super.serialize(parameters);
  //   if (this.replacements === undefined) {
  //     return serializedState;
  //   }
  //   // also serialize replacements
  //   let serializedReplacements = [];
  //   for (let ind = 0; ind < this.replacements.length; ind++) {
  //     serializedReplacements.push(this.replacements[ind].serialize(parameters));
  //   }
  //   serializedState.replacements = serializedReplacements;
  //   if (this.replacementsToWithhold !== undefined) {
  //     serializedState.replacementsToWithhold = this.replacementsToWithhold;
  //   }
  //   return serializedState;
  // }
  // The array replacements contains the components that are substituted for the composite
  // and become the children of the composite's parent instead of the composite itself.
  // Note: components do not set this variable directly.
  // Instead, core will create the replacements array based on the information given by
  // the static function createSerializedReplacements.
  // Core will also change the replacements array based on the instructions returned by
  // the static function calculateReplacementChanges.
  replacements = [];
  // The integer replacementsToWithhold is the number of replacements at the end of the replacements array
  // that are ignored when inserting replacements as children to composite's parent.
  // It is used so that when a composite reduces the number of replacements, we don't need to delete the components,
  // but we can just withhold them and reveal them when the number of replacement is increased again.
  // For example, if the number of replacements in a sequence is controlled dynamically by a slider,
  // the sequence withholds replacements when the number is reduced.
  // Note: components do not set this variable directly.
  // Instead, core will change the replacementsToWithhold based on the instructions returned by
  // the static function calculateReplacementChanges.
  replacementsToWithhold = 0;
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    for (const childIdxStr in this.allChildren) {
      let child = this.allChildren[childIdxStr].component;
      for (let rendererType of child.allPotentialRendererTypes) {
        if (!allPotentialRendererTypes.includes(rendererType)) {
          allPotentialRendererTypes.push(rendererType);
        }
      }
    }
    if (this.replacements) {
      for (let replacement of this.replacements) {
        if (typeof replacement === "object") {
          for (let rendererType of replacement.allPotentialRendererTypes) {
            if (!allPotentialRendererTypes.includes(rendererType)) {
              allPotentialRendererTypes.push(rendererType);
            }
          }
        }
      }
    }
    return allPotentialRendererTypes;
  }
}
function returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
  componentType,
  forceComponentType = false,
  includeNonMacros = false
}) {
  return function({
    matchedChildren,
    componentInfoObjects,
    nComponents,
    stateIdInfo
  }) {
    let newChildren = [];
    let pieces = [];
    function createNewChild() {
      let addedSingleMatch = false;
      if (pieces.length === 1) {
        let comp = pieces[0];
        if (typeof comp !== "string") {
          if (forceComponentType) {
            if (componentInfoObjects.componentIsSpecifiedType(
              comp,
              componentType
            )) {
              newChildren.push(comp);
              addedSingleMatch = true;
            }
          } else {
            newChildren.push(comp);
            addedSingleMatch = true;
          }
        }
      }
      if (!addedSingleMatch && pieces.length > 0) {
        newChildren.push({
          type: "serialized",
          componentType,
          children: pieces,
          componentIdx: nComponents++,
          stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
          attributes: {},
          state: {},
          doenetAttributes: {}
        });
      }
      pieces = [];
    }
    let nParens = 0;
    for (let child of matchedChildren) {
      if (typeof child !== "string") {
        if (!(includeNonMacros || child.extending && "Ref" in child.extending)) {
          createNewChild();
          pieces.push(child);
          createNewChild();
          nParens = 0;
        } else {
          pieces.push(child);
        }
      } else {
        let s2 = child;
        let beginInd = 0;
        for (let ind = 0; ind < s2.length; ind++) {
          let char = s2[ind];
          if (char === "(") {
            nParens++;
          } else if (char === ")") {
            if (nParens === 0) {
              createNewChild();
            } else {
              nParens--;
            }
          } else if (nParens === 0 && char.match(/\s/)) {
            if (ind > beginInd) {
              pieces.push(s2.substring(beginInd, ind));
            }
            createNewChild();
            beginInd = ind + 1;
          }
        }
        if (s2.length > beginInd) {
          pieces.push(s2.substring(beginInd, s2.length));
        }
      }
    }
    createNewChild();
    return {
      success: true,
      newChildren,
      nComponents
    };
  };
}
function returnUnorderedListStateVariableDefinitions() {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.extendListViaComposite = {
    returnDependencies: () => ({
      extendListViaComposite: {
        dependencyType: "doenetAttribute",
        attributeName: "extendListViaComposite"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          extendListViaComposite: dependencyValues.extendListViaComposite
        }
      };
    }
  };
  stateVariableDefinitions.copyListViaComposite = {
    returnDependencies: () => ({
      copyListViaComposite: {
        dependencyType: "doenetAttribute",
        attributeName: "copyListViaComposite"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          copyListViaComposite: dependencyValues.copyListViaComposite
        }
      };
    }
  };
  stateVariableDefinitions.extendListSourceIdx = {
    stateVariablesDeterminingDependencies: ["extendListViaComposite"],
    returnDependencies({ stateValues }) {
      if (stateValues.extendListViaComposite) {
        return {
          extendListSourceIdx: {
            dependencyType: "stateVariable",
            componentIdx: stateValues.extendListViaComposite,
            variableName: "extendIdx"
          }
        };
      } else {
        return {};
      }
    },
    definition({ dependencyValues }) {
      if (typeof dependencyValues.extendListSourceIdx === "number" && dependencyValues.extendListSourceIdx !== -1) {
        return {
          setValue: {
            extendListSourceIdx: dependencyValues.extendListSourceIdx
          }
        };
      } else {
        return { setValue: { extendListSourceIdx: null } };
      }
    }
  };
  stateVariableDefinitions.copyListSourceIdx = {
    stateVariablesDeterminingDependencies: ["copyListViaComposite"],
    returnDependencies({ stateValues }) {
      if (stateValues.copyListViaComposite) {
        return {
          copyListSourceIdx: {
            dependencyType: "stateVariable",
            componentIdx: stateValues.copyListViaComposite,
            variableName: "extendIdx"
          }
        };
      } else {
        return {};
      }
    },
    definition({ dependencyValues }) {
      if (dependencyValues.copyListSourceIdx) {
        return {
          setValue: {
            copyListSourceIdx: dependencyValues.copyListSourceIdx
          }
        };
      } else {
        return { setValue: { copyListSourceIdx: null } };
      }
    }
  };
  stateVariableDefinitions.unorderedFromCopyListSource = {
    immutable: true,
    hasEssential: true,
    defaultValue: false,
    stateVariablesDeterminingDependencies: ["copyListSourceIdx"],
    returnDependencies({ stateValues }) {
      if (stateValues.copyListSourceIdx) {
        return {
          unorderedFromCopy: {
            dependencyType: "stateVariable",
            componentIdx: stateValues.copyListSourceIdx,
            variableName: "unordered",
            variablesOptional: true
          }
        };
      } else {
        return {};
      }
    },
    definition({ dependencyValues, usedDefault }) {
      if (dependencyValues.unorderedFromCopy != void 0 && !usedDefault.unorderedFromCopy) {
        return {
          setValue: {
            unorderedFromCopyListSource: dependencyValues.unorderedFromCopy
          }
        };
      } else {
        return {
          useEssentialOrDefaultValue: {
            unorderedFromCopyListSource: true
          }
        };
      }
    }
  };
  stateVariableDefinitions.unordered = {
    description: "Whether the order of items in this list should be treated as unordered (e.g. for matching).",
    defaultValue: false,
    hasEssential: true,
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    stateVariablesDeterminingDependencies: ["extendListSourceIdx"],
    returnDependencies({ stateValues }) {
      const dependencies = {
        unorderedPrelim: {
          dependencyType: "stateVariable",
          variableName: "unorderedPrelim"
        },
        unorderedFromCopyListSource: {
          dependencyType: "stateVariable",
          variableName: "unorderedFromCopyListSource"
        }
      };
      if (stateValues.extendListSourceIdx !== null) {
        dependencies.unorderedFromExtended = {
          dependencyType: "stateVariable",
          componentIdx: stateValues.extendListSourceIdx,
          variableName: "unordered",
          variablesOptional: true
        };
      }
      return dependencies;
    },
    definition({ dependencyValues, usedDefault }) {
      if (!usedDefault.unorderedPrelim) {
        return {
          setValue: {
            unordered: dependencyValues.unorderedPrelim
          }
        };
      } else if (dependencyValues.unorderedFromExtended != void 0 && !usedDefault.unorderedFromExtended) {
        return {
          setValue: {
            unordered: dependencyValues.unorderedFromExtended
          }
        };
      } else if (!usedDefault.unorderedFromCopyListSource) {
        return {
          setValue: {
            unordered: dependencyValues.unorderedFromCopyListSource
          }
        };
      } else {
        return { useEssentialOrDefaultValue: { unordered: true } };
      }
    }
  };
  return stateVariableDefinitions;
}
class MathList extends CompositeComponent {
  static componentType = "mathList";
  static componentDocs = {
    summary: "A list of math expressions"
  };
  static takesIndex = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static allowInSchemaAsComponent = ["math"];
  // when another component has an attribute that is a mathList,
  // use the maths state variable to populate that attribute
  static stateVariableToBeShadowed = "maths";
  static primaryStateVariableForDefinition = "mathsShadow";
  // even if inside a component that turned on descendantCompositesMustHaveAReplacement
  // don't required composite replacements
  static descendantCompositesMustHaveAReplacement = false;
  static doNotExpandAsShadowed = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedPrelim",
      defaultValue: false,
      description: "Whether the order of items in this list should be treated as unordered (e.g. for matching)."
    };
    attributes.maxNumber = {
      description: "Maximum number of items to retain in the list.",
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: Infinity,
      public: true
    };
    attributes.mergeMathLists = {
      createComponentOfType: "boolean",
      description: "Whether nested math-list children should be flattened into this list."
    };
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this component is treated as a response for the purposes of assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    const numberDisplayAttrs = returnNumberDisplayAttributes();
    for (let attrName in numberDisplayAttrs) {
      attributes[attrName] = {
        leaveRaw: true,
        description: numberDisplayAttrs[attrName].description
      };
    }
    attributes.functionSymbols = {
      description: "Symbols treated as function names when parsing items.",
      createComponentOfType: "textList",
      createStateVariable: "functionSymbols",
      defaultValue: ["f", "g"],
      public: true,
      fallBackToParentStateVariable: "functionSymbols",
      fallBackToSourceCompositeStateVariable: "functionSymbols"
    };
    attributes.referencesAreFunctionSymbols = {
      createReferences: true,
      createStateVariable: "referencesAreFunctionSymbols",
      defaultValue: [],
      fallBackToParentStateVariable: "referencesAreFunctionSymbols",
      fallBackToSourceCompositeStateVariable: "referencesAreFunctionSymbols",
      description: "References whose names should be treated as function symbols when parsing items."
    };
    attributes.splitSymbols = {
      description: "Whether multi-character symbols are split into a product of variables.",
      createComponentOfType: "boolean",
      createStateVariable: "splitSymbols",
      defaultValue: true,
      public: true,
      fallBackToParentStateVariable: "splitSymbols",
      fallBackToSourceCompositeStateVariable: "splitSymbols"
    };
    attributes.parseScientificNotation = {
      description: "Whether to parse expressions like 1e3 as scientific notation.",
      createComponentOfType: "boolean",
      createStateVariable: "parseScientificNotation",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "parseScientificNotation"
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let groupIntoMathsSeparatedBySpaces = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
      componentType: "math"
    });
    sugarInstructions.push({
      replacementFunction: function({
        matchedChildren,
        nComponents,
        stateIdInfo
      }) {
        return groupIntoMathsSeparatedBySpaces({
          matchedChildren,
          nComponents,
          stateIdInfo
        });
      }
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnUnorderedListStateVariableDefinitions()
    );
    stateVariableDefinitions.mathsShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          mathsShadow: true
        }
      })
    };
    stateVariableDefinitions.mergeMathLists = {
      description: "Whether nested math lists are merged into the parent list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        mergeMathListsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "mergeMathLists",
          variableNames: ["value"]
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          skipComponentIndices: true
        }
      }),
      definition({ dependencyValues }) {
        let mergeMathLists = dependencyValues.mergeMathListsAttr?.stateValues.value || dependencyValues.mathChildren.length === 1;
        return { setValue: { mergeMathLists } };
      }
    };
    stateVariableDefinitions.numComponents = {
      description: "The number of items in the math list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      stateVariablesDeterminingDependencies: ["mergeMathLists"],
      additionalStateVariablesDefined: ["childInfoByComponent"],
      returnDependencies({ stateValues }) {
        let dependencies = {
          maxNumber: {
            dependencyType: "stateVariable",
            variableName: "maxNumber"
          },
          mergeMathLists: {
            dependencyType: "stateVariable",
            variableName: "mergeMathLists"
          },
          mathsShadow: {
            dependencyType: "stateVariable",
            variableName: "mathsShadow"
          }
        };
        if (stateValues.mergeMathLists) {
          dependencies.mathChildren = {
            dependencyType: "child",
            childGroups: ["maths"],
            variableNames: ["value"]
          };
        } else {
          dependencies.mathChildren = {
            dependencyType: "child",
            childGroups: ["maths"]
          };
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numComponents = 0;
        let childInfoByComponent = [];
        if (dependencyValues.mathChildren.length > 0) {
          if (dependencyValues.mergeMathLists) {
            for (let [
              childInd,
              child
            ] of dependencyValues.mathChildren.entries()) {
              let childValue = child.stateValues.value;
              if (Array.isArray(childValue.tree) && childValue.tree[0] === "list") {
                let nComponents = childValue.tree.length - 1;
                for (let i2 = 0; i2 < nComponents; i2++) {
                  childInfoByComponent[i2 + numComponents] = {
                    childInd,
                    component: i2,
                    nComponents,
                    childIdx: child.componentIdx
                  };
                }
                numComponents += nComponents;
              } else {
                childInfoByComponent[numComponents] = {
                  childInd,
                  childIdx: child.componentIdx
                };
                numComponents += 1;
              }
            }
          } else {
            numComponents = dependencyValues.mathChildren.length;
            childInfoByComponent = dependencyValues.mathChildren.map((child, i2) => ({
              childInd: i2,
              childIdx: child.componentIdx
            }));
          }
        } else if (dependencyValues.mathsShadow !== null) {
          numComponents = dependencyValues.mathsShadow.length;
        }
        let maxNum = dependencyValues.maxNumber;
        if (numComponents > maxNum) {
          numComponents = maxNum;
          childInfoByComponent = childInfoByComponent.slice(
            0,
            maxNum
          );
        }
        return {
          setValue: { numComponents, childInfoByComponent },
          checkForActualChange: { numComponents: true }
        };
      }
    };
    stateVariableDefinitions.maths = {
      isArray: true,
      entryPrefixes: ["math"],
      stateVariablesDeterminingDependencies: [
        "mergeMathLists",
        "childInfoByComponent"
      ],
      returnArraySizeDependencies: () => ({
        numComponents: {
          dependencyType: "stateVariable",
          variableName: "numComponents"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numComponents];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          mergeMathLists: {
            dependencyType: "stateVariable",
            variableName: "mergeMathLists"
          },
          childInfoByComponent: {
            dependencyType: "stateVariable",
            variableName: "childInfoByComponent"
          },
          mathsShadow: {
            dependencyType: "stateVariable",
            variableName: "mathsShadow"
          }
        };
        for (let arrayKey of arrayKeys) {
          let childIndices = [];
          if (stateValues.childInfoByComponent[arrayKey]) {
            childIndices = [
              stateValues.childInfoByComponent[arrayKey].childInd
            ];
          }
          dependenciesByKey[arrayKey] = {
            mathChildren: {
              dependencyType: "child",
              childGroups: ["maths"],
              variableNames: ["value"],
              childIndices
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let maths = {};
        for (let arrayKey of arrayKeys) {
          let child = dependencyValuesByKey[arrayKey].mathChildren[0];
          if (child) {
            let childValue = child.stateValues.value;
            if (globalDependencyValues.mergeMathLists && Array.isArray(childValue.tree) && childValue.tree[0] === "list") {
              let ind2 = globalDependencyValues.childInfoByComponent[arrayKey].component;
              maths[arrayKey] = childValue.get_component(ind2);
            } else {
              maths[arrayKey] = childValue;
            }
          } else if (globalDependencyValues.mathsShadow !== null) {
            maths[arrayKey] = globalDependencyValues.mathsShadow[arrayKey];
          }
        }
        return { setValue: { maths } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        stateValues,
        workspace
      }) {
        if (globalDependencyValues.mergeMathLists) {
          let instructions2 = [];
          let childInfoByComponent = await stateValues.childInfoByComponent;
          let arrayKeysAddressed = [];
          for (let arrayKey in desiredStateVariableValues.maths) {
            if (!dependencyValuesByKey[arrayKey]) {
              continue;
            }
            if (arrayKeysAddressed.includes(arrayKey)) {
              continue;
            }
            let desiredValue;
            if (childInfoByComponent[arrayKey].nComponents !== void 0) {
              let firstInd = Number(arrayKey) - childInfoByComponent[arrayKey].component;
              let lastInd = firstInd + childInfoByComponent[arrayKey].nComponents - 1;
              if (!workspace.desiredMaths) {
                workspace.desiredMaths = [];
              }
              let desiredTree = ["list"];
              for (let i2 = firstInd; i2 <= lastInd; i2++) {
                if (desiredStateVariableValues.maths[i2] !== void 0) {
                  workspace.desiredMaths[i2] = convertValueToMathExpression(
                    desiredStateVariableValues.maths[i2]
                  );
                } else if (workspace.desiredMaths[i2] === void 0) {
                  workspace.desiredMaths[i2] = (await stateValues.maths)[i2];
                }
                desiredTree.push(
                  workspace.desiredMaths[i2].tree
                );
                arrayKeysAddressed.push(i2.toString());
              }
              desiredValue = me$3.fromAst(desiredTree);
            } else {
              desiredValue = desiredStateVariableValues.maths[arrayKey];
            }
            let child = dependencyValuesByKey[arrayKey].mathChildren[0];
            if (child) {
              instructions2.push({
                setDependency: dependencyNamesByKey[arrayKey].mathChildren,
                desiredValue,
                childIndex: 0,
                variableIndex: 0
              });
            } else if (globalDependencyValues.mathsShadow !== null) {
              if (!workspace.desiredMathShadow) {
                workspace.desiredMathShadow = [
                  ...globalDependencyValues.mathsShadow
                ];
              }
              workspace.desiredMathShadow[arrayKey] = desiredValue;
            }
          }
          if (workspace.desiredMathShadow) {
            instructions2.push({
              setDependency: "mathsShadow",
              desiredValue: workspace.desiredMathShadow
            });
          }
          return {
            success: true,
            instructions: instructions2
          };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.maths) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let child = dependencyValuesByKey[arrayKey].mathChildren[0];
          if (child) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].mathChildren,
              desiredValue: desiredStateVariableValues.maths[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else if (globalDependencyValues.mathsShadow !== null) {
            if (!workspace.desiredMathShadow) {
              workspace.desiredMathShadow = [
                ...globalDependencyValues.mathsShadow
              ];
            }
            workspace.desiredMathShadow[arrayKey] = desiredStateVariableValues.maths[arrayKey];
          }
        }
        if (workspace.desiredMathShadow) {
          instructions.push({
            setDependency: "mathsShadow",
            desiredValue: workspace.desiredMathShadow
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numComponents",
      description: "The number of math expressions in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "maths",
      description: "The list's math expressions."
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        childInfoByComponent: {
          dependencyType: "stateVariable",
          variableName: "childInfoByComponent"
        }
      }),
      // When this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must set
      // stateVariableToEvaluateAfterReplacements
      // to this variable so that it is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let attributesToConvert = gatherRawNumberDisplayFixedResponseAttributes(
      component,
      components
    );
    const copyChild = component.definingChildren.length === 1 && component.definingChildren[0].componentType === "_copy" ? component.definingChildren[0] : null;
    let copyChildSource;
    if (copyChild) {
      const cIdx = await copyChild.stateValues.extendIdx;
      if (cIdx !== -1) {
        copyChildSource = {
          componentIdx: cIdx,
          componentType: components[cIdx].componentType
        };
      }
    }
    let childInfoByComponent = await component.stateValues.childInfoByComponent;
    let numComponents = await component.stateValues.numComponents;
    for (let i2 = 0; i2 < numComponents; i2++) {
      let attributes = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "math",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributes = JSON.parse(JSON.stringify(res.attributes));
        nComponents = res.nComponents;
      }
      if (copyChildSource) {
        nComponents = addShadowNumberDisplayAttributes({
          nComponents,
          stateIdInfo,
          source: copyChildSource,
          compositeIdx: copyChild.componentIdx,
          attributes,
          componentInfoObjects
        });
      }
      let childInfo = childInfoByComponent[i2];
      if (childInfo) {
        let replacementSource = components[childInfo.childIdx];
        if (childInfo.nComponents !== void 0) {
          componentsCopied.push(
            replacementSource.componentIdx + ":" + childInfo.component
          );
        } else {
          componentsCopied.push(replacementSource.componentIdx);
        }
      }
      replacements.push({
        type: "serialized",
        componentType: "math",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes,
        doenetAttributes: {},
        children: [],
        downstreamDependencies: {
          [component.componentIdx]: [
            {
              dependencyType: "referenceShadow",
              compositeIdx: component.componentIdx,
              propVariable: `math${i2 + 1}`
            }
          ]
        }
      });
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    workspace.numComponents = numComponents;
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let numComponents = await component.stateValues.numComponents;
    if (numComponents === workspace.numComponents) {
      let componentsToCopy = [];
      let childInfoByComponent = await component.stateValues.childInfoByComponent;
      for (let childInfo of childInfoByComponent) {
        let replacementSource = components[childInfo.childIdx];
        if (childInfo.nComponents !== void 0) {
          componentsToCopy.push(
            replacementSource.componentIdx + ":" + childInfo.component
          );
        } else {
          componentsToCopy.push(replacementSource.componentIdx);
        }
      }
      if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
        (x2, i2) => x2 === componentsToCopy[i2]
      )) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
function returnScoredSectionAttributes() {
  return {
    aggregateScores: {
      createComponentOfType: "boolean",
      createStateVariable: "aggregateScoresPreliminary",
      defaultValue: false,
      groupName: "scoring",
      description: "Whether to aggregate scores of scored descendants into a section credit-achieved value."
    },
    weight: {
      createComponentOfType: "number",
      createStateVariable: "weight",
      defaultValue: 1,
      public: true,
      groupName: "scoring",
      description: "Relative weight of this section when aggregated by an enclosing scored section."
    },
    sectionWideCheckWork: {
      createComponentOfType: "boolean",
      createStateVariable: "sectionWideCheckWork",
      defaultValue: false,
      public: true,
      groupName: "scoring",
      description: "Whether to show a single section-wide check-work button instead of per-answer buttons."
    },
    maxNumAttempts: {
      createComponentOfType: "integer",
      createStateVariable: "maxNumAttempts",
      defaultValue: Infinity,
      public: true,
      groupName: "scoring",
      description: "Maximum number of times the section-wide check-work button can be submitted. Once reached, all enclosed answers are disabled."
    },
    showCorrectness: {
      createComponentOfType: "boolean",
      createStateVariable: "showCorrectnessPreliminary",
      // This default is shown to authors but is not used to resolve the
      // value: when the attribute is unspecified, the `showCorrectness`
      // state variable ignores this (defaulted) value and falls back to
      // the enclosing ancestor and then the `showCorrectness` flag (which
      // defaults to `true`). It is set to `true` to reflect that effective
      // default.
      defaultValue: true,
      groupName: "scoring",
      description: "Whether to display correctness indicators for the answers it contains."
    },
    colorCorrectness: {
      createComponentOfType: "boolean",
      createStateVariable: "colorCorrectnessPreliminary",
      // See the note on `showCorrectness`: this default is shown to
      // authors but the resolved value falls back to the ancestor and
      // then to whether correctness is shown.
      defaultValue: true,
      groupName: "scoring",
      description: "Whether to color-code the answers it contains based on correctness."
    },
    colorAnswersSeparately: {
      createComponentOfType: "boolean",
      createStateVariable: "colorAnswersSeparately",
      defaultValue: false,
      groupName: "scoring",
      description: "When section-wide check work is enabled, color each answer based on its own correctness rather than the section's overall credit."
    },
    submitLabel: {
      createComponentOfType: "text",
      createStateVariable: "submitLabel",
      defaultValue: "Check Work",
      public: true,
      forRenderer: true,
      groupName: "scoring",
      description: "Label for the section-wide submit button when correctness is shown."
    },
    submitLabelNoCorrectness: {
      createComponentOfType: "text",
      createStateVariable: "submitLabelNoCorrectness",
      defaultValue: "Submit Response",
      public: true,
      forRenderer: true,
      groupName: "scoring",
      description: "Label for the section-wide submit button when correctness is not shown."
    },
    displayDigitsForCreditAchieved: {
      createComponentOfType: "integer",
      createStateVariable: "displayDigitsForCreditAchieved",
      defaultValue: 3,
      public: true,
      groupName: "scoring",
      description: "Number of significant digits to display for the credit achieved value."
    }
  };
}
function returnScoredSectionStateVariableDefinition() {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.scoredDescendants = {
    returnDependencies: () => ({
      scoredDescendants: {
        dependencyType: "descendant",
        componentTypes: [
          "_sectioningComponent",
          "answer",
          "setup",
          "_blockScoredComponent",
          "p",
          "ol",
          "ul",
          "li",
          "div",
          "span"
        ],
        variableNames: [
          "scoredDescendants",
          "aggregateScores",
          "weight"
        ],
        recurseToMatchedChildren: false,
        variablesOptional: true
      }
    }),
    definition({ dependencyValues }) {
      let scoredDescendants = [];
      for (let descendant of dependencyValues.scoredDescendants) {
        if (descendant.componentType === "setup") {
          continue;
        }
        if (descendant.stateValues.aggregateScores || descendant.stateValues.scoredDescendants === void 0) {
          scoredDescendants.push(descendant);
        } else {
          scoredDescendants.push(
            ...descendant.stateValues.scoredDescendants
          );
        }
      }
      return { setValue: { scoredDescendants } };
    }
  };
  stateVariableDefinitions.answerDescendants = {
    returnDependencies: () => ({
      answerDescendants: {
        dependencyType: "descendant",
        componentTypes: ["answer", "_blockScoredComponent"],
        variableNames: ["justSubmitted"],
        recurseToMatchedChildren: false
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          answerDescendants: dependencyValues.answerDescendants
        }
      };
    }
  };
  stateVariableDefinitions.justSubmitted = {
    forRenderer: true,
    returnDependencies: () => ({
      answerDescendants: {
        dependencyType: "stateVariable",
        variableName: "answerDescendants"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          justSubmitted: dependencyValues.answerDescendants.every(
            (x2) => x2.stateValues.justSubmitted
          )
        }
      };
    }
  };
  stateVariableDefinitions.numSubmissions = {
    description: "Number of times the section-wide check-work button has been submitted.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    defaultValue: 0,
    hasEssential: true,
    returnDependencies: () => ({}),
    definition: () => ({
      useEssentialOrDefaultValue: {
        numSubmissions: true
      }
    }),
    inverseDefinition: ({ desiredStateVariableValues }) => ({
      success: true,
      instructions: [
        {
          setEssentialValue: "numSubmissions",
          value: desiredStateVariableValues.numSubmissions
        }
      ]
    })
  };
  stateVariableDefinitions.numAttemptsLeft = {
    description: "Remaining number of section-wide submissions before the maximum is reached.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "integer"
    },
    forRenderer: true,
    returnDependencies: () => ({
      numSubmissions: {
        dependencyType: "stateVariable",
        variableName: "numSubmissions"
      },
      maxNumAttempts: {
        dependencyType: "stateVariable",
        variableName: "maxNumAttempts"
      },
      sectionWideCheckWork: {
        dependencyType: "stateVariable",
        variableName: "sectionWideCheckWork"
      },
      // The nearest enclosing scored container. If it suppresses answer
      // submit buttons, then this container is itself inside a
      // section-wide check work, so its own section-wide button (and thus
      // its `maxNumAttempts`) is not shown — the enclosing container
      // controls the number of attempts.
      ancestorSuppressingAnswerSubmitButtons: {
        dependencyType: "ancestor",
        variableNames: [
          "suppressAnswerSubmitButtons",
          "numAttemptsLeft"
        ]
      },
      // Used to target the ignored-`maxNumAttempts` warning at the
      // attribute itself rather than the whole container.
      maxNumAttemptsAttr: {
        dependencyType: "attributeComponent",
        attributeName: "maxNumAttempts"
      }
    }),
    definition({ dependencyValues, usedDefault }) {
      let sendDiagnostics = [];
      let insideSectionWideCheckWork = dependencyValues.ancestorSuppressingAnswerSubmitButtons?.stateValues.suppressAnswerSubmitButtons;
      if (!usedDefault.maxNumAttempts && dependencyValues.sectionWideCheckWork && insideSectionWideCheckWork) {
        sendDiagnostics.push({
          type: "warning",
          message: "Setting `maxNumAttempts` on a container with `sectionWideCheckWork` that is inside another container with `sectionWideCheckWork` has no effect, as the number of attempts is controlled by the outer container. Set `maxNumAttempts` on the outer container instead.",
          position: dependencyValues.maxNumAttemptsAttr?.position
        });
      }
      let numAttemptsLeft;
      if (insideSectionWideCheckWork) {
        numAttemptsLeft = dependencyValues.ancestorSuppressingAnswerSubmitButtons.stateValues.numAttemptsLeft;
      } else {
        numAttemptsLeft = Math.max(
          0,
          dependencyValues.maxNumAttempts - dependencyValues.numSubmissions
        );
      }
      return {
        setValue: { numAttemptsLeft },
        sendDiagnostics
      };
    }
  };
  stateVariableDefinitions.showCorrectness = {
    forRenderer: true,
    returnDependencies: () => ({
      showCorrectnessPreliminary: {
        dependencyType: "stateVariable",
        variableName: "showCorrectnessPreliminary"
      },
      showCorrectnessFlag: {
        dependencyType: "flag",
        flagName: "showCorrectness"
      },
      showCorrectnessAncestor: {
        dependencyType: "ancestor",
        variableNames: ["showCorrectness"]
      }
    }),
    definition({ dependencyValues, usedDefault }) {
      let showCorrectness;
      if (!usedDefault.showCorrectnessPreliminary) {
        showCorrectness = dependencyValues.showCorrectnessPreliminary;
      } else if (dependencyValues.showCorrectnessAncestor) {
        showCorrectness = dependencyValues.showCorrectnessAncestor.stateValues.showCorrectness;
      } else {
        showCorrectness = dependencyValues.showCorrectnessFlag !== false;
      }
      return { setValue: { showCorrectness } };
    }
  };
  stateVariableDefinitions.colorCorrectness = {
    forRenderer: true,
    returnDependencies: () => ({
      colorCorrectnessPreliminary: {
        dependencyType: "stateVariable",
        variableName: "colorCorrectnessPreliminary"
      },
      showCorrectness: {
        dependencyType: "stateVariable",
        variableName: "showCorrectness"
      },
      colorCorrectnessAncestor: {
        dependencyType: "ancestor",
        variableNames: ["colorCorrectness"]
      }
    }),
    definition({ dependencyValues, usedDefault }) {
      let colorCorrectness = true;
      if (!dependencyValues.showCorrectness) {
        colorCorrectness = false;
      } else if (!usedDefault.colorCorrectnessPreliminary) {
        colorCorrectness = dependencyValues.colorCorrectnessPreliminary;
      } else if (dependencyValues.colorCorrectnessAncestor) {
        colorCorrectness = dependencyValues.colorCorrectnessAncestor.stateValues.colorCorrectness;
      }
      return { setValue: { colorCorrectness } };
    }
  };
  stateVariableDefinitions.displayDecimalsForCreditAchieved = {
    returnDependencies: () => ({}),
    definition: () => ({
      setValue: { displayDecimalsForCreditAchieved: -Infinity }
    })
  };
  stateVariableDefinitions.aggregateScores = {
    description: "Whether scores of scored descendants are aggregated into this section's credit value.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    returnDependencies: () => ({
      aggregateScoresPreliminary: {
        dependencyType: "stateVariable",
        variableName: "aggregateScoresPreliminary"
      },
      sectionWideCheckWork: {
        dependencyType: "stateVariable",
        variableName: "sectionWideCheckWork"
      },
      parentChildrenAggregateScores: {
        dependencyType: "parentStateVariable",
        variableName: "childrenAggregateScores"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          aggregateScores: dependencyValues.aggregateScoresPreliminary || dependencyValues.sectionWideCheckWork || dependencyValues.parentChildrenAggregateScores
        }
      };
    }
  };
  stateVariableDefinitions.creditAchieved = {
    description: "Aggregate credit achieved (between 0 and 1) for scored descendants of this section.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "number",
      addAttributeComponentsShadowingStateVariables: {
        displayDigits: {
          stateVariableToShadow: "displayDigitsForCreditAchieved"
        },
        displayDecimals: {
          stateVariableToShadow: "displayDecimalsForCreditAchieved"
        }
      }
    },
    forRenderer: true,
    defaultValue: 0,
    hasEssential: true,
    additionalStateVariablesDefined: [
      {
        variableName: "percentCreditAchieved",
        description: "Aggregate credit achieved as a percentage (between 0 and 100).",
        public: true,
        shadowingInstructions: {
          createComponentOfType: "number",
          addAttributeComponentsShadowingStateVariables: {
            displayDigits: {
              stateVariableToShadow: "displayDigitsForCreditAchieved"
            },
            displayDecimals: {
              stateVariableToShadow: "displayDecimalsForCreditAchieved"
            }
          }
        },
        defaultValue: 0,
        hasEssential: true
      }
    ],
    stateVariablesDeterminingDependencies: [
      "aggregateScores",
      "scoredDescendants"
    ],
    returnDependencies({ stateValues }) {
      let dependencies = {
        aggregateScores: {
          dependencyType: "stateVariable",
          variableName: "aggregateScores"
        }
      };
      if (stateValues.aggregateScores) {
        dependencies.scoredDescendants = {
          dependencyType: "stateVariable",
          variableName: "scoredDescendants"
        };
        for (let [
          ind,
          descendant
        ] of stateValues.scoredDescendants.entries()) {
          dependencies["creditAchieved" + ind] = {
            dependencyType: "stateVariable",
            componentIdx: descendant.componentIdx,
            variableName: "creditAchieved"
          };
        }
      }
      return dependencies;
    },
    definition({ dependencyValues }) {
      if (!dependencyValues.aggregateScores) {
        return {
          setValue: {
            creditAchieved: 0,
            percentCreditAchieved: 0
          }
        };
      }
      let creditSum = 0;
      let totalWeight = 0;
      for (let [
        ind,
        component
      ] of dependencyValues.scoredDescendants.entries()) {
        let weight = component.stateValues.weight;
        creditSum += dependencyValues["creditAchieved" + ind] * weight;
        totalWeight += weight;
      }
      let creditAchieved;
      if (totalWeight > 0) {
        creditAchieved = creditSum / totalWeight;
      } else {
        creditAchieved = 1;
      }
      let percentCreditAchieved = creditAchieved * 100;
      return { setValue: { creditAchieved, percentCreditAchieved } };
    }
  };
  stateVariableDefinitions.creditAchievedIfSubmit = {
    defaultValue: 0,
    stateVariablesDeterminingDependencies: [
      "aggregateScores",
      "scoredDescendants"
    ],
    returnDependencies({ stateValues }) {
      let dependencies = {
        aggregateScores: {
          dependencyType: "stateVariable",
          variableName: "aggregateScores"
        }
      };
      if (stateValues.aggregateScores) {
        dependencies.scoredDescendants = {
          dependencyType: "stateVariable",
          variableName: "scoredDescendants"
        };
        for (let [
          ind,
          descendant
        ] of stateValues.scoredDescendants.entries()) {
          dependencies["creditAchievedIfSubmit" + ind] = {
            dependencyType: "stateVariable",
            componentIdx: descendant.componentIdx,
            variableName: "creditAchievedIfSubmit"
          };
        }
      }
      return dependencies;
    },
    definition({ dependencyValues }) {
      if (!dependencyValues.aggregateScores) {
        return {
          setValue: {
            creditAchievedIfSubmit: 0
          }
        };
      }
      let creditSum = 0;
      let totalWeight = 0;
      for (let [
        ind,
        component
      ] of dependencyValues.scoredDescendants.entries()) {
        let weight = component.stateValues.weight;
        creditSum += dependencyValues["creditAchievedIfSubmit" + ind] * weight;
        totalWeight += weight;
      }
      let creditAchievedIfSubmit = creditSum / totalWeight;
      return { setValue: { creditAchievedIfSubmit } };
    }
  };
  stateVariableDefinitions.createSubmitAllButton = {
    forRenderer: true,
    additionalStateVariablesDefined: [
      "suppressAnswerSubmitButtons",
      "descendantColorCorrectnessBasedOnIdx"
    ],
    returnDependencies: () => ({
      sectionWideCheckWork: {
        dependencyType: "stateVariable",
        variableName: "sectionWideCheckWork"
      },
      colorAnswersSeparately: {
        dependencyType: "stateVariable",
        variableName: "colorAnswersSeparately"
      },
      ancestorDeterminingSubmit: {
        dependencyType: "ancestor",
        variableNames: [
          "suppressAnswerSubmitButtons",
          "descendantColorCorrectnessBasedOnIdx"
        ]
      }
    }),
    definition({ dependencyValues, componentIdx }) {
      let createSubmitAllButton = false;
      let suppressAnswerSubmitButtons = false;
      let descendantColorCorrectnessBasedOnIdx = null;
      if (dependencyValues.ancestorDeterminingSubmit?.stateValues.suppressAnswerSubmitButtons) {
        suppressAnswerSubmitButtons = true;
        descendantColorCorrectnessBasedOnIdx = dependencyValues.ancestorDeterminingSubmit.stateValues.descendantColorCorrectnessBasedOnIdx;
      } else if (dependencyValues.sectionWideCheckWork) {
        createSubmitAllButton = true;
        suppressAnswerSubmitButtons = true;
        if (!dependencyValues.colorAnswersSeparately) {
          descendantColorCorrectnessBasedOnIdx = componentIdx;
        }
      }
      return {
        setValue: {
          createSubmitAllButton,
          suppressAnswerSubmitButtons,
          descendantColorCorrectnessBasedOnIdx
        }
      };
    }
  };
  stateVariableDefinitions.suppressCheckWork = {
    forRenderer: true,
    returnDependencies: () => ({
      autoSubmit: {
        dependencyType: "flag",
        flagName: "autoSubmit"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          suppressCheckWork: dependencyValues.autoSubmit
        }
      };
    }
  };
  stateVariableDefinitions.showCheckWork = {
    forRenderer: true,
    returnDependencies: () => ({
      createSubmitAllButton: {
        dependencyType: "stateVariable",
        variableName: "createSubmitAllButton"
      },
      suppressCheckWork: {
        dependencyType: "stateVariable",
        variableName: "suppressCheckWork"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          showCheckWork: dependencyValues.createSubmitAllButton && !dependencyValues.suppressCheckWork
        }
      };
    }
  };
  return stateVariableDefinitions;
}
async function submitAllAnswers({
  component,
  actionId,
  sourceInformation = {},
  skipRendererUpdate = false
}) {
  const numAttemptsLeft = await component.stateValues.numAttemptsLeft;
  if (numAttemptsLeft < 1) {
    return;
  }
  component.coreFunctions.requestRecordEvent({
    verb: "submitted",
    object: {
      componentIdx: component.componentIdx,
      componentType: component.componentType
    },
    result: {
      creditAchieved: await component.stateValues.creditAchievedIfSubmit
    }
  });
  let answersToSubmit = [];
  for (let answer of await component.stateValues.answerDescendants) {
    if (!await answer.stateValues.justSubmitted) {
      answersToSubmit.push(answer);
    }
  }
  for (let answer of answersToSubmit) {
    await component.coreFunctions.performAction({
      componentIdx: answer.componentIdx,
      actionName: "submitAnswer",
      args: {
        actionId,
        sourceInformation,
        // Defer renderer updates to the numSubmissions update below so
        // the rendered answer validation state and attempts message
        // update together.
        skipRendererUpdate: true
      }
    });
  }
  await component.coreFunctions.performUpdate({
    updateInstructions: [
      {
        updateType: "updateValue",
        componentIdx: component.componentIdx,
        stateVariable: "numSubmissions",
        value: await component.stateValues.numSubmissions + 1
      }
    ],
    skipRendererUpdate
  });
}
function returnListItemChildStateVariableDefinitions({
  checkInlineVariable = false,
  listItemInlineAlignment = "baseline"
} = {}) {
  return {
    renderInlineForListItem: {
      forRenderer: true,
      additionalStateVariablesDefined: [
        { variableName: "listItemInlineAlignment", forRenderer: true }
      ],
      returnDependencies: () => {
        const dependencies = {
          parentChildrenToRenderInlineForListItem: {
            dependencyType: "parentStateVariable",
            variableName: "childrenToRenderInlineForListItem"
          }
        };
        if (checkInlineVariable) {
          dependencies.inline = {
            dependencyType: "stateVariable",
            variableName: "inline"
          };
        }
        return dependencies;
      },
      definition({
        dependencyValues,
        componentIdx
      }) {
        const shouldRenderInline = returnShouldRenderInline({
          dependencyValues,
          componentIdx
        });
        return {
          setValue: {
            renderInlineForListItem: shouldRenderInline,
            listItemInlineAlignment: shouldRenderInline ? listItemInlineAlignment : "none"
          }
        };
      }
    }
  };
}
function returnPassThroughListItemChildStateVariableDefinitions() {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.renderInlineForListItem = {
    forRenderer: true,
    returnDependencies: () => ({
      parentChildrenToRenderInlineForListItem: {
        dependencyType: "parentStateVariable",
        variableName: "childrenToRenderInlineForListItem"
      }
    }),
    definition({
      dependencyValues,
      componentIdx
    }) {
      const shouldRenderInline = returnShouldRenderInline({
        dependencyValues,
        componentIdx
      });
      return {
        setValue: {
          renderInlineForListItem: shouldRenderInline
        }
      };
    }
  };
  stateVariableDefinitions.childrenToRenderInlineForListItem = {
    returnDependencies: () => ({
      parentChildrenToRenderInlineForListItem: {
        dependencyType: "parentStateVariable",
        variableName: "childrenToRenderInlineForListItem"
      },
      allChildren: {
        dependencyType: "child",
        includeAllChildren: true
      }
    }),
    definition({
      dependencyValues,
      componentIdx
    }) {
      let childrenToRenderInlineForListItem = [];
      const shouldRenderInline = returnShouldRenderInline({
        dependencyValues,
        componentIdx
      });
      if (shouldRenderInline) {
        const firstNonBlankNonLabelChild = dependencyValues.allChildren.find((child) => {
          if (typeof child === "object") {
            return child.componentType !== "label";
          }
          if (typeof child === "string") {
            return child.trim() !== "";
          }
          return false;
        });
        if (firstNonBlankNonLabelChild && typeof firstNonBlankNonLabelChild === "object") {
          childrenToRenderInlineForListItem = [
            firstNonBlankNonLabelChild
          ];
        }
      }
      return {
        setValue: {
          childrenToRenderInlineForListItem
        }
      };
    }
  };
  stateVariableDefinitions.listItemInlineAlignment = {
    forRenderer: true,
    stateVariablesDeterminingDependencies: [
      "childrenToRenderInlineForListItem"
    ],
    returnDependencies: ({
      stateValues
    }) => {
      const dependencies = {
        parentChildrenToRenderInlineForListItem: {
          dependencyType: "parentStateVariable",
          variableName: "childrenToRenderInlineForListItem"
        }
      };
      const child = stateValues.childrenToRenderInlineForListItem?.[0];
      if (child && typeof child === "object") {
        dependencies[`childListItemInlineAlignment`] = {
          dependencyType: "stateVariable",
          componentIdx: child.componentIdx,
          variableName: "listItemInlineAlignment",
          variablesOptional: true
        };
      }
      return dependencies;
    },
    definition({
      dependencyValues,
      componentIdx
    }) {
      const shouldRenderInline = returnShouldRenderInline({
        dependencyValues,
        componentIdx
      });
      if (!shouldRenderInline) {
        return {
          setValue: { listItemInlineAlignment: "none" }
        };
      }
      const childAlignment = dependencyValues[`childListItemInlineAlignment`];
      if (childAlignment === "baseline" || childAlignment === "flex-start") {
        return {
          setValue: {
            listItemInlineAlignment: childAlignment
          }
        };
      }
      return {
        setValue: {
          listItemInlineAlignment: "none"
        }
      };
    }
  };
  return stateVariableDefinitions;
}
function returnShouldRenderInline({
  dependencyValues,
  componentIdx
}) {
  const parentChildrenToRenderInlineForListItem = dependencyValues.parentChildrenToRenderInlineForListItem;
  return Boolean(
    !dependencyValues.inline && parentChildrenToRenderInlineForListItem?.map((c2) => c2.componentIdx).includes(componentIdx)
  );
}
class Div extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAllAnswers: this.submitAllAnswers.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "div";
  static componentDocs = {
    summary: "A generic block-level container"
  };
  static rendererType = "containerBlock";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static includeBlankStringChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    let scoredSectionAttributes = returnScoredSectionAttributes();
    Object.assign(attributes, scoredSectionAttributes);
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnScoredSectionStateVariableDefinition()
    );
    Object.assign(
      stateVariableDefinitions,
      returnPassThroughListItemChildStateVariableDefinitions()
    );
    return stateVariableDefinitions;
  }
  async submitAllAnswers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return submitAllAnswers({
      component: this,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Span extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAllAnswers: this.submitAllAnswers.bind(this)
    });
  }
  static componentType = "span";
  static rendererType = "containerInline";
  static renderChildren = true;
  static componentDocs = {
    summary: "A generic inline container"
  };
  static canDisplayChildErrors = true;
  static includeBlankStringChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    let scoredSectionAttributes = returnScoredSectionAttributes();
    Object.assign(attributes, scoredSectionAttributes);
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnScoredSectionStateVariableDefinition()
    );
    return stateVariableDefinitions;
  }
  async submitAllAnswers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return submitAllAnswers({
      component: this,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
const Divisions = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Div,
  Span
}, Symbol.toStringTag, { value: "Module" }));
class Setup extends CompositeComponent {
  static componentType = "setup";
  static componentDocs = {
    summary: "Container for components used for setup but not rendered"
  };
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let removeDefinitions = function({
      matchedChildren,
      componentInfoObjects,
      nComponents
    }) {
      let componentIsSpecifiedType = componentInfoObjects.componentIsSpecifiedType;
      let foundDefinitionsChildren = false;
      const childrenMovedDefinitions = [];
      for (const child of matchedChildren) {
        childrenMovedDefinitions.push(child);
        if (componentIsSpecifiedType(child, "styleDefinitions") || componentIsSpecifiedType(child, "feedbackDefinitions")) {
          if (child.children?.length > 0) {
            foundDefinitionsChildren = true;
            childrenMovedDefinitions.push(...child.children);
            child.children = [];
          }
        }
      }
      if (foundDefinitionsChildren) {
        return {
          success: true,
          newChildren: childrenMovedDefinitions,
          nComponents
        };
      } else {
        return { success: false };
      }
    };
    sugarInstructions.push({
      replacementFunction: removeDefinitions
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "styleDefinitions",
        componentTypes: ["styleDefinition"]
      },
      {
        group: "feedbackDefinitions",
        componentTypes: ["feedbackDefinition"]
      },
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({}),
      definition() {
        return {
          setValue: { readyToExpandWhenResolved: true }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
let P$2 = class P extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAllAnswers: this.submitAllAnswers.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "p";
  static componentDocs = {
    summary: "A paragraph of inline content"
  };
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static includeBlankStringChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    let scoredSectionAttributes = returnScoredSectionAttributes();
    Object.assign(attributes, scoredSectionAttributes);
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "inlinesAndLists",
        componentTypes: ["_inline", "ol", "ul"]
      },
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnScoredSectionStateVariableDefinition()
    );
    Object.assign(
      stateVariableDefinitions,
      returnListItemChildStateVariableDefinitions()
    );
    stateVariableDefinitions.text = {
      description: "The paragraph's text content.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlinesAndLists"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let text = textFromChildren(dependencyValues.inlineChildren);
        return { setValue: { text } };
      }
    };
    return stateVariableDefinitions;
  }
  async submitAllAnswers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return submitAllAnswers({
      component: this,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
};
class Case extends BaseComponent {
  static componentType = "case";
  static componentDocs = {
    summary: "A single conditional branch within `<conditionalContent>`"
  };
  static rendererType = void 0;
  static inSchemaOnlyInheritAs = [];
  static allowInSchemaAsComponent = void 0;
  static includeBlankStringChildren = true;
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  // since don't have child groups, tell schema about children here
  static additionalSchemaChildren = ["_base"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.condition = {
      createComponentOfType: "boolean",
      description: "Boolean expression; this case is selected when the condition is true."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.serializedChildren = {
      returnDependencies: () => ({
        serializedChildren: {
          dependencyType: "serializedChildren",
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            serializedChildren: dependencyValues.serializedChildren
          }
        };
      }
    };
    stateVariableDefinitions.conditionSatisfied = {
      description: "Whether this case's condition is currently satisfied.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        condition: {
          dependencyType: "attributeComponent",
          attributeName: "condition",
          variableNames: ["value"]
        }
      }),
      definition: function({ dependencyValues }) {
        let conditionSatisfied;
        if (dependencyValues.condition === null) {
          conditionSatisfied = true;
        } else {
          conditionSatisfied = dependencyValues.condition.stateValues.value;
        }
        return { setValue: { conditionSatisfied } };
      }
    };
    return stateVariableDefinitions;
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    let additionalRendererTypes = this.potentialRendererTypesFromSerializedComponents(
      this.serializedChildren
    );
    for (let rendererType of additionalRendererTypes) {
      if (!allPotentialRendererTypes.includes(rendererType)) {
        allPotentialRendererTypes.push(rendererType);
      }
    }
    return allPotentialRendererTypes;
  }
}
class Title extends TextOrInline {
  static componentType = "title";
  static componentDocs = {
    summary: "Creates a title within a document, section, or other block component"
  };
}
class RightHandSide extends MathComponent {
  static componentType = "rightHandSide";
  static componentDocs = {
    summary: "A right-hand-side expression of an equation in an ODE system"
  };
  static rendererType = "math";
}
class XLabel extends Label {
  static componentType = "xLabel";
  static componentDocs = {
    summary: "A label for the x-axis of a graph"
  };
  static rendererType = "label";
}
class YLabel extends Label {
  static componentType = "yLabel";
  static componentDocs = {
    summary: "A label for the y-axis of a graph"
  };
  static rendererType = "label";
}
class MatrixRow extends MathList {
  static componentType = "matrixRow";
  // Author-facing tag is `<row>` — `<matrixRow>` is an implementation
  // detail produced by `<matrix>`'s row/column sugar. The docs index uses
  // `displayName`/`displayContext` to render this entry as
  // `<row> (in a matrix)`, parallel to `<row> (in a table)` (which is
  // declared on the regular `Row` class).
  static componentDocs = {
    summary: "A list of math values defining the row of a matrix",
    docsSlug: "row_matrix",
    displayName: "row",
    displayContext: "in a matrix"
  };
  static excludeFromSchema = true;
}
class MatrixColumn extends MathList {
  static componentType = "matrixColumn";
  // Author-facing tag is `<column>` — `<matrixColumn>` is an implementation
  // detail produced by `<matrix>`'s row/column sugar. The docs index uses
  // `displayName`/`displayContext` to render this entry as
  // `<column> (in a matrix)`, parallel to `<column> (in a table)`
  // (which is declared on the regular `Column` class) and to the same
  // split that `<row>` uses.
  static componentDocs = {
    summary: "A list of math values defining the column of a matrix",
    docsSlug: "column_matrix",
    displayName: "column",
    displayContext: "in a matrix"
  };
  static excludeFromSchema = true;
}
class Statement extends Div {
  static componentType = "statement";
  static componentDocs = {
    summary: "A block container for the statement of a problem, theorem, or similar"
  };
}
class Introduction extends Div {
  static componentType = "introduction";
  static componentDocs = {
    summary: "A block container for the introduction of a section"
  };
}
class Conclusion extends Div {
  static componentType = "conclusion";
  static componentDocs = {
    summary: "A block container for the conclusion of a section"
  };
}
class Topic extends Text {
  static componentType = "topic";
  static rendererType = "text";
  // Present for PreTeXt-conversion compatibility but not surfaced to
  // authors via the schema/autocomplete/reference docs.
  static excludeFromSchema = true;
  static componentDocs = {
    summary: "A topic label rendered as text (e.g. for tagging content)."
  };
}
class RepeatSetup extends Setup {
  static componentType = "_repeatSetup";
}
class Placeholder extends Setup {
  static componentType = "_placeholder";
}
let Br$1 = class Br extends InlineComponent {
  static componentType = "br";
  static componentDocs = {
    summary: "A line break"
  };
};
let Hr$1 = class Hr extends BlockComponent {
  static componentType = "hr";
  static componentDocs = {
    summary: "A horizontal rule used to separate content"
  };
};
class cascadeMessage extends P$2 {
  static componentType = "cascadeMessage";
  static rendererType = "p";
  static inSchemaOnlyInheritAs = [];
  static componentDocs = {
    summary: "Placeholder shown inside a `<cascade>` in place of a not-yet-revealed entry"
  };
}
class Else extends Case {
  static componentType = "else";
  static componentDocs = {
    summary: "Default branch within a `<conditionalContent>`; matched when no `<case>` condition is true"
  };
}
const Aliases = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Br: Br$1,
  Conclusion,
  Else,
  Hr: Hr$1,
  Introduction,
  MatrixColumn,
  MatrixRow,
  Placeholder,
  RepeatSetup,
  RightHandSide,
  Statement,
  Title,
  Topic,
  XLabel,
  YLabel,
  cascadeMessage
}, Symbol.toStringTag, { value: "Module" }));
let M$3 = class M extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveMath: this.moveMath.bind(this),
      mathClicked: this.mathClicked.bind(this),
      mathFocused: this.mathFocused.bind(this)
    });
  }
  static componentType = "m";
  static componentDocs = {
    summary: "Inline math rendered with LaTeX"
  };
  static rendererType = "math";
  // used when creating new component via adapter or copy prop
  static primaryStateVariableForDefinition = "latex";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the math display can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.layer = {
      description: "Z-order layer index when shown on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "inline",
        componentTypes: ["_inline"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.latex = {
      description: "The math content rendered as a LaTeX string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      defaultValue: "",
      hasEssential: true,
      forRenderer: true,
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inline"],
          variableNames: ["latex", "text"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.inlineChildren.length === 0) {
          return {
            useEssentialOrDefaultValue: {
              latex: true
            }
          };
        }
        let latex = createInputStringFromChildren({
          children: dependencyValues.inlineChildren,
          codePre: "",
          format: "latex",
          createDisplayedMathString: true
        }).string;
        return { setValue: { latex } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (typeof desiredStateVariableValues.latex !== "string") {
          return { success: false };
        }
        if (dependencyValues.inlineChildren.length === 0) {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "latex",
                value: desiredStateVariableValues.latex
              }
            ]
          };
        } else if (dependencyValues.inlineChildren.length === 1) {
          let child = dependencyValues.inlineChildren[0];
          if (typeof child !== "object") {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "inlineChildren",
                  desiredValue: desiredStateVariableValues.latex,
                  childIndex: 0
                }
              ]
            };
          } else if (typeof child.stateValues.latex === "string") {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "inlineChildren",
                  desiredValue: desiredStateVariableValues.latex,
                  childIndex: 0,
                  variableIndex: 0
                  // "latex" state variable
                }
              ]
            };
          } else if (typeof child.stateValues.text === "string") {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "inlineChildren",
                  desiredValue: desiredStateVariableValues.latex,
                  childIndex: 0,
                  variableIndex: 1
                  // "text" state variable
                }
              ]
            };
          } else {
            return { success: false };
          }
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.renderMode = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { renderMode: "inline" } })
    };
    stateVariableDefinitions.text = {
      description: "The math content rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        latex: {
          dependencyType: "stateVariable",
          variableName: "latex"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: { text: latexToText(dependencyValues.latex) }
        };
      }
    };
    stateVariableDefinitions.math = {
      description: "The math content as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      returnDependencies: () => ({
        latex: {
          dependencyType: "stateVariable",
          variableName: "latex"
        }
      }),
      definition: function({ dependencyValues }) {
        const latexToMath = latexToMathFactory();
        try {
          return {
            setValue: { math: latexToMath(dependencyValues.latex) }
          };
        } catch (e3) {
          return { setValue: { math: me$3.fromAst("＿") } };
        }
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "latex",
              desiredValue: desiredStateVariableValues.math.toLatex()
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = ["math", "text"];
  async moveMath({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  async mathClicked({
    actionId,
    name,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx: name,
        // use name rather than this.componentIdx to get original name if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async mathFocused({
    actionId,
    name,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx: name,
        // use name rather than this.componentIdx to get original name if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
};
let Me$2 = class Me extends M$3 {
  static componentType = "me";
  static componentDocs = {
    summary: "Display math rendered with LaTeX"
  };
  static canBeInList = false;
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.renderMode.definition = () => ({
      setValue: { renderMode: "display" }
    });
    return stateVariableDefinitions;
  }
};
class Men extends M$3 {
  static componentType = "men";
  static componentDocs = {
    summary: "Numbered display math rendered with LaTeX"
  };
  static canBeInList = false;
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.renderMode.definition = () => ({
      setValue: { renderMode: "numbered" }
    });
    stateVariableDefinitions.equationTag = {
      description: "The equation number (when numbered).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      mustEvaluate: true,
      // must evaluate to make sure all counters are accounted for
      returnDependencies: () => ({
        equationCounter: {
          dependencyType: "counter",
          counterName: "equation"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            equationTag: String(dependencyValues.equationCounter)
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
const MMeMen = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  M: M$3,
  Me: Me$2,
  Men
}, Symbol.toStringTag, { value: "Module" }));
class Md extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveMath: this.moveMath.bind(this),
      mathClicked: this.mathClicked.bind(this),
      mathFocused: this.mathFocused.bind(this)
    });
  }
  static componentType = "md";
  static componentDocs = {
    summary: "Display math with multiple aligned rows"
  };
  static rendererType = "math";
  static canBeInList = false;
  // used when creating new component via adapter or copy prop
  static primaryStateVariableForDefinition = "latex";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the math display can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.layer = {
      description: "Z-order layer index when shown on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "mrows",
        componentTypes: ["mrow"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.mrowChildIndices = {
      returnDependencies: () => ({
        mrowChildren: {
          dependencyType: "child",
          childGroups: ["mrows"]
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          mrowChildIndices: dependencyValues.mrowChildren.map(
            (x2) => x2.componentIdx
          )
        }
      })
    };
    stateVariableDefinitions.mrowChildRendererIds = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["mrowChildIndices"],
      returnDependencies: ({ stateValues }) => {
        const dependencies = {
          numChildren: {
            dependencyType: "value",
            value: stateValues.mrowChildIndices.length
          }
        };
        for (const [i2, idx] of stateValues.mrowChildIndices.entries()) {
          dependencies[`rendererId${i2}`] = {
            dependencyType: "rendererId",
            componentIdx: idx
          };
        }
        return dependencies;
      },
      definition: ({ dependencyValues }) => {
        const mrowChildRendererIds = [];
        for (let i2 = 0; i2 < dependencyValues.numChildren; i2++) {
          mrowChildRendererIds.push(
            dependencyValues[`rendererId${i2}`]
          );
        }
        return {
          setValue: { mrowChildRendererIds }
        };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The math content rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        mrowChildren: {
          dependencyType: "child",
          childGroups: ["mrows"],
          variableNames: ["latex", "hide", "equationTag", "numbered"]
        }
      }),
      definition: function({ dependencyValues }) {
        let latex = "";
        if (dependencyValues.mrowChildren.length > 0) {
          for (let child of dependencyValues.mrowChildren) {
            if (child.stateValues.hide) {
              continue;
            }
            if (latex.length > 0) {
              latex += "\\\\";
            }
            if (child.stateValues.numbered) {
              latex += `\\tag{${child.stateValues.equationTag}}`;
            } else {
              latex += `\\notag `;
            }
            latex += child.stateValues.latex;
          }
        }
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.text = {
      description: "The math content rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        latex: {
          dependencyType: "stateVariable",
          variableName: "latex"
        }
      }),
      definition: function({ dependencyValues }) {
        let expressionText;
        try {
          expressionText = dependencyValues.latex.replaceAll("\\notag", "").replaceAll("\\amp", "").split("\\\\").map((x2) => {
            let result = x2.match(/\\tag\{(\w+)\}(.*)/);
            if (result) {
              x2 = result[2];
            }
            let text = me$3.fromAst(latexToAst.convert(x2)).toString();
            if (result) {
              text += ` (${result[1]})`;
            }
            return text;
          }).join("\\\\\n");
        } catch (e3) {
          return { setValue: { text: dependencyValues.latex } };
        }
        return {
          setValue: {
            text: superSubscriptsToUnicode(
              expressionText.toString()
            )
          }
        };
      }
    };
    stateVariableDefinitions.renderMode = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { renderMode: "align" } })
    };
    stateVariableDefinitions.numbered = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numbered: false } })
    };
    return stateVariableDefinitions;
  }
  async moveMath({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  async mathClicked({
    actionId,
    name,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx: name,
        // use name rather than this.componentIdx to get original name if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async mathFocused({
    actionId,
    name,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx: name,
        // use name rather than this.componentIdx to get original name if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class Mdn extends Md {
  static componentType = "mdn";
  static componentDocs = {
    summary: "Numbered display math with multiple aligned rows"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numbered = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numbered: true } })
    };
    return stateVariableDefinitions;
  }
}
class Mrow extends M$3 {
  static componentType = "mrow";
  static componentDocs = {
    summary: "A row of math within an aligned display"
  };
  static canBeInList = false;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.number = {
      createComponentOfType: "boolean",
      description: "Whether to attach an equation number to this row."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.renderMode.definition = () => ({
      setValue: { renderMode: "display" }
    });
    stateVariableDefinitions.numbered = {
      forRenderer: true,
      returnDependencies: () => ({
        parentNumbered: {
          dependencyType: "parentStateVariable",
          variableName: "numbered"
        },
        numberAttr: {
          dependencyType: "attributeComponent",
          attributeName: "number",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        let numbered;
        if (dependencyValues.numberAttr !== null) {
          numbered = dependencyValues.numberAttr.stateValues.value;
        } else {
          numbered = dependencyValues.parentNumbered;
        }
        return {
          setValue: { numbered }
        };
      }
    };
    stateVariableDefinitions.equationTag = {
      description: "The equation number (when numbered).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["numbered"],
      mustEvaluate: true,
      // must evaluate to make sure all counters are accounted for
      returnDependencies({ stateValues }) {
        if (stateValues.numbered) {
          return {
            equationCounter: {
              dependencyType: "counter",
              counterName: "equation"
            }
          };
        } else {
          return {};
        }
      },
      definition({ dependencyValues }) {
        if (dependencyValues.equationCounter !== void 0) {
          return {
            setValue: {
              equationTag: String(
                dependencyValues.equationCounter
              )
            }
          };
        } else {
          return { setValue: { equationTag: null } };
        }
      }
    };
    return stateVariableDefinitions;
  }
}
const MdMdnMrow = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Md,
  Mdn,
  Mrow
}, Symbol.toStringTag, { value: "Module" }));
const { mod: mod$4, min, fraction, number: mathNumber } = me$3.math;
function periodicSetEquality(expr, other, { min_elements_match = 3, match_partial = false } = {}) {
  if (!is_periodic_set(expr)) return false;
  if (is_periodic_set(other)) {
    if (match_partial) {
      let match1 = contained_in(expr.tree, other.tree, match_partial);
      if (match1 === false) {
        return 0;
      }
      let match2 = contained_in(other.tree, expr.tree, match_partial);
      if (match2 === false) {
        return 0;
      }
      if (match1 === true) {
        if (match2 === true) {
          return 1;
        } else {
          return match2;
        }
      } else if (match2 === true) {
        return match1;
      } else {
        return Math.min(match1, match2);
      }
    } else {
      return contained_in(expr.tree, other.tree, match_partial) && contained_in(other.tree, expr.tree, match_partial);
    }
  } else {
    let operands = expr.tree.slice(1);
    if (operands.length > 1) {
      return false;
    }
    let offset = me$3.fromAst(operands[0][1]).evaluate_to_constant();
    let period = me$3.fromAst(["apply", "abs", operands[0][2]]).evaluate_to_constant();
    if (!Number.isFinite(offset) || !Number.isFinite(period)) {
      return false;
    }
    let min_index = operands[0][3];
    let max_index = operands[0][4];
    if (min_index !== -Infinity || max_index !== Infinity) {
      return false;
    }
    if (other.tree[0] !== "list") {
      return false;
    }
    let number_list = other.tree.slice(1);
    let n_ldots = 0;
    if (number_list[0][0] === "ldots") {
      n_ldots++;
      number_list = number_list.slice(1);
    } else if (!match_partial) {
      return false;
    }
    if (number_list[number_list.length - 1][0] === "ldots") {
      n_ldots++;
      number_list = number_list.slice(0, number_list.length - 1);
    } else if (!match_partial) {
      return false;
    }
    let n_in_list = number_list.length;
    if (n_in_list === 0) {
      return false;
    }
    if (!match_partial && n_in_list < min_elements_match) {
      return false;
    }
    number_list = number_list.map(
      (x2) => me$3.fromAst(x2).evaluate_to_constant()
    );
    let offset_diff = mod$4(number_list[0] - offset, period);
    if (!(min(offset_diff, period - offset_diff) < 1e-10 * period)) {
      return false;
    }
    let n_matches = 1;
    for (let i2 = 1; i2 < n_in_list; i2++) {
      if (Math.abs(number_list[i2] - number_list[i2 - 1] - period) < 1e-10 * period) {
        n_matches++;
      } else {
        if (!match_partial) {
          return false;
        }
        break;
      }
    }
    if (n_matches === n_in_list && n_ldots === 2 && n_in_list >= min_elements_match) {
      return true;
    }
    let fraction_matched = n_matches / Math.max(n_in_list, min_elements_match);
    fraction_matched *= (9 + n_ldots / 2) / 10;
    return fraction_matched;
  }
}
function is_periodic_set(expr) {
  var tree = expr.tree;
  if (!Array.isArray(tree)) return false;
  if (tree[0] !== "periodic_set") return false;
  var operands = tree.slice(1);
  for (var v2 of operands) {
    if (!Array.isArray(v2)) return false;
    if (v2[0] !== "tuple") return false;
    if (v2.length !== 5) return false;
  }
  return true;
}
function contained_in(tree, i_set, match_partial) {
  if (tree[0] === "periodic_set") {
    if (match_partial) {
      let num_matches = 0;
      for (let piece of tree.slice(1)) {
        let match = contained_in(piece, i_set, match_partial);
        if (match === true) {
          num_matches++;
        } else if (match !== false) {
          num_matches += match;
        }
      }
      let num_pieces = tree.length - 1;
      if (num_matches === num_pieces) {
        return true;
      } else if (num_matches === 0) {
        return false;
      } else {
        return num_matches / num_pieces;
      }
    } else {
      return tree.slice(1).every((v2) => contained_in(v2, i_set));
    }
  }
  var offset0 = tree[1];
  var period0 = tree[2];
  var min_index = tree[3];
  var max_index = tree[4];
  if (min_index !== -Infinity || max_index !== Infinity) return false;
  offset0 = me$3.fromAst(["/", offset0, period0]).evaluate_to_constant();
  var tuples = i_set.slice(1);
  var data = [];
  for (let i2 = 0; i2 < tuples.length; i2++) {
    let this_min_index = tuples[i2][3];
    let this_max_index = tuples[i2][4];
    if (this_min_index !== -Infinity || this_max_index !== Infinity)
      return false;
    let offset = me$3.fromAst(["/", tuples[i2][1], period0]).evaluate_to_constant();
    let period = me$3.fromAst(["apply", "abs", ["/", tuples[i2][2], period0]]).evaluate_to_constant();
    if (typeof period !== "number" || Number.isNaN(period)) return false;
    let frac = fraction(period);
    let p2 = mathNumber(frac.n);
    if (p2 > 1e3) {
      return false;
    }
    let q2 = mathNumber(frac.d);
    data.push([p2, q2, offset, period]);
  }
  data.sort();
  while (true) {
    let p2 = data[0][0];
    if (p2 !== 1) break;
    let offset = data[0][2];
    let period = data[0][3];
    let offset_diff = me$3.fromAst(["+", offset, ["-", offset0]]).evaluate_to_constant();
    if (Number.isFinite(offset_diff) && Number.isFinite(period)) {
      offset_diff = mod$4(offset_diff, period);
      if (min(offset_diff, period - offset_diff) < 1e-10 * period)
        return true;
    }
    data.splice(0, 1);
    if (data.length === 0) return false;
  }
  var all_ps = [...new Set(data.map((v2) => v2[0]))];
  let max_fraction_covered = 0;
  for (let base_p of all_ps) {
    let options = data.map(function(v2, i2) {
      let m2 = base_p / v2[0];
      if (Number.isInteger(m2)) return [v2[0], m2, i2];
      else return void 0;
    }).filter((v2) => v2);
    let covered = [];
    for (let opt of options) {
      let p2 = opt[0];
      let m2 = opt[1];
      let i2 = opt[2];
      let offset = data[i2][2];
      let period = data[i2][3];
      for (let j2 = 0; j2 < p2; j2++) {
        let offset_diff = me$3.fromAst(["+", offset, ["-", ["+", offset0, j2]]]).evaluate_to_constant();
        if (Number.isFinite(offset_diff) && Number.isFinite(period)) {
          offset_diff = mod$4(offset_diff, period);
          if (min(offset_diff, period - offset_diff) < 1e-10 * period) {
            for (let k2 = 0; k2 < m2; k2++) {
              covered[j2 + k2 * p2] = true;
            }
            let covered_all = true;
            for (let ind = 0; ind < base_p; ind++) {
              if (!covered[ind]) {
                covered_all = false;
                break;
              }
            }
            if (covered_all) return true;
            break;
          }
        }
      }
    }
    if (match_partial) {
      let fraction_covered = 0;
      for (let ind = 0; ind < base_p; ind++) {
        if (covered[ind]) {
          fraction_covered++;
        }
      }
      fraction_covered /= base_p;
      if (fraction_covered > max_fraction_covered) {
        max_fraction_covered = fraction_covered;
      }
    }
  }
  if (match_partial && max_fraction_covered > 0) {
    return max_fraction_covered;
  }
  return false;
}
const nonTupleVectorOperators = ["vector", "altvector"];
function checkEquality({
  object1,
  object2,
  isUnordered = false,
  partialMatches = false,
  matchByExactPositions = false,
  symbolicEquality = false,
  simplify = "none",
  expand = false,
  allowedErrorInNumbers = 0,
  includeErrorInNumberExponents = false,
  allowedErrorIsAbsolute = false,
  numSignErrorsMatched = 0,
  numPeriodicSetMatchesRequired = 3,
  caseInsensitiveMatch = false,
  matchBlanks = false
}) {
  let haveMathExpressions = false;
  if (object1 instanceof me$3.class) {
    haveMathExpressions = true;
    if (!(object2 instanceof me$3.class)) {
      if (typeof object2 === "number" || typeof object2 === "string") {
        object2 = me$3.fromAst(object2);
      } else {
        return { fraction_equal: 0 };
      }
    }
  } else if (object2 instanceof me$3.class) {
    haveMathExpressions = true;
    if (typeof object1 === "number" || typeof object1 === "string") {
      object1 = me$3.fromAst(object1);
    } else {
      return { fraction_equal: 0 };
    }
  }
  let normalize = function(x2) {
    if (!(x2 instanceof me$3.class)) {
      x2 = me$3.fromAst(x2);
    }
    x2 = x2.normalize_function_names().normalize_applied_functions();
    if (caseInsensitiveMatch) {
      x2 = me$3.fromAst(setStringsInTreeToLowerCase(x2.tree));
    }
    let effectiveSimplify = simplify, effectiveExpand = expand;
    if (effectiveSimplify === "none" || effectiveSimplify === "normalizeorder") {
      if (allowedErrorInNumbers > 0) {
        x2 = x2.constants_to_floats();
      }
    } else if (effectiveSimplify === "numberspreserveorder") {
      x2 = x2.evaluate_numbers({
        max_digits: Infinity,
        skip_ordering: true
      });
    } else if (effectiveSimplify === "number") {
      x2 = x2.evaluate_numbers({ max_digits: Infinity });
    } else {
      x2 = x2.evaluate_numbers({
        max_digits: Infinity,
        evaluate_functions: true
      });
    }
    return normalizeMathExpression({
      value: x2,
      simplify: effectiveSimplify,
      expand: effectiveExpand
    });
  };
  let check_equality;
  if (haveMathExpressions) {
    if (symbolicEquality) {
      check_equality = function(a2, b2) {
        let expr_a = a2;
        let expr_b = b2;
        if (!(a2 instanceof me$3.class)) {
          expr_a = me$3.fromAst(a2);
        }
        if (!(b2 instanceof me$3.class)) {
          expr_b = me$3.fromAst(b2);
        }
        if (numSignErrorsMatched > 0) {
          expr_b = JSON.parse(JSON.stringify(expr_b), me$3.reviver);
          let equalityFunction = function(aa2, bb) {
            aa2 = normalize(aa2);
            return aa2.equalsViaSyntax(bb, {
              allowed_error_in_numbers: allowedErrorInNumbers,
              include_error_in_number_exponents: includeErrorInNumberExponents,
              allowed_error_is_absolute: allowedErrorIsAbsolute,
              allow_blanks: matchBlanks
            });
          };
          if (expr_a.tree.im || expr_b.tree.im) {
            return { fraction_equal: 0 };
          }
          let equality = me$3.equalSpecifiedSignErrors(expr_b, expr_a, {
            equalityFunction,
            n_sign_errors: numSignErrorsMatched
          });
          return { fraction_equal: equality ? 1 : 0 };
        } else {
          if (expr_a.tree.im || expr_b.tree.im) {
            return { fraction_equal: 0 };
          }
          let equality = expr_a.equalsViaSyntax(expr_b, {
            allowed_error_in_numbers: allowedErrorInNumbers,
            include_error_in_number_exponents: includeErrorInNumberExponents,
            allowed_error_is_absolute: allowedErrorIsAbsolute,
            allow_blanks: matchBlanks
          });
          return { fraction_equal: equality ? 1 : 0 };
        }
      };
    } else {
      check_equality = function(a2, b2) {
        let expr_a = a2;
        let expr_b = b2;
        if (!(a2 instanceof me$3.class)) {
          expr_a = me$3.fromAst(a2);
        }
        if (!(b2 instanceof me$3.class)) {
          expr_b = me$3.fromAst(b2);
        }
        if (caseInsensitiveMatch) {
          expr_a = me$3.fromAst(
            setStringsInTreeToLowerCase(expr_a.tree)
          );
          expr_b = me$3.fromAst(
            setStringsInTreeToLowerCase(expr_b.tree)
          );
        }
        if (numSignErrorsMatched > 0) {
          expr_b = JSON.parse(JSON.stringify(expr_b), me$3.reviver);
          let equalityFunction = function(aa2, bb) {
            return aa2.equals(bb, {
              allowed_error_in_numbers: allowedErrorInNumbers,
              include_error_in_number_exponents: includeErrorInNumberExponents,
              allowed_error_is_absolute: allowedErrorIsAbsolute,
              allow_blanks: matchBlanks
            });
          };
          if (expr_a.tree.im || expr_b.tree.im) {
            return { fraction_equal: 0 };
          }
          let equality = me$3.equalSpecifiedSignErrors(expr_b, expr_a, {
            equalityFunction,
            n_sign_errors: numSignErrorsMatched
          });
          return { fraction_equal: equality ? 1 : 0 };
        } else {
          if (expr_a.tree.im || expr_b.tree.im) {
            return { fraction_equal: 0 };
          }
          let equality = expr_a.equals(expr_b, {
            allowed_error_in_numbers: allowedErrorInNumbers,
            include_error_in_number_exponents: includeErrorInNumberExponents,
            allowed_error_is_absolute: allowedErrorIsAbsolute,
            allow_blanks: matchBlanks
          });
          return { fraction_equal: equality ? 1 : 0 };
        }
      };
    }
  } else {
    check_equality = (a2, b2) => {
      if (caseInsensitiveMatch) {
        a2 = convertStringsToLowerCase(a2);
        b2 = convertStringsToLowerCase(b2);
      }
      return { fraction_equal: deepCompare(a2, b2) ? 1 : 0 };
    };
  }
  let partialMatchesOnRecursion = false;
  let matchByExactPositionsOnRecursion = false;
  if (haveMathExpressions) {
    if (symbolicEquality) {
      object1 = normalize(object1);
      object2 = normalize(object2);
    } else {
      object1 = object1.perform_vector_matrix_additions_scalar_multiplications();
      object2 = object2.perform_vector_matrix_additions_scalar_multiplications();
    }
    let object1_operator = object1.tree[0];
    let object2_operator = object2.tree[0];
    if (object1_operator === "periodic_set" || object2_operator === "periodic_set") {
      let set1 = object1, set2 = object2;
      if (object1_operator !== "periodic_set") {
        set1 = object2;
        set2 = object1;
      }
      let equality = periodicSetEquality(set1, set2, {
        match_partial: partialMatches,
        min_elements_match: numPeriodicSetMatchesRequired
      });
      if (equality === true) {
        return { fraction_equal: 1 };
      } else if (equality === false) {
        return { fraction_equal: 0 };
      } else {
        return { fraction_equal: equality };
      }
    } else if (object1_operator === "list") {
      object1 = object1.tree.slice(1);
      if (object2_operator === "list") {
        object2 = object2.tree.slice(1);
      } else {
        object2 = [object2.tree];
      }
    } else if (object2_operator === "list") {
      object2 = object2.tree.slice(1);
      object1 = [object1.tree];
    } else if (nonTupleVectorOperators.includes(object1_operator)) {
      object1 = object1.tree.slice(1);
      if (object2_operator === "interval" || object2_operator === "matrix" || object2_operator === "array" || object2_operator === "set") {
        return { fraction_equal: 0 };
      } else if (vectorOperators.includes(object2_operator)) {
        object2 = object2.tree.slice(1);
      } else {
        object2 = [object2.tree];
      }
    } else if (nonTupleVectorOperators.includes(object2_operator)) {
      object2 = object2.tree.slice(1);
      if (object1_operator === "interval" || object1_operator === "matrix" || object1_operator === "array" || object1_operator === "set") {
        return { fraction_equal: 0 };
      } else if (object1_operator === "tuple") {
        object1 = object1.tree.slice(1);
      } else {
        object1 = [object1.tree];
      }
    } else if (object1_operator === "interval") {
      matchByExactPositions = true;
      let closedInfo = object1.tree[2];
      let leftClosed = closedInfo[1];
      let rightClosed = closedInfo[2];
      object1 = object1.tree[1].slice(1);
      if (object2_operator === "matrix" || object2_operator === "set") {
        return { fraction_equal: 0 };
      } else if (object2_operator === "tuple") {
        let operands = object2.tree.slice(1);
        if (operands.length === 2 && leftClosed === false && rightClosed === false) {
          object2 = operands;
        } else {
          return { fraction_equal: 0 };
        }
      } else if (object2_operator === "array") {
        let operands2 = object2.tree.slice(1);
        if (operands2.length === 2 && leftClosed === true && rightClosed === true) {
          object2 = operands2;
        } else {
          return { fraction_equal: 0 };
        }
      } else if (object2_operator === "interval") {
        let closedInfo2 = object2.tree[2];
        if (closedInfo2[1] !== leftClosed || closedInfo2[2] !== rightClosed) {
          return { fraction_equal: 0 };
        }
        object2 = object2.tree[1].slice(1);
      } else {
        return { fraction_equal: 0 };
      }
    } else if (object2_operator === "interval") {
      matchByExactPositions = true;
      let closedInfo = object2.tree[2];
      let leftClosed = closedInfo[1];
      let rightClosed = closedInfo[2];
      object2 = object2.tree[1].slice(1);
      if (object1_operator === "matrix" || object1_operator === "set") {
        return { fraction_equal: 0 };
      } else if (object1_operator === "tuple") {
        let operands = object1.tree.slice(1);
        if (operands.length === 2 && leftClosed === false && rightClosed === false) {
          object1 = operands;
        } else {
          return { fraction_equal: 0 };
        }
      } else if (object1_operator === "array") {
        let operands = object1.tree.slice(1);
        if (operands.length === 2 && leftClosed === true && rightClosed === true) {
          object1 = operands;
        } else {
          return { fraction_equal: 0 };
        }
      } else {
        return { fraction_equal: 0 };
      }
    } else if (object1_operator === "matrix") {
      if (object2_operator === "matrix") {
        object1 = convertMatrixToArrayOfTuples(object1.tree.slice(1));
        object2 = convertMatrixToArrayOfTuples(object2.tree.slice(1));
        matchByExactPositions = true;
        matchByExactPositionsOnRecursion = true;
        partialMatchesOnRecursion = true;
      } else {
        return { fraction_equal: 0 };
      }
    } else if (object2_operator === "matrix") {
      return { fraction_equal: 0 };
    } else if (object1_operator === "set") {
      let distinctElements = [];
      for (let v2 of object1.tree.slice(1)) {
        if (!distinctElements.some(
          (x2) => check_equality(x2, v2).fraction_equal === 1
        )) {
          distinctElements.push(v2);
        }
      }
      object1 = distinctElements;
      isUnordered = true;
      if (object2_operator === "tuple" || object2_operator === "array") {
        return { fraction_equal: 0 };
      } else if (object2_operator === "set") {
        distinctElements = [];
        for (let v2 of object2.tree.slice(1)) {
          if (!distinctElements.some(
            (x2) => check_equality(x2, v2).fraction_equal === 1
          )) {
            distinctElements.push(v2);
          }
        }
        object2 = distinctElements;
      } else {
        object2 = [object2.tree];
      }
    } else if (object2_operator === "set") {
      let distinctElements = [];
      for (let v2 of object2.tree.slice(1)) {
        if (!distinctElements.some(
          (x2) => check_equality(x2, v2).fraction_equal === 1
        )) {
          distinctElements.push(v2);
        }
      }
      object2 = distinctElements;
      isUnordered = true;
      if (object1_operator === "tuple" || object1_operator === "array") {
        return { fraction_equal: 0 };
      } else {
        object1 = [object1.tree];
      }
    } else if (object1_operator === "tuple") {
      object1 = object1.tree.slice(1);
      if (object2_operator === "array") {
        return { fraction_equal: 0 };
      } else if (object2_operator === "tuple") {
        object2 = object2.tree.slice(1);
      } else {
        object2 = [object2.tree];
      }
    } else if (object2_operator === "tuple") {
      object2 = object2.tree.slice(1);
      if (object1_operator === "array") {
        return { fraction_equal: 0 };
      } else {
        object1 = [object1.tree];
      }
    } else if (object1_operator === "array") {
      object1 = object1.tree.slice(1);
      if (object2_operator === "array") {
        object2 = object2.tree.slice(1);
      } else {
        object2 = [object2.tree];
      }
    } else if (object2_operator === "array") {
      object2 = object2.tree.slice(1);
      object1 = [object1.tree];
    } else {
      return check_equality(object1.tree, object2.tree);
    }
  } else {
    if (Array.isArray(object1)) {
      if (!Array.isArray(object2)) {
        if (typeof object2 === "string" && object1.every(
          (x2) => typeof x2 === "string" && !x2.includes(",")
        )) {
          object2 = object2.split(",").map((x2) => x2.trim());
        } else {
          object2 = [object2];
        }
      }
    } else if (Array.isArray(object2)) {
      if (typeof object1 === "string" && object2.every((x2) => typeof x2 === "string" && !x2.includes(","))) {
        object1 = object1.split(",").map((x2) => x2.trim());
      } else {
        object1 = [object1];
      }
    } else {
      return check_equality(object1, object2);
    }
  }
  let nelts1 = object1.length;
  let nelts2 = object2.length;
  let results = { fraction_equal: 0 };
  if (matchByExactPositions) {
    isUnordered = false;
  }
  if (!isUnordered) {
    let n_matches2 = 0;
    let minN = Math.min(nelts1, nelts2);
    for (let i2 = 0; i2 < minN; i2++) {
      let sub_results = checkEquality({
        object1: me$3.fromAst(object1[i2]),
        object2: me$3.fromAst(object2[i2]),
        isUnordered: false,
        partialMatches: partialMatchesOnRecursion,
        matchByExactPositions: matchByExactPositionsOnRecursion,
        symbolicEquality,
        simplify,
        expand,
        allowedErrorInNumbers,
        allowedErrorIsAbsolute,
        numSignErrorsMatched,
        numPeriodicSetMatchesRequired,
        caseInsensitiveMatch,
        matchBlanks
      });
      n_matches2 += sub_results.fraction_equal;
    }
    if (nelts1 === nelts2 && nelts1 === n_matches2) {
      results["fraction_equal"] = 1;
      return results;
    }
    if (!partialMatches) {
      return results;
    }
    if (matchByExactPositions) {
      results["fraction_equal"] = n_matches2 / Math.max(nelts1, nelts2);
      return results;
    }
    let C3 = Array.from(Array(nelts1 + 1), (_2) => Array(nelts2 + 1).fill(0));
    for (let i2 = 0; i2 < nelts1; i2++) {
      for (let j2 = 0; j2 < nelts2; j2++) {
        let sub_results = checkEquality({
          object1: me$3.fromAst(object1[i2]),
          object2: me$3.fromAst(object2[j2]),
          isUnordered: false,
          partialMatches: partialMatchesOnRecursion,
          matchByExactPositions: matchByExactPositionsOnRecursion,
          symbolicEquality,
          simplify,
          expand,
          allowedErrorInNumbers,
          allowedErrorIsAbsolute,
          numSignErrorsMatched,
          numPeriodicSetMatchesRequired,
          caseInsensitiveMatch,
          matchBlanks
        });
        C3[i2 + 1][j2 + 1] = Math.max(
          C3[i2][j2] + sub_results.fraction_equal,
          C3[i2 + 1][j2],
          C3[i2][j2 + 1]
        );
      }
    }
    let max_matched = C3[nelts1][nelts2];
    results["fraction_equal"] = max_matched / Math.max(nelts1, nelts2);
    return results;
  }
  let object2_indices_used = /* @__PURE__ */ new Set();
  let n_matches = 0;
  for (let expr1 of object1) {
    let best_match_ind = -1;
    let best_match = 0;
    for (let [i2, expr2] of object2.entries()) {
      if (object2_indices_used.has(i2)) {
        continue;
      }
      let sub_results = checkEquality({
        object1: me$3.fromAst(expr1),
        object2: me$3.fromAst(expr2),
        isUnordered: false,
        partialMatches: partialMatchesOnRecursion,
        matchByExactPositions: matchByExactPositionsOnRecursion,
        symbolicEquality,
        simplify,
        expand,
        allowedErrorInNumbers,
        allowedErrorIsAbsolute,
        numSignErrorsMatched,
        numPeriodicSetMatchesRequired,
        caseInsensitiveMatch,
        matchBlanks
      });
      if (sub_results.fraction_equal > best_match) {
        best_match = sub_results.fraction_equal;
        best_match_ind = i2;
      }
    }
    if (best_match_ind !== -1) {
      n_matches += best_match;
      object2_indices_used.add(best_match_ind);
    }
  }
  if (nelts1 === nelts2 && nelts1 === n_matches) {
    results["fraction_equal"] = 1;
    return results;
  }
  if (!partialMatches) {
    return results;
  }
  results["fraction_equal"] = n_matches / Math.max(nelts1, nelts2);
  return results;
}
function setStringsInTreeToLowerCase(tree) {
  if (typeof tree === "string") {
    return tree.toLowerCase();
  }
  if (!Array.isArray(tree)) {
    return tree;
  }
  return [tree[0], ...tree.slice(1).map(setStringsInTreeToLowerCase)];
}
function convertStringsToLowerCase(x2) {
  if (typeof x2 === "string") {
    return x2.toLowerCase();
  }
  if (!Array.isArray(x2)) {
    return x2;
  }
  return x2.map(convertStringsToLowerCase);
}
function convertMatrixToArrayOfTuples(matrixOperands) {
  let numRows = matrixOperands[0][1];
  let numColumns = matrixOperands[0][2];
  if (!(Number.isInteger(numRows) && Number.isInteger(numColumns))) {
    return matrixOperands;
  }
  let result = [];
  for (let rowInd = 0; rowInd < numRows; rowInd++) {
    let row = ["tuple"];
    let rowOperands = matrixOperands[1][rowInd + 1] || [];
    for (let colInd = 0; colInd < numColumns; colInd++) {
      let val = rowOperands[colInd + 1];
      if (val === void 0 || val === null) {
        val = "＿";
      }
      row.push(val);
    }
    result.push(row);
  }
  return result;
}
const appliedFunctionSymbolsWithBooleanOperators = [
  ...appliedFunctionSymbolsDefault,
  "isnumber",
  "isinteger"
];
var fromTextUnsplit = textToMathFactory({
  splitSymbols: false,
  appliedFunctionSymbols: appliedFunctionSymbolsWithBooleanOperators,
  parseScientificNotation: false
});
var fromTextSplit = textToMathFactory({
  parseScientificNotation: false
});
function buildParsedExpression({
  dependencyValues,
  componentInfoObjects,
  doNotSplit = false,
  splitAtInitialLevel = false
}) {
  let codePre = "comp";
  let foundInString = true;
  while (foundInString) {
    foundInString = false;
    for (let child of dependencyValues.stringChildren) {
      if (child.includes(codePre)) {
        foundInString = true;
        codePre += "p";
        break;
      }
    }
  }
  let stringResults = createInputStringFromChildren({
    children: dependencyValues.allChildren,
    codePre,
    format: "text",
    createInternalLists: true,
    parser: fromTextUnsplit
  });
  let inputString = stringResults.string;
  let internalLists = stringResults.internalLists;
  let subnum = 0;
  let nonMathCodes = [];
  for (let child of dependencyValues.allChildren) {
    if (typeof child !== "string") {
      let code = codePre + subnum;
      if (!(componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: child.componentType,
        baseComponentType: "math"
      }) || componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: child.componentType,
        baseComponentType: "number"
      }))) {
        nonMathCodes.push(code);
      }
      subnum += 1;
    }
  }
  let parsedExpression = null;
  try {
    parsedExpression = fromTextUnsplit(inputString);
  } catch (e3) {
  }
  if (parsedExpression) {
    parsedExpression = parsedExpression.substitute(internalLists);
    if (!doNotSplit) {
      parsedExpression = me$3.fromAst(
        splitSymbolsIfMath({
          logicTree: parsedExpression.tree,
          nonMathCodes,
          init: !splitAtInitialLevel
        })
      );
    }
  }
  return {
    setValue: {
      codePre,
      parsedExpression
    }
  };
}
function returnChildrenByCodeStateVariableDefinitions() {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.mathChildrenByCode = {
    additionalStateVariablesDefined: [
      "numberChildrenByCode",
      "textChildrenByCode",
      "booleanChildrenByCode",
      "otherChildrenByCode"
    ],
    returnDependencies: () => ({
      children: {
        dependencyType: "child",
        childGroups: ["comparableTypes"],
        variableNames: [
          "value",
          "fractionSatisfied",
          "unordered",
          "inUnorderedList"
        ],
        variablesOptional: true
      },
      codePre: {
        dependencyType: "stateVariable",
        variableName: "codePre"
      }
    }),
    definition({ dependencyValues, componentInfoObjects }) {
      let mathChildrenByCode = {};
      let numberChildrenByCode = {};
      let textChildrenByCode = {};
      let booleanChildrenByCode = {};
      let otherChildrenByCode = {};
      let subnum = 0;
      let codePre = dependencyValues.codePre;
      for (let child of dependencyValues.children) {
        let code = codePre + subnum;
        if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: child.componentType,
          baseComponentType: "math"
        })) {
          mathChildrenByCode[code] = child;
        } else if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: child.componentType,
          baseComponentType: "number"
        })) {
          numberChildrenByCode[code] = child;
        } else if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: child.componentType,
          baseComponentType: "text"
        })) {
          textChildrenByCode[code] = child;
        } else if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: child.componentType,
          baseComponentType: "boolean"
        })) {
          booleanChildrenByCode[code] = child;
        } else {
          otherChildrenByCode[code] = child;
        }
        subnum += 1;
      }
      return {
        setValue: {
          mathChildrenByCode,
          numberChildrenByCode,
          textChildrenByCode,
          booleanChildrenByCode,
          otherChildrenByCode
        }
      };
    }
  };
  return stateVariableDefinitions;
}
function evaluateLogic({
  logicTree,
  canOverrideUnorderedCompare = false,
  dependencyValues,
  valueOnInvalid = 0
}) {
  let evaluateSub = (x2) => evaluateLogic({
    logicTree: x2,
    canOverrideUnorderedCompare,
    dependencyValues,
    valueOnInvalid
  });
  if (!Array.isArray(logicTree)) {
    if (typeof logicTree === "string") {
      let booleanChild = dependencyValues.booleanChildrenByCode[logicTree];
      if (booleanChild) {
        if (dependencyValues.matchPartial && booleanChild.stateValues.fractionSatisfied !== void 0) {
          return booleanChild.stateValues.fractionSatisfied;
        } else {
          return booleanChild.stateValues.value ? 1 : 0;
        }
      } else {
        let mathChild = dependencyValues.mathChildrenByCode[logicTree];
        if (mathChild) {
          let numericalValue = mathChild.stateValues.value.simplify().evaluate_to_constant();
          if (Number.isFinite(numericalValue) && numericalValue !== 0) {
            return 1;
          } else {
            return 0;
          }
        } else {
          let numberChild = dependencyValues.numberChildrenByCode[logicTree];
          if (numberChild) {
            let numericalValue = numberChild.stateValues.value;
            if (Number.isFinite(numericalValue) && numericalValue !== 0) {
              return 1;
            } else {
              return 0;
            }
          } else if (logicTree.toLowerCase() === "true") {
            return 1;
          } else if (logicTree.toLowerCase() === "false") {
            return 0;
          }
        }
      }
    } else if (typeof logicTree === "number") {
      return logicTree === 0 ? 0 : 1;
    }
    return valueOnInvalid;
  }
  let operator = logicTree[0];
  let operands = logicTree.slice(1);
  if (operator === "not") {
    if (operands.length !== 1) {
      return valueOnInvalid;
    }
    return evaluateSub(operands[0]) === 0 ? 1 : 0;
  }
  if (operator === "and") {
    if (dependencyValues.matchPartial) {
      return operands.reduce((a2, c2) => a2 + evaluateSub(c2), 0) / operands.length;
    } else {
      return operands.every((x2) => evaluateSub(x2) === 1) ? 1 : 0;
    }
  }
  if (operator === "or") {
    if (dependencyValues.matchPartial) {
      return operands.reduce((a2, c2) => Math.max(evaluateSub(c2), a2), 0);
    } else {
      return operands.some((x2) => evaluateSub(x2) === 1) ? 1 : 0;
    }
  }
  let foundMath = false;
  let foundText = false;
  let foundBoolean = false;
  let foundOther = false;
  function findTypes(ops) {
    for (let op of ops) {
      if (typeof op === "string") {
        if (op in dependencyValues.mathChildrenByCode || op in dependencyValues.numberChildrenByCode) {
          foundMath = true;
        } else if (op in dependencyValues.textChildrenByCode) {
          foundText = true;
        } else if (op in dependencyValues.booleanChildrenByCode) {
          foundBoolean = true;
        } else if (op in dependencyValues.otherChildrenByCode) {
          foundOther = true;
        }
      } else if (Array.isArray(op)) {
        findTypes(op.slice(1));
      }
    }
  }
  findTypes(operands);
  let replaceMath = function(tree) {
    if (typeof tree === "string") {
      let child = dependencyValues.mathChildrenByCode[tree];
      if (child !== void 0) {
        return child.stateValues.value.tree;
      }
      child = dependencyValues.numberChildrenByCode[tree];
      if (child !== void 0) {
        return numberToMathExpression(child.stateValues.value).tree;
      }
      return tree;
    }
    if (!Array.isArray(tree)) {
      return tree;
    }
    return [tree[0], ...tree.slice(1).map(replaceMath)];
  };
  if (operator === "apply" && ["isnumber", "isinteger"].includes(operands[0])) {
    if (foundText || foundBoolean || foundOther) {
      return 0;
    }
    let expression = me$3.fromAst(replaceMath(operands[1]));
    let numericalValue = expression.simplify().evaluate_to_constant();
    if (!Number.isFinite(numericalValue)) {
      return 0;
    }
    if (operands[0] === "isnumber") {
      return 1;
    } else {
      let rounded = Math.round(numericalValue);
      if (Math.abs(rounded - numericalValue) <= 1e-15 * Math.abs(numericalValue)) {
        return 1;
      } else {
        return 0;
      }
    }
  }
  if (![
    "=",
    "ne",
    "<",
    ">",
    "le",
    "ge",
    "lts",
    "gts",
    "in",
    "notin",
    "subset",
    "notsubset",
    "superset",
    "notsuperset"
  ].includes(operator)) {
    if (foundText || foundBoolean || foundOther) {
      return valueOnInvalid;
    }
    let expression = me$3.fromAst(replaceMath(logicTree));
    let numericalValue = expression.simplify().evaluate_to_constant();
    if (Number.isFinite(numericalValue) && numericalValue !== 0) {
      return 1;
    } else {
      return 0;
    }
  }
  if (foundBoolean) {
    if (foundMath || foundText || foundOther) {
      return valueOnInvalid;
    }
    let foundUnordered2 = false;
    let replaceBooleanAndFindUnordered = function(tree) {
      if (typeof tree === "string") {
        let child = dependencyValues.booleanChildrenByCode[tree];
        if (child !== void 0) {
          if (child.stateValues.inUnorderedList) {
            foundUnordered2 = true;
          }
          return child.stateValues.value;
        }
        tree = tree.toLowerCase().trim();
        if (tree === "true") {
          return true;
        }
        if (tree === "false") {
          return false;
        }
        throw Error("Invalid format");
      }
      if (!Array.isArray(tree)) {
        throw Error("Invalid format");
      }
      if (tree[0] === "list") {
        return tree.slice(1).map(replaceBooleanAndFindUnordered);
      } else {
        return evaluateSub(tree) === 1 ? true : false;
      }
    };
    try {
      operands = operands.map(replaceBooleanAndFindUnordered);
    } catch (e3) {
      return valueOnInvalid;
    }
    let unorderedCompare2 = dependencyValues.unorderedCompare;
    if (canOverrideUnorderedCompare) {
      if (foundUnordered2) {
        unorderedCompare2 = true;
      }
    }
    if (operator === "=") {
      let boolean1 = operands[0];
      if (dependencyValues.matchPartial) {
        let results = operands.slice(1).map(
          (x2) => checkEquality({
            object1: boolean1,
            object2: x2,
            isUnordered: unorderedCompare2,
            partialMatches: dependencyValues.matchPartial,
            matchByExactPositions: dependencyValues.matchByExactPositions
          })
        );
        let sum2 = results.reduce((a2, c2) => a2 + c2.fraction_equal, 0);
        return sum2 / results.length;
      } else {
        return operands.slice(1).every(
          (x2) => checkEquality({
            object1: boolean1,
            object2: x2,
            isUnordered: unorderedCompare2,
            partialMatches: dependencyValues.matchPartial,
            matchByExactPositions: dependencyValues.matchByExactPositions
          }).fraction_equal === 1
        ) ? 1 : 0;
      }
    } else if (operator === "ne") {
      if (operands.length !== 2) {
        return valueOnInvalid;
      }
      let fraction_equal = checkEquality({
        object1: operands[0],
        object2: operands[1],
        isUnordered: unorderedCompare2,
        partialMatches: dependencyValues.matchPartial,
        matchByExactPositions: dependencyValues.matchByExactPositions
      }).fraction_equal;
      return fraction_equal === 0 ? 1 : 0;
    } else if (operator === "in" || operator === "notin") {
      let boolean1 = operands[0];
      if (!(operands.length === 2 && typeof boolean1 === "boolean" && Array.isArray(operands[1]) && operands[1].every((b2) => typeof b2 === "boolean"))) {
        return valueOnInvalid;
      }
      let isInList = operands[1].includes(boolean1);
      if (operator === "in") {
        return isInList ? 1 : 0;
      } else {
        return isInList ? 0 : 1;
      }
    } else if (["subset", "notsubset", "superset", "notsuperset"].includes(
      operator
    )) {
      let booleanList1 = operands[0];
      let booleanList2 = operands[1];
      if (typeof booleanList1 === "boolean") {
        booleanList1 = [booleanList1];
      }
      if (typeof booleanList2 === "boolean") {
        booleanList2 = [booleanList2];
      }
      if (!(operands.length === 2 && Array.isArray(booleanList1) && booleanList1.every((b2) => typeof b2 === "boolean") && Array.isArray(booleanList2) && booleanList2.every((b2) => typeof b2 === "boolean"))) {
        return valueOnInvalid;
      }
      if (operator === "superset" || operator === "notsuperset") {
        [booleanList1, booleanList2] = [booleanList2, booleanList1];
      }
      let haveContainment = booleanList1.every(
        (b2) => booleanList2.includes(b2)
      );
      if (operator.substring(0, 3) === "not") {
        return haveContainment ? 0 : 1;
      } else {
        return haveContainment ? 1 : 0;
      }
    } else {
      return valueOnInvalid;
    }
  } else if (foundText) {
    if (foundMath || foundOther) {
      return valueOnInvalid;
    }
    let foundUnordered2 = false;
    let replaceTextAndFindUnordered = function(tree, recurse = true) {
      if (typeof tree === "string") {
        let child = dependencyValues.textChildrenByCode[tree];
        if (child !== void 0) {
          if (child.stateValues.inUnorderedList) {
            foundUnordered2 = true;
          }
          return child.stateValues.value.trim().replace(/\s+/, " ");
        }
        return tree.trim();
      }
      if (typeof tree === "number") {
        return tree.toString();
      }
      if (recurse && Array.isArray(tree)) {
        if (tree[0] === "*") {
          return tree.slice(1).map((x2) => replaceTextAndFindUnordered(x2, false)).join(" ");
        } else if (tree[0] === "list") {
          return tree.slice(1).map((x2) => replaceTextAndFindUnordered(x2, false)).map((x2) => x2.trim().replace(/\s+/, " "));
        }
      }
      throw Error("Invalid format");
    };
    try {
      operands = operands.map((x2) => replaceTextAndFindUnordered(x2));
    } catch (e3) {
      return valueOnInvalid;
    }
    let unorderedCompare2 = dependencyValues.unorderedCompare;
    if (canOverrideUnorderedCompare) {
      if (foundUnordered2) {
        unorderedCompare2 = true;
      }
    }
    if (operator === "=") {
      let text1 = operands[0];
      if (dependencyValues.matchPartial) {
        let results = operands.slice(1).map(
          (x2) => checkEquality({
            object1: text1,
            object2: x2,
            isUnordered: unorderedCompare2,
            partialMatches: dependencyValues.matchPartial,
            matchByExactPositions: dependencyValues.matchByExactPositions,
            caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch
          })
        );
        let sum2 = results.reduce((a2, c2) => a2 + c2.fraction_equal, 0);
        return sum2 / results.length;
      } else {
        return operands.slice(1).every(
          (x2) => checkEquality({
            object1: text1,
            object2: x2,
            isUnordered: unorderedCompare2,
            partialMatches: dependencyValues.matchPartial,
            matchByExactPositions: dependencyValues.matchByExactPositions,
            caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch
          }).fraction_equal === 1
        ) ? 1 : 0;
      }
    } else if (operator === "ne") {
      if (operands.length !== 2) {
        return 0;
      }
      let fraction_equal = checkEquality({
        object1: operands[0],
        object2: operands[1],
        isUnordered: unorderedCompare2,
        partialMatches: dependencyValues.matchPartial,
        matchByExactPositions: dependencyValues.matchByExactPositions,
        caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch
      }).fraction_equal;
      return fraction_equal === 0 ? 1 : 0;
    } else if (operator === "in" || operator === "notin") {
      let text1 = operands[0];
      if (operands.length !== 2 || typeof text1 !== "string") {
        return valueOnInvalid;
      }
      if (dependencyValues.caseInsensitiveMatch) {
        text1 = text1.toLowerCase();
      }
      if (typeof operands[1] === "string") {
        let text2 = operands[1];
        if (dependencyValues.caseInsensitiveMatch) {
          text2 = text2.toLowerCase();
        }
        let isSubstring = text2.includes(text1);
        if (operator === "in") {
          return isSubstring ? 1 : 0;
        } else {
          return isSubstring ? 0 : 1;
        }
      } else if (Array.isArray(operands[1]) && operands[1].every((s2) => typeof s2 === "string")) {
        let textlist = operands[1];
        if (dependencyValues.caseInsensitiveMatch) {
          textlist = textlist.map((s2) => s2.toLowerCase());
        }
        let isInList = textlist.includes(text1);
        if (operator === "in") {
          return isInList ? 1 : 0;
        } else {
          return isInList ? 0 : 1;
        }
      } else {
        return valueOnInvalid;
      }
    } else if (["subset", "notsubset", "superset", "notsuperset"].includes(
      operator
    )) {
      let textList1 = operands[0];
      let textList2 = operands[1];
      if (typeof textList1 === "string") {
        textList1 = [textList1];
      }
      if (typeof textList2 === "string") {
        textList2 = [textList2];
      }
      if (!(operands.length === 2 && Array.isArray(textList1) && textList1.every((b2) => typeof b2 === "string") && Array.isArray(textList2) && textList2.every((b2) => typeof b2 === "string"))) {
        return valueOnInvalid;
      }
      if (dependencyValues.caseInsensitiveMatch) {
        textList1 = textList1.map((s2) => s2.toLowerCase());
        textList2 = textList2.map((s2) => s2.toLowerCase());
      }
      if (operator === "superset" || operator === "notsuperset") {
        [textList1, textList2] = [textList2, textList1];
      }
      let haveContainment = textList1.every((b2) => textList2.includes(b2));
      if (operator.substring(0, 3) === "not") {
        return haveContainment ? 0 : 1;
      } else {
        return haveContainment ? 1 : 0;
      }
    } else {
      return valueOnInvalid;
    }
  } else if (foundOther) {
    if (foundMath) {
      return valueOnInvalid;
    }
    let foundInvalidFormat = false;
    operands = operands.map(function(x2) {
      if (typeof x2 === "string") {
        let child = dependencyValues.otherChildrenByCode[x2];
        if (child !== void 0) {
          return child.stateValues.value;
        }
      }
      foundInvalidFormat = true;
      return null;
    });
    if (foundInvalidFormat) {
      return valueOnInvalid;
    }
    if (operator === "=") {
      if (operands.slice(1).every((x2) => deepCompare(x2, operands[0]))) {
        return 1;
      } else {
        return 0;
      }
    }
    if (operator === "ne") {
      if (operands.length !== 2) {
        return 0;
      }
      if (deepCompare(operands[0], operands[1])) {
        return 0;
      } else {
        return 1;
      }
    }
    return 0;
  }
  let strict;
  if (operator === "lts" || operator === "gts") {
    strict = operands[1].slice(1);
    operands = operands[0].slice(1);
  }
  let foundUnordered = false;
  let replaceMathAndFindUnordered = function(tree) {
    if (typeof tree === "string") {
      let child = dependencyValues.mathChildrenByCode[tree];
      if (child !== void 0) {
        if (child.stateValues.unordered || child.stateValues.inUnorderedList) {
          foundUnordered = true;
        }
        return child.stateValues.value.tree;
      }
      child = dependencyValues.numberChildrenByCode[tree];
      if (child !== void 0) {
        if (child.stateValues.inUnorderedList) {
          foundUnordered = true;
        }
        return numberToMathExpression(child.stateValues.value).tree;
      }
      return tree;
    }
    if (!Array.isArray(tree)) {
      return tree;
    }
    return [tree[0], ...tree.slice(1).map(replaceMathAndFindUnordered)];
  };
  let mathOperands = operands.map(function(x2) {
    return me$3.fromAst(replaceMathAndFindUnordered(x2));
  });
  let unorderedCompare = dependencyValues.unorderedCompare;
  if (canOverrideUnorderedCompare) {
    if (foundUnordered) {
      unorderedCompare = true;
    }
  }
  if (operator === "=") {
    let expr = mathOperands[0];
    if (Number.isNaN(expr.tree)) {
      return mathOperands.slice(1).every((x2) => Number.isNaN(x2.tree)) ? 1 : 0;
    }
    if (dependencyValues.matchPartial) {
      let results = mathOperands.slice(1).map(
        (x2) => checkEquality({
          object1: expr,
          object2: x2,
          isUnordered: unorderedCompare,
          partialMatches: dependencyValues.matchPartial,
          matchByExactPositions: dependencyValues.matchByExactPositions,
          symbolicEquality: dependencyValues.symbolicEquality,
          simplify: dependencyValues.simplifyOnCompare,
          expand: dependencyValues.expandOnCompare,
          allowedErrorInNumbers: dependencyValues.allowedErrorInNumbers,
          includeErrorInNumberExponents: dependencyValues.includeErrorInNumberExponents,
          allowedErrorIsAbsolute: dependencyValues.allowedErrorIsAbsolute,
          numSignErrorsMatched: dependencyValues.numSignErrorsMatched,
          numPeriodicSetMatchesRequired: dependencyValues.numPeriodicSetMatchesRequired,
          caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch,
          matchBlanks: dependencyValues.matchBlanks
        })
      );
      let sum2 = results.reduce((a2, c2) => a2 + c2.fraction_equal, 0);
      return sum2 / results.length;
    } else {
      return mathOperands.slice(1).every(
        (x2) => checkEquality({
          object1: expr,
          object2: x2,
          isUnordered: unorderedCompare,
          partialMatches: dependencyValues.matchPartial,
          matchByExactPositions: dependencyValues.matchByExactPositions,
          symbolicEquality: dependencyValues.symbolicEquality,
          simplify: dependencyValues.simplifyOnCompare,
          expand: dependencyValues.expandOnCompare,
          allowedErrorInNumbers: dependencyValues.allowedErrorInNumbers,
          includeErrorInNumberExponents: dependencyValues.includeErrorInNumberExponents,
          allowedErrorIsAbsolute: dependencyValues.allowedErrorIsAbsolute,
          numSignErrorsMatched: dependencyValues.numSignErrorsMatched,
          numPeriodicSetMatchesRequired: dependencyValues.numPeriodicSetMatchesRequired,
          caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch,
          matchBlanks: dependencyValues.matchBlanks
        }).fraction_equal === 1
      ) ? 1 : 0;
    }
  }
  if (operator === "ne") {
    let fraction_equal = checkEquality({
      object1: mathOperands[0],
      object2: mathOperands[1],
      isUnordered: unorderedCompare,
      partialMatches: dependencyValues.matchPartial,
      matchByExactPositions: dependencyValues.matchByExactPositions,
      symbolicEquality: dependencyValues.symbolicEquality,
      simplify: dependencyValues.simplifyOnCompare,
      expand: dependencyValues.expandOnCompare,
      allowedErrorInNumbers: dependencyValues.allowedErrorInNumbers,
      includeErrorInNumberExponents: dependencyValues.includeErrorInNumberExponents,
      allowedErrorIsAbsolute: dependencyValues.allowedErrorIsAbsolute,
      numSignErrorsMatched: dependencyValues.numSignErrorsMatched,
      numPeriodicSetMatchesRequired: dependencyValues.numPeriodicSetMatchesRequired,
      caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch,
      matchBlanks: dependencyValues.matchBlanks
    }).fraction_equal;
    return fraction_equal === 0 ? 1 : 0;
  }
  if (operator === "in" || operator === "notin") {
    if (mathOperands.length !== 2) {
      return valueOnInvalid;
    }
    let element = mathOperands[0];
    let set2 = mathOperands[1];
    let number1 = element.evaluate_to_constant();
    if (Number.isFinite(number1)) {
      let subsetOfReals2 = buildSubsetFromMathExpression(set2);
      if (subsetOfReals2.isValid()) {
        let containsNumber = subsetOfReals2.containsElement(number1);
        if (operator === "in") {
          return containsNumber ? 1 : 0;
        } else {
          return containsNumber ? 0 : 1;
        }
      }
    }
    let set_tree = set2.tree;
    if (!(Array.isArray(set_tree) && ["set", "list"].includes(set_tree[0]))) {
      set_tree = ["list", set_tree];
    }
    if (dependencyValues.matchPartial) {
      let results = set_tree.slice(1).map(
        (x2) => checkEquality({
          object1: element,
          object2: me$3.fromAst(x2),
          isUnordered: unorderedCompare,
          partialMatches: dependencyValues.matchPartial,
          matchByExactPositions: dependencyValues.matchByExactPositions,
          symbolicEquality: dependencyValues.symbolicEquality,
          simplify: dependencyValues.simplifyOnCompare,
          expand: dependencyValues.expandOnCompare,
          allowedErrorInNumbers: dependencyValues.allowedErrorInNumbers,
          includeErrorInNumberExponents: dependencyValues.includeErrorInNumberExponents,
          allowedErrorIsAbsolute: dependencyValues.allowedErrorIsAbsolute,
          numSignErrorsMatched: dependencyValues.numSignErrorsMatched,
          numPeriodicSetMatchesRequired: dependencyValues.numPeriodicSetMatchesRequired,
          caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch,
          matchBlanks: dependencyValues.matchBlanks
        })
      );
      let max_fraction = results.reduce(
        (a2, c2) => Math.max(a2, c2.fraction_equal),
        0
      );
      if (operator === "in") {
        return max_fraction;
      } else {
        return 1 - max_fraction;
      }
    } else {
      let result = set_tree.slice(1).some(
        (x2) => checkEquality({
          object1: element,
          object2: me$3.fromAst(x2),
          isUnordered: unorderedCompare,
          partialMatches: dependencyValues.matchPartial,
          matchByExactPositions: dependencyValues.matchByExactPositions,
          symbolicEquality: dependencyValues.symbolicEquality,
          simplify: dependencyValues.simplifyOnCompare,
          expand: dependencyValues.expandOnCompare,
          allowedErrorInNumbers: dependencyValues.allowedErrorInNumbers,
          includeErrorInNumberExponents: dependencyValues.includeErrorInNumberExponents,
          allowedErrorIsAbsolute: dependencyValues.allowedErrorIsAbsolute,
          numSignErrorsMatched: dependencyValues.numSignErrorsMatched,
          numPeriodicSetMatchesRequired: dependencyValues.numPeriodicSetMatchesRequired,
          caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch,
          matchBlanks: dependencyValues.matchBlanks
        }).fraction_equal === 1
      );
      if (operator === "in") {
        return result ? 1 : 0;
      } else {
        return result ? 0 : 1;
      }
    }
  }
  if (["subset", "notsubset", "superset", "notsuperset"].includes(operator)) {
    if (mathOperands.length !== 2) {
      return valueOnInvalid;
    }
    let set1 = mathOperands[0];
    let set2 = mathOperands[1];
    if (operator === "superset" || operator === "notsuperset") {
      [set1, set2] = [set2, set1];
    }
    let subsetOfReals1 = buildSubsetFromMathExpression(set1);
    if (subsetOfReals1.isValid()) {
      let subsetOfReals2 = buildSubsetFromMathExpression(set2);
      if (subsetOfReals2.isValid()) {
        let haveContainment2 = subsetOfReals2.containsSubset(subsetOfReals1);
        if (operator.substring(0, 3) === "not") {
          return haveContainment2 ? 0 : 1;
        } else {
          return haveContainment2 ? 1 : 0;
        }
      }
    }
    let set1_tree = set1.tree;
    let set2_tree = set2.tree;
    if (!(Array.isArray(set1_tree) && ["set", "list"].includes(set1_tree[0]))) {
      set1_tree = ["list", set1_tree];
    }
    if (!(Array.isArray(set2_tree) && ["set", "list"].includes(set2_tree[0]))) {
      set2_tree = ["list", set2_tree];
    }
    let haveContainment = set1_tree.slice(1).every(
      (elt1) => set2_tree.slice(1).some(
        (elt2) => checkEquality({
          object1: me$3.fromAst(elt1),
          object2: me$3.fromAst(elt2),
          isUnordered: unorderedCompare,
          partialMatches: dependencyValues.matchPartial,
          matchByExactPositions: dependencyValues.matchByExactPositions,
          symbolicEquality: dependencyValues.symbolicEquality,
          simplify: dependencyValues.simplifyOnCompare,
          expand: dependencyValues.expandOnCompare,
          allowedErrorInNumbers: dependencyValues.allowedErrorInNumbers,
          includeErrorInNumberExponents: dependencyValues.includeErrorInNumberExponents,
          allowedErrorIsAbsolute: dependencyValues.allowedErrorIsAbsolute,
          numSignErrorsMatched: dependencyValues.numSignErrorsMatched,
          numPeriodicSetMatchesRequired: dependencyValues.numPeriodicSetMatchesRequired,
          caseInsensitiveMatch: dependencyValues.caseInsensitiveMatch,
          matchBlanks: dependencyValues.matchBlanks
        }).fraction_equal === 1
      )
    );
    if (operator.substring(0, 3) === "not") {
      return haveContainment ? 0 : 1;
    } else {
      return haveContainment ? 1 : 0;
    }
  }
  let numberOperands = mathOperands.map(
    (x2) => x2.simplify().evaluate_to_constant()
  );
  if (numberOperands.some((x2) => Number.isNaN(x2))) {
    return 0;
  }
  if (operator === "<") {
    return numberOperands[0] < numberOperands[1] ? 1 : 0;
  } else if (operator === ">") {
    return numberOperands[0] > numberOperands[1] ? 1 : 0;
  } else if (operator === "le") {
    return numberOperands[0] <= numberOperands[1] ? 1 : 0;
  } else if (operator === "ge") {
    return numberOperands[0] >= numberOperands[1] ? 1 : 0;
  }
  for (let ind = 0; ind < strict.length; ind++) {
    if (operator === "lts") {
      if (strict[ind] === true) {
        if (!(numberOperands[ind] < numberOperands[ind + 1])) {
          return 0;
        }
      } else {
        if (!(numberOperands[ind] <= numberOperands[ind + 1])) {
          return 0;
        }
      }
    } else {
      if (strict[ind] === true) {
        if (!(numberOperands[ind] > numberOperands[ind + 1])) {
          return 0;
        }
      } else {
        if (!(numberOperands[ind] >= numberOperands[ind + 1])) {
          return 0;
        }
      }
    }
  }
  return 1;
}
function splitSymbolsIfMath({
  logicTree,
  nonMathCodes,
  foundNonMath = false,
  init = true
}) {
  if (!Array.isArray(logicTree)) {
    if (typeof logicTree === "string" && !foundNonMath && !init) {
      return fromTextSplit(logicTree).tree;
    } else {
      return logicTree;
    }
  }
  let operator = logicTree[0];
  let operands = logicTree.slice(1);
  if (["and", "not", "or"].includes(operator)) {
    return [
      operator,
      ...operands.map(
        (x2) => splitSymbolsIfMath({
          logicTree: x2,
          nonMathCodes,
          foundNonMath,
          init
        })
      )
    ];
  }
  for (let op of operands) {
    if (nonMathCodes.includes(op)) {
      foundNonMath = true;
      break;
    }
    if (Array.isArray(op) && op[0] === "list" && op.slice(1).some((x2) => nonMathCodes.includes(x2))) {
      foundNonMath = true;
      break;
    }
  }
  if (operator === "apply") {
    operands = [
      operands[0],
      ...operands.slice(1).map(
        (x2) => splitSymbolsIfMath({
          logicTree: x2,
          nonMathCodes,
          foundNonMath,
          init: false
        })
      )
    ];
    let foundInt = false;
    if (operands[0] === "int") {
      foundInt = true;
    } else if (Array.isArray(operands[0])) {
      if (operands[0][0] === "^") {
        if (operands[0][1] === "int") {
          foundInt = true;
        } else if (Array.isArray(operands[0][1])) {
          if (operands[0][1][0] === "_" && operands[0][1][1] === "int") {
            foundInt = true;
          }
        }
      } else if (operands[0][0] === "_") {
        if (operands[0][1] === "int") {
          foundInt = true;
        }
      }
    }
    if (foundInt) {
      operands = me$3.fromAst([operator, ...operands]).tree.slice(1);
      let integrand = operands[1];
      if (Array.isArray(integrand) && integrand[0] === "*") {
        let ds = [];
        for (let i2 = 0; i2 < integrand.length - 1; i2++) {
          let factor1 = integrand[i2];
          if (factor1 === "d") {
            let factor2 = integrand[i2 + 1];
            integrand.splice(i2, 2);
            ds.push(["d", factor2]);
            i2--;
          }
        }
        integrand.push(...ds);
      }
    }
    return [operator, ...operands];
  } else if (operator === "angle") {
    let newOperands = [];
    for (let op of operands) {
      if (typeof op === "string" && !foundNonMath) {
        let splitResult = fromTextSplit(op).tree;
        if (splitResult[0] === "*") {
          newOperands.push(...splitResult.slice(1));
        } else {
          newOperands.push(splitResult);
        }
      } else {
        newOperands.push(
          splitSymbolsIfMath({
            logicTree: op,
            nonMathCodes,
            foundNonMath,
            init: false
          })
        );
      }
    }
    return [operator, ...newOperands];
  } else {
    return [
      operator,
      ...operands.map(
        (x2) => splitSymbolsIfMath({
          logicTree: x2,
          nonMathCodes,
          foundNonMath,
          init: false
        })
      )
    ];
  }
}
class BooleanComponent extends InlineComponent {
  static componentType = "boolean";
  static componentDocs = {
    summary: "A boolean value"
  };
  static variableForImplicitProp = "value";
  static implicitPropReturnsSameType = true;
  static descendantCompositesMustHaveAReplacement = true;
  static descendantCompositesDefaultReplacementType = "math";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.symbolicEquality = {
      createComponentOfType: "boolean",
      createStateVariable: "symbolicEquality",
      defaultValue: false,
      public: true,
      description: "Whether children compared via this boolean use symbolic equality."
    };
    attributes.expandOnCompare = {
      createComponentOfType: "boolean",
      createStateVariable: "expandOnCompare",
      defaultValue: false,
      public: true,
      description: "Whether to expand math expressions before comparing."
    };
    attributes.simplifyOnCompare = {
      createComponentOfType: "text",
      createStateVariable: "simplifyOnCompare",
      defaultValue: "none",
      toLowerCase: true,
      valueForTrue: "full",
      valueForFalse: "none",
      validValues: [
        {
          value: "none",
          description: "No simplification is applied before comparing."
        },
        {
          value: "full",
          description: "Fully simplify both expressions before comparing."
        },
        {
          value: "numbers",
          description: "Simplify numeric subexpressions only, leaving symbolic structure intact."
        },
        {
          value: "numbersPreserveOrder",
          description: "Like `numbers`, but does not reorder commutative operands."
        },
        {
          value: "normalizeOrder",
          description: "Reorder commutative operands into a canonical form without simplifying values."
        }
      ],
      public: true,
      description: "Level of simplification applied to math expressions before comparing."
    };
    attributes.unorderedCompare = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedCompare",
      defaultValue: false,
      public: true,
      description: "Whether order is ignored when comparing list-like values."
    };
    attributes.matchByExactPositions = {
      createComponentOfType: "boolean",
      createStateVariable: "matchByExactPositions",
      defaultValue: false,
      public: true,
      description: "Whether to match list values by exact position rather than by content."
    };
    attributes.allowedErrorInNumbers = {
      createComponentOfType: "number",
      createStateVariable: "allowedErrorInNumbers",
      defaultValue: 0,
      public: true,
      description: "Maximum allowed numeric error when comparing numbers."
    };
    attributes.includeErrorInNumberExponents = {
      createComponentOfType: "boolean",
      createStateVariable: "includeErrorInNumberExponents",
      defaultValue: false,
      public: true,
      description: "Whether the allowed numeric error also applies to numbers in exponents."
    };
    attributes.allowedErrorIsAbsolute = {
      createComponentOfType: "boolean",
      createStateVariable: "allowedErrorIsAbsolute",
      defaultValue: false,
      public: true,
      description: "Whether allowedErrorInNumbers is interpreted as an absolute (rather than relative) tolerance."
    };
    attributes.numSignErrorsMatched = {
      createComponentOfType: "number",
      createStateVariable: "numSignErrorsMatched",
      defaultValue: 0,
      public: true,
      description: "Maximum number of sign errors that still count as a match."
    };
    attributes.numPeriodicSetMatchesRequired = {
      createComponentOfType: "integer",
      createStateVariable: "numPeriodicSetMatchesRequired",
      defaultValue: 3,
      public: true,
      description: "Number of consecutive elements of a periodic set required to count as a match."
    };
    attributes.caseInsensitiveMatch = {
      createComponentOfType: "boolean",
      createStateVariable: "caseInsensitiveMatch",
      defaultValue: false,
      public: true,
      description: "Whether text comparisons ignore letter case."
    };
    attributes.matchBlanks = {
      createComponentOfType: "boolean",
      createStateVariable: "matchBlanks",
      defaultValue: false,
      public: true,
      description: "Whether unfilled blanks in a math expression count as a match."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "strings",
        componentTypes: ["string"]
      },
      {
        group: "comparableTypes",
        componentTypes: [
          "math",
          "number",
          "text",
          "boolean",
          "orbitalDiagram"
        ]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnSimplifyExpandOnCompareWarning()
    );
    stateVariableDefinitions.inUnorderedList = {
      defaultValue: false,
      returnDependencies: () => ({
        sourceCompositeUnordered: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "unordered",
          skipCopies: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.sourceCompositeUnordered !== null && !usedDefault.sourceCompositeUnordered) {
          return {
            setValue: {
              inUnorderedList: Boolean(
                dependencyValues.sourceCompositeUnordered
              )
            }
          };
        } else {
          return {
            setValue: {
              inUnorderedList: false
            }
          };
        }
      }
    };
    stateVariableDefinitions.parsedExpression = {
      additionalStateVariablesDefined: ["codePre"],
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          childGroups: ["strings", "comparableTypes"]
        },
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"],
          variableNames: ["value"]
        }
      }),
      definition: buildParsedExpression
    };
    Object.assign(
      stateVariableDefinitions,
      returnChildrenByCodeStateVariableDefinitions()
    );
    stateVariableDefinitions.value = {
      description: "The boolean value (true or false).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: this.componentType,
        attributesToShadow: ["fixed"]
      },
      forRenderer: true,
      hasEssential: true,
      defaultValue: false,
      set: Boolean,
      returnDependencies: () => ({
        symbolicEquality: {
          dependencyType: "stateVariable",
          variableName: "symbolicEquality"
        },
        expandOnCompare: {
          dependencyType: "stateVariable",
          variableName: "expandOnCompare"
        },
        simplifyOnCompare: {
          dependencyType: "stateVariable",
          variableName: "simplifyOnCompare"
        },
        unorderedCompare: {
          dependencyType: "stateVariable",
          variableName: "unorderedCompare"
        },
        matchByExactPositions: {
          dependencyType: "stateVariable",
          variableName: "matchByExactPositions"
        },
        allowedErrorInNumbers: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorInNumbers"
        },
        includeErrorInNumberExponents: {
          dependencyType: "stateVariable",
          variableName: "includeErrorInNumberExponents"
        },
        allowedErrorIsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorIsAbsolute"
        },
        numSignErrorsMatched: {
          dependencyType: "stateVariable",
          variableName: "numSignErrorsMatched"
        },
        numPeriodicSetMatchesRequired: {
          dependencyType: "stateVariable",
          variableName: "numPeriodicSetMatchesRequired"
        },
        caseInsensitiveMatch: {
          dependencyType: "stateVariable",
          variableName: "caseInsensitiveMatch"
        },
        matchBlanks: {
          dependencyType: "stateVariable",
          variableName: "matchBlanks"
        },
        parsedExpression: {
          dependencyType: "stateVariable",
          variableName: "parsedExpression"
        },
        allChildren: {
          dependencyType: "child",
          childGroups: ["strings", "comparableTypes"],
          variableNames: ["value"],
          variablesOptional: true
        },
        booleanChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "booleanChildrenByCode"
        },
        textChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "textChildrenByCode"
        },
        mathChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "mathChildrenByCode"
        },
        numberChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "numberChildrenByCode"
        },
        otherChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "otherChildrenByCode"
        },
        // include solely to trigger warning
        simplifyExpandOnCompareWarning: {
          dependencyType: "stateVariable",
          variableName: "simplifyExpandOnCompareWarning"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.allChildren.length === 0) {
          return {
            useEssentialOrDefaultValue: {
              value: true
            }
          };
        } else if (dependencyValues.parsedExpression === null) {
          return {
            setValue: { value: false }
          };
        }
        let canOverrideUnorderedCompare = usedDefault.unorderedCompare;
        let fractionSatisfied = evaluateLogic({
          logicTree: dependencyValues.parsedExpression.tree,
          canOverrideUnorderedCompare,
          dependencyValues
        });
        return {
          setValue: { value: fractionSatisfied === 1 }
        };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues,
        componentInfoObjects
      }) {
        if (dependencyValues.allChildren.length === 0) {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "value",
                value: Boolean(
                  desiredStateVariableValues.value
                )
              }
            ]
          };
        } else if (dependencyValues.allChildren.length === 1) {
          let child = dependencyValues.allChildren[0];
          if (typeof child === "string") {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "allChildren",
                  desiredValue: desiredStateVariableValues.value.toString(),
                  childIndex: 0,
                  variableIndex: 0
                }
              ]
            };
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "boolean"
          })) {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "allChildren",
                  desiredValue: desiredStateVariableValues.value,
                  childIndex: 0,
                  variableIndex: 0
                }
              ]
            };
          }
        }
        return { success: false };
      }
    };
    stateVariableDefinitions.text = {
      description: 'The boolean rendered as a text string ("true" or "false").',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            text: dependencyValues.value ? "true" : "false"
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let desiredText = String(
          desiredStateVariableValues.text
        ).toLowerCase();
        let desiredBoolean;
        if (desiredText === "true") {
          desiredBoolean = true;
        } else if (desiredText === "false") {
          desiredBoolean = false;
        }
        if (desiredBoolean !== void 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "value",
                desiredValue: desiredBoolean
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = ["text"];
}
class BooleanOperator extends BooleanComponent {
  static componentType = "_booleanOperator";
  static rendererType = "boolean";
  static descendantCompositesMustHaveAReplacement = false;
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakStringsIntoBooleansBySpaces = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let newChildren = matchedChildren.reduce(function(a2, c2) {
        if (typeof c2 === "string") {
          return [
            ...a2,
            ...c2.split(/\s+/).filter((s2) => s2).map((s2) => ({
              type: "serialized",
              componentType: "boolean",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              attributes: {},
              doenetAttributes: {},
              state: {},
              children: [s2]
            }))
          ];
        } else {
          return [...a2, c2];
        }
      }, []);
      return {
        success: true,
        newChildren,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakStringsIntoBooleansBySpaces
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "booleans",
        componentTypes: ["boolean"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.parsedExpression;
    delete stateVariableDefinitions.mathChildrenByCode;
    let constructor = this;
    stateVariableDefinitions.value = {
      description: "The boolean result of applying this operator to its child boolean values.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        booleanChildren: {
          dependencyType: "child",
          childGroups: ["booleans"],
          variableNames: ["value"]
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            value: constructor.applyBooleanOperator(
              dependencyValues.booleanChildren.map(
                (x2) => x2.stateValues.value
              )
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class BooleanOperatorOneInput extends BooleanComponent {
  static componentType = "_booleanOperatorOneInput";
  static rendererType = "boolean";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let constructor = this;
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "value",
      newName: "valuePreOperator"
    });
    stateVariableDefinitions.value = {
      description: "The boolean result of applying this operator to its single boolean input.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "valuePreOperator"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            value: constructor.applyBooleanOperator(
              dependencyValues.value
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Not extends BooleanOperatorOneInput {
  static componentType = "not";
  static componentDocs = {
    summary: "Logical negation of a boolean value"
  };
  static applyBooleanOperator(value) {
    return !value;
  }
}
class And extends BooleanOperator {
  static componentType = "and";
  static componentDocs = {
    summary: "Logical AND: true only when all child boolean values are true"
  };
  static applyBooleanOperator(values2) {
    return values2.every((x2) => x2);
  }
}
let Or$1 = class Or extends BooleanOperator {
  static componentType = "or";
  static componentDocs = {
    summary: "Logical OR: true when at least one child boolean value is true"
  };
  static applyBooleanOperator(values2) {
    return values2.some((x2) => x2);
  }
};
class Xor extends BooleanOperator {
  static componentType = "xor";
  static componentDocs = {
    summary: "Logical exclusive OR: true when exactly one child boolean value is true"
  };
  static applyBooleanOperator(values2) {
    let numberTrues = values2.reduce((acc, curr) => acc + (curr ? 1 : 0), 0);
    return numberTrues === 1;
  }
}
class Iff extends BooleanOperator {
  static componentType = "iff";
  static componentDocs = {
    summary: "IF and only iF: Logical bicondition (IFF): true when all child boolean values are the same"
  };
  static applyBooleanOperator(values2) {
    return values2.every((x2) => x2 === values2[0]);
  }
}
class Implies extends BooleanOperator {
  static componentType = "implies";
  static componentDocs = {
    summary: "Logical implication (binary): true when the first value is false or the second value is true"
  };
  static applyBooleanOperator(values2) {
    let tooManyValues = values2.length > 2;
    if (tooManyValues) {
      console.warn(
        "Implies operator should have at most two boolean children"
      );
    }
    switch (values2.length) {
      case 0:
        return true;
      case 1:
        return !values2[0];
      case 2:
        return !values2[0] || values2[1];
      default:
        return false;
    }
  }
}
const BooleanOperators = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  And,
  Iff,
  Implies,
  Not,
  Or: Or$1,
  Xor
}, Symbol.toStringTag, { value: "Module" }));
class BooleanBaseOperatorOfMath extends BooleanComponent {
  static componentType = "_booleanOperatorOfMath";
  static rendererType = "boolean";
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = [];
    let wrapStringsAndMacros = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      if (!matchedChildren.every(
        (child) => typeof child === "string" || child.extending && "Ref" in child.extending
      )) {
        return { success: false };
      }
      if (matchedChildren.length === 1 && typeof matchedChildren[0] !== "string") {
        return { success: false };
      }
      return {
        success: true,
        newChildren: [
          {
            type: "serialized",
            componentType: "math",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            attributes: {},
            doenetAttributes: {},
            state: {},
            children: matchedChildren
          }
        ],
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: wrapStringsAndMacros
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.parsedExpression;
    delete stateVariableDefinitions.mathChildrenByCode;
    stateVariableDefinitions.booleanOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { booleanOperator: (x2) => false } })
    };
    stateVariableDefinitions.value = {
      description: "The boolean result of applying this operator to its child math values.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        },
        booleanOperator: {
          dependencyType: "stateVariable",
          variableName: "booleanOperator"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            value: dependencyValues.booleanOperator(
              dependencyValues.mathChildren.map(
                (x2) => x2.stateValues.value
              )
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class IsInteger extends BooleanBaseOperatorOfMath {
  static componentType = "isInteger";
  static componentDocs = {
    summary: "True when the math child evaluates to an integer"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.booleanOperator.definition = () => ({
      setValue: {
        booleanOperator: function(values2) {
          if (values2.length === 0) {
            return false;
          }
          if (values2.length !== 1) {
            console.warn(
              "IsInteger requires exactly one math child"
            );
            return null;
          }
          let numericValue = values2[0].evaluate_to_constant();
          if (!Number.isFinite(numericValue)) {
            return false;
          }
          let rounded = Math.round(numericValue);
          if (Math.abs(rounded - numericValue) <= 1e-15 * Math.abs(numericValue)) {
            return true;
          } else {
            return false;
          }
        }
      }
    });
    return stateVariableDefinitions;
  }
}
class IsNumber extends BooleanBaseOperatorOfMath {
  static componentType = "isNumber";
  static componentDocs = {
    summary: "True when the math child evaluates to a finite number"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.booleanOperator.definition = () => ({
      setValue: {
        booleanOperator: function(values2) {
          if (values2.length === 0) {
            return false;
          }
          if (values2.length !== 1) {
            console.warn(
              "IsNumber requires exactly one math child"
            );
            return null;
          }
          let numericValue = values2[0].evaluate_to_constant();
          return Number.isFinite(numericValue);
        }
      }
    });
    return stateVariableDefinitions;
  }
}
class IsBetween extends BooleanBaseOperatorOfMath {
  static componentType = "isBetween";
  static componentDocs = {
    summary: "True when a math value is between two given bounds"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.limits = {
      createComponentOfType: "numberList",
      createStateVariable: "limits",
      defaultValue: [],
      public: true,
      description: "Numeric limits used by the boolean operator (e.g. bounds for isBetween)."
    };
    attributes.strict = {
      createComponentOfType: "boolean",
      createStateVariable: "strict",
      defaultValue: false,
      public: true,
      description: "Whether the comparison is strict (e.g. uses < instead of ≤)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.booleanOperator = {
      returnDependencies: () => ({
        limits: {
          dependencyType: "stateVariable",
          variableName: "limits"
        },
        strict: {
          dependencyType: "stateVariable",
          variableName: "strict"
        }
      }),
      definition({ dependencyValues }) {
        let upperLimit, lowerLimit;
        let lim1 = dependencyValues.limits[0];
        let lim2 = dependencyValues.limits[1];
        let strict = dependencyValues.strict;
        if (lim1 < lim2 || !strict && lim1 === lim2) {
          lowerLimit = lim1;
          upperLimit = lim2;
        } else if (lim2 < lim1) {
          lowerLimit = lim2;
          upperLimit = lim1;
        } else {
          return { setValue: { booleanOperator: () => false } };
        }
        return {
          setValue: {
            booleanOperator: function(values2) {
              if (values2.length === 0) {
                return false;
              }
              if (values2.length !== 1) {
                console.warn(
                  "IsBetween requires exactly one math child"
                );
                return null;
              }
              let numericValue = values2[0].evaluate_to_constant();
              if (strict) {
                return numericValue > lowerLimit && numericValue < upperLimit;
              } else {
                return numericValue >= lowerLimit && numericValue <= upperLimit;
              }
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
const BooleanOperatorsOfMath = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  IsBetween,
  IsInteger,
  IsNumber
}, Symbol.toStringTag, { value: "Module" }));
class MathOperator extends MathComponent {
  static componentType = "_mathOperator";
  static rendererType = "math";
  // since math operator treats each child a separate argument
  // composites with no replacement should be ignored.
  // turn off this flag set in math
  static descendantCompositesMustHaveAReplacement = false;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.forceSymbolic = {
      createComponentOfType: "boolean",
      createStateVariable: "forceSymbolic",
      defaultValue: false,
      public: true,
      description: "Whether to force the operator to evaluate symbolically rather than numerically."
    };
    attributes.forceNumeric = {
      createComponentOfType: "boolean",
      createStateVariable: "forceNumeric",
      defaultValue: false,
      public: true,
      description: "Whether to force the operator to evaluate numerically rather than symbolically."
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakStringsIntoMathsBySpaces = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let newChildren = matchedChildren.reduce(function(a2, c2) {
        if (typeof c2 === "string") {
          return [
            ...a2,
            ...c2.split(/\s+/).filter((s2) => s2).map((s2) => ({
              type: "serialized",
              componentType: Number.isFinite(Number(s2)) ? "number" : "math",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: [s2],
              attributes: {},
              doenetAttributes: {},
              state: {}
            }))
          ];
        } else {
          return [...a2, c2];
        }
      }, []);
      return {
        success: true,
        newChildren,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakStringsIntoMathsBySpaces
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      },
      {
        group: "numbers",
        componentTypes: ["number"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({
      childGroupsIfSingleMatch: ["maths", "numbers"]
    });
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.isNumericOperator = {
      returnDependencies: () => ({
        forceNumeric: {
          dependencyType: "stateVariable",
          variableName: "forceNumeric"
        },
        forceSymbolic: {
          dependencyType: "stateVariable",
          variableName: "forceSymbolic"
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["isNumber"],
          variablesOptional: true
        },
        shadowSource: {
          dependencyType: "shadowSource",
          variableNames: ["isNumericOperator"]
        }
      }),
      definition({ dependencyValues }) {
        let isNumericOperator;
        if (dependencyValues.forceNumeric) {
          isNumericOperator = true;
        } else if (dependencyValues.forceSymbolic) {
          isNumericOperator = false;
        } else if (dependencyValues.mathChildren.length === 0) {
          isNumericOperator = dependencyValues.shadowSource?.stateValues.isNumericOperator;
          if (isNumericOperator === void 0) {
            isNumericOperator = true;
          }
        } else {
          isNumericOperator = dependencyValues.mathChildren.every(
            (x2) => x2.stateValues.isNumber
          );
        }
        return { setValue: { isNumericOperator } };
      }
    };
    delete stateVariableDefinitions.codePre;
    delete stateVariableDefinitions.expressionWithCodes;
    delete stateVariableDefinitions.mathChildrenFunctionSymbols;
    delete stateVariableDefinitions.codesAdjacentToStrings;
    delete stateVariableDefinitions.mathChildrenByVectorComponent;
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { mathOperator: (x2) => me$3.fromAst("＿") }
      })
    };
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { numericOperator: (x2) => me$3.fromAst("＿") }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { inverseMathOperator: null } })
    };
    stateVariableDefinitions.inverseNumericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { inverseNumericOperator: null } })
    };
    stateVariableDefinitions.unnormalizedValue = {
      returnDependencies: () => ({
        mathNumberChildren: {
          dependencyType: "child",
          childGroups: ["maths", "numbers"],
          variableNames: ["value", "canBeModified"]
        },
        isNumericOperator: {
          dependencyType: "stateVariable",
          variableName: "isNumericOperator"
        },
        mathOperator: {
          dependencyType: "stateVariable",
          variableName: "mathOperator"
        },
        numericOperator: {
          dependencyType: "stateVariable",
          variableName: "numericOperator"
        },
        inverseMathOperator: {
          dependencyType: "stateVariable",
          variableName: "inverseMathOperator"
        },
        inverseNumericOperator: {
          dependencyType: "stateVariable",
          variableName: "inverseNumericOperator"
        }
      }),
      definition: function({ dependencyValues, componentInfoObjects }) {
        if (dependencyValues.mathNumberChildren.length === 0) {
          return {
            setValue: { unnormalizedValue: me$3.fromAst("＿") }
          };
        } else if (dependencyValues.isNumericOperator) {
          let inputs = [];
          for (let child of dependencyValues.mathNumberChildren) {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "number"
            })) {
              inputs.push(child.stateValues.value);
            } else {
              let value = child.stateValues.value.evaluate_to_constant();
              inputs.push(value);
            }
          }
          return {
            setValue: {
              unnormalizedValue: me$3.fromAst(
                dependencyValues.numericOperator(inputs)
              )
            }
          };
        } else {
          let inputs = [];
          for (let child of dependencyValues.mathNumberChildren) {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "number"
            })) {
              inputs.push(me$3.fromAst(child.stateValues.value));
            } else {
              inputs.push(child.stateValues.value);
            }
          }
          return {
            setValue: {
              unnormalizedValue: dependencyValues.mathOperator(inputs)
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues,
        componentInfoObjects
      }) {
        if (dependencyValues.mathNumberChildren.length === 0) {
          return { success: false };
        } else if (dependencyValues.isNumericOperator) {
          if (dependencyValues.inverseNumericOperator) {
            let inputs = [];
            let canBeModified = [];
            let inputToChildIndex = [];
            for (let [
              childInd,
              child
            ] of dependencyValues.mathNumberChildren.entries()) {
              if (componentInfoObjects.isInheritedComponentType({
                inheritedComponentType: child.componentType,
                baseComponentType: "number"
              })) {
                inputs.push(child.stateValues.value);
                canBeModified.push(
                  child.stateValues.canBeModified
                );
                inputToChildIndex.push(childInd);
              } else {
                let value = child.stateValues.value.evaluate_to_constant();
                inputs.push(value);
                canBeModified.push(
                  child.stateValues.canBeModified
                );
                inputToChildIndex.push(childInd);
              }
            }
            let results = dependencyValues.inverseNumericOperator({
              desiredValue: desiredStateVariableValues.unnormalizedValue.evaluate_to_constant(),
              inputs,
              canBeModified,
              desiredMathValue: desiredStateVariableValues.unnormalizedValue
            });
            if (results.success) {
              let childIndex = inputToChildIndex[results.inputNumber];
              if (Number.isFinite(childIndex)) {
                let desiredValue = results.inputValue;
                return {
                  success: true,
                  instructions: [
                    {
                      setDependency: "mathNumberChildren",
                      desiredValue,
                      childIndex,
                      variableIndex: 0
                    }
                  ]
                };
              } else {
                return { success: false };
              }
            } else {
              return { success: false };
            }
          } else {
            return { success: false };
          }
        } else if (dependencyValues.inverseMathOperator) {
          let inputs = [];
          let canBeModified = [];
          let inputToChildIndex = [];
          for (let [
            childInd,
            child
          ] of dependencyValues.mathNumberChildren.entries()) {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "number"
            })) {
              inputs.push(me$3.fromAst(child.stateValues.value));
              canBeModified.push(child.stateValues.canBeModified);
              inputToChildIndex.push(childInd);
            } else {
              inputs.push(child.stateValues.value);
              canBeModified.push(child.stateValues.canBeModified);
              inputToChildIndex.push(childInd);
            }
          }
          let results = dependencyValues.inverseMathOperator({
            desiredValue: desiredStateVariableValues.unnormalizedValue,
            inputs,
            canBeModified
          });
          if (results.success) {
            let childIndex = inputToChildIndex[results.inputNumber];
            if (Number.isFinite(childIndex)) {
              let desiredValue = results.inputValue;
              return {
                success: true,
                instructions: [
                  {
                    setDependency: "mathNumberChildren",
                    desiredValue,
                    childIndex,
                    variableIndex: 0
                  }
                ]
              };
            } else {
              return { success: false };
            }
          } else {
            return { success: false };
          }
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.canBeModified = {
      returnDependencies: () => ({
        modifyIndirectly: {
          dependencyType: "stateVariable",
          variableName: "modifyIndirectly"
        },
        fixed: {
          dependencyType: "stateVariable",
          variableName: "fixed"
        },
        mathNumberChildren: {
          dependencyType: "child",
          childGroups: ["maths", "numbers"],
          variableNames: ["canBeModified"]
        },
        isNumericOperator: {
          dependencyType: "stateVariable",
          variableName: "isNumericOperator"
        },
        inverseMathOperator: {
          dependencyType: "stateVariable",
          variableName: "mathOperator"
        },
        inverseNumericOperator: {
          dependencyType: "stateVariable",
          variableName: "inverseNumericOperator"
        }
      }),
      definition: function({ dependencyValues }) {
        let canBeModified = dependencyValues.modifyIndirectly && !dependencyValues.fixed && Boolean(
          dependencyValues.isNumericOperator ? dependencyValues.inverseNumericOperator : dependencyValues.inverseMathOperator
        );
        if (canBeModified) {
          if (dependencyValues.mathNumberChildren.length > 0) {
            let nModifiable = dependencyValues.mathNumberChildren.filter(
              (x2) => x2.stateValues.canBeModified
            ).length;
            if (nModifiable !== 1) {
              canBeModified = false;
            }
          }
        }
        return { setValue: { canBeModified } };
      }
    };
    return stateVariableDefinitions;
  }
}
class MathOperatorOneInput extends MathComponent {
  static componentType = "_mathOperatorOneInput";
  static rendererType = "math";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { mathOperator: (x2) => me.fromAst("＿") }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { inverseMathOperator: null } })
    };
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "unnormalizedValue",
      newName: "unnormalizedValuePreOperator"
    });
    stateVariableDefinitions.unnormalizedValue = {
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "unnormalizedValuePreOperator"
        },
        mathOperator: {
          dependencyType: "stateVariable",
          variableName: "mathOperator"
        },
        inverseMathOperator: {
          dependencyType: "stateVariable",
          variableName: "inverseMathOperator"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            unnormalizedValue: dependencyValues.mathOperator(
              dependencyValues.value
            )
          }
        };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues,
        componentIdx
      }) {
        if (dependencyValues.inverseMathOperator) {
          let newValue = dependencyValues.inverseMathOperator(
            desiredStateVariableValues.unnormalizedValue
          );
          return {
            success: true,
            instructions: [
              {
                setDependency: "value",
                desiredValue: newValue
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "canBeModified",
      newName: "canBeModifiedPreOperator"
    });
    stateVariableDefinitions.canBeModified = {
      returnDependencies: () => ({
        canBeModifiedPreOperator: {
          dependencyType: "stateVariable",
          variableName: "canBeModifiedPreOperator"
        },
        inverseMathOperator: {
          dependencyType: "stateVariable",
          variableName: "mathOperator"
        }
      }),
      definition: function({ dependencyValues }) {
        let canBeModified = dependencyValues.canBeModifiedPreOperator;
        if (!dependencyValues.inverseMathOperator) {
          canBeModified = false;
        }
        return { setValue: { canBeModified } };
      }
    };
    return stateVariableDefinitions;
  }
}
const { mod: mod$3, median: median$1 } = me$3.math;
class Sum extends MathOperator {
  static componentType = "sum";
  static componentDocs = {
    summary: "Sum of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            return inputs.reduce((a2, c2) => a2 + c2);
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(inputs) {
            return inputs.reduce((a2, c2) => a2.add(c2));
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Product extends MathOperator {
  static componentType = "product";
  static componentDocs = {
    summary: "Product of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            return inputs.reduce((a2, c2) => a2 * c2);
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(values2) {
            return values2.reduce((a2, c2) => a2.multiply(c2));
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class ClampNumber extends MathOperatorOneInput {
  static componentType = "clampNumber";
  static componentDocs = {
    summary: "Clamps a number to a specified range"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.lowerValue = {
      description: "Lower bound used by the operator.",
      createComponentOfType: "number",
      createStateVariable: "lowerValue",
      defaultValue: 0,
      public: true
    };
    attributes.upperValue = {
      description: "Upper bound used by the operator.",
      createComponentOfType: "number",
      createStateVariable: "upperValue",
      defaultValue: 1,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.isNumericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isNumericOperator: true } })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({
        lowerValue: {
          dependencyType: "stateVariable",
          variableName: "lowerValue"
        },
        upperValue: {
          dependencyType: "stateVariable",
          variableName: "upperValue"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          mathOperator: function(value) {
            return clamp({
              value,
              lowerValue: dependencyValues.lowerValue,
              upperValue: dependencyValues.upperValue
            });
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({
        lowerValue: {
          dependencyType: "stateVariable",
          variableName: "lowerValue"
        },
        upperValue: {
          dependencyType: "stateVariable",
          variableName: "upperValue"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          inverseMathOperator: function(value) {
            return clamp({
              value,
              lowerValue: dependencyValues.lowerValue,
              upperValue: dependencyValues.upperValue
            });
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
function clamp({ value, lowerValue, upperValue }) {
  let numericValue = value;
  if (numericValue instanceof me$3.class) {
    numericValue = numericValue.evaluate_to_constant();
  }
  return me$3.fromAst(Math.max(lowerValue, Math.min(upperValue, numericValue)));
}
class WrapNumberPeriodic extends MathOperatorOneInput {
  static componentType = "wrapNumberPeriodic";
  static componentDocs = {
    summary: "Wraps a number into a periodic range"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.lowerValue = {
      createComponentOfType: "number",
      createStateVariable: "lowerValue",
      defaultValue: 0,
      public: true,
      description: "Lower bound used by the operator."
    };
    attributes.upperValue = {
      createComponentOfType: "number",
      createStateVariable: "upperValue",
      defaultValue: 1,
      public: true,
      description: "Upper bound used by the operator."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.isNumericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isNumericOperator: true } })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({
        lowerValue: {
          dependencyType: "stateVariable",
          variableName: "lowerValue"
        },
        upperValue: {
          dependencyType: "stateVariable",
          variableName: "upperValue"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          mathOperator: function(value) {
            return makePeriodic({
              value,
              lowerValue: dependencyValues.lowerValue,
              upperValue: dependencyValues.upperValue
            });
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({
        lowerValue: {
          dependencyType: "stateVariable",
          variableName: "lowerValue"
        },
        upperValue: {
          dependencyType: "stateVariable",
          variableName: "upperValue"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          inverseMathOperator: function(value) {
            return makePeriodic({
              value,
              lowerValue: dependencyValues.lowerValue,
              upperValue: dependencyValues.upperValue
            });
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
function makePeriodic({ value, lowerValue, upperValue }) {
  let numericValue = value;
  if (numericValue instanceof me$3.class) {
    numericValue = numericValue.evaluate_to_constant();
  }
  if (!Number.isFinite(numericValue)) {
    return me$3.fromAst(NaN);
  }
  if (lowerValue === upperValue) {
    return me$3.fromAst(lowerValue);
  }
  if (lowerValue > upperValue) {
    [upperValue, lowerValue] = [lowerValue, upperValue];
  }
  return me$3.fromAst(
    lowerValue + mod$3(numericValue - lowerValue, upperValue - lowerValue)
  );
}
class Round extends MathOperatorOneInput {
  static componentType = "round";
  static componentDocs = {
    summary: "Rounds a number to a specified precision"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numDecimals = {
      description: "Number of decimal places (for round-style operators).",
      createComponentOfType: "number",
      createStateVariable: "numDecimals",
      defaultValue: 0,
      public: true
    };
    attributes.numDigits = {
      description: "Number of significant digits (for round-style operators).",
      createComponentOfType: "number",
      createStateVariable: "numDigits",
      defaultValue: null,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({
      displayDigitsDefault: 14
    });
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({
        numDecimals: {
          dependencyType: "stateVariable",
          variableName: "numDecimals"
        },
        numDigits: {
          dependencyType: "stateVariable",
          variableName: "numDigits"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          mathOperator: function(value) {
            let valueWithNumbers = value.evaluate_numbers({
              max_digits: Infinity,
              evaluate_functions: true
            });
            if (dependencyValues.numDigits !== null) {
              return valueWithNumbers.round_numbers_to_precision(
                dependencyValues.numDigits
              );
            } else {
              return valueWithNumbers.round_numbers_to_decimals(
                dependencyValues.numDecimals
              );
            }
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseMathOperator: (value) => value
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class setSmallToZero extends MathOperatorOneInput {
  static componentType = "setSmallToZero";
  static componentDocs = {
    summary: "Replaces values with magnitude below a threshold with zero"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.threshold = {
      description: "Threshold value used by the operator.",
      createComponentOfType: "number",
      createStateVariable: "threshold",
      defaultValue: 1e-14,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({
        threshold: {
          dependencyType: "stateVariable",
          variableName: "threshold"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          mathOperator: function(value) {
            return value.evaluate_numbers({
              skip_ordering: true,
              set_small_zero: dependencyValues.threshold
            });
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseMathOperator: (value) => value
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class ConvertSetToList extends MathOperatorOneInput {
  static componentType = "convertSetToList";
  static componentDocs = {
    summary: "Converts a math set expression into a list expression"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.unordered;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.unordered = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { unordered: true }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(value) {
            if (value !== void 0 && Array.isArray(value.tree) && value.tree[0] === "set") {
              let distinctElements = [];
              for (let v2 of value.tree.slice(1)) {
                if (!distinctElements.some(
                  (x2) => value.context.equalsViaSyntax(
                    value.context.fromAst(x2),
                    value.context.fromAst(v2)
                  )
                )) {
                  distinctElements.push(v2);
                }
              }
              return value.context.fromAst([
                "list",
                ...distinctElements
              ]);
            }
            return value;
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Ceil extends MathOperatorOneInput {
  static componentType = "ceil";
  static componentDocs = {
    summary: "Smallest integer ≥ the input value"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(value) {
            let numericValue = value.evaluate_to_constant();
            if (!Number.isFinite(numericValue)) {
              return me$3.fromAst(["apply", "ceil", value.tree]);
            }
            let rounded = Math.round(numericValue);
            if (Math.abs((rounded - numericValue) / numericValue) < 1e-15) {
              return me$3.fromAst(rounded);
            }
            return me$3.fromAst(Math.ceil(numericValue));
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseMathOperator: (value) => value
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Floor extends MathOperatorOneInput {
  static componentType = "floor";
  static componentDocs = {
    summary: "Largest integer ≤ the input value"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(value) {
            let numericValue = value.evaluate_to_constant();
            if (!Number.isFinite(numericValue)) {
              return me$3.fromAst(["apply", "floor", value.tree]);
            }
            let rounded = Math.round(numericValue);
            if (Math.abs((rounded - numericValue) / numericValue) < 1e-15) {
              return me$3.fromAst(rounded);
            }
            return me$3.fromAst(Math.floor(numericValue));
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseMathOperator: (value) => value
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Abs extends MathOperatorOneInput {
  static componentType = "abs";
  static componentDocs = {
    summary: "Absolute value of an expression"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(value) {
            let numericValue = value.evaluate_to_constant();
            if (!Number.isFinite(numericValue)) {
              return me$3.fromAst(["apply", "abs", value.tree]);
            }
            return me$3.fromAst(Math.abs(numericValue));
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseMathOperator: function(value) {
            let desiredValue = value;
            let valueNumeric = value.evaluate_to_constant();
            if (Number.isFinite(valueNumeric)) {
              if (valueNumeric < 0) {
                desiredValue = me$3.fromAst(0);
              }
            } else if (Array.isArray(value.tree) && value.tree[0] === "apply" && value.tree[1] === "abs") {
              desiredValue = me$3.fromAst(value.tree[2]);
            }
            return desiredValue;
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Sign extends MathOperatorOneInput {
  static componentType = "sign";
  static componentDocs = {
    summary: "Sign of the input (-1, 0, or 1)"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(value) {
            let numericValue = value.evaluate_to_constant();
            if (!Number.isFinite(numericValue)) {
              return me$3.fromAst(["apply", "sign", value.tree]);
            }
            return me$3.fromAst(Math.sign(numericValue));
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Mean extends MathOperator {
  static componentType = "mean";
  static componentDocs = {
    summary: "Arithmetic mean of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            let mean2 = inputs.reduce((a2, c2) => a2 + c2);
            mean2 /= inputs.length;
            return mean2;
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(inputs) {
            return inputs.reduce((a2, c2) => a2.add(c2)).divide(inputs.length);
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Median extends MathOperator {
  static componentType = "median";
  static componentDocs = {
    summary: "Median of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.isNumericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isNumericOperator: true } })
    };
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            return median$1(inputs);
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Variance extends MathOperator {
  static componentType = "variance";
  static componentDocs = {
    summary: "Variance of the child math or number values"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.population = {
      description: "Whether to use the population formula (rather than sample).",
      createComponentOfType: "boolean",
      createStateVariable: "population",
      defaultValue: false,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({
        population: {
          dependencyType: "stateVariable",
          variableName: "population"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          numericOperator: function(inputs) {
            return calculateNumericVariance(
              inputs,
              dependencyValues.population
            );
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({
        population: {
          dependencyType: "stateVariable",
          variableName: "population"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          mathOperator: function(inputs) {
            return calculateSymbolicVariance(
              inputs,
              dependencyValues.population
            );
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
function calculateNumericVariance(inputs, population) {
  let sum2 = 0, variance2 = 0;
  for (let num of inputs) {
    sum2 += num;
    variance2 += num * num;
  }
  let n2 = inputs.length;
  variance2 -= sum2 ** 2 / n2;
  if (population) {
    variance2 /= n2;
  } else {
    variance2 /= n2 - 1;
  }
  return variance2;
}
function calculateSymbolicVariance(inputs, population) {
  let n2 = inputs.length;
  let sum2 = inputs.reduce((a2, c2) => a2.add(c2));
  let variance2 = inputs.slice(1).reduce((a2, c2) => a2.add(c2.pow(2)), inputs[0].pow(2)).subtract(sum2.pow(2).divide(n2));
  if (population) {
    variance2 = variance2.divide(n2);
  } else {
    variance2 = variance2.divide(n2 - 1);
  }
  return variance2;
}
class StandardDeviation extends Variance {
  static componentType = "standardDeviation";
  static componentDocs = {
    summary: "Standard deviation of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator.definition = ({
      dependencyValues
    }) => ({
      setValue: {
        numericOperator: function(inputs) {
          return Math.sqrt(
            calculateNumericVariance(
              inputs,
              dependencyValues.population
            )
          );
        }
      }
    });
    stateVariableDefinitions.mathOperator.definition = ({
      dependencyValues
    }) => ({
      setValue: {
        mathOperator: function(inputs) {
          return me$3.fromAst([
            "apply",
            "sqrt",
            calculateSymbolicVariance(
              inputs,
              dependencyValues.population
            ).tree
          ]);
        }
      }
    });
    return stateVariableDefinitions;
  }
}
class Count extends MathOperator {
  static componentType = "count";
  static componentDocs = {
    summary: "The number of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.isNumericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isNumericOperator: true } })
    };
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            return inputs.length;
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Min extends MathOperator {
  static componentType = "min";
  static componentDocs = {
    summary: "Minimum of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            return inputs.reduce(
              (a2, c2) => Math.min(a2, c2),
              Infinity
            );
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(inputs) {
            return me$3.fromAst([
              "apply",
              "min",
              ["tuple", ...inputs.map((x2) => x2.tree)]
            ]);
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseMathOperator: function({
            desiredValue,
            canBeModified
          }) {
            if (canBeModified.filter((x2) => x2).length === 1) {
              let inputNumber = canBeModified.indexOf(true);
              if (inputNumber >= 0) {
                return {
                  success: true,
                  inputValue: desiredValue,
                  inputNumber
                };
              }
            }
            return { success: false };
          }
        }
      })
    };
    stateVariableDefinitions.inverseNumericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseNumericOperator: function({
            desiredValue,
            canBeModified,
            desiredMathValue
          }) {
            if (canBeModified.filter((x2) => x2).length === 1) {
              let inputNumber = canBeModified.indexOf(true);
              if (inputNumber >= 0) {
                if (!Number.isFinite(desiredValue)) {
                  desiredValue = desiredMathValue;
                }
                return {
                  success: true,
                  inputValue: desiredValue,
                  inputNumber
                };
              }
            }
            return { success: false };
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Max extends MathOperator {
  static componentType = "max";
  static componentDocs = {
    summary: "Maximum of the child math or number values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            return inputs.reduce(
              (a2, c2) => Math.max(a2, c2),
              -Infinity
            );
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(inputs) {
            return me$3.fromAst([
              "apply",
              "max",
              ["tuple", ...inputs.map((x2) => x2.tree)]
            ]);
          }
        }
      })
    };
    stateVariableDefinitions.inverseMathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseMathOperator: function({
            desiredValue,
            canBeModified
          }) {
            if (canBeModified.filter((x2) => x2).length === 1) {
              let inputNumber = canBeModified.indexOf(true);
              if (inputNumber >= 0) {
                return {
                  success: true,
                  inputValue: desiredValue,
                  inputNumber
                };
              }
            }
            return { success: false };
          }
        }
      })
    };
    stateVariableDefinitions.inverseNumericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          inverseNumericOperator: function({
            desiredValue,
            canBeModified,
            desiredMathValue
          }) {
            if (canBeModified.filter((x2) => x2).length === 1) {
              let inputNumber = canBeModified.indexOf(true);
              if (inputNumber >= 0) {
                if (!Number.isFinite(desiredValue)) {
                  desiredValue = desiredMathValue;
                }
                return {
                  success: true,
                  inputValue: desiredValue,
                  inputNumber
                };
              }
            }
            return { success: false };
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Mod extends MathOperator {
  static componentType = "mod";
  static componentDocs = {
    summary: "Modulo (remainder) of two values"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            if (inputs.length !== 2) {
              return NaN;
            }
            return mod$3(inputs[0], inputs[1]);
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(inputs) {
            if (inputs.length !== 2) {
              return me$3.fromAst("＿");
            }
            return me$3.fromAst([
              "apply",
              "mod",
              ["tuple", ...inputs.map((x2) => x2.tree)]
            ]);
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Gcd extends MathOperator {
  static componentType = "gcd";
  static componentDocs = {
    summary: "Greatest common divisor of integer inputs"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            if (inputs.every(Number.isInteger)) {
              return gcd$2(...inputs);
            }
            return NaN;
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(inputs) {
            return me$3.fromAst([
              "apply",
              "gcd",
              ["tuple", ...inputs.map((x2) => x2.tree)]
            ]);
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class Lcm extends MathOperator {
  static componentType = "lcm";
  static componentDocs = {
    summary: "Least common multiple of integer inputs"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numericOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericOperator: function(inputs) {
            if (inputs.every(Number.isInteger)) {
              return lcm(...inputs);
            }
            return NaN;
          }
        }
      })
    };
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          mathOperator: function(inputs) {
            return me$3.fromAst([
              "apply",
              "lcm",
              ["tuple", ...inputs.map((x2) => x2.tree)]
            ]);
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class ExtractMath extends MathOperatorOneInput {
  static componentType = "extractMath";
  static componentDocs = {
    summary: "Extracts a sub-expression from a math expression by name or index"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      createComponentOfType: "text",
      createStateVariable: "type",
      defaultValue: null,
      toLowerCase: true,
      validValues: [
        {
          value: "operand",
          description: "Extract a single operand of the top-level operation."
        },
        {
          value: "function",
          description: "Extract the function being applied (for a function call)."
        },
        {
          value: "functionArgument",
          description: "Extract an argument of the function being called."
        },
        {
          value: "numOperands",
          description: "Extract the count of operands at the top level."
        },
        {
          value: "recursiveOperands",
          description: "Extract all operands found recursively through nested operations."
        }
      ],
      description: "What kind of sub-expression to extract."
    };
    attributes.operandNumber = {
      description: "1-based index of the operand to extract.",
      createComponentOfType: "number",
      createStateVariable: "operandNumber",
      defaultValue: null,
      public: true
    };
    attributes.argumentNumber = {
      description: "1-based index of the argument to extract.",
      createComponentOfType: "number",
      createStateVariable: "argumentNumber",
      defaultValue: null,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.mathOperator = {
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        operandNumber: {
          dependencyType: "stateVariable",
          variableName: "operandNumber"
        },
        argumentNumber: {
          dependencyType: "stateVariable",
          variableName: "argumentNumber"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.type === "operand") {
          if (dependencyValues.operandNumber === null) {
            let warning = {
              message: `Must specify a operandNumber when extracting a math operand.`,
              type: "warning"
            };
            return {
              setValue: {
                mathOperator: () => me$3.fromAst("＿")
              },
              sendDiagnostics: [warning]
            };
          }
          return {
            setValue: {
              mathOperator: function(value) {
                let tree = value.tree;
                if (!Array.isArray(tree)) {
                  if (dependencyValues.operandNumber === 1) {
                    return value;
                  } else {
                    return me$3.fromAst("＿");
                  }
                }
                let operator = tree[0];
                if (operator === "apply") {
                  if (dependencyValues.operandNumber === 1) {
                    return value;
                  } else {
                    return me$3.fromAst("＿");
                  }
                }
                let operand = tree[dependencyValues.operandNumber];
                if (operand === void 0) {
                  return me$3.fromAst("＿");
                } else {
                  return me$3.fromAst(operand);
                }
              }
            }
          };
        } else if (dependencyValues.type === "function") {
          return {
            setValue: {
              mathOperator: function(value) {
                let tree = value.tree;
                if (!Array.isArray(tree)) {
                  return me$3.fromAst("＿");
                }
                let operator = tree[0];
                if (operator !== "apply") {
                  return me$3.fromAst("＿");
                }
                return me$3.fromAst(tree[1]);
              }
            }
          };
        } else if (dependencyValues.type === "functionargument") {
          return {
            setValue: {
              mathOperator: function(value) {
                let tree = value.tree;
                if (!Array.isArray(tree)) {
                  return me$3.fromAst("＿");
                }
                let operator = tree[0];
                if (operator !== "apply") {
                  return me$3.fromAst("＿");
                }
                let allArgs = tree[2];
                if (dependencyValues.argumentNumber === null) {
                  return me$3.fromAst(allArgs);
                } else if (dependencyValues.argumentNumber === 1) {
                  if (!(Array.isArray(allArgs) && allArgs[0] === "tuple")) {
                    return me$3.fromAst(allArgs);
                  } else {
                    return me$3.fromAst(allArgs[1]);
                  }
                } else {
                  if (!(Array.isArray(allArgs) && allArgs[0] === "tuple")) {
                    return me$3.fromAst("＿");
                  } else {
                    let theArg = allArgs[dependencyValues.argumentNumber];
                    if (theArg === void 0) {
                      return me$3.fromAst("＿");
                    } else {
                      return me$3.fromAst(theArg);
                    }
                  }
                }
              }
            }
          };
        } else if (dependencyValues.type === "numoperands") {
          return {
            setValue: {
              mathOperator: function(value) {
                let tree = value.tree;
                if (!Array.isArray(tree)) {
                  return me$3.fromAst(1);
                }
                let operator = tree[0];
                if (operator === "apply") {
                  return me$3.fromAst(1);
                }
                return me$3.fromAst(tree.length - 1);
              }
            }
          };
        } else if (dependencyValues.type === "recursiveoperands") {
          return {
            setValue: {
              mathOperator: function(value) {
                const getAllOperands = (tree) => {
                  if (!Array.isArray(tree)) {
                    return [tree];
                  } else {
                    let operator = tree[0];
                    if (operator === "apply") {
                      return [tree];
                    } else {
                      return tree.slice(1).flatMap(getAllOperands);
                    }
                  }
                };
                const operands = getAllOperands(value.tree);
                if (operands.length === 1) {
                  return me$3.fromAst(operands[0]);
                } else {
                  return me$3.fromAst(["list", ...operands]);
                }
              }
            }
          };
        } else {
          return {
            setValue: {
              mathOperator: () => me$3.fromAst("＿")
            }
          };
        }
      }
    };
    return stateVariableDefinitions;
  }
}
function gcd$2(x2, y2, ...z2) {
  if (!y2 && z2.length > 0) {
    return gcd$2(x2, ...z2);
  }
  if (!y2) {
    return Math.abs(x2);
  }
  return gcd$2(y2, x2 % y2, ...z2);
}
function lcm(...z2) {
  let prod = z2.reduce((a2, c2) => a2 * c2, 1);
  let gcdArgs = z2.map((v2) => prod / v2);
  let g2 = gcd$2(...gcdArgs);
  return Math.abs(prod / g2);
}
const MathOperators = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Abs,
  Ceil,
  ClampNumber,
  ConvertSetToList,
  Count,
  ExtractMath,
  Floor,
  Gcd,
  Lcm,
  Max,
  Mean,
  Median,
  Min,
  Mod,
  Product,
  Round,
  Sign,
  StandardDeviation,
  Sum,
  Variance,
  WrapNumberPeriodic,
  setSmallToZero
}, Symbol.toStringTag, { value: "Module" }));
function returnLabelAttributes() {
  return {
    labelIsName: {
      createComponentOfType: "boolean",
      createStateVariable: "labelIsName",
      defaultValue: false,
      public: true,
      groupName: "labels",
      description: "Whether to use this component's name as its rendered label."
    }
  };
}
function returnWrapNonLabelsDescriptionsSugarFunction({
  wrappingComponentType,
  createAttributeOfType,
  onlyStringOrMacros = false,
  customWrappingFunction,
  wrapSingleIfNotWrappingComponentType = false
}) {
  return function({
    matchedChildren,
    componentInfoObjects,
    nComponents,
    stateIdInfo
  }) {
    if (matchedChildren.length === 0) {
      return { success: false };
    }
    const componentIsLabelOrDescription = (x2) => typeof x2 !== "string" && (componentInfoObjects.componentIsSpecifiedType(x2, "label") || componentInfoObjects.componentIsSpecifiedType(
      x2,
      "description"
    ) || componentInfoObjects.componentIsSpecifiedType(
      x2,
      "shortDescription"
    ));
    if (onlyStringOrMacros && !matchedChildren.every(
      (child) => typeof child === "string" || child.extending && "Ref" in child.extending || componentIsLabelOrDescription(child)
    )) {
      return { success: false };
    }
    let childIsLabelDescription = matchedChildren.map(
      componentIsLabelOrDescription
    );
    let childrenToWrap = [];
    let childrenToNotWrapBegin = [];
    let childrenToNotWrapEnd = [];
    if (childIsLabelDescription.filter((x2) => x2).length === 0) {
      childrenToWrap = matchedChildren;
    } else {
      if (childIsLabelDescription[0]) {
        const firstNonLabelDescriptionInd = childIsLabelDescription.indexOf(false);
        if (firstNonLabelDescriptionInd !== -1) {
          childrenToNotWrapBegin = matchedChildren.slice(
            0,
            firstNonLabelDescriptionInd
          );
          matchedChildren = matchedChildren.slice(
            firstNonLabelDescriptionInd
          );
          childIsLabelDescription = childIsLabelDescription.slice(
            firstNonLabelDescriptionInd
          );
        }
      }
      const firstLabelDescriptionInd = childIsLabelDescription.indexOf(true);
      if (firstLabelDescriptionInd === -1) {
        childrenToWrap = matchedChildren;
      } else {
        childrenToWrap = matchedChildren.slice(
          0,
          firstLabelDescriptionInd
        );
        childrenToNotWrapEnd = matchedChildren.slice(
          firstLabelDescriptionInd
        );
      }
    }
    if (childrenToWrap.length === 0) {
      return { success: false };
    }
    if (createAttributeOfType) {
      return {
        success: true,
        newAttributes: {
          [createAttributeOfType]: {
            type: "component",
            name: createAttributeOfType,
            component: {
              type: "serialized",
              componentType: wrappingComponentType,
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: childrenToWrap,
              attributes: {},
              state: {},
              doenetAttributes: {}
            }
          }
        },
        newChildren: [
          ...childrenToNotWrapBegin,
          ...childrenToNotWrapEnd
        ],
        nComponents
      };
    } else {
      if (childrenToWrap.length === 1 && typeof childrenToWrap[0] !== "string" && (!wrapSingleIfNotWrappingComponentType || componentInfoObjects.componentIsSpecifiedType(
        childrenToWrap[0],
        wrappingComponentType
      )) || childrenToWrap.length === 0) {
        return { success: false };
      }
      let wrappedChildren;
      if (customWrappingFunction) {
        const wrapResult = customWrappingFunction(
          childrenToWrap,
          nComponents,
          stateIdInfo
        );
        wrappedChildren = wrapResult.newChildren;
        nComponents = wrapResult.nComponents;
      } else {
        wrappedChildren = [
          {
            type: "serialized",
            componentType: wrappingComponentType,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: childrenToWrap,
            attributes: {},
            state: {},
            doenetAttributes: {}
          }
        ];
      }
      return {
        success: true,
        newChildren: [
          ...childrenToNotWrapBegin,
          ...wrappedChildren,
          ...childrenToNotWrapEnd
        ],
        nComponents
      };
    }
  };
}
function returnLabelStateVariableDefinitions({
  getLabelFromParentIfSugared = false
} = {}) {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.componentNameAndShadowSourceNames = {
    returnDependencies: () => ({
      shadowSource: {
        dependencyType: "shadowSource",
        variableNames: ["componentNameAndShadowSourceNames"]
      },
      unlinkedCopySource: {
        dependencyType: "unlinkedCopySource",
        variableNames: ["componentNameAndShadowSourceNames"]
      },
      name: {
        dependencyType: "attributePrimitive",
        attributeName: "name"
      }
    }),
    definition({
      dependencyValues
    }) {
      const componentNameAndShadowSourceNames = [];
      if (dependencyValues.name != null) {
        componentNameAndShadowSourceNames.push(dependencyValues.name);
      }
      if (dependencyValues.shadowSource?.stateValues.componentNameAndShadowSourceNames) {
        componentNameAndShadowSourceNames.push(
          ...dependencyValues.shadowSource.stateValues.componentNameAndShadowSourceNames
        );
      } else if (dependencyValues.unlinkedCopySource?.stateValues.componentNameAndShadowSourceNames) {
        componentNameAndShadowSourceNames.push(
          ...dependencyValues.unlinkedCopySource.stateValues.componentNameAndShadowSourceNames
        );
      }
      return { setValue: { componentNameAndShadowSourceNames } };
    }
  };
  stateVariableDefinitions.label = {
    description: "The label rendered with this component.",
    forRenderer: true,
    public: true,
    groupName: "labels",
    shadowingInstructions: {
      createComponentOfType: "label",
      addStateVariablesShadowingStateVariables: {
        hasLatex: {
          stateVariableToShadow: "labelHasLatex"
        }
      }
    },
    hasEssential: true,
    doNotShadowEssential: true,
    defaultValue: "",
    provideEssentialValuesInDefinition: true,
    additionalStateVariablesDefined: [
      {
        variableName: "labelHasLatex",
        forRenderer: true
      }
    ],
    returnDependencies: () => {
      const dependencies = {
        labelChild: {
          dependencyType: "child",
          childGroups: ["labels"],
          variableNames: ["value", "hasLatex", "hidden"],
          dontRecurseToShadows: true
        },
        // Note: assuming component has a labelIsName attribute
        // that creates an attribute component and state variable
        labelIsName: {
          dependencyType: "stateVariable",
          variableName: "labelIsName"
        },
        labelIsNameAttr: {
          dependencyType: "attributeComponent",
          attributeName: "labelIsName",
          dontRecurseToShadows: true
        },
        componentNameAndShadowSourceNames: {
          dependencyType: "stateVariable",
          variableName: "componentNameAndShadowSourceNames"
        },
        shadowSource: {
          dependencyType: "shadowSource",
          variableNames: ["label", "labelHasLatex"]
        },
        unlinkedCopySource: {
          dependencyType: "unlinkedCopySource",
          variableNames: ["label", "labelHasLatex"]
        },
        getLabelFromParentIfSugared: {
          dependencyType: "value",
          value: getLabelFromParentIfSugared
        }
      };
      if (getLabelFromParentIfSugared) {
        dependencies.createdFromSugar = {
          dependencyType: "doenetAttribute",
          attributeName: "createdFromSugar"
        };
        dependencies.parentLabel = {
          dependencyType: "parentStateVariable",
          variableName: "label"
        };
        dependencies.parentLabelHasLatex = {
          dependencyType: "parentStateVariable",
          variableName: "labelHasLatex"
        };
      }
      return dependencies;
    },
    definition({
      dependencyValues,
      essentialValues
    }) {
      let labelChild = dependencyValues.labelChild[dependencyValues.labelChild.length - 1];
      if (labelChild) {
        if (labelChild.stateValues.hidden) {
          return {
            setValue: {
              label: "",
              labelHasLatex: false
            }
          };
        } else if (labelChild.stateValues.hasLatex) {
          if (labelChild.stateValues.value === "\\(\\)") {
            return {
              setValue: {
                label: "",
                labelHasLatex: false
              }
            };
          } else {
            return {
              setValue: {
                label: labelChild.stateValues.value,
                labelHasLatex: true
              }
            };
          }
        } else {
          return {
            setValue: {
              label: labelChild.stateValues.value.trim(),
              labelHasLatex: false
            }
          };
        }
      } else if (essentialValues.label !== void 0) {
        return {
          useEssentialOrDefaultValue: {
            label: true
          },
          setValue: { labelHasLatex: false }
        };
      } else if (dependencyValues.labelIsName && dependencyValues.labelIsNameAttr) {
        let label = "_";
        let cNames = dependencyValues.componentNameAndShadowSourceNames;
        for (let cN of cNames) {
          label = cN;
          if (label[0] !== "_") {
            break;
          }
        }
        if (label[0] === "_") {
          return {
            setValue: {
              label: "",
              labelHasLatex: false
            }
          };
        }
        if (label.includes("_") || label.includes("-")) {
          label = label.replace(/[_\-]/g, " ");
        } else if (label.match(/^[a-z]/)) {
          if (label.match(/[A-Z]/)) {
            label = label.replace(/([A-Z])/g, " $1").toLowerCase();
          }
        } else if (label.match(/^[A-Z]/)) {
          if (label.match(/[a-z]/)) {
            label = label.replace(/([A-Z])/g, " $1");
            label = label.slice(1);
          }
        }
        return {
          setValue: {
            label,
            labelHasLatex: false
          }
        };
      } else if (typeof dependencyValues.shadowSource?.stateValues.label === "string") {
        return {
          setValue: {
            label: dependencyValues.shadowSource.stateValues.label,
            labelHasLatex: Boolean(
              dependencyValues.shadowSource.stateValues.labelHasLatex
            )
          }
        };
      } else if (typeof dependencyValues.unlinkedCopySource?.stateValues.label === "string") {
        return {
          setValue: {
            label: dependencyValues.unlinkedCopySource.stateValues.label,
            labelHasLatex: Boolean(
              dependencyValues.unlinkedCopySource.stateValues.labelHasLatex
            )
          }
        };
      } else if (dependencyValues.getLabelFromParentIfSugared && dependencyValues.createdFromSugar && dependencyValues.parentLabel) {
        return {
          setValue: {
            label: dependencyValues.parentLabel,
            labelHasLatex: dependencyValues.parentLabelHasLatex
          }
        };
      } else {
        return {
          useEssentialOrDefaultValue: { label: true },
          setValue: { labelHasLatex: false }
        };
      }
    },
    inverseDefinition({
      desiredStateVariableValues,
      dependencyValues
    }) {
      if (typeof desiredStateVariableValues.label !== "string") {
        return { success: false };
      }
      let lastLabelInd = dependencyValues.labelChild.length - 1;
      let labelChild = dependencyValues.labelChild[lastLabelInd];
      if (labelChild) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "labelChild",
              desiredValue: desiredStateVariableValues.label,
              childIndex: lastLabelInd,
              variableIndex: 0
            }
          ]
        };
      } else if (dependencyValues.labelIsName && dependencyValues.labelIsNameAttr) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "label",
              value: desiredStateVariableValues.label
            }
          ]
        };
      } else if (typeof dependencyValues.shadowSource?.stateValues.label === "string") {
        return {
          success: true,
          instructions: [
            {
              setDependency: "shadowSource",
              desiredValue: desiredStateVariableValues.label,
              variableIndex: 0
            }
          ]
        };
      } else {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "label",
              value: desiredStateVariableValues.label
            }
          ]
        };
      }
    }
  };
  stateVariableDefinitions.labelForGraph = {
    forRenderer: true,
    returnDependencies: () => ({
      label: {
        dependencyType: "stateVariable",
        variableName: "label"
      },
      labelHasLatex: {
        dependencyType: "stateVariable",
        variableName: "labelHasLatex"
      }
    }),
    definition({
      dependencyValues
    }) {
      let labelForGraph;
      if (dependencyValues.labelHasLatex) {
        let nParens = 0;
        labelForGraph = "";
        for (let s2 of dependencyValues.label) {
          if (s2 === "(") {
            nParens++;
          } else if (s2 === ")") {
            nParens--;
          } else if (nParens === 0) {
            if (s2 === "_") {
              s2 = "&UnderBar;";
            } else if (s2 === "^") {
              s2 = "&Hat;";
            }
          }
          labelForGraph += s2;
        }
      } else {
        labelForGraph = dependencyValues.label.replaceAll("_", "&UnderBar;").replaceAll("^", "&Hat;");
      }
      return { setValue: { labelForGraph } };
    }
  };
  return stateVariableDefinitions;
}
function resolveOverrideGroups(cls) {
  const categories = cls.styleOverrideCategories ?? [];
  return categories.map((cat) => {
    const group = STYLE_OVERRIDE_CATEGORIES[cat];
    if (!group) {
      const valid = Object.keys(STYLE_OVERRIDE_CATEGORIES).join(", ");
      throw new Error(
        `Component "${cls.componentType}" lists unknown style override category "${cat}" in static styleOverrideCategories. Valid categories are: ${valid}.`
      );
    }
    return group;
  });
}
class GraphicalComponent extends BaseComponent {
  static componentType = "_graphical";
  /**
   * Subclasses opt into per-component style overrides by listing the
   * groups they use, e.g. `static styleOverrideCategories = ["marker"]`
   * for point-like components, `["line", "fill"]` for closed shapes.
   * Leaving this empty (the default) keeps the component as a pure
   * `<styleDefinition>` consumer with no per-component override surface.
   */
  static styleOverrideCategories = [];
  static returnStyleOverrideGroups() {
    return resolveOverrideGroups(this);
  }
  static addStyleOverrideAttributes(attributes) {
    for (const group of GraphicalComponent.returnStyleOverrideGroups.call(
      this
    )) {
      for (const [styleAttr, spec] of Object.entries(group)) {
        attributes[styleAttr] = attributeSpecFromStyleAttribute(spec);
      }
    }
  }
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    Object.assign(attributes, returnLabelAttributes());
    attributes.applyStyleToLabel = {
      createComponentOfType: "boolean",
      createStateVariable: "applyStyleToLabel",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether to apply this component's selected style to its label."
    };
    attributes.maskLabel = {
      createComponentOfType: "boolean",
      createStateVariable: "maskLabel",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether to give the label an opaque background so it stays legible when it overlaps an axis, grid line, or another object."
    };
    attributes.layer = {
      createComponentOfType: "integer",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true,
      attributesForCreatedComponent: { valueOnNaN: "0" },
      description: "Z-order layer index used to stack graphical components (higher values render on top)."
    };
    GraphicalComponent.addStyleOverrideAttributes.call(this, attributes);
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    const overrideAttributeNames = GraphicalComponent.returnStyleOverrideGroups.call(this).flatMap((group) => Object.keys(group));
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition({
      overrideAttributeNames
    });
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let labelDefinitions = returnLabelStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, labelDefinitions);
    return stateVariableDefinitions;
  }
}
function find_local_global_minima({
  domain,
  xscale,
  isInterpolatedFunction,
  xs,
  coeffs,
  numericalf,
  formula,
  variables,
  functionChildrenInfoToCalculateExtrema,
  numInputs,
  numOutputs,
  isPiecewiseFunction,
  numericalDomainsOfChildren,
  numIntervals = 1e3,
  numRecursions = 0,
  numerics
}) {
  if (isInterpolatedFunction) {
    let eps = numerics.eps;
    let minimaList = [];
    if (xs === null) {
      return {
        localMinima: minimaList,
        globalMinimum: null,
        globalInfimum: null
      };
    }
    let globalMinimum = [-Infinity, Infinity];
    let globalInfimum = [-Infinity, Infinity];
    let minimumAtPreviousRight = false;
    let { minx, maxx, openMin, openMax } = find_effective_domain({
      domain
    });
    let buffer = 1e-14 * Math.max(Math.abs(minx), Math.abs(maxx));
    let c2 = coeffs[0];
    let dx = xs[1] - xs[0];
    if (c2[3] === 0) {
      if (c2[2] > 0) {
        let x2 = -c2[1] / (2 * c2[2]);
        if (x2 + xs[0] >= minx - buffer && x2 + xs[0] <= maxx + buffer) {
          if (x2 <= dx - eps) {
            let pt2 = [
              x2 + xs[0],
              ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
            ];
            let atOpenBoundary = openMin && Math.abs(x2 + xs[0] - minx) < buffer || openMax && Math.abs(x2 + xs[0] - maxx) < buffer;
            if (!atOpenBoundary) {
              minimaList.push(pt2);
              if (pt2[1] < globalMinimum[1]) {
                globalMinimum = pt2;
              }
            }
            if (pt2[1] < globalInfimum[1]) {
              globalInfimum = pt2;
            }
          } else if (Math.abs(x2 - dx) < eps) {
            minimumAtPreviousRight = true;
          }
        }
      } else if (c2[2] < 0) {
        if (minx === -Infinity) {
          globalInfimum = [-Infinity, -Infinity];
        }
      } else {
        if (c2[1] === 0) {
          let xPt = xs[1];
          if (xPt >= minx - buffer) {
            if (c2[0] < globalMinimum[1]) {
              let atOpenBoundary = openMin && Math.abs(xPt - minx) < buffer;
              if (!atOpenBoundary) {
                globalMinimum = [xPt, c2[0]];
              }
            }
            if (c2[0] < globalInfimum[1]) {
              globalInfimum = [xPt, c2[0]];
            }
          }
        } else if (c2[1] > 0) {
          if (minx === -Infinity) {
            globalInfimum = [-Infinity, -Infinity];
          }
        }
      }
    } else {
      let discrim = 4 * c2[2] * c2[2] - 12 * c2[3] * c2[1];
      if (discrim > 0) {
        let sqrtdiscrim = Math.sqrt(discrim);
        let x2 = (-2 * c2[2] + sqrtdiscrim) / (6 * c2[3]);
        if (x2 + xs[0] >= minx - buffer && x2 + xs[0] <= maxx + buffer) {
          if (x2 <= dx - eps) {
            let pt2 = [
              x2 + xs[0],
              ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
            ];
            let atOpenBoundary = openMin && Math.abs(x2 + xs[0] - minx) < buffer || openMax && Math.abs(x2 + xs[0] - maxx) < buffer;
            if (!atOpenBoundary) {
              minimaList.push(pt2);
              if (!atOpenBoundary && pt2[1] < globalMinimum[1]) {
                globalMinimum = pt2;
              }
            }
            if (pt2[1] < globalInfimum[1]) {
              globalInfimum = pt2;
            }
          } else if (Math.abs(x2 - dx) < eps) {
            minimumAtPreviousRight = true;
          }
        }
      }
      if (c2[3] > 0) {
        if (minx === -Infinity) {
          globalInfimum = [-Infinity, -Infinity];
        }
      }
    }
    for (let i2 = 1; i2 < xs.length - 2; i2++) {
      c2 = coeffs[i2];
      dx = xs[i2 + 1] - xs[i2];
      if (c2[3] === 0) {
        if (c2[2] > 0) {
          let x2 = -c2[1] / (2 * c2[2]);
          if (x2 + xs[i2] >= minx - buffer) {
            if (x2 + xs[i2] <= maxx + buffer) {
              if (Math.abs(x2) < eps) {
                let pt2 = [
                  x2 + xs[i2],
                  ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
                ];
                let atOpenBoundary = openMin && Math.abs(x2 + xs[i2] - minx) < buffer || openMax && Math.abs(x2 + xs[i2] - maxx) < buffer;
                if (!atOpenBoundary) {
                  if (minimumAtPreviousRight) {
                    minimaList.push(pt2);
                  }
                  if (pt2[1] < globalMinimum[1]) {
                    globalMinimum = pt2;
                  }
                }
                if (pt2[1] < globalInfimum[1]) {
                  globalInfimum = pt2;
                }
              } else if (x2 >= eps && x2 <= dx - eps) {
                let pt2 = [
                  x2 + xs[i2],
                  ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
                ];
                let atOpenBoundary = openMin && Math.abs(x2 + xs[i2] - minx) < buffer || openMax && Math.abs(x2 + xs[i2] - maxx) < buffer;
                if (!atOpenBoundary) {
                  minimaList.push(pt2);
                  if (pt2[1] < globalMinimum[1]) {
                    globalMinimum = pt2;
                  }
                }
                if (pt2[1] < globalInfimum[1]) {
                  globalInfimum = pt2;
                }
              }
              minimumAtPreviousRight = Math.abs(x2 - dx) < eps;
            } else {
              minimumAtPreviousRight = false;
              continue;
            }
          }
        } else {
          if (c2[2] === 0 && c2[1] === 0) {
            if (xs[i2 + 1] >= minx - buffer && xs[i2] <= maxx + buffer) {
              let x1 = Math.max(xs[i2], minx);
              if (c2[0] < globalInfimum[1]) {
                globalInfimum = [x1, c2[0]];
              }
              if (c2[0] < globalMinimum[1]) {
                let atOpenBoundary1 = openMin && Math.abs(x1 - minx) < buffer || openMax && Math.abs(x1 - maxx) < buffer;
                if (!atOpenBoundary1) {
                  globalMinimum = [x1, c2[0]];
                } else {
                  let x2 = Math.min(xs[i2 + 1], maxx);
                  let atOpenBoundary2 = openMin && Math.abs(x2 - minx) < buffer || openMax && Math.abs(x2 - maxx) < buffer;
                  if (!atOpenBoundary2) {
                    globalMinimum = [x2, c2[0]];
                  } else {
                    let x3 = (x1 + x2) / 2;
                    let atOpenBoundary3 = openMin && Math.abs(x3 - minx) < buffer || openMax && Math.abs(x3 - maxx) < buffer;
                    if (!atOpenBoundary3) {
                      globalMinimum = [x3, c2[0]];
                    }
                  }
                }
              }
            }
          }
          minimumAtPreviousRight = false;
        }
      } else {
        let discrim = 4 * c2[2] * c2[2] - 12 * c2[3] * c2[1];
        if (discrim > 0) {
          let sqrtdiscrim = Math.sqrt(discrim);
          let x2 = (-2 * c2[2] + sqrtdiscrim) / (6 * c2[3]);
          if (x2 + xs[i2] >= minx - buffer) {
            if (x2 + xs[i2] <= maxx + buffer) {
              if (Math.abs(x2) < eps) {
                let pt2 = [
                  x2 + xs[i2],
                  ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
                ];
                let atOpenBoundary = openMin && Math.abs(x2 + xs[i2] - minx) < buffer || openMax && Math.abs(x2 + xs[i2] - maxx) < buffer;
                if (!atOpenBoundary) {
                  if (minimumAtPreviousRight) {
                    minimaList.push(pt2);
                  }
                  if (pt2[1] < globalMinimum[1]) {
                    globalMinimum = pt2;
                  }
                }
                if (pt2[1] < globalInfimum[1]) {
                  globalInfimum = pt2;
                }
              } else if (x2 >= eps && x2 <= dx - eps) {
                let pt2 = [
                  x2 + xs[i2],
                  ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
                ];
                let atOpenBoundary = openMin && Math.abs(x2 + xs[i2] - minx) < buffer || openMax && Math.abs(x2 + xs[i2] - maxx) < buffer;
                if (!atOpenBoundary) {
                  minimaList.push(pt2);
                  if (pt2[1] < globalMinimum[1]) {
                    globalMinimum = pt2;
                  }
                }
                if (pt2[1] < globalInfimum[1]) {
                  globalInfimum = pt2;
                }
              }
              minimumAtPreviousRight = Math.abs(x2 - dx) < eps;
            } else {
              minimumAtPreviousRight = false;
              continue;
            }
          }
        } else {
          minimumAtPreviousRight = false;
        }
      }
    }
    c2 = coeffs[xs.length - 2];
    if (c2[3] === 0) {
      if (c2[2] > 0) {
        let x2 = -c2[1] / (2 * c2[2]);
        if (x2 + xs[xs.length - 2] >= minx - buffer && x2 + xs[xs.length - 2] <= maxx + buffer) {
          if (Math.abs(x2) < eps) {
            let pt2 = [
              x2 + xs[xs.length - 2],
              ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
            ];
            let atOpenBoundary = openMin && Math.abs(x2 + xs[xs.length - 2] - minx) < buffer || openMax && Math.abs(x2 + xs[xs.length - 2] - maxx) < buffer;
            if (!atOpenBoundary) {
              if (minimumAtPreviousRight) {
                minimaList.push(pt2);
              }
              if (pt2[1] < globalMinimum[1]) {
                globalMinimum = pt2;
              }
            }
            if (pt2[1] < globalInfimum[1]) {
              globalInfimum = pt2;
            }
          } else if (x2 >= eps) {
            let pt2 = [
              x2 + xs[xs.length - 2],
              ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
            ];
            let atOpenBoundary = openMin && Math.abs(x2 + xs[xs.length - 2] - minx) < buffer || openMax && Math.abs(x2 + xs[xs.length - 2] - maxx) < buffer;
            if (!atOpenBoundary) {
              minimaList.push(pt2);
              if (pt2[1] < globalMinimum[1]) {
                globalMinimum = pt2;
              }
            }
            if (pt2[1] < globalInfimum[1]) {
              globalInfimum = pt2;
            }
          }
        }
      } else if (c2[2] < 0) {
        if (maxx === Infinity) {
          globalInfimum = [Infinity, -Infinity];
        }
      } else {
        if (c2[1] === 0) {
          let xPt = xs[xs.length - 2];
          if (xPt <= maxx + buffer) {
            if (c2[0] < globalMinimum[1]) {
              let atOpenBoundary = openMax && Math.abs(xPt - maxx) < buffer;
              if (!atOpenBoundary) {
                globalMinimum = [xPt, c2[0]];
              }
            }
            if (c2[0] < globalInfimum[1]) {
              globalInfimum = [xPt, c2[0]];
            }
          }
        } else if (c2[1] < 0) {
          if (maxx === Infinity) {
            globalInfimum = [Infinity, -Infinity];
          }
        }
      }
    } else {
      let discrim = 4 * c2[2] * c2[2] - 12 * c2[3] * c2[1];
      if (discrim > 0) {
        let sqrtdiscrim = Math.sqrt(discrim);
        let x2 = (-2 * c2[2] + sqrtdiscrim) / (6 * c2[3]);
        if (x2 + xs[xs.length - 2] >= minx - buffer && x2 + xs[xs.length - 2] <= maxx + buffer) {
          if (x2 >= eps) {
            let pt2 = [
              x2 + xs[xs.length - 2],
              ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
            ];
            let atOpenBoundary = openMin && Math.abs(x2 + xs[xs.length - 2] - minx) < buffer || openMax && Math.abs(x2 + xs[xs.length - 2] - maxx) < buffer;
            if (!atOpenBoundary) {
              minimaList.push(pt2);
              if (pt2[1] < globalMinimum[1]) {
                globalMinimum = pt2;
              }
            }
            if (pt2[1] < globalInfimum[1]) {
              globalInfimum = pt2;
            }
          } else if (Math.abs(x2) < eps) {
            let pt2 = [
              x2 + xs[xs.length - 2],
              ((c2[3] * x2 + c2[2]) * x2 + c2[1]) * x2 + c2[0]
            ];
            let atOpenBoundary = openMin && Math.abs(x2 + xs[xs.length - 2] - minx) < buffer || openMax && Math.abs(x2 + xs[xs.length - 2] - maxx) < buffer;
            if (!atOpenBoundary) {
              if (minimumAtPreviousRight) {
                minimaList.push(pt2);
              }
              if (pt2[1] < globalMinimum[1]) {
                globalMinimum = pt2;
              }
            }
            if (pt2[1] < globalInfimum[1]) {
              globalInfimum = pt2;
            }
          }
        }
      }
      if (c2[3] < 0) {
        if (maxx === Infinity) {
          globalInfimum = [Infinity, -Infinity];
        }
      }
    }
    ({ globalMinimum, globalInfimum } = finalizeGlobalMinimum({
      numericalf,
      minx,
      openMin,
      globalMinimum,
      globalInfimum,
      maxx,
      openMax
    }));
    return {
      localMinima: minimaList,
      globalMinimum,
      globalInfimum
    };
  } else if (isPiecewiseFunction) {
    return find_minima_of_piecewise({
      functionChildrenInfoToCalculateExtrema,
      domain,
      numericalDomainsOfChildren,
      numericalf,
      numerics
    });
  } else if (functionChildrenInfoToCalculateExtrema?.length > 0) {
    let argsForRecursion = {
      ...functionChildrenInfoToCalculateExtrema[0].stateValues
    };
    argsForRecursion.domain = domain;
    argsForRecursion.numerics = numerics;
    return find_local_global_minima(argsForRecursion);
  } else {
    if (!(numInputs === 1 && numOutputs === 1)) {
      return {
        localMinima: [],
        globalMinimum: null,
        globalInfimum: null
      };
    }
    let varString = variables[0].subscripts_to_strings().tree;
    let derivative_f;
    let haveDerivative = true;
    let derivative;
    if (formula.variables().includes("＿")) {
      haveDerivative = false;
      derivative = () => NaN;
    } else {
      let derivative_formula = formula.subscripts_to_strings().derivative(varString);
      try {
        derivative_f = derivative_formula.f();
      } catch (e3) {
        haveDerivative = false;
        derivative = () => NaN;
      }
    }
    if (haveDerivative) {
      derivative = function(x2) {
        try {
          return derivative_f({ [varString]: x2 });
        } catch (e3) {
          return NaN;
        }
      };
    }
    let f2 = (x2) => numericalf(x2, true);
    let { minx, maxx, openMin, openMax } = find_effective_domain({
      domain,
      truncateToFiniteDomain: true,
      xscale
    });
    let dx = (maxx - minx) / numIntervals;
    let globalMinimum = [-Infinity, Infinity];
    let globalMinimumAtLocalMinimum = false;
    let globalInfimum = [-Infinity, Infinity];
    let globalInfimumAtLocalMinimum = false;
    let buffer = 1e-10 * Math.max(Math.abs(minx), Math.abs(maxx));
    let minimaList = [];
    let minimumAtPreviousRight = false;
    let addedAtPreviousRightViaDeriv = false;
    let fright = f2(minx - dx);
    let dright = derivative(minx - dx);
    for (let i2 = -1; i2 < numIntervals + 1; i2++) {
      let xleft = minx + i2 * dx;
      let xright = minx + (i2 + 1) * dx;
      let fleft = fright;
      fright = f2(xright);
      let dleft = dright;
      dright = derivative(xright);
      if (Number.isNaN(fleft) || Number.isNaN(fright)) {
        continue;
      }
      let foundFromDeriv = false;
      if (haveDerivative && dleft * dright <= 0) {
        let x2;
        if (dleft === 0) {
          x2 = xleft;
        } else if (dright === 0) {
          x2 = xright;
        } else {
          x2 = numerics.fzero(derivative, [xleft, xright]);
        }
        if (x2 >= minx - buffer && x2 <= maxx + buffer) {
          let atOpenBoundary = openMin && Math.abs(x2 - minx) < buffer || openMax && Math.abs(x2 - maxx) < buffer;
          let fx = f2(x2);
          let eps = 1e-6;
          let tol_act = 0.5 * eps * (Math.abs(x2) + 1);
          if (derivative(x2 - tol_act) < 0 && derivative(x2 + tol_act) > 0) {
            foundFromDeriv = true;
            minimumAtPreviousRight = false;
            if (!atOpenBoundary && !(addedAtPreviousRightViaDeriv && Math.abs(x2 - xleft) < buffer)) {
              minimaList.push([x2, fx]);
              addedAtPreviousRightViaDeriv = Math.abs(x2 - xright) < buffer;
              if (!globalMinimumAtLocalMinimum && fx <= globalMinimum[1]) {
                globalMinimum = [x2, fx];
                globalMinimumAtLocalMinimum = true;
              }
              if (!globalInfimumAtLocalMinimum && fx <= globalInfimum[1]) {
                globalInfimum = [x2, fx];
                globalInfimumAtLocalMinimum = true;
              }
            } else {
              addedAtPreviousRightViaDeriv = false;
            }
          }
          if (!atOpenBoundary && fx < globalMinimum[1]) {
            globalMinimum = [x2, fx];
            globalMinimumAtLocalMinimum = false;
          }
          if (fx < globalInfimum[1]) {
            globalInfimum = [x2, fx];
            globalInfimumAtLocalMinimum = false;
          }
        }
      }
      if (!foundFromDeriv) {
        addedAtPreviousRightViaDeriv = false;
        let result = numerics.fminbr(f2, [xleft, xright]);
        if (result.success !== true) {
          continue;
        }
        let x2 = result.x;
        let fx = result.fx;
        if (x2 >= minx - buffer && x2 <= maxx + buffer) {
          let mindf = Math.max(
            Math.abs(fx),
            Math.abs(fleft),
            Math.abs(fright)
          ) * 1e-12;
          if (fx > fleft + mindf && x2 - xleft > 2 * result.tol || fx > fright + mindf && xright - x2 > 2 * result.tol) {
            if (numRecursions < 100) {
              let subDomainLeft = Math.max(xleft, minx);
              let subDomainRight = Math.min(xright, maxx);
              let subDomainLeftOpen = subDomainLeft === minx && openMin;
              let subDomainRightOpen = subDomainRight === maxx && openMax;
              let subDomain1 = me$3.fromAst([
                "interval",
                ["tuple", subDomainLeft, subDomainRight],
                [
                  "tuple",
                  !subDomainLeftOpen,
                  !subDomainRightOpen
                ]
              ]);
              let recursionResult = find_local_global_minima({
                domain: [subDomain1],
                xscale,
                isInterpolatedFunction,
                xs,
                coeffs,
                numericalf,
                formula,
                variables,
                functionChildrenInfoToCalculateExtrema,
                numInputs,
                numOutputs,
                numIntervals: 4,
                numRecursions: numRecursions + 1,
                numerics
              });
              let newLocalMinima = recursionResult.localMinima;
              if (newLocalMinima[newLocalMinima.length - 1]?.[0] === xright) {
                newLocalMinima.pop();
                minimumAtPreviousRight = true;
              } else {
                minimumAtPreviousRight = false;
              }
              minimaList.push(...newLocalMinima);
              if (recursionResult.globalMinimum?.[1] < globalMinimum[1]) {
                globalMinimum = recursionResult.globalMinimum;
              }
              if (recursionResult.globalInfimum?.[1] < globalInfimum[1]) {
                globalInfimum = recursionResult.globalInfimum;
              }
              continue;
            }
          }
          let atOpenBoundary = openMin && Math.abs(x2 - minx) < buffer || openMax && Math.abs(x2 - maxx) < buffer;
          if (!atOpenBoundary) {
            if (fleft < fx - mindf) {
              if (minimumAtPreviousRight) {
                if (Number.isFinite(fleft)) {
                  minimaList.push([xleft, fleft]);
                  if (!globalMinimumAtLocalMinimum && fleft <= globalMinimum[1]) {
                    globalMinimum = [xleft, fleft];
                    globalMinimumAtLocalMinimum = true;
                  }
                  if (!globalInfimumAtLocalMinimum && fleft <= globalInfimum[1]) {
                    globalInfimum = [xleft, fleft];
                    globalInfimumAtLocalMinimum = true;
                  }
                }
              }
              minimumAtPreviousRight = false;
            } else if (fright < fx - mindf) {
              minimumAtPreviousRight = true;
            } else {
              minimumAtPreviousRight = false;
              if (Number.isFinite(fx) && fx < fright - mindf && fx < fleft - mindf && fx < f2(x2 + result.tol) - mindf && fx < f2(x2 - result.tol) - mindf) {
                minimaList.push([x2, fx]);
                if (!globalMinimumAtLocalMinimum && fx <= globalMinimum[1]) {
                  globalMinimum = [x2, fx];
                  globalMinimumAtLocalMinimum = true;
                }
                if (!globalInfimumAtLocalMinimum && fx <= globalInfimum[1]) {
                  globalInfimum = [x2, fx];
                  globalInfimumAtLocalMinimum = true;
                }
              }
            }
          }
          if (!atOpenBoundary && fx < globalMinimum[1]) {
            globalMinimum = [x2, fx];
            globalMinimumAtLocalMinimum = false;
          }
          if (fx < globalInfimum[1]) {
            globalInfimum = [x2, fx];
            globalInfimumAtLocalMinimum = false;
          }
        }
      }
    }
    ({ globalMinimum, globalInfimum } = finalizeGlobalMinimum({
      numericalf,
      minx,
      openMin,
      globalMinimum,
      globalInfimum,
      maxx,
      openMax
    }));
    return {
      localMinima: minimaList,
      globalMinimum,
      globalInfimum
    };
  }
}
function find_local_global_maxima({
  domain,
  xscale,
  isInterpolatedFunction,
  xs,
  coeffs,
  numericalf,
  formula,
  variables,
  functionChildrenInfoToCalculateExtrema,
  isPiecewiseFunction,
  numericalDomainsOfChildren,
  numInputs,
  numOutputs,
  numerics
}) {
  let numericalfFlip = (...args) => -1 * numericalf(...args);
  let coeffsFlip;
  let formulaFlip;
  if (isInterpolatedFunction) {
    if (xs === null) {
      return {
        localMaxima: [],
        globalMaximum: null,
        globalInfimum: null
      };
    }
    coeffsFlip = coeffs.map((cs) => cs.map((v2) => -v2));
  } else if (isPiecewiseFunction) {
    return find_maxima_of_piecewise({
      functionChildrenInfoToCalculateExtrema,
      domain,
      numericalDomainsOfChildren,
      numericalf,
      numerics
    });
  } else if (functionChildrenInfoToCalculateExtrema?.length > 0) {
    let argsForRecursion = {
      ...functionChildrenInfoToCalculateExtrema[0].stateValues
    };
    argsForRecursion.domain = domain;
    argsForRecursion.numerics = numerics;
    return find_local_global_maxima(argsForRecursion);
  } else {
    formulaFlip = formula.context.fromAst(["-", formula.tree]);
  }
  let { localMinima, globalMinimum, globalInfimum } = find_local_global_minima({
    domain,
    xscale,
    isInterpolatedFunction,
    xs,
    coeffs: coeffsFlip,
    numericalf: numericalfFlip,
    formula: formulaFlip,
    variables,
    numInputs,
    numOutputs,
    numerics
  });
  let localMaxima = localMinima.map((pt2) => [pt2[0], -pt2[1]]);
  let globalMaximum = null, globalSupremum = null;
  if (globalMinimum) {
    globalMaximum = [globalMinimum[0], -1 * globalMinimum[1]];
  }
  if (globalInfimum) {
    globalSupremum = [globalInfimum[0], -1 * globalInfimum[1]];
  }
  return { localMaxima, globalMaximum, globalSupremum };
}
function finalizeGlobalMinimum({
  numericalf,
  minx,
  openMin,
  globalMinimum,
  globalInfimum,
  maxx,
  openMax
}) {
  if (Number.isFinite(minx)) {
    let fmin = numericalf(minx, true);
    if (Math.abs(fmin) === Infinity) {
      let f_near_min = numericalf(minx + (maxx - minx) * 1e-12, true);
      if (f_near_min > 0) {
        fmin = Infinity;
      } else if (f_near_min < 0) {
        fmin = -Infinity;
      } else {
        fmin = NaN;
      }
    }
    if (!openMin && fmin < globalMinimum[1]) {
      globalMinimum = [minx, fmin];
    }
    if (fmin < globalInfimum[1]) {
      globalInfimum = [minx, fmin];
    }
  }
  if (Number.isFinite(maxx)) {
    let fmax = numericalf(maxx, true);
    if (Math.abs(fmax) === Infinity) {
      let f_near_max = numericalf(maxx - (maxx - minx) * 1e-12, true);
      if (f_near_max > 0) {
        fmax = Infinity;
      } else if (f_near_max < 0) {
        fmax = -Infinity;
      } else {
        fmax = NaN;
      }
    }
    if (!openMax && fmax < globalMinimum[1]) {
      globalMinimum = [maxx, fmax];
    }
    if (fmax < globalInfimum[1]) {
      globalInfimum = [maxx, fmax];
    }
  }
  if (globalMinimum[1] === Infinity) {
    globalMinimum = null;
  }
  if (globalInfimum[1] === Infinity) {
    globalInfimum = null;
  }
  if (globalInfimum && globalMinimum) {
    if (globalInfimum[1] === -Infinity) {
      globalMinimum = null;
    } else {
      let buffer = Math.max(
        Math.abs(globalInfimum[1]),
        Math.abs(globalMinimum[1])
      ) * 1e-12;
      if (globalInfimum[1] < globalMinimum[1] - buffer) {
        globalMinimum = null;
      }
    }
  }
  return { globalMinimum, globalInfimum };
}
function find_minima_of_piecewise({
  functionChildrenInfoToCalculateExtrema,
  domain,
  numericalDomainsOfChildren,
  numericalf,
  numerics
}) {
  let globalMinimum = [-Infinity, Infinity];
  let globalInfimum = [-Infinity, Infinity];
  let eps = numerics.eps;
  let minimaList = [];
  let f2 = numericalf;
  let endpointsToManuallyCheck = [];
  let effectiveDomainsOfChildren = find_effective_domains_piecewise_children({
    domain,
    numericalDomainsOfChildren
  });
  for (let [ind, childDomain] of effectiveDomainsOfChildren.entries()) {
    let childDomainAsMath = mathExpressionFromSubsetValue({
      subsetValue: childDomain
    });
    let childDomainPieces = [];
    if (childDomainAsMath.tree[0] === "union") {
      childDomainPieces = childDomainAsMath.tree.slice(1);
    } else {
      childDomainPieces = [childDomainAsMath.tree];
    }
    for (let domainPiece of childDomainPieces) {
      let thisDomain;
      if (domainPiece === "emptyset") {
        continue;
      } else if (domainPiece === "R") {
        thisDomain = null;
      } else if (domainPiece[0] === "set") {
        endpointsToManuallyCheck.push(domainPiece[1]);
        continue;
      } else {
        thisDomain = domainPiece;
      }
      let args = {
        ...functionChildrenInfoToCalculateExtrema[ind].stateValues
      };
      if (thisDomain) {
        args.domain = [me$3.fromAst(thisDomain)];
      }
      args.numerics = numerics;
      let subResults = find_local_global_minima(args);
      let childMinima = subResults.localMinima;
      if (thisDomain) {
        let minx = me$3.fromAst(thisDomain[1][1]).evaluate_to_constant();
        if (Number.isFinite(minx)) {
          endpointsToManuallyCheck.push(minx);
          childMinima = childMinima.filter(
            (minpair) => minpair[0] > minx + eps
          );
        }
        let maxx = me$3.fromAst(thisDomain[1][2]).evaluate_to_constant();
        if (Number.isFinite(maxx)) {
          endpointsToManuallyCheck.push(maxx);
          childMinima = childMinima.filter(
            (minpair) => minpair[0] < maxx - eps
          );
        }
      }
      minimaList.push(...childMinima);
      if (subResults.globalMinimum?.[1] < globalMinimum[1]) {
        globalMinimum = subResults.globalMinimum;
      }
      if (subResults.globalInfimum?.[1] < globalInfimum[1]) {
        globalInfimum = subResults.globalInfimum;
      }
    }
  }
  for (let ept of endpointsToManuallyCheck) {
    let x2 = me$3.fromAst(ept).evaluate_to_constant();
    if (Number.isFinite(x2)) {
      let fx = f2(x2);
      if (fx < f2(x2 - eps, true) && fx < f2(x2 + eps, true)) {
        if (minimaList.every(
          (minpair) => minpair[0] < x2 - eps || minpair[0] > x2 + eps
        )) {
          minimaList.push([x2, fx]);
        }
      }
      if (fx < globalMinimum[1]) {
        globalMinimum = [x2, fx];
      }
      if (fx < globalInfimum[1]) {
        globalInfimum = [x2, fx];
      }
    }
  }
  minimaList = minimaList.sort((a2, b2) => a2[0] - b2[0]);
  ({ globalMinimum, globalInfimum } = finalizeGlobalMinimum({
    globalMinimum,
    globalInfimum
  }));
  return { localMinima: minimaList, globalMinimum, globalInfimum };
}
function find_maxima_of_piecewise({
  functionChildrenInfoToCalculateExtrema,
  domain,
  numericalDomainsOfChildren,
  numericalf,
  numerics
}) {
  let flippedFunctionChildren = flip_function_children(
    functionChildrenInfoToCalculateExtrema
  );
  let numericalfFlip = (...args) => -1 * numericalf(...args);
  let { localMinima, globalMinimum, globalInfimum } = find_minima_of_piecewise({
    functionChildrenInfoToCalculateExtrema: flippedFunctionChildren,
    domain,
    numericalDomainsOfChildren,
    numericalf: numericalfFlip,
    numerics
  });
  let localMaxima = localMinima.map((pt2) => [pt2[0], -pt2[1]]);
  let globalMaximum = null, globalSupremum = null;
  if (globalMinimum) {
    globalMaximum = [globalMinimum[0], -1 * globalMinimum[1]];
  }
  if (globalInfimum) {
    globalSupremum = [globalInfimum[0], -1 * globalInfimum[1]];
  }
  return { localMaxima, globalMaximum, globalSupremum };
}
function flip_function_children(functionChildren) {
  let flippedFunctionChildren = [];
  for (let functionChild of functionChildren) {
    let stateValues = functionChild.stateValues;
    let flippedStateValues = { ...stateValues };
    flippedStateValues.numericalf = (...args) => -1 * stateValues.numericalf(...args);
    if (stateValues.isInterpolatedFunction) {
      flippedStateValues.coeffsFlip = stateValues.coeffs.map(
        (cs) => cs.map((v2) => -v2)
      );
    } else if (stateValues.isInterpolatedFunction || stateValues.functionChildrenInfoToCalculateExtrema?.length > 0) {
      flippedStateValues.functionChildrenInfoToCalculateExtrema = flip_function_children(
        stateValues.functionChildrenInfoToCalculateExtrema
      );
    } else {
      flippedStateValues.formula = stateValues.formula.context.fromAst([
        "-",
        stateValues.formula.tree
      ]);
    }
    flippedFunctionChildren.push({
      stateValues: flippedStateValues
    });
  }
  return flippedFunctionChildren;
}
let Function$1 = class Function2 extends InlineComponent {
  static componentType = "function";
  static componentDocs = {
    summary: "A mathematical function, defined by formula or interpolation"
  };
  static rendererType = "math";
  // Opt into line style overrides (lineStyle, lineWidth) so that they are
  // forwarded to the curve when this function is adapted into one inside a
  // <graph>. Fill overrides are intentionally excluded: functions have no
  // enclosed interior.
  static styleOverrideCategories = ["line"];
  static primaryStateVariableForDefinition = "numericalfShadow";
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.simplify = {
      createComponentOfType: "text",
      createStateVariable: "simplifySpecified",
      defaultValue: "full",
      toLowerCase: true,
      valueForTrue: "full",
      valueForFalse: "none",
      validValues: [
        {
          value: "none",
          description: "No simplification is applied."
        },
        {
          value: "full",
          description: "Fully simplify the function's formula."
        },
        {
          value: "numbers",
          description: "Simplify numeric subexpressions only, leaving symbolic structure intact."
        },
        {
          value: "numbersPreserveOrder",
          description: "Like `numbers`, but does not reorder commutative operands."
        },
        {
          value: "normalizeOrder",
          description: "Reorder commutative operands into a canonical form without simplifying values."
        }
      ],
      description: "Level of simplification applied to the function's formula."
    };
    attributes.expand = {
      description: "Whether to expand the function's formula.",
      createComponentOfType: "boolean",
      createStateVariable: "expandSpecified",
      defaultValue: false,
      public: true
    };
    attributes.xscale = {
      description: "Scale factor used along the x-axis.",
      createComponentOfType: "number",
      createStateVariable: "xscale",
      defaultValue: 1,
      public: true
    };
    attributes.yscale = {
      description: "Scale factor used along the y-axis.",
      createComponentOfType: "number",
      createStateVariable: "yscale",
      defaultValue: 1,
      public: true
    };
    attributes.numInputs = {
      createComponentOfType: "integer",
      description: "Number of input arguments the function accepts."
    };
    attributes.numOutputs = {
      createComponentOfType: "integer",
      description: "Number of output values the function produces."
    };
    attributes.domain = {
      createComponentOfType: "intervalList",
      description: "Restriction of the function's domain to a list of intervals."
    };
    Object.assign(attributes, returnLabelAttributes());
    attributes.applyStyleToLabel = {
      description: "Whether to apply the function's style to its label.",
      createComponentOfType: "boolean",
      createStateVariable: "applyStyleToLabel",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.labelPosition = {
      description: "Position of the function's label.",
      createComponentOfType: "text",
      createStateVariable: "labelPosition",
      defaultValue: "upperRight",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "upperRight",
          description: "Place the label above and to the right of the function curve."
        },
        {
          value: "upperLeft",
          description: "Place the label above and to the left of the function curve."
        },
        {
          value: "lowerRight",
          description: "Place the label below and to the right of the function curve."
        },
        {
          value: "lowerLeft",
          description: "Place the label below and to the left of the function curve."
        },
        {
          value: "top",
          description: "Place the label directly above the function curve."
        },
        {
          value: "bottom",
          description: "Place the label directly below the function curve."
        },
        {
          value: "left",
          description: "Place the label directly to the left of the function curve."
        },
        {
          value: "right",
          description: "Place the label directly to the right of the function curve."
        }
      ]
    };
    attributes.layer = {
      description: "Z-order layer index for stacking the function on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    attributes.minima = {
      createComponentOfType: "extrema",
      description: "Local minima of an interpolated function."
    };
    attributes.maxima = {
      createComponentOfType: "extrema",
      description: "Local maxima of an interpolated function."
    };
    attributes.extrema = {
      createComponentOfType: "extrema",
      description: "Local extrema (combined minima and maxima) of an interpolated function."
    };
    attributes.through = {
      createComponentOfType: "pointList",
      description: "Points the interpolated function should pass through."
    };
    attributes.throughSlopes = {
      createComponentOfType: "mathList",
      description: "Slopes the interpolated function should have at each through-point."
    };
    attributes.variables = {
      createComponentOfType: "_variableNameList",
      description: "Names of the function's input variables."
    };
    attributes.variable = {
      createComponentOfType: "_variableName",
      description: "Name of the function's single input variable."
    };
    attributes.symbolic = {
      createComponentOfType: "boolean",
      description: "Whether the function should be evaluated symbolically rather than numerically."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.nearestPointAsCurve = {
      createComponentOfType: "boolean",
      createStateVariable: "nearestPointAsCurve",
      defaultValue: false,
      description: "Whether nearest-point queries should treat the function as a curve in the plane rather than a graph y = f(x)."
    };
    GraphicalComponent.addStyleOverrideAttributes.call(this, attributes);
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        wrappingComponentType: "math"
      })
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      },
      {
        group: "functions",
        componentTypes: ["function"]
      },
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = GraphicalComponent.returnStateVariableDefinitions.call(this);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the function's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let styleDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (styleDescription) {
            styleDescription += " ";
          }
          styleDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (styleDescription) {
          styleDescription += " ";
        }
        styleDescription += lineColorWord;
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "function".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " function";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({
      childGroupsIfSingleMatch: ["functions"]
    });
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.isInterpolatedFunction = {
      returnDependencies: () => ({
        through: {
          dependencyType: "attributeComponent",
          attributeName: "through"
        },
        minima: {
          dependencyType: "attributeComponent",
          attributeName: "minima"
        },
        maxima: {
          dependencyType: "attributeComponent",
          attributeName: "maxima"
        },
        extrema: {
          dependencyType: "attributeComponent",
          attributeName: "extrema"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            isInterpolatedFunction: Boolean(
              dependencyValues.through || dependencyValues.minima || dependencyValues.maxima || dependencyValues.extrema
            )
          }
        };
      }
    };
    stateVariableDefinitions.numInputs = {
      description: "Number of input variables of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        numInputsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "numInputs",
          variableNames: ["value"]
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["numInputs"]
        },
        variablesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "variables",
          variableNames: ["numComponents"],
          dontRecurseToShadowsIfHaveAttribute: "variable"
        },
        isInterpolatedFunction: {
          dependencyType: "stateVariable",
          variableName: "isInterpolatedFunction"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.isInterpolatedFunction) {
          return { setValue: { numInputs: 1 } };
        } else if (dependencyValues.numInputsAttr !== null) {
          let numInputs = dependencyValues.numInputsAttr.stateValues.value;
          if (!(numInputs >= 0)) {
            numInputs = 1;
          }
          return { setValue: { numInputs } };
        } else if (dependencyValues.variablesAttr !== null) {
          return {
            setValue: {
              numInputs: Math.max(
                1,
                dependencyValues.variablesAttr.stateValues.numComponents
              )
            }
          };
        } else if (dependencyValues.functionChild.length > 0) {
          return {
            setValue: {
              numInputs: dependencyValues.functionChild[0].stateValues.numInputs
            }
          };
        } else {
          return { setValue: { numInputs: 1 } };
        }
      }
    };
    stateVariableDefinitions.numOutputs = {
      description: "Number of output values of the function.",
      defaultValue: 1,
      hasEssential: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        numOutputsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "numOutputs",
          variableNames: ["value"]
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["numOutputs"]
        },
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.functionChild.length > 0) {
          return {
            setValue: {
              numOutputs: dependencyValues.functionChild[0].stateValues.numOutputs
            }
          };
        } else if (dependencyValues.numOutputsAttr !== null) {
          let numOutputs = dependencyValues.numOutputsAttr.stateValues.value;
          if (!(numOutputs >= 0)) {
            numOutputs = 1;
          }
          return { setValue: { numOutputs } };
        } else if (dependencyValues.mathChild.length > 0) {
          let formula = dependencyValues.mathChild[0].stateValues.value;
          let formulaIsVectorValued = Array.isArray(formula.tree) && vectorOperators.includes(formula.tree[0]);
          let numOutputs = 1;
          if (formulaIsVectorValued) {
            numOutputs = formula.tree.length - 1;
          }
          return { setValue: { numOutputs } };
        } else {
          return { useEssentialOrDefaultValue: { numOutputs: true } };
        }
      }
    };
    stateVariableDefinitions.domain = {
      description: "Domain interval(s) of the function.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "interval"
      },
      returnArraySizeDependencies: () => ({
        numInputs: {
          dependencyType: "stateVariable",
          variableName: "numInputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numInputs];
      },
      returnArrayDependenciesByKey: () => ({
        globalDependencies: {
          domainAttr: {
            dependencyType: "attributeComponent",
            attributeName: "domain",
            variableNames: ["intervals"]
          },
          functionChild: {
            dependencyType: "child",
            childGroups: ["functions"],
            variableNames: ["domain"]
          },
          numInputs: {
            dependencyType: "stateVariable",
            variableName: "numInputs"
          }
        }
      }),
      arrayDefinitionByKey({ globalDependencyValues }) {
        if (globalDependencyValues.domainAttr !== null) {
          let numInputs = globalDependencyValues.numInputs;
          let specifiedDomain = globalDependencyValues.domainAttr.stateValues.intervals.slice(
            0,
            numInputs
          );
          if (specifiedDomain.length !== numInputs) {
            let warning = {
              type: "warning",
              message: `Insufficient dimensions for domain for function. Domain has ${specifiedDomain.length} interval${specifiedDomain.length === 1 ? "" : "s"} but the function has ${numInputs} input${numInputs === 1 ? "" : "s"}.`
            };
            if (globalDependencyValues.domainAttr.position) {
              warning.position = globalDependencyValues.domainAttr.position;
            }
            let infDomain = me$3.fromAst([
              "interval",
              ["tuple", -Infinity, Infinity],
              ["tuple", false, false]
            ]);
            let domain = Array(numInputs).fill(infDomain);
            return {
              setValue: { domain },
              sendDiagnostics: [warning]
            };
          }
          if (!specifiedDomain.every(
            (interval) => (
              // At present, each input dimension must be a
              // single interval tree. Compound 1D domains
              // such as unions are not accepted here.
              Array.isArray(interval.tree) && interval.tree[0] === "interval"
            )
          )) {
            let warning = {
              type: "warning",
              message: `Invalid format for domain for function.`
            };
            if (globalDependencyValues.domainAttr.position) {
              warning.position = globalDependencyValues.domainAttr.position;
            }
            let infDomain = me$3.fromAst([
              "interval",
              ["tuple", -Infinity, Infinity],
              ["tuple", false, false]
            ]);
            let domain = Array(numInputs).fill(infDomain);
            return {
              setValue: { domain },
              sendDiagnostics: [warning]
            };
          }
          return { setValue: { domain: specifiedDomain } };
        } else if (globalDependencyValues.functionChild.length > 0) {
          return {
            setValue: {
              domain: globalDependencyValues.functionChild[0].stateValues.domain
            }
          };
        } else {
          let infDomain = me$3.fromAst([
            "interval",
            ["tuple", -Infinity, Infinity],
            ["tuple", false, false]
          ]);
          let domain = Array(globalDependencyValues.numInputs).fill(
            infDomain
          );
          return { setValue: { domain } };
        }
      }
    };
    stateVariableDefinitions.simplify = {
      description: "Level of simplification applied when displaying the formula.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        simplifySpecified: {
          dependencyType: "stateVariable",
          variableName: "simplifySpecified"
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["simplify"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (usedDefault.simplifySpecified && dependencyValues.functionChild.length > 0) {
          return {
            setValue: {
              simplify: dependencyValues.functionChild[0].stateValues.simplify
            }
          };
        } else {
          return {
            setValue: {
              simplify: dependencyValues.simplifySpecified
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        usedDefault
      }) {
        if (usedDefault.simplifySpecified && dependencyValues.functionChild.length > 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "functionChild",
                desiredValue: desiredStateVariableValues.simplify,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setDependency: "simplifySpecified",
                desiredValue: desiredStateVariableValues.simplify
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.expand = {
      description: "Whether to expand the formula.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        expandSpecified: {
          dependencyType: "stateVariable",
          variableName: "expandSpecified"
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["expand"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (usedDefault.expandSpecified && dependencyValues.functionChild.length > 0) {
          return {
            setValue: {
              expand: dependencyValues.functionChild[0].stateValues.expand
            }
          };
        } else {
          return {
            setValue: { expand: dependencyValues.expandSpecified }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        usedDefault
      }) {
        if (usedDefault.expandSpecified && dependencyValues.functionChild.length > 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "functionChild",
                desiredValue: desiredStateVariableValues.expand,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setDependency: "expandSpecified",
                desiredValue: desiredStateVariableValues.expand
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.numericalfShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          numericalfShadow: true
        }
      })
    };
    stateVariableDefinitions.symbolicfShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          symbolicfShadow: true
        }
      })
    };
    stateVariableDefinitions.symbolic = {
      description: "Whether the function is treated symbolically.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      defaultValue: true,
      hasEssential: true,
      returnDependencies: () => ({
        symbolicAttr: {
          dependencyType: "attributeComponent",
          attributeName: "symbolic",
          variableNames: ["value"]
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["symbolic"]
        },
        numericalfShadow: {
          dependencyType: "stateVariable",
          variableName: "numericalfShadow"
        },
        symbolicfShadow: {
          dependencyType: "stateVariable",
          variableName: "symbolicfShadow"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.symbolicAttr !== null) {
          return {
            setValue: {
              symbolic: dependencyValues.symbolicAttr.stateValues.value
            }
          };
        } else if (dependencyValues.functionChild.length > 0) {
          return {
            setValue: {
              symbolic: dependencyValues.functionChild[0].stateValues.symbolic
            }
          };
        } else if (dependencyValues.numericalfShadow) {
          return { setValue: { symbolic: false } };
        } else if (dependencyValues.symbolicfShadow) {
          return { setValue: { symbolic: true } };
        } else {
          return { useEssentialOrDefaultValue: { symbolic: true } };
        }
      }
    };
    stateVariableDefinitions.variables = {
      description: "The names of the function's input variables.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      entryPrefixes: ["variable"],
      returnArraySizeDependencies: () => ({
        numInputs: {
          dependencyType: "stateVariable",
          variableName: "numInputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numInputs];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          variablesAttr: {
            dependencyType: "attributeComponent",
            attributeName: "variables",
            variableNames: ["variables"],
            dontRecurseToShadowsIfHaveAttribute: "variable"
          },
          variableAttr: {
            dependencyType: "attributeComponent",
            attributeName: "variable",
            variableNames: ["value"],
            dontRecurseToShadowsIfHaveAttribute: "variables"
          },
          parentVariableForChild: {
            dependencyType: "parentStateVariable",
            variableName: "variableForChild"
          },
          functionChild: {
            dependencyType: "child",
            childGroups: ["functions"]
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            functionChild: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: [
                "variable" + (Number(arrayKey) + 1)
              ]
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arraySize,
        arrayKeys,
        usedDefault
      }) {
        if (globalDependencyValues.variablesAttr !== null || globalDependencyValues.variableAttr !== null) {
          let variablesSpecified = globalDependencyValues.variablesAttr?.stateValues.variables;
          if (!variablesSpecified) {
            variablesSpecified = [
              globalDependencyValues.variableAttr.stateValues.value
            ];
          }
          return {
            setValue: {
              variables: returnNVariables(
                arraySize[0],
                variablesSpecified
              )
            }
          };
        } else if (globalDependencyValues.functionChild.length > 0) {
          let variables = {};
          for (let arrayKey of arrayKeys) {
            variables[arrayKey] = dependencyValuesByKey[arrayKey].functionChild[0].stateValues["variable" + (Number(arrayKey) + 1)];
          }
          return { setValue: { variables } };
        } else if (globalDependencyValues.parentVariableForChild && !usedDefault.parentVariableForChild) {
          return {
            setValue: {
              variables: Array(arraySize[0]).fill(
                globalDependencyValues.parentVariableForChild
              )
            }
          };
        } else {
          return {
            setValue: {
              variables: returnNVariables(arraySize[0], [])
            }
          };
        }
      }
    };
    stateVariableDefinitions.variable = {
      isAlias: true,
      targetVariableName: "variable1",
      description: "The function's first input variable name."
    };
    stateVariableDefinitions.mathChildName = {
      returnDependencies() {
        return {
          mathChild: {
            dependencyType: "child",
            childGroups: ["maths"]
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.mathChild.length > 0) {
          return {
            setValue: {
              mathChildName: dependencyValues.mathChild[0].componentIdx
            }
          };
        } else {
          return { setValue: { mathChildName: null } };
        }
      }
    };
    stateVariableDefinitions.mathChildCreatedBySugar = {
      stateVariablesDeterminingDependencies: ["mathChildName"],
      returnDependencies({ stateValues }) {
        if (stateValues.mathChildName) {
          return {
            mathChildCreatedBySugar: {
              dependencyType: "doenetAttribute",
              componentIdx: stateValues.mathChildName,
              attributeName: "createdFromSugar"
            }
          };
        } else {
          return {};
        }
      },
      definition({ dependencyValues }) {
        return {
          setValue: {
            mathChildCreatedBySugar: Boolean(
              dependencyValues.mathChildCreatedBySugar
            )
          }
        };
      }
    };
    stateVariableDefinitions.haveNaNChildToReevaluate = {
      stateVariablesDeterminingDependencies: [
        "mathChildName",
        "mathChildCreatedBySugar"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {};
        if (!stateValues.isInterpolatedFunction) {
          dependencies.variables = {
            dependencyType: "stateVariable",
            variableName: "variables"
          };
          if (stateValues.mathChildName) {
            if (stateValues.mathChildCreatedBySugar) {
              dependencies.mathChildExpressionWithCodes = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "expressionWithCodes"
              };
              dependencies.mathChildMathChildren = {
                dependencyType: "child",
                parentIdx: stateValues.mathChildName,
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluate",
                  "inputMaths"
                ],
                variablesOptional: true
              };
              dependencies.mathChildCodePre = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "codePre"
              };
            } else {
              dependencies.mathChild = {
                dependencyType: "child",
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluate",
                  "inputMaths"
                ],
                variablesOptional: true
              };
            }
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let containsNaN = (ast) => {
          if (typeof ast === "number") {
            return Number.isNaN(ast);
          }
          if (!Array.isArray(ast)) {
            return false;
          }
          return ast.slice(1).some((v2) => containsNaN(v2));
        };
        if (dependencyValues.mathChildMathChildren?.length > 0) {
          let variables = dependencyValues.variables.map(
            (x2) => x2.subscripts_to_strings().tree
          );
          for (let mathGrandChild of dependencyValues.mathChildMathChildren) {
            if (mathGrandChild.stateValues.fReevaluate) {
              let inputVariables = mathGrandChild.stateValues.inputMaths.reduce(
                (a2, c2) => [
                  ...a2,
                  ...c2.subscripts_to_strings().variables()
                ],
                []
              );
              if (inputVariables.some(
                (invar) => variables.includes(invar)
              )) {
                if (containsNaN(
                  mathGrandChild.stateValues.value.tree
                )) {
                  return {
                    setValue: {
                      haveNaNChildToReevaluate: true
                    }
                  };
                }
              }
            }
          }
        } else if (dependencyValues.mathChild?.[0].stateValues.fReevaluate) {
          let variables = dependencyValues.variables.map(
            (x2) => x2.subscripts_to_strings().tree
          );
          let mathChild = dependencyValues.mathChild[0];
          let inputVariables = mathChild.stateValues.inputMaths.reduce(
            (a2, c2) => [
              ...a2,
              ...c2.subscripts_to_strings().variables()
            ],
            []
          );
          if (inputVariables.some(
            (invar) => variables.includes(invar)
          )) {
            if (containsNaN(mathChild.stateValues.value.tree)) {
              return {
                setValue: { haveNaNChildToReevaluate: true }
              };
            }
          }
        }
        return { setValue: { haveNaNChildToReevaluate: false } };
      }
    };
    stateVariableDefinitions.formula = {
      description: "The symbolic formula of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      defaultValue: me$3.fromAst(0),
      hasEssential: true,
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value", "formula"],
          variablesOptional: true
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["formula"]
        },
        isInterpolatedFunction: {
          dependencyType: "stateVariable",
          variableName: "isInterpolatedFunction"
        },
        haveNaNChildToReevaluate: {
          dependencyType: "stateVariable",
          variableName: "haveNaNChildToReevaluate"
        }
      }),
      definition: function({ dependencyValues, usedDefault }) {
        if (dependencyValues.isInterpolatedFunction) {
          return { setValue: { formula: me$3.fromAst("＿") } };
        } else if (dependencyValues.mathChild.length > 0) {
          if (dependencyValues.haveNaNChildToReevaluate) {
            return { setValue: { formula: me$3.fromAst("＿") } };
          } else {
            return {
              setValue: {
                formula: dependencyValues.mathChild[0].stateValues.value
              }
            };
          }
        } else if (dependencyValues.functionChild.length > 0 && !usedDefault.functionChild[0].formula) {
          return {
            setValue: {
              formula: dependencyValues.functionChild[0].stateValues.formula
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { formula: true }
          };
        }
      }
    };
    stateVariableDefinitions.numPrescribedPoints = {
      returnDependencies: () => ({
        through: {
          dependencyType: "attributeComponent",
          attributeName: "through",
          variableNames: ["numPoints"]
        }
      }),
      definition({ dependencyValues }) {
        let numPrescribedPoints = 0;
        if (dependencyValues.through !== null) {
          numPrescribedPoints = dependencyValues.through.stateValues.numPoints;
        }
        return {
          setValue: { numPrescribedPoints }
        };
      }
    };
    stateVariableDefinitions.prescribedPoints = {
      isArray: true,
      entryPrefixes: ["prescribedPoint"],
      returnArraySizeDependencies: () => ({
        numPrescribedPoints: {
          dependencyType: "stateVariable",
          variableName: "numPrescribedPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numPrescribedPoints];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let pointNum = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            through: {
              dependencyType: "attributeComponent",
              attributeName: "through",
              variableNames: ["point" + pointNum]
            },
            throughSlopes: {
              dependencyType: "attributeComponent",
              attributeName: "throughSlopes",
              variableNames: ["math" + pointNum]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let prescribedPoints = {};
        for (let arrayKey of arrayKeys) {
          let through = dependencyValuesByKey[arrayKey].through;
          if (through !== null) {
            let pointNum = Number(arrayKey) + 1;
            let point = through.stateValues["point" + pointNum];
            let slope = null;
            let throughSlopes = dependencyValuesByKey[arrayKey].throughSlopes;
            if (throughSlopes !== null) {
              slope = throughSlopes.stateValues["math" + pointNum];
              if (slope === void 0) {
                slope = null;
              }
            }
            prescribedPoints[arrayKey] = {
              x: point[0] || me$3.fromAst("＿"),
              y: point[1] || me$3.fromAst("＿"),
              slope
            };
          }
        }
        return { setValue: { prescribedPoints } };
      }
    };
    stateVariableDefinitions.prescribedMinima = {
      returnDependencies: () => ({
        minima: {
          dependencyType: "attributeComponent",
          attributeName: "minima",
          variableNames: ["extrema"]
        }
      }),
      definition({ dependencyValues }) {
        let prescribedMinima = [];
        if (dependencyValues.minima !== null) {
          prescribedMinima = dependencyValues.minima.stateValues.extrema.map(
            (v2) => ({
              x: v2[0],
              y: v2[1]
            })
          );
        }
        return {
          setValue: { prescribedMinima }
        };
      }
    };
    stateVariableDefinitions.prescribedMaxima = {
      returnDependencies: () => ({
        maxima: {
          dependencyType: "attributeComponent",
          attributeName: "maxima",
          variableNames: ["extrema"]
        }
      }),
      definition({ dependencyValues }) {
        let prescribedMaxima = [];
        if (dependencyValues.maxima !== null) {
          prescribedMaxima = dependencyValues.maxima.stateValues.extrema.map(
            (v2) => ({
              x: v2[0],
              y: v2[1]
            })
          );
        }
        return {
          setValue: { prescribedMaxima }
        };
      }
    };
    stateVariableDefinitions.prescribedExtrema = {
      returnDependencies: () => ({
        extrema: {
          dependencyType: "attributeComponent",
          attributeName: "extrema",
          variableNames: ["extrema"]
        }
      }),
      definition({ dependencyValues }) {
        let prescribedExtrema = [];
        if (dependencyValues.extrema !== null) {
          prescribedExtrema = dependencyValues.extrema.stateValues.extrema.map(
            (v2) => ({
              x: v2[0],
              y: v2[1]
            })
          );
        }
        return {
          setValue: { prescribedExtrema }
        };
      }
    };
    stateVariableDefinitions.interpolationPoints = {
      returnDependencies: () => ({
        xscale: {
          dependencyType: "stateVariable",
          variableName: "xscale"
        },
        yscale: {
          dependencyType: "stateVariable",
          variableName: "yscale"
        },
        prescribedPoints: {
          dependencyType: "stateVariable",
          variableName: "prescribedPoints"
        },
        prescribedMinima: {
          dependencyType: "stateVariable",
          variableName: "prescribedMinima"
        },
        prescribedMaxima: {
          dependencyType: "stateVariable",
          variableName: "prescribedMaxima"
        },
        prescribedExtrema: {
          dependencyType: "stateVariable",
          variableName: "prescribedExtrema"
        }
      }),
      definition: ({ dependencyValues }) => calculateInterpolationPoints({ dependencyValues, numerics })
    };
    stateVariableDefinitions.xs = {
      additionalStateVariablesDefined: ["coeffs"],
      returnDependencies: () => ({
        interpolationPoints: {
          dependencyType: "stateVariable",
          variableName: "interpolationPoints"
        }
      }),
      definition: computeSplineParamCoeffs
    };
    stateVariableDefinitions.symbolicfs = {
      isArray: true,
      entryPrefixes: ["symbolicf"],
      stateVariablesDeterminingDependencies: [
        "isInterpolatedFunction",
        "mathChildName",
        "mathChildCreatedBySugar"
      ],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey({ stateValues }) {
        if (stateValues.isInterpolatedFunction) {
          return {
            globalDependencies: {
              xs: {
                dependencyType: "stateVariable",
                variableName: "xs"
              },
              coeffs: {
                dependencyType: "stateVariable",
                variableName: "coeffs"
              },
              interpolationPoints: {
                dependencyType: "stateVariable",
                variableName: "interpolationPoints"
              },
              isInterpolatedFunction: {
                dependencyType: "stateVariable",
                variableName: "isInterpolatedFunction"
              },
              domain: {
                dependencyType: "stateVariable",
                variableName: "domain"
              }
            }
          };
        } else {
          let globalDependencies = {
            formula: {
              dependencyType: "stateVariable",
              variableName: "formula"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            },
            numInputs: {
              dependencyType: "stateVariable",
              variableName: "numInputs"
            },
            functionChild: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: ["symbolicfs"]
            },
            simplify: {
              dependencyType: "stateVariable",
              variableName: "simplify"
            },
            expand: {
              dependencyType: "stateVariable",
              variableName: "expand"
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            symbolicfShadow: {
              dependencyType: "stateVariable",
              variableName: "symbolicfShadow"
            },
            numericalfShadow: {
              dependencyType: "stateVariable",
              variableName: "numericalfShadow"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            }
          };
          if (stateValues.mathChildName) {
            if (stateValues.mathChildCreatedBySugar) {
              globalDependencies.mathChildExpressionWithCodes = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "expressionWithCodes"
              };
              globalDependencies.mathChildMathChildren = {
                dependencyType: "child",
                parentIdx: stateValues.mathChildName,
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluate",
                  "inputMaths"
                ],
                variablesOptional: true
              };
              globalDependencies.mathChildCodePre = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "codePre"
              };
            } else {
              globalDependencies.mathChild = {
                dependencyType: "child",
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluate",
                  "inputMaths"
                ],
                variablesOptional: true
              };
            }
          }
          return { globalDependencies };
        }
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        usedDefault,
        arrayKeys
      }) {
        if (globalDependencyValues.isInterpolatedFunction) {
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              let numericalf = returnInterpolatedFunction({
                xs: globalDependencyValues.xs,
                coeffs: globalDependencyValues.coeffs,
                interpolationPoints: globalDependencyValues.interpolationPoints,
                domain: globalDependencyValues.domain
              });
              symbolicfs[arrayKey] = function(x2) {
                let val = x2.evaluate_to_constant();
                return me$3.fromAst(numericalf(val));
              };
            } else {
              symbolicfs[arrayKey] = (x2) => me$3.fromAst("＿");
            }
          }
          return {
            setValue: { symbolicfs }
          };
        } else if (globalDependencyValues.functionChild.length > 0) {
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            symbolicfs[arrayKey] = globalDependencyValues.functionChild[0].stateValues.symbolicfs[arrayKey];
          }
          return {
            setValue: { symbolicfs }
          };
        } else if (!usedDefault.formula) {
          if (globalDependencyValues.mathChildMathChildren?.length > 0) {
            let variables = globalDependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let evaluateChildrenToReevaluate = {};
            let needToReevaluate = false;
            let codePre = globalDependencyValues.mathChildCodePre;
            let subsMapping = {};
            for (let [
              ind,
              mathGrandChild
            ] of globalDependencyValues.mathChildMathChildren.entries()) {
              if (mathGrandChild.stateValues.fReevaluate) {
                let inputVariables = mathGrandChild.stateValues.inputMaths.reduce(
                  (a2, c2) => [
                    ...a2,
                    ...c2.subscripts_to_strings().variables()
                  ],
                  []
                );
                if (inputVariables.some(
                  (invar) => variables.includes(invar)
                )) {
                  evaluateChildrenToReevaluate[codePre + ind] = {
                    fReevaluate: mathGrandChild.stateValues.fReevaluate,
                    inputMathFs: mathGrandChild.stateValues.inputMaths.map(
                      (x2) => {
                        const x_transformed = x2.subscripts_to_strings();
                        return (args) => x_transformed.substitute(args).strings_to_subscripts();
                      }
                    )
                  };
                  needToReevaluate = true;
                } else {
                  subsMapping[codePre + ind] = mathGrandChild.stateValues.value;
                }
              } else {
                subsMapping[codePre + ind] = mathGrandChild.stateValues.value;
              }
            }
            if (needToReevaluate) {
              let formulaExpressionWithCodes = globalDependencyValues.mathChildExpressionWithCodes;
              if (Object.keys(subsMapping).length > 0) {
                formulaExpressionWithCodes = formulaExpressionWithCodes.substitute(
                  subsMapping
                );
              }
              formulaExpressionWithCodes = me$3.fromAst(
                mergeListsWithOtherContainers(
                  formulaExpressionWithCodes.tree
                )
              );
              let symbolicfs2 = {};
              for (let arrayKey of arrayKeys) {
                symbolicfs2[arrayKey] = returnSymbolicFunctionFromReevaluatedFormula(
                  {
                    formulaExpressionWithCodes,
                    evaluateChildrenToReevaluate,
                    simplify: globalDependencyValues.simplify,
                    expand: globalDependencyValues.expand,
                    numInputs: globalDependencyValues.numInputs,
                    variables: globalDependencyValues.variables,
                    domain: globalDependencyValues.domain,
                    component: arrayKey
                  }
                );
              }
              return {
                setValue: { symbolicfs: symbolicfs2 }
              };
            }
          } else if (globalDependencyValues.mathChild?.[0].stateValues.fReevaluate) {
            let variables = globalDependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let mathChild = globalDependencyValues.mathChild[0];
            let inputVariables = mathChild.stateValues.inputMaths.reduce(
              (a2, c2) => [
                ...a2,
                ...c2.subscripts_to_strings().variables()
              ],
              []
            );
            if (inputVariables.some(
              (invar) => variables.includes(invar)
            )) {
              let formulaExpressionWithCodes = me$3.fromAst("code");
              let evaluateChildrenToReevaluate = {
                code: {
                  fReevaluate: mathChild.stateValues.fReevaluate,
                  inputMathFs: mathChild.stateValues.inputMaths.map(
                    (x2) => {
                      const x_transformed = x2.subscripts_to_strings();
                      return (args) => x_transformed.substitute(args).strings_to_subscripts();
                    }
                  )
                }
              };
              let symbolicfs2 = {};
              for (let arrayKey of arrayKeys) {
                symbolicfs2[arrayKey] = returnSymbolicFunctionFromReevaluatedFormula(
                  {
                    formulaExpressionWithCodes,
                    evaluateChildrenToReevaluate,
                    simplify: globalDependencyValues.simplify,
                    expand: globalDependencyValues.expand,
                    numInputs: globalDependencyValues.numInputs,
                    variables: globalDependencyValues.variables,
                    domain: globalDependencyValues.domain,
                    component: arrayKey
                  }
                );
              }
              return {
                setValue: { symbolicfs: symbolicfs2 }
              };
            }
          }
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            symbolicfs[arrayKey] = returnSymbolicFunctionFromFormula({
              formula: globalDependencyValues.formula,
              simplify: globalDependencyValues.simplify,
              expand: globalDependencyValues.expand,
              numInputs: globalDependencyValues.numInputs,
              variables: globalDependencyValues.variables,
              domain: globalDependencyValues.domain,
              component: arrayKey
            });
          }
          return {
            setValue: { symbolicfs }
          };
        } else if (globalDependencyValues.symbolicfShadow) {
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              symbolicfs[arrayKey] = globalDependencyValues.symbolicfShadow;
            } else {
              symbolicfs[arrayKey] = (x2) => me$3.fromAst("＿");
            }
          }
          return {
            setValue: { symbolicfs }
          };
        } else if (globalDependencyValues.numericalfShadow) {
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              symbolicfs[arrayKey] = function(x2) {
                let input = x2.evaluate_to_constant();
                return me$3.fromAst(
                  globalDependencyValues.numericalfShadow(
                    input
                  )
                );
              };
            } else {
              symbolicfs[arrayKey] = (x2) => me$3.fromAst("＿");
            }
          }
          return {
            setValue: { symbolicfs }
          };
        } else {
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            symbolicfs[arrayKey] = returnSymbolicFunctionFromFormula({
              formula: globalDependencyValues.formula,
              simplify: globalDependencyValues.simplify,
              expand: globalDependencyValues.expand,
              numInputs: globalDependencyValues.numInputs,
              variables: globalDependencyValues.variables,
              domain: globalDependencyValues.domain,
              component: arrayKey
            });
          }
          return {
            setValue: { symbolicfs }
          };
        }
      }
    };
    stateVariableDefinitions.symbolicf = {
      isAlias: true,
      targetVariableName: "symbolicf1",
      description: "The function's first symbolic output function."
    };
    stateVariableDefinitions.numericalfs = {
      isArray: true,
      entryPrefixes: ["numericalf"],
      stateVariablesDeterminingDependencies: [
        "isInterpolatedFunction",
        "mathChildName",
        "mathChildCreatedBySugar"
      ],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey({ stateValues }) {
        if (stateValues.isInterpolatedFunction) {
          return {
            globalDependencies: {
              xs: {
                dependencyType: "stateVariable",
                variableName: "xs"
              },
              coeffs: {
                dependencyType: "stateVariable",
                variableName: "coeffs"
              },
              interpolationPoints: {
                dependencyType: "stateVariable",
                variableName: "interpolationPoints"
              },
              isInterpolatedFunction: {
                dependencyType: "stateVariable",
                variableName: "isInterpolatedFunction"
              },
              domain: {
                dependencyType: "stateVariable",
                variableName: "domain"
              }
            }
          };
        } else {
          let globalDependencies = {
            formula: {
              dependencyType: "stateVariable",
              variableName: "formula"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            },
            numInputs: {
              dependencyType: "stateVariable",
              variableName: "numInputs"
            },
            functionChild: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: ["numericalfs"]
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            symbolicfShadow: {
              dependencyType: "stateVariable",
              variableName: "symbolicfShadow"
            },
            numericalfShadow: {
              dependencyType: "stateVariable",
              variableName: "numericalfShadow"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            }
          };
          if (stateValues.mathChildName) {
            if (stateValues.mathChildCreatedBySugar) {
              globalDependencies.mathChildExpressionWithCodes = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "expressionWithCodes"
              };
              globalDependencies.mathChildMathChildren = {
                dependencyType: "child",
                parentIdx: stateValues.mathChildName,
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluate",
                  "inputMaths"
                ],
                variablesOptional: true
              };
              globalDependencies.mathChildCodePre = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "codePre"
              };
            } else {
              globalDependencies.mathChild = {
                dependencyType: "child",
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluate",
                  "inputMaths"
                ],
                variablesOptional: true
              };
            }
          }
          return { globalDependencies };
        }
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        usedDefault,
        arrayKeys
      }) {
        if (globalDependencyValues.isInterpolatedFunction) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              numericalfs[arrayKey] = returnInterpolatedFunction({
                xs: globalDependencyValues.xs,
                coeffs: globalDependencyValues.coeffs,
                interpolationPoints: globalDependencyValues.interpolationPoints,
                domain: globalDependencyValues.domain
              });
            } else {
              numericalfs[arrayKey] = (x2) => me$3.fromAst("＿");
            }
          }
          return {
            setValue: { numericalfs }
          };
        } else if (globalDependencyValues.functionChild.length > 0) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            numericalfs[arrayKey] = globalDependencyValues.functionChild[0].stateValues.numericalfs[arrayKey];
          }
          return {
            setValue: { numericalfs }
          };
        } else if (!usedDefault.formula) {
          if (globalDependencyValues.mathChildMathChildren?.length > 0) {
            let variables = globalDependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let evaluateChildrenToReevaluate = {};
            let needToReevaluate = false;
            let codePre = globalDependencyValues.mathChildCodePre;
            let subsMapping = {};
            for (let [
              ind,
              mathGrandChild
            ] of globalDependencyValues.mathChildMathChildren.entries()) {
              if (mathGrandChild.stateValues.fReevaluate) {
                let inputVariables = mathGrandChild.stateValues.inputMaths.reduce(
                  (a2, c2) => [
                    ...a2,
                    ...c2.subscripts_to_strings().variables()
                  ],
                  []
                );
                if (inputVariables.some(
                  (invar) => variables.includes(invar)
                )) {
                  evaluateChildrenToReevaluate[codePre + ind] = {
                    fReevaluate: mathGrandChild.stateValues.fReevaluate,
                    inputMathFs: mathGrandChild.stateValues.inputMaths.map(
                      (x2) => x2.subscripts_to_strings().f()
                    )
                  };
                  needToReevaluate = true;
                } else {
                  subsMapping[codePre + ind] = mathGrandChild.stateValues.value;
                }
              } else {
                subsMapping[codePre + ind] = mathGrandChild.stateValues.value;
              }
            }
            if (needToReevaluate) {
              let formulaExpressionWithCodes = globalDependencyValues.mathChildExpressionWithCodes;
              if (Object.keys(subsMapping).length > 0) {
                formulaExpressionWithCodes = formulaExpressionWithCodes.substitute(
                  subsMapping
                );
              }
              formulaExpressionWithCodes = me$3.fromAst(
                mergeListsWithOtherContainers(
                  formulaExpressionWithCodes.tree
                )
              );
              let numericalfs2 = {};
              for (let arrayKey of arrayKeys) {
                numericalfs2[arrayKey] = returnNumericalFunctionFromReevaluatedFormula(
                  {
                    formulaExpressionWithCodes,
                    evaluateChildrenToReevaluate,
                    numInputs: globalDependencyValues.numInputs,
                    variables: globalDependencyValues.variables,
                    domain: globalDependencyValues.domain,
                    component: arrayKey
                  }
                );
              }
              return {
                setValue: { numericalfs: numericalfs2 }
              };
            }
          } else if (globalDependencyValues.mathChild?.[0].stateValues.fReevaluate) {
            let variables = globalDependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let mathChild = globalDependencyValues.mathChild[0];
            let inputVariables = mathChild.stateValues.inputMaths.reduce(
              (a2, c2) => [
                ...a2,
                ...c2.subscripts_to_strings().variables()
              ],
              []
            );
            if (inputVariables.some(
              (invar) => variables.includes(invar)
            )) {
              let formulaExpressionWithCodes = me$3.fromAst("code");
              let evaluateChildrenToReevaluate = {
                code: {
                  fReevaluate: mathChild.stateValues.fReevaluate,
                  inputMathFs: mathChild.stateValues.inputMaths.map(
                    (x2) => x2.subscripts_to_strings().f()
                  )
                }
              };
              let numericalfs2 = {};
              for (let arrayKey of arrayKeys) {
                numericalfs2[arrayKey] = returnNumericalFunctionFromReevaluatedFormula(
                  {
                    formulaExpressionWithCodes,
                    evaluateChildrenToReevaluate,
                    numInputs: globalDependencyValues.numInputs,
                    variables: globalDependencyValues.variables,
                    domain: globalDependencyValues.domain,
                    component: arrayKey
                  }
                );
              }
              return {
                setValue: { numericalfs: numericalfs2 }
              };
            }
          }
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            numericalfs[arrayKey] = returnNumericalFunctionFromFormula({
              formula: globalDependencyValues.formula,
              numInputs: globalDependencyValues.numInputs,
              variables: globalDependencyValues.variables,
              domain: globalDependencyValues.domain,
              component: arrayKey
            });
          }
          return {
            setValue: { numericalfs }
          };
        } else if (globalDependencyValues.numericalfShadow) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              numericalfs[arrayKey] = globalDependencyValues.numericalfShadow;
            } else {
              numericalfs[arrayKey] = () => NaN;
            }
          }
          return {
            setValue: { numericalfs }
          };
        } else if (globalDependencyValues.symbolicfShadow) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              numericalfs[arrayKey] = function(x2) {
                let val = globalDependencyValues.symbolicfShadow(me$3.fromAst(x2)).evaluate_to_constant();
                return val;
              };
            } else {
              numericalfs[arrayKey] = () => NaN;
            }
          }
          return {
            setValue: { numericalfs }
          };
        } else {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            numericalfs[arrayKey] = returnNumericalFunctionFromFormula({
              formula: globalDependencyValues.formula,
              numInputs: globalDependencyValues.numInputs,
              variables: globalDependencyValues.variables,
              domain: globalDependencyValues.domain,
              component: arrayKey
            });
          }
          return {
            setValue: { numericalfs }
          };
        }
      }
    };
    stateVariableDefinitions.numericalf = {
      returnDependencies: () => ({
        numericalf1: {
          dependencyType: "stateVariable",
          variableName: "numericalf1"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { numericalf: dependencyValues.numericalf1 }
        };
      }
    };
    stateVariableDefinitions.fDefinitions = {
      isArray: true,
      entryPrefixes: ["fDefinition"],
      stateVariablesDeterminingDependencies: [
        "isInterpolatedFunction",
        "mathChildName",
        "mathChildCreatedBySugar"
      ],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey({ stateValues }) {
        if (stateValues.isInterpolatedFunction) {
          return {
            globalDependencies: {
              xs: {
                dependencyType: "stateVariable",
                variableName: "xs"
              },
              coeffs: {
                dependencyType: "stateVariable",
                variableName: "coeffs"
              },
              interpolationPoints: {
                dependencyType: "stateVariable",
                variableName: "interpolationPoints"
              },
              isInterpolatedFunction: {
                dependencyType: "stateVariable",
                variableName: "isInterpolatedFunction"
              },
              domain: {
                dependencyType: "stateVariable",
                variableName: "domain"
              }
            }
          };
        } else {
          let globalDependencies = {
            formula: {
              dependencyType: "stateVariable",
              variableName: "formula"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            },
            numInputs: {
              dependencyType: "stateVariable",
              variableName: "numInputs"
            },
            functionChild: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: ["fDefinitions"]
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            symbolicfShadow: {
              dependencyType: "stateVariable",
              variableName: "symbolicfShadow"
            },
            numericalfShadow: {
              dependencyType: "stateVariable",
              variableName: "numericalfShadow"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            }
          };
          if (stateValues.mathChildName) {
            if (stateValues.mathChildCreatedBySugar) {
              globalDependencies.mathChildExpressionWithCodes = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "expressionWithCodes"
              };
              globalDependencies.mathChildMathChildren = {
                dependencyType: "child",
                parentIdx: stateValues.mathChildName,
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluateDefinition",
                  "inputMaths"
                ],
                variablesOptional: true
              };
              globalDependencies.mathChildCodePre = {
                dependencyType: "stateVariable",
                componentIdx: stateValues.mathChildName,
                variableName: "codePre"
              };
            } else {
              globalDependencies.mathChild = {
                dependencyType: "child",
                childGroups: ["maths"],
                variableNames: [
                  "value",
                  "fReevaluateDefinition",
                  "inputMaths"
                ],
                variablesOptional: true
              };
            }
          }
          return { globalDependencies };
        }
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        usedDefault,
        arrayKeys
      }) {
        if (globalDependencyValues.isInterpolatedFunction) {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              fDefinitions[arrayKey] = {
                functionType: "interpolated",
                xs: globalDependencyValues.xs,
                coeffs: globalDependencyValues.coeffs,
                interpolationPoints: globalDependencyValues.interpolationPoints,
                domain: globalDependencyValues.domain ? globalDependencyValues.domain.map(
                  (x2) => x2.tree
                ) : null
              };
            } else {
              fDefinitions[arrayKey] = {};
            }
          }
          return {
            setValue: { fDefinitions }
          };
        } else if (globalDependencyValues.functionChild.length > 0) {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = globalDependencyValues.functionChild[0].stateValues.fDefinitions[arrayKey];
          }
          return {
            setValue: { fDefinitions }
          };
        } else if (!usedDefault.formula) {
          if (globalDependencyValues.mathChildMathChildren?.length > 0) {
            let variables = globalDependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let evaluateChildrenToReevaluate = {};
            let needToReevaluate = false;
            let codePre = globalDependencyValues.mathChildCodePre;
            let subsMapping = {};
            for (let [
              ind,
              mathGrandChild
            ] of globalDependencyValues.mathChildMathChildren.entries()) {
              if (mathGrandChild.stateValues.fReevaluateDefinition) {
                let inputVariables = mathGrandChild.stateValues.inputMaths.reduce(
                  (a2, c2) => [
                    ...a2,
                    ...c2.subscripts_to_strings().variables()
                  ],
                  []
                );
                if (inputVariables.some(
                  (invar) => variables.includes(invar)
                )) {
                  evaluateChildrenToReevaluate[codePre + ind] = {
                    fReevaluateDefinition: mathGrandChild.stateValues.fReevaluateDefinition,
                    inputMaths: mathGrandChild.stateValues.inputMaths
                  };
                  needToReevaluate = true;
                } else {
                  subsMapping[codePre + ind] = mathGrandChild.stateValues.value;
                }
              } else {
                subsMapping[codePre + ind] = mathGrandChild.stateValues.value;
              }
            }
            if (needToReevaluate) {
              let formulaExpressionWithCodes = globalDependencyValues.mathChildExpressionWithCodes;
              if (Object.keys(subsMapping).length > 0) {
                formulaExpressionWithCodes = formulaExpressionWithCodes.substitute(
                  subsMapping
                );
              }
              formulaExpressionWithCodes = me$3.fromAst(
                mergeListsWithOtherContainers(
                  formulaExpressionWithCodes.tree
                )
              );
              let fDefinitions2 = {};
              for (let arrayKey of arrayKeys) {
                fDefinitions2[arrayKey] = {
                  functionType: "reevaluatedFormula",
                  formulaExpressionWithCodes,
                  evaluateChildrenToReevaluate,
                  numInputs: globalDependencyValues.numInputs,
                  variables: globalDependencyValues.variables.map(
                    (x2) => x2.tree
                  ),
                  domain: globalDependencyValues.domain ? globalDependencyValues.domain.map(
                    (x2) => x2.tree
                  ) : null,
                  component: arrayKey
                };
              }
              return {
                setValue: { fDefinitions: fDefinitions2 }
              };
            }
          } else if (globalDependencyValues.mathChild?.[0].stateValues.fReevaluateDefinition) {
            let variables = globalDependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let mathChild = globalDependencyValues.mathChild[0];
            let inputVariables = mathChild.stateValues.inputMaths.reduce(
              (a2, c2) => [
                ...a2,
                ...c2.subscripts_to_strings().variables()
              ],
              []
            );
            if (inputVariables.some(
              (invar) => variables.includes(invar)
            )) {
              let formulaExpressionWithCodes = me$3.fromAst("code");
              let evaluateChildrenToReevaluate = {
                code: {
                  fReevaluateDefinition: mathChild.stateValues.fReevaluateDefinition,
                  inputMaths: mathChild.stateValues.inputMaths
                }
              };
              let fDefinitions2 = {};
              for (let arrayKey of arrayKeys) {
                fDefinitions2[arrayKey] = {
                  functionType: "reevaluatedFormula",
                  formulaExpressionWithCodes,
                  evaluateChildrenToReevaluate,
                  numInputs: globalDependencyValues.numInputs,
                  variables: globalDependencyValues.variables.map(
                    (x2) => x2.tree
                  ),
                  domain: globalDependencyValues.domain ? globalDependencyValues.domain.map(
                    (x2) => x2.tree
                  ) : null,
                  component: arrayKey
                };
              }
              return {
                setValue: { fDefinitions: fDefinitions2 }
              };
            }
          }
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = {
              functionType: "formula",
              formula: globalDependencyValues.formula.tree,
              variables: globalDependencyValues.variables.map(
                (x2) => x2.tree
              ),
              numInputs: globalDependencyValues.numInputs,
              numOutputs: globalDependencyValues.numOutputs,
              domain: globalDependencyValues.domain ? globalDependencyValues.domain.map(
                (x2) => x2.tree
              ) : null,
              component: arrayKey
            };
          }
          return {
            setValue: { fDefinitions }
          };
        } else if (globalDependencyValues.numericalfShadow) {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = {};
          }
          return {
            setValue: { fDefinitions }
          };
        } else if (globalDependencyValues.symbolicfShadow) {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = {};
          }
          return {
            setValue: { fDefinitions }
          };
        } else {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = {
              functionType: "formula",
              formula: globalDependencyValues.formula.tree,
              variables: globalDependencyValues.variables.map(
                (x2) => x2.tree
              ),
              numInputs: globalDependencyValues.numInputs,
              numOutputs: globalDependencyValues.numOutputs,
              domain: globalDependencyValues.domain ? globalDependencyValues.domain.map(
                (x2) => x2.tree
              ) : null,
              component: arrayKey
            };
          }
          return {
            setValue: { fDefinitions }
          };
        }
      }
    };
    stateVariableDefinitions.fDefinition = {
      isAlias: true,
      targetVariableName: "fDefinition1",
      description: "Renderer-side definition of the function's first output function."
    };
    stateVariableDefinitions.fs = {
      isArray: true,
      entryPrefixes: ["f"],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          symbolic: {
            dependencyType: "stateVariable",
            variableName: "symbolic"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            symbolicf: {
              dependencyType: "stateVariable",
              variableName: "symbolicf" + varEnding
            },
            numericalf: {
              dependencyType: "stateVariable",
              variableName: "numericalf" + varEnding
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let fs = {};
        if (globalDependencyValues.symbolic) {
          for (let arrayKey of arrayKeys) {
            fs[arrayKey] = dependencyValuesByKey[arrayKey].symbolicf;
          }
        } else {
          for (let arrayKey of arrayKeys) {
            fs[arrayKey] = dependencyValuesByKey[arrayKey].numericalf;
          }
        }
        return { setValue: { fs } };
      }
    };
    stateVariableDefinitions.f = {
      isAlias: true,
      targetVariableName: "f1",
      description: "The function's first output function."
    };
    stateVariableDefinitions.latex = {
      description: "The function rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["latex"]
        },
        formula: {
          dependencyType: "stateVariable",
          variableName: "formula"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.functionChild?.[0]?.componentType === "piecewiseFunction") {
          return {
            setValue: {
              latex: dependencyValues.functionChild[0].stateValues.latex
            }
          };
        }
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let latex = roundForDisplay({
          value: dependencyValues.formula,
          dependencyValues
        }).toLatex(params);
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.text = {
      description: "The function rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["text"]
        },
        formula: {
          dependencyType: "stateVariable",
          variableName: "formula"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.functionChild?.[0]?.componentType === "piecewiseFunction") {
          return {
            setValue: {
              text: dependencyValues.functionChild[0].stateValues.text
            }
          };
        }
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let text = roundForDisplay({
          value: dependencyValues.formula,
          dependencyValues
        }).toString(params);
        return { setValue: { text: superSubscriptsToUnicode(text) } };
      }
    };
    stateVariableDefinitions.functionChildrenInfoToCalculateExtrema = {
      returnDependencies: () => ({
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: [
            "domain",
            "xscale",
            "isInterpolatedFunction",
            "xs",
            "coeffs",
            "numericalf",
            "formula",
            "variables",
            "functionChildrenInfoToCalculateExtrema",
            "numInputs",
            "numOutputs",
            "isPiecewiseFunction",
            "numericalDomainsOfChildren"
          ],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            functionChildrenInfoToCalculateExtrema: dependencyValues.functionChildren
          }
        };
      }
    };
    stateVariableDefinitions.allMinima = {
      stateVariablesDeterminingDependencies: ["isInterpolatedFunction"],
      additionalStateVariablesDefined: [
        "globalMinimumOption",
        "globalInfimumOption"
      ],
      returnDependencies({ stateValues }) {
        if (stateValues.isInterpolatedFunction) {
          return {
            xs: {
              dependencyType: "stateVariable",
              variableName: "xs"
            },
            coeffs: {
              dependencyType: "stateVariable",
              variableName: "coeffs"
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            },
            numericalf: {
              dependencyType: "stateVariable",
              variableName: "numericalf"
            }
          };
        } else {
          return {
            numericalf: {
              dependencyType: "stateVariable",
              variableName: "numericalf"
            },
            formula: {
              dependencyType: "stateVariable",
              variableName: "formula"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            },
            xscale: {
              dependencyType: "stateVariable",
              variableName: "xscale"
            },
            functionChildrenInfoToCalculateExtrema: {
              dependencyType: "stateVariable",
              variableName: "functionChildrenInfoToCalculateExtrema"
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            numInputs: {
              dependencyType: "stateVariable",
              variableName: "numInputs"
            },
            numOutputs: {
              dependencyType: "stateVariable",
              variableName: "numOutputs"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            }
          };
        }
      },
      definition: function({ dependencyValues }) {
        let { localMinima, globalMinimum, globalInfimum } = find_local_global_minima({
          domain: dependencyValues.domain,
          xscale: dependencyValues.xscale,
          isInterpolatedFunction: dependencyValues.isInterpolatedFunction,
          xs: dependencyValues.xs,
          coeffs: dependencyValues.coeffs,
          numericalf: dependencyValues.numericalf,
          formula: dependencyValues.formula,
          variables: dependencyValues.variables,
          functionChildrenInfoToCalculateExtrema: dependencyValues.functionChildrenInfoToCalculateExtrema,
          numInputs: dependencyValues.numInputs,
          numOutputs: dependencyValues.numOutputs,
          numerics
        });
        return {
          setValue: {
            allMinima: localMinima,
            globalMinimumOption: globalMinimum,
            globalInfimumOption: globalInfimum
          }
        };
      }
    };
    stateVariableDefinitions.numMinima = {
      description: "Number of local minima of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        allMinima: {
          dependencyType: "stateVariable",
          variableName: "allMinima"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { numMinima: dependencyValues.allMinima.length },
          checkForActualChange: { numMinima: true }
        };
      }
    };
    stateVariableDefinitions.minima = {
      description: "Local minima of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        attributesToShadow: ["styleNumber"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "minimum" || prefix === void 0) {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          } else {
            return [];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: [
        "minimum",
        "minimumLocations",
        "minimumLocation",
        "minimumValues",
        "minimumValue"
      ],
      schemaSubarrays: {
        minimumLocations: {
          numDimensions: 1,
          description: "The x-coordinates of the function's local minima."
        },
        minimumValues: {
          numDimensions: 1,
          description: "The values of the function's local minima."
        }
      },
      returnEntryDimensions: (prefix) => {
        if (["minimumLocation", "minimumValue"].includes(prefix)) {
          return 0;
        } else {
          return 1;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (["minimum", "minimumLocation", "minimumValue"].includes(
          arrayEntryPrefix
        )) {
          let pointInd = Number(varEnding) - 1;
          if (Number.isInteger(pointInd) && pointInd >= 0) {
            if (!arraySize || pointInd < arraySize[0]) {
              if (arrayEntryPrefix === "minimum") {
                return [pointInd + ",0", pointInd + ",1"];
              } else if (arrayEntryPrefix === "minimumLocation") {
                return [pointInd + ",0"];
              } else {
                return [pointInd + ",1"];
              }
            } else {
              return [];
            }
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "minimumLocations") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,0"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",0");
        } else if (arrayEntryPrefix === "minimumValues") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,1"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",1");
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        let [ind1, ind2] = arrayKey.split(",");
        if (ind2 === "0") {
          return "minimumLocation" + (Number(ind1) + 1);
        } else {
          return "minimumValue" + (Number(ind1) + 1);
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "minima") {
          if (propIndex.length === 1) {
            return "minimum" + propIndex[0];
          } else {
            let componentNum = Number(propIndex[0]);
            if (Number.isInteger(componentNum) && componentNum > 0) {
              if (propIndex[1] === 1) {
                return "minimumLocation" + componentNum;
              } else if (propIndex[1] === 2) {
                return "minimumValue" + componentNum;
              }
            }
            return null;
          }
        }
        if (varName === "minimumLocations") {
          return "minimumLocation" + propIndex[0];
        }
        if (varName === "minimumValues") {
          return "minimumValue" + propIndex[0];
        }
        if (varName.slice(0, 7) === "minimum") {
          let componentNum = Number(varName.slice(7));
          if (Number.isInteger(componentNum) && componentNum > 0) {
            if (propIndex[0] === 1) {
              return "minimumLocation" + componentNum;
            } else if (propIndex[0] === 2) {
              return "minimumValue" + componentNum;
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numMinima: {
          dependencyType: "stateVariable",
          variableName: "numMinima"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numMinima, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allMinima: {
            dependencyType: "stateVariable",
            variableName: "allMinima"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let minima = {};
        for (let ptInd = 0; ptInd < globalDependencyValues.__array_size[0]; ptInd++) {
          for (let i2 = 0; i2 < 2; i2++) {
            let arrayKey = `${ptInd},${i2}`;
            minima[arrayKey] = globalDependencyValues.allMinima[ptInd][i2];
          }
        }
        return { setValue: { minima } };
      }
    };
    stateVariableDefinitions.globalMinimum = {
      description: "The global minimum of the function (if attained).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        attributesToShadow: ["styleNumber"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === void 0) {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          } else {
            return [];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["globalMinimumLocation", "globalMinimumValue"],
      getArrayKeysFromVarName({ arrayEntryPrefix }) {
        if (arrayEntryPrefix === "globalMinimumLocation") {
          return ["0"];
        } else if (arrayEntryPrefix === "globalMinimumValue") {
          return ["1"];
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        if (arrayKey === "0") {
          return "globalMinimumLocation";
        } else if (arrayKey === "1") {
          return "globalMinimumValue";
        } else {
          return "invalid";
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "globalMinimum") {
          if (propIndex.length === 1) {
            if (propIndex[0] === 1) {
              return "globalMinimumLocation";
            } else if (propIndex[0] === 2) {
              return "globalMinimumValue";
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        globalMinimumOption: {
          dependencyType: "stateVariable",
          variableName: "globalMinimumOption"
        }
      }),
      returnArraySize({ dependencyValues }) {
        if (dependencyValues.globalMinimumOption) {
          return [2];
        } else {
          return [0];
        }
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            globalMinimumOption: {
              dependencyType: "stateVariable",
              variableName: "globalMinimumOption"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        return {
          setValue: {
            globalMinimum: globalDependencyValues.globalMinimumOption
          }
        };
      }
    };
    stateVariableDefinitions.globalInfimum = {
      description: "The global infimum of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        attributesToShadow: ["styleNumber"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === void 0) {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          } else {
            return [];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["globalInfimumLocation", "globalInfimumValue"],
      getArrayKeysFromVarName({ arrayEntryPrefix }) {
        if (arrayEntryPrefix === "globalInfimumLocation") {
          return ["0"];
        } else if (arrayEntryPrefix === "globalInfimumValue") {
          return ["1"];
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        if (arrayKey === "0") {
          return "globalInfimumLocation";
        } else if (arrayKey === "1") {
          return "globalInfimumValue";
        } else {
          return "invalid";
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "globalInfimum") {
          if (propIndex.length === 1) {
            if (propIndex[0] === 1) {
              return "globalInfimumLocation";
            } else if (propIndex[0] === 2) {
              return "globalInfimumValue";
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        globalInfimumOption: {
          dependencyType: "stateVariable",
          variableName: "globalInfimumOption"
        }
      }),
      returnArraySize({ dependencyValues }) {
        if (dependencyValues.globalInfimumOption) {
          return [2];
        } else {
          return [0];
        }
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            globalInfimumOption: {
              dependencyType: "stateVariable",
              variableName: "globalInfimumOption"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        return {
          setValue: {
            globalInfimum: globalDependencyValues.globalInfimumOption
          }
        };
      }
    };
    stateVariableDefinitions.allMaxima = {
      stateVariablesDeterminingDependencies: ["isInterpolatedFunction"],
      additionalStateVariablesDefined: [
        "globalMaximumOption",
        "globalSupremumOption"
      ],
      returnDependencies({ stateValues }) {
        if (stateValues.isInterpolatedFunction) {
          return {
            xs: {
              dependencyType: "stateVariable",
              variableName: "xs"
            },
            coeffs: {
              dependencyType: "stateVariable",
              variableName: "coeffs"
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            },
            numericalf: {
              dependencyType: "stateVariable",
              variableName: "numericalf"
            }
          };
        } else {
          return {
            numericalf: {
              dependencyType: "stateVariable",
              variableName: "numericalf"
            },
            formula: {
              dependencyType: "stateVariable",
              variableName: "formula"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            },
            xscale: {
              dependencyType: "stateVariable",
              variableName: "xscale"
            },
            functionChildrenInfoToCalculateExtrema: {
              dependencyType: "stateVariable",
              variableName: "functionChildrenInfoToCalculateExtrema"
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            numInputs: {
              dependencyType: "stateVariable",
              variableName: "numInputs"
            },
            numOutputs: {
              dependencyType: "stateVariable",
              variableName: "numOutputs"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            }
          };
        }
      },
      definition: function({ dependencyValues }) {
        let { localMaxima, globalMaximum, globalSupremum } = find_local_global_maxima({
          domain: dependencyValues.domain,
          xscale: dependencyValues.xscale,
          isInterpolatedFunction: dependencyValues.isInterpolatedFunction,
          xs: dependencyValues.xs,
          coeffs: dependencyValues.coeffs,
          numericalf: dependencyValues.numericalf,
          formula: dependencyValues.formula,
          variables: dependencyValues.variables,
          functionChildrenInfoToCalculateExtrema: dependencyValues.functionChildrenInfoToCalculateExtrema,
          numInputs: dependencyValues.numInputs,
          numOutputs: dependencyValues.numOutputs,
          numerics
        });
        return {
          setValue: {
            allMaxima: localMaxima,
            globalMaximumOption: globalMaximum,
            globalSupremumOption: globalSupremum
          }
        };
      }
    };
    stateVariableDefinitions.numMaxima = {
      description: "Number of local maxima of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        allMaxima: {
          dependencyType: "stateVariable",
          variableName: "allMaxima"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { numMaxima: dependencyValues.allMaxima.length },
          checkForActualChange: { numMaxima: true }
        };
      }
    };
    stateVariableDefinitions.maxima = {
      description: "Local maxima of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        attributesToShadow: ["styleNumber"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "maximum" || prefix === void 0) {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          } else {
            return [];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: [
        "maximum",
        "maximumLocations",
        "maximumLocation",
        "maximumValues",
        "maximumValue"
      ],
      schemaSubarrays: {
        maximumLocations: {
          numDimensions: 1,
          description: "The x-coordinates of the function's local maxima."
        },
        maximumValues: {
          numDimensions: 1,
          description: "The values of the function's local maxima."
        }
      },
      returnEntryDimensions: (prefix) => {
        if (["maximumLocation", "maximumValue"].includes(prefix)) {
          return 0;
        } else {
          return 1;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (["maximum", "maximumLocation", "maximumValue"].includes(
          arrayEntryPrefix
        )) {
          let pointInd = Number(varEnding) - 1;
          if (Number.isInteger(pointInd) && pointInd >= 0) {
            if (!arraySize || pointInd < arraySize[0]) {
              if (arrayEntryPrefix === "maximum") {
                return [pointInd + ",0", pointInd + ",1"];
              } else if (arrayEntryPrefix === "maximumLocation") {
                return [pointInd + ",0"];
              } else {
                return [pointInd + ",1"];
              }
            } else {
              return [];
            }
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "maximumLocations") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,0"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",0");
        } else if (arrayEntryPrefix === "maximumValues") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,1"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",1");
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        let [ind1, ind2] = arrayKey.split(",");
        if (ind2 === "0") {
          return "maximumLocation" + (Number(ind1) + 1);
        } else {
          return "maximumValue" + (Number(ind1) + 1);
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "maxima") {
          if (propIndex.length === 1) {
            return "maximum" + propIndex[0];
          } else {
            let componentNum = Number(propIndex[0]);
            if (Number.isInteger(componentNum) && componentNum > 0) {
              if (propIndex[1] === 1) {
                return "maximumLocation" + componentNum;
              } else if (propIndex[1] === 2) {
                return "maximumValue" + componentNum;
              }
            }
            return null;
          }
        }
        if (varName === "maximumLocations") {
          return "maximumLocation" + propIndex[0];
        }
        if (varName === "maximumValues") {
          return "maximumValue" + propIndex[0];
        }
        if (varName.slice(0, 7) === "maximum") {
          let componentNum = Number(varName.slice(7));
          if (Number.isInteger(componentNum) && componentNum > 0) {
            if (propIndex[0] === 1) {
              return "maximumLocation" + componentNum;
            } else if (propIndex[0] === 2) {
              return "maximumValue" + componentNum;
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numMaxima: {
          dependencyType: "stateVariable",
          variableName: "numMaxima"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numMaxima, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allMaxima: {
            dependencyType: "stateVariable",
            variableName: "allMaxima"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let maxima = {};
        for (let ptInd = 0; ptInd < globalDependencyValues.__array_size[0]; ptInd++) {
          for (let i2 = 0; i2 < 2; i2++) {
            let arrayKey = `${ptInd},${i2}`;
            maxima[arrayKey] = globalDependencyValues.allMaxima[ptInd][i2];
          }
        }
        return { setValue: { maxima } };
      }
    };
    stateVariableDefinitions.globalMaximum = {
      description: "The global maximum of the function (if attained).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        attributesToShadow: ["styleNumber"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === void 0) {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          } else {
            return [];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["globalMaximumLocation", "globalMaximumValue"],
      getArrayKeysFromVarName({ arrayEntryPrefix }) {
        if (arrayEntryPrefix === "globalMaximumLocation") {
          return ["0"];
        } else if (arrayEntryPrefix === "globalMaximumValue") {
          return ["1"];
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        if (arrayKey === "0") {
          return "globalMaximumLocation";
        } else if (arrayKey === "1") {
          return "globalMaximumValue";
        } else {
          return "invalid";
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "globalMaximum") {
          if (propIndex.length === 1) {
            if (propIndex[0] === 1) {
              return "globalMaximumLocation";
            } else if (propIndex[0] === 2) {
              return "globalMaximumValue";
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        globalMaximumOption: {
          dependencyType: "stateVariable",
          variableName: "globalMaximumOption"
        }
      }),
      returnArraySize({ dependencyValues }) {
        if (dependencyValues.globalMaximumOption) {
          return [2];
        } else {
          return [0];
        }
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            globalMaximumOption: {
              dependencyType: "stateVariable",
              variableName: "globalMaximumOption"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        return {
          setValue: {
            globalMaximum: globalDependencyValues.globalMaximumOption
          }
        };
      }
    };
    stateVariableDefinitions.globalSupremum = {
      description: "The global supremum of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        attributesToShadow: ["styleNumber"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === void 0) {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          } else {
            return [];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["globalSupremumLocation", "globalSupremumValue"],
      getArrayKeysFromVarName({ arrayEntryPrefix }) {
        if (arrayEntryPrefix === "globalSupremumLocation") {
          return ["0"];
        } else if (arrayEntryPrefix === "globalSupremumValue") {
          return ["1"];
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        if (arrayKey === "0") {
          return "globalSupremumLocation";
        } else if (arrayKey === "1") {
          return "globalSupremumValue";
        } else {
          return "invalid";
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "globalSupremum") {
          if (propIndex.length === 1) {
            if (propIndex[0] === 1) {
              return "globalSupremumLocation";
            } else if (propIndex[0] === 2) {
              return "globalSupremumValue";
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        globalSupremumOption: {
          dependencyType: "stateVariable",
          variableName: "globalSupremumOption"
        }
      }),
      returnArraySize({ dependencyValues }) {
        if (dependencyValues.globalSupremumOption) {
          return [2];
        } else {
          return [0];
        }
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            globalSupremumOption: {
              dependencyType: "stateVariable",
              variableName: "globalSupremumOption"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        return {
          setValue: {
            globalSupremum: globalDependencyValues.globalSupremumOption
          }
        };
      }
    };
    stateVariableDefinitions.numExtrema = {
      description: "Number of local extrema (minima + maxima) of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        numMinima: {
          dependencyType: "stateVariable",
          variableName: "numMinima"
        },
        numMaxima: {
          dependencyType: "stateVariable",
          variableName: "numMaxima"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            numExtrema: dependencyValues.numMinima + dependencyValues.numMaxima
          },
          checkForActualChange: { numExtrema: true }
        };
      }
    };
    stateVariableDefinitions.allExtrema = {
      returnDependencies: () => ({
        allMinima: {
          dependencyType: "stateVariable",
          variableName: "allMinima"
        },
        allMaxima: {
          dependencyType: "stateVariable",
          variableName: "allMaxima"
        }
      }),
      definition({ dependencyValues }) {
        let allExtrema = [
          ...dependencyValues.allMinima,
          ...dependencyValues.allMaxima
        ].sort((a2, b2) => a2[0] - b2[0]);
        return { setValue: { allExtrema } };
      }
    };
    stateVariableDefinitions.extrema = {
      description: "Local extrema (minima + maxima) of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        attributesToShadow: ["styleNumber"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "extremum" || prefix === void 0) {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          } else {
            return [];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: [
        "extremum",
        "extremumLocations",
        "extremumLocation",
        "extremumValues",
        "extremumValue"
      ],
      schemaSubarrays: {
        extremumLocations: {
          numDimensions: 1,
          description: "The x-coordinates of the function's local extrema."
        },
        extremumValues: {
          numDimensions: 1,
          description: "The values of the function's local extrema."
        }
      },
      returnEntryDimensions: (prefix) => {
        if (["extremumLocation", "extremumValue"].includes(prefix)) {
          return 0;
        } else {
          return 1;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (["extremum", "extremumLocation", "extremumValue"].includes(
          arrayEntryPrefix
        )) {
          let pointInd = Number(varEnding) - 1;
          if (Number.isInteger(pointInd) && pointInd >= 0) {
            if (!arraySize || pointInd < arraySize[0]) {
              if (arrayEntryPrefix === "extremum") {
                return [pointInd + ",0", pointInd + ",1"];
              } else if (arrayEntryPrefix === "extremumLocation") {
                return [pointInd + ",0"];
              } else {
                return [pointInd + ",1"];
              }
            } else {
              return [];
            }
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "extremumLocations") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,0"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",0");
        } else if (arrayEntryPrefix === "extremumValues") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,1"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",1");
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        let [ind1, ind2] = arrayKey.split(",");
        if (ind2 === "0") {
          return "extremumLocation" + (Number(ind1) + 1);
        } else {
          return "extremumValue" + (Number(ind1) + 1);
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "extrema") {
          if (propIndex.length === 1) {
            return "extremum" + propIndex[0];
          } else {
            let componentNum = Number(propIndex[0]);
            if (Number.isInteger(componentNum) && componentNum > 0) {
              if (propIndex[1] === 1) {
                return "extremumLocation" + componentNum;
              } else if (propIndex[1] === 2) {
                return "extremumValue" + componentNum;
              }
            }
            return null;
          }
        }
        if (varName === "extremumLocations") {
          return "extremumLocation" + propIndex[0];
        }
        if (varName === "extremumValues") {
          return "extremumValue" + propIndex[0];
        }
        if (varName.slice(0, 8) === "extremum") {
          let componentNum = Number(varName.slice(8));
          if (Number.isInteger(componentNum) && componentNum > 0) {
            if (propIndex[0] === 1) {
              return "extremumLocation" + componentNum;
            } else if (propIndex[0] === 2) {
              return "extremumValue" + componentNum;
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numExtrema: {
          dependencyType: "stateVariable",
          variableName: "numExtrema"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numExtrema, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allExtrema: {
            dependencyType: "stateVariable",
            variableName: "allExtrema"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let extrema = {};
        for (let ptInd = 0; ptInd < globalDependencyValues.__array_size[0]; ptInd++) {
          for (let i2 = 0; i2 < 2; i2++) {
            let arrayKey = `${ptInd},${i2}`;
            extrema[arrayKey] = globalDependencyValues.allExtrema[ptInd][i2];
          }
        }
        return { setValue: { extrema } };
      }
    };
    stateVariableDefinitions.returnNumericalDerivatives = {
      stateVariablesDeterminingDependencies: ["isInterpolatedFunction"],
      returnDependencies({ stateValues }) {
        if (stateValues.isInterpolatedFunction) {
          return {
            xs: {
              dependencyType: "stateVariable",
              variableName: "xs"
            },
            coeffs: {
              dependencyType: "stateVariable",
              variableName: "coeffs"
            },
            // interpolationPoints: {
            //   dependencyType: "stateVariable",
            //   variableName: "interpolationPoints"
            // },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            }
          };
        } else {
          return {
            functionChild: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: [
                "returnNumericalDerivatives",
                "variables"
              ]
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            }
          };
        }
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.isInterpolatedFunction) {
          return {
            setValue: {
              returnNumericalDerivatives: returnReturnDerivativesOfInterpolatedFunction(
                dependencyValues
              )
            }
          };
        } else {
          if (dependencyValues.functionChild.length > 0 && dependencyValues.functionChild[0].stateValues.returnNumericalDerivatives) {
            let functionVariables = dependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let childVariables = dependencyValues.functionChild[0].stateValues.variables;
            let childVariablesTrans = childVariables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let variableMapping = {};
            for (let [
              ind,
              variable
            ] of functionVariables.entries()) {
              if (childVariablesTrans[ind] && childVariablesTrans[ind] !== variable) {
                variableMapping[variable] = childVariables[ind];
              }
            }
            if (Object.keys(variableMapping).length === 0) {
              return {
                setValue: {
                  returnNumericalDerivatives: dependencyValues.functionChild[0].stateValues.returnNumericalDerivatives
                }
              };
            } else {
              let returnNumericalDerivatives = function(derivVariables) {
                let mappedDerivVariables = [];
                for (let dVar of derivVariables) {
                  let mapped = variableMapping[dVar.subscripts_to_strings().tree];
                  if (mapped) {
                    mappedDerivVariables.push(mapped);
                  } else {
                    mappedDerivVariables.push(
                      me$3.fromAst("＿")
                    );
                  }
                }
                return dependencyValues.functionChild[0].stateValues.returnNumericalDerivatives(
                  mappedDerivVariables
                );
              };
              return {
                setValue: { returnNumericalDerivatives }
              };
            }
          } else {
            return {
              setValue: { returnNumericalDerivatives: null }
            };
          }
        }
      }
    };
    stateVariableDefinitions.numericalDerivativesDefinition = {
      stateVariablesDeterminingDependencies: ["isInterpolatedFunction"],
      returnDependencies({ stateValues }) {
        if (stateValues.isInterpolatedFunction) {
          return {
            xs: {
              dependencyType: "stateVariable",
              variableName: "xs"
            },
            coeffs: {
              dependencyType: "stateVariable",
              variableName: "coeffs"
            },
            // interpolationPoints: {
            //   dependencyType: "stateVariable",
            //   variableName: "interpolationPoints"
            // },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            }
          };
        } else {
          return {
            functionChild: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: [
                "numericalDerivativesDefinition",
                "variables"
              ]
            },
            isInterpolatedFunction: {
              dependencyType: "stateVariable",
              variableName: "isInterpolatedFunction"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            }
          };
        }
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.isInterpolatedFunction) {
          return {
            setValue: {
              numericalDerivativesDefinition: {
                derivativeType: "interpolatedFunction",
                xs: dependencyValues.xs,
                coeffs: dependencyValues.coeffs,
                variables: dependencyValues.variables
              }
            }
          };
        } else {
          if (dependencyValues.functionChild.length > 0 && dependencyValues.functionChild[0].stateValues.numericalDerivativesDefinition) {
            let functionVariables = dependencyValues.variables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let childVariables = dependencyValues.functionChild[0].stateValues.variables;
            let childVariablesTrans = childVariables.map(
              (x2) => x2.subscripts_to_strings().tree
            );
            let variableMapping = {};
            for (let [
              ind,
              variable
            ] of functionVariables.entries()) {
              if (childVariablesTrans[ind] && childVariablesTrans[ind] !== variable) {
                variableMapping[variable] = childVariables[ind];
              }
            }
            if (Object.keys(variableMapping).length === 0) {
              return {
                setValue: {
                  numericalDerivativesDefinition: dependencyValues.functionChild[0].stateValues.numericalDerivativesDefinition
                }
              };
            } else {
              let derivDef = {
                ...dependencyValues.functionChild[0].stateValues.numericalDerivativesDefinition
              };
              if (derivDef.variableMappings) {
                derivDef.variableMappings = [
                  variableMapping,
                  ...derivDef.variableMappings
                ];
              } else {
                derivDef.variableMappings = [variableMapping];
              }
              return {
                setValue: {
                  numericalDerivativesDefinition: derivDef
                }
              };
            }
          } else {
            return {
              setValue: { numericalDerivativesDefinition: {} }
            };
          }
        }
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "numericalfs",
      componentType: "curve",
      // Shadow selectedStyle so the adapted curve inherits the
      // function's per-component lineStyle/lineWidth overrides.
      stateVariablesToShadow: ["label", "labelHasLatex", "selectedStyle"]
    },
    {
      stateVariable: "formula",
      componentType: "math",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
};
function calculateInterpolationPoints({ dependencyValues, numerics }) {
  let pointsWithX = [];
  let pointsWithoutX = [];
  let diagnostics = [];
  let allPoints = {
    maximum: dependencyValues.prescribedMaxima,
    minimum: dependencyValues.prescribedMinima,
    extremum: dependencyValues.prescribedExtrema,
    point: dependencyValues.prescribedPoints
  };
  for (let type in allPoints) {
    for (let point of allPoints[type]) {
      let x2 = null, y2 = null, slope = null;
      if (point.x !== null) {
        x2 = point.x.evaluate_to_constant();
        if (!Number.isFinite(x2)) {
          diagnostics.push({
            message: `Ignoring non-numerical ${type} of function.`,
            type: "warning"
          });
          continue;
        }
      }
      if (point.y !== null) {
        y2 = point.y.evaluate_to_constant();
        if (!Number.isFinite(y2)) {
          diagnostics.push({
            message: `Ignoring non-numerical ${type} of function.`,
            type: "warning"
          });
          continue;
        }
      }
      if (point.slope !== null && point.slope !== void 0) {
        slope = point.slope.evaluate_to_constant();
        if (!Number.isFinite(slope)) {
          diagnostics.push({
            message: `Ignoring non-numerical slope of function.`,
            type: "warning"
          });
          slope = null;
        }
      }
      if (x2 === null) {
        if (y2 === null) {
          diagnostics.push({
            message: `Ignoring empty ${type} of function.`,
            type: "warning"
          });
          continue;
        }
        pointsWithoutX.push({
          type,
          y: y2,
          slope
        });
      } else {
        pointsWithX.push({
          type,
          x: x2,
          y: y2,
          slope
        });
      }
    }
  }
  pointsWithX.sort((a2, b2) => a2.x - b2.x);
  pointsWithoutX.sort((a2, b2) => a2.y - b2.y);
  let xPrev = -Infinity;
  let eps = numerics.eps;
  for (let ind = 0; ind < pointsWithX.length; ind++) {
    let p2 = pointsWithX[ind];
    if (p2.x <= xPrev + eps) {
      diagnostics.push({
        message: `Function contains two points with locations too close together. Can't define function.`,
        type: "warning"
      });
      return {
        setValue: { interpolationPoints: null },
        sendDiagnostics: diagnostics
      };
    }
    xPrev = p2.x;
  }
  let xscale = dependencyValues.xscale;
  let yscale = dependencyValues.yscale;
  xPrev = void 0;
  let yPrev, typePrev;
  let interpolationPoints = [];
  let pNext = pointsWithX[0];
  for (let ind = 0; ind < pointsWithX.length; ind++) {
    let p2 = pNext;
    pNext = pointsWithX[ind + 1];
    let newPoint = addPointWithX({
      p: p2,
      pNext,
      typePrev,
      xPrev,
      yPrev
    });
    typePrev = newPoint.type;
    xPrev = newPoint.x;
    yPrev = newPoint.y;
  }
  let firstPoint = false;
  if (pointsWithX.length === 0) {
    firstPoint = true;
  }
  while (pointsWithoutX.length > 0) {
    let findMatch;
    if (typePrev === void 0) {
      findMatch = getPointWithoutX({
        allowedTypes: ["maximum", "minimum", "extremum"],
        comparison: "atLeast",
        value: -Infinity
      });
    } else if (typePrev === "maximum") {
      findMatch = getPointWithoutX({
        allowedTypes: ["minimum", "extremum"],
        comparison: "atMost",
        value: yPrev - yscale
      });
    } else if (typePrev === "minimum") {
      findMatch = getPointWithoutX({
        allowedTypes: ["maximum", "extremum"],
        comparison: "atLeast",
        value: yPrev + yscale
      });
    } else if (typePrev === "point") {
      findMatch = getPointWithoutX({
        allowedTypes: ["maximum", "extremum"],
        comparison: "atLeast",
        value: yPrev + yscale
      });
      if (findMatch.success !== true) {
        findMatch = getPointWithoutX({
          allowedTypes: ["minimum", "extremum"],
          comparison: "atMost",
          value: yPrev - yscale
        });
      }
    }
    let p2;
    if (findMatch.success === true) {
      p2 = findMatch.point;
      pointsWithoutX.splice(findMatch.ind, 1);
      if (firstPoint) {
        p2.x = 0;
        firstPoint = false;
      } else {
        p2.x = xPrev + xscale;
      }
    } else {
      p2 = pointsWithoutX.pop();
      if (firstPoint) {
        p2.x = 0;
        firstPoint = false;
      } else {
        p2.x = xPrev + 2 * xscale;
      }
    }
    let newPoint = addPointWithX({
      p: p2,
      typePrev,
      xPrev,
      yPrev
    });
    typePrev = newPoint.type;
    xPrev = newPoint.x;
    yPrev = newPoint.y;
  }
  if (interpolationPoints.length === 0) {
    interpolationPoints.push({
      type: "point",
      x: 0,
      y: 0,
      slope: 0
    });
  }
  firstPoint = interpolationPoints[0];
  if (firstPoint.type === "maximum") {
    let newPoint = {
      type: "point",
      x: firstPoint.x - xscale,
      y: firstPoint.y - yscale,
      slope: 2 * yscale / xscale
    };
    interpolationPoints.splice(0, 0, newPoint);
  } else if (firstPoint.type === "minimum") {
    let newPoint = {
      type: "point",
      x: firstPoint.x - xscale,
      y: firstPoint.y + yscale,
      slope: -2 * yscale / xscale
    };
    interpolationPoints.splice(0, 0, newPoint);
  } else if (firstPoint.type === "point") {
    if (interpolationPoints.length === 1) {
      if (firstPoint.slope === null) {
        firstPoint.slope = 0;
      }
    } else {
      let nextPoint = interpolationPoints[1];
      let secantslope = (nextPoint.y - firstPoint.y) / (nextPoint.x - firstPoint.x);
      if (nextPoint.type === "maximum" || nextPoint.type === "minimum") {
        if (firstPoint.slope === null) {
          firstPoint.slope = 2 * secantslope;
        }
      } else {
        if (interpolationPoints.length === 2) {
          if (firstPoint.slope === null) {
            firstPoint.slope = secantslope;
          }
        } else {
          if (nextPoint.slope === null) {
            nextPoint.slope = monotonicSlope({
              point: nextPoint,
              prevPoint: firstPoint,
              nextPoint: interpolationPoints[2]
            });
          }
          if (firstPoint.slope === null) {
            firstPoint.slope = 2 * (firstPoint.y - nextPoint.y) / (firstPoint.x - nextPoint.x) - nextPoint.slope;
          }
        }
        let newPoint = {
          x: firstPoint.x - xscale,
          y: firstPoint.y - xscale * firstPoint.slope,
          slope: firstPoint.slope
        };
        interpolationPoints.splice(0, 0, newPoint);
      }
    }
  }
  let lastPoint = interpolationPoints[interpolationPoints.length - 1];
  if (lastPoint.type === "maximum") {
    let newPoint = {
      type: "point",
      x: lastPoint.x + xscale,
      y: lastPoint.y - yscale,
      slope: -2 * yscale / xscale
    };
    interpolationPoints.push(newPoint);
  } else if (lastPoint.type === "minimum") {
    let newPoint = {
      type: "point",
      x: lastPoint.x + xscale,
      y: lastPoint.y + yscale,
      slope: 2 * yscale / xscale
    };
    interpolationPoints.push(newPoint);
  } else if (lastPoint.type === "point") {
    if (interpolationPoints.length === 1) {
      let newPoint = {
        type: "point",
        x: lastPoint.x + xscale,
        y: lastPoint.y + firstPoint.slope * xscale,
        slope: firstPoint.slope
      };
      interpolationPoints.push(newPoint);
    } else {
      let prevPoint = interpolationPoints[interpolationPoints.length - 2];
      let secantslope = (prevPoint.y - lastPoint.y) / (prevPoint.x - lastPoint.x);
      if (prevPoint.type === "maximum" || prevPoint.type === "minimum") {
        if (lastPoint.slope === null) {
          lastPoint.slope = 2 * secantslope;
        }
      } else {
        if (interpolationPoints.length === 2) {
          if (lastPoint.slope === null) {
            lastPoint.slope = secantslope;
          }
        } else {
          if (prevPoint.slope === null) {
            prevPoint.slope = monotonicSlope({
              point: prevPoint,
              prevPoint: interpolationPoints[interpolationPoints.length - 3],
              nextPoint: lastPoint
            });
          }
          if (lastPoint.slope === null) {
            lastPoint.slope = 2 * (prevPoint.y - lastPoint.y) / (prevPoint.x - lastPoint.x) - prevPoint.slope;
          }
        }
        let newPoint = {
          x: lastPoint.x + xscale,
          y: lastPoint.y + xscale * lastPoint.slope,
          slope: lastPoint.slope
        };
        interpolationPoints.push(newPoint);
      }
    }
  }
  for (let ind = 1; ind < interpolationPoints.length - 1; ind++) {
    let point = interpolationPoints[ind];
    if (point.slope === null) {
      point.slope = monotonicSlope({
        point,
        prevPoint: interpolationPoints[ind - 1],
        nextPoint: interpolationPoints[ind + 1]
      });
    }
  }
  return { setValue: { interpolationPoints }, sendDiagnostics: diagnostics };
  function monotonicSlope({ point, prevPoint, nextPoint }) {
    let dx1 = point.x - prevPoint.x;
    let dx2 = nextPoint.x - point.x;
    let dy1 = point.y - prevPoint.y;
    let dy2 = nextPoint.y - point.y;
    let s1 = dy1 / dx1;
    let s2 = dy2 / dx2;
    let p1 = (s1 * dx2 + s2 * dx1) / (dx1 + dx2);
    let slope = (Math.sign(s1) + Math.sign(s2)) * Math.min(Math.abs(s1), Math.abs(s2), 0.5 * Math.abs(p1));
    return slope;
  }
  function addPointWithX({ p: p2, pNext: pNext2, typePrev: typePrev2, xPrev: xPrev2, yPrev: yPrev2 }) {
    let yNext;
    if (pNext2 !== void 0) {
      yNext = pNext2.y;
    }
    if (p2.type === "maximum") {
      return addMaximum({
        x: p2.x,
        y: p2.y,
        typePrev: typePrev2,
        xPrev: xPrev2,
        yPrev: yPrev2,
        yNext,
        pNext: pNext2
      });
    } else if (p2.type === "minimum") {
      return addMinimum({
        x: p2.x,
        y: p2.y,
        typePrev: typePrev2,
        xPrev: xPrev2,
        yPrev: yPrev2,
        yNext,
        pNext: pNext2
      });
    } else if (p2.type === "extremum") {
      let typeNext;
      if (typePrev2 === void 0) {
        if (pNext2 === void 0) {
          return addMaximum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        }
        typeNext = pNext2.type;
      }
      if (typePrev2 === "maximum" || typeNext === "maximum") {
        if (p2.y !== null && p2.y > yPrev2 - yscale) {
          return addMaximum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        } else if (typeNext !== void 0 && p2.y !== null && p2.y > pNext2.y - yscale) {
          return addMaximum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        } else {
          return addMinimum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        }
      } else if (typePrev2 === "minimum" || typeNext === "minimum") {
        if (p2.y !== null && p2.y < yPrev2 + yscale) {
          return addMinimum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        } else if (typeNext !== void 0 && p2.y !== null && p2.y > pNext2.y + yscale) {
          return addMinimum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        } else {
          return addMaximum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        }
      } else if (typePrev2 === "point" || typeNext === "point") {
        let treatAs = "maximum";
        if (p2.y === null && pNext2 !== void 0 && pNext2.type === maximum) {
          treatAs = "minimum";
        } else if (p2.y !== null && p2.y <= yPrev2 - yscale) {
          treatAs = "minimum";
        } else if (typeNext !== void 0 && p2.y !== null && p2.y >= pNext2.y - yscale) {
          treatAs = "minimum";
        }
        if (treatAs === "minimum") {
          return addMinimum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        } else {
          return addMaximum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        }
      } else {
        if (p2.y !== null && p2.y < pNext2.y - yscale) {
          return addMinimum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        } else {
          return addMaximum({
            x: p2.x,
            y: p2.y,
            typePrev: typePrev2,
            xPrev: xPrev2,
            yPrev: yPrev2,
            yNext,
            pNext: pNext2
          });
        }
      }
    } else if (p2.type === "point") {
      return addPoint({
        x: p2.x,
        y: p2.y,
        slope: p2.slope,
        typePrev: typePrev2,
        xPrev: xPrev2,
        yPrev: yPrev2,
        yNext,
        pNext: pNext2
      });
    }
  }
  function addMaximum({ x: x2, y: y2, typePrev: typePrev2, xPrev: xPrev2, yPrev: yPrev2, yNext, pNext: pNext2 }) {
    if (typePrev2 === void 0) {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = 0;
        } else if (pNext2.type === "maximum") {
          y2 = yNext;
        } else {
          y2 = yNext + yscale;
        }
      }
    } else if (typePrev2 === "maximum") {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = yPrev2;
        } else if (pNext2.type === "maximum") {
          y2 = Math.max(yPrev2, yNext);
        } else {
          y2 = Math.max(yPrev2, yNext + yscale);
        }
      }
      let yMin = Math.min(yPrev2, y2) - yscale;
      let xNew = (x2 + xPrev2) / 2;
      let yNew, typeNew, slopeNew = null;
      let results = getPointWithoutX({
        allowedTypes: ["minimum", "extremum"],
        comparison: "atMost",
        value: yMin
      });
      if (results.success === true) {
        typeNew = "minimum";
        yNew = results.point.y;
        pointsWithoutX.splice(results.ind, 1);
        slopeNew = 0;
      } else {
        typeNew = "point";
        yNew = yMin;
      }
      interpolationPoints.push({
        type: typeNew,
        x: xNew,
        y: yNew,
        slope: slopeNew
      });
    } else if (typePrev2 === "minimum") {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = yPrev2 + yscale;
        } else if (pNext2.type === "maximum") {
          y2 = Math.max(yPrev2 + yscale, yNext);
        } else {
          y2 = Math.max(yPrev2, yNext) + yscale;
        }
      } else {
        if (y2 < yPrev2 + yscale) {
          let xs = [(2 * xPrev2 + x2) / 3, (xPrev2 + 2 * x2) / 3];
          let findValues = [yPrev2 + yscale, y2 - yscale];
          let findComparisons = ["atLeast", "atMost"];
          let findTypes = [
            ["maximum", "extremum"],
            ["minimum", "extremum"]
          ];
          for (let newPointNum = 0; newPointNum < 2; newPointNum++) {
            let xNew = xs[newPointNum];
            let yNew, typeNew, slopeNew = null;
            let results = getPointWithoutX({
              allowedTypes: findTypes[newPointNum],
              comparison: findComparisons[newPointNum],
              value: findValues[newPointNum]
            });
            if (results.success === true) {
              typeNew = findTypes[newPointNum][0];
              yNew = results.point.y;
              pointsWithoutX.splice(results.ind, 1);
              slopeNew = 0;
            } else {
              typeNew = "point";
              yNew = findValues[newPointNum];
              slopeNew = null;
            }
            interpolationPoints.push({
              type: typeNew,
              x: xNew,
              y: yNew,
              slope: slopeNew
            });
          }
        }
      }
    } else if (typePrev2 === "point") {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = yPrev2 + yscale;
        } else if (pNext2.type === "maximum") {
          y2 = Math.max(yPrev2 + yscale, yNext);
        } else {
          y2 = Math.max(yPrev2, yNext) + yscale;
        }
      } else {
        if (y2 < yPrev2 + yscale) {
          let xNew = (x2 + xPrev2) / 2;
          let yNew, typeNew, slopeNew = null;
          let results = getPointWithoutX({
            allowedTypes: ["minimum", "extremum"],
            comparison: "atMost",
            value: y2 - yscale
          });
          if (results.success === true) {
            typeNew = "minimum";
            yNew = results.point.y;
            pointsWithoutX.splice(results.ind, 1);
            slopeNew = 0;
          } else {
            typeNew = "point";
            yNew = y2 - yscale;
          }
          interpolationPoints.push({
            type: typeNew,
            x: xNew,
            y: yNew,
            slope: slopeNew
          });
        }
      }
    }
    let newMaximum = {
      type: "maximum",
      x: x2,
      y: y2,
      slope: 0
    };
    interpolationPoints.push(newMaximum);
    return newMaximum;
  }
  function addMinimum({ x: x2, y: y2, typePrev: typePrev2, xPrev: xPrev2, yPrev: yPrev2, yNext, pNext: pNext2 }) {
    if (typePrev2 === void 0) {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = 0;
        } else if (pNext2.type === "minimum") {
          y2 = yNext;
        } else {
          y2 = yNext - yscale;
        }
      }
    } else if (typePrev2 === "maximum") {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = yPrev2 - yscale;
        } else if (pNext2.type === "minimum") {
          y2 = Math.min(yPrev2 - yscale, yNext);
        } else {
          y2 = Math.min(yPrev2, yNext) - yscale;
        }
      } else {
        if (y2 > yPrev2 - yscale) {
          let xs = [(2 * xPrev2 + x2) / 3, (xPrev2 + 2 * x2) / 3];
          let findValues = [yPrev2 - yscale, y2 + yscale];
          let findComparisons = ["atMost", "atLeast"];
          let findTypes = [
            ["minimum", "extremum"],
            ["maximum", "extremum"]
          ];
          for (let newPointNum = 0; newPointNum < 2; newPointNum++) {
            let xNew = xs[newPointNum];
            let yNew, typeNew, slopeNew = null;
            let results = getPointWithoutX({
              allowedTypes: findTypes[newPointNum],
              comparison: findComparisons[newPointNum],
              value: findValues[newPointNum]
            });
            if (results.success === true) {
              typeNew = findTypes[newPointNum][0];
              yNew = results.point.y;
              pointsWithoutX.splice(results.ind, 1);
              slopeNew = 0;
            } else {
              typeNew = "point";
              yNew = findValues[newPointNum];
              slopeNew = null;
            }
            interpolationPoints.push({
              type: typeNew,
              x: xNew,
              y: yNew,
              slope: slopeNew
            });
          }
        }
      }
    } else if (typePrev2 === "minimum") {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = yPrev2;
        } else if (pNext2.type === "minimum") {
          y2 = Math.min(yPrev2, yNext);
        } else {
          y2 = Math.min(yPrev2, yNext - yscale);
        }
      }
      let yMax = Math.max(yPrev2, y2) + yscale;
      let xNew = (x2 + xPrev2) / 2;
      let yNew, typeNew, slopeNew = null;
      let results = getPointWithoutX({
        allowedTypes: ["maximum", "extremum"],
        comparison: "atLeast",
        value: yMax
      });
      if (results.success === true) {
        typeNew = "maximum";
        yNew = results.point.y;
        pointsWithoutX.splice(results.ind, 1);
        slopeNew = 0;
      } else {
        typeNew = "point";
        yNew = yMax;
      }
      interpolationPoints.push({
        type: typeNew,
        x: xNew,
        y: yNew,
        slope: slopeNew
      });
    } else if (typePrev2 === "point") {
      if (y2 === null) {
        if (yNext === void 0) {
          y2 = yPrev2 - yscale;
        } else if (pNext2.type === "minimum") {
          y2 = Math.min(yPrev2 - yscale, yNext);
        } else {
          y2 = Math.min(yPrev2, yNext) - yscale;
        }
      } else {
        if (y2 > yPrev2 - yscale) {
          let xNew = (x2 + xPrev2) / 2;
          let yNew, typeNew, slopeNew = null;
          let results = getPointWithoutX({
            allowedTypes: ["maximum", "extremum"],
            comparison: "atLeast",
            value: y2 + yscale
          });
          if (results.success === true) {
            typeNew = "maximum";
            yNew = results.point.y;
            pointsWithoutX.splice(results.ind, 1);
            slopeNew = 0;
          } else {
            typeNew = "point";
            yNew = y2 + yscale;
          }
          interpolationPoints.push({
            type: typeNew,
            x: xNew,
            y: yNew,
            slope: slopeNew
          });
        }
      }
    }
    let newMinimum = {
      type: "minimum",
      x: x2,
      y: y2,
      slope: 0
    };
    interpolationPoints.push(newMinimum);
    return newMinimum;
  }
  function addPoint({ x: x2, y: y2, slope, typePrev: typePrev2, xPrev: xPrev2, yPrev: yPrev2, yNext, pNext: pNext2 }) {
    if (typePrev2 === "maximum") {
      if (y2 > yPrev2 - yscale) {
        let yMin = Math.min(yPrev2, y2) - yscale;
        let xNew = (x2 + xPrev2) / 2;
        let yNew, typeNew, slopeNew = null;
        let results = getPointWithoutX({
          allowedTypes: ["minimum", "extremum"],
          comparison: "atMost",
          value: yMin
        });
        if (results.success === true) {
          typeNew = "minimum";
          yNew = results.point.y;
          pointsWithoutX.splice(results.ind, 1);
          slopeNew = 0;
        } else {
          typeNew = "point";
          yNew = yPrev2 - yscale;
        }
        interpolationPoints.push({
          type: typeNew,
          x: xNew,
          y: yNew,
          slope: slopeNew
        });
      }
    } else if (typePrev2 === "minimum") {
      if (y2 < yPrev2 + yscale) {
        let yMax = Math.max(yPrev2, y2) + yscale;
        let xNew = (x2 + xPrev2) / 2;
        let yNew, typeNew, slopeNew = null;
        let results = getPointWithoutX({
          allowedTypes: ["maximum", "extremum"],
          comparison: "atLeast",
          value: yMax
        });
        if (results.success === true) {
          typeNew = "maximum";
          yNew = results.point.y;
          pointsWithoutX.splice(results.ind, 1);
          slopeNew = 0;
        } else {
          typeNew = "point";
          yNew = yPrev2 + yscale;
        }
        interpolationPoints.push({
          type: typeNew,
          x: xNew,
          y: yNew,
          slope: slopeNew
        });
      }
    }
    let newPoint = {
      type: "point",
      x: x2,
      y: y2,
      slope
    };
    interpolationPoints.push(newPoint);
    return newPoint;
  }
  function getPointWithoutX({ allowedTypes, comparison, value }) {
    let inds = [];
    if (comparison === "atMost") {
      inds = Object.keys(pointsWithoutX).reverse();
    } else if (comparison === "atLeast") {
      inds = Object.keys(pointsWithoutX);
    } else {
      return { success: false };
    }
    for (let type of allowedTypes) {
      for (let ind of inds) {
        let p2 = pointsWithoutX[ind];
        if (p2.type !== type) {
          continue;
        }
        if (comparison === "atMost") {
          if (p2.y <= value) {
            return {
              success: true,
              ind,
              point: p2
            };
          }
        } else {
          if (p2.y >= value) {
            return {
              success: true,
              ind,
              point: p2
            };
          }
        }
      }
    }
    return { success: false };
  }
}
function computeSplineParamCoeffs({ dependencyValues }) {
  let initCubicPoly2 = function(x1, x2, t1, t2, s2) {
    return [
      x1,
      t1,
      (-3 * x1 / s2 + 3 * x2 / s2 - 2 * t1 - t2) / s2,
      (2 * x1 / s2 - 2 * x2 / s2 + t1 + t2) / (s2 * s2)
    ];
  };
  let interpolationPoints = dependencyValues.interpolationPoints;
  if (interpolationPoints === null) {
    return {
      setValue: {
        xs: null,
        coeffs: null
      }
    };
  }
  let coeffs = [];
  let xs = [];
  let p0;
  let p1 = interpolationPoints[0];
  xs.push(p1.x);
  for (let ind = 1; ind < interpolationPoints.length; ind++) {
    p0 = p1;
    p1 = interpolationPoints[ind];
    let c2 = initCubicPoly2(p0.y, p1.y, p0.slope, p1.slope, p1.x - p0.x);
    if (Math.abs(c2[3]) < 1e-14 * Math.max(Math.abs(c2[0]), Math.abs(c2[1]), Math.abs(c2[2]))) {
      c2[3] = 0;
      if (Math.abs(c2[2]) < 1e-14 * Math.max(Math.abs(c2[0]), Math.abs(c2[1]))) {
        c2[2] = 0;
      }
    }
    coeffs.push(c2);
    xs.push(p1.x);
  }
  return {
    setValue: {
      xs,
      coeffs
    }
  };
}
class FunctionOperator extends Function$1 {
  static componentType = "_functionOperator";
  static returnSugarInstructions() {
    let sugarInstructions = [];
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        wrappingComponentType: "math",
        onlyStringOrMacros: true
      })
    });
    return sugarInstructions;
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    let componentType = this.componentType;
    stateVariableDefinitions.isInterpolatedFunction = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isInterpolatedFunction: false } })
    };
    delete stateVariableDefinitions.numPrescribedPoints;
    delete stateVariableDefinitions.prescribedPoints;
    delete stateVariableDefinitions.prescribedMinima;
    delete stateVariableDefinitions.prescribedMaxima;
    delete stateVariableDefinitions.prescribedExtrema;
    delete stateVariableDefinitions.interpolationPoints;
    delete stateVariableDefinitions.xs;
    delete stateVariableDefinitions.functionChildrenInfoToCalculateExtrema;
    stateVariableDefinitions.operatorBasedOnFormulaIfAvailable = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { operatorBasedOnFormulaIfAvailable: false }
      })
    };
    stateVariableDefinitions.formula.returnDependencies = () => ({});
    stateVariableDefinitions.formula.definition = () => ({
      setValue: { formula: me$3.fromAst("＿") }
    });
    stateVariableDefinitions.operatorComposesWithOriginal = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { operatorComposesWithOriginal: true }
      })
    };
    stateVariableDefinitions.symbolicFunctionOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          symbolicFunctionOperator: (x2) => me$3.fromAst("＿")
        }
      })
    };
    stateVariableDefinitions.numericalFunctionOperator = {
      additionalStateVariablesDefined: [
        "numericalFunctionOperatorArguments"
      ],
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: {
          numericalFunctionOperator: (x2) => NaN,
          numericalFunctionOperatorArguments: []
        }
      })
    };
    stateVariableDefinitions.formulaOperator = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { formulaOperator: (x2) => me$3.fromAst("＿") }
      })
    };
    stateVariableDefinitions.returnNumericalDerivatives = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { returnNumericalDerivatives: null }
      })
    };
    stateVariableDefinitions.formula = {
      description: "The symbolic formula of the function produced by this operator.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      additionalStateVariablesDefined: ["operatorBasedOnFormula"],
      // stateVariablesDeterminingDependencies: ["operatorBasedOnFormulaIfAvailable"],
      returnDependencies: () => ({
        operatorBasedOnFormulaIfAvailable: {
          dependencyType: "stateVariable",
          variableName: "operatorBasedOnFormulaIfAvailable"
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["formula"]
        },
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        },
        formulaOperator: {
          dependencyType: "stateVariable",
          variableName: "formulaOperator"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!dependencyValues.operatorBasedOnFormulaIfAvailable || (dependencyValues.functionChild.length === 0 || dependencyValues.functionChild[0].stateValues.formula.tree === "＿") && (dependencyValues.mathChild.length === 0 || dependencyValues.mathChild[0].stateValues.value.tree === "＿")) {
          return {
            setValue: {
              formula: me$3.fromAst("＿"),
              operatorBasedOnFormula: false
            }
          };
        }
        let formulaPreOperator;
        if (dependencyValues.functionChild.length === 0) {
          formulaPreOperator = dependencyValues.mathChild[0].stateValues.value;
        } else {
          formulaPreOperator = dependencyValues.functionChild[0].stateValues.formula;
        }
        return {
          setValue: {
            formula: dependencyValues.formulaOperator(
              formulaPreOperator
            ),
            operatorBasedOnFormula: true
          }
        };
      }
    };
    stateVariableDefinitions.symbolicfs = {
      isArray: true,
      entryPrefixes: ["symbolicf"],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey: () => ({
        globalDependencies: {
          operatorBasedOnFormula: {
            dependencyType: "stateVariable",
            variableName: "operatorBasedOnFormula"
          },
          formula: {
            dependencyType: "stateVariable",
            variableName: "formula"
          },
          variables: {
            dependencyType: "stateVariable",
            variableName: "variables"
          },
          numInputs: {
            dependencyType: "stateVariable",
            variableName: "numInputs"
          },
          simplify: {
            dependencyType: "stateVariable",
            variableName: "simplify"
          },
          expand: {
            dependencyType: "stateVariable",
            variableName: "expand"
          },
          functionChild: {
            dependencyType: "child",
            childGroups: ["functions"],
            variableNames: ["symbolicfs"]
          },
          mathChild: {
            dependencyType: "child",
            childGroups: ["maths"],
            variableNames: ["value"]
          },
          symbolicFunctionOperator: {
            dependencyType: "stateVariable",
            variableName: "symbolicFunctionOperator"
          },
          operatorComposesWithOriginal: {
            dependencyType: "stateVariable",
            variableName: "operatorComposesWithOriginal"
          },
          domain: {
            dependencyType: "stateVariable",
            variableName: "domain"
          }
        }
      }),
      arrayDefinitionByKey: function({
        globalDependencyValues,
        usedDefault,
        arrayKeys,
        arraySize
      }) {
        if (globalDependencyValues.operatorBasedOnFormula) {
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            symbolicfs[arrayKey] = returnSymbolicFunctionFromFormula({
              formula: globalDependencyValues.formula,
              simplify: globalDependencyValues.simplify,
              expand: globalDependencyValues.expand,
              numInputs: globalDependencyValues.numInputs,
              variables: globalDependencyValues.variables,
              domain: globalDependencyValues.domain,
              component: arrayKey
            });
          }
          return {
            setValue: { symbolicfs }
          };
        } else if (globalDependencyValues.operatorComposesWithOriginal) {
          if (globalDependencyValues.functionChild.length === 0) {
            if (globalDependencyValues.mathChild.length === 0) {
              let symbolicfs = {};
              for (let arrayKey of arrayKeys) {
                symbolicfs[arrayKey] = (x2) => me$3.fromAst("＿");
              }
              return {
                setValue: { symbolicfs }
              };
            } else {
              let dependencyValuesWithChildFormula = Object.assign({}, globalDependencyValues);
              dependencyValuesWithChildFormula.formula = globalDependencyValues.mathChild[0].stateValues.value;
              let childFs = [];
              for (let ind = 0; ind < arraySize[0]; ind++) {
                childFs.push(
                  returnSymbolicFunctionFromFormula({
                    formula: dependencyValuesWithChildFormula.formula,
                    simplify: dependencyValuesWithChildFormula.simplify,
                    expand: dependencyValuesWithChildFormula.expand,
                    numInputs: dependencyValuesWithChildFormula.numInputs,
                    variables: dependencyValuesWithChildFormula.variables,
                    domain: globalDependencyValues.domain,
                    component: ind
                  })
                );
              }
              let symbolicfs = {};
              for (let arrayKey of arrayKeys) {
                symbolicfs[arrayKey] = (...xs) => globalDependencyValues.symbolicFunctionOperator(
                  ...childFs.map((cf) => cf(...xs))
                );
              }
              return {
                setValue: { symbolicfs }
              };
            }
          } else {
            let childFs = [];
            for (let ind = 0; ind < arraySize[0]; ind++) {
              childFs.push(
                globalDependencyValues.functionChild[0].stateValues.symbolicfs[ind]
              );
            }
            let symbolicfs = {};
            for (let arrayKey of arrayKeys) {
              symbolicfs[arrayKey] = (...xs) => globalDependencyValues.symbolicFunctionOperator(
                ...childFs.map((cf) => cf(...xs))
              );
            }
            return {
              setValue: { symbolicfs }
            };
          }
        } else {
          let symbolicfs = {};
          for (let arrayKey of arrayKeys) {
            symbolicfs[arrayKey] = (...xs) => globalDependencyValues.symbolicFunctionOperator(
              ...xs
            );
          }
          return {
            setValue: { symbolicfs }
          };
        }
      }
    };
    stateVariableDefinitions.numericalfs = {
      isArray: true,
      entryPrefixes: ["numericalf"],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey: () => ({
        globalDependencies: {
          operatorBasedOnFormula: {
            dependencyType: "stateVariable",
            variableName: "operatorBasedOnFormula"
          },
          formula: {
            dependencyType: "stateVariable",
            variableName: "formula"
          },
          variables: {
            dependencyType: "stateVariable",
            variableName: "variables"
          },
          numInputs: {
            dependencyType: "stateVariable",
            variableName: "numInputs"
          },
          functionChild: {
            dependencyType: "child",
            childGroups: ["functions"],
            variableNames: ["numericalfs"]
          },
          mathChild: {
            dependencyType: "child",
            childGroups: ["maths"],
            variableNames: ["value"]
          },
          numericalFunctionOperator: {
            dependencyType: "stateVariable",
            variableName: "numericalFunctionOperator"
          },
          operatorComposesWithOriginal: {
            dependencyType: "stateVariable",
            variableName: "operatorComposesWithOriginal"
          },
          domain: {
            dependencyType: "stateVariable",
            variableName: "domain"
          }
        }
      }),
      arrayDefinitionByKey: function({
        globalDependencyValues,
        usedDefault,
        arrayKeys,
        arraySize
      }) {
        if (globalDependencyValues.operatorBasedOnFormula) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            numericalfs[arrayKey] = returnNumericalFunctionFromFormula({
              formula: globalDependencyValues.formula,
              numInputs: globalDependencyValues.numInputs,
              variables: globalDependencyValues.variables,
              domain: globalDependencyValues.domain,
              component: arrayKey
            });
          }
          return {
            setValue: { numericalfs }
          };
        } else if (globalDependencyValues.operatorComposesWithOriginal) {
          if (globalDependencyValues.functionChild.length === 0) {
            if (globalDependencyValues.mathChild.length === 0) {
              let numericalfs = {};
              for (let arrayKey of arrayKeys) {
                numericalfs[arrayKey] = (x2) => NaN;
              }
              return {
                setValue: { numericalfs }
              };
            } else {
              let dependencyValuesWithChildFormula = Object.assign({}, globalDependencyValues);
              dependencyValuesWithChildFormula.formula = globalDependencyValues.mathChild[0].stateValues.value;
              let childFs = [];
              for (let ind = 0; ind < arraySize[0]; ind++) {
                childFs.push(
                  returnNumericalFunctionFromFormula({
                    formula: globalDependencyValues.formula,
                    numInputs: globalDependencyValues.numInputs,
                    variables: globalDependencyValues.variables,
                    domain: globalDependencyValues.domain,
                    component: ind
                  })
                );
              }
              let numericalfs = {};
              for (let arrayKey of arrayKeys) {
                numericalfs[arrayKey] = (...xs) => globalDependencyValues.numericalFunctionOperator(
                  ...childFs.map((cf) => cf(...xs))
                );
              }
              return {
                setValue: { numericalfs }
              };
            }
          } else {
            let childFs = [];
            for (let ind = 0; ind < arraySize[0]; ind++) {
              childFs.push(
                globalDependencyValues.functionChild[0].stateValues.numericalfs[ind]
              );
            }
            let numericalfs = {};
            for (let arrayKey of arrayKeys) {
              numericalfs[arrayKey] = (...xs) => globalDependencyValues.numericalFunctionOperator(
                ...childFs.map((cf) => cf(...xs))
              );
            }
            return {
              setValue: { numericalfs }
            };
          }
        } else {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            numericalfs[arrayKey] = (...xs) => globalDependencyValues.numericalFunctionOperator(
              ...xs
            );
          }
          return {
            setValue: { numericalfs }
          };
        }
      }
    };
    stateVariableDefinitions.fDefinitions = {
      isArray: true,
      entryPrefixes: ["fDefinition"],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey: () => ({
        globalDependencies: {
          operatorBasedOnFormula: {
            dependencyType: "stateVariable",
            variableName: "operatorBasedOnFormula"
          },
          formula: {
            dependencyType: "stateVariable",
            variableName: "formula"
          },
          variables: {
            dependencyType: "stateVariable",
            variableName: "variables"
          },
          numInputs: {
            dependencyType: "stateVariable",
            variableName: "numInputs"
          },
          numOutputs: {
            dependencyType: "stateVariable",
            variableName: "numOutputs"
          },
          functionChild: {
            dependencyType: "child",
            childGroups: ["functions"],
            variableNames: ["fDefinitions"]
          },
          mathChild: {
            dependencyType: "child",
            childGroups: ["maths"],
            variableNames: ["value"]
          },
          numericalFunctionOperator: {
            dependencyType: "stateVariable",
            variableName: "numericalFunctionOperator"
          },
          numericalFunctionOperatorArguments: {
            dependencyType: "stateVariable",
            variableName: "numericalFunctionOperatorArguments"
          },
          operatorComposesWithOriginal: {
            dependencyType: "stateVariable",
            variableName: "operatorComposesWithOriginal"
          },
          domain: {
            dependencyType: "stateVariable",
            variableName: "domain"
          }
        }
      }),
      arrayDefinitionByKey: function({
        globalDependencyValues,
        usedDefault,
        arrayKeys,
        arraySize
      }) {
        if (globalDependencyValues.operatorBasedOnFormula) {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = {
              functionType: "formula",
              formula: globalDependencyValues.formula.tree,
              variables: globalDependencyValues.variables.map(
                (x2) => x2.tree
              ),
              numInputs: globalDependencyValues.numInputs,
              numOutputs: globalDependencyValues.numOutputs,
              domain: globalDependencyValues.domain,
              component: arrayKey
            };
          }
          return {
            setValue: { fDefinitions }
          };
        } else if (globalDependencyValues.operatorComposesWithOriginal) {
          if (globalDependencyValues.functionChild.length === 0) {
            if (globalDependencyValues.mathChild.length === 0) {
              let fDefinitions = {};
              for (let arrayKey of arrayKeys) {
                fDefinitions[arrayKey] = {
                  functionType: "formula",
                  formula: "＿",
                  variables: globalDependencyValues.variables.map(
                    (x2) => x2.tree
                  ),
                  numInputs: globalDependencyValues.numInputs,
                  numOutputs: globalDependencyValues.numOutputs,
                  domain: globalDependencyValues.domain
                };
              }
              return {
                setValue: { fDefinitions }
              };
            } else {
              throw Error(
                "function operator with math child not implemented yet"
              );
            }
          } else {
            let fDefinitions = {};
            for (let arrayKey of arrayKeys) {
              fDefinitions[arrayKey] = {
                functionType: "functionOperator",
                componentType,
                numOutputs: globalDependencyValues.numOutputs,
                functionOperatorArguments: globalDependencyValues.numericalFunctionOperatorArguments,
                operatorComposesWithOriginal: true,
                originalFDefinition: globalDependencyValues.functionChild[0].stateValues.fDefinitions[arrayKey]
              };
            }
            return {
              setValue: { fDefinitions }
            };
          }
        } else {
          if (globalDependencyValues.functionChild.length === 0) {
            if (globalDependencyValues.mathChild.length === 0) {
              let fDefinitions = {};
              for (let arrayKey of arrayKeys) {
                fDefinitions[arrayKey] = {
                  functionType: "formula",
                  formula: "＿",
                  variables: globalDependencyValues.variables.map(
                    (x2) => x2.tree
                  ),
                  numInputs: globalDependencyValues.numInputs,
                  numOutputs: globalDependencyValues.numOutputs,
                  domain: globalDependencyValues.domain
                };
              }
              return {
                setValue: { fDefinitions }
              };
            } else {
              throw Error(
                "function operator with math child not implemented yet"
              );
            }
          } else {
            let fDefinitions = {};
            for (let arrayKey of arrayKeys) {
              fDefinitions[arrayKey] = {
                functionType: "functionOperator",
                componentType,
                numOutputs: globalDependencyValues.numOutputs,
                functionOperatorArguments: globalDependencyValues.numericalFunctionOperatorArguments,
                operatorComposesWithOriginal: false,
                originalFDefinition: globalDependencyValues.functionChild[0].stateValues.fDefinitions[arrayKey]
              };
            }
            return {
              setValue: { fDefinitions }
            };
          }
        }
      }
    };
    let originalLatexReturnDeps = stateVariableDefinitions.latex.returnDependencies;
    stateVariableDefinitions.latex.returnDependencies = function(args) {
      let dependencies = originalLatexReturnDeps(args);
      delete dependencies.functionChild;
      return dependencies;
    };
    let originalAllMinimaReturnDeps = stateVariableDefinitions.allMinima.returnDependencies;
    stateVariableDefinitions.allMinima.returnDependencies = function(args) {
      let dependencies = originalAllMinimaReturnDeps(args);
      delete dependencies.functionChildrenInfoToCalculateExtrema;
      return dependencies;
    };
    let originalAllMaximaReturnDeps = stateVariableDefinitions.allMaxima.returnDependencies;
    stateVariableDefinitions.allMaxima.returnDependencies = function(args) {
      let dependencies = originalAllMaximaReturnDeps(args);
      delete dependencies.functionChildrenInfoToCalculateExtrema;
      return dependencies;
    };
    return stateVariableDefinitions;
  }
}
class ClampFunction extends FunctionOperator {
  static componentType = "clampFunction";
  static componentDocs = {
    summary: "A function clamped to a specified output range"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.lowerValue = {
      description: "Lower bound for the operator's range.",
      createComponentOfType: "number",
      createStateVariable: "lowerValue",
      defaultValue: 0,
      public: true
    };
    attributes.upperValue = {
      description: "Upper bound for the operator's range.",
      createComponentOfType: "number",
      createStateVariable: "upperValue",
      defaultValue: 1,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    stateVariableDefinitions.numericalFunctionOperator = {
      additionalStateVariablesDefined: [
        "numericalFunctionOperatorArguments"
      ],
      returnDependencies: () => ({
        lowerValue: {
          dependencyType: "stateVariable",
          variableName: "lowerValue"
        },
        upperValue: {
          dependencyType: "stateVariable",
          variableName: "upperValue"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            numericalFunctionOperator: functionOperatorDefinitions.clampFunction(
              dependencyValues.lowerValue,
              dependencyValues.upperValue
            ),
            numericalFunctionOperatorArguments: [
              dependencyValues.lowerValue,
              dependencyValues.upperValue
            ]
          }
        };
      }
    };
    stateVariableDefinitions.symbolicFunctionOperator = {
      returnDependencies: () => ({
        numericalFunctionOperator: {
          dependencyType: "stateVariable",
          variableName: "numericalFunctionOperator"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            symbolicFunctionOperator: (x2) => me$3.fromAst(
              dependencyValues.numericalFunctionOperator(
                x2.evaluate_to_constant()
              )
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class WrapFunctionPeriodic extends FunctionOperator {
  static componentType = "wrapFunctionPeriodic";
  static componentDocs = {
    summary: "A function wrapped into a periodic output range"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.lowerValue = {
      createComponentOfType: "number",
      createStateVariable: "lowerValue",
      defaultValue: 0,
      public: true,
      description: "Lower bound for the operator's range."
    };
    attributes.upperValue = {
      createComponentOfType: "number",
      createStateVariable: "upperValue",
      defaultValue: 1,
      public: true,
      description: "Upper bound for the operator's range."
    };
    return attributes;
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    stateVariableDefinitions.numericalFunctionOperator = {
      additionalStateVariablesDefined: [
        "numericalFunctionOperatorArguments"
      ],
      returnDependencies: () => ({
        lowerValue: {
          dependencyType: "stateVariable",
          variableName: "lowerValue"
        },
        upperValue: {
          dependencyType: "stateVariable",
          variableName: "upperValue"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            numericalFunctionOperator: functionOperatorDefinitions.wrapFunctionPeriodic(
              dependencyValues.lowerValue,
              dependencyValues.upperValue
            ),
            numericalFunctionOperatorArguments: [
              dependencyValues.lowerValue,
              dependencyValues.upperValue
            ]
          }
        };
      }
    };
    stateVariableDefinitions.symbolicFunctionOperator = {
      returnDependencies: () => ({
        numericalFunctionOperator: {
          dependencyType: "stateVariable",
          variableName: "numericalFunctionOperator"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            symbolicFunctionOperator: (x2) => me$3.fromAst(
              dependencyValues.numericalFunctionOperator(
                x2.evaluate_to_constant()
              )
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Derivative extends FunctionOperator {
  static componentType = "derivative";
  static componentDocs = {
    summary: "The derivative of a function"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.derivVariables = {
      createComponentOfType: "_variableNameList",
      description: "Names of variables to differentiate with respect to."
    };
    attributes.derivVariable = {
      createComponentOfType: "_variableName",
      description: "Name of the variable to differentiate with respect to."
    };
    return attributes;
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    stateVariableDefinitions.operatorBasedOnFormulaIfAvailable = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { operatorBasedOnFormulaIfAvailable: true }
      })
    };
    stateVariableDefinitions.operatorComposesWithOriginal = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { operatorComposesWithOriginal: false }
      })
    };
    stateVariableDefinitions.haveFunctionChild = {
      returnDependencies: () => ({
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveFunctionChild: dependencyValues.functionChild.length > 0
          }
        };
      }
    };
    stateVariableDefinitions.numInputs = {
      description: "Number of input variables.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      stateVariablesDeterminingDependencies: ["haveFunctionChild"],
      returnDependencies({ stateValues }) {
        let dependencies = {
          numInputsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "numInputs",
            variableNames: ["value"]
          },
          functionChild: {
            dependencyType: "child",
            childGroups: ["functions"],
            variableNames: ["numInputs"]
          },
          variablesAttr: {
            dependencyType: "attributeComponent",
            attributeName: "variables",
            variableNames: ["numComponents"],
            dontRecurseToShadowsIfHaveAttribute: "variable"
          },
          variableAttr: {
            dependencyType: "attributeComponent",
            attributeName: "variable",
            dontRecurseToShadowsIfHaveAttribute: "variables"
          }
        };
        if (!stateValues.haveFunctionChild) {
          dependencies.derivVariablesAttr = {
            dependencyType: "attributeComponent",
            attributeName: "derivVariables",
            variableNames: ["variables"],
            dontRecurseToShadowsIfHaveAttribute: "derivVariable"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        if (dependencyValues.numInputsAttr !== null) {
          let numInputs = dependencyValues.numInputsAttr.stateValues.value;
          if (!(numInputs >= 0)) {
            numInputs = 1;
          }
          return { setValue: { numInputs } };
        } else if (dependencyValues.variablesAttr !== null || dependencyValues.variableAttr !== null) {
          return {
            setValue: {
              numInputs: Math.max(
                1,
                dependencyValues.variablesAttr?.stateValues.numComponents || 0
              )
            }
          };
        } else if (dependencyValues.functionChild.length > 0) {
          return {
            setValue: {
              numInputs: dependencyValues.functionChild[0].stateValues.numInputs
            }
          };
        } else if (dependencyValues.derivVariablesAttr !== null) {
          let nUniqueDerivVariables = [
            ...new Set(
              dependencyValues.derivVariablesAttr.stateValues.variables.map(
                (x2) => x2.subscripts_to_strings().tree
              )
            )
          ].length;
          return { setValue: { numInputs: nUniqueDerivVariables } };
        } else {
          return { setValue: { numInputs: 1 } };
        }
      }
    };
    stateVariableDefinitions.variables = {
      description: "Names of the input variables.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      entryPrefixes: ["variable"],
      returnArraySizeDependencies: () => ({
        numInputs: {
          dependencyType: "stateVariable",
          variableName: "numInputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numInputs];
      },
      stateVariablesDeterminingDependencies: ["haveFunctionChild"],
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          variablesAttr: {
            dependencyType: "attributeComponent",
            attributeName: "variables",
            variableNames: ["variables"],
            dontRecurseToShadowsIfHaveAttribute: "variable"
          },
          variableAttr: {
            dependencyType: "attributeComponent",
            attributeName: "variable",
            variableNames: ["value"],
            dontRecurseToShadowsIfHaveAttribute: "variables"
          },
          parentVariableForChild: {
            dependencyType: "parentStateVariable",
            variableName: "variableForChild"
          },
          isInterpolatedFunction: {
            dependencyType: "stateVariable",
            variableName: "isInterpolatedFunction"
          },
          haveFunctionChild: {
            dependencyType: "stateVariable",
            variableName: "haveFunctionChild"
          }
        };
        let dependenciesByKey = {};
        if (stateValues.haveFunctionChild) {
          for (let arrayKey of arrayKeys) {
            dependenciesByKey[arrayKey] = {
              functionChild: {
                dependencyType: "child",
                childGroups: ["functions"],
                variableNames: [
                  "variable" + (Number(arrayKey) + 1)
                ]
              }
            };
          }
        } else {
          globalDependencies.derivVariablesAttr = {
            dependencyType: "attributeComponent",
            attributeName: "derivVariables",
            variableNames: ["variables"],
            dontRecurseToShadowsIfHaveAttribute: "derivVariable"
          };
          globalDependencies.derivVariableAttr = {
            dependencyType: "attributeComponent",
            attributeName: "derivVariable",
            variableNames: ["value"],
            dontRecurseToShadowsIfHaveAttribute: "derivVariables"
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arraySize,
        arrayKeys,
        usedDefault
      }) {
        if (globalDependencyValues.variablesAttr !== null || globalDependencyValues.variableAttr !== null) {
          let variablesSpecified = globalDependencyValues.variablesAttr?.stateValues.variables;
          if (!variablesSpecified) {
            variablesSpecified = [
              globalDependencyValues.variableAttr.stateValues.value
            ];
          }
          return {
            setValue: {
              variables: returnNVariables(
                arraySize[0],
                variablesSpecified
              )
            }
          };
        } else if (globalDependencyValues.haveFunctionChild) {
          let variables = {};
          for (let arrayKey of arrayKeys) {
            variables[arrayKey] = dependencyValuesByKey[arrayKey].functionChild[0].stateValues["variable" + (Number(arrayKey) + 1)];
          }
          return { setValue: { variables } };
        } else if (globalDependencyValues.derivVariablesAttr !== null || globalDependencyValues.derivVariableAttr !== null) {
          let variablesSpecified = [];
          let variablesSpecifiedTrans = [];
          let variablesFromAttribute = globalDependencyValues.derivVariablesAttr?.stateValues.variables;
          if (!variablesFromAttribute) {
            variablesFromAttribute = [
              globalDependencyValues.derivVariableAttr.stateValues.value
            ];
          }
          for (let variable of variablesFromAttribute) {
            let variableTrans = variable.subscripts_to_strings().tree;
            if (!variablesSpecifiedTrans.includes(variableTrans)) {
              variablesSpecified.push(variable);
              variablesSpecifiedTrans.push(variableTrans);
            }
          }
          return {
            setValue: {
              variables: returnNVariables(
                arraySize[0],
                variablesSpecified
              )
            }
          };
        } else if (globalDependencyValues.parentVariableForChild && !usedDefault.parentVariableForChild) {
          return {
            setValue: {
              variables: Array(arraySize[0]).fill(
                globalDependencyValues.parentVariableForChild
              )
            }
          };
        } else {
          return {
            setValue: {
              variables: returnNVariables(arraySize[0], [])
            }
          };
        }
      }
    };
    stateVariableDefinitions.numDerivatives = {
      description: "Number of derivatives applied.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies() {
        let dependencies = {
          derivVariablesAttr: {
            dependencyType: "attributeComponent",
            attributeName: "derivVariables",
            variableNames: ["numComponents"],
            dontRecurseToShadowsIfHaveAttribute: "derivVariable"
          }
        };
        return dependencies;
      },
      definition({ dependencyValues }) {
        if (dependencyValues.derivVariablesAttr !== null) {
          return {
            setValue: {
              numDerivatives: dependencyValues.derivVariablesAttr.stateValues.numComponents
            }
          };
        } else {
          return { setValue: { numDerivatives: 1 } };
        }
      }
    };
    stateVariableDefinitions.derivVariables = {
      description: "Variables with respect to which derivatives are taken.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      entryPrefixes: ["derivVariable"],
      returnArraySizeDependencies: () => ({
        numDerivatives: {
          dependencyType: "stateVariable",
          variableName: "numDerivatives"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDerivatives];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          derivVariablesAttr: {
            dependencyType: "attributeComponent",
            attributeName: "derivVariables",
            variableNames: ["variables"],
            dontRecurseToShadowsIfHaveAttribute: "derivVariable"
          },
          derivVariableAttr: {
            dependencyType: "attributeComponent",
            attributeName: "derivVariable",
            variableNames: ["value"],
            dontRecurseToShadowsIfHaveAttribute: "derivVariables"
          },
          variable1: {
            dependencyType: "stateVariable",
            variableName: "variable1"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        if (globalDependencyValues.derivVariablesAttr !== null) {
          return {
            setValue: {
              derivVariables: globalDependencyValues.derivVariablesAttr.stateValues.variables
            }
          };
        } else if (globalDependencyValues.derivVariableAttr !== null) {
          return {
            setValue: {
              derivVariables: [
                globalDependencyValues.derivVariableAttr.stateValues.value
              ]
            }
          };
        } else {
          return {
            setValue: {
              derivVariables: {
                0: globalDependencyValues.variable1
              }
            }
          };
        }
      }
    };
    stateVariableDefinitions.formulaOperator = {
      returnDependencies: () => ({
        derivVariables: {
          dependencyType: "stateVariable",
          variableName: "derivVariables"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            formulaOperator: function(formula) {
              let formulaIsVectorValued = Array.isArray(formula.tree) && vectorOperators.includes(formula.tree[0]);
              let value = formula.subscripts_to_strings();
              if (formulaIsVectorValued) {
                let nComponents = formula.tree.length - 1;
                let derivComps = [];
                for (let comp = 0; comp < nComponents; comp++) {
                  let valComp = value.get_component(comp);
                  for (let variable of dependencyValues.derivVariables) {
                    valComp = valComp.normalize_applied_functions().derivative(
                      variable.subscripts_to_strings().tree
                    );
                  }
                  derivComps.push(valComp.tree);
                }
                value = me$3.fromAst([
                  value.tree[0],
                  ...derivComps
                ]);
              } else {
                for (let variable of dependencyValues.derivVariables) {
                  value = value.normalize_applied_functions().derivative(
                    variable.subscripts_to_strings().tree
                  );
                }
              }
              return value.strings_to_subscripts();
            }
          }
        };
      }
    };
    stateVariableDefinitions.numericalFunctionOperator = {
      returnDependencies: () => ({
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["returnNumericalDerivatives"],
          variablesOptional: true
        },
        derivVariables: {
          dependencyType: "stateVariable",
          variableName: "derivVariables"
        }
      }),
      additionalStateVariablesDefined: ["returnNumericalDerivatives"],
      definition: function({ dependencyValues }) {
        if (dependencyValues.functionChild.length === 0 || !dependencyValues.functionChild[0].stateValues.returnNumericalDerivatives) {
          return {
            setValue: {
              numericalFunctionOperator: (x2) => NaN,
              returnNumericalDerivatives: null
            }
          };
        }
        let derivativeNumericalFunctionOperator = dependencyValues.functionChild[0].stateValues.returnNumericalDerivatives(
          dependencyValues.derivVariables
        );
        let augmentedReturnNumericalDerivatives = function(derivVariables) {
          let allDerivVariables = [
            ...dependencyValues.derivVariables,
            ...derivVariables
          ];
          return dependencyValues.functionChild[0].stateValues.returnNumericalDerivatives(
            allDerivVariables
          );
        };
        return {
          setValue: {
            numericalFunctionOperator: derivativeNumericalFunctionOperator,
            returnNumericalDerivatives: augmentedReturnNumericalDerivatives
          }
        };
      }
    };
    stateVariableDefinitions.numericalFunctionOperatorArguments = {
      returnDependencies: () => ({
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["numericalDerivativesDefinition"],
          variablesOptional: true
        },
        derivVariables: {
          dependencyType: "stateVariable",
          variableName: "derivVariables"
        }
      }),
      additionalStateVariablesDefined: ["numericalDerivativesDefinition"],
      definition: function({ dependencyValues }) {
        if (dependencyValues.functionChild.length === 0 || !dependencyValues.functionChild[0].stateValues.numericalDerivativesDefinition) {
          return {
            setValue: {
              numericalFunctionOperatorArguments: [],
              numericalDerivativesDefinition: {}
            }
          };
        }
        let derivDefinition = dependencyValues.functionChild[0].stateValues.numericalDerivativesDefinition;
        let augmentedDerivDefinition = { ...derivDefinition };
        if (augmentedDerivDefinition.additionalDerivVariables) {
          augmentedDerivDefinition.additionalDerivVariables = [
            ...dependencyValues.derivVariables,
            ...augmentedDerivDefinition.additionalDerivVariables
          ];
        } else {
          augmentedDerivDefinition.additionalDerivVariables = [
            ...dependencyValues.derivVariables
          ];
        }
        return {
          setValue: {
            numericalFunctionOperatorArguments: [
              derivDefinition,
              dependencyValues.derivVariables
            ],
            numericalDerivativesDefinition: augmentedDerivDefinition
          }
        };
      }
    };
    stateVariableDefinitions.symbolicFunctionOperator = {
      returnDependencies: () => ({
        numericalFunctionOperator: {
          dependencyType: "stateVariable",
          variableName: "numericalFunctionOperator"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            symbolicFunctionOperator: (x2) => me$3.fromAst(
              dependencyValues.numericalFunctionOperator(
                x2.evaluate_to_constant()
              )
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
const FunctionOperators = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ClampFunction,
  Derivative,
  WrapFunctionPeriodic
}, Symbol.toStringTag, { value: "Module" }));
class TextBaseOperatorOfMath extends Text {
  static componentType = "_textOperatorOfMath";
  static rendererType = "text";
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let constructor = this;
    stateVariableDefinitions.value = {
      description: "The text result of applying this operator to its child math values.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            value: constructor.applyTextOperator(
              dependencyValues.mathChildren.map(
                (x2) => x2.stateValues.value
              )
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class ExtractMathOperator extends TextBaseOperatorOfMath {
  static componentType = "extractMathOperator";
  static componentDocs = {
    summary: 'Returns the top-level operator of a math expression as text (e.g. "+", "vector")'
  };
  static applyTextOperator(values2) {
    if (values2.length === 0) {
      return "";
    }
    if (values2.length !== 1) {
      console.warn("MathOperator requires exactly one math child");
      return "";
    }
    if (!Array.isArray(values2[0].tree)) {
      return "";
    }
    return values2[0].tree[0];
  }
}
const TextOperatorsOfMath = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ExtractMathOperator
}, Symbol.toStringTag, { value: "Module" }));
function breakStringsAndOthersIntoComponentsByStringCommas(e3) {
  return null;
}
function returnBreakStringsSugarFunction({
  childrenToComponentFunction,
  mustStripOffOuterParentheses = false
}) {
  return function({ matchedChildren, nComponents, stateIdInfo }) {
    let Nparens = 0;
    let pieces = [];
    let currentPiece = [];
    let strippedParens = false;
    let numChildren = matchedChildren.length;
    if (mustStripOffOuterParentheses) {
      let firstComponent = matchedChildren[0];
      if (typeof firstComponent !== "string" || firstComponent.trimLeft()[0] !== "(") {
        return { success: false };
      }
    }
    for (let [compInd, component] of matchedChildren.entries()) {
      if (typeof component !== "string") {
        currentPiece.push(component);
        continue;
      }
      let s2 = component.trim();
      if (compInd === 0 && mustStripOffOuterParentheses && s2[0] === "(") {
        let lastChild = matchedChildren[numChildren - 1];
        if (typeof lastChild === "string") {
          let sLast = lastChild.trimRight();
          if (sLast[sLast.length - 1] === ")") {
            strippedParens = true;
            s2 = s2.substring(1);
          }
        }
      }
      let beginInd = 0;
      for (let ind = 0; ind < s2.length; ind++) {
        let char = s2[ind];
        if (char === "(") {
          Nparens++;
        }
        if (char === ")") {
          if (Nparens === 0) {
            if (strippedParens && compInd === numChildren - 1 && ind === s2.length - 1) {
              s2 = s2.substring(0, s2.length - 1);
              break;
            }
            return { success: false };
          }
          Nparens--;
        }
        if (char === "," && Nparens === 0) {
          if (ind > beginInd) {
            currentPiece.push(s2.substring(beginInd, ind));
          }
          pieces.push(currentPiece);
          currentPiece = [];
          beginInd = ind + 1;
        }
      }
      if (s2.length > beginInd) {
        currentPiece.push(s2.substring(beginInd, s2.length));
      }
    }
    if (Nparens !== 0) {
      return { success: false };
    }
    pieces.push(currentPiece);
    const newChildren = [];
    for (const piece of pieces) {
      const res = childrenToComponentFunction(
        piece,
        nComponents,
        stateIdInfo
      );
      newChildren.push(res.component);
      nComponents = res.nComponents;
    }
    return {
      success: true,
      newChildren,
      nComponents
    };
  };
}
function breakEmbeddedStringByCommas({ childrenList }) {
  let Nparens = 0;
  let pieces = [];
  let currentPiece = [];
  for (let component of childrenList) {
    if (typeof component !== "string") {
      currentPiece.push(component);
      continue;
    }
    let s2 = component.trim();
    let beginInd = 0;
    for (let ind = 0; ind < s2.length; ind++) {
      let char = s2[ind];
      if (char === "(") {
        Nparens++;
      }
      if (char === ")") {
        if (Nparens === 0) {
          return { success: false };
        }
        Nparens--;
      }
      if (char === "," && Nparens === 0) {
        if (ind > beginInd) {
          let newString = s2.substring(beginInd, ind).trim();
          currentPiece.push(newString);
        }
        pieces.push(currentPiece);
        currentPiece = [];
        beginInd = ind + 1;
      }
    }
    if (s2.length > beginInd) {
      let newString = s2.substring(beginInd, s2.length).trim();
      currentPiece.push(newString);
    }
  }
  if (Nparens !== 0) {
    return { success: false };
  }
  pieces.push(currentPiece);
  return {
    success: true,
    pieces
  };
}
function breakEmbeddedStringsIntoParensPieces({
  componentList,
  removeParens = false
}) {
  let Nparens = 0;
  let pieces = [];
  let currentPiece = [];
  for (let component of componentList) {
    if (typeof component !== "string") {
      if (Nparens === 0) {
        pieces.push([component]);
      } else {
        currentPiece.push(component);
      }
      continue;
    }
    let s2 = component;
    let beginInd = 0;
    for (let ind = 0; ind < s2.length; ind++) {
      let char = s2[ind];
      if (char === "(") {
        if (Nparens === 0 && removeParens) {
          beginInd = ind + 1;
        }
        Nparens++;
      } else if (char === ")") {
        if (Nparens === 0) {
          return { success: false };
        }
        if (Nparens === 1) {
          if (ind + 1 > beginInd) {
            let lastInd = removeParens ? ind : ind + 1;
            let newString = s2.substring(beginInd, lastInd);
            if (newString.length > 0) {
              currentPiece.push(newString);
            }
          }
          pieces.push(currentPiece);
          currentPiece = [];
          beginInd = ind + 1;
        }
        Nparens--;
      } else if (Nparens === 0 && !char.match(/\s/)) {
        return { success: false };
      }
    }
    if (s2.length > beginInd) {
      let newString = s2.substring(beginInd, s2.length);
      currentPiece.push(newString);
    }
  }
  if (Nparens !== 0) {
    return { success: false };
  }
  if (currentPiece.length > 0) {
    pieces.push(currentPiece);
  }
  return {
    success: true,
    pieces
  };
}
class Extremum extends BaseComponent {
  static componentType = "extremum";
  static componentDocs = {
    summary: "A local extremum (min or max) of a function."
  };
  static rendererType = void 0;
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.location = {
      createComponentOfType: "math"
    };
    attributes.value = {
      createComponentOfType: "math"
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "number", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakIntoLocationValueByCommas = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let childrenToComponentFunction = (x2, newNComponents, newStateIdInfo2) => ({
        component: {
          type: "serialized",
          componentType: "math",
          componentIdx: newNComponents++,
          stateId: newStateIdInfo2 ? `${newStateIdInfo2.prefix}${newStateIdInfo2.num++}` : void 0,
          children: x2,
          attributes: {},
          doenetAttributes: {},
          state: {}
        },
        nComponents: newNComponents
      });
      let breakFunction = returnBreakStringsSugarFunction({
        childrenToComponentFunction,
        mustStripOffOuterParentheses: true
      });
      const newStateIdInfo = stateIdInfo ? { prefix: stateIdInfo.prefix, num: stateIdInfo.num } : void 0;
      let result = breakFunction({
        matchedChildren,
        nComponents,
        stateIdInfo: newStateIdInfo
      });
      if (!result.success && matchedChildren.length === 1) {
        return {
          success: true,
          newAttributes: {
            value: {
              type: "component",
              name: "value",
              component: {
                type: "serialized",
                componentType: "math",
                componentIdx: nComponents++,
                stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
                children: matchedChildren,
                attributes: {},
                doenetAttributes: {},
                state: {}
              }
            }
          },
          nComponents
        };
      }
      if (result.success) {
        nComponents = result.nComponents;
        if (stateIdInfo) {
          stateIdInfo.num = newStateIdInfo.num;
        }
        if (result.newChildren.length === 1) {
          return {
            success: true,
            newAttributes: {
              value: {
                type: "component",
                name: "value",
                component: result.newChildren[0]
              }
            },
            nComponents
          };
        } else if (result.newChildren.length === 2) {
          let locationComponent = result.newChildren[0];
          let valueComponent = result.newChildren[1];
          let newAttributes = {
            location: {
              type: "component",
              name: "location",
              component: locationComponent
            },
            value: {
              type: "component",
              name: "value",
              component: valueComponent
            }
          };
          if (locationComponent.children.length === 0 || locationComponent.children.length === 1 && typeof locationComponent.children[0] === "string" && locationComponent.children[0].trim() === "") {
            delete newAttributes.location;
          }
          if (valueComponent.children.length === 0 || valueComponent.children.length === 1 && typeof valueComponent.children[0] === "string" && valueComponent.children[0].trim() === "") {
            delete newAttributes.value;
          }
          return {
            success: true,
            newAttributes,
            nComponents
          };
        } else {
          return { success: false };
        }
      }
      return result;
    };
    sugarInstructions.push({
      childrenRegex: /s+(.*s)?/,
      replacementFunction: breakIntoLocationValueByCommas
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "points",
        componentTypes: ["point"]
      }
    ];
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    let componentClass = this;
    stateVariableDefinitions.value = {
      description: "Coordinates of the extremum.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      defaultValue: null,
      hasEssential: true,
      additionalStateVariablesDefined: [
        {
          variableName: "location",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "math"
          },
          defaultValue: null,
          hasEssential: true,
          description: "The x-coordinate of the extremum."
        }
      ],
      returnDependencies: () => ({
        extremumChild: {
          dependencyType: "child",
          childGroups: ["points"],
          variableNames: ["numDimensions", "xs"]
        },
        location: {
          dependencyType: "attributeComponent",
          attributeName: "location",
          variableNames: ["value"]
        },
        value: {
          dependencyType: "attributeComponent",
          attributeName: "value",
          variableNames: ["value"]
        }
      }),
      definition: function({ dependencyValues }) {
        let location, value;
        if (dependencyValues.extremumChild.length > 0) {
          let extremumChild = dependencyValues.extremumChild[0];
          if (extremumChild.stateValues.numDimensions !== 2) {
            console.log(
              "Cannot determine " + componentClass.componentType + " from a point that isn't 2D"
            );
            location = null;
            value = null;
          } else {
            location = extremumChild.stateValues.xs[0];
            value = extremumChild.stateValues.xs[1];
          }
        } else {
          if (dependencyValues.location !== null) {
            location = dependencyValues.location.stateValues.value;
          }
          if (dependencyValues.value !== null) {
            value = dependencyValues.value.stateValues.value;
          }
        }
        let setValue = {};
        let useEssentialOrDefaultValue = {};
        let haveNewValues = false;
        let haveEssential = false;
        if (location === void 0) {
          useEssentialOrDefaultValue.location = true;
          haveEssential = true;
        } else {
          setValue.location = location;
          haveNewValues = true;
        }
        if (value === void 0) {
          useEssentialOrDefaultValue.value = true;
          haveEssential = true;
        } else {
          setValue.value = value;
          haveNewValues = true;
        }
        let result = {};
        if (haveNewValues) {
          result.setValue = setValue;
        }
        if (haveEssential) {
          result.useEssentialOrDefaultValue = useEssentialOrDefaultValue;
        }
        return result;
      }
    };
    return stateVariableDefinitions;
  }
}
class Extrema extends BaseComponent {
  static componentType = "extrema";
  static componentDocs = {
    summary: "Computes local extrema of a function."
  };
  static rendererType = void 0;
  static componentTypeSingular = "extremum";
  static get componentTypeCapitalized() {
    return this.componentType.charAt(0).toUpperCase() + this.componentType.slice(1);
  }
  static excludeFromSchema = true;
  static variableForIndexAsProp = "extrema";
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let extremaClass = this;
    let createExtremumList = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let results = breakEmbeddedStringsIntoParensPieces({
        componentList: matchedChildren
      });
      if (results.success !== true) {
        return { success: false };
      }
      return {
        success: true,
        newChildren: results.pieces.map(function(piece) {
          if (piece.length > 1 || typeof piece[0] === "string") {
            return {
              type: "serialized",
              componentType: extremaClass.componentTypeSingular,
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: piece,
              attributes: {},
              doenetAttributes: {},
              state: {}
            };
          } else {
            return piece[0];
          }
        }),
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: createExtremumList
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "extrema",
        componentTypes: [this.componentTypeSingular]
      },
      {
        group: "points",
        componentTypes: ["point"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let extremaClass = this;
    stateVariableDefinitions["n" + extremaClass.componentTypeCapitalized] = {
      additionalStateVariablesDefined: ["childIdentities"],
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["extrema", "points"]
        }
      }),
      definition: function({ dependencyValues }) {
        let extremeVarName = "n" + extremaClass.componentTypeCapitalized;
        return {
          setValue: {
            [extremeVarName]: dependencyValues.children.length,
            childIdentities: dependencyValues.children
          }
        };
      }
    };
    stateVariableDefinitions[extremaClass.componentType] = {
      isArray: true,
      numDimensions: 2,
      isLocation: true,
      entryPrefixes: [
        extremaClass.componentTypeSingular,
        extremaClass.componentTypeSingular + "Locations",
        extremaClass.componentTypeSingular + "Location",
        extremaClass.componentTypeSingular + "Values",
        extremaClass.componentTypeSingular + "Value"
      ],
      schemaSubarrays: {
        [extremaClass.componentTypeSingular + "Locations"]: {
          numDimensions: 1,
          description: `The x-coordinates of the ${extremaClass.componentType}.`
        },
        [extremaClass.componentTypeSingular + "Values"]: {
          numDimensions: 1,
          description: `The values of the ${extremaClass.componentType}.`
        }
      },
      stateVariablesDeterminingDependencies: ["childIdentities"],
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if ([
          extremaClass.componentTypeSingular,
          extremaClass.componentTypeSingular + "Location",
          extremaClass.componentTypeSingular + "Value"
        ].includes(arrayEntryPrefix)) {
          let extremumInd = Number(varEnding) - 1;
          if (Number.isInteger(extremumInd) && extremumInd >= 0) {
            if (!arraySize || extremumInd < arraySize[0]) {
              if (arrayEntryPrefix === extremaClass.componentTypeSingular) {
                return [extremumInd + ",0", extremumInd + ",1"];
              } else if (arrayEntryPrefix === extremaClass.componentTypeSingular + "Location") {
                return [extremumInd + ",0"];
              } else {
                return [extremumInd + ",1"];
              }
            } else {
              return [];
            }
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === extremaClass.componentTypeSingular + "Locations") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,0"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",0");
        } else if (arrayEntryPrefix === extremaClass.componentTypeSingular + "Values") {
          if (varEnding !== "") {
            return [];
          }
          if (!arraySize) {
            return ["0,1"];
          }
          return Array.from(Array(arraySize[0]), (_2, i2) => i2 + ",1");
        } else {
          return [];
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        let [ind1, ind2] = arrayKey.split(",");
        if (ind2 === "0") {
          return extremaClass.componentTypeSingular + "Location" + (Number(ind1) + 1);
        } else {
          return extremaClass.componentTypeSingular + "Value" + (Number(ind1) + 1);
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === extremaClass.componentType) {
          if (propIndex.length === 1) {
            return extremaClass.componentTypeSingular + propIndex[0];
          } else {
            let componentNum = Number(propIndex[0]);
            if (Number.isInteger(componentNum) && componentNum > 0) {
              if (propIndex[1] === 1) {
                return extremaClass.componentTypeSingular + "Location" + componentNum;
              } else if (propIndex[1] === 2) {
                return extremaClass.componentTypeSingular + "Value" + componentNum;
              }
            }
            return null;
          }
        }
        if (varName === extremaClass.componentTypeSingular + "Locations") {
          return extremaClass.componentTypeSingular + "Location" + propIndex[0];
        }
        if (varName === extremaClass.componentTypeSingular + "Values") {
          return extremaClass.componentTypeSingular + "Value" + propIndex[0];
        }
        let typeLen = extremaClass.componentTypeSingular.length;
        if (varName.slice(0, typeLen) === extremaClass.componentTypeSingular) {
          let componentNum = Number(varName.slice(typeLen));
          if (Number.isInteger(componentNum) && componentNum > 0) {
            if (propIndex[0] === 1) {
              return extremaClass.componentTypeSingular + "Location" + componentNum;
            } else if (propIndex[0] === 2) {
              return extremaClass.componentTypeSingular + "Value" + componentNum;
            }
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numChildren: {
          dependencyType: "stateVariable",
          variableName: "n" + extremaClass.componentTypeCapitalized
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numChildren, 2];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [extremumInd, dim] = arrayKey.split(",");
          let varName;
          if (stateValues.childIdentities[extremumInd].componentType === extremaClass.componentTypeSingular) {
            varName = Number(dim) === 0 ? "location" : "value";
          } else {
            varName = "x" + (Number(dim) + 1);
          }
          dependenciesByKey[arrayKey] = {
            child: {
              dependencyType: "child",
              childGroups: ["extrema", "points"],
              variableNames: [varName],
              childIndices: [extremumInd]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let extrema = {};
        for (let arrayKey of arrayKeys) {
          let child = dependencyValuesByKey[arrayKey].child[0];
          if (child) {
            let dim = arrayKey.split(",")[1];
            let varName = Number(dim) === 0 ? "location" : "value";
            if (varName in child.stateValues) {
              extrema[arrayKey] = child.stateValues[varName];
            } else {
              extrema[arrayKey] = child.stateValues["x" + (Number(dim) + 1)];
            }
          }
        }
        return { setValue: { [extremaClass.componentType]: extrema } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues[extremaClass.componentType]) {
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].extremumChild,
            desiredValue: desiredStateVariableValues[extremaClass.componentType][arrayKey],
            childIndex: 0,
            variableIndex: 0
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    return stateVariableDefinitions;
  }
}
const Extrema$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Extrema,
  Extremum
}, Symbol.toStringTag, { value: "Module" }));
class InlineRenderInlineChildren extends InlineComponent {
  static componentType = "_inlineRenderInlineChildren";
  static renderChildren = true;
  static includeBlankStringChildren = true;
  // Note: these atributes are used just for the text state variable, below.
  // They are ignored in the renderers themselves.
  static beginTextDelimiter = "";
  static endTextDelimiter = "";
  static returnChildGroups() {
    return [
      {
        group: "inlines",
        componentTypes: ["_inline"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let componentClass = this;
    stateVariableDefinitions.text = {
      description: "The combined text content of this component's children, with optional begin/end delimiters.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlines"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let text = textFromChildren(dependencyValues.inlineChildren);
        text = componentClass.beginTextDelimiter + text + componentClass.endTextDelimiter;
        return { setValue: { text } };
      }
    };
    return stateVariableDefinitions;
  }
}
class Em extends InlineRenderInlineChildren {
  static componentType = "em";
  static componentDocs = {
    summary: "Emphasized inline text (italic by default)"
  };
}
class Alert extends InlineRenderInlineChildren {
  static componentType = "alert";
  static componentDocs = {
    summary: "Bold text"
  };
}
let Q$2 = class Q extends InlineRenderInlineChildren {
  static componentType = "q";
  static componentDocs = {
    summary: "Inline double-quoted text"
  };
  static beginTextDelimiter = '"';
  static endTextDelimiter = '"';
};
class SQ extends InlineRenderInlineChildren {
  static componentType = "sq";
  static componentDocs = {
    summary: "Inline single-quoted text"
  };
  static beginTextDelimiter = "'";
  static endTextDelimiter = "'";
}
class Term extends InlineRenderInlineChildren {
  static componentType = "term";
  static componentDocs = {
    summary: "An inline term, styled distinctively"
  };
  static rendererType = "alert";
}
let C$2 = class C extends InlineRenderInlineChildren {
  static componentType = "c";
  static componentDocs = {
    summary: "Inline code text"
  };
};
class Tag extends InlineRenderInlineChildren {
  static componentType = "tag";
  static componentDocs = {
    summary: "Renders enclosed text as an opening tag, `< >`"
  };
  static rendererType = "tag";
  static beginTextDelimiter = "<";
  static endTextDelimiter = ">";
}
class Tage extends InlineRenderInlineChildren {
  static componentType = "tage";
  static componentDocs = {
    summary: "Renders enclosed text as a self-closing tag, `< />`"
  };
  static rendererType = "tag";
  static beginTextDelimiter = "<";
  static endTextDelimiter = "/>";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.selfClosed = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { selfClosed: true } })
    };
    return stateVariableDefinitions;
  }
}
class Tagc extends InlineRenderInlineChildren {
  static componentType = "tagc";
  static componentDocs = {
    summary: "Renders enclosed text as a closing tag, `</ >`"
  };
  static rendererType = "tag";
  static beginTextDelimiter = "</";
  static endTextDelimiter = ">";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.closing = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { closing: true } })
    };
    return stateVariableDefinitions;
  }
}
class Attr extends InlineRenderInlineChildren {
  static componentType = "attr";
  static componentDocs = {
    summary: "Renders a styled attribute name"
  };
  static rendererType = "c";
}
const ParagraphMarkup = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Alert,
  Attr,
  C: C$2,
  Em,
  Q: Q$2,
  SQ,
  Tag,
  Tagc,
  Tage,
  Term
}, Symbol.toStringTag, { value: "Module" }));
class SingleCharacter extends InlineComponent {
  static componentType = "_singleCharacterInline";
  // Note: this attribute is just for the text state variable, below.
  // It is ignored in the renderers themselves.
  static unicodeCharacter = "";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.text = {
      description: "The unicode character rendered by this component.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { text: this.unicodeCharacter } })
    };
    return stateVariableDefinitions;
  }
}
class Ndash extends SingleCharacter {
  static componentType = "ndash";
  static componentDocs = { summary: "Renders an en-dash (–) character" };
  static unicodeCharacter = "–";
}
class Mdash extends SingleCharacter {
  static componentType = "mdash";
  static componentDocs = { summary: "Renders an em-dash (—) character" };
  static unicodeCharacter = "—";
}
class NBSP extends SingleCharacter {
  static componentType = "nbsp";
  static componentDocs = {
    summary: "Renders a non-breaking space character"
  };
  static unicodeCharacter = " ";
}
class Ellipsis extends SingleCharacter {
  static componentType = "ellipsis";
  static componentDocs = {
    summary: "Renders a horizontal ellipsis (…) character"
  };
  static unicodeCharacter = "…";
}
class Lq extends SingleCharacter {
  static componentType = "lq";
  static componentDocs = {
    summary: "Renders a left double quotation mark (“)"
  };
  static unicodeCharacter = "“";
}
class Rq extends SingleCharacter {
  static componentType = "rq";
  static componentDocs = {
    summary: "Renders a right double quotation mark (”)"
  };
  static unicodeCharacter = "”";
}
class Lsq extends SingleCharacter {
  static componentType = "lsq";
  static componentDocs = {
    summary: "Renders a left single quotation mark (‘)"
  };
  static unicodeCharacter = "‘";
}
class Rsq extends SingleCharacter {
  static componentType = "rsq";
  static componentDocs = {
    summary: "Renders a right single quotation mark (’)"
  };
  static unicodeCharacter = "’";
}
const SingleCharacterComponents = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Ellipsis,
  Lq,
  Lsq,
  Mdash,
  NBSP,
  Ndash,
  Rq,
  Rsq
}, Symbol.toStringTag, { value: "Module" }));
function returnDefaultFeedbackDefinitions() {
  return {
    numericalerror: `Credit reduced because numbers in your answer weren't quite right.  Did you round too much?`,
    goodjob: `Good job!`,
    onesignerror: `Credit reduced because it appears that you made a sign error.`,
    twosignerrors: `Credit reduced because it appears that you made two sign errors.`
  };
}
function returnFeedbackDefinitionStateVariables() {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.setupChildren = {
    returnDependencies: () => ({
      setupChildren: {
        dependencyType: "child",
        childGroups: ["setups"],
        proceedIfAllChildrenNotMatched: true
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: { setupChildren: dependencyValues.setupChildren }
      };
    }
  };
  stateVariableDefinitions.feedbackDefinitions = {
    stateVariablesDeterminingDependencies: ["setupChildren"],
    returnDependencies({ stateValues }) {
      let dependencies = {
        ancestorWithFeedback: {
          dependencyType: "ancestor",
          variableNames: ["feedbackDefinitions"]
        },
        feedbackDefinitionSetupChildren: {
          dependencyType: "child",
          childGroups: ["feedbackDefinitions", "setups"],
          variableNames: ["feedbackDefinition"],
          variablesOptional: true,
          proceedIfAllChildrenNotMatched: true
        }
      };
      for (let setupChild of stateValues.setupChildren) {
        dependencies[`feedbackDefinitionsOf${setupChild.componentIdx}`] = {
          dependencyType: "child",
          parentIdx: setupChild.componentIdx,
          childGroups: ["feedbackDefinitions"],
          variableNames: ["feedbackDefinition"]
        };
      }
      return dependencies;
    },
    definition({ dependencyValues }) {
      let feedbackDefinitions = {};
      let startingFeedbackDefinitions;
      if (dependencyValues.ancestorWithFeedback) {
        startingFeedbackDefinitions = dependencyValues.ancestorWithFeedback.stateValues.feedbackDefinitions;
      }
      if (!startingFeedbackDefinitions) {
        startingFeedbackDefinitions = returnDefaultFeedbackDefinitions();
      }
      Object.assign(feedbackDefinitions, startingFeedbackDefinitions);
      let feedbackDefinitionChildren = [];
      for (let child of dependencyValues.feedbackDefinitionSetupChildren) {
        if (child.componentType === "setup") {
          feedbackDefinitionChildren.push(
            ...dependencyValues[`feedbackDefinitionsOf${child.componentIdx}`]
          );
        } else {
          feedbackDefinitionChildren.push(child);
        }
      }
      for (let child of feedbackDefinitionChildren) {
        Object.assign(
          feedbackDefinitions,
          child.stateValues.feedbackDefinition
        );
      }
      return { setValue: { feedbackDefinitions } };
    }
  };
  return stateVariableDefinitions;
}
function findDynamicChildren(component) {
  return component.definingChildren?.findLast(
    (child) => child.componentType === "_dynamicChildren"
  );
}
async function addChildrenToDynamicChild(component, args) {
  const dynamicChildren = findDynamicChildren(component);
  if (!dynamicChildren) {
    return void 0;
  }
  return await dynamicChildren.addChildren(args);
}
async function deleteChildrenFromDynamicChild(component, args) {
  const dynamicChildren = findDynamicChildren(component);
  if (!dynamicChildren) {
    return void 0;
  }
  return await dynamicChildren.deleteChildren(args);
}
const sectionTitleStateKeys = ["completed", "inProgress", "notStarted"];
function titleStateKeyFromCredit(creditAchieved) {
  if (creditAchieved === 1) {
    return "completed";
  }
  if (creditAchieved > 0) {
    return "inProgress";
  }
  return "notStarted";
}
function resolveSectionTitleLightColorSpec({
  dependencyValues,
  usedDefault,
  ownColorName,
  parentColors,
  parentSources,
  parentIsBoxedOrCollapsible,
  stateKey
}) {
  if (!usedDefault[ownColorName]) {
    return {
      value: dependencyValues[ownColorName],
      source: {
        authored: true,
        colorName: ownColorName,
        inherited: false,
        priorBoxedOrCollapsibleAncestor: false
      }
    };
  }
  const inheritedValue = parentColors?.[stateKey];
  if (typeof inheritedValue === "string") {
    return {
      value: inheritedValue,
      source: parentSources?.[stateKey] ? {
        ...parentSources[stateKey],
        inherited: true,
        priorBoxedOrCollapsibleAncestor: parentSources[stateKey].priorBoxedOrCollapsibleAncestor || parentIsBoxedOrCollapsible
      } : {
        authored: false,
        colorName: ownColorName,
        inherited: true,
        priorBoxedOrCollapsibleAncestor: parentIsBoxedOrCollapsible
      }
    };
  }
  return {
    value: dependencyValues[ownColorName],
    source: {
      authored: false,
      colorName: ownColorName,
      inherited: false,
      priorBoxedOrCollapsibleAncestor: false
    }
  };
}
function deriveSectionTitleDarkModeColor(lightColor) {
  return suggestAccessibleDarkModeColorAgainst({
    startColor: invertLightness(lightColor),
    partnerColor: "#ffffff",
    channelRole: "background",
    threshold: TEXT_CONTRAST_THRESHOLD
  });
}
function resolveSectionTitleDarkColorSpec({
  dependencyValues,
  usedDefault,
  ownDarkColorName,
  ownLightColorName,
  parentColorsDarkMode,
  parentSourcesDarkMode,
  parentIsBoxedOrCollapsible,
  stateKey
}) {
  if (!usedDefault[ownDarkColorName]) {
    return {
      value: dependencyValues[ownDarkColorName],
      source: {
        authored: true,
        colorName: ownDarkColorName,
        inherited: false,
        priorBoxedOrCollapsibleAncestor: false
      }
    };
  }
  if (!usedDefault[ownLightColorName]) {
    const derivedDarkColor = deriveSectionTitleDarkModeColor(
      dependencyValues[ownLightColorName]
    );
    if (derivedDarkColor !== null) {
      return {
        value: derivedDarkColor,
        source: {
          authored: true,
          colorName: ownLightColorName,
          inherited: false,
          priorBoxedOrCollapsibleAncestor: false
        }
      };
    }
    return {
      value: dependencyValues[ownDarkColorName],
      source: {
        authored: false,
        colorName: ownDarkColorName,
        inherited: false,
        priorBoxedOrCollapsibleAncestor: false
      }
    };
  }
  const inheritedValue = parentColorsDarkMode?.[stateKey];
  if (typeof inheritedValue === "string") {
    return {
      value: inheritedValue,
      source: parentSourcesDarkMode?.[stateKey] ? {
        ...parentSourcesDarkMode[stateKey],
        inherited: true,
        priorBoxedOrCollapsibleAncestor: parentSourcesDarkMode[stateKey].priorBoxedOrCollapsibleAncestor || parentIsBoxedOrCollapsible
      } : {
        authored: false,
        colorName: ownDarkColorName,
        inherited: true,
        priorBoxedOrCollapsibleAncestor: parentIsBoxedOrCollapsible
      }
    };
  }
  return {
    value: dependencyValues[ownDarkColorName],
    source: {
      authored: false,
      colorName: ownDarkColorName,
      inherited: false,
      priorBoxedOrCollapsibleAncestor: false
    }
  };
}
function shouldEmitSectionTitleColorDiagnostic({ source }) {
  if (!source?.authored) {
    return false;
  }
  if (!source.inherited) {
    return true;
  }
  return !source.priorBoxedOrCollapsibleAncestor;
}
function addSectionTitleColorContrastDiagnostic({
  diagnostics,
  authorSet,
  colorValue,
  colorName: colorName2,
  textColor,
  canvasColor,
  modeSuffix = ""
}) {
  if (!authorSet || !colorValue) {
    return;
  }
  const ratio = compositedContrastRatio({
    foreground: textColor,
    canvas: canvasColor,
    background: colorValue
  });
  if (ratio !== null && ratio < TEXT_CONTRAST_THRESHOLD) {
    diagnostics.push({
      type: "accessibility",
      level: 1,
      message: `${colorName2} has insufficient contrast for the section heading text${modeSuffix} (${ratio.toFixed(2)}:1; requires at least ${TEXT_CONTRAST_THRESHOLD}:1).`
    });
  }
}
class SectioningComponent extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAllAnswers: this.submitAllAnswers.bind(this),
      revealSection: this.revealSection.bind(this),
      closeSection: this.closeSection.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this),
      addChildren: this.addChildren.bind(this),
      deleteChildren: this.deleteChildren.bind(this)
    });
  }
  static componentType = "_sectioningComponent";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static includeBlankStringChildren = true;
  static createsVariants = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    let scoredSectionAttributes = returnScoredSectionAttributes();
    Object.assign(attributes, scoredSectionAttributes);
    attributes.boxed = {
      createComponentOfType: "boolean",
      createStateVariable: "boxedPreliminary",
      defaultValue: false,
      description: "Whether to render this section with a visible box around it."
    };
    attributes.includeAutoName = {
      createComponentOfType: "boolean",
      createStateVariable: "includeAutoName",
      defaultValue: false,
      public: true,
      description: 'Whether to include the auto-generated section name (e.g. "Section") in the rendered title.'
    };
    attributes.includeAutoNumber = {
      createComponentOfType: "boolean",
      createStateVariable: "includeAutoNumber",
      defaultValue: false,
      public: true,
      description: "Whether to include the auto-generated section number in the rendered title."
    };
    attributes.noAutoTitle = {
      createComponentOfType: "boolean",
      createStateVariable: "noAutoTitle",
      defaultValue: false,
      public: true,
      description: "Whether to suppress the auto-generated title entirely."
    };
    attributes.includeAutoNameIfNoTitle = {
      createComponentOfType: "boolean",
      createStateVariable: "includeAutoNameIfNoTitle",
      defaultValue: true,
      public: true,
      description: "Whether to include the auto-generated name when no explicit title is provided."
    };
    attributes.includeAutoNumberIfNoTitle = {
      createComponentOfType: "boolean",
      createStateVariable: "includeAutoNumberIfNoTitle",
      defaultValue: true,
      public: true,
      description: "Whether to include the auto-generated number when no explicit title is provided."
    };
    attributes.asList = {
      createComponentOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether to render this section's children as a list."
    };
    attributes.collapsible = {
      createComponentOfType: "boolean",
      createStateVariable: "collapsible",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether the section can be collapsed and expanded."
    };
    attributes.startOpen = {
      createComponentOfType: "boolean",
      createStateVariable: "startOpen",
      defaultValue: true,
      description: "Whether the collapsible section starts in the open state."
    };
    attributes.level = {
      createComponentOfType: "integer",
      description: "The heading level for this section (overrides the default level inferred from nesting)."
    };
    attributes.renameTo = {
      createComponentOfType: "text",
      description: 'Override the auto-generated section name (e.g. rename "Section" to a custom label).'
    };
    attributes.completedColor = {
      createComponentOfType: "text",
      createStateVariable: "completedColor",
      defaultValue: "var(--lightGreen)",
      description: "Color used to indicate this section has been completed."
    };
    attributes.inProgressColor = {
      createComponentOfType: "text",
      createStateVariable: "inProgressColor",
      defaultValue: "var(--mainGray)",
      description: "Color used to indicate this section is in progress."
    };
    attributes.notStartedColor = {
      createComponentOfType: "text",
      createStateVariable: "notStartedColor",
      defaultValue: "var(--mainGray)",
      description: "Color used to indicate this section has not been started."
    };
    attributes.completedColorDarkMode = {
      createComponentOfType: "text",
      createStateVariable: "completedColorDarkMode",
      // Dark green; white text contrast ≈ 7.9:1 (passes WCAG AA and AAA).
      defaultValue: "#1a5e20",
      description: "Color used to indicate this section has been completed (dark mode). If omitted, the dark-mode color is derived from `completedColor` when that attribute is explicitly set; otherwise falls back to a dark green that meets WCAG AA contrast for white text."
    };
    attributes.inProgressColorDarkMode = {
      createComponentOfType: "text",
      createStateVariable: "inProgressColorDarkMode",
      // Dark gray; white text contrast ≈ 11.4:1 (passes WCAG AA and AAA).
      defaultValue: "#3a3a3a",
      description: "Color used to indicate this section is in progress (dark mode). If omitted, the dark-mode color is derived from `inProgressColor` when that attribute is explicitly set; otherwise falls back to a dark gray that meets WCAG AA contrast for white text."
    };
    attributes.notStartedColorDarkMode = {
      createComponentOfType: "text",
      createStateVariable: "notStartedColorDarkMode",
      // Dark gray; white text contrast ≈ 11.4:1 (passes WCAG AA and AAA).
      defaultValue: "#3a3a3a",
      description: "Color used to indicate this section has not been started (dark mode). If omitted, the dark-mode color is derived from `notStartedColor` when that attribute is explicitly set; otherwise falls back to a dark gray that meets WCAG AA contrast for white text."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "variantControls",
        componentTypes: ["variantControl"]
      },
      {
        group: "titles",
        componentTypes: ["title"]
      },
      {
        group: "setups",
        componentTypes: ["setup"]
      },
      {
        group: "styleDefinitions",
        componentTypes: ["styleDefinition"]
      },
      {
        group: "feedbackDefinitions",
        componentTypes: ["feedbackDefinition"]
      },
      {
        group: "cascadeMessages",
        componentTypes: ["cascadeMessage"]
      },
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let componentClass = this;
    let styleDefinitionStateVariables = returnStyleDefinitionStateVariables();
    Object.assign(stateVariableDefinitions, styleDefinitionStateVariables);
    let feedbackDefinitionStateVariables = returnFeedbackDefinitionStateVariables();
    Object.assign(
      stateVariableDefinitions,
      feedbackDefinitionStateVariables
    );
    Object.assign(
      stateVariableDefinitions,
      returnScoredSectionStateVariableDefinition()
    );
    stateVariableDefinitions.isListItem = {
      forRenderer: true,
      returnDependencies: () => ({
        parentAsList: {
          dependencyType: "parentStateVariable",
          variableName: "asList"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            isListItem: Boolean(dependencyValues.parentAsList)
          }
        };
      }
    };
    stateVariableDefinitions.boxed = {
      description: "Whether this section is rendered with a visible box around it.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        boxedPreliminary: {
          dependencyType: "stateVariable",
          variableName: "boxedPreliminary"
        },
        parentBoxAll: {
          dependencyType: "parentStateVariable",
          variableName: "boxAll"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let boxed = dependencyValues.boxedPreliminary;
        if (usedDefault.boxedPreliminary && typeof dependencyValues.parentBoxAll === "boolean" && !usedDefault.parentBoxAll) {
          boxed = dependencyValues.parentBoxAll;
        }
        return { setValue: { boxed } };
      }
    };
    stateVariableDefinitions.enumeration = {
      stateVariablesDeterminingDependencies: ["isListItem"],
      additionalStateVariablesDefined: [
        {
          variableName: "sectionNumber",
          description: "The displayed number for this section.",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "text"
          },
          forRenderer: true
        }
      ],
      mustEvaluate: true,
      // must evaluate to make sure all counters are accounted for
      returnDependencies: ({ stateValues }) => {
        let dependencies = {
          isListItem: {
            dependencyType: "stateVariable",
            variableName: "isListItem"
          }
        };
        if (stateValues.isListItem) {
          dependencies.countAmongSiblings = {
            dependencyType: "countAmongSiblings",
            componentType: "_sectioningComponent",
            includeInheritedComponentTypes: true
          };
        } else {
          dependencies.sectioningCounter = {
            dependencyType: "counter",
            counterName: "sectioning"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        if (dependencyValues.isListItem) {
          let sectionNumber = dependencyValues.countAmongSiblings;
          let enumeration = [sectionNumber];
          return { setValue: { enumeration, sectionNumber } };
        } else {
          let sectionNumber = String(
            dependencyValues.sectioningCounter
          );
          return {
            setValue: {
              enumeration: [sectionNumber],
              sectionNumber
            }
          };
        }
      }
    };
    stateVariableDefinitions.sectionName = {
      returnDependencies: () => ({
        renameToAttr: {
          dependencyType: "attributeComponent",
          attributeName: "renameTo",
          variableNames: ["value"]
        }
      }),
      definition: ({ dependencyValues }) => {
        if (dependencyValues.renameToAttr) {
          return {
            setValue: {
              sectionName: dependencyValues.renameToAttr.stateValues.value
            }
          };
        } else {
          return { setValue: { sectionName: componentClass.name } };
        }
      }
    };
    stateVariableDefinitions.titleChildName = {
      forRenderer: true,
      returnDependencies: () => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["titles"]
        }
      }),
      definition({ dependencyValues }) {
        let titleChildName = null;
        if (dependencyValues.titleChild.length > 0) {
          titleChildName = dependencyValues.titleChild[dependencyValues.titleChild.length - 1].componentIdx;
        }
        return {
          setValue: { titleChildName }
        };
      }
    };
    stateVariableDefinitions.hideChildren = {
      returnDependencies: () => ({
        parentChildrenToHideChildren: {
          dependencyType: "parentStateVariable",
          variableName: "childrenToHideChildren"
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let hideChildren = Boolean(
          dependencyValues.parentChildrenToHideChildren?.includes(
            componentIdx
          )
        );
        return { setValue: { hideChildren } };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      additionalStateVariablesDefined: ["firstVisibleChild"],
      returnDependencies: () => ({
        titleChildren: {
          dependencyType: "child",
          childGroups: ["titles"]
        },
        allChildren: {
          dependencyType: "child",
          childGroups: [
            "anything",
            "variantControls",
            "titles",
            "setups",
            "cascadeMessages"
          ]
        },
        titleChildName: {
          dependencyType: "stateVariable",
          variableName: "titleChildName"
        },
        asList: {
          dependencyType: "stateVariable",
          variableName: "asList"
        },
        hideChildren: {
          dependencyType: "stateVariable",
          variableName: "hideChildren"
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        const childIndicesToRender = [];
        let firstVisibleChild = null;
        let allTitleChildNames = dependencyValues.titleChildren.map(
          (x2) => x2.componentIdx
        );
        for (let [
          ind,
          child
        ] of dependencyValues.allChildren.entries()) {
          if (dependencyValues.hideChildren && typeof child === "string") {
            continue;
          }
          if (dependencyValues.asList) {
            if (child.componentIdx === dependencyValues.titleChildName || componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "_sectioningComponent"
            }) || ["introduction", "conclusion"].includes(
              child.componentType
            )) {
              childIndicesToRender.push(ind);
              if (firstVisibleChild === null && !dependencyValues.hideChildren) {
                firstVisibleChild = child;
              }
            }
          } else if (typeof child !== "object" || !allTitleChildNames.includes(child.componentIdx) || child.componentIdx === dependencyValues.titleChildName) {
            childIndicesToRender.push(ind);
            if (firstVisibleChild === null && !dependencyValues.hideChildren && (typeof child === "object" || child.trim() !== "")) {
              firstVisibleChild = child;
            }
          }
        }
        return {
          setValue: { childIndicesToRender, firstVisibleChild }
        };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.childrenToHide = {
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          childGroups: [
            "anything",
            "variantControls",
            "titles",
            "setups",
            "cascadeMessages"
          ]
        },
        titleChildName: {
          dependencyType: "stateVariable",
          variableName: "titleChildName"
        },
        hideChildren: {
          dependencyType: "stateVariable",
          variableName: "hideChildren"
        }
      }),
      definition({ dependencyValues }) {
        const childrenToHide = [];
        for (let child of dependencyValues.allChildren) {
          if (child.componentType === "cascadeMessage") {
            if (!dependencyValues.hideChildren && typeof child === "object") {
              childrenToHide.push(child.componentIdx);
            }
          } else if (dependencyValues.hideChildren && typeof child === "object" && child.componentIdx !== dependencyValues.titleChildName && child.componentType !== "cascadeMessage") {
            childrenToHide.push(child.componentIdx);
          }
        }
        return { setValue: { childrenToHide } };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.nonBoxedListItemWithoutTitle = {
      returnDependencies: () => ({
        isListItem: {
          dependencyType: "stateVariable",
          variableName: "isListItem"
        },
        boxed: {
          dependencyType: "stateVariable",
          variableName: "boxed"
        },
        titleChildName: {
          dependencyType: "stateVariable",
          variableName: "titleChildName"
        },
        collapsible: {
          dependencyType: "stateVariable",
          variableName: "collapsible"
        }
      }),
      definition({ dependencyValues }) {
        const nonBoxedListItemWithoutTitle = Boolean(
          dependencyValues.isListItem && !dependencyValues.boxed && !dependencyValues.collapsible && dependencyValues.titleChildName === null
        );
        return { setValue: { nonBoxedListItemWithoutTitle } };
      }
    };
    stateVariableDefinitions.childrenToRenderInlineForListItem = {
      returnDependencies: () => ({
        firstVisibleChild: {
          dependencyType: "stateVariable",
          variableName: "firstVisibleChild"
        },
        nonBoxedListItemWithoutTitle: {
          dependencyType: "stateVariable",
          variableName: "nonBoxedListItemWithoutTitle"
        }
      }),
      definition({ dependencyValues }) {
        let childrenToRenderInlineForListItem = [];
        if (dependencyValues.nonBoxedListItemWithoutTitle && dependencyValues.firstVisibleChild && typeof dependencyValues.firstVisibleChild === "object") {
          childrenToRenderInlineForListItem = [
            dependencyValues.firstVisibleChild
          ];
        }
        return { setValue: { childrenToRenderInlineForListItem } };
      }
    };
    stateVariableDefinitions.firstVisibleChildAdjustedForListItem = {
      forRenderer: true,
      returnDependencies: () => ({
        nonBoxedListItemWithoutTitle: {
          dependencyType: "stateVariable",
          variableName: "nonBoxedListItemWithoutTitle"
        },
        firstVisibleChild: {
          dependencyType: "stateVariable",
          variableName: "firstVisibleChild"
        }
      }),
      definition({ dependencyValues }) {
        const firstVisibleChildAdjustedForListItem = Boolean(
          dependencyValues.nonBoxedListItemWithoutTitle && typeof dependencyValues.firstVisibleChild === "object"
        );
        return {
          setValue: {
            firstVisibleChildAdjustedForListItem
          }
        };
      }
    };
    stateVariableDefinitions.useListItemGridLayout = {
      forRenderer: true,
      returnDependencies: () => ({
        nonBoxedListItemWithoutTitle: {
          dependencyType: "stateVariable",
          variableName: "nonBoxedListItemWithoutTitle"
        },
        firstVisibleChild: {
          dependencyType: "stateVariable",
          variableName: "firstVisibleChild"
        }
      }),
      definition({ dependencyValues }) {
        const useListItemGridLayout = Boolean(
          dependencyValues.nonBoxedListItemWithoutTitle && dependencyValues.firstVisibleChild != null
        );
        return { setValue: { useListItemGridLayout } };
      }
    };
    stateVariableDefinitions.firstChildListItemAlignment = {
      stateVariablesDeterminingDependencies: ["firstVisibleChild"],
      forRenderer: true,
      returnDependencies: ({ stateValues }) => {
        const dependencies = {
          firstVisibleChildAdjustedForListItem: {
            dependencyType: "stateVariable",
            variableName: "firstVisibleChildAdjustedForListItem"
          }
        };
        if (stateValues.firstVisibleChild?.componentIdx !== void 0) {
          dependencies.firstVisibleChildListItemInlineAlignment = {
            dependencyType: "stateVariable",
            componentIdx: stateValues.firstVisibleChild.componentIdx,
            variableName: "listItemInlineAlignment",
            variablesOptional: true
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let firstChildListItemAlignment = "none";
        if (dependencyValues.firstVisibleChildAdjustedForListItem) {
          const alignmentFromFirstChild = dependencyValues.firstVisibleChildListItemInlineAlignment;
          if (alignmentFromFirstChild === "baseline" || alignmentFromFirstChild === "flex-start") {
            firstChildListItemAlignment = alignmentFromFirstChild;
          } else {
            firstChildListItemAlignment = "baseline";
          }
        }
        return { setValue: { firstChildListItemAlignment } };
      }
    };
    stateVariableDefinitions.startsWithIntroduction = {
      additionalStateVariablesDefined: [
        { variableName: "endsWithConclusion", forRenderer: true }
      ],
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          childGroups: [
            "anything",
            "variantControls",
            "titles",
            "setups",
            "cascadeMessages"
          ]
        },
        asList: {
          dependencyType: "stateVariable",
          variableName: "asList"
        },
        childIndicesToRender: {
          dependencyType: "stateVariable",
          variableName: "childIndicesToRender"
        }
      }),
      definition({ dependencyValues }) {
        const childIndices = dependencyValues.childIndicesToRender;
        const renderedNonTitleBlankStringChildren = childIndices.map((idx) => dependencyValues.allChildren[idx]).filter((child) => {
          if (typeof child === "string") {
            return child.trim() !== "";
          }
          return child.componentType !== "title";
        });
        const startsWithIntroduction = renderedNonTitleBlankStringChildren[0]?.componentType === "introduction";
        const lastRenderedChild = renderedNonTitleBlankStringChildren[renderedNonTitleBlankStringChildren.length - 1];
        const endsWithConclusion = lastRenderedChild?.componentType === "conclusion";
        return {
          setValue: { startsWithIntroduction, endsWithConclusion }
        };
      }
    };
    stateVariableDefinitions.title = {
      description: "The displayed title text for this section.",
      additionalStateVariablesDefined: [
        {
          variableName: "titlePrefix",
          forRenderer: true,
          alwaysUpdateRenderer: true
        }
      ],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      alwaysUpdateRenderer: true,
      returnDependencies: ({ sharedParameters }) => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["titles"],
          variableNames: ["text"]
        },
        sectionName: {
          dependencyType: "stateVariable",
          variableName: "sectionName"
        },
        sectionNumber: {
          dependencyType: "stateVariable",
          variableName: "sectionNumber"
        },
        includeAutoName: {
          dependencyType: "stateVariable",
          variableName: "includeAutoName"
        },
        includeAutoNumber: {
          dependencyType: "stateVariable",
          variableName: "includeAutoNumber"
        },
        prerender: {
          dependencyType: "value",
          value: sharedParameters.prerender
        },
        includeAutoNameIfNoTitle: {
          dependencyType: "stateVariable",
          variableName: "includeAutoNameIfNoTitle"
        },
        includeAutoNumberIfNoTitle: {
          dependencyType: "stateVariable",
          variableName: "includeAutoNumberIfNoTitle"
        },
        noAutoTitle: {
          dependencyType: "stateVariable",
          variableName: "noAutoTitle"
        }
      }),
      definition({ dependencyValues }) {
        let titlePrefix = "";
        let title = "";
        const haveTitleChild = dependencyValues.titleChild.length > 0;
        let includeAutoNumber = (dependencyValues.includeAutoNumber || !haveTitleChild && dependencyValues.includeAutoNumberIfNoTitle && !dependencyValues.noAutoTitle) && !dependencyValues.prerender;
        let includeAutoName = dependencyValues.includeAutoName || !haveTitleChild && dependencyValues.includeAutoNameIfNoTitle && !dependencyValues.noAutoTitle;
        if (includeAutoNumber) {
          if (includeAutoName) {
            titlePrefix = dependencyValues.sectionName + " ";
          }
          titlePrefix += dependencyValues.sectionNumber;
        } else {
          if (includeAutoName) {
            titlePrefix = dependencyValues.sectionName;
          }
        }
        if (!haveTitleChild) {
          title = titlePrefix;
        } else {
          if (titlePrefix) {
            if (dependencyValues.includeAutoName) {
              titlePrefix += ": ";
            } else {
              titlePrefix += ". ";
            }
          }
          title = dependencyValues.titleChild[dependencyValues.titleChild.length - 1].stateValues.text;
        }
        return { setValue: { title, titlePrefix } };
      }
    };
    stateVariableDefinitions.sectionTitleStateColors = {
      additionalStateVariablesDefined: [
        "sectionTitleStateColorsDarkMode",
        "sectionTitleStateColorSources",
        "sectionTitleStateColorSourcesDarkMode"
      ],
      returnDependencies: () => ({
        completedColor: {
          dependencyType: "stateVariable",
          variableName: "completedColor"
        },
        inProgressColor: {
          dependencyType: "stateVariable",
          variableName: "inProgressColor"
        },
        notStartedColor: {
          dependencyType: "stateVariable",
          variableName: "notStartedColor"
        },
        completedColorDarkMode: {
          dependencyType: "stateVariable",
          variableName: "completedColorDarkMode"
        },
        inProgressColorDarkMode: {
          dependencyType: "stateVariable",
          variableName: "inProgressColorDarkMode"
        },
        notStartedColorDarkMode: {
          dependencyType: "stateVariable",
          variableName: "notStartedColorDarkMode"
        },
        parentSectionTitleStateColors: {
          dependencyType: "parentStateVariable",
          variableName: "sectionTitleStateColors"
        },
        parentSectionTitleStateColorsDarkMode: {
          dependencyType: "parentStateVariable",
          variableName: "sectionTitleStateColorsDarkMode"
        },
        parentSectionTitleStateColorSources: {
          dependencyType: "parentStateVariable",
          variableName: "sectionTitleStateColorSources"
        },
        parentSectionTitleStateColorSourcesDarkMode: {
          dependencyType: "parentStateVariable",
          variableName: "sectionTitleStateColorSourcesDarkMode"
        },
        parentBoxed: {
          dependencyType: "parentStateVariable",
          variableName: "boxed"
        },
        parentCollapsible: {
          dependencyType: "parentStateVariable",
          variableName: "collapsible"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        const sectionTitleStateColors = {};
        const sectionTitleStateColorsDarkMode = {};
        const sectionTitleStateColorSources = {};
        const sectionTitleStateColorSourcesDarkMode = {};
        const parentIsBoxedOrCollapsible = Boolean(
          dependencyValues.parentBoxed || dependencyValues.parentCollapsible
        );
        const colorNamesByState = {
          completed: {
            light: "completedColor",
            dark: "completedColorDarkMode"
          },
          inProgress: {
            light: "inProgressColor",
            dark: "inProgressColorDarkMode"
          },
          notStarted: {
            light: "notStartedColor",
            dark: "notStartedColorDarkMode"
          }
        };
        for (const stateKey of sectionTitleStateKeys) {
          const colorNames = colorNamesByState[stateKey];
          const lightSpec = resolveSectionTitleLightColorSpec({
            dependencyValues,
            usedDefault,
            ownColorName: colorNames.light,
            parentColors: dependencyValues.parentSectionTitleStateColors,
            parentSources: dependencyValues.parentSectionTitleStateColorSources,
            parentIsBoxedOrCollapsible,
            stateKey
          });
          sectionTitleStateColors[stateKey] = lightSpec.value;
          sectionTitleStateColorSources[stateKey] = lightSpec.source;
          const darkSpec = resolveSectionTitleDarkColorSpec({
            dependencyValues,
            usedDefault,
            ownDarkColorName: colorNames.dark,
            ownLightColorName: colorNames.light,
            parentColorsDarkMode: dependencyValues.parentSectionTitleStateColorsDarkMode,
            parentSourcesDarkMode: dependencyValues.parentSectionTitleStateColorSourcesDarkMode,
            parentIsBoxedOrCollapsible,
            stateKey
          });
          sectionTitleStateColorsDarkMode[stateKey] = darkSpec.value;
          sectionTitleStateColorSourcesDarkMode[stateKey] = darkSpec.source;
        }
        return {
          setValue: {
            sectionTitleStateColors,
            sectionTitleStateColorsDarkMode,
            sectionTitleStateColorSources,
            sectionTitleStateColorSourcesDarkMode
          }
        };
      }
    };
    stateVariableDefinitions.titleColor = {
      // Note: currently title color is used only when boxed or collapsible
      additionalStateVariablesDefined: [
        {
          variableName: "titleColorDarkMode",
          forRenderer: true
        }
      ],
      forRenderer: true,
      returnDependencies: () => ({
        sectionTitleStateColors: {
          dependencyType: "stateVariable",
          variableName: "sectionTitleStateColors"
        },
        sectionTitleStateColorsDarkMode: {
          dependencyType: "stateVariable",
          variableName: "sectionTitleStateColorsDarkMode"
        },
        sectionTitleStateColorSources: {
          dependencyType: "stateVariable",
          variableName: "sectionTitleStateColorSources"
        },
        sectionTitleStateColorSourcesDarkMode: {
          dependencyType: "stateVariable",
          variableName: "sectionTitleStateColorSourcesDarkMode"
        },
        creditAchieved: {
          dependencyType: "stateVariable",
          variableName: "creditAchieved"
        },
        boxed: {
          dependencyType: "stateVariable",
          variableName: "boxed"
        },
        collapsible: {
          dependencyType: "stateVariable",
          variableName: "collapsible"
        }
      }),
      definition({ dependencyValues }) {
        const titleStateKey = titleStateKeyFromCredit(
          dependencyValues.creditAchieved
        );
        const titleColor = dependencyValues.sectionTitleStateColors[titleStateKey];
        const titleColorDarkMode = dependencyValues.sectionTitleStateColorsDarkMode[titleStateKey];
        const diagnostics = [];
        if (dependencyValues.boxed || dependencyValues.collapsible) {
          for (const stateKey of sectionTitleStateKeys) {
            const lightSource = dependencyValues.sectionTitleStateColorSources[stateKey];
            addSectionTitleColorContrastDiagnostic({
              diagnostics,
              authorSet: shouldEmitSectionTitleColorDiagnostic({
                source: lightSource
              }),
              colorValue: dependencyValues.sectionTitleStateColors[stateKey],
              colorName: lightSource?.colorName ?? stateKey,
              textColor: "#000000",
              canvasColor: CANVAS_LIGHT_MODE_COLOR
            });
            const darkSource = dependencyValues.sectionTitleStateColorSourcesDarkMode[stateKey];
            addSectionTitleColorContrastDiagnostic({
              diagnostics,
              authorSet: shouldEmitSectionTitleColorDiagnostic({
                source: darkSource
              }),
              colorValue: dependencyValues.sectionTitleStateColorsDarkMode[stateKey],
              colorName: darkSource?.colorName ?? stateKey,
              textColor: "#ffffff",
              canvasColor: CANVAS_DARK_MODE_COLOR,
              modeSuffix: " (dark mode)"
            });
          }
        }
        return {
          setValue: {
            titleColor,
            titleColorDarkMode
          },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.containerTag = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { containerTag: "section" } })
    };
    stateVariableDefinitions.level = {
      forRenderer: true,
      returnDependencies: () => ({
        ancestorLevel: {
          dependencyType: "ancestor",
          componentType: "_sectioningComponent",
          variableNames: ["level"]
        },
        levelAttr: {
          dependencyType: "attributeComponent",
          attributeName: "level",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        let level = dependencyValues.levelAttr?.stateValues.value;
        if (!(level > 0)) {
          level = (dependencyValues.ancestorLevel?.stateValues.level || 0) + 1;
        }
        return { setValue: { level } };
      }
    };
    stateVariableDefinitions.generatedVariantInfo = {
      shadowVariable: true,
      additionalStateVariablesDefined: ["isVariantComponent"],
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        variantIndex: {
          dependencyType: "value",
          value: sharedParameters.variantIndex
        },
        variantName: {
          dependencyType: "value",
          value: sharedParameters.variantName
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {};
        if (dependencyValues.variantName) {
          generatedVariantInfo.index = dependencyValues.variantIndex;
          generatedVariantInfo.name = dependencyValues.variantName;
        } else {
          generatedVariantInfo.seed = dependencyValues.variantSeed;
        }
        generatedVariantInfo.meta = {
          createdBy: componentIdx
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo,
            isVariantComponent: true
          }
        };
      }
    };
    stateVariableDefinitions.open = {
      description: "Whether this section is currently open (for collapsible sections).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: true,
      hasEssential: true,
      returnDependencies: () => ({
        collapsible: {
          dependencyType: "stateVariable",
          variableName: "collapsible"
        },
        startOpen: {
          dependencyType: "stateVariable",
          variableName: "startOpen"
        }
      }),
      definition({ dependencyValues }) {
        if (!dependencyValues.collapsible) {
          return { setValue: { open: true } };
        }
        return {
          useEssentialOrDefaultValue: {
            open: { defaultValue: dependencyValues.startOpen }
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "open",
              value: desiredStateVariableValues.open
            }
          ]
        };
      }
    };
    stateVariableDefinitions.rendered = {
      forRenderer: true,
      defaultValue: true,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition() {
        return {
          useEssentialOrDefaultValue: {
            rendered: true
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "rendered",
              value: desiredStateVariableValues.rendered
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  async submitAllAnswers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return submitAllAnswers({
      component: this,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async addChildren(args) {
    return await addChildrenToDynamicChild(this, args);
  }
  async deleteChildren(args) {
    return await deleteChildrenFromDynamicChild(this, args);
  }
  async revealSection({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "open",
          value: true
        }
      ],
      overrideReadOnly: true,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "viewed",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        }
      }
    });
    return this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "rendered",
          value: true
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
  async closeSection({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "open",
          value: false
        }
      ],
      overrideReadOnly: true,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "closed",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        }
      }
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents,
    core: core2
  }) {
    if (!serializedComponent.variants?.allPossibleVariants) {
      setUpVariantSeedAndRng({
        serializedComponent,
        sharedParameters,
        descendantVariantComponents,
        useSubpartVariantRng: true
      });
      return;
    }
    let numVariants = serializedComponent.variants.numVariants;
    let variantIndex;
    let desiredVariant = serializedComponent.variants.desiredVariant;
    if (desiredVariant !== void 0) {
      if (desiredVariant.index !== void 0) {
        let desiredVariantIndex = Number(desiredVariant.index);
        if (!Number.isFinite(desiredVariantIndex)) {
          core2.addDiagnostic({
            type: "info",
            message: "Variant index " + desiredVariant.index + " must be a number",
            position: serializedComponent.position,
            sourceDoc: serializedComponent.sourceDoc
          });
          variantIndex = 1;
        } else {
          if (!Number.isInteger(desiredVariantIndex)) {
            core2.addDiagnostic({
              type: "info",
              message: "Variant index " + desiredVariant.index + " must be an integer",
              position: serializedComponent.position,
              sourceDoc: serializedComponent.sourceDoc
            });
            desiredVariantIndex = Math.round(desiredVariantIndex);
          }
          let indexFrom0 = (desiredVariantIndex - 1) % numVariants;
          if (indexFrom0 < 0) {
            indexFrom0 += numVariants;
          }
          variantIndex = indexFrom0 + 1;
        }
      }
    }
    if (variantIndex === void 0) {
      let rand = sharedParameters.variantRng();
      variantIndex = Math.floor(rand * numVariants) + 1;
    }
    sharedParameters.allPossibleVariants = serializedComponent.variants.allPossibleVariants;
    sharedParameters.allVariantNames = serializedComponent.variants.allVariantNames;
    sharedParameters.variantSeed = serializedComponent.variants.allPossibleVariantSeeds[variantIndex - 1];
    sharedParameters.variantIndex = variantIndex;
    sharedParameters.variantName = serializedComponent.variants.allPossibleVariants[variantIndex - 1];
    sharedParameters.uniqueIndex = serializedComponent.variants.allPossibleVariantUniqueIndices[variantIndex - 1];
    sharedParameters.variantRng = sharedParameters.rngClass(
      sharedParameters.variantSeed
    );
    sharedParameters.subpartVariantRng = sharedParameters.rngClass(
      sharedParameters.variantSeed + "s"
    );
    if (desiredVariant?.subvariants && descendantVariantComponents) {
      for (let ind in desiredVariant.subvariants) {
        let subvariant = desiredVariant.subvariants[ind];
        let variantComponent = descendantVariantComponents[ind];
        if (variantComponent === void 0) {
          break;
        }
        variantComponent.variants.desiredVariant = subvariant;
      }
    }
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    return determineVariantsForSection({
      serializedComponent,
      componentInfoObjects,
      infoDiagnostics
    });
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    if (!serializedComponent.variants.allPossibleVariants) {
      return super.getUniqueVariant({
        serializedComponent,
        variantIndex,
        componentInfoObjects
      });
    }
    let uniqueIndex = serializedComponent.variants.allPossibleVariantUniqueIndices[variantIndex - 1];
    if (uniqueIndex === void 0) {
      return { success: false };
    }
    if (!serializedComponent.variants.uniqueVariants) {
      return {
        success: true,
        desiredVariant: {
          index: variantIndex
        }
      };
    }
    let result = getVariantsForDescendantsForUniqueVariants({
      variantIndex: uniqueIndex,
      serializedComponent,
      componentInfoObjects
    });
    if (!result.success) {
      return { success: false };
    }
    serializedComponent.variants.selectedUniqueVariant = true;
    return {
      success: true,
      desiredVariant: {
        index: variantIndex,
        subvariants: result.desiredVariants
      }
    };
  }
}
class SectioningComponentNumberWithSiblings extends SectioningComponent {
  static componentType = "_sectioningComponentNumberWithSiblings";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.renameTo;
    attributes.includeParentNumber = {
      createComponentOfType: "boolean",
      createStateVariable: "includeParentNumber",
      defaultValue: false,
      public: true,
      description: "Whether to prefix this section's number with the parent section's number."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.enumeration = {
      additionalStateVariablesDefined: [
        {
          variableName: "sectionNumber",
          description: "The displayed number for this section.",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "text"
          },
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        countAmongSiblings: {
          dependencyType: "countAmongSiblings",
          componentType: "_sectioningComponentNumberWithSiblings",
          includeInheritedComponentTypes: true
        },
        sectionAncestor: {
          dependencyType: "ancestor",
          componentType: "_sectioningComponent",
          variableNames: ["enumeration"]
        },
        includeParentNumber: {
          dependencyType: "stateVariable",
          variableName: "includeParentNumber"
        },
        isListItem: {
          dependencyType: "stateVariable",
          variableName: "isListItem"
        },
        countAmongSiblingsForListItem: {
          dependencyType: "countAmongSiblings",
          componentType: "_sectioningComponent",
          includeInheritedComponentTypes: true
        }
      }),
      definition({ dependencyValues }) {
        let enumeration = [];
        if (dependencyValues.isListItem) {
          enumeration.push(
            dependencyValues.countAmongSiblingsForListItem
          );
        } else {
          if (dependencyValues.includeParentNumber && dependencyValues.sectionAncestor) {
            enumeration.push(
              ...dependencyValues.sectionAncestor.stateValues.enumeration
            );
          }
          enumeration.push(dependencyValues.countAmongSiblings);
        }
        return {
          setValue: {
            enumeration,
            sectionNumber: enumeration.join(".")
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class UnnumberedSectioningComponent extends SectioningComponent {
  static componentType = "_unnumberedSectioningComponent";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.includeAutoNumberIfNoTitle.defaultValue = false;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.enumeration = {
      additionalStateVariablesDefined: [
        {
          variableName: "sectionNumber",
          description: "The displayed number for this section (always null for unnumbered sections).",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "text"
          },
          forRenderer: true
        }
      ],
      returnDependencies: () => ({}),
      definition() {
        return { setValue: { enumeration: [], sectionNumber: null } };
      }
    };
    return stateVariableDefinitions;
  }
}
const SectioningComponent$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  SectioningComponent,
  SectioningComponentNumberWithSiblings,
  UnnumberedSectioningComponent
}, Symbol.toStringTag, { value: "Module" }));
class Section extends SectioningComponentNumberWithSiblings {
  static componentType = "section";
  static componentDocs = {
    summary: "A section of a document, with a title (auto-generated or custom) and content"
  };
  static rendererType = "section";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.includeParentNumber.defaultValue = true;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    return stateVariableDefinitions;
  }
}
class Subsection extends Section {
  static componentType = "subsection";
  static componentDocs = {
    summary: "A sectional component nested one heading level deeper than `<section>`"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.sectionName.definition = () => ({
      setValue: { sectionName: "Section" }
    });
    return stateVariableDefinitions;
  }
}
class Subsubsection extends Section {
  static componentType = "subsubsection";
  static componentDocs = {
    summary: "A sectional component nested one heading level deeper than `<subsection>`"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.sectionName.definition = () => ({
      setValue: { sectionName: "Section" }
    });
    return stateVariableDefinitions;
  }
}
class Paragraphs extends SectioningComponentNumberWithSiblings {
  static componentType = "paragraphs";
  static componentDocs = {
    summary: "A subsection of paragraphs (rendered at heading level 4)"
  };
  static rendererType = "section";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.level.definition = () => ({
      setValue: { level: 4 }
    });
    return stateVariableDefinitions;
  }
}
class Aside extends SectioningComponentNumberWithSiblings {
  static componentType = "aside";
  static componentDocs = {
    summary: "A note set off from the main flow of the document"
  };
  static rendererType = "section";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.postponeRendering = {
      createPrimitiveOfType: "boolean",
      description: "Whether to delay rendering this section's contents until expanded."
    };
    attributes.collapsible.defaultValue = true;
    attributes.startOpen.defaultValue = false;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.rendered.returnDependencies = () => ({
      startOpen: {
        dependencyType: "stateVariable",
        variableName: "startOpen"
      }
    });
    stateVariableDefinitions.rendered.definition = ({
      dependencyValues
    }) => ({
      useEssentialOrDefaultValue: {
        rendered: {
          defaultValue: dependencyValues.startOpen
        }
      }
    });
    stateVariableDefinitions.level.definition = () => ({
      setValue: { level: 3 }
    });
    stateVariableDefinitions.containerTag.definition = () => ({
      setValue: { containerTag: "aside" }
    });
    return stateVariableDefinitions;
  }
}
class Objectives extends SectioningComponentNumberWithSiblings {
  static componentType = "objectives";
  static componentDocs = {
    summary: "A section listing learning objectives"
  };
  static rendererType = "section";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.boxed.defaultValue = true;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.level.definition = () => ({
      setValue: { level: 3 }
    });
    stateVariableDefinitions.containerTag.definition = () => ({
      setValue: { containerTag: "article" }
    });
    return stateVariableDefinitions;
  }
}
class Problem extends SectioningComponentNumberWithSiblings {
  static componentType = "problem";
  static componentDocs = {
    summary: "A sectional component that defines a scored problem"
  };
  static rendererType = "section";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.aggregateScores.defaultValue = true;
    attributes.isDistractor = {
      createPrimitiveOfType: "boolean",
      description: "Whether this section is a distractor (e.g. a wrong-answer choice for matching)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.containerTag.definition = () => ({
      setValue: { containerTag: "article" }
    });
    return stateVariableDefinitions;
  }
}
class Exercise extends Problem {
  static componentType = "exercise";
  static componentDocs = {
    summary: "A sectional component that defines a scored exercise"
  };
}
class Question extends Problem {
  static componentType = "question";
  static componentDocs = {
    summary: "A sectional component that defines a scored question; similar to a `<problem>`"
  };
}
class Activity extends Problem {
  static componentType = "activity";
  static componentDocs = {
    summary: "A sectional component with score aggregation, similar to `<problem>`"
  };
}
class Example extends SectioningComponentNumberWithSiblings {
  static componentType = "example";
  static componentDocs = {
    summary: "A sectional component that defines an example"
  };
  static rendererType = "section";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.containerTag.definition = () => ({
      setValue: { containerTag: "article" }
    });
    return stateVariableDefinitions;
  }
}
class Definition extends Example {
  static componentType = "definition";
  static componentDocs = {
    summary: "A sectional component for defining a term or concept"
  };
}
class Note extends Example {
  static componentType = "note";
  static componentDocs = {
    summary: "A note section, set off from the main flow of the document"
  };
}
class Theorem extends Example {
  static componentType = "theorem";
  static componentDocs = {
    summary: "A sectional component for the statement of a theorem (or similar formal statement)"
  };
}
class Part extends SectioningComponentNumberWithSiblings {
  static componentType = "part";
  static rendererType = "section";
  static componentDocs = {
    summary: "A subsection-style block rendered as a list item (a part of an exercise or problem)"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.isListItem.definition = () => ({
      setValue: { isListItem: true }
    });
    stateVariableDefinitions.containerTag.definition = () => ({
      setValue: { containerTag: "div" }
    });
    return stateVariableDefinitions;
  }
}
class Task extends Part {
  static componentType = "task";
  static componentDocs = {
    summary: "A task of a multi-task activity or problem (alias for `<part>`)"
  };
}
class Proof extends UnnumberedSectioningComponent {
  static componentType = "proof";
  static componentDocs = {
    summary: "A proof section"
  };
  static rendererType = "section";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.postponeRendering = {
      createPrimitiveOfType: "boolean",
      description: "Whether to delay rendering this section's contents until expanded."
    };
    attributes.collapsible.defaultValue = true;
    attributes.startOpen.defaultValue = false;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.rendered.returnDependencies = () => ({
      startOpen: {
        dependencyType: "stateVariable",
        variableName: "startOpen"
      }
    });
    stateVariableDefinitions.rendered.definition = ({
      dependencyValues
    }) => ({
      useEssentialOrDefaultValue: {
        rendered: {
          defaultValue: dependencyValues.startOpen
        }
      }
    });
    stateVariableDefinitions.level.definition = () => ({
      setValue: { level: 3 }
    });
    stateVariableDefinitions.containerTag.definition = () => ({
      setValue: { containerTag: "article" }
    });
    return stateVariableDefinitions;
  }
}
class Problems extends SectioningComponent {
  static componentType = "problems";
  static rendererType = "section";
  static componentDocs = {
    summary: "A container element grouping `<problem>` components, which are rendered as a list by default"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.asList.defaultValue = true;
    return attributes;
  }
}
class Exercises extends Problems {
  static componentType = "exercises";
  static componentDocs = {
    summary: "A container element grouping `<exercise>` components, which are rendered as a list by default"
  };
}
class StandinForFutureLayoutTag extends SectioningComponent {
  static componentType = "standinForFutureLayoutTag";
  static rendererType = "section";
  static excludeFromSchema = true;
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.level.definition = () => ({
      setValue: { level: 3 }
    });
    stateVariableDefinitions.containerTag.definition = () => ({
      setValue: { containerTag: "aside" }
    });
    return stateVariableDefinitions;
  }
}
class externalContent extends SectioningComponent {
  static componentType = "externalContent";
  static rendererType = "section";
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.includeAutoNameIfNoTitle.defaultValue = false;
    attributes.includeAutoNumberIfNoTitle.defaultValue = false;
    return attributes;
  }
}
const Sectioning = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Activity,
  Aside,
  Definition,
  Example,
  Exercise,
  Exercises,
  Note,
  Objectives,
  Paragraphs,
  Part,
  Problem,
  Problems,
  Proof,
  Question,
  Section,
  StandinForFutureLayoutTag,
  Subsection,
  Subsubsection,
  Task,
  Theorem,
  externalContent
}, Symbol.toStringTag, { value: "Module" }));
class Ol extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAllAnswers: this.submitAllAnswers.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "ol";
  static componentDocs = {
    summary: "An ordered list"
  };
  static rendererType = "list";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.label = {
      description: "Label rendered before each list item.",
      createComponentOfType: "text",
      createStateVariable: "label",
      defaultValue: null,
      public: true,
      forRenderer: true,
      excludeFromSchema: true
    };
    attributes.level = {
      createComponentOfType: "integer",
      description: "Nesting level of this list (1-based)."
    };
    attributes.marker = {
      createComponentOfType: "text",
      createStateVariable: "marker",
      defaultValue: null,
      forRenderer: true,
      description: "Marker style for list items (e.g. 'disc', 'circle', '1', 'a')."
    };
    let scoredSectionAttributes = returnScoredSectionAttributes();
    Object.assign(attributes, scoredSectionAttributes);
    attributes.cols = {
      createComponentOfType: "number",
      description: "Number of columns to lay items out in (currently ignored).",
      excludeFromSchema: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "lis",
        componentTypes: ["li"]
      },
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnScoredSectionStateVariableDefinition()
    );
    stateVariableDefinitions.numbered = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numbered: true } })
    };
    stateVariableDefinitions.level = {
      forRenderer: true,
      returnDependencies: () => ({
        ancestorLevel: {
          dependencyType: "ancestor",
          componentType: "ol",
          variableNames: ["level"]
        },
        levelAttr: {
          dependencyType: "attributeComponent",
          attributeName: "level",
          variableNames: ["value"]
        },
        sectionAncestorIsListItem: {
          dependencyType: "ancestor",
          componentType: "_sectioningComponent",
          variableNames: ["isListItem"]
        }
      }),
      definition({ dependencyValues }) {
        let level = dependencyValues.levelAttr?.stateValues.value;
        if (!(level > 0)) {
          let ancestorLevel = dependencyValues.ancestorLevel?.stateValues.level;
          if (!(ancestorLevel > 0) && dependencyValues.sectionAncestorIsListItem?.stateValues.isListItem) {
            level = 2;
          } else {
            level = (ancestorLevel || 0) + 1;
          }
        }
        return { setValue: { level } };
      }
    };
    return stateVariableDefinitions;
  }
  async submitAllAnswers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return submitAllAnswers({
      component: this,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Ul extends Ol {
  static componentType = "ul";
  static componentDocs = {
    summary: "An unordered list"
  };
  static rendererType = "list";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numbered = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numbered: false } })
    };
    return stateVariableDefinitions;
  }
}
let Li$1 = class Li extends BaseComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAllAnswers: this.submitAllAnswers.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "li";
  static componentDocs = {
    summary: "A list item within `<ol>` / `<ul>`"
  };
  static rendererType = "list";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static inSchemaOnlyInheritAs = [];
  static includeBlankStringChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    let scoredSectionAttributes = returnScoredSectionAttributes();
    Object.assign(attributes, scoredSectionAttributes);
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnScoredSectionStateVariableDefinition()
    );
    stateVariableDefinitions.item = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { item: true } })
    };
    stateVariableDefinitions.text = {
      description: "The list's content rendered as plain text.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let text = textFromChildren(dependencyValues.children);
        return { setValue: { text } };
      }
    };
    return stateVariableDefinitions;
  }
  async submitAllAnswers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return submitAllAnswers({
      component: this,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
};
const Lists = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Li: Li$1,
  Ol,
  Ul
}, Symbol.toStringTag, { value: "Module" }));
const { dopri } = me$3.math;
class ODESystem extends InlineComponent {
  static componentType = "odeSystem";
  static rendererType = "math";
  static componentDocs = {
    summary: "A system of first-order ordinary differential equations with initial conditions, rendered as math and numerically integrable"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.independentVariable = {
      createComponentOfType: "_variableName",
      createStateVariable: "independentVariable",
      defaultValue: me$3.fromAst("t"),
      public: true,
      description: "The independent variable of the ODE system."
    };
    attributes.initialIndependentVariableValue = {
      createComponentOfType: "math",
      createStateVariable: "initialIndependentVariableValue",
      defaultValue: me$3.fromAst(0),
      public: true,
      description: "The value of the independent variable at which the initial conditions are specified."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.renderMode = {
      createComponentOfType: "text",
      createStateVariable: "renderMode",
      defaultValue: "align",
      public: true,
      forRenderer: true,
      description: 'How to format the rendered system of equations (e.g. "align").'
    };
    attributes.chunkSize = {
      createComponentOfType: "number",
      createStateVariable: "chunkSize",
      defaultValue: 10,
      public: true,
      description: "Step-size chunk used when numerically integrating the system."
    };
    attributes.tolerance = {
      createComponentOfType: "number",
      createStateVariable: "tolerance",
      defaultValue: 1e-6,
      public: true,
      description: "Numerical tolerance used by the integrator."
    };
    attributes.maxIterations = {
      createComponentOfType: "number",
      createStateVariable: "maxIterations",
      defaultValue: 1e3,
      public: true,
      description: "Maximum number of iterations the integrator may perform."
    };
    attributes.hideInitialCondition = {
      createComponentOfType: "boolean",
      createStateVariable: "hideInitialCondition",
      defaultValue: false,
      public: true,
      description: "Whether to hide the initial condition from the rendered output."
    };
    attributes.initialConditions = {
      createComponentOfType: "mathList",
      description: "The initial values of each dependent variable, in order."
    };
    attributes.variables = {
      createComponentOfType: "_variableNameList",
      description: "The dependent variables of the system, in the same order as the right-hand sides."
    };
    attributes.number = {
      createComponentOfType: "boolean",
      createStateVariable: "number",
      defaultValue: false,
      public: true,
      description: "Whether to display equation numbers next to each equation."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "rightHandSides",
        componentTypes: ["rightHandSide"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.validIndependentVariable = {
      returnDependencies: () => ({
        independentVarAttr: {
          dependencyType: "attributeComponent",
          attributeName: "independentVariable",
          variableNames: ["validVariable"]
        }
      }),
      definition({ dependencyValues }) {
        let validVariable = true;
        if (dependencyValues.independentVarAttr) {
          validVariable = dependencyValues.independentVarAttr.stateValues.validVariable;
        }
        return {
          setValue: { validIndependentVariable: validVariable }
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      returnDependencies: () => ({
        rhsChildren: {
          dependencyType: "child",
          childGroups: ["rightHandSides"],
          skipComponentIndices: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            numDimensions: dependencyValues.rhsChildren.length
          },
          checkForActualChange: { numDimensions: true }
        };
      }
    };
    stateVariableDefinitions.variables = {
      description: "The dependent variables of the system, in order matching the right-hand sides.",
      additionalStateVariablesDefined: [
        {
          variableName: "validVariables",
          isArray: true,
          entryPrefixes: ["validVar"]
        }
      ],
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      entryPrefixes: ["var"],
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          },
          variables: {
            dependencyType: "attributeComponent",
            attributeName: "variables",
            variableNames: ["variables", "validVariables"]
          },
          independentVariable: {
            dependencyType: "stateVariable",
            variableName: "independentVariable"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let diagnostics = [];
        let variablesSpecified = [];
        let validVariables = [];
        let numDims = globalDependencyValues.numDimensions;
        if (globalDependencyValues.variables !== null) {
          variablesSpecified = globalDependencyValues.variables.stateValues.variables;
          validVariables = [
            ...globalDependencyValues.variables.stateValues.validVariables
          ].slice(0, numDims);
        }
        let variables = returnNVariables(numDims, variablesSpecified);
        if (variables.some(
          (x2) => x2.equals(globalDependencyValues.independentVariable)
        )) {
          const warning = {
            type: "warning",
            message: "Variables of `<odeSystem>` must be different than independent variable."
          };
          if (globalDependencyValues.variables?.position) {
            warning.position = globalDependencyValues.variables.position;
          }
          diagnostics.push(warning);
        }
        if (validVariables.length < numDims) {
          validVariables.push(
            ...Array(numDims - validVariables.length).fill(true)
          );
        }
        return {
          setValue: { variables, validVariables },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.rhss = {
      description: "The right-hand-side expressions of each ODE in the system.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      entryPrefixes: ["rhs", "righthandside"],
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            rhsChild: {
              dependencyType: "child",
              childGroups: ["rightHandSides"],
              variableNames: ["value"],
              childIndices: [arrayKey]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let rhss = {};
        for (let arrayKey of arrayKeys) {
          rhss[arrayKey] = dependencyValuesByKey[arrayKey].rhsChild[0].stateValues.value;
        }
        return {
          setValue: { rhss }
        };
      }
    };
    stateVariableDefinitions.rhs = {
      isAlias: true,
      targetVariableName: "rhs1",
      description: "The right-hand-side expression of the first ODE in the system."
    };
    stateVariableDefinitions.righthandside = {
      isAlias: true,
      targetVariableName: "rhs1",
      description: "The right-hand-side expression of the first ODE in the system."
    };
    stateVariableDefinitions.righthandsides = {
      isAlias: true,
      targetVariableName: "rhss",
      description: "The right-hand-side expressions of each ODE in the system."
    };
    stateVariableDefinitions.initialConditions = {
      description: "The initial values of each dependent variable at the initial independent variable value.",
      isArray: true,
      public: true,
      hasEssential: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      entryPrefixes: ["initialCondition"],
      defaultValueByArrayKey: () => me$3.fromAst(0),
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            initialConditionAttr: {
              dependencyType: "attributeComponent",
              attributeName: "initialConditions",
              variableNames: [`math${Number(arrayKey) + 1}`]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let initialConditions = {};
        let defaultInitialConditions = {};
        for (let arrayKey of arrayKeys) {
          let foundValue = false;
          if (dependencyValuesByKey[arrayKey].initialConditionAttr) {
            let ic = dependencyValuesByKey[arrayKey].initialConditionAttr.stateValues[`math${Number(arrayKey) + 1}`];
            if (ic !== void 0) {
              foundValue = true;
              initialConditions[arrayKey] = ic;
            }
          }
          if (!foundValue) {
            defaultInitialConditions[arrayKey] = true;
          }
        }
        let result = {};
        if (Object.keys(initialConditions).length > 0) {
          result.setValue = { initialConditions };
        }
        if (Object.keys(defaultInitialConditions).length > 0) {
          result.useEssentialOrDefaultValue = {
            initialConditions: defaultInitialConditions
          };
        }
        return result;
      }
    };
    stateVariableDefinitions.initialCondition = {
      isAlias: true,
      targetVariableName: "initialCondition1",
      description: "The initial value of the first dependent variable at the initial independent-variable value."
    };
    stateVariableDefinitions.equationTag = {
      description: "The displayed equation number tag (when equation numbering is enabled).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["number"],
      mustEvaluate: true,
      // must evaluate to make sure all counters are accounted for
      returnDependencies({ stateValues }) {
        if (stateValues.number) {
          return {
            equationCounter: {
              dependencyType: "counter",
              counterName: "equation"
            }
          };
        } else {
          return {};
        }
      },
      definition({ dependencyValues }) {
        if (dependencyValues.equationCounter !== void 0) {
          return {
            setValue: {
              equationTag: String(
                dependencyValues.equationCounter
              )
            }
          };
        } else {
          return { setValue: { equationTag: null } };
        }
      }
    };
    stateVariableDefinitions.latex = {
      description: "The full ODE system rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      forRenderer: true,
      returnDependencies() {
        return {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          },
          variables: {
            dependencyType: "stateVariable",
            variableName: "variables"
          },
          initialConditions: {
            dependencyType: "stateVariable",
            variableName: "initialConditions"
          },
          hideInitialCondition: {
            dependencyType: "stateVariable",
            variableName: "hideInitialCondition"
          },
          displayDigits: {
            dependencyType: "stateVariable",
            variableName: "displayDigits"
          },
          displayDecimals: {
            dependencyType: "stateVariable",
            variableName: "displayDecimals"
          },
          displaySmallAsZero: {
            dependencyType: "stateVariable",
            variableName: "displaySmallAsZero"
          },
          padZeros: {
            dependencyType: "stateVariable",
            variableName: "padZeros"
          },
          avoidScientificNotation: {
            dependencyType: "stateVariable",
            variableName: "avoidScientificNotation"
          },
          independentVariable: {
            dependencyType: "stateVariable",
            variableName: "independentVariable"
          },
          initialIndependentVariableValue: {
            dependencyType: "stateVariable",
            variableName: "initialIndependentVariableValue"
          },
          rhss: {
            dependencyType: "stateVariable",
            variableName: "rhss"
          },
          number: {
            dependencyType: "stateVariable",
            variableName: "number"
          },
          equationTag: {
            dependencyType: "stateVariable",
            variableName: "equationTag"
          }
        };
      },
      definition({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let systemDisplay = [];
        let indVar = dependencyValues.independentVariable.toLatex();
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          let variable = dependencyValues.variables[dim].toLatex();
          let rhs = roundForDisplay({
            value: dependencyValues.rhss[dim],
            dependencyValues
          });
          let thisLatex = `\\frac{d${variable}}{d${indVar}} &=  ${rhs.toLatex(
            params
          )}`;
          if (dependencyValues.number && dim === 0) {
            thisLatex += `\\tag{${dependencyValues.equationTag}}`;
          } else {
            thisLatex += "\\notag";
          }
          systemDisplay.push(thisLatex);
        }
        if (!dependencyValues.hideInitialCondition) {
          let indVarVal0 = dependencyValues.initialIndependentVariableValue;
          for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
            let variable = dependencyValues.variables[dim].toLatex();
            let ic = roundForDisplay({
              value: dependencyValues.initialConditions[dim],
              dependencyValues
            });
            systemDisplay.push(
              `${variable}(${indVarVal0}) &= ${ic.toLatex(
                params
              )}\\notag`
            );
          }
        }
        let latex = systemDisplay.join("\\\\");
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.numericalRHSf = {
      additionalStateVariablesDefined: ["numericalRHSfDefinitions"],
      returnDependencies: () => ({
        validIndependentVariable: {
          dependencyType: "stateVariable",
          variableName: "validIndependentVariable"
        },
        independentVariable: {
          dependencyType: "stateVariable",
          variableName: "independentVariable"
        },
        validVariables: {
          dependencyType: "stateVariable",
          variableName: `validVariables`
        },
        variables: {
          dependencyType: "stateVariable",
          variableName: `variables`
        },
        rhss: {
          dependencyType: "stateVariable",
          variableName: `rhss`
        }
      }),
      definition({ dependencyValues }) {
        let diagnostics = [];
        let valid = true;
        if (!dependencyValues.validIndependentVariable) {
          valid = false;
        }
        if (!dependencyValues.validVariables.every((x2) => x2)) {
          valid = false;
        }
        let indVarName = dependencyValues.independentVariable.subscripts_to_strings().tree;
        let varNames = dependencyValues.variables.map(
          (x2) => x2.subscripts_to_strings().tree
        );
        if (varNames.includes(indVarName)) {
          valid = false;
        }
        if ([...new Set(varNames)].length !== varNames.length) {
          diagnostics.push({
            message: "Can't define ODE RHS functions with duplicate dependent variable names.",
            type: "warning"
          });
          valid = false;
        }
        let fs;
        try {
          fs = dependencyValues.rhss.map(
            (x2) => x2.subscripts_to_strings().f()
          );
        } catch (e3) {
          diagnostics.push({
            message: "Cannot define ODE RHS function.  Error creating mathjs function.",
            type: "warning"
          });
          valid = false;
        }
        if (!valid) {
          let n2 = dependencyValues.rhss.length;
          return {
            setValue: {
              numericalRHSf: () => NaN,
              numericalRHSfDefinitions: Array(n2).fill({})
            },
            sendDiagnostics: diagnostics
          };
        }
        return {
          setValue: {
            numericalRHSf: function(t2, x2) {
              let fargs = { [indVarName]: t2 };
              if (Array.isArray(x2)) {
                x2.forEach((v2, i2) => fargs[varNames[i2]] = v2);
              } else {
                fargs[varNames[0]] = x2;
              }
              try {
                return fs.map((f2) => f2(fargs));
              } catch (e3) {
                return NaN;
              }
            },
            numericalRHSfDefinitions: dependencyValues.rhss.map(
              (x2) => ({
                functionType: "formula",
                formula: x2,
                numInputs: varNames.length + 1,
                variables: [indVarName, ...varNames]
              })
            )
          }
        };
      }
    };
    stateVariableDefinitions.haveNumericalInitialConditions = {
      additionalStateVariablesDefined: ["t0", "x0s"],
      returnDependencies: () => ({
        initialIndependentVariableValue: {
          dependencyType: "stateVariable",
          variableName: "initialIndependentVariableValue"
        },
        initialConditions: {
          dependencyType: "stateVariable",
          variableName: "initialConditions"
        }
      }),
      definition({ dependencyValues }) {
        let t0 = dependencyValues.initialIndependentVariableValue.evaluate_to_constant();
        let x0s = dependencyValues.initialConditions.map(
          (x2) => x2.evaluate_to_constant()
        );
        let haveNumericalInitialConditions = Number.isFinite(t0) && x0s.every((x2) => Number.isFinite(x2));
        return {
          setValue: { t0, x0s, haveNumericalInitialConditions }
        };
      }
    };
    stateVariableDefinitions.numericalSolutions = {
      description: "Numerical solution functions for each dependent variable, as functions of the independent variable.",
      isArray: true,
      entryPrefixes: ["numericalSolution"],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "function",
        addStateVariablesShadowingStateVariables: {
          fDefinitions: {
            stateVariableToShadow: "numericalSolutionFDefinitions"
          }
        },
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      createWorkspace: true,
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          },
          validIndependentVariable: {
            dependencyType: "stateVariable",
            variableName: "validIndependentVariable"
          },
          independentVariable: {
            dependencyType: "stateVariable",
            variableName: "independentVariable"
          },
          validVariables: {
            dependencyType: "stateVariable",
            variableName: `validVariables`
          },
          variables: {
            dependencyType: "stateVariable",
            variableName: `variables`
          },
          chunkSize: {
            dependencyType: "stateVariable",
            variableName: `chunkSize`
          },
          tolerance: {
            dependencyType: "stateVariable",
            variableName: `tolerance`
          },
          maxIterations: {
            dependencyType: "stateVariable",
            variableName: `maxIterations`
          },
          haveNumericalInitialConditions: {
            dependencyType: "stateVariable",
            variableName: `haveNumericalInitialConditions`
          },
          t0: {
            dependencyType: "stateVariable",
            variableName: `t0`
          },
          x0s: {
            dependencyType: "stateVariable",
            variableName: `x0s`
          },
          numericalRHSf: {
            dependencyType: "stateVariable",
            variableName: `numericalRHSf`
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        workspace,
        componentIdx
      }) {
        let numericalSolutions = {};
        workspace.calculatedNumericSolutions = [];
        workspace.endingNumericalValues = [];
        workspace.maxPossibleTime = void 0;
        if (!globalDependencyValues.haveNumericalInitialConditions) {
          for (let ind = 0; ind < globalDependencyValues.numDimensions; ind++) {
            numericalSolutions[ind] = (_2) => NaN;
          }
          return { setValue: { numericalSolutions } };
        }
        let t0 = globalDependencyValues.t0;
        let x0s = globalDependencyValues.x0s;
        let chunkSize = globalDependencyValues.chunkSize;
        let tolerance = globalDependencyValues.tolerance;
        let numericalRHSf = globalDependencyValues.numericalRHSf;
        let maxIterations = globalDependencyValues.maxIterations;
        for (let ind = 0; ind < globalDependencyValues.numDimensions; ind++) {
          numericalSolutions[ind] = function f2(t2) {
            if (!Number.isFinite(t2)) {
              return NaN;
            }
            if (t2 === t0) {
              return x0s[ind];
            }
            let nChunksCalculated = workspace.calculatedNumericSolutions.length;
            let chunk = Math.ceil((t2 - t0) / chunkSize) - 1;
            if (chunk < 0) {
              return NaN;
            }
            if (workspace.maxPossibleTime === void 0 && chunk >= nChunksCalculated) {
              for (let tind = nChunksCalculated; tind <= chunk; tind++) {
                let x0 = workspace.endingNumericalValues[tind - 1];
                if (x0 === void 0) {
                  x0 = x0s;
                }
                let t0shifted = t0 + tind * chunkSize;
                let result = dopri(
                  t0shifted,
                  t0shifted + chunkSize,
                  x0,
                  numericalRHSf,
                  tolerance,
                  maxIterations
                );
                workspace.endingNumericalValues.push(
                  result.y[result.y.length - 1]
                );
                workspace.calculatedNumericSolutions.push(
                  result.at.bind(result)
                );
                let endingTime = result.x[result.x.length - 1];
                if (endingTime < (t0shifted + chunkSize) * (1 - 1e-6)) {
                  workspace.maxPossibleTime = endingTime;
                  let message = "For chunksize " + chunkSize + " and tolerance " + tolerance + ", odeSystem";
                  if (componentIdx !== void 0) {
                    message += " (" + componentIdx + ")";
                  }
                  message += " hit maxiterations (" + maxIterations + ") at t = " + workspace.maxPossibleTime + ". Will not calculate solution beyond that time. Decrease chunksize, increase maxiterations, or increase tolerance to calculate further.";
                  break;
                }
              }
            }
            if (t2 > workspace.maxPossibleTime) {
              return NaN;
            }
            let value = workspace.calculatedNumericSolutions[chunk](t2)[ind];
            return value;
          };
        }
        return { setValue: { numericalSolutions } };
      }
    };
    stateVariableDefinitions.numericalSolution = {
      isAlias: true,
      targetVariableName: "numericalSolution1",
      description: "Numerical solution function for the first dependent variable, as a function of the independent variable."
    };
    stateVariableDefinitions.numericalSolutionFDefinitions = {
      forRenderer: true,
      isArray: true,
      entryPrefixes: ["numericalSolutionFDefinition"],
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          },
          validIndependentVariable: {
            dependencyType: "stateVariable",
            variableName: "validIndependentVariable"
          },
          independentVariable: {
            dependencyType: "stateVariable",
            variableName: "independentVariable"
          },
          validVariables: {
            dependencyType: "stateVariable",
            variableName: `validVariables`
          },
          variables: {
            dependencyType: "stateVariable",
            variableName: `variables`
          },
          chunkSize: {
            dependencyType: "stateVariable",
            variableName: `chunkSize`
          },
          tolerance: {
            dependencyType: "stateVariable",
            variableName: `tolerance`
          },
          maxIterations: {
            dependencyType: "stateVariable",
            variableName: `maxIterations`
          },
          haveNumericalInitialConditions: {
            dependencyType: "stateVariable",
            variableName: `haveNumericalInitialConditions`
          },
          t0: {
            dependencyType: "stateVariable",
            variableName: `t0`
          },
          x0s: {
            dependencyType: "stateVariable",
            variableName: `x0s`
          },
          numericalRHSfDefinitions: {
            dependencyType: "stateVariable",
            variableName: `numericalRHSfDefinitions`
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        if (!globalDependencyValues.haveNumericalInitialConditions) {
          return {
            setValue: {
              numericalSolutionFDefinitions: Array(
                globalDependencyValues.numDimensions
              ).fill({})
            }
          };
        }
        return {
          setValue: {
            numericalSolutionFDefinitions: [
              ...Array(
                globalDependencyValues.numDimensions
              ).keys()
            ].map((ind) => ({
              functionType: "ODESolution",
              numDimensions: globalDependencyValues.numDimensions,
              t0: globalDependencyValues.t0,
              x0s: globalDependencyValues.x0s,
              chunkSize: globalDependencyValues.chunkSize,
              tolerance: globalDependencyValues.tolerance,
              numericalRHSfDefinitions: globalDependencyValues.numericalRHSfDefinitions,
              maxIterations: globalDependencyValues.maxIterations,
              component: ind
            }))
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
function applyConstraintFromComponentConstraints(variables, applyComponentConstraint) {
  let newVariables = {};
  let constrained = false;
  for (let varName in variables) {
    let result = applyComponentConstraint({
      [varName]: variables[varName]
    });
    if (result.constrained) {
      constrained = true;
      newVariables[varName] = result.variables[varName];
    }
  }
  if (constrained) {
    return {
      constrained,
      variables: newVariables
    };
  } else {
    return {};
  }
}
function returnConstraintGraphInfoDefinitions() {
  return {
    scales: {
      description: "The graph axis scales (x and y) used when applying this constraint.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["xScale", "yScale"]
        },
        shadowedConstraints: {
          dependencyType: "shadowSource",
          variableNames: ["scales"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.graphAncestor) {
          let SVs = dependencyValues.graphAncestor.stateValues;
          let scales = [SVs.xScale, SVs.yScale, 1];
          if (scales.every((x2) => Number.isFinite(x2) && x2 > 0)) {
            return { setValue: { scales } };
          }
        } else if (dependencyValues.shadowedConstraints) {
          return {
            setValue: {
              scales: dependencyValues.shadowedConstraints.stateValues.scales
            }
          };
        }
        return { setValue: { scales: [1, 1, 1] } };
      }
    },
    graphXmin: {
      additionalStateVariablesDefined: [
        "graphXmax",
        "graphYmin",
        "graphYmax"
      ],
      returnDependencies: () => ({
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["xMin", "xMax", "yMin", "yMax"]
        },
        shadowedConstraints: {
          dependencyType: "shadowSource",
          variableNames: [
            "graphXmin",
            "graphXmax",
            "graphYmin",
            "graphYmax"
          ]
        }
      }),
      definition({ dependencyValues }) {
        if (!dependencyValues.graphAncestor) {
          if (dependencyValues.shadowedConstraints) {
            let { graphXmin: graphXmin2, graphXmax: graphXmax2, graphYmin: graphYmin2, graphYmax: graphYmax2 } = dependencyValues.shadowedConstraints.stateValues;
            return {
              setValue: {
                graphXmin: graphXmin2,
                graphXmax: graphXmax2,
                graphYmin: graphYmin2,
                graphYmax: graphYmax2
              }
            };
          } else {
            return {
              setValue: {
                graphXmin: null,
                graphXmax: null,
                graphYmin: null,
                graphYmax: null
              }
            };
          }
        }
        let graphXmin = dependencyValues.graphAncestor.stateValues.xMin;
        let graphXmax = dependencyValues.graphAncestor.stateValues.xMax;
        let graphYmin = dependencyValues.graphAncestor.stateValues.yMin;
        let graphYmax = dependencyValues.graphAncestor.stateValues.yMax;
        if ([graphXmin, graphXmax, graphYmin, graphYmax].every(
          Number.isFinite
        )) {
          return {
            setValue: {
              graphXmin,
              graphXmax,
              graphYmin,
              graphYmax
            }
          };
        } else {
          return {
            setValue: {
              graphXmin: null,
              graphXmax: null,
              graphYmin: null,
              graphYmax: null
            }
          };
        }
      }
    }
  };
}
function returnConstraintDefinitions(arrayVariableForConstraints, arrayEntryPrefixForConstraints) {
  let stateVariableDefinitions = returnConstraintGraphInfoDefinitions();
  stateVariableDefinitions.independentComponentConstraints = {
    returnDependencies: () => ({
      constraintChildren: {
        dependencyType: "child",
        childGroups: ["constraints"],
        variableNames: ["independentComponentConstraints"]
      }
    }),
    definition: ({ dependencyValues }) => ({
      setValue: {
        independentComponentConstraints: dependencyValues.constraintChildren.every(
          (x2) => x2.stateValues.independentComponentConstraints
        )
      }
    })
  };
  stateVariableDefinitions.constraintResults = {
    additionalStateVariablesDefined: [
      {
        variableName: "constraintUsedByComponent",
        isArray: true,
        entryPrefixes: ["constraintUsedByComponent"]
      }
    ],
    isArray: true,
    entryPrefixes: ["constraintResult"],
    stateVariablesDeterminingDependencies: [
      "independentComponentConstraints"
    ],
    returnArraySizeDependencies: () => ({
      numDimensions: {
        dependencyType: "stateVariable",
        variableName: "numDimensionsForConstraints"
      }
    }),
    returnArraySize({ dependencyValues }) {
      return [dependencyValues.numDimensions];
    },
    returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
      let globalDependencies = {
        constraintChildren: {
          dependencyType: "child",
          childGroups: ["constraints"],
          variableNames: [
            "applyConstraint",
            "applyComponentConstraint"
          ],
          variablesOptional: true
        },
        independentComponentConstraints: {
          dependencyType: "stateVariable",
          variableName: "independentComponentConstraints"
        }
      };
      let arrayEntryPrefix = arrayEntryPrefixForConstraints;
      let dependenciesByKey = {};
      if (stateValues.independentComponentConstraints) {
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            x: {
              dependencyType: "stateVariable",
              variableName: arrayEntryPrefix + (Number(arrayKey) + 1)
            }
          };
        }
      } else {
        globalDependencies.xs = {
          dependencyType: "stateVariable",
          variableName: arrayVariableForConstraints
        };
      }
      return { globalDependencies, dependenciesByKey };
    },
    arrayDefinitionByKey({
      globalDependencyValues,
      dependencyValuesByKey,
      arrayKeys
    }) {
      if (globalDependencyValues.independentComponentConstraints) {
        let constraintResults = {};
        let constraintUsedByComponent = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          let variables = {
            ["x" + varEnding]: dependencyValuesByKey[arrayKey].x
          };
          let constraintUsed = false;
          for (let constraintChild of globalDependencyValues.constraintChildren) {
            let result = constraintChild.stateValues.applyComponentConstraint(
              variables
            );
            if (result.constrained) {
              variables["x" + varEnding] = convertValueToMathExpression(
                result.variables["x" + varEnding]
              );
              constraintUsed = true;
            }
          }
          constraintResults[arrayKey] = variables["x" + varEnding];
          constraintUsedByComponent[arrayKey] = constraintUsed;
        }
        return {
          setValue: {
            constraintResults,
            constraintUsedByComponent
          }
        };
      } else {
        let variables = {};
        let constraintUsed = false;
        for (let arrayKey in globalDependencyValues.xs) {
          variables[`x${Number(arrayKey) + 1}`] = globalDependencyValues.xs[arrayKey];
        }
        for (let constraintChild of globalDependencyValues.constraintChildren) {
          let constraintResult;
          if (constraintChild.stateValues.applyConstraint) {
            constraintResult = constraintChild.stateValues.applyConstraint(
              variables
            );
          } else {
            constraintResult = applyConstraintFromComponentConstraints(
              variables,
              constraintChild.stateValues.applyComponentConstraint
            );
          }
          if (constraintResult.constrained) {
            for (let varName in constraintResult.variables) {
              variables[varName] = convertValueToMathExpression(
                constraintResult.variables[varName]
              );
            }
            constraintUsed = true;
          }
        }
        let constraintResults = {};
        let constraintUsedByComponent = {};
        for (let arrayKey in globalDependencyValues.xs) {
          constraintResults[arrayKey] = variables[`x${Number(arrayKey) + 1}`];
          constraintUsedByComponent[arrayKey] = constraintUsed;
        }
        return {
          setValue: {
            constraintResults,
            constraintUsedByComponent
          }
        };
      }
    },
    inverseArrayDefinitionByKey: async function({
      desiredStateVariableValues,
      globalDependencyValues,
      dependencyValuesByKey,
      dependencyNamesByKey,
      stateValues,
      workspace
    }) {
      if (globalDependencyValues.independentComponentConstraints) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.constraintResults) {
          let varEnding = Number(arrayKey) + 1;
          let variables = {
            ["x" + varEnding]: convertValueToMathExpression(
              desiredStateVariableValues.constraintResults[arrayKey]
            )
          };
          for (let constraintChild of globalDependencyValues.constraintChildren) {
            let result = constraintChild.stateValues.applyComponentConstraint(
              variables
            );
            if (result.constrained) {
              variables["x" + varEnding] = convertValueToMathExpression(
                result.variables["x" + varEnding]
              );
            }
          }
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].x,
            desiredValue: variables["x" + varEnding]
          });
        }
        return {
          success: true,
          instructions
        };
      } else {
        let variables = {};
        if (!workspace.desiredConstraintResults) {
          workspace.desiredConstraintResults = {};
        }
        Object.assign(
          workspace.desiredConstraintResults,
          desiredStateVariableValues.constraintResults
        );
        let SVconstraintResults = await stateValues.constraintResults;
        for (let arrayKey in SVconstraintResults) {
          let varEnding = Number(arrayKey) + 1;
          if (arrayKey in workspace.desiredConstraintResults) {
            variables["x" + varEnding] = convertValueToMathExpression(
              workspace.desiredConstraintResults[arrayKey]
            );
          } else {
            variables["x" + varEnding] = convertValueToMathExpression(
              SVconstraintResults[arrayKey]
            );
          }
        }
        for (let constraintChild of globalDependencyValues.constraintChildren) {
          let constraintResult;
          if (constraintChild.stateValues.applyConstraint) {
            constraintResult = constraintChild.stateValues.applyConstraint(
              variables
            );
          } else {
            constraintResult = applyConstraintFromComponentConstraints(
              variables,
              constraintChild.stateValues.applyComponentConstraint
            );
          }
          if (constraintResult.constrained) {
            for (let varName in constraintResult.variables) {
              variables[varName] = convertValueToMathExpression(
                constraintResult.variables[varName]
              );
            }
          }
        }
        let constraintResults = {};
        for (let arrayKey in workspace.desiredConstraintResults) {
          constraintResults[arrayKey] = variables[`x${Number(arrayKey) + 1}`];
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "xs",
              desiredValue: constraintResults
            }
          ]
        };
      }
    }
  };
  stateVariableDefinitions.constraintUsed = {
    description: "Whether this constraint was applied to the component.",
    public: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    returnDependencies: () => ({
      constraintUsedByComponent: {
        dependencyType: "stateVariable",
        variableName: "constraintUsedByComponent"
      }
    }),
    definition: function({ dependencyValues }) {
      let constraintUsed = Object.values(
        dependencyValues.constraintUsedByComponent
      ).some((x2) => x2);
      return { setValue: { constraintUsed } };
    }
  };
  return stateVariableDefinitions;
}
function returnStickyGroupDefinitions() {
  return {
    inStickyGroup: {
      returnDependencies: () => ({
        stickyParent: {
          dependencyType: "parentIdentity",
          parentComponentType: "stickyGroup"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            inStickyGroup: Boolean(dependencyValues.stickyParent)
          }
        };
      }
    },
    stickyVerticesConstraintFunction: {
      returnDependencies: () => ({
        verticesConstraintFunction: {
          dependencyType: "parentStateVariable",
          parentComponentType: "stickyGroup",
          variableName: "verticesConstraintFunction"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            stickyVerticesConstraintFunction: dependencyValues.verticesConstraintFunction
          }
        };
      }
    },
    stickyPointConstraintFunction: {
      returnDependencies: () => ({
        pointConstraintFunction: {
          dependencyType: "parentStateVariable",
          parentComponentType: "stickyGroup",
          variableName: "pointConstraintFunction"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            stickyPointConstraintFunction: dependencyValues.pointConstraintFunction
          }
        };
      }
    },
    stickyObjectIndex: {
      returnDependencies: () => ({
        countAmongSiblings: {
          dependencyType: "countAmongSiblings",
          componentType: "_graphical",
          includeInheritedComponentTypes: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            stickyObjectIndex: dependencyValues.countAmongSiblings - 1
          }
        };
      }
    }
  };
}
function returnVertexConstraintFunction(constraintFunction) {
  return function({
    unconstrainedVertices,
    closed,
    enforceRigid,
    shrinkThreshold,
    vertexIndMoved
  }, ...args) {
    let numericalUnconstrainedVertices = unconstrainedVertices.map(
      (vertex) => vertex.map((v2) => v2.evaluate_to_constant())
    );
    let onlyMoveVertexInd = enforceRigid ? null : vertexIndMoved;
    let { numericalConstrainedVertices, constraintUsedForVertex } = constraintFunction(
      {
        numericalUnconstrainedVertices,
        closed,
        shrinkThreshold,
        onlyMoveVertexInd
      },
      ...args
    );
    if (constraintUsedForVertex.every((v2) => !v2)) {
      return unconstrainedVertices;
    } else if (!enforceRigid) {
      return numericalConstrainedVertices.map(
        (vertex) => vertex.map((v2) => me$3.fromAst(v2))
      );
    }
    let translationResults = [];
    let maxConstrainedUnmoved = 0;
    for (let vertexInd in unconstrainedVertices) {
      if (!constraintUsedForVertex[vertexInd]) {
        continue;
      }
      let translation = [];
      let numericalUnconstrainedVertex = numericalUnconstrainedVertices[vertexInd];
      let numericalConstrainedVertex = numericalConstrainedVertices[vertexInd];
      for (let dim in numericalUnconstrainedVertex) {
        let unconstrainedX = numericalUnconstrainedVertex[dim];
        let constrainedX = numericalConstrainedVertex[dim];
        let dx = constrainedX - unconstrainedX;
        translation.push(dx);
      }
      let translatedNumericalVertices = numericalUnconstrainedVertices.map(
        (numericalUnconstrainedVertex2) => numericalUnconstrainedVertex2.map(
          (v2, i2) => v2 + translation[i2]
        )
      );
      let {
        numericalConstrainedVertices: newVertices,
        constraintUsedForVertex: newConstraintsUsed
      } = constraintFunction(
        {
          numericalUnconstrainedVertices: translatedNumericalVertices,
          closed,
          shrinkThreshold
        },
        ...args
      );
      let numVerticesConstrainedUnmoved = 0;
      for (let vertexInd2 in translatedNumericalVertices) {
        if (!newConstraintsUsed[vertexInd2]) {
          continue;
        }
        let eps = 1e-6;
        let moved = false;
        let translatedVertex = translatedNumericalVertices[vertexInd2];
        let newVertex = newVertices[vertexInd2];
        for (let dim in numericalUnconstrainedVertex) {
          let translatedVertexX = translatedVertex[dim];
          let newX = newVertex[dim];
          let dx = newX - translatedVertexX;
          if (Math.abs(dx) > eps) {
            moved = true;
            break;
          }
        }
        if (!moved) {
          numVerticesConstrainedUnmoved++;
        }
      }
      if (numVerticesConstrainedUnmoved > maxConstrainedUnmoved) {
        translationResults = [
          {
            translation,
            vertexInd
          }
        ];
        maxConstrainedUnmoved = numVerticesConstrainedUnmoved;
      } else if (numVerticesConstrainedUnmoved == maxConstrainedUnmoved) {
        translationResults.push({
          translation,
          vertexInd
        });
      }
    }
    if (translationResults.length === 1) {
      let translation = translationResults[0].translation;
      return numericalUnconstrainedVertices.map(
        (unconstrainedVertex) => unconstrainedVertex.map(
          (v2, i2) => me$3.fromAst(v2 + translation[i2])
        )
      );
    }
    let minTranslation = [];
    let minTranslationMagnitude2 = Infinity;
    for (let result of translationResults) {
      let translation = result.translation;
      let translationMag2 = translation.reduce((a2, c2) => a2 + c2 * c2, 0);
      if (translationMag2 < minTranslationMagnitude2) {
        minTranslationMagnitude2 = translationMag2;
        minTranslation = translation;
      }
    }
    if (minTranslationMagnitude2 > 0) {
      return numericalUnconstrainedVertices.map(
        (unconstrainedVertex) => unconstrainedVertex.map(
          (v2, i2) => me$3.fromAst(v2 + minTranslation[i2])
        )
      );
    } else {
      return unconstrainedVertices;
    }
  };
}
function returnVertexConstraintFunctionFromEdges(constraintFunction) {
  return function({
    unconstrainedVertices,
    closed,
    enforceRigid,
    allowRotation,
    shrinkThreshold,
    rotationPoint
  }, ...args) {
    let numericalUnconstrainedVertices = unconstrainedVertices.map(
      (vertex) => vertex.map((v2) => v2.evaluate_to_constant())
    );
    let numericalUnconstrainedEdges = [];
    for (let ind = 1; ind < numericalUnconstrainedVertices.length; ind++) {
      numericalUnconstrainedEdges.push([
        numericalUnconstrainedVertices[ind - 1],
        numericalUnconstrainedVertices[ind]
      ]);
    }
    if (closed) {
      numericalUnconstrainedEdges.push([
        numericalUnconstrainedVertices[numericalUnconstrainedVertices.length - 1],
        numericalUnconstrainedVertices[0]
      ]);
    }
    let { numericalConstrainedEdges, constraintUsedForEdge } = constraintFunction(
      {
        numericalUnconstrainedEdges,
        allowRotation,
        enforceRigid,
        shrinkThreshold,
        rotationPoint
      },
      ...args
    );
    if (constraintUsedForEdge.every((v2) => !v2)) {
      return unconstrainedVertices;
    }
    let deviation2ByEdge = [];
    for (let edgeInd in numericalUnconstrainedEdges) {
      if (!constraintUsedForEdge[edgeInd]) {
        continue;
      }
      let numericalConstrainedEdge2 = numericalConstrainedEdges[edgeInd];
      let numericalUnconstrainedEdge2 = numericalUnconstrainedEdges[edgeInd];
      let deviation2 = 0;
      for (let vertexInd in numericalUnconstrainedEdge2) {
        let numericalUnconstrainedVertex = numericalUnconstrainedEdge2[vertexInd];
        let numericalConstrainedVertex = numericalConstrainedEdge2[vertexInd];
        for (let dim in numericalUnconstrainedVertex) {
          let numericalUnconstrainedX = numericalUnconstrainedVertex[dim];
          let numericalConstrainedX = numericalConstrainedVertex[dim];
          deviation2 += Math.pow(
            numericalUnconstrainedX - numericalConstrainedX,
            2
          );
        }
      }
      deviation2ByEdge.push({ edgeInd, deviation2 });
    }
    if (!enforceRigid) {
      deviation2ByEdge.sort((a2, b2) => a2.deviation2 - b2.deviation2);
      let constrainedVertices2 = [...numericalUnconstrainedVertices];
      let vertexIndsConstrained = [];
      let eps2 = 1e-6 ** 2;
      for (let { edgeInd, deviation2 } of deviation2ByEdge) {
        let vertexInd1 = Number(edgeInd);
        let vertexInd2 = (vertexInd1 + 1) % numericalUnconstrainedVertices.length;
        if (vertexIndsConstrained.includes(vertexInd1)) {
          let vec1 = constrainedVertices2[vertexInd1];
          let vec2 = numericalConstrainedEdges[edgeInd][0];
          if (Math.pow(vec1[0] - vec2[0], 2) + Math.pow(vec1[1] - vec2[1], 2) > eps2) {
            continue;
          }
        }
        if (vertexIndsConstrained.includes(vertexInd2)) {
          let vec1 = constrainedVertices2[vertexInd2];
          let vec2 = numericalConstrainedEdges[edgeInd][1];
          if (Math.pow(vec1[0] - vec2[0], 2) + Math.pow(vec1[1] - vec2[1], 2) > eps2) {
            continue;
          }
        }
        constrainedVertices2[vertexInd1] = numericalConstrainedEdges[edgeInd][0];
        constrainedVertices2[vertexInd2] = numericalConstrainedEdges[edgeInd][1];
        vertexIndsConstrained.push(vertexInd1);
        vertexIndsConstrained.push(vertexInd2);
      }
      return constrainedVertices2.map(
        (vertex) => vertex.map((v2) => me$3.fromAst(v2))
      );
    }
    let minDeviationInd = null;
    let minDeviation2 = Infinity;
    for (let { edgeInd, deviation2 } of deviation2ByEdge) {
      if (deviation2 < minDeviation2) {
        minDeviation2 = deviation2;
        minDeviationInd = Number(edgeInd);
      }
    }
    let numericalUnconstrainedEdge = numericalUnconstrainedEdges[minDeviationInd];
    let numericalConstrainedEdge = numericalConstrainedEdges[minDeviationInd];
    let translation = numericalConstrainedEdge[0].map(
      (v2, i2) => v2 - numericalUnconstrainedEdge[0][i2]
    );
    let constrained_rel = [
      numericalConstrainedEdge[1][0] - numericalConstrainedEdge[0][0],
      numericalConstrainedEdge[1][1] - numericalConstrainedEdge[0][1]
    ];
    let unconstrained_rel = [
      numericalUnconstrainedEdge[1][0] - numericalUnconstrainedEdge[0][0],
      numericalUnconstrainedEdge[1][1] - numericalUnconstrainedEdge[0][1]
    ];
    let theta = Math.atan2(constrained_rel[1], constrained_rel[0]) - Math.atan2(unconstrained_rel[1], unconstrained_rel[0]);
    let sin_theta = Math.sin(theta);
    let cos_theta = Math.cos(theta);
    let constrainedVertices = numericalUnconstrainedVertices.map(
      (vertex) => {
        let dx = vertex[0] + translation[0] - numericalConstrainedEdge[0][0];
        let dy = vertex[1] + translation[1] - numericalConstrainedEdge[0][1];
        let x2 = me$3.fromAst(
          numericalConstrainedEdge[0][0] + dx * cos_theta - dy * sin_theta
        );
        let y2 = me$3.fromAst(
          numericalConstrainedEdge[0][1] + dx * sin_theta + dy * cos_theta
        );
        return [x2, y2];
      }
    );
    return constrainedVertices;
  };
}
class Polyline extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      movePolyline: this.movePolyline.bind(this),
      movePolylineCenter: this.movePolylineCenter.bind(this),
      finalizePolylinePosition: this.finalizePolylinePosition.bind(this),
      reflectPolyline: this.reflectPolyline.bind(this),
      polylineClicked: this.polylineClicked.bind(this),
      polylineFocused: this.polylineFocused.bind(this)
    });
  }
  static componentType = "polyline";
  static styleOverrideCategories = ["line"];
  static componentDocs = {
    summary: "A polyline (open polygon) defined by a list of vertices"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the polyline can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.verticesDraggable = {
      createComponentOfType: "boolean",
      description: "Whether individual vertices may be dragged independently."
    };
    attributes.vertices = {
      createComponentOfType: "pointList",
      description: "The list of vertex points that define the shape."
    };
    attributes.showCoordsWhenDragging = {
      description: "Whether to show coordinate labels while dragging.",
      createComponentOfType: "boolean",
      createStateVariable: "showCoordsWhenDragging",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.rigid = {
      description: "Whether the polyline is treated as rigid (preserves shape under drag).",
      createComponentOfType: "boolean",
      createStateVariable: "rigid",
      defaultValue: false,
      public: true
    };
    attributes.preserveSimilarity = {
      createComponentOfType: "boolean",
      description: "Whether the shape is preserved up to similarity (uniform scaling) under drag."
    };
    attributes.rotationHandleVertices = {
      createComponentOfType: "numberList",
      createStateVariable: "rotationHandleVertices",
      defaultValue: [1],
      description: "Vertex indices that should display rotation handles when the shape is rigid or preserves similarity."
    };
    attributes.rotateAround = {
      createComponentOfType: "text",
      createStateVariable: "rotateAround",
      toLowerCase: true,
      validValues: [
        {
          value: "centroid",
          description: "Rotate around the centroid of the shape."
        },
        {
          value: "vertex",
          description: "Rotate around the vertex specified by `rotationVertex`."
        },
        {
          value: "point",
          description: "Rotate around the explicit point given by `rotationCenter`."
        }
      ],
      defaultValue: "centroid",
      description: "What point to rotate the shape around."
    };
    attributes.rotationCenter = {
      createComponentOfType: "point",
      createStateVariable: "rotationCenterPrescribed",
      defaultValue: null,
      description: "The point to rotate around when rotateAround is 'point'."
    };
    attributes.rotationVertex = {
      createComponentOfType: "integer",
      createStateVariable: "rotationVertex",
      defaultValue: 1,
      description: "The vertex index to rotate around when rotateAround is 'vertex'."
    };
    attributes.allowRotation = {
      description: "Whether the polyline can be rotated under drag.",
      createComponentOfType: "boolean",
      createStateVariable: "allowRotation",
      defaultValue: true,
      public: true
    };
    attributes.allowTranslation = {
      description: "Whether the polyline can be translated under drag.",
      createComponentOfType: "boolean",
      createStateVariable: "allowTranslation",
      defaultValue: true,
      public: true
    };
    attributes.allowDilation = {
      createComponentOfType: "boolean",
      description: "Whether the shape can be dilated (scaled) under drag."
    };
    attributes.minShrink = {
      createComponentOfType: "number",
      createStateVariable: "minShrink",
      defaultValue: 0.1,
      description: "Minimum scaling factor allowed when dilating."
    };
    attributes.allowReflection = {
      description: "Whether the polyline can be reflected under drag.",
      createComponentOfType: "boolean",
      createStateVariable: "allowReflection",
      defaultValue: true,
      public: true
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnChildGroups() {
    let groups = super.returnChildGroups();
    return groups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    Object.assign(stateVariableDefinitions, returnStickyGroupDefinitions());
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the polyline's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let styleDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (styleDescription) {
            styleDescription += " ";
          }
          styleDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (styleDescription) {
          styleDescription += " ";
        }
        styleDescription += lineColorWord;
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "polyline".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " polyline";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.preserveSimilarity = {
      description: "Whether the polyline preserves its shape (up to similarity).",
      public: true,
      hasEssential: true,
      defaultValue: false,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        preserveSimilarityAttr: {
          dependencyType: "attributeComponent",
          attributeName: "preserveSimilarity",
          variableNames: ["value"]
        },
        rigid: {
          dependencyType: "stateVariable",
          variableName: "rigid"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.rigid) {
          return { setValue: { preserveSimilarity: true } };
        }
        if (dependencyValues.preserveSimilarityAttr !== null) {
          return {
            setValue: {
              preserveSimilarity: dependencyValues.preserveSimilarityAttr.stateValues.value
            }
          };
        }
        return {
          useEssentialOrDefaultValue: { preserveSimilarity: true }
        };
      }
    };
    stateVariableDefinitions.allowDilation = {
      description: "Whether the polyline can be dilated under drag.",
      public: true,
      hasEssential: true,
      defaultValue: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        allowDilationAttr: {
          dependencyType: "attributeComponent",
          attributeName: "allowDilation",
          variableNames: ["value"]
        },
        rigid: {
          dependencyType: "stateVariable",
          variableName: "rigid"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.rigid) {
          return { setValue: { allowDilation: false } };
        }
        if (dependencyValues.allowDilationAttr !== null) {
          return {
            setValue: {
              allowDilation: dependencyValues.allowDilationAttr.stateValues.value
            }
          };
        }
        return { useEssentialOrDefaultValue: { allowDilation: true } };
      }
    };
    stateVariableDefinitions.verticesDraggable = {
      description: "Whether each vertex can be dragged independently.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      hasEssential: true,
      forRenderer: true,
      returnDependencies: () => ({
        verticesDraggableAttr: {
          dependencyType: "attributeComponent",
          attributeName: "verticesDraggable",
          variableNames: ["value"]
        },
        draggable: {
          dependencyType: "stateVariable",
          variableName: "draggable"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.verticesDraggableAttr) {
          return {
            setValue: {
              verticesDraggable: dependencyValues.verticesDraggableAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              verticesDraggable: {
                defaultValue: dependencyValues.draggable
              }
            }
          };
        }
      }
    };
    stateVariableDefinitions.vertexIndicesDraggable = {
      forRenderer: true,
      returnDependencies: () => ({
        verticesDraggable: {
          dependencyType: "stateVariable",
          variableName: "verticesDraggable"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        },
        rigid: {
          dependencyType: "stateVariable",
          variableName: "rigid"
        },
        preserveSimilarity: {
          dependencyType: "stateVariable",
          variableName: "preserveSimilarity"
        },
        rotationHandleVertices: {
          dependencyType: "stateVariable",
          variableName: "rotationHandleVertices"
        },
        rotateAround: {
          dependencyType: "stateVariable",
          variableName: "rotateAround"
        },
        rotationVertex: {
          dependencyType: "stateVariable",
          variableName: "rotationVertex"
        }
      }),
      definition({ dependencyValues }) {
        let vertexIndicesDraggable = [];
        if (dependencyValues.verticesDraggable) {
          if (dependencyValues.rigid || dependencyValues.preserveSimilarity) {
            let rotationVertex = dependencyValues.rotateAround === "vertex" ? dependencyValues.rotationVertex : null;
            for (let vertexNum of dependencyValues.rotationHandleVertices) {
              if (Number.isInteger(vertexNum) && vertexNum > 0 && vertexNum <= dependencyValues.numVertices && vertexNum !== rotationVertex) {
                vertexIndicesDraggable.push(vertexNum - 1);
              }
            }
            if (vertexIndicesDraggable.length === 0) {
              vertexIndicesDraggable = [
                ...Array(dependencyValues.numVertices).keys()
              ].filter((x2) => x2 !== rotationVertex - 1);
            }
          } else {
            vertexIndicesDraggable = [
              ...Array(dependencyValues.numVertices).keys()
            ];
          }
        }
        return { setValue: { vertexIndicesDraggable } };
      }
    };
    stateVariableDefinitions.numVertices = {
      description: "The number of vertices in the polyline.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({
        vertices: {
          dependencyType: "attributeComponent",
          attributeName: "vertices",
          variableNames: ["numPoints"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.vertices !== null) {
          return {
            setValue: {
              numVertices: dependencyValues.vertices.stateValues.numPoints
            }
          };
        } else {
          return { setValue: { numVertices: 0 } };
        }
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "The number of dimensions the polyline lives in.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies() {
        return {
          vertices: {
            dependencyType: "attributeComponent",
            attributeName: "vertices",
            variableNames: ["numDimensions"]
          }
        };
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.vertices !== null) {
          let numDimensions = dependencyValues.vertices.stateValues.numDimensions;
          return {
            setValue: { numDimensions: Math.max(2, numDimensions) },
            checkForActualChange: { numDimensions: true }
          };
        } else {
          return { setValue: { numDimensions: 2 } };
        }
      }
    };
    stateVariableDefinitions.desiredUnconstrainedVertices = {
      isArray: true,
      numDimensions: 2,
      hasEssential: true,
      doNotShadowEssential: true,
      entryPrefixes: [
        "desiredUnconstrainedVertexX",
        "desiredUnconstrainedVertex"
      ],
      defaultValueByArrayKey: () => null,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "desiredUnconstrainedVertexX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numVertices,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey() {
        return { dependenciesByKey: {} };
      },
      arrayDefinitionByKey({ arrayKeys }) {
        let useEssential = {};
        for (let arrayKey of arrayKeys) {
          useEssential[arrayKey] = true;
        }
        return {
          useEssentialOrDefaultValue: {
            desiredUnconstrainedVertices: useEssential
          }
        };
      },
      async inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let essentialVertices = {};
        for (let arrayKey in desiredStateVariableValues.desiredUnconstrainedVertices) {
          essentialVertices[arrayKey] = desiredStateVariableValues.desiredUnconstrainedVertices[arrayKey];
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "desiredUnconstrainedVertices",
              value: essentialVertices
            }
          ]
        };
      }
    };
    stateVariableDefinitions.unconstrainedVertices = {
      isLocation: true,
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["unconstrainedVertexX", "unconstrainedVertex"],
      returnEntryDimensions: (prefix) => prefix === "unconstrainedVertex" ? 1 : 0,
      set: (x2) => x2 === null ? null : convertValueToMathExpression(x2),
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "unconstrainedVertexX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      getAllArrayKeys(arraySize, flatten2 = true, desiredSize) {
        function getAllArrayKeysSub(subArraySize) {
          if (subArraySize.length === 1) {
            return Array.from(
              Array(subArraySize[0]),
              (_2, i2) => String(i2)
            );
          } else {
            let currentSize = subArraySize[0];
            let subSubKeys = getAllArrayKeysSub(
              subArraySize.slice(1)
            );
            let subKeys = [];
            for (let ind = 0; ind < currentSize; ind++) {
              if (flatten2) {
                subKeys.push(
                  ...subSubKeys.map((x2) => ind + "," + x2)
                );
              } else {
                subKeys.push(
                  subSubKeys.map((x2) => ind + "," + x2)
                );
              }
            }
            return subKeys;
          }
        }
        if (desiredSize) {
          if (desiredSize.length === 0 || !arraySize) {
            return [];
          } else {
            let desiredSizeOfWholeArray = [...arraySize];
            desiredSizeOfWholeArray[0] = desiredSize[0];
            return getAllArrayKeysSub(desiredSizeOfWholeArray);
          }
        } else if (!arraySize || arraySize.length === 0) {
          return [];
        } else {
          return getAllArrayKeysSub(arraySize);
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "unconstrainedVertices") {
          if (propIndex.length === 1) {
            return "unconstrainedVertex" + propIndex[0];
          } else {
            return `unconstrainedVertexX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 19) === "unconstrainedVertex") {
          let vertexNum = Number(varName.slice(19));
          if (Number.isInteger(vertexNum) && vertexNum > 0) {
            return `unconstrainedVertexX${vertexNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numVertices,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          dependenciesByKey[arrayKey] = {
            vertices: {
              dependencyType: "attributeComponent",
              attributeName: "vertices",
              variableNames: ["pointX" + varEnding]
            },
            // just for inverse definition
            desiredUnconstrainedVertices: {
              dependencyType: "stateVariable",
              variableName: "desiredUnconstrainedVertexX" + varEnding
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let unconstrainedVertices = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          let verticesAttr = dependencyValuesByKey[arrayKey].vertices;
          if (verticesAttr !== null && verticesAttr.stateValues["pointX" + varEnding]) {
            unconstrainedVertices[arrayKey] = verticesAttr.stateValues["pointX" + varEnding];
          } else {
            unconstrainedVertices[arrayKey] = me$3.fromAst("＿");
          }
        }
        return { setValue: { unconstrainedVertices } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.unconstrainedVertices) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          if (dependencyValuesByKey[arrayKey].vertices !== null && dependencyValuesByKey[arrayKey].vertices.stateValues["pointX" + varEnding]) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].vertices,
              desiredValue: desiredStateVariableValues.unconstrainedVertices[arrayKey],
              variableIndex: 0
            });
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].desiredUnconstrainedVertices,
              desiredValue: desiredStateVariableValues.unconstrainedVertices[arrayKey]
            });
          } else {
            return { success: false };
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.haveConstrainedVertices = {
      returnDependencies: () => ({
        // vertexConstraintsChild: {
        //     dependencyType: "child",
        //     childGroups: ["vertexConstraints"],
        // },
        // edgeConstraintsChild: {
        //     dependencyType: "child",
        //     childGroups: ["edgeConstraints"],
        // },
        inStickyGroup: {
          dependencyType: "stateVariable",
          variableName: "inStickyGroup"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveConstrainedVertices: (
              // dependencyValues.vertexConstraintsChild.length >
              //     0 ||
              // dependencyValues.edgeConstraintsChild.length > 0 ||
              dependencyValues.inStickyGroup
            )
          }
        };
      }
    };
    stateVariableDefinitions.vertices = {
      public: true,
      description: "The vertices of the polyline.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "vertexX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      indexAliases: [[], ["x", "y", "z"]],
      entryPrefixes: ["vertexX", "vertex"],
      returnEntryDimensions: (prefix) => prefix === "vertex" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "vertexX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      getAllArrayKeys(arraySize, flatten2 = true, desiredSize) {
        function getAllArrayKeysSub(subArraySize) {
          if (subArraySize.length === 1) {
            return Array.from(
              Array(subArraySize[0]),
              (_2, i2) => String(i2)
            );
          } else {
            let currentSize = subArraySize[0];
            let subSubKeys = getAllArrayKeysSub(
              subArraySize.slice(1)
            );
            let subKeys = [];
            for (let ind = 0; ind < currentSize; ind++) {
              if (flatten2) {
                subKeys.push(
                  ...subSubKeys.map((x2) => ind + "," + x2)
                );
              } else {
                subKeys.push(
                  subSubKeys.map((x2) => ind + "," + x2)
                );
              }
            }
            return subKeys;
          }
        }
        if (desiredSize) {
          if (desiredSize.length === 0 || !arraySize) {
            return [];
          } else {
            let desiredSizeOfWholeArray = [...arraySize];
            desiredSizeOfWholeArray[0] = desiredSize[0];
            return getAllArrayKeysSub(desiredSizeOfWholeArray);
          }
        } else if (!arraySize || arraySize.length === 0) {
          return [];
        } else {
          return getAllArrayKeysSub(arraySize);
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "vertices") {
          if (propIndex.length === 1) {
            return "vertex" + propIndex[0];
          } else {
            return `vertexX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 6) === "vertex") {
          let vertexNum = Number(varName.slice(6));
          if (Number.isInteger(vertexNum) && vertexNum > 0) {
            return `vertexX${vertexNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numVertices,
          dependencyValues.numDimensions
        ];
      },
      stateVariablesDeterminingDependencies: [
        "preserveSimilarity",
        "haveConstrainedVertices"
      ],
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          preserveSimilarity: {
            dependencyType: "stateVariable",
            variableName: "preserveSimilarity"
          },
          closed: {
            dependencyType: "stateVariable",
            variableName: "closed"
          },
          haveConstrainedVertices: {
            dependencyType: "stateVariable",
            variableName: "haveConstrainedVertices"
          },
          // used only in the inverse definition
          rotationReferenceMapping: {
            dependencyType: "stateVariable",
            variableName: "rotationReferenceMapping"
          }
        };
        let dependenciesByKey = {};
        if (stateValues.haveConstrainedVertices || stateValues.preserveSimilarity) {
          globalDependencies.unconstrainedVertices = {
            dependencyType: "stateVariable",
            variableName: "unconstrainedVertices"
          };
        } else {
          for (let arrayKey of arrayKeys) {
            let [pointInd, dim] = arrayKey.split(",");
            let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
            dependenciesByKey[arrayKey] = {
              unconstrainedVertex: {
                dependencyType: "stateVariable",
                variableName: "unconstrainedVertexX" + varEnding
              }
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        arraySize
      }) {
        let vertices = {};
        if (globalDependencyValues.haveConstrainedVertices) {
          let constrainedVertices = globalDependencyValues.unconstrainedVertices;
          for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
            for (let dim = 0; dim < arraySize[1]; dim++) {
              let arrayKey = pointInd + "," + dim;
              vertices[arrayKey] = constrainedVertices[pointInd][dim];
            }
          }
        } else if (globalDependencyValues.preserveSimilarity) {
          for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
            for (let dim = 0; dim < arraySize[1]; dim++) {
              let arrayKey = pointInd + "," + dim;
              vertices[arrayKey] = globalDependencyValues.unconstrainedVertices[pointInd][dim];
            }
          }
        } else {
          for (let arrayKey of arrayKeys) {
            vertices[arrayKey] = dependencyValuesByKey[arrayKey].unconstrainedVertex;
          }
        }
        return { setValue: { vertices } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues,
        arraySize,
        workspace,
        overrides
      }) {
        let instructions = [];
        let movedJustOneVertex = false;
        let vertexIndMoved;
        Object.assign(workspace, desiredStateVariableValues.vertices);
        let nMoved = Object.keys(workspace).length;
        if (nMoved === 1) {
          movedJustOneVertex = true;
          vertexIndMoved = Number(
            Object.keys(workspace)[0].split(",")[0]
          );
        } else if (nMoved === 2) {
          let pointInd1 = Object.keys(workspace)[0].split(",")[0];
          let pointInd2 = Object.keys(workspace)[1].split(",")[0];
          if (pointInd1 === pointInd2) {
            movedJustOneVertex = true;
            vertexIndMoved = Number(pointInd1);
          }
        }
        if (globalDependencyValues.preserveSimilarity) {
          if (arraySize[1] !== 2) {
            console.error(
              "Moving a rigid/preserveSimilarity polyline in inverse direction not implemented in other than 2D"
            );
            return { success: false };
          }
          let allowRotation = movedJustOneVertex && await stateValues.allowRotation;
          let allowDilation = movedJustOneVertex && await stateValues.allowDilation;
          let allowTranslation = await stateValues.allowTranslation;
          let rotateOrDilate = allowRotation || allowDilation;
          const isReflection = Boolean(overrides?.isReflection);
          if (!(rotateOrDilate || allowTranslation || isReflection)) {
            return { success: false };
          }
          if (isReflection) {
            let desired_vertices = [];
            for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
              desired_vertices.push([]);
              for (let dim = 0; dim < arraySize[1]; dim++) {
                desired_vertices[pointInd].push(
                  convertValueToMathExpression(
                    workspace[`${pointInd},${dim}`]
                  )
                );
              }
            }
            if (globalDependencyValues.haveConstrainedVertices) {
              if (await stateValues.inStickyGroup) {
                let stickyObjectIndex = await stateValues.stickyObjectIndex;
                let stickyVerticesConstraintFunction = await stateValues.stickyVerticesConstraintFunction;
                desired_vertices = stickyVerticesConstraintFunction(
                  {
                    unconstrainedVertices: desired_vertices,
                    closed: globalDependencyValues.closed,
                    enforceRigid: true,
                    allowRotation: false,
                    shrinkThreshold: false
                  },
                  { objectInd: stickyObjectIndex }
                );
              }
            }
            instructions.push({
              setDependency: "unconstrainedVertices",
              desiredValue: desired_vertices
            });
          } else if (rotateOrDilate) {
            let numericalCentroidUnconstrained = await stateValues.numericalCentroidUnconstrained;
            let rotationReferenceMapping = globalDependencyValues.rotationReferenceMapping;
            let referenceCentroid;
            let referenceVertices;
            if (Array.isArray(rotationReferenceMapping[0]) && rotationReferenceMapping[0][0] === numericalCentroidUnconstrained[0] && rotationReferenceMapping[0][1] === numericalCentroidUnconstrained[1]) {
              referenceCentroid = rotationReferenceMapping[1];
              referenceVertices = rotationReferenceMapping[2];
            } else {
              referenceCentroid = numericalCentroidUnconstrained;
              referenceVertices = globalDependencyValues.unconstrainedVertices.map(
                (v2) => v2.map((c2) => c2.evaluate_to_constant())
              );
            }
            let rotateAround = await stateValues.rotateAround;
            let rotationPoint;
            if (rotateAround === "vertex") {
              let rotationVertex = await stateValues.rotationVertex;
              rotationPoint = referenceVertices[rotationVertex - 1];
              if (!rotationPoint) {
                rotationPoint = referenceCentroid;
              }
            } else if (rotateAround === "point") {
              let rotationCenter = await stateValues.rotationCenterPrescribed;
              if (rotationCenter && ["vector", "tuple"].includes(
                rotationCenter.tree[0]
              ) && rotationCenter.tree.length === 3) {
                rotationPoint = [
                  rotationCenter.get_component(0).evaluate_to_constant(),
                  rotationCenter.get_component(1).evaluate_to_constant()
                ];
                if (!rotationPoint.every(
                  (v2) => Number.isFinite(v2)
                )) {
                  rotationPoint = referenceCentroid;
                }
              } else {
                rotationPoint = referenceCentroid;
              }
            } else {
              rotationPoint = referenceCentroid;
            }
            let [pointInd1, dim1] = Object.keys(workspace)[0].split(",");
            let original_vertex = referenceVertices[pointInd1];
            let moved_vertex = [...original_vertex];
            for (let arrayKey in workspace) {
              let moved_x = convertValueToMathExpression(
                workspace[arrayKey]
              ).evaluate_to_constant();
              if (!Number.isFinite(moved_x)) {
                return { success: false };
              }
              let dim = arrayKey.split(",")[1];
              moved_vertex[dim] = moved_x;
            }
            let moved_rel = [
              moved_vertex[0] - rotationPoint[0],
              moved_vertex[1] - rotationPoint[1]
            ];
            let orig_rel = [
              original_vertex[0] - rotationPoint[0],
              original_vertex[1] - rotationPoint[1]
            ];
            let orig_mag2;
            if (allowDilation) {
              orig_mag2 = orig_rel[0] ** 2 + orig_rel[1] ** 2;
            }
            let desired_vertices = [];
            if (allowRotation) {
              let theta = Math.atan2(moved_rel[1], moved_rel[0]) - Math.atan2(orig_rel[1], orig_rel[0]);
              let stretchFactor = 1;
              if (allowDilation) {
                stretchFactor = Math.sqrt(
                  (moved_rel[0] ** 2 + moved_rel[1] ** 2) / orig_mag2
                );
              }
              let c_sin_theta = stretchFactor * Math.sin(theta);
              let c_cos_theta = stretchFactor * Math.cos(theta);
              for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
                desired_vertices.push([]);
                let original_vertex2 = referenceVertices[pointInd];
                let orig_rel2 = [
                  original_vertex2[0] - rotationPoint[0],
                  original_vertex2[1] - rotationPoint[1]
                ];
                let rot_rel = [
                  c_cos_theta * orig_rel2[0] - c_sin_theta * orig_rel2[1],
                  c_sin_theta * orig_rel2[0] + c_cos_theta * orig_rel2[1]
                ];
                for (let dim = 0; dim < arraySize[1]; dim++) {
                  desired_vertices[pointInd].push(
                    me$3.fromAst(
                      rot_rel[dim] + rotationPoint[dim]
                    )
                  );
                }
              }
            } else {
              let dot_prod = moved_rel[0] * orig_rel[0] + moved_rel[1] * orig_rel[1];
              let orig_mag = Math.sqrt(orig_mag2);
              let minShrink = await stateValues.minShrink;
              let factor;
              if (orig_mag === 0) {
                factor = 1;
              } else if (!(dot_prod >= minShrink * orig_mag)) {
                factor = minShrink / orig_mag;
              } else {
                factor = dot_prod / orig_mag2;
              }
              for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
                let dilated = referenceVertices[pointInd].map(
                  (x2, i2) => me$3.fromAst(
                    (x2 - rotationPoint[i2]) * factor + rotationPoint[i2]
                  )
                );
                desired_vertices.push(dilated);
              }
            }
            if (globalDependencyValues.haveConstrainedVertices) {
              if (await stateValues.inStickyGroup) {
                let stickyObjectIndex = await stateValues.stickyObjectIndex;
                let stickyVerticesConstraintFunction = await stateValues.stickyVerticesConstraintFunction;
                desired_vertices = stickyVerticesConstraintFunction(
                  {
                    unconstrainedVertices: desired_vertices,
                    closed: globalDependencyValues.closed,
                    enforceRigid: true,
                    allowRotation,
                    shrinkThreshold: true,
                    rotationPoint
                  },
                  { objectInd: stickyObjectIndex }
                );
              }
              let constrainedCentroid = calculateNumericalCentroid(desired_vertices);
              instructions.push({
                setDependency: "rotationReferenceMapping",
                desiredValue: [
                  constrainedCentroid,
                  referenceCentroid,
                  referenceVertices
                ]
              });
            }
            instructions.push({
              setDependency: "unconstrainedVertices",
              desiredValue: desired_vertices
            });
          } else {
            let min_dx = Infinity;
            let min_dy = Infinity;
            let numericalVertices = await stateValues.numericalVertices;
            for (let arrayKey in workspace) {
              let [pointInd, dim] = arrayKey.split(",");
              let moved_val = convertValueToMathExpression(
                workspace[arrayKey]
              ).evaluate_to_constant();
              let d2 = moved_val - numericalVertices[pointInd][dim];
              if (dim === "0") {
                if (Math.abs(d2) < Math.abs(min_dx)) {
                  min_dx = d2;
                }
              } else if (Math.abs(d2) < Math.abs(min_dy)) {
                min_dy = d2;
              }
            }
            if (min_dx === Infinity) {
              min_dx = 0;
            }
            if (min_dy === Infinity) {
              min_dy = 0;
            }
            let desired_vertices = [];
            let min_d = [min_dx, min_dy];
            for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
              desired_vertices.push([]);
              let original_vertex = numericalVertices[pointInd];
              for (let dim = 0; dim < arraySize[1]; dim++) {
                desired_vertices[pointInd].push(
                  me$3.fromAst(
                    original_vertex[dim] + min_d[dim]
                  )
                );
              }
            }
            if (globalDependencyValues.haveConstrainedVertices) {
              if (await stateValues.inStickyGroup) {
                let stickyObjectIndex = await stateValues.stickyObjectIndex;
                let stickyVerticesConstraintFunction = await stateValues.stickyVerticesConstraintFunction;
                desired_vertices = stickyVerticesConstraintFunction(
                  {
                    unconstrainedVertices: desired_vertices,
                    closed: globalDependencyValues.closed,
                    enforceRigid: true,
                    allowRotation: false,
                    shrinkThreshold: false
                  },
                  { objectInd: stickyObjectIndex }
                );
              }
              instructions.push({
                setDependency: "rotationReferenceMapping",
                desiredValue: [null, null, null]
              });
            }
            instructions.push({
              setDependency: "unconstrainedVertices",
              desiredValue: desired_vertices
            });
          }
        } else {
          if (globalDependencyValues.haveConstrainedVertices) {
            let vertices = await stateValues.vertices;
            let desired_vertices = [];
            for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
              let desired_vertex = [];
              let original_vertex = vertices[pointInd];
              for (let dim = 0; dim < arraySize[1]; dim++) {
                let arrayKey = pointInd + "," + dim;
                if (arrayKey in workspace) {
                  desired_vertex.push(workspace[arrayKey]);
                } else {
                  desired_vertex.push(original_vertex[dim]);
                }
              }
              desired_vertices.push(desired_vertex);
            }
            let enforceRigid = !movedJustOneVertex;
            let allowRotation = movedJustOneVertex;
            if (await stateValues.inStickyGroup) {
              let stickyObjectIndex = await stateValues.stickyObjectIndex;
              let stickyVerticesConstraintFunction = await stateValues.stickyVerticesConstraintFunction;
              desired_vertices = stickyVerticesConstraintFunction(
                {
                  unconstrainedVertices: desired_vertices,
                  closed: globalDependencyValues.closed,
                  enforceRigid,
                  allowRotation,
                  shrinkThreshold: false,
                  vertexIndMoved
                },
                { objectInd: stickyObjectIndex }
              );
            }
            instructions.push({
              setDependency: "unconstrainedVertices",
              desiredValue: desired_vertices
            });
          } else {
            for (let arrayKey in desiredStateVariableValues.vertices) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].unconstrainedVertex,
                desiredValue: desiredStateVariableValues.vertices[arrayKey]
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalVertices = {
      isArray: true,
      entryPrefixes: ["numericalVertex"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numVertices];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            vertex: {
              dependencyType: "stateVariable",
              variableName: "vertex" + (Number(arrayKey) + 1)
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalVertices = {};
        for (let arrayKey of arrayKeys) {
          let vert = dependencyValuesByKey[arrayKey].vertex.map(
            (x2) => x2.evaluate_to_constant()
          );
          if (!vert.every((x2) => Number.isFinite(x2))) {
            vert = Array(vert.length).fill(NaN);
          }
          numericalVertices[arrayKey] = vert;
        }
        return { setValue: { numericalVertices } };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalVertices: {
          dependencyType: "stateVariable",
          variableName: "numericalVertices"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues }) {
        let numDimensions = dependencyValues.numDimensions;
        let numVertices = dependencyValues.numVertices;
        let numericalVertices = dependencyValues.numericalVertices;
        let vals = [];
        let prPtx, prPty;
        let nxPtx = numericalVertices[0]?.[0];
        let nxPty = numericalVertices[0]?.[1];
        for (let i2 = 1; i2 < numVertices; i2++) {
          prPtx = nxPtx;
          prPty = nxPty;
          nxPtx = numericalVertices[i2]?.[0];
          nxPty = numericalVertices[i2]?.[1];
          if (!(Number.isFinite(prPtx) && Number.isFinite(prPty) && Number.isFinite(nxPtx) && Number.isFinite(nxPty))) {
            vals.push(null);
          } else {
            let BA1sub = nxPtx - prPtx;
            let BA2sub = nxPty - prPty;
            if (BA1sub === 0 && BA2sub === 0) {
              vals.push(null);
            } else {
              vals.push([BA1sub, BA2sub]);
            }
          }
        }
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              let xscale = scales[0];
              let yscale = scales[1];
              if (numDimensions !== 2 || numVertices === 0) {
                return {};
              }
              let closestDistance2 = Infinity;
              let closestResult = {};
              let x1 = variables.x1?.evaluate_to_constant();
              let x2 = variables.x2?.evaluate_to_constant();
              let prevPtx, prevPty;
              let nextPtx = numericalVertices[0][0];
              let nextPty = numericalVertices[0][1];
              for (let i2 = 1; i2 < numVertices; i2++) {
                prevPtx = nextPtx;
                prevPty = nextPty;
                nextPtx = numericalVertices[i2][0];
                nextPty = numericalVertices[i2][1];
                let val = vals[i2 - 1];
                if (val === null) {
                  continue;
                }
                let BA1 = val[0] / xscale;
                let BA2 = val[1] / yscale;
                let denom = BA1 * BA1 + BA2 * BA2;
                let t2 = ((x1 - prevPtx) / xscale * BA1 + (x2 - prevPty) / yscale * BA2) / denom;
                let result;
                if (t2 <= 0) {
                  result = { x1: prevPtx, x2: prevPty };
                } else if (t2 >= 1) {
                  result = { x1: nextPtx, x2: nextPty };
                } else {
                  result = {
                    x1: prevPtx + t2 * BA1 * xscale,
                    x2: prevPty + t2 * BA2 * yscale
                  };
                }
                let distance2 = Math.pow((x1 - result.x1) / xscale, 2) + Math.pow((x2 - result.x2) / yscale, 2);
                if (distance2 < closestDistance2) {
                  closestDistance2 = distance2;
                  closestResult = result;
                }
              }
              if (variables.x3 !== void 0 && Object.keys(closestResult).length > 0) {
                closestResult.x3 = 0;
              }
              return closestResult;
            }
          }
        };
      }
    };
    stateVariableDefinitions.length = {
      description: "The total length of the polyline.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        numericalVertices: {
          dependencyType: "stateVariable",
          variableName: "numericalVertices"
        }
      }),
      definition({ dependencyValues }) {
        let length = 0;
        let verts = dependencyValues.numericalVertices;
        let numVertices = dependencyValues.numericalVertices.length;
        for (let i2 = 0; i2 < numVertices - 1; i2++) {
          let dx = verts[i2 + 1][0] - verts[i2][0];
          let dy = verts[i2 + 1][1] - verts[i2][1];
          length += Math.sqrt(dx * dx + dy * dy);
        }
        return { setValue: { length } };
      }
    };
    stateVariableDefinitions.center = {
      description: "The centroid of the polyline's vertices.",
      public: true,
      forRenderer: true,
      isLocation: true,
      isArray: true,
      entryPrefixes: ["centerX"],
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "centerX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          vertices: {
            dependencyType: "stateVariable",
            variableName: "vertices"
          },
          numVertices: {
            dependencyType: "stateVariable",
            variableName: "numVertices"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let center = {};
        let numVertices = globalDependencyValues.numVertices;
        for (let arrayKey of arrayKeys) {
          let dim = Number(arrayKey);
          if (!(numVertices > 0)) {
            center[arrayKey] = me$3.fromAst(NaN);
            continue;
          }
          let centerComponent = globalDependencyValues.vertices[0][dim];
          for (let pointInd = 1; pointInd < numVertices; pointInd++) {
            centerComponent = centerComponent.add(
              globalDependencyValues.vertices[pointInd][dim]
            );
          }
          center[arrayKey] = centerComponent.divide(numVertices).simplify();
        }
        return { setValue: { center } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        stateValues
      }) {
        let center = await stateValues.center;
        let desiredVertices = globalDependencyValues.vertices.map(
          (v2) => [...v2]
        );
        for (let arrayKey in desiredStateVariableValues.center) {
          let dim = Number(arrayKey);
          let offset = desiredStateVariableValues.center[arrayKey].subtract(center[dim]).simplify();
          for (let pointInd = 0; pointInd < desiredVertices.length; pointInd++) {
            desiredVertices[pointInd][dim] = desiredVertices[pointInd][dim].add(offset).simplify();
          }
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "vertices",
              desiredValue: desiredVertices
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numericalCentroidUnconstrained = {
      returnDependencies: () => ({
        unconstrainedVertices: {
          dependencyType: "stateVariable",
          variableName: "unconstrainedVertices"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numericalCentroidUnconstrained: calculateNumericalCentroid(
              dependencyValues.unconstrainedVertices
            )
          }
        };
      }
    };
    stateVariableDefinitions.rotationReferenceMapping = {
      hasEssential: true,
      defaultValue: [null, null, null],
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { rotationReferenceMapping: true }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "rotationReferenceMapping",
              value: desiredStateVariableValues.rotationReferenceMapping
            }
          ]
        };
      }
    };
    stateVariableDefinitions.closed = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { closed: false } })
    };
    return stateVariableDefinitions;
  }
  /**
   * After a move action has been applied, checks whether constraints caused
   * a strict subset of vertices to land at positions other than the ones
   * requested.  If so, and if all displaced vertices share the same
   * translational offset (i.e. the displacement is a rigid translation),
   * the remaining (unconstrained) vertices are adjusted by that same offset
   * so the polyline moves as a rigid body.
   *
   * Returns the result of the follow-up `performUpdate` call when a
   * correction was applied, or `null` when no correction was needed.
   */
  async _attemptConstrainedRigidTranslationReconciliation({
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let desiredUnconstrainedVertices = await this.stateValues.desiredUnconstrainedVertices;
    if (desiredUnconstrainedVertices.length === 0 || desiredUnconstrainedVertices[0][0] == null) {
      return null;
    }
    let desiredNumericalVertices = desiredUnconstrainedVertices.map(
      (vertex) => vertex.map((v2) => v2.evaluate_to_constant())
    );
    let resultingNumericalVertices = await this.stateValues.numericalVertices;
    let numVertices = await this.stateValues.numVertices;
    let verticesChanged = [];
    let numVerticesChanged = 0;
    let tol = 1e-6;
    for (let [ind, vrtx] of desiredNumericalVertices.entries()) {
      if (!vrtx.every(
        (v2, i2) => Math.abs(v2 - resultingNumericalVertices[ind][i2]) < tol
      )) {
        verticesChanged.push(ind);
        numVerticesChanged++;
      }
    }
    if (!(numVerticesChanged > 0 && numVerticesChanged < numVertices)) {
      return null;
    }
    let changedInd1 = verticesChanged[0];
    let relationshipPreserved = true;
    let orig1 = desiredNumericalVertices[changedInd1];
    let changed1 = resultingNumericalVertices[changedInd1];
    let changevec1 = orig1.map((v2, i2) => v2 - changed1[i2]);
    if (numVerticesChanged > 1) {
      for (let ind of verticesChanged.slice(1)) {
        let orig2 = desiredNumericalVertices[ind];
        let changed2 = resultingNumericalVertices[ind];
        let changevec2 = orig2.map((v2, i2) => v2 - changed2[i2]);
        if (!changevec1.every(
          (v2, i2) => Math.abs(v2 - changevec2[i2]) < tol
        )) {
          relationshipPreserved = false;
          break;
        }
      }
    }
    if (!relationshipPreserved) {
      return null;
    }
    let newNumericalVertices = [];
    for (let i2 = 0; i2 < numVertices; i2++) {
      if (verticesChanged.includes(i2)) {
        newNumericalVertices.push(resultingNumericalVertices[i2]);
      } else {
        newNumericalVertices.push(
          desiredNumericalVertices[i2].map(
            (v2, j2) => v2 - changevec1[j2]
          )
        );
      }
    }
    let newVertexComponents = {};
    for (let ind in newNumericalVertices) {
      for (let dim = 0; dim < newNumericalVertices[ind].length; dim++) {
        newVertexComponents[`${ind},${dim}`] = me$3.fromAst(
          newNumericalVertices[ind][dim]
        );
      }
    }
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "unconstrainedVertices",
          value: newVertexComponents
        }
      ],
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async movePolylineCenter({
    center,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false,
    pointRole = "polyline"
  }) {
    if (!transient) {
      skippable = false;
    }
    if (!["polyline", "polygon", "triangle"].includes(pointRole)) {
      console.warn(`Invalid pointRole for polyline: ${pointRole}`);
      return;
    }
    let numDimensions = await this.stateValues.numDimensions;
    if (!Array.isArray(center) || center.length !== numDimensions) {
      return;
    }
    if (!center.every((x2) => Number.isFinite(x2))) {
      console.warn(
        `Invalid center coordinates for ${pointRole} move: ${center.join(", ")}`
      );
      return;
    }
    if (!await this.stateValues.draggable) {
      return;
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "center",
        value: center.map((x2) => me$3.fromAst(x2)),
        sourceDetails
      }
    ];
    const performUpdateArgs = {
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate: true
    };
    if (transient) {
      performUpdateArgs.transient = true;
      performUpdateArgs.skippable = skippable;
    } else {
      performUpdateArgs.event = {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          center
        }
      };
    }
    await this.coreFunctions.performUpdate(performUpdateArgs);
    let reconciliationResult = await this._attemptConstrainedRigidTranslationReconciliation({
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
    if (reconciliationResult !== null) {
      return reconciliationResult;
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async movePolyline({
    pointCoords,
    transient,
    sourceDetails,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let numVerticesMoved = Object.keys(pointCoords).length;
    if (numVerticesMoved === 1) {
      if (!await this.stateValues.verticesDraggable) {
        return;
      }
    } else {
      if (!await this.stateValues.draggable) {
        return;
      }
    }
    let vertexComponents = {};
    for (let ind in pointCoords) {
      vertexComponents[ind + ",0"] = me$3.fromAst(pointCoords[ind][0]);
      vertexComponents[ind + ",1"] = me$3.fromAst(pointCoords[ind][1]);
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "vertices",
            value: vertexComponents,
            sourceDetails
          }
        ],
        transient,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "vertices",
            value: vertexComponents,
            sourceDetails
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            pointCoordinates: pointCoords
          }
        }
      });
    }
    if (numVerticesMoved > 1 || await this.stateValues.preserveSimilarity) {
      let reconciliationResult = await this._attemptConstrainedRigidTranslationReconciliation({
        transient,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
      if (reconciliationResult !== null) {
        return reconciliationResult;
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async finalizePolylinePosition({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.actions.movePolyline({
      pointCoords: await this.stateValues.numericalVertices,
      transient: false,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async reflectPolyline({
    sourceDetails,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.allowReflection) {
      return;
    }
    const vertices = await this.stateValues.vertices;
    const centroid = calculateNumericalCentroid(vertices);
    const numericalVertices = await this.stateValues.numericalVertices;
    const reflectedVertices = numericalVertices.map((vert) => [
      2 * centroid[0] - vert[0],
      // 2 * centroid[1] - vert[1],
      vert[1]
    ]);
    let vertexComponents = {};
    for (const [idx, vert] of reflectedVertices.entries()) {
      vertexComponents[idx + ",0"] = me$3.fromAst(vert[0]);
      vertexComponents[idx + ",1"] = me$3.fromAst(vert[1]);
    }
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "vertices",
          value: vertexComponents,
          sourceDetails,
          overrides: { isReflection: true }
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          pointCoordinates: reflectedVertices
        }
      }
    });
  }
  async polylineClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async polylineFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function calculateNumericalCentroid(vertices) {
  let x2 = 0, y2 = 0;
  let numVertices = vertices.length;
  for (let i2 = 0; i2 < numVertices; i2++) {
    x2 += vertices[i2][0].evaluate_to_constant();
    y2 += vertices[i2][1].evaluate_to_constant();
  }
  x2 /= numVertices;
  y2 /= numVertices;
  return [x2, y2];
}
class CobwebPolyline extends Polyline {
  static componentType = "cobwebPolyline";
  static rendererType = "cobwebPolyline";
  static componentDocs = {
    summary: "An interactive cobweb diagram for visualizing iteration of a one-variable function"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.attractThreshold = {
      createComponentOfType: "number",
      createStateVariable: "attractThreshold",
      defaultValue: 0.5,
      public: true,
      description: "Distance within which a dragged vertex snaps to the function curve or diagonal."
    };
    attributes.numPoints = {
      createComponentOfType: "number",
      createStateVariable: "numPoints",
      defaultValue: 1,
      public: true,
      clamp: [0, Infinity],
      forRenderer: true,
      description: "Number of points (iterations) currently shown on the cobweb diagram."
    };
    attributes.variable = {
      createComponentOfType: "_variableName",
      createStateVariable: "variable",
      defaultValue: me$3.fromAst("x"),
      public: true,
      forRenderer: true,
      description: "Symbol used as the independent variable when displaying iterate values."
    };
    attributes.numIterationsRequired = {
      createComponentOfType: "number",
      createStateVariable: "numIterationsRequired",
      defaultValue: 0,
      public: true,
      description: "Minimum number of iterations the user is required to perform."
    };
    attributes.initialPoint = {
      createComponentOfType: "point",
      description: "The starting point on the diagonal where iteration begins."
    };
    attributes.function = {
      createComponentOfType: "function",
      description: "The function being iterated."
    };
    attributes.lockToSolution = {
      createComponentOfType: "boolean",
      createStateVariable: "lockToSolution",
      defaultValue: false,
      description: "Whether to constrain dragged vertices to the correct cobweb solution."
    };
    attributes.defaultPoint = {
      createComponentOfType: "point",
      description: "The default location for newly added points on the cobweb diagram."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.numDimensions.returnDependencies = () => ({});
    stateVariableDefinitions.numDimensions.definition = () => ({
      setValue: { numDimensions: 2 }
    });
    stateVariableDefinitions.initialPoint = {
      description: "The starting point on the diagonal where iteration begins.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "initialPointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      entryPrefixes: ["initialPointX"],
      defaultValueByArrayKey: () => me$3.fromAst(0),
      hasEssential: true,
      returnArraySizeDependencies: () => ({}),
      returnArraySize: () => [2],
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            initialPointAttr: {
              dependencyType: "attributeComponent",
              attributeName: "initialPoint",
              variableNames: ["x" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let initialPoint = {};
        let essentialInitialPoint = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (dependencyValuesByKey[arrayKey].initialPointAttr?.stateValues["x" + varEnding]) {
            initialPoint[arrayKey] = dependencyValuesByKey[arrayKey].initialPointAttr.stateValues["x" + varEnding];
          } else {
            essentialInitialPoint[arrayKey] = true;
          }
        }
        let result = {};
        if (Object.keys(initialPoint).length > 0) {
          result.setValue = { initialPoint };
        }
        if (Object.keys(essentialInitialPoint).length > 0) {
          result.useEssentialOrDefaultValue = {
            initialPoint: essentialInitialPoint
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.initialPoint) {
          if (dependencyValuesByKey[arrayKey].initialPointAttr && dependencyValuesByKey[arrayKey].initialPointAttr.stateValues["x" + (Number(arrayKey) + 1)]) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].initialPointAttr,
              desiredValue: desiredStateVariableValues.initialPoint[arrayKey],
              variableIndex: 0
            });
          } else {
            instructions.push({
              setEssentialValue: "initialPoint",
              value: {
                [arrayKey]: desiredStateVariableValues.initialPoint[arrayKey]
              }
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.f = {
      additionalStateVariablesDefined: [
        {
          variableName: "fDefinition",
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        functionAttr: {
          dependencyType: "attributeComponent",
          attributeName: "function",
          variableNames: ["numericalf", "fDefinition"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.functionAttr) {
          return {
            setValue: {
              f: dependencyValues.functionAttr.stateValues.numericalf,
              fDefinition: dependencyValues.functionAttr.stateValues.fDefinition
            }
          };
        } else {
          return { setValue: { f: null, fDefinition: null } };
        }
      }
    };
    stateVariableDefinitions.numVertices = {
      isAlias: true,
      targetVariableName: "numPoints",
      description: "Number of cobweb vertices currently shown (alias for numPoints)."
    };
    stateVariableDefinitions.numOriginalVertices = {
      providePreviousValuesInDefinition: true,
      returnDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues, previousValues }) {
        let numOriginalVertices = dependencyValues.numVertices;
        if (previousValues.numOriginalVertices > numOriginalVertices) {
          numOriginalVertices = previousValues.numOriginalVertices;
        }
        return { setValue: { numOriginalVertices } };
      }
    };
    stateVariableDefinitions.originalVertices = {
      isArray: true,
      numDimensions: 2,
      hasEssential: true,
      entryPrefixes: ["originalVertexX", "originalVertex"],
      returnEntryDimensions: (prefix) => prefix === "originalVertex" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "originalVertexX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "originalVertices") {
          if (propIndex.length === 1) {
            return "originalVertex" + propIndex[0];
          } else {
            return `originalVertexX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 14) === "originalVertex") {
          let originalVertexNum = Number(varName.slice(14));
          if (Number.isInteger(originalVertexNum) && originalVertexNum > 0) {
            return `originalVertexX${originalVertexNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numOriginalVertices: {
          dependencyType: "stateVariable",
          variableName: "numOriginalVertices"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numOriginalVertices - 1,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey: () => ({
        globalDependencies: {
          graphAncestor: {
            dependencyType: "ancestor",
            componentType: "graph",
            variableNames: ["xMin", "xMax", "yMin", "yMax"]
          },
          defaultPoint: {
            dependencyType: "attributeComponent",
            attributeName: "defaultPoint",
            variableNames: ["xs"]
          }
        }
      }),
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let originalVertices = {};
        for (let arrayKey of arrayKeys) {
          let arrayIndices = arrayKey.split(",").map(Number);
          originalVertices[arrayKey] = {
            get defaultValue() {
              if (globalDependencyValues.defaultPoint) {
                let xs = globalDependencyValues.defaultPoint.stateValues.xs;
                if (xs.length === 2 && arrayIndices[1] < 2) {
                  return xs[arrayIndices[1]];
                }
              }
              if (globalDependencyValues.graphAncestor) {
                if (arrayIndices[1] === 0) {
                  let xMin = globalDependencyValues.graphAncestor.stateValues.xMin;
                  let xMax = globalDependencyValues.graphAncestor.stateValues.xMax;
                  return me$3.fromAst((xMin + xMax) / 2);
                } else if (arrayIndices[1] === 1) {
                  let yMin = globalDependencyValues.graphAncestor.stateValues.yMin;
                  let yMax = globalDependencyValues.graphAncestor.stateValues.yMax;
                  return me$3.fromAst((yMin + yMax) / 2);
                }
              }
              return me$3.fromAst(0);
            }
          };
        }
        return {
          useEssentialOrDefaultValue: { originalVertices }
        };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.originalVertices) {
          instructions.push({
            setEssentialValue: "originalVertices",
            value: {
              [arrayKey]: desiredStateVariableValues.originalVertices[arrayKey]
            }
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.vertices.additionalStateVariablesDefined = [
      {
        variableName: "prelimCorrectVertices",
        entryPrefixes: ["prelimCorrectVertexX", "prelimCorrectVertex"],
        getArrayKeysFromVarName: function({
          arrayEntryPrefix,
          varEnding,
          arraySize
        }) {
          if (arrayEntryPrefix === "prelimCorrectVertexX") {
            let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
            if (indices.length === 2 && indices.every(
              (x2, i2) => Number.isInteger(x2) && x2 >= 0
            )) {
              if (arraySize) {
                if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                  return [String(indices)];
                } else {
                  return [];
                }
              } else {
                return [String(indices)];
              }
            } else {
              return [];
            }
          } else {
            let pointInd = Number(varEnding) - 1;
            if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
              return [];
            }
            if (!arraySize) {
              return [pointInd + ",0"];
            }
            if (pointInd < arraySize[0]) {
              return Array.from(
                Array(arraySize[1]),
                (_2, i2) => pointInd + "," + i2
              );
            } else {
              return [];
            }
          }
        }
      }
    ];
    stateVariableDefinitions.vertices.basedOnArrayKeyStateVariables = true;
    stateVariableDefinitions.vertices.returnArrayDependenciesByKey = function({ arrayKeys }) {
      let globalDependencies = {
        f: {
          dependencyType: "stateVariable",
          variableName: "f"
        },
        attractThreshold: {
          dependencyType: "stateVariable",
          variableName: "attractThreshold"
        },
        lockToSolution: {
          dependencyType: "stateVariable",
          variableName: "lockToSolution"
        }
      };
      let dependenciesByKey = {};
      for (let arrayKey of arrayKeys) {
        let [pointInd, dim] = arrayKey.split(",");
        if (pointInd === "0") {
          dependenciesByKey[arrayKey] = {
            initialPoint: {
              dependencyType: "stateVariable",
              variableName: "initialPointX" + (Number(dim) + 1)
            }
          };
        } else {
          dependenciesByKey[arrayKey] = {
            originalVertexX1: {
              dependencyType: "stateVariable",
              variableName: "originalVertexX" + pointInd + "_1"
            },
            originalVertexX2: {
              dependencyType: "stateVariable",
              variableName: "originalVertexX" + pointInd + "_2"
            },
            previousVertexX1: {
              dependencyType: "stateVariable",
              variableName: "vertexX" + pointInd + "_1"
            },
            previousVertexX2: {
              dependencyType: "stateVariable",
              variableName: "vertexX" + pointInd + "_2"
            }
          };
        }
      }
      return { globalDependencies, dependenciesByKey };
    };
    stateVariableDefinitions.vertices.arrayDefinitionByKey = function({
      globalDependencyValues,
      dependencyValuesByKey,
      arrayKeys
    }) {
      let vertices = {};
      let prelimCorrectVertices = {};
      for (let arrayKey of arrayKeys) {
        let [pointInd, dim] = arrayKey.split(",");
        if (pointInd === "0") {
          vertices[arrayKey] = dependencyValuesByKey[arrayKey].initialPoint;
          prelimCorrectVertices[arrayKey] = null;
        } else {
          let originalVertex, previousVertex;
          try {
            originalVertex = [
              dependencyValuesByKey[arrayKey].originalVertexX1.evaluate_to_constant(),
              dependencyValuesByKey[arrayKey].originalVertexX2.evaluate_to_constant()
            ];
            previousVertex = [
              dependencyValuesByKey[arrayKey].previousVertexX1.evaluate_to_constant(),
              dependencyValuesByKey[arrayKey].previousVertexX2.evaluate_to_constant()
            ];
          } catch (e3) {
            vertices[pointInd + ",0"] = me$3.fromAst(0);
            vertices[pointInd + ",1"] = me$3.fromAst(0);
            prelimCorrectVertices[pointInd + ",0"] = false;
            continue;
          }
          let attractPoint;
          if (Number(pointInd) % 2 === 1) {
            let newY = globalDependencyValues.f(previousVertex[0]);
            attractPoint = [previousVertex[0], newY];
          } else {
            attractPoint = [previousVertex[1], previousVertex[1]];
          }
          let distance2FromAttractor = Math.pow(originalVertex[0] - attractPoint[0], 2) + Math.pow(originalVertex[1] - attractPoint[1], 2);
          if (distance2FromAttractor < globalDependencyValues.attractThreshold * globalDependencyValues.attractThreshold || globalDependencyValues.lockToSolution) {
            vertices[pointInd + ",0"] = me$3.fromAst(attractPoint[0]);
            vertices[pointInd + ",1"] = me$3.fromAst(attractPoint[1]);
            prelimCorrectVertices[pointInd + ",0"] = true;
          } else {
            vertices[pointInd + ",0"] = dependencyValuesByKey[arrayKey].originalVertexX1;
            vertices[pointInd + ",1"] = dependencyValuesByKey[arrayKey].originalVertexX2;
            prelimCorrectVertices[pointInd + ",0"] = false;
          }
          prelimCorrectVertices[pointInd + ",1"] = null;
        }
      }
      return { setValue: { vertices, prelimCorrectVertices } };
    };
    stateVariableDefinitions.vertices.inverseArrayDefinitionByKey = async function({
      desiredStateVariableValues,
      dependencyNamesByKey,
      initialChange,
      stateValues
    }) {
      if (initialChange && !await stateValues.draggable) {
        return { success: false };
      }
      let instructions = [];
      for (let arrayKey in desiredStateVariableValues.vertices) {
        let [pointInd, dim] = arrayKey.split(",");
        if (pointInd === "0") {
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].initialPoint,
            desiredValue: desiredStateVariableValues.vertices[arrayKey]
          });
        } else {
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey]["originalVertexX" + (Number(dim) + 1)],
            desiredValue: desiredStateVariableValues.vertices[arrayKey]
          });
        }
      }
      return {
        success: true,
        instructions
      };
    };
    stateVariableDefinitions.correctVertices = {
      description: "Per-vertex flags indicating whether each vertex is positioned at the correct cobweb solution.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      entryPrefixes: ["correctVertex"],
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numVertices - 1];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            prelimCorrectVertex: {
              dependencyType: "stateVariable",
              variableName: "prelimCorrectVertexX" + (Number(arrayKey) + 2) + "_1"
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let correctVertices = {};
        for (let arrayKey of arrayKeys) {
          correctVertices[arrayKey] = dependencyValuesByKey[arrayKey].prelimCorrectVertex;
        }
        return { setValue: { correctVertices } };
      }
    };
    stateVariableDefinitions.fractionCorrectVertices = {
      description: "The fraction of vertices placed at correct cobweb positions.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      additionalStateVariablesDefined: [
        {
          variableName: "numGradedVertices",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number"
          },
          description: "The number of cobweb vertices that were evaluated for correctness."
        },
        {
          variableName: "numCorrectVertices",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number"
          },
          description: "The number of cobweb vertices placed at correct positions."
        }
      ],
      returnDependencies: () => ({
        correctVertices: {
          dependencyType: "stateVariable",
          variableName: "correctVertices"
        }
      }),
      definition({ dependencyValues }) {
        let numGradedVertices = dependencyValues.correctVertices.length;
        let fractionCorrectVertices;
        let numCorrectVertices;
        if (numGradedVertices === 0) {
          fractionCorrectVertices = 0;
          numCorrectVertices = 0;
        } else {
          numCorrectVertices = dependencyValues.correctVertices.reduce(
            (a2, c2) => a2 + c2,
            0
          );
          fractionCorrectVertices = numCorrectVertices / numGradedVertices;
        }
        return {
          setValue: {
            fractionCorrectVertices,
            numGradedVertices,
            numCorrectVertices
          }
        };
      }
    };
    stateVariableDefinitions.fractionCorrectVerticesAdjusted = {
      description: "The fraction of correct vertices, scaled by the required minimum number of iterations.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      additionalStateVariablesDefined: [
        {
          variableName: "numGradedVerticesAdjusted",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number"
          },
          description: "The effective number of graded vertices after adjusting for the required minimum number of iterations."
        }
      ],
      returnDependencies: () => ({
        numCorrectVertices: {
          dependencyType: "stateVariable",
          variableName: "numCorrectVertices"
        },
        numGradedVertices: {
          dependencyType: "stateVariable",
          variableName: "numGradedVertices"
        },
        numIterationsRequired: {
          dependencyType: "stateVariable",
          variableName: "numIterationsRequired"
        }
      }),
      definition({ dependencyValues }) {
        let numVerticesRequired = 0;
        if (dependencyValues.numIterationsRequired > 0) {
          numVerticesRequired = 2 * dependencyValues.numIterationsRequired - 1;
        }
        let numGradedVerticesAdjusted = Math.max(
          dependencyValues.numGradedVertices,
          numVerticesRequired
        );
        let fractionCorrectVerticesAdjusted;
        if (numGradedVerticesAdjusted === 0) {
          fractionCorrectVerticesAdjusted = 0;
        } else {
          fractionCorrectVerticesAdjusted = dependencyValues.numCorrectVertices / numGradedVerticesAdjusted;
        }
        return {
          setValue: {
            fractionCorrectVerticesAdjusted,
            numGradedVerticesAdjusted
          }
        };
      }
    };
    stateVariableDefinitions.numIterateValues = {
      description: "The number of iterate values produced by the cobweb diagram.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          numIterateValues: Math.ceil(
            (dependencyValues.numVertices + 1) / 2
          )
        }
      })
    };
    stateVariableDefinitions.iterateValues = {
      description: "The list of iterate values shown by the cobweb diagram.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      entryPrefixes: ["iterateValue"],
      returnArraySizeDependencies: () => ({
        numIterateValues: {
          dependencyType: "stateVariable",
          variableName: "numIterateValues"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numIterateValues];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          if (arrayKey === "0") {
            dependenciesByKey[arrayKey] = {
              iterateValue: {
                dependencyType: "stateVariable",
                variableName: "vertexX1_1"
              }
            };
          } else {
            dependenciesByKey[arrayKey] = {
              iterateValue: {
                dependencyType: "stateVariable",
                variableName: "vertexX" + 2 * Number(arrayKey) + "_2"
              }
            };
          }
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let iterateValues = {};
        for (let arrayKey of arrayKeys) {
          iterateValues[arrayKey] = dependencyValuesByKey[arrayKey].iterateValue;
        }
        return { setValue: { iterateValues } };
      }
    };
    return stateVariableDefinitions;
  }
}
class Point extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      movePoint: this.movePoint.bind(this),
      switchPoint: this.switchPoint.bind(this),
      pointClicked: this.pointClicked.bind(this),
      pointFocused: this.pointFocused.bind(this)
    });
  }
  static componentType = "point";
  static styleOverrideCategories = ["marker"];
  static componentDocs = {
    summary: "A point with coordinates that can be displayed and dragged on a graph"
  };
  static canBeInList = true;
  // Note: for other components with public point state variables,
  // the recommended course of action is not to have
  // a public state variable with component type point, which would use coordsShadow
  // Instead have a public array state variable of maths for each component
  // and use wrapping components to create points from those
  static primaryStateVariableForDefinition = "coordsShadow";
  static stateVariableToBeShadowed = "coords";
  static variableForIndexAsProp = "xs";
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["number", "math", "string"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the point can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.x = {
      createComponentOfType: "math",
      description: "The first coordinate (x) of the point."
    };
    attributes.y = {
      createComponentOfType: "math",
      description: "The second coordinate (y) of the point."
    };
    attributes.z = {
      createComponentOfType: "math",
      description: "The third coordinate (z) of the point."
    };
    attributes.xs = {
      createComponentOfType: "mathList",
      description: "The point's coordinates as a list."
    };
    attributes.coords = {
      createComponentOfType: "coords",
      description: "The point's coordinates as a single math expression."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.labelPosition = {
      description: "Position of the point's label.",
      createComponentOfType: "text",
      createStateVariable: "labelPosition",
      defaultValue: "upperRight",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "upperRight",
          description: "Place the label above and to the right of the point."
        },
        {
          value: "upperLeft",
          description: "Place the label above and to the left of the point."
        },
        {
          value: "lowerRight",
          description: "Place the label below and to the right of the point."
        },
        {
          value: "lowerLeft",
          description: "Place the label below and to the left of the point."
        },
        {
          value: "top",
          description: "Place the label directly above the point."
        },
        {
          value: "bottom",
          description: "Place the label directly below the point."
        },
        {
          value: "left",
          description: "Place the label directly to the left of the point."
        },
        {
          value: "right",
          description: "Place the label directly to the right of the point."
        }
      ]
    };
    attributes.showCoordsWhenDragging = {
      description: "Whether to show coordinate labels while dragging.",
      createComponentOfType: "boolean",
      createStateVariable: "showCoordsWhenDragging",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.addControls = {
      description: "Whether to render interactive control handles.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "both",
      public: true,
      toLowerCase: true,
      valueForTrue: "both",
      valueForFalse: "none",
      // Note: validValues are declared in camelCase for autocomplete/validation purposes,
      // but toLowerCase: true ensures stored values are always lowercase at runtime.
      // The preprocessAttributesObject() utility lowercases all defined validValues values,
      // so both the source declaration (camelCase) and stored value (lowercase) coexist.
      // Code comparisons should use lowercase "xonly", "yonly", etc.
      validValues: [
        {
          value: "none",
          description: "Show no control handles."
        },
        {
          value: "both",
          description: "Show control handles for moving the point in both x and y."
        },
        {
          value: "xOnly",
          description: "Show a control handle that constrains motion to the x direction."
        },
        {
          value: "yOnly",
          description: "Show a control handle that constrains motion to the y direction."
        }
      ]
    };
    attributes.hideOffGraphIndicator = {
      createComponentOfType: "boolean",
      description: "Whether to suppress the indicator drawn at the edge when the point is off-screen."
    };
    attributes.controlOrder = returnGraphControlOrderAttribute();
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakIntoXsOrCoords = function({
      matchedChildren,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    }) {
      let componentIsSpecifiedType = componentInfoObjects.componentIsSpecifiedType;
      let foundConstraintsChildren = false;
      const childrenMovedConstraints = [];
      for (const child of matchedChildren) {
        childrenMovedConstraints.push(child);
        if (componentIsSpecifiedType(child, "constraints")) {
          if (child.children?.length > 0) {
            foundConstraintsChildren = true;
            childrenMovedConstraints.push(...child.children);
            child.children = [];
          }
        }
      }
      let componentChildren = [], nonComponentChildrenBegin = [], nonComponentChildrenEnd = [];
      for (let child of childrenMovedConstraints) {
        if (componentIsSpecifiedType(child, "constraints") || componentIsSpecifiedType(child, "_constraint") || componentIsSpecifiedType(child, "label")) {
          if (componentChildren.length > 0) {
            nonComponentChildrenEnd.push(child);
          } else {
            nonComponentChildrenBegin.push(child);
          }
        } else if (nonComponentChildrenEnd.length > 0) {
          nonComponentChildrenEnd.push(child);
        } else {
          componentChildren.push(child);
        }
      }
      if (componentChildren.length === 0) {
        if (foundConstraintsChildren) {
          return {
            success: true,
            newChildren: childrenMovedConstraints,
            nComponents
          };
        } else {
          return { success: false };
        }
      }
      if (componentChildren.length === 1) {
        let child = componentChildren[0];
        if (componentIsSpecifiedType(child, "point") || componentIsSpecifiedType(child, "vector")) {
          if (foundConstraintsChildren) {
            return {
              success: true,
              newChildren: childrenMovedConstraints,
              nComponents
            };
          } else {
            return { success: false };
          }
        }
      }
      let nCompChildren = componentChildren.length;
      let firstChar, lastChar;
      if (typeof componentChildren[0] === "string") {
        componentChildren[0] = componentChildren[0].trimStart();
        firstChar = componentChildren[0][0];
      }
      if (typeof componentChildren[nCompChildren - 1] === "string") {
        componentChildren[nCompChildren - 1] = componentChildren[nCompChildren - 1].trimEnd();
        let lastChild = componentChildren[nCompChildren - 1];
        lastChar = lastChild[lastChild.length - 1];
      }
      if (firstChar === "(" && lastChar === ")") {
        let modifiedChildren = [...componentChildren];
        modifiedChildren[0] = modifiedChildren[0].substring(1);
        let lastChild = modifiedChildren[modifiedChildren.length - 1];
        modifiedChildren[modifiedChildren.length - 1] = lastChild.substring(0, lastChild.length - 1);
        let breakResult = breakEmbeddedStringByCommas({
          childrenList: modifiedChildren
        });
        if (breakResult.success) {
          const newAttributes = {
            xs: {
              type: "component",
              name: "xs",
              component: {
                type: "serialized",
                componentType: "mathList",
                componentIdx: nComponents++,
                stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
                children: breakResult.pieces.map((x2, i2) => ({
                  type: "serialized",
                  componentType: "math",
                  componentIdx: nComponents++,
                  stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
                  children: x2,
                  attributes: {},
                  doenetAttributes: {},
                  state: {}
                })),
                skipSugar: true,
                attributes: {},
                state: {},
                doenetAttributes: {}
              }
            }
          };
          return {
            success: true,
            newAttributes,
            newChildren: [
              ...nonComponentChildrenBegin,
              ...nonComponentChildrenEnd
            ],
            nComponents
          };
        }
      }
      return {
        success: true,
        newAttributes: {
          coords: {
            type: "component",
            name: "coords",
            component: {
              type: "serialized",
              componentType: "coords",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: componentChildren,
              state: {},
              attributes: {},
              doenetAttributes: {}
            }
          }
        },
        newChildren: [
          ...nonComponentChildrenBegin,
          ...nonComponentChildrenEnd
        ],
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakIntoXsOrCoords
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    let childGroups = super.returnChildGroups();
    childGroups.push(
      ...[
        {
          group: "pointsAndVectors",
          componentTypes: ["point", "vector"]
        },
        {
          group: "constraints",
          componentTypes: ["_constraint"]
        }
      ]
    );
    return childGroups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    Object.assign(
      stateVariableDefinitions,
      returnConstraintDefinitions("unconstrainedXs", "unconstrainedX")
    );
    Object.assign(stateVariableDefinitions, returnStickyGroupDefinitions());
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the point's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let markerColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          markerColorWord = dependencyValues.selectedStyle.markerColorWordDarkMode;
        } else {
          markerColorWord = dependencyValues.selectedStyle.markerColorWord;
        }
        return {
          setValue: {
            styleDescription: markerColorWord
          }
        };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "point".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let pointDescription = dependencyValues.styleDescription + " " + dependencyValues.selectedStyle.markerStyleWord;
        return {
          setValue: { styleDescriptionWithNoun: pointDescription }
        };
      }
    };
    stateVariableDefinitions.inUnorderedList = {
      returnDependencies: () => ({
        sourceCompositeUnordered: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "unordered",
          skipCopies: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.sourceCompositeUnordered !== null && !usedDefault.sourceCompositeUnordered) {
          return {
            setValue: {
              inUnorderedList: Boolean(
                dependencyValues.sourceCompositeUnordered
              )
            }
          };
        } else {
          return {
            setValue: {
              inUnorderedList: false
            }
          };
        }
      }
    };
    stateVariableDefinitions.hideOffGraphIndicator = {
      description: "Whether to suppress the off-graph indicator when out of view.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        hideOffGraphIndicatorAttr: {
          dependencyType: "attributeComponent",
          attributeName: "hideOffGraphIndicator",
          variableNames: ["value"]
        },
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["hideOffGraphIndicators"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.hideOffGraphIndicatorAttr) {
          return {
            setValue: {
              hideOffGraphIndicator: dependencyValues.hideOffGraphIndicatorAttr.stateValues.value
            }
          };
        } else if (dependencyValues.graphAncestor) {
          return {
            setValue: {
              hideOffGraphIndicator: dependencyValues.graphAncestor.stateValues.hideOffGraphIndicators
            }
          };
        } else {
          return {
            setValue: { hideOffGraphIndicator: false }
          };
        }
      }
    };
    stateVariableDefinitions.coordsShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "coords",
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          coordsShadow: true
        }
      }),
      inverseDefinition: async function({
        desiredStateVariableValues,
        stateValues,
        workspace
      }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "coordsShadow",
              value: desiredStateVariableValues.coordsShadow
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions of the point.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        coordsShadow: {
          dependencyType: "stateVariable",
          variableName: "coordsShadow"
        },
        coords: {
          dependencyType: "attributeComponent",
          attributeName: "coords",
          variableNames: ["value"]
        },
        x: {
          dependencyType: "attributeComponent",
          attributeName: "x"
        },
        y: {
          dependencyType: "attributeComponent",
          attributeName: "y"
        },
        z: {
          dependencyType: "attributeComponent",
          attributeName: "z"
        },
        xs: {
          dependencyType: "attributeComponent",
          attributeName: "xs",
          variableNames: ["numComponents"]
        },
        pointOrVectorChild: {
          dependencyType: "child",
          childGroups: ["pointsAndVectors"],
          variableNames: ["numDimensions"]
        }
      }),
      definition: function({ dependencyValues }) {
        let basedOnCoords = false;
        let coords;
        let numDimensions;
        if (dependencyValues.z !== null) {
          numDimensions = 3;
        } else if (dependencyValues.y !== null) {
          numDimensions = 2;
        } else {
          numDimensions = 0;
        }
        if (dependencyValues.coords !== null) {
          basedOnCoords = true;
          coords = dependencyValues.coords.stateValues.value;
        } else if (dependencyValues.coordsShadow) {
          basedOnCoords = true;
          coords = dependencyValues.coordsShadow.tuples_to_vectors();
        }
        if (basedOnCoords) {
          let coordsTree = coords.expand().simplify().tree;
          if (Array.isArray(coordsTree) && vectorOperators.includes(coordsTree[0])) {
            numDimensions = Math.max(
              coordsTree.length - 1,
              numDimensions
            );
          } else {
            numDimensions = Math.max(1, numDimensions);
          }
          return {
            setValue: { numDimensions },
            checkForActualChange: { numDimensions: true }
          };
        } else {
          if (dependencyValues.xs !== null) {
            return {
              setValue: {
                numDimensions: Math.max(
                  dependencyValues.xs.stateValues.numComponents,
                  numDimensions
                )
              }
            };
          }
          if (dependencyValues.pointOrVectorChild.length > 0) {
            return {
              setValue: {
                numDimensions: Math.max(
                  dependencyValues.pointOrVectorChild[0].stateValues.numDimensions,
                  numDimensions
                )
              }
            };
          }
          if (numDimensions === 0) {
            numDimensions = 2;
          }
          return {
            setValue: { numDimensions },
            checkForActualChange: { numDimensions: true }
          };
        }
      }
    };
    stateVariableDefinitions.numDimensionsForConstraints = {
      isAlias: true,
      targetVariableName: "numDimensions",
      description: "Number of dimensions exposed to constraint computations (alias for numDimensions)."
    };
    stateVariableDefinitions.unconstrainedXs = {
      isArray: true,
      isLocation: true,
      entryPrefixes: ["unconstrainedX"],
      defaultValueByArrayKey: () => me$3.fromAst(0),
      hasEssential: true,
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          coordsShadow: {
            dependencyType: "stateVariable",
            variableName: "coordsShadow"
          },
          coords: {
            dependencyType: "attributeComponent",
            attributeName: "coords",
            variableNames: ["value"]
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            xs: {
              dependencyType: "attributeComponent",
              attributeName: "xs",
              variableNames: ["math" + varEnding]
            },
            pointOrVectorChild: {
              dependencyType: "child",
              childGroups: ["pointsAndVectors"],
              variableNames: ["x" + varEnding]
            }
          };
          if (arrayKey === "0") {
            dependenciesByKey[arrayKey].component = {
              dependencyType: "attributeComponent",
              attributeName: "x",
              variableNames: ["value"]
            };
          } else if (arrayKey === "1") {
            dependenciesByKey[arrayKey].component = {
              dependencyType: "attributeComponent",
              attributeName: "y",
              variableNames: ["value"]
            };
          } else if (arrayKey === "2") {
            dependenciesByKey[arrayKey].component = {
              dependencyType: "attributeComponent",
              attributeName: "z",
              variableNames: ["value"]
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let newXs = {};
        let essentialXs = {};
        let basedOnCoords = false;
        let coords;
        if (globalDependencyValues.coords !== null) {
          basedOnCoords = true;
          coords = globalDependencyValues.coords.stateValues.value;
        } else if (globalDependencyValues.coordsShadow) {
          basedOnCoords = true;
          coords = globalDependencyValues.coordsShadow.tuples_to_vectors();
        }
        if (basedOnCoords) {
          coords = coords.expand().simplify();
          let coordsTree = coords.tree;
          if (Array.isArray(coordsTree) && vectorOperators.includes(coordsTree[0])) {
            for (let arrayKey of arrayKeys) {
              let ind = Number(arrayKey);
              if (ind >= 0 || ind < coordsTree.length - 1) {
                if (coordsTree[ind + 1] === void 0) {
                  newXs[arrayKey] = me$3.fromAst("＿");
                } else {
                  newXs[arrayKey] = coords.get_component(ind);
                }
              }
            }
          } else {
            if (arrayKeys.includes("0")) {
              newXs[0] = coords;
            }
          }
        } else {
          for (let arrayKey of arrayKeys) {
            let varEnding = Number(arrayKey) + 1;
            let xs = dependencyValuesByKey[arrayKey].xs;
            if (xs !== null) {
              let val = xs.stateValues["math" + varEnding];
              if (val !== void 0) {
                newXs[arrayKey] = val.simplify();
              }
            } else {
              let pointOrVectorChild = dependencyValuesByKey[arrayKey].pointOrVectorChild;
              if (pointOrVectorChild.length > 0) {
                newXs[arrayKey] = pointOrVectorChild[0].stateValues["x" + varEnding];
              }
            }
          }
        }
        for (let arrayKey of arrayKeys) {
          let component = dependencyValuesByKey[arrayKey].component;
          if (component) {
            newXs[arrayKey] = component.stateValues.value.simplify();
          } else if (newXs[arrayKey] === void 0) {
            essentialXs[arrayKey] = true;
          }
        }
        let result = {};
        if (Object.keys(newXs).length > 0) {
          result.setValue = { unconstrainedXs: newXs };
        }
        if (Object.keys(essentialXs).length > 0) {
          result.useEssentialOrDefaultValue = {
            unconstrainedXs: essentialXs
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        let basedOnCoords = false;
        let coordsDependency;
        let coordsTree;
        let setCoords = false;
        if (globalDependencyValues.coords !== null) {
          basedOnCoords = true;
          coordsDependency = "coords";
          coordsTree = Array(arraySize[0] + 1);
        } else if (globalDependencyValues.coordsShadow !== null) {
          basedOnCoords = true;
          coordsDependency = "coordsShadow";
          coordsTree = Array(arraySize[0] + 1);
        }
        for (let arrayKey of Object.keys(
          desiredStateVariableValues.unconstrainedXs
        ).reverse()) {
          let desiredValue = convertValueToMathExpression(
            desiredStateVariableValues.unconstrainedXs[arrayKey]
          );
          let component = dependencyValuesByKey[arrayKey].component;
          if (component !== null) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].component,
              desiredValue,
              childIndex: 0,
              variableIndex: 0
            });
          } else if (basedOnCoords) {
            coordsTree[Number(arrayKey) + 1] = desiredValue.tree;
            setCoords = true;
          } else {
            let xs = dependencyValuesByKey[arrayKey].xs;
            if (xs !== null) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].xs,
                desiredValue,
                childIndex: 0,
                variableIndex: 0
              });
            } else {
              let pointOrVectorChild = dependencyValuesByKey[arrayKey].pointOrVectorChild;
              if (pointOrVectorChild.length > 0) {
                instructions.push({
                  setDependency: dependencyNamesByKey[arrayKey].pointOrVectorChild,
                  desiredValue,
                  childIndex: 0,
                  variableIndex: 0
                });
              } else {
                instructions.push({
                  setEssentialValue: "unconstrainedXs",
                  value: { [arrayKey]: desiredValue }
                });
              }
            }
          }
        }
        if (setCoords) {
          let desiredCoords;
          if (arraySize[0] === 1) {
            desiredCoords = me$3.fromAst(coordsTree[1]);
          } else {
            coordsTree[0] = "vector";
            desiredCoords = me$3.fromAst(coordsTree);
          }
          let instruction = {
            setDependency: coordsDependency,
            desiredValue: desiredCoords
          };
          if (coordsDependency === "coords") {
            instruction.childIndex = 0;
            instruction.variableIndex = 0;
          }
          instructions.push(instruction);
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.xs = {
      description: "The point's coordinates as a list.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      isArray: true,
      entryPrefixes: ["x"],
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          let keyDeps = {};
          keyDeps.unconstrainedX = {
            dependencyType: "stateVariable",
            variableName: `unconstrainedX${varEnding}`
          };
          keyDeps.constraintResult = {
            dependencyType: "stateVariable",
            variableName: `constraintResult${varEnding}`
          };
          dependenciesByKey[arrayKey] = keyDeps;
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let xs = {};
        for (let arrayKey of arrayKeys) {
          xs[arrayKey] = convertValueToMathExpression(
            dependencyValuesByKey[arrayKey].constraintResult
          );
        }
        if (arrayKeys.length > 0) {
          return { setValue: { xs } };
        } else {
          return {};
        }
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues,
        workspace
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        let instructions = [];
        let desiredXs;
        if (await stateValues.inStickyGroup) {
          Object.assign(workspace, desiredStateVariableValues.xs);
          desiredXs = [...await stateValues.xs];
          for (let arrayKey in workspace) {
            desiredXs[arrayKey] = workspace[arrayKey];
          }
          let stickyObjectIndex = await stateValues.stickyObjectIndex;
          let stickyPointConstraintFunction = await stateValues.stickyPointConstraintFunction;
          desiredXs = stickyPointConstraintFunction(
            desiredXs,
            stickyObjectIndex
          );
        } else {
          desiredXs = desiredStateVariableValues.xs;
        }
        for (let arrayKey of Object.keys(
          desiredStateVariableValues.xs
        ).reverse()) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].constraintResult,
            desiredValue: desiredXs[arrayKey],
            childIndex: 0,
            variableIndex: 0
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.x = {
      isAlias: true,
      targetVariableName: "x1",
      description: "The first coordinate (x) of the point."
    };
    stateVariableDefinitions.y = {
      isAlias: true,
      targetVariableName: "x2",
      description: "The second coordinate (y) of the point."
    };
    stateVariableDefinitions.z = {
      isAlias: true,
      targetVariableName: "x3",
      description: "The third coordinate (z) of the point."
    };
    stateVariableDefinitions.coords = {
      description: "The point's coordinates as a math expression.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "coords",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        xs: {
          dependencyType: "stateVariable",
          variableName: "xs"
        }
      }),
      definition: function({ dependencyValues }) {
        let coordsAst = [];
        for (let v2 of dependencyValues.xs) {
          if (v2) {
            coordsAst.push(v2.tree);
          } else {
            coordsAst.push("＿");
          }
        }
        if (coordsAst.length > 1) {
          coordsAst = ["vector", ...coordsAst];
        } else if (coordsAst.length === 1) {
          coordsAst = coordsAst[0];
        } else {
          coordsAst = "＿";
        }
        return { setValue: { coords: me$3.fromAst(coordsAst) } };
      },
      inverseDefinition: async function({
        desiredStateVariableValues,
        stateValues,
        initialChange
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        let instructions = [];
        let desiredXValues = {};
        let coordsTree = desiredStateVariableValues.coords.tree;
        if (!(Array.isArray(coordsTree) && vectorOperators.includes(coordsTree[0]))) {
          desiredXValues[0] = desiredStateVariableValues.coords;
        } else {
          for (let i2 = 0; i2 < coordsTree.length - 1; i2++) {
            let desiredValue = desiredStateVariableValues.coords.get_component(i2);
            if (desiredValue.tree !== void 0) {
              desiredXValues[i2] = desiredValue;
            }
          }
        }
        instructions.push({
          setDependency: "xs",
          desiredValue: desiredXValues
        });
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The point rendered as a LaTeX string.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        coords: {
          dependencyType: "stateVariable",
          variableName: "coords"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let latex = roundForDisplay({
          value: dependencyValues.coords,
          dependencyValues
        }).toLatex(params);
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.value = {
      isAlias: true,
      targetVariableName: "coords",
      description: "The point's coordinates as a single math expression."
    };
    stateVariableDefinitions.numericalXs = {
      isArray: true,
      entryPrefixes: ["numericalX"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            [`x`]: {
              dependencyType: "stateVariable",
              variableName: `x${varEnding}`
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalXs = {};
        for (let arrayKey of arrayKeys) {
          let x2 = dependencyValuesByKey[arrayKey].x;
          if (x2) {
            x2 = dependencyValuesByKey[arrayKey].x.evaluate_to_constant();
            numericalXs[arrayKey] = Number(x2);
          } else {
            numericalXs[arrayKey] = NaN;
          }
        }
        return { setValue: { numericalXs } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        initialChange,
        stateValues
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.numericalXs) {
          if (!dependencyNamesByKey[arrayKey]) {
            continue;
          }
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].xs,
            desiredValue: desiredStateVariableValues.numericalXs[arrayKey]
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalXs: {
          dependencyType: "stateVariable",
          variableName: "numericalXs"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          nearestPoint: function() {
            let result = {};
            for (let ind = 1; ind <= dependencyValues.numDimensions; ind++) {
              let x2 = dependencyValues.numericalXs[ind - 1];
              if (!Number.isFinite(x2)) {
                return {};
              }
              result["x" + ind] = x2;
            }
            return result;
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "coords",
      stateVariablesToShadow: [
        ...Object.keys(returnNumberDisplayStateVariableDefinitions()),
        "inUnorderedList"
      ]
    }
  ];
  async movePoint({
    x: x2,
    y: y2,
    z: z2,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false,
    pointRole = "point"
  }) {
    if (pointRole !== "point") {
      console.warn(`Invalid pointRole: ${pointRole}`);
      return;
    }
    let components = {};
    if (x2 !== void 0) {
      components[0] = me$3.fromAst(x2);
    }
    if (y2 !== void 0) {
      components[1] = me$3.fromAst(y2);
    }
    if (z2 !== void 0) {
      components[2] = me$3.fromAst(z2);
    }
    if (transient) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "xs",
            value: components,
            sourceDetails
          }
        ],
        transient,
        skippable,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "xs",
            value: components,
            sourceDetails
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            x: x2,
            y: y2,
            z: z2
          }
        }
      });
    }
  }
  switchPoint() {
  }
  async pointClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async pointFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class EquilibriumPoint extends Point {
  static componentType = "equilibriumPoint";
  static rendererType = "point";
  static componentDocs = {
    summary: "An equilibrium point of a dynamical system, rendered as a filled point if stable or open if unstable"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.markerStyle = {
      ...attributes.markerStyle,
      validValues: markerStyleValuesWithFillVariants
    };
    delete attributes.markerFilled;
    attributes.stable = {
      createComponentOfType: "boolean",
      createStateVariable: "stable",
      defaultValue: true,
      public: true,
      description: "Whether the equilibrium is stable (rendered filled) or unstable (rendered open)."
    };
    attributes.switchable = {
      createComponentOfType: "boolean",
      createStateVariable: "switchable",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether the user can toggle the stability of this equilibrium by clicking it."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.open = {
      forRenderer: true,
      returnDependencies: () => ({
        stable: {
          dependencyType: "stateVariable",
          variableName: "stable"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { open: !dependencyValues.stable }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "stable",
              desiredValue: !desiredStateVariableValues.open
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  async switchPoint({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (await this.stateValues.switchable) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "stable",
            value: !this.stateValues.stable
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            stable: !this.stateValues.stable
          }
        }
      });
    }
  }
}
const LINE_FAMILY_LABEL_POSITION_VALID_VALUES = [
  {
    value: "upperRight",
    description: "Place the label above and to the right of the line."
  },
  {
    value: "upperLeft",
    description: "Place the label above and to the left of the line."
  },
  {
    value: "lowerRight",
    description: "Place the label below and to the right of the line."
  },
  {
    value: "lowerLeft",
    description: "Place the label below and to the left of the line."
  },
  {
    value: "center",
    description: "Place the label at the midpoint of the line."
  },
  {
    value: "top",
    description: "Place the label directly above the line."
  },
  {
    value: "bottom",
    description: "Place the label directly below the line."
  },
  {
    value: "left",
    description: "Place the label directly to the left of the line."
  },
  {
    value: "right",
    description: "Place the label directly to the right of the line."
  }
];
function returnLineFamilyLabelPositionAttribute() {
  return {
    createComponentOfType: "text",
    createStateVariable: "labelPosition",
    defaultValue: "center",
    public: true,
    forRenderer: true,
    toLowerCase: true,
    validValues: LINE_FAMILY_LABEL_POSITION_VALID_VALUES,
    description: "Where the label sits along this line/curve."
  };
}
class Line extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveLine: this.moveLine.bind(this),
      switchLine: this.switchLine.bind(this),
      lineClicked: this.lineClicked.bind(this),
      lineFocused: this.lineFocused.bind(this)
    });
  }
  static componentType = "line";
  static styleOverrideCategories = ["line"];
  static componentDocs = {
    summary: "A line through two points or defined by an equation"
  };
  static canBeInList = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the line can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.equation = {
      createComponentOfType: "math",
      description: "Implicit equation defining the line."
    };
    attributes.through = {
      createComponentOfType: "pointList",
      description: "Points the line passes through."
    };
    attributes.slope = {
      createComponentOfType: "number",
      description: "Slope of the line."
    };
    attributes.perpendicularTo = {
      createComponentOfType: "_directionComponent",
      description: "Direction the line should be perpendicular to (e.g. another line or vector)."
    };
    attributes.parallelTo = {
      createComponentOfType: "_directionComponent",
      description: "Direction the line should be parallel to (e.g. another line or vector)."
    };
    attributes.variables = {
      createComponentOfType: "_variableNameList",
      description: "Names of the variables used in the line equation."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.labelPosition = returnLineFamilyLabelPositionAttribute();
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "number", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        wrappingComponentType: "math",
        createAttributeOfType: "equation"
      })
    });
    return sugarInstructions;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the line's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let styleDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (styleDescription) {
            styleDescription += " ";
          }
          styleDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (styleDescription) {
          styleDescription += " ";
        }
        styleDescription += lineColorWord;
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "line".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " line";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions the line lives in.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      stateVariablesDeterminingDependencies: ["equationIdentity"],
      returnDependencies: function({ stateValues }) {
        if (stateValues.equationIdentity === null) {
          return {
            throughAttr: {
              dependencyType: "attributeComponent",
              attributeName: "through",
              variableNames: ["numDimensions"]
            },
            parallelToAttr: {
              dependencyType: "attributeComponent",
              attributeName: "parallelTo",
              variableNames: ["numDimensions"]
            },
            perpendicularToAttr: {
              dependencyType: "attributeComponent",
              attributeName: "perpendicularTo",
              variableNames: ["numDimensions"]
            }
          };
        } else {
          return {
            equation: {
              dependencyType: "attributeComponent",
              attributeName: "equation"
            }
          };
        }
      },
      definition: function({ dependencyValues, changes }) {
        if (dependencyValues.equation) {
          if (changes.equation && changes.equation.componentIdentitiesChanged) {
            return {
              setValue: { numDimensions: 2 },
              checkForActualChange: { numDimensions: true }
            };
          } else {
            return { noChanges: ["numDimensions"] };
          }
        } else {
          if (dependencyValues.throughAttr) {
            let numDimensions = dependencyValues.throughAttr.stateValues.numDimensions;
            return {
              setValue: { numDimensions },
              checkForActualChange: { numDimensions: true }
            };
          } else if (dependencyValues.parallelToAttr) {
            let numDimensions = dependencyValues.parallelToAttr.stateValues.numDimensions;
            return {
              setValue: { numDimensions },
              checkForActualChange: { numDimensions: true }
            };
          } else if (dependencyValues.perpendicularToAttr) {
            let numDimensions = dependencyValues.perpendicularToAttr.stateValues.numDimensions;
            return {
              setValue: { numDimensions },
              checkForActualChange: { numDimensions: true }
            };
          } else {
            return { setValue: { numDimensions: 2 } };
          }
        }
      }
    };
    stateVariableDefinitions.numPointsPrescribed = {
      returnDependencies: () => ({
        throughAttr: {
          dependencyType: "attributeComponent",
          attributeName: "through",
          variableNames: ["numPoints"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.throughAttr === null) {
          return { setValue: { numPointsPrescribed: 0 } };
        } else {
          return {
            setValue: {
              numPointsPrescribed: dependencyValues.throughAttr.stateValues.numPoints
            }
          };
        }
      }
    };
    stateVariableDefinitions.basedOnSlope = {
      returnDependencies: () => ({
        slopeAttr: {
          dependencyType: "attributeComponent",
          attributeName: "slope"
        },
        numPointsPrescribed: {
          dependencyType: "stateVariable",
          variableName: "numPointsPrescribed"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            basedOnSlope: dependencyValues.numPointsPrescribed < 2 && dependencyValues.slopeAttr !== null && dependencyValues.numDimensions === 2
          }
        };
      }
    };
    stateVariableDefinitions.distForSecondPt = {
      defaultValue: 1,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          distForSecondPt: true
        }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "distForSecondPt",
              value: desiredStateVariableValues.distForSecondPt
            }
          ]
        };
      }
    };
    stateVariableDefinitions.basedOnParallel = {
      returnDependencies: () => ({
        parallelToAttr: {
          dependencyType: "attributeComponent",
          attributeName: "parallelTo",
          variableNames: ["numDimensions"]
        },
        numPointsPrescribed: {
          dependencyType: "stateVariable",
          variableName: "numPointsPrescribed"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        basedOnSlope: {
          dependencyType: "stateVariable",
          variableName: "basedOnSlope"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            basedOnParallel: !dependencyValues.basedOnSlope && dependencyValues.numPointsPrescribed < 2 && dependencyValues.parallelToAttr?.stateValues.numDimensions === dependencyValues.numDimensions
          }
        };
      }
    };
    stateVariableDefinitions.basedOnPerpendicular = {
      returnDependencies: () => ({
        perpendicularToAttr: {
          dependencyType: "attributeComponent",
          attributeName: "perpendicularTo",
          variableNames: ["numDimensions"]
        },
        numPointsPrescribed: {
          dependencyType: "stateVariable",
          variableName: "numPointsPrescribed"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        basedOnSlope: {
          dependencyType: "stateVariable",
          variableName: "basedOnSlope"
        },
        basedOnParallel: {
          dependencyType: "stateVariable",
          variableName: "basedOnParallel"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            basedOnPerpendicular: !dependencyValues.basedOnSlope && !dependencyValues.basedOnParallel && dependencyValues.numPointsPrescribed < 2 && dependencyValues.perpendicularToAttr?.stateValues.numDimensions === 2 && dependencyValues.numDimensions === 2
          }
        };
      }
    };
    stateVariableDefinitions.variables = {
      description: "Variable names used in the line's equation.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      entryPrefixes: ["var"],
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          variables: {
            dependencyType: "attributeComponent",
            attributeName: "variables",
            variableNames: ["variables"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arraySize }) {
        let variablesSpecified = [];
        if (globalDependencyValues.variables !== null) {
          variablesSpecified = globalDependencyValues.variables.stateValues.variables;
        }
        return {
          setValue: {
            variables: returnNVariables(
              arraySize[0],
              variablesSpecified
            )
          }
        };
      }
    };
    stateVariableDefinitions.equationIdentity = {
      returnDependencies: () => ({
        equation: {
          dependencyType: "attributeComponent",
          attributeName: "equation"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.equation !== null) {
          return {
            setValue: {
              equationIdentity: dependencyValues.equation
            }
          };
        } else {
          return { setValue: { equationIdentity: null } };
        }
      }
    };
    stateVariableDefinitions.essentialPoints = {
      isArray: true,
      numDimensions: 2,
      isLocation: true,
      hasEssential: true,
      entryPrefixes: ["essentialPointX", "essentialPoint"],
      set: convertValueToMathExpression,
      defaultValueByArrayKey: (arrayKey) => me$3.fromAst(arrayKey === "0,0" ? 1 : 0),
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "essentialPointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [2, dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey: () => ({}),
      arrayDefinitionByKey({ arrayKeys }) {
        let essentialPoints = {};
        for (let arrayKey of arrayKeys) {
          essentialPoints[arrayKey] = true;
        }
        return { useEssentialOrDefaultValue: { essentialPoints } };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.essentialPoints) {
          instructions.push({
            setEssentialValue: "essentialPoints",
            value: {
              [arrayKey]: convertValueToMathExpression(
                desiredStateVariableValues.essentialPoints[arrayKey]
              )
            }
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.points = {
      public: true,
      description: "Two points the line passes through.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "pointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      indexAliases: [[], ["x", "y", "z"]],
      entryPrefixes: ["pointX", "point"],
      returnEntryDimensions: (prefix) => prefix === "point" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "pointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "points") {
          if (propIndex.length === 1) {
            return "point" + propIndex[0];
          } else {
            return `pointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 5) === "point") {
          let pointNum = Number(varName.slice(5));
          if (Number.isInteger(pointNum) && pointNum > 0) {
            return `pointX${pointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      stateVariablesDeterminingDependencies: [
        "equationIdentity",
        "numPointsPrescribed",
        "basedOnSlope",
        "basedOnParallel",
        "basedOnPerpendicular"
      ],
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [2, dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey({ stateValues, arrayKeys }) {
        if (stateValues.equationIdentity === null) {
          let dependenciesByKey = {};
          for (let arrayKey of arrayKeys) {
            let [pointInd, dim] = arrayKey.split(",");
            let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
            dependenciesByKey[arrayKey] = {
              through: {
                dependencyType: "attributeComponent",
                attributeName: "through",
                variableNames: ["pointX" + varEnding]
              }
            };
            if (stateValues.basedOnSlope || stateValues.basedOnParallel || stateValues.basedOnPerpendicular) {
              if (pointInd === "1") {
                if (stateValues.numPointsPrescribed === 1) {
                  dependenciesByKey[arrayKey].through.variableNames.push(
                    "pointX1_" + (Number(dim) + 1)
                  );
                }
                dependenciesByKey[arrayKey].distForSecondPt = {
                  dependencyType: "stateVariable",
                  variableName: "distForSecondPt"
                };
                dependenciesByKey[arrayKey].slopeAttr = {
                  dependencyType: "attributeComponent",
                  attributeName: "slope",
                  variableNames: ["value"]
                };
                dependenciesByKey[arrayKey].parallelToAttr = {
                  dependencyType: "attributeComponent",
                  attributeName: "parallelTo",
                  variableNames: ["direction"]
                };
                dependenciesByKey[arrayKey].perpendicularToAttr = {
                  dependencyType: "attributeComponent",
                  attributeName: "perpendicularTo",
                  variableNames: ["direction"]
                };
              }
              if (stateValues.numPointsPrescribed === 0) {
                dependenciesByKey[arrayKey].essentialPoint = {
                  dependencyType: "stateVariable",
                  variableName: "essentialPointX2_" + (Number(dim) + 1)
                };
              }
            } else {
              dependenciesByKey[arrayKey].essentialPoint = {
                dependencyType: "stateVariable",
                variableName: "essentialPointX" + varEnding
              };
            }
          }
          let globalDependencies = {
            numPointsPrescribed: {
              dependencyType: "stateVariable",
              variableName: "numPointsPrescribed"
            },
            numDimensions: {
              dependencyType: "stateVariable",
              variableName: "numDimensions"
            },
            basedOnSlope: {
              dependencyType: "stateVariable",
              variableName: "basedOnSlope"
            },
            basedOnParallel: {
              dependencyType: "stateVariable",
              variableName: "basedOnParallel"
            },
            basedOnPerpendicular: {
              dependencyType: "stateVariable",
              variableName: "basedOnPerpendicular"
            }
          };
          return { dependenciesByKey, globalDependencies };
        } else {
          let globalDependencies = {
            coeff0: {
              dependencyType: "stateVariable",
              variableName: "coeff0"
            },
            coeffvar1: {
              dependencyType: "stateVariable",
              variableName: "coeffvar1"
            },
            coeffvar2: {
              dependencyType: "stateVariable",
              variableName: "coeffvar2"
            },
            variables: {
              dependencyType: "stateVariable",
              variableName: "variables"
            },
            lastPointsFromInverting: {
              dependencyType: "stateVariable",
              variableName: "lastPointsFromInverting"
            }
          };
          return { globalDependencies };
        }
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        arraySize
      }) {
        if ("coeff0" in globalDependencyValues) {
          let result = calculatePointsFromCoeffs(
            globalDependencyValues
          );
          if (!result.success) {
            let points = {};
            for (let ind1 = 0; ind1 < arraySize[0]; ind1++) {
              for (let ind2 = 0; ind2 < arraySize[1]; ind2++) {
                points[ind1 + "," + ind2] = me$3.fromAst("＿");
              }
            }
            return {
              setValue: { points },
              sendDiagnostics: result.sendDiagnostics
            };
          } else {
            return { setValue: { points: result.points } };
          }
        } else {
          let points = {};
          for (let arrayKey of arrayKeys) {
            let [pointInd, dim] = arrayKey.split(",");
            let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
            if (dependencyValuesByKey[arrayKey].through !== null && dependencyValuesByKey[arrayKey].through.stateValues["pointX" + varEnding]) {
              points[arrayKey] = dependencyValuesByKey[arrayKey].through.stateValues["pointX" + varEnding];
            } else {
              if (globalDependencyValues.basedOnSlope || globalDependencyValues.basedOnParallel || globalDependencyValues.basedOnPerpendicular) {
                let point1;
                if (globalDependencyValues.numPointsPrescribed === 1) {
                  point1 = dependencyValuesByKey[arrayKey].through.stateValues["pointX1_" + (Number(dim) + 1)];
                } else {
                  point1 = dependencyValuesByKey[arrayKey].essentialPoint;
                }
                if (pointInd === "0") {
                  points[arrayKey] = point1;
                } else {
                  if (globalDependencyValues.basedOnSlope) {
                    let slope = dependencyValuesByKey[arrayKey].slopeAttr.stateValues.value;
                    if (dependencyValuesByKey[arrayKey].distForSecondPt === 0) {
                      points[arrayKey] = point1;
                    } else if (slope === Infinity || slope === -Infinity) {
                      if (dim === "0") {
                        points[arrayKey] = point1;
                      } else {
                        points[arrayKey] = me$3.fromAst([
                          "+",
                          point1.tree,
                          dependencyValuesByKey[arrayKey].distForSecondPt * Math.sign(slope)
                        ]).simplify();
                      }
                    } else if (Number.isFinite(slope)) {
                      let theta = Math.atan(slope);
                      if (dim === "0") {
                        points[arrayKey] = me$3.fromAst([
                          "+",
                          point1.tree,
                          dependencyValuesByKey[arrayKey].distForSecondPt * Math.cos(theta)
                        ]).simplify();
                      } else {
                        points[arrayKey] = me$3.fromAst([
                          "+",
                          point1.tree,
                          dependencyValuesByKey[arrayKey].distForSecondPt * Math.sin(theta)
                        ]).simplify();
                      }
                    } else {
                      points[arrayKey] = me$3.fromAst("＿");
                    }
                  } else if (globalDependencyValues.basedOnParallel) {
                    let parallelTo = dependencyValuesByKey[arrayKey].parallelToAttr.stateValues.direction.map(
                      (v2) => v2.evaluate_to_constant()
                    );
                    if (dependencyValuesByKey[arrayKey].distForSecondPt === 0) {
                      points[arrayKey] = point1;
                    } else if (!parallelTo.every(Number.isFinite)) {
                      points[arrayKey] = me$3.fromAst("＿");
                    } else {
                      points[arrayKey] = me$3.fromAst([
                        "+",
                        point1.tree,
                        dependencyValuesByKey[arrayKey].distForSecondPt * parallelTo[dim]
                      ]).simplify();
                    }
                  } else {
                    let perpendicularTo = dependencyValuesByKey[arrayKey].perpendicularToAttr.stateValues.direction.map(
                      (v2) => v2.evaluate_to_constant()
                    );
                    if (dependencyValuesByKey[arrayKey].distForSecondPt === 0) {
                      points[arrayKey] = point1;
                    } else if (!perpendicularTo.every(
                      Number.isFinite
                    )) {
                      points[arrayKey] = me$3.fromAst("＿");
                    } else {
                      let parallelTo = [
                        perpendicularTo[1],
                        -perpendicularTo[0]
                      ];
                      points[arrayKey] = me$3.fromAst([
                        "+",
                        point1.tree,
                        dependencyValuesByKey[arrayKey].distForSecondPt * parallelTo[dim]
                      ]).simplify();
                    }
                  }
                }
              } else {
                points[arrayKey] = dependencyValuesByKey[arrayKey].essentialPoint;
              }
            }
          }
          return { setValue: { points } };
        }
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues,
        workspace,
        arraySize
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        if ("coeff0" in globalDependencyValues) {
          if (!workspace.desiredPoints) {
            workspace.desiredPoints = {};
          }
          Object.assign(
            workspace.desiredPoints,
            desiredStateVariableValues.points
          );
          let points = await stateValues.points;
          let point1x, point1y, point2x, point2y;
          if (workspace.desiredPoints["0,0"]) {
            point1x = workspace.desiredPoints["0,0"];
          } else {
            point1x = points[0][0];
          }
          if (workspace.desiredPoints["0,1"]) {
            point1y = workspace.desiredPoints["0,1"];
          } else {
            point1y = points[0][1];
          }
          if (workspace.desiredPoints["1,0"]) {
            point2x = workspace.desiredPoints["1,0"];
          } else {
            point2x = points[1][0];
          }
          if (workspace.desiredPoints["1,1"]) {
            point2y = workspace.desiredPoints["1,1"];
          } else {
            point2y = points[1][1];
          }
          if (typeof point1x.tree === "number" && typeof point1y.tree === "number" && typeof point2x.tree === "number" && typeof point2y.tree === "number") {
            let numericalPoint1 = [point1x.tree, point1y.tree];
            let numericalPoint2 = [point2x.tree, point2y.tree];
            let coeffvar12 = numericalPoint1[1] - numericalPoint2[1];
            let coeffvar22 = numericalPoint2[0] - numericalPoint1[0];
            let coeff02 = numericalPoint1[0] * numericalPoint2[1] - numericalPoint1[1] * numericalPoint2[0];
            let sVCoeffVar1 = await stateValues.coeffvar1;
            let sVCoeffVar2 = await stateValues.coeffvar2;
            let prodDiff = Math.abs(
              coeffvar12 * sVCoeffVar2 - sVCoeffVar1 * coeffvar22
            );
            let instructions = [];
            if (prodDiff < Math.abs(coeffvar12 * sVCoeffVar2) * 1e-12) {
              if (coeffvar12 !== 0) {
                coeff02 *= sVCoeffVar1 / coeffvar12;
              } else {
                coeff02 *= sVCoeffVar2 / coeffvar22;
              }
              instructions.push({
                setDependency: "coeff0",
                desiredValue: coeff02,
                additionalDependencyValues: {
                  coeffvar1: sVCoeffVar1,
                  coeffvar2: sVCoeffVar2
                }
              });
            } else {
              instructions.push({
                setDependency: "coeff0",
                desiredValue: coeff02,
                additionalDependencyValues: {
                  coeffvar1: coeffvar12,
                  coeffvar2: coeffvar22
                }
              });
            }
            instructions.push({
              setDependency: "lastPointsFromInverting",
              desiredValue: [numericalPoint1, numericalPoint2]
            });
            return {
              success: true,
              instructions
            };
          }
          let coeffvar1 = point1y.subtract(point2y).simplify();
          let coeffvar2 = point2x.subtract(point1x).simplify();
          let coeff0 = point1x.multiply(point2y).subtract(point1y.multiply(point2x)).simplify();
          return {
            success: true,
            instructions: [
              {
                setDependency: "coeff0",
                desiredValue: coeff0,
                additionalDependencyValues: {
                  coeffvar1,
                  coeffvar2
                }
              }
            ]
          };
        } else {
          let instructions = [];
          if (globalDependencyValues.basedOnSlope || globalDependencyValues.basedOnParallel || globalDependencyValues.basedOnPerpendicular) {
            if (!workspace.desiredPoint1) {
              workspace.desiredPoint1 = [];
            }
            for (let dim = 0; dim < arraySize[1]; dim++) {
              let desiredP1val = desiredStateVariableValues.points["1," + dim];
              if (desiredP1val) {
                if (desiredP1val instanceof me$3.class) {
                  desiredP1val = desiredP1val.evaluate_to_constant();
                }
                workspace.desiredPoint1[dim] = desiredP1val;
              } else if (workspace.desiredPoint1[dim] === void 0) {
                workspace.desiredPoint1[dim] = (await stateValues.points)[1][dim].evaluate_to_constant();
              }
            }
          }
          for (let arrayKey of Object.keys(
            desiredStateVariableValues.points
          ).reverse()) {
            let [pointInd, dim] = arrayKey.split(",");
            let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
            if (dependencyValuesByKey[arrayKey].through !== null && dependencyValuesByKey[arrayKey].through.stateValues["pointX" + varEnding]) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].through,
                desiredValue: desiredStateVariableValues.points[arrayKey],
                variableIndex: 0
              });
            } else if (globalDependencyValues.basedOnSlope || globalDependencyValues.basedOnParallel || globalDependencyValues.basedOnPerpendicular) {
              if (pointInd === "0") {
                instructions.push({
                  setDependency: dependencyNamesByKey[arrayKey].essentialPoint,
                  desiredValue: desiredStateVariableValues.points[arrayKey],
                  variableIndex: 0
                });
              } else {
                if (workspace.desiredPoint1.every(
                  Number.isFinite
                )) {
                  let otherPt = (await stateValues.points)[0].map((v2) => v2.evaluate_to_constant());
                  if (otherPt.every(Number.isFinite)) {
                    let dx = workspace.desiredPoint1.map(
                      (v2, i2) => v2 - otherPt[i2]
                    );
                    let distForSecondPt = Math.sqrt(
                      dx.reduce((a2, c2) => a2 + c2 * c2, 0)
                    );
                    if (globalDependencyValues.basedOnSlope && dx[0] !== 0) {
                      distForSecondPt *= Math.sign(dx[0]);
                    }
                    instructions.push({
                      setDependency: dependencyNamesByKey[arrayKey].distForSecondPt,
                      desiredValue: distForSecondPt
                    });
                    if (globalDependencyValues.basedOnSlope) {
                      instructions.push({
                        setDependency: dependencyNamesByKey[arrayKey].slopeAttr,
                        desiredValue: dx[1] / dx[0],
                        variableIndex: 0
                      });
                    } else if (globalDependencyValues.basedOnParallel) {
                      let unitVect = dx.map(
                        (v2) => v2 / distForSecondPt
                      );
                      instructions.push({
                        setDependency: dependencyNamesByKey[arrayKey].parallelToAttr,
                        desiredValue: unitVect.map(
                          (v2) => me$3.fromAst(v2)
                        ),
                        variableIndex: 0
                      });
                    } else {
                      let unitVect = dx.map(
                        (v2) => v2 / distForSecondPt
                      );
                      instructions.push({
                        setDependency: dependencyNamesByKey[arrayKey].perpendicularToAttr,
                        desiredValue: [
                          me$3.fromAst(-unitVect[1]),
                          me$3.fromAst(unitVect[0])
                        ],
                        variableIndex: 0
                      });
                    }
                  }
                }
              }
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].essentialPoint,
                desiredValue: desiredStateVariableValues.points[arrayKey]
              });
            }
          }
          return {
            success: true,
            instructions
          };
        }
      }
    };
    stateVariableDefinitions.equation = {
      public: true,
      description: "The line's equation as a math expression.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      stateVariablesDeterminingDependencies: ["equationIdentity"],
      additionalStateVariablesDefined: [
        {
          variableName: "coeff0",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "math",
            addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
          },
          description: "The constant term in the implicit line equation coeff0 + coeffvar1·x + coeffvar2·y = 0."
        },
        {
          variableName: "coeffvar1",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "math",
            addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
          },
          description: "The coefficient of the first variable in the implicit line equation coeff0 + coeffvar1·x + coeffvar2·y = 0."
        },
        {
          variableName: "coeffvar2",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "math",
            addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
          },
          description: "The coefficient of the second variable in the implicit line equation coeff0 + coeffvar1·x + coeffvar2·y = 0."
        }
      ],
      returnDependencies: function({ stateValues }) {
        let dependencies = {
          variables: {
            dependencyType: "stateVariable",
            variableName: "variables"
          }
        };
        if (stateValues.equationIdentity === null) {
          dependencies.points = {
            dependencyType: "stateVariable",
            variableName: "points"
          };
          dependencies.numDimensions = {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          };
        } else {
          dependencies.equation = {
            dependencyType: "attributeComponent",
            attributeName: "equation",
            variableNames: ["value"]
          };
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let variables = dependencyValues.variables;
        let blankMath2 = me$3.fromAst("＿");
        if (dependencyValues.equation) {
          let equation2 = dependencyValues.equation.stateValues.value;
          let result = calculateCoeffsFromEquation({
            equation: equation2,
            variables
          });
          if (!result.success) {
            return {
              setValue: {
                equation: equation2,
                coeff0: blankMath2,
                coeffvar1: blankMath2,
                coeffvar2: blankMath2
              },
              sendDiagnostics: result.sendDiagnostics
            };
          }
          let { coeff0: coeff02, coeffvar1: coeffvar12, coeffvar2: coeffvar22 } = result;
          return {
            setValue: {
              equation: equation2,
              coeff0: coeff02,
              coeffvar1: coeffvar12,
              coeffvar2: coeffvar22
            }
          };
        }
        let numDimens = dependencyValues.numDimensions;
        if (Number.isNaN(numDimens)) {
          let warning = {
            message: "Line through points of undetermined dimensions.",
            type: "warning"
          };
          return {
            setValue: {
              equation: blankMath2,
              coeff0: blankMath2,
              coeffvar1: blankMath2,
              coeffvar2: blankMath2
            },
            sendDiagnostics: [warning]
          };
        }
        if (numDimens < 2) {
          let warning = {
            message: "Line must be through points of at least two dimensions.",
            type: "warning"
          };
          return {
            setValue: {
              equation: blankMath2,
              coeff0: blankMath2,
              coeffvar1: blankMath2,
              coeffvar2: blankMath2
            },
            sendDiagnostics: [warning]
          };
        }
        let point1x = dependencyValues.points[0][0];
        let point1y = dependencyValues.points[0][1];
        let point2x = dependencyValues.points[1][0];
        let point2y = dependencyValues.points[1][1];
        let varStrings = [...variables.map((x2) => x2.toString())];
        for (let i2 = 0; i2 < numDimens; i2++) {
          if (point1x.variables().indexOf(varStrings[i2]) !== -1 || point1y.variables().indexOf(varStrings[i2]) !== -1 || point2x.variables().indexOf(varStrings[i2]) !== -1 || point2y.variables().indexOf(varStrings[i2]) !== -1) {
            let warning = {
              message: "Line is through points that depend on variables: " + varStrings.join(", ") + ".",
              type: "warning"
            };
            return {
              setValue: {
                equation: blankMath2,
                coeff0: blankMath2,
                coeffvar1: blankMath2,
                coeffvar2: blankMath2
              },
              sendDiagnostics: [warning]
            };
          }
        }
        if (numDimens !== 2) {
          return {
            setValue: {
              equation: blankMath2,
              coeff0: blankMath2,
              coeffvar1: blankMath2,
              coeffvar2: blankMath2
            }
          };
        }
        if (point1x.equals(point2x) && point1y.equals(point2y)) {
          let zero = me$3.fromAst(0);
          return {
            setValue: {
              equation: blankMath2,
              coeff0: zero,
              coeffvar1: zero,
              coeffvar2: zero
            }
          };
        }
        let coeffvar1 = point1y.subtract(point2y).simplify();
        let coeffvar2 = point2x.subtract(point1x).simplify();
        let coeff0 = point1x.multiply(point2y).subtract(point1y.multiply(point2x)).simplify();
        let equation = me$3.fromAst([
          "=",
          ["+", ["*", "a", "x"], ["*", "b", "y"], "c"],
          0
        ]).substitute({
          a: coeffvar1,
          b: coeffvar2,
          c: coeff0,
          x: variables[0],
          y: variables[1]
        }).simplify();
        return {
          setValue: {
            equation,
            coeff0,
            coeffvar1,
            coeffvar2
          }
        };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.points) {
          console.log(
            `Haven't implemented inverse definition of equation of line based on points`
          );
          return { success: false };
        }
        if (desiredStateVariableValues.equation) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "equation",
                desiredValue: desiredStateVariableValues.equation,
                variableIndex: 0
              }
            ]
          };
        }
        if (!("coeff0" in desiredStateVariableValues && "coeffvar1" in desiredStateVariableValues && "coeffvar2" in desiredStateVariableValues)) {
          console.log(
            `Haven't implemented inverting coeffs if not specifying all of them`
          );
          return { success: false };
        }
        let equation = me$3.fromAst([
          "=",
          0,
          ["+", ["*", "a", "x"], ["*", "b", "y"], "c"]
        ]).substitute({
          a: desiredStateVariableValues.coeffvar1,
          b: desiredStateVariableValues.coeffvar2,
          c: desiredStateVariableValues.coeff0,
          x: dependencyValues.variables[0],
          y: dependencyValues.variables[1]
        }).simplify();
        return {
          success: true,
          instructions: [
            {
              setDependency: "equation",
              desiredValue: equation,
              variableIndex: 0
            }
          ]
        };
      }
    };
    stateVariableDefinitions.parallelCoords = {
      returnDependencies: () => ({
        points: {
          dependencyType: "stateVariable",
          variableName: "points"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      definition({ dependencyValues }) {
        let parallelCoords = [];
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          parallelCoords.push([
            "+",
            dependencyValues.points[1][dim].tree,
            ["-", dependencyValues.points[0][dim].tree]
          ]);
        }
        parallelCoords = me$3.fromAst(["vector", ...parallelCoords]).simplify();
        return { setValue: { parallelCoords } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredPoints = {};
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          desiredPoints[`1,${dim}`] = me$3.fromAst([
            "+",
            desiredStateVariableValues.parallelCoords.get_component(
              dim
            ).tree,
            dependencyValues.points[0][dim].tree
          ]);
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "points",
              desiredValue: desiredPoints
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numericalPoints = {
      isArray: true,
      entryPrefixes: ["numericalPoint"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        if (Number.isNaN(dependencyValues.numDimensions)) {
          return [0];
        }
        return [2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            point: {
              dependencyType: "stateVariable",
              variableName: "point" + (Number(arrayKey) + 1)
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        if (Number.isNaN(globalDependencyValues.numDimensions)) {
          return {};
        }
        let numericalPoints = {};
        for (let arrayKey of arrayKeys) {
          let point = dependencyValuesByKey[arrayKey].point;
          if (!Array.isArray(point)) {
            point = [point];
          }
          let numericalP = [];
          for (let ind = 0; ind < globalDependencyValues.numDimensions; ind++) {
            let val = point[ind].evaluate_to_constant();
            numericalP.push(val);
          }
          numericalPoints[arrayKey] = numericalP;
        }
        return { setValue: { numericalPoints } };
      }
    };
    stateVariableDefinitions.numericalCoeff0 = {
      additionalStateVariablesDefined: [
        "numericalCoeffvar1",
        "numericalCoeffvar2"
      ],
      returnDependencies: () => ({
        coeff0: {
          dependencyType: "stateVariable",
          variableName: "coeff0"
        },
        coeffvar1: {
          dependencyType: "stateVariable",
          variableName: "coeffvar1"
        },
        coeffvar2: {
          dependencyType: "stateVariable",
          variableName: "coeffvar2"
        }
      }),
      definition: function({ dependencyValues }) {
        let numericalCoeff0 = dependencyValues.coeff0.evaluate_to_constant();
        let numericalCoeffvar1 = dependencyValues.coeffvar1.evaluate_to_constant();
        let numericalCoeffvar2 = dependencyValues.coeffvar2.evaluate_to_constant();
        return {
          setValue: {
            numericalCoeff0,
            numericalCoeffvar1,
            numericalCoeffvar2
          }
        };
      }
    };
    stateVariableDefinitions.slope = {
      description: "The slope of the line (2D only).",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        coeffvar1: {
          dependencyType: "stateVariable",
          variableName: "coeffvar1"
        },
        coeffvar2: {
          dependencyType: "stateVariable",
          variableName: "coeffvar2"
        }
      }),
      definition: function({ dependencyValues }) {
        let slope = me$3.fromAst(["-", ["/", "a", "b"]]).substitute({
          a: dependencyValues.coeffvar1,
          b: dependencyValues.coeffvar2
        }).simplify();
        return { setValue: { slope } };
      }
    };
    stateVariableDefinitions.xintercept = {
      description: "The x-intercept of the line.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        coeff0: {
          dependencyType: "stateVariable",
          variableName: "coeff0"
        },
        coeffvar1: {
          dependencyType: "stateVariable",
          variableName: "coeffvar1"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          xintercept: me$3.fromAst(["-", ["/", "a", "b"]]).substitute({
            a: dependencyValues.coeff0,
            b: dependencyValues.coeffvar1
          }).simplify()
        }
      })
    };
    stateVariableDefinitions.yintercept = {
      description: "The y-intercept of the line.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        coeff0: {
          dependencyType: "stateVariable",
          variableName: "coeff0"
        },
        coeffvar2: {
          dependencyType: "stateVariable",
          variableName: "coeffvar2"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          yintercept: me$3.fromAst(["-", ["/", "a", "b"]]).substitute({
            a: dependencyValues.coeff0,
            b: dependencyValues.coeffvar2
          }).simplify()
        }
      })
    };
    stateVariableDefinitions.lastPointsFromInverting = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          lastPointsFromInverting: true
        }
      }),
      inverseDefinition: ({ desiredStateVariableValues }) => ({
        success: true,
        instructions: [
          {
            setEssentialValue: "lastPointsFromInverting",
            value: desiredStateVariableValues.lastPointsFromInverting
          }
        ]
      })
    };
    stateVariableDefinitions.latex = {
      description: "The line's equation rendered as LaTeX.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        equation: {
          dependencyType: "stateVariable",
          variableName: "equation"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let latex = roundForDisplay({
          value: dependencyValues.equation,
          dependencyValues
        }).toLatex(params);
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.text = {
      description: "The line's equation rendered as plain text.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        equation: {
          dependencyType: "stateVariable",
          variableName: "equation"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let text = roundForDisplay({
          value: dependencyValues.equation,
          dependencyValues
        }).toString(params);
        return { setValue: { text } };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalCoeff0: {
          dependencyType: "stateVariable",
          variableName: "numericalCoeff0"
        },
        numericalCoeffvar1: {
          dependencyType: "stateVariable",
          variableName: "numericalCoeffvar1"
        },
        numericalCoeffvar2: {
          dependencyType: "stateVariable",
          variableName: "numericalCoeffvar2"
        }
      }),
      definition({ dependencyValues }) {
        let a0 = dependencyValues.numericalCoeffvar1;
        let b0 = dependencyValues.numericalCoeffvar2;
        let c2 = dependencyValues.numericalCoeff0;
        let constantCoeffs = Number.isFinite(a0) && Number.isFinite(b0) && Number.isFinite(c2);
        let skip = dependencyValues.numDimensions !== 2 || !constantCoeffs || a0 === 0 && b0 === 0;
        return {
          setValue: {
            nearestPoint: function({
              variables,
              scales = [1, 1]
            }) {
              if (skip) {
                return {};
              }
              let xscale = scales[0];
              let yscale = scales[1];
              let a2 = a0 * xscale;
              let b2 = b0 * yscale;
              let denom = a2 * a2 + b2 * b2;
              let x1 = variables.x1?.evaluate_to_constant();
              let x2 = variables.x2?.evaluate_to_constant();
              if (!(Number.isFinite(x1) && Number.isFinite(x2))) {
                return {};
              }
              let x1Effective = x1 / xscale;
              let x2Effective = x2 / yscale;
              let result = {};
              result.x1 = (b2 * (b2 * x1Effective - a2 * x2Effective) - a2 * c2) * xscale / denom;
              result.x2 = (a2 * (-b2 * x1Effective + a2 * x2Effective) - b2 * c2) * yscale / denom;
              if (variables.x3 !== void 0) {
                result.x3 = 0;
              }
              return result;
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "equation",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    },
    {
      stateVariable: "parallelCoords",
      componentType: "_directionComponent",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
  async moveLine({
    point1coords,
    point2coords,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let desiredPoints = {
      "0,0": me$3.fromAst(point1coords[0]),
      "0,1": me$3.fromAst(point1coords[1])
    };
    if (!(await this.stateValues.basedOnSlope || await this.stateValues.basedOnParallel || await this.stateValues.basedOnPerpendicular)) {
      desiredPoints["1,0"] = me$3.fromAst(point2coords[0]);
      desiredPoints["1,1"] = me$3.fromAst(point2coords[1]);
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "points",
            value: desiredPoints
          }
        ],
        transient: true,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "points",
            value: desiredPoints
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentId: this.componentIdx
          },
          result: {
            point1: point1coords,
            point2: point2coords
          }
        }
      });
    }
    if (!(await this.stateValues.basedOnSlope || await this.stateValues.basedOnParallel || await this.stateValues.basedOnPerpendicular)) {
      let numericalPoints = [point1coords, point2coords];
      let resultingNumericalPoints = await this.stateValues.numericalPoints;
      let pointsChanged = [];
      let numPointsChanged = 0;
      for (let [ind, pt2] of numericalPoints.entries()) {
        if (!pt2.every((v2, i2) => v2 === resultingNumericalPoints[ind][i2])) {
          pointsChanged.push(ind);
          numPointsChanged++;
        }
      }
      if (numPointsChanged === 1) {
        let changedInd = pointsChanged[0];
        let orig1 = numericalPoints[changedInd];
        let changed1 = resultingNumericalPoints[changedInd];
        let changevec1 = orig1.map((v2, i2) => v2 - changed1[i2]);
        let newNumericalPoints = [];
        for (let i2 = 0; i2 < 2; i2++) {
          if (i2 === changedInd) {
            newNumericalPoints.push(resultingNumericalPoints[i2]);
          } else {
            newNumericalPoints.push(
              numericalPoints[i2].map((v2, j2) => v2 - changevec1[j2])
            );
          }
        }
        let newPointComponents = {};
        for (let ind in newNumericalPoints) {
          newPointComponents[ind + ",0"] = me$3.fromAst(
            newNumericalPoints[ind][0]
          );
          newPointComponents[ind + ",1"] = me$3.fromAst(
            newNumericalPoints[ind][1]
          );
        }
        let newInstructions = [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "points",
            value: newPointComponents
          }
        ];
        return await this.coreFunctions.performUpdate({
          updateInstructions: newInstructions,
          transient,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  switchLine() {
  }
  async lineClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async lineFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function calculateCoeffsFromEquation({ equation, variables }) {
  let var1 = variables[0];
  let var2 = variables[1];
  let var1String = var1.toString();
  let var2String = var2.toString();
  equation = equation.expand().simplify();
  if (!(Array.isArray(equation.tree) && equation.tree[0] === "=" && equation.tree.length === 3)) {
    return { success: false };
  }
  let rhs = me$3.fromAst(["+", equation.tree[2], ["-", equation.tree[1]]]).expand().simplify();
  let terms = [];
  if (Array.isArray(rhs.tree) && rhs.tree[0] === "+") {
    terms = rhs.tree.slice(1);
  } else {
    terms = [rhs.tree];
  }
  let coeffvar1 = me$3.fromAst(0);
  let coeffvar2 = me$3.fromAst(0);
  let coeff0 = me$3.fromAst(0);
  for (let term of terms) {
    let coeffs = getTermCoeffs(term);
    if (!coeffs.success) {
      return coeffs;
    }
    coeffvar1 = coeffvar1.add(coeffs.coeffvar1);
    coeffvar2 = coeffvar2.add(coeffs.coeffvar2);
    coeff0 = coeff0.add(coeffs.coeff0);
  }
  coeffvar1 = coeffvar1.simplify();
  coeffvar2 = coeffvar2.simplify();
  coeff0 = coeff0.simplify();
  return { success: true, coeff0, coeffvar1, coeffvar2 };
  function getTermCoeffs(term) {
    let cv1 = 0, cv2 = 0, c0 = 0;
    if (typeof term === "string") {
      if (term === var1String) {
        cv1 = 1;
      } else if (term === var2String) {
        cv2 = 1;
      } else {
        c0 = term;
      }
    } else if (typeof term === "number") {
      c0 = term;
    } else if (!Array.isArray(term)) {
      let warning = {
        message: "Invalid format for equation of line in variables " + var1 + " and " + var2 + ".",
        type: "warning"
      };
      return { success: false, sendDiagnostics: [warning] };
    } else {
      let operator = term[0];
      let operands = term.slice(1);
      if (operator === "-") {
        let coeffs = getTermCoeffs(operands[0]);
        if (!coeffs.success) {
          return coeffs;
        }
        cv1 = ["-", coeffs.coeffvar1.tree];
        cv2 = ["-", coeffs.coeffvar2.tree];
        c0 = ["-", coeffs.coeff0.tree];
      } else if (operator === "+") {
        let warning = {
          message: "Invalid format for equation of line in variables " + var1 + " and " + var2 + ".",
          type: "warning"
        };
        return { success: false, sendDiagnostics: [warning] };
      } else if (operator === "*") {
        let var1ind = -1, var2ind = -1;
        for (let i2 = 0; i2 < operands.length; i2++) {
          if (var1.equals(me$3.fromAst(operands[i2]))) {
            var1ind = i2;
            break;
          } else if (var2.equals(me$3.fromAst(operands[i2]))) {
            var2ind = i2;
            break;
          }
        }
        if (var1ind !== -1) {
          operands.splice(var1ind, 1);
          if (operands.length === 1) {
            cv1 = operands[0];
          } else {
            cv1 = ["*"].concat(operands);
          }
        } else if (var2ind !== -1) {
          operands.splice(var2ind, 1);
          if (operands.length === 1) {
            cv2 = operands[0];
          } else {
            cv2 = ["*"].concat(operands);
          }
        } else {
          c0 = term;
        }
      } else if (operator === "/") {
        let coeffs = getTermCoeffs(operands[0]);
        if (!coeffs.success) {
          return coeffs;
        }
        cv1 = ["/", coeffs.coeffvar1.tree, operands[1]];
        cv2 = ["/", coeffs.coeffvar2.tree, operands[1]];
        c0 = ["/", coeffs.coeff0.tree, operands[1]];
      } else if (operator === "_") {
        if (var1.equals(me$3.fromAst(term))) {
          cv1 = 1;
        } else if (var2.equals(me$3.fromAst(term))) {
          cv2 = 1;
        } else {
          c0 = term;
        }
      } else {
        c0 = term;
      }
    }
    return {
      success: true,
      coeffvar1: me$3.fromAst(cv1),
      coeffvar2: me$3.fromAst(cv2),
      coeff0: me$3.fromAst(c0)
    };
  }
}
function calculatePointsFromCoeffs({
  coeff0,
  coeffvar1,
  coeffvar2,
  variables,
  lastPointsFromInverting
}) {
  let var1 = variables[0];
  let var2 = variables[1];
  let var1String = var1.toString();
  let var2String = var2.toString();
  if (coeffvar1.variables(true).indexOf(var1String) !== -1 || coeffvar1.variables(true).indexOf(var2String) !== -1 || coeffvar2.variables(true).indexOf(var1String) !== -1 || coeffvar2.variables(true).indexOf(var2String) !== -1 || coeff0.variables(true).indexOf(var1String) !== -1 || coeff0.variables(true).indexOf(var2String) !== -1) {
    let warning = {
      message: "Invalid format for equation of line in variables " + var1String + " and " + var2String + ".",
      type: "warning"
    };
    return { success: false, sendDiagnostics: [warning] };
  }
  let zero = me$3.fromAst(0);
  if (coeffvar1.equals(zero) && coeffvar2.equals(zero)) {
    let warning = {
      message: "Invalid format for equation of line in variables " + var1String + " and " + var2String + ".",
      type: "warning"
    };
    return { success: false, sendDiagnostics: [warning] };
  }
  let a2 = coeffvar1.evaluate_to_constant();
  let b2 = coeffvar2.evaluate_to_constant();
  let c2 = coeff0.evaluate_to_constant();
  let point1x, point1y, point2x, point2y;
  let points = {};
  if (Number.isFinite(c2) && Number.isFinite(a2) && Number.isFinite(b2)) {
    let denom = a2 * a2 + b2 * b2;
    if (denom === 0) {
      return { success: false };
    }
    if (lastPointsFromInverting) {
      let x1 = lastPointsFromInverting[0][0];
      let x2 = lastPointsFromInverting[0][1];
      point1x = (b2 * (b2 * x1 - a2 * x2) - a2 * c2) / denom;
      point1y = (a2 * (-b2 * x1 + a2 * x2) - b2 * c2) / denom;
      x1 = lastPointsFromInverting[1][0];
      x2 = lastPointsFromInverting[1][1];
      point2x = (b2 * (b2 * x1 - a2 * x2) - a2 * c2) / denom;
      point2y = (a2 * (-b2 * x1 + a2 * x2) - b2 * c2) / denom;
    } else {
      point1x = (2 * b2 - a2 * c2) / denom;
      point1y = (-2 * a2 - b2 * c2) / denom;
      point2x = (b2 - a2 * c2) / denom;
      point2y = -(a2 + b2 * c2) / denom;
    }
    points["0,0"] = me$3.fromAst(point1x);
    points["0,1"] = me$3.fromAst(point1y);
    points["1,0"] = me$3.fromAst(point2x);
    points["1,1"] = me$3.fromAst(point2y);
  } else {
    let denom = coeffvar1.pow(2).add(coeffvar2.pow(2));
    point1x = coeffvar2.multiply(2).subtract(coeffvar1.multiply(coeff0)).divide(denom);
    point1y = coeffvar1.multiply(-2).subtract(coeffvar2.multiply(coeff0)).divide(denom);
    point2x = coeffvar2.subtract(coeffvar1.multiply(coeff0)).divide(denom);
    point2y = coeffvar1.add(coeffvar2.multiply(coeff0)).multiply(-1).divide(denom);
    points["0,0"] = point1x;
    points["0,1"] = point1y;
    points["1,0"] = point2x;
    points["1,1"] = point2y;
  }
  return { success: true, points };
}
class EquilibriumLine extends Line {
  static componentType = "equilibriumLine";
  static rendererType = "line";
  static componentDocs = {
    summary: "An equilibrium line of a dynamical system, rendered solid if stable or dashed if unstable"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.lineStyle;
    attributes.stable = {
      createComponentOfType: "boolean",
      createStateVariable: "stable",
      defaultValue: true,
      public: true,
      description: "Whether the equilibrium is stable (rendered solid) or unstable (rendered dashed)."
    };
    attributes.switchable = {
      createComponentOfType: "boolean",
      createStateVariable: "switchable",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether the user can toggle the stability of this equilibrium by clicking it."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.dashed = {
      forRenderer: true,
      returnDependencies: () => ({
        stable: {
          dependencyType: "stateVariable",
          variableName: "stable"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { dashed: !dependencyValues.stable }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "stable",
              desiredValue: !desiredStateVariableValues.dashed
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  async switchLine({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (await this.stateValues.switchable) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "stable",
            value: !this.stateValues.stable
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            stable: !this.stateValues.stable
          }
        }
      });
    }
  }
}
class Curve extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveControlVector: this.moveControlVector.bind(this),
      moveThroughPoint: this.moveThroughPoint.bind(this),
      changeVectorControlDirection: this.changeVectorControlDirection.bind(this),
      switchCurve: this.switchCurve.bind(this),
      curveClicked: this.curveClicked.bind(this),
      curveFocused: this.curveFocused.bind(this)
    });
  }
  static componentType = "curve";
  static styleOverrideCategories = ["line", "fill"];
  static componentDocs = {
    summary: "A curve defined by a function, parametric formulas, or control points"
  };
  static rendererType = "curve";
  static primaryStateVariableForDefinition = "fShadow";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the curve can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.labelPosition = {
      description: "Position of the curve's label relative to the curve.",
      createComponentOfType: "text",
      createStateVariable: "labelPosition",
      defaultValue: "upperRight",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "upperRight",
          description: "Place the label above and to the right of the curve."
        },
        {
          value: "upperLeft",
          description: "Place the label above and to the left of the curve."
        },
        {
          value: "lowerRight",
          description: "Place the label below and to the right of the curve."
        },
        {
          value: "lowerLeft",
          description: "Place the label below and to the left of the curve."
        },
        {
          value: "top",
          description: "Place the label directly above the curve."
        },
        {
          value: "bottom",
          description: "Place the label directly below the curve."
        },
        {
          value: "left",
          description: "Place the label directly to the left of the curve."
        },
        {
          value: "right",
          description: "Place the label directly to the right of the curve."
        }
      ]
    };
    attributes.flipFunction = {
      description: "Whether to swap the function and its argument (i.e., reflect across y=x).",
      createComponentOfType: "boolean",
      createStateVariable: "flipFunction",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.numDiscretizationPoints = {
      description: "Number of points used when discretizing the curve for rendering.",
      createComponentOfType: "number",
      createStateVariable: "numDiscretizationPoints",
      defaultValue: 1e3,
      public: true
    };
    attributes.periodic = {
      description: "Whether the curve is treated as periodic when interpolating between control points.",
      createComponentOfType: "boolean",
      createStateVariable: "periodic",
      defaultValue: false,
      public: true
    };
    attributes.splineTension = {
      description: "Tension parameter used when fitting a spline through control points.",
      createComponentOfType: "number",
      createStateVariable: "splineTension",
      defaultValue: 0.8,
      clamp: [0, 1],
      public: true
    };
    attributes.extrapolateBackward = {
      description: "Whether to extrapolate the curve before its first defined point.",
      createComponentOfType: "boolean",
      createStateVariable: "extrapolateBackward",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.extrapolateForward = {
      description: "Whether to extrapolate the curve beyond its last defined point.",
      createComponentOfType: "boolean",
      createStateVariable: "extrapolateForward",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.splineForm = {
      description: "Form of spline used to interpolate between control points.",
      createComponentOfType: "text",
      createStateVariable: "splineForm",
      defaultValue: "centripetal",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "centripetal",
          description: "Centripetal Catmull-Rom spline (avoids self-intersection at sharp turns)."
        },
        {
          value: "uniform",
          description: "Uniform Catmull-Rom spline with evenly spaced parameterization."
        }
      ]
    };
    attributes.variable = {
      createComponentOfType: "_variableName",
      createStateVariable: "variableForChild",
      defaultValue: me$3.fromAst("x"),
      description: "Name of the curve's parameter or independent variable."
    };
    attributes.through = {
      createComponentOfType: "pointList",
      description: "Points the interpolated curve passes through."
    };
    attributes.parMin = {
      createComponentOfType: "math",
      description: "Minimum value of the curve's parameter (for parametric curves)."
    };
    attributes.parMax = {
      createComponentOfType: "math",
      description: "Maximum value of the curve's parameter (for parametric curves)."
    };
    attributes.showCoordsWhenDragging = {
      description: "Whether to show coordinate labels while dragging the curve.",
      createComponentOfType: "boolean",
      createStateVariable: "showCoordsWhenDragging",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.nearestPointAsCurve = {
      createComponentOfType: "boolean",
      createStateVariable: "nearestPointAsCurvePrelim",
      defaultValue: false,
      description: "Whether nearest-point queries should treat this as a curve in the plane rather than a graph y = f(x)."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "number", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakIntoFunctionsByCommas = function(childrenToBreak, nComponents, stateIdInfo) {
      let childrenToComponentFunction = (x2, newNComponents, newStateIdInfo2) => ({
        component: {
          type: "serialized",
          componentType: "function",
          componentIdx: newNComponents++,
          stateId: newStateIdInfo2 ? `${newStateIdInfo2.prefix}${newStateIdInfo2.num++}` : void 0,
          children: x2,
          attributes: {},
          doenetAttributes: {},
          state: {}
        },
        nComponents: newNComponents
      });
      let breakFunction = returnBreakStringsSugarFunction({
        childrenToComponentFunction,
        mustStripOffOuterParentheses: true
      });
      const newStateIdInfo = stateIdInfo ? { prefix: stateIdInfo.prefix, num: stateIdInfo.num } : void 0;
      let result = breakFunction({
        matchedChildren: childrenToBreak,
        nComponents,
        stateIdInfo: newStateIdInfo
      });
      let functionChildren = [];
      if (result.success) {
        functionChildren = result.newChildren;
        nComponents = result.nComponents;
        if (stateIdInfo) {
          stateIdInfo.num = newStateIdInfo.num;
        }
      } else {
        functionChildren = [
          {
            type: "serialized",
            componentType: "function",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: childrenToBreak,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ];
      }
      return { newChildren: functionChildren, nComponents };
    };
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        onlyStringOrMacros: true,
        customWrappingFunction: breakIntoFunctionsByCommas
      })
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    let childGroups = super.returnChildGroups();
    childGroups.push(
      ...[
        {
          group: "functions",
          componentTypes: ["function"]
        },
        {
          group: "bezierControls",
          componentTypes: ["bezierControls"]
        }
      ]
    );
    return childGroups;
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the curve's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let styleDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (styleDescription) {
            styleDescription += " ";
          }
          styleDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (styleDescription) {
          styleDescription += " ";
        }
        styleDescription += lineColorWord;
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "curve".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " curve";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.fShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          fShadow: true
        }
      })
    };
    stateVariableDefinitions.fromVectorValuedFunctionOfDim = {
      returnDependencies: () => ({
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["numOutputs"]
        },
        fShadow: {
          dependencyType: "stateVariable",
          variableName: "fShadow"
        }
      }),
      definition({ dependencyValues }) {
        let fromVectorValuedFunctionOfDim = 0;
        if (dependencyValues.functionChildren.length === 1 && dependencyValues.functionChildren[0].stateValues.numOutputs > 1) {
          fromVectorValuedFunctionOfDim = dependencyValues.functionChildren[0].stateValues.numOutputs;
        } else if (dependencyValues.functionChildren.length === 0 && dependencyValues.fShadow?.length > 1) {
          fromVectorValuedFunctionOfDim = dependencyValues.fShadow.length;
        }
        return { setValue: { fromVectorValuedFunctionOfDim } };
      }
    };
    stateVariableDefinitions.curveType = {
      forRenderer: true,
      returnDependencies: () => ({
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"]
        },
        fromVectorValuedFunctionOfDim: {
          dependencyType: "stateVariable",
          variableName: "fromVectorValuedFunctionOfDim"
        },
        through: {
          dependencyType: "attributeComponent",
          attributeName: "through"
        }
      }),
      definition({ dependencyValues }) {
        let curveType = "function";
        if (dependencyValues.through !== null) {
          curveType = "bezier";
        } else if (dependencyValues.functionChildren.length > 1 || dependencyValues.fromVectorValuedFunctionOfDim > 1) {
          curveType = "parameterization";
        }
        return { setValue: { curveType } };
      }
    };
    stateVariableDefinitions.graphXmin = {
      forRenderer: true,
      additionalStateVariablesDefined: [
        {
          variableName: "graphXmax",
          forRenderer: true
        },
        {
          variableName: "graphYmin",
          forRenderer: true
        },
        {
          variableName: "graphYmax",
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["xMin", "xMax", "yMin", "yMax"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.graphAncestor) {
          return {
            setValue: {
              graphXmin: dependencyValues.graphAncestor.stateValues.xMin,
              graphXmax: dependencyValues.graphAncestor.stateValues.xMax,
              graphYmin: dependencyValues.graphAncestor.stateValues.yMin,
              graphYmax: dependencyValues.graphAncestor.stateValues.yMax
            }
          };
        } else {
          return {
            setValue: {
              graphXmin: null,
              graphXmax: null,
              graphYmin: null,
              graphYmax: null
            }
          };
        }
      }
    };
    stateVariableDefinitions.parMax = {
      description: "Maximum value of the curve's parameter.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      returnDependencies: () => ({
        curveType: {
          dependencyType: "stateVariable",
          variableName: "curveType"
        },
        parMaxAttr: {
          dependencyType: "attributeComponent",
          attributeName: "parMax",
          variableNames: ["value"]
        },
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        },
        extrapolateForward: {
          dependencyType: "stateVariable",
          variableName: "extrapolateForward"
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["domain"]
        },
        adapterSourceDomain: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "domain"
        },
        graphXmin: {
          dependencyType: "stateVariable",
          variableName: "graphXmin"
        },
        graphXmax: {
          dependencyType: "stateVariable",
          variableName: "graphXmax"
        },
        graphYmin: {
          dependencyType: "stateVariable",
          variableName: "graphYmin"
        },
        graphYmax: {
          dependencyType: "stateVariable",
          variableName: "graphYmax"
        },
        flipFunction: {
          dependencyType: "stateVariable",
          variableName: "flipFunction"
        }
      }),
      definition: function({ dependencyValues }) {
        let parMax;
        if (dependencyValues.curveType === "bezier") {
          parMax = dependencyValues.numThroughPoints - 1;
          if (dependencyValues.extrapolateForward) {
            parMax *= 2;
          }
        } else if (dependencyValues.parMaxAttr !== null) {
          parMax = dependencyValues.parMaxAttr.stateValues.value.evaluate_to_constant();
        } else if (dependencyValues.curveType === "function") {
          let domain = null;
          if (dependencyValues.functionChild.length === 1) {
            domain = dependencyValues.functionChild[0].stateValues.domain;
          } else {
            domain = dependencyValues.adapterSourceDomain;
          }
          if (domain !== null) {
            domain = domain[0];
            parMax = me$3.fromAst(domain.tree[1][2]).evaluate_to_constant();
          }
          let graphMin, graphMax;
          if (dependencyValues.flipFunction) {
            graphMax = dependencyValues.graphYmax;
            graphMin = dependencyValues.graphYmin;
          } else {
            graphMax = dependencyValues.graphXmax;
            graphMin = dependencyValues.graphXmin;
          }
          if (graphMax !== null && graphMin !== null) {
            if (parMax === void 0) {
              parMax = graphMax + 0.1 * (graphMax - graphMin);
            } else {
              parMax = Math.min(
                parMax,
                graphMax + 0.1 * (graphMax - graphMin)
              );
            }
          }
          if (parMax === void 0) {
            parMax = Infinity;
          }
        } else {
          parMax = 10;
        }
        return { setValue: { parMax } };
      }
    };
    stateVariableDefinitions.parMin = {
      description: "Minimum value of the curve's parameter.",
      forRenderer: true,
      isLocation: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        curveType: {
          dependencyType: "stateVariable",
          variableName: "curveType"
        },
        parMinAttr: {
          dependencyType: "attributeComponent",
          attributeName: "parMin",
          variableNames: ["value"]
        },
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        },
        extrapolateBackward: {
          dependencyType: "stateVariable",
          variableName: "extrapolateBackward"
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["domain"]
        },
        adapterSourceDomain: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "domain"
        },
        graphXmin: {
          dependencyType: "stateVariable",
          variableName: "graphXmin"
        },
        graphXmax: {
          dependencyType: "stateVariable",
          variableName: "graphXmax"
        },
        graphYmin: {
          dependencyType: "stateVariable",
          variableName: "graphYmin"
        },
        graphYmax: {
          dependencyType: "stateVariable",
          variableName: "graphYmax"
        },
        flipFunction: {
          dependencyType: "stateVariable",
          variableName: "flipFunction"
        }
      }),
      definition: function({ dependencyValues }) {
        let parMin;
        if (dependencyValues.curveType === "bezier") {
          parMin = 0;
          if (dependencyValues.extrapolateBackward) {
            parMin = -(dependencyValues.numThroughPoints - 1);
          }
        } else if (dependencyValues.parMinAttr !== null) {
          parMin = dependencyValues.parMinAttr.stateValues.value.evaluate_to_constant();
        } else if (dependencyValues.curveType === "function") {
          let domain = null;
          if (dependencyValues.functionChild.length === 1) {
            domain = dependencyValues.functionChild[0].stateValues.domain;
          } else {
            domain = dependencyValues.adapterSourceDomain;
          }
          if (domain !== null) {
            domain = domain[0];
            parMin = me$3.fromAst(domain.tree[1][1]).evaluate_to_constant();
          }
          let graphMin, graphMax;
          if (dependencyValues.flipFunction) {
            graphMax = dependencyValues.graphYmax;
            graphMin = dependencyValues.graphYmin;
          } else {
            graphMax = dependencyValues.graphXmax;
            graphMin = dependencyValues.graphXmin;
          }
          if (graphMax !== null && graphMin !== null) {
            if (parMin === void 0) {
              parMin = graphMin + 0.1 * (graphMin - graphMax);
            } else {
              parMin = Math.max(
                parMin,
                graphMin + 0.1 * (graphMin - graphMax)
              );
            }
          }
          if (parMin === void 0) {
            parMin = -Infinity;
          }
        } else {
          parMin = -10;
        }
        return { setValue: { parMin } };
      }
    };
    stateVariableDefinitions.domainForFunctions = {
      returnDependencies: () => ({
        parMin: {
          dependencyType: "stateVariable",
          variableName: "parMin"
        },
        parMax: {
          dependencyType: "stateVariable",
          variableName: "parMax"
        }
      }),
      definition({ dependencyValues }) {
        let interval = me$3.fromAst([
          "interval",
          ["tuple", dependencyValues.parMin, dependencyValues.parMax],
          ["tuple", true, true]
        ]);
        return {
          setValue: { domainForFunctions: [interval] }
        };
      }
    };
    stateVariableDefinitions.numThroughPoints = {
      returnDependencies: () => ({
        through: {
          dependencyType: "attributeComponent",
          attributeName: "through",
          variableNames: ["numPoints"]
        }
      }),
      definition({ dependencyValues }) {
        let numThroughPoints = 0;
        if (dependencyValues.through !== null) {
          numThroughPoints = dependencyValues.through.stateValues.numPoints;
        }
        return { setValue: { numThroughPoints } };
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions the curve lives in.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies() {
        return {
          through: {
            dependencyType: "attributeComponent",
            attributeName: "through",
            variableNames: ["numDimensions"]
          }
        };
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.through !== null) {
          let numDimensions = dependencyValues.through.stateValues.numDimensions;
          return {
            setValue: { numDimensions: Math.max(2, numDimensions) },
            checkForActualChange: { numDimensions: true }
          };
        } else {
          return { setValue: { numDimensions: 2 } };
        }
      }
    };
    stateVariableDefinitions.throughPoints = {
      public: true,
      description: "Points the curve is constrained to pass through.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "throughPointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["throughPointX", "throughPoint"],
      returnEntryDimensions: (prefix) => prefix === "throughPoint" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "throughPointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "throughPoints") {
          if (propIndex.length === 1) {
            return "throughPoint" + propIndex[0];
          } else {
            return `throughPointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 12) === "throughPoint") {
          let throughPointNum = Number(varName.slice(12));
          if (Number.isInteger(throughPointNum) && throughPointNum > 0) {
            return `throughPointX${throughPointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numThroughPoints,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          dependenciesByKey[arrayKey] = {
            through: {
              dependencyType: "attributeComponent",
              attributeName: "through",
              variableNames: ["pointX" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let throughPoints = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          let through = dependencyValuesByKey[arrayKey].through;
          if (through !== null && through.stateValues["pointX" + varEnding]) {
            throughPoints[arrayKey] = through.stateValues["pointX" + varEnding];
          } else {
            throughPoints[arrayKey] = me$3.fromAst(0);
          }
        }
        return { setValue: { throughPoints } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.throughPoints) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          if (dependencyValuesByKey[arrayKey].through !== null && dependencyValuesByKey[arrayKey].through.stateValues["pointX" + varEnding]) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].through,
              desiredValue: desiredStateVariableValues.throughPoints[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else {
            return { success: false };
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalThroughPoints = {
      isArray: true,
      entryPrefixes: ["numericalThroughPoint"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            throughPoint: {
              dependencyType: "stateVariable",
              variableName: "throughPoint" + (Number(arrayKey) + 1)
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalThroughPoints = {};
        for (let arrayKey of arrayKeys) {
          let pt2 = dependencyValuesByKey[arrayKey].throughPoint.map(
            (x2) => x2.evaluate_to_constant()
          );
          if (!pt2.every((x2) => Number.isFinite(x2))) {
            pt2 = Array(pt2.length).fill(NaN);
          }
          numericalThroughPoints[arrayKey] = pt2;
        }
        return { setValue: { numericalThroughPoints } };
      }
    };
    stateVariableDefinitions.haveBezierControls = {
      forRenderer: true,
      returnDependencies: () => ({
        controlChild: {
          dependencyType: "child",
          childGroups: ["bezierControls"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveBezierControls: dependencyValues.controlChild.length > 0
          }
        };
      }
    };
    stateVariableDefinitions.bezierControlsAlwaysVisible = {
      forRenderer: true,
      returnDependencies: () => ({
        controlChild: {
          dependencyType: "child",
          childGroups: ["bezierControls"],
          variableNames: ["alwaysVisible"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            bezierControlsAlwaysVisible: dependencyValues.controlChild.length > 0 && dependencyValues.controlChild[0].stateValues.alwaysVisible
          }
        };
      }
    };
    stateVariableDefinitions.vectorControlDirections = {
      description: "Direction symmetry of each control vector pair.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      isArray: true,
      entryPrefixes: ["vectorControlDirection"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            controlChild: {
              dependencyType: "child",
              childGroups: ["bezierControls"],
              variableNames: [
                "direction" + (Number(arrayKey) + 1)
              ]
            }
          };
        }
        let globalDependencies = {
          haveBezierControls: {
            dependencyType: "stateVariable",
            variableName: "haveBezierControls"
          }
        };
        return { dependenciesByKey, globalDependencies };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let vectorControlDirections = {};
        for (let arrayKey of arrayKeys) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length > 0) {
            vectorControlDirections[arrayKey] = controlChild[0].stateValues["direction" + (Number(arrayKey) + 1)];
          } else {
            vectorControlDirections[arrayKey] = "none";
          }
        }
        return {
          setValue: { vectorControlDirections }
        };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey,
        globalDependencyValues
      }) {
        if (!globalDependencyValues.haveBezierControls) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.vectorControlDirections) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length > 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].controlChild,
              desiredValue: desiredStateVariableValues.vectorControlDirections[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.hiddenControls = {
      description: "Whether each control vector is currently hidden.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      isArray: true,
      entryPrefixes: ["hiddenControl"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            controlChild: {
              dependencyType: "child",
              childGroups: ["bezierControls"],
              variableNames: [
                "hiddenControl" + (Number(arrayKey) + 1)
              ]
            }
          };
        }
        let globalDependencies = {
          haveBezierControls: {
            dependencyType: "stateVariable",
            variableName: "haveBezierControls"
          }
        };
        return { dependenciesByKey, globalDependencies };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let hiddenControls = {};
        for (let arrayKey of arrayKeys) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length > 0) {
            hiddenControls[arrayKey] = controlChild[0].stateValues["hiddenControl" + (Number(arrayKey) + 1)];
          } else {
            hiddenControls[arrayKey] = false;
          }
        }
        return {
          setValue: { hiddenControls }
        };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey,
        globalDependencyValues
      }) {
        if (!globalDependencyValues.haveBezierControls) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.hiddenControls) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length > 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].controlChild,
              desiredValue: desiredStateVariableValues.hiddenControls[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.controlVectors = {
      isArray: true,
      public: true,
      description: "The Bezier control vectors at each through-point.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "controlVectorX") {
            return [];
          } else {
            return [
              [
                "vector",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      indexAliases: [[], [], ["x", "y", "z"]],
      entryPrefixes: [
        "controlVectorX",
        "controlVector",
        "controlVectors"
      ],
      numDimensions: 3,
      returnEntryDimensions: (prefix) => {
        if (prefix === "controlVector") {
          return 1;
        } else if (prefix === "controlVectors") {
          return 2;
        } else {
          return 0;
        }
      },
      stateVariablesDeterminingDependencies: [
        "vectorControlDirections",
        "numThroughPoints"
      ],
      returnEntryDimensions: (prefix) => {
        if (prefix === "controlVector") {
          return 1;
        } else if (prefix === "controlVectors") {
          return 2;
        } else {
          return 0;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "controlVectorX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 3 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "controlVectors") {
          let index2 = Number(varEnding) - 1;
          if (Number.isInteger(index2) && index2 >= 0) {
            if (!arraySize) {
              return [String(index2) + ",0,0"];
            }
            if (index2 < arraySize[0]) {
              let result = [];
              for (let i2 = 0; i2 < arraySize[1]; i2++) {
                let row = [];
                for (let j2 = 0; j2 < arraySize[2]; j2++) {
                  row.push(`${index2},${i2},${j2}`);
                }
                result.push(row);
              }
              return result;
            } else {
              return [];
            }
          } else {
            return [];
          }
        } else {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (!(indices.length === 2 && indices.every((x2) => Number.isInteger(x2) && x2 >= 0))) {
            return [];
          }
          if (!arraySize) {
            return [String(indices) + ",0"];
          }
          if (indices.every((x2, i2) => x2 < arraySize[i2])) {
            return Array.from(
              Array(arraySize[2]),
              (_2, i2) => String(indices) + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "controlVectors") {
          if (propIndex.length === 1) {
            return `controlVectors${propIndex[0]}`;
          }
          if (propIndex.length === 2) {
            return `controlVector${propIndex[0]}_${propIndex[1]}`;
          } else {
            return `controlVectorX${propIndex[0]}_${propIndex[1]}_${propIndex[2]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numThroughPoints,
          2,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          haveBezierControls: {
            dependencyType: "stateVariable",
            variableName: "haveBezierControls"
          },
          numThroughPoints: {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          },
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          },
          splineTension: {
            dependencyType: "stateVariable",
            variableName: "splineTension"
          },
          splineForm: {
            dependencyType: "stateVariable",
            variableName: "splineForm"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let jointVarEnding = varEndings.join("_");
          dependenciesByKey[arrayKey] = {
            direction: {
              dependencyType: "stateVariable",
              variableName: "vectorControlDirection" + varEndings[0]
            },
            controlChild: {
              dependencyType: "child",
              childGroups: ["bezierControls"],
              variableNames: ["control" + jointVarEnding]
            }
          };
          let pointInd = arrayIndices[0];
          let direction = stateValues.vectorControlDirections[pointInd];
          let indsToCheck = [];
          if (direction === "none") {
            indsToCheck = [pointInd - 1, pointInd, pointInd + 1];
          } else if (direction === "previous") {
            indsToCheck = [pointInd, pointInd + 1];
          } else if (direction === "next") {
            indsToCheck = [pointInd - 1, pointInd];
          }
          for (let ind of indsToCheck) {
            if (ind >= 0 && ind < stateValues.numThroughPoints) {
              dependenciesByKey[arrayKey]["throughPoint" + (ind + 1)] = {
                dependencyType: "stateVariable",
                variableName: "throughPoint" + (ind + 1)
              };
            }
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let newControlValues = {};
        for (let arrayKey of arrayKeys) {
          if (globalDependencyValues.numDimensions !== 2) {
            newControlValues[arrayKey] = me$3.fromAst(NaN);
            continue;
          }
          if (arrayKey in newControlValues) {
            continue;
          }
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let jointVarEnding = varEndings.join("_");
          let pointInd = arrayIndices[0];
          let vectorInd = arrayIndices[1];
          let direction = dependencyValuesByKey[arrayKey].direction;
          if (direction === "none") {
            let point2 = dependencyValuesByKey[arrayKey]["throughPoint" + (pointInd + 1)];
            let point1, point3;
            if (pointInd > 0) {
              point1 = dependencyValuesByKey[arrayKey]["throughPoint" + pointInd];
            }
            if (pointInd < globalDependencyValues.numThroughPoints) {
              point3 = dependencyValuesByKey[arrayKey]["throughPoint" + (pointInd + 2)];
            }
            let { coordsNumeric, numericEntries } = calculateControlVectorFromSpline({
              tau: globalDependencyValues.splineTension,
              eps: numerics.eps,
              splineForm: globalDependencyValues.splineForm,
              point1,
              point2,
              point3
            });
            for (let dim = 0; dim < 2; dim++) {
              let arrayKeyDim = pointInd + "," + vectorInd + "," + dim;
              let flippedArrayKeyDim = pointInd + "," + (1 - vectorInd) + "," + dim;
              if (vectorInd === 0) {
                newControlValues[arrayKeyDim] = coordsNumeric[dim];
                newControlValues[flippedArrayKeyDim] = me$3.fromAst(-coordsNumeric[dim].tree);
              } else {
                newControlValues[arrayKeyDim] = me$3.fromAst(
                  -coordsNumeric[dim].tree
                );
                newControlValues[flippedArrayKeyDim] = coordsNumeric[dim];
              }
            }
          } else if (arrayIndices[1] === 0 && direction === "next" || arrayIndices[1] === 1 && direction === "previous") {
            let point1 = dependencyValuesByKey[arrayKey]["throughPoint" + pointInd];
            let point2 = dependencyValuesByKey[arrayKey]["throughPoint" + (pointInd + 1)];
            let point3 = dependencyValuesByKey[arrayKey]["throughPoint" + (pointInd + 2)];
            let { coordsNumeric, numericEntries } = calculateControlVectorFromSpline({
              tau: globalDependencyValues.splineTension,
              eps: numerics.eps,
              splineForm: globalDependencyValues.splineForm,
              point1: point1 ? point1 : point3,
              point2,
              point3: void 0
            });
            for (let dim = 0; dim < 2; dim++) {
              let arrayKeyDim = pointInd + "," + vectorInd + "," + dim;
              newControlValues[arrayKeyDim] = coordsNumeric[dim];
            }
          } else {
            newControlValues[arrayKey] = dependencyValuesByKey[arrayKey].controlChild[0].stateValues["control" + jointVarEnding];
          }
        }
        return {
          setValue: {
            controlVectors: newControlValues
          }
        };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey,
        globalDependencyValues
      }) {
        if (!globalDependencyValues.haveBezierControls || globalDependencyValues.numDimensions !== 2) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.controlVectors) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let jointVarEnding = varEndings.join("_");
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild.length > 0) {
            let control = controlChild[0].stateValues["control" + jointVarEnding];
            if (control) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].controlChild,
                desiredValue: desiredStateVariableValues.controlVectors[arrayKey],
                childIndex: 0,
                variableIndex: 0
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalControlVectors = {
      isArray: true,
      entryPrefixes: ["numericalControlVector"],
      forRenderer: true,
      numDimensions: 2,
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints, 2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let jointVarEnding = varEndings.join("_");
          dependenciesByKey[arrayKey] = {
            controlVector: {
              dependencyType: "stateVariable",
              variableName: "controlVector" + jointVarEnding
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalControlVectors = {};
        for (let arrayKey of arrayKeys) {
          let vect = dependencyValuesByKey[arrayKey].controlVector.map((x2) => x2.tree);
          numericalControlVectors[arrayKey] = vect;
        }
        return { setValue: { numericalControlVectors } };
      }
    };
    stateVariableDefinitions.controlPoints = {
      isArray: true,
      public: true,
      description: "The Bezier control points (vertex + offset) for each through-point.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "controlPointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      entryPrefixes: ["controlPointX", "controlPoint", "controlPoints"],
      numDimensions: 3,
      returnEntryDimensions: (prefix) => {
        if (prefix === "controlPoint") {
          return 1;
        } else if (prefix === "controlPoints") {
          return 2;
        } else {
          return 0;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "controlPointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 3 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "controlPoints") {
          let index2 = Number(varEnding) - 1;
          if (Number.isInteger(index2) && index2 >= 0) {
            if (!arraySize) {
              return [String(index2) + ",0,0"];
            }
            if (index2 < arraySize[0]) {
              let result = [];
              for (let i2 = 0; i2 < arraySize[1]; i2++) {
                let row = [];
                for (let j2 = 0; j2 < arraySize[2]; j2++) {
                  row.push(`${index2},${i2},${j2}`);
                }
                result.push(row);
              }
              return result;
            } else {
              return [];
            }
          } else {
            return [];
          }
        } else {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (!(indices.length === 2 && indices.every((x2) => Number.isInteger(x2) && x2 >= 0))) {
            return [];
          }
          if (!arraySize) {
            return [String(indices) + ",0"];
          }
          if (indices.every((x2, i2) => x2 < arraySize[i2])) {
            return Array.from(
              Array(arraySize[2]),
              (_2, i2) => String(indices) + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "controlPoints") {
          if (propIndex.length === 1) {
            return `controlPoints${propIndex[0]}`;
          }
          if (propIndex.length === 2) {
            return `controlPoint${propIndex[0]}_${propIndex[1]}`;
          } else {
            return `controlPointX${propIndex[0]}_${propIndex[1]}_${propIndex[2]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numThroughPoints,
          2,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          haveBezierControls: {
            dependencyType: "stateVariable",
            variableName: "haveBezierControls"
          },
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let jointVarEnding = varEndings.join("_");
          dependenciesByKey[arrayKey] = {
            throughPointX: {
              dependencyType: "stateVariable",
              variableName: "throughPointX" + varEndings[0] + "_" + varEndings[2]
            },
            controlVectorX: {
              dependencyType: "stateVariable",
              variableName: "controlVectorX" + jointVarEnding
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let newControlValues = {};
        for (let arrayKey of arrayKeys) {
          if (globalDependencyValues.numDimensions !== 2) {
            newControlValues[arrayKey] = me$3.fromAst(NaN);
          } else {
            let vectorX = dependencyValuesByKey[arrayKey].controlVectorX;
            if (vectorX) {
              let pointX = dependencyValuesByKey[arrayKey].throughPointX.evaluate_to_constant();
              newControlValues[arrayKey] = me$3.fromAst(
                pointX + vectorX.tree
              );
            } else {
              newControlValues[arrayKey] = null;
            }
          }
        }
        return {
          setValue: {
            controlPoints: newControlValues
          }
        };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey,
        globalDependencyValues
      }) {
        if (!globalDependencyValues.haveBezierControls || globalDependencyValues.numDimensions !== 2) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.controlPoints) {
          let vectorX = dependencyValuesByKey[arrayKey].controlVectorX;
          if (vectorX) {
            let pointX = dependencyValuesByKey[arrayKey].throughPointX;
            let desiredPoint = desiredStateVariableValues.controlPoints[arrayKey];
            if (desiredPoint.tree) {
              desiredPoint = desiredPoint.tree;
            }
            let desiredValue = me$3.fromAst([
              "+",
              desiredPoint,
              ["-", pointX.tree]
            ]);
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].controlVectorX,
              desiredValue
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalControlPoints = {
      isArray: true,
      entryPrefixes: ["numericalControlPoint"],
      forRenderer: true,
      numDimensions: 2,
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints, 2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let jointVarEnding = varEndings.join("_");
          dependenciesByKey[arrayKey] = {
            controlPoint: {
              dependencyType: "stateVariable",
              variableName: "controlPoint" + jointVarEnding
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalControlPoints = {};
        for (let arrayKey of arrayKeys) {
          let pt2 = dependencyValuesByKey[arrayKey].controlPoint.map(
            (x2) => x2.tree
          );
          numericalControlPoints[arrayKey] = pt2;
        }
        return { setValue: { numericalControlPoints } };
      }
    };
    stateVariableDefinitions.splineCoeffs = {
      isArray: true,
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints - 1];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let ind1 = Number(arrayKey) + 1;
          let ind2 = ind1 + 1;
          dependenciesByKey[arrayKey] = {
            previousPoint: {
              dependencyType: "stateVariable",
              variableName: "numericalThroughPoint" + ind1
            },
            nextPoint: {
              dependencyType: "stateVariable",
              variableName: "numericalThroughPoint" + ind2
            },
            previousVector: {
              dependencyType: "stateVariable",
              variableName: "numericalControlVector" + ind1 + "_2"
            },
            nextVector: {
              dependencyType: "stateVariable",
              variableName: "numericalControlVector" + ind2 + "_1"
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let newSpineCoeffs = {};
        for (let arrayKey of arrayKeys) {
          let p1 = dependencyValuesByKey[arrayKey].previousPoint;
          let p2 = dependencyValuesByKey[arrayKey].nextPoint;
          let cv1 = dependencyValuesByKey[arrayKey].previousVector;
          let cv2 = dependencyValuesByKey[arrayKey].nextVector;
          let c2 = [];
          for (let dim = 0; dim < 2; dim++) {
            c2.push(
              initCubicPoly(
                p1[dim],
                p2[dim],
                3 * cv1[dim],
                -3 * cv2[dim]
              )
            );
          }
          newSpineCoeffs[arrayKey] = c2;
        }
        return {
          setValue: {
            splineCoeffs: newSpineCoeffs
          }
        };
      }
    };
    stateVariableDefinitions.extrapolateBackwardCoeffs = {
      stateVariablesDeterminingDependencies: ["extrapolateBackward"],
      additionalStateVariablesDefined: [
        {
          variableName: "extrapolateBackwardMode",
          public: true,
          description: "The extrapolation style used before the first through-point (e.g., 'line', 'parabolaHorizontal', 'parabolaVertical').",
          shadowingInstructions: {
            createComponentOfType: "text"
          }
        }
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          extrapolateBackward: {
            dependencyType: "stateVariable",
            variableName: "extrapolateBackward"
          },
          numThroughPoints: {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          }
        };
        if (stateValues.extrapolateBackward) {
          dependencies.firstSplineCoeffs = {
            dependencyType: "stateVariable",
            variableName: "splineCoeffs1"
          };
          dependencies.graphXmin = {
            dependencyType: "stateVariable",
            variableName: "graphXmin"
          };
          dependencies.graphXmax = {
            dependencyType: "stateVariable",
            variableName: "graphXmax"
          };
          dependencies.graphYmin = {
            dependencyType: "stateVariable",
            variableName: "graphYmin"
          };
          dependencies.graphYmax = {
            dependencyType: "stateVariable",
            variableName: "graphYmax"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        if (!dependencyValues.extrapolateBackward || !dependencyValues.firstSplineCoeffs) {
          return {
            setValue: {
              extrapolateBackwardCoeffs: null,
              extrapolateBackwardMode: ""
            }
          };
        }
        let cx = dependencyValues.firstSplineCoeffs[0];
        let cy = dependencyValues.firstSplineCoeffs[1];
        let x0 = cx[0];
        let xp0 = cx[1];
        let xpp0 = 2 * cx[2];
        let y0 = cy[0];
        let yp0 = cy[1];
        let ypp0 = 2 * cy[2];
        let d2 = xp0 * xp0 + yp0 * yp0;
        let fac = (yp0 * xpp0 - xp0 * ypp0) / (d2 * d2);
        if (Math.abs(fac) < 1e-12 || Math.abs(xp0) < 1e-12 || Math.abs(yp0) < 1e-12) {
          let xpEffective = xp0;
          let ypEffective = yp0;
          if (dependencyValues.graphXmin !== null && dependencyValues.graphXmax !== null && dependencyValues.graphYmin !== null && dependencyValues.graphYmax !== null) {
            let xscale = dependencyValues.graphXmax - dependencyValues.graphXmin;
            let yscale = dependencyValues.graphYmax - dependencyValues.graphYmin;
            let tMax = dependencyValues.numThroughPoints - 1;
            let scaleSpeedToReachXEdge = xscale / tMax / Math.abs(xpEffective);
            let scaleSpeedToReachYEdge = yscale / tMax / Math.abs(ypEffective);
            let minScale = Math.min(
              scaleSpeedToReachXEdge,
              scaleSpeedToReachYEdge
            );
            if (minScale > 1) {
              xpEffective *= minScale;
              ypEffective *= minScale;
            }
          }
          let c2 = [
            [x0, xpEffective, 0],
            [y0, ypEffective, 0]
          ];
          return {
            setValue: {
              extrapolateBackwardCoeffs: c2,
              extrapolateBackwardMode: "line"
            }
          };
        }
        let dTx = yp0 * fac;
        let dTy = -xp0 * fac;
        if (dTx * xp0 > 0) {
          let r2 = dTx / dTy;
          let rFactor = (1 + r2 * r2) ** 2;
          let xpEffective = xp0;
          if (dependencyValues.graphXmin !== null && dependencyValues.graphXmax !== null && dependencyValues.graphYmin !== null && dependencyValues.graphYmax !== null) {
            let xscale = dependencyValues.graphXmax - dependencyValues.graphXmin;
            let yscale = dependencyValues.graphYmax - dependencyValues.graphYmin;
            let tMax = dependencyValues.numThroughPoints - 1;
            let minSpeedToReachXEdge = xscale / tMax;
            let minSpeedToReachYEdge = Infinity;
            if (dTy !== 0) {
              let alpha = dTy * rFactor / 2 * tMax * tMax;
              let beta2 = -r2 * tMax;
              let sr2 = Math.sqrt(
                beta2 * beta2 + 4 * Math.abs(alpha) * yscale
              );
              minSpeedToReachYEdge = (Math.abs(beta2) + sr2) / (2 * Math.abs(alpha));
            }
            let minSpeed = Math.min(
              minSpeedToReachXEdge,
              minSpeedToReachYEdge
            );
            if (minSpeed > Math.abs(xpEffective)) {
              xpEffective *= minSpeed / Math.abs(xpEffective);
            }
          }
          let v2 = -xpEffective * r2;
          let a2 = dTy * xpEffective * xpEffective * rFactor;
          let c2 = [
            [x0, xpEffective, 0],
            [y0, v2, a2 / 2]
          ];
          return {
            setValue: {
              extrapolateBackwardCoeffs: c2,
              extrapolateBackwardMode: "parabolaVertical"
            }
          };
        } else {
          let r2 = dTy / dTx;
          let rFactor = (1 + r2 * r2) ** 2;
          let ypEffective = yp0;
          if (dependencyValues.graphXmin !== null && dependencyValues.graphXmax !== null && dependencyValues.graphYmin !== null && dependencyValues.graphYmax !== null) {
            let xscale = dependencyValues.graphXmax - dependencyValues.graphXmin;
            let yscale = dependencyValues.graphYmax - dependencyValues.graphYmin;
            let tMax = dependencyValues.numThroughPoints - 1;
            let minSpeedToReachYEdge = yscale / tMax;
            let minSpeedToReachXEdge = Infinity;
            if (dTx !== 0) {
              let alpha = dTx * rFactor / 2 * tMax * tMax;
              let beta2 = -r2 * tMax;
              let sr2 = Math.sqrt(
                beta2 * beta2 + 4 * Math.abs(alpha) * xscale
              );
              minSpeedToReachXEdge = (Math.abs(beta2) + sr2) / (2 * Math.abs(alpha));
            }
            let minSpeed = Math.min(
              minSpeedToReachXEdge,
              minSpeedToReachYEdge
            );
            if (minSpeed > Math.abs(ypEffective)) {
              ypEffective *= minSpeed / Math.abs(ypEffective);
            }
          }
          let v2 = -ypEffective * r2;
          let a2 = dTx * ypEffective * ypEffective * rFactor;
          let c2 = [
            [x0, v2, a2 / 2],
            [y0, ypEffective, 0]
          ];
          return {
            setValue: {
              extrapolateBackwardCoeffs: c2,
              extrapolateBackwardMode: "parabolaHorizontal"
            }
          };
        }
      }
    };
    stateVariableDefinitions.extrapolateForwardCoeffs = {
      stateVariablesDeterminingDependencies: [
        "numThroughPoints",
        "extrapolateForward"
      ],
      additionalStateVariablesDefined: [
        {
          variableName: "extrapolateForwardMode",
          public: true,
          description: "The extrapolation style used beyond the last through-point (e.g., 'line', 'parabolaHorizontal', 'parabolaVertical').",
          shadowingInstructions: {
            createComponentOfType: "text"
          }
        }
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          extrapolateForward: {
            dependencyType: "stateVariable",
            variableName: "extrapolateForward"
          },
          numThroughPoints: {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          }
        };
        if (stateValues.extrapolateForward && stateValues.numThroughPoints >= 2) {
          dependencies.lastSplineCoeffs = {
            dependencyType: "stateVariable",
            variableName: "splineCoeffs" + (stateValues.numThroughPoints - 1)
          };
          dependencies.graphXmin = {
            dependencyType: "stateVariable",
            variableName: "graphXmin"
          };
          dependencies.graphXmax = {
            dependencyType: "stateVariable",
            variableName: "graphXmax"
          };
          dependencies.graphYmin = {
            dependencyType: "stateVariable",
            variableName: "graphYmin"
          };
          dependencies.graphYmax = {
            dependencyType: "stateVariable",
            variableName: "graphYmax"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        if (!dependencyValues.extrapolateForward || !dependencyValues.lastSplineCoeffs) {
          return {
            setValue: {
              extrapolateForwardCoeffs: null,
              extrapolateForwardMode: ""
            }
          };
        }
        let cx = dependencyValues.lastSplineCoeffs[0];
        let cy = dependencyValues.lastSplineCoeffs[1];
        let x0 = cx[0] + cx[1] + cx[2] + cx[3];
        let xp0 = cx[1] + 2 * cx[2] + 3 * cx[3];
        let xpp0 = 2 * cx[2] + 6 * cx[3];
        let y0 = cy[0] + cy[1] + cy[2] + cy[3];
        let yp0 = cy[1] + 2 * cy[2] + 3 * cy[3];
        let ypp0 = 2 * cy[2] + 6 * cy[3];
        let d2 = xp0 * xp0 + yp0 * yp0;
        let fac = (yp0 * xpp0 - xp0 * ypp0) / (d2 * d2);
        if (Math.abs(fac) < 1e-12 || Math.abs(xp0) < 1e-12 || Math.abs(yp0) < 1e-12) {
          let xpEffective = xp0;
          let ypEffective = yp0;
          if (dependencyValues.graphXmin !== null && dependencyValues.graphXmax !== null && dependencyValues.graphYmin !== null && dependencyValues.graphYmax !== null) {
            let xscale = dependencyValues.graphXmax - dependencyValues.graphXmin;
            let yscale = dependencyValues.graphYmax - dependencyValues.graphYmin;
            let tMax = dependencyValues.numThroughPoints - 1;
            let scaleSpeedToReachXEdge = xscale / tMax / Math.abs(xpEffective);
            let scaleSpeedToReachYEdge = yscale / tMax / Math.abs(ypEffective);
            let minScale = Math.min(
              scaleSpeedToReachXEdge,
              scaleSpeedToReachYEdge
            );
            if (minScale > 1) {
              xpEffective *= minScale;
              ypEffective *= minScale;
            }
          }
          let c2 = [
            [x0, xpEffective, 0],
            [y0, ypEffective, 0]
          ];
          return {
            setValue: {
              extrapolateForwardCoeffs: c2,
              extrapolateForwardMode: "line"
            }
          };
        }
        let dTx = yp0 * fac;
        let dTy = -xp0 * fac;
        if (dTx * xp0 < 0) {
          let r2 = dTx / dTy;
          let rFactor = (1 + r2 * r2) ** 2;
          let xpEffective = xp0;
          if (dependencyValues.graphXmin !== null && dependencyValues.graphXmax !== null && dependencyValues.graphYmin !== null && dependencyValues.graphYmax !== null) {
            let xscale = dependencyValues.graphXmax - dependencyValues.graphXmin;
            let yscale = dependencyValues.graphYmax - dependencyValues.graphYmin;
            let tMax = dependencyValues.numThroughPoints - 1;
            let minSpeedToReachXEdge = xscale / tMax;
            let minSpeedToReachYEdge = Infinity;
            if (dTy !== 0) {
              let alpha = dTy * rFactor / 2 * tMax * tMax;
              let beta2 = -r2 * tMax;
              let sr2 = Math.sqrt(
                beta2 * beta2 + 4 * Math.abs(alpha) * yscale
              );
              minSpeedToReachYEdge = (Math.abs(beta2) + sr2) / (2 * Math.abs(alpha));
            }
            let minSpeed = Math.min(
              minSpeedToReachXEdge,
              minSpeedToReachYEdge
            );
            if (minSpeed > Math.abs(xpEffective)) {
              xpEffective *= minSpeed / Math.abs(xpEffective);
            }
          }
          let v2 = -xpEffective * r2;
          let a2 = dTy * xpEffective * xpEffective * rFactor;
          let c2 = [
            [x0, xpEffective, 0],
            [y0, v2, a2 / 2]
          ];
          return {
            setValue: {
              extrapolateForwardCoeffs: c2,
              extrapolateForwardMode: "parabolaVertical"
            }
          };
        } else {
          let r2 = dTy / dTx;
          let rFactor = (1 + r2 * r2) ** 2;
          let ypEffective = yp0;
          if (dependencyValues.graphXmin !== null && dependencyValues.graphXmax !== null && dependencyValues.graphYmin !== null && dependencyValues.graphYmax !== null) {
            let xscale = dependencyValues.graphXmax - dependencyValues.graphXmin;
            let yscale = dependencyValues.graphYmax - dependencyValues.graphYmin;
            let tMax = dependencyValues.numThroughPoints - 1;
            let minSpeedToReachYEdge = yscale / tMax;
            let minSpeedToReachXEdge = Infinity;
            if (dTx !== 0) {
              let alpha = dTx * rFactor / 2 * tMax * tMax;
              let beta2 = -r2 * tMax;
              let sr2 = Math.sqrt(
                beta2 * beta2 + 4 * Math.abs(alpha) * xscale
              );
              minSpeedToReachXEdge = (Math.abs(beta2) + sr2) / (2 * Math.abs(alpha));
            }
            let minSpeed = Math.min(
              minSpeedToReachXEdge,
              minSpeedToReachYEdge
            );
            if (minSpeed > Math.abs(ypEffective)) {
              ypEffective *= minSpeed / Math.abs(ypEffective);
            }
          }
          let v2 = -ypEffective * r2;
          let a2 = dTx * ypEffective * ypEffective * rFactor;
          let c2 = [
            [x0, v2, a2 / 2],
            [y0, ypEffective, 0]
          ];
          return {
            setValue: {
              extrapolateForwardCoeffs: c2,
              extrapolateForwardMode: "parabolaHorizontal"
            }
          };
        }
      }
    };
    stateVariableDefinitions.fs = {
      isArray: true,
      entryPrefixes: ["f"],
      additionalStateVariablesDefined: [
        {
          variableName: "fDefinitions",
          isArray: true,
          forRenderer: true,
          entryPrefixes: ["fDefinition"]
        }
      ],
      public: true,
      description: "The component functions of the curve as functions of its parameter.",
      shadowingInstructions: {
        createComponentOfType: "function",
        addStateVariablesShadowingStateVariables: {
          fDefinitions: {
            stateVariableToShadow: "fDefinitions"
          },
          domain: {
            stateVariableToShadow: "domainForFunctions"
          }
        },
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnArraySizeDependencies: () => ({
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"]
        },
        fromVectorValuedFunctionOfDim: {
          dependencyType: "stateVariable",
          variableName: "fromVectorValuedFunctionOfDim"
        },
        curveType: {
          dependencyType: "stateVariable",
          variableName: "curveType"
        }
      }),
      returnArraySize({ dependencyValues }) {
        if (dependencyValues.curveType === "bezier") {
          return [2];
        } else {
          return [
            Math.max(
              1,
              dependencyValues.functionChildren.length,
              dependencyValues.fromVectorValuedFunctionOfDim
            )
          ];
        }
      },
      stateVariablesDeterminingDependencies: [
        "fromVectorValuedFunctionOfDim"
      ],
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          curveType: {
            dependencyType: "stateVariable",
            variableName: "curveType"
          },
          fromVectorValuedFunctionOfDim: {
            dependencyType: "stateVariable",
            variableName: "fromVectorValuedFunctionOfDim"
          },
          numericalThroughPoints: {
            dependencyType: "stateVariable",
            variableName: "numericalThroughPoints"
          },
          splineCoeffs: {
            dependencyType: "stateVariable",
            variableName: "splineCoeffs"
          },
          numThroughPoints: {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          },
          extrapolateBackward: {
            dependencyType: "stateVariable",
            variableName: "extrapolateBackward"
          },
          extrapolateBackwardCoeffs: {
            dependencyType: "stateVariable",
            variableName: "extrapolateBackwardCoeffs"
          },
          extrapolateForward: {
            dependencyType: "stateVariable",
            variableName: "extrapolateForward"
          },
          extrapolateForwardCoeffs: {
            dependencyType: "stateVariable",
            variableName: "extrapolateForwardCoeffs"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          if (stateValues.fromVectorValuedFunctionOfDim > 1) {
            let dim = Number(arrayKey) + 1;
            dependenciesByKey[arrayKey] = {
              functionChild: {
                dependencyType: "child",
                childGroups: ["functions"],
                variableNames: [
                  `numericalf${dim}`,
                  `fDefinition${dim}`
                ],
                childIndices: [0]
              }
            };
            globalDependencies.fShadow = {
              dependencyType: "stateVariable",
              variableName: "fShadow"
            };
            globalDependencies.fDefinitionsAdapted = {
              dependencyType: "adapterSourceStateVariable",
              variableName: "fDefinitions"
            };
          } else {
            dependenciesByKey[arrayKey] = {
              functionChild: {
                dependencyType: "child",
                childGroups: ["functions"],
                variableNames: ["numericalf", "fDefinition"],
                childIndices: [arrayKey]
              }
            };
            if (Number(arrayKey) === 0) {
              dependenciesByKey[arrayKey].fShadow = {
                dependencyType: "stateVariable",
                variableName: "fShadow"
              };
              dependenciesByKey[arrayKey].fDefinitionAdapted = {
                dependencyType: "adapterSourceStateVariable",
                variableName: "fDefinition"
              };
            }
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        if (globalDependencyValues.curveType === "bezier") {
          let bezierArguments = {
            functionType: "bezier",
            numThroughPoints: globalDependencyValues.numThroughPoints,
            numericalThroughPoints: globalDependencyValues.numericalThroughPoints,
            splineCoeffs: globalDependencyValues.splineCoeffs,
            extrapolateForward: globalDependencyValues.extrapolateForward,
            extrapolateForwardCoeffs: globalDependencyValues.extrapolateForwardCoeffs,
            extrapolateBackward: globalDependencyValues.extrapolateBackward,
            extrapolateBackwardCoeffs: globalDependencyValues.extrapolateBackwardCoeffs
          };
          let fs2 = {};
          let fDefinitions2 = {};
          bezierArguments.component = 0;
          fs2[0] = returnBezierFunctions(bezierArguments);
          fDefinitions2[0] = bezierArguments;
          bezierArguments = { ...bezierArguments };
          bezierArguments.component = 1;
          fs2[1] = returnBezierFunctions(bezierArguments);
          fDefinitions2[1] = bezierArguments;
          return {
            setValue: {
              fs: fs2,
              fDefinitions: fDefinitions2
            }
          };
        }
        let fs = {};
        let fDefinitions = {};
        for (let arrayKey of arrayKeys) {
          let functionChild = dependencyValuesByKey[arrayKey].functionChild;
          if (functionChild.length === 1) {
            if (globalDependencyValues.fromVectorValuedFunctionOfDim > 1) {
              let dim = Number(arrayKey) + 1;
              fs[arrayKey] = functionChild[0].stateValues[`numericalf${dim}`];
              fDefinitions[arrayKey] = functionChild[0].stateValues[`fDefinition${dim}`];
            } else {
              fs[arrayKey] = functionChild[0].stateValues.numericalf;
              fDefinitions[arrayKey] = functionChild[0].stateValues.fDefinition;
            }
          } else {
            if (globalDependencyValues.fromVectorValuedFunctionOfDim > 1) {
              fs[arrayKey] = globalDependencyValues.fShadow[arrayKey];
              fDefinitions[arrayKey] = globalDependencyValues.fDefinitionsAdapted[arrayKey];
            } else {
              if (Number(arrayKey) === 0 && dependencyValuesByKey[arrayKey].fShadow?.[0]) {
                fs[arrayKey] = dependencyValuesByKey[arrayKey].fShadow[0];
                fDefinitions[arrayKey] = dependencyValuesByKey[arrayKey].fDefinitionAdapted;
              } else {
                fs[arrayKey] = () => 0;
                fDefinitions[arrayKey] = {
                  functionType: "zero"
                };
              }
            }
          }
        }
        return {
          setValue: { fs, fDefinitions }
        };
      }
    };
    stateVariableDefinitions.f = {
      isAlias: true,
      targetVariableName: "f1",
      description: "The first component function of the curve as a function of its parameter."
    };
    stateVariableDefinitions.allXCriticalPoints = {
      returnDependencies: () => ({
        splineCoeffs: {
          dependencyType: "stateVariable",
          variableName: "splineCoeffs"
        },
        fs: {
          dependencyType: "stateVariable",
          variableName: "fs"
        },
        curveType: {
          dependencyType: "stateVariable",
          variableName: "curveType"
        }
      }),
      definition({ dependencyValues }) {
        let allXCriticalPoints = [];
        if (dependencyValues.curveType !== "bezier") {
          return { setValue: { allXCriticalPoints } };
        }
        let fx = dependencyValues.fs[0];
        let fy = dependencyValues.fs[1];
        let ts = [];
        let xCriticalPointAtPreviousRight = false;
        for (let [ind, cs] of dependencyValues.splineCoeffs.entries()) {
          let the_cs = cs[0];
          let A2 = 3 * the_cs[3];
          let B2 = 2 * the_cs[2];
          let C3 = the_cs[1];
          if (Math.abs(A2) < 1e-14) {
            let t2 = -C3 / B2;
            xCriticalPointAtPreviousRight = addTimePointBezier({
              t: t2,
              ind,
              ts,
              ignoreLeft: xCriticalPointAtPreviousRight
            });
          } else {
            let discrim = B2 * B2 - 4 * A2 * C3;
            if (discrim == 0) {
              let t2 = -B2 / (2 * A2);
              xCriticalPointAtPreviousRight = addTimePointBezier({
                t: t2,
                ind,
                ts,
                ignoreLeft: xCriticalPointAtPreviousRight
              });
            } else if (discrim > 0) {
              let sqd = Math.sqrt(discrim);
              let newTs = [
                (-B2 - sqd) / (2 * A2),
                (-B2 + sqd) / (2 * A2)
              ];
              if (A2 < 0) {
                newTs = [newTs[1], newTs[0]];
              }
              let foundRight = false;
              for (let t2 of newTs) {
                let temp = addTimePointBezier({
                  t: t2,
                  ind,
                  ts,
                  ignoreLeft: xCriticalPointAtPreviousRight
                });
                if (temp) {
                  foundRight = true;
                }
              }
              xCriticalPointAtPreviousRight = foundRight;
            } else {
              xCriticalPointAtPreviousRight = false;
            }
          }
        }
        for (let t2 of ts) {
          allXCriticalPoints.push([fx(t2), fy(t2)]);
        }
        return { setValue: { allXCriticalPoints } };
      }
    };
    stateVariableDefinitions.numXCriticalPoints = {
      description: "The number of x-critical points (horizontal tangents) on the curve.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        allXCriticalPoints: {
          dependencyType: "stateVariable",
          variableName: "allXCriticalPoints"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numXCriticalPoints: dependencyValues.allXCriticalPoints.length
          }
        };
      }
    };
    stateVariableDefinitions.xCriticalPoints = {
      description: "The x-critical points (horizontal tangents) on the curve.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "xCriticalPointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["xCriticalPointX", "xCriticalPoint"],
      returnEntryDimensions: (prefix) => prefix === "xCriticalPoint" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "xCriticalPointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "xCriticalPoints") {
          if (propIndex.length === 1) {
            return "xCriticalPoint" + propIndex[0];
          } else {
            return `xCriticalPointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 14) === "xCriticalPoint") {
          let xCriticalPointNum = Number(varName.slice(14));
          if (Number.isInteger(xCriticalPointNum) && xCriticalPointNum > 0) {
            return `xCriticalPointX${xCriticalPointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numXCriticalPoints: {
          dependencyType: "stateVariable",
          variableName: "numXCriticalPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numXCriticalPoints, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allXCriticalPoints: {
            dependencyType: "stateVariable",
            variableName: "allXCriticalPoints"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let xCriticalPoints = {};
        for (let ptInd = 0; ptInd < globalDependencyValues.__array_size[0]; ptInd++) {
          for (let i2 = 0; i2 < 2; i2++) {
            let arrayKey = `${ptInd},${i2}`;
            xCriticalPoints[arrayKey] = globalDependencyValues.allXCriticalPoints[ptInd][i2];
          }
        }
        return { setValue: { xCriticalPoints } };
      }
    };
    stateVariableDefinitions.allYCriticalPoints = {
      returnDependencies: () => ({
        splineCoeffs: {
          dependencyType: "stateVariable",
          variableName: "splineCoeffs"
        },
        fs: {
          dependencyType: "stateVariable",
          variableName: "fs"
        },
        curveType: {
          dependencyType: "stateVariable",
          variableName: "curveType"
        }
      }),
      definition({ dependencyValues }) {
        let allYCriticalPoints = [];
        if (dependencyValues.curveType !== "bezier") {
          return { setValue: { allYCriticalPoints } };
        }
        let fx = dependencyValues.fs[0];
        let fy = dependencyValues.fs[1];
        let ts = [];
        let yCriticalPointAtPreviousRight = false;
        for (let [ind, cs] of dependencyValues.splineCoeffs.entries()) {
          let the_cs = cs[1];
          let A2 = 3 * the_cs[3];
          let B2 = 2 * the_cs[2];
          let C3 = the_cs[1];
          if (Math.abs(A2) < 1e-14) {
            let t2 = -C3 / B2;
            yCriticalPointAtPreviousRight = addTimePointBezier({
              t: t2,
              ind,
              ts,
              ignoreLeft: yCriticalPointAtPreviousRight
            });
          } else {
            let discrim = B2 * B2 - 4 * A2 * C3;
            if (discrim == 0) {
              let t2 = -B2 / (2 * A2);
              yCriticalPointAtPreviousRight = addTimePointBezier({
                t: t2,
                ind,
                ts,
                ignoreLeft: yCriticalPointAtPreviousRight
              });
            } else if (discrim > 0) {
              let sqd = Math.sqrt(discrim);
              let newTs = [
                (-B2 - sqd) / (2 * A2),
                (-B2 + sqd) / (2 * A2)
              ];
              if (A2 < 0) {
                newTs = [newTs[1], newTs[0]];
              }
              let foundRight = false;
              for (let t2 of newTs) {
                let temp = addTimePointBezier({
                  t: t2,
                  ind,
                  ts,
                  ignoreLeft: yCriticalPointAtPreviousRight
                });
                if (temp) {
                  foundRight = true;
                }
              }
              yCriticalPointAtPreviousRight = foundRight;
            } else {
              yCriticalPointAtPreviousRight = false;
            }
          }
        }
        for (let t2 of ts) {
          allYCriticalPoints.push([fx(t2), fy(t2)]);
        }
        return { setValue: { allYCriticalPoints } };
      }
    };
    stateVariableDefinitions.numYCriticalPoints = {
      description: "The number of y-critical points (vertical tangents) on the curve.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        allYCriticalPoints: {
          dependencyType: "stateVariable",
          variableName: "allYCriticalPoints"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numYCriticalPoints: dependencyValues.allYCriticalPoints.length
          }
        };
      }
    };
    stateVariableDefinitions.yCriticalPoints = {
      description: "The y-critical points (vertical tangents) on the curve.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "yCriticalPointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["yCriticalPointX", "yCriticalPoint"],
      returnEntryDimensions: (prefix) => prefix === "yCriticalPoint" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "yCriticalPointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "yCriticalPoints") {
          if (propIndex.length === 1) {
            return "yCriticalPoint" + propIndex[0];
          } else {
            return `yCriticalPointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 14) === "yCriticalPoint") {
          let yCriticalPointNum = Number(varName.slice(14));
          if (Number.isInteger(yCriticalPointNum) && yCriticalPointNum > 0) {
            return `yCriticalPointX${yCriticalPointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numYCriticalPoints: {
          dependencyType: "stateVariable",
          variableName: "numYCriticalPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numYCriticalPoints, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allYCriticalPoints: {
            dependencyType: "stateVariable",
            variableName: "allYCriticalPoints"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let yCriticalPoints = {};
        for (let ptInd = 0; ptInd < globalDependencyValues.__array_size[0]; ptInd++) {
          for (let i2 = 0; i2 < 2; i2++) {
            let arrayKey = `${ptInd},${i2}`;
            yCriticalPoints[arrayKey] = globalDependencyValues.allYCriticalPoints[ptInd][i2];
          }
        }
        return { setValue: { yCriticalPoints } };
      }
    };
    stateVariableDefinitions.allCurvatureChangePoints = {
      returnDependencies: () => ({
        splineCoeffs: {
          dependencyType: "stateVariable",
          variableName: "splineCoeffs"
        },
        fs: {
          dependencyType: "stateVariable",
          variableName: "fs"
        },
        curveType: {
          dependencyType: "stateVariable",
          variableName: "curveType"
        }
      }),
      definition({ dependencyValues }) {
        let allCurvatureChangePoints = [];
        if (dependencyValues.curveType !== "bezier") {
          return { setValue: { allCurvatureChangePoints } };
        }
        let fx = dependencyValues.fs[0];
        let fy = dependencyValues.fs[1];
        let ts = [];
        let changePointAtPreviousRight = false;
        for (let [ind, cs] of dependencyValues.splineCoeffs.entries()) {
          let [dx, cx, bx, ax] = cs[0];
          let [dy, cy, by, ay] = cs[1];
          let A2 = 3 * (bx * ay - by * ax);
          let B2 = 3 * (cx * ay - cy * ax);
          let C3 = cx * by - cy * bx;
          if (Math.abs(A2) < 1e-14) {
            let t2 = -C3 / B2;
            changePointAtPreviousRight = addTimePointBezier({
              t: t2,
              ind,
              ts,
              ignoreLeft: changePointAtPreviousRight
            });
          } else {
            let discrim = B2 * B2 - 4 * A2 * C3;
            if (discrim == 0) {
              let t2 = -B2 / (2 * A2);
              changePointAtPreviousRight = addTimePointBezier({
                t: t2,
                ind,
                ts,
                ignoreLeft: changePointAtPreviousRight
              });
            } else if (discrim > 0) {
              let sqd = Math.sqrt(discrim);
              let newTs = [
                (-B2 - sqd) / (2 * A2),
                (-B2 + sqd) / (2 * A2)
              ];
              if (A2 < 0) {
                newTs = [newTs[1], newTs[0]];
              }
              let foundRight = false;
              for (let t2 of newTs) {
                let temp = addTimePointBezier({
                  t: t2,
                  ind,
                  ts,
                  ignoreLeft: changePointAtPreviousRight
                });
                if (temp) {
                  foundRight = true;
                }
              }
              changePointAtPreviousRight = foundRight;
            } else {
              changePointAtPreviousRight = false;
            }
          }
        }
        for (let t2 of ts) {
          allCurvatureChangePoints.push([fx(t2), fy(t2)]);
        }
        return { setValue: { allCurvatureChangePoints } };
      }
    };
    stateVariableDefinitions.numCurvatureChangePoints = {
      description: "The number of points where the curve's curvature changes sign.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        allCurvatureChangePoints: {
          dependencyType: "stateVariable",
          variableName: "allCurvatureChangePoints"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numCurvatureChangePoints: dependencyValues.allCurvatureChangePoints.length
          }
        };
      }
    };
    stateVariableDefinitions.curvatureChangePoints = {
      description: "Points where the curve's curvature changes sign (inflection points).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "curvatureChangePointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["curvatureChangePointX", "curvatureChangePoint"],
      returnEntryDimensions: (prefix) => prefix === "curvatureChangePoint" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "curvatureChangePointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "curvatureChangePoints") {
          if (propIndex.length === 1) {
            return "curvatureChangePoint" + propIndex[0];
          } else {
            return `curvatureChangePointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 20) === "curvatureChangePoint") {
          let curvatureChangePointNum = Number(varName.slice(20));
          if (Number.isInteger(curvatureChangePointNum) && curvatureChangePointNum > 0) {
            return `curvatureChangePointX${curvatureChangePointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numCurvatureChangePoints: {
          dependencyType: "stateVariable",
          variableName: "numCurvatureChangePoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numCurvatureChangePoints, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allCurvatureChangePoints: {
            dependencyType: "stateVariable",
            variableName: "allCurvatureChangePoints"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let curvatureChangePoints = {};
        for (let ptInd = 0; ptInd < globalDependencyValues.__array_size[0]; ptInd++) {
          for (let i2 = 0; i2 < 2; i2++) {
            let arrayKey = `${ptInd},${i2}`;
            curvatureChangePoints[arrayKey] = globalDependencyValues.allCurvatureChangePoints[ptInd][i2];
          }
        }
        return { setValue: { curvatureChangePoints } };
      }
    };
    stateVariableDefinitions.nearestPointAsCurve = {
      returnDependencies: () => ({
        nearestPointAsCurvePrelim: {
          dependencyType: "stateVariable",
          variableName: "nearestPointAsCurvePrelim"
        },
        functionChild: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["nearestPointAsCurve"]
        },
        adapterSourceValue: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "nearestPointAsCurve"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let nearestPointAsCurve = dependencyValues.nearestPointAsCurvePrelim;
        if (usedDefault.nearestPointAsCurvePrelim) {
          if (dependencyValues.functionChild.length > 0) {
            nearestPointAsCurve = dependencyValues.functionChild[0].stateValues.nearestPointAsCurve;
          } else if (dependencyValues.adapterSourceValue !== null) {
            nearestPointAsCurve = dependencyValues.adapterSourceValue;
          }
        }
        return { setValue: { nearestPointAsCurve } };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        curveType: {
          dependencyType: "stateVariable",
          variableName: "curveType"
        },
        fs: {
          dependencyType: "stateVariable",
          variableName: "fs"
        },
        flipFunction: {
          dependencyType: "stateVariable",
          variableName: "flipFunction"
        },
        numDiscretizationPoints: {
          dependencyType: "stateVariable",
          variableName: "numDiscretizationPoints"
        },
        parMin: {
          dependencyType: "stateVariable",
          variableName: "parMin"
        },
        parMax: {
          dependencyType: "stateVariable",
          variableName: "parMax"
        },
        periodic: {
          dependencyType: "stateVariable",
          variableName: "periodic"
        },
        graphXmin: {
          dependencyType: "stateVariable",
          variableName: "graphXmin"
        },
        graphXmax: {
          dependencyType: "stateVariable",
          variableName: "graphXmax"
        },
        graphYmin: {
          dependencyType: "stateVariable",
          variableName: "graphYmin"
        },
        graphYmax: {
          dependencyType: "stateVariable",
          variableName: "graphYmax"
        },
        nearestPointAsCurve: {
          dependencyType: "stateVariable",
          variableName: "nearestPointAsCurve"
        }
      }),
      definition({ dependencyValues }) {
        let nearestPointFunction = null;
        if (dependencyValues.curveType === "function") {
          nearestPointFunction = getNearestPointFunctionCurve({
            dependencyValues,
            numerics
          });
        } else if (["parameterization", "bezier"].includes(
          dependencyValues.curveType
        )) {
          nearestPointFunction = getNearestPointParametrizedCurve({
            dependencyValues,
            numerics
          });
        }
        return {
          setValue: { nearestPoint: nearestPointFunction }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async moveControlVector({
    controlVector,
    controlVectorInds,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let desiredVector = {
      [controlVectorInds + ",0"]: me$3.fromAst(controlVector[0]),
      [controlVectorInds + ",1"]: me$3.fromAst(controlVector[1])
    };
    if (transient) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "controlVectors",
            value: desiredVector,
            sourceDetails: {
              controlVectorMoved: controlVectorInds
            }
          }
        ],
        transient,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "controlVectors",
            value: desiredVector,
            sourceDetails: {
              controlVectorMoved: controlVectorInds
            }
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentId: this.componentIdx
          },
          result: {
            ["controlVector" + controlVectorInds.join("_")]: controlVector
          }
        }
      });
    }
  }
  async moveThroughPoint({
    throughPoint,
    throughPointInd,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let desiredPoint = {
      [throughPointInd + ",0"]: me$3.fromAst(throughPoint[0]),
      [throughPointInd + ",1"]: me$3.fromAst(throughPoint[1])
    };
    if (transient) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "throughPoints",
            value: desiredPoint,
            sourceDetails: { throughPointMoved: throughPointInd }
          }
        ],
        transient,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "throughPoints",
            value: desiredPoint,
            sourceDetails: { throughPointMoved: throughPointInd }
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentId: this.componentIdx
          },
          result: {
            ["throughPoint" + throughPointInd]: throughPoint
          }
        }
      });
    }
  }
  async changeVectorControlDirection({
    direction,
    throughPointInd,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "vectorControlDirections",
          value: { [throughPointInd]: direction }
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  switchCurve() {
  }
  async curveClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async curveFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function getNearestPointFunctionCurve({ dependencyValues, numerics }) {
  let flipFunction = dependencyValues.flipFunction;
  let f2 = dependencyValues.fs[0];
  let numDiscretizationPoints = dependencyValues.numDiscretizationPoints;
  let parMax = dependencyValues.parMax;
  let parMin = dependencyValues.parMin;
  return function({ variables, scales }) {
    let x1 = variables.x1?.evaluate_to_constant();
    let x2 = variables.x2?.evaluate_to_constant();
    let xscale = scales[0];
    let yscale = scales[1];
    let x1AtLeftEndpoint, x2AtLeftEndpoint;
    if (parMin !== -Infinity) {
      x1AtLeftEndpoint = parMin;
      x2AtLeftEndpoint = f2(parMin);
      if (!Number.isFinite(x2AtLeftEndpoint)) {
        let parMinAdjust = parMin * 0.99999 + parMax * 1e-5;
        let x2AtLeftEndpointAdjust = f2(parMinAdjust);
        if (Number.isFinite(x2AtLeftEndpointAdjust)) {
          x1AtLeftEndpoint = parMinAdjust;
          x2AtLeftEndpoint = x2AtLeftEndpointAdjust;
        }
      }
      if (flipFunction) {
        let temp = x1AtLeftEndpoint;
        x1AtLeftEndpoint = x2AtLeftEndpoint;
        x2AtLeftEndpoint = temp;
      }
    }
    let x1AtRightEndpoint, x2AtRightEndpoint;
    if (parMax !== Infinity) {
      x1AtRightEndpoint = parMax;
      x2AtRightEndpoint = f2(parMax);
      if (!Number.isFinite(x2AtRightEndpoint)) {
        let parMaxAdjust = parMin * 1e-5 + parMax * 0.99999;
        let x2AtRightEndpointAdjust = f2(parMaxAdjust);
        if (Number.isFinite(x2AtRightEndpointAdjust)) {
          x1AtRightEndpoint = parMaxAdjust;
          x2AtRightEndpoint = x2AtRightEndpointAdjust;
        }
      }
      if (flipFunction) {
        let temp = x1AtRightEndpoint;
        x1AtRightEndpoint = x2AtRightEndpoint;
        x2AtRightEndpoint = temp;
      }
    }
    if (!dependencyValues.nearestPointAsCurve || !(Number.isFinite(x1) && Number.isFinite(x2))) {
      let x1AsFunction, x2AsFunction;
      if (flipFunction) {
        x2AsFunction = x2;
        x1AsFunction = f2(x2AsFunction);
      } else {
        x1AsFunction = x1;
        x2AsFunction = f2(x1AsFunction);
      }
      if (!(Number.isFinite(x1AsFunction) && Number.isFinite(x2AsFunction))) {
        let distLeft, distRight;
        if (flipFunction) {
          distLeft = Math.abs(parMin - x2);
          distRight = Math.abs(parMax - x2);
        } else {
          distLeft = Math.abs(parMin - x1);
          distRight = Math.abs(parMax - x1);
        }
        if (distLeft < distRight || !Number.isFinite(distRight)) {
          x1AsFunction = x1AtLeftEndpoint;
          x2AsFunction = x2AtLeftEndpoint;
        } else {
          x1AsFunction = x1AtRightEndpoint;
          x2AsFunction = x2AtRightEndpoint;
        }
      }
      if (Number.isFinite(x1AsFunction) && Number.isFinite(x2AsFunction)) {
        let result2 = {
          x1: x1AsFunction,
          x2: x2AsFunction
        };
        if (variables.x3 !== void 0) {
          result2.x3 = 0;
        }
        return result2;
      }
      if (!(Number.isFinite(x1) && Number.isFinite(x2))) {
        return {};
      }
    }
    let minfunc = function(x3) {
      let dx1 = x1;
      let dx2 = x2;
      if (flipFunction) {
        dx1 -= f2(x3);
        dx2 -= x3;
      } else {
        dx1 -= x3;
        dx2 -= f2(x3);
      }
      dx1 /= xscale;
      dx2 /= yscale;
      return dx1 * dx1 + dx2 * dx2;
    };
    let minT = 0;
    let maxT = 1;
    if (parMin !== -Infinity) {
      minT = parMin;
    }
    if (parMax !== Infinity) {
      maxT = parMax;
    }
    let Nsteps = numDiscretizationPoints;
    let delta = (maxT - minT) / Nsteps;
    let tAtMin = minT;
    let fAtMin = minfunc(minT);
    let tIntervalMin = minT;
    let tIntervalMax = minT + delta;
    let fprev, tprev;
    for (let step = 1; step <= Nsteps; step++) {
      let tnew = minT + step * delta;
      let fnew = minfunc(tnew);
      if (Number.isFinite(fnew) && Number.isFinite(fprev) && (fnew < fAtMin || Number.isNaN(fAtMin))) {
        tAtMin = tnew;
        fAtMin = fnew;
        tIntervalMin = tprev;
        if (step === Nsteps) {
          tIntervalMax = tnew;
        } else {
          tIntervalMax = tnew + delta;
        }
      }
      fprev = fnew;
      tprev = tnew;
    }
    let fAtIntervalMax = minfunc(tIntervalMax);
    if (!Number.isFinite(fAtIntervalMax)) {
      tIntervalMax -= delta;
    }
    let result = numerics.fminbr(minfunc, [tIntervalMin, tIntervalMax]);
    tAtMin = result.x;
    let x1AtMin = tAtMin;
    let x2AtMin = f2(x1AtMin);
    let currentD2;
    if (!(result.success && Number.isFinite(x1AtMin) && Number.isFinite(x2AtMin))) {
      currentD2 = Infinity;
    } else {
      if (flipFunction) {
        [x1AtMin, x2AtMin] = [x2AtMin, x1AtMin];
      }
      currentD2 = Math.pow((x1AtMin - x1) / xscale, 2) + Math.pow((x2AtMin - x2) / yscale, 2);
    }
    if (parMin !== -Infinity) {
      let leftEndpointD2 = Math.pow((x1AtLeftEndpoint - x1) / xscale, 2) + Math.pow((x2AtLeftEndpoint - x2) / yscale, 2);
      if (leftEndpointD2 < currentD2) {
        x1AtMin = x1AtLeftEndpoint;
        x2AtMin = x2AtLeftEndpoint;
        currentD2 = leftEndpointD2;
      }
    }
    if (parMax !== Infinity) {
      let rightEndpointD2 = Math.pow((x1AtRightEndpoint - x1) / xscale, 2) + Math.pow((x2AtRightEndpoint - x2) / yscale, 2);
      if (rightEndpointD2 < currentD2) {
        x1AtMin = x1AtRightEndpoint;
        x2AtMin = x2AtRightEndpoint;
        currentD2 = rightEndpointD2;
      }
    }
    result = {
      x1: x1AtMin,
      x2: x2AtMin
    };
    if (variables.x3 !== void 0) {
      result.x3 = 0;
    }
    return result;
  };
}
function getNearestPointParametrizedCurve({ dependencyValues, numerics }) {
  let fs = dependencyValues.fs;
  let parMin = dependencyValues.parMin;
  let parMax = dependencyValues.parMax;
  let numDiscretizationPoints = dependencyValues.numDiscretizationPoints;
  let periodic = dependencyValues.periodic;
  return function({ variables, scales }) {
    let xscale = scales[0];
    let yscale = scales[1];
    if (dependencyValues.fs.length !== 2) {
      return {};
    }
    let x1 = variables.x1?.evaluate_to_constant();
    let x2 = variables.x2?.evaluate_to_constant();
    if (!(Number.isFinite(x1) && Number.isFinite(x2))) {
      return {};
    }
    let minfunc = function(t2) {
      let dx1 = (x1 - fs[0](t2)) / xscale;
      let dx2 = (x2 - fs[1](t2)) / yscale;
      return dx1 * dx1 + dx2 * dx2;
    };
    let minT = parMin;
    let maxT = parMax;
    let Nsteps = numDiscretizationPoints;
    let delta = (maxT - minT) / Nsteps;
    let tAtMin = minT;
    let fAtMin = minfunc(minT);
    let tIntervalMin = minT;
    let tIntervalMax = minT + delta;
    let fprev, tprev;
    for (let step = 1; step <= Nsteps; step++) {
      let tnew = minT + step * delta;
      let fnew = minfunc(tnew);
      if (Number.isFinite(fnew) && Number.isFinite(fprev) && (fnew < fAtMin || Number.isNaN(fAtMin))) {
        tAtMin = tnew;
        fAtMin = fnew;
        tIntervalMin = tprev;
        if (step === Nsteps) {
          tIntervalMax = tnew;
        } else {
          tIntervalMax = tnew + delta;
        }
      }
      fprev = fnew;
      tprev = tnew;
    }
    if (periodic) {
      if (Math.abs(tAtMin - minT) < numerics.eps) {
        tIntervalMin = minT - delta;
      } else if (Math.abs(tAtMin - maxT) < numerics.eps) {
        tIntervalMax = maxT + delta;
      }
    }
    let result = numerics.fminbr(minfunc, [tIntervalMin, tIntervalMax]);
    tAtMin = result.x;
    let x1AtMin = fs[0](tAtMin);
    let x2AtMin = fs[1](tAtMin);
    let fMin = minfunc(tAtMin);
    if (!result.success && Number.isFinite(x1AtMin) && Number.isFinite(x2AtMin)) {
      fMin = Infinity;
    }
    if (Number.isFinite(parMin)) {
      let fParMin = minfunc(parMin);
      if (fParMin < fMin) {
        tAtMin = parMin;
        x1AtMin = fs[0](tAtMin);
        x2AtMin = fs[1](tAtMin);
        fMin = fParMin;
      }
    }
    if (Number.isFinite(parMax)) {
      let fParMax = minfunc(parMax);
      if (fParMax < fMin) {
        tAtMin = parMax;
        x1AtMin = fs[0](tAtMin);
        x2AtMin = fs[1](tAtMin);
        fMin = fParMax;
      }
    }
    result = {
      x1: x1AtMin,
      x2: x2AtMin
    };
    if (variables.x3 !== void 0) {
      result.x3 = 0;
    }
    return result;
  };
}
function calculateControlVectorFromSpline({
  tau,
  eps,
  point1,
  point2,
  point3,
  splineForm
}) {
  let dist2 = function(p12, p22) {
    let dx = p12[0] - p22[0];
    let dy = p12[1] - p22[1];
    return Math.sqrt(dx * dx + dy * dy);
  };
  let p1, p2, p3;
  if (point2) {
    p2 = point2.map((x2) => x2.evaluate_to_constant());
  } else {
    return {
      coordsNumeric: [me$3.fromAst(NaN), me$3.fromAst(NaN)],
      numericEntries: false
    };
  }
  if (point3) {
    p3 = point3.map((x2) => x2.evaluate_to_constant());
    if (point1) {
      p1 = point1.map((x2) => x2.evaluate_to_constant());
    } else {
      p1 = [2 * p2[0] - p3[0], 2 * p2[1] - p3[1]];
    }
  } else {
    if (point1) {
      p1 = point1.map((x2) => x2.evaluate_to_constant());
      p3 = [2 * p2[0] - p1[0], 2 * p2[1] - p1[1]];
    } else {
      return {
        coordsNumeric: [me$3.fromAst(NaN), me$3.fromAst(NaN)],
        numericEntries: false
      };
    }
  }
  let cv = [];
  if (splineForm === "centripetal") {
    let dt0 = dist2(p1, p2);
    let dt1 = dist2(p2, p3);
    dt0 = Math.sqrt(dt0);
    dt1 = Math.sqrt(dt1);
    if (dt1 < eps) {
      dt1 = 1;
    }
    if (dt0 < eps) {
      dt0 = dt1;
    }
    for (let dim = 0; dim < 2; dim++) {
      let t1 = (p2[dim] - p1[dim]) / dt0 - (p3[dim] - p1[dim]) / (dt1 + dt0) + (p3[dim] - p2[dim]) / dt1;
      t1 *= tau * Math.sqrt(dt0 * dt1);
      cv.push(-t1 / 3);
    }
  } else {
    for (let dim = 0; dim < 2; dim++) {
      cv.push(-tau * (p3[dim] - p1[dim]) / 3);
    }
  }
  let coordsNumeric = cv.map((x2) => me$3.fromAst(x2));
  let numericEntries = Number.isFinite(cv[0]) && Number.isFinite(cv[1]);
  return { coordsNumeric, numericEntries };
}
function initCubicPoly(x1, x2, t1, t2) {
  return [x1, t1, -3 * x1 + 3 * x2 - 2 * t1 - t2, 2 * x1 - 2 * x2 + t1 + t2];
}
function addTimePointBezier({ t: t2, ind, ts, ignoreLeft = false }) {
  const eps = 1e-14;
  const one_plus_eps = 1 + eps;
  const one_minus_eps = 1 - eps;
  let foundRight = false;
  if (t2 >= eps) {
    if (t2 <= one_minus_eps) {
      ts.push(ind + t2);
    } else if (t2 < one_plus_eps) {
      ts.push(ind + 1);
      foundRight = true;
    }
  } else if (t2 > -eps && !ignoreLeft) {
    ts.push(ind);
  }
  return foundRight;
}
class EquilibriumCurve extends Curve {
  static componentType = "equilibriumCurve";
  static rendererType = "curve";
  static componentDocs = {
    summary: "An equilibrium curve of a dynamical system, rendered solid if stable or dashed if unstable"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.lineStyle;
    attributes.stable = {
      createComponentOfType: "boolean",
      createStateVariable: "stable",
      defaultValue: true,
      public: true,
      description: "Whether the equilibrium is stable (rendered solid) or unstable (rendered dashed)."
    };
    attributes.switchable = {
      createComponentOfType: "boolean",
      createStateVariable: "switchable",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether the user can toggle the stability of this equilibrium by clicking it."
    };
    return attributes;
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    stateVariableDefinitions.dashed = {
      forRenderer: true,
      returnDependencies: () => ({
        stable: {
          dependencyType: "stateVariable",
          variableName: "stable"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { dashed: !dependencyValues.stable }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "stable",
              desiredValue: !desiredStateVariableValues.dashed
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  async switchCurve({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (await this.stateValues.switchable) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "stable",
            value: !this.stateValues.stable
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            stable: !this.stateValues.stable
          }
        }
      });
    }
  }
}
const DynamicalSystems = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  CobwebPolyline,
  EquilibriumCurve,
  EquilibriumLine,
  EquilibriumPoint,
  ODESystem
}, Symbol.toStringTag, { value: "Module" }));
const _atomDatabase = [
  {
    "Atomic Number": "1",
    Name: "Hydrogen",
    Symbol: "H",
    Group: "1",
    "Atomic Mass": "1.00794",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "1",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "",
    Period: "1",
    "Ionization Energy": "13.5984",
    "Melting Point": "-259.14",
    "Boiling Point": "-252.87",
    "Atomic Radius": "53",
    Density: "0.0899",
    Electronegativity: "2.2",
    "Electron Configuration": "1s^1"
  },
  {
    "Atomic Number": "2",
    Name: "Helium",
    Symbol: "He",
    Group: "18",
    "Atomic Mass": "4.002602",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Noble Gas",
    Period: "1",
    "Ionization Energy": "24.5874",
    "Melting Point": "#N/A",
    "Boiling Point": "-268.93",
    "Atomic Radius": "31",
    Density: "0.1785",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2"
  },
  {
    "Atomic Number": "3",
    Name: "Lithium",
    Symbol: "Li",
    Group: "1",
    "Atomic Mass": "6.941",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "1",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkali Metal",
    Period: "2",
    "Ionization Energy": "5.3917",
    "Melting Point": "180.54",
    "Boiling Point": "1342",
    "Atomic Radius": "167",
    Density: "0.535",
    Electronegativity: "0.98",
    "Electron Configuration": "1s^2 2s^1"
  },
  {
    "Atomic Number": "4",
    Name: "Beryllium",
    Symbol: "Be",
    Group: "2",
    "Atomic Mass": "9.012182",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkaline Earth Metal",
    Period: "2",
    "Ionization Energy": "9.3227",
    "Melting Point": "1287",
    "Boiling Point": "2470",
    "Atomic Radius": "112",
    Density: "1.848",
    Electronegativity: "1.57",
    "Electron Configuration": "1s^2 2s^2"
  },
  {
    "Atomic Number": "5",
    Name: "Boron",
    Symbol: "B",
    Group: "13",
    "Atomic Mass": "10.811",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "2",
    "Ionization Energy": "8.298",
    "Melting Point": "2075",
    "Boiling Point": "4000",
    "Atomic Radius": "87",
    Density: "2.46",
    Electronegativity: "2.04",
    "Electron Configuration": "1s^2 2s^2 2p^1"
  },
  {
    "Atomic Number": "6",
    Name: "Carbon",
    Symbol: "C",
    Group: "14",
    "Atomic Mass": "12.0107",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "",
    Period: "2",
    "Ionization Energy": "11.2603",
    "Melting Point": "3550",
    "Boiling Point": "4027",
    "Atomic Radius": "67",
    Density: "2.26",
    Electronegativity: "2.55",
    "Electron Configuration": "1s^2 2s^2 2p^2"
  },
  {
    "Atomic Number": "7",
    Name: "Nitrogen",
    Symbol: "N",
    Group: "15",
    "Atomic Mass": "14.0067",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "-3",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "",
    Period: "2",
    "Ionization Energy": "14.5341",
    "Melting Point": "-210.1",
    "Boiling Point": "-195.79",
    "Atomic Radius": "56",
    Density: "1.251",
    Electronegativity: "3.04",
    "Electron Configuration": "1s^2 2s^2 2p^3"
  },
  {
    "Atomic Number": "8",
    Name: "Oxygen",
    Symbol: "O",
    Group: "16",
    "Atomic Mass": "15.9994",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "-2",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Chalcogen",
    Period: "2",
    "Ionization Energy": "13.6181",
    "Melting Point": "-218.3",
    "Boiling Point": "-182.9",
    "Atomic Radius": "48",
    Density: "1.429",
    Electronegativity: "3.44",
    "Electron Configuration": "1s^2 2s^2 2p^4"
  },
  {
    "Atomic Number": "9",
    Name: "Fluorine",
    Symbol: "F",
    Group: "17",
    "Atomic Mass": "18.9984032",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "-1",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Halogen",
    Period: "2",
    "Ionization Energy": "17.4228",
    "Melting Point": "-219.6",
    "Boiling Point": "-188.12",
    "Atomic Radius": "42",
    Density: "1.696",
    Electronegativity: "3.98",
    "Electron Configuration": "1s^2 2s^2 2p^5"
  },
  {
    "Atomic Number": "10",
    Name: "Neon",
    Symbol: "Ne",
    Group: "18",
    "Atomic Mass": "20.1797",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Noble Gas",
    Period: "2",
    "Ionization Energy": "21.5645",
    "Melting Point": "-248.59",
    "Boiling Point": "-246.08",
    "Atomic Radius": "38",
    Density: "0.9",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6"
  },
  {
    "Atomic Number": "11",
    Name: "Sodium",
    Symbol: "Na",
    Group: "1",
    "Atomic Mass": "22.98977",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "1",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkali Metal",
    Period: "3",
    "Ionization Energy": "5.1391",
    "Melting Point": "97.72",
    "Boiling Point": "883",
    "Atomic Radius": "190",
    Density: "0.968",
    Electronegativity: "0.93",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^1"
  },
  {
    "Atomic Number": "12",
    Name: "Magnesium",
    Symbol: "Mg",
    Group: "2",
    "Atomic Mass": "24.305",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "2",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkaline Earth Metal",
    Period: "3",
    "Ionization Energy": "7.6462",
    "Melting Point": "650",
    "Boiling Point": "1090",
    "Atomic Radius": "145",
    Density: "1.738",
    Electronegativity: "1.31",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2"
  },
  {
    "Atomic Number": "13",
    Name: "Aluminum",
    Symbol: "Al",
    Group: "13",
    "Atomic Mass": "26.981538",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "3",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "3",
    "Ionization Energy": "5.9858",
    "Melting Point": "660.32",
    "Boiling Point": "2519",
    "Atomic Radius": "118",
    Density: "2.7",
    Electronegativity: "1.61",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^1"
  },
  {
    "Atomic Number": "14",
    Name: "Silicon",
    Symbol: "Si",
    Group: "14",
    "Atomic Mass": "28.0855",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "3",
    "Ionization Energy": "8.1517",
    "Melting Point": "1414",
    "Boiling Point": "2900",
    "Atomic Radius": "111",
    Density: "2.33",
    Electronegativity: "1.9",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^2"
  },
  {
    "Atomic Number": "15",
    Name: "Phosphorus",
    Symbol: "P",
    Group: "15",
    "Atomic Mass": "30.97361",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "-3",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "",
    Period: "3",
    "Ionization Energy": "10.4867",
    "Melting Point": "44.2",
    "Boiling Point": "280.5",
    "Atomic Radius": "98",
    Density: "1.823",
    Electronegativity: "2.19",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^3"
  },
  {
    "Atomic Number": "16",
    Name: "Sulfur",
    Symbol: "S",
    Group: "16",
    "Atomic Mass": "32.065",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "-2",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Chalcogen",
    Period: "3",
    "Ionization Energy": "10.36",
    "Melting Point": "115.21",
    "Boiling Point": "444.72",
    "Atomic Radius": "88",
    Density: "1.96",
    Electronegativity: "2.58",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^4"
  },
  {
    "Atomic Number": "17",
    Name: "Chlorine",
    Symbol: "Cl",
    Group: "17",
    "Atomic Mass": "35.453",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "-1",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Halogen",
    Period: "3",
    "Ionization Energy": "12.9676",
    "Melting Point": "-101.5",
    "Boiling Point": "-34.04",
    "Atomic Radius": "79",
    Density: "3.214",
    Electronegativity: "3.16",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^5"
  },
  {
    "Atomic Number": "18",
    Name: "Argon",
    Symbol: "Ar",
    Group: "18",
    "Atomic Mass": "39.948",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Noble Gas",
    Period: "3",
    "Ionization Energy": "15.7596",
    "Melting Point": "-189.3",
    "Boiling Point": "-185.8",
    "Atomic Radius": "71",
    Density: "1.784",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6"
  },
  {
    "Atomic Number": "19",
    Name: "Potassium",
    Symbol: "K",
    Group: "1",
    "Atomic Mass": "39.0983",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "1",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkali Metal",
    Period: "4",
    "Ionization Energy": "4.3407",
    "Melting Point": "63.38",
    "Boiling Point": "759",
    "Atomic Radius": "243",
    Density: "0.856",
    Electronegativity: "0.82",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 4s^1"
  },
  {
    "Atomic Number": "20",
    Name: "Calcium",
    Symbol: "Ca",
    Group: "2",
    "Atomic Mass": "40.078",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "2",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkaline Earth Metal",
    Period: "4",
    "Ionization Energy": "6.1132",
    "Melting Point": "842",
    "Boiling Point": "1484",
    "Atomic Radius": "194",
    Density: "1.55",
    Electronegativity: "1",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 4s^2"
  },
  {
    "Atomic Number": "21",
    Name: "Scandium",
    Symbol: "Sc",
    Group: "3",
    "Atomic Mass": "44.95591",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "6.5615",
    "Melting Point": "1541",
    "Boiling Point": "2830",
    "Atomic Radius": "184",
    Density: "2.985",
    Electronegativity: "1.36",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^1 4s^2"
  },
  {
    "Atomic Number": "22",
    Name: "Titanium",
    Symbol: "Ti",
    Group: "4",
    "Atomic Mass": "47.867",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "6.8281",
    "Melting Point": "1668",
    "Boiling Point": "3287",
    "Atomic Radius": "176",
    Density: "4.507",
    Electronegativity: "1.54",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^2 4s^2"
  },
  {
    "Atomic Number": "23",
    Name: "Vanadium",
    Symbol: "V",
    Group: "5",
    "Atomic Mass": "50.9415",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "6.7462",
    "Melting Point": "1910",
    "Boiling Point": "3407",
    "Atomic Radius": "171",
    Density: "6.11",
    Electronegativity: "1.63",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^3 4s^2"
  },
  {
    "Atomic Number": "24",
    Name: "Chromium",
    Symbol: "Cr",
    Group: "6",
    "Atomic Mass": "51.9961",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "6.7665",
    "Melting Point": "1907",
    "Boiling Point": "2671",
    "Atomic Radius": "166",
    Density: "7.14",
    Electronegativity: "1.66",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^5 4s^1"
  },
  {
    "Atomic Number": "25",
    Name: "Manganese",
    Symbol: "Mn",
    Group: "7",
    "Atomic Mass": "54.938049",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "7.434",
    "Melting Point": "1246",
    "Boiling Point": "2061",
    "Atomic Radius": "161",
    Density: "7.47",
    Electronegativity: "1.55",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^5 4s^2"
  },
  {
    "Atomic Number": "26",
    Name: "Iron",
    Symbol: "Fe",
    Group: "8",
    "Atomic Mass": "55.845",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "7.9024",
    "Melting Point": "1538",
    "Boiling Point": "2861",
    "Atomic Radius": "156",
    Density: "7.874",
    Electronegativity: "1.83",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^6 4s^2"
  },
  {
    "Atomic Number": "27",
    Name: "Cobalt",
    Symbol: "Co",
    Group: "9",
    "Atomic Mass": "58.9332",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "7.881",
    "Melting Point": "1495",
    "Boiling Point": "2927",
    "Atomic Radius": "152",
    Density: "8.9",
    Electronegativity: "1.88",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^7 4s^2"
  },
  {
    "Atomic Number": "28",
    Name: "Nickel",
    Symbol: "Ni",
    Group: "10",
    "Atomic Mass": "58.6934",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "7.6398",
    "Melting Point": "1455",
    "Boiling Point": "2913",
    "Atomic Radius": "149",
    Density: "8.908",
    Electronegativity: "1.91",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^8 4s^2"
  },
  {
    "Atomic Number": "29",
    Name: "Copper",
    Symbol: "Cu",
    Group: "11",
    "Atomic Mass": "63.546",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "7.7264",
    "Melting Point": "1084.62",
    "Boiling Point": "2927",
    "Atomic Radius": "145",
    Density: "8.92",
    Electronegativity: "1.9",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^1"
  },
  {
    "Atomic Number": "30",
    Name: "Zinc",
    Symbol: "Zn",
    Group: "12",
    "Atomic Mass": "65.409",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "2",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "4",
    "Ionization Energy": "9.3942",
    "Melting Point": "419.53",
    "Boiling Point": "907",
    "Atomic Radius": "142",
    Density: "7.14",
    Electronegativity: "1.65",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2"
  },
  {
    "Atomic Number": "31",
    Name: "Gallium",
    Symbol: "Ga",
    Group: "13",
    "Atomic Mass": "69.723",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "4",
    "Ionization Energy": "5.9993",
    "Melting Point": "29.76",
    "Boiling Point": "2204",
    "Atomic Radius": "136",
    Density: "5.904",
    Electronegativity: "1.81",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^1"
  },
  {
    "Atomic Number": "32",
    Name: "Germanium",
    Symbol: "Ge",
    Group: "14",
    "Atomic Mass": "72.64",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "4",
    "Ionization Energy": "7.8994",
    "Melting Point": "938.3",
    "Boiling Point": "2820",
    "Atomic Radius": "125",
    Density: "5.323",
    Electronegativity: "2.01",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^2"
  },
  {
    "Atomic Number": "33",
    Name: "Arsenic",
    Symbol: "As",
    Group: "15",
    "Atomic Mass": "74.9216",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "-3",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "4",
    "Ionization Energy": "9.7886",
    "Melting Point": "817",
    "Boiling Point": "614",
    "Atomic Radius": "114",
    Density: "5.727",
    Electronegativity: "2.18",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^3"
  },
  {
    "Atomic Number": "34",
    Name: "Selenium",
    Symbol: "Se",
    Group: "16",
    "Atomic Mass": "78.96",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "-2",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Chalcogen",
    Period: "4",
    "Ionization Energy": "9.7524",
    "Melting Point": "221",
    "Boiling Point": "685",
    "Atomic Radius": "103",
    Density: "4.819",
    Electronegativity: "2.55",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^4"
  },
  {
    "Atomic Number": "35",
    Name: "Bromine",
    Symbol: "Br",
    Group: "17",
    "Atomic Mass": "79.904",
    "Phase at STP": "Liquid",
    "Charge of Common Ion": "-1",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Halogen",
    Period: "4",
    "Ionization Energy": "11.8138",
    "Melting Point": "-7.3",
    "Boiling Point": "59",
    "Atomic Radius": "94",
    Density: "3.12",
    Electronegativity: "2.96",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^5"
  },
  {
    "Atomic Number": "36",
    Name: "Krypton",
    Symbol: "Kr",
    Group: "18",
    "Atomic Mass": "83.798",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Noble Gas",
    Period: "4",
    "Ionization Energy": "13.9996",
    "Melting Point": "-157.36",
    "Boiling Point": "-153.22",
    "Atomic Radius": "88",
    Density: "3.75",
    Electronegativity: "3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6"
  },
  {
    "Atomic Number": "37",
    Name: "Rubidium",
    Symbol: "Rb",
    Group: "1",
    "Atomic Mass": "85.4678",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "1",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkali Metal",
    Period: "5",
    "Ionization Energy": "4.1771",
    "Melting Point": "39.31",
    "Boiling Point": "688",
    "Atomic Radius": "265",
    Density: "1.532",
    Electronegativity: "0.82",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 5s^1"
  },
  {
    "Atomic Number": "38",
    Name: "Strontium",
    Symbol: "Sr",
    Group: "2",
    "Atomic Mass": "87.62",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "2",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkaline Earth Metal",
    Period: "5",
    "Ionization Energy": "5.6949",
    "Melting Point": "777",
    "Boiling Point": "1382",
    "Atomic Radius": "219",
    Density: "2.63",
    Electronegativity: "0.95",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 5s^2"
  },
  {
    "Atomic Number": "39",
    Name: "Yttrium",
    Symbol: "Y",
    Group: "3",
    "Atomic Mass": "88.90585",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "6.2173",
    "Melting Point": "1526",
    "Boiling Point": "3345",
    "Atomic Radius": "212",
    Density: "4.472",
    Electronegativity: "1.22",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^1 5s^2"
  },
  {
    "Atomic Number": "40",
    Name: "Zirconium",
    Symbol: "Zr",
    Group: "4",
    "Atomic Mass": "91.224",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "6.6339",
    "Melting Point": "1855",
    "Boiling Point": "4409",
    "Atomic Radius": "206",
    Density: "6.511",
    Electronegativity: "1.33",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^2 5s^2"
  },
  {
    "Atomic Number": "41",
    Name: "Niobium",
    Symbol: "Nb",
    Group: "5",
    "Atomic Mass": "92.90638",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "6.7589",
    "Melting Point": "2477",
    "Boiling Point": "4744",
    "Atomic Radius": "198",
    Density: "8.57",
    Electronegativity: "1.6",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^4 5s^1"
  },
  {
    "Atomic Number": "42",
    Name: "Molybdenum",
    Symbol: "Mo",
    Group: "6",
    "Atomic Mass": "95.94",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "7.0924",
    "Melting Point": "2623",
    "Boiling Point": "4639",
    "Atomic Radius": "190",
    Density: "10.28",
    Electronegativity: "2.16",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^5 5s^1"
  },
  {
    "Atomic Number": "43",
    Name: "Technetium",
    Symbol: "Tc",
    Group: "7",
    "Atomic Mass": "98",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "7.28",
    "Melting Point": "2157",
    "Boiling Point": "4265",
    "Atomic Radius": "183",
    Density: "11.5",
    Electronegativity: "1.9",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^5 5s^2"
  },
  {
    "Atomic Number": "44",
    Name: "Ruthenium",
    Symbol: "Ru",
    Group: "8",
    "Atomic Mass": "101.07",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "7.3605",
    "Melting Point": "2334",
    "Boiling Point": "4150",
    "Atomic Radius": "178",
    Density: "12.37",
    Electronegativity: "2.2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^7 5s^1"
  },
  {
    "Atomic Number": "45",
    Name: "Rhodium",
    Symbol: "Rh",
    Group: "9",
    "Atomic Mass": "102.9055",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "7.4589",
    "Melting Point": "1964",
    "Boiling Point": "3695",
    "Atomic Radius": "173",
    Density: "12.45",
    Electronegativity: "2.28",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^8 5s^1"
  },
  {
    "Atomic Number": "46",
    Name: "Palladium",
    Symbol: "Pd",
    Group: "10",
    "Atomic Mass": "106.42",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "8.3369",
    "Melting Point": "1554.9",
    "Boiling Point": "2963",
    "Atomic Radius": "169",
    Density: "12.023",
    Electronegativity: "2.2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10"
  },
  {
    "Atomic Number": "47",
    Name: "Silver",
    Symbol: "Ag",
    Group: "11",
    "Atomic Mass": "107.8682",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "7.5762",
    "Melting Point": "961.78",
    "Boiling Point": "2162",
    "Atomic Radius": "165",
    Density: "10.49",
    Electronegativity: "1.93",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^1"
  },
  {
    "Atomic Number": "48",
    Name: "Cadmium",
    Symbol: "Cd",
    Group: "12",
    "Atomic Mass": "112.411",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "5",
    "Ionization Energy": "8.9938",
    "Melting Point": "321.07",
    "Boiling Point": "767",
    "Atomic Radius": "161",
    Density: "8.65",
    Electronegativity: "1.69",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2"
  },
  {
    "Atomic Number": "49",
    Name: "Indium",
    Symbol: "In",
    Group: "13",
    "Atomic Mass": "114.818",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "5",
    "Ionization Energy": "5.7864",
    "Melting Point": "156.6",
    "Boiling Point": "2072",
    "Atomic Radius": "156",
    Density: "7.31",
    Electronegativity: "1.78",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^1"
  },
  {
    "Atomic Number": "50",
    Name: "Tin",
    Symbol: "Sn",
    Group: "14",
    "Atomic Mass": "118.71",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "5",
    "Ionization Energy": "7.3439",
    "Melting Point": "231.93",
    "Boiling Point": "2602",
    "Atomic Radius": "145",
    Density: "7.31",
    Electronegativity: "1.96",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^2"
  },
  {
    "Atomic Number": "51",
    Name: "Antimony",
    Symbol: "Sb",
    Group: "15",
    "Atomic Mass": "121.76",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "5",
    "Ionization Energy": "8.6084",
    "Melting Point": "630.63",
    "Boiling Point": "1587",
    "Atomic Radius": "133",
    Density: "6.697",
    Electronegativity: "2.05",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^3"
  },
  {
    "Atomic Number": "52",
    Name: "Tellurium",
    Symbol: "Te",
    Group: "16",
    "Atomic Mass": "127.6",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "5",
    "Ionization Energy": "9.0096",
    "Melting Point": "449.51",
    "Boiling Point": "988",
    "Atomic Radius": "123",
    Density: "6.24",
    Electronegativity: "2.1",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^4"
  },
  {
    "Atomic Number": "53",
    Name: "Iodine",
    Symbol: "I",
    Group: "17",
    "Atomic Mass": "126.90447",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "-1",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Halogen",
    Period: "5",
    "Ionization Energy": "10.4513",
    "Melting Point": "113.7",
    "Boiling Point": "184.3",
    "Atomic Radius": "115",
    Density: "4.94",
    Electronegativity: "2.66",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^5"
  },
  {
    "Atomic Number": "54",
    Name: "Xenon",
    Symbol: "Xe",
    Group: "18",
    "Atomic Mass": "131.293",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Noble Gas",
    Period: "5",
    "Ionization Energy": "12.1298",
    "Melting Point": "-111.8",
    "Boiling Point": "-108",
    "Atomic Radius": "108",
    Density: "5.9",
    Electronegativity: "2.6",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6"
  },
  {
    "Atomic Number": "55",
    Name: "Cesium",
    Symbol: "Cs",
    Group: "1",
    "Atomic Mass": "132.90545",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "1",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkali Metal",
    Period: "6",
    "Ionization Energy": "3.8939",
    "Melting Point": "28.44",
    "Boiling Point": "671",
    "Atomic Radius": "298",
    Density: "1.879",
    Electronegativity: "0.79",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 6s^1"
  },
  {
    "Atomic Number": "56",
    Name: "Barium",
    Symbol: "Ba",
    Group: "2",
    "Atomic Mass": "137.327",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "2",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkaline Earth Metal",
    Period: "6",
    "Ionization Energy": "5.2117",
    "Melting Point": "727",
    "Boiling Point": "1870",
    "Atomic Radius": "253",
    Density: "3.51",
    Electronegativity: "0.89",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 6s^2"
  },
  {
    "Atomic Number": "57",
    Name: "Lanthanum",
    Symbol: "La",
    Group: "3",
    "Atomic Mass": "138.9055",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.5769",
    "Melting Point": "920",
    "Boiling Point": "3464",
    "Atomic Radius": "#N/A",
    Density: "6.146",
    Electronegativity: "1.1",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 5d^1 6s^2"
  },
  {
    "Atomic Number": "58",
    Name: "Cerium",
    Symbol: "Ce",
    Group: "101",
    "Atomic Mass": "140.116",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.5387",
    "Melting Point": "798",
    "Boiling Point": "3360",
    "Atomic Radius": "#N/A",
    Density: "6.689",
    Electronegativity: "1.12",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^1 5d^1 6s^2"
  },
  {
    "Atomic Number": "59",
    Name: "Praseodymium",
    Symbol: "Pr",
    Group: "101",
    "Atomic Mass": "140.90765",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.473",
    "Melting Point": "931",
    "Boiling Point": "3290",
    "Atomic Radius": "247",
    Density: "6.64",
    Electronegativity: "1.13",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^3 6s^2"
  },
  {
    "Atomic Number": "60",
    Name: "Neodymium",
    Symbol: "Nd",
    Group: "101",
    "Atomic Mass": "144.24",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.525",
    "Melting Point": "1021",
    "Boiling Point": "3100",
    "Atomic Radius": "206",
    Density: "7.01",
    Electronegativity: "1.14",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^4 6s^2"
  },
  {
    "Atomic Number": "61",
    Name: "Promethium",
    Symbol: "Pm",
    Group: "101",
    "Atomic Mass": "145",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.582",
    "Melting Point": "1100",
    "Boiling Point": "3000",
    "Atomic Radius": "205",
    Density: "7.264",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^5 6s^2"
  },
  {
    "Atomic Number": "62",
    Name: "Samarium",
    Symbol: "Sm",
    Group: "101",
    "Atomic Mass": "150.36",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.6437",
    "Melting Point": "1072",
    "Boiling Point": "1803",
    "Atomic Radius": "238",
    Density: "7.353",
    Electronegativity: "1.17",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^6 6s^2"
  },
  {
    "Atomic Number": "63",
    Name: "Europium",
    Symbol: "Eu",
    Group: "101",
    "Atomic Mass": "151.964",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.6704",
    "Melting Point": "822",
    "Boiling Point": "1527",
    "Atomic Radius": "231",
    Density: "5.244",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^7 6s^2"
  },
  {
    "Atomic Number": "64",
    Name: "Gadolinium",
    Symbol: "Gd",
    Group: "101",
    "Atomic Mass": "157.25",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "6.1498",
    "Melting Point": "1313",
    "Boiling Point": "3250",
    "Atomic Radius": "233",
    Density: "7.901",
    Electronegativity: "1.2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^7 5d^1 6s^2"
  },
  {
    "Atomic Number": "65",
    Name: "Terbium",
    Symbol: "Tb",
    Group: "101",
    "Atomic Mass": "158.92534",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.8638",
    "Melting Point": "1356",
    "Boiling Point": "3230",
    "Atomic Radius": "225",
    Density: "8.219",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^9 6s^2"
  },
  {
    "Atomic Number": "66",
    Name: "Dysprosium",
    Symbol: "Dy",
    Group: "101",
    "Atomic Mass": "162.5",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.9389",
    "Melting Point": "1412",
    "Boiling Point": "2567",
    "Atomic Radius": "228",
    Density: "8.551",
    Electronegativity: "1.22",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^10 6s^2"
  },
  {
    "Atomic Number": "67",
    Name: "Holmium",
    Symbol: "Ho",
    Group: "101",
    "Atomic Mass": "164.93032",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "6.0215",
    "Melting Point": "1474",
    "Boiling Point": "2700",
    "Atomic Radius": "#N/A",
    Density: "8.795",
    Electronegativity: "1.23",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^11 6s^2"
  },
  {
    "Atomic Number": "68",
    Name: "Erbium",
    Symbol: "Er",
    Group: "101",
    "Atomic Mass": "167.259",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "6.1077",
    "Melting Point": "1497",
    "Boiling Point": "2868",
    "Atomic Radius": "226",
    Density: "9.066",
    Electronegativity: "1.24",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^12 6s^2"
  },
  {
    "Atomic Number": "69",
    Name: "Thulium",
    Symbol: "Tm",
    Group: "101",
    "Atomic Mass": "168.93421",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "6.1843",
    "Melting Point": "1545",
    "Boiling Point": "1950",
    "Atomic Radius": "222",
    Density: "9.321",
    Electronegativity: "1.25",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^13 6s^2"
  },
  {
    "Atomic Number": "70",
    Name: "Ytterbium",
    Symbol: "Yb",
    Group: "101",
    "Atomic Mass": "173.04",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "6.2542",
    "Melting Point": "819",
    "Boiling Point": "1196",
    "Atomic Radius": "222",
    Density: "6.57",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 6s^2"
  },
  {
    "Atomic Number": "71",
    Name: "Lutetium",
    Symbol: "Lu",
    Group: "101",
    "Atomic Mass": "174.967",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "6",
    "Ionization Energy": "5.4259",
    "Melting Point": "1663",
    "Boiling Point": "3402",
    "Atomic Radius": "217",
    Density: "9.841",
    Electronegativity: "1.27",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^1 6s^2"
  },
  {
    "Atomic Number": "72",
    Name: "Hafnium",
    Symbol: "Hf",
    Group: "4",
    "Atomic Mass": "178.49",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "6.8251",
    "Melting Point": "2233",
    "Boiling Point": "4603",
    "Atomic Radius": "208",
    Density: "13.31",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^2 6s^2"
  },
  {
    "Atomic Number": "73",
    Name: "Tantalum",
    Symbol: "Ta",
    Group: "5",
    "Atomic Mass": "180.9479",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "7.5496",
    "Melting Point": "3017",
    "Boiling Point": "5458",
    "Atomic Radius": "200",
    Density: "16.65",
    Electronegativity: "1.5",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^3 6s^2"
  },
  {
    "Atomic Number": "74",
    Name: "Tungsten",
    Symbol: "W",
    Group: "6",
    "Atomic Mass": "183.84",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "7.864",
    "Melting Point": "3422",
    "Boiling Point": "5555",
    "Atomic Radius": "193",
    Density: "19.25",
    Electronegativity: "2.36",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^4 6s^2"
  },
  {
    "Atomic Number": "75",
    Name: "Rhenium",
    Symbol: "Re",
    Group: "7",
    "Atomic Mass": "186.207",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "7.8335",
    "Melting Point": "3186",
    "Boiling Point": "5596",
    "Atomic Radius": "188",
    Density: "21.02",
    Electronegativity: "1.9",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^5 6s^2"
  },
  {
    "Atomic Number": "76",
    Name: "Osmium",
    Symbol: "Os",
    Group: "8",
    "Atomic Mass": "190.23",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "8.4382",
    "Melting Point": "3033",
    "Boiling Point": "5012",
    "Atomic Radius": "185",
    Density: "22.61",
    Electronegativity: "2.2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^6 6s^2"
  },
  {
    "Atomic Number": "77",
    Name: "Iridium",
    Symbol: "Ir",
    Group: "9",
    "Atomic Mass": "192.217",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "8.967",
    "Melting Point": "2466",
    "Boiling Point": "4428",
    "Atomic Radius": "180",
    Density: "22.65",
    Electronegativity: "2.2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^7 6s^2"
  },
  {
    "Atomic Number": "78",
    Name: "Platinum",
    Symbol: "Pt",
    Group: "10",
    "Atomic Mass": "195.078",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "8.9588",
    "Melting Point": "1768.3",
    "Boiling Point": "3825",
    "Atomic Radius": "177",
    Density: "21.09",
    Electronegativity: "2.28",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^9 6s^1"
  },
  {
    "Atomic Number": "79",
    Name: "Gold",
    Symbol: "Au",
    Group: "11",
    "Atomic Mass": "196.96655",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "9.2255",
    "Melting Point": "1064.18",
    "Boiling Point": "2856",
    "Atomic Radius": "174",
    Density: "19.3",
    Electronegativity: "2.54",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^1"
  },
  {
    "Atomic Number": "80",
    Name: "Mercury",
    Symbol: "Hg",
    Group: "12",
    "Atomic Mass": "200.59",
    "Phase at STP": "Liquid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "6",
    "Ionization Energy": "10.4375",
    "Melting Point": "-38.83",
    "Boiling Point": "356.73",
    "Atomic Radius": "171",
    Density: "13.534",
    Electronegativity: "2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2"
  },
  {
    "Atomic Number": "81",
    Name: "Thallium",
    Symbol: "Tl",
    Group: "13",
    "Atomic Mass": "204.3833",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "6",
    "Ionization Energy": "6.1082",
    "Melting Point": "304",
    "Boiling Point": "1473",
    "Atomic Radius": "156",
    Density: "11.85",
    Electronegativity: "1.62",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^1"
  },
  {
    "Atomic Number": "82",
    Name: "Lead",
    Symbol: "Pb",
    Group: "14",
    "Atomic Mass": "207.2",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "6",
    "Ionization Energy": "7.4167",
    "Melting Point": "327.46",
    "Boiling Point": "1749",
    "Atomic Radius": "154",
    Density: "11.34",
    Electronegativity: "2.33",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^2"
  },
  {
    "Atomic Number": "83",
    Name: "Bismuth",
    Symbol: "Bi",
    Group: "15",
    "Atomic Mass": "208.98038",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "6",
    "Ionization Energy": "7.2855",
    "Melting Point": "271.3",
    "Boiling Point": "1564",
    "Atomic Radius": "143",
    Density: "9.78",
    Electronegativity: "2.02",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^3"
  },
  {
    "Atomic Number": "84",
    Name: "Polonium",
    Symbol: "Po",
    Group: "16",
    "Atomic Mass": "209",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "6",
    "Ionization Energy": "8.414",
    "Melting Point": "254",
    "Boiling Point": "962",
    "Atomic Radius": "135",
    Density: "9.196",
    Electronegativity: "2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^4"
  },
  {
    "Atomic Number": "85",
    Name: "Astatine",
    Symbol: "At",
    Group: "17",
    "Atomic Mass": "210",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metalloid",
    "Group Name": "",
    Period: "6",
    "Ionization Energy": "#N/A",
    "Melting Point": "302",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "2.2",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^5"
  },
  {
    "Atomic Number": "86",
    Name: "Radon",
    Symbol: "Rn",
    Group: "18",
    "Atomic Mass": "222",
    "Phase at STP": "Gas",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "Noble Gas",
    Period: "6",
    "Ionization Energy": "10.7485",
    "Melting Point": "-71",
    "Boiling Point": "-61.7",
    "Atomic Radius": "120",
    Density: "9.73",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6"
  },
  {
    "Atomic Number": "87",
    Name: "Francium",
    Symbol: "Fr",
    Group: "1",
    "Atomic Mass": "223",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "1",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkali Metal",
    Period: "7",
    "Ionization Energy": "4.0727",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "0.7",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 7s^1"
  },
  {
    "Atomic Number": "88",
    Name: "Radium",
    Symbol: "Ra",
    Group: "2",
    "Atomic Mass": "226",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "2",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Alkaline Earth Metal",
    Period: "7",
    "Ionization Energy": "5.2784",
    "Melting Point": "700",
    "Boiling Point": "1737",
    "Atomic Radius": "#N/A",
    Density: "5",
    Electronegativity: "0.9",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 7s^2"
  },
  {
    "Atomic Number": "89",
    Name: "Actinium",
    Symbol: "Ac",
    Group: "3",
    "Atomic Mass": "227",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "5.17",
    "Melting Point": "1050",
    "Boiling Point": "3200",
    "Atomic Radius": "#N/A",
    Density: "10.07",
    Electronegativity: "1.1",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 6d^1 7s^2"
  },
  {
    "Atomic Number": "90",
    Name: "Thorium",
    Symbol: "Th",
    Group: "102",
    "Atomic Mass": "232.0381",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.3067",
    "Melting Point": "1750",
    "Boiling Point": "4820",
    "Atomic Radius": "#N/A",
    Density: "11.724",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 6d^2 7s^2"
  },
  {
    "Atomic Number": "91",
    Name: "Protactinium",
    Symbol: "Pa",
    Group: "102",
    "Atomic Mass": "231.03588",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "5.89",
    "Melting Point": "1572",
    "Boiling Point": "4000",
    "Atomic Radius": "#N/A",
    Density: "15.37",
    Electronegativity: "1.5",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^2 6d^1 7s^2"
  },
  {
    "Atomic Number": "92",
    Name: "Uranium",
    Symbol: "U",
    Group: "102",
    "Atomic Mass": "238.02891",
    "Phase at STP": "Solid",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.1941",
    "Melting Point": "1135",
    "Boiling Point": "3927",
    "Atomic Radius": "#N/A",
    Density: "19.05",
    Electronegativity: "1.38",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^3 6d^1 7s^2"
  },
  {
    "Atomic Number": "93",
    Name: "Neptunium",
    Symbol: "Np",
    Group: "102",
    "Atomic Mass": "237",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.2657",
    "Melting Point": "644",
    "Boiling Point": "4000",
    "Atomic Radius": "#N/A",
    Density: "20.45",
    Electronegativity: "1.36",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^4 6d^1 7s^2"
  },
  {
    "Atomic Number": "94",
    Name: "Plutonium",
    Symbol: "Pu",
    Group: "102",
    "Atomic Mass": "244",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.026",
    "Melting Point": "640",
    "Boiling Point": "3230",
    "Atomic Radius": "#N/A",
    Density: "19.816",
    Electronegativity: "1.28",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^6 7s^2"
  },
  {
    "Atomic Number": "95",
    Name: "Americium",
    Symbol: "Am",
    Group: "102",
    "Atomic Mass": "243",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "5.9738",
    "Melting Point": "1176",
    "Boiling Point": "2011",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^7 7s^2"
  },
  {
    "Atomic Number": "96",
    Name: "Curium",
    Symbol: "Cm",
    Group: "102",
    "Atomic Mass": "247",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "5.9914",
    "Melting Point": "1345",
    "Boiling Point": "3110",
    "Atomic Radius": "#N/A",
    Density: "13.51",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^7 6d^1 7s^2"
  },
  {
    "Atomic Number": "97",
    Name: "Berkelium",
    Symbol: "Bk",
    Group: "102",
    "Atomic Mass": "247",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.1979",
    "Melting Point": "1050",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "14.78",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^9 7s^2"
  },
  {
    "Atomic Number": "98",
    Name: "Californium",
    Symbol: "Cf",
    Group: "102",
    "Atomic Mass": "251",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.2817",
    "Melting Point": "900",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "15.1",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^10 7s^2"
  },
  {
    "Atomic Number": "99",
    Name: "Einsteinium",
    Symbol: "Es",
    Group: "102",
    "Atomic Mass": "252",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.42",
    "Melting Point": "860",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^11 7s^2"
  },
  {
    "Atomic Number": "100",
    Name: "Fermium",
    Symbol: "Fm",
    Group: "102",
    "Atomic Mass": "257",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.5",
    "Melting Point": "1527",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^12 7s^2"
  },
  {
    "Atomic Number": "101",
    Name: "Mendelevium",
    Symbol: "Md",
    Group: "102",
    "Atomic Mass": "258",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.58",
    "Melting Point": "827",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^13 7s^2"
  },
  {
    "Atomic Number": "102",
    Name: "Nobelium",
    Symbol: "No",
    Group: "102",
    "Atomic Mass": "259",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "6.65",
    "Melting Point": "827",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "1.3",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 7s^2"
  },
  {
    "Atomic Number": "103",
    Name: "Lawrencium",
    Symbol: "Lr",
    Group: "102",
    "Atomic Mass": "262",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Rare Earth Metal",
    Period: "7",
    "Ionization Energy": "4.9",
    "Melting Point": "1627",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 7s^2 7p^1"
  },
  {
    "Atomic Number": "104",
    Name: "Rutherfordium",
    Symbol: "Rf",
    Group: "4",
    "Atomic Mass": "261",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "7",
    "Ionization Energy": "6",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^2 7s^2"
  },
  {
    "Atomic Number": "105",
    Name: "Dubnium",
    Symbol: "Db",
    Group: "5",
    "Atomic Mass": "262",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^3 7s^2"
  },
  {
    "Atomic Number": "106",
    Name: "Seaborgium",
    Symbol: "Sg",
    Group: "6",
    "Atomic Mass": "266",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^4 7s^2"
  },
  {
    "Atomic Number": "107",
    Name: "Bohrium",
    Symbol: "Bh",
    Group: "7",
    "Atomic Mass": "264",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^5 7s^2"
  },
  {
    "Atomic Number": "108",
    Name: "Hassium",
    Symbol: "Hs",
    Group: "8",
    "Atomic Mass": "277",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "Transition Metal",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^6 7s^2"
  },
  {
    "Atomic Number": "109",
    Name: "Meitnerium",
    Symbol: "Mt",
    Group: "9",
    "Atomic Mass": "268",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^7 7s^2"
  },
  {
    "Atomic Number": "110",
    Name: "Darmstadtium",
    Symbol: "Ds",
    Group: "10",
    "Atomic Mass": "281",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^8 7s^2"
  },
  {
    "Atomic Number": "111",
    Name: "Roentgenium",
    Symbol: "Rg",
    Group: "11",
    "Atomic Mass": "272",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^9 7s^2"
  },
  {
    "Atomic Number": "112",
    Name: "Copernicium",
    Symbol: "Cn",
    Group: "12",
    "Atomic Mass": "285",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^10 7s^2"
  },
  {
    "Atomic Number": "113",
    Name: "Nihonium",
    Symbol: "Nh",
    Group: "13",
    "Atomic Mass": "286",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^10 7s^2 7p^1"
  },
  {
    "Atomic Number": "114",
    Name: "Flerovium",
    Symbol: "Fl",
    Group: "14",
    "Atomic Mass": "289",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^10 7s^2 7p^2"
  },
  {
    "Atomic Number": "115",
    Name: "Moscovium",
    Symbol: "Mc",
    Group: "15",
    "Atomic Mass": "290",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^10 7s^2 7p^3"
  },
  {
    "Atomic Number": "116",
    Name: "Livermorium",
    Symbol: "Lv",
    Group: "16",
    "Atomic Mass": "292",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^10 7s^2 7p^4"
  },
  {
    "Atomic Number": "117",
    Name: "Tennessine",
    Symbol: "Ts",
    Group: "17",
    "Atomic Mass": "294",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^10 7s^2 7p^5"
  },
  {
    "Atomic Number": "118",
    Name: "Oganesson",
    Symbol: "Og",
    Group: "18",
    "Atomic Mass": "294",
    "Phase at STP": "Synthetic",
    "Charge of Common Ion": "",
    "Metal/Nonmetal/Metalloid": "Non Metal",
    "Group Name": "",
    Period: "7",
    "Ionization Energy": "#N/A",
    "Melting Point": "#N/A",
    "Boiling Point": "#N/A",
    "Atomic Radius": "#N/A",
    Density: "#N/A",
    Electronegativity: "#N/A",
    "Electron Configuration": "1s^2 2s^2 2p^6 3s^2 3p^6 3d^10 4s^2 4p^6 4d^10 5s^2 5p^6 4f^14 5d^10 6s^2 6p^6 5f^14 6d^10 7s^2 7p^6"
  }
];
const atomDatabase = _atomDatabase;
function getDataForAtom({
  symbol,
  atomicNumber
}) {
  let rowInd = -1;
  if (atomicNumber !== null) {
    rowInd = atomicNumber - 1;
  } else if (symbol !== null) {
    rowInd = [
      "h",
      "he",
      "li",
      "be",
      "b",
      "c",
      "n",
      "o",
      "f",
      "ne",
      "na",
      "mg",
      "al",
      "si",
      "p",
      "s",
      "cl",
      "ar",
      "k",
      "ca",
      "sc",
      "ti",
      "v",
      "cr",
      "mn",
      "fe",
      "co",
      "ni",
      "cu",
      "zn",
      "ga",
      "ge",
      "as",
      "se",
      "br",
      "kr",
      "rb",
      "sr",
      "y",
      "zr",
      "nb",
      "mo",
      "tc",
      "ru",
      "rh",
      "pd",
      "ag",
      "cd",
      "in",
      "sn",
      "sb",
      "te",
      "i",
      "xe",
      "cs",
      "ba",
      "la",
      "ce",
      "pr",
      "nd",
      "pm",
      "sm",
      "eu",
      "gd",
      "tb",
      "dy",
      "ho",
      "er",
      "tm",
      "yb",
      "lu",
      "hf",
      "ta",
      "w",
      "re",
      "os",
      "ir",
      "pt",
      "au",
      "hg",
      "tl",
      "pb",
      "bi",
      "po",
      "at",
      "rn",
      "fr",
      "ra",
      "ac",
      "th",
      "pa",
      "u",
      "np",
      "pu",
      "am",
      "cm",
      "bk",
      "cf",
      "es",
      "fm",
      "md",
      "no",
      "lr",
      "rf",
      "db",
      "sg",
      "bh",
      "hs",
      "mt",
      "ds",
      "rg",
      "cn",
      "nh",
      "fl",
      "mc",
      "lv",
      "ts",
      "og"
    ].indexOf(symbol.toLowerCase());
  }
  let rowData = atomDatabase[rowInd];
  if (!rowData) {
    return null;
  }
  let columnTypes = {
    "Atomic Number": "number",
    Name: "string",
    Symbol: "string",
    Group: "number",
    "Atomic Mass": "number",
    "Phase at STP": "string",
    "Charge of Common Ion": "number",
    "Metal/Nonmetal/Metalloid": "string",
    "Group Name": "string",
    Period: "number",
    "Ionization Energy": "number",
    "Melting Point": "number",
    "Boiling Point": "number",
    "Atomic Radius": "number",
    Density: "number",
    Electronegativity: "number",
    "Electron Configuration": "string"
  };
  let dataForAtom = {};
  for (let colName in columnTypes) {
    let prescribedType = columnTypes[colName];
    let value;
    if (prescribedType === "number") {
      value = rowData[colName];
      if (value === "") {
        value = NaN;
      } else {
        value = Number(value);
      }
    } else {
      value = rowData[colName];
      if ([`"`, `'`].includes(value[0]) && value[value.length - 1] === value[0]) {
        value = value.substring(1, value.length - 1);
      }
    }
    if (colName === "Electron Configuration") {
      dataForAtom[colName] = me$3.fromText(String(value));
    } else {
      dataForAtom[colName] = value;
    }
  }
  return dataForAtom;
}
class Atom extends InlineComponent {
  static componentType = "atom";
  static rendererType = "math";
  static componentDocs = {
    summary: "Represents a chemical element by symbol or atomic number, exposing its periodic-table properties"
  };
  static primaryStateVariableForDefinition = "atomicNumberShadow";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.symbol = {
      createComponentOfType: "text",
      description: 'The chemical symbol of the atom (e.g. "H", "He", "Li").'
    };
    attributes.atomicNumber = {
      createComponentOfType: "integer",
      description: "The atomic number of the atom (number of protons)."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.atomicNumberShadow = {
      defaultValue: null,
      hasEssential: true,
      essentialVarName: "atomicNumber",
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          atomicNumberShadow: true
        }
      }),
      inverseDefinition: async function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "atomicNumberShadow",
              value: desiredStateVariableValues.atomicNumberShadow
            }
          ]
        };
      }
    };
    stateVariableDefinitions.dataForAtom = {
      returnDependencies: () => ({
        symbolAttr: {
          dependencyType: "attributeComponent",
          attributeName: "symbol",
          variableNames: ["value"]
        },
        atomicNumberAttr: {
          dependencyType: "attributeComponent",
          attributeName: "atomicNumber",
          variableNames: ["value"]
        },
        atomicNumberShadow: {
          dependencyType: "stateVariable",
          variableName: "atomicNumberShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        let symbol = null, atomicNumber = null;
        if (dependencyValues.symbolAttr) {
          symbol = dependencyValues.symbolAttr.stateValues.value.toLowerCase();
        } else if (dependencyValues.atomicNumberAttr) {
          atomicNumber = dependencyValues.atomicNumberAttr.stateValues.value;
        } else if (dependencyValues.atomicNumberShadow) {
          atomicNumber = dependencyValues.atomicNumberShadow;
        } else {
          return { setValue: { dataForAtom: null } };
        }
        let dataForAtom = getDataForAtom({ symbol, atomicNumber });
        return { setValue: { dataForAtom } };
      }
    };
    stateVariableDefinitions.atomicNumber = {
      description: "The atomic number of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let atomicNumber;
        if (dependencyValues.dataForAtom) {
          atomicNumber = dependencyValues.dataForAtom["Atomic Number"];
        } else {
          atomicNumber = null;
        }
        return { setValue: { atomicNumber } };
      }
    };
    stateVariableDefinitions.symbol = {
      description: "The chemical symbol of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let symbol;
        if (dependencyValues.dataForAtom) {
          symbol = dependencyValues.dataForAtom.Symbol;
        } else {
          symbol = null;
        }
        return { setValue: { symbol } };
      }
    };
    stateVariableDefinitions.name = {
      description: "The full name of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let name;
        if (dependencyValues.dataForAtom) {
          name = dependencyValues.dataForAtom.Name;
        } else {
          name = null;
        }
        return { setValue: { name } };
      }
    };
    stateVariableDefinitions.group = {
      description: "The group number of the element in the periodic table.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let group;
        if (dependencyValues.dataForAtom) {
          group = dependencyValues.dataForAtom.Group;
        } else {
          group = null;
        }
        return { setValue: { group } };
      }
    };
    stateVariableDefinitions.atomicMass = {
      description: "The atomic mass of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let atomicMass;
        if (dependencyValues.dataForAtom) {
          atomicMass = dependencyValues.dataForAtom["Atomic Mass"];
        } else {
          atomicMass = null;
        }
        return { setValue: { atomicMass } };
      }
    };
    stateVariableDefinitions.phaseAtSTP = {
      description: "The phase (solid, liquid, or gas) of the element at standard temperature and pressure.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let phaseAtSTP;
        if (dependencyValues.dataForAtom) {
          phaseAtSTP = dependencyValues.dataForAtom["Phase at STP"];
        } else {
          phaseAtSTP = null;
        }
        return { setValue: { phaseAtSTP } };
      }
    };
    stateVariableDefinitions.chargeOfCommonIon = {
      description: "The charge of the most common ion of this element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let chargeOfCommonIon;
        if (dependencyValues.dataForAtom) {
          chargeOfCommonIon = dependencyValues.dataForAtom["Charge of Common Ion"];
        } else {
          chargeOfCommonIon = null;
        }
        return { setValue: { chargeOfCommonIon } };
      }
    };
    stateVariableDefinitions.metalCategory = {
      description: "The metal category of the element (e.g. alkali metal, transition metal, nonmetal).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let metalCategory;
        if (dependencyValues.dataForAtom) {
          metalCategory = dependencyValues.dataForAtom["Metal/Nonmetal/Metalloid"];
        } else {
          metalCategory = null;
        }
        return { setValue: { metalCategory } };
      }
    };
    stateVariableDefinitions.groupName = {
      description: `The descriptive name of the element's periodic group (e.g. "Alkali metals").`,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let groupName;
        if (dependencyValues.dataForAtom) {
          groupName = dependencyValues.dataForAtom["Group Name"];
        } else {
          groupName = null;
        }
        return { setValue: { groupName } };
      }
    };
    stateVariableDefinitions.period = {
      description: "The period number of the element in the periodic table.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let period;
        if (dependencyValues.dataForAtom) {
          period = dependencyValues.dataForAtom.Period;
        } else {
          period = null;
        }
        return { setValue: { period } };
      }
    };
    stateVariableDefinitions.ionizationEnergy = {
      description: "The first ionization energy of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let ionizationEnergy;
        if (dependencyValues.dataForAtom) {
          ionizationEnergy = dependencyValues.dataForAtom["Ionization Energy"];
        } else {
          ionizationEnergy = null;
        }
        return { setValue: { ionizationEnergy } };
      }
    };
    stateVariableDefinitions.meltingPoint = {
      description: "The melting point of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let meltingPoint;
        if (dependencyValues.dataForAtom) {
          meltingPoint = dependencyValues.dataForAtom["Melting Point"];
        } else {
          meltingPoint = null;
        }
        return { setValue: { meltingPoint } };
      }
    };
    stateVariableDefinitions.boilingPoint = {
      description: "The boiling point of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let boilingPoint;
        if (dependencyValues.dataForAtom) {
          boilingPoint = dependencyValues.dataForAtom["Boiling Point"];
        } else {
          boilingPoint = null;
        }
        return { setValue: { boilingPoint } };
      }
    };
    stateVariableDefinitions.atomicRadius = {
      description: "The atomic radius of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let atomicRadius;
        if (dependencyValues.dataForAtom) {
          atomicRadius = dependencyValues.dataForAtom["Atomic Radius"];
        } else {
          atomicRadius = null;
        }
        return { setValue: { atomicRadius } };
      }
    };
    stateVariableDefinitions.density = {
      description: "The density of the element at standard conditions.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let density;
        if (dependencyValues.dataForAtom) {
          density = dependencyValues.dataForAtom.Density;
        } else {
          density = null;
        }
        return { setValue: { density } };
      }
    };
    stateVariableDefinitions.electronegativity = {
      description: "The Pauling electronegativity of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let electronegativity;
        if (dependencyValues.dataForAtom) {
          electronegativity = dependencyValues.dataForAtom.Electronegativity;
        } else {
          electronegativity = null;
        }
        return { setValue: { electronegativity } };
      }
    };
    stateVariableDefinitions.electronConfiguration = {
      description: "The electron configuration of the element as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "electronConfiguration"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let electronConfiguration;
        if (dependencyValues.dataForAtom) {
          electronConfiguration = dependencyValues.dataForAtom["Electron Configuration"];
        } else {
          electronConfiguration = null;
        }
        return { setValue: { electronConfiguration } };
      }
    };
    stateVariableDefinitions.orbitalDiagram = {
      description: "The orbital diagram of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "orbitalDiagram"
      },
      returnDependencies: () => ({
        electronConfiguration: {
          dependencyType: "stateVariable",
          variableName: "electronConfiguration"
        }
      }),
      definition({ dependencyValues }) {
        let orbitalDiagram;
        if (dependencyValues.electronConfiguration) {
          orbitalDiagram = electronConfigurationToOrbitalDiagram(
            dependencyValues.electronConfiguration
          );
        } else {
          orbitalDiagram = null;
        }
        return {
          setValue: { orbitalDiagram }
        };
      }
    };
    stateVariableDefinitions.math = {
      description: "The atom rendered as a math expression with the element symbol.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      returnDependencies: () => ({
        symbol: {
          dependencyType: "stateVariable",
          variableName: "symbol"
        }
      }),
      definition({ dependencyValues }) {
        let tree;
        if (dependencyValues.symbol) {
          tree = dependencyValues.symbol;
        } else {
          tree = "＿";
        }
        let math = me$3.fromAst(tree);
        return {
          setValue: { math }
        };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The atom rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        symbol: {
          dependencyType: "stateVariable",
          variableName: "symbol"
        }
      }),
      definition({ dependencyValues }) {
        let latex;
        if (dependencyValues.symbol) {
          latex = `\\text{${dependencyValues.symbol}}`;
        } else {
          latex = "[\\text{Invalid Chemical Symbol}]";
        }
        return {
          setValue: { latex }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The atom rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        symbol: {
          dependencyType: "stateVariable",
          variableName: "symbol"
        }
      }),
      definition({ dependencyValues }) {
        let text;
        if (dependencyValues.symbol) {
          text = dependencyValues.symbol;
        } else {
          text = "[Invalid Chemical Symbol]";
        }
        return {
          setValue: { text }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    "math",
    "name",
    {
      stateVariable: "atomicNumber",
      componentType: "ion"
    }
  ];
}
function electronConfigurationToOrbitalDiagram(electronConfiguration) {
  let electronConfig = electronConfiguration.tree;
  if (!(Array.isArray(electronConfig) && electronConfig[0] === "*")) {
    return null;
  }
  electronConfig = electronConfig.slice(1);
  let numRows = electronConfig.length / 2;
  if (!Number.isInteger(numRows)) {
    return null;
  }
  let orbitalDiagram = [];
  for (let rowInd = 0; rowInd < numRows; rowInd++) {
    let electronLevel = electronConfig[2 * rowInd];
    if (!(Number.isInteger(electronLevel) && electronLevel > 0)) {
      return null;
    }
    let infoObj = electronConfig[2 * rowInd + 1];
    if (!(Array.isArray(infoObj) && infoObj[0] === "^")) {
      return null;
    }
    let shellType = infoObj[1];
    let nElectrons = infoObj[2];
    if (!(["s", "p", "d", "f"].includes(shellType) && Number.isInteger(nElectrons) && nElectrons > 0)) {
      return null;
    }
    let orbitalText = `${electronLevel}${shellType}`;
    let nBoxes;
    if (shellType === "s") {
      nBoxes = 1;
    } else if (shellType === "p") {
      nBoxes = 3;
    } else if (shellType === "d") {
      nBoxes = 5;
    } else {
      nBoxes = 7;
    }
    let boxes = Array(nBoxes).fill("");
    for (let electronInd = 0; electronInd < Math.min(nBoxes, nElectrons); electronInd++) {
      if (nElectrons <= electronInd + nBoxes) {
        boxes[electronInd] = "U";
      } else {
        boxes[electronInd] = "UD";
      }
    }
    orbitalDiagram.push({ orbitalText, boxes });
  }
  return orbitalDiagram;
}
class Ion extends InlineComponent {
  static componentType = "ion";
  static rendererType = "math";
  static componentDocs = {
    summary: "Represents a charged ion specified by element and charge, rendered with its chemical formula"
  };
  static primaryStateVariableForDefinition = "atomicNumberShadow";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.symbol = {
      createComponentOfType: "text",
      description: 'The chemical symbol of the element (e.g. "Na", "Cl").'
    };
    attributes.atomicNumber = {
      createComponentOfType: "integer",
      description: "The atomic number of the element."
    };
    attributes.charge = {
      createComponentOfType: "integer",
      description: "The charge of the ion (positive for cations, negative for anions)."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "atoms",
        componentTypes: ["atom"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.atomicNumberShadow = {
      defaultValue: null,
      hasEssential: true,
      essentialVarName: "atomicNumber",
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          atomicNumberShadow: true
        }
      }),
      inverseDefinition: async function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "atomicNumberShadow",
              value: desiredStateVariableValues.atomicNumberShadow
            }
          ]
        };
      }
    };
    stateVariableDefinitions.dataForAtom = {
      returnDependencies: () => ({
        symbolAttr: {
          dependencyType: "attributeComponent",
          attributeName: "symbol",
          variableNames: ["value"]
        },
        atomicNumberAttr: {
          dependencyType: "attributeComponent",
          attributeName: "atomicNumber",
          variableNames: ["value"]
        },
        atomChild: {
          dependencyType: "child",
          childGroups: ["atoms"],
          variableNames: ["atomicNumber"]
        },
        atomicNumberShadow: {
          dependencyType: "stateVariable",
          variableName: "atomicNumberShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        let symbol = null, atomicNumber = null;
        if (dependencyValues.atomChild.length > 0) {
          atomicNumber = dependencyValues.atomChild[0].stateValues.atomicNumber;
        } else if (dependencyValues.symbolAttr) {
          symbol = dependencyValues.symbolAttr.stateValues.value.toLowerCase();
        } else if (dependencyValues.atomicNumberAttr) {
          atomicNumber = dependencyValues.atomicNumberAttr.stateValues.value;
        } else if (dependencyValues.atomicNumberShadow) {
          atomicNumber = dependencyValues.atomicNumberShadow;
        } else {
          return { setValue: { dataForAtom: null } };
        }
        let dataForAtom = getDataForAtom({ symbol, atomicNumber });
        return { setValue: { dataForAtom } };
      }
    };
    stateVariableDefinitions.atomicNumber = {
      description: "The atomic number of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let atomicNumber;
        if (dependencyValues.dataForAtom) {
          atomicNumber = dependencyValues.dataForAtom["Atomic Number"];
        } else {
          atomicNumber = null;
        }
        return { setValue: { atomicNumber } };
      }
    };
    stateVariableDefinitions.symbol = {
      description: "The chemical symbol of the element.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let symbol;
        if (dependencyValues.dataForAtom) {
          symbol = dependencyValues.dataForAtom.Symbol;
        } else {
          symbol = null;
        }
        return { setValue: { symbol } };
      }
    };
    stateVariableDefinitions.charge = {
      description: "The charge of the ion.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        },
        chargeAttr: {
          dependencyType: "attributeComponent",
          attributeName: "charge",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        let charge;
        if (dependencyValues.chargeAttr) {
          charge = dependencyValues.chargeAttr.stateValues.value;
          if (!Number.isFinite(charge)) {
            charge = 0;
          }
        } else if (dependencyValues.dataForAtom) {
          charge = dependencyValues.dataForAtom["Charge of Common Ion"] || 0;
        } else {
          charge = 0;
        }
        return { setValue: { charge } };
      }
    };
    stateVariableDefinitions.name = {
      description: 'The full name of the ion (e.g. "sodium ion").',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        },
        charge: {
          dependencyType: "stateVariable",
          variableName: "charge"
        },
        group: {
          dependencyType: "stateVariable",
          variableName: "group"
        },
        atomicNumber: {
          dependencyType: "stateVariable",
          variableName: "atomicNumber"
        }
      }),
      definition({ dependencyValues }) {
        let name;
        if (dependencyValues.dataForAtom) {
          name = dependencyValues.dataForAtom.Name;
          if (dependencyValues.charge < 0) {
            let mapping = {
              Hydrogen: "Hydride",
              Oxygen: "Oxide",
              Sulfur: "Sulfide",
              Nitrogen: "Nitride",
              Phosphorus: "Phosphide",
              Carbon: "Carbide"
            };
            let len = name.length;
            if (name.substring(len - 3, len) === "ine") {
              name = name.substring(0, len - 3) + "ide";
            } else if (name in mapping) {
              name = mapping[name];
            }
          } else if (dependencyValues.charge > 0 && (dependencyValues.group >= 3 && dependencyValues.group <= 12 || dependencyValues.group === 101 || dependencyValues.group === 102 || [13, 31, 49, 50, 81, 82, 83, 84].includes(
            dependencyValues.atomicNumber
          ))) {
            let suffix = "";
            switch (dependencyValues.charge) {
              case 1:
                suffix = " (I)";
                break;
              case 2:
                suffix = " (II)";
                break;
              case 3:
                suffix = " (III)";
                break;
              case 4:
                suffix = " (IV)";
                break;
              case 5:
                suffix = " (V)";
                break;
              case 6:
                suffix = " (VI)";
                break;
              case 7:
                suffix = " (VII)";
                break;
              case 8:
                suffix = " (VIII)";
                break;
            }
            name += suffix;
          }
        } else {
          name = null;
        }
        return { setValue: { name } };
      }
    };
    stateVariableDefinitions.group = {
      description: "The group number of the underlying element in the periodic table.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let group;
        if (dependencyValues.dataForAtom) {
          group = dependencyValues.dataForAtom.Group;
        } else {
          group = null;
        }
        return { setValue: { group } };
      }
    };
    stateVariableDefinitions.metalCategory = {
      description: "The metal category of the underlying element (e.g. alkali metal, transition metal, nonmetal).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let metalCategory;
        if (dependencyValues.dataForAtom) {
          metalCategory = dependencyValues.dataForAtom["Metal/Nonmetal/Metalloid"];
        } else {
          metalCategory = null;
        }
        return { setValue: { metalCategory } };
      }
    };
    stateVariableDefinitions.groupName = {
      description: "The descriptive name of the underlying element's periodic group.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let groupName;
        if (dependencyValues.dataForAtom) {
          groupName = dependencyValues.dataForAtom["Group Name"];
        } else {
          groupName = null;
        }
        return { setValue: { groupName } };
      }
    };
    stateVariableDefinitions.period = {
      description: "The period number of the underlying element in the periodic table.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataForAtom: {
          dependencyType: "stateVariable",
          variableName: "dataForAtom"
        }
      }),
      definition({ dependencyValues }) {
        let period;
        if (dependencyValues.dataForAtom) {
          period = dependencyValues.dataForAtom.Period;
        } else {
          period = null;
        }
        return { setValue: { period } };
      }
    };
    stateVariableDefinitions.math = {
      description: "The ion rendered as a math expression (element symbol with charge).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      returnDependencies: () => ({
        symbol: {
          dependencyType: "stateVariable",
          variableName: "symbol"
        },
        charge: {
          dependencyType: "stateVariable",
          variableName: "charge"
        }
      }),
      definition({ dependencyValues }) {
        let tree;
        if (dependencyValues.symbol) {
          tree = dependencyValues.symbol;
          if (dependencyValues.charge === -1) {
            tree = ["^", tree, "-"];
          } else if (dependencyValues.charge === 1) {
            tree = ["^", tree, "+"];
          } else if (dependencyValues.charge < 0) {
            tree = [
              "^",
              tree,
              Math.abs(dependencyValues.charge) + "-"
            ];
          } else if (dependencyValues.charge > 0) {
            tree = [
              "^",
              tree,
              Math.abs(dependencyValues.charge) + "+"
            ];
          }
        } else {
          tree = "＿";
        }
        let math = me$3.fromAst(tree);
        return {
          setValue: { math }
        };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The ion rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        symbol: {
          dependencyType: "stateVariable",
          variableName: "symbol"
        },
        charge: {
          dependencyType: "stateVariable",
          variableName: "charge"
        }
      }),
      definition({ dependencyValues }) {
        let latex;
        if (dependencyValues.symbol) {
          latex = `\\text{${dependencyValues.symbol}}`;
          if (dependencyValues.charge === -1) {
            latex = latex + `^-`;
          } else if (dependencyValues.charge === 1) {
            latex = latex + `^+`;
          } else if (dependencyValues.charge < 0) {
            latex = latex + `^{${Math.abs(dependencyValues.charge)}-}`;
          } else if (dependencyValues.charge > 0) {
            latex = latex + `^{${Math.abs(dependencyValues.charge)}+}`;
          }
        } else {
          latex = "[\\text{Invalid Chemical Symbol}]";
        }
        return {
          setValue: { latex }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The ion rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        symbol: {
          dependencyType: "stateVariable",
          variableName: "symbol"
        },
        charge: {
          dependencyType: "stateVariable",
          variableName: "charge"
        }
      }),
      definition({ dependencyValues }) {
        let text;
        if (dependencyValues.symbol) {
          text = dependencyValues.symbol;
          if (dependencyValues.charge === -1) {
            text = text + `^-`;
          } else if (dependencyValues.charge === 1) {
            text = text + `^+`;
          } else if (dependencyValues.charge < 0) {
            text = text + `^(${Math.abs(dependencyValues.charge)}-)`;
          } else if (dependencyValues.charge > 0) {
            text = text + `^(${Math.abs(dependencyValues.charge)}+)`;
          }
        } else {
          text = "[Invalid Chemical Symbol]";
        }
        return {
          setValue: { text }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = ["math", "name"];
}
const { gcd: gcd$1 } = me$3.math;
class IonicCompound extends InlineComponent {
  static componentType = "ionicCompound";
  static rendererType = "math";
  static componentDocs = {
    summary: "Forms a balanced ionic compound from constituent ions and renders its chemical formula"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.symbol = {
      createComponentOfType: "text",
      description: "The chemical symbol of an element in the compound."
    };
    attributes.atomicNumber = {
      createComponentOfType: "integer",
      description: "The atomic number of an element in the compound."
    };
    attributes.charge = {
      createComponentOfType: "integer",
      description: "The charge of an ion in the compound."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "ions",
        componentTypes: ["ion"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.ionicCompound = {
      // public: true,
      // shadowingInstructions: {
      //   createComponentOfType: "integer",
      // },
      returnDependencies: () => ({
        ionChildren: {
          dependencyType: "child",
          childGroups: ["ions"],
          variableNames: ["symbol", "charge", "atomicNumber", "name"]
        }
      }),
      definition({ dependencyValues }) {
        let charges = dependencyValues.ionChildren.map(
          (child) => child.stateValues.charge
        );
        if (charges.length !== 2) {
          let warning = {
            message: "Have not implemented ionic compound for anything other than two ions.",
            type: "warning"
          };
          return {
            setValue: { ionicCompound: null },
            sendDiagnostics: [warning]
          };
        }
        if (!(charges[0] * charges[1] < 0)) {
          let warning = {
            message: "Ionic compound implemented only for one cation and one anion.",
            type: "warning"
          };
          return {
            setValue: { ionicCompound: null },
            sendDiagnostics: [warning]
          };
        }
        let n1 = Math.abs(charges[1]);
        let n2 = Math.abs(charges[0]);
        let computedGcd = gcd$1(n1, n2);
        n1 /= computedGcd;
        n2 /= computedGcd;
        let ionicCompound = [
          {
            symbol: dependencyValues.ionChildren[0].stateValues.symbol,
            atomicNumber: dependencyValues.ionChildren[0].stateValues.atomicNumber,
            name: dependencyValues.ionChildren[0].stateValues.name,
            charge: charges[0],
            count: n1
          },
          {
            symbol: dependencyValues.ionChildren[1].stateValues.symbol,
            atomicNumber: dependencyValues.ionChildren[1].stateValues.atomicNumber,
            name: dependencyValues.ionChildren[1].stateValues.name,
            charge: charges[1],
            count: n2
          }
        ];
        return { setValue: { ionicCompound } };
      }
    };
    stateVariableDefinitions.math = {
      description: "The ionic compound rendered as a math expression of its chemical formula.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      returnDependencies: () => ({
        ionicCompound: {
          dependencyType: "stateVariable",
          variableName: "ionicCompound"
        }
      }),
      definition({ dependencyValues }) {
        let tree;
        if (dependencyValues.ionicCompound) {
          tree = [];
          for (let piece of dependencyValues.ionicCompound) {
            let pieceTree = piece.symbol;
            if (piece.count > 1) {
              pieceTree = ["_", pieceTree, piece.count];
            }
            tree.push(pieceTree);
          }
          if (tree.length > 1) {
            tree = ["*", ...tree];
          } else if (tree.length === 1) {
            tree = tree[0];
          } else {
            tree = "＿";
          }
        } else {
          tree = "＿";
        }
        return {
          setValue: { math: me$3.fromAst(tree) }
        };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The ionic compound rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        ionicCompound: {
          dependencyType: "stateVariable",
          variableName: "ionicCompound"
        }
      }),
      definition({ dependencyValues }) {
        let latex;
        if (dependencyValues.ionicCompound) {
          let parts = [];
          for (let piece of dependencyValues.ionicCompound) {
            let part = `\\text{${piece.symbol}}`;
            if (piece.count > 1) {
              part += `_{${piece.count}}`;
            }
            parts.push(part);
          }
          latex = parts.join(" ");
        } else {
          latex = "[\\text{Invalid Ionic Compound}]";
        }
        return {
          setValue: { latex }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The ionic compound rendered as a plain text string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        ionicCompound: {
          dependencyType: "stateVariable",
          variableName: "ionicCompound"
        }
      }),
      definition({ dependencyValues }) {
        let text;
        if (dependencyValues.ionicCompound) {
          let parts = [];
          for (let piece of dependencyValues.ionicCompound) {
            let part = piece.symbol;
            if (piece.count > 1) {
              part += `_${piece.count}`;
            }
            parts.push(part);
          }
          text = parts.join(" ");
        } else {
          text = "[Invalid Ionic Compound]";
        }
        return {
          setValue: { text }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = ["math"];
}
class ElectronConfiguration extends MathComponent {
  static componentType = "electronConfiguration";
  static rendererType = "math";
  static componentDocs = {
    summary: "Renders an electron configuration with sublevel labels and superscripted electron counts"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.latex = {
      description: "The electron configuration rendered as a LaTeX string.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        }
      }),
      definition: function({ dependencyValues }) {
        let latex;
        try {
          latex = dependencyValues.valueForDisplay.toLatex();
        } catch (e3) {
          latex = "＿";
        }
        latex = latex.replaceAll("\\,", "");
        latex = latex.replaceAll("\\cdot", "~");
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.text = {
      description: "The electron configuration rendered as a unicode text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        }
      }),
      definition: function({ dependencyValues }) {
        let text;
        try {
          text = superSubscriptsToUnicode(
            dependencyValues.valueForDisplay.toString()
          );
        } catch (e3) {
          text = "＿";
        }
        text = text.replaceAll(" ", "");
        text = text.replaceAll("*", " ");
        return { setValue: { text } };
      }
    };
    return stateVariableDefinitions;
  }
}
class OrbitalDiagram extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "orbitalDiagram";
  static componentDocs = {
    summary: "Renders an orbital diagram showing electrons in atomic orbitals as up/down arrows in boxes"
  };
  static variableForImplicitProp = "value";
  static implicitPropReturnsSameType = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.labels = {
      createComponentOfType: "textList",
      createStateVariable: "labels",
      defaultValue: [],
      description: "Text labels for each orbital row."
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string", "math", "number"];
  static returnSugarInstructions() {
    let sugarInstructions = [
      {
        replacementFunction: function({
          matchedChildren,
          nComponents,
          stateIdInfo
        }) {
          if (matchedChildren.length === 1 && typeof matchedChildren[0] !== "string") {
            return { success: false };
          }
          return {
            success: true,
            newChildren: [
              {
                type: "serialized",
                componentType: "tupleList",
                componentIdx: nComponents++,
                stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
                children: matchedChildren,
                attributes: {},
                doenetAttributes: {},
                state: {}
              }
            ],
            nComponents
          };
        }
      }
    ];
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "tupleLists",
        componentTypes: ["tupleList"]
      },
      {
        group: "orbitalDiagrams",
        componentTypes: ["orbitalDiagram"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.value = {
      description: "The orbital diagram value as a list of rows, each with a label and boxes containing up/down arrows.",
      defaultValue: [{ orbitalText: "", boxes: [] }],
      public: true,
      shadowingInstructions: {
        createComponentOfType: this.componentType
      },
      forRenderer: true,
      returnDependencies: () => ({
        orbitalDiagramChildren: {
          dependencyType: "child",
          childGroups: ["orbitalDiagrams"],
          variableNames: ["value"]
        },
        tupleListChildren: {
          dependencyType: "child",
          childGroups: ["tupleLists"],
          variableNames: ["maths"]
        },
        labels: {
          dependencyType: "stateVariable",
          variableName: "labels"
        }
      }),
      definition: function({ dependencyValues }) {
        function processedValues() {
          function boxFromEntry(entry) {
            if (entry === "u" || entry === "U") {
              return "U";
            } else if (entry === "d" || entry === "D") {
              return "D";
            } else if (entry === "e" || entry === "E") {
              return "";
            } else if (Array.isArray(entry) && entry[0] === "*") {
              let str = "";
              for (let fac of entry.slice(1)) {
                if (fac === "u" || fac === "U") {
                  str += "U";
                } else if (fac === "d" || fac === "D") {
                  str += "D";
                } else {
                  return "";
                }
              }
              return str;
            } else {
              return "";
            }
          }
          if (dependencyValues.orbitalDiagramChildren.length === 1) {
            return dependencyValues.orbitalDiagramChildren[0].stateValues.value;
          }
          let rows = [];
          if (dependencyValues.tupleListChildren[0]?.stateValues.maths.length > 0) {
            let valuesList = dependencyValues.tupleListChildren[0].stateValues.maths;
            for (let [rowInd, row] of valuesList.entries()) {
              let orbitalText = "";
              if (dependencyValues.labels[rowInd]) {
                orbitalText = dependencyValues.labels[rowInd];
              }
              let boxes = [];
              if (Array.isArray(row.tree) && row.tree[0] === "tuple") {
                for (let entry of row.tree.slice(1)) {
                  boxes.push(boxFromEntry(entry));
                }
              } else {
                boxes.push(boxFromEntry(row.tree));
              }
              rows.push({ orbitalText, boxes });
            }
            return rows;
          } else {
            return [{ orbitalText: "", boxes: [] }];
          }
        }
        return {
          setValue: {
            value: processedValues()
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class OrbitalDiagramInput extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      addRow: this.addRow.bind(this),
      removeRow: this.removeRow.bind(this),
      addBox: this.addBox.bind(this),
      removeBox: this.removeBox.bind(this),
      addUpArrow: this.addUpArrow.bind(this),
      addDownArrow: this.addDownArrow.bind(this),
      removeArrow: this.removeArrow.bind(this),
      updateRowText: this.updateRowText.bind(this),
      selectRow: this.selectRow.bind(this),
      selectBox: this.selectBox.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "orbitalDiagramInput";
  static componentDocs = {
    summary: "An interactive widget where students fill in an orbital diagram by adding boxes, rows, and up/down arrows (block-level; does not inherit from Input)"
  };
  static variableForImplicitProp = "value";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.prefill = {
      createComponentOfType: "orbitalDiagram",
      createStateVariable: "prefill",
      defaultValue: [],
      description: "An orbital diagram used to prefill the input before the student begins."
    };
    attributes.prefillLabel = {
      createComponentOfType: "textList",
      createStateVariable: "prefillLabel",
      defaultValue: [],
      description: "Text labels to prefill for each orbital row."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.value = {
      description: "The current orbital diagram entered by the student.",
      defaultValue: [{ orbitalText: "", boxes: [] }],
      hasEssential: true,
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "orbitalDiagram"
      },
      returnDependencies: () => ({
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        prefillLabel: {
          dependencyType: "stateVariable",
          variableName: "prefillLabel"
        }
      }),
      definition: function({ dependencyValues }) {
        function processedPrefill() {
          if (dependencyValues.prefill?.length > 0) {
            let value = JSON.parse(
              JSON.stringify(dependencyValues.prefill)
            );
            for (let [rowInd, row] of value.entries()) {
              if (dependencyValues.prefillLabel[rowInd]) {
                row.orbitalText = dependencyValues.prefillLabel[rowInd];
              }
            }
            return value;
          } else {
            return [{ orbitalText: "", boxes: [] }];
          }
        }
        return {
          useEssentialOrDefaultValue: {
            value: {
              get defaultValue() {
                return processedPrefill();
              }
            }
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "value",
              value: desiredStateVariableValues.value
            }
          ]
        };
      }
    };
    stateVariableDefinitions.rows = {
      description: "The orbital diagram rows in display order (reversed from the underlying value).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "orbitalDiagram"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition({ dependencyValues }) {
        let rows = [...dependencyValues.value].reverse();
        return { setValue: { rows } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "value",
              desiredValue: [
                ...desiredStateVariableValues.rows
              ].reverse()
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numRows = {
      description: "The number of orbital rows in the diagram.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        rows: {
          dependencyType: "stateVariable",
          variableName: "rows"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { numRows: dependencyValues.rows.length } };
      }
    };
    stateVariableDefinitions.selectedRowIndex = {
      description: "Index of the currently selected orbital row (1-based; 0 if none selected).",
      defaultValue: 0,
      hasEssential: true,
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({}),
      definition: function() {
        return {
          useEssentialOrDefaultValue: { selectedRowIndex: true }
        };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        stateValues
      }) {
        let numRows = await stateValues.numRows;
        let desiredRowIndex = desiredStateVariableValues.selectedRowIndex;
        if (Number.isFinite(desiredRowIndex)) {
          desiredRowIndex = Math.min(
            numRows,
            Math.max(0, Math.round(desiredRowIndex))
          );
        } else {
          desiredRowIndex = 0;
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "selectedRowIndex",
              value: desiredRowIndex
            }
          ]
        };
      }
    };
    stateVariableDefinitions.selectedBoxIndex = {
      description: "Index of the currently selected box within the selected row (1-based; 0 if none selected).",
      defaultValue: 0,
      hasEssential: true,
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({}),
      definition: function() {
        return {
          useEssentialOrDefaultValue: { selectedBoxIndex: true }
        };
      },
      async inverseDefinition({ desiredStateVariableValues }) {
        let desiredBoxIndex = desiredStateVariableValues.selectedBoxIndex;
        if (Number.isFinite(desiredBoxIndex)) {
          desiredBoxIndex = Math.max(0, Math.round(desiredBoxIndex));
        } else {
          desiredBoxIndex = 0;
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "selectedBoxIndex",
              value: desiredBoxIndex
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  async addRow({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    if (oldRows.length < 20) {
      newRows = [{ orbitalText: "", boxes: [] }, ...oldRows];
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "rows",
        value: newRows
      }
    ];
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    if (selectedRowIndex0 !== -1) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "selectedRowIndex",
        value: 0
      });
    }
    let selectedBoxIndex0 = await this.stateValues.selectedBoxIndex - 1;
    if (selectedBoxIndex0 !== -1) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "selectedBoxIndex",
        value: 0
      });
    }
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async removeRow({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    if (oldRows.length > 1) {
      let removeRowIndex0 = oldRows.length - 1 - selectedRowIndex0;
      if (selectedRowIndex0 === -1) {
        removeRowIndex0 = 0;
      }
      newRows.splice(removeRowIndex0, 1);
    }
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "rows",
          value: newRows
        },
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "selectedRowIndex",
          value: 0
        },
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "selectedBoxIndex",
          value: 0
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async addBox({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    let activeRowIndex0 = oldRows.length - selectedRowIndex0 - 1;
    if (selectedRowIndex0 === -1) {
      activeRowIndex0 = 0;
    }
    if (newRows[activeRowIndex0].boxes.length < 17) {
      newRows[activeRowIndex0].boxes.push("");
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "rows",
        value: newRows
      }
    ];
    if (selectedRowIndex0 !== -1) {
      let newIndex = 0;
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "selectedBoxIndex",
        value: newIndex
      });
    }
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async removeBox({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    let activeRowIndex0 = oldRows.length - selectedRowIndex0 - 1;
    if (selectedRowIndex0 === -1) {
      activeRowIndex0 = 0;
    }
    let selectedBoxIndex0 = await this.stateValues.selectedBoxIndex - 1;
    let activeBoxIndex0 = selectedBoxIndex0;
    if (selectedBoxIndex0 === -1) {
      activeBoxIndex0 = newRows[activeRowIndex0].boxes.length - 1;
    }
    newRows[activeRowIndex0].boxes.splice(activeBoxIndex0, 1);
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "rows",
        value: newRows
      }
    ];
    if (selectedBoxIndex0 !== -1) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "selectedBoxIndex",
        value: 0
      });
    }
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async addUpArrow({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    let activeRowIndex0 = oldRows.length - selectedRowIndex0 - 1;
    if (selectedRowIndex0 === -1) {
      activeRowIndex0 = 0;
    }
    let selectedBoxIndex0 = await this.stateValues.selectedBoxIndex - 1;
    let activeBoxIndex0 = selectedBoxIndex0;
    if (selectedBoxIndex0 === -1) {
      activeBoxIndex0 = newRows[activeRowIndex0].boxes.length - 1;
    }
    if (newRows[activeRowIndex0].boxes.length > 0 && //Has to have a box to put an arrow in it
    newRows[activeRowIndex0].boxes[activeBoxIndex0].length < 3) {
      newRows[activeRowIndex0].boxes[activeBoxIndex0] = newRows[activeRowIndex0].boxes[activeBoxIndex0] + "U";
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "rows",
        value: newRows
      }
    ];
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async addDownArrow({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    let activeRowIndex0 = oldRows.length - selectedRowIndex0 - 1;
    if (selectedRowIndex0 === -1) {
      activeRowIndex0 = 0;
    }
    let selectedBoxIndex0 = await this.stateValues.selectedBoxIndex - 1;
    let activeBoxIndex0 = selectedBoxIndex0;
    if (selectedBoxIndex0 === -1) {
      activeBoxIndex0 = newRows[activeRowIndex0].boxes.length - 1;
    }
    if (newRows[activeRowIndex0].boxes.length > 0 && //Has to have a box to put an arrow in it
    newRows[activeRowIndex0].boxes[activeBoxIndex0].length < 3) {
      newRows[activeRowIndex0].boxes[activeBoxIndex0] = newRows[activeRowIndex0].boxes[activeBoxIndex0] + "D";
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "rows",
        value: newRows
      }
    ];
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async removeArrow({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    let activeRowIndex0 = oldRows.length - selectedRowIndex0 - 1;
    if (selectedRowIndex0 === -1) {
      activeRowIndex0 = 0;
    }
    let selectedBoxIndex0 = await this.stateValues.selectedBoxIndex - 1;
    let activeBoxIndex0 = selectedBoxIndex0;
    if (selectedBoxIndex0 === -1) {
      activeBoxIndex0 = newRows[activeRowIndex0].boxes.length - 1;
    }
    if (newRows[activeRowIndex0].boxes.length > 0 && //Has to have a box to remove an arrow from it
    newRows[activeRowIndex0].boxes[activeBoxIndex0].length > 0) {
      newRows[activeRowIndex0].boxes[activeBoxIndex0] = newRows[activeRowIndex0].boxes[activeBoxIndex0].slice(0, -1);
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "rows",
        value: newRows
      }
    ];
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async updateRowText({
    newValue,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let oldRows = await this.stateValues.rows;
    let newRows = JSON.parse(JSON.stringify(oldRows));
    let selectedRowIndex0 = await this.stateValues.selectedRowIndex - 1;
    let activeRowIndex0 = oldRows.length - selectedRowIndex0 - 1;
    if (selectedRowIndex0 === -1) {
      activeRowIndex0 = 0;
    }
    newRows[activeRowIndex0].orbitalText = newValue;
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "rows",
        value: newRows
      }
    ];
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          rows: newRows
        }
      }
    });
  }
  async selectRow({
    index: index2,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "selectedRowIndex",
          value: index2
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          selectedRowIndex: index2
        }
      }
    });
  }
  async selectBox({
    index: index2,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "selectedBoxIndex",
          value: index2
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          selectedBoxIndex: index2
        }
      }
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
const Chemistry = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Atom,
  ElectronConfiguration,
  Ion,
  IonicCompound,
  OrbitalDiagram,
  OrbitalDiagramInput
}, Symbol.toStringTag, { value: "Module" }));
class FeedbackDefinition extends BaseComponent {
  static componentType = "feedbackDefinition";
  static componentDocs = {
    summary: "A reusable feedback definition referenced by other components"
  };
  static rendererType = void 0;
  static inSchemaOnlyInheritAs = [];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.code = {
      createComponentOfType: "text",
      description: "Identifier used to reference this feedback definition."
    };
    attributes.text = {
      createComponentOfType: "text",
      description: "Default feedback text for this code."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.feedbackDefinition = {
      returnDependencies: () => ({
        codeAttr: {
          dependencyType: "attributeComponent",
          attributeName: "code",
          variableNames: ["value"]
        },
        textAttr: {
          dependencyType: "attributeComponent",
          attributeName: "text",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.codeAttr !== null && dependencyValues.textAttr !== null) {
          let code = dependencyValues.codeAttr.stateValues.value.toLowerCase();
          return {
            setValue: {
              feedbackDefinition: {
                [code]: dependencyValues.textAttr.stateValues.value
              }
            }
          };
        } else {
          return {
            setValue: { feedbackDefinition: null }
          };
        }
      }
    };
    return stateVariableDefinitions;
  }
}
class FeedbackDefinitions extends BaseComponent {
  static componentType = "feedbackDefinitions";
  static rendererType = void 0;
  static excludeFromSchema = true;
}
const FeedbackDefinitions$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  FeedbackDefinition,
  FeedbackDefinitions
}, Symbol.toStringTag, { value: "Module" }));
class StyleDefinition extends BaseComponent {
  static componentType = "styleDefinition";
  static componentDocs = {
    summary: "A reusable style definition referenced by other components"
  };
  static rendererType = void 0;
  static inSchemaOnlyInheritAs = [];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.styleNumber = {
      createPrimitiveOfType: "integer",
      createStateVariable: "styleNumber",
      defaultValue: 1,
      clamp: [1, Infinity],
      description: "Index identifying which style this definition applies to."
    };
    for (let styleAttr in styleAttributes$1) {
      attributes[styleAttr] = attributeSpecFromStyleAttribute(
        styleAttributes$1[styleAttr]
      );
    }
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.styleDefinition = {
      returnDependencies: function() {
        let dependencies = {};
        for (let styleAttr in styleAttributes$1) {
          dependencies[styleAttr] = {
            dependencyType: "attributeComponent",
            attributeName: styleAttr,
            variableNames: ["value"]
          };
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let styleDefinition = {};
        for (let styleAttr in styleAttributes$1) {
          if (dependencyValues[styleAttr] !== null) {
            const value = dependencyValues[styleAttr].stateValues.value;
            if (typeof value === "string") {
              styleDefinition[styleAttr] = {
                style: value.toLowerCase()
              };
            } else {
              styleDefinition[styleAttr] = {
                style: value
              };
            }
            if (dependencyValues[styleAttr].position) {
              styleDefinition[styleAttr].position = dependencyValues[styleAttr].position;
            }
          }
        }
        return { setValue: { styleDefinition } };
      }
    };
    return stateVariableDefinitions;
  }
}
class StyleDefinitions extends BaseComponent {
  static componentType = "styleDefinitions";
  static rendererType = void 0;
  static excludeFromSchema = true;
}
const StyleDefinitions$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  StyleDefinition,
  StyleDefinitions
}, Symbol.toStringTag, { value: "Module" }));
class ComponentWithSelectableType extends BaseComponent {
  static componentType = "_componentWithSelectableType";
  static rendererType = void 0;
  static includeBlankStringChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "The data type of the value: number, math, text, letters, or boolean."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = [];
    function addType({
      matchedChildren,
      componentAttributes,
      parentAttributes,
      nComponents,
      stateIdInfo
    }) {
      let diagnostics = [];
      let type = componentAttributes.type?.value;
      if (!type) {
        type = parentAttributes.type?.value;
      }
      if (!type) {
        type = "number";
      } else if (!["number", "letters", "math", "text", "boolean"].includes(type)) {
        diagnostics.push({
          message: `Invalid type ${type}, setting type to number.`,
          type: "warning"
        });
        type = "number";
      }
      let componentType = type === "letters" ? "text" : type;
      if (componentType !== "text") {
        matchedChildren = matchedChildren.filter(
          (x2) => typeof x2 !== "string" || x2.trim() !== ""
        );
      }
      return {
        success: true,
        newChildren: [
          {
            type: "serialized",
            componentType,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: matchedChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ],
        diagnostics,
        nComponents
      };
    }
    sugarInstructions.push({
      replacementFunction: addType
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.type = {
      shadowVariable: true,
      returnDependencies: () => ({
        type: {
          dependencyType: "doenetAttribute",
          attributeName: "type"
        },
        parentType: {
          dependencyType: "parentStateVariable",
          variableName: "type"
        }
      }),
      definition: function({ dependencyValues }) {
        let diagnostics = [];
        let type = dependencyValues.type;
        if (!type) {
          type = dependencyValues.parentType;
        }
        if (!type) {
          type = "number";
        } else if (!["number", "letters", "math", "text", "boolean"].includes(
          type
        )) {
          diagnostics.push({
            message: `Invalid type ${type}, setting type to number.`,
            type: "info"
          });
          type = "number";
        }
        return { setValue: { type }, sendDiagnostics: diagnostics };
      }
    };
    stateVariableDefinitions.value = {
      description: "The selected value, with type determined by the type attribute.",
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true
      },
      shadowVariable: true,
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        atMostOneChild: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        let value;
        if (dependencyValues.atMostOneChild.length > 0) {
          value = dependencyValues.atMostOneChild[0].stateValues.value;
        } else {
          if (dependencyValues.type === "text" || dependencyValues.type === "letters") {
            value = "";
          } else if (dependencyValues.type === "boolean") {
            value = false;
          } else if (dependencyValues.type === "number") {
            value = NaN;
          } else {
            value = me$3.fromAst("＿");
          }
        }
        return {
          setValue: { value },
          setCreateComponentOfType: { value: dependencyValues.type }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.atMostOneChild.length > 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "atMostOneChild",
                desiredValue: desiredStateVariableValues.value,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    return stateVariableDefinitions;
  }
}
class ComponentListWithSelectableType extends ComponentWithSelectableType {
  static componentType = "_componentListWithSelectableType";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "The data type of the values: number, math, text, letters, or boolean."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = [];
    sugarInstructions.push({
      replacementFunction: function({
        matchedChildren,
        componentAttributes,
        parentAttributes,
        nComponents
      }) {
        let diagnostics = [];
        let type = componentAttributes.type?.value;
        if (!type) {
          type = parentAttributes.type?.value;
        }
        if (!type) {
          type = "number";
        } else if (!["number", "letters", "math", "text", "boolean"].includes(
          type
        )) {
          diagnostics.push({
            message: `Invalid type ${type}, setting type to number.`,
            type: "warning"
          });
          type = "number";
        }
        let componentType = type === "letters" ? "text" : type;
        let groupIntoComponentTypesSeparatedBySpaces = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
          componentType
        });
        let result = groupIntoComponentTypesSeparatedBySpaces({
          matchedChildren,
          nComponents
        });
        result.diagnostics = diagnostics;
        return result;
      }
    });
    return sugarInstructions;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.value;
    stateVariableDefinitions.numValues = {
      additionalStateVariablesDefined: ["childForValue"],
      returnDependencies: () => ({
        anythingForSelectedType: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["numValues"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        let numValues = 0;
        let childForValue = [];
        for (let [
          ind,
          child
        ] of dependencyValues.anythingForSelectedType.entries()) {
          let n2 = Number.isInteger(child.stateValues.numValues) ? child.stateValues.numValues : 1;
          numValues += n2;
          for (let i2 = 0; i2 < n2; i2++) {
            childForValue.push({ child: ind, valueIndex: i2 });
          }
        }
        return {
          setValue: { numValues, childForValue }
        };
      }
    };
    stateVariableDefinitions.values = {
      description: "The list of values, with type determined by the type attribute.",
      public: true,
      isArray: true,
      shadowingInstructions: {
        hasVariableComponentType: true
      },
      entryPrefixes: ["value"],
      stateVariablesDeterminingDependencies: ["childForValue"],
      returnArraySizeDependencies: () => ({
        numValues: {
          dependencyType: "stateVariable",
          variableName: "numValues"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numValues];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          type: {
            dependencyType: "stateVariable",
            variableName: "type"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let childInfo = stateValues.childForValue[arrayKey];
          dependenciesByKey[arrayKey] = {
            anythingForSelectedType: {
              dependencyType: "child",
              childGroups: ["anything"],
              variableNames: ["value", "values"],
              childIndices: [childInfo.child],
              variablesOptional: true
            },
            valueIndex: {
              dependencyType: "value",
              value: childInfo.valueIndex
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        componentIdx
      }) {
        let values2 = {};
        for (let arrayKey of arrayKeys) {
          if (dependencyValuesByKey[arrayKey].anythingForSelectedType && dependencyValuesByKey[arrayKey].anythingForSelectedType.length === 1) {
            let child = dependencyValuesByKey[arrayKey].anythingForSelectedType[0];
            let value;
            if (child.stateValues.values) {
              value = child.stateValues.values[dependencyValuesByKey[arrayKey].valueIndex];
            } else {
              value = child.stateValues.value;
            }
            values2[arrayKey] = convertValueToType(
              value,
              globalDependencyValues.type
            );
          }
        }
        return {
          setValue: { values: values2 },
          setCreateComponentOfType: {
            values: globalDependencyValues.type
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class ComponentListOfListsWithSelectableType extends ComponentWithSelectableType {
  static componentType = "_componentListOfListsWithSelectableType";
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["_base"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "The data type of the values: number, math, text, letters, or boolean."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = [];
    let breakIntoListsByParensAndAddType = function({
      matchedChildren,
      componentAttributes,
      parentAttributes,
      nComponents,
      stateIdInfo
    }) {
      let results = breakEmbeddedStringsIntoParensPieces({
        componentList: matchedChildren,
        removeParens: true
      });
      if (results.success !== true) {
        return { success: false };
      }
      let diagnostics = [];
      let type = componentAttributes.type?.value;
      if (!type) {
        type = parentAttributes.type?.value;
      }
      if (!type) {
        type = "number";
      } else if (!["number", "letters", "math", "text", "boolean"].includes(type)) {
        diagnostics.push({
          message: `Invalid type ${type}, setting type to number.`,
          type: "warning"
        });
        type = "number";
      }
      const newChildren = results.pieces.map((x2) => ({
        type: "serialized",
        componentType: "_componentListWithSelectableType",
        componentIdx: nComponents++,
        stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
        attributes: {
          type: {
            type: "primitive",
            name: "type",
            primitive: { type: "string", value: type }
          }
        },
        doenetAttributes: {},
        children: x2,
        state: {}
      }));
      return {
        success: true,
        newChildren,
        diagnostics,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakIntoListsByParensAndAddType
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "lists",
        componentTypes: ["_componentListWithSelectableType"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.value;
    stateVariableDefinitions.numLists = {
      returnDependencies: () => ({
        listChildren: {
          dependencyType: "child",
          childGroups: ["lists"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numLists: dependencyValues.listChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.lists = {
      isArray: true,
      entryPrefixes: ["list"],
      returnArraySizeDependencies: () => ({
        numLists: {
          dependencyType: "stateVariable",
          variableName: "numLists"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numLists];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          type: {
            dependencyType: "stateVariable",
            variableName: "type"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            listChildren: {
              dependencyType: "child",
              childGroups: ["lists"],
              variableNames: ["values", "type"],
              childIndices: [arrayKey]
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let lists = {};
        for (let arrayKey of arrayKeys) {
          if (dependencyValuesByKey[arrayKey].listChildren && dependencyValuesByKey[arrayKey].listChildren.length === 1) {
            let listChild = dependencyValuesByKey[arrayKey].listChildren[0];
            if (listChild.stateValues.type === globalDependencyValues.type) {
              lists[arrayKey] = listChild.stateValues.values;
            } else {
              lists[arrayKey] = listChild.stateValues.values.map(
                (x2) => convertValueToType(
                  x2,
                  globalDependencyValues.type
                )
              );
            }
          }
        }
        return {
          setValue: { lists }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
function convertValueToType(value, type) {
  if (Array.isArray(value)) {
    value = value[0];
  }
  if (type === "number") {
    if (value instanceof me$3.class) {
      let num = value.evaluate_to_constant();
      return num;
    }
    return Number(value);
  } else if (type === "math") {
    if (typeof value === "string") {
      try {
        return me$3.fromAst(textToAst.convert(value));
      } catch (e3) {
      }
    }
    return convertValueToMathExpression(value);
  } else if (type === "boolean") {
    return Boolean(value);
  } else {
    return String(value);
  }
}
const ComponentWithSelectableType$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ComponentListOfListsWithSelectableType,
  ComponentListWithSelectableType,
  ComponentWithSelectableType
}, Symbol.toStringTag, { value: "Module" }));
const { max } = me$3.math;
class SideBySide extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "sideBySide";
  static componentDocs = {
    summary: "Renders children in a side-by-side horizontal layout"
  };
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      createComponentOfType: "componentSize",
      description: "Total rendered width of the side-by-side container."
    };
    attributes.widths = {
      createComponentOfType: "_componentSizeList",
      description: "List of widths for each panel."
    };
    attributes.margins = {
      createComponentOfType: "_componentSizeList",
      description: "List of margin widths between panels."
    };
    attributes.valign = {
      createComponentOfType: "text",
      toLowerCase: true,
      validValues: [
        { value: "top", description: "Align panels to the top." },
        {
          value: "middle",
          description: "Vertically center panels."
        },
        {
          value: "bottom",
          description: "Align panels to the bottom."
        }
      ],
      description: "Default vertical alignment for all panels."
    };
    attributes.valigns = {
      createComponentOfType: "textList",
      toLowerCase: true,
      validValues: [
        { value: "top", description: "Align the panel to the top." },
        {
          value: "middle",
          description: "Vertically center the panel."
        },
        {
          value: "bottom",
          description: "Align the panel to the bottom."
        }
      ],
      description: "Per-panel vertical alignment values."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "blocks",
        componentTypes: ["_block"]
      },
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.listItemInlineAlignment = {
      forRenderer: true,
      additionalStateVariablesDefined: [
        { variableName: "childrenToRenderInlineForListItem" }
      ],
      returnDependencies: () => ({
        parentChildrenToRenderInlineForListItem: {
          dependencyType: "parentStateVariable",
          variableName: "childrenToRenderInlineForListItem"
        },
        blockChildren: {
          dependencyType: "child",
          childGroups: ["blocks"]
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        const shouldRenderInline = dependencyValues.parentChildrenToRenderInlineForListItem?.map((c2) => c2.componentIdx).includes(componentIdx);
        if (!shouldRenderInline) {
          return {
            setValue: {
              listItemInlineAlignment: "none",
              childrenToRenderInlineForListItem: []
            }
          };
        }
        const firstPanel = dependencyValues.blockChildren?.[0];
        return {
          setValue: {
            listItemInlineAlignment: firstPanel?.componentType === "p" ? "baseline" : "flex-start",
            childrenToRenderInlineForListItem: dependencyValues.blockChildren
          }
        };
      }
    };
    stateVariableDefinitions.numPanels = {
      forRenderer: true,
      returnDependencies: () => ({
        blockChildren: {
          dependencyType: "child",
          childGroups: ["blocks"],
          skipComponentIndices: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numPanels: dependencyValues.blockChildren.length
          },
          checkForActualChange: { numPanels: true }
        };
      }
    };
    stateVariableDefinitions.essentialWidths = {
      isArray: true,
      entryPrefixes: ["essentialWidth"],
      defaultValueByArrayKey: () => null,
      hasEssential: true,
      returnArraySizeDependencies: () => ({
        numPanels: {
          dependencyType: "stateVariable",
          variableName: "numPanels"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numPanels];
      },
      returnArrayDependenciesByKey: () => ({}),
      arrayDefinitionByKey({ arrayKeys }) {
        let essentialWidths = {};
        for (let arrayKey of arrayKeys) {
          essentialWidths[arrayKey] = true;
        }
        return { useEssentialOrDefaultValue: { essentialWidths } };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let value = {};
        for (let arrayKey in desiredStateVariableValues.essentialWidths) {
          value[arrayKey] = desiredStateVariableValues.essentialWidths[arrayKey];
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "essentialWidths",
              value
            }
          ]
        };
      }
    };
    stateVariableDefinitions.allWidthsSpecified = {
      additionalStateVariablesDefined: ["widthsAbsolute"],
      returnDependencies() {
        return {
          numPanels: {
            dependencyType: "stateVariable",
            variableName: "numPanels"
          },
          widthAttr: {
            dependencyType: "attributeComponent",
            attributeName: "width",
            variableNames: ["componentSize"]
          },
          widthsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "widths",
            variableNames: ["numComponents", "componentSizes"]
          },
          parentWidths: {
            dependencyType: "parentStateVariable",
            parentComponentType: "sbsGroup",
            variableName: `specifiedWidths`
          },
          parentWidthsAbsolute: {
            dependencyType: "parentStateVariable",
            parentComponentType: "sbsGroup",
            variableName: `widthsAbsolute`
          },
          essentialWidth: {
            dependencyType: "stateVariable",
            variableName: `essentialWidths`
          }
        };
      },
      definition({ dependencyValues }) {
        let allWidthsSpecified = [];
        let widthsAbsolute = null;
        let foundAbsolute = false;
        let diagnostics = [];
        let nWidthsSpecifiedFromAttrs;
        let usingSingleWidth = false;
        if (dependencyValues.widthsAttr !== null) {
          nWidthsSpecifiedFromAttrs = dependencyValues.widthsAttr.stateValues.numComponents;
        } else if (dependencyValues.widthAttr !== null) {
          nWidthsSpecifiedFromAttrs = dependencyValues.numPanels;
          usingSingleWidth = true;
        } else {
          nWidthsSpecifiedFromAttrs = 0;
        }
        for (let ind = 0; ind < dependencyValues.numPanels; ind++) {
          let thisAbsolute = null;
          if (ind < nWidthsSpecifiedFromAttrs) {
            if (usingSingleWidth) {
              if (dependencyValues.widthAttr.stateValues.componentSize) {
                allWidthsSpecified[ind] = dependencyValues.widthAttr.stateValues.componentSize.size;
                thisAbsolute = Boolean(
                  dependencyValues.widthAttr.stateValues.componentSize.isAbsolute
                );
              } else {
                allWidthsSpecified[ind] = null;
              }
            } else {
              if (dependencyValues.widthsAttr.stateValues.componentSizes[ind]) {
                allWidthsSpecified[ind] = dependencyValues.widthsAttr.stateValues.componentSizes[ind].size;
                thisAbsolute = Boolean(
                  dependencyValues.widthsAttr.stateValues.componentSizes[ind].isAbsolute
                );
              } else {
                allWidthsSpecified[ind] = null;
              }
            }
          } else {
            if (dependencyValues.parentWidths) {
              if (dependencyValues.essentialWidth[ind] === null) {
                allWidthsSpecified[ind] = dependencyValues.parentWidths[ind];
              } else {
                allWidthsSpecified[ind] = dependencyValues.essentialWidth[ind];
              }
              thisAbsolute = dependencyValues.parentWidthsAbsolute;
            } else {
              allWidthsSpecified[ind] = dependencyValues.essentialWidth[ind];
            }
          }
          if (thisAbsolute) {
            foundAbsolute = true;
          }
        }
        if (foundAbsolute) {
          diagnostics.push({
            message: "`<sideBySide>` is not implemented for absolute measurements. Setting widths to relative.",
            type: "warning"
          });
        }
        widthsAbsolute = false;
        allWidthsSpecified = allWidthsSpecified.map(
          (x2) => Number.isFinite(x2) ? x2 : null
        );
        return {
          setValue: { allWidthsSpecified, widthsAbsolute },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.essentialMargins = {
      isArray: true,
      entryPrefixes: ["essentialMargin"],
      hasEssential: true,
      defaultValueByArrayKey: () => null,
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey: () => ({}),
      arrayDefinitionByKey({ arrayKeys }) {
        let essentialMargins = {};
        for (let arrayKey of arrayKeys) {
          essentialMargins[arrayKey] = true;
        }
        return { useEssentialOrDefaultValue: { essentialMargins } };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let value = {};
        for (let arrayKey in desiredStateVariableValues.essentialMargins) {
          value[arrayKey] = desiredStateVariableValues.essentialMargins[arrayKey];
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "essentialMargins",
              value
            }
          ]
        };
      }
    };
    stateVariableDefinitions.allMarginsSpecified = {
      additionalStateVariablesDefined: ["marginsAbsolute"],
      returnDependencies() {
        return {
          numPanels: {
            dependencyType: "stateVariable",
            variableName: "numPanels"
          },
          marginsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "margins",
            variableNames: ["numComponents", "componentSizes"]
          },
          parentMargins: {
            dependencyType: "parentStateVariable",
            parentComponentType: "sbsGroup",
            variableName: `specifiedMargins`
          },
          parentMarginsAbsolute: {
            dependencyType: "parentStateVariable",
            parentComponentType: "sbsGroup",
            variableName: `marginsAbsolute`
          },
          essentialMargins: {
            dependencyType: "stateVariable",
            variableName: `essentialMargins`
          }
        };
      },
      definition({ dependencyValues }) {
        let allMarginsSpecified = [];
        let marginsAbsolute = null;
        let secondMarginAbsolute = null;
        let diagnostics = [];
        if (dependencyValues.marginsAttr === null) {
          if (dependencyValues.parentMargins) {
            marginsAbsolute = dependencyValues.parentMarginsAbsolute;
            for (let ind = 0; ind < 2; ind++) {
              if (dependencyValues.essentialMargins[ind] === null) {
                allMarginsSpecified[ind] = dependencyValues.parentMargins[ind];
              } else {
                allMarginsSpecified[ind] = dependencyValues.essentialMargins[ind];
              }
            }
          } else {
            for (let ind = 0; ind < 2; ind++) {
              allMarginsSpecified[ind] = dependencyValues.essentialMargins[ind];
            }
          }
        } else if (dependencyValues.marginsAttr.stateValues.numComponents === 0) {
          for (let ind = 0; ind < 2; ind++) {
            allMarginsSpecified[ind] = dependencyValues.essentialMargins[ind];
          }
        } else if (dependencyValues.marginsAttr.stateValues.numComponents === 1) {
          let margin = null;
          if (dependencyValues.marginsAttr.stateValues.componentSizes[0]) {
            margin = dependencyValues.marginsAttr.stateValues.componentSizes[0].size;
            if (!Number.isFinite(margin)) {
              margin = null;
            }
            marginsAbsolute = Boolean(
              dependencyValues.marginsAttr.stateValues.componentSizes[0].isAbsolute
            );
          }
          allMarginsSpecified = [margin, margin];
        } else {
          if (dependencyValues.marginsAttr.stateValues.componentSizes[0]) {
            marginsAbsolute = Boolean(
              dependencyValues.marginsAttr.stateValues.componentSizes[0].isAbsolute
            );
          } else {
            marginsAbsolute = false;
          }
          if (dependencyValues.marginsAttr.stateValues.componentSizes[1]) {
            secondMarginAbsolute = Boolean(
              dependencyValues.marginsAttr.stateValues.componentSizes[1].isAbsolute
            );
          } else {
            secondMarginAbsolute = false;
          }
          allMarginsSpecified = dependencyValues.marginsAttr.stateValues.componentSizes.slice(0, 2).map(
            (x2) => x2 && Number.isFinite(x2.size) ? x2.size : null
          );
        }
        if (secondMarginAbsolute || marginsAbsolute) {
          diagnostics.push({
            message: "`<sideBySide>` is not implemented for absolute measurements. Setting margins to relative.",
            type: "warning"
          });
        }
        marginsAbsolute = false;
        return {
          setValue: { allMarginsSpecified, marginsAbsolute },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.absoluteMeasurements = {
      description: "Whether children's widths are interpreted in absolute units.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        widthsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "widthsAbsolute"
        },
        marginsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "marginsAbsolute"
        }
      }),
      definition({ dependencyValues }) {
        return { setValue: { absoluteMeasurements: false } };
      }
    };
    stateVariableDefinitions.allWidths = {
      description: "All children's widths combined.",
      additionalStateVariablesDefined: [
        "allMargins",
        {
          variableName: "gapWidth",
          public: true,
          description: "The gap width between panels as a percentage of the total container width.",
          shadowingInstructions: {
            createComponentOfType: "number"
          },
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        numPanels: {
          dependencyType: "stateVariable",
          variableName: "numPanels"
        },
        allWidthsSpecified: {
          dependencyType: "stateVariable",
          variableName: "allWidthsSpecified"
        },
        allMarginsSpecified: {
          dependencyType: "stateVariable",
          variableName: "allMarginsSpecified"
        },
        absoluteMeasurements: {
          dependencyType: "stateVariable",
          variableName: "absoluteMeasurements"
        }
      }),
      definition({ dependencyValues }) {
        let diagnostics = [];
        let gapWidth = 0;
        let allWidths = [...dependencyValues.allWidthsSpecified];
        let allMargins = [...dependencyValues.allMarginsSpecified];
        let totalWidthSpecified = 0;
        let nWidthsUndefined = 0;
        for (let ind = 0; ind < dependencyValues.numPanels; ind++) {
          let width = allWidths[ind];
          if (width === null) {
            nWidthsUndefined++;
          } else {
            totalWidthSpecified += width;
          }
        }
        let totalMarginSpecified = 0;
        let nMarginsUndefined = 0;
        for (let ind = 0; ind < 2; ind++) {
          let margin = allMargins[ind];
          if (margin === null) {
            nMarginsUndefined++;
          } else {
            totalMarginSpecified += margin;
          }
        }
        totalMarginSpecified *= dependencyValues.numPanels;
        if (!dependencyValues.absoluteMeasurements) {
          if (totalWidthSpecified + totalMarginSpecified >= 100) {
            let normalization = 100 / (totalWidthSpecified + totalMarginSpecified);
            for (let ind = 0; ind < dependencyValues.numPanels; ind++) {
              if (allWidths[ind] === null) {
                allWidths[ind] = 0;
              } else {
                allWidths[ind] *= normalization;
              }
            }
            for (let ind = 0; ind < 2; ind++) {
              let margin = allMargins[ind];
              if (margin === null) {
                allMargins[ind] = 0;
              } else {
                allMargins[ind] *= normalization;
              }
            }
          } else {
            if (nWidthsUndefined > 0) {
              let newWidth = (100 - (totalWidthSpecified + totalMarginSpecified)) / nWidthsUndefined;
              for (let ind = 0; ind < dependencyValues.numPanels; ind++) {
                if (allWidths[ind] === null) {
                  allWidths[ind] = newWidth;
                }
              }
              for (let ind = 0; ind < 2; ind++) {
                let margin = allMargins[ind];
                if (margin === null) {
                  allMargins[ind] = 0;
                }
              }
            } else if (nMarginsUndefined > 0) {
              let newMargin = (100 - (totalWidthSpecified + totalMarginSpecified)) / (nMarginsUndefined * dependencyValues.numPanels);
              for (let ind = 0; ind < 2; ind++) {
                if (allMargins[ind] === null) {
                  allMargins[ind] = newMargin;
                }
              }
            } else if (dependencyValues.numPanels > 1) {
              gapWidth = (100 - (totalWidthSpecified + totalMarginSpecified)) / (dependencyValues.numPanels - 1);
            } else if (dependencyValues.numPanels === 1) {
              allMargins[1] = 100 - (allMargins[0] + allWidths[0]);
            } else {
              diagnostics.push({
                message: "Invalid `<sideBySide>`: it must have at least one block child.",
                type: "warning"
              });
            }
          }
        }
        return {
          setValue: { allWidths, allMargins, gapWidth },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.widths = {
      description: "The list of child widths.",
      public: true,
      isArray: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      entryPrefixes: ["width"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numPanels: {
          dependencyType: "stateVariable",
          variableName: "numPanels"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numPanels];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          allWidths: {
            dependencyType: "stateVariable",
            variableName: `allWidths`
          },
          widthsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "widths",
            variableNames: [`numComponents`]
          },
          widthAttr: {
            dependencyType: "attributeComponent",
            attributeName: "width",
            variableNames: ["componentSize"]
          },
          absoluteMeasurements: {
            dependencyType: "stateVariable",
            variableName: "absoluteMeasurements"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            widthsAttr: {
              dependencyType: "attributeComponent",
              attributeName: "widths",
              variableNames: [`componentSize${varEnding}`]
            },
            essentialWidth: {
              dependencyType: "stateVariable",
              variableName: `essentialWidth${varEnding}`
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let widths = {};
        for (let arrayKey of arrayKeys) {
          widths[arrayKey] = globalDependencyValues.allWidths[arrayKey];
        }
        return { setValue: { widths } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey,
        globalDependencyValues,
        arraySize
      }) {
        let nWidthsSpecifiedFromAttrs;
        let usingSingleWidth = false;
        if (globalDependencyValues.widthsAttr !== null) {
          nWidthsSpecifiedFromAttrs = globalDependencyValues.widthsAttr.stateValues.numComponents;
        } else if (globalDependencyValues.widthAttr !== null) {
          nWidthsSpecifiedFromAttrs = arraySize[0];
          usingSingleWidth = true;
        } else {
          nWidthsSpecifiedFromAttrs = 0;
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.widths) {
          let arrayIndex = Number(arrayKey);
          if (arrayIndex < nWidthsSpecifiedFromAttrs) {
            if (usingSingleWidth) {
              instructions.push({
                setDependency: "widthAttr",
                desiredValue: {
                  size: desiredStateVariableValues.widths[arrayKey],
                  isAbsolute: globalDependencyValues.absoluteMeasurements
                },
                variableIndex: 0
              });
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].widthsAttr,
                desiredValue: {
                  size: desiredStateVariableValues.widths[arrayKey],
                  isAbsolute: globalDependencyValues.absoluteMeasurements
                },
                variableIndex: 0
              });
            }
          } else {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].essentialWidth,
              desiredValue: desiredStateVariableValues.widths[arrayKey]
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.margins = {
      description: "Margins between adjacent children.",
      public: true,
      isArray: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      entryPrefixes: ["margin"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          allMargins: {
            dependencyType: "stateVariable",
            variableName: `allMargins`
          },
          marginsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "margins",
            variableNames: [
              "componentSize1",
              "componentSize2",
              "numComponents"
            ]
          },
          absoluteMeasurements: {
            dependencyType: "stateVariable",
            variableName: "absoluteMeasurements"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            essentialMargin: {
              dependencyType: "stateVariable",
              variableName: `essentialMargin${varEnding}`
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let margins = {};
        for (let arrayKey of arrayKeys) {
          margins[arrayKey] = globalDependencyValues.allMargins[arrayKey];
        }
        return { setValue: { margins } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyNamesByKey
      }) {
        let instructions = [];
        if (globalDependencyValues.marginsAttr === null) {
          for (let arrayKey in desiredStateVariableValues.margins) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].essentialMargin,
              desiredValue: desiredStateVariableValues.margins[arrayKey]
            });
          }
        } else if (!(globalDependencyValues.marginsAttr.stateValues.componentSize1 && Number.isFinite(
          globalDependencyValues.marginsAttr.stateValues.componentSize1.size
        ))) {
          if ("0" in desiredStateVariableValues.margins) {
            instructions.push({
              setDependency: "marginsAttr",
              desiredValue: {
                size: desiredStateVariableValues.margins[0],
                isAbsolute: globalDependencyValues.absoluteMeasurements
              },
              variableIndex: 0
            });
          } else {
            instructions.push({
              setDependency: "marginsAttr",
              desiredValue: {
                size: desiredStateVariableValues.margins[1],
                isAbsolute: globalDependencyValues.absoluteMeasurements
              },
              variableIndex: 0
            });
          }
          if ("1" in desiredStateVariableValues.margins) {
            if (globalDependencyValues.marginsAttr.stateValues.numComponents > 1) {
              instructions.push({
                setDependency: "marginsAttr",
                desiredValue: {
                  size: desiredStateVariableValues.margins[1],
                  isAbsolute: globalDependencyValues.absoluteMeasurements
                },
                variableIndex: 1
              });
            }
          }
        } else {
          let symmetricMargins = globalDependencyValues.marginsAttr.stateValues.numComponents === 1;
          for (let arrayKey in desiredStateVariableValues.margins) {
            if (symmetricMargins) {
              instructions.push({
                setDependency: "marginsAttr",
                desiredValue: {
                  size: desiredStateVariableValues.margins[arrayKey],
                  isAbsolute: globalDependencyValues.absoluteMeasurements
                },
                variableIndex: 0
              });
            } else {
              instructions.push({
                setDependency: "marginsAttr",
                desiredValue: {
                  size: desiredStateVariableValues.margins[arrayKey],
                  isAbsolute: globalDependencyValues.absoluteMeasurements
                },
                variableIndex: arrayKey
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.essentialValigns = {
      isArray: true,
      entryPrefixes: ["essentialValign"],
      defaultValueByArrayKey: () => null,
      hasEssential: true,
      returnArraySizeDependencies: () => ({
        numPanels: {
          dependencyType: "stateVariable",
          variableName: "numPanels"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numPanels];
      },
      returnArrayDependenciesByKey: () => ({}),
      arrayDefinitionByKey({ arrayKeys }) {
        let essentialValigns = {};
        for (let arrayKey of arrayKeys) {
          essentialValigns[arrayKey] = true;
        }
        return { useEssentialOrDefaultValue: { essentialValigns } };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let value = {};
        for (let arrayKey in desiredStateVariableValues.essentialValigns) {
          value[arrayKey] = desiredStateVariableValues.essentialValigns[arrayKey];
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "essentialValigns",
              value
            }
          ]
        };
      }
    };
    stateVariableDefinitions.valigns = {
      description: "Vertical alignment of each child.",
      public: true,
      isArray: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      entryPrefixes: ["valign"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numPanels: {
          dependencyType: "stateVariable",
          variableName: "numPanels"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numPanels];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          valignAttr: {
            dependencyType: "attributeComponent",
            attributeName: "valign",
            variableNames: ["value"]
          },
          valignsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "valigns",
            variableNames: ["numComponents"]
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            valignsAttr: {
              dependencyType: "attributeComponent",
              attributeName: "valigns",
              variableNames: [`text${varEnding}`]
            },
            parentValign: {
              dependencyType: "parentStateVariable",
              parentComponentType: "sbsGroup",
              variableName: `valign${varEnding}`
            },
            parentValignAbsolute: {
              dependencyType: "parentStateVariable",
              parentComponentType: "sbsGroup",
              variableName: `valignAbsolute${varEnding}`
            },
            essentialValign: {
              dependencyType: "stateVariable",
              variableName: `essentialValign${varEnding}`
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        arraySize
      }) {
        let valigns = {};
        let nValignsSpecifiedFromAttrs;
        let usingSingleValign = false;
        if (globalDependencyValues.valignsAttr !== null) {
          nValignsSpecifiedFromAttrs = globalDependencyValues.valignsAttr.stateValues.numComponents;
        } else if (globalDependencyValues.valignAttr !== null) {
          nValignsSpecifiedFromAttrs = arraySize[0];
          usingSingleValign = true;
        } else {
          nValignsSpecifiedFromAttrs = 0;
        }
        for (let arrayKey of arrayKeys) {
          let arrayIndex = Number(arrayKey);
          if (arrayIndex < nValignsSpecifiedFromAttrs) {
            if (usingSingleValign) {
              valigns[arrayKey] = globalDependencyValues.valignAttr.stateValues.value.toLowerCase();
            } else {
              let varEnding = arrayIndex + 1;
              valigns[arrayKey] = dependencyValuesByKey[arrayKey].valignsAttr.stateValues[`text${varEnding}`].toLowerCase();
            }
            if (!["top", "middle", "bottom"].includes(
              valigns[arrayKey]
            )) {
              valigns[arrayKey] = "top";
            }
          } else {
            if (dependencyValuesByKey[arrayKey].parentValign) {
              if (dependencyValuesByKey[arrayKey].essentialValign === null) {
                valigns[arrayKey] = dependencyValuesByKey[arrayKey].parentValign;
              } else {
                valigns[arrayKey] = dependencyValuesByKey[arrayKey].essentialValign;
              }
            } else {
              if (dependencyValuesByKey[arrayKey].essentialValign === null) {
                valigns[arrayKey] = "top";
              } else {
                valigns[arrayKey] = dependencyValuesByKey[arrayKey].essentialValign;
              }
            }
          }
        }
        return { setValue: { valigns } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        globalDependencyValues,
        arraySize
      }) {
        let nValignsSpecifiedFromAttrs;
        let usingSingleValign = false;
        if (globalDependencyValues.valignsAttr !== null) {
          nValignsSpecifiedFromAttrs = globalDependencyValues.valignsAttr.stateValues.numComponents;
        } else if (globalDependencyValues.valignAttr !== null) {
          nValignsSpecifiedFromAttrs = arraySize[0];
          usingSingleValign = true;
        } else {
          nValignsSpecifiedFromAttrs = 0;
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.valigns) {
          let desiredValue = desiredStateVariableValues.valigns[arrayKey].toLowerCase();
          if (!["top", "middle", "bottom"].includes(desiredValue)) {
            continue;
          }
          let arrayIndex = Number(arrayKey);
          if (arrayIndex < nValignsSpecifiedFromAttrs) {
            if (usingSingleValign) {
              instructions.push({
                setDependency: "valignAttr",
                desiredValue,
                variableIndex: 0
              });
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].valignsAttr,
                desiredValue,
                variableIndex: 0
              });
            }
          } else {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].essentialValign,
              desiredValue
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class SbsGroup extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "sbsGroup";
  static rendererType = "containerBlock";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static componentDocs = {
    summary: "A group of `<sideBySide>` components that share alignment and spacing"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      createComponentOfType: "componentSize",
      description: "Total rendered width of the side-by-side container."
    };
    attributes.widths = {
      createComponentOfType: "_componentSizeList",
      description: "List of widths for each panel."
    };
    attributes.margins = {
      createComponentOfType: "_componentSizeList",
      description: "List of margin widths between panels."
    };
    attributes.valign = {
      createComponentOfType: "text",
      toLowerCase: true,
      validValues: [
        { value: "top", description: "Align panels to the top." },
        {
          value: "middle",
          description: "Vertically center panels."
        },
        {
          value: "bottom",
          description: "Align panels to the bottom."
        }
      ],
      description: "Default vertical alignment for all panels."
    };
    attributes.valigns = {
      createComponentOfType: "textList",
      toLowerCase: true,
      validValues: [
        { value: "top", description: "Align the panel to the top." },
        {
          value: "middle",
          description: "Vertically center the panel."
        },
        {
          value: "bottom",
          description: "Align the panel to the bottom."
        }
      ],
      description: "Per-panel vertical alignment values."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "sideBySides",
        componentTypes: ["sideBySide"]
      },
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnPassThroughListItemChildStateVariableDefinitions()
    );
    stateVariableDefinitions.maxNPanelsPerRow = {
      // forRenderer: true,
      returnDependencies: () => ({
        sideBySideChildren: {
          dependencyType: "child",
          childGroups: ["sideBySides"],
          variableNames: ["numPanels"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            maxNPanelsPerRow: dependencyValues.sideBySideChildren.length === 0 ? 0 : max(
              dependencyValues.sideBySideChildren.map(
                (x2) => x2.stateValues.numPanels
              )
            )
          },
          checkForActualChange: { maxNPanelsPerRow: true }
        };
      }
    };
    stateVariableDefinitions.specifiedWidths = {
      additionalStateVariablesDefined: [
        {
          variableName: "widthsAbsoluteArray",
          isArray: true
        }
      ],
      isArray: true,
      entryPrefixes: ["specifiedWidth"],
      hasEssential: true,
      defaultValueByArrayKey: () => null,
      returnArraySizeDependencies: () => ({
        maxNPanelsPerRow: {
          dependencyType: "stateVariable",
          variableName: "maxNPanelsPerRow"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.maxNPanelsPerRow];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          widthAttr: {
            dependencyType: "attributeComponent",
            attributeName: "width",
            variableNames: ["componentSize"]
          },
          widthsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "widths",
            variableNames: ["numComponents"]
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            widthsAttr: {
              dependencyType: "attributeComponent",
              attributeName: "widths",
              variableNames: [`componentSize${varEnding}`]
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        arraySize
      }) {
        let specifiedWidths = {};
        let essentialSpecifiedWidths = {};
        let widthsAbsoluteArray = {};
        let nWidthsSpecifiedFromAttrs;
        let usingSingleWidth = false;
        if (globalDependencyValues.widthsAttr !== null) {
          nWidthsSpecifiedFromAttrs = globalDependencyValues.widthsAttr.stateValues.numComponents;
        } else if (globalDependencyValues.widthAttr !== null) {
          nWidthsSpecifiedFromAttrs = arraySize[0];
          usingSingleWidth = true;
        } else {
          nWidthsSpecifiedFromAttrs = 0;
        }
        for (let arrayKey of arrayKeys) {
          let arrayIndex = Number(arrayKey);
          if (arrayIndex < nWidthsSpecifiedFromAttrs) {
            if (usingSingleWidth) {
              if (globalDependencyValues.widthAttr.stateValues.componentSize) {
                specifiedWidths[arrayKey] = globalDependencyValues.widthAttr.stateValues.componentSize.size;
                widthsAbsoluteArray[arrayKey] = globalDependencyValues.widthAttr.stateValues.componentSize.isAbsolute;
              }
            } else {
              let varEnding = arrayIndex + 1;
              let widthComponentSize = dependencyValuesByKey[arrayKey].widthsAttr.stateValues[`componentSize${varEnding}`];
              if (widthComponentSize) {
                specifiedWidths[arrayKey] = widthComponentSize.size;
                widthsAbsoluteArray[arrayKey] = widthComponentSize.isAbsolute;
              }
            }
          } else {
            essentialSpecifiedWidths[arrayKey] = true;
            widthsAbsoluteArray[arrayKey] = null;
          }
        }
        let result = {
          setValue: { widthsAbsoluteArray, specifiedWidths }
        };
        if (Object.keys(essentialSpecifiedWidths).length > 0) {
          result.useEssentialOrDefaultValue = {
            specifiedWidths: essentialSpecifiedWidths
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        globalDependencyValues,
        stateValues,
        arraySize
      }) {
        let nWidthsSpecified;
        let usingSingleWidth = false;
        if (globalDependencyValues.widthsAttr !== null) {
          nWidthsSpecified = globalDependencyValues.widthsAttr.stateValues.numComponents;
        } else if (globalDependencyValues.widthAttr !== null) {
          nWidthsSpecified = arraySize[0];
          usingSingleWidth = true;
        } else {
          nWidthsSpecified = 0;
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.specifiedWidths) {
          let arrayIndex = Number(arrayKey);
          if (arrayIndex < nWidthsSpecified) {
            if (usingSingleWidth) {
              instructions.push({
                setDependency: "widthAttr",
                desiredValue: {
                  size: desiredStateVariableValues.specifiedWidths[arrayKey],
                  isAbsolute: stateValues.widthsAbsolute
                },
                variableIndex: 0
              });
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].widthsAttr,
                desiredValue: {
                  size: desiredStateVariableValues.specifiedWidths[arrayKey],
                  isAbsolute: stateValues.widthsAbsolute
                },
                variableIndex: 0
              });
            }
          } else {
            instructions.push({
              setEssentialValue: "specifiedWidths",
              value: {
                [arrayKey]: desiredStateVariableValues.specifiedWidths[arrayKey]
              }
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.widthsAbsolute = {
      returnDependencies: () => ({
        widthsAbsoluteArray: {
          dependencyType: "stateVariable",
          variableName: "widthsAbsoluteArray"
        }
      }),
      definition({ dependencyValues }) {
        let widthsAbsolute = null;
        let foundAbsolute = false;
        let diagnostics = [];
        for (let ind in dependencyValues.widthsAbsoluteArray) {
          let thisAbsolute = dependencyValues.widthsAbsoluteArray[ind];
          if (thisAbsolute !== null) {
            thisAbsolute = Boolean(thisAbsolute);
            if (thisAbsolute) {
              foundAbsolute = true;
            }
          }
        }
        if (foundAbsolute) {
          diagnostics.push({
            message: "`<sbsGroup>` is not implemented for absolute measurements. Setting widths to relative.",
            type: "warning"
          });
        }
        widthsAbsolute = false;
        return {
          setValue: { widthsAbsolute },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.specifiedMargins = {
      additionalStateVariablesDefined: [
        {
          variableName: "marginsAbsoluteArray",
          isArray: true,
          entryPrefixes: ["marginAbsolute"]
        }
      ],
      isArray: true,
      entryPrefixes: ["specifiedMargin"],
      hasEssential: true,
      defaultValueByArrayKey: () => null,
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          marginsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "margins",
            variableNames: [
              "componentSize1",
              "componentSize2",
              "numComponents"
            ]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({
        dependencyValuesByKey,
        globalDependencyValues,
        arrayKeys
      }) {
        let specifiedMargins = {};
        let essentialSpecifiedMargins = {};
        let marginsAbsoluteArray = {};
        if (globalDependencyValues.marginsAttr === null || globalDependencyValues.marginsAttr.stateValues.numComponents === 0) {
          for (let arrayKey of arrayKeys) {
            essentialSpecifiedMargins[arrayKey] = true;
            marginsAbsoluteArray[arrayKey] = null;
          }
        } else if (globalDependencyValues.marginsAttr.stateValues.numComponents === 1) {
          let margin = null, absolute = null;
          if (globalDependencyValues.marginsAttr.stateValues.componentSize1) {
            margin = globalDependencyValues.marginsAttr.stateValues.componentSize1.size;
            if (!Number.isFinite(margin)) {
              margin = null;
            }
            absolute = Boolean(
              globalDependencyValues.marginsAttr.stateValues.componentSize1.isAbsolute
            );
          }
          for (let arrayKey of arrayKeys) {
            specifiedMargins[arrayKey] = margin;
            marginsAbsoluteArray[arrayKey] = absolute;
          }
        } else {
          for (let arrayKey of arrayKeys) {
            if (globalDependencyValues.marginsAttr.stateValues[`componentSize${Number(arrayKey) + 1}`]) {
              let margin = globalDependencyValues.marginsAttr.stateValues[`componentSize${Number(arrayKey) + 1}`].size;
              if (!Number.isFinite(margin)) {
                margin = null;
              }
              let absolute = Boolean(
                globalDependencyValues.marginsAttr.stateValues[`componentSize${Number(arrayKey) + 1}`].isAbsolute
              );
              specifiedMargins[arrayKey] = margin;
              marginsAbsoluteArray[arrayKey] = absolute;
            }
          }
        }
        let result = {
          setValue: { marginsAbsoluteArray, specifiedMargins }
        };
        if (Object.keys(essentialSpecifiedMargins).length > 0) {
          result.useEssentialOrDefaultValue = {
            specifiedMargins: essentialSpecifiedMargins
          };
        }
        return result;
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        globalDependencyValues,
        stateValues
      }) {
        let instructions = [];
        if (globalDependencyValues.marginsAttr === null) {
          instructions.push({
            setEssentialValue: "specifiedMargins",
            value: desiredStateVariableValues.specifiedMargins
          });
        } else if (!(globalDependencyValues.marginsAttr.stateValues.componentSize1 && Number.isFinite(
          globalDependencyValues.marginsAttr.stateValues.componentSize1.size
        ))) {
          if ("0" in desiredStateVariableValues.specifiedMargins) {
            instructions.push({
              setDependency: "marginsAttr",
              desiredValue: {
                size: desiredStateVariableValues.specifiedMargins[0],
                isAbsolute: await stateValues.marginsAbsolute
              },
              variableIndex: 0
            });
          } else {
            instructions.push({
              setDependency: "marginsAttr",
              desiredValue: {
                size: desiredStateVariableValues.specifiedMargins[1],
                isAbsolute: await stateValues.marginsAbsolute
              },
              variableIndex: 0
            });
          }
          if ("1" in desiredStateVariableValues.specifiedMargins) {
            if (globalDependencyValues.marginsAttr.stateValues.numComponents > 1) {
              instructions.push({
                setDependency: "marginsAttr",
                desiredValue: {
                  size: desiredStateVariableValues.specifiedMargins[1],
                  isAbsolute: await stateValues.marginsAbsolute
                },
                variableIndex: 1
              });
            }
          }
        } else {
          let symmetricMargins = globalDependencyValues.marginsAttr.stateValues.numComponents === 1;
          for (let arrayKey in desiredStateVariableValues.specifiedMargins) {
            if (symmetricMargins) {
              instructions.push({
                setDependency: "marginsAttr",
                desiredValue: {
                  size: desiredStateVariableValues.specifiedMargins[arrayKey],
                  isAbsolute: await stateValues.marginsAbsolute
                },
                variableIndex: 0
              });
            } else {
              instructions.push({
                setDependency: "marginsAttr",
                desiredValue: {
                  size: desiredStateVariableValues.specifiedMargins[arrayKey],
                  isAbsolute: await stateValues.marginsAbsolute
                },
                variableIndex: arrayKey
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.marginsAbsolute = {
      returnDependencies: () => ({
        marginsAbsoluteArray: {
          dependencyType: "stateVariable",
          variableName: "marginsAbsoluteArray"
        }
      }),
      definition({ dependencyValues }) {
        let marginsAbsolute = null;
        let foundAbsolute = false;
        let diagnostics = [];
        for (let ind in dependencyValues.marginsAbsoluteArray) {
          let thisAbsolute = dependencyValues.marginsAbsoluteArray[ind];
          if (thisAbsolute !== null) {
            thisAbsolute = Boolean(thisAbsolute);
            if (thisAbsolute) {
              foundAbsolute = true;
            }
          }
        }
        if (foundAbsolute) {
          diagnostics.push({
            message: "`<sbsGroup>` is not implemented for absolute measurements. Setting margins to relative.",
            type: "warning"
          });
        }
        marginsAbsolute = false;
        return {
          setValue: { marginsAbsolute },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.absoluteMeasurements = {
      public: true,
      description: "Whether children's widths are interpreted in absolute units.",
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      // forRenderer: true,
      returnDependencies: () => ({
        widthsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "widthsAbsolute"
        },
        marginsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "marginsAbsolute"
        }
      }),
      definition({ dependencyValues }) {
        return { setValue: { absoluteMeasurements: false } };
      }
    };
    stateVariableDefinitions.allWidths = {
      additionalStateVariablesDefined: [
        "allMargins",
        {
          variableName: "gapWidth",
          public: true,
          description: "The gap width between panels as a percentage of the total container width.",
          shadowingInstructions: {
            createComponentOfType: "number"
          }
          // forRenderer: true
        }
      ],
      returnDependencies: () => ({
        maxNPanelsPerRow: {
          dependencyType: "stateVariable",
          variableName: "maxNPanelsPerRow"
        },
        specifiedWidths: {
          dependencyType: "stateVariable",
          variableName: "specifiedWidths"
        },
        specifiedMargins: {
          dependencyType: "stateVariable",
          variableName: "specifiedMargins"
        },
        absoluteMeasurements: {
          dependencyType: "stateVariable",
          variableName: "absoluteMeasurements"
        }
      }),
      definition({ dependencyValues }) {
        let diagnostics = [];
        let gapWidth = 0;
        let allWidths = [...dependencyValues.specifiedWidths];
        let allMargins = [...dependencyValues.specifiedMargins];
        let totalWidthSpecified = 0;
        let nWidthsUndefined = 0;
        for (let ind = 0; ind < dependencyValues.maxNPanelsPerRow; ind++) {
          let width = allWidths[ind];
          if (width === null) {
            nWidthsUndefined++;
          } else {
            totalWidthSpecified += width;
          }
        }
        let totalMarginSpecified = 0;
        let nMarginsUndefined = 0;
        for (let ind = 0; ind < 2; ind++) {
          let margin = allMargins[ind];
          if (margin === null) {
            nMarginsUndefined++;
          } else {
            totalMarginSpecified += margin;
          }
        }
        totalMarginSpecified *= dependencyValues.maxNPanelsPerRow;
        if (!dependencyValues.absoluteMeasurements) {
          if (totalWidthSpecified + totalMarginSpecified >= 100) {
            let normalization = 100 / (totalWidthSpecified + totalMarginSpecified);
            for (let ind = 0; ind < dependencyValues.maxNPanelsPerRow; ind++) {
              if (allWidths[ind] === null) {
                allWidths[ind] = 0;
              } else {
                allWidths[ind] *= normalization;
              }
            }
            for (let ind = 0; ind < 2; ind++) {
              let margin = allMargins[ind];
              if (margin === null) {
                allMargins[ind] = 0;
              } else {
                allMargins[ind] *= normalization;
              }
            }
          } else {
            if (nWidthsUndefined > 0) {
              let newWidth = (100 - (totalWidthSpecified + totalMarginSpecified)) / nWidthsUndefined;
              for (let ind = 0; ind < dependencyValues.maxNPanelsPerRow; ind++) {
                if (allWidths[ind] === null) {
                  allWidths[ind] = newWidth;
                }
              }
              for (let ind = 0; ind < 2; ind++) {
                let margin = allMargins[ind];
                if (margin === null) {
                  allMargins[ind] = 0;
                }
              }
            } else if (nMarginsUndefined > 0) {
              let newMargin = (100 - (totalWidthSpecified + totalMarginSpecified)) / (nMarginsUndefined * dependencyValues.maxNPanelsPerRow);
              for (let ind = 0; ind < 2; ind++) {
                if (allMargins[ind] === null) {
                  allMargins[ind] = newMargin;
                }
              }
            } else if (dependencyValues.maxNPanelsPerRow > 1) {
              gapWidth = (100 - (totalWidthSpecified + totalMarginSpecified)) / (dependencyValues.maxNPanelsPerRow - 1);
            } else if (dependencyValues.maxNPanelsPerRow === 1) {
              allMargins[1] = 100 - (allMargins[0] + allWidths[0]);
            } else {
              diagnostics.push({
                message: "Invalid `<sbsGroup>`: it must have at least one block child.",
                type: "warning"
              });
            }
          }
        }
        return {
          setValue: { allWidths, allMargins, gapWidth },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.widths = {
      public: true,
      description: "The list of child widths.",
      isArray: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      entryPrefixes: ["width"],
      // forRenderer: true,
      returnArraySizeDependencies: () => ({
        maxNPanelsPerRow: {
          dependencyType: "stateVariable",
          variableName: "maxNPanelsPerRow"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.maxNPanelsPerRow];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          allWidths: {
            dependencyType: "stateVariable",
            variableName: `allWidths`
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            specifiedWidth: {
              dependencyType: "stateVariable",
              variableName: `specifiedWidth${varEnding}`
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let widths = {};
        for (let arrayKey of arrayKeys) {
          widths[arrayKey] = globalDependencyValues.allWidths[arrayKey];
        }
        return { setValue: { widths } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.widths) {
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].specifiedWidth,
            desiredValue: desiredStateVariableValues.widths[arrayKey],
            variableIndex: 0
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.margins = {
      public: true,
      description: "Margins between adjacent children.",
      isArray: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      entryPrefixes: ["margin"],
      // forRenderer: true,
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          allMargins: {
            dependencyType: "stateVariable",
            variableName: `allMargins`
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            specifiedMargin: {
              dependencyType: "stateVariable",
              variableName: `specifiedMargin${varEnding}`
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let margins = {};
        for (let arrayKey of arrayKeys) {
          margins[arrayKey] = globalDependencyValues.allMargins[arrayKey];
        }
        return { setValue: { margins } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.margins) {
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].specifiedMargin,
            desiredValue: desiredStateVariableValues.margins[arrayKey],
            variableIndex: 0
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.valigns = {
      public: true,
      description: "Vertical alignment of each child.",
      isArray: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      entryPrefixes: ["valign"],
      // forRenderer: true,
      hasEssential: true,
      defaultValueByArrayKey: () => "top",
      returnArraySizeDependencies: () => ({
        maxNPanelsPerRow: {
          dependencyType: "stateVariable",
          variableName: "maxNPanelsPerRow"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.maxNPanelsPerRow];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          valignAttr: {
            dependencyType: "attributeComponent",
            attributeName: "valign",
            variableNames: ["value"]
          },
          valignsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "valigns",
            variableNames: ["numComponents"]
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            valignsAttr: {
              dependencyType: "attributeComponent",
              attributeName: "valigns",
              variableNames: [`text${varEnding}`]
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        arraySize
      }) {
        let valigns = {};
        let essentialValigns = {};
        let nValignsSpecified;
        let usingSingleValign = false;
        if (globalDependencyValues.valignsAttr !== null) {
          nValignsSpecified = globalDependencyValues.valignsAttr.stateValues.numComponents;
        } else if (globalDependencyValues.valignAttr !== null) {
          nValignsSpecified = arraySize[0];
          usingSingleValign = true;
        } else {
          nValignsSpecified = 0;
        }
        for (let arrayKey of arrayKeys) {
          let arrayIndex = Number(arrayKey);
          if (arrayIndex < nValignsSpecified) {
            if (usingSingleValign) {
              valigns[arrayKey] = globalDependencyValues.valignAttr.stateValues.value.toLowerCase();
            } else {
              let varEnding = arrayIndex + 1;
              valigns[arrayKey] = dependencyValuesByKey[arrayKey].valignsAttr.stateValues[`text${varEnding}`].toLowerCase();
            }
            if (!["top", "middle", "bottom"].includes(
              valigns[arrayKey]
            )) {
              valigns[arrayKey] = "top";
            }
          } else {
            essentialValigns[arrayKey] = true;
          }
        }
        let result = {};
        if (Object.keys(valigns).length > 0) {
          result.setValue = { valigns };
        }
        if (Object.keys(essentialValigns).length > 0) {
          result.useEssentialOrDefaultValue = {
            valigns: essentialValigns
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        globalDependencyValues,
        arraySize
      }) {
        let nValignsSpecified;
        let usingSingleValign = false;
        if (globalDependencyValues.valignsAttr !== null) {
          nValignsSpecified = globalDependencyValues.valignsAttr.stateValues.numComponents;
        } else if (globalDependencyValues.valignAttr !== null) {
          nValignsSpecified = arraySize[0];
          usingSingleValign = true;
        } else {
          nValignsSpecified = 0;
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.valigns) {
          let desiredValue = desiredStateVariableValues.valigns[arrayKey].toLowerCase();
          if (!["top", "middle", "bottom"].includes(desiredValue)) {
            continue;
          }
          let arrayIndex = Number(arrayKey);
          if (arrayIndex < nValignsSpecified) {
            if (usingSingleValign) {
              instructions.push({
                setDependency: "valignAttr",
                desiredValue,
                variableIndex: 0
              });
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].valignsAttr,
                desiredValue,
                variableIndex: 0
              });
            }
          } else {
            instructions.push({
              setEssentialValue: "valigns",
              value: { [arrayKey]: desiredValue }
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Stack extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "stack";
  static rendererType = "containerBlock";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static componentDocs = {
    summary: "A vertical container element for organizing content within a `<sideBySide>`"
  };
  static includeBlankStringChildren = true;
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnPassThroughListItemChildStateVariableDefinitions()
    );
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
const SideBySide$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  SbsGroup,
  SideBySide,
  Stack
}, Symbol.toStringTag, { value: "Module" }));
let H$2 = class H extends Text {
  static componentType = "h";
  static componentDocs = {
    summary: "A heading for an index entry (available only for PreTeXt compatibility)."
  };
  static excludeFromSchema = true;
  static rendererType = "text";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.sortby = {
      createComponentOfType: "text",
      description: "Property name to sort the indexed values by."
    };
    return attributes;
  }
};
class Idx extends CompositeComponent {
  static componentType = "idx";
  static componentDocs = {
    summary: "A back-of-the-book index entry (available only for PreTeXt compatibility)."
  };
  static excludeFromSchema = true;
  static returnChildGroups() {
    return [
      {
        group: "hs",
        componentTypes: ["h"]
      },
      {
        group: "stringsTexts",
        componentTypes: ["string", "text"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.terms = {
      returnDependencies: () => ({
        stringTextChildren: {
          dependencyType: "child",
          childGroups: ["stringsTexts"],
          variableNames: ["value"]
        },
        hChildren: {
          dependencyType: "child",
          childGroups: ["hs"],
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        let terms;
        if (dependencyValues.hChildren.length > 0) {
          terms = dependencyValues.hChildren.map(
            (x2) => x2.stateValues.value
          );
        } else {
          let value = "";
          for (let comp of dependencyValues.stringTextChildren) {
            if (typeof comp === "string") {
              value += comp;
            } else {
              value += comp.stateValues.value;
            }
          }
          terms = [value];
        }
        return { setValue: { terms } };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({}),
      definition() {
        return {
          setValue: { readyToExpandWhenResolved: true }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
const Indexing = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  H: H$2,
  Idx
}, Symbol.toStringTag, { value: "Module" }));
class Pre extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "pre";
  static componentDocs = {
    summary: "A preformatted block preserving whitespace"
  };
  static renderChildren = true;
  static includeBlankStringChildren = true;
  static returnChildGroups() {
    return [
      {
        group: "allChildren",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnPassThroughListItemChildStateVariableDefinitions()
    );
    stateVariableDefinitions.displayDoenetMLIndices = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          childGroups: ["allChildren"]
        }
      }),
      definition({ dependencyValues }) {
        let displayDoenetMLIndices = [];
        for (let [
          ind,
          child
        ] of dependencyValues.allChildren.entries()) {
          if (child.componentType === "displayDoenetML") {
            displayDoenetMLIndices.push(ind);
          }
        }
        return { setValue: { displayDoenetMLIndices } };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class DisplayDoenetML extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "displayDoenetML";
  static rendererType = "text";
  static componentDocs = {
    summary: "Displays a DoenetML source string verbatim"
  };
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  static includeBlankStringChildren = true;
  static returnChildGroups() {
    return [
      {
        group: "allChildren",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.value = {
      shadowVariable: true,
      returnDependencies: () => ({
        childrenDoenetML: {
          dependencyType: "doenetML",
          displayOnlyChildren: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { value: dependencyValues.childrenDoenetML }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The verbatim text content.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { text: dependencyValues.value }
      })
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
const Verbatim = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  DisplayDoenetML,
  Pre
}, Symbol.toStringTag, { value: "Module" }));
class Paginator extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      setPage: this.setPage.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "paginator";
  static componentDocs = {
    summary: "Defines a paginated section of content"
  };
  static rendererType = "containerBlock";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.initialPage = {
      createComponentOfType: "integer",
      createStateVariable: "initialPage",
      defaultValue: 1,
      description: "1-based page index shown on first render."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numPages = {
      description: "The total number of pages.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { numPages: dependencyValues.children.length }
        };
      }
    };
    stateVariableDefinitions.currentPage = {
      description: "The currently displayed page (1-based).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      hasEssential: true,
      returnDependencies: () => ({
        initialPage: {
          dependencyType: "stateVariable",
          variableName: "initialPage"
        },
        numPages: {
          dependencyType: "stateVariable",
          variableName: "numPages"
        }
      }),
      definition({ dependencyValues }) {
        return {
          useEssentialOrDefaultValue: {
            currentPage: {
              get defaultValue() {
                let initialPageNumber = dependencyValues.initialPage;
                if (!Number.isInteger(initialPageNumber)) {
                  return 1;
                } else {
                  return Math.max(
                    1,
                    Math.min(
                      dependencyValues.numPages,
                      initialPageNumber
                    )
                  );
                }
              }
            }
          }
        };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        stateValues,
        sourceDetails = {}
      }) {
        if (!sourceDetails.fromSetPage) {
          return { success: false };
        }
        let desiredPageNumber = Number(
          desiredStateVariableValues.currentPage
        );
        if (!Number.isInteger(desiredPageNumber)) {
          return { success: false };
        }
        desiredPageNumber = Math.max(
          1,
          Math.min(await stateValues.numPages, desiredPageNumber)
        );
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "currentPage",
              value: desiredPageNumber
            }
          ]
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        currentPage: {
          dependencyType: "stateVariable",
          variableName: "currentPage"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            childIndicesToRender: [
              dependencyValues.currentPage - 1
            ]
          }
        };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    return stateVariableDefinitions;
  }
  async setPage({
    number,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!Number.isInteger(number)) {
      return;
    }
    let pageNumber = Math.max(
      1,
      Math.min(await this.stateValues.numPages, number)
    );
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "currentPage",
        value: pageNumber,
        sourceDetails: { fromSetPage: true }
      }
    ];
    await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true,
      event: {
        verb: "selected",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          response: pageNumber,
          responseText: pageNumber.toString()
        }
      }
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class PaginatorControls extends BlockComponent {
  constructor(args) {
    super(args);
    this.externalActions = {};
    Object.defineProperty(this.externalActions, "setPage", {
      enumerable: true,
      get: (async function() {
        let paginatorComponentIdx = await this.stateValues.paginatorComponentIdx;
        if (paginatorComponentIdx) {
          return {
            componentIdx: paginatorComponentIdx,
            actionName: "setPage"
          };
        } else {
          return;
        }
      }).bind(this)
    });
  }
  static componentType = "paginatorControls";
  static renderChildren = true;
  static componentDocs = {
    summary: "Previous/next page controls referencing a paginator elsewhere in the document"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.previousLabel = {
      description: 'Label for the "previous page" button.',
      createComponentOfType: "text",
      createStateVariable: "previousLabel",
      defaultValue: "Previous",
      forRenderer: true,
      public: true
    };
    attributes.nextLabel = {
      description: 'Label for the "next page" button.',
      createComponentOfType: "text",
      createStateVariable: "nextLabel",
      defaultValue: "Next",
      forRenderer: true,
      public: true
    };
    attributes.pageLabel = {
      description: "Label format for the current page indicator.",
      createComponentOfType: "text",
      createStateVariable: "pageLabel",
      defaultValue: "Page",
      forRenderer: true,
      public: true
    };
    attributes.paginator = {
      createReferences: true,
      description: "Reference to the `<paginator>` these controls operate on."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.paginatorComponentIdx = {
      additionalStateVariablesDefined: ["unresolvedPath"],
      returnDependencies: () => ({
        paginator: {
          dependencyType: "attributeRefResolutions",
          attributeName: "paginator"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.paginator?.length === 1) {
          const paginator = dependencyValues.paginator[0];
          if (paginator.unresolvedPath == null) {
            return {
              setValue: {
                paginatorComponentIdx: paginator.componentIdx,
                unresolvedPath: paginator.unresolvedPath
              }
            };
          }
        }
        return {
          setValue: {
            paginatorComponentIdx: null,
            unresolvedPath: null
          }
        };
      }
    };
    stateVariableDefinitions.currentPage = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["paginatorComponentIdx"],
      returnDependencies({ stateValues }) {
        if (!stateValues.paginatorComponentIdx) {
          return {};
        } else {
          return {
            paginatorPage: {
              dependencyType: "stateVariable",
              componentIdx: stateValues.paginatorComponentIdx,
              variableName: "currentPage",
              variablesOptional: true
            }
          };
        }
      },
      definition({ dependencyValues }) {
        if ("paginatorPage" in dependencyValues && Number.isInteger(dependencyValues.paginatorPage)) {
          return {
            setValue: {
              currentPage: dependencyValues.paginatorPage
            }
          };
        } else {
          return { setValue: { currentPage: 1 } };
        }
      }
    };
    stateVariableDefinitions.numPages = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["paginatorComponentIdx"],
      returnDependencies({ stateValues }) {
        if (!stateValues.paginatorComponentIdx) {
          return {};
        } else {
          return {
            paginatorNPages: {
              dependencyType: "stateVariable",
              componentIdx: stateValues.paginatorComponentIdx,
              variableName: "numPages",
              variablesOptional: true
            }
          };
        }
      },
      definition({ dependencyValues }) {
        if ("paginatorNPages" in dependencyValues && Number.isInteger(dependencyValues.paginatorNPages)) {
          return {
            setValue: {
              numPages: dependencyValues.paginatorNPages
            }
          };
        } else {
          return { setValue: { numPages: 1 } };
        }
      }
    };
    stateVariableDefinitions.disabledDirectly = {
      description: "Whether the controls are disabled directly (rather than via inheritance).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      hasEssential: true,
      doNotShadowEssential: true,
      defaultValue: false,
      returnDependencies: () => ({
        disabledPreliminary: {
          dependencyType: "stateVariable",
          variableName: "disabledPreliminary",
          variablesOptional: true
        },
        sourceCompositeDisabled: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "disabled"
        },
        adapterSourceDisabled: {
          dependencyType: "adapterSourceStateVariable",
          variableName: "disabled"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (!usedDefault.disabledPreliminary) {
          return {
            setValue: {
              disabledDirectly: dependencyValues.disabledPreliminary
            }
          };
        }
        let disabledDirectly = false;
        let useEssential = true;
        if (dependencyValues.sourceCompositeDisabled !== null && !usedDefault.sourceCompositeDisabled) {
          disabledDirectly = disabledDirectly || dependencyValues.sourceCompositeDisabled;
          useEssential = false;
        }
        if (dependencyValues.adapterSourceDisabled !== null && !usedDefault.adapterSourceDisabled) {
          disabledDirectly = disabledDirectly || dependencyValues.adapterSourceDisabled;
          useEssential = false;
        }
        if (useEssential) {
          return {
            useEssentialOrDefaultValue: {
              disabledDirectly: true
            }
          };
        } else {
          return { setValue: { disabledDirectly } };
        }
      }
    };
    return stateVariableDefinitions;
  }
}
const Paginator$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  Paginator,
  PaginatorControls
}, Symbol.toStringTag, { value: "Module" }));
class Input extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      focusChanged: this.focusChanged.bind(this)
    });
  }
  static componentType = "_input";
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.collaborateGroups = {
      createComponentOfType: "collaborateGroups",
      createStateVariable: "collaborateGroups",
      defaultValue: null,
      public: true,
      excludeFromSchema: true,
      description: "Groups of users that collaborate when working with this input. (Currently ignored.)"
    };
    attributes.labelPosition = {
      createComponentOfType: "text",
      createStateVariable: "labelPosition",
      defaultValue: "left",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "left",
          description: "Place the label to the left of the input."
        },
        {
          value: "right",
          description: "Place the label to the right of the input."
        }
      ],
      description: "Position of the label relative to the input."
    };
    attributes.forAnswer = {
      createReferences: true,
      description: "References to `<answer>` elements that this input should submit to."
    };
    Object.assign(attributes, returnLabelAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    const stateVariableDefinitions = super.returnStateVariableDefinitions();
    let componentClass = this;
    const labelDefinitions = returnLabelStateVariableDefinitions({
      getLabelFromParentIfSugared: true
    });
    Object.assign(stateVariableDefinitions, labelDefinitions);
    stateVariableDefinitions.numValues = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numValues: 1 } })
    };
    stateVariableDefinitions.answerAncestor = {
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "ancestor",
          componentType: "answer",
          variableNames: [
            "delegateCheckWorkToInput",
            "justSubmitted",
            "creditAchieved",
            "showCorrectness",
            "colorCorrectness",
            "submitLabel",
            "submitLabelNoCorrectness",
            "numAttemptsLeft",
            "creditIsReducedByAttempt",
            "numIncorrectSubmissions",
            "numPreviousIncorrectSubmissions",
            "creditFactorUsed",
            "nextCreditFactor",
            "forceFullCheckWorkButton",
            "forceSmallCheckWorkButton",
            "labelsForAnswer",
            "colorInputsSeparately"
          ]
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            answerAncestor: dependencyValues.answerAncestor
          }
        };
      }
    };
    stateVariableDefinitions.sectionDeterminingColorCorrectness = {
      returnDependencies: () => ({
        ancestorForOverride: {
          dependencyType: "ancestor",
          variableNames: ["descendantColorCorrectnessBasedOnIdx"]
        }
      }),
      definition({ dependencyValues }) {
        let sectionDeterminingColorCorrectness = null;
        if (typeof dependencyValues.ancestorForOverride?.stateValues.descendantColorCorrectnessBasedOnIdx === "number") {
          sectionDeterminingColorCorrectness = dependencyValues.ancestorForOverride.stateValues.descendantColorCorrectnessBasedOnIdx;
        }
        return {
          setValue: { sectionDeterminingColorCorrectness }
        };
      }
    };
    stateVariableDefinitions.answerSpecifiedInForAnswer = {
      returnDependencies: () => ({
        forAnswer: {
          dependencyType: "attributeRefResolutions",
          attributeName: "forAnswer"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.forAnswer?.length === 1) {
          const forAnswer = dependencyValues.forAnswer[0];
          if (!forAnswer.unresolvedPath) {
            return {
              setValue: {
                answerSpecifiedInForAnswer: forAnswer.componentIdx
              }
            };
          }
        }
        return {
          setValue: {
            answerSpecifiedInForAnswer: null
          }
        };
      }
    };
    stateVariableDefinitions.componentDeterminingDisplayedCorrectness = {
      stateVariablesDeterminingDependencies: [
        "sectionDeterminingColorCorrectness",
        "answerAncestor",
        "answerSpecifiedInForAnswer"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {};
        if (stateValues.sectionDeterminingColorCorrectness !== null) {
          dependencies.sectionDeterminingColorCorrectness = {
            dependencyType: "multipleStateVariables",
            componentIdx: stateValues.sectionDeterminingColorCorrectness,
            variableNames: [
              "justSubmitted",
              "creditAchieved",
              "showCorrectness",
              "colorCorrectness"
            ],
            variablesOptional: true
          };
        } else if (stateValues.answerAncestor) {
          dependencies.answerAncestor = {
            dependencyType: "stateVariable",
            variableName: "answerAncestor"
          };
        } else if (stateValues.answerSpecifiedInForAnswer !== null) {
          dependencies.answerSpecifiedInForAnswer = {
            dependencyType: "multipleStateVariables",
            componentIdx: stateValues.answerSpecifiedInForAnswer,
            variableNames: [
              "justSubmitted",
              "creditAchieved",
              "showCorrectness",
              "colorCorrectness",
              "colorInputsSeparately"
            ],
            variablesOptional: true
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let componentDeterminingDisplayedCorrectness = null;
        if (dependencyValues.sectionDeterminingColorCorrectness) {
          componentDeterminingDisplayedCorrectness = dependencyValues.sectionDeterminingColorCorrectness;
        } else if (dependencyValues.answerAncestor) {
          componentDeterminingDisplayedCorrectness = dependencyValues.answerAncestor;
        } else if (dependencyValues.answerSpecifiedInForAnswer) {
          componentDeterminingDisplayedCorrectness = dependencyValues.answerSpecifiedInForAnswer;
        }
        return {
          setValue: { componentDeterminingDisplayedCorrectness }
        };
      }
    };
    stateVariableDefinitions.justSubmitted = {
      forRenderer: true,
      returnDependencies: () => ({
        componentDeterminingDisplayedCorrectness: {
          dependencyType: "stateVariable",
          variableName: "componentDeterminingDisplayedCorrectness"
        }
      }),
      definition: function({ dependencyValues }) {
        let justSubmitted = false;
        if (dependencyValues.componentDeterminingDisplayedCorrectness) {
          if (dependencyValues.componentDeterminingDisplayedCorrectness.stateValues.justSubmitted) {
            justSubmitted = true;
          }
        }
        return {
          setValue: { justSubmitted }
        };
      }
    };
    stateVariableDefinitions.showCorrectness = {
      forRenderer: true,
      returnDependencies: () => ({
        showCorrectnessFlag: {
          dependencyType: "flag",
          flagName: "showCorrectness"
        },
        componentDeterminingDisplayedCorrectness: {
          dependencyType: "stateVariable",
          variableName: "componentDeterminingDisplayedCorrectness"
        }
      }),
      definition({ dependencyValues }) {
        let showCorrectness;
        if (dependencyValues.componentDeterminingDisplayedCorrectness) {
          showCorrectness = dependencyValues.componentDeterminingDisplayedCorrectness.stateValues.showCorrectness;
        } else {
          showCorrectness = dependencyValues.showCorrectnessFlag !== false;
        }
        return { setValue: { showCorrectness } };
      }
    };
    stateVariableDefinitions.colorCorrectness = {
      forRenderer: true,
      returnDependencies: () => ({
        componentDeterminingDisplayedCorrectness: {
          dependencyType: "stateVariable",
          variableName: "componentDeterminingDisplayedCorrectness"
        },
        showCorrectness: {
          dependencyType: "stateVariable",
          variableName: "showCorrectness"
        }
      }),
      definition({ dependencyValues }) {
        let colorCorrectness = true;
        if (!dependencyValues.showCorrectness) {
          colorCorrectness = false;
        } else if (dependencyValues.componentDeterminingDisplayedCorrectness) {
          colorCorrectness = dependencyValues.componentDeterminingDisplayedCorrectness.stateValues.colorCorrectness;
        }
        return { setValue: { colorCorrectness } };
      }
    };
    const variableForImplicitProp = this.variableForImplicitProp ?? "value";
    stateVariableDefinitions.creditAchieved = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: [
        "answerAncestor",
        "answerSpecifiedInForAnswer"
      ],
      returnDependencies({ stateValues }) {
        const deps = {
          componentDeterminingDisplayedCorrectness: {
            dependencyType: "stateVariable",
            variableName: "componentDeterminingDisplayedCorrectness"
          }
        };
        if (stateValues.answerAncestor?.stateValues.colorInputsSeparately) {
          deps.answerCreditAchievedPerInput = {
            dependencyType: "ancestor",
            componentType: "answer",
            variableNames: ["creditAchievedPerInput"]
          };
        } else if (stateValues.answerSpecifiedInForAnswer !== null) {
          deps.answerColoringState = {
            dependencyType: "multipleStateVariables",
            componentIdx: stateValues.answerSpecifiedInForAnswer,
            variableNames: [
              "colorInputsSeparately",
              "creditAchievedPerInput"
            ],
            variablesOptional: true
          };
        }
        return deps;
      },
      definition: function({ dependencyValues, componentIdx }) {
        let creditAchieved = 0;
        const comp = dependencyValues.componentDeterminingDisplayedCorrectness;
        if (comp) {
          const overallCredit = comp.stateValues.creditAchieved ?? 0;
          const creditAchievedPerInput = dependencyValues.answerCreditAchievedPerInput?.stateValues.creditAchievedPerInput ?? (dependencyValues.answerColoringState?.stateValues?.colorInputsSeparately ? dependencyValues.answerColoringState.stateValues.creditAchievedPerInput : void 0);
          if (comp.stateValues.colorInputsSeparately && creditAchievedPerInput) {
            const key = `${componentIdx}/${variableForImplicitProp}`;
            let perInputCredit = creditAchievedPerInput[key];
            if (perInputCredit === void 0 && variableForImplicitProp !== "value") {
              const keyPrefix = `${componentIdx}/`;
              const candidateCredits = Object.entries(
                creditAchievedPerInput
              ).filter(
                ([entryKey]) => entryKey.startsWith(keyPrefix)
              ).map(([, credit]) => credit);
              if (candidateCredits.length > 0) {
                perInputCredit = Math.max(...candidateCredits);
              }
            }
            creditAchieved = perInputCredit !== void 0 ? perInputCredit : overallCredit;
          } else {
            creditAchieved = overallCredit;
          }
        }
        return {
          setValue: { creditAchieved }
        };
      }
    };
    stateVariableDefinitions.suppressCheckWork = {
      returnDependencies: () => ({
        autoSubmit: {
          dependencyType: "flag",
          flagName: "autoSubmit"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            suppressCheckWork: dependencyValues.autoSubmit
          }
        };
      }
    };
    stateVariableDefinitions.showCheckWork = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        },
        suppressCheckWork: {
          dependencyType: "stateVariable",
          variableName: "suppressCheckWork"
        }
      }),
      definition: function({ dependencyValues }) {
        let showCheckWork = false;
        if (dependencyValues.answerAncestor && !dependencyValues.suppressCheckWork) {
          showCheckWork = dependencyValues.answerAncestor.stateValues.delegateCheckWorkToInput;
        }
        return {
          setValue: { showCheckWork }
        };
      }
    };
    stateVariableDefinitions.forceFullCheckWorkButton = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        },
        showCheckWork: {
          dependencyType: "stateVariable",
          variableName: "showCheckWork"
        }
      }),
      definition: function({ dependencyValues }) {
        let forceFullCheckWorkButton = false;
        if (dependencyValues.answerAncestor && dependencyValues.showCheckWork) {
          forceFullCheckWorkButton = dependencyValues.answerAncestor.stateValues.forceFullCheckWorkButton;
        }
        return {
          setValue: { forceFullCheckWorkButton }
        };
      }
    };
    stateVariableDefinitions.forceSmallCheckWorkButton = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        },
        showCheckWork: {
          dependencyType: "stateVariable",
          variableName: "showCheckWork"
        }
      }),
      definition: function({ dependencyValues }) {
        let forceSmallCheckWorkButton = false;
        if (dependencyValues.answerAncestor && dependencyValues.showCheckWork) {
          forceSmallCheckWorkButton = dependencyValues.answerAncestor.stateValues.forceSmallCheckWorkButton;
        }
        return {
          setValue: { forceSmallCheckWorkButton }
        };
      }
    };
    stateVariableDefinitions.creditIsReducedByAttempt = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition: function({ dependencyValues }) {
        let creditIsReducedByAttempt = false;
        if (dependencyValues.answerAncestor) {
          creditIsReducedByAttempt = dependencyValues.answerAncestor.stateValues.creditIsReducedByAttempt;
        }
        return {
          setValue: { creditIsReducedByAttempt }
        };
      }
    };
    stateVariableDefinitions.submitLabel = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition({ dependencyValues }) {
        let submitLabel;
        if (dependencyValues.answerAncestor) {
          submitLabel = dependencyValues.answerAncestor.stateValues.submitLabel;
        } else {
          submitLabel = "";
        }
        return { setValue: { submitLabel } };
      }
    };
    stateVariableDefinitions.submitLabelNoCorrectness = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition({ dependencyValues }) {
        let submitLabelNoCorrectness;
        if (dependencyValues.answerAncestor) {
          submitLabelNoCorrectness = dependencyValues.answerAncestor.stateValues.submitLabelNoCorrectness;
        } else {
          submitLabelNoCorrectness = "";
        }
        return { setValue: { submitLabelNoCorrectness } };
      }
    };
    stateVariableDefinitions.numAttemptsLeft = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition({ dependencyValues }) {
        let numAttemptsLeft;
        if (dependencyValues.answerAncestor) {
          numAttemptsLeft = dependencyValues.answerAncestor.stateValues.numAttemptsLeft;
        } else {
          numAttemptsLeft = Infinity;
        }
        return { setValue: { numAttemptsLeft } };
      }
    };
    stateVariableDefinitions.numIncorrectSubmissions = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition({ dependencyValues }) {
        let numIncorrectSubmissions;
        if (dependencyValues.answerAncestor) {
          numIncorrectSubmissions = dependencyValues.answerAncestor.stateValues.numIncorrectSubmissions;
        } else {
          numIncorrectSubmissions = 0;
        }
        return { setValue: { numIncorrectSubmissions } };
      }
    };
    stateVariableDefinitions.numPreviousIncorrectSubmissions = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition({ dependencyValues }) {
        let numPreviousIncorrectSubmissions;
        if (dependencyValues.answerAncestor) {
          numPreviousIncorrectSubmissions = dependencyValues.answerAncestor.stateValues.numPreviousIncorrectSubmissions;
        } else {
          numPreviousIncorrectSubmissions = 0;
        }
        return { setValue: { numPreviousIncorrectSubmissions } };
      }
    };
    stateVariableDefinitions.creditFactorUsed = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition({ dependencyValues }) {
        let creditFactorUsed;
        if (dependencyValues.answerAncestor) {
          creditFactorUsed = dependencyValues.answerAncestor.stateValues.creditFactorUsed;
        } else {
          creditFactorUsed = 1;
        }
        return { setValue: { creditFactorUsed } };
      }
    };
    stateVariableDefinitions.nextCreditFactor = {
      forRenderer: true,
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        }
      }),
      definition({ dependencyValues }) {
        let nextCreditFactor;
        if (dependencyValues.answerAncestor) {
          nextCreditFactor = dependencyValues.answerAncestor.stateValues.nextCreditFactor;
        } else {
          nextCreditFactor = 1;
        }
        return { setValue: { nextCreditFactor } };
      }
    };
    stateVariableDefinitions.labelsReferencingInputByForRaw = {
      returnDependencies: () => ({
        labelsReferencingInputByFor: {
          dependencyType: "componentsReferencingAttribute",
          attributeName: "for"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            labelsReferencingInputByForRaw: dependencyValues.labelsReferencingInputByFor ?? []
          }
        };
      }
    };
    stateVariableDefinitions.externalLabelsReferencingInputByFor = {
      stateVariablesDeterminingDependencies: [
        "answerAncestor",
        "labelsReferencingInputByForRaw"
      ],
      returnDependencies({ stateValues }) {
        const dependencies = {
          answerAncestor: {
            dependencyType: "stateVariable",
            variableName: "answerAncestor"
          },
          rawLabelsReferencingInputByFor: {
            dependencyType: "stateVariable",
            variableName: "labelsReferencingInputByForRaw"
          }
        };
        const answerLabels = stateValues.answerAncestor?.stateValues?.labelsForAnswer ?? [];
        for (const label of [
          ...stateValues.labelsReferencingInputByForRaw ?? [],
          ...answerLabels
        ]) {
          const labelComponentIdx = label?.componentIdx;
          if (labelComponentIdx !== void 0 && labelComponentIdx !== null) {
            dependencies[`externalLabelTargetInputComponentIdx${labelComponentIdx}`] = {
              dependencyType: "stateVariable",
              componentIdx: labelComponentIdx,
              variableName: "forTargetInputComponentIdx"
            };
            dependencies[`externalLabelCanBeAccessibilityLabel${labelComponentIdx}`] = {
              dependencyType: "stateVariable",
              componentIdx: labelComponentIdx,
              variableName: "canBeAccessibilityLabel"
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues, componentIdx }) {
        const externalLabelsReferencingInputByFor = [];
        const seenLabelComponentIndices = /* @__PURE__ */ new Set();
        const candidateLabels = [
          ...dependencyValues.rawLabelsReferencingInputByFor ?? [],
          ...dependencyValues.answerAncestor?.stateValues?.labelsForAnswer ?? []
        ];
        for (const label of candidateLabels) {
          const labelComponentIdx = label?.componentIdx;
          if (labelComponentIdx === void 0 || labelComponentIdx === null || seenLabelComponentIndices.has(labelComponentIdx)) {
            continue;
          }
          seenLabelComponentIndices.add(labelComponentIdx);
          if (dependencyValues[`externalLabelCanBeAccessibilityLabel${labelComponentIdx}`] && dependencyValues[`externalLabelTargetInputComponentIdx${labelComponentIdx}`] === componentIdx) {
            externalLabelsReferencingInputByFor.push(label);
          }
        }
        return {
          setValue: {
            externalLabelsReferencingInputByFor
          }
        };
      }
    };
    stateVariableDefinitions.externalLabelRendererIds = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: [
        "externalLabelsReferencingInputByFor"
      ],
      returnDependencies({ stateValues }) {
        const dependencies = {
          externalLabelsReferencingInputByFor: {
            dependencyType: "stateVariable",
            variableName: "externalLabelsReferencingInputByFor"
          }
        };
        const labelsReferencingInput = stateValues.externalLabelsReferencingInputByFor ?? [];
        for (const label of labelsReferencingInput) {
          const componentIdx = label?.componentIdx;
          if (componentIdx !== void 0 && componentIdx !== null) {
            dependencies[`externalLabelRendererId${componentIdx}`] = {
              dependencyType: "rendererId",
              componentIdx
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        const externalLabelRendererIds = [];
        const seen = /* @__PURE__ */ new Set();
        const labelsReferencingInput = dependencyValues.externalLabelsReferencingInputByFor ?? [];
        for (const label of labelsReferencingInput) {
          const componentIdx = label?.componentIdx;
          if (componentIdx === void 0 || componentIdx === null) {
            continue;
          }
          const rendererId = dependencyValues[`externalLabelRendererId${componentIdx}`];
          if (rendererId && !seen.has(rendererId)) {
            seen.add(rendererId);
            externalLabelRendererIds.push(rendererId);
          }
        }
        return { setValue: { externalLabelRendererIds } };
      }
    };
    stateVariableDefinitions.shortDescription = {
      description: "A short accessibility description of this input; it is visible to screen readers but not rendered visually.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        shortDescriptionChild: {
          dependencyType: "child",
          childGroups: ["shortDescriptions"],
          variableNames: ["text"]
        },
        label: {
          dependencyType: "stateVariable",
          variableName: "label"
        },
        answerAncestor: {
          dependencyType: "stateVariable",
          variableName: "answerAncestor"
        },
        createdFromSugar: {
          dependencyType: "doenetAttribute",
          attributeName: "createdFromSugar"
        },
        externalLabelsReferencingInputByFor: {
          dependencyType: "stateVariable",
          variableName: "externalLabelsReferencingInputByFor"
        }
      }),
      definition({ dependencyValues }) {
        let shortDescription = "";
        const diagnostics = [];
        if (dependencyValues.shortDescriptionChild.length > 0) {
          const shortDescriptionChild = dependencyValues.shortDescriptionChild[dependencyValues.shortDescriptionChild.length - 1];
          shortDescription = shortDescriptionChild.stateValues.text.trim();
        }
        const hasExternalForLabel = Boolean(
          dependencyValues.externalLabelsReferencingInputByFor?.length
        );
        if (shortDescription === "" && !dependencyValues.label && !hasExternalForLabel) {
          let objectNeedingLabel = dependencyValues.createdFromSugar && dependencyValues.answerAncestor ? "an `<answer>` creating an input" : `\`<${componentClass.componentType}>\``;
          diagnostics.push({
            type: "accessibility",
            level: 1,
            message: `For accessibility, ${objectNeedingLabel} must have a short description or a label.`
          });
        }
        return {
          setValue: { shortDescription },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        const descriptionIdx = dependencyValues.allChildren.findLastIndex(
          (child) => child.componentType === "description"
        );
        const childIndicesToRender = descriptionIdx === -1 ? [] : [descriptionIdx];
        return { setValue: { childIndicesToRender } };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.focused = {
      description: "Whether this input currently has keyboard focus.",
      forRenderer: true,
      hasEssential: true,
      defaultValue: false,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      ignoreFixed: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { focused: true }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "focused",
              value: Boolean(desiredStateVariableValues.focused)
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  async focusChanged({ focused, actionId, sourceInformation }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "focused",
          value: focused
        }
      ],
      actionId,
      sourceInformation,
      overrideReadOnly: true,
      doNotSave: true
    });
  }
}
function defineSubmitAnswerExternalAction(component) {
  component.externalActions = {};
  Object.defineProperty(component.externalActions, "submitAnswer", {
    enumerable: true,
    get: (async function() {
      let answerAncestor = await this.stateValues.answerAncestor;
      if (answerAncestor !== null) {
        return {
          componentIdx: answerAncestor.componentIdx,
          actionName: "submitAnswer"
        };
      } else {
        return;
      }
    }).bind(component)
  });
}
function returnInputValueChangedStateVariableDefinitions({
  valueChangedDescription,
  immediateValueChangedDescription
} = {}) {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.valueChanged = {
    description: valueChangedDescription,
    public: true,
    hasEssential: true,
    defaultValue: false,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    returnDependencies: () => ({}),
    definition() {
      return { useEssentialOrDefaultValue: { valueChanged: true } };
    },
    inverseDefinition({ desiredStateVariableValues }) {
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "valueChanged",
            value: Boolean(desiredStateVariableValues.valueChanged)
          }
        ]
      };
    }
  };
  stateVariableDefinitions.immediateValueChanged = {
    description: immediateValueChangedDescription,
    public: true,
    hasEssential: true,
    defaultValue: false,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    returnDependencies: () => ({}),
    definition() {
      return {
        useEssentialOrDefaultValue: { immediateValueChanged: true }
      };
    },
    inverseDefinition({ desiredStateVariableValues }) {
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "immediateValueChanged",
            value: Boolean(
              desiredStateVariableValues.immediateValueChanged
            )
          }
        ]
      };
    }
  };
  return stateVariableDefinitions;
}
async function buildInputResponseEvent({
  component,
  verb = "answered",
  response,
  responseText
}) {
  const event = {
    verb,
    object: {
      componentIdx: component.componentIdx,
      componentType: component.componentType
    },
    result: {
      response,
      responseText
    }
  };
  const answerAncestor = await component.stateValues.answerAncestor;
  if (answerAncestor) {
    event.context = {
      answerAncestor: answerAncestor.componentIdx
    };
  }
  return event;
}
function returnImmediateValueStateVariableDefinition({
  description,
  createComponentOfType = "text"
} = {}) {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.immediateValue = {
    description,
    public: true,
    shadowingInstructions: {
      createComponentOfType
    },
    forRenderer: true,
    hasEssential: true,
    shadowVariable: true,
    returnDependencies: () => ({
      value: {
        dependencyType: "stateVariable",
        variableName: "value"
      },
      immediateValueChanged: {
        dependencyType: "stateVariable",
        variableName: "immediateValueChanged",
        onlyToSetInInverseDefinition: true
      }
    }),
    definition: function({
      dependencyValues,
      changes,
      justUpdatedForNewComponent,
      usedDefault
    }) {
      if (changes.value && !justUpdatedForNewComponent && !usedDefault.value) {
        return {
          setValue: { immediateValue: dependencyValues.value },
          setEssentialValue: {
            immediateValue: dependencyValues.value
          }
        };
      } else {
        return {
          useEssentialOrDefaultValue: {
            immediateValue: {
              variablesToCheck: "immediateValue",
              defaultValue: dependencyValues.value
            }
          }
        };
      }
    },
    inverseDefinition: function({
      desiredStateVariableValues,
      initialChange,
      shadowedVariable
    }) {
      let instructions = [
        {
          setEssentialValue: "immediateValue",
          value: desiredStateVariableValues.immediateValue
        },
        {
          setDependency: "immediateValueChanged",
          desiredValue: true
        }
      ];
      if (!(initialChange || shadowedVariable)) {
        instructions.push({
          setDependency: "value",
          desiredValue: desiredStateVariableValues.immediateValue
        });
      }
      return {
        success: true,
        instructions
      };
    }
  };
  return stateVariableDefinitions;
}
async function inputUpdateImmediateValue({
  text,
  actionId,
  sourceInformation = {},
  skipRendererUpdate = false
}) {
  if (!await this.stateValues.disabled) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "immediateValue",
          value: text
        },
        {
          updateType: "setComponentNeedingUpdateValue",
          componentIdx: this.componentIdx
        }
      ],
      transient: true,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
async function inputUpdateValue({
  actionId,
  sourceInformation = {},
  skipRendererUpdate = false
}) {
  if (!await this.stateValues.disabled) {
    let immediateValue = await this.stateValues.immediateValue;
    if (await this.stateValues.value !== immediateValue) {
      let updateInstructions = [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "value",
          value: immediateValue
        },
        // in case value ended up being a different value than requested
        // we set immediate value to whatever was the result
        // (hence the need to execute update first)
        // Also, this makes sure immediateValue is saved to the database,
        // since in updateImmediateValue, immediateValue is not saved to database
        {
          updateType: "executeUpdate"
        },
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "immediateValue",
          valueOfStateVariable: "value"
        },
        {
          updateType: "unsetComponentNeedingUpdateValue"
        }
      ];
      let event = await buildInputResponseEvent({
        component: this,
        verb: "answered",
        response: immediateValue,
        responseText: immediateValue
      });
      await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event
      });
      return await this.coreFunctions.triggerChainedActions({
        componentIdx: this.componentIdx,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function returnMathInputParsingAttributes() {
  return {
    format: {
      description: "Input format for the math entered.",
      createComponentOfType: "text",
      createStateVariable: "format",
      defaultValue: "text",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "text",
          description: "Plain-text math notation (e.g., `x^2 + 1`)."
        },
        {
          value: "latex",
          description: "LaTeX-formatted math (e.g., `x^{2} + 1`)."
        }
      ]
    },
    functionSymbols: {
      description: "Symbols treated as function names when parsing.",
      createComponentOfType: "textList",
      createStateVariable: "functionSymbols",
      defaultValue: ["f", "g"],
      public: true
    },
    splitSymbols: {
      description: "Whether multi-character symbols are split into a product of variables.",
      createComponentOfType: "boolean",
      createStateVariable: "splitSymbols",
      defaultValue: true,
      public: true
    },
    parseScientificNotation: {
      description: "Whether to parse expressions like 1e3 as scientific notation.",
      createComponentOfType: "boolean",
      createStateVariable: "parseScientificNotation",
      defaultValue: false,
      public: true
    }
  };
}
function returnMathComponentInputConfigStateVariableDefinitions({
  parentComponentType
}) {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.minWidth = {
    forRenderer: true,
    returnDependencies: () => ({
      inputAncestor: {
        dependencyType: "ancestor",
        componentType: parentComponentType,
        variableNames: ["minComponentWidth"]
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          minWidth: dependencyValues.inputAncestor ? dependencyValues.inputAncestor.stateValues.minComponentWidth : 0
        }
      };
    }
  };
  for (const varName of [
    "format",
    "functionSymbols",
    "splitSymbols",
    "parseScientificNotation",
    "displaySmallAsZero",
    "padZeros",
    "avoidScientificNotation",
    "unionFromU"
  ]) {
    stateVariableDefinitions[varName] = {
      returnDependencies: () => ({
        parentValue: {
          dependencyType: "parentStateVariable",
          parentComponentType,
          variableName: varName
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { [varName]: dependencyValues.parentValue }
        };
      }
    };
  }
  for (const varName of ["displayDigits", "displayDecimals"]) {
    stateVariableDefinitions[varName] = {
      returnDependencies: () => ({
        parentValue: {
          dependencyType: "parentStateVariable",
          parentComponentType,
          variableName: varName
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let result = {
          setValue: { [varName]: dependencyValues.parentValue }
        };
        if (usedDefault.parentValue) {
          result.markAsUsedDefault = { [varName]: true };
        }
        return result;
      }
    };
  }
  stateVariableDefinitions.componentType = {
    returnDependencies: () => ({}),
    definition: () => ({ setValue: { componentType: "math" } })
  };
  stateVariableDefinitions.focused = {
    forRenderer: true,
    hasEssential: true,
    defaultValue: false,
    public: true,
    description: "Whether this input currently has keyboard focus.",
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    ignoreFixed: true,
    returnDependencies: () => ({}),
    definition: () => ({
      useEssentialOrDefaultValue: { focused: true }
    }),
    inverseDefinition({ desiredStateVariableValues }) {
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "focused",
            value: Boolean(desiredStateVariableValues.focused)
          }
        ]
      };
    }
  };
  return stateVariableDefinitions;
}
function returnMathComponentInputDisplayStateVariableDefinitions() {
  const stateVariableDefinitions = {};
  stateVariableDefinitions.valueForDisplay = {
    returnDependencies: () => ({
      value: {
        dependencyType: "stateVariable",
        variableName: "value"
      },
      displayDigits: {
        dependencyType: "stateVariable",
        variableName: "displayDigits"
      },
      displayDecimals: {
        dependencyType: "stateVariable",
        variableName: "displayDecimals"
      },
      displaySmallAsZero: {
        dependencyType: "stateVariable",
        variableName: "displaySmallAsZero"
      }
    }),
    definition: function({ dependencyValues }) {
      let rounded = roundForDisplay({
        value: dependencyValues.value,
        dependencyValues
      });
      return {
        setValue: { valueForDisplay: rounded }
      };
    }
  };
  stateVariableDefinitions.text = {
    public: true,
    description: "The current value as a text string.",
    shadowingInstructions: {
      createComponentOfType: "text"
    },
    returnDependencies: () => ({
      valueForDisplay: {
        dependencyType: "stateVariable",
        variableName: "valueForDisplay"
      },
      padZeros: {
        dependencyType: "stateVariable",
        variableName: "padZeros"
      },
      displayDigits: {
        dependencyType: "stateVariable",
        variableName: "displayDigits"
      },
      displayDecimals: {
        dependencyType: "stateVariable",
        variableName: "displayDecimals"
      },
      avoidScientificNotation: {
        dependencyType: "stateVariable",
        variableName: "avoidScientificNotation"
      }
    }),
    definition: function({ dependencyValues }) {
      let params = buildNumberDisplayParameters({
        padZeros: dependencyValues.padZeros,
        displayDigits: dependencyValues.displayDigits,
        displayDecimals: dependencyValues.displayDecimals,
        avoidScientificNotation: dependencyValues.avoidScientificNotation
      });
      return {
        setValue: {
          text: dependencyValues.valueForDisplay.toString(params)
        }
      };
    }
  };
  stateVariableDefinitions.rawRendererValue = {
    description: "The raw value used by the renderer.",
    forRenderer: true,
    hasEssential: true,
    shadowVariable: true,
    defaultValue: "",
    provideEssentialValuesInDefinition: true,
    public: true,
    shadowingInstructions: {
      createComponentOfType: "text"
    },
    additionalStateVariablesDefined: [
      {
        variableName: "lastValueForDisplay",
        hasEssential: true,
        shadowVariable: true,
        defaultValue: null,
        set: convertValueToMathExpression
      }
    ],
    returnDependencies: () => ({
      // include immediateValue for inverse definition
      immediateValue: {
        dependencyType: "stateVariable",
        variableName: "immediateValue"
      },
      valueForDisplay: {
        dependencyType: "stateVariable",
        variableName: "valueForDisplay"
      }
    }),
    definition({ dependencyValues, essentialValues }) {
      if (essentialValues.rawRendererValue === void 0 || !deepCompare(
        essentialValues.lastValueForDisplay.tree,
        dependencyValues.valueForDisplay.tree
      )) {
        let rawRendererValue = stripLatex(
          dependencyValues.valueForDisplay.toLatex()
        );
        if (rawRendererValue === "＿") {
          rawRendererValue = "";
        }
        return {
          setValue: {
            rawRendererValue,
            lastValueForDisplay: dependencyValues.valueForDisplay
          },
          setEssentialValue: {
            rawRendererValue,
            lastValueForDisplay: dependencyValues.valueForDisplay
          }
        };
      } else {
        return {
          useEssentialOrDefaultValue: {
            rawRendererValue: true,
            lastValueForDisplay: true
          }
        };
      }
    },
    async inverseDefinition({
      desiredStateVariableValues,
      stateValues,
      essentialValues
    }) {
      const calculateMathExpressionFromLatex2 = async (text) => {
        let expression;
        text = normalizeLatexString(text, {
          unionFromU: await stateValues.unionFromU
        });
        text = text.replace(/\^(\w)/g, "^{$1}");
        let fromLatex = latexToMathFactory({
          functionSymbols: await stateValues.functionSymbols,
          splitSymbols: await stateValues.splitSymbols,
          parseScientificNotation: await stateValues.parseScientificNotation
        });
        try {
          expression = fromLatex(text);
        } catch (e3) {
          expression = me$3.fromAst("＿");
        }
        return expression;
      };
      let instructions = [];
      if (typeof desiredStateVariableValues.rawRendererValue === "string") {
        let currentValue = essentialValues.rawRendererValue;
        let desiredValue = desiredStateVariableValues.rawRendererValue;
        if (currentValue !== desiredValue) {
          instructions.push({
            setEssentialValue: "rawRendererValue",
            value: desiredValue
          });
        }
        let currentMath = await calculateMathExpressionFromLatex2(currentValue);
        let desiredMath = await calculateMathExpressionFromLatex2(desiredValue);
        if (!deepCompare(desiredMath.tree, currentMath.tree)) {
          instructions.push({
            setDependency: "immediateValue",
            desiredValue: desiredMath,
            treatAsInitialChange: true
            // so does not change value
          });
        }
      } else {
        instructions.push({
          setEssentialValue: "lastValueForDisplay",
          value: desiredStateVariableValues.rawRendererValue
        });
        let currentMath = await calculateMathExpressionFromLatex2(
          essentialValues.rawRendererValue
        );
        if (!deepCompare(
          desiredStateVariableValues.rawRendererValue.tree,
          currentMath.tree
        )) {
          let desiredValue = stripLatex(
            desiredStateVariableValues.rawRendererValue.toLatex()
          );
          if (desiredValue === "＿") {
            desiredValue = "";
          }
          instructions.push({
            setEssentialValue: "rawRendererValue",
            value: desiredValue
          });
        }
      }
      return {
        success: true,
        instructions
      };
    }
  };
  return stateVariableDefinitions;
}
async function mathComponentInputUpdateRawValue({
  rawRendererValue,
  actionId,
  sourceInformation = {},
  skipRendererUpdate = false
}) {
  if (!await this.stateValues.disabled) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "rawRendererValue",
          value: rawRendererValue
        },
        {
          updateType: "setComponentNeedingUpdateValue",
          componentIdx: this.componentIdx
        }
      ],
      transient: true,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
async function mathComponentInputUpdateValue({
  actionId,
  sourceInformation = {},
  skipRendererUpdate = false
}) {
  if (!await this.stateValues.disabled) {
    let immediateValue = await this.stateValues.immediateValue;
    if (!deepCompare(
      (await this.stateValues.value).tree,
      immediateValue.tree
    )) {
      let updateInstructions = [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "value",
          value: immediateValue
        },
        // in case value ended up being a different value than requested
        // we set immediate value to whatever was the result
        // (hence the need to execute update first)
        {
          updateType: "executeUpdate"
        },
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "immediateValue",
          valueOfStateVariable: "value"
        },
        {
          updateType: "unsetComponentNeedingUpdateValue"
        }
      ];
      if (immediateValue.tree !== "＿") {
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "rawRendererValue",
          valueOfStateVariable: "valueForDisplay"
        });
      }
      let event = await buildInputResponseEvent({
        component: this,
        verb: "answered",
        response: immediateValue,
        responseText: immediateValue.toString()
      });
      await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event
      });
      return await this.coreFunctions.triggerChainedActions({
        componentIdx: this.componentIdx,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "rawRendererValue",
            valueOfStateVariable: "rawRendererValue"
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
async function mathComponentInputFocusChanged({
  focused,
  actionId,
  sourceInformation
}) {
  return await this.coreFunctions.performUpdate({
    updateInstructions: [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "focused",
        value: focused
      }
    ],
    actionId,
    sourceInformation,
    overrideReadOnly: true,
    doNotSave: true
  });
}
const vectorAndListOperators = ["list", ...vectorOperators];
class MatrixInput extends Input {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateNumRows: this.updateNumRows.bind(this),
      updateNumColumns: this.updateNumColumns.bind(this)
    });
    defineSubmitAnswerExternalAction(this);
  }
  static componentType = "matrixInput";
  static componentDocs = {
    summary: "An interactive matrix input where each cell accepts a math value"
  };
  static variableForImplicitProp = "value";
  static processWhenJustUpdatedForNewComponent = true;
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numRows = {
      createComponentOfType: "integer",
      createStateVariable: "numRowsPreliminary",
      defaultValue: 1,
      transformNonFiniteTo: 0,
      description: "Number of rows in the matrix input."
    };
    attributes.numColumns = {
      createComponentOfType: "integer",
      createStateVariable: "numColumnsPreliminary",
      defaultValue: 1,
      transformNonFiniteTo: 0,
      description: "Number of columns in the matrix input."
    };
    attributes.showSizeControls = {
      description: "Whether to render controls for resizing the matrix.",
      createComponentOfType: "boolean",
      createStateVariable: "showSizeControls",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.defaultEntry = {
      createComponentOfType: "math",
      createStateVariable: "defaultEntry",
      defaultValue: me$3.fromAst("＿"),
      description: "Default value used for empty matrix cells."
    };
    attributes.prefill = {
      description: "Initial matrix value displayed in the input.",
      createComponentOfType: "math",
      createStateVariable: "prefill",
      defaultValue: me$3.fromAst("＿"),
      public: true,
      copyComponentAttributesForCreatedComponent: [
        "format",
        "functionSymbols",
        "splitSymbols",
        "parseScientificNotation"
      ]
    };
    Object.assign(attributes, returnMathInputParsingAttributes());
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.bindValueTo = {
      createComponentOfType: "math",
      description: "Two-way binding target for the input's value."
    };
    attributes.unionFromU = {
      description: 'Whether "U" between sets is parsed as union.',
      createComponentOfType: "boolean",
      createStateVariable: "unionFromU",
      defaultValue: false,
      public: true
    };
    attributes.minComponentWidth = {
      createComponentOfType: "integer",
      createStateVariable: "minComponentWidth",
      defaultValue: 0,
      clamp: [0, Infinity],
      description: "Minimum rendered width for each cell, in pixels."
    };
    return attributes;
  }
  // Although will actually take any math, the schema shows just matrix
  static additionalSchemaChildren = ["matrix"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let addMatrixInputGrid = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let matrixInputGrid = {
        type: "serialized",
        componentType: "_matrixInputGrid",
        componentIdx: nComponents++,
        stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
        attributes: {},
        doenetAttributes: {},
        children: [],
        state: {}
      };
      return {
        success: true,
        newChildren: [matrixInputGrid, ...matchedChildren],
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: addMatrixInputGrid
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "matrixComponentInputs",
        componentTypes: ["_matrixComponentInput"],
        excludeFromSchema: true
      },
      {
        group: "maths",
        componentTypes: ["math"],
        excludeFromSchema: true
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions({
        displayDigitsDefault: 10,
        displaySmallAsZeroDefault: 0
      })
    );
    const inputValueChangedStateVariableDefinitions = returnInputValueChangedStateVariableDefinitions({
      valueChangedDescription: "Whether the value has been changed from its initial state.",
      immediateValueChangedDescription: "Whether the value, including in-progress edits, has been changed from its initial state."
    });
    stateVariableDefinitions.valueChanged = inputValueChangedStateVariableDefinitions.valueChanged;
    stateVariableDefinitions.valueOriginal = {
      hasEssential: true,
      shadowVariable: true,
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"],
          proceedIfAllChildrenNotMatched: true
          // to avoid circular dependency with readyToExpandWhenResolved of matrixInputGrid child
        },
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["value"]
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        defaultEntry: {
          dependencyType: "stateVariable",
          variableName: "defaultEntry"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      set: convertValueToMathExpression,
      definition: function({ dependencyValues, usedDefault }) {
        if (dependencyValues.mathChild.length > 0) {
          return {
            setValue: {
              valueOriginal: dependencyValues.mathChild[0].stateValues.value
            }
          };
        } else if (dependencyValues.bindValueTo) {
          return {
            setValue: {
              valueOriginal: dependencyValues.bindValueTo.stateValues.value
            }
          };
        } else {
          let defaultValue = usedDefault.prefill ? dependencyValues.defaultEntry : dependencyValues.prefill;
          return {
            useEssentialOrDefaultValue: {
              valueOriginal: {
                defaultValue
              }
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions = [
          {
            setDependency: "valueChanged",
            desiredValue: true
          },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.mathChild.length > 0) {
          instructions.push({
            setDependency: "mathChild",
            desiredValue: desiredStateVariableValues.valueOriginal,
            variableIndex: 0,
            childIndex: 0
          });
        } else if (dependencyValues.bindValueTo) {
          instructions.push({
            setDependency: "bindValueTo",
            desiredValue: desiredStateVariableValues.valueOriginal,
            variableIndex: 0
          });
        } else {
          instructions.push({
            setEssentialValue: "valueOriginal",
            value: desiredStateVariableValues.valueOriginal
          });
        }
        return { success: true, instructions };
      }
    };
    stateVariableDefinitions.immediateValueChanged = inputValueChangedStateVariableDefinitions.immediateValueChanged;
    stateVariableDefinitions.immediateValueOriginal = {
      hasEssential: true,
      shadowVariable: true,
      returnDependencies: () => ({
        valueOriginal: {
          dependencyType: "stateVariable",
          variableName: "valueOriginal"
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      set: convertValueToMathExpression,
      definition: function({
        dependencyValues,
        changes,
        justUpdatedForNewComponent,
        usedDefault
      }) {
        if (changes.valueOriginal && !justUpdatedForNewComponent && !usedDefault.valueOriginal) {
          return {
            setValue: {
              immediateValueOriginal: dependencyValues.valueOriginal
            },
            setEssentialValue: {
              immediateValueOriginal: dependencyValues.valueOriginal
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              immediateValueOriginal: {
                defaultValue: dependencyValues.valueOriginal
              }
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        initialChange,
        shadowedVariable
      }) {
        let instructions = [
          {
            setEssentialValue: "immediateValueOriginal",
            value: desiredStateVariableValues.immediateValueOriginal
          },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (!(initialChange || shadowedVariable)) {
          instructions.push({
            setDependency: "valueOriginal",
            desiredValue: desiredStateVariableValues.immediateValueOriginal
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.haveBoundValue = {
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          proceedIfAllChildrenNotMatched: true
          // to avoid circular dependency with readyToExpandWhenResolved of matrixInputGrid child
        },
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveBoundValue: dependencyValues.mathChild.length > 0 || dependencyValues.bindValueTo !== null
          }
        };
      }
    };
    stateVariableDefinitions.numRows = {
      description: "The number of rows of the matrix.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      forRenderer: true,
      returnDependencies: () => ({
        numRowsPreliminary: {
          dependencyType: "stateVariable",
          variableName: "numRowsPreliminary"
        },
        valueOriginal: {
          dependencyType: "stateVariable",
          variableName: "valueOriginal"
        },
        haveBoundValue: {
          dependencyType: "stateVariable",
          variableName: "haveBoundValue"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let numRows = dependencyValues.numRowsPreliminary;
        if (usedDefault.numRowsPreliminary || dependencyValues.haveBoundValue) {
          let originalTree = dependencyValues.valueOriginal.tree;
          numRows = 1;
          if (Array.isArray(originalTree)) {
            let operator = originalTree[0];
            if (operator === "matrix") {
              numRows = originalTree[1][1];
            } else if (vectorOperators.includes(operator)) {
              numRows = originalTree.length - 1;
            }
          }
        }
        return { setValue: { numRows } };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        let desiredNumRows = desiredStateVariableValues.numRows;
        if (!Number.isInteger(desiredNumRows)) {
          return { success: false };
        }
        desiredNumRows = Math.max(0, desiredNumRows);
        let instructions = [
          {
            setDependency: "numRowsPreliminary",
            desiredValue: desiredNumRows
          },
          { setDependency: "valueChanged", desiredValue: true },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.haveBoundValue) {
          let originalTree = dependencyValues.valueOriginal.tree;
          let defaultEntryTree = (await stateValues.defaultEntry).tree;
          if (await stateValues.numColumns === 1 && Array.isArray(originalTree) && vectorOperators.includes(originalTree[0])) {
            let currentNumRows = originalTree.length - 1;
            if (desiredNumRows < currentNumRows) {
              let newTree = deepClone(originalTree).slice(
                0,
                desiredNumRows + 1
              );
              instructions.push({
                setDependency: "valueOriginal",
                desiredValue: me$3.fromAst(newTree)
              });
            } else if (desiredNumRows > currentNumRows) {
              let newTree = deepClone(originalTree);
              let accumulatedComponents = await stateValues.accumulatedComponents;
              for (let rowInd = currentNumRows; rowInd < desiredNumRows; rowInd++) {
                let accumRow = accumulatedComponents[rowInd];
                let accumVal;
                if (accumRow) {
                  accumVal = accumRow[0];
                }
                newTree[rowInd + 1] = accumVal === void 0 ? defaultEntryTree : accumVal;
              }
              instructions.push({
                setDependency: "valueOriginal",
                desiredValue: me$3.fromAst(newTree)
              });
            }
          } else {
            let valueTree = deepClone(
              (await stateValues.value).tree
            );
            let previousNumRows = valueTree[1][1];
            valueTree[1][1] = desiredNumRows;
            if (desiredNumRows !== previousNumRows) {
              if (desiredNumRows < previousNumRows) {
                valueTree[2].length = desiredNumRows + 1;
              } else {
                let numColumns = await stateValues.numColumns;
                let data = valueTree[2];
                let accumulatedComponents = await stateValues.accumulatedComponents;
                for (let rowInd = previousNumRows; rowInd < desiredNumRows; rowInd++) {
                  if (!data[rowInd + 1]) {
                    data[rowInd + 1] = ["tuple"];
                  }
                  let accumRow = accumulatedComponents[rowInd];
                  if (!accumRow) {
                    accumRow = [];
                  }
                  for (let colInd = 0; colInd < numColumns; colInd++) {
                    let accumVal = accumRow[colInd];
                    data[rowInd + 1][colInd + 1] = accumVal === void 0 ? defaultEntryTree : accumVal;
                  }
                }
              }
              instructions.push({
                setDependency: "valueOriginal",
                desiredValue: me$3.fromAst(valueTree)
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numColumns = {
      description: "The number of columns of the matrix.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      forRenderer: true,
      returnDependencies: () => ({
        numColumnsPreliminary: {
          dependencyType: "stateVariable",
          variableName: "numColumnsPreliminary"
        },
        valueOriginal: {
          dependencyType: "stateVariable",
          variableName: "valueOriginal"
        },
        haveBoundValue: {
          dependencyType: "stateVariable",
          variableName: "haveBoundValue"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let numColumns = dependencyValues.numColumnsPreliminary;
        if (usedDefault.numColumnsPreliminary || dependencyValues.haveBoundValue) {
          let originalTree = dependencyValues.valueOriginal.tree;
          numColumns = 1;
          if (Array.isArray(originalTree)) {
            let operator = originalTree[0];
            if (operator === "matrix") {
              numColumns = originalTree[1][2];
            } else if (Array.isArray(originalTree[1]) && vectorOperators.includes(originalTree[1][0]) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
              numColumns = originalTree[1].length - 1;
            }
          }
        }
        return { setValue: { numColumns } };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        let desiredNumColumns = desiredStateVariableValues.numColumns;
        if (!Number.isInteger(desiredNumColumns)) {
          return { success: false };
        }
        desiredNumColumns = Math.max(0, desiredNumColumns);
        let instructions = [
          {
            setDependency: "numColumnsPreliminary",
            desiredValue: desiredNumColumns
          },
          { setDependency: "valueChanged", desiredValue: true },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.haveBoundValue) {
          let defaultEntryTree = (await stateValues.defaultEntry).tree;
          let originalTree = dependencyValues.valueOriginal.tree;
          let operator = originalTree[0];
          if (Array.isArray(originalTree[1]) && vectorOperators.includes(originalTree[1][0]) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
            let currentNumColumns = originalTree[1].length - 1;
            if (desiredNumColumns < currentNumColumns) {
              let newTree = deepClone(originalTree);
              newTree[1] = newTree[1].slice(
                0,
                desiredNumColumns + 1
              );
              instructions.push({
                setDependency: "valueOriginal",
                desiredValue: me$3.fromAst(newTree)
              });
            } else if (desiredNumColumns > currentNumColumns) {
              let newTree = deepClone(originalTree);
              let accumRow = (await stateValues.accumulatedComponents)[0];
              if (!accumRow) {
                accumRow = [];
              }
              for (let colInd = currentNumColumns; colInd < desiredNumColumns; colInd++) {
                let accumVal = accumRow[colInd];
                newTree[1][colInd + 1] = accumVal === void 0 ? defaultEntryTree : accumVal;
              }
              instructions.push({
                setDependency: "valueOriginal",
                desiredValue: me$3.fromAst(newTree)
              });
            }
          } else {
            let valueTree = deepClone(
              (await stateValues.value).tree
            );
            let previousNumColumns = valueTree[1][2];
            valueTree[1][2] = desiredNumColumns;
            if (desiredNumColumns !== previousNumColumns) {
              let numRows = await stateValues.numRows;
              let data = valueTree[2];
              if (desiredNumColumns < previousNumColumns) {
                for (let rowInd = 0; rowInd < numRows; rowInd++) {
                  data[rowInd + 1].length = desiredNumColumns + 1;
                }
              } else {
                let accumulatedComponents = await stateValues.accumulatedComponents;
                for (let rowInd = 0; rowInd < numRows; rowInd++) {
                  let accumRow = accumulatedComponents[rowInd];
                  if (!accumRow) {
                    accumRow = [];
                  }
                  for (let colInd = previousNumColumns; colInd < desiredNumColumns; colInd++) {
                    let accumVal = accumRow[colInd];
                    data[rowInd + 1][colInd + 1] = accumVal === void 0 ? defaultEntryTree : accumVal;
                  }
                }
              }
              instructions.push({
                setDependency: "valueOriginal",
                desiredValue: me$3.fromAst(valueTree)
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.accumulatedComponents = {
      providePreviousValuesInDefinition: true,
      returnDependencies: () => ({
        valueOriginal: {
          dependencyType: "stateVariable",
          variableName: "valueOriginal"
        }
      }),
      definition({ dependencyValues, previousValues }) {
        let accumulatedComponents = [];
        if (previousValues.accumulatedComponents) {
          accumulatedComponents = deepClone(
            previousValues.accumulatedComponents
          );
        }
        let originalTree = dependencyValues.valueOriginal.tree;
        if (Array.isArray(originalTree)) {
          let operator = originalTree[0];
          if (operator === "matrix") {
            let data = originalTree[2];
            for (let [rowInd, row] of data.slice(1).entries()) {
              let accumRow2 = accumulatedComponents[rowInd];
              if (!accumRow2) {
                accumRow2 = accumulatedComponents[rowInd] = [];
              }
              for (let [colInd, comp] of row.slice(1).entries()) {
                accumRow2[colInd] = comp;
              }
            }
            return { setValue: { accumulatedComponents } };
          } else if (vectorOperators.includes(operator)) {
            for (let [rowInd, comp] of originalTree.slice(1).entries()) {
              let accumRow2 = accumulatedComponents[rowInd];
              if (!accumRow2) {
                accumRow2 = accumulatedComponents[rowInd] = [];
              }
              accumRow2[0] = comp;
            }
            return { setValue: { accumulatedComponents } };
          } else if (Array.isArray(originalTree[1]) && vectorOperators.includes(originalTree[1][0]) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
            let accumRow2 = accumulatedComponents[0];
            if (!accumRow2) {
              accumRow2 = accumulatedComponents[0] = [];
            }
            for (let [colInd, comp] of originalTree[1].slice(1).entries()) {
              accumRow2[colInd] = comp;
            }
            return { setValue: { accumulatedComponents } };
          }
        }
        let accumRow = accumulatedComponents[0];
        if (!accumRow) {
          accumRow = accumulatedComponents[0] = [];
        }
        accumRow[0] = originalTree;
        return { setValue: { accumulatedComponents } };
      }
    };
    stateVariableDefinitions.componentValues = {
      isArray: true,
      entryPrefixes: ["componentValue"],
      numDimensions: 2,
      returnArraySizeDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numRows, dependencyValues.numColumns];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          valueOriginal: {
            dependencyType: "stateVariable",
            variableName: "valueOriginal"
          },
          numRows: {
            dependencyType: "stateVariable",
            variableName: "numRows"
          },
          numColumns: {
            dependencyType: "stateVariable",
            variableName: "numColumns"
          },
          accumulatedComponents: {
            dependencyType: "stateVariable",
            variableName: "accumulatedComponents"
          },
          defaultEntry: {
            dependencyType: "stateVariable",
            variableName: "defaultEntry"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let originalTree = globalDependencyValues.valueOriginal.tree;
        let accumulatedComponents = globalDependencyValues.accumulatedComponents;
        let numRows = globalDependencyValues.numRows;
        let numColumns = globalDependencyValues.numColumns;
        if (Array.isArray(originalTree)) {
          let operator = originalTree[0];
          if (operator === "matrix") {
            if (originalTree[1][1] === numRows && originalTree[1][2] === numColumns) {
              let componentValues3 = {};
              let originalData2 = originalTree[2];
              for (let rowInd = 0; rowInd < numRows; rowInd++) {
                for (let colInd = 0; colInd < numColumns; colInd++) {
                  let arrayKey = `${rowInd},${colInd}`;
                  componentValues3[arrayKey] = me$3.fromAst(
                    originalData2[rowInd + 1][colInd + 1]
                  );
                }
              }
              return { setValue: { componentValues: componentValues3 } };
            }
            let originalNumRows = originalTree[1][1];
            let originalNumColumns = originalTree[1][2];
            let componentValues2 = {};
            let originalData = originalTree[2];
            let numOverlapRows = Math.min(numRows, originalNumRows);
            let numOverlapColumns = Math.min(
              numColumns,
              originalNumColumns
            );
            for (let rowInd = 0; rowInd < numOverlapRows; rowInd++) {
              for (let colInd = 0; colInd < numOverlapColumns; colInd++) {
                let arrayKey = `${rowInd},${colInd}`;
                componentValues2[arrayKey] = me$3.fromAst(
                  originalData[rowInd + 1][colInd + 1]
                );
              }
            }
            if (numColumns > originalNumColumns) {
              for (let rowInd = 0; rowInd < numOverlapRows; rowInd++) {
                let accumRow = accumulatedComponents[rowInd];
                for (let colInd = originalNumColumns; colInd < numColumns; colInd++) {
                  let accumVal = accumRow[colInd];
                  let arrayKey = `${rowInd},${colInd}`;
                  componentValues2[arrayKey] = me$3.fromAst(
                    accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                  );
                }
              }
            }
            if (numRows > originalNumRows) {
              for (let rowInd = originalNumRows; rowInd < numRows; rowInd++) {
                let accumRow = accumulatedComponents[rowInd];
                if (!accumRow) {
                  accumRow = [];
                }
                for (let colInd = 0; colInd < numColumns; colInd++) {
                  let accumVal = accumRow[colInd];
                  let arrayKey = `${rowInd},${colInd}`;
                  componentValues2[arrayKey] = me$3.fromAst(
                    accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                  );
                }
              }
            }
            return { setValue: { componentValues: componentValues2 } };
          } else if (vectorOperators.includes(operator)) {
            let operands = originalTree.slice(1);
            let numRowsFound = operands.length;
            let componentValues2 = {};
            for (let rowInd = 0; rowInd < numRows; rowInd++) {
              let minCol = 0;
              if (rowInd < numRowsFound) {
                let arrayKey = `${rowInd},${0}`;
                componentValues2[arrayKey] = me$3.fromAst(
                  operands[rowInd]
                );
                minCol = 1;
              }
              let accumRow = accumulatedComponents[rowInd];
              if (!accumRow) {
                accumRow = [];
              }
              for (let colInd = minCol; colInd < numColumns; colInd++) {
                let accumVal = accumRow[colInd];
                let arrayKey = `${rowInd},${colInd}`;
                componentValues2[arrayKey] = me$3.fromAst(
                  accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                );
              }
            }
            return { setValue: { componentValues: componentValues2 } };
          } else if (Array.isArray(originalTree[1]) && vectorOperators.includes(originalTree[1][0]) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
            let operands = originalTree[1].slice(1, numColumns + 1);
            accumulatedComponents[0];
            let componentValues2 = {};
            for (let rowInd = 0; rowInd < numRows; rowInd++) {
              let accumRow = accumulatedComponents[rowInd];
              if (!accumRow) {
                accumRow = [];
              }
              let minCol = 0;
              if (rowInd === 0) {
                for (let colInd = 0; colInd < operands.length; colInd++) {
                  let arrayKey = `${0},${colInd}`;
                  componentValues2[arrayKey] = me$3.fromAst(
                    operands[colInd]
                  );
                }
                minCol = operands.length;
              }
              for (let colInd = minCol; colInd < numColumns; colInd++) {
                let accumVal = accumRow[colInd];
                let arrayKey = `${rowInd},${colInd}`;
                componentValues2[arrayKey] = me$3.fromAst(
                  accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                );
              }
            }
            return { setValue: { componentValues: componentValues2 } };
          }
        }
        let componentValues = {};
        for (let rowInd = 0; rowInd < numRows; rowInd++) {
          let minCol = 0;
          if (rowInd === 0) {
            let arrayKey = `${0},${0}`;
            componentValues[arrayKey] = me$3.fromAst(originalTree);
            minCol = 1;
          }
          let accumRow = accumulatedComponents[rowInd];
          if (!accumRow) {
            accumRow = [];
          }
          for (let colInd = minCol; colInd < numColumns; colInd++) {
            let accumVal = accumRow[colInd];
            let arrayKey = `${rowInd},${colInd}`;
            componentValues[arrayKey] = me$3.fromAst(
              accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
            );
          }
        }
        return { setValue: { componentValues } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        initialChange,
        workspace
      }) {
        let numRows = globalDependencyValues.numRows;
        let numColumns = globalDependencyValues.numColumns;
        let accumulatedComponents = globalDependencyValues.accumulatedComponents;
        let originalIsColumnVector = false;
        let originalIsRowVector = false;
        let originalIsMatrix = false;
        let originalTree = globalDependencyValues.valueOriginal.tree;
        if (Array.isArray(originalTree)) {
          let operator = originalTree[0];
          if (vectorOperators.includes(operator)) {
            originalIsColumnVector = true;
          } else if (operator === "matrix") {
            originalIsMatrix = true;
          } else if (Array.isArray(originalTree[1]) && vectorOperators.includes(originalTree[1][0]) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
            originalIsRowVector = true;
          }
        }
        let valueData;
        if (workspace.valueData) {
          valueData = workspace.valueData.map((x2) => [...x2]);
        } else {
          let originalTree2 = globalDependencyValues.valueOriginal.tree;
          if (originalIsColumnVector) {
            valueData = originalTree2.slice(1, 1 + numRows).map((x2) => [x2]);
            if (valueData.length < numRows) {
              for (let rowInd = valueData.length; rowInd < numRows; rowInd++) {
                let accumRow = accumulatedComponents[rowInd] ?? [];
                valueData.push([
                  accumRow[0] ?? globalDependencyValues.defaultEntry.tree
                ]);
              }
            }
            if (numColumns > 1) {
              for (let rowInd = 0; rowInd < numRows; rowInd++) {
                valueData[rowInd].push(
                  ...Array(numColumns - 1).fill(
                    globalDependencyValues.defaultEntry.tree
                  )
                );
              }
            }
          } else if (originalIsRowVector) {
            valueData = [originalTree2[1].slice(1, 1 + numColumns)];
            if (valueData[0].length < numColumns) {
              for (let colInd = valueData[0].length; colInd < numColumns; colInd++) {
                let accumRow = accumulatedComponents[0] ?? [];
                valueData[0].push(
                  accumRow[colInd] ?? globalDependencyValues.defaultEntry.tree
                );
              }
            }
            if (numRows > 1) {
              for (let rowInd = 1; rowInd < numRows; rowInd++) {
                valueData.push(
                  Array(numColumns).fill(
                    globalDependencyValues.defaultEntry.tree
                  )
                );
              }
            }
          } else if (originalIsMatrix) {
            valueData = originalTree2[2].slice(1, numRows + 1).map((x2) => x2.slice(1, numColumns + 1));
            if (valueData[0].length < numColumns) {
              for (let rowInd = 0; rowInd < valueData.length; rowInd++) {
                let row = valueData[rowInd];
                let accumRow = accumulatedComponents[rowInd] ?? [];
                for (let colInd = row.length; colInd < numColumns; colInd++) {
                  row.push(
                    accumRow[colInd] ?? globalDependencyValues.defaultEntry.tree
                  );
                }
              }
            }
            if (valueData.length < numRows) {
              for (let rowInd = valueData.length; rowInd < numRows; rowInd++) {
                let row = [];
                let accumRow = accumulatedComponents[rowInd] ?? [];
                for (let colInd = 0; colInd < numColumns; colInd++) {
                  row.push(
                    accumRow[colInd] ?? globalDependencyValues.defaultEntry.tree
                  );
                }
                valueData.push(row);
              }
            }
          } else {
            valueData = [[originalTree2]];
            if (numColumns > 1) {
              valueData[0].push(
                ...Array(numColumns - 1).fill(
                  globalDependencyValues.defaultEntry.tree
                )
              );
            }
            if (numRows > 1) {
              for (let rowInd = 1; rowInd < numRows; rowInd++) {
                valueData.push(
                  Array(numColumns).fill(
                    globalDependencyValues.defaultEntry.tree
                  )
                );
              }
            }
          }
        }
        for (let arrayKey in desiredStateVariableValues.componentValues) {
          let [rowInd, colInd] = arrayKey.split(",");
          valueData[rowInd][colInd] = convertValueToMathExpression(
            desiredStateVariableValues.componentValues[arrayKey]
          ).tree;
        }
        workspace.valueData = valueData;
        if (numColumns === 1 && originalIsColumnVector) {
          let operator = globalDependencyValues.valueOriginal.tree[0];
          let desiredValue = me$3.fromAst([
            operator,
            ...valueData.map((x2) => x2[0])
          ]);
          return {
            success: true,
            instructions: [
              {
                setDependency: "valueOriginal",
                desiredValue,
                treatAsInitialChange: initialChange
              }
            ]
          };
        } else if (numRows === 1 && originalIsRowVector) {
          let originalTree2 = globalDependencyValues.valueOriginal.tree;
          let operator = originalTree2[0];
          let desiredValue = [originalTree2[1][0], ...valueData[0]];
          if (operator === "^") {
            desiredValue = me$3.fromAst(["^", desiredValue, "T"]);
          } else {
            desiredValue = me$3.fromAst(["prime", desiredValue]);
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "valueOriginal",
                desiredValue,
                treatAsInitialChange: initialChange
              }
            ]
          };
        } else {
          let desiredValueTree = [
            "matrix",
            [
              "tuple",
              globalDependencyValues.numRows,
              globalDependencyValues.numColumns
            ],
            ["tuple", ...valueData.map((x2) => ["tuple", ...x2])]
          ];
          return {
            success: true,
            instructions: [
              {
                setDependency: "valueOriginal",
                desiredValue: me$3.fromAst(desiredValueTree),
                treatAsInitialChange: initialChange
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.componentImmediateValues = {
      isArray: true,
      entryPrefixes: ["componentImmediateValue"],
      numDimensions: 2,
      returnArraySizeDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numRows, dependencyValues.numColumns];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          immediateValueOriginal: {
            dependencyType: "stateVariable",
            variableName: "immediateValueOriginal"
          },
          numRows: {
            dependencyType: "stateVariable",
            variableName: "numRows"
          },
          numColumns: {
            dependencyType: "stateVariable",
            variableName: "numColumns"
          },
          accumulatedComponents: {
            dependencyType: "stateVariable",
            variableName: "accumulatedComponents"
          },
          defaultEntry: {
            dependencyType: "stateVariable",
            variableName: "defaultEntry"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let originalTree = globalDependencyValues.immediateValueOriginal.tree;
        let accumulatedComponents = globalDependencyValues.accumulatedComponents;
        let numRows = globalDependencyValues.numRows;
        let numColumns = globalDependencyValues.numColumns;
        if (Array.isArray(originalTree)) {
          let operator = originalTree[0];
          if (operator === "matrix") {
            if (originalTree[1][1] === numRows && originalTree[1][2] === numColumns) {
              let componentImmediateValues3 = {};
              let originalData2 = originalTree[2];
              for (let rowInd = 0; rowInd < numRows; rowInd++) {
                for (let colInd = 0; colInd < numColumns; colInd++) {
                  let arrayKey = `${rowInd},${colInd}`;
                  componentImmediateValues3[arrayKey] = me$3.fromAst(
                    originalData2[rowInd + 1][colInd + 1]
                  );
                }
              }
              return { setValue: { componentImmediateValues: componentImmediateValues3 } };
            }
            let originalNumRows = originalTree[1][1];
            let originalNumColumns = originalTree[1][2];
            let componentImmediateValues2 = {};
            let originalData = originalTree[2];
            let numOverlapRows = Math.min(numRows, originalNumRows);
            let numOverlapColumns = Math.min(
              numColumns,
              originalNumColumns
            );
            for (let rowInd = 0; rowInd < numOverlapRows; rowInd++) {
              for (let colInd = 0; colInd < numOverlapColumns; colInd++) {
                let arrayKey = `${rowInd},${colInd}`;
                componentImmediateValues2[arrayKey] = me$3.fromAst(
                  originalData[rowInd + 1][colInd + 1]
                );
              }
            }
            if (numColumns > originalNumColumns) {
              for (let rowInd = 0; rowInd < numOverlapRows; rowInd++) {
                let accumRow = accumulatedComponents[rowInd];
                for (let colInd = originalNumColumns; colInd < numColumns; colInd++) {
                  let accumVal = accumRow[colInd];
                  let arrayKey = `${rowInd},${colInd}`;
                  componentImmediateValues2[arrayKey] = me$3.fromAst(
                    accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                  );
                }
              }
            }
            if (numRows > originalNumRows) {
              for (let rowInd = originalNumRows; rowInd < numRows; rowInd++) {
                let accumRow = accumulatedComponents[rowInd];
                if (!accumRow) {
                  accumRow = [];
                }
                for (let colInd = 0; colInd < numColumns; colInd++) {
                  let accumVal = accumRow[colInd];
                  let arrayKey = `${rowInd},${colInd}`;
                  componentImmediateValues2[arrayKey] = me$3.fromAst(
                    accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                  );
                }
              }
            }
            return { setValue: { componentImmediateValues: componentImmediateValues2 } };
          } else if (vectorOperators.includes(operator)) {
            let operands = originalTree.slice(1);
            let numRowsFound = operands.length;
            let componentImmediateValues2 = {};
            for (let rowInd = 0; rowInd < numRows; rowInd++) {
              let minCol = 0;
              if (rowInd < numRowsFound) {
                let arrayKey = `${rowInd},${0}`;
                componentImmediateValues2[arrayKey] = me$3.fromAst(
                  operands[rowInd]
                );
                minCol = 1;
              }
              let accumRow = accumulatedComponents[rowInd];
              if (!accumRow) {
                accumRow = [];
              }
              for (let colInd = minCol; colInd < numColumns; colInd++) {
                let accumVal = accumRow[colInd];
                let arrayKey = `${rowInd},${colInd}`;
                componentImmediateValues2[arrayKey] = me$3.fromAst(
                  accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                );
              }
            }
            return { setValue: { componentImmediateValues: componentImmediateValues2 } };
          } else if (Array.isArray(originalTree[1]) && vectorOperators.includes(originalTree[1][0]) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
            let operands = originalTree[1].slice(1, numColumns + 1);
            accumulatedComponents[0];
            let componentImmediateValues2 = {};
            for (let rowInd = 0; rowInd < numRows; rowInd++) {
              let accumRow = accumulatedComponents[rowInd];
              if (!accumRow) {
                accumRow = [];
              }
              let minCol = 0;
              if (rowInd === 0) {
                for (let colInd = 0; colInd < operands.length; colInd++) {
                  let arrayKey = `${0},${colInd}`;
                  componentImmediateValues2[arrayKey] = me$3.fromAst(operands[colInd]);
                }
                minCol = operands.length;
              }
              for (let colInd = minCol; colInd < numColumns; colInd++) {
                let accumVal = accumRow[colInd];
                let arrayKey = `${rowInd},${colInd}`;
                componentImmediateValues2[arrayKey] = me$3.fromAst(
                  accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
                );
              }
            }
            return { setValue: { componentImmediateValues: componentImmediateValues2 } };
          }
        }
        let componentImmediateValues = {};
        for (let rowInd = 0; rowInd < numRows; rowInd++) {
          let minCol = 0;
          if (rowInd === 0) {
            let arrayKey = `${0},${0}`;
            componentImmediateValues[arrayKey] = me$3.fromAst(originalTree);
            minCol = 1;
          }
          let accumRow = accumulatedComponents[rowInd];
          if (!accumRow) {
            accumRow = [];
          }
          for (let colInd = minCol; colInd < numColumns; colInd++) {
            let accumVal = accumRow[colInd];
            let arrayKey = `${rowInd},${colInd}`;
            componentImmediateValues[arrayKey] = me$3.fromAst(
              accumVal === void 0 ? globalDependencyValues.defaultEntry.tree : accumVal
            );
          }
        }
        return { setValue: { componentImmediateValues } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        initialChange,
        workspace
      }) {
        let accumulatedComponents = globalDependencyValues.accumulatedComponents;
        let numRows = globalDependencyValues.numRows;
        let numColumns = globalDependencyValues.numColumns;
        let originalIsColumnVector = false;
        let originalIsRowVector = false;
        let originalIsMatrix = false;
        let originalTree = globalDependencyValues.immediateValueOriginal.tree;
        if (Array.isArray(originalTree)) {
          let operator = originalTree[0];
          if (vectorOperators.includes(operator)) {
            originalIsColumnVector = true;
          } else if (operator === "matrix") {
            originalIsMatrix = true;
          } else if (Array.isArray(originalTree[1]) && vectorOperators.includes(originalTree[1][0]) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
            originalIsRowVector = true;
          }
        }
        let immediateValueData;
        if (workspace.immediateValueData) {
          immediateValueData = workspace.immediateValueData.map(
            (x2) => [...x2]
          );
        } else {
          let originalTree2 = globalDependencyValues.immediateValueOriginal.tree;
          if (originalIsColumnVector) {
            immediateValueData = originalTree2.slice(1, 1 + numRows).map((x2) => [x2]);
            if (immediateValueData.length < numRows) {
              for (let rowInd = immediateValueData.length; rowInd < numRows; rowInd++) {
                let accumRow = accumulatedComponents[rowInd] ?? [];
                immediateValueData.push([
                  accumRow[0] ?? globalDependencyValues.defaultEntry.tree
                ]);
              }
            }
            if (numColumns > 1) {
              for (let rowInd = 0; rowInd < numRows; rowInd++) {
                immediateValueData[rowInd].push(
                  ...Array(numColumns - 1).fill(
                    globalDependencyValues.defaultEntry.tree
                  )
                );
              }
            }
          } else if (originalIsRowVector) {
            immediateValueData = [
              originalTree2[1].slice(1, 1 + numColumns)
            ];
            if (immediateValueData[0].length < numColumns) {
              for (let colInd = immediateValueData[0].length; colInd < numColumns; colInd++) {
                let accumRow = accumulatedComponents[0] ?? [];
                immediateValueData[0].push(
                  accumRow[colInd] ?? globalDependencyValues.defaultEntry.tree
                );
              }
            }
            if (numRows > 1) {
              for (let rowInd = 1; rowInd < numRows; rowInd++) {
                immediateValueData.push(
                  Array(numColumns).fill(
                    globalDependencyValues.defaultEntry.tree
                  )
                );
              }
            }
          } else if (originalIsMatrix) {
            immediateValueData = originalTree2[2].slice(1, numRows + 1).map((x2) => x2.slice(1, numColumns + 1));
            if (immediateValueData[0].length < numColumns) {
              for (let rowInd = 0; rowInd < immediateValueData.length; rowInd++) {
                let row = immediateValueData[rowInd];
                let accumRow = accumulatedComponents[rowInd] ?? [];
                for (let colInd = row.length; colInd < numColumns; colInd++) {
                  row.push(
                    accumRow[colInd] ?? globalDependencyValues.defaultEntry.tree
                  );
                }
              }
            }
            if (immediateValueData.length < numRows) {
              for (let rowInd = immediateValueData.length; rowInd < numRows; rowInd++) {
                let row = [];
                let accumRow = accumulatedComponents[rowInd] ?? [];
                for (let colInd = 0; colInd < numColumns; colInd++) {
                  row.push(
                    accumRow[colInd] ?? globalDependencyValues.defaultEntry.tree
                  );
                }
                immediateValueData.push(row);
              }
            }
          } else {
            immediateValueData = [[originalTree2]];
            if (numColumns > 1) {
              immediateValueData[0].push(
                ...Array(numColumns - 1).fill(
                  globalDependencyValues.defaultEntry.tree
                )
              );
            }
            if (numRows > 1) {
              for (let rowInd = 1; rowInd < numRows; rowInd++) {
                immediateValueData.push(
                  Array(numColumns).fill(
                    globalDependencyValues.defaultEntry.tree
                  )
                );
              }
            }
          }
        }
        for (let arrayKey in desiredStateVariableValues.componentImmediateValues) {
          let [rowInd, colInd] = arrayKey.split(",");
          immediateValueData[rowInd][colInd] = convertValueToMathExpression(
            desiredStateVariableValues.componentImmediateValues[arrayKey]
          ).tree;
        }
        workspace.immediateValueData = immediateValueData;
        if (numColumns === 1 && originalIsColumnVector) {
          let operator = globalDependencyValues.immediateValueOriginal.tree[0];
          let desiredValue = me$3.fromAst([
            operator,
            ...immediateValueData.map((x2) => x2[0])
          ]);
          return {
            success: true,
            instructions: [
              {
                setDependency: "immediateValueOriginal",
                desiredValue,
                treatAsInitialChange: initialChange
              }
            ]
          };
        } else if (numRows === 1 && originalIsRowVector) {
          let originalTree2 = globalDependencyValues.immediateValueOriginal.tree;
          let operator = originalTree2[0];
          let desiredValue = [
            originalTree2[1][0],
            ...immediateValueData[0]
          ];
          if (operator === "^") {
            desiredValue = me$3.fromAst(["^", desiredValue, "T"]);
          } else {
            desiredValue = me$3.fromAst(["prime", desiredValue]);
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "immediateValueOriginal",
                desiredValue,
                treatAsInitialChange: initialChange
              }
            ]
          };
        } else {
          let desiredValueTree = [
            "matrix",
            [
              "tuple",
              globalDependencyValues.numRows,
              globalDependencyValues.numColumns
            ],
            [
              "tuple",
              ...immediateValueData.map((x2) => ["tuple", ...x2])
            ]
          ];
          return {
            success: true,
            instructions: [
              {
                setDependency: "immediateValueOriginal",
                desiredValue: me$3.fromAst(desiredValueTree),
                treatAsInitialChange: initialChange
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.componentValuesForDisplay = {
      isArray: true,
      entryPrefixes: ["componentValueForDisplay"],
      numDimensions: 2,
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numRows, dependencyValues.numColumns];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          displayDigits: {
            dependencyType: "stateVariable",
            variableName: "displayDigits"
          },
          displayDecimals: {
            dependencyType: "stateVariable",
            variableName: "displayDecimals"
          },
          displaySmallAsZero: {
            dependencyType: "stateVariable",
            variableName: "displaySmallAsZero"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [rowInd, colInd] = arrayKey.split(",");
          let varEnding = Number(rowInd) + 1 + "_" + (Number(colInd) + 1);
          dependenciesByKey[arrayKey] = {
            componentValue: {
              dependencyType: "stateVariable",
              variableName: `componentValue${varEnding}`
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let componentValuesForDisplay = {};
        for (let arrayKey of arrayKeys) {
          let rounded = roundForDisplay({
            value: dependencyValuesByKey[arrayKey].componentValue,
            dependencyValues: globalDependencyValues
          });
          componentValuesForDisplay[arrayKey] = rounded;
        }
        return { setValue: { componentValuesForDisplay } };
      }
    };
    stateVariableDefinitions.value = {
      description: "The matrix value of the input.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        componentValues: {
          dependencyType: "stateVariable",
          variableName: "componentValues"
        },
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        },
        // value original is just for inverse definition
        valueOriginal: {
          dependencyType: "stateVariable",
          variableName: "valueOriginal"
        }
      }),
      definition({ dependencyValues }) {
        let numRows = dependencyValues.numRows;
        let numColumns = dependencyValues.numColumns;
        let newTree = ["matrix", ["tuple", numRows, numColumns]];
        let data = newTree[2] = ["tuple"];
        for (let rowInd = 0; rowInd < numRows; rowInd++) {
          data[rowInd + 1] = ["tuple"];
          for (let colInd = 0; colInd < numColumns; colInd++) {
            data[rowInd + 1][colInd + 1] = dependencyValues.componentValues[rowInd][colInd].tree;
          }
        }
        return { setValue: { value: me$3.fromAst(newTree) } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredTree = desiredStateVariableValues.value.tree;
        if (Array.isArray(desiredTree) && desiredTree[0] === "matrix") {
          if (desiredTree[1][1] === dependencyValues.numRows && desiredTree[1][2] === dependencyValues.numColumns) {
            if (dependencyValues.numColumns === 1) {
              let originalTree = dependencyValues.valueOriginal.tree;
              if (Array.isArray(originalTree)) {
                let operator = originalTree[0];
                if (vectorOperators.includes(operator)) {
                  let desiredValue = me$3.fromAst([
                    operator,
                    ...desiredTree[2].slice(1).map((x2) => x2[1])
                  ]);
                  return {
                    success: true,
                    instructions: [
                      {
                        setDependency: "valueOriginal",
                        desiredValue
                      }
                    ]
                  };
                }
              }
            } else if (dependencyValues.numRows === 1) {
              let originalTree = dependencyValues.valueOriginal.tree;
              if (Array.isArray(originalTree)) {
                let operator = originalTree[0];
                if (Array.isArray(originalTree[1]) && vectorOperators.includes(
                  originalTree[1][0]
                ) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
                  let desiredValue = [
                    originalTree[1][0],
                    ...desiredTree[2][1].slice(1)
                  ];
                  if (operator === "^") {
                    desiredValue = me$3.fromAst([
                      "^",
                      desiredValue,
                      "T"
                    ]);
                  } else {
                    desiredValue = me$3.fromAst([
                      "prime",
                      desiredValue
                    ]);
                  }
                  return {
                    success: true,
                    instructions: [
                      {
                        setDependency: "valueOriginal",
                        desiredValue
                      }
                    ]
                  };
                }
              }
            }
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "valueOriginal",
                desiredValue: desiredStateVariableValues.value
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.immediateValue = {
      description: "The matrix value reflecting the user's in-progress edits.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        componentImmediateValues: {
          dependencyType: "stateVariable",
          variableName: "componentImmediateValues"
        },
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        },
        // immediateValue original is just for inverse definition
        immediateValueOriginal: {
          dependencyType: "stateVariable",
          variableName: "immediateValueOriginal"
        }
      }),
      definition({ dependencyValues }) {
        let numRows = dependencyValues.numRows;
        let numColumns = dependencyValues.numColumns;
        let newTree = ["matrix", ["tuple", numRows, numColumns]];
        let data = newTree[2] = ["tuple"];
        for (let rowInd = 0; rowInd < numRows; rowInd++) {
          data[rowInd + 1] = ["tuple"];
          for (let colInd = 0; colInd < numColumns; colInd++) {
            data[rowInd + 1][colInd + 1] = dependencyValues.componentImmediateValues[rowInd][colInd].tree;
          }
        }
        return { setValue: { immediateValue: me$3.fromAst(newTree) } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredTree = desiredStateVariableValues.immediateValue.tree;
        if (Array.isArray(desiredTree) && desiredTree[0] === "matrix") {
          if (desiredTree[1][1] === dependencyValues.numRows && desiredTree[1][2] === dependencyValues.numColumns) {
            if (dependencyValues.numColumns === 1) {
              let originalTree = dependencyValues.immediateValueOriginal.tree;
              if (Array.isArray(originalTree)) {
                let operator = originalTree[0];
                if (vectorOperators.includes(operator)) {
                  let desiredValue = me$3.fromAst([
                    operator,
                    ...desiredTree[2].slice(1).map((x2) => x2[1])
                  ]);
                  return {
                    success: true,
                    instructions: [
                      {
                        setDependency: "immediateValueOriginal",
                        desiredValue
                      }
                    ]
                  };
                }
              }
            } else if (dependencyValues.numRows === 1) {
              let originalTree = dependencyValues.immediateValueOriginal.tree;
              if (Array.isArray(originalTree)) {
                let operator = originalTree[0];
                if (Array.isArray(originalTree[1]) && vectorOperators.includes(
                  originalTree[1][0]
                ) && (operator === "^" && originalTree[2] === "T" || operator === "prime")) {
                  let desiredValue = [
                    originalTree[1][0],
                    ...desiredTree[2][1].slice(1)
                  ];
                  if (operator === "^") {
                    desiredValue = me$3.fromAst([
                      "^",
                      desiredValue,
                      "T"
                    ]);
                  } else {
                    desiredValue = me$3.fromAst([
                      "prime",
                      desiredValue
                    ]);
                  }
                  return {
                    success: true,
                    instructions: [
                      {
                        setDependency: "immediateValueOriginal",
                        desiredValue
                      }
                    ]
                  };
                }
              }
            }
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "immediateValueOriginal",
                desiredValue: desiredStateVariableValues.immediateValue
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.valueForDisplay = {
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        }
      }),
      definition: function({ dependencyValues }) {
        let rounded = roundForDisplay({
          value: dependencyValues.value,
          dependencyValues
        });
        return {
          setValue: { valueForDisplay: rounded }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The current matrix as a text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        return {
          setValue: {
            text: dependencyValues.valueForDisplay.toString(params)
          }
        };
      }
    };
    stateVariableDefinitions.matrix = {
      public: true,
      description: "The matrix as a math expression.",
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "matrixEntry") {
            return [];
          } else if (prefix === "row") {
            return [["matrix", "matrixRow"]];
          } else if (prefix === "column") {
            return [["matrix", "matrixColumn"]];
          } else {
            return [["matrixRow"], ["matrix"]];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["matrixEntry", "row", "column", "rows", "columns"],
      schemaSubarrays: {
        columns: {
          numDimensions: 2,
          description: "The matrix's entries grouped by column."
        },
        rows: {
          numDimensions: 2,
          description: "The matrix's entries grouped by row."
        }
      },
      returnEntryDimensions: (prefix) => {
        if (prefix === "matrixEntry") {
          return 0;
        } else if (prefix === "rows" || prefix === "columns") {
          return 2;
        } else {
          return 1;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "matrixEntry") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "row") {
          let rowInd = Number(varEnding) - 1;
          if (!(Number.isInteger(rowInd) && rowInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [rowInd + ",0"];
          }
          if (rowInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => rowInd + "," + i2
            );
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "column") {
          let colInd = Number(varEnding) - 1;
          if (!(Number.isInteger(colInd) && colInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return ["0," + colInd];
          }
          if (colInd < arraySize[1]) {
            return Array.from(
              Array(arraySize[0]),
              (_2, i2) => i2 + "," + colInd
            );
          } else {
            return [];
          }
        } else if (arrayEntryPrefix === "rows" || arrayEntryPrefix === "columns") {
          if (!arraySize) {
            return ["0,0"];
          }
          let keys2 = [];
          for (let rowInd = 0; rowInd < arraySize[0]; rowInd++) {
            keys2.push(
              ...Array.from(
                Array(arraySize[1]),
                (_2, i2) => rowInd + "," + i2
              )
            );
          }
          return keys2;
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "matrix" || varName === "rows") {
          if (propIndex.length === 1) {
            return "row" + propIndex[0];
          } else {
            return `matrixEntry${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName === "columns") {
          if (propIndex.length === 1) {
            return "column" + propIndex[0];
          } else {
            return `matrixEntry${propIndex[1]}_${propIndex[0]}`;
          }
        }
        if (varName.slice(0, 3) === "row") {
          let rowNum = Number(varName.slice(3));
          if (Number.isInteger(rowNum) && rowNum > 0) {
            return `matrixEntry${rowNum}_${propIndex[0]}`;
          }
        }
        if (varName.slice(0, 6) === "column") {
          let colNum = Number(varName.slice(6));
          if (Number.isInteger(colNum) && colNum > 0) {
            return `matrixEntry${propIndex[0]}_${colNum}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numRows, dependencyValues.numColumns];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          value: {
            dependencyType: "stateVariable",
            variableName: "value"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arraySize }) {
        let tree = globalDependencyValues.value.tree;
        let createdMatrix = false;
        let matrix2 = {};
        if (Array.isArray(tree)) {
          if (vectorAndListOperators.includes(tree[0])) {
            for (let ind = 0; ind < arraySize[0]; ind++) {
              matrix2[ind + ",0"] = me$3.fromAst(tree[ind + 1]);
            }
            createdMatrix = true;
          } else if (tree[0] === "matrix") {
            let matVals = tree[2];
            for (let i2 = 0; i2 < arraySize[0]; i2++) {
              for (let j2 = 0; j2 < arraySize[1]; j2++) {
                matrix2[`${i2},${j2}`] = me$3.fromAst(
                  matVals[i2 + 1][j2 + 1]
                );
              }
            }
            createdMatrix = true;
          } else if (vectorOperators.includes(tree[1][0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
            for (let ind = 0; ind < arraySize[1]; ind++) {
              matrix2["0," + ind] = me$3.fromAst(tree[1][ind + 1]);
            }
            createdMatrix = true;
          }
        }
        if (!createdMatrix) {
          matrix2["0,0"] = globalDependencyValues.value;
        }
        return { setValue: { matrix: matrix2 } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        stateValues,
        workspace,
        arraySize
      }) {
        if (!workspace.desiredMatrix) {
          workspace.desiredMatrix = [];
        }
        for (let i2 = 0; i2 < arraySize[0]; i2++) {
          for (let j2 = 0; j2 < arraySize[1]; j2++) {
            let arrayKey = i2 + "," + j2;
            if (desiredStateVariableValues.matrix[arrayKey] !== void 0) {
              workspace.desiredMatrix[arrayKey] = convertValueToMathExpression(
                desiredStateVariableValues.matrix[arrayKey]
              );
            } else if (workspace.desiredMatrix[arrayKey] === void 0) {
              workspace.desiredMatrix[arrayKey] = (await stateValues.matrix)[i2][j2];
            }
          }
        }
        let desiredValue;
        let tree = globalDependencyValues.value.tree;
        if (Array.isArray(tree)) {
          if (vectorAndListOperators.includes(tree[0])) {
            desiredValue = [tree[0]];
            for (let ind = 0; ind < arraySize[0]; ind++) {
              desiredValue.push(
                workspace.desiredMatrix[ind + ",0"].tree
              );
            }
          } else if (tree[0] === "matrix") {
            let desiredMatrixVals = ["tuple"];
            for (let i2 = 0; i2 < arraySize[0]; i2++) {
              let row = ["tuple"];
              for (let j2 = 0; j2 < arraySize[1]; j2++) {
                row.push(
                  workspace.desiredMatrix[`${i2},${j2}`].tree
                );
              }
              desiredMatrixVals.push(row);
            }
            desiredValue = me$3.fromAst([
              "matrix",
              tree[1],
              desiredMatrixVals
            ]);
          } else if (vectorOperators.includes(tree[1][0]) && (tree[0] === "^" && tree[2] === "T" || tree[0] === "prime")) {
            desiredValue = [tree[0]];
            let desiredVector = [tree[1][0]];
            for (let ind = 0; ind < arraySize[1]; ind++) {
              desiredVector.push(
                workspace.desiredMatrix["0," + ind].tree
              );
            }
            desiredValue = [tree[0], desiredVector];
            if (tree[2]) {
              desiredValue.push(tree[2]);
            }
            desiredValue = me$3.fromAst(desiredValue);
          }
        }
        if (!desiredValue) {
          desiredValue = workspace.desiredMatrix["0,0"];
        }
        let instructions = [
          {
            setDependency: "value",
            desiredValue
          }
        ];
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.componentType = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { componentType: "matrix" } })
    };
    stateVariableDefinitions.descriptionChildInd = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            descriptionChildInd: dependencyValues.allChildren.findLastIndex(
              (child) => child.componentType === "description"
            )
          }
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        },
        descriptionChildInd: {
          dependencyType: "stateVariable",
          variableName: "descriptionChildInd"
        }
      }),
      definition({ dependencyValues }) {
        let nChildrenToRender = dependencyValues.numRows * dependencyValues.numColumns;
        const childIndicesToRender = [
          ...Array(nChildrenToRender).keys()
        ];
        if (dependencyValues.descriptionChildInd !== -1) {
          childIndicesToRender.push(
            dependencyValues.descriptionChildInd
          );
        }
        return {
          setValue: {
            childIndicesToRender
          }
        };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.focused = {
      description: "Whether the matrix input currently has keyboard focus.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        inputChildren: {
          dependencyType: "child",
          childGroups: ["matrixComponentInputs"],
          variableNames: ["focused"]
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          focused: dependencyValues.inputChildren.some(
            (child) => child.stateValues.focused
          )
        }
      })
    };
    return stateVariableDefinitions;
  }
  async updateNumRows({
    numRows,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.disabled) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "numRows",
            value: numRows
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async updateNumColumns({
    numColumns,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.disabled) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "numColumns",
            value: numColumns
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  static adapters = [
    {
      stateVariable: "value",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
}
class MatrixInputGrid extends CompositeComponent {
  static componentType = "_matrixInputGrid";
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numRows = {
      returnDependencies: () => ({
        parentNumRows: {
          dependencyType: "parentStateVariable",
          parentComponentType: "matrixInput",
          variableName: "numRows"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { numRows: dependencyValues.parentNumRows }
        };
      }
    };
    stateVariableDefinitions.numColumns = {
      returnDependencies: () => ({
        parentNumColumns: {
          dependencyType: "parentStateVariable",
          parentComponentType: "matrixInput",
          variableName: "numColumns"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { numColumns: dependencyValues.parentNumColumns }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      markStale() {
        return { updateReplacements: true };
      },
      definition() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let serializedComponents = [];
    let numRows = await component.stateValues.numRows;
    workspace.previousNumRows = numRows;
    for (let rowInd = 0; rowInd < numRows; rowInd++) {
      serializedComponents.push({
        type: "serialized",
        componentType: "_matrixInputRow",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        state: { rowInd },
        attributes: {},
        doenetAttributes: {},
        children: []
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements: serializedComponents,
      diagnostics: [],
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    workspace,
    nComponents
  }) {
    let replacementChanges = [];
    let previousNumRows = workspace.previousNumRows;
    let numRows = await component.stateValues.numRows;
    let newReplacementsToWithhold;
    let numReplacementsToAdd = 0;
    if (numRows < previousNumRows) {
      newReplacementsToWithhold = component.replacements.length - numRows;
      let replacementInstruction = {
        changeType: "changeReplacementsToWithhold",
        replacementsToWithhold: newReplacementsToWithhold
      };
      replacementChanges.push(replacementInstruction);
    } else if (numRows > previousNumRows) {
      numReplacementsToAdd = numRows - previousNumRows;
      if (component.replacementsToWithhold > 0) {
        if (component.replacementsToWithhold >= numReplacementsToAdd) {
          newReplacementsToWithhold = component.replacementsToWithhold - numReplacementsToAdd;
          previousNumRows += numReplacementsToAdd;
          numReplacementsToAdd = 0;
          let replacementInstruction = {
            changeType: "changeReplacementsToWithhold",
            replacementsToWithhold: newReplacementsToWithhold
          };
          replacementChanges.push(replacementInstruction);
        } else {
          numReplacementsToAdd -= component.replacementsToWithhold;
          previousNumRows += component.replacementsToWithhold;
          newReplacementsToWithhold = 0;
        }
      }
    }
    if (numReplacementsToAdd > 0) {
      let newSerializedReplacements = [];
      const stateIdInfo = {
        prefix: `${component.stateId}|`,
        num: workspace.replacementsCreated
      };
      for (let rowInd = previousNumRows; rowInd < numRows; rowInd++) {
        newSerializedReplacements.push({
          type: "serialized",
          componentType: "_matrixInputRow",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          state: { rowInd },
          attributes: {},
          doenetAttributes: {},
          children: []
        });
      }
      let replacementInstruction = {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: previousNumRows,
        serializedReplacements: newSerializedReplacements,
        replacementsToWithhold: 0
      };
      replacementChanges.push(replacementInstruction);
      workspace.replacementsCreated = stateIdInfo.num;
    }
    workspace.previousNumRows = numRows;
    return { replacementChanges, nComponents };
  }
}
class MatrixInputRow extends CompositeComponent {
  static componentType = "_matrixInputRow";
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.rowInd = {
      hasEssential: true,
      defaultValue: null,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { rowInd: true }
      })
    };
    stateVariableDefinitions.numColumns = {
      returnDependencies: () => ({
        parentNumColumns: {
          dependencyType: "parentStateVariable",
          parentComponentType: "matrixInput",
          variableName: "numColumns"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { numColumns: dependencyValues.parentNumColumns }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      markStale() {
        return { updateReplacements: true };
      },
      definition() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let serializedComponents = [];
    let numColumns = await component.stateValues.numColumns;
    let rowInd = await component.stateValues.rowInd;
    workspace.previousNumColumns = numColumns;
    for (let colInd = 0; colInd < numColumns; colInd++) {
      serializedComponents.push({
        type: "serialized",
        componentType: "_matrixComponentInput",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        state: {
          rowInd,
          colInd
        },
        attributes: {},
        doenetAttributes: {},
        children: []
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements: serializedComponents,
      diagnostics: [],
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    nComponents,
    workspace
  }) {
    let replacementChanges = [];
    let previousNumColumns = workspace.previousNumColumns;
    let numColumns = await component.stateValues.numColumns;
    let rowInd = await component.stateValues.rowInd;
    let newReplacementsToWithhold;
    let numReplacementsToAdd = 0;
    if (numColumns < previousNumColumns) {
      newReplacementsToWithhold = component.replacements.length - numColumns;
      let replacementInstruction = {
        changeType: "changeReplacementsToWithhold",
        replacementsToWithhold: newReplacementsToWithhold
      };
      replacementChanges.push(replacementInstruction);
    } else if (numColumns > previousNumColumns) {
      numReplacementsToAdd = numColumns - previousNumColumns;
      if (component.replacementsToWithhold > 0) {
        if (component.replacementsToWithhold >= numReplacementsToAdd) {
          newReplacementsToWithhold = component.replacementsToWithhold - numReplacementsToAdd;
          previousNumColumns += numReplacementsToAdd;
          numReplacementsToAdd = 0;
          let replacementInstruction = {
            changeType: "changeReplacementsToWithhold",
            replacementsToWithhold: newReplacementsToWithhold
          };
          replacementChanges.push(replacementInstruction);
        } else {
          numReplacementsToAdd -= component.replacementsToWithhold;
          previousNumColumns += component.replacementsToWithhold;
          newReplacementsToWithhold = 0;
        }
      }
    }
    if (numReplacementsToAdd > 0) {
      let newSerializedReplacements = [];
      const stateIdInfo = {
        prefix: `${component.stateId}|`,
        num: workspace.replacementsCreated
      };
      for (let colInd = previousNumColumns; colInd < numColumns; colInd++) {
        newSerializedReplacements.push({
          type: "serialized",
          componentType: "_matrixComponentInput",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          state: {
            rowInd,
            colInd
          },
          attributes: {},
          doenetAttributes: {},
          children: []
        });
      }
      let replacementInstruction = {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: previousNumColumns,
        serializedReplacements: newSerializedReplacements,
        replacementsToWithhold: 0
      };
      replacementChanges.push(replacementInstruction);
      workspace.replacementsCreated = stateIdInfo.num;
    }
    workspace.previousNumColumns = numColumns;
    return { replacementChanges, nComponents };
  }
}
class MatrixComponentInput extends BaseComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateRawValue: mathComponentInputUpdateRawValue.bind(this),
      updateValue: mathComponentInputUpdateValue.bind(this),
      focusChanged: mathComponentInputFocusChanged.bind(this)
    });
  }
  static componentType = "_matrixComponentInput";
  static rendererType = "mathInput";
  static variableForImplicitProp = "value";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnMathComponentInputConfigStateVariableDefinitions({
        parentComponentType: "matrixInput"
      })
    );
    stateVariableDefinitions.rowInd = {
      hasEssential: true,
      defaultValue: null,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { rowInd: true }
      })
    };
    stateVariableDefinitions.colInd = {
      hasEssential: true,
      defaultValue: null,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { colInd: true }
      })
    };
    stateVariableDefinitions.value = {
      stateVariablesDeterminingDependencies: ["rowInd", "colInd"],
      returnDependencies: ({ stateValues }) => {
        let varEnding = "";
        if (stateValues.rowInd !== null && stateValues.colInd !== null) {
          varEnding = `${stateValues.rowInd + 1}_${stateValues.colInd + 1}`;
        }
        return {
          parentComponentValue: {
            dependencyType: "parentStateVariable",
            parentComponentType: "matrixInput",
            variableName: `componentValue${varEnding}`
          }
        };
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.parentComponentValue) {
          return {
            setValue: {
              value: dependencyValues.parentComponentValue
            }
          };
        } else {
          return { setValue: { value: me$3.fromAst("＿") } };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions = [
          {
            setDependency: "parentComponentValue",
            desiredValue: desiredStateVariableValues.value
          }
        ];
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.immediateValue = {
      stateVariablesDeterminingDependencies: ["rowInd", "colInd"],
      returnDependencies: ({ stateValues }) => {
        let varEnding = "";
        if (stateValues.rowInd !== null && stateValues.colInd !== null) {
          varEnding = `${stateValues.rowInd + 1}_${stateValues.colInd + 1}`;
        }
        return {
          parentComponentImmediateValue: {
            dependencyType: "parentStateVariable",
            parentComponentType: "matrixInput",
            variableName: `componentImmediateValue${varEnding}`
          }
        };
      },
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            immediateValue: dependencyValues.parentComponentImmediateValue
          }
        };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        initialChange
      }) {
        let instructions = [
          {
            setDependency: "parentComponentImmediateValue",
            desiredValue: desiredStateVariableValues.immediateValue,
            treatAsInitialChange: initialChange
          }
        ];
        return {
          success: true,
          instructions
        };
      }
    };
    Object.assign(
      stateVariableDefinitions,
      returnMathComponentInputDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.shortDescription = {
      forRenderer: true,
      returnDependencies: () => ({
        rowInd: {
          dependencyType: "stateVariable",
          variableName: "rowInd"
        },
        colInd: {
          dependencyType: "stateVariable",
          variableName: "colInd"
        }
      }),
      definition({ dependencyValues }) {
        const { rowInd, colInd } = dependencyValues;
        if (rowInd !== null && colInd !== null) {
          return {
            setValue: {
              shortDescription: `row ${rowInd + 1}, column ${colInd + 1}`
            }
          };
        }
        return { setValue: { shortDescription: "" } };
      }
    };
    return stateVariableDefinitions;
  }
}
const MatrixInput$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  MatrixInput,
  MatrixInputGrid,
  MatrixInputRow,
  default: MatrixComponentInput
}, Symbol.toStringTag, { value: "Module" }));
const blankMath = () => me$3.fromAst("＿");
function isFractionTree(tree) {
  return Array.isArray(tree) && tree[0] === "/";
}
function isNegatedFractionTree(tree) {
  return Array.isArray(tree) && tree[0] === "-" && isFractionTree(tree[1]);
}
function decomposeFraction(mathValue) {
  let tree = mathValue?.tree;
  if (isFractionTree(tree)) {
    return {
      numerator: me$3.fromAst(tree[1]),
      denominator: me$3.fromAst(tree[2])
    };
  }
  if (isNegatedFractionTree(tree)) {
    return {
      numerator: me$3.fromAst(["-", tree[1][1]]),
      denominator: me$3.fromAst(tree[1][2])
    };
  }
  if (tree === void 0 || tree === "＿") {
    return { numerator: blankMath(), denominator: blankMath() };
  }
  return { numerator: me$3.fromAst(tree), denominator: me$3.fromAst(1) };
}
function reconstructFraction(numerator, denominator) {
  let numeratorTree = numerator?.tree ?? "＿";
  let denominatorTree = denominator?.tree ?? "＿";
  if (numeratorTree === "＿" && denominatorTree === "＿") {
    return blankMath();
  }
  return me$3.fromAst(["/", numeratorTree, denominatorTree]);
}
function ensureFraction(mathValue) {
  let tree = mathValue?.tree;
  if (isFractionTree(tree)) {
    return mathValue;
  }
  if (isNegatedFractionTree(tree)) {
    return me$3.fromAst(["/", ["-", tree[1][1]], tree[1][2]]);
  }
  if (tree === void 0 || tree === "＿") {
    return blankMath();
  }
  return me$3.fromAst(["/", tree, 1]);
}
class FractionInput extends Input {
  constructor(args) {
    super(args);
    defineSubmitAnswerExternalAction(this);
  }
  static componentType = "fractionInput";
  static componentDocs = {
    summary: "An interactive fraction input with a numerator and a denominator, each accepting a math value."
  };
  static variableForImplicitProp = "value";
  static processWhenJustUpdatedForNewComponent = true;
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.prefillNumerator = {
      description: "Initial value displayed in the numerator input.",
      createComponentOfType: "math",
      createStateVariable: "prefillNumerator",
      defaultValue: blankMath(),
      public: true,
      copyComponentAttributesForCreatedComponent: [
        "format",
        "functionSymbols",
        "splitSymbols",
        "parseScientificNotation"
      ]
    };
    attributes.prefillDenominator = {
      description: "Initial value displayed in the denominator input.",
      createComponentOfType: "math",
      createStateVariable: "prefillDenominator",
      defaultValue: blankMath(),
      public: true,
      copyComponentAttributesForCreatedComponent: [
        "format",
        "functionSymbols",
        "splitSymbols",
        "parseScientificNotation"
      ]
    };
    Object.assign(attributes, returnMathInputParsingAttributes());
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.unionFromU = {
      description: 'Whether "U" between sets is parsed as union.',
      createComponentOfType: "boolean",
      createStateVariable: "unionFromU",
      defaultValue: false,
      public: true
    };
    attributes.minComponentWidth = {
      createComponentOfType: "integer",
      createStateVariable: "minComponentWidth",
      defaultValue: 0,
      clamp: [0, Infinity],
      description: "Minimum rendered width for the numerator and denominator, in pixels."
    };
    attributes.bindValueTo = {
      createComponentOfType: "math",
      description: "Two-way binding target for the fraction's value."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "fractionInputComponents",
        componentTypes: ["_fractionInputComponent"],
        excludeFromSchema: true
      },
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions({
        displayDigitsDefault: 10,
        displaySmallAsZeroDefault: 0
      })
    );
    const inputValueChangedStateVariableDefinitions = returnInputValueChangedStateVariableDefinitions({
      valueChangedDescription: "Whether the value has been changed from its initial state.",
      immediateValueChangedDescription: "Whether the value, including in-progress edits, has been changed from its initial state."
    });
    stateVariableDefinitions.valueChanged = inputValueChangedStateVariableDefinitions.valueChanged;
    stateVariableDefinitions.value = {
      description: "The fraction value of the input (numerator divided by denominator).",
      public: true,
      hasEssential: true,
      shadowVariable: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      set: convertValueToMathExpression,
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"],
          proceedIfAllChildrenNotMatched: true
        },
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["value"]
        },
        prefillNumerator: {
          dependencyType: "stateVariable",
          variableName: "prefillNumerator"
        },
        prefillDenominator: {
          dependencyType: "stateVariable",
          variableName: "prefillDenominator"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.mathChild.length > 0) {
          return {
            setValue: {
              value: ensureFraction(
                dependencyValues.mathChild[0].stateValues.value
              )
            }
          };
        } else if (dependencyValues.bindValueTo) {
          return {
            setValue: {
              value: ensureFraction(
                dependencyValues.bindValueTo.stateValues.value
              )
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              value: {
                defaultValue: reconstructFraction(
                  dependencyValues.prefillNumerator,
                  dependencyValues.prefillDenominator
                )
              }
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredValue = ensureFraction(
          desiredStateVariableValues.value
        );
        let instructions = [
          { setDependency: "valueChanged", desiredValue: true },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.mathChild.length > 0) {
          instructions.push({
            setDependency: "mathChild",
            desiredValue,
            variableIndex: 0,
            childIndex: 0
          });
        } else if (dependencyValues.bindValueTo) {
          instructions.push({
            setDependency: "bindValueTo",
            desiredValue,
            variableIndex: 0
          });
        } else {
          instructions.push({
            setEssentialValue: "value",
            value: desiredValue
          });
        }
        return { success: true, instructions };
      }
    };
    stateVariableDefinitions.immediateValueChanged = inputValueChangedStateVariableDefinitions.immediateValueChanged;
    stateVariableDefinitions.immediateValue = {
      description: "The fraction value reflecting the user's in-progress edits.",
      public: true,
      hasEssential: true,
      shadowVariable: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      set: convertValueToMathExpression,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition({
        dependencyValues,
        changes,
        justUpdatedForNewComponent,
        usedDefault
      }) {
        if (changes.value && !justUpdatedForNewComponent && !usedDefault.value) {
          return {
            setValue: { immediateValue: dependencyValues.value },
            setEssentialValue: {
              immediateValue: dependencyValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              immediateValue: {
                defaultValue: dependencyValues.value
              }
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        initialChange,
        shadowedVariable
      }) {
        let desiredValue = ensureFraction(
          desiredStateVariableValues.immediateValue
        );
        let instructions = [
          {
            setEssentialValue: "immediateValue",
            value: desiredValue
          },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (!(initialChange || shadowedVariable)) {
          instructions.push({
            setDependency: "value",
            desiredValue
          });
        }
        return { success: true, instructions };
      }
    };
    stateVariableDefinitions.numerator = {
      description: "The numerator of the fraction.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numerator: decomposeFraction(dependencyValues.value).numerator
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let { denominator } = decomposeFraction(dependencyValues.value);
        return {
          success: true,
          instructions: [
            {
              setDependency: "value",
              desiredValue: reconstructFraction(
                desiredStateVariableValues.numerator,
                denominator
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.denominator = {
      description: "The denominator of the fraction.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            denominator: decomposeFraction(dependencyValues.value).denominator
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let { numerator } = decomposeFraction(dependencyValues.value);
        return {
          success: true,
          instructions: [
            {
              setDependency: "value",
              desiredValue: reconstructFraction(
                numerator,
                desiredStateVariableValues.denominator
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.componentImmediateValueNumerator = {
      returnDependencies: () => ({
        immediateValue: {
          dependencyType: "stateVariable",
          variableName: "immediateValue"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            componentImmediateValueNumerator: decomposeFraction(
              dependencyValues.immediateValue
            ).numerator
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        initialChange
      }) {
        let { denominator } = decomposeFraction(
          dependencyValues.immediateValue
        );
        return {
          success: true,
          instructions: [
            {
              setDependency: "immediateValue",
              desiredValue: reconstructFraction(
                desiredStateVariableValues.componentImmediateValueNumerator,
                denominator
              ),
              treatAsInitialChange: initialChange
            }
          ]
        };
      }
    };
    stateVariableDefinitions.componentImmediateValueDenominator = {
      returnDependencies: () => ({
        immediateValue: {
          dependencyType: "stateVariable",
          variableName: "immediateValue"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            componentImmediateValueDenominator: decomposeFraction(
              dependencyValues.immediateValue
            ).denominator
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        initialChange
      }) {
        let { numerator } = decomposeFraction(
          dependencyValues.immediateValue
        );
        return {
          success: true,
          instructions: [
            {
              setDependency: "immediateValue",
              desiredValue: reconstructFraction(
                numerator,
                desiredStateVariableValues.componentImmediateValueDenominator
              ),
              treatAsInitialChange: initialChange
            }
          ]
        };
      }
    };
    stateVariableDefinitions.valueForDisplay = {
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        }
      }),
      definition: function({ dependencyValues }) {
        let rounded = roundForDisplay({
          value: dependencyValues.value,
          dependencyValues
        });
        return {
          setValue: { valueForDisplay: rounded }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The current fraction as a text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        return {
          setValue: {
            text: dependencyValues.valueForDisplay.toString(params)
          }
        };
      }
    };
    stateVariableDefinitions.componentType = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { componentType: "math" } })
    };
    stateVariableDefinitions.descriptionChildInd = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            descriptionChildInd: dependencyValues.allChildren.findLastIndex(
              (child) => child.componentType === "description"
            )
          }
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        descriptionChildInd: {
          dependencyType: "stateVariable",
          variableName: "descriptionChildInd"
        }
      }),
      definition({ dependencyValues }) {
        const childIndicesToRender = [0, 1];
        if (dependencyValues.descriptionChildInd !== -1) {
          childIndicesToRender.push(
            dependencyValues.descriptionChildInd
          );
        }
        return {
          setValue: {
            childIndicesToRender
          }
        };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.focused = {
      description: "Whether the fraction input currently has keyboard focus.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        fractionChildren: {
          dependencyType: "child",
          childGroups: ["fractionInputComponents"],
          variableNames: ["focused"]
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          focused: dependencyValues.fractionChildren.some(
            (child) => child.stateValues.focused
          )
        }
      })
    };
    stateVariableDefinitions.componentIdx = {
      returnDependencies: () => ({}),
      definition({ componentIdx }) {
        return { setValue: { componentIdx } };
      }
    };
    stateVariableDefinitions.colorInputsSeparately = {
      stateVariablesDeterminingDependencies: [
        "answerSpecifiedInForAnswer"
      ],
      returnDependencies({ stateValues }) {
        const deps = {
          answerAncestor: {
            dependencyType: "stateVariable",
            variableName: "answerAncestor"
          }
        };
        if (stateValues.answerSpecifiedInForAnswer !== null) {
          deps.forAnswerColorInputsSeparately = {
            dependencyType: "stateVariable",
            componentIdx: stateValues.answerSpecifiedInForAnswer,
            variableName: "colorInputsSeparately",
            variablesOptional: true
          };
        }
        return deps;
      },
      definition({ dependencyValues }) {
        if (dependencyValues.answerAncestor) {
          return {
            setValue: {
              colorInputsSeparately: dependencyValues.answerAncestor.stateValues.colorInputsSeparately ?? false
            }
          };
        }
        return {
          setValue: {
            colorInputsSeparately: dependencyValues.forAnswerColorInputsSeparately ?? false
          }
        };
      }
    };
    stateVariableDefinitions.creditAchievedPerInput = {
      stateVariablesDeterminingDependencies: [
        "colorInputsSeparately",
        "answerAncestor",
        "answerSpecifiedInForAnswer"
      ],
      returnDependencies({ stateValues }) {
        if (!stateValues.colorInputsSeparately) {
          return {};
        }
        if (stateValues.answerAncestor) {
          return {
            answerCreditAchievedPerInput: {
              dependencyType: "ancestor",
              componentType: "answer",
              variableNames: ["creditAchievedPerInput"]
            }
          };
        }
        if (stateValues.answerSpecifiedInForAnswer !== null) {
          return {
            answerCreditAchievedPerInput: {
              dependencyType: "stateVariable",
              componentIdx: stateValues.answerSpecifiedInForAnswer,
              variableName: "creditAchievedPerInput",
              returnAsComponentObject: true,
              variablesOptional: true
            }
          };
        }
        return {};
      },
      definition({ dependencyValues }) {
        return {
          setValue: {
            creditAchievedPerInput: dependencyValues.answerCreditAchievedPerInput?.stateValues?.creditAchievedPerInput ?? {}
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "value",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
}
class FractionComponentInput extends BaseComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateRawValue: mathComponentInputUpdateRawValue.bind(this),
      updateValue: mathComponentInputUpdateValue.bind(this),
      focusChanged: mathComponentInputFocusChanged.bind(this)
    });
  }
  static componentType = "_fractionInputComponent";
  static rendererType = "mathInput";
  static variableForImplicitProp = "value";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.part = {
      createPrimitiveOfType: "string",
      createStateVariable: "part",
      defaultValue: null
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnMathComponentInputConfigStateVariableDefinitions({
        parentComponentType: "fractionInput"
      })
    );
    stateVariableDefinitions.value = {
      stateVariablesDeterminingDependencies: ["part"],
      returnDependencies: ({ stateValues }) => ({
        parentComponentValue: {
          dependencyType: "parentStateVariable",
          parentComponentType: "fractionInput",
          variableName: stateValues.part === "denominator" ? "denominator" : "numerator"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            value: dependencyValues.parentComponentValue ?? blankMath()
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "parentComponentValue",
              desiredValue: desiredStateVariableValues.value
            }
          ]
        };
      }
    };
    stateVariableDefinitions.immediateValue = {
      stateVariablesDeterminingDependencies: ["part"],
      returnDependencies: ({ stateValues }) => ({
        parentComponentImmediateValue: {
          dependencyType: "parentStateVariable",
          parentComponentType: "fractionInput",
          variableName: stateValues.part === "denominator" ? "componentImmediateValueDenominator" : "componentImmediateValueNumerator"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            immediateValue: dependencyValues.parentComponentImmediateValue ?? blankMath()
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues, initialChange }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "parentComponentImmediateValue",
              desiredValue: desiredStateVariableValues.immediateValue,
              treatAsInitialChange: initialChange
            }
          ]
        };
      }
    };
    Object.assign(
      stateVariableDefinitions,
      returnMathComponentInputDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.shortDescription = {
      forRenderer: true,
      returnDependencies: () => ({
        part: {
          dependencyType: "stateVariable",
          variableName: "part"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            shortDescription: dependencyValues.part ?? ""
          }
        };
      }
    };
    stateVariableDefinitions.colorCorrectness = {
      forRenderer: true,
      returnDependencies: () => ({
        parentColorCorrectness: {
          dependencyType: "parentStateVariable",
          parentComponentType: "fractionInput",
          variableName: "colorCorrectness"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            colorCorrectness: dependencyValues.parentColorCorrectness ?? false
          }
        };
      }
    };
    stateVariableDefinitions.creditAchieved = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["part"],
      returnDependencies({ stateValues }) {
        const deps = {
          parentCreditAchieved: {
            dependencyType: "parentStateVariable",
            parentComponentType: "fractionInput",
            variableName: "creditAchieved"
          },
          parentColorInputsSeparately: {
            dependencyType: "parentStateVariable",
            parentComponentType: "fractionInput",
            variableName: "colorInputsSeparately"
          }
        };
        deps.parentCreditAchievedPerInput = {
          dependencyType: "parentStateVariable",
          parentComponentType: "fractionInput",
          variableName: "creditAchievedPerInput"
        };
        deps.parentComponentIdx = {
          dependencyType: "parentStateVariable",
          parentComponentType: "fractionInput",
          variableName: "componentIdx"
        };
        deps.part = {
          dependencyType: "stateVariable",
          variableName: "part"
        };
        return deps;
      },
      definition({ dependencyValues }) {
        const fallback = dependencyValues.parentCreditAchieved ?? 0;
        if (dependencyValues.parentColorInputsSeparately && dependencyValues.parentCreditAchievedPerInput && dependencyValues.parentComponentIdx !== void 0 && dependencyValues.part) {
          const key = `${dependencyValues.parentComponentIdx}/${dependencyValues.part}`;
          const perPartCredit = dependencyValues.parentCreditAchievedPerInput[key];
          return {
            setValue: {
              creditAchieved: perPartCredit !== void 0 ? perPartCredit : fallback
            }
          };
        }
        return {
          setValue: { creditAchieved: fallback }
        };
      }
    };
    stateVariableDefinitions.justSubmitted = {
      forRenderer: true,
      returnDependencies: () => ({
        parentJustSubmitted: {
          dependencyType: "parentStateVariable",
          parentComponentType: "fractionInput",
          variableName: "justSubmitted"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            justSubmitted: dependencyValues.parentJustSubmitted ?? false
          }
        };
      }
    };
    stateVariableDefinitions.numAttemptsLeft = {
      forRenderer: true,
      returnDependencies: () => ({
        parentNumAttemptsLeft: {
          dependencyType: "parentStateVariable",
          parentComponentType: "fractionInput",
          variableName: "numAttemptsLeft"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numAttemptsLeft: dependencyValues.parentNumAttemptsLeft ?? Infinity
          }
        };
      }
    };
    stateVariableDefinitions.includeValidationStateInShortDescription = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition() {
        return {
          setValue: {
            includeValidationStateInShortDescription: false
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
const FractionInput$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  FractionInput,
  default: FractionComponentInput
}, Symbol.toStringTag, { value: "Module" }));
class Solution extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      revealSolution: this.revealSolution.bind(this),
      closeSolution: this.closeSolution.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "solution";
  static componentDocs = {
    summary: "A solution to an exercise, shown on demand"
  };
  static rendererType = "solution";
  static renderChildren = true;
  static sendToRendererEvenIfHidden = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.hide = {
      createComponentOfType: "boolean",
      description: "Whether the solution is initially hidden."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "inlinesBlocks",
        componentTypes: ["_inline", "_block"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.hide = {
      description: "Whether the solution is currently hidden.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: false,
      hasEssential: true,
      returnDependencies: () => ({
        hideAttr: {
          dependencyType: "attributeComponent",
          attributeName: "hide",
          variableNames: ["value"]
        },
        displayMode: {
          dependencyType: "flag",
          flagName: "solutionDisplayMode"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.displayMode === "none") {
          return { setValue: { hide: true } };
        } else if (dependencyValues.hideAttr !== null) {
          return {
            setValue: {
              hide: dependencyValues.hideAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              hide: true
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.displayMode === "none") {
          return { success: false };
        } else if (dependencyValues.hideAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "hideAttr",
                desiredValue: desiredStateVariableValues.hide,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setSetVariable: "hide",
                value: desiredStateVariableValues.hide
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.open = {
      description: "Whether the solution is currently open (revealed).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: false,
      hasEssential: true,
      returnDependencies: () => ({
        displayMode: {
          dependencyType: "flag",
          flagName: "solutionDisplayMode"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.displayMode === "displayed") {
          return { setValue: { open: true } };
        } else if (dependencyValues.displayMode === "none") {
          return { setValue: { open: false } };
        } else {
          return {
            useEssentialOrDefaultValue: {
              open: true
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.displayMode === "displayed" || dependencyValues.displayMode === "none") {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "open",
              value: desiredStateVariableValues.open
            }
          ]
        };
      }
    };
    stateVariableDefinitions.rendered = {
      forRenderer: true,
      defaultValue: false,
      hasEssential: true,
      returnDependencies: () => ({
        displayMode: {
          dependencyType: "flag",
          flagName: "solutionDisplayMode"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.displayMode === "displayed") {
          return { setValue: { rendered: true } };
        } else if (dependencyValues.displayMode === "none") {
          return { setValue: { rendered: false } };
        } else {
          return {
            useEssentialOrDefaultValue: {
              rendered: true
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.displayMode === "displayed" || dependencyValues.displayMode === "none") {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "rendered",
              value: desiredStateVariableValues.rendered
            }
          ]
        };
      }
    };
    stateVariableDefinitions.canBeClosed = {
      forRenderer: true,
      returnDependencies: () => ({
        displayMode: {
          dependencyType: "flag",
          flagName: "solutionDisplayMode"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.displayMode === "button" || dependencyValues.displayMode === "buttonRequirePermission") {
          return { setValue: { canBeClosed: true } };
        } else {
          return { setValue: { canBeClosed: false } };
        }
      }
    };
    stateVariableDefinitions.sectionName = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { sectionName: "Solution" } })
    };
    return stateVariableDefinitions;
  }
  async revealSolution({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    const displayMode = this.flags.solutionDisplayMode;
    let allowView = true;
    if (displayMode === "buttonRequirePermission") {
      const requestResult = await this.coreFunctions.requestSolutionView(
        this.componentIdx
      );
      allowView = requestResult.allowView;
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "open",
        value: allowView
      }
    ];
    let event;
    if (allowView) {
      event = {
        verb: "viewed",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        }
      };
    }
    await this.coreFunctions.performUpdate({
      updateInstructions,
      sourceInformation,
      skipRendererUpdate,
      event,
      overrideReadOnly: true
    });
    return this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "rendered",
          value: allowView
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
  async closeSolution({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "open",
          value: false
        }
      ],
      overrideReadOnly: true,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "closed",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        }
      }
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
  static includeBlankStringChildren = true;
}
class GivenAnswer extends Solution {
  static componentType = "givenAnswer";
  static componentDocs = {
    summary: 'A sectional component that renders an expandable block labeled "Answer"'
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.sectionName.definition = () => ({
      setValue: { sectionName: "Answer" }
    });
    return stateVariableDefinitions;
  }
}
const Solution$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  GivenAnswer,
  Solution
}, Symbol.toStringTag, { value: "Module" }));
class Document extends BaseComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAllAnswers: this.submitAllAnswers.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "document";
  static componentDocs = {
    summary: "The top-level container for a DoenetML document (added implicitly if not present)"
  };
  static rendererType = "section";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static createsVariants = true;
  static includeBlankStringChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.hide;
    delete attributes.disabled;
    delete attributes.modifyIndirectly;
    delete attributes.fixed;
    delete attributes.styleNumber;
    delete attributes.isResponse;
    delete attributes.isPotentialResponse;
    let scoredAttributes = returnScoredSectionAttributes();
    delete scoredAttributes.aggregateScores;
    delete scoredAttributes.weight;
    Object.assign(attributes, scoredAttributes);
    attributes.xmlns = {
      createPrimitiveOfType: "string",
      description: "XML namespace declaration (accepted for compatibility; not used)."
    };
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "Document type identifier."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "variantControl",
        componentTypes: ["variantControl"]
      },
      {
        group: "title",
        componentTypes: ["title"]
      },
      {
        group: "setups",
        componentTypes: ["setup"]
      },
      {
        group: "styleDefinitions",
        componentTypes: ["styleDefinition"]
      },
      {
        group: "feedbackDefinitions",
        componentTypes: ["feedbackDefinition"]
      },
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let styleDefinitionStateVariables = returnStyleDefinitionStateVariables();
    Object.assign(stateVariableDefinitions, styleDefinitionStateVariables);
    let feedbackDefinitionStateVariables = returnFeedbackDefinitionStateVariables();
    Object.assign(
      stateVariableDefinitions,
      feedbackDefinitionStateVariables
    );
    Object.assign(
      stateVariableDefinitions,
      returnScoredSectionStateVariableDefinition()
    );
    delete stateVariableDefinitions.aggregateScores;
    stateVariableDefinitions.titleChildName = {
      forRenderer: true,
      returnDependencies: () => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["title"]
        }
      }),
      definition({ dependencyValues }) {
        let titleChildName = null;
        if (dependencyValues.titleChild.length > 0) {
          titleChildName = dependencyValues.titleChild[0].componentIdx;
        }
        return {
          setValue: { titleChildName }
        };
      }
    };
    stateVariableDefinitions.title = {
      description: "The document's title.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["title"],
          variableNames: ["text"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.titleChild.length === 0) {
          return { setValue: { title: "" } };
        } else {
          return {
            setValue: {
              title: dependencyValues.titleChild[0].stateValues.text
            }
          };
        }
      }
    };
    stateVariableDefinitions.theme = {
      hasEssential: true,
      defaultValue: "light",
      returnDependencies: () => ({
        documentAncestor: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.documentAncestor) {
          return {
            setValue: {
              theme: dependencyValues.documentAncestor.stateValues.theme
            }
          };
        } else {
          return { useEssentialOrDefaultValue: { theme: true } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.documentAncestor) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "documentAncestor",
                desiredValue: desiredStateVariableValues.theme
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "theme",
                value: desiredStateVariableValues.theme
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.level = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { level: 0 } })
    };
    stateVariableDefinitions.numScoredDescendants = {
      returnDependencies: () => ({
        scoredDescendants: {
          dependencyType: "stateVariable",
          variableName: "scoredDescendants"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numScoredDescendants: dependencyValues.scoredDescendants.length
          }
        };
      }
    };
    stateVariableDefinitions.componentCreditAchieved = {
      isArray: true,
      returnArraySizeDependencies: () => ({
        numScoredDescendants: {
          dependencyType: "stateVariable",
          variableName: "numScoredDescendants"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numScoredDescendants];
      },
      stateVariablesDeterminingDependencies: ["scoredDescendants"],
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let descendant = stateValues.scoredDescendants[arrayKey];
          if (descendant) {
            dependenciesByKey[arrayKey] = {
              creditAchieved: {
                dependencyType: "stateVariable",
                componentIdx: descendant.componentIdx,
                variableName: "creditAchieved"
              }
            };
          }
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let componentCreditAchieved = {};
        for (let arrayKey of arrayKeys) {
          componentCreditAchieved[arrayKey] = dependencyValuesByKey[arrayKey].creditAchieved;
        }
        return { setValue: { componentCreditAchieved } };
      }
    };
    stateVariableDefinitions.componentNumberByAnswerName = {
      stateVariablesDeterminingDependencies: ["scoredDescendants"],
      returnDependencies({ stateValues }) {
        let dependencies = {
          scoredDescendants: {
            dependencyType: "stateVariable",
            variableName: "scoredDescendants"
          }
        };
        for (let ind in stateValues.scoredDescendants) {
          let descendant = stateValues.scoredDescendants[ind];
          dependencies[`descendantsOf${ind}`] = {
            dependencyType: "descendant",
            ancestorIdx: descendant.componentIdx,
            componentTypes: ["answer", "_blockScoredComponent"],
            recurseToMatchedChildren: false
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        let componentNumberByAnswerName = {};
        for (let [
          ind,
          component
        ] of dependencyValues.scoredDescendants.entries()) {
          let componentNumber = ind + 1;
          for (let answerDescendant of dependencyValues[`descendantsOf${ind}`]) {
            componentNumberByAnswerName[answerDescendant.componentIdx] = componentNumber;
          }
          if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: component.componentType,
            baseComponentType: "answer"
          }) || componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: component.componentType,
            baseComponentType: "_blockScoredComponent"
          })) {
            componentNumberByAnswerName[component.componentIdx] = componentNumber;
          }
        }
        return { setValue: { componentNumberByAnswerName } };
      }
    };
    stateVariableDefinitions.docVariantInfo = {
      isArray: true,
      returnArraySizeDependencies: () => ({
        numScoredDescendants: {
          dependencyType: "stateVariable",
          variableName: "numScoredDescendants"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numScoredDescendants];
      },
      stateVariablesDeterminingDependencies: ["scoredDescendants"],
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let descendant = stateValues.scoredDescendants[arrayKey];
          if (descendant) {
            dependenciesByKey[arrayKey] = {
              generatedVariantInfo: {
                dependencyType: "stateVariable",
                componentIdx: descendant.componentIdx,
                variableName: "generatedVariantInfo",
                variablesOptional: true
              }
            };
          }
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let docVariantInfo = {};
        for (let arrayKey of arrayKeys) {
          docVariantInfo[arrayKey] = dependencyValuesByKey[arrayKey].generatedVariantInfo;
        }
        return { setValue: { docVariantInfo } };
      }
    };
    stateVariableDefinitions.creditAchieved = {
      description: "Aggregate credit achieved (0 to 1) for scored content in the document.",
      public: true,
      forRenderer: true,
      defaultValue: 0,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: {
          displayDigits: {
            stateVariableToShadow: "displayDigitsForCreditAchieved"
          },
          displayDecimals: {
            stateVariableToShadow: "displayDecimalsForCreditAchieved"
          }
        }
      },
      additionalStateVariablesDefined: [
        {
          variableName: "percentCreditAchieved",
          public: true,
          description: "Aggregate credit achieved as a percentage (0 to 100) for scored content in the document.",
          shadowingInstructions: {
            createComponentOfType: "number",
            addAttributeComponentsShadowingStateVariables: {
              displayDigits: {
                stateVariableToShadow: "displayDigitsForCreditAchieved"
              },
              displayDecimals: {
                stateVariableToShadow: "displayDecimalsForCreditAchieved"
              }
            }
          }
        }
      ],
      returnDependencies: () => ({
        scoredDescendants: {
          dependencyType: "stateVariable",
          variableName: "scoredDescendants"
        },
        componentCreditAchieved: {
          dependencyType: "stateVariable",
          variableName: "componentCreditAchieved"
        }
      }),
      definition({ dependencyValues }) {
        let creditSum = 0;
        let totalWeight = 0;
        for (let [
          ind,
          component
        ] of dependencyValues.scoredDescendants.entries()) {
          let weight = component.stateValues.weight;
          creditSum += dependencyValues.componentCreditAchieved[ind] * weight;
          totalWeight += weight;
        }
        let creditAchieved;
        if (totalWeight > 0) {
          creditAchieved = creditSum / totalWeight;
        } else {
          creditAchieved = 1;
        }
        let percentCreditAchieved = creditAchieved * 100;
        return { setValue: { creditAchieved, percentCreditAchieved } };
      }
    };
    stateVariableDefinitions.creditAchievedIfSubmit = {
      defaultValue: 0,
      stateVariablesDeterminingDependencies: ["scoredDescendants"],
      returnDependencies({ stateValues }) {
        let dependencies = {
          scoredDescendants: {
            dependencyType: "stateVariable",
            variableName: "scoredDescendants"
          }
        };
        for (let [
          ind,
          descendant
        ] of stateValues.scoredDescendants.entries()) {
          dependencies["creditAchievedIfSubmit" + ind] = {
            dependencyType: "stateVariable",
            componentIdx: descendant.componentIdx,
            variableName: "creditAchievedIfSubmit"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let creditSum = 0;
        let totalWeight = 0;
        for (let [
          ind,
          component
        ] of dependencyValues.scoredDescendants.entries()) {
          let weight = component.stateValues.weight;
          creditSum += dependencyValues["creditAchievedIfSubmit" + ind] * weight;
          totalWeight += weight;
        }
        let creditAchievedIfSubmit = creditSum / totalWeight;
        return { setValue: { creditAchievedIfSubmit } };
      }
    };
    stateVariableDefinitions.generatedVariantInfo = {
      providePreviousValuesInDefinition: true,
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantIndex: {
          dependencyType: "value",
          value: sharedParameters.variantIndex
        },
        variantName: {
          dependencyType: "value",
          value: sharedParameters.variantName
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx, previousValues }) {
        let generatedVariantInfo = {
          index: dependencyValues.variantIndex,
          name: dependencyValues.variantName,
          meta: {
            createdBy: componentIdx
          }
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        for (let [ind, subvar] of subvariants.entries()) {
          if (!subvar.subvariants && previousValues.generatedVariantInfo) {
            let previousSubvariants = previousValues.generatedVariantInfo.subvariants;
            if (previousSubvariants[ind]?.subvariants) {
              subvariants[ind] = Object.assign(
                {},
                subvariants[ind]
              );
              subvariants[ind].subvariants = previousSubvariants[ind].subvariants;
            }
          }
        }
        return { setValue: { generatedVariantInfo } };
      }
    };
    stateVariableDefinitions.containerTag = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { containerTag: "div" } })
    };
    return stateVariableDefinitions;
  }
  async submitAllAnswers({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return submitAllAnswers({
      component: this,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents,
    core: core2
  }) {
    let numVariants = serializedComponent.variants.numVariants;
    let variantIndex;
    let desiredVariant = serializedComponent.variants.desiredVariant;
    if (desiredVariant !== void 0) {
      if (desiredVariant.index !== void 0) {
        let desiredVariantIndex = Number(desiredVariant.index);
        if (!Number.isFinite(desiredVariantIndex)) {
          core2.addDiagnostic({
            type: "info",
            message: "Variant index " + desiredVariant.index + " must be a number",
            position: serializedComponent.position,
            sourceDoc: serializedComponent.sourceDoc
          });
          variantIndex = 1;
        } else {
          if (!Number.isInteger(desiredVariantIndex)) {
            core2.addDiagnostic({
              type: "info",
              message: "Variant index " + desiredVariant.index + " must be an integer",
              position: serializedComponent.position,
              sourceDoc: serializedComponent.sourceDoc
            });
            desiredVariantIndex = Math.round(desiredVariantIndex);
          }
          let indexFrom0 = (desiredVariantIndex - 1) % numVariants;
          if (indexFrom0 < 0) {
            indexFrom0 += numVariants;
          }
          variantIndex = indexFrom0 + 1;
        }
      }
    }
    if (variantIndex === void 0) {
      variantIndex = 1;
    }
    sharedParameters.allPossibleVariants = serializedComponent.variants.allPossibleVariants;
    sharedParameters.allVariantNames = serializedComponent.variants.allVariantNames;
    sharedParameters.variantSeed = serializedComponent.variants.allPossibleVariantSeeds[variantIndex - 1];
    sharedParameters.variantIndex = variantIndex;
    sharedParameters.variantName = serializedComponent.variants.allPossibleVariants[variantIndex - 1];
    sharedParameters.uniqueIndex = serializedComponent.variants.allPossibleVariantUniqueIndices[variantIndex - 1];
    sharedParameters.variantRng = sharedParameters.rngClass(
      sharedParameters.variantSeed
    );
    sharedParameters.subpartVariantRng = sharedParameters.rngClass(
      sharedParameters.variantSeed + "s"
    );
    if (desiredVariant?.subvariants && descendantVariantComponents) {
      for (let ind in desiredVariant.subvariants) {
        let subvariant = desiredVariant.subvariants[ind];
        let variantComponent = descendantVariantComponents[ind];
        if (variantComponent === void 0) {
          break;
        }
        variantComponent.variants.desiredVariant = subvariant;
      }
    }
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    return determineVariantsForSection({
      serializedComponent,
      componentInfoObjects,
      isDocument: true,
      infoDiagnostics
    });
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let originalVariantIndex = serializedComponent.variants.allPossibleVariantUniqueIndices[variantIndex - 1];
    if (originalVariantIndex === void 0) {
      return { success: false };
    }
    let result = getVariantsForDescendantsForUniqueVariants({
      variantIndex: originalVariantIndex,
      serializedComponent,
      componentInfoObjects
    });
    if (!result.success) {
      return { success: false };
    }
    return {
      success: true,
      desiredVariant: {
        index: variantIndex,
        subvariants: result.desiredVariants
      }
    };
  }
}
let TextList$1 = class TextList extends CompositeComponent {
  static componentType = "textList";
  static componentDocs = {
    summary: "A list of texts"
  };
  static takesIndex = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static allowInSchemaAsComponent = ["text"];
  // when another component has a attribute that is a textList,
  // use the texts state variable to populate that attribute
  static stateVariableToBeShadowed = "texts";
  static primaryStateVariableForDefinition = "textsShadow";
  // even if inside a component that turned on descendantCompositesMustHaveAReplacement
  // don't required composite replacements
  static descendantCompositesMustHaveAReplacement = false;
  static doNotExpandAsShadowed = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = {
      description: "Whether the order of items should be treated as unordered.",
      createComponentOfType: "boolean",
      createStateVariable: "unordered",
      defaultValue: false,
      public: true
    };
    attributes.maxNumber = {
      description: "Maximum number of items to retain in the list.",
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: Infinity,
      public: true
    };
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this component is treated as a response for the purposes of assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let groupIntoTextsSeparatedBySpaces = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
      componentType: "text"
    });
    sugarInstructions.push({
      replacementFunction: function({
        matchedChildren,
        nComponents,
        stateIdInfo
      }) {
        return groupIntoTextsSeparatedBySpaces({
          matchedChildren,
          nComponents,
          stateIdInfo
        });
      }
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "texts",
        componentTypes: ["text"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.textsShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          textsShadow: true
        }
      })
    };
    stateVariableDefinitions.numComponents = {
      description: "The number of items in the text list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      additionalStateVariablesDefined: ["childNameByComponent"],
      returnDependencies() {
        return {
          maxNumber: {
            dependencyType: "stateVariable",
            variableName: "maxNumber"
          },
          textChildren: {
            dependencyType: "child",
            childGroups: ["texts"]
          },
          textsShadow: {
            dependencyType: "stateVariable",
            variableName: "textsShadow"
          }
        };
      },
      definition: function({ dependencyValues }) {
        let numComponents = 0;
        let childNameByComponent = [];
        if (dependencyValues.textChildren.length > 0) {
          childNameByComponent = dependencyValues.textChildren.map(
            (x2) => x2.componentIdx
          );
          numComponents = dependencyValues.textChildren.length;
        } else if (dependencyValues.textsShadow !== null) {
          numComponents = dependencyValues.textsShadow.length;
        }
        let maxNum = dependencyValues.maxNumber;
        if (numComponents > maxNum) {
          numComponents = maxNum;
          childNameByComponent = childNameByComponent.slice(
            0,
            maxNum
          );
        }
        return {
          setValue: { numComponents, childNameByComponent },
          checkForActualChange: { numComponents: true }
        };
      }
    };
    stateVariableDefinitions.texts = {
      isArray: true,
      entryPrefixes: ["text"],
      stateVariablesDeterminingDependencies: ["childNameByComponent"],
      returnArraySizeDependencies: () => ({
        numComponents: {
          dependencyType: "stateVariable",
          variableName: "numComponents"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numComponents];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          childNameByComponent: {
            dependencyType: "stateVariable",
            variableName: "childNameByComponent"
          },
          textsShadow: {
            dependencyType: "stateVariable",
            variableName: "textsShadow"
          }
        };
        for (let arrayKey of arrayKeys) {
          let childIndices = [];
          if (stateValues.childNameByComponent[arrayKey]) {
            childIndices = [arrayKey];
          }
          dependenciesByKey[arrayKey] = {
            textChildren: {
              dependencyType: "child",
              childGroups: ["texts"],
              variableNames: ["value"],
              childIndices
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let texts = {};
        for (let arrayKey of arrayKeys) {
          let child = dependencyValuesByKey[arrayKey].textChildren[0];
          if (child) {
            texts[arrayKey] = child.stateValues.value;
          } else if (globalDependencyValues.textsShadow !== null) {
            texts[arrayKey] = globalDependencyValues.textsShadow[arrayKey];
          }
        }
        return { setValue: { texts } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        workspace
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.texts) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let child = dependencyValuesByKey[arrayKey].textChildren[0];
          if (child) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].textChildren,
              desiredValue: desiredStateVariableValues.texts[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else if (globalDependencyValues.textsShadow !== null) {
            if (!workspace.desiredTextsShadow) {
              workspace.desiredTextsShadow = [
                ...globalDependencyValues.textsShadow
              ];
            }
            workspace.desiredTextsShadow[arrayKey] = desiredStateVariableValues.texts[arrayKey];
          }
        }
        if (workspace.desiredTextsShadow) {
          instructions.push({
            setDependency: "textsShadow",
            desiredValue: workspace.desiredTextsShadow
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numComponents",
      description: "The number of text values in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "texts",
      description: "The list's text values."
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        childNameByComponent: {
          dependencyType: "stateVariable",
          variableName: "childNameByComponent"
        }
      }),
      // When this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must set
      // stateVariableToEvaluateAfterReplacements
      // to this variable so that it is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let attributesToConvert = {};
    for (let attr of ["fixed", "isResponse", "isPotentialResponse"]) {
      if (attr in component.attributes) {
        attributesToConvert[attr] = component.attributes[attr];
      }
    }
    let childNameByComponent = await component.stateValues.childNameByComponent;
    let numComponents = await component.stateValues.numComponents;
    for (let i2 = 0; i2 < numComponents; i2++) {
      let attributesFromComposite = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "text",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributesFromComposite = res.attributes;
        nComponents = res.nComponents;
      }
      let childIdx = childNameByComponent[i2];
      let replacementSource = components[childIdx];
      if (replacementSource) {
        componentsCopied.push(replacementSource.componentIdx);
      }
      replacements.push({
        type: "serialized",
        componentType: "text",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: JSON.parse(JSON.stringify(attributesFromComposite)),
        doenetAttributes: {},
        children: [],
        state: {},
        downstreamDependencies: {
          [component.componentIdx]: [
            {
              dependencyType: "referenceShadow",
              compositeIdx: component.componentIdx,
              propVariable: `text${i2 + 1}`
            }
          ]
        }
      });
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    workspace.numComponents = numComponents;
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let numComponents = await component.stateValues.numComponents;
    if (numComponents === workspace.numComponents) {
      let componentsToCopy = [];
      let childNameByComponent = await component.stateValues.childNameByComponent;
      for (let childIdx of childNameByComponent) {
        let replacementSource = components[childIdx];
        if (replacementSource) {
          componentsToCopy.push(replacementSource.componentIdx);
        }
      }
      if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
        (x2, i2) => x2 === componentsToCopy[i2]
      )) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
};
class TextList2 extends InlineComponent {
  static componentType = "randomizedTextList";
  static componentDocs = {
    summary: "A list of texts in randomized order."
  };
  static excludeFromSchema = true;
  // when another component has a attribute that is a textList,
  // use the texts state variable to populate that attribute
  static stateVariableToBeShadowed = "texts";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = { default: false };
    attributes.maxNumber = { default: null };
    return attributes;
  }
  static returnChildLogic(args) {
    let childLogic = super.returnChildLogic(args);
    let atLeastZeroTexts = childLogic.newLeaf({
      name: "atLeastZeroTexts",
      componentType: "text",
      comparison: "atLeast",
      number: 0
    });
    let atLeastZeroTextLists = childLogic.newLeaf({
      name: "atLeastZeroTextLists",
      componentType: "textList",
      comparison: "atLeast",
      number: 0
    });
    let breakStringIntoTextsByCommas = function({ dependencyValues }) {
      let stringChild = dependencyValues.stringChildren[0];
      let newChildren = stringChild.stateValues.value.split(",").map((x2) => ({
        componentType: "text",
        state: { value: x2.trim() }
      }));
      return {
        success: true,
        newChildren,
        toDelete: [stringChild.componentIdx]
      };
    };
    let exactlyOneString = childLogic.newLeaf({
      name: "exactlyOneString",
      componentType: "string",
      number: 1,
      isSugar: true,
      returnSugarDependencies: () => ({
        stringChildren: {
          dependencyType: "child",
          childLogicName: "exactlyOneString",
          variableNames: ["value"]
        }
      }),
      logicToWaitOnSugar: ["atLeastZeroTexts"],
      replacementFunction: breakStringIntoTextsByCommas
    });
    let textAndTextLists = childLogic.newOperator({
      name: "textAndTextLists",
      operator: "and",
      propositions: [atLeastZeroTexts, atLeastZeroTextLists]
    });
    childLogic.newOperator({
      name: "TextsXorSugar",
      operator: "xor",
      propositions: [exactlyOneString, textAndTextLists],
      setAsBase: true
    });
    return childLogic;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.texts = {
      description: "The list of texts in randomized order.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      isArray: true,
      entryPrefixes: ["text"],
      returnDependencies: () => ({
        textAndTextListChildren: {
          dependencyType: "child",
          childLogicName: "textAndTextLists",
          variableNames: ["value", "texts"],
          variablesOptional: true
        },
        maxNumber: {
          dependencyType: "stateVariable",
          variableName: "maxNumber"
        },
        childrenToRender: {
          dependencyType: "stateVariable",
          variableName: "childrenToRender"
        }
      }),
      definition: function({ dependencyValues }) {
        let texts = [];
        let childNames = dependencyValues.textAndTextListChildren.map(
          (x2) => x2.componentIdx
        );
        for (let childIdx of dependencyValues.childrenToRender) {
          let index2 = childNames.indexOf(childIdx);
          let child = dependencyValues.textAndTextListChildren[index2];
          if (child.stateValues.texts) {
            texts.push(...child.stateValues.texts);
          } else {
            texts.push(child.stateValues.value);
          }
        }
        return { setValue: { texts } };
      }
    };
    stateVariableDefinitions.text = {
      description: "The randomized texts joined as a single text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        texts: {
          dependencyType: "stateVariable",
          variableName: "texts"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { text: dependencyValues.texts.join(", ") }
      })
    };
    stateVariableDefinitions.numComponents = {
      description: "The number of texts in the list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        texts: {
          dependencyType: "stateVariable",
          variableName: "texts"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: { numComponents: dependencyValues.texts.length }
        };
      }
    };
    stateVariableDefinitions.childrenToRender = {
      returnDependencies: ({ sharedParameters }) => ({
        textAndTextListChildren: {
          dependencyType: "child",
          childLogicName: "textAndTextLists",
          variableNames: ["childrenToRender"],
          variablesOptional: true
        },
        maxNumber: {
          dependencyType: "stateVariable",
          variableName: "maxNumber"
        },
        selectRng: {
          dependencyType: "value",
          value: sharedParameters.selectRng,
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        let childrenToRender = [];
        for (let child of dependencyValues.textAndTextListChildren) {
          if (child.stateValues.childrenToRender) {
            childrenToRender.push(
              ...child.stateValues.childrenToRender
            );
          } else {
            childrenToRender.push(child.componentIdx);
          }
        }
        let maxNum = dependencyValues.maxNumber;
        if (maxNum !== null && childrenToRender.length > maxNum) {
          maxNum = Math.max(0, Math.floor(maxNum));
          childrenToRender = childrenToRender.slice(0, maxNum);
        }
        console.log(`randomizing children`);
        for (let i2 = childrenToRender.length - 1; i2 > 0; i2--) {
          const rand = dependencyValues.selectRng();
          const j2 = Math.floor(rand * (i2 + 1));
          [childrenToRender[i2], childrenToRender[j2]] = [
            childrenToRender[j2],
            childrenToRender[i2]
          ];
        }
        let numChildren = Math.ceil(
          dependencyValues.selectRng() * childrenToRender.length
        );
        console.log(`numChildren: ${numChildren}`);
        childrenToRender = childrenToRender.slice(0, numChildren);
        return { setValue: { childrenToRender } };
      }
    };
    return stateVariableDefinitions;
  }
  initializeRenderer() {
    if (this.renderer === void 0) {
      this.renderer = new this.availableRenderers.aslist({
        key: this.componentIdx
      });
    }
  }
}
class TupleList extends MathList {
  static componentType = "tupleList";
  static componentDocs = {
    summary: "A list of math tuples, parsed from parenthesized comma-separated pieces"
  };
  static includeBlankStringChildren = false;
  static returnSugarInstructions() {
    let sugarInstructions = [];
    let createTupleList = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let results = breakEmbeddedStringsIntoParensPieces({
        componentList: matchedChildren
      });
      if (results.success !== true) {
        return { success: false };
      }
      return {
        success: true,
        newChildren: results.pieces.map(function(piece) {
          if (piece.length > 1 || typeof piece[0] === "string") {
            return {
              type: "serialized",
              componentType: "math",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: piece,
              attributes: {},
              doenetAttributes: {},
              state: {}
            };
          } else {
            return piece[0];
          }
        }),
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: createTupleList
    });
    return sugarInstructions;
  }
}
class NumberList extends CompositeComponent {
  static componentType = "numberList";
  static componentDocs = {
    summary: "A list of numbers"
  };
  static takesIndex = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static allowInSchemaAsComponent = ["number"];
  // when another component has an attribute that is a numberList,
  // use the numbers state variable to populate that attribute
  static stateVariableToBeShadowed = "numbers";
  static primaryStateVariableForDefinition = "numbersShadow";
  // even if inside a component that turned on descendantCompositesMustHaveAReplacement
  // don't required composite replacements
  static descendantCompositesMustHaveAReplacement = false;
  static doNotExpandAsShadowed = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedPrelim",
      defaultValue: false,
      description: "Whether the order of items in this list should be treated as unordered (e.g. for matching)."
    };
    attributes.maxNumber = {
      description: "Maximum number of items to retain in the list.",
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: Infinity,
      public: true
    };
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this component is treated as a response for the purposes of assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    const numberDisplayAttrs = returnNumberDisplayAttributes();
    for (let attrName in numberDisplayAttrs) {
      attributes[attrName] = {
        leaveRaw: true,
        description: numberDisplayAttrs[attrName].description
      };
    }
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let groupIntoNumbersSeparatedBySpaces = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
      componentType: "number"
    });
    sugarInstructions.push({
      replacementFunction: function({
        matchedChildren,
        nComponents,
        stateIdInfo
      }) {
        return groupIntoNumbersSeparatedBySpaces({
          matchedChildren,
          nComponents,
          stateIdInfo
        });
      }
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "numbers",
        componentTypes: ["number"]
      },
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnUnorderedListStateVariableDefinitions()
    );
    stateVariableDefinitions.numbersShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          numbersShadow: true
        }
      })
    };
    stateVariableDefinitions.mergeMathLists = {
      returnDependencies: () => ({
        mergeMathListsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "mergeMathLists",
          variableNames: ["value"]
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          skipComponentIndices: true
        },
        numberChildren: {
          dependencyType: "child",
          childGroups: ["numbers"],
          skipComponentIndices: true
        }
      }),
      definition({ dependencyValues }) {
        let mergeMathLists = dependencyValues.mathChildren.length === 1 && dependencyValues.numberChildren.length === 0;
        return { setValue: { mergeMathLists } };
      }
    };
    stateVariableDefinitions.numComponents = {
      description: "The number of items in the number list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      stateVariablesDeterminingDependencies: ["mergeMathLists"],
      additionalStateVariablesDefined: ["childInfoByComponent"],
      returnDependencies({ stateValues }) {
        let dependencies = {
          maxNumber: {
            dependencyType: "stateVariable",
            variableName: "maxNumber"
          },
          mergeMathLists: {
            dependencyType: "stateVariable",
            variableName: "mergeMathLists"
          },
          numbersShadow: {
            dependencyType: "stateVariable",
            variableName: "numbersShadow"
          }
        };
        if (stateValues.mergeMathLists) {
          dependencies.numberMathChildren = {
            dependencyType: "child",
            childGroups: ["numbers", "maths"],
            variableNames: ["value"]
          };
        } else {
          dependencies.numberMathChildren = {
            dependencyType: "child",
            childGroups: ["numbers", "maths"]
          };
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numComponents = 0;
        let childInfoByComponent = [];
        if (dependencyValues.numberMathChildren.length > 0) {
          if (dependencyValues.mergeMathLists) {
            for (let [
              childInd,
              child
            ] of dependencyValues.numberMathChildren.entries()) {
              let childValue = child.stateValues.value;
              if (Array.isArray(childValue.tree) && childValue.tree[0] === "list") {
                let nComponents = childValue.tree.length - 1;
                for (let i2 = 0; i2 < nComponents; i2++) {
                  childInfoByComponent[i2 + numComponents] = {
                    childInd,
                    component: i2,
                    nComponents,
                    childIdx: child.componentIdx
                  };
                }
                numComponents += nComponents;
              } else {
                childInfoByComponent[numComponents] = {
                  childInd,
                  childIdx: child.componentIdx
                };
                numComponents += 1;
              }
            }
          } else {
            numComponents = dependencyValues.numberMathChildren.length;
            childInfoByComponent = dependencyValues.numberMathChildren.map(
              (child, i2) => ({
                childInd: i2,
                childIdx: child.componentIdx
              })
            );
          }
        } else if (dependencyValues.numbersShadow !== null) {
          numComponents = dependencyValues.numbersShadow.length;
        }
        let maxNum = dependencyValues.maxNumber;
        if (numComponents > maxNum) {
          numComponents = maxNum;
          childInfoByComponent = childInfoByComponent.slice(
            0,
            maxNum
          );
        }
        return {
          setValue: { numComponents, childInfoByComponent },
          checkForActualChange: { numComponents: true }
        };
      }
    };
    stateVariableDefinitions.numbers = {
      isArray: true,
      entryPrefixes: ["number"],
      stateVariablesDeterminingDependencies: [
        "mergeMathLists",
        "childInfoByComponent"
      ],
      returnArraySizeDependencies: () => ({
        numComponents: {
          dependencyType: "stateVariable",
          variableName: "numComponents"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numComponents];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          mergeMathLists: {
            dependencyType: "stateVariable",
            variableName: "mergeMathLists"
          },
          childInfoByComponent: {
            dependencyType: "stateVariable",
            variableName: "childInfoByComponent"
          },
          numbersShadow: {
            dependencyType: "stateVariable",
            variableName: "numbersShadow"
          }
        };
        for (let arrayKey of arrayKeys) {
          let childIndices = [];
          if (stateValues.childInfoByComponent[arrayKey]) {
            childIndices = [
              stateValues.childInfoByComponent[arrayKey].childInd
            ];
          }
          dependenciesByKey[arrayKey] = {
            numberMathChildren: {
              dependencyType: "child",
              childGroups: ["numbers", "maths"],
              variableNames: ["value"],
              childIndices
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let numbers = {};
        for (let arrayKey of arrayKeys) {
          let child = dependencyValuesByKey[arrayKey].numberMathChildren[0];
          if (child) {
            let childValue = child.stateValues.value;
            if (globalDependencyValues.mergeMathLists && Array.isArray(childValue.tree) && childValue.tree[0] === "list") {
              let ind2 = globalDependencyValues.childInfoByComponent[arrayKey].component;
              numbers[arrayKey] = childValue.get_component(ind2).evaluate_to_constant();
            } else if (childValue.evaluate_to_constant !== void 0) {
              numbers[arrayKey] = childValue.evaluate_to_constant();
            } else {
              numbers[arrayKey] = childValue;
            }
          } else if (globalDependencyValues.numbersShadow !== null) {
            numbers[arrayKey] = globalDependencyValues.numbersShadow[arrayKey];
          }
        }
        return { setValue: { numbers } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        globalDependencyValues,
        dependencyNamesByKey,
        componentInfoObjects,
        stateValues,
        workspace
      }) {
        if (globalDependencyValues.mergeMathLists) {
          let instructions2 = [];
          let childInfoByComponent = await stateValues.childInfoByComponent;
          let arrayKeysAddressed = [];
          for (let arrayKey in desiredStateVariableValues.numbers) {
            if (!dependencyValuesByKey[arrayKey]) {
              continue;
            }
            if (arrayKeysAddressed.includes(arrayKey)) {
              continue;
            }
            let child = dependencyValuesByKey[arrayKey].numberMathChildren[0];
            let desiredValue;
            if (childInfoByComponent[arrayKey].nComponents !== void 0) {
              let firstInd = Number(arrayKey) - childInfoByComponent[arrayKey].component;
              let lastInd = firstInd + childInfoByComponent[arrayKey].nComponents - 1;
              if (!workspace.desiredMaths) {
                workspace.desiredMaths = [];
              }
              let desiredTree = ["list"];
              for (let i2 = firstInd; i2 <= lastInd; i2++) {
                if (desiredStateVariableValues.numbers[i2] !== void 0) {
                  workspace.desiredMaths[i2] = convertValueToMathExpression(
                    desiredStateVariableValues.numbers[i2]
                  );
                } else if (workspace.desiredMaths[i2] === void 0) {
                  workspace.desiredMaths[i2] = me$3.fromAst(
                    (await stateValues.numbers)[i2]
                  );
                }
                desiredTree.push(
                  workspace.desiredMaths[i2].tree
                );
                arrayKeysAddressed.push(i2.toString());
              }
              desiredValue = me$3.fromAst(desiredTree);
            } else {
              desiredValue = desiredStateVariableValues.numbers[arrayKey];
              if (componentInfoObjects.isInheritedComponentType({
                inheritedComponentType: child?.componentType,
                baseComponentType: "math"
              })) {
                desiredValue = convertValueToMathExpression(desiredValue);
              }
            }
            if (child) {
              instructions2.push({
                setDependency: dependencyNamesByKey[arrayKey].numberMathChildren,
                desiredValue,
                childIndex: 0,
                variableIndex: 0
              });
            } else if (globalDependencyValues.numbersShadow !== null) {
              if (!workspace.desiredNumberShadow) {
                workspace.desiredNumberShadow = [
                  ...globalDependencyValues.numbersShadow
                ];
              }
              workspace.desiredNumberShadow[arrayKey] = desiredValue;
            }
          }
          if (workspace.desiredNumberShadow) {
            instructions2.push({
              setDependency: "numbersShadow",
              desiredValue: workspace.desiredNumberShadow
            });
          }
          return {
            success: true,
            instructions: instructions2
          };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.numbers) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let child = dependencyValuesByKey[arrayKey].numberMathChildren[0];
          if (child) {
            let desiredValue = desiredStateVariableValues.numbers[arrayKey];
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "math"
            })) {
              desiredValue = convertValueToMathExpression(desiredValue);
            }
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].numberMathChildren,
              desiredValue,
              childIndex: 0,
              variableIndex: 0
            });
          } else if (globalDependencyValues.numbersShadow !== null) {
            if (!workspace.desiredNumberShadow) {
              workspace.desiredNumberShadow = [
                ...globalDependencyValues.numbersShadow
              ];
            }
            workspace.desiredNumberShadow[arrayKey] = desiredStateVariableValues.numbers[arrayKey];
          }
        }
        if (workspace.desiredNumberShadow) {
          instructions.push({
            setDependency: "numbersShadow",
            desiredValue: workspace.desiredNumberShadow
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numComponents",
      description: "The number of numbers in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "numbers",
      description: "The list's numbers."
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        childInfoByComponent: {
          dependencyType: "stateVariable",
          variableName: "childInfoByComponent"
        }
      }),
      // When this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must set
      // stateVariableToEvaluateAfterReplacements
      // to this variable so that it is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let attributesToConvert = gatherRawNumberDisplayFixedResponseAttributes(
      component,
      components
    );
    const copyChild = component.definingChildren.length === 1 && component.definingChildren[0].componentType === "_copy" ? component.definingChildren[0] : null;
    let copyChildSource;
    if (copyChild) {
      const cIdx = await copyChild.stateValues.extendIdx;
      if (cIdx !== -1) {
        copyChildSource = {
          componentIdx: cIdx,
          componentType: components[cIdx].componentType
        };
      }
    }
    let childInfoByComponent = await component.stateValues.childInfoByComponent;
    let numComponents = await component.stateValues.numComponents;
    for (let i2 = 0; i2 < numComponents; i2++) {
      let attributes = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "number",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributes = JSON.parse(JSON.stringify(res.attributes));
        nComponents = res.nComponents;
      }
      if (copyChildSource) {
        nComponents = addShadowNumberDisplayAttributes({
          nComponents,
          stateIdInfo,
          source: copyChildSource,
          compositeIdx: copyChild.componentIdx,
          attributes,
          componentInfoObjects
        });
      }
      let childInfo = childInfoByComponent[i2];
      if (childInfo) {
        let replacementSource = components[childInfo.childIdx];
        if (childInfo.nComponents !== void 0) {
          componentsCopied.push(
            replacementSource.componentIdx + ":" + childInfo.component
          );
        } else {
          componentsCopied.push(replacementSource.componentIdx);
        }
      }
      replacements.push({
        type: "serialized",
        componentType: "number",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes,
        doenetAttributes: {},
        children: [],
        state: {},
        downstreamDependencies: {
          [component.componentIdx]: [
            {
              dependencyType: "referenceShadow",
              compositeIdx: component.componentIdx,
              propVariable: `number${i2 + 1}`
            }
          ]
        }
      });
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    workspace.numComponents = numComponents;
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let numComponents = await component.stateValues.numComponents;
    if (numComponents === workspace.numComponents) {
      let componentsToCopy = [];
      let childInfoByComponent = await component.stateValues.childInfoByComponent;
      for (let childInfo of childInfoByComponent) {
        let replacementSource = components[childInfo.childIdx];
        if (childInfo.nComponents !== void 0) {
          componentsToCopy.push(
            replacementSource.componentIdx + ":" + childInfo.component
          );
        } else {
          componentsToCopy.push(replacementSource.componentIdx);
        }
      }
      if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
        (x2, i2) => x2 === componentsToCopy[i2]
      )) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
class BooleanList extends CompositeComponent {
  static componentType = "booleanList";
  static componentDocs = {
    summary: "A list of booleans"
  };
  static takesIndex = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static allowInSchemaAsComponent = ["boolean"];
  // when another component has a attribute that is a booleanList,
  // use the booleans state variable to populate that attribute
  static stateVariableToBeShadowed = "booleans";
  static primaryStateVariableForDefinition = "booleansShadow";
  // even if inside a component that turned on descendantCompositesMustHaveAReplacement
  // don't required composite replacements
  static descendantCompositesMustHaveAReplacement = false;
  static doNotExpandAsShadowed = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = {
      createComponentOfType: "boolean",
      createStateVariable: "unordered",
      defaultValue: false,
      public: true,
      description: "Whether the order of items in this list should be treated as unordered (e.g. for matching)."
    };
    attributes.maxNumber = {
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: Infinity,
      public: true,
      description: "Maximum number of items to retain in the list."
    };
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this component is treated as a response for the purposes of assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let groupIntoBooleansSeparatedBySpaces = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
      componentType: "boolean"
    });
    sugarInstructions.push({
      replacementFunction: function({
        matchedChildren,
        nComponents,
        stateIdInfo
      }) {
        return groupIntoBooleansSeparatedBySpaces({
          matchedChildren,
          nComponents,
          stateIdInfo
        });
      }
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "booleans",
        componentTypes: ["boolean"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.booleansShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          booleansShadow: true
        }
      })
    };
    stateVariableDefinitions.numComponents = {
      description: "The number of items in the boolean list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      additionalStateVariablesDefined: ["childNameByComponent"],
      returnDependencies() {
        return {
          maxNumber: {
            dependencyType: "stateVariable",
            variableName: "maxNumber"
          },
          booleanChildren: {
            dependencyType: "child",
            childGroups: ["booleans"]
          },
          booleansShadow: {
            dependencyType: "stateVariable",
            variableName: "booleansShadow"
          }
        };
      },
      definition: function({ dependencyValues }) {
        let numComponents = 0;
        let childNameByComponent = [];
        if (dependencyValues.booleanChildren.length > 0) {
          childNameByComponent = dependencyValues.booleanChildren.map(
            (x2) => x2.componentIdx
          );
          numComponents = dependencyValues.booleanChildren.length;
        } else if (dependencyValues.booleansShadow !== null) {
          numComponents = dependencyValues.booleansShadow.length;
        }
        let maxNum = dependencyValues.maxNumber;
        if (numComponents > maxNum) {
          numComponents = maxNum;
          childNameByComponent = childNameByComponent.slice(
            0,
            maxNum
          );
        }
        return {
          setValue: { numComponents, childNameByComponent },
          checkForActualChange: { numComponents: true }
        };
      }
    };
    stateVariableDefinitions.booleans = {
      isArray: true,
      entryPrefixes: ["boolean"],
      stateVariablesDeterminingDependencies: ["childNameByComponent"],
      returnArraySizeDependencies: () => ({
        numComponents: {
          dependencyType: "stateVariable",
          variableName: "numComponents"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numComponents];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          childNameByComponent: {
            dependencyType: "stateVariable",
            variableName: "childNameByComponent"
          },
          booleansShadow: {
            dependencyType: "stateVariable",
            variableName: "booleansShadow"
          }
        };
        for (let arrayKey of arrayKeys) {
          let childIndices = [];
          if (stateValues.childNameByComponent[arrayKey]) {
            childIndices = [arrayKey];
          }
          dependenciesByKey[arrayKey] = {
            booleanChildren: {
              dependencyType: "child",
              childGroups: ["booleans"],
              variableNames: ["value"],
              childIndices
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let booleans = {};
        for (let arrayKey of arrayKeys) {
          let child = dependencyValuesByKey[arrayKey].booleanChildren[0];
          if (child) {
            booleans[arrayKey] = child.stateValues.value;
          } else if (globalDependencyValues.booleansShadow !== null) {
            booleans[arrayKey] = globalDependencyValues.booleansShadow[arrayKey];
          }
        }
        return { setValue: { booleans } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        workspace
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.booleans) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let child = dependencyValuesByKey[arrayKey].booleanChildren[0];
          if (child) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].booleanChildren,
              desiredValue: desiredStateVariableValues.booleans[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else if (globalDependencyValues.booleansShadow !== null) {
            if (!workspace.desiredBooleanShadow) {
              workspace.desiredBooleanShadow = [
                ...globalDependencyValues.booleansShadow
              ];
            }
            workspace.desiredBooleanShadow[arrayKey] = desiredStateVariableValues.booleans[arrayKey];
          }
        }
        if (workspace.desiredBooleanShadow) {
          instructions.push({
            setDependency: "booleansShadow",
            desiredValue: workspace.desiredBooleanShadow
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numComponents",
      description: "The number of booleans in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "booleans",
      description: "The list's booleans."
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        childNameByComponent: {
          dependencyType: "stateVariable",
          variableName: "childNameByComponent"
        }
      }),
      // When this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must set
      // stateVariableToEvaluateAfterReplacements
      // to this variable so that it is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let attributesToConvert = {};
    for (let attr of ["fixed", "isResponse", "isPotentialResponse"]) {
      if (attr in component.attributes) {
        attributesToConvert[attr] = component.attributes[attr];
      }
    }
    let childNameByComponent = await component.stateValues.childNameByComponent;
    let numComponents = await component.stateValues.numComponents;
    for (let i2 = 0; i2 < numComponents; i2++) {
      let attributesFromComposite = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "boolean",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributesFromComposite = res.attributes;
        nComponents = res.nComponents;
      }
      let childIdx = childNameByComponent[i2];
      let replacementSource = components[childIdx];
      if (replacementSource) {
        componentsCopied.push(replacementSource.componentIdx);
      }
      replacements.push({
        type: "serialized",
        componentType: "boolean",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: JSON.parse(JSON.stringify(attributesFromComposite)),
        doenetAttributes: {},
        children: [],
        state: {},
        downstreamDependencies: {
          [component.componentIdx]: [
            {
              dependencyType: "referenceShadow",
              compositeIdx: component.componentIdx,
              propVariable: `boolean${i2 + 1}`
            }
          ]
        }
      });
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    workspace.numComponents = numComponents;
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    nComponents,
    componentInfoObjects,
    workspace
  }) {
    let diagnostics = [];
    let numComponents = await component.stateValues.numComponents;
    if (numComponents === workspace.numComponents) {
      let componentsToCopy = [];
      let childNameByComponent = await component.stateValues.childNameByComponent;
      for (let childIdx of childNameByComponent) {
        let replacementSource = components[childIdx];
        if (replacementSource) {
          componentsToCopy.push(replacementSource.componentIdx);
        }
      }
      if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
        (x2, i2) => x2 === componentsToCopy[i2]
      )) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
class Collect extends CompositeComponent {
  static componentType = "collect";
  static componentDocs = {
    summary: "Collects descendants of a component matching a specified component type"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static acceptAnyAttribute = true;
  static stateVariableToEvaluateAfterReplacements = "needsReplacementsUpdatedWhenStale";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.disabled;
    delete attributes.modifyIndirectly;
    delete attributes.fixed;
    delete attributes.styleNumber;
    delete attributes.isResponse;
    delete attributes.isPotentialResponse;
    delete attributes.hide;
    attributes.maxNumber = {
      description: "Maximum number of descendants to collect.",
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: null,
      public: true
    };
    attributes.from = {
      createReferences: true,
      description: "Component subtree(s) to collect descendants from."
    };
    attributes.componentType = {
      createComponentOfType: "text",
      description: "Component type of descendants to collect."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.link = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { link: true } })
    };
    stateVariableDefinitions.sourceComponentIdx = {
      returnDependencies: () => ({
        from: {
          dependencyType: "attributeRefResolutions",
          attributeName: "from"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.from?.length === 1 && dependencyValues.from[0].unresolvedPath === null) {
          return {
            setValue: {
              sourceComponentIdx: dependencyValues.from[0].componentIdx
            }
          };
        } else {
          return {
            setValue: {
              sourceComponentIdx: null
            }
          };
        }
      }
    };
    stateVariableDefinitions.sourceComponent = {
      stateVariablesDeterminingDependencies: ["sourceComponentIdx"],
      returnDependencies({ stateValues }) {
        if (stateValues.sourceComponentIdx != null) {
          return {
            sourceComponent: {
              dependencyType: "componentIdentity",
              componentIdx: stateValues.sourceComponentIdx
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        let sourceComponent = null;
        if (dependencyValues.sourceComponent) {
          sourceComponent = dependencyValues.sourceComponent;
        }
        return {
          setValue: { sourceComponent }
        };
      }
    };
    stateVariableDefinitions.sourceInactive = {
      stateVariablesDeterminingDependencies: ["sourceComponent"],
      returnDependencies({ stateValues }) {
        if (stateValues.sourceComponent) {
          return {
            sourceIsInactiveCompositeReplacement: {
              dependencyType: "stateVariable",
              componentIdx: stateValues.sourceComponent.componentIdx,
              variableName: "isInactiveCompositeReplacement"
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            sourceInactive: Boolean(
              dependencyValues.sourceIsInactiveCompositeReplacement
            )
          }
        };
      }
    };
    stateVariableDefinitions.sourceName = {
      returnDependencies: () => ({
        sourceComponent: {
          dependencyType: "stateVariable",
          variableName: "sourceComponent"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.sourceComponent === null) {
          let warning = {
            message: "No source found for collect.",
            type: "warning"
          };
          return {
            setValue: { sourceName: "" },
            sendDiagnostics: [warning]
          };
        }
        return {
          setValue: {
            sourceName: dependencyValues.sourceComponent.componentIdx
          }
        };
      }
    };
    stateVariableDefinitions.componentTypeToCollect = {
      shadowVariable: true,
      returnDependencies: () => ({
        componentTypeAttr: {
          dependencyType: "attributeComponent",
          attributeName: "componentType",
          variableNames: ["value"]
        }
      }),
      definition: function({ dependencyValues, componentInfoObjects }) {
        let componentTypeToCollect = null;
        let diagnostics = [];
        if (dependencyValues.componentTypeAttr !== null) {
          const cType = dependencyValues.componentTypeAttr.stateValues.value;
          let componentType = componentInfoObjects.componentTypeLowerCaseMapping[cType.toLowerCase()];
          let cClass = componentInfoObjects.allComponentClasses[componentType];
          if (cClass) {
            componentTypeToCollect = componentType;
          } else {
            const warning = {
              type: "warning",
              message: `Cannot collect components of type \`<${cType}>\` as it is an invalid component type.`
            };
            if (dependencyValues.componentTypeAttr.position) {
              warning.position = dependencyValues.componentTypeAttr.position;
            }
            diagnostics.push(warning);
          }
        }
        return {
          setValue: {
            componentTypeToCollect
          },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.collectedComponents = {
      stateVariablesDeterminingDependencies: [
        "componentTypeToCollect",
        "sourceName"
      ],
      returnDependencies: function({ stateValues }) {
        if (!stateValues.sourceName) {
          return {};
        }
        let descendants = {
          dependencyType: "descendant",
          ancestorIdx: stateValues.sourceName,
          componentTypes: [stateValues.componentTypeToCollect],
          useReplacementsForComposites: true,
          includeNonActiveChildren: true,
          recurseToMatchedChildren: false
        };
        return {
          descendants,
          maxNumber: {
            dependencyType: "stateVariable",
            variableName: "maxNumber"
          }
        };
      },
      definition: function({ dependencyValues }) {
        let collectedComponents = dependencyValues.descendants;
        if (!collectedComponents) {
          collectedComponents = [];
        }
        if (dependencyValues.maxNumber !== null && collectedComponents.length > dependencyValues.maxNumber) {
          let maxnum = Math.max(
            0,
            Math.floor(dependencyValues.maxNumber)
          );
          collectedComponents = collectedComponents.slice(0, maxnum);
        }
        return { setValue: { collectedComponents } };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        collectedComponents: {
          dependencyType: "stateVariable",
          variableName: "collectedComponents"
        },
        needsReplacementsUpdatedWhenStale: {
          dependencyType: "stateVariable",
          variableName: "needsReplacementsUpdatedWhenStale"
        }
      }),
      definition: () => ({
        setValue: { readyToExpandWhenResolved: true }
      })
    };
    stateVariableDefinitions.needsReplacementsUpdatedWhenStale = {
      returnDependencies() {
        return {
          collectedComponents: {
            dependencyType: "stateVariable",
            variableName: "collectedComponents"
          }
        };
      },
      // the whole point of this state variable is to return updateReplacements
      // on mark stale
      markStale() {
        return { updateReplacements: true };
      },
      definition() {
        return {
          setValue: { needsReplacementsUpdatedWhenStale: true }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    workspace,
    componentInfoObjects,
    numComponentsForSource,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
    nComponents
  }) {
    let diagnostics = [];
    workspace.numReplacementsByCollected = [];
    workspace.collectedNames = [];
    workspace.replacementNamesByCollected = [];
    if (!await component.stateValues.sourceComponent) {
      return { replacements: [], diagnostics, nComponents };
    }
    let replacements = [];
    let numReplacementsByCollected = [];
    let numReplacementsSoFar = 0;
    let replacementNamesByCollected = [];
    let compositeAttributesObj = this.createAttributesObject();
    let collectedComponents = await component.stateValues.collectedComponents;
    for (let collectedNum = 0; collectedNum < collectedComponents.length; collectedNum++) {
      if (collectedComponents[collectedNum]) {
        let results = await this.createReplacementForCollected({
          component,
          collectedNum,
          components,
          numReplacementsSoFar,
          componentInfoObjects,
          compositeAttributesObj,
          numComponentsForSource,
          publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
          nComponents
        });
        diagnostics.push(...results.diagnostics);
        nComponents = results.nComponents;
        let collectedReplacements = results.serializedReplacements;
        numReplacementsByCollected[collectedNum] = collectedReplacements.length;
        numReplacementsSoFar += collectedReplacements.length;
        replacements.push(...collectedReplacements);
        replacementNamesByCollected[collectedNum] = collectedReplacements.map((x2) => x2.componentIdx);
      } else {
        numReplacementsByCollected[collectedNum] = 0;
        replacementNamesByCollected[collectedNum] = [];
      }
    }
    workspace.numReplacementsByCollected = numReplacementsByCollected;
    workspace.collectedNames = collectedComponents.map(
      (x2) => x2.componentIdx
    );
    workspace.replacementNamesByCollected = replacementNamesByCollected;
    return { replacements, diagnostics, nComponents };
  }
  static async createReplacementForCollected({
    component,
    components,
    collectedNum,
    numReplacementsSoFar,
    componentInfoObjects,
    compositeAttributesObj,
    numComponentsForSource,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
    nComponents
  }) {
    let diagnostics = [];
    let collectedObj = (await component.stateValues.collectedComponents)[collectedNum];
    let collectedName = collectedObj.componentIdx;
    let collectedComponent = components[collectedName];
    let serializedReplacements = [];
    if (!collectedComponent) {
      return {
        serializedReplacements,
        diagnostics
      };
    }
    serializedReplacements = [await collectedComponent.serialize()];
    let res = createNewComponentIndices(
      serializedReplacements,
      nComponents
    );
    serializedReplacements = res.components;
    nComponents = res.nComponents;
    serializedReplacements = postProcessCopy({
      serializedComponents: serializedReplacements,
      componentIdx: component.componentIdx,
      identifierPrefix: collectedNum + "|"
    });
    for (let repl of serializedReplacements) {
      if (!repl.attributes) {
        repl.attributes = {};
      }
      const res2 = convertUnresolvedAttributesForComponentType({
        attributes: component.attributes,
        componentType: repl.componentType,
        componentInfoObjects,
        compositeAttributesObj,
        nComponents
      });
      const attributesFromComposite = res2.attributes;
      nComponents = res2.nComponents;
      Object.assign(repl.attributes, attributesFromComposite);
    }
    return {
      serializedReplacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    componentChanges,
    components,
    nComponents,
    workspace,
    componentInfoObjects,
    numComponentsForSource,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
  }) {
    let diagnostics = [];
    let numReplacementsFoundSoFar = 0;
    workspace.numReplacementsByCollected = [
      ...workspace.numReplacementsByCollected
    ];
    for (let collectedNum = 0; collectedNum < workspace.numReplacementsByCollected.length; collectedNum++) {
      let indsDeleted = [];
      for (let [ind, repName] of workspace.replacementNamesByCollected[collectedNum].entries()) {
        if (!component.replacements[numReplacementsFoundSoFar] || component.replacements[numReplacementsFoundSoFar].componentIdx !== repName) {
          indsDeleted.push(ind);
        } else {
          numReplacementsFoundSoFar++;
        }
      }
      for (let ind of indsDeleted.reverse()) {
        workspace.replacementNamesByCollected[collectedNum].splice(
          ind,
          1
        );
      }
      workspace.numReplacementsByCollected[collectedNum] -= indsDeleted.length;
    }
    let replacementChanges = [];
    let numReplacementsSoFar = 0;
    let numReplacementsByCollected = [];
    let replacementNamesByCollected = [];
    let collectedComponents = await component.stateValues.collectedComponents;
    let maxCollectedLength = Math.max(
      collectedComponents.length,
      workspace.numReplacementsByCollected.length
    );
    let recreateRemaining = false;
    let compositeAttributesObj = this.createAttributesObject();
    for (let collectedNum = 0; collectedNum < maxCollectedLength; collectedNum++) {
      let collected = collectedComponents[collectedNum];
      if (collected === void 0) {
        if (workspace.numReplacementsByCollected[collectedNum] > 0) {
          if (!recreateRemaining) {
            let numberReplacementsLeft = workspace.numReplacementsByCollected.slice(collectedNum).reduce((a2, c2) => a2 + c2, 0);
            if (numberReplacementsLeft > 0) {
              let replacementInstruction = {
                changeType: "delete",
                changeTopLevelReplacements: true,
                firstReplacementInd: numReplacementsSoFar,
                numberReplacementsToDelete: numberReplacementsLeft
              };
              replacementChanges.push(replacementInstruction);
            }
            recreateRemaining = true;
            workspace.numReplacementsByCollected.slice(collectedNum).forEach(
              (v2, i2) => workspace.numReplacementsByCollected[i2] = 0
            );
          }
        }
        numReplacementsByCollected[collectedNum] = 0;
        replacementNamesByCollected.push([]);
        continue;
      }
      let prevCollectedName = workspace.collectedNames[collectedNum];
      if (prevCollectedName === void 0 || collected.componentIdx !== prevCollectedName || recreateRemaining) {
        let prevNumReplacements = 0;
        if (prevCollectedName !== void 0) {
          prevNumReplacements = workspace.numReplacementsByCollected[collectedNum];
        }
        let numReplacementsToDelete = prevNumReplacements;
        if (recreateRemaining) {
          numReplacementsToDelete = 0;
        }
        let results = await this.recreateReplacements({
          component,
          collectedNum,
          numReplacementsSoFar,
          numReplacementsToDelete,
          components,
          nComponents,
          componentInfoObjects,
          compositeAttributesObj,
          numComponentsForSource,
          publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
        });
        diagnostics.push(...results.diagnostics);
        nComponents = results.nComponents;
        numReplacementsSoFar += results.numReplacements;
        numReplacementsByCollected[collectedNum] = results.numReplacements;
        let replacementInstruction = results.replacementInstruction;
        replacementNamesByCollected[collectedNum] = replacementInstruction.serializedReplacements.map(
          (x2) => x2.componentIdx
        );
        if (!recreateRemaining) {
          if (results.numReplacements !== prevNumReplacements) {
            let numberReplacementsLeft = workspace.numReplacementsByCollected.slice(collectedNum).reduce((a2, c2) => a2 + c2, 0);
            replacementInstruction.numberReplacementsToReplace = numberReplacementsLeft;
            recreateRemaining = true;
            workspace.numReplacementsByCollected.slice(collectedNum).forEach(
              (v2, i2) => workspace.numReplacementsByCollected[i2] = 0
            );
          }
        }
        replacementChanges.push(replacementInstruction);
        continue;
      }
      numReplacementsSoFar += workspace.numReplacementsByCollected[collectedNum];
      numReplacementsByCollected[collectedNum] = workspace.numReplacementsByCollected[collectedNum];
      replacementNamesByCollected[collectedNum] = workspace.replacementNamesByCollected[collectedNum];
    }
    workspace.numReplacementsByCollected = numReplacementsByCollected;
    workspace.collectedNames = collectedComponents.map(
      (x2) => x2.componentIdx
    );
    workspace.replacementNamesByCollected = replacementNamesByCollected;
    return { replacementChanges, diagnostics, nComponents };
  }
  static async recreateReplacements({
    component,
    collectedNum,
    numReplacementsSoFar,
    numReplacementsToDelete,
    components,
    componentInfoObjects,
    compositeAttributesObj,
    numComponentsForSource,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
    nComponents
  }) {
    let diagnostics = [];
    let results = await this.createReplacementForCollected({
      component,
      collectedNum,
      components,
      numReplacementsSoFar,
      componentInfoObjects,
      compositeAttributesObj,
      numComponentsForSource,
      publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
      nComponents
    });
    diagnostics.push(...results.diagnostics);
    nComponents = results.nComponents;
    let newSerializedChildren = results.serializedReplacements;
    let replacementInstruction = {
      changeType: "add",
      changeTopLevelReplacements: true,
      firstReplacementInd: numReplacementsSoFar,
      numberReplacementsToReplace: numReplacementsToDelete,
      serializedReplacements: newSerializedChildren
    };
    return {
      numReplacements: newSerializedChildren.length,
      replacementInstruction,
      diagnostics,
      nComponents
    };
  }
}
class Ref extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      navigateToTarget: this.navigateToTarget.bind(this)
    });
  }
  static componentType = "ref";
  static componentDocs = {
    summary: "A link to a component reference or to a URL"
  };
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.to = {
      createReferences: true,
      // Since we allow more than just references in `to`,
      // we specify `allowStrings` to suppress a warning for non-references.
      allowStrings: true,
      description: "The target that the reference points to (component reference or URL string)."
    };
    attributes.textType = {
      description: "Type of text to use for the reference label.",
      createComponentOfType: "text",
      createStateVariable: "textType",
      defaultValue: "type-global",
      public: true
    };
    attributes.createButton = {
      createComponentOfType: "boolean",
      createStateVariable: "createButton",
      defaultValue: false,
      forRenderer: true,
      description: "Whether to render this reference as a clickable button."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.targetComponentIdx = {
      additionalStateVariablesDefined: ["referenceFound"],
      returnDependencies: () => ({
        toAttr: {
          dependencyType: "attributeRefResolutions",
          attributeName: "to"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.toAttr?.length === 1) {
          const toAttr = dependencyValues.toAttr[0];
          if (toAttr.unresolvedPath === null) {
            return {
              setValue: {
                targetComponentIdx: toAttr.componentIdx,
                referenceFound: true
              }
            };
          }
        }
        return {
          setValue: {
            targetComponentIdx: null,
            referenceFound: Boolean(
              dependencyValues.toAttr?.length > 0
            )
          }
        };
      }
    };
    stateVariableDefinitions.targetRendererId = {
      additionalStateVariablesDefined: [
        "targetInactive",
        "targetComponent"
      ],
      stateVariablesDeterminingDependencies: ["targetComponentIdx"],
      forRenderer: true,
      returnDependencies({ stateValues }) {
        if (stateValues.targetComponentIdx != null) {
          return {
            targetComponent: {
              dependencyType: "stateVariable",
              componentIdx: stateValues.targetComponentIdx,
              variableName: "isInactiveCompositeReplacement",
              returnAsComponentObject: true
            },
            targetRendererId: {
              dependencyType: "rendererId",
              componentIdx: stateValues.targetComponentIdx
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.targetComponent) {
          const targetComponent = dependencyValues.targetComponent;
          if (targetComponent.stateValues.isInactiveCompositeReplacement) {
            return {
              setValue: {
                targetComponent,
                targetInactive: true,
                targetRendererId: null
              }
            };
          } else {
            return {
              setValue: {
                targetComponent,
                targetInactive: false,
                targetRendererId: dependencyValues.targetRendererId
              }
            };
          }
        } else {
          return {
            setValue: {
              targetComponent: null,
              targetInactive: false,
              targetRendererId: null
            }
          };
        }
      }
    };
    stateVariableDefinitions.url = {
      description: "The resolved URL of the referenced component.",
      forRenderer: true,
      additionalStateVariablesDefined: [
        {
          variableName: "activityId",
          forRenderer: true
        },
        {
          variableName: "activityUrlPostfix",
          forRenderer: true
        }
      ],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        stringsFromAttribute: {
          dependencyType: "stringsFromReferenceAttribute",
          attributeName: "to"
        },
        referenceFound: {
          dependencyType: "stateVariable",
          variableName: "referenceFound"
        },
        targetRendererId: {
          dependencyType: "stateVariable",
          variableName: "targetRendererId"
        }
      }),
      definition: function({ dependencyValues }) {
        let url = "#";
        let activityId = "";
        let activityUrlPostfix = "";
        if (dependencyValues.targetRendererId) {
          url = "#" + dependencyValues.targetRendererId;
          return {
            setValue: { url, activityId, activityUrlPostfix }
          };
        } else if (dependencyValues.referenceFound || dependencyValues.stringsFromAttribute?.length !== 1) {
          return {
            setValue: { url, activityId, activityUrlPostfix }
          };
        }
        url = dependencyValues.stringsFromAttribute[0] || "#";
        const result = url.match(/^doenet:([a-zA-Z0-9]+)((\?|#|$).*)/i);
        if (result) {
          activityId = result[1];
          activityUrlPostfix = result[2];
        }
        return {
          setValue: { url, activityId, activityUrlPostfix }
        };
      }
    };
    stateVariableDefinitions.linkText = {
      description: "The display text used for the link.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      stateVariablesDeterminingDependencies: [
        "targetRendererId",
        "targetComponentIdx"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          allChildren: {
            dependencyType: "child",
            childGroups: ["anything"],
            variableNames: ["text"],
            variablesOptional: true
          },
          url: {
            dependencyType: "stateVariable",
            variableName: "url"
          },
          targetRendererId: {
            dependencyType: "stateVariable",
            variableName: "targetRendererId"
          }
        };
        if (stateValues.targetRendererId) {
          dependencies.equationTag = {
            dependencyType: "stateVariable",
            componentIdx: stateValues.targetComponentIdx,
            variableName: "equationTag",
            variablesOptional: true
          };
          dependencies.title = {
            dependencyType: "stateVariable",
            componentIdx: stateValues.targetComponentIdx,
            variableName: "title",
            variablesOptional: true
          };
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let linkText = "";
        if (dependencyValues.allChildren.length === 0) {
          if (dependencyValues.targetRendererId) {
            if (dependencyValues.title !== null) {
              linkText = dependencyValues.title;
            } else if (dependencyValues.equationTag !== null) {
              linkText = "(" + dependencyValues.equationTag + ")";
            }
          } else if (dependencyValues.url !== null) {
            linkText = dependencyValues.url;
            if (linkText === "#") {
              linkText = "";
            }
          }
        } else {
          for (let child of dependencyValues.allChildren) {
            if (typeof child !== "object") {
              linkText += child.toString();
            } else if (typeof child.stateValues.text === "string") {
              linkText += child.stateValues.text;
            }
          }
        }
        if (!linkText) {
          linkText = "???";
        }
        return { setValue: { linkText } };
      }
    };
    stateVariableDefinitions.text = {
      isAlias: true,
      targetVariableName: "linkText",
      description: "The text used as the link label."
    };
    return stateVariableDefinitions;
  }
  async navigateToTarget({ actionId }) {
    if (await this.stateValues.disabled) {
      return;
    }
    let url = await this.stateValues.url;
    let activityId = await this.stateValues.activityId;
    let activityUrlPostfix = await this.stateValues.activityUrlPostfix;
    let targetRendererId = await this.stateValues.targetRendererId;
    let effectiveIdx = this.componentOrAdaptedIdx;
    this.coreFunctions.navigateToTarget({
      url,
      activityId,
      activityUrlPostfix,
      targetRendererId,
      actionId,
      componentIdx: this.componentIdx,
      effectiveIdx
    });
  }
}
class Coords extends MathComponent {
  static componentType = "coords";
  static rendererType = "math";
  static componentDocs = {
    summary: "A math expression treated as a vector of coordinates"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.createVectors;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.createVectors = {
      description: "Whether tuple-like expressions render as vectors (always true for `<coords>`).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { createVectors: true } })
    };
    return stateVariableDefinitions;
  }
  // TODO: do we want to give warnings or errors if value is not in form of a vector?
  // updateState(args={}) {
  //   super.updateState(args);
  //   // to be a valid coords, operator can only be a arithmetic, apply, or tuple
  //   let tree = this.state.value.tree;
  //   if(tree === "\uFF3F") {
  //     console.log("Invalid value of a coords");
  //   }
  //   if(typeof tree !== "number" && typeof tree !== "string") {
  //     if(!Array.isArray(tree)) {
  //       console.log("Invalid value of a coords");
  //     }
  //     let operator = tree[0];
  //     if(!["+","-","*","/","_","^","prime","apply"].includes(operator)) {
  //       if(["tuple", "vector"].includes(operator)) {
  //         for(let ind=1; ind<tree.length; ind++) {
  //           let subtree = tree[ind];
  //           if(typeof subtree !== "number" && typeof subtree !== "string") {
  //             if(!Array.isArray(tree)) {
  //               console.log("Invalid value of a coords");
  //             }
  //             if(!["+","-","*","/","_","^","prime","apply"].includes(subtree[0])) {
  //               console.log("Invalid value of a coords");
  //             }
  //           }
  //         }
  //       }else {
  //        console.log("Invalid value of a coords");
  //       }
  //     }
  //   }
  // }
}
class LineSegment extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveLineSegment: this.moveLineSegment.bind(this),
      moveLineSegmentSinglePoint: this.moveLineSegmentSinglePoint.bind(this),
      lineSegmentClicked: this.lineSegmentClicked.bind(this),
      lineSegmentFocused: this.lineSegmentFocused.bind(this)
    });
  }
  static componentType = "lineSegment";
  static styleOverrideCategories = ["line"];
  static componentDocs = {
    summary: "A line segment between two endpoints"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the line segment can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.endpointsDraggable = {
      createComponentOfType: "boolean",
      description: "Whether the line segment's endpoints can be dragged."
    };
    attributes.endpoints = {
      createComponentOfType: "pointList",
      description: "The two endpoints of the line segment."
    };
    attributes.addControls = {
      description: "Whether to render interactive control handles.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "endpoints",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "endpoints",
          description: "Show control handles for both endpoints of the segment."
        },
        {
          value: "none",
          description: "Show no control handles."
        }
      ],
      valueForTrue: "endpoints",
      valueForFalse: "none"
    };
    attributes.showCoordsWhenDragging = {
      description: "Whether to show coordinate labels while dragging.",
      createComponentOfType: "boolean",
      createStateVariable: "showCoordsWhenDragging",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.labelPosition = returnLineFamilyLabelPositionAttribute();
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.controlOrder = returnGraphControlOrderAttribute();
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    Object.assign(stateVariableDefinitions, returnStickyGroupDefinitions());
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the line segment's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let styleDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (styleDescription) {
            styleDescription += " ";
          }
          styleDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (styleDescription) {
          styleDescription += " ";
        }
        styleDescription += lineColorWord;
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including "line segment".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " line segment";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.endpointsDraggable = {
      description: "Whether each endpoint can be dragged independently.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      hasEssential: true,
      forRenderer: true,
      returnDependencies: () => ({
        endpointsDraggableAttr: {
          dependencyType: "attributeComponent",
          attributeName: "endpointsDraggable",
          variableNames: ["value"]
        },
        draggable: {
          dependencyType: "stateVariable",
          variableName: "draggable"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.endpointsDraggableAttr) {
          return {
            setValue: {
              endpointsDraggable: dependencyValues.endpointsDraggableAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              endpointsDraggable: {
                defaultValue: dependencyValues.draggable
              }
            }
          };
        }
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions the segment lives in.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        endpointsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "endpoints",
          variableNames: ["numDimensions"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.endpointsAttr !== null) {
          let numDimensions = dependencyValues.endpointsAttr.stateValues.numDimensions;
          return {
            setValue: { numDimensions: Math.max(numDimensions, 2) },
            checkForActualChange: { numDimensions: true }
          };
        } else {
          return { setValue: { numDimensions: 2 } };
        }
      }
    };
    stateVariableDefinitions.unconstrainedEndpoints = {
      isLocation: true,
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["unconstrainedEndpointX", "unconstrainedEndpoint"],
      returnEntryDimensions: (prefix) => prefix === "unconstrainedEndpoint" ? 1 : 0,
      hasEssential: true,
      set: convertValueToMathExpression,
      defaultValueByArrayKey: (arrayKey) => me$3.fromAst(arrayKey === "0,0" ? 1 : 0),
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "unconstrainedEndpointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "unconstrainedEndpoints") {
          if (propIndex.length === 1) {
            return "unconstrainedEndpoint" + propIndex[0];
          } else {
            return `unconstrainedEndpointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 21) === "unconstrainedEndpoint") {
          let endpointNum = Number(varName.slice(8));
          if (Number.isInteger(endpointNum) && endpointNum > 0) {
            return `unconstrainedEndpointX${endpointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [2, dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          dependenciesByKey[arrayKey] = {
            endpointsAttr: {
              dependencyType: "attributeComponent",
              attributeName: "endpoints",
              variableNames: ["pointX" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let unconstrainedEndpoints = {};
        let essentialUnconstrainedEndpoints = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          if (dependencyValuesByKey[arrayKey].endpointsAttr !== null && dependencyValuesByKey[arrayKey].endpointsAttr.stateValues["pointX" + varEnding]) {
            unconstrainedEndpoints[arrayKey] = dependencyValuesByKey[arrayKey].endpointsAttr.stateValues["pointX" + varEnding];
          } else {
            essentialUnconstrainedEndpoints[arrayKey] = true;
          }
        }
        let result = {};
        if (Object.keys(unconstrainedEndpoints).length > 0) {
          result.setValue = { unconstrainedEndpoints };
        }
        if (Object.keys(essentialUnconstrainedEndpoints).length > 0) {
          result.useEssentialOrDefaultValue = {
            unconstrainedEndpoints: essentialUnconstrainedEndpoints
          };
        }
        return result;
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.unconstrainedEndpoints) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          if (dependencyValuesByKey[arrayKey].endpointsAttr !== null && dependencyValuesByKey[arrayKey].endpointsAttr.stateValues["pointX" + varEnding]) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].endpointsAttr,
              desiredValue: desiredStateVariableValues.unconstrainedEndpoints[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else {
            instructions.push({
              setEssentialValue: "unconstrainedEndpoints",
              value: {
                [arrayKey]: desiredStateVariableValues.unconstrainedEndpoints[arrayKey]
              }
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.haveConstrainedEndpoints = {
      returnDependencies: () => ({
        inStickyGroup: {
          dependencyType: "stateVariable",
          variableName: "inStickyGroup"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveConstrainedEndpoints: dependencyValues.inStickyGroup
          }
        };
      }
    };
    stateVariableDefinitions.endpoints = {
      public: true,
      description: "The endpoints of the line segment.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "endpointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      indexAliases: [[], ["x", "y", "z"]],
      entryPrefixes: ["endpointX", "endpoint"],
      returnEntryDimensions: (prefix) => prefix === "endpoint" ? 1 : 0,
      hasEssential: true,
      set: convertValueToMathExpression,
      defaultValueByArrayKey: (arrayKey) => me$3.fromAst(arrayKey === "0,0" ? 1 : 0),
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "endpointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "endpoints") {
          if (propIndex.length === 1) {
            return "endpoint" + propIndex[0];
          } else {
            return `endpointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 8) === "endpoint") {
          let endpointNum = Number(varName.slice(8));
          if (Number.isInteger(endpointNum) && endpointNum > 0) {
            return `endpointX${endpointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [2, dependencyValues.numDimensions];
      },
      stateVariablesDeterminingDependencies: ["haveConstrainedEndpoints"],
      returnArrayDependenciesByKey({ stateValues, arrayKeys }) {
        let globalDependencies = {
          haveConstrainedEndpoints: {
            dependencyType: "stateVariable",
            variableName: "haveConstrainedEndpoints"
          }
        };
        let dependenciesByKey = {};
        if (stateValues.haveConstrainedEndpoints) {
          globalDependencies.unconstrainedEndpoints = {
            dependencyType: "stateVariable",
            variableName: "unconstrainedEndpoints"
          };
        } else {
          for (let arrayKey of arrayKeys) {
            let [pointInd, dim] = arrayKey.split(",");
            let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
            dependenciesByKey[arrayKey] = {
              unconstrainedEndpoint: {
                dependencyType: "stateVariable",
                variableName: "unconstrainedEndpointX" + varEnding
              }
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        arraySize
      }) {
        let endpoints = {};
        if (globalDependencyValues.haveConstrainedEndpoints) {
          let constrainedEndpoints = globalDependencyValues.unconstrainedEndpoints;
          for (let pointInd = 0; pointInd < 2; pointInd++) {
            for (let dim = 0; dim < arraySize[1]; dim++) {
              let arrayKey = pointInd + "," + dim;
              endpoints[arrayKey] = constrainedEndpoints[pointInd][dim];
            }
          }
        } else {
          for (let arrayKey of arrayKeys) {
            endpoints[arrayKey] = dependencyValuesByKey[arrayKey].unconstrainedEndpoint;
          }
        }
        return { setValue: { endpoints } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues,
        workspace,
        arraySize
      }) {
        let instructions = [];
        if (globalDependencyValues.haveConstrainedEndpoints) {
          let movedJustOneEndpoint = false;
          let endpointIndMoved;
          Object.assign(
            workspace,
            desiredStateVariableValues.endpoints
          );
          let nMoved = Object.keys(workspace).length;
          if (nMoved === 1) {
            movedJustOneEndpoint = true;
            endpointIndMoved = Number(
              Object.keys(workspace)[0].split(",")[0]
            );
          } else if (nMoved === 2) {
            let pointInd1 = Object.keys(workspace)[0].split(",")[0];
            let pointInd2 = Object.keys(workspace)[1].split(",")[0];
            if (pointInd1 === pointInd2) {
              movedJustOneEndpoint = true;
              endpointIndMoved = Number(pointInd1);
            }
          }
          let endpoints = await stateValues.endpoints;
          let desired_endpoints = [];
          for (let pointInd = 0; pointInd < arraySize[0]; pointInd++) {
            let desired_endpoint = [];
            let original_endpoint = endpoints[pointInd];
            for (let dim = 0; dim < arraySize[1]; dim++) {
              let arrayKey = pointInd + "," + dim;
              if (arrayKey in workspace) {
                desired_endpoint.push(workspace[arrayKey]);
              } else {
                desired_endpoint.push(original_endpoint[dim]);
              }
            }
            desired_endpoints.push(desired_endpoint);
          }
          let enforceRigid = !movedJustOneEndpoint;
          let allowRotation = movedJustOneEndpoint;
          if (await stateValues.inStickyGroup) {
            let stickyObjectIndex = await stateValues.stickyObjectIndex;
            let stickyVerticesConstraintFunction = await stateValues.stickyVerticesConstraintFunction;
            desired_endpoints = stickyVerticesConstraintFunction(
              {
                unconstrainedVertices: desired_endpoints,
                closed: false,
                enforceRigid,
                allowRotation,
                shrinkThreshold: false,
                vertexIndMoved: endpointIndMoved
              },
              { objectInd: stickyObjectIndex }
            );
          }
          instructions.push({
            setDependency: "unconstrainedEndpoints",
            desiredValue: desired_endpoints
          });
        } else {
          for (let arrayKey in desiredStateVariableValues.endpoints) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].unconstrainedEndpoint,
              desiredValue: desiredStateVariableValues.endpoints[arrayKey]
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.parallelCoords = {
      returnDependencies: () => ({
        endpoints: {
          dependencyType: "stateVariable",
          variableName: "endpoints"
        }
      }),
      definition({ dependencyValues }) {
        let dxTree = [
          "+",
          dependencyValues.endpoints[1][0].tree,
          ["-", dependencyValues.endpoints[0][0].tree]
        ];
        let dyTree = [
          "+",
          dependencyValues.endpoints[1][1].tree,
          ["-", dependencyValues.endpoints[0][1].tree]
        ];
        let parallelCoords = me$3.fromAst(["vector", dxTree, dyTree]);
        return { setValue: { parallelCoords } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let x2 = me$3.fromAst([
          "+",
          desiredStateVariableValues.parallelCoords.get_component(0).tree,
          dependencyValues.endpoints[0][0].tree
        ]);
        let y2 = me$3.fromAst([
          "+",
          desiredStateVariableValues.parallelCoords.get_component(1).tree,
          dependencyValues.endpoints[0][1].tree
        ]);
        return {
          success: true,
          instructions: [
            {
              setDependency: "endpoints",
              desiredValue: { "1,0": x2, "1,1": y2 }
            }
          ]
        };
      }
    };
    stateVariableDefinitions.length = {
      description: "The length of the line segment.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        endpoints: {
          dependencyType: "stateVariable",
          variableName: "endpoints"
        }
      }),
      definition({ dependencyValues }) {
        let length2 = 0;
        let epoint1 = dependencyValues.endpoints[0];
        let epoint2 = dependencyValues.endpoints[1];
        let all_numeric = true;
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          let v1 = epoint1[dim].evaluate_to_constant();
          if (!Number.isFinite(v1)) {
            all_numeric = false;
            break;
          }
          let v2 = epoint2[dim].evaluate_to_constant();
          if (!Number.isFinite(v2)) {
            all_numeric = false;
            break;
          }
          let d2 = v1 - v2;
          length2 += d2 * d2;
        }
        if (all_numeric) {
          return {
            setValue: { length: me$3.fromAst(Math.sqrt(length2)) }
          };
        }
        length2 = ["+"];
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          length2.push([
            "^",
            ["+", epoint1[dim], ["-", epoint2[dim]]],
            2
          ]);
        }
        return {
          setValue: {
            length: me$3.fromAst(["apply", "sqrt", length2])
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let midpoint = [];
        let dir = [];
        let epoint1 = dependencyValues.endpoints[0];
        let epoint2 = dependencyValues.endpoints[1];
        let all_numeric = true;
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          let v1 = epoint1[dim].evaluate_to_constant();
          if (!Number.isFinite(v1)) {
            all_numeric = false;
            break;
          }
          let v2 = epoint2[dim].evaluate_to_constant();
          if (!Number.isFinite(v2)) {
            all_numeric = false;
            break;
          }
          midpoint.push((v1 + v2) / 2);
          dir.push(v1 - v2);
        }
        if (!all_numeric) {
          return { success: false };
        }
        let dir_length = Math.sqrt(dir.reduce((a2, c2) => a2 + c2 * c2, 0));
        dir = dir.map((x2) => x2 / dir_length);
        let desiredLength = desiredStateVariableValues.length.evaluate_to_constant();
        if (!Number.isFinite(desiredLength) || desiredLength < 0) {
          return { success: false };
        }
        let desiredEndpoint1 = [], desiredEndpoint2 = [];
        let halfDesiredlength = desiredLength / 2;
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          desiredEndpoint1.push(
            me$3.fromAst(
              midpoint[dim] + dir[dim] * halfDesiredlength
            )
          );
          desiredEndpoint2.push(
            me$3.fromAst(
              midpoint[dim] - dir[dim] * halfDesiredlength
            )
          );
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "endpoints",
              desiredValue: [desiredEndpoint1, desiredEndpoint2]
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numericalEndpoints = {
      isArray: true,
      entryPrefixes: ["numericalEndpoint"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        if (Number.isNaN(dependencyValues.numDimensions)) {
          return [0];
        }
        return [2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            endpoint: {
              dependencyType: "stateVariable",
              variableName: "endpoint" + (Number(arrayKey) + 1)
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        if (Number.isNaN(globalDependencyValues.numDimensions)) {
          return {};
        }
        let numericalEndpoints = {};
        for (let arrayKey of arrayKeys) {
          let endpoint = dependencyValuesByKey[arrayKey].endpoint;
          let numericalP = [];
          for (let ind = 0; ind < globalDependencyValues.numDimensions; ind++) {
            let val = endpoint[ind].evaluate_to_constant();
            numericalP.push(val);
          }
          numericalEndpoints[arrayKey] = numericalP;
        }
        return { setValue: { numericalEndpoints } };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalEndpoints: {
          dependencyType: "stateVariable",
          variableName: "numericalEndpoints"
        }
      }),
      definition({ dependencyValues }) {
        let A1 = dependencyValues.numericalEndpoints[0][0];
        let A2 = dependencyValues.numericalEndpoints[0][1];
        let B1 = dependencyValues.numericalEndpoints[1][0];
        let B2 = dependencyValues.numericalEndpoints[1][1];
        let haveConstants = Number.isFinite(A1) && Number.isFinite(A2) && Number.isFinite(B1) && Number.isFinite(B2);
        let skip = dependencyValues.numDimensions !== 2 || !haveConstants || B1 === A1 && B2 === A2;
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              if (skip) {
                return {};
              }
              let xscale = scales[0];
              let yscale = scales[1];
              let BA1 = (B1 - A1) / xscale;
              let BA2 = (B2 - A2) / yscale;
              let denom = BA1 * BA1 + BA2 * BA2;
              let t2 = ((variables.x1 - A1) / xscale * BA1 + (variables.x2 - A2) / yscale * BA2) / denom;
              let result = {};
              if (t2 <= 0) {
                result = { x1: A1, x2: A2 };
              } else if (t2 >= 1) {
                result = { x1: B1, x2: B2 };
              } else {
                result = {
                  x1: A1 + t2 * BA1 * xscale,
                  x2: A2 + t2 * BA2 * yscale
                };
              }
              if (variables.x3 !== void 0) {
                result.x3 = 0;
              }
              return result;
            }
          }
        };
      }
    };
    stateVariableDefinitions.nearestPointAsLine = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalEndpoints: {
          dependencyType: "stateVariable",
          variableName: "numericalEndpoints"
        }
      }),
      definition({ dependencyValues }) {
        let A1 = dependencyValues.numericalEndpoints[0][0];
        let A2 = dependencyValues.numericalEndpoints[0][1];
        let B1 = dependencyValues.numericalEndpoints[1][0];
        let B2 = dependencyValues.numericalEndpoints[1][1];
        let haveConstants = Number.isFinite(A1) && Number.isFinite(A2) && Number.isFinite(B1) && Number.isFinite(B2);
        let skip = dependencyValues.numDimensions !== 2 || !haveConstants || B1 === A1 && B2 === A2;
        return {
          setValue: {
            nearestPointAsLine: function({ variables, scales }) {
              if (skip) {
                return {};
              }
              let xscale = scales[0];
              let yscale = scales[1];
              let BA1 = (B1 - A1) / xscale;
              let BA2 = (B2 - A2) / yscale;
              let denom = BA1 * BA1 + BA2 * BA2;
              let t2 = ((variables.x1 - A1) / xscale * BA1 + (variables.x2 - A2) / yscale * BA2) / denom;
              let result = {
                x1: A1 + t2 * BA1 * xscale,
                x2: A2 + t2 * BA2 * yscale
              };
              if (variables.x3 !== void 0) {
                result.x3 = 0;
              }
              return result;
            }
          }
        };
      }
    };
    stateVariableDefinitions.slope = {
      description: "The slope of the line segment (2D only).",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        numericalEndpoints: {
          dependencyType: "stateVariable",
          variableName: "numericalEndpoints"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.numDimensions !== 2) {
          return { setValue: { slope: NaN } };
        }
        let ps = dependencyValues.numericalEndpoints;
        let slope = (ps[1][1] - ps[0][1]) / (ps[1][0] - ps[0][0]);
        return { setValue: { slope } };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "parallelCoords",
      componentType: "_directionComponent",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
  async moveLineSegmentSinglePoint({
    x: x2,
    y: y2,
    pointRole,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!Number.isFinite(x2) || !Number.isFinite(y2)) {
      console.warn(
        `Invalid endpoint coordinates for line segment move: x=${x2}, y=${y2}`
      );
      return;
    }
    if (pointRole === "endpoint1") {
      return await this.moveLineSegment({
        point1coords: [x2, y2],
        transient,
        skippable,
        actionId,
        sourceDetails,
        sourceInformation,
        skipRendererUpdate
      });
    } else if (pointRole === "endpoint2") {
      return await this.moveLineSegment({
        point2coords: [x2, y2],
        transient,
        skippable,
        actionId,
        sourceDetails,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      console.warn(`Invalid pointRole for line segment: ${pointRole}`);
      return;
    }
  }
  async moveLineSegment({
    point1coords,
    point2coords,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!transient) {
      skippable = false;
    }
    if (point1coords === void 0 || point2coords === void 0) {
      if (!await this.stateValues.endpointsDraggable) {
        return;
      }
    } else {
      if (!await this.stateValues.draggable) {
        return;
      }
    }
    let newComponents = {};
    if (point1coords !== void 0) {
      newComponents["0,0"] = me$3.fromAst(point1coords[0]);
      newComponents["0,1"] = me$3.fromAst(point1coords[1]);
    }
    if (point2coords !== void 0) {
      newComponents["1,0"] = me$3.fromAst(point2coords[0]);
      newComponents["1,1"] = me$3.fromAst(point2coords[1]);
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            componentIdx: this.componentIdx,
            updateType: "updateValue",
            stateVariable: "endpoints",
            value: newComponents,
            sourceDetails
          }
        ],
        transient: true,
        skippable,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            componentIdx: this.componentIdx,
            updateType: "updateValue",
            stateVariable: "endpoints",
            value: newComponents,
            sourceDetails
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            point1: point1coords,
            point2: point2coords
          }
        }
      });
    }
    if (point1coords !== void 0 && point2coords !== void 0) {
      let numericalPoints = [point1coords, point2coords];
      let resultingNumericalPoints = await this.stateValues.numericalEndpoints;
      let pointsChanged = [];
      let numPointsChanged = 0;
      for (let [ind, pt2] of numericalPoints.entries()) {
        if (!pt2.every((v2, i2) => v2 === resultingNumericalPoints[ind][i2])) {
          pointsChanged.push(ind);
          numPointsChanged++;
        }
      }
      if (numPointsChanged === 1) {
        let changedInd = pointsChanged[0];
        let orig1 = numericalPoints[changedInd];
        let changed1 = resultingNumericalPoints[changedInd];
        let changevec1 = orig1.map((v2, i2) => v2 - changed1[i2]);
        let newNumericalPoints = [];
        for (let i2 = 0; i2 < 2; i2++) {
          if (i2 === changedInd) {
            newNumericalPoints.push(resultingNumericalPoints[i2]);
          } else {
            newNumericalPoints.push(
              numericalPoints[i2].map((v2, j2) => v2 - changevec1[j2])
            );
          }
        }
        let newPointComponents = {};
        for (let ind in newNumericalPoints) {
          newPointComponents[ind + ",0"] = me$3.fromAst(
            newNumericalPoints[ind][0]
          );
          newPointComponents[ind + ",1"] = me$3.fromAst(
            newNumericalPoints[ind][1]
          );
        }
        let newInstructions = [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "endpoints",
            value: newPointComponents
          }
        ];
        return await this.coreFunctions.performUpdate({
          updateInstructions: newInstructions,
          transient,
          skippable,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async lineSegmentClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async lineSegmentFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class Ray extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveRay: this.moveRay.bind(this),
      rayClicked: this.rayClicked.bind(this),
      rayFocused: this.rayFocused.bind(this)
    });
  }
  static componentType = "ray";
  static styleOverrideCategories = ["line"];
  static componentDocs = {
    summary: "A ray starting at one point passing through another"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the ray can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.endpoint = {
      createComponentOfType: "point",
      description: "The starting point (endpoint) of the ray."
    };
    attributes.through = {
      createComponentOfType: "point",
      description: "A point the ray passes through (used with endpoint or direction)."
    };
    attributes.direction = {
      createComponentOfType: "vector",
      description: "The direction the ray points in."
    };
    attributes.labelPosition = returnLineFamilyLabelPositionAttribute();
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the ray's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let styleDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (styleDescription) {
            styleDescription += " ";
          }
          styleDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (styleDescription) {
          styleDescription += " ";
        }
        styleDescription += lineColorWord;
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "ray".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " ray";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.directionShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "direction",
      set: convertValueToMathExpression,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          directionShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "directionShadow",
              value: convertValueToMathExpression(
                desiredStateVariableValues.directionShadow
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.throughShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "through",
      set: convertValueToMathExpression,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          throughShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "throughShadow",
              value: convertValueToMathExpression(
                desiredStateVariableValues.throughShadow
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.endpointShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "endpoint",
      set: convertValueToMathExpression,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          endpointShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "endpointShadow",
              value: convertValueToMathExpression(
                desiredStateVariableValues.endpointShadow
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.basedOnThrough = {
      returnDependencies: () => ({
        throughAttr: {
          dependencyType: "attributeComponent",
          attributeName: "through"
        },
        throughShadow: {
          dependencyType: "stateVariable",
          variableName: "throughShadow"
        },
        endpointAttr: {
          dependencyType: "attributeComponent",
          attributeName: "endpoint"
        },
        directionAttr: {
          dependencyType: "attributeComponent",
          attributeName: "direction"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.endpointAttr !== null && dependencyValues.directionAttr !== null) {
          let diagnostics = [];
          if (dependencyValues.throughAttr !== null) {
            const warning = {
              type: "warning",
              message: "Ray is prescribed by through, endpoint, and direction.  Ignoring specified through."
            };
            if (dependencyValues.throughAttr.position) {
              warning.position = dependencyValues.throughAttr.position;
            }
            diagnostics.push(warning);
          }
          return {
            setValue: { basedOnThrough: false },
            checkForActualChange: { basedOnThrough: true },
            sendDiagnostics: diagnostics
          };
        }
        if (dependencyValues.throughAttr !== null) {
          return {
            setValue: { basedOnThrough: true },
            checkForActualChange: { basedOnThrough: true }
          };
        }
        return {
          setValue: {
            basedOnThrough: dependencyValues.throughShadow !== null
          },
          checkForActualChange: { basedOnThrough: true }
        };
      }
    };
    stateVariableDefinitions.basedOnEndpoint = {
      returnDependencies: () => ({
        endpointAttr: {
          dependencyType: "attributeComponent",
          attributeName: "endpoint"
        },
        endpointShadow: {
          dependencyType: "stateVariable",
          variableName: "endpointShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.endpointAttr !== null) {
          return {
            setValue: { basedOnEndpoint: true },
            checkForActualChange: { basedOnEndpoint: true }
          };
        }
        return {
          setValue: {
            basedOnEndpoint: dependencyValues.endpointShadow !== null
          },
          checkForActualChange: { basedOnEndpoint: true }
        };
      }
    };
    stateVariableDefinitions.basedOnDirection = {
      returnDependencies: () => ({
        directionAttr: {
          dependencyType: "attributeComponent",
          attributeName: "direction"
        },
        directionShadow: {
          dependencyType: "stateVariable",
          variableName: "directionShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.directionAttr !== null) {
          return {
            setValue: { basedOnDirection: true },
            checkForActualChange: { basedOnDirection: true }
          };
        }
        return {
          setValue: {
            basedOnDirection: dependencyValues.directionShadow !== null
          },
          checkForActualChange: { basedOnDirection: true }
        };
      }
    };
    stateVariableDefinitions.numDimDirection = {
      stateVariablesDeterminingDependencies: [
        "basedOnDirection",
        "basedOnThrough",
        "basedOnEndpoint"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          basedOnThrough: {
            dependencyType: "stateVariable",
            variableName: "basedOnThrough"
          },
          basedOnEndpoint: {
            dependencyType: "stateVariable",
            variableName: "basedOnEndpoint"
          },
          basedOnDirection: {
            dependencyType: "stateVariable",
            variableName: "basedOnDirection"
          },
          directionShadow: {
            dependencyType: "stateVariable",
            variableName: "directionShadow"
          },
          directionAttr: {
            dependencyType: "attributeComponent",
            attributeName: "direction",
            variableNames: ["numDimensions"]
          }
        };
        if (!stateValues.basedOnDirection) {
          if (stateValues.basedOnThrough) {
            dependencies.numDimThrough = {
              dependencyType: "stateVariable",
              variableName: "numDimThrough"
            };
          }
          if (stateValues.basedOnEndpoint) {
            dependencies.numDimEndpoint = {
              dependencyType: "stateVariable",
              variableName: "numDimEndpoint"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numDimDirection;
        if (dependencyValues.basedOnDirection) {
          if (dependencyValues.directionAttr !== null) {
            numDimDirection = dependencyValues.directionAttr.stateValues.numDimensions;
          } else if (dependencyValues.directionShadow) {
            let directionTree = dependencyValues.directionShadow.tree;
            if (Array.isArray(directionTree) && vectorOperators.includes(directionTree[0])) {
              numDimDirection = directionTree.length - 1;
            } else {
              numDimDirection = 2;
            }
          }
        } else {
          if (dependencyValues.basedOnThrough) {
            if (dependencyValues.basedOnEndpoint) {
              if (dependencyValues.numDimThrough === dependencyValues.numDimEndpoint) {
                numDimDirection = dependencyValues.numDimThrough;
              } else {
                numDimDirection = NaN;
              }
            } else {
              numDimDirection = dependencyValues.numDimThrough;
            }
          } else if (dependencyValues.basedOnEndpoint) {
            numDimDirection = dependencyValues.numDimEndpoint;
          } else {
            numDimDirection = 2;
          }
        }
        return {
          setValue: { numDimDirection },
          checkForActualChange: { numDimDirection: true }
        };
      }
    };
    stateVariableDefinitions.numDimThrough = {
      stateVariablesDeterminingDependencies: [
        "basedOnDirection",
        "basedOnThrough",
        "basedOnEndpoint"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          basedOnThrough: {
            dependencyType: "stateVariable",
            variableName: "basedOnThrough"
          },
          basedOnEndpoint: {
            dependencyType: "stateVariable",
            variableName: "basedOnEndpoint"
          },
          basedOnDirection: {
            dependencyType: "stateVariable",
            variableName: "basedOnDirection"
          },
          throughShadow: {
            dependencyType: "stateVariable",
            variableName: "throughShadow"
          },
          throughAttr: {
            dependencyType: "attributeComponent",
            attributeName: "through",
            variableNames: ["numDimensions"]
          }
        };
        if (!stateValues.basedOnThrough) {
          if (stateValues.basedOnEndpoint) {
            dependencies.numDimEndpoint = {
              dependencyType: "stateVariable",
              variableName: "numDimEndpoint"
            };
          }
          if (stateValues.basedOnDirection) {
            dependencies.numDimDirection = {
              dependencyType: "stateVariable",
              variableName: "numDimDirection"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numDimThrough;
        if (dependencyValues.basedOnThrough) {
          if (dependencyValues.throughAttr !== null) {
            numDimThrough = dependencyValues.throughAttr.stateValues.numDimensions;
          } else if (dependencyValues.throughShadow) {
            let throughTree = dependencyValues.throughShadow.tree;
            if (Array.isArray(throughTree) && vectorOperators.includes(throughTree[0])) {
              numDimThrough = throughTree.length - 1;
            } else {
              numDimThrough = 2;
            }
          }
        } else {
          if (dependencyValues.basedOnDirection) {
            if (dependencyValues.basedOnEndpoint) {
              if (dependencyValues.numDimDirection === dependencyValues.numDimEndpoint) {
                numDimThrough = dependencyValues.numDimDirection;
              } else {
                numDimThrough = NaN;
              }
            } else {
              numDimThrough = dependencyValues.numDimDirection;
            }
          } else if (dependencyValues.basedOnEndpoint) {
            numDimThrough = dependencyValues.numDimEndpoint;
          } else {
            numDimThrough = 2;
          }
        }
        return {
          setValue: { numDimThrough },
          checkForActualChange: { numDimThrough: true }
        };
      }
    };
    stateVariableDefinitions.numDimEndpoint = {
      stateVariablesDeterminingDependencies: [
        "basedOnDirection",
        "basedOnThrough",
        "basedOnEndpoint"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          basedOnThrough: {
            dependencyType: "stateVariable",
            variableName: "basedOnThrough"
          },
          basedOnEndpoint: {
            dependencyType: "stateVariable",
            variableName: "basedOnEndpoint"
          },
          basedOnDirection: {
            dependencyType: "stateVariable",
            variableName: "basedOnDirection"
          },
          endpointShadow: {
            dependencyType: "stateVariable",
            variableName: "endpointShadow"
          },
          endpointAttr: {
            dependencyType: "attributeComponent",
            attributeName: "endpoint",
            variableNames: ["numDimensions"]
          }
        };
        if (!stateValues.basedOnEndpoint) {
          if (stateValues.basedOnThrough) {
            dependencies.numDimThrough = {
              dependencyType: "stateVariable",
              variableName: "numDimThrough"
            };
          }
          if (stateValues.basedOnDirection) {
            dependencies.numDimDirection = {
              dependencyType: "stateVariable",
              variableName: "numDimDirection"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numDimEndpoint;
        if (dependencyValues.basedOnEndpoint) {
          if (dependencyValues.endpointAttr !== null) {
            numDimEndpoint = dependencyValues.endpointAttr.stateValues.numDimensions;
          } else if (dependencyValues.endpointShadow) {
            let endpointTree = dependencyValues.endpointShadow.tree;
            if (Array.isArray(endpointTree) && vectorOperators.includes(endpointTree[0])) {
              numDimEndpoint = endpointTree.length - 1;
            } else {
              numDimEndpoint = 2;
            }
          }
        } else {
          if (dependencyValues.basedOnDirection) {
            if (dependencyValues.basedOnThrough) {
              if (dependencyValues.numDimDirection === dependencyValues.numDimThrough) {
                numDimEndpoint = dependencyValues.numDimDirection;
              } else {
                numDimEndpoint = NaN;
              }
            } else {
              numDimEndpoint = dependencyValues.numDimDirection;
            }
          } else if (dependencyValues.basedOnThrough) {
            numDimEndpoint = dependencyValues.numDimThrough;
          } else {
            numDimEndpoint = 2;
          }
        }
        return {
          setValue: { numDimEndpoint },
          checkForActualChange: { numDimEndpoint: true }
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions the ray lives in.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        basedOnThrough: {
          dependencyType: "stateVariable",
          variableName: "basedOnThrough"
        },
        basedOnEndpoint: {
          dependencyType: "stateVariable",
          variableName: "basedOnEndpoint"
        },
        basedOnDirection: {
          dependencyType: "stateVariable",
          variableName: "basedOnDirection"
        },
        numDimDirection: {
          dependencyType: "stateVariable",
          variableName: "numDimDirection"
        },
        numDimThrough: {
          dependencyType: "stateVariable",
          variableName: "numDimThrough"
        },
        numDimEndpoint: {
          dependencyType: "stateVariable",
          variableName: "numDimEndpoint"
        }
      }),
      definition: function({ dependencyValues }) {
        let numDimensions;
        if (dependencyValues.basedOnDirection) {
          if (dependencyValues.basedOnEndpoint) {
            if (dependencyValues.numDimDirection !== dependencyValues.numDimEndpoint) {
              let warning = {
                message: "numDimensions mismatch in ray.",
                type: "warning"
              };
              return {
                setValue: { numDimensions: NaN },
                sendDiagnostics: [warning]
              };
            }
          } else if (dependencyValues.basedOnThrough) {
            if (dependencyValues.numDimDirection !== dependencyValues.numDimThrough) {
              let warning = {
                message: "numDimensions mismatch in ray.",
                type: "warning"
              };
              return {
                setValue: { numDimensions: NaN },
                sendDiagnostics: [warning]
              };
            }
          }
          numDimensions = dependencyValues.numDimDirection;
        } else if (dependencyValues.basedOnEndpoint) {
          if (dependencyValues.basedOnThrough) {
            if (dependencyValues.numDimEndpoint !== dependencyValues.numDimThrough) {
              let warning = {
                message: "numDimensions mismatch in ray.",
                type: "warning"
              };
              return {
                setValue: { numDimensions: NaN },
                sendDiagnostics: [warning]
              };
            }
          }
          numDimensions = dependencyValues.numDimEndpoint;
        } else if (dependencyValues.basedOnThrough) {
          numDimensions = dependencyValues.numDimThrough;
        } else {
          numDimensions = 2;
        }
        return {
          setValue: { numDimensions },
          checkForActualChange: { numDimensions: true }
        };
      }
    };
    stateVariableDefinitions.direction = {
      public: true,
      description: "The direction vector of the ray.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "directionX") {
            return [];
          } else {
            return [
              [
                "vector",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["directionX"],
      hasEssential: true,
      essentialVarName: "direction2",
      // since "direction" used for directionShadow
      set: convertValueToMathExpression,
      stateVariablesDeterminingDependencies: [
        "basedOnDirection",
        "basedOnThrough"
      ],
      returnArraySizeDependencies: () => ({
        numDimDirection: {
          dependencyType: "stateVariable",
          variableName: "numDimDirection"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimDirection];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          basedOnThrough: {
            dependencyType: "stateVariable",
            variableName: "basedOnThrough"
          },
          basedOnEndpoint: {
            dependencyType: "stateVariable",
            variableName: "basedOnEndpoint"
          },
          basedOnDirection: {
            dependencyType: "stateVariable",
            variableName: "basedOnDirection"
          },
          directionShadow: {
            dependencyType: "stateVariable",
            variableName: "directionShadow"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            directionAttr: {
              dependencyType: "attributeComponent",
              attributeName: "direction",
              variableNames: ["x" + varEnding]
            }
          };
          if (!stateValues.basedOnDirection && stateValues.basedOnThrough) {
            dependenciesByKey[arrayKey].endpointX = {
              dependencyType: "stateVariable",
              variableName: "endpointX" + varEnding
            };
            dependenciesByKey[arrayKey].throughX = {
              dependencyType: "stateVariable",
              variableName: "throughX" + varEnding
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let direction = {};
        let essentialDirection = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (globalDependencyValues.basedOnDirection) {
            if (dependencyValuesByKey[arrayKey].directionAttr !== null) {
              direction[arrayKey] = dependencyValuesByKey[arrayKey].directionAttr.stateValues["x" + varEnding];
            } else if (globalDependencyValues.directionShadow !== null) {
              direction[arrayKey] = globalDependencyValues.directionShadow.get_component(
                Number(arrayKey)
              );
            }
          } else if (globalDependencyValues.basedOnThrough) {
            direction[arrayKey] = dependencyValuesByKey[arrayKey].throughX.subtract(dependencyValuesByKey[arrayKey].endpointX).simplify();
          } else {
            essentialDirection[arrayKey] = {
              defaultValue: me$3.fromAst(arrayKey === "0" ? 1 : 0)
            };
          }
        }
        let result = {};
        if (Object.keys(direction).length > 0) {
          result.setValue = { direction };
        }
        if (Object.keys(essentialDirection).length > 0) {
          result.useEssentialOrDefaultValue = {
            direction: essentialDirection
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        let updateDirectionShadow = false;
        for (let arrayKey in desiredStateVariableValues.direction) {
          if (globalDependencyValues.basedOnDirection) {
            if (dependencyValuesByKey[arrayKey].directionAttr !== null) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].directionAttr,
                desiredValue: desiredStateVariableValues.direction[arrayKey],
                variableIndex: 0
              });
            } else {
              updateDirectionShadow = true;
            }
          } else if (globalDependencyValues.basedOnThrough) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].throughX,
              desiredValue: dependencyValuesByKey[arrayKey].endpointX.add(
                desiredStateVariableValues.direction[arrayKey]
              ).simplify()
            });
          } else {
            instructions.push({
              setEssentialValue: "direction",
              value: {
                [arrayKey]: convertValueToMathExpression(
                  desiredStateVariableValues.direction[arrayKey]
                )
              }
            });
          }
        }
        if (updateDirectionShadow) {
          if (arraySize[0] > 1) {
            let desiredDirection = ["vector"];
            for (let arrayKey in desiredStateVariableValues.direction) {
              desiredDirection[Number(arrayKey) + 1] = desiredStateVariableValues.direction[arrayKey].tree;
            }
            desiredDirection.length = arraySize[0] + 1;
            instructions.push({
              setDependency: "directionShadow",
              desiredValue: me$3.fromAst(desiredDirection)
            });
          } else if (arraySize[0] === 1 && "0" in desiredStateVariableValues.direction) {
            instructions.push({
              setDependency: "directionShadow",
              desiredValue: desiredStateVariableValues.direction[0]
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.through = {
      public: true,
      description: "A point the ray passes through.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "throughX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["throughX"],
      indexAliases: [["x", "y", "z"]],
      set: convertValueToMathExpression,
      stateVariablesDeterminingDependencies: ["basedOnThrough"],
      returnArraySizeDependencies: () => ({
        numDimThrough: {
          dependencyType: "stateVariable",
          variableName: "numDimThrough"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimThrough];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          basedOnDirection: {
            dependencyType: "stateVariable",
            variableName: "basedOnDirection"
          },
          basedOnEndpoint: {
            dependencyType: "stateVariable",
            variableName: "basedOnEndpoint"
          },
          basedOnThrough: {
            dependencyType: "stateVariable",
            variableName: "basedOnThrough"
          },
          throughShadow: {
            dependencyType: "stateVariable",
            variableName: "throughShadow"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            throughAttr: {
              dependencyType: "attributeComponent",
              attributeName: "through",
              variableNames: ["x" + varEnding]
            }
          };
          if (!stateValues.basedOnThrough) {
            dependenciesByKey[arrayKey].endpointX = {
              dependencyType: "stateVariable",
              variableName: "endpointX" + varEnding
            };
            dependenciesByKey[arrayKey].directionX = {
              dependencyType: "stateVariable",
              variableName: "directionX" + varEnding
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let through = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (globalDependencyValues.basedOnThrough) {
            if (dependencyValuesByKey[arrayKey].throughAttr !== null) {
              through[arrayKey] = dependencyValuesByKey[arrayKey].throughAttr.stateValues["x" + varEnding];
            } else if (globalDependencyValues.throughShadow !== null) {
              through[arrayKey] = globalDependencyValues.throughShadow.get_component(
                Number(arrayKey)
              );
            }
          } else {
            through[arrayKey] = dependencyValuesByKey[arrayKey].endpointX.add(dependencyValuesByKey[arrayKey].directionX).simplify();
          }
        }
        return { setValue: { through } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        let updateThroughShadow = false;
        for (let arrayKey in desiredStateVariableValues.through) {
          if (globalDependencyValues.basedOnThrough) {
            if (dependencyValuesByKey[arrayKey].throughAttr && dependencyValuesByKey[arrayKey].throughAttr !== null) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].throughAttr,
                desiredValue: desiredStateVariableValues.through[arrayKey],
                variableIndex: 0
              });
            } else if (globalDependencyValues.throughShadow !== null) {
              updateThroughShadow = true;
            }
          } else {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].directionX,
              desiredValue: desiredStateVariableValues.through[arrayKey].subtract(
                dependencyValuesByKey[arrayKey].endpointX
              ).simplify()
            });
          }
        }
        if (updateThroughShadow) {
          if (arraySize[0] > 1) {
            let desiredThrough = ["vector"];
            for (let arrayKey in desiredStateVariableValues.through) {
              desiredThrough[Number(arrayKey) + 1] = desiredStateVariableValues.through[arrayKey].tree;
            }
            desiredThrough.length = arraySize[0] + 1;
            instructions.push({
              setDependency: "throughShadow",
              desiredValue: me$3.fromAst(desiredThrough)
            });
          } else if (arraySize[0] === 1 && "0" in desiredStateVariableValues.through) {
            instructions.push({
              setDependency: "throughShadow",
              desiredValue: desiredStateVariableValues.through[0]
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.endpoint = {
      public: true,
      description: "The starting endpoint of the ray.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "endpointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["endpointX"],
      indexAliases: [["x", "y", "z"]],
      hasEssential: true,
      defaultValueByArrayKey: () => me$3.fromAst(0),
      essentialVarName: "endpoint2",
      // since endpointShadow uses "endpoint"
      set: convertValueToMathExpression,
      stateVariablesDeterminingDependencies: [
        "basedOnEndpoint",
        "basedOnThrough",
        "basedOnDirection"
      ],
      returnArraySizeDependencies: () => ({
        numDimEndpoint: {
          dependencyType: "stateVariable",
          variableName: "numDimEndpoint"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimEndpoint];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          basedOnDirection: {
            dependencyType: "stateVariable",
            variableName: "basedOnDirection"
          },
          basedOnThrough: {
            dependencyType: "stateVariable",
            variableName: "basedOnThrough"
          },
          endpointShadow: {
            dependencyType: "stateVariable",
            variableName: "endpointShadow"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            endpointAttr: {
              dependencyType: "attributeComponent",
              attributeName: "endpoint",
              variableNames: ["x" + varEnding]
            }
          };
          if (!stateValues.basedOnEndpoint) {
            if (stateValues.basedOnThrough && stateValues.basedOnDirection) {
              dependenciesByKey[arrayKey].throughX = {
                dependencyType: "stateVariable",
                variableName: "throughX" + varEnding
              };
              dependenciesByKey[arrayKey].directionX = {
                dependencyType: "stateVariable",
                variableName: "directionX" + varEnding
              };
            }
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let endpoint = {};
        let essentialEndpoint = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (dependencyValuesByKey[arrayKey].endpointAttr !== null) {
            endpoint[arrayKey] = dependencyValuesByKey[arrayKey].endpointAttr.stateValues["x" + varEnding];
          } else if (globalDependencyValues.endpointShadow !== null) {
            endpoint[arrayKey] = globalDependencyValues.endpointShadow.get_component(
              Number(arrayKey)
            );
          } else {
            if (globalDependencyValues.basedOnThrough && globalDependencyValues.basedOnDirection) {
              endpoint[arrayKey] = dependencyValuesByKey[arrayKey].throughX.subtract(
                dependencyValuesByKey[arrayKey].directionX
              ).simplify();
            } else {
              essentialEndpoint[arrayKey] = true;
            }
          }
        }
        let result = {};
        if (Object.keys(endpoint).length > 0) {
          result.setValue = { endpoint };
        }
        if (Object.keys(essentialEndpoint).length > 0) {
          result.useEssentialOrDefaultValue = {
            endpoint: essentialEndpoint
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        let updateEndpointShadow = false;
        for (let arrayKey in desiredStateVariableValues.endpoint) {
          if (dependencyValuesByKey[arrayKey].endpointAttr && dependencyValuesByKey[arrayKey].endpointAttr !== null) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].endpointAttr,
              desiredValue: desiredStateVariableValues.endpoint[arrayKey],
              variableIndex: 0
            });
          } else if (globalDependencyValues.endpointShadow !== null) {
            updateEndpointShadow = true;
          } else {
            if (globalDependencyValues.basedOnThrough && globalDependencyValues.basedOnDirection) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].directionX,
                desiredValue: dependencyValuesByKey[arrayKey].throughX.subtract(
                  desiredStateVariableValues.endpoint[arrayKey]
                ).simplify()
              });
            } else {
              instructions.push({
                setEssentialValue: "endpoint",
                value: {
                  [arrayKey]: convertValueToMathExpression(
                    desiredStateVariableValues.endpoint[arrayKey]
                  )
                }
              });
            }
          }
        }
        if (updateEndpointShadow) {
          if (arraySize[0] > 1) {
            let desiredEndpoint = ["vector"];
            for (let arrayKey in desiredStateVariableValues.endpoint) {
              desiredEndpoint[Number(arrayKey) + 1] = desiredStateVariableValues.endpoint[arrayKey].tree;
            }
            desiredEndpoint.length = arraySize[0] + 1;
            instructions.push({
              setDependency: "endpointShadow",
              desiredValue: me$3.fromAst(desiredEndpoint)
            });
          } else if (arraySize[0] === 1 && "0" in desiredStateVariableValues.endpoint) {
            instructions.push({
              setDependency: "endpointShadow",
              desiredValue: desiredStateVariableValues.endpoint[0]
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalEndpoint = {
      forRenderer: true,
      returnDependencies() {
        return {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          },
          endpoint: {
            dependencyType: "stateVariable",
            variableName: "endpoint"
          }
        };
      },
      definition({ dependencyValues }) {
        if (Number.isNaN(dependencyValues.numDimensions)) {
          return { setValue: { numericalEndpoint: [] } };
        }
        let endpoint = dependencyValues.endpoint;
        let numericalEndpoint = [];
        for (let ind = 0; ind < dependencyValues.numDimensions; ind++) {
          let val = endpoint[ind].evaluate_to_constant();
          numericalEndpoint.push(val);
        }
        return { setValue: { numericalEndpoint } };
      }
    };
    stateVariableDefinitions.numericalThroughpoint = {
      forRenderer: true,
      returnDependencies() {
        return {
          numDimensions: {
            dependencyType: "stateVariable",
            variableName: "numDimensions"
          },
          through: {
            dependencyType: "stateVariable",
            variableName: "through"
          }
        };
      },
      definition({ dependencyValues }) {
        if (Number.isNaN(dependencyValues.numDimensions)) {
          return { setValue: { numericalThroughpoint: [] } };
        }
        let through = dependencyValues.through;
        let numericalThroughpoint = [];
        for (let ind = 0; ind < dependencyValues.numDimensions; ind++) {
          let val = through[ind].evaluate_to_constant();
          numericalThroughpoint.push(val);
        }
        return { setValue: { numericalThroughpoint } };
      }
    };
    stateVariableDefinitions.directionCoords = {
      isLocation: true,
      returnDependencies: () => ({
        direction: {
          dependencyType: "stateVariable",
          variableName: "direction"
        }
      }),
      definition({ dependencyValues }) {
        let coordsAst = [];
        for (let v2 of dependencyValues.direction) {
          if (v2) {
            coordsAst.push(v2.tree);
          } else {
            coordsAst.push("＿");
          }
        }
        if (coordsAst.length > 1) {
          coordsAst = ["vector", ...coordsAst];
        } else if (coordsAst.length === 1) {
          coordsAst = coordsAst[0];
        } else {
          coordsAst = "＿";
        }
        return { setValue: { directionCoords: me$3.fromAst(coordsAst) } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let coordsAst = desiredStateVariableValues.directionCoords.tree;
        let newDirection;
        if (Array.isArray(coordsAst) && vectorOperators.includes(coordsAst[0])) {
          newDirection = coordsAst.slice(1).map((x2) => me$3.fromAst(x2));
        } else {
          newDirection = [desiredStateVariableValues.directionCoords];
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "direction",
              desiredValue: newDirection
            }
          ]
        };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalEndpoint: {
          dependencyType: "stateVariable",
          variableName: "numericalEndpoint"
        },
        numericalThroughpoint: {
          dependencyType: "stateVariable",
          variableName: "numericalThroughpoint"
        }
      }),
      definition({ dependencyValues }) {
        let A1 = dependencyValues.numericalEndpoint[0];
        let A2 = dependencyValues.numericalEndpoint[1];
        let B1 = dependencyValues.numericalThroughpoint[0];
        let B2 = dependencyValues.numericalThroughpoint[1];
        let haveConstants = Number.isFinite(A1) && Number.isFinite(A2) && Number.isFinite(B1) && Number.isFinite(B2);
        let skip = dependencyValues.numDimensions !== 2 || !haveConstants || B1 === A1 && B2 === A2;
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              if (skip) {
                return {};
              }
              let xscale = scales[0];
              let yscale = scales[1];
              let BA1 = (B1 - A1) / xscale;
              let BA2 = (B2 - A2) / yscale;
              let denom = BA1 * BA1 + BA2 * BA2;
              let t2 = ((variables.x1 - A1) / xscale * BA1 + (variables.x2 - A2) / yscale * BA2) / denom;
              let result = {};
              if (t2 <= 0) {
                result = { x1: A1, x2: A2 };
              } else {
                result = {
                  x1: A1 + t2 * BA1 * xscale,
                  x2: A2 + t2 * BA2 * yscale
                };
              }
              if (variables.x3 !== void 0) {
                result.x3 = 0;
              }
              return result;
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "directionCoords",
      componentType: "_directionComponent",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
  async moveRay({
    endpointcoords,
    throughcoords,
    transient,
    skippable,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let updateInstructions = [];
    if (endpointcoords !== void 0) {
      if (await this.stateValues.basedOnThrough && await this.stateValues.basedOnDirection) {
        let direction;
        if (throughcoords === void 0) {
          let numericalThroughpoint = await this.stateValues.numericalThroughpoint;
          direction = endpointcoords.map(
            (x2, i2) => numericalThroughpoint[i2] - x2
          );
        } else {
          direction = endpointcoords.map(
            (x2, i2) => throughcoords[i2] - x2
          );
        }
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "direction",
          value: direction.map((x2) => me$3.fromAst(x2))
        });
      } else {
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "endpoint",
          value: endpointcoords.map((x2) => me$3.fromAst(x2))
        });
      }
      if (throughcoords === void 0) {
        if (!await this.stateValues.basedOnThrough) {
          let numericalThroughpoint = await this.stateValues.numericalThroughpoint;
          let direction = endpointcoords.map(
            (x2, i2) => numericalThroughpoint[i2] - x2
          );
          updateInstructions.push({
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "direction",
            value: direction.map((x2) => me$3.fromAst(x2))
          });
        }
      }
    }
    if (throughcoords !== void 0) {
      if (await this.stateValues.basedOnThrough) {
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "through",
          value: throughcoords.map((x2) => me$3.fromAst(x2))
        });
      } else {
        if (endpointcoords == void 0) {
          endpointcoords = await this.stateValues.numericalEndpoint;
        }
        let direction = endpointcoords.map(
          (x2, i2) => throughcoords[i2] - x2
        );
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "direction",
          value: direction.map((x2) => me$3.fromAst(x2))
        });
      }
      if (endpointcoords === void 0) {
        if (await this.stateValues.basedOnThrough && await this.stateValues.basedOnDirection) {
          let numericalEndpoint = await this.stateValues.numericalEndpoint;
          let direction = throughcoords.map(
            (x2, i2) => x2 - numericalEndpoint[i2]
          );
          updateInstructions.push({
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "direction",
            value: direction.map((x2) => me$3.fromAst(x2))
          });
        }
      }
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        transient,
        skippable,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            through: throughcoords,
            endpoint: endpointcoords
          }
        }
      });
    }
    if (endpointcoords !== void 0 && throughcoords !== void 0 && await this.stateValues.basedOnEndpoint && await this.stateValues.basedOnThrough) {
      let numericalPoints = [endpointcoords, throughcoords];
      let resultingNumericalPoints = [
        await this.stateValues.numericalEndpoint,
        await this.stateValues.numericalThroughpoint
      ];
      let pointsChanged = [];
      let numPointsChanged = 0;
      for (let [ind, pt2] of numericalPoints.entries()) {
        if (!pt2.every((v2, i2) => v2 === resultingNumericalPoints[ind][i2])) {
          pointsChanged.push(ind);
          numPointsChanged++;
        }
      }
      if (numPointsChanged === 1) {
        let changedInd = pointsChanged[0];
        let orig1 = numericalPoints[changedInd];
        let changed1 = resultingNumericalPoints[changedInd];
        let changevec1 = orig1.map((v2, i2) => v2 - changed1[i2]);
        let newNumericalPoints = [];
        for (let i2 = 0; i2 < 2; i2++) {
          if (i2 === changedInd) {
            newNumericalPoints.push(resultingNumericalPoints[i2]);
          } else {
            newNumericalPoints.push(
              numericalPoints[i2].map((v2, j2) => v2 - changevec1[j2])
            );
          }
        }
        let newInstructions = [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "endpoint",
            value: newNumericalPoints[0].map((x2) => me$3.fromAst(x2))
          },
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "through",
            value: newNumericalPoints[1].map((x2) => me$3.fromAst(x2))
          }
        ];
        return await this.coreFunctions.performUpdate({
          updateInstructions: newInstructions,
          transient,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async rayClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async rayFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function getLineColorWord(selectedStyle, theme) {
  return theme === "dark" ? selectedStyle.lineColorWordDarkMode : selectedStyle.lineColorWord;
}
function getFillColorWord(selectedStyle, theme) {
  return theme === "dark" ? selectedStyle.fillColorWordDarkMode : selectedStyle.fillColorWord;
}
function getBorderDescription(selectedStyle) {
  let borderDescription = selectedStyle.lineWidthWord;
  if (selectedStyle.lineStyleWord) {
    if (borderDescription) {
      borderDescription += " ";
    }
    borderDescription += selectedStyle.lineStyleWord;
  }
  if (borderDescription) {
    borderDescription += " ";
  }
  return borderDescription;
}
function buildClosedShapeStyleDescription({
  filled,
  lineColorWord,
  fillColorWord,
  fillStyleWord,
  borderDescription,
  noun = "",
  includeBorderArticle = false
}) {
  if (!filled) {
    return borderDescription + lineColorWord + noun;
  }
  let styleDescription = `filled ${fillColorWord}${noun}`;
  if (fillStyleWord) {
    styleDescription += ` with ${fillStyleWord}`;
  }
  if (fillColorWord === lineColorWord) {
    if (borderDescription) {
      styleDescription += (fillStyleWord ? includeBorderArticle ? " and a " : " and " : includeBorderArticle ? " with a " : " with ") + borderDescription + "border";
    }
    return styleDescription;
  }
  return styleDescription + (fillStyleWord ? includeBorderArticle ? " and a " : " and " : includeBorderArticle ? " with a " : " with ") + borderDescription + lineColorWord + " border";
}
function buildFillStyleDescription({
  filled,
  fillColorWord,
  fillStyleWord
}) {
  if (!filled) {
    return "unfilled";
  }
  return fillStyleWord ? `${fillColorWord} ${fillStyleWord}` : fillColorWord;
}
class Polygon extends Polyline {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      movePolygon: this.movePolygon.bind(this),
      movePolygonCenter: this.movePolygonCenter.bind(this),
      reflectPolygon: this.reflectPolygon.bind(this),
      polygonClicked: this.polygonClicked.bind(this),
      polygonFocused: this.polygonFocused.bind(this)
    });
  }
  static componentType = "polygon";
  // Overrides Polyline's ["line"]: polygons are closed and can be filled.
  static styleOverrideCategories = ["line", "fill"];
  static componentDocs = {
    summary: "A polygon defined by a list of vertices"
  };
  static representsClosedPath = true;
  get movePolygon() {
    return this.movePolyline;
  }
  /**
   * Delegates to parent Polyline's movePolylineCenter action.
   * Provides polygon-specific naming for semantic clarity.
   */
  get movePolygonCenter() {
    return this.movePolylineCenter;
  }
  get reflectPolygon() {
    return this.reflectPolyline;
  }
  get polygonClicked() {
    return this.polylineClicked;
  }
  get polygonFocused() {
    return this.polylineFocused;
  }
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.addControls = {
      description: "Whether to render interactive vertex control handles.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "center",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "center",
          description: "Show a control handle for moving the polygon's center."
        },
        {
          value: "none",
          description: "Show no control handles."
        }
      ],
      valueForTrue: "center",
      valueForFalse: "none"
    };
    attributes.filled = {
      description: "Whether to fill the interior of the polygon.",
      createComponentOfType: "boolean",
      createStateVariable: "filled",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.controlOrder = returnGraphControlOrderAttribute();
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the polygon's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        filled: {
          dependencyType: "stateVariable",
          variableName: "filled"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const theme = dependencyValues.document?.stateValues.theme;
        const lineColorWord = getLineColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const fillColorWord = getFillColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const borderDescription = getBorderDescription(
          dependencyValues.selectedStyle
        );
        const styleDescription = buildClosedShapeStyleDescription({
          filled: dependencyValues.filled,
          lineColorWord,
          fillColorWord,
          fillStyleWord: dependencyValues.selectedStyle.fillStyleWord,
          borderDescription
        });
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "polygon".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        filled: {
          dependencyType: "stateVariable",
          variableName: "filled"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const theme = dependencyValues.document?.stateValues.theme;
        const lineColorWord = getLineColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const fillColorWord = getFillColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const borderDescription = getBorderDescription(
          dependencyValues.selectedStyle
        );
        const styleDescriptionWithNoun = buildClosedShapeStyleDescription({
          filled: dependencyValues.filled,
          lineColorWord,
          fillColorWord,
          fillStyleWord: dependencyValues.selectedStyle.fillStyleWord,
          borderDescription,
          noun: " polygon",
          includeBorderArticle: true
        });
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.borderStyleDescription = {
      description: "A textual description of the polygon's border style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const borderStyleDescription = getBorderDescription(dependencyValues.selectedStyle) + getLineColorWord(
          dependencyValues.selectedStyle,
          dependencyValues.document?.stateValues.theme
        );
        return { setValue: { borderStyleDescription } };
      }
    };
    stateVariableDefinitions.fillStyleDescription = {
      description: "A textual description of the polygon's fill style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        filled: {
          dependencyType: "stateVariable",
          variableName: "filled"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const fillStyleDescription = buildFillStyleDescription({
          filled: dependencyValues.filled,
          fillColorWord: getFillColorWord(
            dependencyValues.selectedStyle,
            dependencyValues.document?.stateValues.theme
          ),
          fillStyleWord: dependencyValues.selectedStyle.fillStyleWord
        });
        return { setValue: { fillStyleDescription } };
      }
    };
    stateVariableDefinitions.numSides = {
      isAlias: true,
      targetVariableName: "numVertices",
      description: "The number of sides of the polygon."
    };
    delete stateVariableDefinitions.length;
    stateVariableDefinitions.perimeter = {
      description: "The perimeter of the polygon.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        numericalVertices: {
          dependencyType: "stateVariable",
          variableName: "numericalVertices"
        }
      }),
      definition({ dependencyValues }) {
        let perimeter = 0;
        let verts = dependencyValues.numericalVertices;
        let nVerts = dependencyValues.numericalVertices.length;
        for (let i2 = 0; i2 < nVerts; i2++) {
          let dx = verts[(i2 + 1) % nVerts][0] - verts[i2][0];
          let dy = verts[(i2 + 1) % nVerts][1] - verts[i2][1];
          perimeter += Math.sqrt(dx * dx + dy * dy);
        }
        return { setValue: { perimeter } };
      }
    };
    stateVariableDefinitions.area = {
      description: "The area enclosed by the polygon.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        numericalVertices: {
          dependencyType: "stateVariable",
          variableName: "numericalVertices"
        }
      }),
      definition({ dependencyValues }) {
        let area2 = 0;
        let verts = dependencyValues.numericalVertices;
        let nVerts = dependencyValues.numericalVertices.length;
        for (let i2 = 1; i2 <= nVerts; i2++) {
          area2 += verts[i2 % nVerts][1] * (verts[(i2 + 1) % nVerts][0] - verts[i2 - 1][0]);
        }
        return { setValue: { area: Math.abs(area2 / 2) } };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalVertices: {
          dependencyType: "stateVariable",
          variableName: "numericalVertices"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues }) {
        let numDimensions = dependencyValues.numDimensions;
        let numVertices = dependencyValues.numVertices;
        let numericalVertices = dependencyValues.numericalVertices;
        let vals = [];
        let prPtx, prPty;
        let nxPtx = numericalVertices[numVertices - 1]?.[0];
        let nxPty = numericalVertices[numVertices - 1]?.[1];
        for (let i2 = 0; i2 < numVertices; i2++) {
          prPtx = nxPtx;
          prPty = nxPty;
          nxPtx = numericalVertices[i2]?.[0];
          nxPty = numericalVertices[i2]?.[1];
          if (!(Number.isFinite(prPtx) && Number.isFinite(prPty) && Number.isFinite(nxPtx) && Number.isFinite(nxPty))) {
            vals.push(null);
          } else {
            let BA1sub = nxPtx - prPtx;
            let BA2sub = nxPty - prPty;
            if (BA1sub === 0 && BA2sub === 0) {
              vals.push(null);
            } else {
              vals.push([BA1sub, BA2sub]);
            }
          }
        }
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              let xscale = scales[0];
              let yscale = scales[1];
              if (numDimensions !== 2 || numVertices === 0) {
                return {};
              }
              let closestDistance2 = Infinity;
              let closestResult = {};
              let x1 = variables.x1?.evaluate_to_constant();
              let x2 = variables.x2?.evaluate_to_constant();
              let prevPtx, prevPty;
              let nextPtx = numericalVertices[numVertices - 1][0];
              let nextPty = numericalVertices[numVertices - 1][1];
              for (let i2 = 0; i2 < numVertices; i2++) {
                prevPtx = nextPtx;
                prevPty = nextPty;
                nextPtx = numericalVertices[i2][0];
                nextPty = numericalVertices[i2][1];
                let val = vals[i2];
                if (val === null) {
                  continue;
                }
                let BA1 = val[0] / xscale;
                let BA2 = val[1] / yscale;
                let denom = BA1 * BA1 + BA2 * BA2;
                let t2 = ((x1 - prevPtx) / xscale * BA1 + (x2 - prevPty) / yscale * BA2) / denom;
                let result;
                if (t2 <= 0) {
                  result = { x1: prevPtx, x2: prevPty };
                } else if (t2 >= 1) {
                  result = { x1: nextPtx, x2: nextPty };
                } else {
                  result = {
                    x1: prevPtx + t2 * BA1 * xscale,
                    x2: prevPty + t2 * BA2 * yscale
                  };
                }
                let distance2 = Math.pow((x1 - result.x1) / xscale, 2) + Math.pow((x2 - result.x2) / yscale, 2);
                if (distance2 < closestDistance2) {
                  closestDistance2 = distance2;
                  closestResult = result;
                }
              }
              if (variables.x3 !== void 0 && Object.keys(closestResult).length > 0) {
                closestResult.x3 = 0;
              }
              return closestResult;
            }
          }
        };
      }
    };
    stateVariableDefinitions.closed = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { closed: true } })
    };
    stateVariableDefinitions.containsPoint = {
      returnDependencies: () => ({
        numericalVertices: {
          dependencyType: "stateVariable",
          variableName: "numericalVertices"
        }
      }),
      definition({ dependencyValues }) {
        let vertices = dependencyValues.numericalVertices;
        let numVertices = vertices.length;
        let dim = vertices[0]?.length;
        if (dim !== 2) {
          return { setValue: { containsPoint: () => false } };
        }
        let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
        for (let vertex of vertices) {
          if (vertex[0] < minx) {
            minx = vertex[0];
          }
          if (vertex[0] > maxx) {
            maxx = vertex[0];
          }
          if (vertex[1] < miny) {
            miny = vertex[1];
          }
          if (vertex[1] > maxy) {
            maxy = vertex[1];
          }
        }
        function isLeft(P0, P1, P22) {
          return (P1[0] - P0[0]) * (P22[1] - P0[1]) - (P22[0] - P0[0]) * (P1[1] - P0[1]);
        }
        let containsPoint = function(P3) {
          if (!(P3[0] <= maxx && P3[0] >= minx && P3[1] <= maxy && P3[1] >= miny)) {
            return false;
          }
          let wn2 = 0;
          for (let i2 = 0; i2 < numVertices; i2++) {
            let iPlus1 = (i2 + 1) % numVertices;
            if (vertices[i2][1] <= P3[1]) {
              if (vertices[iPlus1][1] > P3[1]) {
                if (isLeft(vertices[i2], vertices[iPlus1], P3) > 0)
                  wn2++;
              }
            } else {
              if (vertices[iPlus1][1] <= P3[1]) {
                if (isLeft(vertices[i2], vertices[iPlus1], P3) < 0)
                  wn2--;
              }
            }
          }
          return wn2 !== 0;
        };
        return { setValue: { containsPoint } };
      }
    };
    return stateVariableDefinitions;
  }
}
class Triangle extends Polygon {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveTriangleCenter: this.moveTriangleCenter.bind(this)
    });
  }
  static componentType = "triangle";
  static componentDocs = {
    summary: "A triangle defined by three vertices"
  };
  static rendererType = "polygon";
  /**
   * Delegates to parent Polygon's movePolygonCenter action.
   * Provides triangle-specific naming for semantic clarity.
   */
  get moveTriangleCenter() {
    return this.movePolygonCenter;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let styleDescriptionWithNounDef = stateVariableDefinitions.styleDescriptionWithNoun.definition;
    stateVariableDefinitions.styleDescriptionWithNoun.definition = function({ dependencyValues }) {
      let styleDescriptionWithNoun = styleDescriptionWithNounDef({
        dependencyValues
      }).setValue.styleDescriptionWithNoun;
      styleDescriptionWithNoun = styleDescriptionWithNoun.replaceAll(
        "polygon",
        "triangle"
      );
      return { setValue: { styleDescriptionWithNoun } };
    };
    stateVariableDefinitions.unconstrainedVertices.hasEssential = true;
    stateVariableDefinitions.unconstrainedVertices.defaultValueByArrayKey = function(arrayKey) {
      if (["0,1", "1,0"].includes(arrayKey)) {
        return me$3.fromAst(1);
      } else {
        return me$3.fromAst(0);
      }
    };
    stateVariableDefinitions.unconstrainedVertices.returnArraySizeDependencies = () => ({
      numDimensions: {
        dependencyType: "stateVariable",
        variableName: "numDimensions"
      }
    });
    stateVariableDefinitions.unconstrainedVertices.returnArraySize = function({ dependencyValues }) {
      return [3, dependencyValues.numDimensions];
    };
    stateVariableDefinitions.unconstrainedVertices.arrayDefinitionByKey = function({ dependencyValuesByKey, arrayKeys }) {
      let unconstrainedVertices = {};
      let useEssential = {};
      for (let arrayKey of arrayKeys) {
        let [pointInd, dim] = arrayKey.split(",");
        let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
        let verticesAttr = dependencyValuesByKey[arrayKey].vertices;
        if (verticesAttr !== null && verticesAttr.stateValues["pointX" + varEnding]) {
          unconstrainedVertices[arrayKey] = verticesAttr.stateValues["pointX" + varEnding];
        } else {
          useEssential[arrayKey] = true;
        }
      }
      return {
        setValue: { unconstrainedVertices },
        useEssentialOrDefaultValue: {
          unconstrainedVertices: useEssential
        }
      };
    };
    stateVariableDefinitions.unconstrainedVertices.inverseArrayDefinitionByKey = async function({
      desiredStateVariableValues,
      dependencyValuesByKey,
      dependencyNamesByKey,
      initialChange,
      stateValues
    }) {
      if (initialChange && !await stateValues.draggable) {
        return { success: false };
      }
      let instructions = [];
      let essentialVertices = {};
      for (let arrayKey in desiredStateVariableValues.unconstrainedVertices) {
        let [pointInd, dim] = arrayKey.split(",");
        let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
        if (dependencyValuesByKey[arrayKey].vertices !== null && dependencyValuesByKey[arrayKey].vertices.stateValues["pointX" + varEnding]) {
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].vertices,
            desiredValue: desiredStateVariableValues.unconstrainedVertices[arrayKey],
            variableIndex: 0
          });
        } else {
          essentialVertices[arrayKey] = desiredStateVariableValues.unconstrainedVertices[arrayKey].simplify();
        }
        instructions.push({
          setDependency: dependencyNamesByKey[arrayKey].desiredUnconstrainedVertices,
          desiredValue: desiredStateVariableValues.unconstrainedVertices[arrayKey]
        });
      }
      instructions.push({
        setEssentialValue: "unconstrainedVertices",
        value: essentialVertices
      });
      return {
        success: true,
        instructions
      };
    };
    stateVariableDefinitions.numVertices = {
      description: "The number of vertices (always 3 for a triangle).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numVertices: 3 } })
    };
    return stateVariableDefinitions;
  }
}
class Rectangle extends Polygon {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      changeWidth: this.changeWidth.bind(this),
      changeHeight: this.changeHeight.bind(this),
      movePolygon: this.movePolygon.bind(this),
      moveRectangleCenter: this.moveRectangleCenter.bind(this)
    });
  }
  static componentType = "rectangle";
  static componentDocs = {
    summary: "A rectangle, potentially defined by width, height, center and/or corner vertices"
  };
  static rendererType = "polygon";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.addControls = {
      description: "Whether to render interactive control handles.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "centerWidthAndHeight",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "center",
          description: "Show a single control handle for moving the rectangle's center."
        },
        {
          value: "widthAndHeight",
          description: "Show control handles for resizing width and height."
        },
        {
          value: "centerWidthAndHeight",
          description: "Show control handles for both moving the center and resizing."
        },
        {
          value: "none",
          description: "Show no control handles."
        }
      ],
      valueForTrue: "centerWidthAndHeight",
      valueForFalse: "none"
    };
    attributes.center = {
      createComponentOfType: "point",
      description: "The rectangle's center point."
    };
    attributes.width = {
      createComponentOfType: "number",
      description: "The rectangle's width."
    };
    attributes.height = {
      createComponentOfType: "number",
      description: "The rectangle's height."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let styleDescriptionWithNounDef = stateVariableDefinitions.styleDescriptionWithNoun.definition;
    stateVariableDefinitions.styleDescriptionWithNoun.definition = function({ dependencyValues }) {
      let styleDescriptionWithNoun = styleDescriptionWithNounDef({
        dependencyValues
      }).setValue.styleDescriptionWithNoun;
      styleDescriptionWithNoun = styleDescriptionWithNoun.replaceAll(
        "polygon",
        "rectangle"
      );
      return { setValue: { styleDescriptionWithNoun } };
    };
    stateVariableDefinitions.numVerticesSpecified = {
      returnDependencies: () => ({
        verticesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "vertices",
          variableNames: ["numPoints"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.verticesAttr !== null) {
          return {
            setValue: {
              numVerticesSpecified: dependencyValues.verticesAttr.stateValues.numPoints
            }
          };
        } else {
          return { setValue: { numVerticesSpecified: 0 } };
        }
      }
    };
    stateVariableDefinitions.essentialVertex = {
      isArray: true,
      isLocation: true,
      entryPrefixes: ["essentialVertexX"],
      defaultValueByArrayKey: () => me$3.fromAst(0),
      hasEssential: true,
      returnArraySizeDependencies: () => ({
        numVerticesSpecified: {
          dependencyType: "stateVariable",
          variableName: "numVerticesSpecified"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numVerticesSpecified === 0 ? 2 : 0];
      },
      returnArrayDependenciesByKey() {
        return {};
      },
      arrayDefinitionByKey: function({ arrayKeys }) {
        let essentialVertex = {};
        for (let arrayKey of arrayKeys) {
          essentialVertex[arrayKey] = true;
        }
        return { useEssentialOrDefaultValue: { essentialVertex } };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.essentialVertex) {
          instructions.push({
            setEssentialValue: "essentialVertex",
            value: {
              [arrayKey]: desiredStateVariableValues.essentialVertex[arrayKey]
            }
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.haveSpecifiedCenter = {
      returnDependencies: () => ({
        centerAttr: {
          dependencyType: "attributeComponent",
          attributeName: "center"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          haveSpecifiedCenter: dependencyValues.centerAttr !== null
        }
      })
    };
    stateVariableDefinitions.specifiedCenter = {
      isArray: true,
      isLocation: true,
      entryPrefixes: ["specifiedCenterX"],
      returnArraySizeDependencies: () => ({
        haveSpecifiedCenter: {
          dependencyType: "stateVariable",
          variableName: "haveSpecifiedCenter"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.haveSpecifiedCenter ? 2 : 0];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            centerAttr: {
              dependencyType: "attributeComponent",
              attributeName: "center",
              variableNames: ["x" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        dependencyValuesByKey,
        arrayKeys
      }) {
        let specifiedCenter = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (dependencyValuesByKey[arrayKey].centerAttr !== null) {
            specifiedCenter[arrayKey] = dependencyValuesByKey[arrayKey].centerAttr.stateValues["x" + varEnding];
          }
        }
        return { setValue: { specifiedCenter } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.specifiedCenter) {
          if (dependencyValuesByKey[arrayKey].centerAttr && dependencyValuesByKey[arrayKey].centerAttr !== null) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].centerAttr,
              desiredValue: desiredStateVariableValues.specifiedCenter[arrayKey],
              variableIndex: 0
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.specifiedWidth = {
      defaultValue: 1,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "width",
      returnDependencies() {
        return {
          widthAttr: {
            dependencyType: "attributeComponent",
            attributeName: "width",
            variableNames: ["value"]
          },
          numVerticesSpecified: {
            dependencyType: "stateVariable",
            variableName: "numVerticesSpecified"
          },
          haveSpecifiedCenter: {
            dependencyType: "stateVariable",
            variableName: "haveSpecifiedCenter"
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.widthAttr !== null) {
          return {
            setValue: {
              specifiedWidth: dependencyValues.widthAttr.stateValues.value
            }
          };
        } else if ((dependencyValues.haveSpecifiedCenter ? 1 : 0) + dependencyValues.numVerticesSpecified <= 1) {
          return {
            useEssentialOrDefaultValue: { specifiedWidth: true }
          };
        }
        return { setValue: { specifiedWidth: null } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.widthAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "widthAttr",
                desiredValue: desiredStateVariableValues.specifiedWidth,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "specifiedWidth",
                value: desiredStateVariableValues.specifiedWidth
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.specifiedHeight = {
      defaultValue: 1,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "height",
      returnDependencies() {
        return {
          heightAttr: {
            dependencyType: "attributeComponent",
            attributeName: "height",
            variableNames: ["value"]
          },
          numVerticesSpecified: {
            dependencyType: "stateVariable",
            variableName: "numVerticesSpecified"
          },
          haveSpecifiedCenter: {
            dependencyType: "stateVariable",
            variableName: "haveSpecifiedCenter"
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.heightAttr !== null) {
          return {
            setValue: {
              specifiedHeight: dependencyValues.heightAttr.stateValues.value
            }
          };
        } else if ((dependencyValues.haveSpecifiedCenter ? 1 : 0) + dependencyValues.numVerticesSpecified <= 1) {
          return {
            useEssentialOrDefaultValue: { specifiedHeight: true }
          };
        }
        return { setValue: { specifiedHeight: null } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.heightAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "heightAttr",
                desiredValue: desiredStateVariableValues.specifiedHeight,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "specifiedHeight",
                value: desiredStateVariableValues.specifiedHeight
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.center = {
      description: "The rectangle's center coordinates.",
      public: true,
      isLocation: true,
      isArray: true,
      entryPrefixes: ["centerX"],
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "centerX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      returnArraySizeDependencies: () => ({}),
      returnArraySize: () => [2],
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            vertex0: {
              dependencyType: "stateVariable",
              variableName: "vertexX1_" + varEnding
            },
            vertex2: {
              dependencyType: "stateVariable",
              variableName: "vertexX3_" + varEnding
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let center = {};
        for (let arrayKey of arrayKeys) {
          let v0 = dependencyValuesByKey[arrayKey].vertex0;
          let v2 = dependencyValuesByKey[arrayKey].vertex2;
          center[arrayKey] = v0.add(v2).divide(2).simplify();
        }
        return { setValue: { center } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        stateValues
      }) {
        let instructions = [];
        let center = await stateValues.center;
        for (let arrayKey in desiredStateVariableValues.center) {
          let dim = Number(arrayKey);
          let v0 = dependencyValuesByKey[arrayKey].vertex0;
          let v2 = dependencyValuesByKey[arrayKey].vertex2;
          let offset = desiredStateVariableValues.center[dim].subtract(center[dim]);
          let desiredV0 = v0.add(offset).simplify();
          let desiredV2 = v2.add(offset).simplify();
          instructions.push(
            {
              setDependency: dependencyNamesByKey[arrayKey].vertex0,
              desiredValue: desiredV0
            },
            {
              setDependency: dependencyNamesByKey[arrayKey].vertex2,
              desiredValue: desiredV2
            }
          );
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.width = {
      description: "The rectangle's width.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies() {
        return {
          vertex0: {
            dependencyType: "stateVariable",
            variableName: "vertexX1_1"
          },
          vertex2: {
            dependencyType: "stateVariable",
            variableName: "vertexX3_1"
          }
        };
      },
      definition({ dependencyValues }) {
        let v0 = dependencyValues.vertex0.evaluate_to_constant();
        let v2 = dependencyValues.vertex2.evaluate_to_constant();
        let width = Math.abs(v0 - v2);
        return { setValue: { width } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let v0 = dependencyValues.vertex0.evaluate_to_constant();
        let v2 = dependencyValues.vertex2.evaluate_to_constant();
        let center = (v2 + v0) / 2;
        let widthSign = v2 - v0 < 0 ? -1 : 1;
        let offset = widthSign * Math.max(0, desiredStateVariableValues.width) / 2;
        let desiredV0 = me$3.fromAst(center - offset);
        let desiredV2 = me$3.fromAst(center + offset);
        return {
          success: true,
          instructions: [
            {
              setDependency: "vertex0",
              desiredValue: desiredV0
            },
            {
              setDependency: "vertex2",
              desiredValue: desiredV2
            }
          ]
        };
      }
    };
    stateVariableDefinitions.height = {
      description: "The rectangle's height.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies() {
        return {
          vertex0: {
            dependencyType: "stateVariable",
            variableName: "vertexX1_2"
          },
          vertex2: {
            dependencyType: "stateVariable",
            variableName: "vertexX3_2"
          }
        };
      },
      definition({ dependencyValues }) {
        let v0 = dependencyValues.vertex0.evaluate_to_constant();
        let v2 = dependencyValues.vertex2.evaluate_to_constant();
        let height = Math.abs(v0 - v2);
        return { setValue: { height } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let v0 = dependencyValues.vertex0.evaluate_to_constant();
        let v2 = dependencyValues.vertex2.evaluate_to_constant();
        let center = (v2 + v0) / 2;
        let heightSign = v2 - v0 < 0 ? -1 : 1;
        let offset = heightSign * Math.max(0, desiredStateVariableValues.height) / 2;
        let desiredV0 = me$3.fromAst(center - offset);
        let desiredV2 = me$3.fromAst(center + offset);
        return {
          success: true,
          instructions: [
            {
              setDependency: "vertex0",
              desiredValue: desiredV0
            },
            {
              setDependency: "vertex2",
              desiredValue: desiredV2
            }
          ]
        };
      }
    };
    stateVariableDefinitions.vertices = {
      public: true,
      description: "The four corner vertices of the rectangle.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "vertexX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      indexAliases: [[], ["x", "y", "z"]],
      entryPrefixes: ["vertexX", "vertex"],
      returnEntryDimensions: (prefix) => prefix === "vertex" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "vertexX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "vertices") {
          if (propIndex.length === 1) {
            return "vertex" + propIndex[0];
          } else {
            return `vertexX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 6) === "vertex") {
          let vertexNum = Number(varName.slice(6));
          if (Number.isInteger(vertexNum) && vertexNum > 0) {
            return `vertexX${vertexNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      stateVariablesDeterminingDependencies: [
        "numVerticesSpecified",
        "haveSpecifiedCenter"
      ],
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [4, 2];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          numVerticesSpecified: {
            dependencyType: "stateVariable",
            variableName: "numVerticesSpecified"
          }
        };
        if (stateValues.numVerticesSpecified === 0) {
          globalDependencies.haveSpecifiedCenter = {
            dependencyType: "stateVariable",
            variableName: "haveSpecifiedCenter"
          };
          if (stateValues.haveSpecifiedCenter) {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              dependenciesByKey[arrayKey] = {
                specifiedCenter: {
                  dependencyType: "stateVariable",
                  variableName: "specifiedCenterX" + (Number(dim) + 1)
                }
              };
              if (dim === "0") {
                dependenciesByKey[arrayKey].specifiedWidth = {
                  dependencyType: "stateVariable",
                  variableName: "specifiedWidth"
                };
              } else {
                dependenciesByKey[arrayKey].specifiedHeight = {
                  dependencyType: "stateVariable",
                  variableName: "specifiedHeight"
                };
              }
            }
          } else {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              dependenciesByKey[arrayKey] = {
                essentialVertex: {
                  dependencyType: "stateVariable",
                  variableName: "essentialVertexX" + (Number(dim) + 1)
                }
              };
              if (dim === "0") {
                if (vertexInd === "1" || vertexInd === "2") {
                  dependenciesByKey[arrayKey].specifiedWidth = {
                    dependencyType: "stateVariable",
                    variableName: "specifiedWidth"
                  };
                }
              } else {
                if (vertexInd === "2" || vertexInd === "3") {
                  dependenciesByKey[arrayKey].specifiedHeight = {
                    dependencyType: "stateVariable",
                    variableName: "specifiedHeight"
                  };
                }
              }
            }
          }
        } else if (stateValues.numVerticesSpecified === 1) {
          globalDependencies.haveSpecifiedCenter = {
            dependencyType: "stateVariable",
            variableName: "haveSpecifiedCenter"
          };
          if (stateValues.haveSpecifiedCenter) {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              let varEnding = "1_" + (Number(dim) + 1);
              dependenciesByKey[arrayKey] = {
                verticesAttr: {
                  dependencyType: "attributeComponent",
                  attributeName: "vertices",
                  variableNames: ["pointX" + varEnding]
                }
              };
              if (dim === "0" && vertexInd === "1" || vertexInd === "2" || dim === "1" && vertexInd === "2" || vertexInd === "3") {
                dependenciesByKey[arrayKey].specifiedCenter = {
                  dependencyType: "stateVariable",
                  variableName: "specifiedCenterX" + (Number(dim) + 1)
                };
              }
            }
          } else {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              let varEnding = "1_" + (Number(dim) + 1);
              dependenciesByKey[arrayKey] = {
                verticesAttr: {
                  dependencyType: "attributeComponent",
                  attributeName: "vertices",
                  variableNames: ["pointX" + varEnding]
                }
              };
              if (dim === "0") {
                if (vertexInd === "1" || vertexInd === "2")
                  dependenciesByKey[arrayKey].specifiedWidth = {
                    dependencyType: "stateVariable",
                    variableName: "specifiedWidth"
                  };
              } else {
                if (vertexInd === "2" || vertexInd === "3") {
                  dependenciesByKey[arrayKey].specifiedHeight = {
                    dependencyType: "stateVariable",
                    variableName: "specifiedHeight"
                  };
                }
              }
            }
          }
        } else {
          for (let arrayKey of arrayKeys) {
            let [vertexInd, dim] = arrayKey.split(",");
            let varEnding1 = "1_" + (Number(dim) + 1);
            let varEnding2 = "2_" + (Number(dim) + 1);
            let varEnding;
            if (vertexInd === "0") {
              varEnding = varEnding1;
            } else if (vertexInd === "2") {
              varEnding = varEnding2;
            } else if (vertexInd === "1") {
              if (dim === "0") {
                varEnding = varEnding2;
              } else {
                varEnding = varEnding1;
              }
            } else {
              if (dim === "0") {
                varEnding = varEnding1;
              } else {
                varEnding = varEnding2;
              }
            }
            dependenciesByKey[arrayKey] = {
              verticesAttr: {
                dependencyType: "attributeComponent",
                attributeName: "vertices",
                variableNames: ["pointX" + varEnding]
              }
            };
          }
        }
        return {
          dependenciesByKey,
          globalDependencies
        };
      },
      arrayDefinitionByKey({
        dependencyValuesByKey,
        arrayKeys,
        globalDependencyValues
      }) {
        let vertices = {};
        if (globalDependencyValues.numVerticesSpecified === 0) {
          if (globalDependencyValues.haveSpecifiedCenter) {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              let centerComponent = dependencyValuesByKey[arrayKey].specifiedCenter;
              if (dim === "0") {
                let width = dependencyValuesByKey[arrayKey].specifiedWidth;
                if (vertexInd === "0" || vertexInd === "3") {
                  vertices[arrayKey] = centerComponent.subtract(width / 2).simplify();
                } else {
                  vertices[arrayKey] = centerComponent.add(width / 2).simplify();
                }
              } else {
                let height = dependencyValuesByKey[arrayKey].specifiedHeight;
                if (vertexInd === "0" || vertexInd === "1") {
                  vertices[arrayKey] = centerComponent.subtract(height / 2).simplify();
                } else {
                  vertices[arrayKey] = centerComponent.add(height / 2).simplify();
                }
              }
            }
          } else {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              let vertComponent = dependencyValuesByKey[arrayKey].essentialVertex;
              if (dim === "0") {
                if (vertexInd === "0" || vertexInd === "3") {
                  vertices[arrayKey] = vertComponent;
                } else {
                  let width = dependencyValuesByKey[arrayKey].specifiedWidth;
                  vertices[arrayKey] = vertComponent.add(width).simplify();
                }
              } else {
                if (vertexInd === "0" || vertexInd === "1") {
                  vertices[arrayKey] = vertComponent;
                } else {
                  let height = dependencyValuesByKey[arrayKey].specifiedHeight;
                  vertices[arrayKey] = vertComponent.add(height).simplify();
                }
              }
            }
          }
        } else if (globalDependencyValues.numVerticesSpecified === 1) {
          if (globalDependencyValues.haveSpecifiedCenter) {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              let verticesAttr = dependencyValuesByKey[arrayKey].verticesAttr;
              let vertComponent;
              if (verticesAttr !== null && Object.keys(verticesAttr.stateValues).length === 1) {
                vertComponent = Object.values(
                  verticesAttr.stateValues
                )[0];
              } else {
                vertComponent = me$3.fromAst("＿");
              }
              if (dim === "0" && (vertexInd === "0" || vertexInd === "3") || dim === "1" && (vertexInd === "0" || vertexInd === "1")) {
                vertices[arrayKey] = vertComponent;
              } else {
                let centerComponent = dependencyValuesByKey[arrayKey].specifiedCenter;
                vertices[arrayKey] = vertComponent.add(
                  centerComponent.subtract(vertComponent).multiply(2).simplify()
                );
              }
            }
          } else {
            for (let arrayKey of arrayKeys) {
              let [vertexInd, dim] = arrayKey.split(",");
              let verticesAttr = dependencyValuesByKey[arrayKey].verticesAttr;
              let vertComponent;
              if (verticesAttr !== null && Object.keys(verticesAttr.stateValues).length === 1) {
                vertComponent = Object.values(
                  verticesAttr.stateValues
                )[0];
              } else {
                vertComponent = me$3.fromAst("＿");
              }
              if (dim === "0") {
                if (vertexInd === "0" || vertexInd === "3") {
                  vertices[arrayKey] = vertComponent;
                } else {
                  let width = dependencyValuesByKey[arrayKey].specifiedWidth;
                  vertices[arrayKey] = vertComponent.add(width).simplify();
                }
              } else {
                if (vertexInd === "0" || vertexInd === "1") {
                  vertices[arrayKey] = vertComponent;
                } else {
                  let height = dependencyValuesByKey[arrayKey].specifiedHeight;
                  vertices[arrayKey] = vertComponent.add(height).simplify();
                }
              }
            }
          }
        } else {
          for (let arrayKey of arrayKeys) {
            let verticesAttr = dependencyValuesByKey[arrayKey].verticesAttr;
            if (verticesAttr !== null && Object.keys(verticesAttr.stateValues).length === 1) {
              vertices[arrayKey] = Object.values(
                verticesAttr.stateValues
              )[0];
            } else {
              vertices[arrayKey] = me$3.fromAst("＿");
            }
          }
        }
        return { setValue: { vertices } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        globalDependencyValues,
        stateValues,
        workspace,
        initialChange,
        dependencyNamesByKey
      }) {
        if (!workspace.v0) {
          let vertices = await stateValues.vertices;
          workspace.v0 = [...vertices[0]];
          workspace.v2 = [...vertices[2]];
        }
        let keyX, keyY, keyV0X, keyV0Y, keyV2X, keyV2Y;
        for (let arrayKey in desiredStateVariableValues.vertices) {
          let [vertexInd, dim] = arrayKey.split(",");
          let desiredValue = desiredStateVariableValues.vertices[arrayKey];
          if (vertexInd === "0") {
            workspace.v0[Number(dim)] = desiredValue;
          } else if (vertexInd === "2") {
            workspace.v2[Number(dim)] = desiredValue;
          } else if (vertexInd === "1") {
            if (dim === "0") {
              workspace.v2[Number(dim)] = desiredValue;
            } else {
              workspace.v0[Number(dim)] = desiredValue;
            }
          } else {
            if (dim === "0") {
              workspace.v0[Number(dim)] = desiredValue;
            } else {
              workspace.v2[Number(dim)] = desiredValue;
            }
          }
          if (dim === "0") {
            keyX = arrayKey;
            if (vertexInd === "0" || vertexInd === "3") {
              keyV0X = arrayKey;
            } else {
              keyV2X = arrayKey;
            }
          } else {
            keyY = arrayKey;
            if (vertexInd === "0" || vertexInd === "1") {
              keyV0Y = arrayKey;
            } else {
              keyV2Y = arrayKey;
            }
          }
        }
        let instructions = [];
        if (globalDependencyValues.numVerticesSpecified === 0) {
          if (globalDependencyValues.haveSpecifiedCenter) {
            if (keyX !== void 0) {
              let width = workspace.v2[0].subtract(workspace.v0[0]).evaluate_to_constant();
              let center = workspace.v2[0].add(workspace.v0[0]).divide(2).simplify();
              instructions.push(
                {
                  setDependency: dependencyNamesByKey[keyX].specifiedWidth,
                  desiredValue: width
                },
                {
                  setDependency: dependencyNamesByKey[keyX].specifiedCenter,
                  desiredValue: center
                }
              );
            }
            if (keyY !== void 0) {
              let height = workspace.v2[1].subtract(workspace.v0[1]).evaluate_to_constant();
              let center = workspace.v2[1].add(workspace.v0[1]).divide(2).simplify();
              instructions.push(
                {
                  setDependency: dependencyNamesByKey[keyY].specifiedHeight,
                  desiredValue: height
                },
                {
                  setDependency: dependencyNamesByKey[keyY].specifiedCenter,
                  desiredValue: center
                }
              );
            }
          } else {
            if (keyV0X !== void 0) {
              let vert = workspace.v0[0].simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV0X].essentialVertex,
                desiredValue: vert
              });
            }
            if (keyV2X !== void 0) {
              let width = workspace.v2[0].subtract(workspace.v0[0]).evaluate_to_constant();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV2X].specifiedWidth,
                desiredValue: width
              });
            }
            if (keyV0Y !== void 0) {
              let vert = workspace.v0[1].simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV0Y].essentialVertex,
                desiredValue: vert
              });
            }
            if (keyV2Y !== void 0) {
              let height = workspace.v2[1].subtract(workspace.v0[1]).evaluate_to_constant();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV2Y].specifiedHeight,
                desiredValue: height
              });
            }
          }
        } else if (globalDependencyValues.numVerticesSpecified === 1) {
          if (globalDependencyValues.haveSpecifiedCenter) {
            if (keyV0X !== void 0) {
              let vert = workspace.v0[0].simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV0X].verticesAttr,
                desiredValue: vert,
                variableIndex: 0
              });
            }
            if (keyV2X !== void 0) {
              let center = workspace.v2[0].add(workspace.v0[0]).divide(2).simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV2X].specifiedCenter,
                desiredValue: center
              });
            }
            if (keyV0Y !== void 0) {
              let vert = workspace.v0[1].simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV0Y].verticesAttr,
                desiredValue: vert,
                variableIndex: 0
              });
            }
            if (keyV2Y !== void 0) {
              let center = workspace.v2[1].add(workspace.v0[1]).divide(2).simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV2Y].specifiedCenter,
                desiredValue: center
              });
            }
          } else {
            if (keyV0X !== void 0) {
              let vert = workspace.v0[0].simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV0X].verticesAttr,
                desiredValue: vert,
                variableIndex: 0
              });
            }
            if (keyV2X !== void 0) {
              let width = workspace.v2[0].subtract(workspace.v0[0]).evaluate_to_constant();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV2X].specifiedWidth,
                desiredValue: width
              });
            }
            if (keyV0Y !== void 0) {
              let vert = workspace.v0[1].simplify();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV0Y].verticesAttr,
                desiredValue: vert,
                variableIndex: 0
              });
            }
            if (keyV2Y !== void 0) {
              let height = workspace.v2[1].subtract(workspace.v0[1]).evaluate_to_constant();
              instructions.push({
                setDependency: dependencyNamesByKey[keyV2Y].specifiedHeight,
                desiredValue: height
              });
            }
          }
        } else {
          if (keyV0X !== void 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[keyV0X].verticesAttr,
              desiredValue: workspace.v0[0],
              variableIndex: 0
            });
          }
          if (keyV2X !== void 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[keyV2X].verticesAttr,
              desiredValue: workspace.v2[0],
              variableIndex: 0
            });
          }
          if (keyV0Y !== void 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[keyV0Y].verticesAttr,
              desiredValue: workspace.v0[1],
              variableIndex: 0
            });
          }
          if (keyV2Y !== void 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[keyV2Y].verticesAttr,
              desiredValue: workspace.v2[1],
              variableIndex: 0
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numVertices = {
      description: "The number of vertices (always 4 for a rectangle).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numVertices: 4 } })
    };
    return stateVariableDefinitions;
  }
  async moveRectangleCenter({
    center,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false,
    pointRole = "rectangle"
  }) {
    if (!transient) {
      skippable = false;
    }
    if (pointRole !== "rectangle") {
      console.warn(`Invalid pointRole for rectangle: ${pointRole}`);
      return;
    }
    if (!Array.isArray(center) || center.length !== 2) {
      return;
    }
    if (!center.every((x2) => Number.isFinite(x2))) {
      console.warn(
        `Invalid center coordinates for ${pointRole} move: ${center.join(", ")}`
      );
      return;
    }
    if (!await this.stateValues.draggable) {
      return;
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "center",
        value: center.map((x2) => me$3.fromAst(x2)),
        sourceDetails
      }
    ];
    const performUpdateArgs = {
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate
    };
    if (transient) {
      performUpdateArgs.transient = true;
      performUpdateArgs.skippable = skippable;
    } else {
      performUpdateArgs.event = {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          center
        }
      };
    }
    return await this.coreFunctions.performUpdate(performUpdateArgs);
  }
  async changeWidth({
    width,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!transient) {
      skippable = false;
    }
    if (!Number.isFinite(width)) {
      console.warn(`Invalid width for rectangle change: width=${width}`);
      return;
    }
    if (!await this.stateValues.verticesDraggable) {
      return;
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "width",
        value: Math.max(0, width),
        sourceDetails
      }
    ];
    const performUpdateArgs = {
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate
    };
    if (transient) {
      performUpdateArgs.transient = true;
      performUpdateArgs.skippable = skippable;
    } else {
      performUpdateArgs.event = {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          width
        }
      };
    }
    return await this.coreFunctions.performUpdate(performUpdateArgs);
  }
  async changeHeight({
    height,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!transient) {
      skippable = false;
    }
    if (!Number.isFinite(height)) {
      console.warn(
        `Invalid height for rectangle change: height=${height}`
      );
      return;
    }
    if (!await this.stateValues.verticesDraggable) {
      return;
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "height",
        value: Math.max(0, height),
        sourceDetails
      }
    ];
    const performUpdateArgs = {
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate
    };
    if (transient) {
      performUpdateArgs.transient = true;
      performUpdateArgs.skippable = skippable;
    } else {
      performUpdateArgs.event = {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          height
        }
      };
    }
    return await this.coreFunctions.performUpdate(performUpdateArgs);
  }
  async movePolygon({
    pointCoords,
    transient,
    sourceDetails,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let numVerticesMoved = Object.keys(pointCoords).length;
    if (numVerticesMoved === 1) {
      if (!await this.stateValues.verticesDraggable) {
        return;
      }
    } else {
      if (!await this.stateValues.draggable) {
        return;
      }
    }
    let updateInstructions = [];
    let vertexComponents = {};
    for (let ind in pointCoords) {
      vertexComponents[ind + ",0"] = me$3.fromAst(pointCoords[ind][0]);
      vertexComponents[ind + ",1"] = me$3.fromAst(pointCoords[ind][1]);
    }
    updateInstructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "vertices",
      value: vertexComponents,
      sourceDetails
    });
    if (numVerticesMoved === 1) {
      let ind = Number(Object.keys(pointCoords)[0]);
      let vertexX = me$3.fromAst(pointCoords[ind][0]);
      let vertexY = me$3.fromAst(pointCoords[ind][1]);
      let oppositeInd = (ind + 2) % 4;
      let vertices = await this.stateValues.vertices;
      let oppositeX = vertices[oppositeInd][0];
      let oppositeY = vertices[oppositeInd][1];
      if (await this.stateValues.numVerticesSpecified < 2) {
        if (await this.stateValues.haveSpecifiedCenter) {
          let centerX, centerY;
          if (ind === 0) {
            centerX = vertexX.add(oppositeX).divide(2);
            centerY = vertexY.add(oppositeY).divide(2);
          } else if (ind === 1) {
            centerY = vertexY.add(oppositeY).divide(2);
          } else if (ind === 3) {
            centerX = vertexX.add(oppositeX).divide(2);
          }
          if (centerX !== void 0) {
            updateInstructions.push({
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "specifiedCenter",
              value: { 0: centerX.simplify() }
            });
          }
          if (centerY !== void 0) {
            updateInstructions.push({
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "specifiedCenter",
              value: { 1: centerY.simplify() }
            });
          }
        } else {
          let width, height;
          if (ind === 0) {
            width = oppositeX.subtract(vertexX);
            height = oppositeY.subtract(vertexY);
          } else if (ind === 1) {
            height = oppositeY.subtract(vertexY);
          } else if (ind === 3) {
            width = oppositeX.subtract(vertexX);
          }
          if (width !== void 0) {
            updateInstructions.push({
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "specifiedWidth",
              value: width.simplify()
            });
          }
          if (height !== void 0) {
            updateInstructions.push({
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "specifiedHeight",
              value: height.simplify()
            });
          }
        }
      }
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        transient,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            pointCoordinates: pointCoords
          }
        }
      });
    }
    if (numVerticesMoved > 1) {
      let numVerticesSpecified = await this.stateValues.numVerticesSpecified;
      if (numVerticesSpecified > 1 || numVerticesSpecified === 1 && await this.stateValues.haveSpecifiedCenter) {
        let resultingNumericalVertices = await this.stateValues.numericalVertices;
        let numericalPoints, resultingNumericalPoints;
        if (numVerticesSpecified > 1) {
          numericalPoints = [pointCoords[0], pointCoords[2]];
          resultingNumericalPoints = [
            resultingNumericalVertices[0],
            resultingNumericalVertices[2]
          ];
        } else {
          let numericalCenter = [
            (pointCoords[0][0] + pointCoords[2][0]) / 2,
            (pointCoords[0][1] + pointCoords[2][1]) / 2
          ];
          let resultingNumericalCenter = [
            (resultingNumericalVertices[0][0] + resultingNumericalVertices[2][0]) / 2,
            (resultingNumericalVertices[0][1] + resultingNumericalVertices[2][1]) / 2
          ];
          numericalPoints = [numericalCenter, pointCoords[0]];
          resultingNumericalPoints = [
            resultingNumericalCenter,
            resultingNumericalVertices[0]
          ];
        }
        let pointsChanged = [];
        let numPointsChanged = 0;
        for (let [ind, pt2] of numericalPoints.entries()) {
          if (!pt2.every(
            (v2, i2) => v2 === resultingNumericalPoints[ind][i2]
          )) {
            pointsChanged.push(ind);
            numPointsChanged++;
          }
        }
        if (numPointsChanged === 1) {
          let changedInd = pointsChanged[0];
          let orig1 = numericalPoints[changedInd];
          let changed1 = resultingNumericalPoints[changedInd];
          let changevec1 = orig1.map((v2, i2) => v2 - changed1[i2]);
          let newNumericalVertices = [];
          for (let i2 = 0; i2 < 4; i2++) {
            newNumericalVertices.push(
              pointCoords[i2].map((v2, j2) => v2 - changevec1[j2])
            );
          }
          let newVertexComponents = {};
          for (let ind in newNumericalVertices) {
            newVertexComponents[ind + ",0"] = me$3.fromAst(
              newNumericalVertices[ind][0]
            );
            newVertexComponents[ind + ",1"] = me$3.fromAst(
              newNumericalVertices[ind][1]
            );
          }
          let newInstructions = [
            {
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "vertices",
              value: newVertexComponents
            }
          ];
          return await this.coreFunctions.performUpdate({
            updateInstructions: newInstructions,
            transient,
            actionId,
            sourceInformation,
            skipRendererUpdate
          });
        }
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
class RegularPolygon extends Polygon {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      movePolygonCenter: this.movePolygonCenter.bind(this),
      changeRadius: this.changeRadius.bind(this),
      movePolygon: this.movePolygon.bind(this)
    });
  }
  static componentType = "regularPolygon";
  static componentDocs = {
    summary: "A regular polygon with a given number of sides"
  };
  static rendererType = "polygon";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.preserveSimilarity;
    attributes.numVertices = {
      createComponentOfType: "integer",
      description: "Number of vertices in the regular polygon."
    };
    attributes.numSides = {
      createComponentOfType: "integer",
      description: "Number of sides in the regular polygon (alias for numVertices)."
    };
    attributes.center = {
      createComponentOfType: "point",
      description: "Center of the regular polygon."
    };
    attributes.circumradius = {
      createComponentOfType: "number",
      description: "Distance from center to a vertex (circumscribed-circle radius)."
    };
    attributes.radius = {
      createComponentOfType: "number",
      description: "Alias for circumradius."
    };
    attributes.inradius = {
      createComponentOfType: "number",
      description: "Distance from center to the midpoint of a side (inscribed-circle radius)."
    };
    attributes.apothem = {
      createComponentOfType: "number",
      description: "Alias for inradius."
    };
    attributes.sideLength = {
      createComponentOfType: "number",
      description: "Length of each side of the regular polygon."
    };
    attributes.perimeter = {
      createComponentOfType: "number",
      description: "Total perimeter of the regular polygon."
    };
    attributes.area = {
      createComponentOfType: "number",
      description: "Total area enclosed by the regular polygon."
    };
    attributes.addControls = {
      description: "Whether to render interactive control handles.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "centerAndRadius",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "center",
          description: "Show a control handle for moving the polygon's center."
        },
        {
          value: "radius",
          description: "Show a control handle for resizing the polygon's radius."
        },
        {
          value: "centerAndRadius",
          description: "Show control handles for both moving the center and resizing the radius."
        },
        {
          value: "none",
          description: "Show no control handles."
        }
      ],
      valueForTrue: "centerAndRadius",
      valueForFalse: "none"
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let styleDescriptionWithNounDeps = stateVariableDefinitions.styleDescriptionWithNoun.returnDependencies();
    styleDescriptionWithNounDeps.numSides = {
      dependencyType: "stateVariable",
      variableName: "numSides"
    };
    stateVariableDefinitions.styleDescriptionWithNoun.returnDependencies = () => styleDescriptionWithNounDeps;
    let styleDescriptionWithNounDef = stateVariableDefinitions.styleDescriptionWithNoun.definition;
    stateVariableDefinitions.styleDescriptionWithNoun.definition = function({ dependencyValues }) {
      let styleDescriptionWithNoun = styleDescriptionWithNounDef({
        dependencyValues
      }).setValue.styleDescriptionWithNoun;
      styleDescriptionWithNoun = styleDescriptionWithNoun.replaceAll(
        "polygon",
        `${dependencyValues.numSides}-sided regular polygon`
      );
      return { setValue: { styleDescriptionWithNoun } };
    };
    stateVariableDefinitions.preserveSimilarity = {
      description: "Whether to preserve similarity when dragging.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { preserveSimilarity: true } })
    };
    stateVariableDefinitions.numVertices = {
      description: "The number of vertices (sides) of the regular polygon.",
      isLocation: true,
      hasEssential: true,
      defaultValue: 3,
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        numVerticesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "numVertices",
          variableNames: ["value"]
        },
        numSidesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "numSides",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.numVerticesAttr) {
          return {
            setValue: {
              numVertices: dependencyValues.numVerticesAttr.stateValues.value
            }
          };
        } else if (dependencyValues.numSidesAttr) {
          return {
            setValue: {
              numVertices: dependencyValues.numSidesAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { numVertices: true }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.numVerticesAttr) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "numVerticesAttr",
                desiredValue: desiredStateVariableValues.numVertices,
                variableIndex: 0
              }
            ]
          };
        } else if (dependencyValues.numSidesAttr) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "numSidesAttr",
                desiredValue: desiredStateVariableValues.numVertices,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "numVertices",
                value: desiredStateVariableValues.numVertices
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.numSides = {
      isAlias: true,
      targetVariableName: "numVertices",
      description: "The number of sides of the regular polygon."
    };
    stateVariableDefinitions.numVerticesSpecified = {
      returnDependencies: () => ({
        verticesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "vertices",
          variableNames: ["numPoints"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.verticesAttr !== null) {
          return {
            setValue: {
              numVerticesSpecified: dependencyValues.verticesAttr.stateValues.numPoints
            }
          };
        } else {
          return { setValue: { numVerticesSpecified: 0 } };
        }
      }
    };
    stateVariableDefinitions.essentialDirection = {
      isArray: true,
      isLocation: true,
      entryPrefixes: ["essentialVertexX"],
      defaultValueByArrayKey: () => 0,
      hasEssential: true,
      returnArraySizeDependencies: () => ({
        numVerticesSpecified: {
          dependencyType: "stateVariable",
          variableName: "numVerticesSpecified"
        },
        haveSpecifiedCenter: {
          dependencyType: "stateVariable",
          variableName: "haveSpecifiedCenter"
        }
      }),
      returnArraySize({ dependencyValues }) {
        let needDir = (dependencyValues.haveSpecifiedCenter ? 1 : 0) + dependencyValues.numVerticesSpecified <= 1;
        return [needDir ? 2 : 0];
      },
      returnArrayDependenciesByKey() {
        return {};
      },
      arrayDefinitionByKey: function({ arrayKeys }) {
        let essentialDirection = {};
        for (let arrayKey of arrayKeys) {
          if (arrayKey === "0") {
            essentialDirection[arrayKey] = { defaultValue: 1 };
          } else {
            essentialDirection[arrayKey] = true;
          }
        }
        return { useEssentialOrDefaultValue: { essentialDirection } };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.essentialDirection) {
          instructions.push({
            setEssentialValue: "essentialDirection",
            value: {
              [arrayKey]: desiredStateVariableValues.essentialDirection[arrayKey]
            }
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.haveSpecifiedCenter = {
      returnDependencies: () => ({
        centerAttr: {
          dependencyType: "attributeComponent",
          attributeName: "center"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          haveSpecifiedCenter: dependencyValues.centerAttr !== null
        }
      })
    };
    stateVariableDefinitions.specifiedCenter = {
      isArray: true,
      isLocation: true,
      entryPrefixes: ["specifiedCenterX"],
      returnArraySizeDependencies: () => ({
        haveSpecifiedCenter: {
          dependencyType: "stateVariable",
          variableName: "haveSpecifiedCenter"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.haveSpecifiedCenter ? 2 : 0];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            centerAttr: {
              dependencyType: "attributeComponent",
              attributeName: "center",
              variableNames: ["x" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        dependencyValuesByKey,
        arrayKeys
      }) {
        let specifiedCenter = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (dependencyValuesByKey[arrayKey].centerAttr !== null) {
            specifiedCenter[arrayKey] = dependencyValuesByKey[arrayKey].centerAttr.stateValues["x" + varEnding].evaluate_to_constant();
          }
        }
        return { setValue: { specifiedCenter } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.specifiedCenter) {
          if (dependencyValuesByKey[arrayKey].centerAttr && dependencyValuesByKey[arrayKey].centerAttr !== null) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].centerAttr,
              desiredValue: me$3.fromAst(
                desiredStateVariableValues.specifiedCenter[arrayKey]
              ),
              variableIndex: 0
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.essentialCenter = {
      isArray: true,
      isLocation: true,
      entryPrefixes: ["essentialCenterX"],
      defaultValueByArrayKey: () => 0,
      hasEssential: true,
      returnArraySizeDependencies: () => ({
        haveSpecifiedCenter: {
          dependencyType: "stateVariable",
          variableName: "haveSpecifiedCenter"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.haveSpecifiedCenter ? 0 : 2];
      },
      returnArrayDependenciesByKey() {
        return {};
      },
      arrayDefinitionByKey: function({ arrayKeys }) {
        let essentialCenter = {};
        for (let arrayKey of arrayKeys) {
          essentialCenter[arrayKey] = true;
        }
        return { useEssentialOrDefaultValue: { essentialCenter } };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.essentialCenter) {
          instructions.push({
            setEssentialValue: "essentialCenter",
            value: {
              [arrayKey]: desiredStateVariableValues.essentialCenter[arrayKey]
            }
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.specifiedCircumradius = {
      isLocation: true,
      returnDependencies() {
        return {
          circumradiusAttr: {
            dependencyType: "attributeComponent",
            attributeName: "circumradius",
            variableNames: ["value"]
          },
          radiusAttr: {
            dependencyType: "attributeComponent",
            attributeName: "radius",
            variableNames: ["value"]
          },
          numVerticesSpecified: {
            dependencyType: "stateVariable",
            variableName: "numVerticesSpecified"
          },
          haveSpecifiedCenter: {
            dependencyType: "stateVariable",
            variableName: "haveSpecifiedCenter"
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.circumradiusAttr !== null) {
          return {
            setValue: {
              specifiedCircumradius: dependencyValues.circumradiusAttr.stateValues.value
            }
          };
        } else if (dependencyValues.radiusAttr !== null) {
          return {
            setValue: {
              specifiedCircumradius: dependencyValues.radiusAttr.stateValues.value
            }
          };
        } else {
          return { setValue: { specifiedCircumradius: null } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.circumradiusAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "circumradiusAttr",
                desiredValue: desiredStateVariableValues.specifiedCircumradius,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else if (dependencyValues.radiusAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "radiusAttr",
                desiredValue: desiredStateVariableValues.specifiedCircumradius,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.specifiedInradius = {
      isLocation: true,
      returnDependencies() {
        return {
          inradiusAttr: {
            dependencyType: "attributeComponent",
            attributeName: "inradius",
            variableNames: ["value"]
          },
          apothemAttr: {
            dependencyType: "attributeComponent",
            attributeName: "apothem",
            variableNames: ["value"]
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.inradiusAttr !== null) {
          return {
            setValue: {
              specifiedInradius: dependencyValues.inradiusAttr.stateValues.value
            }
          };
        } else if (dependencyValues.apothemAttr !== null) {
          return {
            setValue: {
              specifiedInradius: dependencyValues.apothemAttr.stateValues.value
            }
          };
        } else {
          return { setValue: { specifiedInradius: null } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.inradiusAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "inradiusAttr",
                desiredValue: desiredStateVariableValues.specifiedInradius,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else if (dependencyValues.apothemAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "apothemAttr",
                desiredValue: desiredStateVariableValues.specifiedInradius,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { sucess: false };
        }
      }
    };
    stateVariableDefinitions.specifiedSideLength = {
      isLocation: true,
      returnDependencies() {
        return {
          sideLengthAttr: {
            dependencyType: "attributeComponent",
            attributeName: "sideLength",
            variableNames: ["value"]
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.sideLengthAttr !== null) {
          return {
            setValue: {
              specifiedSideLength: dependencyValues.sideLengthAttr.stateValues.value
            }
          };
        } else {
          return { setValue: { specifiedSideLength: null } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.sideLengthAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "sideLengthAttr",
                desiredValue: desiredStateVariableValues.specifiedSideLength,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { sucess: false };
        }
      }
    };
    stateVariableDefinitions.specifiedPerimeter = {
      isLocation: true,
      returnDependencies() {
        return {
          perimeterAttr: {
            dependencyType: "attributeComponent",
            attributeName: "perimeter",
            variableNames: ["value"]
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.perimeterAttr !== null) {
          return {
            setValue: {
              specifiedPerimeter: dependencyValues.perimeterAttr.stateValues.value
            }
          };
        } else {
          return { setValue: { specifiedPerimeter: null } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.perimeterAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "perimeterAttr",
                desiredValue: desiredStateVariableValues.specifiedPerimeter,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { sucess: false };
        }
      }
    };
    stateVariableDefinitions.specifiedArea = {
      isLocation: true,
      returnDependencies() {
        return {
          areaAttr: {
            dependencyType: "attributeComponent",
            attributeName: "area",
            variableNames: ["value"]
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.areaAttr !== null) {
          return {
            setValue: {
              specifiedArea: dependencyValues.areaAttr.stateValues.value
            }
          };
        } else {
          return { setValue: { specifiedArea: null } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.areaAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "areaAttr",
                desiredValue: desiredStateVariableValues.specifiedArea,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { sucess: false };
        }
      }
    };
    stateVariableDefinitions.essentialCircumradius = {
      isLocation: true,
      hasEssential: true,
      defaultValue: 1,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { essentialCircumradius: true }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "essentialCircumradius",
              value: desiredStateVariableValues.essentialCircumradius
            }
          ]
        };
      }
    };
    stateVariableDefinitions.unconstrainedCenterComponents = {
      isLocation: true,
      additionalStateVariablesDefined: [
        "unconstrainedDirectionWithRadius"
      ],
      returnDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        },
        numVerticesSpecified: {
          dependencyType: "stateVariable",
          variableName: "numVerticesSpecified"
        },
        haveSpecifiedCenter: {
          dependencyType: "stateVariable",
          variableName: "haveSpecifiedCenter"
        },
        specifiedCircumradius: {
          dependencyType: "stateVariable",
          variableName: "specifiedCircumradius"
        },
        specifiedInradius: {
          dependencyType: "stateVariable",
          variableName: "specifiedInradius"
        },
        specifiedSideLength: {
          dependencyType: "stateVariable",
          variableName: "specifiedSideLength"
        },
        specifiedPerimeter: {
          dependencyType: "stateVariable",
          variableName: "specifiedPerimeter"
        },
        specifiedArea: {
          dependencyType: "stateVariable",
          variableName: "specifiedArea"
        },
        essentialCircumradius: {
          dependencyType: "stateVariable",
          variableName: "essentialCircumradius"
        },
        essentialDirection: {
          dependencyType: "stateVariable",
          variableName: "essentialDirection"
        },
        verticesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "vertices",
          variableNames: ["points"]
        },
        specifiedCenter: {
          dependencyType: "stateVariable",
          variableName: "specifiedCenter"
        },
        essentialCenter: {
          dependencyType: "stateVariable",
          variableName: "essentialCenter"
        }
      }),
      definition({ dependencyValues }) {
        let numVertices = dependencyValues.numVertices;
        let center;
        let unconstrainedDirectionWithRadius;
        if (dependencyValues.numVerticesSpecified === 0) {
          if (dependencyValues.haveSpecifiedCenter) {
            center = dependencyValues.specifiedCenter;
          } else {
            center = dependencyValues.essentialCenter;
          }
          let circumradius;
          if (dependencyValues.specifiedCircumradius !== null) {
            circumradius = dependencyValues.specifiedCircumradius;
          } else if (dependencyValues.specifiedInradius !== null) {
            circumradius = dependencyValues.specifiedInradius / Math.cos(Math.PI / numVertices);
          } else if (dependencyValues.specifiedSideLength !== null) {
            circumradius = dependencyValues.specifiedSideLength / (2 * Math.sin(Math.PI / numVertices));
          } else if (dependencyValues.specifiedPerimeter !== null) {
            circumradius = dependencyValues.specifiedPerimeter / (2 * numVertices * Math.sin(Math.PI / numVertices));
          } else if (dependencyValues.specifiedArea !== null) {
            circumradius = Math.sqrt(
              dependencyValues.specifiedArea / (numVertices / 2 * Math.sin(2 * Math.PI / numVertices))
            );
          } else {
            circumradius = dependencyValues.essentialCircumradius;
          }
          unconstrainedDirectionWithRadius = dependencyValues.essentialDirection.map(
            (x2) => x2 * circumradius
          );
        } else if (dependencyValues.haveSpecifiedCenter) {
          center = dependencyValues.specifiedCenter;
          let vertex = dependencyValues.verticesAttr.stateValues.points[0].map(
            (x2) => x2.evaluate_to_constant()
          );
          unconstrainedDirectionWithRadius = [
            vertex[0] - center[0],
            vertex[1] - center[1]
          ];
        } else if (dependencyValues.numVerticesSpecified === 1) {
          let circumradius;
          if (dependencyValues.specifiedCircumradius !== null) {
            circumradius = dependencyValues.specifiedCircumradius;
          } else if (dependencyValues.specifiedInradius !== null) {
            circumradius = dependencyValues.specifiedInradius / Math.cos(Math.PI / numVertices);
          } else if (dependencyValues.specifiedSideLength !== null) {
            circumradius = dependencyValues.specifiedSideLength / (2 * Math.sin(Math.PI / numVertices));
          } else if (dependencyValues.specifiedPerimeter !== null) {
            circumradius = dependencyValues.specifiedPerimeter / (2 * numVertices * Math.sin(Math.PI / numVertices));
          } else if (dependencyValues.specifiedArea !== null) {
            circumradius = Math.sqrt(
              dependencyValues.specifiedArea / (numVertices / 2 * Math.sin(2 * Math.PI / numVertices))
            );
          } else {
            circumradius = dependencyValues.essentialCircumradius;
          }
          unconstrainedDirectionWithRadius = dependencyValues.essentialDirection.map(
            (x2) => x2 * circumradius
          );
          let vertex = dependencyValues.verticesAttr.stateValues.points[0].map(
            (x2) => x2.evaluate_to_constant()
          );
          center = [
            vertex[0] - unconstrainedDirectionWithRadius[0],
            vertex[1] - unconstrainedDirectionWithRadius[1]
          ];
        } else {
          let vertex1 = dependencyValues.verticesAttr.stateValues.points[0].map(
            (x2) => x2.evaluate_to_constant()
          );
          let vertex2 = dependencyValues.verticesAttr.stateValues.points[1].map(
            (x2) => x2.evaluate_to_constant()
          );
          let sideVector = [
            vertex2[0] - vertex1[0],
            vertex2[1] - vertex1[1]
          ];
          let midpoint = [
            (vertex1[0] + vertex2[0]) / 2,
            (vertex1[1] + vertex2[1]) / 2
          ];
          let sideLength = Math.sqrt(
            sideVector[0] ** 2 + sideVector[1] ** 2
          );
          let inradius = sideLength / (2 * Math.tan(Math.PI / numVertices));
          let inradiusDirection = [
            -sideVector[1] / sideLength,
            sideVector[0] / sideLength
          ];
          center = [
            midpoint[0] + inradiusDirection[0] * inradius,
            midpoint[1] + inradiusDirection[1] * inradius
          ];
          unconstrainedDirectionWithRadius = [
            vertex1[0] - center[0],
            vertex1[1] - center[1]
          ];
        }
        return {
          setValue: {
            unconstrainedCenterComponents: center,
            unconstrainedDirectionWithRadius
          }
        };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        workspace,
        stateValues
      }) {
        let numVertices = dependencyValues.numVertices;
        let instructions = [];
        let desiredCenter = desiredStateVariableValues.unconstrainedCenterComponents;
        if (!desiredCenter) {
          desiredCenter = workspace.desiredCenter;
        }
        if (!desiredCenter) {
          desiredCenter = (await stateValues.center).map(
            (x2) => x2.evaluate_to_constant()
          );
        }
        let desiredDirectionWithRadius = desiredStateVariableValues.unconstrainedDirectionWithRadius;
        if (!desiredDirectionWithRadius) {
          desiredDirectionWithRadius = workspace.desiredDirectionWithRadius;
        }
        if (!desiredDirectionWithRadius) {
          let center = (await stateValues.center).map(
            (x2) => x2.evaluate_to_constant()
          );
          let vertex1 = (await stateValues.vertices)[0].map(
            (x2) => x2.evaluate_to_constant()
          );
          desiredDirectionWithRadius = [
            vertex1[0] - center[0],
            vertex1[1] - center[1]
          ];
        }
        workspace.desiredCenter = desiredCenter;
        workspace.desiredDirectionWithRadius = desiredDirectionWithRadius;
        if (dependencyValues.numVerticesSpecified === 0) {
          if (dependencyValues.haveSpecifiedCenter) {
            instructions.push({
              setDependency: "specifiedCenter",
              desiredValue: desiredCenter
            });
          } else {
            instructions.push({
              setDependency: "essentialCenter",
              desiredValue: desiredCenter
            });
          }
          let desiredCircumradius = Math.sqrt(
            desiredDirectionWithRadius[0] ** 2 + desiredDirectionWithRadius[1] ** 2
          );
          let desiredDirection = desiredDirectionWithRadius.map(
            (x2) => x2 / desiredCircumradius
          );
          if (dependencyValues.specifiedCircumradius !== null) {
            instructions.push({
              setDependency: "specifiedCircumradius",
              desiredValue: desiredCircumradius
            });
          } else if (dependencyValues.specifiedInradius !== null) {
            instructions.push({
              setDependency: "specifiedInradius",
              desiredValue: desiredCircumradius * Math.cos(Math.PI / numVertices)
            });
          } else if (dependencyValues.specifiedSideLength !== null) {
            instructions.push({
              setDependency: "specifiedSideLength",
              desiredValue: desiredCircumradius * (2 * Math.sin(Math.PI / numVertices))
            });
          } else if (dependencyValues.specifiedPerimeter !== null) {
            instructions.push({
              setDependency: "specifiedPerimeter",
              desiredValue: desiredCircumradius * (2 * numVertices * Math.sin(Math.PI / numVertices))
            });
          } else if (dependencyValues.specifiedArea !== null) {
            instructions.push({
              setDependency: "specifiedArea",
              desiredValue: desiredCircumradius ** 2 * (numVertices / 2 * Math.sin(2 * Math.PI / numVertices))
            });
          } else {
            instructions.push({
              setDependency: "essentialCircumradius",
              desiredValue: desiredCircumradius
            });
          }
          instructions.push({
            setDependency: "essentialDirection",
            desiredValue: desiredDirection
          });
        } else if (dependencyValues.haveSpecifiedCenter) {
          instructions.push({
            setDependency: "specifiedCenter",
            desiredValue: desiredCenter
          });
          let desiredVertices = {
            "0,0": me$3.fromAst(
              desiredDirectionWithRadius[0] + desiredCenter[0]
            ),
            "0,1": me$3.fromAst(
              desiredDirectionWithRadius[1] + desiredCenter[1]
            )
          };
          instructions.push({
            setDependency: "verticesAttr",
            desiredValue: desiredVertices,
            variableIndex: 0
          });
        } else if (dependencyValues.numVerticesSpecified === 1) {
          let desiredCircumradius = Math.sqrt(
            desiredDirectionWithRadius[0] ** 2 + desiredDirectionWithRadius[1] ** 2
          );
          let desiredDirection = desiredDirectionWithRadius.map(
            (x2) => x2 / desiredCircumradius
          );
          if (dependencyValues.specifiedCircumradius !== null) {
            instructions.push({
              setDependency: "specifiedCircumradius",
              desiredValue: desiredCircumradius
            });
          } else if (dependencyValues.specifiedInradius !== null) {
            instructions.push({
              setDependency: "specifiedInradius",
              desiredValue: desiredCircumradius * Math.cos(Math.PI / numVertices)
            });
          } else if (dependencyValues.specifiedSideLength !== null) {
            instructions.push({
              setDependency: "specifiedSideLength",
              desiredValue: desiredCircumradius * (2 * Math.sin(Math.PI / numVertices))
            });
          } else if (dependencyValues.specifiedPerimeter !== null) {
            instructions.push({
              setDependency: "specifiedPerimeter",
              desiredValue: desiredCircumradius * (2 * numVertices * Math.sin(Math.PI / numVertices))
            });
          } else if (dependencyValues.specifiedArea !== null) {
            instructions.push({
              setDependency: "specifiedArea",
              desiredValue: desiredCircumradius ** 2 * (numVertices / 2 * Math.sin(2 * Math.PI / numVertices))
            });
          } else {
            instructions.push({
              setDependency: "essentialCircumradius",
              desiredValue: desiredCircumradius
            });
          }
          instructions.push({
            setDependency: "essentialDirection",
            desiredValue: desiredDirection
          });
          let desiredVertices = {
            "0,0": me$3.fromAst(
              desiredDirectionWithRadius[0] + desiredCenter[0]
            ),
            "0,1": me$3.fromAst(
              desiredDirectionWithRadius[1] + desiredCenter[1]
            )
          };
          instructions.push({
            setDependency: "verticesAttr",
            desiredValue: desiredVertices,
            variableIndex: 0
          });
        } else {
          let angle = 2 * Math.PI / numVertices;
          let c2 = Math.cos(angle);
          let s2 = Math.sin(angle);
          let desiredDirectionWithRadius2 = [
            desiredDirectionWithRadius[0] * c2 - desiredDirectionWithRadius[1] * s2,
            desiredDirectionWithRadius[0] * s2 + desiredDirectionWithRadius[1] * c2
          ];
          let desiredVertices = {
            "0,0": me$3.fromAst(
              desiredDirectionWithRadius[0] + desiredCenter[0]
            ),
            "0,1": me$3.fromAst(
              desiredDirectionWithRadius[1] + desiredCenter[1]
            ),
            "1,0": me$3.fromAst(
              desiredDirectionWithRadius2[0] + desiredCenter[0]
            ),
            "1,1": me$3.fromAst(
              desiredDirectionWithRadius2[1] + desiredCenter[1]
            )
          };
          instructions.push({
            setDependency: "verticesAttr",
            desiredValue: desiredVertices,
            variableIndex: 0
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.desiredUnconstrainedCenterComponents = {
      additionalStateVariablesDefined: [
        {
          variableName: "desiredUnconstrainedDirectionWithRadius",
          hasEssential: true,
          defaultValue: null
        }
      ],
      hasEssential: true,
      defaultValue: null,
      returnDependencies: () => ({}),
      definition() {
        return {
          useEssentialOrDefaultValue: {
            desiredUnconstrainedCenterComponents: true,
            desiredUnconstrainedDirectionWithRadius: true
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let instructions = [];
        if (desiredStateVariableValues.desiredUnconstrainedCenterComponents) {
          instructions.push({
            setEssentialValue: "desiredUnconstrainedCenterComponents",
            value: desiredStateVariableValues.desiredUnconstrainedCenterComponents
          });
        }
        if (desiredStateVariableValues.desiredUnconstrainedDirectionWithRadius) {
          instructions.push({
            setEssentialValue: "desiredUnconstrainedDirectionWithRadius",
            value: desiredStateVariableValues.desiredUnconstrainedDirectionWithRadius
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.unconstrainedVertices = {
      isLocation: true,
      isArray: true,
      numDimensions: 2,
      hasEssential: true,
      entryPrefixes: ["unconstrainedVertexX", "unconstrainedVertex"],
      returnEntryDimensions: (prefix) => prefix === "unconstrainedVertex" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "unconstrainedVertexX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "unconstrainedVertices") {
          if (propIndex.length === 1) {
            return "unconstrainedVertex" + propIndex[0];
          } else {
            return `unconstrainedVertexX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 19) === "unconstrainedVertex") {
          let unconstrainedVertexNum = Number(varName.slice(19));
          if (Number.isInteger(unconstrainedVertexNum) && unconstrainedVertexNum > 0) {
            return `unconstrainedVertexX${unconstrainedVertexNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numVertices, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          numVertices: {
            dependencyType: "stateVariable",
            variableName: "numVertices"
          },
          unconstrainedCenterComponents: {
            dependencyType: "stateVariable",
            variableName: "unconstrainedCenterComponents"
          },
          unconstrainedDirectionWithRadius: {
            dependencyType: "stateVariable",
            variableName: "unconstrainedDirectionWithRadius"
          },
          // just for inverse definition
          desiredUnconstrainedCenterComponents: {
            dependencyType: "stateVariable",
            variableName: "desiredUnconstrainedCenterComponents"
          },
          desiredUnconstrainedDirectionWithRadius: {
            dependencyType: "stateVariable",
            variableName: "desiredUnconstrainedDirectionWithRadius"
          }
        };
        return {
          globalDependencies
        };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let numVertices = globalDependencyValues.numVertices;
        let center = globalDependencyValues.unconstrainedCenterComponents;
        let unconstrainedDirectionWithRadius = globalDependencyValues.unconstrainedDirectionWithRadius;
        let unconstrainedVertices = {};
        if (center.some((x2) => !Number.isFinite(x2)) || unconstrainedDirectionWithRadius.some(
          (x2) => !Number.isFinite(x2)
        )) {
          for (let vertexInd = 0; vertexInd < numVertices; vertexInd++) {
            unconstrainedVertices[`${vertexInd},0`] = me$3.fromAst("＿");
            unconstrainedVertices[`${vertexInd},1`] = me$3.fromAst("＿");
          }
        } else {
          for (let vertexInd = 0; vertexInd < numVertices; vertexInd++) {
            let rotation = vertexInd * 2 * Math.PI / numVertices;
            let s2 = Math.sin(rotation);
            let c2 = Math.cos(rotation);
            unconstrainedVertices[`${vertexInd},0`] = me$3.fromAst(
              center[0] + unconstrainedDirectionWithRadius[0] * c2 - unconstrainedDirectionWithRadius[1] * s2
            );
            unconstrainedVertices[`${vertexInd},1`] = me$3.fromAst(
              center[1] + unconstrainedDirectionWithRadius[0] * s2 + unconstrainedDirectionWithRadius[1] * c2
            );
          }
        }
        return { setValue: { unconstrainedVertices } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        stateValues,
        workspace
      }) {
        let numVertices = globalDependencyValues.numVertices;
        if (!workspace.desiredVertices) {
          workspace.desiredVertices = {};
        }
        Object.assign(
          workspace.desiredVertices,
          desiredStateVariableValues.unconstrainedVertices
        );
        let desiredKeys = Object.keys(workspace.desiredVertices);
        let vertexInd1String = desiredKeys[0].split(",")[0];
        let changingJustOneVertex = desiredKeys.every(
          (v2) => v2.split(",")[0] === vertexInd1String
        );
        if (!workspace.allVertices) {
          workspace.allVertices = {};
        }
        Object.assign(workspace.allVertices, workspace.desiredVertices);
        let unconstrainedVertices = await stateValues.unconstrainedVertices;
        for (let vertexInd = 0; vertexInd < numVertices; vertexInd++) {
          for (let dim = 0; dim < 2; dim++) {
            let arrayKey = vertexInd + "," + dim;
            if (!workspace.allVertices[arrayKey]) {
              workspace.allVertices[arrayKey] = unconstrainedVertices[vertexInd][dim];
            }
          }
        }
        let desiredCenter;
        if (changingJustOneVertex) {
          desiredCenter = (await stateValues.center).map(
            (x2) => x2.evaluate_to_constant()
          );
        } else {
          let center_x = 0, center_y = 0;
          for (let vertexInd = 0; vertexInd < numVertices; vertexInd++) {
            let v_x = workspace.allVertices[vertexInd + ",0"];
            let v_y = workspace.allVertices[vertexInd + ",1"];
            center_x += v_x.evaluate_to_constant();
            center_y += v_y.evaluate_to_constant();
          }
          center_x /= numVertices;
          center_y /= numVertices;
          desiredCenter = [center_x, center_y];
        }
        let vertexInd1 = Number(vertexInd1String);
        let desiredVertex_x = workspace.desiredVertices[vertexInd1String + ",0"];
        if (!desiredVertex_x) {
          let unconstrainedVertices2 = await stateValues.unconstrainedVertices;
          desiredVertex_x = unconstrainedVertices2[vertexInd1][0];
        }
        let desiredVertex_y = workspace.desiredVertices[vertexInd1String + ",1"];
        if (!desiredVertex_y) {
          let unconstrainedVertices2 = await stateValues.unconstrainedVertices;
          desiredVertex_y = unconstrainedVertices2[vertexInd1][1];
        }
        let desiredVertex = [
          desiredVertex_x.evaluate_to_constant(),
          desiredVertex_y.evaluate_to_constant()
        ];
        let centerToVertex = [
          desiredVertex[0] - desiredCenter[0],
          desiredVertex[1] - desiredCenter[1]
        ];
        let angle = -vertexInd1 * 2 * Math.PI / numVertices;
        let c2 = Math.cos(angle);
        let s2 = Math.sin(angle);
        let desiredDirectionWithRadius = [
          centerToVertex[0] * c2 - centerToVertex[1] * s2,
          centerToVertex[0] * s2 + centerToVertex[1] * c2
        ];
        let instructions = [
          {
            setDependency: "unconstrainedCenterComponents",
            desiredValue: desiredCenter
          },
          {
            setDependency: "unconstrainedDirectionWithRadius",
            desiredValue: desiredDirectionWithRadius
          },
          {
            setDependency: "desiredUnconstrainedCenterComponents",
            desiredValue: desiredCenter
          },
          {
            setDependency: "desiredUnconstrainedDirectionWithRadius",
            desiredValue: desiredDirectionWithRadius
          }
        ];
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.center = {
      description: "The center coordinates of the regular polygon.",
      isLocation: true,
      public: true,
      isArray: true,
      entryPrefixes: ["centerX"],
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "centerX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      returnArraySizeDependencies: () => ({}),
      returnArraySize: () => [2],
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          vertices: {
            dependencyType: "stateVariable",
            variableName: "vertices"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let centerComponents = [0, 0];
        for (let vertex of globalDependencyValues.vertices) {
          centerComponents[0] += vertex[0].evaluate_to_constant();
          centerComponents[1] += vertex[1].evaluate_to_constant();
        }
        let numVertices = globalDependencyValues.vertices.length;
        centerComponents[0] /= numVertices;
        centerComponents[1] /= numVertices;
        return {
          setValue: {
            center: centerComponents.map((x2) => me$3.fromAst(x2))
          }
        };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        stateValues,
        workspace
      }) {
        let previous_center = (await stateValues.center).map(
          (v2) => v2.evaluate_to_constant()
        );
        let desired_center_x = desiredStateVariableValues.center[0]?.evaluate_to_constant();
        if (!Number.isFinite(desired_center_x)) {
          desired_center_x = workspace.desired_center_x;
        }
        if (!Number.isFinite(desired_center_x)) {
          desired_center_x = previous_center[0];
        }
        workspace.desired_center_x = desired_center_x;
        let desired_center_y = desiredStateVariableValues.center[1]?.evaluate_to_constant();
        if (!Number.isFinite(desired_center_y)) {
          desired_center_y = workspace.desired_center_y;
        }
        if (!Number.isFinite(desired_center_y)) {
          desired_center_y = previous_center[1];
        }
        workspace.desired_center_y = desired_center_y;
        let shift = [
          desired_center_x - previous_center[0],
          desired_center_y - previous_center[1]
        ];
        let numericalVertices = await stateValues.numericalVertices;
        let desired_vertices = numericalVertices.map(
          (vertex) => vertex.map((v2, i2) => me$3.fromAst(v2 + shift[i2]))
        );
        let instructions = [
          {
            setDependency: "vertices",
            desiredValue: desired_vertices
          }
        ];
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.circumradius = {
      description: "The radius of the circumscribed circle.",
      isLocation: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        center: {
          dependencyType: "stateVariable",
          variableName: "center"
        },
        vertex1: {
          dependencyType: "stateVariable",
          variableName: "vertex1"
        }
      }),
      definition({ dependencyValues }) {
        let center = dependencyValues.center.map(
          (x2) => x2.evaluate_to_constant()
        );
        let vertex1 = dependencyValues.vertex1.map(
          (x2) => x2.evaluate_to_constant()
        );
        let circumradius = Math.sqrt(
          (vertex1[0] - center[0]) ** 2 + (vertex1[1] - center[1]) ** 2
        );
        return { setValue: { circumradius } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let center = dependencyValues.center.map(
          (x2) => x2.evaluate_to_constant()
        );
        let vertex1 = dependencyValues.vertex1.map(
          (x2) => x2.evaluate_to_constant()
        );
        let directionWithRadius = [
          vertex1[0] - center[0],
          vertex1[1] - center[1]
        ];
        let previousRadius = Math.sqrt(
          directionWithRadius[0] ** 2 + directionWithRadius[1] ** 2
        );
        let desiredRadius = desiredStateVariableValues.circumradius;
        let desiredDirectionWithRadius = directionWithRadius.map(
          (x2) => x2 / previousRadius * desiredRadius
        );
        let desiredVertex1 = [
          me$3.fromAst(desiredDirectionWithRadius[0] + center[0]),
          me$3.fromAst(desiredDirectionWithRadius[1] + center[1])
        ];
        return {
          success: true,
          instructions: [
            {
              setDependency: "vertex1",
              desiredValue: desiredVertex1
            }
          ]
        };
      }
    };
    stateVariableDefinitions.radius = {
      isAlias: true,
      targetVariableName: "circumradius",
      description: "Distance from center to a vertex (alias for circumradius)."
    };
    stateVariableDefinitions.inradius = {
      description: "The radius of the inscribed circle.",
      isLocation: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        circumradius: {
          dependencyType: "stateVariable",
          variableName: "circumradius"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues }) {
        let circumradius = dependencyValues.circumradius;
        let numVertices = dependencyValues.numVertices;
        let inradius = circumradius * Math.cos(Math.PI / numVertices);
        return { setValue: { inradius } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredInradius = desiredStateVariableValues.inradius;
        let numVertices = dependencyValues.numVertices;
        let desiredCircumradius = desiredInradius / Math.cos(Math.PI / numVertices);
        return {
          success: true,
          instructions: [
            {
              setDependency: "circumradius",
              desiredValue: desiredCircumradius
            }
          ]
        };
      }
    };
    stateVariableDefinitions.apothem = {
      isAlias: true,
      targetVariableName: "inradius",
      description: "Distance from center to the midpoint of a side (alias for inradius)."
    };
    stateVariableDefinitions.sideLength = {
      description: "The length of each side.",
      isLocation: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        circumradius: {
          dependencyType: "stateVariable",
          variableName: "circumradius"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues }) {
        let circumradius = dependencyValues.circumradius;
        let numVertices = dependencyValues.numVertices;
        let sideLength = circumradius * (2 * Math.sin(Math.PI / numVertices));
        return { setValue: { sideLength } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredSideLength = desiredStateVariableValues.sideLength;
        let numVertices = dependencyValues.numVertices;
        let desiredCircumradius = desiredSideLength / (2 * Math.sin(Math.PI / numVertices));
        return {
          success: true,
          instructions: [
            {
              setDependency: "circumradius",
              desiredValue: desiredCircumradius
            }
          ]
        };
      }
    };
    stateVariableDefinitions.perimeter = {
      description: "The perimeter of the polygon.",
      isLocation: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        circumradius: {
          dependencyType: "stateVariable",
          variableName: "circumradius"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues }) {
        let circumradius = dependencyValues.circumradius;
        let numVertices = dependencyValues.numVertices;
        let perimeter = circumradius * (2 * numVertices * Math.sin(Math.PI / numVertices));
        return { setValue: { perimeter } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredPerimeter = desiredStateVariableValues.perimeter;
        let numVertices = dependencyValues.numVertices;
        let desiredCircumradius = desiredPerimeter / (2 * numVertices * Math.sin(Math.PI / numVertices));
        return {
          success: true,
          instructions: [
            {
              setDependency: "circumradius",
              desiredValue: desiredCircumradius
            }
          ]
        };
      }
    };
    stateVariableDefinitions.area = {
      description: "The area enclosed by the polygon.",
      isLocation: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        circumradius: {
          dependencyType: "stateVariable",
          variableName: "circumradius"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues }) {
        let circumradius = dependencyValues.circumradius;
        let numVertices = dependencyValues.numVertices;
        let area = circumradius ** 2 * (numVertices / 2 * Math.sin(2 * Math.PI / numVertices));
        return { setValue: { area } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredArea = desiredStateVariableValues.area;
        let numVertices = dependencyValues.numVertices;
        let desiredCircumradius = Math.sqrt(
          desiredArea / (numVertices / 2 * Math.sin(2 * Math.PI / numVertices))
        );
        return {
          success: true,
          instructions: [
            {
              setDependency: "circumradius",
              desiredValue: desiredCircumradius
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  async movePolygonCenter({
    center,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false,
    pointRole = "regularPolygon"
  }) {
    if (!transient) {
      skippable = false;
    }
    if (pointRole !== "regularPolygon") {
      console.warn(`Invalid pointRole for regular polygon: ${pointRole}`);
      return;
    }
    if (!Array.isArray(center) || center.length !== 2) {
      return;
    }
    if (!center.every((x2) => Number.isFinite(x2))) {
      console.warn(
        `Invalid center coordinates for ${pointRole} move: ${center.join(", ")}`
      );
      return;
    }
    if (!await this.stateValues.draggable) {
      return;
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "center",
        value: center.map((x2) => me$3.fromAst(x2)),
        sourceDetails
      }
    ];
    const performUpdateArgs = {
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate
    };
    if (transient) {
      performUpdateArgs.transient = true;
      performUpdateArgs.skippable = skippable;
    } else {
      performUpdateArgs.event = {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          center
        }
      };
    }
    return await this.coreFunctions.performUpdate(performUpdateArgs);
  }
  async changeRadius({
    radius,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!transient) {
      skippable = false;
    }
    if (!Number.isFinite(radius)) {
      console.warn(
        `Invalid radius for regular polygon change: radius=${radius}`
      );
      return;
    }
    if (!await this.stateValues.verticesDraggable) {
      return;
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "circumradius",
        value: Math.max(1e-15, radius),
        sourceDetails
      }
    ];
    const performUpdateArgs = {
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate
    };
    if (transient) {
      performUpdateArgs.transient = true;
      performUpdateArgs.skippable = skippable;
    } else {
      performUpdateArgs.event = {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          radius
        }
      };
    }
    return await this.coreFunctions.performUpdate(performUpdateArgs);
  }
  async movePolygon({
    pointCoords,
    transient,
    skippable,
    sourceDetails,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!transient) {
      skippable = false;
    }
    let numVerticesMoved = Object.keys(pointCoords).length;
    if (numVerticesMoved === 1) {
      if (!await this.stateValues.verticesDraggable) {
        return;
      }
    } else {
      if (!await this.stateValues.draggable) {
        return;
      }
    }
    let vertexComponents = {};
    for (let ind in pointCoords) {
      vertexComponents[ind + ",0"] = me$3.fromAst(pointCoords[ind][0]);
      vertexComponents[ind + ",1"] = me$3.fromAst(pointCoords[ind][1]);
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "vertices",
            value: vertexComponents,
            sourceDetails
          }
        ],
        transient,
        skippable,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "vertices",
            value: vertexComponents,
            sourceDetails
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            pointCoordinates: pointCoords
          }
        }
      });
    }
    let numVerticesSpecified = await this.stateValues.numVerticesSpecified;
    let haveSpecifiedCenter = await this.stateValues.haveSpecifiedCenter;
    let desiredUnconstrainedCenter = await this.stateValues.desiredUnconstrainedCenterComponents;
    let desiredUnconstrainedDirectionWithRadius = await this.stateValues.desiredUnconstrainedDirectionWithRadius;
    if (desiredUnconstrainedCenter !== null && desiredUnconstrainedDirectionWithRadius !== null) {
      let desiredUnconstrainedVertex1 = [
        desiredUnconstrainedCenter[0] + desiredUnconstrainedDirectionWithRadius[0],
        desiredUnconstrainedCenter[1] + desiredUnconstrainedDirectionWithRadius[1]
      ];
      if (haveSpecifiedCenter) {
        if (numVerticesSpecified >= 1) {
          let resultingCenter = await this.stateValues.unconstrainedCenterComponents;
          let resultingDirectionWithRadius = await this.stateValues.unconstrainedDirectionWithRadius;
          let resultingVertex1 = [
            resultingCenter[0] + resultingDirectionWithRadius[0],
            resultingCenter[1] + resultingDirectionWithRadius[1]
          ];
          let tol = 1e-6;
          let vertex1Changed = !desiredUnconstrainedVertex1.every(
            (v2, i2) => Math.abs(v2 - resultingVertex1[i2]) < tol
          );
          let centerChanged = !desiredUnconstrainedCenter.every(
            (v2, i2) => Math.abs(v2 - resultingCenter[i2]) < tol
          );
          if (centerChanged) {
            if (!vertex1Changed) {
              let newInstructions = [
                {
                  updateType: "updateValue",
                  componentIdx: this.componentIdx,
                  stateVariable: "unconstrainedCenterComponents",
                  value: resultingCenter
                },
                {
                  updateType: "updateValue",
                  componentIdx: this.componentIdx,
                  stateVariable: "unconstrainedDirectionWithRadius",
                  value: desiredUnconstrainedDirectionWithRadius
                }
              ];
              return await this.coreFunctions.performUpdate({
                updateInstructions: newInstructions,
                transient,
                actionId,
                sourceInformation,
                skipRendererUpdate
              });
            }
          } else if (vertex1Changed) {
            let newCenter = [
              resultingVertex1[0] - desiredUnconstrainedDirectionWithRadius[0],
              resultingVertex1[1] - desiredUnconstrainedDirectionWithRadius[1]
            ];
            let newInstructions = [
              {
                updateType: "updateValue",
                componentIdx: this.componentIdx,
                stateVariable: "unconstrainedCenterComponents",
                value: newCenter
              },
              {
                updateType: "updateValue",
                componentIdx: this.componentIdx,
                stateVariable: "unconstrainedDirectionWithRadius",
                value: desiredUnconstrainedDirectionWithRadius
              }
            ];
            return await this.coreFunctions.performUpdate({
              updateInstructions: newInstructions,
              transient,
              actionId,
              sourceInformation,
              skipRendererUpdate
            });
          }
        }
      } else if (numVerticesSpecified >= 2) {
        let numVertices = await this.stateValues.numVertices;
        let angle = 2 * Math.PI / numVertices;
        let c2 = Math.cos(angle);
        let s2 = Math.sin(angle);
        let directionWithRadius2 = [
          desiredUnconstrainedDirectionWithRadius[0] * c2 - desiredUnconstrainedDirectionWithRadius[1] * s2,
          desiredUnconstrainedDirectionWithRadius[0] * s2 + desiredUnconstrainedDirectionWithRadius[1] * c2
        ];
        let vertex2 = [
          directionWithRadius2[0] + desiredUnconstrainedCenter[0],
          directionWithRadius2[1] + desiredUnconstrainedCenter[1]
        ];
        let resultingVertices = await this.stateValues.unconstrainedVertices;
        let resultingVertex1 = resultingVertices[0].map(
          (x2) => x2.evaluate_to_constant()
        );
        let resultingVertex2 = resultingVertices[1].map(
          (x2) => x2.evaluate_to_constant()
        );
        let tol = 1e-6;
        let vertex1Changed = !desiredUnconstrainedVertex1.every(
          (v2, i2) => Math.abs(v2 - resultingVertex1[i2]) < tol
        );
        let vertex2Changed = !vertex2.every(
          (v2, i2) => Math.abs(v2 - resultingVertex2[i2]) < tol
        );
        if (vertex1Changed) {
          if (!vertex2Changed) {
            let newCenter = [
              resultingVertex1[0] - desiredUnconstrainedDirectionWithRadius[0],
              resultingVertex1[1] - desiredUnconstrainedDirectionWithRadius[1]
            ];
            let newInstructions = [
              {
                updateType: "updateValue",
                componentIdx: this.componentIdx,
                stateVariable: "unconstrainedCenterComponents",
                value: newCenter
              },
              {
                updateType: "updateValue",
                componentIdx: this.componentIdx,
                stateVariable: "unconstrainedDirectionWithRadius",
                value: desiredUnconstrainedDirectionWithRadius
              }
            ];
            return await this.coreFunctions.performUpdate({
              updateInstructions: newInstructions,
              transient,
              actionId,
              sourceInformation,
              skipRendererUpdate
            });
          }
        } else if (vertex2Changed) {
          let newCenter = [
            resultingVertex2[0] - directionWithRadius2[0],
            resultingVertex2[1] - directionWithRadius2[1]
          ];
          let newInstructions = [
            {
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "unconstrainedCenterComponents",
              value: newCenter
            },
            {
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "unconstrainedDirectionWithRadius",
              value: desiredUnconstrainedDirectionWithRadius
            }
          ];
          return await this.coreFunctions.performUpdate({
            updateInstructions: newInstructions,
            transient,
            actionId,
            sourceInformation,
            skipRendererUpdate
          });
        }
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
class Circle extends Curve {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveCircle: this.moveCircle.bind(this),
      changeRadius: this.changeRadius.bind(this),
      circleClicked: this.circleClicked.bind(this),
      circleFocused: this.circleFocused.bind(this)
    });
  }
  static componentType = "circle";
  static componentDocs = {
    summary: "A circle, potentially defined by center, radius, and/or points on the circle"
  };
  static rendererType = "circle";
  static representsClosedPath = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.through = {
      createComponentOfType: "pointList",
      description: "Points the circle passes through."
    };
    attributes.center = {
      createComponentOfType: "point",
      description: "The circle's center point."
    };
    attributes.radius = {
      createComponentOfType: "math",
      description: "The circle's radius."
    };
    attributes.addControls = {
      description: "Whether to render interactive control handles on the circle.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "centerAndRadius",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "center",
          description: "Show a control handle for moving the circle's center."
        },
        {
          value: "radius",
          description: "Show a control handle for resizing the circle's radius."
        },
        {
          value: "centerAndRadius",
          description: "Show control handles for both moving the center and resizing the radius."
        },
        {
          value: "none",
          description: "Show no control handles."
        }
      ],
      valueForTrue: "centerAndRadius",
      valueForFalse: "none"
    };
    attributes.filled = {
      description: "Whether to fill the interior of the circle.",
      createComponentOfType: "boolean",
      createStateVariable: "filled",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.hideOffGraphIndicator = {
      createComponentOfType: "boolean",
      description: "Whether to suppress the indicator drawn at the edge when the component is off-screen."
    };
    attributes.controlOrder = returnGraphControlOrderAttribute();
    delete attributes.parMin;
    delete attributes.parMax;
    delete attributes.variable;
    return attributes;
  }
  static returnChildGroups() {
    return GraphicalComponent.returnChildGroups();
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = GraphicalComponent.returnStateVariableDefinitions.call(
      this,
      numerics
    );
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the circle's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        filled: {
          dependencyType: "stateVariable",
          variableName: "filled"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const theme = dependencyValues.document?.stateValues.theme;
        const lineColorWord = getLineColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const fillColorWord = getFillColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const borderDescription = getBorderDescription(
          dependencyValues.selectedStyle
        );
        const styleDescription = buildClosedShapeStyleDescription({
          filled: dependencyValues.filled,
          lineColorWord,
          fillColorWord,
          fillStyleWord: dependencyValues.selectedStyle.fillStyleWord,
          borderDescription
        });
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "circle".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        filled: {
          dependencyType: "stateVariable",
          variableName: "filled"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const theme = dependencyValues.document?.stateValues.theme;
        const lineColorWord = getLineColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const fillColorWord = getFillColorWord(
          dependencyValues.selectedStyle,
          theme
        );
        const borderDescription = getBorderDescription(
          dependencyValues.selectedStyle
        );
        const styleDescriptionWithNoun = buildClosedShapeStyleDescription({
          filled: dependencyValues.filled,
          lineColorWord,
          fillColorWord,
          fillStyleWord: dependencyValues.selectedStyle.fillStyleWord,
          borderDescription,
          noun: " circle",
          includeBorderArticle: true
        });
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.borderStyleDescription = {
      description: "A textual description of the circle's border style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const borderStyleDescription = getBorderDescription(dependencyValues.selectedStyle) + getLineColorWord(
          dependencyValues.selectedStyle,
          dependencyValues.document?.stateValues.theme
        );
        return { setValue: { borderStyleDescription } };
      }
    };
    stateVariableDefinitions.fillStyleDescription = {
      description: "A textual description of the circle's fill style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        filled: {
          dependencyType: "stateVariable",
          variableName: "filled"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        const fillStyleDescription = buildFillStyleDescription({
          filled: dependencyValues.filled,
          fillColorWord: getFillColorWord(
            dependencyValues.selectedStyle,
            dependencyValues.document?.stateValues.theme
          ),
          fillStyleWord: dependencyValues.selectedStyle.fillStyleWord
        });
        return { setValue: { fillStyleDescription } };
      }
    };
    stateVariableDefinitions.hideOffGraphIndicator = {
      description: "Whether to suppress the off-graph indicator when the circle is outside the visible area.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        hideOffGraphIndicatorAttr: {
          dependencyType: "attributeComponent",
          attributeName: "hideOffGraphIndicator",
          variableNames: ["value"]
        },
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["hideOffGraphIndicators"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.hideOffGraphIndicatorAttr) {
          return {
            setValue: {
              hideOffGraphIndicator: dependencyValues.hideOffGraphIndicatorAttr.stateValues.value
            }
          };
        } else if (dependencyValues.graphAncestor) {
          return {
            setValue: {
              hideOffGraphIndicator: dependencyValues.graphAncestor.stateValues.hideOffGraphIndicators
            }
          };
        } else {
          return {
            setValue: { hideOffGraphIndicator: false }
          };
        }
      }
    };
    stateVariableDefinitions.curveType = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { curveType: "circle" } })
    };
    stateVariableDefinitions.parMax = {
      description: "Maximum value of the curve parameter (always 2π for a circle).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { parMax: NaN } })
    };
    stateVariableDefinitions.parMin = {
      description: "Minimum value of the curve parameter (always 0 for a circle).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { parMin: NaN } })
    };
    stateVariableDefinitions.fs = {
      forRenderer: true,
      isArray: true,
      entryPrefixes: ["f"],
      returnArraySizeDependencies: () => ({}),
      returnArraySize: () => [0],
      returnArrayDependenciesByKey: () => ({}),
      arrayDefinitionByKey: () => ({ setValue: { fs: {} } })
    };
    stateVariableDefinitions.numThroughPoints = {
      returnDependencies: () => ({
        throughAttr: {
          dependencyType: "attributeComponent",
          attributeName: "through",
          variableNames: ["numPoints"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.throughAttr !== null) {
          return {
            setValue: {
              numThroughPoints: dependencyValues.throughAttr.stateValues.numPoints
            }
          };
        } else {
          return { setValue: { numThroughPoints: 0 } };
        }
      }
    };
    stateVariableDefinitions.throughPoints = {
      public: true,
      description: "Points the circle is constrained to pass through.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "throughPointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      indexAliases: [[], ["x", "y", "z"]],
      entryPrefixes: ["throughPointX", "throughPoint"],
      returnEntryDimensions: (prefix) => prefix === "throughPoint" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "throughPointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "throughPoints") {
          if (propIndex.length === 1) {
            return "throughPoint" + propIndex[0];
          } else {
            return `throughPointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 12) === "throughPoint") {
          let throughPointNum = Number(varName.slice(12));
          if (Number.isInteger(throughPointNum) && throughPointNum > 0) {
            return `throughPointX${throughPointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints, 2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          dependenciesByKey[arrayKey] = {
            throughAttr: {
              dependencyType: "attributeComponent",
              attributeName: "through",
              variableNames: ["pointX" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let throughPoints = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          let throughAttr = dependencyValuesByKey[arrayKey].throughAttr;
          if (throughAttr !== null && throughAttr.stateValues["pointX" + varEnding]) {
            throughPoints[arrayKey] = throughAttr.stateValues["pointX" + varEnding];
          } else {
            throughPoints[arrayKey] = me$3.fromAst("＿");
          }
        }
        return { setValue: { throughPoints } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.throughPoints) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          if (dependencyValuesByKey[arrayKey].throughAttr !== null && dependencyValuesByKey[arrayKey].throughAttr.stateValues["pointX" + varEnding]) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].throughAttr,
              desiredValue: desiredStateVariableValues.throughPoints[arrayKey],
              variableIndex: 0
            });
          } else {
            return { success: false };
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.havePrescribedCenter = {
      returnDependencies: () => ({
        centerAttr: {
          dependencyType: "attributeComponent",
          attributeName: "center"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          havePrescribedCenter: dependencyValues.centerAttr !== null
        },
        checkForActualChange: { havePrescribedCenter: true }
      })
    };
    stateVariableDefinitions.prescribedCenter = {
      isArray: true,
      isLocation: true,
      entryPrefixes: ["prescribedCenterX"],
      returnArraySizeDependencies: () => ({
        havePrescribedCenter: {
          dependencyType: "stateVariable",
          variableName: "havePrescribedCenter"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.havePrescribedCenter ? 2 : 0];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            centerAttr: {
              dependencyType: "attributeComponent",
              attributeName: "center",
              variableNames: ["x" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let prescribedCenter = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (dependencyValuesByKey[arrayKey].centerAttr !== null) {
            prescribedCenter[arrayKey] = dependencyValuesByKey[arrayKey].centerAttr.stateValues["x" + varEnding];
            if (!prescribedCenter[arrayKey]) {
              prescribedCenter[arrayKey] = me$3.fromAst("＿");
            }
          }
        }
        return { setValue: { prescribedCenter } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        for (let arrayKey of Object.keys(
          desiredStateVariableValues.prescribedCenter
        ).reverse()) {
          if (dependencyValuesByKey[arrayKey].centerAttr !== null) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].centerAttr,
              desiredValue: desiredStateVariableValues.prescribedCenter[arrayKey],
              variableIndex: 0
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.prescribedRadius = {
      defaultValue: null,
      isLocation: true,
      returnDependencies: () => ({
        radiusAttr: {
          dependencyType: "attributeComponent",
          attributeName: "radius",
          variableNames: ["value"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.radiusAttr !== null) {
          return {
            setValue: {
              prescribedRadius: dependencyValues.radiusAttr.stateValues.value
            }
          };
        } else {
          return {
            setValue: {
              prescribedRadius: null
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.radiusAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "radiusAttr",
                desiredValue: desiredStateVariableValues.prescribedRadius,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.havePrescribedRadius = {
      returnDependencies: () => ({
        radiusAttr: {
          dependencyType: "attributeComponent",
          attributeName: "radius"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          havePrescribedRadius: dependencyValues.radiusAttr !== null
        },
        checkForActualChange: { havePrescribedRadius: true }
      })
    };
    stateVariableDefinitions.numericalPrescribedRadius = {
      returnDependencies: () => ({
        prescribedRadius: {
          dependencyType: "stateVariable",
          variableName: "prescribedRadius"
        }
      }),
      additionalStateVariablesDefined: [
        "haveNonNumericalPrescribedRadius"
      ],
      definition: function({ dependencyValues }) {
        let haveNonNumericalPrescribedRadius = false;
        let numericalPrescribedRadius;
        if (dependencyValues.prescribedRadius === null) {
          numericalPrescribedRadius = null;
        } else {
          numericalPrescribedRadius = dependencyValues.prescribedRadius.evaluate_to_constant();
          if (!Number.isFinite(numericalPrescribedRadius)) {
            numericalPrescribedRadius = NaN;
            haveNonNumericalPrescribedRadius = true;
          }
        }
        return {
          setValue: {
            haveNonNumericalPrescribedRadius,
            numericalPrescribedRadius
          },
          checkForActualChange: {
            haveNonNumericalEntriesNumericalRadius: true
          }
        };
      },
      inverseDefinition: async function({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (await stateValues.haveNonNumericalPrescribedRadius) {
          return { success: false };
        }
        if (desiredStateVariableValues.numericalPrescribedRadius !== void 0) {
          if (dependencyValues.prescribedRadius === null) {
            return { success: false };
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "prescribedRadius",
                desiredValue: me$3.fromAst(
                  desiredStateVariableValues.numericalPrescribedRadius
                )
              }
            ]
          };
        }
        return { success: false };
      }
    };
    stateVariableDefinitions.numericalPrescribedCenter = {
      isArray: true,
      entryPrefixes: ["numericalPrescribedCenterX"],
      returnArraySizeDependencies: () => ({
        havePrescribedCenter: {
          dependencyType: "stateVariable",
          variableName: "havePrescribedCenter"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.havePrescribedCenter ? 2 : 0];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            prescribedCenterX: {
              dependencyType: "stateVariable",
              variableName: "prescribedCenterX" + varEnding
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        dependencyValuesByKey,
        arrayKeys
      }) {
        let numericalPrescribedCenter = {};
        for (let arrayKey of arrayKeys) {
          let prescribedX = dependencyValuesByKey[arrayKey].prescribedCenterX;
          if (prescribedX) {
            numericalPrescribedCenter[arrayKey] = prescribedX.evaluate_to_constant();
          } else {
            numericalPrescribedCenter[arrayKey] = NaN;
          }
        }
        return { setValue: { numericalPrescribedCenter } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.numericalPrescribedCenter) {
          instructions.push({
            setDependency: dependencyNamesByKey[arrayKey].prescribedCenterX,
            desiredValue: me$3.fromAst(
              desiredStateVariableValues.numericalPrescribedCenter[arrayKey]
            )
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.haveNonNumericalPrescribedCenter = {
      returnDependencies: () => ({
        numericalPrescribedCenter: {
          dependencyType: "stateVariable",
          variableName: "numericalPrescribedCenter"
        }
      }),
      definition({ dependencyValues }) {
        let haveNonNumericalPrescribedCenter = dependencyValues.numericalPrescribedCenter.some(
          (x2) => !Number.isFinite(x2)
        );
        return {
          setValue: {
            haveNonNumericalPrescribedCenter
          },
          checkForActualChange: {
            haveNonNumericalPrescribedCenter: true
          }
        };
      }
    };
    stateVariableDefinitions.numericalThroughPoints = {
      returnDependencies: () => ({
        throughPoints: {
          dependencyType: "stateVariable",
          variableName: "throughPoints"
        }
      }),
      additionalStateVariablesDefined: ["haveNonNumericalThroughPoints"],
      definition: function({ dependencyValues }) {
        let haveNonNumericalThroughPoints = false;
        let numericalThroughPoints = [];
        for (let point of dependencyValues.throughPoints) {
          let numericalPoint = [];
          for (let dim = 0; dim < 2; dim++) {
            let numericalValue = point[dim].evaluate_to_constant();
            if (Number.isFinite(numericalValue)) {
              numericalPoint.push(numericalValue);
            } else {
              haveNonNumericalThroughPoints = true;
              numericalPoint = [];
              break;
            }
          }
          if (numericalPoint.length > 0) {
            numericalThroughPoints.push(numericalPoint);
          } else {
            numericalThroughPoints = [];
            break;
          }
        }
        return {
          setValue: {
            haveNonNumericalThroughPoints,
            numericalThroughPoints
          },
          checkForActualChange: {
            haveNonNumericalThroughPoints: true
          }
        };
      },
      inverseDefinition: async function({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (await stateValues.haveNonNumericalThroughPoints) {
          return { success: false };
        }
        if (desiredStateVariableValues.numericalThroughPoints !== void 0) {
          let desiredThroughPoints = {};
          for (let [
            ind,
            pt2
          ] of desiredStateVariableValues.numericalThroughPoints.entries()) {
            desiredThroughPoints[ind + ",0"] = me$3.fromAst(pt2[0]);
            desiredThroughPoints[ind + ",1"] = me$3.fromAst(pt2[1]);
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "throughPoints",
                desiredValue: desiredThroughPoints
              }
            ]
          };
        }
        return { success: false };
      }
    };
    stateVariableDefinitions.haveNumericalEntries = {
      returnDependencies: () => ({
        haveNonNumericalPrescribedCenter: {
          dependencyType: "stateVariable",
          variableName: "haveNonNumericalPrescribedCenter"
        },
        haveNonNumericalPrescribedRadius: {
          dependencyType: "stateVariable",
          variableName: "haveNonNumericalPrescribedRadius"
        },
        haveNonNumericalThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "haveNonNumericalThroughPoints"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          haveNumericalEntries: !(dependencyValues.haveNonNumericalPrescribedCenter || dependencyValues.haveNonNumericalPrescribedRadius || dependencyValues.haveNonNumericalThroughPoints)
        }
      })
    };
    stateVariableDefinitions.numericalRadiusCalculatedWithCenter = {
      additionalStateVariablesDefined: [
        "numericalCenterCalculatedWithRadius"
      ],
      returnDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        },
        numericalThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numericalThroughPoints"
        },
        haveNonNumericalThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "haveNonNumericalThroughPoints"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.haveNonNumericalThroughPoints) {
          let message = "Haven't implemented `<circle>` through " + dependencyValues.numThroughPoints + " points";
          message += " in case where the points don't have numerical values.";
          let warning = { message, type: "warning" };
          return {
            setValue: {
              numericalRadiusCalculatedWithCenter: null,
              numericalCenterCalculatedWithRadius: null
            },
            sendDiagnostics: [warning]
          };
        }
        if (dependencyValues.numThroughPoints === 2) {
          let { numericalCenter, numericalRadius } = circleFromTwoNumericalPoints({
            point1: dependencyValues.numericalThroughPoints[0],
            point2: dependencyValues.numericalThroughPoints[1]
          });
          return {
            setValue: {
              numericalCenterCalculatedWithRadius: numericalCenter,
              numericalRadiusCalculatedWithCenter: numericalRadius
            }
          };
        } else if (dependencyValues.numThroughPoints === 3) {
          let x1 = dependencyValues.numericalThroughPoints[0][0];
          let x2 = dependencyValues.numericalThroughPoints[1][0];
          let x3 = dependencyValues.numericalThroughPoints[2][0];
          let y1 = dependencyValues.numericalThroughPoints[0][1];
          let y2 = dependencyValues.numericalThroughPoints[1][1];
          let y3 = dependencyValues.numericalThroughPoints[2][1];
          let numericalCenter, numericalRadius;
          if (x1 === x2 && y1 === y2) {
            if (x1 === x3 && y1 === y3) {
              numericalCenter = [x1, y1];
              numericalRadius = 0;
            } else {
              let result = circleFromTwoNumericalPoints({
                point1: [x1, y1],
                point2: [x3, y3]
              });
              numericalCenter = result.numericalCenter;
              numericalRadius = result.numericalRadius;
            }
          } else if (x1 === x3 && y1 === y3 || x2 === x3 && y2 === y3) {
            let result = circleFromTwoNumericalPoints({
              point1: [x1, y1],
              point2: [x2, y2]
            });
            numericalCenter = result.numericalCenter;
            numericalRadius = result.numericalRadius;
          } else {
            let mag1 = x1 * x1 + y1 * y1;
            let mag2 = x2 * x2 + y2 * y2;
            let mag3 = x3 * x3 + y3 * y3;
            let A2 = x1 * (y2 - y3) - y1 * (x2 - x3) + x2 * y3 - x3 * y2;
            let B2 = mag1 * (y3 - y2) + mag2 * (y1 - y3) + mag3 * (y2 - y1);
            let C3 = mag1 * (x2 - x3) + mag2 * (x3 - x1) + mag3 * (x1 - x2);
            let D2 = mag1 * (x3 * y2 - x2 * y3) + mag2 * (x1 * y3 - x3 * y1) + mag3 * (x2 * y1 - x1 * y2);
            if (A2 !== 0) {
              numericalCenter = [-B2 / (2 * A2), -C3 / (2 * A2)];
              numericalRadius = Math.sqrt(
                (B2 * B2 + C3 * C3 - 4 * A2 * D2) / (4 * A2 * A2)
              );
            } else {
              numericalCenter = [NaN, NaN];
              numericalRadius = NaN;
            }
          }
          return {
            setValue: {
              numericalCenterCalculatedWithRadius: numericalCenter,
              numericalRadiusCalculatedWithCenter: numericalRadius
            }
          };
        } else if (dependencyValues.numThroughPoints > 3) {
          let warning = {
            message: "Cannot calculate circle through more than 3 points.",
            type: "warning"
          };
          return {
            setValue: {
              numericalRadiusCalculatedWithCenter: null,
              numericalCenterCalculatedWithRadius: null
            },
            sendDiagnostics: [warning]
          };
        } else {
          return {
            setValue: {
              numericalRadiusCalculatedWithCenter: null,
              numericalCenterCalculatedWithRadius: null
            }
          };
        }
      }
    };
    stateVariableDefinitions.haveCenterRadiusPoints = {
      returnDependencies: () => ({
        havePrescribedCenter: {
          dependencyType: "stateVariable",
          variableName: "havePrescribedCenter"
        },
        havePrescribedRadius: {
          dependencyType: "stateVariable",
          variableName: "havePrescribedRadius"
        },
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.havePrescribedCenter && dependencyValues.havePrescribedRadius && dependencyValues.numThroughPoints > 0) {
          let warning = {
            message: "Cannot calculate circle with specified radius, center and through points.",
            type: "warning"
          };
          return {
            setValue: { haveCenterRadiusPoints: true },
            sendDiagnostics: [warning]
          };
        } else {
          return { setValue: { haveCenterRadiusPoints: false } };
        }
      }
    };
    stateVariableDefinitions.essentialRadius = {
      defaultValue: 1,
      hasEssential: true,
      isLocation: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          essentialRadius: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "essentialRadius",
              value: desiredStateVariableValues.essentialRadius
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numericalRadius = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: [
        "numThroughPoints",
        "havePrescribedCenter",
        "havePrescribedRadius"
      ],
      returnDependencies: function({ stateValues }) {
        let dependencies = {
          haveNonNumericalPrescribedRadius: {
            dependencyType: "stateVariable",
            variableName: "haveNonNumericalPrescribedRadius"
          },
          numThroughPoints: {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          },
          essentialRadius: {
            dependencyType: "stateVariable",
            variableName: "essentialRadius"
          },
          haveCenterRadiusPoints: {
            dependencyType: "stateVariable",
            variableName: "haveCenterRadiusPoints"
          }
        };
        if (stateValues.havePrescribedRadius) {
          dependencies.numericalPrescribedRadius = {
            dependencyType: "stateVariable",
            variableName: "numericalPrescribedRadius"
          };
        } else {
          if (stateValues.havePrescribedCenter) {
            if (stateValues.numThroughPoints >= 1) {
              dependencies.haveNonNumericalPrescribedCenter = {
                dependencyType: "stateVariable",
                variableName: "haveNonNumericalPrescribedCenter"
              };
              dependencies.numericalPrescribedCenter = {
                dependencyType: "stateVariable",
                variableName: "numericalPrescribedCenter"
              };
              dependencies.haveNonNumericalThroughPoints = {
                dependencyType: "stateVariable",
                variableName: "haveNonNumericalThroughPoints"
              };
              dependencies.numericalThroughPoints = {
                dependencyType: "stateVariable",
                variableName: "numericalThroughPoints"
              };
            }
          } else if (stateValues.numThroughPoints > 1) {
            dependencies.numericalRadiusCalculatedWithCenter = {
              dependencyType: "stateVariable",
              variableName: "numericalRadiusCalculatedWithCenter"
            };
            dependencies.numericalThroughPoints = {
              dependencyType: "stateVariable",
              variableName: "numericalThroughPoints"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.haveNonNumericalPrescribedRadius || dependencyValues.haveNonNumericalPrescribedCenter || dependencyValues.haveNonNumericalThroughPoints) {
          return {
            setValue: {
              numericalRadius: NaN
            }
          };
        }
        if (dependencyValues.numericalPrescribedRadius !== void 0) {
          if (dependencyValues.haveCenterRadiusPoints) {
            return {
              setValue: { numericalRadius: NaN }
            };
          }
          return {
            setValue: {
              numericalRadius: Math.max(
                0,
                dependencyValues.numericalPrescribedRadius
              )
            }
          };
        }
        if (dependencyValues.numericalPrescribedCenter !== void 0) {
          if (dependencyValues.numThroughPoints === 0) {
            let r2 = dependencyValues.essentialRadius;
            if (r2 instanceof me$3.class) {
              r2 = r2.evaluate_to_constant();
            }
            return {
              setValue: { numericalRadius: r2 }
            };
          } else if (dependencyValues.numThroughPoints === 1) {
            let pt2 = dependencyValues.numericalThroughPoints[0];
            if (pt2 === void 0) {
              return { setValue: { numericalRadius: NaN } };
            }
            let numericalRadius = Math.sqrt(
              Math.pow(
                pt2[0] - dependencyValues.numericalPrescribedCenter[0],
                2
              ) + Math.pow(
                pt2[1] - dependencyValues.numericalPrescribedCenter[1],
                2
              )
            );
            return { setValue: { numericalRadius } };
          } else {
            let warning = {
              message: "Cannot calculate circle with specified center through more than 1 point.",
              type: "warning"
            };
            return {
              setValue: { numericalRadius: NaN },
              sendDiagnostics: [warning]
            };
          }
        }
        if (dependencyValues.numThroughPoints < 2) {
          let r2 = dependencyValues.essentialRadius;
          if (r2 instanceof me$3.class) {
            r2 = r2.evaluate_to_constant();
          }
          return {
            setValue: { numericalRadius: r2 }
          };
        } else {
          return {
            setValue: {
              numericalRadius: dependencyValues.numericalRadiusCalculatedWithCenter
            }
          };
        }
      },
      inverseDefinition: async function({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (dependencyValues.numericalPrescribedRadius !== void 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "numericalPrescribedRadius",
                desiredValue: Math.max(
                  0,
                  desiredStateVariableValues.numericalRadius
                )
              }
            ]
          };
        }
        if (dependencyValues.numericalPrescribedCenter !== void 0) {
          if (dependencyValues.numThroughPoints === 0) {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "essentialRadius",
                  desiredValue: Math.max(
                    0,
                    desiredStateVariableValues.numericalRadius
                  )
                }
              ]
            };
          } else if (dependencyValues.numThroughPoints === 1) {
            let numericalRadius = Math.max(
              0,
              desiredStateVariableValues.numericalRadius
            );
            let theta = (await stateValues.throughAngles)[0];
            if (!Number.isFinite(theta)) {
              return { success: false };
            }
            let numericalCenter = await stateValues.numericalCenter;
            let pt2 = [
              numericalCenter[0] + numericalRadius * Math.cos(theta),
              numericalCenter[1] + numericalRadius * Math.sin(theta)
            ];
            return {
              success: true,
              instructions: [
                {
                  setDependency: "numericalThroughPoints",
                  desiredValue: [pt2]
                }
              ]
            };
          } else {
            return { success: false };
          }
        }
        if (dependencyValues.numThroughPoints < 2) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "essentialRadius",
                desiredValue: Math.max(
                  0,
                  desiredStateVariableValues.numericalRadius
                )
              }
            ]
          };
        } else {
          let newThroughPoints = [];
          let numericalRadius = Math.max(
            0,
            desiredStateVariableValues.numericalRadius
          );
          for (let i2 = 0; i2 < dependencyValues.numThroughPoints; i2++) {
            let theta = (await stateValues.throughAngles)[i2];
            if (!Number.isFinite(theta)) {
              return { success: false };
            }
            let numericalCenter = await stateValues.numericalCenter;
            let pt2 = [
              numericalCenter[0] + numericalRadius * Math.cos(theta),
              numericalCenter[1] + numericalRadius * Math.sin(theta)
            ];
            newThroughPoints.push(pt2);
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "numericalThroughPoints",
                desiredValue: newThroughPoints
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.essentialCenter = {
      isLocation: true,
      isArray: true,
      entryPrefixes: ["essentialCenterX"],
      hasEssential: true,
      defaultValueByArrayKey: () => 0,
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey: () => ({}),
      arrayDefinitionByKey: function({ arrayKeys }) {
        let essentialCenter = {};
        for (let arrayKey of arrayKeys) {
          essentialCenter[arrayKey] = true;
        }
        return {
          useEssentialOrDefaultValue: { essentialCenter }
        };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        stateValues,
        workspace
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.essentialCenter) {
          instructions.push({
            setEssentialValue: "essentialCenter",
            value: {
              [arrayKey]: desiredStateVariableValues.essentialCenter[arrayKey]
            }
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalCenter = {
      forRenderer: true,
      isArray: true,
      entryPrefixes: ["numericalCenterX"],
      stateVariablesDeterminingDependencies: [
        "numThroughPoints",
        "havePrescribedCenter",
        "havePrescribedRadius"
      ],
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          haveCenterRadiusPoints: {
            dependencyType: "stateVariable",
            variableName: "haveCenterRadiusPoints"
          }
        };
        let dependenciesByKey = {};
        if (stateValues.havePrescribedCenter) {
          for (let arrayKey of arrayKeys) {
            let varEnding = Number(arrayKey) + 1;
            dependenciesByKey[arrayKey] = {
              numericalPrescribedCenterX: {
                dependencyType: "stateVariable",
                variableName: "numericalPrescribedCenterX" + varEnding
              }
            };
          }
        } else {
          for (let arrayKey of arrayKeys) {
            let varEnding = Number(arrayKey) + 1;
            dependenciesByKey[arrayKey] = {
              essentialCenterX: {
                dependencyType: "stateVariable",
                variableName: "essentialCenterX" + varEnding
              }
            };
          }
          globalDependencies.numThroughPoints = {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          };
          globalDependencies.numericalThroughPoints = {
            dependencyType: "stateVariable",
            variableName: "numericalThroughPoints"
          };
          globalDependencies.haveNonNumericalThroughPoints = {
            dependencyType: "stateVariable",
            variableName: "haveNonNumericalThroughPoints"
          };
          if (stateValues.havePrescribedRadius) {
            if (stateValues.numThroughPoints > 0) {
              globalDependencies.numericalRadius = {
                dependencyType: "stateVariable",
                variableName: "numericalRadius"
              };
            }
          } else if (stateValues.numThroughPoints == 1) {
            globalDependencies.numericalRadius = {
              dependencyType: "stateVariable",
              variableName: "numericalRadius"
            };
          } else if (stateValues.numThroughPoints > 1) {
            globalDependencies.numericalCenterCalculatedWithRadius = {
              dependencyType: "stateVariable",
              variableName: "numericalCenterCalculatedWithRadius"
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let numericalCenter = {};
        for (let arrayKey of arrayKeys) {
          if (dependencyValuesByKey[arrayKey].numericalPrescribedCenterX !== void 0) {
            if (globalDependencyValues.haveCenterRadiusPoints) {
              return {
                setValue: { numericalCenter: [NaN, NaN] }
              };
            }
            numericalCenter[arrayKey] = dependencyValuesByKey[arrayKey].numericalPrescribedCenterX;
          }
        }
        if (Object.keys(numericalCenter).length > 0) {
          return { setValue: { numericalCenter } };
        }
        if (globalDependencyValues.haveNonNumericalPrescribedRadius || globalDependencyValues.haveNonNumericalThroughPoints) {
          return {
            setValue: {
              numericalCenter: [NaN, NaN]
            }
          };
        }
        if (globalDependencyValues.numericalRadius !== void 0) {
          if (globalDependencyValues.numThroughPoints === 0) {
            for (let arrayKey of arrayKeys) {
              let value = dependencyValuesByKey[arrayKey].essentialCenterX;
              if (value instanceof me$3.class) {
                value = value.evaluate_to_constant();
              }
              numericalCenter[arrayKey] = value;
            }
            return { setValue: { numericalCenter } };
          } else if (globalDependencyValues.numThroughPoints === 1) {
            if (globalDependencyValues.numericalThroughPoints.length < 1) {
              return {
                setValue: { numericalCenter: [NaN, NaN] }
              };
            }
            let numericalCenter2 = [
              globalDependencyValues.numericalThroughPoints[0][0],
              globalDependencyValues.numericalThroughPoints[0][1] - globalDependencyValues.numericalRadius
            ];
            return { setValue: { numericalCenter: numericalCenter2 } };
          } else if (globalDependencyValues.numThroughPoints === 2) {
            if (globalDependencyValues.numericalThroughPoints.length < 2) {
              return {
                setValue: { numericalCenter: [NaN, NaN] }
              };
            }
            let r2 = globalDependencyValues.numericalRadius;
            let x1 = globalDependencyValues.numericalThroughPoints[0][0];
            let x2 = globalDependencyValues.numericalThroughPoints[1][0];
            let y1 = globalDependencyValues.numericalThroughPoints[0][1];
            let y2 = globalDependencyValues.numericalThroughPoints[1][1];
            let dist2 = Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2);
            let r22 = r2 * r2;
            if (r2 < 0 || 4 * r22 < dist2) {
              let dist3 = Math.round(Math.sqrt(dist2) * 100) / 100;
              let warning = {
                message: `Cannot calculate circle: given that the distance between the two points is ${dist3}, the specified radius ${r2} is too small.`,
                type: "warning"
              };
              return {
                setValue: { numericalCenter: [NaN, NaN] },
                sendDiagnostics: [warning]
              };
            }
            if (dist2 === 0) {
              return {
                setValue: { numericalCenter: [x1, y1 - r2] }
              };
            }
            let centerx = 0.5 * (dist2 * (x1 + x2) + (y1 - y2) * Math.sqrt((4 * r22 - dist2) * dist2)) / dist2;
            let centery = 0.5 * (dist2 * (y1 + y2) + (x2 - x1) * Math.sqrt((4 * r22 - dist2) * dist2)) / dist2;
            return {
              setValue: { numericalCenter: [centerx, centery] }
            };
          } else {
            let warning = {
              message: "Cannot create circle through more than two points with a specified radius.",
              type: "warning"
            };
            return {
              setValue: { numericalCenter: [NaN, NaN] },
              sendDiagnostics: [warning]
            };
          }
        }
        if (globalDependencyValues.numThroughPoints === 0) {
          for (let arrayKey of arrayKeys) {
            let value = dependencyValuesByKey[arrayKey].essentialCenterX;
            if (value instanceof me$3.class) {
              value = value.evaluate_to_constant();
            }
            numericalCenter[arrayKey] = value;
          }
          return { setValue: { numericalCenter } };
        } else {
          if (globalDependencyValues.numericalCenterCalculatedWithRadius === void 0) {
            return {
              setValue: {
                numericalCenter: [NaN, NaN]
              }
            };
          } else {
            return {
              setValue: {
                numericalCenter: globalDependencyValues.numericalCenterCalculatedWithRadius
              }
            };
          }
        }
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        stateValues,
        workspace
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.numericalCenter) {
          if (dependencyValuesByKey[arrayKey].numericalPrescribedCenterX !== void 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].numericalPrescribedCenterX,
              desiredValue: desiredStateVariableValues.numericalCenter[arrayKey]
            });
          }
        }
        if (instructions.length > 0) {
          return {
            success: true,
            instructions
          };
        }
        if (globalDependencyValues.numThroughPoints === 0) {
          for (let arrayKey in desiredStateVariableValues.numericalCenter) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].essentialCenterX,
              desiredValue: desiredStateVariableValues.numericalCenter[arrayKey]
            });
          }
          return {
            success: true,
            instructions
          };
        } else {
          if (!workspace.desiredCenter) {
            workspace.desiredCenter = {};
          }
          for (let key = 0; key < 2; key++) {
            if (desiredStateVariableValues.numericalCenter[key] !== void 0) {
              workspace.desiredCenter[key] = desiredStateVariableValues.numericalCenter[key];
            } else if (workspace.desiredCenter[key] === void 0) {
              workspace.desiredCenter[key] = stateValues.numericalCenter[key];
            }
          }
          let newThroughPoints = [];
          let throughAngles = await stateValues.throughAngles;
          let numericalRadius = await stateValues.numericalRadius;
          for (let i2 = 0; i2 < globalDependencyValues.numThroughPoints; i2++) {
            let theta = throughAngles[i2];
            if (!Number.isFinite(theta)) {
              return { success: false };
            }
            let pt2 = [
              workspace.desiredCenter[0] + numericalRadius * Math.cos(theta),
              workspace.desiredCenter[1] + numericalRadius * Math.sin(theta)
            ];
            newThroughPoints.push(pt2);
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "numericalThroughPoints",
                desiredValue: newThroughPoints
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.throughAngles = {
      // send to renderer just so it can try to preserve original angles
      // when dragging circle based on points with constraints
      forRenderer: true,
      isLocation: true,
      defaultValue: [],
      hasEssential: true,
      doNotShadowEssential: true,
      returnDependencies: () => ({
        haveNumericalEntries: {
          dependencyType: "stateVariable",
          variableName: "haveNumericalEntries"
        },
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        },
        numericalThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numericalThroughPoints"
        },
        numericalRadius: {
          dependencyType: "stateVariable",
          variableName: "numericalRadius"
        },
        numericalCenter: {
          dependencyType: "stateVariable",
          variableName: "numericalCenter"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!(dependencyValues.haveNumericalEntries && dependencyValues.numThroughPoints > 0 && dependencyValues.numericalRadius > 0 && dependencyValues.numericalCenter.every(
          (x2) => Number.isFinite(x2)
        ))) {
          return {
            useEssentialOrDefaultValue: {
              throughAngles: true
            }
          };
        }
        let throughAngles = [];
        for (let pt2 of dependencyValues.numericalThroughPoints) {
          throughAngles.push(
            Math.atan2(
              pt2[1] - dependencyValues.numericalCenter[1],
              pt2[0] - dependencyValues.numericalCenter[0]
            )
          );
        }
        return {
          setValue: { throughAngles },
          setEssentialValue: { throughAngles }
        };
      }
    };
    stateVariableDefinitions.radius = {
      public: true,
      description: "The radius of the circle.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      stateVariablesDeterminingDependencies: [
        "numThroughPoints",
        "havePrescribedCenter",
        "havePrescribedRadius"
      ],
      returnDependencies: function({ stateValues }) {
        let dependencies = {
          numericalRadius: {
            dependencyType: "stateVariable",
            variableName: "numericalRadius"
          },
          numThroughPoints: {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          },
          essentialRadius: {
            dependencyType: "stateVariable",
            variableName: "essentialRadius"
          },
          haveCenterRadiusPoints: {
            dependencyType: "stateVariable",
            variableName: "haveCenterRadiusPoints"
          }
        };
        if (stateValues.havePrescribedRadius) {
          dependencies.prescribedRadius = {
            dependencyType: "stateVariable",
            variableName: "prescribedRadius"
          };
        } else {
          if (stateValues.havePrescribedCenter && stateValues.numThroughPoints >= 1) {
            dependencies.prescribedCenter = {
              dependencyType: "stateVariable",
              variableName: "prescribedCenter"
            };
            dependencies.throughPoints = {
              dependencyType: "stateVariable",
              variableName: "throughPoints"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        if (Number.isFinite(dependencyValues.numericalRadius)) {
          return {
            setValue: {
              radius: me$3.fromAst(
                dependencyValues.numericalRadius
              )
            }
          };
        }
        if (dependencyValues.prescribedRadius !== void 0) {
          if (dependencyValues.haveCenterRadiusPoints) {
            return {
              setValue: { radius: me$3.fromAst("＿") }
            };
          }
          return {
            setValue: {
              radius: dependencyValues.prescribedRadius
            }
          };
        }
        if (dependencyValues.prescribedCenter !== void 0) {
          if (dependencyValues.numThroughPoints === 0) {
            let r2 = dependencyValues.essentialRadius;
            if (!(r2 instanceof me$3.class)) {
              if (Number.isFinite(r2)) {
                r2 = me$3.fromAst(r2);
              } else {
                r2 = me$3.fromAst("＿");
              }
            }
            return {
              setValue: { radius: r2 }
            };
          } else if (dependencyValues.numThroughPoints === 1) {
            try {
              let pt2 = dependencyValues.throughPoints[0];
              let ptx = pt2[0];
              let pty = pt2[1];
              let ctx = dependencyValues.prescribedCenter[0];
              let cty = dependencyValues.prescribedCenter[1];
              let radius = ptx.subtract(ctx).pow(2).add(pty.subtract(cty).pow(2)).pow(0.5).simplify();
              return { setValue: { radius } };
            } catch (e3) {
              let warning = {
                message: "Invalid center or through points of circle.",
                type: "warning"
              };
              return {
                setValue: { radius: me$3.fromAst("＿") },
                sendDiagnostics: [warning]
              };
            }
          } else {
            let warning = {
              message: "Cannot calculate radius of circle with specified center through more than 1 point.",
              type: "warning"
            };
            return {
              setValue: { radius: me$3.fromAst("＿") },
              sendDiagnostics: [warning]
            };
          }
        }
        if (dependencyValues.numThroughPoints < 2) {
          let r2 = dependencyValues.essentialRadius;
          if (!(r2 instanceof me$3.class)) {
            if (Number.isFinite(r2)) {
              r2 = me$3.fromAst(r2);
            } else {
              r2 = me$3.fromAst("＿");
            }
          }
          return {
            setValue: { radius: r2 }
          };
        } else {
          return {
            setValue: { radius: me$3.fromAst("＿") }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let numericalRadius = desiredStateVariableValues.radius.evaluate_to_constant();
        if (Number.isFinite(numericalRadius) && Number.isFinite(dependencyValues.numericalRadius)) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "numericalRadius",
                desiredValue: numericalRadius
              }
            ]
          };
        }
        if (dependencyValues.prescribedRadius !== void 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "prescribedRadius",
                desiredValue: desiredStateVariableValues.radius
              }
            ]
          };
        }
        if (dependencyValues.numThroughPoints === 0) {
          let instructions = [
            {
              setDependency: "essentialRadius",
              desiredValue: desiredStateVariableValues.radius
            }
          ];
          return {
            success: true,
            instructions
          };
        } else {
          let warning = {
            message: "Cannot change radius of circle with non-numerical through points",
            type: "warning"
          };
          return { success: false, sendDiagnostics: [warning] };
        }
      }
    };
    stateVariableDefinitions.diameter = {
      description: "The diameter of the circle.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        radius: {
          dependencyType: "stateVariable",
          variableName: "radius"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            diameter: dependencyValues.radius.multiply(2).simplify()
          }
        };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "radius",
              desiredValue: desiredStateVariableValues.diameter.divide(2).simplify()
            }
          ]
        };
      }
    };
    stateVariableDefinitions.circumference = {
      description: "The circumference of the circle.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        radius: {
          dependencyType: "stateVariable",
          variableName: "radius"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            circumference: dependencyValues.radius.multiply(me$3.fromAst(["*", 2, "pi"])).simplify()
          }
        };
      }
    };
    stateVariableDefinitions.area = {
      description: "The area enclosed by the circle.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        radius: {
          dependencyType: "stateVariable",
          variableName: "radius"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            area: dependencyValues.radius.multiply(dependencyValues.radius).multiply(me$3.fromAst("pi")).simplify()
          }
        };
      }
    };
    stateVariableDefinitions.center = {
      forRenderer: true,
      isLocation: true,
      public: true,
      description: "The center coordinates of the circle.",
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "centerX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["centerX"],
      indexAliases: [["x", "y", "z"]],
      stateVariablesDeterminingDependencies: [
        "numThroughPoints",
        "havePrescribedCenter",
        "havePrescribedRadius"
      ],
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          haveCenterRadiusPoints: {
            dependencyType: "stateVariable",
            variableName: "haveCenterRadiusPoints"
          }
        };
        let dependenciesByKey = {};
        if (stateValues.havePrescribedCenter) {
          globalDependencies.havePrescribedCenter = {
            dependencyType: "value",
            value: true
          };
          for (let arrayKey of arrayKeys) {
            let varEnding = Number(arrayKey) + 1;
            dependenciesByKey[arrayKey] = {
              numericalCenterX: {
                dependencyType: "stateVariable",
                variableName: "numericalCenterX" + varEnding
              },
              prescribedCenterX: {
                dependencyType: "stateVariable",
                variableName: "prescribedCenterX" + varEnding
              }
            };
          }
        } else {
          for (let arrayKey of arrayKeys) {
            let varEnding = Number(arrayKey) + 1;
            dependenciesByKey[arrayKey] = {
              essentialCenterX: {
                dependencyType: "stateVariable",
                variableName: "essentialCenterX" + varEnding
              }
            };
          }
          globalDependencies.numericalCenter = {
            dependencyType: "stateVariable",
            variableName: "numericalCenter"
          };
          globalDependencies.numThroughPoints = {
            dependencyType: "stateVariable",
            variableName: "numThroughPoints"
          };
          globalDependencies.throughPoints = {
            dependencyType: "stateVariable",
            variableName: "throughPoints"
          };
          if (stateValues.havePrescribedRadius) {
            if (stateValues.numThroughPoints > 0) {
              globalDependencies.radius = {
                dependencyType: "stateVariable",
                variableName: "prescribedRadius"
              };
              globalDependencies.haveNonNumericalPrescribedRadius = {
                dependencyType: "stateVariable",
                variableName: "haveNonNumericalPrescribedRadius"
              };
            }
            if (stateValues.numThroughPoints > 1) {
              globalDependencies.haveNonNumericalThroughPoints = {
                dependencyType: "stateVariable",
                variableName: "haveNonNumericalThroughPoints"
              };
            }
          } else if (stateValues.numThroughPoints == 1) {
            globalDependencies.radius = {
              dependencyType: "stateVariable",
              variableName: "radius"
            };
          } else if (stateValues.numThroughPoints > 1) {
            globalDependencies.haveNonNumericalThroughPoints = {
              dependencyType: "stateVariable",
              variableName: "haveNonNumericalThroughPoints"
            };
          }
        }
        return { dependenciesByKey, globalDependencies };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        if (globalDependencyValues.havePrescribedCenter) {
          if (globalDependencyValues.haveCenterRadiusPoints) {
            return {
              setValue: {
                center: [
                  me$3.fromAst("＿"),
                  me$3.fromAst("＿")
                ]
              }
            };
          }
          let center = {};
          for (let arrayKey of arrayKeys) {
            if (Number.isFinite(
              dependencyValuesByKey[arrayKey].numericalCenterX
            )) {
              center[arrayKey] = me$3.fromAst(
                dependencyValuesByKey[arrayKey].numericalCenterX
              );
            } else {
              center[arrayKey] = dependencyValuesByKey[arrayKey].prescribedCenterX;
            }
          }
          return { setValue: { center } };
        }
        if (globalDependencyValues.numericalCenter.every(
          (x2) => Number.isFinite(x2)
        )) {
          return {
            setValue: {
              center: globalDependencyValues.numericalCenter.map(
                (x2) => me$3.fromAst(x2)
              )
            }
          };
        }
        if (globalDependencyValues.radius !== void 0) {
          if (globalDependencyValues.numThroughPoints === 0) {
            let center = {};
            for (let arrayKey of arrayKeys) {
              let value = dependencyValuesByKey[arrayKey].essentialCenterX;
              if (!(value instanceof me$3.class)) {
                if (Number.isFinite(value)) {
                  value = me$3.fromAst(value);
                } else {
                  value = me$3.fromAst("＿");
                }
              }
              center[arrayKey] = value;
            }
            return { setValue: { center } };
          } else if (globalDependencyValues.numThroughPoints === 1) {
            let center;
            try {
              center = [
                globalDependencyValues.throughPoints[0][0],
                globalDependencyValues.throughPoints[0][1].subtract(globalDependencyValues.radius).simplify()
              ];
            } catch (e3) {
              center = [
                me$3.fromAst("＿"),
                me$3.fromAst("＿")
              ];
            }
            return { setValue: { center } };
          } else {
            let diagnostics = [];
            if (globalDependencyValues.haveNonNumericalPrescribedRadius || globalDependencyValues.haveNonNumericalThroughPoints) {
              diagnostics.push({
                message: "Cannot create circle through more than one point with specified radius when don't have numerical values.",
                type: "warning"
              });
            }
            return {
              setValue: {
                center: [
                  me$3.fromAst("＿"),
                  me$3.fromAst("＿")
                ]
              },
              sendDiagnostics: diagnostics
            };
          }
        }
        if (globalDependencyValues.numThroughPoints === 0) {
          let center = {};
          for (let arrayKey of arrayKeys) {
            let value = dependencyValuesByKey[arrayKey].essentialCenterX;
            if (!(value instanceof me$3.class)) {
              if (Number.isFinite(value)) {
                value = me$3.fromAst(value);
              } else {
                value = me$3.fromAst("＿");
              }
            }
            center[arrayKey] = value;
          }
          return { setValue: { center } };
        } else {
          return {
            setValue: {
              center: [
                me$3.fromAst("＿"),
                me$3.fromAst("＿")
              ]
            }
          };
        }
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        stateValues,
        dependencyNamesByKey,
        workspace
      }) {
        if (globalDependencyValues.havePrescribedCenter) {
          let instructions = [];
          for (let arrayKey in desiredStateVariableValues.center) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].prescribedCenterX,
              desiredValue: desiredStateVariableValues.center[arrayKey],
              arrayKey
            });
          }
          return {
            success: true,
            instructions
          };
        }
        if (!workspace.desiredCenter) {
          workspace.desiredCenter = {};
        }
        for (let key = 0; key < 2; key++) {
          if (desiredStateVariableValues.center[key] !== void 0) {
            workspace.desiredCenter[key] = desiredStateVariableValues.center[key];
          } else if (workspace.desiredCenter[key] === void 0) {
            workspace.desiredCenter[key] = (await stateValues.center)[key];
          }
        }
        let numericalCenter = [];
        let desiredCenterIsNumeric = true;
        for (let i2 = 0; i2 < 2; i2++) {
          let component = workspace.desiredCenter[i2].evaluate_to_constant();
          if (!Number.isFinite(component)) {
            desiredCenterIsNumeric = false;
            break;
          }
          numericalCenter.push(component);
        }
        if (desiredCenterIsNumeric && globalDependencyValues.numericalCenter.every(
          (x2) => Number.isFinite(x2)
        )) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "numericalCenter",
                desiredValue: numericalCenter
              }
            ]
          };
        }
        if (globalDependencyValues.numThroughPoints === 0) {
          let instructions = [];
          for (let arrayKey in desiredStateVariableValues.center) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].essentialCenterX,
              desiredValue: desiredStateVariableValues.center[arrayKey],
              arrayKey
            });
          }
          return {
            success: true,
            instructions
          };
        } else {
          let warning = {
            message: "Haven't implemented changing center of circle through points with non numerical values.",
            type: "warning"
          };
          return { success: false, sendDiagnostics: [warning] };
        }
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numericalCenter: {
          dependencyType: "stateVariable",
          variableName: "numericalCenter"
        },
        numericalRadius: {
          dependencyType: "stateVariable",
          variableName: "numericalRadius"
        }
      }),
      definition({ dependencyValues }) {
        let radius = dependencyValues.numericalRadius;
        let centerX = dependencyValues.numericalCenter[0];
        let centerY = dependencyValues.numericalCenter[1];
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              let x1 = variables.x1?.evaluate_to_constant();
              let x2 = variables.x2?.evaluate_to_constant();
              if (!(Number.isFinite(x1) && Number.isFinite(x2))) {
                return {};
              }
              if (!(Number.isFinite(centerX) && Number.isFinite(centerY) && Number.isFinite(radius))) {
                return {};
              }
              let theta = Math.atan2(x2 - centerY, x1 - centerX);
              let result = {
                x1: centerX + radius * Math.cos(theta),
                x2: centerY + radius * Math.sin(theta)
              };
              if (variables.x3 !== void 0) {
                result.x3 = 0;
              }
              return result;
            }
          }
        };
      }
    };
    stateVariableDefinitions.containsPoint = {
      returnDependencies: () => ({
        numericalRadius: {
          dependencyType: "stateVariable",
          variableName: "numericalRadius"
        },
        numericalCenter: {
          dependencyType: "stateVariable",
          variableName: "numericalCenter"
        }
      }),
      definition({ dependencyValues }) {
        if (!(dependencyValues.numericalRadius >= 0 && dependencyValues.numericalCenter.length == 2 && dependencyValues.numericalCenter.every(Number.isFinite))) {
          return { setValue: { containsPoint: () => false } };
        }
        const radius2 = dependencyValues.numericalRadius ** 2;
        const center = dependencyValues.numericalCenter;
        let containsPoint = function(P3) {
          return (P3[0] - center[0]) ** 2 + (P3[1] - center[1]) ** 2 <= radius2;
        };
        return { setValue: { containsPoint } };
      }
    };
    return stateVariableDefinitions;
  }
  async moveCircle({
    center,
    radius,
    throughAngles,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false,
    pointRole = "center"
  }) {
    if (!transient) {
      skippable = false;
    }
    if (pointRole !== "center") {
      console.warn(`Invalid pointRole for circle: ${pointRole}`);
      return;
    }
    if (!Array.isArray(center) || center.length !== 2) {
      return;
    }
    if (!Number.isFinite(center[0]) || !Number.isFinite(center[1])) {
      console.warn(
        `Invalid center coordinates for circle move: x=${center[0]}, y=${center[1]}`
      );
      return;
    }
    let instructions = [];
    let numThroughPoints = await this.stateValues.numThroughPoints;
    let numericalPrescribedCenter = await this.stateValues.numericalPrescribedCenter;
    if (numThroughPoints <= 1 || numericalPrescribedCenter.length > 0) {
      instructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "numericalCenter",
        value: center,
        sourceDetails
      });
    }
    let numericalThroughPoints = [];
    if (numThroughPoints >= 1) {
      if (throughAngles === void 0) {
        throughAngles = await this.stateValues.throughAngles;
      }
      if (radius === void 0) {
        radius = await this.stateValues.numericalRadius;
      }
      for (let i2 = 0; i2 < numThroughPoints; i2++) {
        let theta = throughAngles[i2];
        let pt2 = [
          center[0] + radius * Math.cos(theta),
          center[1] + radius * Math.sin(theta)
        ];
        numericalThroughPoints.push(pt2);
      }
      instructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "numericalThroughPoints",
        value: numericalThroughPoints,
        sourceDetails
      });
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions: instructions,
        transient,
        skippable,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions: instructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            center
          }
        }
      });
    }
    let resultingCenter = await this.stateValues.numericalCenter;
    let resultingNumericalThroughPoints = await this.stateValues.numericalThroughPoints;
    let tol = 1e-6;
    if (numericalPrescribedCenter.length > 0 && numThroughPoints === 1) {
      let throughPointUnchanged = numericalThroughPoints[0].every(
        (v2, i2) => v2 === resultingNumericalThroughPoints[0][i2]
      );
      let centerUnchanged = center.every(
        (v2, i2) => v2 === resultingCenter[i2]
      );
      if (throughPointUnchanged && !centerUnchanged) {
        let theta = throughAngles[0];
        let newNumericalThroughPoints = [
          [
            resultingCenter[0] + radius * Math.cos(theta),
            resultingCenter[1] + radius * Math.sin(theta)
          ]
        ];
        let newInstructions = [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "numericalThroughPoints",
            value: newNumericalThroughPoints
          }
        ];
        return await this.coreFunctions.performUpdate({
          updateInstructions: newInstructions,
          transient,
          skippable,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      } else if (centerUnchanged && !throughPointUnchanged) {
        let theta = throughAngles[0];
        let newCenter = [
          resultingNumericalThroughPoints[0][0] - radius * Math.cos(theta),
          resultingNumericalThroughPoints[0][1] - radius * Math.sin(theta)
        ];
        let newInstructions = [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "numericalCenter",
            value: newCenter
          }
        ];
        return await this.coreFunctions.performUpdate({
          updateInstructions: newInstructions,
          transient,
          skippable,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      }
    } else if (numThroughPoints >= 2) {
      let throughPointsChanged = [];
      let numThroughPointsChanged = 0;
      for (let [ind, pt2] of numericalThroughPoints.entries()) {
        if (!pt2.every(
          (v2, i2) => Math.abs(
            v2 - resultingNumericalThroughPoints[ind][i2]
          ) < tol
        )) {
          throughPointsChanged.push(ind);
          numThroughPointsChanged++;
        }
      }
      if (numThroughPointsChanged > 0 && numThroughPointsChanged < numThroughPoints) {
        let changedInd1 = throughPointsChanged[0];
        let relationshipPreserved = true;
        if (numThroughPointsChanged > 1) {
          let orig1 = numericalThroughPoints[changedInd1];
          let changed1 = resultingNumericalThroughPoints[changedInd1];
          let changevec1 = orig1.map((v2, i2) => v2 - changed1[i2]);
          for (let ind of throughPointsChanged.slice(1)) {
            let orig2 = numericalThroughPoints[ind];
            let changed2 = resultingNumericalThroughPoints[ind];
            let changevec2 = orig2.map((v2, i2) => v2 - changed2[i2]);
            if (!changevec1.every(
              (v2, i2) => Math.abs(v2 - changevec2[i2]) < tol
            )) {
              relationshipPreserved = false;
              break;
            }
          }
        }
        if (relationshipPreserved) {
          let thetaOfChanged = throughAngles[changedInd1];
          let newCenter = [
            resultingNumericalThroughPoints[changedInd1][0] - radius * Math.cos(thetaOfChanged),
            resultingNumericalThroughPoints[changedInd1][1] - radius * Math.sin(thetaOfChanged)
          ];
          let newNumericalThroughPoints = [];
          for (let i2 = 0; i2 < numThroughPoints; i2++) {
            if (throughPointsChanged.includes(i2)) {
              newNumericalThroughPoints.push(
                resultingNumericalThroughPoints[i2]
              );
            } else {
              let theta = throughAngles[i2];
              let pt2 = [
                newCenter[0] + radius * Math.cos(theta),
                newCenter[1] + radius * Math.sin(theta)
              ];
              newNumericalThroughPoints.push(pt2);
            }
          }
          let newInstructions = [
            {
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "numericalThroughPoints",
              value: newNumericalThroughPoints
            }
          ];
          return await this.coreFunctions.performUpdate({
            updateInstructions: newInstructions,
            transient,
            skippable,
            actionId,
            sourceInformation,
            skipRendererUpdate
          });
        }
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async changeRadius({
    radius,
    transient,
    skippable,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!transient) {
      skippable = false;
    }
    if (!Number.isFinite(radius)) {
      console.warn(`Invalid radius for circle change: radius=${radius}`);
      return;
    }
    const updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "numericalRadius",
        value: Math.max(0, radius)
      }
    ];
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        transient,
        skippable,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            radius
          }
        }
      });
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async circleClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async circleFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
function circleFromTwoNumericalPoints({ point1, point2 }) {
  let xcenter = (point1[0] + point2[0]) / 2;
  let ycenter = (point1[1] + point2[1]) / 2;
  let numericalCenter = [xcenter, ycenter];
  let numericalRadius = Math.sqrt(
    Math.pow(xcenter - point1[0], 2) + Math.pow(ycenter - point1[1], 2)
  );
  return { numericalCenter, numericalRadius };
}
class Parabola extends Curve {
  static componentType = "parabola";
  // Overrides Curve's ["line", "fill"]: parabolas have no enclosed interior to fill.
  static styleOverrideCategories = ["line"];
  static componentDocs = {
    summary: "A parabola defined by formula or geometric parameters"
  };
  static rendererType = "curve";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.through = {
      createComponentOfType: "pointList",
      description: "Points the parabola passes through."
    };
    attributes.vertex = {
      createComponentOfType: "point",
      isLocation: true,
      createStateVariable: "prescribedVertex",
      defaultValue: null,
      description: "The vertex of the parabola."
    };
    delete attributes.parMin;
    delete attributes.parMax;
    delete attributes.variable;
    return attributes;
  }
  static returnChildGroups() {
    return GraphicalComponent.returnChildGroups();
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = GraphicalComponent.returnStateVariableDefinitions.call(
      this,
      numerics
    );
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    let curveStateVariableDefinitions = super.returnStateVariableDefinitions(numerics);
    stateVariableDefinitions.graphXmin = curveStateVariableDefinitions.graphXmin;
    stateVariableDefinitions.styleDescription = curveStateVariableDefinitions.styleDescription;
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "parabola".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " parabola";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.curveType = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { curveType: "function" } })
    };
    stateVariableDefinitions.parMax = {
      description: "Maximum value of the parabola's parameter.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { parMax: Infinity } })
    };
    stateVariableDefinitions.parMin = {
      description: "Minimum value of the parabola's parameter.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { parMin: -Infinity } })
    };
    stateVariableDefinitions.aShadow = {
      defaultValue: 1,
      hasEssential: true,
      isLocation: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          aShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "aShadow",
              value: desiredStateVariableValues.aShadow
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numThroughPoints = {
      returnDependencies: () => ({
        throughAttr: {
          dependencyType: "attributeComponent",
          attributeName: "through",
          variableNames: ["numPoints"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.throughAttr === null) {
          return {
            setValue: { numThroughPoints: 0 }
          };
        } else {
          return {
            setValue: {
              numThroughPoints: dependencyValues.throughAttr.stateValues.numPoints
            }
          };
        }
      }
    };
    stateVariableDefinitions.throughPoints = {
      public: true,
      description: "Points the parabola passes through.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "throughPointX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["throughPointX", "throughPoint"],
      returnEntryDimensions: (prefix) => prefix === "throughPoint" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "throughPointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "throughPoints") {
          if (propIndex.length === 1) {
            return "throughPoint" + propIndex[0];
          } else {
            return `throughPointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 12) === "throughPoint") {
          let throughPointNum = Number(varName.slice(12));
          if (Number.isInteger(throughPointNum) && throughPointNum > 0) {
            return `throughPointX${throughPointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints, 2];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          dependenciesByKey[arrayKey] = {
            throughAttr: {
              dependencyType: "attributeComponent",
              attributeName: "through",
              variableNames: ["pointX" + varEnding]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let throughPoints = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          let throughAttr = dependencyValuesByKey[arrayKey].throughAttr;
          if (throughAttr !== null && throughAttr.stateValues["pointX" + varEnding]) {
            let numericalVal = throughAttr.stateValues["pointX" + varEnding].evaluate_to_constant();
            if (Number.isFinite(numericalVal)) {
              throughPoints[arrayKey] = me$3.fromAst(numericalVal);
            } else {
              throughPoints[arrayKey] = me$3.fromAst("＿");
            }
          } else {
            throughPoints[arrayKey] = me$3.fromAst("＿");
          }
        }
        return { setValue: { throughPoints } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        initialChange,
        stateValues
      }) {
        if (initialChange && !await stateValues.draggable) {
          return { success: false };
        }
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.throughPoints) {
          let [pointInd, dim] = arrayKey.split(",");
          let varEnding = Number(pointInd) + 1 + "_" + (Number(dim) + 1);
          if (dependencyValuesByKey[arrayKey].throughAttr !== null && dependencyValuesByKey[arrayKey].throughAttr.stateValues["pointX" + varEnding]) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].throughAttr,
              desiredValue: desiredStateVariableValues.throughPoints[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else {
            return { success: false };
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalThroughPoints = {
      isArray: true,
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numThroughPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numThroughPoints];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            throughPoint: {
              dependencyType: "stateVariable",
              variableName: "throughPoint" + (Number(arrayKey) + 1)
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalThroughPoints = {};
        for (let arrayKey of arrayKeys) {
          let point = dependencyValuesByKey[arrayKey].throughPoint.map((x2) => x2.tree);
          if (!point.every((x2) => Number.isFinite(x2))) {
            point = Array(point.length).fill(NaN);
          }
          numericalThroughPoints[arrayKey] = point;
        }
        return { setValue: { numericalThroughPoints } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyValuesByKey,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.numericalThroughPoints) {
          if (dependencyValuesByKey[arrayKey].throughPoint) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].throughPoint,
              desiredValue: desiredStateVariableValues.numericalThroughPoints[arrayKey].map((x2) => me$3.fromAst(x2))
            });
          } else {
            return { success: false };
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numericalPrescribedVertex = {
      returnDependencies: () => ({
        prescribedVertex: {
          dependencyType: "stateVariable",
          variableName: "prescribedVertex"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.prescribedVertex === null) {
          return { setValue: { numericalPrescribedVertex: null } };
        }
        let x2, y2;
        try {
          x2 = dependencyValues.prescribedVertex.get_component(0).evaluate_to_constant();
          y2 = dependencyValues.prescribedVertex.get_component(1).evaluate_to_constant();
          if (!(Number.isFinite(x2) && Number.isFinite(y2))) {
            x2 = NaN;
            y2 = NaN;
          }
        } catch (e3) {
          x2 = NaN;
          y2 = NaN;
        }
        return { setValue: { numericalPrescribedVertex: [x2, y2] } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.prescribedVertex === null) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "prescribedVertex",
              desiredValue: me$3.fromAst([
                "vector",
                ...desiredStateVariableValues.numericalPrescribedVertex
              ])
            }
          ]
        };
      }
    };
    stateVariableDefinitions.pointsAreNumerical = {
      returnDependencies: () => ({
        numericalThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numericalThroughPoints"
        },
        numericalPrescribedVertex: {
          dependencyType: "stateVariable",
          variableName: "numericalPrescribedVertex"
        }
      }),
      definition: ({ dependencyValues }) => ({
        // need to check just the first entry of numericalThroughPoints
        // and numericalPrescribedVertex
        setValue: {
          pointsAreNumerical: dependencyValues.numericalThroughPoints.every(
            (x2) => Number.isFinite(x2[0])
          ) && (!dependencyValues.numericalPrescribedVertex || Number.isFinite(
            dependencyValues.numericalPrescribedVertex[0]
          ))
        },
        checkForActualChange: { pointsAreNumerical: true }
      })
    };
    stateVariableDefinitions.a = {
      public: true,
      description: "The leading coefficient of the parabola (in y = a x^2 + b x + c).",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      additionalStateVariablesDefined: [
        {
          variableName: "b",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number",
            addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
          },
          hasEssential: true,
          defaultValue: 0,
          description: "The linear coefficient in y = a x^2 + b x + c."
        },
        {
          variableName: "c",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number",
            addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
          },
          hasEssential: true,
          defaultValue: 0,
          description: "The constant term in y = a x^2 + b x + c."
        },
        "realValued"
      ],
      returnDependencies: () => ({
        numericalThroughPoints: {
          dependencyType: "stateVariable",
          variableName: "numericalThroughPoints"
        },
        numericalPrescribedVertex: {
          dependencyType: "stateVariable",
          variableName: "numericalPrescribedVertex"
        },
        pointsAreNumerical: {
          dependencyType: "stateVariable",
          variableName: "pointsAreNumerical"
        },
        aShadow: {
          dependencyType: "stateVariable",
          variableName: "aShadow"
        }
      }),
      definition: function({ dependencyValues, componentIdx }) {
        if (!dependencyValues.pointsAreNumerical) {
          return {
            setValue: {
              a: NaN,
              b: NaN,
              c: NaN,
              realValued: false
            }
          };
        }
        let a2, b2, c2;
        let realValued = true;
        if (dependencyValues.numericalPrescribedVertex) {
          if (dependencyValues.numericalThroughPoints.length === 0) {
            let v2 = dependencyValues.numericalPrescribedVertex;
            let x1 = v2[0];
            let y1 = v2[1];
            a2 = dependencyValues.aShadow;
            b2 = -2 * a2 * x1;
            c2 = a2 * x1 * x1 + y1;
            return {
              setValue: {
                a: a2,
                b: b2,
                c: c2,
                realValued: true
              }
            };
          } else if (dependencyValues.numericalThroughPoints.length === 1) {
            let v2 = dependencyValues.numericalPrescribedVertex;
            let x1 = v2[0];
            let y1 = v2[1];
            let p1 = dependencyValues.numericalThroughPoints[0];
            let x2 = p1[0];
            let y2 = p1[1];
            if (x1 === x2) {
              if (y1 == y2) {
                a2 = dependencyValues.aShadow;
                b2 = -2 * a2 * x1;
                c2 = a2 * x1 ** 2 + y1;
              } else {
                realValued = false;
                a2 = NaN;
                b2 = NaN;
                c2 = NaN;
              }
            } else {
              a2 = (y2 - y1) / (x2 - x1) ** 2;
              b2 = -2 * a2 * x1;
              c2 = a2 * x1 * x1 + y1;
            }
            return { setValue: { a: a2, b: b2, c: c2, realValued } };
          } else {
            let warning = {
              message: "Haven't implemented parabola with vertex through more than 1 point.",
              type: "warning"
            };
            return {
              setValue: {
                a: NaN,
                b: NaN,
                c: NaN,
                realValued: false
              },
              sendDiagnostics: [warning]
            };
          }
        }
        if (dependencyValues.numericalThroughPoints.length === 0) {
          return {
            setValue: {
              a: dependencyValues.aShadow,
              realValued: true
            },
            useEssentialOrDefaultValue: {
              b: true,
              c: true
            }
          };
        } else if (dependencyValues.numericalThroughPoints.length === 1) {
          let p1 = dependencyValues.numericalThroughPoints[0];
          let x1 = p1[0];
          let y1 = p1[1];
          a2 = dependencyValues.aShadow;
          b2 = -2 * a2 * x1;
          c2 = a2 * x1 * x1 + y1;
          return {
            setValue: {
              a: a2,
              b: b2,
              c: c2,
              realValued: true
            }
          };
        } else if (dependencyValues.numericalThroughPoints.length === 2) {
          a2 = dependencyValues.aShadow;
          let p1 = dependencyValues.numericalThroughPoints[0];
          let x1 = p1[0];
          let y1 = p1[1];
          let x12 = x1 * x1;
          let p2 = dependencyValues.numericalThroughPoints[1];
          let x2 = p2[0];
          let y2 = p2[1];
          let x22 = x2 * x2;
          if (x1 === x2) {
            if (y1 == y2) {
              b2 = -2 * a2 * x1;
              c2 = a2 * x12 + y1;
            } else {
              realValued = false;
              a2 = NaN;
              b2 = NaN;
              c2 = NaN;
            }
          } else {
            b2 = (y1 - y2 - a2 * (x12 - x22)) / (x1 - x2);
            c2 = y1 - a2 * x12 - b2 * x1;
          }
          return { setValue: { a: a2, b: b2, c: c2, realValued } };
        } else if (dependencyValues.numericalThroughPoints.length === 3) {
          let p1 = dependencyValues.numericalThroughPoints[0];
          let x1 = p1[0];
          let y1 = p1[1];
          let x12 = x1 * x1;
          let p2 = dependencyValues.numericalThroughPoints[1];
          let x2 = p2[0];
          let y2 = p2[1];
          let x22 = x2 * x2;
          let p3 = dependencyValues.numericalThroughPoints[2];
          let x3 = p3[0];
          let y3 = p3[1];
          let x32 = x3 * x3;
          let u1 = x12 - x32;
          let u2 = x22 - x32;
          let v1 = x1 - x3;
          let v2 = x2 - x3;
          let z1 = y1 - y3;
          let z2 = y2 - y3;
          let det = u1 * v2 - u2 * v1;
          if (det === 0) {
            let foundInconsistentPoints = false;
            let nUniquePoints;
            let ux2, uy2;
            if (x1 === x2) {
              if (y1 === y2) {
                if (x1 === x3) {
                  if (y1 == y3) {
                    nUniquePoints = 1;
                  } else {
                    foundInconsistentPoints = true;
                  }
                } else {
                  nUniquePoints = 2;
                  ux2 = x3;
                  uy2 = y3;
                }
              } else {
                foundInconsistentPoints = true;
              }
            } else if (x1 === x3) {
              if (y1 === y3) {
                nUniquePoints = 2;
                ux2 = x2;
                uy2 = y2;
              } else {
                foundInconsistentPoints = true;
              }
            } else if (x2 === x3) {
              if (y2 === y3) {
                nUniquePoints = 2;
                ux2 = x2;
                uy2 = y2;
              } else {
                foundInconsistentPoints = true;
              }
            } else {
              foundInconsistentPoints = true;
            }
            if (foundInconsistentPoints) {
              realValued = false;
              a2 = NaN;
              b2 = NaN;
              c2 = NaN;
            } else if (nUniquePoints === 1) {
              a2 = dependencyValues.aShadow;
              b2 = -2 * a2 * x1;
              c2 = a2 * x1 * x1 + y1;
            } else {
              a2 = dependencyValues.aShadow;
              b2 = (y1 - uy2 - a2 * (x12 - ux2 * ux2)) / (x1 - ux2);
              c2 = y1 - a2 * x12 - b2 * x1;
            }
          } else {
            a2 = (z1 * v2 - z2 * v1) / det;
            b2 = (z2 * u1 - z1 * u2) / det;
            c2 = y1 - b2 * x1 - a2 * x12;
          }
          return { setValue: { a: a2, b: b2, c: c2, realValued } };
        } else {
          let warning = {
            message: "Haven't implemented parabola through more than 3 points.",
            type: "warning"
          };
          return {
            setValue: {
              a: NaN,
              b: NaN,
              c: NaN,
              realValued: false
            },
            sendDiagnostics: [warning]
          };
        }
      },
      inverseDefinition: async function({
        desiredStateVariableValues,
        dependencyValues,
        stateValues,
        workspace
      }) {
        if (!dependencyValues.pointsAreNumerical) {
          return { success: false };
        }
        let desiredNumericalValues = {};
        let findNumericalValue = function(par) {
          if (Number.isFinite(par)) {
            return { success: true, value: par };
          } else if (par instanceof me$3.class) {
            let val = par.evaluate_to_constant();
            if (Number.isFinite(val)) {
              return { success: true, value: val };
            }
          }
          return { success: false };
        };
        if (desiredStateVariableValues.a !== void 0) {
          let numerical = findNumericalValue(
            desiredStateVariableValues.a
          );
          if (numerical.success) {
            desiredNumericalValues.a = numerical.value;
          } else {
            return { success: false };
          }
        }
        if (desiredStateVariableValues.b !== void 0) {
          let numerical = findNumericalValue(
            desiredStateVariableValues.b
          );
          if (numerical.success) {
            desiredNumericalValues.b = numerical.value;
          } else {
            return { success: false };
          }
        }
        if (desiredStateVariableValues.c !== void 0) {
          let numerical = findNumericalValue(
            desiredStateVariableValues.c
          );
          if (numerical.success) {
            desiredNumericalValues.c = numerical.value;
          } else {
            return { success: false };
          }
        }
        Object.assign(workspace, desiredNumericalValues);
        let getWorkingParameterValue = async function(parName) {
          if (workspace[parName] !== void 0) {
            return workspace[parName];
          } else {
            return await stateValues[parName];
          }
        };
        if (dependencyValues.numericalPrescribedVertex) {
          if (dependencyValues.numericalThroughPoints.length === 0) {
            let a2 = await getWorkingParameterValue("a");
            let b2 = await getWorkingParameterValue("b");
            let c2 = await getWorkingParameterValue("c");
            let x1 = -b2 / (2 * a2);
            let y1 = c2 - b2 * b2 / (4 * a2);
            let instructions = [
              {
                setDependency: "numericalPrescribedVertex",
                desiredValue: [x1, y1]
              }
            ];
            if (desiredNumericalValues.a !== void 0) {
              instructions.push({
                setDependency: "aShadow",
                desiredValue: desiredNumericalValues.a
              });
            }
            return {
              success: true,
              instructions
            };
          } else {
            let a2 = await getWorkingParameterValue("a");
            let b2 = await getWorkingParameterValue("b");
            let c2 = await getWorkingParameterValue("c");
            let vx = -b2 / (2 * a2);
            let vy = c2 - b2 * b2 / (4 * a2);
            let dx = vx - dependencyValues.numericalPrescribedVertex[0];
            let px = dependencyValues.numericalThroughPoints[0][0] + dx;
            let py = a2 * px * px + b2 * px + c2;
            let instructions = [
              {
                setDependency: "numericalPrescribedVertex",
                desiredValue: [vx, vy]
              },
              {
                setDependency: "numericalThroughPoints",
                desiredValue: [[px, py]]
              }
            ];
            if (desiredNumericalValues.a !== void 0) {
              instructions.push({
                setDependency: "aShadow",
                desiredValue: desiredNumericalValues.a
              });
            }
            return {
              success: true,
              instructions
            };
          }
        }
        if (dependencyValues.numericalThroughPoints.length === 0) {
          let instructions = [];
          if (desiredNumericalValues.a !== void 0) {
            instructions.push({
              setDependency: "aShadow",
              desiredValue: desiredNumericalValues.a
            });
          }
          if (desiredNumericalValues.b !== void 0) {
            instructions.push({
              setEssentialValue: "b",
              value: desiredNumericalValues.b
            });
          }
          if (desiredNumericalValues.c !== void 0) {
            instructions.push({
              setEssentialValue: "c",
              value: desiredNumericalValues.c
            });
          }
          return {
            success: true,
            instructions
          };
        } else if (dependencyValues.numericalThroughPoints.length === 1) {
          let a2 = await getWorkingParameterValue("a");
          let b2 = await getWorkingParameterValue("b");
          let c2 = await getWorkingParameterValue("c");
          let x1 = -b2 / (2 * a2);
          let y1 = c2 - b2 * b2 / (4 * a2);
          let instructions = [
            {
              setDependency: "numericalThroughPoints",
              desiredValue: [[x1, y1]]
            }
          ];
          if (desiredNumericalValues.a !== void 0) {
            instructions.push({
              setDependency: "aShadow",
              desiredValue: desiredNumericalValues.a
            });
          }
          return {
            success: true,
            instructions
          };
        } else if (dependencyValues.numericalThroughPoints.length === 2) {
          let a2 = await getWorkingParameterValue("a");
          let b2 = await getWorkingParameterValue("b");
          let c2 = await getWorkingParameterValue("c");
          let p1 = dependencyValues.numericalThroughPoints[0];
          let x1 = p1[0];
          let p2 = dependencyValues.numericalThroughPoints[1];
          let x2 = p2[0];
          if (x1 === x2) {
            x1 = -b2 / (2 * a2);
            let y1 = c2 - b2 * b2 / (4 * a2);
            let instructions = [
              {
                setDependency: "numericalThroughPoints",
                desiredValue: [
                  [x1, y1],
                  [x1, y1]
                ]
              }
            ];
            if (desiredNumericalValues.a !== void 0) {
              instructions.push({
                setDependency: "aShadow",
                desiredValue: desiredNumericalValues.a
              });
            }
            return {
              success: true,
              instructions
            };
          } else {
            let y1 = a2 * x1 * x1 + b2 * x1 + c2;
            let y2 = a2 * x2 * x2 + b2 * x2 + c2;
            let instructions = [
              {
                setDependency: "numericalThroughPoints",
                desiredValue: [
                  [x1, y1],
                  [x2, y2]
                ]
              }
            ];
            if (desiredNumericalValues.a !== void 0) {
              instructions.push({
                setDependency: "aShadow",
                desiredValue: desiredNumericalValues.a
              });
            }
            return {
              success: true,
              instructions
            };
          }
        } else if (dependencyValues.numericalThroughPoints.length === 3) {
          let a2 = await getWorkingParameterValue("a");
          let b2 = await getWorkingParameterValue("b");
          let c2 = await getWorkingParameterValue("c");
          let p1 = dependencyValues.numericalThroughPoints[0];
          let x1 = p1[0];
          let p2 = dependencyValues.numericalThroughPoints[1];
          let x2 = p2[0];
          let p3 = dependencyValues.numericalThroughPoints[2];
          let x3 = p3[0];
          let nUniquePoints = 3;
          let nonIdenticalPointInd;
          if (x1 === x2) {
            if (x1 === x3) {
              nUniquePoints = 1;
            } else {
              nUniquePoints = 2;
              nonIdenticalPointInd = 3;
            }
          } else if (x1 === x3) {
            nUniquePoints = 2;
            nonIdenticalPointInd = 2;
          } else if (x2 === x3) {
            nUniquePoints = 2;
            nonIdenticalPointInd = 1;
          }
          if (nUniquePoints === 3) {
            let y1 = a2 * x1 * x1 + b2 * x1 + c2;
            let y2 = a2 * x2 * x2 + b2 * x2 + c2;
            let y3 = a2 * x3 * x3 + b2 * x3 + c2;
            let instructions = [
              {
                setDependency: "numericalThroughPoints",
                desiredValue: [
                  [x1, y1],
                  [x2, y2],
                  [x3, y3]
                ]
              }
            ];
            if (desiredNumericalValues.a !== void 0) {
              instructions.push({
                setDependency: "aShadow",
                desiredValue: desiredNumericalValues.a
              });
            }
            return {
              success: true,
              instructions
            };
          } else if (nUniquePoints === 2) {
            if (nonIdenticalPointInd === 1) {
              let ux1 = x1, ux2 = x2;
              let uy1 = a2 * ux1 * ux1 + b2 * ux1 + c2;
              let uy2 = a2 * ux2 * ux2 + b2 * ux2 + c2;
              let instructions = [
                {
                  setDependency: "numericalThroughPoints",
                  desiredValue: [
                    [ux1, uy1],
                    [ux2, uy2],
                    [ux2, uy2]
                  ]
                }
              ];
              if (desiredNumericalValues.a !== void 0) {
                instructions.push({
                  setDependency: "aShadow",
                  desiredValue: desiredNumericalValues.a
                });
              }
              return {
                success: true,
                instructions
              };
            } else if (nonIdenticalPointInd === 2) {
              let ux1 = x1, ux2 = x2;
              let uy1 = a2 * ux1 * ux1 + b2 * ux1 + c2;
              let uy2 = a2 * ux2 * ux2 + b2 * ux2 + c2;
              let instructions = [
                {
                  setDependency: "numericalThroughPoints",
                  desiredValue: [
                    [ux1, uy1],
                    [ux2, uy2],
                    [ux1, uy1]
                  ]
                }
              ];
              if (desiredNumericalValues.a !== void 0) {
                instructions.push({
                  setDependency: "aShadow",
                  desiredValue: desiredNumericalValues.a
                });
              }
              return {
                success: true,
                instructions
              };
            } else {
              let ux1 = x1, ux2 = x3;
              let uy1 = a2 * ux1 * ux1 + b2 * ux1 + c2;
              let uy2 = a2 * ux2 * ux2 + b2 * ux2 + c2;
              let instructions = [
                {
                  setDependency: "numericalThroughPoints",
                  desiredValue: [
                    [ux1, uy1],
                    [ux1, uy1],
                    [ux2, uy2]
                  ]
                }
              ];
              if (desiredNumericalValues.a !== void 0) {
                instructions.push({
                  setDependency: "aShadow",
                  desiredValue: desiredNumericalValues.a
                });
              }
              return {
                success: true,
                instructions
              };
            }
          } else {
            x1 = -b2 / (2 * a2);
            let y1 = c2 - b2 * b2 / (4 * a2);
            let instructions = [
              {
                setDependency: "numericalThroughPoints",
                desiredValue: [
                  [x1, y1],
                  [x1, y1],
                  [x1, y1]
                ]
              }
            ];
            if (desiredNumericalValues.a !== void 0) {
              instructions.push({
                setDependency: "aShadow",
                desiredValue: desiredNumericalValues.a
              });
            }
            return {
              success: true,
              instructions
            };
          }
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.vertex = {
      description: "The vertex coordinates of the parabola.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "vertexX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["vertexX"],
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          a: {
            dependencyType: "stateVariable",
            variableName: "a"
          },
          b: {
            dependencyType: "stateVariable",
            variableName: "b"
          },
          c: {
            dependencyType: "stateVariable",
            variableName: "c"
          },
          realValued: {
            dependencyType: "stateVariable",
            variableName: "realValued"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey: function({ globalDependencyValues }) {
        let vertex = {};
        if (globalDependencyValues.realValued && globalDependencyValues.a !== 0) {
          vertex[0] = me$3.fromAst(
            -globalDependencyValues.b / (2 * globalDependencyValues.a)
          );
          vertex[1] = me$3.fromAst(
            globalDependencyValues.c - globalDependencyValues.b ** 2 / (4 * globalDependencyValues.a)
          );
        } else {
          vertex[0] = me$3.fromAst("＿");
          vertex[1] = me$3.fromAst("＿");
        }
        return { setValue: { vertex } };
      },
      inverseArrayDefinitionByKey: async function({
        desiredStateVariableValues,
        globalDependencyValues,
        workspace,
        stateValues
      }) {
        let x2;
        if ("0" in desiredStateVariableValues.vertex) {
          x2 = desiredStateVariableValues.vertex[0].evaluate_to_constant();
        } else if (workspace.x !== void 0) {
          x2 = workspace.x;
        } else {
          x2 = (await stateValues.vertex)[0].tree;
        }
        if (Number.isFinite(x2)) {
          workspace.x = x2;
        } else {
          return { success: false };
        }
        let y2;
        if ("1" in desiredStateVariableValues.vertex) {
          y2 = desiredStateVariableValues.vertex[1].evaluate_to_constant();
        } else if (workspace.y !== void 0) {
          y2 = workspace.y;
        } else {
          y2 = (await stateValues.vertex)[1].tree;
        }
        if (Number.isFinite(y2)) {
          workspace.y = y2;
        } else {
          return { success: false };
        }
        let a2 = globalDependencyValues.a;
        let b2 = -2 * a2 * x2;
        let c2 = y2 + a2 * x2 * x2;
        return {
          success: true,
          instructions: [
            {
              setDependency: "b",
              desiredValue: b2,
              additionalDependencyValues: { c: c2 }
            }
          ]
        };
      }
    };
    stateVariableDefinitions.equation = {
      description: "The parabola's equation as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      // TODO: implement additional properties
      additionalProperties: {
        simplify: "numberspreserveorder",
        displaysmallaszero: true
      },
      returnDependencies: () => ({
        a: {
          dependencyType: "stateVariable",
          variableName: "a"
        },
        b: {
          dependencyType: "stateVariable",
          variableName: "b"
        },
        c: {
          dependencyType: "stateVariable",
          variableName: "c"
        }
      }),
      definition: function({ dependencyValues }) {
        let ast = [
          "=",
          "y",
          [
            "+",
            ["*", dependencyValues.a, ["^", "x", 2]],
            ["*", dependencyValues.b, "x"],
            dependencyValues.c
          ]
        ];
        let equation = me$3.fromAst(ast).evaluate_numbers({ skip_ordering: true });
        return { setValue: { equation } };
      }
    };
    stateVariableDefinitions.fs = {
      forRenderer: true,
      isArray: true,
      entryPrefixes: ["f"],
      additionalStateVariablesDefined: [
        {
          variableName: "fDefinitions",
          isArray: true,
          forRenderer: true
        }
      ],
      returnArraySizeDependencies: () => ({}),
      returnArraySize: () => [1],
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          a: {
            dependencyType: "stateVariable",
            variableName: "a"
          },
          b: {
            dependencyType: "stateVariable",
            variableName: "b"
          },
          c: {
            dependencyType: "stateVariable",
            variableName: "c"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let f2 = function(x2) {
          return globalDependencyValues.a * x2 * x2 + globalDependencyValues.b * x2 + globalDependencyValues.c;
        };
        return {
          setValue: {
            fs: [f2],
            fDefinitions: [
              {
                functionType: "formula",
                formula: [
                  "+",
                  ["*", globalDependencyValues.a, "x", "x"],
                  ["*", globalDependencyValues.b, "x"],
                  globalDependencyValues.c
                ],
                variables: ["x"],
                numInputs: 1,
                numOutputs: 1,
                domain: null
              }
            ]
          }
        };
      }
    };
    stateVariableDefinitions.f = {
      isAlias: true,
      targetVariableName: "f1",
      description: "The parabola as a function of its parameter (alias to the first component function)."
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        f: {
          dependencyType: "stateVariable",
          variableName: "f"
        },
        a: {
          dependencyType: "stateVariable",
          variableName: "a"
        },
        b: {
          dependencyType: "stateVariable",
          variableName: "b"
        },
        c: {
          dependencyType: "stateVariable",
          variableName: "c"
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let skip = !(Number.isFinite(dependencyValues.a) && Number.isFinite(dependencyValues.b) && Number.isFinite(dependencyValues.c));
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              if (skip) {
                return {};
              }
              let xscale = scales[0];
              let yscale = scales[1];
              let x1 = variables.x1?.evaluate_to_constant();
              let x2 = variables.x2?.evaluate_to_constant();
              if (!(Number.isFinite(x1) && Number.isFinite(x2))) {
                return {};
              }
              x1 /= xscale;
              x2 /= yscale;
              let a0 = dependencyValues.a * xscale * xscale / yscale;
              let b0 = dependencyValues.b * xscale / yscale;
              let c0 = dependencyValues.c / yscale;
              if (a0 === 0) {
                let denom = b0 * b0 + 1;
                let result2 = {};
                result2.x1 = (x1 + b0 * x2 - b0 * c0) / denom * xscale;
                result2.x2 = (b0 * (x1 + b0 * x2) + c0) / denom * yscale;
                if (variables.x3 !== void 0) {
                  result2.x3 = 0;
                }
                return result2;
              }
              let d2 = c0 - x2;
              let a2 = 2 * a0 ** 2;
              let b2 = 3 * a0 * b0 / a2;
              let c2 = (2 * a0 * d2 + b0 ** 2 + 1) / a2;
              let d3 = (b0 * d2 - x1) / a2;
              let resultCardano = cardano(b2, c2, d3, 1e-14);
              let x1AtMin = resultCardano[0];
              let x2AtMin = dependencyValues.f(x1AtMin * xscale) / yscale;
              let d2AtMin = (x1 - x1AtMin) ** 2 + (x2 - x2AtMin) ** 2;
              for (let r2 of resultCardano.slice(1)) {
                let x3 = r2;
                let fx = dependencyValues.f(x3 * xscale) / yscale;
                let d22 = (x1 - x3) ** 2 + (x2 - fx) ** 2;
                if (d22 < d2AtMin) {
                  x1AtMin = x3;
                  x2AtMin = fx;
                  d2AtMin = d22;
                }
              }
              let result = {
                x1: x1AtMin * xscale,
                x2: x2AtMin * yscale
              };
              if (variables.x3 !== void 0) {
                result.x3 = 0;
              }
              return result;
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  // parameterizationMin = 0;
  // parameterizationMax = 1;
  // parameterizationPeriodic = true;
}
function cardano(b2, c2, d2, ε = 1e-10) {
  const [p2, q2] = [
    -1 / 3 * b2 * b2 + c2,
    2 / 27 * b2 * b2 * b2 - 1 / 3 * b2 * c2 + d2
  ];
  if (Math.abs(q2) <= ε) {
    if (Math.abs(p2) <= ε) return [-b2 / 3];
    let s2 = Math.sqrt(Math.abs(p2));
    if (p2 < 0) return [-s2 - b2 / 3, -b2 / 3, s2 - b2 / 3];
    return [-b2 / 3];
  }
  const Δ = -27 * q2 * q2 - 4 * p2 * p2 * p2;
  if (Math.abs(Δ) <= ε) {
    const cr2 = Math.cbrt(-q2 / 2);
    return [2 * cr2 - b2 / 3, -cr2 - b2 / 3];
  }
  if (Δ > 0) {
    const D3 = Math.sqrt(Δ / 108);
    const mod2 = Math.sqrt(Math.cbrt(q2 * q2 / 4 + Δ / 108)) * 2;
    const arg = Math.atan2(D3, -q2 / 2);
    return [
      Math.cos(arg / 3),
      Math.cos(arg / 3 + 2 * Math.PI / 3),
      Math.cos(arg / 3 - 2 * Math.PI / 3)
    ].sort((x2, y2) => x2 - y2).map((x2) => mod2 * x2 - b2 / 3);
  }
  const D2 = Math.sqrt(-Δ / 108);
  const α = Math.cbrt(-q2 / 2 + D2), β = Math.cbrt(-q2 / 2 - D2), r2 = α + β - b2 / 3;
  return [r2];
}
class BezierControls extends InlineComponent {
  static componentType = "bezierControls";
  static componentDocs = {
    summary: "Defines Bezier control vectors at points along a curve"
  };
  static rendererType = "containerInline";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.alwaysVisible = {
      createComponentOfType: "boolean",
      createStateVariable: "alwaysVisible",
      defaultValue: false,
      public: true,
      description: "Whether Bezier control handles are always shown (rather than only when the curve is selected)."
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "number", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let groupIntoVectorsSeparatedBySpacesOutsideParens = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
      componentType: "vector"
    });
    let createControlVectorsList = function({
      matchedChildren,
      nComponents,
      stateIdInfo,
      componentInfoObjects
    }) {
      let results = groupIntoVectorsSeparatedBySpacesOutsideParens({
        matchedChildren,
        nComponents
      });
      if (!results.success) {
        return { success: false };
      }
      nComponents = results.nComponents;
      return {
        success: true,
        newChildren: results.newChildren.map(function(child) {
          if (!componentInfoObjects.componentIsSpecifiedType(
            child,
            "controlVectors"
          )) {
            return {
              type: "serialized",
              componentType: "controlVectors",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: [child],
              attributes: {},
              doenetAttributes: {},
              state: {}
            };
          } else {
            return child;
          }
        }),
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: createControlVectorsList
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "controlVectors",
        componentTypes: ["controlVectors"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numControls = {
      returnDependencies: () => ({
        nParentPoints: {
          dependencyType: "parentStateVariable",
          variableName: "numThroughPoints",
          skipComponentIndices: true
        }
      }),
      definition({ dependencyValues }) {
        let numControls = dependencyValues.nParentPoints;
        if (!(Number.isInteger(numControls) && numControls >= 0)) {
          numControls = 0;
        }
        return { setValue: { numControls } };
      }
    };
    stateVariableDefinitions.pointIndMap = {
      returnDependencies: () => ({
        controlChildren: {
          dependencyType: "child",
          childGroups: ["controlVectors"],
          variableNames: ["pointNumber"]
        }
      }),
      definition: function({ dependencyValues }) {
        let pointIndMap = [];
        let currentPointInd = -1;
        for (let [
          controlInd,
          controlChild
        ] of dependencyValues.controlChildren.entries()) {
          let pointNumber = controlChild.stateValues.pointNumber;
          if (Number.isFinite(pointNumber)) {
            currentPointInd = Math.round(pointNumber) - 1;
          } else {
            currentPointInd += 1;
          }
          pointIndMap[currentPointInd] = controlInd;
        }
        return { setValue: { pointIndMap } };
      }
    };
    stateVariableDefinitions.directions = {
      isArray: true,
      entryPrefixes: ["direction"],
      hasEssential: true,
      isLocation: true,
      defaultValueByArrayKey: () => "none",
      stateVariablesDeterminingDependencies: ["pointIndMap"],
      returnArraySizeDependencies: () => ({
        numControls: {
          dependencyType: "stateVariable",
          variableName: "numControls"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numControls];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let controlInd = stateValues.pointIndMap[arrayKey];
          if (controlInd !== void 0) {
            dependenciesByKey[arrayKey] = {
              controlChild: {
                dependencyType: "child",
                childGroups: ["controlVectors"],
                variableNames: ["direction"],
                childIndices: [controlInd]
              }
            };
          }
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let directions = {};
        let essentialDirections = {};
        for (let arrayKey of arrayKeys) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length === 1) {
            directions[arrayKey] = controlChild[0].stateValues.direction;
          } else {
            essentialDirections[arrayKey] = true;
          }
        }
        return {
          setValue: { directions },
          useEssentialOrDefaultValue: {
            directions: essentialDirections
          }
        };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey
        // componentIdx
      }) {
        let instructions = [];
        let newDirectionValues = {};
        for (let arrayKey in desiredStateVariableValues.directions) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length === 1) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].controlChild,
              desiredValue: desiredStateVariableValues.directions[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else {
            newDirectionValues[arrayKey] = desiredStateVariableValues.directions[arrayKey];
          }
        }
        if (Object.keys(newDirectionValues).length > 0) {
          instructions.push({
            setEssentialValue: "directions",
            value: newDirectionValues
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.hiddenControls = {
      isArray: true,
      entryPrefixes: ["hiddenControl"],
      hasEssential: true,
      defaultValueByArrayKey: () => false,
      stateVariablesDeterminingDependencies: ["pointIndMap"],
      returnArraySizeDependencies: () => ({
        numControls: {
          dependencyType: "stateVariable",
          variableName: "numControls"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numControls];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let controlInd = stateValues.pointIndMap[arrayKey];
          if (controlInd !== void 0) {
            dependenciesByKey[arrayKey] = {
              controlChild: {
                dependencyType: "child",
                childGroups: ["controlVectors"],
                variableNames: ["hide"],
                childIndices: [controlInd]
              }
            };
          }
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let hiddenControls = {};
        let essentialHiddenControls = {};
        for (let arrayKey of arrayKeys) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length === 1) {
            hiddenControls[arrayKey] = controlChild[0].stateValues.hide;
          } else {
            essentialHiddenControls[arrayKey] = true;
          }
        }
        return {
          setValue: { hiddenControls },
          useEssentialOrDefaultValue: {
            hiddenControls: essentialHiddenControls
          }
        };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey
        // componentIdx
      }) {
        let instructions = [];
        let newHiddenControlValues = {};
        for (let arrayKey in desiredStateVariableValues.hiddenControls) {
          let controlChild = dependencyValuesByKey[arrayKey].controlChild;
          if (controlChild && controlChild.length === 1) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].controlChild,
              desiredValue: desiredStateVariableValues.hiddenControls[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else {
            newHiddenControlValues[arrayKey] = desiredStateVariableValues.hiddenControls[arrayKey];
          }
        }
        if (Object.keys(newHiddenControlValues).length > 0) {
          instructions.push({
            setEssentialValue: "hiddenControls",
            value: newHiddenControlValues
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      returnDependencies() {
        return {
          nParentDimensions: {
            dependencyType: "parentStateVariable",
            variableName: "numDimensions"
          }
        };
      },
      definition: function({ dependencyValues }) {
        let numDimensions = dependencyValues.nParentDimensions;
        if (!(Number.isInteger(numDimensions) && numDimensions >= 0)) {
          numDimensions = 0;
        }
        return {
          setValue: { numDimensions },
          checkForActualChange: { numDimensions: true }
        };
      }
    };
    stateVariableDefinitions.essentialSymmetricControls = {
      isArray: true,
      entryPrefixes: ["essentialSymmetricControl"],
      numDimensions: 2,
      hasEssential: true,
      isLocation: true,
      defaultValueByArrayKey: () => me$3.fromAst(1),
      returnArraySizeDependencies: () => ({
        numControls: {
          dependencyType: "stateVariable",
          variableName: "numControls"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numControls,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey: () => ({}),
      arrayDefinitionByKey({ arrayKeys }) {
        let essentialControls = {};
        for (let arrayKey of arrayKeys) {
          arrayKey.split(",").map((x2) => Number(x2));
          essentialControls[arrayKey] = true;
        }
        return {
          useEssentialOrDefaultValue: {
            essentialSymmetricControls: essentialControls
          }
        };
      },
      inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
        let instructions = [];
        let newControlValues = {};
        for (let arrayKey in desiredStateVariableValues.essentialSymmetricControls) {
          newControlValues[arrayKey] = desiredStateVariableValues.essentialSymmetricControls[arrayKey];
        }
        if (Object.keys(newControlValues).length > 0) {
          instructions.push({
            setEssentialValue: "essentialSymmetricControls",
            value: newControlValues
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.controls = {
      isArray: true,
      entryPrefixes: ["control"],
      numDimensions: 3,
      hasEssential: true,
      shadowVariable: true,
      isLocation: true,
      defaultValueByArrayKey: () => me$3.fromAst(1),
      stateVariablesDeterminingDependencies: [
        "pointIndMap",
        "directions"
      ],
      returnArraySizeDependencies: () => ({
        numControls: {
          dependencyType: "stateVariable",
          variableName: "numControls"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numControls,
          2,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          dependenciesByKey[arrayKey] = {
            direction: {
              dependencyType: "stateVariable",
              variableName: "direction" + varEndings[0]
            }
          };
          let direction = stateValues.directions[arrayIndices[0]];
          if (direction !== "none" && (arrayIndices[1] === 0 || arrayIndices[1] === 1)) {
            let controlInd = stateValues.pointIndMap[arrayIndices[0]];
            if (controlInd !== void 0) {
              if (direction === "symmetric" || direction === "previous" && arrayIndices[1] === 0 || direction === "next" && arrayIndices[1] === 1) {
                dependenciesByKey[arrayKey].controlChild = {
                  dependencyType: "child",
                  childGroups: ["controlVectors"],
                  variableNames: [
                    "vectorX1_" + varEndings[2]
                  ],
                  childIndices: [controlInd]
                };
              } else if (direction === "both") {
                dependenciesByKey[arrayKey].controlChild = {
                  dependencyType: "child",
                  childGroups: ["controlVectors"],
                  variableNames: [
                    "vectorX" + varEndings[1] + "_" + varEndings[2]
                  ],
                  childIndices: [controlInd]
                };
              }
            }
            if (direction === "symmetric") {
              dependenciesByKey[arrayKey].essentialSymmetricControl = {
                dependencyType: "stateVariable",
                variableName: "essentialSymmetricControl" + varEndings[0] + "_" + varEndings[2]
              };
            }
          }
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({
        dependencyValuesByKey,
        arrayKeys,
        componentIdx
      }) {
        let newControlValues = {};
        let essentialControls = {};
        for (let arrayKey of arrayKeys) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let direction = dependencyValuesByKey[arrayKey].direction;
          if (arrayIndices[1] === 0) {
            if (direction === "none" || direction === "next") {
              newControlValues[arrayKey] = null;
            } else {
              let controlChild = dependencyValuesByKey[arrayKey].controlChild;
              let useEssential = true;
              if (controlChild && controlChild.length === 1) {
                let value = controlChild[0].stateValues["vectorX1_" + varEndings[2]];
                if (value) {
                  useEssential = false;
                  value = value.evaluate_to_constant();
                  newControlValues[arrayKey] = me$3.fromAst(value);
                }
              }
              if (useEssential) {
                if (direction === "symmetric") {
                  newControlValues[arrayKey] = dependencyValuesByKey[arrayKey].essentialSymmetricControl;
                } else {
                  essentialControls[arrayKey] = true;
                }
              }
            }
          } else if (arrayIndices[1] === 1) {
            if (direction === "none" || direction === "previous") {
              newControlValues[arrayKey] = null;
            } else {
              let controlChild = dependencyValuesByKey[arrayKey].controlChild;
              let useEssential = true;
              if (controlChild && controlChild.length === 1) {
                if (direction === "both") {
                  let value = controlChild[0].stateValues["vectorX2_" + varEndings[2]];
                  if (value) {
                    useEssential = false;
                    value = value.evaluate_to_constant();
                    newControlValues[arrayKey] = me$3.fromAst(value);
                  }
                } else {
                  let value = controlChild[0].stateValues["vectorX1_" + varEndings[2]];
                  if (value) {
                    useEssential = false;
                    value = value.evaluate_to_constant();
                    if (direction === "symmetric") {
                      newControlValues[arrayKey] = me$3.fromAst(-value);
                    } else {
                      newControlValues[arrayKey] = me$3.fromAst(value);
                    }
                  }
                }
              }
              if (useEssential) {
                if (direction === "symmetric") {
                  if (dependencyValuesByKey[arrayKey].essentialSymmetricControl) {
                    newControlValues[arrayKey] = me$3.fromAst(
                      -dependencyValuesByKey[arrayKey].essentialSymmetricControl.tree
                    );
                  }
                } else {
                  essentialControls[arrayKey] = true;
                }
              }
            }
          }
        }
        return {
          setValue: {
            controls: newControlValues
          },
          useEssentialOrDefaultValue: {
            controls: essentialControls
          }
        };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey,
        dependencyValuesByKey
      }) {
        let instructions = [];
        let newControlValues = {};
        for (let arrayKey in desiredStateVariableValues.controls) {
          let arrayIndices = arrayKey.split(",").map((x2) => Number(x2));
          let varEndings = arrayIndices.map((x2) => x2 + 1);
          let direction = dependencyValuesByKey[arrayKey].direction;
          if (direction) {
            if (arrayIndices[1] === 0) {
              if (!(direction === "none" || direction === "next")) {
                let controlChild = dependencyValuesByKey[arrayKey].controlChild;
                let useEssential = true;
                if (controlChild && controlChild.length === 1) {
                  let value = controlChild[0].stateValues["vectorX1_" + varEndings[2]];
                  if (value) {
                    useEssential = false;
                    instructions.push({
                      setDependency: dependencyNamesByKey[arrayKey].controlChild,
                      desiredValue: desiredStateVariableValues.controls[arrayKey],
                      childIndex: 0,
                      variableIndex: 0
                    });
                  }
                }
                if (useEssential) {
                  let desiredValue = me$3.fromAst(
                    desiredStateVariableValues.controls[arrayKey].evaluate_to_constant()
                  );
                  if (direction === "symmetric") {
                    instructions.push({
                      setDependency: dependencyNamesByKey[arrayKey].essentialSymmetricControl,
                      desiredValue
                    });
                  } else {
                    newControlValues[arrayKey] = desiredValue;
                  }
                }
              }
            } else if (arrayIndices[1] === 1) {
              if (!(direction === "none" || direction === "previous")) {
                let controlChild = dependencyValuesByKey[arrayKey].controlChild;
                let useEssential = true;
                if (controlChild && controlChild.length === 1) {
                  if (direction === "both") {
                    let value = controlChild[0].stateValues["vectorX2_" + varEndings[2]];
                    if (value) {
                      useEssential = false;
                    }
                  } else {
                    let value = controlChild[0].stateValues["vectorX1_" + varEndings[2]];
                    if (value) {
                      useEssential = false;
                    }
                  }
                }
                let desiredValue;
                if (direction === "symmetric") {
                  desiredValue = me$3.fromAst([
                    "-",
                    desiredStateVariableValues.controls[arrayKey].tree
                  ]);
                } else {
                  desiredValue = desiredStateVariableValues.controls[arrayKey];
                }
                if (useEssential) {
                  desiredValue = me$3.fromAst(
                    desiredValue.evaluate_to_constant()
                  );
                  if (direction === "symmetric") {
                    instructions.push({
                      setDependency: dependencyNamesByKey[arrayKey].essentialSymmetricControl,
                      desiredValue
                    });
                  } else {
                    newControlValues[arrayKey] = desiredValue;
                  }
                } else {
                  instructions.push({
                    setDependency: dependencyNamesByKey[arrayKey].controlChild,
                    desiredValue,
                    childIndex: 0,
                    variableIndex: 0
                  });
                }
              }
            }
          }
        }
        if (Object.keys(newControlValues).length > 0) {
          instructions.push({
            setEssentialValue: "controls",
            value: newControlValues
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class VectorListComponent extends CompositeComponent {
  static componentType = "vectorList";
  static componentDocs = {
    summary: "A list of vectors"
  };
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static allowInSchemaAsComponent = ["vector"];
  // when another component has an attribute that is a vectorList,
  // use the vectors state variable to populate that attribute
  static stateVariableToBeShadowed = "vectors";
  static primaryStateVariableForDefinition = "vectorsShadow";
  // even if inside a component that turned on descendantCompositesMustHaveAReplacement
  // don't required composite replacements
  static descendantCompositesMustHaveAReplacement = false;
  static doNotExpandAsShadowed = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedPrelim",
      defaultValue: false,
      description: "Whether the order of vectors in this list should be treated as unordered (e.g. for matching)."
    };
    attributes.maxNumber = {
      description: "Maximum number of vectors to retain in the list.",
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: Infinity,
      public: true
    };
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this component is treated as a response for the purposes of assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    const numberDisplayAttrs = returnNumberDisplayAttributes();
    for (let attrName in numberDisplayAttrs) {
      attributes[attrName] = {
        leaveRaw: true,
        description: numberDisplayAttrs[attrName].description
      };
    }
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string", "math"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let groupIntoVectorsSeparatedBySpacesOutsideParens = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
      componentType: "vector"
    });
    sugarInstructions.push({
      replacementFunction: function({
        matchedChildren,
        nComponents,
        stateIdInfo
      }) {
        return groupIntoVectorsSeparatedBySpacesOutsideParens({
          matchedChildren,
          nComponents,
          stateIdInfo
        });
      }
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "vectors",
        componentTypes: ["vector"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnUnorderedListStateVariableDefinitions()
    );
    stateVariableDefinitions.vectorsShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          vectorsShadow: true
        }
      })
    };
    stateVariableDefinitions.numVectors = {
      description: "The number of vectors in the list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        maxNumber: {
          dependencyType: "stateVariable",
          variableName: "maxNumber"
        },
        vectorChildren: {
          dependencyType: "child",
          childGroups: ["vectors"],
          skipComponentIndices: true
        },
        vectorsShadow: {
          dependencyType: "stateVariable",
          variableName: "vectorsShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        let numVectors = 0;
        if (dependencyValues.vectorChildren.length > 0) {
          numVectors = dependencyValues.vectorChildren.length;
        } else if (dependencyValues.vectorsShadow !== null) {
          numVectors = dependencyValues.vectorsShadow.length;
        }
        let maxNum = dependencyValues.maxNumber;
        if (numVectors > maxNum) {
          numVectors = maxNum;
        }
        return {
          setValue: { numVectors },
          checkForActualChange: { numVectors: true }
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      returnDependencies: () => ({
        maxNumber: {
          dependencyType: "stateVariable",
          variableName: "maxNumber"
        },
        vectorChildren: {
          dependencyType: "child",
          childGroups: ["vectors"],
          variableNames: ["numDimensions"],
          skipComponentIndices: true
        },
        vectorsShadow: {
          dependencyType: "stateVariable",
          variableName: "vectorsShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        let numDimensions;
        let numDimensionsByVector = [];
        if (dependencyValues.vectorChildren.length > 0) {
          for (let vector of dependencyValues.vectorChildren) {
            if (Number.isFinite(vector.stateValues.numDimensions)) {
              numDimensionsByVector.push(
                vector.stateValues.numDimensions
              );
            } else {
              numDimensionsByVector.push(1);
            }
          }
        } else if (dependencyValues.vectorsShadow !== null) {
          for (let vector of dependencyValues.vectorsShadow) {
            numDimensionsByVector.push(vector.length);
          }
        }
        let maxNum = dependencyValues.maxNumber;
        if (numDimensionsByVector.length > maxNum) {
          numDimensionsByVector = numDimensionsByVector.slice(
            0,
            maxNum
          );
        }
        if (numDimensionsByVector.length === 0) {
          numDimensions = 2;
        } else {
          numDimensions = Math.max(...numDimensionsByVector);
        }
        return {
          setValue: { numDimensions },
          checkForActualChange: { numDimensions: true }
        };
      }
    };
    stateVariableDefinitions.childIndicesByVector = {
      isArray: true,
      returnArraySizeDependencies: () => ({
        numVectors: {
          dependencyType: "stateVariable",
          variableName: "numVectors"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numVectors];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            vectorChild: {
              dependencyType: "child",
              childGroups: ["vectors"],
              childIndices: [arrayKey]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let childIndicesByVector = {};
        for (let arrayKey of arrayKeys) {
          let vectorChild = dependencyValuesByKey[arrayKey].vectorChild[0];
          if (vectorChild) {
            childIndicesByVector[arrayKey] = vectorChild.componentIdx;
          }
        }
        return { setValue: { childIndicesByVector } };
      }
    };
    stateVariableDefinitions.vectors = {
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["vectorX", "vector"],
      stateVariablesDeterminingDependencies: ["childIndicesByVector"],
      returnEntryDimensions: (prefix) => prefix === "vector" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "vectorX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "vectors") {
          if (propIndex.length === 1) {
            return "vector" + propIndex[0];
          } else {
            return `vectorX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 6) === "vector") {
          let vectorNum = Number(varName.slice(6));
          if (Number.isInteger(vectorNum) && vectorNum > 0) {
            return `vectorX${vectorNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numVectors: {
          dependencyType: "stateVariable",
          variableName: "numVectors"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numVectors,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          childIndicesByVector: {
            dependencyType: "stateVariable",
            variableName: "childIndicesByVector"
          },
          vectorsShadow: {
            dependencyType: "stateVariable",
            variableName: "vectorsShadow"
          }
        };
        for (let arrayKey of arrayKeys) {
          let [vectorInd, dim] = arrayKey.split(",");
          let childIndices = [];
          if (stateValues.childIndicesByVector[vectorInd]) {
            childIndices = [vectorInd];
          }
          dependenciesByKey[arrayKey] = {
            vectorChild: {
              dependencyType: "child",
              childGroups: ["vectors"],
              variableNames: ["x" + (Number(dim) + 1)],
              childIndices
            }
          };
        }
        return { dependenciesByKey, globalDependencies };
      },
      arrayDefinitionByKey({
        dependencyValuesByKey,
        globalDependencyValues,
        arrayKeys
      }) {
        let vectors = {};
        for (let arrayKey of arrayKeys) {
          let [vectorInd, dim] = arrayKey.split(",");
          let vectorChild = dependencyValuesByKey[arrayKey].vectorChild[0];
          if (vectorChild) {
            vectors[arrayKey] = vectorChild.stateValues["x" + (Number(dim) + 1)];
          } else {
            vectors[arrayKey] = globalDependencyValues.vectorsShadow[vectorInd][dim];
          }
        }
        return { setValue: { vectors } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        workspace
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.vectors) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let vectorChild = dependencyValuesByKey[arrayKey].vectorChild[0];
          if (vectorChild) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].vectorChild,
              desiredValue: desiredStateVariableValues.vectors[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else if (globalDependencyValues.vectorsShadow !== null) {
            if (!workspace.desiredVectorsShadow) {
              workspace.desiredVectorsShadow = [
                ...globalDependencyValues.vectorsShadow
              ];
            }
            let [vectorInd, dim] = arrayKey.split(",");
            workspace.desiredVectorsShadow[vectorInd][dim] = desiredStateVariableValues.texts[arrayKey];
          }
        }
        if (workspace.desiredVectorsShadow) {
          instructions.push({
            setDependency: "vectorsShadow",
            desiredValue: workspace.desiredVectorsShadow
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numVectors",
      description: "The number of vectors in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "vectors",
      description: "The list's vectors."
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        childIndicesByVector: {
          dependencyType: "stateVariable",
          variableName: "childIndicesByVector"
        }
      }),
      // When this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must set
      // stateVariableToEvaluateAfterReplacements
      // to this variable so that it is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let attributesToConvert = gatherRawNumberDisplayFixedResponseAttributes(
      component,
      components
    );
    const copyChild = component.definingChildren.length === 1 && component.definingChildren[0].componentType === "_copy" ? component.definingChildren[0] : null;
    let copyChildSource;
    if (copyChild) {
      const cIdx = await copyChild.stateValues.extendIdx;
      if (cIdx !== -1) {
        copyChildSource = {
          componentIdx: cIdx,
          componentType: components[cIdx].componentType
        };
      }
    }
    let childIndicesByVector = await component.stateValues.childIndicesByVector;
    let numVectors = await component.stateValues.numVectors;
    let numDimensions = await component.stateValues.numDimensions;
    for (let i2 = 0; i2 < numVectors; i2++) {
      let attributes = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "vector",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributes = JSON.parse(JSON.stringify(res.attributes));
        nComponents = res.nComponents;
      }
      if (copyChildSource) {
        nComponents = addShadowNumberDisplayAttributes({
          nComponents,
          stateIdInfo,
          source: copyChildSource,
          compositeIdx: copyChild.componentIdx,
          attributes,
          componentInfoObjects
        });
      }
      let childIdx = childIndicesByVector[i2];
      let replacementSource = components[childIdx];
      if (replacementSource) {
        componentsCopied.push(replacementSource.componentIdx);
      }
      const mathListChildren = [];
      for (let dim = 0; dim < numDimensions; dim++) {
        mathListChildren.push({
          type: "serialized",
          componentType: "math",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          children: [],
          state: {},
          downstreamDependencies: {
            [component.componentIdx]: [
              {
                dependencyType: "referenceShadow",
                compositeIdx: component.componentIdx,
                propVariable: `vectorX${i2 + 1}_${dim + 1}`
              }
            ]
          }
        });
      }
      const mathList = {
        type: "serialized",
        componentType: "mathList",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: {},
        doenetAttributes: {},
        children: mathListChildren,
        state: {}
      };
      attributes.xs = {
        type: "component",
        name: "xs",
        component: mathList
      };
      replacements.push({
        type: "serialized",
        componentType: "vector",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes,
        doenetAttributes: {},
        children: [],
        state: {}
      });
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    workspace.numVectors = numVectors;
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let numVectors = await component.stateValues.numVectors;
    if (numVectors === workspace.numVectors) {
      let componentsToCopy = [];
      let childIndicesByVector = await component.stateValues.childIndicesByVector;
      for (let childIdx of childIndicesByVector) {
        let replacementSource = components[childIdx];
        if (replacementSource) {
          componentsToCopy.push(replacementSource.componentIdx);
        }
      }
      if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
        (x2, i2) => x2 === componentsToCopy[i2]
      )) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
class ControlVectors extends VectorListComponent {
  static componentType = "controlVectors";
  static componentDocs = {
    summary: "Bezier control vectors at points defining a curve (used inside `<bezierControls>`)"
  };
  static inSchemaOnlyInheritAs = [];
  // don't let it appear in schema as a vector
  static allowInSchemaAsComponent = void 0;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.direction = {
      createComponentOfType: "text",
      createStateVariable: "direction",
      defaultValue: "symmetric",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "symmetric",
          description: "Mirror the control vector on both sides of the vertex."
        },
        {
          value: "previous",
          description: "Apply the control vector toward the previous segment only."
        },
        {
          value: "next",
          description: "Apply the control vector toward the next segment only."
        },
        {
          value: "both",
          description: "Apply independent control vectors to both adjacent segments."
        },
        {
          value: "none",
          description: "No control vector at this vertex."
        }
      ],
      description: "Direction in which the control vectors apply at the vertex."
    };
    attributes.pointNumber = {
      createComponentOfType: "number",
      createStateVariable: "pointNumber",
      defaultValue: null,
      public: true,
      description: "1-based index of the vertex these control vectors attach to."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.hidden = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { hidden: true } })
    };
    return stateVariableDefinitions;
  }
}
class PointList extends CompositeComponent {
  static componentType = "pointList";
  static componentDocs = {
    summary: "A list of points"
  };
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  // when another component has an attribute that is a pointList,
  // use the points state variable to populate that attribute
  static stateVariableToBeShadowed = "points";
  static primaryStateVariableForDefinition = "pointsShadow";
  static allowInSchemaAsComponent = ["point"];
  // even if inside a component that turned on descendantCompositesMustHaveAReplacement
  // don't required composite replacements
  static descendantCompositesMustHaveAReplacement = false;
  static doNotExpandAsShadowed = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedPrelim",
      defaultValue: false,
      description: "Whether the order of points in this list should be treated as unordered (e.g. for matching)."
    };
    attributes.maxNumber = {
      description: "Maximum number of points to retain in the list.",
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: Infinity,
      public: true
    };
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this component is treated as a response for the purposes of assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    const numberDisplayAttrs = returnNumberDisplayAttributes();
    for (let attrName in numberDisplayAttrs) {
      attributes[attrName] = {
        leaveRaw: true,
        description: numberDisplayAttrs[attrName].description
      };
    }
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string", "math"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let groupIntoPointsSeparatedBySpacesOutsideParens = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
      componentType: "point"
    });
    sugarInstructions.push({
      replacementFunction: function({
        matchedChildren,
        nComponents,
        stateIdInfo
      }) {
        return groupIntoPointsSeparatedBySpacesOutsideParens({
          matchedChildren,
          nComponents,
          stateIdInfo
        });
      }
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "points",
        componentTypes: ["point"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnUnorderedListStateVariableDefinitions()
    );
    stateVariableDefinitions.pointsShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          pointsShadow: true
        }
      })
    };
    stateVariableDefinitions.numPoints = {
      description: "The number of points in the list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        maxNumber: {
          dependencyType: "stateVariable",
          variableName: "maxNumber"
        },
        pointChildren: {
          dependencyType: "child",
          childGroups: ["points"],
          skipComponentIndices: true
        },
        pointsShadow: {
          dependencyType: "stateVariable",
          variableName: "pointsShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        let numPoints = 0;
        if (dependencyValues.pointChildren.length > 0) {
          numPoints = dependencyValues.pointChildren.length;
        } else if (dependencyValues.pointsShadow !== null) {
          numPoints = dependencyValues.pointsShadow.length;
        }
        let maxNum = dependencyValues.maxNumber;
        if (numPoints > maxNum) {
          numPoints = maxNum;
        }
        return {
          setValue: { numPoints },
          checkForActualChange: { numPoints: true }
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      returnDependencies: () => ({
        maxNumber: {
          dependencyType: "stateVariable",
          variableName: "maxNumber"
        },
        pointChildren: {
          dependencyType: "child",
          childGroups: ["points"],
          variableNames: ["numDimensions"],
          skipComponentIndices: true
        },
        pointsShadow: {
          dependencyType: "stateVariable",
          variableName: "pointsShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        let numDimensions;
        let numDimensionsByPoint = [];
        if (dependencyValues.pointChildren.length > 0) {
          for (let point of dependencyValues.pointChildren) {
            if (Number.isFinite(point.stateValues.numDimensions)) {
              numDimensionsByPoint.push(
                point.stateValues.numDimensions
              );
            } else {
              numDimensionsByPoint.push(1);
            }
          }
        } else if (dependencyValues.pointsShadow !== null) {
          for (let point of dependencyValues.pointsShadow) {
            numDimensionsByPoint.push(point.length);
          }
        }
        let maxNum = dependencyValues.maxNumber;
        if (numDimensionsByPoint.length > maxNum) {
          numDimensionsByPoint = numDimensionsByPoint.slice(
            0,
            maxNum
          );
        }
        if (numDimensionsByPoint.length === 0) {
          numDimensions = 2;
        } else {
          numDimensions = Math.max(...numDimensionsByPoint);
        }
        return {
          setValue: { numDimensions },
          checkForActualChange: { numDimensions: true }
        };
      }
    };
    stateVariableDefinitions.childIndicesByPoint = {
      isArray: true,
      returnArraySizeDependencies: () => ({
        numPoints: {
          dependencyType: "stateVariable",
          variableName: "numPoints"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numPoints];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            pointChild: {
              dependencyType: "child",
              childGroups: ["points"],
              childIndices: [arrayKey]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let childIndicesByPoint = {};
        for (let arrayKey of arrayKeys) {
          let pointChild = dependencyValuesByKey[arrayKey].pointChild[0];
          if (pointChild) {
            childIndicesByPoint[arrayKey] = pointChild.componentIdx;
          }
        }
        return { setValue: { childIndicesByPoint } };
      }
    };
    stateVariableDefinitions.points = {
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["pointX", "point"],
      stateVariablesDeterminingDependencies: ["childIndicesByPoint"],
      returnEntryDimensions: (prefix) => prefix === "point" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "pointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "points") {
          if (propIndex.length === 1) {
            return "point" + propIndex[0];
          } else {
            return `pointX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 5) === "point") {
          let pointNum = Number(varName.slice(5));
          if (Number.isInteger(pointNum) && pointNum > 0) {
            return `pointX${pointNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        numPoints: {
          dependencyType: "stateVariable",
          variableName: "numPoints"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [
          dependencyValues.numPoints,
          dependencyValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          childIndicesByPoint: {
            dependencyType: "stateVariable",
            variableName: "childIndicesByPoint"
          },
          pointsShadow: {
            dependencyType: "stateVariable",
            variableName: "pointsShadow"
          }
        };
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let childIndices = [];
          if (stateValues.childIndicesByPoint[pointInd]) {
            childIndices = [pointInd];
          }
          dependenciesByKey[arrayKey] = {
            pointChild: {
              dependencyType: "child",
              childGroups: ["points"],
              variableNames: ["x" + (Number(dim) + 1)],
              childIndices
            }
          };
        }
        return { dependenciesByKey, globalDependencies };
      },
      arrayDefinitionByKey({
        dependencyValuesByKey,
        globalDependencyValues,
        arrayKeys
      }) {
        let points = {};
        for (let arrayKey of arrayKeys) {
          let [pointInd, dim] = arrayKey.split(",");
          let pointChild = dependencyValuesByKey[arrayKey].pointChild[0];
          if (pointChild) {
            points[arrayKey] = pointChild.stateValues["x" + (Number(dim) + 1)];
          } else {
            points[arrayKey] = globalDependencyValues.pointsShadow[pointInd][dim];
          }
        }
        return { setValue: { points } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        workspace
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.points) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let pointChild = dependencyValuesByKey[arrayKey].pointChild[0];
          if (pointChild) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].pointChild,
              desiredValue: desiredStateVariableValues.points[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else if (globalDependencyValues.pointsShadow !== null) {
            if (!workspace.desiredPointsShadow) {
              workspace.desiredPointsShadow = [
                ...globalDependencyValues.pointsShadow
              ];
            }
            let [pointInd, dim] = arrayKey.split(",");
            workspace.desiredPointsShadow[pointInd][dim] = desiredStateVariableValues.texts[arrayKey];
          }
        }
        if (workspace.desiredPointsShadow) {
          instructions.push({
            setDependency: "pointsShadow",
            desiredValue: workspace.desiredPointsShadow
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numPoints",
      description: "The number of points in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "points",
      description: "The list's points."
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        childIndicesByPoint: {
          dependencyType: "stateVariable",
          variableName: "childIndicesByPoint"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      // When this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must set
      // stateVariableToEvaluateAfterReplacements
      // to this variable so that it is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let attributesToConvert = gatherRawNumberDisplayFixedResponseAttributes(
      component,
      components
    );
    const copyChild = component.definingChildren.length === 1 && component.definingChildren[0].componentType === "_copy" ? component.definingChildren[0] : null;
    let copyChildSource;
    if (copyChild) {
      const cIdx = await copyChild.stateValues.extendIdx;
      if (cIdx !== -1) {
        copyChildSource = {
          componentIdx: cIdx,
          componentType: components[cIdx].componentType
        };
      }
    }
    let childIndicesByPoint = await component.stateValues.childIndicesByPoint;
    let numPoints = await component.stateValues.numPoints;
    let numDimensions = await component.stateValues.numDimensions;
    for (let i2 = 0; i2 < numPoints; i2++) {
      let attributes = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "point",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributes = JSON.parse(JSON.stringify(res.attributes));
        nComponents = res.nComponents;
      }
      if (copyChildSource) {
        nComponents = addShadowNumberDisplayAttributes({
          nComponents,
          stateIdInfo,
          source: copyChildSource,
          compositeIdx: copyChild.componentIdx,
          attributes,
          componentInfoObjects
        });
      }
      let childIdx = childIndicesByPoint[i2];
      let replacementSource = components[childIdx];
      if (replacementSource) {
        componentsCopied.push(replacementSource.componentIdx);
      }
      const mathListChildren = [];
      for (let dim = 0; dim < numDimensions; dim++) {
        mathListChildren.push({
          type: "serialized",
          componentType: "math",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          children: [],
          state: {},
          downstreamDependencies: {
            [component.componentIdx]: [
              {
                dependencyType: "referenceShadow",
                compositeIdx: component.componentIdx,
                propVariable: `pointX${i2 + 1}_${dim + 1}`
              }
            ]
          }
        });
      }
      const mathList = {
        type: "serialized",
        componentType: "mathList",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: {},
        doenetAttributes: {},
        children: mathListChildren,
        state: {}
      };
      attributes.xs = {
        type: "component",
        name: "xs",
        component: mathList
      };
      replacements.push({
        type: "serialized",
        componentType: "point",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes,
        doenetAttributes: {},
        children: [],
        state: {}
      });
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    workspace.numPoints = numPoints;
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let numPoints = await component.stateValues.numPoints;
    if (numPoints === workspace.numPoints) {
      let componentsToCopy = [];
      let childIndicesByPoint = await component.stateValues.childIndicesByPoint;
      for (let childIdx of childIndicesByPoint) {
        let replacementSource = components[childIdx];
        if (replacementSource) {
          componentsToCopy.push(replacementSource.componentIdx);
        }
      }
      if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
        (x2, i2) => x2 === componentsToCopy[i2]
      )) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
class IntervalList extends CompositeComponent {
  static componentType = "intervalList";
  static componentDocs = {
    summary: "A list of intervals"
  };
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static allowInSchemaAsComponent = ["interval"];
  // when another component has an attribute that is a intervalList,
  // use the intervals state variable to populate that attribute
  static stateVariableToBeShadowed = "intervals";
  static primaryStateVariableForDefinition = "intervalsShadow";
  // even if inside a component that turned on descendantCompositesMustHaveAReplacement
  // don't required composite replacements
  static descendantCompositesMustHaveAReplacement = false;
  static doNotExpandAsShadowed = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.unordered = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedPrelim",
      defaultValue: false,
      description: "Whether the order of intervals in this list should be treated as unordered (e.g. for matching)."
    };
    attributes.maxNumber = {
      description: "Maximum number of intervals to retain in the list.",
      createComponentOfType: "number",
      createStateVariable: "maxNumber",
      defaultValue: Infinity,
      public: true
    };
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this component is treated as a response for the purposes of assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    const numberDisplayAttrs = returnNumberDisplayAttributes();
    for (let attrName in numberDisplayAttrs) {
      attributes[attrName] = {
        leaveRaw: true,
        description: numberDisplayAttrs[attrName].description
      };
    }
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let createIntervalList = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let results = breakEmbeddedStringsIntoIntervalPieces({
        componentList: matchedChildren
      });
      if (results.success !== true) {
        return { success: false };
      }
      return {
        success: true,
        newChildren: results.pieces.map(function(piece) {
          if (piece.length > 1 || typeof piece[0] === "string") {
            return {
              type: "serialized",
              componentType: "interval",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              attributes: {},
              doenetAttributes: {},
              state: {},
              children: piece
            };
          } else {
            return piece[0];
          }
        }),
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: createIntervalList
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "intervals",
        componentTypes: ["interval"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnUnorderedListStateVariableDefinitions()
    );
    stateVariableDefinitions.intervalsShadow = {
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          intervalsShadow: true
        }
      })
    };
    stateVariableDefinitions.numIntervals = {
      description: "The number of intervals in the list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        maxNumber: {
          dependencyType: "stateVariable",
          variableName: "maxNumber"
        },
        intervalChildren: {
          dependencyType: "child",
          childGroups: ["intervals"],
          skipComponentIndices: true
        },
        intervalsShadow: {
          dependencyType: "stateVariable",
          variableName: "intervalsShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        let numIntervals = 0;
        if (dependencyValues.intervalChildren.length > 0) {
          numIntervals = dependencyValues.intervalChildren.length;
        } else if (dependencyValues.intervalsShadow !== null) {
          numIntervals = dependencyValues.intervalsShadow.length;
        }
        let maxNum = dependencyValues.maxNumber;
        if (numIntervals > maxNum) {
          numIntervals = maxNum;
        }
        return {
          setValue: { numIntervals },
          checkForActualChange: { numIntervals: true }
        };
      }
    };
    stateVariableDefinitions.childIndicesByInterval = {
      isArray: true,
      returnArraySizeDependencies: () => ({
        numIntervals: {
          dependencyType: "stateVariable",
          variableName: "numIntervals"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numIntervals];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            intervalChild: {
              dependencyType: "child",
              childGroups: ["intervals"],
              childIndices: [arrayKey]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let childIndicesByInterval = {};
        for (let arrayKey of arrayKeys) {
          let intervalChild = dependencyValuesByKey[arrayKey].intervalChild[0];
          if (intervalChild) {
            childIndicesByInterval[arrayKey] = intervalChild.componentIdx;
          }
        }
        return { setValue: { childIndicesByInterval } };
      }
    };
    stateVariableDefinitions.intervals = {
      isArray: true,
      numDimensions: 1,
      entryPrefixes: ["interval"],
      stateVariablesDeterminingDependencies: ["childIndicesByInterval"],
      returnArraySizeDependencies: () => ({
        numIntervals: {
          dependencyType: "stateVariable",
          variableName: "numIntervals"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numIntervals];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          childIndicesByInterval: {
            dependencyType: "stateVariable",
            variableName: "childIndicesByInterval"
          },
          intervalsShadow: {
            dependencyType: "stateVariable",
            variableName: "intervalsShadow"
          }
        };
        for (let arrayKey of arrayKeys) {
          let childIndices = [];
          if (stateValues.childIndicesByInterval[arrayKey]) {
            childIndices = [arrayKey];
          }
          dependenciesByKey[arrayKey] = {
            intervalChild: {
              dependencyType: "child",
              childGroups: ["intervals"],
              variableNames: ["value"],
              childIndices
            }
          };
        }
        return { dependenciesByKey, globalDependencies };
      },
      arrayDefinitionByKey({
        dependencyValuesByKey,
        globalDependencyValues,
        arrayKeys
      }) {
        let intervals = {};
        for (let arrayKey of arrayKeys) {
          let intervalChild = dependencyValuesByKey[arrayKey].intervalChild[0];
          if (intervalChild) {
            intervals[arrayKey] = intervalChild.stateValues["value"];
          } else {
            intervals[arrayKey] = globalDependencyValues.intervalsShadow[arrayKey];
          }
        }
        return { setValue: { intervals } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        workspace
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.intervals) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let intervalChild = dependencyValuesByKey[arrayKey].intervalChild[0];
          if (intervalChild) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].intervalChild,
              desiredValue: desiredStateVariableValues.intervals[arrayKey],
              childIndex: 0,
              variableIndex: 0
            });
          } else if (globalDependencyValues.intervalsShadow !== null) {
            if (!workspace.desiredIntervalsShadow) {
              workspace.desiredIntervalsShadow = [
                ...globalDependencyValues.intervalsShadow
              ];
            }
            workspace.desiredIntervalsShadow[arrayKey] = desiredStateVariableValues.texts[arrayKey];
          }
        }
        if (workspace.desiredIntervalsShadow) {
          instructions.push({
            setDependency: "intervalsShadow",
            desiredValue: workspace.desiredIntervalsShadow
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numIntervals",
      description: "The number of intervals in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "intervals",
      description: "The list's intervals."
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        childIndicesByInterval: {
          dependencyType: "stateVariable",
          variableName: "childIndicesByInterval"
        }
      }),
      // When this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must set
      // stateVariableToEvaluateAfterReplacements
      // to this variable so that it is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let attributesToConvert = gatherRawNumberDisplayFixedResponseAttributes(
      component,
      components
    );
    const copyChild = component.definingChildren.length === 1 && component.definingChildren[0].componentType === "_copy" ? component.definingChildren[0] : null;
    let copyChildSource;
    if (copyChild) {
      const cIdx = await copyChild.stateValues.extendIdx;
      if (cIdx !== -1) {
        copyChildSource = {
          componentIdx: cIdx,
          componentType: components[cIdx].componentType
        };
      }
    }
    let childIndicesByInterval = await component.stateValues.childIndicesByInterval;
    let numIntervals = await component.stateValues.numIntervals;
    for (let i2 = 0; i2 < numIntervals; i2++) {
      let attributes = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "interval",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributes = JSON.parse(JSON.stringify(res.attributes));
        nComponents = res.nComponents;
      }
      if (copyChildSource) {
        nComponents = addShadowNumberDisplayAttributes({
          nComponents,
          stateIdInfo,
          source: copyChildSource,
          compositeIdx: copyChild.componentIdx,
          attributes,
          componentInfoObjects
        });
      }
      let childIdx = childIndicesByInterval[i2];
      let replacementSource = components[childIdx];
      if (replacementSource) {
        componentsCopied.push(replacementSource.componentIdx);
      }
      replacements.push({
        type: "serialized",
        componentType: "interval",
        componentIdx: nComponents++,
        stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
        attributes,
        doenetAttributes: {},
        children: [],
        state: {},
        downstreamDependencies: {
          [component.componentIdx]: [
            {
              dependencyType: "referenceShadow",
              compositeIdx: component.componentIdx,
              propVariable: `interval${i2 + 1}`
            }
          ]
        }
      });
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    workspace.numIntervals = numIntervals;
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let numIntervals = await component.stateValues.numIntervals;
    if (numIntervals === workspace.numIntervals) {
      let componentsToCopy = [];
      let childIndicesByInterval = await component.stateValues.childIndicesByInterval;
      for (let childIdx of childIndicesByInterval) {
        let replacementSource = components[childIdx];
        if (replacementSource) {
          componentsToCopy.push(replacementSource.componentIdx);
        }
      }
      if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
        (x2, i2) => x2 === componentsToCopy[i2]
      )) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
function breakEmbeddedStringsIntoIntervalPieces({ componentList }) {
  let Nparens = 0;
  let pieces = [];
  let currentPiece = [];
  for (let component of componentList) {
    if (typeof component !== "string") {
      if (Nparens === 0) {
        return { success: false };
      } else {
        currentPiece.push(component);
      }
      continue;
    }
    let s2 = component.trim();
    let beginInd = 0;
    for (let ind = 0; ind < s2.length; ind++) {
      let char = s2[ind];
      if (char === "(" || Nparens === 0 && char === "[") {
        Nparens++;
      } else if (char === ")" || Nparens === 1 && char === "]") {
        if (Nparens === 0) {
          return { success: false };
        }
        if (Nparens === 1) {
          if (ind + 1 > beginInd) {
            let lastInd = ind + 1;
            let newString = s2.substring(beginInd, lastInd).trim();
            if (newString.length > 0) {
              currentPiece.push(newString);
            }
          }
          pieces.push(currentPiece);
          currentPiece = [];
          beginInd = ind + 1;
        }
        Nparens--;
      } else if (Nparens === 0 && !char.match(/\s/)) {
        return { success: false };
      }
    }
    if (s2.length > beginInd) {
      let newString = s2.substring(beginInd, s2.length).trim();
      currentPiece.push(newString);
    }
  }
  if (Nparens !== 0) {
    return { success: false };
  }
  if (currentPiece.length > 0) {
    return { success: false };
  }
  return {
    success: true,
    pieces
  };
}
class LineListComponent extends BaseComponent {
  static componentType = "_lineListComponent";
  static rendererType = "containerInline";
  static renderChildren = true;
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakStringsIntoLinesBySpaces = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let newChildren = matchedChildren.reduce(function(a2, c2) {
        if (typeof c2 === "string") {
          return [
            ...a2,
            ...c2.split(/\s+/).filter((s2) => s2).map((s2) => ({
              type: "serialized",
              componentType: "line",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: [s2],
              attributes: {},
              doenetAttributes: {},
              state: {}
            }))
          ];
        } else {
          return [...a2, c2];
        }
      }, []);
      return {
        success: true,
        newChildren,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakStringsIntoLinesBySpaces
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "lines",
        componentTypes: ["line"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numLines = {
      returnDependencies: () => ({
        lineChildren: {
          dependencyType: "child",
          childGroups: ["lines"],
          skipComponentIndices: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            numLines: dependencyValues.lineChildren.length
          },
          checkForActualChange: { numLines: true }
        };
      }
    };
    stateVariableDefinitions.lineNames = {
      isArray: true,
      entryPrefixes: ["lineName"],
      returnArraySizeDependencies: () => ({
        numLines: {
          dependencyType: "stateVariable",
          variableName: "numLines"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numLines];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            lineChild: {
              dependencyType: "child",
              childGroups: ["lines"],
              childIndices: [arrayKey]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let lineNames = {};
        for (let arrayKey of arrayKeys) {
          let lineChild = dependencyValuesByKey[arrayKey].lineChild[0];
          if (lineChild) {
            lineNames[arrayKey] = lineChild.componentIdx;
          }
        }
        return { setValue: { lineNames } };
      }
    };
    return stateVariableDefinitions;
  }
}
class AngleListComponent extends BaseComponent {
  static componentType = "_angleListComponent";
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.hide = { default: true };
    return attributes;
  }
  static returnChildLogic(args) {
    let childLogic = super.returnChildLogic(args);
    let AtLeastZeroAngles = childLogic.newLeaf({
      name: "AtLeastZeroAngles",
      componentType: "angle",
      comparison: "atLeast",
      number: 0
    });
    let breakIntoAnglesByCommas = breakStringsAndOthersIntoComponentsByStringCommas();
    let AtLeastOneString = childLogic.newLeaf({
      name: "AtLeastOneString",
      componentType: "string",
      comparison: "atLeast",
      number: 1
    });
    let AtLeastOneMath = childLogic.newLeaf({
      name: "AtLeastOneMath",
      componentType: "math",
      comparison: "atLeast",
      number: 1
    });
    let StringsAndMaths = childLogic.newOperator({
      name: "StringsAndMaths",
      operator: "or",
      propositions: [AtLeastOneString, AtLeastOneMath],
      requireConsecutive: true,
      isSugar: true,
      replacementFunction: breakIntoAnglesByCommas
    });
    childLogic.newOperator({
      name: "AnglesXorSugar",
      operator: "xor",
      propositions: [AtLeastZeroAngles, StringsAndMaths],
      setAsBase: true
    });
    return childLogic;
  }
  updateState(args = {}) {
    if (args.init) {
      this._state.angles = { trackChanges: true };
    }
    super.updateState(args);
    if (!this.childLogicSatisfied) {
      this.unresolvedState.angles = true;
      this.unresolvedState.numAngles = true;
      return;
    }
    let trackChanges = this.currentTracker.trackChanges;
    let childrenChanged = trackChanges.childrenChanged(this.componentIdx);
    if (childrenChanged) {
      delete this.unresolvedState.angles;
      delete this.unresolvedState.numAngles;
      let AtLeastZeroAngles = this.childLogic.returnMatches("AtLeastZeroAngles");
      this.state.numAngles = AtLeastZeroAngles.length;
      this.state.angles = AtLeastZeroAngles.map(
        (i2) => this.activeChildren[i2]
      );
    }
  }
  initializeRenderer() {
    if (this.renderer === void 0) {
      this.renderer = new this.availableRenderers.container({
        key: this.componentIdx
      });
    }
  }
  updateChildrenWhoRender() {
    this.childrenWhoRender = this.state.angles.map((x2) => x2.componentIdx);
  }
  // allowDownstreamUpdates() {
  //   return true;
  // }
  // get variablesUpdatableDownstream() {
  //   return ["angles"];
  // }
  // calculateDownstreamChanges({ stateVariablesToUpdate, stateVariableChangesToSave,
  //   dependenciesToUpdate, dryRun }) {
  //   let angleNames = this.state.angles.map(x => x.componentIdx);
  //   let newAngle = stateVariablesToUpdate.angles;
  //   for (let ind in angleNames) {
  //     if (dryRun === true) {
  //       dependenciesToUpdate[angleNames[ind]] = { angle: undefined };
  //     } else {
  //       dependenciesToUpdate[angleNames[ind]] = { angle: newAngle[ind] };
  //     }
  //   }
  //   let newStateVariables = { angles: newAngle };
  //   this.updateShadowSources({
  //     newStateVariables: newStateVariables,
  //     dependenciesToUpdate: dependenciesToUpdate,
  //     dryRun: dryRun
  //   });
  //   return true;
  // }
}
class Vector extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveVector: this.moveVector.bind(this),
      moveVectorSinglePoint: this.moveVectorSinglePoint.bind(this),
      vectorClicked: this.vectorClicked.bind(this),
      vectorFocused: this.vectorFocused.bind(this)
    });
  }
  static componentType = "vector";
  static styleOverrideCategories = ["line"];
  static componentDocs = {
    summary: "A geometric vector with tail and head"
  };
  static canBeInList = true;
  static primaryStateVariableForDefinition = "displacementShadow";
  static variableForIndexAsProp = "displacement";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the vector can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.headDraggable = {
      createComponentOfType: "boolean",
      description: "Whether the vector's head can be dragged."
    };
    attributes.tailDraggable = {
      createComponentOfType: "boolean",
      description: "Whether the vector's tail can be dragged."
    };
    attributes.x = {
      createComponentOfType: "math",
      description: "The first component (x) of the vector's displacement."
    };
    attributes.y = {
      createComponentOfType: "math",
      description: "The second component (y) of the vector's displacement."
    };
    attributes.z = {
      createComponentOfType: "math",
      description: "The third component (z) of the vector's displacement."
    };
    attributes.xs = {
      createComponentOfType: "mathList",
      description: "The vector's displacement components as a list."
    };
    attributes.displacement = {
      createComponentOfType: "coords",
      description: "The vector's displacement (head minus tail)."
    };
    attributes.head = {
      createComponentOfType: "point",
      description: "The head (tip) point of the vector."
    };
    attributes.tail = {
      createComponentOfType: "point",
      description: "The tail (base) point of the vector."
    };
    attributes.addControls = {
      description: "Whether to render interactive control handles.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "displacement",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "displacement",
          description: "Show a control handle for translating the whole vector."
        },
        {
          value: "headAndTail",
          description: "Show control handles for both the head and the tail."
        },
        {
          value: "headOnly",
          description: "Show a control handle only at the head."
        },
        {
          value: "tailOnly",
          description: "Show a control handle only at the tail."
        },
        {
          value: "none",
          description: "Show no control handles."
        }
      ],
      valueForTrue: "displacement",
      valueForFalse: "none"
    };
    attributes.labelPosition = returnLineFamilyLabelPositionAttribute();
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.displayWithAngleBrackets = {
      description: "Whether to display the vector with angle brackets (e.g. ⟨1,2⟩).",
      createComponentOfType: "boolean",
      createStateVariable: "displayWithAngleBrackets",
      defaultValue: false,
      public: true
    };
    attributes.showCoordsWhenDragging = {
      description: "Whether to show coordinate labels while dragging.",
      createComponentOfType: "boolean",
      createStateVariable: "showCoordsWhenDragging",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.controlOrder = returnGraphControlOrderAttribute();
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["number", "math", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakIntoXsOrCoords = function({
      matchedChildren,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    }) {
      let componentIsSpecifiedType = componentInfoObjects.componentIsSpecifiedType;
      let componentChildren = [], nonComponentChildrenBegin = [], nonComponentChildrenEnd = [];
      for (let child of matchedChildren) {
        if (componentIsSpecifiedType(child, "constraints") || componentIsSpecifiedType(child, "label")) {
          if (componentChildren.length > 0) {
            nonComponentChildrenEnd.push(child);
          } else {
            nonComponentChildrenBegin.push(child);
          }
        } else if (nonComponentChildrenEnd.length > 0) {
          nonComponentChildrenEnd.push(child);
        } else {
          componentChildren.push(child);
        }
      }
      if (componentChildren.length === 0) {
        return { success: false };
      }
      if (componentChildren.length === 1) {
        let child = componentChildren[0];
        if (componentIsSpecifiedType(child, "point") || componentIsSpecifiedType(child, "vector")) {
          return { success: false };
        }
      }
      let nCompChildren = componentChildren.length;
      let firstChar, lastChar;
      if (typeof componentChildren[0] === "string") {
        componentChildren[0] = componentChildren[0].trimStart();
        firstChar = componentChildren[0][0];
      }
      if (typeof componentChildren[nCompChildren - 1] === "string") {
        componentChildren[nCompChildren - 1] = componentChildren[nCompChildren - 1].trimEnd();
        let lastChild = componentChildren[nCompChildren - 1];
        lastChar = lastChild[lastChild.length - 1];
      }
      if (firstChar === "(" && lastChar === ")") {
        let modifiedChildren = [...componentChildren];
        modifiedChildren[0] = modifiedChildren[0].substring(1);
        let lastChild = modifiedChildren[modifiedChildren.length - 1];
        modifiedChildren[modifiedChildren.length - 1] = lastChild.substring(0, lastChild.length - 1);
        let breakResult = breakEmbeddedStringByCommas({
          childrenList: modifiedChildren
        });
        if (breakResult.success) {
          const newAttributes = {
            xs: {
              type: "component",
              name: "xs",
              component: {
                type: "serialized",
                componentType: "mathList",
                componentIdx: nComponents++,
                stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
                children: breakResult.pieces.map((x2) => ({
                  type: "serialized",
                  componentType: "math",
                  componentIdx: nComponents++,
                  stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
                  children: x2,
                  attributes: {},
                  doenetAttributes: {},
                  state: {}
                })),
                skipSugar: true,
                state: {},
                attributes: {},
                doenetAttributes: {}
              }
            }
          };
          return {
            success: true,
            newAttributes,
            newChildren: [
              ...nonComponentChildrenBegin,
              ...nonComponentChildrenEnd
            ],
            nComponents
          };
        }
      }
      return {
        success: true,
        newAttributes: {
          displacement: {
            type: "component",
            name: "displacement",
            component: {
              type: "serialized",
              componentType: "coords",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: componentChildren,
              state: {},
              attributes: {},
              doenetAttributes: {}
            }
          }
        },
        newChildren: [
          ...nonComponentChildrenBegin,
          ...nonComponentChildrenEnd
        ],
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakIntoXsOrCoords
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    let childGroups = super.returnChildGroups();
    childGroups.push(
      ...[
        {
          group: "pointsAndVectors",
          componentTypes: ["point", "vector"]
        }
      ]
    );
    return childGroups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the vector's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let styleDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (styleDescription) {
            styleDescription += " ";
          }
          styleDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (styleDescription) {
          styleDescription += " ";
        }
        styleDescription += lineColorWord;
        return { setValue: { styleDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "vector".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " vector";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.inUnorderedList = {
      returnDependencies: () => ({
        sourceCompositeUnordered: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "unordered",
          skipCopies: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.sourceCompositeUnordered !== null && !usedDefault.sourceCompositeUnordered) {
          return {
            setValue: {
              inUnorderedList: Boolean(
                dependencyValues.sourceCompositeUnordered
              )
            }
          };
        } else {
          return {
            setValue: {
              inUnorderedList: false
            }
          };
        }
      }
    };
    stateVariableDefinitions.tailDraggable = {
      description: "Whether the vector's tail can be dragged independently.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      hasEssential: true,
      forRenderer: true,
      returnDependencies: () => ({
        tailDraggableAttr: {
          dependencyType: "attributeComponent",
          attributeName: "tailDraggable",
          variableNames: ["value"]
        },
        draggable: {
          dependencyType: "stateVariable",
          variableName: "draggable"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.tailDraggableAttr) {
          return {
            setValue: {
              tailDraggable: dependencyValues.tailDraggableAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              tailDraggable: {
                defaultValue: dependencyValues.draggable
              }
            }
          };
        }
      }
    };
    stateVariableDefinitions.headDraggable = {
      description: "Whether the vector's head can be dragged independently.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      hasEssential: true,
      forRenderer: true,
      returnDependencies: () => ({
        headDraggableAttr: {
          dependencyType: "attributeComponent",
          attributeName: "headDraggable",
          variableNames: ["value"]
        },
        draggable: {
          dependencyType: "stateVariable",
          variableName: "draggable"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.headDraggableAttr) {
          return {
            setValue: {
              headDraggable: dependencyValues.headDraggableAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              headDraggable: {
                defaultValue: dependencyValues.draggable
              }
            }
          };
        }
      }
    };
    stateVariableDefinitions.displacementShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "displacement",
      set: convertValueToMathExpression,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          displacementShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "displacementShadow",
              value: convertValueToMathExpression(
                desiredStateVariableValues.displacementShadow
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.headShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "head",
      set: convertValueToMathExpression,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          headShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "headShadow",
              value: convertValueToMathExpression(
                desiredStateVariableValues.headShadow
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.tailShadow = {
      defaultValue: null,
      isLocation: true,
      hasEssential: true,
      essentialVarName: "tail",
      set: convertValueToMathExpression,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          tailShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "tailShadow",
              value: convertValueToMathExpression(
                desiredStateVariableValues.tailShadow
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.sourceOfDisplacement = {
      returnDependencies: () => ({
        xAttr: {
          dependencyType: "attributeComponent",
          attributeName: "x"
        },
        yAttr: {
          dependencyType: "attributeComponent",
          attributeName: "y"
        },
        zAttr: {
          dependencyType: "attributeComponent",
          attributeName: "z"
        },
        xsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "xs"
        },
        displacementAttr: {
          dependencyType: "attributeComponent",
          attributeName: "displacement"
        },
        pointOrVectorChild: {
          dependencyType: "child",
          childGroups: ["pointsAndVectors"]
        }
      }),
      definition({ dependencyValues }) {
        let sourceOfDisplacement = null;
        if (dependencyValues.pointOrVectorChild.length > 0) {
          sourceOfDisplacement = "pointOrVectorChild";
        } else if (dependencyValues.displacementAttr !== null) {
          sourceOfDisplacement = "displacementAttr";
        } else if (dependencyValues.xsAttr !== null) {
          sourceOfDisplacement = "xsAttr";
        } else if (dependencyValues.xAttr !== null || dependencyValues.yAttr !== null || dependencyValues.zAttr !== null) {
          sourceOfDisplacement = "componentAttrs";
        }
        return {
          setValue: { sourceOfDisplacement }
        };
      }
    };
    stateVariableDefinitions.basedOnHead = {
      returnDependencies: () => ({
        headAttr: {
          dependencyType: "attributeComponent",
          attributeName: "head"
        },
        headShadow: {
          dependencyType: "stateVariable",
          variableName: "headShadow"
        },
        tailAttr: {
          dependencyType: "attributeComponent",
          attributeName: "tail"
        },
        sourceOfDisplacement: {
          dependencyType: "stateVariable",
          variableName: "sourceOfDisplacement"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.tailAttr !== null && dependencyValues.sourceOfDisplacement !== null) {
          let diagnostics = [];
          if (dependencyValues.headAttr !== null) {
            const warning = {
              type: "warning",
              message: "Vector is prescribed by head, tail, and displacement.  Ignoring specified head."
            };
            if (dependencyValues.headAttr.position) {
              warning.position = dependencyValues.headAttr.position;
            }
            diagnostics.push(warning);
          }
          return {
            setValue: { basedOnHead: false },
            checkForActualChange: { basedOnHead: true },
            sendDiagnostics: diagnostics
          };
        }
        if (dependencyValues.headAttr !== null) {
          return {
            setValue: { basedOnHead: true },
            checkForActualChange: { basedOnHead: true }
          };
        }
        return {
          setValue: {
            basedOnHead: dependencyValues.headShadow !== null
          },
          checkForActualChange: { basedOnHead: true }
        };
      }
    };
    stateVariableDefinitions.basedOnTail = {
      returnDependencies: () => ({
        tailAttr: {
          dependencyType: "attributeComponent",
          attributeName: "tail"
        },
        tailShadow: {
          dependencyType: "stateVariable",
          variableName: "tailShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.tailAttr !== null) {
          return {
            setValue: { basedOnTail: true },
            checkForActualChange: { basedOnTail: true }
          };
        }
        return {
          setValue: {
            basedOnTail: dependencyValues.tailShadow !== null
          },
          checkForActualChange: { basedOnTail: true }
        };
      }
    };
    stateVariableDefinitions.basedOnDisplacement = {
      returnDependencies: () => ({
        sourceOfDisplacement: {
          dependencyType: "stateVariable",
          variableName: "sourceOfDisplacement"
        },
        displacementShadow: {
          dependencyType: "stateVariable",
          variableName: "displacementShadow"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.sourceOfDisplacement !== null) {
          return {
            setValue: { basedOnDisplacement: true },
            checkForActualChange: { basedOnDisplacement: true }
          };
        }
        return {
          setValue: {
            basedOnDisplacement: dependencyValues.displacementShadow !== null
          },
          checkForActualChange: { basedOnDisplacement: true }
        };
      }
    };
    stateVariableDefinitions.numDimDisplacement = {
      stateVariablesDeterminingDependencies: [
        "basedOnDisplacement",
        "basedOnHead",
        "basedOnTail"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          basedOnHead: {
            dependencyType: "stateVariable",
            variableName: "basedOnHead"
          },
          basedOnTail: {
            dependencyType: "stateVariable",
            variableName: "basedOnTail"
          },
          basedOnDisplacement: {
            dependencyType: "stateVariable",
            variableName: "basedOnDisplacement"
          },
          sourceOfDisplacement: {
            dependencyType: "stateVariable",
            variableName: "sourceOfDisplacement"
          },
          displacementShadow: {
            dependencyType: "stateVariable",
            variableName: "displacementShadow"
          },
          displacementAttr: {
            dependencyType: "attributeComponent",
            attributeName: "displacement",
            variableNames: ["value"]
          },
          pointOrVectorChild: {
            dependencyType: "child",
            childGroups: ["pointsAndVectors"],
            variableNames: ["numDimensions"]
          },
          xAttr: {
            dependencyType: "attributeComponent",
            attributeName: "x"
          },
          yAttr: {
            dependencyType: "attributeComponent",
            attributeName: "y"
          },
          zAttr: {
            dependencyType: "attributeComponent",
            attributeName: "z"
          },
          xsAttr: {
            dependencyType: "attributeComponent",
            attributeName: "xs",
            variableNames: ["numComponents"]
          }
        };
        if (!stateValues.basedOnDisplacement) {
          if (stateValues.basedOnHead) {
            dependencies.numDimHead = {
              dependencyType: "stateVariable",
              variableName: "numDimHead"
            };
          }
          if (stateValues.basedOnTail) {
            dependencies.numDimTail = {
              dependencyType: "stateVariable",
              variableName: "numDimTail"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numDimDisplacement;
        if (dependencyValues.basedOnDisplacement) {
          switch (dependencyValues.sourceOfDisplacement) {
            case "pointOrVectorChild":
              numDimDisplacement = dependencyValues.pointOrVectorChild[0].stateValues.numDimensions;
              break;
            case "displacementAttr":
              let displacementTree1 = dependencyValues.displacementAttr.stateValues.value.expand().simplify().tree;
              if (Array.isArray(displacementTree1) && vectorOperators.includes(displacementTree1[0])) {
                numDimDisplacement = displacementTree1.length - 1;
              } else {
                numDimDisplacement = 1;
              }
              break;
            case "xsAttr":
              numDimDisplacement = dependencyValues.xsAttr.stateValues.numComponents;
              break;
            case "componentAttrs":
              if (dependencyValues.zAttr !== null) {
                numDimDisplacement = 3;
              } else if (dependencyValues.yAttr !== null) {
                numDimDisplacement = 2;
              } else {
                numDimDisplacement = 1;
              }
              break;
            default:
              let displacementTree2 = dependencyValues.displacementShadow.tuples_to_vectors().expand().simplify().tree;
              if (Array.isArray(displacementTree2) && vectorOperators.includes(displacementTree2[0])) {
                numDimDisplacement = displacementTree2.length - 1;
              } else {
                numDimDisplacement = 1;
              }
          }
        } else {
          if (dependencyValues.basedOnHead) {
            if (dependencyValues.basedOnTail) {
              if (dependencyValues.numDimHead === dependencyValues.numDimTail) {
                numDimDisplacement = dependencyValues.numDimHead;
              } else {
                numDimDisplacement = NaN;
              }
            } else {
              numDimDisplacement = dependencyValues.numDimHead;
            }
          } else if (dependencyValues.basedOnTail) {
            numDimDisplacement = dependencyValues.numDimTail;
          } else {
            numDimDisplacement = 2;
          }
        }
        return {
          setValue: { numDimDisplacement },
          checkForActualChange: { numDimDisplacement: true }
        };
      }
    };
    stateVariableDefinitions.numDimHead = {
      stateVariablesDeterminingDependencies: [
        "basedOnDisplacement",
        "basedOnHead",
        "basedOnTail"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          basedOnHead: {
            dependencyType: "stateVariable",
            variableName: "basedOnHead"
          },
          basedOnTail: {
            dependencyType: "stateVariable",
            variableName: "basedOnTail"
          },
          basedOnDisplacement: {
            dependencyType: "stateVariable",
            variableName: "basedOnDisplacement"
          },
          headShadow: {
            dependencyType: "stateVariable",
            variableName: "headShadow"
          },
          headAttr: {
            dependencyType: "attributeComponent",
            attributeName: "head",
            variableNames: ["numDimensions"]
          }
        };
        if (!stateValues.basedOnHead) {
          if (stateValues.basedOnTail) {
            dependencies.numDimTail = {
              dependencyType: "stateVariable",
              variableName: "numDimTail"
            };
          }
          if (stateValues.basedOnDisplacement) {
            dependencies.numDimDisplacement = {
              dependencyType: "stateVariable",
              variableName: "numDimDisplacement"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numDimHead;
        if (dependencyValues.basedOnHead) {
          if (dependencyValues.headAttr !== null) {
            numDimHead = dependencyValues.headAttr.stateValues.numDimensions;
          } else if (dependencyValues.headShadow) {
            let headTree = dependencyValues.headShadow.tree;
            if (Array.isArray(headTree) && vectorOperators.includes(headTree[0])) {
              numDimHead = headTree.length - 1;
            } else {
              numDimHead = 2;
            }
          }
        } else {
          if (dependencyValues.basedOnDisplacement) {
            if (dependencyValues.basedOnTail) {
              if (dependencyValues.numDimDisplacement === dependencyValues.numDimTail) {
                numDimHead = dependencyValues.numDimDisplacement;
              } else {
                numDimHead = NaN;
              }
            } else {
              numDimHead = dependencyValues.numDimDisplacement;
            }
          } else if (dependencyValues.basedOnTail) {
            numDimHead = dependencyValues.numDimTail;
          } else {
            numDimHead = 2;
          }
        }
        return {
          setValue: { numDimHead },
          checkForActualChange: { numDimHead: true }
        };
      }
    };
    stateVariableDefinitions.numDimTail = {
      stateVariablesDeterminingDependencies: [
        "basedOnDisplacement",
        "basedOnHead",
        "basedOnTail"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          basedOnHead: {
            dependencyType: "stateVariable",
            variableName: "basedOnHead"
          },
          basedOnTail: {
            dependencyType: "stateVariable",
            variableName: "basedOnTail"
          },
          basedOnDisplacement: {
            dependencyType: "stateVariable",
            variableName: "basedOnDisplacement"
          },
          tailShadow: {
            dependencyType: "stateVariable",
            variableName: "tailShadow"
          },
          tailAttr: {
            dependencyType: "attributeComponent",
            attributeName: "tail",
            variableNames: ["numDimensions"]
          }
        };
        if (!stateValues.basedOnTail) {
          if (stateValues.basedOnHead) {
            dependencies.numDimHead = {
              dependencyType: "stateVariable",
              variableName: "numDimHead"
            };
          }
          if (stateValues.basedOnDisplacement) {
            dependencies.numDimDisplacement = {
              dependencyType: "stateVariable",
              variableName: "numDimDisplacement"
            };
          }
        }
        return dependencies;
      },
      definition: function({ dependencyValues }) {
        let numDimTail;
        if (dependencyValues.basedOnTail) {
          if (dependencyValues.tailAttr !== null) {
            numDimTail = dependencyValues.tailAttr.stateValues.numDimensions;
          } else if (dependencyValues.tailShadow) {
            let tailTree = dependencyValues.tailShadow.tree;
            if (Array.isArray(tailTree) && vectorOperators.includes(tailTree[0])) {
              numDimTail = tailTree.length - 1;
            } else {
              numDimTail = 2;
            }
          }
        } else {
          if (dependencyValues.basedOnDisplacement) {
            if (dependencyValues.basedOnHead) {
              if (dependencyValues.numDimDisplacement === dependencyValues.numDimHead) {
                numDimTail = dependencyValues.numDimDisplacement;
              } else {
                numDimTail = NaN;
              }
            } else {
              numDimTail = dependencyValues.numDimDisplacement;
            }
          } else if (dependencyValues.basedOnHead) {
            numDimTail = dependencyValues.numDimHead;
          } else {
            numDimTail = 2;
          }
        }
        return {
          setValue: { numDimTail },
          checkForActualChange: { numDimTail: true }
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions of the vector.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        basedOnHead: {
          dependencyType: "stateVariable",
          variableName: "basedOnHead"
        },
        basedOnTail: {
          dependencyType: "stateVariable",
          variableName: "basedOnTail"
        },
        basedOnDisplacement: {
          dependencyType: "stateVariable",
          variableName: "basedOnDisplacement"
        },
        numDimDisplacement: {
          dependencyType: "stateVariable",
          variableName: "numDimDisplacement"
        },
        numDimHead: {
          dependencyType: "stateVariable",
          variableName: "numDimHead"
        },
        numDimTail: {
          dependencyType: "stateVariable",
          variableName: "numDimTail"
        }
      }),
      definition: function({ dependencyValues }) {
        let numDimensions;
        if (dependencyValues.basedOnDisplacement) {
          if (dependencyValues.basedOnTail) {
            if (dependencyValues.numDimDisplacement !== dependencyValues.numDimTail) {
              let warning = {
                message: "numDimensions mismatch in vector.",
                type: "warning"
              };
              return {
                setValue: { numDimensions: NaN },
                sendDiagnostics: [warning]
              };
            }
          } else if (dependencyValues.basedOnHead) {
            if (dependencyValues.numDimDisplacement !== dependencyValues.numDimHead) {
              let warning = {
                message: "numDimensions mismatch in vector.",
                type: "warning"
              };
              return {
                setValue: { numDimensions: NaN },
                sendDiagnostics: [warning]
              };
            }
          }
          numDimensions = dependencyValues.numDimDisplacement;
        } else if (dependencyValues.basedOnTail) {
          if (dependencyValues.basedOnHead) {
            if (dependencyValues.numDimTail !== dependencyValues.numDimHead) {
              let warning = {
                message: "numDimensions mismatch in vector.",
                type: "warning"
              };
              return {
                setValue: { numDimensions: NaN },
                sendDiagnostics: [warning]
              };
            }
          }
          numDimensions = dependencyValues.numDimTail;
        } else if (dependencyValues.basedOnHead) {
          numDimensions = dependencyValues.numDimHead;
        } else {
          numDimensions = 2;
        }
        return {
          setValue: { numDimensions },
          checkForActualChange: { numDimensions: true }
        };
      }
    };
    stateVariableDefinitions.displacement = {
      public: true,
      description: "The displacement (head − tail) of the vector.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        attributesToShadow: ["displayWithAngleBrackets"],
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "x") {
            return [];
          } else {
            return [
              [
                "vector",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      indexAliases: [["x", "y", "z"]],
      entryPrefixes: ["x"],
      hasEssential: true,
      essentialVarName: "displacement2",
      // since "displacement" used for displacementShadow
      set: convertValueToMathExpression,
      stateVariablesDeterminingDependencies: [
        "basedOnDisplacement",
        "basedOnHead",
        "sourceOfDisplacement"
      ],
      returnArraySizeDependencies: () => ({
        numDimDisplacement: {
          dependencyType: "stateVariable",
          variableName: "numDimDisplacement"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimDisplacement];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          basedOnHead: {
            dependencyType: "stateVariable",
            variableName: "basedOnHead"
          },
          basedOnTail: {
            dependencyType: "stateVariable",
            variableName: "basedOnTail"
          },
          basedOnDisplacement: {
            dependencyType: "stateVariable",
            variableName: "basedOnDisplacement"
          },
          displacementShadow: {
            dependencyType: "stateVariable",
            variableName: "displacementShadow"
          },
          displacementAttr: {
            dependencyType: "attributeComponent",
            attributeName: "displacement",
            variableNames: ["value"]
          },
          sourceOfDisplacement: {
            dependencyType: "stateVariable",
            variableName: "sourceOfDisplacement"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            xsAttr: {
              dependencyType: "attributeComponent",
              attributeName: "xs",
              variableNames: ["math" + varEnding]
            },
            pointOrVectorChild: {
              dependencyType: "child",
              childGroups: ["pointsAndVectors"],
              variableNames: ["x" + varEnding]
            }
          };
          if (arrayKey === "0") {
            dependenciesByKey[arrayKey].componentAttr = {
              dependencyType: "attributeComponent",
              attributeName: "x",
              variableNames: ["value"]
            };
          } else if (arrayKey === "1") {
            dependenciesByKey[arrayKey].componentAttr = {
              dependencyType: "attributeComponent",
              attributeName: "y",
              variableNames: ["value"]
            };
          } else if (arrayKey === "2") {
            dependenciesByKey[arrayKey].componentAttr = {
              dependencyType: "attributeComponent",
              attributeName: "z",
              variableNames: ["value"]
            };
          }
          if (!stateValues.basedOnDisplacement && stateValues.basedOnHead) {
            dependenciesByKey[arrayKey].tailX = {
              dependencyType: "stateVariable",
              variableName: "tailX" + varEnding
            };
            dependenciesByKey[arrayKey].headX = {
              dependencyType: "stateVariable",
              variableName: "headX" + varEnding
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let displacement = {};
        let essentialDisplacement = {};
        let prescribedDisplacement;
        if (globalDependencyValues.basedOnDisplacement) {
          if (globalDependencyValues.sourceOfDisplacement === "displacementAttr") {
            prescribedDisplacement = globalDependencyValues.displacementAttr.stateValues.value;
          } else if (globalDependencyValues.sourceOfDisplacement === null) {
            prescribedDisplacement = globalDependencyValues.displacementShadow.tuples_to_vectors();
          }
        }
        if (prescribedDisplacement) {
          prescribedDisplacement = prescribedDisplacement.expand().simplify();
          let displacementTree = prescribedDisplacement.tree;
          if (Array.isArray(displacementTree) && vectorOperators.includes(displacementTree[0])) {
            for (let arrayKey of arrayKeys) {
              let ind = Number(arrayKey);
              if (ind >= 0 || ind < displacementTree.length - 1) {
                if (displacementTree[ind + 1] === void 0) {
                  displacement[arrayKey] = me$3.fromAst("＿");
                } else {
                  displacement[arrayKey] = prescribedDisplacement.get_component(
                    ind
                  );
                }
              }
            }
          } else {
            if (arrayKeys.includes("0")) {
              displacement[0] = prescribedDisplacement;
            }
          }
        } else {
          for (let arrayKey of arrayKeys) {
            let varEnding = Number(arrayKey) + 1;
            if (globalDependencyValues.basedOnDisplacement) {
              switch (globalDependencyValues.sourceOfDisplacement) {
                case "pointOrVectorChild":
                  displacement[arrayKey] = dependencyValuesByKey[arrayKey].pointOrVectorChild[0].stateValues["x" + varEnding];
                  break;
                case "xsAttr":
                  displacement[arrayKey] = dependencyValuesByKey[arrayKey].xsAttr.stateValues["math" + varEnding].simplify();
                  break;
                case "componentAttrs":
                  let componentAttr = dependencyValuesByKey[arrayKey].componentAttr;
                  if (componentAttr === null) {
                    essentialDisplacement[arrayKey] = {
                      defaultValue: me$3.fromAst(0)
                    };
                  } else {
                    displacement[arrayKey] = componentAttr.stateValues.value.simplify();
                  }
                  break;
              }
            } else if (globalDependencyValues.basedOnHead) {
              displacement[arrayKey] = dependencyValuesByKey[arrayKey].headX.subtract(dependencyValuesByKey[arrayKey].tailX).simplify();
            } else {
              essentialDisplacement[arrayKey] = {
                defaultValue: me$3.fromAst(
                  arrayKey === "0" ? 1 : 0
                )
              };
            }
          }
        }
        let result = {};
        if (Object.keys(displacement).length > 0) {
          result.setValue = { displacement };
        }
        if (Object.keys(essentialDisplacement).length > 0) {
          result.useEssentialOrDefaultValue = {
            displacement: essentialDisplacement
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        if (globalDependencyValues.basedOnDisplacement) {
          if (globalDependencyValues.sourceOfDisplacement === "displacementAttr") {
            if (arraySize[0] > 1) {
              let desiredDisplacement = ["vector"];
              for (let arrayKey in desiredStateVariableValues.displacement) {
                desiredDisplacement[Number(arrayKey) + 1] = desiredStateVariableValues.displacement[arrayKey].tree;
              }
              desiredDisplacement.length = arraySize[0] + 1;
              instructions.push({
                setDependency: "displacementAttr",
                desiredValue: me$3.fromAst(desiredDisplacement),
                variableIndex: 0
              });
            } else if (arraySize[0] === 1 && "0" in desiredStateVariableValues.displacement) {
              instructions.push({
                setDependency: "displacementAttr",
                desiredValue: desiredStateVariableValues.displacement[0],
                variableIndex: 0
              });
            }
            return {
              success: true,
              instructions
            };
          } else if (globalDependencyValues.sourceOfDisplacement === null) {
            if (arraySize[0] > 1) {
              let desiredDisplacement = ["vector"];
              for (let arrayKey in desiredStateVariableValues.displacement) {
                desiredDisplacement[Number(arrayKey) + 1] = desiredStateVariableValues.displacement[arrayKey].tree;
              }
              desiredDisplacement.length = arraySize[0] + 1;
              instructions.push({
                setDependency: "displacementShadow",
                desiredValue: me$3.fromAst(desiredDisplacement)
              });
            } else if (arraySize[0] === 1 && "0" in desiredStateVariableValues.displacement) {
              instructions.push({
                setDependency: "displacementShadow",
                desiredValue: desiredStateVariableValues.displacement[0]
              });
            }
            return {
              success: true,
              instructions
            };
          }
        }
        for (let arrayKey in desiredStateVariableValues.displacement) {
          if (globalDependencyValues.basedOnDisplacement) {
            switch (globalDependencyValues.sourceOfDisplacement) {
              case "pointOrVectorChild":
                instructions.push({
                  setDependency: dependencyNamesByKey[arrayKey].pointOrVectorChild,
                  desiredValue: desiredStateVariableValues.displacement[arrayKey],
                  childIndex: 0,
                  variableIndex: 0
                });
                break;
              case "xsAttr":
                instructions.push({
                  setDependency: dependencyNamesByKey[arrayKey].xsAttr,
                  desiredValue: desiredStateVariableValues.displacement[arrayKey],
                  variableIndex: 0
                });
                break;
              case "componentAttrs":
                let componentAttr = dependencyValuesByKey[arrayKey].componentAttr;
                if (componentAttr === null) {
                  instructions.push({
                    setEssentialValue: "displacement",
                    value: {
                      [arrayKey]: convertValueToMathExpression(
                        desiredStateVariableValues.displacement[arrayKey]
                      )
                    }
                  });
                } else {
                  instructions.push({
                    setDependency: dependencyNamesByKey[arrayKey].componentAttr,
                    desiredValue: desiredStateVariableValues.displacement[arrayKey],
                    variableIndex: 0
                  });
                }
                break;
            }
          } else if (globalDependencyValues.basedOnHead) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].headX,
              desiredValue: dependencyValuesByKey[arrayKey].tailX.add(
                desiredStateVariableValues.displacement[arrayKey]
              ).simplify()
            });
          } else {
            instructions.push({
              setEssentialValue: "displacement",
              value: {
                [arrayKey]: convertValueToMathExpression(
                  desiredStateVariableValues.displacement[arrayKey]
                )
              }
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.x = {
      isAlias: true,
      targetVariableName: "x1",
      description: "The first component (x) of the vector's displacement."
    };
    stateVariableDefinitions.y = {
      isAlias: true,
      targetVariableName: "x2",
      description: "The second component (y) of the vector's displacement."
    };
    stateVariableDefinitions.z = {
      isAlias: true,
      targetVariableName: "x3",
      description: "The third component (z) of the vector's displacement."
    };
    stateVariableDefinitions.head = {
      public: true,
      description: "Coordinates of the vector's head.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "headX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      indexAliases: [["x", "y", "z"]],
      entryPrefixes: ["headX"],
      set: convertValueToMathExpression,
      stateVariablesDeterminingDependencies: ["basedOnHead"],
      returnArraySizeDependencies: () => ({
        numDimHead: {
          dependencyType: "stateVariable",
          variableName: "numDimHead"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimHead];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          basedOnDisplacement: {
            dependencyType: "stateVariable",
            variableName: "basedOnDisplacement"
          },
          basedOnTail: {
            dependencyType: "stateVariable",
            variableName: "basedOnTail"
          },
          basedOnHead: {
            dependencyType: "stateVariable",
            variableName: "basedOnHead"
          },
          headShadow: {
            dependencyType: "stateVariable",
            variableName: "headShadow"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            headAttr: {
              dependencyType: "attributeComponent",
              attributeName: "head",
              variableNames: ["x" + varEnding]
            }
          };
          if (!stateValues.basedOnHead) {
            dependenciesByKey[arrayKey].tailX = {
              dependencyType: "stateVariable",
              variableName: "tailX" + varEnding
            };
            dependenciesByKey[arrayKey].x = {
              dependencyType: "stateVariable",
              variableName: "x" + varEnding
            };
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let head = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (globalDependencyValues.basedOnHead) {
            if (dependencyValuesByKey[arrayKey].headAttr !== null) {
              head[arrayKey] = dependencyValuesByKey[arrayKey].headAttr.stateValues["x" + varEnding];
            } else if (globalDependencyValues.headShadow !== null) {
              head[arrayKey] = globalDependencyValues.headShadow.get_component(
                Number(arrayKey)
              );
            }
          } else {
            head[arrayKey] = dependencyValuesByKey[arrayKey].tailX.add(dependencyValuesByKey[arrayKey].x).simplify();
          }
        }
        return { setValue: { head } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        let updateHeadShadow = false;
        for (let arrayKey in desiredStateVariableValues.head) {
          if (globalDependencyValues.basedOnHead) {
            if (dependencyValuesByKey[arrayKey].headAttr && dependencyValuesByKey[arrayKey].headAttr !== null) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].headAttr,
                desiredValue: desiredStateVariableValues.head[arrayKey],
                variableIndex: 0
              });
            } else if (globalDependencyValues.headShadow !== null) {
              updateHeadShadow = true;
            }
          } else {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].x,
              desiredValue: desiredStateVariableValues.head[arrayKey].subtract(dependencyValuesByKey[arrayKey].tailX).simplify()
            });
          }
        }
        if (updateHeadShadow) {
          if (arraySize[0] > 1) {
            let desiredHead = ["vector"];
            for (let arrayKey in desiredStateVariableValues.head) {
              desiredHead[Number(arrayKey) + 1] = desiredStateVariableValues.head[arrayKey].tree;
            }
            desiredHead.length = arraySize[0] + 1;
            instructions.push({
              setDependency: "headShadow",
              desiredValue: me$3.fromAst(desiredHead)
            });
          } else if (arraySize[0] === 1 && "0" in desiredStateVariableValues.head) {
            instructions.push({
              setDependency: "headShadow",
              desiredValue: desiredStateVariableValues.head[0]
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.tail = {
      public: true,
      description: "Coordinates of the vector's tail.",
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "tailX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      indexAliases: [["x", "y", "z"]],
      entryPrefixes: ["tailX"],
      hasEssential: true,
      defaultValueByArrayKey: () => me$3.fromAst(0),
      essentialVarName: "tail2",
      // since tailShadow uses "tail"
      set: convertValueToMathExpression,
      stateVariablesDeterminingDependencies: [
        "basedOnTail",
        "basedOnHead",
        "basedOnDisplacement"
      ],
      returnArraySizeDependencies: () => ({
        numDimTail: {
          dependencyType: "stateVariable",
          variableName: "numDimTail"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimTail];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let globalDependencies = {
          basedOnDisplacement: {
            dependencyType: "stateVariable",
            variableName: "basedOnDisplacement"
          },
          basedOnHead: {
            dependencyType: "stateVariable",
            variableName: "basedOnHead"
          },
          tailShadow: {
            dependencyType: "stateVariable",
            variableName: "tailShadow"
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          dependenciesByKey[arrayKey] = {
            tailAttr: {
              dependencyType: "attributeComponent",
              attributeName: "tail",
              variableNames: ["x" + varEnding]
            }
          };
          if (!stateValues.basedOnTail) {
            if (stateValues.basedOnHead && stateValues.basedOnDisplacement) {
              dependenciesByKey[arrayKey].headX = {
                dependencyType: "stateVariable",
                variableName: "headX" + varEnding
              };
              dependenciesByKey[arrayKey].x = {
                dependencyType: "stateVariable",
                variableName: "x" + varEnding
              };
            }
          }
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys
      }) {
        let tail = {};
        let essentialTail = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = Number(arrayKey) + 1;
          if (dependencyValuesByKey[arrayKey].tailAttr !== null) {
            tail[arrayKey] = dependencyValuesByKey[arrayKey].tailAttr.stateValues["x" + varEnding];
          } else if (globalDependencyValues.tailShadow !== null) {
            tail[arrayKey] = globalDependencyValues.tailShadow.get_component(
              Number(arrayKey)
            );
          } else {
            if (globalDependencyValues.basedOnHead && globalDependencyValues.basedOnDisplacement) {
              tail[arrayKey] = dependencyValuesByKey[arrayKey].headX.subtract(dependencyValuesByKey[arrayKey].x).simplify();
            } else {
              essentialTail[arrayKey] = true;
            }
          }
        }
        let result = {};
        if (Object.keys(tail).length > 0) {
          result.setValue = { tail };
        }
        if (Object.keys(essentialTail).length > 0) {
          result.useEssentialOrDefaultValue = { tail: essentialTail };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize
      }) {
        let instructions = [];
        let updateTailShadow = false;
        for (let arrayKey in desiredStateVariableValues.tail) {
          if (dependencyValuesByKey[arrayKey].tailAttr && dependencyValuesByKey[arrayKey].tailAttr !== null) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].tailAttr,
              desiredValue: desiredStateVariableValues.tail[arrayKey],
              variableIndex: 0
            });
          } else if (globalDependencyValues.tailShadow !== null) {
            updateTailShadow = true;
          } else {
            if (globalDependencyValues.basedOnHead && globalDependencyValues.basedOnDisplacement) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].x,
                desiredValue: dependencyValuesByKey[arrayKey].headX.subtract(
                  desiredStateVariableValues.tail[arrayKey]
                ).simplify()
              });
            } else {
              instructions.push({
                setEssentialValue: "tail",
                value: {
                  [arrayKey]: convertValueToMathExpression(
                    desiredStateVariableValues.tail[arrayKey]
                  )
                }
              });
            }
          }
        }
        if (updateTailShadow) {
          if (arraySize[0] > 1) {
            let desiredTail = ["vector"];
            for (let arrayKey in desiredStateVariableValues.tail) {
              desiredTail[Number(arrayKey) + 1] = desiredStateVariableValues.tail[arrayKey].tree;
            }
            desiredTail.length = arraySize[0] + 1;
            instructions.push({
              setDependency: "tailShadow",
              desiredValue: me$3.fromAst(desiredTail)
            });
          } else if (arraySize[0] === 1 && "0" in desiredStateVariableValues.tail) {
            instructions.push({
              setDependency: "tailShadow",
              desiredValue: desiredStateVariableValues.tail[0]
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.magnitude = {
      description: "The magnitude (length) of the vector.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        displacement: {
          dependencyType: "stateVariable",
          variableName: "displacement"
        }
      }),
      definition({ dependencyValues }) {
        let magnitude2 = 0;
        let all_numeric = true;
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          let disp = dependencyValues.displacement[dim].evaluate_to_constant();
          if (!Number.isFinite(disp)) {
            all_numeric = false;
            break;
          }
          magnitude2 += disp * disp;
        }
        if (all_numeric) {
          return {
            setValue: {
              magnitude: me$3.fromAst(Math.sqrt(magnitude2))
            }
          };
        }
        magnitude2 = ["+"];
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          magnitude2.push([
            "^",
            dependencyValues.displacement[dim],
            2
          ]);
        }
        return {
          setValue: {
            magnitude: me$3.fromAst(["apply", "sqrt", magnitude2])
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let dir = [];
        let dir_length2 = 0;
        let all_numeric = true;
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          let disp = dependencyValues.displacement[dim].evaluate_to_constant();
          if (!Number.isFinite(disp)) {
            all_numeric = false;
            break;
          }
          dir.push(disp);
          dir_length2 += disp * disp;
        }
        if (!all_numeric) {
          return { success: false };
        }
        let dir_length = Math.sqrt(dir_length2);
        dir = dir.map((x2) => x2 / dir_length);
        let desiredMagnitude = desiredStateVariableValues.magnitude.evaluate_to_constant();
        if (!Number.isFinite(desiredMagnitude) || desiredMagnitude < 0) {
          return { success: false };
        }
        let desiredDisplacement = [];
        for (let dim = 0; dim < dependencyValues.numDimensions; dim++) {
          desiredDisplacement.push(
            me$3.fromAst(dir[dim] * desiredMagnitude)
          );
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "displacement",
              desiredValue: desiredDisplacement
            }
          ]
        };
      }
    };
    stateVariableDefinitions.numericalEndpoints = {
      forRenderer: true,
      returnDependencies: () => ({
        head: {
          dependencyType: "stateVariable",
          variableName: "head"
        },
        tail: {
          dependencyType: "stateVariable",
          variableName: "tail"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      definition: function({ dependencyValues }) {
        let numericalHead, numericalTail;
        if (dependencyValues.numDimensions === 1) {
          numericalHead = dependencyValues.head[0].evaluate_to_constant();
          numericalTail = dependencyValues.tail[0].evaluate_to_constant();
        } else {
          numericalHead = [];
          numericalTail = [];
          for (let i2 = 0; i2 < dependencyValues.numDimensions; i2++) {
            let head = dependencyValues.head[i2].evaluate_to_constant();
            numericalHead.push(head);
            let tail = dependencyValues.tail[i2].evaluate_to_constant();
            numericalTail.push(tail);
          }
        }
        return {
          setValue: {
            numericalEndpoints: [numericalTail, numericalHead]
          }
        };
      }
    };
    stateVariableDefinitions.displacementCoords = {
      isLocation: true,
      returnDependencies: () => ({
        displacement: {
          dependencyType: "stateVariable",
          variableName: "displacement"
        },
        displayWithAngleBrackets: {
          dependencyType: "stateVariable",
          variableName: "displayWithAngleBrackets"
        }
      }),
      definition({ dependencyValues }) {
        let coordsAst = [];
        for (let v2 of dependencyValues.displacement) {
          if (v2) {
            coordsAst.push(v2.tree);
          } else {
            coordsAst.push("＿");
          }
        }
        if (coordsAst.length > 1) {
          let operator = dependencyValues.displayWithAngleBrackets ? "altvector" : "vector";
          coordsAst = [operator, ...coordsAst];
        } else if (coordsAst.length === 1) {
          coordsAst = coordsAst[0];
        } else {
          coordsAst = "＿";
        }
        return {
          setValue: { displacementCoords: me$3.fromAst(coordsAst) }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let coordsAst = desiredStateVariableValues.displacementCoords.tree;
        let newDisplacement;
        if (Array.isArray(coordsAst) && vectorOperators.includes(coordsAst[0])) {
          newDisplacement = coordsAst.slice(1).map((x2) => me$3.fromAst(x2));
        } else {
          newDisplacement = [
            desiredStateVariableValues.displacementCoords
          ];
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "displacement",
              desiredValue: newDisplacement
            }
          ]
        };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The vector rendered as a LaTeX string.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        displacementCoords: {
          dependencyType: "stateVariable",
          variableName: "displacementCoords"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let latex = roundForDisplay({
          value: dependencyValues.displacementCoords,
          dependencyValues
        }).toLatex(params);
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.value = {
      isAlias: true,
      targetVariableName: "displacementCoords",
      description: "The vector's displacement as a single math expression."
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalEndpoints: {
          dependencyType: "stateVariable",
          variableName: "numericalEndpoints"
        }
      }),
      definition({ dependencyValues }) {
        let A1 = dependencyValues.numericalEndpoints[0]?.[0];
        let A2 = dependencyValues.numericalEndpoints[0]?.[1];
        let B1 = dependencyValues.numericalEndpoints[1]?.[0];
        let B2 = dependencyValues.numericalEndpoints[1]?.[1];
        let haveConstants = Number.isFinite(A1) && Number.isFinite(A2) && Number.isFinite(B1) && Number.isFinite(B2);
        let skip = dependencyValues.numDimensions !== 2 || !haveConstants || B1 === A1 && B2 === A2;
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              if (skip) {
                return {};
              }
              let xscale = scales[0];
              let yscale = scales[1];
              let BA1 = (B1 - A1) / xscale;
              let BA2 = (B2 - A2) / yscale;
              let denom = BA1 * BA1 + BA2 * BA2;
              let t2 = ((variables.x1 - A1) / xscale * BA1 + (variables.x2 - A2) / yscale * BA2) / denom;
              let result = {};
              if (t2 <= 0) {
                result = { x1: A1, x2: A2 };
              } else if (t2 >= 1) {
                result = { x1: B1, x2: B2 };
              } else {
                result = {
                  x1: A1 + t2 * BA1 * xscale,
                  x2: A2 + t2 * BA2 * yscale
                };
              }
              if (variables.x3 !== void 0) {
                result.x3 = 0;
              }
              return result;
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "displacementCoords",
      componentType: "_directionComponent",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    },
    {
      stateVariable: "displacementCoords",
      componentType: "coords",
      stateVariablesToShadow: [
        ...Object.keys(returnNumberDisplayStateVariableDefinitions()),
        "inUnorderedList"
      ]
    },
    {
      stateVariable: "displacementCoords",
      componentType: "point",
      stateVariablesToShadow: [
        ...Object.keys(returnNumberDisplayStateVariableDefinitions()),
        "inUnorderedList"
      ]
    }
  ];
  async moveVectorSinglePoint({
    x: x2,
    y: y2,
    pointRole,
    transient,
    skippable,
    actionId,
    sourceDetails,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!Number.isFinite(x2) || !Number.isFinite(y2)) {
      console.warn(
        `Invalid vector point coordinates: x=${x2}, y=${y2}, role=${pointRole}`
      );
      return;
    }
    if (pointRole === "head") {
      return await this.moveVector({
        headcoords: [x2, y2],
        transient,
        skippable,
        actionId,
        sourceDetails,
        sourceInformation,
        skipRendererUpdate
      });
    } else if (pointRole === "tail") {
      return await this.moveVector({
        tailcoords: [x2, y2],
        transient,
        skippable,
        actionId,
        sourceDetails,
        sourceInformation,
        skipRendererUpdate
      });
    } else if (pointRole === "displacement") {
      return await this.moveVector({
        displacement: [x2, y2],
        transient,
        skippable,
        actionId,
        sourceDetails,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      console.warn(`Invalid pointRole for vector: ${pointRole}`);
      return;
    }
  }
  async moveVector({
    tailcoords,
    headcoords,
    displacement,
    transient,
    skippable,
    sourceDetails,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!transient) {
      skippable = false;
    }
    if (displacement !== void 0 && tailcoords === void 0 && headcoords === void 0) {
      if (!await this.stateValues.headDraggable) {
        return;
      }
      const updateInstructions2 = [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "displacement",
          value: displacement.map((x2) => me$3.fromAst(x2)),
          sourceDetails
        }
      ];
      if (transient) {
        await this.coreFunctions.performUpdate({
          updateInstructions: updateInstructions2,
          transient,
          skippable,
          actionId,
          sourceInformation,
          skipRendererUpdate: true
        });
      } else {
        await this.coreFunctions.performUpdate({
          updateInstructions: updateInstructions2,
          actionId,
          sourceInformation,
          skipRendererUpdate: true,
          event: {
            verb: "interacted",
            object: {
              componentIdx: this.componentIdx,
              componentType: this.componentType
            },
            result: {
              displacement
            }
          }
        });
      }
      return await this.coreFunctions.updateRenderers({
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
    if (tailcoords !== void 0) {
      if (headcoords !== void 0) {
        if (!await this.stateValues.draggable) {
          return;
        }
      } else {
        if (!await this.stateValues.tailDraggable) {
          return;
        }
      }
    } else {
      if (!await this.stateValues.headDraggable) {
        return;
      }
    }
    let updateInstructions = [];
    if (tailcoords !== void 0) {
      if (await this.stateValues.basedOnHead && await this.stateValues.basedOnDisplacement) {
        let displacement2;
        if (headcoords === void 0) {
          let numericalEndpoints = await this.stateValues.numericalEndpoints;
          displacement2 = tailcoords.map(
            (x2, i2) => numericalEndpoints[1][i2] - x2
          );
        } else {
          displacement2 = tailcoords.map((x2, i2) => headcoords[i2] - x2);
        }
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "displacement",
          value: displacement2.map((x2) => me$3.fromAst(x2)),
          sourceDetails
        });
      } else {
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "tail",
          value: tailcoords.map((x2) => me$3.fromAst(x2)),
          sourceDetails
        });
      }
      if (headcoords === void 0) {
        if (!await this.stateValues.basedOnHead) {
          let numericalEndpoints = await this.stateValues.numericalEndpoints;
          let displacement2 = tailcoords.map(
            (x2, i2) => numericalEndpoints[1][i2] - x2
          );
          updateInstructions.push({
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "displacement",
            value: displacement2.map((x2) => me$3.fromAst(x2)),
            sourceDetails
          });
        }
      }
    }
    if (headcoords !== void 0) {
      if (await this.stateValues.basedOnHead) {
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "head",
          value: headcoords.map((x2) => me$3.fromAst(x2)),
          sourceDetails
        });
      } else {
        if (tailcoords == void 0) {
          tailcoords = (await this.stateValues.numericalEndpoints)[0];
        }
        let displacement2 = tailcoords.map((x2, i2) => headcoords[i2] - x2);
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "displacement",
          value: displacement2.map((x2) => me$3.fromAst(x2)),
          sourceDetails
        });
      }
      if (tailcoords === void 0) {
        if (await this.stateValues.basedOnHead && await this.stateValues.basedOnDisplacement) {
          let numericalEndpoints = await this.stateValues.numericalEndpoints;
          let displacement2 = headcoords.map(
            (x2, i2) => x2 - numericalEndpoints[0][i2]
          );
          updateInstructions.push({
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "displacement",
            value: displacement2.map((x2) => me$3.fromAst(x2)),
            sourceDetails
          });
        }
      }
    }
    if (transient) {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        transient,
        skippable,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
    } else {
      await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            head: headcoords,
            tail: tailcoords
          }
        }
      });
    }
    if (tailcoords !== void 0 && headcoords !== void 0 && await this.stateValues.basedOnTail && await this.stateValues.basedOnHead) {
      let numericalPoints = [tailcoords, headcoords];
      let resultingNumericalPoints = await this.stateValues.numericalEndpoints;
      let pointsChanged = [];
      let numPointsChanged = 0;
      for (let [ind, pt2] of numericalPoints.entries()) {
        if (!pt2.every((v2, i2) => v2 === resultingNumericalPoints[ind][i2])) {
          pointsChanged.push(ind);
          numPointsChanged++;
        }
      }
      if (numPointsChanged === 1) {
        let changedInd = pointsChanged[0];
        let orig1 = numericalPoints[changedInd];
        let changed1 = resultingNumericalPoints[changedInd];
        let changevec1 = orig1.map((v2, i2) => v2 - changed1[i2]);
        let newNumericalPoints = [];
        for (let i2 = 0; i2 < 2; i2++) {
          if (i2 === changedInd) {
            newNumericalPoints.push(resultingNumericalPoints[i2]);
          } else {
            newNumericalPoints.push(
              numericalPoints[i2].map((v2, j2) => v2 - changevec1[j2])
            );
          }
        }
        let newInstructions = [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "tail",
            value: newNumericalPoints[0].map((x2) => me$3.fromAst(x2))
          },
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "head",
            value: newNumericalPoints[1].map((x2) => me$3.fromAst(x2))
          }
        ];
        return await this.coreFunctions.performUpdate({
          updateInstructions: newInstructions,
          transient,
          skippable,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      }
    }
    return await this.coreFunctions.updateRenderers({
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async vectorClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async vectorFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class Angle extends GraphicalComponent {
  static componentType = "angle";
  static styleOverrideCategories = ["fill"];
  static componentDocs = {
    summary: "An angle defined by three points or a measure"
  };
  static canBeInList = true;
  static representsClosedPath = true;
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["number", "math", "string"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.radius = {
      createComponentOfType: "math",
      createStateVariable: "radius",
      defaultValue: me$3.fromAst(1),
      public: true,
      description: "Radius of the rendered angle arc."
    };
    attributes.chooseReflexAngle = {
      createComponentOfType: "text",
      createStateVariable: "chooseReflexAngle",
      defaultValue: "never",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "never",
          description: "Always normalize to the non-reflex angle (less than 180°)."
        },
        {
          value: "allowed",
          description: "Use a reflex angle (greater than 180°) if it matches the input."
        },
        {
          value: "always",
          description: "Always render the angle as a reflex angle."
        }
      ],
      description: "How to handle reflex angles."
    };
    attributes.inDegrees = {
      createComponentOfType: "boolean",
      createStateVariable: "inDegrees",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether to interpret and display the angle in degrees rather than radians."
    };
    attributes.radians = {
      createComponentOfType: "math",
      description: "The angle measure in radians."
    };
    attributes.degrees = {
      createComponentOfType: "math",
      description: "The angle measure in degrees."
    };
    attributes.through = {
      createComponentOfType: "pointList",
      description: "Three points (vertex in the middle) defining the angle."
    };
    attributes.betweenLines = {
      createComponentOfType: "_lineListComponent",
      description: "Two lines whose intersection forms the angle."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.emphasizeRightAngle = {
      createComponentOfType: "boolean",
      createStateVariable: "emphasizeRightAngle",
      defaultValue: true,
      public: true,
      forRenderer: true,
      description: "Whether to render right angles with the conventional square symbol."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        wrappingComponentType: "math",
        createAttributeOfType: "radians"
      })
    });
    return sugarInstructions;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.filled = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { filled: true } })
    };
    stateVariableDefinitions.betweenLinesName = {
      returnDependencies: () => ({
        betweenLines: {
          dependencyType: "attributeComponent",
          attributeName: "betweenLines"
        }
      }),
      definition({ dependencyValues }) {
        let betweenLinesName = null;
        if (dependencyValues.betweenLines !== null) {
          betweenLinesName = dependencyValues.betweenLines.componentIdx;
        }
        return { setValue: { betweenLinesName } };
      }
    };
    stateVariableDefinitions.numPointsSpecified = {
      returnDependencies: () => ({
        through: {
          dependencyType: "attributeComponent",
          attributeName: "through",
          variableNames: ["numPoints"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.through !== null) {
          return {
            setValue: {
              numPointsSpecified: dependencyValues.through.stateValues.numPoints
            }
          };
        } else {
          return { setValue: { numPointsSpecified: 0 } };
        }
      }
    };
    stateVariableDefinitions.points = {
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["pointX", "point"],
      returnEntryDimensions: (prefix) => prefix === "point" ? 1 : 0,
      stateVariablesDeterminingDependencies: ["betweenLinesName"],
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [3, 2];
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "pointX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      returnArrayDependenciesByKey({ stateValues }) {
        let globalDependencies = {
          numPointsSpecified: {
            dependencyType: "stateVariable",
            variableName: "numPointsSpecified"
          },
          throughAttr: {
            dependencyType: "attributeComponent",
            attributeName: "through",
            variableNames: ["points"]
          },
          radiansAttr: {
            dependencyType: "attributeComponent",
            attributeName: "radians",
            variableNames: ["value"]
          },
          degreesAttr: {
            dependencyType: "attributeComponent",
            attributeName: "degrees",
            variableNames: ["value"]
          }
        };
        if (stateValues.betweenLinesName !== null) {
          globalDependencies.lineChildren = {
            dependencyType: "child",
            parentIdx: stateValues.betweenLinesName,
            childGroups: ["lines"],
            variableNames: [
              "points",
              "numDimensions",
              "coeff0",
              "coeffvar1",
              "coeffvar2",
              "nearestPoint"
            ]
          };
        }
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        if (globalDependencyValues.lineChildren) {
          if (globalDependencyValues.lineChildren.length > 2) {
            let warning = {
              message: `Cannot define an angle between ${globalDependencyValues.lineChildren.length} lines`,
              type: "info"
            };
            let points2 = {};
            for (let i2 = 0; i2 < 3; i2++) {
              for (let j2 = 0; j2 < 2; j2++) {
                points2[i2 + "," + j2] = me$3.fromAst("＿");
              }
            }
            return {
              setValue: { points: points2 },
              sendDiagnostics: [warning]
            };
          } else if (globalDependencyValues.lineChildren.length === 1) {
            let line1 = globalDependencyValues.lineChildren[0];
            if (line1.stateValues.numDimensions !== 2) {
              let points3 = {};
              for (let i2 = 0; i2 < 3; i2++) {
                for (let j2 = 0; j2 < 2; j2++) {
                  points3[i2 + "," + j2] = me$3.fromAst("＿");
                }
              }
              return { setValue: { points: points3 } };
            }
            let pointNearOrigin = line1.stateValues.nearestPoint({
              variables: {
                x1: me$3.fromAst(0),
                x2: me$3.fromAst(0)
              }
            });
            let a1 = line1.stateValues.points[0][0].evaluate_to_constant();
            let a2 = line1.stateValues.points[0][1].evaluate_to_constant();
            let b1 = line1.stateValues.points[1][0].evaluate_to_constant();
            let b2 = line1.stateValues.points[1][1].evaluate_to_constant();
            let angleOfLine = Math.atan2(b2 - a2, b1 - a1);
            let points2 = {
              "0,0": me$3.fromAst(
                pointNearOrigin.x1 + Math.cos(angleOfLine)
              ),
              "0,1": me$3.fromAst(
                pointNearOrigin.x2 + Math.sin(angleOfLine)
              ),
              "1,0": me$3.fromAst(pointNearOrigin.x1),
              "1,1": me$3.fromAst(pointNearOrigin.x2)
            };
            let radians = null;
            if (globalDependencyValues.radiansAttr) {
              radians = globalDependencyValues.radiansAttr.stateValues.value.evaluate_to_constant();
              if (!Number.isFinite(radians)) {
                let points3 = {};
                points3["2,0"] = me$3.fromAst("＿");
                points3["2,1"] = me$3.fromAst("＿");
                return { setValue: { points: points3 } };
              }
            } else if (globalDependencyValues.degreesAttr) {
              let degrees = globalDependencyValues.degreesAttr.stateValues.value.evaluate_to_constant();
              if (Number.isFinite(degrees)) {
                radians = degrees / 180 * Math.PI;
              } else {
                points2["2,0"] = me$3.fromAst("＿");
                points2["2,1"] = me$3.fromAst("＿");
                return { setValue: { points: points2 } };
              }
            } else {
              radians = Math.PI / 2;
            }
            let desiredAngle = angleOfLine + radians;
            points2["2,0"] = me$3.fromAst(
              pointNearOrigin.x1 + Math.cos(desiredAngle)
            );
            points2["2,1"] = me$3.fromAst(
              pointNearOrigin.x2 + Math.sin(desiredAngle)
            );
            return { setValue: { points: points2 } };
          } else {
            let line1 = globalDependencyValues.lineChildren[0];
            let line2 = globalDependencyValues.lineChildren[1];
            let lineIntersection = calculateLineIntersection(
              line1,
              line2
            );
            if (lineIntersection === void 0) {
              let points2 = {};
              for (let i2 = 0; i2 < 3; i2++) {
                for (let j2 = 0; j2 < 2; j2++) {
                  points2[i2 + "," + j2] = me$3.fromAst("＿");
                }
              }
              return { setValue: { points: points2 } };
            }
            let point2 = lineIntersection;
            let a1 = line1.stateValues.points[0][0].evaluate_to_constant();
            let a2 = line1.stateValues.points[0][1].evaluate_to_constant();
            let b1 = line1.stateValues.points[1][0].evaluate_to_constant();
            let b2 = line1.stateValues.points[1][1].evaluate_to_constant();
            let point1 = [
              me$3.fromAst(point2[0].tree + b1 - a1),
              me$3.fromAst(point2[1].tree + b2 - a2)
            ];
            a1 = line2.stateValues.points[0][0].evaluate_to_constant();
            a2 = line2.stateValues.points[0][1].evaluate_to_constant();
            b1 = line2.stateValues.points[1][0].evaluate_to_constant();
            b2 = line2.stateValues.points[1][1].evaluate_to_constant();
            let point3 = [
              me$3.fromAst(point2[0].tree + b1 - a1),
              me$3.fromAst(point2[1].tree + b2 - a2)
            ];
            return {
              setValue: {
                points: {
                  "0,0": point1[0],
                  "0,1": point1[1],
                  "1,0": point2[0],
                  "1,1": point2[1],
                  "2,0": point3[0],
                  "2,1": point3[1]
                }
              }
            };
          }
        }
        let numPointsSpecified = globalDependencyValues.numPointsSpecified;
        let prescribedPoints;
        if (globalDependencyValues.throughAttr) {
          prescribedPoints = globalDependencyValues.throughAttr.stateValues.points;
        } else {
          prescribedPoints = [];
        }
        let points = {};
        let foundBadThroughPoint = false;
        for (let [ind, prescribedPoint] of prescribedPoints.entries()) {
          for (let i2 = 0; i2 < 2; i2++) {
            if (prescribedPoint[i2]) {
              points[`${ind},${i2}`] = prescribedPoint[i2];
            } else {
              points[`${ind},${i2}`] = me$3.fromAst("＿");
              foundBadThroughPoint = true;
            }
          }
        }
        const diagnostics = [];
        if (foundBadThroughPoint) {
          diagnostics.push({
            message: "Invalid point in through of `<angle>`",
            type: "warning"
          });
          if (globalDependencyValues.throughAttr.position) {
            diagnostics[diagnostics.length - 1].position = globalDependencyValues.throughAttr.position;
          }
        }
        if (numPointsSpecified === 0) {
          points["0,0"] = me$3.fromAst(1);
          points["0,1"] = me$3.fromAst(0);
        }
        if (numPointsSpecified < 2) {
          points["1,0"] = me$3.fromAst(0);
          points["1,1"] = me$3.fromAst(0);
        }
        if (numPointsSpecified < 3) {
          let radians = null;
          if (globalDependencyValues.radiansAttr) {
            radians = globalDependencyValues.radiansAttr.stateValues.value.evaluate_to_constant();
            if (!Number.isFinite(radians)) {
              points["2,0"] = me$3.fromAst("＿");
              points["2,1"] = me$3.fromAst("＿");
              return {
                setValue: { points },
                sendDiagnostics: diagnostics
              };
            }
          } else if (globalDependencyValues.degreesAttr) {
            let degrees = globalDependencyValues.degreesAttr.stateValues.value.evaluate_to_constant();
            if (Number.isFinite(degrees)) {
              radians = degrees / 180 * Math.PI;
            } else {
              points["2,0"] = me$3.fromAst("＿");
              points["2,1"] = me$3.fromAst("＿");
              return {
                setValue: { points },
                sendDiagnostics: diagnostics
              };
            }
          } else {
            radians = Math.PI / 2;
          }
          let a1 = points["0,0"].evaluate_to_constant();
          let a2 = points["0,1"].evaluate_to_constant();
          let b1 = points["1,0"].evaluate_to_constant();
          let b2 = points["1,1"].evaluate_to_constant();
          let angleFromTwoPoints = Math.atan2(a2 - b2, a1 - b1);
          let desiredAngle = angleFromTwoPoints + radians;
          points["2,0"] = me$3.fromAst(b1 + Math.cos(desiredAngle));
          points["2,1"] = me$3.fromAst(b2 + Math.sin(desiredAngle));
        }
        return {
          setValue: { points },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.radians = {
      description: "The angle measure in radians.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      additionalStateVariablesDefined: [
        {
          variableName: "swapPointOrder",
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        radians: {
          dependencyType: "attributeComponent",
          attributeName: "radians",
          variableNames: ["value"]
        },
        degrees: {
          dependencyType: "attributeComponent",
          attributeName: "degrees",
          variableNames: ["value"]
        },
        points: {
          dependencyType: "stateVariable",
          variableName: "points"
        },
        chooseReflexAngle: {
          dependencyType: "stateVariable",
          variableName: "chooseReflexAngle"
        }
      }),
      definition({ dependencyValues }) {
        let swapPointOrder = false;
        if (dependencyValues.radians !== null) {
          return {
            setValue: {
              radians: dependencyValues.radians.stateValues.value.simplify(),
              swapPointOrder
            }
          };
        } else if (dependencyValues.degrees !== null) {
          return {
            setValue: {
              radians: dependencyValues.degrees.stateValues.value.divide(me$3.fromAst(180)).simplify().multiply(me$3.fromAst("pi")).simplify(),
              swapPointOrder
            }
          };
        }
        let ps = [];
        let foundNaN = false;
        for (let i2 = 0; i2 < 3; i2++) {
          ps.push([
            dependencyValues.points[i2][0].evaluate_to_constant(),
            dependencyValues.points[i2][1].evaluate_to_constant()
          ]);
          if (Number.isNaN(ps[i2][0]) || Number.isNaN(ps[i2][1])) {
            foundNaN = true;
          }
        }
        let radians;
        if (foundNaN) {
          return {
            setValue: {
              radians: me$3.fromAst("＿"),
              swapPointOrder
            }
          };
        } else {
          radians = Math.atan2(ps[2][1] - ps[1][1], ps[2][0] - ps[1][0]) - Math.atan2(ps[0][1] - ps[1][1], ps[0][0] - ps[1][0]);
        }
        if (radians < 0) {
          radians += 2 * Math.PI;
        }
        if (radians > Math.PI) {
          if (dependencyValues.chooseReflexAngle === "never") {
            radians = 2 * Math.PI - radians;
            swapPointOrder = true;
          }
        } else if (dependencyValues.chooseReflexAngle === "always") {
          radians = 2 * Math.PI - radians;
          swapPointOrder = true;
        }
        return {
          setValue: { radians: me$3.fromAst(radians), swapPointOrder }
        };
      }
    };
    stateVariableDefinitions.value = {
      isAlias: true,
      targetVariableName: "radians",
      description: "The angle measure in radians."
    };
    stateVariableDefinitions.angle = {
      isAlias: true,
      targetVariableName: "radians",
      description: "The angle measure in radians."
    };
    stateVariableDefinitions.degrees = {
      description: "The angle measure in degrees.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        radians: {
          dependencyType: "stateVariable",
          variableName: "radians"
        }
      }),
      definition({ dependencyValues }) {
        let degrees;
        if (dependencyValues.radians.tree === "＿" || Number.isNaN(dependencyValues.radians.tree)) {
          degrees = dependencyValues.radians;
        } else {
          let radNum = dependencyValues.radians.evaluate_to_constant();
          if (Number.isFinite(radNum)) {
            degrees = me$3.fromAst(radNum * 180 / Math.PI);
          } else {
            degrees = dependencyValues.radians.multiply(me$3.fromAst(["/", 180, "pi"])).simplify();
          }
        }
        return { setValue: { degrees } };
      }
    };
    stateVariableDefinitions.latexForRenderer = {
      forRenderer: true,
      returnDependencies: () => ({
        inDegrees: {
          dependencyType: "stateVariable",
          variableName: "inDegrees"
        },
        radians: {
          dependencyType: "stateVariable",
          variableName: "radians"
        },
        degrees: {
          dependencyType: "stateVariable",
          variableName: "degrees"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let value = dependencyValues.inDegrees ? dependencyValues.degrees : dependencyValues.radians;
        let latexForRenderer = roundForDisplay({
          value,
          dependencyValues
        }).toLatex(params);
        if (dependencyValues.inDegrees) {
          latexForRenderer += "^\\circ";
        }
        return { setValue: { latexForRenderer } };
      }
    };
    stateVariableDefinitions.numericalPoints = {
      isArray: true,
      entryPrefixes: ["numericalPoint"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({}),
      returnArraySize() {
        return [3];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            point: {
              dependencyType: "stateVariable",
              variableName: "point" + (Number(arrayKey) + 1)
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalPoints = {};
        for (let arrayKey of arrayKeys) {
          let point = dependencyValuesByKey[arrayKey].point;
          let numericalP = [];
          for (let ind = 0; ind < 2; ind++) {
            let val = point[ind].evaluate_to_constant();
            numericalP.push(val);
          }
          numericalPoints[arrayKey] = numericalP;
        }
        return { setValue: { numericalPoints } };
      }
    };
    stateVariableDefinitions.numericalRadius = {
      forRenderer: true,
      returnDependencies: () => ({
        radius: {
          dependencyType: "stateVariable",
          variableName: "radius"
        }
      }),
      definition({ dependencyValues }) {
        let numericalRadius = dependencyValues.radius.evaluate_to_constant();
        return { setValue: { numericalRadius } };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = [
    {
      stateVariable: "radians",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
}
function calculateLineIntersection(line1, line2) {
  if (line1.stateValues.numDimensions !== 2 || line2.stateValues.numDimensions !== 2) {
    console.log(
      "Calculating angle between two lines implemented only in 2D"
    );
    return;
  }
  let a1 = line1.stateValues.coeffvar1.evaluate_to_constant();
  let b1 = line1.stateValues.coeffvar2.evaluate_to_constant();
  let c1 = line1.stateValues.coeff0.evaluate_to_constant();
  let a2 = line2.stateValues.coeffvar1.evaluate_to_constant();
  let b2 = line2.stateValues.coeffvar2.evaluate_to_constant();
  let c2 = line2.stateValues.coeff0.evaluate_to_constant();
  if (!(Number.isFinite(a1) && Number.isFinite(b1) && Number.isFinite(c1) && Number.isFinite(a2) && Number.isFinite(b2) && Number.isFinite(c2))) {
    console.log(
      "Calculating angle between two lines implemented only for constant coefficients"
    );
    return;
  }
  let d2 = a1 * b2 - a2 * b1;
  if (Math.abs(d2) < 1e-14) {
    if (Math.abs(c2 * a1 - c1 * a2) > 1e-14) {
      console.log("Cannot calculate angle between two parallel lines");
      return;
    } else if (a1 === 0 && b1 === 0 && c1 === 0 || a2 === 0 && b2 === 0 && c2 === 0) {
      return;
    } else {
      if (b1 !== 0) {
        return [me$3.fromAst(0), me$3.fromAst(-c1 / b1)];
      } else {
        return [me$3.fromAst(-c1 / a1), me$3.fromAst(0)];
      }
    }
  }
  let x2 = (c2 * b1 - c1 * b2) / d2;
  let y2 = (c1 * a2 - c2 * a1) / d2;
  return [me$3.fromAst(x2), me$3.fromAst(y2)];
}
class Answer extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      submitAnswer: this.submitAnswer.bind(this)
    });
  }
  static componentType = "answer";
  static componentDocs = {
    summary: "Assigns credit to student responses based on matches or logical rules",
    docsSlug: "answer1"
  };
  // Include children that can be added due to sugar
  static additionalSchemaChildren = [
    "math",
    "mathList",
    "number",
    "numberList",
    "text",
    "textList",
    "boolean",
    "booleanList",
    "choice"
  ];
  static renderChildren = true;
  static variableForImplicitProp = "submittedResponses";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static processWhenJustUpdatedForNewComponent = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    Object.assign(attributes, returnStandardAnswerAttributes());
    attributes.inline = {
      createComponentOfType: "boolean",
      createStateVariable: "inline",
      forRenderer: true,
      defaultValue: false,
      public: true,
      highlighted: true,
      description: "Whether to render the answer's input inline rather than as a block."
    };
    attributes.symbolicEquality = {
      createComponentOfType: "boolean",
      createStateVariable: "symbolicEquality",
      defaultValue: false,
      public: true,
      highlighted: true,
      description: "Whether comparison uses symbolic equality (rather than numeric evaluation)."
    };
    attributes.forceFullCheckWorkButton = {
      createComponentOfType: "boolean",
      createStateVariable: "forceFullCheckWorkButton",
      forRenderer: true,
      defaultValue: false,
      public: true,
      description: "Whether to always render a full-size check-work button."
    };
    attributes.forceSmallCheckWorkButton = {
      createComponentOfType: "boolean",
      createStateVariable: "forceSmallCheckWorkButton",
      forRenderer: true,
      defaultValue: false,
      public: true,
      description: "Whether to always render a small check-work button."
    };
    attributes.simplifyOnCompare = {
      createComponentOfType: "text",
      createStateVariable: "simplifyOnCompare",
      defaultValue: "none",
      toLowerCase: true,
      valueForTrue: "full",
      valueForFalse: "none",
      validValues: [
        {
          value: "none",
          description: "No simplification is applied before comparing."
        },
        {
          value: "full",
          description: "Fully simplify both expressions before comparing."
        },
        {
          value: "numbers",
          description: "Simplify numeric subexpressions only, leaving symbolic structure intact."
        },
        {
          value: "numbersPreserveOrder",
          description: "Like `numbers`, but does not reorder commutative operands."
        },
        {
          value: "normalizeOrder",
          description: "Reorder commutative operands into a canonical form without simplifying values."
        }
      ],
      public: true,
      description: "Level of simplification applied to math expressions before comparing them."
    };
    attributes.expandOnCompare = {
      createComponentOfType: "boolean",
      createStateVariable: "expandOnCompare",
      defaultValue: false,
      public: true,
      description: "Whether to expand math expressions before comparing them."
    };
    attributes.unorderedCompare = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedCompare",
      defaultValue: false,
      public: true,
      description: "Whether order is ignored when comparing list-like responses."
    };
    attributes.matchByExactPositions = {
      createComponentOfType: "boolean",
      createStateVariable: "matchByExactPositions",
      defaultValue: false,
      public: true,
      description: "Whether to match list responses by exact position rather than by content."
    };
    attributes.numAwardsCredited = {
      createComponentOfType: "integer",
      createStateVariable: "numAwardsCredited",
      defaultValue: 1,
      public: true,
      description: "Maximum number of matching awards whose credits are summed."
    };
    attributes.colorInputsSeparately = {
      createComponentOfType: "boolean",
      createStateVariable: "colorInputsSeparately",
      defaultValue: false,
      public: true,
      description: "Attempt to color each input separately based on the awards that reference it, rather than all inputs sharing the same overall credit color."
    };
    attributes.allowedErrorInNumbers = {
      createComponentOfType: "number",
      createStateVariable: "allowedErrorInNumbers",
      defaultValue: 0,
      public: true,
      description: "Maximum allowed numeric error when comparing numbers (relative or absolute)."
    };
    attributes.includeErrorInNumberExponents = {
      createComponentOfType: "boolean",
      createStateVariable: "includeErrorInNumberExponents",
      defaultValue: false,
      public: true,
      description: "Whether the allowed numeric error also applies to numbers in exponents."
    };
    attributes.allowedErrorIsAbsolute = {
      createComponentOfType: "boolean",
      createStateVariable: "allowedErrorIsAbsolute",
      defaultValue: false,
      public: true,
      description: "Whether allowedErrorInNumbers is interpreted as an absolute (rather than relative) tolerance."
    };
    attributes.numSignErrorsMatched = {
      createComponentOfType: "number",
      createStateVariable: "numSignErrorsMatched",
      defaultValue: 0,
      public: true,
      description: "Maximum number of sign errors that still count as a match."
    };
    attributes.numPeriodicSetMatchesRequired = {
      createComponentOfType: "integer",
      createStateVariable: "numPeriodicSetMatchesRequired",
      defaultValue: 3,
      public: true,
      description: "Number of consecutive elements of a periodic set required to count as a match."
    };
    attributes.caseInsensitiveMatch = {
      createComponentOfType: "boolean",
      createStateVariable: "caseInsensitiveMatch",
      defaultValue: false,
      public: true,
      description: "Whether text comparisons ignore letter case."
    };
    attributes.matchBlanks = {
      createComponentOfType: "boolean",
      createStateVariable: "matchBlanks",
      defaultValue: false,
      public: true,
      description: "Whether unfilled blanks in a math expression count as a match."
    };
    attributes.type = {
      createPrimitiveOfType: "string",
      createStateVariable: "type",
      defaultValue: null,
      toLowerCase: true,
      validValues: [
        {
          value: "math",
          description: "Expect a math expression answer."
        },
        {
          value: "text",
          description: "Expect a text answer."
        },
        {
          value: "boolean",
          description: "Expect a boolean answer."
        },
        {
          value: "videoWatched",
          description: "Mark correct when the associated video has been watched."
        }
      ],
      description: "Type of input the answer expects."
    };
    attributes.selectMultiple = {
      createComponentOfType: "boolean",
      createStateVariable: "selectMultiple",
      defaultValue: false,
      public: true,
      description: "For choice answers: whether multiple choices may be selected."
    };
    attributes.shuffleOrder = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "shuffleOrder",
      defaultValue: false,
      public: true,
      description: "For choice answers: whether to display choices in randomized order."
    };
    attributes.preserveLastChoice = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "preserveLastChoice",
      defaultValue: false,
      public: true,
      description: "For choice answers: whether the last-rendered choice keeps its position when shuffling."
    };
    attributes.disableWrongChoices = {
      createComponentOfType: "boolean",
      createStateVariable: "disableWrongChoices",
      defaultValue: false,
      public: true,
      description: "Whether incorrect choices are disabled after being submitted."
    };
    attributes.creditByAttempt = {
      createComponentOfType: "numberList",
      createStateVariable: "creditByAttempt",
      defaultValue: [],
      public: true,
      description: 'Per-attempt credit multipliers (e.g. "1 0.7 0.5" for full/70%/50% on attempts 1/2/3+).'
    };
    attributes.splitSymbols = {
      createComponentOfType: "boolean",
      createStateVariable: "splitSymbols",
      defaultValue: true,
      public: true,
      description: "Whether multi-character symbols should be split into a product of single-character variables when parsing."
    };
    attributes.parseScientificNotation = {
      createComponentOfType: "boolean",
      createStateVariable: "parseScientificNotation",
      defaultValue: false,
      public: true,
      description: "Whether to parse expressions like 1e3 as scientific notation."
    };
    attributes.expanded = {
      createComponentOfType: "boolean",
      createStateVariable: "expanded",
      defaultValue: false,
      public: true,
      description: "Whether the answer's submitted-response panel is expanded by default."
    };
    attributes.description = {
      createComponentOfType: "text",
      createStateVariable: "description",
      defaultValue: "",
      public: true,
      forRenderer: true,
      excludeFromSchema: true,
      description: "Currently unused"
    };
    attributes.showPreview = {
      createComponentOfType: "boolean",
      createStateVariable: "showPreview",
      defaultValue: false,
      public: true,
      description: "Whether to display a preview of the response as the student types."
    };
    attributes.video = {
      createReferences: true,
      description: "Reference to a video; when watched, this answer's videoWatched type marks credit."
    };
    attributes.videoCreditLabel = {
      createComponentOfType: "text",
      description: "Label shown next to the credit indicator for a video-watched answer."
    };
    Object.assign(attributes, returnLabelAttributes());
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let addAwardAndPossiblyInput = function({
      matchedChildren,
      componentAttributes,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    }) {
      function checkForResponseDescendant(components) {
        for (let component of components) {
          if (component?.attributes) {
            for (const attr in component.attributes) {
              if (attr.toLowerCase() === "isresponse") {
                const attribute = component.attributes[attr];
                if (attribute.type === "primitive") {
                  if (attribute.primitive.value) {
                    return true;
                  }
                } else if (attribute.type === "unresolved") {
                  const children = attribute.children;
                  if (children.length === 0 || children.length === 1 && typeof children[0] === "string" && children[0].toLowerCase() === "true") {
                    return true;
                  }
                }
              }
            }
          }
          if (component.children && checkForResponseDescendant(component.children)) {
            return true;
          }
        }
        return false;
      }
      function addPotentialResponsesToReferenceDescendants(components) {
        for (let component of components) {
          if (component.componentType === "_copy") {
            if (!component.attributes) {
              component.attributes = {};
            }
            component.attributes.isPotentialResponse = {
              type: "unresolved",
              name: "isPotentialResponse",
              children: ["true"]
            };
          } else if (component.children) {
            addPotentialResponsesToReferenceDescendants(
              component.children
            );
          }
        }
      }
      let componentIsSpecifiedType = componentInfoObjects.componentIsSpecifiedType;
      let foundMath = false, foundText = false, foundBoolean = false;
      let nChoicesFound = 0;
      let definitelyDoNotAddInput = false, mayNeedInput = false;
      let foundResponse = false;
      let foundAward = false;
      const descriptionChildren = matchedChildren.filter(
        (child) => componentIsSpecifiedType(child, "description") || componentIsSpecifiedType(child, "shortDescription")
      );
      const labelChildren = matchedChildren.filter(
        (child) => componentIsSpecifiedType(child, "label")
      );
      matchedChildren = matchedChildren.filter(
        (child) => !(componentIsSpecifiedType(child, "label") || componentIsSpecifiedType(child, "description") || componentIsSpecifiedType(child, "shortDescription"))
      );
      const firstNonBlankInd = matchedChildren.findIndex(
        (child) => typeof child !== "string" || child.trim() !== ""
      );
      const lastNonBlankInd = matchedChildren.findLastIndex(
        (child) => typeof child !== "string" || child.trim() !== ""
      );
      matchedChildren = matchedChildren.slice(
        firstNonBlankInd,
        lastNonBlankInd + 1
      );
      let childIsWrappable = [];
      for (let child of matchedChildren) {
        if (typeof child !== "object") {
          childIsWrappable.push(true);
          if (child.trim()) {
            mayNeedInput = true;
          }
        } else if (componentIsSpecifiedType(child, "math") || componentIsSpecifiedType(child, "number") || componentIsSpecifiedType(child, "mathList") || componentIsSpecifiedType(child, "numberList")) {
          childIsWrappable.push(true);
          mayNeedInput = true;
          foundMath = true;
        } else if (componentIsSpecifiedType(child, "text") || componentIsSpecifiedType(child, "textList")) {
          childIsWrappable.push(true);
          foundText = true;
          mayNeedInput = true;
        } else if (componentIsSpecifiedType(child, "boolean")) {
          childIsWrappable.push(true);
          foundBoolean = true;
          mayNeedInput = true;
        } else if (componentIsSpecifiedType(child, "booleanList")) {
          childIsWrappable.push(true);
          foundBoolean = true;
        } else if (componentIsSpecifiedType(child, "choice")) {
          childIsWrappable.push(true);
          nChoicesFound++;
        } else if (componentIsSpecifiedType(child, "award")) {
          foundAward = true;
          childIsWrappable.push(false);
          if (child.attributes?.referencesAreResponses) {
            foundResponse = true;
          }
          if (child.children?.length > 0) {
            for (let grandChild of child.children) {
              if (typeof grandChild !== "object") {
                if (grandChild.trim()) {
                  mayNeedInput = true;
                }
              } else if (componentIsSpecifiedType(grandChild, "when")) ;
              else if (componentIsSpecifiedType(grandChild, "math") || componentIsSpecifiedType(
                grandChild,
                "number"
              ) || componentIsSpecifiedType(
                grandChild,
                "mathList"
              ) || componentIsSpecifiedType(
                grandChild,
                "numberList"
              )) {
                foundMath = true;
                mayNeedInput = true;
              } else if (componentIsSpecifiedType(grandChild, "text") || componentIsSpecifiedType(grandChild, "textList")) {
                foundText = true;
                mayNeedInput = true;
              } else if (componentIsSpecifiedType(grandChild, "boolean")) {
                foundBoolean = true;
                mayNeedInput = true;
              } else if (componentIsSpecifiedType(
                grandChild,
                "booleanList"
              )) {
                foundBoolean = true;
              } else if (componentInfoObjects.isInheritedComponentType({
                inheritedComponentType: grandChild.componentType,
                baseComponentType: "_composite"
              }) && !grandChild.attributes?.createComponentOfType?.primitive) {
                mayNeedInput = true;
              } else if (componentIsSpecifiedType(
                grandChild,
                "orbitalDiagram"
              )) ;
              else {
                mayNeedInput = true;
              }
            }
          } else {
            mayNeedInput = true;
          }
        } else if (componentIsSpecifiedType(child, "mathInput")) {
          childIsWrappable.push(false);
          foundMath = true;
          definitelyDoNotAddInput = true;
        } else if (componentIsSpecifiedType(child, "textInput")) {
          childIsWrappable.push(false);
          foundText = true;
          definitelyDoNotAddInput = true;
        } else if (componentIsSpecifiedType(child, "_input")) {
          childIsWrappable.push(false);
          definitelyDoNotAddInput = true;
        } else if (componentIsSpecifiedType(child, "considerAsResponses")) {
          childIsWrappable.push(false);
          definitelyDoNotAddInput = true;
        } else if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: child.componentType,
          baseComponentType: "_composite"
        }) && !child.attributes?.createComponentOfType?.primitive) {
          childIsWrappable.push(true);
          mayNeedInput = true;
        } else {
          childIsWrappable.push(true);
          mayNeedInput = true;
        }
      }
      if (definitelyDoNotAddInput) {
        foundResponse = true;
      }
      if (nChoicesFound > 0) {
        matchedChildren = matchedChildren.filter(
          (x2) => typeof x2 !== "string" || x2.trim() !== ""
        );
        if (matchedChildren.length !== nChoicesFound) {
          return { success: false };
        } else {
          let choiceInput = {
            type: "serialized",
            componentType: "choiceInput",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: [...descriptionChildren, ...matchedChildren],
            attributes: {},
            doenetAttributes: {},
            state: {}
          };
          if (componentAttributes.shuffleOrder?.value) {
            choiceInput.attributes.shuffleOrder = {
              type: "primitive",
              name: "shuffleOrder",
              primitive: { type: "boolean", value: true }
            };
          }
          if (componentAttributes.preserveLastChoice?.value) {
            choiceInput.attributes.preserveLastChoice = {
              type: "primitive",
              name: "preserveLastChoice",
              primitive: { type: "boolean", value: true }
            };
          }
          return {
            success: true,
            newChildren: [...labelChildren, choiceInput],
            nComponents
          };
        }
      }
      if (componentAttributes.handGraded?.value && !foundAward) {
        mayNeedInput = true;
      }
      if (!mayNeedInput && !foundResponse) {
        for (let child of matchedChildren) {
          if (componentIsSpecifiedType(child, "award")) {
            if (child.children?.length > 0) {
              if (checkForResponseDescendant(child.children)) {
                foundResponse = true;
                break;
              }
            }
          }
        }
        if (!foundResponse) {
          for (let child of matchedChildren) {
            if (componentIsSpecifiedType(child, "award")) {
              if (child.children?.length > 0) {
                addPotentialResponsesToReferenceDescendants(
                  child.children
                );
              }
            }
          }
        }
      }
      let childrenToWrap = [], childrenToNotWrapBegin = labelChildren, childrenToNotWrapEnd = [];
      if (childIsWrappable.filter((x2) => !x2).length === 0) {
        childrenToWrap = matchedChildren;
      } else {
        if (!childIsWrappable[0]) {
          let firstWrappableInd = childIsWrappable.indexOf(true);
          if (firstWrappableInd !== -1) {
            childrenToNotWrapBegin.push(
              ...matchedChildren.slice(0, firstWrappableInd)
            );
            matchedChildren = matchedChildren.slice(firstWrappableInd);
            childIsWrappable = childIsWrappable.slice(firstWrappableInd);
          }
        }
        let firstNonWrappableInd = childIsWrappable.indexOf(false);
        if (firstNonWrappableInd === -1) {
          childrenToWrap = matchedChildren;
        } else {
          childrenToWrap = matchedChildren.slice(
            0,
            firstNonWrappableInd
          );
          childrenToNotWrapEnd = matchedChildren.slice(firstNonWrappableInd);
        }
      }
      while (typeof childrenToWrap[0] === "string" && childrenToWrap[0].trim() === "") {
        childrenToWrap = childrenToWrap.slice(1);
      }
      let nWrap = childrenToWrap.length;
      while (typeof childrenToWrap[nWrap - 1] === "string" && childrenToWrap[nWrap - 1].trim() === "") {
        childrenToWrap = childrenToWrap.slice(0, nWrap - 1);
        nWrap = childrenToWrap.length;
      }
      let newChildren;
      let type;
      let diagnostics = [];
      if (componentAttributes.type) {
        type = componentAttributes.type.value;
        if (!["math", "text", "boolean"].includes(type)) {
          if (type.toLowerCase() === "videowatched") {
            return { success: false };
          }
          diagnostics.push({
            message: `Invalid type for answer: ${type}`,
            type: "warning"
          });
          type = "math";
        }
      } else {
        if (foundMath) {
          type = "math";
        } else if (foundText) {
          type = "text";
        } else if (foundBoolean) {
          type = "boolean";
        } else {
          type = "math";
        }
      }
      if (childrenToWrap.length === 0) {
        newChildren = [
          ...childrenToNotWrapBegin,
          ...childrenToNotWrapEnd
        ];
      } else {
        let awardChildren = childrenToWrap;
        newChildren = [
          ...childrenToNotWrapBegin,
          {
            type: "serialized",
            componentType: "award",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: awardChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          },
          ...childrenToNotWrapEnd
        ];
      }
      if (mayNeedInput && !definitelyDoNotAddInput) {
        let inputType = type + "Input";
        newChildren = [
          {
            type: "serialized",
            componentType: inputType,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: descriptionChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          },
          ...newChildren
        ];
      } else {
        newChildren = [...descriptionChildren, ...newChildren];
      }
      return {
        success: true,
        newChildren,
        diagnostics,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: addAwardAndPossiblyInput
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "awards",
        componentTypes: ["award"]
      },
      {
        group: "inputs",
        componentTypes: ["_input"]
      },
      {
        group: "responses",
        componentTypes: ["considerAsResponses"]
      },
      {
        group: "labels",
        componentTypes: ["label"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "disabled",
      newName: "disabledOriginal"
    });
    Object.assign(
      stateVariableDefinitions,
      returnStandardAnswerStateVariableDefinition()
    );
    Object.assign(
      stateVariableDefinitions,
      returnSimplifyExpandOnCompareWarning()
    );
    stateVariableDefinitions.renderInlineForListItem = {
      forRenderer: true,
      additionalStateVariablesDefined: [
        { variableName: "listItemInlineAlignment", forRenderer: true },
        { variableName: "childrenToRenderInlineForListItem" }
      ],
      returnDependencies: () => ({
        parentChildrenToRenderInlineForListItem: {
          dependencyType: "parentStateVariable",
          variableName: "childrenToRenderInlineForListItem"
        },
        inputChildren: {
          dependencyType: "child",
          childGroups: ["inputs"],
          variableNames: ["inline"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        const isInParentList = Boolean(
          dependencyValues.parentChildrenToRenderInlineForListItem?.map((c2) => c2.componentIdx).includes(componentIdx)
        );
        if (!isInParentList) {
          return {
            setValue: {
              renderInlineForListItem: false,
              listItemInlineAlignment: "none",
              childrenToRenderInlineForListItem: []
            }
          };
        }
        const firstBlockChoiceInput = dependencyValues.inputChildren?.find(
          (child) => child.componentType === "choiceInput" && child.stateValues.inline === false
        );
        return {
          setValue: {
            renderInlineForListItem: false,
            listItemInlineAlignment: firstBlockChoiceInput ? "flex-start" : "none",
            childrenToRenderInlineForListItem: firstBlockChoiceInput ? [firstBlockChoiceInput] : []
          }
        };
      }
    };
    const labelDefinitions = returnLabelStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, labelDefinitions);
    stateVariableDefinitions.haveAwardThatRequiresInput = {
      returnDependencies: () => ({
        awardChildren: {
          dependencyType: "child",
          childGroups: ["awards"],
          variableNames: ["requireInputInAnswer"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveAwardThatRequiresInput: dependencyValues.awardChildren.some(
              (x2) => x2.stateValues.requireInputInAnswer
            )
          }
        };
      }
    };
    stateVariableDefinitions.allInputChildrenIncludingSugared = {
      returnDependencies: () => ({
        allInputChildrenIncludingSugared: {
          dependencyType: "child",
          childGroups: ["inputs"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            allInputChildrenIncludingSugared: dependencyValues.allInputChildrenIncludingSugared
          }
        };
      }
    };
    stateVariableDefinitions.inputChildren = {
      stateVariablesDeterminingDependencies: [
        "allInputChildrenIncludingSugared"
      ],
      additionalStateVariablesDefined: [
        "inputChildRelativeIndices",
        "skippedFirstInput"
      ],
      forRenderer: true,
      returnDependencies({ stateValues }) {
        let dependencies = {
          allInputChildrenIncludingSugared: {
            dependencyType: "stateVariable",
            variableName: "allInputChildrenIncludingSugared"
          },
          haveAwardThatRequiresInput: {
            dependencyType: "stateVariable",
            variableName: "haveAwardThatRequiresInput"
          },
          handGraded: {
            dependencyType: "stateVariable",
            variableName: "handGraded"
          }
        };
        if (stateValues.allInputChildrenIncludingSugared.length > 0) {
          dependencies.firstInputFromSugar = {
            dependencyType: "doenetAttribute",
            componentIdx: stateValues.allInputChildrenIncludingSugared[0].componentIdx,
            attributeName: "createdFromSugar"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let inputChildren = [
          ...dependencyValues.allInputChildrenIncludingSugared
        ];
        let inputChildRelativeIndices = [
          ...dependencyValues.allInputChildrenIncludingSugared.keys()
        ];
        let skippedFirstInput = false;
        let skipFirstSugaredInput = inputChildren[0]?.componentType !== "choiceInput" && (!(dependencyValues.haveAwardThatRequiresInput || dependencyValues.handGraded) || dependencyValues.allInputChildrenIncludingSugared.length > 1);
        if (skipFirstSugaredInput && dependencyValues.firstInputFromSugar) {
          skippedFirstInput = true;
          inputChildren = inputChildren.slice(1);
          inputChildRelativeIndices = inputChildRelativeIndices.slice(1);
        }
        return {
          setValue: {
            inputChildren,
            inputChildRelativeIndices,
            skippedFirstInput
          }
        };
      }
    };
    stateVariableDefinitions.haveBlockInputChild = {
      forRenderer: true,
      returnDependencies: () => ({
        allInputChildrenIncludingSugared: {
          dependencyType: "child",
          childGroups: ["inputs"],
          variableNames: ["inline"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveBlockInputChild: dependencyValues.allInputChildrenIncludingSugared.some(
              (child) => child.stateValues.inline === false
            )
          }
        };
      }
    };
    stateVariableDefinitions.descriptionChildInd = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            descriptionChildInd: dependencyValues.allChildren.findLastIndex(
              (child) => child.componentType === "description"
            )
          }
        };
      }
    };
    stateVariableDefinitions.inputChildIndices = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        },
        skippedFirstInput: {
          dependencyType: "stateVariable",
          variableName: "skippedFirstInput"
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        let inputChildIndices = dependencyValues.allChildren.map(
          (child, ind) => componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "_input"
          }) ? ind : -1
        ).filter((ind) => ind !== -1);
        if (dependencyValues.skippedFirstInput) {
          inputChildIndices = inputChildIndices.slice(1);
        }
        return { setValue: { inputChildIndices } };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        inputChildIndices: {
          dependencyType: "stateVariable",
          variableName: "inputChildIndices"
        },
        descriptionChildInd: {
          dependencyType: "stateVariable",
          variableName: "descriptionChildInd"
        }
      }),
      definition: function({ dependencyValues }) {
        const childIndicesToRender = [
          ...dependencyValues.inputChildIndices
        ];
        if (dependencyValues.descriptionChildInd !== -1) {
          childIndicesToRender.push(
            dependencyValues.descriptionChildInd
          );
        }
        return {
          setValue: {
            childIndicesToRender
          }
        };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.inputChildrenWithValues = {
      stateVariablesDeterminingDependencies: [
        "inputChildRelativeIndices"
      ],
      forRenderer: true,
      returnDependencies: ({ stateValues }) => ({
        inputChildren: {
          dependencyType: "child",
          childGroups: ["inputs"],
          variableNames: [
            "valueToRecordOnSubmit",
            "valueRecordedAtSubmit",
            "value"
          ],
          childIndices: stateValues.inputChildRelativeIndices,
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            inputChildrenWithValues: dependencyValues.inputChildren
          }
        };
      }
    };
    stateVariableDefinitions.inputChildWithValues = {
      returnDependencies: () => ({
        inputChildrenWithValues: {
          dependencyType: "stateVariable",
          variableName: "inputChildrenWithValues"
        }
      }),
      definition({ dependencyValues }) {
        let inputChildWithValues = null;
        if (dependencyValues.inputChildrenWithValues.length === 1) {
          inputChildWithValues = dependencyValues.inputChildrenWithValues[0];
        }
        return { setValue: { inputChildWithValues } };
      }
    };
    stateVariableDefinitions.allInputComponentIdxs = {
      stateVariablesDeterminingDependencies: [
        "inputChildRelativeIndices"
      ],
      returnDependencies: ({ stateValues }) => ({
        inputChildren: {
          dependencyType: "child",
          childGroups: ["inputs"],
          childIndices: stateValues.inputChildRelativeIndices
        },
        inputsForAnswer: {
          dependencyType: "stateVariable",
          variableName: "inputsForAnswer"
        }
      }),
      definition({ dependencyValues }) {
        const allInputComponentIdxs = /* @__PURE__ */ new Set([
          ...dependencyValues.inputChildren.map(
            (c2) => c2.componentIdx
          ),
          ...dependencyValues.inputsForAnswer.map(
            (c2) => c2.componentIdx
          )
        ]);
        return {
          setValue: { allInputComponentIdxs }
        };
      }
    };
    stateVariableDefinitions.inputComponentIdxForLabel = {
      stateVariablesDeterminingDependencies: ["inputChildrenWithValues"],
      additionalStateVariablesDefined: [
        {
          variableName: "hasAuthoredInputChildrenForLabel"
        }
      ],
      returnDependencies({ stateValues }) {
        const dependencies = {
          inputChildrenWithValues: {
            dependencyType: "stateVariable",
            variableName: "inputChildrenWithValues"
          }
        };
        for (const [ind, input] of (stateValues.inputChildrenWithValues ?? []).entries()) {
          dependencies[`inputChildCreatedFromSugar${ind}`] = {
            dependencyType: "doenetAttribute",
            componentIdx: input.componentIdx,
            attributeName: "createdFromSugar"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        const inputChildrenWithValues = dependencyValues.inputChildrenWithValues ?? [];
        let inputComponentIdxForLabel = null;
        let hasAuthoredInputChildrenForLabel = false;
        const inputChildrenCreatedFromSugar = inputChildrenWithValues.map(
          (_input, ind) => dependencyValues[`inputChildCreatedFromSugar${ind}`]
        );
        hasAuthoredInputChildrenForLabel = inputChildrenCreatedFromSugar.some(
          (createdFromSugar) => !createdFromSugar
        );
        if (inputChildrenWithValues.length === 1 && inputChildrenCreatedFromSugar[0]) {
          inputComponentIdxForLabel = inputChildrenWithValues[0].componentIdx;
        }
        return {
          setValue: {
            inputComponentIdxForLabel,
            hasAuthoredInputChildrenForLabel
          }
        };
      }
    };
    stateVariableDefinitions.awardInputResponseChildren = {
      returnDependencies: () => ({
        awardInputResponseChildren: {
          dependencyType: "child",
          childGroups: ["awards", "inputs", "responses"]
        },
        skippedFirstInput: {
          dependencyType: "stateVariable",
          variableName: "skippedFirstInput"
        }
      }),
      definition({ dependencyValues }) {
        let awardInputResponseChildren = [
          ...dependencyValues.awardInputResponseChildren
        ];
        if (dependencyValues.skippedFirstInput) {
          awardInputResponseChildren = awardInputResponseChildren.slice(1);
        }
        return {
          setValue: { awardInputResponseChildren }
        };
      }
    };
    stateVariableDefinitions.inputsForAnswer = {
      returnDependencies: () => ({
        inputsReferencing: {
          dependencyType: "componentsReferencingAttribute",
          attributeName: "forAnswer"
        },
        inputChildren: {
          dependencyType: "stateVariable",
          variableName: "inputChildrenWithValues"
        }
      }),
      definition({ dependencyValues }) {
        const inputsForAnswer = [];
        if (dependencyValues.inputsReferencing) {
          inputsForAnswer.push(
            ...dependencyValues.inputsReferencing.filter(
              (comp) => !dependencyValues.inputChildren.map((child) => child.componentIdx).includes(comp.componentIdx)
            )
          );
        }
        return { setValue: { inputsForAnswer } };
      }
    };
    stateVariableDefinitions.labelsForAnswer = {
      returnDependencies: () => ({
        labelsReferencing: {
          dependencyType: "componentsReferencingAttribute",
          attributeName: "for"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            labelsForAnswer: dependencyValues.labelsReferencing ?? []
          }
        };
      }
    };
    stateVariableDefinitions.focused = {
      description: "Whether any input within this answer currently has keyboard focus.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      stateVariablesDeterminingDependencies: [
        "inputsForAnswer",
        "inputChildrenWithValues"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          numInputs: {
            dependencyType: "value",
            value: stateValues.inputsForAnswer.length
          },
          numInputChildren: {
            dependencyType: "value",
            value: stateValues.inputChildrenWithValues.length
          }
        };
        for (const [
          i2,
          input
        ] of stateValues.inputsForAnswer.entries()) {
          dependencies["inputForAnswer" + i2] = {
            dependencyType: "stateVariable",
            componentIdx: input.componentIdx,
            variableName: "focused"
          };
        }
        for (const [
          i2,
          input
        ] of stateValues.inputChildrenWithValues.entries()) {
          dependencies["inputChild" + i2] = {
            dependencyType: "stateVariable",
            componentIdx: input.componentIdx,
            variableName: "focused"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let focused = false;
        for (let i2 = 0; i2 < dependencyValues.numInputs; i2++) {
          if (dependencyValues["inputForAnswer" + i2]) {
            focused = true;
            break;
          }
        }
        for (let i2 = 0; i2 < dependencyValues.numInputChildren; i2++) {
          if (dependencyValues["inputChild" + i2]) {
            focused = true;
            break;
          }
        }
        return { setValue: { focused } };
      }
    };
    stateVariableDefinitions.numResponses = {
      description: "The number of current (unsubmitted) response values from this answer's inputs.",
      additionalStateVariablesDefined: ["usePotentialResponses"],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      stateVariablesDeterminingDependencies: [
        "awardInputResponseChildren",
        "inputsForAnswer"
      ],
      returnDependencies({ stateValues, componentInfoObjects }) {
        let dependencies = {
          childTypes: {
            dependencyType: "value",
            value: stateValues.awardInputResponseChildren.map(
              (x2) => x2.componentType
            )
          },
          numInputsForAnswer: {
            dependencyType: "value",
            value: stateValues.inputsForAnswer.length
          }
        };
        for (let [
          ind,
          inputComp
        ] of stateValues.inputsForAnswer.entries()) {
          dependencies["inputForAnswerNValues" + ind] = {
            dependencyType: "stateVariable",
            componentIdx: inputComp.componentIdx,
            variableName: "numValues",
            variablesOptional: true
          };
        }
        for (let [
          ind,
          child
        ] of stateValues.awardInputResponseChildren.entries()) {
          if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "award"
          })) {
            dependencies["child" + ind] = {
              dependencyType: "descendant",
              ancestorIdx: child.componentIdx,
              componentTypes: ["_base"],
              variableNames: [
                "isResponse",
                "isPotentialResponse",
                "numValues"
              ],
              variablesOptional: true,
              recurseToMatchedChildren: true,
              includeNonActiveChildren: true,
              skipOverAdapters: true
            };
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "_input"
          })) {
            dependencies["childNValues" + ind] = {
              dependencyType: "stateVariable",
              componentIdx: child.componentIdx,
              variableName: "numValues",
              variablesOptional: true
            };
          } else {
            dependencies["child" + ind] = {
              dependencyType: "stateVariable",
              componentIdx: child.componentIdx,
              variableName: "childrenWithNValues"
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        let numResponses = 0;
        let numPotentialResponses = 0;
        for (let ind = 0; ind < dependencyValues.numInputsForAnswer; ind++) {
          let numValues = dependencyValues["inputForAnswerNValues" + ind];
          if (numValues === void 0) {
            numResponses += 1;
          } else {
            numResponses += numValues;
          }
        }
        for (let [
          ind,
          childType
        ] of dependencyValues.childTypes.entries()) {
          if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: childType,
            baseComponentType: "award"
          })) {
            for (let descendant of dependencyValues["child" + ind]) {
              if (!descendant.stateValues.isResponse && !descendant.stateValues.isPotentialResponse || componentInfoObjects.isInheritedComponentType({
                inheritedComponentType: descendant.componentType,
                baseComponentType: "_composite"
              })) {
                continue;
              }
              let numValues = descendant.stateValues.numValues ?? 1;
              if (descendant.stateValues.isResponse) {
                numResponses += numValues;
              } else {
                numPotentialResponses += numValues;
              }
            }
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: childType,
            baseComponentType: "_input"
          })) {
            let numValues = dependencyValues["childNValues" + ind];
            if (numValues === void 0) {
              numResponses += 1;
            } else {
              numResponses += numValues;
            }
          } else {
            for (let child of dependencyValues["child" + ind]) {
              if (child.stateValues.numValues === void 0) {
                numResponses += 1;
              } else {
                numResponses += child.stateValues.numValues;
              }
            }
          }
        }
        let usePotentialResponses = false;
        if (numResponses === 0 && numPotentialResponses > 0) {
          numResponses = numPotentialResponses;
          usePotentialResponses = true;
        }
        return {
          setValue: { numResponses, usePotentialResponses }
        };
      }
    };
    stateVariableDefinitions.currentResponses = {
      description: "The current (unsubmitted) response values from this answer's inputs.",
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true,
        addAttributeComponentsShadowingStateVariables: {
          displayDigits: {
            stateVariableToShadow: "displayDigitsForResponses"
          }
        }
      },
      isArray: true,
      entryPrefixes: ["currentResponse"],
      stateVariablesDeterminingDependencies: [
        "awardInputResponseChildren",
        "inputsForAnswer"
      ],
      returnArraySizeDependencies: () => ({
        numResponses: {
          dependencyType: "stateVariable",
          variableName: "numResponses"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numResponses];
      },
      returnArrayDependenciesByKey({
        stateValues,
        componentInfoObjects
      }) {
        let globalDependencies = {
          childTypes: {
            dependencyType: "value",
            value: stateValues.awardInputResponseChildren.map(
              (x2) => x2.componentType
            )
          },
          numInputsForAnswer: {
            dependencyType: "value",
            value: stateValues.inputsForAnswer.length
          },
          usePotentialResponses: {
            dependencyType: "stateVariable",
            variableName: "usePotentialResponses"
          }
        };
        for (let [
          ind,
          inputComp
        ] of stateValues.inputsForAnswer.entries()) {
          globalDependencies["inputForAnswer" + ind] = {
            dependencyType: "multipleStateVariables",
            componentIdx: inputComp.componentIdx,
            variableNames: ["value", "values", "componentType"],
            variablesOptional: true
          };
        }
        for (let [
          ind,
          child
        ] of stateValues.awardInputResponseChildren.entries()) {
          if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "award"
          })) {
            globalDependencies["child" + ind] = {
              dependencyType: "descendant",
              ancestorIdx: child.componentIdx,
              componentTypes: ["_base"],
              variableNames: [
                "isResponse",
                "isPotentialResponse",
                "value",
                "values",
                "formula",
                "componentType"
              ],
              variablesOptional: true,
              recurseToMatchedChildren: true,
              includeAttributeChildren: true,
              includeNonActiveChildren: true,
              skipOverAdapters: true
            };
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "_input"
          })) {
            globalDependencies["child" + ind] = {
              dependencyType: "multipleStateVariables",
              componentIdx: child.componentIdx,
              variableNames: ["value", "values", "componentType"],
              variablesOptional: true
            };
          } else {
            globalDependencies["child" + ind] = {
              dependencyType: "stateVariable",
              componentIdx: child.componentIdx,
              variableName: "childrenAsResponses"
            };
          }
        }
        return { globalDependencies };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        componentInfoObjects
      }) {
        let currentResponses = [];
        let componentType = [];
        let responseComponents = [];
        for (let ind = 0; ind < globalDependencyValues.numInputsForAnswer; ind++) {
          const input = globalDependencyValues["inputForAnswer" + ind];
          input.componentType = input.stateValues.componentType;
          responseComponents.push(input);
        }
        for (let [
          ind,
          childType
        ] of globalDependencyValues.childTypes.entries()) {
          if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: childType,
            baseComponentType: "award"
          })) {
            for (let descendant of globalDependencyValues["child" + ind]) {
              if (!descendant.stateValues.isResponse && (!globalDependencyValues.usePotentialResponses || !descendant.stateValues.isPotentialResponse) || componentInfoObjects.isInheritedComponentType({
                inheritedComponentType: descendant.componentType,
                baseComponentType: "_composite"
              })) {
                continue;
              }
              responseComponents.push(descendant);
            }
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: childType,
            baseComponentType: "_input"
          })) {
            const child = globalDependencyValues["child" + ind];
            child.componentType = child.stateValues.componentType;
            responseComponents.push(child);
          } else {
            responseComponents.push(
              ...globalDependencyValues["child" + ind]
            );
          }
        }
        for (let component of responseComponents) {
          let ct2 = component.stateValues.componentType;
          if (!ct2) {
            ct2 = component.componentType;
          }
          if (ct2 === "mathList") {
            ct2 = "math";
          } else if (ct2 === "numberList") {
            ct2 = "number";
          } else if (ct2 === "textList") {
            ct2 = "text";
          } else if (ct2 === "booleanList") {
            ct2 = "boolean";
          }
          if (Array.isArray(component.stateValues.values)) {
            currentResponses.push(...component.stateValues.values);
            componentType.push(
              ...Array(component.stateValues.values.length).fill(
                ct2
              )
            );
          } else if (component.stateValues.value !== void 0) {
            currentResponses.push(component.stateValues.value);
            componentType.push(ct2);
          } else if (component.stateValues.formula instanceof me$3.class) {
            currentResponses.push(component.stateValues.formula);
            componentType.push("math");
          } else {
            currentResponses.push("");
            componentType.push("text");
          }
        }
        return {
          setValue: { currentResponses },
          setCreateComponentOfType: {
            currentResponses: componentType
          }
        };
      }
    };
    stateVariableDefinitions.currentResponse = {
      isAlias: true,
      targetVariableName: "currentResponse1",
      description: "The current (unsubmitted) response value from the answer's first input."
    };
    stateVariableDefinitions.numSubmittedResponses = {
      description: "The number of response values from the most recent submission.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      hasEssential: true,
      defaultValue: 0,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          numSubmittedResponses: true
        }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "numSubmittedResponses",
              value: desiredStateVariableValues.numSubmittedResponses
            }
          ]
        };
      }
    };
    stateVariableDefinitions.submittedResponsesComponentType = {
      hasEssential: true,
      defaultValue: null,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          submittedResponsesComponentType: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        if (desiredStateVariableValues.submittedResponsesComponentType) {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "submittedResponsesComponentType",
                value: [
                  ...desiredStateVariableValues.submittedResponsesComponentType
                ]
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "submittedResponsesComponentType",
                value: []
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.submittedResponses = {
      description: "The response values from the most recent submission.",
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true,
        addAttributeComponentsShadowingStateVariables: {
          displayDigits: {
            stateVariableToShadow: "displayDigitsForResponses"
          }
        }
      },
      isArray: true,
      allowExtraArrayKeysInInverse: true,
      entryPrefixes: ["submittedResponse"],
      defaultValueByArrayKey: () => "＿",
      hasEssential: true,
      inverseShadowToSetEntireArray: true,
      doNotCombineInverseArrayInstructions: true,
      returnArraySizeDependencies: () => ({
        numSubmittedResponses: {
          dependencyType: "stateVariable",
          variableName: "numSubmittedResponses"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numSubmittedResponses];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          submittedResponsesComponentType: {
            dependencyType: "stateVariable",
            variableName: "submittedResponsesComponentType"
          },
          numSubmittedResponses: {
            dependencyType: "stateVariable",
            variableName: "numSubmittedResponses"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let componentType = [];
        if (globalDependencyValues.submittedResponsesComponentType) {
          componentType.push(
            ...globalDependencyValues.submittedResponsesComponentType.slice(
              0,
              globalDependencyValues.numSubmittedResponses
            )
          );
        }
        let essentialSubmittedResponses = {};
        for (let ind = 0; ind < globalDependencyValues.numSubmittedResponses; ind++) {
          essentialSubmittedResponses[ind] = true;
          if (!componentType[ind]) {
            componentType[ind] = "math";
          }
        }
        return {
          useEssentialOrDefaultValue: {
            submittedResponses: essentialSubmittedResponses
          },
          setCreateComponentOfType: {
            submittedResponses: componentType
          }
        };
      },
      inverseArrayDefinitionByKey: function({
        desiredStateVariableValues,
        initialChange
      }) {
        if (!initialChange) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "numSubmittedResponses",
              desiredValue: desiredStateVariableValues.submittedResponses.length
            },
            {
              setEssentialValue: "submittedResponses",
              value: [
                ...desiredStateVariableValues.submittedResponses
              ]
            }
          ]
        };
      }
    };
    stateVariableDefinitions.submittedResponse = {
      isAlias: true,
      targetVariableName: "submittedResponse1",
      description: "The first response value from the most recent submission."
    };
    stateVariableDefinitions.delegateCheckWork = {
      additionalStateVariablesDefined: [
        "delegateCheckWorkToInput",
        "delegateCheckWorkToAncestor"
      ],
      returnDependencies: () => ({
        inputChildren: {
          dependencyType: "stateVariable",
          variableName: "inputChildren"
        },
        ancestorSuppressingAnswerSubmitButtons: {
          dependencyType: "ancestor",
          variableNames: ["suppressAnswerSubmitButtons"]
        }
      }),
      definition: function({ dependencyValues }) {
        let delegateCheckWorkToAncestor = false;
        let delegateCheckWorkToInput = false;
        let delegateCheckWork = false;
        if (dependencyValues.ancestorSuppressingAnswerSubmitButtons?.stateValues.suppressAnswerSubmitButtons) {
          delegateCheckWorkToAncestor = delegateCheckWork = true;
        } else if (dependencyValues.inputChildren.length === 1) {
          delegateCheckWorkToInput = delegateCheckWork = true;
        }
        return {
          setValue: {
            delegateCheckWork,
            delegateCheckWorkToAncestor,
            delegateCheckWorkToInput
          }
        };
      }
    };
    stateVariableDefinitions.showCheckWork = {
      forRenderer: true,
      returnDependencies: () => ({
        delegateCheckWork: {
          dependencyType: "stateVariable",
          variableName: "delegateCheckWork"
        },
        suppressCheckWork: {
          dependencyType: "stateVariable",
          variableName: "suppressCheckWork"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            showCheckWork: !dependencyValues.delegateCheckWork && !dependencyValues.suppressCheckWork
          }
        };
      }
    };
    stateVariableDefinitions.creditIsReducedByAttempt = {
      forRenderer: true,
      returnDependencies: () => ({
        creditByAttempt: {
          dependencyType: "stateVariable",
          variableName: "creditByAttempt"
        }
      }),
      definition({ dependencyValues }) {
        let creditIsReducedByAttempt = false;
        if (dependencyValues.creditByAttempt.length > 0) {
          creditIsReducedByAttempt = dependencyValues.creditByAttempt.some(
            (reduction) => reduction < 1
          );
        }
        return { setValue: { creditIsReducedByAttempt } };
      }
    };
    stateVariableDefinitions.creditAchievedIfSubmit = {
      additionalStateVariablesDefined: [
        "awardsUsedIfSubmit",
        "awardChildren",
        "inputUsedIfSubmit"
      ],
      stateVariablesDeterminingDependencies: [
        "inputChildRelativeIndices"
      ],
      returnDependencies: ({ stateValues }) => ({
        awardChildren: {
          dependencyType: "child",
          childGroups: ["awards"],
          variableNames: [
            "credit",
            "creditAchievedIfSubmit",
            "fractionSatisfiedIfSubmit"
          ]
        },
        inputChildren: {
          dependencyType: "child",
          childGroups: ["inputs"],
          variableNames: ["creditAchievedIfSubmit", "value"],
          // include value so inputs always make dependency values change
          childIndices: stateValues.inputChildRelativeIndices,
          variablesOptional: true
        },
        numAwardsCredited: {
          dependencyType: "stateVariable",
          variableName: "numAwardsCredited"
        }
      }),
      definition: function({ dependencyValues }) {
        let creditAchieved = 0;
        let n2 = dependencyValues.numAwardsCredited;
        let awardsUsed = Array(n2).fill(null);
        let inputUsed = null;
        if (dependencyValues.awardChildren.length === 0) {
          if (dependencyValues.inputChildren.length === 1) {
            let inputCredit = dependencyValues.inputChildren[0].stateValues.creditAchievedIfSubmit;
            if (inputCredit >= 0) {
              creditAchieved = inputCredit;
              inputUsed = dependencyValues.inputChildren[0].componentIdx;
            }
          }
        } else {
          let awardCredits = Array(n2).fill(null);
          let minimumFromAwardCredits = 0;
          for (let child of dependencyValues.awardChildren) {
            let creditFromChild = child.stateValues.creditAchievedIfSubmit;
            if (creditFromChild > minimumFromAwardCredits || awardsUsed[n2 - 1] === null) {
              if (child.stateValues.fractionSatisfiedIfSubmit > 0) {
                if (awardsUsed[0] === null) {
                  awardsUsed[0] = child.componentIdx;
                  awardCredits[0] = creditFromChild;
                  minimumFromAwardCredits = Math.min(
                    ...awardCredits
                  );
                } else {
                  for (let [
                    ind,
                    credit
                  ] of awardCredits.entries()) {
                    if (creditFromChild > credit || credit === null) {
                      awardsUsed.splice(
                        ind,
                        0,
                        child.componentIdx
                      );
                      awardsUsed = awardsUsed.slice(0, n2);
                      awardCredits.splice(
                        ind,
                        0,
                        creditFromChild
                      );
                      awardCredits = awardCredits.slice(
                        0,
                        n2
                      );
                      minimumFromAwardCredits = Math.min(
                        ...awardCredits
                      );
                      break;
                    }
                  }
                }
              }
            }
          }
          creditAchieved = Math.min(
            1,
            awardCredits.reduce((a2, c2) => a2 + c2, 0)
          );
        }
        let lastNull = awardsUsed.indexOf(null);
        if (lastNull !== -1) {
          awardsUsed = awardsUsed.slice(0, lastNull);
        }
        return {
          setValue: {
            creditAchievedIfSubmit: creditAchieved,
            awardsUsedIfSubmit: awardsUsed,
            awardChildren: dependencyValues.awardChildren,
            inputUsedIfSubmit: inputUsed
          }
        };
      }
    };
    stateVariableDefinitions.creditAchievedPerInput = {
      returnDependencies: () => ({
        awardChildren: {
          dependencyType: "child",
          childGroups: ["awards"],
          variableNames: [
            "awarded",
            "creditAchieved",
            "credit",
            "referencedInputStateVars"
          ]
        }
      }),
      definition({ dependencyValues }) {
        const maxCreditByKey = {};
        const actualCreditByKey = {};
        for (const award of dependencyValues.awardChildren) {
          const referencedInputStateVars = award.stateValues.referencedInputStateVars;
          const credit = award.stateValues.credit;
          const awarded = award.stateValues.awarded;
          const creditAchieved = award.stateValues.creditAchieved;
          for (const {
            componentIdx,
            propVariable
          } of referencedInputStateVars) {
            const key = `${componentIdx}/${propVariable}`;
            if (maxCreditByKey[key] === void 0 || credit > maxCreditByKey[key]) {
              maxCreditByKey[key] = credit;
            }
            if (awarded && (actualCreditByKey[key] === void 0 || creditAchieved > actualCreditByKey[key])) {
              actualCreditByKey[key] = creditAchieved;
            }
          }
        }
        const creditAchievedPerInput = {};
        for (const key of Object.keys(maxCreditByKey)) {
          const maxCredit = maxCreditByKey[key];
          if (maxCredit > 0) {
            creditAchievedPerInput[key] = (actualCreditByKey[key] ?? 0) / maxCredit;
          }
        }
        return { setValue: { creditAchievedPerInput } };
      }
    };
    stateVariableDefinitions.numPreviousIncorrectSubmissions = {
      defaultValue: 0,
      hasEssential: true,
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          numPreviousIncorrectSubmissions: true
        }
      }),
      inverseDefinition: ({ desiredStateVariableValues }) => ({
        success: true,
        instructions: [
          {
            setEssentialValue: "numPreviousIncorrectSubmissions",
            value: desiredStateVariableValues.numPreviousIncorrectSubmissions
          }
        ]
      })
    };
    stateVariableDefinitions.nextCreditFactor = {
      forRenderer: true,
      returnDependencies: () => ({
        creditByAttempt: {
          dependencyType: "stateVariable",
          variableName: "creditByAttempt"
        },
        numIncorrectSubmissions: {
          dependencyType: "stateVariable",
          variableName: "numIncorrectSubmissions"
        }
      }),
      definition({ dependencyValues }) {
        let nextCreditFactor = 1;
        if (dependencyValues.creditByAttempt.length > 0) {
          const factorByAttempt = dependencyValues.creditByAttempt;
          const effectiveAttempt = Math.min(
            dependencyValues.numIncorrectSubmissions,
            factorByAttempt.length - 1
          );
          nextCreditFactor = Math.min(
            1,
            Math.max(0, factorByAttempt[effectiveAttempt])
          );
        }
        return { setValue: { nextCreditFactor } };
      }
    };
    stateVariableDefinitions.creditFactorUsed = {
      forRenderer: true,
      hasEssential: true,
      defaultValue: 1,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { creditFactorUsed: true }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "creditFactorUsed",
              value: desiredStateVariableValues.creditFactorUsed
            }
          ]
        };
      }
    };
    stateVariableDefinitions.allFeedbacks = {
      returnDependencies: () => ({
        awardChildren: {
          dependencyType: "child",
          childGroups: ["awards"],
          variableNames: ["feedbacks"]
        },
        feedbackComponents: {
          dependencyType: "descendant",
          componentTypes: ["_input"],
          variableNames: ["feedbacks"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let feedbacks = [];
        for (let award of dependencyValues.awardChildren) {
          feedbacks.push(...award.stateValues.feedbacks);
        }
        for (let feedbackComponent of dependencyValues.feedbackComponents) {
          if (Array.isArray(feedbackComponent.stateValues.feedbacks)) {
            feedbacks.push(
              ...feedbackComponent.stateValues.feedbacks
            );
          }
        }
        return {
          setValue: {
            allFeedbacks: feedbacks
          }
        };
      }
    };
    stateVariableDefinitions.numFeedbacks = {
      description: "The number of feedback messages produced by matched awards or applicable feedback definitions.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        allFeedbacks: {
          dependencyType: "stateVariable",
          variableName: "allFeedbacks"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numFeedbacks: dependencyValues.allFeedbacks.length
          },
          checkForActualChange: { numFeedbacks: true }
        };
      }
    };
    stateVariableDefinitions.feedbacks = {
      description: "The feedback messages produced by matched awards or applicable feedback definitions.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "feedback"
      },
      isArray: true,
      entryPrefixes: ["feedback"],
      returnArraySizeDependencies: () => ({
        numFeedbacks: {
          dependencyType: "stateVariable",
          variableName: "numFeedbacks"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numFeedbacks];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allFeedbacks: {
            dependencyType: "stateVariable",
            variableName: "allFeedbacks"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let feedbacks = {};
        for (let arrayKey = 0; arrayKey < globalDependencyValues.__array_size; arrayKey++) {
          feedbacks[arrayKey] = globalDependencyValues.allFeedbacks[arrayKey];
        }
        return { setValue: { feedbacks } };
      }
    };
    stateVariableDefinitions.triggerSimplifyExpandOnCompareWarning = {
      // set for renderer so that state variable is always evaluated and the warning will be issued
      forRenderer: true,
      returnDependencies: () => ({
        simplifyExpandOnCompareWarning: {
          dependencyType: "stateVariable",
          variableName: "simplifyExpandOnCompareWarning"
        },
        awardChildren: {
          dependencyType: "child",
          childGroups: ["awards"],
          variableNames: ["simplifyExpandOnCompareWarning"]
        }
      }),
      definition: () => ({
        setValue: { triggerSimplifyExpandOnCompareWarning: null }
      })
    };
    return stateVariableDefinitions;
  }
  async submitAnswer({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    const numAttemptsLeft = await this.stateValues.numAttemptsLeft;
    if (numAttemptsLeft < 1) {
      return;
    }
    const disabled = await this.stateValues.disabled;
    if (disabled) {
      return;
    }
    const unadjustedCreditAchieved = await this.stateValues.creditAchievedIfSubmit;
    const creditFactor = await this.stateValues.nextCreditFactor;
    const creditAchieved = await this.stateValues.handGraded ? 0 : unadjustedCreditAchieved * creditFactor;
    const awardsUsed = await this.stateValues.awardsUsedIfSubmit;
    const inputUsed = await this.stateValues.inputUsedIfSubmit;
    const instructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "creditAchieved",
        value: creditAchieved
      },
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "responseHasBeenSubmitted",
        value: true
      }
    ];
    const inputChildrenWithValues = await this.stateValues.inputChildrenWithValues;
    if (inputChildrenWithValues.length === 1) {
      const inputChild = inputChildrenWithValues[0];
      if (inputUsed === inputChild.componentIdx && "valueToRecordOnSubmit" in inputChild.stateValues && "valueRecordedAtSubmit" in inputChild.stateValues) {
        instructions.push({
          updateType: "updateValue",
          componentIdx: inputChild.componentIdx,
          stateVariable: "valueRecordedAtSubmit",
          value: inputChild.stateValues.valueToRecordOnSubmit
        });
      }
    }
    const currentResponses = await this.stateValues.currentResponses;
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "submittedResponses",
      value: currentResponses
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "submittedResponsesComponentType",
      value: this.state.currentResponses.shadowingInstructions.createComponentOfType
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "justSubmitted",
      value: true
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "creditAchievedDependenciesAtSubmit",
      value: await this.stateValues.creditAchievedDependencies
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "numSubmissions",
      value: await this.stateValues.numSubmissions + 1
    });
    if (unadjustedCreditAchieved < 1) {
      instructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "numIncorrectSubmissions",
        value: await this.stateValues.numIncorrectSubmissions + 1
      });
    }
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "numPreviousIncorrectSubmissions",
      value: await this.stateValues.numIncorrectSubmissions
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "creditFactorUsed",
      value: creditFactor
    });
    for (const child of await this.stateValues.awardChildren) {
      const awarded = awardsUsed.includes(child.componentIdx);
      instructions.push({
        updateType: "updateValue",
        componentIdx: child.componentIdx,
        stateVariable: "awarded",
        value: awarded
      });
      instructions.push({
        updateType: "updateValue",
        componentIdx: child.componentIdx,
        stateVariable: "creditAchieved",
        value: child.stateValues.creditAchievedIfSubmit
      });
      instructions.push({
        updateType: "updateValue",
        componentIdx: child.componentIdx,
        stateVariable: "fractionSatisfied",
        value: child.stateValues.fractionSatisfiedIfSubmit
      });
    }
    const responseText = [];
    for (const response of currentResponses) {
      if (response.toString) {
        try {
          responseText.push(response.toString());
        } catch (e3) {
          responseText.push("＿");
        }
      } else {
        responseText.push(response);
      }
    }
    instructions.push({
      updateType: "recordItemSubmission",
      componentNumber: await this.stateValues.inComponentNumber,
      submittedComponent: this.componentIdx,
      response: currentResponses,
      responseText,
      creditAchieved
    });
    await this.coreFunctions.performUpdate({
      updateInstructions: instructions,
      actionId,
      sourceInformation,
      skipRendererUpdate: true,
      event: {
        verb: "submitted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType,
          answerNumber: this.answerNumber,
          rootName: this.rootName
        },
        result: {
          response: currentResponses,
          responseText,
          componentTypes: this.state.currentResponses.shadowingInstructions.createComponentOfType,
          creditAchieved
        }
      }
    });
    return await this.coreFunctions.triggerChainedActions({
      componentIdx: this.componentIdx,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
function comparePathsIgnorePosition(path1, path2) {
  if (path1 === null) {
    if (path2 == null) {
      return true;
    } else {
      return false;
    }
  } else if (path2 === null) {
    return false;
  }
  if (path1.length !== path2.length) {
    return false;
  }
  return path1.every((p1, i2) => {
    const p2 = path2[i2];
    if (p1.name !== p2.name || p1.index.length !== p2.index.length) {
      return false;
    }
    return p1.index.every((index1, j2) => {
      const index2 = p2.index[j2];
      if (index1.value.length !== index2.value.length) {
        return false;
      }
      return index1.value.every((v2, k2) => v2 == index2.value[k2]);
    });
  });
}
class Award extends BaseComponent {
  static componentType = "award";
  static componentDocs = {
    summary: "Specifies conditions for awarding credit in an answer"
  };
  static rendererType = void 0;
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static variableForImplicitProp = "awarded";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.credit = {
      createComponentOfType: "number",
      createStateVariable: "credit",
      defaultValue: 1,
      public: true,
      attributesForCreatedComponent: { convertBoolean: "true" },
      description: "Fraction of credit (0 to 1) granted when this award matches."
    };
    attributes.matchPartial = {
      createComponentOfType: "boolean",
      createStateVariable: "matchPartial",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "matchPartial",
      description: "Whether to award partial credit when the response is partially correct."
    };
    attributes.symbolicEquality = {
      createComponentOfType: "boolean",
      createStateVariable: "symbolicEquality",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "symbolicEquality",
      description: "Whether comparison uses symbolic equality (rather than numeric evaluation)."
    };
    attributes.expandOnCompare = {
      createComponentOfType: "boolean",
      createStateVariable: "expandOnCompare",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "expandOnCompare",
      description: "Whether to expand math expressions before comparing them."
    };
    attributes.simplifyOnCompare = {
      createComponentOfType: "text",
      createStateVariable: "simplifyOnCompare",
      defaultValue: "none",
      toLowerCase: true,
      valueForTrue: "full",
      valueForFalse: "none",
      validValues: [
        {
          value: "none",
          description: "No simplification is applied before comparing."
        },
        {
          value: "full",
          description: "Fully simplify both expressions before comparing."
        },
        {
          value: "numbers",
          description: "Simplify numeric subexpressions only, leaving symbolic structure intact."
        },
        {
          value: "numbersPreserveOrder",
          description: "Like `numbers`, but does not reorder commutative operands."
        },
        {
          value: "normalizeOrder",
          description: "Reorder commutative operands into a canonical form without simplifying values."
        }
      ],
      public: true,
      fallBackToParentStateVariable: "simplifyOnCompare",
      description: "Level of simplification applied to math expressions before comparing them."
    };
    attributes.unorderedCompare = {
      createComponentOfType: "boolean",
      createStateVariable: "unorderedCompare",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "unorderedCompare",
      description: "Whether order is ignored when comparing list-like responses."
    };
    attributes.matchByExactPositions = {
      createComponentOfType: "boolean",
      createStateVariable: "matchByExactPositions",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "matchByExactPositions",
      description: "Whether to match list responses by exact position rather than by content."
    };
    attributes.allowedErrorInNumbers = {
      createComponentOfType: "number",
      createStateVariable: "allowedErrorInNumbers",
      defaultValue: 0,
      public: true,
      fallBackToParentStateVariable: "allowedErrorInNumbers",
      description: "Maximum allowed numeric error when comparing numbers (relative or absolute)."
    };
    attributes.includeErrorInNumberExponents = {
      createComponentOfType: "boolean",
      createStateVariable: "includeErrorInNumberExponents",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "includeErrorInNumberExponents",
      description: "Whether the allowed numeric error also applies to numbers in exponents."
    };
    attributes.allowedErrorIsAbsolute = {
      createComponentOfType: "boolean",
      createStateVariable: "allowedErrorIsAbsolute",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "allowedErrorIsAbsolute",
      description: "Whether allowedErrorInNumbers is interpreted as an absolute (rather than relative) tolerance."
    };
    attributes.numSignErrorsMatched = {
      createComponentOfType: "number",
      createStateVariable: "numSignErrorsMatched",
      defaultValue: 0,
      public: true,
      fallBackToParentStateVariable: "numSignErrorsMatched",
      description: "Maximum number of sign errors that still count as a match."
    };
    attributes.numPeriodicSetMatchesRequired = {
      createComponentOfType: "integer",
      createStateVariable: "numPeriodicSetMatchesRequired",
      defaultValue: 3,
      public: true,
      fallBackToParentStateVariable: "numPeriodicSetMatchesRequired",
      description: "Number of consecutive elements of a periodic set required to count as a match."
    };
    attributes.caseInsensitiveMatch = {
      createComponentOfType: "boolean",
      createStateVariable: "caseInsensitiveMatch",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "caseInsensitiveMatch",
      description: "Whether text comparisons ignore letter case."
    };
    attributes.matchBlanks = {
      createComponentOfType: "boolean",
      createStateVariable: "matchBlanks",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "matchBlanks",
      description: "Whether unfilled blanks in a math expression count as a match."
    };
    attributes.feedbackCodes = {
      createComponentOfType: "textList",
      createStateVariable: "feedbackCodes",
      defaultValue: [],
      public: true,
      description: "Codes that select reusable feedback messages when this award matches."
    };
    attributes.feedbackText = {
      createComponentOfType: "text",
      createStateVariable: "feedbackText",
      defaultValue: null,
      public: true,
      description: "Custom feedback text shown when this award matches."
    };
    attributes.referencesAreResponses = {
      createReferences: true,
      description: "References to components whose values should be treated as responses for this award."
    };
    attributes.splitSymbols = {
      createComponentOfType: "boolean",
      createStateVariable: "splitSymbols",
      defaultValue: true,
      public: true,
      fallBackToParentStateVariable: "splitSymbols",
      description: "Whether multi-character symbols should be split into a product of single-character variables when parsing."
    };
    attributes.parseScientificNotation = {
      createComponentOfType: "boolean",
      createStateVariable: "parseScientificNotation",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "parseScientificNotation",
      description: "Whether to parse expressions like 1e3 as scientific notation."
    };
    return attributes;
  }
  static preprocessSerializedChildren({ serializedChildren, attributes }) {
    if (attributes.referencesAreResponses?.type === "references") {
      const references = attributes.referencesAreResponses.references.filter((child) => child.componentType === "_copy").map((child) => child.extending.Ref);
      for (let reference of references) {
        addResponsesToDescendantsWithReference(
          serializedChildren,
          reference
        );
      }
    }
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let wrapWithComponentTypeIfNeeded = function({
      matchedChildren,
      parentAttributes,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    }) {
      while (typeof matchedChildren[0] === "string" && matchedChildren[0].trim() === "") {
        matchedChildren = matchedChildren.slice(1);
      }
      let numChildren = matchedChildren.length;
      while (typeof matchedChildren[numChildren - 1] === "string" && matchedChildren[numChildren - 1].trim() === "") {
        matchedChildren = matchedChildren.slice(0, numChildren - 1);
        numChildren = matchedChildren.length;
      }
      if (matchedChildren.length === 1 && typeof matchedChildren[0] === "object") {
        return { success: false };
      }
      let componentIsSpecifiedType = componentInfoObjects.componentIsSpecifiedType;
      let foundMath = false, foundText = false, foundBoolean = false;
      for (let child of matchedChildren) {
        if (typeof child !== "object") {
          continue;
        } else if (componentIsSpecifiedType(child, "math") || componentIsSpecifiedType(child, "number") || componentIsSpecifiedType(child, "mathList") || componentIsSpecifiedType(child, "numberList")) {
          foundMath = true;
        } else if (componentIsSpecifiedType(child, "text") || componentIsSpecifiedType(child, "textList")) {
          foundText = true;
        } else if (componentIsSpecifiedType(child, "boolean") || componentIsSpecifiedType(child, "booleanList")) {
          foundBoolean = true;
        }
      }
      let type;
      if (parentAttributes.type) {
        type = parentAttributes.type.value;
        if (!["math", "text", "boolean"].includes(type)) {
          type = "math";
        }
      } else {
        if (foundMath) {
          type = "math";
        } else if (foundText) {
          type = "text";
        } else if (foundBoolean) {
          type = "boolean";
        } else {
          type = "math";
        }
      }
      return {
        success: true,
        newChildren: [
          {
            type: "serialized",
            componentType: type,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: matchedChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ],
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: wrapWithComponentTypeIfNeeded
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "whens",
        componentTypes: ["when"]
      },
      {
        group: "strings",
        componentTypes: ["string"]
      },
      {
        group: "comparableTypes",
        componentTypes: [
          "math",
          "number",
          "text",
          "boolean",
          "orbitalDiagram"
        ]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnSimplifyExpandOnCompareWarning()
    );
    stateVariableDefinitions.parsedExpression = {
      additionalStateVariablesDefined: [
        "requireInputInAnswer",
        "codePre"
      ],
      returnDependencies: () => ({
        whenChild: {
          dependencyType: "child",
          childGroups: ["whens"]
        },
        // call it "allChildren" so that can use the buildParsedExpression function that Boolean uses
        allChildren: {
          dependencyType: "child",
          childGroups: ["strings", "comparableTypes"]
        },
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"],
          variableNames: ["value"]
        },
        answerType: {
          dependencyType: "parentStateVariable",
          parentComponentType: "answer",
          variableName: "type"
        },
        splitSymbols: {
          dependencyType: "stateVariable",
          variableName: "splitSymbols"
        }
      }),
      definition: function({ dependencyValues, componentInfoObjects }) {
        let parsedExpression = null;
        let requireInputInAnswer = false;
        let codePre = "";
        if (dependencyValues.whenChild.length == 0 && dependencyValues.allChildren.length > 0) {
          requireInputInAnswer = true;
          let doNotSplit = dependencyValues.splitSymbols === false || dependencyValues.answerType && !["number", "math"].includes(
            dependencyValues.answerType
          );
          let { setValue } = buildParsedExpression({
            dependencyValues,
            componentInfoObjects,
            doNotSplit,
            splitAtInitialLevel: true
          });
          codePre = setValue.codePre;
          parsedExpression = me$3.fromAst([
            "=",
            codePre + "Input",
            setValue.parsedExpression.tree
          ]);
        }
        return {
          setValue: {
            parsedExpression,
            requireInputInAnswer,
            codePre
          }
        };
      }
    };
    Object.assign(
      stateVariableDefinitions,
      returnChildrenByCodeStateVariableDefinitions()
    );
    stateVariableDefinitions.creditAchievedIfSubmit = {
      description: "Credit (0 to 1) this award would grant if submitted with the current response.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      additionalStateVariablesDefined: [
        {
          variableName: "fractionSatisfiedIfSubmit",
          description: "Fraction (0 to 1) by which this award's match condition is currently satisfied.",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number"
          }
        }
      ],
      returnDependencies: () => ({
        credit: {
          dependencyType: "stateVariable",
          variableName: "credit"
        },
        whenChild: {
          dependencyType: "child",
          childGroups: ["whens"],
          variableNames: ["fractionSatisfied"]
        },
        answerInput: {
          dependencyType: "parentStateVariable",
          variableName: "inputChildWithValues"
        },
        symbolicEquality: {
          dependencyType: "stateVariable",
          variableName: "symbolicEquality"
        },
        expandOnCompare: {
          dependencyType: "stateVariable",
          variableName: "expandOnCompare"
        },
        simplifyOnCompare: {
          dependencyType: "stateVariable",
          variableName: "simplifyOnCompare"
        },
        unorderedCompare: {
          dependencyType: "stateVariable",
          variableName: "unorderedCompare"
        },
        matchByExactPositions: {
          dependencyType: "stateVariable",
          variableName: "matchByExactPositions"
        },
        allowedErrorInNumbers: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorInNumbers"
        },
        includeErrorInNumberExponents: {
          dependencyType: "stateVariable",
          variableName: "includeErrorInNumberExponents"
        },
        allowedErrorIsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorIsAbsolute"
        },
        numSignErrorsMatched: {
          dependencyType: "stateVariable",
          variableName: "numSignErrorsMatched"
        },
        numPeriodicSetMatchesRequired: {
          dependencyType: "stateVariable",
          variableName: "numPeriodicSetMatchesRequired"
        },
        caseInsensitiveMatch: {
          dependencyType: "stateVariable",
          variableName: "caseInsensitiveMatch"
        },
        matchBlanks: {
          dependencyType: "stateVariable",
          variableName: "matchBlanks"
        },
        parsedExpression: {
          dependencyType: "stateVariable",
          variableName: "parsedExpression"
        },
        allChildren: {
          dependencyType: "child",
          childGroups: ["strings", "comparableTypes"],
          variableNames: ["value"],
          variablesOptional: true
        },
        booleanChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "booleanChildrenByCode"
        },
        textChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "textChildrenByCode"
        },
        mathChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "mathChildrenByCode"
        },
        numberChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "numberChildrenByCode"
        },
        otherChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "otherChildrenByCode"
        },
        matchPartial: {
          dependencyType: "stateVariable",
          variableName: "matchPartial"
        },
        codePre: {
          dependencyType: "stateVariable",
          variableName: "codePre"
        }
      }),
      definition: function({ dependencyValues, usedDefault }) {
        let fractionSatisfiedIfSubmit;
        if (dependencyValues.whenChild.length > 0) {
          fractionSatisfiedIfSubmit = dependencyValues.whenChild[0].stateValues.fractionSatisfied;
        } else {
          if (!dependencyValues.answerInput || !dependencyValues.parsedExpression) {
            return {
              setValue: {
                creditAchievedIfSubmit: 0,
                fractionSatisfiedIfSubmit: 0
              }
            };
          }
          fractionSatisfiedIfSubmit = evaluateLogicDirectlyFromChildren({
            dependencyValues,
            usedDefault
          });
        }
        fractionSatisfiedIfSubmit = Math.max(
          0,
          Math.min(1, fractionSatisfiedIfSubmit)
        );
        let creditAchievedIfSubmit = 0;
        if (Number.isFinite(dependencyValues.credit)) {
          creditAchievedIfSubmit = Math.max(0, Math.min(1, dependencyValues.credit)) * fractionSatisfiedIfSubmit;
        }
        return {
          setValue: {
            fractionSatisfiedIfSubmit,
            creditAchievedIfSubmit
          }
        };
      }
    };
    stateVariableDefinitions.fractionSatisfied = {
      description: "Fraction (0 to 1) by which this award's match condition was satisfied at last submission.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      defaultValue: 0,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          fractionSatisfied: true
        }
      }),
      inverseDefinition: function({
        desiredStateVariableValues,
        initialChange
      }) {
        if (!initialChange) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "fractionSatisfied",
              value: desiredStateVariableValues.fractionSatisfied
            }
          ]
        };
      }
    };
    stateVariableDefinitions.creditAchieved = {
      description: "Credit (0 to 1) this award granted at the last submission.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      defaultValue: 0,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          creditAchieved: true
        }
      }),
      inverseDefinition: function({
        desiredStateVariableValues,
        initialChange
      }) {
        if (!initialChange) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "creditAchieved",
              value: desiredStateVariableValues.creditAchieved
            }
          ]
        };
      }
    };
    stateVariableDefinitions.awarded = {
      description: "Whether this award was matched at the last submission.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      defaultValue: false,
      hasEssential: true,
      doNotShadowEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          awarded: true
        }
      }),
      inverseDefinition: function({
        desiredStateVariableValues,
        initialChange
      }) {
        if (!initialChange) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "awarded",
              value: desiredStateVariableValues.awarded
            }
          ]
        };
      }
    };
    stateVariableDefinitions.allFeedbacks = {
      returnDependencies: () => ({
        feedbackText: {
          dependencyType: "stateVariable",
          variableName: "feedbackText"
        },
        feedbackCodes: {
          dependencyType: "stateVariable",
          variableName: "feedbackCodes"
        },
        feedbackDefinitionAncestor: {
          dependencyType: "ancestor",
          variableNames: ["feedbackDefinitions"]
        },
        awarded: {
          dependencyType: "stateVariable",
          variableName: "awarded"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!dependencyValues.awarded) {
          return { setValue: { allFeedbacks: [] } };
        }
        let allFeedbacks = [];
        let feedbackDefinitions = dependencyValues.feedbackDefinitionAncestor.stateValues.feedbackDefinitions;
        for (let feedbackCode of dependencyValues.feedbackCodes) {
          let code = feedbackCode.toLowerCase();
          let feedbackText = feedbackDefinitions[code];
          if (feedbackText) {
            allFeedbacks.push(feedbackText);
          }
        }
        if (dependencyValues.feedbackText !== null) {
          allFeedbacks.push(dependencyValues.feedbackText);
        }
        return { setValue: { allFeedbacks } };
      }
    };
    stateVariableDefinitions.numFeedbacks = {
      description: "The number of feedback messages associated with this award.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        allFeedbacks: {
          dependencyType: "stateVariable",
          variableName: "allFeedbacks"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numFeedbacks: dependencyValues.allFeedbacks.length
          },
          checkForActualChange: { numFeedbacks: true }
        };
      }
    };
    stateVariableDefinitions.feedbacks = {
      description: "The feedback messages produced by this award.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "feedback"
      },
      isArray: true,
      entryPrefixes: ["feedback"],
      returnArraySizeDependencies: () => ({
        numFeedbacks: {
          dependencyType: "stateVariable",
          variableName: "numFeedbacks"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numFeedbacks];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allFeedbacks: {
            dependencyType: "stateVariable",
            variableName: "allFeedbacks"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let feedbacks = {};
        for (let arrayKey = 0; arrayKey < globalDependencyValues.__array_size; arrayKey++) {
          feedbacks[arrayKey] = globalDependencyValues.allFeedbacks[arrayKey];
        }
        return { setValue: { feedbacks } };
      }
    };
    stateVariableDefinitions.feedback = {
      isAlias: true,
      targetVariableName: "feedback1",
      description: "The first feedback message produced by this award."
    };
    stateVariableDefinitions.colorInputsSeparatelyFromAnswer = {
      returnDependencies: () => ({
        colorInputsSeparately: {
          dependencyType: "parentStateVariable",
          variableName: "colorInputsSeparately"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            colorInputsSeparatelyFromAnswer: dependencyValues.colorInputsSeparately ?? false
          }
        };
      }
    };
    stateVariableDefinitions.referencedInputStateVars = {
      stateVariablesDeterminingDependencies: [
        "colorInputsSeparatelyFromAnswer"
      ],
      returnDependencies({ stateValues }) {
        const deps = {
          colorInputsSeparatelyFromAnswer: {
            dependencyType: "parentStateVariable",
            variableName: "colorInputsSeparately"
          }
        };
        if (stateValues.colorInputsSeparatelyFromAnswer) {
          deps.allInputComponentIdxs = {
            dependencyType: "parentStateVariable",
            variableName: "allInputComponentIdxs"
          };
          deps.whenChild = {
            dependencyType: "child",
            childGroups: ["whens"],
            variableNames: ["referencedStateVars"]
          };
        }
        return deps;
      },
      definition({ dependencyValues }) {
        if (!dependencyValues.colorInputsSeparatelyFromAnswer) {
          return { setValue: { referencedInputStateVars: [] } };
        }
        const allInputComponentIdxs = dependencyValues.allInputComponentIdxs ?? /* @__PURE__ */ new Set();
        const referencedStateVars = dependencyValues.whenChild?.[0]?.stateValues.referencedStateVars ?? [];
        return {
          setValue: {
            referencedInputStateVars: referencedStateVars.filter(
              (ref) => allInputComponentIdxs.has(ref.componentIdx)
            )
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static standardizedFeedback = {
    numericalerror: `Credit reduced because numbers in your answer weren't quite right.  Did you round too much?`,
    goodjob: `Good job!`,
    onesignerror: `Credit reduced because it appears that you made a sign error.`,
    twosignerrors: `Credit reduced because it appears that you made two sign errors.`
  };
}
function evaluateLogicDirectlyFromChildren({ dependencyValues, usedDefault }) {
  let dependenciesForEvaluateLogic = {};
  Object.assign(dependenciesForEvaluateLogic, dependencyValues);
  let canOverrideUnorderedCompare = usedDefault.unorderedCompare;
  let answerValue = dependencyValues.answerInput.stateValues.immediateValue;
  if (answerValue === void 0) {
    answerValue = dependencyValues.answerInput.stateValues.value;
  }
  let answerChildForLogic = {
    stateValues: { value: answerValue }
  };
  let inputCode = dependencyValues.codePre + "Input";
  if (dependencyValues.answerInput.componentType === "textInput") {
    dependenciesForEvaluateLogic.textChildrenByCode[inputCode] = answerChildForLogic;
  } else if (dependencyValues.answerInput.componentType === "booleanInput") {
    dependenciesForEvaluateLogic.booleanChildrenByCode[inputCode] = answerChildForLogic;
  } else {
    dependenciesForEvaluateLogic.mathChildrenByCode[inputCode] = answerChildForLogic;
  }
  return evaluateLogic({
    logicTree: dependencyValues.parsedExpression.tree,
    canOverrideUnorderedCompare,
    dependencyValues: dependenciesForEvaluateLogic
  });
}
function addResponsesToDescendantsWithReference(components, reference) {
  for (let component of components) {
    if (component.type === "serialized") {
      if (component.extending) {
        const refResolution = unwrapSource(component.extending);
        if (refResolution.nodeIdx === reference.nodeIdx && comparePathsIgnorePosition(
          refResolution.unresolvedPath,
          reference.unresolvedPath
        )) {
          if (!component.attributes) {
            component.attributes = {};
          }
          let foundIsResponse = Object.keys(component.attributes).map((x2) => x2.toLowerCase()).includes("isresponse");
          if (!foundIsResponse) {
            component.attributes.isResponse = {
              type: "unresolved",
              name: "isResponse",
              children: []
            };
          }
        }
      }
      if (component.children) {
        addResponsesToDescendantsWithReference(
          component.children,
          reference
        );
      }
    }
  }
}
class When extends BooleanComponent {
  static componentType = "when";
  static componentDocs = {
    summary: "Defines logical conditions within an `<award>`"
  };
  static rendererType = void 0;
  static inSchemaOnlyInheritAs = [];
  static stateVariableToBeShadowed = "conditionSatisfied";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.matchPartial = {
      description: "Whether to match partial conditions for partial credit.",
      createComponentOfType: "boolean",
      createStateVariable: "matchPartial",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "matchPartial"
    };
    for (let attrName of [
      "symbolicEquality",
      "expandOnCompare",
      "simplifyOnCompare",
      "unorderedCompare",
      "matchByExactPositions",
      "allowedErrorInNumbers",
      "includeErrorInNumberExponents",
      "allowedErrorIsAbsolute",
      "numSignErrorsMatched",
      "numPeriodicSetMatchesRequired",
      "caseInsensitiveMatch",
      "matchBlanks"
    ]) {
      attributes[attrName].fallBackToParentStateVariable = attrName;
    }
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.value = {
      description: "Whether the condition is currently true.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      additionalStateVariablesDefined: [
        {
          variableName: "fractionSatisfied",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number"
          },
          description: "Fraction of the boolean condition that is satisfied (0 to 1)."
        },
        {
          variableName: "conditionSatisfied",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "boolean"
          },
          description: "Whether the condition is currently satisfied; equivalent to value."
        }
      ],
      returnDependencies: () => ({
        matchPartial: {
          dependencyType: "stateVariable",
          variableName: "matchPartial"
        },
        symbolicEquality: {
          dependencyType: "stateVariable",
          variableName: "symbolicEquality"
        },
        expandOnCompare: {
          dependencyType: "stateVariable",
          variableName: "expandOnCompare"
        },
        simplifyOnCompare: {
          dependencyType: "stateVariable",
          variableName: "simplifyOnCompare"
        },
        unorderedCompare: {
          dependencyType: "stateVariable",
          variableName: "unorderedCompare"
        },
        matchByExactPositions: {
          dependencyType: "stateVariable",
          variableName: "matchByExactPositions"
        },
        allowedErrorInNumbers: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorInNumbers"
        },
        includeErrorInNumberExponents: {
          dependencyType: "stateVariable",
          variableName: "includeErrorInNumberExponents"
        },
        allowedErrorIsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorIsAbsolute"
        },
        numSignErrorsMatched: {
          dependencyType: "stateVariable",
          variableName: "numSignErrorsMatched"
        },
        numPeriodicSetMatchesRequired: {
          dependencyType: "stateVariable",
          variableName: "numPeriodicSetMatchesRequired"
        },
        caseInsensitiveMatch: {
          dependencyType: "stateVariable",
          variableName: "caseInsensitiveMatch"
        },
        matchBlanks: {
          dependencyType: "stateVariable",
          variableName: "matchBlanks"
        },
        parsedExpression: {
          dependencyType: "stateVariable",
          variableName: "parsedExpression"
        },
        booleanChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "booleanChildrenByCode"
        },
        textChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "textChildrenByCode"
        },
        mathChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "mathChildrenByCode"
        },
        numberChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "numberChildrenByCode"
        },
        otherChildrenByCode: {
          dependencyType: "stateVariable",
          variableName: "otherChildrenByCode"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.parsedExpression === null) {
          return {
            setValue: {
              conditionSatisfied: false,
              value: false,
              fractionSatisfied: 0
            }
          };
        }
        let canOverrideUnorderedCompare = usedDefault.unorderedCompare;
        let fractionSatisfied = evaluateLogic({
          logicTree: dependencyValues.parsedExpression.tree,
          canOverrideUnorderedCompare,
          dependencyValues
        });
        let conditionSatisfied = fractionSatisfied === 1;
        return {
          setValue: {
            fractionSatisfied,
            conditionSatisfied,
            value: conditionSatisfied
          }
        };
      }
    };
    stateVariableDefinitions._descendantIdxs = {
      returnDependencies: () => ({
        allDescendants: {
          dependencyType: "descendant",
          componentTypes: ["_base"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            _descendantIdxs: (dependencyValues.allDescendants ?? []).map((d2) => d2.componentIdx)
          }
        };
      }
    };
    stateVariableDefinitions.referencedStateVars = {
      stateVariablesDeterminingDependencies: ["_descendantIdxs"],
      returnDependencies({ stateValues }) {
        const deps = {};
        for (const idx of stateValues._descendantIdxs ?? []) {
          deps[`shadowInfo_${idx}`] = {
            dependencyType: "shadowInfo",
            componentIdx: idx
          };
        }
        return deps;
      },
      definition({ dependencyValues }) {
        const referencedStateVars = [];
        for (const [key, value] of Object.entries(dependencyValues)) {
          if (key.startsWith("shadowInfo_") && value !== null) {
            referencedStateVars.push(value);
          }
        }
        return { setValue: { referencedStateVars } };
      }
    };
    return stateVariableDefinitions;
  }
  // "when" does not adapt to "text", even though boolean does
  static adapters = [];
}
class MathInput extends Input {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateRawValue: this.updateRawValue.bind(this),
      updateValue: this.updateValue.bind(this),
      moveInput: this.moveInput.bind(this)
    });
    defineSubmitAnswerExternalAction(this);
  }
  static componentType = "mathInput";
  static componentDocs = {
    summary: "An interactive math input"
  };
  static variableForImplicitProp = "value";
  static variableForIndexAsProp = "vector";
  static processWhenJustUpdatedForNewComponent = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.prefill = {
      description: "Initial value displayed in the input.",
      createComponentOfType: "math",
      createStateVariable: "prefill",
      defaultValue: me$3.fromAst("＿"),
      public: true,
      copyComponentAttributesForCreatedComponent: [
        "format",
        "functionSymbols",
        "splitSymbols",
        "parseScientificNotation"
      ]
    };
    attributes.prefillLatex = {
      description: "Initial value as a LaTeX string.",
      createComponentOfType: "latex",
      createStateVariable: "prefillLatex",
      defaultValue: "",
      public: true
    };
    attributes.format = {
      description: "Input format.",
      createComponentOfType: "text",
      createStateVariable: "format",
      defaultValue: "text",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "text",
          description: "Plain-text math notation (e.g., `x^2 + 1`)."
        },
        {
          value: "latex",
          description: "LaTeX-formatted math (e.g., `x^{2} + 1`)."
        }
      ]
    };
    attributes.functionSymbols = {
      description: "Symbols treated as function names when parsing.",
      createComponentOfType: "textList",
      createStateVariable: "functionSymbols",
      defaultValue: ["f", "g"],
      public: true
    };
    attributes.additionalFunctionNames = {
      description: "Extra identifiers to auto-format as function names in the editor (e.g., 'erf'). Entries that also appear in `removedFunctionNames` are dropped.",
      createComponentOfType: "textList",
      createStateVariable: "additionalFunctionNames",
      defaultValue: [],
      public: true
    };
    attributes.removedFunctionNames = {
      description: "Built-in function names to stop auto-formatting in the editor (e.g., 'min' so 'kg/min' can be typed as a unit).",
      createComponentOfType: "textList",
      createStateVariable: "removedFunctionNames",
      defaultValue: [],
      public: true
    };
    attributes.resetFunctionNames = {
      description: "When set, replaces the entire auto-formatted function name list (defaults, `additionalFunctionNames`, and `removedFunctionNames` are all ignored). Set to an empty value to disable auto-formatting entirely.",
      createComponentOfType: "textList",
      createStateVariable: "resetFunctionNames",
      defaultValue: null,
      public: true
    };
    attributes.splitSymbols = {
      description: "Whether multi-character symbols are split into a product of single-character variables.",
      createComponentOfType: "boolean",
      createStateVariable: "splitSymbols",
      defaultValue: true,
      public: true,
      fallBackToParentStateVariable: "splitSymbols"
    };
    attributes.parseScientificNotation = {
      description: "Whether to parse expressions like 1e3 as scientific notation.",
      createComponentOfType: "boolean",
      createStateVariable: "parseScientificNotation",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "parseScientificNotation"
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.bindValueTo = {
      createComponentOfType: "math",
      description: "Two-way binding target for the input's value."
    };
    attributes.unionFromU = {
      description: 'Whether "U" between sets is parsed as union.',
      createComponentOfType: "boolean",
      createStateVariable: "unionFromU",
      defaultValue: false,
      public: true
    };
    attributes.hideNaN = {
      description: "Whether to hide NaN values when displaying the input.",
      createComponentOfType: "boolean",
      createStateVariable: "hideNaN",
      defaultValue: true,
      public: true
    };
    attributes.removeStrings = {
      createComponentOfType: "textList",
      createStateVariable: "removeStrings",
      defaultValue: null,
      description: "Substrings to strip from the input before parsing."
    };
    attributes.minWidth = {
      description: "Minimum rendered width of the input.",
      createComponentOfType: "integer",
      createStateVariable: "minWidth",
      defaultValue: 50,
      clamp: [0, Infinity],
      public: true,
      forRenderer: true
    };
    attributes.showPreview = {
      createComponentOfType: "boolean",
      createStateVariable: "showPreviewPreliminary",
      defaultValue: false,
      description: "Whether to display a preview of the parsed math."
    };
    attributes.draggable = {
      description: "Whether the input can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        wrappingComponentType: "math",
        wrapSingleIfNotWrappingComponentType: true
      })
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions({
        displayDigitsDefault: 10,
        displaySmallAsZeroDefault: 0
      })
    );
    Object.assign(
      stateVariableDefinitions,
      returnAnchorStateVariableDefinition()
    );
    stateVariableDefinitions.effectiveFunctionNames = {
      // Resolved MathQuill `autoOperatorNames` list — defaults
      // merged with `additionalFunctionNames` / `removedFunctionNames`
      // (or replaced wholesale when `resetFunctionNames` is set).
      // Computed here in the worker (rather than the renderer) so
      // we can emit a `warning` diagnostic when authored tokens
      // are dropped for failing MathQuill's validator — letting
      // those reach MathQuill would crash the `EditableMathField`
      // mount instead.
      forRenderer: true,
      returnDependencies: () => ({
        additionalFunctionNames: {
          dependencyType: "stateVariable",
          variableName: "additionalFunctionNames"
        },
        removedFunctionNames: {
          dependencyType: "stateVariable",
          variableName: "removedFunctionNames"
        },
        resetFunctionNames: {
          dependencyType: "stateVariable",
          variableName: "resetFunctionNames"
        },
        // The attribute components carry their own source
        // positions; we read them so each emitted diagnostic
        // points at the attribute whose contents actually
        // contributed an invalid token, rather than the whole
        // `<mathInput>` element. The `attributeComponent`
        // dependency returns `null` when the attribute is
        // absent, so the `?.position` guards below are real.
        additionalFunctionNamesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "additionalFunctionNames"
        },
        resetFunctionNamesAttr: {
          dependencyType: "attributeComponent",
          attributeName: "resetFunctionNames"
        }
      }),
      definition({ dependencyValues }) {
        const { names, droppedFromAdditional, droppedFromReset } = buildEffectiveMathInputFunctionNames({
          additional: dependencyValues.additionalFunctionNames,
          removed: dependencyValues.removedFunctionNames,
          reset: dependencyValues.resetFunctionNames
        });
        const result = { setValue: { effectiveFunctionNames: names } };
        const diagnostics = [];
        const buildMessage = (attr, list) => `<mathInput>: ignored invalid function name(s) in ${attr}: ${list.map((n2) => `'${n2}'`).join(", ")}. Each name's display segment must be at least 2 characters (letters or dashes); an optional \`|<mathspeak alternative>\` suffix may follow.`;
        if (droppedFromAdditional.length > 0) {
          diagnostics.push({
            type: "warning",
            message: buildMessage(
              "additionalFunctionNames",
              droppedFromAdditional
            ),
            position: dependencyValues.additionalFunctionNamesAttr?.position
          });
        }
        if (droppedFromReset.length > 0) {
          diagnostics.push({
            type: "warning",
            message: buildMessage(
              "resetFunctionNames",
              droppedFromReset
            ),
            position: dependencyValues.resetFunctionNamesAttr?.position
          });
        }
        if (diagnostics.length > 0)
          result.sendDiagnostics = diagnostics;
        return result;
      }
    };
    stateVariableDefinitions.showPreview = {
      description: "Whether to display a preview of the parsed math.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        showPreviewPreliminary: {
          dependencyType: "stateVariable",
          variableName: "showPreviewPreliminary"
        },
        parentShowPreview: {
          dependencyType: "parentStateVariable",
          variableName: "showPreview"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let showPreview = dependencyValues.showPreviewPreliminary;
        if (usedDefault.showPreviewPreliminary && typeof dependencyValues.parentShowPreview === "boolean" && !usedDefault.parentShowPreview) {
          showPreview = dependencyValues.parentShowPreview;
        }
        return { setValue: { showPreview } };
      }
    };
    stateVariableDefinitions.valueChanged = {
      description: "Whether the value has been changed from its initial state.",
      public: true,
      hasEssential: true,
      defaultValue: false,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition() {
        return { useEssentialOrDefaultValue: { valueChanged: true } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "valueChanged",
              value: Boolean(
                desiredStateVariableValues.valueChanged
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.value = {
      description: "The math value of the input.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      hasEssential: true,
      shadowVariable: true,
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        },
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["value"]
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        prefillLatex: {
          dependencyType: "stateVariable",
          variableName: "prefillLatex"
        },
        unionFromU: {
          dependencyType: "stateVariable",
          variableName: "unionFromU"
        },
        functionSymbols: {
          dependencyType: "stateVariable",
          variableName: "functionSymbols"
        },
        splitSymbols: {
          dependencyType: "stateVariable",
          variableName: "splitSymbols"
        },
        parseScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "parseScientificNotation"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      set: convertValueToMathExpression,
      definition: function({ dependencyValues, usedDefault }) {
        if (dependencyValues.mathChild.length > 0) {
          return {
            setValue: {
              value: dependencyValues.mathChild[0].stateValues.value
            }
          };
        } else if (dependencyValues.bindValueTo) {
          return {
            setValue: {
              value: dependencyValues.bindValueTo.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              value: {
                get defaultValue() {
                  if (!usedDefault.prefill || usedDefault.prefillLatex) {
                    return dependencyValues.prefill;
                  } else {
                    return calculateMathExpressionFromLatex(
                      {
                        latex: dependencyValues.prefillLatex,
                        unionFromU: dependencyValues.unionFromU,
                        functionSymbols: dependencyValues.functionSymbols,
                        splitSymbols: dependencyValues.splitSymbols,
                        parseScientificNotation: dependencyValues.parseScientificNotation
                      }
                    ).expression;
                  }
                }
              }
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions = [
          {
            setDependency: "valueChanged",
            desiredValue: true
          },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.mathChild.length > 0) {
          instructions.push({
            setDependency: "mathChild",
            desiredValue: desiredStateVariableValues.value,
            variableIndex: 0,
            childIndex: 0
          });
        } else if (dependencyValues.bindValueTo) {
          instructions.push({
            setDependency: "bindValueTo",
            desiredValue: desiredStateVariableValues.value,
            variableIndex: 0
          });
        } else {
          instructions.push({
            setEssentialValue: "value",
            value: desiredStateVariableValues.value
          });
        }
        return { success: true, instructions };
      }
    };
    stateVariableDefinitions.immediateValueChanged = {
      description: "Whether the value, including in-progress edits, has been changed from its initial state.",
      public: true,
      hasEssential: true,
      defaultValue: false,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition() {
        return {
          useEssentialOrDefaultValue: { immediateValueChanged: true }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "immediateValueChanged",
              value: Boolean(
                desiredStateVariableValues.immediateValueChanged
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.immediateValue = {
      description: "The math value reflecting the user's in-progress edits.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      hasEssential: true,
      shadowVariable: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      set: convertValueToMathExpression,
      definition: function({
        dependencyValues,
        changes,
        justUpdatedForNewComponent,
        usedDefault
      }) {
        if (changes.value && !justUpdatedForNewComponent && !usedDefault.value) {
          return {
            setValue: { immediateValue: dependencyValues.value },
            setEssentialValue: {
              immediateValue: dependencyValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              immediateValue: {
                defaultValue: dependencyValues.value
              }
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        initialChange,
        shadowedVariable
      }) {
        let instructions = [
          {
            setEssentialValue: "immediateValue",
            value: desiredStateVariableValues.immediateValue
          },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (!(initialChange || shadowedVariable)) {
          instructions.push({
            setDependency: "value",
            desiredValue: desiredStateVariableValues.immediateValue
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.immediateValueLatex = {
      forRenderer: true,
      returnDependencies: () => ({
        immediateValue: {
          dependencyType: "stateVariable",
          variableName: "immediateValue"
        },
        showPreview: {
          dependencyType: "stateVariable",
          variableName: "showPreview"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            immediateValueLatex: dependencyValues.showPreview ? dependencyValues.immediateValue.toLatex({
              showBlanks: true
            }) : ""
          }
        };
      }
    };
    stateVariableDefinitions.valueForDisplay = {
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        }
      }),
      set: convertValueToMathExpression,
      definition: function({ dependencyValues }) {
        let rounded = roundForDisplay({
          value: dependencyValues.value,
          dependencyValues
        });
        return {
          setValue: { valueForDisplay: rounded }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The current input as a text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        return {
          setValue: {
            text: dependencyValues.valueForDisplay.toString(params)
          }
        };
      }
    };
    stateVariableDefinitions.dontUpdateRawValueInDefinition = {
      defaultValue: false,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          dontUpdateRawValueInDefinition: true
        }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "dontUpdateRawValueInDefinition",
              value: desiredStateVariableValues.dontUpdateRawValueInDefinition
            }
          ]
        };
      }
    };
    stateVariableDefinitions.rawRendererValue = {
      forRenderer: true,
      hasEssential: true,
      shadowVariable: true,
      defaultValue: "",
      provideEssentialValuesInDefinition: true,
      public: true,
      description: "The raw value used by the renderer (e.g. LaTeX string).",
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      additionalStateVariablesDefined: [
        {
          variableName: "lastValueForDisplay",
          hasEssential: true,
          shadowVariable: true,
          defaultValue: null,
          set: convertValueToMathExpression
        }
      ],
      returnDependencies: () => ({
        // include immediateValue for inverse definition
        // and to determine if used default value
        immediateValue: {
          dependencyType: "stateVariable",
          variableName: "immediateValue"
        },
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        hideNaN: {
          dependencyType: "stateVariable",
          variableName: "hideNaN"
        },
        dontUpdateRawValueInDefinition: {
          dependencyType: "stateVariable",
          variableName: "dontUpdateRawValueInDefinition"
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        prefillLatex: {
          dependencyType: "stateVariable",
          variableName: "prefillLatex"
        }
      }),
      definition({
        dependencyValues,
        essentialValues,
        justUpdatedForNewComponent,
        usedDefault
      }) {
        if (essentialValues.rawRendererValue === void 0 || !(justUpdatedForNewComponent || deepCompare(
          essentialValues.lastValueForDisplay.tree,
          dependencyValues.valueForDisplay.tree
        ) || dependencyValues.dontUpdateRawValueInDefinition)) {
          let rawRendererValue;
          if (usedDefault.immediateValue && usedDefault.prefill && !usedDefault.prefillLatex) {
            rawRendererValue = stripLatex(
              dependencyValues.prefillLatex
            );
          } else {
            rawRendererValue = stripLatex(
              dependencyValues.valueForDisplay.toLatex({
                showBlanks: false
              })
            );
          }
          if (dependencyValues.hideNaN && rawRendererValue === "NaN") {
            rawRendererValue = "";
          }
          return {
            setValue: {
              rawRendererValue,
              lastValueForDisplay: dependencyValues.valueForDisplay
            },
            setEssentialValue: {
              rawRendererValue,
              lastValueForDisplay: dependencyValues.valueForDisplay
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              rawRendererValue: true,
              lastValueForDisplay: true
            }
          };
        }
      },
      async inverseDefinition({
        desiredStateVariableValues,
        stateValues,
        essentialValues,
        dependencyValues,
        componentIdx
      }) {
        let instructions = [];
        if (typeof desiredStateVariableValues.rawRendererValue === "string") {
          let currentValue = essentialValues.rawRendererValue;
          let desiredValue = desiredStateVariableValues.rawRendererValue;
          if (currentValue !== desiredValue) {
            instructions.push({
              setEssentialValue: "rawRendererValue",
              value: desiredValue
            });
          }
          let unionFromU = await stateValues.unionFromU;
          let functionSymbols = await stateValues.functionSymbols;
          let splitSymbols = await stateValues.splitSymbols;
          let parseScientificNotation = await stateValues.parseScientificNotation;
          let removeStrings = await stateValues.removeStrings;
          let currentMath = calculateMathExpressionFromLatex({
            latex: currentValue,
            unionFromU,
            functionSymbols,
            splitSymbols,
            parseScientificNotation,
            removeStrings
          }).expression;
          let desiredMath = calculateMathExpressionFromLatex({
            latex: desiredValue,
            unionFromU,
            functionSymbols,
            splitSymbols,
            parseScientificNotation,
            removeStrings
          }).expression;
          if (!deepCompare(desiredMath.tree, currentMath.tree)) {
            instructions.push({
              setDependency: "immediateValue",
              desiredValue: desiredMath,
              treatAsInitialChange: true
              // so does not change value
            });
          }
        } else if (desiredStateVariableValues.rawRendererValue instanceof me$3.class) {
          instructions.push({
            setEssentialValue: "lastValueForDisplay",
            value: desiredStateVariableValues.rawRendererValue
          });
          let unionFromU = await stateValues.unionFromU;
          let functionSymbols = await stateValues.functionSymbols;
          let splitSymbols = await stateValues.splitSymbols;
          let parseScientificNotation = await stateValues.parseScientificNotation;
          let removeStrings = await stateValues.removeStrings;
          let currentMath = calculateMathExpressionFromLatex({
            latex: essentialValues.rawRendererValue,
            unionFromU,
            functionSymbols,
            splitSymbols,
            parseScientificNotation,
            removeStrings
          }).expression;
          if (!deepCompare(
            desiredStateVariableValues.rawRendererValue.tree,
            currentMath.tree
          )) {
            let desiredValue = stripLatex(
              desiredStateVariableValues.rawRendererValue.toLatex(
                {
                  showBlanks: false
                }
              )
            );
            if (dependencyValues.hideNaN && desiredValue === "NaN") {
              desiredValue = "";
            }
            instructions.push({
              setEssentialValue: "rawRendererValue",
              value: desiredValue
            });
          }
        } else if (desiredStateVariableValues.lastValueForDisplay instanceof me$3.class) {
          instructions.push({
            setEssentialValue: "lastValueForDisplay",
            value: desiredStateVariableValues.lastValueForDisplay
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.errorMessageRawRenderer = {
      forRenderer: true,
      returnDependencies: () => ({
        rawRendererValue: {
          dependencyType: "stateVariable",
          variableName: "rawRendererValue"
        },
        unionFromU: {
          dependencyType: "stateVariable",
          variableName: "unionFromU"
        },
        functionSymbols: {
          dependencyType: "stateVariable",
          variableName: "functionSymbols"
        },
        splitSymbols: {
          dependencyType: "stateVariable",
          variableName: "splitSymbols"
        },
        parseScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "parseScientificNotation"
        },
        removeStrings: {
          dependencyType: "stateVariable",
          variableName: "removeStrings"
        },
        showPreview: {
          dependencyType: "stateVariable",
          variableName: "showPreview"
        },
        immediateValue: {
          dependencyType: "stateVariable",
          variableName: "immediateValue"
        }
      }),
      definition: function({ dependencyValues }) {
        let errorMessage = null;
        if (dependencyValues.showPreview && dependencyValues.rawRendererValue !== null && dependencyValues.rawRendererValue !== "") {
          const placeholder = "＿";
          if (dependencyValues.immediateValue?.tree === placeholder) {
            errorMessage = calculateMathExpressionFromLatex({
              latex: dependencyValues.rawRendererValue,
              unionFromU: dependencyValues.unionFromU,
              functionSymbols: dependencyValues.functionSymbols,
              splitSymbols: dependencyValues.splitSymbols,
              parseScientificNotation: dependencyValues.parseScientificNotation,
              removeStrings: dependencyValues.removeStrings
            }).errorMessage;
          }
        }
        return {
          setValue: {
            errorMessageRawRenderer: errorMessage
          }
        };
      }
    };
    stateVariableDefinitions.componentType = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { componentType: "math" } })
    };
    Object.assign(
      stateVariableDefinitions,
      returnMathVectorMatrixStateVariableDefinitions()
    );
    return stateVariableDefinitions;
  }
  async updateRawValue({
    rawRendererValue,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.disabled) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "rawRendererValue",
            value: rawRendererValue
          },
          {
            updateType: "setComponentNeedingUpdateValue",
            componentIdx: this.componentIdx
          }
        ],
        transient: true,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async updateValue({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.disabled) {
      let immediateValue = await this.stateValues.immediateValue;
      if (!deepCompare(
        (await this.stateValues.value).tree,
        immediateValue.tree
      )) {
        let updateInstructions = [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "dontUpdateRawValueInDefinition",
            value: true
          },
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "value",
            value: immediateValue
          },
          // in case value ended up being a different value than requested
          // we set immediate value to whatever was the result
          // (hence the need to execute update first)
          {
            updateType: "executeUpdate"
          },
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "dontUpdateRawValueInDefinition",
            value: false
          },
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "immediateValue",
            valueOfStateVariable: "value"
          },
          {
            updateType: "unsetComponentNeedingUpdateValue"
          }
        ];
        updateInstructions.push({
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "rawRendererValue",
          valueOfStateVariable: "valueForDisplay"
        });
        let event = await buildInputResponseEvent({
          component: this,
          verb: "answered",
          response: immediateValue,
          responseText: immediateValue.toString()
        });
        await this.coreFunctions.performUpdate({
          updateInstructions,
          actionId,
          sourceInformation,
          skipRendererUpdate: false,
          event
        });
        return await this.coreFunctions.triggerChainedActions({
          componentIdx: this.componentIdx,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      }
    }
  }
  async moveInput({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  static adapters = [
    {
      stateVariable: "value",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    }
  ];
}
function calculateMathExpressionFromLatex({
  latex,
  unionFromU,
  functionSymbols,
  splitSymbols,
  parseScientificNotation,
  removeStrings
}) {
  let expression;
  if (removeStrings) {
    for (let s2 of removeStrings) {
      if (["$", "%"].includes(s2)) {
        s2 = "\\" + s2;
      }
      latex = latex.replaceAll(s2, "");
    }
  }
  latex = normalizeLatexString(latex, {
    unionFromU
  });
  let fromLatex = latexToMathFactory({
    functionSymbols,
    splitSymbols,
    parseScientificNotation
  });
  let errorMessage = null;
  try {
    expression = fromLatex(latex);
  } catch (e3) {
    expression = me$3.fromAst("＿");
    if (e3.name === "ParseError") {
      errorMessage = e3.message;
    }
  }
  return { expression, errorMessage };
}
class Textinput extends Input {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateImmediateValue: inputUpdateImmediateValue.bind(this),
      updateValue: inputUpdateValue.bind(this),
      moveInput: this.moveInput.bind(this)
    });
    defineSubmitAnswerExternalAction(this);
  }
  static componentType = "textInput";
  static componentDocs = {
    summary: "An interactive text input"
  };
  static variableForImplicitProp = "value";
  static processWhenJustUpdatedForNewComponent = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.prefill = {
      description: "Initial text shown in the input before the user types.",
      createComponentOfType: "text",
      createStateVariable: "prefill",
      defaultValue: "",
      public: true
    };
    attributes.bindValueTo = {
      createComponentOfType: "text",
      description: "Two-way binding target for the input's value."
    };
    attributes.expanded = {
      description: "Whether the input renders as a multi-line text area.",
      createComponentOfType: "boolean",
      createStateVariable: "expanded",
      defaultValue: false,
      forRenderer: true,
      public: true,
      fallBackToParentStateVariable: "expanded"
    };
    attributes.width = {
      createComponentOfType: "componentSize",
      description: "Display width of the input."
    };
    attributes.height = {
      description: "Display height of the input.",
      createComponentOfType: "componentSize",
      createStateVariable: "height",
      defaultValue: { size: 120, isAbsolute: true },
      forRenderer: true,
      public: true
    };
    attributes.draggable = {
      description: "Whether the input can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        wrappingComponentType: "text",
        wrapSingleIfNotWrappingComponentType: true
      })
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "texts",
        componentTypes: ["text"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnInputValueChangedStateVariableDefinitions({
        valueChangedDescription: "Whether the value has been changed from its initial state.",
        immediateValueChangedDescription: "Whether the value, including in-progress edits, has been changed from its initial state."
      })
    );
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.width = {
      description: "Display width of the input.",
      forRenderer: true,
      hasEssential: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      returnDependencies: () => ({
        widthAttr: {
          dependencyType: "attributeComponent",
          attributeName: "width",
          variableNames: ["componentSize"]
        },
        expanded: {
          dependencyType: "stateVariable",
          variableName: "expanded"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.widthAttr) {
          return {
            setValue: {
              width: dependencyValues.widthAttr.stateValues.componentSize
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              width: {
                defaultValue: {
                  size: dependencyValues.expanded ? 600 : 100,
                  isAbsolute: true
                }
              }
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.widthAttr) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "widthAttr",
                desiredValue: desiredStateVariableValues.width
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "width",
                value: desiredStateVariableValues.width
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.value = {
      description: "The text value of the input.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      hasEssential: true,
      shadowVariable: true,
      returnDependencies: () => ({
        textChild: {
          dependencyType: "child",
          childGroups: ["texts"],
          variableNames: ["value"]
        },
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["value"]
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.textChild.length > 0) {
          return {
            setValue: {
              value: dependencyValues.textChild[0].stateValues.value
            }
          };
        } else if (dependencyValues.bindValueTo) {
          return {
            setValue: {
              value: dependencyValues.bindValueTo.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              value: {
                defaultValue: dependencyValues.prefill
              }
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions = [
          {
            setDependency: "valueChanged",
            desiredValue: true
          },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.textChild.length > 0) {
          instructions.push({
            setDependency: "textChild",
            desiredValue: desiredStateVariableValues.value,
            variableIndex: 0,
            childIndex: 0
          });
        } else if (dependencyValues.bindValueTo) {
          instructions.push({
            setDependency: "bindValueTo",
            desiredValue: desiredStateVariableValues.value,
            variableIndex: 0
          });
        } else {
          instructions.push({
            setEssentialValue: "value",
            value: desiredStateVariableValues.value
          });
        }
        return { success: true, instructions };
      }
    };
    Object.assign(
      stateVariableDefinitions,
      returnImmediateValueStateVariableDefinition({
        description: "The text value reflecting the user's in-progress edits.",
        createComponentOfType: "text"
      })
    );
    stateVariableDefinitions.text = {
      description: "The current text as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { text: dependencyValues.value } };
      }
    };
    stateVariableDefinitions.componentType = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { componentType: "text" } })
    };
    let pieceDefs = returnTextPieceStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, pieceDefs);
    return stateVariableDefinitions;
  }
  async moveInput({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
}
class BooleanInput extends Input {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateBoolean: this.updateBoolean.bind(this),
      moveInput: this.moveInput.bind(this)
    });
    defineSubmitAnswerExternalAction(this);
  }
  static componentType = "booleanInput";
  static componentDocs = {
    summary: "An interactive boolean (toggle/checkbox) input"
  };
  static variableForImplicitProp = "value";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.prefill = {
      createComponentOfType: "boolean",
      createStateVariable: "prefill",
      defaultValue: false,
      public: true,
      description: "The initial value of the input before the student interacts."
    };
    attributes.asToggleButton = {
      createComponentOfType: "boolean",
      createStateVariable: "asToggleButton",
      defaultValue: false,
      forRenderer: true,
      public: true,
      description: "Whether to render the input as a toggle button rather than a checkbox."
    };
    attributes.bindValueTo = {
      createComponentOfType: "boolean",
      description: "Two-way binding target for the input's value."
    };
    attributes.draggable = {
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true,
      description: "Whether the input can be dragged on a graph by its anchor."
    };
    Object.assign(attributes, returnAnchorAttributes());
    attributes.labelPosition.defaultValue = "right";
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "number", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    sugarInstructions.push({
      replacementFunction: returnWrapNonLabelsDescriptionsSugarFunction({
        wrappingComponentType: "boolean"
      })
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "booleans",
        componentTypes: ["boolean"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.valueChanged = {
      description: "Whether the input's value has been changed from its prefill value.",
      public: true,
      hasEssential: true,
      defaultValue: false,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition() {
        return { useEssentialOrDefaultValue: { valueChanged: true } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "valueChanged",
              value: Boolean(
                desiredStateVariableValues.valueChanged
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.value = {
      description: "The current boolean value of the input.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      hasEssential: true,
      shadowVariable: true,
      returnDependencies: () => ({
        booleanChild: {
          dependencyType: "child",
          childGroups: ["booleans"],
          variableNames: ["value"]
        },
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["value"]
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.booleanChild.length > 0) {
          return {
            setValue: {
              value: dependencyValues.booleanChild[0].stateValues.value
            }
          };
        } else if (dependencyValues.bindValueTo) {
          return {
            setValue: {
              value: dependencyValues.bindValueTo.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              value: {
                defaultValue: dependencyValues.prefill
              }
            }
          };
        }
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions = [
          {
            setDependency: "valueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.booleanChild.length > 0) {
          instructions.push({
            setDependency: "booleanChild",
            desiredValue: desiredStateVariableValues.value,
            variableIndex: 0,
            childIndex: 0
          });
        } else if (dependencyValues.bindValueTo) {
          instructions.push({
            setDependency: "bindValueTo",
            desiredValue: desiredStateVariableValues.value,
            variableIndex: 0
          });
        } else {
          instructions.push({
            setEssentialValue: "value",
            value: desiredStateVariableValues.value
          });
        }
        return { success: true, instructions };
      }
    };
    stateVariableDefinitions.text = {
      description: 'The current value rendered as a text string ("true" or "false").',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            text: dependencyValues.value ? "true" : "false"
          }
        };
      }
    };
    stateVariableDefinitions.componentType = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { componentType: "boolean" } })
    };
    return stateVariableDefinitions;
  }
  static adapters = ["value"];
  async updateBoolean({
    boolean,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.disabled) {
      let updateInstructions = [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "value",
          value: boolean
        }
      ];
      let event = await buildInputResponseEvent({
        component: this,
        verb: "selected",
        response: boolean,
        responseText: boolean.toString()
      });
      await this.coreFunctions.performUpdate({
        updateInstructions,
        event,
        actionId,
        sourceInformation,
        skipRendererUpdate: true
      });
      return await this.coreFunctions.triggerChainedActions({
        componentIdx: this.componentIdx,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async moveInput({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
}
class Choiceinput extends Input {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateSelectedIndices: this.updateSelectedIndices.bind(this)
    });
    defineSubmitAnswerExternalAction(this);
  }
  static componentType = "choiceInput";
  static componentDocs = {
    summary: "A multiple-choice input, allowing selection from a list of choices"
  };
  static renderChildren = true;
  static variableForImplicitProp = "selectedValues";
  static createsVariants = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.selectMultiple = {
      createComponentOfType: "boolean",
      createStateVariable: "selectMultiple",
      defaultValue: false,
      public: true,
      forRenderer: true,
      fallBackToParentStateVariable: "selectMultiple",
      description: "Whether multiple choices may be selected."
    };
    attributes.matchPartial = {
      createComponentOfType: "boolean",
      createStateVariable: "matchPartial",
      defaultValue: false,
      public: true,
      fallBackToParentStateVariable: "matchPartial",
      description: "Whether to award partial credit when only some correct choices are selected."
    };
    attributes.inline = {
      createComponentOfType: "boolean",
      description: "Whether to render the input inline (as a dropdown) rather than as a list of choices."
    };
    attributes.shuffleOrder = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "shuffleOrder",
      defaultValue: false,
      public: true,
      description: "Whether to display the choices in randomized order."
    };
    attributes.preserveLastChoice = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "preserveLastChoice",
      defaultValue: false,
      public: true,
      description: "Whether the last-rendered choice keeps its position when shuffling."
    };
    attributes.preselectChoice = {
      createComponentOfType: "number",
      createStateVariable: "preselectChoice",
      defaultValue: null,
      description: "Index of a choice to pre-select on initial render."
    };
    attributes.bindValueTo = {
      createComponentOfType: "text",
      description: "Two-way binding target for the input's value."
    };
    attributes.placeHolder = {
      createComponentOfType: "text",
      createStateVariable: "placeHolder",
      defaultValue: "",
      forRenderer: true,
      description: "Placeholder text shown when no choice is selected."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "choices",
        componentTypes: ["choice"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnListItemChildStateVariableDefinitions({
        checkInlineVariable: true,
        listItemInlineAlignment: "flex-start"
      })
    );
    stateVariableDefinitions.inline = {
      description: "Whether the input is rendered inline (as a dropdown).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: false,
      hasEssential: true,
      provideEssentialValuesInDefinition: true,
      returnDependencies: () => ({
        inlineAttr: {
          dependencyType: "attributeComponent",
          attributeName: "inline",
          variableNames: ["value"]
        },
        parentInline: {
          dependencyType: "parentStateVariable",
          variableName: "inline"
        },
        labelPositionAttr: {
          dependencyType: "attributeComponent",
          attributeName: "labelPosition",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues, essentialValues }) {
        const setValue = {};
        const useEssentialOrDefaultValue = {};
        let isInline = false;
        if (dependencyValues.inlineAttr) {
          isInline = Boolean(
            dependencyValues.inlineAttr.stateValues.value
          );
          setValue.inline = isInline;
        } else if (dependencyValues.parentInline !== null) {
          isInline = Boolean(dependencyValues.parentInline);
          setValue.inline = isInline;
        } else {
          isInline = Boolean(essentialValues.inline);
          useEssentialOrDefaultValue.inline = {};
        }
        const diagnostics = [];
        if (dependencyValues.labelPositionAttr && !isInline) {
          diagnostics.push({
            message: "labelPosition is ignored for non-inline choiceInput",
            type: "warning",
            position: dependencyValues.labelPositionAttr.position
          });
        }
        return {
          setValue,
          useEssentialOrDefaultValue,
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.numChoices = {
      description: "The total number of available choices.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        choiceChildren: {
          dependencyType: "child",
          childGroups: ["choices"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numChoices: dependencyValues.choiceChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.choiceOrder = {
      forRenderer: true,
      shadowVariable: true,
      returnDependencies: ({ sharedParameters }) => ({
        choiceChildren: {
          dependencyType: "child",
          childGroups: ["choices"]
        },
        shuffleOrder: {
          dependencyType: "stateVariable",
          variableName: "shuffleOrder"
        },
        preserveLastChoice: {
          dependencyType: "stateVariable",
          variableName: "preserveLastChoice"
        },
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        rngClass: {
          dependencyType: "value",
          value: sharedParameters.rngClass,
          doNotProxy: true
        },
        variants: {
          dependencyType: "variants"
        }
      }),
      definition: function({ dependencyValues }) {
        let numChoices = dependencyValues.choiceChildren.length;
        let diagnostics = [];
        let choiceOrder;
        if (!dependencyValues.shuffleOrder) {
          choiceOrder = [...Array(numChoices).keys()].map(
            (x2) => x2 + 1
          );
        } else {
          let desiredChoiceOrder = dependencyValues.variants?.desiredVariant?.indices;
          if (desiredChoiceOrder !== void 0) {
            if (desiredChoiceOrder.length !== numChoices) {
              diagnostics.push({
                message: "Ignoring indices specified for choiceInput as number of indices doesn't match number of choice children.",
                type: "info"
              });
            } else {
              desiredChoiceOrder = desiredChoiceOrder.map(Number);
              if (!desiredChoiceOrder.every(Number.isInteger)) {
                throw Error(
                  "All indices specified for choiceInput must be integers"
                );
              }
              if (!desiredChoiceOrder.every(
                (x2) => x2 >= 1 && x2 <= numChoices
              )) {
                diagnostics.push({
                  message: "Ignoring indices specified for choiceInput as some indices out of range.",
                  type: "info"
                });
              } else {
                return {
                  setValue: {
                    choiceOrder: desiredChoiceOrder
                  }
                };
              }
            }
          }
          let variantRng = dependencyValues.rngClass(
            dependencyValues.variantSeed + "co"
          );
          choiceOrder = [...Array(numChoices).keys()].map(
            (x2) => x2 + 1
          );
          const lastShuffledIdx = numChoices - (dependencyValues.preserveLastChoice ? 2 : 1);
          for (let i2 = lastShuffledIdx; i2 > 0; i2--) {
            const rand = variantRng();
            const j2 = Math.floor(rand * (i2 + 1));
            [choiceOrder[i2], choiceOrder[j2]] = [
              choiceOrder[j2],
              choiceOrder[i2]
            ];
          }
        }
        return {
          setValue: { choiceOrder },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.generatedVariantInfo = {
      additionalStateVariablesDefined: ["isVariantComponent"],
      returnDependencies: ({
        componentInfoObjects,
        sharedParameters
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        choiceOrder: {
          dependencyType: "stateVariable",
          variableName: "choiceOrder"
        },
        shuffleOrder: {
          dependencyType: "stateVariable",
          variableName: "shuffleOrder"
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: { createdBy: componentIdx }
        };
        if (dependencyValues.shuffleOrder) {
          generatedVariantInfo.indices = dependencyValues.choiceOrder;
        }
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo,
            isVariantComponent: true
          }
        };
      }
    };
    stateVariableDefinitions.choiceChildrenOrdered = {
      additionalStateVariablesDefined: [
        {
          variableName: "numChoices",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number"
          },
          description: "The number of choices available in the input."
        }
      ],
      returnDependencies: () => ({
        choiceOrder: {
          dependencyType: "stateVariable",
          variableName: "choiceOrder"
        },
        choiceChildren: {
          dependencyType: "child",
          childGroups: ["choices"]
        }
      }),
      definition: function({ dependencyValues }) {
        let numChoices = dependencyValues.choiceChildren.length;
        let choiceChildrenOrdered = dependencyValues.choiceOrder.map(
          (i2) => dependencyValues.choiceChildren[i2 - 1]
        );
        return {
          setValue: {
            choiceChildrenOrdered,
            numChoices
          }
        };
      }
    };
    stateVariableDefinitions.choiceTexts = {
      description: "The text contents of all choices in displayed order.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      isArray: true,
      entryPrefixes: ["choiceText"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numChoices: {
          dependencyType: "stateVariable",
          variableName: "numChoices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numChoices];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          choiceOrder: {
            dependencyType: "stateVariable",
            variableName: "choiceOrder"
          },
          choiceChildren: {
            dependencyType: "child",
            childGroups: ["choices"],
            variableNames: ["text"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let choiceChildrenOrdered = globalDependencyValues.choiceOrder.map(
          (i2) => globalDependencyValues.choiceChildren[i2 - 1]
        );
        return {
          setValue: {
            choiceTexts: choiceChildrenOrdered.map(
              (x2) => x2.stateValues.text
            )
          }
        };
      }
    };
    stateVariableDefinitions.choiceMaths = {
      description: "The math expressions of all choices in displayed order.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      isArray: true,
      entryPrefixes: ["choiceMath"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numChoices: {
          dependencyType: "stateVariable",
          variableName: "numChoices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numChoices];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          choiceOrder: {
            dependencyType: "stateVariable",
            variableName: "choiceOrder"
          },
          choiceChildren: {
            dependencyType: "child",
            childGroups: ["choices"],
            variableNames: ["math"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let choiceChildrenOrdered = globalDependencyValues.choiceOrder.map(
          (i2) => globalDependencyValues.choiceChildren[i2 - 1]
        );
        return {
          setValue: {
            choiceMaths: choiceChildrenOrdered.map(
              (x2) => x2.stateValues.math
            )
          }
        };
      }
    };
    stateVariableDefinitions.choicePreselects = {
      isArray: true,
      returnArraySizeDependencies: () => ({
        numChoices: {
          dependencyType: "stateVariable",
          variableName: "numChoices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numChoices];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          choiceOrder: {
            dependencyType: "stateVariable",
            variableName: "choiceOrder"
          },
          choiceChildren: {
            dependencyType: "child",
            childGroups: ["choices"],
            variableNames: ["preSelect"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let choiceChildrenOrdered = globalDependencyValues.choiceOrder.map(
          (i2) => globalDependencyValues.choiceChildren[i2 - 1]
        );
        return {
          setValue: {
            choicePreselects: choiceChildrenOrdered.map(
              (x2) => x2.stateValues.preSelect
            )
          }
        };
      }
    };
    stateVariableDefinitions.disableWrongChoices = {
      returnDependencies: () => ({
        answerAncestor: {
          dependencyType: "ancestor",
          componentType: "answer",
          variableNames: ["disableWrongChoices"]
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            disableWrongChoices: Boolean(
              dependencyValues.answerAncestor?.stateValues.disableWrongChoices
            )
          }
        };
      }
    };
    stateVariableDefinitions.choicesDisabled = {
      isArray: true,
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numChoices: {
          dependencyType: "stateVariable",
          variableName: "numChoices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numChoices];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          choiceOrder: {
            dependencyType: "stateVariable",
            variableName: "choiceOrder"
          },
          choiceChildren: {
            dependencyType: "child",
            childGroups: ["choices"],
            variableNames: ["disabled"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let choiceChildrenOrdered = globalDependencyValues.choiceOrder.map(
          (i2) => globalDependencyValues.choiceChildren[i2 - 1]
        );
        return {
          setValue: {
            choicesDisabled: choiceChildrenOrdered.map(
              (x2) => x2.stateValues.disabled
            )
          }
        };
      }
    };
    stateVariableDefinitions.choicesHidden = {
      isArray: true,
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numChoices: {
          dependencyType: "stateVariable",
          variableName: "numChoices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numChoices];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          choiceOrder: {
            dependencyType: "stateVariable",
            variableName: "choiceOrder"
          },
          choiceChildren: {
            dependencyType: "child",
            childGroups: ["choices"],
            variableNames: ["hidden"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let choiceChildrenOrdered = globalDependencyValues.choiceOrder.map(
          (i2) => globalDependencyValues.choiceChildren[i2 - 1]
        );
        return {
          setValue: {
            choicesHidden: choiceChildrenOrdered.map(
              (x2) => x2.stateValues.hidden
            )
          }
        };
      }
    };
    stateVariableDefinitions.componentType = {
      returnDependencies: () => ({
        choiceChildren: {
          dependencyType: "child",
          childGroups: ["choices"],
          variableNames: ["math"]
        }
      }),
      definition({ dependencyValues }) {
        let componentType = "text";
        if (dependencyValues.choiceChildren.length > 0 && dependencyValues.choiceChildren.every(
          (x2) => x2.stateValues.math
        )) {
          componentType = "math";
        }
        return { setValue: { componentType } };
      }
    };
    stateVariableDefinitions.hasBoundValue = {
      returnDependencies: () => ({
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            hasBoundValue: dependencyValues.bindValueTo !== null
          }
        };
      }
    };
    stateVariableDefinitions.indicesMatchedByBoundValue = {
      // Only pull in bound-value-specific dependencies when
      // bindValueTo is present. When bindValueTo is absent this
      // variable always returns [], so those dependencies are
      // unnecessary and the choiceChildren.text dependency causes the
      // cycle described on hasBoundValue above.
      stateVariablesDeterminingDependencies: ["hasBoundValue"],
      returnDependencies: ({ stateValues }) => {
        const deps = {
          bindValueTo: {
            dependencyType: "attributeComponent",
            attributeName: "bindValueTo",
            variableNames: ["value"]
          }
        };
        if (stateValues.hasBoundValue) {
          deps.choiceOrder = {
            dependencyType: "stateVariable",
            variableName: "choiceOrder"
          };
          deps.selectMultiple = {
            dependencyType: "stateVariable",
            variableName: "selectMultiple"
          };
          deps.choiceChildren = {
            dependencyType: "child",
            childGroups: ["choices"],
            variableNames: ["text"]
          };
        }
        return deps;
      },
      definition({ dependencyValues }) {
        if (dependencyValues.bindValueTo !== null) {
          const choiceChildrenOrdered = dependencyValues.choiceOrder.map(
            (i2) => dependencyValues.choiceChildren[i2 - 1]
          );
          let choiceTexts = choiceChildrenOrdered.map(
            (x2) => x2.stateValues.text.toLowerCase().trim()
          );
          if (dependencyValues.bindValueTo.stateValues.value) {
            if (dependencyValues.selectMultiple) {
              let valuesToBind = dependencyValues.bindValueTo.stateValues.value.toLowerCase().split(",").map((x2) => x2.trim());
              let indicesMatchedByBoundValue = [];
              for (let val of valuesToBind) {
                let ind = choiceTexts.indexOf(val);
                if (ind !== -1 && !indicesMatchedByBoundValue.includes(
                  ind + 1
                )) {
                  indicesMatchedByBoundValue.push(ind + 1);
                }
              }
              indicesMatchedByBoundValue.sort((a2, b2) => a2 - b2);
              return { setValue: { indicesMatchedByBoundValue } };
            } else {
              let ind = choiceTexts.indexOf(
                dependencyValues.bindValueTo.stateValues.value.toLowerCase().trim()
              );
              if (ind !== -1) {
                return {
                  setValue: {
                    indicesMatchedByBoundValue: [ind + 1]
                  }
                };
              }
            }
          }
        }
        return { setValue: { indicesMatchedByBoundValue: [] } };
      }
    };
    stateVariableDefinitions.valueChanged = {
      description: "Whether the input's selection has been changed from its initial state.",
      public: true,
      hasEssential: true,
      defaultValue: false,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition() {
        return { useEssentialOrDefaultValue: { valueChanged: true } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "valueChanged",
              value: Boolean(
                desiredStateVariableValues.valueChanged
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.allSelectedIndices = {
      hasEssential: true,
      returnDependencies() {
        return {
          indicesMatchedByBoundValue: {
            dependencyType: "stateVariable",
            variableName: "indicesMatchedByBoundValue"
          },
          preselectChoice: {
            dependencyType: "stateVariable",
            variableName: "preselectChoice"
          },
          choicePreselects: {
            dependencyType: "stateVariable",
            variableName: "choicePreselects"
          },
          bindValueTo: {
            dependencyType: "attributeComponent",
            attributeName: "bindValueTo",
            variableNames: ["value"]
          },
          valueChanged: {
            dependencyType: "stateVariable",
            variableName: "valueChanged",
            onlyToSetInInverseDefinition: true
          }
        };
      },
      definition({ dependencyValues }) {
        if (dependencyValues.bindValueTo) {
          return {
            setValue: {
              allSelectedIndices: dependencyValues.indicesMatchedByBoundValue
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              allSelectedIndices: {
                get defaultValue() {
                  let ind = dependencyValues.choicePreselects.indexOf(
                    true
                  );
                  if (ind !== -1) {
                    return [ind + 1];
                  } else if (Number.isInteger(
                    dependencyValues.preselectChoice
                  )) {
                    return [
                      dependencyValues.preselectChoice
                    ];
                  } else {
                    return [];
                  }
                }
              }
            }
          };
        }
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        let instructions = [
          {
            setDependency: "valueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.bindValueTo) {
          let desiredText = "";
          if (desiredStateVariableValues.allSelectedIndices.length > 0) {
            let selectedTexts = [];
            const choiceTexts = await stateValues.choiceTexts;
            for (let ind of desiredStateVariableValues.allSelectedIndices) {
              let selectedText = choiceTexts[ind - 1];
              if (selectedText) {
                selectedTexts.push(selectedText);
              }
            }
            desiredText = selectedTexts.join(", ");
          }
          instructions.push({
            setDependency: "bindValueTo",
            desiredValue: desiredText,
            variableIndex: 0
          });
        } else {
          instructions.push({
            setEssentialValue: "allSelectedIndices",
            value: desiredStateVariableValues.allSelectedIndices
          });
        }
        return { success: true, instructions };
      }
    };
    stateVariableDefinitions.numSelectedIndices = {
      returnDependencies: () => ({
        allSelectedIndices: {
          dependencyType: "stateVariable",
          variableName: "allSelectedIndices"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numSelectedIndices: dependencyValues.allSelectedIndices.length
          }
        };
      }
    };
    stateVariableDefinitions.selectedIndices = {
      description: "Indices (1-based) of the currently selected choices.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      isArray: true,
      allowExtraArrayKeysInInverse: true,
      doNotCombineInverseArrayInstructions: true,
      entryPrefixes: ["selectedIndex"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numSelectedIndices: {
          dependencyType: "stateVariable",
          variableName: "numSelectedIndices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numSelectedIndices];
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            allSelectedIndices: {
              dependencyType: "stateVariable",
              variableName: "allSelectedIndices"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let selectedIndices = {};
        for (let key in globalDependencyValues.allSelectedIndices) {
          selectedIndices[key] = globalDependencyValues.allSelectedIndices[key];
        }
        return { setValue: { selectedIndices } };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        stateValues
      }) {
        let selectMultiple = await stateValues.selectMultiple;
        let numChoices = await stateValues.numChoices;
        let desiredIndices = {};
        for (let key in desiredStateVariableValues.selectedIndices) {
          let newInd = desiredStateVariableValues.selectedIndices[key];
          if (newInd instanceof me$3.class) {
            newInd = newInd.evaluate_to_constant();
          }
          if (Number.isInteger(newInd) && newInd >= 1 && newInd <= numChoices) {
            desiredIndices[key] = newInd;
          }
        }
        let newSelectedIndices = [];
        if (selectMultiple) {
          if (!Array.isArray(
            desiredStateVariableValues.selectedIndices
          )) {
            newSelectedIndices = [
              ...globalDependencyValues.allSelectedIndices
            ];
          }
          for (let key in desiredIndices) {
            newSelectedIndices[key] = desiredIndices[key];
          }
          newSelectedIndices = newSelectedIndices.reduce((a2, c2) => {
            if (c2 !== void 0 && !a2.includes(c2)) {
              return [...a2, c2];
            } else {
              return a2;
            }
          }, []);
          newSelectedIndices.sort((a2, b2) => a2 - b2);
        } else {
          if (desiredIndices[0] !== void 0) {
            newSelectedIndices = [desiredIndices[0]];
          } else {
            newSelectedIndices = [];
          }
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "allSelectedIndices",
              desiredValue: newSelectedIndices
            }
          ]
        };
      }
    };
    stateVariableDefinitions.selectedIndex = {
      isAlias: true,
      targetVariableName: "selectedIndex1",
      description: "The 1-based index of the first currently selected choice."
    };
    stateVariableDefinitions.selectedValues = {
      description: "Text values of the currently selected choices.",
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true
      },
      isArray: true,
      allowExtraArrayKeysInInverse: true,
      entryPrefixes: ["selectedValue"],
      returnArraySizeDependencies: () => ({
        numSelectedIndices: {
          dependencyType: "stateVariable",
          variableName: "numSelectedIndices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numSelectedIndices];
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            selectedIndices: {
              dependencyType: "stateVariable",
              variableName: "selectedIndices"
            },
            choiceTexts: {
              dependencyType: "stateVariable",
              variableName: "choiceTexts"
            },
            choiceMaths: {
              dependencyType: "stateVariable",
              variableName: "choiceMaths"
            },
            componentType: {
              dependencyType: "stateVariable",
              variableName: "componentType"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let selectedValues = {};
        let componentType = globalDependencyValues.componentType;
        for (let arrayKey of arrayKeys) {
          if (componentType === "math") {
            selectedValues[arrayKey] = globalDependencyValues.choiceMaths[globalDependencyValues.selectedIndices[arrayKey] - 1];
          } else {
            selectedValues[arrayKey] = globalDependencyValues.choiceTexts[globalDependencyValues.selectedIndices[arrayKey] - 1];
          }
        }
        return {
          setValue: { selectedValues },
          setCreateComponentOfType: { selectedValues: componentType }
        };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        stateValues
      }) {
        let desiredSelectedValues = desiredStateVariableValues.selectedValues;
        if (typeof desiredSelectedValues === "string") {
          desiredSelectedValues = [desiredSelectedValues];
        }
        let selectMultiple = await stateValues.selectMultiple;
        let componentType = globalDependencyValues.componentType;
        if (selectMultiple) {
          let newSelectedIndices = [
            ...globalDependencyValues.selectedIndices
          ];
          let foundValue = false;
          for (let key in desiredSelectedValues) {
            let desiredVal = desiredSelectedValues[key];
            let ind = getIndOfDesiredValue(desiredVal);
            if (ind !== -1) {
              newSelectedIndices[key] = ind + 1;
              foundValue = true;
            }
          }
          if (foundValue) {
            newSelectedIndices = newSelectedIndices.reduce(
              (a2, c2) => {
                if (c2 !== void 0 && !a2.includes(c2)) {
                  return [...a2, c2];
                } else {
                  return a2;
                }
              },
              []
            );
            newSelectedIndices.sort((a2, b2) => a2 - b2);
            return {
              success: true,
              instructions: [
                {
                  setDependency: "selectedIndices",
                  desiredValue: newSelectedIndices
                }
              ]
            };
          }
        } else {
          let desiredVal = desiredSelectedValues[0];
          let ind = getIndOfDesiredValue(desiredVal);
          if (ind !== -1) {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "selectedIndices",
                  desiredValue: [ind + 1]
                }
              ]
            };
          }
        }
        return { success: false };
        function getIndOfDesiredValue(desiredVal) {
          let ind = -1;
          if (componentType === "math") {
            if (desiredVal instanceof me$3.class) {
              for (let [
                ind2,
                val
              ] of globalDependencyValues.choiceMaths.entries()) {
                if (val.equals(desiredVal)) {
                  ind = ind2;
                  break;
                }
              }
            }
          } else {
            ind = globalDependencyValues.choiceTexts.map((v2) => v2.toLowerCase().trim()).indexOf(desiredVal?.toLowerCase().trim());
          }
          return ind;
        }
      }
    };
    stateVariableDefinitions.selectedValue = {
      isAlias: true,
      targetVariableName: "selectedValue1",
      description: "The text value of the first currently selected choice."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "selectedValues",
      description: "The text values of the currently selected choices."
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numSelectedIndices",
      description: "The number of currently selected choices."
    };
    stateVariableDefinitions.childIndicesSelected = {
      returnDependencies: () => ({
        selectedIndices: {
          dependencyType: "stateVariable",
          variableName: "selectedIndices"
        },
        choiceOrder: {
          dependencyType: "stateVariable",
          variableName: "choiceOrder"
        }
      }),
      definition({ dependencyValues }) {
        let childIndicesSelected = dependencyValues.selectedIndices.map(
          (x2) => dependencyValues.choiceOrder[x2 - 1]
        );
        return { setValue: { childIndicesSelected } };
      }
    };
    stateVariableDefinitions.creditAchievedIfSubmit = {
      returnDependencies: () => ({
        choiceChildren: {
          dependencyType: "child",
          childGroups: ["choices"],
          variableNames: ["selected", "credit"]
        },
        selectMultiple: {
          dependencyType: "stateVariable",
          variableName: "selectMultiple"
        },
        matchPartial: {
          dependencyType: "stateVariable",
          variableName: "matchPartial"
        }
      }),
      definition: function({ dependencyValues }) {
        let creditAchievedIfSubmit = 0;
        if (dependencyValues.selectMultiple) {
          let nCorrectlySelected = 0;
          let nIncorrectlySelected = 0;
          let nIncorrectlyUnselected = 0;
          for (let choiceChild of dependencyValues.choiceChildren) {
            if (choiceChild.stateValues.selected) {
              if (choiceChild.stateValues.credit === 1) {
                nCorrectlySelected++;
              } else {
                nIncorrectlySelected++;
              }
            } else {
              if (choiceChild.stateValues.credit === 1) {
                nIncorrectlyUnselected++;
              }
            }
          }
          if (dependencyValues.matchPartial) {
            let denominator = nCorrectlySelected + nIncorrectlySelected + nIncorrectlyUnselected;
            if (denominator === 0) {
              creditAchievedIfSubmit = 1;
            } else {
              creditAchievedIfSubmit = nCorrectlySelected / denominator;
            }
          } else {
            if (nIncorrectlySelected + nIncorrectlyUnselected === 0) {
              creditAchievedIfSubmit = 1;
            }
          }
        } else {
          for (let choiceChild of dependencyValues.choiceChildren) {
            if (choiceChild.stateValues.selected) {
              creditAchievedIfSubmit = choiceChild.stateValues.credit;
              break;
            }
          }
        }
        return { setValue: { creditAchievedIfSubmit } };
      }
    };
    stateVariableDefinitions.valueToRecordOnSubmit = {
      isAlias: true,
      targetVariableName: "selectedIndices",
      description: "Indices of the currently selected choices (recorded when the answer is submitted)."
    };
    stateVariableDefinitions.submittedIndices = {
      returnDependencies: () => ({
        choiceOrder: {
          dependencyType: "stateVariable",
          variableName: "choiceOrder"
        },
        choiceChildren: {
          dependencyType: "child",
          childGroups: ["choices"],
          variableNames: ["submitted"]
        }
      }),
      definition({ dependencyValues }) {
        let submittedIndices = [];
        let choiceChildrenOrdered = dependencyValues.choiceOrder.map(
          (i2) => dependencyValues.choiceChildren[i2 - 1]
        );
        for (let [
          ind,
          choiceChild
        ] of choiceChildrenOrdered.entries()) {
          if (choiceChild.stateValues.submitted) {
            submittedIndices.push(ind + 1);
          }
        }
        return { setValue: { submittedIndices } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions = [];
        for (let [
          ind,
          choiceInd
        ] of dependencyValues.choiceOrder.entries()) {
          instructions.push({
            setDependency: "choiceChildren",
            desiredValue: desiredStateVariableValues.submittedIndices.includes(
              ind + 1
            ),
            variableIndex: 0,
            childIndex: choiceInd - 1
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.valueRecordedAtSubmit = {
      isAlias: true,
      targetVariableName: "submittedIndices",
      description: "Indices of the choices selected at the most recent submission."
    };
    stateVariableDefinitions.allFeedbacks = {
      returnDependencies: () => ({
        choiceOrder: {
          dependencyType: "stateVariable",
          variableName: "choiceOrder"
        },
        choiceChildren: {
          dependencyType: "child",
          childGroups: ["choices"],
          variableNames: ["feedbacks"]
        }
      }),
      definition({ dependencyValues }) {
        let choiceChildrenOrdered = dependencyValues.choiceOrder.map(
          (i2) => dependencyValues.choiceChildren[i2 - 1]
        );
        let feedbacks = [];
        for (let choiceChild of choiceChildrenOrdered) {
          feedbacks.push(...choiceChild.stateValues.feedbacks);
        }
        return {
          setValue: {
            allFeedbacks: feedbacks
          }
        };
      }
    };
    stateVariableDefinitions.numFeedbacks = {
      description: "The number of feedback messages produced by selected choices.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        allFeedbacks: {
          dependencyType: "stateVariable",
          variableName: "allFeedbacks"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numFeedbacks: dependencyValues.allFeedbacks.length
          },
          checkForActualChange: { numFeedbacks: true }
        };
      }
    };
    stateVariableDefinitions.feedbacks = {
      description: "Feedback messages produced by selected choices.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "feedback"
      },
      isArray: true,
      entryPrefixes: ["feedback"],
      returnArraySizeDependencies: () => ({
        numFeedbacks: {
          dependencyType: "stateVariable",
          variableName: "numFeedbacks"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numFeedbacks];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allFeedbacks: {
            dependencyType: "stateVariable",
            variableName: "allFeedbacks"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let feedbacks = {};
        for (let arrayKey = 0; arrayKey < globalDependencyValues.__array_size; arrayKey++) {
          feedbacks[arrayKey] = globalDependencyValues.allFeedbacks[arrayKey];
        }
        return { setValue: { feedbacks } };
      }
    };
    stateVariableDefinitions.descriptionChildInd = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            descriptionChildInd: dependencyValues.allChildren.findLastIndex(
              (child) => child.componentType === "description"
            )
          }
        };
      }
    };
    stateVariableDefinitions.choiceChildIndices = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            choiceChildIndices: dependencyValues.allChildren.map(
              (child, ind) => child.componentType === "choice" ? ind : -1
            ).filter((ind) => ind !== -1)
          }
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          includeAllChildren: true
        },
        inline: {
          dependencyType: "stateVariable",
          variableName: "inline"
        },
        descriptionChildInd: {
          dependencyType: "stateVariable",
          variableName: "descriptionChildInd"
        }
      }),
      definition: function({ dependencyValues }) {
        const childIndicesToRender = [];
        for (const [
          ind,
          child
        ] of dependencyValues.children.entries()) {
          if (child.componentType === "choice") {
            childIndicesToRender.push(ind);
          }
        }
        if (dependencyValues.descriptionChildInd !== -1) {
          childIndicesToRender.push(
            dependencyValues.descriptionChildInd
          );
        }
        return {
          setValue: {
            childIndicesToRender
          }
        };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    return stateVariableDefinitions;
  }
  async updateSelectedIndices({
    selectedIndices,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.disabled) {
      let choicesDisabled = await this.stateValues.choicesDisabled;
      let effectiveSelectedIndices = selectedIndices.filter(
        (v2) => !choicesDisabled[v2 - 1]
      );
      let updateInstructions = [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "allSelectedIndices",
          value: effectiveSelectedIndices
        }
      ];
      let choiceTexts = await this.stateValues.choiceTexts;
      let event = await buildInputResponseEvent({
        component: this,
        verb: "selected",
        response: effectiveSelectedIndices,
        responseText: effectiveSelectedIndices.map(
          (i2) => choiceTexts[i2 - 1]
        )
      });
      await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate: true,
        event
      });
      return await this.coreFunctions.triggerChainedActions({
        componentIdx: this.componentIdx,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents
    });
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    if (!serializedComponent.attributes?.shuffleOrder?.primitive.value) {
      return super.determineNumberOfUniqueVariants({
        serializedComponent,
        componentInfoObjects,
        infoDiagnostics
      });
    }
    let numChoices = 0;
    for (let child of serializedComponent.children) {
      if (child.componentType === "choice") {
        numChoices++;
      } else {
        if (componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: child.componentType,
          baseComponentType: "_composite"
        }) && child.attributes.createComponentOfType?.primitive.value === "choice") {
          if (child.attributes.numComponents?.primitive !== void 0) {
            let newChoices = Number(
              child.attributes.numComponents?.primitive.value
            );
            if (Number.isInteger(newChoices) && newChoices >= 0) {
              numChoices += newChoices;
            } else {
              return { success: false };
            }
          } else {
            numChoices++;
          }
        } else {
          return { success: false };
        }
      }
    }
    const preserveLastChoice = serializedComponent.attributes?.preserveLastChoice?.primitive.value;
    const numChoicesAdjusted = preserveLastChoice ? Math.max(1, numChoices - 1) : numChoices;
    let numberOfPermutations = 1;
    for (let i2 = 2; i2 <= numChoicesAdjusted; i2++) {
      numberOfPermutations *= i2;
    }
    let result = super.determineNumberOfUniqueVariants({
      serializedComponent,
      componentInfoObjects,
      infoDiagnostics
    });
    if (!result.success) {
      return { success: false };
    }
    let numVariants = result.numVariants * numberOfPermutations;
    serializedComponent.variants.numVariants = numVariants;
    serializedComponent.variants.uniqueVariantData = {
      numVariantsByDescendant: serializedComponent.variants.uniqueVariantData.numVariantsByDescendant,
      numberOfPermutations,
      numChoices,
      numChoicesAdjusted
    };
    return { success: true, numVariants };
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    if (!serializedComponent.attributes.shuffleOrder?.primitive.value) {
      return super.getUniqueVariant({
        serializedComponent,
        variantIndex,
        componentInfoObjects
      });
    }
    let numVariantsByDescendant = serializedComponent.variants.uniqueVariantData.numVariantsByDescendant;
    let descendantVariantComponents = serializedComponent.variants.descendantVariantComponents;
    let numberOfPermutations = serializedComponent.variants.uniqueVariantData.numberOfPermutations;
    let numChoices = serializedComponent.variants.uniqueVariantData.numChoices;
    let numChoicesAdjusted = serializedComponent.variants.uniqueVariantData.numChoicesAdjusted;
    let numbersOfOptions = [...numVariantsByDescendant];
    numbersOfOptions.push(numberOfPermutations);
    let indicesForEachOption = enumerateCombinations({
      numberOfOptionsByIndex: numbersOfOptions,
      maxNumber: variantIndex
    })[variantIndex - 1].map((x2) => x2 + 1);
    let permutationsIndex = indicesForEachOption.pop();
    let indicesForEachDescendant = indicesForEachOption;
    let indicesToPermute = [...Array(numChoicesAdjusted).keys()].map(
      (x2) => x2 + 1
    );
    let permutedIndices = enumeratePermutations({
      values: indicesToPermute,
      maxNumber: permutationsIndex
    })[permutationsIndex - 1];
    if (numChoicesAdjusted < numChoices) {
      permutedIndices = [...permutedIndices, numChoices];
    }
    let haveNontrivialSubvariants = false;
    let subvariants = [];
    for (let descendantNum = 0; descendantNum < numVariantsByDescendant.length; descendantNum++) {
      if (numVariantsByDescendant[descendantNum] > 1) {
        let descendant = descendantVariantComponents[descendantNum];
        let compClass = componentInfoObjects.allComponentClasses[descendant.componentType];
        let result = compClass.getUniqueVariant({
          serializedComponent: descendant,
          variantIndex: indicesForEachDescendant[descendantNum],
          componentInfoObjects
        });
        if (!result.success) {
          return { success: false };
        }
        subvariants.push(result.desiredVariant);
        haveNontrivialSubvariants = true;
      } else {
        subvariants.push({});
      }
    }
    let desiredVariant = { indices: permutedIndices };
    if (haveNontrivialSubvariants) {
      desiredVariant.subvariants = subvariants;
    }
    return { success: true, desiredVariant };
  }
}
class Choice extends InlineComponent {
  static componentType = "choice";
  static componentDocs = {
    summary: "A single choice within a `<choiceInput>` or an `<answer>`."
  };
  static rendererType = "containerInline";
  static renderChildren = true;
  static variableForImplicitProp = "submitted";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.credit = {
      createComponentOfType: "number",
      createStateVariable: "credit",
      defaultValue: 0,
      public: true,
      attributesForCreatedComponent: { convertBoolean: "true" },
      description: "Fraction of credit (0 to 1) granted when this choice is selected."
    };
    attributes.feedbackCodes = {
      createComponentOfType: "textList",
      createStateVariable: "feedbackCodes",
      defaultValue: [],
      public: true,
      description: "Codes that select reusable feedback messages when this choice is selected."
    };
    attributes.feedbackText = {
      createComponentOfType: "text",
      createStateVariable: "feedbackText",
      defaultValue: null,
      public: true,
      description: "Custom feedback text shown when this choice is selected."
    };
    attributes.preSelect = {
      createComponentOfType: "boolean",
      createStateVariable: "preSelect",
      defaultValue: false,
      description: "Whether this choice is pre-selected on initial render."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "children",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "disabled",
      newName: "disabledOriginal"
    });
    stateVariableDefinitions.text = {
      description: "The choice's content as a text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["children"],
          variableNames: ["text", "hiddenIgnoreParent"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        const inlineChildren = dependencyValues.inlineChildren.map(
          (child) => {
            if (typeof child !== "object") {
              return child;
            } else {
              child = { ...child };
              child.stateValues = { ...child.stateValues };
              child.stateValues.hidden = child.stateValues.hiddenIgnoreParent;
              delete child.stateValues.hiddenIgnoreParent;
              return child;
            }
          }
        );
        inlineChildren.compositeReplacementRange = dependencyValues.inlineChildren.compositeReplacementRange;
        let text = textFromChildren(inlineChildren);
        return { setValue: { text } };
      }
    };
    stateVariableDefinitions.math = {
      description: "The choice's content interpreted as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["children"],
          variableNames: ["value", "latex"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let math = null;
        if (dependencyValues.inlineChildren.length === 1) {
          let child = dependencyValues.inlineChildren[0];
          if (typeof child === "object") {
            let value = child.stateValues.value;
            if (value instanceof me$3.class) {
              math = value;
            } else if (typeof value === "number") {
              math = me$3.fromAst(value);
            } else if (typeof child.stateValues.latex === "string") {
              try {
                math = me$3.fromLatex(child.stateValues.latex);
              } catch (e3) {
              }
            }
          }
        }
        return { setValue: { math } };
      }
    };
    stateVariableDefinitions.selected = {
      description: "Whether this choice is currently selected.",
      defaultValue: false,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        countAmongSiblings: {
          dependencyType: "countAmongSiblings",
          sameType: true
        },
        childIndicesSelected: {
          dependencyType: "parentStateVariable",
          parentComponentType: "choiceInput",
          variableName: "childIndicesSelected"
        },
        // Note: existence of primary shadow means that the choice is inside a shuffle or sort
        // and the replacement from the shuffle/sort is the primary shadow (and the only one visible to parent)
        primaryShadow: {
          dependencyType: "primaryShadow",
          variableNames: ["selected"]
        }
      }),
      definition({ dependencyValues }) {
        let selected;
        if (dependencyValues.childIndicesSelected) {
          selected = dependencyValues.childIndicesSelected.includes(
            dependencyValues.countAmongSiblings
          );
        } else if (dependencyValues.primaryShadow) {
          selected = dependencyValues.primaryShadow.stateValues.selected;
        } else {
          selected = false;
        }
        return { setValue: { selected } };
      }
    };
    stateVariableDefinitions.submitted = {
      description: "Whether this choice was selected at the most recent submission.",
      defaultValue: false,
      hasEssential: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      doNotShadowEssential: true,
      additionalStateVariablesDefined: [
        {
          variableName: "hasBeenSubmitted",
          defaultValue: false,
          hasEssential: true,
          doNotShadowEssential: true
        }
      ],
      returnDependencies: () => ({
        // Note: existence of primary shadow means that the choice is inside a shuffle or sort
        // and the replacement from the shuffle/sort is the primary shadow (and the only one visible to parent)
        primaryShadow: {
          dependencyType: "primaryShadow",
          variableNames: ["submitted", "hasBeenSubmitted"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.primaryShadow) {
          return {
            setValue: {
              submitted: dependencyValues.primaryShadow.stateValues.submitted,
              hasBeenSubmitted: dependencyValues.primaryShadow.stateValues.hasBeenSubmitted
            }
          };
        } else
          return {
            useEssentialOrDefaultValue: {
              submitted: true,
              hasBeenSubmitted: true
            }
          };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.primaryShadow) {
          return { success: false };
        } else {
          const instructions = [
            {
              setEssentialValue: "submitted",
              value: desiredStateVariableValues.submitted
            }
          ];
          if (desiredStateVariableValues.submitted) {
            instructions.push({
              setEssentialValue: "hasBeenSubmitted",
              value: desiredStateVariableValues.submitted
            });
          }
          return {
            success: true,
            instructions
          };
        }
      }
    };
    stateVariableDefinitions.disabled = {
      description: "Whether this choice is disabled (cannot be selected).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        disabledOriginal: {
          dependencyType: "stateVariable",
          variableName: "disabledOriginal"
        },
        hasBeenSubmitted: {
          dependencyType: "stateVariable",
          variableName: "hasBeenSubmitted"
        },
        credit: {
          dependencyType: "stateVariable",
          variableName: "credit"
        },
        disableWrongChoices: {
          dependencyType: "parentStateVariable",
          parentComponentType: "choiceInput",
          variableName: "disableWrongChoices"
        },
        selectMultiple: {
          dependencyType: "parentStateVariable",
          parentComponentType: "choiceInput",
          variableName: "selectMultiple"
        }
      }),
      definition({ dependencyValues }) {
        const disabled = dependencyValues.disabledOriginal || dependencyValues.disableWrongChoices && dependencyValues.hasBeenSubmitted && dependencyValues.credit < 1 && !dependencyValues.selectMultiple;
        return { setValue: { disabled } };
      }
    };
    stateVariableDefinitions.feedbacks = {
      description: "Feedback messages shown when this choice is selected.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "feedbacktext"
      },
      // isArray: true,
      // entireArrayAtOnce: true,
      // entryPrefixes: ['feedback'],
      returnDependencies: () => ({
        feedbackText: {
          dependencyType: "stateVariable",
          variableName: "feedbackText"
        },
        feedbackCodes: {
          dependencyType: "stateVariable",
          variableName: "feedbackCodes"
        },
        feedbackDefinitionAncestor: {
          dependencyType: "ancestor",
          variableNames: ["feedbackDefinitions"]
        },
        submitted: {
          dependencyType: "stateVariable",
          variableName: "submitted"
        }
      }),
      definition({ dependencyValues }) {
        if (!dependencyValues.submitted) {
          return { setValue: { feedbacks: [] } };
        }
        let feedbacks = [];
        let feedbackDefinitions = dependencyValues.feedbackDefinitionAncestor.stateValues.feedbackDefinitions;
        for (let feedbackCode of dependencyValues.feedbackCodes) {
          let code = feedbackCode.toLowerCase();
          let feedbackText = feedbackDefinitions[code];
          if (feedbackText) {
            feedbacks.push(feedbackText);
          }
        }
        if (dependencyValues.feedbackText !== null) {
          feedbacks.push(dependencyValues.feedbackText);
        }
        return { setValue: { feedbacks } };
      }
    };
    return stateVariableDefinitions;
  }
  static includeBlankStringChildren = true;
  static adapters = ["text", "math"];
}
class NumberComponent extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveNumber: this.moveNumber.bind(this),
      numberClicked: this.numberClicked.bind(this),
      numberFocused: this.numberFocused.bind(this)
    });
  }
  static componentType = "number";
  static componentDocs = {
    summary: "A numeric floating point value"
  };
  static variableForImplicitProp = "value";
  static implicitPropReturnsSameType = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.renderAsMath = {
      description: "Whether to render the number using math typography.",
      createComponentOfType: "boolean",
      createStateVariable: "renderAsMath",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.convertBoolean = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "convertBoolean",
      defaultValue: false,
      description: "Whether to convert boolean inputs to 1/0 instead of NaN."
    };
    attributes.valueOnNaN = {
      createPrimitiveOfType: "number",
      createStateVariable: "valueOnNaN",
      defaultValue: NaN,
      description: "Numeric value to use when the input cannot be parsed."
    };
    attributes.draggable = {
      description: "Whether the number can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.layer = {
      description: "Z-order layer index when shown on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    sugarInstructions.push({
      childrenRegex: /..+/,
      replacementFunction: ({
        matchedChildren,
        componentAttributes,
        nComponents,
        stateIdInfo
      }) => ({
        success: !componentAttributes.convertBoolean?.value,
        newChildren: [
          {
            type: "serialized",
            componentType: "math",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: matchedChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ],
        nComponents
      })
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "strings",
        componentTypes: ["string"]
      },
      {
        group: "numbers",
        componentTypes: ["number"]
      },
      {
        group: "maths",
        componentTypes: ["math"]
      },
      {
        group: "texts",
        componentTypes: ["text"]
      },
      {
        group: "booleans",
        componentTypes: ["boolean"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({
      childGroupsIfSingleMatch: ["maths", "numbers"],
      childGroupsToStopSingleMatch: ["strings", "texts", "booleans"]
    });
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.inUnorderedList = {
      returnDependencies: () => ({
        sourceCompositeUnordered: {
          dependencyType: "sourceCompositeStateVariable",
          variableName: "unordered",
          skipCopies: true
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.sourceCompositeUnordered !== null && !usedDefault.sourceCompositeUnordered) {
          return {
            setValue: {
              inUnorderedList: Boolean(
                dependencyValues.sourceCompositeUnordered
              )
            }
          };
        } else {
          return {
            setValue: {
              inUnorderedList: false
            }
          };
        }
      }
    };
    stateVariableDefinitions.singleNumberOrStringChild = {
      additionalStateVariablesDefined: ["singleMathChild"],
      returnDependencies: () => ({
        numberChildren: {
          dependencyType: "child",
          childGroups: ["numbers"]
        },
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"]
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"]
        },
        booleanChildren: {
          dependencyType: "child",
          childGroups: ["booleans"]
        },
        textChildren: {
          dependencyType: "child",
          childGroups: ["texts"]
        },
        convertBoolean: {
          dependencyType: "stateVariable",
          variableName: "convertBoolean"
        }
      }),
      definition({ dependencyValues }) {
        let nNumberStrings = dependencyValues.numberChildren.length + dependencyValues.stringChildren.length;
        let nMaths = dependencyValues.mathChildren.length;
        let nOthers = dependencyValues.booleanChildren.length;
        if (dependencyValues.convertBoolean) {
          nOthers += dependencyValues.textChildren.length;
        } else {
          nNumberStrings += dependencyValues.textChildren.length;
        }
        let singleNumberOrStringChild = nNumberStrings <= 1 && nMaths + nOthers === 0;
        let singleMathChild = nMaths === 1 && nNumberStrings + nOthers === 0;
        return {
          setValue: { singleNumberOrStringChild, singleMathChild }
        };
      }
    };
    stateVariableDefinitions.parsedExpression = {
      additionalStateVariablesDefined: ["codePre"],
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          childGroups: [
            "strings",
            "numbers",
            "maths",
            "texts",
            "booleans"
          ]
        },
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"],
          variableNames: ["value"]
        },
        convertBoolean: {
          dependencyType: "stateVariable",
          variableName: "convertBoolean"
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        if (!dependencyValues.convertBoolean) {
          dependencyValues = { ...dependencyValues };
          dependencyValues.allChildren = dependencyValues.allChildren.map((child) => {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "text"
            })) {
              child = { componentType: "number" };
            }
            return child;
          });
        }
        return buildParsedExpression({
          dependencyValues,
          componentInfoObjects
        });
      }
    };
    stateVariableDefinitions.mathChildrenByCode = {
      additionalStateVariablesDefined: [
        "textChildrenByCode",
        "numberChildrenByCode",
        "booleanChildrenByCode"
      ],
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          childGroups: [
            "strings",
            "numbers",
            "maths",
            "texts",
            "booleans"
          ],
          variableNames: [
            "value",
            "texts",
            "maths",
            "booleans",
            "number"
          ],
          variablesOptional: true
        },
        codePre: {
          dependencyType: "stateVariable",
          variableName: "codePre"
        },
        convertBoolean: {
          dependencyType: "stateVariable",
          variableName: "convertBoolean"
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        let mathChildrenByCode = {};
        let numberChildrenByCode = {};
        let textChildrenByCode = {};
        let booleanChildrenByCode = {};
        let subnum = 0;
        let codePre = dependencyValues.codePre;
        for (let child of dependencyValues.allChildren) {
          if (typeof child !== "string") {
            let code = codePre + subnum;
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "math"
            })) {
              mathChildrenByCode[code] = child;
            } else if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "number"
            })) {
              numberChildrenByCode[code] = child;
            } else if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "text"
            })) {
              if (dependencyValues.convertBoolean) {
                textChildrenByCode[code] = child;
              } else {
                child = {
                  componentType: "number",
                  stateValues: {
                    value: child.stateValues.number
                  }
                };
                numberChildrenByCode[code] = child;
              }
            } else {
              booleanChildrenByCode[code] = child;
            }
            subnum += 1;
          }
        }
        return {
          setValue: {
            mathChildrenByCode,
            numberChildrenByCode,
            textChildrenByCode,
            booleanChildrenByCode
          }
        };
      }
    };
    stateVariableDefinitions.value = {
      public: true,
      description: "The numeric value.",
      shadowingInstructions: {
        createComponentOfType: this.componentType,
        // the reason we create a attribute component from the state variable,
        // rather than just shadowing the attribute,
        // is that a sequence creates a number where it sets fixed directly in the state
        addAttributeComponentsShadowingStateVariables: {
          fixed: {
            stateVariableToShadow: "fixed"
          },
          ...returnNumberDisplayAttributeComponentShadowing()
        }
      },
      hasEssential: true,
      defaultValue: NaN,
      stateVariablesDeterminingDependencies: [
        "singleNumberOrStringChild"
      ],
      returnDependencies({ stateValues }) {
        if (stateValues.singleNumberOrStringChild) {
          return {
            singleNumberOrStringChild: {
              dependencyType: "stateVariable",
              variableName: "singleNumberOrStringChild"
            },
            convertBoolean: {
              dependencyType: "stateVariable",
              variableName: "convertBoolean"
            },
            numberChild: {
              dependencyType: "child",
              childGroups: ["numbers"],
              variableNames: ["value"]
            },
            textChild: {
              dependencyType: "child",
              childGroups: ["texts"],
              variableNames: ["number"]
            },
            stringChild: {
              dependencyType: "child",
              childGroups: ["strings"],
              variableNames: ["value"]
            },
            valueOnNaN: {
              dependencyType: "stateVariable",
              variableName: "valueOnNaN"
            }
          };
        } else {
          return {
            singleNumberOrStringChild: {
              dependencyType: "stateVariable",
              variableName: "singleNumberOrStringChild"
            },
            singleMathChild: {
              dependencyType: "stateVariable",
              variableName: "singleMathChild"
            },
            convertBoolean: {
              dependencyType: "stateVariable",
              variableName: "convertBoolean"
            },
            parsedExpression: {
              dependencyType: "stateVariable",
              variableName: "parsedExpression"
            },
            allChildren: {
              dependencyType: "child",
              childGroups: [
                "strings",
                "numbers",
                "maths",
                "texts",
                "booleans"
              ],
              variableNames: [
                "value",
                "texts",
                "maths",
                "unordered"
              ],
              variablesOptional: true
            },
            booleanChildrenByCode: {
              dependencyType: "stateVariable",
              variableName: "booleanChildrenByCode"
            },
            textChildrenByCode: {
              dependencyType: "stateVariable",
              variableName: "textChildrenByCode"
            },
            mathChildrenByCode: {
              dependencyType: "stateVariable",
              variableName: "mathChildrenByCode"
            },
            numberChildrenByCode: {
              dependencyType: "stateVariable",
              variableName: "numberChildrenByCode"
            },
            valueOnNaN: {
              dependencyType: "stateVariable",
              variableName: "valueOnNaN"
            }
          };
        }
      },
      definition({ dependencyValues, componentInfoObjects }) {
        if (dependencyValues.singleNumberOrStringChild) {
          if (dependencyValues.numberChild.length + dependencyValues.textChild.length === 0) {
            if (dependencyValues.stringChild.length === 0) {
              return {
                useEssentialOrDefaultValue: {
                  value: {
                    defaultValue: dependencyValues.valueOnNaN
                  }
                }
              };
            }
            let number = Number(
              dependencyValues.stringChild[0] || void 0
            );
            if (Number.isNaN(number)) {
              try {
                number = me$3.fromAst(
                  textToAst.convert(
                    dependencyValues.stringChild[0]
                  )
                ).evaluate_to_constant();
                if (typeof number === "boolean") {
                  if (dependencyValues.convertBoolean) {
                    number = number ? 1 : 0;
                  } else {
                    number = dependencyValues.valueOnNaN;
                  }
                } else if (Number.isNaN(number)) {
                  if (dependencyValues.convertBoolean) {
                    let parsedExpression = buildParsedExpression({
                      dependencyValues: {
                        stringChildren: dependencyValues.stringChild,
                        allChildren: dependencyValues.stringChild
                      },
                      componentInfoObjects
                    }).setValue.parsedExpression;
                    number = evaluateLogic({
                      logicTree: parsedExpression.tree,
                      dependencyValues: {
                        booleanChildrenByCode: {},
                        booleanListChildrenByCode: {},
                        textChildrenByCode: {},
                        textListChildrenByCode: {},
                        mathChildrenByCode: {},
                        mathListChildrenByCode: {},
                        numberChildrenByCode: {},
                        numberListChildrenByCode: {},
                        otherChildrenByCode: {}
                      },
                      valueOnInvalid: dependencyValues.valueOnNaN
                    });
                  } else {
                    number = dependencyValues.valueOnNaN;
                  }
                } else if (number?.re === Infinity || number?.re === -Infinity || number?.im === Infinity || number?.im === -Infinity) {
                  number = numberToMathExpression(
                    number
                  ).evaluate_to_constant();
                } else if (number?.im === 0) {
                  number = number.re;
                }
              } catch (e3) {
                number = dependencyValues.valueOnNaN;
              }
            }
            return { setValue: { value: number } };
          } else {
            let number = dependencyValues.numberChild.length === 1 ? dependencyValues.numberChild[0].stateValues.value : dependencyValues.textChild[0].stateValues.number;
            if (Number.isNaN(number)) {
              number = dependencyValues.valueOnNaN;
            }
            return { setValue: { value: number } };
          }
        } else {
          if (dependencyValues.parsedExpression === null) {
            return {
              setValue: { value: dependencyValues.valueOnNaN }
            };
          }
          if (Object.keys(dependencyValues.textChildrenByCode).length === 0 && Object.keys(dependencyValues.booleanChildrenByCode).length === 0) {
            let replaceMath = function(tree) {
              if (typeof tree === "string") {
                let child = dependencyValues.mathChildrenByCode[tree];
                if (child !== void 0) {
                  return child.stateValues.value.tree;
                }
                child = dependencyValues.numberChildrenByCode[tree];
                if (child !== void 0) {
                  return numberToMathExpression(
                    child.stateValues.value
                  ).tree;
                }
                return tree;
              }
              if (!Array.isArray(tree)) {
                return tree;
              }
              return [tree[0], ...tree.slice(1).map(replaceMath)];
            };
            let number;
            try {
              number = me$3.fromAst(
                replaceMath(
                  dependencyValues.parsedExpression.tree
                )
              ).evaluate_to_constant();
            } catch (e3) {
              number = dependencyValues.valueOnNaN;
            }
            if (!Number.isNaN(number) && (typeof number === "number" || typeof number?.re === "number" && typeof number?.im === "number")) {
              return { setValue: { value: number } };
            }
          }
          if (!dependencyValues.convertBoolean) {
            return {
              setValue: { value: dependencyValues.valueOnNaN }
            };
          }
          dependencyValues = Object.assign({}, dependencyValues);
          dependencyValues.mathListChildrenByCode = {};
          dependencyValues.numberListChildrenByCode = {};
          dependencyValues.textListChildrenByCode = {};
          dependencyValues.booleanListChildrenByCode = {};
          dependencyValues.otherChildrenByCode = {};
          let fractionSatisfied = evaluateLogic({
            logicTree: dependencyValues.parsedExpression.tree,
            dependencyValues,
            valueOnInvalid: dependencyValues.valueOnNaN
          });
          return { setValue: { value: fractionSatisfied } };
        }
      },
      set: function(value) {
        if (value === null) {
          return NaN;
        }
        if (typeof value === "number" || typeof value?.re === "number" && typeof value?.im === "number") {
          return value;
        }
        let number = Number(value);
        if (Number.isNaN(number)) {
          try {
            number = me$3.fromAst(textToAst.convert(value)).evaluate_to_constant();
          } catch (e3) {
            number = NaN;
          }
        }
        return number;
      },
      inverseDefinition: async function({
        desiredStateVariableValues,
        dependencyValues,
        stateValues,
        overrideFixed
      }) {
        if (!await stateValues.canBeModified && !overrideFixed) {
          return { success: false };
        }
        let desiredValue = desiredStateVariableValues.value;
        if (desiredValue instanceof me$3.class) {
          desiredValue = desiredValue.evaluate_to_constant();
          if (Number.isNaN(desiredValue) || !(typeof desiredValue === "number" || typeof desiredValue?.re === "number" && typeof desiredValue?.im === "number")) {
            desiredValue = dependencyValues.valueOnNaN;
          }
        } else {
          if (Number.isNaN(desiredValue) || !(typeof desiredValue === "number" || typeof desiredValue?.re === "number" && typeof desiredValue?.im === "number")) {
            desiredValue = Number(desiredValue);
            if (Number.isNaN(desiredValue)) {
              desiredValue = dependencyValues.valueOnNaN;
            }
          }
        }
        if (!dependencyValues.singleNumberOrStringChild) {
          if (dependencyValues.singleMathChild) {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "allChildren",
                  desiredValue: numberToMathExpression(desiredValue),
                  childIndex: 0,
                  variableIndex: 0
                }
              ]
            };
          } else {
            return { success: false };
          }
        }
        let instructions;
        if (dependencyValues.numberChild.length === 0) {
          if (dependencyValues.stringChild.length === 0) {
            instructions = [
              {
                setEssentialValue: "value",
                value: numberToMathExpression(
                  desiredValue
                ).evaluate_to_constant()
                // to normalize form
              }
            ];
          } else {
            instructions = [
              {
                setDependency: "stringChild",
                desiredValue: numberToMathExpression(
                  desiredValue
                ).toString(),
                childIndex: 0,
                variableIndex: 0
              }
            ];
          }
        } else {
          instructions = [
            {
              setDependency: "numberChild",
              desiredValue,
              childIndex: 0,
              variableIndex: 0
            }
          ];
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.valueForDisplay = {
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        }
      }),
      definition: function({ dependencyValues }) {
        let rounded = roundForDisplay({
          value: numberToMathExpression(dependencyValues.value),
          dependencyValues
        }).evaluate_to_constant();
        return {
          setValue: {
            valueForDisplay: rounded
          }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The number rendered as plain text.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        value: {
          dependencyType: "stateVariable",
          variableName: "value",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition: function({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        return {
          setValue: {
            text: numberToMathExpression(
              dependencyValues.valueForDisplay
            ).toString(params)
          }
        };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        stateValues
      }) {
        let desiredNumber = Number(desiredStateVariableValues.text);
        if (Number.isFinite(desiredNumber)) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "value",
                desiredValue: desiredNumber
              }
            ]
          };
        } else {
          let fromText = textToMathFactory({
            parseScientificNotation: false
          });
          let expr;
          try {
            expr = fromText(desiredStateVariableValues.text);
          } catch (e3) {
            return { success: false };
          }
          desiredNumber = expr.evaluate_to_constant();
          if (Number.isFinite(desiredNumber)) {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "value",
                  desiredValue: desiredNumber
                }
              ]
            };
          } else {
            return { success: false };
          }
        }
      }
    };
    stateVariableDefinitions.math = mathStateVariableFromNumberStateVariable({
      numberVariableName: "value",
      mathVariableName: "math",
      isPublic: true
    });
    stateVariableDefinitions.math.description = "The number's value as a math expression.";
    stateVariableDefinitions.math.shadowingInstructions.addAttributeComponentsShadowingStateVariables = returnNumberDisplayAttributeComponentShadowing();
    stateVariableDefinitions.latex = {
      description: "The number rendered as LaTeX.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        valueForDisplay: {
          dependencyType: "stateVariable",
          variableName: "valueForDisplay"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        }
      }),
      definition({ dependencyValues }) {
        let params = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        return {
          setValue: {
            latex: numberToMathExpression(
              dependencyValues.valueForDisplay
            ).toLatex(params)
          }
        };
      }
    };
    stateVariableDefinitions.canBeModified = {
      returnDependencies: () => ({
        numberChildModifiable: {
          dependencyType: "child",
          childGroups: ["numbers"],
          variableNames: ["canBeModified"]
        },
        modifyIndirectly: {
          dependencyType: "stateVariable",
          variableName: "modifyIndirectly"
        },
        fixed: {
          dependencyType: "stateVariable",
          variableName: "fixed"
        },
        singleNumberOrStringChild: {
          dependencyType: "stateVariable",
          variableName: "singleNumberOrStringChild"
        },
        singleMathChild: {
          dependencyType: "stateVariable",
          variableName: "singleMathChild"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!dependencyValues.modifyIndirectly || dependencyValues.fixed || !(dependencyValues.singleNumberOrStringChild || dependencyValues.singleMathChild)) {
          return { setValue: { canBeModified: false } };
        }
        if (dependencyValues.numberChildModifiable.length === 1 && !dependencyValues.numberChildModifiable[0].stateValues.canBeModified) {
          return { setValue: { canBeModified: false } };
        }
        return { setValue: { canBeModified: true } };
      }
    };
    return stateVariableDefinitions;
  }
  // returnSerializeInstructions() {
  //   let stringMatches = this.childLogic.returnMatches("atMostOneString");
  //   let skipChildren = stringMatches && stringMatches.length === 1;
  //   if (skipChildren) {
  //     let stateVariables = ["value"];
  //     return { skipChildren, stateVariables };
  //   }
  //   return {};
  // }
  static adapters = [
    {
      stateVariable: "math",
      stateVariablesToShadow: Object.keys(
        returnNumberDisplayStateVariableDefinitions()
      )
    },
    "text"
  ];
  async moveNumber({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  async numberClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async numberFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class Integer extends NumberComponent {
  static componentType = "integer";
  static componentDocs = {
    summary: "An integer value"
  };
  static rendererType = "number";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "value",
      newName: "valuePreRound"
    });
    stateVariableDefinitions.valuePreRound.essentialVarName = "value";
    stateVariableDefinitions.value = {
      description: "The integer value.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        valuePreRound: {
          dependencyType: "stateVariable",
          variableName: "valuePreRound"
        }
      }),
      set: function(value) {
        if (value === null) {
          return NaN;
        }
        let number = Number(value);
        if (Number.isNaN(number)) {
          try {
            number = me$3.fromAst(textToAst.convert(value)).evaluate_to_constant();
            if (number === null) {
              number = NaN;
            }
          } catch (e3) {
            number = NaN;
          }
        }
        return Math.round(number);
      },
      definition({ dependencyValues }) {
        return {
          setValue: {
            value: Math.round(dependencyValues.valuePreRound)
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let desiredValue = desiredStateVariableValues.value;
        if (desiredValue instanceof me$3.class) {
          desiredValue = desiredValue.evaluate_to_constant();
        } else {
          desiredValue = Number(desiredValue);
        }
        desiredValue = Math.round(desiredValue);
        return {
          success: true,
          instructions: [
            {
              setDependency: "valuePreRound",
              desiredValue
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
}
function extractControlDisplaySettings(stateValues) {
  return {
    label: typeof stateValues.label === "string" ? stateValues.label : "",
    labelHasLatex: Boolean(stateValues.labelHasLatex),
    displayDigits: stateValues.displayDigits,
    displayDecimals: stateValues.displayDecimals,
    displaySmallAsZero: stateValues.displaySmallAsZero,
    padZeros: stateValues.padZeros,
    avoidScientificNotation: stateValues.avoidScientificNotation
  };
}
function hasValid2DNumericalVertices(numericalVertices, minVertexCount) {
  return Array.isArray(numericalVertices) && numericalVertices.length >= minVertexCount && numericalVertices.every(
    (vertex) => Array.isArray(vertex) && vertex.length >= 2 && Number.isFinite(Number(vertex[0])) && Number.isFinite(Number(vertex[1]))
  );
}
function calculate2DVerticesCenter(numericalVertices) {
  const center = numericalVertices.reduce(
    (acc, vertex) => {
      acc[0] += Number(vertex[0]);
      acc[1] += Number(vertex[1]);
      return acc;
    },
    [0, 0]
  );
  center[0] /= numericalVertices.length;
  center[1] /= numericalVertices.length;
  if (!Number.isFinite(center[0]) || !Number.isFinite(center[1])) {
    return null;
  }
  return {
    x: center[0],
    y: center[1]
  };
}
function resolveCompositeControlsMode({
  requestedMode,
  draggable,
  verticesDraggable,
  centerMode,
  sizeMode,
  compositeMode
}) {
  if (requestedMode === centerMode) {
    return draggable ? centerMode : null;
  }
  if (requestedMode === sizeMode) {
    return verticesDraggable ? sizeMode : null;
  }
  if (requestedMode !== compositeMode) {
    return requestedMode;
  }
  if (draggable && !verticesDraggable) {
    return centerMode;
  }
  if (verticesDraggable && !draggable) {
    return sizeMode;
  }
  if (!draggable && !verticesDraggable) {
    return null;
  }
  return compositeMode;
}
function extractFiniteControlOrder(stateValues) {
  const controlOrder = Number(stateValues.controlOrder);
  if (!Number.isFinite(controlOrder)) {
    return 0;
  }
  return controlOrder;
}
function buildControlMetadata(stateValues) {
  return {
    controlOrder: extractFiniteControlOrder(stateValues),
    ...extractControlDisplaySettings(stateValues)
  };
}
const GRAPH_CONTROL_COMPONENT_TYPES = [
  "point",
  "circle",
  "triangle",
  "rectangle",
  "regularPolygon",
  "polygon",
  "lineSegment",
  "vector"
];
const GRAPH_CONTROL_VARIABLE_NAMES = [
  "numericalXs",
  "numericalCenter",
  "numericalRadius",
  "numericalVertices",
  "numericalEndpoints",
  "width",
  "height",
  "draggable",
  "verticesDraggable",
  "endpointsDraggable",
  "headDraggable",
  "tailDraggable",
  "fixed",
  "fixLocation",
  "addControls",
  "label",
  "labelHasLatex",
  "displayDigits",
  "displayDecimals",
  "displaySmallAsZero",
  "padZeros",
  "avoidScientificNotation",
  "controlOrder"
];
const GRAPH_CONTROL_DESCENDANT_CONFIGS = [
  {
    componentType: "point",
    controlType: "point",
    buildPayload({ stateValues, componentIdx, number }) {
      const numericalXs = stateValues.numericalXs;
      if (!Array.isArray(numericalXs) || numericalXs.length < 2) {
        return null;
      }
      const x2 = Number(numericalXs[0]);
      const y2 = Number(numericalXs[1]);
      if (!Number.isFinite(x2) || !Number.isFinite(y2)) {
        return null;
      }
      const draggable = stateValues.draggable !== false;
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const addControls = stateValues.addControls;
      if (!draggable || fixed || fixLocation || addControls === "none") {
        return null;
      }
      return {
        controlType: "point",
        componentIdx,
        pointNumber: number,
        x: x2,
        y: y2,
        addControls,
        ...buildControlMetadata(stateValues)
      };
    }
  },
  {
    componentType: "circle",
    controlType: "circle",
    buildPayload({ stateValues, componentIdx, number }) {
      const center = stateValues.numericalCenter;
      const radius = Number(stateValues.numericalRadius);
      if (!Array.isArray(center) || center.length < 2 || !Number.isFinite(center[0]) || !Number.isFinite(center[1]) || !Number.isFinite(radius) || radius < 0) {
        return null;
      }
      const draggable = stateValues.draggable !== false;
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const addControls = stateValues.addControls;
      if (fixed || fixLocation || addControls === "none" || !draggable) {
        return null;
      }
      return {
        controlType: "circle",
        componentIdx,
        circleNumber: number,
        center: {
          x: Number(center[0]),
          y: Number(center[1])
        },
        radius,
        addControls,
        ...buildControlMetadata(stateValues)
      };
    }
  },
  {
    componentType: "triangle",
    controlType: "triangle",
    buildPayload({ stateValues, componentIdx, number }) {
      const numericalVertices = stateValues.numericalVertices;
      if (!hasValid2DNumericalVertices(numericalVertices, 3)) {
        return null;
      }
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const draggable = stateValues.draggable !== false;
      const addControls = stateValues.addControls;
      if (fixed || fixLocation || addControls === "none" || !draggable) {
        return null;
      }
      const center = calculate2DVerticesCenter(numericalVertices);
      if (!center) {
        return null;
      }
      return {
        controlType: "triangle",
        componentIdx,
        triangleNumber: number,
        center,
        addControls,
        ...buildControlMetadata(stateValues)
      };
    }
  },
  {
    componentType: "rectangle",
    controlType: "rectangle",
    buildPayload({ stateValues, componentIdx, number }) {
      const numericalVertices = stateValues.numericalVertices;
      const width = Number(stateValues.width);
      const height = Number(stateValues.height);
      const addControls = stateValues.addControls;
      if (!hasValid2DNumericalVertices(numericalVertices, 4)) {
        return null;
      }
      if (!Number.isFinite(width) || !Number.isFinite(height) || width < 0 || height < 0) {
        return null;
      }
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const draggable = stateValues.draggable !== false;
      const verticesDraggable = stateValues.verticesDraggable !== false;
      if (fixed || fixLocation || addControls === "none") {
        return null;
      }
      const effectiveAddControls = resolveCompositeControlsMode({
        requestedMode: addControls,
        draggable,
        verticesDraggable,
        centerMode: "center",
        sizeMode: "widthandheight",
        compositeMode: "centerwidthandheight"
      });
      if (effectiveAddControls === null) {
        return null;
      }
      const center = calculate2DVerticesCenter(numericalVertices);
      if (!center) {
        return null;
      }
      return {
        controlType: "rectangle",
        componentIdx,
        rectangleNumber: number,
        center,
        width,
        height,
        addControls: effectiveAddControls,
        ...buildControlMetadata(stateValues)
      };
    }
  },
  {
    componentType: "regularPolygon",
    controlType: "regularPolygon",
    buildPayload({ stateValues, componentIdx, number }) {
      const numericalVertices = stateValues.numericalVertices;
      const addControls = stateValues.addControls;
      if (!hasValid2DNumericalVertices(numericalVertices, 3)) {
        return null;
      }
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const draggable = stateValues.draggable !== false;
      const verticesDraggable = stateValues.verticesDraggable !== false;
      if (fixed || fixLocation || addControls === "none") {
        return null;
      }
      const effectiveAddControls = resolveCompositeControlsMode({
        requestedMode: addControls,
        draggable,
        verticesDraggable,
        centerMode: "center",
        sizeMode: "radius",
        compositeMode: "centerandradius"
      });
      if (effectiveAddControls === null) {
        return null;
      }
      const center = calculate2DVerticesCenter(numericalVertices);
      if (!center) {
        return null;
      }
      const firstVertex = numericalVertices[0];
      const radius = Math.sqrt(
        (Number(firstVertex[0]) - center.x) ** 2 + (Number(firstVertex[1]) - center.y) ** 2
      );
      if (!Number.isFinite(radius) || radius < 0) {
        return null;
      }
      return {
        controlType: "regularPolygon",
        componentIdx,
        regularPolygonNumber: number,
        center,
        radius,
        addControls: effectiveAddControls,
        ...buildControlMetadata(stateValues)
      };
    }
  },
  {
    componentType: "polygon",
    controlType: "polygon",
    buildPayload({ stateValues, componentIdx, number }) {
      const numericalVertices = stateValues.numericalVertices;
      const addControls = stateValues.addControls;
      if (!hasValid2DNumericalVertices(numericalVertices, 3)) {
        return null;
      }
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const draggable = stateValues.draggable !== false;
      if (fixed || fixLocation || addControls === "none" || !draggable) {
        return null;
      }
      const center = calculate2DVerticesCenter(numericalVertices);
      if (!center) {
        return null;
      }
      return {
        controlType: "polygon",
        componentIdx,
        polygonNumber: number,
        center,
        addControls,
        ...buildControlMetadata(stateValues)
      };
    }
  },
  {
    componentType: "lineSegment",
    controlType: "lineSegment",
    buildPayload({ stateValues, componentIdx, number }) {
      const numericalEndpoints = stateValues.numericalEndpoints;
      if (!Array.isArray(numericalEndpoints) || numericalEndpoints.length < 2 || !Array.isArray(numericalEndpoints[0]) || !Array.isArray(numericalEndpoints[1]) || numericalEndpoints[0].length < 2 || numericalEndpoints[1].length < 2) {
        return null;
      }
      const endpoint1x = Number(numericalEndpoints[0][0]);
      const endpoint1y = Number(numericalEndpoints[0][1]);
      const endpoint2x = Number(numericalEndpoints[1][0]);
      const endpoint2y = Number(numericalEndpoints[1][1]);
      if (!Number.isFinite(endpoint1x) || !Number.isFinite(endpoint1y) || !Number.isFinite(endpoint2x) || !Number.isFinite(endpoint2y)) {
        return null;
      }
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const endpointsDraggable = stateValues.endpointsDraggable !== false;
      const addControls = stateValues.addControls;
      if (fixed || fixLocation || addControls === "none" || !endpointsDraggable) {
        return null;
      }
      return {
        controlType: "lineSegment",
        componentIdx,
        lineSegmentNumber: number,
        endpoint1: { x: endpoint1x, y: endpoint1y },
        endpoint2: { x: endpoint2x, y: endpoint2y },
        addControls,
        ...buildControlMetadata(stateValues)
      };
    }
  },
  {
    componentType: "vector",
    controlType: "vector",
    buildPayload({ stateValues, componentIdx, number }) {
      const numericalEndpoints = stateValues.numericalEndpoints;
      if (!Array.isArray(numericalEndpoints) || numericalEndpoints.length < 2 || !Array.isArray(numericalEndpoints[0]) || !Array.isArray(numericalEndpoints[1]) || numericalEndpoints[0].length < 2 || numericalEndpoints[1].length < 2) {
        return null;
      }
      const tailX = Number(numericalEndpoints[0][0]);
      const tailY = Number(numericalEndpoints[0][1]);
      const headX = Number(numericalEndpoints[1][0]);
      const headY = Number(numericalEndpoints[1][1]);
      if (!Number.isFinite(tailX) || !Number.isFinite(tailY) || !Number.isFinite(headX) || !Number.isFinite(headY)) {
        return null;
      }
      const fixed = stateValues.fixed === true;
      const fixLocation = stateValues.fixLocation === true;
      const headDraggable = stateValues.headDraggable !== false;
      const tailDraggable = stateValues.tailDraggable !== false;
      if (fixed || fixLocation || stateValues.addControls === "none") {
        return null;
      }
      let effectiveAddControls = stateValues.addControls;
      if (effectiveAddControls === "displacement") {
        if (!headDraggable) {
          return null;
        }
      } else if (effectiveAddControls === "headandtail") {
        if (headDraggable && !tailDraggable) {
          effectiveAddControls = "headonly";
        } else if (tailDraggable && !headDraggable) {
          effectiveAddControls = "tailonly";
        } else if (!headDraggable && !tailDraggable) {
          return null;
        }
      } else if (effectiveAddControls === "headonly") {
        if (!headDraggable) {
          return null;
        }
      } else if (effectiveAddControls === "tailonly") {
        if (!tailDraggable) {
          return null;
        }
      }
      return {
        controlType: "vector",
        componentIdx,
        vectorNumber: number,
        head: {
          x: headX,
          y: headY
        },
        tail: {
          x: tailX,
          y: tailY
        },
        displacement: {
          x: headX - tailX,
          y: headY - tailY
        },
        addControls: effectiveAddControls,
        ...buildControlMetadata(stateValues)
      };
    }
  }
];
function escapeXml(value) {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}
function asFiniteNumber(value) {
  return Number.isFinite(value) ? Number(value) : null;
}
function formatNumber$1(value) {
  const num = asFiniteNumber(value);
  return num === null ? null : `${num}`;
}
function formatPoint(point) {
  if (!Array.isArray(point) || point.length < 2) {
    return null;
  }
  const x2 = formatNumber$1(point[0]);
  const y2 = formatNumber$1(point[1]);
  if (x2 === null || y2 === null) {
    return null;
  }
  return `(${x2},${y2})`;
}
function extractFinitePoint(point) {
  if (!Array.isArray(point) || point.length < 2) {
    return null;
  }
  const x2 = asFiniteNumber(point[0]);
  const y2 = asFiniteNumber(point[1]);
  if (x2 === null || y2 === null) {
    return null;
  }
  return [x2, y2];
}
function extractFinitePointPair(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return null;
  }
  const point1 = extractFinitePoint(points[0]);
  const point2 = extractFinitePoint(points[1]);
  if (point1 === null || point2 === null) {
    return null;
  }
  return [point1, point2];
}
function sanitizeHandle(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function createStableHandle(descendant, index2, usedHandles) {
  const stem = sanitizeHandle(`${descendant.componentType}_${index2}`);
  let handle = stem || `graphical_${index2}`;
  let suffix = 1;
  while (usedHandles.has(handle)) {
    suffix += 1;
    handle = `${stem}_${suffix}`;
  }
  usedHandles.add(handle);
  return handle;
}
function warningMessageForDescendant(descendant) {
  if (descendant?.componentName) {
    return `<${descendant.componentType}> (${descendant.componentName})`;
  }
  return `<${descendant?.componentType ?? "unknown"}>`;
}
function pushWarning({
  diagnostics,
  message,
  position
}) {
  const warning = {
    type: "warning",
    message
  };
  if (position) {
    warning.position = position;
  }
  diagnostics.push(warning);
}
function sortDescendantsByOrder(a2, b2) {
  if (Number.isFinite(a2.componentIdx) && Number.isFinite(b2.componentIdx)) {
    return a2.componentIdx - b2.componentIdx;
  }
  return String(a2.componentName ?? "").localeCompare(
    String(b2.componentName ?? "")
  );
}
function normalizeLineClipInputs({
  point1,
  point2,
  bounds
}) {
  if (!Array.isArray(point1) || !Array.isArray(point2) || point1.length < 2 || point2.length < 2 || !Array.isArray(bounds) || bounds.length < 4) {
    return null;
  }
  const x0 = asFiniteNumber(point1[0]);
  const y0 = asFiniteNumber(point1[1]);
  const x1 = asFiniteNumber(point2[0]);
  const y1 = asFiniteNumber(point2[1]);
  const bxMin = asFiniteNumber(bounds[0]);
  const byMin = asFiniteNumber(bounds[1]);
  const bxMax = asFiniteNumber(bounds[2]);
  const byMax = asFiniteNumber(bounds[3]);
  if (x0 === null || y0 === null || x1 === null || y1 === null || bxMin === null || byMin === null || bxMax === null || byMax === null) {
    return null;
  }
  const dx = x1 - x0;
  const dy = y1 - y0;
  if (dx === 0 && dy === 0) {
    return null;
  }
  return { x0, y0, dx, dy, bxMin, byMin, bxMax, byMax };
}
function clipLineLikeToBounds({
  point1,
  point2,
  bounds,
  tMin,
  tMax
}) {
  const inputs = normalizeLineClipInputs({ point1, point2, bounds });
  if (!inputs) {
    return null;
  }
  const { x0, y0, dx, dy, bxMin, byMin, bxMax, byMax } = inputs;
  let clippedTMin = tMin;
  let clippedTMax = tMax;
  const updateRange = (p0, delta, minBound, maxBound) => {
    if (delta !== 0) {
      let ta2 = (minBound - p0) / delta;
      let tb = (maxBound - p0) / delta;
      if (ta2 > tb) {
        [ta2, tb] = [tb, ta2];
      }
      clippedTMin = Math.max(clippedTMin, ta2);
      clippedTMax = Math.min(clippedTMax, tb);
      return clippedTMin <= clippedTMax;
    }
    return p0 >= minBound && p0 <= maxBound;
  };
  if (!updateRange(x0, dx, bxMin, bxMax)) {
    return null;
  }
  if (!updateRange(y0, dy, byMin, byMax)) {
    return null;
  }
  return [
    [x0 + clippedTMin * dx, y0 + clippedTMin * dy],
    [x0 + clippedTMax * dx, y0 + clippedTMax * dy]
  ];
}
const prefigurePointAlignmentByLabelPosition = {
  upperright: "ne",
  upperleft: "nw",
  lowerright: "se",
  lowerleft: "sw",
  top: "n",
  bottom: "s",
  left: "w",
  right: "e"
};
function normalizeKey(value) {
  return String(value).toLowerCase().replace(/[^a-z0-9]+/g, "");
}
const LABEL_EDGE_PADDING_RATIO = 0.02;
const LINE_LABEL_ENDPOINT_INSET_RATIO = 0.05;
const LINE_LABEL_ENDPOINT_INSET_PIXELS = 12;
const KNOWN_LINE_POSITIONS = /* @__PURE__ */ new Set([
  "left",
  "upperleft",
  "lowerleft",
  "right",
  "upperright",
  "lowerright",
  "center",
  "top",
  "bottom"
]);
function formatLocation(value) {
  const rounded = Number(value.toFixed(6));
  return `${rounded}`;
}
function clamp01(value) {
  return Math.min(1, Math.max(0, value));
}
function getPreferredDirectionForLabelPosition(labelPosition) {
  switch (normalizeKey(labelPosition ?? "")) {
    case "right":
      return [1, 0];
    case "left":
      return [-1, 0];
    case "top":
      return [0, 1];
    case "bottom":
      return [0, -1];
    case "upperright":
      return [1 / Math.SQRT2, 1 / Math.SQRT2];
    case "upperleft":
      return [-1 / Math.SQRT2, 1 / Math.SQRT2];
    case "lowerright":
      return [1 / Math.SQRT2, -1 / Math.SQRT2];
    case "lowerleft":
      return [-1 / Math.SQRT2, -1 / Math.SQRT2];
    default:
      return null;
  }
}
function biasFromPreferredDirection(preferredDirection, ep1, ep2) {
  const dx = ep2[0] - ep1[0];
  const dy = ep2[1] - ep1[1];
  const segmentLength = Math.hypot(dx, dy);
  if (!(segmentLength > 0)) {
    return 0;
  }
  const tx = dx / segmentLength;
  const ty = dy / segmentLength;
  const agreement = tx * preferredDirection[0] + ty * preferredDirection[1];
  return Math.max(-1, Math.min(1, Math.SQRT2 * agreement));
}
function chooseEndpointBiasForLabelPosition(labelPosition, ep1, ep2) {
  const pos = normalizeKey(labelPosition ?? "");
  if (!KNOWN_LINE_POSITIONS.has(pos) || pos === "center") {
    return null;
  }
  const preferredDirection = getPreferredDirectionForLabelPosition(pos);
  if (!preferredDirection) {
    return null;
  }
  return biasFromPreferredDirection(preferredDirection, ep1, ep2);
}
function lineLabelAnchorPoint({
  ep1,
  ep2,
  location
}) {
  return [
    ep1[0] + (ep2[0] - ep1[0]) * location,
    ep1[1] + (ep2[1] - ep1[1]) * location
  ];
}
function getGraphBounds(graphBounds) {
  const bounds = Array.isArray(graphBounds) ? graphBounds : null;
  if (!bounds || bounds.length !== 4) {
    return null;
  }
  const [xMin, yMin, xMax, yMax] = bounds;
  if (!Number.isFinite(xMin) || !Number.isFinite(yMin) || !Number.isFinite(xMax) || !Number.isFinite(yMax) || !(xMax > xMin) || !(yMax > yMin)) {
    return null;
  }
  return { xMin, yMin, xMax, yMax };
}
function getGraphDimensions(graphDimensions) {
  const dims = Array.isArray(graphDimensions) ? graphDimensions : null;
  if (!dims || dims.length !== 2) {
    return null;
  }
  const [width, height] = dims;
  if (!Number.isFinite(width) || !Number.isFinite(height) || !(width > 0) || !(height > 0)) {
    return null;
  }
  return { width, height };
}
function getLineTangent(ep1, ep2) {
  const dx = ep2[0] - ep1[0];
  const dy = ep2[1] - ep1[1];
  const length = Math.hypot(dx, dy);
  if (!(length > 0)) {
    return null;
  }
  return [dx / length, dy / length];
}
function estimateLabelMetrics(labelText, labelHasLatex) {
  const raw = typeof labelText === "string" ? labelText.trim() : "";
  const visibleLength = raw.length;
  let effectiveLength = visibleLength;
  if (labelHasLatex) {
    const latexRuns = (raw.match(/\\\(|\\\)|<m>|<\/m>/g) ?? []).length;
    effectiveLength += latexRuns * 3;
  }
  return {
    width: Math.min(6, Math.max(0.9, effectiveLength * 0.27)),
    height: 1
  };
}
function getLineAlignmentCandidates(labelPosition, location, preferNorth = true) {
  const pos = normalizeKey(labelPosition ?? "");
  if (!pos || !KNOWN_LINE_POSITIONS.has(pos)) {
    return null;
  }
  const h2 = location >= 0.5 ? "w" : "e";
  const oppositeH = h2 === "w" ? "e" : "w";
  const primary = preferNorth ? "n" : "s";
  const secondary = preferNorth ? "s" : "n";
  if (pos === "center") {
    return ["n", "s", `n${h2}`, `s${h2}`, `n${oppositeH}`, `s${oppositeH}`];
  }
  return [
    primary,
    `${primary}${h2}`,
    secondary,
    `${secondary}${h2}`,
    `${primary}${oppositeH}`,
    `${secondary}${oppositeH}`
  ];
}
function getAlignmentExtents(tangent, alignment, metrics) {
  const normal2 = [-tangent[1], tangent[0]];
  const width = metrics?.width ?? 1.2;
  const height = metrics?.height ?? 1;
  let minT = -width / 2;
  let maxT = width / 2;
  let minN = -height / 2;
  let maxN = height / 2;
  switch (alignment) {
    case "n":
      minN = 0;
      maxN = height;
      break;
    case "s":
      minN = -height;
      maxN = 0;
      break;
    case "e":
      minT = 0;
      maxT = width;
      break;
    case "w":
      minT = -width;
      maxT = 0;
      break;
    case "ne":
      minT = 0;
      maxT = width;
      minN = 0;
      maxN = height;
      break;
    case "nw":
      minT = -width;
      maxT = 0;
      minN = 0;
      maxN = height;
      break;
    case "se":
      minT = 0;
      maxT = width;
      minN = -height;
      maxN = 0;
      break;
    case "sw":
      minT = -width;
      maxT = 0;
      minN = -height;
      maxN = 0;
      break;
  }
  const points = [
    [
      tangent[0] * minT + normal2[0] * minN,
      tangent[1] * minT + normal2[1] * minN
    ],
    [
      tangent[0] * minT + normal2[0] * maxN,
      tangent[1] * minT + normal2[1] * maxN
    ],
    [
      tangent[0] * maxT + normal2[0] * minN,
      tangent[1] * maxT + normal2[1] * minN
    ],
    [
      tangent[0] * maxT + normal2[0] * maxN,
      tangent[1] * maxT + normal2[1] * maxN
    ]
  ];
  return {
    minX: Math.min(...points.map((point) => point[0])),
    maxX: Math.max(...points.map((point) => point[0])),
    minY: Math.min(...points.map((point) => point[1])),
    maxY: Math.max(...points.map((point) => point[1]))
  };
}
function computeAlignmentOverflowAt({
  anchor,
  tangent,
  alignment,
  graphBounds,
  labelMetrics
}) {
  const bounds = getGraphBounds(graphBounds);
  if (!bounds) {
    return { overflowAmount: 0 };
  }
  const { xMin, yMin, xMax, yMax } = bounds;
  const width = xMax - xMin;
  const height = yMax - yMin;
  const edgePaddingX = width * LABEL_EDGE_PADDING_RATIO;
  const edgePaddingY = height * LABEL_EDGE_PADDING_RATIO;
  const extents = getAlignmentExtents(tangent, alignment, labelMetrics);
  const projectedLeft = anchor[0] + extents.minX;
  const projectedRight = anchor[0] + extents.maxX;
  const projectedBottom = anchor[1] + extents.minY;
  const projectedTop = anchor[1] + extents.maxY;
  const overflowLeft = Math.max(0, xMin + edgePaddingX - projectedLeft);
  const overflowRight = Math.max(0, projectedRight - (xMax - edgePaddingX));
  const overflowBottom = Math.max(0, yMin + edgePaddingY - projectedBottom);
  const overflowTop = Math.max(0, projectedTop - (yMax - edgePaddingY));
  const overflowAmount = overflowLeft + overflowRight + overflowBottom + overflowTop;
  return { overflowAmount };
}
function evaluateLineAlignmentOverflow({
  alignment,
  ep1,
  ep2,
  location,
  graphBounds,
  labelMetrics
}) {
  const tangent = getLineTangent(ep1, ep2);
  if (!tangent) {
    return { overflowAmount: 0 };
  }
  const anchor = lineLabelAnchorPoint({ ep1, ep2, location });
  return computeAlignmentOverflowAt({
    anchor,
    tangent,
    alignment,
    graphBounds,
    labelMetrics
  });
}
function getLineLabelAlignment({
  labelPosition,
  location,
  ep1,
  ep2,
  graphBounds,
  labelText,
  labelHasLatex
}) {
  const tangent = getLineTangent(ep1, ep2);
  const preferredDirection = getPreferredDirectionForLabelPosition(labelPosition);
  const preferNorth = preferredDirection && tangent ? -tangent[1] * preferredDirection[0] + tangent[0] * preferredDirection[1] >= 0 : true;
  const candidates = getLineAlignmentCandidates(
    labelPosition,
    location,
    preferNorth
  );
  if (!candidates?.length) {
    return null;
  }
  const labelMetrics = estimateLabelMetrics(labelText, labelHasLatex);
  let bestAlignment = candidates[0];
  let minimumOverflow = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const result = evaluateLineAlignmentOverflow({
      alignment: candidate,
      ep1,
      ep2,
      location,
      graphBounds,
      labelMetrics
    });
    if (result.overflowAmount === 0) {
      return candidate;
    }
    if (result.overflowAmount < minimumOverflow) {
      bestAlignment = candidate;
      minimumOverflow = result.overflowAmount;
    }
  }
  return bestAlignment;
}
function orientEndpointsForLineLabel(p1Raw, p2Raw) {
  if (p1Raw[0] > p2Raw[0] || p1Raw[0] === p2Raw[0] && p1Raw[1] > p2Raw[1]) {
    return [p2Raw, p1Raw];
  }
  return [p1Raw, p2Raw];
}
function labelMarkup({
  label,
  labelHasLatex
}) {
  if (typeof label !== "string") {
    return null;
  }
  const text = label.trim();
  if (!text) {
    return null;
  }
  if (labelHasLatex) {
    const openDelimiters = (text.match(/\\\(/g) ?? []).length;
    const closeDelimiters = (text.match(/\\\)/g) ?? []).length;
    if (openDelimiters > 0 && openDelimiters === closeDelimiters) {
      return escapeXml(text).replace(/\\\(/g, "<m>").replace(/\\\)/g, "</m>");
    }
    return `<m>${escapeXml(text)}</m>`;
  }
  return escapeXml(text);
}
const POINT_ALIGNMENT_CANDIDATES = {
  n: ["n", "nw", "ne", "w", "e", "sw", "se", "s"],
  ne: ["ne", "n", "e", "nw", "se", "w", "s", "sw"],
  e: ["e", "ne", "se", "n", "s", "nw", "sw", "w"],
  se: ["se", "e", "s", "ne", "sw", "n", "w", "nw"],
  s: ["s", "se", "sw", "e", "w", "ne", "nw", "n"],
  sw: ["sw", "s", "w", "se", "nw", "e", "n", "ne"],
  w: ["w", "nw", "sw", "n", "s", "ne", "se", "e"],
  nw: ["nw", "n", "w", "ne", "sw", "e", "s", "se"]
};
function getPointAlignmentCandidates(labelPosition) {
  const key = normalizeKey(labelPosition ?? "");
  const primary = prefigurePointAlignmentByLabelPosition[key];
  if (!primary) return null;
  return POINT_ALIGNMENT_CANDIDATES[primary] ?? null;
}
function getPointLabelAlignment({
  labelPosition,
  point,
  graphBounds,
  labelText,
  labelHasLatex
}) {
  const candidates = getPointAlignmentCandidates(labelPosition);
  if (!candidates?.length) return null;
  const tangent = [1, 0];
  const labelMetrics = estimateLabelMetrics(labelText, labelHasLatex);
  let bestAlignment = candidates[0];
  let minimumOverflow = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) {
    const { overflowAmount } = computeAlignmentOverflowAt({
      anchor: point,
      tangent,
      alignment: candidate,
      graphBounds,
      labelMetrics
    });
    if (overflowAmount === 0) {
      return candidate;
    }
    if (overflowAmount < minimumOverflow) {
      bestAlignment = candidate;
      minimumOverflow = overflowAmount;
    }
  }
  return bestAlignment;
}
function pointLabelAttributes({
  stateValues,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const label = labelMarkup({
    label: stateValues?.label,
    labelHasLatex: stateValues?.labelHasLatex
  });
  if (!label) {
    return null;
  }
  const attrs = [];
  const rawPosition = stateValues?.labelPosition;
  if (rawPosition) {
    const rawXs = stateValues?.numericalXs;
    const pointCoord = Array.isArray(rawXs) && rawXs.length >= 2 && Number.isFinite(rawXs[0]) && Number.isFinite(rawXs[1]) ? [Number(rawXs[0]), Number(rawXs[1])] : null;
    const directAlignment = prefigurePointAlignmentByLabelPosition[normalizeKey(rawPosition)];
    if (directAlignment) {
      const alignment = pointCoord !== null && stateValues?.graphBounds ? getPointLabelAlignment({
        labelPosition: rawPosition,
        point: pointCoord,
        graphBounds: stateValues.graphBounds,
        labelText: stateValues?.label,
        labelHasLatex: stateValues?.labelHasLatex
      }) ?? directAlignment : directAlignment;
      attrs.push(`alignment="${escapeXml(alignment)}"`);
    } else {
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: unsupported labelPosition '${rawPosition}' for point label; default PreFigure alignment used.`,
        position: warningPosition
      });
    }
  }
  return { attrs, label };
}
function locationFromBiasWithInsetRatio({
  bias,
  insetRatio
}) {
  const clampedBias = Math.max(-1, Math.min(1, bias));
  const clampedInset = clamp01(Math.min(0.5, insetRatio));
  const span = Math.max(0, 0.5 - clampedInset);
  return clamp01(0.5 + clampedBias * span);
}
function locationFromBiasWithAbsoluteEndpointOffset({
  bias,
  ep1,
  ep2,
  graphBounds,
  graphDimensions
}) {
  const bounds = getGraphBounds(graphBounds);
  const dims = getGraphDimensions(graphDimensions);
  const dx = ep2[0] - ep1[0];
  const dy = ep2[1] - ep1[1];
  const segmentLength = Math.hypot(dx, dy);
  const graphWidth = bounds ? bounds.xMax - bounds.xMin : 0;
  const graphHeight = bounds ? bounds.yMax - bounds.yMin : 0;
  const shouldUseRelativeInsetFallback = !bounds || !dims || !(segmentLength > 0) || !(graphWidth > 0) || !(graphHeight > 0);
  if (shouldUseRelativeInsetFallback) {
    return locationFromBiasWithInsetRatio({
      bias,
      insetRatio: LINE_LABEL_ENDPOINT_INSET_RATIO
    });
  }
  const tx = dx / segmentLength;
  const ty = dy / segmentLength;
  const unitsPerPixelAlongLine = Math.hypot(
    tx * (graphWidth / dims.width),
    ty * (graphHeight / dims.height)
  );
  const absoluteOffset = LINE_LABEL_ENDPOINT_INSET_PIXELS * unitsPerPixelAlongLine;
  const insetRatio = Math.min(0.45, clamp01(absoluteOffset / segmentLength));
  const location = locationFromBiasWithInsetRatio({
    bias,
    insetRatio
  });
  return location;
}
function lineLabelLocationValue(labelPosition, ep1, ep2, options = {}) {
  const endpointBias = chooseEndpointBiasForLabelPosition(
    labelPosition,
    ep1,
    ep2
  );
  if (endpointBias === null) {
    return null;
  }
  if (options.absoluteEndpointOffset) {
    return locationFromBiasWithAbsoluteEndpointOffset({
      bias: endpointBias,
      ep1,
      ep2,
      graphBounds: options.graphBounds,
      graphDimensions: options.graphDimensions
    });
  }
  return locationFromBiasWithInsetRatio({
    bias: endpointBias,
    insetRatio: LINE_LABEL_ENDPOINT_INSET_RATIO
  });
}
function getLabelForLine({
  stateValues,
  ep1,
  ep2,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const label = labelMarkup({
    label: stateValues?.label,
    labelHasLatex: stateValues?.labelHasLatex
  });
  if (!label) {
    return {
      labelAttrs: [],
      label: null
    };
  }
  const labelAttrs = [];
  const rawPosition = stateValues?.labelPosition;
  const normalizedPosition = normalizeKey(rawPosition ?? "");
  const overrideLocRaw = stateValues?.lineLabelLocationOverride;
  const overrideLoc = Number(overrideLocRaw);
  const hasOverrideLoc = Number.isFinite(overrideLoc);
  const baseLoc = hasOverrideLoc ? clamp01(overrideLoc) : lineLabelLocationValue(rawPosition, ep1, ep2, {
    absoluteEndpointOffset: stateValues?.lineLabelAbsoluteEndpointOffset === true,
    graphBounds: stateValues?.graphBounds,
    graphDimensions: stateValues?.graphDimensions
  });
  let location = 0.5;
  if (baseLoc !== null && Number.isFinite(baseLoc)) {
    location = baseLoc;
    labelAttrs.push(
      `label-location="${escapeXml(formatLocation(location))}"`
    );
  }
  const alignmentLocationRaw = stateValues?.lineLabelAlignmentLocationOverride;
  const alignmentLocation = Number.isFinite(Number(alignmentLocationRaw)) ? Number(alignmentLocationRaw) : location;
  if (rawPosition) {
    const alignment = getLineLabelAlignment({
      labelPosition: rawPosition,
      location: alignmentLocation,
      ep1,
      ep2,
      graphBounds: stateValues?.graphBounds,
      labelText: stateValues?.label,
      labelHasLatex: stateValues?.labelHasLatex
    });
    if (alignment) {
      labelAttrs.push(`alignment="${escapeXml(alignment)}"`);
    } else if (!KNOWN_LINE_POSITIONS.has(normalizedPosition)) {
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: unsupported labelPosition '${rawPosition}' for line-family label; default PreFigure alignment used.`,
        position: warningPosition
      });
    }
  }
  return { labelAttrs, label };
}
const prefigureFillPatternByFillStyle = {
  horizontal: "horizontal",
  vertical: "vertical",
  diagonal: "diagonal",
  backdiagonal: "backdiagonal",
  dots: "dot",
  diamonds: "diamond"
};
const prefigureDashByLineStyle = {
  solid: null,
  dashed: "9 9",
  dotted: "4 4"
};
const prefigurePointStyleByMarkerStyle = {
  circle: "circle",
  square: "box",
  box: "box",
  diamond: "diamond",
  cross: "cross",
  plus: "plus",
  "double-circle": "double-circle"
};
function resolveSelectedStyleForTheme(selectedStyle, darkMode) {
  if (!selectedStyle || !darkMode) {
    return selectedStyle;
  }
  const pickDark = (darkKey, lightValue) => {
    const darkValue = selectedStyle[darkKey];
    return typeof darkValue === "string" ? darkValue : lightValue;
  };
  return {
    ...selectedStyle,
    lineColor: pickDark("lineColorDarkMode", selectedStyle.lineColor),
    lineColorWord: pickDark(
      "lineColorWordDarkMode",
      selectedStyle.lineColorWord
    ),
    fillColor: pickDark("fillColorDarkMode", selectedStyle.fillColor),
    fillColorWord: pickDark(
      "fillColorWordDarkMode",
      selectedStyle.fillColorWord
    ),
    markerColor: pickDark("markerColorDarkMode", selectedStyle.markerColor),
    markerColorWord: pickDark(
      "markerColorWordDarkMode",
      selectedStyle.markerColorWord
    )
  };
}
function styleAttributes({
  selectedStyle,
  diagnostics,
  warningPrefix,
  warningPosition,
  includeFill = true
}) {
  const attrs = [];
  const stroke = selectedStyle?.lineColor ?? selectedStyle?.lineColorWord;
  if (stroke) {
    attrs.push(`stroke="${escapeXml(stroke)}"`);
  }
  const thickness = formatNumber$1(selectedStyle?.lineWidth);
  if (thickness !== null) {
    attrs.push(`thickness="${escapeXml(thickness)}"`);
  }
  if (includeFill) {
    const fillStyle = selectedStyle?.fillStyle;
    const prefigurePattern = fillStyle && fillStyle !== "solid" ? prefigureFillPatternByFillStyle[fillStyle] : void 0;
    const fill = selectedStyle?.fillColor ?? selectedStyle?.fillColorWord;
    if (prefigurePattern !== void 0) {
      attrs.push(`fill-pattern="${escapeXml(prefigurePattern)}"`);
      if (fill) {
        attrs.push(`fill="${escapeXml(fill)}"`);
      }
      const fillPatternOpacity = formatNumber$1(
        selectedStyle?.fillPatternOpacity
      );
      if (fillPatternOpacity !== null) {
        attrs.push(`fill-opacity="${escapeXml(fillPatternOpacity)}"`);
      }
    } else {
      if (fill) {
        attrs.push(`fill="${escapeXml(fill)}"`);
      }
      const fillOpacity = formatNumber$1(selectedStyle?.fillOpacity);
      if (fillOpacity !== null) {
        attrs.push(`fill-opacity="${escapeXml(fillOpacity)}"`);
      }
      if (fillStyle && fillStyle !== "solid") {
        pushWarning({
          diagnostics,
          message: `${warningPrefix}: fill style '${fillStyle}' is unsupported by PreFigure; falling back to a solid fill.`,
          position: warningPosition
        });
      }
    }
  }
  const lineOpacity = formatNumber$1(selectedStyle?.lineOpacity);
  if (lineOpacity !== null) {
    attrs.push(`stroke-opacity="${escapeXml(lineOpacity)}"`);
  }
  const lineStyle = selectedStyle?.lineStyle;
  if (lineStyle) {
    const dash = prefigureDashByLineStyle[lineStyle];
    if (dash) {
      attrs.push(`dash="${escapeXml(dash)}"`);
    } else if (!(lineStyle in prefigureDashByLineStyle)) {
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: unknown line style '${lineStyle}' omitted from PreFigure output.`,
        position: warningPosition
      });
    }
  }
  return attrs;
}
function pointStyleAttributes({
  selectedStyle,
  diagnostics,
  warningPrefix,
  warningPosition,
  includeFill = true
}) {
  const attrs = [];
  const markerStyle = selectedStyle?.markerStyle;
  if (markerStyle) {
    const markerStyleString = String(markerStyle);
    const mappedStyle = prefigurePointStyleByMarkerStyle[markerStyleString];
    if (mappedStyle) {
      attrs.push(`style="${escapeXml(mappedStyle)}"`);
    } else if (markerStyleString.slice(0, 8) === "triangle") {
      attrs.push('style="diamond"');
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: marker style '${markerStyleString}' mapped to PreFigure style 'diamond'.`,
        position: warningPosition
      });
    } else {
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: marker style '${markerStyleString}' is unsupported by PreFigure; default style used.`,
        position: warningPosition
      });
    }
  }
  const size = formatNumber$1(selectedStyle?.markerSize);
  if (size !== null) {
    attrs.push(`size="${escapeXml(size)}"`);
  }
  const color = selectedStyle?.markerColor ?? selectedStyle?.markerColorWord ?? null;
  if (color) {
    if (includeFill) {
      attrs.push(`fill="${escapeXml(color)}"`);
    }
    attrs.push(`stroke="${escapeXml(color)}"`);
  }
  const opacity = formatNumber$1(selectedStyle?.markerOpacity);
  if (opacity !== null) {
    if (includeFill) {
      attrs.push(`fill-opacity="${escapeXml(opacity)}"`);
    }
    attrs.push(`stroke-opacity="${escapeXml(opacity)}"`);
  }
  const thickness = formatNumber$1(selectedStyle?.lineWidth);
  if (thickness !== null) {
    attrs.push(`thickness="${escapeXml(thickness)}"`);
  }
  return attrs;
}
function convertPointToPrefigure({
  sv,
  handle,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const p2 = formatPoint(sv.numericalXs);
  if (p2 === null) {
    return null;
  }
  const pointAttrs = pointStyleAttributes({
    selectedStyle: sv.selectedStyle,
    diagnostics,
    warningPrefix,
    warningPosition,
    // endpoint/equilibriumPoint expose `open`; open points should be
    // stroke-only (no fill) to match Doenet semantics.
    includeFill: sv.open !== true
  });
  const pointLabel = pointLabelAttributes({
    stateValues: sv,
    diagnostics,
    warningPrefix,
    warningPosition
  });
  const attrs = [
    `at="${escapeXml(handle)}"`,
    `p="${escapeXml(p2)}"`,
    ...pointAttrs,
    ...pointLabel?.attrs ?? []
  ];
  if (pointLabel?.label) {
    return `<point ${attrs.join(" ")}>${pointLabel.label}</point>`;
  }
  return `<point ${attrs.join(" ")} />`;
}
function lineElement({
  handle,
  p1,
  p2,
  infinite,
  styleAttrs,
  labelAttrs,
  label
}) {
  const attrs = [
    `at="${escapeXml(handle)}"`,
    `endpoints="${escapeXml(`(${p1},${p2})`)}"`,
    `infinite="${infinite}"`,
    ...styleAttrs,
    ...labelAttrs
  ];
  if (label) {
    return `<line ${attrs.join(" ")}>${label}</line>`;
  }
  return `<line ${attrs.join(" ")} />`;
}
function pointInsideBounds(point, bounds) {
  if (!Array.isArray(point) || point.length < 2 || !Array.isArray(bounds) || bounds.length < 4) {
    return false;
  }
  const x2 = asFiniteNumber(point[0]);
  const y2 = asFiniteNumber(point[1]);
  const xMin = asFiniteNumber(bounds[0]);
  const yMin = asFiniteNumber(bounds[1]);
  const xMax = asFiniteNumber(bounds[2]);
  const yMax = asFiniteNumber(bounds[3]);
  if (x2 === null || y2 === null || xMin === null || yMin === null || xMax === null || yMax === null) {
    return false;
  }
  return x2 >= xMin && x2 <= xMax && y2 >= yMin && y2 <= yMax;
}
function paramAlongSegment({
  start,
  end,
  point
}) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const denom = dx * dx + dy * dy;
  if (!(denom > 0)) {
    return null;
  }
  return ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / denom;
}
function convertLineToPrefigure({
  sv,
  handle,
  styleAttrs,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const points = extractFinitePointPair(sv.numericalPoints);
  if (!points) {
    return null;
  }
  const [p1Point, p2Point] = points;
  const [ep1, ep2] = orientEndpointsForLineLabel(p1Point, p2Point);
  const p1 = formatPoint(ep1);
  const p2 = formatPoint(ep2);
  if (p1 === null || p2 === null) {
    return null;
  }
  const clippedForLabel = clipLineLikeToBounds({
    point1: p1Point,
    point2: p2Point,
    bounds: sv.graphBounds,
    tMin: -Infinity,
    tMax: Infinity
  });
  const [labelEp1, labelEp2] = clippedForLabel ? orientEndpointsForLineLabel(clippedForLabel[0], clippedForLabel[1]) : [ep1, ep2];
  const { labelAttrs, label } = getLabelForLine({
    stateValues: sv,
    ep1: labelEp1,
    ep2: labelEp2,
    diagnostics,
    warningPrefix,
    warningPosition
  });
  return lineElement({
    handle,
    p1,
    p2,
    infinite: "yes",
    styleAttrs,
    labelAttrs,
    label
  });
}
function convertLineSegmentToPrefigure({
  sv,
  handle,
  styleAttrs,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const endpoints = extractFinitePointPair(sv.numericalEndpoints);
  if (!endpoints) {
    return null;
  }
  const [segmentP1, segmentP2] = endpoints;
  const [ep1, ep2] = orientEndpointsForLineLabel(segmentP1, segmentP2);
  const p1 = formatPoint(ep1);
  const p2 = formatPoint(ep2);
  if (p1 === null || p2 === null) {
    return null;
  }
  let labelEp1 = ep1;
  let labelEp2 = ep2;
  const endpoint1Inside = pointInsideBounds(ep1, sv?.graphBounds);
  const endpoint2Inside = pointInsideBounds(ep2, sv?.graphBounds);
  const hasOffscreenEndpoint = !endpoint1Inside || !endpoint2Inside;
  const stateValuesForLabel = {
    ...sv,
    // Use fixed pixel offset semantics so endpoint-side labels stay at a
    // stable on-screen distance from their chosen endpoint as the segment
    // is extended/clipped, instead of drifting under normalized [0, 1]
    // location scaling, which is a fraction of the full segment length.
    lineLabelAbsoluteEndpointOffset: true
  };
  if (hasOffscreenEndpoint) {
    const clippedVisibleSegment = clipLineLikeToBounds({
      point1: ep1,
      point2: ep2,
      bounds: sv?.graphBounds,
      tMin: 0,
      tMax: 1
    });
    if (clippedVisibleSegment) {
      [labelEp1, labelEp2] = orientEndpointsForLineLabel(
        clippedVisibleSegment[0],
        clippedVisibleSegment[1]
      );
      const t0 = paramAlongSegment({
        start: ep1,
        end: ep2,
        point: labelEp1
      });
      const t1 = paramAlongSegment({
        start: ep1,
        end: ep2,
        point: labelEp2
      });
      if (t0 !== null && t1 !== null) {
        const visibleLoc = lineLabelLocationValue(
          sv?.labelPosition,
          labelEp1,
          labelEp2,
          {
            absoluteEndpointOffset: true,
            graphBounds: sv?.graphBounds,
            graphDimensions: sv?.graphDimensions
          }
        ) ?? 0.5;
        stateValuesForLabel.lineLabelAlignmentLocationOverride = visibleLoc;
        stateValuesForLabel.lineLabelLocationOverride = t0 + visibleLoc * (t1 - t0);
      }
    }
  }
  const { labelAttrs, label } = getLabelForLine({
    stateValues: stateValuesForLabel,
    ep1: labelEp1,
    ep2: labelEp2,
    diagnostics,
    warningPrefix,
    warningPosition
  });
  return lineElement({
    handle,
    p1,
    p2,
    infinite: "no",
    styleAttrs,
    labelAttrs,
    label
  });
}
function convertRayToPrefigure({
  sv,
  handle,
  styleAttrs,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const endpoint = extractFinitePoint(sv.numericalEndpoint);
  const throughpoint = extractFinitePoint(sv.numericalThroughpoint);
  if (!endpoint || !throughpoint) {
    return null;
  }
  const clipped = clipLineLikeToBounds({
    point1: endpoint,
    point2: throughpoint,
    bounds: sv.graphBounds,
    tMin: 0,
    tMax: Infinity
  });
  if (!clipped) {
    return null;
  }
  const [ep1, ep2] = orientEndpointsForLineLabel(clipped[0], clipped[1]);
  const c1 = formatPoint(ep1);
  const c2 = formatPoint(ep2);
  if (c1 === null || c2 === null) {
    return null;
  }
  const { labelAttrs, label } = getLabelForLine({
    stateValues: {
      ...sv,
      lineLabelAbsoluteEndpointOffset: true
    },
    ep1,
    ep2,
    diagnostics,
    warningPrefix,
    warningPosition
  });
  return lineElement({
    handle,
    p1: c1,
    p2: c2,
    infinite: "no",
    styleAttrs,
    labelAttrs,
    label
  });
}
function convertVectorToPrefigure({
  sv,
  handle,
  styleAttrs,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const endpoints = extractFinitePointPair(sv.numericalEndpoints);
  if (!endpoints) {
    return null;
  }
  const [tail, head] = endpoints;
  const tailText = formatPoint(tail);
  if (tailText === null) {
    return null;
  }
  const dx = formatNumber$1(head[0] - tail[0]);
  const dy = formatNumber$1(head[1] - tail[1]);
  if (dx === null || dy === null) {
    return null;
  }
  const [ep1, ep2] = orientEndpointsForLineLabel(tail, head);
  const labelInfo = getLabelForLine({
    stateValues: sv,
    ep1,
    ep2,
    diagnostics,
    warningPrefix,
    warningPosition
  });
  const label = labelInfo?.label;
  const attrs = [
    `at="${escapeXml(handle)}"`,
    `tail="${escapeXml(tailText)}"`,
    `v="${escapeXml(`(${dx},${dy})`)}"`,
    ...styleAttrs
  ];
  const vectorXml = `<vector ${attrs.join(" ")} />`;
  if (!label) {
    return vectorXml;
  }
  const location = lineLabelLocationValue(sv?.labelPosition, ep1, ep2) ?? 0.5;
  const anchor = [
    Number((ep1[0] + (ep2[0] - ep1[0]) * location).toFixed(12)),
    Number((ep1[1] + (ep2[1] - ep1[1]) * location).toFixed(12))
  ];
  const anchorText = formatPoint(anchor);
  if (anchorText === null) {
    return vectorXml;
  }
  const labelXml = `<label p="${escapeXml(anchorText)}" alignment="north">${label}</label>`;
  return `${vectorXml}${labelXml}`;
}
function convertCircleToPrefigure({
  sv,
  handle,
  styleAttrs
}) {
  const center = formatPoint(sv.numericalCenter);
  const radius = formatNumber$1(sv.numericalRadius);
  if (center === null || radius === null) {
    return null;
  }
  return `<circle at="${escapeXml(handle)}" center="${escapeXml(center)}" radius="${escapeXml(radius)}" ${styleAttrs.join(" ")} />`;
}
function prefigurePointsFromVertices(vertices) {
  if (!Array.isArray(vertices)) {
    return [];
  }
  return vertices.map((pt2) => formatPoint(pt2)).filter((pt2) => pt2 !== null);
}
function polygonElement({
  handle,
  points,
  closed,
  styleAttrs
}) {
  return `<polygon at="${escapeXml(handle)}" points="${escapeXml(`(${points.join(",")})`)}" closed="${closed}" ${styleAttrs.join(" ")} />`;
}
function convertPolylineToPrefigure({
  sv,
  handle,
  styleAttrs
}) {
  const points = prefigurePointsFromVertices(sv.numericalVertices);
  if (points.length < 2) {
    return null;
  }
  return polygonElement({ handle, points, closed: "no", styleAttrs });
}
function convertPolygonToPrefigure({
  sv,
  handle,
  styleAttrs
}) {
  const points = prefigurePointsFromVertices(sv.numericalVertices);
  if (points.length < 3) {
    return null;
  }
  return polygonElement({ handle, points, closed: "yes", styleAttrs });
}
function anglePointsFromStateValues(sv) {
  const points = Array.isArray(sv.numericalPoints) ? sv.numericalPoints : null;
  const p1Raw = points?.[0];
  const pRaw = points?.[1];
  const p2Raw = points?.[2];
  if (!Array.isArray(p1Raw) || !Array.isArray(pRaw) || !Array.isArray(p2Raw)) {
    return null;
  }
  if (p1Raw.length < 2 || pRaw.length < 2 || p2Raw.length < 2) {
    return null;
  }
  const p1 = [Number(p1Raw[0]), Number(p1Raw[1])];
  const p2 = [Number(pRaw[0]), Number(pRaw[1])];
  const p22 = [Number(p2Raw[0]), Number(p2Raw[1])];
  if ([...p1, ...p2, ...p22].some((x2) => !Number.isFinite(x2))) {
    return null;
  }
  if (sv.swapPointOrder) {
    return [p22, p2, p1];
  }
  return [p1, p2, p22];
}
function normalizeVector(v2) {
  const mag = Math.hypot(v2[0], v2[1]);
  if (!(mag > 0)) {
    return null;
  }
  return [v2[0] / mag, v2[1] / mag];
}
function offsetPoint(base, direction, distance) {
  return [
    base[0] + direction[0] * distance,
    base[1] + direction[1] * distance
  ];
}
function labelAnchorForAngle({
  p1,
  p: p2,
  p2: p22,
  radius
}) {
  const u2 = normalizeVector([p1[0] - p2[0], p1[1] - p2[1]]);
  const v2 = normalizeVector([p22[0] - p2[0], p22[1] - p2[1]]);
  if (!u2 || !v2) {
    return null;
  }
  let bisector = [u2[0] + v2[0], u2[1] + v2[1]];
  if (Math.hypot(bisector[0], bisector[1]) < 1e-10) {
    bisector = [u2[1], -u2[0]];
  }
  const bisectorUnit = normalizeVector(bisector);
  if (!bisectorUnit) {
    return null;
  }
  return offsetPoint(p2, bisectorUnit, radius * 1.25);
}
function convertAngleToPrefigure({
  sv,
  handle,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const points = anglePointsFromStateValues(sv);
  if (!points) {
    return null;
  }
  const [p1Raw, pRaw, p2Raw] = points;
  const p1 = formatPoint(p1Raw);
  const p2 = formatPoint(pRaw);
  const p22 = formatPoint(p2Raw);
  if (p1 === null || p2 === null || p22 === null) {
    return null;
  }
  const radiusNumber = Number(sv.numericalRadius);
  if (!Number.isFinite(radiusNumber) || radiusNumber <= 0) {
    return null;
  }
  const radius = formatNumber$1(radiusNumber);
  const fromVertexToP1 = normalizeVector([
    p1Raw[0] - pRaw[0],
    p1Raw[1] - pRaw[1]
  ]);
  const fromVertexToP2 = normalizeVector([
    p2Raw[0] - pRaw[0],
    p2Raw[1] - pRaw[1]
  ]);
  if (!fromVertexToP1 || !fromVertexToP2) {
    return null;
  }
  const leg1 = offsetPoint(pRaw, fromVertexToP1, radiusNumber);
  const leg2 = offsetPoint(pRaw, fromVertexToP2, radiusNumber);
  const leg1Text = formatPoint(leg1);
  const leg2Text = formatPoint(leg2);
  if (leg1Text === null || leg2Text === null) {
    return null;
  }
  const pointsText = `(${leg1Text},${p2},${leg2Text})`;
  const sectorStyleAttrs = styleAttributes({
    selectedStyle: sv.selectedStyle,
    diagnostics,
    warningPrefix,
    warningPosition,
    includeFill: true
  });
  const arcXml = `<arc at="${escapeXml(`${handle}-arc`)}" points="${escapeXml(pointsText)}" radius="${escapeXml(radius)}" sector="yes" ${sectorStyleAttrs.join(" ")} />`;
  const label = labelMarkup({
    label: sv.label,
    labelHasLatex: sv.labelHasLatex
  });
  if (!label) {
    return arcXml;
  }
  const labelAnchor = labelAnchorForAngle({
    p1: leg1,
    p: pRaw,
    p2: leg2,
    radius: radiusNumber
  });
  const labelAnchorText = formatPoint(labelAnchor);
  if (labelAnchorText === null) {
    return arcXml;
  }
  const labelXml = `<label anchor="${escapeXml(labelAnchorText)}">${label}</label>`;
  return `${arcXml}${labelXml}`;
}
function astToExpressionString(ast) {
  try {
    return me$3.fromAst(ast).toString({ explicitMultiplicationSymbols: true });
  } catch (_e2) {
    return null;
  }
}
function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function rewriteExpressionVariable({
  expression,
  fromVariable,
  toVariable
}) {
  if (!fromVariable || fromVariable === toVariable) {
    return expression;
  }
  const escaped = escapeRegex(fromVariable);
  const symbolPattern = new RegExp(
    `(^|[^A-Za-z0-9_])(${escaped})(?=$|[^A-Za-z0-9_])`,
    "g"
  );
  return expression.replace(symbolPattern, `$1${toVariable}`);
}
function parseFormulaDefinition(definition, fallbackVariableName) {
  if (!definition || typeof definition !== "object") {
    return null;
  }
  const def = definition;
  if (def.functionType !== "formula") {
    return null;
  }
  const expression = astToExpressionString(def.formula);
  if (!expression) {
    return null;
  }
  const variables = Array.isArray(def.variables) ? def.variables : [];
  const variableExpression = variables.length ? astToExpressionString(variables[0]) : null;
  return {
    variableName: variableExpression || fallbackVariableName,
    expression
  };
}
function definitionTypeLabel(definition) {
  if (!definition || definition.functionType === void 0) {
    return "unknown";
  }
  return `${definition.functionType}`;
}
const SUPPORTED_CURVE_FUNCTION_TYPES = /* @__PURE__ */ new Set([
  "formula",
  "interpolated",
  "bezier",
  "piecewise"
]);
function makePieceHandle(baseHandle, index2, reserveBaseHandle = false) {
  if (!reserveBaseHandle && index2 === 0) {
    return baseHandle;
  }
  return `${baseHandle}_${index2}`;
}
function wrapCurvePieces({
  handle,
  pieces
}) {
  if (pieces.length === 0) {
    return null;
  }
  if (pieces.length === 1) {
    return pieces[0];
  }
  return `<group at="${escapeXml(handle)}">${pieces.join("")}</group>`;
}
function curveDefinitionAtIndex(fDefinitions, index2) {
  if (Array.isArray(fDefinitions)) {
    const candidate = fDefinitions[index2];
    return candidate && typeof candidate === "object" ? candidate : null;
  }
  if (fDefinitions && typeof fDefinitions === "object") {
    if ("functionType" in fDefinitions) {
      return fDefinitions;
    }
    const byNumericKey = fDefinitions[`${index2}`];
    if (byNumericKey && typeof byNumericKey === "object") {
      return byNumericKey;
    }
  }
  return null;
}
function intervalFromCurveDefinitionDomain(domain) {
  if (!Array.isArray(domain) || domain.length === 0) {
    return null;
  }
  const firstDomain = domain[0];
  if (firstDomain?.tree !== void 0) {
    const pieces = intervalPiecesFromMathTree(firstDomain.tree);
    return pieces[0] ?? null;
  }
  if (Array.isArray(firstDomain) && firstDomain.length > 0 && typeof firstDomain[0] === "string") {
    const pieces = intervalPiecesFromMathTree(firstDomain);
    return pieces[0] ?? null;
  }
  if (Array.isArray(firstDomain) && firstDomain.length >= 2 && Array.isArray(firstDomain[0]) && Array.isArray(firstDomain[1])) {
    const endpoints = firstDomain[0];
    const closedFlags = firstDomain[1];
    const min2 = Number(endpoints[0]);
    const max2 = Number(endpoints[1]);
    if (!Number.isFinite(min2) && min2 !== -Infinity || !Number.isFinite(max2) && max2 !== Infinity) {
      return null;
    }
    if (min2 > max2) {
      return null;
    }
    return {
      min: min2,
      max: max2,
      openMin: !Boolean(closedFlags[0]),
      openMax: !Boolean(closedFlags[1])
    };
  }
  return null;
}
function parsedInterpolatedDefinitionPieces({
  definition,
  fallbackVariableName,
  curveBounds
}) {
  const xs = Array.isArray(definition.xs) ? definition.xs.map((x2) => Number(x2)) : null;
  const coeffs = Array.isArray(definition.coeffs) ? definition.coeffs : null;
  if (!xs || !coeffs || xs.length < 2 || coeffs.length < 1 || coeffs.length !== xs.length - 1 || xs.some((x2) => !Number.isFinite(x2)) || coeffs.some(
    (c2) => !Array.isArray(c2) || c2.length < 4 || c2.slice(0, 4).some((v2) => !Number.isFinite(v2))
  )) {
    return null;
  }
  const parsedVariable = Array.isArray(definition.variables) ? astToExpressionString(definition.variables[0]) : null;
  const variableName = parsedVariable || fallbackVariableName;
  const domainInterval = intervalFromCurveDefinitionDomain(
    definition.domain
  ) ?? {
    min: -Infinity,
    max: Infinity,
    openMin: false,
    openMax: false
  };
  const makeExpression = (coeff, baseX) => {
    const shiftedVar = `(${variableName}-(${baseX}))`;
    return `(${coeff[0]}+(${coeff[1]})*${shiftedVar}+(${coeff[2]})*${shiftedVar}^2+(${coeff[3]})*${shiftedVar}^3)`;
  };
  const rawPieces = [];
  rawPieces.push({
    parsed: {
      variableName,
      expression: makeExpression(coeffs[0], xs[0])
    },
    interval: {
      min: -Infinity,
      max: xs[0],
      openMin: true,
      openMax: false
    }
  });
  for (let i2 = 0; i2 < coeffs.length; i2 += 1) {
    rawPieces.push({
      parsed: {
        variableName,
        expression: makeExpression(coeffs[i2], xs[i2])
      },
      interval: {
        min: xs[i2],
        max: xs[i2 + 1],
        openMin: false,
        openMax: false
      }
    });
  }
  rawPieces.push({
    parsed: {
      variableName,
      expression: makeExpression(
        coeffs[coeffs.length - 1],
        xs[xs.length - 2]
      )
    },
    interval: {
      min: xs[xs.length - 1],
      max: Infinity,
      openMin: false,
      openMax: true
    }
  });
  const pieces = [];
  let hasOpenEndpoints = false;
  for (const piece of rawPieces) {
    const withDefinitionDomain = intersectIntervals(
      piece.interval,
      domainInterval
    );
    if (!withDefinitionDomain) {
      continue;
    }
    const clipped = intersectIntervalWithBounds(
      withDefinitionDomain,
      curveBounds
    );
    if (!clipped) {
      continue;
    }
    if (clipped.openMin || clipped.openMax) {
      hasOpenEndpoints = true;
    }
    pieces.push({
      parsed: piece.parsed,
      interval: clipped
    });
  }
  return {
    sourceType: "interpolated",
    hasOpenEndpoints,
    pieces
  };
}
function parsedBezierDefinitionPieces({
  definition,
  fallbackVariableName,
  curveBounds
}) {
  const parsedVariable = Array.isArray(definition.variables) ? astToExpressionString(definition.variables[0]) : null;
  const variableName = parsedVariable || fallbackVariableName;
  const numThroughPoints = Number(definition.numThroughPoints);
  if (!Number.isInteger(numThroughPoints) || numThroughPoints < 1) {
    return null;
  }
  const segmentCount = numThroughPoints - 1;
  const component = Number.isInteger(definition.component) ? Number(definition.component) : 0;
  const rawSplineCoeffs = Array.isArray(definition.splineCoeffs) ? definition.splineCoeffs : null;
  if (!rawSplineCoeffs || rawSplineCoeffs.length !== segmentCount) {
    return null;
  }
  const splineCoeffs = rawSplineCoeffs.map((segment) => {
    if (!Array.isArray(segment) || !Array.isArray(segment[component])) {
      return null;
    }
    const coeffs = segment[component].slice(0, 4).map((v2) => Number(v2));
    if (coeffs.length < 4 || coeffs.some((v2) => !Number.isFinite(v2))) {
      return null;
    }
    return coeffs;
  });
  if (splineCoeffs.some((x2) => x2 === null)) {
    return null;
  }
  const forwardExtrapolation = Boolean(definition.extrapolateForward);
  const backwardExtrapolation = Boolean(definition.extrapolateBackward);
  const getExtrapolationCoeffs = (coeffsByComponent) => {
    if (!Array.isArray(coeffsByComponent)) {
      return null;
    }
    const componentCoeffs = coeffsByComponent[component];
    if (!Array.isArray(componentCoeffs)) {
      return null;
    }
    const coeffs = componentCoeffs.slice(0, 3).map((v2) => Number(v2));
    if (coeffs.length < 3 || coeffs.some((v2) => !Number.isFinite(v2))) {
      return null;
    }
    return coeffs;
  };
  const backwardCoeffs = backwardExtrapolation ? getExtrapolationCoeffs(definition.extrapolateBackwardCoeffs) : null;
  if (backwardExtrapolation && !backwardCoeffs) {
    return null;
  }
  const forwardCoeffs = forwardExtrapolation ? getExtrapolationCoeffs(definition.extrapolateForwardCoeffs) : null;
  if (forwardExtrapolation && !forwardCoeffs) {
    return null;
  }
  const rawPieces = [];
  if (backwardCoeffs) {
    rawPieces.push({
      parsed: {
        variableName,
        expression: `(${backwardCoeffs[0]}+(${backwardCoeffs[1]})*(${variableName})+(${backwardCoeffs[2]})*(${variableName})^2)`
      },
      interval: {
        min: -Infinity,
        max: 0,
        openMin: true,
        openMax: false
      }
    });
  }
  for (let i2 = 0; i2 < segmentCount; i2 += 1) {
    const coeffs = splineCoeffs[i2];
    const shiftedVar = `(${variableName}-(${i2}))`;
    rawPieces.push({
      parsed: {
        variableName,
        expression: `(${coeffs[0]}+(${coeffs[1]})*${shiftedVar}+(${coeffs[2]})*${shiftedVar}^2+(${coeffs[3]})*${shiftedVar}^3)`
      },
      interval: {
        min: i2,
        max: i2 + 1,
        openMin: false,
        openMax: false
      }
    });
  }
  if (forwardCoeffs) {
    const len = segmentCount;
    const shiftedVar = `(${variableName}-(${len}))`;
    rawPieces.push({
      parsed: {
        variableName,
        expression: `(${forwardCoeffs[0]}+(${forwardCoeffs[1]})*${shiftedVar}+(${forwardCoeffs[2]})*${shiftedVar}^2)`
      },
      interval: {
        min: len,
        max: Infinity,
        openMin: false,
        openMax: true
      }
    });
  }
  const pieces = [];
  let hasOpenEndpoints = false;
  for (const piece of rawPieces) {
    const clipped = intersectIntervalWithBounds(
      piece.interval,
      curveBounds
    );
    if (!clipped) {
      continue;
    }
    if (clipped.openMin || clipped.openMax) {
      hasOpenEndpoints = true;
    }
    pieces.push({
      parsed: piece.parsed,
      interval: clipped
    });
  }
  return {
    sourceType: "bezier",
    hasOpenEndpoints,
    pieces
  };
}
function formatDomainBound(value) {
  if (value === Infinity) {
    return "inf";
  }
  if (value === -Infinity) {
    return "-inf";
  }
  if (!Number.isFinite(value)) {
    return null;
  }
  return `${Number(value)}`;
}
function numericBoundFromStateValue(value) {
  if (value === -Infinity || value === Infinity) {
    return value;
  }
  return Number.isFinite(value) ? Number(value) : null;
}
function domainBoundsFromStateValues({
  parMin,
  parMax
}) {
  const min2 = numericBoundFromStateValue(parMin);
  const max2 = numericBoundFromStateValue(parMax);
  if (min2 === null || max2 === null || min2 > max2) {
    return null;
  }
  return { min: min2, max: max2 };
}
function intervalPiecesFromMathTree(tree) {
  if (!Array.isArray(tree) || tree.length === 0) {
    return [];
  }
  const operator = tree[0];
  if (operator === "interval") {
    const endpoints = tree[1];
    const closedFlags = tree[2];
    if (!Array.isArray(endpoints) || !Array.isArray(closedFlags) || endpoints.length < 3 || closedFlags.length < 3) {
      return [];
    }
    const min2 = Number(endpoints[1]);
    const max2 = Number(endpoints[2]);
    const minClosed = Boolean(closedFlags[1]);
    const maxClosed = Boolean(closedFlags[2]);
    if (!Number.isFinite(min2) && min2 !== -Infinity || !Number.isFinite(max2) && max2 !== Infinity) {
      return [];
    }
    if (min2 > max2 || min2 === max2 && (!minClosed || !maxClosed)) {
      return [];
    }
    return [
      {
        min: min2,
        max: max2,
        openMin: !minClosed,
        openMax: !maxClosed
      }
    ];
  }
  if (operator === "set") {
    if (tree.length < 2) {
      return [];
    }
    const value = Number(tree[1]);
    if (!Number.isFinite(value)) {
      return [];
    }
    return [
      {
        min: value,
        max: value,
        openMin: false,
        openMax: false
      }
    ];
  }
  if (operator === "union") {
    return tree.slice(1).flatMap((piece) => intervalPiecesFromMathTree(piece));
  }
  return [];
}
function computeOpenEndpoints(interval1, interval2) {
  let openMin;
  if (interval1.min > interval2.min) {
    openMin = interval1.openMin;
  } else if (interval1.min < interval2.min) {
    openMin = interval2.openMin;
  } else {
    openMin = interval1.openMin || interval2.openMin;
  }
  let openMax;
  if (interval1.max < interval2.max) {
    openMax = interval1.openMax;
  } else if (interval1.max > interval2.max) {
    openMax = interval2.openMax;
  } else {
    openMax = interval1.openMax || interval2.openMax;
  }
  return { openMin, openMax };
}
function intersectIntervalWithBounds(interval, bounds) {
  const min2 = Math.max(interval.min, bounds.min);
  const max2 = Math.min(interval.max, bounds.max);
  if (min2 > max2) {
    return null;
  }
  let openMin = interval.openMin;
  if (min2 === bounds.min && interval.min < bounds.min) {
    openMin = false;
  }
  let openMax = interval.openMax;
  if (max2 === bounds.max && interval.max > bounds.max) {
    openMax = false;
  }
  if (min2 === max2 && (openMin || openMax)) {
    return null;
  }
  return {
    min: min2,
    max: max2,
    openMin,
    openMax
  };
}
function domainFromIntervalPiece(interval) {
  const min2 = formatDomainBound(interval.min) ?? "-inf";
  const max2 = formatDomainBound(interval.max) ?? "inf";
  return `(${min2},${max2})`;
}
function intersectIntervals(interval1, interval2) {
  const min2 = Math.max(interval1.min, interval2.min);
  const max2 = Math.min(interval1.max, interval2.max);
  if (min2 > max2) {
    return null;
  }
  const { openMin, openMax } = computeOpenEndpoints(interval1, interval2);
  if (min2 === max2 && (openMin || openMax)) {
    return null;
  }
  return {
    min: min2,
    max: max2,
    openMin,
    openMax
  };
}
function parsedFormulaPiecesFromDefinition({
  definition,
  fallbackVariableName,
  curveBounds,
  diagnostics,
  warningPrefix,
  warningPosition,
  pieceRole
}) {
  if (!definition) {
    return null;
  }
  if (definition.functionType === "formula") {
    const parsed = parseFormulaDefinition(definition, fallbackVariableName);
    if (!parsed) {
      return null;
    }
    return {
      sourceType: "formula",
      hasOpenEndpoints: false,
      pieces: [
        {
          parsed,
          interval: {
            min: curveBounds.min,
            max: curveBounds.max,
            openMin: false,
            openMax: false
          }
        }
      ]
    };
  }
  if (definition.functionType === "interpolated") {
    return parsedInterpolatedDefinitionPieces({
      definition,
      fallbackVariableName,
      curveBounds
    });
  }
  if (definition.functionType === "bezier") {
    return parsedBezierDefinitionPieces({
      definition,
      fallbackVariableName,
      curveBounds
    });
  }
  if (definition.functionType !== "piecewise") {
    return null;
  }
  const childDefinitions = Array.isArray(definition.fDefinitionsOfChildren) ? definition.fDefinitionsOfChildren : null;
  const numericalDomainsOfChildren = Array.isArray(
    definition.numericalDomainsOfChildren
  ) ? definition.numericalDomainsOfChildren : null;
  if (!childDefinitions || !numericalDomainsOfChildren) {
    return null;
  }
  const effectiveDomains = find_effective_domains_piecewise_children({
    domain: definition.domain,
    numericalDomainsOfChildren
  });
  const pieces = [];
  let hasOpenEndpoints = false;
  for (let i2 = 0; i2 < childDefinitions.length; i2 += 1) {
    const childDefinition = childDefinitions[i2];
    const childPiecesResult = parsedFormulaPiecesFromDefinition({
      definition: childDefinition,
      fallbackVariableName,
      curveBounds,
      diagnostics,
      warningPrefix,
      warningPosition,
      pieceRole: `${pieceRole} piecewise child`
    });
    if (!childPiecesResult) {
      const typeLabel = definitionTypeLabel(childDefinition);
      const message = SUPPORTED_CURVE_FUNCTION_TYPES.has(typeLabel) ? `${warningPrefix}: ${pieceRole} piecewise child ${i2 + 1} failed to build curve pieces (type '${typeLabel}'); child skipped.` : `${warningPrefix}: ${pieceRole} piecewise child ${i2 + 1} has unsupported function definition type '${typeLabel}'; child skipped.`;
      pushWarning({ diagnostics, message, position: warningPosition });
      continue;
    }
    if (childPiecesResult.pieces.length === 0) {
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: ${pieceRole} piecewise child ${i2 + 1} has no pieces overlapping the active domain/bounds; child skipped.`,
        position: warningPosition
      });
      continue;
    }
    if (childPiecesResult.hasOpenEndpoints) {
      hasOpenEndpoints = true;
    }
    const domainTree = effectiveDomains?.[i2]?.toMathExpression?.()?.tree ?? null;
    for (const interval of intervalPiecesFromMathTree(domainTree)) {
      const effectiveInterval = intersectIntervalWithBounds(
        interval,
        curveBounds
      );
      if (!effectiveInterval) {
        continue;
      }
      if (effectiveInterval.openMin || effectiveInterval.openMax) {
        hasOpenEndpoints = true;
      }
      for (const childPiece of childPiecesResult.pieces) {
        const overlap = intersectIntervals(
          childPiece.interval,
          effectiveInterval
        );
        if (!overlap) {
          continue;
        }
        if (overlap.openMin || overlap.openMax) {
          hasOpenEndpoints = true;
        }
        pieces.push({
          parsed: childPiece.parsed,
          interval: overlap
        });
      }
    }
  }
  return {
    sourceType: "piecewise",
    hasOpenEndpoints,
    pieces
  };
}
function warnCurveLabelOmitted({
  label,
  labelHasLatex,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  if (labelMarkup({
    label,
    labelHasLatex
  })) {
    pushWarning({
      diagnostics,
      message: `${warningPrefix}: labels are not supported on converted curve elements; label omitted.`,
      position: warningPosition
    });
  }
}
function validatePiecesResult(result, diagnostics, warningPrefix, warningPosition, definitionTypeLabel2) {
  const hasUnsupportedType = definitionTypeLabel2.split(",").map((x2) => x2.trim()).some((x2) => !SUPPORTED_CURVE_FUNCTION_TYPES.has(x2));
  if (!result || result.pieces.length === 0) {
    if (hasUnsupportedType) {
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: unsupported curve function definition type '${definitionTypeLabel2}'; descendant skipped.`,
        position: warningPosition
      });
    }
    return false;
  }
  return true;
}
function emitCurveElement({
  handle,
  styleAttrs,
  parsed,
  domain,
  isParametric
}) {
  const tagName = isParametric ? "parametric-curve" : "graph";
  const prefix = isParametric ? "r" : "f";
  const functionDefinition = isParametric ? `${handle}_${prefix}(${parsed.variableName})=(${parsed.expression},${parsed.variableName})` : `${handle}_${prefix}(${parsed.variableName})=${parsed.expression}`;
  const attrs = [
    `at="${escapeXml(handle)}"`,
    `function="${escapeXml(functionDefinition)}"`,
    ...domain ? [`domain="${escapeXml(domain)}"`] : [],
    ...styleAttrs
  ];
  return `<${tagName} ${attrs.join(" ")} />`;
}
function convertFunctionCurve({
  sv,
  handle,
  styleAttrs,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const definition = curveDefinitionAtIndex(sv.fDefinitions, 0);
  const curveBounds = domainBoundsFromStateValues({
    parMin: sv.parMin,
    parMax: sv.parMax
  });
  if (!curveBounds) {
    return null;
  }
  const result = parsedFormulaPiecesFromDefinition({
    definition,
    fallbackVariableName: "x",
    curveBounds,
    diagnostics,
    warningPrefix,
    warningPosition,
    pieceRole: "function"
  });
  if (!validatePiecesResult(
    result,
    diagnostics,
    warningPrefix,
    warningPosition,
    definitionTypeLabel(definition)
  )) {
    return null;
  }
  const validResult = result;
  const reserveBaseHandle = validResult.pieces.length > 1;
  const pieces = validResult.pieces.map(
    ({ parsed, interval }, i2) => emitCurveElement({
      handle: makePieceHandle(handle, i2, reserveBaseHandle),
      styleAttrs,
      parsed,
      domain: domainFromIntervalPiece(interval),
      isParametric: Boolean(sv.flipFunction)
    })
  );
  return wrapCurvePieces({ handle, pieces });
}
function convertParametricCurve({
  sv,
  handle,
  styleAttrs,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  const xDefinition = curveDefinitionAtIndex(sv.fDefinitions, 0);
  const yDefinition = curveDefinitionAtIndex(sv.fDefinitions, 1);
  const curveBounds = domainBoundsFromStateValues({
    parMin: sv.parMin,
    parMax: sv.parMax
  });
  if (!curveBounds) {
    return null;
  }
  const xPiecesResult = parsedFormulaPiecesFromDefinition({
    definition: xDefinition,
    fallbackVariableName: "t",
    curveBounds,
    diagnostics,
    warningPrefix,
    warningPosition,
    pieceRole: "x"
  });
  const yPiecesResult = parsedFormulaPiecesFromDefinition({
    definition: yDefinition,
    fallbackVariableName: xPiecesResult?.pieces?.[0]?.parsed.variableName ?? "t",
    curveBounds,
    diagnostics,
    warningPrefix,
    warningPosition,
    pieceRole: "y"
  });
  if (!validatePiecesResult(
    xPiecesResult,
    diagnostics,
    warningPrefix,
    warningPosition,
    `${definitionTypeLabel(xDefinition)}, ${definitionTypeLabel(yDefinition)}`
  ) || !validatePiecesResult(
    yPiecesResult,
    diagnostics,
    warningPrefix,
    warningPosition,
    `${definitionTypeLabel(xDefinition)}, ${definitionTypeLabel(yDefinition)}`
  )) {
    return null;
  }
  const validXPiecesResult = xPiecesResult;
  const validYPiecesResult = yPiecesResult;
  const comparePiecesByIntervalStart = (a2, b2) => {
    if (a2.interval.min !== b2.interval.min) {
      return a2.interval.min - b2.interval.min;
    }
    return a2.interval.max - b2.interval.max;
  };
  const xPieces = [...validXPiecesResult.pieces].sort(
    comparePiecesByIntervalStart
  );
  const yPieces = [...validYPiecesResult.pieces].sort(
    comparePiecesByIntervalStart
  );
  const pieceDefinitions = [];
  let xIndex = 0;
  let yIndex = 0;
  while (xIndex < xPieces.length && yIndex < yPieces.length) {
    const xPiece = xPieces[xIndex];
    const yPiece = yPieces[yIndex];
    const overlap = intersectIntervals(xPiece.interval, yPiece.interval);
    if (overlap && overlap.min !== overlap.max) {
      const parameterVariable = xPiece.parsed.variableName;
      const xExpression = rewriteExpressionVariable({
        expression: xPiece.parsed.expression,
        fromVariable: xPiece.parsed.variableName,
        toVariable: parameterVariable
      });
      const yExpression = rewriteExpressionVariable({
        expression: yPiece.parsed.expression,
        fromVariable: yPiece.parsed.variableName,
        toVariable: parameterVariable
      });
      pieceDefinitions.push({
        parameterVariable,
        xExpression,
        yExpression,
        domain: domainFromIntervalPiece(overlap)
      });
    }
    if (xPiece.interval.max < yPiece.interval.max) {
      xIndex += 1;
    } else if (xPiece.interval.max > yPiece.interval.max) {
      yIndex += 1;
    } else {
      xIndex += 1;
      yIndex += 1;
    }
  }
  if (pieceDefinitions.length === 0) {
    return null;
  }
  const reserveBaseHandle = pieceDefinitions.length > 1;
  const pieces = pieceDefinitions.map(
    ({ parameterVariable, xExpression, yExpression, domain }, index2) => {
      const pieceHandle = makePieceHandle(
        handle,
        index2,
        reserveBaseHandle
      );
      const functionDefinition = `${pieceHandle}_r(${parameterVariable})=(${xExpression},${yExpression})`;
      const attrs = [
        `at="${escapeXml(pieceHandle)}"`,
        `function="${escapeXml(functionDefinition)}"`,
        `domain="${escapeXml(domain)}"`,
        ...styleAttrs
      ];
      return `<parametric-curve ${attrs.join(" ")} />`;
    }
  );
  return wrapCurvePieces({
    handle,
    pieces
  });
}
function convertBezierCurve(args) {
  return convertParametricCurve(args);
}
function convertCurveToPrefigure(args) {
  const { sv } = args;
  warnCurveLabelOmitted({
    label: sv.label,
    labelHasLatex: sv.labelHasLatex,
    diagnostics: args.diagnostics,
    warningPrefix: args.warningPrefix,
    warningPosition: args.warningPosition
  });
  if (sv.curveType === "function") {
    return convertFunctionCurve(args);
  }
  if (sv.curveType === "parameterization") {
    return convertParametricCurve(args);
  }
  if (sv.curveType === "bezier") {
    return convertBezierCurve(args);
  }
  return null;
}
function convertRegionBetweenCurvesToPrefigure({
  sv,
  handle,
  styleAttrs,
  diagnostics,
  warningPrefix,
  warningPosition
}) {
  if (!sv.haveFunctions) {
    return null;
  }
  if (!Array.isArray(sv.boundaryValues) || sv.boundaryValues.length !== 2) {
    return null;
  }
  const bv0 = asFiniteNumber(sv.boundaryValues[0]);
  const bv1 = asFiniteNumber(sv.boundaryValues[1]);
  if (bv0 === null || bv1 === null) {
    return null;
  }
  const [boundMin, boundMax] = bv0 <= bv1 ? [bv0, bv1] : [bv1, bv0];
  if (sv.flipFunctions) {
    pushWarning({
      diagnostics,
      message: `${warningPrefix}: unsupported flipFunctions attribute on regionBetweenCurves; descendant skipped.`,
      position: warningPosition
    });
    return null;
  }
  const fDefinitions = sv.fDefinitions;
  if (!Array.isArray(fDefinitions) || fDefinitions.length < 2) {
    return null;
  }
  const f1 = parseFormulaDefinition(fDefinitions[0], "x");
  const f2 = parseFormulaDefinition(fDefinitions[1], "x");
  if (!f1 || !f2) {
    pushWarning({
      diagnostics,
      message: `${warningPrefix}: only formula-typed child functions are supported on regionBetweenCurves; descendant skipped.`,
      position: warningPosition
    });
    return null;
  }
  const fName1 = `${handle}_f1`;
  const fName2 = `${handle}_f2`;
  const def1Expr = rewriteExpressionVariable({
    expression: f1.expression,
    fromVariable: f1.variableName,
    toVariable: "x"
  });
  const def2Expr = rewriteExpressionVariable({
    expression: f2.expression,
    fromVariable: f2.variableName,
    toVariable: "x"
  });
  const def1 = `<definition>${escapeXml(`${fName1}(x)=${def1Expr}`)}</definition>`;
  const def2 = `<definition>${escapeXml(`${fName2}(x)=${def2Expr}`)}</definition>`;
  const areaAttrs = [
    `at="${escapeXml(handle)}"`,
    `function1="${escapeXml(fName1)}"`,
    `function2="${escapeXml(fName2)}"`,
    `domain="${escapeXml(`(${boundMin},${boundMax})`)}"`,
    ...styleAttrs
  ];
  return `${def1}${def2}<area-between-curves ${areaAttrs.join(" ")} />`;
}
const FILLED_COMPONENT_TYPES = /* @__PURE__ */ new Set([
  "circle",
  "polygon",
  "triangle",
  "rectangle"
]);
const NO_FILL_COMPONENT_TYPES = /* @__PURE__ */ new Set(["polyline", "angle", "curve"]);
function styleIncludesFill(componentType, sv) {
  if (FILLED_COMPONENT_TYPES.has(componentType)) {
    return Boolean(sv.filled);
  }
  return !NO_FILL_COMPONENT_TYPES.has(componentType);
}
const convertByComponentType = {
  point: convertPointToPrefigure,
  line: convertLineToPrefigure,
  lineSegment: convertLineSegmentToPrefigure,
  ray: convertRayToPrefigure,
  vector: convertVectorToPrefigure,
  circle: convertCircleToPrefigure,
  polyline: convertPolylineToPrefigure,
  polygon: convertPolygonToPrefigure,
  angle: convertAngleToPrefigure,
  curve: convertCurveToPrefigure,
  regionBetweenCurves: convertRegionBetweenCurvesToPrefigure,
  endpoint: convertPointToPrefigure,
  equilibriumPoint: convertPointToPrefigure,
  triangle: convertPolygonToPrefigure,
  rectangle: convertPolygonToPrefigure
};
function convertGraphicalDescendantToPrefigure({
  descendant,
  index: index2,
  usedHandles,
  diagnostics,
  graphBounds,
  graphDimensions,
  darkMode
}) {
  const sv = {
    ...descendant?.stateValues ?? {},
    graphBounds,
    graphDimensions
  };
  sv.selectedStyle = resolveSelectedStyleForTheme(sv.selectedStyle, darkMode);
  if (sv.hidden) {
    return null;
  }
  const warningPrefix = warningMessageForDescendant(descendant);
  const warningPosition = descendant?.position;
  const handle = createStableHandle(descendant, index2, usedHandles);
  const styleAttrs = styleAttributes({
    selectedStyle: sv.selectedStyle,
    diagnostics,
    warningPrefix,
    warningPosition,
    includeFill: styleIncludesFill(descendant.componentType, sv)
  });
  const converter = convertByComponentType[descendant.componentType];
  if (!converter) {
    pushWarning({
      diagnostics,
      message: `${warningPrefix}: unsupported in graph prefigure renderer; descendant skipped.`,
      position: warningPosition
    });
    return null;
  }
  const diagnosticsCountBeforeConvert = diagnostics.length;
  const body = converter({
    sv,
    handle,
    styleAttrs,
    diagnostics,
    warningPrefix,
    warningPosition
  });
  if (!body) {
    if (diagnostics.length === diagnosticsCountBeforeConvert) {
      pushWarning({
        diagnostics,
        message: `${warningPrefix}: non-finite or incomplete geometry; descendant skipped.`,
        position: warningPosition
      });
    }
    return null;
  }
  return {
    xml: body,
    handle
  };
}
const INVALID_REF_WARNING = "`<annotation>`: invalid `ref`; cannot resolve target. Annotation omitted.";
function nextAvailableConceptualAnnotationRef({
  initialCounter,
  usedRefs
}) {
  let nextCounter = initialCounter;
  while (usedRefs.has(`annotation_${nextCounter}`)) {
    nextCounter += 1;
  }
  return { ref: `annotation_${nextCounter}`, nextCounter: nextCounter + 1 };
}
function nextConceptualRef(state) {
  const { ref, nextCounter } = nextAvailableConceptualAnnotationRef({
    initialCounter: state.conceptualRefCounter,
    usedRefs: state.usedAnnotationRefs
  });
  state.conceptualRefCounter = nextCounter;
  state.usedAnnotationRefs.add(ref);
  return ref;
}
function convertDoenetMLAnnotationsToPreFigureXml({
  annotations,
  diagnostics,
  handleByComponentIdx,
  graphComponentIdx,
  graphDescendantComponentIndices,
  functionToCurveComponentIdx
}) {
  if (!Array.isArray(annotations) || annotations.length === 0) {
    return "<annotations></annotations>";
  }
  const state = {
    diagnostics,
    handleByComponentIdx,
    graphComponentIdx,
    graphDescendantComponentIndices,
    functionToCurveComponentIdx: functionToCurveComponentIdx ?? {},
    conceptualRefCounter: 1,
    usedAnnotationRefs: new Set(handleByComponentIdx.values())
  };
  const annotationsXml = annotations.map(
    (annotation) => convertAnnotationNodeToPreFigureXml(annotation, state)
  ).filter((x2) => x2 !== null).join("");
  return `<annotations>${annotationsXml}</annotations>`;
}
function pushAnnotationWarning({
  diagnostics,
  message,
  annotation
}) {
  const warning = {
    type: "warning",
    message,
    position: annotation?.refResolutions?.[0]?.position
  };
  diagnostics.push(warning);
}
function pushInvalidRefWarning(annotation, state) {
  pushAnnotationWarning({
    diagnostics: state.diagnostics,
    annotation,
    message: INVALID_REF_WARNING
  });
  return null;
}
function resolveAnnotationRef(annotation, state) {
  if (!annotation.hasRefAttribute) {
    return nextConceptualRef(state);
  }
  const [firstResolution, ...otherResolutions] = annotation.refResolutions ?? [];
  if (!firstResolution) {
    return pushInvalidRefWarning(annotation, state);
  }
  if (otherResolutions.length > 0) {
    pushAnnotationWarning({
      diagnostics: state.diagnostics,
      annotation,
      message: "`<annotation>`: `ref` resolved to multiple targets; using the first target."
    });
  }
  const unresolvedPath = firstResolution.unresolvedPath;
  const targetComponentIdx = Number.isFinite(firstResolution.componentIdx) ? Number(firstResolution.componentIdx) : null;
  if (targetComponentIdx === null || unresolvedPath !== null) {
    return pushInvalidRefWarning(annotation, state);
  }
  if (targetComponentIdx === state.graphComponentIdx) {
    state.usedAnnotationRefs.add("figure");
    return "figure";
  }
  let resolvedIdx = targetComponentIdx;
  const directHandle = state.handleByComponentIdx.get(resolvedIdx);
  if (!directHandle) {
    const aliasedTargetComponentIdx = state.functionToCurveComponentIdx[resolvedIdx];
    if (Number.isFinite(aliasedTargetComponentIdx)) {
      resolvedIdx = aliasedTargetComponentIdx;
    }
  }
  if (!state.graphDescendantComponentIndices.has(resolvedIdx)) {
    pushAnnotationWarning({
      diagnostics: state.diagnostics,
      annotation,
      message: "`<annotation>`: invalid `ref`; target is outside the containing graph. Annotation omitted."
    });
    return null;
  }
  const handle = directHandle ?? state.handleByComponentIdx.get(resolvedIdx);
  if (!handle) {
    pushAnnotationWarning({
      diagnostics: state.diagnostics,
      annotation,
      message: "`<annotation>`: invalid `ref`; target is not a supported graphical object in prefigure conversion. Annotation omitted."
    });
    return null;
  }
  state.usedAnnotationRefs.add(handle);
  return handle;
}
function convertAnnotationNodeToPreFigureXml(annotation, state) {
  const ref = resolveAnnotationRef(annotation, state);
  if (ref === null) {
    return null;
  }
  const text = typeof annotation.text === "string" ? annotation.text : "";
  if (text.trim() === "") {
    pushAnnotationWarning({
      diagnostics: state.diagnostics,
      annotation,
      message: "`<annotation>`: missing or empty `text`; emitting empty text."
    });
  }
  const attrs = [`ref="${escapeXml(ref)}"`, `text="${escapeXml(text)}"`];
  if (typeof annotation.speech === "string" && annotation.speech !== "") {
    attrs.push(`speech="${escapeXml(annotation.speech)}"`);
  }
  if (Boolean(annotation.sonify)) {
    attrs.push('sonify="yes"');
  }
  if (Boolean(annotation.circular)) {
    attrs.push('circular="yes"');
  }
  const childAnnotationsXml = (annotation.children ?? []).map((child) => convertAnnotationNodeToPreFigureXml(child, state)).filter((x2) => x2 !== null).join("");
  return `<annotation ${attrs.join(" ")}>${childAnnotationsXml}</annotation>`;
}
function axisModeFromVisibility({
  displayXAxis,
  displayYAxis
}) {
  const showXAxis = Boolean(displayXAxis);
  const showYAxis = Boolean(displayYAxis);
  if (showXAxis && showYAxis) {
    return "all";
  }
  if (showXAxis) {
    return "horizontal";
  }
  if (showYAxis) {
    return "vertical";
  }
  return null;
}
function pushUnsupportedAxisPositionWarnings({
  dependencyValues,
  diagnostics
}) {
  if (dependencyValues.xLabelPosition === "left") {
    diagnostics.push({
      type: "warning",
      message: '`<graph>`: xLabelPosition="left" is not supported in prefigure renderer; using right-position behavior.'
    });
  }
  if (dependencyValues.yLabelPosition === "bottom") {
    diagnostics.push({
      type: "warning",
      message: '`<graph>`: yLabelPosition="bottom" is not supported in prefigure renderer; using top-position behavior.'
    });
  }
}
const PREFIGURE_DARK_AXIS_COLOR = "#ffffff";
function axesElementFromLabels({
  dependencyValues,
  axesMode,
  darkMode
}) {
  const axisLabelElements = [];
  const xLabel = labelMarkup({
    label: dependencyValues.xLabel,
    labelHasLatex: dependencyValues.xLabelHasLatex
  });
  if (xLabel && dependencyValues.displayXAxis) {
    axisLabelElements.push(`<xlabel alignment="nw">${xLabel}</xlabel>`);
  }
  const yLabel = labelMarkup({
    label: dependencyValues.yLabel,
    labelHasLatex: dependencyValues.yLabelHasLatex
  });
  if (yLabel && dependencyValues.displayYAxis) {
    axisLabelElements.push(`<ylabel alignment="se">${yLabel}</ylabel>`);
  }
  const strokeAttr = darkMode ? ` stroke="${PREFIGURE_DARK_AXIS_COLOR}"` : "";
  if (axisLabelElements.length > 0) {
    return `<axes axes="${axesMode}"${strokeAttr}>${axisLabelElements.join("")}</axes>`;
  }
  return `<axes axes="${axesMode}"${strokeAttr} />`;
}
function createPrefigureXML({
  dependencyValues,
  descendants,
  unsupported,
  annotations,
  graphComponentIdx,
  functionToCurveComponentIdx,
  darkMode = false
}) {
  const diagnostics = [];
  const usedHandles = /* @__PURE__ */ new Set();
  const handleByComponentIdx = /* @__PURE__ */ new Map();
  const elements = [];
  const rawXMin = asFiniteNumber(dependencyValues.xMin);
  const rawYMin = asFiniteNumber(dependencyValues.yMin);
  const rawXMax = asFiniteNumber(dependencyValues.xMax);
  const rawYMax = asFiniteNumber(dependencyValues.yMax);
  if ([rawXMin, rawYMin, rawXMax, rawYMax].some((x2) => x2 === null)) {
    diagnostics.push({
      type: "warning",
      message: "`<graph>`: invalid axis bounds for prefigure conversion; using default bbox (-10,-10,10,10)."
    });
  }
  const graphBounds = rawXMin === null || rawYMin === null || rawXMax === null || rawYMax === null ? [-10, -10, 10, 10] : [rawXMin, rawYMin, rawXMax, rawYMax];
  let dimensionWidth = asFiniteNumber(dependencyValues.width?.size);
  if (dimensionWidth === null || dimensionWidth <= 0) {
    diagnostics.push({
      type: "warning",
      message: "`<graph>`: invalid width for prefigure conversion; using default diagram width 425."
    });
    dimensionWidth = 425;
  }
  let diagramAspectRatio = asFiniteNumber(dependencyValues.aspectRatio);
  if (diagramAspectRatio === null || diagramAspectRatio <= 0) {
    diagnostics.push({
      type: "warning",
      message: "`<graph>`: invalid aspectRatio for prefigure conversion; using default aspect ratio 1."
    });
    diagramAspectRatio = 1;
  }
  const dimensionHeight = dimensionWidth / diagramAspectRatio;
  const graphDimensions = [dimensionWidth, dimensionHeight];
  for (const descendant of unsupported ?? []) {
    pushWarning({
      diagnostics,
      message: `${warningMessageForDescendant(descendant)}: unsupported in graph prefigure renderer; descendant skipped.`,
      position: descendant?.position
    });
  }
  for (const [index2, descendant] of descendants.entries()) {
    const converted = convertGraphicalDescendantToPrefigure({
      descendant,
      index: index2,
      usedHandles,
      diagnostics,
      graphBounds,
      graphDimensions,
      darkMode
    });
    if (converted) {
      elements.push(converted.xml);
      if (Number.isFinite(descendant.componentIdx)) {
        handleByComponentIdx.set(
          descendant.componentIdx,
          converted.handle
        );
      }
    }
  }
  const graphDescendantComponentIndices = new Set(
    (dependencyValues.allDescendants ?? []).map((x2) => x2.componentIdx).filter((x2) => Number.isFinite(x2))
  );
  const xMin = formatNumber$1(graphBounds[0]);
  const yMin = formatNumber$1(graphBounds[1]);
  const xMax = formatNumber$1(graphBounds[2]);
  const yMax = formatNumber$1(graphBounds[3]);
  const bbox = `(${xMin},${yMin},${xMax},${yMax})`;
  const widthText = formatNumber$1(dimensionWidth) ?? "425";
  const heightText = formatNumber$1(dimensionHeight) ?? "425";
  const dimensions = `(${widthText},${heightText})`;
  let axesElement = "";
  const axesMode = axisModeFromVisibility(dependencyValues);
  if (axesMode) {
    pushUnsupportedAxisPositionWarnings({ dependencyValues, diagnostics });
    axesElement = axesElementFromLabels({
      dependencyValues,
      axesMode,
      darkMode
    });
  }
  const annotationsElement = convertDoenetMLAnnotationsToPreFigureXml({
    annotations,
    diagnostics,
    handleByComponentIdx,
    graphComponentIdx,
    graphDescendantComponentIndices,
    functionToCurveComponentIdx
  });
  const xml = `<diagram dimensions="${escapeXml(dimensions)}"><coordinates bbox="${escapeXml(bbox)}">${axesElement}${elements.join("")}</coordinates>${annotationsElement}</diagram>`;
  return { xml, diagnostics };
}
const CURVE_DESCENDANT_CONFIG = {
  key: "curveDescendants",
  componentType: "curve",
  variableNames: [
    "curveType",
    "fDefinitions",
    "parMin",
    "parMax",
    "flipFunction",
    "numericalThroughPoints",
    "periodic",
    "extrapolateBackward",
    "extrapolateForward",
    "selectedStyle",
    "label",
    "labelHasLatex",
    "hidden"
  ],
  variablesOptional: true
};
const GRAPHICAL_DESCENDANT_CONFIGS = [
  {
    key: "pointDescendants",
    componentType: "point",
    variableNames: [
      "numericalXs",
      "selectedStyle",
      "label",
      "labelHasLatex",
      "labelPosition",
      "open",
      "hidden"
    ],
    variablesOptional: true
  },
  {
    key: "lineDescendants",
    componentType: "line",
    variableNames: [
      "numericalPoints",
      "selectedStyle",
      "label",
      "labelHasLatex",
      "labelPosition",
      "hidden"
    ]
  },
  {
    key: "lineSegmentDescendants",
    componentType: "lineSegment",
    variableNames: [
      "numericalEndpoints",
      "selectedStyle",
      "label",
      "labelHasLatex",
      "labelPosition",
      "hidden"
    ]
  },
  {
    key: "rayDescendants",
    componentType: "ray",
    variableNames: [
      "numericalEndpoint",
      "numericalThroughpoint",
      "selectedStyle",
      "label",
      "labelHasLatex",
      "labelPosition",
      "hidden"
    ]
  },
  {
    key: "vectorDescendants",
    componentType: "vector",
    variableNames: [
      "numericalEndpoints",
      "selectedStyle",
      "label",
      "labelHasLatex",
      "labelPosition",
      "hidden"
    ]
  },
  {
    key: "angleDescendants",
    componentType: "angle",
    variableNames: [
      "numericalPoints",
      "numericalRadius",
      "selectedStyle",
      "label",
      "labelHasLatex",
      "swapPointOrder",
      "hidden"
    ]
  },
  {
    key: "circleDescendants",
    componentType: "circle",
    variableNames: [
      "numericalCenter",
      "numericalRadius",
      "selectedStyle",
      "filled",
      "hidden"
    ]
  },
  {
    key: "polylineDescendants",
    componentType: "polyline",
    variableNames: ["numericalVertices", "selectedStyle", "hidden"]
  },
  {
    key: "polygonDescendants",
    componentType: "polygon",
    variableNames: [
      "numericalVertices",
      "selectedStyle",
      "filled",
      "hidden"
    ]
  },
  {
    key: "regionBetweenCurvesDescendants",
    componentType: "regionBetweenCurves",
    variableNames: [
      "boundaryValues",
      "flipFunctions",
      "haveFunctions",
      "fDefinitions",
      "selectedStyle",
      "hidden"
    ]
  }
];
function descendantDependency({
  componentType,
  variableNames,
  variablesOptional
}) {
  const dependency = {
    dependencyType: "descendant",
    componentTypes: [componentType],
    variableNames
  };
  if (variablesOptional) {
    dependency.variablesOptional = true;
  }
  return dependency;
}
function prefigureBaseDependencies() {
  return {
    effectiveRenderer: {
      dependencyType: "stateVariable",
      variableName: "effectiveRenderer"
    },
    haveGraphParent: {
      dependencyType: "stateVariable",
      variableName: "haveGraphParent"
    },
    xMin: {
      dependencyType: "stateVariable",
      variableName: "xMin"
    },
    xMax: {
      dependencyType: "stateVariable",
      variableName: "xMax"
    },
    yMin: {
      dependencyType: "stateVariable",
      variableName: "yMin"
    },
    yMax: {
      dependencyType: "stateVariable",
      variableName: "yMax"
    },
    width: {
      dependencyType: "stateVariable",
      variableName: "width"
    },
    aspectRatio: {
      dependencyType: "stateVariable",
      variableName: "aspectRatio"
    },
    displayXAxis: {
      dependencyType: "stateVariable",
      variableName: "displayXAxis"
    },
    displayYAxis: {
      dependencyType: "stateVariable",
      variableName: "displayYAxis"
    },
    xLabel: {
      dependencyType: "stateVariable",
      variableName: "xLabel"
    },
    xLabelHasLatex: {
      dependencyType: "stateVariable",
      variableName: "xLabelHasLatex"
    },
    xLabelPosition: {
      dependencyType: "stateVariable",
      variableName: "xLabelPosition"
    },
    yLabel: {
      dependencyType: "stateVariable",
      variableName: "yLabel"
    },
    yLabelHasLatex: {
      dependencyType: "stateVariable",
      variableName: "yLabelHasLatex"
    },
    yLabelPosition: {
      dependencyType: "stateVariable",
      variableName: "yLabelPosition"
    },
    document: {
      dependencyType: "ancestor",
      componentType: "document",
      variableNames: ["theme"]
    }
  };
}
function prefigureDescendantDependencies() {
  return Object.fromEntries(
    GRAPHICAL_DESCENDANT_CONFIGS.map((config) => [
      config.key,
      descendantDependency(config)
    ])
  );
}
function returnGraphCurveDescendantsStateVariableDefinition() {
  return {
    stateVariablesDeterminingDependencies: ["effectiveRenderer"],
    returnDependencies: ({
      stateValues
    }) => {
      if (stateValues.effectiveRenderer !== "prefigure") {
        return {};
      }
      return {
        curveDescendants: descendantDependency(CURVE_DESCENDANT_CONFIG)
      };
    },
    definition({
      dependencyValues
    }) {
      return {
        setValue: {
          curveDescendants: dependencyValues.curveDescendants ?? []
        }
      };
    }
  };
}
function returnGraphCurveDescendantComponentIndicesStateVariableDefinition() {
  return {
    returnDependencies: () => ({
      curveDescendants: {
        dependencyType: "stateVariable",
        variableName: "curveDescendants"
      }
    }),
    definition({
      dependencyValues
    }) {
      const curveDescendantComponentIndices = (dependencyValues.curveDescendants ?? []).map((x2) => x2.componentIdx).filter((x2) => Number.isFinite(x2));
      return { setValue: { curveDescendantComponentIndices } };
    }
  };
}
function returnGraphFunctionCurveAliasMapStateVariableDefinition() {
  return {
    stateVariablesDeterminingDependencies: [
      "curveDescendantComponentIndices"
    ],
    returnDependencies: ({
      stateValues
    }) => {
      const dependencies = {
        curveDescendantComponentIndices: {
          dependencyType: "stateVariable",
          variableName: "curveDescendantComponentIndices"
        }
      };
      const curveDescendantComponentIndices = stateValues.curveDescendantComponentIndices;
      if (Array.isArray(curveDescendantComponentIndices)) {
        for (const [
          index2,
          curveComponentIdx
        ] of curveDescendantComponentIndices.entries()) {
          dependencies[`curveAdapterSource${index2}`] = {
            dependencyType: "adapterSource",
            componentIdx: curveComponentIdx
          };
        }
      }
      return dependencies;
    },
    definition({
      dependencyValues
    }) {
      const functionToCurveComponentIdx = {};
      const curveDescendantComponentIndices = dependencyValues.curveDescendantComponentIndices;
      if (Array.isArray(curveDescendantComponentIndices)) {
        for (const [
          index2,
          curveComponentIdx
        ] of curveDescendantComponentIndices.entries()) {
          const adapterSource = dependencyValues[`curveAdapterSource${index2}`];
          if (Number.isFinite(adapterSource?.componentIdx) && adapterSource?.componentType === "function") {
            functionToCurveComponentIdx[adapterSource.componentIdx] = curveComponentIdx;
          }
        }
      }
      return { setValue: { functionToCurveComponentIdx } };
    }
  };
}
function collectConfiguredDescendants(dependencyValues) {
  const byComponentIdx = /* @__PURE__ */ new Map();
  for (const config of [
    CURVE_DESCENDANT_CONFIG,
    ...GRAPHICAL_DESCENDANT_CONFIGS
  ]) {
    for (const descendant of dependencyValues[config.key] ?? []) {
      if (!Number.isFinite(descendant.componentIdx)) {
        continue;
      }
      byComponentIdx.set(descendant.componentIdx, descendant);
    }
  }
  return [...byComponentIdx.values()];
}
function returnGraphPrefigureXMLStateVariableDefinition() {
  return {
    description: "The Prefigure-formatted XML rendered for this graph, or null if not using the Prefigure renderer.",
    public: true,
    forRenderer: true,
    stateVariablesDeterminingDependencies: ["effectiveRenderer"],
    shadowingInstructions: {
      createComponentOfType: "text"
    },
    returnDependencies: ({
      stateValues
    }) => {
      const annotationsChildren = {
        annotationsChildren: {
          dependencyType: "child",
          childGroups: ["annotations"],
          variableNames: ["annotationSubtrees"]
        }
      };
      if (stateValues.effectiveRenderer !== "prefigure") {
        return annotationsChildren;
      }
      return {
        ...prefigureBaseDependencies(),
        ...prefigureDescendantDependencies(),
        curveDescendants: {
          dependencyType: "stateVariable",
          variableName: "curveDescendants"
        },
        functionToCurveComponentIdx: {
          dependencyType: "stateVariable",
          variableName: "functionToCurveComponentIdx"
        },
        ...annotationsChildren,
        allGraphicalDescendants: {
          dependencyType: "descendant",
          componentTypes: ["_graphical"]
        },
        allDescendants: {
          dependencyType: "descendant",
          componentTypes: ["_base"]
        }
      };
    },
    definition({
      dependencyValues,
      componentIdx
    }) {
      if (dependencyValues.effectiveRenderer !== "prefigure") {
        const diagnostics2 = [];
        if (dependencyValues.annotationsChildren && dependencyValues.annotationsChildren.length > 0) {
          diagnostics2.push({
            type: "info",
            message: "`<graph>`: annotations will not be rendered when not using the PreFigure renderer."
          });
        }
        return {
          setValue: { prefigureXML: null },
          sendDiagnostics: diagnostics2
        };
      }
      if (dependencyValues.haveGraphParent) {
        return { setValue: { prefigureXML: null } };
      }
      const descendants = collectConfiguredDescendants(dependencyValues);
      descendants.sort(sortDescendantsByOrder);
      const handledDescendantIndices = new Set(
        descendants.map((x2) => x2.componentIdx).filter((x2) => Number.isFinite(x2))
      );
      const unsupported = (dependencyValues.allGraphicalDescendants ?? []).filter(
        (x2) => Number.isFinite(x2.componentIdx) && !handledDescendantIndices.has(x2.componentIdx)
      );
      unsupported.sort(sortDescendantsByOrder);
      const selectedAnnotationsChild = dependencyValues.annotationsChildren?.[dependencyValues.annotationsChildren.length - 1];
      const annotations = selectedAnnotationsChild?.stateValues?.annotationSubtrees ?? null;
      const { xml, diagnostics } = createPrefigureXML({
        dependencyValues,
        descendants,
        unsupported,
        annotations,
        graphComponentIdx: componentIdx,
        functionToCurveComponentIdx: dependencyValues.functionToCurveComponentIdx ?? {},
        darkMode: dependencyValues.document?.stateValues?.theme === "dark"
      });
      if (dependencyValues.annotationsChildren && dependencyValues.annotationsChildren.length > 1) {
        const secondToLastAnnotationsChild = dependencyValues.annotationsChildren[dependencyValues.annotationsChildren.length - 2];
        diagnostics.push({
          type: "info",
          message: "Multiple `<annotations>` children found in `<graph>`; all but the last one are ignored.",
          position: secondToLastAnnotationsChild?.position
        });
      }
      return {
        setValue: { prefigureXML: xml },
        sendDiagnostics: diagnostics
      };
    }
  };
}
function returnGraphHasAuthorAnnotationsStateVariableDefinition() {
  return {
    description: "Whether the graph contains any author-provided `<annotations>` child.",
    public: true,
    forRenderer: true,
    shadowingInstructions: {
      createComponentOfType: "boolean"
    },
    returnDependencies: () => ({
      annotationsChildren: {
        dependencyType: "child",
        childGroups: ["annotations"]
      }
    }),
    definition({
      dependencyValues
    }) {
      return {
        setValue: {
          hasAuthorAnnotations: (dependencyValues.annotationsChildren?.length ?? 0) > 0
        }
      };
    }
  };
}
function returnGraphPrefigureStateVariableDefinitions() {
  return {
    curveDescendants: returnGraphCurveDescendantsStateVariableDefinition(),
    curveDescendantComponentIndices: returnGraphCurveDescendantComponentIndicesStateVariableDefinition(),
    functionToCurveComponentIdx: returnGraphFunctionCurveAliasMapStateVariableDefinition(),
    hasAuthorAnnotations: returnGraphHasAuthorAnnotationsStateVariableDefinition(),
    prefigureXML: returnGraphPrefigureXMLStateVariableDefinition()
  };
}
let Graph$1 = class Graph extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      changeAxisLimits: this.changeAxisLimits.bind(this),
      addChildren: this.addChildren.bind(this),
      deleteChildren: this.deleteChildren.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "graph";
  static componentDocs = {
    summary: "A 2D coordinate-axis graph"
  };
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.xMin = {
      createComponentOfType: "number",
      createStateVariable: "xminPrelim",
      defaultValue: -10,
      description: "Minimum value displayed on the x axis."
    };
    attributes.xMax = {
      createComponentOfType: "number",
      createStateVariable: "xmaxPrelim",
      defaultValue: 10,
      description: "Maximum value displayed on the x axis."
    };
    attributes.yMin = {
      createComponentOfType: "number",
      createStateVariable: "yminPrelim",
      defaultValue: -10,
      description: "Minimum value displayed on the y axis."
    };
    attributes.yMax = {
      createComponentOfType: "number",
      createStateVariable: "ymaxPrelim",
      defaultValue: 10,
      description: "Maximum value displayed on the y axis."
    };
    attributes.width = {
      createComponentOfType: "componentSize",
      description: "Explicit width of the graph (overrides size)."
    };
    attributes.size = {
      createComponentOfType: "text",
      createStateVariable: "specifiedSize",
      defaultValue: "medium",
      toLowerCase: true,
      validValues: [
        { value: "tiny", description: "About 1/12 the full width." },
        { value: "small", description: "About 30% of the full width." },
        { value: "medium", description: "About half the full width." },
        { value: "large", description: "About 70% of the full width." },
        { value: "full", description: "The full available width." }
      ],
      description: "Named size preset for the graph."
    };
    attributes.aspectRatio = {
      createComponentOfType: "number",
      description: "Aspect ratio (width / height) for the graph."
    };
    attributes.displayMode = {
      description: "How to size the graph.",
      createComponentOfType: "text",
      createStateVariable: "displayMode",
      validValues: [
        {
          value: "block",
          description: "Display as a block element on its own line."
        },
        {
          value: "inline",
          description: "Render inline with surrounding text."
        }
      ],
      defaultValue: "block",
      toLowerCase: true,
      forRenderer: true,
      public: true
    };
    attributes.horizontalAlign = {
      description: "Horizontal alignment of the graph within its container.",
      createComponentOfType: "text",
      createStateVariable: "horizontalAlign",
      validValues: [
        {
          value: "center",
          description: "Center the graph horizontally."
        },
        {
          value: "left",
          description: "Align the graph to the left edge."
        },
        {
          value: "right",
          description: "Align the graph to the right edge."
        }
      ],
      defaultValue: "center",
      toLowerCase: true,
      forRenderer: true,
      public: true
    };
    attributes.identicalAxisScales = {
      description: "Whether to force the x and y axis scales to be equal.",
      createPrimitiveOfType: "boolean",
      createStateVariable: "identicalAxisScales",
      defaultValue: false,
      public: true
    };
    attributes.displayXAxis = {
      description: "Whether to display the x axis.",
      createComponentOfType: "boolean",
      createStateVariable: "displayXAxis",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.displayYAxis = {
      description: "Whether to display the y axis.",
      createComponentOfType: "boolean",
      createStateVariable: "displayYAxis",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.addControls = {
      description: "Whether to render interactive zoom/pan controls.",
      createComponentOfType: "text",
      createStateVariable: "addControls",
      defaultValue: "none",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "all",
          description: "Render both sliders and input boxes."
        },
        {
          value: "slidersOnly",
          description: "Render only slider controls."
        },
        {
          value: "inputsOnly",
          description: "Render only input-box controls."
        },
        {
          value: "none",
          description: "Render no interactive controls."
        }
      ],
      valueForTrue: "all",
      valueForFalse: "none",
      forRenderer: true
    };
    attributes.controlsPosition = {
      description: "Position of the graph controls.",
      createComponentOfType: "text",
      createStateVariable: "controlsPosition",
      defaultValue: "left",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "bottom",
          description: "Place controls below the graph."
        },
        {
          value: "left",
          description: "Place controls to the left of the graph."
        },
        {
          value: "right",
          description: "Place controls to the right of the graph."
        },
        {
          value: "top",
          description: "Place controls above the graph."
        }
      ]
    };
    attributes.displayXAxisTicks = {
      description: "Whether to display tick marks on the x axis.",
      createComponentOfType: "boolean",
      createStateVariable: "displayXAxisTicks",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.displayYAxisTicks = {
      description: "Whether to display tick marks on the y axis.",
      createComponentOfType: "boolean",
      createStateVariable: "displayYAxisTicks",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.displayXAxisTickLabels = {
      createComponentOfType: "boolean",
      createStateVariable: "displayXAxisTickLabelsPrelim",
      defaultValue: true,
      description: "Whether to display labels on x-axis ticks."
    };
    attributes.displayYAxisTickLabels = {
      createComponentOfType: "boolean",
      createStateVariable: "displayYAxisTickLabelsPrelim",
      defaultValue: true,
      description: "Whether to display labels on y-axis ticks."
    };
    attributes.xLabelPosition = {
      description: "Position of the x-axis label.",
      createComponentOfType: "text",
      createStateVariable: "xLabelPosition",
      defaultValue: "right",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "right",
          description: "Place the x-axis label at the right end of the axis."
        },
        {
          value: "left",
          description: "Place the x-axis label at the left end of the axis."
        }
      ]
    };
    attributes.xTickScaleFactor = {
      description: "Scale factor applied to x-axis tick spacing.",
      createComponentOfType: "math",
      createStateVariable: "xTickScaleFactor",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.yLabelPosition = {
      description: "Position of the y-axis label.",
      createComponentOfType: "text",
      createStateVariable: "yLabelPosition",
      defaultValue: "top",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "top",
          description: "Place the y-axis label at the top of the axis."
        },
        {
          value: "bottom",
          description: "Place the y-axis label at the bottom of the axis."
        }
      ]
    };
    attributes.yLabelAlignment = {
      description: "Alignment of the y-axis label.",
      createComponentOfType: "text",
      createStateVariable: "yLabelAlignment",
      defaultValue: "left",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "left",
          description: "Align the y-axis label to the left of the axis."
        },
        {
          value: "right",
          description: "Align the y-axis label to the right of the axis."
        }
      ]
    };
    attributes.yTickScaleFactor = {
      description: "Scale factor applied to y-axis tick spacing.",
      createComponentOfType: "math",
      createStateVariable: "yTickScaleFactor",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.showNavigation = {
      description: "Whether to show navigation controls (pan/zoom).",
      createComponentOfType: "boolean",
      createStateVariable: "showNavigation",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.fixAxes = {
      createComponentOfType: "boolean",
      createStateVariable: "fixAxesPreliminary",
      defaultValue: false,
      description: "Whether the axis limits are locked (preventing zoom/pan)."
    };
    attributes.grid = {
      createComponentOfType: "text",
      valueForTrue: "medium",
      description: "Grid density to render on the graph (off, minor, medium, or major)."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.showBorder = {
      description: "Whether to render a border around the graph.",
      createComponentOfType: "boolean",
      createStateVariable: "showBorder",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.hideOffGraphIndicators = {
      description: "Whether to suppress indicators for objects outside the visible region.",
      createComponentOfType: "boolean",
      createStateVariable: "hideOffGraphIndicators",
      defaultValue: false,
      public: true
    };
    attributes.decorative = {
      description: "Whether the graph is purely decorative (excluded from a11y tree).",
      createPrimitiveOfType: "boolean",
      createStateVariable: "decorative",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.renderer = {
      description: "Which renderer to use for the graph.",
      createPrimitiveOfType: "string",
      createStateVariable: "renderer",
      validValues: [
        {
          value: "doenet",
          description: "Render using the built-in Doenet graph renderer."
        },
        {
          value: "prefigure",
          description: "Render using the PreFigure SVG-based renderer."
        }
      ],
      defaultValue: "doenet",
      public: true,
      toLowerCase: true,
      forRenderer: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "xLabels",
        componentTypes: ["xLabel"]
      },
      {
        group: "yLabels",
        componentTypes: ["yLabel"]
      },
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      },
      {
        group: "graphical",
        componentTypes: [
          "_graphical",
          "image",
          "text",
          "math",
          "m",
          "md",
          "label",
          "number",
          "updateValue",
          "callAction",
          "triggerSet",
          "booleanInput",
          "textInput",
          "mathInput"
        ]
      },
      {
        group: "graphs",
        componentTypes: ["graph"]
      },
      {
        group: "annotations",
        componentTypes: ["annotations"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnListItemChildStateVariableDefinitions({
        listItemInlineAlignment: "flex-start"
      })
    );
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.shortDescription = {
      description: "A short accessibility description of the graph.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        shortDescriptionChild: {
          dependencyType: "child",
          childGroups: ["shortDescriptions"],
          variableNames: ["text"]
        },
        decorative: {
          dependencyType: "stateVariable",
          variableName: "decorative"
        }
      }),
      definition({ dependencyValues }) {
        let shortDescription = "";
        const diagnostics = [];
        if (dependencyValues.shortDescriptionChild.length > 0) {
          const shortDescriptionChild = dependencyValues.shortDescriptionChild[dependencyValues.shortDescriptionChild.length - 1];
          shortDescription = shortDescriptionChild.stateValues.text.trim();
        }
        if (shortDescription === "" && !dependencyValues.decorative) {
          diagnostics.push({
            type: "accessibility",
            level: 1,
            message: "For accessibility, `<graph>` must either have a short description or be specified as decorative."
          });
        }
        return {
          setValue: { shortDescription },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.descriptionChildInd = {
      forRenderer: true,
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            descriptionChildInd: dependencyValues.allChildren.findLastIndex(
              (child) => child.componentType === "description"
            )
          }
        };
      }
    };
    stateVariableDefinitions.fixAxes = {
      description: "Whether the visible axes range is locked.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        fixAxesPreliminary: {
          dependencyType: "stateVariable",
          variableName: "fixAxesPreliminary"
        },
        fixed: {
          dependencyType: "stateVariable",
          variableName: "fixed"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            fixAxes: dependencyValues.fixAxesPreliminary || dependencyValues.fixed
          }
        };
      }
    };
    stateVariableDefinitions.xLabel = {
      description: "The x-axis label text.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "label",
        addStateVariablesShadowingStateVariables: {
          hasLatex: {
            stateVariableToShadow: "xLabelHasLatex"
          }
        }
      },
      hasEssential: true,
      defaultValue: "",
      additionalStateVariablesDefined: [
        {
          variableName: "xLabelHasLatex",
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        xLabelChild: {
          dependencyType: "child",
          childGroups: ["xLabels"],
          variableNames: ["value", "hasLatex"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.xLabelChild.length > 0) {
          let xLabelChild = dependencyValues.xLabelChild[dependencyValues.xLabelChild.length - 1];
          return {
            setValue: {
              xLabel: xLabelChild.stateValues.value,
              xLabelHasLatex: xLabelChild.stateValues.hasLatex
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { xLabel: true },
            setValue: { xLabelHasLatex: false }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (typeof desiredStateVariableValues.xLabel !== "string") {
          return { success: false };
        }
        if (dependencyValues.xLabelChild.length > 0) {
          let lastLabelInd = dependencyValues.xLabelChild.length - 1;
          return {
            success: true,
            instructions: [
              {
                setDependency: "xLabelChild",
                desiredValue: desiredStateVariableValues.xLabel,
                childIndex: lastLabelInd,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "xLabel",
                value: desiredStateVariableValues.xLabel
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.yLabel = {
      description: "The y-axis label text.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "label",
        addStateVariablesShadowingStateVariables: {
          hasLatex: {
            stateVariableToShadow: "yLabelHasLatex"
          }
        }
      },
      hasEssential: true,
      defaultValue: "",
      additionalStateVariablesDefined: [
        {
          variableName: "yLabelHasLatex",
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        yLabelChild: {
          dependencyType: "child",
          childGroups: ["yLabels"],
          variableNames: ["value", "hasLatex"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.yLabelChild.length > 0) {
          let yLabelChild = dependencyValues.yLabelChild[dependencyValues.yLabelChild.length - 1];
          return {
            setValue: {
              yLabel: yLabelChild.stateValues.value,
              yLabelHasLatex: yLabelChild.stateValues.hasLatex
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { yLabel: true },
            setValue: { yLabelHasLatex: false }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (typeof desiredStateVariableValues.yLabel !== "string") {
          return { success: false };
        }
        if (dependencyValues.yLabelChild.length > 0) {
          let lastLabelInd = dependencyValues.yLabelChild.length - 1;
          return {
            success: true,
            instructions: [
              {
                setDependency: "yLabelChild",
                desiredValue: desiredStateVariableValues.yLabel,
                childIndex: lastLabelInd,
                variableIndex: 0
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "yLabel",
                value: desiredStateVariableValues.yLabel
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.graphicalDescendants = {
      forRenderer: true,
      returnDependencies: () => ({
        graphicalDescendants: {
          dependencyType: "descendant",
          componentTypes: ["_graphical"]
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            graphicalDescendants: dependencyValues.graphicalDescendants
          }
        };
      }
    };
    stateVariableDefinitions.graphicalDescendantsForControls = {
      forRenderer: true,
      returnDependencies: () => ({
        addControls: {
          dependencyType: "stateVariable",
          variableName: "addControls"
        },
        controlDescendants: {
          dependencyType: "descendant",
          componentTypes: GRAPH_CONTROL_COMPONENT_TYPES,
          variableNames: GRAPH_CONTROL_VARIABLE_NAMES,
          variablesOptional: true
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        if (dependencyValues.addControls === "none") {
          return {
            setValue: {
              graphicalDescendantsForControls: []
            }
          };
        }
        const graphicalDescendantsForControls = [];
        const countersByType = Object.fromEntries(
          GRAPH_CONTROL_DESCENDANT_CONFIGS.map((config) => [
            config.controlType,
            0
          ])
        );
        for (const descendant of dependencyValues.controlDescendants ?? []) {
          const componentType = descendant.componentType;
          for (const config of GRAPH_CONTROL_DESCENDANT_CONFIGS) {
            if (!componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: componentType,
              baseComponentType: config.componentType
            })) {
              continue;
            }
            countersByType[config.controlType] += 1;
            const componentIdx = descendant.componentIdx;
            if (!Number.isFinite(componentIdx)) {
              break;
            }
            const payload = config.buildPayload({
              stateValues: descendant.stateValues ?? {},
              componentIdx,
              number: countersByType[config.controlType]
            });
            if (payload !== null) {
              graphicalDescendantsForControls.push(payload);
            }
            break;
          }
        }
        return {
          setValue: {
            graphicalDescendantsForControls
          }
        };
      }
    };
    Object.assign(
      stateVariableDefinitions,
      returnGraphPrefigureStateVariableDefinitions()
    );
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        graphicalOrGraphChildren: {
          dependencyType: "child",
          childGroups: ["graphical", "graphs"]
        },
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        },
        descriptionChildInd: {
          dependencyType: "stateVariable",
          variableName: "descriptionChildInd"
        }
      }),
      definition({ dependencyValues }) {
        const childIndicesToRender = [];
        const graphicalChildNames = dependencyValues.graphicalOrGraphChildren.map(
          (x2) => x2.componentIdx
        );
        for (const [
          ind,
          child
        ] of dependencyValues.allChildren.entries()) {
          if (graphicalChildNames.includes(child.componentIdx)) {
            childIndicesToRender.push(ind);
          }
        }
        if (dependencyValues.descriptionChildInd !== -1) {
          childIndicesToRender.push(
            dependencyValues.descriptionChildInd
          );
        }
        return { setValue: { childIndicesToRender } };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.numChildrenAdded = {
      defaultValue: 0,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { numChildrenAdded: true }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "numChildrenAdded",
              value: desiredStateVariableValues.numChildrenAdded
            }
          ]
        };
      }
    };
    stateVariableDefinitions.size = {
      description: "The size of the graph.",
      public: true,
      defaultValue: "medium",
      hasEssential: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        specifiedSize: {
          dependencyType: "stateVariable",
          variableName: "specifiedSize"
        },
        widthAttr: {
          dependencyType: "attributeComponent",
          attributeName: "width",
          variableNames: ["componentSize"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        const defaultSize = "medium";
        if (!usedDefault.specifiedSize) {
          return {
            setValue: { size: dependencyValues.specifiedSize }
          };
        } else if (dependencyValues.widthAttr) {
          let componentSize = dependencyValues.widthAttr.stateValues.componentSize;
          if (componentSize === null) {
            return {
              setValue: { size: defaultSize }
            };
          }
          let { isAbsolute, size: widthSize } = componentSize;
          let size;
          if (isAbsolute) {
            for (let [
              ind,
              pixels
            ] of orderedWidthMidpoints.entries()) {
              if (widthSize <= pixels) {
                size = sizePossibilities[ind];
                break;
              }
            }
            if (!size) {
              size = defaultSize;
            }
          } else {
            for (let [
              ind,
              percent
            ] of orderedPercentWidthMidpoints.entries()) {
              if (widthSize <= percent) {
                size = sizePossibilities[ind];
                break;
              }
            }
            if (!size) {
              size = defaultSize;
            }
          }
          return {
            setValue: { size }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { size: true }
          };
        }
      }
    };
    stateVariableDefinitions.width = {
      description: "The width of the graph.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      returnDependencies: () => ({
        size: {
          dependencyType: "stateVariable",
          variableName: "size"
        }
      }),
      definition({ dependencyValues }) {
        let width = {
          isAbsolute: true,
          size: widthsBySize[dependencyValues.size]
        };
        return {
          setValue: { width }
        };
      }
    };
    stateVariableDefinitions.aspectRatioFromAxisScales = {
      returnDependencies: () => ({
        aspectRatioAttr: {
          dependencyType: "attributeComponent",
          attributeName: "aspectRatio",
          variableNames: ["value"]
        },
        identicalAxisScales: {
          dependencyType: "stateVariable",
          variableName: "identicalAxisScales"
        }
      }),
      definition({ dependencyValues }) {
        let aspectRatioFromAxisScales = dependencyValues.identicalAxisScales && dependencyValues.aspectRatioAttr === null;
        return {
          setValue: { aspectRatioFromAxisScales },
          checkForActualChange: { aspectRatioFromAxisScales: true }
        };
      }
    };
    stateVariableDefinitions.aspectRatio = {
      description: "The aspect ratio (width / height) of the graph.",
      public: true,
      forRenderer: true,
      defaultValue: 1,
      hasEssential: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      stateVariablesDeterminingDependencies: [
        "aspectRatioFromAxisScales"
      ],
      returnDependencies({ stateValues }) {
        if (stateValues.aspectRatioFromAxisScales) {
          return {
            aspectRatioFromAxisScales: {
              dependencyType: "stateVariable",
              variableName: "aspectRatioFromAxisScales"
            },
            xScale: {
              dependencyType: "stateVariable",
              variableName: "xScale"
            },
            yScale: {
              dependencyType: "stateVariable",
              variableName: "yScale"
            }
          };
        } else {
          return {
            aspectRatioFromAxisScales: {
              dependencyType: "stateVariable",
              variableName: "aspectRatioFromAxisScales"
            },
            aspectRatioAttr: {
              dependencyType: "attributeComponent",
              attributeName: "aspectRatio",
              variableNames: ["value"]
            },
            width: {
              dependencyType: "stateVariable",
              variableName: "width"
            }
          };
        }
      },
      definition({ dependencyValues }) {
        if (dependencyValues.aspectRatioFromAxisScales) {
          let aspectRatio = dependencyValues.xScale / dependencyValues.yScale;
          return {
            setValue: { aspectRatio }
          };
        } else if (dependencyValues.aspectRatioAttr !== null) {
          let aspectRatio = dependencyValues.aspectRatioAttr.stateValues.value;
          if (!Number.isFinite(aspectRatio)) {
            aspectRatio = 1;
          }
          return {
            setValue: { aspectRatio }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { aspectRatio: true }
          };
        }
      }
    };
    stateVariableDefinitions.haveGraphParent = {
      forRenderer: true,
      returnDependencies: () => ({
        graphParent: {
          dependencyType: "parentIdentity",
          parentComponentType: "graph"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            haveGraphParent: dependencyValues.graphParent !== null
          }
        };
      }
    };
    stateVariableDefinitions.effectiveRenderer = {
      description: "The renderer actually used after resolving fallbacks.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        renderer: {
          dependencyType: "stateVariable",
          variableName: "renderer"
        },
        graphParentRenderer: {
          dependencyType: "parentStateVariable",
          parentComponentType: "graph",
          variableName: "effectiveRenderer"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            effectiveRenderer: dependencyValues.graphParentRenderer ?? dependencyValues.renderer
          }
        };
      }
    };
    stateVariableDefinitions.displayXAxisTickLabels = {
      description: "Whether x-axis tick labels are displayed.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        displayXAxisTickLabelsPrelim: {
          dependencyType: "stateVariable",
          variableName: "displayXAxisTickLabelsPrelim"
        },
        displayXAxisTicks: {
          dependencyType: "stateVariable",
          variableName: "displayXAxisTicks"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (usedDefault.displayXAxisTickLabelsPrelim) {
          return {
            setValue: {
              displayXAxisTickLabels: dependencyValues.displayXAxisTicks
            }
          };
        } else {
          return {
            setValue: {
              displayXAxisTickLabels: dependencyValues.displayXAxisTickLabelsPrelim
            }
          };
        }
      }
    };
    stateVariableDefinitions.displayYAxisTickLabels = {
      description: "Whether y-axis tick labels are displayed.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        displayYAxisTickLabelsPrelim: {
          dependencyType: "stateVariable",
          variableName: "displayYAxisTickLabelsPrelim"
        },
        displayYAxisTicks: {
          dependencyType: "stateVariable",
          variableName: "displayYAxisTicks"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (usedDefault.displayYAxisTickLabelsPrelim) {
          return {
            setValue: {
              displayYAxisTickLabels: dependencyValues.displayYAxisTicks
            }
          };
        } else {
          return {
            setValue: {
              displayYAxisTickLabels: dependencyValues.displayYAxisTickLabelsPrelim
            }
          };
        }
      }
    };
    stateVariableDefinitions.xMin = {
      description: "Minimum x value displayed.",
      stateVariablesDeterminingDependencies: [
        "identicalAxisScales",
        "aspectRatioFromAxisScales"
      ],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      returnDependencies({ stateValues }) {
        let dependencies = {
          identicalAxisScales: {
            dependencyType: "stateVariable",
            variableName: "identicalAxisScales"
          },
          aspectRatioFromAxisScales: {
            dependencyType: "stateVariable",
            variableName: "aspectRatioFromAxisScales"
          },
          xminPrelim: {
            dependencyType: "stateVariable",
            variableName: "xminPrelim"
          },
          graphParentXmin: {
            dependencyType: "parentStateVariable",
            parentComponentType: "graph",
            variableName: "xMin"
          }
        };
        if (stateValues.identicalAxisScales && !stateValues.aspectRatioFromAxisScales) {
          dependencies.xmaxPrelim = {
            dependencyType: "stateVariable",
            variableName: "xmaxPrelim"
          };
          dependencies.yminPrelim = {
            dependencyType: "stateVariable",
            variableName: "yminPrelim"
          };
          dependencies.ymaxPrelim = {
            dependencyType: "stateVariable",
            variableName: "ymaxPrelim"
          };
          dependencies.aspectRatio = {
            dependencyType: "stateVariable",
            variableName: "aspectRatio"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.graphParentXmin !== null) {
          return {
            setValue: { xMin: dependencyValues.graphParentXmin }
          };
        }
        if (!dependencyValues.identicalAxisScales || dependencyValues.aspectRatioFromAxisScales) {
          return { setValue: { xMin: dependencyValues.xminPrelim } };
        }
        let xminSpecified = !usedDefault.xminPrelim;
        if (xminSpecified) {
          return { setValue: { xMin: dependencyValues.xminPrelim } };
        }
        let xmaxSpecified = !usedDefault.xmaxPrelim;
        let yminSpecified = !usedDefault.yminPrelim;
        let ymaxSpecified = !usedDefault.ymaxPrelim;
        let yscaleSpecified = yminSpecified && ymaxSpecified;
        if (yscaleSpecified) {
          let aspectRatio = dependencyValues.aspectRatio;
          let yscaleAdjusted = (dependencyValues.ymaxPrelim - dependencyValues.yminPrelim) * aspectRatio;
          if (xmaxSpecified) {
            return {
              setValue: {
                xMin: dependencyValues.xmaxPrelim - yscaleAdjusted
              }
            };
          } else {
            return { setValue: { xMin: -yscaleAdjusted / 2 } };
          }
        } else {
          if (xmaxSpecified) {
            return {
              setValue: {
                xMin: dependencyValues.xmaxPrelim - 20
              }
            };
          } else {
            return { setValue: { xMin: -10 } };
          }
        }
      },
      markStale: () => ({ updateDescendantRenderers: true }),
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (dependencyValues.graphParentXmin !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "graphParentXmin",
                desiredValue: desiredStateVariableValues.xMin
              }
            ]
          };
        }
        if (await stateValues.fixAxes) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "xminPrelim",
              desiredValue: desiredStateVariableValues.xMin
            }
          ]
        };
      }
    };
    stateVariableDefinitions.xMax = {
      description: "Maximum x value displayed.",
      stateVariablesDeterminingDependencies: [
        "identicalAxisScales",
        "aspectRatioFromAxisScales"
      ],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      returnDependencies({ stateValues }) {
        let dependencies = {
          identicalAxisScales: {
            dependencyType: "stateVariable",
            variableName: "identicalAxisScales"
          },
          aspectRatioFromAxisScales: {
            dependencyType: "stateVariable",
            variableName: "aspectRatioFromAxisScales"
          },
          xmaxPrelim: {
            dependencyType: "stateVariable",
            variableName: "xmaxPrelim"
          },
          graphParentXmax: {
            dependencyType: "parentStateVariable",
            parentComponentType: "graph",
            variableName: "xMax"
          }
        };
        if (stateValues.identicalAxisScales && !stateValues.aspectRatioFromAxisScales) {
          dependencies.xminPrelim = {
            dependencyType: "stateVariable",
            variableName: "xminPrelim"
          };
          dependencies.yminPrelim = {
            dependencyType: "stateVariable",
            variableName: "yminPrelim"
          };
          dependencies.ymaxPrelim = {
            dependencyType: "stateVariable",
            variableName: "ymaxPrelim"
          };
          dependencies.aspectRatio = {
            dependencyType: "stateVariable",
            variableName: "aspectRatio"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.graphParentXmax !== null) {
          return {
            setValue: { xMax: dependencyValues.graphParentXmax }
          };
        }
        if (!dependencyValues.identicalAxisScales || dependencyValues.aspectRatioFromAxisScales) {
          return { setValue: { xMax: dependencyValues.xmaxPrelim } };
        }
        let xminSpecified = !usedDefault.xminPrelim;
        let xmaxSpecified = !usedDefault.xmaxPrelim;
        let yminSpecified = !usedDefault.yminPrelim;
        let ymaxSpecified = !usedDefault.ymaxPrelim;
        let yscaleSpecified = yminSpecified && ymaxSpecified;
        let xscaleSpecified = xminSpecified && xmaxSpecified;
        let xMin = dependencyValues.xminPrelim;
        if (yscaleSpecified) {
          let aspectRatio = dependencyValues.aspectRatio;
          let yscaleAdjusted = (dependencyValues.ymaxPrelim - dependencyValues.yminPrelim) * aspectRatio;
          if (xscaleSpecified) {
            let xscale = dependencyValues.xmaxPrelim - xMin;
            let maxScale = Math.max(xscale, yscaleAdjusted);
            return { setValue: { xMax: xMin + maxScale } };
          } else {
            if (xminSpecified) {
              return {
                setValue: { xMax: xMin + yscaleAdjusted }
              };
            } else if (xmaxSpecified) {
              return {
                setValue: { xMax: dependencyValues.xmaxPrelim }
              };
            } else {
              return { setValue: { xMax: yscaleAdjusted / 2 } };
            }
          }
        } else {
          if (xmaxSpecified) {
            return {
              setValue: { xMax: dependencyValues.xmaxPrelim }
            };
          } else if (xminSpecified) {
            return { setValue: { xMax: xMin + 20 } };
          } else {
            return { setValue: { xMax: 10 } };
          }
        }
      },
      markStale: () => ({ updateDescendantRenderers: true }),
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (dependencyValues.graphParentXmax !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "graphParentXmax",
                desiredValue: desiredStateVariableValues.xMax
              }
            ]
          };
        }
        if (await stateValues.fixAxes) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "xmaxPrelim",
              desiredValue: desiredStateVariableValues.xMax
            }
          ]
        };
      }
    };
    stateVariableDefinitions.yMin = {
      description: "Minimum y value displayed.",
      stateVariablesDeterminingDependencies: [
        "identicalAxisScales",
        "aspectRatioFromAxisScales"
      ],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      returnDependencies({ stateValues }) {
        let dependencies = {
          identicalAxisScales: {
            dependencyType: "stateVariable",
            variableName: "identicalAxisScales"
          },
          aspectRatioFromAxisScales: {
            dependencyType: "stateVariable",
            variableName: "aspectRatioFromAxisScales"
          },
          yminPrelim: {
            dependencyType: "stateVariable",
            variableName: "yminPrelim"
          },
          graphParentYmin: {
            dependencyType: "parentStateVariable",
            parentComponentType: "graph",
            variableName: "yMin"
          }
        };
        if (stateValues.identicalAxisScales && !stateValues.aspectRatioFromAxisScales) {
          dependencies.xmaxPrelim = {
            dependencyType: "stateVariable",
            variableName: "xmaxPrelim"
          };
          dependencies.xminPrelim = {
            dependencyType: "stateVariable",
            variableName: "xminPrelim"
          };
          dependencies.ymaxPrelim = {
            dependencyType: "stateVariable",
            variableName: "ymaxPrelim"
          };
          dependencies.aspectRatio = {
            dependencyType: "stateVariable",
            variableName: "aspectRatio"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.graphParentYmin !== null) {
          return {
            setValue: { yMin: dependencyValues.graphParentYmin }
          };
        }
        if (!dependencyValues.identicalAxisScales || dependencyValues.aspectRatioFromAxisScales) {
          return { setValue: { yMin: dependencyValues.yminPrelim } };
        }
        let yminSpecified = !usedDefault.yminPrelim;
        if (yminSpecified) {
          return { setValue: { yMin: dependencyValues.yminPrelim } };
        }
        let ymaxSpecified = !usedDefault.ymaxPrelim;
        let xminSpecified = !usedDefault.xminPrelim;
        let xmaxSpecified = !usedDefault.xmaxPrelim;
        let xscaleSpecified = xminSpecified && xmaxSpecified;
        let aspectRatio = dependencyValues.aspectRatio;
        if (xscaleSpecified) {
          let xscaleAdjusted = (dependencyValues.xmaxPrelim - dependencyValues.xminPrelim) / aspectRatio;
          if (ymaxSpecified) {
            return {
              setValue: {
                yMin: dependencyValues.ymaxPrelim - xscaleAdjusted
              }
            };
          } else {
            return { setValue: { yMin: -xscaleAdjusted / 2 } };
          }
        } else {
          if (ymaxSpecified) {
            return {
              setValue: {
                yMin: dependencyValues.ymaxPrelim - 20 / aspectRatio
              }
            };
          } else {
            return { setValue: { yMin: -10 / aspectRatio } };
          }
        }
      },
      markStale: () => ({ updateDescendantRenderers: true }),
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (dependencyValues.graphParentYmin !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "graphParentYmin",
                desiredValue: desiredStateVariableValues.yMin
              }
            ]
          };
        }
        if (await stateValues.fixAxes) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "yminPrelim",
              desiredValue: desiredStateVariableValues.yMin
            }
          ]
        };
      }
    };
    stateVariableDefinitions.yMax = {
      description: "Maximum y value displayed.",
      stateVariablesDeterminingDependencies: [
        "identicalAxisScales",
        "aspectRatioFromAxisScales"
      ],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      returnDependencies({ stateValues }) {
        let dependencies = {
          identicalAxisScales: {
            dependencyType: "stateVariable",
            variableName: "identicalAxisScales"
          },
          aspectRatioFromAxisScales: {
            dependencyType: "stateVariable",
            variableName: "aspectRatioFromAxisScales"
          },
          ymaxPrelim: {
            dependencyType: "stateVariable",
            variableName: "ymaxPrelim"
          },
          graphParentYmax: {
            dependencyType: "parentStateVariable",
            parentComponentType: "graph",
            variableName: "yMax"
          }
        };
        if (stateValues.identicalAxisScales && !stateValues.aspectRatioFromAxisScales) {
          dependencies.xminPrelim = {
            dependencyType: "stateVariable",
            variableName: "xminPrelim"
          };
          dependencies.yminPrelim = {
            dependencyType: "stateVariable",
            variableName: "yminPrelim"
          };
          dependencies.xmaxPrelim = {
            dependencyType: "stateVariable",
            variableName: "xmaxPrelim"
          };
          dependencies.aspectRatio = {
            dependencyType: "stateVariable",
            variableName: "aspectRatio"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.graphParentYmax !== null) {
          return {
            setValue: { yMax: dependencyValues.graphParentYmax }
          };
        }
        if (!dependencyValues.identicalAxisScales || dependencyValues.aspectRatioFromAxisScales) {
          return { setValue: { yMax: dependencyValues.ymaxPrelim } };
        }
        let xminSpecified = !usedDefault.xminPrelim;
        let xmaxSpecified = !usedDefault.xmaxPrelim;
        let yminSpecified = !usedDefault.yminPrelim;
        let ymaxSpecified = !usedDefault.ymaxPrelim;
        let yscaleSpecified = yminSpecified && ymaxSpecified;
        let xscaleSpecified = xminSpecified && xmaxSpecified;
        let yMin = dependencyValues.yminPrelim;
        let aspectRatio = dependencyValues.aspectRatio;
        if (xscaleSpecified) {
          let xscaleAdjusted = (dependencyValues.xmaxPrelim - dependencyValues.xminPrelim) / aspectRatio;
          if (yscaleSpecified) {
            let yscale = dependencyValues.ymaxPrelim - yMin;
            let maxScale = Math.max(yscale, xscaleAdjusted);
            return { setValue: { yMax: yMin + maxScale } };
          } else {
            if (yminSpecified) {
              return {
                setValue: { yMax: yMin + xscaleAdjusted }
              };
            } else if (ymaxSpecified) {
              return {
                setValue: { yMax: dependencyValues.ymaxPrelim }
              };
            } else {
              return { setValue: { yMax: xscaleAdjusted / 2 } };
            }
          }
        } else {
          if (ymaxSpecified) {
            return {
              setValue: { yMax: dependencyValues.ymaxPrelim }
            };
          } else if (yminSpecified) {
            return { setValue: { yMax: yMin + 20 / aspectRatio } };
          } else {
            return { setValue: { yMax: 10 / aspectRatio } };
          }
        }
      },
      markStale: () => ({ updateDescendantRenderers: true }),
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (dependencyValues.graphParentYmax !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "graphParentYmax",
                desiredValue: desiredStateVariableValues.yMax
              }
            ]
          };
        }
        if (await stateValues.fixAxes) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "ymaxPrelim",
              desiredValue: desiredStateVariableValues.yMax
            }
          ]
        };
      }
    };
    stateVariableDefinitions.boundingbox = {
      forRenderer: true,
      returnDependencies: () => ({
        xMin: {
          dependencyType: "stateVariable",
          variableName: "xMin"
        },
        xMax: {
          dependencyType: "stateVariable",
          variableName: "xMax"
        },
        yMin: {
          dependencyType: "stateVariable",
          variableName: "yMin"
        },
        yMax: {
          dependencyType: "stateVariable",
          variableName: "yMax"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            boundingbox: [
              dependencyValues.xMin,
              dependencyValues.yMax,
              dependencyValues.xMax,
              dependencyValues.yMin
            ]
          }
        };
      }
    };
    function returnScaleInverseDefinition({
      scaleStateVariable,
      minDependency,
      maxDependency
    }) {
      return function inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let desiredScale = desiredStateVariableValues[scaleStateVariable];
        let midpoint = dependencyValues[minDependency] / 2 + dependencyValues[maxDependency] / 2;
        let desiredMin = midpoint - desiredScale / 2;
        let desiredMax = midpoint + desiredScale / 2;
        if (!Number.isFinite(desiredScale) || desiredScale <= 0 || !Number.isFinite(midpoint) || !Number.isFinite(desiredMin) || !Number.isFinite(desiredMax)) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: minDependency,
              desiredValue: desiredMin
            },
            {
              setDependency: maxDependency,
              desiredValue: desiredMax
            }
          ]
        };
      };
    }
    stateVariableDefinitions.xScale = {
      description: "Scale used along the x axis (xMax − xMin).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        xMin: {
          dependencyType: "stateVariable",
          variableName: "xMin"
        },
        xMax: {
          dependencyType: "stateVariable",
          variableName: "xMax"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            xScale: dependencyValues.xMax - dependencyValues.xMin
          }
        };
      },
      inverseDefinition: returnScaleInverseDefinition({
        scaleStateVariable: "xScale",
        minDependency: "xMin",
        maxDependency: "xMax"
      })
    };
    stateVariableDefinitions.yScale = {
      description: "Scale used along the y axis (yMax − yMin).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        yMin: {
          dependencyType: "stateVariable",
          variableName: "yMin"
        },
        yMax: {
          dependencyType: "stateVariable",
          variableName: "yMax"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            yScale: dependencyValues.yMax - dependencyValues.yMin
          }
        };
      },
      inverseDefinition: returnScaleInverseDefinition({
        scaleStateVariable: "yScale",
        minDependency: "yMin",
        maxDependency: "yMax"
      })
    };
    stateVariableDefinitions.gridAttrCompName = {
      returnDependencies: () => ({
        gridAttr: {
          dependencyType: "attributeComponent",
          attributeName: "grid"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.gridAttr) {
          return {
            setValue: {
              gridAttrCompName: dependencyValues.gridAttr.componentIdx
            }
          };
        } else {
          return { setValue: { gridAttrCompName: null } };
        }
      }
    };
    stateVariableDefinitions.gridAttrCompChildren = {
      stateVariablesDeterminingDependencies: ["gridAttrCompName"],
      returnDependencies: ({ stateValues }) => {
        if (stateValues.gridAttrCompName) {
          return {
            gridAttrCompChildren: {
              dependencyType: "child",
              parentIdx: stateValues.gridAttrCompName,
              childGroups: ["textLike"],
              variableNames: ["value"]
            }
          };
        } else {
          return {};
        }
      },
      definition({ dependencyValues }) {
        if (dependencyValues.gridAttrCompChildren) {
          return {
            setValue: {
              gridAttrCompChildren: dependencyValues.gridAttrCompChildren
            }
          };
        } else {
          return { setValue: { gridAttrCompChildren: null } };
        }
      }
    };
    stateVariableDefinitions.grid = {
      description: "Grid line spacing on the graph.",
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true,
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["gridAttrCompChildren"],
      returnDependencies({ stateValues }) {
        if (stateValues.gridAttrCompChildren) {
          let dependencies = {
            gridAttrCompChildren: {
              dependencyType: "stateVariable",
              variableName: "gridAttrCompChildren"
            },
            gridAttr: {
              dependencyType: "attributeComponent",
              attributeName: "grid",
              variableNames: ["value"]
            }
          };
          for (let [
            ind,
            child
          ] of stateValues.gridAttrCompChildren.entries()) {
            dependencies["childAdapter" + ind] = {
              dependencyType: "adapterSourceStateVariable",
              componentIdx: child.componentIdx,
              variableName: "value"
            };
          }
          return dependencies;
        } else {
          return {};
        }
      },
      definition({ dependencyValues }) {
        if (!dependencyValues.gridAttrCompChildren) {
          return {
            setValue: { grid: "none" },
            setCreateComponentOfType: { grid: "text" }
          };
        }
        let grid = dependencyValues.gridAttr.stateValues.value.toLowerCase().trim();
        if (grid === "true") {
          grid = "medium";
        } else if (grid === "false") {
          grid = "none";
        }
        if (["medium", "dense", "none"].includes(grid)) {
          return {
            setValue: { grid },
            setCreateComponentOfType: { grid: "text" }
          };
        }
        let groupedChildren = [];
        let pieces = [];
        for (let child of dependencyValues.gridAttrCompChildren) {
          if (typeof child !== "string") {
            pieces.push(child);
          } else {
            let stringPieces = child.split(/\s+/);
            let s0 = stringPieces[0];
            if (s0 === "") {
              if (pieces.length > 0) {
                groupedChildren.push(pieces);
                pieces = [];
              }
            } else {
              pieces.push(s0);
            }
            for (let s2 of stringPieces.slice(1)) {
              if (pieces.length > 0) {
                groupedChildren.push(pieces);
                pieces = [];
              }
              if (s2 !== "") {
                pieces.push(s2);
              }
            }
          }
        }
        if (pieces.length > 0) {
          groupedChildren.push(pieces);
        }
        if (groupedChildren.length < 2) {
          return {
            setValue: { grid: "none" },
            setCreateComponentOfType: { grid: "text" }
          };
        }
        grid = [];
        for (let group of groupedChildren) {
          if (group.length === 1) {
            let child = group[0];
            if (typeof child === "string") {
              let num = me$3.fromText(child).evaluate_to_constant();
              if (num > 0) {
                grid.push(num);
              } else {
                return {
                  setValue: { grid: "none" },
                  setCreateComponentOfType: { grid: "text" }
                };
              }
            } else {
              let childInd = dependencyValues.gridAttrCompChildren.indexOf(
                child
              );
              let num = dependencyValues["childAdapter" + childInd];
              if (num instanceof me$3.class) {
                num = num.evaluate_to_constant();
              }
              if (num > 0) {
                grid.push(num);
              } else {
                return {
                  setValue: { grid: "none" },
                  setCreateComponentOfType: { grid: "text" }
                };
              }
            }
          } else {
            let num = 1;
            for (let piece of group) {
              if (typeof piece === "string") {
                num *= me$3.fromText(piece).evaluate_to_constant();
              } else {
                let childInd = dependencyValues.gridAttrCompChildren.indexOf(
                  piece
                );
                let factor = dependencyValues["childAdapter" + childInd];
                if (factor instanceof me$3.class) {
                  factor = factor.evaluate_to_constant();
                }
                num *= factor;
              }
            }
            if (num > 0) {
              grid.push(num);
            } else {
              return {
                setValue: { grid: "none" },
                setCreateComponentOfType: { grid: "text" }
              };
            }
          }
        }
        return {
          setValue: { grid },
          setCreateComponentOfType: { grid: "numberList" }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async changeAxisLimits({
    xMin,
    xMax,
    yMin,
    yMax,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let updateInstructions = [];
    if (xMin !== void 0) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "xMin",
        value: xMin
      });
    }
    if (xMax !== void 0) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "xMax",
        value: xMax
      });
    }
    if (yMin !== void 0) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "yMin",
        value: yMin
      });
    }
    if (yMax !== void 0) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "yMax",
        value: yMax
      });
    }
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          xMin,
          xMax,
          yMin,
          yMax
        }
      }
    });
  }
  async addChildren(args) {
    return await addChildrenToDynamicChild(this, args);
  }
  async deleteChildren(args) {
    return await deleteChildrenFromDynamicChild(this, args);
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
};
class Annotations extends BaseComponent {
  static componentType = "annotations";
  static rendererType = void 0;
  static componentDocs = {
    summary: "A container of annotation children for screen reader navigation of a graph"
  };
  static returnChildGroups() {
    return [
      {
        group: "annotations",
        componentTypes: ["annotation"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    const stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.annotationSubtrees = {
      returnDependencies: () => ({
        annotationChildren: {
          dependencyType: "child",
          childGroups: ["annotations"],
          variableNames: ["prefigureAnnotationNode"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            annotationSubtrees: dependencyValues.annotationChildren.map(
              (child) => child?.stateValues?.prefigureAnnotationNode
            ).filter((x2) => x2 !== null && x2 !== void 0)
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Annotation extends BaseComponent {
  static componentType = "annotation";
  static componentDocs = {
    summary: "An annotation for screen reader navigation of a graph"
  };
  static rendererType = void 0;
  static createAttributesObject() {
    const attributes = super.createAttributesObject();
    attributes.ref = {
      createReferences: true,
      description: "Reference to the component this annotation is attached to."
    };
    attributes.text = {
      createComponentOfType: "text",
      createStateVariable: "text",
      defaultValue: "",
      public: true,
      description: "The annotation text read by screen readers."
    };
    attributes.speech = {
      createComponentOfType: "text",
      createStateVariable: "speech",
      defaultValue: "",
      public: true,
      description: "More detailed spoken text used by screen readers; used if expert mode is activated or extra details are requested."
    };
    attributes.sonify = {
      createComponentOfType: "boolean",
      createStateVariable: "sonify",
      defaultValue: false,
      public: true,
      description: "Whether to play a tone when this annotation is focused and it references a function."
    };
    attributes.circular = {
      createComponentOfType: "boolean",
      createStateVariable: "circular",
      defaultValue: false,
      public: true,
      description: "Whether navigation through this annotation's children wraps around."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "annotations",
        componentTypes: ["annotation"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    const stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.refData = {
      returnDependencies: () => ({
        refResolutions: {
          dependencyType: "attributeRefResolutions",
          attributeName: "ref"
        }
      }),
      definition({ dependencyValues }) {
        const refResolutions = dependencyValues.refResolutions ?? [];
        return {
          setValue: {
            refData: {
              hasRefAttribute: refResolutions.length > 0,
              refResolutions
            }
          }
        };
      }
    };
    stateVariableDefinitions.prefigureAnnotationNode = {
      returnDependencies: () => ({
        text: {
          dependencyType: "stateVariable",
          variableName: "text"
        },
        speech: {
          dependencyType: "stateVariable",
          variableName: "speech"
        },
        sonify: {
          dependencyType: "stateVariable",
          variableName: "sonify"
        },
        circular: {
          dependencyType: "stateVariable",
          variableName: "circular"
        },
        refData: {
          dependencyType: "stateVariable",
          variableName: "refData"
        },
        annotationChildren: {
          dependencyType: "child",
          childGroups: ["annotations"],
          variableNames: ["prefigureAnnotationNode"]
        }
      }),
      definition({ dependencyValues, componentIdx, componentName }) {
        return {
          setValue: {
            prefigureAnnotationNode: {
              componentIdx,
              componentName,
              text: dependencyValues.text,
              speech: dependencyValues.speech,
              sonify: Boolean(dependencyValues.sonify),
              circular: Boolean(dependencyValues.circular),
              hasRefAttribute: dependencyValues.refData.hasRefAttribute,
              refResolutions: dependencyValues.refData.refResolutions,
              children: dependencyValues.annotationChildren.map(
                (child) => child?.stateValues?.prefigureAnnotationNode
              ).filter((x2) => x2 !== null && x2 !== void 0)
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class PiecewiseFunction extends Function$1 {
  static componentType = "piecewiseFunction";
  static componentDocs = {
    summary: "A function defined piecewise from sub-functions on intervals"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.numInputs;
    delete attributes.numOutputs;
    delete attributes.minima;
    delete attributes.maxima;
    delete attributes.extrema;
    delete attributes.through;
    delete attributes.throughSlope;
    return attributes;
  }
  // Include children that can be added due to sugar, remove options from function
  static additionalSchemaChildren = [];
  static returnSugarInstructions() {
    return [];
  }
  static returnChildGroups() {
    return [
      {
        group: "functions",
        componentTypes: ["function"]
      },
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    delete stateVariableDefinitions.isInterpolatedFunction;
    stateVariableDefinitions.isPiecewiseFunction = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isPiecewiseFunction: true } })
    };
    stateVariableDefinitions.numInputs = {
      description: "Number of input variables.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({}),
      definition() {
        return { setValue: { numInputs: 1 } };
      }
    };
    stateVariableDefinitions.numOutputs = {
      description: "Number of output values.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["numOutputs"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.functionChildren.length > 0) {
          return {
            setValue: {
              numOutputs: dependencyValues.functionChildren[0].stateValues.numOutputs
            }
          };
        } else {
          return { setValue: { numOutputs: 1 } };
        }
      }
    };
    stateVariableDefinitions.numericalDomainsOfChildren = {
      additionalStateVariablesDefined: ["childrenWithNonNumericDomains"],
      returnDependencies: () => ({
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["domain"]
        }
      }),
      definition({ dependencyValues }) {
        let numericalDomainsOfChildren = [];
        let childrenWithNonNumericDomains = [];
        for (let functionChild of dependencyValues.functionChildren) {
          let fDomain = functionChild.stateValues.domain?.[0];
          if (fDomain) {
            let intervalMin = me$3.fromAst(fDomain.tree[1][1]).evaluate_to_constant();
            let intervalMax = me$3.fromAst(fDomain.tree[1][2]).evaluate_to_constant();
            let intervalMinIsClosed = fDomain.tree[2][1];
            let intervalMaxIsClosed = fDomain.tree[2][2];
            childrenWithNonNumericDomains.push(
              Number.isNaN(intervalMin) || Number.isNaN(intervalMax)
            );
            if (!Number.isFinite(intervalMin) && intervalMin !== -Infinity) {
              numericalDomainsOfChildren.push(null);
            } else if (!Number.isFinite(intervalMax) && intervalMax !== Infinity) {
              numericalDomainsOfChildren.push(null);
            } else if (intervalMax < intervalMin) {
              numericalDomainsOfChildren.push(null);
            } else if (intervalMax === intervalMin && !(intervalMinIsClosed && intervalMaxIsClosed)) {
              numericalDomainsOfChildren.push(null);
            } else {
              numericalDomainsOfChildren.push([
                [intervalMin, intervalMax],
                [intervalMinIsClosed, intervalMaxIsClosed]
              ]);
            }
          } else {
            numericalDomainsOfChildren.push([
              [-Infinity, Infinity],
              [false, false]
            ]);
          }
        }
        return {
          setValue: {
            numericalDomainsOfChildren,
            childrenWithNonNumericDomains
          }
        };
      }
    };
    stateVariableDefinitions.domain = {
      returnDependencies: () => ({
        domainAttr: {
          dependencyType: "attributeComponent",
          attributeName: "domain",
          variableNames: ["intervals"]
        },
        numericalDomainsOfChildren: {
          dependencyType: "stateVariable",
          variableName: "numericalDomainsOfChildren"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.domainAttr !== null) {
          let domain = dependencyValues.domainAttr.stateValues.intervals[0];
          if (domain && Array.isArray(domain.tree) && domain.tree[0] === "interval") {
            return { setValue: { domain: [domain] } };
          } else {
            return { setValue: { domain: null } };
          }
        }
        let foundChildDomain = false;
        let minD = Infinity;
        let maxD = -Infinity;
        let minDIsClosed = false;
        let maxDIsClosed = false;
        if (dependencyValues.numericalDomainsOfChildren.length > 0) {
          for (let childDomain of dependencyValues.numericalDomainsOfChildren) {
            if (childDomain) {
              foundChildDomain = true;
              let intervalMin = childDomain[0][0];
              let intervalMax = childDomain[0][1];
              let intervalMinIsClosed = childDomain[1][0];
              let intervalMaxIsClosed = childDomain[1][1];
              if (intervalMin < minD) {
                minD = intervalMin;
                minDIsClosed = intervalMinIsClosed;
              } else if (intervalMin === minD && intervalMinIsClosed) {
                minDIsClosed = true;
              }
              if (intervalMax > maxD) {
                maxD = intervalMax;
                maxDIsClosed = intervalMaxIsClosed;
              } else if (intervalMax === maxD && intervalMaxIsClosed) {
                maxDIsClosed = true;
              }
            }
          }
        }
        if (foundChildDomain) {
          let domain = [
            me$3.fromAst([
              "interval",
              ["tuple", minD, maxD],
              ["tuple", minDIsClosed, maxDIsClosed]
            ])
          ];
          return { setValue: { domain } };
        } else {
          return { setValue: { domain: null } };
        }
      }
    };
    delete stateVariableDefinitions.unnormalizedFormula;
    stateVariableDefinitions.formula = {
      description: "The piecewise formula of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { formula: me$3.fromAst("＿") } })
    };
    delete stateVariableDefinitions.numPrescribedPoints;
    delete stateVariableDefinitions.prescribedPoints;
    delete stateVariableDefinitions.prescribedMinima;
    delete stateVariableDefinitions.prescribedMaxima;
    delete stateVariableDefinitions.prescribedExtrema;
    delete stateVariableDefinitions.interpolationPoints;
    delete stateVariableDefinitions.xs;
    delete stateVariableDefinitions.mathChildName;
    delete stateVariableDefinitions.mathChildCreatedBySugar;
    delete stateVariableDefinitions.haveNaNChildToReevaluate;
    stateVariableDefinitions.symbolicfs = {
      isArray: true,
      entryPrefixes: ["symbolicf"],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey() {
        return {};
      },
      arrayDefinitionByKey: function({ arrayKeys }) {
        let symbolicfs = {};
        for (let arrayKey of arrayKeys) {
          symbolicfs[arrayKey] = (x2) => me$3.fromAst("＿");
        }
        return {
          setValue: { symbolicfs }
        };
      }
    };
    stateVariableDefinitions.numericalfs = {
      isArray: true,
      entryPrefixes: ["numericalf"],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            functionChildren: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: ["numericalfs"]
            },
            numericalDomainsOfChildren: {
              dependencyType: "stateVariable",
              variableName: "numericalDomainsOfChildren"
            },
            numericalfShadow: {
              dependencyType: "stateVariable",
              variableName: "numericalfShadow"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            }
          }
        };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        arrayKeys
      }) {
        if (globalDependencyValues.functionChildren.length > 0) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            let numericalFsOfChildren = globalDependencyValues.functionChildren.filter(
              (_2, i2) => globalDependencyValues.numericalDomainsOfChildren[i2]
            ).map(
              (functionChild) => functionChild.stateValues.numericalfs[arrayKey]
            );
            let numericalDomainsOfChildren = globalDependencyValues.numericalDomainsOfChildren.filter(
              (x2) => x2
            );
            numericalfs[arrayKey] = returnPiecewiseNumericalFunctionFromChildren({
              numericalFsOfChildren,
              numericalDomainsOfChildren,
              domain: globalDependencyValues.domain,
              component: arrayKey
            });
          }
          return {
            setValue: { numericalfs }
          };
        } else if (globalDependencyValues.numericalfShadow) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              numericalfs[arrayKey] = globalDependencyValues.numericalfShadow;
            } else {
              numericalfs[arrayKey] = () => NaN;
            }
          }
          return {
            setValue: { numericalfs }
          };
        } else if (globalDependencyValues.symbolicfShadow) {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            if (arrayKey === "0") {
              numericalfs[arrayKey] = function(x2) {
                let val = globalDependencyValues.symbolicfShadow(me$3.fromAst(x2)).evaluate_to_constant();
                return val;
              };
            } else {
              numericalfs[arrayKey] = () => NaN;
            }
          }
          return {
            setValue: { numericalfs }
          };
        } else {
          let numericalfs = {};
          for (let arrayKey of arrayKeys) {
            numericalfs[arrayKey] = () => NaN;
          }
          return {
            setValue: { numericalfs }
          };
        }
      }
    };
    stateVariableDefinitions.fDefinitions = {
      isArray: true,
      entryPrefixes: ["fDefinition"],
      returnArraySizeDependencies: () => ({
        numOutputs: {
          dependencyType: "stateVariable",
          variableName: "numOutputs"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numOutputs];
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            functionChildren: {
              dependencyType: "child",
              childGroups: ["functions"],
              variableNames: ["fDefinitions"]
            },
            numericalDomainsOfChildren: {
              dependencyType: "stateVariable",
              variableName: "numericalDomainsOfChildren"
            },
            numericalfShadow: {
              dependencyType: "stateVariable",
              variableName: "numericalfShadow"
            },
            domain: {
              dependencyType: "stateVariable",
              variableName: "domain"
            }
          }
        };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        arrayKeys
      }) {
        if (globalDependencyValues.functionChildren.length > 0) {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            let fDefinitionsOfChildren = globalDependencyValues.functionChildren.filter(
              (_2, i2) => globalDependencyValues.numericalDomainsOfChildren[i2]
            ).map(
              (functionChild) => functionChild.stateValues.fDefinitions[arrayKey]
            );
            let numericalDomainsOfChildren = globalDependencyValues.numericalDomainsOfChildren.filter(
              (x2) => x2
            );
            fDefinitions[arrayKey] = {
              functionType: "piecewise",
              fDefinitionsOfChildren,
              numericalDomainsOfChildren,
              domain: globalDependencyValues.domain,
              component: arrayKey
            };
          }
          return {
            setValue: { fDefinitions }
          };
        } else if (globalDependencyValues.numericalfShadow) {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = {};
          }
          return {
            setValue: { fDefinitions }
          };
        } else {
          let fDefinitions = {};
          for (let arrayKey of arrayKeys) {
            fDefinitions[arrayKey] = {};
          }
          return {
            setValue: { fDefinitions }
          };
        }
      }
    };
    stateVariableDefinitions.latex = {
      public: true,
      description: "The piecewise function rendered as a LaTeX string.",
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        variable: {
          dependencyType: "stateVariable",
          variableName: "variable"
        },
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: ["formula", "variable", "domain"]
        },
        numericalDomainsOfChildren: {
          dependencyType: "stateVariable",
          variableName: "numericalDomainsOfChildren"
        },
        childrenWithNonNumericDomains: {
          dependencyType: "stateVariable",
          variableName: "childrenWithNonNumericDomains"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        },
        padZeros: {
          dependencyType: "stateVariable",
          variableName: "padZeros"
        },
        avoidScientificNotation: {
          dependencyType: "stateVariable",
          variableName: "avoidScientificNotation"
        }
      }),
      definition: function({ dependencyValues }) {
        let functionVariable = dependencyValues.variable;
        let toLatexParams = buildNumberDisplayParameters({
          padZeros: dependencyValues.padZeros,
          displayDigits: dependencyValues.displayDigits,
          displayDecimals: dependencyValues.displayDecimals,
          avoidScientificNotation: dependencyValues.avoidScientificNotation
        });
        let effectiveDomainsOfChildrenIgnoringDomain = find_effective_domains_piecewise_children({
          numericalDomainsOfChildren: dependencyValues.numericalDomainsOfChildren
        });
        let me_infinity = me$3.fromAst(Infinity);
        let me_minus_infinity = me$3.fromAst(-Infinity);
        let formulaLatexByLine = [];
        let subsetDomainsByLine = [];
        let nonNumericConditionsByLine = [];
        let lineContainsNonNumeric = [];
        let lastNonNumeric = -1;
        for (let [
          ind,
          functionChild
        ] of dependencyValues.functionChildren.entries()) {
          let formula = functionChild.stateValues.formula.subscripts_to_strings();
          let variable = functionChild.stateValues.variable.subscripts_to_strings();
          formula = formula.substitute({ [variable]: functionVariable }).strings_to_subscripts();
          let formulaLatex = roundForDisplay({
            value: formula,
            dependencyValues
          }).toLatex(toLatexParams);
          let conditionLatex;
          let fDomain = functionChild.stateValues.domain?.[0];
          let haveNonNumeric = false;
          if (dependencyValues.childrenWithNonNumericDomains[ind]) {
            haveNonNumeric = true;
            let intervalMin = me$3.fromAst(fDomain.tree[1][1]).simplify();
            let intervalMax = me$3.fromAst(fDomain.tree[1][2]).simplify();
            let intervalMinIsClosed = fDomain.tree[2][1];
            let intervalMaxIsClosed = fDomain.tree[2][2];
            if (intervalMin.equals(me_minus_infinity)) {
              let operator = intervalMaxIsClosed ? "\\le" : "<";
              let maxxLatex = roundForDisplay({
                value: intervalMax,
                dependencyValues
              }).toLatex(toLatexParams);
              conditionLatex = `${functionVariable} ${operator} ${maxxLatex}`;
            } else if (intervalMax.equals(me_infinity)) {
              let operator = intervalMinIsClosed ? "\\ge" : ">";
              let minxLatex = roundForDisplay({
                value: intervalMin,
                dependencyValues
              }).toLatex(toLatexParams);
              conditionLatex = `${functionVariable} ${operator} ${minxLatex}`;
            } else if (intervalMax.equals(intervalMin) && intervalMinIsClosed && intervalMaxIsClosed) {
              let maxxLatex = roundForDisplay({
                value: intervalMax,
                dependencyValues
              }).toLatex(toLatexParams);
              conditionLatex = `${functionVariable} = ${maxxLatex}`;
            } else {
              let domainString = "{" + roundForDisplay({
                value: intervalMin,
                dependencyValues
              }).toLatex(toLatexParams) + "}";
              if (intervalMinIsClosed) {
                domainString += " \\le ";
              } else {
                domainString += " < ";
              }
              domainString += functionVariable;
              if (intervalMaxIsClosed) {
                domainString += " \\le ";
              } else {
                domainString += " < ";
              }
              domainString += roundForDisplay({
                value: intervalMax,
                dependencyValues
              }).toLatex(toLatexParams);
              conditionLatex = domainString;
            }
          } else {
            if (effectiveDomainsOfChildrenIgnoringDomain[ind].isEmpty()) {
              continue;
            }
            if (!fDomain || fDomain.tree[1][1] === -Infinity && fDomain.tree[1][2] === Infinity) {
              if (formulaLatexByLine.length === 0) {
                return { setValue: { latex: formulaLatex } };
              }
              let indexOfFormula2 = formulaLatexByLine.lastIndexOf(formulaLatex);
              if (indexOfFormula2 > lastNonNumeric || indexOfFormula2 === formulaLatexByLine.length - 1) {
                formulaLatexByLine.splice(indexOfFormula2, 1);
                nonNumericConditionsByLine.splice(
                  indexOfFormula2,
                  1
                );
                subsetDomainsByLine.splice(indexOfFormula2, 1);
                lineContainsNonNumeric.splice(
                  indexOfFormula2,
                  1
                );
              }
              formulaLatexByLine.push(formulaLatex);
              subsetDomainsByLine.push([]);
              nonNumericConditionsByLine.push([
                "\\text{otherwise}"
              ]);
              break;
            }
            conditionLatex = latexFromSubsetDomain({
              subsetDomain: effectiveDomainsOfChildrenIgnoringDomain[ind],
              dependencyValues,
              functionVariable,
              toLatexParams
            });
          }
          let indexOfFormula = formulaLatexByLine.lastIndexOf(formulaLatex);
          let canCombine = false;
          if (indexOfFormula !== -1 && indexOfFormula >= lastNonNumeric) {
            canCombine = true;
            if (haveNonNumeric && indexOfFormula !== formulaLatexByLine.length - 1) {
              canCombine = false;
            }
          }
          let indexUsed;
          if (canCombine) {
            indexUsed = indexOfFormula;
          } else {
            formulaLatexByLine.push(formulaLatex);
            subsetDomainsByLine.push([]);
            nonNumericConditionsByLine.push([]);
            indexUsed = formulaLatexByLine.length - 1;
          }
          if (haveNonNumeric) {
            lastNonNumeric = formulaLatexByLine.length - 1;
            lineContainsNonNumeric[indexUsed] = true;
            nonNumericConditionsByLine[indexUsed].push(
              conditionLatex
            );
          } else {
            subsetDomainsByLine[indexUsed].push(
              effectiveDomainsOfChildrenIgnoringDomain[ind]
            );
          }
        }
        let conditionLatexByLine = subsetDomainsByLine.map(
          (subsetDomains, i2) => {
            if (nonNumericConditionsByLine[i2].length === 1 && nonNumericConditionsByLine[i2][0] === "\\text{otherwise}") {
              return "\\text{otherwise}";
            } else {
              let conditionLatex = "\\text{if }";
              if (nonNumericConditionsByLine[i2].length > 0) {
                conditionLatex += " " + nonNumericConditionsByLine[i2].join(
                  " \\text{ or } "
                );
              }
              if (subsetDomains.length > 0) {
                if (nonNumericConditionsByLine[i2].length > 0) {
                  conditionLatex += " \\text{ or } ";
                } else {
                  conditionLatex += " ";
                }
                let overallDomain = subsetOfReals.Union(subsetDomains);
                conditionLatex += latexFromSubsetDomain({
                  subsetDomain: overallDomain,
                  dependencyValues,
                  functionVariable,
                  toLatexParams
                });
              }
              return conditionLatex;
            }
          }
        );
        let latex = formulaLatexByLine.map((v2, i2) => `${v2} & ${conditionLatexByLine[i2]}`).join("\\\\\n    ");
        latex = `\\begin{cases}
    ${latex}
\\end{cases}`;
        return { setValue: { latex } };
      }
    };
    stateVariableDefinitions.functionChildrenInfoToCalculateExtrema = {
      returnDependencies: () => ({
        functionChildren: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: [
            "domain",
            "xscale",
            "isInterpolatedFunction",
            "xs",
            "coeffs",
            "numericalf",
            "formula",
            "variables",
            "functionChildrenInfoToCalculateExtrema",
            "numInputs",
            "numOutputs",
            "numericalDomainsOfChildren"
          ],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            functionChildrenInfoToCalculateExtrema: dependencyValues.functionChildren
          }
        };
      }
    };
    stateVariableDefinitions.allMinima = {
      additionalStateVariablesDefined: [
        "globalMinimumOption",
        "globalInfimumOption"
      ],
      returnDependencies() {
        return {
          functionChildrenInfoToCalculateExtrema: {
            dependencyType: "stateVariable",
            variableName: "functionChildrenInfoToCalculateExtrema"
          },
          domain: {
            dependencyType: "stateVariable",
            variableName: "domain"
          },
          numericalDomainsOfChildren: {
            dependencyType: "stateVariable",
            variableName: "numericalDomainsOfChildren"
          },
          numericalf: {
            dependencyType: "stateVariable",
            variableName: "numericalf"
          }
        };
      },
      definition: function({ dependencyValues }) {
        let { localMinima, globalMinimum, globalInfimum } = find_minima_of_piecewise({
          functionChildrenInfoToCalculateExtrema: dependencyValues.functionChildrenInfoToCalculateExtrema,
          domain: dependencyValues.domain,
          numericalDomainsOfChildren: dependencyValues.numericalDomainsOfChildren,
          numericalf: dependencyValues.numericalf,
          numerics
        });
        return {
          setValue: {
            allMinima: localMinima,
            globalMinimumOption: globalMinimum,
            globalInfimumOption: globalInfimum
          }
        };
      }
    };
    stateVariableDefinitions.allMaxima = {
      additionalStateVariablesDefined: [
        "globalMaximumOption",
        "globalSupremumOption"
      ],
      returnDependencies() {
        return {
          functionChildrenInfoToCalculateExtrema: {
            dependencyType: "stateVariable",
            variableName: "functionChildrenInfoToCalculateExtrema"
          },
          domain: {
            dependencyType: "stateVariable",
            variableName: "domain"
          },
          numericalDomainsOfChildren: {
            dependencyType: "stateVariable",
            variableName: "numericalDomainsOfChildren"
          },
          numericalf: {
            dependencyType: "stateVariable",
            variableName: "numericalf"
          }
        };
      },
      definition: function({ dependencyValues }) {
        let { localMaxima, globalMaximum, globalSupremum } = find_maxima_of_piecewise({
          functionChildrenInfoToCalculateExtrema: dependencyValues.functionChildrenInfoToCalculateExtrema,
          domain: dependencyValues.domain,
          numericalDomainsOfChildren: dependencyValues.numericalDomainsOfChildren,
          numericalf: dependencyValues.numericalf,
          numerics
        });
        return {
          setValue: {
            allMaxima: localMaxima,
            globalMaximumOption: globalMaximum,
            globalSupremumOption: globalSupremum
          }
        };
      }
    };
    stateVariableDefinitions.returnNumericalDerivatives = {
      returnDependencies: () => ({}),
      definition: function() {
        return { setValue: { returnNumericalDerivatives: null } };
      }
    };
    stateVariableDefinitions.numericalDerivativesDefinition = {
      returnDependencies: () => ({}),
      definition: function() {
        return { setValue: { numericalDerivativesDefinition: {} } };
      }
    };
    return stateVariableDefinitions;
  }
}
function latexFromSubsetDomain({
  subsetDomain,
  dependencyValues,
  functionVariable,
  toLatexParams
}) {
  let childDomainMath = mathExpressionFromSubsetValue({
    subsetValue: subsetDomain,
    variable: functionVariable,
    displayMode: "inequalities"
  });
  let childDomainLatex;
  if (childDomainMath.tree[0] === "or") {
    childDomainLatex = childDomainMath.tree.slice(1).map(
      (subint) => roundForDisplay({
        value: me$3.fromAst(subint),
        dependencyValues
      }).toLatex(toLatexParams)
    ).join(" \\text{ or }");
  } else {
    childDomainLatex = roundForDisplay({
      value: childDomainMath,
      dependencyValues
    }).toLatex(toLatexParams);
  }
  return childDomainLatex;
}
class Interval extends MathComponent {
  static componentType = "interval";
  static rendererType = "math";
  static componentDocs = {
    summary: "A math expression treated as an interval of real numbers"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.createIntervals;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.createIntervals = {
      description: "Whether this math component renders intervals as intervals (always true for `<interval>`).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { createIntervals: true } })
    };
    return stateVariableDefinitions;
  }
  // TODO: do we want to give warnings or errors if value is not in form of an interval?
}
let Option$1 = class Option extends BaseComponent {
  static componentType = "option";
  static componentDocs = {
    summary: "A single option within a `<select>` block"
  };
  static rendererType = void 0;
  static includeBlankStringChildren = true;
  static createsVariants = true;
  static inSchemaOnlyInheritAs = [];
  static allowInSchemaAsComponent = void 0;
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  // since don't have child groups, tell schema about children here
  static additionalSchemaChildren = ["_base"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.selectForVariants = {
      description: "Variant indices for which this option is selected.",
      createComponentOfType: "textListFromString",
      createStateVariable: "selectForVariants",
      defaultValue: [],
      public: true
    };
    attributes.selectWeight = {
      description: "Relative weight when randomly selecting an option.",
      createComponentOfType: "number",
      createStateVariable: "selectWeight",
      defaultValue: 1,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.serializedChildren = {
      returnDependencies: () => ({
        serializedChildren: {
          dependencyType: "serializedChildren",
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            serializedChildren: dependencyValues.serializedChildren
          }
        };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents,
      useSubpartVariantRng: true
    });
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    let additionalRendererTypes = this.potentialRendererTypesFromSerializedComponents(
      this.serializedChildren
    );
    for (let rendererType of additionalRendererTypes) {
      if (!allPotentialRendererTypes.includes(rendererType)) {
        allPotentialRendererTypes.push(rendererType);
      }
    }
    return allPotentialRendererTypes;
  }
};
function returnStandardSequenceAttributes() {
  return {
    type: {
      createPrimitiveOfType: "string",
      createStateVariable: "type",
      defaultPrimitiveValue: "number",
      toLowerCase: true,
      validValues: [
        {
          value: "number",
          description: "A sequence of numeric values."
        },
        {
          value: "math",
          description: "A sequence of math expressions (allows symbolic from/to/step)."
        },
        {
          value: "letters",
          description: "A sequence of letters (single-letter or multi-letter labels)."
        }
      ],
      description: "The kind of values in the sequence."
    },
    from: {
      createComponentOfType: "_componentWithSelectableType",
      description: "The first value in the sequence."
    },
    to: {
      createComponentOfType: "_componentWithSelectableType",
      description: "The last value in the sequence. Used together with from to determine length when length is not specified."
    },
    step: {
      createComponentOfType: "math",
      description: "The increment between successive values in the sequence."
    },
    length: {
      createComponentOfType: "integer",
      description: "The number of values in the sequence."
    },
    exclude: {
      createComponentOfType: "_componentListWithSelectableType",
      description: "Values to exclude from the sequence."
    }
  };
}
function returnStandardSequenceStateVariableDefinitions() {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.specifiedFrom = {
    returnDependencies: () => ({
      fromAttr: {
        dependencyType: "attributeComponent",
        attributeName: "from",
        variableNames: ["value"]
      }
    }),
    defaultValue: null,
    hasEssential: true,
    essentialVarName: "from",
    definition: function({ dependencyValues }) {
      if (dependencyValues.fromAttr === null) {
        return {
          useEssentialOrDefaultValue: { specifiedFrom: true }
        };
      }
      if (dependencyValues.fromAttr.stateValues.value === null) {
        return {
          setValue: {
            specifiedFrom: NaN
          }
        };
      }
      return {
        setValue: {
          specifiedFrom: dependencyValues.fromAttr.stateValues.value
        }
      };
    }
  };
  stateVariableDefinitions.specifiedTo = {
    returnDependencies: () => ({
      toAttr: {
        dependencyType: "attributeComponent",
        attributeName: "to",
        variableNames: ["value"]
      }
    }),
    defaultValue: null,
    hasEssential: true,
    essentialVarName: "to",
    definition: function({ dependencyValues }) {
      if (dependencyValues.toAttr === null) {
        return {
          useEssentialOrDefaultValue: {
            specifiedTo: true
          }
        };
      }
      if (dependencyValues.toAttr.stateValues.value === null) {
        return {
          setValue: {
            specifiedTo: NaN
          }
        };
      }
      return {
        setValue: {
          specifiedTo: dependencyValues.toAttr.stateValues.value
        }
      };
    }
  };
  stateVariableDefinitions.specifiedLength = {
    returnDependencies: () => ({
      lengthAttr: {
        dependencyType: "attributeComponent",
        attributeName: "length",
        variableNames: ["value"]
      }
    }),
    defaultValue: null,
    hasEssential: true,
    essentialVarName: "length",
    definition: function({ dependencyValues }) {
      if (dependencyValues.lengthAttr === null) {
        return {
          useEssentialOrDefaultValue: {
            specifiedLength: true
          }
        };
      }
      if (dependencyValues.lengthAttr.stateValues.value === null) {
        return {
          setValue: {
            specifiedLength: NaN
          }
        };
      }
      return {
        setValue: {
          specifiedLength: dependencyValues.lengthAttr.stateValues.value
        }
      };
    }
  };
  stateVariableDefinitions.specifiedStep = {
    returnDependencies: () => ({
      stepAttr: {
        dependencyType: "attributeComponent",
        attributeName: "step",
        variableNames: ["value"]
      },
      type: {
        dependencyType: "stateVariable",
        variableName: "type"
      }
    }),
    defaultValue: null,
    hasEssential: true,
    essentialVarName: "step",
    definition: function({ dependencyValues }) {
      if (dependencyValues.stepAttr === null) {
        return {
          useEssentialOrDefaultValue: {
            specifiedStep: true
          }
        };
      }
      let step = dependencyValues.stepAttr.stateValues.value;
      if (step === null) {
        return {
          setValue: {
            specifiedStep: NaN
          }
        };
      }
      return { setValue: { specifiedStep: step } };
    }
  };
  stateVariableDefinitions.specifiedExclude = {
    returnDependencies: () => ({
      excludeAttr: {
        dependencyType: "attributeComponent",
        attributeName: "exclude",
        variableNames: ["values"]
      }
    }),
    defaultValue: [],
    hasEssential: true,
    essentialVarName: "exclude",
    definition: function({ dependencyValues }) {
      if (dependencyValues.excludeAttr === null) {
        return {
          useEssentialOrDefaultValue: {
            specifiedExclude: true
          }
        };
      }
      return {
        setValue: {
          specifiedExclude: dependencyValues.excludeAttr.stateValues.values
        }
      };
    }
  };
  stateVariableDefinitions.lowercase = {
    returnDependencies: () => ({
      specifiedTo: {
        dependencyType: "stateVariable",
        variableName: "specifiedTo"
      },
      specifiedFrom: {
        dependencyType: "stateVariable",
        variableName: "specifiedFrom"
      }
    }),
    definition: function({ dependencyValues }) {
      let capitalRegex = /^[A-Z]*$/;
      let lowercase = true;
      if (dependencyValues.specifiedFrom !== null) {
        if (capitalRegex.test(dependencyValues.specifiedFrom)) {
          lowercase = false;
        }
      } else if (dependencyValues.specifiedTo !== null) {
        if (capitalRegex.test(dependencyValues.specifiedTo)) {
          lowercase = false;
        }
      }
      return { setValue: { lowercase } };
    }
  };
  stateVariableDefinitions.validSequence = {
    returnDependencies: () => ({
      specifiedLength: {
        dependencyType: "stateVariable",
        variableName: "specifiedLength"
      },
      specifiedStep: {
        dependencyType: "stateVariable",
        variableName: "specifiedStep"
      },
      specifiedFrom: {
        dependencyType: "stateVariable",
        variableName: "specifiedFrom"
      },
      specifiedTo: {
        dependencyType: "stateVariable",
        variableName: "specifiedTo"
      },
      type: {
        dependencyType: "stateVariable",
        variableName: "type"
      },
      lowercase: {
        dependencyType: "stateVariable",
        variableName: "lowercase"
      }
    }),
    definition: function({ dependencyValues }) {
      let diagnostics = [];
      let validSequence = true;
      if (dependencyValues.specifiedLength !== null) {
        if (!Number.isInteger(dependencyValues.specifiedLength) || dependencyValues.specifiedLength < 0) {
          diagnostics.push({
            message: "Invalid length of sequence.  Must be a non-negative integer.",
            type: "info"
          });
          validSequence = false;
        }
      }
      if (dependencyValues.specifiedStep !== null) {
        if (dependencyValues.type !== "math") {
          let numericalStep = findFiniteNumericalValue(
            dependencyValues.specifiedStep
          );
          if (!Number.isFinite(numericalStep)) {
            diagnostics.push({
              message: "Invalid step of sequence.  Must be a number for sequence of type " + dependencyValues.type + ".",
              type: "info"
            });
            validSequence = false;
          }
        }
      }
      if (dependencyValues.specifiedFrom !== null) {
        if (dependencyValues.type === "number") {
          let numericalFrom = findFiniteNumericalValue(
            dependencyValues.specifiedFrom
          );
          if (!Number.isFinite(numericalFrom)) {
            diagnostics.push({
              message: `Invalid "from" of number sequence.  Must be a number.`,
              type: "info"
            });
            validSequence = false;
          }
        } else if (dependencyValues.type === "letters") {
          if (lettersToNumber(dependencyValues.specifiedFrom) === void 0) {
            diagnostics.push({
              message: `Invalid "from" of letters sequence.  Must be a letter combination.`,
              type: "info"
            });
            validSequence = false;
          }
        } else {
          if (Number.isNaN(dependencyValues.specifiedFrom.tree)) {
            diagnostics.push({
              message: `Invalid "from" of sequence.`,
              type: "info"
            });
            validSequence = false;
          }
        }
      }
      if (dependencyValues.specifiedTo !== null) {
        if (dependencyValues.type === "number") {
          let numericalTo = findFiniteNumericalValue(
            dependencyValues.specifiedTo
          );
          if (!Number.isFinite(numericalTo)) {
            diagnostics.push({
              message: `Invalid "to" of number sequence.  Must be a number.`,
              type: "info"
            });
            validSequence = false;
          }
        } else if (dependencyValues.type === "letters") {
          if (lettersToNumber(dependencyValues.specifiedTo) === void 0) {
            diagnostics.push({
              message: `Invalid "to" of letters sequence.  Must be a letter combination.`,
              type: "info"
            });
            validSequence = false;
          }
        } else {
          if (Number.isNaN(dependencyValues.specifiedTo.tree)) {
            diagnostics.push({
              message: `Invalid "to" of sequence.`,
              type: "info"
            });
            validSequence = false;
          }
        }
      }
      return {
        setValue: { validSequence },
        sendDiagnostics: diagnostics
      };
    }
  };
  stateVariableDefinitions.from = {
    additionalStateVariablesDefined: ["step", "length", "exclude"],
    returnDependencies: () => ({
      specifiedFrom: {
        dependencyType: "stateVariable",
        variableName: "specifiedFrom"
      },
      specifiedTo: {
        dependencyType: "stateVariable",
        variableName: "specifiedTo"
      },
      specifiedLength: {
        dependencyType: "stateVariable",
        variableName: "specifiedLength"
      },
      specifiedStep: {
        dependencyType: "stateVariable",
        variableName: "specifiedStep"
      },
      type: {
        dependencyType: "stateVariable",
        variableName: "type"
      },
      specifiedExclude: {
        dependencyType: "stateVariable",
        variableName: "specifiedExclude"
      },
      validSequence: {
        dependencyType: "stateVariable",
        variableName: "validSequence"
      }
    }),
    definition: function({ dependencyValues }) {
      let from = dependencyValues.specifiedFrom;
      let to2 = dependencyValues.specifiedTo;
      let step = dependencyValues.specifiedStep;
      let length = dependencyValues.specifiedLength;
      let exclude = [...dependencyValues.specifiedExclude];
      let type = dependencyValues.type;
      if (dependencyValues.type === "math") {
        if (to2 !== null) {
          if (!(to2 instanceof me$3.class)) {
            to2 = me$3.fromAst(to2);
          }
        }
        if (from !== null) {
          if (!(from instanceof me$3.class)) {
            from = me$3.fromAst(from);
          }
        }
        if (step !== null) {
          if (!(step instanceof me$3.class)) {
            step = me$3.fromAst(step);
          }
        }
      } else {
        if (step !== null) {
          if (step instanceof me$3.class) {
            step = step.evaluate_to_constant();
          }
        }
        if (dependencyValues.type === "letters") {
          if (from !== null) {
            from = lettersToNumber(from);
          }
          if (to2 !== null) {
            to2 = lettersToNumber(to2);
          }
          exclude = exclude.map(lettersToNumber);
        } else if (dependencyValues.type === "number") {
          if (to2 !== null) {
            if (to2 instanceof me$3.class) {
              to2 = to2.evaluate_to_constant();
            } else {
              to2 = Number(to2);
            }
          }
          if (from !== null) {
            if (from instanceof me$3.class) {
              from = from.evaluate_to_constant();
            } else {
              from = Number(from);
            }
          }
          for (let [index2, value] of exclude.entries()) {
            if (value instanceof me$3.class) {
              exclude[index2] = value.evaluate_to_constant();
            } else {
              exclude[index2] = Number(value);
            }
          }
        }
      }
      if (dependencyValues.validSequence) {
        let results = calculateSequenceParameters({
          from,
          to: to2,
          step,
          length,
          type
        });
        results.exclude = exclude;
        return { setValue: results };
      }
      if (!Number.isInteger(length) || length < 0) {
        length = 0;
      }
      return { setValue: { from, step, length, exclude } };
    }
  };
  return stateVariableDefinitions;
}
function calculateSequenceParameters({ from, to: to2, step, length, type }) {
  if (from === null) {
    if (to2 === null) {
      if (type === "math") {
        from = me$3.fromAst(1);
      } else {
        from = 1;
      }
      if (step === null) {
        if (type === "math") {
          step = me$3.fromAst(1);
        } else {
          step = 1;
        }
        if (length === null) {
          length = 10;
        }
      } else {
        if (length === null) {
          length = 10;
        }
      }
    } else {
      if (step === null) {
        if (type === "math") {
          step = me$3.fromAst(1);
        } else {
          step = 1;
        }
      }
      if (length === null) {
        if (type === "math") {
          length = Math.floor(
            (to2.subtract(1).divide(step).evaluate_to_constant() + 1) * (1 + 1e-14)
          );
        } else {
          length = Math.floor(((to2 - 1) / step + 1) * (1 + 1e-14));
        }
      }
      if (type === "math") {
        from = to2.subtract(step.multiply(length - 1)).simplify();
      } else {
        from = to2 - step * (length - 1);
        if (type === "letters") {
          if (from < 1) {
            length = Math.floor(
              ((to2 - 1) / step + 1) * (1 + 1e-14)
            );
            from = to2 - step * (length - 1);
          }
        }
      }
    }
  } else {
    if (to2 === null) {
      if (step === null) {
        if (type === "math") {
          step = me$3.fromAst(1);
        } else {
          step = 1;
        }
      }
      if (length === null) {
        length = 10;
      }
    } else {
      if (step === null) {
        if (length === null) {
          if (type === "math") {
            step = me$3.fromAst(1);
            length = Math.floor(
              to2.subtract(from).add(1).evaluate_to_constant() * (1 + 1e-14)
            );
          } else {
            step = 1;
            length = Math.floor((to2 - from + 1) * (1 + 1e-14));
          }
        } else {
          if (type === "math") {
            step = to2.subtract(from).divide(length - 1);
          } else {
            step = (to2 - from) / (length - 1);
            if (type === "letters") {
              step = Math.floor(step);
            }
          }
        }
      } else {
        if (length === null) {
          if (type === "math") {
            length = Math.floor(
              to2.subtract(from).divide(step).add(1).evaluate_to_constant() * (1 + 1e-14)
            );
          } else {
            length = Math.floor(
              ((to2 - from) / step + 1) * (1 + 1e-14)
            );
          }
        } else {
          throw Error(
            "Can't define from, to, step, and length for sequence"
          );
        }
      }
    }
  }
  if (!Number.isInteger(length) || length < 0) {
    console.warn(
      "Invalid length of sequence.  Must be a non-negative integer."
    );
    length = 0;
  }
  return {
    from,
    step,
    length
  };
}
function returnSequenceValues({ from, step, length, exclude, type, lowercase, maxNum }, includeOriginalIndex = false) {
  let sequenceValues = [];
  let numValues = 0;
  for (let ind = 0; ind < length; ind++) {
    let value = from;
    if (ind > 0) {
      if (type === "math") {
        value = value.add(step.multiply(me$3.fromAst(ind))).expand().simplify();
      } else {
        value += step * ind;
      }
    }
    if (type === "math") {
      if (exclude.some((x2) => x2 && x2.equals(value))) {
        continue;
      }
    } else if (type === "number") {
      if (exclude.some(
        (x2) => Math.abs(x2 - value) <= 1e-14 * Math.max(
          Math.abs(x2),
          Math.abs(value),
          Math.abs(step)
        )
      )) {
        continue;
      }
    } else {
      if (exclude.includes(value)) {
        continue;
      }
    }
    if (type === "letters") {
      value = numberToLetters(value, lowercase);
    }
    if (includeOriginalIndex) {
      sequenceValues.push({ value, originalIndex: ind });
    } else {
      sequenceValues.push(value);
    }
    numValues++;
    if (numValues === maxNum) {
      break;
    }
  }
  return sequenceValues;
}
function returnSequenceValueForIndex({
  index: index2,
  from,
  step,
  length,
  exclude,
  type,
  lowercase
}) {
  if (!(index2 >= 0 && (length === void 0 || index2 < length))) {
    return null;
  }
  let value = from;
  if (index2 > 0) {
    if (type === "math") {
      value = value.add(step.multiply(me$3.fromAst(index2))).expand().simplify();
    } else {
      value += step * index2;
    }
  }
  if (type === "math") {
    if (exclude.some((x2) => x2 && x2.equals(value))) {
      return null;
    }
  } else if (type === "number") {
    if (exclude.some(
      (x2) => Math.abs(x2 - value) <= 1e-14 * Math.max(Math.abs(x2), Math.abs(value), Math.abs(step))
    )) {
      return null;
    }
  } else {
    if (exclude.includes(value)) {
      return null;
    }
  }
  if (type === "letters") {
    value = numberToLetters(value, lowercase);
  }
  return value;
}
class Sequence extends CompositeComponent {
  static componentType = "sequence";
  static componentDocs = {
    summary: "Generates a sequence of numbers, math expressions, or letters"
  };
  static takesIndex = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static allowInSchemaAsComponent = ["number", "math", "text"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.fixed = {
      leaveRaw: true,
      description: "Whether this component's value is fixed and cannot be modified."
    };
    const numberDisplayAttrs = returnNumberDisplayAttributes();
    for (let attrName in numberDisplayAttrs) {
      attributes[attrName] = {
        leaveRaw: true,
        description: numberDisplayAttrs[attrName].description
      };
    }
    let sequenceAttributes = returnStandardSequenceAttributes();
    Object.assign(attributes, sequenceAttributes);
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let sequenceDefs = returnStandardSequenceStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, sequenceDefs);
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        length: {
          dependencyType: "stateVariable",
          variableName: "length"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        }
      }),
      // when this state variable is marked stale
      // it indicates we should update replacements.
      // For this to work, must get value in replacement functions
      // so that the variable is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    workspace,
    componentInfoObjects,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    if (!await component.stateValues.validSequence) {
      workspace.lastReplacementParameters = {
        from: null,
        length: null,
        step: null,
        type: null,
        exclude: null
      };
      return { replacements: [], diagnostics, nComponents };
    }
    let from = await component.stateValues.from;
    let length = await component.stateValues.length;
    let step = await component.stateValues.step;
    let type = await component.stateValues.type;
    let exclude = await component.stateValues.exclude;
    workspace.lastReplacementParameters = {
      from,
      length,
      step,
      type,
      exclude
    };
    let sequenceValues = returnSequenceValues({
      from,
      step,
      length,
      exclude,
      type,
      lowercase: await component.stateValues.lowercase
    });
    let componentType = type;
    if (type === "letters") {
      componentType = "text";
    }
    let replacements = [];
    let attributesToConvert = {};
    for (let attr of [
      "fixed",
      ...Object.keys(returnNumberDisplayAttributes())
    ]) {
      if (attr in component.attributes) {
        attributesToConvert[attr] = component.attributes[attr];
      }
    }
    for (let componentValue of sequenceValues) {
      let attributesFromComposite = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType,
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        nComponents = res.nComponents;
        attributesFromComposite = res.attributes;
      }
      let serializedComponent = {
        type: "serialized",
        componentType,
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: attributesFromComposite,
        doenetAttributes: {},
        children: [],
        state: { value: componentValue, fixed: true }
      };
      replacements.push(serializedComponent);
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    workspace,
    componentInfoObjects,
    nComponents
  }) {
    let diagnostics = [];
    let lrp = { ...workspace.lastReplacementParameters };
    let replacementChanges = [];
    if (!await component.stateValues.validSequence) {
      let currentReplacementsWithheld = component.replacementsToWithhold;
      if (!currentReplacementsWithheld) {
        currentReplacementsWithheld = 0;
      }
      if (component.replacements.length - currentReplacementsWithheld > 0) {
        let replacementsToWithhold = component.replacements.length;
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold
        };
        replacementChanges.push(replacementInstruction);
      }
      lrp.length = 0;
      workspace.lastReplacementParameters = lrp;
      return { replacementChanges, diagnostics, nComponents };
    }
    let from = await component.stateValues.from;
    let length = await component.stateValues.length;
    let step = await component.stateValues.step;
    let type = await component.stateValues.type;
    let exclude = await component.stateValues.exclude;
    let lowercase = await component.stateValues.lowercase;
    if (lrp.type !== type || lrp.exclude.length > 0 || exclude.length > 0) {
      let replacementResults = await this.createSerializedReplacements({
        component,
        workspace,
        componentInfoObjects,
        nComponents
      });
      let newSerializedReplacements = replacementResults.replacements;
      diagnostics.push(...replacementResults.diagnostics);
      nComponents = replacementResults.nComponents;
      let replacementInstruction = {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: newSerializedReplacements,
        replacementsToWithhold: 0
      };
      replacementChanges.push(replacementInstruction);
    } else {
      let modifyExistingValues = false;
      if (type === "math") {
        if (!(from.equals(lrp.from) && step.equals(lrp.step))) {
          modifyExistingValues = true;
        }
      } else {
        if (from !== lrp.from || step !== lrp.step) {
          modifyExistingValues = true;
        }
      }
      let prevLength = lrp.length;
      let numReplacementsToAdd = 0;
      let numToModify = 0;
      let firstToModify = prevLength;
      let newReplacementsToWithhold;
      if (length < prevLength) {
        newReplacementsToWithhold = component.replacements.length - length;
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: newReplacementsToWithhold
        };
        replacementChanges.push(replacementInstruction);
      } else if (length > prevLength) {
        numReplacementsToAdd = length - prevLength;
        if (component.replacementsToWithhold > 0) {
          if (component.replacementsToWithhold >= numReplacementsToAdd) {
            newReplacementsToWithhold = component.replacementsToWithhold - numReplacementsToAdd;
            numToModify += numReplacementsToAdd;
            prevLength += numReplacementsToAdd;
            numReplacementsToAdd = 0;
            let replacementInstruction = {
              changeType: "changeReplacementsToWithhold",
              replacementsToWithhold: newReplacementsToWithhold
            };
            replacementChanges.push(replacementInstruction);
          } else {
            numReplacementsToAdd -= component.replacementsToWithhold;
            numToModify += component.replacementsToWithhold;
            prevLength += component.replacementsToWithhold;
            newReplacementsToWithhold = 0;
          }
        }
      }
      if (modifyExistingValues === true) {
        numToModify = prevLength;
        firstToModify = 0;
      }
      if (numToModify > 0) {
        for (let ind = firstToModify; ind < firstToModify + numToModify; ind++) {
          let componentValue = returnSequenceValueForIndex({
            index: ind,
            from,
            step,
            exclude: [],
            type,
            lowercase
          });
          let replacementInstruction = {
            changeType: "updateStateVariables",
            component: component.replacements[ind],
            stateChanges: { value: componentValue }
          };
          replacementChanges.push(replacementInstruction);
        }
      }
      if (numReplacementsToAdd > 0) {
        const stateIdInfo = {
          prefix: `${component.stateId}|`,
          num: workspace.replacementsCreated
        };
        let newSerializedReplacements = [];
        let attributesToConvert = {};
        for (let attr of [
          "fixed",
          ...Object.keys(returnNumberDisplayAttributes())
        ]) {
          if (attr in component.attributes) {
            attributesToConvert[attr] = component.attributes[attr];
          }
        }
        for (let ind = prevLength; ind < await component.stateValues.length; ind++) {
          let componentValue = returnSequenceValueForIndex({
            index: ind,
            from,
            step,
            exclude: [],
            type,
            lowercase
          });
          let componentType = await component.stateValues.type;
          if (componentType === "letters") {
            componentType = "text";
          }
          let attributesFromComposite = {};
          if (Object.keys(attributesToConvert).length > 0) {
            const res = convertUnresolvedAttributesForComponentType(
              {
                attributes: attributesToConvert,
                componentType,
                componentInfoObjects,
                nComponents,
                stateIdInfo
              }
            );
            nComponents = res.nComponents;
            attributesFromComposite = res.attributes;
          }
          let serializedComponent = {
            type: "serialized",
            componentType,
            componentIdx: nComponents++,
            stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
            attributes: attributesFromComposite,
            state: { value: componentValue, fixed: true },
            doenetAttributes: {},
            children: []
          };
          newSerializedReplacements.push(serializedComponent);
        }
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: prevLength,
          serializedReplacements: newSerializedReplacements,
          replacementsToWithhold: 0
        };
        replacementChanges.push(replacementInstruction);
        workspace.replacementsCreated = stateIdInfo.num;
      }
    }
    lrp.type = type;
    lrp.from = from;
    lrp.length = length;
    lrp.step = step;
    lrp.exclude = exclude;
    workspace.lastReplacementParameters = lrp;
    return { replacementChanges, diagnostics, nComponents };
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    let type = "number";
    if (this.attributes.type && this.attributes.type.primitive) {
      type = this.attributes.type.primitive.value.toLowerCase();
    }
    if (!["number", "math", "letters"].includes(type)) {
      type = "number";
    }
    let rendererType = this.componentInfoObjects.allComponentClasses[type === "letters" ? "text" : type].rendererType;
    if (!allPotentialRendererTypes.includes(rendererType)) {
      allPotentialRendererTypes.push(rendererType);
    }
    return allPotentialRendererTypes;
  }
}
class Repeat extends CompositeComponent {
  static componentType = "repeat";
  static componentDocs = {
    summary: "Repeats template content for each item in a source"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static createsVariants = true;
  static includeBlankStringChildren = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  // Since we add components with names into the `for` attribute
  // (which cannot be done with DoenetML)
  // we need to add the `for` attribute to the resolver
  static addAttributeToResolver = "for";
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  // since don't have child groups, tell schema about children here
  static additionalSchemaChildren = ["_base"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.valueName = {
      createPrimitiveOfType: "string",
      createStateVariable: "valueName",
      defaultValue: null,
      description: "Name to bind the current item to inside the repeat template."
    };
    attributes.indexName = {
      createPrimitiveOfType: "string",
      createStateVariable: "indexName",
      defaultValue: null,
      description: "Name to bind the current 1-based index to inside the repeat template."
    };
    attributes.for = {
      createComponentOfType: "group",
      description: "The collection of items to iterate over when generating repeats."
    };
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "Component type used to wrap each item in the source."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether the generated repeats should be treated as responses for assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.sourcesComponentIdx = {
      returnDependencies: () => ({
        forAttr: {
          dependencyType: "attributeComponent",
          attributeName: "for"
        }
      }),
      definition({ dependencyValues }) {
        let sourcesComponentIdx = null;
        if (dependencyValues.forAttr) {
          sourcesComponentIdx = dependencyValues.forAttr.componentIdx;
        }
        return { setValue: { sourcesComponentIdx } };
      }
    };
    stateVariableDefinitions.numIterates = {
      additionalStateVariablesDefined: ["sourcesChildIndices"],
      stateVariablesDeterminingDependencies: ["sourcesComponentIdx"],
      returnDependencies: ({ stateValues }) => {
        if (stateValues.sourcesComponentIdx != null) {
          return {
            sources: {
              dependencyType: "replacement",
              compositeIdx: stateValues.sourcesComponentIdx,
              recursive: true,
              recurseNonStandardComposites: true
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        if (dependencyValues.sources) {
          const nonBlankSources = dependencyValues.sources.filter(
            (s2) => typeof s2 !== "string" || s2.trim() !== ""
          );
          let numIterates = nonBlankSources.length;
          let sourcesChildIndices = nonBlankSources.map(
            (x2) => x2.componentIdx
          );
          return {
            setValue: {
              numIterates,
              sourcesChildIndices
            }
          };
        } else {
          return {
            setValue: { numIterates: 0, sourcesChildIndices: [] }
          };
        }
      }
    };
    stateVariableDefinitions.serializedChildren = {
      additionalStateVariablesDefined: ["valueDummyIdx", "indexDummyIdx"],
      returnDependencies: () => ({
        serializedChildren: {
          dependencyType: "serializedChildren",
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        let valueDummyIdx = null;
        let indexDummyIdx = null;
        const repeatSetup = dependencyValues.serializedChildren.find(
          (child) => child.componentType === "_repeatSetup"
        );
        if (repeatSetup != void 0) {
          for (const child of repeatSetup.children) {
            if (child.componentType === "_placeholder") {
              valueDummyIdx = child.componentIdx;
            } else if (child.componentType === "integer") {
              indexDummyIdx = child.componentIdx;
            }
          }
        }
        const serializedChildren = dependencyValues.serializedChildren.filter(
          (child) => child.componentType !== "_repeatSetup"
        );
        if (serializedChildren.length > 0) {
          if (typeof serializedChildren[0] === "string") {
            const trimmed = serializedChildren[0].trimStart();
            if (trimmed.length > 0) {
              serializedChildren[0] = trimmed;
            } else {
              serializedChildren.splice(0, 1);
            }
          }
        }
        const n2 = serializedChildren.length;
        if (n2 > 0) {
          if (typeof serializedChildren[n2 - 1] === "string") {
            const trimmed = serializedChildren[n2 - 1].trimEnd();
            if (trimmed.length > 0) {
              serializedChildren[n2 - 1] = trimmed;
            } else {
              serializedChildren.pop();
            }
          }
        }
        return {
          setValue: {
            serializedChildren,
            valueDummyIdx,
            indexDummyIdx
          }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        numIterates: {
          dependencyType: "stateVariable",
          variableName: "numIterates"
        }
      }),
      // when this state variable is marked stale
      // it indicates we should update replacement
      // For this to work, must get value in replacement functions
      // so that the variable is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    nComponents,
    workspace,
    componentInfoObjects,
    isUpdate = false
  }) {
    let diagnostics = [];
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const numIterates = await component.stateValues.numIterates;
    workspace.lastReplacementParameters = {
      sourcesChildIndices: await component.stateValues.sourcesChildIndices,
      numIterates,
      replacementsToWithhold: 0,
      withheldSubstitutionChildNames: []
    };
    let replacements = [];
    for (let iter = 0; iter < numIterates; iter++) {
      const res = await this.replacementForIter({
        component,
        iter,
        componentInfoObjects,
        components,
        nComponents,
        isUpdate,
        workspace
      });
      replacements.push(...res.replacements);
      diagnostics.push(...res.diagnostics);
      nComponents = res.nComponents;
    }
    return { replacements, diagnostics, nComponents };
  }
  static async replacementForIter({
    component,
    iter,
    componentInfoObjects,
    components,
    isUpdate = false,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    let replacements = [
      {
        type: "serialized",
        componentType: "group",
        componentIdx: 0,
        // will be replaced, below
        attributes: {},
        doenetAttributes: {},
        state: {},
        children: deepClone(
          await component.stateValues.serializedChildren
        )
      }
    ];
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    const idxResult = createNewComponentIndices(
      replacements,
      nComponents,
      stateIdInfo
    );
    replacements = idxResult.components;
    nComponents = idxResult.nComponents;
    let compositeAttributesObj = this.createAttributesObject();
    const res = convertUnresolvedAttributesForComponentType({
      attributes: component.attributes,
      componentType: replacements[0].componentType,
      componentInfoObjects,
      compositeAttributesObj,
      nComponents,
      stateIdInfo
    });
    const attributesFromComposite = res.attributes;
    nComponents = res.nComponents;
    Object.assign(replacements[0].attributes, attributesFromComposite);
    const aliasResult = await addAndLinkAliasComponents$1(
      replacements[0],
      component,
      iter,
      nComponents,
      stateIdInfo
    );
    replacements[0] = aliasResult.replacement;
    nComponents = aliasResult.nComponents;
    if (component.unlinkedCopySource && !isUpdate) {
      await copyStateFromUnlinkedSource({
        components,
        component,
        iterateNumber: iter,
        replacement: replacements[0]
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return { replacements, diagnostics, nComponents };
  }
  static async calculateReplacementChanges({
    component,
    components,
    workspace,
    componentInfoObjects,
    nComponents
  }) {
    let diagnostics = [];
    let replacementChanges = [];
    let lrp = workspace.lastReplacementParameters;
    let recreateReplacements = false;
    let numIterates = await component.stateValues.numIterates;
    let sourcesChildIndices = await component.stateValues.sourcesChildIndices;
    let allSameChildSubstitutionNames = true;
    if (lrp.numIterates === void 0) {
      recreateReplacements = true;
    } else {
      let currentNIters = numIterates;
      let prevNIters = lrp.numIterates;
      if (currentNIters !== prevNIters) {
        allSameChildSubstitutionNames = false;
      }
      let minNiters = Math.min(currentNIters, prevNIters);
      for (let ind = 0; ind < minNiters; ind++) {
        if (sourcesChildIndices[ind] != lrp.sourcesChildIndices[ind]) {
          recreateReplacements = true;
          allSameChildSubstitutionNames = false;
          break;
        }
      }
    }
    if (allSameChildSubstitutionNames) {
      return { replacementChanges: [], diagnostics, nComponents };
    }
    if (recreateReplacements) {
      let replacementResults = await this.createSerializedReplacements({
        component,
        components,
        workspace,
        componentInfoObjects,
        nComponents,
        isUpdate: true
      });
      let newSerializedReplacements = replacementResults.replacements;
      diagnostics.push(...replacementResults.diagnostics);
      nComponents = replacementResults.nComponents;
      let replacementInstruction = {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: newSerializedReplacements,
        replacementsToWithhold: 0
      };
      replacementChanges.push(replacementInstruction);
      workspace.lastReplacementParameters = {
        sourcesChildIndices,
        numIterates,
        replacementsToWithhold: 0,
        withheldSubstitutionChildNames: []
      };
      return { replacementChanges, diagnostics, nComponents };
    }
    let currentNumIterates = await component.stateValues.numIterates;
    let prevNumIterates = lrp.numIterates;
    let newReplacementsToWithhold = 0;
    let currentReplacementsToWithhold = component.replacementsToWithhold;
    if (!currentReplacementsToWithhold) {
      currentReplacementsToWithhold = 0;
    }
    let withheldSubstitutionChildNames = lrp.withheldSubstitutionChildNames;
    let foundDeletedSourcesChild = false;
    if (currentNumIterates < prevNumIterates) {
      for (let ind = currentNumIterates; ind < prevNumIterates; ind++) {
        if (components[lrp.sourcesChildIndices[ind]] === void 0) {
          foundDeletedSourcesChild = true;
        }
      }
      if (!foundDeletedSourcesChild) {
        for (let name of lrp.withheldSubstitutionChildNames) {
          if (components[name] === void 0) {
            foundDeletedSourcesChild = true;
          }
        }
      }
    }
    if (foundDeletedSourcesChild) {
      let firstReplacementToDelete = Math.min(
        currentNumIterates,
        prevNumIterates
      );
      let numberReplacementsToDelete = component.replacements.length - firstReplacementToDelete;
      let replacementInstruction = {
        changeType: "delete",
        changeTopLevelReplacements: true,
        firstReplacementInd: firstReplacementToDelete,
        numberReplacementsToDelete,
        replacementsToWithhold: 0
      };
      replacementChanges.push(replacementInstruction);
      withheldSubstitutionChildNames = [];
      currentReplacementsToWithhold = 0;
    }
    if (currentNumIterates < prevNumIterates) {
      if (!foundDeletedSourcesChild) {
        newReplacementsToWithhold = component.replacements.length - currentNumIterates;
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: newReplacementsToWithhold
        };
        replacementChanges.push(replacementInstruction);
        let withheldNames = lrp.withheldSubstitutionChildNames;
        if (withheldNames) {
          withheldNames = [...withheldNames];
        } else {
          withheldNames = [];
        }
        withheldNames = /* @__PURE__ */ new Set([
          ...lrp.sourcesChildIndices.slice(currentNumIterates),
          ...withheldNames
        ]);
        withheldSubstitutionChildNames = withheldNames;
      }
    } else if (currentNumIterates > prevNumIterates) {
      let numReplacementsToAdd = currentNumIterates - prevNumIterates;
      if (currentReplacementsToWithhold > 0) {
        if (currentReplacementsToWithhold >= numReplacementsToAdd) {
          newReplacementsToWithhold = currentReplacementsToWithhold - numReplacementsToAdd;
          numReplacementsToAdd = 0;
          let replacementInstruction = {
            changeType: "changeReplacementsToWithhold",
            replacementsToWithhold: newReplacementsToWithhold
          };
          replacementChanges.push(replacementInstruction);
        } else {
          numReplacementsToAdd -= currentReplacementsToWithhold;
          prevNumIterates += currentReplacementsToWithhold;
          newReplacementsToWithhold = 0;
        }
      }
      if (numReplacementsToAdd > 0) {
        let replacements = [];
        for (let iter = prevNumIterates; iter < currentNumIterates; iter++) {
          let res = await this.replacementForIter({
            component,
            iter,
            componentInfoObjects,
            components,
            isUpdate: true,
            nComponents,
            workspace
          });
          replacements.push(...res.replacements);
          diagnostics.push(...res.diagnostics);
          nComponents = res.nComponents;
        }
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: prevNumIterates,
          serializedReplacements: replacements,
          replacementsToWithhold: 0
        };
        replacementChanges.push(replacementInstruction);
      }
    }
    workspace.lastReplacementParameters = {
      sourcesChildIndices,
      numIterates,
      replacementsToWithhold: newReplacementsToWithhold,
      withheldSubstitutionChildNames
    };
    return { replacementChanges, diagnostics, nComponents };
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents,
      useSubpartVariantRng: true
    });
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants !== void 0) {
      return { success: true, numVariants };
    }
    let descendantVariantComponents = gatherVariantComponents({
      serializedComponents: serializedComponent.children,
      componentInfoObjects
    });
    let numVariantsByDescendant = [];
    for (let descendant of descendantVariantComponents) {
      let descendantClass = componentInfoObjects.allComponentClasses[descendant.componentType];
      let result = descendantClass.determineNumberOfUniqueVariants({
        serializedComponent: descendant,
        componentInfoObjects,
        infoDiagnostics
      });
      if (!result.success) {
        return { success: false };
      }
      numVariantsByDescendant.push(result.numVariants);
    }
    if (numVariantsByDescendant.every((x2) => x2 === 1)) {
      serializedComponent.variants.numVariants = 1;
      return {
        success: true,
        numVariants: 1
      };
    }
    return { success: false };
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let desiredVariant = { index: variantIndex };
    return { success: true, desiredVariant };
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    let additionalRendererTypes = this.potentialRendererTypesFromSerializedComponents(
      this.serializedChildren
    );
    for (let rendererType of additionalRendererTypes) {
      if (!allPotentialRendererTypes.includes(rendererType)) {
        allPotentialRendererTypes.push(rendererType);
      }
    }
    return allPotentialRendererTypes;
  }
}
async function copyStateFromUnlinkedSource({
  components,
  component,
  iterateNumber,
  replacement
}) {
  let sourceGroup = components[component.unlinkedCopySource].replacements[iterateNumber];
  if (sourceGroup) {
    let serializedSourceGroup = await sourceGroup.serialize({
      copyAll: true,
      copyVariants: true,
      copyPrimaryEssential: true,
      copyEssentialState: true
    });
    copyStateFromUnlinkedSourceSub(
      replacement.children,
      serializedSourceGroup.children
    );
  }
}
function copyStateFromUnlinkedSourceSub(replacements, sources) {
  for (let [i2, repl] of replacements.entries()) {
    let src = sources[i2];
    if (typeof repl === "object" && repl.componentType === src.componentType) {
      repl.state = src.state;
      if (repl.children && src.children) {
        copyStateFromUnlinkedSourceSub(repl.children, src.children);
      }
      for (let attrName in repl.attributes) {
        if (repl.attributes[attrName].component && src.attributes[attrName]?.component) {
          copyStateFromUnlinkedSourceSub(
            [repl.attributes[attrName].component],
            [src.attributes[attrName].component]
          );
        }
      }
    }
  }
}
async function addAndLinkAliasComponents$1(thisRepl, component, iter, nComponents, stateIdInfo) {
  let sourcesComponentIdx = await component.stateValues.sourcesComponentIdx;
  const setupComponent = {
    type: "serialized",
    componentType: "setup",
    componentIdx: nComponents++,
    stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
    attributes: {},
    doenetAttributes: {},
    state: {},
    children: []
  };
  const valueName = await component.stateValues.valueName;
  const extendIdxMapping = {};
  if (valueName) {
    const valueComponentIdx = nComponents++;
    const valueDummyIdx = await component.stateValues.valueDummyIdx;
    if (valueDummyIdx != null) {
      extendIdxMapping[valueDummyIdx] = valueComponentIdx;
    }
    setupComponent.children.push({
      type: "serialized",
      componentType: "_copy",
      componentIdx: nComponents++,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      attributes: {
        createComponentIdx: {
          type: "primitive",
          name: "createComponentIdx",
          primitive: { type: "number", value: valueComponentIdx }
        },
        createComponentName: {
          type: "primitive",
          name: "createComponentName",
          primitive: { type: "string", value: valueName }
        }
      },
      doenetAttributes: {},
      children: [],
      state: {},
      extending: {
        Ref: {
          nodeIdx: sourcesComponentIdx,
          originalPath: [
            { name: "", index: [{ value: [`${iter + 1}`] }] }
          ],
          unresolvedPath: [
            // get the item from the sources
            { name: "", index: [{ value: [`${iter + 1}`] }] }
          ],
          nodesInResolvedPath: [sourcesComponentIdx]
        }
      }
    });
  }
  const indexName = await component.stateValues.indexName;
  if (indexName) {
    const indexComponentIdx = nComponents++;
    const indexDummyIdx = await component.stateValues.indexDummyIdx;
    if (indexDummyIdx != null) {
      extendIdxMapping[indexDummyIdx] = indexComponentIdx;
    }
    setupComponent.children.push({
      type: "serialized",
      componentType: "integer",
      componentIdx: indexComponentIdx,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      attributes: {
        name: {
          type: "primitive",
          name: "name",
          primitive: { type: "string", value: indexName }
        }
      },
      doenetAttributes: {},
      children: [],
      state: { value: iter + 1, fixed: true }
    });
  }
  const newRepl = { ...thisRepl };
  if (Object.keys(extendIdxMapping).length > 0) {
    newRepl.children = remapExtendIndices(
      thisRepl.children,
      extendIdxMapping
    );
  }
  newRepl.children.push(setupComponent);
  return { replacement: newRepl, nComponents };
}
function remapExtendIndices(components, extendIdxMapping) {
  const newComponents = [];
  for (const comp of components) {
    if (typeof comp === "string") {
      newComponents.push(comp);
      continue;
    }
    let newComponent = { ...comp };
    const extending = newComponent.extending;
    if (extending) {
      const refResolution = unwrapSource(extending);
      const newRefResolution = { ...refResolution };
      const remapIdx = extendIdxMapping[refResolution.nodeIdx];
      if (remapIdx != void 0) {
        newRefResolution.nodeIdx = remapIdx;
      }
      if (refResolution.unresolvedPath) {
        newRefResolution.unresolvedPath = remapExtendIndicesInPath(
          refResolution.unresolvedPath,
          extendIdxMapping
        );
      }
      newRefResolution.originalPath = remapExtendIndicesInPath(
        refResolution.originalPath,
        extendIdxMapping
      );
      newRefResolution.nodesInResolvedPath = refResolution.nodesInResolvedPath.map((idx) => {
        const remapIdx2 = extendIdxMapping[idx];
        if (remapIdx2 != void 0) {
          return remapIdx2;
        } else {
          return idx;
        }
      });
      newComponent.extending = addSource(newRefResolution, extending);
    }
    newComponent.children = remapExtendIndices(
      newComponent.children,
      extendIdxMapping
    );
    const attributes = { ...newComponent.attributes };
    for (const attrName in attributes) {
      const attr = attributes[attrName];
      if (attr.type === "component") {
        attr.component = remapExtendIndices(
          [attr.component],
          extendIdxMapping
        )[0];
      } else if (attr.type === "references") {
        attr.references = remapExtendIndices(
          attr.references,
          extendIdxMapping
        );
      } else if (attr.type === "unresolved") {
        attr.children = remapExtendIndices(
          attr.children,
          extendIdxMapping
        );
      }
    }
    newComponent.attributes = attributes;
    newComponents.push(newComponent);
  }
  return newComponents;
}
function remapExtendIndicesInPath(path, extendIdxMapping) {
  const unresolvedPath = [];
  for (const pathPath of path) {
    const newPathPart = { ...pathPath };
    const index2 = [];
    for (const indexPart of newPathPart.index) {
      const newIndexPart = { ...indexPart };
      newIndexPart.value = remapExtendIndices(
        newIndexPart.value,
        extendIdxMapping
      );
      index2.push(newIndexPart);
    }
    newPathPart.index = index2;
    unresolvedPath.push(newPathPart);
  }
  return unresolvedPath;
}
class RepeatForSequence extends CompositeComponent {
  static componentType = "repeatForSequence";
  static componentDocs = {
    summary: "Repeats template content for each value in an arithmetic sequence"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static createsVariants = true;
  static includeBlankStringChildren = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  // since don't have child groups, tell schema about children here
  static additionalSchemaChildren = ["_base"];
  static createAttributesObject() {
    const attributes = super.createAttributesObject();
    const sequenceAttributes = returnStandardSequenceAttributes();
    Object.assign(attributes, sequenceAttributes);
    attributes.valueName = {
      createPrimitiveOfType: "string",
      createStateVariable: "valueName",
      defaultValue: null,
      description: "Name to bind the current item to inside the repeat template."
    };
    attributes.indexName = {
      createPrimitiveOfType: "string",
      createStateVariable: "indexName",
      defaultValue: null,
      description: "Name to bind the current 1-based index to inside the repeat template."
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether the generated repeats should be treated as responses for assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    const stateVariableDefinitions = super.returnStateVariableDefinitions();
    const sequenceDefs = returnStandardSequenceStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, sequenceDefs);
    stateVariableDefinitions.forValues = {
      additionalStateVariablesDefined: ["numIterates"],
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        length: {
          dependencyType: "stateVariable",
          variableName: "length"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        },
        lowercase: {
          dependencyType: "stateVariable",
          variableName: "lowercase"
        }
      }),
      definition({ dependencyValues }) {
        let forValues = returnSequenceValues(dependencyValues);
        return {
          setValue: {
            forValues,
            numIterates: forValues.length
          }
        };
      }
    };
    stateVariableDefinitions.serializedChildren = {
      additionalStateVariablesDefined: ["valueDummyIdx", "indexDummyIdx"],
      returnDependencies: () => ({
        serializedChildren: {
          dependencyType: "serializedChildren",
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        let valueDummyIdx = null;
        let indexDummyIdx = null;
        const repeatSetup = dependencyValues.serializedChildren.find(
          (child) => child.componentType === "_repeatSetup"
        );
        if (repeatSetup != void 0) {
          for (const child of repeatSetup.children) {
            if (child.componentType === "_placeholder") {
              valueDummyIdx = child.componentIdx;
            } else if (child.componentType === "integer") {
              indexDummyIdx = child.componentIdx;
            }
          }
        }
        const serializedChildren = dependencyValues.serializedChildren.filter(
          (child) => child.componentType !== "_repeatSetup"
        );
        if (serializedChildren.length > 0) {
          if (typeof serializedChildren[0] === "string") {
            const trimmed = serializedChildren[0].trimStart();
            if (trimmed.length > 0) {
              serializedChildren[0] = trimmed;
            } else {
              serializedChildren.splice(0, 1);
            }
          }
        }
        const n2 = serializedChildren.length;
        if (n2 > 0) {
          if (typeof serializedChildren[n2 - 1] === "string") {
            const trimmed = serializedChildren[n2 - 1].trimEnd();
            if (trimmed.length > 0) {
              serializedChildren[n2 - 1] = trimmed;
            } else {
              serializedChildren.pop();
            }
          }
        }
        return {
          setValue: {
            serializedChildren,
            valueDummyIdx,
            indexDummyIdx
          }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        numIterates: {
          dependencyType: "stateVariable",
          variableName: "numIterates"
        }
      }),
      // when this state variable is marked stale
      // it indicates we should update replacement
      // For this to work, must get value in replacement functions
      // so that the variable is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    nComponents,
    workspace,
    componentInfoObjects
  }) {
    let diagnostics = [];
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    if (!await component.stateValues.validSequence) {
      workspace.lastReplacementParameters = {
        from: null,
        length: null,
        step: null,
        type: null,
        exclude: null
      };
      return { replacements: [], diagnostics, nComponents };
    }
    let from = await component.stateValues.from;
    let length = await component.stateValues.length;
    let step = await component.stateValues.step;
    let type = await component.stateValues.type;
    let exclude = await component.stateValues.exclude;
    workspace.lastReplacementParameters = {
      from,
      length,
      step,
      type,
      exclude
    };
    const numIterates = await component.stateValues.numIterates;
    let replacements = [];
    workspace.valueComponentIndices = [];
    for (let iter = 0; iter < numIterates; iter++) {
      const res = await this.replacementForIter({
        component,
        iter,
        componentInfoObjects,
        components,
        nComponents,
        workspace
      });
      replacements.push(...res.replacements);
      diagnostics.push(...res.diagnostics);
      nComponents = res.nComponents;
      workspace.valueComponentIndices.push(res.valueComponentIdx);
    }
    return { replacements, diagnostics, nComponents };
  }
  static async replacementForIter({
    component,
    iter,
    componentInfoObjects,
    components,
    isUpdate = false,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    let replacements = [
      {
        type: "serialized",
        componentType: "group",
        componentIdx: 0,
        // will be replaced, below
        attributes: {},
        doenetAttributes: {},
        state: {},
        children: deepClone(
          await component.stateValues.serializedChildren
        )
      }
    ];
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    const idxResult = createNewComponentIndices(
      replacements,
      nComponents,
      stateIdInfo
    );
    replacements = idxResult.components;
    nComponents = idxResult.nComponents;
    let compositeAttributesObj = this.createAttributesObject();
    const res = convertUnresolvedAttributesForComponentType({
      attributes: component.attributes,
      componentType: replacements[0].componentType,
      componentInfoObjects,
      compositeAttributesObj,
      nComponents,
      stateIdInfo
    });
    const attributesFromComposite = res.attributes;
    nComponents = res.nComponents;
    Object.assign(replacements[0].attributes, attributesFromComposite);
    const aliasResult = await addAndLinkAliasComponents(
      replacements[0],
      component,
      iter,
      nComponents,
      stateIdInfo
    );
    replacements[0] = aliasResult.replacement;
    nComponents = aliasResult.nComponents;
    const valueComponentIdx = aliasResult.valueComponentIdx;
    if (component.unlinkedCopySource && !isUpdate) {
      await copyStateFromUnlinkedSource({
        components,
        component,
        iterateNumber: iter,
        replacement: replacements[0]
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents,
      valueComponentIdx
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    workspace,
    componentInfoObjects,
    nComponents
  }) {
    let diagnostics = [];
    let replacementChanges = [];
    let lrp = { ...workspace.lastReplacementParameters };
    if (!await component.stateValues.validSequence) {
      let currentReplacementsWithheld = component.replacementsToWithhold;
      if (!currentReplacementsWithheld) {
        currentReplacementsWithheld = 0;
      }
      if (component.replacements.length - currentReplacementsWithheld > 0) {
        let replacementsToWithhold = component.replacements.length;
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold
        };
        replacementChanges.push(replacementInstruction);
      }
      lrp.length = 0;
      workspace.lastReplacementParameters = lrp;
      return { replacementChanges, diagnostics, nComponents };
    }
    let from = await component.stateValues.from;
    let length = await component.stateValues.length;
    let step = await component.stateValues.step;
    let type = await component.stateValues.type;
    let exclude = await component.stateValues.exclude;
    if (lrp.type !== type || lrp.exclude.length > 0 || exclude.length > 0) {
      let replacementResults = await this.createSerializedReplacements({
        component,
        workspace,
        componentInfoObjects,
        nComponents
      });
      let newSerializedReplacements = replacementResults.replacements;
      diagnostics.push(...replacementResults.diagnostics);
      nComponents = replacementResults.nComponents;
      let replacementInstruction = {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: newSerializedReplacements,
        replacementsToWithhold: 0
      };
      replacementChanges.push(replacementInstruction);
    } else {
      let modifyExistingValues = false;
      if (type === "math") {
        if (!(from.equals(lrp.from) && step.equals(lrp.step))) {
          modifyExistingValues = true;
        }
      } else {
        if (from !== lrp.from || step !== lrp.step) {
          modifyExistingValues = true;
        }
      }
      let prevLength = lrp.length;
      let numReplacementsToAdd = 0;
      let numToModify = 0;
      let firstToModify = prevLength;
      let newReplacementsToWithhold;
      if (length < prevLength) {
        newReplacementsToWithhold = component.replacements.length - length;
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: newReplacementsToWithhold
        };
        replacementChanges.push(replacementInstruction);
      } else if (length > prevLength) {
        numReplacementsToAdd = length - prevLength;
        if (component.replacementsToWithhold > 0) {
          if (component.replacementsToWithhold >= numReplacementsToAdd) {
            newReplacementsToWithhold = component.replacementsToWithhold - numReplacementsToAdd;
            numToModify += numReplacementsToAdd;
            prevLength += numReplacementsToAdd;
            numReplacementsToAdd = 0;
            let replacementInstruction = {
              changeType: "changeReplacementsToWithhold",
              replacementsToWithhold: newReplacementsToWithhold
            };
            replacementChanges.push(replacementInstruction);
          } else {
            numReplacementsToAdd -= component.replacementsToWithhold;
            numToModify += component.replacementsToWithhold;
            prevLength += component.replacementsToWithhold;
            newReplacementsToWithhold = 0;
          }
        }
      }
      if (modifyExistingValues === true) {
        numToModify = prevLength;
        firstToModify = 0;
      }
      if (numToModify > 0) {
        for (let ind = firstToModify; ind < firstToModify + numToModify; ind++) {
          if (workspace.valueComponentIndices[ind] != void 0) {
            const componentValue = (await component.stateValues.forValues)[ind];
            const replacementInstruction = {
              changeType: "updateStateVariables",
              component: {
                componentIdx: workspace.valueComponentIndices[ind]
              },
              stateChanges: { value: componentValue }
            };
            replacementChanges.push(replacementInstruction);
          }
        }
      }
      if (numReplacementsToAdd > 0) {
        let newSerializedReplacements = [];
        for (let ind = prevLength; ind < await component.stateValues.length; ind++) {
          const res = await this.replacementForIter({
            component,
            iter: ind,
            componentInfoObjects,
            components,
            nComponents,
            workspace
          });
          newSerializedReplacements.push(...res.replacements);
          diagnostics.push(...res.diagnostics);
          nComponents = res.nComponents;
          workspace.valueComponentIndices[ind] = res.valueComponentIdx;
        }
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: prevLength,
          serializedReplacements: newSerializedReplacements,
          replacementsToWithhold: 0
        };
        replacementChanges.push(replacementInstruction);
      }
    }
    lrp.type = type;
    lrp.from = from;
    lrp.length = length;
    lrp.step = step;
    lrp.exclude = exclude;
    workspace.lastReplacementParameters = lrp;
    return { replacementChanges, diagnostics, nComponents };
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents,
      useSubpartVariantRng: true
    });
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants !== void 0) {
      return { success: true, numVariants };
    }
    let descendantVariantComponents = gatherVariantComponents({
      serializedComponents: serializedComponent.children,
      componentInfoObjects
    });
    let numVariantsByDescendant = [];
    for (let descendant of descendantVariantComponents) {
      let descendantClass = componentInfoObjects.allComponentClasses[descendant.componentType];
      let result = descendantClass.determineNumberOfUniqueVariants({
        serializedComponent: descendant,
        componentInfoObjects,
        infoDiagnostics
      });
      if (!result.success) {
        return { success: false };
      }
      numVariantsByDescendant.push(result.numVariants);
    }
    if (numVariantsByDescendant.every((x2) => x2 === 1)) {
      serializedComponent.variants.numVariants = 1;
      return {
        success: true,
        numVariants: 1
      };
    }
    return { success: false };
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let desiredVariant = { index: variantIndex };
    return { success: true, desiredVariant };
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    let additionalRendererTypes = this.potentialRendererTypesFromSerializedComponents(
      this.serializedChildren
    );
    for (let rendererType of additionalRendererTypes) {
      if (!allPotentialRendererTypes.includes(rendererType)) {
        allPotentialRendererTypes.push(rendererType);
      }
    }
    return allPotentialRendererTypes;
  }
}
async function addAndLinkAliasComponents(thisRepl, component, iter, nComponents, stateIdInfo) {
  const setupComponent = {
    type: "serialized",
    componentType: "setup",
    componentIdx: nComponents++,
    stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
    attributes: {},
    doenetAttributes: {},
    state: {},
    children: []
  };
  const valueName = await component.stateValues.valueName;
  const extendIdxMapping = {};
  let valueComponentIdx;
  if (valueName) {
    valueComponentIdx = nComponents++;
    const valueDummyIdx = await component.stateValues.valueDummyIdx;
    if (valueDummyIdx != null) {
      extendIdxMapping[valueDummyIdx] = valueComponentIdx;
    }
    let type = await component.stateValues.type;
    if (type === "letters") {
      type = "text";
    }
    setupComponent.children.push({
      type: "serialized",
      componentType: type,
      componentIdx: valueComponentIdx,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      attributes: {
        name: {
          type: "primitive",
          name: "name",
          primitive: { type: "string", value: valueName }
        }
      },
      doenetAttributes: {},
      children: [],
      state: {
        fixed: true,
        value: (await component.stateValues.forValues)[iter]
      }
    });
  }
  const indexName = await component.stateValues.indexName;
  if (indexName) {
    const indexComponentIdx = nComponents++;
    const indexDummyIdx = await component.stateValues.indexDummyIdx;
    if (indexDummyIdx != null) {
      extendIdxMapping[indexDummyIdx] = indexComponentIdx;
    }
    setupComponent.children.push({
      type: "serialized",
      componentType: "integer",
      componentIdx: indexComponentIdx,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      attributes: {
        name: {
          type: "primitive",
          name: "name",
          primitive: { type: "string", value: indexName }
        }
      },
      doenetAttributes: {},
      children: [],
      state: { value: iter + 1, fixed: true }
    });
  }
  const newRepl = { ...thisRepl };
  if (Object.keys(extendIdxMapping).length > 0) {
    newRepl.children = remapExtendIndices(
      thisRepl.children,
      extendIdxMapping
    );
  }
  newRepl.children.push(setupComponent);
  return { replacement: newRepl, nComponents, valueComponentIdx };
}
class Slider extends BaseComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      changeValue: this.changeValue.bind(this)
    });
  }
  static componentType = "slider";
  static componentDocs = {
    summary: "An interactive slider input"
  };
  static variableForImplicitProp = "value";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      createPrimitiveOfType: "string",
      createStateVariable: "type",
      defaultValue: "number",
      toLowerCase: true,
      validValues: [
        {
          value: "number",
          description: "Slider produces numeric values between `from` and `to`."
        },
        {
          value: "text",
          description: "Slider produces text values, one per child of the slider."
        }
      ],
      description: "Type of value the slider produces.",
      forRenderer: true,
      highlighted: true
    };
    attributes.width = {
      description: "Display width of the slider.",
      createComponentOfType: "componentSize",
      createStateVariable: "width",
      defaultValue: { size: 300, isAbsolute: true },
      public: true,
      forRenderer: true
    };
    attributes.height = {
      description: "Display height of the slider.",
      createComponentOfType: "componentSize",
      createStateVariable: "height",
      defaultValue: { size: 100, isAbsolute: true },
      public: true,
      forRenderer: true
    };
    attributes.initialValue = {
      createComponentOfType: "_componentWithSelectableType",
      createStateVariable: "initialValue",
      defaultValue: null,
      description: "Initial value of the slider before any user interaction."
    };
    attributes.showControls = {
      description: "Whether to render play/pause controls alongside the slider.",
      createComponentOfType: "boolean",
      createStateVariable: "showControls",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.showTicks = {
      description: "Whether to render tick marks on the slider.",
      createComponentOfType: "boolean",
      createStateVariable: "showTicks",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.rotateTickLabels = {
      description: "Whether to rotate the tick labels.",
      createComponentOfType: "boolean",
      createStateVariable: "rotateTickLabels",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.showValue = {
      description: "Whether to display the current value next to the slider.",
      createComponentOfType: "boolean",
      createStateVariable: "showValue",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.from = {
      description: "Minimum value of the slider's range.",
      createComponentOfType: "number",
      createStateVariable: "from",
      defaultValue: 0,
      public: true,
      forRenderer: true,
      highlighted: true
    };
    attributes.to = {
      description: "Maximum value of the slider's range.",
      createComponentOfType: "number",
      createStateVariable: "to",
      defaultValue: 10,
      public: true,
      forRenderer: true,
      highlighted: true
    };
    attributes.step = {
      description: "Increment between successive slider positions.",
      createComponentOfType: "number",
      createStateVariable: "step",
      defaultValue: 1,
      public: true,
      forRenderer: true,
      highlighted: true
    };
    Object.assign(attributes, returnLabelAttributes());
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.bindValueTo = {
      createComponentOfType: "_componentWithSelectableType",
      description: "Two-way binding target for the input's value."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "numbersTexts",
        componentTypes: ["number", "text"]
      },
      {
        group: "markers",
        componentTypes: ["markers"]
      },
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let labelDefinitions = returnLabelStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, labelDefinitions);
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.items = {
      description: "The values the slider can take.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true
      },
      // isArray: true,
      // entryPrefixes: ["item"],
      // entireArrayAtOnce: true,
      returnDependencies: () => ({
        numberAndTextChildren: {
          dependencyType: "child",
          childGroups: ["numbersTexts"],
          variableNames: ["value"]
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        }
      }),
      definition: function({ dependencyValues, componentInfoObjects }) {
        let items = [];
        if (dependencyValues.type === "text") {
          for (let child of dependencyValues.numberAndTextChildren) {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "text"
            })) {
              items.push(child.stateValues.value);
            } else {
              items.push(child.stateValues.value.toString());
            }
          }
        } else if (dependencyValues.numberAndTextChildren.length > 0) {
          for (let child of dependencyValues.numberAndTextChildren) {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "number"
            })) {
              items.push(child.stateValues.value);
            } else {
              let num = Number(child.stateValues.value);
              if (Number.isFinite(num)) {
                items.push(num);
              }
            }
          }
          items.sort((a2, b2) => {
            return a2 - b2;
          });
        }
        return {
          setValue: { items },
          setCreateComponentOfType: { items: dependencyValues.type }
        };
      }
    };
    stateVariableDefinitions.numItems = {
      description: "The total number of values.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        items: {
          dependencyType: "stateVariable",
          variableName: "items"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        to: {
          dependencyType: "stateVariable",
          variableName: "to"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        }
      }),
      definition({ dependencyValues }) {
        let numItems = dependencyValues.items.length;
        if (numItems === 0 && dependencyValues.type === "number") {
          numItems = Math.floor(
            (dependencyValues.to - dependencyValues.from) / dependencyValues.step + 1e-10
          ) + 1;
          if (!(numItems >= 0 && Number.isFinite(numItems))) {
            numItems = 0;
          }
        }
        return { setValue: { numItems } };
      }
    };
    stateVariableDefinitions.firstItem = {
      forRenderer: true,
      returnDependencies: () => ({
        items: {
          dependencyType: "stateVariable",
          variableName: "items"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        }
      }),
      definition({ dependencyValues }) {
        let firstItem;
        if (dependencyValues.items.length > 0) {
          firstItem = dependencyValues.items[0];
        } else if (dependencyValues.type === "number") {
          firstItem = dependencyValues.from;
        } else {
          firstItem = null;
        }
        return { setValue: { firstItem } };
      }
    };
    stateVariableDefinitions.lastItem = {
      forRenderer: true,
      returnDependencies: () => ({
        items: {
          dependencyType: "stateVariable",
          variableName: "items"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        numItems: {
          dependencyType: "stateVariable",
          variableName: "numItems"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        }
      }),
      definition({ dependencyValues }) {
        let lastItem;
        if (dependencyValues.items.length > 0) {
          lastItem = dependencyValues.items[dependencyValues.items.length - 1];
        } else if (dependencyValues.type === "number") {
          lastItem = dependencyValues.from + (dependencyValues.numItems - 1) * dependencyValues.step;
        } else {
          lastItem = null;
        }
        return { setValue: { lastItem } };
      }
    };
    stateVariableDefinitions.valueToIndex = {
      forRenderer: true,
      returnDependencies: () => ({
        items: {
          dependencyType: "stateVariable",
          variableName: "items"
        }
      }),
      definition: function({ dependencyValues }) {
        let valueToIndex = {};
        for (let [ind, item] of dependencyValues.items.entries()) {
          valueToIndex[item] = ind;
        }
        return { setValue: { valueToIndex } };
      }
    };
    stateVariableDefinitions.preliminaryValue = {
      hasEssential: true,
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        initialValue: {
          dependencyType: "stateVariable",
          variableName: "initialValue"
        },
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (!dependencyValues.bindValueTo) {
          return {
            useEssentialOrDefaultValue: {
              preliminaryValue: {
                get defaultValue() {
                  return dependencyValues.initialValue;
                }
              }
            }
          };
        }
        return {
          setValue: {
            preliminaryValue: dependencyValues.bindValueTo.stateValues.value
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.bindValueTo) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "bindValueTo",
                desiredValue: desiredStateVariableValues.preliminaryValue,
                variableIndex: 0
              }
            ]
          };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "preliminaryValue",
              value: desiredStateVariableValues.preliminaryValue
            }
          ]
        };
      }
    };
    stateVariableDefinitions.index = {
      forRenderer: true,
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        valueToIndex: {
          dependencyType: "stateVariable",
          variableName: "valueToIndex"
        },
        items: {
          dependencyType: "stateVariable",
          variableName: "items"
        },
        preliminaryValue: {
          dependencyType: "stateVariable",
          variableName: "preliminaryValue"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        numItems: {
          dependencyType: "stateVariable",
          variableName: "numItems"
        }
      }),
      definition: function({ dependencyValues }) {
        let index2 = findIndexOfClosestValidValue(dependencyValues);
        if (index2 === void 0) {
          index2 = 0;
        }
        return { setValue: { index: index2 } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.items.length === 0) {
          let from = dependencyValues.from;
          let step = dependencyValues.step;
          let ind = desiredStateVariableValues.index;
          if (!Number.isInteger(ind)) {
            return { success: false };
          }
          if (ind >= 0 && ind < dependencyValues.numItems) {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "preliminaryValue",
                  desiredValue: from + ind * step
                }
              ]
            };
          } else {
            return { success: false };
          }
        }
        let desiredValue = dependencyValues.items[desiredStateVariableValues.index];
        if (desiredValue === void 0) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "preliminaryValue",
              desiredValue
            }
          ]
        };
      }
    };
    stateVariableDefinitions.value = {
      description: "The current value of the slider.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true,
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        items: {
          dependencyType: "stateVariable",
          variableName: "items"
        },
        index: {
          dependencyType: "stateVariable",
          variableName: "index"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        }
      }),
      definition: function({ dependencyValues }) {
        let value;
        if (dependencyValues.items.length > 0) {
          value = dependencyValues.items[dependencyValues.index];
        } else {
          value = dependencyValues.from + dependencyValues.step * dependencyValues.index;
        }
        return {
          setValue: { value },
          setCreateComponentOfType: { value: dependencyValues.type }
        };
      },
      inverseDefinition: invertSliderValue
    };
    stateVariableDefinitions.valueForDisplay = {
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        }
      }),
      definition: function({ dependencyValues }) {
        let rounded = roundForDisplay({
          value: dependencyValues.value,
          dependencyValues
        });
        return {
          setValue: {
            valueForDisplay: rounded.tree
          }
        };
      }
    };
    stateVariableDefinitions.markers = {
      forRenderer: true,
      returnDependencies: () => ({
        markersChild: {
          dependencyType: "child",
          childGroups: ["markers"],
          variableNames: ["markerType", "markers"]
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        items: {
          dependencyType: "stateVariable",
          variableName: "items"
        }
      }),
      definition: function({ dependencyValues }) {
        let markers = [];
        let diagnostics = [];
        if (dependencyValues.markersChild.length > 0) {
          let markerType = dependencyValues.markersChild[0].stateValues.markerType;
          if (markerType === "empty") {
            markers = [...dependencyValues.items];
          } else if (markerType !== dependencyValues.type) {
            const warning = {
              type: "warning",
              message: "Markers type doesn't match slider type."
            };
            if (dependencyValues.markersChild[0].position) {
              warning.position = dependencyValues.markersChild[0].position;
            }
            diagnostics.push(warning);
            markers = [];
          } else {
            markers = dependencyValues.markersChild[0].stateValues.markers;
          }
        }
        return { setValue: { markers }, sendDiagnostics: diagnostics };
      }
    };
    return stateVariableDefinitions;
  }
  async changeValue({
    value,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (value === void 0) {
      return;
    }
    if (!await this.stateValues.disabled) {
      if (transient) {
        return await this.coreFunctions.performUpdate({
          updateInstructions: [
            {
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "value",
              value
            }
          ],
          transient,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      } else {
        return await this.coreFunctions.performUpdate({
          updateInstructions: [
            {
              updateType: "updateValue",
              componentIdx: this.componentIdx,
              stateVariable: "value",
              value
            }
          ],
          actionId,
          sourceInformation,
          skipRendererUpdate,
          event: {
            verb: "selected",
            object: {
              componentIdx: this.componentIdx,
              componentType: this.componentType
            },
            result: {
              response: value,
              responseText: value.toString()
            }
          }
        });
      }
    }
  }
}
function findIndexOfClosestValidValue({
  preliminaryValue,
  valueToIndex,
  type,
  items,
  from,
  step,
  numItems
}) {
  let value = preliminaryValue;
  if (items.length === 0) {
    if (!Number.isFinite(value)) {
      return void 0;
    }
    let ind = Math.round((value - from) / step);
    if (ind >= 0) {
      if (ind < numItems) {
        return ind;
      } else {
        return numItems - 1;
      }
    } else {
      return 0;
    }
  }
  let matchedIndex = valueToIndex[value];
  if (matchedIndex !== void 0) {
    return matchedIndex;
  }
  if (type === "text") {
    return void 0;
  }
  let findNextLargerIndex = function(minIndex = 0, maxIndex = items.length - 1) {
    if (maxIndex <= minIndex + 1) {
      if (value > items[minIndex]) {
        return maxIndex;
      } else {
        return minIndex;
      }
    }
    let midIndex = Math.round((maxIndex + minIndex) / 2);
    if (value > items[midIndex]) {
      return findNextLargerIndex(midIndex, maxIndex);
    } else {
      return findNextLargerIndex(minIndex, midIndex);
    }
  };
  let closeIndex = findNextLargerIndex();
  if (closeIndex !== 0) {
    let leftValue = items[closeIndex - 1];
    let rightValue = items[closeIndex];
    let leftDist = Math.abs(value - leftValue);
    let rightDist = Math.abs(value - rightValue);
    if (leftDist < rightDist) {
      closeIndex--;
    }
  }
  return closeIndex;
}
async function invertSliderValue({ desiredStateVariableValues, stateValues }) {
  let preliminaryValue = desiredStateVariableValues.value;
  if (await stateValues.type === "text") {
    preliminaryValue = preliminaryValue.toString();
  } else {
    if (preliminaryValue instanceof me$3.class) {
      preliminaryValue = preliminaryValue.evaluate_to_constant();
      if (Number.isNaN(preliminaryValue)) {
        return { success: false };
      }
    } else {
      preliminaryValue = Number(preliminaryValue);
    }
  }
  let newIndex = findIndexOfClosestValidValue({
    preliminaryValue,
    valueToIndex: await stateValues.valueToIndex,
    type: await stateValues.type,
    items: await stateValues.items,
    from: await stateValues.from,
    step: await stateValues.step,
    numItems: await stateValues.numItems
  });
  if (newIndex === void 0) {
    return { success: false };
  } else {
    return {
      success: true,
      instructions: [
        {
          setDependency: "index",
          desiredValue: newIndex
        }
      ]
    };
  }
}
class Markers extends BaseComponent {
  static componentType = "markers";
  // TODO: <markers> is currently non-functional — examples crash with
  // "tA.returnDependencies is not a function" because the recent <slider>
  // rework dropped the supporting infrastructure. Excluded from the schema
  // until that is restored; tracked in
  // https://github.com/Doenet/DoenetML/issues/1164.
  static excludeFromSchema = true;
  static componentDocs = {
    summary: "A list of text or number markers used by graphical components (e.g. axis tick labels)."
  };
  static returnChildGroups() {
    return [
      {
        group: "texts",
        componentTypes: ["text"]
      },
      {
        group: "numbers",
        componentTypes: ["number"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.markerType = {
      description: 'The type of markers ("text", "number", or "empty").',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        textChildren: {
          dependencyType: "child",
          childGroups: ["texts"]
        },
        numberChildren: {
          dependencyType: "child",
          childGroups: ["numbers"]
        }
      }),
      definition: function({ dependencyValues }) {
        let markerType;
        if (dependencyValues.textChildren.length > 0) {
          markerType = "text";
        } else if (dependencyValues.numberChildren.length > 0) {
          markerType = "number";
        } else {
          markerType = "empty";
        }
        return { setValue: { markerType } };
      }
    };
    stateVariableDefinitions.markers = {
      description: "The list of marker values.",
      public: true,
      isArray: true,
      shadowingInstructions: {
        hasVariableComponentType: true
      },
      entryPrefixes: ["item"],
      returnDependencies: () => ({
        markerType: {
          dependencyType: "stateVariable",
          variableName: "markerType"
        },
        textChildren: {
          dependencyType: "child",
          childGroups: ["texts"],
          variableNames: ["value"]
        },
        numberChildren: {
          dependencyType: "child",
          childGroups: ["numbers"],
          variableNames: ["value"]
        }
      }),
      definition: function({ dependencyValues }) {
        let markers = [];
        let componentType = dependencyValues.markerType;
        if (dependencyValues.markerType === "text") {
          markers = dependencyValues.textChildren.map(
            (x2) => x2.stateValues.value
          );
        } else if (dependencyValues.markerType === "number") {
          markers = dependencyValues.numberChildren.map(
            (x2) => x2.stateValues.value
          );
          markers.sort((a2, b2) => {
            return a2 - b2;
          });
        } else {
          componentType = "text";
        }
        return {
          setValue: { markers },
          setCreateComponentOfType: { markers: componentType }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Pegboard extends GraphicalComponent {
  static componentType = "pegboard";
  static componentDocs = {
    summary: "Renders a grid of pegs on a graph"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.dx = {
      createComponentOfType: "number",
      createStateVariable: "dx",
      defaultValue: 1,
      public: true,
      forRenderer: true,
      description: "Horizontal spacing between pegs."
    };
    attributes.dy = {
      createComponentOfType: "number",
      createStateVariable: "dy",
      defaultValue: 1,
      public: true,
      forRenderer: true,
      description: "Vertical spacing between pegs."
    };
    attributes.xoffset = {
      createComponentOfType: "number",
      createStateVariable: "xoffset",
      defaultValue: 0,
      public: true,
      forRenderer: true,
      description: "Horizontal offset of the peg grid origin."
    };
    attributes.yoffset = {
      createComponentOfType: "number",
      createStateVariable: "yoffset",
      defaultValue: 0,
      public: true,
      forRenderer: true,
      description: "Vertical offset of the peg grid origin."
    };
    return attributes;
  }
}
class Constraints extends BaseComponent {
  static componentType = "constraints";
  static componentDocs = {
    summary: "A container of constraints applied to a graphical component."
  };
  static rendererType = void 0;
  static excludeFromSchema = true;
  static returnChildGroups() {
    return [
      {
        group: "constraints",
        componentTypes: ["_constraint"]
      }
    ];
  }
}
class ConstraintComponent extends BaseComponent {
  static componentType = "_constraint";
  static rendererType = void 0;
  static inSchemaOnlyInheritAs = ["_constraint"];
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.independentComponentConstraints = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { independentComponentConstraints: false }
      })
    };
    return stateVariableDefinitions;
  }
}
class ConstrainToGrid extends ConstraintComponent {
  static componentType = "constrainToGrid";
  static componentDocs = {
    summary: "Constrains a graphical component's position to lie on a grid"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.dx = {
      description: "Horizontal grid spacing.",
      createComponentOfType: "number",
      createStateVariable: "dx",
      defaultValue: 1,
      public: true
    };
    attributes.dy = {
      description: "Vertical grid spacing.",
      createComponentOfType: "number",
      createStateVariable: "dy",
      defaultValue: 1,
      public: true
    };
    attributes.dz = {
      description: "Depth-wise grid spacing.",
      createComponentOfType: "number",
      createStateVariable: "dz",
      defaultValue: 1,
      public: true
    };
    attributes.xoffset = {
      description: "Horizontal offset of the grid origin.",
      createComponentOfType: "number",
      createStateVariable: "xoffset",
      defaultValue: 0,
      public: true
    };
    attributes.yoffset = {
      description: "Vertical offset of the grid origin.",
      createComponentOfType: "number",
      createStateVariable: "yoffset",
      defaultValue: 0,
      public: true
    };
    attributes.zoffset = {
      description: "Depth-wise offset of the grid origin.",
      createComponentOfType: "number",
      createStateVariable: "zoffset",
      defaultValue: 0,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.independentComponentConstraints = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { independentComponentConstraints: true }
      })
    };
    stateVariableDefinitions.applyComponentConstraint = {
      returnDependencies: () => ({
        dx: {
          dependencyType: "stateVariable",
          variableName: "dx"
        },
        dy: {
          dependencyType: "stateVariable",
          variableName: "dy"
        },
        dz: {
          dependencyType: "stateVariable",
          variableName: "dz"
        },
        xoffset: {
          dependencyType: "stateVariable",
          variableName: "xoffset"
        },
        yoffset: {
          dependencyType: "stateVariable",
          variableName: "yoffset"
        },
        zoffset: {
          dependencyType: "stateVariable",
          variableName: "zoffset"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          applyComponentConstraint: function(variables) {
            if ("x1" in variables) {
              let x1 = findFiniteNumericalValue(variables.x1);
              if (!Number.isFinite(x1)) {
                return {};
              }
              let dx = dependencyValues.dx;
              let xoffset = dependencyValues.xoffset;
              let x1constrained = Math.round((x1 - xoffset) / dx) * dx + xoffset;
              if (Number.isFinite(x1constrained)) {
                return {
                  constrained: true,
                  variables: { x1: x1constrained }
                };
              } else {
                return {};
              }
            }
            if ("x2" in variables) {
              let x2 = findFiniteNumericalValue(variables.x2);
              if (!Number.isFinite(x2)) {
                return {};
              }
              let dy = dependencyValues.dy;
              let yoffset = dependencyValues.yoffset;
              let x2constrained = Math.round((x2 - yoffset) / dy) * dy + yoffset;
              if (Number.isFinite(x2constrained)) {
                return {
                  constrained: true,
                  variables: { x2: x2constrained }
                };
              } else {
                return {};
              }
            }
            if ("x3" in variables) {
              let x3 = findFiniteNumericalValue(variables.x3);
              if (!Number.isFinite(x3)) {
                return {};
              }
              let dz = dependencyValues.dz;
              let zoffset = dependencyValues.zoffset;
              let x3constrained = Math.round((x3 - zoffset) / dz) * dz + zoffset;
              if (Number.isFinite(x3constrained)) {
                return {
                  constrained: true,
                  variables: { x3: x3constrained }
                };
              } else {
                return {};
              }
            }
            return {};
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class ConstrainToGraph extends ConstraintComponent {
  static componentType = "constrainToGraph";
  static componentDocs = {
    summary: "Constrains a graphical component's position to lie within a graph's bounds"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.buffer = {
      description: "Distance from the graph edge inside which the position is still considered constrained.",
      createComponentOfType: "number",
      createStateVariable: "buffer",
      defaultValue: 0.01,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.independentComponentConstraints = {
      returnDependencies: () => ({}),
      definition: () => ({
        setValue: { independentComponentConstraints: true }
      })
    };
    stateVariableDefinitions.applyComponentConstraint = {
      returnDependencies: () => ({
        constraintAncestor: {
          dependencyType: "ancestor",
          componentType: "_graphical",
          variableNames: [
            "graphXmin",
            "graphXmax",
            "graphYmin",
            "graphYmax"
          ],
          variablesOptional: true
        },
        buffer: {
          dependencyType: "stateVariable",
          variableName: "buffer"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          applyComponentConstraint: function(variables) {
            if (dependencyValues.constraintAncestor === null || dependencyValues.constraintAncestor.stateValues.graphXmin === null) {
              return {};
            }
            if ("x1" in variables) {
              let x1 = findFiniteNumericalValue(variables.x1);
              if (!Number.isFinite(x1)) {
                return {};
              }
              let xMin = dependencyValues.constraintAncestor.stateValues.graphXmin;
              let xMax = dependencyValues.constraintAncestor.stateValues.graphXmax;
              if (!(Number.isFinite(xMin) && Number.isFinite(xMax))) {
                return {};
              }
              let lowerBound2 = xMin;
              let upperBound = xMax;
              let buffer = dependencyValues.buffer;
              if (buffer > 0) {
                let bufferAdjust = buffer * (xMax - xMin);
                lowerBound2 += bufferAdjust;
                upperBound -= bufferAdjust;
              }
              let x1constrained = Math.max(
                lowerBound2,
                Math.min(upperBound, x1)
              );
              return {
                constrained: true,
                variables: { x1: x1constrained }
              };
            }
            if ("x2" in variables) {
              let x2 = findFiniteNumericalValue(variables.x2);
              if (!Number.isFinite(x2)) {
                return {};
              }
              let yMin = dependencyValues.constraintAncestor.stateValues.graphYmin;
              let yMax = dependencyValues.constraintAncestor.stateValues.graphYmax;
              if (!(Number.isFinite(yMin) && Number.isFinite(yMax))) {
                return {};
              }
              let lowerBound2 = yMin;
              let upperBound = yMax;
              let buffer = dependencyValues.buffer;
              if (buffer > 0) {
                let bufferAdjust = buffer * (yMax - yMin);
                lowerBound2 += bufferAdjust;
                upperBound -= bufferAdjust;
              }
              let x2constrained = Math.max(
                lowerBound2,
                Math.min(upperBound, x2)
              );
              return {
                constrained: true,
                variables: { x2: x2constrained }
              };
            }
            return {};
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class AttractToGrid extends ConstraintComponent {
  static componentType = "attractToGrid";
  static componentDocs = {
    summary: "Attracts a graphical component's position to grid lines when nearby"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.dx = {
      createComponentOfType: "number",
      createStateVariable: "dx",
      defaultValue: 1,
      public: true,
      description: "Horizontal grid spacing."
    };
    attributes.dy = {
      createComponentOfType: "number",
      createStateVariable: "dy",
      defaultValue: 1,
      public: true,
      description: "Vertical grid spacing."
    };
    attributes.dz = {
      createComponentOfType: "number",
      createStateVariable: "dz",
      defaultValue: 1,
      public: true,
      description: "Depth-wise grid spacing."
    };
    attributes.xoffset = {
      createComponentOfType: "number",
      createStateVariable: "xoffset",
      defaultValue: 0,
      public: true,
      description: "Horizontal offset of the grid origin."
    };
    attributes.yoffset = {
      createComponentOfType: "number",
      createStateVariable: "yoffset",
      defaultValue: 0,
      public: true,
      description: "Vertical offset of the grid origin."
    };
    attributes.zoffset = {
      createComponentOfType: "number",
      createStateVariable: "zoffset",
      defaultValue: 0,
      public: true,
      description: "Depth-wise offset of the grid origin."
    };
    attributes.xthreshold = {
      createComponentOfType: "number",
      createStateVariable: "xthreshold",
      defaultValue: 0.2,
      public: true,
      description: "Horizontal distance within which the position attracts to a grid line."
    };
    attributes.ythreshold = {
      createComponentOfType: "number",
      createStateVariable: "ythreshold",
      defaultValue: 0.2,
      public: true,
      description: "Vertical distance within which the position attracts to a grid line."
    };
    attributes.zthreshold = {
      createComponentOfType: "number",
      createStateVariable: "zthreshold",
      defaultValue: 0.2,
      public: true,
      description: "Depth-wise distance within which the position attracts to a grid line."
    };
    attributes.includeGridlines = {
      createComponentOfType: "boolean",
      createStateVariable: "includeGridlines",
      defaultValue: false,
      public: true,
      description: "Whether to attract to entire grid lines (in addition to grid intersection points)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.independentComponentConstraints = {
      returnDependencies: () => ({
        includeGridlines: {
          dependencyType: "stateVariable",
          variableName: "includeGridlines"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          independentComponentConstraints: dependencyValues.includeGridlines
        }
      })
    };
    stateVariableDefinitions.applyComponentConstraint = {
      returnDependencies: () => ({
        dx: {
          dependencyType: "stateVariable",
          variableName: "dx"
        },
        dy: {
          dependencyType: "stateVariable",
          variableName: "dy"
        },
        dz: {
          dependencyType: "stateVariable",
          variableName: "dz"
        },
        xoffset: {
          dependencyType: "stateVariable",
          variableName: "xoffset"
        },
        yoffset: {
          dependencyType: "stateVariable",
          variableName: "yoffset"
        },
        zoffset: {
          dependencyType: "stateVariable",
          variableName: "zoffset"
        },
        xthreshold: {
          dependencyType: "stateVariable",
          variableName: "xthreshold"
        },
        ythreshold: {
          dependencyType: "stateVariable",
          variableName: "ythreshold"
        },
        zthreshold: {
          dependencyType: "stateVariable",
          variableName: "zthreshold"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          applyComponentConstraint: function(variables) {
            if ("x1" in variables) {
              let x1 = findFiniteNumericalValue(variables.x1);
              if (!Number.isFinite(x1)) {
                return {};
              }
              let dx = dependencyValues.dx;
              let xoffset = dependencyValues.xoffset;
              let x1grid = Math.round((x1 - xoffset) / dx) * dx + xoffset;
              if (!Number.isFinite(x1grid)) {
                return {};
              }
              if (Math.abs(x1 - x1grid) < dependencyValues.xthreshold) {
                return {
                  constrained: true,
                  variables: { x1: x1grid }
                };
              } else {
                return {};
              }
            }
            if ("x2" in variables) {
              let x2 = findFiniteNumericalValue(variables.x2);
              if (!Number.isFinite(x2)) {
                return {};
              }
              let dy = dependencyValues.dy;
              let yoffset = dependencyValues.yoffset;
              let x2grid = Math.round((x2 - yoffset) / dy) * dy + yoffset;
              if (!Number.isFinite(x2grid)) {
                return {};
              }
              if (Math.abs(x2 - x2grid) < dependencyValues.ythreshold) {
                return {
                  constrained: true,
                  variables: { x2: x2grid }
                };
              } else {
                return {};
              }
            }
            if ("x3" in variables) {
              let x3 = findFiniteNumericalValue(variables.x3);
              if (!Number.isFinite(x3)) {
                return {};
              }
              let dz = dependencyValues.dz;
              let zoffset = dependencyValues.zoffset;
              let x3grid = Math.round((x3 - zoffset) / dz) * dz + zoffset;
              if (Number.isFinite(x3grid) && Math.abs(x3 - x3grid) < dependencyValues.zthreshold) {
                return {
                  constrained: true,
                  variables: { x3: x3grid }
                };
              } else {
                return {};
              }
            }
            return {};
          }
        }
      })
    };
    stateVariableDefinitions.applyConstraint = {
      returnDependencies: () => ({
        dx: {
          dependencyType: "stateVariable",
          variableName: "dx"
        },
        dy: {
          dependencyType: "stateVariable",
          variableName: "dy"
        },
        dz: {
          dependencyType: "stateVariable",
          variableName: "dz"
        },
        xoffset: {
          dependencyType: "stateVariable",
          variableName: "xoffset"
        },
        yoffset: {
          dependencyType: "stateVariable",
          variableName: "yoffset"
        },
        zoffset: {
          dependencyType: "stateVariable",
          variableName: "zoffset"
        },
        xthreshold: {
          dependencyType: "stateVariable",
          variableName: "xthreshold"
        },
        ythreshold: {
          dependencyType: "stateVariable",
          variableName: "ythreshold"
        },
        zthreshold: {
          dependencyType: "stateVariable",
          variableName: "zthreshold"
        },
        includeGridlines: {
          dependencyType: "stateVariable",
          variableName: "includeGridlines"
        },
        applyComponentConstraint: {
          dependencyType: "stateVariable",
          variableName: "applyComponentConstraint"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          applyConstraint: function(variables) {
            let newVariables = {};
            let constrained = false;
            for (let varName in variables) {
              let result = dependencyValues.applyComponentConstraint({
                [varName]: variables[varName]
              });
              if (result.constrained) {
                constrained = true;
                newVariables[varName] = result.variables[varName];
              }
            }
            if (!constrained) {
              return {};
            }
            if (!dependencyValues.includeGridlines) {
              if (variables.x1 !== void 0 && newVariables.x1 === void 0) {
                return {};
              }
              if (variables.x2 !== void 0 && newVariables.x2 === void 0) {
                return {};
              }
              if (variables.x3 !== void 0 && newVariables.x3 === void 0) {
                return {};
              }
            }
            return {
              constrained,
              variables: newVariables
            };
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class ConstrainTo extends ConstraintComponent {
  static componentType = "constrainTo";
  static componentDocs = {
    summary: "Constrains a graphical component's position to be within another component or components"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.relativeToGraphScales = {
      description: "Whether the constraint is interpreted relative to the enclosing graph's scales.",
      createComponentOfType: "boolean",
      createStateVariable: "relativeToGraphScales",
      defaultValue: false,
      public: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "graphical",
        componentTypes: ["_graphical"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.nearestPointFunctions = {
      returnDependencies: () => ({
        graphicalChildren: {
          dependencyType: "child",
          childGroups: ["graphical"],
          variableNames: ["nearestPoint"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let nearestPointFunctions = [];
        let diagnostics = [];
        for (let child of dependencyValues.graphicalChildren) {
          if (!child.stateValues.nearestPoint) {
            const warning = {
              type: "warning",
              message: `Cannot constrain to a \`<${child.componentType}>\` as it doesn't have a nearestPoint state variable.`
            };
            if (child.position) {
              warning.position = child.position;
            }
            diagnostics.push(warning);
            continue;
          }
          nearestPointFunctions.push(child.stateValues.nearestPoint);
        }
        return {
          setValue: { nearestPointFunctions },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.applyConstraint = {
      returnDependencies: () => ({
        nearestPointFunctions: {
          dependencyType: "stateVariable",
          variableName: "nearestPointFunctions"
        },
        relativeToGraphScales: {
          dependencyType: "stateVariable",
          variableName: "relativeToGraphScales"
        },
        constraintsAncestor: {
          dependencyType: "ancestor",
          componentType: "_graphical",
          variableNames: ["scales"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let scales;
        if (dependencyValues.relativeToGraphScales) {
          scales = dependencyValues.constraintsAncestor?.stateValues.scales || [1, 1, 1];
        } else {
          scales = [1, 1, 1];
        }
        return {
          setValue: {
            applyConstraint: function(variables) {
              let closestDistance2 = Infinity;
              let closestPoint = {};
              let constrained = false;
              let numericalVariables = {};
              for (let varName in variables) {
                numericalVariables[varName] = findFiniteNumericalValue(
                  variables[varName]
                );
              }
              for (let nearestPointFunction of dependencyValues.nearestPointFunctions) {
                let nearestPoint = nearestPointFunction({
                  variables,
                  scales
                });
                if (nearestPoint === void 0) {
                  continue;
                }
                let constrainedVariables = {};
                let distance2 = 0;
                if (numericalVariables.x1 !== void 0) {
                  if (nearestPoint.x1 === void 0) {
                    continue;
                  }
                  constrainedVariables.x1 = nearestPoint.x1;
                  distance2 += Math.pow(
                    numericalVariables.x1 - nearestPoint.x1,
                    2
                  );
                }
                if (numericalVariables.x2 !== void 0) {
                  if (nearestPoint.x2 === void 0) {
                    continue;
                  }
                  constrainedVariables.x2 = nearestPoint.x2;
                  distance2 += Math.pow(
                    numericalVariables.x2 - nearestPoint.x2,
                    2
                  );
                }
                if (numericalVariables.x3 !== void 0) {
                  if (nearestPoint.x3 === void 0) {
                    continue;
                  }
                  constrainedVariables.x3 = nearestPoint.x3;
                  distance2 += Math.pow(
                    numericalVariables.x3 - nearestPoint.x3,
                    2
                  );
                }
                if (distance2 < closestDistance2) {
                  closestPoint = constrainedVariables;
                  closestDistance2 = distance2;
                  constrained = true;
                }
              }
              if (!constrained) {
                return {};
              }
              return { constrained, variables: closestPoint };
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class AttractTo extends ConstraintComponent {
  static componentType = "attractTo";
  static componentDocs = {
    summary: "Attracts a graphical component's position to another component when nearby"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.relativeToGraphScales = {
      createComponentOfType: "boolean",
      createStateVariable: "relativeToGraphScales",
      defaultValue: false,
      public: true,
      description: "Whether the attraction threshold is interpreted relative to the enclosing graph's scales rather than absolute coordinates."
    };
    attributes.threshold = {
      createComponentOfType: "number",
      description: "Distance within which the position attracts to a target."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "graphical",
        componentTypes: ["_graphical"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.threshold = {
      description: "Distance within which the position attracts to a target.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      hasEssential: true,
      returnDependencies: () => ({
        thresholdAttr: {
          dependencyType: "attributeComponent",
          attributeName: "threshold",
          variableNames: ["value"]
        },
        constraintsAncestor: {
          dependencyType: "ancestor",
          componentType: "_graphical",
          variableNames: ["graphXmin"],
          variablesOptional: true
        },
        relativeToGraphScales: {
          dependencyType: "stateVariable",
          variableName: "relativeToGraphScales"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.thresholdAttr) {
          return {
            setValue: {
              threshold: dependencyValues.thresholdAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              threshold: {
                get defaultValue() {
                  let useRelative = dependencyValues.relativeToGraphScales && dependencyValues.constraintsAncestor !== null && dependencyValues.constraintsAncestor.stateValues.graphXmin !== null;
                  return useRelative ? 0.02 : 0.5;
                }
              }
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.thresholdAttr) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "thresholdAttr",
                desiredValue: desiredStateVariableValues.threshold
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "threshold",
                value: desiredStateVariableValues.threshold
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.nearestPointFunctions = {
      returnDependencies: () => ({
        graphicalChildren: {
          dependencyType: "child",
          childGroups: ["graphical"],
          variableNames: ["nearestPoint"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let nearestPointFunctions = [];
        let diagnostics = [];
        for (let child of dependencyValues.graphicalChildren) {
          if (!child.stateValues.nearestPoint) {
            const warning = {
              type: "warning",
              message: `Cannot attract to a \`<${child.componentType}>\` as it doesn't have a nearestPoint state variable.`
            };
            if (child.position) {
              warning.position = child.position;
            }
            diagnostics.push(warning);
            continue;
          }
          nearestPointFunctions.push(child.stateValues.nearestPoint);
        }
        return {
          setValue: { nearestPointFunctions },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.applyConstraint = {
      returnDependencies() {
        let dependencies = {
          nearestPointFunctions: {
            dependencyType: "stateVariable",
            variableName: "nearestPointFunctions"
          },
          threshold: {
            dependencyType: "stateVariable",
            variableName: "threshold"
          },
          relativeToGraphScales: {
            dependencyType: "stateVariable",
            variableName: "relativeToGraphScales"
          },
          constraintsAncestor: {
            dependencyType: "ancestor",
            componentType: "_graphical",
            variableNames: ["scales"],
            variablesOptional: true
          }
        };
        return dependencies;
      },
      definition({ dependencyValues }) {
        let scales;
        if (dependencyValues.relativeToGraphScales) {
          scales = dependencyValues.constraintsAncestor?.stateValues.scales || [1, 1, 1];
        } else {
          scales = [1, 1, 1];
        }
        return {
          setValue: {
            applyConstraint: function(variables) {
              let closestDistance2 = Infinity;
              let closestPoint = {};
              let numericalVariables = {};
              for (let varName in variables) {
                numericalVariables[varName] = findFiniteNumericalValue(
                  variables[varName]
                );
              }
              for (let nearestPointFunction of dependencyValues.nearestPointFunctions) {
                let nearestPoint = nearestPointFunction({
                  variables,
                  scales
                });
                if (nearestPoint === void 0) {
                  continue;
                }
                let constrainedVariables = {};
                let distance2 = 0;
                if (numericalVariables.x1 !== void 0) {
                  if (nearestPoint.x1 === void 0) {
                    continue;
                  }
                  constrainedVariables.x1 = nearestPoint.x1;
                  distance2 += Math.pow(
                    (numericalVariables.x1 - nearestPoint.x1) / scales[0],
                    2
                  );
                }
                if (numericalVariables.x2 !== void 0) {
                  if (nearestPoint.x2 === void 0) {
                    continue;
                  }
                  constrainedVariables.x2 = nearestPoint.x2;
                  distance2 += Math.pow(
                    (numericalVariables.x2 - nearestPoint.x2) / scales[1],
                    2
                  );
                }
                if (numericalVariables.x3 !== void 0) {
                  if (nearestPoint.x3 === void 0) {
                    continue;
                  }
                  constrainedVariables.x3 = nearestPoint.x3;
                  distance2 += Math.pow(
                    numericalVariables.x3 - nearestPoint.x3,
                    2
                  );
                }
                if (distance2 < closestDistance2) {
                  closestPoint = constrainedVariables;
                  closestDistance2 = distance2;
                }
              }
              if (closestDistance2 > dependencyValues.threshold * dependencyValues.threshold) {
                return {};
              }
              return {
                constrained: true,
                variables: closestPoint
              };
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class ConstraintUnion extends ConstraintComponent {
  static componentType = "constraintUnion";
  static componentDocs = {
    summary: "A constraint that is satisfied if any of its child constraints are satisfied"
  };
  static returnChildGroups() {
    return [
      {
        group: "constraints",
        componentTypes: ["_constraint"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.applyConstraint = {
      returnDependencies: () => ({
        constraintChildren: {
          dependencyType: "child",
          childGroups: ["constraints"],
          variableNames: [
            "applyConstraint",
            "applyComponentConstraint"
          ],
          variablesOptional: true
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          applyConstraint: function(variables) {
            let constraintResult;
            if (dependencyValues.constraintChildren.length === 1) {
              let constraintChild = dependencyValues.constraintChildren[0];
              if (constraintChild.stateValues.applyConstraint) {
                constraintResult = constraintChild.stateValues.applyConstraint(
                  variables
                );
              } else {
                constraintResult = applyConstraintFromComponentConstraints(
                  variables,
                  constraintChild.stateValues.applyComponentConstraint
                );
              }
              return constraintResult;
            }
            let closestDistance2 = Infinity;
            let closestResult = {};
            let closestInd;
            for (let [
              ind,
              constraintChild
            ] of dependencyValues.constraintChildren.entries()) {
              if (constraintChild.stateValues.applyConstraint) {
                constraintResult = constraintChild.stateValues.applyConstraint(
                  variables
                );
              } else {
                constraintResult = applyConstraintFromComponentConstraints(
                  variables,
                  constraintChild.stateValues.applyComponentConstraint
                );
              }
              if (!constraintResult.constrained) {
                continue;
              }
              let distance2 = 0;
              for (let varname in constraintResult.variables) {
                let originalVar = findFiniteNumericalValue(
                  variables[varname]
                );
                let constrainedVar = findFiniteNumericalValue(
                  constraintResult.variables[varname]
                );
                if (!Number.isFinite(originalVar) || !Number.isFinite(constrainedVar)) {
                  distance2 = Infinity;
                  break;
                }
                distance2 += Math.pow(
                  originalVar - constrainedVar,
                  2
                );
              }
              if (distance2 < closestDistance2) {
                closestResult = constraintResult;
                closestInd = ind + 1;
                closestDistance2 = distance2;
              }
            }
            if (closestInd === void 0) {
              return {};
            }
            if (closestResult.constraintIndices === void 0) {
              closestResult.constraintIndices = [closestInd];
            } else {
              closestResult.constraintIndices = [
                closestInd,
                ...closestResult.constraintIndices
              ];
            }
            return closestResult;
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class AttractToConstraint extends ConstraintComponent {
  static componentType = "attractToConstraint";
  static componentDocs = {
    summary: "Wraps another constraint so it acts as a soft attraction within a threshold"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.threshold = {
      createComponentOfType: "number",
      createStateVariable: "threshold",
      defaultValue: 0.5,
      public: true,
      description: "Distance within which the position attracts to the constraint location."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "constraints",
        componentTypes: ["_constraint"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.applyConstraint = {
      returnDependencies: () => ({
        constraintChild: {
          dependencyType: "child",
          childGroups: ["constraints"],
          variableNames: [
            "applyConstraint",
            "applyComponentConstraint"
          ],
          variablesOptional: true
        },
        independentComponentConstraints: {
          dependencyType: "stateVariable",
          variableName: "independentComponentConstraints"
        },
        threshold: {
          dependencyType: "stateVariable",
          variableName: "threshold"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          applyConstraint: function(variables) {
            let constraintResult;
            let constraintChild = dependencyValues.constraintChild[0];
            if (!constraintChild) {
              return {};
            }
            if (constraintChild.stateValues.applyConstraint) {
              constraintResult = constraintChild.stateValues.applyConstraint(
                variables
              );
            } else {
              constraintResult = applyConstraintFromComponentConstraints(
                variables,
                constraintChild.stateValues.applyComponentConstraint
              );
            }
            let distance2 = 0;
            for (let varname in constraintResult.variables) {
              let originalVar = findFiniteNumericalValue(
                variables[varname]
              );
              let constrainedVar = findFiniteNumericalValue(
                constraintResult.variables[varname]
              );
              if (!Number.isFinite(originalVar) || !Number.isFinite(constrainedVar)) {
                return {};
              }
              distance2 += Math.pow(
                originalVar - constrainedVar,
                2
              );
            }
            if (distance2 > dependencyValues.threshold * dependencyValues.threshold) {
              return {};
            }
            return constraintResult;
          }
        }
      })
    };
    return stateVariableDefinitions;
  }
}
class ConstrainToInterior extends ConstraintComponent {
  static componentType = "constrainToInterior";
  static componentDocs = {
    summary: "Constrains a graphical component's position to lie inside a region"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.relativeToGraphScales = {
      description: "Whether the constraint is interpreted relative to the enclosing graph's scales.",
      createComponentOfType: "boolean",
      createStateVariable: "relativeToGraphScales",
      defaultValue: false,
      public: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "graphical",
        componentTypes: ["_graphical"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.constraintFunctions = {
      returnDependencies: () => ({
        graphicalChildren: {
          dependencyType: "child",
          childGroups: ["graphical"],
          variableNames: ["nearestPoint", "containsPoint"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let constraintFunctions = [];
        let diagnostics = [];
        for (let child of dependencyValues.graphicalChildren) {
          if (!child.stateValues.nearestPoint) {
            const warning = {
              type: "warning",
              message: `Cannot constrain to interior of a \`<${child.componentType}>\` as it doesn't have a nearestPoint state variable.`
            };
            if (child.position) {
              warning.position = child.position;
            }
            diagnostics.push(warning);
            continue;
          }
          let fs = { nearestPoint: child.stateValues.nearestPoint };
          if (child.stateValues.containsPoint) {
            fs.containsPoint = child.stateValues.containsPoint;
          }
          constraintFunctions.push(fs);
        }
        return {
          setValue: { constraintFunctions },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.applyConstraint = {
      returnDependencies: () => ({
        constraintFunctions: {
          dependencyType: "stateVariable",
          variableName: "constraintFunctions"
        },
        relativeToGraphScales: {
          dependencyType: "stateVariable",
          variableName: "relativeToGraphScales"
        },
        constraintsAncestor: {
          dependencyType: "ancestor",
          componentType: "_graphical",
          variableNames: ["scales"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let scales;
        if (dependencyValues.relativeToGraphScales) {
          scales = dependencyValues.constraintsAncestor?.stateValues.scales || [1, 1, 1];
        } else {
          scales = [1, 1, 1];
        }
        return {
          setValue: {
            applyConstraint: function(variables) {
              let closestDistance2 = Infinity;
              let closestPoint = {};
              let constrained = false;
              let numericalVariables = {};
              for (let varName in variables) {
                numericalVariables[varName] = findFiniteNumericalValue(
                  variables[varName]
                );
              }
              for (let constraintFunctionsForObject of dependencyValues.constraintFunctions) {
                if (constraintFunctionsForObject.containsPoint) {
                  if (numericalVariables.x1 !== void 0 && numericalVariables.x2 !== void 0) {
                    let pointInInterior = constraintFunctionsForObject.containsPoint(
                      [
                        numericalVariables.x1,
                        numericalVariables.x2
                      ]
                    );
                    if (pointInInterior) {
                      return {
                        constrained: true,
                        variables
                      };
                    }
                  }
                }
                let nearestPoint = constraintFunctionsForObject.nearestPoint({
                  variables,
                  scales
                });
                if (nearestPoint === void 0) {
                  continue;
                }
                let constrainedVariables = {};
                let distance2 = 0;
                if (numericalVariables.x1 !== void 0) {
                  if (nearestPoint.x1 === void 0) {
                    continue;
                  }
                  constrainedVariables.x1 = nearestPoint.x1;
                  distance2 += Math.pow(
                    numericalVariables.x1 - nearestPoint.x1,
                    2
                  );
                }
                if (numericalVariables.x2 !== void 0) {
                  if (nearestPoint.x2 === void 0) {
                    continue;
                  }
                  constrainedVariables.x2 = nearestPoint.x2;
                  distance2 += Math.pow(
                    numericalVariables.x2 - nearestPoint.x2,
                    2
                  );
                }
                if (numericalVariables.x3 !== void 0) {
                  if (nearestPoint.x3 === void 0) {
                    continue;
                  }
                  constrainedVariables.x3 = nearestPoint.x3;
                  distance2 += Math.pow(
                    numericalVariables.x3 - nearestPoint.x3,
                    2
                  );
                }
                if (distance2 < closestDistance2) {
                  closestPoint = constrainedVariables;
                  closestDistance2 = distance2;
                  constrained = true;
                }
              }
              if (!constrained) {
                return {};
              }
              return { constrained, variables: closestPoint };
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Intersection extends CompositeComponent {
  static componentType = "intersection";
  static componentDocs = {
    summary: "Renders intersections between graphical objects"
  };
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.styleNumber = {
      leaveRaw: true,
      description: "Style number applied to the resulting intersection components."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "lines",
        componentTypes: ["line", "lineSegment"]
      },
      {
        group: "circles",
        componentTypes: ["circle"]
      },
      {
        group: "polygonsPolylines",
        componentTypes: ["polygon", "polyline"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.lineChildren = {
      returnDependencies: () => ({
        lineChildren: {
          dependencyType: "child",
          childGroups: ["lines"],
          variableNames: [
            "numDimensions",
            "numericalPoints",
            "numericalEndpoints"
          ],
          variablesOptional: true
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          lineChildren: dependencyValues.lineChildren
        }
      })
    };
    stateVariableDefinitions.circleChildren = {
      returnDependencies: () => ({
        circleChildren: {
          dependencyType: "child",
          childGroups: ["circles"],
          variableNames: ["numericalCenter", "numericalRadius"]
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          circleChildren: dependencyValues.circleChildren
        }
      })
    };
    stateVariableDefinitions.polyChildren = {
      returnDependencies: () => ({
        polyChildren: {
          dependencyType: "child",
          childGroups: ["polygonsPolylines"],
          variableNames: ["numDimensions", "numericalVertices"]
        }
      }),
      definition: ({ dependencyValues, componentInfoObjects }) => ({
        setValue: {
          polyChildren: dependencyValues.polyChildren.map((child) => {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "polygon"
            })) {
              child = { ...child };
              child.stateValues = { ...child.stateValues };
              child.stateValues.isPolygon = true;
            }
            return child;
          })
        }
      })
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        lineChildren: {
          dependencyType: "stateVariable",
          variableName: "lineChildren"
        },
        circleChildren: {
          dependencyType: "stateVariable",
          variableName: "circleChildren"
        },
        polyChildren: {
          dependencyType: "stateVariable",
          variableName: "polyChildren"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    let diagnostics = [];
    let lines = (await component.stateValues.lineChildren).map(
      (x2) => x2.stateValues
    );
    let numLines = lines.length;
    let circles = (await component.stateValues.circleChildren).map(
      (x2) => x2.stateValues
    );
    let numCircles = circles.length;
    let polys = (await component.stateValues.polyChildren).map(
      (x2) => x2.stateValues
    );
    let numPolys = polys.length;
    let totNums = numLines + numCircles + numPolys;
    if (totNums < 2) {
      return { replacements: [], diagnostics, nComponents };
    } else if (totNums > 2) {
      diagnostics.push({
        message: "Haven't implemented intersection for more than two items",
        type: "warning"
      });
      return { replacements: [], diagnostics, nComponents };
    }
    let points = [];
    if (numCircles === 2) {
      points = intersectTwoCircles(circles);
    } else if (numLines === 2) {
      points = intersectTwoLines(lines);
    } else if (numPolys === 2) {
      points = intersectTwoPolys(polys);
    } else {
      if (numCircles === 1) {
        if (numLines === 1) {
          points = intersectLineAndCircle(lines[0], circles[0]);
        } else {
          points = intersectPolyAndCircle(polys[0], circles[0]);
        }
      } else {
        points = intersectLineAndPoly(lines[0], polys[0]);
      }
    }
    if (points.length === 0) {
      return { replacements: [], diagnostics, nComponents };
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let serializedReplacements = points.map((pt2) => ({
      type: "serialized",
      componentType: "point",
      componentIdx: nComponents++,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      state: {
        coords: me$3.fromAst(["vector", ...pt2]),
        draggable: false,
        fixed: true
      },
      attributes: {},
      doenetAttributes: {},
      children: []
    }));
    let attributesToConvert = {};
    if (component.attributes.styleNumber) {
      attributesToConvert.styleNumber = component.attributes.styleNumber;
    }
    if (Object.keys(attributesToConvert).length > 0) {
      for (let repl of serializedReplacements) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: repl.componentType,
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        const attributesFromComposite = res.attributes;
        nComponents = res.nComponents;
        if (!repl.attributes) {
          repl.attributes = {};
        }
        Object.assign(repl.attributes, attributesFromComposite);
      }
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements: serializedReplacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    let replacementChanges = [];
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      nComponents,
      workspace
    });
    let serializedIntersections = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    const newNComponents = replacementResults.nComponents;
    let nNewIntersections = serializedIntersections.length;
    let recreateReplacements = true;
    if (nNewIntersections === component.replacements.length) {
      recreateReplacements = false;
      for (let ind = 0; ind < nNewIntersections; ind++) {
        if (serializedIntersections[ind].componentType !== component.replacements[ind].componentType) {
          recreateReplacements = true;
          break;
        }
        if (serializedIntersections[ind].state === void 0) {
          break;
        }
        if (serializedIntersections[ind].componentType !== "point") {
          recreateReplacements = true;
          break;
        }
        let replacementInstruction2 = {
          changeType: "updateStateVariables",
          component: component.replacements[ind],
          stateChanges: {
            coords: serializedIntersections[ind].state.coords
          }
        };
        replacementChanges.push(replacementInstruction2);
      }
    }
    if (recreateReplacements === false) {
      workspace.replacementsCreated = stateIdInfo.num;
      return { replacementChanges, diagnostics, nComponents };
    }
    let replacementInstruction = {
      changeType: "add",
      changeTopLevelReplacements: true,
      firstReplacementInd: 0,
      numberReplacementsToReplace: component.replacements.length,
      serializedReplacements: serializedIntersections
    };
    nComponents = newNComponents;
    return {
      replacementChanges: [replacementInstruction],
      diagnostics,
      nComponents
    };
  }
}
function intersectTwoLines(lines) {
  let line1 = lines[0];
  let line2 = lines[1];
  if (line1.numDimensions !== 2 || line2.numDimensions !== 2) {
    console.log("Intersection of lines implemented only in 2D");
    return [];
  }
  let [[x1, y1], [x2, y2]] = line1.numericalPoints || line1.numericalEndpoints;
  let [[x3, y3], [x4, y4]] = line2.numericalPoints || line2.numericalEndpoints;
  if (!(Number.isFinite(x1) && Number.isFinite(y1) && Number.isFinite(x2) && Number.isFinite(y2) && Number.isFinite(x3) && Number.isFinite(y3) && Number.isFinite(x4) && Number.isFinite(y4))) {
    console.log(
      "Intersection of lines implemented only for numerical values"
    );
    return [];
  }
  let dx12 = x1 - x2;
  let dx34 = x3 - x4;
  let dy12 = y1 - y2;
  let dy34 = y3 - y4;
  let D2 = dx12 * dy34 - dx34 * dy12;
  if (Math.abs(D2) < 1e-14) {
    return [];
  }
  let line1isSegment = Boolean(line1.numericalEndpoints);
  let line2isSegment = Boolean(line2.numericalEndpoints);
  if (line1isSegment || line2isSegment) {
    let dx13 = x1 - x3;
    let dy13 = y1 - y3;
    if (line1isSegment) {
      let num = (dx13 * dy34 - dy13 * dx34) * Math.sign(D2);
      if (num < 0 || num > Math.abs(D2)) {
        return [];
      }
    }
    if (line2isSegment) {
      let num = (dx13 * dy12 - dy13 * dx12) * Math.sign(D2);
      if (num < 0 || num > Math.abs(D2)) {
        return [];
      }
    }
  }
  let det12 = x1 * y2 - x2 * y1;
  let det34 = x3 * y4 - x4 * y3;
  let x5 = (det12 * dx34 - det34 * dx12) / D2;
  let y5 = (det12 * dy34 - det34 * dy12) / D2;
  return [[x5, y5]];
}
function intersectTwoCircles(circleChildren) {
  let circ1 = circleChildren[0];
  let r1 = circ1.numericalRadius;
  let [x1, y1] = circ1.numericalCenter;
  let circ2 = circleChildren[1];
  let r2 = circ2.numericalRadius;
  let [x2, y2] = circ2.numericalCenter;
  if (!(Number.isFinite(x1) && Number.isFinite(y1) && Number.isFinite(r1) && Number.isFinite(x2) && Number.isFinite(y2) && Number.isFinite(r2))) {
    console.log(
      "Intersection of circles implemented only for numerical values"
    );
    return [];
  }
  let dx = x2 - x1;
  let dy = y2 - y1;
  let dr2 = r1 ** 2 - r2 ** 2;
  let sr2 = r1 ** 2 + r2 ** 2;
  let cDist2 = dx ** 2 + dy ** 2;
  if (cDist2 === 0) {
    return [];
  }
  let D2 = 2 * sr2 / cDist2 - dr2 ** 2 / cDist2 ** 2 - 1;
  if (D2 < 0) {
    return [];
  }
  let c2 = dr2 / (2 * cDist2);
  let x3 = (x1 + x2) / 2 + c2 * dx;
  let y3 = (y1 + y2) / 2 + c2 * dy;
  let intersectionPoints = [];
  if (D2 === 0) {
    intersectionPoints.push([x3, y3]);
  } else {
    let D22 = Math.sqrt(D2) / 2;
    intersectionPoints.push([x3 + D22 * dy, y3 - D22 * dx]);
    intersectionPoints.push([x3 - D22 * dy, y3 + D22 * dx]);
  }
  return intersectionPoints;
}
function intersectLineAndCircle(line, circle) {
  if (line.numDimensions !== 2) {
    console.log("Intersection involving lines implemented only in 2D");
    return [];
  }
  let [[x1, y1], [x2, y2]] = line.numericalPoints || line.numericalEndpoints;
  let r2 = circle.numericalRadius;
  let [cx, cy] = circle.numericalCenter;
  if (!(Number.isFinite(x1) && Number.isFinite(y1) && Number.isFinite(x2) && Number.isFinite(y2) && Number.isFinite(r2) && Number.isFinite(cx) && Number.isFinite(cy))) {
    console.log(
      "Intersection of line and circle implemented only for numerical values"
    );
    return [];
  }
  x1 -= cx;
  x2 -= cx;
  y1 -= cy;
  y2 -= cy;
  let dx = x2 - x1;
  let dy = y2 - y1;
  let dr2 = dx ** 2 + dy ** 2;
  if (dr2 === 0) {
    return [];
  }
  let det = x1 * y2 - x2 * y1;
  let det2 = det ** 2;
  let D2 = r2 ** 2 * dr2 - det2;
  if (D2 < 0) {
    return [];
  }
  let x3 = cx + det * dy / dr2;
  let y3 = cy - det * dx / dr2;
  let intersectionPoints = [];
  if (D2 === 0) {
    intersectionPoints.push([x3, y3]);
  } else {
    let sqrtD = Math.sqrt(D2);
    let signDy = dy < 0 ? -1 : 1;
    let xshift = signDy * dx * sqrtD / dr2;
    let yshift = Math.abs(dy) * sqrtD / dr2;
    intersectionPoints.push([x3 + xshift, y3 + yshift]);
    intersectionPoints.push([x3 - xshift, y3 - yshift]);
  }
  if (line.numericalEndpoints) {
    intersectionPoints = intersectionPoints.filter((pt2) => {
      let [x4, y4] = pt2;
      x4 -= cx;
      y4 -= cy;
      if (x2 > x1) {
        if (x4 < x1 || x4 > x2) {
          return false;
        }
      } else if (x2 < x1) {
        if (x4 < x2 || x4 > x1) {
          return false;
        }
      } else if (y2 > y1) {
        if (y4 < y1 || y4 > y2) {
          return false;
        }
      } else if (y4 < y2 || y4 > y1) {
        return false;
      }
      return true;
    });
  }
  return intersectionPoints;
}
function ptEqual(pt1, pt2) {
  return pt1[0] === pt2[0] && pt1[1] == pt2[1];
}
function dedupPts(points) {
  return points.filter(
    (pt2, ind, arr) => !arr.slice(0, ind).some((pt22) => ptEqual(pt2, pt22))
  );
}
function intersectTwoPolys(polys) {
  let poly1 = polys[0];
  let poly2 = polys[1];
  if (poly1.numDimensions !== 2 || poly2.numDimensions !== 2) {
    console.log(
      "Intersection involving polygons/polylines implemented only in 2D"
    );
    return [];
  }
  let intersectionPts = [];
  let numVertices1 = poly1.numericalVertices.length;
  let numEdges1 = numVertices1;
  if (!poly1.isPolygon) {
    numEdges1--;
  }
  let numVertices2 = poly2.numericalVertices.length;
  let numEdges2 = numVertices2;
  if (!poly2.isPolygon) {
    numEdges2--;
  }
  for (let ind1 = 0; ind1 < numEdges1; ind1++) {
    let ind1a = (ind1 + 1) % numVertices1;
    let segmentFromPoly1 = {
      numericalEndpoints: [
        poly1.numericalVertices[ind1],
        poly1.numericalVertices[ind1a]
      ],
      numDimensions: 2
    };
    for (let ind2 = 0; ind2 < numEdges2; ind2++) {
      let ind2a = (ind2 + 1) % numVertices2;
      let segmentFromPoly2 = {
        numericalEndpoints: [
          poly2.numericalVertices[ind2],
          poly2.numericalVertices[ind2a]
        ],
        numDimensions: 2
      };
      intersectionPts.push(
        ...intersectTwoLines([segmentFromPoly1, segmentFromPoly2])
      );
    }
  }
  return dedupPts(intersectionPts);
}
function intersectLineAndPoly(line, poly) {
  if (line.numDimensions !== 2 || poly.numDimensions !== 2) {
    console.log(
      "Intersection involving lines and polygons/polylines implemented only in 2D"
    );
    return [];
  }
  let points = [];
  let numVertices = poly.numericalVertices.length;
  let numEdges = numVertices;
  if (!poly.isPolygon) {
    numEdges--;
  }
  for (let ind = 0; ind < numEdges; ind++) {
    let ind2 = (ind + 1) % numVertices;
    let segmentFromPoly = {
      numericalEndpoints: [
        poly.numericalVertices[ind],
        poly.numericalVertices[ind2]
      ],
      numDimensions: 2
    };
    points.push(...intersectTwoLines([line, segmentFromPoly]));
  }
  return dedupPts(points);
}
function intersectPolyAndCircle(poly, circle) {
  if (poly.numDimensions !== 2) {
    console.log(
      "Intersection involving polygons/polylines implemented only in 2D"
    );
    return [];
  }
  let points = [];
  let numVertices = poly.numericalVertices.length;
  let numEdges = numVertices;
  if (!poly.isPolygon) {
    numEdges--;
  }
  for (let ind = 0; ind < numEdges; ind++) {
    let ind2 = (ind + 1) % numVertices;
    let segmentFromPoly = {
      numericalEndpoints: [
        poly.numericalVertices[ind],
        poly.numericalVertices[ind2]
      ],
      numDimensions: 2
    };
    points.push(...intersectLineAndCircle(segmentFromPoly, circle));
  }
  return dedupPts(points);
}
class Panel extends InlineComponent {
  static componentType = "panel";
  static componentDocs = {
    summary: "A bordered panel containing arbitrary content."
  };
  static rendererType = "containerInline";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.columns = {
      description: "Number of columns to lay out children in.",
      createComponentOfType: "text",
      createStateVariable: "columns",
      defaultValue: null,
      public: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.breakpoints = {
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["width"]
        },
        columns: {
          dependencyType: "stateVariable",
          variableName: "columns"
        }
      }),
      definition: function({ dependencyValues }) {
        let breakpoints = [];
        let itemWidths = dependencyValues.children.map(
          (x2) => x2.stateValues.width
        );
        if (dependencyValues.columns !== null) {
          let possibleColumnNumbers;
          if (dependencyValues.columns.indexOf(",") !== -1) {
            possibleColumnNumbers = dependencyValues.columns.split(",").map(Number);
          } else if (dependencyValues.columns.indexOf("-") !== -1) {
            let [start, end] = dependencyValues.columns.split("-");
            possibleColumnNumbers = [];
            for (let ind = Number(start); ind <= end; ind++) {
              possibleColumnNumbers.push(ind);
            }
          } else {
            possibleColumnNumbers = [
              Number(dependencyValues.columns)
            ];
          }
          let maxWidths = {};
          let totalOfMaxWidths = {};
          for (let numberOfColumns of possibleColumnNumbers) {
            let maxWidthsForThisColumn = [];
            let totalWidthsForThisCoumn = 0;
            for (let columnNum = 0; columnNum < numberOfColumns; columnNum++) {
              let maxWidth2 = -1;
              for (let ind = columnNum; ind < itemWidths.length; ind = ind + numberOfColumns) {
                if (itemWidths[ind] > maxWidth2) {
                  maxWidth2 = itemWidths[ind];
                }
              }
              maxWidthsForThisColumn.push(maxWidth2);
              totalWidthsForThisCoumn = totalWidthsForThisCoumn + Number(maxWidth2);
            }
            maxWidths[numberOfColumns] = maxWidthsForThisColumn;
            totalOfMaxWidths[numberOfColumns] = totalWidthsForThisCoumn;
          }
          let lastColumnNumber = -1;
          for (let number of possibleColumnNumbers) {
            let minBreakPoint = Number.POSITIVE_INFINITY;
            let minColumnNumber = -1;
            for (let columnNumber of possibleColumnNumbers) {
              if (totalOfMaxWidths[columnNumber] <= minBreakPoint && columnNumber > lastColumnNumber) {
                minBreakPoint = totalOfMaxWidths[columnNumber];
                minColumnNumber = columnNumber;
              }
            }
            if (minBreakPoint < Number.POSITIVE_INFINITY) {
              breakpoints.push({
                breakpoint: minBreakPoint,
                possibleColumnNumbers: minColumnNumber,
                arrayOfWidths: maxWidths[minColumnNumber]
              });
              lastColumnNumber = minColumnNumber;
            }
          }
        }
        return { setValue: { breakpoints } };
      }
    };
    return stateVariableDefinitions;
  }
}
class ConstrainToAngles extends ConstraintComponent {
  static componentType = "constrainToAngles";
  static componentDocs = {
    summary: "Constrains a graphical component's position to specified angles."
  };
  static excludeFromSchema = true;
  static returnChildLogic(args) {
    let childLogic = super.returnChildLogic(args);
    let addAngles = function({ activeChildrenMatched }) {
      let anglesChildren = [];
      for (let child of activeChildrenMatched) {
        anglesChildren.push({
          createdComponent: true,
          componentIdx: child.componentIdx
        });
      }
      return {
        success: true,
        newChildren: [
          { componentType: "angles", children: anglesChildren }
        ]
      };
    };
    let AtLeastOneAngle = childLogic.newLeaf({
      name: "AtLeastOneAngle",
      componentType: "angle",
      comparison: "atLeast",
      number: 1,
      isSugar: true,
      replacementFunction: addAngles
    });
    let AtLeastOneString = childLogic.newLeaf({
      name: "AtLeastOneString",
      componentType: "string",
      comparison: "atLeast",
      number: 1
    });
    let AtLeastOneMath = childLogic.newLeaf({
      name: "AtLeastOneMath",
      componentType: "math",
      comparison: "atLeast",
      number: 1
    });
    let StringsAndMaths = childLogic.newOperator({
      name: "StringsAndMaths",
      operator: "or",
      propositions: [AtLeastOneString, AtLeastOneMath],
      requireConsecutive: true,
      isSugar: true,
      replacementFunction: addAngles
    });
    let AtMostOneAngles = childLogic.newLeaf({
      name: "AtMostOneAngles",
      componentType: "angles",
      comparison: "atMost",
      number: 1
    });
    let Angles = childLogic.newOperator({
      name: "Angles",
      operator: "xor",
      propositions: [AtLeastOneAngle, StringsAndMaths, AtMostOneAngles],
      setAsBase: true
    });
    let AtMostOnePoint = childLogic.newLeaf({
      name: "AtMostOnePoint",
      componentType: "point",
      comparison: "atMost",
      number: 1
    });
    childLogic.newOperator({
      name: "AnglesAndPoint",
      operator: "and",
      propositions: [Angles, AtMostOnePoint],
      setAsBase: true
    });
    return childLogic;
  }
  updateState(args = {}) {
    super.updateState(args);
    if (!this.childLogicSatisfied) {
      this.unresolvedState.angles = true;
      this.unresolvedState.offset = true;
      return;
    }
    let trackChanges = this.currentTracker.trackChanges;
    let childrenChanged = trackChanges.childrenChanged(this.componentIdx);
    if (childrenChanged) {
      let anglesInd = this.childLogic.returnMatches("AtMostOneAngles");
      if (anglesInd.length === 1) {
        this.state.anglesChild = this.activeChildren[anglesInd[0]];
      } else {
        delete this.state.anglesChild;
        this.state.angles = [];
        delete this.unresolvedState.angles;
      }
      let pointInd = this.childLogic.returnMatches("AtMostOnePoint");
      if (pointInd.length === 1) {
        this.state.pointChild = this.activeChildren[pointInd[0]];
      } else {
        delete this.state.pointChild;
        this.state.offset = [0, 0, 0];
        delete this.unresolvedState.offset;
      }
    }
    if (this.state.anglesChild) {
      if (this.state.anglesChild.unresolvedState.angles) {
        this.unresolvedState.angles = true;
        return;
      }
      if (childrenChanged || trackChanges.getVariableChanges({
        component: this.state.anglesChild,
        variable: "angles"
      })) {
        delete this.unresolvedState.angles;
        this.state.angles = this.state.anglesChild.state.angles;
      }
    }
    if (this.state.pointChild) {
      if (this.state.pointChild.unresolvedState.xs) {
        this.unresolvedState.offset = true;
        return;
      }
      if (childrenChanged || trackChanges.getVariableChanges({
        component: this.state.pointChild,
        variable: "xs"
      })) {
        delete this.unresolvedState.offset;
        this.state.offset = [];
        for (let i2 = 0; i2 < this.state.pointChild.state.ndimensions; i2++) {
          let temp = this.state.pointChild.state.xs[i2].evaluate_to_constant();
          if (!Number.isFinite(temp)) {
            this.state.offset = NaN;
            break;
          }
          this.state.offset.push(temp);
        }
      }
    }
  }
  applyTheConstraint({ x1, x2, x3, offset }) {
    if (this.unresolvedState.angles || this.unresolvedState.offset) {
      return {};
    }
    if (x1 === void 0 || x2 === void 0 || x3 !== void 0) {
      return {};
    }
    x1 = this.findFiniteNumericalValue(x1);
    x2 = this.findFiniteNumericalValue(x2);
    if (!Number.isFinite(x1) || !Number.isFinite(x2)) {
      return {};
    }
    if (offset === void 0) {
      offset = this.state.offset;
    }
    if (Number.isNaN(offset)) {
      return {};
    }
    let dx1 = x1 - offset[0];
    let dx2 = x2 - offset[1];
    let foundAngle = Math.atan2(dx2, dx1);
    let closestDistance = Infinity;
    let closestAngle;
    let closestInd;
    for (let [ind, angleComp] of this.state.angles.entries()) {
      let angle = angleComp.state.angle.evaluate_to_constant();
      if (!Number.isFinite(angle)) {
        continue;
      }
      let distanceToAngle = Math.abs(
        (angle - foundAngle + Math.PI) % (2 * Math.PI) - Math.PI
      );
      if (distanceToAngle < closestDistance) {
        closestAngle = angle;
        closestInd = ind;
        closestDistance = distanceToAngle;
      }
    }
    if (closestAngle === void 0) {
      return {};
    }
    let mag = Math.sqrt(dx1 * dx1 + dx2 * dx2);
    let newX1 = offset[0] + mag * Math.cos(closestAngle);
    let newX2 = offset[1] + mag * Math.sin(closestAngle);
    let result = {
      variables: {
        x1: newX1,
        x2: newX2
      },
      constraintIndices: [closestInd + 1],
      constrained: true
    };
    return result;
  }
}
class AttractToAngles extends ConstraintComponent {
  static componentType = "attractToAngles";
  static componentDocs = {
    summary: "Attracts a graphical component's position to specified angles when nearby."
  };
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.threshold = {
      createComponentOfType: "number",
      createStateVariable: "threshold",
      defaultValue: 0.3,
      public: true,
      description: "Angular distance (radians) within which the angle attracts to a target."
    };
    return attributes;
  }
  static returnChildLogic(args) {
    let childLogic = super.returnChildLogic(args);
    let addAngles = function({ activeChildrenMatched }) {
      let anglesChildren = [];
      for (let child of activeChildrenMatched) {
        anglesChildren.push({
          createdComponent: true,
          componentIdx: child.componentIdx
        });
      }
      return {
        success: true,
        newChildren: [
          { componentType: "angles", children: anglesChildren }
        ]
      };
    };
    let AtLeastOneAngle = childLogic.newLeaf({
      name: "AtLeastOneAngle",
      componentType: "angle",
      comparison: "atLeast",
      number: 1,
      isSugar: true,
      replacementFunction: addAngles
    });
    let AtLeastOneString = childLogic.newLeaf({
      name: "AtLeastOneString",
      componentType: "string",
      comparison: "atLeast",
      number: 1
    });
    let AtLeastOneMath = childLogic.newLeaf({
      name: "AtLeastOneMath",
      componentType: "math",
      comparison: "atLeast",
      number: 1
    });
    let StringsAndMaths = childLogic.newOperator({
      name: "StringsAndMaths",
      operator: "or",
      propositions: [AtLeastOneString, AtLeastOneMath],
      requireConsecutive: true,
      isSugar: true,
      replacementFunction: addAngles
    });
    let AtMostOneAngles = childLogic.newLeaf({
      name: "AtMostOneAngles",
      componentType: "angles",
      comparison: "atMost",
      number: 1
    });
    let Angles = childLogic.newOperator({
      name: "Angles",
      operator: "xor",
      propositions: [AtLeastOneAngle, StringsAndMaths, AtMostOneAngles],
      setAsBase: true
    });
    let AtMostOnePoint = childLogic.newLeaf({
      name: "AtMostOnePoint",
      componentType: "point",
      comparison: "atMost",
      number: 1
    });
    childLogic.newOperator({
      name: "AnglesAndPoint",
      operator: "and",
      propositions: [Angles, AtMostOnePoint],
      setAsBase: true
    });
    return childLogic;
  }
  updateState(args = {}) {
    super.updateState(args);
    if (!this.childLogicSatisfied) {
      this.unresolvedState.angles = true;
      this.unresolvedState.offset = true;
      return;
    }
    let trackChanges = this.currentTracker.trackChanges;
    let childrenChanged = trackChanges.childrenChanged(this.componentIdx);
    if (childrenChanged) {
      let anglesInd = this.childLogic.returnMatches("AtMostOneAngles");
      if (anglesInd.length === 1) {
        this.state.anglesChild = this.activeChildren[anglesInd[0]];
      } else {
        delete this.state.anglesChild;
        this.state.angles = [];
        delete this.unresolvedState.angles;
      }
      let pointInd = this.childLogic.returnMatches("AtMostOnePoint");
      if (pointInd.length === 1) {
        this.state.pointChild = this.activeChildren[pointInd[0]];
      } else {
        delete this.state.pointChild;
        this.state.offset = [0, 0, 0];
        delete this.unresolvedState.offset;
      }
    }
    if (this.state.anglesChild) {
      if (this.state.anglesChild.unresolvedState.angles) {
        this.unresolvedState.angles = true;
        return;
      }
      if (childrenChanged || trackChanges.getVariableChanges({
        component: this.state.anglesChild,
        variable: "angles"
      })) {
        delete this.unresolvedState.angles;
        this.state.angles = this.state.anglesChild.state.angles;
      }
    }
    if (this.state.pointChild) {
      if (this.state.pointChild.unresolvedState.xs) {
        this.unresolvedState.offset = true;
        return;
      }
      if (childrenChanged || trackChanges.getVariableChanges({
        component: this.state.pointChild,
        variable: "xs"
      })) {
        delete this.unresolvedState.offset;
        this.state.offset = [];
        for (let i2 = 0; i2 < this.state.pointChild.state.ndimensions; i2++) {
          let temp = this.state.pointChild.state.xs[i2].evaluate_to_constant();
          if (!Number.isFinite(temp)) {
            this.state.offset = NaN;
            break;
          }
          this.state.offset.push(temp);
        }
      }
    }
  }
  applyTheConstraint({ x1, x2, x3, offset }) {
    if (this.unresolvedState.angles || this.unresolvedState.offset) {
      return {};
    }
    if (x1 === void 0 || x2 === void 0 || x3 !== void 0) {
      return {};
    }
    x1 = this.findFiniteNumericalValue(x1);
    x2 = this.findFiniteNumericalValue(x2);
    if (!Number.isFinite(x1) || !Number.isFinite(x2)) {
      return {};
    }
    if (offset === void 0) {
      offset = this.state.offset;
    }
    if (Number.isNaN(offset)) {
      return {};
    }
    let dx1 = x1 - offset[0];
    let dx2 = x2 - offset[1];
    let foundAngle = Math.atan2(dx2, dx1);
    let closestDistance = Infinity;
    let closestAngle;
    let closestInd;
    for (let [ind, angleComp] of this.state.angles.entries()) {
      let angle = angleComp.state.angle.evaluate_to_constant();
      if (!Number.isFinite(angle)) {
        continue;
      }
      let distanceToAngle = Math.abs(
        (angle - foundAngle + Math.PI) % (2 * Math.PI) - Math.PI
      );
      if (distanceToAngle < closestDistance) {
        closestAngle = angle;
        closestInd = ind;
        closestDistance = distanceToAngle;
      }
    }
    if (closestAngle === void 0) {
      return {};
    }
    let mag = Math.sqrt(dx1 * dx1 + dx2 * dx2);
    let newX1 = offset[0] + mag * Math.cos(closestAngle);
    let newX2 = offset[1] + mag * Math.sin(closestAngle);
    let distanceAsPoints = Math.sqrt(
      Math.pow(newX1 - x1, 2) + Math.pow(newX2 - x2, 2)
    );
    if (distanceAsPoints >= this.state.threshold) {
      return {};
    }
    let result = {
      variables: {
        x1: newX1,
        x2: newX2
      },
      constraintIndices: [closestInd + 1],
      constrained: true
    };
    return result;
  }
}
class ConditionalContent extends CompositeComponent {
  static componentType = "conditionalContent";
  static componentDocs = {
    summary: "Renders content conditionally based on its `<case>` or `<else>` children"
  };
  static allowInSchemaAnywhere = true;
  static createsVariants = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.condition = {
      createComponentOfType: "boolean",
      description: "Convenience for a single-case form: the boolean condition for which the children are rendered."
    };
    return attributes;
  }
  // conditionalContent can be authored with any children.
  // Case children will be sugared in so they are the only child group.
  // We need to allow for any children in the schema.
  static additionalSchemaChildren = ["_base", "else"];
  static returnChildGroups() {
    return [
      {
        group: "cases",
        componentTypes: ["case"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.warnIfCondition = {
      returnDependencies: () => ({
        conditionAttribute: {
          dependencyType: "attributeComponent",
          attributeName: "condition"
        }
      }),
      definition({ dependencyValues }) {
        const diagnostics = [];
        if (dependencyValues.conditionAttribute) {
          const warning = {
            type: "warning",
            message: "Attribute `condition` is ignored on a `<conditionalContent>` component with case or else children."
          };
          if (dependencyValues.conditionAttribute.position) {
            warning.position = dependencyValues.conditionAttribute.position;
          }
          diagnostics.push(warning);
        }
        return {
          sendDiagnostics: diagnostics,
          setValue: { warnIfCondition: null }
        };
      }
    };
    stateVariableDefinitions.numCases = {
      additionalStateVariablesDefined: ["caseChildren"],
      returnDependencies: () => ({
        caseChildren: {
          dependencyType: "child",
          childGroups: ["cases"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            caseChildren: dependencyValues.caseChildren,
            numCases: dependencyValues.caseChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.selectedIndex = {
      returnDependencies: () => ({
        caseChildren: {
          dependencyType: "child",
          childGroups: ["cases"],
          variableNames: ["conditionSatisfied"]
        }
      }),
      definition({ dependencyValues }) {
        let selectedIndex = null;
        for (let [
          ind,
          child
        ] of dependencyValues.caseChildren.entries()) {
          if (child.stateValues.conditionSatisfied) {
            selectedIndex = ind;
            break;
          }
        }
        return {
          setValue: {
            selectedIndex
          }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        selectedIndex: {
          dependencyType: "stateVariable",
          variableName: "selectedIndex"
        },
        warnIfCondition: {
          dependencyType: "stateVariable",
          variableName: "warnIfCondition"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition() {
        return {
          setValue: { readyToExpandWhenResolved: true }
        };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    workspace,
    nComponents
  }) {
    let replacementResults = await this.getReplacements(
      component,
      components
    );
    workspace.previousSelectedIndex = await component.stateValues.selectedIndex;
    return { ...replacementResults, nComponents };
  }
  static async getReplacements(component, components) {
    let diagnostics = [];
    let replacements = [];
    let caseChildren = await component.stateValues.caseChildren;
    let selectedIndex = await component.stateValues.selectedIndex;
    if (selectedIndex !== null) {
      const selectedChildComponentIdx = caseChildren[selectedIndex].componentIdx;
      let serializedChild = deepClone(
        (await components[selectedChildComponentIdx].state.serializedChildren.value)[0]
      );
      serializedChild.doenetAttributes = Object.assign(
        {},
        components[selectedChildComponentIdx].doenetAttributes
      );
      serializedChild.variants = {
        desiredVariant: {
          seed: component.sharedParameters.variantSeed + "|" + selectedIndex.toString()
        }
      };
      replacements.push(serializedChild);
    }
    return {
      replacements,
      diagnostics
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    workspace,
    nComponents
  }) {
    const diagnostics = [];
    const selectedIndex = await component.stateValues.selectedIndex;
    if (workspace.previousSelectedIndex === selectedIndex) {
      return { replacementChanges: [], diagnostics, nComponents };
    }
    const numCases = await component.stateValues.numCases;
    if (numCases === 1) {
      if (selectedIndex === null) {
        const replacementInstruction2 = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: component.replacements.length
        };
        workspace.previousSelectedIndex = selectedIndex;
        return {
          replacementChanges: [replacementInstruction2],
          diagnostics,
          nComponents
        };
      } else {
        const caseChildren = await component.stateValues.caseChildren;
        const selectedChildComponentIdx = caseChildren[selectedIndex].componentIdx;
        const numSerializedGrandchildren = (await components[selectedChildComponentIdx].stateValues.serializedChildren).length;
        if (numSerializedGrandchildren === component.replacements.length) {
          const replacementInstruction2 = {
            changeType: "changeReplacementsToWithhold",
            replacementsToWithhold: 0
          };
          workspace.previousSelectedIndex = selectedIndex;
          return {
            replacementChanges: [replacementInstruction2],
            diagnostics,
            nComponents
          };
        }
      }
    }
    const replacementChanges = [];
    const replacementResults = await this.getReplacements(
      component,
      components
    );
    diagnostics.push(...replacementResults.diagnostics);
    const replacementInstruction = {
      changeType: "add",
      changeTopLevelReplacements: true,
      firstReplacementInd: 0,
      numberReplacementsToReplace: component.replacements.length,
      serializedReplacements: replacementResults.replacements,
      replacementsToWithhold: 0
    };
    replacementChanges.push(replacementInstruction);
    workspace.previousSelectedIndex = selectedIndex;
    return { replacementChanges, diagnostics, nComponents };
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents,
      useSubpartVariantRng: true
    });
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    if (this.serializedChildren) {
      let additionalRendererTypes = this.potentialRendererTypesFromSerializedComponents(
        this.serializedChildren
      );
      for (let rendererType of additionalRendererTypes) {
        if (!allPotentialRendererTypes.includes(rendererType)) {
          allPotentialRendererTypes.push(rendererType);
        }
      }
    }
    return allPotentialRendererTypes;
  }
}
class AsList extends InlineComponent {
  static componentType = "asList";
  static componentDocs = {
    summary: "Renders its children as a comma-separated list"
  };
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static returnChildGroups() {
    return [
      {
        group: "inline",
        componentTypes: ["_inline"]
      },
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.text = {
      description: "The children rendered as a comma-separated text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inline"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let textpieces = dependencyValues.inlineChildren.map(textFromComponent);
        let text = textpieces.filter((x2) => x2).join(", ");
        return { setValue: { text } };
      }
    };
    stateVariableDefinitions.latex = {
      description: "The children rendered as a comma-separated LaTeX string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "latex"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inline"],
          variableNames: ["text", "latex"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let latexpieces = [];
        for (let child of dependencyValues.inlineChildren) {
          if (typeof child !== "object") {
            latexpieces.push(child.toString().trim());
          } else if (typeof child.stateValues.latex === "string") {
            latexpieces.push(child.stateValues.latex.trim());
          } else if (typeof child.stateValues.text === "string") {
            latexpieces.push(child.stateValues.text.trim());
          } else {
            latexpieces.push("");
          }
        }
        let latex = latexpieces.join(", ");
        return { setValue: { latex } };
      }
    };
    return stateVariableDefinitions;
  }
}
function normalizeIndex(numberOrLetter) {
  if (numberOrLetter === null) {
    return void 0;
  }
  if (numberOrLetter === "") {
    return void 0;
  }
  if (Number.isFinite(Number(numberOrLetter))) {
    return Number(numberOrLetter) - 1;
  }
  if (typeof numberOrLetter !== "string") {
    return void 0;
  }
  return lettersToNumber(numberOrLetter) - 1;
}
var ReferenceType;
(function(ReferenceType2) {
  ReferenceType2["RELATIVE"] = "RELATIVE";
  ReferenceType2["ABSOLUTE"] = "ABSOLUTE";
})(ReferenceType || (ReferenceType = {}));
class ColumnAddress {
  constructor(type, col, sheet) {
    this.type = type;
    this.col = col;
    this.sheet = sheet;
  }
  static absolute(column, sheet) {
    return new ColumnAddress(ReferenceType.ABSOLUTE, column, sheet);
  }
  static relative(column, sheet) {
    return new ColumnAddress(ReferenceType.RELATIVE, column, sheet);
  }
  static compareByAbsoluteAddress(baseAddress) {
    return (colA, colB) => colA.toSimpleColumnAddress(baseAddress).col - colB.toSimpleColumnAddress(baseAddress).col;
  }
  isColumnAbsolute() {
    return this.type === ReferenceType.ABSOLUTE;
  }
  isColumnRelative() {
    return this.type === ReferenceType.RELATIVE;
  }
  isAbsolute() {
    return this.type === ReferenceType.ABSOLUTE && this.sheet !== void 0;
  }
  moved(toSheet, toRight, _toBottom) {
    const newSheet = this.sheet === void 0 ? void 0 : toSheet;
    return new ColumnAddress(this.type, this.col + toRight, newSheet);
  }
  shiftedByColumns(numberOfColumns) {
    return new ColumnAddress(this.type, this.col + numberOfColumns, this.sheet);
  }
  toSimpleColumnAddress(baseAddress) {
    const sheet = absoluteSheetReference(this, baseAddress);
    let column = this.col;
    if (this.isColumnRelative()) {
      column = baseAddress.col + this.col;
    }
    return simpleColumnAddress(sheet, column);
  }
  shiftRelativeDimensions(toRight, _toBottom) {
    const col = this.isColumnRelative() ? this.col + toRight : this.col;
    return new ColumnAddress(this.type, col, this.sheet);
  }
  shiftAbsoluteDimensions(toRight, _toBottom) {
    const col = this.isColumnAbsolute() ? this.col + toRight : this.col;
    return new ColumnAddress(this.type, col, this.sheet);
  }
  withSheet(sheet) {
    return new ColumnAddress(this.type, this.col, sheet);
  }
  isInvalid(baseAddress) {
    return this.toSimpleColumnAddress(baseAddress).col < 0;
  }
  hash(withSheet) {
    const sheetPart = withSheet && this.sheet !== void 0 ? `#${this.sheet}` : "";
    switch (this.type) {
      case ReferenceType.RELATIVE: {
        return `${sheetPart}#COLR${this.col}`;
      }
      case ReferenceType.ABSOLUTE: {
        return `${sheetPart}#COLA${this.col}`;
      }
    }
  }
  unparse(baseAddress) {
    const simpleAddress = this.toSimpleColumnAddress(baseAddress);
    if (invalidSimpleColumnAddress(simpleAddress)) {
      return void 0;
    }
    const column = columnIndexToLabel(simpleAddress.col);
    const dollar = this.type === ReferenceType.ABSOLUTE ? "$" : "";
    return `${dollar}${column}`;
  }
  exceedsSheetSizeLimits(maxColumns) {
    return this.col >= maxColumns;
  }
}
class RowAddress {
  constructor(type, row, sheet) {
    this.type = type;
    this.row = row;
    this.sheet = sheet;
  }
  static absolute(row, sheet) {
    return new RowAddress(ReferenceType.ABSOLUTE, row, sheet);
  }
  static relative(row, sheet) {
    return new RowAddress(ReferenceType.RELATIVE, row, sheet);
  }
  static compareByAbsoluteAddress(baseAddress) {
    return (rowA, rowB) => rowA.toSimpleRowAddress(baseAddress).row - rowB.toSimpleRowAddress(baseAddress).row;
  }
  isRowAbsolute() {
    return this.type === ReferenceType.ABSOLUTE;
  }
  isRowRelative() {
    return this.type === ReferenceType.RELATIVE;
  }
  isAbsolute() {
    return this.type === ReferenceType.ABSOLUTE && this.sheet !== void 0;
  }
  moved(toSheet, toRight, toBottom) {
    const newSheet = this.sheet === void 0 ? void 0 : toSheet;
    return new RowAddress(this.type, this.row + toBottom, newSheet);
  }
  shiftedByRows(numberOfColumns) {
    return new RowAddress(this.type, this.row + numberOfColumns, this.sheet);
  }
  toSimpleRowAddress(baseAddress) {
    const sheet = absoluteSheetReference(this, baseAddress);
    let row = this.row;
    if (this.isRowRelative()) {
      row = baseAddress.row + this.row;
    }
    return simpleRowAddress(sheet, row);
  }
  shiftRelativeDimensions(toRight, toBottom) {
    const row = this.isRowRelative() ? this.row + toBottom : this.row;
    return new RowAddress(this.type, row, this.sheet);
  }
  shiftAbsoluteDimensions(toRight, toBottom) {
    const row = this.isRowAbsolute() ? this.row + toBottom : this.row;
    return new RowAddress(this.type, row, this.sheet);
  }
  withSheet(sheet) {
    return new RowAddress(this.type, this.row, sheet);
  }
  isInvalid(baseAddress) {
    return this.toSimpleRowAddress(baseAddress).row < 0;
  }
  hash(withSheet) {
    const sheetPart = withSheet && this.sheet !== void 0 ? `#${this.sheet}` : "";
    switch (this.type) {
      case ReferenceType.RELATIVE: {
        return `${sheetPart}#ROWR${this.row}`;
      }
      case ReferenceType.ABSOLUTE: {
        return `${sheetPart}#ROWA${this.row}`;
      }
    }
  }
  unparse(baseAddress) {
    const simpleAddress = this.toSimpleRowAddress(baseAddress);
    if (invalidSimpleRowAddress(simpleAddress)) {
      return void 0;
    }
    const dollar = this.type === ReferenceType.ABSOLUTE ? "$" : "";
    return `${dollar}${simpleAddress.row + 1}`;
  }
  exceedsSheetSizeLimits(maxRows) {
    return this.row >= maxRows;
  }
}
var CellReferenceType;
(function(CellReferenceType2) {
  CellReferenceType2["CELL_REFERENCE_RELATIVE"] = "CELL_REFERENCE";
  CellReferenceType2["CELL_REFERENCE_ABSOLUTE"] = "CELL_REFERENCE_ABSOLUTE";
  CellReferenceType2["CELL_REFERENCE_ABSOLUTE_COL"] = "CELL_REFERENCE_ABSOLUTE_COL";
  CellReferenceType2["CELL_REFERENCE_ABSOLUTE_ROW"] = "CELL_REFERENCE_ABSOLUTE_ROW";
})(CellReferenceType || (CellReferenceType = {}));
class CellAddress {
  constructor(col, row, type, sheet) {
    this.col = col;
    this.row = row;
    this.type = type;
    this.sheet = sheet;
  }
  static fromColAndRow(col, row, sheet) {
    const factoryMethod = col.isColumnAbsolute() && row.isRowAbsolute() ? CellAddress.absolute.bind(this) : col.isColumnAbsolute() ? CellAddress.absoluteCol.bind(this) : row.isRowAbsolute() ? CellAddress.absoluteRow.bind(this) : CellAddress.relative.bind(this);
    return factoryMethod(col.col, row.row, sheet);
  }
  static relative(col, row, sheet) {
    return new CellAddress(col, row, CellReferenceType.CELL_REFERENCE_RELATIVE, sheet);
  }
  static absolute(col, row, sheet) {
    return new CellAddress(col, row, CellReferenceType.CELL_REFERENCE_ABSOLUTE, sheet);
  }
  static absoluteCol(col, row, sheet) {
    return new CellAddress(col, row, CellReferenceType.CELL_REFERENCE_ABSOLUTE_COL, sheet);
  }
  static absoluteRow(col, row, sheet) {
    return new CellAddress(col, row, CellReferenceType.CELL_REFERENCE_ABSOLUTE_ROW, sheet);
  }
  /**
   * Converts R0C0 representation of cell address to simple object representation.
   *
   * @param baseAddress - base address for R0C0 shifts
   */
  toSimpleCellAddress(baseAddress) {
    const sheet = absoluteSheetReference(this, baseAddress);
    if (this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE) {
      return simpleCellAddress(sheet, this.col, this.row);
    } else if (this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_ROW) {
      return simpleCellAddress(sheet, baseAddress.col + this.col, this.row);
    } else if (this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_COL) {
      return simpleCellAddress(sheet, this.col, baseAddress.row + this.row);
    } else {
      return simpleCellAddress(sheet, baseAddress.col + this.col, baseAddress.row + this.row);
    }
  }
  toColumnAddress() {
    const refType = this.isColumnRelative() ? ReferenceType.RELATIVE : ReferenceType.ABSOLUTE;
    return new ColumnAddress(refType, this.col, this.sheet);
  }
  toRowAddress() {
    const refType = this.isRowRelative() ? ReferenceType.RELATIVE : ReferenceType.ABSOLUTE;
    return new RowAddress(refType, this.row, this.sheet);
  }
  toSimpleColumnAddress(baseAddress) {
    const sheet = absoluteSheetReference(this, baseAddress);
    let column = this.col;
    if (this.isColumnRelative()) {
      column += baseAddress.col;
    }
    return simpleColumnAddress(sheet, column);
  }
  toSimpleRowAddress(baseAddress) {
    const sheet = absoluteSheetReference(this, baseAddress);
    let row = this.row;
    if (this.isRowRelative()) {
      row += baseAddress.row;
    }
    return simpleRowAddress(sheet, row);
  }
  isRowAbsolute() {
    return this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE || this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_ROW;
  }
  isColumnAbsolute() {
    return this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE || this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_COL;
  }
  isColumnRelative() {
    return this.type === CellReferenceType.CELL_REFERENCE_RELATIVE || this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_ROW;
  }
  isRowRelative() {
    return this.type === CellReferenceType.CELL_REFERENCE_RELATIVE || this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_COL;
  }
  isAbsolute() {
    return this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE && this.sheet !== void 0;
  }
  shiftedByRows(numberOfRows) {
    return new CellAddress(this.col, this.row + numberOfRows, this.type, this.sheet);
  }
  shiftedByColumns(numberOfColumns) {
    return new CellAddress(this.col + numberOfColumns, this.row, this.type, this.sheet);
  }
  moved(toSheet, toRight, toBottom) {
    const newSheet = this.sheet === void 0 ? void 0 : toSheet;
    return new CellAddress(this.col + toRight, this.row + toBottom, this.type, newSheet);
  }
  withSheet(sheet) {
    return new CellAddress(this.col, this.row, this.type, sheet);
  }
  isInvalid(baseAddress) {
    return isColOrRowInvalid(this.toSimpleCellAddress(baseAddress));
  }
  shiftRelativeDimensions(toRight, toBottom) {
    const col = this.isColumnAbsolute() ? this.col : this.col + toRight;
    const row = this.isRowAbsolute() ? this.row : this.row + toBottom;
    return new CellAddress(col, row, this.type, this.sheet);
  }
  shiftAbsoluteDimensions(toRight, toBottom) {
    const col = this.isColumnRelative() ? this.col : this.col + toRight;
    const row = this.isRowRelative() ? this.row : this.row + toBottom;
    return new CellAddress(col, row, this.type, this.sheet);
  }
  hash(withSheet) {
    const sheetPart = withSheet && this.sheet !== void 0 ? `#${this.sheet}` : "";
    switch (this.type) {
      case CellReferenceType.CELL_REFERENCE_RELATIVE: {
        return `${sheetPart}#${this.row}R${this.col}`;
      }
      case CellReferenceType.CELL_REFERENCE_ABSOLUTE: {
        return `${sheetPart}#${this.row}A${this.col}`;
      }
      case CellReferenceType.CELL_REFERENCE_ABSOLUTE_COL: {
        return `${sheetPart}#${this.row}AC${this.col}`;
      }
      case CellReferenceType.CELL_REFERENCE_ABSOLUTE_ROW: {
        return `${sheetPart}#${this.row}AR${this.col}`;
      }
    }
  }
  unparse(baseAddress) {
    const simpleAddress = this.toSimpleCellAddress(baseAddress);
    if (isColOrRowInvalid(simpleAddress)) {
      return void 0;
    }
    const column = columnIndexToLabel(simpleAddress.col);
    const rowDollar = this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE || this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_ROW ? "$" : "";
    const colDollar = this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE || this.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_COL ? "$" : "";
    return `${colDollar}${column}${rowDollar}${simpleAddress.row + 1}`;
  }
  exceedsSheetSizeLimits(maxColumns, maxRows) {
    return this.row >= maxRows || this.col >= maxColumns;
  }
}
const RANGE_OPERATOR = ":";
const ABSOLUTE_OPERATOR = "$";
const ALL_WHITESPACE_PATTERN = "\\s+";
const ODFF_WHITESPACE_PATTERN = "[ \\t\\n\\r]+";
const UNICODE_LETTER_PATTERN = "A-Za-zÀ-ʯ";
const NON_RESERVED_CHARACTER_PATTERN = `${UNICODE_LETTER_PATTERN}0-9_.`;
const UNQUOTED_SHEET_NAME_PATTERN = `[${UNICODE_LETTER_PATTERN}0-9_]+`;
const QUOTED_SHEET_NAME_PATTERN = "'(((?!').|'')*)'";
const SHEET_NAME_PATTERN = `(${UNQUOTED_SHEET_NAME_PATTERN}|${QUOTED_SHEET_NAME_PATTERN})!`;
const CELL_REFERENCE_PATTERN = `(${SHEET_NAME_PATTERN})?\\${ABSOLUTE_OPERATOR}?[A-Za-z]+\\${ABSOLUTE_OPERATOR}?[0-9]+`;
const COLUMN_REFERENCE_PATTERN = `(${SHEET_NAME_PATTERN})?\\${ABSOLUTE_OPERATOR}?[A-Za-z]+`;
const ROW_REFERENCE_PATTERN = `(${SHEET_NAME_PATTERN})?\\${ABSOLUTE_OPERATOR}?[0-9]+`;
const R1C1_CELL_REFERENCE_PATTERN = "[rR][0-9]*[cC][0-9]*";
const CELL_REFERENCE_WITH_NEXT_CHARACTER_PATTERN = `(${CELL_REFERENCE_PATTERN})[^${NON_RESERVED_CHARACTER_PATTERN}]`;
const NAMED_EXPRESSION_PATTERN = `[${UNICODE_LETTER_PATTERN}_][${NON_RESERVED_CHARACTER_PATTERN}]*`;
const ALL_DIGITS_ARRAY = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const ALL_UNICODE_LETTERS_ARRAY = [...Array.from(Array(26)).map((_2, i2) => i2 + "A".charCodeAt(0)), ...Array.from(Array(26)).map((_2, i2) => i2 + "a".charCodeAt(0)), ...Array.from(Array(687 - 192 + 1)).map((_2, i2) => i2 + 192)].map((code) => String.fromCharCode(code));
const addressRegex = new RegExp(`^(${SHEET_NAME_PATTERN})?(\\${ABSOLUTE_OPERATOR}?)([A-Za-z]+)(\\${ABSOLUTE_OPERATOR}?)([0-9]+)$`);
const columnRegex = new RegExp(`^(${SHEET_NAME_PATTERN})?(\\${ABSOLUTE_OPERATOR}?)([A-Za-z]+)$`);
const rowRegex = new RegExp(`^(${SHEET_NAME_PATTERN})?(\\${ABSOLUTE_OPERATOR}?)([0-9]+)$`);
const simpleSheetNameRegex = new RegExp(`^${UNQUOTED_SHEET_NAME_PATTERN}$`);
const cellAddressFromString = (stringAddress, baseAddress, resolveSheetReference) => {
  const result = addressRegex.exec(stringAddress);
  if (!result) {
    return void 0;
  }
  const col = columnLabelToIndex(result[6]);
  const row = Number(result[8]) - 1;
  const sheetName = extractSheetName(result);
  const sheet = sheetNameToId(sheetName, resolveSheetReference);
  if (sheet === null) {
    return void 0;
  }
  if (result[5] === ABSOLUTE_OPERATOR && result[7] === ABSOLUTE_OPERATOR) {
    return CellAddress.absolute(col, row, sheet);
  } else if (result[5] === ABSOLUTE_OPERATOR) {
    return CellAddress.absoluteCol(col, row - baseAddress.row, sheet);
  } else if (result[7] === ABSOLUTE_OPERATOR) {
    return CellAddress.absoluteRow(col - baseAddress.col, row, sheet);
  } else {
    return CellAddress.relative(col - baseAddress.col, row - baseAddress.row, sheet);
  }
};
const columnAddressFromString = (stringAddress, baseAddress, resolveSheetReference) => {
  const result = columnRegex.exec(stringAddress);
  if (!result) {
    return void 0;
  }
  const col = columnLabelToIndex(result[6]);
  const sheetName = extractSheetName(result);
  const sheet = sheetNameToId(sheetName, resolveSheetReference);
  if (sheet === null) {
    return void 0;
  }
  if (result[5] === ABSOLUTE_OPERATOR) {
    return ColumnAddress.absolute(col, sheet);
  } else {
    return ColumnAddress.relative(col - baseAddress.col, sheet);
  }
};
const rowAddressFromString = (stringAddress, baseAddress, resolveSheetReference) => {
  const result = rowRegex.exec(stringAddress);
  if (!result) {
    return void 0;
  }
  const row = Number(result[6]) - 1;
  const sheetName = extractSheetName(result);
  const sheet = sheetNameToId(sheetName, resolveSheetReference);
  if (sheet === null) {
    return void 0;
  }
  if (result[5] === ABSOLUTE_OPERATOR) {
    return RowAddress.absolute(row, sheet);
  } else {
    return RowAddress.relative(row - baseAddress.row, sheet);
  }
};
const simpleCellAddressFromString = (resolveSheetReference, stringAddress, contextSheetId) => {
  const regExpExecArray = addressRegex.exec(stringAddress);
  if (!regExpExecArray) {
    return void 0;
  }
  const col = columnLabelToIndex(regExpExecArray[6]);
  const row = Number(regExpExecArray[8]) - 1;
  const sheetName = extractSheetName(regExpExecArray);
  const sheet = sheetNameToId(sheetName, resolveSheetReference);
  if (sheet === null) {
    return void 0;
  }
  const effectiveSheet = sheet === void 0 ? contextSheetId : sheet;
  return simpleCellAddress(effectiveSheet, col, row);
};
const simpleCellRangeFromString = (resolveSheetReference, stringAddress, contextSheetId) => {
  const split2 = stringAddress.split(RANGE_OPERATOR);
  if (split2.length !== 2) {
    return void 0;
  }
  const [startString, endString] = split2;
  const start = simpleCellAddressFromString(resolveSheetReference, startString, contextSheetId);
  if (start === void 0) {
    return void 0;
  }
  const end = simpleCellAddressFromString(resolveSheetReference, endString, start.sheet);
  if (end === void 0) {
    return void 0;
  }
  if (start.sheet !== end.sheet) {
    return void 0;
  }
  return simpleCellRange(start, end);
};
const simpleCellAddressToString = (sheetIndexMapping, address, sheetIndex) => {
  const column = columnIndexToLabel(address.col);
  const sheetName = sheetIndexToString(address.sheet, sheetIndexMapping);
  if (sheetName === void 0) {
    return void 0;
  }
  if (sheetIndex !== address.sheet) {
    return `${sheetName}!${column}${address.row + 1}`;
  } else {
    return `${column}${address.row + 1}`;
  }
};
const simpleCellRangeToString = (sheetIndexMapping, address, sheetIndex) => {
  const startString = simpleCellAddressToString(sheetIndexMapping, address.start, sheetIndex);
  const endString = simpleCellAddressToString(sheetIndexMapping, address.end, address.start.sheet);
  if (startString === void 0 || endString === void 0) {
    return void 0;
  } else {
    return `${startString}${RANGE_OPERATOR}${endString}`;
  }
};
function columnLabelToIndex(columnStringRepresentation) {
  if (columnStringRepresentation.length === 1) {
    return columnStringRepresentation.toUpperCase().charCodeAt(0) - 65;
  } else {
    return columnStringRepresentation.split("").reduce((currentColumn, nextLetter) => {
      return currentColumn * 26 + (nextLetter.toUpperCase().charCodeAt(0) - 64);
    }, 0) - 1;
  }
}
function columnIndexToLabel(column) {
  let result = "";
  while (column >= 0) {
    result = String.fromCharCode(column % 26 + 97) + result;
    column = Math.floor(column / 26) - 1;
  }
  return result.toUpperCase();
}
function sheetIndexToString(sheetId, sheetMappingFn) {
  let sheetName = sheetMappingFn(sheetId);
  if (sheetName === void 0) {
    return void 0;
  }
  if (simpleSheetNameRegex.test(sheetName)) {
    return sheetName;
  } else {
    sheetName = sheetName.replace(/'/g, "''");
    return `'${sheetName}'`;
  }
}
function extractSheetName(regexResult) {
  var _a2;
  const maybeSheetName = (_a2 = regexResult[3]) !== null && _a2 !== void 0 ? _a2 : regexResult[2];
  return maybeSheetName ? maybeSheetName.replace(/''/g, "'") : null;
}
function sheetNameToId(sheetName, resolveSheetReference) {
  if (!sheetName) {
    return void 0;
  }
  const sheetId = resolveSheetReference(sheetName);
  return sheetId === void 0 ? null : sheetId;
}
function isEmpty(arr) {
  return arr && arr.length === 0;
}
function keys(obj) {
  if (obj === void 0 || obj === null) {
    return [];
  }
  return Object.keys(obj);
}
function values(obj) {
  var vals = [];
  var keys2 = Object.keys(obj);
  for (var i2 = 0; i2 < keys2.length; i2++) {
    vals.push(obj[keys2[i2]]);
  }
  return vals;
}
function mapValues(obj, callback) {
  var result = [];
  var objKeys = keys(obj);
  for (var idx = 0; idx < objKeys.length; idx++) {
    var currKey = objKeys[idx];
    result.push(callback.call(null, obj[currKey], currKey));
  }
  return result;
}
function map(arr, callback) {
  var result = [];
  for (var idx = 0; idx < arr.length; idx++) {
    result.push(callback.call(null, arr[idx], idx));
  }
  return result;
}
function flatten(arr) {
  var result = [];
  for (var idx = 0; idx < arr.length; idx++) {
    var currItem = arr[idx];
    if (Array.isArray(currItem)) {
      result = result.concat(flatten(currItem));
    } else {
      result.push(currItem);
    }
  }
  return result;
}
function first$1(arr) {
  return isEmpty(arr) ? void 0 : arr[0];
}
function last(arr) {
  var len = arr && arr.length;
  return len ? arr[len - 1] : void 0;
}
function forEach(collection, iteratorCallback) {
  if (Array.isArray(collection)) {
    for (var i2 = 0; i2 < collection.length; i2++) {
      iteratorCallback.call(null, collection[i2], i2);
    }
  } else if (isObject(collection)) {
    var colKeys = keys(collection);
    for (var i2 = 0; i2 < colKeys.length; i2++) {
      var key = colKeys[i2];
      var value = collection[key];
      iteratorCallback.call(null, value, key);
    }
  } else {
    throw Error("non exhaustive match");
  }
}
function isString(item) {
  return typeof item === "string";
}
function isUndefined(item) {
  return item === void 0;
}
function isFunction(item) {
  return item instanceof Function;
}
function drop(arr, howMuch) {
  if (howMuch === void 0) {
    howMuch = 1;
  }
  return arr.slice(howMuch, arr.length);
}
function dropRight(arr, howMuch) {
  if (howMuch === void 0) {
    howMuch = 1;
  }
  return arr.slice(0, arr.length - howMuch);
}
function filter(arr, predicate) {
  var result = [];
  if (Array.isArray(arr)) {
    for (var i2 = 0; i2 < arr.length; i2++) {
      var item = arr[i2];
      if (predicate.call(null, item)) {
        result.push(item);
      }
    }
  }
  return result;
}
function reject(arr, predicate) {
  return filter(arr, function(item) {
    return !predicate(item);
  });
}
function pick(obj, predicate) {
  var keys2 = Object.keys(obj);
  var result = {};
  for (var i2 = 0; i2 < keys2.length; i2++) {
    var currKey = keys2[i2];
    var currItem = obj[currKey];
    if (predicate(currItem)) {
      result[currKey] = currItem;
    }
  }
  return result;
}
function has(obj, prop) {
  if (isObject(obj)) {
    return obj.hasOwnProperty(prop);
  }
  return false;
}
function contains(arr, item) {
  return find(arr, function(currItem) {
    return currItem === item;
  }) !== void 0 ? true : false;
}
function cloneArr(arr) {
  var newArr = [];
  for (var i2 = 0; i2 < arr.length; i2++) {
    newArr.push(arr[i2]);
  }
  return newArr;
}
function cloneObj(obj) {
  var clonedObj = {};
  for (var key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      clonedObj[key] = obj[key];
    }
  }
  return clonedObj;
}
function find(arr, predicate) {
  for (var i2 = 0; i2 < arr.length; i2++) {
    var item = arr[i2];
    if (predicate.call(null, item)) {
      return item;
    }
  }
  return void 0;
}
function findAll(arr, predicate) {
  var found = [];
  for (var i2 = 0; i2 < arr.length; i2++) {
    var item = arr[i2];
    if (predicate.call(null, item)) {
      found.push(item);
    }
  }
  return found;
}
function reduce(arrOrObj, iterator, initial) {
  var isArr = Array.isArray(arrOrObj);
  var vals = isArr ? arrOrObj : values(arrOrObj);
  var objKeys = isArr ? [] : keys(arrOrObj);
  var accumulator = initial;
  for (var i2 = 0; i2 < vals.length; i2++) {
    accumulator = iterator.call(null, accumulator, vals[i2], isArr ? i2 : objKeys[i2]);
  }
  return accumulator;
}
function compact(arr) {
  return reject(arr, function(item) {
    return item === null || item === void 0;
  });
}
function uniq(arr, identity) {
  if (identity === void 0) {
    identity = function(item) {
      return item;
    };
  }
  var identities = [];
  return reduce(arr, function(result, currItem) {
    var currIdentity = identity(currItem);
    if (contains(identities, currIdentity)) {
      return result;
    } else {
      identities.push(currIdentity);
      return result.concat(currItem);
    }
  }, []);
}
function isArray(obj) {
  return Array.isArray(obj);
}
function isRegExp(obj) {
  return obj instanceof RegExp;
}
function isObject(obj) {
  return obj instanceof Object;
}
function every(arr, predicate) {
  for (var i2 = 0; i2 < arr.length; i2++) {
    if (!predicate(arr[i2], i2)) {
      return false;
    }
  }
  return true;
}
function difference(arr, values2) {
  return reject(arr, function(item) {
    return contains(values2, item);
  });
}
function some(arr, predicate) {
  for (var i2 = 0; i2 < arr.length; i2++) {
    if (predicate(arr[i2])) {
      return true;
    }
  }
  return false;
}
function indexOf(arr, value) {
  for (var i2 = 0; i2 < arr.length; i2++) {
    if (arr[i2] === value) {
      return i2;
    }
  }
  return -1;
}
function assign(target) {
  var sources = [];
  for (var _i2 = 1; _i2 < arguments.length; _i2++) {
    sources[_i2 - 1] = arguments[_i2];
  }
  for (var i2 = 0; i2 < sources.length; i2++) {
    var curSource = sources[i2];
    var currSourceKeys = keys(curSource);
    for (var j2 = 0; j2 < currSourceKeys.length; j2++) {
      var currKey = currSourceKeys[j2];
      target[currKey] = curSource[currKey];
    }
  }
  return target;
}
function assignNoOverwrite(target) {
  var sources = [];
  for (var _i2 = 1; _i2 < arguments.length; _i2++) {
    sources[_i2 - 1] = arguments[_i2];
  }
  for (var i2 = 0; i2 < sources.length; i2++) {
    var curSource = sources[i2];
    if (isUndefined(curSource)) {
      continue;
    }
    var currSourceKeys = keys(curSource);
    for (var j2 = 0; j2 < currSourceKeys.length; j2++) {
      var currKey = currSourceKeys[j2];
      if (!has(target, currKey)) {
        target[currKey] = curSource[currKey];
      }
    }
  }
  return target;
}
function defaults() {
  var sources = [];
  for (var _i2 = 0; _i2 < arguments.length; _i2++) {
    sources[_i2] = arguments[_i2];
  }
  return assignNoOverwrite.apply(null, [{}].concat(sources));
}
function groupBy(arr, groupKeyFunc) {
  var result = {};
  forEach(arr, function(item) {
    var currGroupKey = groupKeyFunc(item);
    var currGroupArr = result[currGroupKey];
    if (currGroupArr) {
      currGroupArr.push(item);
    } else {
      result[currGroupKey] = [item];
    }
  });
  return result;
}
function merge(obj1, obj2) {
  var result = cloneObj(obj1);
  var keys2 = keys(obj2);
  for (var i2 = 0; i2 < keys2.length; i2++) {
    var key = keys2[i2];
    var value = obj2[key];
    result[key] = value;
  }
  return result;
}
function NOOP() {
}
function IDENTITY(item) {
  return item;
}
function packArray(holeyArr) {
  var result = [];
  for (var i2 = 0; i2 < holeyArr.length; i2++) {
    var orgValue = holeyArr[i2];
    result.push(orgValue !== void 0 ? orgValue : void 0);
  }
  return result;
}
function PRINT_ERROR(msg) {
  if (console && console.error) {
    console.error("Error: " + msg);
  }
}
function PRINT_WARNING(msg) {
  if (console && console.warn) {
    console.warn("Warning: " + msg);
  }
}
function isES2015MapSupported() {
  return typeof Map === "function";
}
function applyMixins(derivedCtor, baseCtors) {
  baseCtors.forEach(function(baseCtor) {
    var baseProto = baseCtor.prototype;
    Object.getOwnPropertyNames(baseProto).forEach(function(propName) {
      if (propName === "constructor") {
        return;
      }
      var basePropDescriptor = Object.getOwnPropertyDescriptor(baseProto, propName);
      if (basePropDescriptor && (basePropDescriptor.get || basePropDescriptor.set)) {
        Object.defineProperty(derivedCtor.prototype, propName, basePropDescriptor);
      } else {
        derivedCtor.prototype[propName] = baseCtor.prototype[propName];
      }
    });
  });
}
function toFastProperties(toBecomeFast) {
  function FakeConstructor() {
  }
  FakeConstructor.prototype = toBecomeFast;
  var fakeInstance = new FakeConstructor();
  function fakeAccess() {
    return typeof fakeInstance.bar;
  }
  fakeAccess();
  fakeAccess();
  return toBecomeFast;
}
function peek(arr) {
  return arr[arr.length - 1];
}
function timer(func) {
  var start = (/* @__PURE__ */ new Date()).getTime();
  var val = func();
  var end = (/* @__PURE__ */ new Date()).getTime();
  var total = end - start;
  return { time: total, value: val };
}
var regexpToAst$1 = { exports: {} };
var regexpToAst = regexpToAst$1.exports;
var hasRequiredRegexpToAst;
function requireRegexpToAst() {
  if (hasRequiredRegexpToAst) return regexpToAst$1.exports;
  hasRequiredRegexpToAst = 1;
  (function(module) {
    (function(root, factory) {
      if (module.exports) {
        module.exports = factory();
      } else {
        root.regexpToAst = factory();
      }
    })(
      typeof self !== "undefined" ? (
        // istanbul ignore next
        self
      ) : regexpToAst,
      function() {
        function RegExpParser() {
        }
        RegExpParser.prototype.saveState = function() {
          return {
            idx: this.idx,
            input: this.input,
            groupIdx: this.groupIdx
          };
        };
        RegExpParser.prototype.restoreState = function(newState) {
          this.idx = newState.idx;
          this.input = newState.input;
          this.groupIdx = newState.groupIdx;
        };
        RegExpParser.prototype.pattern = function(input) {
          this.idx = 0;
          this.input = input;
          this.groupIdx = 0;
          this.consumeChar("/");
          var value = this.disjunction();
          this.consumeChar("/");
          var flags = {
            type: "Flags",
            global: false,
            ignoreCase: false,
            multiLine: false,
            unicode: false,
            sticky: false
          };
          while (this.isRegExpFlag()) {
            switch (this.popChar()) {
              case "g":
                addFlag(flags, "global");
                break;
              case "i":
                addFlag(flags, "ignoreCase");
                break;
              case "m":
                addFlag(flags, "multiLine");
                break;
              case "u":
                addFlag(flags, "unicode");
                break;
              case "y":
                addFlag(flags, "sticky");
                break;
            }
          }
          if (this.idx !== this.input.length) {
            throw Error(
              "Redundant input: " + this.input.substring(this.idx)
            );
          }
          return { type: "Pattern", flags, value };
        };
        RegExpParser.prototype.disjunction = function() {
          var alts = [];
          alts.push(this.alternative());
          while (this.peekChar() === "|") {
            this.consumeChar("|");
            alts.push(this.alternative());
          }
          return { type: "Disjunction", value: alts };
        };
        RegExpParser.prototype.alternative = function() {
          var terms = [];
          while (this.isTerm()) {
            terms.push(this.term());
          }
          return { type: "Alternative", value: terms };
        };
        RegExpParser.prototype.term = function() {
          if (this.isAssertion()) {
            return this.assertion();
          } else {
            return this.atom();
          }
        };
        RegExpParser.prototype.assertion = function() {
          switch (this.popChar()) {
            case "^":
              return { type: "StartAnchor" };
            case "$":
              return { type: "EndAnchor" };
            // '\b' or '\B'
            case "\\":
              switch (this.popChar()) {
                case "b":
                  return { type: "WordBoundary" };
                case "B":
                  return { type: "NonWordBoundary" };
              }
              throw Error("Invalid Assertion Escape");
            // '(?=' or '(?!'
            case "(":
              this.consumeChar("?");
              var type;
              switch (this.popChar()) {
                case "=":
                  type = "Lookahead";
                  break;
                case "!":
                  type = "NegativeLookahead";
                  break;
              }
              ASSERT_EXISTS(type);
              var disjunction = this.disjunction();
              this.consumeChar(")");
              return { type, value: disjunction };
          }
          ASSERT_NEVER_REACH_HERE();
        };
        RegExpParser.prototype.quantifier = function(isBacktracking) {
          var range;
          switch (this.popChar()) {
            case "*":
              range = {
                atLeast: 0,
                atMost: Infinity
              };
              break;
            case "+":
              range = {
                atLeast: 1,
                atMost: Infinity
              };
              break;
            case "?":
              range = {
                atLeast: 0,
                atMost: 1
              };
              break;
            case "{":
              var atLeast = this.integerIncludingZero();
              switch (this.popChar()) {
                case "}":
                  range = {
                    atLeast,
                    atMost: atLeast
                  };
                  break;
                case ",":
                  var atMost;
                  if (this.isDigit()) {
                    atMost = this.integerIncludingZero();
                    range = {
                      atLeast,
                      atMost
                    };
                  } else {
                    range = {
                      atLeast,
                      atMost: Infinity
                    };
                  }
                  this.consumeChar("}");
                  break;
              }
              if (isBacktracking === true && range === void 0) {
                return void 0;
              }
              ASSERT_EXISTS(range);
              break;
          }
          if (isBacktracking === true && range === void 0) {
            return void 0;
          }
          ASSERT_EXISTS(range);
          if (this.peekChar(0) === "?") {
            this.consumeChar("?");
            range.greedy = false;
          } else {
            range.greedy = true;
          }
          range.type = "Quantifier";
          return range;
        };
        RegExpParser.prototype.atom = function() {
          var atom;
          switch (this.peekChar()) {
            case ".":
              atom = this.dotAll();
              break;
            case "\\":
              atom = this.atomEscape();
              break;
            case "[":
              atom = this.characterClass();
              break;
            case "(":
              atom = this.group();
              break;
          }
          if (atom === void 0 && this.isPatternCharacter()) {
            atom = this.patternCharacter();
          }
          ASSERT_EXISTS(atom);
          if (this.isQuantifier()) {
            atom.quantifier = this.quantifier();
          }
          return atom;
        };
        RegExpParser.prototype.dotAll = function() {
          this.consumeChar(".");
          return {
            type: "Set",
            complement: true,
            value: [cc("\n"), cc("\r"), cc("\u2028"), cc("\u2029")]
          };
        };
        RegExpParser.prototype.atomEscape = function() {
          this.consumeChar("\\");
          switch (this.peekChar()) {
            case "1":
            case "2":
            case "3":
            case "4":
            case "5":
            case "6":
            case "7":
            case "8":
            case "9":
              return this.decimalEscapeAtom();
            case "d":
            case "D":
            case "s":
            case "S":
            case "w":
            case "W":
              return this.characterClassEscape();
            case "f":
            case "n":
            case "r":
            case "t":
            case "v":
              return this.controlEscapeAtom();
            case "c":
              return this.controlLetterEscapeAtom();
            case "0":
              return this.nulCharacterAtom();
            case "x":
              return this.hexEscapeSequenceAtom();
            case "u":
              return this.regExpUnicodeEscapeSequenceAtom();
            default:
              return this.identityEscapeAtom();
          }
        };
        RegExpParser.prototype.decimalEscapeAtom = function() {
          var value = this.positiveInteger();
          return { type: "GroupBackReference", value };
        };
        RegExpParser.prototype.characterClassEscape = function() {
          var set2;
          var complement = false;
          switch (this.popChar()) {
            case "d":
              set2 = digitsCharCodes;
              break;
            case "D":
              set2 = digitsCharCodes;
              complement = true;
              break;
            case "s":
              set2 = whitespaceCodes;
              break;
            case "S":
              set2 = whitespaceCodes;
              complement = true;
              break;
            case "w":
              set2 = wordCharCodes;
              break;
            case "W":
              set2 = wordCharCodes;
              complement = true;
              break;
          }
          ASSERT_EXISTS(set2);
          return { type: "Set", value: set2, complement };
        };
        RegExpParser.prototype.controlEscapeAtom = function() {
          var escapeCode;
          switch (this.popChar()) {
            case "f":
              escapeCode = cc("\f");
              break;
            case "n":
              escapeCode = cc("\n");
              break;
            case "r":
              escapeCode = cc("\r");
              break;
            case "t":
              escapeCode = cc("	");
              break;
            case "v":
              escapeCode = cc("\v");
              break;
          }
          ASSERT_EXISTS(escapeCode);
          return { type: "Character", value: escapeCode };
        };
        RegExpParser.prototype.controlLetterEscapeAtom = function() {
          this.consumeChar("c");
          var letter = this.popChar();
          if (/[a-zA-Z]/.test(letter) === false) {
            throw Error("Invalid ");
          }
          var letterCode = letter.toUpperCase().charCodeAt(0) - 64;
          return { type: "Character", value: letterCode };
        };
        RegExpParser.prototype.nulCharacterAtom = function() {
          this.consumeChar("0");
          return { type: "Character", value: cc("\0") };
        };
        RegExpParser.prototype.hexEscapeSequenceAtom = function() {
          this.consumeChar("x");
          return this.parseHexDigits(2);
        };
        RegExpParser.prototype.regExpUnicodeEscapeSequenceAtom = function() {
          this.consumeChar("u");
          return this.parseHexDigits(4);
        };
        RegExpParser.prototype.identityEscapeAtom = function() {
          var escapedChar = this.popChar();
          return { type: "Character", value: cc(escapedChar) };
        };
        RegExpParser.prototype.classPatternCharacterAtom = function() {
          switch (this.peekChar()) {
            // istanbul ignore next
            case "\n":
            // istanbul ignore next
            case "\r":
            // istanbul ignore next
            case "\u2028":
            // istanbul ignore next
            case "\u2029":
            // istanbul ignore next
            case "\\":
            // istanbul ignore next
            case "]":
              throw Error("TBD");
            default:
              var nextChar = this.popChar();
              return { type: "Character", value: cc(nextChar) };
          }
        };
        RegExpParser.prototype.characterClass = function() {
          var set2 = [];
          var complement = false;
          this.consumeChar("[");
          if (this.peekChar(0) === "^") {
            this.consumeChar("^");
            complement = true;
          }
          while (this.isClassAtom()) {
            var from = this.classAtom();
            var isFromSingleChar = from.type === "Character";
            if (isFromSingleChar && this.isRangeDash()) {
              this.consumeChar("-");
              var to2 = this.classAtom();
              var isToSingleChar = to2.type === "Character";
              if (isToSingleChar) {
                if (to2.value < from.value) {
                  throw Error("Range out of order in character class");
                }
                set2.push({ from: from.value, to: to2.value });
              } else {
                insertToSet(from.value, set2);
                set2.push(cc("-"));
                insertToSet(to2.value, set2);
              }
            } else {
              insertToSet(from.value, set2);
            }
          }
          this.consumeChar("]");
          return { type: "Set", complement, value: set2 };
        };
        RegExpParser.prototype.classAtom = function() {
          switch (this.peekChar()) {
            // istanbul ignore next
            case "]":
            // istanbul ignore next
            case "\n":
            // istanbul ignore next
            case "\r":
            // istanbul ignore next
            case "\u2028":
            // istanbul ignore next
            case "\u2029":
              throw Error("TBD");
            case "\\":
              return this.classEscape();
            default:
              return this.classPatternCharacterAtom();
          }
        };
        RegExpParser.prototype.classEscape = function() {
          this.consumeChar("\\");
          switch (this.peekChar()) {
            // Matches a backspace.
            // (Not to be confused with \b word boundary outside characterClass)
            case "b":
              this.consumeChar("b");
              return { type: "Character", value: cc("\b") };
            case "d":
            case "D":
            case "s":
            case "S":
            case "w":
            case "W":
              return this.characterClassEscape();
            case "f":
            case "n":
            case "r":
            case "t":
            case "v":
              return this.controlEscapeAtom();
            case "c":
              return this.controlLetterEscapeAtom();
            case "0":
              return this.nulCharacterAtom();
            case "x":
              return this.hexEscapeSequenceAtom();
            case "u":
              return this.regExpUnicodeEscapeSequenceAtom();
            default:
              return this.identityEscapeAtom();
          }
        };
        RegExpParser.prototype.group = function() {
          var capturing = true;
          this.consumeChar("(");
          switch (this.peekChar(0)) {
            case "?":
              this.consumeChar("?");
              this.consumeChar(":");
              capturing = false;
              break;
            default:
              this.groupIdx++;
              break;
          }
          var value = this.disjunction();
          this.consumeChar(")");
          var groupAst = {
            type: "Group",
            capturing,
            value
          };
          if (capturing) {
            groupAst.idx = this.groupIdx;
          }
          return groupAst;
        };
        RegExpParser.prototype.positiveInteger = function() {
          var number = this.popChar();
          if (decimalPatternNoZero.test(number) === false) {
            throw Error("Expecting a positive integer");
          }
          while (decimalPattern.test(this.peekChar(0))) {
            number += this.popChar();
          }
          return parseInt(number, 10);
        };
        RegExpParser.prototype.integerIncludingZero = function() {
          var number = this.popChar();
          if (decimalPattern.test(number) === false) {
            throw Error("Expecting an integer");
          }
          while (decimalPattern.test(this.peekChar(0))) {
            number += this.popChar();
          }
          return parseInt(number, 10);
        };
        RegExpParser.prototype.patternCharacter = function() {
          var nextChar = this.popChar();
          switch (nextChar) {
            // istanbul ignore next
            case "\n":
            // istanbul ignore next
            case "\r":
            // istanbul ignore next
            case "\u2028":
            // istanbul ignore next
            case "\u2029":
            // istanbul ignore next
            case "^":
            // istanbul ignore next
            case "$":
            // istanbul ignore next
            case "\\":
            // istanbul ignore next
            case ".":
            // istanbul ignore next
            case "*":
            // istanbul ignore next
            case "+":
            // istanbul ignore next
            case "?":
            // istanbul ignore next
            case "(":
            // istanbul ignore next
            case ")":
            // istanbul ignore next
            case "[":
            // istanbul ignore next
            case "|":
              throw Error("TBD");
            default:
              return { type: "Character", value: cc(nextChar) };
          }
        };
        RegExpParser.prototype.isRegExpFlag = function() {
          switch (this.peekChar(0)) {
            case "g":
            case "i":
            case "m":
            case "u":
            case "y":
              return true;
            default:
              return false;
          }
        };
        RegExpParser.prototype.isRangeDash = function() {
          return this.peekChar() === "-" && this.isClassAtom(1);
        };
        RegExpParser.prototype.isDigit = function() {
          return decimalPattern.test(this.peekChar(0));
        };
        RegExpParser.prototype.isClassAtom = function(howMuch) {
          if (howMuch === void 0) {
            howMuch = 0;
          }
          switch (this.peekChar(howMuch)) {
            case "]":
            case "\n":
            case "\r":
            case "\u2028":
            case "\u2029":
              return false;
            default:
              return true;
          }
        };
        RegExpParser.prototype.isTerm = function() {
          return this.isAtom() || this.isAssertion();
        };
        RegExpParser.prototype.isAtom = function() {
          if (this.isPatternCharacter()) {
            return true;
          }
          switch (this.peekChar(0)) {
            case ".":
            case "\\":
            // atomEscape
            case "[":
            // characterClass
            // TODO: isAtom must be called before isAssertion - disambiguate
            case "(":
              return true;
            default:
              return false;
          }
        };
        RegExpParser.prototype.isAssertion = function() {
          switch (this.peekChar(0)) {
            case "^":
            case "$":
              return true;
            // '\b' or '\B'
            case "\\":
              switch (this.peekChar(1)) {
                case "b":
                case "B":
                  return true;
                default:
                  return false;
              }
            // '(?=' or '(?!'
            case "(":
              return this.peekChar(1) === "?" && (this.peekChar(2) === "=" || this.peekChar(2) === "!");
            default:
              return false;
          }
        };
        RegExpParser.prototype.isQuantifier = function() {
          var prevState = this.saveState();
          try {
            return this.quantifier(true) !== void 0;
          } catch (e3) {
            return false;
          } finally {
            this.restoreState(prevState);
          }
        };
        RegExpParser.prototype.isPatternCharacter = function() {
          switch (this.peekChar()) {
            case "^":
            case "$":
            case "\\":
            case ".":
            case "*":
            case "+":
            case "?":
            case "(":
            case ")":
            case "[":
            case "|":
            case "/":
            case "\n":
            case "\r":
            case "\u2028":
            case "\u2029":
              return false;
            default:
              return true;
          }
        };
        RegExpParser.prototype.parseHexDigits = function(howMany) {
          var hexString = "";
          for (var i3 = 0; i3 < howMany; i3++) {
            var hexChar = this.popChar();
            if (hexDigitPattern.test(hexChar) === false) {
              throw Error("Expecting a HexDecimal digits");
            }
            hexString += hexChar;
          }
          var charCode = parseInt(hexString, 16);
          return { type: "Character", value: charCode };
        };
        RegExpParser.prototype.peekChar = function(howMuch) {
          if (howMuch === void 0) {
            howMuch = 0;
          }
          return this.input[this.idx + howMuch];
        };
        RegExpParser.prototype.popChar = function() {
          var nextChar = this.peekChar(0);
          this.consumeChar();
          return nextChar;
        };
        RegExpParser.prototype.consumeChar = function(char) {
          if (char !== void 0 && this.input[this.idx] !== char) {
            throw Error(
              "Expected: '" + char + "' but found: '" + this.input[this.idx] + "' at offset: " + this.idx
            );
          }
          if (this.idx >= this.input.length) {
            throw Error("Unexpected end of input");
          }
          this.idx++;
        };
        var hexDigitPattern = /[0-9a-fA-F]/;
        var decimalPattern = /[0-9]/;
        var decimalPatternNoZero = /[1-9]/;
        function cc(char) {
          return char.charCodeAt(0);
        }
        function insertToSet(item, set2) {
          if (item.length !== void 0) {
            item.forEach(function(subItem) {
              set2.push(subItem);
            });
          } else {
            set2.push(item);
          }
        }
        function addFlag(flagObj, flagKey) {
          if (flagObj[flagKey] === true) {
            throw "duplicate flag " + flagKey;
          }
          flagObj[flagKey] = true;
        }
        function ASSERT_EXISTS(obj) {
          if (obj === void 0) {
            throw Error("Internal Error - Should never get here!");
          }
        }
        function ASSERT_NEVER_REACH_HERE() {
          throw Error("Internal Error - Should never get here!");
        }
        var i2;
        var digitsCharCodes = [];
        for (i2 = cc("0"); i2 <= cc("9"); i2++) {
          digitsCharCodes.push(i2);
        }
        var wordCharCodes = [cc("_")].concat(digitsCharCodes);
        for (i2 = cc("a"); i2 <= cc("z"); i2++) {
          wordCharCodes.push(i2);
        }
        for (i2 = cc("A"); i2 <= cc("Z"); i2++) {
          wordCharCodes.push(i2);
        }
        var whitespaceCodes = [
          cc(" "),
          cc("\f"),
          cc("\n"),
          cc("\r"),
          cc("	"),
          cc("\v"),
          cc("	"),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc(" "),
          cc("\u2028"),
          cc("\u2029"),
          cc(" "),
          cc(" "),
          cc("　"),
          cc("\uFEFF")
        ];
        function BaseRegExpVisitor() {
        }
        BaseRegExpVisitor.prototype.visitChildren = function(node) {
          for (var key in node) {
            var child = node[key];
            if (node.hasOwnProperty(key)) {
              if (child.type !== void 0) {
                this.visit(child);
              } else if (Array.isArray(child)) {
                child.forEach(function(subChild) {
                  this.visit(subChild);
                }, this);
              }
            }
          }
        };
        BaseRegExpVisitor.prototype.visit = function(node) {
          switch (node.type) {
            case "Pattern":
              this.visitPattern(node);
              break;
            case "Flags":
              this.visitFlags(node);
              break;
            case "Disjunction":
              this.visitDisjunction(node);
              break;
            case "Alternative":
              this.visitAlternative(node);
              break;
            case "StartAnchor":
              this.visitStartAnchor(node);
              break;
            case "EndAnchor":
              this.visitEndAnchor(node);
              break;
            case "WordBoundary":
              this.visitWordBoundary(node);
              break;
            case "NonWordBoundary":
              this.visitNonWordBoundary(node);
              break;
            case "Lookahead":
              this.visitLookahead(node);
              break;
            case "NegativeLookahead":
              this.visitNegativeLookahead(node);
              break;
            case "Character":
              this.visitCharacter(node);
              break;
            case "Set":
              this.visitSet(node);
              break;
            case "Group":
              this.visitGroup(node);
              break;
            case "GroupBackReference":
              this.visitGroupBackReference(node);
              break;
            case "Quantifier":
              this.visitQuantifier(node);
              break;
          }
          this.visitChildren(node);
        };
        BaseRegExpVisitor.prototype.visitPattern = function(node) {
        };
        BaseRegExpVisitor.prototype.visitFlags = function(node) {
        };
        BaseRegExpVisitor.prototype.visitDisjunction = function(node) {
        };
        BaseRegExpVisitor.prototype.visitAlternative = function(node) {
        };
        BaseRegExpVisitor.prototype.visitStartAnchor = function(node) {
        };
        BaseRegExpVisitor.prototype.visitEndAnchor = function(node) {
        };
        BaseRegExpVisitor.prototype.visitWordBoundary = function(node) {
        };
        BaseRegExpVisitor.prototype.visitNonWordBoundary = function(node) {
        };
        BaseRegExpVisitor.prototype.visitLookahead = function(node) {
        };
        BaseRegExpVisitor.prototype.visitNegativeLookahead = function(node) {
        };
        BaseRegExpVisitor.prototype.visitCharacter = function(node) {
        };
        BaseRegExpVisitor.prototype.visitSet = function(node) {
        };
        BaseRegExpVisitor.prototype.visitGroup = function(node) {
        };
        BaseRegExpVisitor.prototype.visitGroupBackReference = function(node) {
        };
        BaseRegExpVisitor.prototype.visitQuantifier = function(node) {
        };
        return {
          RegExpParser,
          BaseRegExpVisitor,
          VERSION: "0.4.0"
        };
      }
    );
  })(regexpToAst$1);
  return regexpToAst$1.exports;
}
var regexpToAstExports = requireRegexpToAst();
var regExpAstCache = {};
var regExpParser = new regexpToAstExports.RegExpParser();
function getRegExpAst(regExp) {
  var regExpStr = regExp.toString();
  if (regExpAstCache.hasOwnProperty(regExpStr)) {
    return regExpAstCache[regExpStr];
  } else {
    var regExpAst = regExpParser.pattern(regExpStr);
    regExpAstCache[regExpStr] = regExpAst;
    return regExpAst;
  }
}
function clearRegExpParserCache() {
  regExpAstCache = {};
}
var __extends$a = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
var complementErrorMessage = "Complement Sets are not supported for first char optimization";
var failedOptimizationPrefixMsg = 'Unable to use "first char" lexer optimizations:\n';
function getOptimizedStartCodesIndices(regExp, ensureOptimizations) {
  if (ensureOptimizations === void 0) {
    ensureOptimizations = false;
  }
  try {
    var ast = getRegExpAst(regExp);
    var firstChars = firstCharOptimizedIndices(ast.value, {}, ast.flags.ignoreCase);
    return firstChars;
  } catch (e3) {
    if (e3.message === complementErrorMessage) {
      if (ensureOptimizations) {
        PRINT_WARNING("" + failedOptimizationPrefixMsg + ("	Unable to optimize: < " + regExp.toString() + " >\n") + "	Complement Sets cannot be automatically optimized.\n	This will disable the lexer's first char optimizations.\n	See: https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#COMPLEMENT for details.");
      }
    } else {
      var msgSuffix = "";
      if (ensureOptimizations) {
        msgSuffix = "\n	This will disable the lexer's first char optimizations.\n	See: https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#REGEXP_PARSING for details.";
      }
      PRINT_ERROR(failedOptimizationPrefixMsg + "\n" + ("	Failed parsing: < " + regExp.toString() + " >\n") + ("	Using the regexp-to-ast library version: " + regexpToAstExports.VERSION + "\n") + "	Please open an issue at: https://github.com/bd82/regexp-to-ast/issues" + msgSuffix);
    }
  }
  return [];
}
function firstCharOptimizedIndices(ast, result, ignoreCase) {
  switch (ast.type) {
    case "Disjunction":
      for (var i2 = 0; i2 < ast.value.length; i2++) {
        firstCharOptimizedIndices(ast.value[i2], result, ignoreCase);
      }
      break;
    case "Alternative":
      var terms = ast.value;
      for (var i2 = 0; i2 < terms.length; i2++) {
        var term = terms[i2];
        switch (term.type) {
          case "EndAnchor":
          // A group back reference cannot affect potential starting char.
          // because if a back reference is the first production than automatically
          // the group being referenced has had to come BEFORE so its codes have already been added
          case "GroupBackReference":
          // assertions do not affect potential starting codes
          case "Lookahead":
          case "NegativeLookahead":
          case "StartAnchor":
          case "WordBoundary":
          case "NonWordBoundary":
            continue;
        }
        var atom = term;
        switch (atom.type) {
          case "Character":
            addOptimizedIdxToResult(atom.value, result, ignoreCase);
            break;
          case "Set":
            if (atom.complement === true) {
              throw Error(complementErrorMessage);
            }
            forEach(atom.value, function(code) {
              if (typeof code === "number") {
                addOptimizedIdxToResult(code, result, ignoreCase);
              } else {
                var range = code;
                if (ignoreCase === true) {
                  for (var rangeCode = range.from; rangeCode <= range.to; rangeCode++) {
                    addOptimizedIdxToResult(rangeCode, result, ignoreCase);
                  }
                } else {
                  for (var rangeCode = range.from; rangeCode <= range.to && rangeCode < minOptimizationVal; rangeCode++) {
                    addOptimizedIdxToResult(rangeCode, result, ignoreCase);
                  }
                  if (range.to >= minOptimizationVal) {
                    var minUnOptVal = range.from >= minOptimizationVal ? range.from : minOptimizationVal;
                    var maxUnOptVal = range.to;
                    var minOptIdx = charCodeToOptimizedIndex(minUnOptVal);
                    var maxOptIdx = charCodeToOptimizedIndex(maxUnOptVal);
                    for (var currOptIdx = minOptIdx; currOptIdx <= maxOptIdx; currOptIdx++) {
                      result[currOptIdx] = currOptIdx;
                    }
                  }
                }
              }
            });
            break;
          case "Group":
            firstCharOptimizedIndices(atom.value, result, ignoreCase);
            break;
          /* istanbul ignore next */
          default:
            throw Error("Non Exhaustive Match");
        }
        var isOptionalQuantifier = atom.quantifier !== void 0 && atom.quantifier.atLeast === 0;
        if (
          // A group may be optional due to empty contents /(?:)/
          // or if everything inside it is optional /((a)?)/
          atom.type === "Group" && isWholeOptional(atom) === false || // If this term is not a group it may only be optional if it has an optional quantifier
          atom.type !== "Group" && isOptionalQuantifier === false
        ) {
          break;
        }
      }
      break;
    /* istanbul ignore next */
    default:
      throw Error("non exhaustive match!");
  }
  return values(result);
}
function addOptimizedIdxToResult(code, result, ignoreCase) {
  var optimizedCharIdx = charCodeToOptimizedIndex(code);
  result[optimizedCharIdx] = optimizedCharIdx;
  if (ignoreCase === true) {
    handleIgnoreCase(code, result);
  }
}
function handleIgnoreCase(code, result) {
  var char = String.fromCharCode(code);
  var upperChar = char.toUpperCase();
  if (upperChar !== char) {
    var optimizedCharIdx = charCodeToOptimizedIndex(upperChar.charCodeAt(0));
    result[optimizedCharIdx] = optimizedCharIdx;
  } else {
    var lowerChar = char.toLowerCase();
    if (lowerChar !== char) {
      var optimizedCharIdx = charCodeToOptimizedIndex(lowerChar.charCodeAt(0));
      result[optimizedCharIdx] = optimizedCharIdx;
    }
  }
}
function findCode(setNode, targetCharCodes) {
  return find(setNode.value, function(codeOrRange) {
    if (typeof codeOrRange === "number") {
      return contains(targetCharCodes, codeOrRange);
    } else {
      var range_1 = codeOrRange;
      return find(targetCharCodes, function(targetCode) {
        return range_1.from <= targetCode && targetCode <= range_1.to;
      }) !== void 0;
    }
  });
}
function isWholeOptional(ast) {
  if (ast.quantifier && ast.quantifier.atLeast === 0) {
    return true;
  }
  if (!ast.value) {
    return false;
  }
  return isArray(ast.value) ? every(ast.value, isWholeOptional) : isWholeOptional(ast.value);
}
var CharCodeFinder = (
  /** @class */
  (function(_super) {
    __extends$a(CharCodeFinder2, _super);
    function CharCodeFinder2(targetCharCodes) {
      var _this = _super.call(this) || this;
      _this.targetCharCodes = targetCharCodes;
      _this.found = false;
      return _this;
    }
    CharCodeFinder2.prototype.visitChildren = function(node) {
      if (this.found === true) {
        return;
      }
      switch (node.type) {
        case "Lookahead":
          this.visitLookahead(node);
          return;
        case "NegativeLookahead":
          this.visitNegativeLookahead(node);
          return;
      }
      _super.prototype.visitChildren.call(this, node);
    };
    CharCodeFinder2.prototype.visitCharacter = function(node) {
      if (contains(this.targetCharCodes, node.value)) {
        this.found = true;
      }
    };
    CharCodeFinder2.prototype.visitSet = function(node) {
      if (node.complement) {
        if (findCode(node, this.targetCharCodes) === void 0) {
          this.found = true;
        }
      } else {
        if (findCode(node, this.targetCharCodes) !== void 0) {
          this.found = true;
        }
      }
    };
    return CharCodeFinder2;
  })(regexpToAstExports.BaseRegExpVisitor)
);
function canMatchCharCode(charCodes, pattern) {
  if (pattern instanceof RegExp) {
    var ast = getRegExpAst(pattern);
    var charCodeFinder = new CharCodeFinder(charCodes);
    charCodeFinder.visit(ast);
    return charCodeFinder.found;
  } else {
    return find(pattern, function(char) {
      return contains(charCodes, char.charCodeAt(0));
    }) !== void 0;
  }
}
var __extends$9 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
var PATTERN = "PATTERN";
var DEFAULT_MODE = "defaultMode";
var MODES = "modes";
var SUPPORT_STICKY = typeof new RegExp("(?:)").sticky === "boolean";
function analyzeTokenTypes(tokenTypes, options) {
  options = defaults(options, {
    useSticky: SUPPORT_STICKY,
    debug: false,
    safeMode: false,
    positionTracking: "full",
    lineTerminatorCharacters: ["\r", "\n"],
    tracer: function(msg, action) {
      return action();
    }
  });
  var tracer = options.tracer;
  tracer("initCharCodeToOptimizedIndexMap", function() {
    initCharCodeToOptimizedIndexMap();
  });
  var onlyRelevantTypes;
  tracer("Reject Lexer.NA", function() {
    onlyRelevantTypes = reject(tokenTypes, function(currType) {
      return currType[PATTERN] === Lexer.NA;
    });
  });
  var hasCustom = false;
  var allTransformedPatterns;
  tracer("Transform Patterns", function() {
    hasCustom = false;
    allTransformedPatterns = map(onlyRelevantTypes, function(currType) {
      var currPattern = currType[PATTERN];
      if (isRegExp(currPattern)) {
        var regExpSource = currPattern.source;
        if (regExpSource.length === 1 && // only these regExp meta characters which can appear in a length one regExp
        regExpSource !== "^" && regExpSource !== "$" && regExpSource !== ".") {
          return regExpSource;
        } else if (regExpSource.length === 2 && regExpSource[0] === "\\" && // not a meta character
        !contains([
          "d",
          "D",
          "s",
          "S",
          "t",
          "r",
          "n",
          "t",
          "0",
          "c",
          "b",
          "B",
          "f",
          "v",
          "w",
          "W"
        ], regExpSource[1])) {
          return regExpSource[1];
        } else {
          return options.useSticky ? addStickyFlag(currPattern) : addStartOfInput(currPattern);
        }
      } else if (isFunction(currPattern)) {
        hasCustom = true;
        return { exec: currPattern };
      } else if (has(currPattern, "exec")) {
        hasCustom = true;
        return currPattern;
      } else if (typeof currPattern === "string") {
        if (currPattern.length === 1) {
          return currPattern;
        } else {
          var escapedRegExpString = currPattern.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&");
          var wrappedRegExp = new RegExp(escapedRegExpString);
          return options.useSticky ? addStickyFlag(wrappedRegExp) : addStartOfInput(wrappedRegExp);
        }
      } else {
        throw Error("non exhaustive match");
      }
    });
  });
  var patternIdxToType;
  var patternIdxToGroup;
  var patternIdxToLongerAltIdx;
  var patternIdxToPushMode;
  var patternIdxToPopMode;
  tracer("misc mapping", function() {
    patternIdxToType = map(onlyRelevantTypes, function(currType) {
      return currType.tokenTypeIdx;
    });
    patternIdxToGroup = map(onlyRelevantTypes, function(clazz) {
      var groupName = clazz.GROUP;
      if (groupName === Lexer.SKIPPED) {
        return void 0;
      } else if (isString(groupName)) {
        return groupName;
      } else if (isUndefined(groupName)) {
        return false;
      } else {
        throw Error("non exhaustive match");
      }
    });
    patternIdxToLongerAltIdx = map(onlyRelevantTypes, function(clazz) {
      var longerAltType = clazz.LONGER_ALT;
      if (longerAltType) {
        var longerAltIdx = indexOf(onlyRelevantTypes, longerAltType);
        return longerAltIdx;
      }
    });
    patternIdxToPushMode = map(onlyRelevantTypes, function(clazz) {
      return clazz.PUSH_MODE;
    });
    patternIdxToPopMode = map(onlyRelevantTypes, function(clazz) {
      return has(clazz, "POP_MODE");
    });
  });
  var patternIdxToCanLineTerminator;
  tracer("Line Terminator Handling", function() {
    var lineTerminatorCharCodes = getCharCodes(options.lineTerminatorCharacters);
    patternIdxToCanLineTerminator = map(onlyRelevantTypes, function(tokType) {
      return false;
    });
    if (options.positionTracking !== "onlyOffset") {
      patternIdxToCanLineTerminator = map(onlyRelevantTypes, function(tokType) {
        if (has(tokType, "LINE_BREAKS")) {
          return tokType.LINE_BREAKS;
        } else {
          if (checkLineBreaksIssues(tokType, lineTerminatorCharCodes) === false) {
            return canMatchCharCode(lineTerminatorCharCodes, tokType.PATTERN);
          }
        }
      });
    }
  });
  var patternIdxToIsCustom;
  var patternIdxToShort;
  var emptyGroups;
  var patternIdxToConfig;
  tracer("Misc Mapping #2", function() {
    patternIdxToIsCustom = map(onlyRelevantTypes, isCustomPattern);
    patternIdxToShort = map(allTransformedPatterns, isShortPattern);
    emptyGroups = reduce(onlyRelevantTypes, function(acc, clazz) {
      var groupName = clazz.GROUP;
      if (isString(groupName) && !(groupName === Lexer.SKIPPED)) {
        acc[groupName] = [];
      }
      return acc;
    }, {});
    patternIdxToConfig = map(allTransformedPatterns, function(x2, idx) {
      return {
        pattern: allTransformedPatterns[idx],
        longerAlt: patternIdxToLongerAltIdx[idx],
        canLineTerminator: patternIdxToCanLineTerminator[idx],
        isCustom: patternIdxToIsCustom[idx],
        short: patternIdxToShort[idx],
        group: patternIdxToGroup[idx],
        push: patternIdxToPushMode[idx],
        pop: patternIdxToPopMode[idx],
        tokenTypeIdx: patternIdxToType[idx],
        tokenType: onlyRelevantTypes[idx]
      };
    });
  });
  var canBeOptimized = true;
  var charCodeToPatternIdxToConfig = [];
  if (!options.safeMode) {
    tracer("First Char Optimization", function() {
      charCodeToPatternIdxToConfig = reduce(onlyRelevantTypes, function(result, currTokType, idx) {
        if (typeof currTokType.PATTERN === "string") {
          var charCode = currTokType.PATTERN.charCodeAt(0);
          var optimizedIdx = charCodeToOptimizedIndex(charCode);
          addToMapOfArrays(result, optimizedIdx, patternIdxToConfig[idx]);
        } else if (isArray(currTokType.START_CHARS_HINT)) {
          var lastOptimizedIdx_1;
          forEach(currTokType.START_CHARS_HINT, function(charOrInt) {
            var charCode2 = typeof charOrInt === "string" ? charOrInt.charCodeAt(0) : charOrInt;
            var currOptimizedIdx = charCodeToOptimizedIndex(charCode2);
            if (lastOptimizedIdx_1 !== currOptimizedIdx) {
              lastOptimizedIdx_1 = currOptimizedIdx;
              addToMapOfArrays(result, currOptimizedIdx, patternIdxToConfig[idx]);
            }
          });
        } else if (isRegExp(currTokType.PATTERN)) {
          if (currTokType.PATTERN.unicode) {
            canBeOptimized = false;
            if (options.ensureOptimizations) {
              PRINT_ERROR("" + failedOptimizationPrefixMsg + ("	Unable to analyze < " + currTokType.PATTERN.toString() + " > pattern.\n") + "	The regexp unicode flag is not currently supported by the regexp-to-ast library.\n	This will disable the lexer's first char optimizations.\n	For details See: https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#UNICODE_OPTIMIZE");
            }
          } else {
            var optimizedCodes = getOptimizedStartCodesIndices(currTokType.PATTERN, options.ensureOptimizations);
            if (isEmpty(optimizedCodes)) {
              canBeOptimized = false;
            }
            forEach(optimizedCodes, function(code) {
              addToMapOfArrays(result, code, patternIdxToConfig[idx]);
            });
          }
        } else {
          if (options.ensureOptimizations) {
            PRINT_ERROR("" + failedOptimizationPrefixMsg + ("	TokenType: <" + currTokType.name + "> is using a custom token pattern without providing <start_chars_hint> parameter.\n") + "	This will disable the lexer's first char optimizations.\n	For details See: https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#CUSTOM_OPTIMIZE");
          }
          canBeOptimized = false;
        }
        return result;
      }, []);
    });
  }
  tracer("ArrayPacking", function() {
    charCodeToPatternIdxToConfig = packArray(charCodeToPatternIdxToConfig);
  });
  return {
    emptyGroups,
    patternIdxToConfig,
    charCodeToPatternIdxToConfig,
    hasCustom,
    canBeOptimized
  };
}
function validatePatterns(tokenTypes, validModesNames) {
  var errors = [];
  var missingResult = findMissingPatterns(tokenTypes);
  errors = errors.concat(missingResult.errors);
  var invalidResult = findInvalidPatterns(missingResult.valid);
  var validTokenTypes = invalidResult.valid;
  errors = errors.concat(invalidResult.errors);
  errors = errors.concat(validateRegExpPattern(validTokenTypes));
  errors = errors.concat(findInvalidGroupType(validTokenTypes));
  errors = errors.concat(findModesThatDoNotExist(validTokenTypes, validModesNames));
  errors = errors.concat(findUnreachablePatterns(validTokenTypes));
  return errors;
}
function validateRegExpPattern(tokenTypes) {
  var errors = [];
  var withRegExpPatterns = filter(tokenTypes, function(currTokType) {
    return isRegExp(currTokType[PATTERN]);
  });
  errors = errors.concat(findEndOfInputAnchor(withRegExpPatterns));
  errors = errors.concat(findStartOfInputAnchor(withRegExpPatterns));
  errors = errors.concat(findUnsupportedFlags(withRegExpPatterns));
  errors = errors.concat(findDuplicatePatterns(withRegExpPatterns));
  errors = errors.concat(findEmptyMatchRegExps(withRegExpPatterns));
  return errors;
}
function findMissingPatterns(tokenTypes) {
  var tokenTypesWithMissingPattern = filter(tokenTypes, function(currType) {
    return !has(currType, PATTERN);
  });
  var errors = map(tokenTypesWithMissingPattern, function(currType) {
    return {
      message: "Token Type: ->" + currType.name + "<- missing static 'PATTERN' property",
      type: LexerDefinitionErrorType.MISSING_PATTERN,
      tokenTypes: [currType]
    };
  });
  var valid = difference(tokenTypes, tokenTypesWithMissingPattern);
  return { errors, valid };
}
function findInvalidPatterns(tokenTypes) {
  var tokenTypesWithInvalidPattern = filter(tokenTypes, function(currType) {
    var pattern = currType[PATTERN];
    return !isRegExp(pattern) && !isFunction(pattern) && !has(pattern, "exec") && !isString(pattern);
  });
  var errors = map(tokenTypesWithInvalidPattern, function(currType) {
    return {
      message: "Token Type: ->" + currType.name + "<- static 'PATTERN' can only be a RegExp, a Function matching the {CustomPatternMatcherFunc} type or an Object matching the {ICustomPattern} interface.",
      type: LexerDefinitionErrorType.INVALID_PATTERN,
      tokenTypes: [currType]
    };
  });
  var valid = difference(tokenTypes, tokenTypesWithInvalidPattern);
  return { errors, valid };
}
var end_of_input = /[^\\][\$]/;
function findEndOfInputAnchor(tokenTypes) {
  var EndAnchorFinder = (
    /** @class */
    (function(_super) {
      __extends$9(EndAnchorFinder2, _super);
      function EndAnchorFinder2() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.found = false;
        return _this;
      }
      EndAnchorFinder2.prototype.visitEndAnchor = function(node) {
        this.found = true;
      };
      return EndAnchorFinder2;
    })(regexpToAstExports.BaseRegExpVisitor)
  );
  var invalidRegex = filter(tokenTypes, function(currType) {
    var pattern = currType[PATTERN];
    try {
      var regexpAst = getRegExpAst(pattern);
      var endAnchorVisitor = new EndAnchorFinder();
      endAnchorVisitor.visit(regexpAst);
      return endAnchorVisitor.found;
    } catch (e3) {
      return end_of_input.test(pattern.source);
    }
  });
  var errors = map(invalidRegex, function(currType) {
    return {
      message: "Unexpected RegExp Anchor Error:\n	Token Type: ->" + currType.name + "<- static 'PATTERN' cannot contain end of input anchor '$'\n	See sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#ANCHORS	for details.",
      type: LexerDefinitionErrorType.EOI_ANCHOR_FOUND,
      tokenTypes: [currType]
    };
  });
  return errors;
}
function findEmptyMatchRegExps(tokenTypes) {
  var matchesEmptyString = filter(tokenTypes, function(currType) {
    var pattern = currType[PATTERN];
    return pattern.test("");
  });
  var errors = map(matchesEmptyString, function(currType) {
    return {
      message: "Token Type: ->" + currType.name + "<- static 'PATTERN' must not match an empty string",
      type: LexerDefinitionErrorType.EMPTY_MATCH_PATTERN,
      tokenTypes: [currType]
    };
  });
  return errors;
}
var start_of_input = /[^\\[][\^]|^\^/;
function findStartOfInputAnchor(tokenTypes) {
  var StartAnchorFinder = (
    /** @class */
    (function(_super) {
      __extends$9(StartAnchorFinder2, _super);
      function StartAnchorFinder2() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.found = false;
        return _this;
      }
      StartAnchorFinder2.prototype.visitStartAnchor = function(node) {
        this.found = true;
      };
      return StartAnchorFinder2;
    })(regexpToAstExports.BaseRegExpVisitor)
  );
  var invalidRegex = filter(tokenTypes, function(currType) {
    var pattern = currType[PATTERN];
    try {
      var regexpAst = getRegExpAst(pattern);
      var startAnchorVisitor = new StartAnchorFinder();
      startAnchorVisitor.visit(regexpAst);
      return startAnchorVisitor.found;
    } catch (e3) {
      return start_of_input.test(pattern.source);
    }
  });
  var errors = map(invalidRegex, function(currType) {
    return {
      message: "Unexpected RegExp Anchor Error:\n	Token Type: ->" + currType.name + "<- static 'PATTERN' cannot contain start of input anchor '^'\n	See https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#ANCHORS	for details.",
      type: LexerDefinitionErrorType.SOI_ANCHOR_FOUND,
      tokenTypes: [currType]
    };
  });
  return errors;
}
function findUnsupportedFlags(tokenTypes) {
  var invalidFlags = filter(tokenTypes, function(currType) {
    var pattern = currType[PATTERN];
    return pattern instanceof RegExp && (pattern.multiline || pattern.global);
  });
  var errors = map(invalidFlags, function(currType) {
    return {
      message: "Token Type: ->" + currType.name + "<- static 'PATTERN' may NOT contain global('g') or multiline('m')",
      type: LexerDefinitionErrorType.UNSUPPORTED_FLAGS_FOUND,
      tokenTypes: [currType]
    };
  });
  return errors;
}
function findDuplicatePatterns(tokenTypes) {
  var found = [];
  var identicalPatterns = map(tokenTypes, function(outerType) {
    return reduce(tokenTypes, function(result, innerType) {
      if (outerType.PATTERN.source === innerType.PATTERN.source && !contains(found, innerType) && innerType.PATTERN !== Lexer.NA) {
        found.push(innerType);
        result.push(innerType);
        return result;
      }
      return result;
    }, []);
  });
  identicalPatterns = compact(identicalPatterns);
  var duplicatePatterns = filter(identicalPatterns, function(currIdenticalSet) {
    return currIdenticalSet.length > 1;
  });
  var errors = map(duplicatePatterns, function(setOfIdentical) {
    var tokenTypeNames = map(setOfIdentical, function(currType) {
      return currType.name;
    });
    var dupPatternSrc = first$1(setOfIdentical).PATTERN;
    return {
      message: "The same RegExp pattern ->" + dupPatternSrc + "<-" + ("has been used in all of the following Token Types: " + tokenTypeNames.join(", ") + " <-"),
      type: LexerDefinitionErrorType.DUPLICATE_PATTERNS_FOUND,
      tokenTypes: setOfIdentical
    };
  });
  return errors;
}
function findInvalidGroupType(tokenTypes) {
  var invalidTypes = filter(tokenTypes, function(clazz) {
    if (!has(clazz, "GROUP")) {
      return false;
    }
    var group = clazz.GROUP;
    return group !== Lexer.SKIPPED && group !== Lexer.NA && !isString(group);
  });
  var errors = map(invalidTypes, function(currType) {
    return {
      message: "Token Type: ->" + currType.name + "<- static 'GROUP' can only be Lexer.SKIPPED/Lexer.NA/A String",
      type: LexerDefinitionErrorType.INVALID_GROUP_TYPE_FOUND,
      tokenTypes: [currType]
    };
  });
  return errors;
}
function findModesThatDoNotExist(tokenTypes, validModes) {
  var invalidModes = filter(tokenTypes, function(clazz) {
    return clazz.PUSH_MODE !== void 0 && !contains(validModes, clazz.PUSH_MODE);
  });
  var errors = map(invalidModes, function(tokType) {
    var msg = "Token Type: ->" + tokType.name + "<- static 'PUSH_MODE' value cannot refer to a Lexer Mode ->" + tokType.PUSH_MODE + "<-which does not exist";
    return {
      message: msg,
      type: LexerDefinitionErrorType.PUSH_MODE_DOES_NOT_EXIST,
      tokenTypes: [tokType]
    };
  });
  return errors;
}
function findUnreachablePatterns(tokenTypes) {
  var errors = [];
  var canBeTested = reduce(tokenTypes, function(result, tokType, idx) {
    var pattern = tokType.PATTERN;
    if (pattern === Lexer.NA) {
      return result;
    }
    if (isString(pattern)) {
      result.push({ str: pattern, idx, tokenType: tokType });
    } else if (isRegExp(pattern) && noMetaChar(pattern)) {
      result.push({ str: pattern.source, idx, tokenType: tokType });
    }
    return result;
  }, []);
  forEach(tokenTypes, function(tokType, testIdx) {
    forEach(canBeTested, function(_a2) {
      var str = _a2.str, idx = _a2.idx, tokenType = _a2.tokenType;
      if (testIdx < idx && testTokenType(str, tokType.PATTERN)) {
        var msg = "Token: ->" + tokenType.name + "<- can never be matched.\n" + ("Because it appears AFTER the Token Type ->" + tokType.name + "<-") + "in the lexer's definition.\nSee https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#UNREACHABLE";
        errors.push({
          message: msg,
          type: LexerDefinitionErrorType.UNREACHABLE_PATTERN,
          tokenTypes: [tokType, tokenType]
        });
      }
    });
  });
  return errors;
}
function testTokenType(str, pattern) {
  if (isRegExp(pattern)) {
    var regExpArray = pattern.exec(str);
    return regExpArray !== null && regExpArray.index === 0;
  } else if (isFunction(pattern)) {
    return pattern(str, 0, [], {});
  } else if (has(pattern, "exec")) {
    return pattern.exec(str, 0, [], {});
  } else if (typeof pattern === "string") {
    return pattern === str;
  } else {
    throw Error("non exhaustive match");
  }
}
function noMetaChar(regExp) {
  var metaChars = [
    ".",
    "\\",
    "[",
    "]",
    "|",
    "^",
    "$",
    "(",
    ")",
    "?",
    "*",
    "+",
    "{"
  ];
  return find(metaChars, function(char) {
    return regExp.source.indexOf(char) !== -1;
  }) === void 0;
}
function addStartOfInput(pattern) {
  var flags = pattern.ignoreCase ? "i" : "";
  return new RegExp("^(?:" + pattern.source + ")", flags);
}
function addStickyFlag(pattern) {
  var flags = pattern.ignoreCase ? "iy" : "y";
  return new RegExp("" + pattern.source, flags);
}
function performRuntimeChecks(lexerDefinition, trackLines, lineTerminatorCharacters) {
  var errors = [];
  if (!has(lexerDefinition, DEFAULT_MODE)) {
    errors.push({
      message: "A MultiMode Lexer cannot be initialized without a <" + DEFAULT_MODE + "> property in its definition\n",
      type: LexerDefinitionErrorType.MULTI_MODE_LEXER_WITHOUT_DEFAULT_MODE
    });
  }
  if (!has(lexerDefinition, MODES)) {
    errors.push({
      message: "A MultiMode Lexer cannot be initialized without a <" + MODES + "> property in its definition\n",
      type: LexerDefinitionErrorType.MULTI_MODE_LEXER_WITHOUT_MODES_PROPERTY
    });
  }
  if (has(lexerDefinition, MODES) && has(lexerDefinition, DEFAULT_MODE) && !has(lexerDefinition.modes, lexerDefinition.defaultMode)) {
    errors.push({
      message: "A MultiMode Lexer cannot be initialized with a " + DEFAULT_MODE + ": <" + lexerDefinition.defaultMode + ">which does not exist\n",
      type: LexerDefinitionErrorType.MULTI_MODE_LEXER_DEFAULT_MODE_VALUE_DOES_NOT_EXIST
    });
  }
  if (has(lexerDefinition, MODES)) {
    forEach(lexerDefinition.modes, function(currModeValue, currModeName) {
      forEach(currModeValue, function(currTokType, currIdx) {
        if (isUndefined(currTokType)) {
          errors.push({
            message: "A Lexer cannot be initialized using an undefined Token Type. Mode:" + ("<" + currModeName + "> at index: <" + currIdx + ">\n"),
            type: LexerDefinitionErrorType.LEXER_DEFINITION_CANNOT_CONTAIN_UNDEFINED
          });
        }
      });
    });
  }
  return errors;
}
function performWarningRuntimeChecks(lexerDefinition, trackLines, lineTerminatorCharacters) {
  var warnings = [];
  var hasAnyLineBreak = false;
  var allTokenTypes = compact(flatten(mapValues(lexerDefinition.modes, function(tokTypes) {
    return tokTypes;
  })));
  var concreteTokenTypes = reject(allTokenTypes, function(currType) {
    return currType[PATTERN] === Lexer.NA;
  });
  var terminatorCharCodes = getCharCodes(lineTerminatorCharacters);
  if (trackLines) {
    forEach(concreteTokenTypes, function(tokType) {
      var currIssue = checkLineBreaksIssues(tokType, terminatorCharCodes);
      if (currIssue !== false) {
        var message = buildLineBreakIssueMessage(tokType, currIssue);
        var warningDescriptor = {
          message,
          type: currIssue.issue,
          tokenType: tokType
        };
        warnings.push(warningDescriptor);
      } else {
        if (has(tokType, "LINE_BREAKS")) {
          if (tokType.LINE_BREAKS === true) {
            hasAnyLineBreak = true;
          }
        } else {
          if (canMatchCharCode(terminatorCharCodes, tokType.PATTERN)) {
            hasAnyLineBreak = true;
          }
        }
      }
    });
  }
  if (trackLines && !hasAnyLineBreak) {
    warnings.push({
      message: "Warning: No LINE_BREAKS Found.\n	This Lexer has been defined to track line and column information,\n	But none of the Token Types can be identified as matching a line terminator.\n	See https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#LINE_BREAKS \n	for details.",
      type: LexerDefinitionErrorType.NO_LINE_BREAKS_FLAGS
    });
  }
  return warnings;
}
function cloneEmptyGroups(emptyGroups) {
  var clonedResult = {};
  var groupKeys = keys(emptyGroups);
  forEach(groupKeys, function(currKey) {
    var currGroupValue = emptyGroups[currKey];
    if (isArray(currGroupValue)) {
      clonedResult[currKey] = [];
    } else {
      throw Error("non exhaustive match");
    }
  });
  return clonedResult;
}
function isCustomPattern(tokenType) {
  var pattern = tokenType.PATTERN;
  if (isRegExp(pattern)) {
    return false;
  } else if (isFunction(pattern)) {
    return true;
  } else if (has(pattern, "exec")) {
    return true;
  } else if (isString(pattern)) {
    return false;
  } else {
    throw Error("non exhaustive match");
  }
}
function isShortPattern(pattern) {
  if (isString(pattern) && pattern.length === 1) {
    return pattern.charCodeAt(0);
  } else {
    return false;
  }
}
var LineTerminatorOptimizedTester = {
  // implements /\n|\r\n?/g.test
  test: function(text) {
    var len = text.length;
    for (var i2 = this.lastIndex; i2 < len; i2++) {
      var c2 = text.charCodeAt(i2);
      if (c2 === 10) {
        this.lastIndex = i2 + 1;
        return true;
      } else if (c2 === 13) {
        if (text.charCodeAt(i2 + 1) === 10) {
          this.lastIndex = i2 + 2;
        } else {
          this.lastIndex = i2 + 1;
        }
        return true;
      }
    }
    return false;
  },
  lastIndex: 0
};
function checkLineBreaksIssues(tokType, lineTerminatorCharCodes) {
  if (has(tokType, "LINE_BREAKS")) {
    return false;
  } else {
    if (isRegExp(tokType.PATTERN)) {
      try {
        canMatchCharCode(lineTerminatorCharCodes, tokType.PATTERN);
      } catch (e3) {
        return {
          issue: LexerDefinitionErrorType.IDENTIFY_TERMINATOR,
          errMsg: e3.message
        };
      }
      return false;
    } else if (isString(tokType.PATTERN)) {
      return false;
    } else if (isCustomPattern(tokType)) {
      return { issue: LexerDefinitionErrorType.CUSTOM_LINE_BREAK };
    } else {
      throw Error("non exhaustive match");
    }
  }
}
function buildLineBreakIssueMessage(tokType, details) {
  if (details.issue === LexerDefinitionErrorType.IDENTIFY_TERMINATOR) {
    return "Warning: unable to identify line terminator usage in pattern.\n" + ("	The problem is in the <" + tokType.name + "> Token Type\n") + ("	 Root cause: " + details.errMsg + ".\n") + "	For details See: https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#IDENTIFY_TERMINATOR";
  } else if (details.issue === LexerDefinitionErrorType.CUSTOM_LINE_BREAK) {
    return "Warning: A Custom Token Pattern should specify the <line_breaks> option.\n" + ("	The problem is in the <" + tokType.name + "> Token Type\n") + "	For details See: https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#CUSTOM_LINE_BREAK";
  } else {
    throw Error("non exhaustive match");
  }
}
function getCharCodes(charsOrCodes) {
  var charCodes = map(charsOrCodes, function(numOrString) {
    if (isString(numOrString) && numOrString.length > 0) {
      return numOrString.charCodeAt(0);
    } else {
      return numOrString;
    }
  });
  return charCodes;
}
function addToMapOfArrays(map2, key, value) {
  if (map2[key] === void 0) {
    map2[key] = [value];
  } else {
    map2[key].push(value);
  }
}
var minOptimizationVal = 256;
function charCodeToOptimizedIndex(charCode) {
  return charCode < minOptimizationVal ? charCode : charCodeToOptimizedIdxMap[charCode];
}
var charCodeToOptimizedIdxMap = [];
function initCharCodeToOptimizedIndexMap() {
  if (isEmpty(charCodeToOptimizedIdxMap)) {
    charCodeToOptimizedIdxMap = new Array(65536);
    for (var i2 = 0; i2 < 65536; i2++) {
      charCodeToOptimizedIdxMap[i2] = i2 > 255 ? 255 + ~~(i2 / 255) : i2;
    }
  }
}
function tokenStructuredMatcher(tokInstance, tokConstructor) {
  var instanceType = tokInstance.tokenTypeIdx;
  if (instanceType === tokConstructor.tokenTypeIdx) {
    return true;
  } else {
    return tokConstructor.isParent === true && tokConstructor.categoryMatchesMap[instanceType] === true;
  }
}
function tokenStructuredMatcherNoCategories(token, tokType) {
  return token.tokenTypeIdx === tokType.tokenTypeIdx;
}
var tokenShortNameIdx = 1;
var tokenIdxToClass = {};
function augmentTokenTypes(tokenTypes) {
  var tokenTypesAndParents = expandCategories(tokenTypes);
  assignTokenDefaultProps(tokenTypesAndParents);
  assignCategoriesMapProp(tokenTypesAndParents);
  assignCategoriesTokensProp(tokenTypesAndParents);
  forEach(tokenTypesAndParents, function(tokType) {
    tokType.isParent = tokType.categoryMatches.length > 0;
  });
}
function expandCategories(tokenTypes) {
  var result = cloneArr(tokenTypes);
  var categories = tokenTypes;
  var searching = true;
  while (searching) {
    categories = compact(flatten(map(categories, function(currTokType) {
      return currTokType.CATEGORIES;
    })));
    var newCategories = difference(categories, result);
    result = result.concat(newCategories);
    if (isEmpty(newCategories)) {
      searching = false;
    } else {
      categories = newCategories;
    }
  }
  return result;
}
function assignTokenDefaultProps(tokenTypes) {
  forEach(tokenTypes, function(currTokType) {
    if (!hasShortKeyProperty(currTokType)) {
      tokenIdxToClass[tokenShortNameIdx] = currTokType;
      currTokType.tokenTypeIdx = tokenShortNameIdx++;
    }
    if (hasCategoriesProperty(currTokType) && !isArray(currTokType.CATEGORIES)) {
      currTokType.CATEGORIES = [currTokType.CATEGORIES];
    }
    if (!hasCategoriesProperty(currTokType)) {
      currTokType.CATEGORIES = [];
    }
    if (!hasExtendingTokensTypesProperty(currTokType)) {
      currTokType.categoryMatches = [];
    }
    if (!hasExtendingTokensTypesMapProperty(currTokType)) {
      currTokType.categoryMatchesMap = {};
    }
  });
}
function assignCategoriesTokensProp(tokenTypes) {
  forEach(tokenTypes, function(currTokType) {
    currTokType.categoryMatches = [];
    forEach(currTokType.categoryMatchesMap, function(val, key) {
      currTokType.categoryMatches.push(tokenIdxToClass[key].tokenTypeIdx);
    });
  });
}
function assignCategoriesMapProp(tokenTypes) {
  forEach(tokenTypes, function(currTokType) {
    singleAssignCategoriesToksMap([], currTokType);
  });
}
function singleAssignCategoriesToksMap(path, nextNode) {
  forEach(path, function(pathNode) {
    nextNode.categoryMatchesMap[pathNode.tokenTypeIdx] = true;
  });
  forEach(nextNode.CATEGORIES, function(nextCategory) {
    var newPath = path.concat(nextNode);
    if (!contains(newPath, nextCategory)) {
      singleAssignCategoriesToksMap(newPath, nextCategory);
    }
  });
}
function hasShortKeyProperty(tokType) {
  return has(tokType, "tokenTypeIdx");
}
function hasCategoriesProperty(tokType) {
  return has(tokType, "CATEGORIES");
}
function hasExtendingTokensTypesProperty(tokType) {
  return has(tokType, "categoryMatches");
}
function hasExtendingTokensTypesMapProperty(tokType) {
  return has(tokType, "categoryMatchesMap");
}
function isTokenType(tokType) {
  return has(tokType, "tokenTypeIdx");
}
var defaultLexerErrorProvider = {
  buildUnableToPopLexerModeMessage: function(token) {
    return "Unable to pop Lexer Mode after encountering Token ->" + token.image + "<- The Mode Stack is empty";
  },
  buildUnexpectedCharactersMessage: function(fullText, startOffset, length, line, column) {
    return "unexpected character: ->" + fullText.charAt(startOffset) + "<- at offset: " + startOffset + "," + (" skipped " + length + " characters.");
  }
};
var LexerDefinitionErrorType;
(function(LexerDefinitionErrorType2) {
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["MISSING_PATTERN"] = 0] = "MISSING_PATTERN";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["INVALID_PATTERN"] = 1] = "INVALID_PATTERN";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["EOI_ANCHOR_FOUND"] = 2] = "EOI_ANCHOR_FOUND";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["UNSUPPORTED_FLAGS_FOUND"] = 3] = "UNSUPPORTED_FLAGS_FOUND";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["DUPLICATE_PATTERNS_FOUND"] = 4] = "DUPLICATE_PATTERNS_FOUND";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["INVALID_GROUP_TYPE_FOUND"] = 5] = "INVALID_GROUP_TYPE_FOUND";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["PUSH_MODE_DOES_NOT_EXIST"] = 6] = "PUSH_MODE_DOES_NOT_EXIST";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["MULTI_MODE_LEXER_WITHOUT_DEFAULT_MODE"] = 7] = "MULTI_MODE_LEXER_WITHOUT_DEFAULT_MODE";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["MULTI_MODE_LEXER_WITHOUT_MODES_PROPERTY"] = 8] = "MULTI_MODE_LEXER_WITHOUT_MODES_PROPERTY";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["MULTI_MODE_LEXER_DEFAULT_MODE_VALUE_DOES_NOT_EXIST"] = 9] = "MULTI_MODE_LEXER_DEFAULT_MODE_VALUE_DOES_NOT_EXIST";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["LEXER_DEFINITION_CANNOT_CONTAIN_UNDEFINED"] = 10] = "LEXER_DEFINITION_CANNOT_CONTAIN_UNDEFINED";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["SOI_ANCHOR_FOUND"] = 11] = "SOI_ANCHOR_FOUND";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["EMPTY_MATCH_PATTERN"] = 12] = "EMPTY_MATCH_PATTERN";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["NO_LINE_BREAKS_FLAGS"] = 13] = "NO_LINE_BREAKS_FLAGS";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["UNREACHABLE_PATTERN"] = 14] = "UNREACHABLE_PATTERN";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["IDENTIFY_TERMINATOR"] = 15] = "IDENTIFY_TERMINATOR";
  LexerDefinitionErrorType2[LexerDefinitionErrorType2["CUSTOM_LINE_BREAK"] = 16] = "CUSTOM_LINE_BREAK";
})(LexerDefinitionErrorType || (LexerDefinitionErrorType = {}));
var DEFAULT_LEXER_CONFIG = {
  deferDefinitionErrorsHandling: false,
  positionTracking: "full",
  lineTerminatorsPattern: /\n|\r\n?/g,
  lineTerminatorCharacters: ["\n", "\r"],
  ensureOptimizations: false,
  safeMode: false,
  errorMessageProvider: defaultLexerErrorProvider,
  traceInitPerf: false,
  skipValidations: false
};
Object.freeze(DEFAULT_LEXER_CONFIG);
var Lexer = (
  /** @class */
  (function() {
    function Lexer2(lexerDefinition, config) {
      var _this = this;
      if (config === void 0) {
        config = DEFAULT_LEXER_CONFIG;
      }
      this.lexerDefinition = lexerDefinition;
      this.lexerDefinitionErrors = [];
      this.lexerDefinitionWarning = [];
      this.patternIdxToConfig = {};
      this.charCodeToPatternIdxToConfig = {};
      this.modes = [];
      this.emptyGroups = {};
      this.config = void 0;
      this.trackStartLines = true;
      this.trackEndLines = true;
      this.hasCustom = false;
      this.canModeBeOptimized = {};
      if (typeof config === "boolean") {
        throw Error("The second argument to the Lexer constructor is now an ILexerConfig Object.\na boolean 2nd argument is no longer supported");
      }
      this.config = merge(DEFAULT_LEXER_CONFIG, config);
      var traceInitVal = this.config.traceInitPerf;
      if (traceInitVal === true) {
        this.traceInitMaxIdent = Infinity;
        this.traceInitPerf = true;
      } else if (typeof traceInitVal === "number") {
        this.traceInitMaxIdent = traceInitVal;
        this.traceInitPerf = true;
      }
      this.traceInitIndent = -1;
      this.TRACE_INIT("Lexer Constructor", function() {
        var actualDefinition;
        var hasOnlySingleMode = true;
        _this.TRACE_INIT("Lexer Config handling", function() {
          if (_this.config.lineTerminatorsPattern === DEFAULT_LEXER_CONFIG.lineTerminatorsPattern) {
            _this.config.lineTerminatorsPattern = LineTerminatorOptimizedTester;
          } else {
            if (_this.config.lineTerminatorCharacters === DEFAULT_LEXER_CONFIG.lineTerminatorCharacters) {
              throw Error("Error: Missing <lineTerminatorCharacters> property on the Lexer config.\n	For details See: https://sap.github.io/chevrotain/docs/guide/resolving_lexer_errors.html#MISSING_LINE_TERM_CHARS");
            }
          }
          if (config.safeMode && config.ensureOptimizations) {
            throw Error('"safeMode" and "ensureOptimizations" flags are mutually exclusive.');
          }
          _this.trackStartLines = /full|onlyStart/i.test(_this.config.positionTracking);
          _this.trackEndLines = /full/i.test(_this.config.positionTracking);
          if (isArray(lexerDefinition)) {
            actualDefinition = { modes: {} };
            actualDefinition.modes[DEFAULT_MODE] = cloneArr(lexerDefinition);
            actualDefinition[DEFAULT_MODE] = DEFAULT_MODE;
          } else {
            hasOnlySingleMode = false;
            actualDefinition = cloneObj(lexerDefinition);
          }
        });
        if (_this.config.skipValidations === false) {
          _this.TRACE_INIT("performRuntimeChecks", function() {
            _this.lexerDefinitionErrors = _this.lexerDefinitionErrors.concat(performRuntimeChecks(actualDefinition, _this.trackStartLines, _this.config.lineTerminatorCharacters));
          });
          _this.TRACE_INIT("performWarningRuntimeChecks", function() {
            _this.lexerDefinitionWarning = _this.lexerDefinitionWarning.concat(performWarningRuntimeChecks(actualDefinition, _this.trackStartLines, _this.config.lineTerminatorCharacters));
          });
        }
        actualDefinition.modes = actualDefinition.modes ? actualDefinition.modes : {};
        forEach(actualDefinition.modes, function(currModeValue, currModeName) {
          actualDefinition.modes[currModeName] = reject(currModeValue, function(currTokType) {
            return isUndefined(currTokType);
          });
        });
        var allModeNames = keys(actualDefinition.modes);
        forEach(actualDefinition.modes, function(currModDef, currModName) {
          _this.TRACE_INIT("Mode: <" + currModName + "> processing", function() {
            _this.modes.push(currModName);
            if (_this.config.skipValidations === false) {
              _this.TRACE_INIT("validatePatterns", function() {
                _this.lexerDefinitionErrors = _this.lexerDefinitionErrors.concat(validatePatterns(currModDef, allModeNames));
              });
            }
            if (isEmpty(_this.lexerDefinitionErrors)) {
              augmentTokenTypes(currModDef);
              var currAnalyzeResult_1;
              _this.TRACE_INIT("analyzeTokenTypes", function() {
                currAnalyzeResult_1 = analyzeTokenTypes(currModDef, {
                  lineTerminatorCharacters: _this.config.lineTerminatorCharacters,
                  positionTracking: config.positionTracking,
                  ensureOptimizations: config.ensureOptimizations,
                  safeMode: config.safeMode,
                  tracer: _this.TRACE_INIT.bind(_this)
                });
              });
              _this.patternIdxToConfig[currModName] = currAnalyzeResult_1.patternIdxToConfig;
              _this.charCodeToPatternIdxToConfig[currModName] = currAnalyzeResult_1.charCodeToPatternIdxToConfig;
              _this.emptyGroups = merge(_this.emptyGroups, currAnalyzeResult_1.emptyGroups);
              _this.hasCustom = currAnalyzeResult_1.hasCustom || _this.hasCustom;
              _this.canModeBeOptimized[currModName] = currAnalyzeResult_1.canBeOptimized;
            }
          });
        });
        _this.defaultMode = actualDefinition.defaultMode;
        if (!isEmpty(_this.lexerDefinitionErrors) && !_this.config.deferDefinitionErrorsHandling) {
          var allErrMessages = map(_this.lexerDefinitionErrors, function(error) {
            return error.message;
          });
          var allErrMessagesString = allErrMessages.join("-----------------------\n");
          throw new Error("Errors detected in definition of Lexer:\n" + allErrMessagesString);
        }
        forEach(_this.lexerDefinitionWarning, function(warningDescriptor) {
          PRINT_WARNING(warningDescriptor.message);
        });
        _this.TRACE_INIT("Choosing sub-methods implementations", function() {
          if (SUPPORT_STICKY) {
            _this.chopInput = IDENTITY;
            _this.match = _this.matchWithTest;
          } else {
            _this.updateLastIndex = NOOP;
            _this.match = _this.matchWithExec;
          }
          if (hasOnlySingleMode) {
            _this.handleModes = NOOP;
          }
          if (_this.trackStartLines === false) {
            _this.computeNewColumn = IDENTITY;
          }
          if (_this.trackEndLines === false) {
            _this.updateTokenEndLineColumnLocation = NOOP;
          }
          if (/full/i.test(_this.config.positionTracking)) {
            _this.createTokenInstance = _this.createFullToken;
          } else if (/onlyStart/i.test(_this.config.positionTracking)) {
            _this.createTokenInstance = _this.createStartOnlyToken;
          } else if (/onlyOffset/i.test(_this.config.positionTracking)) {
            _this.createTokenInstance = _this.createOffsetOnlyToken;
          } else {
            throw Error('Invalid <positionTracking> config option: "' + _this.config.positionTracking + '"');
          }
          if (_this.hasCustom) {
            _this.addToken = _this.addTokenUsingPush;
            _this.handlePayload = _this.handlePayloadWithCustom;
          } else {
            _this.addToken = _this.addTokenUsingMemberAccess;
            _this.handlePayload = _this.handlePayloadNoCustom;
          }
        });
        _this.TRACE_INIT("Failed Optimization Warnings", function() {
          var unOptimizedModes = reduce(_this.canModeBeOptimized, function(cannotBeOptimized, canBeOptimized, modeName) {
            if (canBeOptimized === false) {
              cannotBeOptimized.push(modeName);
            }
            return cannotBeOptimized;
          }, []);
          if (config.ensureOptimizations && !isEmpty(unOptimizedModes)) {
            throw Error("Lexer Modes: < " + unOptimizedModes.join(", ") + ' > cannot be optimized.\n	 Disable the "ensureOptimizations" lexer config flag to silently ignore this and run the lexer in an un-optimized mode.\n	 Or inspect the console log for details on how to resolve these issues.');
          }
        });
        _this.TRACE_INIT("clearRegExpParserCache", function() {
          clearRegExpParserCache();
        });
        _this.TRACE_INIT("toFastProperties", function() {
          toFastProperties(_this);
        });
      });
    }
    Lexer2.prototype.tokenize = function(text, initialMode) {
      if (initialMode === void 0) {
        initialMode = this.defaultMode;
      }
      if (!isEmpty(this.lexerDefinitionErrors)) {
        var allErrMessages = map(this.lexerDefinitionErrors, function(error) {
          return error.message;
        });
        var allErrMessagesString = allErrMessages.join("-----------------------\n");
        throw new Error("Unable to Tokenize because Errors detected in definition of Lexer:\n" + allErrMessagesString);
      }
      var lexResult = this.tokenizeInternal(text, initialMode);
      return lexResult;
    };
    Lexer2.prototype.tokenizeInternal = function(text, initialMode) {
      var _this = this;
      var i2, j2, matchAltImage, longerAltIdx, matchedImage, payload, altPayload, imageLength, group, tokType, newToken, errLength, msg, match;
      var orgText = text;
      var orgLength = orgText.length;
      var offset = 0;
      var matchedTokensIndex = 0;
      var guessedNumberOfTokens = this.hasCustom ? 0 : Math.floor(text.length / 10);
      var matchedTokens = new Array(guessedNumberOfTokens);
      var errors = [];
      var line = this.trackStartLines ? 1 : void 0;
      var column = this.trackStartLines ? 1 : void 0;
      var groups = cloneEmptyGroups(this.emptyGroups);
      var trackLines = this.trackStartLines;
      var lineTerminatorPattern = this.config.lineTerminatorsPattern;
      var currModePatternsLength = 0;
      var patternIdxToConfig = [];
      var currCharCodeToPatternIdxToConfig = [];
      var modeStack = [];
      var emptyArray = [];
      Object.freeze(emptyArray);
      var getPossiblePatterns = void 0;
      function getPossiblePatternsSlow() {
        return patternIdxToConfig;
      }
      function getPossiblePatternsOptimized(charCode) {
        var optimizedCharIdx = charCodeToOptimizedIndex(charCode);
        var possiblePatterns = currCharCodeToPatternIdxToConfig[optimizedCharIdx];
        if (possiblePatterns === void 0) {
          return emptyArray;
        } else {
          return possiblePatterns;
        }
      }
      var pop_mode = function(popToken) {
        if (modeStack.length === 1 && // if we have both a POP_MODE and a PUSH_MODE this is in-fact a "transition"
        // So no error should occur.
        popToken.tokenType.PUSH_MODE === void 0) {
          var msg_1 = _this.config.errorMessageProvider.buildUnableToPopLexerModeMessage(popToken);
          errors.push({
            offset: popToken.startOffset,
            line: popToken.startLine !== void 0 ? popToken.startLine : void 0,
            column: popToken.startColumn !== void 0 ? popToken.startColumn : void 0,
            length: popToken.image.length,
            message: msg_1
          });
        } else {
          modeStack.pop();
          var newMode = last(modeStack);
          patternIdxToConfig = _this.patternIdxToConfig[newMode];
          currCharCodeToPatternIdxToConfig = _this.charCodeToPatternIdxToConfig[newMode];
          currModePatternsLength = patternIdxToConfig.length;
          var modeCanBeOptimized = _this.canModeBeOptimized[newMode] && _this.config.safeMode === false;
          if (currCharCodeToPatternIdxToConfig && modeCanBeOptimized) {
            getPossiblePatterns = getPossiblePatternsOptimized;
          } else {
            getPossiblePatterns = getPossiblePatternsSlow;
          }
        }
      };
      function push_mode(newMode) {
        modeStack.push(newMode);
        currCharCodeToPatternIdxToConfig = this.charCodeToPatternIdxToConfig[newMode];
        patternIdxToConfig = this.patternIdxToConfig[newMode];
        currModePatternsLength = patternIdxToConfig.length;
        currModePatternsLength = patternIdxToConfig.length;
        var modeCanBeOptimized = this.canModeBeOptimized[newMode] && this.config.safeMode === false;
        if (currCharCodeToPatternIdxToConfig && modeCanBeOptimized) {
          getPossiblePatterns = getPossiblePatternsOptimized;
        } else {
          getPossiblePatterns = getPossiblePatternsSlow;
        }
      }
      push_mode.call(this, initialMode);
      var currConfig;
      while (offset < orgLength) {
        matchedImage = null;
        var nextCharCode = orgText.charCodeAt(offset);
        var chosenPatternIdxToConfig = getPossiblePatterns(nextCharCode);
        var chosenPatternsLength = chosenPatternIdxToConfig.length;
        for (i2 = 0; i2 < chosenPatternsLength; i2++) {
          currConfig = chosenPatternIdxToConfig[i2];
          var currPattern = currConfig.pattern;
          payload = null;
          var singleCharCode = currConfig.short;
          if (singleCharCode !== false) {
            if (nextCharCode === singleCharCode) {
              matchedImage = currPattern;
            }
          } else if (currConfig.isCustom === true) {
            match = currPattern.exec(orgText, offset, matchedTokens, groups);
            if (match !== null) {
              matchedImage = match[0];
              if (match.payload !== void 0) {
                payload = match.payload;
              }
            } else {
              matchedImage = null;
            }
          } else {
            this.updateLastIndex(currPattern, offset);
            matchedImage = this.match(currPattern, text, offset);
          }
          if (matchedImage !== null) {
            longerAltIdx = currConfig.longerAlt;
            if (longerAltIdx !== void 0) {
              var longerAltConfig = patternIdxToConfig[longerAltIdx];
              var longerAltPattern = longerAltConfig.pattern;
              altPayload = null;
              if (longerAltConfig.isCustom === true) {
                match = longerAltPattern.exec(orgText, offset, matchedTokens, groups);
                if (match !== null) {
                  matchAltImage = match[0];
                  if (match.payload !== void 0) {
                    altPayload = match.payload;
                  }
                } else {
                  matchAltImage = null;
                }
              } else {
                this.updateLastIndex(longerAltPattern, offset);
                matchAltImage = this.match(longerAltPattern, text, offset);
              }
              if (matchAltImage && matchAltImage.length > matchedImage.length) {
                matchedImage = matchAltImage;
                payload = altPayload;
                currConfig = longerAltConfig;
              }
            }
            break;
          }
        }
        if (matchedImage !== null) {
          imageLength = matchedImage.length;
          group = currConfig.group;
          if (group !== void 0) {
            tokType = currConfig.tokenTypeIdx;
            newToken = this.createTokenInstance(matchedImage, offset, tokType, currConfig.tokenType, line, column, imageLength);
            this.handlePayload(newToken, payload);
            if (group === false) {
              matchedTokensIndex = this.addToken(matchedTokens, matchedTokensIndex, newToken);
            } else {
              groups[group].push(newToken);
            }
          }
          text = this.chopInput(text, imageLength);
          offset = offset + imageLength;
          column = this.computeNewColumn(column, imageLength);
          if (trackLines === true && currConfig.canLineTerminator === true) {
            var numOfLTsInMatch = 0;
            var foundTerminator = void 0;
            var lastLTEndOffset = void 0;
            lineTerminatorPattern.lastIndex = 0;
            do {
              foundTerminator = lineTerminatorPattern.test(matchedImage);
              if (foundTerminator === true) {
                lastLTEndOffset = lineTerminatorPattern.lastIndex - 1;
                numOfLTsInMatch++;
              }
            } while (foundTerminator === true);
            if (numOfLTsInMatch !== 0) {
              line = line + numOfLTsInMatch;
              column = imageLength - lastLTEndOffset;
              this.updateTokenEndLineColumnLocation(newToken, group, lastLTEndOffset, numOfLTsInMatch, line, column, imageLength);
            }
          }
          this.handleModes(currConfig, pop_mode, push_mode, newToken);
        } else {
          var errorStartOffset = offset;
          var errorLine = line;
          var errorColumn = column;
          var foundResyncPoint = false;
          while (!foundResyncPoint && offset < orgLength) {
            orgText.charCodeAt(offset);
            text = this.chopInput(text, 1);
            offset++;
            for (j2 = 0; j2 < currModePatternsLength; j2++) {
              var currConfig_1 = patternIdxToConfig[j2];
              var currPattern = currConfig_1.pattern;
              var singleCharCode = currConfig_1.short;
              if (singleCharCode !== false) {
                if (orgText.charCodeAt(offset) === singleCharCode) {
                  foundResyncPoint = true;
                }
              } else if (currConfig_1.isCustom === true) {
                foundResyncPoint = currPattern.exec(orgText, offset, matchedTokens, groups) !== null;
              } else {
                this.updateLastIndex(currPattern, offset);
                foundResyncPoint = currPattern.exec(text) !== null;
              }
              if (foundResyncPoint === true) {
                break;
              }
            }
          }
          errLength = offset - errorStartOffset;
          msg = this.config.errorMessageProvider.buildUnexpectedCharactersMessage(orgText, errorStartOffset, errLength, errorLine, errorColumn);
          errors.push({
            offset: errorStartOffset,
            line: errorLine,
            column: errorColumn,
            length: errLength,
            message: msg
          });
        }
      }
      if (!this.hasCustom) {
        matchedTokens.length = matchedTokensIndex;
      }
      return {
        tokens: matchedTokens,
        groups,
        errors
      };
    };
    Lexer2.prototype.handleModes = function(config, pop_mode, push_mode, newToken) {
      if (config.pop === true) {
        var pushMode = config.push;
        pop_mode(newToken);
        if (pushMode !== void 0) {
          push_mode.call(this, pushMode);
        }
      } else if (config.push !== void 0) {
        push_mode.call(this, config.push);
      }
    };
    Lexer2.prototype.chopInput = function(text, length) {
      return text.substring(length);
    };
    Lexer2.prototype.updateLastIndex = function(regExp, newLastIndex) {
      regExp.lastIndex = newLastIndex;
    };
    Lexer2.prototype.updateTokenEndLineColumnLocation = function(newToken, group, lastLTIdx, numOfLTsInMatch, line, column, imageLength) {
      var lastCharIsLT, fixForEndingInLT;
      if (group !== void 0) {
        lastCharIsLT = lastLTIdx === imageLength - 1;
        fixForEndingInLT = lastCharIsLT ? -1 : 0;
        if (!(numOfLTsInMatch === 1 && lastCharIsLT === true)) {
          newToken.endLine = line + fixForEndingInLT;
          newToken.endColumn = column - 1 + -fixForEndingInLT;
        }
      }
    };
    Lexer2.prototype.computeNewColumn = function(oldColumn, imageLength) {
      return oldColumn + imageLength;
    };
    Lexer2.prototype.createTokenInstance = function() {
      return null;
    };
    Lexer2.prototype.createOffsetOnlyToken = function(image, startOffset, tokenTypeIdx, tokenType) {
      return {
        image,
        startOffset,
        tokenTypeIdx,
        tokenType
      };
    };
    Lexer2.prototype.createStartOnlyToken = function(image, startOffset, tokenTypeIdx, tokenType, startLine, startColumn) {
      return {
        image,
        startOffset,
        startLine,
        startColumn,
        tokenTypeIdx,
        tokenType
      };
    };
    Lexer2.prototype.createFullToken = function(image, startOffset, tokenTypeIdx, tokenType, startLine, startColumn, imageLength) {
      return {
        image,
        startOffset,
        endOffset: startOffset + imageLength - 1,
        startLine,
        endLine: startLine,
        startColumn,
        endColumn: startColumn + imageLength - 1,
        tokenTypeIdx,
        tokenType
      };
    };
    Lexer2.prototype.addToken = function(tokenVector, index2, tokenToAdd) {
      return 666;
    };
    Lexer2.prototype.addTokenUsingPush = function(tokenVector, index2, tokenToAdd) {
      tokenVector.push(tokenToAdd);
      return index2;
    };
    Lexer2.prototype.addTokenUsingMemberAccess = function(tokenVector, index2, tokenToAdd) {
      tokenVector[index2] = tokenToAdd;
      index2++;
      return index2;
    };
    Lexer2.prototype.handlePayload = function(token, payload) {
    };
    Lexer2.prototype.handlePayloadNoCustom = function(token, payload) {
    };
    Lexer2.prototype.handlePayloadWithCustom = function(token, payload) {
      if (payload !== null) {
        token.payload = payload;
      }
    };
    Lexer2.prototype.match = function(pattern, text, offset) {
      return null;
    };
    Lexer2.prototype.matchWithTest = function(pattern, text, offset) {
      var found = pattern.test(text);
      if (found === true) {
        return text.substring(offset, pattern.lastIndex);
      }
      return null;
    };
    Lexer2.prototype.matchWithExec = function(pattern, text) {
      var regExpArray = pattern.exec(text);
      return regExpArray !== null ? regExpArray[0] : regExpArray;
    };
    Lexer2.prototype.TRACE_INIT = function(phaseDesc, phaseImpl) {
      if (this.traceInitPerf === true) {
        this.traceInitIndent++;
        var indent = new Array(this.traceInitIndent + 1).join("	");
        if (this.traceInitIndent < this.traceInitMaxIdent) {
          console.log(indent + "--> <" + phaseDesc + ">");
        }
        var _a2 = timer(phaseImpl), time = _a2.time, value = _a2.value;
        var traceMethod = time > 10 ? console.warn : console.log;
        if (this.traceInitIndent < this.traceInitMaxIdent) {
          traceMethod(indent + "<-- <" + phaseDesc + "> time: " + time + "ms");
        }
        this.traceInitIndent--;
        return value;
      } else {
        return phaseImpl();
      }
    };
    Lexer2.SKIPPED = "This marks a skipped Token pattern, this means each token identified by it willbe consumed and then thrown into oblivion, this can be used to for example to completely ignore whitespace.";
    Lexer2.NA = /NOT_APPLICABLE/;
    return Lexer2;
  })()
);
function tokenLabel(tokType) {
  if (hasTokenLabel(tokType)) {
    return tokType.LABEL;
  } else {
    return tokType.name;
  }
}
function hasTokenLabel(obj) {
  return isString(obj.LABEL) && obj.LABEL !== "";
}
var PARENT = "parent";
var CATEGORIES = "categories";
var LABEL = "label";
var GROUP = "group";
var PUSH_MODE = "push_mode";
var POP_MODE = "pop_mode";
var LONGER_ALT = "longer_alt";
var LINE_BREAKS = "line_breaks";
var START_CHARS_HINT = "start_chars_hint";
function createToken(config) {
  return createTokenInternal(config);
}
function createTokenInternal(config) {
  var pattern = config.pattern;
  var tokenType = {};
  tokenType.name = config.name;
  if (!isUndefined(pattern)) {
    tokenType.PATTERN = pattern;
  }
  if (has(config, PARENT)) {
    throw "The parent property is no longer supported.\nSee: https://github.com/SAP/chevrotain/issues/564#issuecomment-349062346 for details.";
  }
  if (has(config, CATEGORIES)) {
    tokenType.CATEGORIES = config[CATEGORIES];
  }
  augmentTokenTypes([tokenType]);
  if (has(config, LABEL)) {
    tokenType.LABEL = config[LABEL];
  }
  if (has(config, GROUP)) {
    tokenType.GROUP = config[GROUP];
  }
  if (has(config, POP_MODE)) {
    tokenType.POP_MODE = config[POP_MODE];
  }
  if (has(config, PUSH_MODE)) {
    tokenType.PUSH_MODE = config[PUSH_MODE];
  }
  if (has(config, LONGER_ALT)) {
    tokenType.LONGER_ALT = config[LONGER_ALT];
  }
  if (has(config, LINE_BREAKS)) {
    tokenType.LINE_BREAKS = config[LINE_BREAKS];
  }
  if (has(config, START_CHARS_HINT)) {
    tokenType.START_CHARS_HINT = config[START_CHARS_HINT];
  }
  return tokenType;
}
var EOF = createToken({ name: "EOF", pattern: Lexer.NA });
augmentTokenTypes([EOF]);
function createTokenInstance(tokType, image, startOffset, endOffset, startLine, endLine, startColumn, endColumn) {
  return {
    image,
    startOffset,
    endOffset,
    startLine,
    endLine,
    startColumn,
    endColumn,
    tokenTypeIdx: tokType.tokenTypeIdx,
    tokenType: tokType
  };
}
function tokenMatcher(token, tokType) {
  return tokenStructuredMatcher(token, tokType);
}
var __extends$8 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
var AbstractProduction = (
  /** @class */
  (function() {
    function AbstractProduction2(definition) {
      this.definition = definition;
    }
    AbstractProduction2.prototype.accept = function(visitor) {
      visitor.visit(this);
      forEach(this.definition, function(prod) {
        prod.accept(visitor);
      });
    };
    return AbstractProduction2;
  })()
);
var NonTerminal = (
  /** @class */
  (function(_super) {
    __extends$8(NonTerminal2, _super);
    function NonTerminal2(options) {
      var _this = _super.call(this, []) || this;
      _this.idx = 1;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    Object.defineProperty(NonTerminal2.prototype, "definition", {
      get: function() {
        if (this.referencedRule !== void 0) {
          return this.referencedRule.definition;
        }
        return [];
      },
      set: function(definition) {
      },
      enumerable: true,
      configurable: true
    });
    NonTerminal2.prototype.accept = function(visitor) {
      visitor.visit(this);
    };
    return NonTerminal2;
  })(AbstractProduction)
);
var Rule = (
  /** @class */
  (function(_super) {
    __extends$8(Rule2, _super);
    function Rule2(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.orgText = "";
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return Rule2;
  })(AbstractProduction)
);
var Flat = (
  /** @class */
  (function(_super) {
    __extends$8(Flat2, _super);
    function Flat2(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.ignoreAmbiguities = false;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return Flat2;
  })(AbstractProduction)
);
var Option2 = (
  /** @class */
  (function(_super) {
    __extends$8(Option3, _super);
    function Option3(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.idx = 1;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return Option3;
  })(AbstractProduction)
);
var RepetitionMandatory = (
  /** @class */
  (function(_super) {
    __extends$8(RepetitionMandatory2, _super);
    function RepetitionMandatory2(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.idx = 1;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return RepetitionMandatory2;
  })(AbstractProduction)
);
var RepetitionMandatoryWithSeparator = (
  /** @class */
  (function(_super) {
    __extends$8(RepetitionMandatoryWithSeparator2, _super);
    function RepetitionMandatoryWithSeparator2(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.idx = 1;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return RepetitionMandatoryWithSeparator2;
  })(AbstractProduction)
);
var Repetition = (
  /** @class */
  (function(_super) {
    __extends$8(Repetition2, _super);
    function Repetition2(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.idx = 1;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return Repetition2;
  })(AbstractProduction)
);
var RepetitionWithSeparator = (
  /** @class */
  (function(_super) {
    __extends$8(RepetitionWithSeparator2, _super);
    function RepetitionWithSeparator2(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.idx = 1;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return RepetitionWithSeparator2;
  })(AbstractProduction)
);
var Alternation = (
  /** @class */
  (function(_super) {
    __extends$8(Alternation2, _super);
    function Alternation2(options) {
      var _this = _super.call(this, options.definition) || this;
      _this.idx = 1;
      _this.ignoreAmbiguities = false;
      _this.hasPredicates = false;
      assign(_this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
      return _this;
    }
    return Alternation2;
  })(AbstractProduction)
);
var Terminal = (
  /** @class */
  (function() {
    function Terminal2(options) {
      this.idx = 1;
      assign(this, pick(options, function(v2) {
        return v2 !== void 0;
      }));
    }
    Terminal2.prototype.accept = function(visitor) {
      visitor.visit(this);
    };
    return Terminal2;
  })()
);
function serializeGrammar(topRules) {
  return map(topRules, serializeProduction);
}
function serializeProduction(node) {
  function convertDefinition(definition) {
    return map(definition, serializeProduction);
  }
  if (node instanceof NonTerminal) {
    return {
      type: "NonTerminal",
      name: node.nonTerminalName,
      idx: node.idx
    };
  } else if (node instanceof Flat) {
    return {
      type: "Flat",
      definition: convertDefinition(node.definition)
    };
  } else if (node instanceof Option2) {
    return {
      type: "Option",
      idx: node.idx,
      definition: convertDefinition(node.definition)
    };
  } else if (node instanceof RepetitionMandatory) {
    return {
      type: "RepetitionMandatory",
      name: node.name,
      idx: node.idx,
      definition: convertDefinition(node.definition)
    };
  } else if (node instanceof RepetitionMandatoryWithSeparator) {
    return {
      type: "RepetitionMandatoryWithSeparator",
      name: node.name,
      idx: node.idx,
      separator: serializeProduction(new Terminal({ terminalType: node.separator })),
      definition: convertDefinition(node.definition)
    };
  } else if (node instanceof RepetitionWithSeparator) {
    return {
      type: "RepetitionWithSeparator",
      name: node.name,
      idx: node.idx,
      separator: serializeProduction(new Terminal({ terminalType: node.separator })),
      definition: convertDefinition(node.definition)
    };
  } else if (node instanceof Repetition) {
    return {
      type: "Repetition",
      name: node.name,
      idx: node.idx,
      definition: convertDefinition(node.definition)
    };
  } else if (node instanceof Alternation) {
    return {
      type: "Alternation",
      name: node.name,
      idx: node.idx,
      definition: convertDefinition(node.definition)
    };
  } else if (node instanceof Terminal) {
    var serializedTerminal = {
      type: "Terminal",
      name: node.terminalType.name,
      label: tokenLabel(node.terminalType),
      idx: node.idx
    };
    var pattern = node.terminalType.PATTERN;
    if (node.terminalType.PATTERN) {
      serializedTerminal.pattern = isRegExp(pattern) ? pattern.source : pattern;
    }
    return serializedTerminal;
  } else if (node instanceof Rule) {
    return {
      type: "Rule",
      name: node.name,
      orgText: node.orgText,
      definition: convertDefinition(node.definition)
    };
  } else {
    throw Error("non exhaustive match");
  }
}
var RestWalker = (
  /** @class */
  (function() {
    function RestWalker2() {
    }
    RestWalker2.prototype.walk = function(prod, prevRest) {
      var _this = this;
      if (prevRest === void 0) {
        prevRest = [];
      }
      forEach(prod.definition, function(subProd, index2) {
        var currRest = drop(prod.definition, index2 + 1);
        if (subProd instanceof NonTerminal) {
          _this.walkProdRef(subProd, currRest, prevRest);
        } else if (subProd instanceof Terminal) {
          _this.walkTerminal(subProd, currRest, prevRest);
        } else if (subProd instanceof Flat) {
          _this.walkFlat(subProd, currRest, prevRest);
        } else if (subProd instanceof Option2) {
          _this.walkOption(subProd, currRest, prevRest);
        } else if (subProd instanceof RepetitionMandatory) {
          _this.walkAtLeastOne(subProd, currRest, prevRest);
        } else if (subProd instanceof RepetitionMandatoryWithSeparator) {
          _this.walkAtLeastOneSep(subProd, currRest, prevRest);
        } else if (subProd instanceof RepetitionWithSeparator) {
          _this.walkManySep(subProd, currRest, prevRest);
        } else if (subProd instanceof Repetition) {
          _this.walkMany(subProd, currRest, prevRest);
        } else if (subProd instanceof Alternation) {
          _this.walkOr(subProd, currRest, prevRest);
        } else {
          throw Error("non exhaustive match");
        }
      });
    };
    RestWalker2.prototype.walkTerminal = function(terminal, currRest, prevRest) {
    };
    RestWalker2.prototype.walkProdRef = function(refProd, currRest, prevRest) {
    };
    RestWalker2.prototype.walkFlat = function(flatProd, currRest, prevRest) {
      var fullOrRest = currRest.concat(prevRest);
      this.walk(flatProd, fullOrRest);
    };
    RestWalker2.prototype.walkOption = function(optionProd, currRest, prevRest) {
      var fullOrRest = currRest.concat(prevRest);
      this.walk(optionProd, fullOrRest);
    };
    RestWalker2.prototype.walkAtLeastOne = function(atLeastOneProd, currRest, prevRest) {
      var fullAtLeastOneRest = [
        new Option2({ definition: atLeastOneProd.definition })
      ].concat(currRest, prevRest);
      this.walk(atLeastOneProd, fullAtLeastOneRest);
    };
    RestWalker2.prototype.walkAtLeastOneSep = function(atLeastOneSepProd, currRest, prevRest) {
      var fullAtLeastOneSepRest = restForRepetitionWithSeparator(atLeastOneSepProd, currRest, prevRest);
      this.walk(atLeastOneSepProd, fullAtLeastOneSepRest);
    };
    RestWalker2.prototype.walkMany = function(manyProd, currRest, prevRest) {
      var fullManyRest = [
        new Option2({ definition: manyProd.definition })
      ].concat(currRest, prevRest);
      this.walk(manyProd, fullManyRest);
    };
    RestWalker2.prototype.walkManySep = function(manySepProd, currRest, prevRest) {
      var fullManySepRest = restForRepetitionWithSeparator(manySepProd, currRest, prevRest);
      this.walk(manySepProd, fullManySepRest);
    };
    RestWalker2.prototype.walkOr = function(orProd, currRest, prevRest) {
      var _this = this;
      var fullOrRest = currRest.concat(prevRest);
      forEach(orProd.definition, function(alt) {
        var prodWrapper = new Flat({ definition: [alt] });
        _this.walk(prodWrapper, fullOrRest);
      });
    };
    return RestWalker2;
  })()
);
function restForRepetitionWithSeparator(repSepProd, currRest, prevRest) {
  var repSepRest = [
    new Option2({
      definition: [
        new Terminal({ terminalType: repSepProd.separator })
      ].concat(repSepProd.definition)
    })
  ];
  var fullRepSepRest = repSepRest.concat(currRest, prevRest);
  return fullRepSepRest;
}
var GAstVisitor = (
  /** @class */
  (function() {
    function GAstVisitor2() {
    }
    GAstVisitor2.prototype.visit = function(node) {
      var nodeAny = node;
      switch (nodeAny.constructor) {
        case NonTerminal:
          return this.visitNonTerminal(nodeAny);
        case Flat:
          return this.visitFlat(nodeAny);
        case Option2:
          return this.visitOption(nodeAny);
        case RepetitionMandatory:
          return this.visitRepetitionMandatory(nodeAny);
        case RepetitionMandatoryWithSeparator:
          return this.visitRepetitionMandatoryWithSeparator(nodeAny);
        case RepetitionWithSeparator:
          return this.visitRepetitionWithSeparator(nodeAny);
        case Repetition:
          return this.visitRepetition(nodeAny);
        case Alternation:
          return this.visitAlternation(nodeAny);
        case Terminal:
          return this.visitTerminal(nodeAny);
        case Rule:
          return this.visitRule(nodeAny);
        /* istanbul ignore next */
        default:
          throw Error("non exhaustive match");
      }
    };
    GAstVisitor2.prototype.visitNonTerminal = function(node) {
    };
    GAstVisitor2.prototype.visitFlat = function(node) {
    };
    GAstVisitor2.prototype.visitOption = function(node) {
    };
    GAstVisitor2.prototype.visitRepetition = function(node) {
    };
    GAstVisitor2.prototype.visitRepetitionMandatory = function(node) {
    };
    GAstVisitor2.prototype.visitRepetitionMandatoryWithSeparator = function(node) {
    };
    GAstVisitor2.prototype.visitRepetitionWithSeparator = function(node) {
    };
    GAstVisitor2.prototype.visitAlternation = function(node) {
    };
    GAstVisitor2.prototype.visitTerminal = function(node) {
    };
    GAstVisitor2.prototype.visitRule = function(node) {
    };
    return GAstVisitor2;
  })()
);
var __extends$7 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
function isSequenceProd(prod) {
  return prod instanceof Flat || prod instanceof Option2 || prod instanceof Repetition || prod instanceof RepetitionMandatory || prod instanceof RepetitionMandatoryWithSeparator || prod instanceof RepetitionWithSeparator || prod instanceof Terminal || prod instanceof Rule;
}
function isOptionalProd(prod, alreadyVisited) {
  if (alreadyVisited === void 0) {
    alreadyVisited = [];
  }
  var isDirectlyOptional = prod instanceof Option2 || prod instanceof Repetition || prod instanceof RepetitionWithSeparator;
  if (isDirectlyOptional) {
    return true;
  }
  if (prod instanceof Alternation) {
    return some(prod.definition, function(subProd) {
      return isOptionalProd(subProd, alreadyVisited);
    });
  } else if (prod instanceof NonTerminal && contains(alreadyVisited, prod)) {
    return false;
  } else if (prod instanceof AbstractProduction) {
    if (prod instanceof NonTerminal) {
      alreadyVisited.push(prod);
    }
    return every(prod.definition, function(subProd) {
      return isOptionalProd(subProd, alreadyVisited);
    });
  } else {
    return false;
  }
}
function isBranchingProd(prod) {
  return prod instanceof Alternation;
}
function getProductionDslName(prod) {
  if (prod instanceof NonTerminal) {
    return "SUBRULE";
  } else if (prod instanceof Option2) {
    return "OPTION";
  } else if (prod instanceof Alternation) {
    return "OR";
  } else if (prod instanceof RepetitionMandatory) {
    return "AT_LEAST_ONE";
  } else if (prod instanceof RepetitionMandatoryWithSeparator) {
    return "AT_LEAST_ONE_SEP";
  } else if (prod instanceof RepetitionWithSeparator) {
    return "MANY_SEP";
  } else if (prod instanceof Repetition) {
    return "MANY";
  } else if (prod instanceof Terminal) {
    return "CONSUME";
  } else {
    throw Error("non exhaustive match");
  }
}
var DslMethodsCollectorVisitor = (
  /** @class */
  (function(_super) {
    __extends$7(DslMethodsCollectorVisitor2, _super);
    function DslMethodsCollectorVisitor2() {
      var _this = _super !== null && _super.apply(this, arguments) || this;
      _this.separator = "-";
      _this.dslMethods = {
        option: [],
        alternation: [],
        repetition: [],
        repetitionWithSeparator: [],
        repetitionMandatory: [],
        repetitionMandatoryWithSeparator: []
      };
      return _this;
    }
    DslMethodsCollectorVisitor2.prototype.reset = function() {
      this.dslMethods = {
        option: [],
        alternation: [],
        repetition: [],
        repetitionWithSeparator: [],
        repetitionMandatory: [],
        repetitionMandatoryWithSeparator: []
      };
    };
    DslMethodsCollectorVisitor2.prototype.visitTerminal = function(terminal) {
      var key = terminal.terminalType.name + this.separator + "Terminal";
      if (!has(this.dslMethods, key)) {
        this.dslMethods[key] = [];
      }
      this.dslMethods[key].push(terminal);
    };
    DslMethodsCollectorVisitor2.prototype.visitNonTerminal = function(subrule) {
      var key = subrule.nonTerminalName + this.separator + "Terminal";
      if (!has(this.dslMethods, key)) {
        this.dslMethods[key] = [];
      }
      this.dslMethods[key].push(subrule);
    };
    DslMethodsCollectorVisitor2.prototype.visitOption = function(option) {
      this.dslMethods.option.push(option);
    };
    DslMethodsCollectorVisitor2.prototype.visitRepetitionWithSeparator = function(manySep) {
      this.dslMethods.repetitionWithSeparator.push(manySep);
    };
    DslMethodsCollectorVisitor2.prototype.visitRepetitionMandatory = function(atLeastOne) {
      this.dslMethods.repetitionMandatory.push(atLeastOne);
    };
    DslMethodsCollectorVisitor2.prototype.visitRepetitionMandatoryWithSeparator = function(atLeastOneSep) {
      this.dslMethods.repetitionMandatoryWithSeparator.push(atLeastOneSep);
    };
    DslMethodsCollectorVisitor2.prototype.visitRepetition = function(many) {
      this.dslMethods.repetition.push(many);
    };
    DslMethodsCollectorVisitor2.prototype.visitAlternation = function(or2) {
      this.dslMethods.alternation.push(or2);
    };
    return DslMethodsCollectorVisitor2;
  })(GAstVisitor)
);
var collectorVisitor = new DslMethodsCollectorVisitor();
function collectMethods(rule) {
  collectorVisitor.reset();
  rule.accept(collectorVisitor);
  var dslMethods = collectorVisitor.dslMethods;
  collectorVisitor.reset();
  return dslMethods;
}
function first(prod) {
  if (prod instanceof NonTerminal) {
    return first(prod.referencedRule);
  } else if (prod instanceof Terminal) {
    return firstForTerminal(prod);
  } else if (isSequenceProd(prod)) {
    return firstForSequence(prod);
  } else if (isBranchingProd(prod)) {
    return firstForBranching(prod);
  } else {
    throw Error("non exhaustive match");
  }
}
function firstForSequence(prod) {
  var firstSet = [];
  var seq = prod.definition;
  var nextSubProdIdx = 0;
  var hasInnerProdsRemaining = seq.length > nextSubProdIdx;
  var currSubProd;
  var isLastInnerProdOptional = true;
  while (hasInnerProdsRemaining && isLastInnerProdOptional) {
    currSubProd = seq[nextSubProdIdx];
    isLastInnerProdOptional = isOptionalProd(currSubProd);
    firstSet = firstSet.concat(first(currSubProd));
    nextSubProdIdx = nextSubProdIdx + 1;
    hasInnerProdsRemaining = seq.length > nextSubProdIdx;
  }
  return uniq(firstSet);
}
function firstForBranching(prod) {
  var allAlternativesFirsts = map(prod.definition, function(innerProd) {
    return first(innerProd);
  });
  return uniq(flatten(allAlternativesFirsts));
}
function firstForTerminal(terminal) {
  return [terminal.terminalType];
}
var IN = "_~IN~_";
var __extends$6 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
var ResyncFollowsWalker = (
  /** @class */
  (function(_super) {
    __extends$6(ResyncFollowsWalker2, _super);
    function ResyncFollowsWalker2(topProd) {
      var _this = _super.call(this) || this;
      _this.topProd = topProd;
      _this.follows = {};
      return _this;
    }
    ResyncFollowsWalker2.prototype.startWalking = function() {
      this.walk(this.topProd);
      return this.follows;
    };
    ResyncFollowsWalker2.prototype.walkTerminal = function(terminal, currRest, prevRest) {
    };
    ResyncFollowsWalker2.prototype.walkProdRef = function(refProd, currRest, prevRest) {
      var followName = buildBetweenProdsFollowPrefix(refProd.referencedRule, refProd.idx) + this.topProd.name;
      var fullRest = currRest.concat(prevRest);
      var restProd = new Flat({ definition: fullRest });
      var t_in_topProd_follows = first(restProd);
      this.follows[followName] = t_in_topProd_follows;
    };
    return ResyncFollowsWalker2;
  })(RestWalker)
);
function computeAllProdsFollows(topProductions) {
  var reSyncFollows = {};
  forEach(topProductions, function(topProd) {
    var currRefsFollow = new ResyncFollowsWalker(topProd).startWalking();
    assign(reSyncFollows, currRefsFollow);
  });
  return reSyncFollows;
}
function buildBetweenProdsFollowPrefix(inner, occurenceInParent) {
  return inner.name + occurenceInParent + IN;
}
var BITS_FOR_METHOD_TYPE = 4;
var BITS_FOR_OCCURRENCE_IDX = 8;
var BITS_FOR_ALT_IDX = 8;
var OR_IDX = 1 << BITS_FOR_OCCURRENCE_IDX;
var OPTION_IDX = 2 << BITS_FOR_OCCURRENCE_IDX;
var MANY_IDX = 3 << BITS_FOR_OCCURRENCE_IDX;
var AT_LEAST_ONE_IDX = 4 << BITS_FOR_OCCURRENCE_IDX;
var MANY_SEP_IDX = 5 << BITS_FOR_OCCURRENCE_IDX;
var AT_LEAST_ONE_SEP_IDX = 6 << BITS_FOR_OCCURRENCE_IDX;
function getKeyForAutomaticLookahead(ruleIdx, dslMethodIdx, occurrence) {
  return occurrence | dslMethodIdx | ruleIdx;
}
var BITS_START_FOR_ALT_IDX = 32 - BITS_FOR_ALT_IDX;
function getKeyForAltIndex(ruleIdx, dslMethodIdx, occurrence, altIdx) {
  var altIdxBitMap = altIdx + 1 << BITS_START_FOR_ALT_IDX;
  return getKeyForAutomaticLookahead(ruleIdx, dslMethodIdx, occurrence) | altIdxBitMap;
}
var __extends$5 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
function setNodeLocationOnlyOffset(currNodeLocation, newLocationInfo) {
  if (isNaN(currNodeLocation.startOffset) === true) {
    currNodeLocation.startOffset = newLocationInfo.startOffset;
    currNodeLocation.endOffset = newLocationInfo.endOffset;
  } else if (currNodeLocation.endOffset < newLocationInfo.endOffset === true) {
    currNodeLocation.endOffset = newLocationInfo.endOffset;
  }
}
function setNodeLocationFull(currNodeLocation, newLocationInfo) {
  if (isNaN(currNodeLocation.startOffset) === true) {
    currNodeLocation.startOffset = newLocationInfo.startOffset;
    currNodeLocation.startColumn = newLocationInfo.startColumn;
    currNodeLocation.startLine = newLocationInfo.startLine;
    currNodeLocation.endOffset = newLocationInfo.endOffset;
    currNodeLocation.endColumn = newLocationInfo.endColumn;
    currNodeLocation.endLine = newLocationInfo.endLine;
  } else if (currNodeLocation.endOffset < newLocationInfo.endOffset === true) {
    currNodeLocation.endOffset = newLocationInfo.endOffset;
    currNodeLocation.endColumn = newLocationInfo.endColumn;
    currNodeLocation.endLine = newLocationInfo.endLine;
  }
}
function addTerminalToCst(node, token, tokenTypeName) {
  if (node.children[tokenTypeName] === void 0) {
    node.children[tokenTypeName] = [token];
  } else {
    node.children[tokenTypeName].push(token);
  }
}
function addNoneTerminalToCst(node, ruleName, ruleResult) {
  if (node.children[ruleName] === void 0) {
    node.children[ruleName] = [ruleResult];
  } else {
    node.children[ruleName].push(ruleResult);
  }
}
var NamedDSLMethodsCollectorVisitor = (
  /** @class */
  (function(_super) {
    __extends$5(NamedDSLMethodsCollectorVisitor2, _super);
    function NamedDSLMethodsCollectorVisitor2(ruleIdx) {
      var _this = _super.call(this) || this;
      _this.result = [];
      _this.ruleIdx = ruleIdx;
      return _this;
    }
    NamedDSLMethodsCollectorVisitor2.prototype.collectNamedDSLMethod = function(node, newNodeConstructor, methodIdx) {
      if (!isUndefined(node.name)) {
        var nameLessNode = void 0;
        if (node instanceof Option2 || node instanceof Repetition || node instanceof RepetitionMandatory || node instanceof Alternation) {
          nameLessNode = new newNodeConstructor({
            definition: node.definition,
            idx: node.idx
          });
        } else if (node instanceof RepetitionMandatoryWithSeparator || node instanceof RepetitionWithSeparator) {
          nameLessNode = new newNodeConstructor({
            definition: node.definition,
            idx: node.idx,
            separator: node.separator
          });
        } else {
          throw Error("non exhaustive match");
        }
        var def = [nameLessNode];
        var key = getKeyForAutomaticLookahead(this.ruleIdx, methodIdx, node.idx);
        this.result.push({ def, key, name: node.name, orgProd: node });
      }
    };
    NamedDSLMethodsCollectorVisitor2.prototype.visitOption = function(node) {
      this.collectNamedDSLMethod(node, Option2, OPTION_IDX);
    };
    NamedDSLMethodsCollectorVisitor2.prototype.visitRepetition = function(node) {
      this.collectNamedDSLMethod(node, Repetition, MANY_IDX);
    };
    NamedDSLMethodsCollectorVisitor2.prototype.visitRepetitionMandatory = function(node) {
      this.collectNamedDSLMethod(node, RepetitionMandatory, AT_LEAST_ONE_IDX);
    };
    NamedDSLMethodsCollectorVisitor2.prototype.visitRepetitionMandatoryWithSeparator = function(node) {
      this.collectNamedDSLMethod(node, RepetitionMandatoryWithSeparator, AT_LEAST_ONE_SEP_IDX);
    };
    NamedDSLMethodsCollectorVisitor2.prototype.visitRepetitionWithSeparator = function(node) {
      this.collectNamedDSLMethod(node, RepetitionWithSeparator, MANY_SEP_IDX);
    };
    NamedDSLMethodsCollectorVisitor2.prototype.visitAlternation = function(node) {
      var _this = this;
      this.collectNamedDSLMethod(node, Alternation, OR_IDX);
      var hasMoreThanOneAlternative = node.definition.length > 1;
      forEach(node.definition, function(currFlatAlt, altIdx) {
        if (!isUndefined(currFlatAlt.name)) {
          var def = currFlatAlt.definition;
          if (hasMoreThanOneAlternative) {
            def = [new Option2({ definition: currFlatAlt.definition })];
          } else {
            def = currFlatAlt.definition;
          }
          var key = getKeyForAltIndex(_this.ruleIdx, OR_IDX, node.idx, altIdx);
          _this.result.push({
            def,
            key,
            name: currFlatAlt.name,
            orgProd: currFlatAlt
          });
        }
      });
    };
    return NamedDSLMethodsCollectorVisitor2;
  })(GAstVisitor)
);
function expandAllNestedRuleNames(topRules, fullToShortName) {
  var result = {
    allRuleNames: []
  };
  forEach(topRules, function(currTopRule) {
    var currTopRuleShortName = fullToShortName[currTopRule.name];
    result.allRuleNames.push(currTopRule.name);
    var namedCollectorVisitor = new NamedDSLMethodsCollectorVisitor(currTopRuleShortName);
    currTopRule.accept(namedCollectorVisitor);
    forEach(namedCollectorVisitor.result, function(_a2) {
      _a2.def;
      _a2.key;
      var name = _a2.name;
      result.allRuleNames.push(currTopRule.name + name);
    });
  });
  return result;
}
var __extends$4 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
var AbstractNextPossibleTokensWalker = (
  /** @class */
  (function(_super) {
    __extends$4(AbstractNextPossibleTokensWalker2, _super);
    function AbstractNextPossibleTokensWalker2(topProd, path) {
      var _this = _super.call(this) || this;
      _this.topProd = topProd;
      _this.path = path;
      _this.possibleTokTypes = [];
      _this.nextProductionName = "";
      _this.nextProductionOccurrence = 0;
      _this.found = false;
      _this.isAtEndOfPath = false;
      return _this;
    }
    AbstractNextPossibleTokensWalker2.prototype.startWalking = function() {
      this.found = false;
      if (this.path.ruleStack[0] !== this.topProd.name) {
        throw Error("The path does not start with the walker's top Rule!");
      }
      this.ruleStack = cloneArr(this.path.ruleStack).reverse();
      this.occurrenceStack = cloneArr(this.path.occurrenceStack).reverse();
      this.ruleStack.pop();
      this.occurrenceStack.pop();
      this.updateExpectedNext();
      this.walk(this.topProd);
      return this.possibleTokTypes;
    };
    AbstractNextPossibleTokensWalker2.prototype.walk = function(prod, prevRest) {
      if (prevRest === void 0) {
        prevRest = [];
      }
      if (!this.found) {
        _super.prototype.walk.call(this, prod, prevRest);
      }
    };
    AbstractNextPossibleTokensWalker2.prototype.walkProdRef = function(refProd, currRest, prevRest) {
      if (refProd.referencedRule.name === this.nextProductionName && refProd.idx === this.nextProductionOccurrence) {
        var fullRest = currRest.concat(prevRest);
        this.updateExpectedNext();
        this.walk(refProd.referencedRule, fullRest);
      }
    };
    AbstractNextPossibleTokensWalker2.prototype.updateExpectedNext = function() {
      if (isEmpty(this.ruleStack)) {
        this.nextProductionName = "";
        this.nextProductionOccurrence = 0;
        this.isAtEndOfPath = true;
      } else {
        this.nextProductionName = this.ruleStack.pop();
        this.nextProductionOccurrence = this.occurrenceStack.pop();
      }
    };
    return AbstractNextPossibleTokensWalker2;
  })(RestWalker)
);
var NextAfterTokenWalker = (
  /** @class */
  (function(_super) {
    __extends$4(NextAfterTokenWalker2, _super);
    function NextAfterTokenWalker2(topProd, path) {
      var _this = _super.call(this, topProd, path) || this;
      _this.path = path;
      _this.nextTerminalName = "";
      _this.nextTerminalOccurrence = 0;
      _this.nextTerminalName = _this.path.lastTok.name;
      _this.nextTerminalOccurrence = _this.path.lastTokOccurrence;
      return _this;
    }
    NextAfterTokenWalker2.prototype.walkTerminal = function(terminal, currRest, prevRest) {
      if (this.isAtEndOfPath && terminal.terminalType.name === this.nextTerminalName && terminal.idx === this.nextTerminalOccurrence && !this.found) {
        var fullRest = currRest.concat(prevRest);
        var restProd = new Flat({ definition: fullRest });
        this.possibleTokTypes = first(restProd);
        this.found = true;
      }
    };
    return NextAfterTokenWalker2;
  })(AbstractNextPossibleTokensWalker)
);
var AbstractNextTerminalAfterProductionWalker = (
  /** @class */
  (function(_super) {
    __extends$4(AbstractNextTerminalAfterProductionWalker2, _super);
    function AbstractNextTerminalAfterProductionWalker2(topRule, occurrence) {
      var _this = _super.call(this) || this;
      _this.topRule = topRule;
      _this.occurrence = occurrence;
      _this.result = {
        token: void 0,
        occurrence: void 0,
        isEndOfRule: void 0
      };
      return _this;
    }
    AbstractNextTerminalAfterProductionWalker2.prototype.startWalking = function() {
      this.walk(this.topRule);
      return this.result;
    };
    return AbstractNextTerminalAfterProductionWalker2;
  })(RestWalker)
);
var NextTerminalAfterManyWalker = (
  /** @class */
  (function(_super) {
    __extends$4(NextTerminalAfterManyWalker2, _super);
    function NextTerminalAfterManyWalker2() {
      return _super !== null && _super.apply(this, arguments) || this;
    }
    NextTerminalAfterManyWalker2.prototype.walkMany = function(manyProd, currRest, prevRest) {
      if (manyProd.idx === this.occurrence) {
        var firstAfterMany = first$1(currRest.concat(prevRest));
        this.result.isEndOfRule = firstAfterMany === void 0;
        if (firstAfterMany instanceof Terminal) {
          this.result.token = firstAfterMany.terminalType;
          this.result.occurrence = firstAfterMany.idx;
        }
      } else {
        _super.prototype.walkMany.call(this, manyProd, currRest, prevRest);
      }
    };
    return NextTerminalAfterManyWalker2;
  })(AbstractNextTerminalAfterProductionWalker)
);
var NextTerminalAfterManySepWalker = (
  /** @class */
  (function(_super) {
    __extends$4(NextTerminalAfterManySepWalker2, _super);
    function NextTerminalAfterManySepWalker2() {
      return _super !== null && _super.apply(this, arguments) || this;
    }
    NextTerminalAfterManySepWalker2.prototype.walkManySep = function(manySepProd, currRest, prevRest) {
      if (manySepProd.idx === this.occurrence) {
        var firstAfterManySep = first$1(currRest.concat(prevRest));
        this.result.isEndOfRule = firstAfterManySep === void 0;
        if (firstAfterManySep instanceof Terminal) {
          this.result.token = firstAfterManySep.terminalType;
          this.result.occurrence = firstAfterManySep.idx;
        }
      } else {
        _super.prototype.walkManySep.call(this, manySepProd, currRest, prevRest);
      }
    };
    return NextTerminalAfterManySepWalker2;
  })(AbstractNextTerminalAfterProductionWalker)
);
var NextTerminalAfterAtLeastOneWalker = (
  /** @class */
  (function(_super) {
    __extends$4(NextTerminalAfterAtLeastOneWalker2, _super);
    function NextTerminalAfterAtLeastOneWalker2() {
      return _super !== null && _super.apply(this, arguments) || this;
    }
    NextTerminalAfterAtLeastOneWalker2.prototype.walkAtLeastOne = function(atLeastOneProd, currRest, prevRest) {
      if (atLeastOneProd.idx === this.occurrence) {
        var firstAfterAtLeastOne = first$1(currRest.concat(prevRest));
        this.result.isEndOfRule = firstAfterAtLeastOne === void 0;
        if (firstAfterAtLeastOne instanceof Terminal) {
          this.result.token = firstAfterAtLeastOne.terminalType;
          this.result.occurrence = firstAfterAtLeastOne.idx;
        }
      } else {
        _super.prototype.walkAtLeastOne.call(this, atLeastOneProd, currRest, prevRest);
      }
    };
    return NextTerminalAfterAtLeastOneWalker2;
  })(AbstractNextTerminalAfterProductionWalker)
);
var NextTerminalAfterAtLeastOneSepWalker = (
  /** @class */
  (function(_super) {
    __extends$4(NextTerminalAfterAtLeastOneSepWalker2, _super);
    function NextTerminalAfterAtLeastOneSepWalker2() {
      return _super !== null && _super.apply(this, arguments) || this;
    }
    NextTerminalAfterAtLeastOneSepWalker2.prototype.walkAtLeastOneSep = function(atleastOneSepProd, currRest, prevRest) {
      if (atleastOneSepProd.idx === this.occurrence) {
        var firstAfterfirstAfterAtLeastOneSep = first$1(currRest.concat(prevRest));
        this.result.isEndOfRule = firstAfterfirstAfterAtLeastOneSep === void 0;
        if (firstAfterfirstAfterAtLeastOneSep instanceof Terminal) {
          this.result.token = firstAfterfirstAfterAtLeastOneSep.terminalType;
          this.result.occurrence = firstAfterfirstAfterAtLeastOneSep.idx;
        }
      } else {
        _super.prototype.walkAtLeastOneSep.call(this, atleastOneSepProd, currRest, prevRest);
      }
    };
    return NextTerminalAfterAtLeastOneSepWalker2;
  })(AbstractNextTerminalAfterProductionWalker)
);
function possiblePathsFrom(targetDef, maxLength, currPath) {
  if (currPath === void 0) {
    currPath = [];
  }
  currPath = cloneArr(currPath);
  var result = [];
  var i2 = 0;
  function remainingPathWith(nextDef) {
    return nextDef.concat(drop(targetDef, i2 + 1));
  }
  function getAlternativesForProd(definition) {
    var alternatives = possiblePathsFrom(remainingPathWith(definition), maxLength, currPath);
    return result.concat(alternatives);
  }
  while (currPath.length < maxLength && i2 < targetDef.length) {
    var prod = targetDef[i2];
    if (prod instanceof Flat) {
      return getAlternativesForProd(prod.definition);
    } else if (prod instanceof NonTerminal) {
      return getAlternativesForProd(prod.definition);
    } else if (prod instanceof Option2) {
      result = getAlternativesForProd(prod.definition);
    } else if (prod instanceof RepetitionMandatory) {
      var newDef = prod.definition.concat([
        new Repetition({
          definition: prod.definition
        })
      ]);
      return getAlternativesForProd(newDef);
    } else if (prod instanceof RepetitionMandatoryWithSeparator) {
      var newDef = [
        new Flat({ definition: prod.definition }),
        new Repetition({
          definition: [
            new Terminal({ terminalType: prod.separator })
          ].concat(prod.definition)
        })
      ];
      return getAlternativesForProd(newDef);
    } else if (prod instanceof RepetitionWithSeparator) {
      var newDef = prod.definition.concat([
        new Repetition({
          definition: [
            new Terminal({ terminalType: prod.separator })
          ].concat(prod.definition)
        })
      ]);
      result = getAlternativesForProd(newDef);
    } else if (prod instanceof Repetition) {
      var newDef = prod.definition.concat([
        new Repetition({
          definition: prod.definition
        })
      ]);
      result = getAlternativesForProd(newDef);
    } else if (prod instanceof Alternation) {
      forEach(prod.definition, function(currAlt) {
        result = getAlternativesForProd(currAlt.definition);
      });
      return result;
    } else if (prod instanceof Terminal) {
      currPath.push(prod.terminalType);
    } else {
      throw Error("non exhaustive match");
    }
    i2++;
  }
  result.push({
    partialPath: currPath,
    suffixDef: drop(targetDef, i2)
  });
  return result;
}
function nextPossibleTokensAfter(initialDef, tokenVector, tokMatcher, maxLookAhead) {
  var EXIT_NON_TERMINAL = "EXIT_NONE_TERMINAL";
  var EXIT_NON_TERMINAL_ARR = [EXIT_NON_TERMINAL];
  var EXIT_ALTERNATIVE = "EXIT_ALTERNATIVE";
  var foundCompletePath = false;
  var tokenVectorLength = tokenVector.length;
  var minimalAlternativesIndex = tokenVectorLength - maxLookAhead - 1;
  var result = [];
  var possiblePaths = [];
  possiblePaths.push({
    idx: -1,
    def: initialDef,
    ruleStack: [],
    occurrenceStack: []
  });
  while (!isEmpty(possiblePaths)) {
    var currPath = possiblePaths.pop();
    if (currPath === EXIT_ALTERNATIVE) {
      if (foundCompletePath && last(possiblePaths).idx <= minimalAlternativesIndex) {
        possiblePaths.pop();
      }
      continue;
    }
    var currDef = currPath.def;
    var currIdx = currPath.idx;
    var currRuleStack = currPath.ruleStack;
    var currOccurrenceStack = currPath.occurrenceStack;
    if (isEmpty(currDef)) {
      continue;
    }
    var prod = currDef[0];
    if (prod === EXIT_NON_TERMINAL) {
      var nextPath = {
        idx: currIdx,
        def: drop(currDef),
        ruleStack: dropRight(currRuleStack),
        occurrenceStack: dropRight(currOccurrenceStack)
      };
      possiblePaths.push(nextPath);
    } else if (prod instanceof Terminal) {
      if (currIdx < tokenVectorLength - 1) {
        var nextIdx = currIdx + 1;
        var actualToken = tokenVector[nextIdx];
        if (tokMatcher(actualToken, prod.terminalType)) {
          var nextPath = {
            idx: nextIdx,
            def: drop(currDef),
            ruleStack: currRuleStack,
            occurrenceStack: currOccurrenceStack
          };
          possiblePaths.push(nextPath);
        }
      } else if (currIdx === tokenVectorLength - 1) {
        result.push({
          nextTokenType: prod.terminalType,
          nextTokenOccurrence: prod.idx,
          ruleStack: currRuleStack,
          occurrenceStack: currOccurrenceStack
        });
        foundCompletePath = true;
      } else {
        throw Error("non exhaustive match");
      }
    } else if (prod instanceof NonTerminal) {
      var newRuleStack = cloneArr(currRuleStack);
      newRuleStack.push(prod.nonTerminalName);
      var newOccurrenceStack = cloneArr(currOccurrenceStack);
      newOccurrenceStack.push(prod.idx);
      var nextPath = {
        idx: currIdx,
        def: prod.definition.concat(EXIT_NON_TERMINAL_ARR, drop(currDef)),
        ruleStack: newRuleStack,
        occurrenceStack: newOccurrenceStack
      };
      possiblePaths.push(nextPath);
    } else if (prod instanceof Option2) {
      var nextPathWithout = {
        idx: currIdx,
        def: drop(currDef),
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPathWithout);
      possiblePaths.push(EXIT_ALTERNATIVE);
      var nextPathWith = {
        idx: currIdx,
        def: prod.definition.concat(drop(currDef)),
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPathWith);
    } else if (prod instanceof RepetitionMandatory) {
      var secondIteration = new Repetition({
        definition: prod.definition,
        idx: prod.idx
      });
      var nextDef = prod.definition.concat([secondIteration], drop(currDef));
      var nextPath = {
        idx: currIdx,
        def: nextDef,
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPath);
    } else if (prod instanceof RepetitionMandatoryWithSeparator) {
      var separatorGast = new Terminal({
        terminalType: prod.separator
      });
      var secondIteration = new Repetition({
        definition: [separatorGast].concat(prod.definition),
        idx: prod.idx
      });
      var nextDef = prod.definition.concat([secondIteration], drop(currDef));
      var nextPath = {
        idx: currIdx,
        def: nextDef,
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPath);
    } else if (prod instanceof RepetitionWithSeparator) {
      var nextPathWithout = {
        idx: currIdx,
        def: drop(currDef),
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPathWithout);
      possiblePaths.push(EXIT_ALTERNATIVE);
      var separatorGast = new Terminal({
        terminalType: prod.separator
      });
      var nthRepetition = new Repetition({
        definition: [separatorGast].concat(prod.definition),
        idx: prod.idx
      });
      var nextDef = prod.definition.concat([nthRepetition], drop(currDef));
      var nextPathWith = {
        idx: currIdx,
        def: nextDef,
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPathWith);
    } else if (prod instanceof Repetition) {
      var nextPathWithout = {
        idx: currIdx,
        def: drop(currDef),
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPathWithout);
      possiblePaths.push(EXIT_ALTERNATIVE);
      var nthRepetition = new Repetition({
        definition: prod.definition,
        idx: prod.idx
      });
      var nextDef = prod.definition.concat([nthRepetition], drop(currDef));
      var nextPathWith = {
        idx: currIdx,
        def: nextDef,
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      };
      possiblePaths.push(nextPathWith);
    } else if (prod instanceof Alternation) {
      for (var i2 = prod.definition.length - 1; i2 >= 0; i2--) {
        var currAlt = prod.definition[i2];
        var currAltPath = {
          idx: currIdx,
          def: currAlt.definition.concat(drop(currDef)),
          ruleStack: currRuleStack,
          occurrenceStack: currOccurrenceStack
        };
        possiblePaths.push(currAltPath);
        possiblePaths.push(EXIT_ALTERNATIVE);
      }
    } else if (prod instanceof Flat) {
      possiblePaths.push({
        idx: currIdx,
        def: prod.definition.concat(drop(currDef)),
        ruleStack: currRuleStack,
        occurrenceStack: currOccurrenceStack
      });
    } else if (prod instanceof Rule) {
      possiblePaths.push(expandTopLevelRule(prod, currIdx, currRuleStack, currOccurrenceStack));
    } else {
      throw Error("non exhaustive match");
    }
  }
  return result;
}
function expandTopLevelRule(topRule, currIdx, currRuleStack, currOccurrenceStack) {
  var newRuleStack = cloneArr(currRuleStack);
  newRuleStack.push(topRule.name);
  var newCurrOccurrenceStack = cloneArr(currOccurrenceStack);
  newCurrOccurrenceStack.push(1);
  return {
    idx: currIdx,
    def: topRule.definition,
    ruleStack: newRuleStack,
    occurrenceStack: newCurrOccurrenceStack
  };
}
var __extends$3 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
var PROD_TYPE;
(function(PROD_TYPE2) {
  PROD_TYPE2[PROD_TYPE2["OPTION"] = 0] = "OPTION";
  PROD_TYPE2[PROD_TYPE2["REPETITION"] = 1] = "REPETITION";
  PROD_TYPE2[PROD_TYPE2["REPETITION_MANDATORY"] = 2] = "REPETITION_MANDATORY";
  PROD_TYPE2[PROD_TYPE2["REPETITION_MANDATORY_WITH_SEPARATOR"] = 3] = "REPETITION_MANDATORY_WITH_SEPARATOR";
  PROD_TYPE2[PROD_TYPE2["REPETITION_WITH_SEPARATOR"] = 4] = "REPETITION_WITH_SEPARATOR";
  PROD_TYPE2[PROD_TYPE2["ALTERNATION"] = 5] = "ALTERNATION";
})(PROD_TYPE || (PROD_TYPE = {}));
function getProdType(prod) {
  if (prod instanceof Option2) {
    return PROD_TYPE.OPTION;
  } else if (prod instanceof Repetition) {
    return PROD_TYPE.REPETITION;
  } else if (prod instanceof RepetitionMandatory) {
    return PROD_TYPE.REPETITION_MANDATORY;
  } else if (prod instanceof RepetitionMandatoryWithSeparator) {
    return PROD_TYPE.REPETITION_MANDATORY_WITH_SEPARATOR;
  } else if (prod instanceof RepetitionWithSeparator) {
    return PROD_TYPE.REPETITION_WITH_SEPARATOR;
  } else if (prod instanceof Alternation) {
    return PROD_TYPE.ALTERNATION;
  } else {
    throw Error("non exhaustive match");
  }
}
function buildLookaheadFuncForOr(occurrence, ruleGrammar, maxLookahead, hasPredicates, dynamicTokensEnabled, laFuncBuilder) {
  var lookAheadPaths = getLookaheadPathsForOr(occurrence, ruleGrammar, maxLookahead);
  var tokenMatcher2 = areTokenCategoriesNotUsed(lookAheadPaths) ? tokenStructuredMatcherNoCategories : tokenStructuredMatcher;
  return laFuncBuilder(lookAheadPaths, hasPredicates, tokenMatcher2, dynamicTokensEnabled);
}
function buildLookaheadFuncForOptionalProd(occurrence, ruleGrammar, k2, dynamicTokensEnabled, prodType, lookaheadBuilder) {
  var lookAheadPaths = getLookaheadPathsForOptionalProd(occurrence, ruleGrammar, prodType, k2);
  var tokenMatcher2 = areTokenCategoriesNotUsed(lookAheadPaths) ? tokenStructuredMatcherNoCategories : tokenStructuredMatcher;
  return lookaheadBuilder(lookAheadPaths[0], tokenMatcher2, dynamicTokensEnabled);
}
function buildAlternativesLookAheadFunc(alts, hasPredicates, tokenMatcher2, dynamicTokensEnabled) {
  var numOfAlts = alts.length;
  var areAllOneTokenLookahead = every(alts, function(currAlt) {
    return every(currAlt, function(currPath) {
      return currPath.length === 1;
    });
  });
  if (hasPredicates) {
    return function(orAlts) {
      var predicates = map(orAlts, function(currAlt2) {
        return currAlt2.GATE;
      });
      for (var t2 = 0; t2 < numOfAlts; t2++) {
        var currAlt = alts[t2];
        var currNumOfPaths = currAlt.length;
        var currPredicate = predicates[t2];
        if (currPredicate !== void 0 && currPredicate.call(this) === false) {
          continue;
        }
        nextPath: for (var j2 = 0; j2 < currNumOfPaths; j2++) {
          var currPath = currAlt[j2];
          var currPathLength = currPath.length;
          for (var i2 = 0; i2 < currPathLength; i2++) {
            var nextToken = this.LA(i2 + 1);
            if (tokenMatcher2(nextToken, currPath[i2]) === false) {
              continue nextPath;
            }
          }
          return t2;
        }
      }
      return void 0;
    };
  } else if (areAllOneTokenLookahead && !dynamicTokensEnabled) {
    var singleTokenAlts = map(alts, function(currAlt) {
      return flatten(currAlt);
    });
    var choiceToAlt_1 = reduce(singleTokenAlts, function(result, currAlt, idx) {
      forEach(currAlt, function(currTokType) {
        if (!has(result, currTokType.tokenTypeIdx)) {
          result[currTokType.tokenTypeIdx] = idx;
        }
        forEach(currTokType.categoryMatches, function(currExtendingType) {
          if (!has(result, currExtendingType)) {
            result[currExtendingType] = idx;
          }
        });
      });
      return result;
    }, []);
    return function() {
      var nextToken = this.LA(1);
      return choiceToAlt_1[nextToken.tokenTypeIdx];
    };
  } else {
    return function() {
      for (var t2 = 0; t2 < numOfAlts; t2++) {
        var currAlt = alts[t2];
        var currNumOfPaths = currAlt.length;
        nextPath: for (var j2 = 0; j2 < currNumOfPaths; j2++) {
          var currPath = currAlt[j2];
          var currPathLength = currPath.length;
          for (var i2 = 0; i2 < currPathLength; i2++) {
            var nextToken = this.LA(i2 + 1);
            if (tokenMatcher2(nextToken, currPath[i2]) === false) {
              continue nextPath;
            }
          }
          return t2;
        }
      }
      return void 0;
    };
  }
}
function buildSingleAlternativeLookaheadFunction(alt, tokenMatcher2, dynamicTokensEnabled) {
  var areAllOneTokenLookahead = every(alt, function(currPath) {
    return currPath.length === 1;
  });
  var numOfPaths = alt.length;
  if (areAllOneTokenLookahead && !dynamicTokensEnabled) {
    var singleTokensTypes = flatten(alt);
    if (singleTokensTypes.length === 1 && isEmpty(singleTokensTypes[0].categoryMatches)) {
      var expectedTokenType = singleTokensTypes[0];
      var expectedTokenUniqueKey_1 = expectedTokenType.tokenTypeIdx;
      return function() {
        return this.LA(1).tokenTypeIdx === expectedTokenUniqueKey_1;
      };
    } else {
      var choiceToAlt_2 = reduce(singleTokensTypes, function(result, currTokType, idx) {
        result[currTokType.tokenTypeIdx] = true;
        forEach(currTokType.categoryMatches, function(currExtendingType) {
          result[currExtendingType] = true;
        });
        return result;
      }, []);
      return function() {
        var nextToken = this.LA(1);
        return choiceToAlt_2[nextToken.tokenTypeIdx] === true;
      };
    }
  } else {
    return function() {
      nextPath: for (var j2 = 0; j2 < numOfPaths; j2++) {
        var currPath = alt[j2];
        var currPathLength = currPath.length;
        for (var i2 = 0; i2 < currPathLength; i2++) {
          var nextToken = this.LA(i2 + 1);
          if (tokenMatcher2(nextToken, currPath[i2]) === false) {
            continue nextPath;
          }
        }
        return true;
      }
      return false;
    };
  }
}
var RestDefinitionFinderWalker = (
  /** @class */
  (function(_super) {
    __extends$3(RestDefinitionFinderWalker2, _super);
    function RestDefinitionFinderWalker2(topProd, targetOccurrence, targetProdType) {
      var _this = _super.call(this) || this;
      _this.topProd = topProd;
      _this.targetOccurrence = targetOccurrence;
      _this.targetProdType = targetProdType;
      return _this;
    }
    RestDefinitionFinderWalker2.prototype.startWalking = function() {
      this.walk(this.topProd);
      return this.restDef;
    };
    RestDefinitionFinderWalker2.prototype.checkIsTarget = function(node, expectedProdType, currRest, prevRest) {
      if (node.idx === this.targetOccurrence && this.targetProdType === expectedProdType) {
        this.restDef = currRest.concat(prevRest);
        return true;
      }
      return false;
    };
    RestDefinitionFinderWalker2.prototype.walkOption = function(optionProd, currRest, prevRest) {
      if (!this.checkIsTarget(optionProd, PROD_TYPE.OPTION, currRest, prevRest)) {
        _super.prototype.walkOption.call(this, optionProd, currRest, prevRest);
      }
    };
    RestDefinitionFinderWalker2.prototype.walkAtLeastOne = function(atLeastOneProd, currRest, prevRest) {
      if (!this.checkIsTarget(atLeastOneProd, PROD_TYPE.REPETITION_MANDATORY, currRest, prevRest)) {
        _super.prototype.walkOption.call(this, atLeastOneProd, currRest, prevRest);
      }
    };
    RestDefinitionFinderWalker2.prototype.walkAtLeastOneSep = function(atLeastOneSepProd, currRest, prevRest) {
      if (!this.checkIsTarget(atLeastOneSepProd, PROD_TYPE.REPETITION_MANDATORY_WITH_SEPARATOR, currRest, prevRest)) {
        _super.prototype.walkOption.call(this, atLeastOneSepProd, currRest, prevRest);
      }
    };
    RestDefinitionFinderWalker2.prototype.walkMany = function(manyProd, currRest, prevRest) {
      if (!this.checkIsTarget(manyProd, PROD_TYPE.REPETITION, currRest, prevRest)) {
        _super.prototype.walkOption.call(this, manyProd, currRest, prevRest);
      }
    };
    RestDefinitionFinderWalker2.prototype.walkManySep = function(manySepProd, currRest, prevRest) {
      if (!this.checkIsTarget(manySepProd, PROD_TYPE.REPETITION_WITH_SEPARATOR, currRest, prevRest)) {
        _super.prototype.walkOption.call(this, manySepProd, currRest, prevRest);
      }
    };
    return RestDefinitionFinderWalker2;
  })(RestWalker)
);
var InsideDefinitionFinderVisitor = (
  /** @class */
  (function(_super) {
    __extends$3(InsideDefinitionFinderVisitor2, _super);
    function InsideDefinitionFinderVisitor2(targetOccurrence, targetProdType, targetRef) {
      var _this = _super.call(this) || this;
      _this.targetOccurrence = targetOccurrence;
      _this.targetProdType = targetProdType;
      _this.targetRef = targetRef;
      _this.result = [];
      return _this;
    }
    InsideDefinitionFinderVisitor2.prototype.checkIsTarget = function(node, expectedProdName) {
      if (node.idx === this.targetOccurrence && this.targetProdType === expectedProdName && (this.targetRef === void 0 || node === this.targetRef)) {
        this.result = node.definition;
      }
    };
    InsideDefinitionFinderVisitor2.prototype.visitOption = function(node) {
      this.checkIsTarget(node, PROD_TYPE.OPTION);
    };
    InsideDefinitionFinderVisitor2.prototype.visitRepetition = function(node) {
      this.checkIsTarget(node, PROD_TYPE.REPETITION);
    };
    InsideDefinitionFinderVisitor2.prototype.visitRepetitionMandatory = function(node) {
      this.checkIsTarget(node, PROD_TYPE.REPETITION_MANDATORY);
    };
    InsideDefinitionFinderVisitor2.prototype.visitRepetitionMandatoryWithSeparator = function(node) {
      this.checkIsTarget(node, PROD_TYPE.REPETITION_MANDATORY_WITH_SEPARATOR);
    };
    InsideDefinitionFinderVisitor2.prototype.visitRepetitionWithSeparator = function(node) {
      this.checkIsTarget(node, PROD_TYPE.REPETITION_WITH_SEPARATOR);
    };
    InsideDefinitionFinderVisitor2.prototype.visitAlternation = function(node) {
      this.checkIsTarget(node, PROD_TYPE.ALTERNATION);
    };
    return InsideDefinitionFinderVisitor2;
  })(GAstVisitor)
);
function initializeArrayOfArrays(size) {
  var result = new Array(size);
  for (var i2 = 0; i2 < size; i2++) {
    result[i2] = [];
  }
  return result;
}
function pathToHashKeys(path) {
  var keys2 = [""];
  for (var i2 = 0; i2 < path.length; i2++) {
    var tokType = path[i2];
    var longerKeys = [];
    for (var j2 = 0; j2 < keys2.length; j2++) {
      var currShorterKey = keys2[j2];
      longerKeys.push(currShorterKey + "_" + tokType.tokenTypeIdx);
      for (var t2 = 0; t2 < tokType.categoryMatches.length; t2++) {
        var categoriesKeySuffix = "_" + tokType.categoryMatches[t2];
        longerKeys.push(currShorterKey + categoriesKeySuffix);
      }
    }
    keys2 = longerKeys;
  }
  return keys2;
}
function isUniquePrefixHash(altKnownPathsKeys, searchPathKeys, idx) {
  for (var currAltIdx = 0; currAltIdx < altKnownPathsKeys.length; currAltIdx++) {
    if (currAltIdx === idx) {
      continue;
    }
    var otherAltKnownPathsKeys = altKnownPathsKeys[currAltIdx];
    for (var searchIdx = 0; searchIdx < searchPathKeys.length; searchIdx++) {
      var searchKey = searchPathKeys[searchIdx];
      if (otherAltKnownPathsKeys[searchKey] === true) {
        return false;
      }
    }
  }
  return true;
}
function lookAheadSequenceFromAlternatives(altsDefs, k2) {
  var partialAlts = map(altsDefs, function(currAlt) {
    return possiblePathsFrom([currAlt], 1);
  });
  var finalResult = initializeArrayOfArrays(partialAlts.length);
  var altsHashes = map(partialAlts, function(currAltPaths) {
    var dict = {};
    forEach(currAltPaths, function(item) {
      var keys2 = pathToHashKeys(item.partialPath);
      forEach(keys2, function(currKey) {
        dict[currKey] = true;
      });
    });
    return dict;
  });
  var newData = partialAlts;
  for (var pathLength = 1; pathLength <= k2; pathLength++) {
    var currDataset = newData;
    newData = initializeArrayOfArrays(currDataset.length);
    var _loop_1 = function(altIdx2) {
      var currAltPathsAndSuffixes = currDataset[altIdx2];
      for (var currPathIdx = 0; currPathIdx < currAltPathsAndSuffixes.length; currPathIdx++) {
        var currPathPrefix = currAltPathsAndSuffixes[currPathIdx].partialPath;
        var suffixDef = currAltPathsAndSuffixes[currPathIdx].suffixDef;
        var prefixKeys = pathToHashKeys(currPathPrefix);
        var isUnique = isUniquePrefixHash(altsHashes, prefixKeys, altIdx2);
        if (isUnique || isEmpty(suffixDef) || currPathPrefix.length === k2) {
          var currAltResult = finalResult[altIdx2];
          if (containsPath(currAltResult, currPathPrefix) === false) {
            currAltResult.push(currPathPrefix);
            for (var j2 = 0; j2 < prefixKeys.length; j2++) {
              var currKey = prefixKeys[j2];
              altsHashes[altIdx2][currKey] = true;
            }
          }
        } else {
          var newPartialPathsAndSuffixes = possiblePathsFrom(suffixDef, pathLength + 1, currPathPrefix);
          newData[altIdx2] = newData[altIdx2].concat(newPartialPathsAndSuffixes);
          forEach(newPartialPathsAndSuffixes, function(item) {
            var prefixKeys2 = pathToHashKeys(item.partialPath);
            forEach(prefixKeys2, function(key) {
              altsHashes[altIdx2][key] = true;
            });
          });
        }
      }
    };
    for (var altIdx = 0; altIdx < currDataset.length; altIdx++) {
      _loop_1(altIdx);
    }
  }
  return finalResult;
}
function getLookaheadPathsForOr(occurrence, ruleGrammar, k2, orProd) {
  var visitor = new InsideDefinitionFinderVisitor(occurrence, PROD_TYPE.ALTERNATION, orProd);
  ruleGrammar.accept(visitor);
  return lookAheadSequenceFromAlternatives(visitor.result, k2);
}
function getLookaheadPathsForOptionalProd(occurrence, ruleGrammar, prodType, k2) {
  var insideDefVisitor = new InsideDefinitionFinderVisitor(occurrence, prodType);
  ruleGrammar.accept(insideDefVisitor);
  var insideDef = insideDefVisitor.result;
  var afterDefWalker = new RestDefinitionFinderWalker(ruleGrammar, occurrence, prodType);
  var afterDef = afterDefWalker.startWalking();
  var insideFlat = new Flat({ definition: insideDef });
  var afterFlat = new Flat({ definition: afterDef });
  return lookAheadSequenceFromAlternatives([insideFlat, afterFlat], k2);
}
function containsPath(alternative, searchPath) {
  compareOtherPath: for (var i2 = 0; i2 < alternative.length; i2++) {
    var otherPath = alternative[i2];
    if (otherPath.length !== searchPath.length) {
      continue;
    }
    for (var j2 = 0; j2 < otherPath.length; j2++) {
      var searchTok = searchPath[j2];
      var otherTok = otherPath[j2];
      var matchingTokens = searchTok === otherTok || otherTok.categoryMatchesMap[searchTok.tokenTypeIdx] !== void 0;
      if (matchingTokens === false) {
        continue compareOtherPath;
      }
    }
    return true;
  }
  return false;
}
function isStrictPrefixOfPath(prefix, other) {
  return prefix.length < other.length && every(prefix, function(tokType, idx) {
    var otherTokType = other[idx];
    return tokType === otherTokType || otherTokType.categoryMatchesMap[tokType.tokenTypeIdx];
  });
}
function areTokenCategoriesNotUsed(lookAheadPaths) {
  return every(lookAheadPaths, function(singleAltPaths) {
    return every(singleAltPaths, function(singlePath) {
      return every(singlePath, function(token) {
        return isEmpty(token.categoryMatches);
      });
    });
  });
}
var __extends$2 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
function validateGrammar$1(topLevels, globalMaxLookahead, tokenTypes, ignoredIssues, errMsgProvider, grammarName) {
  var duplicateErrors = map(topLevels, function(currTopLevel) {
    return validateDuplicateProductions(currTopLevel, errMsgProvider);
  });
  var leftRecursionErrors = map(topLevels, function(currTopRule) {
    return validateNoLeftRecursion(currTopRule, currTopRule, errMsgProvider);
  });
  var emptyAltErrors = [];
  var ambiguousAltsErrors = [];
  var emptyRepetitionErrors = [];
  if (every(leftRecursionErrors, isEmpty)) {
    emptyAltErrors = map(topLevels, function(currTopRule) {
      return validateEmptyOrAlternative(currTopRule, errMsgProvider);
    });
    ambiguousAltsErrors = map(topLevels, function(currTopRule) {
      return validateAmbiguousAlternationAlternatives(currTopRule, globalMaxLookahead, ignoredIssues, errMsgProvider);
    });
    emptyRepetitionErrors = validateSomeNonEmptyLookaheadPath(topLevels, globalMaxLookahead, errMsgProvider);
  }
  var termsNamespaceConflictErrors = checkTerminalAndNoneTerminalsNameSpace(topLevels, tokenTypes, errMsgProvider);
  var tokenNameErrors = map(tokenTypes, function(currTokType) {
    return validateTokenName(currTokType, errMsgProvider);
  });
  var nestedRulesNameErrors = validateNestedRulesNames(topLevels, errMsgProvider);
  var nestedRulesDuplicateErrors = validateDuplicateNestedRules(topLevels, errMsgProvider);
  var tooManyAltsErrors = map(topLevels, function(curRule) {
    return validateTooManyAlts(curRule, errMsgProvider);
  });
  var ruleNameErrors = map(topLevels, function(curRule) {
    return validateRuleName(curRule, errMsgProvider);
  });
  var duplicateRulesError = map(topLevels, function(curRule) {
    return validateRuleDoesNotAlreadyExist(curRule, topLevels, grammarName, errMsgProvider);
  });
  return flatten(duplicateErrors.concat(tokenNameErrors, nestedRulesNameErrors, nestedRulesDuplicateErrors, emptyRepetitionErrors, leftRecursionErrors, emptyAltErrors, ambiguousAltsErrors, termsNamespaceConflictErrors, tooManyAltsErrors, ruleNameErrors, duplicateRulesError));
}
function validateNestedRulesNames(topLevels, errMsgProvider) {
  var result = [];
  forEach(topLevels, function(curTopLevel) {
    var namedCollectorVisitor = new NamedDSLMethodsCollectorVisitor("");
    curTopLevel.accept(namedCollectorVisitor);
    var nestedProds = map(namedCollectorVisitor.result, function(currItem) {
      return currItem.orgProd;
    });
    result.push(map(nestedProds, function(currNestedProd) {
      return validateNestedRuleName(curTopLevel, currNestedProd, errMsgProvider);
    }));
  });
  return flatten(result);
}
function validateDuplicateProductions(topLevelRule, errMsgProvider) {
  var collectorVisitor2 = new OccurrenceValidationCollector();
  topLevelRule.accept(collectorVisitor2);
  var allRuleProductions = collectorVisitor2.allProductions;
  var productionGroups = groupBy(allRuleProductions, identifyProductionForDuplicates);
  var duplicates = pick(productionGroups, function(currGroup) {
    return currGroup.length > 1;
  });
  var errors = map(values(duplicates), function(currDuplicates) {
    var firstProd = first$1(currDuplicates);
    var msg = errMsgProvider.buildDuplicateFoundError(topLevelRule, currDuplicates);
    var dslName = getProductionDslName(firstProd);
    var defError = {
      message: msg,
      type: ParserDefinitionErrorType.DUPLICATE_PRODUCTIONS,
      ruleName: topLevelRule.name,
      dslName,
      occurrence: firstProd.idx
    };
    var param = getExtraProductionArgument(firstProd);
    if (param) {
      defError.parameter = param;
    }
    return defError;
  });
  return errors;
}
function identifyProductionForDuplicates(prod) {
  return getProductionDslName(prod) + "_#_" + prod.idx + "_#_" + getExtraProductionArgument(prod);
}
function getExtraProductionArgument(prod) {
  if (prod instanceof Terminal) {
    return prod.terminalType.name;
  } else if (prod instanceof NonTerminal) {
    return prod.nonTerminalName;
  } else {
    return "";
  }
}
var OccurrenceValidationCollector = (
  /** @class */
  (function(_super) {
    __extends$2(OccurrenceValidationCollector2, _super);
    function OccurrenceValidationCollector2() {
      var _this = _super !== null && _super.apply(this, arguments) || this;
      _this.allProductions = [];
      return _this;
    }
    OccurrenceValidationCollector2.prototype.visitNonTerminal = function(subrule) {
      this.allProductions.push(subrule);
    };
    OccurrenceValidationCollector2.prototype.visitOption = function(option) {
      this.allProductions.push(option);
    };
    OccurrenceValidationCollector2.prototype.visitRepetitionWithSeparator = function(manySep) {
      this.allProductions.push(manySep);
    };
    OccurrenceValidationCollector2.prototype.visitRepetitionMandatory = function(atLeastOne) {
      this.allProductions.push(atLeastOne);
    };
    OccurrenceValidationCollector2.prototype.visitRepetitionMandatoryWithSeparator = function(atLeastOneSep) {
      this.allProductions.push(atLeastOneSep);
    };
    OccurrenceValidationCollector2.prototype.visitRepetition = function(many) {
      this.allProductions.push(many);
    };
    OccurrenceValidationCollector2.prototype.visitAlternation = function(or2) {
      this.allProductions.push(or2);
    };
    OccurrenceValidationCollector2.prototype.visitTerminal = function(terminal) {
      this.allProductions.push(terminal);
    };
    return OccurrenceValidationCollector2;
  })(GAstVisitor)
);
var validTermsPattern = /^[a-zA-Z_]\w*$/;
var validNestedRuleName = new RegExp(validTermsPattern.source.replace("^", "^\\$"));
function validateRuleName(rule, errMsgProvider) {
  var errors = [];
  var ruleName = rule.name;
  if (!ruleName.match(validTermsPattern)) {
    errors.push({
      message: errMsgProvider.buildInvalidRuleNameError({
        topLevelRule: rule,
        expectedPattern: validTermsPattern
      }),
      type: ParserDefinitionErrorType.INVALID_RULE_NAME,
      ruleName
    });
  }
  return errors;
}
function validateNestedRuleName(topLevel, nestedProd, errMsgProvider) {
  var errors = [];
  var errMsg;
  if (!nestedProd.name.match(validNestedRuleName)) {
    errMsg = errMsgProvider.buildInvalidNestedRuleNameError(topLevel, nestedProd);
    errors.push({
      message: errMsg,
      type: ParserDefinitionErrorType.INVALID_NESTED_RULE_NAME,
      ruleName: topLevel.name
    });
  }
  return errors;
}
function validateTokenName(tokenType, errMsgProvider) {
  var errors = [];
  var tokTypeName = tokenType.name;
  if (!tokTypeName.match(validTermsPattern)) {
    errors.push({
      message: errMsgProvider.buildTokenNameError({
        tokenType,
        expectedPattern: validTermsPattern
      }),
      type: ParserDefinitionErrorType.INVALID_TOKEN_NAME
    });
  }
  return errors;
}
function validateRuleDoesNotAlreadyExist(rule, allRules, className, errMsgProvider) {
  var errors = [];
  var occurrences = reduce(allRules, function(result, curRule) {
    if (curRule.name === rule.name) {
      return result + 1;
    }
    return result;
  }, 0);
  if (occurrences > 1) {
    var errMsg = errMsgProvider.buildDuplicateRuleNameError({
      topLevelRule: rule,
      grammarName: className
    });
    errors.push({
      message: errMsg,
      type: ParserDefinitionErrorType.DUPLICATE_RULE_NAME,
      ruleName: rule.name
    });
  }
  return errors;
}
function validateRuleIsOverridden(ruleName, definedRulesNames, className) {
  var errors = [];
  var errMsg;
  if (!contains(definedRulesNames, ruleName)) {
    errMsg = "Invalid rule override, rule: ->" + ruleName + "<- cannot be overridden in the grammar: ->" + className + "<-as it is not defined in any of the super grammars ";
    errors.push({
      message: errMsg,
      type: ParserDefinitionErrorType.INVALID_RULE_OVERRIDE,
      ruleName
    });
  }
  return errors;
}
function validateNoLeftRecursion(topRule, currRule, errMsgProvider, path) {
  if (path === void 0) {
    path = [];
  }
  var errors = [];
  var nextNonTerminals = getFirstNoneTerminal(currRule.definition);
  if (isEmpty(nextNonTerminals)) {
    return [];
  } else {
    var ruleName = topRule.name;
    var foundLeftRecursion = contains(nextNonTerminals, topRule);
    if (foundLeftRecursion) {
      errors.push({
        message: errMsgProvider.buildLeftRecursionError({
          topLevelRule: topRule,
          leftRecursionPath: path
        }),
        type: ParserDefinitionErrorType.LEFT_RECURSION,
        ruleName
      });
    }
    var validNextSteps = difference(nextNonTerminals, path.concat([topRule]));
    var errorsFromNextSteps = map(validNextSteps, function(currRefRule) {
      var newPath = cloneArr(path);
      newPath.push(currRefRule);
      return validateNoLeftRecursion(topRule, currRefRule, errMsgProvider, newPath);
    });
    return errors.concat(flatten(errorsFromNextSteps));
  }
}
function getFirstNoneTerminal(definition) {
  var result = [];
  if (isEmpty(definition)) {
    return result;
  }
  var firstProd = first$1(definition);
  if (firstProd instanceof NonTerminal) {
    result.push(firstProd.referencedRule);
  } else if (firstProd instanceof Flat || firstProd instanceof Option2 || firstProd instanceof RepetitionMandatory || firstProd instanceof RepetitionMandatoryWithSeparator || firstProd instanceof RepetitionWithSeparator || firstProd instanceof Repetition) {
    result = result.concat(getFirstNoneTerminal(firstProd.definition));
  } else if (firstProd instanceof Alternation) {
    result = flatten(map(firstProd.definition, function(currSubDef) {
      return getFirstNoneTerminal(currSubDef.definition);
    }));
  } else if (firstProd instanceof Terminal) ;
  else {
    throw Error("non exhaustive match");
  }
  var isFirstOptional = isOptionalProd(firstProd);
  var hasMore = definition.length > 1;
  if (isFirstOptional && hasMore) {
    var rest = drop(definition);
    return result.concat(getFirstNoneTerminal(rest));
  } else {
    return result;
  }
}
var OrCollector = (
  /** @class */
  (function(_super) {
    __extends$2(OrCollector2, _super);
    function OrCollector2() {
      var _this = _super !== null && _super.apply(this, arguments) || this;
      _this.alternations = [];
      return _this;
    }
    OrCollector2.prototype.visitAlternation = function(node) {
      this.alternations.push(node);
    };
    return OrCollector2;
  })(GAstVisitor)
);
function validateEmptyOrAlternative(topLevelRule, errMsgProvider) {
  var orCollector = new OrCollector();
  topLevelRule.accept(orCollector);
  var ors = orCollector.alternations;
  var errors = reduce(ors, function(errors2, currOr) {
    var exceptLast = dropRight(currOr.definition);
    var currErrors = map(exceptLast, function(currAlternative, currAltIdx) {
      var possibleFirstInAlt = nextPossibleTokensAfter([currAlternative], [], null, 1);
      if (isEmpty(possibleFirstInAlt)) {
        return {
          message: errMsgProvider.buildEmptyAlternationError({
            topLevelRule,
            alternation: currOr,
            emptyChoiceIdx: currAltIdx
          }),
          type: ParserDefinitionErrorType.NONE_LAST_EMPTY_ALT,
          ruleName: topLevelRule.name,
          occurrence: currOr.idx,
          alternative: currAltIdx + 1
        };
      } else {
        return null;
      }
    });
    return errors2.concat(compact(currErrors));
  }, []);
  return errors;
}
function validateAmbiguousAlternationAlternatives(topLevelRule, globalMaxLookahead, ignoredIssues, errMsgProvider) {
  var orCollector = new OrCollector();
  topLevelRule.accept(orCollector);
  var ors = orCollector.alternations;
  var ignoredIssuesForCurrentRule = ignoredIssues[topLevelRule.name];
  if (ignoredIssuesForCurrentRule) {
    ors = reject(ors, function(currOr) {
      return ignoredIssuesForCurrentRule[getProductionDslName(currOr) + (currOr.idx === 0 ? "" : currOr.idx)];
    });
  }
  ors = reject(ors, function(currOr) {
    return currOr.ignoreAmbiguities === true;
  });
  var errors = reduce(ors, function(result, currOr) {
    var currOccurrence = currOr.idx;
    var actualMaxLookahead = currOr.maxLookahead || globalMaxLookahead;
    var alternatives = getLookaheadPathsForOr(currOccurrence, topLevelRule, actualMaxLookahead, currOr);
    var altsAmbiguityErrors = checkAlternativesAmbiguities(alternatives, currOr, topLevelRule, errMsgProvider);
    var altsPrefixAmbiguityErrors = checkPrefixAlternativesAmbiguities(alternatives, currOr, topLevelRule, errMsgProvider);
    return result.concat(altsAmbiguityErrors, altsPrefixAmbiguityErrors);
  }, []);
  return errors;
}
var RepetionCollector = (
  /** @class */
  (function(_super) {
    __extends$2(RepetionCollector2, _super);
    function RepetionCollector2() {
      var _this = _super !== null && _super.apply(this, arguments) || this;
      _this.allProductions = [];
      return _this;
    }
    RepetionCollector2.prototype.visitRepetitionWithSeparator = function(manySep) {
      this.allProductions.push(manySep);
    };
    RepetionCollector2.prototype.visitRepetitionMandatory = function(atLeastOne) {
      this.allProductions.push(atLeastOne);
    };
    RepetionCollector2.prototype.visitRepetitionMandatoryWithSeparator = function(atLeastOneSep) {
      this.allProductions.push(atLeastOneSep);
    };
    RepetionCollector2.prototype.visitRepetition = function(many) {
      this.allProductions.push(many);
    };
    return RepetionCollector2;
  })(GAstVisitor)
);
function validateTooManyAlts(topLevelRule, errMsgProvider) {
  var orCollector = new OrCollector();
  topLevelRule.accept(orCollector);
  var ors = orCollector.alternations;
  var errors = reduce(ors, function(errors2, currOr) {
    if (currOr.definition.length > 255) {
      errors2.push({
        message: errMsgProvider.buildTooManyAlternativesError({
          topLevelRule,
          alternation: currOr
        }),
        type: ParserDefinitionErrorType.TOO_MANY_ALTS,
        ruleName: topLevelRule.name,
        occurrence: currOr.idx
      });
    }
    return errors2;
  }, []);
  return errors;
}
function validateSomeNonEmptyLookaheadPath(topLevelRules, maxLookahead, errMsgProvider) {
  var errors = [];
  forEach(topLevelRules, function(currTopRule) {
    var collectorVisitor2 = new RepetionCollector();
    currTopRule.accept(collectorVisitor2);
    var allRuleProductions = collectorVisitor2.allProductions;
    forEach(allRuleProductions, function(currProd) {
      var prodType = getProdType(currProd);
      var actualMaxLookahead = currProd.maxLookahead || maxLookahead;
      var currOccurrence = currProd.idx;
      var paths = getLookaheadPathsForOptionalProd(currOccurrence, currTopRule, prodType, actualMaxLookahead);
      var pathsInsideProduction = paths[0];
      if (isEmpty(flatten(pathsInsideProduction))) {
        var errMsg = errMsgProvider.buildEmptyRepetitionError({
          topLevelRule: currTopRule,
          repetition: currProd
        });
        errors.push({
          message: errMsg,
          type: ParserDefinitionErrorType.NO_NON_EMPTY_LOOKAHEAD,
          ruleName: currTopRule.name
        });
      }
    });
  });
  return errors;
}
function checkAlternativesAmbiguities(alternatives, alternation, rule, errMsgProvider) {
  var foundAmbiguousPaths = [];
  var identicalAmbiguities = reduce(alternatives, function(result, currAlt, currAltIdx) {
    if (alternation.definition[currAltIdx].ignoreAmbiguities === true) {
      return result;
    }
    forEach(currAlt, function(currPath) {
      var altsCurrPathAppearsIn = [currAltIdx];
      forEach(alternatives, function(currOtherAlt, currOtherAltIdx) {
        if (currAltIdx !== currOtherAltIdx && containsPath(currOtherAlt, currPath) && // ignore (skip) ambiguities with this "other" alternative
        alternation.definition[currOtherAltIdx].ignoreAmbiguities !== true) {
          altsCurrPathAppearsIn.push(currOtherAltIdx);
        }
      });
      if (altsCurrPathAppearsIn.length > 1 && !containsPath(foundAmbiguousPaths, currPath)) {
        foundAmbiguousPaths.push(currPath);
        result.push({
          alts: altsCurrPathAppearsIn,
          path: currPath
        });
      }
    });
    return result;
  }, []);
  var currErrors = map(identicalAmbiguities, function(currAmbDescriptor) {
    var ambgIndices = map(currAmbDescriptor.alts, function(currAltIdx) {
      return currAltIdx + 1;
    });
    var currMessage = errMsgProvider.buildAlternationAmbiguityError({
      topLevelRule: rule,
      alternation,
      ambiguityIndices: ambgIndices,
      prefixPath: currAmbDescriptor.path
    });
    return {
      message: currMessage,
      type: ParserDefinitionErrorType.AMBIGUOUS_ALTS,
      ruleName: rule.name,
      occurrence: alternation.idx,
      alternatives: [currAmbDescriptor.alts]
    };
  });
  return currErrors;
}
function checkPrefixAlternativesAmbiguities(alternatives, alternation, rule, errMsgProvider) {
  var errors = [];
  var pathsAndIndices = reduce(alternatives, function(result, currAlt, idx) {
    var currPathsAndIdx = map(currAlt, function(currPath) {
      return { idx, path: currPath };
    });
    return result.concat(currPathsAndIdx);
  }, []);
  forEach(pathsAndIndices, function(currPathAndIdx) {
    var alternativeGast = alternation.definition[currPathAndIdx.idx];
    if (alternativeGast.ignoreAmbiguities === true) {
      return;
    }
    var targetIdx = currPathAndIdx.idx;
    var targetPath = currPathAndIdx.path;
    var prefixAmbiguitiesPathsAndIndices = findAll(pathsAndIndices, function(searchPathAndIdx) {
      return (
        // ignore (skip) ambiguities with this "other" alternative
        alternation.definition[searchPathAndIdx.idx].ignoreAmbiguities !== true && searchPathAndIdx.idx < targetIdx && // checking for strict prefix because identical lookaheads
        // will be be detected using a different validation.
        isStrictPrefixOfPath(searchPathAndIdx.path, targetPath)
      );
    });
    var currPathPrefixErrors = map(prefixAmbiguitiesPathsAndIndices, function(currAmbPathAndIdx) {
      var ambgIndices = [currAmbPathAndIdx.idx + 1, targetIdx + 1];
      var occurrence = alternation.idx === 0 ? "" : alternation.idx;
      var message = errMsgProvider.buildAlternationPrefixAmbiguityError({
        topLevelRule: rule,
        alternation,
        ambiguityIndices: ambgIndices,
        prefixPath: currAmbPathAndIdx.path
      });
      return {
        message,
        type: ParserDefinitionErrorType.AMBIGUOUS_PREFIX_ALTS,
        ruleName: rule.name,
        occurrence,
        alternatives: ambgIndices
      };
    });
    errors = errors.concat(currPathPrefixErrors);
  });
  return errors;
}
function checkTerminalAndNoneTerminalsNameSpace(topLevels, tokenTypes, errMsgProvider) {
  var errors = [];
  var tokenNames = map(tokenTypes, function(currToken) {
    return currToken.name;
  });
  forEach(topLevels, function(currRule) {
    var currRuleName = currRule.name;
    if (contains(tokenNames, currRuleName)) {
      var errMsg = errMsgProvider.buildNamespaceConflictError(currRule);
      errors.push({
        message: errMsg,
        type: ParserDefinitionErrorType.CONFLICT_TOKENS_RULES_NAMESPACE,
        ruleName: currRuleName
      });
    }
  });
  return errors;
}
function validateDuplicateNestedRules(topLevelRules, errMsgProvider) {
  var errors = [];
  forEach(topLevelRules, function(currTopRule) {
    var namedCollectorVisitor = new NamedDSLMethodsCollectorVisitor("");
    currTopRule.accept(namedCollectorVisitor);
    var prodsByGroup = groupBy(namedCollectorVisitor.result, function(item) {
      return item.name;
    });
    var duplicates = pick(prodsByGroup, function(currGroup) {
      return currGroup.length > 1;
    });
    forEach(values(duplicates), function(currDupGroup) {
      var currDupProds = map(currDupGroup, function(dupGroup) {
        return dupGroup.orgProd;
      });
      var errMsg = errMsgProvider.buildDuplicateNestedRuleNameError(currTopRule, currDupProds);
      errors.push({
        message: errMsg,
        type: ParserDefinitionErrorType.DUPLICATE_NESTED_NAME,
        ruleName: currTopRule.name
      });
    });
  });
  return errors;
}
var defaultParserErrorProvider = {
  buildMismatchTokenMessage: function(_a2) {
    var expected = _a2.expected, actual = _a2.actual;
    _a2.previous;
    _a2.ruleName;
    var hasLabel = hasTokenLabel(expected);
    var expectedMsg = hasLabel ? "--> " + tokenLabel(expected) + " <--" : "token of type --> " + expected.name + " <--";
    var msg = "Expecting " + expectedMsg + " but found --> '" + actual.image + "' <--";
    return msg;
  },
  buildNotAllInputParsedMessage: function(_a2) {
    var firstRedundant = _a2.firstRedundant;
    _a2.ruleName;
    return "Redundant input, expecting EOF but found: " + firstRedundant.image;
  },
  buildNoViableAltMessage: function(_a2) {
    var expectedPathsPerAlt = _a2.expectedPathsPerAlt, actual = _a2.actual;
    _a2.previous;
    var customUserDescription = _a2.customUserDescription;
    _a2.ruleName;
    var errPrefix = "Expecting: ";
    var actualText = first$1(actual).image;
    var errSuffix = "\nbut found: '" + actualText + "'";
    if (customUserDescription) {
      return errPrefix + customUserDescription + errSuffix;
    } else {
      var allLookAheadPaths = reduce(expectedPathsPerAlt, function(result, currAltPaths) {
        return result.concat(currAltPaths);
      }, []);
      var nextValidTokenSequences = map(allLookAheadPaths, function(currPath) {
        return "[" + map(currPath, function(currTokenType) {
          return tokenLabel(currTokenType);
        }).join(", ") + "]";
      });
      var nextValidSequenceItems = map(nextValidTokenSequences, function(itemMsg, idx) {
        return "  " + (idx + 1) + ". " + itemMsg;
      });
      var calculatedDescription = "one of these possible Token sequences:\n" + nextValidSequenceItems.join("\n");
      return errPrefix + calculatedDescription + errSuffix;
    }
  },
  buildEarlyExitMessage: function(_a2) {
    var expectedIterationPaths = _a2.expectedIterationPaths, actual = _a2.actual, customUserDescription = _a2.customUserDescription;
    _a2.ruleName;
    var errPrefix = "Expecting: ";
    var actualText = first$1(actual).image;
    var errSuffix = "\nbut found: '" + actualText + "'";
    if (customUserDescription) {
      return errPrefix + customUserDescription + errSuffix;
    } else {
      var nextValidTokenSequences = map(expectedIterationPaths, function(currPath) {
        return "[" + map(currPath, function(currTokenType) {
          return tokenLabel(currTokenType);
        }).join(",") + "]";
      });
      var calculatedDescription = "expecting at least one iteration which starts with one of these possible Token sequences::\n  " + ("<" + nextValidTokenSequences.join(" ,") + ">");
      return errPrefix + calculatedDescription + errSuffix;
    }
  }
};
Object.freeze(defaultParserErrorProvider);
var defaultGrammarResolverErrorProvider = {
  buildRuleNotFoundError: function(topLevelRule, undefinedRule) {
    var msg = "Invalid grammar, reference to a rule which is not defined: ->" + undefinedRule.nonTerminalName + "<-\ninside top level rule: ->" + topLevelRule.name + "<-";
    return msg;
  }
};
var defaultGrammarValidatorErrorProvider = {
  buildDuplicateFoundError: function(topLevelRule, duplicateProds) {
    function getExtraProductionArgument2(prod) {
      if (prod instanceof Terminal) {
        return prod.terminalType.name;
      } else if (prod instanceof NonTerminal) {
        return prod.nonTerminalName;
      } else {
        return "";
      }
    }
    var topLevelName = topLevelRule.name;
    var duplicateProd = first$1(duplicateProds);
    var index2 = duplicateProd.idx;
    var dslName = getProductionDslName(duplicateProd);
    var extraArgument = getExtraProductionArgument2(duplicateProd);
    var hasExplicitIndex = index2 > 0;
    var msg = "->" + dslName + (hasExplicitIndex ? index2 : "") + "<- " + (extraArgument ? "with argument: ->" + extraArgument + "<-" : "") + "\n                  appears more than once (" + duplicateProds.length + " times) in the top level rule: ->" + topLevelName + "<-.                  \n                  For further details see: https://sap.github.io/chevrotain/docs/FAQ.html#NUMERICAL_SUFFIXES \n                  ";
    msg = msg.replace(/[ \t]+/g, " ");
    msg = msg.replace(/\s\s+/g, "\n");
    return msg;
  },
  buildInvalidNestedRuleNameError: function(topLevelRule, nestedProd) {
    var msg = "Invalid nested rule name: ->" + nestedProd.name + "<- inside rule: ->" + topLevelRule.name + "<-\n" + ("it must match the pattern: ->" + validNestedRuleName.toString() + "<-.\n") + "Note that this means a nested rule name must start with the '$'(dollar) sign.";
    return msg;
  },
  buildDuplicateNestedRuleNameError: function(topLevelRule, nestedProd) {
    var duplicateName = first$1(nestedProd).name;
    var errMsg = "Duplicate nested rule name: ->" + duplicateName + "<- inside rule: ->" + topLevelRule.name + "<-\nA nested name must be unique in the scope of a top level grammar rule.";
    return errMsg;
  },
  buildNamespaceConflictError: function(rule) {
    var errMsg = "Namespace conflict found in grammar.\n" + ("The grammar has both a Terminal(Token) and a Non-Terminal(Rule) named: <" + rule.name + ">.\n") + "To resolve this make sure each Terminal and Non-Terminal names are unique\nThis is easy to accomplish by using the convention that Terminal names start with an uppercase letter\nand Non-Terminal names start with a lower case letter.";
    return errMsg;
  },
  buildAlternationPrefixAmbiguityError: function(options) {
    var pathMsg = map(options.prefixPath, function(currTok) {
      return tokenLabel(currTok);
    }).join(", ");
    var occurrence = options.alternation.idx === 0 ? "" : options.alternation.idx;
    var errMsg = "Ambiguous alternatives: <" + options.ambiguityIndices.join(" ,") + "> due to common lookahead prefix\n" + ("in <OR" + occurrence + "> inside <" + options.topLevelRule.name + "> Rule,\n") + ("<" + pathMsg + "> may appears as a prefix path in all these alternatives.\n") + "See: https://sap.github.io/chevrotain/docs/guide/resolving_grammar_errors.html#COMMON_PREFIX\nFor Further details.";
    return errMsg;
  },
  buildAlternationAmbiguityError: function(options) {
    var pathMsg = map(options.prefixPath, function(currtok) {
      return tokenLabel(currtok);
    }).join(", ");
    var occurrence = options.alternation.idx === 0 ? "" : options.alternation.idx;
    var currMessage = "Ambiguous Alternatives Detected: <" + options.ambiguityIndices.join(" ,") + "> in <OR" + occurrence + ">" + (" inside <" + options.topLevelRule.name + "> Rule,\n") + ("<" + pathMsg + "> may appears as a prefix path in all these alternatives.\n");
    currMessage = currMessage + "See: https://sap.github.io/chevrotain/docs/guide/resolving_grammar_errors.html#AMBIGUOUS_ALTERNATIVES\nFor Further details.";
    return currMessage;
  },
  buildEmptyRepetitionError: function(options) {
    var dslName = getProductionDslName(options.repetition);
    if (options.repetition.idx !== 0) {
      dslName += options.repetition.idx;
    }
    var errMsg = "The repetition <" + dslName + "> within Rule <" + options.topLevelRule.name + "> can never consume any tokens.\nThis could lead to an infinite loop.";
    return errMsg;
  },
  buildTokenNameError: function(options) {
    var tokTypeName = options.tokenType.name;
    var errMsg = "Invalid Grammar Token name: ->" + tokTypeName + "<- it must match the pattern: ->" + options.expectedPattern.toString() + "<-";
    return errMsg;
  },
  buildEmptyAlternationError: function(options) {
    var errMsg = "Ambiguous empty alternative: <" + (options.emptyChoiceIdx + 1) + ">" + (" in <OR" + options.alternation.idx + "> inside <" + options.topLevelRule.name + "> Rule.\n") + "Only the last alternative may be an empty alternative.";
    return errMsg;
  },
  buildTooManyAlternativesError: function(options) {
    var errMsg = "An Alternation cannot have more than 256 alternatives:\n" + ("<OR" + options.alternation.idx + "> inside <" + options.topLevelRule.name + "> Rule.\n has " + (options.alternation.definition.length + 1) + " alternatives.");
    return errMsg;
  },
  buildLeftRecursionError: function(options) {
    var ruleName = options.topLevelRule.name;
    var pathNames = map(options.leftRecursionPath, function(currRule) {
      return currRule.name;
    });
    var leftRecursivePath = ruleName + " --> " + pathNames.concat([ruleName]).join(" --> ");
    var errMsg = "Left Recursion found in grammar.\n" + ("rule: <" + ruleName + "> can be invoked from itself (directly or indirectly)\n") + ("without consuming any Tokens. The grammar path that causes this is: \n " + leftRecursivePath + "\n") + " To fix this refactor your grammar to remove the left recursion.\nsee: https://en.wikipedia.org/wiki/LL_parser#Left_Factoring.";
    return errMsg;
  },
  buildInvalidRuleNameError: function(options) {
    var ruleName = options.topLevelRule.name;
    var expectedPatternString = options.expectedPattern.toString();
    var errMsg = "Invalid grammar rule name: ->" + ruleName + "<- it must match the pattern: ->" + expectedPatternString + "<-";
    return errMsg;
  },
  buildDuplicateRuleNameError: function(options) {
    var ruleName;
    if (options.topLevelRule instanceof Rule) {
      ruleName = options.topLevelRule.name;
    } else {
      ruleName = options.topLevelRule;
    }
    var errMsg = "Duplicate definition, rule: ->" + ruleName + "<- is already defined in the grammar: ->" + options.grammarName + "<-";
    return errMsg;
  }
};
var __extends$1 = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
function resolveGrammar$1(topLevels, errMsgProvider) {
  var refResolver = new GastRefResolverVisitor(topLevels, errMsgProvider);
  refResolver.resolveRefs();
  return refResolver.errors;
}
var GastRefResolverVisitor = (
  /** @class */
  (function(_super) {
    __extends$1(GastRefResolverVisitor2, _super);
    function GastRefResolverVisitor2(nameToTopRule, errMsgProvider) {
      var _this = _super.call(this) || this;
      _this.nameToTopRule = nameToTopRule;
      _this.errMsgProvider = errMsgProvider;
      _this.errors = [];
      return _this;
    }
    GastRefResolverVisitor2.prototype.resolveRefs = function() {
      var _this = this;
      forEach(values(this.nameToTopRule), function(prod) {
        _this.currTopLevel = prod;
        prod.accept(_this);
      });
    };
    GastRefResolverVisitor2.prototype.visitNonTerminal = function(node) {
      var ref = this.nameToTopRule[node.nonTerminalName];
      if (!ref) {
        var msg = this.errMsgProvider.buildRuleNotFoundError(this.currTopLevel, node);
        this.errors.push({
          message: msg,
          type: ParserDefinitionErrorType.UNRESOLVED_SUBRULE_REF,
          ruleName: this.currTopLevel.name,
          unresolvedRefName: node.nonTerminalName
        });
      } else {
        node.referencedRule = ref;
      }
    };
    return GastRefResolverVisitor2;
  })(GAstVisitor)
);
function resolveGrammar(options) {
  options = defaults(options, {
    errMsgProvider: defaultGrammarResolverErrorProvider
  });
  var topRulesTable = {};
  forEach(options.rules, function(rule) {
    topRulesTable[rule.name] = rule;
  });
  return resolveGrammar$1(topRulesTable, options.errMsgProvider);
}
function validateGrammar(options) {
  options = defaults(options, {
    errMsgProvider: defaultGrammarValidatorErrorProvider,
    ignoredIssues: {}
  });
  return validateGrammar$1(options.rules, options.maxLookahead, options.tokenTypes, options.ignoredIssues, options.errMsgProvider, options.grammarName);
}
var MISMATCHED_TOKEN_EXCEPTION = "MismatchedTokenException";
var NO_VIABLE_ALT_EXCEPTION = "NoViableAltException";
var EARLY_EXIT_EXCEPTION = "EarlyExitException";
var NOT_ALL_INPUT_PARSED_EXCEPTION = "NotAllInputParsedException";
var RECOGNITION_EXCEPTION_NAMES = [
  MISMATCHED_TOKEN_EXCEPTION,
  NO_VIABLE_ALT_EXCEPTION,
  EARLY_EXIT_EXCEPTION,
  NOT_ALL_INPUT_PARSED_EXCEPTION
];
Object.freeze(RECOGNITION_EXCEPTION_NAMES);
function isRecognitionException(error) {
  return contains(RECOGNITION_EXCEPTION_NAMES, error.name);
}
function MismatchedTokenException(message, token, previousToken) {
  this.name = MISMATCHED_TOKEN_EXCEPTION;
  this.message = message;
  this.token = token;
  this.previousToken = previousToken;
  this.resyncedTokens = [];
}
MismatchedTokenException.prototype = Error.prototype;
function NoViableAltException(message, token, previousToken) {
  this.name = NO_VIABLE_ALT_EXCEPTION;
  this.message = message;
  this.token = token;
  this.previousToken = previousToken;
  this.resyncedTokens = [];
}
NoViableAltException.prototype = Error.prototype;
function NotAllInputParsedException(message, token) {
  this.name = NOT_ALL_INPUT_PARSED_EXCEPTION;
  this.message = message;
  this.token = token;
  this.resyncedTokens = [];
}
NotAllInputParsedException.prototype = Error.prototype;
function EarlyExitException(message, token, previousToken) {
  this.name = EARLY_EXIT_EXCEPTION;
  this.message = message;
  this.token = token;
  this.previousToken = previousToken;
  this.resyncedTokens = [];
}
EarlyExitException.prototype = Error.prototype;
var EOF_FOLLOW_KEY = {};
var IN_RULE_RECOVERY_EXCEPTION = "InRuleRecoveryException";
function InRuleRecoveryException(message) {
  this.name = IN_RULE_RECOVERY_EXCEPTION;
  this.message = message;
}
InRuleRecoveryException.prototype = Error.prototype;
var Recoverable = (
  /** @class */
  (function() {
    function Recoverable2() {
    }
    Recoverable2.prototype.initRecoverable = function(config) {
      this.firstAfterRepMap = {};
      this.resyncFollows = {};
      this.recoveryEnabled = has(config, "recoveryEnabled") ? config.recoveryEnabled : DEFAULT_PARSER_CONFIG.recoveryEnabled;
      if (this.recoveryEnabled) {
        this.attemptInRepetitionRecovery = attemptInRepetitionRecovery;
      }
    };
    Recoverable2.prototype.getTokenToInsert = function(tokType) {
      var tokToInsert = createTokenInstance(tokType, "", NaN, NaN, NaN, NaN, NaN, NaN);
      tokToInsert.isInsertedInRecovery = true;
      return tokToInsert;
    };
    Recoverable2.prototype.canTokenTypeBeInsertedInRecovery = function(tokType) {
      return true;
    };
    Recoverable2.prototype.tryInRepetitionRecovery = function(grammarRule, grammarRuleArgs, lookAheadFunc, expectedTokType) {
      var _this = this;
      var reSyncTokType = this.findReSyncTokenType();
      var savedLexerState = this.exportLexerState();
      var resyncedTokens = [];
      var passedResyncPoint = false;
      var nextTokenWithoutResync = this.LA(1);
      var currToken = this.LA(1);
      var generateErrorMessage = function() {
        var previousToken = _this.LA(0);
        var msg = _this.errorMessageProvider.buildMismatchTokenMessage({
          expected: expectedTokType,
          actual: nextTokenWithoutResync,
          previous: previousToken,
          ruleName: _this.getCurrRuleFullName()
        });
        var error = new MismatchedTokenException(msg, nextTokenWithoutResync, _this.LA(0));
        error.resyncedTokens = dropRight(resyncedTokens);
        _this.SAVE_ERROR(error);
      };
      while (!passedResyncPoint) {
        if (this.tokenMatcher(currToken, expectedTokType)) {
          generateErrorMessage();
          return;
        } else if (lookAheadFunc.call(this)) {
          generateErrorMessage();
          grammarRule.apply(this, grammarRuleArgs);
          return;
        } else if (this.tokenMatcher(currToken, reSyncTokType)) {
          passedResyncPoint = true;
        } else {
          currToken = this.SKIP_TOKEN();
          this.addToResyncTokens(currToken, resyncedTokens);
        }
      }
      this.importLexerState(savedLexerState);
    };
    Recoverable2.prototype.shouldInRepetitionRecoveryBeTried = function(expectTokAfterLastMatch, nextTokIdx, notStuck) {
      if (notStuck === false) {
        return false;
      }
      if (expectTokAfterLastMatch === void 0 || nextTokIdx === void 0) {
        return false;
      }
      if (this.tokenMatcher(this.LA(1), expectTokAfterLastMatch)) {
        return false;
      }
      if (this.isBackTracking()) {
        return false;
      }
      if (this.canPerformInRuleRecovery(expectTokAfterLastMatch, this.getFollowsForInRuleRecovery(expectTokAfterLastMatch, nextTokIdx))) {
        return false;
      }
      return true;
    };
    Recoverable2.prototype.getFollowsForInRuleRecovery = function(tokType, tokIdxInRule) {
      var grammarPath = this.getCurrentGrammarPath(tokType, tokIdxInRule);
      var follows = this.getNextPossibleTokenTypes(grammarPath);
      return follows;
    };
    Recoverable2.prototype.tryInRuleRecovery = function(expectedTokType, follows) {
      if (this.canRecoverWithSingleTokenInsertion(expectedTokType, follows)) {
        var tokToInsert = this.getTokenToInsert(expectedTokType);
        return tokToInsert;
      }
      if (this.canRecoverWithSingleTokenDeletion(expectedTokType)) {
        var nextTok = this.SKIP_TOKEN();
        this.consumeToken();
        return nextTok;
      }
      throw new InRuleRecoveryException("sad sad panda");
    };
    Recoverable2.prototype.canPerformInRuleRecovery = function(expectedToken, follows) {
      return this.canRecoverWithSingleTokenInsertion(expectedToken, follows) || this.canRecoverWithSingleTokenDeletion(expectedToken);
    };
    Recoverable2.prototype.canRecoverWithSingleTokenInsertion = function(expectedTokType, follows) {
      var _this = this;
      if (!this.canTokenTypeBeInsertedInRecovery(expectedTokType)) {
        return false;
      }
      if (isEmpty(follows)) {
        return false;
      }
      var mismatchedTok = this.LA(1);
      var isMisMatchedTokInFollows = find(follows, function(possibleFollowsTokType) {
        return _this.tokenMatcher(mismatchedTok, possibleFollowsTokType);
      }) !== void 0;
      return isMisMatchedTokInFollows;
    };
    Recoverable2.prototype.canRecoverWithSingleTokenDeletion = function(expectedTokType) {
      var isNextTokenWhatIsExpected = this.tokenMatcher(this.LA(2), expectedTokType);
      return isNextTokenWhatIsExpected;
    };
    Recoverable2.prototype.isInCurrentRuleReSyncSet = function(tokenTypeIdx) {
      var followKey = this.getCurrFollowKey();
      var currentRuleReSyncSet = this.getFollowSetFromFollowKey(followKey);
      return contains(currentRuleReSyncSet, tokenTypeIdx);
    };
    Recoverable2.prototype.findReSyncTokenType = function() {
      var allPossibleReSyncTokTypes = this.flattenFollowSet();
      var nextToken = this.LA(1);
      var k2 = 2;
      while (true) {
        var nextTokenType = nextToken.tokenType;
        if (contains(allPossibleReSyncTokTypes, nextTokenType)) {
          return nextTokenType;
        }
        nextToken = this.LA(k2);
        k2++;
      }
    };
    Recoverable2.prototype.getCurrFollowKey = function() {
      if (this.RULE_STACK.length === 1) {
        return EOF_FOLLOW_KEY;
      }
      var currRuleShortName = this.getLastExplicitRuleShortName();
      var currRuleIdx = this.getLastExplicitRuleOccurrenceIndex();
      var prevRuleShortName = this.getPreviousExplicitRuleShortName();
      return {
        ruleName: this.shortRuleNameToFullName(currRuleShortName),
        idxInCallingRule: currRuleIdx,
        inRule: this.shortRuleNameToFullName(prevRuleShortName)
      };
    };
    Recoverable2.prototype.buildFullFollowKeyStack = function() {
      var _this = this;
      var explicitRuleStack = this.RULE_STACK;
      var explicitOccurrenceStack = this.RULE_OCCURRENCE_STACK;
      if (!isEmpty(this.LAST_EXPLICIT_RULE_STACK)) {
        explicitRuleStack = map(this.LAST_EXPLICIT_RULE_STACK, function(idx) {
          return _this.RULE_STACK[idx];
        });
        explicitOccurrenceStack = map(this.LAST_EXPLICIT_RULE_STACK, function(idx) {
          return _this.RULE_OCCURRENCE_STACK[idx];
        });
      }
      return map(explicitRuleStack, function(ruleName, idx) {
        if (idx === 0) {
          return EOF_FOLLOW_KEY;
        }
        return {
          ruleName: _this.shortRuleNameToFullName(ruleName),
          idxInCallingRule: explicitOccurrenceStack[idx],
          inRule: _this.shortRuleNameToFullName(explicitRuleStack[idx - 1])
        };
      });
    };
    Recoverable2.prototype.flattenFollowSet = function() {
      var _this = this;
      var followStack = map(this.buildFullFollowKeyStack(), function(currKey) {
        return _this.getFollowSetFromFollowKey(currKey);
      });
      return flatten(followStack);
    };
    Recoverable2.prototype.getFollowSetFromFollowKey = function(followKey) {
      if (followKey === EOF_FOLLOW_KEY) {
        return [EOF];
      }
      var followName = followKey.ruleName + followKey.idxInCallingRule + IN + followKey.inRule;
      return this.resyncFollows[followName];
    };
    Recoverable2.prototype.addToResyncTokens = function(token, resyncTokens) {
      if (!this.tokenMatcher(token, EOF)) {
        resyncTokens.push(token);
      }
      return resyncTokens;
    };
    Recoverable2.prototype.reSyncTo = function(tokType) {
      var resyncedTokens = [];
      var nextTok = this.LA(1);
      while (this.tokenMatcher(nextTok, tokType) === false) {
        nextTok = this.SKIP_TOKEN();
        this.addToResyncTokens(nextTok, resyncedTokens);
      }
      return dropRight(resyncedTokens);
    };
    Recoverable2.prototype.attemptInRepetitionRecovery = function(prodFunc, args, lookaheadFunc, dslMethodIdx, prodOccurrence, nextToksWalker, notStuck) {
    };
    Recoverable2.prototype.getCurrentGrammarPath = function(tokType, tokIdxInRule) {
      var pathRuleStack = this.getHumanReadableRuleStack();
      var pathOccurrenceStack = cloneArr(this.RULE_OCCURRENCE_STACK);
      var grammarPath = {
        ruleStack: pathRuleStack,
        occurrenceStack: pathOccurrenceStack,
        lastTok: tokType,
        lastTokOccurrence: tokIdxInRule
      };
      return grammarPath;
    };
    Recoverable2.prototype.getHumanReadableRuleStack = function() {
      var _this = this;
      if (!isEmpty(this.LAST_EXPLICIT_RULE_STACK)) {
        return map(this.LAST_EXPLICIT_RULE_STACK, function(currIdx) {
          return _this.shortRuleNameToFullName(_this.RULE_STACK[currIdx]);
        });
      } else {
        return map(this.RULE_STACK, function(currShortName) {
          return _this.shortRuleNameToFullName(currShortName);
        });
      }
    };
    return Recoverable2;
  })()
);
function attemptInRepetitionRecovery(prodFunc, args, lookaheadFunc, dslMethodIdx, prodOccurrence, nextToksWalker, notStuck) {
  var key = this.getKeyForAutomaticLookahead(dslMethodIdx, prodOccurrence);
  var firstAfterRepInfo = this.firstAfterRepMap[key];
  if (firstAfterRepInfo === void 0) {
    var currRuleName = this.getCurrRuleFullName();
    var ruleGrammar = this.getGAstProductions()[currRuleName];
    var walker = new nextToksWalker(ruleGrammar, prodOccurrence);
    firstAfterRepInfo = walker.startWalking();
    this.firstAfterRepMap[key] = firstAfterRepInfo;
  }
  var expectTokAfterLastMatch = firstAfterRepInfo.token;
  var nextTokIdx = firstAfterRepInfo.occurrence;
  var isEndOfRule = firstAfterRepInfo.isEndOfRule;
  if (this.RULE_STACK.length === 1 && isEndOfRule && expectTokAfterLastMatch === void 0) {
    expectTokAfterLastMatch = EOF;
    nextTokIdx = 1;
  }
  if (this.shouldInRepetitionRecoveryBeTried(expectTokAfterLastMatch, nextTokIdx, notStuck)) {
    this.tryInRepetitionRecovery(prodFunc, args, lookaheadFunc, expectTokAfterLastMatch);
  }
}
var LooksAhead = (
  /** @class */
  (function() {
    function LooksAhead2() {
    }
    LooksAhead2.prototype.initLooksAhead = function(config) {
      this.dynamicTokensEnabled = has(config, "dynamicTokensEnabled") ? config.dynamicTokensEnabled : DEFAULT_PARSER_CONFIG.dynamicTokensEnabled;
      this.maxLookahead = has(config, "maxLookahead") ? config.maxLookahead : DEFAULT_PARSER_CONFIG.maxLookahead;
      this.lookAheadFuncsCache = isES2015MapSupported() ? /* @__PURE__ */ new Map() : [];
      if (isES2015MapSupported()) {
        this.getLaFuncFromCache = this.getLaFuncFromMap;
        this.setLaFuncCache = this.setLaFuncCacheUsingMap;
      } else {
        this.getLaFuncFromCache = this.getLaFuncFromObj;
        this.setLaFuncCache = this.setLaFuncUsingObj;
      }
    };
    LooksAhead2.prototype.preComputeLookaheadFunctions = function(rules) {
      var _this = this;
      forEach(rules, function(currRule) {
        _this.TRACE_INIT(currRule.name + " Rule Lookahead", function() {
          var _a2 = collectMethods(currRule), alternation = _a2.alternation, repetition = _a2.repetition, option = _a2.option, repetitionMandatory = _a2.repetitionMandatory, repetitionMandatoryWithSeparator = _a2.repetitionMandatoryWithSeparator, repetitionWithSeparator = _a2.repetitionWithSeparator;
          forEach(alternation, function(currProd) {
            var prodIdx = currProd.idx === 0 ? "" : currProd.idx;
            _this.TRACE_INIT("" + getProductionDslName(currProd) + prodIdx, function() {
              var laFunc = buildLookaheadFuncForOr(currProd.idx, currRule, currProd.maxLookahead || _this.maxLookahead, currProd.hasPredicates, _this.dynamicTokensEnabled, _this.lookAheadBuilderForAlternatives);
              var key = getKeyForAutomaticLookahead(_this.fullRuleNameToShort[currRule.name], OR_IDX, currProd.idx);
              _this.setLaFuncCache(key, laFunc);
            });
          });
          forEach(repetition, function(currProd) {
            _this.computeLookaheadFunc(currRule, currProd.idx, MANY_IDX, PROD_TYPE.REPETITION, currProd.maxLookahead, getProductionDslName(currProd));
          });
          forEach(option, function(currProd) {
            _this.computeLookaheadFunc(currRule, currProd.idx, OPTION_IDX, PROD_TYPE.OPTION, currProd.maxLookahead, getProductionDslName(currProd));
          });
          forEach(repetitionMandatory, function(currProd) {
            _this.computeLookaheadFunc(currRule, currProd.idx, AT_LEAST_ONE_IDX, PROD_TYPE.REPETITION_MANDATORY, currProd.maxLookahead, getProductionDslName(currProd));
          });
          forEach(repetitionMandatoryWithSeparator, function(currProd) {
            _this.computeLookaheadFunc(currRule, currProd.idx, AT_LEAST_ONE_SEP_IDX, PROD_TYPE.REPETITION_MANDATORY_WITH_SEPARATOR, currProd.maxLookahead, getProductionDslName(currProd));
          });
          forEach(repetitionWithSeparator, function(currProd) {
            _this.computeLookaheadFunc(currRule, currProd.idx, MANY_SEP_IDX, PROD_TYPE.REPETITION_WITH_SEPARATOR, currProd.maxLookahead, getProductionDslName(currProd));
          });
        });
      });
    };
    LooksAhead2.prototype.computeLookaheadFunc = function(rule, prodOccurrence, prodKey, prodType, prodMaxLookahead, dslMethodName) {
      var _this = this;
      this.TRACE_INIT("" + dslMethodName + (prodOccurrence === 0 ? "" : prodOccurrence), function() {
        var laFunc = buildLookaheadFuncForOptionalProd(prodOccurrence, rule, prodMaxLookahead || _this.maxLookahead, _this.dynamicTokensEnabled, prodType, _this.lookAheadBuilderForOptional);
        var key = getKeyForAutomaticLookahead(_this.fullRuleNameToShort[rule.name], prodKey, prodOccurrence);
        _this.setLaFuncCache(key, laFunc);
      });
    };
    LooksAhead2.prototype.lookAheadBuilderForOptional = function(alt, tokenMatcher2, dynamicTokensEnabled) {
      return buildSingleAlternativeLookaheadFunction(alt, tokenMatcher2, dynamicTokensEnabled);
    };
    LooksAhead2.prototype.lookAheadBuilderForAlternatives = function(alts, hasPredicates, tokenMatcher2, dynamicTokensEnabled) {
      return buildAlternativesLookAheadFunc(alts, hasPredicates, tokenMatcher2, dynamicTokensEnabled);
    };
    LooksAhead2.prototype.getKeyForAutomaticLookahead = function(dslMethodIdx, occurrence) {
      var currRuleShortName = this.getLastExplicitRuleShortName();
      return getKeyForAutomaticLookahead(currRuleShortName, dslMethodIdx, occurrence);
    };
    LooksAhead2.prototype.getLaFuncFromCache = function(key) {
      return void 0;
    };
    LooksAhead2.prototype.getLaFuncFromMap = function(key) {
      return this.lookAheadFuncsCache.get(key);
    };
    LooksAhead2.prototype.getLaFuncFromObj = function(key) {
      return this.lookAheadFuncsCache[key];
    };
    LooksAhead2.prototype.setLaFuncCache = function(key, value) {
    };
    LooksAhead2.prototype.setLaFuncCacheUsingMap = function(key, value) {
      this.lookAheadFuncsCache.set(key, value);
    };
    LooksAhead2.prototype.setLaFuncUsingObj = function(key, value) {
      this.lookAheadFuncsCache[key] = value;
    };
    return LooksAhead2;
  })()
);
function classNameFromInstance(instance) {
  return functionName(instance.constructor);
}
var FUNC_NAME_REGEXP = /^\s*function\s*(\S*)\s*\(/;
var NAME = "name";
function functionName(func) {
  var existingNameProp = func.name;
  if (existingNameProp) {
    return existingNameProp;
  }
  var computedName = func.toString().match(FUNC_NAME_REGEXP)[1];
  return computedName;
}
function defineNameProp(obj, nameValue) {
  var namePropDescriptor = Object.getOwnPropertyDescriptor(obj, NAME);
  if (isUndefined(namePropDescriptor) || namePropDescriptor.configurable) {
    Object.defineProperty(obj, NAME, {
      enumerable: false,
      configurable: true,
      writable: false,
      value: nameValue
    });
    return true;
  }
  return false;
}
function defaultVisit(ctx, param) {
  var childrenNames = keys(ctx);
  var childrenNamesLength = childrenNames.length;
  for (var i2 = 0; i2 < childrenNamesLength; i2++) {
    var currChildName = childrenNames[i2];
    var currChildArray = ctx[currChildName];
    var currChildArrayLength = currChildArray.length;
    for (var j2 = 0; j2 < currChildArrayLength; j2++) {
      var currChild = currChildArray[j2];
      if (currChild.tokenTypeIdx === void 0) {
        if (currChild.fullName !== void 0) {
          this[currChild.fullName](currChild.children, param);
        } else {
          this[currChild.name](currChild.children, param);
        }
      }
    }
  }
  return void 0;
}
function createBaseSemanticVisitorConstructor(grammarName, ruleNames) {
  var derivedConstructor = function() {
  };
  defineNameProp(derivedConstructor, grammarName + "BaseSemantics");
  var semanticProto = {
    visit: function(cstNode, param) {
      if (isArray(cstNode)) {
        cstNode = cstNode[0];
      }
      if (isUndefined(cstNode)) {
        return void 0;
      }
      if (cstNode.fullName !== void 0) {
        return this[cstNode.fullName](cstNode.children, param);
      } else {
        return this[cstNode.name](cstNode.children, param);
      }
    },
    validateVisitor: function() {
      var semanticDefinitionErrors = validateVisitor(this, ruleNames);
      if (!isEmpty(semanticDefinitionErrors)) {
        var errorMessages = map(semanticDefinitionErrors, function(currDefError) {
          return currDefError.msg;
        });
        throw Error("Errors Detected in CST Visitor <" + functionName(this.constructor) + ">:\n	" + ("" + errorMessages.join("\n\n").replace(/\n/g, "\n	")));
      }
    }
  };
  derivedConstructor.prototype = semanticProto;
  derivedConstructor.prototype.constructor = derivedConstructor;
  derivedConstructor._RULE_NAMES = ruleNames;
  return derivedConstructor;
}
function createBaseVisitorConstructorWithDefaults(grammarName, ruleNames, baseConstructor) {
  var derivedConstructor = function() {
  };
  defineNameProp(derivedConstructor, grammarName + "BaseSemanticsWithDefaults");
  var withDefaultsProto = Object.create(baseConstructor.prototype);
  forEach(ruleNames, function(ruleName) {
    withDefaultsProto[ruleName] = defaultVisit;
  });
  derivedConstructor.prototype = withDefaultsProto;
  derivedConstructor.prototype.constructor = derivedConstructor;
  return derivedConstructor;
}
var CstVisitorDefinitionError;
(function(CstVisitorDefinitionError2) {
  CstVisitorDefinitionError2[CstVisitorDefinitionError2["REDUNDANT_METHOD"] = 0] = "REDUNDANT_METHOD";
  CstVisitorDefinitionError2[CstVisitorDefinitionError2["MISSING_METHOD"] = 1] = "MISSING_METHOD";
})(CstVisitorDefinitionError || (CstVisitorDefinitionError = {}));
function validateVisitor(visitorInstance, ruleNames) {
  var missingErrors = validateMissingCstMethods(visitorInstance, ruleNames);
  var redundantErrors = validateRedundantMethods(visitorInstance, ruleNames);
  return missingErrors.concat(redundantErrors);
}
function validateMissingCstMethods(visitorInstance, ruleNames) {
  var errors = map(ruleNames, function(currRuleName) {
    if (!isFunction(visitorInstance[currRuleName])) {
      return {
        msg: "Missing visitor method: <" + currRuleName + "> on " + functionName(visitorInstance.constructor) + " CST Visitor.",
        type: CstVisitorDefinitionError.MISSING_METHOD,
        methodName: currRuleName
      };
    }
  });
  return compact(errors);
}
var VALID_PROP_NAMES = ["constructor", "visit", "validateVisitor"];
function validateRedundantMethods(visitorInstance, ruleNames) {
  var errors = [];
  for (var prop in visitorInstance) {
    if (validTermsPattern.test(prop) && isFunction(visitorInstance[prop]) && !contains(VALID_PROP_NAMES, prop) && !contains(ruleNames, prop)) {
      errors.push({
        msg: "Redundant visitor method: <" + prop + "> on " + functionName(visitorInstance.constructor) + " CST Visitor\nThere is no Grammar Rule corresponding to this method's name.\n" + ("For utility methods on visitor classes use methods names that do not match /" + validTermsPattern.source + "/."),
        type: CstVisitorDefinitionError.REDUNDANT_METHOD,
        methodName: prop
      });
    }
  }
  return errors;
}
var TreeBuilder = (
  /** @class */
  (function() {
    function TreeBuilder2() {
    }
    TreeBuilder2.prototype.initTreeBuilder = function(config) {
      this.LAST_EXPLICIT_RULE_STACK = [];
      this.CST_STACK = [];
      this.outputCst = has(config, "outputCst") ? config.outputCst : DEFAULT_PARSER_CONFIG.outputCst;
      this.nodeLocationTracking = has(config, "nodeLocationTracking") ? config.nodeLocationTracking : DEFAULT_PARSER_CONFIG.nodeLocationTracking;
      if (!this.outputCst) {
        this.cstInvocationStateUpdate = NOOP;
        this.cstFinallyStateUpdate = NOOP;
        this.cstPostTerminal = NOOP;
        this.cstPostNonTerminal = NOOP;
        this.cstPostRule = NOOP;
        this.getLastExplicitRuleShortName = this.getLastExplicitRuleShortNameNoCst;
        this.getPreviousExplicitRuleShortName = this.getPreviousExplicitRuleShortNameNoCst;
        this.getLastExplicitRuleOccurrenceIndex = this.getLastExplicitRuleOccurrenceIndexNoCst;
        this.manyInternal = this.manyInternalNoCst;
        this.orInternal = this.orInternalNoCst;
        this.optionInternal = this.optionInternalNoCst;
        this.atLeastOneInternal = this.atLeastOneInternalNoCst;
        this.manySepFirstInternal = this.manySepFirstInternalNoCst;
        this.atLeastOneSepFirstInternal = this.atLeastOneSepFirstInternalNoCst;
      } else {
        if (/full/i.test(this.nodeLocationTracking)) {
          if (this.recoveryEnabled) {
            this.setNodeLocationFromToken = setNodeLocationFull;
            this.setNodeLocationFromNode = setNodeLocationFull;
            this.cstPostRule = NOOP;
            this.setInitialNodeLocation = this.setInitialNodeLocationFullRecovery;
          } else {
            this.setNodeLocationFromToken = NOOP;
            this.setNodeLocationFromNode = NOOP;
            this.cstPostRule = this.cstPostRuleFull;
            this.setInitialNodeLocation = this.setInitialNodeLocationFullRegular;
          }
        } else if (/onlyOffset/i.test(this.nodeLocationTracking)) {
          if (this.recoveryEnabled) {
            this.setNodeLocationFromToken = setNodeLocationOnlyOffset;
            this.setNodeLocationFromNode = setNodeLocationOnlyOffset;
            this.cstPostRule = NOOP;
            this.setInitialNodeLocation = this.setInitialNodeLocationOnlyOffsetRecovery;
          } else {
            this.setNodeLocationFromToken = NOOP;
            this.setNodeLocationFromNode = NOOP;
            this.cstPostRule = this.cstPostRuleOnlyOffset;
            this.setInitialNodeLocation = this.setInitialNodeLocationOnlyOffsetRegular;
          }
        } else if (/none/i.test(this.nodeLocationTracking)) {
          this.setNodeLocationFromToken = NOOP;
          this.setNodeLocationFromNode = NOOP;
          this.cstPostRule = NOOP;
          this.setInitialNodeLocation = NOOP;
        } else {
          throw Error('Invalid <nodeLocationTracking> config option: "' + config.nodeLocationTracking + '"');
        }
      }
    };
    TreeBuilder2.prototype.setInitialNodeLocationOnlyOffsetRecovery = function(cstNode) {
      cstNode.location = {
        startOffset: NaN,
        endOffset: NaN
      };
    };
    TreeBuilder2.prototype.setInitialNodeLocationOnlyOffsetRegular = function(cstNode) {
      cstNode.location = {
        // without error recovery the starting Location of a new CstNode is guaranteed
        // To be the next Token's startOffset (for valid inputs).
        // For invalid inputs there won't be any CSTOutput so this potential
        // inaccuracy does not matter
        startOffset: this.LA(1).startOffset,
        endOffset: NaN
      };
    };
    TreeBuilder2.prototype.setInitialNodeLocationFullRecovery = function(cstNode) {
      cstNode.location = {
        startOffset: NaN,
        startLine: NaN,
        startColumn: NaN,
        endOffset: NaN,
        endLine: NaN,
        endColumn: NaN
      };
    };
    TreeBuilder2.prototype.setInitialNodeLocationFullRegular = function(cstNode) {
      var nextToken = this.LA(1);
      cstNode.location = {
        startOffset: nextToken.startOffset,
        startLine: nextToken.startLine,
        startColumn: nextToken.startColumn,
        endOffset: NaN,
        endLine: NaN,
        endColumn: NaN
      };
    };
    TreeBuilder2.prototype.cstNestedInvocationStateUpdate = function(nestedName, shortName) {
      var cstNode = {
        name: nestedName,
        fullName: this.shortRuleNameToFull[this.getLastExplicitRuleShortName()] + nestedName,
        children: {}
      };
      this.setInitialNodeLocation(cstNode);
      this.CST_STACK.push(cstNode);
    };
    TreeBuilder2.prototype.cstInvocationStateUpdate = function(fullRuleName, shortName) {
      this.LAST_EXPLICIT_RULE_STACK.push(this.RULE_STACK.length - 1);
      var cstNode = {
        name: fullRuleName,
        children: {}
      };
      this.setInitialNodeLocation(cstNode);
      this.CST_STACK.push(cstNode);
    };
    TreeBuilder2.prototype.cstFinallyStateUpdate = function() {
      this.LAST_EXPLICIT_RULE_STACK.pop();
      this.CST_STACK.pop();
    };
    TreeBuilder2.prototype.cstNestedFinallyStateUpdate = function() {
      var lastCstNode = this.CST_STACK.pop();
      this.cstPostRule(lastCstNode);
    };
    TreeBuilder2.prototype.cstPostRuleFull = function(ruleCstNode) {
      var prevToken = this.LA(0);
      var loc = ruleCstNode.location;
      if (loc.startOffset <= prevToken.startOffset === true) {
        loc.endOffset = prevToken.endOffset;
        loc.endLine = prevToken.endLine;
        loc.endColumn = prevToken.endColumn;
      } else {
        loc.startOffset = NaN;
        loc.startLine = NaN;
        loc.startColumn = NaN;
      }
    };
    TreeBuilder2.prototype.cstPostRuleOnlyOffset = function(ruleCstNode) {
      var prevToken = this.LA(0);
      var loc = ruleCstNode.location;
      if (loc.startOffset <= prevToken.startOffset === true) {
        loc.endOffset = prevToken.endOffset;
      } else {
        loc.startOffset = NaN;
      }
    };
    TreeBuilder2.prototype.cstPostTerminal = function(key, consumedToken) {
      var rootCst = this.CST_STACK[this.CST_STACK.length - 1];
      addTerminalToCst(rootCst, consumedToken, key);
      this.setNodeLocationFromToken(rootCst.location, consumedToken);
    };
    TreeBuilder2.prototype.cstPostNonTerminal = function(ruleCstResult, ruleName) {
      if (this.isBackTracking() !== true) {
        var preCstNode = this.CST_STACK[this.CST_STACK.length - 1];
        addNoneTerminalToCst(preCstNode, ruleName, ruleCstResult);
        this.setNodeLocationFromNode(preCstNode.location, ruleCstResult.location);
      }
    };
    TreeBuilder2.prototype.getBaseCstVisitorConstructor = function() {
      if (isUndefined(this.baseCstVisitorConstructor)) {
        var newBaseCstVisitorConstructor = createBaseSemanticVisitorConstructor(this.className, this.allRuleNames);
        this.baseCstVisitorConstructor = newBaseCstVisitorConstructor;
        return newBaseCstVisitorConstructor;
      }
      return this.baseCstVisitorConstructor;
    };
    TreeBuilder2.prototype.getBaseCstVisitorConstructorWithDefaults = function() {
      if (isUndefined(this.baseCstVisitorWithDefaultsConstructor)) {
        var newConstructor = createBaseVisitorConstructorWithDefaults(this.className, this.allRuleNames, this.getBaseCstVisitorConstructor());
        this.baseCstVisitorWithDefaultsConstructor = newConstructor;
        return newConstructor;
      }
      return this.baseCstVisitorWithDefaultsConstructor;
    };
    TreeBuilder2.prototype.nestedRuleBeforeClause = function(methodOpts, laKey) {
      var nestedName;
      if (methodOpts.NAME !== void 0) {
        nestedName = methodOpts.NAME;
        this.nestedRuleInvocationStateUpdate(nestedName, laKey);
        return nestedName;
      } else {
        return void 0;
      }
    };
    TreeBuilder2.prototype.nestedAltBeforeClause = function(methodOpts, occurrence, methodKeyIdx, altIdx) {
      var ruleIdx = this.getLastExplicitRuleShortName();
      var shortName = getKeyForAltIndex(ruleIdx, methodKeyIdx, occurrence, altIdx);
      var nestedName;
      if (methodOpts.NAME !== void 0) {
        nestedName = methodOpts.NAME;
        this.nestedRuleInvocationStateUpdate(nestedName, shortName);
        return {
          shortName,
          nestedName
        };
      } else {
        return void 0;
      }
    };
    TreeBuilder2.prototype.nestedRuleFinallyClause = function(laKey, nestedName) {
      var cstStack = this.CST_STACK;
      var nestedRuleCst = cstStack[cstStack.length - 1];
      this.nestedRuleFinallyStateUpdate();
      var parentCstNode = cstStack[cstStack.length - 1];
      addNoneTerminalToCst(parentCstNode, nestedName, nestedRuleCst);
      this.setNodeLocationFromNode(parentCstNode.location, nestedRuleCst.location);
    };
    TreeBuilder2.prototype.getLastExplicitRuleShortName = function() {
      var lastExplictIndex = this.LAST_EXPLICIT_RULE_STACK[this.LAST_EXPLICIT_RULE_STACK.length - 1];
      return this.RULE_STACK[lastExplictIndex];
    };
    TreeBuilder2.prototype.getLastExplicitRuleShortNameNoCst = function() {
      var ruleStack = this.RULE_STACK;
      return ruleStack[ruleStack.length - 1];
    };
    TreeBuilder2.prototype.getPreviousExplicitRuleShortName = function() {
      var lastExplicitIndex = this.LAST_EXPLICIT_RULE_STACK[this.LAST_EXPLICIT_RULE_STACK.length - 2];
      return this.RULE_STACK[lastExplicitIndex];
    };
    TreeBuilder2.prototype.getPreviousExplicitRuleShortNameNoCst = function() {
      var ruleStack = this.RULE_STACK;
      return ruleStack[ruleStack.length - 2];
    };
    TreeBuilder2.prototype.getLastExplicitRuleOccurrenceIndex = function() {
      var lastExplicitIndex = this.LAST_EXPLICIT_RULE_STACK[this.LAST_EXPLICIT_RULE_STACK.length - 1];
      return this.RULE_OCCURRENCE_STACK[lastExplicitIndex];
    };
    TreeBuilder2.prototype.getLastExplicitRuleOccurrenceIndexNoCst = function() {
      var occurrenceStack = this.RULE_OCCURRENCE_STACK;
      return occurrenceStack[occurrenceStack.length - 1];
    };
    TreeBuilder2.prototype.nestedRuleInvocationStateUpdate = function(nestedRuleName, shortNameKey) {
      this.RULE_OCCURRENCE_STACK.push(1);
      this.RULE_STACK.push(shortNameKey);
      this.cstNestedInvocationStateUpdate(nestedRuleName, shortNameKey);
    };
    TreeBuilder2.prototype.nestedRuleFinallyStateUpdate = function() {
      this.RULE_STACK.pop();
      this.RULE_OCCURRENCE_STACK.pop();
      this.cstNestedFinallyStateUpdate();
    };
    return TreeBuilder2;
  })()
);
var LexerAdapter = (
  /** @class */
  (function() {
    function LexerAdapter2() {
    }
    LexerAdapter2.prototype.initLexerAdapter = function() {
      this.tokVector = [];
      this.tokVectorLength = 0;
      this.currIdx = -1;
    };
    Object.defineProperty(LexerAdapter2.prototype, "input", {
      get: function() {
        return this.tokVector;
      },
      set: function(newInput) {
        if (this.selfAnalysisDone !== true) {
          throw Error("Missing <performSelfAnalysis> invocation at the end of the Parser's constructor.");
        }
        this.reset();
        this.tokVector = newInput;
        this.tokVectorLength = newInput.length;
      },
      enumerable: true,
      configurable: true
    });
    LexerAdapter2.prototype.SKIP_TOKEN = function() {
      if (this.currIdx <= this.tokVector.length - 2) {
        this.consumeToken();
        return this.LA(1);
      } else {
        return END_OF_FILE;
      }
    };
    LexerAdapter2.prototype.LA = function(howMuch) {
      var soughtIdx = this.currIdx + howMuch;
      if (soughtIdx < 0 || this.tokVectorLength <= soughtIdx) {
        return END_OF_FILE;
      } else {
        return this.tokVector[soughtIdx];
      }
    };
    LexerAdapter2.prototype.consumeToken = function() {
      this.currIdx++;
    };
    LexerAdapter2.prototype.exportLexerState = function() {
      return this.currIdx;
    };
    LexerAdapter2.prototype.importLexerState = function(newState) {
      this.currIdx = newState;
    };
    LexerAdapter2.prototype.resetLexerState = function() {
      this.currIdx = -1;
    };
    LexerAdapter2.prototype.moveToTerminatedState = function() {
      this.currIdx = this.tokVector.length - 1;
    };
    LexerAdapter2.prototype.getLexerPosition = function() {
      return this.exportLexerState();
    };
    return LexerAdapter2;
  })()
);
var RecognizerApi = (
  /** @class */
  (function() {
    function RecognizerApi2() {
    }
    RecognizerApi2.prototype.ACTION = function(impl) {
      return impl.call(this);
    };
    RecognizerApi2.prototype.consume = function(idx, tokType, options) {
      return this.consumeInternal(tokType, idx, options);
    };
    RecognizerApi2.prototype.subrule = function(idx, ruleToCall, options) {
      return this.subruleInternal(ruleToCall, idx, options);
    };
    RecognizerApi2.prototype.option = function(idx, actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, idx);
    };
    RecognizerApi2.prototype.or = function(idx, altsOrOpts) {
      return this.orInternal(altsOrOpts, idx);
    };
    RecognizerApi2.prototype.many = function(idx, actionORMethodDef) {
      return this.manyInternal(idx, actionORMethodDef);
    };
    RecognizerApi2.prototype.atLeastOne = function(idx, actionORMethodDef) {
      return this.atLeastOneInternal(idx, actionORMethodDef);
    };
    RecognizerApi2.prototype.CONSUME = function(tokType, options) {
      return this.consumeInternal(tokType, 0, options);
    };
    RecognizerApi2.prototype.CONSUME1 = function(tokType, options) {
      return this.consumeInternal(tokType, 1, options);
    };
    RecognizerApi2.prototype.CONSUME2 = function(tokType, options) {
      return this.consumeInternal(tokType, 2, options);
    };
    RecognizerApi2.prototype.CONSUME3 = function(tokType, options) {
      return this.consumeInternal(tokType, 3, options);
    };
    RecognizerApi2.prototype.CONSUME4 = function(tokType, options) {
      return this.consumeInternal(tokType, 4, options);
    };
    RecognizerApi2.prototype.CONSUME5 = function(tokType, options) {
      return this.consumeInternal(tokType, 5, options);
    };
    RecognizerApi2.prototype.CONSUME6 = function(tokType, options) {
      return this.consumeInternal(tokType, 6, options);
    };
    RecognizerApi2.prototype.CONSUME7 = function(tokType, options) {
      return this.consumeInternal(tokType, 7, options);
    };
    RecognizerApi2.prototype.CONSUME8 = function(tokType, options) {
      return this.consumeInternal(tokType, 8, options);
    };
    RecognizerApi2.prototype.CONSUME9 = function(tokType, options) {
      return this.consumeInternal(tokType, 9, options);
    };
    RecognizerApi2.prototype.SUBRULE = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 0, options);
    };
    RecognizerApi2.prototype.SUBRULE1 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 1, options);
    };
    RecognizerApi2.prototype.SUBRULE2 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 2, options);
    };
    RecognizerApi2.prototype.SUBRULE3 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 3, options);
    };
    RecognizerApi2.prototype.SUBRULE4 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 4, options);
    };
    RecognizerApi2.prototype.SUBRULE5 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 5, options);
    };
    RecognizerApi2.prototype.SUBRULE6 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 6, options);
    };
    RecognizerApi2.prototype.SUBRULE7 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 7, options);
    };
    RecognizerApi2.prototype.SUBRULE8 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 8, options);
    };
    RecognizerApi2.prototype.SUBRULE9 = function(ruleToCall, options) {
      return this.subruleInternal(ruleToCall, 9, options);
    };
    RecognizerApi2.prototype.OPTION = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 0);
    };
    RecognizerApi2.prototype.OPTION1 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 1);
    };
    RecognizerApi2.prototype.OPTION2 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 2);
    };
    RecognizerApi2.prototype.OPTION3 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 3);
    };
    RecognizerApi2.prototype.OPTION4 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 4);
    };
    RecognizerApi2.prototype.OPTION5 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 5);
    };
    RecognizerApi2.prototype.OPTION6 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 6);
    };
    RecognizerApi2.prototype.OPTION7 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 7);
    };
    RecognizerApi2.prototype.OPTION8 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 8);
    };
    RecognizerApi2.prototype.OPTION9 = function(actionORMethodDef) {
      return this.optionInternal(actionORMethodDef, 9);
    };
    RecognizerApi2.prototype.OR = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 0);
    };
    RecognizerApi2.prototype.OR1 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 1);
    };
    RecognizerApi2.prototype.OR2 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 2);
    };
    RecognizerApi2.prototype.OR3 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 3);
    };
    RecognizerApi2.prototype.OR4 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 4);
    };
    RecognizerApi2.prototype.OR5 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 5);
    };
    RecognizerApi2.prototype.OR6 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 6);
    };
    RecognizerApi2.prototype.OR7 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 7);
    };
    RecognizerApi2.prototype.OR8 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 8);
    };
    RecognizerApi2.prototype.OR9 = function(altsOrOpts) {
      return this.orInternal(altsOrOpts, 9);
    };
    RecognizerApi2.prototype.MANY = function(actionORMethodDef) {
      this.manyInternal(0, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY1 = function(actionORMethodDef) {
      this.manyInternal(1, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY2 = function(actionORMethodDef) {
      this.manyInternal(2, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY3 = function(actionORMethodDef) {
      this.manyInternal(3, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY4 = function(actionORMethodDef) {
      this.manyInternal(4, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY5 = function(actionORMethodDef) {
      this.manyInternal(5, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY6 = function(actionORMethodDef) {
      this.manyInternal(6, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY7 = function(actionORMethodDef) {
      this.manyInternal(7, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY8 = function(actionORMethodDef) {
      this.manyInternal(8, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY9 = function(actionORMethodDef) {
      this.manyInternal(9, actionORMethodDef);
    };
    RecognizerApi2.prototype.MANY_SEP = function(options) {
      this.manySepFirstInternal(0, options);
    };
    RecognizerApi2.prototype.MANY_SEP1 = function(options) {
      this.manySepFirstInternal(1, options);
    };
    RecognizerApi2.prototype.MANY_SEP2 = function(options) {
      this.manySepFirstInternal(2, options);
    };
    RecognizerApi2.prototype.MANY_SEP3 = function(options) {
      this.manySepFirstInternal(3, options);
    };
    RecognizerApi2.prototype.MANY_SEP4 = function(options) {
      this.manySepFirstInternal(4, options);
    };
    RecognizerApi2.prototype.MANY_SEP5 = function(options) {
      this.manySepFirstInternal(5, options);
    };
    RecognizerApi2.prototype.MANY_SEP6 = function(options) {
      this.manySepFirstInternal(6, options);
    };
    RecognizerApi2.prototype.MANY_SEP7 = function(options) {
      this.manySepFirstInternal(7, options);
    };
    RecognizerApi2.prototype.MANY_SEP8 = function(options) {
      this.manySepFirstInternal(8, options);
    };
    RecognizerApi2.prototype.MANY_SEP9 = function(options) {
      this.manySepFirstInternal(9, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE = function(actionORMethodDef) {
      this.atLeastOneInternal(0, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE1 = function(actionORMethodDef) {
      return this.atLeastOneInternal(1, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE2 = function(actionORMethodDef) {
      this.atLeastOneInternal(2, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE3 = function(actionORMethodDef) {
      this.atLeastOneInternal(3, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE4 = function(actionORMethodDef) {
      this.atLeastOneInternal(4, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE5 = function(actionORMethodDef) {
      this.atLeastOneInternal(5, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE6 = function(actionORMethodDef) {
      this.atLeastOneInternal(6, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE7 = function(actionORMethodDef) {
      this.atLeastOneInternal(7, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE8 = function(actionORMethodDef) {
      this.atLeastOneInternal(8, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE9 = function(actionORMethodDef) {
      this.atLeastOneInternal(9, actionORMethodDef);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP = function(options) {
      this.atLeastOneSepFirstInternal(0, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP1 = function(options) {
      this.atLeastOneSepFirstInternal(1, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP2 = function(options) {
      this.atLeastOneSepFirstInternal(2, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP3 = function(options) {
      this.atLeastOneSepFirstInternal(3, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP4 = function(options) {
      this.atLeastOneSepFirstInternal(4, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP5 = function(options) {
      this.atLeastOneSepFirstInternal(5, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP6 = function(options) {
      this.atLeastOneSepFirstInternal(6, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP7 = function(options) {
      this.atLeastOneSepFirstInternal(7, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP8 = function(options) {
      this.atLeastOneSepFirstInternal(8, options);
    };
    RecognizerApi2.prototype.AT_LEAST_ONE_SEP9 = function(options) {
      this.atLeastOneSepFirstInternal(9, options);
    };
    RecognizerApi2.prototype.RULE = function(name, implementation, config) {
      if (config === void 0) {
        config = DEFAULT_RULE_CONFIG;
      }
      if (contains(this.definedRulesNames, name)) {
        var errMsg = defaultGrammarValidatorErrorProvider.buildDuplicateRuleNameError({
          topLevelRule: name,
          grammarName: this.className
        });
        var error = {
          message: errMsg,
          type: ParserDefinitionErrorType.DUPLICATE_RULE_NAME,
          ruleName: name
        };
        this.definitionErrors.push(error);
      }
      this.definedRulesNames.push(name);
      var ruleImplementation = this.defineRule(name, implementation, config);
      this[name] = ruleImplementation;
      return ruleImplementation;
    };
    RecognizerApi2.prototype.OVERRIDE_RULE = function(name, impl, config) {
      if (config === void 0) {
        config = DEFAULT_RULE_CONFIG;
      }
      var ruleErrors = [];
      ruleErrors = ruleErrors.concat(validateRuleIsOverridden(name, this.definedRulesNames, this.className));
      this.definitionErrors.push.apply(this.definitionErrors, ruleErrors);
      var ruleImplementation = this.defineRule(name, impl, config);
      this[name] = ruleImplementation;
      return ruleImplementation;
    };
    RecognizerApi2.prototype.BACKTRACK = function(grammarRule, args) {
      return function() {
        this.isBackTrackingStack.push(1);
        var orgState = this.saveRecogState();
        try {
          grammarRule.apply(this, args);
          return true;
        } catch (e3) {
          if (isRecognitionException(e3)) {
            return false;
          } else {
            throw e3;
          }
        } finally {
          this.reloadRecogState(orgState);
          this.isBackTrackingStack.pop();
        }
      };
    };
    RecognizerApi2.prototype.getGAstProductions = function() {
      return this.gastProductionsCache;
    };
    RecognizerApi2.prototype.getSerializedGastProductions = function() {
      return serializeGrammar(values(this.gastProductionsCache));
    };
    return RecognizerApi2;
  })()
);
var RecognizerEngine = (
  /** @class */
  (function() {
    function RecognizerEngine2() {
    }
    RecognizerEngine2.prototype.initRecognizerEngine = function(tokenVocabulary, config) {
      this.className = classNameFromInstance(this);
      this.shortRuleNameToFull = {};
      this.fullRuleNameToShort = {};
      this.ruleShortNameIdx = 256;
      this.tokenMatcher = tokenStructuredMatcherNoCategories;
      this.definedRulesNames = [];
      this.tokensMap = {};
      this.allRuleNames = [];
      this.isBackTrackingStack = [];
      this.RULE_STACK = [];
      this.RULE_OCCURRENCE_STACK = [];
      this.gastProductionsCache = {};
      if (has(config, "serializedGrammar")) {
        throw Error("The Parser's configuration can no longer contain a <serializedGrammar> property.\n	See: https://sap.github.io/chevrotain/docs/changes/BREAKING_CHANGES.html#_6-0-0\n	For Further details.");
      }
      if (isArray(tokenVocabulary)) {
        if (isEmpty(tokenVocabulary)) {
          throw Error("A Token Vocabulary cannot be empty.\n	Note that the first argument for the parser constructor\n	is no longer a Token vector (since v4.0).");
        }
        if (typeof tokenVocabulary[0].startOffset === "number") {
          throw Error("The Parser constructor no longer accepts a token vector as the first argument.\n	See: https://sap.github.io/chevrotain/docs/changes/BREAKING_CHANGES.html#_4-0-0\n	For Further details.");
        }
      }
      if (isArray(tokenVocabulary)) {
        this.tokensMap = reduce(tokenVocabulary, function(acc, tokType) {
          acc[tokType.name] = tokType;
          return acc;
        }, {});
      } else if (has(tokenVocabulary, "modes") && every(flatten(values(tokenVocabulary.modes)), isTokenType)) {
        var allTokenTypes = flatten(values(tokenVocabulary.modes));
        var uniqueTokens = uniq(allTokenTypes);
        this.tokensMap = reduce(uniqueTokens, function(acc, tokType) {
          acc[tokType.name] = tokType;
          return acc;
        }, {});
      } else if (isObject(tokenVocabulary)) {
        this.tokensMap = cloneObj(tokenVocabulary);
      } else {
        throw new Error("<tokensDictionary> argument must be An Array of Token constructors, A dictionary of Token constructors or an IMultiModeLexerDefinition");
      }
      this.tokensMap["EOF"] = EOF;
      var noTokenCategoriesUsed = every(values(tokenVocabulary), function(tokenConstructor) {
        return isEmpty(tokenConstructor.categoryMatches);
      });
      this.tokenMatcher = noTokenCategoriesUsed ? tokenStructuredMatcherNoCategories : tokenStructuredMatcher;
      augmentTokenTypes(values(this.tokensMap));
    };
    RecognizerEngine2.prototype.defineRule = function(ruleName, impl, config) {
      if (this.selfAnalysisDone) {
        throw Error("Grammar rule <" + ruleName + "> may not be defined after the 'performSelfAnalysis' method has been called'\nMake sure that all grammar rule definitions are done before 'performSelfAnalysis' is called.");
      }
      var resyncEnabled = has(config, "resyncEnabled") ? config.resyncEnabled : DEFAULT_RULE_CONFIG.resyncEnabled;
      var recoveryValueFunc = has(config, "recoveryValueFunc") ? config.recoveryValueFunc : DEFAULT_RULE_CONFIG.recoveryValueFunc;
      var shortName = this.ruleShortNameIdx << BITS_FOR_METHOD_TYPE + BITS_FOR_OCCURRENCE_IDX;
      this.ruleShortNameIdx++;
      this.shortRuleNameToFull[shortName] = ruleName;
      this.fullRuleNameToShort[ruleName] = shortName;
      function invokeRuleWithTry(args) {
        try {
          if (this.outputCst === true) {
            impl.apply(this, args);
            var cst = this.CST_STACK[this.CST_STACK.length - 1];
            this.cstPostRule(cst);
            return cst;
          } else {
            return impl.apply(this, args);
          }
        } catch (e3) {
          return this.invokeRuleCatch(e3, resyncEnabled, recoveryValueFunc);
        } finally {
          this.ruleFinallyStateUpdate();
        }
      }
      var wrappedGrammarRule;
      wrappedGrammarRule = function(idxInCallingRule, args) {
        if (idxInCallingRule === void 0) {
          idxInCallingRule = 0;
        }
        this.ruleInvocationStateUpdate(shortName, ruleName, idxInCallingRule);
        return invokeRuleWithTry.call(this, args);
      };
      var ruleNamePropName = "ruleName";
      wrappedGrammarRule[ruleNamePropName] = ruleName;
      wrappedGrammarRule["originalGrammarAction"] = impl;
      return wrappedGrammarRule;
    };
    RecognizerEngine2.prototype.invokeRuleCatch = function(e3, resyncEnabledConfig, recoveryValueFunc) {
      var isFirstInvokedRule = this.RULE_STACK.length === 1;
      var reSyncEnabled = resyncEnabledConfig && !this.isBackTracking() && this.recoveryEnabled;
      if (isRecognitionException(e3)) {
        var recogError = e3;
        if (reSyncEnabled) {
          var reSyncTokType = this.findReSyncTokenType();
          if (this.isInCurrentRuleReSyncSet(reSyncTokType)) {
            recogError.resyncedTokens = this.reSyncTo(reSyncTokType);
            if (this.outputCst) {
              var partialCstResult = this.CST_STACK[this.CST_STACK.length - 1];
              partialCstResult.recoveredNode = true;
              return partialCstResult;
            } else {
              return recoveryValueFunc();
            }
          } else {
            if (this.outputCst) {
              var partialCstResult = this.CST_STACK[this.CST_STACK.length - 1];
              partialCstResult.recoveredNode = true;
              recogError.partialCstResult = partialCstResult;
            }
            throw recogError;
          }
        } else if (isFirstInvokedRule) {
          this.moveToTerminatedState();
          return recoveryValueFunc();
        } else {
          throw recogError;
        }
      } else {
        throw e3;
      }
    };
    RecognizerEngine2.prototype.optionInternal = function(actionORMethodDef, occurrence) {
      var key = this.getKeyForAutomaticLookahead(OPTION_IDX, occurrence);
      var nestedName = this.nestedRuleBeforeClause(actionORMethodDef, key);
      try {
        return this.optionInternalLogic(actionORMethodDef, occurrence, key);
      } finally {
        if (nestedName !== void 0) {
          this.nestedRuleFinallyClause(key, nestedName);
        }
      }
    };
    RecognizerEngine2.prototype.optionInternalNoCst = function(actionORMethodDef, occurrence) {
      var key = this.getKeyForAutomaticLookahead(OPTION_IDX, occurrence);
      return this.optionInternalLogic(actionORMethodDef, occurrence, key);
    };
    RecognizerEngine2.prototype.optionInternalLogic = function(actionORMethodDef, occurrence, key) {
      var _this = this;
      var lookAheadFunc = this.getLaFuncFromCache(key);
      var action;
      var predicate;
      if (actionORMethodDef.DEF !== void 0) {
        action = actionORMethodDef.DEF;
        predicate = actionORMethodDef.GATE;
        if (predicate !== void 0) {
          var orgLookaheadFunction_1 = lookAheadFunc;
          lookAheadFunc = function() {
            return predicate.call(_this) && orgLookaheadFunction_1.call(_this);
          };
        }
      } else {
        action = actionORMethodDef;
      }
      if (lookAheadFunc.call(this) === true) {
        return action.call(this);
      }
      return void 0;
    };
    RecognizerEngine2.prototype.atLeastOneInternal = function(prodOccurrence, actionORMethodDef) {
      var laKey = this.getKeyForAutomaticLookahead(AT_LEAST_ONE_IDX, prodOccurrence);
      var nestedName = this.nestedRuleBeforeClause(actionORMethodDef, laKey);
      try {
        return this.atLeastOneInternalLogic(prodOccurrence, actionORMethodDef, laKey);
      } finally {
        if (nestedName !== void 0) {
          this.nestedRuleFinallyClause(laKey, nestedName);
        }
      }
    };
    RecognizerEngine2.prototype.atLeastOneInternalNoCst = function(prodOccurrence, actionORMethodDef) {
      var key = this.getKeyForAutomaticLookahead(AT_LEAST_ONE_IDX, prodOccurrence);
      this.atLeastOneInternalLogic(prodOccurrence, actionORMethodDef, key);
    };
    RecognizerEngine2.prototype.atLeastOneInternalLogic = function(prodOccurrence, actionORMethodDef, key) {
      var _this = this;
      var lookAheadFunc = this.getLaFuncFromCache(key);
      var action;
      var predicate;
      if (actionORMethodDef.DEF !== void 0) {
        action = actionORMethodDef.DEF;
        predicate = actionORMethodDef.GATE;
        if (predicate !== void 0) {
          var orgLookaheadFunction_2 = lookAheadFunc;
          lookAheadFunc = function() {
            return predicate.call(_this) && orgLookaheadFunction_2.call(_this);
          };
        }
      } else {
        action = actionORMethodDef;
      }
      if (lookAheadFunc.call(this) === true) {
        var notStuck = this.doSingleRepetition(action);
        while (lookAheadFunc.call(this) === true && notStuck === true) {
          notStuck = this.doSingleRepetition(action);
        }
      } else {
        throw this.raiseEarlyExitException(prodOccurrence, PROD_TYPE.REPETITION_MANDATORY, actionORMethodDef.ERR_MSG);
      }
      this.attemptInRepetitionRecovery(this.atLeastOneInternal, [prodOccurrence, actionORMethodDef], lookAheadFunc, AT_LEAST_ONE_IDX, prodOccurrence, NextTerminalAfterAtLeastOneWalker);
    };
    RecognizerEngine2.prototype.atLeastOneSepFirstInternal = function(prodOccurrence, options) {
      var laKey = this.getKeyForAutomaticLookahead(AT_LEAST_ONE_SEP_IDX, prodOccurrence);
      var nestedName = this.nestedRuleBeforeClause(options, laKey);
      try {
        this.atLeastOneSepFirstInternalLogic(prodOccurrence, options, laKey);
      } finally {
        if (nestedName !== void 0) {
          this.nestedRuleFinallyClause(laKey, nestedName);
        }
      }
    };
    RecognizerEngine2.prototype.atLeastOneSepFirstInternalNoCst = function(prodOccurrence, options) {
      var laKey = this.getKeyForAutomaticLookahead(AT_LEAST_ONE_SEP_IDX, prodOccurrence);
      this.atLeastOneSepFirstInternalLogic(prodOccurrence, options, laKey);
    };
    RecognizerEngine2.prototype.atLeastOneSepFirstInternalLogic = function(prodOccurrence, options, key) {
      var _this = this;
      var action = options.DEF;
      var separator = options.SEP;
      var firstIterationLookaheadFunc = this.getLaFuncFromCache(key);
      if (firstIterationLookaheadFunc.call(this) === true) {
        action.call(this);
        var separatorLookAheadFunc = function() {
          return _this.tokenMatcher(_this.LA(1), separator);
        };
        while (this.tokenMatcher(this.LA(1), separator) === true) {
          this.CONSUME(separator);
          action.call(this);
        }
        this.attemptInRepetitionRecovery(this.repetitionSepSecondInternal, [
          prodOccurrence,
          separator,
          separatorLookAheadFunc,
          action,
          NextTerminalAfterAtLeastOneSepWalker
        ], separatorLookAheadFunc, AT_LEAST_ONE_SEP_IDX, prodOccurrence, NextTerminalAfterAtLeastOneSepWalker);
      } else {
        throw this.raiseEarlyExitException(prodOccurrence, PROD_TYPE.REPETITION_MANDATORY_WITH_SEPARATOR, options.ERR_MSG);
      }
    };
    RecognizerEngine2.prototype.manyInternal = function(prodOccurrence, actionORMethodDef) {
      var laKey = this.getKeyForAutomaticLookahead(MANY_IDX, prodOccurrence);
      var nestedName = this.nestedRuleBeforeClause(actionORMethodDef, laKey);
      try {
        return this.manyInternalLogic(prodOccurrence, actionORMethodDef, laKey);
      } finally {
        if (nestedName !== void 0) {
          this.nestedRuleFinallyClause(laKey, nestedName);
        }
      }
    };
    RecognizerEngine2.prototype.manyInternalNoCst = function(prodOccurrence, actionORMethodDef) {
      var laKey = this.getKeyForAutomaticLookahead(MANY_IDX, prodOccurrence);
      return this.manyInternalLogic(prodOccurrence, actionORMethodDef, laKey);
    };
    RecognizerEngine2.prototype.manyInternalLogic = function(prodOccurrence, actionORMethodDef, key) {
      var _this = this;
      var lookaheadFunction = this.getLaFuncFromCache(key);
      var action;
      var predicate;
      if (actionORMethodDef.DEF !== void 0) {
        action = actionORMethodDef.DEF;
        predicate = actionORMethodDef.GATE;
        if (predicate !== void 0) {
          var orgLookaheadFunction_3 = lookaheadFunction;
          lookaheadFunction = function() {
            return predicate.call(_this) && orgLookaheadFunction_3.call(_this);
          };
        }
      } else {
        action = actionORMethodDef;
      }
      var notStuck = true;
      while (lookaheadFunction.call(this) === true && notStuck === true) {
        notStuck = this.doSingleRepetition(action);
      }
      this.attemptInRepetitionRecovery(
        this.manyInternal,
        [prodOccurrence, actionORMethodDef],
        lookaheadFunction,
        MANY_IDX,
        prodOccurrence,
        NextTerminalAfterManyWalker,
        // The notStuck parameter is only relevant when "attemptInRepetitionRecovery"
        // is invoked from manyInternal, in the MANY_SEP case and AT_LEAST_ONE[_SEP]
        // An infinite loop cannot occur as:
        // - Either the lookahead is guaranteed to consume something (Single Token Separator)
        // - AT_LEAST_ONE by definition is guaranteed to consume something (or error out).
        notStuck
      );
    };
    RecognizerEngine2.prototype.manySepFirstInternal = function(prodOccurrence, options) {
      var laKey = this.getKeyForAutomaticLookahead(MANY_SEP_IDX, prodOccurrence);
      var nestedName = this.nestedRuleBeforeClause(options, laKey);
      try {
        this.manySepFirstInternalLogic(prodOccurrence, options, laKey);
      } finally {
        if (nestedName !== void 0) {
          this.nestedRuleFinallyClause(laKey, nestedName);
        }
      }
    };
    RecognizerEngine2.prototype.manySepFirstInternalNoCst = function(prodOccurrence, options) {
      var laKey = this.getKeyForAutomaticLookahead(MANY_SEP_IDX, prodOccurrence);
      this.manySepFirstInternalLogic(prodOccurrence, options, laKey);
    };
    RecognizerEngine2.prototype.manySepFirstInternalLogic = function(prodOccurrence, options, key) {
      var _this = this;
      var action = options.DEF;
      var separator = options.SEP;
      var firstIterationLaFunc = this.getLaFuncFromCache(key);
      if (firstIterationLaFunc.call(this) === true) {
        action.call(this);
        var separatorLookAheadFunc = function() {
          return _this.tokenMatcher(_this.LA(1), separator);
        };
        while (this.tokenMatcher(this.LA(1), separator) === true) {
          this.CONSUME(separator);
          action.call(this);
        }
        this.attemptInRepetitionRecovery(this.repetitionSepSecondInternal, [
          prodOccurrence,
          separator,
          separatorLookAheadFunc,
          action,
          NextTerminalAfterManySepWalker
        ], separatorLookAheadFunc, MANY_SEP_IDX, prodOccurrence, NextTerminalAfterManySepWalker);
      }
    };
    RecognizerEngine2.prototype.repetitionSepSecondInternal = function(prodOccurrence, separator, separatorLookAheadFunc, action, nextTerminalAfterWalker) {
      while (separatorLookAheadFunc()) {
        this.CONSUME(separator);
        action.call(this);
      }
      this.attemptInRepetitionRecovery(this.repetitionSepSecondInternal, [
        prodOccurrence,
        separator,
        separatorLookAheadFunc,
        action,
        nextTerminalAfterWalker
      ], separatorLookAheadFunc, AT_LEAST_ONE_SEP_IDX, prodOccurrence, nextTerminalAfterWalker);
    };
    RecognizerEngine2.prototype.doSingleRepetition = function(action) {
      var beforeIteration = this.getLexerPosition();
      action.call(this);
      var afterIteration = this.getLexerPosition();
      return afterIteration > beforeIteration;
    };
    RecognizerEngine2.prototype.orInternalNoCst = function(altsOrOpts, occurrence) {
      var alts = isArray(altsOrOpts) ? altsOrOpts : altsOrOpts.DEF;
      var laKey = this.getKeyForAutomaticLookahead(OR_IDX, occurrence);
      var laFunc = this.getLaFuncFromCache(laKey);
      var altIdxToTake = laFunc.call(this, alts);
      if (altIdxToTake !== void 0) {
        var chosenAlternative = alts[altIdxToTake];
        return chosenAlternative.ALT.call(this);
      }
      this.raiseNoAltException(occurrence, altsOrOpts.ERR_MSG);
    };
    RecognizerEngine2.prototype.orInternal = function(altsOrOpts, occurrence) {
      var laKey = this.getKeyForAutomaticLookahead(OR_IDX, occurrence);
      var nestedName = this.nestedRuleBeforeClause(altsOrOpts, laKey);
      try {
        var alts = isArray(altsOrOpts) ? altsOrOpts : altsOrOpts.DEF;
        var laFunc = this.getLaFuncFromCache(laKey);
        var altIdxToTake = laFunc.call(this, alts);
        if (altIdxToTake !== void 0) {
          var chosenAlternative = alts[altIdxToTake];
          var nestedAltBeforeClauseResult = this.nestedAltBeforeClause(chosenAlternative, occurrence, OR_IDX, altIdxToTake);
          try {
            return chosenAlternative.ALT.call(this);
          } finally {
            if (nestedAltBeforeClauseResult !== void 0) {
              this.nestedRuleFinallyClause(nestedAltBeforeClauseResult.shortName, nestedAltBeforeClauseResult.nestedName);
            }
          }
        }
        this.raiseNoAltException(occurrence, altsOrOpts.ERR_MSG);
      } finally {
        if (nestedName !== void 0) {
          this.nestedRuleFinallyClause(laKey, nestedName);
        }
      }
    };
    RecognizerEngine2.prototype.ruleFinallyStateUpdate = function() {
      this.RULE_STACK.pop();
      this.RULE_OCCURRENCE_STACK.pop();
      this.cstFinallyStateUpdate();
      if (this.RULE_STACK.length === 0 && this.isAtEndOfInput() === false) {
        var firstRedundantTok = this.LA(1);
        var errMsg = this.errorMessageProvider.buildNotAllInputParsedMessage({
          firstRedundant: firstRedundantTok,
          ruleName: this.getCurrRuleFullName()
        });
        this.SAVE_ERROR(new NotAllInputParsedException(errMsg, firstRedundantTok));
      }
    };
    RecognizerEngine2.prototype.subruleInternal = function(ruleToCall, idx, options) {
      var ruleResult;
      try {
        var args = options !== void 0 ? options.ARGS : void 0;
        ruleResult = ruleToCall.call(this, idx, args);
        this.cstPostNonTerminal(ruleResult, options !== void 0 && options.LABEL !== void 0 ? options.LABEL : ruleToCall.ruleName);
        return ruleResult;
      } catch (e3) {
        this.subruleInternalError(e3, options, ruleToCall.ruleName);
      }
    };
    RecognizerEngine2.prototype.subruleInternalError = function(e3, options, ruleName) {
      if (isRecognitionException(e3) && e3.partialCstResult !== void 0) {
        this.cstPostNonTerminal(e3.partialCstResult, options !== void 0 && options.LABEL !== void 0 ? options.LABEL : ruleName);
        delete e3.partialCstResult;
      }
      throw e3;
    };
    RecognizerEngine2.prototype.consumeInternal = function(tokType, idx, options) {
      var consumedToken;
      try {
        var nextToken = this.LA(1);
        if (this.tokenMatcher(nextToken, tokType) === true) {
          this.consumeToken();
          consumedToken = nextToken;
        } else {
          this.consumeInternalError(tokType, nextToken, options);
        }
      } catch (eFromConsumption) {
        consumedToken = this.consumeInternalRecovery(tokType, idx, eFromConsumption);
      }
      this.cstPostTerminal(options !== void 0 && options.LABEL !== void 0 ? options.LABEL : tokType.name, consumedToken);
      return consumedToken;
    };
    RecognizerEngine2.prototype.consumeInternalError = function(tokType, nextToken, options) {
      var msg;
      var previousToken = this.LA(0);
      if (options !== void 0 && options.ERR_MSG) {
        msg = options.ERR_MSG;
      } else {
        msg = this.errorMessageProvider.buildMismatchTokenMessage({
          expected: tokType,
          actual: nextToken,
          previous: previousToken,
          ruleName: this.getCurrRuleFullName()
        });
      }
      throw this.SAVE_ERROR(new MismatchedTokenException(msg, nextToken, previousToken));
    };
    RecognizerEngine2.prototype.consumeInternalRecovery = function(tokType, idx, eFromConsumption) {
      if (this.recoveryEnabled && // TODO: more robust checking of the exception type. Perhaps Typescript extending expressions?
      eFromConsumption.name === "MismatchedTokenException" && !this.isBackTracking()) {
        var follows = this.getFollowsForInRuleRecovery(tokType, idx);
        try {
          return this.tryInRuleRecovery(tokType, follows);
        } catch (eFromInRuleRecovery) {
          if (eFromInRuleRecovery.name === IN_RULE_RECOVERY_EXCEPTION) {
            throw eFromConsumption;
          } else {
            throw eFromInRuleRecovery;
          }
        }
      } else {
        throw eFromConsumption;
      }
    };
    RecognizerEngine2.prototype.saveRecogState = function() {
      var savedErrors = this.errors;
      var savedRuleStack = cloneArr(this.RULE_STACK);
      return {
        errors: savedErrors,
        lexerState: this.exportLexerState(),
        RULE_STACK: savedRuleStack,
        CST_STACK: this.CST_STACK,
        LAST_EXPLICIT_RULE_STACK: this.LAST_EXPLICIT_RULE_STACK
      };
    };
    RecognizerEngine2.prototype.reloadRecogState = function(newState) {
      this.errors = newState.errors;
      this.importLexerState(newState.lexerState);
      this.RULE_STACK = newState.RULE_STACK;
    };
    RecognizerEngine2.prototype.ruleInvocationStateUpdate = function(shortName, fullName, idxInCallingRule) {
      this.RULE_OCCURRENCE_STACK.push(idxInCallingRule);
      this.RULE_STACK.push(shortName);
      this.cstInvocationStateUpdate(fullName, shortName);
    };
    RecognizerEngine2.prototype.isBackTracking = function() {
      return this.isBackTrackingStack.length !== 0;
    };
    RecognizerEngine2.prototype.getCurrRuleFullName = function() {
      var shortName = this.getLastExplicitRuleShortName();
      return this.shortRuleNameToFull[shortName];
    };
    RecognizerEngine2.prototype.shortRuleNameToFullName = function(shortName) {
      return this.shortRuleNameToFull[shortName];
    };
    RecognizerEngine2.prototype.isAtEndOfInput = function() {
      return this.tokenMatcher(this.LA(1), EOF);
    };
    RecognizerEngine2.prototype.reset = function() {
      this.resetLexerState();
      this.isBackTrackingStack = [];
      this.errors = [];
      this.RULE_STACK = [];
      this.LAST_EXPLICIT_RULE_STACK = [];
      this.CST_STACK = [];
      this.RULE_OCCURRENCE_STACK = [];
    };
    return RecognizerEngine2;
  })()
);
var ErrorHandler = (
  /** @class */
  (function() {
    function ErrorHandler2() {
    }
    ErrorHandler2.prototype.initErrorHandler = function(config) {
      this._errors = [];
      this.errorMessageProvider = defaults(config.errorMessageProvider, DEFAULT_PARSER_CONFIG.errorMessageProvider);
    };
    ErrorHandler2.prototype.SAVE_ERROR = function(error) {
      if (isRecognitionException(error)) {
        error.context = {
          ruleStack: this.getHumanReadableRuleStack(),
          ruleOccurrenceStack: cloneArr(this.RULE_OCCURRENCE_STACK)
        };
        this._errors.push(error);
        return error;
      } else {
        throw Error("Trying to save an Error which is not a RecognitionException");
      }
    };
    Object.defineProperty(ErrorHandler2.prototype, "errors", {
      // TODO: extract these methods to ErrorHandler Trait?
      get: function() {
        return cloneArr(this._errors);
      },
      set: function(newErrors) {
        this._errors = newErrors;
      },
      enumerable: true,
      configurable: true
    });
    ErrorHandler2.prototype.raiseEarlyExitException = function(occurrence, prodType, userDefinedErrMsg) {
      var ruleName = this.getCurrRuleFullName();
      var ruleGrammar = this.getGAstProductions()[ruleName];
      var lookAheadPathsPerAlternative = getLookaheadPathsForOptionalProd(occurrence, ruleGrammar, prodType, this.maxLookahead);
      var insideProdPaths = lookAheadPathsPerAlternative[0];
      var actualTokens = [];
      for (var i2 = 1; i2 <= this.maxLookahead; i2++) {
        actualTokens.push(this.LA(i2));
      }
      var msg = this.errorMessageProvider.buildEarlyExitMessage({
        expectedIterationPaths: insideProdPaths,
        actual: actualTokens,
        previous: this.LA(0),
        customUserDescription: userDefinedErrMsg,
        ruleName
      });
      throw this.SAVE_ERROR(new EarlyExitException(msg, this.LA(1), this.LA(0)));
    };
    ErrorHandler2.prototype.raiseNoAltException = function(occurrence, errMsgTypes) {
      var ruleName = this.getCurrRuleFullName();
      var ruleGrammar = this.getGAstProductions()[ruleName];
      var lookAheadPathsPerAlternative = getLookaheadPathsForOr(occurrence, ruleGrammar, this.maxLookahead);
      var actualTokens = [];
      for (var i2 = 1; i2 <= this.maxLookahead; i2++) {
        actualTokens.push(this.LA(i2));
      }
      var previousToken = this.LA(0);
      var errMsg = this.errorMessageProvider.buildNoViableAltMessage({
        expectedPathsPerAlt: lookAheadPathsPerAlternative,
        actual: actualTokens,
        previous: previousToken,
        customUserDescription: errMsgTypes,
        ruleName: this.getCurrRuleFullName()
      });
      throw this.SAVE_ERROR(new NoViableAltException(errMsg, this.LA(1), previousToken));
    };
    return ErrorHandler2;
  })()
);
var ContentAssist = (
  /** @class */
  (function() {
    function ContentAssist2() {
    }
    ContentAssist2.prototype.initContentAssist = function() {
    };
    ContentAssist2.prototype.computeContentAssist = function(startRuleName, precedingInput) {
      var startRuleGast = this.gastProductionsCache[startRuleName];
      if (isUndefined(startRuleGast)) {
        throw Error("Rule ->" + startRuleName + "<- does not exist in this grammar.");
      }
      return nextPossibleTokensAfter([startRuleGast], precedingInput, this.tokenMatcher, this.maxLookahead);
    };
    ContentAssist2.prototype.getNextPossibleTokenTypes = function(grammarPath) {
      var topRuleName = first$1(grammarPath.ruleStack);
      var gastProductions = this.getGAstProductions();
      var topProduction = gastProductions[topRuleName];
      var nextPossibleTokenTypes = new NextAfterTokenWalker(topProduction, grammarPath).startWalking();
      return nextPossibleTokenTypes;
    };
    return ContentAssist2;
  })()
);
var RECORDING_NULL_OBJECT = {
  description: "This Object indicates the Parser is during Recording Phase"
};
Object.freeze(RECORDING_NULL_OBJECT);
var HANDLE_SEPARATOR = true;
var MAX_METHOD_IDX = Math.pow(2, BITS_FOR_OCCURRENCE_IDX) - 1;
var RFT = createToken({ name: "RECORDING_PHASE_TOKEN", pattern: Lexer.NA });
augmentTokenTypes([RFT]);
var RECORDING_PHASE_TOKEN = createTokenInstance(
  RFT,
  "This IToken indicates the Parser is in Recording Phase\n	See: https://sap.github.io/chevrotain/docs/guide/internals.html#grammar-recording for details",
  // Using "-1" instead of NaN (as in EOF) because an actual number is less likely to
  // cause errors if the output of LA or CONSUME would be (incorrectly) used during the recording phase.
  -1,
  -1,
  -1,
  -1,
  -1,
  -1
);
Object.freeze(RECORDING_PHASE_TOKEN);
var RECORDING_PHASE_CSTNODE = {
  name: "This CSTNode indicates the Parser is in Recording Phase\n	See: https://sap.github.io/chevrotain/docs/guide/internals.html#grammar-recording for details",
  children: {}
};
var GastRecorder = (
  /** @class */
  (function() {
    function GastRecorder2() {
    }
    GastRecorder2.prototype.initGastRecorder = function(config) {
      this.recordingProdStack = [];
      this.RECORDING_PHASE = false;
    };
    GastRecorder2.prototype.enableRecording = function() {
      var _this = this;
      this.RECORDING_PHASE = true;
      this.TRACE_INIT("Enable Recording", function() {
        var _loop_1 = function(i3) {
          var idx = i3 > 0 ? i3 : "";
          _this["CONSUME" + idx] = function(arg1, arg2) {
            return this.consumeInternalRecord(arg1, i3, arg2);
          };
          _this["SUBRULE" + idx] = function(arg1, arg2) {
            return this.subruleInternalRecord(arg1, i3, arg2);
          };
          _this["OPTION" + idx] = function(arg1) {
            return this.optionInternalRecord(arg1, i3);
          };
          _this["OR" + idx] = function(arg1) {
            return this.orInternalRecord(arg1, i3);
          };
          _this["MANY" + idx] = function(arg1) {
            this.manyInternalRecord(i3, arg1);
          };
          _this["MANY_SEP" + idx] = function(arg1) {
            this.manySepFirstInternalRecord(i3, arg1);
          };
          _this["AT_LEAST_ONE" + idx] = function(arg1) {
            this.atLeastOneInternalRecord(i3, arg1);
          };
          _this["AT_LEAST_ONE_SEP" + idx] = function(arg1) {
            this.atLeastOneSepFirstInternalRecord(i3, arg1);
          };
        };
        for (var i2 = 0; i2 < 10; i2++) {
          _loop_1(i2);
        }
        _this["consume"] = function(idx, arg1, arg2) {
          return this.consumeInternalRecord(arg1, idx, arg2);
        };
        _this["subrule"] = function(idx, arg1, arg2) {
          return this.subruleInternalRecord(arg1, idx, arg2);
        };
        _this["option"] = function(idx, arg1) {
          return this.optionInternalRecord(arg1, idx);
        };
        _this["or"] = function(idx, arg1) {
          return this.orInternalRecord(arg1, idx);
        };
        _this["many"] = function(idx, arg1) {
          this.manyInternalRecord(idx, arg1);
        };
        _this["atLeastOne"] = function(idx, arg1) {
          this.atLeastOneInternalRecord(idx, arg1);
        };
        _this.ACTION = _this.ACTION_RECORD;
        _this.BACKTRACK = _this.BACKTRACK_RECORD;
        _this.LA = _this.LA_RECORD;
      });
    };
    GastRecorder2.prototype.disableRecording = function() {
      var _this = this;
      this.RECORDING_PHASE = false;
      this.TRACE_INIT("Deleting Recording methods", function() {
        for (var i2 = 0; i2 < 10; i2++) {
          var idx = i2 > 0 ? i2 : "";
          delete _this["CONSUME" + idx];
          delete _this["SUBRULE" + idx];
          delete _this["OPTION" + idx];
          delete _this["OR" + idx];
          delete _this["MANY" + idx];
          delete _this["MANY_SEP" + idx];
          delete _this["AT_LEAST_ONE" + idx];
          delete _this["AT_LEAST_ONE_SEP" + idx];
        }
        delete _this["consume"];
        delete _this["subrule"];
        delete _this["option"];
        delete _this["or"];
        delete _this["many"];
        delete _this["atLeastOne"];
        delete _this.ACTION;
        delete _this.BACKTRACK;
        delete _this.LA;
      });
    };
    GastRecorder2.prototype.ACTION_RECORD = function(impl) {
      return;
    };
    GastRecorder2.prototype.BACKTRACK_RECORD = function(grammarRule, args) {
      return function() {
        return true;
      };
    };
    GastRecorder2.prototype.LA_RECORD = function(howMuch) {
      return END_OF_FILE;
    };
    GastRecorder2.prototype.topLevelRuleRecord = function(name, def) {
      try {
        var newTopLevelRule = new Rule({ definition: [], name });
        newTopLevelRule.name = name;
        this.recordingProdStack.push(newTopLevelRule);
        def.call(this);
        this.recordingProdStack.pop();
        return newTopLevelRule;
      } catch (originalError) {
        if (originalError.KNOWN_RECORDER_ERROR !== true) {
          try {
            originalError.message = originalError.message + '\n	 This error was thrown during the "grammar recording phase" For more info see:\n	https://sap.github.io/chevrotain/docs/guide/internals.html#grammar-recording';
          } catch (mutabilityError) {
            throw originalError;
          }
        }
        throw originalError;
      }
    };
    GastRecorder2.prototype.optionInternalRecord = function(actionORMethodDef, occurrence) {
      return recordProd.call(this, Option2, actionORMethodDef, occurrence);
    };
    GastRecorder2.prototype.atLeastOneInternalRecord = function(occurrence, actionORMethodDef) {
      recordProd.call(this, RepetitionMandatory, actionORMethodDef, occurrence);
    };
    GastRecorder2.prototype.atLeastOneSepFirstInternalRecord = function(occurrence, options) {
      recordProd.call(this, RepetitionMandatoryWithSeparator, options, occurrence, HANDLE_SEPARATOR);
    };
    GastRecorder2.prototype.manyInternalRecord = function(occurrence, actionORMethodDef) {
      recordProd.call(this, Repetition, actionORMethodDef, occurrence);
    };
    GastRecorder2.prototype.manySepFirstInternalRecord = function(occurrence, options) {
      recordProd.call(this, RepetitionWithSeparator, options, occurrence, HANDLE_SEPARATOR);
    };
    GastRecorder2.prototype.orInternalRecord = function(altsOrOpts, occurrence) {
      return recordOrProd.call(this, altsOrOpts, occurrence);
    };
    GastRecorder2.prototype.subruleInternalRecord = function(ruleToCall, occurrence, options) {
      assertMethodIdxIsValid(occurrence);
      if (!ruleToCall || has(ruleToCall, "ruleName") === false) {
        var error = new Error("<SUBRULE" + getIdxSuffix(occurrence) + "> argument is invalid" + (" expecting a Parser method reference but got: <" + JSON.stringify(ruleToCall) + ">") + ("\n inside top level rule: <" + this.recordingProdStack[0].name + ">"));
        error.KNOWN_RECORDER_ERROR = true;
        throw error;
      }
      var prevProd = peek(this.recordingProdStack);
      var ruleName = ruleToCall["ruleName"];
      var newNoneTerminal = new NonTerminal({
        idx: occurrence,
        nonTerminalName: ruleName,
        // The resolving of the `referencedRule` property will be done once all the Rule's GASTs have been created
        referencedRule: void 0
      });
      prevProd.definition.push(newNoneTerminal);
      return this.outputCst ? RECORDING_PHASE_CSTNODE : RECORDING_NULL_OBJECT;
    };
    GastRecorder2.prototype.consumeInternalRecord = function(tokType, occurrence, options) {
      assertMethodIdxIsValid(occurrence);
      if (!hasShortKeyProperty(tokType)) {
        var error = new Error("<CONSUME" + getIdxSuffix(occurrence) + "> argument is invalid" + (" expecting a TokenType reference but got: <" + JSON.stringify(tokType) + ">") + ("\n inside top level rule: <" + this.recordingProdStack[0].name + ">"));
        error.KNOWN_RECORDER_ERROR = true;
        throw error;
      }
      var prevProd = peek(this.recordingProdStack);
      var newNoneTerminal = new Terminal({
        idx: occurrence,
        terminalType: tokType
      });
      prevProd.definition.push(newNoneTerminal);
      return RECORDING_PHASE_TOKEN;
    };
    return GastRecorder2;
  })()
);
function recordProd(prodConstructor, mainProdArg, occurrence, handleSep) {
  if (handleSep === void 0) {
    handleSep = false;
  }
  assertMethodIdxIsValid(occurrence);
  var prevProd = peek(this.recordingProdStack);
  var grammarAction = isFunction(mainProdArg) ? mainProdArg : mainProdArg.DEF;
  var newProd = new prodConstructor({ definition: [], idx: occurrence });
  if (has(mainProdArg, "NAME")) {
    newProd.name = mainProdArg.NAME;
  }
  if (handleSep) {
    newProd.separator = mainProdArg.SEP;
  }
  if (has(mainProdArg, "MAX_LOOKAHEAD")) {
    newProd.maxLookahead = mainProdArg.MAX_LOOKAHEAD;
  }
  this.recordingProdStack.push(newProd);
  grammarAction.call(this);
  prevProd.definition.push(newProd);
  this.recordingProdStack.pop();
  return RECORDING_NULL_OBJECT;
}
function recordOrProd(mainProdArg, occurrence) {
  var _this = this;
  assertMethodIdxIsValid(occurrence);
  var prevProd = peek(this.recordingProdStack);
  var hasOptions = isArray(mainProdArg) === false;
  var alts = hasOptions === false ? mainProdArg : mainProdArg.DEF;
  var newOrProd = new Alternation({
    definition: [],
    idx: occurrence,
    ignoreAmbiguities: hasOptions && mainProdArg.IGNORE_AMBIGUITIES === true
  });
  if (has(mainProdArg, "NAME")) {
    newOrProd.name = mainProdArg.NAME;
  }
  if (has(mainProdArg, "MAX_LOOKAHEAD")) {
    newOrProd.maxLookahead = mainProdArg.MAX_LOOKAHEAD;
  }
  var hasPredicates = some(alts, function(currAlt) {
    return isFunction(currAlt.GATE);
  });
  newOrProd.hasPredicates = hasPredicates;
  prevProd.definition.push(newOrProd);
  forEach(alts, function(currAlt) {
    var currAltFlat = new Flat({ definition: [] });
    newOrProd.definition.push(currAltFlat);
    if (has(currAlt, "NAME")) {
      currAltFlat.name = currAlt.NAME;
    }
    if (has(currAlt, "IGNORE_AMBIGUITIES")) {
      currAltFlat.ignoreAmbiguities = currAlt.IGNORE_AMBIGUITIES;
    } else if (has(currAlt, "GATE")) {
      currAltFlat.ignoreAmbiguities = true;
    }
    _this.recordingProdStack.push(currAltFlat);
    currAlt.ALT.call(_this);
    _this.recordingProdStack.pop();
  });
  return RECORDING_NULL_OBJECT;
}
function getIdxSuffix(idx) {
  return idx === 0 ? "" : "" + idx;
}
function assertMethodIdxIsValid(idx) {
  if (idx < 0 || idx > MAX_METHOD_IDX) {
    var error = new Error(
      // The stack trace will contain all the needed details
      "Invalid DSL Method idx value: <" + idx + ">\n	" + ("Idx value must be a none negative value smaller than " + (MAX_METHOD_IDX + 1))
    );
    error.KNOWN_RECORDER_ERROR = true;
    throw error;
  }
}
var PerformanceTracer = (
  /** @class */
  (function() {
    function PerformanceTracer2() {
    }
    PerformanceTracer2.prototype.initPerformanceTracer = function(config) {
      if (has(config, "traceInitPerf")) {
        var userTraceInitPerf = config.traceInitPerf;
        var traceIsNumber = typeof userTraceInitPerf === "number";
        this.traceInitMaxIdent = traceIsNumber ? userTraceInitPerf : Infinity;
        this.traceInitPerf = traceIsNumber ? userTraceInitPerf > 0 : userTraceInitPerf;
      } else {
        this.traceInitMaxIdent = 0;
        this.traceInitPerf = DEFAULT_PARSER_CONFIG.traceInitPerf;
      }
      this.traceInitIndent = -1;
    };
    PerformanceTracer2.prototype.TRACE_INIT = function(phaseDesc, phaseImpl) {
      if (this.traceInitPerf === true) {
        this.traceInitIndent++;
        var indent = new Array(this.traceInitIndent + 1).join("	");
        if (this.traceInitIndent < this.traceInitMaxIdent) {
          console.log(indent + "--> <" + phaseDesc + ">");
        }
        var _a2 = timer(phaseImpl), time = _a2.time, value = _a2.value;
        var traceMethod = time > 10 ? console.warn : console.log;
        if (this.traceInitIndent < this.traceInitMaxIdent) {
          traceMethod(indent + "<-- <" + phaseDesc + "> time: " + time + "ms");
        }
        this.traceInitIndent--;
        return value;
      } else {
        return phaseImpl();
      }
    };
    return PerformanceTracer2;
  })()
);
var __extends = /* @__PURE__ */ (function() {
  var extendStatics = function(d2, b2) {
    extendStatics = Object.setPrototypeOf || { __proto__: [] } instanceof Array && function(d3, b3) {
      d3.__proto__ = b3;
    } || function(d3, b3) {
      for (var p2 in b3) if (b3.hasOwnProperty(p2)) d3[p2] = b3[p2];
    };
    return extendStatics(d2, b2);
  };
  return function(d2, b2) {
    extendStatics(d2, b2);
    function __() {
      this.constructor = d2;
    }
    d2.prototype = b2 === null ? Object.create(b2) : (__.prototype = b2.prototype, new __());
  };
})();
var END_OF_FILE = createTokenInstance(EOF, "", NaN, NaN, NaN, NaN, NaN, NaN);
Object.freeze(END_OF_FILE);
var DEFAULT_PARSER_CONFIG = Object.freeze({
  recoveryEnabled: false,
  maxLookahead: 4,
  ignoredIssues: {},
  dynamicTokensEnabled: false,
  outputCst: true,
  errorMessageProvider: defaultParserErrorProvider,
  nodeLocationTracking: "none",
  traceInitPerf: false,
  skipValidations: false
});
var DEFAULT_RULE_CONFIG = Object.freeze({
  recoveryValueFunc: function() {
    return void 0;
  },
  resyncEnabled: true
});
var ParserDefinitionErrorType;
(function(ParserDefinitionErrorType2) {
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["INVALID_RULE_NAME"] = 0] = "INVALID_RULE_NAME";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["DUPLICATE_RULE_NAME"] = 1] = "DUPLICATE_RULE_NAME";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["INVALID_RULE_OVERRIDE"] = 2] = "INVALID_RULE_OVERRIDE";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["DUPLICATE_PRODUCTIONS"] = 3] = "DUPLICATE_PRODUCTIONS";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["UNRESOLVED_SUBRULE_REF"] = 4] = "UNRESOLVED_SUBRULE_REF";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["LEFT_RECURSION"] = 5] = "LEFT_RECURSION";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["NONE_LAST_EMPTY_ALT"] = 6] = "NONE_LAST_EMPTY_ALT";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["AMBIGUOUS_ALTS"] = 7] = "AMBIGUOUS_ALTS";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["CONFLICT_TOKENS_RULES_NAMESPACE"] = 8] = "CONFLICT_TOKENS_RULES_NAMESPACE";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["INVALID_TOKEN_NAME"] = 9] = "INVALID_TOKEN_NAME";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["INVALID_NESTED_RULE_NAME"] = 10] = "INVALID_NESTED_RULE_NAME";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["DUPLICATE_NESTED_NAME"] = 11] = "DUPLICATE_NESTED_NAME";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["NO_NON_EMPTY_LOOKAHEAD"] = 12] = "NO_NON_EMPTY_LOOKAHEAD";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["AMBIGUOUS_PREFIX_ALTS"] = 13] = "AMBIGUOUS_PREFIX_ALTS";
  ParserDefinitionErrorType2[ParserDefinitionErrorType2["TOO_MANY_ALTS"] = 14] = "TOO_MANY_ALTS";
})(ParserDefinitionErrorType || (ParserDefinitionErrorType = {}));
function EMPTY_ALT(value) {
  if (value === void 0) {
    value = void 0;
  }
  return function() {
    return value;
  };
}
var Parser = (
  /** @class */
  (function() {
    function Parser2(tokenVocabulary, config) {
      if (config === void 0) {
        config = DEFAULT_PARSER_CONFIG;
      }
      this.ignoredIssues = DEFAULT_PARSER_CONFIG.ignoredIssues;
      this.definitionErrors = [];
      this.selfAnalysisDone = false;
      var that = this;
      that.initErrorHandler(config);
      that.initLexerAdapter();
      that.initLooksAhead(config);
      that.initRecognizerEngine(tokenVocabulary, config);
      that.initRecoverable(config);
      that.initTreeBuilder(config);
      that.initContentAssist();
      that.initGastRecorder(config);
      that.initPerformanceTracer(config);
      if (has(config, "ignoredIssues") && config.ignoredIssues !== DEFAULT_PARSER_CONFIG.ignoredIssues) {
        PRINT_WARNING("The <ignoredIssues> IParserConfig property is soft-deprecated and will be removed in future versions.\n	Please use the <IGNORE_AMBIGUITIES> flag on the relevant DSL method instead.");
      }
      this.ignoredIssues = has(config, "ignoredIssues") ? config.ignoredIssues : DEFAULT_PARSER_CONFIG.ignoredIssues;
      this.skipValidations = has(config, "skipValidations") ? config.skipValidations : DEFAULT_PARSER_CONFIG.skipValidations;
    }
    Parser2.performSelfAnalysis = function(parserInstance) {
      parserInstance.performSelfAnalysis();
    };
    Parser2.prototype.performSelfAnalysis = function() {
      var _this = this;
      this.TRACE_INIT("performSelfAnalysis", function() {
        var defErrorsMsgs;
        _this.selfAnalysisDone = true;
        var className = _this.className;
        _this.TRACE_INIT("toFastProps", function() {
          toFastProperties(_this);
        });
        _this.TRACE_INIT("Grammar Recording", function() {
          try {
            _this.enableRecording();
            forEach(_this.definedRulesNames, function(currRuleName) {
              var wrappedRule = _this[currRuleName];
              var originalGrammarAction = wrappedRule["originalGrammarAction"];
              var recordedRuleGast = void 0;
              _this.TRACE_INIT(currRuleName + " Rule", function() {
                recordedRuleGast = _this.topLevelRuleRecord(currRuleName, originalGrammarAction);
              });
              _this.gastProductionsCache[currRuleName] = recordedRuleGast;
            });
          } finally {
            _this.disableRecording();
          }
        });
        var resolverErrors = [];
        _this.TRACE_INIT("Grammar Resolving", function() {
          resolverErrors = resolveGrammar({
            rules: values(_this.gastProductionsCache)
          });
          _this.definitionErrors.push.apply(_this.definitionErrors, resolverErrors);
        });
        _this.TRACE_INIT("Grammar Validations", function() {
          if (isEmpty(resolverErrors) && _this.skipValidations === false) {
            var validationErrors = validateGrammar({
              rules: values(_this.gastProductionsCache),
              maxLookahead: _this.maxLookahead,
              tokenTypes: values(_this.tokensMap),
              ignoredIssues: _this.ignoredIssues,
              errMsgProvider: defaultGrammarValidatorErrorProvider,
              grammarName: className
            });
            _this.definitionErrors.push.apply(_this.definitionErrors, validationErrors);
          }
        });
        if (isEmpty(_this.definitionErrors)) {
          if (_this.recoveryEnabled) {
            _this.TRACE_INIT("computeAllProdsFollows", function() {
              var allFollows = computeAllProdsFollows(values(_this.gastProductionsCache));
              _this.resyncFollows = allFollows;
            });
          }
          _this.TRACE_INIT("ComputeLookaheadFunctions", function() {
            _this.preComputeLookaheadFunctions(values(_this.gastProductionsCache));
          });
        }
        _this.TRACE_INIT("expandAllNestedRuleNames", function() {
          var cstAnalysisResult = expandAllNestedRuleNames(values(_this.gastProductionsCache), _this.fullRuleNameToShort);
          _this.allRuleNames = cstAnalysisResult.allRuleNames;
        });
        if (!Parser2.DEFER_DEFINITION_ERRORS_HANDLING && !isEmpty(_this.definitionErrors)) {
          defErrorsMsgs = map(_this.definitionErrors, function(defError) {
            return defError.message;
          });
          throw new Error("Parser Definition Errors detected:\n " + defErrorsMsgs.join("\n-------------------------------\n"));
        }
      });
    };
    Parser2.DEFER_DEFINITION_ERRORS_HANDLING = false;
    return Parser2;
  })()
);
applyMixins(Parser, [
  Recoverable,
  LooksAhead,
  TreeBuilder,
  LexerAdapter,
  RecognizerEngine,
  RecognizerApi,
  ErrorHandler,
  ContentAssist,
  GastRecorder,
  PerformanceTracer
]);
(function(_super) {
  __extends(CstParser, _super);
  function CstParser(tokenVocabulary, config) {
    if (config === void 0) {
      config = DEFAULT_PARSER_CONFIG;
    }
    var _this = this;
    var configClone = cloneObj(config);
    configClone.outputCst = true;
    _this = _super.call(this, tokenVocabulary, configClone) || this;
    return _this;
  }
  return CstParser;
})(Parser);
var EmbeddedActionsParser = (
  /** @class */
  (function(_super) {
    __extends(EmbeddedActionsParser2, _super);
    function EmbeddedActionsParser2(tokenVocabulary, config) {
      if (config === void 0) {
        config = DEFAULT_PARSER_CONFIG;
      }
      var _this = this;
      var configClone = cloneObj(config);
      configClone.outputCst = false;
      _this = _super.call(this, tokenVocabulary, configClone) || this;
      return _this;
    }
    return EmbeddedActionsParser2;
  })(Parser)
);
const parsingError = (type, message) => ({
  type,
  message
});
var ParsingErrorType;
(function(ParsingErrorType2) {
  ParsingErrorType2["LexingError"] = "LexingError";
  ParsingErrorType2["ParserError"] = "ParsingError";
  ParsingErrorType2["StaticOffsetError"] = "StaticOffsetError";
  ParsingErrorType2["StaticOffsetOutOfRangeError"] = "StaticOffsetOutOfRangeError";
  ParsingErrorType2["RangeOffsetNotAllowed"] = "RangeOffsetNotAllowed";
  ParsingErrorType2["InvalidRangeSize"] = "InvalidRangeSize";
})(ParsingErrorType || (ParsingErrorType = {}));
var AstNodeType;
(function(AstNodeType2) {
  AstNodeType2["EMPTY"] = "EMPTY";
  AstNodeType2["NUMBER"] = "NUMBER";
  AstNodeType2["STRING"] = "STRING";
  AstNodeType2["MINUS_UNARY_OP"] = "MINUS_UNARY_OP";
  AstNodeType2["PLUS_UNARY_OP"] = "PLUS_UNARY_OP";
  AstNodeType2["PERCENT_OP"] = "PERCENT_OP";
  AstNodeType2["CONCATENATE_OP"] = "CONCATENATE_OP";
  AstNodeType2["EQUALS_OP"] = "EQUALS_OP";
  AstNodeType2["NOT_EQUAL_OP"] = "NOT_EQUAL_OP";
  AstNodeType2["GREATER_THAN_OP"] = "GREATER_THAN_OP";
  AstNodeType2["LESS_THAN_OP"] = "LESS_THAN_OP";
  AstNodeType2["GREATER_THAN_OR_EQUAL_OP"] = "GREATER_THAN_OR_EQUAL_OP";
  AstNodeType2["LESS_THAN_OR_EQUAL_OP"] = "LESS_THAN_OR_EQUAL_OP";
  AstNodeType2["PLUS_OP"] = "PLUS_OP";
  AstNodeType2["MINUS_OP"] = "MINUS_OP";
  AstNodeType2["TIMES_OP"] = "TIMES_OP";
  AstNodeType2["DIV_OP"] = "DIV_OP";
  AstNodeType2["POWER_OP"] = "POWER_OP";
  AstNodeType2["FUNCTION_CALL"] = "FUNCTION_CALL";
  AstNodeType2["NAMED_EXPRESSION"] = "NAMED_EXPRESSION";
  AstNodeType2["PARENTHESIS"] = "PARENTHESES";
  AstNodeType2["CELL_REFERENCE"] = "CELL_REFERENCE";
  AstNodeType2["CELL_RANGE"] = "CELL_RANGE";
  AstNodeType2["COLUMN_RANGE"] = "COLUMN_RANGE";
  AstNodeType2["ROW_RANGE"] = "ROW_RANGE";
  AstNodeType2["ERROR"] = "ERROR";
  AstNodeType2["ERROR_WITH_RAW_INPUT"] = "ERROR_WITH_RAW_INPUT";
  AstNodeType2["ARRAY"] = "ARRAY";
})(AstNodeType || (AstNodeType = {}));
var RangeSheetReferenceType;
(function(RangeSheetReferenceType2) {
  RangeSheetReferenceType2[RangeSheetReferenceType2["RELATIVE"] = 0] = "RELATIVE";
  RangeSheetReferenceType2[RangeSheetReferenceType2["START_ABSOLUTE"] = 1] = "START_ABSOLUTE";
  RangeSheetReferenceType2[RangeSheetReferenceType2["BOTH_ABSOLUTE"] = 2] = "BOTH_ABSOLUTE";
})(RangeSheetReferenceType || (RangeSheetReferenceType = {}));
const buildEmptyArgAst = (leadingWhitespace) => ({
  type: AstNodeType.EMPTY,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildNumberAst = (value, leadingWhitespace) => ({
  type: AstNodeType.NUMBER,
  value,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildStringAst = (token) => {
  var _a2;
  return {
    type: AstNodeType.STRING,
    value: token.image.slice(1, -1),
    leadingWhitespace: (_a2 = token.leadingWhitespace) === null || _a2 === void 0 ? void 0 : _a2.image
  };
};
const buildCellReferenceAst = (reference, leadingWhitespace) => ({
  type: AstNodeType.CELL_REFERENCE,
  reference,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildCellRangeAst = (start, end, sheetReferenceType, leadingWhitespace) => {
  assertRangeConsistency(start, end, sheetReferenceType);
  return {
    type: AstNodeType.CELL_RANGE,
    start,
    end,
    sheetReferenceType,
    leadingWhitespace
  };
};
const buildColumnRangeAst = (start, end, sheetReferenceType, leadingWhitespace) => {
  assertRangeConsistency(start, end, sheetReferenceType);
  return {
    type: AstNodeType.COLUMN_RANGE,
    start,
    end,
    sheetReferenceType,
    leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
  };
};
const buildRowRangeAst = (start, end, sheetReferenceType, leadingWhitespace) => {
  assertRangeConsistency(start, end, sheetReferenceType);
  return {
    type: AstNodeType.ROW_RANGE,
    start,
    end,
    sheetReferenceType,
    leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
  };
};
const buildConcatenateOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.CONCATENATE_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildEqualsOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.EQUALS_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildNotEqualOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.NOT_EQUAL_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildGreaterThanOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.GREATER_THAN_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildLessThanOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.LESS_THAN_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildGreaterThanOrEqualOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.GREATER_THAN_OR_EQUAL_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildLessThanOrEqualOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.LESS_THAN_OR_EQUAL_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildPlusOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.PLUS_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildMinusOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.MINUS_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildTimesOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.TIMES_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildDivOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.DIV_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildPowerOpAst = (left, right, leadingWhitespace) => ({
  type: AstNodeType.POWER_OP,
  left,
  right,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildMinusUnaryOpAst = (value, leadingWhitespace) => ({
  type: AstNodeType.MINUS_UNARY_OP,
  value,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildPlusUnaryOpAst = (value, leadingWhitespace) => ({
  type: AstNodeType.PLUS_UNARY_OP,
  value,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildPercentOpAst = (value, leadingWhitespace) => ({
  type: AstNodeType.PERCENT_OP,
  value,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildProcedureAst = (procedureName, args, leadingWhitespace, internalWhitespace, hyperlink) => ({
  type: AstNodeType.FUNCTION_CALL,
  procedureName,
  args,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image,
  internalWhitespace: internalWhitespace === null || internalWhitespace === void 0 ? void 0 : internalWhitespace.image,
  hyperlink
});
const buildArrayAst = (args, leadingWhitespace, internalWhitespace) => ({
  type: AstNodeType.ARRAY,
  args,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image,
  internalWhitespace: internalWhitespace === null || internalWhitespace === void 0 ? void 0 : internalWhitespace.image
});
const buildNamedExpressionAst = (expressionName, leadingWhitespace) => ({
  type: AstNodeType.NAMED_EXPRESSION,
  expressionName,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildParenthesisAst = (expression, leadingWhitespace, internalWhitespace) => ({
  type: AstNodeType.PARENTHESIS,
  expression,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image,
  internalWhitespace: internalWhitespace === null || internalWhitespace === void 0 ? void 0 : internalWhitespace.image
});
const buildCellErrorAst = (error, leadingWhitespace) => ({
  type: AstNodeType.ERROR,
  error,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildErrorWithRawInputAst = (rawInput, error, leadingWhitespace) => ({
  type: AstNodeType.ERROR_WITH_RAW_INPUT,
  error,
  rawInput,
  leadingWhitespace: leadingWhitespace === null || leadingWhitespace === void 0 ? void 0 : leadingWhitespace.image
});
const buildParsingErrorAst = () => ({
  type: AstNodeType.ERROR,
  error: CellError.parsingError()
});
function assertRangeConsistency(start, end, sheetReferenceType) {
  if (start.sheet !== void 0 && end.sheet === void 0 || start.sheet === void 0 && end.sheet !== void 0) {
    throw new Error("Start address inconsistent with end address");
  }
  if (start.sheet === void 0 && sheetReferenceType !== RangeSheetReferenceType.RELATIVE || start.sheet !== void 0 && sheetReferenceType === RangeSheetReferenceType.RELATIVE) {
    throw new Error("Sheet address inconsistent with sheet reference type");
  }
}
function imageWithWhitespace(image, leadingWhitespace) {
  return (leadingWhitespace !== null && leadingWhitespace !== void 0 ? leadingWhitespace : "") + image;
}
const binaryOpTokenMap = {
  [AstNodeType.PLUS_OP]: "+",
  [AstNodeType.MINUS_OP]: "-",
  [AstNodeType.TIMES_OP]: "*",
  [AstNodeType.DIV_OP]: "/",
  [AstNodeType.CONCATENATE_OP]: "&",
  [AstNodeType.POWER_OP]: "^",
  [AstNodeType.EQUALS_OP]: "=",
  [AstNodeType.NOT_EQUAL_OP]: "<>",
  [AstNodeType.GREATER_THAN_OP]: ">",
  [AstNodeType.GREATER_THAN_OR_EQUAL_OP]: ">=",
  [AstNodeType.LESS_THAN_OP]: "<",
  [AstNodeType.LESS_THAN_OR_EQUAL_OP]: "<="
};
const buildCacheEntry = (ast, relativeDependencies, hasVolatileFunction, hasStructuralChangeFunction) => ({
  ast,
  relativeDependencies,
  hasVolatileFunction,
  hasStructuralChangeFunction
});
class Cache {
  constructor(functionRegistry) {
    this.functionRegistry = functionRegistry;
    this.cache = /* @__PURE__ */ new Map();
  }
  set(hash, ast) {
    const astRelativeDependencies = collectDependencies(ast, this.functionRegistry);
    const cacheEntry = buildCacheEntry(ast, astRelativeDependencies, doesContainFunctions(ast, this.functionRegistry.isFunctionVolatile), doesContainFunctions(ast, this.functionRegistry.isFunctionDependentOnSheetStructureChange));
    this.cache.set(hash, cacheEntry);
    return cacheEntry;
  }
  get(hash) {
    return this.cache.get(hash);
  }
  maybeSetAndThenGet(hash, ast) {
    const entryFromCache = this.cache.get(hash);
    if (entryFromCache !== void 0) {
      return entryFromCache.ast;
    } else {
      this.set(hash, ast);
      return ast;
    }
  }
}
const doesContainFunctions = (ast, functionCriterion) => {
  switch (ast.type) {
    case AstNodeType.EMPTY:
    case AstNodeType.NUMBER:
    case AstNodeType.STRING:
    case AstNodeType.ERROR:
    case AstNodeType.ERROR_WITH_RAW_INPUT:
    case AstNodeType.CELL_REFERENCE:
    case AstNodeType.CELL_RANGE:
    case AstNodeType.COLUMN_RANGE:
    case AstNodeType.ROW_RANGE:
    case AstNodeType.NAMED_EXPRESSION:
      return false;
    case AstNodeType.PERCENT_OP:
    case AstNodeType.PLUS_UNARY_OP:
    case AstNodeType.MINUS_UNARY_OP: {
      return doesContainFunctions(ast.value, functionCriterion);
    }
    case AstNodeType.CONCATENATE_OP:
    case AstNodeType.EQUALS_OP:
    case AstNodeType.NOT_EQUAL_OP:
    case AstNodeType.LESS_THAN_OP:
    case AstNodeType.GREATER_THAN_OP:
    case AstNodeType.LESS_THAN_OR_EQUAL_OP:
    case AstNodeType.GREATER_THAN_OR_EQUAL_OP:
    case AstNodeType.MINUS_OP:
    case AstNodeType.PLUS_OP:
    case AstNodeType.TIMES_OP:
    case AstNodeType.DIV_OP:
    case AstNodeType.POWER_OP:
      return doesContainFunctions(ast.left, functionCriterion) || doesContainFunctions(ast.right, functionCriterion);
    case AstNodeType.PARENTHESIS:
      return doesContainFunctions(ast.expression, functionCriterion);
    case AstNodeType.FUNCTION_CALL: {
      if (functionCriterion(ast.procedureName)) {
        return true;
      }
      return ast.args.some((arg) => doesContainFunctions(arg, functionCriterion));
    }
    case AstNodeType.ARRAY: {
      return ast.args.some((row) => row.some((arg) => doesContainFunctions(arg, functionCriterion)));
    }
  }
};
class ErrorMessage {
}
ErrorMessage.DistinctSigns = "Distinct signs.";
ErrorMessage.WrongArgNumber = "Wrong number of arguments.";
ErrorMessage.EmptyArg = "Empty function argument.";
ErrorMessage.EmptyArray = "Empty array not allowed.";
ErrorMessage.ArrayDimensions = "Array dimensions are not compatible.";
ErrorMessage.NoSpaceForArrayResult = "No space for array result.";
ErrorMessage.ValueSmall = "Value too small.";
ErrorMessage.ValueLarge = "Value too large.";
ErrorMessage.BadCriterion = "Incorrect criterion.";
ErrorMessage.RangeManySheets = "Range spans more than one sheet.";
ErrorMessage.CellRangeExpected = "Cell range expected.";
ErrorMessage.WrongDimension = "Wrong range dimension.";
ErrorMessage.ScalarExpected = "Cell range not allowed.";
ErrorMessage.NumberCoercion = "Value cannot be coerced to number.";
ErrorMessage.NumberExpected = "Number argument expected.";
ErrorMessage.IntegerExpected = "Value needs to be an integer.";
ErrorMessage.BadMode = "Mode not recognized.";
ErrorMessage.DateBounds = "Date outside of bounds.";
ErrorMessage.OutOfSheet = "Resulting reference is out of the sheet.";
ErrorMessage.WrongType = "Wrong type of argument.";
ErrorMessage.NaN = "NaN or infinite value encountered.";
ErrorMessage.EqualLength = "Ranges need to be of equal length.";
ErrorMessage.Negative = "Value cannot be negative.";
ErrorMessage.NotBinary = "String does not represent a binary number.";
ErrorMessage.NotOctal = "String does not represent an octal number.";
ErrorMessage.NotHex = "String does not represent a hexadecimal number.";
ErrorMessage.EndStartPeriod = "End period needs to be at least start period.";
ErrorMessage.CellRefExpected = "Cell reference expected.";
ErrorMessage.EmptyRange = "Empty range not allowed.";
ErrorMessage.BadRef = "Address is not correct.";
ErrorMessage.NumberRange = "Number-only range expected.";
ErrorMessage.ValueNotFound = "Value not found.";
ErrorMessage.ValueBaseLarge = "Value in base too large.";
ErrorMessage.ValueBaseSmall = "Value in base too small.";
ErrorMessage.ValueBaseLong = "Value in base too long.";
ErrorMessage.NegativeLength = "Length cannot be negative.";
ErrorMessage.PatternNotFound = "Pattern not found.";
ErrorMessage.OneValue = "Needs at least one value.";
ErrorMessage.TwoValues = "Range needs to contain at least two elements.";
ErrorMessage.ThreeValues = "Range needs to contain at least three elements.";
ErrorMessage.IndexBounds = "Index out of bounds.";
ErrorMessage.IndexLarge = "Index too large.";
ErrorMessage.Formula = "Expected formula.";
ErrorMessage.NegativeCount = "Count cannot be negative.";
ErrorMessage.ParseError = "Parsing error.";
ErrorMessage.SheetRef = "Sheet does not exist.";
ErrorMessage.PeriodLong = "Period number cannot exceed life length.";
ErrorMessage.InvalidDate = "Invalid date.";
ErrorMessage.BitshiftLong = "Result of bitshift is too long.";
ErrorMessage.EmptyString = "Empty-string argument not allowed.";
ErrorMessage.LengthBounds = "Length out of bounds.";
ErrorMessage.NegativeTime = "Time cannot be negative.";
ErrorMessage.NoDefault = "No default option.";
ErrorMessage.NoConditionMet = "None of the conditions were met.";
ErrorMessage.Selector = "Selector cannot exceed the number of arguments.";
ErrorMessage.StartEndDate = "Start date needs to be earlier than end date.";
ErrorMessage.IncorrectDateTime = "String does not represent correct DateTime.";
ErrorMessage.CharacterCodeBounds = "Character code out of bounds.";
ErrorMessage.NonZero = "Argument cannot be 0.";
ErrorMessage.LessThanOne = "Argument cannot be less than 1.";
ErrorMessage.WeekendString = "Incorrect weekend bitmask string.";
ErrorMessage.InvalidRoman = "Invalid roman numeral.";
ErrorMessage.WrongOrder = "Wrong order of values.";
ErrorMessage.ComplexNumberExpected = "Complex number expected.";
ErrorMessage.ShouldBeIorJ = "Should be 'i' or 'j'.";
ErrorMessage.SizeMismatch = "Array dimensions mismatched.";
ErrorMessage.ResultTooLong = "Result exceeds the maximum allowed length.";
ErrorMessage.FunctionName = (arg) => `Function name ${arg} not recognized.`;
ErrorMessage.NamedExpressionName = (arg) => `Named expression ${arg} not recognized.`;
ErrorMessage.LicenseKey = (arg) => `License key is ${arg}.`;
class CellReferenceMatcher {
  constructor() {
    this.POSSIBLE_START_CHARACTERS = [...ALL_UNICODE_LETTERS_ARRAY, ...ALL_DIGITS_ARRAY, ABSOLUTE_OPERATOR, "'", "_"];
    this.cellReferenceRegexp = new RegExp(CELL_REFERENCE_WITH_NEXT_CHARACTER_PATTERN, "y");
  }
  /**
   * Method used by the lexer to recognize CellReference token in text
   *
   * Note: using 'y' sticky flag for a named expression which is not supported on IE11...
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/sticky
   */
  match(text, startOffset) {
    this.cellReferenceRegexp.lastIndex = startOffset;
    const execResult = this.cellReferenceRegexp.exec(text + "@");
    if (execResult == null || execResult[1] == null) {
      return null;
    }
    execResult[0] = execResult[1];
    return execResult;
  }
}
class NamedExpressionMatcher {
  constructor() {
    this.POSSIBLE_START_CHARACTERS = [...ALL_UNICODE_LETTERS_ARRAY, "_"];
    this.namedExpressionRegexp = new RegExp(NAMED_EXPRESSION_PATTERN, "y");
    this.r1c1CellRefRegexp = new RegExp(`^${R1C1_CELL_REFERENCE_PATTERN}$`);
  }
  /**
   * Method used by the lexer to recognize NamedExpression token in text
   *
   * Note: using 'y' sticky flag for a named expression which is not supported on IE11...
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/sticky
   */
  match(text, startOffset) {
    this.namedExpressionRegexp.lastIndex = startOffset;
    const execResult = this.namedExpressionRegexp.exec(text);
    if (execResult == null || execResult[0] == null) {
      return null;
    }
    if (this.r1c1CellRefRegexp.test(execResult[0])) {
      return null;
    }
    return execResult;
  }
}
const AdditionOp = createToken({
  name: "AdditionOp",
  pattern: Lexer.NA
});
const PlusOp = createToken({
  name: "PlusOp",
  pattern: /\+/,
  categories: AdditionOp
});
const MinusOp = createToken({
  name: "MinusOp",
  pattern: /-/,
  categories: AdditionOp
});
const MultiplicationOp = createToken({
  name: "MultiplicationOp",
  pattern: Lexer.NA
});
const TimesOp = createToken({
  name: "TimesOp",
  pattern: /\*/,
  categories: MultiplicationOp
});
const DivOp = createToken({
  name: "DivOp",
  pattern: /\//,
  categories: MultiplicationOp
});
const PowerOp = createToken({
  name: "PowerOp",
  pattern: /\^/
});
const PercentOp = createToken({
  name: "PercentOp",
  pattern: /%/
});
const BooleanOp = createToken({
  name: "BooleanOp",
  pattern: Lexer.NA
});
const EqualsOp = createToken({
  name: "EqualsOp",
  pattern: /=/,
  categories: BooleanOp
});
const NotEqualOp = createToken({
  name: "NotEqualOp",
  pattern: /<>/,
  categories: BooleanOp
});
const GreaterThanOp = createToken({
  name: "GreaterThanOp",
  pattern: />/,
  categories: BooleanOp
});
const LessThanOp = createToken({
  name: "LessThanOp",
  pattern: /</,
  categories: BooleanOp
});
const GreaterThanOrEqualOp = createToken({
  name: "GreaterThanOrEqualOp",
  pattern: />=/,
  categories: BooleanOp
});
const LessThanOrEqualOp = createToken({
  name: "LessThanOrEqualOp",
  pattern: /<=/,
  categories: BooleanOp
});
const ConcatenateOp = createToken({
  name: "ConcatenateOp",
  pattern: /&/
});
const LParen = createToken({
  name: "LParen",
  pattern: /\(/
});
const RParen = createToken({
  name: "RParen",
  pattern: /\)/
});
const ArrayLParen = createToken({
  name: "ArrayLParen",
  pattern: /{/
});
const ArrayRParen = createToken({
  name: "ArrayRParen",
  pattern: /}/
});
const StringLiteral = createToken({
  name: "StringLiteral",
  pattern: /"([^"\\]*(\\.[^"\\]*)*)"/
});
const ErrorLiteral = createToken({
  name: "ErrorLiteral",
  pattern: /#[A-Za-z0-9\/]+[?!]?/
});
const RangeSeparator = createToken({
  name: "RangeSeparator",
  pattern: new RegExp(RANGE_OPERATOR)
});
const ColumnRange = createToken({
  name: "ColumnRange",
  pattern: new RegExp(`${COLUMN_REFERENCE_PATTERN}${RANGE_OPERATOR}${COLUMN_REFERENCE_PATTERN}`)
});
const RowRange = createToken({
  name: "RowRange",
  pattern: new RegExp(`${ROW_REFERENCE_PATTERN}${RANGE_OPERATOR}${ROW_REFERENCE_PATTERN}`)
});
const ProcedureName = createToken({
  name: "ProcedureName",
  pattern: new RegExp(`([${UNICODE_LETTER_PATTERN}][${NON_RESERVED_CHARACTER_PATTERN}]*)\\(`)
});
const cellReferenceMatcher = new CellReferenceMatcher();
const CellReference = createToken({
  name: "CellReference",
  pattern: cellReferenceMatcher.match.bind(cellReferenceMatcher),
  start_chars_hint: cellReferenceMatcher.POSSIBLE_START_CHARACTERS,
  line_breaks: false
});
const namedExpressionMatcher = new NamedExpressionMatcher();
const NamedExpression = createToken({
  name: "NamedExpression",
  pattern: namedExpressionMatcher.match.bind(namedExpressionMatcher),
  start_chars_hint: namedExpressionMatcher.POSSIBLE_START_CHARACTERS,
  line_breaks: false
});
const buildLexerConfig = (config) => {
  const offsetProcedureNameLiteral = config.translationPackage.getFunctionTranslation("OFFSET");
  const errorMapping = config.errorMapping;
  const functionMapping = config.translationPackage.buildFunctionMapping();
  const whitespaceTokenRegexp = new RegExp(config.ignoreWhiteSpace === "standard" ? ODFF_WHITESPACE_PATTERN : ALL_WHITESPACE_PATTERN);
  const WhiteSpace = createToken({
    name: "WhiteSpace",
    pattern: whitespaceTokenRegexp
  });
  const ArrayRowSeparator = createToken({
    name: "ArrayRowSep",
    pattern: config.arrayRowSeparator
  });
  const ArrayColSeparator = createToken({
    name: "ArrayColSep",
    pattern: config.arrayColumnSeparator
  });
  const NumberLiteral = createToken({
    name: "NumberLiteral",
    pattern: new RegExp(`(([${config.decimalSeparator}]\\d+)|(\\d+([${config.decimalSeparator}]\\d*)?))(e[+-]?\\d+)?`)
  });
  const OffsetProcedureName = createToken({
    name: "OffsetProcedureName",
    pattern: new RegExp(offsetProcedureNameLiteral, "i")
  });
  let ArgSeparator;
  let inject;
  if (config.functionArgSeparator === config.arrayColumnSeparator) {
    ArgSeparator = ArrayColSeparator;
    inject = [];
  } else if (config.functionArgSeparator === config.arrayRowSeparator) {
    ArgSeparator = ArrayRowSeparator;
    inject = [];
  } else {
    ArgSeparator = createToken({
      name: "ArgSeparator",
      pattern: config.functionArgSeparator
    });
    inject = [ArgSeparator];
  }
  const allTokens = [WhiteSpace, PlusOp, MinusOp, TimesOp, DivOp, PowerOp, EqualsOp, NotEqualOp, PercentOp, GreaterThanOrEqualOp, LessThanOrEqualOp, GreaterThanOp, LessThanOp, LParen, RParen, ArrayLParen, ArrayRParen, OffsetProcedureName, ProcedureName, RangeSeparator, ...inject, ColumnRange, RowRange, NumberLiteral, StringLiteral, ErrorLiteral, ConcatenateOp, BooleanOp, AdditionOp, MultiplicationOp, CellReference, NamedExpression, ArrayRowSeparator, ArrayColSeparator];
  return {
    ArgSeparator,
    NumberLiteral,
    OffsetProcedureName,
    ArrayRowSeparator,
    ArrayColSeparator,
    WhiteSpace,
    allTokens,
    errorMapping,
    functionMapping,
    decimalSeparator: config.decimalSeparator,
    maxColumns: config.maxColumns,
    maxRows: config.maxRows
  };
};
class FormulaParser extends EmbeddedActionsParser {
  constructor(lexerConfig, resolveSheetReference) {
    super(lexerConfig.allTokens, {
      outputCst: false,
      maxLookahead: 7
    });
    this.resolveSheetReference = resolveSheetReference;
    this.booleanExpressionOrEmpty = this.RULE("booleanExpressionOrEmpty", () => {
      return this.OR([{
        ALT: () => this.SUBRULE(this.booleanExpression)
      }, {
        ALT: EMPTY_ALT(buildEmptyArgAst())
      }]);
    });
    this.procedureExpression = this.RULE("procedureExpression", () => {
      var _a2;
      const procedureNameToken = this.CONSUME(ProcedureName);
      const procedureName = procedureNameToken.image.toUpperCase().slice(0, -1);
      const canonicalProcedureName = (_a2 = this.lexerConfig.functionMapping[procedureName]) !== null && _a2 !== void 0 ? _a2 : procedureName;
      const args = [];
      let argument = this.SUBRULE(this.booleanExpressionOrEmpty);
      this.MANY(() => {
        var _a3;
        const separator = this.CONSUME(this.lexerConfig.ArgSeparator);
        if (argument.type === AstNodeType.EMPTY) {
          argument.leadingWhitespace = (_a3 = separator.leadingWhitespace) === null || _a3 === void 0 ? void 0 : _a3.image;
        }
        args.push(argument);
        argument = this.SUBRULE2(this.booleanExpressionOrEmpty);
      });
      args.push(argument);
      if (args.length === 1 && args[0].type === AstNodeType.EMPTY) {
        args.length = 0;
      }
      const rParenToken = this.CONSUME(RParen);
      return buildProcedureAst(canonicalProcedureName, args, procedureNameToken.leadingWhitespace, rParenToken.leadingWhitespace);
    });
    this.namedExpressionExpression = this.RULE("namedExpressionExpression", () => {
      const name = this.CONSUME(NamedExpression);
      return buildNamedExpressionAst(name.image, name.leadingWhitespace);
    });
    this.offsetProcedureExpression = this.RULE("offsetProcedureExpression", () => {
      const args = [];
      this.CONSUME(this.lexerConfig.OffsetProcedureName);
      this.CONSUME(LParen);
      this.MANY_SEP({
        SEP: this.lexerConfig.ArgSeparator,
        DEF: () => {
          args.push(this.SUBRULE(this.booleanExpression));
        }
      });
      this.CONSUME(RParen);
      return this.handleOffsetHeuristic(args);
    });
    this.columnRangeExpression = this.RULE("columnRangeExpression", () => {
      const range = this.CONSUME(ColumnRange);
      const [startImage, endImage] = range.image.split(":");
      const firstAddress = this.ACTION(() => columnAddressFromString(startImage, this.formulaAddress, this.resolveSheetReference));
      const secondAddress = this.ACTION(() => columnAddressFromString(endImage, this.formulaAddress, this.resolveSheetReference));
      if (firstAddress === void 0 || secondAddress === void 0) {
        return buildCellErrorAst(new CellError(ErrorType.REF));
      }
      if (firstAddress.exceedsSheetSizeLimits(this.lexerConfig.maxColumns) || secondAddress.exceedsSheetSizeLimits(this.lexerConfig.maxColumns)) {
        return buildErrorWithRawInputAst(range.image, new CellError(ErrorType.NAME), range.leadingWhitespace);
      }
      if (firstAddress.sheet === void 0 && secondAddress.sheet !== void 0) {
        return this.parsingError(ParsingErrorType.ParserError, "Malformed range expression");
      }
      const {
        firstEnd,
        secondEnd,
        sheetRefType
      } = FormulaParser.fixSheetIdsForRangeEnds(firstAddress, secondAddress);
      return buildColumnRangeAst(firstEnd, secondEnd, sheetRefType, range.leadingWhitespace);
    });
    this.rowRangeExpression = this.RULE("rowRangeExpression", () => {
      const range = this.CONSUME(RowRange);
      const [startImage, endImage] = range.image.split(":");
      const firstAddress = this.ACTION(() => rowAddressFromString(startImage, this.formulaAddress, this.resolveSheetReference));
      const secondAddress = this.ACTION(() => rowAddressFromString(endImage, this.formulaAddress, this.resolveSheetReference));
      if (firstAddress === void 0 || secondAddress === void 0) {
        return buildCellErrorAst(new CellError(ErrorType.REF));
      }
      if (firstAddress.exceedsSheetSizeLimits(this.lexerConfig.maxRows) || secondAddress.exceedsSheetSizeLimits(this.lexerConfig.maxRows)) {
        return buildErrorWithRawInputAst(range.image, new CellError(ErrorType.NAME), range.leadingWhitespace);
      }
      if (firstAddress.sheet === void 0 && secondAddress.sheet !== void 0) {
        return this.parsingError(ParsingErrorType.ParserError, "Malformed range expression");
      }
      const {
        firstEnd,
        secondEnd,
        sheetRefType
      } = FormulaParser.fixSheetIdsForRangeEnds(firstAddress, secondAddress);
      return buildRowRangeAst(firstEnd, secondEnd, sheetRefType, range.leadingWhitespace);
    });
    this.cellReference = this.RULE("cellReference", () => {
      const cell = this.CONSUME(CellReference);
      const address = this.ACTION(() => {
        return cellAddressFromString(cell.image, this.formulaAddress, this.resolveSheetReference);
      });
      if (address === void 0) {
        return buildErrorWithRawInputAst(cell.image, new CellError(ErrorType.REF), cell.leadingWhitespace);
      } else if (address.exceedsSheetSizeLimits(this.lexerConfig.maxColumns, this.lexerConfig.maxRows)) {
        return buildErrorWithRawInputAst(cell.image, new CellError(ErrorType.NAME), cell.leadingWhitespace);
      } else {
        return buildCellReferenceAst(address, cell.leadingWhitespace);
      }
    });
    this.endRangeReference = this.RULE("endRangeReference", (start) => {
      var _a2;
      const end = this.CONSUME(CellReference);
      const startAddress = this.ACTION(() => {
        return cellAddressFromString(start.image, this.formulaAddress, this.resolveSheetReference);
      });
      const endAddress = this.ACTION(() => {
        return cellAddressFromString(end.image, this.formulaAddress, this.resolveSheetReference);
      });
      if (startAddress === void 0 || endAddress === void 0) {
        return this.ACTION(() => {
          return buildErrorWithRawInputAst(`${start.image}:${end.image}`, new CellError(ErrorType.REF), start.leadingWhitespace);
        });
      } else if (startAddress.exceedsSheetSizeLimits(this.lexerConfig.maxColumns, this.lexerConfig.maxRows) || endAddress.exceedsSheetSizeLimits(this.lexerConfig.maxColumns, this.lexerConfig.maxRows)) {
        return this.ACTION(() => {
          return buildErrorWithRawInputAst(`${start.image}:${end.image}`, new CellError(ErrorType.NAME), start.leadingWhitespace);
        });
      }
      return this.buildCellRange(startAddress, endAddress, (_a2 = start.leadingWhitespace) === null || _a2 === void 0 ? void 0 : _a2.image);
    });
    this.endOfRangeExpression = this.RULE("endOfRangeExpression", (start) => {
      return this.OR([{
        ALT: () => {
          return this.SUBRULE(this.endRangeReference, {
            ARGS: [start]
          });
        }
      }, {
        ALT: () => {
          var _a2;
          const offsetProcedure = this.SUBRULE(this.offsetProcedureExpression);
          const startAddress = this.ACTION(() => {
            return cellAddressFromString(start.image, this.formulaAddress, this.resolveSheetReference);
          });
          if (startAddress === void 0) {
            return buildCellErrorAst(new CellError(ErrorType.REF));
          }
          if (offsetProcedure.type === AstNodeType.CELL_REFERENCE) {
            return this.buildCellRange(startAddress, offsetProcedure.reference, (_a2 = start.leadingWhitespace) === null || _a2 === void 0 ? void 0 : _a2.image);
          } else {
            return this.parsingError(ParsingErrorType.RangeOffsetNotAllowed, "Range offset not allowed here");
          }
        }
      }]);
    });
    this.cellRangeExpression = this.RULE("cellRangeExpression", () => {
      const start = this.CONSUME(CellReference);
      this.CONSUME2(RangeSeparator);
      return this.SUBRULE(this.endOfRangeExpression, {
        ARGS: [start]
      });
    });
    this.endRangeWithOffsetStartReference = this.RULE("endRangeWithOffsetStartReference", (start) => {
      const end = this.CONSUME(CellReference);
      const endAddress = this.ACTION(() => {
        return cellAddressFromString(end.image, this.formulaAddress, this.resolveSheetReference);
      });
      if (endAddress === void 0) {
        return this.ACTION(() => {
          return buildCellErrorAst(new CellError(ErrorType.REF));
        });
      }
      return this.buildCellRange(start.reference, endAddress, start.leadingWhitespace);
    });
    this.endOfRangeWithOffsetStartExpression = this.RULE("endOfRangeWithOffsetStartExpression", (start) => {
      return this.OR([{
        ALT: () => {
          return this.SUBRULE(this.endRangeWithOffsetStartReference, {
            ARGS: [start]
          });
        }
      }, {
        ALT: () => {
          const offsetProcedure = this.SUBRULE(this.offsetProcedureExpression);
          if (offsetProcedure.type === AstNodeType.CELL_REFERENCE) {
            return this.buildCellRange(start.reference, offsetProcedure.reference, start.leadingWhitespace);
          } else {
            return this.parsingError(ParsingErrorType.RangeOffsetNotAllowed, "Range offset not allowed here");
          }
        }
      }]);
    });
    this.offsetExpression = this.RULE("offsetExpression", () => {
      const offsetProcedure = this.SUBRULE(this.offsetProcedureExpression);
      let end;
      this.OPTION(() => {
        this.CONSUME(RangeSeparator);
        if (offsetProcedure.type === AstNodeType.CELL_RANGE) {
          end = this.parsingError(ParsingErrorType.RangeOffsetNotAllowed, "Range offset not allowed here");
        } else {
          end = this.SUBRULE(this.endOfRangeWithOffsetStartExpression, {
            ARGS: [offsetProcedure]
          });
        }
      });
      if (end !== void 0) {
        return end;
      }
      return offsetProcedure;
    });
    this.insideArrayExpression = this.RULE("insideArrayExpression", () => {
      const ret = [[]];
      ret[ret.length - 1].push(this.SUBRULE(this.booleanExpression));
      this.MANY(() => {
        this.OR([{
          ALT: () => {
            this.CONSUME(this.lexerConfig.ArrayColSeparator);
            ret[ret.length - 1].push(this.SUBRULE2(this.booleanExpression));
          }
        }, {
          ALT: () => {
            this.CONSUME(this.lexerConfig.ArrayRowSeparator);
            ret.push([]);
            ret[ret.length - 1].push(this.SUBRULE3(this.booleanExpression));
          }
        }]);
      });
      return buildArrayAst(ret);
    });
    this.parenthesisExpression = this.RULE("parenthesisExpression", () => {
      const lParenToken = this.CONSUME(LParen);
      const expression = this.SUBRULE(this.booleanExpression);
      const rParenToken = this.CONSUME(RParen);
      return buildParenthesisAst(expression, lParenToken.leadingWhitespace, rParenToken.leadingWhitespace);
    });
    this.arrayExpression = this.RULE("arrayExpression", () => {
      return this.OR([{
        ALT: () => {
          const ltoken = this.CONSUME(ArrayLParen);
          const ret = this.SUBRULE(this.insideArrayExpression);
          const rtoken = this.CONSUME(ArrayRParen);
          return buildArrayAst(ret.args, ltoken.leadingWhitespace, rtoken.leadingWhitespace);
        }
      }, {
        ALT: () => this.SUBRULE(this.parenthesisExpression)
      }]);
    });
    this.numericStringToNumber = (input) => {
      const normalized = input.replace(this.lexerConfig.decimalSeparator, ".");
      return Number(normalized);
    };
    this.positiveAtomicExpression = this.RULE("positiveAtomicExpression", () => {
      var _a2;
      return this.OR((_a2 = this.atomicExpCache) !== null && _a2 !== void 0 ? _a2 : this.atomicExpCache = [{
        ALT: () => this.SUBRULE(this.arrayExpression)
      }, {
        ALT: () => this.SUBRULE(this.cellRangeExpression)
      }, {
        ALT: () => this.SUBRULE(this.columnRangeExpression)
      }, {
        ALT: () => this.SUBRULE(this.rowRangeExpression)
      }, {
        ALT: () => this.SUBRULE(this.offsetExpression)
      }, {
        ALT: () => this.SUBRULE(this.cellReference)
      }, {
        ALT: () => this.SUBRULE(this.procedureExpression)
      }, {
        ALT: () => this.SUBRULE(this.namedExpressionExpression)
      }, {
        ALT: () => {
          const number = this.CONSUME(this.lexerConfig.NumberLiteral);
          return buildNumberAst(this.numericStringToNumber(number.image), number.leadingWhitespace);
        }
      }, {
        ALT: () => {
          const str = this.CONSUME(StringLiteral);
          return buildStringAst(str);
        }
      }, {
        ALT: () => {
          const token = this.CONSUME(ErrorLiteral);
          const errString = token.image.toUpperCase();
          const errorType = this.lexerConfig.errorMapping[errString];
          if (errorType) {
            return buildCellErrorAst(new CellError(errorType), token.leadingWhitespace);
          } else {
            return this.parsingError(ParsingErrorType.ParserError, "Unknown error literal");
          }
        }
      }]);
    });
    this.rightUnaryOpAtomicExpression = this.RULE("rightUnaryOpAtomicExpression", () => {
      const positiveAtomicExpression = this.SUBRULE(this.positiveAtomicExpression);
      const percentage = this.OPTION(() => {
        return this.CONSUME(PercentOp);
      });
      if (percentage) {
        return buildPercentOpAst(positiveAtomicExpression, percentage.leadingWhitespace);
      }
      return positiveAtomicExpression;
    });
    this.atomicExpression = this.RULE("atomicExpression", () => {
      return this.OR([{
        ALT: () => {
          const op = this.CONSUME(AdditionOp);
          const value = this.SUBRULE(this.atomicExpression);
          if (tokenMatcher(op, PlusOp)) {
            return buildPlusUnaryOpAst(value, op.leadingWhitespace);
          } else if (tokenMatcher(op, MinusOp)) {
            return buildMinusUnaryOpAst(value, op.leadingWhitespace);
          } else {
            this.customParsingError = parsingError(ParsingErrorType.ParserError, "Mismatched token type");
            return this.customParsingError;
          }
        }
      }, {
        ALT: () => this.SUBRULE2(this.rightUnaryOpAtomicExpression)
      }]);
    });
    this.powerExpression = this.RULE("powerExpression", () => {
      let lhs = this.SUBRULE(this.atomicExpression);
      this.MANY(() => {
        const op = this.CONSUME(PowerOp);
        const rhs = this.SUBRULE2(this.atomicExpression);
        if (tokenMatcher(op, PowerOp)) {
          lhs = buildPowerOpAst(lhs, rhs, op.leadingWhitespace);
        } else {
          this.ACTION(() => {
            throw Error("Operator not supported");
          });
        }
      });
      return lhs;
    });
    this.multiplicationExpression = this.RULE("multiplicationExpression", () => {
      let lhs = this.SUBRULE(this.powerExpression);
      this.MANY(() => {
        const op = this.CONSUME(MultiplicationOp);
        const rhs = this.SUBRULE2(this.powerExpression);
        if (tokenMatcher(op, TimesOp)) {
          lhs = buildTimesOpAst(lhs, rhs, op.leadingWhitespace);
        } else if (tokenMatcher(op, DivOp)) {
          lhs = buildDivOpAst(lhs, rhs, op.leadingWhitespace);
        } else {
          this.ACTION(() => {
            throw Error("Operator not supported");
          });
        }
      });
      return lhs;
    });
    this.additionExpression = this.RULE("additionExpression", () => {
      let lhs = this.SUBRULE(this.multiplicationExpression);
      this.MANY(() => {
        const op = this.CONSUME(AdditionOp);
        const rhs = this.SUBRULE2(this.multiplicationExpression);
        if (tokenMatcher(op, PlusOp)) {
          lhs = buildPlusOpAst(lhs, rhs, op.leadingWhitespace);
        } else if (tokenMatcher(op, MinusOp)) {
          lhs = buildMinusOpAst(lhs, rhs, op.leadingWhitespace);
        } else {
          this.ACTION(() => {
            throw Error("Operator not supported");
          });
        }
      });
      return lhs;
    });
    this.concatenateExpression = this.RULE("concatenateExpression", () => {
      let lhs = this.SUBRULE(this.additionExpression);
      this.MANY(() => {
        const op = this.CONSUME(ConcatenateOp);
        const rhs = this.SUBRULE2(this.additionExpression);
        lhs = buildConcatenateOpAst(lhs, rhs, op.leadingWhitespace);
      });
      return lhs;
    });
    this.booleanExpression = this.RULE("booleanExpression", () => {
      let lhs = this.SUBRULE(this.concatenateExpression);
      this.MANY(() => {
        const op = this.CONSUME(BooleanOp);
        const rhs = this.SUBRULE2(this.concatenateExpression);
        if (tokenMatcher(op, EqualsOp)) {
          lhs = buildEqualsOpAst(lhs, rhs, op.leadingWhitespace);
        } else if (tokenMatcher(op, NotEqualOp)) {
          lhs = buildNotEqualOpAst(lhs, rhs, op.leadingWhitespace);
        } else if (tokenMatcher(op, GreaterThanOp)) {
          lhs = buildGreaterThanOpAst(lhs, rhs, op.leadingWhitespace);
        } else if (tokenMatcher(op, LessThanOp)) {
          lhs = buildLessThanOpAst(lhs, rhs, op.leadingWhitespace);
        } else if (tokenMatcher(op, GreaterThanOrEqualOp)) {
          lhs = buildGreaterThanOrEqualOpAst(lhs, rhs, op.leadingWhitespace);
        } else if (tokenMatcher(op, LessThanOrEqualOp)) {
          lhs = buildLessThanOrEqualOpAst(lhs, rhs, op.leadingWhitespace);
        } else {
          this.ACTION(() => {
            throw Error("Operator not supported");
          });
        }
      });
      return lhs;
    });
    this.formula = this.RULE("formula", () => {
      this.CONSUME(EqualsOp);
      return this.SUBRULE(this.booleanExpression);
    });
    this.lexerConfig = lexerConfig;
    this.formulaAddress = simpleCellAddress(0, 0, 0);
    this.performSelfAnalysis();
  }
  /**
   * Parses tokenized formula and builds abstract syntax tree
   *
   * @param {ExtendedToken[]} tokens - tokenized formula
   * @param {SimpleCellAddress} formulaAddress - address of the cell in which formula is located
   */
  parseFromTokens(tokens, formulaAddress) {
    this.input = tokens;
    let ast = this.formulaWithContext(formulaAddress);
    let errors = [];
    if (this.customParsingError) {
      errors.push(this.customParsingError);
    }
    errors = errors.concat(this.errors.map((e3) => ({
      type: ParsingErrorType.ParserError,
      message: e3.message
    })));
    if (errors.length > 0) {
      ast = buildParsingErrorAst();
    }
    return {
      ast,
      errors
    };
  }
  reset() {
    super.reset();
    this.customParsingError = void 0;
  }
  /**
   * Entry rule wrapper that sets formula address
   *
   * @param {SimpleCellAddress} address - address of the cell in which formula is located
   */
  formulaWithContext(address) {
    this.formulaAddress = address;
    return this.formula();
  }
  buildCellRange(firstAddress, secondAddress, leadingWhitespace) {
    if (firstAddress.sheet === void 0 && secondAddress.sheet !== void 0) {
      return this.parsingError(ParsingErrorType.ParserError, "Malformed range expression");
    }
    const {
      firstEnd,
      secondEnd,
      sheetRefType
    } = FormulaParser.fixSheetIdsForRangeEnds(firstAddress, secondAddress);
    return buildCellRangeAst(firstEnd, secondEnd, sheetRefType, leadingWhitespace);
  }
  static fixSheetIdsForRangeEnds(firstEnd, secondEnd) {
    const sheetRefType = FormulaParser.rangeSheetReferenceType(firstEnd.sheet, secondEnd.sheet);
    const secondEndFixed = firstEnd.sheet !== void 0 && secondEnd.sheet === void 0 ? secondEnd.withSheet(firstEnd.sheet) : secondEnd;
    return {
      firstEnd,
      secondEnd: secondEndFixed,
      sheetRefType
    };
  }
  /**
   * Returns {@link CellReferenceAst} or {@link CellRangeAst} based on OFFSET function arguments
   *
   * @param {Ast[]} args - OFFSET function arguments
   */
  handleOffsetHeuristic(args) {
    var _a2;
    const cellArg = args[0];
    if (cellArg.type !== AstNodeType.CELL_REFERENCE) {
      return this.parsingError(ParsingErrorType.StaticOffsetError, "First argument to OFFSET is not a reference");
    }
    const rowsArg = args[1];
    let rowShift;
    if (rowsArg.type === AstNodeType.NUMBER && Number.isInteger(rowsArg.value)) {
      rowShift = rowsArg.value;
    } else if (rowsArg.type === AstNodeType.PLUS_UNARY_OP && rowsArg.value.type === AstNodeType.NUMBER && Number.isInteger(rowsArg.value.value)) {
      rowShift = rowsArg.value.value;
    } else if (rowsArg.type === AstNodeType.MINUS_UNARY_OP && rowsArg.value.type === AstNodeType.NUMBER && Number.isInteger(rowsArg.value.value)) {
      rowShift = -rowsArg.value.value;
    } else {
      return this.parsingError(ParsingErrorType.StaticOffsetError, "Second argument to OFFSET is not a static number");
    }
    const columnsArg = args[2];
    let colShift;
    if (columnsArg.type === AstNodeType.NUMBER && Number.isInteger(columnsArg.value)) {
      colShift = columnsArg.value;
    } else if (columnsArg.type === AstNodeType.PLUS_UNARY_OP && columnsArg.value.type === AstNodeType.NUMBER && Number.isInteger(columnsArg.value.value)) {
      colShift = columnsArg.value.value;
    } else if (columnsArg.type === AstNodeType.MINUS_UNARY_OP && columnsArg.value.type === AstNodeType.NUMBER && Number.isInteger(columnsArg.value.value)) {
      colShift = -columnsArg.value.value;
    } else {
      return this.parsingError(ParsingErrorType.StaticOffsetError, "Third argument to OFFSET is not a static number");
    }
    const heightArg = args[3];
    let height;
    if (heightArg === void 0) {
      height = 1;
    } else if (heightArg.type === AstNodeType.NUMBER) {
      height = heightArg.value;
      if (height < 1) {
        return this.parsingError(ParsingErrorType.StaticOffsetError, "Fourth argument to OFFSET is too small number");
      } else if (!Number.isInteger(height)) {
        return this.parsingError(ParsingErrorType.StaticOffsetError, "Fourth argument to OFFSET is not integer");
      }
    } else {
      return this.parsingError(ParsingErrorType.StaticOffsetError, "Fourth argument to OFFSET is not a static number");
    }
    const widthArg = args[4];
    let width;
    if (widthArg === void 0) {
      width = 1;
    } else if (widthArg.type === AstNodeType.NUMBER) {
      width = widthArg.value;
      if (width < 1) {
        return this.parsingError(ParsingErrorType.StaticOffsetError, "Fifth argument to OFFSET is too small number");
      } else if (!Number.isInteger(width)) {
        return this.parsingError(ParsingErrorType.StaticOffsetError, "Fifth argument to OFFSET is not integer");
      }
    } else {
      return this.parsingError(ParsingErrorType.StaticOffsetError, "Fifth argument to OFFSET is not a static number");
    }
    const topLeftCorner = new CellAddress(cellArg.reference.col + colShift, cellArg.reference.row + rowShift, cellArg.reference.type, (_a2 = cellArg.reference.sheet) !== null && _a2 !== void 0 ? _a2 : void 0);
    let absoluteCol = topLeftCorner.col;
    let absoluteRow = topLeftCorner.row;
    if (cellArg.reference.type === CellReferenceType.CELL_REFERENCE_RELATIVE || cellArg.reference.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_COL) {
      absoluteRow = absoluteRow + this.formulaAddress.row;
    }
    if (cellArg.reference.type === CellReferenceType.CELL_REFERENCE_RELATIVE || cellArg.reference.type === CellReferenceType.CELL_REFERENCE_ABSOLUTE_ROW) {
      absoluteCol = absoluteCol + this.formulaAddress.col;
    }
    if (absoluteCol < 0 || absoluteRow < 0) {
      return buildCellErrorAst(new CellError(ErrorType.REF, ErrorMessage.OutOfSheet));
    }
    if (width === 1 && height === 1) {
      return buildCellReferenceAst(topLeftCorner);
    } else {
      const bottomRightCorner = new CellAddress(topLeftCorner.col + width - 1, topLeftCorner.row + height - 1, topLeftCorner.type, topLeftCorner.sheet);
      const rangeSheetReferenceType = cellArg.reference.sheet == null ? RangeSheetReferenceType.RELATIVE : RangeSheetReferenceType.BOTH_ABSOLUTE;
      return buildCellRangeAst(topLeftCorner, bottomRightCorner, rangeSheetReferenceType);
    }
  }
  parsingError(type, message) {
    this.customParsingError = parsingError(type, message);
    return buildParsingErrorAst();
  }
  static rangeSheetReferenceType(start, end) {
    if (start === void 0) {
      return RangeSheetReferenceType.RELATIVE;
    } else if (end === void 0) {
      return RangeSheetReferenceType.START_ABSOLUTE;
    } else {
      return RangeSheetReferenceType.BOTH_ABSOLUTE;
    }
  }
}
class FormulaLexer {
  constructor(lexerConfig) {
    this.lexerConfig = lexerConfig;
    this.lexer = new Lexer(lexerConfig.allTokens, {
      ensureOptimizations: true
    });
  }
  /**
   * Returns Lexer tokens from formula string
   *
   * @param {string} text - string representation of a formula
   */
  tokenizeFormula(text) {
    const lexingResult = this.lexer.tokenize(text);
    let tokens = lexingResult.tokens;
    tokens = this.trimTrailingWhitespaces(tokens);
    tokens = this.skipWhitespacesInsideRanges(tokens);
    tokens = this.skipWhitespacesBeforeArgSeparators(tokens);
    lexingResult.tokens = tokens;
    return lexingResult;
  }
  skipWhitespacesInsideRanges(tokens) {
    return FormulaLexer.filterTokensByNeighbors(tokens, (previous, current, next) => {
      return (tokenMatcher(previous, CellReference) || tokenMatcher(previous, RangeSeparator)) && tokenMatcher(current, this.lexerConfig.WhiteSpace) && (tokenMatcher(next, CellReference) || tokenMatcher(next, RangeSeparator));
    });
  }
  skipWhitespacesBeforeArgSeparators(tokens) {
    return FormulaLexer.filterTokensByNeighbors(tokens, (previous, current, next) => {
      return !tokenMatcher(previous, this.lexerConfig.ArgSeparator) && tokenMatcher(current, this.lexerConfig.WhiteSpace) && tokenMatcher(next, this.lexerConfig.ArgSeparator);
    });
  }
  static filterTokensByNeighbors(tokens, shouldBeSkipped) {
    if (tokens.length < 3) {
      return tokens;
    }
    let i2 = 0;
    const filteredTokens = [tokens[i2++]];
    while (i2 < tokens.length - 1) {
      if (!shouldBeSkipped(tokens[i2 - 1], tokens[i2], tokens[i2 + 1])) {
        filteredTokens.push(tokens[i2]);
      }
      ++i2;
    }
    filteredTokens.push(tokens[i2]);
    return filteredTokens;
  }
  trimTrailingWhitespaces(tokens) {
    if (tokens.length > 0 && tokenMatcher(tokens[tokens.length - 1], this.lexerConfig.WhiteSpace)) {
      tokens.pop();
    }
    return tokens;
  }
}
class Unparser {
  constructor(config, sheetMapping, namedExpressions) {
    this.config = config;
    this.sheetMapping = sheetMapping;
    this.namedExpressions = namedExpressions;
  }
  unparse(ast, address) {
    return "=" + this.unparseAst(ast, address);
  }
  unparseAst(ast, address) {
    var _a2, _b;
    switch (ast.type) {
      case AstNodeType.EMPTY: {
        return imageWithWhitespace("", ast.leadingWhitespace);
      }
      case AstNodeType.NUMBER: {
        return imageWithWhitespace(formatNumber(ast.value, this.config.decimalSeparator), ast.leadingWhitespace);
      }
      case AstNodeType.STRING: {
        return imageWithWhitespace('"' + ast.value + '"', ast.leadingWhitespace);
      }
      case AstNodeType.FUNCTION_CALL: {
        const args = ast.args.map((arg) => arg !== void 0 ? this.unparseAst(arg, address) : "").join(this.config.functionArgSeparator);
        const procedureName = this.config.translationPackage.isFunctionTranslated(ast.procedureName) ? this.config.translationPackage.getFunctionTranslation(ast.procedureName) : ast.procedureName;
        const rightPart = procedureName + "(" + args + imageWithWhitespace(")", ast.internalWhitespace);
        return imageWithWhitespace(rightPart, ast.leadingWhitespace);
      }
      case AstNodeType.NAMED_EXPRESSION: {
        const originalNamedExpressionName = (_a2 = this.namedExpressions.nearestNamedExpression(ast.expressionName, address.sheet)) === null || _a2 === void 0 ? void 0 : _a2.displayName;
        return imageWithWhitespace(originalNamedExpressionName || ast.expressionName, ast.leadingWhitespace);
      }
      case AstNodeType.CELL_REFERENCE: {
        let image;
        if (ast.reference.sheet !== void 0) {
          image = this.unparseSheetName(ast.reference.sheet) + "!";
        } else {
          image = "";
        }
        image += (_b = ast.reference.unparse(address)) !== null && _b !== void 0 ? _b : this.config.translationPackage.getErrorTranslation(ErrorType.REF);
        return imageWithWhitespace(image, ast.leadingWhitespace);
      }
      case AstNodeType.COLUMN_RANGE:
      case AstNodeType.ROW_RANGE:
      case AstNodeType.CELL_RANGE: {
        return imageWithWhitespace(this.formatRange(ast, address), ast.leadingWhitespace);
      }
      case AstNodeType.PLUS_UNARY_OP: {
        const unparsedExpr = this.unparseAst(ast.value, address);
        return imageWithWhitespace("+", ast.leadingWhitespace) + unparsedExpr;
      }
      case AstNodeType.MINUS_UNARY_OP: {
        const unparsedExpr = this.unparseAst(ast.value, address);
        return imageWithWhitespace("-", ast.leadingWhitespace) + unparsedExpr;
      }
      case AstNodeType.PERCENT_OP: {
        return this.unparseAst(ast.value, address) + imageWithWhitespace("%", ast.leadingWhitespace);
      }
      case AstNodeType.ERROR: {
        const image = this.config.translationPackage.getErrorTranslation(ast.error ? ast.error.type : ErrorType.ERROR);
        return imageWithWhitespace(image, ast.leadingWhitespace);
      }
      case AstNodeType.ERROR_WITH_RAW_INPUT: {
        return imageWithWhitespace(ast.rawInput, ast.leadingWhitespace);
      }
      case AstNodeType.PARENTHESIS: {
        const expression = this.unparseAst(ast.expression, address);
        const rightPart = "(" + expression + imageWithWhitespace(")", ast.internalWhitespace);
        return imageWithWhitespace(rightPart, ast.leadingWhitespace);
      }
      case AstNodeType.ARRAY: {
        const ret = "{" + ast.args.map((row) => row.map((val) => this.unparseAst(val, address)).join(this.config.arrayColumnSeparator)).join(this.config.arrayRowSeparator) + imageWithWhitespace("}", ast.internalWhitespace);
        return imageWithWhitespace(ret, ast.leadingWhitespace);
      }
      default: {
        const left = this.unparseAst(ast.left, address);
        const right = this.unparseAst(ast.right, address);
        return left + imageWithWhitespace(binaryOpTokenMap[ast.type], ast.leadingWhitespace) + right;
      }
    }
  }
  /**
   * Unparses a sheet name.
   * @param {number} sheetId - the ID of the sheet
   * @returns {string} the unparsed sheet name
   */
  unparseSheetName(sheetId) {
    const sheetName = sheetIndexToString(sheetId, (id) => this.sheetMapping.getSheetNameOrThrowError(id, {
      includePlaceholders: true
    }));
    if (sheetName === void 0) {
      throw new NoSheetWithIdError(sheetId);
    }
    return sheetName;
  }
  formatRange(ast, baseAddress) {
    let startSheeet = "";
    let endSheet = "";
    if (ast.start.sheet !== void 0 && ast.sheetReferenceType !== RangeSheetReferenceType.RELATIVE) {
      startSheeet = this.unparseSheetName(ast.start.sheet) + "!";
    }
    if (ast.end.sheet !== void 0 && ast.sheetReferenceType === RangeSheetReferenceType.BOTH_ABSOLUTE) {
      endSheet = this.unparseSheetName(ast.end.sheet) + "!";
    }
    const unparsedStart = ast.start.unparse(baseAddress);
    const unparsedEnd = ast.end.unparse(baseAddress);
    if (unparsedStart === void 0 || unparsedEnd === void 0) {
      return this.config.translationPackage.getErrorTranslation(ErrorType.REF);
    }
    return `${startSheeet}${unparsedStart}:${endSheet}${unparsedEnd}`;
  }
}
function formatNumber(number, decimalSeparator) {
  const numericString = number.toString();
  return numericString.replace(".", decimalSeparator);
}
class ParserWithCaching {
  constructor(config, functionRegistry, resolveSheetReference) {
    this.config = config;
    this.functionRegistry = functionRegistry;
    this.resolveSheetReference = resolveSheetReference;
    this.statsCacheUsed = 0;
    this.lexerConfig = buildLexerConfig(config);
    this.lexer = new FormulaLexer(this.lexerConfig);
    this.formulaParser = new FormulaParser(this.lexerConfig, this.resolveSheetReference);
    this.cache = new Cache(this.functionRegistry);
  }
  /**
   * Parses a formula.
   *
   * @param text - formula to parse
   * @param formulaAddress - address with regard to which formula should be parsed. Impacts computed addresses in R0C0 format.
   */
  parse(text, formulaAddress) {
    this.formulaAddress = formulaAddress;
    const lexerResult = this.tokenizeFormula(text);
    if (lexerResult.errors.length > 0) {
      const errors = lexerResult.errors.map((e3) => ({
        type: ParsingErrorType.LexingError,
        message: e3.message
      }));
      return {
        ast: buildParsingErrorAst(),
        errors,
        hasVolatileFunction: false,
        hasStructuralChangeFunction: false,
        dependencies: []
      };
    }
    const hash = this.computeHashFromTokens(lexerResult.tokens, formulaAddress);
    let cacheResult = this.cache.get(hash);
    if (cacheResult !== void 0) {
      ++this.statsCacheUsed;
    } else {
      const processedTokens = this.bindWhitespacesToTokens(lexerResult.tokens);
      const parsingResult = this.formulaParser.parseFromTokens(processedTokens, formulaAddress);
      if (parsingResult.errors.length > 0) {
        return Object.assign(Object.assign({}, parsingResult), {
          hasVolatileFunction: false,
          hasStructuralChangeFunction: false,
          dependencies: []
        });
      } else {
        cacheResult = this.cache.set(hash, parsingResult.ast);
      }
    }
    const {
      ast,
      hasVolatileFunction,
      hasStructuralChangeFunction
    } = cacheResult;
    const astWithNoReversedRanges = this.convertReversedRangesToRegularRanges(ast);
    const dependencies = collectDependencies(astWithNoReversedRanges, this.functionRegistry);
    return {
      ast: astWithNoReversedRanges,
      errors: [],
      hasVolatileFunction,
      hasStructuralChangeFunction,
      dependencies
    };
  }
  convertReversedRangesToRegularRanges(ast) {
    switch (ast.type) {
      case AstNodeType.EMPTY:
      case AstNodeType.NUMBER:
      case AstNodeType.STRING:
      case AstNodeType.ERROR:
      case AstNodeType.ERROR_WITH_RAW_INPUT:
      case AstNodeType.CELL_REFERENCE:
      case AstNodeType.NAMED_EXPRESSION:
        return ast;
      case AstNodeType.CELL_RANGE: {
        const {
          start,
          end
        } = ast;
        const orderedEnds = this.orderCellRangeEnds(start, end);
        return Object.assign(Object.assign({}, ast), {
          start: orderedEnds.start,
          end: orderedEnds.end
        });
      }
      case AstNodeType.COLUMN_RANGE: {
        const {
          start,
          end
        } = ast;
        const orderedEnds = this.orderColumnRangeEnds(start, end);
        return Object.assign(Object.assign({}, ast), {
          start: orderedEnds.start,
          end: orderedEnds.end
        });
      }
      case AstNodeType.ROW_RANGE: {
        const {
          start,
          end
        } = ast;
        const orderedEnds = this.orderRowRangeEnds(start, end);
        return Object.assign(Object.assign({}, ast), {
          start: orderedEnds.start,
          end: orderedEnds.end
        });
      }
      case AstNodeType.PERCENT_OP:
      case AstNodeType.PLUS_UNARY_OP:
      case AstNodeType.MINUS_UNARY_OP: {
        const valueFixed = this.convertReversedRangesToRegularRanges(ast.value);
        return Object.assign(Object.assign({}, ast), {
          value: valueFixed
        });
      }
      case AstNodeType.CONCATENATE_OP:
      case AstNodeType.EQUALS_OP:
      case AstNodeType.NOT_EQUAL_OP:
      case AstNodeType.LESS_THAN_OP:
      case AstNodeType.GREATER_THAN_OP:
      case AstNodeType.LESS_THAN_OR_EQUAL_OP:
      case AstNodeType.GREATER_THAN_OR_EQUAL_OP:
      case AstNodeType.MINUS_OP:
      case AstNodeType.PLUS_OP:
      case AstNodeType.TIMES_OP:
      case AstNodeType.DIV_OP:
      case AstNodeType.POWER_OP: {
        const leftFixed = this.convertReversedRangesToRegularRanges(ast.left);
        const rightFixed = this.convertReversedRangesToRegularRanges(ast.right);
        return Object.assign(Object.assign({}, ast), {
          left: leftFixed,
          right: rightFixed
        });
      }
      case AstNodeType.PARENTHESIS: {
        const exprFixed = this.convertReversedRangesToRegularRanges(ast.expression);
        return Object.assign(Object.assign({}, ast), {
          expression: exprFixed
        });
      }
      case AstNodeType.FUNCTION_CALL: {
        const argsFixed = ast.args.map((arg) => this.convertReversedRangesToRegularRanges(arg));
        return Object.assign(Object.assign({}, ast), {
          args: argsFixed
        });
      }
      case AstNodeType.ARRAY: {
        const argsFixed = ast.args.map((argsRow) => argsRow.map((arg) => this.convertReversedRangesToRegularRanges(arg)));
        return Object.assign(Object.assign({}, ast), {
          args: argsFixed
        });
      }
    }
  }
  orderCellRangeEnds(endA, endB) {
    const ends = [endA, endB];
    const [startCol, endCol] = ends.map((e3) => e3.toColumnAddress()).sort(ColumnAddress.compareByAbsoluteAddress(this.formulaAddress));
    const [startRow, endRow] = ends.map((e3) => e3.toRowAddress()).sort(RowAddress.compareByAbsoluteAddress(this.formulaAddress));
    const [startSheet, endSheet] = ends.map((e3) => e3.sheet).sort(ParserWithCaching.compareSheetIds.bind(this));
    return {
      start: CellAddress.fromColAndRow(startCol, startRow, startSheet),
      end: CellAddress.fromColAndRow(endCol, endRow, endSheet)
    };
  }
  orderColumnRangeEnds(endA, endB) {
    const ends = [endA, endB];
    const [startCol, endCol] = ends.sort(ColumnAddress.compareByAbsoluteAddress(this.formulaAddress));
    const [startSheet, endSheet] = ends.map((e3) => e3.sheet).sort(ParserWithCaching.compareSheetIds.bind(this));
    return {
      start: new ColumnAddress(startCol.type, startCol.col, startSheet),
      end: new ColumnAddress(endCol.type, endCol.col, endSheet)
    };
  }
  orderRowRangeEnds(endA, endB) {
    const ends = [endA, endB];
    const [startRow, endRow] = ends.sort(RowAddress.compareByAbsoluteAddress(this.formulaAddress));
    const [startSheet, endSheet] = ends.map((e3) => e3.sheet).sort(ParserWithCaching.compareSheetIds.bind(this));
    return {
      start: new RowAddress(startRow.type, startRow.row, startSheet),
      end: new RowAddress(endRow.type, endRow.row, endSheet)
    };
  }
  static compareSheetIds(sheetA, sheetB) {
    sheetA = sheetA != null ? sheetA : Infinity;
    sheetB = sheetB != null ? sheetB : Infinity;
    return sheetA - sheetB;
  }
  fetchCachedResultForAst(ast) {
    const hash = this.computeHashFromAst(ast);
    return this.fetchCachedResult(hash);
  }
  fetchCachedResult(hash) {
    const cacheResult = this.cache.get(hash);
    if (cacheResult === void 0) {
      throw new Error("There is no AST with such key in the cache");
    } else {
      const {
        ast,
        hasVolatileFunction,
        hasStructuralChangeFunction,
        relativeDependencies
      } = cacheResult;
      return {
        ast,
        errors: [],
        hasVolatileFunction,
        hasStructuralChangeFunction,
        dependencies: relativeDependencies
      };
    }
  }
  computeHashFromTokens(tokens, baseAddress) {
    var _a2;
    let hash = "";
    let idx = 0;
    while (idx < tokens.length) {
      const token = tokens[idx];
      if (tokenMatcher(token, CellReference)) {
        const cellAddress = cellAddressFromString(token.image, baseAddress, this.resolveSheetReference);
        if (cellAddress === void 0) {
          hash = hash.concat(token.image);
        } else {
          hash = hash.concat(cellAddress.hash(true));
        }
      } else if (tokenMatcher(token, ProcedureName)) {
        const procedureName = token.image.toUpperCase().slice(0, -1);
        const canonicalProcedureName = (_a2 = this.lexerConfig.functionMapping[procedureName]) !== null && _a2 !== void 0 ? _a2 : procedureName;
        hash = hash.concat(canonicalProcedureName, "(");
      } else if (tokenMatcher(token, ColumnRange)) {
        const [start, end] = token.image.split(":");
        const startAddress = columnAddressFromString(start, baseAddress, this.resolveSheetReference);
        const endAddress = columnAddressFromString(end, baseAddress, this.resolveSheetReference);
        if (startAddress === void 0 || endAddress === void 0) {
          hash = hash.concat("!REF");
        } else {
          hash = hash.concat(startAddress.hash(true), ":", endAddress.hash(true));
        }
      } else if (tokenMatcher(token, RowRange)) {
        const [start, end] = token.image.split(":");
        const startAddress = rowAddressFromString(start, baseAddress, this.resolveSheetReference);
        const endAddress = rowAddressFromString(end, baseAddress, this.resolveSheetReference);
        if (startAddress === void 0 || endAddress === void 0) {
          hash = hash.concat("!REF");
        } else {
          hash = hash.concat(startAddress.hash(true), ":", endAddress.hash(true));
        }
      } else {
        hash = hash.concat(token.image);
      }
      idx++;
    }
    return hash;
  }
  rememberNewAst(ast) {
    const hash = this.computeHashFromAst(ast);
    return this.cache.maybeSetAndThenGet(hash, ast);
  }
  computeHashFromAst(ast) {
    return "=" + this.computeHashOfAstNode(ast);
  }
  computeHashOfAstNode(ast) {
    switch (ast.type) {
      case AstNodeType.EMPTY: {
        return ast.leadingWhitespace || "";
      }
      case AstNodeType.NUMBER: {
        return imageWithWhitespace(formatNumber(ast.value, this.config.decimalSeparator), ast.leadingWhitespace);
      }
      case AstNodeType.STRING: {
        return imageWithWhitespace('"' + ast.value + '"', ast.leadingWhitespace);
      }
      case AstNodeType.NAMED_EXPRESSION: {
        return imageWithWhitespace(ast.expressionName, ast.leadingWhitespace);
      }
      case AstNodeType.FUNCTION_CALL: {
        const args = ast.args.map((arg) => this.computeHashOfAstNode(arg)).join(this.config.functionArgSeparator);
        const rightPart = ast.procedureName + "(" + args + imageWithWhitespace(")", ast.internalWhitespace);
        return imageWithWhitespace(rightPart, ast.leadingWhitespace);
      }
      case AstNodeType.CELL_REFERENCE: {
        return imageWithWhitespace(ast.reference.hash(true), ast.leadingWhitespace);
      }
      case AstNodeType.COLUMN_RANGE:
      case AstNodeType.ROW_RANGE:
      case AstNodeType.CELL_RANGE: {
        const start = ast.start.hash(ast.sheetReferenceType !== RangeSheetReferenceType.RELATIVE);
        const end = ast.end.hash(ast.sheetReferenceType === RangeSheetReferenceType.BOTH_ABSOLUTE);
        return imageWithWhitespace(start + ":" + end, ast.leadingWhitespace);
      }
      case AstNodeType.MINUS_UNARY_OP: {
        return imageWithWhitespace("-" + this.computeHashOfAstNode(ast.value), ast.leadingWhitespace);
      }
      case AstNodeType.PLUS_UNARY_OP: {
        return imageWithWhitespace("+" + this.computeHashOfAstNode(ast.value), ast.leadingWhitespace);
      }
      case AstNodeType.PERCENT_OP: {
        return this.computeHashOfAstNode(ast.value) + imageWithWhitespace("%", ast.leadingWhitespace);
      }
      case AstNodeType.ERROR: {
        const image = this.config.translationPackage.getErrorTranslation(ast.error ? ast.error.type : ErrorType.ERROR);
        return imageWithWhitespace(image, ast.leadingWhitespace);
      }
      case AstNodeType.ERROR_WITH_RAW_INPUT: {
        return imageWithWhitespace(ast.rawInput, ast.leadingWhitespace);
      }
      case AstNodeType.ARRAY: {
        const args = ast.args.map((row) => row.map((val) => this.computeHashOfAstNode(val)).join(",")).join(";");
        return imageWithWhitespace("{" + args + imageWithWhitespace("}", ast.internalWhitespace), ast.leadingWhitespace);
      }
      case AstNodeType.PARENTHESIS: {
        const expression = this.computeHashOfAstNode(ast.expression);
        const rightPart = "(" + expression + imageWithWhitespace(")", ast.internalWhitespace);
        return imageWithWhitespace(rightPart, ast.leadingWhitespace);
      }
      default: {
        return this.computeHashOfAstNode(ast.left) + imageWithWhitespace(binaryOpTokenMap[ast.type], ast.leadingWhitespace) + this.computeHashOfAstNode(ast.right);
      }
    }
  }
  bindWhitespacesToTokens(tokens) {
    const processedTokens = [];
    const first2 = tokens[0];
    if (!tokenMatcher(first2, this.lexerConfig.WhiteSpace)) {
      processedTokens.push(first2);
    }
    for (let i2 = 1; i2 < tokens.length; ++i2) {
      const current = tokens[i2];
      if (tokenMatcher(current, this.lexerConfig.WhiteSpace)) {
        continue;
      }
      const previous = tokens[i2 - 1];
      if (tokenMatcher(previous, this.lexerConfig.WhiteSpace)) {
        current.leadingWhitespace = previous;
      }
      processedTokens.push(current);
    }
    return processedTokens;
  }
  tokenizeFormula(text) {
    return this.lexer.tokenizeFormula(text);
  }
}
const collectDependenciesFn = (ast, functionRegistry, dependenciesSet, needArgument) => {
  switch (ast.type) {
    case AstNodeType.EMPTY:
    case AstNodeType.NUMBER:
    case AstNodeType.STRING:
    case AstNodeType.ERROR:
      return;
    case AstNodeType.NAMED_EXPRESSION: {
      if (needArgument) {
        dependenciesSet.push(new NamedExpressionDependency(ast.expressionName));
      }
      return;
    }
    case AstNodeType.CELL_REFERENCE: {
      if (needArgument) {
        dependenciesSet.push(new AddressDependency(ast.reference));
      }
      return;
    }
    case AstNodeType.CELL_RANGE: {
      if (needArgument && ast.start.sheet === ast.end.sheet) {
        dependenciesSet.push(new CellRangeDependency(ast.start, ast.end));
      }
      return;
    }
    case AstNodeType.COLUMN_RANGE: {
      if (needArgument && ast.start.sheet === ast.end.sheet) {
        dependenciesSet.push(new ColumnRangeDependency(ast.start, ast.end));
      }
      return;
    }
    case AstNodeType.ROW_RANGE: {
      if (needArgument && ast.start.sheet === ast.end.sheet) {
        dependenciesSet.push(new RowRangeDependency(ast.start, ast.end));
      }
      return;
    }
    case AstNodeType.PERCENT_OP:
    case AstNodeType.PLUS_UNARY_OP:
    case AstNodeType.MINUS_UNARY_OP: {
      collectDependenciesFn(ast.value, functionRegistry, dependenciesSet, true);
      return;
    }
    case AstNodeType.CONCATENATE_OP:
    case AstNodeType.EQUALS_OP:
    case AstNodeType.NOT_EQUAL_OP:
    case AstNodeType.LESS_THAN_OP:
    case AstNodeType.GREATER_THAN_OP:
    case AstNodeType.LESS_THAN_OR_EQUAL_OP:
    case AstNodeType.GREATER_THAN_OR_EQUAL_OP:
    case AstNodeType.MINUS_OP:
    case AstNodeType.PLUS_OP:
    case AstNodeType.TIMES_OP:
    case AstNodeType.DIV_OP:
    case AstNodeType.POWER_OP:
      collectDependenciesFn(ast.left, functionRegistry, dependenciesSet, true);
      collectDependenciesFn(ast.right, functionRegistry, dependenciesSet, true);
      return;
    case AstNodeType.PARENTHESIS:
      collectDependenciesFn(ast.expression, functionRegistry, dependenciesSet, needArgument);
      return;
    case AstNodeType.FUNCTION_CALL: {
      const functionNeedArgument = !functionRegistry.doesFunctionNeedArgumentToBeComputed(ast.procedureName);
      ast.args.forEach((argAst) => collectDependenciesFn(argAst, functionRegistry, dependenciesSet, functionNeedArgument));
      return;
    }
  }
};
const collectDependencies = (ast, functionRegistry) => {
  const result = new Array();
  collectDependenciesFn(ast, functionRegistry, result, true);
  return result;
};
class AddressDependency {
  constructor(dependency) {
    this.dependency = dependency;
  }
  absolutize(baseAddress) {
    return this.dependency.toSimpleCellAddress(baseAddress);
  }
}
class CellRangeDependency {
  constructor(start, end) {
    this.start = start;
    this.end = end;
  }
  absolutize(baseAddress) {
    return new AbsoluteCellRange(this.start.toSimpleCellAddress(baseAddress), this.end.toSimpleCellAddress(baseAddress));
  }
}
class ColumnRangeDependency {
  constructor(start, end) {
    this.start = start;
    this.end = end;
  }
  absolutize(baseAddress) {
    const start = this.start.toSimpleColumnAddress(baseAddress);
    const end = this.end.toSimpleColumnAddress(baseAddress);
    return new AbsoluteColumnRange(start.sheet, start.col, end.col);
  }
}
class RowRangeDependency {
  constructor(start, end) {
    this.start = start;
    this.end = end;
  }
  absolutize(baseAddress) {
    const start = this.start.toSimpleRowAddress(baseAddress);
    const end = this.end.toSimpleRowAddress(baseAddress);
    return new AbsoluteRowRange(start.sheet, start.row, end.row);
  }
}
class NamedExpressionDependency {
  constructor(name) {
    this.name = name;
  }
  absolutize(_baseAddress) {
    return this;
  }
}
const absolutizeDependencies = (deps, baseAddress) => {
  return deps.map((dep) => dep.absolutize(baseAddress));
};
const filterDependenciesOutOfScope = (deps) => {
  return deps.filter((dep) => {
    if (dep instanceof NamedExpressionDependency) {
      return true;
    }
    if (dep instanceof AbsoluteCellRange) {
      return !(isColOrRowInvalid(dep.start) || isColOrRowInvalid(dep.end));
    } else {
      return !isColOrRowInvalid(dep);
    }
  });
};
const EmptyValue = /* @__PURE__ */ Symbol("Empty value");
function getRawValue(num) {
  if (num instanceof RichNumber) {
    return num.val;
  } else {
    return num;
  }
}
class RichNumber {
  constructor(val, format2) {
    this.val = val;
    this.format = format2;
  }
  fromNumber(val) {
    return new this.constructor(val);
  }
}
function cloneNumber(val, newVal) {
  if (typeof val === "number") {
    return newVal;
  } else {
    const ret = val.fromNumber(newVal);
    ret.format = val.format;
    return ret;
  }
}
class DateNumber extends RichNumber {
  getDetailedType() {
    return NumberType.NUMBER_DATE;
  }
}
class CurrencyNumber extends RichNumber {
  getDetailedType() {
    return NumberType.NUMBER_CURRENCY;
  }
}
class TimeNumber extends RichNumber {
  getDetailedType() {
    return NumberType.NUMBER_TIME;
  }
}
class DateTimeNumber extends RichNumber {
  getDetailedType() {
    return NumberType.NUMBER_DATETIME;
  }
}
class PercentNumber extends RichNumber {
  getDetailedType() {
    return NumberType.NUMBER_PERCENT;
  }
}
function isExtendedNumber(val) {
  return typeof val === "number" || val instanceof RichNumber;
}
var NumberType;
(function(NumberType2) {
  NumberType2["NUMBER_RAW"] = "NUMBER_RAW";
  NumberType2["NUMBER_DATE"] = "NUMBER_DATE";
  NumberType2["NUMBER_TIME"] = "NUMBER_TIME";
  NumberType2["NUMBER_DATETIME"] = "NUMBER_DATETIME";
  NumberType2["NUMBER_CURRENCY"] = "NUMBER_CURRENCY";
  NumberType2["NUMBER_PERCENT"] = "NUMBER_PERCENT";
})(NumberType || (NumberType = {}));
function getTypeOfExtendedNumber(num) {
  if (num instanceof RichNumber) {
    return num.getDetailedType();
  } else {
    return NumberType.NUMBER_RAW;
  }
}
function getFormatOfExtendedNumber(num) {
  if (num instanceof RichNumber) {
    return num.format;
  } else {
    return void 0;
  }
}
function getTypeFormatOfExtendedNumber(num) {
  if (num instanceof RichNumber) {
    return {
      type: num.getDetailedType(),
      format: num.format
    };
  } else {
    return {
      type: NumberType.NUMBER_RAW
    };
  }
}
class SimpleRangeValue {
  /**
   * In most cases, it's more convenient to create a `SimpleRangeValue` object
   * by calling one of the [static factory methods](#fromrange).
   */
  constructor(_data, range, dependencyGraph, _hasOnlyNumbers) {
    this._data = _data;
    this.range = range;
    this.dependencyGraph = dependencyGraph;
    this._hasOnlyNumbers = _hasOnlyNumbers;
    this.size = _data === void 0 ? new ArraySize(range.effectiveWidth(dependencyGraph), range.effectiveHeight(dependencyGraph)) : new ArraySize(_data[0].length, _data.length);
  }
  /**
   * Returns the range data as a 2D array.
   */
  get data() {
    this.ensureThatComputed();
    return this._data;
  }
  /**
   * A factory method. Returns a `SimpleRangeValue` object with the provided range address and the provided data.
   */
  static fromRange(data, range, dependencyGraph) {
    return new SimpleRangeValue(data, range, dependencyGraph, true);
  }
  /**
   * A factory method. Returns a `SimpleRangeValue` object with the provided numeric data.
   */
  static onlyNumbers(data) {
    return new SimpleRangeValue(data, void 0, void 0, true);
  }
  /**
   * A factory method. Returns a `SimpleRangeValue` object with the provided data.
   */
  static onlyValues(data) {
    return new SimpleRangeValue(data, void 0, void 0, void 0);
  }
  /**
   * A factory method. Returns a `SimpleRangeValue` object with the provided range address.
   */
  static onlyRange(range, dependencyGraph) {
    return new SimpleRangeValue(void 0, range, dependencyGraph, void 0);
  }
  /**
   * A factory method. Returns a `SimpleRangeValue` object that contains a single value.
   */
  static fromScalar(scalar) {
    return new SimpleRangeValue([[scalar]], void 0, void 0, void 0);
  }
  /**
   * Returns `true` if and only if the `SimpleRangeValue` has no address set.
   */
  isAdHoc() {
    return this.range === void 0;
  }
  /**
   * Returns the number of columns contained in the range.
   */
  width() {
    return this.size.width;
  }
  /**
   * Returns the number of rows contained in the range.
   */
  height() {
    return this.size.height;
  }
  /**
   * Returns the range data as a 1D array.
   */
  valuesFromTopLeftCorner() {
    this.ensureThatComputed();
    const ret = [];
    for (let i2 = 0; i2 < this._data.length; i2++) {
      for (let j2 = 0; j2 < this._data[0].length; j2++) {
        ret.push(this._data[i2][j2]);
      }
    }
    return ret;
  }
  /**
   * Generates the addresses of the cells contained in the range assuming the provided address is the left corner of the range.
   */
  *effectiveAddressesFromData(leftCorner) {
    for (let row = 0; row < this.data.length; ++row) {
      const rowData = this.data[row];
      for (let col = 0; col < rowData.length; ++col) {
        yield simpleCellAddress(leftCorner.sheet, leftCorner.col + col, leftCorner.row + row);
      }
    }
  }
  /**
   * Generates values and addresses of the cells contained in the range assuming the provided address is the left corner of the range.
   *
   * This method combines the functionalities of [`iterateValuesFromTopLeftCorner()`](#iteratevaluesfromtopleftcorner) and [`effectiveAddressesFromData()`](#effectiveaddressesfromdata).
   */
  *entriesFromTopLeftCorner(leftCorner) {
    this.ensureThatComputed();
    for (let row = 0; row < this.size.height; ++row) {
      for (let col = 0; col < this.size.width; ++col) {
        yield [this._data[row][col], simpleCellAddress(leftCorner.sheet, leftCorner.col + col, leftCorner.row + row)];
      }
    }
  }
  /**
   * Generates the values of the cells contained in the range assuming the provided address is the left corner of the range.
   */
  *iterateValuesFromTopLeftCorner() {
    yield* this.valuesFromTopLeftCorner();
  }
  /**
   * Returns the number of cells contained in the range.
   */
  numberOfElements() {
    return this.size.width * this.size.height;
  }
  /**
   * Returns `true` if and only if the range contains only numeric values.
   */
  hasOnlyNumbers() {
    if (this._hasOnlyNumbers === void 0) {
      this._hasOnlyNumbers = true;
      for (const row of this.data) {
        for (const v2 of row) {
          if (typeof v2 !== "number") {
            this._hasOnlyNumbers = false;
            return false;
          }
        }
      }
    }
    return this._hasOnlyNumbers;
  }
  /**
   * Returns the range data as a 2D array of numbers.
   *
   * Internal use only.
   */
  rawNumbers() {
    return this._data;
  }
  /**
   * Returns the range data as a 2D array.
   *
   * Internal use only.
   */
  rawData() {
    var _a2;
    this.ensureThatComputed();
    return (_a2 = this._data) !== null && _a2 !== void 0 ? _a2 : [];
  }
  /**
   * Returns `true` if and only if the range has the same width and height as the `other` range object.
   */
  sameDimensionsAs(other) {
    return this.width() === other.width() && this.height() === other.height();
  }
  /**
   * Computes the range data if it is not computed yet.
   */
  ensureThatComputed() {
    if (this._data !== void 0) {
      return;
    }
    this._hasOnlyNumbers = true;
    this._data = this.range.addressesArrayMap(this.dependencyGraph, (cellFromRange) => {
      const value = this.dependencyGraph.getCellValue(cellFromRange);
      if (value instanceof SimpleRangeValue) {
        this._hasOnlyNumbers = false;
        return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
      } else if (isExtendedNumber(value)) {
        return value;
      } else {
        this._hasOnlyNumbers = false;
        return value;
      }
    });
  }
}
class ContentChanges {
  constructor() {
    this.changes = /* @__PURE__ */ new Map();
  }
  static empty() {
    return new ContentChanges();
  }
  addAll(other) {
    for (const change of other.changes.values()) {
      this.add(change.address, change);
    }
    return this;
  }
  addChange(newValue, address, oldValue) {
    this.addInterpreterValue(newValue, address, oldValue);
  }
  exportChanges(exporter) {
    let ret = [];
    this.changes.forEach((e3) => {
      const change = exporter.exportChange(e3);
      if (Array.isArray(change)) {
        ret = ret.concat(change);
      } else {
        ret.push(change);
      }
    });
    return ret;
  }
  getChanges() {
    return Array.from(this.changes.values());
  }
  isEmpty() {
    return this.changes.size === 0;
  }
  add(address, change) {
    const value = change.value;
    if (value instanceof SimpleRangeValue) {
      for (const cellAddress of value.effectiveAddressesFromData(address)) {
        this.changes.delete(addressKey(cellAddress));
      }
    }
    this.changes.set(addressKey(address), change);
  }
  addInterpreterValue(value, address, oldValue) {
    this.add(address, {
      address,
      value,
      oldValue
    });
  }
}
class InternalNamedExpression {
  constructor(displayName, address, added, options) {
    this.displayName = displayName;
    this.address = address;
    this.added = added;
    this.options = options;
  }
  normalizeExpressionName() {
    return this.displayName.toLowerCase();
  }
  copy() {
    return new InternalNamedExpression(this.displayName, this.address, this.added, this.options);
  }
}
class WorkbookStore {
  constructor() {
    this.mapping = /* @__PURE__ */ new Map();
  }
  has(expressionName) {
    return this.mapping.has(this.normalizeExpressionName(expressionName));
  }
  isNameAvailable(expressionName) {
    const normalizedExpressionName = this.normalizeExpressionName(expressionName);
    const namedExpression = this.mapping.get(normalizedExpressionName);
    return !(namedExpression && namedExpression.added);
  }
  add(namedExpression) {
    this.mapping.set(namedExpression.normalizeExpressionName(), namedExpression);
  }
  get(expressionName) {
    return this.mapping.get(this.normalizeExpressionName(expressionName));
  }
  getExisting(expressionName) {
    const namedExpression = this.mapping.get(this.normalizeExpressionName(expressionName));
    if (namedExpression && namedExpression.added) {
      return namedExpression;
    } else {
      return void 0;
    }
  }
  remove(expressionName) {
    const normalizedExpressionName = this.normalizeExpressionName(expressionName);
    const namedExpression = this.mapping.get(normalizedExpressionName);
    if (namedExpression) {
      namedExpression.added = false;
    }
  }
  getAllNamedExpressions() {
    return Array.from(this.mapping.values()).filter((ne2) => ne2.added);
  }
  normalizeExpressionName(expressionName) {
    return expressionName.toLowerCase();
  }
}
class WorksheetStore {
  constructor() {
    this.mapping = /* @__PURE__ */ new Map();
  }
  add(namedExpression) {
    this.mapping.set(this.normalizeExpressionName(namedExpression.displayName), namedExpression);
  }
  get(expressionName) {
    return this.mapping.get(this.normalizeExpressionName(expressionName));
  }
  has(expressionName) {
    return this.mapping.has(this.normalizeExpressionName(expressionName));
  }
  getAllNamedExpressions() {
    return Array.from(this.mapping.values()).filter((ne2) => ne2.added);
  }
  isNameAvailable(expressionName) {
    const normalizedExpressionName = this.normalizeExpressionName(expressionName);
    return !this.mapping.has(normalizedExpressionName);
  }
  remove(expressionName) {
    const normalizedExpressionName = this.normalizeExpressionName(expressionName);
    const namedExpression = this.mapping.get(normalizedExpressionName);
    if (namedExpression) {
      this.mapping.delete(normalizedExpressionName);
    }
  }
  normalizeExpressionName(expressionName) {
    return expressionName.toLowerCase();
  }
}
class NamedExpressions {
  constructor() {
    this.nextNamedExpressionRow = 0;
    this.workbookStore = new WorkbookStore();
    this.worksheetStores = /* @__PURE__ */ new Map();
    this.addressCache = /* @__PURE__ */ new Map();
  }
  isNameAvailable(expressionName, sheetId) {
    var _a2, _b;
    if (sheetId === void 0) {
      return this.workbookStore.isNameAvailable(expressionName);
    } else {
      return (_b = (_a2 = this.worksheetStore(sheetId)) === null || _a2 === void 0 ? void 0 : _a2.isNameAvailable(expressionName)) !== null && _b !== void 0 ? _b : true;
    }
  }
  namedExpressionInAddress(row) {
    const namedExpression = this.addressCache.get(row);
    if (namedExpression && namedExpression.added) {
      return namedExpression;
    } else {
      return void 0;
    }
  }
  namedExpressionForScope(expressionName, sheetId) {
    var _a2;
    if (sheetId === void 0) {
      return this.workbookStore.getExisting(expressionName);
    } else {
      return (_a2 = this.worksheetStore(sheetId)) === null || _a2 === void 0 ? void 0 : _a2.get(expressionName);
    }
  }
  nearestNamedExpression(expressionName, sheetId) {
    var _a2, _b;
    return (_b = (_a2 = this.worksheetStore(sheetId)) === null || _a2 === void 0 ? void 0 : _a2.get(expressionName)) !== null && _b !== void 0 ? _b : this.workbookStore.getExisting(expressionName);
  }
  isExpressionInScope(expressionName, sheetId) {
    var _a2, _b;
    return (_b = (_a2 = this.worksheetStore(sheetId)) === null || _a2 === void 0 ? void 0 : _a2.has(expressionName)) !== null && _b !== void 0 ? _b : false;
  }
  /**
   * Checks the validity of a named-expression's name.
   *
   * The name:
   * - Must start with a Unicode letter or with an underscore (`_`).
   * - Can contain only Unicode letters, numbers, underscores, and periods (`.`).
   * - Can't be the same as any possible reference in the A1 notation (`[A-Za-z]+[0-9]+`).
   * - Can't be the same as any possible reference in the R1C1 notation (`[rR][0-9]*[cC][0-9]*`).
   *
   * The naming rules follow the [OpenDocument](https://docs.oasis-open.org/office/OpenDocument/v1.3/os/part4-formula/OpenDocument-v1.3-os-part4-formula.html#__RefHeading__1017964_715980110) standard.
   */
  isNameValid(expressionName) {
    const a1CellRefRegexp = new RegExp(`^${CELL_REFERENCE_PATTERN}$`);
    const r1c1CellRefRegexp = new RegExp(`^${R1C1_CELL_REFERENCE_PATTERN}$`);
    const namedExpRegexp = new RegExp(`^${NAMED_EXPRESSION_PATTERN}$`);
    if (a1CellRefRegexp.test(expressionName) || r1c1CellRefRegexp.test(expressionName)) {
      return false;
    }
    return namedExpRegexp.test(expressionName);
  }
  addNamedExpression(expressionName, sheetId, options) {
    const store = sheetId === void 0 ? this.workbookStore : this.worksheetStoreOrCreate(sheetId);
    let namedExpression = store.get(expressionName);
    if (namedExpression !== void 0) {
      namedExpression.added = true;
      namedExpression.displayName = expressionName;
      namedExpression.options = options;
    } else {
      namedExpression = new InternalNamedExpression(expressionName, this.nextAddress(), true, options);
      store.add(namedExpression);
    }
    this.addressCache.set(namedExpression.address.row, namedExpression);
    return namedExpression;
  }
  restoreNamedExpression(namedExpression, sheetId) {
    const store = sheetId === void 0 ? this.workbookStore : this.worksheetStoreOrCreate(sheetId);
    namedExpression.added = true;
    store.add(namedExpression);
    this.addressCache.set(namedExpression.address.row, namedExpression);
    return namedExpression;
  }
  namedExpressionOrPlaceholder(expressionName, sheetId) {
    var _a2;
    return (_a2 = this.worksheetStoreOrCreate(sheetId).get(expressionName)) !== null && _a2 !== void 0 ? _a2 : this.workbookNamedExpressionOrPlaceholder(expressionName);
  }
  workbookNamedExpressionOrPlaceholder(expressionName) {
    let namedExpression = this.workbookStore.get(expressionName);
    if (namedExpression === void 0) {
      namedExpression = new InternalNamedExpression(expressionName, this.nextAddress(), false);
      this.workbookStore.add(namedExpression);
    }
    return namedExpression;
  }
  remove(expressionName, sheetId) {
    let store;
    if (sheetId === void 0) {
      store = this.workbookStore;
    } else {
      store = this.worksheetStore(sheetId);
    }
    const namedExpression = store === null || store === void 0 ? void 0 : store.get(expressionName);
    if (store === void 0 || namedExpression === void 0 || !namedExpression.added) {
      throw Error("Named expression does not exist");
    }
    store.remove(expressionName);
    if (store instanceof WorksheetStore && store.mapping.size === 0) {
      this.worksheetStores.delete(sheetId);
    }
    this.addressCache.delete(namedExpression.address.row);
  }
  getAllNamedExpressionsNamesInScope(sheetId) {
    return this.getAllNamedExpressions().filter(({
      scope
    }) => scope === sheetId).map((ne2) => ne2.expression.displayName);
  }
  getAllNamedExpressionsNames() {
    return this.getAllNamedExpressions().map((ne2) => ne2.expression.displayName);
  }
  getAllNamedExpressions() {
    const storedNamedExpressions = [];
    this.workbookStore.getAllNamedExpressions().forEach((expr) => {
      storedNamedExpressions.push({
        expression: expr,
        scope: void 0
      });
    });
    this.worksheetStores.forEach((store, sheetNum) => {
      store.getAllNamedExpressions().forEach((expr) => {
        storedNamedExpressions.push({
          expression: expr,
          scope: sheetNum
        });
      });
    });
    return storedNamedExpressions;
  }
  getAllNamedExpressionsForScope(scope) {
    var _a2, _b;
    if (scope === void 0) {
      return this.workbookStore.getAllNamedExpressions();
    } else {
      return (_b = (_a2 = this.worksheetStores.get(scope)) === null || _a2 === void 0 ? void 0 : _a2.getAllNamedExpressions()) !== null && _b !== void 0 ? _b : [];
    }
  }
  worksheetStoreOrCreate(sheetId) {
    let store = this.worksheetStores.get(sheetId);
    if (!store) {
      store = new WorksheetStore();
      this.worksheetStores.set(sheetId, store);
    }
    return store;
  }
  worksheetStore(sheetId) {
    return this.worksheetStores.get(sheetId);
  }
  nextAddress() {
    return simpleCellAddress(NamedExpressions.SHEET_FOR_WORKBOOK_EXPRESSIONS, 0, this.nextNamedExpressionRow++);
  }
}
NamedExpressions.SHEET_FOR_WORKBOOK_EXPRESSIONS = -1;
const doesContainRelativeReferences = (ast) => {
  switch (ast.type) {
    case AstNodeType.EMPTY:
    case AstNodeType.NUMBER:
    case AstNodeType.STRING:
    case AstNodeType.ERROR:
    case AstNodeType.ERROR_WITH_RAW_INPUT:
      return false;
    case AstNodeType.CELL_REFERENCE:
      return !ast.reference.isAbsolute();
    case AstNodeType.CELL_RANGE:
    case AstNodeType.COLUMN_RANGE:
    case AstNodeType.ROW_RANGE:
      return !ast.start.isAbsolute();
    case AstNodeType.NAMED_EXPRESSION:
      return false;
    case AstNodeType.PERCENT_OP:
    case AstNodeType.PLUS_UNARY_OP:
    case AstNodeType.MINUS_UNARY_OP: {
      return doesContainRelativeReferences(ast.value);
    }
    case AstNodeType.CONCATENATE_OP:
    case AstNodeType.EQUALS_OP:
    case AstNodeType.NOT_EQUAL_OP:
    case AstNodeType.LESS_THAN_OP:
    case AstNodeType.GREATER_THAN_OP:
    case AstNodeType.LESS_THAN_OR_EQUAL_OP:
    case AstNodeType.GREATER_THAN_OR_EQUAL_OP:
    case AstNodeType.MINUS_OP:
    case AstNodeType.PLUS_OP:
    case AstNodeType.TIMES_OP:
    case AstNodeType.DIV_OP:
    case AstNodeType.POWER_OP:
      return doesContainRelativeReferences(ast.left) || doesContainRelativeReferences(ast.right);
    case AstNodeType.PARENTHESIS:
      return doesContainRelativeReferences(ast.expression);
    case AstNodeType.FUNCTION_CALL: {
      return ast.args.some((arg) => doesContainRelativeReferences(arg));
    }
    case AstNodeType.ARRAY: {
      return ast.args.some((row) => row.some((arg) => doesContainRelativeReferences(arg)));
    }
  }
};
class RowsSpan {
  constructor(sheet, rowStart, rowEnd) {
    this.sheet = sheet;
    this.rowStart = rowStart;
    this.rowEnd = rowEnd;
    if (rowStart < 0) {
      throw Error("Starting row cant be less than 0");
    }
    if (rowEnd < rowStart) {
      throw Error("Row span cant end before start");
    }
  }
  get numberOfRows() {
    return this.rowEnd - this.rowStart + 1;
  }
  get start() {
    return this.rowStart;
  }
  get end() {
    return this.rowEnd;
  }
  static fromNumberOfRows(sheet, rowStart, numberOfRows) {
    return new RowsSpan(sheet, rowStart, rowStart + numberOfRows - 1);
  }
  static fromRowStartAndEnd(sheet, rowStart, rowEnd) {
    return new RowsSpan(sheet, rowStart, rowEnd);
  }
  *rows() {
    for (let col = this.rowStart; col <= this.rowEnd; ++col) {
      yield col;
    }
  }
  intersect(otherSpan) {
    if (this.sheet !== otherSpan.sheet) {
      throw Error("Can't intersect spans from different sheets");
    }
    const start = Math.max(this.rowStart, otherSpan.rowStart);
    const end = Math.min(this.rowEnd, otherSpan.rowEnd);
    if (start > end) {
      return null;
    }
    return new RowsSpan(this.sheet, start, end);
  }
  firstRow() {
    return new RowsSpan(this.sheet, this.rowStart, this.rowStart);
  }
}
class ColumnsSpan {
  constructor(sheet, columnStart, columnEnd) {
    this.sheet = sheet;
    this.columnStart = columnStart;
    this.columnEnd = columnEnd;
    if (columnStart < 0) {
      throw Error("Starting column cant be less than 0");
    }
    if (columnEnd < columnStart) {
      throw Error("Column span cant end before start");
    }
  }
  get numberOfColumns() {
    return this.columnEnd - this.columnStart + 1;
  }
  get start() {
    return this.columnStart;
  }
  get end() {
    return this.columnEnd;
  }
  static fromNumberOfColumns(sheet, columnStart, numberOfColumns) {
    return new ColumnsSpan(sheet, columnStart, columnStart + numberOfColumns - 1);
  }
  static fromColumnStartAndEnd(sheet, columnStart, columnEnd) {
    return new ColumnsSpan(sheet, columnStart, columnEnd);
  }
  *columns() {
    for (let col = this.columnStart; col <= this.columnEnd; ++col) {
      yield col;
    }
  }
  intersect(otherSpan) {
    if (this.sheet !== otherSpan.sheet) {
      throw Error("Can't intersect spans from different sheets");
    }
    const start = Math.max(this.columnStart, otherSpan.columnStart);
    const end = Math.min(this.columnEnd, otherSpan.columnEnd);
    if (start > end) {
      return null;
    }
    return new ColumnsSpan(this.sheet, start, end);
  }
  firstColumn() {
    return new ColumnsSpan(this.sheet, this.columnStart, this.columnStart);
  }
}
var StatType;
(function(StatType2) {
  StatType2["BUILD_ENGINE_TOTAL"] = "BUILD_ENGINE_TOTAL";
  StatType2["PARSER"] = "PARSER";
  StatType2["GRAPH_BUILD"] = "GRAPH_BUILD";
  StatType2["COLLECT_DEPENDENCIES"] = "COLLECT_DEPENDENCIES";
  StatType2["PROCESS_DEPENDENCIES"] = "PROCESS_DEPENDENCIES";
  StatType2["TOP_SORT"] = "TOP_SORT";
  StatType2["BUILD_COLUMN_INDEX"] = "BUILD_COLUMN_INDEX";
  StatType2["EVALUATION"] = "EVALUATION";
  StatType2["VLOOKUP"] = "VLOOKUP";
  StatType2["TRANSFORM_ASTS"] = "TRANSFORM_ASTS";
  StatType2["TRANSFORM_ASTS_POSTPONED"] = "TRANSFORM_ASTS_POSTPONED";
  StatType2["ADJUSTING_ADDRESS_MAPPING"] = "ADJUSTING_ADDRESS_MAPPING";
  StatType2["ADJUSTING_ARRAY_MAPPING"] = "ADJUSTING_ARRAY_MAPPING";
  StatType2["ADJUSTING_RANGES"] = "ADJUSTING_RANGES";
  StatType2["ADJUSTING_GRAPH"] = "ADJUSTING_GRAPH";
  StatType2["CRITERION_FUNCTION_FULL_CACHE_USED"] = "CRITERION_FUNCTION_FULL_CACHE_USED";
  StatType2["CRITERION_FUNCTION_PARTIAL_CACHE_USED"] = "CRITERION_FUNCTION_PARTIAL_CACHE_USED";
})(StatType || (StatType = {}));
class Statistics {
  constructor() {
    this.stats = /* @__PURE__ */ new Map([[StatType.CRITERION_FUNCTION_FULL_CACHE_USED, 0], [StatType.CRITERION_FUNCTION_PARTIAL_CACHE_USED, 0]]);
    this.startTimes = /* @__PURE__ */ new Map();
  }
  incrementCriterionFunctionFullCacheUsed() {
    var _a2;
    const newValue = ((_a2 = this.stats.get(StatType.CRITERION_FUNCTION_FULL_CACHE_USED)) !== null && _a2 !== void 0 ? _a2 : 0) + 1;
    this.stats.set(StatType.CRITERION_FUNCTION_FULL_CACHE_USED, newValue);
  }
  incrementCriterionFunctionPartialCacheUsed() {
    var _a2;
    const newValue = ((_a2 = this.stats.get(StatType.CRITERION_FUNCTION_PARTIAL_CACHE_USED)) !== null && _a2 !== void 0 ? _a2 : 0) + 1;
    this.stats.set(StatType.CRITERION_FUNCTION_PARTIAL_CACHE_USED, newValue);
  }
  /**
   * Resets statistics
   */
  reset() {
    this.stats.clear();
    this.startTimes.clear();
    this.stats.set(StatType.CRITERION_FUNCTION_FULL_CACHE_USED, 0);
    this.stats.set(StatType.CRITERION_FUNCTION_PARTIAL_CACHE_USED, 0);
  }
  /**
   * Starts tracking particular statistic.
   *
   * @param name - statistic to start tracking
   */
  start(name) {
    if (this.startTimes.get(name)) {
      throw Error(`Statistics ${name} already started`);
    } else {
      this.startTimes.set(name, Date.now());
    }
  }
  /**
   * Stops tracking particular statistic.
   * Raise error if tracking statistic wasn't started.
   *
   * @param name - statistic to stop tracking
   */
  end(name) {
    var _a2;
    const now = Date.now();
    const startTime = this.startTimes.get(name);
    if (startTime) {
      let values2 = (_a2 = this.stats.get(name)) !== null && _a2 !== void 0 ? _a2 : 0;
      values2 += now - startTime;
      this.stats.set(name, values2);
      this.startTimes.delete(name);
    } else {
      throw Error(`Statistics ${name} not started`);
    }
  }
  /**
   * Measure given statistic as execution of given function.
   *
   * @param name - statistic to track
   * @param func - function to call
   * @returns result of the function call
   */
  measure(name, func) {
    this.start(name);
    const result = func();
    this.end(name);
    return result;
  }
  /**
   * Returns the snapshot of current results
   */
  snapshot() {
    return new Map(this.stats);
  }
}
class EmptyStatistics extends Statistics {
  /** @inheritDoc */
  incrementCriterionFunctionFullCacheUsed() {
  }
  /** @inheritDoc */
  incrementCriterionFunctionPartialCacheUsed() {
  }
  /** @inheritDoc */
  start(_name) {
  }
  /** @inheritDoc */
  end(_name) {
  }
}
class NoSheetWithIdError extends Error {
  constructor(sheetId) {
    super(`There's no sheet with id = ${sheetId}`);
  }
}
class NoSheetWithNameError extends Error {
  constructor(sheetName) {
    super(`There's no sheet with name '${sheetName}'`);
  }
}
class SheetNameAlreadyTakenError extends Error {
  constructor(sheetName) {
    super(`Sheet with name ${sheetName} already exists`);
  }
}
class SheetSizeLimitExceededError extends Error {
  constructor() {
    super("Sheet size limit exceeded");
  }
}
class NotAFormulaError extends Error {
  constructor() {
    super("This is not a formula");
  }
}
class InvalidAddressError extends Error {
  constructor(address) {
    super(`Address (row = ${address.row}, col = ${address.col}) is invalid`);
  }
}
class InvalidArgumentsError extends Error {
  constructor(expectedArguments) {
    super(`Invalid arguments, expected ${expectedArguments}`);
  }
}
class SheetsNotEqual extends Error {
  constructor(sheet1, sheet2) {
    super(`Sheets ${sheet1} and ${sheet2} are not equal.`);
  }
}
class NamedExpressionNameIsAlreadyTakenError extends Error {
  constructor(expressionName) {
    super(`Name of Named Expression '${expressionName}' is already present`);
  }
}
class NamedExpressionNameIsInvalidError extends Error {
  constructor(expressionName) {
    super(`Name of Named Expression '${expressionName}' is invalid`);
  }
}
class NamedExpressionDoesNotExistError extends Error {
  constructor(expressionName) {
    super(`Named Expression '${expressionName}' does not exist`);
  }
}
class NoOperationToUndoError extends Error {
  constructor() {
    super("There is no operation to undo");
  }
}
class NoOperationToRedoError extends Error {
  constructor() {
    super("There is no operation to redo");
  }
}
class NothingToPasteError extends Error {
  constructor() {
    super("There is nothing to paste");
  }
}
function replacer(key, val) {
  switch (typeof val) {
    case "function":
    case "symbol":
      return val.toString();
    case "bigint":
      return "BigInt(" + val.toString() + ")";
    default: {
      if (val instanceof RegExp) {
        return "RegExp(" + val.toString() + ")";
      } else {
        return val;
      }
    }
  }
}
class UnableToParseError extends Error {
  constructor(value) {
    super(`Unable to parse value: ${JSON.stringify(value, replacer, 4)}`);
  }
}
class ExpectedValueOfTypeError extends Error {
  constructor(expectedType, paramName) {
    super(`Expected value of type: ${expectedType} for config parameter: ${paramName}`);
  }
}
class ConfigValueEmpty extends Error {
  constructor(paramName) {
    super(`Config parameter ${paramName} cannot be empty.`);
  }
}
class ConfigValueTooSmallError extends Error {
  constructor(paramName, minimum) {
    super(`Config parameter ${paramName} should be at least ${minimum}`);
  }
}
class ConfigValueTooBigError extends Error {
  constructor(paramName, maximum2) {
    super(`Config parameter ${paramName} should be at most ${maximum2}`);
  }
}
class ExpectedOneOfValuesError extends Error {
  constructor(values2, paramName) {
    super(`Expected one of ${values2} for config parameter: ${paramName}`);
  }
}
class EvaluationSuspendedError extends Error {
  constructor() {
    super("Computations are suspended");
  }
}
class MissingTranslationError extends Error {
  constructor(key) {
    super(`Translation for ${key} is missing in the translation package you're using.`);
  }
}
class ProtectedFunctionTranslationError extends Error {
  constructor(key) {
    super(`Cannot register translation for function with id: ${key}`);
  }
}
class LanguageNotRegisteredError extends Error {
  constructor() {
    super("Language not registered.");
  }
}
class LanguageAlreadyRegisteredError extends Error {
  constructor() {
    super("Language already registered.");
  }
}
class FunctionPluginValidationError extends Error {
  static functionNotDeclaredInPlugin(functionId, pluginName) {
    return new FunctionPluginValidationError(`Function with id ${functionId} not declared in plugin ${pluginName}`);
  }
  static functionMethodNotFound(functionName2, pluginName) {
    return new FunctionPluginValidationError(`Function method ${functionName2} not found in plugin ${pluginName}`);
  }
}
class ProtectedFunctionError extends Error {
  static cannotRegisterFunctionWithId(functionId) {
    return new ProtectedFunctionError(`Cannot register function with id ${functionId}`);
  }
  static cannotUnregisterFunctionWithId(functionId) {
    return new ProtectedFunctionError(`Cannot unregister function with id ${functionId}`);
  }
  static cannotUnregisterProtectedPlugin() {
    return new ProtectedFunctionError("Cannot unregister protected plugin");
  }
}
class SourceLocationHasArrayError extends Error {
  constructor() {
    super("Cannot perform this operation, source location has an array inside.");
  }
}
class TargetLocationHasArrayError extends Error {
  constructor() {
    super("Cannot perform this operation, target location has an array inside.");
  }
}
class NoRelativeAddressesAllowedError extends Error {
  constructor() {
    super("Relative addresses not allowed in named expressions.");
  }
}
class AliasAlreadyExisting extends Error {
  constructor(name, pluginName) {
    super(`Alias id ${name} in plugin ${pluginName} already defined as a function or alias.`);
  }
}
class AddressMapping {
  constructor(policy) {
    this.policy = policy;
    this.mapping = /* @__PURE__ */ new Map();
  }
  /**
   * Gets the cell vertex at the specified address.
   */
  getCell(address, options = {}) {
    const sheetMapping = this.mapping.get(address.sheet);
    if (!sheetMapping) {
      if (options.throwIfSheetNotExists) {
        throw new NoSheetWithIdError(address.sheet);
      }
      return void 0;
    }
    const cell = sheetMapping.getCell(address);
    if (!cell && options.throwIfCellNotExists) {
      throw Error("Vertex for address missing in AddressMapping");
    }
    return cell;
  }
  /**
   * Gets the cell vertex at the specified address or throws if it doesn't exist.
   * @throws {NoSheetWithIdError} if sheet doesn't exist
   * @throws {Error} if cell doesn't exist
   */
  getCellOrThrow(address) {
    const sheetMapping = this.mapping.get(address.sheet);
    if (!sheetMapping) {
      throw new NoSheetWithIdError(address.sheet);
    }
    const cell = sheetMapping.getCell(address);
    if (!cell) {
      throw Error("Vertex for address missing in AddressMapping");
    }
    return cell;
  }
  /**
   * Gets the address mapping strategy for the specified sheet.
   * @throws {NoSheetWithIdError} if sheet doesn't exist
   */
  getStrategyForSheetOrThrow(sheetId) {
    const strategy = this.mapping.get(sheetId);
    if (strategy === void 0) {
      throw new NoSheetWithIdError(sheetId);
    }
    return strategy;
  }
  /**
   * Adds a new sheet with the specified strategy.
   * @throws {Error} if sheet is already added and throwIfSheetAlreadyExists is true
   */
  addSheetWithStrategy(sheetId, strategy, options = {
    throwIfSheetAlreadyExists: true
  }) {
    const strategyFound = this.mapping.get(sheetId);
    if (strategyFound) {
      if (options.throwIfSheetAlreadyExists) {
        throw Error("Sheet already added");
      }
      return strategyFound;
    }
    this.mapping.set(sheetId, strategy);
    return strategy;
  }
  /**
   * Adds a sheet or changes the strategy for an existing sheet.
   * Designed for the purpose of exchanging the placeholder strategy for a real strategy.
   */
  addSheetOrChangeStrategy(sheetId, sheetBoundaries) {
    const newStrategy = this.createStrategyBasedOnBoundaries(sheetBoundaries);
    const strategyPlaceholder = this.mapping.get(sheetId);
    if (!strategyPlaceholder) {
      this.mapping.set(sheetId, newStrategy);
      return newStrategy;
    }
    if (newStrategy instanceof DenseStrategy) {
      return strategyPlaceholder;
    }
    this.moveStrategyContent(strategyPlaceholder, newStrategy, sheetId);
    this.mapping.set(sheetId, newStrategy);
    return newStrategy;
  }
  /**
   * Moves the content of the source strategy to the target strategy.
   */
  moveStrategyContent(sourceStrategy, targetStrategy, sheetContext) {
    const sourceVertices = sourceStrategy.getEntries(sheetContext);
    for (const [address, vertex] of sourceVertices) {
      targetStrategy.setCell(address, vertex);
    }
  }
  /**
   * Adds a sheet and sets the strategy based on the sheet boundaries.
   * @throws {Error} if sheet already exists and throwIfSheetAlreadyExists is true
   */
  addSheetAndSetStrategyBasedOnBoundaries(sheetId, sheetBoundaries, options = {
    throwIfSheetAlreadyExists: true
  }) {
    this.addSheetWithStrategy(sheetId, this.createStrategyBasedOnBoundaries(sheetBoundaries), options);
  }
  /**
   * Creates a strategy based on the sheet boundaries.
   */
  createStrategyBasedOnBoundaries(sheetBoundaries) {
    const {
      height,
      width,
      fill
    } = sheetBoundaries;
    const strategyConstructor = this.policy.call(fill);
    return new strategyConstructor(width, height);
  }
  /**
   * Adds a placeholder strategy (DenseStrategy) for a sheet. If the sheet already exists, does nothing.
   */
  addSheetStrategyPlaceholderIfNotExists(sheetId) {
    if (this.mapping.has(sheetId)) {
      return;
    }
    this.mapping.set(sheetId, new DenseStrategy(0, 0));
  }
  /**
   * Removes a sheet from the address mapping.
   * If sheet does not exist, does nothing.
   * @returns {boolean} true if sheet was removed, false if it did not exist.
   */
  removeSheetIfExists(sheetId) {
    return this.mapping.delete(sheetId);
  }
  /**
   * Gets the interpreter value of a cell at the specified address.
   * @returns {InterpreterValue} The interpreter value (returns EmptyValue if cell doesn't exist)
   */
  getCellValue(address) {
    const vertex = this.getCell(address);
    if (vertex === void 0) {
      return EmptyValue;
    } else if (vertex instanceof ArrayFormulaVertex) {
      return vertex.getArrayCellValue(address);
    } else {
      return vertex.getCellValue();
    }
  }
  /**
   * Gets the raw cell content at the specified address.
   * @returns {RawCellContent} The raw cell content or null if cell doesn't exist or is not a value cell
   */
  getRawValue(address) {
    const vertex = this.getCell(address);
    if (vertex instanceof ValueCellVertex) {
      return vertex.getValues().rawValue;
    } else if (vertex instanceof ArrayFormulaVertex) {
      return vertex.getArrayCellRawValue(address);
    } else {
      return null;
    }
  }
  /**
   * Sets a cell vertex at the specified address.
   * @throws {Error} if sheet not initialized
   */
  setCell(address, newVertex) {
    const sheetMapping = this.mapping.get(address.sheet);
    if (!sheetMapping) {
      throw Error("Sheet not initialized");
    }
    sheetMapping.setCell(address, newVertex);
  }
  /**
   * Moves a cell from source address to destination address.
   * Supports cross-sheet moves (used for placeholder sheet merging).
   * @throws {Error} if source sheet not initialized
   * @throws {Error} if destination occupied
   * @throws {Error} if source cell doesn't exist
   */
  moveCell(source, destination) {
    const sheetMapping = this.mapping.get(source.sheet);
    if (!sheetMapping) {
      throw Error("Sheet not initialized.");
    }
    if (this.has(destination)) {
      throw new Error("Cannot move cell. Destination already occupied.");
    }
    const vertex = sheetMapping.getCell(source);
    if (vertex === void 0) {
      throw new Error("Cannot move cell. No cell with such address.");
    }
    this.setCell(destination, vertex);
    this.removeCell(source);
  }
  /**
   * Removes a cell at the specified address.
   * @throws Error if sheet not initialized
   */
  removeCell(address) {
    const sheetMapping = this.mapping.get(address.sheet);
    if (!sheetMapping) {
      throw Error("Sheet not initialized");
    }
    sheetMapping.removeCell(address);
  }
  /**
   * Checks if a cell exists at the specified address.
   */
  has(address) {
    const sheetMapping = this.mapping.get(address.sheet);
    if (sheetMapping === void 0) {
      return false;
    }
    return sheetMapping.has(address);
  }
  /**
   * Gets the height of the specified sheet.
   */
  getSheetHeight(sheetId) {
    const sheetMapping = this.getStrategyForSheetOrThrow(sheetId);
    return sheetMapping.getHeight();
  }
  /**
   * Gets the width of the specified sheet.
   */
  getSheetWidth(sheetId) {
    const sheetMapping = this.getStrategyForSheetOrThrow(sheetId);
    return sheetMapping.getWidth();
  }
  /**
   * Adds rows to a sheet.
   */
  addRows(sheetId, row, numberOfRows) {
    const sheetMapping = this.getStrategyForSheetOrThrow(sheetId);
    sheetMapping.addRows(row, numberOfRows);
  }
  /**
   * Removes rows from a sheet.
   */
  removeRows(removedRows) {
    const sheetMapping = this.getStrategyForSheetOrThrow(removedRows.sheet);
    sheetMapping.removeRows(removedRows);
  }
  /**
   * Adds columns to a sheet starting at the specified column index.
   */
  addColumns(sheetId, column, numberOfColumns) {
    const sheetMapping = this.getStrategyForSheetOrThrow(sheetId);
    sheetMapping.addColumns(column, numberOfColumns);
  }
  /**
   * Removes columns from a sheet.
   */
  removeColumns(removedColumns) {
    const sheetMapping = this.getStrategyForSheetOrThrow(removedColumns.sheet);
    sheetMapping.removeColumns(removedColumns);
  }
  /**
   * Returns an iterator of cell vertices within the specified rows span.
   */
  *verticesFromRowsSpan(rowsSpan) {
    yield* this.mapping.get(rowsSpan.sheet).verticesFromRowsSpan(rowsSpan);
  }
  /**
   * Returns an iterator of cell vertices within the specified columns span.
   */
  *verticesFromColumnsSpan(columnsSpan) {
    yield* this.mapping.get(columnsSpan.sheet).verticesFromColumnsSpan(columnsSpan);
  }
  /**
   * Returns an iterator of address-vertex pairs within the specified rows span.
   */
  *entriesFromRowsSpan(rowsSpan) {
    const sheetMapping = this.getStrategyForSheetOrThrow(rowsSpan.sheet);
    yield* sheetMapping.entriesFromRowsSpan(rowsSpan);
  }
  /**
   * Returns an iterator of address-vertex pairs within the specified columns span.
   */
  *entriesFromColumnsSpan(columnsSpan) {
    const sheetMapping = this.getStrategyForSheetOrThrow(columnsSpan.sheet);
    yield* sheetMapping.entriesFromColumnsSpan(columnsSpan);
  }
  /**
   * Returns an iterator of all address-vertex pairs across all sheets.
   * @returns {IterableIterator<[SimpleCellAddress, Maybe<CellVertex>]>} Iterator of [address, vertex] tuples
   */
  *entries() {
    for (const [sheet, mapping] of this.mapping.entries()) {
      yield* mapping.getEntries(sheet);
    }
  }
  /**
   * Returns an iterator of address-vertex pairs for a specific sheet.
   * @returns {IterableIterator<[SimpleCellAddress, CellVertex]>} Iterator of [address, vertex] tuples
   * @throws {NoSheetWithIdError} if sheet doesn't exist
   */
  *sheetEntries(sheetId) {
    const sheetMapping = this.getStrategyForSheetOrThrow(sheetId);
    yield* sheetMapping.getEntries(sheetId);
  }
  /**
   * Checks if a sheet has any entries.
   * @throws {NoSheetWithIdError} if sheet doesn't exist
   */
  hasAnyEntries(sheetId) {
    const iterator = this.sheetEntries(sheetId);
    return !iterator.next().done;
  }
}
class ArrayMapping {
  constructor() {
    this.arrayMapping = /* @__PURE__ */ new Map();
  }
  getArray(range) {
    const array = this.getArrayByCorner(range.start);
    if (array === null || array === void 0 ? void 0 : array.getRange().sameAs(range)) {
      return array;
    }
    return;
  }
  getArrayByCorner(address) {
    return this.arrayMapping.get(addressKey(address));
  }
  setArray(range, vertex) {
    this.arrayMapping.set(addressKey(range.start), vertex);
  }
  removeArray(range) {
    if (typeof range === "string") {
      this.arrayMapping.delete(range);
    } else {
      this.arrayMapping.delete(addressKey(range.start));
    }
  }
  count() {
    return this.arrayMapping.size;
  }
  *arraysInRows(rowsSpan) {
    for (const [mtxKey, mtx] of this.arrayMapping.entries()) {
      if (mtx.spansThroughSheetRows(rowsSpan.sheet, rowsSpan.rowStart, rowsSpan.rowEnd)) {
        yield [mtxKey, mtx];
      }
    }
  }
  *arraysInCols(col) {
    for (const [mtxKey, mtx] of this.arrayMapping.entries()) {
      if (mtx.spansThroughSheetColumn(col.sheet, col.columnStart, col.columnEnd)) {
        yield [mtxKey, mtx];
      }
    }
  }
  isFormulaArrayInRow(sheet, row) {
    for (const mtx of this.arrayMapping.values()) {
      if (mtx.spansThroughSheetRows(sheet, row)) {
        return true;
      }
    }
    return false;
  }
  isFormulaArrayInAllRows(span) {
    let result = true;
    for (const row of span.rows()) {
      if (!this.isFormulaArrayInRow(span.sheet, row)) {
        result = false;
      }
    }
    return result;
  }
  isFormulaArrayInColumn(sheet, column) {
    for (const mtx of this.arrayMapping.values()) {
      if (mtx.spansThroughSheetColumn(sheet, column)) {
        return true;
      }
    }
    return false;
  }
  isFormulaArrayInAllColumns(span) {
    let result = true;
    for (const col of span.columns()) {
      if (!this.isFormulaArrayInColumn(span.sheet, col)) {
        result = false;
      }
    }
    return result;
  }
  isFormulaArrayInRange(range) {
    for (const mtx of this.arrayMapping.values()) {
      if (mtx.getRange().doesOverlap(range)) {
        return true;
      }
    }
    return false;
  }
  isFormulaArrayAtAddress(address) {
    for (const mtx of this.arrayMapping.values()) {
      if (mtx.getRange().addressInRange(address)) {
        return true;
      }
    }
    return false;
  }
  moveArrayVerticesAfterRowByRows(sheet, row, numberOfRows) {
    this.updateArrayVerticesInSheet(sheet, (key, vertex) => {
      const range = vertex.getRange();
      return row <= range.start.row ? [range.shifted(0, numberOfRows), vertex] : void 0;
    });
  }
  moveArrayVerticesAfterColumnByColumns(sheet, column, numberOfColumns) {
    this.updateArrayVerticesInSheet(sheet, (key, vertex) => {
      const range = vertex.getRange();
      return column <= range.start.col ? [range.shifted(numberOfColumns, 0), vertex] : void 0;
    });
  }
  updateArrayVerticesInSheet(sheet, fn2) {
    const updated = Array();
    for (const [key, vertex] of this.arrayMapping.entries()) {
      if (vertex.sheet !== sheet) {
        continue;
      }
      const result = fn2(key, vertex);
      if (result !== void 0) {
        this.removeArray(key);
        updated.push(result);
      }
    }
    updated.forEach(([range, array]) => {
      this.setArray(range, array);
    });
  }
}
class NotComputedArray {
  constructor(size) {
    this.size = size;
  }
  width() {
    return this.size.width;
  }
  height() {
    return this.size.height;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  get(col, row) {
    throw Error("Array not computed yet.");
  }
  simpleRangeValue() {
    throw Error("Array not computed yet.");
  }
}
class ArrayValue {
  constructor(array) {
    this.size = new ArraySize(array.length > 0 ? array[0].length : 0, array.length);
    this.array = array;
    if (this.size.width <= 0 || this.size.height <= 0) {
      throw Error("Incorrect array size");
    }
  }
  static fromInterpreterValue(value) {
    if (value instanceof SimpleRangeValue) {
      return new ArrayValue(value.data);
    } else {
      return new ArrayValue([[value]]);
    }
  }
  simpleRangeValue() {
    return SimpleRangeValue.onlyValues(this.array);
  }
  addRows(aboveRow, numberOfRows) {
    this.array.splice(aboveRow, 0, ...this.nullArrays(numberOfRows, this.width()));
    this.size.height += numberOfRows;
  }
  addColumns(aboveColumn, numberOfColumns) {
    for (let i2 = 0; i2 < this.height(); i2++) {
      this.array[i2].splice(aboveColumn, 0, ...new Array(numberOfColumns).fill(EmptyValue));
    }
    this.size.width += numberOfColumns;
  }
  removeRows(startRow, endRow) {
    if (this.outOfBound(0, startRow) || this.outOfBound(0, endRow)) {
      throw Error("Array index out of bound");
    }
    const numberOfRows = endRow - startRow + 1;
    this.array.splice(startRow, numberOfRows);
    this.size.height -= numberOfRows;
  }
  removeColumns(leftmostColumn, rightmostColumn) {
    if (this.outOfBound(leftmostColumn, 0) || this.outOfBound(rightmostColumn, 0)) {
      throw Error("Array index out of bound");
    }
    const numberOfColumns = rightmostColumn - leftmostColumn + 1;
    for (const row of this.array) {
      row.splice(leftmostColumn, numberOfColumns);
    }
    this.size.width -= numberOfColumns;
  }
  nullArrays(count, size) {
    const result = [];
    for (let i2 = 0; i2 < count; ++i2) {
      result.push(new Array(size).fill(EmptyValue));
    }
    return result;
  }
  get(col, row) {
    if (this.outOfBound(col, row)) {
      throw Error("Array index out of bound");
    }
    return this.array[row][col];
  }
  set(col, row, value) {
    if (this.outOfBound(col, row)) {
      throw Error("Array index out of bound");
    }
    this.array[row][col] = value;
  }
  width() {
    return this.size.width;
  }
  height() {
    return this.size.height;
  }
  raw() {
    return this.array;
  }
  resize(newSize) {
    if (this.height() < newSize.height && isFinite(newSize.height)) {
      this.addRows(this.height(), newSize.height - this.height());
    }
    if (this.height() > newSize.height) {
      throw Error("Resizing to smaller array");
    }
    if (this.width() < newSize.width && isFinite(newSize.width)) {
      this.addColumns(this.width(), newSize.width - this.width());
    }
    if (this.width() > newSize.width) {
      throw Error("Resizing to smaller array");
    }
  }
  outOfBound(col, row) {
    return col < 0 || row < 0 || row > this.size.height - 1 || col > this.size.width - 1;
  }
}
class ErroredArray {
  constructor(error, size) {
    this.error = error;
    this.size = size;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  get(col, row) {
    return this.error;
  }
  width() {
    return this.size.width;
  }
  height() {
    return this.size.height;
  }
  simpleRangeValue() {
    return this.error;
  }
}
class Vertex {
}
class CellVertex extends Vertex {
  constructor() {
    super();
  }
}
class FormulaVertex extends CellVertex {
  constructor(formula, cellAddress, version) {
    super();
    this.formula = formula;
    this.cellAddress = cellAddress;
    this.version = version;
  }
  get width() {
    return 1;
  }
  get height() {
    return 1;
  }
  static fromAst(formula, address, size, version) {
    if (size.isScalar()) {
      return new ScalarFormulaVertex(formula, address, version);
    } else {
      return new ArrayFormulaVertex(formula, address, size, version);
    }
  }
  /**
   * Returns formula stored in this vertex
   */
  getFormula(updatingService) {
    this.ensureRecentData(updatingService);
    return this.formula;
  }
  ensureRecentData(updatingService) {
    if (this.version != updatingService.version()) {
      const [newAst, newAddress, newVersion] = updatingService.applyTransformations(this.formula, this.cellAddress, this.version);
      this.formula = newAst;
      this.cellAddress = newAddress;
      this.version = newVersion;
    }
  }
  /**
   * Returns address of the cell associated with vertex
   */
  getAddress(updatingService) {
    this.ensureRecentData(updatingService);
    return this.cellAddress;
  }
}
class ArrayFormulaVertex extends FormulaVertex {
  constructor(formula, cellAddress, size, version = 0) {
    super(formula, cellAddress, version);
    if (size.isRef) {
      this.array = new ErroredArray(new CellError(ErrorType.REF, ErrorMessage.NoSpaceForArrayResult), ArraySize.error());
    } else {
      this.array = new NotComputedArray(size);
    }
  }
  get width() {
    return this.array.width();
  }
  get height() {
    return this.array.height();
  }
  get sheet() {
    return this.cellAddress.sheet;
  }
  get leftCorner() {
    return this.cellAddress;
  }
  setCellValue(value) {
    if (value instanceof CellError) {
      this.setErrorValue(value);
      return value;
    }
    const array = ArrayValue.fromInterpreterValue(value);
    array.resize(this.array.size);
    this.array = array;
    return value;
  }
  getCellValue() {
    if (this.array instanceof NotComputedArray) {
      throw Error("Array not computed yet.");
    }
    return this.array.simpleRangeValue();
  }
  valueOrUndef() {
    if (this.array instanceof NotComputedArray) {
      return void 0;
    }
    return this.array.simpleRangeValue();
  }
  getArrayCellValue(address) {
    const col = address.col - this.cellAddress.col;
    const row = address.row - this.cellAddress.row;
    try {
      return this.array.get(col, row);
    } catch (e3) {
      return new CellError(ErrorType.REF);
    }
  }
  getArrayCellRawValue(address) {
    const val = this.getArrayCellValue(address);
    if (val instanceof CellError || val === EmptyValue) {
      return void 0;
    } else {
      return getRawValue(val);
    }
  }
  setArrayCellValue(address, value) {
    const col = address.col - this.cellAddress.col;
    const row = address.row - this.cellAddress.row;
    if (this.array instanceof ArrayValue) {
      this.array.set(col, row, value);
    }
  }
  setNoSpace() {
    this.array = new ErroredArray(new CellError(ErrorType.SPILL, ErrorMessage.NoSpaceForArrayResult), ArraySize.error());
    return this.getCellValue();
  }
  getRange() {
    return AbsoluteCellRange.spanFrom(this.cellAddress, this.width, this.height);
  }
  getRangeOrUndef() {
    return AbsoluteCellRange.spanFromOrUndef(this.cellAddress, this.width, this.height);
  }
  setAddress(address) {
    this.cellAddress = address;
  }
  setFormula(newFormula) {
    this.formula = newFormula;
  }
  spansThroughSheetRows(sheet, startRow, endRow = startRow) {
    return this.cellAddress.sheet === sheet && this.cellAddress.row <= endRow && startRow < this.cellAddress.row + this.height;
  }
  spansThroughSheetColumn(sheet, col, columnEnd = col) {
    return this.cellAddress.sheet === sheet && this.cellAddress.col <= columnEnd && col < this.cellAddress.col + this.width;
  }
  isComputed() {
    return !(this.array instanceof NotComputedArray);
  }
  columnsFromArray() {
    return ColumnsSpan.fromNumberOfColumns(this.cellAddress.sheet, this.cellAddress.col, this.width);
  }
  rowsFromArray() {
    return RowsSpan.fromNumberOfRows(this.cellAddress.sheet, this.cellAddress.row, this.height);
  }
  /**
   * No-op as array vertices are transformed eagerly.
   */
  ensureRecentData(_updatingService) {
  }
  isLeftCorner(address) {
    return equalSimpleCellAddress(this.cellAddress, address);
  }
  setErrorValue(error) {
    this.array = new ErroredArray(error, this.array.size);
  }
}
class ScalarFormulaVertex extends FormulaVertex {
  constructor(formula, address, version) {
    super(formula, address, version);
  }
  valueOrUndef() {
    return this.cachedCellValue;
  }
  /**
   * Sets computed cell value stored in this vertex
   */
  setCellValue(cellValue) {
    this.cachedCellValue = cellValue;
    return this.cachedCellValue;
  }
  /**
   * Returns cell value stored in vertex
   */
  getCellValue() {
    if (this.cachedCellValue !== void 0) {
      return this.cachedCellValue;
    } else {
      throw Error("Value of the formula cell is not computed.");
    }
  }
  isComputed() {
    return this.cachedCellValue !== void 0;
  }
}
class RangeVertex extends Vertex {
  constructor(range) {
    super();
    this.range = range;
    this.functionCache = /* @__PURE__ */ new Map();
    this.criterionFunctionCache = /* @__PURE__ */ new Map();
    this.dependentCacheRanges = /* @__PURE__ */ new Set();
    this.bruteForce = false;
  }
  get start() {
    return this.range.start;
  }
  get end() {
    return this.range.end;
  }
  get sheet() {
    return this.range.start.sheet;
  }
  /**
   * Returns cached value stored for given function
   *
   * @param functionName - name of the function
   */
  getFunctionValue(functionName2) {
    return this.functionCache.get(functionName2);
  }
  /**
   * Stores cached value for given function
   *
   * @param functionName - name of the function
   * @param value - cached value
   */
  setFunctionValue(functionName2, value) {
    this.functionCache.set(functionName2, value);
  }
  /**
   * Returns cached value for given cache key and criterion text representation
   *
   * @param cacheKey - key to retrieve from the cache
   * @param criterionString - criterion text (ex. '<=5')
   */
  getCriterionFunctionValue(cacheKey, criterionString) {
    var _a2;
    return (_a2 = this.getCriterionFunctionValues(cacheKey).get(criterionString)) === null || _a2 === void 0 ? void 0 : _a2[0];
  }
  /**
   * Returns all cached values stored for given criterion function
   *
   * @param cacheKey - key to retrieve from the cache
   */
  getCriterionFunctionValues(cacheKey) {
    var _a2;
    return (_a2 = this.criterionFunctionCache.get(cacheKey)) !== null && _a2 !== void 0 ? _a2 : /* @__PURE__ */ new Map();
  }
  /**
   * Stores all values for given criterion function
   *
   * @param cacheKey - key to store in the cache
   * @param values - map with values
   */
  setCriterionFunctionValues(cacheKey, values2) {
    this.criterionFunctionCache.set(cacheKey, values2);
  }
  addDependentCacheRange(dependentRange) {
    if (dependentRange !== this) {
      this.dependentCacheRanges.add(dependentRange);
    }
  }
  /**
   * Clears function cache
   */
  clearCache() {
    this.functionCache.clear();
    this.criterionFunctionCache.clear();
    this.dependentCacheRanges.forEach((range) => range.criterionFunctionCache.clear());
    this.dependentCacheRanges.clear();
  }
}
const collectAddressesDependentToRange = (functionRegistry, vertex, range, lazilyTransformingAstService, dependencyGraph) => {
  if (vertex instanceof RangeVertex) {
    const intersection = vertex.range.intersectionWith(range);
    if (intersection !== void 0) {
      return Array.from(intersection.addresses(dependencyGraph));
    } else {
      return [];
    }
  }
  let formula;
  let address;
  if (vertex instanceof FormulaVertex) {
    formula = vertex.getFormula(lazilyTransformingAstService);
    address = vertex.getAddress(lazilyTransformingAstService);
  } else {
    return [];
  }
  return collectDependencies(formula, functionRegistry).filter((d2) => d2 instanceof AddressDependency).map((d2) => d2.dependency.toSimpleCellAddress(address)).filter((d2) => range.addressInRange(d2));
};
var NodeVisitStatus;
(function(NodeVisitStatus2) {
  NodeVisitStatus2[NodeVisitStatus2["ON_STACK"] = 0] = "ON_STACK";
  NodeVisitStatus2[NodeVisitStatus2["PROCESSED"] = 1] = "PROCESSED";
  NodeVisitStatus2[NodeVisitStatus2["POPPED"] = 2] = "POPPED";
})(NodeVisitStatus || (NodeVisitStatus = {}));
class TopSort {
  constructor(nodesSparseArray = [], edgesSparseArray = []) {
    this.nodesSparseArray = nodesSparseArray;
    this.edgesSparseArray = edgesSparseArray;
    this.entranceTime = [];
    this.low = [];
    this.parent = [];
    this.inSCC = [];
    this.nodeStatus = [];
    this.order = [];
    this.sccNonSingletons = [];
    this.timeCounter = 0;
  }
  /**
   * An iterative implementation of Tarjan's algorithm for finding strongly connected components.
   * Returns vertices in order of topological sort, but vertices that are on cycles are kept separate.
   *
   * @param modifiedNodes - seed for computation. During engine init run, all of the vertices of grap. In recomputation run, changed vertices.
   * @param operatingFunction - recomputes value of a node, and returns whether a change occurred
   * @param onCycle - action to be performed when node is on cycle
   */
  getTopSortedWithSccSubgraphFrom(modifiedNodeIds, operatingFunction, onCycle) {
    const modifiedNodeIdsReversed = modifiedNodeIds.reverse();
    modifiedNodeIdsReversed.forEach((id) => this.runDFS(id));
    return this.postprocess(modifiedNodeIdsReversed, onCycle, operatingFunction);
  }
  /**
   * Returns adjacent nodes of a given node.
   */
  getAdjacentNodeIds(id) {
    return this.edgesSparseArray[id].filter((adjacentId) => adjacentId !== void 0 && this.nodesSparseArray[adjacentId]);
  }
  /**
   * Runs DFS starting from a given node.
   */
  runDFS(v2) {
    if (this.nodeStatus[v2] !== void 0) {
      return;
    }
    this.nodeStatus[v2] = NodeVisitStatus.ON_STACK;
    const DFSstack = [v2];
    const SCCstack = [];
    while (DFSstack.length > 0) {
      const u2 = DFSstack[DFSstack.length - 1];
      switch (this.nodeStatus[u2]) {
        case NodeVisitStatus.ON_STACK: {
          this.handleOnStack(u2, SCCstack, DFSstack);
          break;
        }
        case NodeVisitStatus.PROCESSED: {
          this.handleProcessed(u2, SCCstack, DFSstack);
          break;
        }
        case NodeVisitStatus.POPPED: {
          DFSstack.pop();
          break;
        }
      }
    }
  }
  /**
   * Handles a node that is on stack.
   */
  handleOnStack(u2, SCCstack, DFSstack) {
    this.entranceTime[u2] = this.timeCounter;
    this.low[u2] = this.timeCounter;
    this.timeCounter++;
    SCCstack.push(u2);
    this.getAdjacentNodeIds(u2).forEach((t2) => {
      if (this.entranceTime[t2] === void 0) {
        DFSstack.push(t2);
        this.parent[t2] = u2;
        this.nodeStatus[t2] = NodeVisitStatus.ON_STACK;
      }
    });
    this.nodeStatus[u2] = NodeVisitStatus.PROCESSED;
  }
  /**
   * Handles a node that is already processed.
   */
  handleProcessed(u2, SCCstack, DFSstack) {
    let uLow = this.entranceTime[u2];
    this.getAdjacentNodeIds(u2).forEach((t2) => {
      if (this.inSCC[t2]) {
        return;
      }
      uLow = this.parent[t2] === u2 ? Math.min(uLow, this.low[t2]) : Math.min(uLow, this.entranceTime[t2]);
    });
    this.low[u2] = uLow;
    if (uLow === this.entranceTime[u2]) {
      const currentSCC = [];
      do {
        currentSCC.push(SCCstack[SCCstack.length - 1]);
        SCCstack.pop();
      } while (currentSCC[currentSCC.length - 1] !== u2);
      currentSCC.forEach((t2) => {
        this.inSCC[t2] = true;
      });
      this.order.push(...currentSCC);
      if (currentSCC.length > 1) {
        currentSCC.forEach((t2) => {
          this.sccNonSingletons[t2] = true;
        });
      }
    }
    DFSstack.pop();
    this.nodeStatus[u2] = NodeVisitStatus.POPPED;
  }
  /**
   * Postprocesses the result of Tarjan's algorithm.
   */
  postprocess(modifiedNodeIds, onCycle, operatingFunction) {
    const shouldBeUpdatedMapping = [];
    modifiedNodeIds.forEach((t2) => {
      shouldBeUpdatedMapping[t2] = true;
    });
    const sorted = [];
    const cycled = [];
    this.order.reverse();
    this.order.forEach((t2) => {
      const adjacentNodes = this.getAdjacentNodeIds(t2);
      if (this.sccNonSingletons[t2] || adjacentNodes.includes(t2)) {
        cycled.push(this.nodesSparseArray[t2]);
        onCycle(this.nodesSparseArray[t2]);
        adjacentNodes.forEach((s2) => shouldBeUpdatedMapping[s2] = true);
      } else {
        sorted.push(this.nodesSparseArray[t2]);
        if (shouldBeUpdatedMapping[t2] && operatingFunction(this.nodesSparseArray[t2])) {
          adjacentNodes.forEach((s2) => shouldBeUpdatedMapping[s2] = true);
        }
      }
    });
    return {
      sorted,
      cycled
    };
  }
}
class ProcessableValue {
  constructor(rawValue, processFn) {
    this.rawValue = rawValue;
    this.processFn = processFn;
    this.processedValue = null;
  }
  getProcessedValue() {
    if (this.processedValue === null) {
      this.processedValue = this.processFn(this.rawValue);
    }
    return this.processedValue;
  }
  markAsModified() {
    this.processedValue = null;
  }
}
class Graph2 {
  constructor(dependencyQuery) {
    this.dependencyQuery = dependencyQuery;
    this.nodesSparseArray = [];
    this.edgesSparseArray = [];
    this.dirtyAndVolatileNodeIds = new ProcessableValue({
      dirty: [],
      volatile: []
    }, (r2) => this.processDirtyAndVolatileNodeIds(r2));
    this.infiniteRangeIds = /* @__PURE__ */ new Set();
    this.changingWithStructureNodeIds = [];
    this.nextId = 0;
  }
  /**
   * Iterate over all nodes the in graph
   */
  getNodes() {
    return this.nodesSparseArray.filter((node) => node !== void 0);
  }
  /**
   * Checks whether a node is present in graph
   *
   * @param {Node} node - node to check
   */
  hasNode(node) {
    return node.idInGraph != null;
  }
  /**
   * Checks whether exists edge between nodes. If one or both of nodes are not present in graph, returns false.
   *
   * @param {Node} fromNode - node from which edge is outcoming
   * @param {Node} toNode - node to which edge is incoming
   */
  existsEdge(fromNode, toNode) {
    const fromId = fromNode.idInGraph;
    const toId = toNode.idInGraph;
    if (fromId === void 0 || toId === void 0) {
      return false;
    }
    return this.edgesSparseArray[fromId].includes(toId);
  }
  /**
   * Returns nodes adjacent to given node. May contain removed nodes.
   *
   * @param {Node} node - node to which adjacent nodes we want to retrieve
   *
   * Idea for performance improvement:
   * - return an array instead of set
   */
  adjacentNodes(node) {
    const nodeId = node.idInGraph;
    if (nodeId === void 0) {
      throw this.missingNodeError(node);
    }
    return new Set(this.edgesSparseArray[nodeId].filter((id) => id !== void 0).map((id) => this.nodesSparseArray[id]).filter((node2) => node2 !== void 0));
  }
  /**
   * Returns number of nodes adjacent to given node. Contrary to adjacentNodes(), this method returns only nodes that are present in graph.
   *
   * @param {Node} node - node to which adjacent nodes we want to retrieve
   */
  adjacentNodesCount(node) {
    const id = node.idInGraph;
    if (id === void 0) {
      throw this.missingNodeError(node);
    }
    return this.fixEdgesArrayForNode(id).length;
  }
  /**
   * Adds node to a graph if it does not exist yet.
   *
   * @param {Node} node - a node to be added
   * @returns {NodeId} - the id of the added node
   */
  addNodeIfNotExists(node) {
    const idOfExistingNode = node.idInGraph;
    if (idOfExistingNode !== void 0) {
      return idOfExistingNode;
    }
    const newId = this.nextId;
    this.nextId++;
    this.nodesSparseArray[newId] = node;
    this.edgesSparseArray[newId] = [];
    node.idInGraph = newId;
    return newId;
  }
  /**
   * Adds edge between nodes.
   *
   * The nodes had to be added to the graph before, or the error will be raised
   *
   * @param {Node | NodeId} fromNode - node from which edge is outcoming
   * @param {Node | NodeId} toNode - node to which edge is incoming
   */
  addEdge(fromNode, toNode) {
    const fromId = this.getNodeIdIfNotNumber(fromNode);
    const toId = this.getNodeIdIfNotNumber(toNode);
    if (fromId === void 0) {
      throw this.missingNodeError(fromNode);
    }
    if (toId === void 0) {
      throw this.missingNodeError(toNode);
    }
    if (this.edgesSparseArray[fromId].includes(toId)) {
      return;
    }
    this.edgesSparseArray[fromId].push(toId);
  }
  /**
   * Removes node from graph
   */
  removeNode(node) {
    const id = node.idInGraph;
    if (id === void 0) {
      throw this.missingNodeError(node);
    }
    if (this.edgesSparseArray[id].length > 0) {
      this.edgesSparseArray[id].forEach((adjacentId) => this.dirtyAndVolatileNodeIds.rawValue.dirty.push(adjacentId));
      this.dirtyAndVolatileNodeIds.markAsModified();
    }
    const dependencies = this.removeDependencies(node);
    delete this.nodesSparseArray[id];
    delete this.edgesSparseArray[id];
    this.infiniteRangeIds.delete(id);
    node.idInGraph = void 0;
    return dependencies;
  }
  /**
   * Removes edge between nodes.
   */
  removeEdge(fromNode, toNode) {
    const fromId = this.getNodeIdIfNotNumber(fromNode);
    const toId = this.getNodeIdIfNotNumber(toNode);
    if (fromId === void 0) {
      throw this.missingNodeError(fromNode);
    }
    if (toId === void 0) {
      throw this.missingNodeError(toNode);
    }
    const indexOfToId = this.edgesSparseArray[fromId].indexOf(toId);
    if (indexOfToId === -1) {
      throw new Error("Edge does not exist");
    }
    delete this.edgesSparseArray[fromId][indexOfToId];
  }
  /**
   * Removes edge between nodes if it exists.
   */
  removeEdgeIfExists(fromNode, toNode) {
    const fromId = this.getNodeIdIfNotNumber(fromNode);
    const toId = this.getNodeIdIfNotNumber(toNode);
    if (fromId === void 0) {
      return;
    }
    if (toId === void 0) {
      return;
    }
    const indexOfToId = this.edgesSparseArray[fromId].indexOf(toId);
    if (indexOfToId === -1) {
      return;
    }
    delete this.edgesSparseArray[fromId][indexOfToId];
  }
  /**
   * Sorts the whole graph topologically. Nodes that are on cycles are kept separate.
   */
  topSortWithScc() {
    return this.getTopSortedWithSccSubgraphFrom(this.getNodes(), () => true, () => {
    });
  }
  /**
   * Sorts the graph topologically. Nodes that are on cycles are kept separate.
   *
   * @param {Node[]} modifiedNodes - seed for computation. The algorithm assumes that only these nodes have changed since the last run.
   * @param {(node: Node) => boolean} operatingFunction - recomputes value of a node, and returns whether a change occurred
   * @param {(node: Node) => void} onCycle - action to be performed when node is on cycle
   */
  getTopSortedWithSccSubgraphFrom(modifiedNodes, operatingFunction, onCycle) {
    const topSortAlgorithm = new TopSort(this.nodesSparseArray, this.edgesSparseArray);
    const modifiedNodesIds = modifiedNodes.map((node) => node.idInGraph).filter((id) => id !== void 0);
    return topSortAlgorithm.getTopSortedWithSccSubgraphFrom(modifiedNodesIds, operatingFunction, onCycle);
  }
  /**
   * Marks node as volatile.
   */
  markNodeAsVolatile(node) {
    const id = node.idInGraph;
    if (id === void 0) {
      return;
    }
    this.dirtyAndVolatileNodeIds.rawValue.volatile.push(id);
    this.dirtyAndVolatileNodeIds.markAsModified();
  }
  /**
   * Marks node as dirty.
   */
  markNodeAsDirty(node) {
    const id = node.idInGraph;
    if (id === void 0) {
      return;
    }
    this.dirtyAndVolatileNodeIds.rawValue.dirty.push(id);
    this.dirtyAndVolatileNodeIds.markAsModified();
  }
  /**
   * Returns an array of nodes that are marked as dirty and/or volatile.
   */
  getDirtyAndVolatileNodes() {
    return this.dirtyAndVolatileNodeIds.getProcessedValue();
  }
  /**
   * Clears dirty nodes.
   */
  clearDirtyNodes() {
    this.dirtyAndVolatileNodeIds.rawValue.dirty = [];
    this.dirtyAndVolatileNodeIds.markAsModified();
  }
  /**
   * Marks node as changingWithStructure.
   */
  markNodeAsChangingWithStructure(node) {
    const id = node.idInGraph;
    if (id === void 0) {
      return;
    }
    this.changingWithStructureNodeIds.push(id);
  }
  /**
   * Marks all nodes marked as changingWithStructure as dirty.
   */
  markChangingWithStructureNodesAsDirty() {
    if (this.changingWithStructureNodeIds.length <= 0) {
      return;
    }
    this.dirtyAndVolatileNodeIds.rawValue.dirty = [...this.dirtyAndVolatileNodeIds.rawValue.dirty, ...this.changingWithStructureNodeIds];
    this.dirtyAndVolatileNodeIds.markAsModified();
  }
  /**
   * Marks node as infinite range.
   */
  markNodeAsInfiniteRange(node) {
    const id = this.getNodeIdIfNotNumber(node);
    if (id === void 0) {
      return;
    }
    this.infiniteRangeIds.add(id);
  }
  /**
   * Returns an array of nodes marked as infinite ranges
   */
  getInfiniteRanges() {
    return [...this.infiniteRangeIds].map((id) => this.nodesSparseArray[id]);
  }
  /**
   *
   */
  getNodeIdIfNotNumber(node) {
    return typeof node === "number" ? node : node.idInGraph;
  }
  /**
   * Removes invalid neighbors of a given node from the edges array and returns adjacent nodes for the input node.
   */
  fixEdgesArrayForNode(id) {
    const adjacentNodeIds = this.edgesSparseArray[id];
    this.edgesSparseArray[id] = adjacentNodeIds.filter((adjacentId) => adjacentId !== void 0 && this.nodesSparseArray[adjacentId]);
    return this.edgesSparseArray[id];
  }
  /**
   * Removes edges from the given node to its dependencies based on the dependencyQuery function.
   */
  removeDependencies(node) {
    const dependencies = this.dependencyQuery(node);
    dependencies.forEach(([_2, dependency]) => {
      this.removeEdgeIfExists(dependency, node);
    });
    return dependencies;
  }
  /**
   * processFn for dirtyAndVolatileNodeIds ProcessableValue instance
   * @private
   */
  processDirtyAndVolatileNodeIds({
    dirty,
    volatile
  }) {
    return [.../* @__PURE__ */ new Set([...dirty, ...volatile])].map((id) => this.nodesSparseArray[id]).filter((node) => node !== void 0);
  }
  /**
   * Returns error for missing node.
   */
  missingNodeError(node) {
    return new Error(`Unknown node ${JSON.stringify(node)}`);
  }
}
class RangeMapping {
  constructor() {
    this.rangeMapping = /* @__PURE__ */ new Map();
  }
  /**
   * Returns number of ranges in the sheet or 0 if the sheet does not exist
   */
  getNumberOfRangesInSheet(sheet) {
    var _a2, _b;
    return (_b = (_a2 = this.rangeMapping.get(sheet)) === null || _a2 === void 0 ? void 0 : _a2.size) !== null && _b !== void 0 ? _b : 0;
  }
  /**
   * Adds or updates vertex in the mapping
   */
  addOrUpdateVertex(vertex) {
    let sheetMap = this.rangeMapping.get(vertex.sheet);
    if (sheetMap === void 0) {
      sheetMap = /* @__PURE__ */ new Map();
      this.rangeMapping.set(vertex.sheet, sheetMap);
    }
    const key = RangeMapping.calculateRangeKey(vertex.start, vertex.end);
    sheetMap.set(key, vertex);
  }
  /**
   * Removes vertex from the mapping if it exists
   */
  removeVertexIfExists(vertex) {
    const sheet = vertex.sheet;
    const sheetMap = this.rangeMapping.get(sheet);
    if (sheetMap === void 0) {
      return;
    }
    const key = RangeMapping.calculateRangeKey(vertex.start, vertex.end);
    sheetMap.delete(key);
    if (sheetMap.size === 0) {
      this.rangeMapping.delete(sheet);
    }
  }
  /**
   * Returns associated vertex for given range
   */
  getRangeVertex(start, end) {
    const sheetMap = this.rangeMapping.get(start.sheet);
    const key = RangeMapping.calculateRangeKey(start, end);
    return sheetMap === null || sheetMap === void 0 ? void 0 : sheetMap.get(key);
  }
  /**
   * Returns associated vertex for given range or throws an error if not found
   */
  getVertexOrThrow(start, end) {
    const maybeRange = this.getRangeVertex(start, end);
    if (!maybeRange) {
      throw Error("Range does not exist");
    }
    return maybeRange;
  }
  truncateRanges(span, coordinate) {
    const verticesToRemove = Array();
    const updated = Array();
    const verticesWithChangedSize = Array();
    const sheet = span.sheet;
    for (const [key, vertex] of this.entriesFromSheet(span.sheet)) {
      const range = vertex.range;
      if (span.start <= coordinate(vertex.range.end)) {
        range.removeSpan(span);
        if (range.shouldBeRemoved()) {
          this.removeByKey(sheet, key);
          verticesToRemove.push(vertex);
        } else {
          updated.push([key, vertex]);
        }
        verticesWithChangedSize.push(vertex);
      }
    }
    const verticesToMerge = [];
    updated.sort((left, right) => RangeMapping.compareBy(left[1], right[1], coordinate));
    for (const [oldKey, vertex] of updated) {
      const newKey = RangeMapping.calculateRangeKey(vertex.range.start, vertex.range.end);
      if (newKey === oldKey) {
        continue;
      }
      const existingVertex = this.getByKey(sheet, newKey);
      this.removeByKey(sheet, oldKey);
      if (existingVertex !== void 0 && vertex != existingVertex) {
        verticesToMerge.push([existingVertex, vertex]);
      } else {
        this.addOrUpdateVertex(vertex);
      }
    }
    return {
      verticesToRemove,
      verticesToMerge,
      verticesWithChangedSize
    };
  }
  moveAllRangesInSheetAfterAddingRows(sheet, row, numberOfRows) {
    return this.updateVerticesFromSheet(sheet, (key, vertex) => {
      if (row <= vertex.start.row) {
        vertex.range.shiftByRows(numberOfRows);
        return {
          changedSize: false,
          vertex
        };
      } else if (row > vertex.start.row && row <= vertex.end.row) {
        vertex.range.expandByRows(numberOfRows);
        return {
          changedSize: true,
          vertex
        };
      } else {
        return void 0;
      }
    });
  }
  moveAllRangesInSheetAfterAddingColumns(sheet, column, numberOfColumns) {
    return this.updateVerticesFromSheet(sheet, (key, vertex) => {
      if (column <= vertex.start.col) {
        vertex.range.shiftByColumns(numberOfColumns);
        return {
          changedSize: false,
          vertex
        };
      } else if (column > vertex.start.col && column <= vertex.end.col) {
        vertex.range.expandByColumns(numberOfColumns);
        return {
          changedSize: true,
          vertex
        };
      } else {
        return void 0;
      }
    });
  }
  moveRangesInsideSourceRange(sourceRange, toRight, toBottom, toSheet) {
    this.updateVerticesFromSheet(sourceRange.sheet, (key, vertex) => {
      if (sourceRange.containsRange(vertex.range)) {
        vertex.range.shiftByColumns(toRight);
        vertex.range.shiftByRows(toBottom);
        vertex.range.moveToSheet(toSheet);
        return {
          changedSize: false,
          vertex
        };
      } else {
        return void 0;
      }
    });
  }
  *rangesInSheet(sheet) {
    const sheetMap = this.rangeMapping.get(sheet);
    if (!sheetMap) {
      return;
    }
    yield* sheetMap.values();
  }
  *rangeVerticesContainedInRange(sourceRange) {
    for (const rangeVertex of this.rangesInSheet(sourceRange.sheet)) {
      if (sourceRange.containsRange(rangeVertex.range)) {
        yield rangeVertex;
      }
    }
  }
  /**
   * Finds smaller range if exists.
   */
  findSmallerRange(range) {
    if (range.height() > 1 && Number.isFinite(range.height())) {
      const valuesRangeEndRowLess = simpleCellAddress(range.end.sheet, range.end.col, range.end.row - 1);
      const rowLessVertex = this.getRangeVertex(range.start, valuesRangeEndRowLess);
      if (rowLessVertex !== void 0) {
        const restRange = AbsoluteCellRange.fromSimpleCellAddresses(simpleCellAddress(range.start.sheet, range.start.col, range.end.row), range.end);
        return {
          smallerRangeVertex: rowLessVertex,
          restRange
        };
      }
    }
    return {
      restRange: range
    };
  }
  /**
   * Calculates a string key from start and end addresses
   */
  static calculateRangeKey(start, end) {
    return `${start.col},${start.row},${end.col},${end.row}`;
  }
  /**
   * Compares two range vertices by their start and end addresses using the provided coordinate function
   */
  static compareBy(left, right, coordinate) {
    const leftStart = coordinate(left.range.start);
    const rightStart = coordinate(right.range.start);
    if (leftStart === rightStart) {
      const leftEnd = coordinate(left.range.end);
      const rightEnd = coordinate(right.range.end);
      return leftEnd - rightEnd;
    } else {
      return leftStart - rightStart;
    }
  }
  *entriesFromSheet(sheet) {
    const sheetMap = this.rangeMapping.get(sheet);
    if (!sheetMap) {
      return;
    }
    yield* sheetMap.entries();
  }
  removeByKey(sheet, key) {
    const sheetMap = this.rangeMapping.get(sheet);
    if (!sheetMap) {
      throw new Error(`Sheet ${sheet} not found`);
    }
    sheetMap.delete(key);
  }
  getByKey(sheet, key) {
    var _a2;
    return (_a2 = this.rangeMapping.get(sheet)) === null || _a2 === void 0 ? void 0 : _a2.get(key);
  }
  updateVerticesFromSheet(sheet, fn2) {
    const updated = Array();
    for (const [key, vertex] of this.entriesFromSheet(sheet)) {
      const result = fn2(key, vertex);
      if (result !== void 0) {
        this.removeByKey(sheet, key);
        updated.push(result);
      }
    }
    updated.forEach((entry) => {
      this.addOrUpdateVertex(entry.vertex);
    });
    return {
      verticesWithChangedSize: updated.filter((entry) => entry.changedSize).map((entry) => entry.vertex)
    };
  }
}
class TranslationPackage {
  constructor(functions, errors, ui2) {
    this.functions = functions;
    this.errors = errors;
    this.ui = ui2;
    this._protectedTranslations = {
      "VERSION": "VERSION"
    };
    this.checkUI();
    this.checkErrors();
    this.checkFunctionTranslations(this.functions);
    Object.assign(this.functions, this._protectedTranslations);
  }
  extendFunctions(additionalFunctionTranslations) {
    this.checkFunctionTranslations(additionalFunctionTranslations);
    Object.assign(this.functions, additionalFunctionTranslations);
  }
  buildFunctionMapping() {
    return Object.keys(this.functions).reduce((ret, key) => {
      ret[this.functions[key]] = key;
      return ret;
    }, {});
  }
  buildErrorMapping() {
    return Object.keys(this.errors).reduce((ret, key) => {
      ret[this.errors[key]] = key;
      return ret;
    }, {});
  }
  isFunctionTranslated(key) {
    return this.functions[key] !== void 0;
  }
  getFunctionTranslations(functionIds) {
    const translations = [];
    for (const functionId of functionIds) {
      if (this.isFunctionTranslated(functionId)) {
        translations.push(this.functions[functionId]);
      }
    }
    return translations;
  }
  getFunctionTranslation(key) {
    const val = this.functions[key];
    if (val === void 0) {
      throw new MissingTranslationError(`functions.${key}`);
    } else {
      return val;
    }
  }
  getMaybeFunctionTranslation(key) {
    return this.functions[key];
  }
  getErrorTranslation(key) {
    if (key === ErrorType.LIC) {
      return `#${ErrorType.LIC}!`;
    }
    const val = this.errors[key];
    if (val === void 0) {
      throw new MissingTranslationError(`errors.${key}`);
    } else {
      return val;
    }
  }
  getUITranslation(key) {
    const val = this.ui[key];
    if (val === void 0) {
      throw new MissingTranslationError(`ui.${key}`);
    } else {
      return val;
    }
  }
  checkUI() {
    for (const key of Object.values(UIElement)) {
      if (!(key in this.ui)) {
        throw new MissingTranslationError(`ui.${key}`);
      }
    }
  }
  checkErrors() {
    for (const key of Object.values(ErrorType)) {
      if (!(key in this.errors) && key !== ErrorType.LIC) {
        throw new MissingTranslationError(`errors.${key}`);
      }
    }
  }
  checkFunctionTranslations(functions) {
    const functionNames = new Set(Object.getOwnPropertyNames(functions));
    for (const protectedTranslation of Object.getOwnPropertyNames(this._protectedTranslations)) {
      if (functionNames.has(protectedTranslation)) {
        throw new ProtectedFunctionTranslationError(protectedTranslation);
      }
    }
  }
}
function buildTranslationPackage(rawTranslationPackage) {
  return new TranslationPackage(Object.assign({}, rawTranslationPackage.functions), Object.assign({}, rawTranslationPackage.errors), Object.assign({}, rawTranslationPackage.ui));
}
var UIElement;
(function(UIElement2) {
  UIElement2["NEW_SHEET_PREFIX"] = "NEW_SHEET_PREFIX";
})(UIElement || (UIElement = {}));
class Sheet {
  constructor(id, displayName, isPlaceholder = false) {
    this.id = id;
    this.displayName = displayName;
    this.isPlaceholder = isPlaceholder;
  }
  /**
   * Returns the canonical (normalized) name of the sheet.
   */
  get canonicalName() {
    return SheetMapping.canonicalizeSheetName(this.displayName);
  }
}
class SheetMapping {
  constructor(languages) {
    this.lastSheetId = -1;
    this.mappingFromCanonicalNameToId = /* @__PURE__ */ new Map();
    this.allSheets = /* @__PURE__ */ new Map();
    this.sheetNamePrefix = languages.getUITranslation(UIElement.NEW_SHEET_PREFIX);
  }
  /**
   * Converts sheet name to canonical/normalized form.
   * @static
   */
  static canonicalizeSheetName(sheetDisplayName) {
    return sheetDisplayName.toLowerCase();
  }
  /**
   * Returns sheet ID for the given name. By default excludes placeholders.
   */
  getSheetId(sheetName, options = {}) {
    var _a2;
    return (_a2 = this._getSheetByName(sheetName, options)) === null || _a2 === void 0 ? void 0 : _a2.id;
  }
  /**
   * Returns sheet ID for the given name. Excludes placeholders.
   *
   * @throws {NoSheetWithNameError} if the sheet with the given name does not exist.
   */
  getSheetIdOrThrowError(sheetName) {
    const sheet = this._getSheetByName(sheetName, {});
    if (sheet === void 0) {
      throw new NoSheetWithNameError(sheetName);
    }
    return sheet.id;
  }
  /**
   * Returns display name for the given sheet ID. Excludes placeholders.
   *
   * @returns {Maybe<string>} the display name, or undefined if the sheet with the given ID does not exist.
   */
  getSheetName(sheetId) {
    var _a2;
    return (_a2 = this._getSheet(sheetId, {})) === null || _a2 === void 0 ? void 0 : _a2.displayName;
  }
  /**
   * Returns display name for the given sheet ID. Excludes placeholders.
   *
   * @throws {NoSheetWithIdError} if the sheet with the given ID does not exist.
   */
  getSheetNameOrThrowError(sheetId, options = {}) {
    return this._getSheetOrThrowError(sheetId, options).displayName;
  }
  /**
   * Iterates over all sheet display names. By default excludes placeholders.
   */
  *iterateSheetNames(options = {}) {
    for (const sheet of this.allSheets.values()) {
      if (options.includePlaceholders || !sheet.isPlaceholder) {
        yield sheet.displayName;
      }
    }
  }
  /**
   * Returns array of all sheet display names. By default excludes placeholders.
   */
  getSheetNames(options = {}) {
    return Array.from(this.iterateSheetNames(options));
  }
  /**
   * Returns total count of sheets. By default excludes placeholders.
   */
  numberOfSheets(options = {}) {
    return this.getSheetNames(options).length;
  }
  /**
   * Checks if sheet with given ID exists. By default excludes placeholders.
   */
  hasSheetWithId(sheetId, options = {}) {
    return this._getSheet(sheetId, options) !== void 0;
  }
  /**
   * Checks if sheet with given name exists (case-insensitive). Excludes placeholders.
   */
  hasSheetWithName(sheetName) {
    return this._getSheetByName(sheetName, {}) !== void 0;
  }
  /**
   * Adds new sheet with optional name and returns its ID.
   * If called with a name of an existing placeholder sheet, converts the placeholder sheet to a real sheet.
   *
   * @throws {SheetNameAlreadyTakenError} if the sheet with the given name already exists.
   */
  addSheet(newSheetDisplayName = `${this.sheetNamePrefix}${this.lastSheetId + 2}`) {
    const sheetWithConflictingName = this._getSheetByName(newSheetDisplayName, {
      includePlaceholders: true
    });
    if (sheetWithConflictingName) {
      if (!sheetWithConflictingName.isPlaceholder) {
        throw new SheetNameAlreadyTakenError(newSheetDisplayName);
      }
      sheetWithConflictingName.isPlaceholder = false;
      return sheetWithConflictingName.id;
    }
    this.lastSheetId++;
    const sheet = new Sheet(this.lastSheetId, newSheetDisplayName);
    this._storeSheetInMappings(sheet);
    return sheet.id;
  }
  /**
   * Adds a sheet with a specific ID and name. Used for redo operations.
   * If called with a name of an existing placeholder sheet, converts the placeholder sheet to a real sheet.
   *
   * @throws {SheetNameAlreadyTakenError} if the sheet with the given name already exists.
   */
  addSheetWithId(sheetId, sheetDisplayName) {
    const sheetWithConflictingName = this._getSheetByName(sheetDisplayName, {
      includePlaceholders: true
    });
    if (sheetWithConflictingName) {
      if (sheetWithConflictingName.id !== sheetId) {
        throw new SheetNameAlreadyTakenError(sheetDisplayName);
      }
      if (!sheetWithConflictingName.isPlaceholder) {
        throw new SheetNameAlreadyTakenError(sheetDisplayName);
      }
      sheetWithConflictingName.isPlaceholder = false;
      return;
    }
    if (sheetId > this.lastSheetId) {
      this.lastSheetId = sheetId;
    }
    const sheet = new Sheet(sheetId, sheetDisplayName);
    this._storeSheetInMappings(sheet);
  }
  /**
   * Adds a placeholder sheet with the given name if it does not exist yet
   */
  addPlaceholderIfNotExists(sheetName) {
    const sheetWithConflictingName = this._getSheetByName(sheetName, {
      includePlaceholders: true
    });
    if (sheetWithConflictingName) {
      return sheetWithConflictingName.id;
    }
    this.lastSheetId++;
    const sheet = new Sheet(this.lastSheetId, sheetName, true);
    this._storeSheetInMappings(sheet);
    return sheet.id;
  }
  /**
   * Adds a placeholder sheet with a specific ID and name.
   * Used for undo operations to restore previously merged placeholder sheets.
   *
   * @throws {SheetNameAlreadyTakenError} if the sheet with the given name already exists.
   */
  addPlaceholderWithId(sheetId, sheetDisplayName) {
    const sheetWithConflictingName = this._getSheetByName(sheetDisplayName, {
      includePlaceholders: true
    });
    if (sheetWithConflictingName) {
      throw new SheetNameAlreadyTakenError(sheetDisplayName);
    }
    if (this.hasSheetWithId(sheetId, {
      includePlaceholders: true
    })) {
      throw new Error(`Sheet with id ${sheetId} already exists`);
    }
    if (sheetId > this.lastSheetId) {
      this.lastSheetId = sheetId;
    }
    const sheet = new Sheet(sheetId, sheetDisplayName, true);
    this._storeSheetInMappings(sheet);
  }
  /**
   *
   * Removes sheet with given ID.
   * If sheet does not exist, does nothing.
   * @returns {boolean} true if sheet was removed, false if it did not exist.
   */
  removeSheetIfExists(sheetId, options = {}) {
    const sheet = this._getSheet(sheetId, options);
    if (!sheet) {
      return false;
    }
    this.allSheets.delete(sheetId);
    this.mappingFromCanonicalNameToId.delete(sheet.canonicalName);
    if (sheetId === this.lastSheetId) {
      this.lastSheetId--;
    }
    return true;
  }
  /**
   * Marks sheet with given ID as a placeholder.
   * @throws {NoSheetWithIdError} if the sheet with the given ID does not exist
   */
  markSheetAsPlaceholder(sheetId) {
    const sheet = this._getSheetOrThrowError(sheetId, {});
    sheet.isPlaceholder = true;
  }
  /**
   * Renames sheet.
   * - If called with sheetId of a placeholder sheet, throws {NoSheetWithIdError}.
   * - If newDisplayName is conflicting with an existing sheet, throws {SheetNameAlreadyTakenError}.
   * - If newDisplayName is conflicting with a placeholder sheet name, deletes the placeholder sheet and returns its id as mergedWithPlaceholderSheet.
   *
   * @throws {SheetNameAlreadyTakenError} if the sheet with the given name already exists.
   * @throws {NoSheetWithIdError} if the sheet with the given ID does not exist.
   */
  renameSheet(sheetId, newDisplayName) {
    const sheet = this._getSheetOrThrowError(sheetId, {});
    const currentDisplayName = sheet.displayName;
    if (currentDisplayName === newDisplayName) {
      return {
        previousDisplayName: void 0
      };
    }
    const sheetWithConflictingName = this._getSheetByName(newDisplayName, {
      includePlaceholders: true
    });
    let mergedWithPlaceholderSheet = void 0;
    if (sheetWithConflictingName !== void 0 && sheetWithConflictingName.id !== sheet.id) {
      if (!sheetWithConflictingName.isPlaceholder) {
        throw new SheetNameAlreadyTakenError(newDisplayName);
      } else {
        this.mappingFromCanonicalNameToId.delete(sheetWithConflictingName.canonicalName);
        this.allSheets.delete(sheetWithConflictingName.id);
        if (sheetWithConflictingName.id === this.lastSheetId) {
          this.lastSheetId--;
        }
        mergedWithPlaceholderSheet = sheetWithConflictingName.id;
      }
    }
    const currentCanonicalName = sheet.canonicalName;
    this.mappingFromCanonicalNameToId.delete(currentCanonicalName);
    sheet.displayName = newDisplayName;
    this._storeSheetInMappings(sheet);
    return {
      previousDisplayName: currentDisplayName,
      mergedWithPlaceholderSheet
    };
  }
  /**
   * Stores sheet in both internal mappings.
   * - If ID exists, it is updated. If not, it is added.
   * - If canonical name exists, it is updated. If not, it is added.
   *
   * @internal
   */
  _storeSheetInMappings(sheet) {
    this.allSheets.set(sheet.id, sheet);
    this.mappingFromCanonicalNameToId.set(sheet.canonicalName, sheet.id);
  }
  /**
   * Returns sheet by ID
   *
   * @returns {Maybe<Sheet>} the sheet, or undefined if not found.
   * @internal
   */
  _getSheet(sheetId, options) {
    const retrievedSheet = this.allSheets.get(sheetId);
    if (retrievedSheet === void 0) {
      return void 0;
    }
    return options.includePlaceholders || !retrievedSheet.isPlaceholder ? retrievedSheet : void 0;
  }
  /**
   * Returns sheet by name
   *
   * @returns {Maybe<Sheet>} the sheet, or undefined if not found.
   * @internal
   */
  _getSheetByName(sheetName, options) {
    const sheetId = this.mappingFromCanonicalNameToId.get(SheetMapping.canonicalizeSheetName(sheetName));
    if (sheetId === void 0) {
      return void 0;
    }
    return this._getSheet(sheetId, options);
  }
  /**
   * Returns sheet by ID
   *
   * @throws {NoSheetWithIdError} if the sheet with the given ID does not exist.
   * @internal
   */
  _getSheetOrThrowError(sheetId, options) {
    const sheet = this._getSheet(sheetId, options);
    if (sheet === void 0) {
      throw new NoSheetWithIdError(sheetId);
    }
    return sheet;
  }
}
class SheetReferenceRegistrar {
  constructor(sheetMapping, addressMapping) {
    this.sheetMapping = sheetMapping;
    this.addressMapping = addressMapping;
  }
  /**
   * Adds placeholder sheet if it doesn't exist and adds placeholder strategy to address mapping.
   * @returns {number} sheet id
   */
  ensureSheetRegistered(sheetName) {
    const sheetId = this.sheetMapping.addPlaceholderIfNotExists(sheetName);
    this.addressMapping.addSheetStrategyPlaceholderIfNotExists(sheetId);
    return sheetId;
  }
}
function validateAsSheet(sheet) {
  if (!Array.isArray(sheet)) {
    throw new InvalidArgumentsError("an array of arrays.");
  }
  for (let i2 = 0; i2 < sheet.length; i2++) {
    if (!Array.isArray(sheet[i2])) {
      throw new InvalidArgumentsError("an array of arrays.");
    }
  }
}
function findBoundaries(sheet) {
  let width = 0;
  let height = 0;
  let cellsCount = 0;
  for (let currentRow = 0; currentRow < sheet.length; currentRow++) {
    let currentRowWidth = 0;
    for (let currentCol = 0; currentCol < sheet[currentRow].length; currentCol++) {
      const currentValue = sheet[currentRow][currentCol];
      if (currentValue === void 0 || currentValue === null) {
        continue;
      }
      currentRowWidth = currentCol + 1;
      ++cellsCount;
    }
    width = Math.max(width, currentRowWidth);
    if (currentRowWidth > 0) {
      height = currentRow + 1;
    }
  }
  const sheetSize = width * height;
  return {
    height,
    width,
    fill: sheetSize === 0 ? 0 : cellsCount / sheetSize
  };
}
class DependencyGraph {
  constructor(addressMapping, rangeMapping, sheetMapping, arrayMapping, stats, lazilyTransformingAstService, functionRegistry, namedExpressions) {
    this.addressMapping = addressMapping;
    this.rangeMapping = rangeMapping;
    this.sheetMapping = sheetMapping;
    this.arrayMapping = arrayMapping;
    this.stats = stats;
    this.lazilyTransformingAstService = lazilyTransformingAstService;
    this.functionRegistry = functionRegistry;
    this.namedExpressions = namedExpressions;
    this.changes = ContentChanges.empty();
    this.dependencyQueryAddresses = (vertex) => {
      if (vertex instanceof RangeVertex) {
        return this.rangeDependencyQuery(vertex).map(([address, _2]) => address);
      } else {
        const dependenciesResult = this.formulaDependencyQuery(vertex);
        if (dependenciesResult !== void 0) {
          const [address, dependencies] = dependenciesResult;
          return dependencies.map((dependency) => {
            if (dependency instanceof NamedExpressionDependency) {
              return this.namedExpressions.namedExpressionOrPlaceholder(dependency.name, address.sheet).address;
            } else if (isSimpleCellAddress(dependency)) {
              return dependency;
            } else {
              return simpleCellRange(dependency.start, dependency.end);
            }
          });
        } else {
          return [];
        }
      }
    };
    this.dependencyQueryVertices = (vertex) => {
      if (vertex instanceof RangeVertex) {
        return this.rangeDependencyQuery(vertex);
      } else {
        const dependenciesResult = this.formulaDependencyQuery(vertex);
        if (dependenciesResult !== void 0) {
          const [address, dependencies] = dependenciesResult;
          return dependencies.map((dependency) => {
            if (dependency instanceof AbsoluteCellRange) {
              return [dependency.start, this.rangeMapping.getVertexOrThrow(dependency.start, dependency.end)];
            } else if (dependency instanceof NamedExpressionDependency) {
              const namedExpression = this.namedExpressions.namedExpressionOrPlaceholder(dependency.name, address.sheet);
              return [namedExpression.address, this.addressMapping.getCellOrThrow(namedExpression.address)];
            } else {
              return [dependency, this.addressMapping.getCellOrThrow(dependency)];
            }
          });
        } else {
          return [];
        }
      }
    };
    this.rangeDependencyQuery = (vertex) => {
      const allDeps = [];
      const {
        smallerRangeVertex,
        restRange
      } = this.rangeMapping.findSmallerRange(vertex.range);
      let range;
      if (smallerRangeVertex !== void 0 && this.graph.adjacentNodes(smallerRangeVertex).has(vertex)) {
        range = restRange;
        allDeps.push([new AbsoluteCellRange(smallerRangeVertex.start, smallerRangeVertex.end), smallerRangeVertex]);
      } else {
        range = vertex.range;
      }
      for (const address of range.addresses(this)) {
        const cell = this.addressMapping.getCell(address);
        if (cell !== void 0) {
          allDeps.push([address, cell]);
        }
      }
      return allDeps;
    };
    this.formulaDependencyQuery = (vertex) => {
      let formula;
      let address;
      if (vertex instanceof FormulaVertex) {
        address = vertex.getAddress(this.lazilyTransformingAstService);
        formula = vertex.getFormula(this.lazilyTransformingAstService);
      } else {
        return void 0;
      }
      const deps = collectDependencies(formula, this.functionRegistry);
      return [address, absolutizeDependencies(deps, address)];
    };
    this.graph = new Graph2(this.dependencyQueryVertices);
    this.sheetReferenceRegistrar = new SheetReferenceRegistrar(sheetMapping, addressMapping);
  }
  /**
   * Invariants:
   * - empty cell has associated EmptyCellVertex if and only if it is a dependency (possibly indirect, through range) to some formula
   */
  static buildEmpty(lazilyTransformingAstService, config, functionRegistry, namedExpressions, stats) {
    return new DependencyGraph(new AddressMapping(config.chooseAddressMappingPolicy), new RangeMapping(), new SheetMapping(config.translationPackage), new ArrayMapping(), stats, lazilyTransformingAstService, functionRegistry, namedExpressions);
  }
  setFormulaToCell(address, ast, dependencies, size, hasVolatileFunction, hasStructuralChangeFunction) {
    const newVertex = FormulaVertex.fromAst(ast, address, size, this.lazilyTransformingAstService.version());
    this.exchangeOrAddFormulaVertex(newVertex);
    this.processCellDependencies(dependencies, newVertex);
    this.graph.markNodeAsDirty(newVertex);
    if (hasVolatileFunction) {
      this.markAsVolatile(newVertex);
    }
    if (hasStructuralChangeFunction) {
      this.markAsDependentOnStructureChange(newVertex);
    }
    this.correctInfiniteRangesDependency(address);
    return this.getAndClearContentChanges();
  }
  setParsingErrorToCell(address, errorVertex) {
    const vertex = this.shrinkPossibleArrayAndGetCell(address);
    this.exchangeOrAddGraphNode(vertex, errorVertex);
    this.addressMapping.setCell(address, errorVertex);
    this.graph.markNodeAsDirty(errorVertex);
    this.correctInfiniteRangesDependency(address);
    return this.getAndClearContentChanges();
  }
  setValueToCell(address, value) {
    const vertex = this.shrinkPossibleArrayAndGetCell(address);
    if (vertex instanceof ArrayFormulaVertex) {
      this.arrayMapping.removeArray(vertex.getRange());
    }
    if (vertex instanceof ValueCellVertex) {
      const oldValues = vertex.getValues();
      if (oldValues.rawValue !== value.rawValue) {
        vertex.setValues(value);
        this.graph.markNodeAsDirty(vertex);
      }
    } else {
      const newVertex = new ValueCellVertex(value.parsedValue, value.rawValue);
      this.exchangeOrAddGraphNode(vertex, newVertex);
      this.addressMapping.setCell(address, newVertex);
      this.graph.markNodeAsDirty(newVertex);
    }
    this.correctInfiniteRangesDependency(address);
    return this.getAndClearContentChanges();
  }
  /**
   * Sets a cell empty.
   * - if vertex has no dependents, removes it from graph, address mapping and range mapping and cleans up its dependencies
   * - if vertex has dependents, exchanges it for an EmptyCellVertex and marks it as dirty
   */
  setCellEmpty(address) {
    const vertex = this.shrinkPossibleArrayAndGetCell(address);
    if (vertex === void 0) {
      return ContentChanges.empty();
    }
    if (this.graph.adjacentNodes(vertex).size > 0) {
      const emptyVertex = new EmptyCellVertex();
      this.exchangeGraphNode(vertex, emptyVertex);
      if (this.graph.adjacentNodesCount(emptyVertex) === 0) {
        this.removeVertex(emptyVertex);
        this.addressMapping.removeCell(address);
      } else {
        this.graph.markNodeAsDirty(emptyVertex);
        this.addressMapping.setCell(address, emptyVertex);
      }
    } else {
      this.removeVertex(vertex);
      this.addressMapping.removeCell(address);
    }
    return this.getAndClearContentChanges();
  }
  clearDirtyVertices() {
    this.graph.clearDirtyNodes();
  }
  verticesToRecompute() {
    return this.graph.getDirtyAndVolatileNodes();
  }
  processCellDependencies(cellDependencies, endVertex) {
    const endVertexId = endVertex.idInGraph;
    if (endVertexId === void 0) {
      throw new Error("End vertex not found");
    }
    cellDependencies.forEach((dep) => {
      if (dep instanceof AbsoluteCellRange) {
        const range = dep;
        let rangeVertex = this.getRange(range.start, range.end);
        if (rangeVertex === void 0) {
          rangeVertex = new RangeVertex(range);
          this.rangeMapping.addOrUpdateVertex(rangeVertex);
        }
        this.graph.addNodeIfNotExists(rangeVertex);
        const rangeVertexId = rangeVertex.idInGraph;
        if (rangeVertexId === void 0) {
          throw new Error("Range vertex not found");
        }
        if (!range.isFinite()) {
          this.graph.markNodeAsInfiniteRange(rangeVertexId);
        }
        const {
          smallerRangeVertex,
          restRange
        } = this.rangeMapping.findSmallerRange(range);
        if (smallerRangeVertex !== void 0) {
          this.graph.addEdge(smallerRangeVertex, rangeVertexId);
          if (rangeVertex.bruteForce) {
            rangeVertex.bruteForce = false;
            for (const cellFromRange of range.addresses(this)) {
              this.graph.removeEdge(this.fetchCell(cellFromRange), rangeVertexId);
            }
          }
        } else {
          rangeVertex.bruteForce = true;
        }
        const array = this.arrayMapping.getArray(restRange);
        if (array !== void 0) {
          this.graph.addEdge(array, rangeVertexId);
        } else {
          for (const cellFromRange of restRange.addresses(this)) {
            const {
              vertex,
              id
            } = this.fetchCellOrCreateEmpty(cellFromRange);
            this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, rangeVertexId);
          }
        }
        this.graph.addEdge(rangeVertexId, endVertexId);
        if (range.isFinite()) {
          this.correctInfiniteRangesDependenciesByRangeVertex(rangeVertex);
        }
      } else if (dep instanceof NamedExpressionDependency) {
        const sheetOfVertex = endVertex.getAddress(this.lazilyTransformingAstService).sheet;
        const {
          vertex,
          id
        } = this.fetchNamedExpressionVertex(dep.name, sheetOfVertex);
        this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, endVertexId);
      } else {
        const {
          vertex,
          id
        } = this.fetchCellOrCreateEmpty(dep);
        this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, endVertexId);
      }
    });
  }
  fetchNamedExpressionVertex(expressionName, sheetId) {
    const namedExpression = this.namedExpressions.namedExpressionOrPlaceholder(expressionName, sheetId);
    return this.fetchCellOrCreateEmpty(namedExpression.address);
  }
  exchangeNode(addressFrom, addressTo) {
    const vertexFrom = this.fetchCellOrCreateEmpty(addressFrom).vertex;
    const vertexTo = this.fetchCellOrCreateEmpty(addressTo).vertex;
    this.addressMapping.removeCell(addressFrom);
    this.exchangeGraphNode(vertexFrom, vertexTo);
  }
  fetchCellOrCreateEmpty(address) {
    const existingVertex = this.addressMapping.getCell(address);
    if (existingVertex !== void 0) {
      return {
        vertex: existingVertex,
        id: void 0
      };
    }
    const newVertex = new EmptyCellVertex();
    const newVertexId = this.graph.addNodeIfNotExists(newVertex);
    this.addressMapping.setCell(address, newVertex);
    return {
      vertex: newVertex,
      id: newVertexId
    };
  }
  removeRows(removedRows) {
    this.stats.measure(StatType.ADJUSTING_GRAPH, () => {
      for (const [address, vertex] of this.addressMapping.entriesFromRowsSpan(removedRows)) {
        for (const adjacentNode of this.graph.adjacentNodes(vertex)) {
          this.graph.markNodeAsDirty(adjacentNode);
        }
        if (vertex instanceof ArrayFormulaVertex) {
          if (vertex.isLeftCorner(address)) {
            this.shrinkArrayToCorner(vertex);
            this.arrayMapping.removeArray(vertex.getRange());
          } else {
            continue;
          }
        }
        this.removeVertex(vertex);
      }
    });
    this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
      this.addressMapping.removeRows(removedRows);
    });
    const affectedArrays = this.stats.measure(StatType.ADJUSTING_RANGES, () => {
      const affectedRanges = this.truncateRanges(removedRows, (address) => address.row);
      return this.getArrayVerticesRelatedToRanges(affectedRanges);
    });
    this.stats.measure(StatType.ADJUSTING_ARRAY_MAPPING, () => {
      this.fixArraysAfterRemovingRows(removedRows.sheet, removedRows.rowStart, removedRows.numberOfRows);
    });
    this.addStructuralNodesToChangeSet();
    return {
      affectedArrays,
      contentChanges: this.getAndClearContentChanges()
    };
  }
  /**
   * Adds a new sheet to the graph.
   * If the sheetId was a placeholder sheet, marks its vertices as dirty.
   */
  addSheet(sheetId) {
    this.addressMapping.addSheetOrChangeStrategy(sheetId, findBoundaries([]));
    this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
      this.markAllCellsAsDirtyInSheet(sheetId);
    });
    this.stats.measure(StatType.ADJUSTING_RANGES, () => {
      this.markAllRangesAsDirtyInSheet(sheetId);
    });
  }
  /**
   * Removes all vertices without dependents in other sheets from address mapping, range mapping and array mapping.
   * - If nothing is left, removes the sheet from sheet mapping and address mapping.
   * - Otherwise, marks it as placeholder.
   */
  removeSheet(sheetId) {
    this.clearSheet(sheetId);
    const addressMappingCleared = !this.addressMapping.hasAnyEntries(sheetId);
    const rangeMappingCleared = this.rangeMapping.getNumberOfRangesInSheet(sheetId) === 0;
    if (addressMappingCleared && rangeMappingCleared) {
      this.sheetMapping.removeSheetIfExists(sheetId);
      this.addressMapping.removeSheetIfExists(sheetId);
    } else {
      this.sheetMapping.markSheetAsPlaceholder(sheetId);
    }
  }
  /**
   * Removes placeholderSheetToDelete and reroutes edges to the corresponding vertices in sheetToKeep
   *
   * Assumptions about placeholderSheetToDelete:
   * - is empty (contains only empty cell vertices and range vertices),
   * - empty cell vertices have no dependencies,
   * - range vertices have dependencies only in placeholderSheetToDelete,
   * - vertices may have dependents in placeholderSheetToDelete and other sheets,
   */
  mergeSheets(sheetToKeep, placeholderSheetToDelete) {
    if (!this.isPlaceholder(placeholderSheetToDelete)) {
      throw new Error(`Cannot merge sheets: sheet ${placeholderSheetToDelete} is not a placeholder`);
    }
    this.mergeRangeVertices(sheetToKeep, placeholderSheetToDelete);
    this.mergeCellVertices(sheetToKeep, placeholderSheetToDelete);
    this.addressMapping.removeSheetIfExists(placeholderSheetToDelete);
    this.addStructuralNodesToChangeSet();
  }
  /**
   * Clears the sheet content.
   * - removes all cell vertices without dependents
   * - removes all array vertices
   * - for vertices with dependents, exchanges them for EmptyCellVertex and marks them as dirty
   */
  clearSheet(sheetId) {
    const arrays = /* @__PURE__ */ new Set();
    for (const [address, vertex] of this.addressMapping.sheetEntries(sheetId)) {
      if (vertex instanceof ArrayFormulaVertex) {
        arrays.add(vertex);
      } else {
        this.setCellEmpty(address);
      }
    }
    for (const array of arrays.values()) {
      this.setArrayEmpty(array);
    }
    this.addStructuralNodesToChangeSet();
  }
  removeColumns(removedColumns) {
    this.stats.measure(StatType.ADJUSTING_GRAPH, () => {
      for (const [address, vertex] of this.addressMapping.entriesFromColumnsSpan(removedColumns)) {
        for (const adjacentNode of this.graph.adjacentNodes(vertex)) {
          this.graph.markNodeAsDirty(adjacentNode);
        }
        if (vertex instanceof ArrayFormulaVertex) {
          if (vertex.isLeftCorner(address)) {
            this.shrinkArrayToCorner(vertex);
            this.arrayMapping.removeArray(vertex.getRange());
          } else {
            continue;
          }
        }
        this.removeVertex(vertex);
      }
    });
    this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
      this.addressMapping.removeColumns(removedColumns);
    });
    const affectedArrays = this.stats.measure(StatType.ADJUSTING_RANGES, () => {
      const affectedRanges = this.truncateRanges(removedColumns, (address) => address.col);
      return this.getArrayVerticesRelatedToRanges(affectedRanges);
    });
    this.stats.measure(StatType.ADJUSTING_ARRAY_MAPPING, () => {
      return this.fixArraysAfterRemovingColumns(removedColumns.sheet, removedColumns.columnStart, removedColumns.numberOfColumns);
    });
    this.addStructuralNodesToChangeSet();
    return {
      affectedArrays,
      contentChanges: this.getAndClearContentChanges()
    };
  }
  addRows(addedRows) {
    this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
      this.addressMapping.addRows(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
    });
    const affectedArrays = this.stats.measure(StatType.ADJUSTING_RANGES, () => {
      const result = this.rangeMapping.moveAllRangesInSheetAfterAddingRows(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
      this.fixRangesWhenAddingRows(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
      return this.getArrayVerticesRelatedToRanges(result.verticesWithChangedSize);
    });
    this.stats.measure(StatType.ADJUSTING_ARRAY_MAPPING, () => {
      this.fixArraysAfterAddingRow(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
    });
    for (const vertex of this.addressMapping.verticesFromRowsSpan(addedRows)) {
      this.graph.markNodeAsDirty(vertex);
    }
    this.addStructuralNodesToChangeSet();
    return {
      affectedArrays
    };
  }
  addColumns(addedColumns) {
    this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
      this.addressMapping.addColumns(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
    });
    const affectedArrays = this.stats.measure(StatType.ADJUSTING_RANGES, () => {
      const result = this.rangeMapping.moveAllRangesInSheetAfterAddingColumns(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
      this.fixRangesWhenAddingColumns(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
      return this.getArrayVerticesRelatedToRanges(result.verticesWithChangedSize);
    });
    this.stats.measure(StatType.ADJUSTING_ARRAY_MAPPING, () => {
      return this.fixArraysAfterAddingColumn(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
    });
    for (const vertex of this.addressMapping.verticesFromColumnsSpan(addedColumns)) {
      this.graph.markNodeAsDirty(vertex);
    }
    this.addStructuralNodesToChangeSet();
    return {
      affectedArrays,
      contentChanges: this.getAndClearContentChanges()
    };
  }
  isThereSpaceForArray(arrayVertex) {
    const range = arrayVertex.getRangeOrUndef();
    if (range === void 0) {
      return false;
    }
    for (const address of range.addresses(this)) {
      const vertexUnderAddress = this.addressMapping.getCell(address);
      if (vertexUnderAddress !== void 0 && !(vertexUnderAddress instanceof EmptyCellVertex) && vertexUnderAddress !== arrayVertex) {
        return false;
      }
    }
    return true;
  }
  moveCells(sourceRange, toRight, toBottom, toSheet) {
    for (const sourceAddress of sourceRange.addressesWithDirection(toRight, toBottom, this)) {
      const targetAddress = simpleCellAddress(toSheet, sourceAddress.col + toRight, sourceAddress.row + toBottom);
      let sourceVertex = this.addressMapping.getCell(sourceAddress);
      const targetVertex = this.addressMapping.getCell(targetAddress);
      this.addressMapping.removeCell(sourceAddress);
      if (sourceVertex !== void 0) {
        this.graph.markNodeAsDirty(sourceVertex);
        this.addressMapping.setCell(targetAddress, sourceVertex);
        let emptyVertex = void 0;
        for (const adjacentNode of this.graph.adjacentNodes(sourceVertex)) {
          if (adjacentNode instanceof RangeVertex && !sourceRange.containsRange(adjacentNode.range)) {
            emptyVertex = emptyVertex !== null && emptyVertex !== void 0 ? emptyVertex : this.fetchCellOrCreateEmpty(sourceAddress).vertex;
            this.graph.addEdge(emptyVertex, adjacentNode);
            this.graph.removeEdge(sourceVertex, adjacentNode);
          }
        }
        if (emptyVertex) {
          this.graph.markNodeAsDirty(emptyVertex);
          this.addressMapping.setCell(sourceAddress, emptyVertex);
        }
      }
      if (targetVertex !== void 0) {
        if (sourceVertex === void 0) {
          this.addressMapping.removeCell(targetAddress);
        }
        for (const adjacentNode of this.graph.adjacentNodes(targetVertex)) {
          sourceVertex = sourceVertex !== null && sourceVertex !== void 0 ? sourceVertex : this.fetchCellOrCreateEmpty(targetAddress).vertex;
          this.graph.addEdge(sourceVertex, adjacentNode);
          this.graph.markNodeAsDirty(sourceVertex);
        }
        this.removeVertex(targetVertex);
      }
    }
    for (const rangeVertex of this.rangeMapping.rangeVerticesContainedInRange(sourceRange)) {
      for (const adjacentNode of this.graph.adjacentNodes(rangeVertex)) {
        if (adjacentNode instanceof RangeVertex && !sourceRange.containsRange(adjacentNode.range)) {
          this.graph.removeEdge(rangeVertex, adjacentNode);
          for (const address of rangeVertex.range.addresses(this)) {
            const {
              vertex,
              id
            } = this.fetchCellOrCreateEmpty(address);
            this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, adjacentNode);
            this.addressMapping.setCell(address, vertex);
            this.graph.markNodeAsDirty(vertex);
          }
        }
      }
    }
    this.rangeMapping.moveRangesInsideSourceRange(sourceRange, toRight, toBottom, toSheet);
  }
  /**
   * Sets an array empty.
   * - removes all corresponding entries from address mapping
   * - reroutes the edges
   * - removes vertex from graph and cleans up its dependencies
   * - removes vertex from range mapping and array mapping
   */
  setArrayEmpty(arrayVertex) {
    const arrayRange = AbsoluteCellRange.spanFrom(arrayVertex.getAddress(this.lazilyTransformingAstService), arrayVertex.width, arrayVertex.height);
    const dependentVertices = this.graph.adjacentNodes(arrayVertex);
    for (const address of arrayRange.addresses(this)) {
      this.addressMapping.removeCell(address);
    }
    for (const adjacentNode of dependentVertices.values()) {
      const nodeDependencies = collectAddressesDependentToRange(this.functionRegistry, adjacentNode, arrayVertex.getRange(), this.lazilyTransformingAstService, this);
      for (const address of nodeDependencies) {
        const {
          vertex,
          id
        } = this.fetchCellOrCreateEmpty(address);
        this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, adjacentNode);
      }
      if (nodeDependencies.length > 0) {
        this.graph.markNodeAsDirty(adjacentNode);
      }
    }
    this.removeVertex(arrayVertex);
    this.arrayMapping.removeArray(arrayVertex.getRange());
  }
  addVertex(address, vertex) {
    this.graph.addNodeIfNotExists(vertex);
    this.addressMapping.setCell(address, vertex);
  }
  addArrayVertex(address, vertex) {
    this.graph.addNodeIfNotExists(vertex);
    this.setAddressMappingForArrayVertex(vertex, address);
  }
  /**
   * Iterator over all array formula nodes in the graph.
   */
  *arrayFormulaNodes() {
    for (const vertex of this.graph.getNodes()) {
      if (vertex instanceof ArrayFormulaVertex) {
        yield vertex;
      }
    }
  }
  *entriesFromRowsSpan(rowsSpan) {
    yield* this.addressMapping.entriesFromRowsSpan(rowsSpan);
  }
  *entriesFromColumnsSpan(columnsSpan) {
    yield* this.addressMapping.entriesFromColumnsSpan(columnsSpan);
  }
  fetchCell(address) {
    return this.addressMapping.getCellOrThrow(address);
  }
  /**
   * Gets the cell vertex at the specified address.
   * @throws {NoSheetWithIdError} if sheet doesn't exist
   */
  getCell(address) {
    return this.addressMapping.getCell(address, {
      throwIfSheetNotExists: true
    });
  }
  getCellValue(address) {
    if (this.isPlaceholder(address.sheet)) {
      return new CellError(ErrorType.REF, ErrorMessage.SheetRef);
    }
    return this.addressMapping.getCellValue(address);
  }
  getRawValue(address) {
    if (this.isPlaceholder(address.sheet)) {
      return null;
    }
    return this.addressMapping.getRawValue(address);
  }
  getScalarValue(address) {
    if (this.isPlaceholder(address.sheet)) {
      return new CellError(ErrorType.REF, ErrorMessage.SheetRef);
    }
    const value = this.addressMapping.getCellValue(address);
    if (value instanceof SimpleRangeValue) {
      return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
    }
    return value;
  }
  existsEdge(fromNode, toNode) {
    return this.graph.existsEdge(fromNode, toNode);
  }
  getSheetId(sheetName) {
    return this.sheetMapping.getSheetIdOrThrowError(sheetName);
  }
  getSheetHeight(sheet) {
    return this.addressMapping.getSheetHeight(sheet);
  }
  getSheetWidth(sheet) {
    return this.addressMapping.getSheetWidth(sheet);
  }
  getArray(range) {
    return this.arrayMapping.getArray(range);
  }
  getRange(start, end) {
    return this.rangeMapping.getRangeVertex(start, end);
  }
  topSortWithScc() {
    return this.graph.topSortWithScc();
  }
  markAsVolatile(vertex) {
    this.graph.markNodeAsVolatile(vertex);
  }
  markAsDependentOnStructureChange(vertex) {
    this.graph.markNodeAsChangingWithStructure(vertex);
  }
  forceApplyPostponedTransformations() {
    for (const vertex of this.graph.getNodes()) {
      if (vertex instanceof ScalarFormulaVertex) {
        vertex.ensureRecentData(this.lazilyTransformingAstService);
      }
    }
  }
  *rawValuesFromRange(range) {
    for (const address of range.addresses(this)) {
      const value = this.getScalarValue(address);
      if (value !== EmptyValue) {
        yield [getRawValue(value), address];
      }
    }
  }
  computeListOfValuesInRange(range) {
    const values2 = [];
    for (const cellFromRange of range.addresses(this)) {
      const value = this.getScalarValue(cellFromRange);
      values2.push(value);
    }
    return values2;
  }
  shrinkArrayToCorner(array) {
    this.cleanAddressMappingUnderArray(array);
    for (const adjacentVertex of this.adjacentArrayVertices(array)) {
      let relevantDependencies;
      if (adjacentVertex instanceof FormulaVertex) {
        relevantDependencies = this.formulaDirectDependenciesToArray(adjacentVertex, array);
      } else {
        relevantDependencies = this.rangeDirectDependenciesToArray(adjacentVertex, array);
      }
      let dependentToCorner = false;
      for (const [address, vertex] of relevantDependencies) {
        if (array.isLeftCorner(address)) {
          dependentToCorner = true;
        }
        this.graph.addEdge(vertex, adjacentVertex);
        this.graph.markNodeAsDirty(vertex);
      }
      if (!dependentToCorner) {
        this.graph.removeEdge(array, adjacentVertex);
      }
    }
    this.graph.markNodeAsDirty(array);
  }
  isArrayInternalCell(address) {
    const vertex = this.getCell(address);
    return vertex instanceof ArrayFormulaVertex && !vertex.isLeftCorner(address);
  }
  getAndClearContentChanges() {
    const changes = this.changes;
    this.changes = ContentChanges.empty();
    return changes;
  }
  getAdjacentNodesAddresses(inputVertex) {
    const deps = this.graph.adjacentNodes(inputVertex);
    const ret = [];
    deps.forEach((vertex) => {
      if (vertex instanceof RangeVertex) {
        ret.push(simpleCellRange(vertex.start, vertex.end));
      } else if (vertex instanceof FormulaVertex) {
        ret.push(vertex.getAddress(this.lazilyTransformingAstService));
      }
    });
    return ret;
  }
  /**
   * Marks all cell vertices in the sheet as dirty.
   */
  markAllCellsAsDirtyInSheet(sheetId) {
    const sheetCells = this.addressMapping.sheetEntries(sheetId);
    for (const [, vertex] of sheetCells) {
      this.graph.markNodeAsDirty(vertex);
    }
  }
  /**
   * Marks all range vertices in the sheet as dirty.
   */
  markAllRangesAsDirtyInSheet(sheetId) {
    const sheetRanges = this.rangeMapping.rangesInSheet(sheetId);
    for (const vertex of sheetRanges) {
      this.graph.markNodeAsDirty(vertex);
    }
  }
  /**
   * For each range vertex in placeholderSheetToDelete:
   * - reroutes dependencies and dependents of range vertex to the corresponding vertex in sheetToKeep
   * - removes range vertex from graph and range mapping
   * - cleans up dependencies of the removed vertex
   */
  mergeRangeVertices(sheetToKeep, placeholderSheetToDelete) {
    const rangeVertices = Array.from(this.rangeMapping.rangesInSheet(placeholderSheetToDelete));
    for (const vertexToDelete of rangeVertices) {
      if (!this.graph.hasNode(vertexToDelete)) {
        continue;
      }
      const start = vertexToDelete.start;
      const end = vertexToDelete.end;
      if (start.sheet !== placeholderSheetToDelete && end.sheet !== placeholderSheetToDelete) {
        continue;
      }
      const targetStart = simpleCellAddress(sheetToKeep, start.col, start.row);
      const targetEnd = simpleCellAddress(sheetToKeep, end.col, end.row);
      const vertexToKeep = this.rangeMapping.getRangeVertex(targetStart, targetEnd);
      if (vertexToKeep) {
        this.rerouteDependents(vertexToDelete, vertexToKeep);
        this.removeVertexAndRerouteDependencies(vertexToDelete, vertexToKeep);
        this.rangeMapping.removeVertexIfExists(vertexToDelete);
        this.graph.markNodeAsDirty(vertexToKeep);
      } else {
        this.rangeMapping.removeVertexIfExists(vertexToDelete);
        vertexToDelete.range.moveToSheet(sheetToKeep);
        this.rangeMapping.addOrUpdateVertex(vertexToDelete);
        this.graph.markNodeAsDirty(vertexToDelete);
      }
    }
  }
  /**
   * For each cell vertex in placeholderSheetToDelete:
   * - reroutes dependents of cell vertex to the corresponding vertex in sheetToKeep
   * - removes cell vertex from graph and address mapping
   * - cleans up dependencies of the removed vertex
   */
  mergeCellVertices(sheetToKeep, placeholderSheetToDelete) {
    const cellVertices = Array.from(this.addressMapping.sheetEntries(placeholderSheetToDelete));
    for (const [addressToDelete, vertexToDelete] of cellVertices) {
      const addressToKeep = simpleCellAddress(sheetToKeep, addressToDelete.col, addressToDelete.row);
      const vertexToKeep = this.getCell(addressToKeep);
      if (vertexToKeep) {
        this.rerouteDependents(vertexToDelete, vertexToKeep);
        this.removeVertexAndCleanupDependencies(vertexToDelete);
        this.addressMapping.removeCell(addressToDelete);
        this.graph.markNodeAsDirty(vertexToKeep);
      } else {
        this.addressMapping.moveCell(addressToDelete, addressToKeep);
        this.graph.markNodeAsDirty(vertexToDelete);
      }
    }
  }
  /**
   * Checks if the given sheet ID refers to a placeholder sheet (doesn't exist but is referenced by other sheets)
   */
  isPlaceholder(sheetId) {
    return sheetId !== NamedExpressions.SHEET_FOR_WORKBOOK_EXPRESSIONS && !this.sheetMapping.hasSheetWithId(sheetId, {
      includePlaceholders: false
    });
  }
  exchangeGraphNode(oldNode, newNode) {
    this.graph.addNodeIfNotExists(newNode);
    const adjNodesStored = this.graph.adjacentNodes(oldNode);
    this.removeVertex(oldNode);
    adjNodesStored.forEach((adjacentNode) => {
      if (this.graph.hasNode(adjacentNode)) {
        this.graph.addEdge(newNode, adjacentNode);
      }
    });
  }
  setArray(range, vertex) {
    this.arrayMapping.setArray(range, vertex);
  }
  correctInfiniteRangesDependency(address) {
    const relevantInfiniteRanges = this.graph.getInfiniteRanges().filter((node) => node.range.addressInRange(address));
    if (relevantInfiniteRanges.length <= 0) {
      return;
    }
    const {
      vertex,
      id: maybeVertexId
    } = this.fetchCellOrCreateEmpty(address);
    const vertexId = maybeVertexId !== null && maybeVertexId !== void 0 ? maybeVertexId : vertex.idInGraph;
    if (vertexId === void 0) {
      throw new Error("Vertex not found");
    }
    relevantInfiniteRanges.forEach((node) => {
      this.graph.addEdge(vertexId, node);
    });
  }
  exchangeOrAddGraphNode(oldNode, newNode) {
    if (oldNode) {
      this.exchangeGraphNode(oldNode, newNode);
    } else {
      this.graph.addNodeIfNotExists(newNode);
    }
  }
  getArrayVerticesRelatedToRanges(ranges) {
    const arrayVertices = /* @__PURE__ */ new Set();
    ranges.forEach((range) => {
      if (!this.graph.hasNode(range)) {
        return;
      }
      this.graph.adjacentNodes(range).forEach((adjacentVertex) => {
        if (adjacentVertex instanceof ArrayFormulaVertex) {
          arrayVertices.add(adjacentVertex);
        }
      });
    });
    return arrayVertices;
  }
  correctInfiniteRangesDependenciesByRangeVertex(vertex) {
    this.graph.getInfiniteRanges().forEach((infiniteRangeVertex) => {
      const intersection = vertex.range.intersectionWith(infiniteRangeVertex.range);
      if (intersection === void 0) {
        return;
      }
      intersection.addresses(this).forEach((address) => {
        const {
          vertex: vertex2,
          id
        } = this.fetchCellOrCreateEmpty(address);
        this.graph.addEdge(id !== null && id !== void 0 ? id : vertex2, infiniteRangeVertex);
      });
    });
  }
  cleanAddressMappingUnderArray(vertex) {
    const arrayRange = vertex.getRange();
    for (const address of arrayRange.addresses(this)) {
      const oldValue = vertex.getArrayCellValue(address);
      if (this.getCell(address) === vertex) {
        if (vertex.isLeftCorner(address)) {
          this.changes.addChange(new CellError(ErrorType.REF), address, oldValue);
        } else {
          this.addressMapping.removeCell(address);
          this.changes.addChange(EmptyValue, address, oldValue);
        }
      } else {
        this.changes.addChange(EmptyValue, address, oldValue);
      }
    }
  }
  *formulaDirectDependenciesToArray(vertex, array) {
    var _a2;
    const [, formulaDependencies] = (_a2 = this.formulaDependencyQuery(vertex)) !== null && _a2 !== void 0 ? _a2 : [];
    if (formulaDependencies === void 0) {
      return;
    }
    for (const dependency of formulaDependencies) {
      if (dependency instanceof NamedExpressionDependency || dependency instanceof AbsoluteCellRange) {
        continue;
      }
      if (array.getRange().addressInRange(dependency)) {
        const vertex2 = this.fetchCellOrCreateEmpty(dependency).vertex;
        yield [dependency, vertex2];
      }
    }
  }
  *rangeDirectDependenciesToArray(vertex, array) {
    const {
      restRange: range
    } = this.rangeMapping.findSmallerRange(vertex.range);
    for (const address of range.addresses(this)) {
      if (array.getRange().addressInRange(address)) {
        const cell = this.fetchCellOrCreateEmpty(address).vertex;
        yield [address, cell];
      }
    }
  }
  *adjacentArrayVertices(vertex) {
    const adjacentNodes = this.graph.adjacentNodes(vertex);
    for (const item of adjacentNodes) {
      if (item instanceof FormulaVertex || item instanceof RangeVertex) {
        yield item;
      }
    }
  }
  addStructuralNodesToChangeSet() {
    this.graph.markChangingWithStructureNodesAsDirty();
  }
  fixRangesWhenAddingRows(sheet, row, numberOfRows) {
    const originalValues = Array.from(this.rangeMapping.rangesInSheet(sheet));
    for (const rangeVertex of originalValues) {
      if (rangeVertex.range.includesRow(row + numberOfRows)) {
        if (rangeVertex.bruteForce) {
          const addedSubrangeInThatRange = rangeVertex.range.rangeWithSameWidth(row, numberOfRows);
          for (const address of addedSubrangeInThatRange.addresses(this)) {
            const {
              vertex,
              id
            } = this.fetchCellOrCreateEmpty(address);
            this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, rangeVertex);
          }
        } else {
          let currentRangeVertex = rangeVertex;
          let find2 = this.rangeMapping.findSmallerRange(currentRangeVertex.range);
          if (find2.smallerRangeVertex !== void 0) {
            continue;
          }
          while (find2.smallerRangeVertex === void 0) {
            const newRangeVertex = new RangeVertex(AbsoluteCellRange.spanFrom(currentRangeVertex.range.start, currentRangeVertex.range.width(), currentRangeVertex.range.height() - 1));
            this.rangeMapping.addOrUpdateVertex(newRangeVertex);
            this.graph.addNodeIfNotExists(newRangeVertex);
            const restRange = new AbsoluteCellRange(simpleCellAddress(currentRangeVertex.range.start.sheet, currentRangeVertex.range.start.col, currentRangeVertex.range.end.row), currentRangeVertex.range.end);
            this.addAllFromRange(restRange, currentRangeVertex);
            this.graph.addEdge(newRangeVertex, currentRangeVertex);
            currentRangeVertex = newRangeVertex;
            find2 = this.rangeMapping.findSmallerRange(currentRangeVertex.range);
          }
          this.graph.addEdge(find2.smallerRangeVertex, currentRangeVertex);
          this.addAllFromRange(find2.restRange, currentRangeVertex);
          this.graph.removeEdge(find2.smallerRangeVertex, rangeVertex);
        }
      }
    }
  }
  addAllFromRange(range, rangeVertex) {
    for (const address of range.addresses(this)) {
      const {
        vertex,
        id
      } = this.fetchCellOrCreateEmpty(address);
      this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, rangeVertex);
    }
  }
  fixRangesWhenAddingColumns(sheet, column, numberOfColumns) {
    for (const rangeVertex of this.rangeMapping.rangesInSheet(sheet)) {
      if (rangeVertex.range.includesColumn(column + numberOfColumns)) {
        let subrange;
        if (rangeVertex.bruteForce) {
          subrange = rangeVertex.range.rangeWithSameHeight(column, numberOfColumns);
        } else {
          subrange = AbsoluteCellRange.spanFrom(simpleCellAddress(sheet, column, rangeVertex.range.end.row), numberOfColumns, 1);
        }
        for (const address of subrange.addresses(this)) {
          const {
            vertex,
            id
          } = this.fetchCellOrCreateEmpty(address);
          this.graph.addEdge(id !== null && id !== void 0 ? id : vertex, rangeVertex);
        }
      }
    }
  }
  exchangeOrAddFormulaVertex(vertex) {
    const address = vertex.getAddress(this.lazilyTransformingAstService);
    const range = AbsoluteCellRange.spanFrom(address, vertex.width, vertex.height);
    const oldNode = this.shrinkPossibleArrayAndGetCell(address);
    if (vertex instanceof ArrayFormulaVertex) {
      this.setArray(range, vertex);
    }
    this.exchangeOrAddGraphNode(oldNode, vertex);
    this.addressMapping.setCell(address, vertex);
    if (vertex instanceof ArrayFormulaVertex) {
      if (!this.isThereSpaceForArray(vertex)) {
        return;
      }
      for (const cellAddress of range.addresses(this)) {
        if (vertex.isLeftCorner(cellAddress)) {
          continue;
        }
        const old = this.getCell(cellAddress);
        this.exchangeOrAddGraphNode(old, vertex);
      }
    }
    for (const cellAddress of range.addresses(this)) {
      this.addressMapping.setCell(cellAddress, vertex);
    }
  }
  setAddressMappingForArrayVertex(vertex, formulaAddress) {
    this.addressMapping.setCell(formulaAddress, vertex);
    if (!(vertex instanceof ArrayFormulaVertex)) {
      return;
    }
    const range = AbsoluteCellRange.spanFromOrUndef(formulaAddress, vertex.width, vertex.height);
    if (range === void 0) {
      return;
    }
    this.setArray(range, vertex);
    if (!this.isThereSpaceForArray(vertex)) {
      return;
    }
    for (const address of range.addresses(this)) {
      this.addressMapping.setCell(address, vertex);
    }
  }
  truncateRanges(span, coordinate) {
    const {
      verticesToRemove,
      verticesToMerge,
      verticesWithChangedSize
    } = this.rangeMapping.truncateRanges(span, coordinate);
    for (const [existingVertex, mergedVertex] of verticesToMerge) {
      this.rerouteDependents(mergedVertex, existingVertex);
      this.removeVertexAndCleanupDependencies(mergedVertex);
    }
    for (const rangeVertex of verticesToRemove) {
      this.removeVertexAndCleanupDependencies(rangeVertex);
    }
    return verticesWithChangedSize;
  }
  fixArraysAfterAddingRow(sheet, rowStart, numberOfRows) {
    this.arrayMapping.moveArrayVerticesAfterRowByRows(sheet, rowStart, numberOfRows);
    if (rowStart <= 0) {
      return;
    }
    for (const [, array] of this.arrayMapping.arraysInRows(RowsSpan.fromRowStartAndEnd(sheet, rowStart - 1, rowStart - 1))) {
      const arrayRange = array.getRange();
      for (let col = arrayRange.start.col; col <= arrayRange.end.col; ++col) {
        for (let row = rowStart; row <= arrayRange.end.row; ++row) {
          const destination = simpleCellAddress(sheet, col, row);
          const source = simpleCellAddress(sheet, col, row + numberOfRows);
          const value = array.getArrayCellValue(destination);
          this.addressMapping.moveCell(source, destination);
          this.changes.addChange(EmptyValue, source, value);
        }
      }
    }
  }
  fixArraysAfterRemovingRows(sheet, rowStart, numberOfRows) {
    this.arrayMapping.moveArrayVerticesAfterRowByRows(sheet, rowStart, -numberOfRows);
    if (rowStart <= 0) {
      return;
    }
    for (const [, array] of this.arrayMapping.arraysInRows(RowsSpan.fromRowStartAndEnd(sheet, rowStart - 1, rowStart - 1))) {
      if (this.isThereSpaceForArray(array)) {
        for (const address of array.getRange().addresses(this)) {
          this.addressMapping.setCell(address, array);
        }
      } else {
        this.setNoSpaceIfArray(array);
      }
    }
  }
  fixArraysAfterAddingColumn(sheet, columnStart, numberOfColumns) {
    this.arrayMapping.moveArrayVerticesAfterColumnByColumns(sheet, columnStart, numberOfColumns);
    if (columnStart <= 0) {
      return;
    }
    for (const [, array] of this.arrayMapping.arraysInCols(ColumnsSpan.fromColumnStartAndEnd(sheet, columnStart - 1, columnStart - 1))) {
      const arrayRange = array.getRange();
      for (let row = arrayRange.start.row; row <= arrayRange.end.row; ++row) {
        for (let col = columnStart; col <= arrayRange.end.col; ++col) {
          const destination = simpleCellAddress(sheet, col, row);
          const source = simpleCellAddress(sheet, col + numberOfColumns, row);
          const value = array.getArrayCellValue(destination);
          this.addressMapping.moveCell(source, destination);
          this.changes.addChange(EmptyValue, source, value);
        }
      }
    }
  }
  fixArraysAfterRemovingColumns(sheet, columnStart, numberOfColumns) {
    this.arrayMapping.moveArrayVerticesAfterColumnByColumns(sheet, columnStart, -numberOfColumns);
    if (columnStart <= 0) {
      return;
    }
    for (const [, array] of this.arrayMapping.arraysInCols(ColumnsSpan.fromColumnStartAndEnd(sheet, columnStart - 1, columnStart - 1))) {
      if (this.isThereSpaceForArray(array)) {
        for (const address of array.getRange().addresses(this)) {
          this.addressMapping.setCell(address, array);
        }
      } else {
        this.setNoSpaceIfArray(array);
      }
    }
  }
  shrinkPossibleArrayAndGetCell(address) {
    const vertex = this.getCell(address);
    if (!(vertex instanceof ArrayFormulaVertex)) {
      return vertex;
    }
    this.setNoSpaceIfArray(vertex);
    return this.getCell(address);
  }
  setNoSpaceIfArray(vertex) {
    if (vertex instanceof ArrayFormulaVertex) {
      this.shrinkArrayToCorner(vertex);
      vertex.setNoSpace();
    }
  }
  /**
   * Removes a vertex from the graph and range mapping and cleans up its dependencies.
   */
  removeVertex(vertex) {
    this.removeVertexAndCleanupDependencies(vertex);
    if (vertex instanceof RangeVertex) {
      this.rangeMapping.removeVertexIfExists(vertex);
    }
  }
  /**
   * Reroutes dependent vertices of source to target. Also removes the edge target -> source if it exists.
   */
  rerouteDependents(source, target) {
    const dependents = this.graph.adjacentNodes(source);
    this.graph.removeEdgeIfExists(target, source);
    dependents.forEach((adjacentNode) => {
      if (this.graph.hasNode(adjacentNode)) {
        this.graph.addEdge(target, adjacentNode);
      }
    });
  }
  /**
   * Removes a vertex from graph and reroutes its dependencies to other vertex. Also removes the edge vertexToKeep -> vertexToDelete if it exists.
   */
  removeVertexAndRerouteDependencies(vertexToDelete, vertexToKeep) {
    const dependencies = this.graph.removeNode(vertexToDelete);
    this.graph.removeEdgeIfExists(vertexToKeep, vertexToDelete);
    dependencies.forEach(([_2, dependency]) => {
      if (this.graph.hasNode(dependency)) {
        this.graph.addEdge(dependency, vertexToKeep);
      }
    });
  }
  /**
   * Removes a vertex from graph and cleans up its dependencies.
   * Dependency clean up = remove all RangeVertex and EmptyCellVertex dependencies if no other vertex depends on them.
   * Also cleans up placeholder sheets that have no remaining vertices (not needed anymore)
   */
  removeVertexAndCleanupDependencies(inputVertex) {
    const dependencies = new Set(this.graph.removeNode(inputVertex));
    const affectedSheets = /* @__PURE__ */ new Set();
    while (dependencies.size > 0) {
      const dependency = dependencies.values().next().value;
      dependencies.delete(dependency);
      const [address, vertex] = dependency;
      if (this.graph.hasNode(vertex) && this.graph.adjacentNodesCount(vertex) === 0) {
        if (vertex instanceof RangeVertex || vertex instanceof EmptyCellVertex) {
          this.graph.removeNode(vertex).forEach((candidate) => dependencies.add(candidate));
        }
        if (vertex instanceof RangeVertex) {
          this.rangeMapping.removeVertexIfExists(vertex);
          affectedSheets.add(vertex.sheet);
        } else if (vertex instanceof EmptyCellVertex && isSimpleCellAddress(address)) {
          this.addressMapping.removeCell(address);
          affectedSheets.add(address.sheet);
        }
      }
    }
    this.cleanupPlaceholderSheets(affectedSheets);
  }
  /**
   * Removes placeholder sheets that have no remaining vertices.
   */
  cleanupPlaceholderSheets(sheetIds) {
    for (const sheetId of sheetIds) {
      if (this.isPlaceholder(sheetId) && !this.addressMapping.hasAnyEntries(sheetId) && this.rangeMapping.getNumberOfRangesInSheet(sheetId) === 0) {
        this.sheetMapping.removeSheetIfExists(sheetId, {
          includePlaceholders: true
        });
        this.addressMapping.removeSheetIfExists(sheetId);
      }
    }
  }
}
class EmptyCellVertex extends CellVertex {
  constructor() {
    super();
  }
  /**
   * Retrieves cell value bound to that singleton
   */
  getCellValue() {
    return EmptyValue;
  }
}
class ValueCellVertex extends CellVertex {
  /** Static cell value. */
  constructor(parsedValue, rawValue) {
    super();
    this.parsedValue = parsedValue;
    this.rawValue = rawValue;
  }
  getValues() {
    return {
      parsedValue: this.parsedValue,
      rawValue: this.rawValue
    };
  }
  setValues(values2) {
    this.parsedValue = values2.parsedValue;
    this.rawValue = values2.rawValue;
  }
  /**
   * Returns cell value stored in vertex
   */
  getCellValue() {
    return this.parsedValue;
  }
}
class ParsingErrorVertex extends CellVertex {
  /**
   * Constructor
   */
  constructor(errors, rawInput) {
    super();
    this.errors = errors;
    this.rawInput = rawInput;
  }
  /**
   * Returns the value of the cell.
   */
  getCellValue() {
    const firstNonemptyMessage = this.errors.map((error) => error.message).find((msg) => msg);
    return CellError.parsingError(firstNonemptyMessage);
  }
  /**
   * Returns the formula of the cell.
   */
  getFormula() {
    return this.rawInput;
  }
}
class SparseStrategy {
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.mapping = /* @__PURE__ */ new Map();
  }
  /** @inheritDoc */
  getCell(address) {
    var _a2;
    return (_a2 = this.mapping.get(address.col)) === null || _a2 === void 0 ? void 0 : _a2.get(address.row);
  }
  /** @inheritDoc */
  setCell(address, newVertex) {
    this.width = Math.max(this.width, address.col + 1);
    this.height = Math.max(this.height, address.row + 1);
    let colMapping = this.mapping.get(address.col);
    if (!colMapping) {
      colMapping = /* @__PURE__ */ new Map();
      this.mapping.set(address.col, colMapping);
    }
    colMapping.set(address.row, newVertex);
  }
  /** @inheritDoc */
  has(address) {
    var _a2;
    return !!((_a2 = this.mapping.get(address.col)) === null || _a2 === void 0 ? void 0 : _a2.get(address.row));
  }
  /** @inheritDoc */
  getHeight() {
    return this.height;
  }
  /** @inheritDoc */
  getWidth() {
    return this.width;
  }
  removeCell(address) {
    var _a2;
    (_a2 = this.mapping.get(address.col)) === null || _a2 === void 0 ? void 0 : _a2.delete(address.row);
  }
  addRows(row, numberOfRows) {
    this.mapping.forEach((rowMapping) => {
      const tmpMapping = /* @__PURE__ */ new Map();
      rowMapping.forEach((vertex, rowNumber) => {
        if (rowNumber >= row) {
          tmpMapping.set(rowNumber + numberOfRows, vertex);
          rowMapping.delete(rowNumber);
        }
      });
      tmpMapping.forEach((vertex, rowNumber) => {
        rowMapping.set(rowNumber, vertex);
      });
    });
    this.height += numberOfRows;
  }
  addColumns(column, numberOfColumns) {
    const tmpMapping = /* @__PURE__ */ new Map();
    this.mapping.forEach((rowMapping, colNumber) => {
      if (colNumber >= column) {
        tmpMapping.set(colNumber + numberOfColumns, rowMapping);
        this.mapping.delete(colNumber);
      }
    });
    tmpMapping.forEach((rowMapping, colNumber) => {
      this.mapping.set(colNumber, rowMapping);
    });
    this.width += numberOfColumns;
  }
  removeRows(removedRows) {
    this.mapping.forEach((rowMapping) => {
      const tmpMapping = /* @__PURE__ */ new Map();
      rowMapping.forEach((vertex, rowNumber) => {
        if (rowNumber >= removedRows.rowStart) {
          rowMapping.delete(rowNumber);
          if (rowNumber > removedRows.rowEnd) {
            tmpMapping.set(rowNumber - removedRows.numberOfRows, vertex);
          }
        }
      });
      tmpMapping.forEach((vertex, rowNumber) => {
        rowMapping.set(rowNumber, vertex);
      });
    });
    const rightmostRowRemoved = Math.min(this.height - 1, removedRows.rowEnd);
    const numberOfRowsRemoved = Math.max(0, rightmostRowRemoved - removedRows.rowStart + 1);
    this.height = Math.max(0, this.height - numberOfRowsRemoved);
  }
  removeColumns(removedColumns) {
    const tmpMapping = /* @__PURE__ */ new Map();
    this.mapping.forEach((rowMapping, colNumber) => {
      if (colNumber >= removedColumns.columnStart) {
        this.mapping.delete(colNumber);
        if (colNumber > removedColumns.columnEnd) {
          tmpMapping.set(colNumber - removedColumns.numberOfColumns, rowMapping);
        }
      }
    });
    tmpMapping.forEach((rowMapping, colNumber) => {
      this.mapping.set(colNumber, rowMapping);
    });
    const rightmostColumnRemoved = Math.min(this.width - 1, removedColumns.columnEnd);
    const numberOfColumnsRemoved = Math.max(0, rightmostColumnRemoved - removedColumns.columnStart + 1);
    this.width = Math.max(0, this.width - numberOfColumnsRemoved);
  }
  *getEntries(sheet) {
    for (const [colNumber, col] of this.mapping) {
      for (const [rowNumber, value] of col) {
        yield [simpleCellAddress(sheet, colNumber, rowNumber), value];
      }
    }
  }
  *verticesFromColumn(column) {
    const colMapping = this.mapping.get(column);
    if (colMapping === void 0) {
      return;
    }
    for (const [_2, vertex] of colMapping) {
      yield vertex;
    }
  }
  *verticesFromRow(row) {
    for (const colMapping of this.mapping.values()) {
      const rowVertex = colMapping.get(row);
      if (rowVertex !== void 0) {
        yield rowVertex;
      }
    }
  }
  *verticesFromColumnsSpan(columnsSpan) {
    for (const column of columnsSpan.columns()) {
      const colMapping = this.mapping.get(column);
      if (colMapping === void 0) {
        continue;
      }
      for (const [_2, vertex] of colMapping) {
        yield vertex;
      }
    }
  }
  *verticesFromRowsSpan(rowsSpan) {
    for (const colMapping of this.mapping.values()) {
      for (const row of rowsSpan.rows()) {
        const rowVertex = colMapping.get(row);
        if (rowVertex !== void 0) {
          yield rowVertex;
        }
      }
    }
  }
  *entriesFromRowsSpan(rowsSpan) {
    for (const [col, colMapping] of this.mapping.entries()) {
      for (const row of rowsSpan.rows()) {
        const rowVertex = colMapping.get(row);
        if (rowVertex !== void 0) {
          yield [simpleCellAddress(rowsSpan.sheet, col, row), rowVertex];
        }
      }
    }
  }
  *entriesFromColumnsSpan(columnsSpan) {
    for (const col of columnsSpan.columns()) {
      const colMapping = this.mapping.get(col);
      if (colMapping !== void 0) {
        for (const [row, vertex] of colMapping.entries()) {
          yield [simpleCellAddress(columnsSpan.sheet, col, row), vertex];
        }
      }
    }
  }
  *vertices() {
    for (const [_2, col] of this.mapping) {
      for (const [_3, value] of col) {
        if (value !== void 0) {
          yield value;
        }
      }
    }
  }
}
class DenseStrategy {
  /**
   * @param width - width of the stored sheet
   * @param height - height of the stored sheet
   */
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.mapping = new Array(height);
    for (let i2 = 0; i2 < height; i2++) {
      this.mapping[i2] = new Array(width);
    }
  }
  /** @inheritDoc */
  getCell(address) {
    return this.getCellVertex(address.col, address.row);
  }
  /** @inheritDoc */
  setCell(address, newVertex) {
    this.width = Math.max(this.width, address.col + 1);
    this.height = Math.max(this.height, address.row + 1);
    const rowMapping = this.mapping[address.row];
    if (!rowMapping) {
      this.mapping[address.row] = new Array(this.width);
    }
    this.mapping[address.row][address.col] = newVertex;
  }
  /** @inheritDoc */
  has(address) {
    const row = this.mapping[address.row];
    if (!row) {
      return false;
    }
    return !!row[address.col];
  }
  /** @inheritDoc */
  getHeight() {
    return this.height;
  }
  /** @inheritDoc */
  getWidth() {
    return this.width;
  }
  removeCell(address) {
    if (this.mapping[address.row] !== void 0) {
      delete this.mapping[address.row][address.col];
    }
  }
  addRows(row, numberOfRows) {
    const newRows = [];
    for (let i2 = 0; i2 < numberOfRows; i2++) {
      newRows.push(new Array(this.width));
    }
    this.mapping.splice(row, 0, ...newRows);
    this.height += numberOfRows;
  }
  addColumns(column, numberOfColumns) {
    for (let i2 = 0; i2 < this.height; i2++) {
      if (this.mapping[i2] !== void 0) {
        this.mapping[i2].splice(column, 0, ...new Array(numberOfColumns));
      }
    }
    this.width += numberOfColumns;
  }
  removeRows(removedRows) {
    this.mapping.splice(removedRows.rowStart, removedRows.numberOfRows);
    const rightmostRowRemoved = Math.min(this.height - 1, removedRows.rowEnd);
    const numberOfRowsRemoved = Math.max(0, rightmostRowRemoved - removedRows.rowStart + 1);
    this.height = Math.max(0, this.height - numberOfRowsRemoved);
  }
  removeColumns(removedColumns) {
    for (let i2 = 0; i2 < this.height; i2++) {
      if (this.mapping[i2] !== void 0) {
        this.mapping[i2].splice(removedColumns.columnStart, removedColumns.numberOfColumns);
      }
    }
    const rightmostColumnRemoved = Math.min(this.width - 1, removedColumns.columnEnd);
    const numberOfColumnsRemoved = Math.max(0, rightmostColumnRemoved - removedColumns.columnStart + 1);
    this.width = Math.max(0, this.width - numberOfColumnsRemoved);
  }
  *getEntries(sheet) {
    for (let y2 = 0; y2 < this.height; ++y2) {
      for (let x2 = 0; x2 < this.width; ++x2) {
        const vertex = this.getCellVertex(x2, y2);
        if (vertex) {
          yield [simpleCellAddress(sheet, x2, y2), vertex];
        }
      }
    }
  }
  *verticesFromColumn(column) {
    for (let y2 = 0; y2 < this.height; ++y2) {
      const vertex = this.getCellVertex(column, y2);
      if (vertex) {
        yield vertex;
      }
    }
  }
  *verticesFromRow(row) {
    for (let x2 = 0; x2 < this.width; ++x2) {
      const vertex = this.getCellVertex(x2, row);
      if (vertex) {
        yield vertex;
      }
    }
  }
  *verticesFromColumnsSpan(columnsSpan) {
    for (let x2 = columnsSpan.columnStart; x2 <= columnsSpan.columnEnd; ++x2) {
      for (let y2 = 0; y2 < this.height; ++y2) {
        const vertex = this.getCellVertex(x2, y2);
        if (vertex) {
          yield vertex;
        }
      }
    }
  }
  *verticesFromRowsSpan(rowsSpan) {
    for (let x2 = 0; x2 < this.width; ++x2) {
      for (let y2 = rowsSpan.rowStart; y2 <= rowsSpan.rowEnd; ++y2) {
        const vertex = this.getCellVertex(x2, y2);
        if (vertex) {
          yield vertex;
        }
      }
    }
  }
  *entriesFromRowsSpan(rowsSpan) {
    for (let x2 = 0; x2 < this.width; ++x2) {
      for (let y2 = rowsSpan.rowStart; y2 <= rowsSpan.rowEnd; ++y2) {
        const vertex = this.getCellVertex(x2, y2);
        if (vertex) {
          yield [simpleCellAddress(rowsSpan.sheet, x2, y2), vertex];
        }
      }
    }
  }
  *entriesFromColumnsSpan(columnsSpan) {
    for (let x2 = columnsSpan.columnStart; x2 <= columnsSpan.columnEnd; ++x2) {
      for (let y2 = 0; y2 < this.height; ++y2) {
        const vertex = this.getCellVertex(x2, y2);
        if (vertex) {
          yield [simpleCellAddress(columnsSpan.sheet, x2, y2), vertex];
        }
      }
    }
  }
  *vertices() {
    for (let y2 = 0; y2 < this.height; ++y2) {
      for (let x2 = 0; x2 < this.width; ++x2) {
        const vertex = this.getCellVertex(x2, y2);
        if (vertex) {
          yield vertex;
        }
      }
    }
  }
  getCellVertex(x2, y2) {
    var _a2;
    return (_a2 = this.mapping[y2]) === null || _a2 === void 0 ? void 0 : _a2[x2];
  }
}
var ErrorType;
(function(ErrorType2) {
  ErrorType2["DIV_BY_ZERO"] = "DIV_BY_ZERO";
  ErrorType2["NAME"] = "NAME";
  ErrorType2["VALUE"] = "VALUE";
  ErrorType2["NUM"] = "NUM";
  ErrorType2["NA"] = "NA";
  ErrorType2["CYCLE"] = "CYCLE";
  ErrorType2["REF"] = "REF";
  ErrorType2["SPILL"] = "SPILL";
  ErrorType2["LIC"] = "LIC";
  ErrorType2["ERROR"] = "ERROR";
})(ErrorType || (ErrorType = {}));
var CellType;
(function(CellType2) {
  CellType2["FORMULA"] = "FORMULA";
  CellType2["VALUE"] = "VALUE";
  CellType2["ARRAY"] = "ARRAY";
  CellType2["EMPTY"] = "EMPTY";
  CellType2["ARRAYFORMULA"] = "ARRAYFORMULA";
})(CellType || (CellType = {}));
const getCellType = (vertex, address) => {
  if (vertex instanceof ArrayFormulaVertex) {
    if (vertex.isLeftCorner(address)) {
      return CellType.ARRAYFORMULA;
    } else {
      return CellType.ARRAY;
    }
  }
  if (vertex instanceof ScalarFormulaVertex || vertex instanceof ParsingErrorVertex) {
    return CellType.FORMULA;
  }
  if (vertex instanceof ValueCellVertex) {
    return CellType.VALUE;
  }
  return CellType.EMPTY;
};
var CellValueNoNumber;
(function(CellValueNoNumber2) {
  CellValueNoNumber2["EMPTY"] = "EMPTY";
  CellValueNoNumber2["NUMBER"] = "NUMBER";
  CellValueNoNumber2["STRING"] = "STRING";
  CellValueNoNumber2["BOOLEAN"] = "BOOLEAN";
  CellValueNoNumber2["ERROR"] = "ERROR";
})(CellValueNoNumber || (CellValueNoNumber = {}));
var CellValueJustNumber;
(function(CellValueJustNumber2) {
  CellValueJustNumber2["NUMBER"] = "NUMBER";
})(CellValueJustNumber || (CellValueJustNumber = {}));
const CellValueType = Object.assign(Object.assign({}, CellValueNoNumber), CellValueJustNumber);
const CellValueDetailedType = Object.assign(Object.assign({}, CellValueNoNumber), NumberType);
const CellValueTypeOrd = (arg) => {
  switch (arg) {
    case CellValueType.EMPTY:
      return 0;
    case CellValueType.NUMBER:
      return 1;
    case CellValueType.STRING:
      return 2;
    case CellValueType.BOOLEAN:
      return 3;
    case CellValueType.ERROR:
      return 4;
  }
  throw new Error("Cell value not computed");
};
const getCellValueType = (cellValue) => {
  if (cellValue === EmptyValue) {
    return CellValueType.EMPTY;
  }
  if (cellValue instanceof CellError || cellValue instanceof SimpleRangeValue) {
    return CellValueType.ERROR;
  }
  if (typeof cellValue === "string") {
    return CellValueType.STRING;
  } else if (isExtendedNumber(cellValue)) {
    return CellValueType.NUMBER;
  } else if (typeof cellValue === "boolean") {
    return CellValueType.BOOLEAN;
  }
  throw new Error("Cell value not computed");
};
const getCellValueDetailedType = (cellValue) => {
  if (isExtendedNumber(cellValue)) {
    return getTypeOfExtendedNumber(cellValue);
  } else {
    return getCellValueType(cellValue);
  }
};
const getCellValueFormat = (cellValue) => {
  if (isExtendedNumber(cellValue)) {
    return getFormatOfExtendedNumber(cellValue);
  } else {
    return void 0;
  }
};
class CellError {
  constructor(type, message, root) {
    this.type = type;
    this.message = message;
    this.root = root;
  }
  /**
   * Returns a CellError with a given message.
   * @param {string} detailedMessage - message to be displayed
   */
  static parsingError(detailedMessage) {
    return new CellError(ErrorType.ERROR, `${ErrorMessage.ParseError}${detailedMessage ? " " + detailedMessage : ""}`);
  }
  attachRootVertex(vertex) {
    if (this.root === void 0) {
      return new CellError(this.type, this.message, vertex);
    } else {
      return this;
    }
  }
}
const simpleRowAddress = (sheet, row) => ({
  sheet,
  row
});
const invalidSimpleRowAddress = (address) => address.row < 0;
const simpleColumnAddress = (sheet, col) => ({
  sheet,
  col
});
const invalidSimpleColumnAddress = (address) => address.col < 0;
const simpleCellAddress = (sheet, col, row) => ({
  sheet,
  col,
  row
});
const isColOrRowInvalid = (address) => address.col < 0 || address.row < 0;
const movedSimpleCellAddress = (address, toSheet, toRight, toBottom) => {
  return simpleCellAddress(toSheet, address.col + toRight, address.row + toBottom);
};
const addressKey = (address) => `${address.sheet},${address.row},${address.col}`;
function isSimpleCellAddress(obj) {
  var _a2, _b, _c;
  return obj && (typeof obj === "object" || typeof obj === "function") && typeof ((_a2 = obj) === null || _a2 === void 0 ? void 0 : _a2.sheet) === "number" && typeof ((_b = obj) === null || _b === void 0 ? void 0 : _b.col) === "number" && typeof ((_c = obj) === null || _c === void 0 ? void 0 : _c.row) === "number";
}
const absoluteSheetReference = (address, baseAddress) => {
  var _a2;
  return (_a2 = address.sheet) !== null && _a2 !== void 0 ? _a2 : baseAddress.sheet;
};
const equalSimpleCellAddress = (left, right) => {
  return left.sheet === right.sheet && left.col === right.col && left.row === right.row;
};
const WRONG_RANGE_SIZE = "AbsoluteCellRange: Wrong range size";
function isSimpleCellRange(val) {
  if (val && (typeof val === "object" || typeof val === "function")) {
    const obj = val;
    return "start" in obj && isSimpleCellAddress(obj.start) && "end" in obj && isSimpleCellAddress(obj.end);
  } else {
    return false;
  }
}
const simpleCellRange = (start, end) => ({
  start,
  end
});
class AbsoluteCellRange {
  constructor(start, end) {
    if (start.sheet !== end.sheet) {
      throw new SheetsNotEqual(start.sheet, end.sheet);
    }
    this.start = simpleCellAddress(start.sheet, start.col, start.row);
    this.end = simpleCellAddress(end.sheet, end.col, end.row);
  }
  get sheet() {
    return this.start.sheet;
  }
  static fromSimpleCellAddresses(start, end) {
    if (start.sheet !== end.sheet) {
      throw new SheetsNotEqual(start.sheet, end.sheet);
    }
    const width = end.col - start.col;
    const height = end.row - start.row;
    if (Number.isFinite(height) && Number.isFinite(width)) {
      return new AbsoluteCellRange(start, end);
    }
    if (Number.isFinite(height)) {
      return new AbsoluteRowRange(start.sheet, start.row, end.row);
    }
    return new AbsoluteColumnRange(start.sheet, start.col, end.col);
  }
  static fromAst(ast, baseAddress) {
    if (ast.type === AstNodeType.CELL_RANGE) {
      return AbsoluteCellRange.fromCellRange(ast, baseAddress);
    } else if (ast.type === AstNodeType.COLUMN_RANGE) {
      return AbsoluteColumnRange.fromColumnRange(ast, baseAddress);
    } else {
      return AbsoluteRowRange.fromRowRangeAst(ast, baseAddress);
    }
  }
  static fromAstOrUndef(ast, baseAddress) {
    try {
      return AbsoluteCellRange.fromAst(ast, baseAddress);
    } catch (_e2) {
      return void 0;
    }
  }
  static fromCellRange(x2, baseAddress) {
    return new AbsoluteCellRange(x2.start.toSimpleCellAddress(baseAddress), x2.end.toSimpleCellAddress(baseAddress));
  }
  static spanFrom(topLeftCorner, width, height) {
    const ret = AbsoluteCellRange.spanFromOrUndef(topLeftCorner, width, height);
    if (ret === void 0) {
      throw new Error(WRONG_RANGE_SIZE);
    }
    return ret;
  }
  static spanFromOrUndef(topLeftCorner, width, height) {
    if (!Number.isFinite(width) && Number.isFinite(height)) {
      if (topLeftCorner.col !== 0) {
        return void 0;
      }
      return new AbsoluteRowRange(topLeftCorner.sheet, topLeftCorner.row, topLeftCorner.row + height - 1);
    } else if (!Number.isFinite(height) && Number.isFinite(width)) {
      if (topLeftCorner.row !== 0) {
        return void 0;
      }
      return new AbsoluteColumnRange(topLeftCorner.sheet, topLeftCorner.col, topLeftCorner.col + width - 1);
    } else if (Number.isFinite(height) && Number.isFinite(width)) {
      return new AbsoluteCellRange(topLeftCorner, simpleCellAddress(topLeftCorner.sheet, topLeftCorner.col + width - 1, topLeftCorner.row + height - 1));
    }
    return void 0;
  }
  static fromCoordinates(sheet, x1, y1, x2, y2) {
    return new AbsoluteCellRange(simpleCellAddress(sheet, x1, y1), simpleCellAddress(sheet, x2, y2));
  }
  isFinite() {
    return Number.isFinite(this.size());
  }
  doesOverlap(other) {
    if (this.start.sheet != other.start.sheet) {
      return false;
    }
    if (this.end.row < other.start.row || this.start.row > other.end.row) {
      return false;
    }
    if (this.end.col < other.start.col || this.start.col > other.end.col) {
      return false;
    }
    return true;
  }
  addressInRange(address) {
    if (this.sheet !== address.sheet) {
      return false;
    }
    return this.start.row <= address.row && this.end.row >= address.row && this.start.col <= address.col && this.end.col >= address.col;
  }
  columnInRange(address) {
    if (this.sheet !== address.sheet) {
      return false;
    }
    return this.start.col <= address.col && this.end.col >= address.col;
  }
  rowInRange(address) {
    if (this.sheet !== address.sheet) {
      return false;
    }
    return this.start.row <= address.row && this.end.row >= address.row;
  }
  containsRange(range) {
    return this.addressInRange(range.start) && this.addressInRange(range.end);
  }
  intersectionWith(other) {
    if (this.sheet !== other.start.sheet) {
      return void 0;
    }
    const startRow = Math.max(this.start.row, other.start.row);
    const endRow = Math.min(this.end.row, other.end.row);
    const startCol = Math.max(this.start.col, other.start.col);
    const endCol = Math.min(this.end.col, other.end.col);
    if (startRow > endRow || startCol > endCol) {
      return void 0;
    }
    return new AbsoluteCellRange(simpleCellAddress(this.sheet, startCol, startRow), simpleCellAddress(this.sheet, endCol, endRow));
  }
  includesRow(row) {
    return this.start.row < row && this.end.row >= row;
  }
  includesColumn(column) {
    return this.start.col < column && this.end.col >= column;
  }
  shiftByRows(numberOfRows) {
    this.start.row += numberOfRows;
    this.end.row += numberOfRows;
  }
  expandByRows(numberOfRows) {
    this.end.row += numberOfRows;
  }
  shiftByColumns(numberOfColumns) {
    this.start.col += numberOfColumns;
    this.end.col += numberOfColumns;
  }
  shifted(byCols, byRows) {
    return AbsoluteCellRange.spanFrom(simpleCellAddress(this.sheet, this.start.col + byCols, this.start.row + byRows), this.width(), this.height());
  }
  expandByColumns(numberOfColumns) {
    this.end.col += numberOfColumns;
  }
  moveToSheet(toSheet) {
    this.start.sheet = toSheet;
    this.end.sheet = toSheet;
  }
  removeSpan(span) {
    if (span instanceof RowsSpan) {
      this.removeRows(span.start, span.end);
    } else {
      this.removeColumns(span.start, span.end);
    }
  }
  shouldBeRemoved() {
    return this.width() <= 0 || this.height() <= 0;
  }
  rangeWithSameWidth(startRow, numberOfRows) {
    return AbsoluteCellRange.spanFrom(simpleCellAddress(this.sheet, this.start.col, startRow), this.width(), numberOfRows);
  }
  rangeWithSameHeight(startColumn, numberOfColumns) {
    return AbsoluteCellRange.spanFrom(simpleCellAddress(this.sheet, startColumn, this.start.row), numberOfColumns, this.height());
  }
  toString() {
    return `${this.start.sheet},${this.start.col},${this.start.row},${this.end.col},${this.end.row}`;
  }
  width() {
    return this.end.col - this.start.col + 1;
  }
  height() {
    return this.end.row - this.start.row + 1;
  }
  size() {
    return this.height() * this.width();
  }
  arrayOfAddressesInRange() {
    const result = [];
    for (let y2 = 0; y2 < this.height(); ++y2) {
      result[y2] = [];
      for (let x2 = 0; x2 < this.width(); ++x2) {
        const value = simpleCellAddress(this.sheet, this.start.col + x2, this.start.row + y2);
        result[y2].push(value);
      }
    }
    return result;
  }
  withStart(newStart) {
    return new AbsoluteCellRange(newStart, this.end);
  }
  sameDimensionsAs(other) {
    return this.width() === other.width() && this.height() === other.height();
  }
  sameAs(other) {
    return equalSimpleCellAddress(this.start, other.start) && equalSimpleCellAddress(this.end, other.end);
  }
  addressesArrayMap(dependencyGraph, op) {
    const ret = [];
    let currentRow = this.start.row;
    while (currentRow <= this.effectiveEndRow(dependencyGraph)) {
      let currentColumn = this.start.col;
      const tmp = [];
      while (currentColumn <= this.effectiveEndColumn(dependencyGraph)) {
        tmp.push(op(simpleCellAddress(this.start.sheet, currentColumn, currentRow)));
        currentColumn++;
      }
      ret.push(tmp);
      currentRow++;
    }
    return ret;
  }
  addresses(dependencyGraph) {
    const ret = [];
    let currentRow = this.start.row;
    const limitRow = this.effectiveEndRow(dependencyGraph);
    const limitColumn = this.effectiveEndColumn(dependencyGraph);
    while (currentRow <= limitRow) {
      let currentColumn = this.start.col;
      while (currentColumn <= limitColumn) {
        ret.push(simpleCellAddress(this.start.sheet, currentColumn, currentRow));
        currentColumn++;
      }
      currentRow++;
    }
    return ret;
  }
  *addressesWithDirection(right, bottom, dependencyGraph) {
    if (right > 0) {
      if (bottom > 0) {
        let currentRow = this.effectiveEndRow(dependencyGraph);
        while (currentRow >= this.start.row) {
          let currentColumn = this.effectiveEndColumn(dependencyGraph);
          while (currentColumn >= this.start.col) {
            yield simpleCellAddress(this.start.sheet, currentColumn, currentRow);
            currentColumn -= 1;
          }
          currentRow -= 1;
        }
      } else {
        let currentRow = this.start.row;
        while (currentRow <= this.effectiveEndRow(dependencyGraph)) {
          let currentColumn = this.effectiveEndColumn(dependencyGraph);
          while (currentColumn >= this.start.col) {
            yield simpleCellAddress(this.start.sheet, currentColumn, currentRow);
            currentColumn -= 1;
          }
          currentRow += 1;
        }
      }
    } else {
      if (bottom > 0) {
        let currentRow = this.effectiveEndRow(dependencyGraph);
        while (currentRow >= this.start.row) {
          let currentColumn = this.start.col;
          while (currentColumn <= this.effectiveEndColumn(dependencyGraph)) {
            yield simpleCellAddress(this.start.sheet, currentColumn, currentRow);
            currentColumn += 1;
          }
          currentRow -= 1;
        }
      } else {
        let currentRow = this.start.row;
        while (currentRow <= this.effectiveEndRow(dependencyGraph)) {
          let currentColumn = this.start.col;
          while (currentColumn <= this.effectiveEndColumn(dependencyGraph)) {
            yield simpleCellAddress(this.start.sheet, currentColumn, currentRow);
            currentColumn += 1;
          }
          currentRow += 1;
        }
      }
    }
  }
  getAddress(col, row) {
    if (col < 0 || row < 0 || row > this.height() - 1 || col > this.width() - 1) {
      throw Error("Index out of bound");
    }
    return simpleCellAddress(this.start.sheet, this.start.col + col, this.start.row + row);
  }
  exceedsSheetSizeLimits(maxColumns, maxRows) {
    return this.end.col >= maxColumns || this.end.row >= maxRows;
  }
  effectiveEndColumn(_dependencyGraph) {
    return this.end.col;
  }
  effectiveEndRow(_dependencyGraph) {
    return this.end.row;
  }
  effectiveWidth(_dependencyGraph) {
    return this.width();
  }
  effectiveHeight(_dependencyGraph) {
    return this.height();
  }
  removeRows(rowStart, rowEnd) {
    if (rowStart > this.end.row) {
      return;
    }
    if (rowEnd < this.start.row) {
      const numberOfRows = rowEnd - rowStart + 1;
      return this.shiftByRows(-numberOfRows);
    }
    if (rowStart <= this.start.row) {
      this.start.row = rowStart;
    }
    this.end.row -= Math.min(rowEnd, this.end.row) - rowStart + 1;
  }
  removeColumns(columnStart, columnEnd) {
    if (columnStart > this.end.col) {
      return;
    }
    if (columnEnd < this.start.col) {
      const numberOfColumns = columnEnd - columnStart + 1;
      return this.shiftByColumns(-numberOfColumns);
    }
    if (columnStart <= this.start.col) {
      this.start.col = columnStart;
    }
    this.end.col -= Math.min(columnEnd, this.end.col) - columnStart + 1;
  }
}
class AbsoluteColumnRange extends AbsoluteCellRange {
  constructor(sheet, columnStart, columnEnd) {
    super(simpleCellAddress(sheet, columnStart, 0), simpleCellAddress(sheet, columnEnd, Number.POSITIVE_INFINITY));
  }
  static fromColumnRange(x2, baseAddress) {
    const start = x2.start.toSimpleColumnAddress(baseAddress);
    const end = x2.end.toSimpleColumnAddress(baseAddress);
    if (start.sheet !== end.sheet) {
      throw new SheetsNotEqual(start.sheet, end.sheet);
    }
    return new AbsoluteColumnRange(start.sheet, start.col, end.col);
  }
  shouldBeRemoved() {
    return this.width() <= 0;
  }
  shiftByRows(_numberOfRows) {
    return;
  }
  expandByRows(_numberOfRows) {
    return;
  }
  shifted(byCols, _byRows) {
    return new AbsoluteColumnRange(this.sheet, this.start.col + byCols, this.end.col + byCols);
  }
  rangeWithSameHeight(startColumn, numberOfColumns) {
    return new AbsoluteColumnRange(this.sheet, startColumn, startColumn + numberOfColumns - 1);
  }
  exceedsSheetSizeLimits(maxColumns, _maxRows) {
    return this.end.col >= maxColumns;
  }
  effectiveEndRow(dependencyGraph) {
    return this.effectiveHeight(dependencyGraph) - 1;
  }
  effectiveHeight(dependencyGraph) {
    return dependencyGraph.getSheetHeight(this.sheet);
  }
  removeRows(_rowStart, _rowEnd) {
    return;
  }
}
class AbsoluteRowRange extends AbsoluteCellRange {
  constructor(sheet, rowStart, rowEnd) {
    super(simpleCellAddress(sheet, 0, rowStart), simpleCellAddress(sheet, Number.POSITIVE_INFINITY, rowEnd));
  }
  static fromRowRangeAst(x2, baseAddress) {
    const start = x2.start.toSimpleRowAddress(baseAddress);
    const end = x2.end.toSimpleRowAddress(baseAddress);
    if (start.sheet !== end.sheet) {
      throw new SheetsNotEqual(start.sheet, end.sheet);
    }
    return new AbsoluteRowRange(start.sheet, start.row, end.row);
  }
  shouldBeRemoved() {
    return this.height() <= 0;
  }
  shiftByColumns(_numberOfColumns) {
    return;
  }
  expandByColumns(_numberOfColumns) {
    return;
  }
  shifted(byCols, byRows) {
    return new AbsoluteRowRange(this.sheet, this.start.row + byRows, this.end.row + byRows);
  }
  rangeWithSameWidth(startRow, numberOfRows) {
    return new AbsoluteRowRange(this.sheet, startRow, startRow + numberOfRows - 1);
  }
  exceedsSheetSizeLimits(_maxColumns, maxRows) {
    return this.end.row >= maxRows;
  }
  effectiveEndColumn(dependencyGraph) {
    return this.effectiveWidth(dependencyGraph) - 1;
  }
  effectiveWidth(dependencyGraph) {
    return dependencyGraph.getSheetWidth(this.sheet);
  }
  removeColumns(_columnStart, _columnEnd) {
    return;
  }
}
class InterpreterState {
  constructor(formulaAddress, arraysFlag, formulaVertex) {
    this.formulaAddress = formulaAddress;
    this.arraysFlag = arraysFlag;
    this.formulaVertex = formulaVertex;
  }
}
function collatorFromConfig(config) {
  const sensitivity = config.caseSensitive ? config.accentSensitive ? "variant" : "case" : config.accentSensitive ? "accent" : "base";
  const caseFirst = config.caseFirst;
  const ignorePunctuation = config.ignorePunctuation;
  return new Intl.Collator(config.localeLang, {
    sensitivity,
    caseFirst,
    ignorePunctuation
  });
}
const COMPLEX_NUMBER_SYMBOL = "i";
const complexParsingRegexp = /^\s*([+-]?)\s*(([\d\.,]+(e[+-]?\d+)?)\s*([ij]?)|([ij]))\s*(([+-])\s*([+-]?)\s*(([\d\.,]+(e[+-]?\d+)?)\s*([ij]?)|([ij])))?$/;
class ArithmeticHelper {
  constructor(config, dateTimeHelper, numberLiteralsHelper) {
    this.config = config;
    this.dateTimeHelper = dateTimeHelper;
    this.numberLiteralsHelper = numberLiteralsHelper;
    this.lt = (left, right) => {
      return this.compare(left, right) < 0;
    };
    this.leq = (left, right) => {
      return this.compare(left, right) <= 0;
    };
    this.gt = (left, right) => {
      return this.compare(left, right) > 0;
    };
    this.geq = (left, right) => {
      return this.compare(left, right) >= 0;
    };
    this.eq = (left, right) => {
      return this.compare(left, right) === 0;
    };
    this.neq = (left, right) => {
      return this.compare(left, right) !== 0;
    };
    this.pow = (left, right) => {
      return Math.pow(getRawValue(left), getRawValue(right));
    };
    this.addWithEpsilonRaw = (left, right) => {
      const ret = left + right;
      if (Math.abs(ret) < this.actualEps * Math.abs(left)) {
        return 0;
      } else {
        return ret;
      }
    };
    this.addWithEpsilon = (left, right) => {
      const typeOfResult = inferExtendedNumberTypeAdditive(left, right);
      return this.ExtendedNumberFactory(this.addWithEpsilonRaw(getRawValue(left), getRawValue(right)), typeOfResult);
    };
    this.unaryMinus = (arg) => {
      return cloneNumber(arg, -getRawValue(arg));
    };
    this.unaryPlus = (arg) => arg;
    this.unaryPercent = (arg) => {
      return new PercentNumber(getRawValue(arg) / 100);
    };
    this.concat = (left, right) => {
      return left.concat(right);
    };
    this.nonstrictadd = (left, right) => {
      if (left instanceof CellError) {
        return left;
      } else if (right instanceof CellError) {
        return right;
      } else if (typeof left === "number") {
        if (typeof right === "number") {
          return this.addWithEpsilonRaw(left, right);
        } else {
          return left;
        }
      } else if (typeof right === "number") {
        return right;
      } else {
        return 0;
      }
    };
    this.subtract = (leftArg, rightArg) => {
      const typeOfResult = inferExtendedNumberTypeAdditive(leftArg, rightArg);
      const left = getRawValue(leftArg);
      const right = getRawValue(rightArg);
      let ret = left - right;
      if (Math.abs(ret) < this.actualEps * Math.abs(left)) {
        ret = 0;
      }
      return this.ExtendedNumberFactory(ret, typeOfResult);
    };
    this.divide = (leftArg, rightArg) => {
      const left = getRawValue(leftArg);
      const right = getRawValue(rightArg);
      if (right === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      } else {
        const typeOfResult = inferExtendedNumberTypeMultiplicative(leftArg, rightArg);
        return this.ExtendedNumberFactory(left / right, typeOfResult);
      }
    };
    this.multiply = (left, right) => {
      const typeOfResult = inferExtendedNumberTypeMultiplicative(left, right);
      return this.ExtendedNumberFactory(getRawValue(left) * getRawValue(right), typeOfResult);
    };
    this.manyToExactComplex = (args) => {
      const ret = [];
      for (const arg of args) {
        if (arg instanceof CellError) {
          return arg;
        } else if (isExtendedNumber(arg) || typeof arg === "string") {
          const coerced = this.coerceScalarToComplex(arg);
          if (!(coerced instanceof CellError)) {
            ret.push(coerced);
          }
        }
      }
      return ret;
    };
    this.coerceNumbersExactRanges = (args) => this.manyToNumbers(args, this.manyToExactNumbers);
    this.coerceNumbersCoerceRangesDropNulls = (args) => this.manyToNumbers(args, this.manyToCoercedNumbersDropNulls);
    this.manyToExactNumbers = (args) => {
      const ret = [];
      for (const arg of args) {
        if (arg instanceof CellError) {
          return arg;
        } else if (isExtendedNumber(arg)) {
          ret.push(getRawValue(arg));
        }
      }
      return ret;
    };
    this.manyToOnlyNumbersDropNulls = (args) => {
      const ret = [];
      for (const arg of args) {
        if (arg instanceof CellError) {
          return arg;
        } else if (isExtendedNumber(arg)) {
          ret.push(getRawValue(arg));
        } else if (arg !== EmptyValue) {
          return new CellError(ErrorType.VALUE, ErrorMessage.NumberExpected);
        }
      }
      return ret;
    };
    this.manyToCoercedNumbersDropNulls = (args) => {
      const ret = [];
      for (const arg of args) {
        if (arg instanceof CellError) {
          return arg;
        }
        if (arg === EmptyValue) {
          continue;
        }
        const coerced = this.coerceScalarToNumberOrError(arg);
        if (isExtendedNumber(coerced)) {
          ret.push(getRawValue(coerced));
        }
      }
      return ret;
    };
    this.collator = collatorFromConfig(config);
    this.actualEps = config.smartRounding ? config.precisionEpsilon : 0;
  }
  eqMatcherFunction(pattern) {
    const regexp = this.buildRegex(pattern);
    return (cellValue) => typeof cellValue === "string" && regexp.test(this.normalizeString(cellValue));
  }
  neqMatcherFunction(pattern) {
    const regexp = this.buildRegex(pattern);
    return (cellValue) => {
      return !(typeof cellValue === "string") || !regexp.test(this.normalizeString(cellValue));
    };
  }
  searchString(pattern, text) {
    var _a2;
    const regexp = this.buildRegex(pattern, false);
    const result = regexp.exec(text);
    return (_a2 = result === null || result === void 0 ? void 0 : result.index) !== null && _a2 !== void 0 ? _a2 : -1;
  }
  requiresRegex(pattern) {
    if (!this.config.useRegularExpressions && !this.config.useWildcards) {
      return !this.config.matchWholeCell;
    }
    for (let i2 = 0; i2 < pattern.length; i2++) {
      const c2 = pattern.charAt(i2);
      if (isWildcard(c2) || this.config.useRegularExpressions && needsEscape(c2)) {
        return true;
      }
    }
    return false;
  }
  floatCmp(leftArg, rightArg) {
    const left = getRawValue(leftArg);
    const right = getRawValue(rightArg);
    const mod2 = 1 + this.actualEps;
    if (right >= 0 && left * mod2 >= right && left <= right * mod2) {
      return 0;
    } else if (right <= 0 && left * mod2 <= right && left >= right * mod2) {
      return 0;
    } else if (left > right) {
      return 1;
    } else {
      return -1;
    }
  }
  coerceScalarToNumberOrError(arg) {
    var _a2;
    if (arg instanceof CellError) {
      return arg;
    }
    return (_a2 = this.coerceToMaybeNumber(arg)) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.VALUE, ErrorMessage.NumberCoercion);
  }
  coerceToMaybeNumber(arg) {
    var _a2;
    return (_a2 = this.coerceNonDateScalarToMaybeNumber(arg)) !== null && _a2 !== void 0 ? _a2 : typeof arg === "string" ? this.dateTimeHelper.dateStringToDateNumber(arg) : void 0;
  }
  coerceNonDateScalarToMaybeNumber(arg) {
    if (arg === EmptyValue) {
      return 0;
    } else if (typeof arg === "string") {
      if (arg === "") {
        return 0;
      }
      const maybePercentNumber = this.coerceStringToMaybePercentNumber(arg);
      if (maybePercentNumber !== void 0) {
        return maybePercentNumber;
      }
      const maybeCurrencyNumber = this.coerceStringToMaybeCurrencyNumber(arg);
      if (maybeCurrencyNumber !== void 0) {
        return maybeCurrencyNumber;
      }
      return this.numberLiteralsHelper.numericStringToMaybeNumber(arg.trim());
    } else if (isExtendedNumber(arg)) {
      return arg;
    } else if (typeof arg === "boolean") {
      return Number(arg);
    } else {
      return void 0;
    }
  }
  coerceStringToMaybePercentNumber(input) {
    const trimmedInput = input.trim();
    if (trimmedInput.endsWith("%")) {
      const numOfPercents = trimmedInput.slice(0, trimmedInput.length - 1).trim();
      const parsedNumOfPercents = this.numberLiteralsHelper.numericStringToMaybeNumber(numOfPercents);
      if (parsedNumOfPercents !== void 0) {
        return new PercentNumber(parsedNumOfPercents / 100);
      }
    }
    return void 0;
  }
  coerceStringToMaybeCurrencyNumber(input) {
    const matchedCurrency = this.currencyMatcher(input.trim());
    if (matchedCurrency !== void 0) {
      const [currencySymbol, currencyValue] = matchedCurrency;
      const parsedCurrencyValue = this.numberLiteralsHelper.numericStringToMaybeNumber(currencyValue);
      if (parsedCurrencyValue !== void 0) {
        return new CurrencyNumber(parsedCurrencyValue, currencySymbol);
      }
    }
    return void 0;
  }
  currencyMatcher(token) {
    for (const currency of this.config.currencySymbol) {
      if (token.startsWith(currency)) {
        return [currency, token.slice(currency.length).trim()];
      }
      if (token.endsWith(currency)) {
        return [currency, token.slice(0, token.length - currency.length).trim()];
      }
    }
    return void 0;
  }
  coerceComplexExactRanges(args) {
    const vals = [];
    for (const arg of args) {
      if (arg instanceof SimpleRangeValue) {
        vals.push(arg);
      } else if (arg !== EmptyValue) {
        const coerced = this.coerceScalarToComplex(arg);
        if (coerced instanceof CellError) {
          return coerced;
        } else {
          vals.push(coerced);
        }
      }
    }
    const expandedVals = [];
    for (const val of vals) {
      if (val instanceof SimpleRangeValue) {
        const arr = this.manyToExactComplex(val.valuesFromTopLeftCorner());
        if (arr instanceof CellError) {
          return arr;
        } else {
          expandedVals.push(...arr);
        }
      } else {
        expandedVals.push(val);
      }
    }
    return expandedVals;
  }
  coerceScalarToComplex(arg) {
    if (arg instanceof CellError) {
      return arg;
    } else if (arg === EmptyValue) {
      return [0, 0];
    } else if (isExtendedNumber(arg)) {
      return [getRawValue(arg), 0];
    } else if (typeof arg === "string") {
      return this.coerceStringToComplex(arg);
    } else {
      return new CellError(ErrorType.NUM, ErrorMessage.ComplexNumberExpected);
    }
  }
  ExtendedNumberFactory(value, typeFormat) {
    const {
      type,
      format: format2
    } = typeFormat;
    switch (type) {
      case NumberType.NUMBER_RAW:
        return value;
      case NumberType.NUMBER_CURRENCY: {
        return new CurrencyNumber(value, format2 !== null && format2 !== void 0 ? format2 : this.config.currencySymbol[0]);
      }
      case NumberType.NUMBER_DATE:
        return new DateNumber(value, format2);
      case NumberType.NUMBER_DATETIME:
        return new DateTimeNumber(value, format2);
      case NumberType.NUMBER_TIME:
        return new TimeNumber(value, format2);
      case NumberType.NUMBER_PERCENT:
        return new PercentNumber(value, format2);
    }
  }
  buildRegex(pattern, matchWholeCell = true) {
    pattern = this.normalizeString(pattern);
    let regexpStr;
    let useWildcards = this.config.useWildcards;
    let useRegularExpressions = this.config.useRegularExpressions;
    if (useRegularExpressions) {
      try {
        RegExp(pattern);
      } catch (e3) {
        useRegularExpressions = false;
        useWildcards = false;
      }
    }
    if (useRegularExpressions) {
      regexpStr = escapeNoCharacters(pattern, this.config.caseSensitive);
    } else if (useWildcards) {
      regexpStr = escapeNonWildcards(pattern, this.config.caseSensitive);
    } else {
      regexpStr = escapeAllCharacters(pattern, this.config.caseSensitive);
    }
    if (this.config.matchWholeCell && matchWholeCell) {
      return RegExp("^(" + regexpStr + ")$");
    } else {
      return RegExp(regexpStr);
    }
  }
  normalizeString(str) {
    if (!this.config.caseSensitive) {
      str = str.toLowerCase();
    }
    if (!this.config.accentSensitive) {
      str = normalizeString(str, "nfd").replace(/[\u0300-\u036f]/g, "");
    }
    return str;
  }
  compare(left, right) {
    if (typeof left === "string" || typeof right === "string") {
      const leftTmp = typeof left === "string" ? this.dateTimeHelper.dateStringToDateNumber(left) : left;
      const rightTmp = typeof right === "string" ? this.dateTimeHelper.dateStringToDateNumber(right) : right;
      if (isExtendedNumber(leftTmp) && isExtendedNumber(rightTmp)) {
        return this.floatCmp(leftTmp, rightTmp);
      }
    }
    if (left === EmptyValue) {
      left = coerceEmptyToValue(right);
    } else if (right === EmptyValue) {
      right = coerceEmptyToValue(left);
    }
    if (typeof left === "string" && typeof right === "string") {
      return this.stringCmp(left, right);
    } else if (typeof left === "boolean" && typeof right === "boolean") {
      return numberCmp(coerceBooleanToNumber(left), coerceBooleanToNumber(right));
    } else if (isExtendedNumber(left) && isExtendedNumber(right)) {
      return this.floatCmp(left, right);
    } else if (left === EmptyValue && right === EmptyValue) {
      return 0;
    } else {
      return numberCmp(CellValueTypeOrd(getCellValueType(left)), CellValueTypeOrd(getCellValueType(right)));
    }
  }
  stringCmp(left, right) {
    return this.collator.compare(left, right);
  }
  manyToNumbers(args, rangeFn) {
    const vals = [];
    for (const arg of args) {
      if (arg instanceof SimpleRangeValue) {
        vals.push(arg);
      } else {
        const coerced = getRawValue(this.coerceScalarToNumberOrError(arg));
        if (coerced instanceof CellError) {
          return coerced;
        } else {
          vals.push(coerced);
        }
      }
    }
    const expandedVals = [];
    for (const val of vals) {
      if (val instanceof SimpleRangeValue) {
        const arr = rangeFn(val.valuesFromTopLeftCorner());
        if (arr instanceof CellError) {
          return arr;
        } else {
          expandedVals.push(...arr);
        }
      } else {
        expandedVals.push(val);
      }
    }
    return expandedVals;
  }
  coerceStringToComplex(arg) {
    const match = complexParsingRegexp.exec(arg);
    if (match === null) {
      return new CellError(ErrorType.NUM, ErrorMessage.ComplexNumberExpected);
    }
    let val1;
    if (match[6] !== void 0) {
      val1 = match[1] === "-" ? [0, -1] : [0, 1];
    } else {
      val1 = this.parseComplexToken(match[1] + match[3], match[5]);
    }
    if (val1 instanceof CellError) {
      return val1;
    }
    if (match[8] === void 0) {
      return val1;
    }
    let val2;
    if (match[14] !== void 0) {
      val2 = match[9] === "-" ? [0, -1] : [0, 1];
    } else {
      val2 = this.parseComplexToken(match[9] + match[11], match[13]);
    }
    if (val2 instanceof CellError) {
      return val2;
    }
    if (match[5] !== "" || match[13] === "") {
      return new CellError(ErrorType.NUM, ErrorMessage.ComplexNumberExpected);
    }
    if (match[8] === "+") {
      return [val1[0] + val2[0], val1[1] + val2[1]];
    } else {
      return [val1[0] - val2[0], val1[1] - val2[1]];
    }
  }
  parseComplexToken(arg, mod2) {
    const val = getRawValue(this.coerceNonDateScalarToMaybeNumber(arg));
    if (val === void 0) {
      return new CellError(ErrorType.NUM, ErrorMessage.ComplexNumberExpected);
    }
    if (mod2 === "") {
      return [val, 0];
    } else {
      return [0, val];
    }
  }
}
function coerceComplexToString([re2, im], symb) {
  if (!isFinite(re2) || !isFinite(im)) {
    return new CellError(ErrorType.NUM, ErrorMessage.NaN);
  }
  symb = symb !== null && symb !== void 0 ? symb : COMPLEX_NUMBER_SYMBOL;
  if (im === 0) {
    return `${re2}`;
  }
  const imStr = `${im === -1 || im === 1 ? "" : Math.abs(im)}${symb}`;
  if (re2 === 0) {
    return `${im < 0 ? "-" : ""}${imStr}`;
  }
  return `${re2}${im < 0 ? "-" : "+"}${imStr}`;
}
function coerceToRange(arg) {
  if (arg instanceof SimpleRangeValue) {
    return arg;
  } else {
    return SimpleRangeValue.fromScalar(arg);
  }
}
function coerceBooleanToNumber(arg) {
  return Number(arg);
}
function coerceEmptyToValue(arg) {
  if (typeof arg === "string") {
    return "";
  } else if (isExtendedNumber(arg)) {
    return 0;
  } else if (typeof arg === "boolean") {
    return false;
  } else {
    return EmptyValue;
  }
}
function coerceScalarToBoolean(arg) {
  if (arg instanceof CellError || typeof arg === "boolean") {
    return arg;
  } else if (arg === EmptyValue) {
    return false;
  } else if (isExtendedNumber(arg)) {
    return getRawValue(arg) !== 0;
  } else {
    const argUppered = arg.toUpperCase();
    if (argUppered === "TRUE") {
      return true;
    } else if (argUppered === "FALSE") {
      return false;
    } else if (argUppered === "") {
      return false;
    } else {
      return void 0;
    }
  }
}
function coerceScalarToString(arg) {
  if (arg instanceof CellError || typeof arg === "string") {
    return arg;
  } else if (arg === EmptyValue) {
    return "";
  } else if (isExtendedNumber(arg)) {
    return getRawValue(arg).toString();
  } else {
    return arg ? "TRUE" : "FALSE";
  }
}
function zeroIfEmpty(arg) {
  return arg === EmptyValue ? 0 : arg;
}
function numberCmp(leftArg, rightArg) {
  const left = getRawValue(leftArg);
  const right = getRawValue(rightArg);
  if (left > right) {
    return 1;
  } else if (left < right) {
    return -1;
  } else {
    return 0;
  }
}
function isNumberOverflow(arg) {
  return isNaN(arg) || arg === Infinity || arg === -Infinity;
}
function fixNegativeZero(arg) {
  if (arg === 0) {
    return 0;
  } else {
    return arg;
  }
}
function isWildcard(c2) {
  return ["*", "?"].includes(c2);
}
const escapedCharacters = ["{", "}", "[", "]", "(", ")", "<", ">", "=", ".", "+", "-", ",", "\\", "$", "^", "!"];
function needsEscape(c2) {
  return escapedCharacters.includes(c2);
}
function escapeNonWildcards(pattern, caseSensitive) {
  let str = "";
  for (let i2 = 0; i2 < pattern.length; i2++) {
    const c2 = pattern.charAt(i2);
    if (c2 === "~") {
      if (i2 == pattern.length - 1) {
        str += "~";
        continue;
      }
      const d2 = pattern.charAt(i2 + 1);
      if (isWildcard(d2) || needsEscape(d2)) {
        str += "\\" + d2;
        i2++;
      } else {
        str += d2;
        i2++;
      }
    } else if (isWildcard(c2)) {
      str += "." + c2;
    } else if (needsEscape(c2)) {
      str += "\\" + c2;
    } else if (caseSensitive) {
      str += c2;
    } else {
      str += c2.toLowerCase();
    }
  }
  return str;
}
function escapeAllCharacters(pattern, caseSensitive) {
  let str = "";
  for (let i2 = 0; i2 < pattern.length; i2++) {
    const c2 = pattern.charAt(i2);
    if (isWildcard(c2) || needsEscape(c2)) {
      str += "\\" + c2;
    } else if (caseSensitive) {
      str += c2;
    } else {
      str += c2.toLowerCase();
    }
  }
  return str;
}
function escapeNoCharacters(pattern, caseSensitive) {
  let str = "";
  for (let i2 = 0; i2 < pattern.length; i2++) {
    const c2 = pattern.charAt(i2);
    if (isWildcard(c2) || needsEscape(c2)) {
      str += c2;
    } else if (caseSensitive) {
      str += c2;
    } else {
      str += c2.toLowerCase();
    }
  }
  return str;
}
function inferExtendedNumberTypeAdditive(leftArg, rightArg) {
  const {
    type: leftType,
    format: leftFormat
  } = getTypeFormatOfExtendedNumber(leftArg);
  const {
    type: rightType,
    format: rightFormat
  } = getTypeFormatOfExtendedNumber(rightArg);
  if (leftType === NumberType.NUMBER_RAW) {
    return {
      type: rightType,
      format: rightFormat
    };
  }
  if (rightType === NumberType.NUMBER_RAW) {
    return {
      type: leftType,
      format: leftFormat
    };
  }
  if ((leftType === NumberType.NUMBER_DATETIME || leftType === NumberType.NUMBER_DATE) && (rightType === NumberType.NUMBER_DATETIME || rightType === NumberType.NUMBER_DATE)) {
    return {
      type: NumberType.NUMBER_RAW
    };
  }
  if (leftType === NumberType.NUMBER_TIME) {
    if (rightType === NumberType.NUMBER_DATE) {
      return {
        type: NumberType.NUMBER_DATETIME,
        format: `${rightFormat} ${leftFormat}`
      };
    }
    if (rightType === NumberType.NUMBER_DATETIME) {
      return {
        type: NumberType.NUMBER_DATETIME,
        format: rightFormat
      };
    }
  }
  if (rightType === NumberType.NUMBER_TIME) {
    if (leftType === NumberType.NUMBER_DATE) {
      return {
        type: NumberType.NUMBER_DATETIME,
        format: `${leftFormat} ${rightFormat}`
      };
    }
    if (leftType === NumberType.NUMBER_DATETIME) {
      return {
        type: NumberType.NUMBER_DATETIME,
        format: leftFormat
      };
    }
  }
  return {
    type: leftType,
    format: leftFormat
  };
}
function inferExtendedNumberTypeMultiplicative(leftArg, rightArg) {
  let {
    type: leftType,
    format: leftFormat
  } = getTypeFormatOfExtendedNumber(leftArg);
  let {
    type: rightType,
    format: rightFormat
  } = getTypeFormatOfExtendedNumber(rightArg);
  if (leftType === NumberType.NUMBER_PERCENT) {
    leftType = NumberType.NUMBER_RAW;
    leftFormat = void 0;
  }
  if (rightType === NumberType.NUMBER_PERCENT) {
    rightType = NumberType.NUMBER_RAW;
    rightFormat = void 0;
  }
  if (leftType === NumberType.NUMBER_RAW) {
    return {
      type: rightType,
      format: rightFormat
    };
  }
  if (rightType === NumberType.NUMBER_RAW) {
    return {
      type: leftType,
      format: leftFormat
    };
  }
  return {
    type: NumberType.NUMBER_RAW
  };
}
function forceNormalizeString(str) {
  return normalizeString(str.toLowerCase(), "nfd").replace(/[\u0300-\u036f]/g, "");
}
function coerceRangeToScalar(arg, state) {
  var _a2;
  if (arg.isAdHoc()) {
    return (_a2 = arg.data[0]) === null || _a2 === void 0 ? void 0 : _a2[0];
  }
  const range = arg.range;
  if (state.formulaAddress.sheet === range.sheet) {
    if (range.width() === 1) {
      const offset = state.formulaAddress.row - range.start.row;
      if (offset >= 0 && offset < range.height()) {
        return arg.data[offset][0];
      }
    } else if (range.height() === 1) {
      const offset = state.formulaAddress.col - range.start.col;
      if (offset >= 0 && offset < range.width()) {
        return arg.data[0][offset];
      }
    }
  }
  return void 0;
}
function normalizeString(str, form) {
  return str.normalize(form.toUpperCase());
}
var FunctionArgumentType;
(function(FunctionArgumentType2) {
  FunctionArgumentType2["STRING"] = "STRING";
  FunctionArgumentType2["NUMBER"] = "NUMBER";
  FunctionArgumentType2["BOOLEAN"] = "BOOLEAN";
  FunctionArgumentType2["SCALAR"] = "SCALAR";
  FunctionArgumentType2["NOERROR"] = "NOERROR";
  FunctionArgumentType2["RANGE"] = "RANGE";
  FunctionArgumentType2["INTEGER"] = "INTEGER";
  FunctionArgumentType2["COMPLEX"] = "COMPLEX";
  FunctionArgumentType2["ANY"] = "ANY";
})(FunctionArgumentType || (FunctionArgumentType = {}));
class FunctionPlugin {
  constructor(interpreter) {
    this.coerceScalarToNumberOrError = (arg) => this.arithmeticHelper.coerceScalarToNumberOrError(arg);
    this.runFunction = (args, state, metadata, functionImplementation) => {
      const evaluatedArguments = this.evaluateArguments(args, state, metadata);
      const argumentValues = evaluatedArguments.map(([value]) => value);
      const argumentIgnorableFlags = evaluatedArguments.map(([, ignorable]) => ignorable);
      const syntacticallyEmptyFlags = evaluatedArguments.map(([, , empty2]) => empty2);
      const argumentMetadata = this.buildMetadataForEachArgumentValue(argumentValues.length, metadata);
      const isVectorizationOn = state.arraysFlag && !metadata.vectorizationForbidden;
      if (!this.isNumberOfArgumentValuesValid(argumentMetadata, argumentValues.length)) {
        return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
      }
      const [resultArrayHeight, resultArrayWidth] = isVectorizationOn ? this.calculateSizeOfVectorizedResultArray(argumentValues, argumentMetadata) : [1, 1];
      if (resultArrayHeight === 1 && resultArrayWidth === 1) {
        const vectorizedArguments = this.vectorizeAndBroadcastArgumentsIfNecessary(isVectorizationOn, argumentValues, argumentMetadata, 0, 0);
        return this.calculateSingleCellOfResultArray(state, vectorizedArguments, argumentMetadata, argumentIgnorableFlags, syntacticallyEmptyFlags, functionImplementation, metadata.returnNumberType);
      }
      const resultArray = [...Array(resultArrayHeight).keys()].map((row) => [...Array(resultArrayWidth).keys()].map((col) => {
        const vectorizedArguments = this.vectorizeAndBroadcastArgumentsIfNecessary(isVectorizationOn, argumentValues, argumentMetadata, row, col);
        const result = this.calculateSingleCellOfResultArray(state, vectorizedArguments, argumentMetadata, argumentIgnorableFlags, syntacticallyEmptyFlags, functionImplementation, metadata.returnNumberType);
        if (result instanceof SimpleRangeValue) {
          throw new Error("Function returning array cannot be vectorized.");
        }
        return result;
      }));
      return SimpleRangeValue.onlyValues(resultArray);
    };
    this.runFunctionWithReferenceArgument = (args, state, metadata, noArgCallback, referenceCallback, nonReferenceCallback = () => new CellError(ErrorType.NA, ErrorMessage.CellRefExpected)) => {
      if (args.length === 0) {
        return this.returnNumberWrapper(noArgCallback(), metadata.returnNumberType);
      } else if (args.length > 1) {
        return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
      }
      let arg = args[0];
      while (arg.type === AstNodeType.PARENTHESIS) {
        arg = arg.expression;
      }
      let cellReference;
      if (arg.type === AstNodeType.CELL_REFERENCE) {
        cellReference = arg.reference.toSimpleCellAddress(state.formulaAddress);
      } else if (arg.type === AstNodeType.CELL_RANGE || arg.type === AstNodeType.COLUMN_RANGE || arg.type === AstNodeType.ROW_RANGE) {
        try {
          cellReference = AbsoluteCellRange.fromAst(arg, state.formulaAddress).start;
        } catch (e3) {
          return new CellError(ErrorType.REF, ErrorMessage.CellRefExpected);
        }
      }
      if (cellReference !== void 0) {
        return this.returnNumberWrapper(referenceCallback(cellReference), metadata.returnNumberType);
      }
      return this.runFunction(args, state, metadata, nonReferenceCallback);
    };
    this.interpreter = interpreter;
    this.dependencyGraph = interpreter.dependencyGraph;
    this.columnSearch = interpreter.columnSearch;
    this.config = interpreter.config;
    this.serialization = interpreter.serialization;
    this.arraySizePredictor = interpreter.arraySizePredictor;
    this.dateTimeHelper = interpreter.dateTimeHelper;
    this.arithmeticHelper = interpreter.arithmeticHelper;
  }
  evaluateAst(ast, state) {
    return this.interpreter.evaluateAst(ast, state);
  }
  arraySizeForAst(ast, state) {
    return this.arraySizePredictor.checkArraySizeForAst(ast, state);
  }
  listOfScalarValues(asts, state) {
    const ret = [];
    for (const argAst of asts) {
      const isSyntacticallyEmpty = argAst.type === AstNodeType.EMPTY;
      const value = this.evaluateAst(argAst, state);
      if (value instanceof SimpleRangeValue) {
        for (const scalarValue of value.valuesFromTopLeftCorner()) {
          ret.push([scalarValue, true, isSyntacticallyEmpty]);
        }
      } else {
        ret.push([value, false, isSyntacticallyEmpty]);
      }
    }
    return ret;
  }
  coerceToType(arg, coercedType, state) {
    let ret;
    if (arg instanceof SimpleRangeValue) {
      switch (coercedType.argumentType) {
        case FunctionArgumentType.RANGE:
        case FunctionArgumentType.ANY:
          ret = arg;
          break;
        default: {
          const coerce = coerceRangeToScalar(arg, state);
          if (coerce === void 0) {
            return void 0;
          }
          arg = coerce;
        }
      }
    }
    if (!(arg instanceof SimpleRangeValue)) {
      switch (coercedType.argumentType) {
        case FunctionArgumentType.INTEGER:
        case FunctionArgumentType.NUMBER:
          const coerced = this.coerceScalarToNumberOrError(arg);
          if (!isExtendedNumber(coerced)) {
            ret = coerced;
            break;
          }
          const value = getRawValue(coerced);
          if (coercedType.maxValue !== void 0 && value > coercedType.maxValue) {
            return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
          }
          if (coercedType.minValue !== void 0 && value < coercedType.minValue) {
            return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
          }
          if (coercedType.lessThan !== void 0 && value >= coercedType.lessThan) {
            return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
          }
          if (coercedType.greaterThan !== void 0 && value <= coercedType.greaterThan) {
            return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
          }
          if (coercedType.argumentType === FunctionArgumentType.INTEGER && !Number.isInteger(value)) {
            return new CellError(ErrorType.NUM, ErrorMessage.IntegerExpected);
          }
          ret = coerced;
          break;
        case FunctionArgumentType.STRING:
          ret = coerceScalarToString(arg);
          break;
        case FunctionArgumentType.BOOLEAN:
          ret = coerceScalarToBoolean(arg);
          break;
        case FunctionArgumentType.SCALAR:
        case FunctionArgumentType.NOERROR:
        case FunctionArgumentType.ANY:
          ret = arg;
          break;
        case FunctionArgumentType.RANGE:
          if (arg instanceof CellError) {
            return arg;
          }
          ret = coerceToRange(arg);
          break;
        case FunctionArgumentType.COMPLEX:
          return this.arithmeticHelper.coerceScalarToComplex(getRawValue(arg));
      }
    }
    if (coercedType.passSubtype || ret === void 0) {
      return ret;
    } else {
      return getRawValue(ret);
    }
  }
  calculateSingleCellOfResultArray(state, vectorizedArguments, argumentsMetadata, argumentIgnorableFlags, syntacticallyEmptyFlags, functionImplementation, returnNumberType) {
    const coercedArguments = this.coerceArgumentsToRequiredTypes(state, vectorizedArguments, argumentsMetadata, argumentIgnorableFlags, syntacticallyEmptyFlags);
    if (coercedArguments instanceof CellError) {
      return coercedArguments;
    }
    const functionCalculationResult = functionImplementation(...coercedArguments);
    return this.returnNumberWrapper(functionCalculationResult, returnNumberType);
  }
  coerceArgumentsToRequiredTypes(state, vectorizedArguments, argumentsMetadata, argumentIgnorableFlags, syntacticallyEmptyFlags) {
    const coercedArguments = [];
    for (let i2 = 0; i2 < argumentsMetadata.length; i2++) {
      const argumentMetadata = argumentsMetadata[i2];
      const rawArg = vectorizedArguments[i2];
      const argumentValue = rawArg === void 0 ? argumentMetadata === null || argumentMetadata === void 0 ? void 0 : argumentMetadata.defaultValue : syntacticallyEmptyFlags[i2] && (argumentMetadata === null || argumentMetadata === void 0 ? void 0 : argumentMetadata.emptyAsDefault) && (argumentMetadata === null || argumentMetadata === void 0 ? void 0 : argumentMetadata.defaultValue) !== void 0 ? argumentMetadata.defaultValue : rawArg;
      if (argumentValue === void 0) {
        coercedArguments.push(void 0);
        continue;
      }
      const coercedValue = this.coerceToType(argumentValue, argumentMetadata, state);
      if (coercedValue === void 0 && !argumentIgnorableFlags[i2]) {
        return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
      }
      if (coercedValue instanceof CellError && argumentMetadata.argumentType !== FunctionArgumentType.SCALAR) {
        return coercedValue;
      }
      coercedArguments.push(coercedValue);
    }
    return coercedArguments;
  }
  vectorizeAndBroadcastArgumentsIfNecessary(isVectorizationOn, argumentValues, argumentMetadata, row, col) {
    return argumentValues.map((value, i2) => isVectorizationOn && this.isRangePassedAsAScalarArgument(value, argumentMetadata[i2]) ? this.vectorizeAndBroadcastRangeArgument(value, row, col) : value);
  }
  vectorizeAndBroadcastRangeArgument(argumentValue, rowNum, colNum) {
    var _a2;
    const targetRowNum = argumentValue.height() === 1 ? 0 : rowNum;
    const targetColNum = argumentValue.width() === 1 ? 0 : colNum;
    return (_a2 = argumentValue.data[targetRowNum]) === null || _a2 === void 0 ? void 0 : _a2[targetColNum];
  }
  evaluateArguments(args, state, metadata) {
    return metadata.expandRanges ? this.listOfScalarValues(args, state) : args.map((ast) => [this.evaluateAst(ast, state), false, ast.type === AstNodeType.EMPTY]);
  }
  buildMetadataForEachArgumentValue(numberOfArgumentValuesPassed, metadata) {
    const argumentsMetadata = metadata.parameters ? [...metadata.parameters] : [];
    const isRepeatLastArgsValid = metadata.repeatLastArgs !== void 0 && Number.isInteger(metadata.repeatLastArgs) && metadata.repeatLastArgs > 0;
    if (isRepeatLastArgsValid) {
      while (numberOfArgumentValuesPassed > argumentsMetadata.length) {
        argumentsMetadata.push(...argumentsMetadata.slice(argumentsMetadata.length - metadata.repeatLastArgs));
      }
    }
    return argumentsMetadata;
  }
  isNumberOfArgumentValuesValid(argumentsMetadata, numberOfArgumentValuesPassed) {
    if (numberOfArgumentValuesPassed > argumentsMetadata.length) {
      return false;
    }
    if (numberOfArgumentValuesPassed < argumentsMetadata.length) {
      const metadataForMissingArguments = argumentsMetadata.slice(numberOfArgumentValuesPassed);
      const areMissingArgumentsOptional = metadataForMissingArguments.every((argMetadata) => (argMetadata === null || argMetadata === void 0 ? void 0 : argMetadata.optionalArg) || (argMetadata === null || argMetadata === void 0 ? void 0 : argMetadata.defaultValue) !== void 0);
      return areMissingArgumentsOptional;
    }
    return true;
  }
  calculateSizeOfVectorizedResultArray(argumentValues, argumentMetadata) {
    const argumentsThatRequireVectorization = argumentValues.filter((value, i2) => this.isRangePassedAsAScalarArgument(value, argumentMetadata[i2]));
    const height = Math.max(1, ...argumentsThatRequireVectorization.map((val) => val.height()));
    const width = Math.max(1, ...argumentsThatRequireVectorization.map((val) => val.width()));
    return [height, width];
  }
  isRangePassedAsAScalarArgument(argumentValue, argumentMetadata) {
    if (argumentValue == null || argumentMetadata == null) {
      return false;
    }
    return argumentValue instanceof SimpleRangeValue && ![FunctionArgumentType.RANGE, FunctionArgumentType.ANY].includes(argumentMetadata.argumentType);
  }
  metadata(name) {
    const params = this.constructor.implementedFunctions[name];
    if (params !== void 0) {
      return params;
    }
    throw new Error(`No metadata for function ${name}.`);
  }
  returnNumberWrapper(val, type, format2) {
    if (type !== void 0 && isExtendedNumber(val)) {
      return this.arithmeticHelper.ExtendedNumberFactory(getRawValue(val), {
        type,
        format: format2
      });
    } else {
      return val;
    }
  }
}
class ArraySize {
  constructor(width, height, isRef = false) {
    this.width = width;
    this.height = height;
    this.isRef = isRef;
  }
  static error() {
    return new ArraySize(1, 1, true);
  }
  static scalar() {
    return new ArraySize(1, 1, false);
  }
  isScalar() {
    return this.width === 1 && this.height === 1 || this.isRef;
  }
}
function arraySizeForBinaryOp(leftArraySize, rightArraySize) {
  return new ArraySize(Math.max(leftArraySize.width, rightArraySize.width), Math.max(leftArraySize.height, rightArraySize.height));
}
function arraySizeForUnaryOp(arraySize) {
  return new ArraySize(arraySize.width, arraySize.height);
}
class ArraySizePredictor {
  constructor(config, functionRegistry) {
    this.config = config;
    this.functionRegistry = functionRegistry;
  }
  checkArraySize(ast, formulaAddress) {
    return this.checkArraySizeForAst(ast, {
      formulaAddress,
      arraysFlag: this.config.useArrayArithmetic
    });
  }
  checkArraySizeForAst(ast, state) {
    switch (ast.type) {
      case AstNodeType.FUNCTION_CALL: {
        return this.checkArraySizeForFunction(ast, state);
      }
      case AstNodeType.COLUMN_RANGE:
      case AstNodeType.ROW_RANGE:
      case AstNodeType.CELL_RANGE: {
        const range = AbsoluteCellRange.fromAstOrUndef(ast, state.formulaAddress);
        if (range === void 0) {
          return ArraySize.error();
        } else {
          return new ArraySize(range.width(), range.height(), true);
        }
      }
      case AstNodeType.ARRAY: {
        const heights = [];
        const widths = [];
        for (const row of ast.args) {
          const sizes = row.map((ast2) => this.checkArraySizeForAst(ast2, state));
          const h2 = Math.min(...sizes.map((size) => size.height));
          const w2 = sizes.reduce((total, size) => total + size.width, 0);
          heights.push(h2);
          widths.push(w2);
        }
        const height = heights.reduce((total, h2) => total + h2, 0);
        const width = Math.min(...widths);
        return new ArraySize(width, height);
      }
      case AstNodeType.STRING:
      case AstNodeType.NUMBER:
        return ArraySize.scalar();
      case AstNodeType.CELL_REFERENCE:
        return new ArraySize(1, 1, true);
      case AstNodeType.DIV_OP:
      case AstNodeType.CONCATENATE_OP:
      case AstNodeType.EQUALS_OP:
      case AstNodeType.GREATER_THAN_OP:
      case AstNodeType.GREATER_THAN_OR_EQUAL_OP:
      case AstNodeType.LESS_THAN_OP:
      case AstNodeType.LESS_THAN_OR_EQUAL_OP:
      case AstNodeType.MINUS_OP:
      case AstNodeType.NOT_EQUAL_OP:
      case AstNodeType.PLUS_OP:
      case AstNodeType.POWER_OP:
      case AstNodeType.TIMES_OP: {
        const left = this.checkArraySizeForAst(ast.left, state);
        const right = this.checkArraySizeForAst(ast.right, state);
        if (!state.arraysFlag && (left.height > 1 || left.width > 1 || right.height > 1 || right.width > 1)) {
          return ArraySize.error();
        }
        return arraySizeForBinaryOp(left, right);
      }
      case AstNodeType.MINUS_UNARY_OP:
      case AstNodeType.PLUS_UNARY_OP:
      case AstNodeType.PERCENT_OP: {
        const val = this.checkArraySizeForAst(ast.value, state);
        if (!state.arraysFlag && (val.height > 1 || val.width > 1)) {
          return ArraySize.error();
        }
        return arraySizeForUnaryOp(val);
      }
      case AstNodeType.PARENTHESIS: {
        return this.checkArraySizeForAst(ast.expression, state);
      }
      case AstNodeType.EMPTY:
        return ArraySize.error();
      default:
        return ArraySize.error();
    }
  }
  checkArraySizeForFunction(ast, state) {
    const pluginArraySizeFunction = this.functionRegistry.getArraySizeFunction(ast.procedureName);
    if (pluginArraySizeFunction !== void 0) {
      return pluginArraySizeFunction(ast, state);
    }
    const metadata = this.functionRegistry.getMetadata(ast.procedureName);
    if (metadata === void 0 || metadata.expandRanges || !state.arraysFlag || metadata.vectorizationForbidden || metadata.parameters === void 0) {
      return new ArraySize(1, 1);
    }
    const subChecks = ast.args.map((arg) => {
      var _a2;
      return this.checkArraySizeForAst(arg, new InterpreterState(state.formulaAddress, state.arraysFlag || ((_a2 = metadata === null || metadata === void 0 ? void 0 : metadata.enableArrayArithmeticForArguments) !== null && _a2 !== void 0 ? _a2 : false)));
    });
    const argumentDefinitions = [...metadata.parameters];
    if (metadata.repeatLastArgs !== void 0 && argumentDefinitions.length < subChecks.length && (subChecks.length - argumentDefinitions.length) % metadata.repeatLastArgs !== 0) {
      return ArraySize.error();
    }
    while (argumentDefinitions.length < subChecks.length) {
      if (metadata.repeatLastArgs === void 0) {
        return ArraySize.error();
      }
      argumentDefinitions.push(...argumentDefinitions.slice(argumentDefinitions.length - metadata.repeatLastArgs));
    }
    let maxWidth2 = 1;
    let maxHeight = 1;
    for (let i2 = 0; i2 < subChecks.length; i2++) {
      if (argumentDefinitions[i2].argumentType !== FunctionArgumentType.RANGE && argumentDefinitions[i2].argumentType !== FunctionArgumentType.ANY) {
        maxHeight = Math.max(maxHeight, subChecks[i2].height);
        maxWidth2 = Math.max(maxWidth2, subChecks[i2].width);
      }
    }
    return new ArraySize(maxWidth2, maxHeight);
  }
}
class DetailedCellError {
  constructor(error, value, address) {
    var _a2;
    this.value = value;
    this.address = address;
    this.type = error.type;
    this.message = (_a2 = error.message) !== null && _a2 !== void 0 ? _a2 : "";
  }
  toString() {
    return this.value;
  }
  valueOf() {
    return this.value;
  }
}
function configValueFromParam(inputValue, expectedType, paramName) {
  if (typeof inputValue === "undefined") {
    return Config.defaultConfig[paramName];
  } else if (typeof expectedType === "string") {
    if (typeof inputValue === expectedType) {
      return inputValue;
    } else {
      throw new ExpectedValueOfTypeError(expectedType, paramName);
    }
  } else {
    if (expectedType.includes(inputValue)) {
      return inputValue;
    } else {
      throw new ExpectedOneOfValuesError(expectedType.map((val) => `'${val}'`).join(" "), paramName);
    }
  }
}
function validateNumberToBeAtLeast(value, paramName, minimum) {
  if (value < minimum) {
    throw new ConfigValueTooSmallError(paramName, minimum);
  }
}
function validateNumberToBeAtMost(value, paramName, maximum2) {
  if (value > maximum2) {
    throw new ConfigValueTooBigError(paramName, maximum2);
  }
}
function configValueFromParamCheck(inputValue, typeCheck, expectedType, paramName) {
  if (typeCheck(inputValue)) {
    return inputValue;
  } else if (typeof inputValue === "undefined") {
    return Config.defaultConfig[paramName];
  } else {
    throw new ExpectedValueOfTypeError(expectedType, paramName);
  }
}
function configCheckIfParametersNotInConflict(...params) {
  const valuesMap = /* @__PURE__ */ new Map();
  params.forEach((param) => {
    const names = valuesMap.get(param.value) || [];
    names.push(param.name);
    valuesMap.set(param.value, names);
  });
  const duplicates = [];
  for (const entry of valuesMap.values()) {
    if (entry.length > 1) {
      duplicates.push(entry);
    }
  }
  if (duplicates.length > 0) {
    duplicates.forEach((entry) => entry.sort());
    const paramNames = duplicates.map((entry) => `[${entry}]`).join("; ");
    throw new Error(`Config initialization failed. Parameters in conflict: ${paramNames}`);
  }
}
function validateArgToType(inputValue, expectedType, paramName) {
  if (typeof inputValue !== expectedType) {
    throw new ExpectedValueOfTypeError(expectedType, paramName);
  }
}
const TIME_FORMAT_SECONDS_ITEM_REGEXP = new RegExp("^ss(\\.(s+|0+))?$");
const QUICK_CHECK_REGEXP = new RegExp("^[0-9/.\\-: ]+[ap]?m?$");
const WHITESPACE_REGEXP = new RegExp("\\s+");
const DATE_SEPARATOR_REGEXP = new RegExp("[ /.-]");
const TIME_SEPARATOR = ":";
const SECONDS_PRECISION = 1e3;
const memoizedParseTimeFormat = memoize(parseTimeFormat);
const memoizedParseDateFormat = memoize(parseDateFormat);
function defaultParseToDateTime(text, dateFormat, timeFormat) {
  if (dateFormat === void 0 && timeFormat === void 0) {
    return void 0;
  }
  let dateTimeString = text.replace(WHITESPACE_REGEXP, " ").trim().toLowerCase();
  if (!doesItLookLikeADateTimeQuickCheck(dateTimeString)) {
    return void 0;
  }
  let ampmToken = dateTimeString.substring(dateTimeString.length - 2);
  if (ampmToken === "am" || ampmToken === "pm") {
    dateTimeString = dateTimeString.substring(0, dateTimeString.length - 2).trim();
  } else {
    ampmToken = dateTimeString.substring(dateTimeString.length - 1);
    if (ampmToken === "a" || ampmToken === "p") {
      dateTimeString = dateTimeString.substring(0, dateTimeString.length - 1).trim();
    } else {
      ampmToken = void 0;
    }
  }
  const dateItems = dateTimeString.split(DATE_SEPARATOR_REGEXP);
  if (dateItems.length >= 2 && dateItems[dateItems.length - 2].includes(TIME_SEPARATOR)) {
    dateItems[dateItems.length - 2] = dateItems[dateItems.length - 2] + "." + dateItems[dateItems.length - 1];
    dateItems.pop();
  }
  const timeItems = dateItems[dateItems.length - 1].split(TIME_SEPARATOR);
  if (ampmToken !== void 0) {
    timeItems.push(ampmToken);
  }
  if (dateItems.length === 1) {
    return defaultParseToTime(timeItems, timeFormat);
  }
  if (timeItems.length === 1) {
    return defaultParseToDate(dateItems, dateFormat);
  }
  const parsedDate = defaultParseToDate(dateItems.slice(0, dateItems.length - 1), dateFormat);
  const parsedTime = defaultParseToTime(timeItems, timeFormat);
  if (parsedDate === void 0) {
    return void 0;
  } else if (parsedTime === void 0) {
    return void 0;
  } else {
    return Object.assign(Object.assign({}, parsedDate), parsedTime);
  }
}
function defaultParseToTime(timeItems, timeFormat) {
  var _a2, _b, _c;
  if (timeFormat === void 0) {
    return void 0;
  }
  const {
    itemsCount,
    hourItem,
    minuteItem,
    secondItem
  } = memoizedParseTimeFormat(timeFormat);
  let ampm = void 0;
  if (timeItems[timeItems.length - 1] === "am" || timeItems[timeItems.length - 1] === "a") {
    ampm = false;
    timeItems.pop();
  } else if (timeItems[timeItems.length - 1] === "pm" || timeItems[timeItems.length - 1] === "p") {
    ampm = true;
    timeItems.pop();
  }
  if (timeItems.length !== itemsCount) {
    return void 0;
  }
  const secondsParsed = Number((_a2 = timeItems[secondItem]) !== null && _a2 !== void 0 ? _a2 : "0");
  if (!Number.isFinite(secondsParsed)) {
    return void 0;
  }
  const seconds = Math.round(secondsParsed * SECONDS_PRECISION) / SECONDS_PRECISION;
  const minutes = Number((_b = timeItems[minuteItem]) !== null && _b !== void 0 ? _b : "0");
  if (!(Number.isFinite(minutes) && Number.isInteger(minutes))) {
    return void 0;
  }
  const hoursParsed = Number((_c = timeItems[hourItem]) !== null && _c !== void 0 ? _c : "0");
  if (!(Number.isFinite(hoursParsed) && Number.isInteger(hoursParsed))) {
    return void 0;
  }
  if (ampm !== void 0 && (hoursParsed < 0 || hoursParsed > 12)) {
    return void 0;
  }
  const hours = ampm !== void 0 ? hoursParsed % 12 + (ampm ? 12 : 0) : hoursParsed;
  return {
    hours,
    minutes,
    seconds
  };
}
function defaultParseToDate(dateItems, dateFormat) {
  var _a2;
  if (dateFormat === void 0) {
    return void 0;
  }
  const {
    itemsCount,
    dayItem,
    monthItem,
    shortYearItem,
    longYearItem
  } = memoizedParseDateFormat(dateFormat);
  if (dateItems.length !== itemsCount) {
    return void 0;
  }
  const day = Number(dateItems[dayItem]);
  if (!(Number.isFinite(day) && Number.isInteger(day))) {
    return void 0;
  }
  const month = Number(dateItems[monthItem]);
  if (!(Number.isFinite(month) && Number.isInteger(month))) {
    return void 0;
  }
  if (dateItems[longYearItem] && dateItems[shortYearItem]) {
    return void 0;
  }
  const year = Number((_a2 = dateItems[longYearItem]) !== null && _a2 !== void 0 ? _a2 : dateItems[shortYearItem]);
  if (!(Number.isFinite(year) && Number.isInteger(year))) {
    return void 0;
  }
  if (dateItems[longYearItem] && (year < 1e3 || year > 9999)) {
    return void 0;
  }
  if (dateItems[shortYearItem] && (year < 0 || year > 99)) {
    return void 0;
  }
  return {
    year,
    month,
    day
  };
}
function parseTimeFormat(timeFormat) {
  const formatLowercase = timeFormat.toLowerCase().trim();
  const formatWithoutAmPmItem = formatLowercase.endsWith("am/pm") ? formatLowercase.substring(0, formatLowercase.length - 5) : formatLowercase.endsWith("a/p") ? formatLowercase.substring(0, timeFormat.length - 3) : formatLowercase;
  const items = formatWithoutAmPmItem.trim().split(TIME_SEPARATOR);
  return {
    itemsCount: items.length,
    hourItem: items.indexOf("hh"),
    minuteItem: items.indexOf("mm"),
    secondItem: items.findIndex((item) => TIME_FORMAT_SECONDS_ITEM_REGEXP.test(item))
  };
}
function parseDateFormat(dateFormat) {
  const items = dateFormat.toLowerCase().trim().split(DATE_SEPARATOR_REGEXP);
  return {
    itemsCount: items.length,
    dayItem: items.indexOf("dd"),
    monthItem: items.indexOf("mm"),
    shortYearItem: items.indexOf("yy"),
    longYearItem: items.indexOf("yyyy")
  };
}
function doesItLookLikeADateTimeQuickCheck(text) {
  return QUICK_CHECK_REGEXP.test(text);
}
function memoize(fn2) {
  const memoizedResults = {};
  return (arg) => {
    const memoizedResult = memoizedResults[arg];
    if (memoizedResult !== void 0) {
      return memoizedResult;
    }
    const result = fn2(arg);
    memoizedResults[arg] = result;
    return result;
  };
}
const numDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const prefSumDays = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
function instanceOfSimpleDate(obj) {
  if (obj && (typeof obj === "object" || typeof obj === "function")) {
    return "year" in obj && typeof obj.year === "number" && "month" in obj && typeof obj.month === "number" && "day" in obj && typeof obj.day === "number";
  } else {
    return false;
  }
}
function instanceOfSimpleTime(obj) {
  if (obj && (typeof obj === "object" || typeof obj === "function")) {
    return "hours" in obj && typeof obj.hours === "number" && "minutes" in obj && typeof obj.minutes === "number" && "seconds" in obj && typeof obj.seconds === "number";
  } else {
    return false;
  }
}
const maxDate = {
  year: 9999,
  month: 12,
  day: 31
};
class DateTimeHelper {
  constructor(config) {
    this.config = config;
    this.minDateAbsoluteValue = this.dateToNumberFromZero(config.nullDate);
    this.maxDateValue = this.dateToNumber(maxDate);
    this.leapYear1900 = config.leapYear1900;
    if (!this.leapYear1900 && 0 <= this.dateToNumber({
      year: 1900,
      month: 2,
      day: 28
    })) {
      this.epochYearZero = this.numberToSimpleDate(2).year;
    } else {
      this.epochYearZero = this.numberToSimpleDate(1).year;
    }
    this.parseDateTime = config.parseDateTime;
  }
  getWithinBounds(dayNumber) {
    return dayNumber <= this.maxDateValue && dayNumber >= 0 ? dayNumber : void 0;
  }
  dateStringToDateNumber(dateTimeString) {
    const {
      dateTime,
      dateFormat = "",
      timeFormat = ""
    } = this.parseDateTimeFromConfigFormats(dateTimeString);
    if (dateTime === void 0) {
      return void 0;
    }
    if (instanceOfSimpleTime(dateTime)) {
      if (instanceOfSimpleDate(dateTime)) {
        return new DateTimeNumber(timeToNumber(dateTime) + this.dateToNumber(dateTime), dateFormat + " " + timeFormat);
      } else {
        return new TimeNumber(timeToNumber(dateTime), timeFormat);
      }
    } else {
      if (instanceOfSimpleDate(dateTime)) {
        return new DateNumber(this.dateToNumber(dateTime), dateFormat);
      } else {
        return 0;
      }
    }
  }
  parseDateTimeFromConfigFormats(dateTimeString) {
    return this.parseDateTimeFromFormats(dateTimeString, this.config.dateFormats, this.config.timeFormats);
  }
  getNullYear() {
    return this.config.nullYear;
  }
  getEpochYearZero() {
    return this.epochYearZero;
  }
  isValidDate(date) {
    if (isNaN(date.year) || isNaN(date.month) || isNaN(date.day)) {
      return false;
    } else if (date.day !== Math.round(date.day) || date.month !== Math.round(date.month) || date.year !== Math.round(date.year)) {
      return false;
    } else if (date.year < 1582) {
      return false;
    } else if (date.month < 1 || date.month > 12) {
      return false;
    } else if (date.day < 1) {
      return false;
    } else if (this.isLeapYear(date.year) && date.month === 2) {
      return date.day <= 29;
    } else {
      return date.day <= numDays[date.month - 1];
    }
  }
  dateToNumber(date) {
    return this.dateToNumberFromZero(date) - this.minDateAbsoluteValue;
  }
  relativeNumberToAbsoluteNumber(arg) {
    return arg + this.minDateAbsoluteValue - (this.leapYear1900 ? 1 : 0);
  }
  numberToSimpleDate(arg) {
    const dateNumber = Math.floor(arg) + this.minDateAbsoluteValue;
    let year = Math.floor(dateNumber / 365.2425);
    if (this.dateToNumberFromZero({
      year: year + 1,
      month: 1,
      day: 1
    }) <= dateNumber) {
      year++;
    } else if (this.dateToNumberFromZero({
      year: year - 1,
      month: 1,
      day: 1
    }) > dateNumber) {
      year--;
    }
    const dayOfYear = dateNumber - this.dateToNumberFromZero({
      year,
      month: 1,
      day: 1
    });
    const month = dayToMonth(dayOfYear - (this.isLeapYear(year) && dayOfYear >= 59 ? 1 : 0));
    const day = dayOfYear - prefSumDays[month] - (this.isLeapYear(year) && month > 1 ? 1 : 0);
    return {
      year,
      month: month + 1,
      day: day + 1
    };
  }
  numberToSimpleDateTime(arg) {
    const time = numberToSimpleTime(arg % 1);
    const carryDays = Math.floor(time.hours / HOURS_PER_DAY);
    time.hours = time.hours % HOURS_PER_DAY;
    const date = this.numberToSimpleDate(Math.floor(arg) + carryDays);
    return Object.assign(Object.assign({}, date), time);
  }
  leapYearsCount(year) {
    return Math.floor(year / 4) - Math.floor(year / 100) + Math.floor(year / 400) + (this.config.leapYear1900 && year >= 1900 ? 1 : 0);
  }
  daysInMonth(year, month) {
    if (this.isLeapYear(year) && month === 2) {
      return 29;
    } else {
      return numDays[month - 1];
    }
  }
  endOfMonth(date) {
    return {
      year: date.year,
      month: date.month,
      day: this.daysInMonth(date.year, date.month)
    };
  }
  toBasisUS(start, end) {
    if (start.day === 31) {
      start.day = 30;
    }
    if (start.day === 30 && end.day === 31) {
      end.day = 30;
    }
    if (start.month === 2 && start.day === this.daysInMonth(start.year, start.month)) {
      start.day = 30;
      if (end.month === 2 && end.day === this.daysInMonth(end.year, end.month)) {
        end.day = 30;
      }
    }
    return [start, end];
  }
  yearLengthForBasis(start, end) {
    if (start.year !== end.year) {
      if (start.year + 1 !== end.year || start.month < end.month || start.month === end.month && start.day < end.day) {
        return (this.leapYearsCount(end.year) - this.leapYearsCount(start.year - 1)) / (end.year - start.year + 1) + 365;
      }
      if (this.countLeapDays(end) !== this.countLeapDays({
        year: start.year,
        month: start.month,
        day: start.day - 1
      })) {
        return 366;
      } else {
        return 365;
      }
    }
    if (this.isLeapYear(start.year)) {
      return 366;
    } else {
      return 365;
    }
  }
  parseSingleFormat(dateString, dateFormat, timeFormat) {
    const dateTime = this.parseDateTime(dateString, dateFormat, timeFormat);
    if (instanceOfSimpleDate(dateTime)) {
      if (dateTime.year >= 0 && dateTime.year < 100) {
        if (dateTime.year < this.getNullYear()) {
          dateTime.year += 2e3;
        } else {
          dateTime.year += 1900;
        }
      }
      if (!this.isValidDate(dateTime)) {
        return void 0;
      }
    }
    return dateTime;
  }
  parseDateTimeFromFormats(dateTimeString, dateFormats, timeFormats) {
    const dateFormatsArray = dateFormats.length === 0 ? [void 0] : dateFormats;
    const timeFormatsArray = timeFormats.length === 0 ? [void 0] : timeFormats;
    for (const dateFormat of dateFormatsArray) {
      for (const timeFormat of timeFormatsArray) {
        const dateTime = this.parseSingleFormat(dateTimeString, dateFormat, timeFormat);
        if (dateTime !== void 0) {
          return {
            dateTime,
            timeFormat,
            dateFormat
          };
        }
      }
    }
    return {};
  }
  countLeapDays(date) {
    if (date.month > 2 || date.month === 2 && date.day >= 29) {
      return this.leapYearsCount(date.year);
    } else {
      return this.leapYearsCount(date.year - 1);
    }
  }
  dateToNumberFromZero(date) {
    return 365 * date.year + prefSumDays[date.month - 1] + date.day - 1 + (date.month <= 2 ? this.leapYearsCount(date.year - 1) : this.leapYearsCount(date.year));
  }
  isLeapYear(year) {
    if (year % 4) {
      return false;
    } else if (year % 100) {
      return true;
    } else if (year % 400) {
      return year === 1900 && this.config.leapYear1900;
    } else {
      return true;
    }
  }
}
function dayToMonth(dayOfYear) {
  let month = 0;
  if (prefSumDays[month + 6] <= dayOfYear) {
    month += 6;
  }
  if (prefSumDays[month + 3] <= dayOfYear) {
    month += 3;
  }
  if (prefSumDays[month + 2] <= dayOfYear) {
    month += 2;
  } else if (prefSumDays[month + 1] <= dayOfYear) {
    month += 1;
  }
  return month;
}
function offsetMonth(date, offset) {
  const totalM = 12 * date.year + date.month - 1 + offset;
  return {
    year: Math.floor(totalM / 12),
    month: totalM % 12 + 1,
    day: date.day
  };
}
function truncateDayInMonth(date) {
  return {
    year: date.year,
    month: date.month,
    day: Math.min(date.day, numDays[date.month - 1])
  };
}
function roundToNearestSecond(arg) {
  return Math.round(arg * 3600 * 24) / (3600 * 24);
}
function roundToEpsilon(arg, epsilon = 1) {
  return Math.round(arg * epsilon) / epsilon;
}
function numberToSimpleTime(arg) {
  const argAsSeconds = arg * HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE;
  const seconds = roundToEpsilon(argAsSeconds % SECONDS_PER_MINUTE, 1e5) % SECONDS_PER_MINUTE;
  const argAsMinutes = (argAsSeconds - seconds) / SECONDS_PER_MINUTE;
  const minutes = Math.round(argAsMinutes % MINUTES_PER_HOUR) % MINUTES_PER_HOUR;
  const argAsHours = (argAsMinutes - minutes) / MINUTES_PER_HOUR;
  const hours = Math.round(argAsHours);
  return {
    hours,
    minutes,
    seconds
  };
}
function timeToNumber(time) {
  return ((time.seconds / 60 + time.minutes) / 60 + time.hours) / 24;
}
function toBasisEU(date) {
  return {
    year: date.year,
    month: date.month,
    day: Math.min(30, date.day)
  };
}
class AlwaysDense {
  call() {
    return DenseStrategy;
  }
}
const dateFormatRegex = /(\\.|dd|DD|d|D|mm|MM|m|M|YYYY|YY|yyyy|yy|HH|hh|H|h|ss(\.(0+|s+))?|s|AM\/PM|am\/pm|A\/P|a\/p|\[mm]|\[MM]|\[hh]|\[HH])/g;
const numberFormatRegex = /(\\.|[#0]+(\.[#0]*)?)/g;
var TokenType;
(function(TokenType2) {
  TokenType2["FORMAT"] = "FORMAT";
  TokenType2["FREE_TEXT"] = "FREE_TEXT";
})(TokenType || (TokenType = {}));
function formatToken(type, value) {
  return {
    type,
    value
  };
}
var FormatExpressionType;
(function(FormatExpressionType2) {
  FormatExpressionType2["DATE"] = "DATE";
  FormatExpressionType2["NUMBER"] = "NUMBER";
  FormatExpressionType2["STRING"] = "STRING";
})(FormatExpressionType || (FormatExpressionType = {}));
function matchDateFormat(str) {
  dateFormatRegex.lastIndex = 0;
  const tokens = [];
  let m2;
  do {
    m2 = dateFormatRegex.exec(str);
    if (m2 !== null) {
      tokens.push(m2);
    }
  } while (m2);
  return tokens;
}
function matchNumberFormat(str) {
  numberFormatRegex.lastIndex = 0;
  const numberFormatToken = numberFormatRegex.exec(str);
  if (numberFormatToken !== null) {
    return [numberFormatToken];
  } else {
    return [];
  }
}
function createTokens(regexTokens, str) {
  const tokens = [];
  let start = 0;
  for (let i2 = 0; i2 < regexTokens.length; ++i2) {
    const token = regexTokens[i2];
    if (token.index !== start) {
      const beforeToken = str.substr(start, token.index - start);
      tokens.push(formatToken(TokenType.FREE_TEXT, beforeToken));
    }
    if (token[0].startsWith("\\")) {
      tokens.push(formatToken(TokenType.FREE_TEXT, token[0]));
    } else {
      tokens.push(formatToken(TokenType.FORMAT, token[0]));
    }
    start = token.index + token[0].length;
  }
  const lastToken = regexTokens[regexTokens.length - 1];
  if (lastToken.index + lastToken[0].length < str.length) {
    const afterLastToken = str.substr(lastToken.index + lastToken[0].length, str.length);
    tokens.push(formatToken(TokenType.FREE_TEXT, afterLastToken));
  }
  return tokens;
}
function parseForDateTimeFormat(str) {
  const dateFormatTokens = matchDateFormat(str);
  if (dateFormatTokens.every((elem) => isEscapeToken(elem))) {
    return void 0;
  } else {
    return {
      type: FormatExpressionType.DATE,
      tokens: createTokens(dateFormatTokens, str)
    };
  }
}
function parseForNumberFormat(str) {
  const numberFormatTokens = matchNumberFormat(str);
  if (numberFormatTokens.every((elem) => isEscapeToken(elem))) {
    return void 0;
  } else {
    return {
      type: FormatExpressionType.NUMBER,
      tokens: createTokens(numberFormatTokens, str)
    };
  }
}
function isEscapeToken(token) {
  return token[0].startsWith("\\");
}
function format(value, formatArg, config, dateHelper) {
  const tryDateTime = config.stringifyDateTime(dateHelper.numberToSimpleDateTime(value), formatArg);
  if (tryDateTime !== void 0) {
    return tryDateTime;
  }
  const tryDuration = config.stringifyDuration(numberToSimpleTime(value), formatArg);
  if (tryDuration !== void 0) {
    return tryDuration;
  }
  const expression = parseForNumberFormat(formatArg);
  if (expression !== void 0) {
    return numberFormat(expression.tokens, value);
  }
  return formatArg;
}
function padLeft(number, size) {
  let result = `${number}`;
  while (result.length < size) {
    result = "0" + result;
  }
  return result;
}
function padRight(number, size) {
  let result = `${number}`;
  while (result.length < size) {
    result = result + "0";
  }
  return result;
}
function countChars(text, char) {
  return text.split(char).length - 1;
}
function numberFormat(tokens, value) {
  let result = "";
  for (let i2 = 0; i2 < tokens.length; ++i2) {
    const token = tokens[i2];
    if (token.type === TokenType.FREE_TEXT) {
      result += token.value;
      continue;
    }
    const tokenParts = token.value.split(".");
    const integerFormat = tokenParts[0];
    const decimalFormat = tokenParts[1] || "";
    const separator = tokenParts[1] ? "." : "";
    const valueParts = Number(value.toFixed(decimalFormat.length)).toString().split(".");
    let integerPart = valueParts[0] || "";
    let decimalPart = valueParts[1] || "";
    if (integerFormat.length > integerPart.length) {
      const padSizeInteger = countChars(integerFormat.substr(0, integerFormat.length - integerPart.length), "0");
      integerPart = padLeft(integerPart, padSizeInteger + integerPart.length);
    }
    const padSizeDecimal = countChars(decimalFormat.substr(decimalPart.length, decimalFormat.length - decimalPart.length), "0");
    decimalPart = padRight(decimalPart, padSizeDecimal + decimalPart.length);
    result += integerPart + separator + decimalPart;
  }
  return result;
}
function defaultStringifyDuration(time, formatArg) {
  const expression = parseForDateTimeFormat(formatArg);
  if (expression === void 0) {
    return void 0;
  }
  const tokens = expression.tokens;
  let result = "";
  for (const token of tokens) {
    if (token.type === TokenType.FREE_TEXT) {
      result += token.value;
      continue;
    }
    switch (token.value.toLowerCase()) {
      case "h":
      case "hh": {
        result += padLeft(time.hours, token.value.length);
        time.hours = 0;
        break;
      }
      case "[hh]": {
        result += padLeft(time.hours, token.value.length - 2);
        time.hours = 0;
        break;
      }
      case "m":
      case "mm": {
        result += padLeft(time.minutes, token.value.length);
        time.minutes = 0;
        break;
      }
      case "[mm]": {
        result += padLeft(time.minutes + 60 * time.hours, token.value.length - 2);
        time.minutes = 0;
        time.hours = 0;
        break;
      }
      /* seconds */
      case "s":
      case "ss": {
        result += padLeft(Math.floor(time.seconds), token.value.length);
        break;
      }
      default: {
        if (TIME_FORMAT_SECONDS_ITEM_REGEXP.test(token.value)) {
          const fractionOfSecondPrecision = Math.max(token.value.length - 3, 0);
          result += `${time.seconds < 10 ? "0" : ""}${Math.floor(time.seconds * Math.pow(10, fractionOfSecondPrecision)) / Math.pow(10, fractionOfSecondPrecision)}`;
          continue;
        }
        return void 0;
      }
    }
  }
  return result;
}
function defaultStringifyDateTime(dateTime, formatArg) {
  const expression = parseForDateTimeFormat(formatArg);
  if (expression === void 0) {
    return void 0;
  }
  const tokens = expression.tokens;
  let result = "";
  let minutes = false;
  const ampm = tokens.some((token) => token.type === TokenType.FORMAT && (token.value === "a/p" || token.value === "A/P" || token.value === "am/pm" || token.value === "AM/PM"));
  for (let i2 = 0; i2 < tokens.length; i2++) {
    const token = tokens[i2];
    if (token.type === TokenType.FREE_TEXT) {
      result += token.value;
      continue;
    }
    switch (token.value.toLowerCase()) {
      /* hours*/
      case "h":
      case "hh": {
        minutes = true;
        result += padLeft(ampm ? (dateTime.hours + 11) % 12 + 1 : dateTime.hours, token.value.length);
        break;
      }
      /* days */
      case "d":
      case "dd": {
        result += padLeft(dateTime.day, token.value.length);
        break;
      }
      /* seconds */
      case "s":
      case "ss": {
        result += padLeft(Math.floor(dateTime.seconds), token.value.length);
        break;
      }
      /* minutes / months */
      case "m":
      case "mm": {
        if (i2 + 1 < tokens.length && tokens[i2 + 1].value.startsWith(":")) {
          minutes = true;
        }
        if (minutes) {
          result += padLeft(dateTime.minutes, token.value.length);
        } else {
          result += padLeft(dateTime.month, token.value.length);
        }
        minutes = true;
        break;
      }
      /* years */
      case "yy": {
        result += padLeft(dateTime.year % 100, token.value.length);
        break;
      }
      case "yyyy": {
        result += dateTime.year;
        break;
      }
      /* AM / PM */
      case "am/pm":
      case "a/p": {
        const [am, pm] = token.value.split("/");
        result += dateTime.hours < 12 ? am : pm;
        break;
      }
      default: {
        if (TIME_FORMAT_SECONDS_ITEM_REGEXP.test(token.value)) {
          const fractionOfSecondPrecision = token.value.length - 3;
          result += `${dateTime.seconds < 10 ? "0" : ""}${Math.floor(dateTime.seconds * Math.pow(10, fractionOfSecondPrecision)) / Math.pow(10, fractionOfSecondPrecision)}`;
          continue;
        }
        return void 0;
      }
    }
  }
  return result;
}
const _rl = "length";
const _hd = (v2) => parseInt(v2, 16);
const _pi = (v2) => parseInt(v2, 10);
const _nm = (v2) => (v2 + "").replace(/\-/g, "");
const _ss = (v2, s2, l2) => v2["substr"](s2, l2);
const _cp = (v2) => v2["codePointAt"](0) - 65;
function extractTime(v2) {
  return _nm(v2)[_rl] === 50 >> 1 ? _hd(_ss(_nm(v2), _hd("12"), _cp("F"))) / (_hd(_ss(_nm(v2), _cp("C"), _cp("Y") >> 4)) || (~~![][_rl] << 3) + 1) : 0;
}
function checkKeySchema(v2) {
  v2 = (v2 + "").replace(/\-/g, "");
  if (v2[_rl] !== _cp("Z")) {
    return false;
  }
  let sp = 0;
  return [[0, _cp("G") + 1], [_cp("H"), _cp("H") - 1], [_cp("G") + _cp("H"), _cp("G")]].reduce((e3, [a2, b2], c2) => {
    e3 |= (_pi(`${_pi(_hd(_ss(v2, ...[sp + a2 - (c2 === 3 >> 2 ? 0 : 2), b2 + (!c2 ? 0 : 2)])) + (_hd(_ss(v2, ...[sp + a2 + b2, 2])) + []).padStart(2, "0"))}`) % 97 || 2) >> 1;
    sp += 2;
    return e3;
  }, _cp("A")) === "1" >> 1;
}
const consoleMessages = {
  invalid: () => "The license key for HyperFormula is invalid.",
  expired: ({
    keyValidityDate
  }) => `The license key for HyperFormula expired on ${keyValidityDate}, and is not valid for the installed version.`,
  missing: () => "The license key for HyperFormula is missing."
};
let _notified = false;
function checkLicenseKeyValidity(licenseKey) {
  const messageDescriptor = {
    template: "missing",
    vars: {}
  };
  if (licenseKey === "gpl-v3" || licenseKey === "internal-use-in-handsontable" || licenseKey === "hftrial-0168e-1f2b7-47158-70b05-0842f") {
    messageDescriptor.template = "valid";
  } else if (typeof licenseKey === "string" && checkKeySchema(licenseKey)) {
    const [day, month, year] = "20/05/2026".split("/");
    const releaseDays = Math.floor((/* @__PURE__ */ new Date(`${month}/${day}/${year}`)).getTime() / 864e5);
    const keyValidityDays = extractTime(licenseKey);
    messageDescriptor.vars.keyValidityDate = formatDate(new Date((keyValidityDays + 1) * 864e5));
    if (releaseDays > keyValidityDays) {
      messageDescriptor.template = "expired";
    } else {
      messageDescriptor.template = "valid";
    }
  } else if (licenseKey !== "") {
    messageDescriptor.template = "invalid";
  }
  if (!_notified && messageDescriptor.template !== "valid") {
    console.warn(consoleMessages[messageDescriptor.template](messageDescriptor.vars));
    _notified = true;
  }
  return messageDescriptor.template;
}
function formatDate(date) {
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}
var CellContent;
(function(CellContent2) {
  class Number2 {
    constructor(value) {
      this.value = value;
      this.value = cloneNumber(this.value, fixNegativeZero(getRawValue(this.value)));
    }
  }
  CellContent2.Number = Number2;
  class String2 {
    constructor(value) {
      this.value = value;
    }
  }
  CellContent2.String = String2;
  class Boolean2 {
    constructor(value) {
      this.value = value;
    }
  }
  CellContent2.Boolean = Boolean2;
  class Empty {
    static getSingletonInstance() {
      if (!Empty.instance) {
        Empty.instance = new Empty();
      }
      return Empty.instance;
    }
  }
  CellContent2.Empty = Empty;
  class Formula {
    constructor(formula) {
      this.formula = formula;
    }
  }
  CellContent2.Formula = Formula;
  class Error2 {
    constructor(errorType, message) {
      this.value = new CellError(errorType, message);
    }
  }
  CellContent2.Error = Error2;
})(CellContent || (CellContent = {}));
function isFormula(text) {
  return text.startsWith("=");
}
function isBoolean(text) {
  const tl = text.toLowerCase();
  return tl === "true" || tl === "false";
}
function isError(text, errorMapping) {
  const upperCased = text.toUpperCase();
  const errorRegex = /#[A-Za-z0-9\/]+[?!]?/;
  return errorRegex.test(upperCased) && Object.prototype.hasOwnProperty.call(errorMapping, upperCased);
}
class CellContentParser {
  constructor(config, dateHelper, numberLiteralsHelper) {
    this.config = config;
    this.dateHelper = dateHelper;
    this.numberLiteralsHelper = numberLiteralsHelper;
  }
  parse(content) {
    if (content === void 0 || content === null) {
      return CellContent.Empty.getSingletonInstance();
    } else if (typeof content === "number") {
      if (isNumberOverflow(content)) {
        return new CellContent.Error(ErrorType.NUM, ErrorMessage.ValueLarge);
      } else {
        return new CellContent.Number(content);
      }
    } else if (typeof content === "boolean") {
      return new CellContent.Boolean(content);
    } else if (content instanceof Date) {
      const dateVal = this.dateHelper.dateToNumber({
        day: content.getDate(),
        month: content.getMonth() + 1,
        year: content.getFullYear()
      });
      const timeVal = timeToNumber({
        hours: content.getHours(),
        minutes: content.getMinutes(),
        seconds: content.getSeconds() + content.getMilliseconds() / 1e3
      });
      const val = dateVal + timeVal;
      if (val < 0) {
        return new CellContent.Error(ErrorType.NUM, ErrorMessage.DateBounds);
      }
      if (val % 1 === 0) {
        return new CellContent.Number(new DateNumber(val, "Date()"));
      } else if (val < 1) {
        return new CellContent.Number(new TimeNumber(val, "Date()"));
      } else {
        return new CellContent.Number(new DateTimeNumber(val, "Date()"));
      }
    } else if (typeof content === "string") {
      if (isBoolean(content)) {
        return new CellContent.Boolean(content.toLowerCase() === "true");
      } else if (isFormula(content)) {
        return new CellContent.Formula(content);
      } else if (isError(content, this.config.errorMapping)) {
        return new CellContent.Error(this.config.errorMapping[content.toUpperCase()]);
      } else {
        let trimmedContent = content.trim();
        let mode = 0;
        let currency;
        if (trimmedContent.endsWith("%")) {
          mode = 1;
          trimmedContent = trimmedContent.slice(0, trimmedContent.length - 1);
        } else {
          const res = this.currencyMatcher(trimmedContent);
          if (res !== void 0) {
            mode = 2;
            [currency, trimmedContent] = res;
          }
        }
        const val = this.numberLiteralsHelper.numericStringToMaybeNumber(trimmedContent);
        if (val !== void 0) {
          let parseAsNum;
          if (mode === 1) {
            parseAsNum = new PercentNumber(val / 100);
          } else if (mode === 2) {
            parseAsNum = new CurrencyNumber(val, currency);
          } else {
            parseAsNum = val;
          }
          return new CellContent.Number(parseAsNum);
        }
        const parsedDateNumber = this.dateHelper.dateStringToDateNumber(trimmedContent);
        if (parsedDateNumber !== void 0) {
          return new CellContent.Number(parsedDateNumber);
        } else {
          return new CellContent.String(content.startsWith("'") ? content.slice(1) : content);
        }
      }
    } else {
      throw new UnableToParseError(content);
    }
  }
  currencyMatcher(token) {
    for (const currency of this.config.currencySymbol) {
      if (token.startsWith(currency)) {
        return [currency, token.slice(currency.length)];
      }
      if (token.endsWith(currency)) {
        return [currency, token.slice(0, token.length - currency.length)];
      }
    }
    return void 0;
  }
}
var ClipboardOperationType;
(function(ClipboardOperationType2) {
  ClipboardOperationType2[ClipboardOperationType2["COPY"] = 0] = "COPY";
  ClipboardOperationType2[ClipboardOperationType2["CUT"] = 1] = "CUT";
})(ClipboardOperationType || (ClipboardOperationType = {}));
var ClipboardCellType;
(function(ClipboardCellType2) {
  ClipboardCellType2[ClipboardCellType2["VALUE"] = 0] = "VALUE";
  ClipboardCellType2[ClipboardCellType2["EMPTY"] = 1] = "EMPTY";
  ClipboardCellType2[ClipboardCellType2["FORMULA"] = 2] = "FORMULA";
  ClipboardCellType2[ClipboardCellType2["PARSING_ERROR"] = 3] = "PARSING_ERROR";
})(ClipboardCellType || (ClipboardCellType = {}));
class Clipboard {
  constructor(sourceLeftCorner, width, height, type, content) {
    this.sourceLeftCorner = sourceLeftCorner;
    this.width = width;
    this.height = height;
    this.type = type;
    this.content = content;
  }
  *getContent(leftCorner) {
    if (this.content === void 0) {
      return;
    } else {
      for (let y2 = 0; y2 < this.height; ++y2) {
        for (let x2 = 0; x2 < this.width; ++x2) {
          yield [simpleCellAddress(leftCorner.sheet, leftCorner.col + x2, leftCorner.row + y2), this.content[y2][x2]];
        }
      }
    }
  }
}
class ClipboardOperations {
  constructor(config, dependencyGraph, operations) {
    this.dependencyGraph = dependencyGraph;
    this.operations = operations;
    this.maxRows = config.maxRows;
    this.maxColumns = config.maxColumns;
  }
  cut(leftCorner, width, height) {
    this.clipboard = new Clipboard(leftCorner, width, height, ClipboardOperationType.CUT);
  }
  copy(leftCorner, width, height) {
    const content = [];
    for (let y2 = 0; y2 < height; ++y2) {
      content[y2] = [];
      for (let x2 = 0; x2 < width; ++x2) {
        const clipboardCell = this.operations.getClipboardCell(simpleCellAddress(leftCorner.sheet, leftCorner.col + x2, leftCorner.row + y2));
        content[y2].push(clipboardCell);
      }
    }
    this.clipboard = new Clipboard(leftCorner, width, height, ClipboardOperationType.COPY, content);
  }
  abortCut() {
    if (this.clipboard && this.clipboard.type === ClipboardOperationType.CUT) {
      this.clear();
    }
  }
  clear() {
    this.clipboard = void 0;
  }
  ensureItIsPossibleToCopyPaste(destinationLeftCorner) {
    if (this.clipboard === void 0) {
      return;
    }
    if (isColOrRowInvalid(destinationLeftCorner) || !this.dependencyGraph.sheetMapping.hasSheetWithId(destinationLeftCorner.sheet)) {
      throw new InvalidArgumentsError("a valid target address.");
    }
    const targetRange = AbsoluteCellRange.spanFrom(destinationLeftCorner, this.clipboard.width, this.clipboard.height);
    if (targetRange.exceedsSheetSizeLimits(this.maxColumns, this.maxRows)) {
      throw new SheetSizeLimitExceededError();
    }
    if (this.dependencyGraph.arrayMapping.isFormulaArrayInRange(targetRange)) {
      throw new Error("It is not possible to paste onto an array");
    }
  }
  isCutClipboard() {
    return this.clipboard !== void 0 && this.clipboard.type === ClipboardOperationType.CUT;
  }
  isCopyClipboard() {
    return this.clipboard !== void 0 && this.clipboard.type === ClipboardOperationType.COPY;
  }
}
class Transformer {
  performEagerTransformations(graph, parser) {
    for (const node of graph.arrayFormulaNodes()) {
      const [newAst, newAddress] = this.transformSingleAst(node.getFormula(graph.lazilyTransformingAstService), node.getAddress(graph.lazilyTransformingAstService));
      const cachedAst = parser.rememberNewAst(newAst);
      node.setFormula(cachedAst);
      node.setAddress(newAddress);
    }
  }
  transformSingleAst(ast, address) {
    const newAst = this.transformAst(ast, address);
    const newAddress = this.fixNodeAddress(address);
    return [newAst, newAddress];
  }
  transformAst(ast, address) {
    switch (ast.type) {
      case AstNodeType.CELL_REFERENCE: {
        return this.transformCellReferenceAst(ast, address);
      }
      case AstNodeType.CELL_RANGE: {
        return this.transformCellRangeAst(ast, address);
      }
      case AstNodeType.COLUMN_RANGE: {
        return this.transformColumnRangeAst(ast, address);
      }
      case AstNodeType.ROW_RANGE: {
        return this.transformRowRangeAst(ast, address);
      }
      case AstNodeType.EMPTY:
      case AstNodeType.ERROR:
      case AstNodeType.NUMBER:
      case AstNodeType.NAMED_EXPRESSION:
      case AstNodeType.ERROR_WITH_RAW_INPUT:
      case AstNodeType.STRING: {
        return ast;
      }
      case AstNodeType.PERCENT_OP:
      case AstNodeType.MINUS_UNARY_OP:
      case AstNodeType.PLUS_UNARY_OP: {
        return Object.assign(Object.assign({}, ast), {
          value: this.transformAst(ast.value, address)
        });
      }
      case AstNodeType.FUNCTION_CALL: {
        return Object.assign(Object.assign({}, ast), {
          procedureName: ast.procedureName,
          args: ast.args.map((arg) => this.transformAst(arg, address))
        });
      }
      case AstNodeType.PARENTHESIS: {
        return Object.assign(Object.assign({}, ast), {
          expression: this.transformAst(ast.expression, address)
        });
      }
      case AstNodeType.ARRAY: {
        return Object.assign(Object.assign({}, ast), {
          args: ast.args.map((row) => row.map((val) => this.transformAst(val, address)))
        });
      }
      default: {
        return Object.assign(Object.assign({}, ast), {
          left: this.transformAst(ast.left, address),
          right: this.transformAst(ast.right, address)
        });
      }
    }
  }
  transformCellReferenceAst(ast, formulaAddress) {
    const newCellAddress = this.transformCellAddress(ast.reference, formulaAddress);
    if (newCellAddress instanceof CellAddress) {
      return Object.assign(Object.assign({}, ast), {
        reference: newCellAddress
      });
    } else if (newCellAddress === ErrorType.REF) {
      return buildCellErrorAst(new CellError(ErrorType.REF));
    } else {
      return ast;
    }
  }
  transformCellRangeAst(ast, formulaAddress) {
    const newRange = this.transformCellRange(ast.start, ast.end, formulaAddress);
    if (Array.isArray(newRange)) {
      return Object.assign(Object.assign({}, ast), {
        start: newRange[0],
        end: newRange[1]
      });
    } else if (newRange === ErrorType.REF) {
      return buildCellErrorAst(new CellError(ErrorType.REF));
    } else {
      return ast;
    }
  }
  transformColumnRangeAst(ast, formulaAddress) {
    const newRange = this.transformColumnRange(ast.start, ast.end, formulaAddress);
    if (Array.isArray(newRange)) {
      return Object.assign(Object.assign({}, ast), {
        start: newRange[0],
        end: newRange[1]
      });
    } else if (newRange === ErrorType.REF) {
      return buildCellErrorAst(new CellError(ErrorType.REF));
    } else {
      return ast;
    }
  }
  transformRowRangeAst(ast, formulaAddress) {
    const newRange = this.transformRowRange(ast.start, ast.end, formulaAddress);
    if (Array.isArray(newRange)) {
      return Object.assign(Object.assign({}, ast), {
        start: newRange[0],
        end: newRange[1]
      });
    } else if (newRange === ErrorType.REF) {
      return buildCellErrorAst(new CellError(ErrorType.REF));
    } else {
      return ast;
    }
  }
}
class AddColumnsTransformer extends Transformer {
  constructor(columnsSpan) {
    super();
    this.columnsSpan = columnsSpan;
  }
  get sheet() {
    return this.columnsSpan.sheet;
  }
  isIrreversible() {
    return false;
  }
  transformRowRangeAst(ast, _formulaAddress) {
    return ast;
  }
  transformCellRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformRowRange(_start, _end, _formulaAddress) {
    throw Error("Not implemented");
  }
  transformColumnRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformCellAddress(dependencyAddress, formulaAddress) {
    const absoluteDependencySheet = absoluteSheetReference(dependencyAddress, formulaAddress);
    if (absoluteDependencySheet !== this.columnsSpan.sheet && formulaAddress.sheet !== this.columnsSpan.sheet) {
      return false;
    }
    const absolutizedDependencyAddress = dependencyAddress.toSimpleColumnAddress(formulaAddress);
    if (absoluteDependencySheet === this.columnsSpan.sheet && formulaAddress.sheet !== this.columnsSpan.sheet) {
      if (this.columnsSpan.columnStart <= absolutizedDependencyAddress.col) {
        return dependencyAddress.shiftedByColumns(this.columnsSpan.numberOfColumns);
      } else {
        return false;
      }
    }
    if (formulaAddress.sheet === this.columnsSpan.sheet && absoluteDependencySheet !== this.columnsSpan.sheet) {
      if (dependencyAddress.isColumnAbsolute()) {
        return false;
      }
      if (formulaAddress.col < this.columnsSpan.columnStart) {
        return false;
      }
      return dependencyAddress.shiftedByColumns(-this.columnsSpan.numberOfColumns);
    }
    if (dependencyAddress.isColumnAbsolute()) {
      if (dependencyAddress.col < this.columnsSpan.columnStart) {
        return false;
      } else {
        return dependencyAddress.shiftedByColumns(this.columnsSpan.numberOfColumns);
      }
    } else {
      const absolutizedDependencyAddress2 = dependencyAddress.toSimpleColumnAddress(formulaAddress);
      if (absolutizedDependencyAddress2.col < this.columnsSpan.columnStart) {
        if (formulaAddress.col < this.columnsSpan.columnStart) {
          return false;
        } else {
          return dependencyAddress.shiftedByColumns(-this.columnsSpan.numberOfColumns);
        }
      } else {
        if (formulaAddress.col < this.columnsSpan.columnStart) {
          return dependencyAddress.shiftedByColumns(this.columnsSpan.numberOfColumns);
        } else {
          return false;
        }
      }
    }
  }
  fixNodeAddress(address) {
    if (this.columnsSpan.sheet === address.sheet && this.columnsSpan.columnStart <= address.col) {
      return Object.assign(Object.assign({}, address), {
        col: address.col + this.columnsSpan.numberOfColumns
      });
    } else {
      return address;
    }
  }
  transformRange(start, end, formulaAddress) {
    const newStart = this.transformCellAddress(start, formulaAddress);
    const newEnd = this.transformCellAddress(end, formulaAddress);
    if (newStart === ErrorType.REF || newEnd === ErrorType.REF) {
      return ErrorType.REF;
    } else if (newStart || newEnd) {
      return [newStart || start, newEnd || end];
    } else {
      return false;
    }
  }
}
class AddRowsTransformer extends Transformer {
  constructor(rowsSpan) {
    super();
    this.rowsSpan = rowsSpan;
  }
  get sheet() {
    return this.rowsSpan.sheet;
  }
  isIrreversible() {
    return false;
  }
  transformColumnRangeAst(ast, _formulaAddress) {
    return ast;
  }
  transformCellRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformRowRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformColumnRange(_start, _end, _formulaAddress) {
    throw Error("Not implemented");
  }
  transformCellAddress(dependencyAddress, formulaAddress) {
    const absoluteDependencySheet = absoluteSheetReference(dependencyAddress, formulaAddress);
    if (absoluteDependencySheet !== this.rowsSpan.sheet && formulaAddress.sheet !== this.rowsSpan.sheet) {
      return false;
    }
    const absolutizedDependencyAddress = dependencyAddress.toSimpleRowAddress(formulaAddress);
    if (absoluteDependencySheet === this.rowsSpan.sheet && formulaAddress.sheet !== this.rowsSpan.sheet) {
      if (this.rowsSpan.rowStart <= absolutizedDependencyAddress.row) {
        return dependencyAddress.shiftedByRows(this.rowsSpan.numberOfRows);
      } else {
        return false;
      }
    }
    if (formulaAddress.sheet === this.rowsSpan.sheet && absoluteDependencySheet !== this.rowsSpan.sheet) {
      if (dependencyAddress.isRowAbsolute()) {
        return false;
      }
      if (formulaAddress.row < this.rowsSpan.rowStart) {
        return false;
      }
      return dependencyAddress.shiftedByRows(-this.rowsSpan.numberOfRows);
    }
    if (dependencyAddress.isRowAbsolute()) {
      if (dependencyAddress.row < this.rowsSpan.rowStart) {
        return false;
      } else {
        return dependencyAddress.shiftedByRows(this.rowsSpan.numberOfRows);
      }
    } else {
      if (absolutizedDependencyAddress.row < this.rowsSpan.rowStart) {
        if (formulaAddress.row < this.rowsSpan.rowStart) {
          return false;
        } else {
          return dependencyAddress.shiftedByRows(-this.rowsSpan.numberOfRows);
        }
      } else {
        if (formulaAddress.row < this.rowsSpan.rowStart) {
          return dependencyAddress.shiftedByRows(this.rowsSpan.numberOfRows);
        } else {
          return false;
        }
      }
    }
  }
  fixNodeAddress(address) {
    if (this.rowsSpan.sheet === address.sheet && this.rowsSpan.rowStart <= address.row) {
      return Object.assign(Object.assign({}, address), {
        row: address.row + this.rowsSpan.numberOfRows
      });
    } else {
      return address;
    }
  }
  transformRange(start, end, formulaAddress) {
    const newStart = this.transformCellAddress(start, formulaAddress);
    const newEnd = this.transformCellAddress(end, formulaAddress);
    if (newStart === ErrorType.REF || newEnd === ErrorType.REF) {
      return ErrorType.REF;
    } else if (newStart || newEnd) {
      return [newStart || start, newEnd || end];
    } else {
      return false;
    }
  }
}
class CleanOutOfScopeDependenciesTransformer extends Transformer {
  constructor(sheet) {
    super();
    this.sheet = sheet;
  }
  isIrreversible() {
    return true;
  }
  fixNodeAddress(address) {
    return address;
  }
  transformCellAddress(dependencyAddress, formulaAddress) {
    return dependencyAddress.isInvalid(formulaAddress) ? ErrorType.REF : false;
  }
  transformCellRange(start, end, formulaAddress) {
    return start.isInvalid(formulaAddress) || end.isInvalid(formulaAddress) ? ErrorType.REF : false;
  }
  transformColumnRange(start, end, formulaAddress) {
    return start.isInvalid(formulaAddress) || end.isInvalid(formulaAddress) ? ErrorType.REF : false;
  }
  transformRowRange(start, end, formulaAddress) {
    return start.isInvalid(formulaAddress) || end.isInvalid(formulaAddress) ? ErrorType.REF : false;
  }
}
class MoveCellsTransformer extends Transformer {
  constructor(sourceRange, toRight, toBottom, toSheet) {
    super();
    this.sourceRange = sourceRange;
    this.toRight = toRight;
    this.toBottom = toBottom;
    this.toSheet = toSheet;
    this.dependentFormulaTransformer = new DependentFormulaTransformer(sourceRange, toRight, toBottom, toSheet);
  }
  get sheet() {
    return this.sourceRange.sheet;
  }
  isIrreversible() {
    return true;
  }
  transformSingleAst(ast, address) {
    if (this.sourceRange.addressInRange(address)) {
      const newAst = this.transformAst(ast, address);
      return [newAst, this.fixNodeAddress(address)];
    } else {
      return this.dependentFormulaTransformer.transformSingleAst(ast, address);
    }
  }
  fixNodeAddress(address) {
    return simpleCellAddress(this.toSheet, address.col + this.toRight, address.row + this.toBottom);
  }
  transformCellAddress(dependencyAddress, formulaAddress) {
    return this.transformAddress(dependencyAddress, formulaAddress);
  }
  transformCellRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformColumnRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformRowRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformAddress(dependencyAddress, formulaAddress) {
    const sourceRange = this.sourceRange;
    if (dependencyAddress instanceof CellAddress) {
      const absoluteDependencyAddress = dependencyAddress.toSimpleCellAddress(formulaAddress);
      if (sourceRange.addressInRange(absoluteDependencyAddress)) {
        return dependencyAddress.shiftAbsoluteDimensions(this.toRight, this.toBottom);
      }
    }
    return dependencyAddress.shiftRelativeDimensions(-this.toRight, -this.toBottom);
  }
  transformRange(start, end, formulaAddress) {
    const sourceRange = this.sourceRange;
    if (start instanceof CellAddress && end instanceof CellAddress) {
      const absoluteStart = start.toSimpleCellAddress(formulaAddress);
      const absoluteEnd = end.toSimpleCellAddress(formulaAddress);
      if (sourceRange.addressInRange(absoluteStart) && sourceRange.addressInRange(absoluteEnd)) {
        return [start.shiftAbsoluteDimensions(this.toRight, this.toBottom), end.shiftAbsoluteDimensions(this.toRight, this.toBottom)];
      }
    }
    return [start.shiftRelativeDimensions(-this.toRight, -this.toBottom), end.shiftRelativeDimensions(-this.toRight, -this.toBottom)];
  }
}
class DependentFormulaTransformer extends Transformer {
  constructor(sourceRange, toRight, toBottom, toSheet) {
    super();
    this.sourceRange = sourceRange;
    this.toRight = toRight;
    this.toBottom = toBottom;
    this.toSheet = toSheet;
  }
  get sheet() {
    return this.sourceRange.sheet;
  }
  isIrreversible() {
    return true;
  }
  fixNodeAddress(address) {
    return address;
  }
  transformCellAddress(dependencyAddress, formulaAddress) {
    if (this.shouldMove(dependencyAddress, formulaAddress)) {
      return dependencyAddress.moved(this.toSheet, this.toRight, this.toBottom);
    }
    return false;
  }
  transformCellRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformColumnRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformRowRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  shouldMove(dependencyAddress, formulaAddress) {
    if (dependencyAddress instanceof CellAddress) {
      return this.sourceRange.addressInRange(dependencyAddress.toSimpleCellAddress(formulaAddress));
    } else if (dependencyAddress instanceof RowAddress) {
      return this.sourceRange.rowInRange(dependencyAddress.toSimpleRowAddress(formulaAddress)) && !this.sourceRange.isFinite();
    } else {
      return this.sourceRange.columnInRange(dependencyAddress.toSimpleColumnAddress(formulaAddress)) && !this.sourceRange.isFinite();
    }
  }
  transformRange(start, end, formulaAddress) {
    const newStart = this.transformCellAddress(start, formulaAddress);
    const newEnd = this.transformCellAddress(end, formulaAddress);
    if (newStart && newEnd) {
      return [newStart, newEnd];
    }
    return false;
  }
}
class RemoveColumnsTransformer extends Transformer {
  constructor(columnsSpan) {
    super();
    this.columnsSpan = columnsSpan;
  }
  get sheet() {
    return this.columnsSpan.sheet;
  }
  isIrreversible() {
    return true;
  }
  transformRowRangeAst(ast, _formulaAddress) {
    return ast;
  }
  transformCellRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformRowRange(_start, _end, _formulaAddress) {
    throw Error("Not implemented");
  }
  transformColumnRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformCellAddress(dependencyAddress, formulaAddress) {
    const absoluteDependencySheet = absoluteSheetReference(dependencyAddress, formulaAddress);
    if (this.columnsSpan.sheet !== formulaAddress.sheet && this.columnsSpan.sheet !== absoluteDependencySheet) {
      return false;
    }
    if (this.columnsSpan.sheet !== formulaAddress.sheet && this.columnsSpan.sheet === absoluteDependencySheet) {
      const absoluteDependencyAddress = dependencyAddress.toSimpleColumnAddress(formulaAddress);
      if (absoluteDependencyAddress.col < this.columnsSpan.columnStart) {
        return false;
      } else if (absoluteDependencyAddress.col > this.columnsSpan.columnEnd) {
        return dependencyAddress.shiftedByColumns(-this.columnsSpan.numberOfColumns);
      }
    }
    if (this.columnsSpan.sheet === formulaAddress.sheet && this.columnsSpan.sheet !== absoluteDependencySheet) {
      if (dependencyAddress.isColumnAbsolute()) {
        return false;
      } else {
        if (formulaAddress.col < this.columnsSpan.columnStart) {
          return false;
        } else if (formulaAddress.col > this.columnsSpan.columnEnd) {
          return dependencyAddress.shiftedByColumns(this.columnsSpan.numberOfColumns);
        }
      }
    }
    if (this.columnsSpan.sheet === formulaAddress.sheet && this.columnsSpan.sheet === absoluteDependencySheet) {
      if (dependencyAddress.isColumnAbsolute()) {
        if (dependencyAddress.col < this.columnsSpan.columnStart) {
          return false;
        } else if (dependencyAddress.col > this.columnsSpan.columnEnd) {
          return dependencyAddress.shiftedByColumns(-this.columnsSpan.numberOfColumns);
        }
      } else {
        const absoluteDependencyAddress = dependencyAddress.toSimpleColumnAddress(formulaAddress);
        if (absoluteDependencyAddress.col < this.columnsSpan.columnStart) {
          if (formulaAddress.col < this.columnsSpan.columnStart) {
            return false;
          } else if (formulaAddress.col > this.columnsSpan.columnEnd) {
            return dependencyAddress.shiftedByColumns(this.columnsSpan.numberOfColumns);
          }
        } else if (absoluteDependencyAddress.col > this.columnsSpan.columnEnd) {
          if (formulaAddress.col < this.columnsSpan.columnStart) {
            return dependencyAddress.shiftedByColumns(-this.columnsSpan.numberOfColumns);
          } else if (formulaAddress.col > this.columnsSpan.columnEnd) {
            return false;
          }
        }
      }
    }
    return ErrorType.REF;
  }
  fixNodeAddress(address) {
    if (this.columnsSpan.sheet === address.sheet && this.columnsSpan.columnStart <= address.col) {
      return Object.assign(Object.assign({}, address), {
        col: address.col - this.columnsSpan.numberOfColumns
      });
    } else {
      return address;
    }
  }
  transformRange(start, end, formulaAddress) {
    const startSheet = absoluteSheetReference(start, formulaAddress);
    let actualStart = start;
    let actualEnd = end;
    if (this.columnsSpan.sheet === startSheet) {
      const startSCA = start.toSimpleColumnAddress(formulaAddress);
      const endSCA = end.toSimpleColumnAddress(formulaAddress);
      if (this.columnsSpan.columnStart <= startSCA.col && this.columnsSpan.columnEnd >= endSCA.col) {
        return ErrorType.REF;
      }
      if (startSCA.col >= this.columnsSpan.columnStart && startSCA.col <= this.columnsSpan.columnEnd) {
        actualStart = start.shiftedByColumns(this.columnsSpan.columnEnd - startSCA.col + 1);
      }
      if (endSCA.col >= this.columnsSpan.columnStart && endSCA.col <= this.columnsSpan.columnEnd) {
        actualEnd = end.shiftedByColumns(-(endSCA.col - this.columnsSpan.columnStart + 1));
      }
    }
    const newStart = this.transformCellAddress(actualStart, formulaAddress);
    const newEnd = this.transformCellAddress(actualEnd, formulaAddress);
    if (newStart === false && newEnd === false) {
      return [actualStart, actualEnd];
    } else if (newStart === ErrorType.REF || newEnd === ErrorType.REF) {
      throw Error("Cannot happen");
    } else {
      return [newStart || actualStart, newEnd || actualEnd];
    }
  }
}
class RemoveRowsTransformer extends Transformer {
  constructor(rowsSpan) {
    super();
    this.rowsSpan = rowsSpan;
  }
  get sheet() {
    return this.rowsSpan.sheet;
  }
  isIrreversible() {
    return true;
  }
  transformColumnRangeAst(ast, _formulaAddress) {
    return ast;
  }
  transformCellAddress(dependencyAddress, formulaAddress) {
    const absoluteDependencySheet = absoluteSheetReference(dependencyAddress, formulaAddress);
    if (this.rowsSpan.sheet !== formulaAddress.sheet && this.rowsSpan.sheet !== absoluteDependencySheet) {
      return false;
    }
    if (this.rowsSpan.sheet !== formulaAddress.sheet && this.rowsSpan.sheet === absoluteDependencySheet) {
      const absoluteDependencyAddress = dependencyAddress.toSimpleRowAddress(formulaAddress);
      if (absoluteDependencyAddress.row < this.rowsSpan.rowStart) {
        return false;
      } else if (absoluteDependencyAddress.row > this.rowsSpan.rowEnd) {
        return dependencyAddress.shiftedByRows(-this.rowsSpan.numberOfRows);
      }
    }
    if (this.rowsSpan.sheet === formulaAddress.sheet && this.rowsSpan.sheet !== absoluteDependencySheet) {
      if (dependencyAddress.isRowAbsolute()) {
        return false;
      } else {
        if (formulaAddress.row < this.rowsSpan.rowStart) {
          return false;
        } else if (formulaAddress.row > this.rowsSpan.rowEnd) {
          return dependencyAddress.shiftedByRows(this.rowsSpan.numberOfRows);
        }
      }
    }
    if (this.rowsSpan.sheet === formulaAddress.sheet && this.rowsSpan.sheet === absoluteDependencySheet) {
      if (dependencyAddress.isRowAbsolute()) {
        if (dependencyAddress.row < this.rowsSpan.rowStart) {
          return false;
        } else if (dependencyAddress.row > this.rowsSpan.rowEnd) {
          return dependencyAddress.shiftedByRows(-this.rowsSpan.numberOfRows);
        }
      } else {
        const absoluteDependencyAddress = dependencyAddress.toSimpleRowAddress(formulaAddress);
        if (absoluteDependencyAddress.row < this.rowsSpan.rowStart) {
          if (formulaAddress.row < this.rowsSpan.rowStart) {
            return false;
          } else if (formulaAddress.row > this.rowsSpan.rowEnd) {
            return dependencyAddress.shiftedByRows(this.rowsSpan.numberOfRows);
          }
        } else if (absoluteDependencyAddress.row > this.rowsSpan.rowEnd) {
          if (formulaAddress.row < this.rowsSpan.rowStart) {
            return dependencyAddress.shiftedByRows(-this.rowsSpan.numberOfRows);
          } else if (formulaAddress.row > this.rowsSpan.rowEnd) {
            return false;
          }
        }
      }
    }
    return ErrorType.REF;
  }
  transformCellRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformRowRange(start, end, formulaAddress) {
    return this.transformRange(start, end, formulaAddress);
  }
  transformColumnRange(_start, _end, _formulaAddress) {
    throw Error("Not implemented");
  }
  fixNodeAddress(address) {
    if (this.rowsSpan.sheet === address.sheet && this.rowsSpan.rowStart <= address.row) {
      return Object.assign(Object.assign({}, address), {
        row: address.row - this.rowsSpan.numberOfRows
      });
    } else {
      return address;
    }
  }
  transformRange(start, end, formulaAddress) {
    const startSheet = absoluteSheetReference(start, formulaAddress);
    let actualStart = start;
    let actualEnd = end;
    if (this.rowsSpan.sheet === startSheet) {
      const startSCA = start.toSimpleRowAddress(formulaAddress);
      const endSCA = end.toSimpleRowAddress(formulaAddress);
      if (this.rowsSpan.rowStart <= startSCA.row && this.rowsSpan.rowEnd >= endSCA.row) {
        return ErrorType.REF;
      }
      if (startSCA.row >= this.rowsSpan.rowStart && startSCA.row <= this.rowsSpan.rowEnd) {
        actualStart = start.shiftedByRows(this.rowsSpan.rowEnd - startSCA.row + 1);
      }
      if (endSCA.row >= this.rowsSpan.rowStart && endSCA.row <= this.rowsSpan.rowEnd) {
        actualEnd = end.shiftedByRows(-(endSCA.row - this.rowsSpan.rowStart + 1));
      }
    }
    const newStart = this.transformCellAddress(actualStart, formulaAddress);
    const newEnd = this.transformCellAddress(actualEnd, formulaAddress);
    if (newStart === false && newEnd === false) {
      return [actualStart, actualEnd];
    } else if (newStart === ErrorType.REF || newEnd === ErrorType.REF) {
      return ErrorType.REF;
    } else {
      return [newStart || actualStart, newEnd || actualEnd];
    }
  }
}
class RenameSheetTransformer extends Transformer {
  constructor(sheetIdToKeep, sheetBeingMerged) {
    super();
    this.sheetIdToKeep = sheetIdToKeep;
    this.sheetBeingMerged = sheetBeingMerged;
  }
  /**
   * Returns id of sheet that survives merge operation.
   *
   * @returns {number} sheet identifier.
   */
  get sheet() {
    return this.sheetIdToKeep;
  }
  /**
   * Sheet merge cannot be undone because original sheet id is lost.
   *
   * @returns {boolean} always true to indicate transformation irreversibility.
   */
  isIrreversible() {
    return true;
  }
  /**
   * Updates cell address sheet when it points to merged sheet.
   *
   * @param {T} dependencyAddress - dependency address needing sheet update.
   * @param {SimpleCellAddress} _formulaAddress - location of formula (unused but required by base class).
   * @returns {T | false} updated address or false when nothing changes.
   */
  transformCellAddress(dependencyAddress, _formulaAddress) {
    return this.updateSheetInAddress(dependencyAddress);
  }
  /**
   * Updates sheet for both ends of cell range.
   *
   * @param {CellAddress} start - start address of range.
   * @param {CellAddress} end - end address of range.
   * @param {SimpleCellAddress} _formulaAddress - formula location (unused).
   * @returns {[CellAddress, CellAddress] | false} updated range tuple or false when unchanged.
   */
  transformCellRange(start, end, _formulaAddress) {
    return this.transformRange(start, end);
  }
  /**
   * Updates sheet for both ends of column range.
   *
   * @param {ColumnAddress} start - beginning column of range.
   * @param {ColumnAddress} end - ending column of range.
   * @param {SimpleCellAddress} _formulaAddress - formula location (unused).
   * @returns {[ColumnAddress, ColumnAddress] | false} updated column range or false.
   */
  transformColumnRange(start, end, _formulaAddress) {
    return this.transformRange(start, end);
  }
  /**
   * Updates sheet for both ends of row range.
   *
   * @param {RowAddress} start - beginning row address.
   * @param {RowAddress} end - ending row address.
   * @param {SimpleCellAddress} _formulaAddress - formula location (unused).
   * @returns {[RowAddress, RowAddress] | false} updated row range or false.
   */
  transformRowRange(start, end, _formulaAddress) {
    return this.transformRange(start, end);
  }
  /**
   * Node addresses are already absolute, so no change is needed.
   *
   * @param {SimpleCellAddress} address - node address to inspect.
   * @returns {SimpleCellAddress} original address unchanged.
   */
  fixNodeAddress(address) {
    return address;
  }
  /**
   * Updates sheet identifier for both range ends if needed.
   *
   * @param {T} start - range start address.
   * @param {T} end - range end address.
   * @returns {[T, T] | false} tuple with updated addresses or false when no updates happen.
   */
  transformRange(start, end) {
    const newStart = this.updateSheetInAddress(start);
    const newEnd = this.updateSheetInAddress(end);
    if (newStart || newEnd) {
      return [newStart || start, newEnd || end];
    }
    return false;
  }
  /**
   * Replaces sheet id in address when it points to merged sheet.
   *
   * @param {T} address - address to update.
   * @returns {T | false} address with new sheet id or false when no change occurs.
   */
  updateSheetInAddress(address) {
    if (address.sheet === this.sheetBeingMerged) {
      return address.withSheet(this.sheetIdToKeep);
    }
    return false;
  }
}
class RemoveRowsCommand {
  constructor(sheet, indexes) {
    this.sheet = sheet;
    this.indexes = indexes;
  }
  normalizedIndexes() {
    return normalizeRemovedIndexes(this.indexes);
  }
  rowsSpans() {
    return this.normalizedIndexes().map((normalizedIndex) => RowsSpan.fromNumberOfRows(this.sheet, normalizedIndex[0], normalizedIndex[1]));
  }
}
class AddRowsCommand {
  constructor(sheet, indexes) {
    this.sheet = sheet;
    this.indexes = indexes;
  }
  normalizedIndexes() {
    return normalizeAddedIndexes(this.indexes);
  }
  rowsSpans() {
    return this.normalizedIndexes().map((normalizedIndex) => RowsSpan.fromNumberOfRows(this.sheet, normalizedIndex[0], normalizedIndex[1]));
  }
}
class AddColumnsCommand {
  constructor(sheet, indexes) {
    this.sheet = sheet;
    this.indexes = indexes;
  }
  normalizedIndexes() {
    return normalizeAddedIndexes(this.indexes);
  }
  columnsSpans() {
    return this.normalizedIndexes().map((normalizedIndex) => ColumnsSpan.fromNumberOfColumns(this.sheet, normalizedIndex[0], normalizedIndex[1]));
  }
}
class RemoveColumnsCommand {
  constructor(sheet, indexes) {
    this.sheet = sheet;
    this.indexes = indexes;
  }
  normalizedIndexes() {
    return normalizeRemovedIndexes(this.indexes);
  }
  columnsSpans() {
    return this.normalizedIndexes().map((normalizedIndex) => ColumnsSpan.fromNumberOfColumns(this.sheet, normalizedIndex[0], normalizedIndex[1]));
  }
}
class Operations {
  constructor(config, dependencyGraph, columnSearch, cellContentParser, parser, stats, lazilyTransformingAstService, namedExpressions, arraySizePredictor) {
    this.dependencyGraph = dependencyGraph;
    this.columnSearch = columnSearch;
    this.cellContentParser = cellContentParser;
    this.parser = parser;
    this.stats = stats;
    this.lazilyTransformingAstService = lazilyTransformingAstService;
    this.namedExpressions = namedExpressions;
    this.arraySizePredictor = arraySizePredictor;
    this.changes = ContentChanges.empty();
    this.allocateNamedExpressionAddressSpace();
    this.maxColumns = config.maxColumns;
    this.maxRows = config.maxRows;
  }
  get sheetMapping() {
    return this.dependencyGraph.sheetMapping;
  }
  get addressMapping() {
    return this.dependencyGraph.addressMapping;
  }
  removeRows(cmd) {
    const rowsRemovals = [];
    for (const rowsToRemove of cmd.rowsSpans()) {
      const rowsRemoval = this.doRemoveRows(rowsToRemove);
      if (rowsRemoval) {
        rowsRemovals.push(rowsRemoval);
      }
    }
    return rowsRemovals;
  }
  addRows(cmd) {
    for (const addedRows of cmd.rowsSpans()) {
      this.doAddRows(addedRows);
    }
  }
  addColumns(cmd) {
    for (const addedColumns of cmd.columnsSpans()) {
      this.doAddColumns(addedColumns);
    }
  }
  removeColumns(cmd) {
    const columnsRemovals = [];
    for (const columnsToRemove of cmd.columnsSpans()) {
      const columnsRemoval = this.doRemoveColumns(columnsToRemove);
      if (columnsRemoval) {
        columnsRemovals.push(columnsRemoval);
      }
    }
    return columnsRemovals;
  }
  /**
   * Clears the sheet content.
   */
  clearSheet(sheetId) {
    this.dependencyGraph.clearSheet(sheetId);
    this.columnSearch.removeSheet(sheetId);
  }
  /**
   * Adds a new sheet to the workbook.
   */
  addSheet(name) {
    const sheetId = this.sheetMapping.addSheet(name);
    this.dependencyGraph.addSheet(sheetId);
    return {
      sheetName: this.sheetMapping.getSheetNameOrThrowError(sheetId),
      sheetId
    };
  }
  /**
   * Adds a sheet with a specific ID for redo operations.
   */
  addSheetWithId(sheetId, name) {
    this.sheetMapping.addSheetWithId(sheetId, name);
    this.dependencyGraph.addSheet(sheetId);
  }
  /**
   * Adds a placeholder sheet with a specific ID for undo operations.
   * Used to restore previously merged placeholder sheets.
   *
   * Note: Unlike `addSheetWithId`, this does NOT call `dependencyGraph.addSheet()`
   * because placeholders don't need dirty marking or strategy changes - they only
   * need to exist in the mappings so formulas can reference them again.
   */
  addPlaceholderSheetWithId(sheetId, name) {
    this.sheetMapping.addPlaceholderWithId(sheetId, name);
    this.addressMapping.addSheetStrategyPlaceholderIfNotExists(sheetId);
  }
  /**
   * Removes a sheet from the workbook.
   */
  removeSheet(sheetId) {
    this.dependencyGraph.removeSheet(sheetId);
    this.columnSearch.removeSheet(sheetId);
    const scopedNamedExpressions = this.namedExpressions.getAllNamedExpressionsForScope(sheetId).map((namedExpression) => this.removeNamedExpression(namedExpression.normalizeExpressionName(), sheetId));
    return scopedNamedExpressions;
  }
  /**
   * Removes a sheet from the workbook by name.
   */
  removeSheetByName(sheetName) {
    const sheetId = this.sheetMapping.getSheetIdOrThrowError(sheetName);
    return this.removeSheet(sheetId);
  }
  /**
   * Renames a sheet in the workbook.
   */
  renameSheet(sheetId, newName) {
    const {
      previousDisplayName,
      mergedWithPlaceholderSheet
    } = this.sheetMapping.renameSheet(sheetId, newName);
    let version;
    if (mergedWithPlaceholderSheet !== void 0) {
      this.dependencyGraph.mergeSheets(sheetId, mergedWithPlaceholderSheet);
      this.stats.measure(StatType.TRANSFORM_ASTS, () => {
        const transformation = new RenameSheetTransformer(sheetId, mergedWithPlaceholderSheet);
        transformation.performEagerTransformations(this.dependencyGraph, this.parser);
        version = this.lazilyTransformingAstService.addTransformation(transformation);
      });
    }
    return {
      previousDisplayName,
      version,
      mergedPlaceholderSheetId: mergedWithPlaceholderSheet
    };
  }
  moveRows(sheet, startRow, numberOfRows, targetRow) {
    const rowsToAdd = RowsSpan.fromNumberOfRows(sheet, targetRow, numberOfRows);
    this.lazilyTransformingAstService.beginCombinedMode(sheet);
    this.doAddRows(rowsToAdd);
    if (targetRow < startRow) {
      startRow += numberOfRows;
    }
    const startAddress = simpleCellAddress(sheet, 0, startRow);
    const targetAddress = simpleCellAddress(sheet, 0, targetRow);
    this.moveCells(startAddress, Number.POSITIVE_INFINITY, numberOfRows, targetAddress);
    const rowsToRemove = RowsSpan.fromNumberOfRows(sheet, startRow, numberOfRows);
    this.doRemoveRows(rowsToRemove);
    return this.lazilyTransformingAstService.commitCombinedMode();
  }
  moveColumns(sheet, startColumn, numberOfColumns, targetColumn) {
    const columnsToAdd = ColumnsSpan.fromNumberOfColumns(sheet, targetColumn, numberOfColumns);
    this.lazilyTransformingAstService.beginCombinedMode(sheet);
    this.doAddColumns(columnsToAdd);
    if (targetColumn < startColumn) {
      startColumn += numberOfColumns;
    }
    const startAddress = simpleCellAddress(sheet, startColumn, 0);
    const targetAddress = simpleCellAddress(sheet, targetColumn, 0);
    this.moveCells(startAddress, numberOfColumns, Number.POSITIVE_INFINITY, targetAddress);
    const columnsToRemove = ColumnsSpan.fromNumberOfColumns(sheet, startColumn, numberOfColumns);
    this.doRemoveColumns(columnsToRemove);
    return this.lazilyTransformingAstService.commitCombinedMode();
  }
  moveCells(sourceLeftCorner, width, height, destinationLeftCorner) {
    this.ensureItIsPossibleToMoveCells(sourceLeftCorner, width, height, destinationLeftCorner);
    const sourceRange = AbsoluteCellRange.spanFrom(sourceLeftCorner, width, height);
    const targetRange = AbsoluteCellRange.spanFrom(destinationLeftCorner, width, height);
    const toRight = destinationLeftCorner.col - sourceLeftCorner.col;
    const toBottom = destinationLeftCorner.row - sourceLeftCorner.row;
    const toSheet = destinationLeftCorner.sheet;
    const currentDataAtTarget = this.getRangeClipboardCells(targetRange);
    const valuesToRemove = this.dependencyGraph.rawValuesFromRange(targetRange);
    this.columnSearch.removeValues(valuesToRemove);
    const valuesToMove = this.dependencyGraph.rawValuesFromRange(sourceRange);
    this.columnSearch.moveValues(valuesToMove, toRight, toBottom, toSheet);
    let version = 0;
    this.stats.measure(StatType.TRANSFORM_ASTS, () => {
      const transformation = new MoveCellsTransformer(sourceRange, toRight, toBottom, toSheet);
      transformation.performEagerTransformations(this.dependencyGraph, this.parser);
      version = this.lazilyTransformingAstService.addTransformation(transformation);
    });
    this.dependencyGraph.moveCells(sourceRange, toRight, toBottom, toSheet);
    const addedGlobalNamedExpressions = this.updateNamedExpressionsForMovedCells(sourceLeftCorner, width, height, destinationLeftCorner);
    return {
      version,
      overwrittenCellsData: currentDataAtTarget,
      addedGlobalNamedExpressions
    };
  }
  setRowOrder(sheetId, rowMapping) {
    const buffer = [];
    let oldContent = [];
    for (const [source, target] of rowMapping) {
      if (source !== target) {
        const rowRange = AbsoluteCellRange.spanFrom({
          sheet: sheetId,
          col: 0,
          row: source
        }, Infinity, 1);
        const row = this.getRangeClipboardCells(rowRange);
        oldContent = oldContent.concat(row);
        buffer.push(row.map(([{
          sheet,
          col
        }, cell]) => [{
          sheet,
          col,
          row: target
        }, cell]));
      }
    }
    buffer.forEach((row) => this.restoreClipboardCells(sheetId, row.values()));
    return oldContent;
  }
  setColumnOrder(sheetId, columnMapping) {
    const buffer = [];
    let oldContent = [];
    for (const [source, target] of columnMapping) {
      if (source !== target) {
        const rowRange = AbsoluteCellRange.spanFrom({
          sheet: sheetId,
          col: source,
          row: 0
        }, 1, Infinity);
        const column = this.getRangeClipboardCells(rowRange);
        oldContent = oldContent.concat(column);
        buffer.push(column.map(([{
          sheet,
          col: _col,
          row
        }, cell]) => [{
          sheet,
          col: target,
          row
        }, cell]));
      }
    }
    buffer.forEach((column) => this.restoreClipboardCells(sheetId, column.values()));
    return oldContent;
  }
  addNamedExpression(expressionName, expression, sheetId, options) {
    const namedExpression = this.namedExpressions.addNamedExpression(expressionName, sheetId, options);
    this.storeNamedExpressionInCell(namedExpression.address, expression);
    this.adjustNamedExpressionEdges(namedExpression, expressionName, sheetId);
  }
  restoreNamedExpression(namedExpression, content, sheetId) {
    const expressionName = namedExpression.displayName;
    this.restoreCell(namedExpression.address, content);
    const restoredNamedExpression = this.namedExpressions.restoreNamedExpression(namedExpression, sheetId);
    this.adjustNamedExpressionEdges(restoredNamedExpression, expressionName, sheetId);
  }
  changeNamedExpressionExpression(expressionName, newExpression, sheetId, options) {
    const namedExpression = this.namedExpressions.namedExpressionForScope(expressionName, sheetId);
    if (!namedExpression) {
      throw new NamedExpressionDoesNotExistError(expressionName);
    }
    const oldNamedExpression = namedExpression.copy();
    namedExpression.options = options;
    const content = this.getClipboardCell(namedExpression.address);
    this.storeNamedExpressionInCell(namedExpression.address, newExpression);
    return [oldNamedExpression, content];
  }
  removeNamedExpression(expressionName, sheetId) {
    const namedExpression = this.namedExpressions.namedExpressionForScope(expressionName, sheetId);
    if (!namedExpression) {
      throw new NamedExpressionDoesNotExistError(expressionName);
    }
    this.namedExpressions.remove(namedExpression.displayName, sheetId);
    const content = this.getClipboardCell(namedExpression.address);
    if (sheetId !== void 0) {
      const globalNamedExpression = this.namedExpressions.workbookNamedExpressionOrPlaceholder(expressionName);
      this.dependencyGraph.exchangeNode(namedExpression.address, globalNamedExpression.address);
    } else {
      this.dependencyGraph.setCellEmpty(namedExpression.address);
    }
    return [namedExpression, content];
  }
  ensureItIsPossibleToMoveCells(sourceLeftCorner, width, height, destinationLeftCorner) {
    if (isColOrRowInvalid(sourceLeftCorner) || !(isPositiveInteger$1(width) && isPositiveInteger$1(height) || isRowOrColumnRange(sourceLeftCorner, width, height)) || isColOrRowInvalid(destinationLeftCorner) || !this.sheetMapping.hasSheetWithId(sourceLeftCorner.sheet) || !this.sheetMapping.hasSheetWithId(destinationLeftCorner.sheet)) {
      throw new InvalidArgumentsError("a valid range of cells to move.");
    }
    const sourceRange = AbsoluteCellRange.spanFrom(sourceLeftCorner, width, height);
    const targetRange = AbsoluteCellRange.spanFrom(destinationLeftCorner, width, height);
    if (targetRange.exceedsSheetSizeLimits(this.maxColumns, this.maxRows)) {
      throw new SheetSizeLimitExceededError();
    }
    if (this.dependencyGraph.arrayMapping.isFormulaArrayInRange(sourceRange)) {
      throw new SourceLocationHasArrayError();
    }
    if (this.dependencyGraph.arrayMapping.isFormulaArrayInRange(targetRange)) {
      throw new TargetLocationHasArrayError();
    }
  }
  restoreClipboardCells(sourceSheetId, cells) {
    const addedNamedExpressions = [];
    for (const [address, clipboardCell] of cells) {
      this.restoreCell(address, clipboardCell);
      if (clipboardCell.type === ClipboardCellType.FORMULA) {
        const {
          dependencies
        } = this.parser.fetchCachedResult(clipboardCell.hash);
        addedNamedExpressions.push(...this.updateNamedExpressionsForTargetAddress(sourceSheetId, address, dependencies));
      }
    }
    return addedNamedExpressions;
  }
  /**
   * Restores a single cell.
   */
  restoreCell(address, clipboardCell) {
    switch (clipboardCell.type) {
      case ClipboardCellType.VALUE: {
        this.setValueToCell(clipboardCell, address);
        break;
      }
      case ClipboardCellType.FORMULA: {
        this.setFormulaToCellFromCache(clipboardCell.hash, address);
        break;
      }
      case ClipboardCellType.EMPTY: {
        this.setCellEmpty(address);
        break;
      }
      case ClipboardCellType.PARSING_ERROR: {
        this.setParsingErrorToCell(clipboardCell.rawInput, clipboardCell.errors, address);
        break;
      }
    }
  }
  getOldContent(address) {
    const vertex = this.dependencyGraph.getCell(address);
    if (vertex === void 0 || vertex instanceof EmptyCellVertex) {
      return [address, {
        type: ClipboardCellType.EMPTY
      }];
    } else if (vertex instanceof ValueCellVertex) {
      return [address, Object.assign({
        type: ClipboardCellType.VALUE
      }, vertex.getValues())];
    } else if (vertex instanceof FormulaVertex) {
      return [vertex.getAddress(this.lazilyTransformingAstService), {
        type: ClipboardCellType.FORMULA,
        hash: this.parser.computeHashFromAst(vertex.getFormula(this.lazilyTransformingAstService))
      }];
    } else if (vertex instanceof ParsingErrorVertex) {
      return [address, {
        type: ClipboardCellType.PARSING_ERROR,
        rawInput: vertex.rawInput,
        errors: vertex.errors
      }];
    }
    throw Error("Trying to copy unsupported type");
  }
  getClipboardCell(address) {
    const vertex = this.dependencyGraph.getCell(address);
    if (vertex === void 0 || vertex instanceof EmptyCellVertex) {
      return {
        type: ClipboardCellType.EMPTY
      };
    } else if (vertex instanceof ValueCellVertex) {
      return Object.assign({
        type: ClipboardCellType.VALUE
      }, vertex.getValues());
    } else if (vertex instanceof ArrayFormulaVertex) {
      const val = vertex.getArrayCellValue(address);
      if (val === EmptyValue) {
        return {
          type: ClipboardCellType.EMPTY
        };
      }
      return {
        type: ClipboardCellType.VALUE,
        parsedValue: val,
        rawValue: vertex.getArrayCellRawValue(address)
      };
    } else if (vertex instanceof ScalarFormulaVertex) {
      return {
        type: ClipboardCellType.FORMULA,
        hash: this.parser.computeHashFromAst(vertex.getFormula(this.lazilyTransformingAstService))
      };
    } else if (vertex instanceof ParsingErrorVertex) {
      return {
        type: ClipboardCellType.PARSING_ERROR,
        rawInput: vertex.rawInput,
        errors: vertex.errors
      };
    }
    throw Error("Trying to copy unsupported type");
  }
  getSheetClipboardCells(sheet) {
    const sheetHeight = this.dependencyGraph.getSheetHeight(sheet);
    const sheetWidth = this.dependencyGraph.getSheetWidth(sheet);
    const arr = new Array(sheetHeight);
    for (let i2 = 0; i2 < sheetHeight; i2++) {
      arr[i2] = new Array(sheetWidth);
      for (let j2 = 0; j2 < sheetWidth; j2++) {
        const address = simpleCellAddress(sheet, j2, i2);
        arr[i2][j2] = this.getClipboardCell(address);
      }
    }
    return arr;
  }
  getRangeClipboardCells(range) {
    const result = [];
    for (const address of range.addresses(this.dependencyGraph)) {
      result.push([address, this.getClipboardCell(address)]);
    }
    return result;
  }
  setCellContent(address, newCellContent) {
    const parsedCellContent = this.cellContentParser.parse(newCellContent);
    const oldContent = this.getOldContent(address);
    if (parsedCellContent instanceof CellContent.Formula) {
      const parserResult = this.parser.parse(parsedCellContent.formula, address);
      const {
        ast,
        errors
      } = parserResult;
      if (errors.length > 0) {
        this.setParsingErrorToCell(parsedCellContent.formula, errors, address);
      } else {
        try {
          const size = this.arraySizePredictor.checkArraySize(ast, address);
          if (size.width <= 0 || size.height <= 0) {
            throw Error("Incorrect array size");
          }
          this.setFormulaToCell(address, size, parserResult);
        } catch (error) {
          if (!error.message) {
            throw error;
          }
          const parsingError2 = {
            type: ParsingErrorType.InvalidRangeSize,
            message: "Invalid range size."
          };
          this.setParsingErrorToCell(parsedCellContent.formula, [parsingError2], address);
        }
      }
    } else if (parsedCellContent instanceof CellContent.Empty) {
      this.setCellEmpty(address);
    } else {
      this.setValueToCell({
        parsedValue: parsedCellContent.value,
        rawValue: newCellContent
      }, address);
    }
    return oldContent;
  }
  setSheetContent(sheetId, newSheetContent) {
    this.clearSheet(sheetId);
    for (let i2 = 0; i2 < newSheetContent.length; i2++) {
      for (let j2 = 0; j2 < newSheetContent[i2].length; j2++) {
        const address = simpleCellAddress(sheetId, j2, i2);
        this.setCellContent(address, newSheetContent[i2][j2]);
      }
    }
  }
  /**
   * Sets cell content to an instance of parsing error.
   * Creates a ParsingErrorVertex and updates the dependency graph and column search index.
   */
  setParsingErrorToCell(rawInput, errors, address) {
    this.removeCellValueFromColumnSearch(address);
    const vertex = new ParsingErrorVertex(errors, rawInput);
    const arrayChanges = this.dependencyGraph.setParsingErrorToCell(address, vertex);
    this.columnSearch.applyChanges(arrayChanges.getChanges());
    this.changes.addAll(arrayChanges);
    this.changes.addChange(vertex.getCellValue(), address);
  }
  /**
   * Sets cell content to a formula.
   * Creates a ScalarFormulaVertex and updates the dependency graph and column search index.
   */
  setFormulaToCell(address, size, {
    ast,
    hasVolatileFunction,
    hasStructuralChangeFunction,
    dependencies
  }) {
    this.removeCellValueFromColumnSearch(address);
    const arrayChanges = this.dependencyGraph.setFormulaToCell(address, ast, absolutizeDependencies(dependencies, address), size, hasVolatileFunction, hasStructuralChangeFunction);
    this.columnSearch.applyChanges(arrayChanges.getChanges());
    this.changes.addAll(arrayChanges);
  }
  /**
   * Sets cell content to a value.
   * Creates a ValueCellVertex and updates the dependency graph and column search index.
   */
  setValueToCell(value, address) {
    this.changeCellValueInColumnSearch(address, value.parsedValue);
    const arrayChanges = this.dependencyGraph.setValueToCell(address, value);
    this.columnSearch.applyChanges(arrayChanges.getChanges().filter((change) => !equalSimpleCellAddress(change.address, address)));
    this.changes.addAll(arrayChanges);
    this.changes.addChange(value.parsedValue, address);
  }
  /**
   * Sets cell content to an empty value.
   * Creates an EmptyCellVertex and updates the dependency graph and column search index.
   */
  setCellEmpty(address) {
    if (this.dependencyGraph.isArrayInternalCell(address)) {
      return;
    }
    this.removeCellValueFromColumnSearch(address);
    const arrayChanges = this.dependencyGraph.setCellEmpty(address);
    this.columnSearch.applyChanges(arrayChanges.getChanges());
    this.changes.addAll(arrayChanges);
    this.changes.addChange(EmptyValue, address);
  }
  setFormulaToCellFromCache(formulaHash, address) {
    const {
      ast,
      hasVolatileFunction,
      hasStructuralChangeFunction,
      dependencies
    } = this.parser.fetchCachedResult(formulaHash);
    const absoluteDependencies = absolutizeDependencies(dependencies, address);
    const [cleanedAst] = new CleanOutOfScopeDependenciesTransformer(address.sheet).transformSingleAst(ast, address);
    this.parser.rememberNewAst(cleanedAst);
    const cleanedDependencies = filterDependenciesOutOfScope(absoluteDependencies);
    const size = this.arraySizePredictor.checkArraySize(ast, address);
    this.dependencyGraph.setFormulaToCell(address, cleanedAst, cleanedDependencies, size, hasVolatileFunction, hasStructuralChangeFunction);
  }
  /**
   * Returns true if row number is outside of given sheet.
   * @param {number} row - row number
   * @param {number} sheet - sheet ID number
   */
  rowEffectivelyNotInSheet(row, sheet) {
    const height = this.dependencyGraph.addressMapping.getSheetHeight(sheet);
    return row >= height;
  }
  getAndClearContentChanges() {
    const changes = this.changes;
    this.changes = ContentChanges.empty();
    return changes;
  }
  /**
   * Forces all formula vertices and column index entries to apply pending lazy
   * transformations, bringing them up to the current LazilyTransformingAstService version.
   * Called before undo of move operations and before compaction.
   */
  forceApplyPostponedTransformations() {
    this.dependencyGraph.forceApplyPostponedTransformations();
    this.columnSearch.forceApplyPostponedTransformations();
  }
  /**
   * Removes multiple rows from sheet. </br>
   * Does nothing if rows are outside of effective sheet size.
   * @param {RowsSpan} rowsToRemove - rows to remove
   */
  doRemoveRows(rowsToRemove) {
    if (this.rowEffectivelyNotInSheet(rowsToRemove.rowStart, rowsToRemove.sheet)) {
      return;
    }
    const removedCells = [];
    for (const [address] of this.dependencyGraph.entriesFromRowsSpan(rowsToRemove)) {
      removedCells.push({
        address,
        cellType: this.getClipboardCell(address)
      });
    }
    const {
      affectedArrays,
      contentChanges
    } = this.dependencyGraph.removeRows(rowsToRemove);
    this.columnSearch.applyChanges(contentChanges.getChanges());
    let version = 0;
    this.stats.measure(StatType.TRANSFORM_ASTS, () => {
      const transformation = new RemoveRowsTransformer(rowsToRemove);
      transformation.performEagerTransformations(this.dependencyGraph, this.parser);
      version = this.lazilyTransformingAstService.addTransformation(transformation);
    });
    this.rewriteAffectedArrays(affectedArrays);
    return {
      version,
      removedCells,
      rowFrom: rowsToRemove.rowStart,
      rowCount: rowsToRemove.numberOfRows
    };
  }
  /**
   * Removes multiple columns from sheet. </br>
   * Does nothing if columns are outside of effective sheet size.
   * @param {ColumnsSpan} columnsToRemove - columns to remove
   */
  doRemoveColumns(columnsToRemove) {
    if (this.columnEffectivelyNotInSheet(columnsToRemove.columnStart, columnsToRemove.sheet)) {
      return;
    }
    const removedCells = [];
    for (const [address] of this.dependencyGraph.entriesFromColumnsSpan(columnsToRemove)) {
      removedCells.push({
        address,
        cellType: this.getClipboardCell(address)
      });
    }
    const {
      affectedArrays,
      contentChanges
    } = this.dependencyGraph.removeColumns(columnsToRemove);
    this.columnSearch.applyChanges(contentChanges.getChanges());
    this.columnSearch.removeColumns(columnsToRemove);
    let version = 0;
    this.stats.measure(StatType.TRANSFORM_ASTS, () => {
      const transformation = new RemoveColumnsTransformer(columnsToRemove);
      transformation.performEagerTransformations(this.dependencyGraph, this.parser);
      version = this.lazilyTransformingAstService.addTransformation(transformation);
    });
    this.rewriteAffectedArrays(affectedArrays);
    return {
      version,
      removedCells,
      columnFrom: columnsToRemove.columnStart,
      columnCount: columnsToRemove.numberOfColumns
    };
  }
  /**
   * Add multiple rows to sheet. </br>
   * Does nothing if rows are outside of effective sheet size.
   * @param {RowsSpan} addedRows - rows to add
   */
  doAddRows(addedRows) {
    if (this.rowEffectivelyNotInSheet(addedRows.rowStart, addedRows.sheet)) {
      return;
    }
    const {
      affectedArrays
    } = this.dependencyGraph.addRows(addedRows);
    this.stats.measure(StatType.TRANSFORM_ASTS, () => {
      const transformation = new AddRowsTransformer(addedRows);
      transformation.performEagerTransformations(this.dependencyGraph, this.parser);
      this.lazilyTransformingAstService.addTransformation(transformation);
    });
    this.rewriteAffectedArrays(affectedArrays);
  }
  rewriteAffectedArrays(affectedArrays) {
    for (const arrayVertex of affectedArrays.values()) {
      if (arrayVertex.array.size.isRef) {
        continue;
      }
      const ast = arrayVertex.getFormula(this.lazilyTransformingAstService);
      const address = arrayVertex.getAddress(this.lazilyTransformingAstService);
      const hash = this.parser.computeHashFromAst(ast);
      this.setFormulaToCellFromCache(hash, address);
    }
  }
  /**
   * Add multiple columns to sheet </br>
   * Does nothing if columns are outside of effective sheet size
   * @param {ColumnsSpan} addedColumns - object containing information about columns to add
   */
  doAddColumns(addedColumns) {
    if (this.columnEffectivelyNotInSheet(addedColumns.columnStart, addedColumns.sheet)) {
      return;
    }
    const {
      affectedArrays,
      contentChanges
    } = this.dependencyGraph.addColumns(addedColumns);
    this.columnSearch.addColumns(addedColumns);
    this.columnSearch.applyChanges(contentChanges.getChanges());
    this.stats.measure(StatType.TRANSFORM_ASTS, () => {
      const transformation = new AddColumnsTransformer(addedColumns);
      transformation.performEagerTransformations(this.dependencyGraph, this.parser);
      this.lazilyTransformingAstService.addTransformation(transformation);
    });
    this.rewriteAffectedArrays(affectedArrays);
  }
  /**
   * Returns true if row number is outside of given sheet.
   * @param {number} column - row number
   * @param {number} sheet - sheet ID number
   */
  columnEffectivelyNotInSheet(column, sheet) {
    const width = this.dependencyGraph.addressMapping.getSheetWidth(sheet);
    return column >= width;
  }
  adjustNamedExpressionEdges(namedExpression, expressionName, sheetId) {
    if (sheetId === void 0) {
      return;
    }
    const {
      vertex: localVertex,
      id: maybeLocalVertexId
    } = this.dependencyGraph.fetchCellOrCreateEmpty(namedExpression.address);
    const localVertexId = maybeLocalVertexId !== null && maybeLocalVertexId !== void 0 ? maybeLocalVertexId : localVertex.idInGraph;
    const globalNamedExpression = this.namedExpressions.workbookNamedExpressionOrPlaceholder(expressionName);
    const {
      vertex: globalVertex,
      id: maybeGlobalVertexId
    } = this.dependencyGraph.fetchCellOrCreateEmpty(globalNamedExpression.address);
    const globalVertexId = maybeGlobalVertexId !== null && maybeGlobalVertexId !== void 0 ? maybeGlobalVertexId : globalVertex.idInGraph;
    for (const adjacentNode of this.dependencyGraph.graph.adjacentNodes(globalVertex)) {
      if (adjacentNode instanceof ScalarFormulaVertex && adjacentNode.getAddress(this.lazilyTransformingAstService).sheet === sheetId) {
        const ast = adjacentNode.getFormula(this.lazilyTransformingAstService);
        const formulaAddress = adjacentNode.getAddress(this.lazilyTransformingAstService);
        const {
          dependencies
        } = this.parser.fetchCachedResultForAst(ast);
        for (const dependency of absolutizeDependencies(dependencies, formulaAddress)) {
          if (dependency instanceof NamedExpressionDependency && dependency.name.toLowerCase() === namedExpression.displayName.toLowerCase()) {
            this.dependencyGraph.graph.removeEdgeIfExists(globalVertexId, adjacentNode);
            this.dependencyGraph.graph.addEdge(localVertexId, adjacentNode);
          }
        }
      }
    }
  }
  storeNamedExpressionInCell(address, expression) {
    const parsedCellContent = this.cellContentParser.parse(expression);
    if (parsedCellContent instanceof CellContent.Formula) {
      const parsingResult = this.parser.parse(parsedCellContent.formula, simpleCellAddress(-1, 0, 0));
      if (doesContainRelativeReferences(parsingResult.ast)) {
        throw new NoRelativeAddressesAllowedError();
      }
      const {
        ast,
        hasVolatileFunction,
        hasStructuralChangeFunction,
        dependencies
      } = parsingResult;
      this.dependencyGraph.setFormulaToCell(address, ast, absolutizeDependencies(dependencies, address), ArraySize.scalar(), hasVolatileFunction, hasStructuralChangeFunction);
    } else if (parsedCellContent instanceof CellContent.Empty) {
      this.setCellEmpty(address);
    } else {
      this.setValueToCell({
        parsedValue: parsedCellContent.value,
        rawValue: expression
      }, address);
    }
  }
  updateNamedExpressionsForMovedCells(sourceLeftCorner, width, height, destinationLeftCorner) {
    if (sourceLeftCorner.sheet === destinationLeftCorner.sheet) {
      return [];
    }
    const addedGlobalNamedExpressions = [];
    const targetRange = AbsoluteCellRange.spanFrom(destinationLeftCorner, width, height);
    for (const formulaAddress of targetRange.addresses(this.dependencyGraph)) {
      const vertex = this.addressMapping.getCell(formulaAddress, {
        throwIfCellNotExists: true
      });
      if (vertex instanceof ScalarFormulaVertex && formulaAddress.sheet !== sourceLeftCorner.sheet) {
        const ast = vertex.getFormula(this.lazilyTransformingAstService);
        const {
          dependencies
        } = this.parser.fetchCachedResultForAst(ast);
        addedGlobalNamedExpressions.push(...this.updateNamedExpressionsForTargetAddress(sourceLeftCorner.sheet, formulaAddress, dependencies));
      }
    }
    return addedGlobalNamedExpressions;
  }
  updateNamedExpressionsForTargetAddress(sourceSheet, targetAddress, dependencies) {
    if (sourceSheet === targetAddress.sheet) {
      return [];
    }
    const addedGlobalNamedExpressions = [];
    const vertex = this.addressMapping.getCellOrThrow(targetAddress);
    for (const namedExpressionDependency of absolutizeDependencies(dependencies, targetAddress)) {
      if (!(namedExpressionDependency instanceof NamedExpressionDependency)) {
        continue;
      }
      const expressionName = namedExpressionDependency.name;
      const sourceVertex = this.dependencyGraph.fetchNamedExpressionVertex(expressionName, sourceSheet).vertex;
      const namedExpressionInTargetScope = this.namedExpressions.isExpressionInScope(expressionName, targetAddress.sheet);
      const targetScopeExpressionVertex = namedExpressionInTargetScope ? this.dependencyGraph.fetchNamedExpressionVertex(expressionName, targetAddress.sheet).vertex : this.copyOrFetchGlobalNamedExpressionVertex(expressionName, sourceVertex, addedGlobalNamedExpressions);
      if (targetScopeExpressionVertex !== sourceVertex) {
        this.dependencyGraph.graph.removeEdgeIfExists(sourceVertex, vertex);
        this.dependencyGraph.graph.addEdge(targetScopeExpressionVertex, vertex);
      }
    }
    return addedGlobalNamedExpressions;
  }
  allocateNamedExpressionAddressSpace() {
    this.dependencyGraph.addressMapping.addSheetWithStrategy(NamedExpressions.SHEET_FOR_WORKBOOK_EXPRESSIONS, new SparseStrategy(0, 0));
  }
  copyOrFetchGlobalNamedExpressionVertex(expressionName, sourceVertex, addedNamedExpressions) {
    let expression = this.namedExpressions.namedExpressionForScope(expressionName);
    if (expression === void 0) {
      expression = this.namedExpressions.addNamedExpression(expressionName);
      addedNamedExpressions.push(expression.normalizeExpressionName());
      if (sourceVertex instanceof ScalarFormulaVertex) {
        const parsingResult = this.parser.fetchCachedResultForAst(sourceVertex.getFormula(this.lazilyTransformingAstService));
        const {
          ast,
          hasVolatileFunction,
          hasStructuralChangeFunction,
          dependencies
        } = parsingResult;
        this.dependencyGraph.setFormulaToCell(expression.address, ast, absolutizeDependencies(dependencies, expression.address), ArraySize.scalar(), hasVolatileFunction, hasStructuralChangeFunction);
      } else if (sourceVertex instanceof EmptyCellVertex) {
        this.setCellEmpty(expression.address);
      } else if (sourceVertex instanceof ValueCellVertex) {
        this.setValueToCell(sourceVertex.getValues(), expression.address);
      }
    }
    return this.dependencyGraph.fetchCellOrCreateEmpty(expression.address).vertex;
  }
  /**
   * Removes a cell value from the columnSearch index.
   * Ignores the non-computed formula vertices.
   */
  removeCellValueFromColumnSearch(address) {
    if (this.isNotComputed(address)) {
      return;
    }
    const oldValue = this.dependencyGraph.getCellValue(address);
    this.columnSearch.remove(getRawValue(oldValue), address);
  }
  /**
   * Changes a cell value in the columnSearch index.
   * Ignores the non-computed formula vertices.
   */
  changeCellValueInColumnSearch(address, newValue) {
    if (this.isNotComputed(address)) {
      return;
    }
    const oldValue = this.dependencyGraph.getCellValue(address);
    this.columnSearch.change(getRawValue(oldValue), getRawValue(newValue), address);
  }
  /**
   * Checks if the ScalarFormulaVertex or ArrayFormulaVertex at the given address is not computed.
   */
  isNotComputed(address) {
    const vertex = this.dependencyGraph.getCell(address);
    if (!vertex) {
      return false;
    }
    return vertex instanceof FormulaVertex && !vertex.isComputed();
  }
}
function normalizeRemovedIndexes(indexes) {
  if (indexes.length <= 1) {
    return indexes;
  }
  const sorted = [...indexes].sort(([a2], [b2]) => a2 - b2);
  const merged = sorted.reduce((acc, [startIndex, amount]) => {
    const previous = acc[acc.length - 1];
    const lastIndex = previous[0] + previous[1];
    if (startIndex <= lastIndex) {
      previous[1] += Math.max(0, amount - (lastIndex - startIndex));
    } else {
      acc.push([startIndex, amount]);
    }
    return acc;
  }, [sorted[0]]);
  let shift = 0;
  for (let i2 = 0; i2 < merged.length; ++i2) {
    merged[i2][0] -= shift;
    shift += merged[i2][1];
  }
  return merged;
}
function normalizeAddedIndexes(indexes) {
  if (indexes.length <= 1) {
    return indexes;
  }
  const sorted = [...indexes].sort(([a2], [b2]) => a2 - b2);
  const merged = sorted.reduce((acc, [startIndex, amount]) => {
    const previous = acc[acc.length - 1];
    if (startIndex === previous[0]) {
      previous[1] = Math.max(previous[1], amount);
    } else {
      acc.push([startIndex, amount]);
    }
    return acc;
  }, [sorted[0]]);
  let shift = 0;
  for (let i2 = 0; i2 < merged.length; ++i2) {
    merged[i2][0] += shift;
    shift += merged[i2][1];
  }
  return merged;
}
function isPositiveInteger$1(n2) {
  return Number.isInteger(n2) && n2 > 0;
}
function isRowOrColumnRange(leftCorner, width, height) {
  return leftCorner.row === 0 && isPositiveInteger$1(width) && height === Number.POSITIVE_INFINITY || leftCorner.col === 0 && isPositiveInteger$1(height) && width === Number.POSITIVE_INFINITY;
}
class BaseUndoEntry {
  /**
   * Returns LazilyTransformingAstService version keys referenced by this entry's oldData.
   * Default implementation returns empty — override in entries that store oldData.
   */
  getReferencedOldDataVersions() {
    return [];
  }
}
class RemoveRowsUndoEntry extends BaseUndoEntry {
  constructor(command, rowsRemovals) {
    super();
    this.command = command;
    this.rowsRemovals = rowsRemovals;
  }
  doUndo(undoRedo) {
    undoRedo.undoRemoveRows(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoRemoveRows(this);
  }
  getReferencedOldDataVersions() {
    return this.rowsRemovals.filter((r2) => r2.version > 0).map((r2) => r2.version - 1);
  }
}
class MoveCellsUndoEntry extends BaseUndoEntry {
  constructor(sourceLeftCorner, width, height, destinationLeftCorner, overwrittenCellsData, addedGlobalNamedExpressions, version) {
    super();
    this.sourceLeftCorner = sourceLeftCorner;
    this.width = width;
    this.height = height;
    this.destinationLeftCorner = destinationLeftCorner;
    this.overwrittenCellsData = overwrittenCellsData;
    this.addedGlobalNamedExpressions = addedGlobalNamedExpressions;
    this.version = version;
  }
  doUndo(undoRedo) {
    undoRedo.undoMoveCells(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoMoveCells(this);
  }
  getReferencedOldDataVersions() {
    return this.version > 0 ? [this.version - 1] : [];
  }
}
class AddRowsUndoEntry extends BaseUndoEntry {
  constructor(command) {
    super();
    this.command = command;
  }
  doUndo(undoRedo) {
    undoRedo.undoAddRows(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoAddRows(this);
  }
}
class SetRowOrderUndoEntry extends BaseUndoEntry {
  constructor(sheetId, rowMapping, oldContent) {
    super();
    this.sheetId = sheetId;
    this.rowMapping = rowMapping;
    this.oldContent = oldContent;
  }
  doUndo(undoRedo) {
    undoRedo.undoSetRowOrder(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoSetRowOrder(this);
  }
}
class SetColumnOrderUndoEntry extends BaseUndoEntry {
  constructor(sheetId, columnMapping, oldContent) {
    super();
    this.sheetId = sheetId;
    this.columnMapping = columnMapping;
    this.oldContent = oldContent;
  }
  doUndo(undoRedo) {
    undoRedo.undoSetColumnOrder(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoSetColumnOrder(this);
  }
}
class SetSheetContentUndoEntry extends BaseUndoEntry {
  constructor(sheetId, oldSheetContent, newSheetContent) {
    super();
    this.sheetId = sheetId;
    this.oldSheetContent = oldSheetContent;
    this.newSheetContent = newSheetContent;
  }
  doUndo(undoRedo) {
    undoRedo.undoSetSheetContent(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoSetSheetContent(this);
  }
}
class MoveRowsUndoEntry extends BaseUndoEntry {
  constructor(sheet, startRow, numberOfRows, targetRow, version) {
    super();
    this.sheet = sheet;
    this.startRow = startRow;
    this.numberOfRows = numberOfRows;
    this.targetRow = targetRow;
    this.version = version;
    this.undoStart = this.startRow < this.targetRow ? this.targetRow - this.numberOfRows : this.targetRow;
    this.undoEnd = this.startRow > this.targetRow ? this.startRow + this.numberOfRows : this.startRow;
  }
  doUndo(undoRedo) {
    undoRedo.undoMoveRows(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoMoveRows(this);
  }
  getReferencedOldDataVersions() {
    return this.version > 0 ? [this.version - 1] : [];
  }
}
class MoveColumnsUndoEntry extends BaseUndoEntry {
  constructor(sheet, startColumn, numberOfColumns, targetColumn, version) {
    super();
    this.sheet = sheet;
    this.startColumn = startColumn;
    this.numberOfColumns = numberOfColumns;
    this.targetColumn = targetColumn;
    this.version = version;
    this.undoStart = this.startColumn < this.targetColumn ? this.targetColumn - this.numberOfColumns : this.targetColumn;
    this.undoEnd = this.startColumn > this.targetColumn ? this.startColumn + this.numberOfColumns : this.startColumn;
  }
  doUndo(undoRedo) {
    undoRedo.undoMoveColumns(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoMoveColumns(this);
  }
  getReferencedOldDataVersions() {
    return this.version > 0 ? [this.version - 1] : [];
  }
}
class AddColumnsUndoEntry extends BaseUndoEntry {
  constructor(command) {
    super();
    this.command = command;
  }
  doUndo(undoRedo) {
    undoRedo.undoAddColumns(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoAddColumns(this);
  }
}
class RemoveColumnsUndoEntry extends BaseUndoEntry {
  constructor(command, columnsRemovals) {
    super();
    this.command = command;
    this.columnsRemovals = columnsRemovals;
  }
  doUndo(undoRedo) {
    undoRedo.undoRemoveColumns(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoRemoveColumns(this);
  }
  getReferencedOldDataVersions() {
    return this.columnsRemovals.filter((r2) => r2.version > 0).map((r2) => r2.version - 1);
  }
}
class AddSheetUndoEntry extends BaseUndoEntry {
  constructor(sheetName, sheetId) {
    super();
    this.sheetName = sheetName;
    this.sheetId = sheetId;
  }
  doUndo(undoRedo) {
    undoRedo.undoAddSheet(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoAddSheet(this);
  }
}
class RemoveSheetUndoEntry extends BaseUndoEntry {
  constructor(sheetName, sheetId, oldSheetContent, scopedNamedExpressions) {
    super();
    this.sheetName = sheetName;
    this.sheetId = sheetId;
    this.oldSheetContent = oldSheetContent;
    this.scopedNamedExpressions = scopedNamedExpressions;
  }
  doUndo(undoRedo) {
    undoRedo.undoRemoveSheet(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoRemoveSheet(this);
  }
}
class RenameSheetUndoEntry extends BaseUndoEntry {
  constructor(sheetId, oldName, newName, version, mergedPlaceholderSheetId) {
    super();
    this.sheetId = sheetId;
    this.oldName = oldName;
    this.newName = newName;
    this.version = version;
    this.mergedPlaceholderSheetId = mergedPlaceholderSheetId;
  }
  doUndo(undoRedo) {
    undoRedo.undoRenameSheet(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoRenameSheet(this);
  }
  getReferencedOldDataVersions() {
    return this.version !== void 0 && this.version > 0 ? [this.version - 1] : [];
  }
}
class ClearSheetUndoEntry extends BaseUndoEntry {
  constructor(sheetId, oldSheetContent) {
    super();
    this.sheetId = sheetId;
    this.oldSheetContent = oldSheetContent;
  }
  doUndo(undoRedo) {
    undoRedo.undoClearSheet(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoClearSheet(this);
  }
}
class SetCellContentsUndoEntry extends BaseUndoEntry {
  constructor(cellContents) {
    super();
    this.cellContents = cellContents;
  }
  doUndo(undoRedo) {
    undoRedo.undoSetCellContents(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoSetCellContents(this);
  }
}
class PasteUndoEntry extends BaseUndoEntry {
  constructor(targetLeftCorner, oldContent, newContent, addedGlobalNamedExpressions) {
    super();
    this.targetLeftCorner = targetLeftCorner;
    this.oldContent = oldContent;
    this.newContent = newContent;
    this.addedGlobalNamedExpressions = addedGlobalNamedExpressions;
  }
  doUndo(undoRedo) {
    undoRedo.undoPaste(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoPaste(this);
  }
}
class AddNamedExpressionUndoEntry extends BaseUndoEntry {
  constructor(name, newContent, scope, options) {
    super();
    this.name = name;
    this.newContent = newContent;
    this.scope = scope;
    this.options = options;
  }
  doUndo(undoRedo) {
    undoRedo.undoAddNamedExpression(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoAddNamedExpression(this);
  }
}
class RemoveNamedExpressionUndoEntry extends BaseUndoEntry {
  constructor(namedExpression, content, scope) {
    super();
    this.namedExpression = namedExpression;
    this.content = content;
    this.scope = scope;
  }
  doUndo(undoRedo) {
    undoRedo.undoRemoveNamedExpression(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoRemoveNamedExpression(this);
  }
}
class ChangeNamedExpressionUndoEntry extends BaseUndoEntry {
  constructor(namedExpression, newContent, oldContent, scope, options) {
    super();
    this.namedExpression = namedExpression;
    this.newContent = newContent;
    this.oldContent = oldContent;
    this.scope = scope;
    this.options = options;
  }
  doUndo(undoRedo) {
    undoRedo.undoChangeNamedExpression(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoChangeNamedExpression(this);
  }
}
class BatchUndoEntry extends BaseUndoEntry {
  constructor() {
    super(...arguments);
    this.operations = [];
  }
  add(operation) {
    this.operations.push(operation);
  }
  *reversedOperations() {
    for (let i2 = this.operations.length - 1; i2 >= 0; i2--) {
      yield this.operations[i2];
    }
  }
  doUndo(undoRedo) {
    undoRedo.undoBatch(this);
  }
  doRedo(undoRedo) {
    undoRedo.redoBatch(this);
  }
  getReferencedOldDataVersions() {
    return this.operations.flatMap((op) => op.getReferencedOldDataVersions());
  }
}
class UndoRedo {
  constructor(config, operations) {
    this.operations = operations;
    this.oldData = /* @__PURE__ */ new Map();
    this.undoStack = [];
    this.redoStack = [];
    this.undoLimit = config.undoLimit;
  }
  saveOperation(operation) {
    if (this.batchUndoEntry !== void 0) {
      this.batchUndoEntry.add(operation);
    } else {
      this.addUndoEntry(operation);
    }
  }
  beginBatchMode() {
    this.batchUndoEntry = new BatchUndoEntry();
  }
  commitBatchMode() {
    if (this.batchUndoEntry === void 0) {
      throw Error("Batch mode wasn't started");
    }
    this.addUndoEntry(this.batchUndoEntry);
    this.batchUndoEntry = void 0;
  }
  /**
   * Stores a formula AST hash snapshot for the given LazilyTransformingAstService version.
   * Skipped when `undoLimit` is 0 (undo disabled) to avoid storing data that would never be used.
   */
  storeDataForVersion(version, address, astHash) {
    if (this.undoLimit === 0) {
      return;
    }
    if (!this.oldData.has(version)) {
      this.oldData.set(version, []);
    }
    const currentOldData = this.oldData.get(version);
    currentOldData.push([address, astHash]);
  }
  /** Clears the redo stack and removes oldData entries no longer referenced by any remaining entry. */
  clearRedoStack() {
    this.cleanupOldDataForEntries(this.redoStack, this.undoStack);
    this.redoStack = [];
  }
  /** Clears the undo stack and removes oldData entries no longer referenced by any remaining entry. */
  clearUndoStack() {
    this.cleanupOldDataForEntries(this.undoStack, this.redoStack);
    this.undoStack = [];
  }
  isUndoStackEmpty() {
    return this.undoStack.length === 0;
  }
  isRedoStackEmpty() {
    return this.redoStack.length === 0;
  }
  undo() {
    const operation = this.undoStack.pop();
    if (!operation) {
      throw Error("Attempted to undo without operation on stack");
    }
    this.undoEntry(operation);
    this.redoStack.push(operation);
  }
  undoBatch(batchOperation) {
    for (const operation of batchOperation.reversedOperations()) {
      this.undoEntry(operation);
    }
  }
  undoRemoveRows(operation) {
    this.operations.forceApplyPostponedTransformations();
    const {
      command: {
        sheet
      },
      rowsRemovals
    } = operation;
    for (let i2 = rowsRemovals.length - 1; i2 >= 0; --i2) {
      const rowsRemoval = rowsRemovals[i2];
      this.operations.addRows(new AddRowsCommand(sheet, [[rowsRemoval.rowFrom, rowsRemoval.rowCount]]));
      for (const {
        address,
        cellType
      } of rowsRemoval.removedCells) {
        this.operations.restoreCell(address, cellType);
      }
      this.restoreOldDataFromVersion(rowsRemoval.version - 1);
    }
  }
  undoRemoveColumns(operation) {
    this.operations.forceApplyPostponedTransformations();
    const {
      command: {
        sheet
      },
      columnsRemovals
    } = operation;
    for (let i2 = columnsRemovals.length - 1; i2 >= 0; --i2) {
      const columnsRemoval = columnsRemovals[i2];
      this.operations.addColumns(new AddColumnsCommand(sheet, [[columnsRemoval.columnFrom, columnsRemoval.columnCount]]));
      for (const {
        address,
        cellType
      } of columnsRemoval.removedCells) {
        this.operations.restoreCell(address, cellType);
      }
      this.restoreOldDataFromVersion(columnsRemoval.version - 1);
    }
  }
  undoAddRows(operation) {
    const addedRowsSpans = operation.command.rowsSpans();
    for (let i2 = addedRowsSpans.length - 1; i2 >= 0; --i2) {
      const addedRows = addedRowsSpans[i2];
      this.operations.removeRows(new RemoveRowsCommand(operation.command.sheet, [[addedRows.rowStart, addedRows.numberOfRows]]));
    }
  }
  undoAddColumns(operation) {
    const addedColumnsSpans = operation.command.columnsSpans();
    for (let i2 = addedColumnsSpans.length - 1; i2 >= 0; --i2) {
      const addedColumns = addedColumnsSpans[i2];
      this.operations.removeColumns(new RemoveColumnsCommand(operation.command.sheet, [[addedColumns.columnStart, addedColumns.numberOfColumns]]));
    }
  }
  undoSetCellContents(operation) {
    for (const cellContentData of operation.cellContents) {
      const address = cellContentData.address;
      const [oldContentAddress, oldContent] = cellContentData.oldContent;
      if (!equalSimpleCellAddress(address, oldContentAddress)) {
        this.operations.setCellEmpty(address);
      }
      this.operations.restoreCell(oldContentAddress, oldContent);
    }
  }
  undoPaste(operation) {
    this.restoreOperationOldContent(operation.oldContent);
    for (const namedExpression of operation.addedGlobalNamedExpressions) {
      this.operations.removeNamedExpression(namedExpression);
    }
  }
  undoMoveRows(operation) {
    this.operations.forceApplyPostponedTransformations();
    const {
      sheet
    } = operation;
    this.operations.moveRows(sheet, operation.undoStart, operation.numberOfRows, operation.undoEnd);
    this.restoreOldDataFromVersion(operation.version - 1);
  }
  undoMoveColumns(operation) {
    this.operations.forceApplyPostponedTransformations();
    const {
      sheet
    } = operation;
    this.operations.moveColumns(sheet, operation.undoStart, operation.numberOfColumns, operation.undoEnd);
    this.restoreOldDataFromVersion(operation.version - 1);
  }
  undoMoveCells(operation) {
    this.operations.forceApplyPostponedTransformations();
    this.operations.moveCells(operation.destinationLeftCorner, operation.width, operation.height, operation.sourceLeftCorner);
    this.restoreOperationOldContent(operation.overwrittenCellsData);
    this.restoreOldDataFromVersion(operation.version - 1);
    for (const namedExpression of operation.addedGlobalNamedExpressions) {
      this.operations.removeNamedExpression(namedExpression);
    }
  }
  undoAddSheet(operation) {
    const {
      sheetName
    } = operation;
    this.operations.removeSheetByName(sheetName);
  }
  undoRemoveSheet(operation) {
    this.operations.forceApplyPostponedTransformations();
    const {
      oldSheetContent,
      sheetId,
      scopedNamedExpressions,
      sheetName
    } = operation;
    this.operations.addSheetWithId(sheetId, sheetName);
    for (let rowIndex = 0; rowIndex < oldSheetContent.length; rowIndex++) {
      const row = oldSheetContent[rowIndex];
      for (let col = 0; col < row.length; col++) {
        const cellType = row[col];
        const address = simpleCellAddress(sheetId, col, rowIndex);
        this.operations.restoreCell(address, cellType);
      }
    }
    for (const [namedexpression, content] of scopedNamedExpressions) {
      this.operations.restoreNamedExpression(namedexpression, content, sheetId);
    }
  }
  undoRenameSheet(operation) {
    this.operations.forceApplyPostponedTransformations();
    this.operations.renameSheet(operation.sheetId, operation.oldName);
    if (operation.mergedPlaceholderSheetId !== void 0 && operation.version !== void 0) {
      this.operations.addPlaceholderSheetWithId(operation.mergedPlaceholderSheetId, operation.newName);
      this.restoreOldDataFromVersion(operation.version - 1);
    }
  }
  undoClearSheet(operation) {
    const {
      oldSheetContent,
      sheetId
    } = operation;
    for (let rowIndex = 0; rowIndex < oldSheetContent.length; rowIndex++) {
      const row = oldSheetContent[rowIndex];
      for (let col = 0; col < row.length; col++) {
        const cellType = row[col];
        const address = simpleCellAddress(sheetId, col, rowIndex);
        this.operations.restoreCell(address, cellType);
      }
    }
  }
  undoSetSheetContent(operation) {
    const {
      oldSheetContent,
      sheetId
    } = operation;
    this.operations.clearSheet(sheetId);
    for (let rowIndex = 0; rowIndex < oldSheetContent.length; rowIndex++) {
      const row = oldSheetContent[rowIndex];
      for (let col = 0; col < row.length; col++) {
        const cellType = row[col];
        const address = simpleCellAddress(sheetId, col, rowIndex);
        this.operations.restoreCell(address, cellType);
      }
    }
  }
  undoAddNamedExpression(operation) {
    this.operations.removeNamedExpression(operation.name, operation.scope);
  }
  undoRemoveNamedExpression(operation) {
    this.operations.restoreNamedExpression(operation.namedExpression, operation.content, operation.scope);
  }
  undoChangeNamedExpression(operation) {
    this.operations.restoreNamedExpression(operation.namedExpression, operation.oldContent, operation.scope);
  }
  undoSetRowOrder(operation) {
    this.restoreOperationOldContent(operation.oldContent);
  }
  undoSetColumnOrder(operation) {
    this.restoreOperationOldContent(operation.oldContent);
  }
  redo() {
    const operation = this.redoStack.pop();
    if (!operation) {
      throw Error("Attempted to redo without operation on stack");
    }
    this.redoEntry(operation);
    this.undoStack.push(operation);
  }
  redoBatch(batchOperation) {
    for (const operation of batchOperation.operations) {
      this.redoEntry(operation);
    }
  }
  redoRemoveRows(operation) {
    this.operations.removeRows(operation.command);
  }
  redoMoveCells(operation) {
    this.operations.moveCells(operation.sourceLeftCorner, operation.width, operation.height, operation.destinationLeftCorner);
  }
  redoRemoveColumns(operation) {
    this.operations.removeColumns(operation.command);
  }
  redoPaste(operation) {
    const {
      targetLeftCorner,
      newContent
    } = operation;
    const height = newContent.length;
    const width = newContent[0].length;
    for (let y2 = 0; y2 < height; ++y2) {
      for (let x2 = 0; x2 < width; ++x2) {
        const address = simpleCellAddress(targetLeftCorner.sheet, targetLeftCorner.col + x2, targetLeftCorner.row + y2);
        this.operations.restoreCell(address, newContent[y2][x2]);
      }
    }
  }
  redoSetCellContents(operation) {
    for (const cellContentData of operation.cellContents) {
      this.operations.setCellContent(cellContentData.address, cellContentData.newContent);
    }
  }
  redoAddRows(operation) {
    this.operations.addRows(operation.command);
  }
  redoAddColumns(operation) {
    this.operations.addColumns(operation.command);
  }
  redoRemoveSheet(operation) {
    this.operations.removeSheetByName(operation.sheetName);
  }
  redoAddSheet(operation) {
    this.operations.addSheetWithId(operation.sheetId, operation.sheetName);
  }
  redoRenameSheet(operation) {
    this.operations.renameSheet(operation.sheetId, operation.newName);
  }
  redoMoveRows(operation) {
    this.operations.moveRows(operation.sheet, operation.startRow, operation.numberOfRows, operation.targetRow);
  }
  redoMoveColumns(operation) {
    this.operations.moveColumns(operation.sheet, operation.startColumn, operation.numberOfColumns, operation.targetColumn);
  }
  redoClearSheet(operation) {
    this.operations.clearSheet(operation.sheetId);
  }
  redoSetSheetContent(operation) {
    const {
      sheetId,
      newSheetContent
    } = operation;
    this.operations.setSheetContent(sheetId, newSheetContent);
  }
  redoAddNamedExpression(operation) {
    this.operations.addNamedExpression(operation.name, operation.newContent, operation.scope, operation.options);
  }
  redoRemoveNamedExpression(operation) {
    this.operations.removeNamedExpression(operation.namedExpression.displayName, operation.scope);
  }
  redoChangeNamedExpression(operation) {
    this.operations.changeNamedExpressionExpression(operation.namedExpression.displayName, operation.newContent, operation.scope, operation.options);
  }
  redoSetRowOrder(operation) {
    this.operations.setRowOrder(operation.sheetId, operation.rowMapping);
  }
  redoSetColumnOrder(operation) {
    this.operations.setColumnOrder(operation.sheetId, operation.columnMapping);
  }
  /**
   * Adds an entry to the undo stack, evicting the oldest entries when undoLimit is exceeded.
   * Evicted entries have their oldData keys cleaned up to prevent memory leaks.
   */
  addUndoEntry(operation) {
    this.undoStack.push(operation);
    const evictCount = Math.max(0, this.undoStack.length - this.undoLimit);
    if (evictCount > 0) {
      const evicted = this.undoStack.splice(0, evictCount);
      this.cleanupOldDataForEntries(evicted, [...this.undoStack, ...this.redoStack]);
    }
  }
  /**
   * Removes oldData entries whose version keys are not referenced by any
   * entry on the undo stack, redo stack, or in-progress batch. Called after
   * compaction forces lazy formula evaluation, which may insert oldData for
   * already-evicted entries.
   */
  cleanupOrphanedOldData() {
    const referencedVersions = this.collectReferencedOldDataVersions(this.undoStack, this.redoStack);
    for (const version of this.oldData.keys()) {
      if (!referencedVersions.has(version)) {
        this.oldData.delete(version);
      }
    }
  }
  /**
   * Removes oldData entries referenced by permanently discarded undo/redo entries,
   * but only if not still referenced by entries remaining on the other stack or
   * in-progress batch.
   */
  cleanupOldDataForEntries(discardedEntries, retainedEntries) {
    const retainedVersions = this.collectReferencedOldDataVersions(retainedEntries);
    for (const entry of discardedEntries) {
      for (const version of entry.getReferencedOldDataVersions()) {
        if (!retainedVersions.has(version)) {
          this.oldData.delete(version);
        }
      }
    }
  }
  /** Collects all oldData version keys referenced by the given entry lists and in-progress batch. */
  collectReferencedOldDataVersions(...entryLists) {
    var _a2, _b;
    return /* @__PURE__ */ new Set([...entryLists.flatMap((list) => list.flatMap((e3) => e3.getReferencedOldDataVersions())), ...(_b = (_a2 = this.batchUndoEntry) === null || _a2 === void 0 ? void 0 : _a2.getReferencedOldDataVersions()) !== null && _b !== void 0 ? _b : []]);
  }
  undoEntry(operation) {
    operation.doUndo(this);
  }
  restoreOperationOldContent(oldContent) {
    for (const [address, clipboardCell] of oldContent) {
      this.operations.restoreCell(address, clipboardCell);
    }
  }
  redoEntry(operation) {
    operation.doRedo(this);
  }
  restoreOldDataFromVersion(version) {
    const oldDataToRestore = this.oldData.get(version) || [];
    for (const entryToRestore of oldDataToRestore) {
      const [address, hash] = entryToRestore;
      this.operations.setFormulaToCellFromCache(hash, address);
    }
  }
}
class CrudOperations {
  constructor(config, operations, undoRedo, clipboardOperations, dependencyGraph, columnSearch, parser, cellContentParser, lazilyTransformingAstService, namedExpressions) {
    this.operations = operations;
    this.undoRedo = undoRedo;
    this.clipboardOperations = clipboardOperations;
    this.dependencyGraph = dependencyGraph;
    this.columnSearch = columnSearch;
    this.parser = parser;
    this.cellContentParser = cellContentParser;
    this.lazilyTransformingAstService = lazilyTransformingAstService;
    this.namedExpressions = namedExpressions;
    this.maxRows = config.maxRows;
    this.maxColumns = config.maxColumns;
  }
  get sheetMapping() {
    return this.dependencyGraph.sheetMapping;
  }
  addRows(sheet, ...indexes) {
    const addRowsCommand = new AddRowsCommand(sheet, indexes);
    this.ensureItIsPossibleToAddRows(sheet, ...indexes);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    this.operations.addRows(addRowsCommand);
    this.undoRedo.saveOperation(new AddRowsUndoEntry(addRowsCommand));
  }
  removeRows(sheet, ...indexes) {
    const removeRowsCommand = new RemoveRowsCommand(sheet, indexes);
    this.ensureItIsPossibleToRemoveRows(sheet, ...indexes);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const rowsRemovals = this.operations.removeRows(removeRowsCommand);
    this.undoRedo.saveOperation(new RemoveRowsUndoEntry(removeRowsCommand, rowsRemovals));
  }
  addColumns(sheet, ...indexes) {
    const addColumnsCommand = new AddColumnsCommand(sheet, indexes);
    this.ensureItIsPossibleToAddColumns(sheet, ...indexes);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    this.operations.addColumns(addColumnsCommand);
    this.undoRedo.saveOperation(new AddColumnsUndoEntry(addColumnsCommand));
  }
  removeColumns(sheet, ...indexes) {
    const removeColumnsCommand = new RemoveColumnsCommand(sheet, indexes);
    this.ensureItIsPossibleToRemoveColumns(sheet, ...indexes);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const columnsRemovals = this.operations.removeColumns(removeColumnsCommand);
    this.undoRedo.saveOperation(new RemoveColumnsUndoEntry(removeColumnsCommand, columnsRemovals));
  }
  moveCells(sourceLeftCorner, width, height, destinationLeftCorner) {
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const {
      version,
      overwrittenCellsData,
      addedGlobalNamedExpressions
    } = this.operations.moveCells(sourceLeftCorner, width, height, destinationLeftCorner);
    this.undoRedo.saveOperation(new MoveCellsUndoEntry(sourceLeftCorner, width, height, destinationLeftCorner, overwrittenCellsData, addedGlobalNamedExpressions, version));
  }
  moveRows(sheet, startRow, numberOfRows, targetRow) {
    this.ensureItIsPossibleToMoveRows(sheet, startRow, numberOfRows, targetRow);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const version = this.operations.moveRows(sheet, startRow, numberOfRows, targetRow);
    this.undoRedo.saveOperation(new MoveRowsUndoEntry(sheet, startRow, numberOfRows, targetRow, version));
  }
  moveColumns(sheet, startColumn, numberOfColumns, targetColumn) {
    this.ensureItIsPossibleToMoveColumns(sheet, startColumn, numberOfColumns, targetColumn);
    this.undoRedo.clearRedoStack();
    const version = this.operations.moveColumns(sheet, startColumn, numberOfColumns, targetColumn);
    this.undoRedo.saveOperation(new MoveColumnsUndoEntry(sheet, startColumn, numberOfColumns, targetColumn, version));
  }
  cut(sourceLeftCorner, width, height) {
    this.clipboardOperations.cut(sourceLeftCorner, width, height);
  }
  ensureItIsPossibleToCopy(sourceLeftCorner, width, height) {
    if (!isPositiveInteger(width)) {
      throw new InvalidArgumentsError("width to be positive integer.");
    }
    if (!isPositiveInteger(height)) {
      throw new InvalidArgumentsError("height to be positive integer.");
    }
  }
  copy(sourceLeftCorner, width, height) {
    this.ensureItIsPossibleToCopy(sourceLeftCorner, width, height);
    this.clipboardOperations.copy(sourceLeftCorner, width, height);
  }
  paste(targetLeftCorner) {
    const clipboard = this.clipboardOperations.clipboard;
    if (clipboard === void 0) {
      throw new NothingToPasteError();
    } else if (this.clipboardOperations.isCutClipboard()) {
      this.moveCells(clipboard.sourceLeftCorner, clipboard.width, clipboard.height, targetLeftCorner);
    } else if (this.clipboardOperations.isCopyClipboard()) {
      this.clipboardOperations.ensureItIsPossibleToCopyPaste(targetLeftCorner);
      const targetRange = AbsoluteCellRange.spanFrom(targetLeftCorner, clipboard.width, clipboard.height);
      const oldContent = this.operations.getRangeClipboardCells(targetRange);
      this.undoRedo.clearRedoStack();
      const addedGlobalNamedExpressions = this.operations.restoreClipboardCells(clipboard.sourceLeftCorner.sheet, clipboard.getContent(targetLeftCorner));
      this.undoRedo.saveOperation(new PasteUndoEntry(targetLeftCorner, oldContent, clipboard.content, addedGlobalNamedExpressions));
    }
  }
  beginUndoRedoBatchMode() {
    this.undoRedo.beginBatchMode();
  }
  commitUndoRedoBatchMode() {
    this.undoRedo.commitBatchMode();
  }
  isClipboardEmpty() {
    return this.clipboardOperations.clipboard === void 0;
  }
  clearClipboard() {
    this.clipboardOperations.clear();
  }
  addSheet(name) {
    if (name !== void 0) {
      this.ensureItIsPossibleToAddSheet(name);
    }
    this.undoRedo.clearRedoStack();
    const {
      sheetName,
      sheetId
    } = this.operations.addSheet(name);
    this.undoRedo.saveOperation(new AddSheetUndoEntry(sheetName, sheetId));
    return sheetName;
  }
  removeSheet(sheetId) {
    this.ensureScopeIdIsValid(sheetId);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const originalName = this.sheetMapping.getSheetNameOrThrowError(sheetId);
    const oldSheetContent = this.operations.getSheetClipboardCells(sheetId);
    const scopedNamedExpressions = this.operations.removeSheet(sheetId);
    this.undoRedo.saveOperation(new RemoveSheetUndoEntry(originalName, sheetId, oldSheetContent, scopedNamedExpressions));
  }
  renameSheet(sheetId, newName) {
    this.ensureItIsPossibleToRenameSheet(sheetId, newName);
    const {
      previousDisplayName,
      version,
      mergedPlaceholderSheetId
    } = this.operations.renameSheet(sheetId, newName);
    if (previousDisplayName !== void 0) {
      this.undoRedo.clearRedoStack();
      this.undoRedo.saveOperation(new RenameSheetUndoEntry(sheetId, previousDisplayName, newName, version, mergedPlaceholderSheetId));
    }
    return previousDisplayName;
  }
  clearSheet(sheetId) {
    this.ensureScopeIdIsValid(sheetId);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const oldSheetContent = this.operations.getSheetClipboardCells(sheetId);
    this.operations.clearSheet(sheetId);
    this.undoRedo.saveOperation(new ClearSheetUndoEntry(sheetId, oldSheetContent));
  }
  setCellContents(topLeftCornerAddress, cellContents) {
    if (!(cellContents instanceof Array)) {
      cellContents = [[cellContents]];
    } else {
      for (let i2 = 0; i2 < cellContents.length; i2++) {
        if (!(cellContents[i2] instanceof Array)) {
          throw new InvalidArgumentsError("an array of arrays or a raw cell value.");
        }
      }
    }
    this.ensureItIsPossibleToChangeCellContents(topLeftCornerAddress, cellContents);
    this.undoRedo.clearRedoStack();
    const oldContents = [];
    for (let i2 = 0; i2 < cellContents.length; i2++) {
      for (let j2 = 0; j2 < cellContents[i2].length; j2++) {
        const address = {
          sheet: topLeftCornerAddress.sheet,
          row: topLeftCornerAddress.row + i2,
          col: topLeftCornerAddress.col + j2
        };
        const newContent = cellContents[i2][j2];
        this.clipboardOperations.abortCut();
        const oldContent = this.operations.setCellContent(address, newContent);
        oldContents.push({
          address,
          newContent,
          oldContent
        });
      }
    }
    this.undoRedo.saveOperation(new SetCellContentsUndoEntry(oldContents));
  }
  setSheetContent(sheetId, values2) {
    this.ensureScopeIdIsValid(sheetId);
    this.ensureItIsPossibleToChangeSheetContents(sheetId, values2);
    validateAsSheet(values2);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const oldSheetContent = this.operations.getSheetClipboardCells(sheetId);
    this.operations.setSheetContent(sheetId, values2);
    this.undoRedo.saveOperation(new SetSheetContentUndoEntry(sheetId, oldSheetContent, values2));
  }
  setRowOrder(sheetId, rowMapping) {
    this.validateSwapRowIndexes(sheetId, rowMapping);
    this.testRowOrderForArrays(sheetId, rowMapping);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const oldContent = this.operations.setRowOrder(sheetId, rowMapping);
    this.undoRedo.saveOperation(new SetRowOrderUndoEntry(sheetId, rowMapping, oldContent));
  }
  validateSwapRowIndexes(sheetId, rowMapping) {
    if (!this.sheetMapping.hasSheetWithId(sheetId)) {
      throw new NoSheetWithIdError(sheetId);
    }
    this.validateRowOrColumnMapping(sheetId, rowMapping, "row");
  }
  testColumnOrderForArrays(sheetId, columnMapping) {
    for (const [source, target] of columnMapping) {
      if (source !== target) {
        const rowRange = AbsoluteCellRange.spanFrom({
          sheet: sheetId,
          col: source,
          row: 0
        }, 1, Infinity);
        if (this.dependencyGraph.arrayMapping.isFormulaArrayInRange(rowRange)) {
          throw new SourceLocationHasArrayError();
        }
      }
    }
  }
  setColumnOrder(sheetId, columnMapping) {
    this.validateSwapColumnIndexes(sheetId, columnMapping);
    this.testColumnOrderForArrays(sheetId, columnMapping);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    const oldContent = this.operations.setColumnOrder(sheetId, columnMapping);
    this.undoRedo.saveOperation(new SetColumnOrderUndoEntry(sheetId, columnMapping, oldContent));
  }
  validateSwapColumnIndexes(sheetId, columnMapping) {
    if (!this.sheetMapping.hasSheetWithId(sheetId)) {
      throw new NoSheetWithIdError(sheetId);
    }
    this.validateRowOrColumnMapping(sheetId, columnMapping, "column");
  }
  testRowOrderForArrays(sheetId, rowMapping) {
    for (const [source, target] of rowMapping) {
      if (source !== target) {
        const rowRange = AbsoluteCellRange.spanFrom({
          sheet: sheetId,
          col: 0,
          row: source
        }, Infinity, 1);
        if (this.dependencyGraph.arrayMapping.isFormulaArrayInRange(rowRange)) {
          throw new SourceLocationHasArrayError();
        }
      }
    }
  }
  mappingFromOrder(sheetId, newOrder, rowOrColumn) {
    if (!this.sheetMapping.hasSheetWithId(sheetId)) {
      throw new NoSheetWithIdError(sheetId);
    }
    const limit = rowOrColumn === "row" ? this.dependencyGraph.getSheetHeight(sheetId) : this.dependencyGraph.getSheetWidth(sheetId);
    if (newOrder.length !== limit) {
      throw new InvalidArgumentsError(`number of ${rowOrColumn}s provided to be sheet ${rowOrColumn === "row" ? "height" : "width"}.`);
    }
    const ret = [];
    for (let i2 = 0; i2 < limit; i2++) {
      if (newOrder[i2] !== i2) {
        ret.push([i2, newOrder[i2]]);
      }
    }
    return ret;
  }
  undo() {
    if (this.undoRedo.isUndoStackEmpty()) {
      throw new NoOperationToUndoError();
    }
    this.clipboardOperations.abortCut();
    this.undoRedo.undo();
  }
  redo() {
    if (this.undoRedo.isRedoStackEmpty()) {
      throw new NoOperationToRedoError();
    }
    this.clipboardOperations.abortCut();
    this.undoRedo.redo();
  }
  addNamedExpression(expressionName, expression, sheetId, options) {
    this.ensureItIsPossibleToAddNamedExpression(expressionName, expression, sheetId);
    this.operations.addNamedExpression(expressionName, expression, sheetId, options);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    this.undoRedo.saveOperation(new AddNamedExpressionUndoEntry(expressionName, expression, sheetId, options));
  }
  changeNamedExpressionExpression(expressionName, sheetId, newExpression, options) {
    this.ensureItIsPossibleToChangeNamedExpression(expressionName, newExpression, sheetId);
    const [oldNamedExpression, content] = this.operations.changeNamedExpressionExpression(expressionName, newExpression, sheetId, options);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    this.undoRedo.saveOperation(new ChangeNamedExpressionUndoEntry(oldNamedExpression, newExpression, content, sheetId, options));
  }
  removeNamedExpression(expressionName, sheetId) {
    this.ensureScopeIdIsValid(sheetId);
    const [namedExpression, content] = this.operations.removeNamedExpression(expressionName, sheetId);
    this.undoRedo.clearRedoStack();
    this.clipboardOperations.abortCut();
    this.undoRedo.saveOperation(new RemoveNamedExpressionUndoEntry(namedExpression, content, sheetId));
    return namedExpression;
  }
  ensureItIsPossibleToAddNamedExpression(expressionName, expression, sheetId) {
    this.ensureScopeIdIsValid(sheetId);
    this.ensureNamedExpressionNameIsValid(expressionName, sheetId);
    this.ensureNamedExpressionIsValid(expression);
  }
  ensureItIsPossibleToChangeNamedExpression(expressionName, expression, sheetId) {
    this.ensureScopeIdIsValid(sheetId);
    if (this.namedExpressions.namedExpressionForScope(expressionName, sheetId) === void 0) {
      throw new NamedExpressionDoesNotExistError(expressionName);
    }
    this.ensureNamedExpressionIsValid(expression);
  }
  isItPossibleToRemoveNamedExpression(expressionName, sheetId) {
    this.ensureScopeIdIsValid(sheetId);
    if (this.namedExpressions.namedExpressionForScope(expressionName, sheetId) === void 0) {
      throw new NamedExpressionDoesNotExistError(expressionName);
    }
  }
  ensureItIsPossibleToAddRows(sheet, ...indexes) {
    if (!this.sheetMapping.hasSheetWithId(sheet)) {
      throw new NoSheetWithIdError(sheet);
    }
    const sheetHeight = this.dependencyGraph.getSheetHeight(sheet);
    const newRowsCount = indexes.map((index2) => index2[1]).reduce((a2, b2) => a2 + b2, 0);
    if (sheetHeight + newRowsCount > this.maxRows) {
      throw new SheetSizeLimitExceededError();
    }
    for (const [row, numberOfRowsToAdd] of indexes) {
      if (!isNonnegativeInteger(row) || !isPositiveInteger(numberOfRowsToAdd)) {
        throw new InvalidArgumentsError("row number to be nonnegative and number of rows to add to be positive.");
      }
    }
  }
  ensureItIsPossibleToRemoveRows(sheet, ...indexes) {
    for (const [rowStart, numberOfRows] of indexes) {
      const rowEnd = rowStart + numberOfRows - 1;
      if (!isNonnegativeInteger(rowStart) || !isNonnegativeInteger(rowEnd)) {
        throw new InvalidArgumentsError("starting and ending row to be nonnegative.");
      }
      if (rowEnd < rowStart) {
        throw new InvalidArgumentsError("starting row to be smaller than the ending row.");
      }
      if (!this.sheetMapping.hasSheetWithId(sheet)) {
        throw new NoSheetWithIdError(sheet);
      }
    }
  }
  ensureItIsPossibleToAddColumns(sheet, ...indexes) {
    if (!this.sheetMapping.hasSheetWithId(sheet)) {
      throw new NoSheetWithIdError(sheet);
    }
    const sheetWidth = this.dependencyGraph.getSheetWidth(sheet);
    const newColumnsCount = indexes.map((index2) => index2[1]).reduce((a2, b2) => a2 + b2, 0);
    if (sheetWidth + newColumnsCount > this.maxColumns) {
      throw new SheetSizeLimitExceededError();
    }
    for (const [column, numberOfColumnsToAdd] of indexes) {
      if (!isNonnegativeInteger(column) || !isPositiveInteger(numberOfColumnsToAdd)) {
        throw new InvalidArgumentsError("column number to be nonnegative and number of columns to add to be positive.");
      }
    }
  }
  ensureItIsPossibleToRemoveColumns(sheet, ...indexes) {
    for (const [columnStart, numberOfColumns] of indexes) {
      const columnEnd = columnStart + numberOfColumns - 1;
      if (!isNonnegativeInteger(columnStart) || !isNonnegativeInteger(columnEnd)) {
        throw new InvalidArgumentsError("starting and ending column to be nonnegative.");
      }
      if (columnEnd < columnStart) {
        throw new InvalidArgumentsError("starting column to be smaller than the ending column.");
      }
      if (!this.sheetMapping.hasSheetWithId(sheet)) {
        throw new NoSheetWithIdError(sheet);
      }
    }
  }
  ensureItIsPossibleToMoveRows(sheet, startRow, numberOfRows, targetRow) {
    this.ensureItIsPossibleToAddRows(sheet, [targetRow, numberOfRows]);
    const sourceStart = simpleCellAddress(sheet, 0, startRow);
    const targetStart = simpleCellAddress(sheet, 0, targetRow);
    if (!this.sheetMapping.hasSheetWithId(sheet) || isColOrRowInvalid(sourceStart) || isColOrRowInvalid(targetStart) || !isPositiveInteger(numberOfRows) || targetRow <= startRow + numberOfRows && targetRow >= startRow) {
      throw new InvalidArgumentsError("a valid range of rows to move.");
    }
    const width = this.dependencyGraph.getSheetWidth(sheet);
    const sourceRange = AbsoluteCellRange.spanFrom(sourceStart, width, numberOfRows);
    if (this.dependencyGraph.arrayMapping.isFormulaArrayInRange(sourceRange)) {
      throw new SourceLocationHasArrayError();
    }
    if (targetRow > 0 && this.dependencyGraph.arrayMapping.isFormulaArrayInAllRows(RowsSpan.fromNumberOfRows(sheet, targetRow - 1, 2))) {
      throw new TargetLocationHasArrayError();
    }
  }
  ensureItIsPossibleToMoveColumns(sheet, startColumn, numberOfColumns, targetColumn) {
    this.ensureItIsPossibleToAddColumns(sheet, [targetColumn, numberOfColumns]);
    const sourceStart = simpleCellAddress(sheet, startColumn, 0);
    const targetStart = simpleCellAddress(sheet, targetColumn, 0);
    if (!this.sheetMapping.hasSheetWithId(sheet) || isColOrRowInvalid(sourceStart) || isColOrRowInvalid(targetStart) || !isPositiveInteger(numberOfColumns) || targetColumn <= startColumn + numberOfColumns && targetColumn >= startColumn) {
      throw new InvalidArgumentsError("a valid range of columns to move.");
    }
    const sheetHeight = this.dependencyGraph.getSheetHeight(sheet);
    const sourceRange = AbsoluteCellRange.spanFrom(sourceStart, numberOfColumns, sheetHeight);
    if (this.dependencyGraph.arrayMapping.isFormulaArrayInRange(sourceRange)) {
      throw new SourceLocationHasArrayError();
    }
    if (targetColumn > 0 && this.dependencyGraph.arrayMapping.isFormulaArrayInAllColumns(ColumnsSpan.fromNumberOfColumns(sheet, targetColumn - 1, 2))) {
      throw new TargetLocationHasArrayError();
    }
  }
  ensureItIsPossibleToAddSheet(name) {
    if (this.sheetMapping.hasSheetWithName(name)) {
      throw new SheetNameAlreadyTakenError(name);
    }
  }
  ensureItIsPossibleToRenameSheet(sheetId, name) {
    if (!this.sheetMapping.hasSheetWithId(sheetId)) {
      throw new NoSheetWithIdError(sheetId);
    }
    const existingSheetId = this.sheetMapping.getSheetId(name);
    if (existingSheetId !== void 0 && existingSheetId !== sheetId) {
      throw new SheetNameAlreadyTakenError(name);
    }
  }
  ensureItIsPossibleToChangeContent(address) {
    if (isColOrRowInvalid(address)) {
      throw new InvalidAddressError(address);
    }
    if (!this.sheetMapping.hasSheetWithId(address.sheet)) {
      throw new NoSheetWithIdError(address.sheet);
    }
  }
  ensureItIsPossibleToChangeCellContents(inputAddress, content) {
    const boundaries = findBoundaries(content);
    const targetRange = AbsoluteCellRange.spanFrom(inputAddress, boundaries.width, boundaries.height);
    this.ensureRangeInSizeLimits(targetRange);
    for (const address of targetRange.addresses(this.dependencyGraph)) {
      this.ensureItIsPossibleToChangeContent(address);
    }
  }
  ensureItIsPossibleToChangeSheetContents(sheetId, content) {
    const boundaries = findBoundaries(content);
    const targetRange = AbsoluteCellRange.spanFrom(simpleCellAddress(sheetId, 0, 0), boundaries.width, boundaries.height);
    this.ensureRangeInSizeLimits(targetRange);
  }
  ensureRangeInSizeLimits(range) {
    if (range.exceedsSheetSizeLimits(this.maxColumns, this.maxRows)) {
      throw new SheetSizeLimitExceededError();
    }
  }
  isThereSomethingToUndo() {
    return !this.undoRedo.isUndoStackEmpty();
  }
  isThereSomethingToRedo() {
    return !this.undoRedo.isRedoStackEmpty();
  }
  getAndClearContentChanges() {
    return this.operations.getAndClearContentChanges();
  }
  ensureScopeIdIsValid(scopeId) {
    if (scopeId !== void 0 && !this.sheetMapping.hasSheetWithId(scopeId)) {
      throw new NoSheetWithIdError(scopeId);
    }
  }
  validateRowOrColumnMapping(sheetId, rowMapping, rowOrColumn) {
    const limit = rowOrColumn === "row" ? this.dependencyGraph.getSheetHeight(sheetId) : this.dependencyGraph.getSheetWidth(sheetId);
    const sources = rowMapping.map(([a2, _2]) => a2).sort((a2, b2) => a2 - b2);
    const targets = rowMapping.map(([_2, b2]) => b2).sort((a2, b2) => a2 - b2);
    for (let i2 = 0; i2 < sources.length; i2++) {
      if (!isNonnegativeInteger(sources[i2]) || sources[i2] >= limit) {
        throw new InvalidArgumentsError(`${rowOrColumn} numbers to be nonnegative integers and less than sheet ${rowOrColumn === "row" ? "height" : "width"}.`);
      }
      if (sources[i2] === sources[i2 + 1]) {
        throw new InvalidArgumentsError(`source ${rowOrColumn} numbers to be unique.`);
      }
      if (sources[i2] !== targets[i2]) {
        throw new InvalidArgumentsError(`target ${rowOrColumn} numbers to be permutation of source ${rowOrColumn} numbers.`);
      }
    }
  }
  ensureNamedExpressionNameIsValid(expressionName, sheetId) {
    if (!this.namedExpressions.isNameValid(expressionName)) {
      throw new NamedExpressionNameIsInvalidError(expressionName);
    }
    if (!this.namedExpressions.isNameAvailable(expressionName, sheetId)) {
      throw new NamedExpressionNameIsAlreadyTakenError(expressionName);
    }
  }
  ensureNamedExpressionIsValid(expression) {
    const parsedExpression = this.cellContentParser.parse(expression);
    if (parsedExpression instanceof CellContent.Formula) {
      const parsingResult = this.parser.parse(parsedExpression.formula, simpleCellAddress(-1, 0, 0));
      if (doesContainRelativeReferences(parsingResult.ast)) {
        throw new NoRelativeAddressesAllowedError();
      }
    }
  }
}
function isPositiveInteger(x2) {
  return Number.isInteger(x2) && x2 > 0;
}
function isNonnegativeInteger(x2) {
  return Number.isInteger(x2) && x2 >= 0;
}
class Evaluator {
  constructor(config, stats, interpreter, lazilyTransformingAstService, dependencyGraph, columnSearch) {
    this.config = config;
    this.stats = stats;
    this.interpreter = interpreter;
    this.lazilyTransformingAstService = lazilyTransformingAstService;
    this.dependencyGraph = dependencyGraph;
    this.columnSearch = columnSearch;
  }
  run() {
    this.stats.start(StatType.TOP_SORT);
    const {
      sorted,
      cycled
    } = this.dependencyGraph.topSortWithScc();
    this.stats.end(StatType.TOP_SORT);
    this.stats.measure(StatType.EVALUATION, () => {
      this.recomputeFormulas(cycled, sorted);
    });
  }
  partialRun(vertices) {
    const changes = ContentChanges.empty();
    this.stats.measure(StatType.EVALUATION, () => {
      this.dependencyGraph.graph.getTopSortedWithSccSubgraphFrom(vertices, (vertex) => this.recomputeVertex(vertex, changes), (vertex) => this.processVertexOnCycle(vertex, changes));
    });
    return changes;
  }
  runAndForget(ast, address, dependencies) {
    const tmpRanges = [];
    for (const dep of absolutizeDependencies(dependencies, address)) {
      if (dep instanceof AbsoluteCellRange) {
        const range = dep;
        if (this.dependencyGraph.getRange(range.start, range.end) === void 0) {
          const rangeVertex = new RangeVertex(range);
          this.dependencyGraph.rangeMapping.addOrUpdateVertex(rangeVertex);
          tmpRanges.push(rangeVertex);
        }
      }
    }
    const ret = this.evaluateAstToCellValue(ast, new InterpreterState(address, this.config.useArrayArithmetic));
    tmpRanges.forEach((rangeVertex) => {
      this.dependencyGraph.rangeMapping.removeVertexIfExists(rangeVertex);
    });
    return ret;
  }
  /**
   * Recalculates the value of a single vertex assuming its dependencies have already been recalculated
   */
  recomputeVertex(vertex, changes) {
    if (vertex instanceof FormulaVertex) {
      const currentValue = vertex.isComputed() ? vertex.getCellValue() : void 0;
      const newCellValue = this.recomputeFormulaVertexValue(vertex);
      if (newCellValue !== currentValue) {
        const address = vertex.getAddress(this.lazilyTransformingAstService);
        changes.addChange(newCellValue, address);
        this.columnSearch.change(getRawValue(currentValue), getRawValue(newCellValue), address);
        return true;
      }
      return false;
    } else if (vertex instanceof RangeVertex) {
      vertex.clearCache();
      return true;
    } else {
      return true;
    }
  }
  /**
   * Processes a vertex that is part of a cycle in dependency graph
   */
  processVertexOnCycle(vertex, changes) {
    if (vertex instanceof RangeVertex) {
      vertex.clearCache();
    } else if (vertex instanceof FormulaVertex) {
      const address = vertex.getAddress(this.lazilyTransformingAstService);
      this.columnSearch.remove(getRawValue(vertex.valueOrUndef()), address);
      const error = new CellError(ErrorType.CYCLE, void 0, vertex);
      vertex.setCellValue(error);
      changes.addChange(error, address);
    }
  }
  /**
   * Recalculates formulas in the topological sort order
   */
  recomputeFormulas(cycled, sorted) {
    cycled.forEach((vertex) => {
      if (vertex instanceof FormulaVertex) {
        vertex.setCellValue(new CellError(ErrorType.CYCLE, void 0, vertex));
      }
    });
    sorted.forEach((vertex) => {
      if (vertex instanceof FormulaVertex) {
        const newCellValue = this.recomputeFormulaVertexValue(vertex);
        const address = vertex.getAddress(this.lazilyTransformingAstService);
        this.columnSearch.add(getRawValue(newCellValue), address);
      } else if (vertex instanceof RangeVertex) {
        vertex.clearCache();
      }
    });
  }
  recomputeFormulaVertexValue(vertex) {
    const address = vertex.getAddress(this.lazilyTransformingAstService);
    if (vertex instanceof ArrayFormulaVertex && (vertex.array.size.isRef || !this.dependencyGraph.isThereSpaceForArray(vertex))) {
      return vertex.setNoSpace();
    } else {
      const formula = vertex.getFormula(this.lazilyTransformingAstService);
      const newCellValue = this.evaluateAstToCellValue(formula, new InterpreterState(address, this.config.useArrayArithmetic, vertex));
      return vertex.setCellValue(newCellValue);
    }
  }
  evaluateAstToCellValue(ast, state) {
    const interpreterValue = this.interpreter.evaluateAst(ast, state);
    if (interpreterValue instanceof SimpleRangeValue) {
      return interpreterValue;
    } else if (interpreterValue === EmptyValue && this.config.evaluateNullToZero) {
      return 0;
    } else {
      return interpreterValue;
    }
  }
}
class ExportedCellChange {
  constructor(address, newValue) {
    this.address = address;
    this.newValue = newValue;
  }
  get col() {
    return this.address.col;
  }
  get row() {
    return this.address.row;
  }
  get sheet() {
    return this.address.sheet;
  }
  get value() {
    return this.newValue;
  }
}
class ExportedNamedExpressionChange {
  constructor(name, newValue) {
    this.name = name;
    this.newValue = newValue;
  }
}
class Exporter {
  constructor(config, namedExpressions, sheetMapping, lazilyTransformingService) {
    this.config = config;
    this.namedExpressions = namedExpressions;
    this.sheetMapping = sheetMapping;
    this.lazilyTransformingService = lazilyTransformingService;
  }
  exportChange(change) {
    const value = change.value;
    const address = change.address;
    if (address.sheet === NamedExpressions.SHEET_FOR_WORKBOOK_EXPRESSIONS) {
      const namedExpression = this.namedExpressions.namedExpressionInAddress(address.row);
      if (!namedExpression) {
        throw new Error("Missing named expression");
      }
      return new ExportedNamedExpressionChange(namedExpression.displayName, this.exportScalarOrRange(value));
    } else if (value instanceof SimpleRangeValue) {
      const result = [];
      for (const [cellValue, cellAddress] of value.entriesFromTopLeftCorner(address)) {
        result.push(new ExportedCellChange(cellAddress, this.exportValue(cellValue)));
      }
      return result;
    } else {
      return new ExportedCellChange(address, this.exportValue(value));
    }
  }
  exportValue(value) {
    if (value instanceof SimpleRangeValue) {
      return this.detailedError(new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected));
    } else if (this.config.smartRounding && isExtendedNumber(value)) {
      return this.cellValueRounding(getRawValue(value));
    } else if (value instanceof CellError) {
      return this.detailedError(value);
    } else if (value === EmptyValue) {
      return null;
    } else {
      return getRawValue(value);
    }
  }
  exportScalarOrRange(value) {
    if (value instanceof SimpleRangeValue) {
      return value.rawData().map((row) => row.map((v2) => this.exportValue(v2)));
    } else {
      return this.exportValue(value);
    }
  }
  detailedError(error) {
    var _a2, _b;
    let address = void 0;
    const originAddress = (_a2 = error.root) === null || _a2 === void 0 ? void 0 : _a2.getAddress(this.lazilyTransformingService);
    if (originAddress !== void 0) {
      if (originAddress.sheet === NamedExpressions.SHEET_FOR_WORKBOOK_EXPRESSIONS) {
        address = (_b = this.namedExpressions.namedExpressionInAddress(originAddress.row)) === null || _b === void 0 ? void 0 : _b.displayName;
      } else {
        address = simpleCellAddressToString(this.sheetMapping.getSheetNameOrThrowError.bind(this.sheetMapping), originAddress, -1);
      }
    }
    return new DetailedCellError(error, this.config.translationPackage.getErrorTranslation(error.type), address);
  }
  cellValueRounding(value) {
    if (value === 0) {
      return value;
    }
    const magnitudeMultiplierExponent = Math.floor(Math.log10(Math.abs(value)));
    const placesMultiplier = Math.pow(10, this.config.precisionRounding - magnitudeMultiplierExponent);
    if (value < 0) {
      return -Math.round(-value * placesMultiplier) / placesMultiplier;
    } else {
      return Math.round(value * placesMultiplier) / placesMultiplier;
    }
  }
}
class GraphBuilder {
  /**
   * Configures the building service.
   */
  constructor(dependencyGraph, columnSearch, parser, cellContentParser, stats, arraySizePredictor) {
    this.dependencyGraph = dependencyGraph;
    this.columnSearch = columnSearch;
    this.parser = parser;
    this.cellContentParser = cellContentParser;
    this.stats = stats;
    this.arraySizePredictor = arraySizePredictor;
    this.buildStrategy = new SimpleStrategy(dependencyGraph, columnSearch, parser, stats, cellContentParser, arraySizePredictor);
  }
  /**
   * Builds graph.
   */
  buildGraph(sheets, stats) {
    const dependencies = stats.measure(StatType.COLLECT_DEPENDENCIES, () => this.buildStrategy.run(sheets));
    this.dependencyGraph.getAndClearContentChanges();
    stats.measure(StatType.PROCESS_DEPENDENCIES, () => this.processDependencies(dependencies));
  }
  processDependencies(dependencies) {
    dependencies.forEach((cellDependencies, endVertex) => {
      this.dependencyGraph.processCellDependencies(cellDependencies, endVertex);
    });
  }
}
class SimpleStrategy {
  constructor(dependencyGraph, columnIndex, parser, stats, cellContentParser, arraySizePredictor) {
    this.dependencyGraph = dependencyGraph;
    this.columnIndex = columnIndex;
    this.parser = parser;
    this.stats = stats;
    this.cellContentParser = cellContentParser;
    this.arraySizePredictor = arraySizePredictor;
  }
  run(sheets) {
    const dependencies = /* @__PURE__ */ new Map();
    for (const sheetName in sheets) {
      const sheetId = this.dependencyGraph.getSheetId(sheetName);
      const sheet = sheets[sheetName];
      for (let i2 = 0; i2 < sheet.length; ++i2) {
        const row = sheet[i2];
        for (let j2 = 0; j2 < row.length; ++j2) {
          const cellContent = row[j2];
          const address = simpleCellAddress(sheetId, j2, i2);
          const parsedCellContent = this.cellContentParser.parse(cellContent);
          if (parsedCellContent instanceof CellContent.Formula) {
            const parseResult = this.stats.measure(StatType.PARSER, () => this.parser.parse(parsedCellContent.formula, address));
            if (parseResult.errors.length > 0) {
              this.shrinkArrayIfNeeded(address);
              const vertex = new ParsingErrorVertex(parseResult.errors, parsedCellContent.formula);
              this.dependencyGraph.addVertex(address, vertex);
            } else {
              this.shrinkArrayIfNeeded(address);
              const size = this.arraySizePredictor.checkArraySize(parseResult.ast, address);
              if (size.isScalar()) {
                const vertex = new ScalarFormulaVertex(parseResult.ast, address, 0);
                dependencies.set(vertex, absolutizeDependencies(parseResult.dependencies, address));
                this.dependencyGraph.addVertex(address, vertex);
                if (parseResult.hasVolatileFunction) {
                  this.dependencyGraph.markAsVolatile(vertex);
                }
                if (parseResult.hasStructuralChangeFunction) {
                  this.dependencyGraph.markAsDependentOnStructureChange(vertex);
                }
              } else {
                const vertex = new ArrayFormulaVertex(parseResult.ast, address, new ArraySize(size.width, size.height));
                dependencies.set(vertex, absolutizeDependencies(parseResult.dependencies, address));
                this.dependencyGraph.addArrayVertex(address, vertex);
              }
            }
          } else if (parsedCellContent instanceof CellContent.Empty) ;
          else {
            this.shrinkArrayIfNeeded(address);
            const vertex = new ValueCellVertex(parsedCellContent.value, cellContent);
            this.columnIndex.add(getRawValue(parsedCellContent.value), address);
            this.dependencyGraph.addVertex(address, vertex);
          }
        }
      }
    }
    return dependencies;
  }
  shrinkArrayIfNeeded(address) {
    const vertex = this.dependencyGraph.getCell(address);
    if (vertex instanceof ArrayFormulaVertex) {
      this.dependencyGraph.shrinkArrayToCorner(vertex);
    }
  }
}
const LICENSE_STATUS_MAP = /* @__PURE__ */ new Map([["gpl-v3", 1], ["missing", 2], ["invalid", 3], ["expired", 4]]);
class VersionPlugin extends FunctionPlugin {
  version(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("VERSION"), () => {
      const {
        licenseKeyValidityState: validityState,
        licenseKey
      } = this.config;
      let status;
      if (LICENSE_STATUS_MAP.has(licenseKey)) {
        status = LICENSE_STATUS_MAP.get(licenseKey);
      } else if (LICENSE_STATUS_MAP.has(validityState)) {
        status = LICENSE_STATUS_MAP.get(validityState);
      } else if (validityState === "valid") {
        status = licenseKey.slice(-5);
      }
      return `HyperFormula v${HyperFormula.version}, ${status}`;
    });
  }
}
VersionPlugin.implementedFunctions = {
  "VERSION": {
    method: "version",
    parameters: []
  }
};
function validateAndReturnMetadataFromName(functionId, plugin) {
  var _a2;
  let entry = plugin.implementedFunctions[functionId];
  const key = (_a2 = plugin.aliases) === null || _a2 === void 0 ? void 0 : _a2[functionId];
  if (key !== void 0) {
    if (entry !== void 0) {
      throw new AliasAlreadyExisting(functionId, plugin.name);
    }
    entry = plugin.implementedFunctions[key];
  }
  if (entry === void 0) {
    throw FunctionPluginValidationError.functionNotDeclaredInPlugin(functionId, plugin.name);
  }
  return entry;
}
class FunctionRegistry {
  constructor(config) {
    this.config = config;
    this.functions = /* @__PURE__ */ new Map();
    this.arraySizeFunctions = /* @__PURE__ */ new Map();
    this.volatileFunctions = /* @__PURE__ */ new Set();
    this.arrayFunctions = /* @__PURE__ */ new Set();
    this.structuralChangeFunctions = /* @__PURE__ */ new Set();
    this.functionsWhichDoesNotNeedArgumentsToBeComputed = /* @__PURE__ */ new Set();
    this.functionsMetadata = /* @__PURE__ */ new Map();
    this.doesFunctionNeedArgumentToBeComputed = (functionId) => this.functionsWhichDoesNotNeedArgumentsToBeComputed.has(functionId);
    this.isFunctionVolatile = (functionId) => this.volatileFunctions.has(functionId);
    this.isArrayFunction = (functionId) => this.arrayFunctions.has(functionId);
    this.isFunctionDependentOnSheetStructureChange = (functionId) => this.structuralChangeFunctions.has(functionId);
    if (config.functionPlugins.length > 0) {
      this.instancePlugins = /* @__PURE__ */ new Map();
      for (const plugin of config.functionPlugins) {
        FunctionRegistry.loadPluginFunctions(plugin, this.instancePlugins);
      }
    } else {
      this.instancePlugins = new Map(FunctionRegistry.plugins);
    }
    for (const [functionId, plugin] of FunctionRegistry.protectedFunctions()) {
      FunctionRegistry.loadFunctionUnprotected(plugin, functionId, this.instancePlugins);
    }
    for (const [functionId, plugin] of this.instancePlugins.entries()) {
      this.categorizeFunction(functionId, validateAndReturnMetadataFromName(functionId, plugin));
    }
  }
  static registerFunctionPlugin(plugin, translations) {
    this.loadPluginFunctions(plugin, this.plugins);
    if (translations !== void 0) {
      this.loadTranslations(translations);
    }
  }
  static registerFunction(functionId, plugin, translations) {
    this.loadPluginFunction(plugin, functionId, this.plugins);
    if (translations !== void 0) {
      this.loadTranslations(translations);
    }
  }
  static unregisterFunction(functionId) {
    if (this.functionIsProtected(functionId)) {
      throw ProtectedFunctionError.cannotUnregisterFunctionWithId(functionId);
    }
    this.plugins.delete(functionId);
  }
  static unregisterFunctionPlugin(plugin) {
    for (const protectedPlugin of this.protectedPlugins()) {
      if (protectedPlugin === plugin) {
        throw ProtectedFunctionError.cannotUnregisterProtectedPlugin();
      }
    }
    for (const [functionId, registeredPlugin] of this.plugins.entries()) {
      if (registeredPlugin === plugin) {
        this.plugins.delete(functionId);
      }
    }
  }
  static unregisterAll() {
    this.plugins.clear();
  }
  static getRegisteredFunctionIds() {
    return [...Array.from(this.plugins.keys()), ...Array.from(this._protectedPlugins.keys())];
  }
  static getPlugins() {
    return Array.from(new Set(this.plugins.values()).values());
  }
  static getFunctionPlugin(functionId) {
    if (this.functionIsProtected(functionId)) {
      return void 0;
    } else {
      return this.plugins.get(functionId);
    }
  }
  static functionIsProtected(functionId) {
    return this._protectedPlugins.has(functionId);
  }
  static loadTranslations(translations) {
    const registeredLanguages = new Set(HyperFormula.getRegisteredLanguagesCodes());
    Object.keys(translations).forEach((code) => {
      if (registeredLanguages.has(code)) {
        HyperFormula.getLanguage(code).extendFunctions(translations[code]);
      }
    });
  }
  static loadPluginFunctions(plugin, registry) {
    Object.keys(plugin.implementedFunctions).forEach((functionName2) => {
      this.loadPluginFunction(plugin, functionName2, registry);
    });
    if (plugin.aliases !== void 0) {
      Object.keys(plugin.aliases).forEach((functionName2) => {
        this.loadPluginFunction(plugin, functionName2, registry);
      });
    }
  }
  static loadPluginFunction(plugin, functionId, registry) {
    if (this.functionIsProtected(functionId)) {
      throw ProtectedFunctionError.cannotRegisterFunctionWithId(functionId);
    } else {
      this.loadFunctionUnprotected(plugin, functionId, registry);
    }
  }
  /**
   * Handles deprecated metadata properties.
   */
  static handleDeprecatedMetadata(functionId, metadata) {
    if (metadata && metadata.arrayFunction !== void 0) {
      console.warn(`${functionId}: 'arrayFunction' parameter is deprecated since 3.1.0; Use 'enableArrayArithmeticForArguments' instead.`);
      metadata.enableArrayArithmeticForArguments = metadata.arrayFunction;
    }
    if (metadata && metadata.arraySizeMethod !== void 0) {
      console.warn(`${functionId}: 'arraySizeMethod' parameter is deprecated since 3.1.0; Use 'sizeOfResultArrayMethod' instead.`);
      metadata.sizeOfResultArrayMethod = metadata.arraySizeMethod;
    }
  }
  /**
   * Loads a function into the registry.
   */
  static loadFunctionUnprotected(plugin, functionId, registry) {
    const metadata = validateAndReturnMetadataFromName(functionId, plugin);
    const methodName = metadata.method;
    this.handleDeprecatedMetadata(functionId, metadata);
    if (Object.prototype.hasOwnProperty.call(plugin.prototype, methodName)) {
      registry.set(functionId, plugin);
    } else {
      throw FunctionPluginValidationError.functionMethodNotFound(methodName, plugin.name);
    }
  }
  static *protectedFunctions() {
    for (const [functionId, plugin] of this._protectedPlugins) {
      if (plugin !== void 0) {
        yield [functionId, plugin];
      }
    }
  }
  static *protectedPlugins() {
    for (const [, plugin] of this._protectedPlugins) {
      if (plugin !== void 0) {
        yield plugin;
      }
    }
  }
  initializePlugins(interpreter) {
    const instances = [];
    for (const [functionId, plugin] of this.instancePlugins.entries()) {
      let foundPluginInstance = instances.find((pluginInstance) => pluginInstance instanceof plugin);
      if (foundPluginInstance === void 0) {
        foundPluginInstance = new plugin(interpreter);
        instances.push(foundPluginInstance);
      }
      const metadata = validateAndReturnMetadataFromName(functionId, plugin);
      const methodName = metadata.method;
      this.functions.set(functionId, [methodName, foundPluginInstance]);
      const arraySizeMethodName = metadata.sizeOfResultArrayMethod;
      if (arraySizeMethodName !== void 0) {
        this.arraySizeFunctions.set(functionId, [arraySizeMethodName, foundPluginInstance]);
      }
    }
  }
  getFunctionPlugin(functionId) {
    if (FunctionRegistry.functionIsProtected(functionId)) {
      return void 0;
    }
    return this.instancePlugins.get(functionId);
  }
  getFunction(functionId) {
    const pluginEntry = this.functions.get(functionId);
    if (pluginEntry !== void 0 && this.config.translationPackage.isFunctionTranslated(functionId)) {
      const [pluginFunction, pluginInstance] = pluginEntry;
      return (ast, state) => pluginInstance[pluginFunction](ast, state);
    } else {
      return void 0;
    }
  }
  getArraySizeFunction(functionId) {
    const pluginEntry = this.arraySizeFunctions.get(functionId);
    if (pluginEntry !== void 0 && this.config.translationPackage.isFunctionTranslated(functionId)) {
      const [pluginArraySizeFunction, pluginInstance] = pluginEntry;
      return (ast, state) => pluginInstance[pluginArraySizeFunction](ast, state);
    } else {
      return void 0;
    }
  }
  getMetadata(functionId) {
    return this.functionsMetadata.get(functionId);
  }
  getPlugins() {
    const plugins2 = /* @__PURE__ */ new Set();
    for (const [functionId, plugin] of this.instancePlugins) {
      if (!FunctionRegistry.functionIsProtected(functionId)) {
        plugins2.add(plugin);
      }
    }
    return Array.from(plugins2);
  }
  getRegisteredFunctionIds() {
    return Array.from(this.functions.keys());
  }
  categorizeFunction(functionId, functionMetadata) {
    if (functionMetadata.isVolatile) {
      this.volatileFunctions.add(functionId);
    }
    if (functionMetadata.enableArrayArithmeticForArguments) {
      this.arrayFunctions.add(functionId);
    }
    if (functionMetadata.doesNotNeedArgumentsToBeComputed) {
      this.functionsWhichDoesNotNeedArgumentsToBeComputed.add(functionId);
    }
    if (functionMetadata.isDependentOnSheetStructureChange) {
      this.structuralChangeFunctions.add(functionId);
    }
    this.functionsMetadata.set(functionId, functionMetadata);
  }
}
FunctionRegistry.plugins = /* @__PURE__ */ new Map();
FunctionRegistry._protectedPlugins = /* @__PURE__ */ new Map([["VERSION", VersionPlugin], ["OFFSET", void 0]]);
var CriterionType;
(function(CriterionType2) {
  CriterionType2["GREATER_THAN"] = "GREATER_THAN";
  CriterionType2["GREATER_THAN_OR_EQUAL"] = "GREATER_THAN_OR_EQUAL";
  CriterionType2["LESS_THAN"] = "LESS_THAN";
  CriterionType2["LESS_THAN_OR_EQUAL"] = "LESS_THAN_OR_EQUAL";
  CriterionType2["NOT_EQUAL"] = "NOT_EQUAL";
  CriterionType2["EQUAL"] = "EQUAL";
})(CriterionType || (CriterionType = {}));
const buildCriterion = (operator, value) => ({
  operator,
  value
});
class CriterionBuilder {
  constructor(config) {
    var _a2, _b, _c, _d;
    this.trueString = (_b = (_a2 = config.translationPackage.getMaybeFunctionTranslation("TRUE")) === null || _a2 === void 0 ? void 0 : _a2.toLowerCase()) !== null && _b !== void 0 ? _b : "true";
    this.falseString = (_d = (_c = config.translationPackage.getMaybeFunctionTranslation("FALSE")) === null || _c === void 0 ? void 0 : _c.toLowerCase()) !== null && _d !== void 0 ? _d : "false";
  }
  fromCellValue(raw, arithmeticHelper) {
    if (typeof raw !== "string" && typeof raw !== "boolean" && typeof raw !== "number") {
      return void 0;
    }
    const criterion = this.parseCriterion(raw, arithmeticHelper);
    if (criterion === void 0) {
      return void 0;
    }
    return {
      raw,
      lambda: buildCriterionLambda(criterion, arithmeticHelper)
    };
  }
  parseCriterion(criterion, arithmeticHelper) {
    if (typeof criterion === "number" || typeof criterion === "boolean") {
      return buildCriterion(CriterionType.EQUAL, criterion);
    } else if (typeof criterion === "string") {
      const regexResult = ANY_CRITERION_REGEX.exec(criterion);
      let criterionValue;
      let criterionType;
      if (regexResult) {
        criterionType = StrToCriterionType(regexResult[1]);
        criterionValue = regexResult[2];
      } else {
        criterionType = CriterionType.EQUAL;
        criterionValue = criterion;
      }
      const value = arithmeticHelper.coerceToMaybeNumber(criterionValue);
      const boolvalue = criterionValue.toLowerCase() === this.trueString ? true : criterionValue.toLowerCase() === this.falseString ? false : void 0;
      if (criterionType === void 0) {
        return void 0;
      }
      if (criterionValue === "") {
        return buildCriterion(criterionType, null);
      } else if (value === void 0) {
        if (criterionType === CriterionType.EQUAL || criterionType === CriterionType.NOT_EQUAL) {
          return buildCriterion(criterionType, boolvalue !== null && boolvalue !== void 0 ? boolvalue : criterionValue);
        }
      } else {
        return buildCriterion(criterionType, getRawValue(value));
      }
    }
    return void 0;
  }
}
const ANY_CRITERION_REGEX = /([<>=]+)(.*)/;
function StrToCriterionType(str) {
  switch (str) {
    case ">":
      return CriterionType.GREATER_THAN;
    case ">=":
      return CriterionType.GREATER_THAN_OR_EQUAL;
    case "<":
      return CriterionType.LESS_THAN;
    case "<=":
      return CriterionType.LESS_THAN_OR_EQUAL;
    case "<>":
      return CriterionType.NOT_EQUAL;
    case "=":
      return CriterionType.EQUAL;
    default:
      return void 0;
  }
}
const buildCriterionLambda = (criterion, arithmeticHelper) => {
  switch (criterion.operator) {
    case CriterionType.GREATER_THAN: {
      if (typeof criterion.value === "number") {
        return (cellValue) => typeof cellValue === "number" && arithmeticHelper.floatCmp(cellValue, criterion.value) > 0;
      } else {
        return (_cellValue) => false;
      }
    }
    case CriterionType.GREATER_THAN_OR_EQUAL: {
      if (typeof criterion.value === "number") {
        return (cellValue) => typeof cellValue === "number" && arithmeticHelper.floatCmp(cellValue, criterion.value) >= 0;
      } else {
        return (_cellValue) => false;
      }
    }
    case CriterionType.LESS_THAN: {
      if (typeof criterion.value === "number") {
        return (cellValue) => typeof cellValue === "number" && arithmeticHelper.floatCmp(cellValue, criterion.value) < 0;
      } else {
        return (_cellValue) => false;
      }
    }
    case CriterionType.LESS_THAN_OR_EQUAL: {
      if (typeof criterion.value === "number") {
        return (cellValue) => typeof cellValue === "number" && arithmeticHelper.floatCmp(cellValue, criterion.value) <= 0;
      } else {
        return (_cellValue) => false;
      }
    }
    case CriterionType.EQUAL: {
      if (typeof criterion.value === "number") {
        return (cellValue) => {
          if (typeof cellValue === "number") {
            return arithmeticHelper.floatCmp(cellValue, criterion.value) === 0;
          } else if (typeof cellValue === "string") {
            if (cellValue === "") {
              return false;
            }
            const val = arithmeticHelper.coerceToMaybeNumber(cellValue);
            if (val === void 0) {
              return false;
            }
            return arithmeticHelper.floatCmp(val, criterion.value) === 0;
          } else {
            return false;
          }
        };
      } else if (typeof criterion.value === "string") {
        return arithmeticHelper.eqMatcherFunction(criterion.value);
      } else if (typeof criterion.value === "boolean") {
        return (cellValue) => typeof cellValue === "boolean" && cellValue === criterion.value;
      } else {
        return (cellValue) => cellValue === EmptyValue;
      }
    }
    case CriterionType.NOT_EQUAL: {
      if (typeof criterion.value === "number") {
        return (cellValue) => {
          if (typeof cellValue === "number") {
            return arithmeticHelper.floatCmp(cellValue, criterion.value) !== 0;
          } else if (typeof cellValue === "string") {
            if (cellValue === "") {
              return true;
            }
            const val = arithmeticHelper.coerceToMaybeNumber(cellValue);
            if (val === void 0) {
              return true;
            }
            return arithmeticHelper.floatCmp(val, criterion.value) !== 0;
          } else {
            return true;
          }
        };
      } else if (typeof criterion.value === "string") {
        return arithmeticHelper.neqMatcherFunction(criterion.value);
      } else if (typeof criterion.value === "boolean") {
        return (cellValue) => typeof cellValue !== "boolean" || cellValue !== criterion.value;
      } else {
        return (cellValue) => cellValue !== EmptyValue;
      }
    }
  }
};
class Interpreter {
  constructor(config, dependencyGraph, columnSearch, stats, arithmeticHelper, functionRegistry, namedExpressions, serialization, arraySizePredictor, dateTimeHelper) {
    this.config = config;
    this.dependencyGraph = dependencyGraph;
    this.columnSearch = columnSearch;
    this.stats = stats;
    this.arithmeticHelper = arithmeticHelper;
    this.functionRegistry = functionRegistry;
    this.namedExpressions = namedExpressions;
    this.serialization = serialization;
    this.arraySizePredictor = arraySizePredictor;
    this.dateTimeHelper = dateTimeHelper;
    this.equalOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.eq, arg1, arg2);
    this.notEqualOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.neq, arg1, arg2);
    this.greaterThanOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.gt, arg1, arg2);
    this.lessThanOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.lt, arg1, arg2);
    this.greaterThanOrEqualOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.geq, arg1, arg2);
    this.lessThanOrEqualOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.leq, arg1, arg2);
    this.concatOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.concat, coerceScalarToString(arg1), coerceScalarToString(arg2));
    this.plusOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.addWithEpsilon, this.arithmeticHelper.coerceScalarToNumberOrError(arg1), this.arithmeticHelper.coerceScalarToNumberOrError(arg2));
    this.minusOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.subtract, this.arithmeticHelper.coerceScalarToNumberOrError(arg1), this.arithmeticHelper.coerceScalarToNumberOrError(arg2));
    this.timesOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.multiply, this.arithmeticHelper.coerceScalarToNumberOrError(arg1), this.arithmeticHelper.coerceScalarToNumberOrError(arg2));
    this.powerOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.pow, this.arithmeticHelper.coerceScalarToNumberOrError(arg1), this.arithmeticHelper.coerceScalarToNumberOrError(arg2));
    this.divOp = (arg1, arg2) => binaryErrorWrapper(this.arithmeticHelper.divide, this.arithmeticHelper.coerceScalarToNumberOrError(arg1), this.arithmeticHelper.coerceScalarToNumberOrError(arg2));
    this.unaryMinusOp = (arg) => unaryErrorWrapper(this.arithmeticHelper.unaryMinus, this.arithmeticHelper.coerceScalarToNumberOrError(arg));
    this.percentOp = (arg) => unaryErrorWrapper(this.arithmeticHelper.unaryPercent, this.arithmeticHelper.coerceScalarToNumberOrError(arg));
    this.unaryPlusOp = (arg) => this.arithmeticHelper.unaryPlus(arg);
    this.functionRegistry.initializePlugins(this);
    this.criterionBuilder = new CriterionBuilder(config);
  }
  evaluateAst(ast, state) {
    let val = this.evaluateAstWithoutPostprocessing(ast, state);
    if (isExtendedNumber(val)) {
      if (isNumberOverflow(getRawValue(val))) {
        return new CellError(ErrorType.NUM, ErrorMessage.NaN);
      } else {
        val = cloneNumber(val, fixNegativeZero(getRawValue(val)));
      }
    }
    if (val instanceof SimpleRangeValue && val.height() === 1 && val.width() === 1) {
      [[val]] = val.data;
    }
    return wrapperForRootVertex(val, state.formulaVertex);
  }
  /**
   * Calculates cell value from formula abstract syntax tree
   *
   * @param {Ast} ast - abstract syntax tree of formula
   * @param {InterpreterState} state - interpreter state
   */
  evaluateAstWithoutPostprocessing(ast, state) {
    switch (ast.type) {
      case AstNodeType.EMPTY: {
        return EmptyValue;
      }
      case AstNodeType.CELL_REFERENCE: {
        const address = ast.reference.toSimpleCellAddress(state.formulaAddress);
        if (isColOrRowInvalid(address)) {
          return new CellError(ErrorType.REF, ErrorMessage.BadRef);
        }
        if (!this.isSheetValid(ast.reference)) {
          return new CellError(ErrorType.REF, ErrorMessage.SheetRef);
        }
        return this.dependencyGraph.getCellValue(address);
      }
      case AstNodeType.NUMBER:
      case AstNodeType.STRING: {
        return ast.value;
      }
      case AstNodeType.CONCATENATE_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.concatOp, leftResult, rightResult, state);
      }
      case AstNodeType.EQUALS_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.equalOp, leftResult, rightResult, state);
      }
      case AstNodeType.NOT_EQUAL_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.notEqualOp, leftResult, rightResult, state);
      }
      case AstNodeType.GREATER_THAN_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.greaterThanOp, leftResult, rightResult, state);
      }
      case AstNodeType.LESS_THAN_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.lessThanOp, leftResult, rightResult, state);
      }
      case AstNodeType.GREATER_THAN_OR_EQUAL_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.greaterThanOrEqualOp, leftResult, rightResult, state);
      }
      case AstNodeType.LESS_THAN_OR_EQUAL_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.lessThanOrEqualOp, leftResult, rightResult, state);
      }
      case AstNodeType.PLUS_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.plusOp, leftResult, rightResult, state);
      }
      case AstNodeType.MINUS_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.minusOp, leftResult, rightResult, state);
      }
      case AstNodeType.TIMES_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.timesOp, leftResult, rightResult, state);
      }
      case AstNodeType.POWER_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.powerOp, leftResult, rightResult, state);
      }
      case AstNodeType.DIV_OP: {
        const leftResult = this.evaluateAst(ast.left, state);
        const rightResult = this.evaluateAst(ast.right, state);
        return this.binaryRangeWrapper(this.divOp, leftResult, rightResult, state);
      }
      case AstNodeType.PLUS_UNARY_OP: {
        const result = this.evaluateAst(ast.value, state);
        return this.unaryRangeWrapper(this.unaryPlusOp, result, state);
      }
      case AstNodeType.MINUS_UNARY_OP: {
        const result = this.evaluateAst(ast.value, state);
        return this.unaryRangeWrapper(this.unaryMinusOp, result, state);
      }
      case AstNodeType.PERCENT_OP: {
        const result = this.evaluateAst(ast.value, state);
        return this.unaryRangeWrapper(this.percentOp, result, state);
      }
      case AstNodeType.FUNCTION_CALL: {
        if (this.config.licenseKeyValidityState !== "valid" && !FunctionRegistry.functionIsProtected(ast.procedureName)) {
          return new CellError(ErrorType.LIC, ErrorMessage.LicenseKey(this.config.licenseKeyValidityState));
        }
        const pluginFunction = this.functionRegistry.getFunction(ast.procedureName);
        if (pluginFunction !== void 0) {
          return pluginFunction(ast, new InterpreterState(state.formulaAddress, state.arraysFlag || this.functionRegistry.isArrayFunction(ast.procedureName), state.formulaVertex));
        } else {
          return new CellError(ErrorType.NAME, ErrorMessage.FunctionName(ast.procedureName));
        }
      }
      case AstNodeType.NAMED_EXPRESSION: {
        const namedExpression = this.namedExpressions.nearestNamedExpression(ast.expressionName, state.formulaAddress.sheet);
        if (namedExpression) {
          return this.dependencyGraph.getCellValue(namedExpression.address);
        } else {
          return new CellError(ErrorType.NAME, ErrorMessage.NamedExpressionName(ast.expressionName));
        }
      }
      case AstNodeType.CELL_RANGE: {
        if (!this.isSheetValid(ast.start) || !this.isSheetValid(ast.end)) {
          return new CellError(ErrorType.REF, ErrorMessage.SheetRef);
        }
        if (!this.rangeSpansOneSheet(ast)) {
          return new CellError(ErrorType.REF, ErrorMessage.RangeManySheets);
        }
        const range = AbsoluteCellRange.fromCellRange(ast, state.formulaAddress);
        const arrayVertex = this.dependencyGraph.getArray(range);
        if (arrayVertex) {
          const array = arrayVertex.array;
          if (array instanceof NotComputedArray) {
            throw new Error("Array should be already computed");
          } else if (array instanceof CellError) {
            return array;
          } else if (array instanceof ArrayValue) {
            return SimpleRangeValue.fromRange(array.raw(), range, this.dependencyGraph);
          } else {
            throw new Error("Unknown array");
          }
        }
        return SimpleRangeValue.onlyRange(range, this.dependencyGraph);
      }
      case AstNodeType.COLUMN_RANGE: {
        if (!this.isSheetValid(ast.start) || !this.isSheetValid(ast.end)) {
          return new CellError(ErrorType.REF, ErrorMessage.SheetRef);
        }
        if (!this.rangeSpansOneSheet(ast)) {
          return new CellError(ErrorType.REF, ErrorMessage.RangeManySheets);
        }
        const range = AbsoluteColumnRange.fromColumnRange(ast, state.formulaAddress);
        return SimpleRangeValue.onlyRange(range, this.dependencyGraph);
      }
      case AstNodeType.ROW_RANGE: {
        if (!this.isSheetValid(ast.start) || !this.isSheetValid(ast.end)) {
          return new CellError(ErrorType.REF, ErrorMessage.SheetRef);
        }
        if (!this.rangeSpansOneSheet(ast)) {
          return new CellError(ErrorType.REF, ErrorMessage.RangeManySheets);
        }
        const range = AbsoluteRowRange.fromRowRangeAst(ast, state.formulaAddress);
        return SimpleRangeValue.onlyRange(range, this.dependencyGraph);
      }
      case AstNodeType.PARENTHESIS: {
        return this.evaluateAst(ast.expression, state);
      }
      case AstNodeType.ARRAY: {
        let totalWidth = void 0;
        const ret = [];
        for (const astRow of ast.args) {
          let rowHeight = void 0;
          const rowRet = [];
          for (const astIt of astRow) {
            const arr = coerceToRange(this.evaluateAst(astIt, state));
            const height = arr.height();
            if (rowHeight === void 0) {
              rowHeight = height;
              rowRet.push(...arr.data);
            } else if (rowHeight === height) {
              for (let i2 = 0; i2 < height; i2++) {
                rowRet[i2].push(...arr.data[i2]);
              }
            } else {
              return new CellError(ErrorType.REF, ErrorMessage.SizeMismatch);
            }
          }
          const width = rowRet[0].length;
          if (totalWidth === void 0) {
            totalWidth = width;
            ret.push(...rowRet);
          } else if (totalWidth === width) {
            ret.push(...rowRet);
          } else {
            return new CellError(ErrorType.REF, ErrorMessage.SizeMismatch);
          }
        }
        return SimpleRangeValue.onlyValues(ret);
      }
      case AstNodeType.ERROR_WITH_RAW_INPUT:
      case AstNodeType.ERROR: {
        return ast.error;
      }
    }
  }
  /**
   * Sheet is valid if:
   * - sheet is undefined OR
   * - sheet is a named expressions store OR
   * - sheet exists in sheet mapping
   * - sheet is not a placeholder
   */
  isSheetValid(address) {
    return address.sheet === void 0 || address.sheet === NamedExpressions.SHEET_FOR_WORKBOOK_EXPRESSIONS || this.dependencyGraph.sheetMapping.hasSheetWithId(address.sheet, {
      includePlaceholders: false
    });
  }
  rangeSpansOneSheet(ast) {
    return ast.start.sheet === ast.end.sheet;
  }
  unaryRangeWrapper(op, arg, state) {
    var _a2;
    if (arg instanceof SimpleRangeValue && !state.arraysFlag) {
      arg = (_a2 = coerceRangeToScalar(arg, state)) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
    }
    if (arg instanceof CellError) {
      return arg;
    }
    if (arg instanceof SimpleRangeValue) {
      const newRaw = arg.data.map((row) => row.map(op));
      return SimpleRangeValue.onlyValues(newRaw);
    }
    return op(arg);
  }
  binaryRangeWrapper(op, arg1, arg2, state) {
    var _a2, _b;
    if (arg1 instanceof SimpleRangeValue && !state.arraysFlag) {
      arg1 = (_a2 = coerceRangeToScalar(arg1, state)) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
    }
    if (arg1 instanceof CellError) {
      return arg1;
    }
    if (arg2 instanceof SimpleRangeValue && !state.arraysFlag) {
      arg2 = (_b = coerceRangeToScalar(arg2, state)) !== null && _b !== void 0 ? _b : new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
    }
    if (arg2 instanceof CellError) {
      return arg2;
    }
    if (arg1 instanceof SimpleRangeValue || arg2 instanceof SimpleRangeValue) {
      if (!(arg1 instanceof SimpleRangeValue)) {
        if (arg2.isAdHoc()) {
          const raw2 = arg2.data;
          for (let i2 = 0; i2 < raw2.length; i2++) {
            for (let j2 = 0; j2 < raw2[0].length; j2++) {
              raw2[i2][j2] = op(arg1, raw2[i2][j2]);
            }
          }
          return SimpleRangeValue.onlyValues(raw2);
        } else {
          arg1 = SimpleRangeValue.fromScalar(arg1);
        }
      }
      if (!(arg2 instanceof SimpleRangeValue)) {
        if (arg1.isAdHoc()) {
          const raw1 = arg1.data;
          for (let i2 = 0; i2 < raw1.length; i2++) {
            for (let j2 = 0; j2 < raw1[0].length; j2++) {
              raw1[i2][j2] = op(raw1[i2][j2], arg2);
            }
          }
          return SimpleRangeValue.onlyValues(raw1);
        } else {
          arg2 = SimpleRangeValue.fromScalar(arg2);
        }
      }
      if (arg1.width() === arg2.width() && arg1.height() === arg2.height()) {
        if (arg1.isAdHoc()) {
          const raw1 = arg1.data;
          const raw2 = arg2.data;
          for (let i2 = 0; i2 < raw1.length; i2++) {
            for (let j2 = 0; j2 < raw1[0].length; j2++) {
              raw1[i2][j2] = op(raw1[i2][j2], raw2[i2][j2]);
            }
          }
          return SimpleRangeValue.onlyValues(raw1);
        }
        if (arg2.isAdHoc()) {
          const raw1 = arg1.data;
          const raw2 = arg2.data;
          for (let i2 = 0; i2 < raw1.length; i2++) {
            for (let j2 = 0; j2 < raw1[0].length; j2++) {
              raw2[i2][j2] = op(raw1[i2][j2], raw2[i2][j2]);
            }
          }
          return SimpleRangeValue.onlyValues(raw2);
        }
      }
      const width = Math.max(arg1.width(), arg2.width());
      const height = Math.max(arg1.height(), arg2.height());
      const ret = Array(height);
      for (let i2 = 0; i2 < height; i2++) {
        ret[i2] = Array(width);
      }
      for (let i2 = 0; i2 < height; i2++) {
        const i1 = arg1.height() !== 1 ? i2 : 0;
        const i22 = arg2.height() !== 1 ? i2 : 0;
        for (let j2 = 0; j2 < width; j2++) {
          const j1 = arg1.width() !== 1 ? j2 : 0;
          const j22 = arg2.width() !== 1 ? j2 : 0;
          if (i1 < arg1.height() && i22 < arg2.height() && j1 < arg1.width() && j22 < arg2.width()) {
            ret[i2][j2] = op(arg1.data[i1][j1], arg2.data[i22][j22]);
          } else {
            ret[i2][j2] = new CellError(ErrorType.NA);
          }
        }
      }
      return SimpleRangeValue.onlyValues(ret);
    }
    return op(arg1, arg2);
  }
}
function unaryErrorWrapper(op, arg) {
  if (arg instanceof CellError) {
    return arg;
  } else {
    return op(arg);
  }
}
function binaryErrorWrapper(op, arg1, arg2) {
  if (arg1 instanceof CellError) {
    return arg1;
  } else if (arg2 instanceof CellError) {
    return arg2;
  } else {
    return op(arg1, arg2);
  }
}
function wrapperForRootVertex(val, vertex) {
  if (val instanceof CellError && vertex !== void 0) {
    return val.attachRootVertex(vertex);
  }
  return val;
}
class CombinedTransformer {
  constructor(sheet) {
    this.sheet = sheet;
    this.transformations = [];
  }
  add(transformation) {
    this.transformations.push(transformation);
  }
  performEagerTransformations(graph, parser) {
    this.transformations.forEach((transformation) => transformation.performEagerTransformations(graph, parser));
  }
  transformSingleAst(ast, address) {
    let [transformedAst, transformedAddress] = [ast, address];
    this.transformations.forEach((transformation) => {
      [transformedAst, transformedAddress] = transformation.transformSingleAst(transformedAst, transformedAddress);
    });
    return [transformedAst, transformedAddress];
  }
  isIrreversible() {
    return true;
  }
}
class LazilyTransformingAstService {
  constructor(stats, maxPendingLazyTransformations) {
    this.stats = stats;
    this.maxPendingLazyTransformations = maxPendingLazyTransformations;
    this.transformations = [];
    this.versionOffset = 0;
  }
  version() {
    return this.versionOffset + this.transformations.length;
  }
  addTransformation(transformation) {
    if (this.combinedTransformer !== void 0) {
      this.combinedTransformer.add(transformation);
    } else {
      this.transformations.push(transformation);
    }
    return this.version();
  }
  beginCombinedMode(sheet) {
    this.combinedTransformer = new CombinedTransformer(sheet);
  }
  commitCombinedMode() {
    if (this.combinedTransformer === void 0) {
      throw Error("Combined mode wasn't started");
    }
    this.transformations.push(this.combinedTransformer);
    this.combinedTransformer = void 0;
    return this.version();
  }
  applyTransformations(ast, address, version) {
    this.stats.start(StatType.TRANSFORM_ASTS_POSTPONED);
    const currentVersion = this.version();
    for (let v2 = Math.max(version, this.versionOffset); v2 < currentVersion; v2++) {
      const transformation = this.transformations[v2 - this.versionOffset];
      if (transformation.isIrreversible()) {
        this.undoRedo.storeDataForVersion(v2, address, this.parser.computeHashFromAst(ast));
        this.parser.rememberNewAst(ast);
      }
      const [newAst, newAddress] = transformation.transformSingleAst(ast, address);
      ast = newAst;
      address = newAddress;
    }
    const cachedAst = this.parser.rememberNewAst(ast);
    this.stats.end(StatType.TRANSFORM_ASTS_POSTPONED);
    return [cachedAst, address, currentVersion];
  }
  *getTransformationsFrom(version, filter2) {
    const currentVersion = this.version();
    for (let v2 = Math.max(version, this.versionOffset); v2 < currentVersion; v2++) {
      const transformation = this.transformations[v2 - this.versionOffset];
      if (!filter2 || filter2(transformation)) {
        yield transformation;
      }
    }
  }
  /**
   * Returns true when enough transformations have accumulated to justify the cost
   * of forcing all consumers (FormulaVertex, ColumnIndex) to apply pending changes.
   */
  needsCompaction() {
    return this.transformations.length >= this.maxPendingLazyTransformations;
  }
  /**
   * Compacts the transformations array by discarding all entries that have already
   * been applied by every consumer. Safe to call only after all FormulaVertex and
   * ColumnIndex consumers have been brought up to the current version.
   * After calling, UndoRedo.cleanupOrphanedOldData() must be invoked to remove
   * oldData entries written during forceApplyPostponedTransformations for
   * already-evicted undo entries.
   */
  compact() {
    this.versionOffset += this.transformations.length;
    this.transformations = [];
  }
}
const NOT_FOUND$1 = -1;
function findLastOccurrenceInOrderedRange(searchKey, range, {
  searchCoordinate,
  orderingDirection,
  ifNoMatch
}, dependencyGraph) {
  const start = range.start[searchCoordinate];
  const end = searchCoordinate === "col" ? range.effectiveEndColumn(dependencyGraph) : range.effectiveEndRow(dependencyGraph);
  const getValueFromIndexFn = searchCoordinate === "col" ? (index2) => getRawValue(dependencyGraph.getCellValue(simpleCellAddress(range.sheet, index2, range.start.row))) : (index2) => getRawValue(dependencyGraph.getCellValue(simpleCellAddress(range.sheet, range.start.col, index2)));
  const compareFn = orderingDirection === "asc" ? (left, right) => compare(left, right) : (left, right) => -compare(left, right);
  const foundIndex = findLastMatchingIndex((index2) => compareFn(searchKey, getValueFromIndexFn(index2)) >= 0, start, end);
  const foundValue = getValueFromIndexFn(foundIndex);
  if (foundValue === searchKey) {
    return foundIndex - start;
  }
  if (ifNoMatch === "returnLowerBound") {
    if (foundIndex === NOT_FOUND$1) {
      return orderingDirection === "asc" ? NOT_FOUND$1 : 0;
    }
    if (typeof foundValue !== typeof searchKey) {
      return NOT_FOUND$1;
    }
    if (orderingDirection === "asc") {
      return foundIndex - start;
    }
    const nextIndex = foundIndex + 1;
    return nextIndex <= end ? nextIndex - start : NOT_FOUND$1;
  }
  if (ifNoMatch === "returnUpperBound") {
    if (foundIndex === NOT_FOUND$1) {
      return orderingDirection === "asc" ? 0 : NOT_FOUND$1;
    }
    if (typeof foundValue !== typeof searchKey) {
      return NOT_FOUND$1;
    }
    if (orderingDirection === "desc") {
      return foundIndex - start;
    }
    const nextIndex = foundIndex + 1;
    return nextIndex <= end ? nextIndex - start : NOT_FOUND$1;
  }
  return NOT_FOUND$1;
}
function findLastMatchingIndex(predicate, startRange, endRange) {
  let start = startRange;
  let end = endRange;
  while (start < end) {
    const pivot = Math.ceil((start + end) / 2);
    if (predicate(pivot)) {
      start = pivot;
    } else {
      end = pivot - 1;
    }
  }
  if (start === end && predicate(start)) {
    return start;
  }
  return NOT_FOUND$1;
}
function compare(left, right) {
  if (typeof left === typeof right) {
    if (left === EmptyValue) {
      return 0;
    }
    return left < right ? -1 : left > right ? 1 : 0;
  }
  if (left === EmptyValue) {
    return -1;
  }
  if (right === EmptyValue) {
    return 1;
  }
  if (right instanceof CellError) {
    return -1;
  }
  if (typeof left === "number" && typeof right === "string") {
    return -1;
  }
  if (typeof left === "number" && typeof right === "boolean") {
    return -1;
  }
  if (typeof left === "string" && typeof right === "number") {
    return 1;
  }
  if (typeof left === "string" && typeof right === "boolean") {
    return -1;
  }
  return 1;
}
const NOT_FOUND = -1;
class AdvancedFind {
  constructor(dependencyGraph) {
    this.dependencyGraph = dependencyGraph;
  }
  advancedFind(keyMatcher, rangeValue, {
    returnOccurrence
  } = {
    returnOccurrence: "first"
  }) {
    const range = rangeValue.range;
    const values2 = range === void 0 ? rangeValue.valuesFromTopLeftCorner() : this.dependencyGraph.computeListOfValuesInRange(range);
    const initialIterationIndex = returnOccurrence === "first" ? 0 : values2.length - 1;
    const iterationCondition = returnOccurrence === "first" ? (i2) => i2 < values2.length : (i2) => i2 >= 0;
    const incrementIndex = returnOccurrence === "first" ? (i2) => i2 + 1 : (i2) => i2 - 1;
    for (let i2 = initialIterationIndex; iterationCondition(i2); i2 = incrementIndex(i2)) {
      if (keyMatcher(getRawValue(values2[i2]))) {
        return i2;
      }
    }
    return NOT_FOUND;
  }
  basicFind(searchKey, rangeValue, searchCoordinate, {
    ordering,
    ifNoMatch,
    returnOccurrence
  }) {
    const normalizedSearchKey = typeof searchKey === "string" ? forceNormalizeString(searchKey) : searchKey;
    const range = rangeValue.range;
    if (range === void 0) {
      return this.findNormalizedValue(normalizedSearchKey, rangeValue.valuesFromTopLeftCorner(), ifNoMatch, returnOccurrence);
    }
    if (ordering === "none") {
      return this.findNormalizedValue(normalizedSearchKey, this.dependencyGraph.computeListOfValuesInRange(range), ifNoMatch, returnOccurrence);
    }
    return findLastOccurrenceInOrderedRange(normalizedSearchKey, range, {
      searchCoordinate,
      orderingDirection: ordering,
      ifNoMatch
    }, this.dependencyGraph);
  }
  findNormalizedValue(searchKey, searchArray, ifNoMatch = "returnNotFound", returnOccurrence = "first") {
    const normalizedArray = searchArray.map(getRawValue).map((val) => typeof val === "string" ? forceNormalizeString(val) : val);
    if (ifNoMatch === "returnNotFound") {
      return returnOccurrence === "first" ? normalizedArray.indexOf(searchKey) : normalizedArray.lastIndexOf(searchKey);
    }
    const compareFn = ifNoMatch === "returnLowerBound" ? (left, right) => compare(left, right) : (left, right) => -compare(left, right);
    let bestValue = ifNoMatch === "returnLowerBound" ? -Infinity : Infinity;
    let bestIndex = NOT_FOUND;
    const initialIterationIndex = returnOccurrence === "first" ? 0 : normalizedArray.length - 1;
    const iterationCondition = returnOccurrence === "first" ? (i2) => i2 < normalizedArray.length : (i2) => i2 >= 0;
    const incrementIndex = returnOccurrence === "first" ? (i2) => i2 + 1 : (i2) => i2 - 1;
    for (let i2 = initialIterationIndex; iterationCondition(i2); i2 = incrementIndex(i2)) {
      const value = normalizedArray[i2];
      if (value === searchKey) {
        return i2;
      }
      if (compareFn(value, searchKey) > 0) {
        continue;
      }
      if (compareFn(bestValue, value) < 0) {
        bestValue = value;
        bestIndex = i2;
      }
    }
    return bestIndex;
  }
}
class ColumnBinarySearch extends AdvancedFind {
  constructor(dependencyGraph) {
    super(dependencyGraph);
    this.dependencyGraph = dependencyGraph;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars 
  add(value, address) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remove(value, address) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  change(oldValue, newValue, address) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  applyChanges(contentChanges) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addColumns(columnsSpan) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeColumns(columnsSpan) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeSheet(sheetId) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  moveValues(sourceRange, toRight, toBottom, toSheet) {
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeValues(range) {
  }
  /**
   * No-op: ColumnBinarySearch reads cell values directly from the dependency graph
   * on every lookup, so it has no cached data that could become stale.
   * Unlike ColumnIndex, which maintains a separate value-to-address index that
   * must be kept in sync with lazy transformations, binary search always operates
   * on the current graph state.
   */
  forceApplyPostponedTransformations() {
  }
  /*
   * WARNING: Finding lower/upper bounds in unordered ranges is not supported. When ordering === 'none', assumes matchExactly === true
   */
  find(searchKey, rangeValue, searchOptions) {
    return this.basicFind(searchKey, rangeValue, "row", searchOptions);
  }
}
class ColumnIndex {
  constructor(dependencyGraph, config, stats) {
    this.dependencyGraph = dependencyGraph;
    this.config = config;
    this.stats = stats;
    this.index = /* @__PURE__ */ new Map();
    this.transformingService = this.dependencyGraph.lazilyTransformingAstService;
    this.binarySearchStrategy = new ColumnBinarySearch(dependencyGraph);
  }
  add(value, address) {
    if (value === EmptyValue || value instanceof CellError) {
      return;
    } else if (value instanceof SimpleRangeValue) {
      for (const [arrayValue, cellAddress] of value.entriesFromTopLeftCorner(address)) {
        this.addSingleCellValue(getRawValue(arrayValue), cellAddress);
      }
    } else {
      this.addSingleCellValue(value, address);
    }
  }
  remove(value, address) {
    if (value === void 0) {
      return;
    }
    if (value instanceof SimpleRangeValue) {
      for (const [arrayValue, cellAddress] of value.entriesFromTopLeftCorner(address)) {
        this.removeSingleValue(getRawValue(arrayValue), cellAddress);
      }
    } else {
      this.removeSingleValue(value, address);
    }
  }
  change(oldValue, newValue, address) {
    if (oldValue === newValue) {
      return;
    }
    this.remove(oldValue, address);
    this.add(newValue, address);
  }
  applyChanges(contentChanges) {
    for (const change of contentChanges) {
      if (change.oldValue !== void 0) {
        this.change(getRawValue(change.oldValue), getRawValue(change.value), change.address);
      }
    }
  }
  moveValues(sourceRange, toRight, toBottom, toSheet) {
    for (const [value, address] of sourceRange) {
      const targetAddress = movedSimpleCellAddress(address, toSheet, toRight, toBottom);
      this.remove(value, address);
      this.add(value, targetAddress);
    }
  }
  removeValues(range) {
    for (const [value, address] of range) {
      this.remove(value, address);
    }
  }
  find(searchKey, rangeValue, {
    ordering,
    ifNoMatch,
    returnOccurrence
  }) {
    if (returnOccurrence == null) {
      returnOccurrence = ordering === "none" ? "first" : "last";
    }
    const resultUsingColumnIndex = this.findUsingColumnIndex(searchKey, rangeValue, returnOccurrence);
    return resultUsingColumnIndex !== void 0 ? resultUsingColumnIndex : this.binarySearchStrategy.find(searchKey, rangeValue, {
      ordering,
      ifNoMatch,
      returnOccurrence
    });
  }
  findUsingColumnIndex(key, rangeValue, returnOccurrence) {
    const range = rangeValue.range;
    if (range === void 0) {
      return void 0;
    }
    this.ensureRecentData(range.sheet, range.start.col, key);
    const columnMap = this.getColumnMap(range.sheet, range.start.col);
    if (!columnMap) {
      return -1;
    }
    const normalizedKey = typeof key === "string" ? forceNormalizeString(key) : key;
    const valueIndexForTheKey = columnMap.get(normalizedKey);
    if (!valueIndexForTheKey || !valueIndexForTheKey.index || valueIndexForTheKey.index.length === 0) {
      return void 0;
    }
    const rowNumber = ColumnIndex.findRowBelongingToRange(valueIndexForTheKey, range, returnOccurrence);
    return rowNumber !== void 0 ? rowNumber - range.start.row : void 0;
  }
  static findRowBelongingToRange(valueIndex, range, returnOccurrence) {
    const start = range.start.row;
    const end = range.end.row;
    const positionInIndex = returnOccurrence === "first" ? findInOrderedArray(start, valueIndex.index, "upperBound") : findInOrderedArray(end, valueIndex.index, "lowerBound");
    if (positionInIndex === -1) {
      return void 0;
    }
    const rowNumber = valueIndex.index[positionInIndex];
    const isRowNumberBelongingToRange = rowNumber >= start && rowNumber <= end;
    return isRowNumberBelongingToRange ? rowNumber : void 0;
  }
  advancedFind(keyMatcher, range, options = {
    returnOccurrence: "first"
  }) {
    return this.binarySearchStrategy.advancedFind(keyMatcher, range, options);
  }
  addColumns(columnsSpan) {
    const sheetIndex = this.index.get(columnsSpan.sheet);
    if (!sheetIndex) {
      return;
    }
    sheetIndex.splice(columnsSpan.columnStart, 0, ...Array(columnsSpan.numberOfColumns));
  }
  removeColumns(columnsSpan) {
    const sheetIndex = this.index.get(columnsSpan.sheet);
    if (!sheetIndex) {
      return;
    }
    sheetIndex.splice(columnsSpan.columnStart, columnsSpan.numberOfColumns);
  }
  removeSheet(sheetId) {
    this.index.delete(sheetId);
  }
  /**
   * Forces all ValueIndex entries to apply any pending lazy transformations,
   * bringing every entry up to the current LazilyTransformingAstService version.
   * Must be called before compacting LazilyTransformingAstService.
   */
  forceApplyPostponedTransformations() {
    for (const [sheet, sheetIndex] of this.index) {
      sheetIndex.forEach((columnMap, col) => {
        if (!columnMap) {
          return;
        }
        for (const value of columnMap.keys()) {
          this.ensureRecentData(sheet, col, value);
        }
      });
    }
  }
  getColumnMap(sheet, col) {
    if (!this.index.has(sheet)) {
      this.index.set(sheet, []);
    }
    const sheetMap = this.index.get(sheet);
    let columnMap = sheetMap[col];
    if (!columnMap) {
      columnMap = /* @__PURE__ */ new Map();
      sheetMap[col] = columnMap;
    }
    return columnMap;
  }
  getValueIndex(sheet, col, value) {
    const columnMap = this.getColumnMap(sheet, col);
    let index2 = this.getColumnMap(sheet, col).get(value);
    if (!index2) {
      index2 = {
        version: this.transformingService.version(),
        index: []
      };
      columnMap.set(value, index2);
    }
    return index2;
  }
  ensureRecentData(sheet, col, value) {
    const valueIndex = this.getValueIndex(sheet, col, value);
    const actualVersion = this.transformingService.version();
    if (valueIndex.version === actualVersion) {
      return;
    }
    const relevantTransformations = this.transformingService.getTransformationsFrom(valueIndex.version, (transformation) => {
      return transformation.sheet === sheet && (transformation instanceof AddRowsTransformer || transformation instanceof RemoveRowsTransformer);
    });
    for (const transformation of relevantTransformations) {
      if (transformation instanceof AddRowsTransformer) {
        this.addRows(col, transformation.rowsSpan, value);
      } else if (transformation instanceof RemoveRowsTransformer) {
        this.removeRows(col, transformation.rowsSpan, value);
      }
    }
    valueIndex.version = actualVersion;
  }
  addSingleCellValue(value, address) {
    this.stats.measure(StatType.BUILD_COLUMN_INDEX, () => {
      this.ensureRecentData(address.sheet, address.col, value);
      if (typeof value === "string") {
        value = forceNormalizeString(value);
      }
      const valueIndex = this.getValueIndex(address.sheet, address.col, value);
      ColumnIndex.addValue(valueIndex, address.row);
    });
  }
  removeSingleValue(value, address) {
    this.stats.measure(StatType.BUILD_COLUMN_INDEX, () => {
      this.ensureRecentData(address.sheet, address.col, value);
      const columnMap = this.getColumnMap(address.sheet, address.col);
      if (typeof value === "string") {
        value = forceNormalizeString(value);
      }
      const valueIndex = columnMap.get(value);
      if (!valueIndex) {
        return;
      }
      const positionInIndex = findInOrderedArray(address.row, valueIndex.index);
      if (positionInIndex > -1) {
        valueIndex.index.splice(positionInIndex, 1);
      }
      if (valueIndex.index.length === 0) {
        columnMap.delete(value);
      }
      if (columnMap.size === 0) {
        delete this.index.get(address.sheet)[address.col];
      }
    });
  }
  addRows(col, rowsSpan, value) {
    const valueIndex = this.getValueIndex(rowsSpan.sheet, col, value);
    ColumnIndex.shiftRows(valueIndex, rowsSpan.rowStart, rowsSpan.numberOfRows);
  }
  removeRows(col, rowsSpan, value) {
    const valueIndex = this.getValueIndex(rowsSpan.sheet, col, value);
    ColumnIndex.removeRowsFromValues(valueIndex, rowsSpan);
    ColumnIndex.shiftRows(valueIndex, rowsSpan.rowEnd + 1, -rowsSpan.numberOfRows);
  }
  static addValue(valueIndex, rowNumber) {
    const rowIndex = findInOrderedArray(rowNumber, valueIndex.index, "lowerBound");
    const isRowNumberAlreadyInIndex = valueIndex.index[rowIndex] === rowNumber;
    if (!isRowNumberAlreadyInIndex) {
      valueIndex.index.splice(rowIndex + 1, 0, rowNumber);
    }
  }
  static removeRowsFromValues(valueIndex, rowsSpan) {
    const start = findInOrderedArray(rowsSpan.rowStart, valueIndex.index, "upperBound");
    const end = findInOrderedArray(rowsSpan.rowEnd, valueIndex.index, "lowerBound");
    const isFoundSpanValid = start > -1 && end > -1 && start <= end && valueIndex.index[start] <= rowsSpan.rowEnd;
    if (isFoundSpanValid) {
      valueIndex.index.splice(start, end - start + 1);
    }
  }
  static shiftRows(valueIndex, afterRow, numberOfRows) {
    const positionInIndex = findInOrderedArray(afterRow, valueIndex.index, "upperBound");
    if (positionInIndex === -1) {
      return;
    }
    for (let i2 = positionInIndex; i2 < valueIndex.index.length; ++i2) {
      valueIndex.index[i2] += numberOfRows;
    }
  }
}
function findInOrderedArray(key, values2, handlingMisses = "upperBound") {
  let start = 0;
  let end = values2.length - 1;
  while (start <= end) {
    const center = Math.floor((start + end) / 2);
    if (key > values2[center]) {
      start = center + 1;
    } else if (key < values2[center]) {
      end = center - 1;
    } else {
      return center;
    }
  }
  const foundIndex = handlingMisses === "lowerBound" ? end : start;
  const isIndexInRange = foundIndex >= 0 && foundIndex <= values2.length;
  return isIndexInRange ? foundIndex : -1;
}
function buildColumnSearchStrategy(dependencyGraph, config, statistics) {
  if (config.useColumnIndex) {
    return new ColumnIndex(dependencyGraph, config, statistics);
  } else {
    return new ColumnBinarySearch(dependencyGraph);
  }
}
class NumberLiteralHelper {
  constructor(config) {
    this.config = config;
    const thousandSeparator = this.config.thousandSeparator === "." ? `\\${this.config.thousandSeparator}` : this.config.thousandSeparator;
    const decimalSeparator = this.config.decimalSeparator === "." ? `\\${this.config.decimalSeparator}` : this.config.decimalSeparator;
    this.numberPattern = new RegExp(`^([+-]?((${decimalSeparator}\\d+)|(\\d+(${thousandSeparator}\\d{3,})*(${decimalSeparator}\\d*)?)))([eE][+-]?\\d+)?$`);
    this.allThousandSeparatorsRegex = new RegExp(`${thousandSeparator}`, "g");
  }
  numericStringToMaybeNumber(input) {
    if (this.numberPattern.test(input)) {
      const num = this.numericStringToNumber(input);
      if (isNaN(num)) {
        return void 0;
      }
      return num;
    }
    return void 0;
  }
  numericStringToNumber(input) {
    const normalized = input.replace(this.allThousandSeparatorsRegex, "").replace(this.config.decimalSeparator, ".");
    return Number(normalized);
  }
}
class Serialization {
  constructor(dependencyGraph, unparser, exporter) {
    this.dependencyGraph = dependencyGraph;
    this.unparser = unparser;
    this.exporter = exporter;
  }
  getCellHyperlink(address) {
    const formulaVertex = this.dependencyGraph.getCell(address);
    if (formulaVertex instanceof ScalarFormulaVertex) {
      const formula = formulaVertex.getFormula(this.dependencyGraph.lazilyTransformingAstService);
      if ("HYPERLINK" === formula.procedureName) {
        return formula.hyperlink;
      }
    }
    return void 0;
  }
  getCellFormula(address, targetAddress) {
    const formulaVertex = this.dependencyGraph.getCell(address);
    if (formulaVertex instanceof ScalarFormulaVertex) {
      const formula = formulaVertex.getFormula(this.dependencyGraph.lazilyTransformingAstService);
      targetAddress = targetAddress !== null && targetAddress !== void 0 ? targetAddress : address;
      return this.unparser.unparse(formula, targetAddress);
    } else if (formulaVertex instanceof ArrayFormulaVertex) {
      const arrayVertexAddress = formulaVertex.getAddress(this.dependencyGraph.lazilyTransformingAstService);
      if (arrayVertexAddress.row !== address.row || arrayVertexAddress.col !== address.col || arrayVertexAddress.sheet !== address.sheet) {
        return void 0;
      }
      targetAddress = targetAddress !== null && targetAddress !== void 0 ? targetAddress : address;
      const formula = formulaVertex.getFormula(this.dependencyGraph.lazilyTransformingAstService);
      if (formula !== void 0) {
        return this.unparser.unparse(formula, targetAddress);
      }
    } else if (formulaVertex instanceof ParsingErrorVertex) {
      return formulaVertex.getFormula();
    }
    return void 0;
  }
  getCellSerialized(address, targetAddress) {
    var _a2;
    return (_a2 = this.getCellFormula(address, targetAddress)) !== null && _a2 !== void 0 ? _a2 : this.getRawValue(address);
  }
  getCellValue(address) {
    return this.exporter.exportValue(this.dependencyGraph.getScalarValue(address));
  }
  getRawValue(address) {
    return this.dependencyGraph.getRawValue(address);
  }
  getSheetValues(sheet) {
    return this.genericSheetGetter(sheet, (arg) => this.getCellValue(arg));
  }
  getSheetFormulas(sheet) {
    return this.genericSheetGetter(sheet, (arg) => this.getCellFormula(arg));
  }
  genericSheetGetter(sheet, getter) {
    const sheetHeight = this.dependencyGraph.getSheetHeight(sheet);
    const sheetWidth = this.dependencyGraph.getSheetWidth(sheet);
    const arr = new Array(sheetHeight);
    for (let i2 = 0; i2 < sheetHeight; i2++) {
      arr[i2] = new Array(sheetWidth);
      for (let j2 = 0; j2 < sheetWidth; j2++) {
        const address = simpleCellAddress(sheet, j2, i2);
        arr[i2][j2] = getter(address);
      }
      for (let j2 = sheetWidth - 1; j2 >= 0; j2--) {
        if (arr[i2][j2] === null || arr[i2][j2] === void 0) {
          arr[i2].pop();
        } else {
          break;
        }
      }
    }
    for (let i2 = sheetHeight - 1; i2 >= 0; i2--) {
      if (arr[i2].length === 0) {
        arr.pop();
      } else {
        break;
      }
    }
    return arr;
  }
  genericAllSheetsGetter(sheetGetter) {
    const result = {};
    for (const sheetName of this.dependencyGraph.sheetMapping.iterateSheetNames()) {
      const sheetId = this.dependencyGraph.sheetMapping.getSheetIdOrThrowError(sheetName);
      result[sheetName] = sheetGetter(sheetId);
    }
    return result;
  }
  getSheetSerialized(sheet) {
    return this.genericSheetGetter(sheet, (arg) => this.getCellSerialized(arg));
  }
  getAllSheetsValues() {
    return this.genericAllSheetsGetter((arg) => this.getSheetValues(arg));
  }
  getAllSheetsFormulas() {
    return this.genericAllSheetsGetter((arg) => this.getSheetFormulas(arg));
  }
  getAllSheetsSerialized() {
    return this.genericAllSheetsGetter((arg) => this.getSheetSerialized(arg));
  }
  getAllNamedExpressionsSerialized() {
    const idMap = [];
    let id = 0;
    for (const sheetName of this.dependencyGraph.sheetMapping.iterateSheetNames()) {
      const sheetId = this.dependencyGraph.sheetMapping.getSheetIdOrThrowError(sheetName);
      idMap[sheetId] = id;
      id++;
    }
    return this.dependencyGraph.namedExpressions.getAllNamedExpressions().map((entry) => {
      return {
        name: entry.expression.displayName,
        expression: this.getCellSerialized(entry.expression.address),
        scope: entry.scope !== void 0 ? idMap[entry.scope] : void 0,
        options: entry.expression.options
      };
    });
  }
  withNewConfig(newConfig, namedExpressions) {
    const newUnparser = new Unparser(newConfig, this.dependencyGraph.sheetMapping, namedExpressions);
    return new Serialization(this.dependencyGraph, newUnparser, this.exporter);
  }
}
class BuildEngineFactory {
  static buildFromSheets(sheets, configInput = {}, namedExpressions = []) {
    const config = new Config(configInput);
    return this.buildEngine(config, sheets, namedExpressions);
  }
  static buildFromSheet(sheet, configInput = {}, namedExpressions = []) {
    const config = new Config(configInput);
    const newsheetprefix = config.translationPackage.getUITranslation(UIElement.NEW_SHEET_PREFIX) + "1";
    return this.buildEngine(config, {
      [newsheetprefix]: sheet
    }, namedExpressions);
  }
  static buildEmpty(configInput = {}, namedExpressions = []) {
    return this.buildEngine(new Config(configInput), {}, namedExpressions);
  }
  static rebuildWithConfig(config, sheets, namedExpressions, stats) {
    return this.buildEngine(config, sheets, namedExpressions, stats);
  }
  static buildEngine(config, sheets = {}, inputNamedExpressions = [], stats = config.useStats ? new Statistics() : new EmptyStatistics()) {
    stats.start(StatType.BUILD_ENGINE_TOTAL);
    const namedExpressions = new NamedExpressions();
    const functionRegistry = new FunctionRegistry(config);
    const lazilyTransformingAstService = new LazilyTransformingAstService(stats, config.maxPendingLazyTransformations);
    const dependencyGraph = DependencyGraph.buildEmpty(lazilyTransformingAstService, config, functionRegistry, namedExpressions, stats);
    const columnSearch = buildColumnSearchStrategy(dependencyGraph, config, stats);
    const sheetMapping = dependencyGraph.sheetMapping;
    const addressMapping = dependencyGraph.addressMapping;
    for (const sheetName in sheets) {
      if (Object.prototype.hasOwnProperty.call(sheets, sheetName)) {
        const sheet = sheets[sheetName];
        validateAsSheet(sheet);
        const boundaries = findBoundaries(sheet);
        if (boundaries.height > config.maxRows || boundaries.width > config.maxColumns) {
          throw new SheetSizeLimitExceededError();
        }
        const sheetId = sheetMapping.addSheet(sheetName);
        addressMapping.addSheetAndSetStrategyBasedOnBoundaries(sheetId, boundaries, {
          throwIfSheetAlreadyExists: true
        });
      }
    }
    const parser = new ParserWithCaching(config, functionRegistry, dependencyGraph.sheetReferenceRegistrar.ensureSheetRegistered.bind(dependencyGraph.sheetReferenceRegistrar));
    lazilyTransformingAstService.parser = parser;
    const unparser = new Unparser(config, sheetMapping, namedExpressions);
    const dateTimeHelper = new DateTimeHelper(config);
    const numberLiteralHelper = new NumberLiteralHelper(config);
    const arithmeticHelper = new ArithmeticHelper(config, dateTimeHelper, numberLiteralHelper);
    const cellContentParser = new CellContentParser(config, dateTimeHelper, numberLiteralHelper);
    const arraySizePredictor = new ArraySizePredictor(config, functionRegistry);
    const operations = new Operations(config, dependencyGraph, columnSearch, cellContentParser, parser, stats, lazilyTransformingAstService, namedExpressions, arraySizePredictor);
    const undoRedo = new UndoRedo(config, operations);
    lazilyTransformingAstService.undoRedo = undoRedo;
    const clipboardOperations = new ClipboardOperations(config, dependencyGraph, operations);
    const crudOperations = new CrudOperations(config, operations, undoRedo, clipboardOperations, dependencyGraph, columnSearch, parser, cellContentParser, lazilyTransformingAstService, namedExpressions);
    const exporter = new Exporter(config, namedExpressions, sheetMapping, lazilyTransformingAstService);
    const serialization = new Serialization(dependencyGraph, unparser, exporter);
    const interpreter = new Interpreter(config, dependencyGraph, columnSearch, stats, arithmeticHelper, functionRegistry, namedExpressions, serialization, arraySizePredictor, dateTimeHelper);
    stats.measure(StatType.GRAPH_BUILD, () => {
      const graphBuilder = new GraphBuilder(dependencyGraph, columnSearch, parser, cellContentParser, stats, arraySizePredictor);
      graphBuilder.buildGraph(sheets, stats);
    });
    inputNamedExpressions.forEach((entry) => {
      crudOperations.ensureItIsPossibleToAddNamedExpression(entry.name, entry.expression, entry.scope);
      crudOperations.operations.addNamedExpression(entry.name, entry.expression, entry.scope, entry.options);
    });
    const evaluator = new Evaluator(config, stats, interpreter, lazilyTransformingAstService, dependencyGraph, columnSearch);
    evaluator.run();
    stats.end(StatType.BUILD_ENGINE_TOTAL);
    return {
      config,
      stats,
      dependencyGraph,
      columnSearch,
      parser,
      unparser,
      cellContentParser,
      evaluator,
      lazilyTransformingAstService,
      crudOperations,
      exporter,
      namedExpressions,
      serialization,
      functionRegistry
    };
  }
}
function objectDestroy(object) {
  for (const [key, value] of Object.entries(object)) {
    if (value instanceof Function) {
      object[key] = postMortem(value);
    } else {
      delete object[key];
    }
  }
}
function postMortem(method) {
  return () => {
    throw new Error(`The "${method}" method cannot be called because this HyperFormula instance has been destroyed`);
  };
}
var tinyEmitter = { exports: {} };
var hasRequiredTinyEmitter;
function requireTinyEmitter() {
  if (hasRequiredTinyEmitter) return tinyEmitter.exports;
  hasRequiredTinyEmitter = 1;
  function E2() {
  }
  E2.prototype = {
    on: function(name, callback, ctx) {
      var e3 = this.e || (this.e = {});
      (e3[name] || (e3[name] = [])).push({
        fn: callback,
        ctx
      });
      return this;
    },
    once: function(name, callback, ctx) {
      var self2 = this;
      function listener() {
        self2.off(name, listener);
        callback.apply(ctx, arguments);
      }
      listener._ = callback;
      return this.on(name, listener, ctx);
    },
    emit: function(name) {
      var data = [].slice.call(arguments, 1);
      var evtArr = ((this.e || (this.e = {}))[name] || []).slice();
      var i2 = 0;
      var len = evtArr.length;
      for (i2; i2 < len; i2++) {
        evtArr[i2].fn.apply(evtArr[i2].ctx, data);
      }
      return this;
    },
    off: function(name, callback) {
      var e3 = this.e || (this.e = {});
      var evts = e3[name];
      var liveEvents = [];
      if (evts && callback) {
        for (var i2 = 0, len = evts.length; i2 < len; i2++) {
          if (evts[i2].fn !== callback && evts[i2].fn._ !== callback)
            liveEvents.push(evts[i2]);
        }
      }
      liveEvents.length ? e3[name] = liveEvents : delete e3[name];
      return this;
    }
  };
  tinyEmitter.exports = E2;
  tinyEmitter.exports.TinyEmitter = E2;
  return tinyEmitter.exports;
}
var tinyEmitterExports = requireTinyEmitter();
var Events;
(function(Events2) {
  Events2["SheetAdded"] = "sheetAdded";
  Events2["SheetRemoved"] = "sheetRemoved";
  Events2["SheetRenamed"] = "sheetRenamed";
  Events2["NamedExpressionAdded"] = "namedExpressionAdded";
  Events2["NamedExpressionRemoved"] = "namedExpressionRemoved";
  Events2["ValuesUpdated"] = "valuesUpdated";
  Events2["EvaluationSuspended"] = "evaluationSuspended";
  Events2["EvaluationResumed"] = "evaluationResumed";
})(Events || (Events = {}));
class Emitter extends tinyEmitterExports.TinyEmitter {
  emit(event, ...args) {
    super.emit(event, ...args);
    return this;
  }
}
class HyperFormula {
  /**
   * Constructor
   *
   * @internal
   */
  constructor(_config, _stats, _dependencyGraph, _columnSearch, _parser, _unparser, _cellContentParser, _evaluator, _lazilyTransformingAstService, _crudOperations, _exporter, _namedExpressions, _serialization, _functionRegistry) {
    this._config = _config;
    this._stats = _stats;
    this._dependencyGraph = _dependencyGraph;
    this._columnSearch = _columnSearch;
    this._parser = _parser;
    this._unparser = _unparser;
    this._cellContentParser = _cellContentParser;
    this._evaluator = _evaluator;
    this._lazilyTransformingAstService = _lazilyTransformingAstService;
    this._crudOperations = _crudOperations;
    this._exporter = _exporter;
    this._namedExpressions = _namedExpressions;
    this._serialization = _serialization;
    this._functionRegistry = _functionRegistry;
    this._emitter = new Emitter();
    this._evaluationSuspended = false;
  }
  /**
   * Returns all of HyperFormula's default [configuration options](/guide/configuration-options.md).
   *
   * @example
   * ```js
   * // returns all default configuration options
   * const defaultConfig = HyperFormula.defaultConfig;
   * ```
   *
   * @category Static Accessors
   */
  static get defaultConfig() {
    return getDefaultConfig();
  }
  /**
   * Calls the `graph` method on the dependency graph.
   * Allows for executing `graph` directly, without a need to refer to `dependencyGraph`.
   *
   * @internal
   */
  get graph() {
    return this.dependencyGraph.graph;
  }
  /**
   * Calls the `rangeMapping` method on the dependency graph.
   * Allows for executing `rangeMapping` directly, without a need to refer to `dependencyGraph`.
   *
   * @internal
   */
  get rangeMapping() {
    return this.dependencyGraph.rangeMapping;
  }
  /**
   * Calls the `arrayMapping` method on the dependency graph.
   * Allows for executing `arrayMapping` directly, without a need to refer to `dependencyGraph`.
   *
   * @internal
   */
  get arrayMapping() {
    return this.dependencyGraph.arrayMapping;
  }
  /**
   * Calls the `sheetMapping` method on the dependency graph.
   * Allows for executing `sheetMapping` directly, without a need to refer to `dependencyGraph`.
   *
   * @internal
   */
  get sheetMapping() {
    return this.dependencyGraph.sheetMapping;
  }
  /**
   * Calls the `addressMapping` method on the dependency graph.
   * Allows for executing `addressMapping` directly, without a need to refer to `dependencyGraph`.
   *
   * @internal
   */
  get addressMapping() {
    return this.dependencyGraph.addressMapping;
  }
  /** @internal */
  get dependencyGraph() {
    return this._dependencyGraph;
  }
  /** @internal */
  get evaluator() {
    return this._evaluator;
  }
  /** @internal */
  get columnSearch() {
    return this._columnSearch;
  }
  /** @internal */
  get lazilyTransformingAstService() {
    return this._lazilyTransformingAstService;
  }
  /**
   * Returns state of the validity of the license key.
   *
   * @internal
   */
  get licenseKeyValidityState() {
    return this._config.licenseKeyValidityState;
  }
  /**
   * Builds the engine for a sheet from a two-dimensional array representation.
   * The engine is created with a single sheet.
   * Can be configured with the optional second parameter that represents a [[ConfigParams]].
   * If not specified, the engine will be built with the default configuration.
   *
   * @param {Sheet} sheet - two-dimensional array representation of sheet
   * @param {Partial<ConfigParams>} configInput - engine configuration
   * @param {SerializedNamedExpression[]} namedExpressions - starting named expressions
   *
   * @throws [[SheetSizeLimitExceededError]] when sheet size exceeds the limits
   * @throws [[InvalidArgumentsError]] when sheet is not an array of arrays
   * @throws [[FunctionPluginValidationError]] when plugin class definition is not consistent with metadata
   *
   * @example
   * ```js
   * // data represented as an array
   * const sheetData = [
   *  ['0', '=SUM(1, 2, 3)', '52'],
   *  ['=SUM(A1:C1)', '', '=A1'],
   *  ['2', '=SUM(A1:C1)', '=theUltimateQuestionOfLife'],
   * ];
   *
   * const namedExpressions = [
   *  {
   *    name: 'theUltimateQuestionOfLife',
   *    expression: '=42',
   *  },
   * ];
   *
   * // method with optional config parameter maxColumns
   * const hfInstance = HyperFormula.buildFromArray(sheetData, { maxColumns: 1000 }, namedExpressions);
   * ```
   *
   * @category Factories
   */
  static buildFromArray(sheet, configInput = {}, namedExpressions = []) {
    return this.buildFromEngineState(BuildEngineFactory.buildFromSheet(sheet, configInput, namedExpressions));
  }
  /**
   * Builds the engine from an object containing multiple sheets with names.
   * The engine is created with one or more sheets.
   * Can be configured with the optional second parameter that represents a [[ConfigParams]].
   * If not specified the engine will be built with the default configuration.
   *
   * @param {Sheet} sheets - object with sheets definition
   * @param {Partial<ConfigParams>} configInput - engine configuration
   * @param {SerializedNamedExpression[]} namedExpressions - starting named expressions
   *
   * @throws [[SheetSizeLimitExceededError]] when sheet size exceeds the limits
   * @throws [[InvalidArgumentsError]] when any sheet is not an array of arrays
   * @throws [[FunctionPluginValidationError]] when plugin class definition is not consistent with metadata
   *
   * @example
   * ```js
   * // data represented as an object with sheets: Sheet1 and Sheet2
   * const sheetData = {
   *  'Sheet1': [
   *    ['1', '', '=Sheet2!$A1'],
   *    ['', '2', '=SUM(1, 2, 3)'],
   *    ['=Sheet2!$A2', '2', ''],
   *   ],
   *  'Sheet2': [
   *    ['', '4', '=Sheet1!$B1'],
   *    ['', '8', '=SUM(9, 3, 3)'],
   *    ['=Sheet1!$B1', '2', '=theUltimateQuestionOfLife'],
   *   ],
   * };
   *
   * const namedExpressions = [
   *  {
   *    name: 'theUltimateQuestionOfLife',
   *    expression: '=42',
   *  },
   * ];
   *
   * // method with optional config parameter useColumnIndex
   * const hfInstance = HyperFormula.buildFromSheets(sheetData, { useColumnIndex: true }, namedExpressions);
   * ```
   *
   * @category Factories
   */
  static buildFromSheets(sheets, configInput = {}, namedExpressions = []) {
    return this.buildFromEngineState(BuildEngineFactory.buildFromSheets(sheets, configInput, namedExpressions));
  }
  /**
   * Builds an empty engine instance.
   * Can be configured with the optional parameter that represents a [[ConfigParams]].
   * If not specified the engine will be built with the default configuration.
   *
   * @param {Partial<ConfigParams>} configInput - engine configuration
   * @param {SerializedNamedExpression[]} namedExpressions - starting named expressions
   *
   * @example
   * ```js
   * const namedExpressions = [
   *  {
   *    name: 'theUltimateQuestionOfLife',
   *    expression: '=42',
   *  },
   * ];
   *
   * // build with no initial data and with optional config parameter maxColumns
   * const hfInstance = HyperFormula.buildEmpty({ maxColumns: 1000 }, namedExpressions);
   * ```
   *
   * @category Factories
   */
  static buildEmpty(configInput = {}, namedExpressions = []) {
    return this.buildFromEngineState(BuildEngineFactory.buildEmpty(configInput, namedExpressions));
  }
  /**
   * Returns registered language from its code string.
   *
   * For more information, see the [Localizing functions guide](/guide/localizing-functions.md).
   *
   * @param {string} languageCode - code string of the translation package
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[LanguageNotRegisteredError]] when trying to retrieve not registered language
   *
   * @example
   * ```js
   * // return registered language
   * const language = HyperFormula.getLanguage('enGB');
   * ```
   *
   * @category Static Methods
   */
  static getLanguage(languageCode) {
    validateArgToType(languageCode, "string", "languageCode");
    const val = this.registeredLanguages.get(languageCode);
    if (val === void 0) {
      throw new LanguageNotRegisteredError();
    } else {
      return val;
    }
  }
  /**
   * Registers language under given code string.
   *
   * For more information, see the [Localizing functions guide](/guide/localizing-functions.md).
   *
   * @param {string} languageCode - code string of the translation package
   * @param {RawTranslationPackage} languagePackage - translation package to be registered
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[ProtectedFunctionTranslationError]] when trying to register translation for protected function
   * @throws [[LanguageAlreadyRegisteredError]] when given language is already registered
   *
   * @example
   * ```js
   * // return registered language
   * HyperFormula.registerLanguage('enUS', enUS);
   * const engine = HyperFormula.buildEmpty({language: 'enUS'});
   * ```
   *
   * @category Static Methods
   */
  static registerLanguage(languageCode, languagePackage) {
    validateArgToType(languageCode, "string", "languageCode");
    if (this.registeredLanguages.has(languageCode)) {
      throw new LanguageAlreadyRegisteredError();
    } else {
      this.registeredLanguages.set(languageCode, buildTranslationPackage(languagePackage));
    }
  }
  /**
   * Unregisters language that is registered under given code string.
   *
   * For more information, see the [Localizing functions guide](/guide/localizing-functions.md).
   *
   * @param {string} languageCode - code string of the translation package
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[LanguageNotRegisteredError]] when given language is not registered
   *
   * @example
   * ```js
   * // register the language for the instance
   * HyperFormula.registerLanguage('plPL', plPL);
   *
   * // unregister plPL
   * HyperFormula.unregisterLanguage('plPL');
   * ```
   *
   * @category Static Methods
   */
  static unregisterLanguage(languageCode) {
    validateArgToType(languageCode, "string", "languageCode");
    if (this.registeredLanguages.has(languageCode)) {
      this.registeredLanguages.delete(languageCode);
    } else {
      throw new LanguageNotRegisteredError();
    }
  }
  /**
   * Returns all registered languages codes.
   *
   * @example
   * ```js
   * // should return all registered language codes: ['enGB', 'plPL']
   * const registeredLanguages = HyperFormula.getRegisteredLanguagesCodes();
   * ```
   *
   * @category Static Methods
   */
  static getRegisteredLanguagesCodes() {
    return Array.from(this.registeredLanguages.keys());
  }
  /**
   * Registers all functions in a given plugin with optional translations.
   *
   * For more information, see the [Custom functions guide](/guide/custom-functions.md).
   *
   * Note: FunctionPlugins must be registered prior to the creation of HyperFormula instances in which they are used.
   * HyperFormula instances created prior to the registration of a FunctionPlugin are unable to access the FunctionPlugin.
   * Registering a FunctionPlugin with [[custom-functions]] requires the translations parameter.
   *
   * @param {FunctionPluginDefinition} plugin - plugin class
   * @param {FunctionTranslationsPackage} translations - optional package of function names translations
   *
   * @throws [[FunctionPluginValidationError]] when plugin class definition is not consistent with metadata
   * @throws [[ProtectedFunctionTranslationError]] when trying to register translation for protected function
   *
   * @example
   * ```js
   * // import your own plugin
   * import { MyExamplePlugin } from './file_with_your_plugin';
   *
   * // register the plugin
   * HyperFormula.registerFunctionPlugin(MyExamplePlugin);
   * ```
   *
   * @category Static Methods
   */
  static registerFunctionPlugin(plugin, translations) {
    FunctionRegistry.registerFunctionPlugin(plugin, translations);
  }
  /**
   * Unregisters all functions defined in given plugin.
   *
   * For more information, see the [Custom functions guide](/guide/custom-functions.md).
   *
   * Note: This method does not affect the existing HyperFormula instances.
   *
   * @param {FunctionPluginDefinition} plugin - plugin class
   *
   * @example
   * ```js
   * // get the class of a plugin
   * const registeredPluginClass = HyperFormula.getFunctionPlugin('EXAMPLE');
   *
   * // unregister all functions defined in a plugin of ID 'EXAMPLE'
   * HyperFormula.unregisterFunctionPlugin(registeredPluginClass);
   * ```
   *
   * @category Static Methods
   */
  static unregisterFunctionPlugin(plugin) {
    FunctionRegistry.unregisterFunctionPlugin(plugin);
  }
  /**
   * Registers a function with a given id if such exists in a plugin.
   *
   * For more information, see the [Custom functions guide](/guide/custom-functions.md).
   *
   * Note: This method does not affect the existing HyperFormula instances.
   *
   * @param {string} functionId - function id, e.g., 'SUMIF'
   * @param {FunctionPluginDefinition} plugin - plugin class
   * @param {FunctionTranslationsPackage} translations - translations for the function name
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[FunctionPluginValidationError]] when function with a given id does not exist in plugin or plugin class definition is not consistent with metadata
   * @throws [[ProtectedFunctionTranslationError]] when trying to register translation for protected function
   *
   * @example
   * ```js
   * // import your own plugin
   * import { MyExamplePlugin } from './file_with_your_plugin';
   *
   * // register a function
   * HyperFormula.registerFunction('EXAMPLE', MyExamplePlugin);
   * ```
   *
   * @category Static Methods
   */
  static registerFunction(functionId, plugin, translations) {
    validateArgToType(functionId, "string", "functionId");
    FunctionRegistry.registerFunction(functionId, plugin, translations);
  }
  /**
   * Unregisters a function with a given id.
   *
   * For more information, see the [Custom functions guide](/guide/custom-functions.md).
   *
   * Note: This method does not affect the existing HyperFormula instances.
   *
   * @param {string} functionId - function id, e.g., 'SUMIF'
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * // import your own plugin
   * import { MyExamplePlugin } from './file_with_your_plugin';
   *
   * // register a function
   * HyperFormula.registerFunction('EXAMPLE', MyExamplePlugin);
   *
   * // unregister a function
   * HyperFormula.unregisterFunction('EXAMPLE');
   * ```
   *
   * @category Static Methods
   */
  static unregisterFunction(functionId) {
    validateArgToType(functionId, "string", "functionId");
    FunctionRegistry.unregisterFunction(functionId);
  }
  /**
   * Clears function registry.
   *
   * Note: This method does not affect the existing HyperFormula instances.
   *
   * @example
   * ```js
   * HyperFormula.unregisterAllFunctions();
   * ```
   *
   * @category Static Methods
   */
  static unregisterAllFunctions() {
    FunctionRegistry.unregisterAll();
  }
  /**
   * Returns translated names of all registered functions for a given language
   *
   * @param {string} code - language code
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * // return a list of function names registered for enGB
   * const allNames = HyperFormula.getRegisteredFunctionNames('enGB');
   * ```
   *
   * @category Static Methods
   */
  static getRegisteredFunctionNames(code) {
    validateArgToType(code, "string", "code");
    const functionIds = FunctionRegistry.getRegisteredFunctionIds();
    const language = this.getLanguage(code);
    return language.getFunctionTranslations(functionIds);
  }
  /**
   * Returns class of a plugin used by function with given id
   *
   * For more information, see the [Custom functions guide](/guide/custom-functions.md).
   *
   * @param {string} functionId - id of a function, e.g., 'SUMIF'
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * // import your own plugin
   * import { MyExamplePlugin } from './file_with_your_plugin';
   *
   * // register a plugin
   * HyperFormula.registerFunctionPlugin(MyExamplePlugin);
   *
   * // return the class of a given plugin
   * const myFunctionClass = HyperFormula.getFunctionPlugin('EXAMPLE');
   * ```
   *
   * @category Static Methods
   */
  static getFunctionPlugin(functionId) {
    validateArgToType(functionId, "string", "functionId");
    return FunctionRegistry.getFunctionPlugin(functionId);
  }
  /**
   * Returns classes of all plugins registered in HyperFormula.
   *
   * @example
   * ```js
   * // return classes of all plugins
   * const allClasses = HyperFormula.getAllFunctionPlugins();
   * ```
   *
   * @category Static Methods
   */
  static getAllFunctionPlugins() {
    return FunctionRegistry.getPlugins();
  }
  /**
   * @internal
   */
  static buildFromEngineState(engine) {
    return new HyperFormula(engine.config, engine.stats, engine.dependencyGraph, engine.columnSearch, engine.parser, engine.unparser, engine.cellContentParser, engine.evaluator, engine.lazilyTransformingAstService, engine.crudOperations, engine.exporter, engine.namedExpressions, engine.serialization, engine.functionRegistry);
  }
  /**
   * Returns the cell value of a given address.
   * Applies rounding and post-processing.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[ExpectedValueOfTypeError]] when cellAddress is of incorrect type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(1, 2, 3)', '2'],
   * ]);
   *
   * // get value of A1 cell, should be '6'
   * const A1Value = hfInstance.getCellValue({ sheet: 0, col: 0, row: 0 });
   *
   * // get value of B1 cell, should be '2'
   * const B1Value = hfInstance.getCellValue({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellValue(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    this.ensureEvaluationIsNotSuspended();
    return this._serialization.getCellValue(cellAddress);
  }
  /**
   * Returns a normalized formula string from the cell of a given address or `undefined` for an address that does not exist and empty values.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] when cellAddress is of incorrect type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(1, 2, 3)', '0'],
   * ]);
   *
   * // should return a normalized A1 cell formula: '=SUM(1, 2, 3)'
   * const A1Formula = hfInstance.getCellFormula({ sheet: 0, col: 0, row: 0 });
   *
   * // should return a normalized B1 cell formula: 'undefined'
   * const B1Formula = hfInstance.getCellFormula({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellFormula(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    return this._serialization.getCellFormula(cellAddress);
  }
  /**
   * Returns the `HYPERLINK` url for a cell of a given address or `undefined` for an address that does not exist or a cell that is not `HYPERLINK`
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] when cellAddress is of incorrect type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=HYPERLINK("https://hyperformula.handsontable.com/", "HyperFormula")', '0'],
   * ]);
   *
   * // should return url of 'HYPERLINK': https://hyperformula.handsontable.com/
   * const A1Hyperlink = hfInstance.getCellHyperlink({ sheet: 0, col: 0, row: 0 });
   *
   * // should return 'undefined' for a cell that is not 'HYPERLINK'
   * const B1Hyperlink = hfInstance.getCellHyperlink({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellHyperlink(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    this.ensureEvaluationIsNotSuspended();
    return this._serialization.getCellHyperlink(cellAddress);
  }
  /**
   * Returns [[RawCellContent]] with a serialized content of the cell of a given address: either a cell formula, an explicit value, or an error.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   * @throws [[ExpectedValueOfTypeError]] when cellAddress is of incorrect type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(1, 2, 3)', '0'],
   * ]);
   *
   * // should return serialized content of A1 cell: '=SUM(1, 2, 3)'
   * const cellA1Serialized = hfInstance.getCellSerialized({ sheet: 0, col: 0, row: 0 });
   *
   * // should return serialized content of B1 cell: '0'
   * const cellB1Serialized = hfInstance.getCellSerialized({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellSerialized(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    this.ensureEvaluationIsNotSuspended();
    return this._serialization.getCellSerialized(cellAddress);
  }
  /**
   * Returns an array of arrays of [[CellValue]] with values of all cells from [[Sheet]].
   * Applies rounding and post-processing.
   *
   * @param {number} sheetId - sheet ID number
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['0', '=SUM(1, 2, 3)', '=A1'],
   *  ['1', '=TEXT(A2, "0.0%")', '=C1'],
   *  ['2', '=SUM(A1:C1)', '=C1'],
   * ]);
   *
   * // should return all values of a sheet: [[0, 6, 0], [1, '1.0%', 0], [2, 6, 0]]
   * const sheetValues = hfInstance.getSheetValues(0);
   * ```
   *
   * @category Sheets
   */
  getSheetValues(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    this.ensureEvaluationIsNotSuspended();
    return this._serialization.getSheetValues(sheetId);
  }
  /**
   * Returns an array with normalized formula strings from [[Sheet]] or `undefined` for a cells that have no value.
   *
   * @param {SimpleCellAddress} sheetId - sheet ID number
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['0', '=SUM(1, 2, 3)', '=A1'],
   *  ['1', '=TEXT(A2, "0.0%")', '=C1'],
   *  ['2', '=SUM(A1:C1)', '=C1'],
   * ]);
   *
   * // should return all formulas of a sheet:
   * // [
   * //  [undefined, '=SUM(1, 2, 3)', '=A1'],
   * //  [undefined, '=TEXT(A2, "0.0%")', '=C1'],
   * //  [undefined, '=SUM(A1:C1)', '=C1'],
   * // ];
   * const sheetFormulas = hfInstance.getSheetFormulas(0);
   * ```
   *
   * @category Sheets
   */
  getSheetFormulas(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    return this._serialization.getSheetFormulas(sheetId);
  }
  /**
   * Returns an array of arrays of [[RawCellContent]] with serialized content of cells from [[Sheet]], either a cell formula or an explicit value.
   *
   * @param {SimpleCellAddress} sheetId - sheet ID number
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['0', '=SUM(1, 2, 3)', '=A1'],
   *  ['1', '=TEXT(A2, "0.0%")', '=C1'],
   *  ['2', '=SUM(A1:C1)', '=C1'],
   * ]);
   *
   * // should return:
   * // [
   * //  ['0', '=SUM(1, 2, 3)', '=A1'],
   * //  ['1', '=TEXT(A2, "0.0%")', '=C1'],
   * //  ['2', '=SUM(A1:C1)', '=C1'],
   * // ];
   * const serializedContent = hfInstance.getSheetSerialized(0);
   * ```
   *
   * @category Sheets
   */
  getSheetSerialized(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    this.ensureEvaluationIsNotSuspended();
    return this._serialization.getSheetSerialized(sheetId);
  }
  /**
   * Returns a map containing dimensions of all sheets for the engine instance represented as a key-value pairs where keys are sheet IDs and dimensions are returned as numbers, width and height respectively.
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *   Sheet1: [
   *    ['1', '2', '=Sheet2!$A1'],
   *   ],
   *   Sheet2: [
   *    ['3'],
   *    ['4'],
   *   ],
   * });
   *
   * // should return the dimensions of all sheets:
   * // { Sheet1: { width: 3, height: 1 }, Sheet2: { width: 1, height: 2 } }
   * const allSheetsDimensions = hfInstance.getAllSheetsDimensions();
   * ```
   *
   * @category Sheets
   */
  getAllSheetsDimensions() {
    return this._serialization.genericAllSheetsGetter((arg) => this.getSheetDimensions(arg));
  }
  /**
   * Returns dimensions of a specified sheet.
   * The sheet dimensions is represented with numbers: width and height.
   *
   * Note: Due to the memory optimizations, some of the empty bottom rows and rightmost columns are not counted to the dimensions.
   *
   * @param {number} sheetId - sheet ID number
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *    ['1', '2', '=Sheet2!$A1'],
   * ]);
   *
   * // should return provided sheet's dimensions: { width: 3, height: 1 }
   * const sheetDimensions = hfInstance.getSheetDimensions(0);
   * ```
   *
   * @category Sheets
   */
  getSheetDimensions(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    return {
      width: this.dependencyGraph.getSheetWidth(sheetId),
      height: this.dependencyGraph.getSheetHeight(sheetId)
    };
  }
  /**
   * Returns values of all sheets in a form of an object which property keys are strings and values are 2D arrays of [[CellValue]].
   *
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '=A1+10', '3'],
   * ]);
   *
   * // should return all sheets values: { Sheet1: [ [ 1, 11, 3 ] ] }
   * const allSheetsValues = hfInstance.getAllSheetsValues();
   * ```
   *
   * @category Sheets
   */
  getAllSheetsValues() {
    this.ensureEvaluationIsNotSuspended();
    return this._serialization.getAllSheetsValues();
  }
  /**
   * Returns formulas of all sheets in a form of an object which property keys are strings and values are 2D arrays of strings or possibly `undefined` when the call does not contain a formula.
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2', '=A1+10'],
   * ]);
   *
   * // should return only formulas: { Sheet1: [ [ undefined, undefined, '=A1+10' ] ] }
   * const allSheetsFormulas = hfInstance.getAllSheetsFormulas();
   * ```
   * @category Sheets
   */
  getAllSheetsFormulas() {
    return this._serialization.getAllSheetsFormulas();
  }
  /**
   * Returns formulas or values of all sheets in a form of an object which property keys are strings and values are 2D arrays of [[RawCellContent]].
   *
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2', '=A1+10'],
   * ]);
   *
   * // should return all sheets serialized content: { Sheet1: [ [ 1, 2, '=A1+10' ] ] }
   * const allSheetsSerialized = hfInstance.getAllSheetsSerialized();
   * ```
   *
   * @category Sheets
   */
  getAllSheetsSerialized() {
    this.ensureEvaluationIsNotSuspended();
    return this._serialization.getAllSheetsSerialized();
  }
  /**
   * Updates the config with given new metadata. It is an expensive operation, as it might trigger rebuilding the engine and recalculation of all formulas.
   *
   * For more information, see the [Configuration options guide](/guide/configuration-options.md).
   *
   * @param {Partial<ConfigParams>} newParams configuration options to be updated or added
   *
   * @throws [[ExpectedValueOfTypeError]] when some parameters of config are of wrong type (e.g., currencySymbol)
   * @throws [[ConfigValueEmpty]] when some parameters of config are of invalid value (e.g., currencySymbol)
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // add a config param, for example maxColumns,
   * // you can check the configuration with getConfig method
   * hfInstance.updateConfig({ maxColumns: 1000 });
   * ```
   *
   * @category Instance
   */
  updateConfig(newParams) {
    const isNewConfigTheSame = Object.entries(newParams).every(([key, value]) => this._config[key] === value);
    if (isNewConfigTheSame) {
      return;
    }
    this.rebuildWithConfig(newParams);
  }
  /**
   * Returns current configuration of the engine instance.
   *
   * For more information, see the [Configuration options guide](/guide/configuration-options.md).
   *
   * @example
   * ```js
   * // should return all config metadata including default and those which were added
   * const hfConfig = hfInstance.getConfig();
   * ```
   *
   * @category Instance
   */
  getConfig() {
    return this._config.getConfig();
  }
  /**
   * Rebuilds the HyperFormula instance preserving the current sheets data.
   *
   * @example
   * ```js
   * hfInstance.rebuildAndRecalculate();
   * ```
   *
   * @category Instance
   */
  rebuildAndRecalculate() {
    this.rebuildWithConfig({});
  }
  /**
   * Returns a snapshot of computation time statistics.
   * It returns a map with key-value pairs where keys are enums for stat type and time (number).
   *
   * @internal
   *
   * @category Instance
   */
  getStats() {
    return this._stats.snapshot();
  }
  /**
   * Undo the previous operation.
   *
   * For more information, see the [Undo-Redo guide](/guide/undo-redo.md).
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[NoOperationToUndoError]] when there is no operation running that can be undone
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   *  ['3', ''],
   * ]);
   *
   * // perform CRUD operation, for example remove the second row
   * hfInstance.removeRows(0, [1, 1]);
   *
   * // undo the operation, it should return the changes
   * const changes = hfInstance.undo();
   * ```
   *
   * @category Undo and Redo
   */
  undo() {
    this._crudOperations.undo();
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Re-do recently undone operation.
   *
   * For more information, see the [Undo-Redo guide](/guide/undo-redo.md).
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[NoOperationToRedoError]] when there is no operation running that can be re-done
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1'],
   *  ['2'],
   *  ['3'],
   * ]);
   *
   * // perform CRUD operation, for example remove the second row
   * hfInstance.removeRows(0, [1, 1]);
   *
   * // undo the operation, it should return previous values: [['1'], ['2'], ['3']]
   * hfInstance.undo();
   *
   * // do a redo, it should return the values after removing the second row: [['1'], ['3']]
   * const changes = hfInstance.redo();
   * ```
   *
   * @category Undo and Redo
   */
  redo() {
    this._crudOperations.redo();
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Checks if there is at least one operation that can be undone.
   *
   * For more information, see the [Undo-Redo guide](/guide/undo-redo.md).
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1'],
   *  ['2'],
   *  ['3'],
   * ]);
   *
   * // perform CRUD operation, for example remove the second row
   * hfInstance.removeRows(0, [1, 1]);
   *
   * // should return 'true', it is possible to undo last operation
   * // which is removing rows in this example
   * const isSomethingToUndo = hfInstance.isThereSomethingToUndo();
   * ```
   *
   * @category Undo and Redo
   */
  isThereSomethingToUndo() {
    return this._crudOperations.isThereSomethingToUndo();
  }
  /**
   * Checks if there is at least one operation that can be re-done.
   *
   * For more information, see the [Undo-Redo guide](/guide/undo-redo.md).
   *
   * @example
   * ```js
   * hfInstance.undo();
   *
   * // when there is an action to redo, this returns 'true'
   * const isSomethingToRedo = hfInstance.isThereSomethingToRedo();
   * ```
   *
   * @category Undo and Redo
   */
  isThereSomethingToRedo() {
    return this._crudOperations.isThereSomethingToRedo();
  }
  /**
   * Returns information whether it is possible to change the content in a rectangular area bounded by the box.
   * If returns `true`, doing [[setCellContents]] operation won't throw any errors.
   * Returns `false` if the address is invalid or the sheet does not exist.
   *
   * @param {SimpleCellAddress | SimpleCellRange} address - single cell or block of cells to check
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // top left corner
   * const address1 = { col: 0, row: 0, sheet: 0 };
   * // bottom right corner
   * const address2 = { col: 1, row: 0, sheet: 0 };
   *
   * // should return 'true' for this example, it is possible to set content of
   * // width 2, height 1 in the first row and column of sheet 0
   * const isSettable = hfInstance.isItPossibleToSetCellContents({ start: address1, end: address2 });
   * ```
   *
   * @category Cells
   */
  isItPossibleToSetCellContents(address) {
    let range;
    if (isSimpleCellAddress(address)) {
      range = new AbsoluteCellRange(address, address);
    } else if (isSimpleCellRange(address)) {
      range = new AbsoluteCellRange(address.start, address.end);
    } else {
      throw new ExpectedValueOfTypeError("SimpleCellAddress | SimpleCellRange", "address");
    }
    try {
      this._crudOperations.ensureRangeInSizeLimits(range);
      for (const it2 of range.addresses(this._dependencyGraph)) {
        this._crudOperations.ensureItIsPossibleToChangeContent(it2);
      }
    } catch (e3) {
      return false;
    }
    return true;
  }
  /**
   * Sets the content for a block of cells of a given coordinates.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {SimpleCellAddress} topLeftCornerAddress - top left corner of block of cells
   * @param {(RawCellContent[][]|RawCellContent)} cellContents - array with content
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when the value is not an array of arrays or a raw cell value
   * @throws [[SheetSizeLimitExceededError]] when performing this operation would result in sheet size limits exceeding
   * @throws [[ExpectedValueOfTypeError]] if topLeftCornerAddress argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2', '=A1'],
   * ]);
   *
   * // should set the content, returns:
   * // [{
   * //   address: { sheet: 0, col: 3, row: 0 },
   * //   newValue: 2,
   * // }]
   * const changes = hfInstance.setCellContents({ col: 3, row: 0, sheet: 0 }, [['=B1']]);
   * ```
   *
   * @category Cells
   */
  setCellContents(topLeftCornerAddress, cellContents) {
    this._crudOperations.setCellContents(topLeftCornerAddress, cellContents);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Reorders rows of a sheet according to a source-target mapping.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - ID of a sheet to operate on
   * @param {[number, number][]} rowMapping - array mapping original positions to final positions of rows
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when rowMapping does not define correct row permutation for some subset of rows of the given sheet
   * @throws [[SourceLocationHasArrayError]] when the selected position has array inside
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  [1],
   *  [2],
   *  [4, 5],
   * ]);
   *
   * // should set swap rows 0 and 2 in place, returns:
   * // [{
   * //   address: { sheet: 0, col: 0, row: 2 },
   * //   newValue: 1,
   * // },
   * // {
   * //   address: { sheet: 0, col: 1, row: 2 },
   * //   newValue: null,
   * // },
   * // {
   * //   address: { sheet: 0, col: 0, row: 0 },
   * //   newValue: 4,
   * // },
   * // {
   * //   address: { sheet: 0, col: 1, row: 0 },
   * //   newValue: 5,
   * // }]
   * const changes = hfInstance.swapRowIndexes(0, [[0, 2], [2, 0]]);
   * ```
   *
   * @category Rows
   */
  swapRowIndexes(sheetId, rowMapping) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.setRowOrder(sheetId, rowMapping);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Checks if it is possible to reorder rows of a sheet according to a source-target mapping.
   *
   * @param {number} sheetId - ID of a sheet to operate on
   * @param {[number, number][]} rowMapping - array mapping original positions to final positions of rows
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  [1],
   *  [2],
   *  [4, 5],
   * ]);
   *
   * // returns true
   * const isSwappable = hfInstance.isItPossibleToSwapRowIndexes(0, [[0, 2], [2, 0]]);
   *
   * // returns false
   * const isSwappable = hfInstance.isItPossibleToSwapRowIndexes(0, [[0, 1]]);
   * ```
   *
   * @category Rows
   */
  isItPossibleToSwapRowIndexes(sheetId, rowMapping) {
    validateArgToType(sheetId, "number", "sheetId");
    try {
      this._crudOperations.validateSwapRowIndexes(sheetId, rowMapping);
      this._crudOperations.testRowOrderForArrays(sheetId, rowMapping);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Reorders rows of a sheet according to a permutation of 0-based indexes.
   * Parameter `newRowOrder` should have a form `[ newPositionForRow0, newPositionForRow1, newPositionForRow2, ... ]`.
   * This method might be used to [sort the rows of a sheet](../../guide/sorting-data.md).
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note: This method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - ID of a sheet to operate on
   * @param {number[]} newRowOrder - permutation of rows; array length must match the number of rows returned by [getSheetDimensions()](#getsheetdimensions)
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when rowMapping does not define correct row permutation for some subset of rows of the given sheet
   * @throws [[SourceLocationHasArrayError]] when the selected position has array inside
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['A'],
   *  ['B'],
   *  ['C'],
   *  ['D']
   * ]);
   *
   * const newRowOrder = [0, 3, 2, 1]; // [ newPosForA, newPosForB, newPosForC, newPosForD ]
   *
   * const changes = hfInstance.setRowOrder(0, newRowOrder);
   *
   * // Sheet after this operation: [['A'], ['D'], ['C'], ['B']]
   * ```
   *
   * @category Rows
   */
  setRowOrder(sheetId, newRowOrder) {
    validateArgToType(sheetId, "number", "sheetId");
    const mapping = this._crudOperations.mappingFromOrder(sheetId, newRowOrder, "row");
    return this.swapRowIndexes(sheetId, mapping);
  }
  /**
   * Checks if it is possible to reorder rows of a sheet according to a permutation.
   *
   * @param {number} sheetId - ID of a sheet to operate on
   * @param {number[]} newRowOrder - permutation of rows
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  [1],
   *  [2],
   *  [4, 5],
   * ]);
   *
   * // returns true
   * hfInstance.isItPossibleToSetRowOrder(0, [2, 1, 0]);
   *
   * // returns false
   * hfInstance.isItPossibleToSetRowOrder(0, [2]);
   * ```
   *
   * @category Rows
   */
  isItPossibleToSetRowOrder(sheetId, newRowOrder) {
    validateArgToType(sheetId, "number", "sheetId");
    try {
      const rowMapping = this._crudOperations.mappingFromOrder(sheetId, newRowOrder, "row");
      this._crudOperations.validateSwapRowIndexes(sheetId, rowMapping);
      this._crudOperations.testRowOrderForArrays(sheetId, rowMapping);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Reorders columns of a sheet according to a source-target mapping.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - ID of a sheet to operate on
   * @param {[number, number][]} columnMapping - array mapping original positions to final positions of columns
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when columnMapping does not define correct column permutation for some subset of columns of the given sheet
   * @throws [[SourceLocationHasArrayError]] when the selected position has array inside
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  [1, 2, 4],
   *  [5]
   * ]);
   *
   * // should set swap columns 0 and 2 in place, returns:
   * // [{
   * //   address: { sheet: 0, col: 2, row: 0 },
   * //   newValue: 1,
   * // },
   * // {
   * //   address: { sheet: 0, col: 2, row: 1 },
   * //   newValue: 5,
   * // },
   * // {
   * //   address: { sheet: 0, col: 0, row: 0 },
   * //   newValue: 4,
   * // },
   * // {
   * //   address: { sheet: 0, col: 0, row: 1 },
   * //   newValue: null,
   * // }]
   * const changes = hfInstance.swapColumnIndexes(0, [[0, 2], [2, 0]]);
   * ```
   *
   * @category Columns
   */
  swapColumnIndexes(sheetId, columnMapping) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.setColumnOrder(sheetId, columnMapping);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Checks if it is possible to reorder columns of a sheet according to a source-target mapping.
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  [1, 2, 4],
   *  [5]
   * ]);
   *
   * // returns true
   * hfInstance.isItPossibleToSwapColumnIndexes(0, [[0, 2], [2, 0]]);
   *
   * // returns false
   * hfInstance.isItPossibleToSwapColumnIndexes(0, [[0, 1]]);
   * ```
   *
   * @category Columns
   */
  isItPossibleToSwapColumnIndexes(sheetId, columnMapping) {
    validateArgToType(sheetId, "number", "sheetId");
    try {
      this._crudOperations.validateSwapColumnIndexes(sheetId, columnMapping);
      this._crudOperations.testColumnOrderForArrays(sheetId, columnMapping);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Reorders columns of a sheet according to a permutation of 0-based indexes.
   * Parameter `newColumnOrder` should have a form `[ newPositionForColumn0, newPositionForColumn1, newPositionForColumn2, ... ]`.
   * This method might be used to [sort the columns of a sheet](../../guide/sorting-data.md).
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note: This method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - ID of a sheet to operate on
   * @param {number[]} newColumnOrder - permutation of columns; array length must match the number of columns returned by [getSheetDimensions()](#getsheetdimensions)
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when columnMapping does not define correct column permutation for some subset of columns of the given sheet
   * @throws [[SourceLocationHasArrayError]] when the selected position has array inside
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *   ['A', 'B', 'C', 'D']
   * ]);
   *
   * const newColumnOrder = [0, 3, 2, 1]; // [ newPosForA, newPosForB, newPosForC, newPosForD ]
   *
   * const changes = hfInstance.setColumnOrder(0, newColumnOrder);
   *
   * // Sheet after this operation: [['A', 'D', 'C', 'B']]
   * ```
   *
   * @category Columns
   */
  setColumnOrder(sheetId, newColumnOrder) {
    validateArgToType(sheetId, "number", "sheetId");
    const mapping = this._crudOperations.mappingFromOrder(sheetId, newColumnOrder, "column");
    return this.swapColumnIndexes(sheetId, mapping);
  }
  /**
   * Checks if it is possible to reorder columns of a sheet according to a permutation.
   *
   * @param {number} sheetId - ID of a sheet to operate on
   * @param {number[]} newColumnOrder - permutation of columns
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  [1, 2, 4],
   *  [5]
   * ]);
   *
   * // returns true
   * hfInstance.isItPossibleToSetColumnOrder(0, [2, 1, 0]);
   *
   * // returns false
   * hfInstance.isItPossibleToSetColumnOrder(0, [1]);
   * ```
   *
   * @category Columns
   */
  isItPossibleToSetColumnOrder(sheetId, newColumnOrder) {
    validateArgToType(sheetId, "number", "sheetId");
    try {
      const columnMapping = this._crudOperations.mappingFromOrder(sheetId, newColumnOrder, "column");
      this._crudOperations.validateSwapColumnIndexes(sheetId, columnMapping);
      this._crudOperations.testColumnOrderForArrays(sheetId, columnMapping);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Returns information whether it is possible to add rows into a specified position in a given sheet.
   * Checks against particular rules to ascertain that addRows can be called.
   * If returns `true`, doing [[addRows]] operation won't throw any errors.
   * Returns `false` if adding rows would exceed the sheet size limit or given arguments are invalid.
   *
   * @param {number} sheetId - sheet ID in which rows will be added
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format [row, amount], where row is a row number above which the rows will be added
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2', '3'],
   * ]);
   *
   * // should return 'true' for this example,
   * // it is possible to add one row in the second row of sheet 0
   * const isAddable = hfInstance.isItPossibleToAddRows(0, [1, 1]);
   * ```
   *
   * @category Rows
   */
  isItPossibleToAddRows(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    const normalizedIndexes = normalizeAddedIndexes(indexes);
    try {
      this._crudOperations.ensureItIsPossibleToAddRows(sheetId, ...normalizedIndexes);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Adds multiple rows into a specified position in a given sheet.
   * Does nothing if rows are outside effective sheet size.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - sheet ID in which rows will be added
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format [row, amount], where row is a row number above which the rows will be added
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[SheetSizeLimitExceededError]] when performing this operation would result in sheet size limits exceeding
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1'],
   *  ['2'],
   * ]);
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values
   * const changes = hfInstance.addRows(0, [0, 1]);
   * ```
   *
   * @category Rows
   */
  addRows(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.addRows(sheetId, ...indexes);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to remove rows from a specified position in a given sheet.
   * Checks against particular rules to ascertain that removeRows can be called.
   * If returns `true`, doing [[removeRows]] operation won't throw any errors.
   * Returns `false` if given arguments are invalid.
   *
   * @param {number} sheetId - sheet ID from which rows will be removed
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format: [row, amount]
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1'],
   *  ['2'],
   * ]);
   *
   * // should return 'true' for this example
   * // it is possible to remove one row from row 1 of sheet 0
   * const isRemovable = hfInstance.isItPossibleToRemoveRows(0, [1, 1]);
   * ```
   *
   * @category Rows
   */
  isItPossibleToRemoveRows(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    const normalizedIndexes = normalizeRemovedIndexes(indexes);
    try {
      this._crudOperations.ensureItIsPossibleToRemoveRows(sheetId, ...normalizedIndexes);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Removes multiple rows from a specified position in a given sheet.
   * Does nothing if rows are outside the effective sheet size.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - sheet ID from which rows will be removed
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format: [row, amount]
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[InvalidArgumentsError]] when the given arguments are invalid
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1'],
   *  ['2'],
   * ]);
   *
   * // should return: [{ sheet: 0, col: 1, row: 2, value: null }] for this example
   * const changes = hfInstance.removeRows(0, [1, 1]);
   * ```
   *
   * @category Rows
   */
  removeRows(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.removeRows(sheetId, ...indexes);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to add columns into a specified position in a given sheet.
   * Checks against particular rules to ascertain that addColumns can be called.
   * If returns `true`, doing [[addColumns]] operation won't throw any errors.
   * Returns `false` if adding columns would exceed the sheet size limit or given arguments are invalid.
   *
   * @param {number} sheetId - sheet ID in which columns will be added
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format: [column, amount], where column is a column number from which new columns will be added
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // should return 'true' for this example,
   * // it is possible to add 1 column in sheet 0, at column 1
   * const isAddable = hfInstance.isItPossibleToAddColumns(0, [1, 1]);
   * ```
   *
   * @category Columns
   */
  isItPossibleToAddColumns(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    const normalizedIndexes = normalizeAddedIndexes(indexes);
    try {
      this._crudOperations.ensureItIsPossibleToAddColumns(sheetId, ...normalizedIndexes);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Adds multiple columns into a specified position in a given sheet.
   * Does nothing if the columns are outside the effective sheet size.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - sheet ID in which columns will be added
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format: [column, amount], where column is a column number from which new columns will be added
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when the given arguments are invalid
   * @throws [[SheetSizeLimitExceededError]] when performing this operation would result in sheet size limits exceeding
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=RAND()', '42'],
   * ]);
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values, for this example:
   * // [{
   * //   address: { sheet: 0, col: 1, row: 0 },
   * //   newValue: 0.92754862796338,
   * // }]
   * const changes = hfInstance.addColumns(0, [0, 1]);
   * ```
   *
   * @category Columns
   */
  addColumns(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.addColumns(sheetId, ...indexes);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to remove columns from a specified position in a given sheet.
   * Checks against particular rules to ascertain that removeColumns can be called.
   * If returns `true`, doing [[removeColumns]] operation won't throw any errors.
   * Returns `false` if given arguments are invalid.
   *
   * @param {number} sheetId - sheet ID from which columns will be removed
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format [column, amount]
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // should return 'true' for this example
   * // it is possible to remove one column, in place of the second column of sheet 0
   * const isRemovable = hfInstance.isItPossibleToRemoveColumns(0, [1, 1]);
   * ```
   *
   * @category Columns
   */
  isItPossibleToRemoveColumns(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    const normalizedIndexes = normalizeRemovedIndexes(indexes);
    try {
      this._crudOperations.ensureItIsPossibleToRemoveColumns(sheetId, ...normalizedIndexes);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Removes multiple columns from a specified position in a given sheet.
   * Does nothing if columns are outside the effective sheet size.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - sheet ID from which columns will be removed
   * @param {ColumnRowIndex[]} indexes - non-contiguous indexes with format: [column, amount]
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when the given arguments are invalid
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['0', '=SUM(1, 2, 3)', '=A1'],
   * ]);
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values, in this example it will return:
   * // [{
   * //   address: { sheet: 0, col: 1, row: 0 },
   * //   newValue: { error: [CellError], value: '#REF!' },
   * // }]
   * const changes = hfInstance.removeColumns(0, [0, 1]);
   * ```
   *
   * @category Columns
   */
  removeColumns(sheetId, ...indexes) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.removeColumns(sheetId, ...indexes);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to move cells to a specified position in a given sheet.
   * Checks against particular rules to ascertain that moveCells can be called.
   * If returns `true`, doing [[moveCells]] operation won't throw any errors.
   * Returns `false` if the operation might be disrupted and causes side effects by the fact that there is an array inside the selected columns, the target location includes an array or the provided address is invalid.
   *
   * @param {SimpleCellRange} source - range for a moved block
   * @param {SimpleCellAddress} destinationLeftCorner - upper left address of the target cell block
   *
   * @throws [[ExpectedValueOfTypeError]] if destinationLeftCorner, source, or any of basic type arguments are of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // choose the coordinates and assign them to variables
   * const source = { sheet: 0, col: 1, row: 0 };
   * const destination = { sheet: 0, col: 3, row: 0 };
   *
   * // should return 'true' for this example
   * // it is possible to move a block of width 1 and height 1
   * // from the corner: column 1 and row 0 of sheet 0
   * // into destination corner: column 3, row 0 of sheet 0
   * const isMovable = hfInstance.isItPossibleToMoveCells({ start: source, end: source }, destination);
   * ```
   * @category Cells
   */
  isItPossibleToMoveCells(source, destinationLeftCorner) {
    if (!isSimpleCellAddress(destinationLeftCorner)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "destinationLeftCorner");
    }
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    try {
      const range = new AbsoluteCellRange(source.start, source.end);
      this._crudOperations.operations.ensureItIsPossibleToMoveCells(range.start, range.width(), range.height(), destinationLeftCorner);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Moves the content of a cell block from source to the target location.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {SimpleCellRange} source - range for a moved block
   * @param {SimpleCellAddress} destinationLeftCorner - upper left address of the target cell block
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if destinationLeftCorner or source are of wrong type
   * @throws [[InvalidArgumentsError]] when the given arguments are invalid
   * @throws [[SheetSizeLimitExceededError]] when performing this operation would result in sheet size limits exceeding
   * @throws [[SourceLocationHasArrayError]] when the source location has array inside - array cannot be moved
   * @throws [[TargetLocationHasArrayError]] when the target location has array inside - cells cannot be replaced by the array
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=RAND()', '42'],
   * ]);
   *
   * // choose the coordinates and assign them to variables
   * const source = { sheet: 0, col: 1, row: 0 };
   * const destination = { sheet: 0, col: 3, row: 0 };
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values, for this example:
   * // [{
   * //   address: { sheet: 0, col: 0, row: 0 },
   * //   newValue: 0.93524248002062,
   * // }]
   * const changes = hfInstance.moveCells({ start: source, end: source }, destination);
   * ```
   *
   * @category Cells
   */
  moveCells(source, destinationLeftCorner) {
    if (!isSimpleCellAddress(destinationLeftCorner)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "destinationLeftCorner");
    }
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    const range = new AbsoluteCellRange(source.start, source.end);
    this._crudOperations.moveCells(range.start, range.width(), range.height(), destinationLeftCorner);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to move a particular number of rows to a specified position in a given sheet.
   * Checks against particular rules to ascertain that moveRows can be called.
   * If returns `true`, doing [[moveRows]] operation won't throw any errors.
   * Returns `false` if the operation might be disrupted and causes side effects by the fact that there is an array inside the selected rows, the target location includes an array or the provided address is invalid.
   *
   * @param {number} sheetId - a sheet number in which the operation will be performed
   * @param {number} startRow - number of the first row to move
   * @param {number} numberOfRows - number of rows to move
   * @param {number} targetRow - row number before which rows will be moved
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1'],
   *  ['2'],
   * ]);
   *
   * // should return 'true' for this example
   * // it is possible to move one row from row 0 into row 2
   * const isMovable = hfInstance.isItPossibleToMoveRows(0, 0, 1, 2);
   * ```
   *
   * @category Rows
   */
  isItPossibleToMoveRows(sheetId, startRow, numberOfRows, targetRow) {
    validateArgToType(sheetId, "number", "sheetId");
    validateArgToType(startRow, "number", "startRow");
    validateArgToType(numberOfRows, "number", "numberOfRows");
    validateArgToType(targetRow, "number", "targetRow");
    try {
      this._crudOperations.ensureItIsPossibleToMoveRows(sheetId, startRow, numberOfRows, targetRow);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Moves a particular number of rows to a specified position in a given sheet.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - a sheet number in which the operation will be performed
   * @param {number} startRow - number of the first row to move
   * @param {number} numberOfRows - number of rows to move
   * @param {number} targetRow - row number before which rows will be moved
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[InvalidArgumentsError]] when the given arguments are invalid
   * @throws [[SourceLocationHasArrayError]] when the source location has array inside - array cannot be moved
   * @throws [[TargetLocationHasArrayError]] when the target location has array inside - cells cannot be replaced by the array
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1'],
   *  ['2'],
   * ]);
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values
   * const changes = hfInstance.moveRows(0, 0, 1, 2);
   * ```
   *
   * @category Rows
   */
  moveRows(sheetId, startRow, numberOfRows, targetRow) {
    validateArgToType(sheetId, "number", "sheetId");
    validateArgToType(startRow, "number", "startRow");
    validateArgToType(numberOfRows, "number", "numberOfRows");
    validateArgToType(targetRow, "number", "targetRow");
    this._crudOperations.moveRows(sheetId, startRow, numberOfRows, targetRow);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to move a particular number of columns to a specified position in a given sheet.
   * Checks against particular rules to ascertain that moveColumns can be called.
   * If returns `true`, doing [[moveColumns]] operation won't throw any errors.
   * Returns `false` if the operation might be disrupted and causes side effects by the fact that there is an array inside the selected columns, the target location includes an array or the provided address is invalid.
   *
   * @param {number} sheetId - a sheet number in which the operation will be performed
   * @param {number} startColumn - number of the first column to move
   * @param {number} numberOfColumns - number of columns to move
   * @param {number} targetColumn - column number before which columns will be moved
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // should return 'true' for this example
   * // it is possible to move one column from column 1 into column 2 of sheet 0
   * const isMovable = hfInstance.isItPossibleToMoveColumns(0, 1, 1, 2);
   * ```
   *
   * @category Columns
   */
  isItPossibleToMoveColumns(sheetId, startColumn, numberOfColumns, targetColumn) {
    validateArgToType(sheetId, "number", "sheetId");
    validateArgToType(startColumn, "number", "startColumn");
    validateArgToType(numberOfColumns, "number", "numberOfColumns");
    validateArgToType(targetColumn, "number", "targetColumn");
    try {
      this._crudOperations.ensureItIsPossibleToMoveColumns(sheetId, startColumn, numberOfColumns, targetColumn);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Moves a particular number of columns to a specified position in a given sheet.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - a sheet number in which the operation will be performed
   * @param {number} startColumn - number of the first column to move
   * @param {number} numberOfColumns - number of columns to move
   * @param {number} targetColumn - column number before which columns will be moved
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[InvalidArgumentsError]] when the given arguments are invalid
   * @throws [[SourceLocationHasArrayError]] when the source location has array inside - array cannot be moved
   * @throws [[TargetLocationHasArrayError]] when the target location has array inside - cells cannot be replaced by the array
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2', '3', '=RAND()', '=SUM(A1:C1)'],
   * ]);
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values, for this example:
   * // [{
   * //   address: { sheet: 0, col: 1, row: 0 },
   * //   newValue: 0.16210054671639,
   * //  }, {
   * //   address: { sheet: 0, col: 4, row: 0 },
   * //   newValue: 6.16210054671639,
   * // }]
   * const changes = hfInstance.moveColumns(0, 1, 1, 2);
   * ```
   *
   * @category Columns
   */
  moveColumns(sheetId, startColumn, numberOfColumns, targetColumn) {
    validateArgToType(sheetId, "number", "sheetId");
    validateArgToType(startColumn, "number", "startColumn");
    validateArgToType(numberOfColumns, "number", "numberOfColumns");
    validateArgToType(targetColumn, "number", "targetColumn");
    this._crudOperations.moveColumns(sheetId, startColumn, numberOfColumns, targetColumn);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Stores a copy of the cell block in internal clipboard for the further paste.
   * Returns the copied values for use in external clipboard.
   *
   * For more information, see the [Clipboard Operations guide](/guide/clipboard-operations.md).
   *
   * @param {SimpleCellRange} source - rectangle range to copy
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if source is of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *   ['1', '2'],
   * ]);
   *
   * // it copies [ [ 2 ] ]
   * const clipboardContent = hfInstance.copy({
   *   start: { sheet: 0, col: 1, row: 0 },
   *   end: { sheet: 0, col: 1, row: 0 },
   * });
   * ```
   *
   * The usage of the internal clipboard is described thoroughly in the [Clipboard Operations guide](../../guide/clipboard-operations.md).
   *
   * @category Clipboard
   */
  copy(source) {
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    const range = new AbsoluteCellRange(source.start, source.end);
    this._crudOperations.copy(range.start, range.width(), range.height());
    return this.getRangeValues(source);
  }
  /**
   * Stores information of the cell block in internal clipboard for further paste.
   * Calling [[paste]] right after this method is equivalent to call [[moveCells]].
   * Almost any CRUD operation called after this method will abort the cut operation.
   * Returns the cut values for use in external clipboard.
   *
   * For more information, see the [Clipboard Operations guide](/guide/clipboard-operations.md).
   *
   * @param {SimpleCellRange} source - rectangle range to cut
   *
   * @throws [[ExpectedValueOfTypeError]] if source is of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *   ['1', '2'],
   * ]);
   *
   * // returns the values that were cut: [ [ 1 ] ]
   * const clipboardContent = hfInstance.cut({
   *   start: { sheet: 0, col: 0, row: 0 },
   *   end: { sheet: 0, col: 0, row: 0 },
   * });
   * ```
   *
   * The usage of the internal clipboard is described thoroughly in the [Clipboard Operations guide](../../guide/clipboard-operations.md).
   *
   * @category Clipboard
   */
  cut(source) {
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    const range = new AbsoluteCellRange(source.start, source.end);
    this._crudOperations.cut(range.start, range.width(), range.height());
    return this.getRangeValues(source);
  }
  /**
   * When called after [[copy]] it pastes copied values and formulas into a cell block.
   * When called after [[cut]] it performs [[moveCells]] operation into the cell block.
   * Does nothing if the clipboard is empty.
   *
   * For more information, see the [Clipboard Operations guide](/guide/clipboard-operations.md).
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {SimpleCellAddress} targetLeftCorner - upper left address of the target cell block
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   * @throws [[SheetSizeLimitExceededError]] when performing this operation would result in sheet size limits exceeding
   * @throws [[NothingToPasteError]] when clipboard is empty
   * @throws [[TargetLocationHasArrayError]] when the selected target area has array inside
   * @throws [[ExpectedValueOfTypeError]] if targetLeftCorner is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *   ['1', '2'],
   * ]);
   *
   * // [ [ 2 ] ] was copied
   * const clipboardContent = hfInstance.copy({
   *   start: { sheet: 0, col: 1, row: 0 },
   *   end: { sheet: 0, col: 1, row: 0 },
   * });
   *
   * // returns a list of modified cells: their absolute addresses and new values
   * const changes = hfInstance.paste({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * The usage of the internal clipboard is described thoroughly in the [Clipboard Operations guide](../../guide/clipboard-operations.md).
   *
   * @category Clipboard
   */
  paste(targetLeftCorner) {
    if (!isSimpleCellAddress(targetLeftCorner)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "targetLeftCorner");
    }
    this.ensureEvaluationIsNotSuspended();
    this._crudOperations.paste(targetLeftCorner);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether there is something in the clipboard.
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // copy desired content
   * const clipboardContent = hfInstance.copy({
   *   start: { sheet: 0, col: 1, row: 0 },
   *   end: { sheet: 0, col: 1, row: 0 },
   * });
   *
   * // returns 'false', there is content in the clipboard
   * const isClipboardEmpty = hfInstance.isClipboardEmpty();
   * ```
   *
   * The usage of the internal clipboard is described thoroughly in the [Clipboard Operations guide](../../guide/clipboard-operations.md).
   *
   * @category Clipboard
   */
  isClipboardEmpty() {
    return this._crudOperations.isClipboardEmpty();
  }
  /**
   * Clears the clipboard content.
   *
   * @example
   * ```js
   * // clears the clipboard, isClipboardEmpty() should return true if called afterwards
   * hfInstance.clearClipboard();
   * ```
   *
   * The usage of the internal clipboard is described thoroughly in the [Clipboard Operations guide](../../guide/clipboard-operations.md).
   *
   * @category Clipboard
   */
  clearClipboard() {
    this._crudOperations.clearClipboard();
  }
  /**
   * Clears the redo stack in undoRedo history.
   *
   * For more information, see the [Undo-Redo guide](/guide/undo-redo.md).
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *   ['1', '2', '3'],
   * ]);
   *
   * // do an operation, for example remove columns
   * hfInstance.removeColumns(0, [0, 1]);
   *
   * // undo the operation
   * hfInstance.undo();
   *
   * // redo the operation
   * hfInstance.redo();
   *
   * // clear the redo stack
   * hfInstance.clearRedoStack();
   * ```
   *
   * @category Undo and Redo
   */
  clearRedoStack() {
    this._crudOperations.undoRedo.clearRedoStack();
  }
  /**
   * Clears the undo stack in undoRedo history.
   *
   * For more information, see the [Undo-Redo guide](/guide/undo-redo.md).
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *   ['1', '2', '3'],
   * ]);
   *
   * // do an operation, for example remove columns
   * hfInstance.removeColumns(0, [0, 1]);
   *
   * // undo the operation
   * hfInstance.undo();
   *
   * // clear the undo stack
   * hfInstance.clearUndoStack();
   * ```
   *
   * @category Undo and Redo
   */
  clearUndoStack() {
    this._crudOperations.undoRedo.clearUndoStack();
  }
  /**
   * Returns the cell content of a given range in a [[CellValue]][][] format.
   *
   * @param {SimpleCellRange} source - rectangular range
   *
   * @throws [[ExpectedValueOfTypeError]] if source is of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(1, 2)', '2', '10'],
   *  ['5', '6', '7'],
   *  ['40', '30', '20'],
   * ]);
   *
   *
   * // returns calculated cells content: [ [ 3, 2 ], [ 5, 6 ] ]
   * const rangeValues = hfInstance.getRangeValues({ start: { sheet: 0, col: 0, row: 0 }, end: { sheet: 0, col: 1, row: 1 } });
   * ```
   *
   * @category Ranges
   */
  getRangeValues(source) {
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    const cellRange = new AbsoluteCellRange(source.start, source.end);
    return cellRange.arrayOfAddressesInRange().map((subarray) => subarray.map((address) => this.getCellValue(address)));
  }
  /**
   * Returns cell formulas in given range.
   *
   * @param {SimpleCellRange} source - rectangular range
   *
   * @throws [[ExpectedValueOfTypeError]] if source is of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(1, 2)', '2', '10'],
   *  ['5', '6', '7'],
   *  ['40', '30', '20'],
   * ]);
   *
   * // returns cell formulas of a given range only:
   * // [ [ '=SUM(1, 2)', undefined ], [ undefined, undefined ] ]
   * const rangeFormulas = hfInstance.getRangeFormulas({ start: { sheet: 0, col: 0, row: 0 }, end: { sheet: 0, col: 1, row: 1 } });
   * ```
   *
   * @category Ranges
   */
  getRangeFormulas(source) {
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    const cellRange = new AbsoluteCellRange(source.start, source.end);
    return cellRange.arrayOfAddressesInRange().map((subarray) => subarray.map((address) => this.getCellFormula(address)));
  }
  /**
   * Returns serialized cells in given range.
   *
   * @param {SimpleCellRange} source - rectangular range
   *
   * @throws [[ExpectedValueOfTypeError]] if source is of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(1, 2)', '2', '10'],
   *  ['5', '6', '7'],
   *  ['40', '30', '20'],
   * ]);
   *
   * // should return serialized cell content for the given range:
   * // [ [ '=SUM(1, 2)', 2 ], [ 5, 6 ] ]
   * const rangeSerialized = hfInstance.getRangeSerialized({ start: { sheet: 0, col: 0, row: 0 }, end: { sheet: 0, col: 1, row: 1 } });
   * ```
   *
   * @category Ranges
   */
  getRangeSerialized(source) {
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    const cellRange = new AbsoluteCellRange(source.start, source.end);
    return cellRange.arrayOfAddressesInRange().map((subarray) => subarray.map((address) => this.getCellSerialized(address)));
  }
  /**
   * Returns values to fill target range using source range, with properly extending the range using wrap-around heuristic.
   *
   * @param {SimpleCellRange} source of data
   * @param {SimpleCellRange} target range where data is intended to be put
   * @param {boolean} offsetsFromTarget if true, offsets are computed from target corner, otherwise from source corner
   *
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   * @throws [[ExpectedValueOfTypeError]] if source or target are of wrong type
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([[1, '=A1'], ['=$A$1', '2']]);
   *
   * // should return [['2', '=$A$1', '2'], ['=A3', 1, '=C3'], ['2', '=$A$1', '2']]
   * hfInstance.getFillRangeData( {start: {sheet: 0, row: 0, col: 0}, end: {sheet: 0, row: 1, col: 1}},
   * {start: {sheet: 0, row: 1, col: 1}, end: {sheet: 0, row: 3, col: 3}});
   * ```
   *
   * @category Ranges
   */
  getFillRangeData(source, target, offsetsFromTarget = false) {
    if (!isSimpleCellRange(source)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "source");
    }
    if (!isSimpleCellRange(target)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "target");
    }
    const sourceRange = new AbsoluteCellRange(source.start, source.end);
    const targetRange = new AbsoluteCellRange(target.start, target.end);
    this.ensureEvaluationIsNotSuspended();
    return targetRange.arrayOfAddressesInRange().map((subarray) => subarray.map((address) => {
      const row = ((address.row - (offsetsFromTarget ? target : source).start.row) % sourceRange.height() + sourceRange.height()) % sourceRange.height() + source.start.row;
      const col = ((address.col - (offsetsFromTarget ? target : source).start.col) % sourceRange.width() + sourceRange.width()) % sourceRange.width() + source.start.col;
      return this._serialization.getCellSerialized({
        row,
        col,
        sheet: sourceRange.sheet
      }, address);
    }));
  }
  /**
   * Returns information whether it is possible to add a sheet to the engine.
   * Checks against particular rules to ascertain that addSheet can be called.
   * If returns `true`, doing [[addSheet]] operation won't throw any errors, and it is possible to add sheet with provided name.
   * Returns `false` if the chosen name is already used.
   *
   * @param {string} sheetName - sheet name, case-insensitive
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *   MySheet1: [ ['1'] ],
   *   MySheet2: [ ['10'] ],
   * });
   *
   * // should return 'false' because 'MySheet2' already exists
   * const isAddable = hfInstance.isItPossibleToAddSheet('MySheet2');
   * ```
   *
   * @category Sheets
   */
  isItPossibleToAddSheet(sheetName) {
    validateArgToType(sheetName, "string", "sheetName");
    try {
      this._crudOperations.ensureItIsPossibleToAddSheet(sheetName);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Adds a new sheet to the HyperFormula instance. Returns given or autogenerated name of a new sheet.
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {string} [sheetName] - if not specified, name is autogenerated
   *
   * @fires [[sheetAdded]] after the sheet was added
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[SheetNameAlreadyTakenError]] when sheet with a given name already exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return 'MySheet3'
   * const nameProvided = hfInstance.addSheet('MySheet3');
   *
   * // should return autogenerated 'Sheet4'
   * // because no name was provided and 3 other ones already exist
   * const generatedName = hfInstance.addSheet();
   * ```
   *
   * @category Sheets
   */
  addSheet(sheetName) {
    if (sheetName !== void 0) {
      validateArgToType(sheetName, "string", "sheetName");
    }
    const addedSheetName = this._crudOperations.addSheet(sheetName);
    this.recomputeIfDependencyGraphNeedsIt();
    this._emitter.emit(Events.SheetAdded, addedSheetName);
    return addedSheetName;
  }
  /**
   * Returns information whether it is possible to remove sheet for the engine.
   * Returns `true` if the provided sheet exists, and therefore it can be removed, doing [[removeSheet]] operation won't throw any errors.
   * Returns `false` otherwise
   *
   * @param {number} sheetId - sheet ID.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return 'true' because sheet with ID 1 exists and is removable
   * const isRemovable = hfInstance.isItPossibleToRemoveSheet(1);
   * ```
   *
   * @category Sheets
   */
  isItPossibleToRemoveSheet(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    try {
      this._crudOperations.ensureScopeIdIsValid(sheetId);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Removes a sheet
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - sheet ID.
   *
   * @fires [[sheetRemoved]] after the sheet was removed
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['=SUM(MySheet2!A1:A2)'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values, in this example it will return:
   * // [{
   * //   address: { sheet: 0, col: 0, row: 0 },
   * //   newValue: { error: [CellError], value: '#REF!' },
   * // }]
   * const changes = hfInstance.removeSheet(1);
   * ```
   *
   * @category Sheets
   */
  removeSheet(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    const displayName = this.sheetMapping.getSheetName(sheetId);
    this._crudOperations.removeSheet(sheetId);
    const changes = this.recomputeIfDependencyGraphNeedsIt();
    this._emitter.emit(Events.SheetRemoved, displayName, changes);
    return changes;
  }
  /**
   * Returns information whether it is possible to clear a specified sheet.
   * If returns `true`, doing [[clearSheet]] operation won't throw any errors, provided sheet exists and its content can be cleared.
   * Returns `false` otherwise
   *
   * @param {number} sheetId - sheet ID.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return 'true' because 'MySheet2' exists and can be cleared
   * const isClearable = hfInstance.isItPossibleToClearSheet(1);
   * ```
   *
   * @category Sheets
   */
  isItPossibleToClearSheet(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    try {
      this._crudOperations.ensureScopeIdIsValid(sheetId);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Clears the sheet content. Double-checks if the sheet exists.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - sheet ID.
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['=SUM(MySheet2!A1:A2)'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values, in this example it will return:
   * // [{
   * //   address: { sheet: 0, col: 0, row: 0 },
   * //   newValue: 0,
   * // }]
   * const changes = hfInstance.clearSheet(0);
   * ```
   *
   * @category Sheets
   */
  clearSheet(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.clearSheet(sheetId);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to replace the sheet content.
   * If returns `true`, doing [[setSheetContent]] operation won't throw any errors, the provided sheet exists and then its content can be replaced.
   * Returns `false` otherwise
   *
   * @param {number} sheetId - sheet ID.
   * @param {RawCellContent[][]} values - array of new values
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return 'true' because sheet of ID 0 exists
   * // and the provided content can be placed in this sheet
   * const isReplaceable = hfInstance.isItPossibleToReplaceSheetContent(0, [['50'], ['60']]);
   * ```
   *
   * @category Sheets
   */
  isItPossibleToReplaceSheetContent(sheetId, values2) {
    validateArgToType(sheetId, "number", "sheetId");
    try {
      this._crudOperations.ensureScopeIdIsValid(sheetId);
      this._crudOperations.ensureItIsPossibleToChangeSheetContents(sheetId, values2);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Replaces the sheet content with new values.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * @param {number} sheetId - sheet ID.
   * @param {RawCellContent[][]} values - array of new values
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[InvalidArgumentsError]] when values argument is not an array of arrays
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return a list of cells which values changed after the operation,
   * // their absolute addresses and new values
   * const changes = hfInstance.setSheetContent(0, [['50'], ['60']]);
   * ```
   *
   * @category Sheets
   */
  setSheetContent(sheetId, values2) {
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.setSheetContent(sheetId, values2);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Computes the simple (absolute) address of a cell address, based on its string representation.
   * - If a sheet name is present in the string representation but is not present in the engine, returns `undefined`.
   * - If no sheet name is present in the string representation, uses `contextSheetId` as a sheet id in the returned address.
   *
   * For more information, see the [Cell references guide](/guide/cell-references.md).
   *
   * @param {string} cellAddress - string representation of cell address in A1 notation
   * @param {number} contextSheetId - sheet id used to construct the simple address in case of missing sheet name in `cellAddress` argument
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   * hfInstance.addSheet('Sheet0'); //sheetId = 0
   *
   * // returns { sheet: 42, col: 0, row: 0 }
   * const simpleCellAddress = hfInstance.simpleCellAddressFromString('A1', 42);
   *
   * // returns { sheet: 0, col: 0, row: 5 }
   * const simpleCellAddress = hfInstance.simpleCellAddressFromString('Sheet0!A6', 42);
   *
   * // returns { sheet: 0, col: 0, row: 5 }
   * const simpleCellAddress = hfInstance.simpleCellAddressFromString('Sheet0!$A$6', 42);
   *
   * // returns 'undefined', as there's no 'Sheet 2' in the HyperFormula instance
   * const simpleCellAddress = hfInstance.simpleCellAddressFromString('Sheet2!A6', 42);
   * ```
   *
   * @category Helpers
   */
  simpleCellAddressFromString(cellAddress, contextSheetId) {
    validateArgToType(cellAddress, "string", "cellAddress");
    validateArgToType(contextSheetId, "number", "sheetId");
    return simpleCellAddressFromString(this.sheetMapping.getSheetId.bind(this.sheetMapping), cellAddress, contextSheetId);
  }
  /**
   * Computes simple (absolute) address of a cell range based on its string representation.
   * If sheet name is present in string representation but not present in the engine, returns `undefined`.
   *
   * For more information, see the [Cell references guide](/guide/cell-references.md).
   *
   * @param {string} cellRange - string representation of cell range in A1 notation
   * @param {number} contextSheetId - sheet id used to construct the simple address in case of missing sheet name in `cellRange` argument
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   * hfInstance.addSheet('Sheet0'); //sheetId = 0
   *
   * // should return { start: { sheet: 0, col: 0, row: 0 }, end: { sheet: 0, col: 1, row: 0 } }
   * const simpleCellAddress = hfInstance.simpleCellRangeFromString('A1:A2', 0);
   * ```
   *
   * @category Helpers
   */
  simpleCellRangeFromString(cellRange, contextSheetId) {
    validateArgToType(cellRange, "string", "cellRange");
    validateArgToType(contextSheetId, "number", "sheetId");
    return simpleCellRangeFromString(this.sheetMapping.getSheetId.bind(this.sheetMapping), cellRange, contextSheetId);
  }
  /**
   * Computes string representation of an absolute address in A1 notation. If `cellAddress.sheet` is not present in the engine, returns `undefined`.
   *
   * For more information, see the [Cell references guide](/guide/cell-references.md).
   *
   * @param {SimpleCellAddress} cellAddress - object representation of an absolute address
   * @param {object | number} optionsOrContextSheetId - options object or number used as context sheet id to construct the string address (see examples)
   *
   * @throws [[ExpectedValueOfTypeError]] if its arguments are of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   * hfInstance.addSheet('Sheet0'); //sheetId = 0
   * const addr = { sheet: 0, col: 1, row: 1 };
   *
   * // should return 'B2'
   * const A1Notation = hfInstance.simpleCellAddressToString(addr);
   *
   * // should return 'B2'
   * const A1Notation = hfInstance.simpleCellAddressToString(addr, { includeSheetName: false });
   *
   * // should return 'Sheet0!B2'
   * const A1Notation = hfInstance.simpleCellAddressToString(addr, { includeSheetName: true });
   *
   * // should return 'B2' as context sheet id is the same as addr.sheet
   * const A1Notation = hfInstance.simpleCellAddressToString(addr, 0);
   *
   * // should return 'Sheet0!B2' as context sheet id is different from addr.sheet
   * const A1Notation = hfInstance.simpleCellAddressToString(addr, 42);
   * ```
   *
   * @category Helpers
   */
  simpleCellAddressToString(cellAddress, optionsOrContextSheetId = {}) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    const contextSheetId = typeof optionsOrContextSheetId === "number" ? optionsOrContextSheetId : optionsOrContextSheetId.includeSheetName ? cellAddress.sheet + 1 : cellAddress.sheet;
    return simpleCellAddressToString(this.sheetMapping.getSheetNameOrThrowError.bind(this.sheetMapping), cellAddress, contextSheetId);
  }
  /**
   * Computes string representation of an absolute range in A1 notation.
   * Returns `undefined` if:
   * - `cellRange` is not a valid range,
   * - `cellRange.start.sheet` and `cellRange.start.end` are different,
   * - `cellRange.start.sheet` is not present in the engine,
   * - `cellRange.start.end` is not present in the engine.
   *
   * Note: This method is useful only for cell ranges; does not work with column ranges and row ranges.
   *
   * For more information, see the [Cell references guide](/guide/cell-references.md).
   *
   * @param {SimpleCellRange} cellRange - object representation of an absolute range
   * @param {object | number} optionsOrContextSheetId - options object or number used as context sheet id to construct the string address (see examples)
   *
   * @throws [[ExpectedValueOfTypeError]] if its arguments are of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   * hfInstance.addSheet('Sheet0'); //sheetId = 0
   * const range = { start: { sheet: 0, col: 1, row: 1 }, end: { sheet: 0, col: 2, row: 1 } };
   *
   * // should return 'B2:C2'
   * const A1Notation = hfInstance.simpleCellRangeToString(range);
   *
   * // should return 'B2:C2'
   * const A1Notation = hfInstance.simpleCellRangeToString(range, { includeSheetName: false });
   *
   * // should return 'Sheet0!B2:C2'
   * const A1Notation = hfInstance.simpleCellRangeToString(range, { includeSheetName: true });
   *
   * // should return 'B2:C2' as context sheet id is the same as range.start.sheet and range.end.sheet
   * const A1Notation = hfInstance.simpleCellRangeToString(range, 0);
   *
   * // should return 'Sheet0!B2:C2' as context sheet id is different from range.start.sheet and range.end.sheet
   * const A1Notation = hfInstance.simpleCellRangeToString(range, 42);
   * ```
   *
   * @category Helpers
   */
  simpleCellRangeToString(cellRange, optionsOrContextSheetId = {}) {
    if (!isSimpleCellRange(cellRange)) {
      throw new ExpectedValueOfTypeError("SimpleCellRange", "cellRange");
    }
    const contextSheetId = typeof optionsOrContextSheetId === "number" ? optionsOrContextSheetId : optionsOrContextSheetId.includeSheetName ? cellRange.start.sheet + cellRange.end.sheet + 1 : cellRange.start.sheet;
    return simpleCellRangeToString(this.sheetMapping.getSheetNameOrThrowError.bind(this.sheetMapping), cellRange, contextSheetId);
  }
  /**
   * Returns all the out-neighbors in the [dependency graph](../../guide/dependency-graph.md) for a given cell address or range. Including:
   * - All cells with formulas that contain the given cell address or range
   * - Some of the ranges that contain the given cell address or range
   *
   * The exact result depends on the optimizations applied by the HyperFormula to the dependency graph, some of which are described in the section ["Optimizations for large ranges"](../../guide/dependency-graph.md#optimizations-for-large-ranges).
   *
   * The returned array includes also named expression dependents. They are represented as cell references with sheet ID `-1`.
   *
   * @param {SimpleCellAddress | SimpleCellRange} address - object representation of an absolute address or range of addresses
   *
   * @throws [[ExpectedValueOfTypeError]] if address is not [[SimpleCellAddress]] or [[SimpleCellRange]]
   * @throws [[SheetsNotEqual]] if range provided has distinct sheet numbers for start and end
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray( [ ['1', '=A1', '=A1+B1'] ] );
   *
   * hfInstance.getCellDependents({ sheet: 0, col: 0, row: 0});
   * // returns [{ sheet: 0, col: 1, row: 0}, { sheet: 0, col: 2, row: 0}]
   * ```
   *
   * @category Helpers
   */
  getCellDependents(address) {
    let vertex;
    if (isSimpleCellAddress(address)) {
      vertex = this._dependencyGraph.addressMapping.getCell(address);
    } else if (isSimpleCellRange(address)) {
      vertex = this._dependencyGraph.rangeMapping.getRangeVertex(address.start, address.end);
    } else {
      throw new ExpectedValueOfTypeError("SimpleCellAddress | SimpleCellRange", address);
    }
    if (vertex === void 0) {
      return [];
    }
    return this._dependencyGraph.getAdjacentNodesAddresses(vertex);
  }
  /**
   * Returns all the in-neighbors in the [dependency graph](../../guide/dependency-graph.md) for a given cell address or range. In particular:
   * - If the argument is a single cell, `getCellPrecedents()` returns all cells and ranges contained in that cell's formula.
   * - If the argument is a range of cells, `getCellPrecedents()` returns some of the cell addresses and smaller ranges contained in that range (but not all of them). The exact result depends on the optimizations applied by the HyperFormula to the dependency graph, some of which are described in the section ["Optimizations for large ranges"](../../guide/dependency-graph.md#optimizations-for-large-ranges).
   *
   * The returned array includes also named expression precedents. They are represented as cell references with sheet ID `-1`.
   *
   * @param {SimpleCellAddress | SimpleCellRange} address - object representation of an absolute address or range of addresses
   *
   * @throws [[ExpectedValueOfTypeError]] if address is of wrong type
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray( [ ['1', '=A1', '=A1+B1'] ] );
   *
   * hfInstance.getCellPrecedents({ sheet: 0, col: 2, row: 0});
   * // returns [{ sheet: 0, col: 0, row: 0}, { sheet: 0, col: 1, row: 0}]
   * ```
   *
   * @category Helpers
   */
  getCellPrecedents(address) {
    let vertex;
    if (isSimpleCellAddress(address)) {
      vertex = this._dependencyGraph.addressMapping.getCell(address);
    } else if (isSimpleCellRange(address)) {
      vertex = this._dependencyGraph.rangeMapping.getRangeVertex(address.start, address.end);
    } else {
      throw new ExpectedValueOfTypeError("SimpleCellAddress | SimpleCellRange", address);
    }
    if (vertex === void 0) {
      return [];
    }
    return this._dependencyGraph.dependencyQueryAddresses(vertex);
  }
  /**
   * Returns a unique sheet name assigned to the sheet of a given ID or `undefined` if the there is no sheet with a given ID.
   *
   * @param {number} sheetId - ID of the sheet, for which we want to retrieve name
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return 'MySheet2' as this sheet is the second one
   * const sheetName = hfInstance.getSheetName(1);
   * ```
   *
   * @category Sheets
   */
  getSheetName(sheetId) {
    validateArgToType(sheetId, "number", "sheetId");
    return this.sheetMapping.getSheetName(sheetId);
  }
  /**
   * List all sheet names.
   * Returns an array of sheet names as strings.
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // should return all sheets names: ['MySheet1', 'MySheet2']
   * const sheetNames = hfInstance.getSheetNames();
   * ```
   *
   * @category Sheets
   */
  getSheetNames() {
    return this.sheetMapping.getSheetNames();
  }
  /**
   * Returns a unique sheet ID assigned to the sheet with a given name or `undefined` if the sheet does not exist.
   *
   * @param {string} sheetName - name of the sheet, for which we want to retrieve ID, case-insensitive.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *   MySheet1: [ ['1'] ],
   *   MySheet2: [ ['10'] ],
   * });
   *
   * // should return '0' because 'MySheet1' is of ID '0'
   * const sheetID = hfInstance.getSheetId('MySheet1');
   * ```
   *
   * @category Sheets
   */
  getSheetId(sheetName) {
    validateArgToType(sheetName, "string", "sheetName");
    return this.sheetMapping.getSheetId(sheetName);
  }
  /**
   * Returns `true` whether sheet with a given name exists. The method accepts sheet name to be checked.
   *
   * @param {string} sheetName - name of the sheet, case-insensitive.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *   MySheet1: [ ['1'] ],
   *   MySheet2: [ ['10'] ],
   * });
   *
   * // should return 'true' since 'MySheet1' exists
   * const sheetExist = hfInstance.doesSheetExist('MySheet1');
   * ```
   *
   * @category Sheets
   */
  doesSheetExist(sheetName) {
    validateArgToType(sheetName, "string", "sheetName");
    return this.sheetMapping.hasSheetWithName(sheetName);
  }
  /**
   * Returns the type of a cell at a given address.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(A2:A3)', '2'],
   * ]);
   *
   * // should return 'FORMULA', the cell of given coordinates is of this type
   * const cellA1Type = hfInstance.getCellType({ sheet: 0, col: 0, row: 0 });
   *
   * // should return 'VALUE', the cell of given coordinates is of this type
   * const cellB1Type = hfInstance.getCellType({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellType(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    const vertex = this.dependencyGraph.getCell(cellAddress);
    return getCellType(vertex, cellAddress);
  }
  /**
   * Returns `true` if the specified cell contains a simple value.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(A2:A3)', '2'],
   * ]);
   *
   * // should return 'true' since the selected cell contains a simple value
   * const isA1Simple = hfInstance.doesCellHaveSimpleValue({ sheet: 0, col: 0, row: 0 });
   *
   * // should return 'false' since the selected cell does not contain a simple value
   * const isB1Simple = hfInstance.doesCellHaveSimpleValue({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  doesCellHaveSimpleValue(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    return this.getCellType(cellAddress) === CellType.VALUE;
  }
  /**
   * Returns `true` if the specified cell contains a formula.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(A2:A3)', '2'],
   * ]);
   *
   * // should return 'true' since the A1 cell contains a formula
   * const A1Formula = hfInstance.doesCellHaveFormula({ sheet: 0, col: 0, row: 0 });
   *
   * // should return 'false' since the B1 cell does not contain a formula
   * const B1NoFormula = hfInstance.doesCellHaveFormula({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  doesCellHaveFormula(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    const cellType = this.getCellType(cellAddress);
    return cellType === CellType.FORMULA || cellType === CellType.ARRAYFORMULA;
  }
  /**
   * Returns`true` if the specified cell is empty.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *   [null, '1'],
   * ]);
   *
   * // should return 'true', cell of provided coordinates is empty
   * const isEmpty = hfInstance.isCellEmpty({ sheet: 0, col: 0, row: 0 });
   *
   * // should return 'false', cell of provided coordinates is not empty
   * const isNotEmpty = hfInstance.isCellEmpty({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  isCellEmpty(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    return this.getCellType(cellAddress) === CellType.EMPTY;
  }
  /**
   * Returns `true` if a given cell is a part of an array.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *    ['{=TRANSPOSE(B1:B1)}'],
   * ]);
   *
   * // should return 'true', cell of provided coordinates is a part of an array
   * const isPartOfArray = hfInstance.isCellPartOfArray({ sheet: 0, col: 0, row: 0 });
   * ```
   *
   * @category Cells
   */
  isCellPartOfArray(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    const cellType = this.getCellType(cellAddress);
    return cellType === CellType.ARRAY || cellType === CellType.ARRAYFORMULA;
  }
  /**
   * Returns type of the cell value of a given address.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * For more information, see the [Types of values guide](/guide/types-of-values.md).
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['=SUM(1, 2, 3)', '2'],
   * ]);
   *
   * // should return 'NUMBER', cell value type of provided coordinates is a number
   * const cellValue = hfInstance.getCellValueType({ sheet: 0, col: 1, row: 0 });
   *
   * // should return 'NUMBER', cell value type of provided coordinates is a number
   * const cellValue = hfInstance.getCellValueType({ sheet: 0, col: 0, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellValueType(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    this.ensureEvaluationIsNotSuspended();
    const value = this.dependencyGraph.getCellValue(cellAddress);
    return getCellValueType(value);
  }
  /**
   * Returns detailed type of the cell value of a given address.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * For more information, see the [Types of values guide](/guide/types-of-values.md).
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1%', '1$'],
   * ]);
   *
   * // should return 'NUMBER_PERCENT', cell value type of provided coordinates is a number with a format inference percent.
   * const cellType = hfInstance.getCellValueDetailedType({ sheet: 0, col: 0, row: 0 });
   *
   * // should return 'NUMBER_CURRENCY', cell value type of provided coordinates is a number with a format inference currency.
   * const cellType = hfInstance.getCellValueDetailedType({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellValueDetailedType(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    this.ensureEvaluationIsNotSuspended();
    const value = this.dependencyGraph.getCellValue(cellAddress);
    return getCellValueDetailedType(value);
  }
  /**
   * Returns auxiliary format information of the cell value of a given address.
   * The method accepts cell coordinates as object with column, row and sheet numbers.
   *
   * @param {SimpleCellAddress} cellAddress - cell coordinates
   *
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[EvaluationSuspendedError]] when the evaluation is suspended
   * @throws [[ExpectedValueOfTypeError]] if cellAddress is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1$', '1'],
   * ]);
   *
   * // should return '$', cell value type of provided coordinates is a number with a format inference currency, parsed as using '$' as currency.
   * const cellFormat = hfInstance.getCellValueFormat({ sheet: 0, col: 0, row: 0 });
   *
   * // should return undefined, cell value type of provided coordinates is a number with no format information.
   * const cellFormat = hfInstance.getCellValueFormat({ sheet: 0, col: 1, row: 0 });
   * ```
   *
   * @category Cells
   */
  getCellValueFormat(cellAddress) {
    if (!isSimpleCellAddress(cellAddress)) {
      throw new ExpectedValueOfTypeError("SimpleCellAddress", "cellAddress");
    }
    this.ensureEvaluationIsNotSuspended();
    const value = this.dependencyGraph.getCellValue(cellAddress);
    return getCellValueFormat(value);
  }
  /**
   * Returns the number of existing sheets.
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['1', '2'],
   * ]);
   *
   * // should return the number of sheets which is '1'
   * const sheetsCount = hfInstance.countSheets();
   * ```
   *
   * @category Sheets
   */
  countSheets() {
    return this.sheetMapping.numberOfSheets();
  }
  /**
   * Returns information whether it is possible to rename sheet.
   * Returns `true` if the sheet with provided id exists and new name is available
   * Returns `false` if sheet cannot be renamed
   *
   * @param {number} sheetId - a sheet number
   * @param {string} newName - a name of the sheet to be given
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *   MySheet1: [ ['1'] ],
   *   MySheet2: [ ['10'] ],
   * });
   *
   * // returns true
   * hfInstance.isItPossibleToRenameSheet(0, 'MySheet0');
   * ```
   *
   * @category Sheets
   */
  isItPossibleToRenameSheet(sheetId, newName) {
    validateArgToType(sheetId, "number", "sheetId");
    validateArgToType(newName, "string", "newName");
    try {
      this._crudOperations.ensureItIsPossibleToRenameSheet(sheetId, newName);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Renames a specified sheet.
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {number} sheetId - a sheet ID
   * @param {string} newName - a name of the sheet to be given, if is the same as the old one the method does nothing
   *
   * @fires [[sheetRenamed]] after the sheet was renamed
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] when the given sheet ID does not exist
   * @throws [[SheetNameAlreadyTakenError]] when the provided sheet name already exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *   MySheet1: [ ['1'] ],
   *   MySheet2: [ ['10'] ],
   * });
   *
   * // renames the sheet 'MySheet1'
   * hfInstance.renameSheet(0, 'MySheet0');
   * ```
   *
   * @category Sheets
   */
  renameSheet(sheetId, newName) {
    validateArgToType(sheetId, "number", "sheetId");
    validateArgToType(newName, "string", "newName");
    const oldName = this._crudOperations.renameSheet(sheetId, newName);
    this.recomputeIfDependencyGraphNeedsIt();
    if (oldName !== void 0) {
      this._emitter.emit(Events.SheetRenamed, oldName, newName);
    }
  }
  /**
   * Runs the provided callback as a single [batch operation](../../guide/batch-operations.md) and returns the changed cells.
   *
   * Returns [an array of cells whose values changed as a result of all batched operations](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {() => void} batchOperations - a function with operations to be performed
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   * @fires [[evaluationSuspended]] always
   * @fires [[evaluationResumed]] after the recomputation of necessary values
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // multiple operations in a single callback will trigger evaluation only once
   * // and only one set of changes is returned as a combined result of all
   * // the operations that were triggered within the callback
   * const changes = hfInstance.batch(() => {
   *  hfInstance.setCellContents({ col: 3, row: 0, sheet: 0 }, [['=B1']]);
   *  hfInstance.setCellContents({ col: 4, row: 0, sheet: 0 }, [['=A1']]);
   * });
   * ```
   *
   * @category Batch
   */
  batch(batchOperations) {
    this.suspendEvaluation();
    this._crudOperations.beginUndoRedoBatchMode();
    try {
      batchOperations();
    } catch (e3) {
      this._crudOperations.commitUndoRedoBatchMode();
      this.resumeEvaluation();
      throw e3;
    }
    this._crudOperations.commitUndoRedoBatchMode();
    return this.resumeEvaluation();
  }
  /**
   * Suspends the dependency graph recalculation to start a [batch operation](../../guide/batch-operations.md).
   * It allows optimizing the performance.
   * With this method, multiple CRUD operations can be done without triggering recalculation after every operation.
   * Suspending evaluation should result in an overall faster calculation compared to recalculating after each operation separately.
   * To resume the evaluation use [[resumeEvaluation]].
   *
   * @fires [[evaluationSuspended]] always
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // similar to batch() but operations are not within a callback,
   * // one method suspends the recalculation
   * // the second will resume calculations and return the changes
   *
   * // suspend the evaluation with this method
   * hfInstance.suspendEvaluation();
   *
   * // perform operations
   * hfInstance.setCellContents({ col: 3, row: 0, sheet: 0 }, [['=B1']]);
   * hfInstance.setSheetContent(1, [['50'], ['60']]);
   *
   * // use resumeEvaluation to resume
   * const changes = hfInstance.resumeEvaluation();
   * ```
   *
   * @category Batch
   */
  suspendEvaluation() {
    this._evaluationSuspended = true;
    this._emitter.emit(Events.EvaluationSuspended);
  }
  /**
   * Resumes the dependency graph recalculation that was [suspended](../../guide/batch-operations.md) with [[suspendEvaluation]].
   * It also triggers the recalculation and returns [an array of cells whose values changed as a result of all batched operations](/guide/basic-operations.md#changes-array).
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   * @fires [[evaluationResumed]] after the recomputation of necessary values
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  MySheet1: [ ['1'] ],
   *  MySheet2: [ ['10'] ],
   * });
   *
   * // similar to batch() but operations are not within a callback,
   * // one method suspends the recalculation
   * // the second will resume calculations and return the changes
   *
   * // first, suspend the evaluation
   * hfInstance.suspendEvaluation();
   *
   * // perform operations
   * hfInstance.setCellContents({ col: 3, row: 0, sheet: 0 }, [['=B1']]);
   * hfInstance.setSheetContent(1, [['50'], ['60']]);
   *
   * // resume the evaluation
   * const changes = hfInstance.resumeEvaluation();
   * ```
   *
   * @category Batch
   */
  resumeEvaluation() {
    this._evaluationSuspended = false;
    const changes = this.recomputeIfDependencyGraphNeedsIt();
    this._emitter.emit(Events.EvaluationResumed, changes);
    return changes;
  }
  /**
   * Checks if the dependency graph recalculation process is [suspended](../../guide/batch-operations.md) or not.
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // suspend the evaluation
   * hfInstance.suspendEvaluation();
   *
   * // between suspendEvaluation() and resumeEvaluation()
   * // or inside batch() callback it will return 'true', otherwise 'false'
   * const isEvaluationSuspended = hfInstance.isEvaluationSuspended();
   *
   * const changes = hfInstance.resumeEvaluation();
   * ```
   *
   * @category Batch
   */
  isEvaluationSuspended() {
    return this._evaluationSuspended;
  }
  /**
   * Returns information whether it is possible to add named expression into a specific scope.
   * Checks against particular rules to ascertain that addNamedExpression can be called.
   * If returns `true`, doing [[addNamedExpression]] operation won't throw any errors.
   * Returns `false` if the operation might be disrupted.
   *
   * @param {string} expressionName - a name of the expression to be added
   * @param {RawCellContent} expression - the expression
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // should return 'true' for this example,
   * // it is possible to add named expression to global scope
   * const isAddable = hfInstance.isItPossibleToAddNamedExpression('prettyName', '=Sheet1!$A$1+100');
   * ```
   *
   * @category Named Expressions
   */
  isItPossibleToAddNamedExpression(expressionName, expression, scope) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    try {
      this._crudOperations.ensureItIsPossibleToAddNamedExpression(expressionName, expression, scope);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Adds a specified named expression.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {string} expressionName - a name of the expression to be added
   * @param {RawCellContent} expression - the expression
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   * @param {NamedExpressionOptions?} options - additional metadata related to named expression
   *
   * @fires [[namedExpressionAdded]] always, unless [[batch]] mode is used
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NamedExpressionNameIsAlreadyTakenError]] when the named-expression name is not available.
   * @throws [[NamedExpressionNameIsInvalidError]] when the named-expression name is not valid
   * @throws [[NoRelativeAddressesAllowedError]] when the named-expression formula contains relative references
   * @throws [[NoSheetWithIdError]] if no sheet with given sheetId exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add own expression, scope limited to 'Sheet1' (sheetId=0), the method should return a list of cells which values
   * // changed after the operation, their absolute addresses and new values
   * // for this example:
   * // [{
   * //   name: 'prettyName',
   * //   newValue: 142,
   * // }]
   * const changes = hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100', 0);
   * ```
   *
   * @category Named Expressions
   */
  addNamedExpression(expressionName, expression, scope, options) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    this._crudOperations.addNamedExpression(expressionName, expression, scope, options);
    const changes = this.recomputeIfDependencyGraphNeedsIt();
    this._emitter.emit(Events.NamedExpressionAdded, expressionName, changes);
    return changes;
  }
  /**
   * Gets specified named expression value.
   * Returns a [[CellValue]] or undefined if the given named expression does not exist.
   *
   * For more information, see the [Named expressions guide](/guide/named-expressions.md).
   *
   * @param {string} expressionName - expression name, case-insensitive.
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] if no sheet with given sheetId exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add a named expression, only 'Sheet1' (sheetId=0) considered as it is the scope
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100', 'Sheet1');
   *
   * // returns the calculated value of a passed named expression, '142' for this example
   * const myFormula = hfInstance.getNamedExpressionValue('prettyName', 'Sheet1');
   * ```
   *
   * @category Named Expressions
   */
  getNamedExpressionValue(expressionName, scope) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    this.ensureEvaluationIsNotSuspended();
    this._crudOperations.ensureScopeIdIsValid(scope);
    const namedExpression = this._namedExpressions.namedExpressionForScope(expressionName, scope);
    if (namedExpression) {
      return this._serialization.getCellValue(namedExpression.address);
    } else {
      return void 0;
    }
  }
  /**
   * Returns a normalized formula string for given named expression, or `undefined` for a named expression that does not exist or does not hold a formula.
   *
   * For more information, see the [Named expressions guide](/guide/named-expressions.md).
   *
   * @param {string} expressionName - expression name, case-insensitive.
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] if no sheet with given sheetId exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add a named expression in 'Sheet1' (sheetId=0)
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100', 0);
   *
   * // returns a normalized formula string corresponding to the passed name from 'Sheet1' (sheetId=0),
   * // '=Sheet1!A1+100' for this example
   * const myFormula = hfInstance.getNamedExpressionFormula('prettyName', 0);
   * ```
   *
   * @category Named Expressions
   */
  getNamedExpressionFormula(expressionName, scope) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    this._crudOperations.ensureScopeIdIsValid(scope);
    const namedExpression = this._namedExpressions.namedExpressionForScope(expressionName, scope);
    if (namedExpression === void 0) {
      return void 0;
    } else {
      return this._serialization.getCellFormula(namedExpression.address);
    }
  }
  /**
   * Returns a named expression, or `undefined` for a named expression that does not exist or does not hold a formula.
   *
   * For more information, see the [Named expressions guide](/guide/named-expressions.md).
   *
   * @param {string} expressionName - expression name, case-insensitive.
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] if no sheet with given sheetId exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add a named expression in 'Sheet1' (sheetId=0)
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100', 0);
   *
   * // returns a named expression that corresponds to the passed name from 'Sheet1' (sheetId=0)
   * // for this example, returns:
   * // {name: 'prettyName', expression: '=Sheet1!$A$1+100', options: undefined, scope: 0}
   * const myFormula = hfInstance.getNamedExpression('prettyName', 0);
   *
   * // for a named expression that doesn't exist, returns 'undefined':
   * const myFormulaTwo = hfInstance.getNamedExpression('uglyName', 0);
   * ```
   *
   * @category Named Expressions
   */
  getNamedExpression(expressionName, scope) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    const namedExpression = this._namedExpressions.namedExpressionForScope(expressionName, scope);
    if (namedExpression === void 0) {
      return void 0;
    }
    const expression = this._serialization.getCellFormula(namedExpression.address);
    return {
      name: expressionName,
      scope,
      expression,
      options: namedExpression.options
    };
  }
  /**
   * Returns information whether it is possible to change named expression in a specific scope.
   * Checks against particular rules to ascertain that changeNamedExpression can be called.
   * If returns `true`, doing [[changeNamedExpression]] operation won't throw any errors.
   * Returns `false` if the operation might be disrupted.
   *
   * @param {string} expressionName - an expression name, case-insensitive.
   * @param {RawCellContent} newExpression - a new expression
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add a named expression
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100');
   *
   * // should return 'true' for this example,
   * // it is possible to change named expression
   * const isAddable = hfInstance.isItPossibleToChangeNamedExpression('prettyName', '=Sheet1!$A$1+100');
   * ```
   *
   * @category Named Expressions
   */
  isItPossibleToChangeNamedExpression(expressionName, newExpression, scope) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    try {
      this._crudOperations.ensureItIsPossibleToChangeNamedExpression(expressionName, newExpression, scope);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Changes a given named expression to a specified formula.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {string} expressionName - an expression name, case-insensitive.
   * @param {RawCellContent} newExpression - a new expression
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   * @param {NamedExpressionOptions?} options - additional metadata related to named expression
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NamedExpressionDoesNotExistError]] when the given expression does not exist.
   * @throws [[NoSheetWithIdError]] if no sheet with given sheetId exists
   * @throws [[ArrayFormulasNotSupportedError]] when the named expression formula is an array formula
   * @throws [[NoRelativeAddressesAllowedError]] when the named expression formula contains relative references
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add a named expression, scope limited to 'Sheet1' (sheetId=0)
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100', 0);
   *
   * // change the named expression
   * const changes = hfInstance.changeNamedExpression('prettyName', '=Sheet1!$A$1+200');
   * ```
   *
   * @category Named Expressions
   */
  changeNamedExpression(expressionName, newExpression, scope, options) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    this._crudOperations.changeNamedExpressionExpression(expressionName, scope, newExpression, options);
    return this.recomputeIfDependencyGraphNeedsIt();
  }
  /**
   * Returns information whether it is possible to remove named expression from a specific scope.
   * Checks against particular rules to ascertain that removeNamedExpression can be called.
   * If returns `true`, doing [[removeNamedExpression]] operation won't throw any errors.
   * Returns `false` if the operation might be disrupted.
   *
   * @param {string} expressionName - an expression name, case-insensitive.
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add a named expression
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100');
   *
   * // should return 'true' for this example,
   * // it is possible to change named expression
   * const isAddable = hfInstance.isItPossibleToRemoveNamedExpression('prettyName');
   * ```
   *
   * @category Named Expressions
   */
  isItPossibleToRemoveNamedExpression(expressionName, scope) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    try {
      this._crudOperations.isItPossibleToRemoveNamedExpression(expressionName, scope);
      return true;
    } catch (e3) {
      return false;
    }
  }
  /**
   * Removes a named expression.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @param {string} expressionName - expression name, case-insensitive.
   * @param {number?} scope - scope definition, `sheetId` for local scope or `undefined` for global scope
   *
   * @fires [[namedExpressionRemoved]] after the expression was removed
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NamedExpressionDoesNotExistError]] when the given expression does not exist.
   * @throws [[NoSheetWithIdError]] if no sheet with given sheetId exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   * ]);
   *
   * // add a named expression
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100', 0);
   *
   * // remove the named expression
   * const changes = hfInstance.removeNamedExpression('prettyName', 0);
   * ```
   *
   * @category Named Expressions
   */
  removeNamedExpression(expressionName, scope) {
    validateArgToType(expressionName, "string", "expressionName");
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    const removedNamedExpression = this._crudOperations.removeNamedExpression(expressionName, scope);
    if (removedNamedExpression) {
      const changes = this.recomputeIfDependencyGraphNeedsIt();
      this._emitter.emit(Events.NamedExpressionRemoved, removedNamedExpression.displayName, changes);
      return changes;
    } else {
      return [];
    }
  }
  /**
   * Lists named expressions.
   * - If scope parameter is provided, returns an array of expression names defined for this scope.
   * - If scope parameter is undefined, returns an array of global expression names.
   *
   * For more information, see the [Named expressions guide](/guide/named-expressions.md).
   *
   * @param {number?} scope - scope of the named expressions, `sheetId` for local scope or `undefined` for global scope
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NoSheetWithIdError]] if no sheet with given sheetId exists
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   *  ['50'],
   *  ['60'],
   * ]);
   *
   * // add two named expressions and one scoped
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100');
   * hfInstance.addNamedExpression('anotherPrettyName', '=Sheet1!$A$2+100');
   * hfInstance.addNamedExpression('alsoPrettyName', '=Sheet1!$A$3+100', 0);
   *
   * // list the expressions, should return: ['prettyName', 'anotherPrettyName'] for this example
   * const listOfExpressions = hfInstance.listNamedExpressions();
   *
   *  // list the expressions, should return: ['alsoPrettyName'] for this example
   * const listOfExpressions = hfInstance.listNamedExpressions(0);
   * ```
   *
   * @category Named Expressions
   */
  listNamedExpressions(scope) {
    if (scope !== void 0) {
      validateArgToType(scope, "number", "scope");
    }
    this._crudOperations.ensureScopeIdIsValid(scope);
    return this._namedExpressions.getAllNamedExpressionsNamesInScope(scope);
  }
  /**
   * Returns all named expressions serialized.
   *
   * For more information, see the [Named expressions guide](/guide/named-expressions.md).
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   *  ['50'],
   *  ['60'],
   * ]);
   *
   * // add two named expressions and one scoped
   * hfInstance.addNamedExpression('prettyName', '=Sheet1!$A$1+100');
   * hfInstance.addNamedExpression('anotherPrettyName', '=Sheet1!$A$2+100');
   * hfInstance.addNamedExpression('prettyName3', '=Sheet1!$A$3+100', 0);
   *
   * // get all expressions serialized
   * // should return:
   * // [
   * // {name: 'prettyName', expression: '=Sheet1!$A$1+100', options: undefined, scope: undefined},
   * // {name: 'anotherPrettyName', expression: '=Sheet1!$A$2+100', options: undefined, scope: undefined},
   * // {name: 'alsoPrettyName', expression: '=Sheet1!$A$3+100', options: undefined, scope: 0}
   * // ]
   * const allExpressions = hfInstance.getAllNamedExpressionsSerialized();
   * ```
   *
   * @category Named Expressions
   */
  getAllNamedExpressionsSerialized() {
    return this._serialization.getAllNamedExpressionsSerialized();
  }
  /**
   * Parses and then unparses a formula.
   * Returns a normalized formula (e.g., restores the original capitalization of sheet names, function names, cell addresses, and named expressions).
   *
   * @param {string} formulaString - a formula in a proper format - it must start with "="
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   * @throws [[NotAFormulaError]] when the provided string is not a valid formula, i.e., does not start with "="
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromArray([
   *  ['42'],
   *  ['50'],
   * ]);
   *
   * // returns '=Sheet1!$A$1+10'
   * const normalizedFormula = hfInstance.normalizeFormula('=SHEET1!$A$1+10');
   *
   * // returns '=3*$A$1'
   * const normalizedFormula = hfInstance.normalizeFormula('=3*$a$1');
   * ```
   *
   * @category Helpers
   */
  normalizeFormula(formulaString) {
    validateArgToType(formulaString, "string", "formulaString");
    const {
      ast,
      address
    } = this.extractTemporaryFormula(formulaString);
    if (ast === void 0) {
      throw new NotAFormulaError();
    }
    return this._unparser.unparse(ast, address);
  }
  /**
   * Calculates fire-and-forget formula, returns the calculated value.
   *
   * @param {string} formulaString - A formula in a proper format, starting with `=`.
   * @param {number} sheetId - The ID of a sheet in context of which the formula gets evaluated.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type arguments is of wrong type.
   * @throws [[NotAFormulaError]] when the provided string is not a valid formula (i.e., doesn't start with `=`).
   * @throws [[NoSheetWithIdError]] when the provided `sheetID` doesn't exist.
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildFromSheets({
   *  Sheet1: [['58']],
   *  Sheet2: [['1', '2', '3'], ['4', '5', '6']]
   * });
   *
   * // returns the calculated formula's value
   * // for this example, returns `68`
   * const calculatedFormula = hfInstance.calculateFormula('=A1+10', 0);
   *
   * // for this example, returns [['11', '12', '13'], ['14', '15', '16']]
   * const calculatedFormula = hfInstance.calculateFormula('=A1:B3+10', 1);
   * ```
   *
   * @category Helpers
   */
  calculateFormula(formulaString, sheetId) {
    validateArgToType(formulaString, "string", "formulaString");
    validateArgToType(sheetId, "number", "sheetId");
    this._crudOperations.ensureScopeIdIsValid(sheetId);
    const {
      ast,
      address,
      dependencies
    } = this.extractTemporaryFormula(formulaString, sheetId);
    if (ast === void 0) {
      throw new NotAFormulaError();
    }
    const internalCellValue = this.evaluator.runAndForget(ast, address, dependencies);
    return this._exporter.exportScalarOrRange(internalCellValue);
  }
  /**
   * Return a list of named expressions used by a formula.
   *
   * @param {string} formulaString - A formula in a proper format, starting with `=`.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type arguments is of wrong type.
   * @throws [[NotAFormulaError]] when the provided string is not a valid formula (i.e., doesn't start with `=`).
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // returns a list of named expressions used by a formula
   * // for this example, returns ['foo', 'bar']
   * const namedExpressions = hfInstance.getNamedExpressionsFromFormula('=foo+bar*2');
   * ```
   *
   * @category Helpers
   */
  getNamedExpressionsFromFormula(formulaString) {
    validateArgToType(formulaString, "string", "formulaString");
    const {
      ast,
      dependencies
    } = this.extractTemporaryFormula(formulaString);
    if (ast === void 0) {
      throw new NotAFormulaError();
    }
    const namedExpressionDependencies = dependencies.filter((dep) => dep instanceof NamedExpressionDependency).map((namedExpr) => namedExpr.name);
    const uniqueNamedExpressionDependencies = [...new Set(namedExpressionDependencies)];
    return uniqueNamedExpressionDependencies;
  }
  /**
   * Validates the formula.
   * If the provided string starts with "=" and is a parsable formula, the method returns `true`.
   * The validation is purely grammatical: the method doesn't verify if the formula can be calculated or not.
   *
   * @param {string} formulaString -  a formula in a proper format - it must start with "="
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * // checks if the given string is a valid formula, should return 'true' for this example
   * const isFormula = hfInstance.validateFormula('=SUM(1, 2)');
   * ```
   *
   * @category Helpers
   */
  validateFormula(formulaString) {
    validateArgToType(formulaString, "string", "formulaString");
    const {
      ast
    } = this.extractTemporaryFormula(formulaString);
    return ast !== void 0;
  }
  /**
   * Returns translated names of all functions registered in this instance of HyperFormula
   * according to the language set in the configuration
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // return translated names of all functions, assign to a variable
   * const allNames = hfInstance.getRegisteredFunctionNames();
   * ```
   *
   * @category Custom Functions
   */
  getRegisteredFunctionNames() {
    const language = HyperFormula.getLanguage(this._config.language);
    return language.getFunctionTranslations(this._functionRegistry.getRegisteredFunctionIds());
  }
  /**
   * Returns class of a plugin used by function with given id
   *
   * For more information, see the [Custom functions guide](/guide/custom-functions.md).
   *
   * @param {string} functionId - id of a function, e.g., 'SUMIF'
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * // import your own plugin
   * import { MyExamplePlugin } from './file_with_your_plugin';
   *
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // register a plugin
   * HyperFormula.registerFunctionPlugin(MyExamplePlugin);
   *
   * // get the plugin
   * const myPlugin = hfInstance.getFunctionPlugin('EXAMPLE');
   * ```
   *
   * @category Custom Functions
   */
  getFunctionPlugin(functionId) {
    validateArgToType(functionId, "string", "functionId");
    return this._functionRegistry.getFunctionPlugin(functionId);
  }
  /**
   * Returns classes of all plugins registered in this instance of HyperFormula
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // return classes of all plugins registered, assign to a variable
   * const allNames = hfInstance.getAllFunctionPlugins();
   * ```
   *
   * @category Custom Functions
   */
  getAllFunctionPlugins() {
    return this._functionRegistry.getPlugins();
  }
  /**
   * Interprets number as a date + time.
   *
   * For more information, see the [Date and time handling guide](/guide/date-and-time-handling.md).
   *
   * @param {number} inputNumber - number of days since nullDate, should be non-negative, fractions are interpreted as hours/minutes/seconds.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // pass the number of days since nullDate
   * // the method should return formatted date and time, for this example:
   * // {year: 2020, month: 1, day: 15, hours: 2, minutes: 24, seconds: 0}
   * const dateTimeFromNumber = hfInstance.numberToDateTime(43845.1);
   *
   * ```
   *
   * @category Helpers
   */
  numberToDateTime(inputNumber) {
    validateArgToType(inputNumber, "number", "val");
    return this._evaluator.interpreter.dateTimeHelper.numberToSimpleDateTime(inputNumber);
  }
  /**
   * Interprets number as a date.
   *
   * For more information, see the [Date and time handling guide](/guide/date-and-time-handling.md).
   *
   * @param {number} inputNumber - number of days since nullDate, should be non-negative, fractions are ignored.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // pass the number of days since nullDate
   * // the method should return formatted date, for this example:
   * // {year: 2020, month: 1, day: 15}
   * const dateFromNumber = hfInstance.numberToDate(43845);
   * ```
   *
   * @category Helpers
   */
  numberToDate(inputNumber) {
    validateArgToType(inputNumber, "number", "val");
    return this._evaluator.interpreter.dateTimeHelper.numberToSimpleDate(inputNumber);
  }
  /**
   * Interprets number as a time (hours/minutes/seconds).
   *
   * For more information, see the [Date and time handling guide](/guide/date-and-time-handling.md).
   *
   * @param {number} inputNumber - time in 24h units.
   *
   * @throws [[ExpectedValueOfTypeError]] if any of its basic type argument is of wrong type
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // pass a number to be interpreted as a time
   * // should return {hours: 26, minutes: 24} for this example
   * const timeFromNumber = hfInstance.numberToTime(1.1);
   * ```
   *
   * @category Helpers
   */
  numberToTime(inputNumber) {
    validateArgToType(inputNumber, "number", "val");
    return numberToSimpleTime(inputNumber);
  }
  /**
   * Subscribes to an event.
   * For the list of all available events, see [[Listeners]].
   *
   * @param {Event} event the name of the event to subscribe to
   * @param {Listener} listener to be called when event is emitted
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // subscribe to a 'sheetAdded', pass a simple handler
   * hfInstance.on('sheetAdded', ( ) => { console.log('foo') });
   *
   * // add a sheet to trigger an event,
   * // console should print 'foo' after each time sheet is added in this example
   * hfInstance.addSheet('FooBar');
   * ```
   *
   * @category Events
   */
  on(event, listener) {
    this._emitter.on(event, listener);
  }
  /**
   * Subscribes to an event once.
   * For the list of all available events, see [[Listeners]].
   *
   * @param {Event} event the name of the event to subscribe to
   * @param {Listener} listener to be called when event is emitted
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // subscribe to a 'sheetAdded', pass a simple handler
   * hfInstance.once('sheetAdded', ( ) => { console.log('foo') });
   *
   * // call addSheet twice,
   * // console should print 'foo' only once when the sheet is added in this example
   * hfInstance.addSheet('FooBar');
   * hfInstance.addSheet('FooBaz');
   * ```
   *
   * @category Events
   */
  once(event, listener) {
    this._emitter.once(event, listener);
  }
  /**
   * Unsubscribes from an event or from all events.
   * For the list of all available events, see [[Listeners]].
   *
   * @param {Event} event the name of the event to subscribe to
   * @param {Listener} listener to be called when event is emitted
   *
   * @example
   * ```js
   * const hfInstance = HyperFormula.buildEmpty();
   *
   * // define a simple function to be called upon emitting an event
   * const handler = ( ) => { console.log('baz') }
   *
   * // subscribe to a 'sheetAdded', pass the handler
   * hfInstance.on('sheetAdded', handler);
   *
   * // add a sheet to trigger an event,
   * // console should print 'baz' each time a sheet is added
   * hfInstance.addSheet('FooBar');
   *
   * // unsubscribe from a 'sheetAdded'
   * hfInstance.off('sheetAdded', handler);
   *
   * // add a sheet, the console should not print anything
   * hfInstance.addSheet('FooBaz');
   * ```
   *
   * @category Events
   */
  off(event, listener) {
    this._emitter.off(event, listener);
  }
  /**
   * Destroys instance of HyperFormula.
   *
   * @example
   * ```js
   * // destroys the instance
   * hfInstance.destroy();
   * ```
   *
   * @category Instance
   */
  destroy() {
    objectDestroy(this);
  }
  /**
   * Throws an error if evaluation is suspended.
   *
   * @internal
   */
  ensureEvaluationIsNotSuspended() {
    if (this._evaluationSuspended) {
      throw new EvaluationSuspendedError();
    }
  }
  /**
   * Parses a formula string and extracts its AST and dependencies.
   *
   * @internal
   */
  extractTemporaryFormula(formulaString, sheetId = 1) {
    const parsedCellContent = this._cellContentParser.parse(formulaString);
    const address = {
      sheet: sheetId,
      col: 0,
      row: 0
    };
    if (!(parsedCellContent instanceof CellContent.Formula)) {
      return {
        address,
        dependencies: []
      };
    }
    const {
      ast,
      errors,
      dependencies
    } = this._parser.parse(parsedCellContent.formula, address);
    if (errors.length > 0) {
      return {
        address,
        dependencies: []
      };
    }
    return {
      ast,
      address,
      dependencies
    };
  }
  /**
   * Rebuilds the engine with new configuration.
   */
  rebuildWithConfig(newParams) {
    const newConfig = this._config.mergeConfig(newParams);
    const configNewLanguage = this._config.mergeConfig({
      language: newParams.language
    });
    const serializedSheets = this._serialization.withNewConfig(configNewLanguage, this._namedExpressions).getAllSheetsSerialized();
    const serializedNamedExpressions = this._serialization.getAllNamedExpressionsSerialized();
    const newEngine = BuildEngineFactory.rebuildWithConfig(newConfig, serializedSheets, serializedNamedExpressions, this._stats);
    this._config = newEngine.config;
    this._stats = newEngine.stats;
    this._dependencyGraph = newEngine.dependencyGraph;
    this._columnSearch = newEngine.columnSearch;
    this._parser = newEngine.parser;
    this._unparser = newEngine.unparser;
    this._cellContentParser = newEngine.cellContentParser;
    this._evaluator = newEngine.evaluator;
    this._lazilyTransformingAstService = newEngine.lazilyTransformingAstService;
    this._crudOperations = newEngine.crudOperations;
    this._exporter = newEngine.exporter;
    this._namedExpressions = newEngine.namedExpressions;
    this._serialization = newEngine.serialization;
    this._functionRegistry = newEngine.functionRegistry;
  }
  /**
   * When enough transformations have accumulated, forces all formula vertices and
   * column index entries to apply pending lazy transformations, then compacts the
   * transformation history and cleans up orphaned undo oldData entries.
   */
  compactLazyTransformationsIfNeeded() {
    var _a2;
    if (this._lazilyTransformingAstService.needsCompaction()) {
      this._dependencyGraph.forceApplyPostponedTransformations();
      this._columnSearch.forceApplyPostponedTransformations();
      this._lazilyTransformingAstService.compact();
      (_a2 = this._lazilyTransformingAstService.undoRedo) === null || _a2 === void 0 ? void 0 : _a2.cleanupOrphanedOldData();
    }
  }
  /**
   * Runs a recomputation starting from recently changed vertices.
   *
   * Returns [an array of cells whose values changed as a result of this operation](/guide/basic-operations.md#changes-array).
   *
   * Note that this method may trigger dependency graph recalculation.
   *
   * @fires [[valuesUpdated]] if recalculation was triggered by this change
   */
  recomputeIfDependencyGraphNeedsIt() {
    if (!this._evaluationSuspended) {
      const changes = this._crudOperations.getAndClearContentChanges();
      const verticesToRecomputeFrom = this.dependencyGraph.verticesToRecompute();
      this.dependencyGraph.clearDirtyVertices();
      this.compactLazyTransformationsIfNeeded();
      if (verticesToRecomputeFrom.length > 0) {
        changes.addAll(this.evaluator.partialRun(verticesToRecomputeFrom));
      }
      const exportedChanges = changes.exportChanges(this._exporter);
      if (!changes.isEmpty()) {
        this._emitter.emit(Events.ValuesUpdated, exportedChanges);
      }
      return exportedChanges;
    } else {
      return [];
    }
  }
}
HyperFormula.version = "3.3.0";
HyperFormula.buildDate = "20/05/2026 14:43:58";
HyperFormula.releaseDate = "20/05/2026";
HyperFormula.languages = {};
HyperFormula.registeredLanguages = /* @__PURE__ */ new Map();
const privatePool = /* @__PURE__ */ new WeakMap();
class Config {
  constructor(options = {}, showDeprecatedWarns = true) {
    const {
      accentSensitive,
      caseSensitive,
      caseFirst,
      chooseAddressMappingPolicy,
      context,
      currencySymbol,
      dateFormats,
      decimalSeparator,
      evaluateNullToZero,
      functionArgSeparator,
      functionPlugins,
      ignorePunctuation,
      leapYear1900,
      localeLang,
      language,
      ignoreWhiteSpace,
      licenseKey,
      matchWholeCell,
      arrayColumnSeparator,
      arrayRowSeparator,
      maxRows,
      maxColumns,
      nullYear,
      nullDate,
      parseDateTime,
      precisionEpsilon,
      precisionRounding,
      stringifyDateTime,
      stringifyDuration,
      smartRounding,
      timeFormats,
      thousandSeparator,
      useArrayArithmetic,
      useStats,
      undoLimit,
      maxPendingLazyTransformations,
      useColumnIndex,
      useRegularExpressions,
      useWildcards
    } = options;
    this.useArrayArithmetic = configValueFromParam(useArrayArithmetic, "boolean", "useArrayArithmetic");
    this.accentSensitive = configValueFromParam(accentSensitive, "boolean", "accentSensitive");
    this.caseSensitive = configValueFromParam(caseSensitive, "boolean", "caseSensitive");
    this.caseFirst = configValueFromParam(caseFirst, ["upper", "lower", "false"], "caseFirst");
    this.ignorePunctuation = configValueFromParam(ignorePunctuation, "boolean", "ignorePunctuation");
    this.chooseAddressMappingPolicy = chooseAddressMappingPolicy !== null && chooseAddressMappingPolicy !== void 0 ? chooseAddressMappingPolicy : Config.defaultConfig.chooseAddressMappingPolicy;
    this.dateFormats = [...configValueFromParamCheck(dateFormats, Array.isArray, "array", "dateFormats")];
    this.timeFormats = [...configValueFromParamCheck(timeFormats, Array.isArray, "array", "timeFormats")];
    this.functionArgSeparator = configValueFromParam(functionArgSeparator, "string", "functionArgSeparator");
    this.decimalSeparator = configValueFromParam(decimalSeparator, [".", ","], "decimalSeparator");
    this.language = configValueFromParam(language, "string", "language");
    this.ignoreWhiteSpace = configValueFromParam(ignoreWhiteSpace, ["standard", "any"], "ignoreWhiteSpace");
    this.licenseKey = configValueFromParam(licenseKey, "string", "licenseKey");
    this.thousandSeparator = configValueFromParam(thousandSeparator, ["", ",", " ", "."], "thousandSeparator");
    this.arrayColumnSeparator = configValueFromParam(arrayColumnSeparator, [",", ";"], "arrayColumnSeparator");
    this.arrayRowSeparator = configValueFromParam(arrayRowSeparator, [";", "|"], "arrayRowSeparator");
    this.localeLang = configValueFromParam(localeLang, "string", "localeLang");
    this.functionPlugins = [...functionPlugins !== null && functionPlugins !== void 0 ? functionPlugins : Config.defaultConfig.functionPlugins];
    this.smartRounding = configValueFromParam(smartRounding, "boolean", "smartRounding");
    this.evaluateNullToZero = configValueFromParam(evaluateNullToZero, "boolean", "evaluateNullToZero");
    this.nullYear = configValueFromParam(nullYear, "number", "nullYear");
    validateNumberToBeAtLeast(this.nullYear, "nullYear", 0);
    validateNumberToBeAtMost(this.nullYear, "nullYear", 100);
    this.precisionRounding = configValueFromParam(precisionRounding, "number", "precisionRounding");
    validateNumberToBeAtLeast(this.precisionRounding, "precisionRounding", 0);
    this.precisionEpsilon = configValueFromParam(precisionEpsilon, "number", "precisionEpsilon");
    validateNumberToBeAtLeast(this.precisionEpsilon, "precisionEpsilon", 0);
    this.useColumnIndex = configValueFromParam(useColumnIndex, "boolean", "useColumnIndex");
    this.useStats = configValueFromParam(useStats, "boolean", "useStats");
    this.parseDateTime = configValueFromParam(parseDateTime, "function", "parseDateTime");
    this.stringifyDateTime = configValueFromParam(stringifyDateTime, "function", "stringifyDateTime");
    this.stringifyDuration = configValueFromParam(stringifyDuration, "function", "stringifyDuration");
    this.translationPackage = HyperFormula.getLanguage(this.language);
    this.errorMapping = this.translationPackage.buildErrorMapping();
    this.nullDate = configValueFromParamCheck(nullDate, instanceOfSimpleDate, "IDate", "nullDate");
    this.leapYear1900 = configValueFromParam(leapYear1900, "boolean", "leapYear1900");
    this.undoLimit = configValueFromParam(undoLimit, "number", "undoLimit");
    this.maxPendingLazyTransformations = configValueFromParam(maxPendingLazyTransformations, "number", "maxPendingLazyTransformations");
    this.useRegularExpressions = configValueFromParam(useRegularExpressions, "boolean", "useRegularExpressions");
    this.useWildcards = configValueFromParam(useWildcards, "boolean", "useWildcards");
    this.matchWholeCell = configValueFromParam(matchWholeCell, "boolean", "matchWholeCell");
    validateNumberToBeAtLeast(this.undoLimit, "undoLimit", 0);
    validateNumberToBeAtLeast(this.maxPendingLazyTransformations, "maxPendingLazyTransformations", 1);
    this.maxRows = configValueFromParam(maxRows, "number", "maxRows");
    validateNumberToBeAtLeast(this.maxRows, "maxRows", 1);
    this.maxColumns = configValueFromParam(maxColumns, "number", "maxColumns");
    this.currencySymbol = this.setupCurrencySymbol(currencySymbol);
    validateNumberToBeAtLeast(this.maxColumns, "maxColumns", 1);
    this.context = context;
    privatePool.set(this, {
      licenseKeyValidityState: checkLicenseKeyValidity(this.licenseKey)
    });
    configCheckIfParametersNotInConflict({
      value: this.decimalSeparator,
      name: "decimalSeparator"
    }, {
      value: this.functionArgSeparator,
      name: "functionArgSeparator"
    }, {
      value: this.thousandSeparator,
      name: "thousandSeparator"
    });
    configCheckIfParametersNotInConflict({
      value: this.arrayRowSeparator,
      name: "arrayRowSeparator"
    }, {
      value: this.arrayColumnSeparator,
      name: "arrayColumnSeparator"
    });
  }
  setupCurrencySymbol(currencySymbol) {
    const valueAfterCheck = [...configValueFromParamCheck(currencySymbol, Array.isArray, "array", "currencySymbol")];
    valueAfterCheck.forEach((val) => {
      if (typeof val !== "string") {
        throw new ExpectedValueOfTypeError("string[]", "currencySymbol");
      }
      if (val === "") {
        throw new ConfigValueEmpty("currencySymbol");
      }
    });
    return valueAfterCheck;
  }
  /**
   * Proxied property to its private counterpart. This makes the property
   * as accessible as the other Config options but without ability to change the value.
   *
   * @internal
   */
  get licenseKeyValidityState() {
    return privatePool.get(this).licenseKeyValidityState;
  }
  getConfig() {
    return getFullConfigFromPartial(this);
  }
  mergeConfig(init) {
    const mergedConfig = Object.assign({}, this.getConfig(), init);
    return new Config(mergedConfig, false);
  }
  static warnDeprecatedOptions(options) {
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static warnDeprecatedIfUsed(inputValue, paramName, fromVersion, replacementName) {
    if (inputValue !== void 0) {
      if (replacementName === void 0) {
        console.warn(`${paramName} option is deprecated since ${fromVersion}`);
      } else {
        console.warn(`${paramName} option is deprecated since ${fromVersion}, please use ${replacementName}`);
      }
    }
  }
}
Config.defaultConfig = {
  accentSensitive: false,
  currencySymbol: ["$"],
  caseSensitive: false,
  caseFirst: "lower",
  context: void 0,
  chooseAddressMappingPolicy: new AlwaysDense(),
  dateFormats: ["DD/MM/YYYY", "DD/MM/YY"],
  decimalSeparator: ".",
  evaluateNullToZero: false,
  functionArgSeparator: ",",
  functionPlugins: [],
  ignorePunctuation: false,
  language: "enGB",
  ignoreWhiteSpace: "standard",
  licenseKey: "",
  leapYear1900: false,
  localeLang: "en",
  matchWholeCell: true,
  arrayColumnSeparator: ",",
  arrayRowSeparator: ";",
  maxRows: 4e4,
  maxColumns: 18278,
  nullYear: 30,
  nullDate: {
    year: 1899,
    month: 12,
    day: 30
  },
  parseDateTime: defaultParseToDateTime,
  precisionEpsilon: 1e-13,
  precisionRounding: 10,
  smartRounding: true,
  stringifyDateTime: defaultStringifyDateTime,
  stringifyDuration: defaultStringifyDuration,
  timeFormats: ["hh:mm", "hh:mm:ss.sss"],
  thousandSeparator: "",
  undoLimit: 20,
  maxPendingLazyTransformations: 50,
  useRegularExpressions: false,
  useWildcards: true,
  useColumnIndex: false,
  useStats: false,
  useArrayArithmetic: false
};
function getFullConfigFromPartial(partialConfig) {
  var _a2;
  const ret = {};
  for (const key in Config.defaultConfig) {
    const val = (_a2 = partialConfig[key]) !== null && _a2 !== void 0 ? _a2 : Config.defaultConfig[key];
    if (Array.isArray(val)) {
      ret[key] = [...val];
    } else {
      ret[key] = val;
    }
  }
  return ret;
}
function getDefaultConfig() {
  return getFullConfigFromPartial({});
}
const dictionary = {
  errors: {
    CYCLE: "#CYCLE!",
    DIV_BY_ZERO: "#DIV/0!",
    ERROR: "#ERROR!",
    NA: "#N/A",
    NAME: "#NAME?",
    NUM: "#NUM!",
    REF: "#REF!",
    SPILL: "#SPILL!",
    VALUE: "#VALUE!"
  },
  functions: {
    FILTER: "FILTER",
    ADDRESS: "ADDRESS",
    "ARRAY_CONSTRAIN": "ARRAY_CONSTRAIN",
    ARRAYFORMULA: "ARRAYFORMULA",
    ABS: "ABS",
    ACOS: "ACOS",
    ACOSH: "ACOSH",
    ACOT: "ACOT",
    ACOTH: "ACOTH",
    AND: "AND",
    ARABIC: "ARABIC",
    ASIN: "ASIN",
    ASINH: "ASINH",
    ATAN2: "ATAN2",
    ATAN: "ATAN",
    ATANH: "ATANH",
    AVERAGE: "AVERAGE",
    AVERAGEA: "AVERAGEA",
    AVERAGEIF: "AVERAGEIF",
    BASE: "BASE",
    BIN2DEC: "BIN2DEC",
    BIN2HEX: "BIN2HEX",
    BIN2OCT: "BIN2OCT",
    BITAND: "BITAND",
    BITLSHIFT: "BITLSHIFT",
    BITOR: "BITOR",
    BITRSHIFT: "BITRSHIFT",
    BITXOR: "BITXOR",
    CEILING: "CEILING",
    CHAR: "CHAR",
    CHOOSE: "CHOOSE",
    CLEAN: "CLEAN",
    CODE: "CODE",
    COLUMN: "COLUMN",
    COLUMNS: "COLUMNS",
    CONCATENATE: "CONCATENATE",
    CORREL: "CORREL",
    COS: "COS",
    COSH: "COSH",
    COT: "COT",
    COTH: "COTH",
    COUNT: "COUNT",
    COUNTA: "COUNTA",
    COUNTBLANK: "COUNTBLANK",
    COUNTIF: "COUNTIF",
    COUNTIFS: "COUNTIFS",
    COUNTUNIQUE: "COUNTUNIQUE",
    CSC: "CSC",
    CSCH: "CSCH",
    CUMIPMT: "CUMIPMT",
    CUMPRINC: "CUMPRINC",
    DATE: "DATE",
    DATEDIF: "DATEDIF",
    DATEVALUE: "DATEVALUE",
    DAY: "DAY",
    DAYS360: "DAYS360",
    DAYS: "DAYS",
    DB: "DB",
    DAVERAGE: "DAVERAGE",
    DCOUNT: "DCOUNT",
    DCOUNTA: "DCOUNTA",
    DGET: "DGET",
    DMAX: "DMAX",
    DMIN: "DMIN",
    DPRODUCT: "DPRODUCT",
    DSTDEV: "DSTDEV",
    DSTDEVP: "DSTDEVP",
    DSUM: "DSUM",
    DVAR: "DVAR",
    DVARP: "DVARP",
    DDB: "DDB",
    DEC2BIN: "DEC2BIN",
    DEC2HEX: "DEC2HEX",
    DEC2OCT: "DEC2OCT",
    DECIMAL: "DECIMAL",
    DEGREES: "DEGREES",
    DELTA: "DELTA",
    DOLLARDE: "DOLLARDE",
    DOLLARFR: "DOLLARFR",
    EDATE: "EDATE",
    EFFECT: "EFFECT",
    EOMONTH: "EOMONTH",
    ERF: "ERF",
    ERFC: "ERFC",
    EVEN: "EVEN",
    EXACT: "EXACT",
    EXP: "EXP",
    FALSE: "FALSE",
    FIND: "FIND",
    FORMULATEXT: "FORMULATEXT",
    FV: "FV",
    FVSCHEDULE: "FVSCHEDULE",
    HEX2BIN: "HEX2BIN",
    HEX2DEC: "HEX2DEC",
    HEX2OCT: "HEX2OCT",
    HLOOKUP: "HLOOKUP",
    HOUR: "HOUR",
    HYPERLINK: "HYPERLINK",
    IF: "IF",
    IFERROR: "IFERROR",
    IFNA: "IFNA",
    IFS: "IFS",
    INDEX: "INDEX",
    INT: "INT",
    INTERVAL: "INTERVAL",
    IPMT: "IPMT",
    IRR: "IRR",
    ISBINARY: "ISBINARY",
    ISBLANK: "ISBLANK",
    ISERR: "ISERR",
    ISERROR: "ISERROR",
    ISEVEN: "ISEVEN",
    ISFORMULA: "ISFORMULA",
    ISLOGICAL: "ISLOGICAL",
    ISNA: "ISNA",
    ISNONTEXT: "ISNONTEXT",
    ISNUMBER: "ISNUMBER",
    ISODD: "ISODD",
    ISOWEEKNUM: "ISOWEEKNUM",
    ISPMT: "ISPMT",
    ISREF: "ISREF",
    ISTEXT: "ISTEXT",
    LEFT: "LEFT",
    LEN: "LEN",
    LN: "LN",
    LOG10: "LOG10",
    LOG: "LOG",
    LOWER: "LOWER",
    MATCH: "MATCH",
    MAX: "MAX",
    MAXA: "MAXA",
    MAXIFS: "MAXIFS",
    MAXPOOL: "MAXPOOL",
    MEDIAN: "MEDIAN",
    MEDIANPOOL: "MEDIANPOOL",
    MID: "MID",
    MIN: "MIN",
    MINA: "MINA",
    MINIFS: "MINIFS",
    MINUTE: "MINUTE",
    MIRR: "MIRR",
    MMULT: "MMULT",
    MOD: "MOD",
    MONTH: "MONTH",
    N: "N",
    NA: "NA",
    NETWORKDAYS: "NETWORKDAYS",
    "NETWORKDAYS.INTL": "NETWORKDAYS.INTL",
    NOMINAL: "NOMINAL",
    NOT: "NOT",
    NOW: "NOW",
    NPER: "NPER",
    NPV: "NPV",
    OCT2BIN: "OCT2BIN",
    OCT2DEC: "OCT2DEC",
    OCT2HEX: "OCT2HEX",
    ODD: "ODD",
    OFFSET: "OFFSET",
    OR: "OR",
    PI: "PI",
    PMT: "PMT",
    PDURATION: "PDURATION",
    PRODUCT: "PRODUCT",
    POWER: "POWER",
    PPMT: "PPMT",
    PROPER: "PROPER",
    PV: "PV",
    RADIANS: "RADIANS",
    RAND: "RAND",
    RATE: "RATE",
    REPLACE: "REPLACE",
    REPT: "REPT",
    RIGHT: "RIGHT",
    ROMAN: "ROMAN",
    ROUND: "ROUND",
    ROUNDDOWN: "ROUNDDOWN",
    ROUNDUP: "ROUNDUP",
    ROW: "ROW",
    ROWS: "ROWS",
    RRI: "RRI",
    SEARCH: "SEARCH",
    SEC: "SEC",
    SECH: "SECH",
    SECOND: "SECOND",
    SEQUENCE: "SEQUENCE",
    SHEET: "SHEET",
    SHEETS: "SHEETS",
    SIN: "SIN",
    SINH: "SINH",
    SLN: "SLN",
    SPLIT: "SPLIT",
    SQRT: "SQRT",
    STDEVA: "STDEVA",
    "STDEV.P": "STDEV.P",
    STDEVPA: "STDEVPA",
    "STDEV.S": "STDEV.S",
    SUBSTITUTE: "SUBSTITUTE",
    SUBTOTAL: "SUBTOTAL",
    SUM: "SUM",
    SUMIF: "SUMIF",
    SUMIFS: "SUMIFS",
    SUMPRODUCT: "SUMPRODUCT",
    SUMSQ: "SUMSQ",
    SWITCH: "SWITCH",
    SYD: "SYD",
    T: "T",
    TAN: "TAN",
    TANH: "TANH",
    TBILLEQ: "TBILLEQ",
    TBILLPRICE: "TBILLPRICE",
    TBILLYIELD: "TBILLYIELD",
    TEXT: "TEXT",
    TEXTJOIN: "TEXTJOIN",
    TIME: "TIME",
    TIMEVALUE: "TIMEVALUE",
    TODAY: "TODAY",
    TRANSPOSE: "TRANSPOSE",
    TRIM: "TRIM",
    TRUE: "TRUE",
    TRUNC: "TRUNC",
    UNICHAR: "UNICHAR",
    UNICODE: "UNICODE",
    UPPER: "UPPER",
    VALUE: "VALUE",
    VARA: "VARA",
    "VAR.P": "VAR.P",
    VARPA: "VARPA",
    "VAR.S": "VAR.S",
    VLOOKUP: "VLOOKUP",
    WEEKDAY: "WEEKDAY",
    WEEKNUM: "WEEKNUM",
    WORKDAY: "WORKDAY",
    "WORKDAY.INTL": "WORKDAY.INTL",
    XNPV: "XNPV",
    XOR: "XOR",
    XLOOKUP: "XLOOKUP",
    YEAR: "YEAR",
    YEARFRAC: "YEARFRAC",
    "HF.ADD": "HF.ADD",
    "HF.CONCAT": "HF.CONCAT",
    "HF.DIVIDE": "HF.DIVIDE",
    "HF.EQ": "HF.EQ",
    "HF.GT": "HF.GT",
    "HF.GTE": "HF.GTE",
    "HF.LT": "HF.LT",
    "HF.LTE": "HF.LTE",
    "HF.MINUS": "HF.MINUS",
    "HF.MULTIPLY": "HF.MULTIPLY",
    "HF.NE": "HF.NE",
    "HF.POW": "HF.POW",
    "HF.UMINUS": "HF.UMINUS",
    "HF.UNARY_PERCENT": "HF.UNARY_PERCENT",
    "HF.UPLUS": "HF.UPLUS",
    VARP: "VARP",
    VAR: "VAR",
    STDEVP: "STDEVP",
    STDEV: "STDEV",
    "EXPON.DIST": "EXPON.DIST",
    FISHER: "FISHER",
    FISHERINV: "FISHERINV",
    GAMMA: "GAMMA",
    "GAMMA.DIST": "GAMMA.DIST",
    GAMMALN: "GAMMALN",
    "GAMMALN.PRECISE": "GAMMALN.PRECISE",
    "GAMMA.INV": "GAMMA.INV",
    GAUSS: "GAUSS",
    FACT: "FACT",
    FACTDOUBLE: "FACTDOUBLE",
    COMBIN: "COMBIN",
    COMBINA: "COMBINA",
    GCD: "GCD",
    LCM: "LCM",
    MROUND: "MROUND",
    MULTINOMIAL: "MULTINOMIAL",
    QUOTIENT: "QUOTIENT",
    RANDBETWEEN: "RANDBETWEEN",
    SERIESSUM: "SERIESSUM",
    SIGN: "SIGN",
    SQRTPI: "SQRTPI",
    SUMX2MY2: "SUMX2MY2",
    SUMX2PY2: "SUMX2PY2",
    SUMXMY2: "SUMXMY2",
    "EXPONDIST": "EXPONDIST",
    GAMMADIST: "GAMMADIST",
    GAMMAINV: "GAMMAINV",
    "BETA.DIST": "BETA.DIST",
    BETADIST: "BETADIST",
    "BETA.INV": "BETA.INV",
    BETAINV: "BETAINV",
    "BINOM.DIST": "BINOM.DIST",
    BINOMDIST: "BINOMDIST",
    "BINOM.INV": "BINOM.INV",
    BESSELI: "BESSELI",
    BESSELJ: "BESSELJ",
    BESSELK: "BESSELK",
    BESSELY: "BESSELY",
    "CHISQ.DIST": "CHISQ.DIST",
    "CHISQ.DIST.RT": "CHISQ.DIST.RT",
    "CHISQ.INV": "CHISQ.INV",
    "CHISQ.INV.RT": "CHISQ.INV.RT",
    CHIDIST: "CHIDIST",
    CHIINV: "CHIINV",
    "F.DIST": "F.DIST",
    "F.DIST.RT": "F.DIST.RT",
    "F.INV": "F.INV",
    "F.INV.RT": "F.INV.RT",
    FDIST: "FDIST",
    FINV: "FINV",
    WEIBULL: "WEIBULL",
    "WEIBULL.DIST": "WEIBULL.DIST",
    POISSON: "POISSON",
    "POISSON.DIST": "POISSON.DIST",
    "HYPGEOM.DIST": "HYPGEOM.DIST",
    HYPGEOMDIST: "HYPGEOMDIST",
    "T.DIST": "T.DIST",
    "T.DIST.2T": "T.DIST.2T",
    "T.DIST.RT": "T.DIST.RT",
    "T.INV": "T.INV",
    "T.INV.2T": "T.INV.2T",
    TDIST: "TDIST",
    TINV: "TINV",
    LOGINV: "LOGINV",
    "LOGNORM.DIST": "LOGNORM.DIST",
    "LOGNORM.INV": "LOGNORM.INV",
    LOGNORMDIST: "LOGNORMDIST",
    "NORM.DIST": "NORM.DIST",
    "NORM.INV": "NORM.INV",
    "NORM.S.DIST": "NORM.S.DIST",
    "NORM.S.INV": "NORM.S.INV",
    NORMDIST: "NORMDIST",
    NORMINV: "NORMINV",
    NORMSDIST: "NORMSDIST",
    NORMSINV: "NORMSINV",
    PHI: "PHI",
    "NEGBINOM.DIST": "NEGBINOM.DIST",
    "NEGBINOMDIST": "NEGBINOMDIST",
    COMPLEX: "COMPLEX",
    IMABS: "IMABS",
    IMAGINARY: "IMAGINARY",
    IMARGUMENT: "IMARGUMENT",
    IMCONJUGATE: "IMCONJUGATE",
    IMCOS: "IMCOS",
    IMCOSH: "IMCOSH",
    IMCOT: "IMCOT",
    IMCSC: "IMCSC",
    IMCSCH: "IMCSCH",
    IMDIV: "IMDIV",
    IMEXP: "IMEXP",
    IMLN: "IMLN",
    IMLOG10: "IMLOG10",
    IMLOG2: "IMLOG2",
    IMPOWER: "IMPOWER",
    IMPRODUCT: "IMPRODUCT",
    IMREAL: "IMREAL",
    IMSEC: "IMSEC",
    IMSECH: "IMSECH",
    IMSIN: "IMSIN",
    IMSINH: "IMSINH",
    IMSQRT: "IMSQRT",
    IMSUB: "IMSUB",
    IMSUM: "IMSUM",
    IMTAN: "IMTAN",
    LARGE: "LARGE",
    SMALL: "SMALL",
    PERCENTILE: "PERCENTILE",
    "PERCENTILE.INC": "PERCENTILE.INC",
    "PERCENTILE.EXC": "PERCENTILE.EXC",
    QUARTILE: "QUARTILE",
    "QUARTILE.INC": "QUARTILE.INC",
    "QUARTILE.EXC": "QUARTILE.EXC",
    AVEDEV: "AVEDEV",
    CONFIDENCE: "CONFIDENCE",
    "CONFIDENCE.NORM": "CONFIDENCE.NORM",
    "CONFIDENCE.T": "CONFIDENCE.T",
    DEVSQ: "DEVSQ",
    GEOMEAN: "GEOMEAN",
    HARMEAN: "HARMEAN",
    CRITBINOM: "CRITBINOM",
    "COVARIANCE.P": "COVARIANCE.P",
    "COVARIANCE.S": "COVARIANCE.S",
    "COVAR": "COVAR",
    PEARSON: "PEARSON",
    RSQ: "RSQ",
    STANDARDIZE: "STANDARDIZE",
    "Z.TEST": "Z.TEST",
    ZTEST: "ZTEST",
    "F.TEST": "F.TEST",
    FTEST: "FTEST",
    STEYX: "STEYX",
    SLOPE: "SLOPE",
    "CHISQ.TEST": "CHISQ.TEST",
    CHITEST: "CHITEST",
    "T.TEST": "T.TEST",
    TTEST: "TTEST",
    SKEW: "SKEW",
    "SKEW.P": "SKEW.P",
    WEIBULLDIST: "WEIBULLDIST",
    VARS: "VARS",
    TINV2T: "TINV2T",
    TDISTRT: "TDISTRT",
    TDIST2T: "TDIST2T",
    STDEVS: "STDEVS",
    FINVRT: "FINVRT",
    FDISTRT: "FDISTRT",
    CHIDISTRT: "CHIDISTRT",
    CHIINVRT: "CHIINVRT",
    COVARIANCEP: "COVARIANCEP",
    COVARIANCES: "COVARIANCES",
    LOGNORMINV: "LOGNORMINV",
    POISSONDIST: "POISSONDIST",
    SKEWP: "SKEWP",
    "CEILING.MATH": "CEILING.MATH",
    FLOOR: "FLOOR",
    "FLOOR.MATH": "FLOOR.MATH",
    "CEILING.PRECISE": "CEILING.PRECISE",
    "FLOOR.PRECISE": "FLOOR.PRECISE",
    "ISO.CEILING": "ISO.CEILING"
  },
  langCode: "enGB",
  ui: {
    NEW_SHEET_PREFIX: "Sheet"
  }
};
var AbsStyle;
(function(AbsStyle2) {
  AbsStyle2[AbsStyle2["FullyAbsolute"] = 1] = "FullyAbsolute";
  AbsStyle2[AbsStyle2["RowAbsoluteColRelative"] = 2] = "RowAbsoluteColRelative";
  AbsStyle2[AbsStyle2["RowRelativeColAbsolute"] = 3] = "RowRelativeColAbsolute";
  AbsStyle2[AbsStyle2["FullyRelative"] = 4] = "FullyRelative";
})(AbsStyle || (AbsStyle = {}));
class AddressPlugin extends FunctionPlugin {
  verifyAddressArguments(row, col, abs2, useA1Style) {
    if (useA1Style) {
      if (row < 1 || col < 1) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
    } else {
      if (AbsStyle.FullyAbsolute == abs2) {
        if (row < 1 || col < 1) {
          return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
        }
      } else if (AbsStyle.RowAbsoluteColRelative == abs2) {
        if (row < 1) {
          return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
        }
      } else if (AbsStyle.RowRelativeColAbsolute == abs2) {
        if (col < 1) {
          return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
        }
      }
    }
    return void 0;
  }
  address(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ADDRESS"), (row, col, abs2, useA1Style, sheetName) => {
      const argumentError = this.verifyAddressArguments(row, col, abs2, useA1Style);
      if (argumentError !== void 0) {
        return argumentError;
      }
      const colLetter = columnIndexToLabel(col - 1);
      let sheetPrefix = "";
      if (sheetName !== void 0 && sheetName !== null) {
        sheetPrefix = `${sheetName}!`;
      }
      const r1c1ColSegment = col == 0 ? "C" : `C[${col}]`;
      const r1c1RowSegment = row == 0 ? "R" : `R[${row}]`;
      if (AbsStyle.FullyRelative == abs2) {
        return useA1Style ? `${sheetPrefix}${colLetter}${row}` : `${sheetPrefix}${r1c1RowSegment}${r1c1ColSegment}`;
      } else if (AbsStyle.RowRelativeColAbsolute == abs2) {
        return useA1Style ? `${sheetPrefix}$${colLetter}${row}` : `${sheetPrefix}${r1c1RowSegment}C${col}`;
      } else if (AbsStyle.RowAbsoluteColRelative == abs2) {
        return useA1Style ? `${sheetPrefix}${colLetter}$${row}` : `${sheetPrefix}R${row}${r1c1ColSegment}`;
      }
      return useA1Style ? `${sheetPrefix}$${colLetter}$${row}` : `${sheetPrefix}R${row}C${col}`;
    });
  }
}
AddressPlugin.implementedFunctions = {
  "ADDRESS": {
    method: "address",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      defaultValue: 1,
      minValue: 1,
      maxValue: 4,
      emptyAsDefault: true
    }, {
      argumentType: FunctionArgumentType.BOOLEAN,
      optionalArg: true,
      defaultValue: true,
      emptyAsDefault: true
    }, {
      argumentType: FunctionArgumentType.STRING,
      optionalArg: true
    }]
  }
};
class ArrayPlugin extends FunctionPlugin {
  arrayformula(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ARRAYFORMULA"), (value) => value);
  }
  arrayformulaArraySize(ast, state) {
    if (ast.args.length !== 1) {
      return ArraySize.error();
    }
    const metadata = this.metadata("ARRAYFORMULA");
    const subChecks = ast.args.map((arg) => {
      var _a2;
      return this.arraySizeForAst(arg, new InterpreterState(state.formulaAddress, state.arraysFlag || ((_a2 = metadata === null || metadata === void 0 ? void 0 : metadata.enableArrayArithmeticForArguments) !== null && _a2 !== void 0 ? _a2 : false)));
    });
    return subChecks[0];
  }
  arrayconstrain(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ARRAY_CONSTRAIN"), (range, numRows, numCols) => {
      numRows = Math.min(numRows, range.height());
      numCols = Math.min(numCols, range.width());
      const data = range.data;
      const ret = [];
      for (let i2 = 0; i2 < numRows; i2++) {
        ret.push(data[i2].slice(0, numCols));
      }
      return SimpleRangeValue.onlyValues(ret);
    });
  }
  arrayconstrainArraySize(ast, state) {
    if (ast.args.length !== 3) {
      return ArraySize.error();
    }
    const metadata = this.metadata("ARRAY_CONSTRAIN");
    const subChecks = ast.args.map((arg) => {
      var _a2;
      return this.arraySizeForAst(arg, new InterpreterState(state.formulaAddress, state.arraysFlag || ((_a2 = metadata === null || metadata === void 0 ? void 0 : metadata.enableArrayArithmeticForArguments) !== null && _a2 !== void 0 ? _a2 : false)));
    });
    let {
      height,
      width
    } = subChecks[0];
    if (ast.args[1].type === AstNodeType.NUMBER) {
      height = Math.min(height, ast.args[1].value);
    }
    if (ast.args[2].type === AstNodeType.NUMBER) {
      width = Math.min(width, ast.args[2].value);
    }
    if (height < 1 || width < 1 || !Number.isInteger(height) || !Number.isInteger(width)) {
      return ArraySize.error();
    }
    return new ArraySize(width, height);
  }
  filter(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FILTER"), (rangeVals, ...rangeFilters) => {
      for (const filter2 of rangeFilters) {
        if (rangeVals.width() !== filter2.width() || rangeVals.height() !== filter2.height()) {
          return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
        }
      }
      if (rangeVals.width() > 1 && rangeVals.height() > 1) {
        return new CellError(ErrorType.NA, ErrorMessage.WrongDimension);
      }
      const vals = rangeVals.data;
      const ret = [];
      for (let i2 = 0; i2 < rangeVals.height(); i2++) {
        const row = [];
        for (let j2 = 0; j2 < rangeVals.width(); j2++) {
          let ok = true;
          for (const filter2 of rangeFilters) {
            const val = coerceScalarToBoolean(filter2.data[i2][j2]);
            if (val !== true) {
              ok = false;
              break;
            }
          }
          if (ok) {
            row.push(vals[i2][j2]);
          }
        }
        if (row.length > 0) {
          ret.push(row);
        }
      }
      if (ret.length > 0) {
        return SimpleRangeValue.onlyValues(ret);
      } else {
        return new CellError(ErrorType.NA, ErrorMessage.EmptyRange);
      }
    });
  }
  filterArraySize(ast, state) {
    if (ast.args.length <= 1) {
      return ArraySize.error();
    }
    const metadata = this.metadata("FILTER");
    const subChecks = ast.args.map((arg) => {
      var _a2;
      return this.arraySizeForAst(arg, new InterpreterState(state.formulaAddress, state.arraysFlag || ((_a2 = metadata === null || metadata === void 0 ? void 0 : metadata.enableArrayArithmeticForArguments) !== null && _a2 !== void 0 ? _a2 : false)));
    });
    const width = Math.max(...subChecks.map((val) => val.width));
    const height = Math.max(...subChecks.map((val) => val.height));
    return new ArraySize(width, height);
  }
}
ArrayPlugin.implementedFunctions = {
  "ARRAYFORMULA": {
    method: "arrayformula",
    sizeOfResultArrayMethod: "arrayformulaArraySize",
    enableArrayArithmeticForArguments: true,
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }]
  },
  "ARRAY_CONSTRAIN": {
    method: "arrayconstrain",
    sizeOfResultArrayMethod: "arrayconstrainArraySize",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1
    }],
    vectorizationForbidden: true
  },
  "FILTER": {
    method: "filter",
    sizeOfResultArrayMethod: "filterArraySize",
    enableArrayArithmeticForArguments: true,
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }],
    repeatLastArgs: 1
  }
};
class AbsPlugin extends FunctionPlugin {
  abs(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ABS"), Math.abs);
  }
}
AbsPlugin.implementedFunctions = {
  "ABS": {
    method: "abs",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
const MAX_48BIT_INTEGER = 281474976710655;
const SHIFT_MIN_POSITIONS = -53;
const SHIFT_MAX_POSITIONS = 53;
class BitShiftPlugin extends FunctionPlugin {
  bitlshift(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BITLSHIFT"), shiftLeft);
  }
  bitrshift(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BITRSHIFT"), shiftRight);
  }
}
BitShiftPlugin.implementedFunctions = {
  "BITLSHIFT": {
    method: "bitlshift",
    parameters: [{
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: SHIFT_MIN_POSITIONS,
      maxValue: SHIFT_MAX_POSITIONS
    }]
  },
  "BITRSHIFT": {
    method: "bitrshift",
    parameters: [{
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: SHIFT_MIN_POSITIONS,
      maxValue: SHIFT_MAX_POSITIONS
    }]
  }
};
function shiftLeft(value, positions) {
  if (positions < 0) {
    return shiftRight(value, -positions);
  } else {
    return validate(value * Math.pow(2, positions));
  }
}
function shiftRight(value, positions) {
  if (positions < 0) {
    return shiftLeft(value, -positions);
  } else {
    return validate(Math.floor(value / Math.pow(2, positions)));
  }
}
function validate(result) {
  if (result > MAX_48BIT_INTEGER) {
    return new CellError(ErrorType.NUM, ErrorMessage.BitshiftLong);
  } else {
    return result;
  }
}
class BitwiseLogicOperationsPlugin extends FunctionPlugin {
  bitand(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BITAND"), (left, right) => left & right);
  }
  bitor(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BITOR"), (left, right) => left | right);
  }
  bitxor(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BITXOR"), (left, right) => left ^ right);
  }
}
BitwiseLogicOperationsPlugin.implementedFunctions = {
  "BITAND": {
    method: "bitand",
    parameters: [{
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }]
  },
  "BITOR": {
    method: "bitor",
    parameters: [{
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }]
  },
  "BITXOR": {
    method: "bitxor",
    parameters: [{
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }]
  }
};
class BooleanPlugin extends FunctionPlugin {
  /**
   * Corresponds to TRUE()
   *
   * Returns the logical true
   *
   * @param ast
   * @param state
   */
  literalTrue(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TRUE"), () => true);
  }
  /**
   * Corresponds to FALSE()
   *
   * Returns the logical false
   *
   * @param ast
   * @param state
   */
  literalFalse(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FALSE"), () => false);
  }
  /**
   * Corresponds to IF(expression, value_if_true, value_if_false)
   *
   * Returns value specified as second argument if expression is true and third argument if expression is false
   *
   * @param ast
   * @param state
   */
  conditionalIf(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IF"), (condition, arg2, arg3) => {
      return condition ? arg2 : arg3;
    });
  }
  /**
   * Implementation for the IFS function. Returns the value that corresponds to the first true condition.
   *
   * @param ast
   * @param state
   */
  ifs(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IFS"), (...args) => {
      for (let idx = 0; idx < args.length; idx += 2) {
        if (args[idx]) {
          return args[idx + 1];
        }
      }
      return new CellError(ErrorType.NA, ErrorMessage.NoConditionMet);
    });
  }
  /**
   * Corresponds to AND(expression1, [expression2, ...])
   *
   * Returns true if all of the provided arguments are logically true, and false if any of it is logically false
   *
   * @param ast
   * @param state
   */
  and(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("AND"), (...args) => args.filter((arg) => arg !== void 0).every((arg) => !!arg));
  }
  /**
   * Corresponds to OR(expression1, [expression2, ...])
   *
   * Returns true if any of the provided arguments are logically true, and false otherwise
   *
   * @param ast
   * @param state
   */
  or(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("OR"), (...args) => args.filter((arg) => arg !== void 0).some((arg) => arg));
  }
  not(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NOT"), (arg) => !arg);
  }
  xor(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("XOR"), (...args) => {
      let cnt = 0;
      args.filter((arg) => arg !== void 0).forEach((arg) => {
        if (arg) {
          cnt++;
        }
      });
      return cnt % 2 === 1;
    });
  }
  switch(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SWITCH"), (selector, ...args) => {
      const n2 = args.length;
      let i2 = 0;
      for (; i2 + 1 < n2; i2 += 2) {
        if (args[i2] instanceof CellError) {
          continue;
        }
        if (this.arithmeticHelper.eq(selector, args[i2])) {
          return args[i2 + 1];
        }
      }
      if (i2 < n2) {
        return args[i2];
      } else {
        return new CellError(ErrorType.NA, ErrorMessage.NoDefault);
      }
    });
  }
  iferror(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IFERROR"), (arg1, arg2) => {
      if (arg1 instanceof CellError) {
        return arg2;
      } else {
        return arg1;
      }
    });
  }
  ifna(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IFNA"), (arg1, arg2) => {
      if (arg1 instanceof CellError && arg1.type === ErrorType.NA) {
        return arg2;
      } else {
        return arg1;
      }
    });
  }
  choose(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHOOSE"), (selector, ...args) => {
      if (selector > args.length) {
        return new CellError(ErrorType.NUM, ErrorMessage.Selector);
      }
      return args[selector - 1];
    });
  }
}
BooleanPlugin.implementedFunctions = {
  "TRUE": {
    method: "literalTrue",
    parameters: []
  },
  "FALSE": {
    method: "literalFalse",
    parameters: []
  },
  "IF": {
    method: "conditionalIf",
    parameters: [{
      argumentType: FunctionArgumentType.BOOLEAN
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      defaultValue: false,
      passSubtype: true
    }]
  },
  "IFS": {
    method: "ifs",
    parameters: [{
      argumentType: FunctionArgumentType.BOOLEAN
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }],
    repeatLastArgs: 2
  },
  "AND": {
    method: "and",
    parameters: [{
      argumentType: FunctionArgumentType.BOOLEAN
    }],
    repeatLastArgs: 1,
    expandRanges: true
  },
  "OR": {
    method: "or",
    parameters: [{
      argumentType: FunctionArgumentType.BOOLEAN
    }],
    repeatLastArgs: 1,
    expandRanges: true
  },
  "XOR": {
    method: "xor",
    parameters: [{
      argumentType: FunctionArgumentType.BOOLEAN
    }],
    repeatLastArgs: 1,
    expandRanges: true
  },
  "NOT": {
    method: "not",
    parameters: [{
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "SWITCH": {
    method: "switch",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }],
    repeatLastArgs: 1
  },
  "IFERROR": {
    method: "iferror",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }]
  },
  "IFNA": {
    method: "ifna",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }]
  },
  "CHOOSE": {
    method: "choose",
    parameters: [{
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.SCALAR,
      passSubtype: true
    }],
    repeatLastArgs: 1
  }
};
class CharPlugin extends FunctionPlugin {
  char(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHAR"), (value) => {
      if (value < 1 || value >= 256) {
        return new CellError(ErrorType.VALUE, ErrorMessage.CharacterCodeBounds);
      }
      return String.fromCharCode(Math.trunc(value));
    });
  }
  unichar(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHAR"), (value) => {
      if (value < 1 || value >= 1114112) {
        return new CellError(ErrorType.VALUE, ErrorMessage.CharacterCodeBounds);
      }
      return String.fromCodePoint(Math.trunc(value));
    });
  }
}
CharPlugin.implementedFunctions = {
  "CHAR": {
    method: "char",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "UNICHAR": {
    method: "unichar",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
class CodePlugin extends FunctionPlugin {
  code(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CODE"), (value) => {
      if (value.length === 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.EmptyString);
      }
      return value.charCodeAt(0);
    });
  }
  unicode(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("UNICODE"), (value) => {
      var _a2;
      return (_a2 = value.codePointAt(0)) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.VALUE, ErrorMessage.EmptyString);
    });
  }
}
CodePlugin.implementedFunctions = {
  "CODE": {
    method: "code",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "UNICODE": {
    method: "unicode",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  }
};
class CountBlankPlugin extends FunctionPlugin {
  countblank(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COUNTBLANK"), (...args) => {
      let counter = 0;
      args.forEach((arg) => {
        if (arg === EmptyValue) {
          counter++;
        }
      });
      return counter;
    });
  }
}
CountBlankPlugin.implementedFunctions = {
  "COUNTBLANK": {
    method: "countblank",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }],
    repeatLastArgs: 1,
    expandRanges: true
  }
};
class CountUniquePlugin extends FunctionPlugin {
  /**
   * Corresponds to COUNTUNIQUE(Number1, Number2, ...).
   *
   * Returns number of unique numbers from arguments
   *
   * @param ast
   * @param state
   */
  countunique(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COUNTUNIQUE"), (...args) => {
      const valuesSet = /* @__PURE__ */ new Set();
      const errorsSet = /* @__PURE__ */ new Set();
      for (const scalarValue of args) {
        if (scalarValue instanceof CellError) {
          errorsSet.add(scalarValue.type);
        } else if (scalarValue !== "") {
          valuesSet.add(scalarValue);
        }
      }
      return valuesSet.size + errorsSet.size;
    });
  }
}
CountUniquePlugin.implementedFunctions = {
  "COUNTUNIQUE": {
    method: "countunique",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }],
    repeatLastArgs: 1,
    expandRanges: true
  }
};
const databaseFunctionParameters = [{
  argumentType: FunctionArgumentType.RANGE
}, {
  argumentType: FunctionArgumentType.SCALAR
}, {
  argumentType: FunctionArgumentType.RANGE
}];
class DatabasePlugin extends FunctionPlugin {
  /**
   * Resolves field index and criteria, then delegates to the callback with the parsed database arguments.
   * Shared boilerplate for all 12 database functions.
   */
  withDatabaseArgs(ast, state, functionName2, callback) {
    return this.runFunction(ast.args, state, this.metadata(functionName2), (database, field, criteria) => {
      const fieldIndex = this.resolveFieldIndex(database, field);
      if (fieldIndex instanceof CellError) {
        return fieldIndex;
      }
      const criteriaRows = this.buildDatabaseCriteria(database, criteria);
      if (criteriaRows instanceof CellError) {
        return criteriaRows;
      }
      return callback(database.data, fieldIndex, criteriaRows);
    });
  }
  /**
   * Counts cells containing numbers in the specified field of a database range,
   * for rows that match all criteria.
   *
   * DCOUNT(database, field, criteria)
   */
  dcount(ast, state) {
    return this.withDatabaseArgs(ast, state, "DCOUNT", (dbData, fieldIndex, criteriaRows) => {
      let count = 0;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          const cellValue = dbData[rowIdx][fieldIndex];
          if (cellValue instanceof CellError) {
            return cellValue;
          }
          if (isExtendedNumber(cellValue)) {
            count++;
          }
        }
      }
      return count;
    });
  }
  /**
   * Counts all non-blank cells in the specified field of a database range,
   * for rows that match all criteria.
   *
   * DCOUNTA(database, field, criteria)
   */
  dcounta(ast, state) {
    return this.withDatabaseArgs(ast, state, "DCOUNTA", (dbData, fieldIndex, criteriaRows) => {
      let count = 0;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          const cellValue = dbData[rowIdx][fieldIndex];
          if (cellValue instanceof CellError) {
            return cellValue;
          }
          if (cellValue !== EmptyValue && cellValue !== void 0 && cellValue !== null) {
            count++;
          }
        }
      }
      return count;
    });
  }
  /**
   * Returns the product of numeric values in the specified field of a database range,
   * for rows that match all criteria.
   *
   * DPRODUCT(database, field, criteria)
   */
  dproduct(ast, state) {
    return this.withDatabaseArgs(ast, state, "DPRODUCT", (dbData, fieldIndex, criteriaRows) => {
      let product2 = 1;
      let hasNumeric = false;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          const cellValue = dbData[rowIdx][fieldIndex];
          if (cellValue instanceof CellError) {
            return cellValue;
          }
          if (isExtendedNumber(cellValue)) {
            product2 *= getRawValue(cellValue);
            hasNumeric = true;
          }
        }
      }
      return hasNumeric ? product2 : 0;
    });
  }
  /**
   * Returns the sum of numeric values in the specified field of a database range,
   * for rows that match all criteria.
   *
   * DSUM(database, field, criteria)
   */
  dsum(ast, state) {
    return this.withDatabaseArgs(ast, state, "DSUM", (dbData, fieldIndex, criteriaRows) => {
      let sum2 = 0;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          const cellValue = dbData[rowIdx][fieldIndex];
          if (cellValue instanceof CellError) {
            return cellValue;
          }
          if (isExtendedNumber(cellValue)) {
            sum2 += getRawValue(cellValue);
          }
        }
      }
      return sum2;
    });
  }
  /**
   * Returns the average of numeric values in the specified field of a database range,
   * for rows that match all criteria.
   * Returns #DIV/0! when no numeric values are found.
   *
   * DAVERAGE(database, field, criteria)
   */
  daverage(ast, state) {
    return this.withDatabaseArgs(ast, state, "DAVERAGE", (dbData, fieldIndex, criteriaRows) => {
      let sum2 = 0;
      let count = 0;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          const cellValue = dbData[rowIdx][fieldIndex];
          if (cellValue instanceof CellError) {
            return cellValue;
          }
          if (isExtendedNumber(cellValue)) {
            sum2 += getRawValue(cellValue);
            count++;
          }
        }
      }
      if (count === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return sum2 / count;
    });
  }
  /**
   * Returns a single value from the specified field of a database range,
   * for the row that matches all criteria.
   * Returns #VALUE! if no rows match, #NUM! if more than one row matches.
   *
   * DGET(database, field, criteria)
   */
  dget(ast, state) {
    return this.withDatabaseArgs(ast, state, "DGET", (dbData, fieldIndex, criteriaRows) => {
      let matchedValue;
      let matchCount = 0;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          matchCount++;
          if (matchCount > 1) {
            return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
          }
          matchedValue = dbData[rowIdx][fieldIndex];
        }
      }
      if (matchCount === 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
      }
      if (matchedValue instanceof CellError) {
        return matchedValue;
      }
      return matchedValue === EmptyValue || matchedValue === void 0 || matchedValue === null ? 0 : matchedValue;
    });
  }
  /**
   * Returns the maximum numeric value in the specified field of a database range,
   * for rows that match all criteria.
   * Returns 0 when no numeric values are found (Excel behavior).
   *
   * DMAX(database, field, criteria)
   */
  dmax(ast, state) {
    return this.withDatabaseArgs(ast, state, "DMAX", (dbData, fieldIndex, criteriaRows) => {
      let max2 = -Infinity;
      let hasNumeric = false;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          const cellValue = dbData[rowIdx][fieldIndex];
          if (cellValue instanceof CellError) {
            return cellValue;
          }
          if (isExtendedNumber(cellValue)) {
            const numValue = getRawValue(cellValue);
            if (numValue > max2) {
              max2 = numValue;
            }
            hasNumeric = true;
          }
        }
      }
      return hasNumeric ? max2 : 0;
    });
  }
  /**
   * Returns the minimum numeric value in the specified field of a database range,
   * for rows that match all criteria.
   * Returns 0 when no numeric values are found (Excel behavior).
   *
   * DMIN(database, field, criteria)
   */
  dmin(ast, state) {
    return this.withDatabaseArgs(ast, state, "DMIN", (dbData, fieldIndex, criteriaRows) => {
      let min2 = Infinity;
      let hasNumeric = false;
      for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
        if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
          const cellValue = dbData[rowIdx][fieldIndex];
          if (cellValue instanceof CellError) {
            return cellValue;
          }
          if (isExtendedNumber(cellValue)) {
            const numValue = getRawValue(cellValue);
            if (numValue < min2) {
              min2 = numValue;
            }
            hasNumeric = true;
          }
        }
      }
      return hasNumeric ? min2 : 0;
    });
  }
  /**
   * Returns the sample standard deviation of numeric values in the specified field
   * of a database range, for rows that match all criteria.
   * Returns #DIV/0! when fewer than 2 numeric values are found.
   *
   * DSTDEV(database, field, criteria)
   */
  dstdev(ast, state) {
    return this.withDatabaseArgs(ast, state, "DSTDEV", (dbData, fieldIndex, criteriaRows) => {
      const values2 = this.collectNumericValues(dbData, fieldIndex, criteriaRows);
      if (values2 instanceof CellError) {
        return values2;
      }
      if (values2.length <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const mean2 = values2.reduce((a2, b2) => a2 + b2, 0) / values2.length;
      const variance2 = values2.reduce((sum2, v2) => sum2 + Math.pow(v2 - mean2, 2), 0) / (values2.length - 1);
      return Math.sqrt(variance2);
    });
  }
  /**
   * Returns the population standard deviation of numeric values in the specified field
   * of a database range, for rows that match all criteria.
   * Returns #DIV/0! when no numeric values are found.
   * Returns 0 when exactly one numeric value is found.
   *
   * DSTDEVP(database, field, criteria)
   */
  dstdevp(ast, state) {
    return this.withDatabaseArgs(ast, state, "DSTDEVP", (dbData, fieldIndex, criteriaRows) => {
      const values2 = this.collectNumericValues(dbData, fieldIndex, criteriaRows);
      if (values2 instanceof CellError) {
        return values2;
      }
      if (values2.length === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const mean2 = values2.reduce((a2, b2) => a2 + b2, 0) / values2.length;
      const variance2 = values2.reduce((sum2, v2) => sum2 + Math.pow(v2 - mean2, 2), 0) / values2.length;
      return Math.sqrt(variance2);
    });
  }
  /**
   * Returns the sample variance of numeric values in the specified field
   * of a database range, for rows that match all criteria.
   * Returns #DIV/0! when fewer than 2 numeric values are found.
   *
   * DVAR(database, field, criteria)
   */
  dvar(ast, state) {
    return this.withDatabaseArgs(ast, state, "DVAR", (dbData, fieldIndex, criteriaRows) => {
      const values2 = this.collectNumericValues(dbData, fieldIndex, criteriaRows);
      if (values2 instanceof CellError) {
        return values2;
      }
      if (values2.length <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const mean2 = values2.reduce((a2, b2) => a2 + b2, 0) / values2.length;
      return values2.reduce((sum2, v2) => sum2 + Math.pow(v2 - mean2, 2), 0) / (values2.length - 1);
    });
  }
  /**
   * Returns the population variance of numeric values in the specified field
   * of a database range, for rows that match all criteria.
   * Returns #DIV/0! when no numeric values are found.
   * Returns 0 when exactly one numeric value is found.
   *
   * DVARP(database, field, criteria)
   */
  dvarp(ast, state) {
    return this.withDatabaseArgs(ast, state, "DVARP", (dbData, fieldIndex, criteriaRows) => {
      const values2 = this.collectNumericValues(dbData, fieldIndex, criteriaRows);
      if (values2 instanceof CellError) {
        return values2;
      }
      if (values2.length === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const mean2 = values2.reduce((a2, b2) => a2 + b2, 0) / values2.length;
      return values2.reduce((sum2, v2) => sum2 + Math.pow(v2 - mean2, 2), 0) / values2.length;
    });
  }
  /**
   * Collects numeric values from the specified field column of matching database rows.
   *
   * @param dbData - Full database data including header row.
   * @param fieldIndex - 0-based column index of the target field.
   * @param criteriaRows - Parsed criteria rows.
   * @returns Array of numeric values from matching rows, or the first CellError
   *   encountered in a matching field cell (propagated per Excel semantics).
   */
  collectNumericValues(dbData, fieldIndex, criteriaRows) {
    const values2 = [];
    for (let rowIdx = 1; rowIdx < dbData.length; rowIdx++) {
      if (this.rowMatchesCriteria(dbData[rowIdx], criteriaRows)) {
        const cellValue = dbData[rowIdx][fieldIndex];
        if (cellValue instanceof CellError) {
          return cellValue;
        }
        if (isExtendedNumber(cellValue)) {
          values2.push(getRawValue(cellValue));
        }
      }
    }
    return values2;
  }
  /**
   * Resolves the field argument to a 0-based column index within the database range.
   *
   * @param database - The database range (first row = headers).
   * @param field - A string (header name, case-insensitive) or number (1-based column index).
   *   Booleans are coerced to numbers (TRUE → 1, FALSE → 0) per Excel convention.
   * @returns 0-based column index, or CellError if field is invalid.
   */
  resolveFieldIndex(database, field) {
    if (field instanceof CellError) {
      return field;
    }
    const headers = database.data[0];
    if (typeof field === "string") {
      const lowerField = field.toLowerCase();
      for (let i2 = 0; i2 < headers.length; i2++) {
        const header = headers[i2];
        if (typeof header === "string" && header.toLowerCase() === lowerField) {
          return i2;
        }
      }
      return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
    }
    const numericField = typeof field === "boolean" ? Number(field) : field;
    if (isExtendedNumber(numericField)) {
      const index2 = Math.trunc(getRawValue(numericField));
      if (!Number.isFinite(index2) || index2 < 1 || index2 > headers.length) {
        return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
      }
      return index2 - 1;
    }
    return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
  }
  /**
   * Parses the criteria range into an array of criteria rows.
   * Each row is a list of AND-ed conditions. Rows are OR-ed together.
   *
   * @param database - The database range (first row = headers).
   * @param criteria - The criteria range (first row = header labels, subsequent rows = conditions).
   * @returns Array of criteria rows, or CellError if a criterion cannot be parsed.
   */
  buildDatabaseCriteria(database, criteria) {
    var _a2;
    const dbHeaders = database.data[0];
    const criteriaData = criteria.data;
    const criteriaHeaders = criteriaData[0];
    for (const criteriaHeader of criteriaHeaders) {
      if (criteriaHeader instanceof CellError) {
        return criteriaHeader;
      }
    }
    const criteriaColumnMapping = criteriaHeaders.map((criteriaHeader) => {
      if (typeof criteriaHeader !== "string") {
        return -1;
      }
      const lowerHeader = criteriaHeader.toLowerCase();
      return dbHeaders.findIndex((dbHeader) => typeof dbHeader === "string" && dbHeader.toLowerCase() === lowerHeader);
    });
    const rows = [];
    for (let rowIdx = 1; rowIdx < criteriaData.length; rowIdx++) {
      const row = [];
      for (let colIdx = 0; colIdx < criteriaHeaders.length; colIdx++) {
        const dbColIndex = criteriaColumnMapping[colIdx];
        if (dbColIndex === -1) {
          continue;
        }
        const criterionValue = (_a2 = criteriaData[rowIdx]) === null || _a2 === void 0 ? void 0 : _a2[colIdx];
        if (criterionValue === EmptyValue || criterionValue === void 0 || criterionValue === null) {
          continue;
        }
        if (criterionValue instanceof CellError) {
          return criterionValue;
        }
        const rawCriterionValue = isExtendedNumber(criterionValue) ? getRawValue(criterionValue) : criterionValue;
        const criterionPackage = this.interpreter.criterionBuilder.fromCellValue(rawCriterionValue, this.arithmeticHelper);
        if (criterionPackage === void 0) {
          return new CellError(ErrorType.VALUE, ErrorMessage.BadCriterion);
        }
        row.push({
          columnIndex: dbColIndex,
          lambda: criterionPackage.lambda
        });
      }
      rows.push(row);
    }
    return rows;
  }
  /**
   * Tests whether a database data row matches any of the criteria rows (OR logic).
   * Within each criteria row, all conditions must match (AND logic).
   *
   * @param dataRow - A single row of data from the database (excluding the header row).
   * @param criteriaRows - Parsed criteria rows.
   * @returns true if the row qualifies, false otherwise.
   */
  rowMatchesCriteria(dataRow, criteriaRows) {
    if (criteriaRows.length === 0) {
      return false;
    }
    return criteriaRows.some((criteriaRow) => {
      if (criteriaRow.length === 0) {
        return true;
      }
      return criteriaRow.every((entry) => {
        const cellValue = dataRow[entry.columnIndex];
        const rawValue = isExtendedNumber(cellValue) ? getRawValue(cellValue) : cellValue;
        return entry.lambda(rawValue);
      });
    });
  }
}
DatabasePlugin.implementedFunctions = {
  "DCOUNT": {
    method: "dcount",
    parameters: databaseFunctionParameters
  },
  "DCOUNTA": {
    method: "dcounta",
    parameters: databaseFunctionParameters
  },
  "DPRODUCT": {
    method: "dproduct",
    parameters: databaseFunctionParameters
  },
  "DSTDEV": {
    method: "dstdev",
    parameters: databaseFunctionParameters
  },
  "DSTDEVP": {
    method: "dstdevp",
    parameters: databaseFunctionParameters
  },
  "DSUM": {
    method: "dsum",
    parameters: databaseFunctionParameters
  },
  "DVAR": {
    method: "dvar",
    parameters: databaseFunctionParameters
  },
  "DVARP": {
    method: "dvarp",
    parameters: databaseFunctionParameters
  },
  "DAVERAGE": {
    method: "daverage",
    parameters: databaseFunctionParameters
  },
  "DGET": {
    method: "dget",
    parameters: databaseFunctionParameters
  },
  "DMAX": {
    method: "dmax",
    parameters: databaseFunctionParameters
  },
  "DMIN": {
    method: "dmin",
    parameters: databaseFunctionParameters
  }
};
class DateTimePlugin extends FunctionPlugin {
  constructor() {
    super(...arguments);
    this.isoweeknumCore = (day) => {
      const absoluteDay = Math.floor(this.dateTimeHelper.relativeNumberToAbsoluteNumber(day));
      const date = this.dateTimeHelper.numberToSimpleDate(day);
      const yearStart = this.dateTimeHelper.dateToNumber({
        year: date.year,
        month: 1,
        day: 1
      });
      const yearStartAbsolute = this.dateTimeHelper.relativeNumberToAbsoluteNumber(yearStart);
      const firstThursdayAbs = yearStartAbsolute + ((4 - yearStartAbsolute) % 7 + 7) % 7;
      const ret = Math.floor((absoluteDay - 1) / 7) - Math.floor((firstThursdayAbs - 1) / 7) + 1;
      if (ret === 0) {
        return this.isoweeknumCore(day - 7) + 1;
      }
      return ret;
    };
    this.days360Core = (startDate, endDate, mode) => {
      const start = this.dateTimeHelper.numberToSimpleDate(startDate);
      const end = this.dateTimeHelper.numberToSimpleDate(endDate);
      let nStart, nEnd;
      if (mode) {
        nStart = toBasisEU(start);
        nEnd = toBasisEU(end);
      } else {
        [nStart, nEnd] = this.dateTimeHelper.toBasisUS(start, end);
      }
      return 360 * (nEnd.year - nStart.year) + 30 * (nEnd.month - nStart.month) + nEnd.day - nStart.day;
    };
  }
  /**
   * Corresponds to DATE(year, month, day)
   *
   * Converts a provided year, month and day into date
   *
   * @param ast
   * @param state
   */
  date(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DATE"), (year, month, day) => {
      const d2 = Math.trunc(day);
      let m2 = Math.trunc(month);
      let y2 = Math.trunc(year);
      if (y2 < this.dateTimeHelper.getEpochYearZero()) {
        y2 += this.dateTimeHelper.getEpochYearZero();
      }
      const delta = Math.floor((m2 - 1) / 12);
      y2 += delta;
      m2 -= delta * 12;
      const date = {
        year: y2,
        month: m2,
        day: 1
      };
      if (this.dateTimeHelper.isValidDate(date)) {
        let ret = this.dateTimeHelper.dateToNumber(date) + (d2 - 1);
        ret = this.dateTimeHelper.getWithinBounds(ret);
        if (ret === void 0) {
          return new CellError(ErrorType.NUM, ErrorMessage.DateBounds);
        }
        return ret;
      }
      return new CellError(ErrorType.VALUE, ErrorMessage.InvalidDate);
    });
  }
  time(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TIME"), (h2, m2, s2) => {
      const ret = timeToNumber({
        hours: Math.trunc(h2),
        minutes: Math.trunc(m2),
        seconds: Math.trunc(s2)
      });
      if (ret < 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NegativeTime);
      }
      return ret % 1;
    });
  }
  /**
   * Implementation for the EOMONTH function
   */
  eomonth(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("EOMONTH"), (dateNumber, numberOfMonthsToShift) => {
      const date = this.dateTimeHelper.numberToSimpleDate(dateNumber);
      let ret = this.dateTimeHelper.dateToNumber(this.dateTimeHelper.endOfMonth(offsetMonth(date, numberOfMonthsToShift)));
      ret = this.dateTimeHelper.getWithinBounds(ret);
      if (ret === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.DateBounds);
      }
      return ret;
    });
  }
  day(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DAY"), (dateNumber) => this.dateTimeHelper.numberToSimpleDate(dateNumber).day);
  }
  days(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DAYS"), (endDate, startDate) => Math.trunc(endDate) - Math.trunc(startDate));
  }
  /**
   * Corresponds to MONTH(date)
   *
   * Returns the month of the year specified by a given date
   *
   * @param ast
   * @param state
   */
  month(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MONTH"), (dateNumber) => this.dateTimeHelper.numberToSimpleDate(dateNumber).month);
  }
  /**
   * Corresponds to YEAR(date)
   *
   * Returns the year specified by a given date
   *
   * @param ast
   * @param state
   */
  year(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("YEAR"), (dateNumber) => this.dateTimeHelper.numberToSimpleDate(dateNumber).year);
  }
  hour(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HOUR"), (timeNumber) => numberToSimpleTime(roundToNearestSecond(timeNumber) % 1).hours);
  }
  minute(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MINUTE"), (timeNumber) => numberToSimpleTime(roundToNearestSecond(timeNumber) % 1).minutes);
  }
  second(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SECOND"), (timeNumber) => numberToSimpleTime(roundToNearestSecond(timeNumber) % 1).seconds);
  }
  /**
   * Corresponds to TEXT(number, format)
   *
   * Tries to convert number to specified date format.
   *
   * @param ast
   * @param state
   */
  text(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TEXT"), (numberRepresentation, formatArg) => format(numberRepresentation, formatArg, this.config, this.dateTimeHelper));
  }
  weekday(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("WEEKDAY"), (day, type) => {
      const absoluteDay = Math.floor(this.dateTimeHelper.relativeNumberToAbsoluteNumber(day));
      if (type === 3) {
        return (absoluteDay - 1) % 7;
      }
      const offset = weekdayOffsets.get(type);
      if (offset === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.BadMode);
      }
      return (absoluteDay - offset) % 7 + 1;
    });
  }
  weeknum(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("WEEKNUM"), (day, type) => {
      const absoluteDay = Math.floor(this.dateTimeHelper.relativeNumberToAbsoluteNumber(day));
      const date = this.dateTimeHelper.numberToSimpleDate(day);
      const yearStart = this.dateTimeHelper.dateToNumber({
        year: date.year,
        month: 1,
        day: 1
      });
      const yearStartAbsolute = this.dateTimeHelper.relativeNumberToAbsoluteNumber(yearStart);
      if (type === 21) {
        return this.isoweeknumCore(day);
      }
      const offset = weekdayOffsets.get(type);
      if (offset === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.BadMode);
      }
      return Math.floor((absoluteDay - offset) / 7) - Math.floor((yearStartAbsolute - offset) / 7) + 1;
    });
  }
  isoweeknum(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISOWEEKNUM"), this.isoweeknumCore);
  }
  datevalue(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DATEVALUE"), (date) => {
      const {
        dateTime
      } = this.dateTimeHelper.parseDateTimeFromConfigFormats(date);
      if (dateTime === void 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.IncorrectDateTime);
      }
      if (!instanceOfSimpleDate(dateTime)) {
        return 0;
      }
      return (instanceOfSimpleTime(dateTime) ? Math.trunc(timeToNumber(dateTime)) : 0) + this.dateTimeHelper.dateToNumber(dateTime);
    });
  }
  timevalue(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TIMEVALUE"), (date) => {
      const dateNumber = this.dateTimeHelper.dateStringToDateNumber(date);
      if (dateNumber === void 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.IncorrectDateTime);
      }
      return getRawValue(dateNumber) % 1;
    });
  }
  now(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NOW"), () => {
      const now = new Date(Date.now());
      return timeToNumber({
        hours: now.getHours(),
        minutes: now.getMinutes(),
        seconds: now.getSeconds()
      }) + this.dateTimeHelper.dateToNumber({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate()
      });
    });
  }
  today(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TODAY"), () => {
      const now = new Date(Date.now());
      return this.dateTimeHelper.dateToNumber({
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate()
      });
    });
  }
  edate(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("EDATE"), (dateNumber, delta) => {
      const date = this.dateTimeHelper.numberToSimpleDate(dateNumber);
      const newDate = truncateDayInMonth(offsetMonth(date, delta));
      let ret = this.dateTimeHelper.dateToNumber(newDate);
      ret = this.dateTimeHelper.getWithinBounds(ret);
      if (ret === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.DateBounds);
      }
      return ret;
    });
  }
  datedif(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DATEDIF"), (startDate, endDate, unit) => {
      if (startDate > endDate) {
        return new CellError(ErrorType.NUM, ErrorMessage.StartEndDate);
      }
      if (unit === "D") {
        return Math.floor(endDate) - Math.floor(startDate);
      }
      const start = this.dateTimeHelper.numberToSimpleDate(startDate);
      const end = this.dateTimeHelper.numberToSimpleDate(endDate);
      switch (unit) {
        case "M":
          return (end.year - start.year) * 12 + (end.month - start.month) - (end.day < start.day ? 1 : 0);
        case "YM":
          return (12 + (end.month - start.month) - (end.day < start.day ? 1 : 0)) % 12;
        case "Y":
          if (end.month > start.month || end.month === start.month && end.day >= start.day) {
            return end.year - start.year;
          } else {
            return end.year - start.year - 1;
          }
        case "MD":
          if (end.day >= start.day) {
            return end.day - start.day;
          } else {
            const m2 = end.month === 1 ? 12 : end.month - 1;
            const y2 = end.month === 1 ? end.year - 1 : end.year;
            return this.dateTimeHelper.daysInMonth(y2, m2) + end.day - start.day;
          }
        case "YD":
          if (end.month > start.month || end.month === start.month && end.day >= start.day) {
            return Math.floor(endDate) - this.dateTimeHelper.dateToNumber({
              year: end.year,
              month: start.month,
              day: start.day
            });
          } else {
            return Math.floor(endDate) - Math.floor(startDate) - 365 * (end.year - start.year - 1) - this.dateTimeHelper.leapYearsCount(end.year - 1) + this.dateTimeHelper.leapYearsCount(start.year);
          }
        default:
          return new CellError(ErrorType.NUM, ErrorMessage.BadMode);
      }
    });
  }
  days360(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DAYS360"), this.days360Core);
  }
  yearfrac(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("YEARFRAC"), (startDate, endDate, mode) => {
      startDate = Math.trunc(startDate);
      endDate = Math.trunc(endDate);
      if (startDate > endDate) {
        [startDate, endDate] = [endDate, startDate];
      }
      switch (mode) {
        case 0:
          return this.days360Core(startDate, endDate, false) / 360;
        case 1:
          return (endDate - startDate) / this.dateTimeHelper.yearLengthForBasis(this.dateTimeHelper.numberToSimpleDate(startDate), this.dateTimeHelper.numberToSimpleDate(endDate));
        case 2:
          return (endDate - startDate) / 360;
        case 3:
          return (endDate - startDate) / 365;
        case 4:
          return this.days360Core(startDate, endDate, true) / 360;
      }
      throw new Error("Should not be reachable.");
    });
  }
  interval(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("INTERVAL"), (arg) => {
      arg = Math.trunc(arg);
      const second = arg % 60;
      arg = Math.trunc(arg / 60);
      const minute = arg % 60;
      arg = Math.trunc(arg / 60);
      const hour = arg % 24;
      arg = Math.trunc(arg / 24);
      const day = arg % 30;
      arg = Math.trunc(arg / 30);
      const month = arg % 12;
      const year = Math.trunc(arg / 12);
      return "P" + (year > 0 ? `${year}Y` : "") + (month > 0 ? `${month}M` : "") + (day > 0 ? `${day}D` : "") + "T" + (hour > 0 ? `${hour}H` : "") + (minute > 0 ? `${minute}M` : "") + (second > 0 ? `${second}S` : "");
    });
  }
  networkdays(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NETWORKDAYS"), (start, end, holidays) => this.networkdayscore(start, end, 1, holidays));
  }
  networkdaysintl(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NETWORKDAYS.INTL"), (start, end, weekend, holidays) => this.networkdayscore(start, end, weekend, holidays));
  }
  workday(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("WORKDAY"), (start, end, holidays) => this.workdaycore(start, end, 1, holidays));
  }
  workdayintl(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("WORKDAY.INTL"), (start, end, weekend, holidays) => this.workdaycore(start, end, weekend, holidays));
  }
  networkdayscore(start, end, weekend, holidays) {
    start = Math.trunc(start);
    end = Math.trunc(end);
    let multiplier = 1;
    if (start > end) {
      [start, end] = [end, start];
      multiplier = -1;
    }
    const weekendPattern = computeWeekendPattern(weekend);
    if (weekendPattern instanceof CellError) {
      return weekendPattern;
    }
    const filteredHolidays = this.simpleRangeToFilteredHolidays(weekendPattern, holidays);
    if (filteredHolidays instanceof CellError) {
      return filteredHolidays;
    }
    return multiplier * this.countWorkdays(start, end, weekendPattern, filteredHolidays);
  }
  workdaycore(start, delta, weekend, holidays) {
    start = Math.trunc(start);
    delta = Math.trunc(delta);
    const weekendPattern = computeWeekendPattern(weekend);
    if (weekendPattern instanceof CellError) {
      return weekendPattern;
    }
    const filteredHolidays = this.simpleRangeToFilteredHolidays(weekendPattern, holidays);
    if (filteredHolidays instanceof CellError) {
      return filteredHolidays;
    }
    if (delta > 0) {
      let upper = 1;
      while (this.countWorkdays(start + 1, start + upper, weekendPattern, filteredHolidays) < delta) {
        upper *= 2;
      }
      let lower = 1;
      while (lower + 1 < upper) {
        const mid = Math.trunc((lower + upper) / 2);
        if (this.countWorkdays(start + 1, start + mid, weekendPattern, filteredHolidays) < delta) {
          lower = mid;
        } else {
          upper = mid;
        }
      }
      return start + upper;
    } else if (delta < 0) {
      delta *= -1;
      let upper = 1;
      while (this.countWorkdays(start - upper, start - 1, weekendPattern, filteredHolidays) < delta) {
        upper *= 2;
      }
      let lower = 1;
      while (lower + 1 < upper) {
        const mid = Math.trunc((lower + upper) / 2);
        if (this.countWorkdays(start - mid, start - 1, weekendPattern, filteredHolidays) < delta) {
          lower = mid;
        } else {
          upper = mid;
        }
      }
      return start - upper;
    } else {
      return start;
    }
  }
  countWorkdays(start, end, weekendPattern, sortedHolidays) {
    const absoluteEnd = Math.floor(this.dateTimeHelper.relativeNumberToAbsoluteNumber(end));
    const absoluteStart = Math.floor(this.dateTimeHelper.relativeNumberToAbsoluteNumber(start));
    let ans = 0;
    for (let i2 = 0; i2 < 7; i2++) {
      if (weekendPattern.charAt(i2) === "0") {
        ans += Math.floor((absoluteEnd + 6 - i2) / 7);
        ans -= Math.floor((absoluteStart - 1 + 6 - i2) / 7);
      }
    }
    ans -= lowerBound(end + 1, sortedHolidays) - lowerBound(start, sortedHolidays);
    return ans;
  }
  simpleRangeToFilteredHolidays(weekendPattern, holidays) {
    var _a2;
    const holidaysArr = (_a2 = holidays === null || holidays === void 0 ? void 0 : holidays.valuesFromTopLeftCorner()) !== null && _a2 !== void 0 ? _a2 : [];
    for (const val of holidaysArr) {
      if (val instanceof CellError) {
        return val;
      }
    }
    const processedHolidays = [];
    for (const val of holidaysArr) {
      if (val === EmptyValue) {
        continue;
      }
      if (isExtendedNumber(val)) {
        processedHolidays.push(Math.trunc(getRawValue(val)));
      } else {
        return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
      }
    }
    return [...new Set(processedHolidays)].sort((a2, b2) => a2 - b2).filter((arg) => {
      const val = this.dateTimeHelper.relativeNumberToAbsoluteNumber(arg);
      const i2 = (val - 1) % 7;
      return weekendPattern.charAt(i2) === "0";
    });
  }
}
DateTimePlugin.implementedFunctions = {
  "DATE": {
    method: "date",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    returnNumberType: NumberType.NUMBER_DATE
  },
  "TIME": {
    method: "time",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    returnNumberType: NumberType.NUMBER_TIME
  },
  "MONTH": {
    method: "month",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "YEAR": {
    method: "year",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "HOUR": {
    method: "hour",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "MINUTE": {
    method: "minute",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "SECOND": {
    method: "second",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "TEXT": {
    method: "text",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "EOMONTH": {
    method: "eomonth",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    returnNumberType: NumberType.NUMBER_DATE
  },
  "DAY": {
    method: "day",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "DAYS": {
    method: "days",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "WEEKDAY": {
    method: "weekday",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "WEEKNUM": {
    method: "weeknum",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "ISOWEEKNUM": {
    method: "isoweeknum",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "DATEVALUE": {
    method: "datevalue",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }],
    returnNumberType: NumberType.NUMBER_DATE
  },
  "TIMEVALUE": {
    method: "timevalue",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }],
    returnNumberType: NumberType.NUMBER_TIME
  },
  "NOW": {
    method: "now",
    parameters: [],
    isVolatile: true,
    returnNumberType: NumberType.NUMBER_DATETIME
  },
  "TODAY": {
    method: "today",
    parameters: [],
    isVolatile: true,
    returnNumberType: NumberType.NUMBER_DATE
  },
  "EDATE": {
    method: "edate",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    returnNumberType: NumberType.NUMBER_DATE
  },
  "DAYS360": {
    method: "days360",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN,
      defaultValue: false
    }]
  },
  "DATEDIF": {
    method: "datedif",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "YEARFRAC": {
    method: "yearfrac",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      defaultValue: 0,
      minValue: 0,
      maxValue: 4
    }]
  },
  "INTERVAL": {
    method: "interval",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "NETWORKDAYS": {
    method: "networkdays",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.RANGE,
      optionalArg: true
    }]
  },
  "NETWORKDAYS.INTL": {
    method: "networkdaysintl",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      defaultValue: 1
    }, {
      argumentType: FunctionArgumentType.RANGE,
      optionalArg: true
    }]
  },
  "WORKDAY": {
    method: "workday",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.RANGE,
      optionalArg: true
    }]
  },
  "WORKDAY.INTL": {
    method: "workdayintl",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      defaultValue: 1
    }, {
      argumentType: FunctionArgumentType.RANGE,
      optionalArg: true
    }]
  }
};
function lowerBound(val, sortedArray) {
  if (sortedArray.length === 0) {
    return 0;
  }
  if (val <= sortedArray[0]) {
    return 0;
  }
  if (sortedArray[sortedArray.length - 1] < val) {
    return sortedArray.length;
  }
  let lower = 0;
  let upper = sortedArray.length - 1;
  while (lower + 1 < upper) {
    const mid = Math.floor((upper + lower) / 2);
    if (sortedArray[mid] >= val) {
      upper = mid;
    } else {
      lower = mid;
    }
  }
  return upper;
}
function computeWeekendPattern(weekend) {
  var _a2;
  if (typeof weekend !== "number" && typeof weekend !== "string") {
    return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
  }
  if (typeof weekend === "string") {
    if (weekend.length !== 7 || !/^(0|1)*$/.test(weekend) || weekend === "1111111") {
      return new CellError(ErrorType.NUM, ErrorMessage.WeekendString);
    } else {
      return weekend;
    }
  } else {
    return (_a2 = workdayPatterns.get(weekend)) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.NUM, ErrorMessage.BadMode);
  }
}
const weekdayOffsets = /* @__PURE__ */ new Map([[1, 0], [2, 1], [11, 1], [12, 2], [13, 3], [14, 4], [15, 5], [16, 6], [17, 0]]);
const workdayPatterns = /* @__PURE__ */ new Map([[1, "0000011"], [2, "1000001"], [3, "1100000"], [4, "0110000"], [5, "0011000"], [6, "0001100"], [7, "0000110"], [11, "0000001"], [12, "1000000"], [13, "0100000"], [14, "0010000"], [15, "0001000"], [16, "0000100"], [17, "0000010"]]);
class DegreesPlugin extends FunctionPlugin {
  degrees(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DEGREES"), (arg) => arg * (180 / Math.PI));
  }
}
DegreesPlugin.implementedFunctions = {
  "DEGREES": {
    method: "degrees",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
class DeltaPlugin extends FunctionPlugin {
  delta(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DELTA"), (left, right) => left === right ? 1 : 0);
  }
}
DeltaPlugin.implementedFunctions = {
  "DELTA": {
    method: "delta",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }]
  }
};
class ExpPlugin extends FunctionPlugin {
  /**
   * Corresponds to EXP(value)
   *
   * Calculates the exponent for basis e
   *
   * @param ast
   * @param state
   */
  exp(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("EXP"), Math.exp);
  }
}
ExpPlugin.implementedFunctions = {
  "EXP": {
    method: "exp",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
class FinancialPlugin extends FunctionPlugin {
  pmt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PMT"), pmtCore);
  }
  ipmt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IPMT"), ipmtCore);
  }
  ppmt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PPMT"), ppmtCore);
  }
  fv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FV"), fvCore);
  }
  cumipmt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CUMIPMT"), (rate, periods, value, start, end, type) => {
      if (start > end) {
        return new CellError(ErrorType.NUM, ErrorMessage.EndStartPeriod);
      }
      let acc = 0;
      for (let i2 = start; i2 <= end; i2++) {
        acc += ipmtCore(rate, i2, periods, value, 0, type);
      }
      return acc;
    });
  }
  cumprinc(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CUMPRINC"), (rate, periods, value, start, end, type) => {
      if (start > end) {
        return new CellError(ErrorType.NUM, ErrorMessage.EndStartPeriod);
      }
      let acc = 0;
      for (let i2 = start; i2 <= end; i2++) {
        acc += ppmtCore(rate, i2, periods, value, 0, type);
      }
      return acc;
    });
  }
  db(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DB"), (cost, salvage, life, period, month) => {
      if (month === 12 && period > life || period > life + 1) {
        return new CellError(ErrorType.NUM, ErrorMessage.PeriodLong);
      }
      if (salvage >= cost) {
        return 0;
      }
      const rate = Math.round((1 - Math.pow(salvage / cost, 1 / life)) * 1e3) / 1e3;
      const initial = cost * rate * month / 12;
      if (period === 1) {
        return initial;
      }
      let total = initial;
      for (let i2 = 0; i2 < period - 2; i2++) {
        total += (cost - total) * rate;
      }
      if (period === life + 1) {
        return (cost - total) * rate * (12 - month) / 12;
      }
      return (cost - total) * rate;
    });
  }
  ddb(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DDB"), (cost, salvage, life, period, factor) => {
      if (period > life) {
        return new CellError(ErrorType.NUM);
      }
      let rate = factor / life;
      let oldValue;
      if (rate >= 1) {
        rate = 1;
        if (period === 1) {
          oldValue = cost;
        } else {
          oldValue = 0;
        }
      } else {
        oldValue = cost * Math.pow(1 - rate, period - 1);
      }
      const newValue = cost * Math.pow(1 - rate, period);
      return Math.max(oldValue - Math.max(salvage, newValue), 0);
    });
  }
  dollarde(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DOLLARDE"), (dollar, fraction2) => {
      if (fraction2 < 1) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      fraction2 = Math.trunc(fraction2);
      while (fraction2 > 10) {
        fraction2 /= 10;
      }
      return Math.trunc(dollar) + (dollar - Math.trunc(dollar)) * 10 / fraction2;
    });
  }
  dollarfr(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DOLLARFR"), (dollar, fraction2) => {
      if (fraction2 < 1) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      fraction2 = Math.trunc(fraction2);
      while (fraction2 > 10) {
        fraction2 /= 10;
      }
      return Math.trunc(dollar) + (dollar - Math.trunc(dollar)) * fraction2 / 10;
    });
  }
  effect(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("EFFECT"), (rate, periods) => {
      periods = Math.trunc(periods);
      return Math.pow(1 + rate / periods, periods) - 1;
    });
  }
  ispmt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISPMT"), (rate, period, periods, value) => {
      if (periods === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return value * rate * (period / periods - 1);
    });
  }
  nominal(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NOMINAL"), (rate, periods) => {
      periods = Math.trunc(periods);
      return (Math.pow(rate + 1, 1 / periods) - 1) * periods;
    });
  }
  nper(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NPER"), (rate, payment, present, future, type) => {
      if (rate === 0) {
        if (payment === 0) {
          return new CellError(ErrorType.DIV_BY_ZERO);
        }
        return (-present - future) / payment;
      }
      if (type) {
        payment *= 1 + rate;
      }
      return Math.log((payment - future * rate) / (present * rate + payment)) / Math.log(1 + rate);
    });
  }
  rate(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("RATE"), (periods, payment, present, future, type, guess) => {
      if (guess <= -1) {
        return new CellError(ErrorType.VALUE);
      }
      const epsMax = 1e-7;
      const iterMax = 50;
      let rate = guess;
      type = type ? 1 : 0;
      for (let i2 = 0; i2 < iterMax; i2++) {
        if (rate <= -1) {
          return new CellError(ErrorType.NUM);
        }
        let y2;
        if (Math.abs(rate) < epsMax) {
          y2 = present * (1 + periods * rate) + payment * (1 + rate * type) * periods + future;
        } else {
          const f2 = Math.pow(1 + rate, periods);
          y2 = present * f2 + payment * (1 / rate + type) * (f2 - 1) + future;
        }
        if (Math.abs(y2) < epsMax) {
          return rate;
        }
        let dy;
        if (Math.abs(rate) < epsMax) {
          dy = present * periods + payment * type * periods;
        } else {
          const f2 = Math.pow(1 + rate, periods);
          const df = periods * Math.pow(1 + rate, periods - 1);
          dy = present * df + payment * (1 / rate + type) * df + payment * (-1 / (rate * rate)) * (f2 - 1);
        }
        rate -= y2 / dy;
      }
      return new CellError(ErrorType.NUM);
    });
  }
  pv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PV"), (rate, periods, payment, future, type) => {
      type = type ? 1 : 0;
      if (rate === -1) {
        if (periods === 0) {
          return new CellError(ErrorType.NUM);
        } else {
          return new CellError(ErrorType.DIV_BY_ZERO);
        }
      }
      if (rate === 0) {
        return -payment * periods - future;
      } else {
        return ((1 - Math.pow(1 + rate, periods)) * payment * (1 + rate * type) / rate - future) / Math.pow(1 + rate, periods);
      }
    });
  }
  rri(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("RRI"), (periods, present, future) => {
      if (present === 0 || future < 0 && present > 0 || future > 0 && present < 0) {
        return new CellError(ErrorType.NUM);
      }
      return Math.pow(future / present, 1 / periods) - 1;
    });
  }
  sln(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SLN"), (cost, salvage, life) => {
      if (life === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return (cost - salvage) / life;
    });
  }
  syd(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SYD"), (cost, salvage, life, period) => {
      if (period > life) {
        return new CellError(ErrorType.NUM);
      }
      return (cost - salvage) * (life - period + 1) * 2 / (life * (life + 1));
    });
  }
  tbilleq(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TBILLEQ"), (settlement, maturity, discount) => {
      settlement = Math.round(settlement);
      maturity = Math.round(maturity);
      if (settlement >= maturity) {
        return new CellError(ErrorType.NUM);
      }
      const startDate = this.dateTimeHelper.numberToSimpleDate(settlement);
      const endDate = this.dateTimeHelper.numberToSimpleDate(maturity);
      if (endDate.year > startDate.year + 1 || endDate.year === startDate.year + 1 && (endDate.month > startDate.month || endDate.month === startDate.month && endDate.day > startDate.day)) {
        return new CellError(ErrorType.NUM);
      }
      const denom = 360 - discount * (maturity - settlement);
      if (denom === 0) {
        return 0;
      }
      if (denom < 0) {
        return new CellError(ErrorType.NUM);
      }
      return 365 * discount / denom;
    });
  }
  tbillprice(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TBILLPRICE"), (settlement, maturity, discount) => {
      settlement = Math.round(settlement);
      maturity = Math.round(maturity);
      if (settlement >= maturity) {
        return new CellError(ErrorType.NUM);
      }
      const startDate = this.dateTimeHelper.numberToSimpleDate(settlement);
      const endDate = this.dateTimeHelper.numberToSimpleDate(maturity);
      if (endDate.year > startDate.year + 1 || endDate.year === startDate.year + 1 && (endDate.month > startDate.month || endDate.month === startDate.month && endDate.day > startDate.day)) {
        return new CellError(ErrorType.NUM);
      }
      const denom = 360 - discount * (maturity - settlement);
      if (denom === 0) {
        return 0;
      }
      if (denom < 0) {
        return new CellError(ErrorType.NUM);
      }
      return 100 * (1 - discount * (maturity - settlement) / 360);
    });
  }
  tbillyield(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TBILLYIELD"), (settlement, maturity, price) => {
      settlement = Math.round(settlement);
      maturity = Math.round(maturity);
      if (settlement >= maturity) {
        return new CellError(ErrorType.NUM);
      }
      const startDate = this.dateTimeHelper.numberToSimpleDate(settlement);
      const endDate = this.dateTimeHelper.numberToSimpleDate(maturity);
      if (endDate.year > startDate.year + 1 || endDate.year === startDate.year + 1 && (endDate.month > startDate.month || endDate.month === startDate.month && endDate.day > startDate.day)) {
        return new CellError(ErrorType.NUM);
      }
      return (100 - price) * 360 / (price * (maturity - settlement));
    });
  }
  fvschedule(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FVSCHEDULE"), (value, ratios) => {
      const vals = ratios.valuesFromTopLeftCorner();
      for (const val of vals) {
        if (val instanceof CellError) {
          return val;
        }
      }
      for (const val of vals) {
        if (isExtendedNumber(val)) {
          value *= 1 + getRawValue(val);
        } else if (val !== EmptyValue) {
          return new CellError(ErrorType.VALUE, ErrorMessage.NumberExpected);
        }
      }
      return value;
    });
  }
  npv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NPV"), (rate, ...args) => {
      const coerced = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      return npvCore(rate, coerced);
    });
  }
  mirr(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MIRR"), (range, frate, rrate) => {
      const vals = this.arithmeticHelper.manyToExactNumbers(range.valuesFromTopLeftCorner());
      if (vals instanceof CellError) {
        return vals;
      }
      let posFlag = false;
      let negFlag = false;
      const posValues = [];
      const negValues = [];
      for (const val of vals) {
        if (val > 0) {
          posFlag = true;
          posValues.push(val);
          negValues.push(0);
        } else if (val < 0) {
          negFlag = true;
          negValues.push(val);
          posValues.push(0);
        } else {
          negValues.push(0);
          posValues.push(0);
        }
      }
      if (!posFlag || !negFlag) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const n2 = vals.length;
      const nom = npvCore(rrate, posValues);
      if (nom instanceof CellError) {
        return nom;
      }
      const denom = npvCore(frate, negValues);
      if (denom instanceof CellError) {
        return denom;
      }
      return Math.pow(-nom * Math.pow(1 + rrate, n2) / denom / (1 + frate), 1 / (n2 - 1)) - 1;
    });
  }
  pduration(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PDURATION"), (rate, pv, fv) => (Math.log(fv) - Math.log(pv)) / Math.log(1 + rate));
  }
  xnpv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("XNPV"), (rate, values2, dates) => {
      const valArr = values2.valuesFromTopLeftCorner();
      for (const val of valArr) {
        if (typeof val !== "number") {
          return new CellError(ErrorType.VALUE, ErrorMessage.NumberExpected);
        }
      }
      const valArrNum = valArr;
      const dateArr = dates.valuesFromTopLeftCorner();
      for (const date of dateArr) {
        if (typeof date !== "number") {
          return new CellError(ErrorType.VALUE, ErrorMessage.NumberExpected);
        }
      }
      const dateArrNum = dateArr;
      if (dateArrNum.length !== valArrNum.length) {
        return new CellError(ErrorType.NUM, ErrorMessage.EqualLength);
      }
      const n2 = dateArrNum.length;
      let ret = 0;
      if (dateArrNum[0] < 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
      }
      for (let i2 = 0; i2 < n2; i2++) {
        dateArrNum[i2] = Math.floor(dateArrNum[i2]);
        if (dateArrNum[i2] < dateArrNum[0]) {
          return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
        }
        ret += valArrNum[i2] / Math.pow(1 + rate, (dateArrNum[i2] - dateArrNum[0]) / 365);
      }
      return ret;
    });
  }
  /**
   * Calculates the internal rate of return for a series of cash flows.
   * @param {ProcedureAst} ast - The AST node representing the function call.
   * @param {InterpreterState} state - The interpreter state.
   * @returns {InterpreterValue} The internal rate of return.
   */
  irr(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IRR"), (range, guess) => {
      if (guess <= -1) {
        return new CellError(ErrorType.VALUE);
      }
      const vals = this.arithmeticHelper.manyToExactNumbers(range.valuesFromTopLeftCorner());
      if (vals instanceof CellError) {
        return vals;
      }
      const hasPositive = vals.some((val) => val > 0);
      const hasNegative = vals.some((val) => val < 0);
      if (!hasPositive || !hasNegative) {
        return new CellError(ErrorType.NUM);
      }
      return irrCore(vals, guess);
    });
  }
}
FinancialPlugin.implementedFunctions = {
  "PMT": {
    method: "pmt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "IPMT": {
    method: "ipmt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "PPMT": {
    method: "ppmt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "FV": {
    method: "fv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "CUMIPMT": {
    method: "cumipmt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0,
      maxValue: 1
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "CUMPRINC": {
    method: "cumprinc",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0,
      maxValue: 1
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "DB": {
    method: "db",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1,
      maxValue: 12,
      defaultValue: 12
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "DDB": {
    method: "ddb",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      defaultValue: 2
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "DOLLARDE": {
    method: "dollarde",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "DOLLARFR": {
    method: "dollarfr",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "EFFECT": {
    method: "effect",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  },
  "ISPMT": {
    method: "ispmt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "NOMINAL": {
    method: "nominal",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  },
  "NPER": {
    method: "nper",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }]
  },
  "PV": {
    method: "pv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "RATE": {
    method: "rate",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0.1
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  },
  "RRI": {
    method: "rri",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  },
  "SLN": {
    method: "sln",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "SYD": {
    method: "syd",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "TBILLEQ": {
    method: "tbilleq",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  },
  "TBILLPRICE": {
    method: "tbillprice",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "TBILLYIELD": {
    method: "tbillyield",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  },
  "FVSCHEDULE": {
    method: "fvschedule",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.RANGE
    }],
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "NPV": {
    method: "npv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1,
    returnNumberType: NumberType.NUMBER_CURRENCY
  },
  "MIRR": {
    method: "mirr",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  },
  "PDURATION": {
    method: "pduration",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }]
  },
  "XNPV": {
    method: "xnpv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: -1
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "IRR": {
    method: "irr",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0.1
    }],
    returnNumberType: NumberType.NUMBER_PERCENT
  }
};
function pmtCore(rate, periods, present, future, type) {
  if (rate === 0) {
    return (-present - future) / periods;
  } else {
    const term = Math.pow(1 + rate, periods);
    return (future * rate + present * rate * term) * (type ? 1 / (1 + rate) : 1) / (1 - term);
  }
}
function ipmtCore(rate, period, periods, present, future, type) {
  const payment = pmtCore(rate, periods, present, future, type);
  if (period === 1) {
    return rate * (type ? 0 : -present);
  } else {
    return rate * (type ? fvCore(rate, period - 2, payment, present, type) - payment : fvCore(rate, period - 1, payment, present, type));
  }
}
function fvCore(rate, periods, payment, value, type) {
  if (rate === 0) {
    return -value - payment * periods;
  } else {
    const term = Math.pow(1 + rate, periods);
    return payment * (type ? 1 + rate : 1) * (1 - term) / rate - value * term;
  }
}
function ppmtCore(rate, period, periods, present, future, type) {
  return pmtCore(rate, periods, present, future, type) - ipmtCore(rate, period, periods, present, future, type);
}
function npvCore(rate, args) {
  let acc = 0;
  for (let i2 = args.length - 1; i2 >= 0; i2--) {
    acc += args[i2];
    if (rate === -1) {
      if (acc === 0) {
        continue;
      } else {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
    }
    acc /= 1 + rate;
  }
  return acc;
}
function irrCore(values2, guess) {
  const epsMax = 1e-10;
  const iterMax = 50;
  let rate = guess;
  for (let iter = 0; iter < iterMax; iter++) {
    let npv = 0;
    let dnpv = 0;
    for (let i2 = 0; i2 < values2.length; i2++) {
      const factor = Math.pow(1 + rate, i2);
      if (!isFinite(factor) || factor === 0) {
        return new CellError(ErrorType.NUM);
      }
      npv += values2[i2] / factor;
      if (i2 > 0) {
        dnpv -= i2 * values2[i2] / (factor * (1 + rate));
      }
    }
    if (Math.abs(npv) < epsMax) {
      return rate;
    }
    if (Math.abs(dnpv) < epsMax) {
      return new CellError(ErrorType.NUM);
    }
    let newRate = rate - npv / dnpv;
    if (!isFinite(newRate)) {
      return new CellError(ErrorType.NUM);
    }
    if (newRate <= -1) {
      newRate = (rate - 1) / 2;
    }
    if (Math.abs(newRate - rate) < epsMax) {
      return newRate;
    }
    rate = newRate;
  }
  return new CellError(ErrorType.NUM);
}
class FormulaTextPlugin extends FunctionPlugin {
  /**
   * Corresponds to FORMULATEXT(value)
   *
   * Returns a formula in a given cell as a string.
   *
   * @param ast
   * @param state
   */
  formulatext(ast, state) {
    return this.runFunctionWithReferenceArgument(ast.args, state, this.metadata("FORMULATEXT"), () => new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber), (cellReference) => {
      var _a2;
      return (_a2 = this.serialization.getCellFormula(cellReference)) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.NA, ErrorMessage.Formula);
    });
  }
}
FormulaTextPlugin.implementedFunctions = {
  "FORMULATEXT": {
    method: "formulatext",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR
    }],
    doesNotNeedArgumentsToBeComputed: true,
    isDependentOnSheetStructureChange: true,
    vectorizationForbidden: true
  }
};
class HyperlinkPlugin extends FunctionPlugin {
  hyperlink(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HYPERLINK"), (url, linkLabel) => {
      ast.hyperlink = url;
      return linkLabel !== null && linkLabel !== void 0 ? linkLabel : url;
    });
  }
}
HyperlinkPlugin.implementedFunctions = {
  "HYPERLINK": {
    method: "hyperlink",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.STRING,
      optionalArg: true
    }]
  }
};
class InformationPlugin extends FunctionPlugin {
  /**
   * Corresponds to ISBINARY(value)
   *
   * Returns true if provided value is a valid binary number
   *
   * @param ast
   * @param state
   */
  isbinary(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISBINARY"), (arg) => /^[01]{1,10}$/.test(arg));
  }
  /**
   * Corresponds to ISERR(value)
   *
   * Returns true if provided value is an error except #N/A!
   *
   * @param ast
   * @param state
   */
  iserr(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISERR"), (arg) => arg instanceof CellError && arg.type !== ErrorType.NA);
  }
  /**
   * Corresponds to ISERROR(value)
   *
   * Checks whether provided value is an error
   *
   * @param ast
   * @param state
   */
  iserror(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISERROR"), (arg) => arg instanceof CellError);
  }
  /**
   * Corresponds to ISFORMULA(value)
   *
   * Checks whether referenced cell is a formula
   *
   * @param ast
   * @param state
   */
  isformula(ast, state) {
    return this.runFunctionWithReferenceArgument(ast.args, state, this.metadata("ISFORMULA"), () => new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber), (reference) => {
      const vertex = this.dependencyGraph.addressMapping.getCell(reference);
      return vertex instanceof FormulaVertex;
    });
  }
  /**
   * Corresponds to ISBLANK(value)
   *
   * Checks whether provided cell reference is empty
   *
   * @param ast
   * @param state
   */
  isblank(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISBLANK"), (arg) => arg === EmptyValue);
  }
  /**
   * Corresponds to ISNA(value)
   *
   * Returns true if provided value is #N/A! error
   *
   * @param ast
   * @param state
   */
  isna(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISNA"), (arg) => arg instanceof CellError && arg.type == ErrorType.NA);
  }
  /**
   * Corresponds to ISNUMBER(value)
   *
   * Checks whether provided cell reference is a number
   *
   * @param ast
   * @param state
   */
  isnumber(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISNUMBER"), isExtendedNumber);
  }
  /**
   * Corresponds to ISLOGICAL(value)
   *
   * Checks whether provided cell reference is of logical type
   *
   * @param ast
   * @param state
   */
  islogical(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISLOGICAL"), (arg) => typeof arg === "boolean");
  }
  /**
   * Corresponds to ISREF(value)
   *
   * Returns true if provided value is #REF! error
   *
   * @param ast
   * @param state
   */
  isref(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISREF"), (arg) => arg instanceof CellError && (arg.type == ErrorType.REF || arg.type == ErrorType.CYCLE));
  }
  /**
   * Corresponds to ISTEXT(value)
   *
   * Checks whether provided cell reference is of logical type
   *
   * @param ast
   * @param state
   */
  istext(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISTEXT"), (arg) => typeof arg === "string");
  }
  /**
   * Corresponds to ISNONTEXT(value)
   *
   * Checks whether provided cell reference is of logical type
   *
   * @param ast
   * @param state
   */
  isnontext(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISNONTEXT"), (arg) => !(typeof arg === "string"));
  }
  /**
   * Corresponds to COLUMN(reference)
   *
   * Returns column number of a reference or a formula cell if reference not provided
   *
   * @param ast
   * @param state
   */
  column(ast, state) {
    return this.runFunctionWithReferenceArgument(ast.args, state, this.metadata("COLUMN"), () => state.formulaAddress.col + 1, (reference) => reference.col + 1);
  }
  /**
   * Corresponds to COLUMNS(range)
   *
   * Returns number of columns in provided range of cells
   *
   * @param ast
   * @param state
   */
  columns(ast, state) {
    if (ast.args.length !== 1) {
      return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
    }
    if (ast.args.some((astIt) => astIt.type === AstNodeType.EMPTY)) {
      return new CellError(ErrorType.NUM, ErrorMessage.EmptyArg);
    }
    let argAst = ast.args[0];
    while (argAst.type === AstNodeType.PARENTHESIS) {
      argAst = argAst.expression;
    }
    if (argAst.type === AstNodeType.CELL_RANGE || argAst.type === AstNodeType.COLUMN_RANGE) {
      return argAst.end.col - argAst.start.col + 1;
    } else if (argAst.type === AstNodeType.CELL_REFERENCE) {
      return 1;
    } else if (argAst.type === AstNodeType.ROW_RANGE) {
      return this.config.maxColumns;
    } else {
      const val = this.evaluateAst(argAst, state);
      if (val instanceof SimpleRangeValue) {
        return val.width();
      } else if (val instanceof CellError) {
        return val;
      } else {
        return 1;
      }
    }
  }
  /**
   * Corresponds to ROW(reference)
   *
   * Returns row number of a reference or a formula cell if reference not provided
   *
   * @param ast
   * @param state
   */
  row(ast, state) {
    return this.runFunctionWithReferenceArgument(ast.args, state, this.metadata("ROW"), () => state.formulaAddress.row + 1, (reference) => reference.row + 1);
  }
  /**
   * Corresponds to ROWS(range)
   *
   * Returns number of rows in provided range of cells
   *
   * @param ast
   * @param state
   */
  rows(ast, state) {
    if (ast.args.length !== 1) {
      return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
    }
    if (ast.args.some((astIt) => astIt.type === AstNodeType.EMPTY)) {
      return new CellError(ErrorType.NUM, ErrorMessage.EmptyArg);
    }
    let argAst = ast.args[0];
    while (argAst.type === AstNodeType.PARENTHESIS) {
      argAst = argAst.expression;
    }
    if (argAst.type === AstNodeType.CELL_RANGE || argAst.type === AstNodeType.ROW_RANGE) {
      return argAst.end.row - argAst.start.row + 1;
    } else if (argAst.type === AstNodeType.CELL_REFERENCE) {
      return 1;
    } else if (argAst.type === AstNodeType.COLUMN_RANGE) {
      return this.config.maxRows;
    } else {
      const val = this.evaluateAst(argAst, state);
      if (val instanceof SimpleRangeValue) {
        return val.height();
      } else if (val instanceof CellError) {
        return val;
      } else {
        return 1;
      }
    }
  }
  /**
   * Corresponds to INDEX
   *
   * Returns specific position in 2d array.
   *
   * @param ast
   * @param state
   */
  index(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("INDEX"), (rangeValue, row, col) => {
      var _a2, _b, _c, _d, _e2, _f;
      if (col < 1 || row < 1) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      if (col > rangeValue.width() || row > rangeValue.height()) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      return (_f = (_c = (_b = (_a2 = rangeValue === null || rangeValue === void 0 ? void 0 : rangeValue.data) === null || _a2 === void 0 ? void 0 : _a2[row - 1]) === null || _b === void 0 ? void 0 : _b[col - 1]) !== null && _c !== void 0 ? _c : (_e2 = (_d = rangeValue === null || rangeValue === void 0 ? void 0 : rangeValue.data) === null || _d === void 0 ? void 0 : _d[0]) === null || _e2 === void 0 ? void 0 : _e2[0]) !== null && _f !== void 0 ? _f : new CellError(ErrorType.VALUE, ErrorMessage.CellRangeExpected);
    });
  }
  /**
   * Corresponds to NA()
   *
   * Returns #N/A!
   *
   * @param _ast
   * @param _state
   */
  na(_ast, _state) {
    return new CellError(ErrorType.NA);
  }
  /**
   * Corresponds to SHEET(value)
   *
   * Returns sheet number of a given value or a formula sheet number if no argument is provided
   *
   * @param ast
   * @param state
   */
  sheet(ast, state) {
    return this.runFunctionWithReferenceArgument(ast.args, state, this.metadata("SHEET"), () => state.formulaAddress.sheet + 1, (reference) => reference.sheet + 1, (value) => {
      const sheetNumber = this.dependencyGraph.sheetMapping.getSheetId(value);
      if (sheetNumber !== void 0) {
        return sheetNumber + 1;
      } else {
        return new CellError(ErrorType.NA, ErrorMessage.SheetRef);
      }
    });
  }
  /**
   * Corresponds to SHEETS(value)
   *
   * Returns number of sheet of a given reference or number of all sheets in workbook when no argument is provided.
   * It returns always 1 for a valid reference as 3D references are not supported.
   *
   * @param ast
   * @param state
   */
  sheets(ast, state) {
    return this.runFunctionWithReferenceArgument(
      ast.args,
      state,
      this.metadata("SHEETS"),
      () => this.dependencyGraph.sheetMapping.numberOfSheets(),
      // return number of sheets if no argument
      () => 1,
      // return 1 for valid reference
      () => new CellError(ErrorType.VALUE, ErrorMessage.CellRefExpected)
      // error otherwise
    );
  }
}
InformationPlugin.implementedFunctions = {
  "COLUMN": {
    method: "column",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      optionalArg: true
    }],
    isDependentOnSheetStructureChange: true,
    doesNotNeedArgumentsToBeComputed: true,
    vectorizationForbidden: true
  },
  "COLUMNS": {
    method: "columns",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }],
    isDependentOnSheetStructureChange: true,
    doesNotNeedArgumentsToBeComputed: true,
    vectorizationForbidden: true
  },
  "ISBINARY": {
    method: "isbinary",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "ISERR": {
    method: "iserr",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "ISFORMULA": {
    method: "isformula",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR
    }],
    doesNotNeedArgumentsToBeComputed: true,
    vectorizationForbidden: true
  },
  "ISNA": {
    method: "isna",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "ISREF": {
    method: "isref",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }],
    vectorizationForbidden: true
  },
  "ISERROR": {
    method: "iserror",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "ISBLANK": {
    method: "isblank",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "ISNUMBER": {
    method: "isnumber",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "ISLOGICAL": {
    method: "islogical",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "ISTEXT": {
    method: "istext",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "ISNONTEXT": {
    method: "isnontext",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "INDEX": {
    method: "index",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "NA": {
    method: "na",
    parameters: []
  },
  "ROW": {
    method: "row",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      optionalArg: true
    }],
    isDependentOnSheetStructureChange: true,
    doesNotNeedArgumentsToBeComputed: true,
    vectorizationForbidden: true
  },
  "ROWS": {
    method: "rows",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }],
    isDependentOnSheetStructureChange: true,
    doesNotNeedArgumentsToBeComputed: true,
    vectorizationForbidden: true
  },
  "SHEET": {
    method: "sheet",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }],
    doesNotNeedArgumentsToBeComputed: true,
    vectorizationForbidden: true
  },
  "SHEETS": {
    method: "sheets",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }],
    doesNotNeedArgumentsToBeComputed: true,
    vectorizationForbidden: true
  }
};
class IsEvenPlugin extends FunctionPlugin {
  iseven(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISEVEN"), (val) => val % 2 === 0);
  }
}
IsEvenPlugin.implementedFunctions = {
  "ISEVEN": {
    method: "iseven",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
class IsOddPlugin extends FunctionPlugin {
  isodd(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ISODD"), (val) => val % 2 === 1);
  }
}
IsOddPlugin.implementedFunctions = {
  "ISODD": {
    method: "isodd",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
class LogarithmPlugin extends FunctionPlugin {
  log10(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LOG10"), Math.log10);
  }
  log(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LOG"), (arg, base) => Math.log(arg) / Math.log(base));
  }
  ln(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LN"), Math.log);
  }
}
LogarithmPlugin.implementedFunctions = {
  "LOG10": {
    method: "log10",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "LOG": {
    method: "log",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 10,
      greaterThan: 0
    }]
  },
  "LN": {
    method: "ln",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
const PI = parseFloat(Math.PI.toFixed(14));
class MathConstantsPlugin extends FunctionPlugin {
  pi(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PI"), () => PI);
  }
  sqrtpi(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SQRTPI"), (arg) => Math.sqrt(PI * arg));
  }
}
MathConstantsPlugin.implementedFunctions = {
  "PI": {
    method: "pi",
    parameters: []
  },
  "SQRTPI": {
    method: "sqrtpi",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  }
};
function arraySizeForMultiplication(leftArraySize, rightArraySize) {
  return new ArraySize(rightArraySize.width, leftArraySize.height);
}
function arraySizeForPoolFunction(inputArray, windowSize, stride) {
  return new ArraySize(1 + (inputArray.width - windowSize) / stride, 1 + (inputArray.height - windowSize) / stride);
}
class MatrixPlugin extends FunctionPlugin {
  mmult(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MMULT"), (leftMatrix, rightMatrix) => {
      if (!leftMatrix.hasOnlyNumbers() || !rightMatrix.hasOnlyNumbers()) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NumberRange);
      }
      if (rightMatrix.height() !== leftMatrix.width()) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ArrayDimensions);
      }
      const outputSize = arraySizeForMultiplication(leftMatrix.size, rightMatrix.size);
      const result = this.createKernel(function(a2, b2, width) {
        let sum2 = 0;
        for (let i2 = 0; i2 < width; ++i2) {
          sum2 += a2[this.thread.y][i2] * b2[i2][this.thread.x];
        }
        return sum2;
      }, outputSize)(leftMatrix.rawNumbers(), rightMatrix.rawNumbers(), leftMatrix.width());
      return SimpleRangeValue.onlyNumbers(result);
    });
  }
  mmultArraySize(ast, state) {
    if (ast.args.length !== 2) {
      return ArraySize.error();
    }
    const metadata = this.metadata("MMULT");
    const subChecks = ast.args.map((arg) => {
      var _a2;
      return this.arraySizeForAst(arg, new InterpreterState(state.formulaAddress, state.arraysFlag || ((_a2 = metadata === null || metadata === void 0 ? void 0 : metadata.enableArrayArithmeticForArguments) !== null && _a2 !== void 0 ? _a2 : false)));
    });
    const [left, right] = subChecks;
    return arraySizeForMultiplication(left, right);
  }
  maxpool(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MAXPOOL"), (matrix2, windowSize, stride = windowSize) => {
      if (!matrix2.hasOnlyNumbers()) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NumberRange);
      }
      const outputSize = arraySizeForPoolFunction(matrix2.size, windowSize, stride);
      const result = this.createKernel(function(a2, windowSize2, stride2) {
        const leftCornerX = this.thread.x * stride2;
        const leftCornerY = this.thread.y * stride2;
        let currentMax = a2[leftCornerY][leftCornerX];
        for (let i2 = 0; i2 < windowSize2; i2++) {
          for (let j2 = 0; j2 < windowSize2; j2++) {
            currentMax = Math.max(currentMax, a2[leftCornerY + i2][leftCornerX + j2]);
          }
        }
        return currentMax;
      }, outputSize)(matrix2.rawNumbers(), windowSize, stride);
      return SimpleRangeValue.onlyNumbers(result);
    });
  }
  medianpool(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MEDIANPOOL"), (matrix2, windowSize, stride = windowSize) => {
      if (!matrix2.hasOnlyNumbers()) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NumberRange);
      }
      const outputSize = arraySizeForPoolFunction(matrix2.size, windowSize, stride);
      const result = this.createKernel(function(a2, windowSize2, stride2) {
        const leftCornerX = this.thread.x * stride2;
        const leftCornerY = this.thread.y * stride2;
        let currentMax = a2[leftCornerY][leftCornerX];
        for (let i2 = 0; i2 < windowSize2; i2++) {
          for (let j2 = 0; j2 < windowSize2; j2++) {
            currentMax = Math.max(currentMax, a2[leftCornerY + i2][leftCornerX + j2]);
          }
        }
        let currentMin = a2[leftCornerY][leftCornerX];
        for (let i2 = 0; i2 < windowSize2; i2++) {
          for (let j2 = 0; j2 < windowSize2; j2++) {
            currentMin = Math.min(currentMin, a2[leftCornerY + i2][leftCornerX + j2]);
          }
        }
        const numberOfElements = windowSize2 * windowSize2;
        let leftEnd = currentMin;
        let rightEnd = currentMax;
        let result2 = 42;
        for (let iter = 0; iter < 32; iter++) {
          const medianGuess = (leftEnd + rightEnd) / 2;
          let medianGuessCount = 0;
          for (let i3 = 0; i3 < windowSize2; i3++) {
            for (let j3 = 0; j3 < windowSize2; j3++) {
              if (a2[leftCornerY + i3][leftCornerX + j3] > medianGuess) {
                medianGuessCount++;
              }
            }
          }
          if (windowSize2 % 2 === 0) {
            if (medianGuessCount === numberOfElements / 2) {
              result2 = medianGuess;
              break;
            } else if (medianGuessCount > numberOfElements / 2) {
              leftEnd = medianGuess;
            } else {
              rightEnd = medianGuess;
            }
          } else {
            if (medianGuessCount === (numberOfElements - 1) / 2) {
              result2 = medianGuess;
              break;
            } else if (medianGuessCount > (numberOfElements - 1) / 2) {
              leftEnd = medianGuess;
            } else {
              rightEnd = medianGuess;
            }
          }
        }
        return result2;
      }, outputSize)(matrix2.rawNumbers(), windowSize, stride);
      return SimpleRangeValue.onlyNumbers(result);
    });
  }
  maxpoolArraySize(ast, state) {
    if (ast.args.length < 2 || ast.args.length > 3) {
      return ArraySize.error();
    }
    const metadata = this.metadata("MAXPOOL");
    const subChecks = ast.args.map((arg) => {
      var _a2;
      return this.arraySizeForAst(arg, new InterpreterState(state.formulaAddress, state.arraysFlag || ((_a2 = metadata === null || metadata === void 0 ? void 0 : metadata.enableArrayArithmeticForArguments) !== null && _a2 !== void 0 ? _a2 : false)));
    });
    const array = subChecks[0];
    const windowArg = ast.args[1];
    let window2;
    if (windowArg.type === AstNodeType.NUMBER) {
      window2 = windowArg.value;
    } else {
      window2 = 1;
    }
    let stride = window2;
    if (ast.args.length === 3) {
      const strideArg = ast.args[2];
      if (strideArg.type === AstNodeType.NUMBER) {
        stride = strideArg.value;
      } else {
        stride = 1;
      }
    }
    if (window2 > array.width || window2 > array.height || stride > window2 || (array.width - window2) % stride !== 0 || (array.height - window2) % stride !== 0) {
      return ArraySize.error();
    }
    return arraySizeForPoolFunction(array, window2, stride);
  }
  medianpoolArraySize(ast, state) {
    return this.maxpoolArraySize(ast, state);
  }
  transpose(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TRANSPOSE"), (matrix2) => {
      const input = matrix2.rawData();
      const inputSize = matrix2.size;
      const result = [];
      for (let i2 = 0; i2 < inputSize.width; ++i2) {
        result[i2] = [];
        for (let j2 = 0; j2 < inputSize.height; ++j2) {
          result[i2][j2] = input[j2][i2];
        }
      }
      return SimpleRangeValue.onlyValues(result);
    });
  }
  transposeArraySize(ast, state) {
    if (ast.args.length !== 1) {
      return ArraySize.error();
    }
    const metadata = this.metadata("TRANSPOSE");
    const subChecks = ast.args.map((arg) => {
      var _a2;
      return this.arraySizeForAst(arg, new InterpreterState(state.formulaAddress, state.arraysFlag || ((_a2 = metadata === null || metadata === void 0 ? void 0 : metadata.enableArrayArithmeticForArguments) !== null && _a2 !== void 0 ? _a2 : false)));
    });
    const [size] = subChecks;
    return new ArraySize(size.height, size.width);
  }
  createKernel(kernel, outputSize) {
    return function(...args) {
      const result = [];
      for (let y2 = 0; y2 < outputSize.height; ++y2) {
        result.push([]);
        for (let x2 = 0; x2 < outputSize.width; ++x2) {
          result[y2][x2] = kernel.apply({
            thread: {
              x: x2,
              y: y2
            }
          }, args);
        }
      }
      return result;
    };
  }
}
MatrixPlugin.implementedFunctions = {
  "MMULT": {
    method: "mmult",
    sizeOfResultArrayMethod: "mmultArraySize",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }],
    vectorizationForbidden: true
  },
  "TRANSPOSE": {
    method: "transpose",
    sizeOfResultArrayMethod: "transposeArraySize",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }],
    vectorizationForbidden: true
  },
  "MAXPOOL": {
    method: "maxpool",
    sizeOfResultArrayMethod: "maxpoolArraySize",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true
    }],
    vectorizationForbidden: true
  },
  "MEDIANPOOL": {
    method: "medianpool",
    sizeOfResultArrayMethod: "medianpoolArraySize",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true
    }],
    vectorizationForbidden: true
  }
};
class MedianPlugin extends FunctionPlugin {
  /**
   * Corresponds to MEDIAN(Number1, Number2, ...).
   *
   * Returns a median of given numbers.
   *
   * @param ast
   * @param state
   */
  median(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MEDIAN"), (...args) => {
      const values2 = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (values2 instanceof CellError) {
        return values2;
      }
      if (values2.length === 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.OneValue);
      }
      values2.sort((a2, b2) => a2 - b2);
      if (values2.length % 2 === 0) {
        return (values2[values2.length / 2 - 1] + values2[values2.length / 2]) / 2;
      } else {
        return values2[Math.floor(values2.length / 2)];
      }
    });
  }
  large(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LARGE"), (range, n2) => {
      const vals = this.arithmeticHelper.manyToExactNumbers(range.valuesFromTopLeftCorner());
      if (vals instanceof CellError) {
        return vals;
      }
      vals.sort((a2, b2) => a2 - b2);
      n2 = Math.trunc(n2);
      if (n2 > vals.length) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      return vals[vals.length - n2];
    });
  }
  small(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SMALL"), (range, n2) => {
      const vals = this.arithmeticHelper.manyToExactNumbers(range.valuesFromTopLeftCorner());
      if (vals instanceof CellError) {
        return vals;
      }
      vals.sort((a2, b2) => a2 - b2);
      n2 = Math.trunc(n2);
      if (n2 > vals.length) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      return vals[n2 - 1];
    });
  }
}
MedianPlugin.implementedFunctions = {
  "MEDIAN": {
    method: "median",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "LARGE": {
    method: "large",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "SMALL": {
    method: "small",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  }
};
class ModuloPlugin extends FunctionPlugin {
  mod(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MOD"), (dividend, divisor) => {
      if (divisor === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      } else {
        return dividend % divisor;
      }
    });
  }
}
ModuloPlugin.implementedFunctions = {
  "MOD": {
    method: "mod",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
function percentileInclusive(sortedVals, k2) {
  const n2 = sortedVals.length;
  const rank = k2 * (n2 - 1);
  const lowerIndex = Math.floor(rank);
  const fraction2 = rank - lowerIndex;
  if (lowerIndex + 1 < n2) {
    return sortedVals[lowerIndex] + fraction2 * (sortedVals[lowerIndex + 1] - sortedVals[lowerIndex]);
  }
  return sortedVals[lowerIndex];
}
function percentileExclusive(sortedVals, k2) {
  const n2 = sortedVals.length;
  const rank = k2 * (n2 + 1);
  if (rank < 1) {
    return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
  }
  if (rank > n2) {
    return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
  }
  const lowerIndex = Math.floor(rank);
  const fraction2 = rank - lowerIndex;
  if (lowerIndex < n2) {
    return sortedVals[lowerIndex - 1] + fraction2 * (sortedVals[lowerIndex] - sortedVals[lowerIndex - 1]);
  }
  return sortedVals[lowerIndex - 1];
}
class PercentilePlugin extends FunctionPlugin {
  /**
   * Corresponds to PERCENTILE(array, k) and PERCENTILE.INC(array, k).
   *
   * Returns the k-th percentile of values in a range using inclusive interpolation.
   *
   * @param ast - procedure AST node
   * @param state - interpreter state
   * @returns interpolated percentile value, or CellError on invalid input
   */
  percentile(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PERCENTILE.INC"), (range, k2) => {
      const vals = this.getSortedValues(range);
      if (vals instanceof CellError) {
        return vals;
      }
      return percentileInclusive(vals, k2);
    });
  }
  /**
   * Corresponds to PERCENTILE.EXC(array, k).
   *
   * Returns the k-th percentile of values in a range using exclusive interpolation.
   *
   * @param ast - procedure AST node
   * @param state - interpreter state
   * @returns interpolated percentile value, or CellError on invalid input
   */
  percentileExc(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PERCENTILE.EXC"), (range, k2) => {
      const vals = this.getSortedValues(range);
      if (vals instanceof CellError) {
        return vals;
      }
      return percentileExclusive(vals, k2);
    });
  }
  /**
   * Corresponds to QUARTILE(array, quart) and QUARTILE.INC(array, quart).
   *
   * Returns the quartile of a data set using inclusive interpolation.
   * quart is truncated to an integer and validated in [0, 4].
   *
   * @param ast - procedure AST node
   * @param state - interpreter state
   * @returns quartile value, or CellError on invalid input
   */
  quartile(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("QUARTILE.INC"), (range, quart) => {
      quart = Math.trunc(quart);
      if (quart < 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
      }
      if (quart > 4) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      const vals = this.getSortedValues(range);
      if (vals instanceof CellError) {
        return vals;
      }
      return percentileInclusive(vals, quart / 4);
    });
  }
  /**
   * Corresponds to QUARTILE.EXC(array, quart).
   *
   * Returns the quartile of a data set using exclusive interpolation.
   * quart is truncated to an integer and validated in [1, 3].
   *
   * @param ast - procedure AST node
   * @param state - interpreter state
   * @returns quartile value, or CellError on invalid input
   */
  quartileExc(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("QUARTILE.EXC"), (range, quart) => {
      quart = Math.trunc(quart);
      if (quart < 1) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
      }
      if (quart > 3) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      const vals = this.getSortedValues(range);
      if (vals instanceof CellError) {
        return vals;
      }
      return percentileExclusive(vals, quart / 4);
    });
  }
  /**
   * Extracts numeric values from a range, filters non-numbers, and returns them sorted.
   * Returns CellError if the range contains an error, or if no numeric values exist.
   *
   * @param range - input range from the spreadsheet
   * @returns sorted numeric values (ascending), or CellError
   */
  getSortedValues(range) {
    const vals = this.arithmeticHelper.manyToExactNumbers(range.valuesFromTopLeftCorner());
    if (vals instanceof CellError) {
      return vals;
    }
    if (vals.length === 0) {
      return new CellError(ErrorType.NUM, ErrorMessage.OneValue);
    }
    vals.sort((a2, b2) => a2 - b2);
    return vals;
  }
}
PercentilePlugin.implementedFunctions = {
  "PERCENTILE.INC": {
    method: "percentile",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }]
  },
  "PERCENTILE.EXC": {
    method: "percentileExc",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }]
  },
  "QUARTILE.INC": {
    method: "quartile",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "QUARTILE.EXC": {
    method: "quartileExc",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
PercentilePlugin.aliases = {
  PERCENTILE: "PERCENTILE.INC",
  QUARTILE: "QUARTILE.INC"
};
function zeroForInfinite$1(value) {
  if (isExtendedNumber(value) && !Number.isFinite(getRawValue(value))) {
    return 0;
  } else {
    return value;
  }
}
class MomentsAggregate {
  constructor(sumsq, sum2, count) {
    this.sumsq = sumsq;
    this.sum = sum2;
    this.count = count;
  }
  static single(arg) {
    return new MomentsAggregate(arg * arg, arg, 1);
  }
  compose(other) {
    return new MomentsAggregate(this.sumsq + other.sumsq, this.sum + other.sum, this.count + other.count);
  }
  averageValue() {
    if (this.count > 0) {
      return this.sum / this.count;
    } else {
      return void 0;
    }
  }
  varSValue() {
    if (this.count > 1) {
      return (this.sumsq - this.sum * this.sum / this.count) / (this.count - 1);
    } else {
      return void 0;
    }
  }
  varPValue() {
    if (this.count > 0) {
      return (this.sumsq - this.sum * this.sum / this.count) / this.count;
    } else {
      return void 0;
    }
  }
}
MomentsAggregate.empty = new MomentsAggregate(0, 0, 0);
class NumericAggregationPlugin extends FunctionPlugin {
  constructor() {
    super(...arguments);
    this.addWithEpsilonRaw = (left, right) => this.arithmeticHelper.addWithEpsilonRaw(left, right);
  }
  /**
   * Corresponds to SUM(Number1, Number2, ...).
   *
   * Returns a sum of given numbers.
   *
   * @param ast
   * @param state
   */
  sum(ast, state) {
    return this.doSum(ast.args, state);
  }
  sumsq(ast, state) {
    return this.reduce(ast.args, state, 0, "SUMSQ", this.addWithEpsilonRaw, (arg) => Math.pow(getRawValue(arg), 2), strictlyNumbers);
  }
  /**
   * Corresponds to MAX(Number1, Number2, ...).
   *
   * Returns a max of given numbers.
   *
   * @param ast
   * @param state
   */
  max(ast, state) {
    return this.doMax(ast.args, state);
  }
  maxa(ast, state) {
    const value = this.reduce(ast.args, state, Number.NEGATIVE_INFINITY, "MAXA", (left, right) => Math.max(left, right), getRawValue, numbersBooleans);
    return zeroForInfinite$1(value);
  }
  /**
   * Corresponds to MIN(Number1, Number2, ...).
   *
   * Returns a min of given numbers.
   *
   * @param ast
   * @param state
   */
  min(ast, state) {
    return this.doMin(ast.args, state);
  }
  mina(ast, state) {
    const value = this.reduce(ast.args, state, Number.POSITIVE_INFINITY, "MINA", (left, right) => Math.min(left, right), getRawValue, numbersBooleans);
    return zeroForInfinite$1(value);
  }
  count(ast, state) {
    return this.doCount(ast.args, state);
  }
  counta(ast, state) {
    return this.doCounta(ast.args, state);
  }
  average(ast, state) {
    return this.doAverage(ast.args, state);
  }
  averagea(ast, state) {
    var _a2;
    const result = this.reduce(ast.args, state, MomentsAggregate.empty, "_AGGREGATE_A", (left, right) => left.compose(right), (arg) => MomentsAggregate.single(getRawValue(arg)), numbersBooleans);
    if (result instanceof CellError) {
      return result;
    } else {
      return (_a2 = result.averageValue()) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.DIV_BY_ZERO);
    }
  }
  vars(ast, state) {
    return this.doVarS(ast.args, state);
  }
  varp(ast, state) {
    return this.doVarP(ast.args, state);
  }
  vara(ast, state) {
    var _a2;
    const result = this.reduceAggregateA(ast.args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      return (_a2 = result.varSValue()) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.DIV_BY_ZERO);
    }
  }
  varpa(ast, state) {
    var _a2;
    const result = this.reduceAggregateA(ast.args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      return (_a2 = result.varPValue()) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.DIV_BY_ZERO);
    }
  }
  stdevs(ast, state) {
    return this.doStdevS(ast.args, state);
  }
  stdevp(ast, state) {
    return this.doStdevP(ast.args, state);
  }
  stdeva(ast, state) {
    const result = this.reduceAggregateA(ast.args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      const val = result.varSValue();
      return val === void 0 ? new CellError(ErrorType.DIV_BY_ZERO) : Math.sqrt(val);
    }
  }
  stdevpa(ast, state) {
    const result = this.reduceAggregateA(ast.args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      const val = result.varPValue();
      return val === void 0 ? new CellError(ErrorType.DIV_BY_ZERO) : Math.sqrt(val);
    }
  }
  product(ast, state) {
    return this.doProduct(ast.args, state);
  }
  subtotal(ast, state) {
    if (ast.args.length < 2) {
      return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
    }
    const functionType = this.coerceToType(this.evaluateAst(ast.args[0], state), {
      argumentType: FunctionArgumentType.NUMBER
    }, state);
    const args = ast.args.slice(1);
    switch (functionType) {
      case 1:
      case 101:
        return this.doAverage(args, state);
      case 2:
      case 102:
        return this.doCount(args, state);
      case 3:
      case 103:
        return this.doCounta(args, state);
      case 4:
      case 104:
        return this.doMax(args, state);
      case 5:
      case 105:
        return this.doMin(args, state);
      case 6:
      case 106:
        return this.doProduct(args, state);
      case 7:
      case 107:
        return this.doStdevS(args, state);
      case 8:
      case 108:
        return this.doStdevP(args, state);
      case 9:
      case 109:
        return this.doSum(args, state);
      case 10:
      case 110:
        return this.doVarS(args, state);
      case 11:
      case 111:
        return this.doVarP(args, state);
      default:
        return new CellError(ErrorType.VALUE, ErrorMessage.BadMode);
    }
  }
  reduceAggregate(args, state) {
    return this.reduce(args, state, MomentsAggregate.empty, "_AGGREGATE", (left, right) => {
      return left.compose(right);
    }, (arg) => {
      return MomentsAggregate.single(getRawValue(arg));
    }, strictlyNumbers);
  }
  reduceAggregateA(args, state) {
    return this.reduce(args, state, MomentsAggregate.empty, "_AGGREGATE_A", (left, right) => {
      return left.compose(right);
    }, (arg) => {
      return MomentsAggregate.single(getRawValue(arg));
    }, numbersBooleans);
  }
  doAverage(args, state) {
    var _a2;
    const result = this.reduceAggregate(args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      return (_a2 = result.averageValue()) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.DIV_BY_ZERO);
    }
  }
  doVarS(args, state) {
    var _a2;
    const result = this.reduceAggregate(args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      return (_a2 = result.varSValue()) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.DIV_BY_ZERO);
    }
  }
  doVarP(args, state) {
    var _a2;
    const result = this.reduceAggregate(args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      return (_a2 = result.varPValue()) !== null && _a2 !== void 0 ? _a2 : new CellError(ErrorType.DIV_BY_ZERO);
    }
  }
  doStdevS(args, state) {
    const result = this.reduceAggregate(args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      const val = result.varSValue();
      return val === void 0 ? new CellError(ErrorType.DIV_BY_ZERO) : Math.sqrt(val);
    }
  }
  doStdevP(args, state) {
    const result = this.reduceAggregate(args, state);
    if (result instanceof CellError) {
      return result;
    } else {
      const val = result.varPValue();
      return val === void 0 ? new CellError(ErrorType.DIV_BY_ZERO) : Math.sqrt(val);
    }
  }
  doCount(args, state) {
    return this.reduce(args, state, 0, "COUNT", (left, right) => left + right, getRawValue, (arg) => isExtendedNumber(arg) ? 1 : 0);
  }
  doCounta(args, state) {
    return this.reduce(args, state, 0, "COUNTA", (left, right) => left + right, getRawValue, (arg) => arg === EmptyValue ? 0 : 1);
  }
  doMax(args, state) {
    const value = this.reduce(args, state, Number.NEGATIVE_INFINITY, "MAX", (left, right) => Math.max(left, right), getRawValue, strictlyNumbers);
    return zeroForInfinite$1(value);
  }
  doMin(args, state) {
    const value = this.reduce(args, state, Number.POSITIVE_INFINITY, "MIN", (left, right) => Math.min(left, right), getRawValue, strictlyNumbers);
    return zeroForInfinite$1(value);
  }
  doSum(args, state) {
    return this.reduce(args, state, 0, "SUM", this.addWithEpsilonRaw, getRawValue, strictlyNumbers);
  }
  doProduct(args, state) {
    return this.reduce(args, state, 1, "PRODUCT", (left, right) => left * right, getRawValue, strictlyNumbers);
  }
  /**
   * Reduces procedure arguments with given reducing function
   *
   * @param args
   * @param state
   * @param initialAccValue - "neutral" value (equivalent of 0)
   * @param functionName - function name to use as cache key
   * @param reducingFunction - reducing function
   * @param mapFunction
   * @param coercionFunction
   */
  reduce(args, state, initialAccValue, functionName2, reducingFunction, mapFunction, coercionFunction) {
    if (args.length < 1) {
      return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
    }
    return args.reduce((acc, arg) => {
      if (acc instanceof CellError) {
        return acc;
      }
      if (arg.type === AstNodeType.CELL_RANGE || arg.type === AstNodeType.COLUMN_RANGE || arg.type === AstNodeType.ROW_RANGE) {
        const val = this.evaluateRange(arg, state, initialAccValue, functionName2, reducingFunction, mapFunction, coercionFunction);
        if (val instanceof CellError) {
          return val;
        }
        return reducingFunction(val, acc);
      }
      let value;
      value = this.evaluateAst(arg, state);
      if (value instanceof SimpleRangeValue) {
        const coercedRangeValues = Array.from(value.valuesFromTopLeftCorner()).map(coercionFunction).filter((arg2) => arg2 !== void 0);
        return coercedRangeValues.map((arg2) => {
          if (arg2 instanceof CellError) {
            return arg2;
          } else {
            return mapFunction(arg2);
          }
        }).reduce((left, right) => {
          if (left instanceof CellError) {
            return left;
          } else if (right instanceof CellError) {
            return right;
          } else {
            return reducingFunction(left, right);
          }
        }, acc);
      } else if (arg.type === AstNodeType.CELL_REFERENCE) {
        value = coercionFunction(value);
        if (value === void 0) {
          return acc;
        }
      } else {
        value = this.coerceScalarToNumberOrError(value);
        value = coercionFunction(value);
        if (value === void 0) {
          return acc;
        }
      }
      if (value instanceof CellError) {
        return value;
      }
      return reducingFunction(acc, mapFunction(value));
    }, initialAccValue);
  }
  /**
   * Performs range operation on given range
   *
   * @param {CellRangeAst | ColumnRangeAst | RowRangeAst} ast - cell range ast
   * @param {InterpreterState} state - interpreter state
   * @param {T} initialAccValue - initial accumulator value for reducing function
   * @param {string} functionName - function name to use as cache key
   * @param {BinaryOperation<T>} reducingFunction - reducing function
   * @param {MapOperation<T>} mapFunction - mapper transforming coerced scalar
   * @param {coercionOperation} coercionFunction - scalar-to-number coercer
   */
  evaluateRange(ast, state, initialAccValue, functionName2, reducingFunction, mapFunction, coercionFunction) {
    let range;
    try {
      range = AbsoluteCellRange.fromAst(ast, state.formulaAddress);
    } catch (err) {
      if (err instanceof SheetsNotEqual) {
        return new CellError(ErrorType.REF, ErrorMessage.RangeManySheets);
      } else {
        throw err;
      }
    }
    if (!this.isSheetValid(range)) {
      return new CellError(ErrorType.REF, ErrorMessage.SheetRef);
    }
    const rangeVertex = this.dependencyGraph.getRange(range.start, range.end);
    if (rangeVertex === void 0) {
      throw new Error("Range does not exists in graph");
    }
    let value = rangeVertex.getFunctionValue(functionName2);
    if (value === void 0) {
      const rangeValues = this.getRangeValues(functionName2, range, rangeVertex, mapFunction, coercionFunction);
      value = rangeValues.reduce((arg1, arg2) => {
        if (arg1 instanceof CellError) {
          return arg1;
        } else if (arg2 instanceof CellError) {
          return arg2;
        } else {
          return reducingFunction(arg1, arg2);
        }
      }, initialAccValue);
      rangeVertex.setFunctionValue(functionName2, value);
    }
    return value;
  }
  /**
   * Checks whether both ends of a range point to existing sheets (placeholders excluded).
   */
  isSheetValid(range) {
    return this.dependencyGraph.sheetMapping.hasSheetWithId(range.start.sheet, {
      includePlaceholders: false
    }) && this.dependencyGraph.sheetMapping.hasSheetWithId(range.end.sheet, {
      includePlaceholders: false
    });
  }
  /**
   * Returns list of values for given range and function name
   *
   * If range is dependent on smaller range, list will contain value of smaller range for this function
   * and values of cells that are not present in smaller range
   *
   * @param functionName - function name (e.g., SUM)
   * @param range - cell range
   * @param rangeVertex
   * @param mapFunction
   * @param coercionFunction
   */
  getRangeValues(functionName2, range, rangeVertex, mapFunction, coercionFunction) {
    const rangeResult = [];
    const {
      smallerRangeVertex,
      restRange
    } = this.dependencyGraph.rangeMapping.findSmallerRange(range);
    let actualRange;
    if (smallerRangeVertex !== void 0 && this.dependencyGraph.existsEdge(smallerRangeVertex, rangeVertex)) {
      const cachedValue = smallerRangeVertex.getFunctionValue(functionName2);
      if (cachedValue !== void 0) {
        rangeResult.push(cachedValue);
      } else {
        for (const cellFromRange of smallerRangeVertex.range.addresses(this.dependencyGraph)) {
          const val = coercionFunction(this.dependencyGraph.getScalarValue(cellFromRange));
          if (val instanceof CellError) {
            rangeResult.push(val);
          } else if (val !== void 0) {
            rangeResult.push(mapFunction(val));
          }
        }
      }
      actualRange = restRange;
    } else {
      actualRange = range;
    }
    for (const cellFromRange of actualRange.addresses(this.dependencyGraph)) {
      const val = coercionFunction(this.dependencyGraph.getScalarValue(cellFromRange));
      if (val instanceof CellError) {
        rangeResult.push(val);
      } else if (val !== void 0) {
        rangeResult.push(mapFunction(val));
      }
    }
    return rangeResult;
  }
}
NumericAggregationPlugin.implementedFunctions = {
  "SUM": {
    method: "sum",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "SUMSQ": {
    method: "sumsq",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "MAX": {
    method: "max",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "MIN": {
    method: "min",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "MAXA": {
    method: "maxa",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "MINA": {
    method: "mina",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "COUNT": {
    method: "count",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "COUNTA": {
    method: "counta",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "AVERAGE": {
    method: "average",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "AVERAGEA": {
    method: "averagea",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "PRODUCT": {
    method: "product",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "VAR.S": {
    method: "vars",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "VAR.P": {
    method: "varp",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "VARA": {
    method: "vara",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "VARPA": {
    method: "varpa",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "STDEV.S": {
    method: "stdevs",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "STDEV.P": {
    method: "stdevp",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "STDEVA": {
    method: "stdeva",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "STDEVPA": {
    method: "stdevpa",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "SUBTOTAL": {
    method: "subtotal",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  }
};
NumericAggregationPlugin.aliases = {
  VAR: "VAR.S",
  VARP: "VAR.P",
  STDEV: "STDEV.S",
  STDEVP: "STDEV.P",
  VARS: "VAR.S",
  STDEVS: "STDEV.S"
};
function strictlyNumbers(arg) {
  if (isExtendedNumber(arg) || arg instanceof CellError) {
    return arg;
  } else {
    return void 0;
  }
}
function numbersBooleans(arg) {
  if (typeof arg === "boolean") {
    return coerceBooleanToNumber(arg);
  } else if (isExtendedNumber(arg) || arg instanceof CellError) {
    return arg;
  } else if (typeof arg === "string") {
    return 0;
  } else {
    return void 0;
  }
}
class PowerPlugin extends FunctionPlugin {
  power(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("POWER"), Math.pow);
  }
}
PowerPlugin.implementedFunctions = {
  "POWER": {
    method: "power",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
class RadiansPlugin extends FunctionPlugin {
  radians(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("RADIANS"), (arg) => arg * (Math.PI / 180));
  }
}
RadiansPlugin.implementedFunctions = {
  "RADIANS": {
    method: "radians",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
const MAX_LENGTH = 10;
const DECIMAL_NUMBER_OF_BITS = 255;
const MIN_BASE = 2;
const MAX_BASE = 36;
const ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
class RadixConversionPlugin extends FunctionPlugin {
  dec2bin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DEC2BIN"), (value, places) => decimalToBaseWithExactPadding(value, 2, places));
  }
  dec2oct(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DEC2OCT"), (value, places) => decimalToBaseWithExactPadding(value, 8, places));
  }
  dec2hex(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DEC2HEX"), (value, places) => decimalToBaseWithExactPadding(value, 16, places));
  }
  bin2dec(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BIN2DEC"), (binary) => {
      const binaryWithSign = coerceStringToBase(binary, 2, MAX_LENGTH);
      if (binaryWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotBinary);
      }
      return twoComplementToDecimal(binaryWithSign, 2);
    });
  }
  bin2oct(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BIN2OCT"), (binary, places) => {
      const binaryWithSign = coerceStringToBase(binary, 2, MAX_LENGTH);
      if (binaryWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotBinary);
      }
      return decimalToBaseWithExactPadding(twoComplementToDecimal(binaryWithSign, 2), 8, places);
    });
  }
  bin2hex(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BIN2HEX"), (binary, places) => {
      const binaryWithSign = coerceStringToBase(binary, 2, MAX_LENGTH);
      if (binaryWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotBinary);
      }
      return decimalToBaseWithExactPadding(twoComplementToDecimal(binaryWithSign, 2), 16, places);
    });
  }
  oct2dec(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("OCT2DEC"), (octal) => {
      const octalWithSign = coerceStringToBase(octal, 8, MAX_LENGTH);
      if (octalWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotOctal);
      }
      return twoComplementToDecimal(octalWithSign, 8);
    });
  }
  oct2bin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("OCT2BIN"), (octal, places) => {
      const octalWithSign = coerceStringToBase(octal, 8, MAX_LENGTH);
      if (octalWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotOctal);
      }
      return decimalToBaseWithExactPadding(twoComplementToDecimal(octalWithSign, 8), 2, places);
    });
  }
  oct2hex(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("OCT2HEX"), (octal, places) => {
      const octalWithSign = coerceStringToBase(octal, 8, MAX_LENGTH);
      if (octalWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotOctal);
      }
      return decimalToBaseWithExactPadding(twoComplementToDecimal(octalWithSign, 8), 16, places);
    });
  }
  hex2dec(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HEX2DEC"), (hexadecimal) => {
      const hexadecimalWithSign = coerceStringToBase(hexadecimal, 16, MAX_LENGTH);
      if (hexadecimalWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotHex);
      }
      return twoComplementToDecimal(hexadecimalWithSign, 16);
    });
  }
  hex2bin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HEX2BIN"), (hexadecimal, places) => {
      const hexadecimalWithSign = coerceStringToBase(hexadecimal, 16, MAX_LENGTH);
      if (hexadecimalWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotHex);
      }
      return decimalToBaseWithExactPadding(twoComplementToDecimal(hexadecimalWithSign, 16), 2, places);
    });
  }
  hex2oct(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HEX2OCT"), (hexadecimal, places) => {
      const hexadecimalWithSign = coerceStringToBase(hexadecimal, 16, MAX_LENGTH);
      if (hexadecimalWithSign === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotHex);
      }
      return decimalToBaseWithExactPadding(twoComplementToDecimal(hexadecimalWithSign, 16), 8, places);
    });
  }
  base(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BASE"), decimalToBaseWithMinimumPadding);
  }
  decimal(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DECIMAL"), (arg, base) => {
      const input = coerceStringToBase(arg, base, DECIMAL_NUMBER_OF_BITS);
      if (input === void 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NotHex);
      }
      return parseInt(input, base);
    });
  }
}
RadixConversionPlugin.implementedFunctions = {
  "DEC2BIN": {
    method: "dec2bin",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 1,
      maxValue: 10
    }]
  },
  "DEC2OCT": {
    method: "dec2oct",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 1,
      maxValue: 10
    }]
  },
  "DEC2HEX": {
    method: "dec2hex",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 1,
      maxValue: 10
    }]
  },
  "BIN2DEC": {
    method: "bin2dec",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "BIN2OCT": {
    method: "bin2oct",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 0,
      maxValue: MAX_LENGTH
    }]
  },
  "BIN2HEX": {
    method: "bin2hex",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 0,
      maxValue: MAX_LENGTH
    }]
  },
  "OCT2DEC": {
    method: "oct2dec",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "OCT2BIN": {
    method: "oct2bin",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 0,
      maxValue: MAX_LENGTH
    }]
  },
  "OCT2HEX": {
    method: "oct2hex",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 0,
      maxValue: MAX_LENGTH
    }]
  },
  "HEX2DEC": {
    method: "hex2dec",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "HEX2BIN": {
    method: "hex2bin",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 0,
      maxValue: MAX_LENGTH
    }]
  },
  "HEX2OCT": {
    method: "hex2oct",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 0,
      maxValue: MAX_LENGTH
    }]
  },
  "DECIMAL": {
    method: "decimal",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: MIN_BASE,
      maxValue: MAX_BASE
    }]
  },
  "BASE": {
    method: "base",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: MIN_BASE,
      maxValue: MAX_BASE
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true,
      minValue: 0,
      maxValue: DECIMAL_NUMBER_OF_BITS
    }]
  }
};
function coerceStringToBase(value, base, maxLength) {
  const baseAlphabet = ALPHABET.substr(0, base);
  const regex = new RegExp(`^[${baseAlphabet}]+$`);
  if (value.length > maxLength || !regex.test(value)) {
    return void 0;
  }
  return value;
}
function decimalToBaseWithExactPadding(value, base, places) {
  if (value > maxValFromBase(base)) {
    return new CellError(ErrorType.NUM, ErrorMessage.ValueBaseLarge);
  }
  if (value < minValFromBase(base)) {
    return new CellError(ErrorType.NUM, ErrorMessage.ValueBaseSmall);
  }
  const result = decimalToRadixComplement(value, base);
  if (places === void 0 || value < 0) {
    return result;
  } else if (result.length > places) {
    return new CellError(ErrorType.NUM, ErrorMessage.ValueBaseLong);
  } else {
    return padLeft(result, places);
  }
}
function minValFromBase(base) {
  return -Math.pow(base, MAX_LENGTH) / 2;
}
function maxValFromBase(base) {
  return -minValFromBase(base) - 1;
}
function decimalToBaseWithMinimumPadding(value, base, places) {
  const result = decimalToRadixComplement(value, base);
  if (places !== void 0 && places > result.length) {
    return padLeft(result, places);
  } else {
    return result;
  }
}
function decimalToRadixComplement(value, base) {
  const offset = value < 0 ? Math.pow(base, MAX_LENGTH) : 0;
  return (value + offset).toString(base).toUpperCase();
}
function twoComplementToDecimal(value, base) {
  const parsed = parseInt(value, base);
  const offset = Math.pow(base, MAX_LENGTH);
  return parsed >= offset / 2 ? parsed - offset : parsed;
}
class RandomPlugin extends FunctionPlugin {
  /**
   * Corresponds to RAND()
   *
   * Returns a pseudo-random floating-point random number
   * in the range [0,1).
   *
   * @param ast
   * @param state
   */
  rand(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("RAND"), Math.random);
  }
  randbetween(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("RANDBETWEEN"), (lower, upper) => {
      if (upper < lower) {
        return new CellError(ErrorType.NUM, ErrorMessage.WrongOrder);
      }
      lower = Math.ceil(lower);
      upper = Math.floor(upper) + 1;
      if (lower === upper) {
        upper += 1;
      }
      return lower + Math.floor(Math.random() * (upper - lower));
    });
  }
}
RandomPlugin.implementedFunctions = {
  "RAND": {
    method: "rand",
    parameters: [],
    isVolatile: true
  },
  "RANDBETWEEN": {
    method: "randbetween",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }],
    isVolatile: true
  }
};
class SequencePlugin extends FunctionPlugin {
  /** Returns true when `n` is a finite number at least {@link MIN_DIMENSION}. */
  static isValidDimension(n2) {
    return Number.isFinite(n2) && n2 >= SequencePlugin.MIN_DIMENSION;
  }
  /**
   * Parses a literal dimension from an AST node at parse time.
   * Handles NUMBER nodes directly, STRING nodes via numeric coercion,
   * PLUS/MINUS_UNARY_OP wrapping a NUMBER (e.g. `+3`, `-2`),
   * and TRUE()/FALSE() function calls (returning 1/0).
   * Returns undefined for non-literal nodes (cell refs, formulas, binary ops).
   */
  static parseLiteralDimension(node) {
    if (node.type === AstNodeType.NUMBER) {
      return Math.trunc(node.value);
    }
    if (node.type === AstNodeType.STRING) {
      const parsed = Number(node.value);
      return Number.isFinite(parsed) ? Math.trunc(parsed) : void 0;
    }
    if (node.type === AstNodeType.PLUS_UNARY_OP && node.value.type === AstNodeType.NUMBER) {
      return Math.trunc(node.value.value);
    }
    if (node.type === AstNodeType.MINUS_UNARY_OP && node.value.type === AstNodeType.NUMBER) {
      return Math.trunc(-node.value.value);
    }
    if (node.type === AstNodeType.FUNCTION_CALL) {
      if (node.procedureName === "TRUE" && node.args.length === 0) {
        return 1;
      }
      if (node.procedureName === "FALSE" && node.args.length === 0) {
        return 0;
      }
    }
    return void 0;
  }
  /**
   * Corresponds to SEQUENCE(rows, [cols], [start], [step])
   *
   * Returns a rows×cols array of sequential numbers starting at `start`
   * and incrementing by `step`, filled row-major.
   *
   * Note: dynamic arguments (cell references, formulas) for `rows` or `cols`
   * cause a size mismatch between parse-time prediction and runtime result,
   * which results in a #VALUE! error. Use literal numbers for rows and cols.
   *
   * @param {ProcedureAst} ast - The parsed function call AST node.
   * @param {InterpreterState} state - Current interpreter evaluation state.
   */
  sequence(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SEQUENCE"), (rows, cols, start, step) => {
      if (!Number.isFinite(rows) || !Number.isFinite(cols)) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ValueLarge);
      }
      if (rows < 0 || cols < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      const numRows = Math.trunc(rows);
      const numCols = Math.trunc(cols);
      if (!SequencePlugin.isValidDimension(numRows) || !SequencePlugin.isValidDimension(numCols)) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      if (numRows > this.config.maxRows || numCols > this.config.maxColumns) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ValueLarge);
      }
      const result = [];
      for (let r2 = 0; r2 < numRows; r2++) {
        const row = [];
        for (let c2 = 0; c2 < numCols; c2++) {
          row.push(start + (r2 * numCols + c2) * step);
        }
        result.push(row);
      }
      return SimpleRangeValue.onlyNumbers(result);
    });
  }
  /**
   * Predicts the output array size for SEQUENCE at parse time.
   *
   * Handles NUMBER and STRING literals for rows/cols via `parseLiteralDimension`.
   * Non-literal args (cell refs, formulas, unary/binary ops) fall back to 1,
   * which will cause a size mismatch at eval time when the actual result is larger.
   *
   * @param {ProcedureAst} ast - The parsed function call AST node.
   * @param {InterpreterState} _state - Current interpreter evaluation state (unused).
   */
  sequenceArraySize(ast, _state) {
    if (ast.args.length < 1 || ast.args.length > 4) {
      return ArraySize.error();
    }
    const rowsArg = ast.args[0];
    const colsArg = ast.args.length > 1 ? ast.args[1] : void 0;
    if (rowsArg.type === AstNodeType.EMPTY) {
      return ArraySize.error();
    }
    const rows = SequencePlugin.parseLiteralDimension(rowsArg);
    if (rows === void 0) {
      return ArraySize.error();
    }
    const cols = colsArg === void 0 || colsArg.type === AstNodeType.EMPTY ? 1 : SequencePlugin.parseLiteralDimension(colsArg);
    if (cols === void 0) {
      return ArraySize.error();
    }
    if (!SequencePlugin.isValidDimension(rows) || !SequencePlugin.isValidDimension(cols)) {
      return ArraySize.error();
    }
    if (rows > this.config.maxRows || cols > this.config.maxColumns) {
      return ArraySize.error();
    }
    return new ArraySize(cols, rows);
  }
}
SequencePlugin.MIN_DIMENSION = 1;
SequencePlugin.implementedFunctions = {
  "SEQUENCE": {
    method: "sequence",
    sizeOfResultArrayMethod: "sequenceArraySize",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1,
      emptyAsDefault: true
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1,
      emptyAsDefault: true
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1,
      emptyAsDefault: true
    }],
    vectorizationForbidden: true
  }
};
function findNextOddNumber(arg) {
  const ceiled = Math.ceil(arg);
  return ceiled % 2 === 1 ? ceiled : ceiled + 1;
}
function findNextEvenNumber(arg) {
  const ceiled = Math.ceil(arg);
  return ceiled % 2 === 0 ? ceiled : ceiled + 1;
}
class RoundingPlugin extends FunctionPlugin {
  roundup(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ROUNDDOWN"), (numberToRound, places) => {
      const placesMultiplier = Math.pow(10, places);
      if (numberToRound < 0) {
        return -Math.ceil(-numberToRound * placesMultiplier) / placesMultiplier;
      } else {
        return Math.ceil(numberToRound * placesMultiplier) / placesMultiplier;
      }
    });
  }
  rounddown(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ROUNDDOWN"), (numberToRound, places) => {
      const placesMultiplier = Math.pow(10, places);
      if (numberToRound < 0) {
        return -Math.floor(-numberToRound * placesMultiplier) / placesMultiplier;
      } else {
        return Math.floor(numberToRound * placesMultiplier) / placesMultiplier;
      }
    });
  }
  round(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ROUND"), (numberToRound, places) => {
      const placesMultiplier = Math.pow(10, places);
      if (numberToRound < 0) {
        return -Math.round(-numberToRound * placesMultiplier) / placesMultiplier;
      } else {
        return Math.round(numberToRound * placesMultiplier) / placesMultiplier;
      }
    });
  }
  intFunc(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("INT"), (coercedNumberToRound) => {
      if (coercedNumberToRound < 0) {
        return -Math.floor(-coercedNumberToRound);
      } else {
        return Math.floor(coercedNumberToRound);
      }
    });
  }
  even(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("EVEN"), (coercedNumberToRound) => {
      if (coercedNumberToRound < 0) {
        return -findNextEvenNumber(-coercedNumberToRound);
      } else {
        return findNextEvenNumber(coercedNumberToRound);
      }
    });
  }
  odd(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ODD"), (coercedNumberToRound) => {
      if (coercedNumberToRound < 0) {
        return -findNextOddNumber(-coercedNumberToRound);
      } else {
        return findNextOddNumber(coercedNumberToRound);
      }
    });
  }
  ceilingmath(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CEILING.MATH"), (value, significance, mode) => {
      if (significance === 0 || value === 0) {
        return 0;
      }
      significance = Math.abs(significance);
      if (mode === 1 && value < 0) {
        significance = -significance;
      }
      return Math.ceil(value / significance) * significance;
    });
  }
  ceiling(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CEILING"), (value, significance) => {
      if (value === 0) {
        return 0;
      }
      if (significance === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      if (value > 0 && significance < 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.DistinctSigns);
      }
      return Math.ceil(value / significance) * significance;
    });
  }
  ceilingprecise(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CEILING.PRECISE"), (value, significance) => {
      if (significance === 0 || value === 0) {
        return 0;
      }
      significance = Math.abs(significance);
      return Math.ceil(value / significance) * significance;
    });
  }
  floormath(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FLOOR.MATH"), (value, significance, mode) => {
      if (significance === 0 || value === 0) {
        return 0;
      }
      significance = Math.abs(significance);
      if (mode === 1 && value < 0) {
        significance *= -1;
      }
      return Math.floor(value / significance) * significance;
    });
  }
  floor(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FLOOR"), (value, significance) => {
      if (value === 0) {
        return 0;
      }
      if (significance === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      if (value > 0 && significance < 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.DistinctSigns);
      }
      return Math.floor(value / significance) * significance;
    });
  }
  floorprecise(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FLOOR.PRECISE"), (value, significance) => {
      if (significance === 0 || value === 0) {
        return 0;
      }
      significance = Math.abs(significance);
      return Math.floor(value / significance) * significance;
    });
  }
}
RoundingPlugin.implementedFunctions = {
  "ROUNDUP": {
    method: "roundup",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }]
  },
  "ROUNDDOWN": {
    method: "rounddown",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }]
  },
  "ROUND": {
    method: "round",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }]
  },
  "INT": {
    method: "intFunc",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "EVEN": {
    method: "even",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ODD": {
    method: "odd",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "CEILING.MATH": {
    method: "ceilingmath",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }]
  },
  "CEILING": {
    method: "ceiling",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "CEILING.PRECISE": {
    method: "ceilingprecise",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "FLOOR.MATH": {
    method: "floormath",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }]
  },
  "FLOOR": {
    method: "floor",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "FLOOR.PRECISE": {
    method: "floorprecise",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  }
};
RoundingPlugin.aliases = {
  "ISO.CEILING": "CEILING.PRECISE",
  "TRUNC": "ROUNDDOWN"
};
class SqrtPlugin extends FunctionPlugin {
  sqrt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SQRT"), Math.sqrt);
  }
}
SqrtPlugin.implementedFunctions = {
  "SQRT": {
    method: "sqrt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
function* empty() {
}
function split(iterable) {
  const iterator = iterable[Symbol.iterator]();
  const {
    done,
    value
  } = iterator.next();
  if (done) {
    return {
      rest: empty()
    };
  } else {
    return {
      value,
      rest: iterator
    };
  }
}
const findSmallerRangeForMany = (dependencyGraph, conditionRanges, valuesRange) => {
  if (valuesRange.end.row > valuesRange.start.row) {
    const valuesRangeEndRowLess = simpleCellAddress(valuesRange.end.sheet, valuesRange.end.col, valuesRange.end.row - 1);
    const rowLessVertex = dependencyGraph.getRange(valuesRange.start, valuesRangeEndRowLess);
    if (rowLessVertex !== void 0) {
      return {
        smallerRangeVertex: rowLessVertex,
        restValuesRange: valuesRange.withStart(simpleCellAddress(valuesRange.start.sheet, valuesRange.start.col, valuesRange.end.row)),
        restConditionRanges: conditionRanges.map((conditionRange) => conditionRange.withStart(simpleCellAddress(conditionRange.start.sheet, conditionRange.start.col, conditionRange.end.row)))
      };
    }
  }
  return {
    restValuesRange: valuesRange,
    restConditionRanges: conditionRanges
  };
};
class CriterionFunctionCompute {
  constructor(interpreter, cacheKey, reduceInitialValue, composeFunction, mapFunction) {
    this.interpreter = interpreter;
    this.cacheKey = cacheKey;
    this.reduceInitialValue = reduceInitialValue;
    this.composeFunction = composeFunction;
    this.mapFunction = mapFunction;
    this.dependencyGraph = this.interpreter.dependencyGraph;
  }
  compute(simpleValuesRange, conditions) {
    for (const condition of conditions) {
      if (!condition.conditionRange.sameDimensionsAs(simpleValuesRange)) {
        return new CellError(ErrorType.VALUE, ErrorMessage.EqualLength);
      }
    }
    const valuesRangeVertex = this.tryToGetRangeVertexForRangeValue(simpleValuesRange);
    const conditionsVertices = conditions.map((c2) => this.tryToGetRangeVertexForRangeValue(c2.conditionRange));
    if (valuesRangeVertex && conditionsVertices.every((e3) => e3 !== void 0)) {
      const fullCriterionString = conditions.map((c2) => c2.criterionPackage.raw).join(",");
      const cachedResult = this.findAlreadyComputedValueInCache(valuesRangeVertex, this.cacheKey(conditions), fullCriterionString);
      if (cachedResult !== void 0) {
        this.interpreter.stats.incrementCriterionFunctionFullCacheUsed();
        return cachedResult;
      }
      const cache = this.buildNewCriterionCache(this.cacheKey(conditions), conditions.map((c2) => c2.conditionRange.range), simpleValuesRange.range);
      if (!cache.has(fullCriterionString)) {
        cache.set(fullCriterionString, [this.evaluateRangeValue(simpleValuesRange, conditions), conditions.map((condition) => condition.criterionPackage.lambda)]);
      }
      valuesRangeVertex.setCriterionFunctionValues(this.cacheKey(conditions), cache);
      conditionsVertices.forEach((range) => {
        if (range !== void 0) {
          range.addDependentCacheRange(valuesRangeVertex);
        }
      });
      return cache.get(fullCriterionString)[0];
    } else {
      return this.evaluateRangeValue(simpleValuesRange, conditions);
    }
  }
  tryToGetRangeVertexForRangeValue(rangeValue) {
    const maybeRange = rangeValue.range;
    if (maybeRange === void 0) {
      return void 0;
    } else {
      return this.dependencyGraph.getRange(maybeRange.start, maybeRange.end);
    }
  }
  reduceFunction(iterable) {
    let acc = this.reduceInitialValue;
    for (const val of iterable) {
      acc = this.composeFunction(acc, val);
    }
    return acc;
  }
  findAlreadyComputedValueInCache(rangeVertex, cacheKey, criterionString) {
    return rangeVertex.getCriterionFunctionValue(cacheKey, criterionString);
  }
  evaluateRangeValue(simpleValuesRange, conditions) {
    const criterionLambdas = conditions.map((condition) => condition.criterionPackage.lambda);
    const values2 = Array.from(simpleValuesRange.valuesFromTopLeftCorner()).map(this.mapFunction)[Symbol.iterator]();
    const conditionsIterators = conditions.map((condition) => condition.conditionRange.iterateValuesFromTopLeftCorner());
    const filteredValues = ifFilter(criterionLambdas, conditionsIterators, values2);
    return this.reduceFunction(filteredValues);
  }
  buildNewCriterionCache(cacheKey, simpleConditionRanges, simpleValuesRange) {
    const currentRangeVertex = this.dependencyGraph.getRange(simpleValuesRange.start, simpleValuesRange.end);
    const {
      smallerRangeVertex,
      restConditionRanges,
      restValuesRange
    } = findSmallerRangeForMany(this.dependencyGraph, simpleConditionRanges, simpleValuesRange);
    let smallerCache;
    if (smallerRangeVertex !== void 0 && this.dependencyGraph.existsEdge(smallerRangeVertex, currentRangeVertex)) {
      smallerCache = smallerRangeVertex.getCriterionFunctionValues(cacheKey);
    } else {
      smallerCache = /* @__PURE__ */ new Map();
    }
    const newCache = /* @__PURE__ */ new Map();
    smallerCache.forEach(([value, criterionLambdas], key) => {
      const filteredValues = ifFilter(criterionLambdas, restConditionRanges.map((rcr) => getRangeValues(this.dependencyGraph, rcr)), Array.from(getRangeValues(this.dependencyGraph, restValuesRange)).map(this.mapFunction)[Symbol.iterator]());
      const newCacheValue = this.composeFunction(value, this.reduceFunction(filteredValues));
      this.interpreter.stats.incrementCriterionFunctionPartialCacheUsed();
      newCache.set(key, [newCacheValue, criterionLambdas]);
    });
    return newCache;
  }
}
class Condition {
  constructor(conditionRange, criterionPackage) {
    this.conditionRange = conditionRange;
    this.criterionPackage = criterionPackage;
  }
}
function* getRangeValues(dependencyGraph, cellRange) {
  for (const cellFromRange of cellRange.addresses(dependencyGraph)) {
    yield getRawValue(dependencyGraph.getScalarValue(cellFromRange));
  }
}
function* ifFilter(criterionLambdas, conditionalIterables, computableIterable) {
  for (const computable of computableIterable) {
    const conditionalSplits = conditionalIterables.map((conditionalIterable) => split(conditionalIterable));
    if (!conditionalSplits.every((cs) => Object.prototype.hasOwnProperty.call(cs, "value"))) {
      return;
    }
    const conditionalFirsts = conditionalSplits.map((cs) => getRawValue(cs.value));
    if (zip(conditionalFirsts, criterionLambdas).every(([conditionalFirst, criterionLambda]) => criterionLambda(conditionalFirst))) {
      yield computable;
    }
    conditionalIterables = conditionalSplits.map((cs) => cs.rest);
  }
}
function zip(arr1, arr2) {
  const result = [];
  for (let i2 = 0; i2 < Math.min(arr1.length, arr2.length); i2++) {
    result.push([arr1[i2], arr2[i2]]);
  }
  return result;
}
class AverageResult {
  constructor(sum2, count) {
    this.sum = sum2;
    this.count = count;
  }
  static single(arg) {
    return new AverageResult(arg, 1);
  }
  compose(other) {
    return new AverageResult(this.sum + other.sum, this.count + other.count);
  }
  averageValue() {
    if (this.count > 0) {
      return this.sum / this.count;
    } else {
      return void 0;
    }
  }
}
AverageResult.empty = new AverageResult(0, 0);
function conditionalAggregationFunctionCacheKey(functionName2) {
  return (conditions) => {
    const conditionsStrings = conditions.map((c2) => `${c2.conditionRange.range.sheet},${c2.conditionRange.range.start.col},${c2.conditionRange.range.start.row}`);
    return [functionName2, ...conditionsStrings].join(",");
  };
}
function zeroForInfinite(value) {
  if (isExtendedNumber(value) && !Number.isFinite(getRawValue(value))) {
    return 0;
  } else {
    return value;
  }
}
function mapToRawScalarValue(arg) {
  if (arg instanceof CellError) {
    return arg;
  }
  if (isExtendedNumber(arg)) {
    return getRawValue(arg);
  }
  return void 0;
}
class ConditionalAggregationPlugin extends FunctionPlugin {
  /**
   * Corresponds to SUMIF(Range, Criterion, SumRange)
   *
   * Range is the range to which criterion is to be applied.
   * Criterion is the criteria used to choose which cells will be included in sum.
   * SumRange is the range on which adding will be performed.
   *
   * @param ast
   * @param state
   */
  sumif(ast, state) {
    const functionName2 = "SUMIF";
    const computeFn = (conditionRange, criterion, values2) => this.computeConditionalAggregationFunction(values2 !== null && values2 !== void 0 ? values2 : conditionRange, [conditionRange, criterion], functionName2, 0, (left, right) => this.arithmeticHelper.nonstrictadd(left, right), mapToRawScalarValue);
    return this.runFunction(ast.args, state, this.metadata(functionName2), computeFn);
  }
  sumifs(ast, state) {
    const functionName2 = "SUMIFS";
    const computeFn = (values2, ...args) => this.computeConditionalAggregationFunction(values2, args, functionName2, 0, (left, right) => this.arithmeticHelper.nonstrictadd(left, right), mapToRawScalarValue);
    return this.runFunction(ast.args, state, this.metadata(functionName2), computeFn);
  }
  averageif(ast, state) {
    const functionName2 = "AVERAGEIF";
    const computeFn = (conditionRange, criterion, values2) => {
      const averageResult = this.computeConditionalAggregationFunction(values2 !== null && values2 !== void 0 ? values2 : conditionRange, [conditionRange, criterion], functionName2, AverageResult.empty, (left, right) => left.compose(right), (arg) => isExtendedNumber(arg) ? AverageResult.single(getRawValue(arg)) : AverageResult.empty);
      if (averageResult instanceof CellError) {
        return averageResult;
      } else {
        return averageResult.averageValue() || new CellError(ErrorType.DIV_BY_ZERO);
      }
    };
    return this.runFunction(ast.args, state, this.metadata(functionName2), computeFn);
  }
  /**
   * Corresponds to COUNTIF(Range, Criterion)
   *
   * Range is the range to which criterion is to be applied.
   * Criterion is the criteria used to choose which cells will be included in sum.
   *
   * Returns number of cells on which criteria evaluate to true.
   *
   * @param ast
   * @param state
   */
  countif(ast, state) {
    const functionName2 = "COUNTIF";
    const computeFn = (conditionRange, criterion) => this.computeConditionalAggregationFunction(conditionRange, [conditionRange, criterion], functionName2, 0, (left, right) => left + right, () => 1);
    return this.runFunction(ast.args, state, this.metadata(functionName2), computeFn);
  }
  countifs(ast, state) {
    const functionName2 = "COUNTIFS";
    const computeFn = (...args) => this.computeConditionalAggregationFunction(args[0], args, functionName2, 0, (left, right) => left + right, () => 1);
    return this.runFunction(ast.args, state, this.metadata(functionName2), computeFn);
  }
  minifs(ast, state) {
    const functionName2 = "MINIFS";
    const composeFunction = (left, right) => {
      if (right === void 0 || left === void 0) {
        return right === void 0 ? left : right;
      }
      return Math.min(left, right);
    };
    const computeFn = (values2, ...args) => {
      const minResult = this.computeConditionalAggregationFunction(values2, args, functionName2, Number.POSITIVE_INFINITY, composeFunction, mapToRawScalarValue);
      return zeroForInfinite(minResult);
    };
    return this.runFunction(ast.args, state, this.metadata(functionName2), computeFn);
  }
  maxifs(ast, state) {
    const functionName2 = "MAXIFS";
    const composeFunction = (left, right) => {
      if (right === void 0 || left === void 0) {
        return right === void 0 ? left : right;
      }
      return Math.max(left, right);
    };
    const computeFn = (values2, ...args) => {
      const maxResult = this.computeConditionalAggregationFunction(values2, args, functionName2, Number.NEGATIVE_INFINITY, composeFunction, mapToRawScalarValue);
      return zeroForInfinite(maxResult);
    };
    return this.runFunction(ast.args, state, this.metadata(functionName2), computeFn);
  }
  computeConditionalAggregationFunction(valuesRange, conditionArgs, functionName2, reduceInitialValue, composeFunction, mapFunction) {
    const conditions = [];
    for (let i2 = 0; i2 < conditionArgs.length; i2 += 2) {
      const conditionArg = conditionArgs[i2];
      const criterionPackage = this.interpreter.criterionBuilder.fromCellValue(conditionArgs[i2 + 1], this.arithmeticHelper);
      if (criterionPackage === void 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.BadCriterion);
      }
      conditions.push(new Condition(conditionArg, criterionPackage));
    }
    return new CriterionFunctionCompute(this.interpreter, conditionalAggregationFunctionCacheKey(functionName2), reduceInitialValue, composeFunction, mapFunction).compute(valuesRange, conditions);
  }
}
ConditionalAggregationPlugin.implementedFunctions = {
  SUMIF: {
    method: "sumif",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NOERROR
    }, {
      argumentType: FunctionArgumentType.RANGE,
      optionalArg: true
    }]
  },
  COUNTIF: {
    method: "countif",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NOERROR
    }]
  },
  AVERAGEIF: {
    method: "averageif",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NOERROR
    }, {
      argumentType: FunctionArgumentType.RANGE,
      optionalArg: true
    }]
  },
  SUMIFS: {
    method: "sumifs",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NOERROR
    }],
    repeatLastArgs: 2
  },
  COUNTIFS: {
    method: "countifs",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NOERROR
    }],
    repeatLastArgs: 2
  },
  MINIFS: {
    method: "minifs",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NOERROR
    }],
    repeatLastArgs: 2
  },
  MAXIFS: {
    method: "maxifs",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NOERROR
    }],
    repeatLastArgs: 2
  }
};
class SumprodPlugin extends FunctionPlugin {
  sumproduct(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SUMPRODUCT"), (...args) => {
      const width = args[0].width();
      const height = args[0].height();
      for (const arg of args) {
        if (arg.width() !== width || arg.height() !== height) {
          return new CellError(ErrorType.VALUE, ErrorMessage.EqualLength);
        }
      }
      let ret = 0;
      const iterators = args.map((arg) => arg.iterateValuesFromTopLeftCorner());
      for (let i2 = 0; i2 < width * height; i2++) {
        let acc = 1;
        for (const it2 of iterators) {
          const val = it2.next().value;
          if (val instanceof CellError) {
            return val;
          }
          const coercedVal = this.coerceScalarToNumberOrError(val);
          if (isExtendedNumber(coercedVal)) {
            acc *= getRawValue(coercedVal);
          } else {
            acc = 0;
          }
        }
        ret += acc;
      }
      return ret;
    });
  }
}
SumprodPlugin.implementedFunctions = {
  "SUMPRODUCT": {
    method: "sumproduct",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }],
    repeatLastArgs: 1
  }
};
class TextPlugin extends FunctionPlugin {
  /**
   * Corresponds to CONCATENATE(value1, [value2, ...])
   *
   * Concatenates provided arguments to one string.
   *
   * @param {ProcedureAst} ast - The procedure AST node
   * @param {InterpreterState} state - The interpreter state
   */
  concatenate(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CONCATENATE"), (...args) => {
      return "".concat(...args);
    });
  }
  /**
   * Corresponds to SPLIT(string, index)
   *
   * Splits provided string using space separator and returns chunk at zero-based position specified by second argument
   *
   * @param {ProcedureAst} ast - The procedure AST node
   * @param {InterpreterState} state - The interpreter state
   */
  split(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SPLIT"), (stringToSplit, indexToUse) => {
      const splittedString = stringToSplit.split(" ");
      if (indexToUse >= splittedString.length || indexToUse < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.IndexBounds);
      }
      return splittedString[indexToUse];
    });
  }
  len(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LEN"), (arg) => {
      return arg.length;
    });
  }
  lower(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LOWER"), (arg) => {
      return arg.toLowerCase();
    });
  }
  trim(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TRIM"), (arg) => {
      return arg.replace(/^ +/g, "").replace(/ +$/g, "").replace(/ +/g, " ");
    });
  }
  proper(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PROPER"), (arg) => {
      return arg.replace(new RegExp("\\p{L}+", "gu"), (word) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase());
    });
  }
  clean(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CLEAN"), (arg) => {
      return arg.replace(/[\u0000-\u001F]/g, "");
    });
  }
  exact(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("EXACT"), (left, right) => {
      return left === right;
    });
  }
  rept(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("REPT"), (text, count) => {
      if (count < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NegativeCount);
      }
      return text.repeat(count);
    });
  }
  right(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("RIGHT"), (text, length) => {
      if (length < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
      } else if (length === 0) {
        return "";
      }
      return text.slice(-length);
    });
  }
  left(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LEFT"), (text, length) => {
      if (length < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
      }
      return text.slice(0, length);
    });
  }
  mid(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MID"), (text, startPosition, numberOfChars) => {
      if (startPosition < 1) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      if (numberOfChars < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
      }
      return text.substring(startPosition - 1, startPosition + numberOfChars - 1);
    });
  }
  replace(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("REPLACE"), (text, startPosition, numberOfChars, newText) => {
      if (startPosition < 1) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      if (numberOfChars < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.NegativeLength);
      }
      return text.substring(0, startPosition - 1) + newText + text.substring(startPosition + numberOfChars - 1);
    });
  }
  search(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SEARCH"), (pattern, text, startIndex) => {
      if (startIndex < 1 || startIndex > text.length) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LengthBounds);
      }
      const normalizedPattern = pattern.toLowerCase();
      const normalizedText = text.substring(startIndex - 1).toLowerCase();
      const index2 = this.arithmeticHelper.requiresRegex(normalizedPattern) ? this.arithmeticHelper.searchString(normalizedPattern, normalizedText) : normalizedText.indexOf(normalizedPattern);
      return index2 > -1 ? index2 + startIndex : new CellError(ErrorType.VALUE, ErrorMessage.PatternNotFound);
    });
  }
  substitute(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SUBSTITUTE"), (text, searchString, replacementString, occurrenceNum) => {
      const escapedSearchString = this.escapeRegExpSpecialCharacters(searchString);
      const searchRegExp = new RegExp(escapedSearchString, "g");
      if (occurrenceNum === void 0) {
        return text.replace(searchRegExp, replacementString);
      }
      if (occurrenceNum < 1) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      let match;
      let i2 = 0;
      while ((match = searchRegExp.exec(text)) !== null) {
        if (occurrenceNum === ++i2) {
          return text.substring(0, match.index) + replacementString + text.substring(searchRegExp.lastIndex);
        }
      }
      return text;
    });
  }
  find(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FIND"), (pattern, text, startIndex) => {
      if (startIndex < 1 || startIndex > text.length) {
        return new CellError(ErrorType.VALUE, ErrorMessage.IndexBounds);
      }
      const shiftedText = text.substring(startIndex - 1);
      const index2 = shiftedText.indexOf(pattern) + startIndex;
      return index2 > 0 ? index2 : new CellError(ErrorType.VALUE, ErrorMessage.PatternNotFound);
    });
  }
  t(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("T"), (arg) => {
      if (arg instanceof CellError) {
        return arg;
      }
      return typeof arg === "string" ? arg : "";
    });
  }
  /**
   * Corresponds to N(value)
   *
   * Converts a value to a number according to Excel specification:
   * - Numbers return themselves
   * - Dates return their serial number (stored as numbers internally)
   * - TRUE returns 1, FALSE returns 0
   * - Error values propagate
   * - Anything else (text, empty) returns 0
   * - For ranges, uses the first cell value
   */
  n(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("N"), (arg) => {
      var _a2;
      const value = arg instanceof SimpleRangeValue ? (_a2 = arg.data[0]) === null || _a2 === void 0 ? void 0 : _a2[0] : arg;
      if (value instanceof CellError) {
        return value;
      }
      if (typeof value === "number") {
        return value;
      }
      if (typeof value === "boolean") {
        return value ? 1 : 0;
      }
      return 0;
    });
  }
  upper(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("UPPER"), (arg) => {
      return arg.toUpperCase();
    });
  }
  /**
   * Corresponds to VALUE(text)
   *
   * Converts a text string that represents a number to a number.
   *
   * @param {ProcedureAst} ast - The procedure AST node
   * @param {InterpreterState} state - The interpreter state
   */
  value(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("VALUE"), (arg) => {
      if (arg instanceof CellError) {
        return arg;
      }
      if (isExtendedNumber(arg)) {
        return arg;
      }
      if (typeof arg !== "string") {
        return new CellError(ErrorType.VALUE, ErrorMessage.NumberCoercion);
      }
      const trimmedArg = arg.trim();
      const parenthesesMatch = /^\(([^()]+)\)$/.exec(trimmedArg);
      if (parenthesesMatch) {
        const innerValue = this.parseStringToNumber(parenthesesMatch[1]);
        if (innerValue !== void 0) {
          return -innerValue;
        }
      }
      const parsedValue = this.parseStringToNumber(trimmedArg);
      if (parsedValue !== void 0) {
        return parsedValue;
      }
      return new CellError(ErrorType.VALUE, ErrorMessage.NumberCoercion);
    });
  }
  /**
   * Corresponds to TEXTJOIN(delimiter, ignore_empty, text1, [text2], …)
   *
   * Joins text from multiple strings/ranges with a configurable delimiter.
   * Supports array/range delimiters that cycle through gaps between text values.
   * When ignore_empty is TRUE, empty strings are skipped.
   * Returns #VALUE! if the result exceeds 32,767 characters (Excel cell content limit).
   *
   * @param {ProcedureAst} ast - The procedure AST node
   * @param {InterpreterState} state - The interpreter state
   */
  textjoin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TEXTJOIN"), (delimiterArg, ignoreEmpty, ...textArgs) => {
      const delimiters = this.flattenArgToStrings(delimiterArg);
      if (delimiters instanceof CellError) {
        return delimiters;
      }
      const texts = [];
      for (const arg of textArgs) {
        const coerced = this.flattenArgToStrings(arg);
        if (coerced instanceof CellError) {
          return coerced;
        }
        texts.push(...coerced);
      }
      const parts = ignoreEmpty ? texts.filter((t2) => t2 !== "") : texts;
      if (parts.length === 0) {
        return "";
      }
      const result = parts.reduce((acc, part, i2) => i2 === 0 ? part : acc + delimiters[(i2 - 1) % delimiters.length] + part, "");
      if (result.length > 32767) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ResultTooLong);
      }
      return result;
    });
  }
  /**
   * Flattens a scalar or range argument into an array of coerced strings.
   * Returns a CellError immediately if any value in the argument is an error or cannot be coerced.
   *
   * @param {InternalScalarValue | SimpleRangeValue} arg - Scalar or range to flatten
   * @returns {string[] | CellError} - Array of string values, or the first error encountered
   */
  flattenArgToStrings(arg) {
    const values2 = arg instanceof SimpleRangeValue ? arg.valuesFromTopLeftCorner() : [arg];
    const result = [];
    for (const val of values2) {
      if (val instanceof CellError) {
        return val;
      }
      const coerced = coerceScalarToString(val);
      if (coerced instanceof CellError) {
        return coerced;
      }
      result.push(coerced);
    }
    return result;
  }
  /**
   * Parses a string to a numeric value, handling whitespace trimming and empty string validation.
   *
   * @param {string} input - The string to parse
   * @returns {Maybe<ExtendedNumber>} The parsed number or undefined if parsing fails or input is empty
   */
  parseStringToNumber(input) {
    const trimmedInput = input.trim();
    if (trimmedInput === "") {
      return void 0;
    }
    return this.arithmeticHelper.coerceToMaybeNumber(trimmedInput);
  }
  escapeRegExpSpecialCharacters(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
}
TextPlugin.implementedFunctions = {
  "CONCATENATE": {
    method: "concatenate",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }],
    repeatLastArgs: 1,
    expandRanges: true
  },
  "EXACT": {
    method: "exact",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "SPLIT": {
    method: "split",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "LEN": {
    method: "len",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "LOWER": {
    method: "lower",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "MID": {
    method: "mid",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "TRIM": {
    method: "trim",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "T": {
    method: "t",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "N": {
    method: "n",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }]
  },
  "PROPER": {
    method: "proper",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "CLEAN": {
    method: "clean",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "REPT": {
    method: "rept",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "RIGHT": {
    method: "right",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "LEFT": {
    method: "left",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "REPLACE": {
    method: "replace",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "SEARCH": {
    method: "search",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "SUBSTITUTE": {
    method: "substitute",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true
    }]
  },
  "FIND": {
    method: "find",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.STRING
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "UPPER": {
    method: "upper",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  },
  "VALUE": {
    method: "value",
    parameters: [{
      argumentType: FunctionArgumentType.SCALAR
    }]
  },
  "TEXTJOIN": {
    method: "textjoin",
    repeatLastArgs: 1,
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }, {
      argumentType: FunctionArgumentType.ANY
    }]
  }
};
class TrigonometryPlugin extends FunctionPlugin {
  /**
   * Corresponds to ACOS(value)
   *
   * Returns the arc cosine (or inverse cosine) of a number.
   *
   * @param ast
   * @param state
   */
  acos(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ACOS"), Math.acos);
  }
  asin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ASIN"), Math.asin);
  }
  cos(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COS"), Math.cos);
  }
  sin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SIN"), Math.sin);
  }
  tan(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TAN"), Math.tan);
  }
  atan(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ATAN"), Math.atan);
  }
  atan2(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ATAN2"), (x2, y2) => {
      if (x2 === 0 && y2 === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return Math.atan2(y2, x2);
    });
  }
  cot(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COT"), (arg) => arg === 0 ? new CellError(ErrorType.DIV_BY_ZERO) : 1 / Math.tan(arg));
  }
  acot(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ACOT"), (arg) => arg === 0 ? PI / 2 : Math.atan(1 / arg));
  }
  sec(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SEC"), (arg) => 1 / Math.cos(arg));
  }
  csc(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CSC"), (arg) => arg === 0 ? new CellError(ErrorType.DIV_BY_ZERO) : 1 / Math.sin(arg));
  }
  sinh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SINH"), Math.sinh);
  }
  asinh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ASINH"), Math.asinh);
  }
  cosh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COSH"), Math.cosh);
  }
  acosh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ACOSH"), Math.acosh);
  }
  tanh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TANH"), Math.tanh);
  }
  atanh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ATANH"), Math.atanh);
  }
  coth(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COTH"), (arg) => arg === 0 ? new CellError(ErrorType.DIV_BY_ZERO) : 1 / Math.tanh(arg));
  }
  acoth(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ACOTH"), (arg) => arg === 0 ? new CellError(ErrorType.NUM, ErrorMessage.NonZero) : Math.atanh(1 / arg));
  }
  sech(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SECH"), (arg) => 1 / Math.cosh(arg));
  }
  csch(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CSCH"), (arg) => arg === 0 ? new CellError(ErrorType.DIV_BY_ZERO) : 1 / Math.sinh(arg));
  }
}
TrigonometryPlugin.implementedFunctions = {
  "ACOS": {
    method: "acos",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ASIN": {
    method: "asin",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "COS": {
    method: "cos",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "SIN": {
    method: "sin",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "TAN": {
    method: "tan",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ATAN": {
    method: "atan",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ATAN2": {
    method: "atan2",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "COT": {
    method: "cot",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "SEC": {
    method: "sec",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "CSC": {
    method: "csc",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "SINH": {
    method: "sinh",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "COSH": {
    method: "cosh",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "TANH": {
    method: "tanh",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "COTH": {
    method: "coth",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "SECH": {
    method: "sech",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "CSCH": {
    method: "csch",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ACOT": {
    method: "acot",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ASINH": {
    method: "asinh",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ACOSH": {
    method: "acosh",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ATANH": {
    method: "atanh",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "ACOTH": {
    method: "acoth",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  }
};
class RowSearchStrategy extends AdvancedFind {
  constructor(dependencyGraph) {
    super(dependencyGraph);
    this.dependencyGraph = dependencyGraph;
  }
  /*
   * WARNING: Finding lower/upper bounds in unordered ranges is not supported. When ordering === 'none', assumes matchExactly === true
   */
  find(searchKey, rangeValue, searchOptions) {
    return this.basicFind(searchKey, rangeValue, "col", searchOptions);
  }
}
class LookupPlugin extends FunctionPlugin {
  constructor() {
    super(...arguments);
    this.rowSearch = new RowSearchStrategy(this.dependencyGraph);
  }
  /**
   * Corresponds to VLOOKUP(key, range, index, [sorted])
   *
   * @param ast
   * @param state
   */
  vlookup(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("VLOOKUP"), (key, rangeValue, index2, sorted) => {
      const range = rangeValue.range;
      if (range === void 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
      }
      if (index2 < 1) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      if (index2 > range.width()) {
        return new CellError(ErrorType.REF, ErrorMessage.IndexLarge);
      }
      const searchOptions = {
        ordering: sorted ? "asc" : "none",
        ifNoMatch: sorted ? "returnLowerBound" : "returnNotFound"
      };
      return this.doVlookup(zeroIfEmpty(key), rangeValue, index2 - 1, searchOptions);
    });
  }
  /**
   * Corresponds to HLOOKUP(key, range, index, [sorted])
   *
   * @param ast
   * @param state
   */
  hlookup(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HLOOKUP"), (key, rangeValue, index2, sorted) => {
      const range = rangeValue.range;
      if (range === void 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
      }
      if (index2 < 1) {
        return new CellError(ErrorType.VALUE, ErrorMessage.LessThanOne);
      }
      if (index2 > range.height()) {
        return new CellError(ErrorType.REF, ErrorMessage.IndexLarge);
      }
      const searchOptions = {
        ordering: sorted ? "asc" : "none",
        ifNoMatch: sorted ? "returnLowerBound" : "returnNotFound"
      };
      return this.doHlookup(zeroIfEmpty(key), rangeValue, index2 - 1, searchOptions);
    });
  }
  /**
   * Corresponds to XLOOKUP(lookup_value, lookup_array, return_array, [if_not_found], [match_mode], [search_mode])
   *
   * @param ast
   * @param state
   */
  xlookup(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("XLOOKUP"), (key, lookupRangeValue, returnRangeValue, notFoundFlag, matchMode, searchMode) => {
      if (![0, -1, 1, 2].includes(matchMode)) {
        return new CellError(ErrorType.VALUE, ErrorMessage.BadMode);
      }
      if (![1, -1, 2, -2].includes(searchMode)) {
        return new CellError(ErrorType.VALUE, ErrorMessage.BadMode);
      }
      const lookupRange = lookupRangeValue instanceof SimpleRangeValue ? lookupRangeValue : SimpleRangeValue.fromScalar(lookupRangeValue);
      const returnRange = returnRangeValue instanceof SimpleRangeValue ? returnRangeValue : SimpleRangeValue.fromScalar(returnRangeValue);
      const isWildcardMatchMode = matchMode === 2;
      const searchOptions = {
        ordering: searchMode === 2 ? "asc" : searchMode === -2 ? "desc" : "none",
        returnOccurrence: searchMode === -1 ? "last" : "first",
        ifNoMatch: matchMode === -1 ? "returnLowerBound" : matchMode === 1 ? "returnUpperBound" : "returnNotFound"
      };
      return this.doXlookup(zeroIfEmpty(key), lookupRange, returnRange, notFoundFlag, isWildcardMatchMode, searchOptions);
    });
  }
  xlookupArraySize(ast) {
    var _a2, _b;
    const lookupRange = (_a2 = ast === null || ast === void 0 ? void 0 : ast.args) === null || _a2 === void 0 ? void 0 : _a2[1];
    const returnRange = (_b = ast === null || ast === void 0 ? void 0 : ast.args) === null || _b === void 0 ? void 0 : _b[2];
    if ((lookupRange === null || lookupRange === void 0 ? void 0 : lookupRange.start) == null || (lookupRange === null || lookupRange === void 0 ? void 0 : lookupRange.end) == null || (returnRange === null || returnRange === void 0 ? void 0 : returnRange.start) == null || (returnRange === null || returnRange === void 0 ? void 0 : returnRange.end) == null) {
      return ArraySize.error();
    }
    const lookupRangeHeight = lookupRange.end.row - lookupRange.start.row + 1;
    const lookupRangeWidth = lookupRange.end.col - lookupRange.start.col + 1;
    const returnRangeHeight = returnRange.end.row - returnRange.start.row + 1;
    const returnRangeWidth = returnRange.end.col - returnRange.start.col + 1;
    const isVerticalSearch = lookupRangeWidth === 1 && returnRangeHeight === lookupRangeHeight;
    const isHorizontalSearch = lookupRangeHeight === 1 && returnRangeWidth === lookupRangeWidth;
    if (!isVerticalSearch && !isHorizontalSearch) {
      return ArraySize.error();
    }
    if (isVerticalSearch) {
      return new ArraySize(returnRangeWidth, 1);
    }
    return new ArraySize(1, returnRangeHeight);
  }
  match(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MATCH"), (key, rangeValue, type) => {
      return this.doMatch(zeroIfEmpty(key), rangeValue, type);
    });
  }
  searchInRange(key, range, isWildcardMatchMode, searchOptions, searchStrategy) {
    if (isWildcardMatchMode && typeof key === "string" && this.arithmeticHelper.requiresRegex(key)) {
      return searchStrategy.advancedFind(this.arithmeticHelper.eqMatcherFunction(key), range, {
        returnOccurrence: searchOptions.returnOccurrence
      });
    }
    return searchStrategy.find(key, range, searchOptions);
  }
  doVlookup(key, rangeValue, index2, searchOptions) {
    this.dependencyGraph.stats.start(StatType.VLOOKUP);
    const range = rangeValue.range;
    let searchedRange;
    if (range === void 0) {
      searchedRange = SimpleRangeValue.onlyValues(rangeValue.data.map((arg) => [arg[0]]));
    } else {
      searchedRange = SimpleRangeValue.onlyRange(AbsoluteCellRange.spanFrom(range.start, 1, range.height()), this.dependencyGraph);
    }
    const rowIndex = this.searchInRange(key, searchedRange, searchOptions.ordering === "none", searchOptions, this.columnSearch);
    this.dependencyGraph.stats.end(StatType.VLOOKUP);
    if (rowIndex === -1) {
      return new CellError(ErrorType.NA, ErrorMessage.ValueNotFound);
    }
    let value;
    if (range === void 0) {
      value = rangeValue.data[rowIndex][index2];
    } else {
      const address = simpleCellAddress(range.sheet, range.start.col + index2, range.start.row + rowIndex);
      value = this.dependencyGraph.getCellValue(address);
    }
    if (value instanceof SimpleRangeValue) {
      return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
    }
    return value;
  }
  doHlookup(key, rangeValue, index2, searchOptions) {
    const range = rangeValue.range;
    let searchedRange;
    if (range === void 0) {
      searchedRange = SimpleRangeValue.onlyValues([rangeValue.data[0]]);
    } else {
      searchedRange = SimpleRangeValue.onlyRange(AbsoluteCellRange.spanFrom(range.start, range.width(), 1), this.dependencyGraph);
    }
    const colIndex = this.searchInRange(key, searchedRange, searchOptions.ordering === "none", searchOptions, this.rowSearch);
    if (colIndex === -1) {
      return new CellError(ErrorType.NA, ErrorMessage.ValueNotFound);
    }
    let value;
    if (range === void 0) {
      value = rangeValue.data[index2][colIndex];
    } else {
      const address = simpleCellAddress(range.sheet, range.start.col + colIndex, range.start.row + index2);
      value = this.dependencyGraph.getCellValue(address);
    }
    if (value instanceof SimpleRangeValue) {
      return new CellError(ErrorType.VALUE, ErrorMessage.WrongType);
    }
    return value;
  }
  doXlookup(key, lookupRange, returnRange, notFoundFlag, isWildcardMatchMode, searchOptions) {
    const isVerticalSearch = lookupRange.width() === 1 && returnRange.height() === lookupRange.height();
    const isHorizontalSearch = lookupRange.height() === 1 && returnRange.width() === lookupRange.width();
    if (!isVerticalSearch && !isHorizontalSearch) {
      return new CellError(ErrorType.VALUE, ErrorMessage.WrongDimension);
    }
    const searchStrategy = isVerticalSearch ? this.columnSearch : this.rowSearch;
    const indexFound = this.searchInRange(key, lookupRange, isWildcardMatchMode, searchOptions, searchStrategy);
    if (indexFound === -1) {
      return notFoundFlag == ErrorType.NA ? new CellError(ErrorType.NA, ErrorMessage.ValueNotFound) : notFoundFlag;
    }
    const returnValues = isVerticalSearch ? [returnRange.data[indexFound]] : returnRange.data.map((row) => [row[indexFound]]);
    return SimpleRangeValue.onlyValues(returnValues);
  }
  doMatch(key, rangeValue, type) {
    if (![-1, 0, 1].includes(type)) {
      return new CellError(ErrorType.VALUE, ErrorMessage.BadMode);
    }
    if (rangeValue.width() > 1 && rangeValue.height() > 1) {
      return new CellError(ErrorType.NA);
    }
    const searchStrategy = rangeValue.width() === 1 ? this.columnSearch : this.rowSearch;
    const searchOptions = type === 0 ? {
      ordering: "none",
      ifNoMatch: "returnNotFound"
    } : {
      ordering: type === -1 ? "desc" : "asc",
      ifNoMatch: type === -1 ? "returnUpperBound" : "returnLowerBound"
    };
    const index2 = searchStrategy.find(key, rangeValue, searchOptions);
    if (index2 === -1) {
      return new CellError(ErrorType.NA, ErrorMessage.ValueNotFound);
    }
    return index2 + 1;
  }
}
LookupPlugin.implementedFunctions = {
  "VLOOKUP": {
    method: "vlookup",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.BOOLEAN,
      defaultValue: true
    }]
  },
  "HLOOKUP": {
    method: "hlookup",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.BOOLEAN,
      defaultValue: true
    }]
  },
  "XLOOKUP": {
    method: "xlookup",
    sizeOfResultArrayMethod: "xlookupArraySize",
    parameters: [
      // lookup_value
      {
        argumentType: FunctionArgumentType.NOERROR
      },
      // lookup_array
      {
        argumentType: FunctionArgumentType.RANGE
      },
      // return_array
      {
        argumentType: FunctionArgumentType.RANGE
      },
      // [if_not_found]
      {
        argumentType: FunctionArgumentType.SCALAR,
        optionalArg: true,
        defaultValue: ErrorType.NA
      },
      // [match_mode]
      {
        argumentType: FunctionArgumentType.NUMBER,
        optionalArg: true,
        defaultValue: 0
      },
      // [search_mode]
      {
        argumentType: FunctionArgumentType.NUMBER,
        optionalArg: true,
        defaultValue: 1
      }
    ]
  },
  "MATCH": {
    method: "match",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  }
};
class RomanPlugin extends FunctionPlugin {
  roman(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ROMAN"), (val, mode) => {
      val = Math.trunc(val);
      if (mode === false) {
        mode = 4;
      } else if (mode === true) {
        mode = 0;
      }
      mode = getRawValue(this.coerceScalarToNumberOrError(mode));
      if (mode instanceof CellError) {
        return mode;
      }
      mode = Math.trunc(mode);
      if (mode < 0) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ValueSmall);
      }
      if (mode > 4) {
        return new CellError(ErrorType.VALUE, ErrorMessage.ValueLarge);
      }
      return romanMode(val, mode);
    });
  }
  arabic(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ARABIC"), (inputString) => {
      inputString = inputString.trim().toUpperCase();
      let minusSign = false;
      if (inputString.startsWith("-")) {
        inputString = inputString.slice(1);
        minusSign = true;
        if (inputString === "") {
          return new CellError(ErrorType.VALUE, ErrorMessage.InvalidRoman);
        }
      }
      const work = {
        input: inputString,
        acc: 0
      };
      eatToken(work, {
        token: "MMM",
        val: 3e3
      }, {
        token: "MM",
        val: 2e3
      }, {
        token: "M",
        val: 1e3
      });
      eatToken(work, {
        token: "IM",
        val: 999
      }, {
        token: "VM",
        val: 995
      }, {
        token: "XM",
        val: 990
      }, {
        token: "LM",
        val: 950
      }, {
        token: "CM",
        val: 900
      });
      eatToken(work, {
        token: "D",
        val: 500
      }, {
        token: "ID",
        val: 499
      }, {
        token: "VD",
        val: 495
      }, {
        token: "XD",
        val: 490
      }, {
        token: "LD",
        val: 450
      }, {
        token: "CD",
        val: 400
      });
      eatToken(work, {
        token: "CCC",
        val: 300
      }, {
        token: "CC",
        val: 200
      }, {
        token: "C",
        val: 100
      });
      eatToken(work, {
        token: "IC",
        val: 99
      }, {
        token: "VC",
        val: 95
      }, {
        token: "XC",
        val: 90
      });
      eatToken(work, {
        token: "L",
        val: 50
      }, {
        token: "IL",
        val: 49
      }, {
        token: "VL",
        val: 45
      }, {
        token: "XL",
        val: 40
      });
      eatToken(work, {
        token: "XXX",
        val: 30
      }, {
        token: "XX",
        val: 20
      }, {
        token: "X",
        val: 10
      });
      eatToken(work, {
        token: "IX",
        val: 9
      });
      eatToken(work, {
        token: "V",
        val: 5
      }, {
        token: "IV",
        val: 4
      });
      eatToken(work, {
        token: "III",
        val: 3
      }, {
        token: "II",
        val: 2
      }, {
        token: "I",
        val: 1
      });
      if (work.input !== "") {
        return new CellError(ErrorType.VALUE, ErrorMessage.InvalidRoman);
      } else {
        return minusSign ? -work.acc : work.acc;
      }
    });
  }
}
RomanPlugin.implementedFunctions = {
  "ROMAN": {
    method: "roman",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1,
      lessThan: 4e3
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      optionalArg: true,
      defaultValue: 0
    }]
  },
  "ARABIC": {
    method: "arabic",
    parameters: [{
      argumentType: FunctionArgumentType.STRING
    }]
  }
};
function eatToken(inputAcc, ...tokens) {
  for (const token of tokens) {
    if (inputAcc.input.startsWith(token.token)) {
      inputAcc.input = inputAcc.input.slice(token.token.length);
      inputAcc.acc += token.val;
      break;
    }
  }
}
function romanMode(input, mode) {
  const work = {
    val: input % 1e3,
    acc: "M".repeat(Math.floor(input / 1e3))
  };
  if (mode === 4) {
    absorb(work, "IM", 999, 1e3);
    absorb(work, "ID", 499, 500);
  }
  if (mode >= 3) {
    absorb(work, "VM", 995, 1e3);
    absorb(work, "VD", 495, 500);
  }
  if (mode >= 2) {
    absorb(work, "XM", 990, 1e3);
    absorb(work, "XD", 490, 500);
  }
  if (mode >= 1) {
    absorb(work, "LM", 950, 1e3);
    absorb(work, "LD", 450, 500);
  }
  absorb(work, "CM", 900, 1e3);
  absorb(work, "CD", 400, 500);
  absorb(work, "D", 500, 900);
  work.acc += "C".repeat(Math.floor(work.val / 100));
  work.val %= 100;
  if (mode >= 2) {
    absorb(work, "IC", 99, 100);
    absorb(work, "IL", 49, 50);
  }
  if (mode >= 1) {
    absorb(work, "VC", 95, 100);
    absorb(work, "VL", 45, 50);
  }
  absorb(work, "XC", 90, 100);
  absorb(work, "XL", 40, 50);
  absorb(work, "L", 50, 90);
  work.acc += "X".repeat(Math.floor(work.val / 10));
  work.val %= 10;
  absorb(work, "IX", 9, 10);
  absorb(work, "IV", 4, 5);
  absorb(work, "V", 5, 9);
  work.acc += "I".repeat(work.val);
  return work.acc;
}
function absorb(valAcc, token, lower, upper) {
  if (valAcc.val >= lower && valAcc.val < upper) {
    valAcc.val -= lower;
    valAcc.acc += token;
  }
}
class SimpleArithmerticPlugin extends FunctionPlugin {
  add(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.ADD"), this.arithmeticHelper.addWithEpsilon);
  }
  concat(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.CONCAT"), this.arithmeticHelper.concat);
  }
  divide(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.DIVIDE"), this.arithmeticHelper.divide);
  }
  eq(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.EQ"), this.arithmeticHelper.eq);
  }
  gt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.GT"), this.arithmeticHelper.gt);
  }
  gte(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.GTE"), this.arithmeticHelper.geq);
  }
  lt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.LT"), this.arithmeticHelper.lt);
  }
  lte(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.LTE"), this.arithmeticHelper.leq);
  }
  minus(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.MINUS"), this.arithmeticHelper.subtract);
  }
  multiply(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.MULTIPLY"), this.arithmeticHelper.multiply);
  }
  ne(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.NE"), this.arithmeticHelper.neq);
  }
  pow(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.POW"), this.arithmeticHelper.pow);
  }
  uminus(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.UMINUS"), this.arithmeticHelper.unaryMinus);
  }
  upercent(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.UNARY_PERCENT"), this.arithmeticHelper.unaryPercent);
  }
  uplus(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HF.UPLUS"), this.arithmeticHelper.unaryPlus);
  }
}
SimpleArithmerticPlugin.implementedFunctions = {
  "HF.ADD": {
    method: "add",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  },
  "HF.CONCAT": {
    method: "concat",
    parameters: [{
      argumentType: FunctionArgumentType.STRING,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.STRING,
      passSubtype: true
    }]
  },
  "HF.DIVIDE": {
    method: "divide",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  },
  "HF.EQ": {
    method: "eq",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }]
  },
  "HF.GT": {
    method: "gt",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }]
  },
  "HF.GTE": {
    method: "gte",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }]
  },
  "HF.LT": {
    method: "lt",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }]
  },
  "HF.LTE": {
    method: "lte",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }]
  },
  "HF.MINUS": {
    method: "minus",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  },
  "HF.MULTIPLY": {
    method: "multiply",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  },
  "HF.NE": {
    method: "ne",
    parameters: [{
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NOERROR,
      passSubtype: true
    }]
  },
  "HF.POW": {
    method: "pow",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  },
  "HF.UMINUS": {
    method: "uminus",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  },
  "HF.UNARY_PERCENT": {
    method: "upercent",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  },
  "HF.UPLUS": {
    method: "uplus",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      passSubtype: true
    }]
  }
};
var M$2 = Math;
function _horner(arr, v2) {
  for (var i2 = 0, z2 = 0; i2 < arr.length; ++i2) {
    z2 = v2 * z2 + arr[i2];
  }
  return z2;
}
function _bessel_iter(x2, n2, f0, f1, sign) {
  if (n2 === 0) {
    return f0;
  }
  if (n2 === 1) {
    return f1;
  }
  var tdx = 2 / x2, f2 = f1;
  for (var o2 = 1; o2 < n2; ++o2) {
    f2 = f1 * o2 * tdx + sign * f0;
    f0 = f1;
    f1 = f2;
  }
  return f2;
}
function _bessel_wrap(bessel0, bessel1, name, nonzero, sign) {
  return function bessel(x2, n2) {
    if (nonzero) {
      if (x2 === 0) {
        return nonzero == 1 ? -Infinity : Infinity;
      } else if (x2 < 0) {
        return NaN;
      }
    }
    if (n2 === 0) {
      return bessel0(x2);
    }
    if (n2 === 1) {
      return bessel1(x2);
    }
    if (n2 < 0) {
      return NaN;
    }
    n2 |= 0;
    var b0 = bessel0(x2), b1 = bessel1(x2);
    return _bessel_iter(x2, n2, b0, b1, sign);
  };
}
var besselj = (function() {
  var W2 = 0.636619772;
  var b0_a1a = [57568490574, -13362590354, 6516196407e-1, -1121442418e-2, 77392.33017, -184.9052456].reverse();
  var b0_a2a = [57568490411, 1029532985, 9494680718e-3, 59272.64853, 267.8532712, 1].reverse();
  var b0_a1b = [1, -0.001098628627, 2734510407e-14, -2073370639e-15, 2093887211e-16].reverse();
  var b0_a2b = [-0.01562499995, 1430488765e-13, -6911147651e-15, 7621095161e-16, -934935152e-16].reverse();
  function bessel0(x2) {
    var a2 = 0, a1 = 0, a22 = 0, y2 = x2 * x2;
    if (x2 < 8) {
      a1 = _horner(b0_a1a, y2);
      a22 = _horner(b0_a2a, y2);
      a2 = a1 / a22;
    } else {
      var xx = x2 - 0.785398164;
      y2 = 64 / y2;
      a1 = _horner(b0_a1b, y2);
      a22 = _horner(b0_a2b, y2);
      a2 = M$2.sqrt(W2 / x2) * (M$2.cos(xx) * a1 - M$2.sin(xx) * a22 * 8 / x2);
    }
    return a2;
  }
  var b1_a1a = [72362614232, -7895059235, 2423968531e-1, -2972611439e-3, 15704.4826, -30.16036606].reverse();
  var b1_a2a = [144725228442, 2300535178, 1858330474e-2, 99447.43394, 376.9991397, 1].reverse();
  var b1_a1b = [1, 183105e-8, -3516396496e-14, 2457520174e-15, -240337019e-15].reverse();
  var b1_a2b = [0.04687499995, -2002690873e-13, 8449199096e-15, -88228987e-14, 105787412e-15].reverse();
  function bessel1(x2) {
    var a2 = 0, a1 = 0, a22 = 0, y2 = x2 * x2, xx = M$2.abs(x2) - 2.356194491;
    if (Math.abs(x2) < 8) {
      a1 = x2 * _horner(b1_a1a, y2);
      a22 = _horner(b1_a2a, y2);
      a2 = a1 / a22;
    } else {
      y2 = 64 / y2;
      a1 = _horner(b1_a1b, y2);
      a22 = _horner(b1_a2b, y2);
      a2 = M$2.sqrt(W2 / M$2.abs(x2)) * (M$2.cos(xx) * a1 - M$2.sin(xx) * a22 * 8 / M$2.abs(x2));
      if (x2 < 0) {
        a2 = -a2;
      }
    }
    return a2;
  }
  return function besselj2(x2, n2) {
    n2 = Math.round(n2);
    if (!isFinite(x2)) {
      return isNaN(x2) ? x2 : 0;
    }
    if (n2 < 0) {
      return (n2 % 2 ? -1 : 1) * besselj2(x2, -n2);
    }
    if (x2 < 0) {
      return (n2 % 2 ? -1 : 1) * besselj2(-x2, n2);
    }
    if (n2 === 0) {
      return bessel0(x2);
    }
    if (n2 === 1) {
      return bessel1(x2);
    }
    if (x2 === 0) {
      return 0;
    }
    var ret = 0;
    if (x2 > n2) {
      ret = _bessel_iter(x2, n2, bessel0(x2), bessel1(x2), -1);
    } else {
      var m2 = 2 * M$2.floor((n2 + M$2.floor(M$2.sqrt(40 * n2))) / 2);
      var jsum = false;
      var bjp = 0, sum2 = 0;
      var bj = 1, bjm = 0;
      var tox = 2 / x2;
      for (var j2 = m2; j2 > 0; j2--) {
        bjm = j2 * tox * bj - bjp;
        bjp = bj;
        bj = bjm;
        if (M$2.abs(bj) > 1e10) {
          bj *= 1e-10;
          bjp *= 1e-10;
          ret *= 1e-10;
          sum2 *= 1e-10;
        }
        if (jsum) {
          sum2 += bj;
        }
        jsum = !jsum;
        if (j2 == n2) {
          ret = bjp;
        }
      }
      sum2 = 2 * sum2 - bj;
      ret /= sum2;
    }
    return ret;
  };
})();
var bessely = (function() {
  var W2 = 0.636619772;
  var b0_a1a = [-2957821389, 7062834065, -5123598036e-1, 1087988129e-2, -86327.92757, 228.4622733].reverse();
  var b0_a2a = [40076544269, 7452499648e-1, 7189466438e-3, 47447.2647, 226.1030244, 1].reverse();
  var b0_a1b = [1, -0.001098628627, 2734510407e-14, -2073370639e-15, 2093887211e-16].reverse();
  var b0_a2b = [-0.01562499995, 1430488765e-13, -6911147651e-15, 7621095161e-16, -934945152e-16].reverse();
  function bessel0(x2) {
    var a2 = 0, a1 = 0, a22 = 0, y2 = x2 * x2, xx = x2 - 0.785398164;
    if (x2 < 8) {
      a1 = _horner(b0_a1a, y2);
      a22 = _horner(b0_a2a, y2);
      a2 = a1 / a22 + W2 * besselj(x2, 0) * M$2.log(x2);
    } else {
      y2 = 64 / y2;
      a1 = _horner(b0_a1b, y2);
      a22 = _horner(b0_a2b, y2);
      a2 = M$2.sqrt(W2 / x2) * (M$2.sin(xx) * a1 + M$2.cos(xx) * a22 * 8 / x2);
    }
    return a2;
  }
  var b1_a1a = [-4900604943e3, 127527439e4, -51534381390, 7349264551e-1, -4237922726e-3, 8511.937935].reverse();
  var b1_a2a = [249958057e5, 424441966400, 3733650367, 2245904002e-2, 102042.605, 354.9632885, 1].reverse();
  var b1_a1b = [1, 183105e-8, -3516396496e-14, 2457520174e-15, -240337019e-15].reverse();
  var b1_a2b = [0.04687499995, -2002690873e-13, 8449199096e-15, -88228987e-14, 105787412e-15].reverse();
  function bessel1(x2) {
    var a2 = 0, a1 = 0, a22 = 0, y2 = x2 * x2, xx = x2 - 2.356194491;
    if (x2 < 8) {
      a1 = x2 * _horner(b1_a1a, y2);
      a22 = _horner(b1_a2a, y2);
      a2 = a1 / a22 + W2 * (besselj(x2, 1) * M$2.log(x2) - 1 / x2);
    } else {
      y2 = 64 / y2;
      a1 = _horner(b1_a1b, y2);
      a22 = _horner(b1_a2b, y2);
      a2 = M$2.sqrt(W2 / x2) * (M$2.sin(xx) * a1 + M$2.cos(xx) * a22 * 8 / x2);
    }
    return a2;
  }
  return _bessel_wrap(bessel0, bessel1, "BESSELY", 1, -1);
})();
var besseli = (function() {
  var b0_a = [1, 3.5156229, 3.0899424, 1.2067492, 0.2659732, 0.0360768, 45813e-7].reverse();
  var b0_b = [0.39894228, 0.01328592, 225319e-8, -157565e-8, 916281e-8, -0.02057706, 0.02635537, -0.01647633, 392377e-8].reverse();
  function bessel0(x2) {
    if (x2 <= 3.75) {
      return _horner(b0_a, x2 * x2 / (3.75 * 3.75));
    }
    return M$2.exp(M$2.abs(x2)) / M$2.sqrt(M$2.abs(x2)) * _horner(b0_b, 3.75 / M$2.abs(x2));
  }
  var b1_a = [0.5, 0.87890594, 0.51498869, 0.15084934, 0.02658733, 301532e-8, 32411e-8].reverse();
  var b1_b = [0.39894228, -0.03988024, -362018e-8, 163801e-8, -0.01031555, 0.02282967, -0.02895312, 0.01787654, -420059e-8].reverse();
  function bessel1(x2) {
    if (x2 < 3.75) {
      return x2 * _horner(b1_a, x2 * x2 / (3.75 * 3.75));
    }
    return (x2 < 0 ? -1 : 1) * M$2.exp(M$2.abs(x2)) / M$2.sqrt(M$2.abs(x2)) * _horner(b1_b, 3.75 / M$2.abs(x2));
  }
  return function besseli2(x2, n2) {
    n2 = Math.round(n2);
    if (n2 === 0) {
      return bessel0(x2);
    }
    if (n2 === 1) {
      return bessel1(x2);
    }
    if (n2 < 0) {
      return NaN;
    }
    if (M$2.abs(x2) === 0) {
      return 0;
    }
    if (x2 == Infinity) {
      return Infinity;
    }
    var ret = 0, j2, tox = 2 / M$2.abs(x2), bip = 0, bi2 = 1, bim = 0;
    var m2 = 2 * M$2.round((n2 + M$2.round(M$2.sqrt(40 * n2))) / 2);
    for (j2 = m2; j2 > 0; j2--) {
      bim = j2 * tox * bi2 + bip;
      bip = bi2;
      bi2 = bim;
      if (M$2.abs(bi2) > 1e10) {
        bi2 *= 1e-10;
        bip *= 1e-10;
        ret *= 1e-10;
      }
      if (j2 == n2) {
        ret = bip;
      }
    }
    ret *= besseli2(x2, 0) / bi2;
    return x2 < 0 && n2 % 2 ? -ret : ret;
  };
})();
var besselk = (function() {
  var b0_a = [-0.57721566, 0.4227842, 0.23069756, 0.0348859, 262698e-8, 1075e-7, 74e-7].reverse();
  var b0_b = [1.25331414, -0.07832358, 0.02189568, -0.01062446, 587872e-8, -25154e-7, 53208e-8].reverse();
  function bessel0(x2) {
    if (x2 <= 2) {
      return -M$2.log(x2 / 2) * besseli(x2, 0) + _horner(b0_a, x2 * x2 / 4);
    }
    return M$2.exp(-x2) / M$2.sqrt(x2) * _horner(b0_b, 2 / x2);
  }
  var b1_a = [1, 0.15443144, -0.67278579, -0.18156897, -0.01919402, -110404e-8, -4686e-8].reverse();
  var b1_b = [1.25331414, 0.23498619, -0.0365562, 0.01504268, -780353e-8, 325614e-8, -68245e-8].reverse();
  function bessel1(x2) {
    if (x2 <= 2) {
      return M$2.log(x2 / 2) * besseli(x2, 1) + 1 / x2 * _horner(b1_a, x2 * x2 / 4);
    }
    return M$2.exp(-x2) / M$2.sqrt(x2) * _horner(b1_b, 2 / x2);
  }
  return _bessel_wrap(bessel0, bessel1, "BESSELK", 2, 1);
})();
function erf(x2) {
  const cof = [-1.3026537197817094, 0.6419697923564902, 0.019476473204185836, -0.00956151478680863, -946595344482036e-18, 366839497852761e-18, 42523324806907e-18, -20278578112534e-18, -1624290004647e-18, 130365583558e-17, 15626441722e-18, -85238095915e-18, 6529054439e-18, 5059343495e-18, -991364156e-18, -227365122e-18, 96467911e-18, 2394038e-18, -6886027e-18, 894487e-18, 313092e-18, -112708e-18, 381e-18, 7106e-18, -1523e-18, -94e-18, 121e-18, -28e-18];
  let j2 = cof.length - 1;
  let isneg = false;
  let d2 = 0;
  let dd = 0;
  let t2, ty, tmp, res;
  if (x2 === 0) {
    return 0;
  }
  if (x2 < 0) {
    x2 = -x2;
    isneg = true;
  }
  t2 = 2 / (2 + x2);
  ty = 4 * t2 - 2;
  for (; j2 > 0; j2--) {
    tmp = d2;
    d2 = ty * d2 - dd + cof[j2];
    dd = tmp;
  }
  res = t2 * Math.exp(-x2 * x2 + 0.5 * (cof[0] + ty * d2) - dd);
  return isneg ? res - 1 : 1 - res;
}
function erfc(x2) {
  return 1 - erf(x2);
}
function erfcinv(p2) {
  let j2 = 0;
  let x2, err, t2, pp;
  if (p2 >= 2) {
    return -100;
  }
  if (p2 <= 0) {
    return 100;
  }
  pp = p2 < 1 ? p2 : 2 - p2;
  t2 = Math.sqrt(-2 * Math.log(pp / 2));
  x2 = -0.70711 * ((2.30753 + t2 * 0.27061) / (1 + t2 * (0.99229 + t2 * 0.04481)) - t2);
  for (; j2 < 2; j2++) {
    err = erfc(x2) - pp;
    x2 += err / (1.1283791670955126 * Math.exp(-x2 * x2) - x2 * err);
  }
  return p2 < 1 ? x2 : -x2;
}
const exponential = {
  pdf: (x2, rate) => {
    return x2 < 0 ? 0 : rate * Math.exp(-rate * x2);
  },
  cdf: (x2, rate) => {
    return x2 < 0 ? 0 : 1 - Math.exp(-rate * x2);
  }
};
function gammafn(x2) {
  const p2 = [-1.716185138865495, 24.76565080557592, -379.80425647094563, 629.3311553128184, 866.9662027904133, -31451.272968848367, -36144.413418691176, 66456.14382024054];
  const q2 = [-30.8402300119739, 315.35062697960416, -1015.1563674902192, -3107.771671572311, 22538.11842098015, 4755.846277527881, -134659.9598649693, -115132.2596755535];
  let fact = false;
  let n2 = 0;
  let xden = 0;
  let xnum = 0;
  let y2 = x2;
  let i2, z2, yi2, res;
  if (x2 > 171.6243769536076) {
    return Infinity;
  }
  if (y2 <= 0) {
    res = y2 % 1;
    if (res) {
      fact = (!(y2 & 1) ? 1 : -1) * Math.PI / Math.sin(Math.PI * res);
      y2 = 1 - y2;
    } else {
      return Infinity;
    }
  }
  yi2 = y2;
  if (y2 < 1) {
    z2 = y2++;
  } else {
    z2 = (y2 -= n2 = (y2 | 0) - 1) - 1;
  }
  for (i2 = 0; i2 < 8; ++i2) {
    xnum = (xnum + p2[i2]) * z2;
    xden = xden * z2 + q2[i2];
  }
  res = xnum / xden + 1;
  if (yi2 < y2) {
    res /= yi2;
  } else if (yi2 > y2) {
    for (i2 = 0; i2 < n2; ++i2) {
      res *= y2;
      y2++;
    }
  }
  if (fact) {
    res = fact / res;
  }
  return res;
}
const gamma = {
  pdf: function pdf(x2, shape, scale) {
    if (x2 < 0) {
      return 0;
    }
    return x2 === 0 && shape === 1 ? 1 / scale : Math.exp((shape - 1) * Math.log(x2) - x2 / scale - gammaln(shape) - shape * Math.log(scale));
  },
  cdf: function cdf(x2, shape, scale) {
    if (x2 < 0) {
      return 0;
    }
    return lowRegGamma(shape, x2 / scale);
  },
  inv: function(p2, shape, scale) {
    return gammapinv(p2, shape) * scale;
  }
};
function gammaln(x2) {
  let j2 = 0;
  const cof = [76.18009172947146, -86.50532032941678, 24.01409824083091, -1.231739572450155, 0.001208650973866179, -5395239384953e-18];
  let ser = 1.000000000190015;
  let xx, y2, tmp;
  tmp = (y2 = xx = x2) + 5.5;
  tmp -= (xx + 0.5) * Math.log(tmp);
  for (; j2 < 6; j2++) {
    ser += cof[j2] / ++y2;
  }
  return Math.log(2.5066282746310007 * ser / xx) - tmp;
}
function lowRegGamma(a2, x2) {
  const aln = gammaln(a2);
  let ap = a2;
  let sum2 = 1 / a2;
  let del = sum2;
  let b2 = x2 + 1 - a2;
  let c2 = 1 / 1e-30;
  let d2 = 1 / b2;
  let h2 = d2;
  let i2 = 1;
  const ITMAX = -~(Math.log(a2 >= 1 ? a2 : 1 / a2) * 8.5 + a2 * 0.4 + 17);
  let an2;
  if (x2 < 0 || a2 <= 0) {
    return NaN;
  } else if (x2 < a2 + 1) {
    for (; i2 <= ITMAX; i2++) {
      sum2 += del *= x2 / ++ap;
    }
    return sum2 * Math.exp(-x2 + a2 * Math.log(x2) - aln);
  }
  for (; i2 <= ITMAX; i2++) {
    an2 = -i2 * (i2 - a2);
    b2 += 2;
    d2 = an2 * d2 + b2;
    c2 = b2 + an2 / c2;
    d2 = 1 / d2;
    h2 *= d2 * c2;
  }
  return 1 - h2 * Math.exp(-x2 + a2 * Math.log(x2) - aln);
}
function gammapinv(p2, a2) {
  let j2 = 0;
  const a1 = a2 - 1;
  const EPS = 1e-8;
  const gln = gammaln(a2);
  let x2, err, t2, u2, pp;
  let lna1;
  let afac;
  if (p2 >= 1) {
    return Math.max(100, a2 + 100 * Math.sqrt(a2));
  }
  if (p2 <= 0) {
    return 0;
  }
  if (a2 > 1) {
    lna1 = Math.log(a1);
    afac = Math.exp(a1 * (lna1 - 1) - gln);
    pp = p2 < 0.5 ? p2 : 1 - p2;
    t2 = Math.sqrt(-2 * Math.log(pp));
    x2 = (2.30753 + t2 * 0.27061) / (1 + t2 * (0.99229 + t2 * 0.04481)) - t2;
    if (p2 < 0.5) {
      x2 = -x2;
    }
    x2 = Math.max(1e-3, a2 * Math.pow(1 - 1 / (9 * a2) - x2 / (3 * Math.sqrt(a2)), 3));
  } else {
    t2 = 1 - a2 * (0.253 + a2 * 0.12);
    if (p2 < t2) {
      x2 = Math.pow(p2 / t2, 1 / a2);
    } else {
      x2 = 1 - Math.log(1 - (p2 - t2) / (1 - t2));
    }
  }
  for (; j2 < 12; j2++) {
    if (x2 <= 0) {
      return 0;
    }
    err = lowRegGamma(a2, x2) - p2;
    if (a2 > 1) {
      t2 = afac * Math.exp(-(x2 - a1) + a1 * (Math.log(x2) - lna1));
    } else {
      t2 = Math.exp(-x2 + a1 * Math.log(x2) - gln);
    }
    u2 = err / t2;
    x2 -= t2 = u2 / (1 - 0.5 * Math.min(1, u2 * ((a2 - 1) / x2 - 1)));
    if (x2 <= 0) {
      x2 = 0.5 * (x2 + t2);
    }
    if (Math.abs(t2) < EPS * x2) {
      break;
    }
  }
  return x2;
}
const normal = {
  pdf: function pdf2(x2, mean2, std2) {
    return Math.exp(-0.5 * Math.log(2 * Math.PI) - Math.log(std2) - Math.pow(x2 - mean2, 2) / (2 * std2 * std2));
  },
  cdf: function cdf2(x2, mean2, std2) {
    return 0.5 * (1 + erf((x2 - mean2) / Math.sqrt(2 * std2 * std2)));
  },
  inv: function(p2, mean2, std2) {
    return -1.4142135623730951 * std2 * erfcinv(2 * p2) + mean2;
  }
};
const beta = {
  pdf: function pdf3(x2, alpha, beta2) {
    if (x2 > 1 || x2 < 0) {
      return 0;
    }
    if (alpha == 1 && beta2 == 1) {
      return 1;
    }
    if (alpha < 512 && beta2 < 512) {
      return Math.pow(x2, alpha - 1) * Math.pow(1 - x2, beta2 - 1) / betafn(alpha, beta2);
    } else {
      return Math.exp((alpha - 1) * Math.log(x2) + (beta2 - 1) * Math.log(1 - x2) - betaln(alpha, beta2));
    }
  },
  cdf: function cdf3(x2, alpha, beta2) {
    return x2 > 1 || x2 < 0 ? +(x2 > 1) : ibeta(x2, alpha, beta2);
  },
  inv: function inv(x2, alpha, beta2) {
    return ibetainv(x2, alpha, beta2);
  }
};
function betafn(x2, y2) {
  if (x2 <= 0 || y2 <= 0) {
    return void 0;
  }
  return x2 + y2 > 170 ? Math.exp(betaln(x2, y2)) : gammafn(x2) * gammafn(y2) / gammafn(x2 + y2);
}
function betaln(x2, y2) {
  return gammaln(x2) + gammaln(y2) - gammaln(x2 + y2);
}
function ibetainv(p2, a2, b2) {
  var EPS = 1e-8;
  var a1 = a2 - 1;
  var b1 = b2 - 1;
  var j2 = 0;
  var lna, lnb, pp, t2, u2, err, x2, al, h2, w2, afac;
  if (p2 <= 0) {
    return 0;
  }
  if (p2 >= 1) {
    return 1;
  }
  if (a2 >= 1 && b2 >= 1) {
    pp = p2 < 0.5 ? p2 : 1 - p2;
    t2 = Math.sqrt(-2 * Math.log(pp));
    x2 = (2.30753 + t2 * 0.27061) / (1 + t2 * (0.99229 + t2 * 0.04481)) - t2;
    if (p2 < 0.5) {
      x2 = -x2;
    }
    al = (x2 * x2 - 3) / 6;
    h2 = 2 / (1 / (2 * a2 - 1) + 1 / (2 * b2 - 1));
    w2 = x2 * Math.sqrt(al + h2) / h2 - (1 / (2 * b2 - 1) - 1 / (2 * a2 - 1)) * (al + 5 / 6 - 2 / (3 * h2));
    x2 = a2 / (a2 + b2 * Math.exp(2 * w2));
  } else {
    lna = Math.log(a2 / (a2 + b2));
    lnb = Math.log(b2 / (a2 + b2));
    t2 = Math.exp(a2 * lna) / a2;
    u2 = Math.exp(b2 * lnb) / b2;
    w2 = t2 + u2;
    if (p2 < t2 / w2) {
      x2 = Math.pow(a2 * w2 * p2, 1 / a2);
    } else {
      x2 = 1 - Math.pow(b2 * w2 * (1 - p2), 1 / b2);
    }
  }
  afac = -gammaln(a2) - gammaln(b2) + gammaln(a2 + b2);
  for (; j2 < 10; j2++) {
    if (x2 === 0 || x2 === 1) {
      return x2;
    }
    err = ibeta(x2, a2, b2) - p2;
    t2 = Math.exp(a1 * Math.log(x2) + b1 * Math.log(1 - x2) + afac);
    u2 = err / t2;
    x2 -= t2 = u2 / (1 - 0.5 * Math.min(1, u2 * (a1 / x2 - b1 / (1 - x2))));
    if (x2 <= 0) {
      x2 = 0.5 * (x2 + t2);
    }
    if (x2 >= 1) {
      x2 = 0.5 * (x2 + t2 + 1);
    }
    if (Math.abs(t2) < EPS * x2 && j2 > 0) {
      break;
    }
  }
  return x2;
}
function ibeta(x2, a2, b2) {
  var bt2 = x2 === 0 || x2 === 1 ? 0 : Math.exp(gammaln(a2 + b2) - gammaln(a2) - gammaln(b2) + a2 * Math.log(x2) + b2 * Math.log(1 - x2));
  if (x2 < 0 || x2 > 1) {
    return false;
  }
  if (x2 < (a2 + 1) / (a2 + b2 + 2)) {
    return bt2 * betacf(x2, a2, b2) / a2;
  }
  return 1 - bt2 * betacf(1 - x2, b2, a2) / b2;
}
function betacf(x2, a2, b2) {
  var fpmin = 1e-30;
  var m2 = 1;
  var qab = a2 + b2;
  var qap = a2 + 1;
  var qam = a2 - 1;
  var c2 = 1;
  var d2 = 1 - qab * x2 / qap;
  var m22, aa2, del, h2;
  if (Math.abs(d2) < fpmin) {
    d2 = fpmin;
  }
  d2 = 1 / d2;
  h2 = d2;
  for (; m2 <= 100; m2++) {
    m22 = 2 * m2;
    aa2 = m2 * (b2 - m2) * x2 / ((qam + m22) * (a2 + m22));
    d2 = 1 + aa2 * d2;
    if (Math.abs(d2) < fpmin) {
      d2 = fpmin;
    }
    c2 = 1 + aa2 / c2;
    if (Math.abs(c2) < fpmin) {
      c2 = fpmin;
    }
    d2 = 1 / d2;
    h2 *= d2 * c2;
    aa2 = -(a2 + m2) * (qab + m2) * x2 / ((a2 + m22) * (qap + m22));
    d2 = 1 + aa2 * d2;
    if (Math.abs(d2) < fpmin) {
      d2 = fpmin;
    }
    c2 = 1 + aa2 / c2;
    if (Math.abs(c2) < fpmin) {
      c2 = fpmin;
    }
    d2 = 1 / d2;
    del = d2 * c2;
    h2 *= del;
    if (Math.abs(del - 1) < 3e-7) {
      break;
    }
  }
  return h2;
}
const binomial = {
  pdf: function(k2, n2, p2) {
    return p2 === 0 || p2 === 1 ? n2 * p2 === k2 ? 1 : 0 : combination(n2, k2) * Math.pow(p2, k2) * Math.pow(1 - p2, n2 - k2);
  },
  cdf: function(x2, n2, p2) {
    var betacdf;
    var eps = 1e-10;
    if (x2 < 0) {
      return 0;
    }
    if (x2 >= n2) {
      return 1;
    }
    if (p2 < 0 || p2 > 1 || n2 <= 0) {
      return NaN;
    }
    x2 = Math.floor(x2);
    var z2 = p2;
    var a2 = x2 + 1;
    var b2 = n2 - x2;
    var s2 = a2 + b2;
    var bt2 = Math.exp(gammaln(s2) - gammaln(b2) - gammaln(a2) + a2 * Math.log(z2) + b2 * Math.log(1 - z2));
    if (z2 < (a2 + 1) / (s2 + 2)) {
      betacdf = bt2 * betinc(z2, a2, b2, eps);
    } else {
      betacdf = 1 - bt2 * betinc(1 - z2, b2, a2, eps);
    }
    return Math.round((1 - betacdf) * (1 / eps)) / (1 / eps);
  }
};
function betinc(x2, a2, b2, eps) {
  var a0 = 0;
  var b0 = 1;
  var a1 = 1;
  var b1 = 1;
  var m9 = 0;
  var a22 = 0;
  var c9;
  while (Math.abs((a1 - a22) / a1) > eps) {
    a22 = a1;
    c9 = -(a2 + m9) * (a2 + b2 + m9) * x2 / (a2 + 2 * m9) / (a2 + 2 * m9 + 1);
    a0 = a1 + c9 * a0;
    b0 = b1 + c9 * b0;
    m9 = m9 + 1;
    c9 = m9 * (b2 - m9) * x2 / (a2 + 2 * m9 - 1) / (a2 + 2 * m9);
    a1 = a0 + c9 * a1;
    b1 = b0 + c9 * b1;
    a0 = a0 / b1;
    b0 = b0 / b1;
    a1 = a1 / b1;
    b1 = 1;
  }
  return a1 / a2;
}
function combination(n2, m2) {
  return n2 > 170 || m2 > 170 ? Math.exp(combinationln(n2, m2)) : factorial(n2) / factorial(m2) / factorial(n2 - m2);
}
function combinationln(n2, m2) {
  return factorialln(n2) - factorialln(m2) - factorialln(n2 - m2);
}
function factorialln(n2) {
  return n2 < 0 ? NaN : gammaln(n2 + 1);
}
function factorial(n2) {
  return n2 < 0 ? NaN : gammafn(n2 + 1);
}
const chisquare = {
  pdf: function pdf4(x2, dof) {
    if (x2 < 0) {
      return 0;
    }
    return x2 === 0 && dof === 2 ? 0.5 : Math.exp((dof / 2 - 1) * Math.log(x2) - x2 / 2 - dof / 2 * Math.log(2) - gammaln(dof / 2));
  },
  cdf: function cdf4(x2, dof) {
    if (x2 < 0) {
      return 0;
    }
    return lowRegGamma(dof / 2, x2 / 2);
  },
  inv: function(p2, dof) {
    return 2 * gammapinv(p2, 0.5 * dof);
  }
};
const centralF = {
  // This implementation of the pdf function avoids float overflow
  // See the way that R calculates this value:
  // https://svn.r-project.org/R/trunk/src/nmath/df.c
  pdf: function pdf5(x2, df1, df2) {
    var p2, q2, f2;
    if (x2 < 0) {
      return 0;
    }
    if (df1 <= 2) {
      if (x2 === 0 && df1 < 2) {
        return Infinity;
      }
      if (x2 === 0 && df1 === 2) {
        return 1;
      }
      return 1 / betafn(df1 / 2, df2 / 2) * Math.pow(df1 / df2, df1 / 2) * Math.pow(x2, df1 / 2 - 1) * Math.pow(1 + df1 / df2 * x2, -(df1 + df2) / 2);
    }
    p2 = df1 * x2 / (df2 + x2 * df1);
    q2 = df2 / (df2 + x2 * df1);
    f2 = df1 * q2 / 2;
    return f2 * binomial.pdf((df1 - 2) / 2, (df1 + df2 - 2) / 2, p2);
  },
  cdf: function cdf5(x2, df1, df2) {
    if (x2 < 0) {
      return 0;
    }
    return ibeta(df1 * x2 / (df1 * x2 + df2), df1 / 2, df2 / 2);
  },
  inv: function inv2(x2, df1, df2) {
    return df2 / (df1 * (1 / ibetainv(x2, df1 / 2, df2 / 2) - 1));
  }
};
const weibull = {
  pdf: function pdf6(x2, scale, shape) {
    if (x2 < 0 || scale < 0 || shape < 0) {
      return 0;
    }
    return shape / scale * Math.pow(x2 / scale, shape - 1) * Math.exp(-Math.pow(x2 / scale, shape));
  },
  cdf: function cdf6(x2, scale, shape) {
    return x2 < 0 ? 0 : 1 - Math.exp(-Math.pow(x2 / scale, shape));
  }
};
const poisson = {
  pdf: function pdf7(k2, l2) {
    if (l2 < 0 || k2 % 1 !== 0 || k2 < 0) {
      return 0;
    }
    return Math.pow(l2, k2) * Math.exp(-l2) / factorial(k2);
  },
  cdf: function cdf7(x2, l2) {
    var k2 = 0;
    if (x2 < 0) {
      return 0;
    }
    var sum2 = 0;
    for (; k2 <= x2; k2++) {
      sum2 += poisson.pdf(k2, l2);
    }
    return sum2;
  }
};
const hypgeom = {
  pdf: function pdf8(k2, N2, m2, n2) {
    if (k2 < 0 || k2 < m2 - (N2 - n2)) {
      return 0;
    } else if (k2 > n2 || k2 > m2) {
      return 0;
    } else if (m2 * 2 > N2) {
      if (n2 * 2 > N2) {
        return hypgeom.pdf(N2 - m2 - n2 + k2, N2, N2 - m2, N2 - n2);
      } else {
        return hypgeom.pdf(n2 - k2, N2, N2 - m2, n2);
      }
    } else if (n2 * 2 > N2) {
      return hypgeom.pdf(m2 - k2, N2, m2, N2 - n2);
    } else if (m2 < n2) {
      return hypgeom.pdf(k2, N2, n2, m2);
    } else {
      var scaledPDF = 1;
      var samplesDone = 0;
      for (var i2 = 0; i2 < k2; i2++) {
        while (scaledPDF > 1 && samplesDone < n2) {
          scaledPDF *= 1 - m2 / (N2 - samplesDone);
          samplesDone++;
        }
        scaledPDF *= (n2 - i2) * (m2 - i2) / ((i2 + 1) * (N2 - m2 - n2 + i2 + 1));
      }
      for (; samplesDone < n2; samplesDone++) {
        scaledPDF *= 1 - m2 / (N2 - samplesDone);
      }
      return Math.min(1, Math.max(0, scaledPDF));
    }
  },
  cdf: function cdf8(x2, N2, m2, n2) {
    if (x2 < 0 || x2 < m2 - (N2 - n2)) {
      return 0;
    } else if (x2 >= n2 || x2 >= m2) {
      return 1;
    } else if (m2 * 2 > N2) {
      if (n2 * 2 > N2) {
        return cdf8(N2 - m2 - n2 + x2, N2, N2 - m2, N2 - n2);
      } else {
        return 1 - hypgeom.cdf(n2 - x2 - 1, N2, N2 - m2, n2);
      }
    } else if (n2 * 2 > N2) {
      return 1 - hypgeom.cdf(m2 - x2 - 1, N2, m2, N2 - n2);
    } else if (m2 < n2) {
      return hypgeom.cdf(x2, N2, n2, m2);
    } else {
      var scaledCDF = 1;
      var scaledPDF = 1;
      var samplesDone = 0;
      for (var i2 = 0; i2 < x2; i2++) {
        while (scaledCDF > 1 && samplesDone < n2) {
          var factor = 1 - m2 / (N2 - samplesDone);
          scaledPDF *= factor;
          scaledCDF *= factor;
          samplesDone++;
        }
        scaledPDF *= (n2 - i2) * (m2 - i2) / ((i2 + 1) * (N2 - m2 - n2 + i2 + 1));
        scaledCDF += scaledPDF;
      }
      for (; samplesDone < n2; samplesDone++) {
        scaledCDF *= 1 - m2 / (N2 - samplesDone);
      }
      return Math.min(1, Math.max(0, scaledCDF));
    }
  }
};
const studentt = {
  pdf: function pdf9(x2, dof) {
    dof = dof > 1e100 ? 1e100 : dof;
    return 1 / (Math.sqrt(dof) * betafn(0.5, dof / 2)) * Math.pow(1 + x2 * x2 / dof, -((dof + 1) / 2));
  },
  cdf: function cdf9(x2, dof) {
    var dof2 = dof / 2;
    return ibeta((x2 + Math.sqrt(x2 * x2 + dof)) / (2 * Math.sqrt(x2 * x2 + dof)), dof2, dof2);
  },
  inv: function(p2, dof) {
    var x2 = ibetainv(2 * Math.min(p2, 1 - p2), 0.5 * dof, 0.5);
    x2 = Math.sqrt(dof * (1 - x2) / x2);
    return p2 > 0.5 ? x2 : -x2;
  }
};
const lognormal = {
  pdf: function pdf10(x2, mu, sigma) {
    if (x2 <= 0) {
      return 0;
    }
    return Math.exp(-Math.log(x2) - 0.5 * Math.log(2 * Math.PI) - Math.log(sigma) - Math.pow(Math.log(x2) - mu, 2) / (2 * sigma * sigma));
  },
  cdf: function cdf10(x2, mu, sigma) {
    if (x2 < 0) {
      return 0;
    }
    return 0.5 + 0.5 * erf((Math.log(x2) - mu) / Math.sqrt(2 * sigma * sigma));
  },
  inv: function(p2, mu, sigma) {
    return Math.exp(-1.4142135623730951 * sigma * erfcinv(2 * p2) + mu);
  }
};
const negbin = {
  pdf: function pdf11(k2, r2, p2) {
    if (k2 !== k2 >>> 0) {
      return false;
    }
    if (k2 < 0) {
      return 0;
    }
    return combination(k2 + r2 - 1, r2 - 1) * Math.pow(1 - p2, k2) * Math.pow(p2, r2);
  },
  cdf: function cdf11(x2, r2, p2) {
    var sum2 = 0, k2 = 0;
    if (x2 < 0) {
      return 0;
    }
    for (; k2 <= x2; k2++) {
      sum2 += negbin.pdf(k2, r2, p2);
    }
    return sum2;
  }
};
function sum(arr) {
  var sum2 = 0;
  var i2 = arr.length;
  while (--i2 >= 0) {
    sum2 += arr[i2];
  }
  return sum2;
}
function mean$1(arr) {
  return sum(arr) / arr.length;
}
function sumsqerr(arr) {
  var meanv = mean$1(arr);
  var sum2 = 0;
  var i2 = arr.length;
  var tmp;
  while (--i2 >= 0) {
    tmp = arr[i2] - meanv;
    sum2 += tmp * tmp;
  }
  return sum2;
}
function variance$1(arr, flag) {
  return sumsqerr(arr) / (arr.length - (flag ? 1 : 0));
}
function stdev(arr, flag) {
  return Math.sqrt(variance$1(arr, flag));
}
function normalci() {
  var args = [].slice.call(arguments), ans = new Array(2), change;
  if (args.length === 4) {
    change = Math.abs(normal.inv(args[1] / 2, 0, 1) * args[2] / Math.sqrt(args[3]));
  } else {
    change = Math.abs(normal.inv(args[1] / 2, 0, 1) * stdev(args[2]) / Math.sqrt(args[2].length));
  }
  ans[0] = args[0] - change;
  ans[1] = args[0] + change;
  return ans;
}
function tci() {
  var args = [].slice.call(arguments), ans = new Array(2), change;
  if (args.length === 4) {
    change = Math.abs(studentt.inv(args[1] / 2, args[3] - 1) * args[2] / Math.sqrt(args[3]));
  } else {
    change = Math.abs(studentt.inv(args[1] / 2, args[2].length - 1) * stdev(args[2], true) / Math.sqrt(args[2].length));
  }
  ans[0] = args[0] - change;
  ans[1] = args[0] + change;
  return ans;
}
function product(arr) {
  var prod = 1;
  var i2 = arr.length;
  while (--i2 >= 0) {
    prod *= arr[i2];
  }
  return prod;
}
function geomean(arr) {
  return Math.pow(product(arr), 1 / arr.length);
}
function covariance(arr1, arr2) {
  var u2 = mean$1(arr1);
  var v2 = mean$1(arr2);
  var arr1Len = arr1.length;
  var sq_dev = new Array(arr1Len);
  var i2;
  for (i2 = 0; i2 < arr1Len; i2++) {
    sq_dev[i2] = (arr1[i2] - u2) * (arr2[i2] - v2);
  }
  return sum(sq_dev) / (arr1Len - 1);
}
function corrcoeff(arr1, arr2) {
  return covariance(arr1, arr2) / stdev(arr1, 1) / stdev(arr2, 1);
}
class StatisticalPlugin extends FunctionPlugin {
  erf(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ERF"), (lowerBound2, upperBound) => {
      if (upperBound === void 0) {
        return erf(lowerBound2);
      } else {
        return erf(upperBound) - erf(lowerBound2);
      }
    });
  }
  erfc(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("ERFC"), erfc);
  }
  expondist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("EXPON.DIST"), (x2, lambda, cumulative) => {
      if (cumulative) {
        return exponential.cdf(x2, lambda);
      } else {
        return exponential.pdf(x2, lambda);
      }
    });
  }
  fisher(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FISHER"), (x2) => Math.log((1 + x2) / (1 - x2)) / 2);
  }
  fisherinv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FISHERINV"), (y2) => 1 - 2 / (Math.exp(2 * y2) + 1));
  }
  gamma(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GAMMA"), gammafn);
  }
  gammadist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GAMMA.DIST"), (value, alphaVal, betaVal, cumulative) => {
      if (cumulative) {
        return gamma.cdf(value, alphaVal, betaVal);
      } else {
        return gamma.pdf(value, alphaVal, betaVal);
      }
    });
  }
  gammaln(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GAMMALN"), gammaln);
  }
  gammainv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GAMMA.INV"), gamma.inv);
  }
  gauss(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GAUSS"), (z2) => normal.cdf(z2, 0, 1) - 0.5);
  }
  betadist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BETA.DIST"), (x2, alphaVal, betaVal, cumulative, A2, B2) => {
      if (x2 <= A2) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
      } else if (x2 >= B2) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      x2 = (x2 - A2) / (B2 - A2);
      if (cumulative) {
        return beta.cdf(x2, alphaVal, betaVal);
      } else {
        return beta.pdf(x2, alphaVal, betaVal);
      }
    });
  }
  betainv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BETA.INV"), (x2, alphaVal, betaVal, A2, B2) => {
      if (A2 >= B2) {
        return new CellError(ErrorType.NUM, ErrorMessage.WrongOrder);
      } else {
        return beta.inv(x2, alphaVal, betaVal) * (B2 - A2) + A2;
      }
    });
  }
  binomialdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BINOM.DIST"), (succ, trials, prob, cumulative) => {
      if (succ > trials) {
        return new CellError(ErrorType.NUM, ErrorMessage.WrongOrder);
      }
      succ = Math.trunc(succ);
      trials = Math.trunc(trials);
      if (cumulative) {
        return binomial.cdf(succ, trials, prob);
      } else {
        return binomial.pdf(succ, trials, prob);
      }
    });
  }
  binomialinv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BINOM.INV"), (trials, prob, alpha) => {
      trials = Math.trunc(trials);
      let lower = -1;
      let upper = trials;
      while (upper > lower + 1) {
        const mid = Math.trunc((lower + upper) / 2);
        if (binomial.cdf(mid, trials, prob) >= alpha) {
          upper = mid;
        } else {
          lower = mid;
        }
      }
      return upper;
    });
  }
  besselifn(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BESSELI"), (x2, n2) => besseli(x2, Math.trunc(n2)));
  }
  besseljfn(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BESSELJ"), (x2, n2) => besselj(x2, Math.trunc(n2)));
  }
  besselkfn(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BESSELK"), (x2, n2) => besselk(x2, Math.trunc(n2)));
  }
  besselyfn(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("BESSELY"), (x2, n2) => bessely(x2, Math.trunc(n2)));
  }
  chisqdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHISQ.DIST"), (x2, deg, cumulative) => {
      deg = Math.trunc(deg);
      if (cumulative) {
        return chisquare.cdf(x2, deg);
      } else {
        return chisquare.pdf(x2, deg);
      }
    });
  }
  chisqdistrt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHISQ.DIST.RT"), (x2, deg) => 1 - chisquare.cdf(x2, Math.trunc(deg)));
  }
  chisqinv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHISQ.INV"), (p2, deg) => chisquare.inv(p2, Math.trunc(deg)));
  }
  chisqinvrt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHISQ.INV.RT"), (p2, deg) => chisquare.inv(1 - p2, Math.trunc(deg)));
  }
  fdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("F.DIST"), (x2, deg1, deg2, cumulative) => {
      deg1 = Math.trunc(deg1);
      deg2 = Math.trunc(deg2);
      if (cumulative) {
        return centralF.cdf(x2, deg1, deg2);
      } else {
        return centralF.pdf(x2, deg1, deg2);
      }
    });
  }
  fdistrt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("F.DIST.RT"), (x2, deg1, deg2) => 1 - centralF.cdf(x2, Math.trunc(deg1), Math.trunc(deg2)));
  }
  finv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("F.INV"), (p2, deg1, deg2) => centralF.inv(p2, Math.trunc(deg1), Math.trunc(deg2)));
  }
  finvrt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("F.INV.RT"), (p2, deg1, deg2) => centralF.inv(1 - p2, Math.trunc(deg1), Math.trunc(deg2)));
  }
  weibulldist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("WEIBULL.DIST"), (x2, shape, scale, cumulative) => {
      if (cumulative) {
        return weibull.cdf(x2, scale, shape);
      } else {
        return weibull.pdf(x2, scale, shape);
      }
    });
  }
  poissondist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("POISSON.DIST"), (x2, mean2, cumulative) => {
      x2 = Math.trunc(x2);
      if (cumulative) {
        return poisson.cdf(x2, mean2);
      } else {
        return poisson.pdf(x2, mean2);
      }
    });
  }
  hypgeomdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HYPGEOM.DIST"), (s2, numberS, populationS, numberPop, cumulative) => {
      if (s2 > numberS || s2 > populationS || numberS > numberPop || populationS > numberPop) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      if (s2 + numberPop < populationS + numberS) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      s2 = Math.trunc(s2);
      numberS = Math.trunc(numberS);
      populationS = Math.trunc(populationS);
      numberPop = Math.trunc(numberPop);
      if (cumulative) {
        return hypgeom.cdf(s2, numberPop, populationS, numberS);
      } else {
        return hypgeom.pdf(s2, numberPop, populationS, numberS);
      }
    });
  }
  tdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("T.DIST"), (x2, deg, cumulative) => {
      deg = Math.trunc(deg);
      if (cumulative) {
        return studentt.cdf(x2, deg);
      } else {
        return studentt.pdf(x2, deg);
      }
    });
  }
  tdist2t(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("T.DIST.2T"), (x2, deg) => (1 - studentt.cdf(x2, Math.trunc(deg))) * 2);
  }
  tdistrt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("T.DIST.RT"), (x2, deg) => 1 - studentt.cdf(x2, Math.trunc(deg)));
  }
  tdistold(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("TDIST"), (x2, deg, mode) => mode * (1 - studentt.cdf(x2, Math.trunc(deg))));
  }
  tinv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("T.INV"), (p2, deg) => studentt.inv(p2, Math.trunc(deg)));
  }
  tinv2t(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("T.INV.2T"), (p2, deg) => studentt.inv(1 - p2 / 2, Math.trunc(deg)));
  }
  lognormdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LOGNORM.DIST"), (x2, mean2, stddev, cumulative) => {
      if (cumulative) {
        return lognormal.cdf(x2, mean2, stddev);
      } else {
        return lognormal.pdf(x2, mean2, stddev);
      }
    });
  }
  lognorminv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LOGNORM.INV"), (p2, mean2, stddev) => lognormal.inv(p2, mean2, stddev));
  }
  normdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NORM.DIST"), (x2, mean2, stddev, cumulative) => {
      if (cumulative) {
        return normal.cdf(x2, mean2, stddev);
      } else {
        return normal.pdf(x2, mean2, stddev);
      }
    });
  }
  norminv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NORM.INV"), (p2, mean2, stddev) => normal.inv(p2, mean2, stddev));
  }
  normsdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NORM.S.DIST"), (x2, cumulative) => {
      if (cumulative) {
        return normal.cdf(x2, 0, 1);
      } else {
        return normal.pdf(x2, 0, 1);
      }
    });
  }
  normsinv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NORM.S.INV"), (p2) => normal.inv(p2, 0, 1));
  }
  phi(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("PHI"), (x2) => normal.pdf(x2, 0, 1));
  }
  negbinomdist(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("NEGBINOM.DIST"), (nf, ns, p2, cumulative) => {
      nf = Math.trunc(nf);
      ns = Math.trunc(ns);
      if (cumulative) {
        return negbin.cdf(nf, ns, p2);
      } else {
        return negbin.pdf(nf, ns, p2);
      }
    });
  }
  confidencenorm(ast, state) {
    return this.runFunction(
      ast.args,
      state,
      this.metadata("CONFIDENCE.NORM"),
      // eslint-disable-next-line
      // @ts-ignore
      (alpha, stddev, size) => normalci(1, alpha, stddev, Math.trunc(size))[1] - 1
    );
  }
  confidencet(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CONFIDENCE.T"), (alpha, stddev, size) => {
      size = Math.trunc(size);
      if (size === 1) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return tci(1, alpha, stddev, size)[1] - 1;
    });
  }
  standardize(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("STANDARDIZE"), (x2, mean2, stddev) => (x2 - mean2) / stddev);
  }
}
StatisticalPlugin.implementedFunctions = {
  "ERF": {
    method: "erf",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true
    }]
  },
  "ERFC": {
    method: "erfc",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "EXPON.DIST": {
    method: "expondist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "FISHER": {
    method: "fisher",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: -1,
      lessThan: 1
    }]
  },
  "FISHERINV": {
    method: "fisherinv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "GAMMA": {
    method: "gamma",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "GAMMA.DIST": {
    method: "gammadist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "GAMMALN": {
    method: "gammaln",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }]
  },
  "GAMMA.INV": {
    method: "gammainv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      lessThan: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }]
  },
  "GAUSS": {
    method: "gauss",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "BETA.DIST": {
    method: "betadist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "BETA.INV": {
    method: "betainv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      defaultValue: 1
    }]
  },
  "BINOM.DIST": {
    method: "binomialdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "BINOM.INV": {
    method: "binomialinv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }]
  },
  "BESSELI": {
    method: "besselifn",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "BESSELJ": {
    method: "besseljfn",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "BESSELK": {
    method: "besselkfn",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "BESSELY": {
    method: "besselyfn",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "CHISQ.DIST": {
    method: "chisqdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1,
      maxValue: 1e10
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "CHISQ.DIST.RT": {
    method: "chisqdistrt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1,
      maxValue: 1e10
    }]
  },
  "CHISQ.INV": {
    method: "chisqinv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1,
      maxValue: 1e10
    }]
  },
  "CHISQ.INV.RT": {
    method: "chisqinvrt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "F.DIST": {
    method: "fdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "F.DIST.RT": {
    method: "fdistrt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "F.INV": {
    method: "finv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "F.INV.RT": {
    method: "finvrt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "WEIBULL.DIST": {
    method: "weibulldist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "POISSON.DIST": {
    method: "poissondist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "HYPGEOM.DIST": {
    method: "hypgeomdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "T.DIST": {
    method: "tdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "T.DIST.2T": {
    method: "tdist2t",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "T.DIST.RT": {
    method: "tdistrt",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "TDIST": {
    method: "tdistold",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1,
      maxValue: 2
    }]
  },
  "T.INV": {
    method: "tinv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "T.INV.2T": {
    method: "tinv2t",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "LOGNORM.DIST": {
    method: "lognormdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "LOGNORM.INV": {
    method: "lognorminv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }]
  },
  "NORM.DIST": {
    method: "normdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "NORM.INV": {
    method: "norminv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }]
  },
  "NORM.S.DIST": {
    method: "normsdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "NORM.S.INV": {
    method: "normsinv",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }]
  },
  "PHI": {
    method: "phi",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "NEGBINOM.DIST": {
    method: "negbinomdist",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 1
    }, {
      argumentType: FunctionArgumentType.BOOLEAN
    }]
  },
  "CONFIDENCE.NORM": {
    method: "confidencenorm",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "CONFIDENCE.T": {
    method: "confidencet",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0,
      lessThan: 1
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 1
    }]
  },
  "STANDARDIZE": {
    method: "standardize",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      greaterThan: 0
    }]
  }
};
StatisticalPlugin.aliases = {
  NEGBINOMDIST: "NEGBINOM.DIST",
  EXPONDIST: "EXPON.DIST",
  BETADIST: "BETA.DIST",
  NORMDIST: "NORM.DIST",
  NORMINV: "NORM.INV",
  NORMSDIST: "NORM.S.DIST",
  NORMSINV: "NORM.S.INV",
  LOGNORMDIST: "LOGNORM.DIST",
  LOGINV: "LOGNORM.INV",
  TINV: "T.INV.2T",
  HYPGEOMDIST: "HYPGEOM.DIST",
  POISSON: "POISSON.DIST",
  WEIBULL: "WEIBULL.DIST",
  FINV: "F.INV.RT",
  FDIST: "F.DIST.RT",
  CHIDIST: "CHISQ.DIST.RT",
  CHIINV: "CHISQ.INV.RT",
  GAMMADIST: "GAMMA.DIST",
  "GAMMALN.PRECISE": "GAMMALN",
  GAMMAINV: "GAMMA.INV",
  BETAINV: "BETA.INV",
  BINOMDIST: "BINOM.DIST",
  CONFIDENCE: "CONFIDENCE.NORM",
  CRITBINOM: "BINOM.INV",
  WEIBULLDIST: "WEIBULL.DIST",
  TINV2T: "T.INV.2T",
  TDISTRT: "T.DIST.RT",
  TDIST2T: "T.DIST.2T",
  FINVRT: "F.INV.RT",
  FDISTRT: "F.DIST.RT",
  CHIDISTRT: "CHISQ.DIST.RT",
  CHIINVRT: "CHISQ.INV.RT",
  LOGNORMINV: "LOGNORM.INV",
  POISSONDIST: "POISSON.DIST"
};
class MathPlugin extends FunctionPlugin {
  fact(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FACT"), (arg) => {
      arg = Math.trunc(arg);
      let ret = 1;
      for (let i2 = 1; i2 <= arg; i2++) {
        ret *= i2;
      }
      return ret;
    });
  }
  factdouble(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("FACTDOUBLE"), (arg) => {
      arg = Math.trunc(arg);
      let ret = 1;
      for (let i2 = arg; i2 >= 1; i2 -= 2) {
        ret *= i2;
      }
      return ret;
    });
  }
  combin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COMBIN"), (n2, m2) => {
      if (m2 > n2) {
        return new CellError(ErrorType.NUM, ErrorMessage.WrongOrder);
      }
      n2 = Math.trunc(n2);
      m2 = Math.trunc(m2);
      return combin(n2, m2);
    });
  }
  combina(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COMBINA"), (n2, m2) => {
      n2 = Math.trunc(n2);
      m2 = Math.trunc(m2);
      if (n2 + m2 - 1 >= 1030) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      if (n2 === 0 && m2 === 0) {
        return 1;
      }
      return combin(n2 + m2 - 1, m2);
    });
  }
  gcd(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GCD"), (...args) => {
      const processedArgs = this.arithmeticHelper.coerceNumbersCoerceRangesDropNulls(args);
      if (processedArgs instanceof CellError) {
        return processedArgs;
      }
      let ret = 0;
      for (const val of processedArgs) {
        if (val < 0) {
          return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
        }
        ret = binaryGCD(ret, Math.trunc(val));
      }
      if (ret > Number.MAX_SAFE_INTEGER) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      return ret;
    });
  }
  lcm(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("LCM"), (...args) => {
      const processedArgs = this.arithmeticHelper.coerceNumbersCoerceRangesDropNulls(args);
      if (processedArgs instanceof CellError) {
        return processedArgs;
      }
      let ret = 1;
      for (const val of processedArgs) {
        if (val < 0) {
          return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
        }
        ret = binaryLCM(ret, Math.trunc(val));
      }
      if (ret > Number.MAX_SAFE_INTEGER) {
        return new CellError(ErrorType.NUM, ErrorMessage.ValueLarge);
      }
      return ret;
    });
  }
  mround(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MROUND"), (nom, denom) => {
      if (denom === 0) {
        return 0;
      }
      if (nom > 0 && denom < 0 || nom < 0 && denom > 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.DistinctSigns);
      }
      return Math.round(nom / denom) * denom;
    });
  }
  multinomial(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("MULTINOMIAL"), (...args) => {
      let n2 = 0;
      let ans = 1;
      for (let arg of args) {
        if (arg < 0) {
          return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
        }
        arg = Math.trunc(arg);
        for (let i2 = 1; i2 <= arg; i2++) {
          ans *= (n2 + i2) / i2;
        }
        n2 += arg;
      }
      return Math.round(ans);
    });
  }
  quotient(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("QUOTIENT"), (nom, denom) => {
      if (denom === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return Math.trunc(nom / denom);
    });
  }
  seriessum(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SERIESSUM"), (x2, n2, m2, range) => {
      const coefs = this.arithmeticHelper.manyToOnlyNumbersDropNulls(range.valuesFromTopLeftCorner());
      if (coefs instanceof CellError) {
        return coefs;
      }
      let ret = 0;
      coefs.reverse();
      for (const coef of coefs) {
        ret *= Math.pow(x2, m2);
        ret += coef;
      }
      return ret * Math.pow(x2, n2);
    });
  }
  sign(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SIGN"), (arg) => {
      if (arg > 0) {
        return 1;
      } else if (arg < 0) {
        return -1;
      } else {
        return 0;
      }
    });
  }
  sumx2my2(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SUMX2MY2"), (rangeX, rangeY) => {
      const valsX = rangeX.valuesFromTopLeftCorner();
      const valsY = rangeY.valuesFromTopLeftCorner();
      if (valsX.length !== valsY.length) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const n2 = valsX.length;
      let ret = 0;
      for (let i2 = 0; i2 < n2; i2++) {
        const valX = valsX[i2];
        const valY = valsY[i2];
        if (valX instanceof CellError) {
          return valX;
        }
        if (valY instanceof CellError) {
          return valY;
        }
        if (typeof valX === "number" && typeof valY === "number") {
          ret += Math.pow(valX, 2) - Math.pow(valY, 2);
        }
      }
      return ret;
    });
  }
  sumx2py2(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SUMX2PY2"), (rangeX, rangeY) => {
      const valsX = rangeX.valuesFromTopLeftCorner();
      const valsY = rangeY.valuesFromTopLeftCorner();
      if (valsX.length !== valsY.length) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const n2 = valsX.length;
      let ret = 0;
      for (let i2 = 0; i2 < n2; i2++) {
        const valX = valsX[i2];
        const valY = valsY[i2];
        if (valX instanceof CellError) {
          return valX;
        }
        if (valY instanceof CellError) {
          return valY;
        }
        if (typeof valX === "number" && typeof valY === "number") {
          ret += Math.pow(valX, 2) + Math.pow(valY, 2);
        }
      }
      return ret;
    });
  }
  sumxmy2(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SUMXMY2"), (rangeX, rangeY) => {
      const valsX = rangeX.valuesFromTopLeftCorner();
      const valsY = rangeY.valuesFromTopLeftCorner();
      if (valsX.length !== valsY.length) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const n2 = valsX.length;
      let ret = 0;
      for (let i2 = 0; i2 < n2; i2++) {
        const valX = valsX[i2];
        const valY = valsY[i2];
        if (valX instanceof CellError) {
          return valX;
        }
        if (valY instanceof CellError) {
          return valY;
        }
        if (typeof valX === "number" && typeof valY === "number") {
          ret += Math.pow(valX - valY, 2);
        }
      }
      return ret;
    });
  }
}
MathPlugin.implementedFunctions = {
  "FACT": {
    method: "fact",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 170
    }]
  },
  "FACTDOUBLE": {
    method: "factdouble",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      maxValue: 288
    }]
  },
  "COMBIN": {
    method: "combin",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0,
      lessThan: 1030
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "COMBINA": {
    method: "combina",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      minValue: 0
    }]
  },
  "GCD": {
    method: "gcd",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "LCM": {
    method: "lcm",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "MROUND": {
    method: "mround",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "MULTINOMIAL": {
    method: "multinomial",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }],
    repeatLastArgs: 1,
    expandRanges: true
  },
  "QUOTIENT": {
    method: "quotient",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "SERIESSUM": {
    method: "seriessum",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "SIGN": {
    method: "sign",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "SUMX2MY2": {
    method: "sumx2my2",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "SUMX2PY2": {
    method: "sumx2py2",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "SUMXMY2": {
    method: "sumxmy2",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  }
};
function combin(n2, m2) {
  if (2 * m2 > n2) {
    m2 = n2 - m2;
  }
  let ret = 1;
  for (let i2 = 1; i2 <= m2; i2++) {
    ret *= (n2 - m2 + i2) / i2;
  }
  return Math.round(ret);
}
function binaryGCD(a2, b2) {
  if (a2 < b2) {
    [a2, b2] = [b2, a2];
  }
  while (b2 > 0) {
    [a2, b2] = [b2, a2 % b2];
  }
  return a2;
}
function binaryLCM(a2, b2) {
  if (a2 === 0 || b2 === 0) {
    return 0;
  }
  return a2 * (b2 / binaryGCD(a2, b2));
}
class ComplexPlugin extends FunctionPlugin {
  complex(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COMPLEX"), (re2, im, unit) => {
      if (unit !== "i" && unit !== "j") {
        return new CellError(ErrorType.VALUE, ErrorMessage.ShouldBeIorJ);
      }
      return coerceComplexToString([re2, im], unit);
    });
  }
  imabs(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMABS"), abs$1);
  }
  imaginary(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMAGINARY"), ([_re, im]) => im);
  }
  imreal(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMREAL"), ([re2, _im]) => re2);
  }
  imargument(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMARGUMENT"), ([re2, im]) => {
      if (re2 === 0 && im === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return Math.atan2(im, re2);
    });
  }
  imconjugate(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMCONJUGATE"), ([re2, im]) => coerceComplexToString([re2, -im]));
  }
  imcos(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMCOS"), (arg) => coerceComplexToString(cos(arg)));
  }
  imcosh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMCOSH"), (arg) => coerceComplexToString(cosh(arg)));
  }
  imcot(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMCOT"), (arg) => coerceComplexToString(div(cos(arg), sin(arg))));
  }
  imcsc(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMCSC"), (arg) => coerceComplexToString(div([1, 0], sin(arg))));
  }
  imcsch(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMCSCH"), (arg) => coerceComplexToString(div([1, 0], sinh(arg))));
  }
  imsec(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMSEC"), (arg) => coerceComplexToString(div([1, 0], cos(arg))));
  }
  imsech(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMSECH"), (arg) => coerceComplexToString(div([1, 0], cosh(arg))));
  }
  imsin(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMSIN"), (arg) => coerceComplexToString(sin(arg)));
  }
  imsinh(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMSINH"), (arg) => coerceComplexToString(sinh(arg)));
  }
  imtan(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMTAN"), (arg) => coerceComplexToString(div(sin(arg), cos(arg))));
  }
  imdiv(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMDIV"), (arg1, arg2) => coerceComplexToString(div(arg1, arg2)));
  }
  improduct(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMPRODUCT"), (...args) => {
      const coerced = this.arithmeticHelper.coerceComplexExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      let prod = [1, 0];
      for (const val of coerced) {
        prod = mul(prod, val);
      }
      return coerceComplexToString(prod);
    });
  }
  imsum(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMSUM"), (...args) => {
      const coerced = this.arithmeticHelper.coerceComplexExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      let sum2 = [0, 0];
      for (const val of coerced) {
        sum2 = add(sum2, val);
      }
      return coerceComplexToString(sum2);
    });
  }
  imsub(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMSUB"), (arg1, arg2) => coerceComplexToString(sub(arg1, arg2)));
  }
  imexp(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMEXP"), (arg) => coerceComplexToString(exp(arg)));
  }
  imln(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMLN"), (arg) => coerceComplexToString(ln$1(arg)));
  }
  imlog10(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMLOG10"), (arg) => {
      const [re2, im] = ln$1(arg);
      const c2 = Math.log(10);
      return coerceComplexToString([re2 / c2, im / c2]);
    });
  }
  imlog2(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMLOG2"), (arg) => {
      const [re2, im] = ln$1(arg);
      const c2 = Math.log(2);
      return coerceComplexToString([re2 / c2, im / c2]);
    });
  }
  impower(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMPOWER"), (arg, n2) => coerceComplexToString(power(arg, n2)));
  }
  imsqrt(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("IMSQRT"), (arg) => coerceComplexToString(power(arg, 0.5)));
  }
}
ComplexPlugin.implementedFunctions = {
  "COMPLEX": {
    method: "complex",
    parameters: [{
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.STRING,
      defaultValue: "i"
    }]
  },
  "IMABS": {
    method: "imabs",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMAGINARY": {
    method: "imaginary",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMREAL": {
    method: "imreal",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMARGUMENT": {
    method: "imargument",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMCONJUGATE": {
    method: "imconjugate",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMCOS": {
    method: "imcos",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMCOSH": {
    method: "imcosh",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMCOT": {
    method: "imcot",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMCSC": {
    method: "imcsc",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMCSCH": {
    method: "imcsch",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMSEC": {
    method: "imsec",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMSECH": {
    method: "imsech",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMSIN": {
    method: "imsin",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMSINH": {
    method: "imsinh",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMTAN": {
    method: "imtan",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMDIV": {
    method: "imdiv",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }, {
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMPRODUCT": {
    method: "improduct",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "IMSUM": {
    method: "imsum",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "IMSUB": {
    method: "imsub",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }, {
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMEXP": {
    method: "imexp",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMLN": {
    method: "imln",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMLOG10": {
    method: "imlog10",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMLOG2": {
    method: "imlog2",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  },
  "IMPOWER": {
    method: "impower",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }]
  },
  "IMSQRT": {
    method: "imsqrt",
    parameters: [{
      argumentType: FunctionArgumentType.COMPLEX
    }]
  }
};
function add([re1, im1], [re2, im2]) {
  return [re1 + re2, im1 + im2];
}
function sub([re1, im1], [re2, im2]) {
  return [re1 - re2, im1 - im2];
}
function mul([re1, im1], [re2, im2]) {
  return [re1 * re2 - im1 * im2, re1 * im2 + re2 * im1];
}
function div([re1, im1], [re2, im2]) {
  const denom = Math.pow(re2, 2) + Math.pow(im2, 2);
  const [nomRe, nomIm] = mul([re1, im1], [re2, -im2]);
  return [nomRe / denom, nomIm / denom];
}
function cos([re2, im]) {
  return [Math.cos(re2) * Math.cosh(im), -Math.sin(re2) * Math.sinh(im)];
}
function cosh([re2, im]) {
  return [Math.cosh(re2) * Math.cos(im), Math.sinh(re2) * Math.sin(im)];
}
function sin([re2, im]) {
  return [Math.sin(re2) * Math.cosh(im), Math.cos(re2) * Math.sinh(im)];
}
function sinh([re2, im]) {
  return [Math.sinh(re2) * Math.cos(im), Math.cosh(re2) * Math.sin(im)];
}
function exp([re2, im]) {
  return [Math.exp(re2) * Math.cos(im), Math.exp(re2) * Math.sin(im)];
}
function abs$1([re2, im]) {
  return Math.sqrt(re2 * re2 + im * im);
}
function ln$1([re2, im]) {
  return [Math.log(abs$1([re2, im])), Math.atan2(im, re2)];
}
function power(arg, n2) {
  const [re2, im] = ln$1(arg);
  return exp([n2 * re2, n2 * im]);
}
class StatisticalAggregationPlugin extends FunctionPlugin {
  avedev(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("AVEDEV"), (...args) => {
      const coerced = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      if (coerced.length === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const avg = mean$1(coerced);
      return coerced.reduce((a2, b2) => a2 + Math.abs(b2 - avg), 0) / coerced.length;
    });
  }
  devsq(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("DEVSQ"), (...args) => {
      const coerced = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      if (coerced.length === 0) {
        return 0;
      }
      return sumsqerr(coerced);
    });
  }
  geomean(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("GEOMEAN"), (...args) => {
      const coerced = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      if (coerced.length === 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.OneValue);
      }
      for (const val of coerced) {
        if (val <= 0) {
          return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
        }
      }
      return geomean(coerced);
    });
  }
  harmean(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("HARMEAN"), (...args) => {
      const coerced = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      if (coerced.length === 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.OneValue);
      }
      for (const val of coerced) {
        if (val <= 0) {
          return new CellError(ErrorType.NUM, ErrorMessage.ValueSmall);
        }
      }
      return coerced.length / coerced.reduce((a2, b2) => a2 + 1 / b2, 0);
    });
  }
  correl(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CORREL"), (dataX, dataY) => {
      if (dataX.numberOfElements() !== dataY.numberOfElements()) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const ret = parseTwoArrays(dataX, dataY);
      if (ret instanceof CellError) {
        return ret;
      }
      const n2 = ret[0].length;
      if (n2 <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
      }
      return corrcoeff(ret[0], ret[1]);
    });
  }
  rsq(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("RSQ"), (dataX, dataY) => {
      if (dataX.numberOfElements() !== dataY.numberOfElements()) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const ret = parseTwoArrays(dataX, dataY);
      if (ret instanceof CellError) {
        return ret;
      }
      const n2 = ret[0].length;
      if (n2 <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
      }
      return Math.pow(corrcoeff(ret[0], ret[1]), 2);
    });
  }
  covariancep(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COVARIANCE.P"), (dataX, dataY) => {
      if (dataX.numberOfElements() !== dataY.numberOfElements()) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const ret = parseTwoArrays(dataX, dataY);
      if (ret instanceof CellError) {
        return ret;
      }
      const n2 = ret[0].length;
      if (n2 < 1) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.OneValue);
      }
      if (n2 === 1) {
        return 0;
      }
      return covariance(ret[0], ret[1]) * (n2 - 1) / n2;
    });
  }
  covariances(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("COVARIANCE.S"), (dataX, dataY) => {
      if (dataX.numberOfElements() !== dataY.numberOfElements()) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const ret = parseTwoArrays(dataX, dataY);
      if (ret instanceof CellError) {
        return ret;
      }
      const n2 = ret[0].length;
      if (n2 <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
      }
      return covariance(ret[0], ret[1]);
    });
  }
  ztest(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("Z.TEST"), (range, x2, sigma) => {
      const vals = this.arithmeticHelper.manyToExactNumbers(range.valuesFromTopLeftCorner());
      if (vals instanceof CellError) {
        return vals;
      }
      const n2 = vals.length;
      if (sigma === void 0) {
        if (n2 < 2) {
          return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
        }
        sigma = stdev(vals, true);
      }
      if (n2 < 1) {
        return new CellError(ErrorType.NA, ErrorMessage.OneValue);
      }
      if (sigma === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return 1 - normal.cdf((mean$1(vals) - x2) / (sigma / Math.sqrt(n2)), 0, 1);
    });
  }
  ftest(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("F.TEST"), (dataX, dataY) => {
      const arrX = this.arithmeticHelper.manyToExactNumbers(dataX.valuesFromTopLeftCorner());
      const arrY = this.arithmeticHelper.manyToExactNumbers(dataY.valuesFromTopLeftCorner());
      if (arrX instanceof CellError) {
        return arrX;
      }
      if (arrY instanceof CellError) {
        return arrY;
      }
      if (arrX.length <= 1 || arrY.length <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const vx = variance$1(arrX, true);
      const vy = variance$1(arrY, true);
      if (vx === 0 || vy === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      const r2 = vx / vy;
      const v2 = centralF.cdf(r2, arrX.length - 1, arrY.length - 1);
      return 2 * Math.min(v2, 1 - v2);
    });
  }
  steyx(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("STEYX"), (dataX, dataY) => {
      if (dataX.numberOfElements() !== dataY.numberOfElements()) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const ret = parseTwoArrays(dataX, dataY);
      if (ret instanceof CellError) {
        return ret;
      }
      const n2 = ret[0].length;
      if (n2 <= 2) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.ThreeValues);
      }
      return Math.sqrt((sumsqerr(ret[0]) - Math.pow(covariance(ret[0], ret[1]) * (n2 - 1), 2) / sumsqerr(ret[1])) / (n2 - 2));
    });
  }
  slope(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SLOPE"), (dataX, dataY) => {
      if (dataX.numberOfElements() !== dataY.numberOfElements()) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const ret = parseTwoArrays(dataX, dataY);
      if (ret instanceof CellError) {
        return ret;
      }
      const n2 = ret[0].length;
      if (n2 <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
      }
      return covariance(ret[0], ret[1]) * (n2 - 1) / sumsqerr(ret[1]);
    });
  }
  chisqtest(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("CHISQ.TEST"), (dataX, dataY) => {
      const r2 = dataX.height();
      const c2 = dataX.width();
      if (dataY.height() !== r2 || dataY.width() !== c2) {
        return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
      }
      const ret = parseTwoArrays(dataX, dataY);
      if (ret instanceof CellError) {
        return ret;
      }
      if (ret[0].length <= 1) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
      }
      let sum2 = 0;
      for (let i2 = 0; i2 < ret[0].length; i2++) {
        if (ret[1][i2] === 0) {
          return new CellError(ErrorType.DIV_BY_ZERO);
        }
        sum2 += Math.pow(ret[0][i2] - ret[1][i2], 2) / ret[1][i2];
      }
      if (sum2 < 0) {
        return new CellError(ErrorType.NUM, ErrorMessage.NaN);
      }
      return 1 - chisquare.cdf(sum2, r2 > 1 && c2 > 1 ? (r2 - 1) * (c2 - 1) : r2 * c2 - 1);
    });
  }
  ttest(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("T.TEST"), (dataX, dataY, tails, type) => {
      const arrX = this.arithmeticHelper.manyToExactNumbers(dataX.valuesFromTopLeftCorner());
      const arrY = this.arithmeticHelper.manyToExactNumbers(dataY.valuesFromTopLeftCorner());
      if (arrX instanceof CellError) {
        return arrX;
      }
      if (arrY instanceof CellError) {
        return arrY;
      }
      const n2 = arrX.length;
      const m2 = arrY.length;
      if (type === 1) {
        if (m2 !== n2) {
          return new CellError(ErrorType.NA, ErrorMessage.EqualLength);
        }
        if (n2 <= 1) {
          return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
        }
        const sub2 = Array(n2);
        for (let i2 = 0; i2 < n2; i2++) {
          sub2[i2] = arrX[i2] - arrY[i2];
        }
        const s2 = stdev(sub2, true);
        if (s2 === 0) {
          return new CellError(ErrorType.DIV_BY_ZERO);
        }
        const t2 = Math.abs(Math.sqrt(n2) * mean$1(sub2) / s2);
        return tails * (1 - studentt.cdf(t2, n2 - 1));
      } else if (type === 2) {
        if (n2 <= 1 || m2 <= 1) {
          return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
        }
        const s2 = (sumsqerr(arrX) + sumsqerr(arrY)) / (n2 + m2 - 2);
        if (s2 === 0) {
          return new CellError(ErrorType.DIV_BY_ZERO);
        }
        const t2 = Math.abs((mean$1(arrX) - mean$1(arrY)) / Math.sqrt(s2 * (1 / n2 + 1 / m2)));
        return tails * (1 - studentt.cdf(t2, n2 + m2 - 2));
      } else {
        if (n2 <= 1 || m2 <= 1) {
          return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.TwoValues);
        }
        const sx = variance$1(arrX, true);
        const sy = variance$1(arrY, true);
        if (sx === 0 && sy === 0) {
          return new CellError(ErrorType.DIV_BY_ZERO);
        }
        const t2 = Math.abs((mean$1(arrX) - mean$1(arrY)) / Math.sqrt(sx / n2 + sy / m2));
        const v2 = Math.pow(sx / n2 + sy / m2, 2) / (Math.pow(sx / n2, 2) / (n2 - 1) + Math.pow(sy / m2, 2) / (m2 - 1));
        return tails * (1 - studentt.cdf(t2, v2));
      }
    });
  }
  skew(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SKEW"), (...args) => {
      const coerced = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      const n2 = coerced.length;
      if (n2 < 3) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.ThreeValues);
      }
      const avg = mean$1(coerced);
      const s2 = stdev(coerced, true);
      if (s2 === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return coerced.reduce((a2, b2) => a2 + Math.pow((b2 - avg) / s2, 3), 0) * n2 / (n2 - 1) / (n2 - 2);
    });
  }
  skewp(ast, state) {
    return this.runFunction(ast.args, state, this.metadata("SKEW.P"), (...args) => {
      const coerced = this.arithmeticHelper.coerceNumbersExactRanges(args);
      if (coerced instanceof CellError) {
        return coerced;
      }
      const n2 = coerced.length;
      if (n2 < 3) {
        return new CellError(ErrorType.DIV_BY_ZERO, ErrorMessage.ThreeValues);
      }
      const avg = mean$1(coerced);
      const s2 = stdev(coerced, false);
      if (s2 === 0) {
        return new CellError(ErrorType.DIV_BY_ZERO);
      }
      return coerced.reduce((a2, b2) => a2 + Math.pow((b2 - avg) / s2, 3), 0) / n2;
    });
  }
}
StatisticalAggregationPlugin.implementedFunctions = {
  "AVEDEV": {
    method: "avedev",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "DEVSQ": {
    method: "devsq",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "GEOMEAN": {
    method: "geomean",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "HARMEAN": {
    method: "harmean",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "CORREL": {
    method: "correl",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "RSQ": {
    method: "rsq",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "COVARIANCE.P": {
    method: "covariancep",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "COVARIANCE.S": {
    method: "covariances",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "Z.TEST": {
    method: "ztest",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.NUMBER
    }, {
      argumentType: FunctionArgumentType.NUMBER,
      optionalArg: true
    }]
  },
  "F.TEST": {
    method: "ftest",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "STEYX": {
    method: "steyx",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "SLOPE": {
    method: "slope",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "CHISQ.TEST": {
    method: "chisqtest",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }]
  },
  "T.TEST": {
    method: "ttest",
    parameters: [{
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.RANGE
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1,
      maxValue: 2
    }, {
      argumentType: FunctionArgumentType.INTEGER,
      minValue: 1,
      maxValue: 3
    }]
  },
  "SKEW": {
    method: "skew",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  },
  "SKEW.P": {
    method: "skewp",
    parameters: [{
      argumentType: FunctionArgumentType.ANY
    }],
    repeatLastArgs: 1
  }
};
StatisticalAggregationPlugin.aliases = {
  COVAR: "COVARIANCE.P",
  FTEST: "F.TEST",
  PEARSON: "CORREL",
  ZTEST: "Z.TEST",
  CHITEST: "CHISQ.TEST",
  TTEST: "T.TEST",
  COVARIANCEP: "COVARIANCE.P",
  COVARIANCES: "COVARIANCE.S",
  SKEWP: "SKEW.P"
};
function parseTwoArrays(dataX, dataY) {
  const xit = dataX.iterateValuesFromTopLeftCorner();
  const yit = dataY.iterateValuesFromTopLeftCorner();
  let x2, y2;
  const arrX = [];
  const arrY = [];
  while (x2 = xit.next(), y2 = yit.next(), !x2.done && !y2.done) {
    const xval = x2.value;
    const yval = y2.value;
    if (xval instanceof CellError) {
      return xval;
    } else if (yval instanceof CellError) {
      return yval;
    } else if (isExtendedNumber(xval) && isExtendedNumber(yval)) {
      arrX.push(getRawValue(xval));
      arrY.push(getRawValue(yval));
    }
  }
  return [arrX, arrY];
}
const plugins = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  AbsPlugin,
  AddressPlugin,
  ArrayPlugin,
  BitShiftPlugin,
  BitwiseLogicOperationsPlugin,
  BooleanPlugin,
  CharPlugin,
  CodePlugin,
  ComplexPlugin,
  ConditionalAggregationPlugin,
  CountBlankPlugin,
  CountUniquePlugin,
  DatabasePlugin,
  DateTimePlugin,
  DegreesPlugin,
  DeltaPlugin,
  ExpPlugin,
  FinancialPlugin,
  FormulaTextPlugin,
  HyperlinkPlugin,
  InformationPlugin,
  IsEvenPlugin,
  IsOddPlugin,
  LogarithmPlugin,
  LookupPlugin,
  MathConstantsPlugin,
  MathPlugin,
  MatrixPlugin,
  MedianPlugin,
  ModuloPlugin,
  NumericAggregationPlugin,
  PercentilePlugin,
  PowerPlugin,
  RadiansPlugin,
  RadixConversionPlugin,
  RandomPlugin,
  RomanPlugin,
  RoundingPlugin,
  SequencePlugin,
  SimpleArithmerticPlugin,
  SqrtPlugin,
  StatisticalAggregationPlugin,
  StatisticalPlugin,
  SumprodPlugin,
  TextPlugin,
  TrigonometryPlugin
}, Symbol.toStringTag, { value: "Module" }));
class HyperFormulaNS extends HyperFormula {
}
HyperFormulaNS.HyperFormula = HyperFormula;
HyperFormulaNS.ErrorType = ErrorType;
HyperFormulaNS.CellError = CellError;
HyperFormulaNS.CellType = CellType;
HyperFormulaNS.CellValueType = CellValueType;
HyperFormulaNS.CellValueDetailedType = CellValueDetailedType;
HyperFormulaNS.DetailedCellError = DetailedCellError;
HyperFormulaNS.ExportedCellChange = ExportedCellChange;
HyperFormulaNS.ExportedNamedExpressionChange = ExportedNamedExpressionChange;
HyperFormulaNS.ConfigValueTooBigError = ConfigValueTooBigError;
HyperFormulaNS.ConfigValueTooSmallError = ConfigValueTooSmallError;
HyperFormulaNS.EvaluationSuspendedError = EvaluationSuspendedError;
HyperFormulaNS.ExpectedOneOfValuesError = ExpectedOneOfValuesError;
HyperFormulaNS.ExpectedValueOfTypeError = ExpectedValueOfTypeError;
HyperFormulaNS.ArraySize = ArraySize;
HyperFormulaNS.SimpleRangeValue = SimpleRangeValue;
HyperFormulaNS.EmptyValue = EmptyValue;
HyperFormulaNS.FunctionPlugin = FunctionPlugin;
HyperFormulaNS.FunctionArgumentType = FunctionArgumentType;
HyperFormulaNS.FunctionPluginValidationError = FunctionPluginValidationError;
HyperFormulaNS.InvalidAddressError = InvalidAddressError;
HyperFormulaNS.InvalidArgumentsError = InvalidArgumentsError;
HyperFormulaNS.LanguageNotRegisteredError = LanguageNotRegisteredError;
HyperFormulaNS.LanguageAlreadyRegisteredError = LanguageAlreadyRegisteredError;
HyperFormulaNS.MissingTranslationError = MissingTranslationError;
HyperFormulaNS.NamedExpressionDoesNotExistError = NamedExpressionDoesNotExistError;
HyperFormulaNS.NamedExpressionNameIsAlreadyTakenError = NamedExpressionNameIsAlreadyTakenError;
HyperFormulaNS.NamedExpressionNameIsInvalidError = NamedExpressionNameIsInvalidError;
HyperFormulaNS.NoOperationToRedoError = NoOperationToRedoError;
HyperFormulaNS.NoOperationToUndoError = NoOperationToUndoError;
HyperFormulaNS.NoRelativeAddressesAllowedError = NoRelativeAddressesAllowedError;
HyperFormulaNS.NoSheetWithIdError = NoSheetWithIdError;
HyperFormulaNS.NoSheetWithNameError = NoSheetWithNameError;
HyperFormulaNS.NotAFormulaError = NotAFormulaError;
HyperFormulaNS.NothingToPasteError = NothingToPasteError;
HyperFormulaNS.ProtectedFunctionTranslationError = ProtectedFunctionTranslationError;
HyperFormulaNS.SheetNameAlreadyTakenError = SheetNameAlreadyTakenError;
HyperFormulaNS.SheetSizeLimitExceededError = SheetSizeLimitExceededError;
HyperFormulaNS.SourceLocationHasArrayError = SourceLocationHasArrayError;
HyperFormulaNS.TargetLocationHasArrayError = TargetLocationHasArrayError;
HyperFormulaNS.UnableToParseError = UnableToParseError;
const defaultLanguage = Config.defaultConfig.language;
HyperFormula.registerLanguage(defaultLanguage, dictionary);
HyperFormula.languages[dictionary.langCode] = dictionary;
for (const pluginName of Object.getOwnPropertyNames(plugins)) {
  if (!pluginName.startsWith("_")) {
    HyperFormula.registerFunctionPlugin(plugins[pluginName]);
  }
}
class Spreadsheet extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      onChange: this.onChange.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "spreadsheet";
  static componentDocs = {
    summary: "An interactive spreadsheet"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      description: "Display width of the spreadsheet.",
      createComponentOfType: "componentSize",
      createStateVariable: "width",
      defaultValue: { size: 100, isAbsolute: false },
      public: true,
      forRenderer: true
    };
    attributes.minNumRows = {
      description: "Minimum number of rows displayed.",
      createComponentOfType: "number",
      createStateVariable: "minNumRows",
      // defaultValue: 1,
      defaultValue: 4,
      public: true,
      forRenderer: true
    };
    attributes.minNumColumns = {
      description: "Minimum number of columns displayed.",
      createComponentOfType: "number",
      createStateVariable: "minNumColumns",
      // defaultValue: 1,
      defaultValue: 4,
      public: true,
      forRenderer: true
    };
    attributes.height = {
      createComponentOfType: "componentSize",
      description: "Display height of the spreadsheet."
    };
    attributes.columnHeaders = {
      description: "Header labels for the columns.",
      createComponentOfType: "boolean",
      createStateVariable: "columnHeaders",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.rowHeaders = {
      description: "Header labels for the rows.",
      createComponentOfType: "boolean",
      createStateVariable: "rowHeaders",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.fixedRowsTop = {
      description: "Number of top rows fixed in place when scrolling.",
      createComponentOfType: "integer",
      createStateVariable: "fixedRowsTop",
      defaultValue: 0,
      clamp: [0, Infinity],
      public: true,
      forRenderer: true
    };
    attributes.fixedColumnsLeft = {
      description: "Number of left columns fixed in place when scrolling.",
      createComponentOfType: "integer",
      createStateVariable: "fixedColumnsLeft",
      defaultValue: 0,
      clamp: [0, Infinity],
      public: true,
      forRenderer: true
    };
    attributes.hiddenColumns = {
      description: "Indices of columns to hide.",
      createComponentOfType: "numberList",
      createStateVariable: "hiddenColumns",
      defaultValue: [],
      public: true,
      forRenderer: true
    };
    attributes.hiddenRows = {
      description: "Indices of rows to hide.",
      createComponentOfType: "numberList",
      createStateVariable: "hiddenRows",
      defaultValue: [],
      public: true,
      forRenderer: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "cells",
        componentTypes: ["cell"]
      },
      {
        group: "rows",
        componentTypes: ["row"]
      },
      {
        group: "columns",
        componentTypes: ["column"]
      },
      {
        group: "cellBlocks",
        componentTypes: ["cellBlock"]
      },
      {
        group: "dataFrames",
        componentTypes: ["dataFrame"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnListItemChildStateVariableDefinitions({
        listItemInlineAlignment: "flex-start"
      })
    );
    stateVariableDefinitions.cellIdxToRowCol = {
      additionalStateVariablesDefined: ["cellIndicesByRowCol"],
      returnDependencies: () => ({
        cellRelatedChildren: {
          dependencyType: "child",
          childGroups: ["cells", "rows", "columns", "cellBlocks"],
          variableNames: [
            "rowNum",
            "colNum",
            "prescribedCellsWithColNum",
            "prescribedCellsWithRowNum",
            "prescribedCellsRowsColumnsBlocks"
          ],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        let result = determineCellMapping({
          cellRelatedChildren: dependencyValues.cellRelatedChildren,
          componentInfoObjects
        });
        return {
          setValue: {
            cellIdxToRowCol: result.cellIdxToRowCol,
            cellIndicesByRowCol: result.cellIndicesByRowCol
          }
        };
      }
    };
    stateVariableDefinitions.numRows = {
      description: "The number of rows.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        minNumRows: {
          dependencyType: "stateVariable",
          variableName: "minNumRows"
        },
        cellIndicesByRowCol: {
          dependencyType: "stateVariable",
          variableName: "cellIndicesByRowCol"
        },
        // rowChildren: {
        //   dependencyType: "child",
        //   childLogicName: "zeroOrMoreRows"
        // }
        dataFrameChild: {
          dependencyType: "child",
          childGroups: ["dataFrames"],
          variableNames: ["numRows"]
        }
      }),
      definition({ dependencyValues }) {
        let numRows = dependencyValues.minNumRows;
        if (!Number.isFinite(numRows)) {
          numRows = 4;
        }
        numRows = Math.max(
          numRows,
          dependencyValues.cellIndicesByRowCol.length
        );
        if (dependencyValues.dataFrameChild.length > 0) {
          numRows = Math.max(
            numRows,
            dependencyValues.dataFrameChild[0].stateValues.numRows
          );
        }
        return { setValue: { numRows } };
      }
    };
    stateVariableDefinitions.numColumns = {
      description: "The number of columns.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        minNumColumns: {
          dependencyType: "stateVariable",
          variableName: "minNumColumns"
        },
        cellIndicesByRowCol: {
          dependencyType: "stateVariable",
          variableName: "cellIndicesByRowCol"
        },
        // rowChildren: {
        //   dependencyType: "child",
        //   childLogicName: "zeroOrMoreRows"
        // }
        dataFrameChild: {
          dependencyType: "child",
          childGroups: ["dataFrames"],
          variableNames: ["numColumns"]
        }
      }),
      definition({ dependencyValues }) {
        let numColumns = dependencyValues.minNumColumns;
        if (!Number.isFinite(numColumns)) {
          numColumns = 4;
        }
        for (let row of dependencyValues.cellIndicesByRowCol) {
          if (row) {
            numColumns = Math.max(numColumns, row.length);
          }
        }
        if (dependencyValues.dataFrameChild.length > 0) {
          numColumns = Math.max(
            numColumns,
            dependencyValues.dataFrameChild[0].stateValues.numColumns
          );
        }
        return { setValue: { numColumns } };
      }
    };
    stateVariableDefinitions.height = {
      description: "Display height of the spreadsheet.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      forRenderer: true,
      returnDependencies: () => ({
        heightAttr: {
          dependencyType: "attributeComponent",
          attributeName: "height",
          variableNames: ["componentSize"]
        },
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.heightAttr === null) {
          let height;
          if (Number.isFinite(dependencyValues.numRows) && dependencyValues.numRows >= 0) {
            height = 40 + dependencyValues.numRows * 23;
          } else {
            height = 132;
          }
          return {
            setValue: {
              height: { size: height, isAbsolute: true }
            }
          };
        }
        return {
          setValue: {
            height: dependencyValues.heightAttr.stateValues.componentSize
          }
        };
      }
    };
    stateVariableDefinitions.cells = {
      public: true,
      description: "The raw text contents of all cells.",
      shadowingInstructions: {
        createComponentOfType: "cell",
        returnWrappingComponents(prefix) {
          if (prefix === "cell") {
            return [];
          } else if (prefix === "row") {
            return [["row"]];
          } else if (prefix === "column") {
            return [["column"]];
          } else {
            return [["row"], ["cellBlock"]];
          }
        }
      },
      forRenderer: true,
      isArray: true,
      entryPrefixes: [
        "cell",
        "row",
        "column",
        "range",
        "rows",
        "columns"
      ],
      schemaSubarrays: {
        rows: {
          numDimensions: 2,
          description: "The cells grouped by row."
        },
        columns: {
          numDimensions: 2,
          description: "The cells grouped by column."
        }
      },
      numDimensions: 2,
      defaultValueByArrayKey: () => "",
      hasEssential: true,
      shadowVariable: true,
      stateVariablesDeterminingDependencies: ["cellIndicesByRowCol"],
      // stateVariablesDeterminingDependencies: ["numRows", "numColumns"],
      returnArraySizeDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numRows, dependencyValues.numColumns];
      },
      returnEntryDimensions: (prefix) => {
        if (prefix === "cell") {
          return 0;
        } else if (["range", "rows", "columns"].includes(prefix)) {
          return 2;
        } else {
          return 1;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "cell") {
          let rowNum, colNum;
          let letterNumStyle = /^([a-zA-Z]+)([1-9]\d*)$/;
          let result = varEnding.match(letterNumStyle);
          if (result) {
            colNum = result[1];
            rowNum = result[2];
          } else {
            let tupleStyle = /^\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\)$/;
            result = varEnding.match(tupleStyle);
            if (result) {
              rowNum = result[1];
              colNum = result[2];
            } else {
              return [];
            }
          }
          let rowIndex = normalizeIndex(rowNum);
          let colIndex = normalizeIndex(colNum);
          if (!(rowIndex >= 0 && colIndex >= 0)) {
            return [];
          }
          if (arraySize && !(rowIndex < arraySize[0] && colIndex < arraySize[1])) {
            return [];
          }
          return [String(rowIndex) + "," + String(colIndex)];
        } else if (arrayEntryPrefix === "row") {
          let rowIndex = normalizeIndex(varEnding);
          if (!(rowIndex >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [String(rowIndex) + ",0"];
          }
          if (!(rowIndex < arraySize[0])) {
            return [];
          }
          let arrayKeys = [];
          let arrayKeyBegin = String(rowIndex) + ",";
          for (let i2 = 0; i2 < arraySize[1]; i2++) {
            arrayKeys.push(arrayKeyBegin + i2);
          }
          return arrayKeys;
        } else if (arrayEntryPrefix === "column") {
          let colIndex = normalizeIndex(varEnding);
          if (!(colIndex >= 0)) {
            return [];
          }
          if (!arraySize) {
            return ["0," + String(colIndex)];
          }
          if (!(colIndex < arraySize[1])) {
            return [];
          }
          let arrayKeys = [];
          let arrayKeyEnd = "," + String(colIndex);
          for (let i2 = 0; i2 < arraySize[0]; i2++) {
            arrayKeys.push(i2 + arrayKeyEnd);
          }
          return arrayKeys;
        } else if (arrayEntryPrefix === "range") {
          let fromRow, fromCol, toRow, toCol;
          let letterNumStyle = /^([a-zA-Z]+)([1-9]\d*)([a-zA-Z]+)([1-9]\d*)$/;
          let result = varEnding.match(letterNumStyle);
          if (result) {
            fromCol = normalizeIndex(result[1]);
            fromRow = normalizeIndex(result[2]);
            toCol = normalizeIndex(result[3]);
            toRow = normalizeIndex(result[4]);
          } else {
            let tupleStyle = /^\(\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\),\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\)\)$/;
            result = varEnding.match(tupleStyle);
            if (result) {
              fromRow = normalizeIndex(result[1]);
              fromCol = normalizeIndex(result[2]);
              toRow = normalizeIndex(result[3]);
              toCol = normalizeIndex(result[4]);
            } else {
              return [];
            }
          }
          if (!(fromRow >= 0 && toRow >= 0 && fromCol >= 0 && toCol >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [String(fromRow) + "," + String(fromCol)];
          }
          let row1 = Math.min(
            Math.min(fromRow, toRow),
            arraySize[0] - 1
          );
          let row2 = Math.min(
            Math.max(fromRow, toRow),
            arraySize[0] - 1
          );
          let col1 = Math.min(
            Math.min(fromCol, toCol),
            arraySize[1] - 1
          );
          let col2 = Math.min(
            Math.max(fromCol, toCol),
            arraySize[1] - 1
          );
          let arrayKeys = [];
          for (let rowIndex = row1; rowIndex <= row2; rowIndex++) {
            let rowKeys = [];
            let arrayKeyBegin = String(rowIndex) + ",";
            for (let colIndex = col1; colIndex <= col2; colIndex++) {
              rowKeys.push(arrayKeyBegin + colIndex);
            }
            arrayKeys.push(rowKeys);
          }
          return arrayKeys;
        } else {
          if (!arraySize) {
            return [String(0) + "," + String(0)];
          }
          let arrayKeys = [];
          for (let rowIndex = 0; rowIndex < arraySize[0]; rowIndex++) {
            let rowKeys = [];
            let arrayKeyBegin = String(rowIndex) + ",";
            for (let colIndex = 0; colIndex < arraySize[1]; colIndex++) {
              rowKeys.push(arrayKeyBegin + colIndex);
            }
            arrayKeys.push(rowKeys);
          }
          return arrayKeys;
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        return "cell(" + arrayKey.split(",").map((x2) => Number(x2) + 1).join(",") + ")";
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "cells" || varName === "rows") {
          if (propIndex.length === 1) {
            return "row" + propIndex[0];
          } else {
            return `cell(${propIndex[0]},${propIndex[1]})`;
          }
        }
        if (varName === "columns") {
          if (propIndex.length === 1) {
            return "column" + propIndex[0];
          } else {
            return `cell(${propIndex[1]},${propIndex[0]})`;
          }
        }
        if (varName.slice(0, 3) === "row") {
          let rowNum = varName.slice(3);
          return `cell(${rowNum},${propIndex[0]})`;
        }
        if (varName.slice(0, 6) === "column") {
          let columnNum = varName.slice(6);
          return `cell(${propIndex[0]},${columnNum})`;
        }
        return null;
      },
      returnArrayDependenciesByKey({ stateValues, arrayKeys }) {
        let dependenciesByKey = {};
        let cnbrc = stateValues.cellIndicesByRowCol;
        for (let arrayKey of arrayKeys) {
          let [rowInd, colInd] = arrayKey.split(",");
          if (cnbrc[rowInd] && cnbrc[rowInd][colInd]) {
            dependenciesByKey[arrayKey] = {
              cellText: {
                dependencyType: "stateVariable",
                componentIdx: cnbrc[rowInd][colInd],
                variableName: "text",
                variablesOptional: true
              }
            };
          }
        }
        let globalDependencies = {
          dataFrameChild: {
            dependencyType: "child",
            childGroups: ["dataFrames"],
            variableNames: ["dataFrame", "numRows", "numColumns"]
          }
        };
        return { dependenciesByKey, globalDependencies };
      },
      arrayDefinitionByKey({
        dependencyValuesByKey,
        globalDependencyValues,
        arrayKeys
      }) {
        let cells = {};
        let essentialCells = {};
        let dataFrame = null;
        let dataFrameNumRows, dataFrameNumColumns;
        if (globalDependencyValues.dataFrameChild.length > 0) {
          dataFrame = globalDependencyValues.dataFrameChild[0];
          dataFrameNumRows = dataFrame.stateValues.numRows;
          dataFrameNumColumns = dataFrame.stateValues.numColumns;
        }
        for (let arrayKey of arrayKeys) {
          let [rowInd, colInd] = arrayKey.split(",");
          if (dependencyValuesByKey[arrayKey].cellText !== void 0) {
            cells[arrayKey] = dependencyValuesByKey[arrayKey].cellText;
          } else if (rowInd < dataFrameNumRows && colInd < dataFrameNumColumns) {
            cells[arrayKey] = dataFrame.stateValues.dataFrame.data[rowInd][colInd];
          } else {
            essentialCells[arrayKey] = true;
          }
        }
        let result = {};
        if (Object.keys(cells).length > 0) {
          result.setValue = { cells };
        }
        if (Object.keys(essentialCells).length > 0) {
          result.useEssentialOrDefaultValue = {
            cells: essentialCells
          };
        }
        return result;
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        dependencyNamesByKey
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.cells) {
          if (dependencyNamesByKey[arrayKey].cellText !== void 0) {
            instructions.push({
              setDependency: dependencyNamesByKey[arrayKey].cellText,
              desiredValue: desiredStateVariableValues.cells[arrayKey]
            });
          } else {
            let cellText = desiredStateVariableValues.cells[arrayKey];
            instructions.push({
              setEssentialValue: "cells",
              value: {
                [arrayKey]: cellText === null ? "" : String(cellText)
              }
            });
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.evaluatedCells = {
      isArray: true,
      public: true,
      description: "The evaluated values of all cells.",
      shadowingInstructions: {
        createComponentOfType: "cell",
        returnWrappingComponents(prefix) {
          if (prefix === "evaluatedCell") {
            return [];
          } else if (prefix === "evaluatedRow") {
            return [["row"]];
          } else if (prefix === "evaluatedColumn") {
            return [["column"]];
          } else {
            return [["row"], ["cellBlock"]];
          }
        }
      },
      entryPrefixes: [
        "evaluatedCell",
        "evaluatedRow",
        "evaluatedColumn",
        "evaluatedRange",
        "evaluatedRows",
        "evaluatedColumns"
      ],
      schemaSubarrays: {
        evaluatedRows: {
          numDimensions: 2,
          description: "The evaluated cell values grouped by row."
        },
        evaluatedColumns: {
          numDimensions: 2,
          description: "The evaluated cell values grouped by column."
        }
      },
      numDimensions: 2,
      stateVariablesDeterminingDependencies: ["cellIndicesByRowCol"],
      returnArraySizeDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numRows, dependencyValues.numColumns];
      },
      returnEntryDimensions: (prefix) => {
        if (prefix === "evaluatedCell") {
          return 0;
        } else if ([
          "evaluatedRange",
          "evaluatedRows",
          "evaluatedColumns"
        ].includes(prefix)) {
          return 2;
        } else {
          return 1;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "evaluatedCell") {
          let rowNum, colNum;
          let letterNumStyle = /^([a-zA-Z]+)([1-9]\d*)$/;
          let result = varEnding.match(letterNumStyle);
          if (result) {
            colNum = result[1];
            rowNum = result[2];
          } else {
            let tupleStyle = /^\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\)$/;
            result = varEnding.match(tupleStyle);
            if (result) {
              rowNum = result[1];
              colNum = result[2];
            } else {
              return [];
            }
          }
          let rowIndex = normalizeIndex(rowNum);
          let colIndex = normalizeIndex(colNum);
          if (!(rowIndex >= 0 && colIndex >= 0)) {
            return [];
          }
          if (arraySize && !(rowIndex < arraySize[0] && colIndex < arraySize[1])) {
            return [];
          }
          return [String(rowIndex) + "," + String(colIndex)];
        } else if (arrayEntryPrefix === "evaluatedRow") {
          let rowIndex = normalizeIndex(varEnding);
          if (!(rowIndex >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [String(rowIndex) + ",0"];
          }
          if (!(rowIndex < arraySize[0])) {
            return [];
          }
          let arrayKeys = [];
          let arrayKeyBegin = String(rowIndex) + ",";
          for (let i2 = 0; i2 < arraySize[1]; i2++) {
            arrayKeys.push(arrayKeyBegin + i2);
          }
          return arrayKeys;
        } else if (arrayEntryPrefix === "evaluatedColumn") {
          let colIndex = normalizeIndex(varEnding);
          if (!(colIndex >= 0)) {
            return [];
          }
          if (!arraySize) {
            return ["0," + String(colIndex)];
          }
          if (!(colIndex < arraySize[1])) {
            return [];
          }
          let arrayKeys = [];
          let arrayKeyEnd = "," + String(colIndex);
          for (let i2 = 0; i2 < arraySize[0]; i2++) {
            arrayKeys.push(i2 + arrayKeyEnd);
          }
          return arrayKeys;
        } else if (arrayEntryPrefix === "evaluatedRange") {
          let fromRow, fromCol, toRow, toCol;
          let letterNumStyle = /^([a-zA-Z]+)([1-9]\d*)([a-zA-Z]+)([1-9]\d*)$/;
          let result = varEnding.match(letterNumStyle);
          if (result) {
            fromCol = normalizeIndex(result[1]);
            fromRow = normalizeIndex(result[2]);
            toCol = normalizeIndex(result[3]);
            toRow = normalizeIndex(result[4]);
          } else {
            let tupleStyle = /^\(\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\),\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\)\)$/;
            result = varEnding.match(tupleStyle);
            if (result) {
              fromRow = normalizeIndex(result[1]);
              fromCol = normalizeIndex(result[2]);
              toRow = normalizeIndex(result[3]);
              toCol = normalizeIndex(result[4]);
            } else {
              return [];
            }
          }
          if (!(fromRow >= 0 && toRow >= 0 && fromCol >= 0 && toCol >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [String(fromRow) + "," + String(fromCol)];
          }
          let row1 = Math.min(
            Math.min(fromRow, toRow),
            arraySize[0] - 1
          );
          let row2 = Math.min(
            Math.max(fromRow, toRow),
            arraySize[0] - 1
          );
          let col1 = Math.min(
            Math.min(fromCol, toCol),
            arraySize[1] - 1
          );
          let col2 = Math.min(
            Math.max(fromCol, toCol),
            arraySize[1] - 1
          );
          let arrayKeys = [];
          for (let rowIndex = row1; rowIndex <= row2; rowIndex++) {
            let rowKeys = [];
            let arrayKeyBegin = String(rowIndex) + ",";
            for (let colIndex = col1; colIndex <= col2; colIndex++) {
              rowKeys.push(arrayKeyBegin + colIndex);
            }
            arrayKeys.push(rowKeys);
          }
          return arrayKeys;
        } else {
          if (!arraySize) {
            return [String(0) + "," + String(0)];
          }
          let arrayKeys = [];
          for (let rowIndex = 0; rowIndex < arraySize[0]; rowIndex++) {
            let rowKeys = [];
            let arrayKeyBegin = String(rowIndex) + ",";
            for (let colIndex = 0; colIndex < arraySize[1]; colIndex++) {
              rowKeys.push(arrayKeyBegin + colIndex);
            }
            arrayKeys.push(rowKeys);
          }
          return arrayKeys;
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        return "evaluatedCell(" + arrayKey.split(",").map((x2) => Number(x2) + 1).join(",") + ")";
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "evaluatedCells" || varName === "evaluatedRows") {
          if (propIndex.length === 1) {
            return "evaluatedRow" + propIndex[0];
          } else {
            return `evaluatedCell(${propIndex[0]},${propIndex[1]})`;
          }
        }
        if (varName === "evaluatedColumns") {
          if (propIndex.length === 1) {
            return "evaluatedColumn" + propIndex[0];
          } else {
            return `evaluatedCell(${propIndex[1]},${propIndex[0]})`;
          }
        }
        if (varName.slice(0, 12) === "evaluatedRow") {
          let rowNum = varName.slice(12);
          return `evaluatedCell(${rowNum},${propIndex[0]})`;
        }
        if (varName.slice(0, 15) === "evaluatedColumn") {
          let columnNum = varName.slice(15);
          return `evaluatedCell(${propIndex[0]},${columnNum})`;
        }
        return null;
      },
      returnArrayDependenciesByKey: () => ({
        globalDependencies: {
          cells: {
            dependencyType: "stateVariable",
            variableName: "cells"
          }
        }
      }),
      arrayDefinitionByKey({ globalDependencyValues }) {
        let hf = HyperFormula.buildFromArray(
          globalDependencyValues.cells,
          {
            licenseKey: "gpl-v3"
          }
        );
        let allEvaluated = hf.getSheetValues(0);
        let evaluatedCells = {};
        for (let ind1 in allEvaluated) {
          let row = allEvaluated[ind1];
          for (let ind2 in row) {
            evaluatedCells[[ind1, ind2]] = row[ind2];
          }
        }
        return { setValue: { evaluatedCells } };
      }
      // inverseArrayDefinitionByKey({ desiredStateVariableValues }) {
      //   // console.log(`inverse definition by key of evaluatedCells`)
      //   // console.log(desiredStateVariableValues);
      //   let instructions = [];
      //   for (let arrayKey in desiredStateVariableValues.evaluatedCells) {
      //     instructions.push({
      //       setEssentialValue: "evaluatedCells",
      //       value: { [arrayKey]: desiredStateVariableValues.evaluatedCells[arrayKey] }
      //     })
      //   }
      //   return {
      //     success: true,
      //     instructions
      //   }
      // }
    };
    stateVariableDefinitions.pointsInCells = {
      isArray: true,
      numDimensions: 2,
      public: true,
      description: "Cells whose values are interpreted as points.",
      shadowingInstructions: {
        createComponentOfType: "point"
      },
      entryPrefixes: [
        "pointsInCell",
        "pointsInRow",
        "pointsInColumn",
        "pointsInRange",
        "pointsInRows",
        "pointsInColumns"
      ],
      schemaSubarrays: {
        pointsInRows: {
          numDimensions: 2,
          description: "Cell-derived points grouped by row."
        },
        pointsInColumns: {
          numDimensions: 2,
          description: "Cell-derived points grouped by column."
        }
      },
      stateVariablesDeterminingDependencies: ["cellIndicesByRowCol"],
      returnArraySizeDependencies: () => ({
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        },
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numRows, dependencyValues.numColumns];
      },
      returnEntryDimensions: (prefix) => {
        if (prefix === "pointsInCell") {
          return 0;
        } else if ([
          "pointsInRange",
          "pointsInRows",
          "pointsInColumns"
        ].includes(prefix)) {
          return 2;
        } else {
          return 1;
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "pointsInCell") {
          let rowNum, colNum;
          let letterNumStyle = /^([a-zA-Z]+)([1-9]\d*)$/;
          let result = varEnding.match(letterNumStyle);
          if (result) {
            colNum = result[1];
            rowNum = result[2];
          } else {
            let tupleStyle = /^\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\)$/;
            result = varEnding.match(tupleStyle);
            if (result) {
              rowNum = result[1];
              colNum = result[2];
            } else {
              return [];
            }
          }
          let rowIndex = normalizeIndex(rowNum);
          let colIndex = normalizeIndex(colNum);
          if (!(rowIndex >= 0 && colIndex >= 0)) {
            return [];
          }
          if (arraySize && !(rowIndex < arraySize[0] && colIndex < arraySize[1])) {
            return [];
          }
          return [String(rowIndex) + "," + String(colIndex)];
        } else if (arrayEntryPrefix === "pointsInRow") {
          let rowIndex = normalizeIndex(varEnding);
          if (!(rowIndex >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [String(rowIndex), ",0"];
          }
          if (!(rowIndex >= 0 && rowIndex < arraySize[0])) {
            return [];
          }
          let arrayKeys = [];
          let arrayKeyBegin = String(rowIndex) + ",";
          for (let i2 = 0; i2 < arraySize[1]; i2++) {
            arrayKeys.push(arrayKeyBegin + i2);
          }
          return arrayKeys;
        } else if (arrayEntryPrefix === "pointsInColumn") {
          let colIndex = normalizeIndex(varEnding);
          if (!(colIndex >= 0)) {
            return [];
          }
          if (!arraySize) {
            return ["0," + String(colIndex)];
          }
          if (!(colIndex < arraySize[1])) {
            return [];
          }
          let arrayKeys = [];
          let arrayKeyEnd = "," + String(colIndex);
          for (let i2 = 0; i2 < arraySize[0]; i2++) {
            arrayKeys.push(i2 + arrayKeyEnd);
          }
          return arrayKeys;
        } else if (arrayEntryPrefix === "pointsInRange") {
          let fromRow, fromCol, toRow, toCol;
          let letterNumStyle = /^([a-zA-Z]+)([1-9]\d*)([a-zA-Z]+)([1-9]\d*)$/;
          let result = varEnding.match(letterNumStyle);
          if (result) {
            fromCol = normalizeIndex(result[1]);
            fromRow = normalizeIndex(result[2]);
            toCol = normalizeIndex(result[3]);
            toRow = normalizeIndex(result[4]);
          } else {
            let tupleStyle = /^\(\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\),\(([a-zA-Z]+|\d+),([a-zA-Z]+|\d+)\)\)$/;
            result = varEnding.match(tupleStyle);
            if (result) {
              fromRow = normalizeIndex(result[1]);
              fromCol = normalizeIndex(result[2]);
              toRow = normalizeIndex(result[3]);
              toCol = normalizeIndex(result[4]);
            } else {
              return [];
            }
          }
          if (!(fromRow >= 0 && toRow >= 0 && fromCol >= 0 && toCol >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [String(fromRow) + "," + String(fromCol)];
          }
          let row1 = Math.min(
            Math.min(fromRow, toRow),
            arraySize[0] - 1
          );
          let row2 = Math.min(
            Math.max(fromRow, toRow),
            arraySize[0] - 1
          );
          let col1 = Math.min(
            Math.min(fromCol, toCol),
            arraySize[1] - 1
          );
          let col2 = Math.min(
            Math.max(fromCol, toCol),
            arraySize[1] - 1
          );
          let arrayKeys = [];
          for (let rowIndex = row1; rowIndex <= row2; rowIndex++) {
            let rowKeys = [];
            let arrayKeyBegin = String(rowIndex) + ",";
            for (let colIndex = col1; colIndex <= col2; colIndex++) {
              rowKeys.push(arrayKeyBegin + colIndex);
            }
            arrayKeys.push(rowKeys);
          }
          return arrayKeys;
        } else {
          if (!arraySize) {
            return [String(0) + "," + String(0)];
          }
          let arrayKeys = [];
          for (let rowIndex = 0; rowIndex < arraySize[0]; rowIndex++) {
            let rowKeys = [];
            let arrayKeyBegin = String(rowIndex) + ",";
            for (let colIndex = 0; colIndex < arraySize[1]; colIndex++) {
              rowKeys.push(arrayKeyBegin + colIndex);
            }
            arrayKeys.push(rowKeys);
          }
          return arrayKeys;
        }
      },
      arrayVarNameFromArrayKey(arrayKey) {
        return "pointsInCell(" + arrayKey.split(",").map((x2) => Number(x2) + 1).join(",") + ")";
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "pointsInCells" || varName === "pointsInRows") {
          if (propIndex.length === 1) {
            return "pointsInRow" + propIndex[0];
          } else {
            return `pointsInCell(${propIndex[0]},${propIndex[1]})`;
          }
        }
        if (varName === "pointsInColumns") {
          if (propIndex.length === 1) {
            return "pointsInColumn" + propIndex[0];
          } else {
            return `pointsInCell(${propIndex[1]},${propIndex[0]})`;
          }
        }
        if (varName.slice(0, 11) === "pointsInRow") {
          let rowNum = varName.slice(11);
          return `pointsInCell(${rowNum},${propIndex[0]})`;
        }
        if (varName.slice(0, 14) === "pointsInColumn") {
          let columnNum = varName.slice(14);
          return `pointsInCell(${propIndex[0]},${columnNum})`;
        }
        return null;
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let varEnding = arrayKey.split(",").map((x2) => Number(x2) + 1).join(",");
          dependenciesByKey[arrayKey] = {
            cellText: {
              dependencyType: "stateVariable",
              variableName: `evaluatedCell(${varEnding})`,
              variablesOptional: true
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let pointsInCells = {};
        for (let arrayKey of arrayKeys) {
          let cellText = dependencyValuesByKey[arrayKey].cellText;
          if (!cellText) {
            pointsInCells[arrayKey] = null;
            continue;
          }
          let cellME;
          try {
            cellME = me$3.fromAst(textToAst.convert(cellText));
          } catch (e3) {
            pointsInCells[arrayKey] = null;
            continue;
          }
          if (Array.isArray(cellME.tree) && vectorOperators.includes(cellME.tree[0])) {
            pointsInCells[arrayKey] = cellME;
          } else {
            pointsInCells[arrayKey] = null;
          }
        }
        return { setValue: { pointsInCells } };
      }
      // inverseArrayDefinitionByKey({ desiredStateVariableValues, dependencyNamesByKey }) {
      //   // console.log(`inverse definition by key of pointsInCells`)
      //   // console.log(desiredStateVariableValues);
      //   let instructions = [];
      //   for (let arrayKey in desiredStateVariableValues.pointsInCells) {
      //     instructions.push({
      //       setDependency: dependencyNamesByKey[arrayKey].cellText,
      //       desiredValue: desiredStateVariableValues.pointsInCells[arrayKey].toString()
      //     })
      //   }
      //   return {
      //     success: true,
      //     instructions
      //   }
      // }
    };
    return stateVariableDefinitions;
  }
  async onChange({
    changes,
    source,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (changes) {
      let cellChanges = {};
      for (let change of changes) {
        let [row, col, prev, value] = change;
        cellChanges[[row, col]] = value === null ? "" : value;
      }
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "cells",
            value: cellChanges
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: cellChanges
        }
      });
    }
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
function determineCellMapping({
  cellRelatedChildren,
  rowOffset = 0,
  colOffset = 0,
  cellIdxToRowCol = {},
  cellIndicesByRowCol = [],
  componentInfoObjects
}) {
  var nextCellRowIndex = rowOffset;
  var nextCellColIndex = colOffset;
  var nextCellColIndexIfBothUndefined = colOffset;
  var nextRowComponentRowIndex = rowOffset;
  var nextColComponentColIndex = colOffset;
  var maxRowIndex = rowOffset;
  var maxColIndex = colOffset;
  for (let child of cellRelatedChildren) {
    if (componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: child.componentType,
      baseComponentType: "cell"
    })) {
      let cell = child;
      let rowIndex = normalizeIndex(cell.stateValues.rowNum);
      let colIndex = normalizeIndex(cell.stateValues.colNum);
      if (rowIndex === void 0) {
        rowIndex = nextCellRowIndex;
        if (colIndex === void 0) {
          colIndex = nextCellColIndexIfBothUndefined;
        } else {
          colIndex = colIndex + colOffset;
        }
      } else {
        rowIndex = rowIndex + rowOffset;
        if (colIndex === void 0) {
          colIndex = nextCellColIndex;
        } else {
          colIndex = colIndex + colOffset;
        }
      }
      addCellToMapping({
        cell,
        rowIndex,
        colIndex,
        cellIdxToRowCol,
        cellIndicesByRowCol
      });
      maxRowIndex = Math.max(rowIndex, maxRowIndex);
      maxColIndex = Math.max(colIndex, maxColIndex);
      nextCellRowIndex = rowIndex;
      nextCellColIndex = colIndex;
      nextCellColIndexIfBothUndefined = colIndex + 1;
    } else if (componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: child.componentType,
      baseComponentType: "row"
    })) {
      let row = child;
      let rowIndex = normalizeIndex(row.stateValues.rowNum);
      if (rowIndex === void 0) {
        rowIndex = nextRowComponentRowIndex;
      } else {
        rowIndex = rowIndex + rowOffset;
      }
      let cellChildren = row.stateValues.prescribedCellsWithColNum;
      let nextColIndex = colOffset;
      for (let cell of cellChildren) {
        let colIndex = normalizeIndex(cell.stateValues.colNum);
        if (colIndex === void 0) {
          colIndex = nextColIndex;
        } else {
          colIndex = colIndex + colOffset;
        }
        addCellToMapping({
          cell,
          rowIndex,
          colIndex,
          cellIdxToRowCol,
          cellIndicesByRowCol
        });
        nextColIndex = colIndex + 1;
        maxRowIndex = Math.max(rowIndex, maxRowIndex);
        maxColIndex = Math.max(colIndex, maxColIndex);
      }
      nextRowComponentRowIndex = rowIndex + 1;
      nextCellRowIndex = rowIndex + 1;
      nextCellColIndex = colOffset;
      nextCellColIndexIfBothUndefined = nextCellColIndex;
    } else if (componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: child.componentType,
      baseComponentType: "column"
    })) {
      let col = child;
      let colIndex = normalizeIndex(col.stateValues.colNum);
      if (colIndex === void 0) {
        colIndex = nextColComponentColIndex;
      } else {
        colIndex = colIndex + colOffset;
      }
      let cellChildren = col.stateValues.prescribedCellsWithRowNum;
      let nextRowIndex = rowOffset;
      for (let cell of cellChildren) {
        let rowIndex = normalizeIndex(cell.stateValues.rowNum);
        if (rowIndex === void 0) {
          rowIndex = nextRowIndex;
        } else {
          rowIndex = rowIndex + rowOffset;
        }
        addCellToMapping({
          cell,
          rowIndex,
          colIndex,
          cellIdxToRowCol,
          cellIndicesByRowCol
        });
        nextRowIndex = rowIndex + 1;
        maxRowIndex = Math.max(rowIndex, maxRowIndex);
        maxColIndex = Math.max(colIndex, maxColIndex);
      }
      nextColComponentColIndex = colIndex + 1;
      nextCellRowIndex = rowOffset;
      nextCellColIndex = colIndex + 1;
      nextCellColIndexIfBothUndefined = nextCellColIndex;
    } else if (componentInfoObjects.isInheritedComponentType({
      inheritedComponentType: child.componentType,
      baseComponentType: "cellBlock"
    })) {
      let cellBlock = child;
      let rowIndex = normalizeIndex(cellBlock.stateValues.rowNum);
      let colIndex = normalizeIndex(cellBlock.stateValues.colNum);
      if (rowIndex === void 0) {
        rowIndex = nextCellRowIndex;
        if (colIndex === void 0) {
          colIndex = nextCellColIndexIfBothUndefined;
        } else {
          colIndex = colIndex + colOffset;
        }
      } else {
        rowIndex = rowIndex + rowOffset;
        if (colIndex === void 0) {
          colIndex = nextCellColIndex;
        } else {
          colIndex = colIndex + colOffset;
        }
      }
      let results = determineCellMapping({
        cellRelatedChildren: cellBlock.stateValues.prescribedCellsRowsColumnsBlocks,
        rowOffset: rowIndex,
        colOffset: colIndex,
        cellIdxToRowCol,
        cellIndicesByRowCol,
        componentInfoObjects
      });
      maxRowIndex = Math.max(results.maxRowIndex, maxRowIndex);
      maxColIndex = Math.max(results.maxColIndex, maxColIndex);
      nextCellRowIndex = rowIndex;
      nextCellColIndex = colIndex;
      nextCellColIndexIfBothUndefined = results.maxColIndex + 1;
    }
  }
  return { maxRowIndex, maxColIndex, cellIdxToRowCol, cellIndicesByRowCol };
}
function addCellToMapping({
  cell,
  rowIndex,
  colIndex,
  cellIdxToRowCol,
  cellIndicesByRowCol
}) {
  cellIdxToRowCol[cell.componentIdx] = [rowIndex, colIndex];
  if (cellIndicesByRowCol[rowIndex] === void 0) {
    cellIndicesByRowCol[rowIndex] = [];
  }
  if (cellIndicesByRowCol[rowIndex][colIndex] !== void 0) {
    console.warn(
      `Cell is overwriting previous cell at rowNum=${rowIndex + 1}, colNum=${colIndex + 1}`
    );
    let previousComponentIdx = cellIndicesByRowCol[rowIndex][colIndex];
    cellIdxToRowCol[previousComponentIdx] = null;
  }
  cellIndicesByRowCol[rowIndex][colIndex] = cell.componentIdx;
}
class DiscreteSimulationResultList extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      onChange: this.onChange.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "DiscreteSimulationResultList";
  static rendererType = "spreadsheet";
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      description: "Display width of the results list.",
      createComponentOfType: "componentSize",
      createStateVariable: "width",
      defaultValue: { size: 100, isAbsolute: false },
      public: true,
      forRenderer: true
    };
    attributes.minNumRows = {
      description: "Minimum number of rows to display.",
      createComponentOfType: "number",
      createStateVariable: "minNumRows",
      defaultValue: 1,
      public: true,
      forRenderer: true
    };
    attributes.minNumColumns = {
      description: "Minimum number of columns to display.",
      createComponentOfType: "number",
      createStateVariable: "minNumColumns",
      defaultValue: 1,
      public: true,
      forRenderer: true
    };
    attributes.height = {
      createComponentOfType: "componentSize"
    };
    attributes.columnHeaders = {
      description: "Header labels for the columns.",
      createComponentOfType: "boolean",
      createStateVariable: "columnHeaders",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.rowHeaders = {
      description: "Header labels for the rows.",
      createComponentOfType: "boolean",
      createStateVariable: "rowHeaders",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.fixedRowsTop = {
      description: "Number of top rows fixed in place when scrolling.",
      createComponentOfType: "integer",
      createStateVariable: "fixedRowsTop",
      defaultValue: 0,
      clamp: [0, Infinity],
      public: true,
      forRenderer: true
    };
    attributes.fixedColumnsLeft = {
      description: "Number of left columns fixed in place when scrolling.",
      createComponentOfType: "integer",
      createStateVariable: "fixedColumnsLeft",
      defaultValue: 0,
      clamp: [0, Infinity],
      public: true,
      forRenderer: true
    };
    attributes.hiddenColumns = {
      description: "Indices of columns to hide.",
      createComponentOfType: "numberList",
      createStateVariable: "hiddenColumns",
      defaultValue: [],
      public: true,
      forRenderer: true
    };
    attributes.hiddenRows = {
      description: "Indices of rows to hide.",
      createComponentOfType: "numberList",
      createStateVariable: "hiddenRows",
      defaultValue: [],
      public: true,
      forRenderer: true
    };
    attributes.allIterates = {
      createComponentOfType: "mathList",
      createStateVariable: "allIterates",
      defaultValue: []
    };
    attributes.headerRow = {
      createComponentOfType: "textList",
      createStateVariable: "headerRow",
      defaultValue: null
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.cells = {
      forRenderer: true,
      shadowVariable: true,
      returnDependencies: () => ({
        allIterates: {
          dependencyType: "stateVariable",
          variableName: "allIterates"
        },
        headerRow: {
          dependencyType: "stateVariable",
          variableName: "headerRow"
        },
        displayDigits: {
          dependencyType: "stateVariable",
          variableName: "displayDigits"
        },
        displayDecimals: {
          dependencyType: "stateVariable",
          variableName: "displayDecimals"
        },
        displaySmallAsZero: {
          dependencyType: "stateVariable",
          variableName: "displaySmallAsZero"
        }
      }),
      definition({ dependencyValues }) {
        let cells = [];
        let numComponents = 1;
        let haveVector = false;
        if (dependencyValues.allIterates.length > 0 && Array.isArray(dependencyValues.allIterates[0].tree) && vectorOperators.includes(
          dependencyValues.allIterates[0].tree[0]
        )) {
          numComponents = dependencyValues.allIterates[0].tree.length - 1;
          haveVector = true;
        }
        if (dependencyValues.headerRow) {
          let headerRow = [];
          let headerLen = Math.min(
            dependencyValues.headerRow.length,
            numComponents + 1
          );
          for (let comp = 0; comp < headerLen; comp++) {
            headerRow.push(dependencyValues.headerRow[comp]);
          }
          cells.push(headerRow);
        }
        for (let [
          ind,
          iter
        ] of dependencyValues.allIterates.entries()) {
          let cell = [ind.toString()];
          for (let comp = 0; comp < numComponents; comp++) {
            let val = iter;
            if (haveVector) {
              val = val.get_component(comp);
            }
            let rounded = roundForDisplay({
              value: val,
              dependencyValues
            });
            try {
              cell.push(rounded.toString());
            } catch (e3) {
              cell.push("");
            }
          }
          cells.push(cell);
        }
        return { setValue: { cells } };
      }
    };
    stateVariableDefinitions.numRows = {
      description: "Number of rows in the results list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        minNumRows: {
          dependencyType: "stateVariable",
          variableName: "minNumRows"
        },
        cells: {
          dependencyType: "stateVariable",
          variableName: "cells"
        }
      }),
      definition({ dependencyValues }) {
        let numRows = dependencyValues.minNumRows;
        if (!Number.isFinite(numRows)) {
          numRows = 4;
        }
        numRows = Math.max(numRows, dependencyValues.cells.length);
        return { setValue: { numRows } };
      }
    };
    stateVariableDefinitions.numColumns = {
      description: "Number of columns in the results list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        minNumColumns: {
          dependencyType: "stateVariable",
          variableName: "minNumColumns"
        },
        cells: {
          dependencyType: "stateVariable",
          variableName: "cells"
        }
      }),
      definition({ dependencyValues }) {
        let numColumns = dependencyValues.minNumColumns;
        if (!Number.isFinite(numColumns)) {
          numColumns = 4;
        }
        for (let row of dependencyValues.cells) {
          if (row) {
            numColumns = Math.max(numColumns, row.length);
          }
        }
        return { setValue: { numColumns } };
      }
    };
    stateVariableDefinitions.height = {
      description: "Display height of the results list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      forRenderer: true,
      returnDependencies: () => ({
        heightAttr: {
          dependencyType: "attributeComponent",
          attributeName: "height",
          variableNames: ["componentSize"]
        },
        numRows: {
          dependencyType: "stateVariable",
          variableName: "numRows"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.heightAttr === null) {
          let height;
          if (Number.isFinite(dependencyValues.numRows) && dependencyValues.numRows >= 0) {
            height = 50 + dependencyValues.numRows * 20;
          } else {
            height = 130;
          }
          return {
            setValue: {
              height: { size: height, isAbsolute: true }
            }
          };
        }
        return {
          setValue: {
            height: dependencyValues.heightAttr.stateValues.componentSize
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async onChange({
    changes,
    source,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (changes) {
      let cellChanges = {};
      for (let change of changes) {
        let [row, col, prev, value] = change;
        cellChanges[[row, col]] = value === null ? "" : value;
      }
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "cells",
            value: cellChanges
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: cellChanges
        }
      });
    }
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class DiscreteSimulationResultPolyline extends GraphicalComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      movePolyline: this.movePolyline.bind(this),
      finalizePolylinePosition: this.finalizePolylinePosition.bind(this),
      polylineClicked: this.polylineClicked.bind(this),
      polylineFocused: this.polylineFocused.bind(this)
    });
  }
  static componentType = "discreteSimulationResultPolyline";
  static componentDocs = {
    summary: "A polyline of points produced by a discrete simulation."
  };
  static rendererType = "polyline";
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the result polyline can be dragged.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.allIterates = {
      createComponentOfType: "mathList",
      createStateVariable: "allIteratesSub",
      defaultValue: []
    };
    attributes.seriesNumber = {
      createComponentOfType: "number",
      createStateVariable: "seriesNumber",
      defaultValue: null
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the polyline's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let lineColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          lineColorWord = dependencyValues.selectedStyle.lineColorWordDarkMode;
        } else {
          lineColorWord = dependencyValues.selectedStyle.lineColorWord;
        }
        let curveDescription = dependencyValues.selectedStyle.lineWidthWord;
        if (dependencyValues.selectedStyle.lineStyleWord) {
          if (curveDescription) {
            curveDescription += " ";
          }
          curveDescription += dependencyValues.selectedStyle.lineStyleWord;
        }
        if (curveDescription) {
          curveDescription += " ";
        }
        curveDescription += lineColorWord;
        return { setValue: { styleDescription: curveDescription } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "polyline".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " polyline";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.allIterates = {
      returnDependencies: () => ({
        allIteratesSub: {
          dependencyType: "stateVariable",
          variableName: "allIteratesSub"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            allIterates: dependencyValues.allIteratesSub.filter(
              (x2) => Number.isFinite(x2.tree) || Array.isArray(x2.tree) && vectorOperators.includes(x2.tree[0]) && x2.tree.slice(1).every(Number.isFinite)
            )
          }
        };
      }
    };
    stateVariableDefinitions.numVertices = {
      description: "Number of vertices in the polyline.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({
        allIterates: {
          dependencyType: "stateVariable",
          variableName: "allIterates"
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            numVertices: dependencyValues.allIterates.length
          }
        };
      }
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions of each vertex.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: function({ dependencyValues }) {
        return { setValue: { numDimensions: 2 } };
      }
    };
    stateVariableDefinitions.vertices = {
      description: "The vertices of the polyline.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        returnWrappingComponents(prefix) {
          if (prefix === "vertexX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["vertexX", "vertex"],
      returnEntryDimensions: (prefix) => prefix === "vertex" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "vertexX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let pointInd = Number(varEnding) - 1;
          if (!(Number.isInteger(pointInd) && pointInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [pointInd + ",0"];
          }
          if (pointInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => pointInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "vertices") {
          if (propIndex.length === 1) {
            return "vertex" + propIndex[0];
          } else {
            return `vertexX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 6) === "vertex") {
          let vertexNum = Number(varName.slice(6));
          if (Number.isInteger(vertexNum) && vertexNum > 0) {
            return `vertexX${vertexNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      getAllArrayKeys(arraySize, flatten2 = true, desiredSize) {
        function getAllArrayKeysSub(subArraySize) {
          if (subArraySize.length === 1) {
            return Array.from(
              Array(subArraySize[0]),
              (_2, i2) => String(i2)
            );
          } else {
            let currentSize = subArraySize[0];
            let subSubKeys = getAllArrayKeysSub(
              subArraySize.slice(1)
            );
            let subKeys = [];
            for (let ind = 0; ind < currentSize; ind++) {
              if (flatten2) {
                subKeys.push(
                  ...subSubKeys.map((x2) => ind + "," + x2)
                );
              } else {
                subKeys.push(
                  subSubKeys.map((x2) => ind + "," + x2)
                );
              }
            }
            return subKeys;
          }
        }
        if (desiredSize) {
          if (desiredSize.length === 0 || !arraySize) {
            return [];
          } else {
            let desiredSizeOfWholeArray = [...arraySize];
            desiredSizeOfWholeArray[0] = desiredSize[0];
            return getAllArrayKeysSub(desiredSizeOfWholeArray);
          }
        } else if (!arraySize || arraySize.length === 0) {
          return [];
        } else {
          return getAllArrayKeysSub(arraySize);
        }
      },
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numVertices, 2];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          seriesNumber: {
            dependencyType: "stateVariable",
            variableName: "seriesNumber"
          },
          allIterates: {
            dependencyType: "stateVariable",
            variableName: "allIterates"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arraySize }) {
        let vertices = {};
        for (let ind = 0; ind < arraySize[0]; ind++) {
          vertices[`${ind},0`] = me$3.fromAst(ind);
          let val = globalDependencyValues.allIterates[ind];
          if (globalDependencyValues.seriesNumber !== null) {
            val = val.get_component(
              globalDependencyValues.seriesNumber - 1
            );
          }
          vertices[`${ind},1`] = val;
        }
        return { setValue: { vertices } };
      }
    };
    stateVariableDefinitions.numericalVertices = {
      isArray: true,
      entryPrefixes: ["numericalVertex"],
      forRenderer: true,
      returnArraySizeDependencies: () => ({
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numVertices];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            vertex: {
              dependencyType: "stateVariable",
              variableName: "vertex" + (Number(arrayKey) + 1)
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let numericalVertices = {};
        for (let arrayKey of arrayKeys) {
          let vert = dependencyValuesByKey[arrayKey].vertex.map(
            (x2) => x2.evaluate_to_constant()
          );
          if (!vert.every((x2) => Number.isFinite(x2))) {
            vert = Array(vert.length).fill(NaN);
          }
          numericalVertices[arrayKey] = vert;
        }
        return { setValue: { numericalVertices } };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        numericalVertices: {
          dependencyType: "stateVariable",
          variableName: "numericalVertices"
        },
        numVertices: {
          dependencyType: "stateVariable",
          variableName: "numVertices"
        }
      }),
      definition({ dependencyValues }) {
        let numDimensions = dependencyValues.numDimensions;
        let numVertices = dependencyValues.numVertices;
        let numericalVertices = dependencyValues.numericalVertices;
        let vals = [];
        let prPtx, prPty;
        let nxPtx = numericalVertices[0]?.[0];
        let nxPty = numericalVertices[0]?.[1];
        for (let i2 = 1; i2 < numVertices; i2++) {
          prPtx = nxPtx;
          prPty = nxPty;
          nxPtx = numericalVertices[i2]?.[0];
          nxPty = numericalVertices[i2]?.[1];
          if (!(Number.isFinite(prPtx) && Number.isFinite(prPty) && Number.isFinite(nxPtx) && Number.isFinite(nxPty))) {
            vals.push(null);
          } else {
            let BA1sub = nxPtx - prPtx;
            let BA2sub = nxPty - prPty;
            if (BA1sub === 0 && BA2sub === 0) {
              vals.push(null);
            } else {
              vals.push([BA1sub, BA2sub]);
            }
          }
        }
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              let xscale = scales[0];
              let yscale = scales[1];
              if (numDimensions !== 2 || numVertices === 0) {
                return {};
              }
              let closestDistance2 = Infinity;
              let closestResult = {};
              let x1 = variables.x1?.evaluate_to_constant();
              let x2 = variables.x2?.evaluate_to_constant();
              let prevPtx, prevPty;
              let nextPtx = numericalVertices[0][0];
              let nextPty = numericalVertices[0][1];
              for (let i2 = 1; i2 < numVertices; i2++) {
                prevPtx = nextPtx;
                prevPty = nextPty;
                nextPtx = numericalVertices[i2][0];
                nextPty = numericalVertices[i2][1];
                let val = vals[i2 - 1];
                if (val === null) {
                  continue;
                }
                let BA1 = val[0] / xscale;
                let BA2 = val[1] / yscale;
                let denom = BA1 * BA1 + BA2 * BA2;
                let t2 = ((x1 - prevPtx) / xscale * BA1 + (x2 - prevPty) / yscale * BA2) / denom;
                let result;
                if (t2 <= 0) {
                  result = { x1: prevPtx, x2: prevPty };
                } else if (t2 >= 1) {
                  result = { x1: nextPtx, x2: nextPty };
                } else {
                  result = {
                    x1: prevPtx + t2 * BA1 * xscale,
                    x2: prevPty + t2 * BA2 * yscale
                  };
                }
                let distance2 = Math.pow((x1 - result.x1) / xscale, 2) + Math.pow((x2 - result.x2) / yscale, 2);
                if (distance2 < closestDistance2) {
                  closestDistance2 = distance2;
                  closestResult = result;
                }
              }
              if (variables.x3 !== void 0 && Object.keys(closestResult).length > 0) {
                closestResult.x3 = 0;
              }
              return closestResult;
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async movePolyline({
    pointCoords,
    transient,
    sourceDetails,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let vertexComponents = {};
    for (let ind in pointCoords) {
      vertexComponents[ind + ",0"] = me$3.fromAst(pointCoords[ind][0]);
      vertexComponents[ind + ",1"] = me$3.fromAst(pointCoords[ind][1]);
    }
    if (transient) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "vertices",
            value: vertexComponents,
            sourceDetails
          }
        ],
        transient: true,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "vertices",
            value: vertexComponents,
            sourceDetails
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            pointCoordinates: pointCoords
          }
        }
      });
    }
  }
  async finalizePolylinePosition({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.actions.movePolyline({
      pointCoords: await this.stateValues.numericalVertices,
      transient: false,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async polylineClicked({
    actionId,
    name,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx: name,
        // use name rather than this.componentIdx to get original name if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async polylineFocused({
    actionId,
    name,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx: name,
        // use name rather than this.componentIdx to get original name if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class Cell extends BaseComponent {
  static componentType = "cell";
  static componentDocs = {
    summary: "A single cell within a tabular layout or spreadsheet"
  };
  static rendererType = "cell";
  static renderChildren = true;
  static includeBlankStringChildren = true;
  static primaryStateVariableForDefinition = "text";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.rowNum = {
      createComponentOfType: "text",
      createStateVariable: "rowNum",
      defaultValue: null,
      public: true,
      description: "Row number where this cell is placed (1-based)."
    };
    attributes.colNum = {
      createComponentOfType: "text",
      createStateVariable: "colNum",
      defaultValue: null,
      public: true,
      description: "Column number where this cell is placed (1-based)."
    };
    attributes.colSpan = {
      createComponentOfType: "integer",
      createStateVariable: "colSpan",
      defaultValue: 1,
      public: true,
      forRenderer: true,
      description: "Number of columns this cell spans."
    };
    attributes.halign = {
      createComponentOfType: "text",
      description: "Horizontal alignment for the cell's content (left, center, or right)."
    };
    attributes.bottom = {
      createComponentOfType: "text",
      description: "Border style for the bottom edge of the cell."
    };
    attributes.right = {
      createComponentOfType: "text",
      description: "Border style for the right edge of the cell."
    };
    attributes.prefill = {
      createComponentOfType: "text",
      createStateVariable: "prefill",
      defaultValue: "",
      public: true,
      description: "Initial text content of the cell."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      },
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.halign = {
      description: "Horizontal alignment of the cell's content (left, center, or right).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      hasEssential: true,
      defaultValue: "left",
      returnDependencies: () => ({
        halignAttr: {
          dependencyType: "attributeComponent",
          attributeName: "halign",
          variableNames: ["value"]
        },
        parentHalign: {
          dependencyType: "parentStateVariable",
          variableName: "halign"
        },
        // TODO: get halign for corresponding col
        tabularHalign: {
          dependencyType: "ancestor",
          componentType: "tabular",
          variableNames: ["halign"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.halignAttr !== null) {
          let halign = dependencyValues.halignAttr.stateValues.value;
          if (!["left", "center", "right", "justify"].includes(halign)) {
            halign = "left";
          }
          return { setValue: { halign } };
        } else if (!usedDefault.parentHalign && dependencyValues.parentHalign) {
          return {
            setValue: { halign: dependencyValues.parentHalign }
          };
        } else if (!usedDefault.tabularHalign && dependencyValues.tabularHalign) {
          return {
            setValue: {
              halign: dependencyValues.tabularHalign.stateValues.halign
            }
          };
        } else {
          return { useEssentialOrDefaultValue: { halign: true } };
        }
      }
    };
    stateVariableDefinitions.bottom = {
      description: "Border style for the bottom edge of the cell.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      hasEssential: true,
      defaultValue: "none",
      returnDependencies: () => ({
        bottomAttr: {
          dependencyType: "attributeComponent",
          attributeName: "bottom",
          variableNames: ["value"]
        },
        parentBottom: {
          dependencyType: "parentStateVariable",
          variableName: "bottom"
        },
        tabularBottom: {
          dependencyType: "ancestor",
          componentType: "tabular",
          variableNames: ["bottom"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.bottomAttr !== null) {
          let bottom = dependencyValues.bottomAttr.stateValues.value;
          if (!["none", "minor", "medium", "major"].includes(bottom)) {
            bottom = "none";
          }
          return { setValue: { bottom } };
        } else if (!usedDefault.parentBottom && dependencyValues.parentBottom) {
          return {
            setValue: { bottom: dependencyValues.parentBottom }
          };
        } else if (!usedDefault.tabularBottom && dependencyValues.tabularBottom) {
          return {
            setValue: {
              bottom: dependencyValues.tabularBottom.stateValues.bottom
            }
          };
        } else {
          return { useEssentialOrDefaultValue: { bottom: true } };
        }
      }
    };
    stateVariableDefinitions.right = {
      description: "Border style for the right edge of the cell.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      hasEssential: true,
      defaultValue: "none",
      returnDependencies: () => ({
        rightAttr: {
          dependencyType: "attributeComponent",
          attributeName: "right",
          variableNames: ["value"]
        },
        // TODO: get right for corresponding col
        tabularRight: {
          dependencyType: "ancestor",
          componentType: "tabular",
          variableNames: ["right"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.rightAttr !== null) {
          let right = dependencyValues.rightAttr.stateValues.value;
          if (!["none", "minor", "medium", "major"].includes(right)) {
            right = "none";
          }
          return { setValue: { right } };
        } else if (!usedDefault.tabularRight && dependencyValues.tabularRight) {
          return {
            setValue: {
              right: dependencyValues.tabularRight.stateValues.right
            }
          };
        } else {
          return { useEssentialOrDefaultValue: { right: true } };
        }
      }
    };
    stateVariableDefinitions.inHeader = {
      description: "Whether this cell is in a header row of the table.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: false,
      returnDependencies: () => ({
        parentHeader: {
          dependencyType: "parentStateVariable",
          variableName: "header"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            inHeader: dependencyValues.parentHeader === true
          }
        };
      }
    };
    stateVariableDefinitions.onlyMathChild = {
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"]
        },
        otherChildren: {
          dependencyType: "child",
          childGroups: ["anything"]
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          onlyMathChild: dependencyValues.mathChild.length === 1 && dependencyValues.otherChildren.length === 0
        }
      })
    };
    stateVariableDefinitions.text = {
      description: "The cell's content as a text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      hasEssential: true,
      forRenderer: true,
      // Note: even though the default value isn't used in the definition, below,
      // it needed because `text` is the `primaryStateVariableForDefinition`, defined above.
      // When `cell` is extended from a prop, this definition is overwritten
      // with a definition that assumes a default value.
      defaultValue: "",
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["maths", "anything"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.children.length === 0) {
          return {
            useEssentialOrDefaultValue: {
              text: {
                defaultValue: dependencyValues.prefill
              }
            }
          };
        }
        let text = textFromChildren(dependencyValues.children);
        return { setValue: { text } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.children.length === 0) {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "text",
                value: desiredStateVariableValues.text === null ? "" : String(
                  desiredStateVariableValues.text
                )
              }
            ]
          };
        } else if (dependencyValues.children.length === 1) {
          if (typeof dependencyValues.children[0] === "object" && dependencyValues.children[0].stateValues.text === void 0) {
            return { success: false };
          } else {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "children",
                  desiredValue: desiredStateVariableValues.text,
                  childIndex: 0,
                  variableIndex: 0
                }
              ]
            };
          }
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.math = {
      description: "The cell's content interpreted as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      stateVariablesDeterminingDependencies: ["onlyMathChild"],
      returnDependencies({ stateValues }) {
        if (stateValues.onlyMathChild) {
          return {
            mathChild: {
              dependencyType: "child",
              childGroups: ["maths"],
              variableNames: ["value"]
            }
          };
        } else {
          return {
            text: {
              dependencyType: "stateVariable",
              variableName: "text"
            }
          };
        }
      },
      definition({ dependencyValues }) {
        if (dependencyValues.mathChild) {
          return {
            setValue: {
              math: dependencyValues.mathChild[0].stateValues.value
            }
          };
        } else {
          let math;
          try {
            math = me$3.fromAst(
              textToAst.convert(dependencyValues.text)
            );
          } catch (e3) {
            math = me$3.fromAst("＿");
          }
          return { setValue: { math } };
        }
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues,
        workspace
      }) {
        if (dependencyValues.mathChild) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "mathChild",
                desiredValue: desiredStateVariableValues.math,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          let result = await preprocessMathInverseDefinition({
            desiredValue: desiredStateVariableValues.math,
            stateValues,
            variableName: "math",
            workspace
          });
          return {
            success: true,
            instructions: [
              {
                setDependency: "text",
                desiredValue: result.desiredValue.toString()
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.number = {
      description: "The cell's content interpreted as a number.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        math: {
          dependencyType: "stateVariable",
          variableName: "math"
        }
      }),
      definition({ dependencyValues }) {
        let number = dependencyValues.math.evaluate_to_constant();
        return { setValue: { number } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "math",
              desiredValue: me$3.fromAst(
                desiredStateVariableValues.number
              )
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = ["text", "math", "number"];
}
class Row extends BaseComponent {
  static componentType = "row";
  // `<row>` is also accepted inside `<matrix>`, where it sugars into
  // `<matrixRow>`. Both entries appear in the docs index disambiguated by
  // `displayContext`: this one is `<row> (in a table)`; the matrix-flavored
  // alias (in `Aliases.js`) is `<row> (in a matrix)`.
  static componentDocs = {
    summary: "A row within a tabular layout",
    docsSlug: "row_table",
    displayContext: "in a table"
  };
  static rendererType = "row";
  static renderChildren = true;
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.rowNum = {
      description: "Row number where this row is placed (1-based).",
      createComponentOfType: "text",
      createStateVariable: "rowNum",
      defaultValue: null,
      public: true
    };
    attributes.header = {
      description: "Whether this row is a header row.",
      createComponentOfType: "boolean",
      createStateVariable: "header",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.halign = {
      createComponentOfType: "text",
      description: "Default horizontal alignment for cells in this row."
    };
    attributes.valign = {
      createComponentOfType: "text",
      description: "Default vertical alignment for cells in this row."
    };
    attributes.left = {
      createComponentOfType: "text",
      description: "Border style for the left edge of this row."
    };
    attributes.bottom = {
      createComponentOfType: "text",
      description: "Border style for the bottom edge of this row."
    };
    attributes.functionSymbols = {
      createComponentOfType: "textList",
      description: "Symbols treated as function names when parsing."
    };
    attributes.referencesAreFunctionSymbols = {
      createReferences: true,
      description: "References whose names should be treated as function symbols when parsing."
    };
    attributes.splitSymbols = {
      createComponentOfType: "boolean",
      description: "Whether multi-character symbols are split into a product of variables."
    };
    attributes.parseScientificNotation = {
      createComponentOfType: "boolean",
      description: "Whether to parse expressions like 1e3 as scientific notation."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "cells",
        componentTypes: ["cell"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.halign = {
      description: "Default horizontal alignment for cells in this row.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      defaultValue: "left",
      hasEssential: true,
      returnDependencies: () => ({
        halignAttr: {
          dependencyType: "attributeComponent",
          attributeName: "halign",
          variableNames: ["value"]
        },
        parentHalign: {
          dependencyType: "parentStateVariable",
          variableName: "halign"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.halignAttr !== null) {
          let halign = dependencyValues.halignAttr.stateValues.value;
          if (!["left", "center", "right", "justify"].includes(halign)) {
            halign = "left";
          }
          return { setValue: { halign } };
        } else if (dependencyValues.parentHalign !== null && !usedDefault.parentHalign) {
          return {
            setValue: { halign: dependencyValues.parentHalign }
          };
        } else {
          return { useEssentialOrDefaultValue: { halign: true } };
        }
      }
    };
    stateVariableDefinitions.valign = {
      description: "Default vertical alignment for cells in this row.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      defaultValue: "middle",
      hasEssential: true,
      returnDependencies: () => ({
        valignAttr: {
          dependencyType: "attributeComponent",
          attributeName: "valign",
          variableNames: ["value"]
        },
        parentValign: {
          dependencyType: "parentStateVariable",
          variableName: "valign"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.valignAttr !== null) {
          let valign = dependencyValues.valignAttr.stateValues.value;
          if (!["top", "middle", "bottom"].includes(valign)) {
            valign = "middle";
          }
          return { setValue: { valign } };
        } else if (dependencyValues.parentValign !== null && !usedDefault.parentValign) {
          return {
            setValue: { valign: dependencyValues.parentValign }
          };
        } else {
          return { useEssentialOrDefaultValue: { valign: true } };
        }
      }
    };
    stateVariableDefinitions.left = {
      description: "Border style for the left edge of the row.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      defaultValue: "none",
      hasEssential: true,
      returnDependencies: () => ({
        leftAttr: {
          dependencyType: "attributeComponent",
          attributeName: "left",
          variableNames: ["value"]
        },
        parentLeft: {
          dependencyType: "parentStateVariable",
          variableName: "left"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.leftAttr !== null) {
          let left = dependencyValues.leftAttr.stateValues.value;
          if (!["none", "minor", "medium", "major"].includes(left)) {
            left = "none";
          }
          return { setValue: { left } };
        } else if (dependencyValues.parentLeft !== null && !usedDefault.parentLeft) {
          return { setValue: { left: dependencyValues.parentLeft } };
        } else {
          return { useEssentialOrDefaultValue: { left: true } };
        }
      }
    };
    stateVariableDefinitions.bottom = {
      description: "Border style for the bottom edge of the row.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      defaultValue: "none",
      hasEssential: true,
      returnDependencies: () => ({
        bottomAttr: {
          dependencyType: "attributeComponent",
          attributeName: "bottom",
          variableNames: ["value"]
        },
        parentBottom: {
          dependencyType: "parentStateVariable",
          variableName: "bottom"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        if (dependencyValues.bottomAttr !== null) {
          let bottom = dependencyValues.bottomAttr.stateValues.value;
          if (!["none", "minor", "medium", "major"].includes(bottom)) {
            bottom = "none";
          }
          return { setValue: { bottom } };
        } else if (dependencyValues.parentBottom !== null && !usedDefault.parentBottom) {
          return {
            setValue: { bottom: dependencyValues.parentBottom }
          };
        } else {
          return { useEssentialOrDefaultValue: { bottom: true } };
        }
      }
    };
    stateVariableDefinitions.prescribedCellsWithColNum = {
      returnDependencies: () => ({
        cellChildren: {
          dependencyType: "child",
          childGroups: ["cells"],
          variableNames: ["colNum"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            prescribedCellsWithColNum: dependencyValues.cellChildren
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Column extends BaseComponent {
  static componentType = "column";
  // `<column>` is also accepted inside `<matrix>`, where it sugars into
  // `<matrixColumn>`. Both entries appear in the docs index disambiguated
  // by `displayContext`: this one is `<column> (in a table)`; the
  // matrix-flavored alias (in `Aliases.js`) is `<column> (in a matrix)`.
  static componentDocs = {
    summary: "A column of cells within a `<spreadsheet>`",
    docsSlug: "column_table",
    displayContext: "in a table"
  };
  static rendererType = "containerBlock";
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.colNum = {
      description: "Column number where this column is placed (1-based).",
      createComponentOfType: "text",
      createStateVariable: "colNum",
      defaultValue: null,
      public: true
    };
    attributes.functionSymbols = {
      createComponentOfType: "textList",
      description: "Symbols treated as function names when parsing."
    };
    attributes.referencesAreFunctionSymbols = {
      createReferences: true,
      description: "References whose names should be treated as function symbols when parsing."
    };
    attributes.splitSymbols = {
      createComponentOfType: "boolean",
      description: "Whether multi-character symbols are split into a product of variables."
    };
    attributes.parseScientificNotation = {
      createComponentOfType: "boolean",
      description: "Whether to parse expressions like 1e3 as scientific notation."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "cells",
        componentTypes: ["cell"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnPassThroughListItemChildStateVariableDefinitions()
    );
    stateVariableDefinitions.prescribedCellsWithRowNum = {
      returnDependencies: () => ({
        cellChildren: {
          dependencyType: "child",
          childGroups: ["cells"],
          variableNames: ["rowNum"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            prescribedCellsWithRowNum: dependencyValues.cellChildren
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class CellBlock extends BaseComponent {
  static componentType = "cellBlock";
  static rendererType = void 0;
  static componentDocs = {
    summary: "Container for grouping `<cell>`, `<row>`, and `<column>` children inside a `<spreadsheet>`"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.rowNum = {
      createComponentOfType: "text",
      createStateVariable: "rowNum",
      defaultValue: null,
      public: true,
      description: "Row number where this block starts (1-based; can be a letter or number)."
    };
    attributes.colNum = {
      createComponentOfType: "text",
      createStateVariable: "colNum",
      defaultValue: null,
      public: true,
      description: "Column number where this block starts (1-based; can be a letter or number)."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "children",
        componentTypes: ["cell", "row", "column", "cellBlock"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.prescribedCellsRowsColumnsBlocks = {
      returnDependencies: () => ({
        cellRelatedChildren: {
          dependencyType: "child",
          childGroups: ["children"],
          variableNames: [
            "rowNum",
            "colNum",
            "prescribedCellsWithColNum",
            "prescribedCellsWithRowNum",
            "prescribedCellsRowsColumnsBlocks"
          ],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            prescribedCellsRowsColumnsBlocks: dependencyValues.cellRelatedChildren
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Tabular extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "tabular";
  static componentDocs = {
    summary: "A simple tabular layout of cells"
  };
  static rendererType = "tabular";
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      description: "Display width of the tabular layout.",
      createComponentOfType: "componentSize",
      createStateVariable: "width",
      defaultValue: { size: 100, isAbsolute: false },
      public: true,
      forRenderer: true
    };
    attributes.height = {
      description: "Display height of the tabular layout.",
      createComponentOfType: "componentSize",
      createStateVariable: "height",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.halign = {
      description: "Default horizontal alignment for cells.",
      createComponentOfType: "text",
      createStateVariable: "halign",
      defaultValue: "left",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "left",
          description: "Left-align cell content."
        },
        {
          value: "center",
          description: "Center cell content horizontally."
        },
        {
          value: "right",
          description: "Right-align cell content."
        },
        {
          value: "justify",
          description: "Justify cell content across the full cell width."
        }
      ]
    };
    attributes.valign = {
      description: "Default vertical alignment for cells.",
      createComponentOfType: "text",
      createStateVariable: "valign",
      defaultValue: "middle",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "top",
          description: "Align cell content to the top."
        },
        {
          value: "middle",
          description: "Center cell content vertically."
        },
        {
          value: "bottom",
          description: "Align cell content to the bottom."
        }
      ]
    };
    attributes.top = {
      description: "Border style for the top edge of the layout.",
      createComponentOfType: "text",
      createStateVariable: "top",
      defaultValue: "none",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "none",
          description: "Do not render a border."
        },
        {
          value: "minor",
          description: "Render a thin border line."
        },
        {
          value: "medium",
          description: "Render a medium-weight border line."
        },
        {
          value: "major",
          description: "Render a thick border line."
        }
      ],
      forRenderer: true
    };
    attributes.left = {
      description: "Border style for the left edge of the layout.",
      createComponentOfType: "text",
      createStateVariable: "left",
      defaultValue: "none",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "none",
          description: "Do not render a border."
        },
        {
          value: "minor",
          description: "Render a thin border line."
        },
        {
          value: "medium",
          description: "Render a medium-weight border line."
        },
        {
          value: "major",
          description: "Render a thick border line."
        }
      ]
    };
    attributes.bottom = {
      description: "Border style for the bottom edge of the layout.",
      createComponentOfType: "text",
      createStateVariable: "bottom",
      defaultValue: "none",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "none",
          description: "Do not render a border."
        },
        {
          value: "minor",
          description: "Render a thin border line."
        },
        {
          value: "medium",
          description: "Render a medium-weight border line."
        },
        {
          value: "major",
          description: "Render a thick border line."
        }
      ]
    };
    attributes.right = {
      description: "Border style for the right edge of the layout.",
      createComponentOfType: "text",
      createStateVariable: "right",
      defaultValue: "none",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "none",
          description: "Do not render a border."
        },
        {
          value: "minor",
          description: "Render a thin border line."
        },
        {
          value: "medium",
          description: "Render a medium-weight border line."
        },
        {
          value: "major",
          description: "Render a thick border line."
        }
      ]
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "rows",
        componentTypes: ["row"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnListItemChildStateVariableDefinitions({
        listItemInlineAlignment: "flex-start"
      })
    );
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Table extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "table";
  static componentDocs = {
    summary: "A container element for a `<tabular>`"
  };
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.suppressTableNameInTitle = {
      createComponentOfType: "boolean",
      createStateVariable: "suppressTableNameInTitle",
      defaultValue: false,
      forRenderer: true,
      description: "Whether to omit the auto-generated table name from the title."
    };
    attributes.number = {
      createComponentOfType: "boolean",
      createStateVariable: "number",
      defaultValue: true,
      description: "Whether to display an auto-generated table number.",
      forRenderer: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "titles",
        componentTypes: ["title"]
      },
      {
        group: "inlinesBlocks",
        componentTypes: ["_inline", "_block"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.tableEnumeration = {
      description: "Auto-generated number for this table.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["number"],
      additionalStateVariablesDefined: [
        {
          variableName: "tableName",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "text"
          },
          forRenderer: true,
          description: "The full display name of the table (e.g., 'Table 2')."
        }
      ],
      mustEvaluate: true,
      // must evaluate to make sure all counters are accounted for
      returnDependencies({ stateValues }) {
        let dependencies = {};
        if (stateValues.number) {
          dependencies.tableCounter = {
            dependencyType: "counter",
            counterName: "sectioning"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        if (dependencyValues.tableCounter === void 0) {
          return {
            setValue: {
              tableEnumeration: null,
              tableName: "Table"
            }
          };
        }
        let tableEnumeration = String(dependencyValues.tableCounter);
        let tableName = "Table " + tableEnumeration;
        return {
          setValue: { tableEnumeration, tableName }
        };
      }
    };
    stateVariableDefinitions.titleChildName = {
      forRenderer: true,
      returnDependencies: () => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["titles"]
        }
      }),
      definition({ dependencyValues }) {
        let titleChildName = null;
        if (dependencyValues.titleChild.length > 0) {
          titleChildName = dependencyValues.titleChild[0].componentIdx;
        }
        return {
          setValue: { titleChildName }
        };
      }
    };
    stateVariableDefinitions.title = {
      description: "The table's title.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["titles"],
          variableNames: ["text"]
        }
      }),
      definition({ dependencyValues }) {
        let title = null;
        if (dependencyValues.titleChild.length > 0) {
          title = dependencyValues.titleChild[0].stateValues.text;
        }
        return { setValue: { title } };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Figure extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "figure";
  static componentDocs = {
    summary: "A figure container holding graphical content with a caption"
  };
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.suppressFigureNameInCaption = {
      createComponentOfType: "boolean",
      createStateVariable: "suppressFigureNameInCaption",
      defaultValue: false,
      forRenderer: true,
      description: "Whether to omit the auto-generated figure name from the caption."
    };
    attributes.number = {
      createComponentOfType: "boolean",
      createStateVariable: "number",
      defaultValue: true,
      description: "Whether to display an auto-generated figure number.",
      forRenderer: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "captions",
        componentTypes: ["caption"]
      },
      {
        group: "inlinesBlocks",
        componentTypes: ["_inline", "_block"]
      },
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.figureEnumeration = {
      description: "Auto-generated number for this figure.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["number"],
      additionalStateVariablesDefined: [
        {
          variableName: "figureName",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "text"
          },
          forRenderer: true,
          description: "The full display name of the figure (e.g., 'Figure 3')."
        }
      ],
      mustEvaluate: true,
      // must evaluate to make sure all counters are accounted for
      returnDependencies({ stateValues }) {
        let dependencies = {};
        if (stateValues.number) {
          dependencies.figureCounter = {
            dependencyType: "counter",
            counterName: "sectioning"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        if (dependencyValues.figureCounter === void 0) {
          return {
            setValue: {
              figureEnumeration: null,
              figureName: "Figure"
            }
          };
        }
        let figureEnumeration = String(dependencyValues.figureCounter);
        let figureName = "Figure " + figureEnumeration;
        return {
          setValue: { figureEnumeration, figureName }
        };
      }
    };
    stateVariableDefinitions.captionChildName = {
      forRenderer: true,
      returnDependencies: () => ({
        captionChild: {
          dependencyType: "child",
          childGroups: ["captions"]
        }
      }),
      definition({ dependencyValues }) {
        let captionChildName = null;
        if (dependencyValues.captionChild.length > 0) {
          captionChildName = dependencyValues.captionChild[0].componentIdx;
        }
        return {
          setValue: { captionChildName }
        };
      }
    };
    stateVariableDefinitions.caption = {
      description: "The figure's caption text.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        captionChild: {
          dependencyType: "child",
          childGroups: ["captions"],
          variableNames: ["text"]
        }
      }),
      definition({ dependencyValues }) {
        let caption = null;
        if (dependencyValues.captionChild.length > 0) {
          caption = dependencyValues.captionChild[0].stateValues.text;
        }
        return { setValue: { caption } };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class TextListFromString extends InlineComponent {
  static componentType = "textListFromString";
  static componentDocs = {
    summary: "A list of text strings parsed from a single string by splitting on whitespace."
  };
  static rendererType = "asList";
  static renderChildren = true;
  static excludeFromSchema = true;
  static stateVariableToBeShadowed = "texts";
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakStringsBySpaces = function({ matchedChildren, nComponents }) {
      let newChildren = matchedChildren.reduce(function(a2, c2) {
        if (typeof c2 === "string") {
          return [...a2, ...c2.split(/\s+/).filter((s2) => s2)];
        } else {
          return [...a2, c2];
        }
      }, []);
      return {
        success: true,
        newChildren,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakStringsBySpaces
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "strings",
        componentTypes: ["string"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.numComponents = {
      description: "The number of items in the text list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        stringChildren: {
          dependencyType: "child",
          childGroups: ["strings"]
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            numComponents: dependencyValues.stringChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.texts = {
      description: "The list of texts parsed from the input string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      isArray: true,
      entryPrefixes: ["text"],
      returnArraySizeDependencies: () => ({
        numComponents: {
          dependencyType: "stateVariable",
          variableName: "numComponents"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numComponents];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            stringChild: {
              dependencyType: "child",
              childGroups: ["strings"],
              variableNames: ["value"],
              childIndices: [arrayKey]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey: function({
        dependencyValuesByKey,
        arrayKeys
      }) {
        let texts = {};
        for (let arrayKey of arrayKeys) {
          if (dependencyValuesByKey[arrayKey].stringChild.length === 1) {
            texts[arrayKey] = dependencyValuesByKey[arrayKey].stringChild[0];
          }
        }
        return { setValue: { texts } };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numComponents",
      description: "The number of text values in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "texts",
      description: "The list's text values."
    };
    stateVariableDefinitions.text = {
      description: "The texts joined as a single text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        texts: {
          dependencyType: "stateVariable",
          variableName: "texts"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { text: dependencyValues.texts.join(", ") }
      })
    };
    return stateVariableDefinitions;
  }
}
class VariantControl extends BaseComponent {
  static componentType = "variantControl";
  static rendererType = void 0;
  static componentDocs = {
    summary: "Configures how variants are generated for the enclosing document or section"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numVariants = {
      createPrimitiveOfType: "integer",
      description: "The total number of variants to generate."
    };
    attributes.uniqueVariants = {
      createPrimitiveOfType: "boolean",
      description: "Whether to require generated variants to be uniquely distinguishable."
    };
    attributes.variantsToInclude = {
      createComponentOfType: "textListFromString",
      description: "Restrict the available variants to this list (by name or index)."
    };
    attributes.variantsToExclude = {
      createComponentOfType: "textListFromString",
      description: "Variants to exclude from those generated."
    };
    attributes.variantNames = {
      createComponentOfType: "textListFromString",
      description: "Names assigned to the generated variants."
    };
    attributes.seeds = {
      createComponentOfType: "textListFromString",
      description: "Random seeds used when generating variants."
    };
    return attributes;
  }
}
function mergeContainingNumberCombinations(combinations) {
  if (combinations.length === 0) {
    return { merged: false, combinations: [] };
  }
  let mergedCombinations = [combinations[0]];
  let mergedAtLeastOne = false;
  for (let comb of combinations.slice(1)) {
    let newCombinations = [];
    let merged = false;
    for (let oldComb of mergedCombinations) {
      if (merged) {
        newCombinations.push(oldComb);
        continue;
      }
      let newComb = [];
      merged = true;
      let mergingInto = null;
      for (let k2 = 0; k2 < comb.length; k2++) {
        let v1 = comb[k2], v2 = oldComb[k2];
        if (Number.isNaN(v1)) {
          if (Number.isNaN(v2)) {
            newComb.push(NaN);
          } else {
            if (mergingInto === 2) {
              merged = false;
              break;
            } else {
              newComb.push(NaN);
              mergingInto = 1;
            }
          }
        } else if (Number.isNaN(v2)) {
          if (mergingInto === 1) {
            merged = false;
            break;
          } else {
            newComb.push(NaN);
            mergingInto = 2;
          }
        } else {
          if (v1 === v2) {
            newComb.push(v1);
          } else {
            merged = false;
            break;
          }
        }
      }
      if (merged) {
        newCombinations.push(newComb);
      } else {
        newCombinations.push(oldComb);
      }
    }
    if (merged) {
      mergedAtLeastOne = true;
    } else {
      newCombinations.push(comb);
    }
    mergedCombinations = newCombinations;
  }
  return {
    merged: mergedAtLeastOne,
    combinations: mergedCombinations
  };
}
function checkForExcludedCombination({
  type,
  excludedCombinations,
  values: values2
}) {
  if (type === "math") {
    return excludedCombinations.some(
      (x2) => x2.every((v2, i2) => v2.equals(values2[i2]))
    );
  } else if (type === "number") {
    return excludedCombinations.some(
      (x2) => x2.every(
        (v2, i2) => Number.isNaN(v2) || Math.abs(v2 - values2[i2]) <= 1e-14 * Math.max(Math.abs(v2), Math.abs(values2[i2]))
      )
    );
  } else {
    return excludedCombinations.some(
      (x2) => x2.every((v2, i2) => v2 === values2[i2])
    );
  }
}
function estimateNumberOfNumberCombinationsExcluded({
  excludedCombinations,
  numValues,
  withReplacement,
  numToSelect
}) {
  let numCombinationsExcluded = 0;
  for (let comb of excludedCombinations) {
    let numNans = comb.reduce((a2, c2) => a2 + (Number.isNaN(c2) ? 1 : 0), 0);
    if (numNans > 0) {
      if (withReplacement) {
        numCombinationsExcluded += Math.pow(numValues, numNans);
      } else {
        let n2 = numValues - numToSelect + numNans;
        let nExcl = n2;
        for (let i2 = 1; i2 < numNans; i2++) {
          nExcl *= n2 - i2;
        }
        numCombinationsExcluded += nExcl;
      }
    } else {
      numCombinationsExcluded += 1;
    }
  }
  return numCombinationsExcluded;
}
function estimateNumberOfDuplicateCombinations(combinations, numValues, withReplacement) {
  let nCombs = combinations.length;
  if (nCombs === 0) {
    return 0;
  }
  let duplicateCombinations = [];
  for (let i2 = 0; i2 < nCombs; i2++) {
    let comb1 = combinations[i2];
    for (let j2 = i2 + 1; j2 < nCombs; j2++) {
      let comb2 = combinations[j2];
      let foundDuplicate = true;
      let duplicate = [];
      for (let k2 = 0; k2 < comb1.length; k2++) {
        let v1 = comb1[k2], v2 = comb2[k2];
        if (Number.isNaN(v1)) {
          if (Number.isNaN(v2)) {
            duplicate.push(NaN);
          } else {
            duplicate.push(v2);
          }
        } else if (Number.isNaN(v2)) {
          duplicate.push(v1);
        } else {
          if (v1 === v2) {
            duplicate.push(v1);
          } else {
            foundDuplicate = false;
            break;
          }
        }
      }
      if (foundDuplicate) {
        if (withReplacement) {
          duplicateCombinations.push(duplicate);
        } else {
          let nonNanEntries = duplicate.filter(
            (x2) => !Number.isNaN(x2)
          );
          if ([...new Set(nonNanEntries)].length === nonNanEntries.length) {
            duplicateCombinations.push(duplicate);
          }
        }
      }
    }
  }
  while (true) {
    let result = mergeContainingNumberCombinations(duplicateCombinations);
    if (result.merged) {
      duplicateCombinations = result.combinations;
    } else {
      break;
    }
  }
  let numberDuplicated = 0;
  if (duplicateCombinations.length > 0) {
    for (let comb of duplicateCombinations) {
      let numNans = comb.reduce(
        (a2, c2) => a2 + (Number.isNaN(c2) ? 1 : 0),
        0
      );
      if (numNans > 0) {
        if (withReplacement) {
          numberDuplicated += Math.pow(numValues, numNans);
        } else {
          let n2 = numValues - comb.length + numNans;
          let nDup = n2;
          for (let i2 = 1; i2 < numNans; i2++) {
            nDup *= n2 - i2;
          }
          numberDuplicated += nDup;
        }
      } else {
        numberDuplicated += 1;
      }
    }
  }
  numberDuplicated -= estimateNumberOfDuplicateCombinations(
    duplicateCombinations,
    numValues,
    withReplacement
  );
  return numberDuplicated;
}
const { gcd } = me$3.math;
class SelectFromSequence extends Sequence {
  static componentType = "selectFromSequence";
  static componentDocs = {
    summary: "Randomly selects values from a sequence of numbers, math expressions, or letters to create document variants"
  };
  static takesIndex = true;
  static createsVariants = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numToSelect = {
      createComponentOfType: "integer",
      createStateVariable: "numToSelect",
      defaultValue: 1,
      public: true,
      description: "How many values to select from the sequence."
    };
    attributes.withReplacement = {
      createComponentOfType: "boolean",
      createStateVariable: "withReplacement",
      defaultValue: false,
      public: true,
      description: "Whether the same value can be selected more than once."
    };
    attributes.sort = {
      createComponentOfType: "text",
      createStateVariable: "sort",
      defaultValue: "unsorted",
      public: true,
      toLowerCase: true,
      valueForTrue: "increasing",
      valueForFalse: "unsorted",
      validValues: [
        {
          value: "unsorted",
          description: "Preserve the random selection order."
        },
        {
          value: "increasing",
          description: "Sort the selected values in increasing order."
        },
        {
          value: "decreasing",
          description: "Sort the selected values in decreasing order."
        }
      ],
      description: "Whether and how to sort the selected values."
    };
    attributes.excludeCombinations = {
      createComponentOfType: "_componentListOfListsWithSelectableType",
      description: "Lists of value combinations to exclude from the selection."
    };
    attributes.coprime = {
      createComponentOfType: "boolean",
      createStateVariable: "coprime",
      defaultValue: false,
      public: true,
      description: "Require the selected numbers to be pairwise coprime (numbers only)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.excludedCombinations = {
      returnDependencies: () => ({
        excludeCombinations: {
          dependencyType: "attributeComponent",
          attributeName: "excludeCombinations",
          variableNames: ["lists"]
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        numToSelect: {
          dependencyType: "stateVariable",
          variableName: "numToSelect"
        },
        coprime: {
          dependencyType: "stateVariable",
          variableName: "coprime"
        }
      }),
      definition: function({ dependencyValues }) {
        const diagnostics = [];
        if (dependencyValues.coprime && dependencyValues.type !== "number") {
          diagnostics.push({
            message: "coprime ignored since not selecting numbers",
            type: "warning"
          });
        }
        if (dependencyValues.excludeCombinations !== null) {
          let excludedCombinations = dependencyValues.excludeCombinations.stateValues.lists.map(
            (x2) => x2.slice(0, dependencyValues.numToSelect)
          ).filter(
            (x2) => x2.length === dependencyValues.numToSelect
          );
          if (dependencyValues.type === "number") {
            while (true) {
              let result = mergeContainingNumberCombinations(
                excludedCombinations
              );
              if (result.merged) {
                excludedCombinations = result.combinations;
              } else {
                break;
              }
            }
            if (dependencyValues.coprime) {
              diagnostics.push({
                message: "coprime ignored since excludeCombinations specified",
                type: "warning"
              });
            }
          }
          return {
            setValue: { excludedCombinations },
            sendDiagnostics: diagnostics
          };
        } else {
          return {
            setValue: {
              excludedCombinations: []
            },
            sendDiagnostics: diagnostics
          };
        }
      }
    };
    stateVariableDefinitions.variants = {
      returnDependencies: () => ({
        variants: {
          dependencyType: "variants"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { variants: dependencyValues.variants } };
      }
    };
    stateVariableDefinitions.selectedValues = {
      immutable: true,
      hasEssential: true,
      shadowVariable: true,
      additionalStateVariablesDefined: [
        {
          variableName: "selectedIndices",
          hasEssential: true,
          shadowVariable: true,
          immutable: true
        },
        {
          variableName: "errorMessage",
          hasEssential: true,
          shadowVariable: true,
          immutable: true
        }
      ],
      returnDependencies: ({ sharedParameters }) => ({
        numToSelect: {
          dependencyType: "stateVariable",
          variableName: "numToSelect"
        },
        withReplacement: {
          dependencyType: "stateVariable",
          variableName: "withReplacement"
        },
        length: {
          dependencyType: "stateVariable",
          variableName: "length"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        },
        excludedCombinations: {
          dependencyType: "stateVariable",
          variableName: "excludedCombinations"
        },
        coprime: {
          dependencyType: "stateVariable",
          variableName: "coprime"
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        lowercase: {
          dependencyType: "stateVariable",
          variableName: "lowercase"
        },
        sort: {
          dependencyType: "stateVariable",
          variableName: "sort"
        },
        variants: {
          dependencyType: "stateVariable",
          variableName: "variants"
        },
        variantRng: {
          dependencyType: "value",
          value: sharedParameters.variantRng,
          doNotProxy: true
        }
      }),
      definition: makeSelection$1
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: () => ({
        selectedIndices: {
          dependencyType: "stateVariable",
          variableName: "selectedIndices"
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          indices: dependencyValues.selectedIndices,
          meta: { createdBy: componentIdx }
        };
        return { setValue: { generatedVariantInfo } };
      }
    };
    let originalReturnDependencies = stateVariableDefinitions.readyToExpandWhenResolved.returnDependencies;
    stateVariableDefinitions.readyToExpandWhenResolved.returnDependencies = function() {
      let deps = originalReturnDependencies();
      deps.selectedValues = {
        dependencyType: "stateVariable",
        variableName: "selectedValues"
      };
      return deps;
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let errorMessage = await component.stateValues.errorMessage;
    if (errorMessage) {
      return {
        replacements: [
          {
            type: "serialized",
            componentType: "_error",
            componentIdx: nComponents++,
            stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
            state: { message: errorMessage },
            attributes: {},
            doenetAttributes: {},
            children: []
          }
        ],
        diagnostics,
        nComponents
      };
    }
    let componentType = await component.stateValues.type;
    if (componentType === "letters") {
      componentType = "text";
    }
    let attributesToConvert = {};
    for (let attr of [
      "fixed",
      ...Object.keys(returnNumberDisplayAttributes())
    ]) {
      if (attr in component.attributes) {
        attributesToConvert[attr] = component.attributes[attr];
      }
    }
    let attributesFromComposite = {};
    if (Object.keys(attributesToConvert).length > 0) {
      const res = attributesFromComposite = convertUnresolvedAttributesForComponentType({
        attributes: attributesToConvert,
        componentType,
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      nComponents = res.nComponents;
      attributesFromComposite = res.attributes;
    }
    let replacements = [];
    for (let value of await component.stateValues.selectedValues) {
      replacements.push({
        type: "serialized",
        componentType,
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: attributesFromComposite,
        doenetAttributes: {},
        children: [],
        state: { value, fixed: true }
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static calculateReplacementChanges({ nComponents }) {
    return { replacementChanges: [], nComponents };
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    infoDiagnostics
  }) {
    const info = (message) => pushVariantInfo(infoDiagnostics, message, serializedComponent);
    let numToSelect = 1, withReplacement = false;
    let sequenceType = serializedComponent.attributes.type.primitive.value;
    let numToSelectComponent = serializedComponent.attributes.numToSelect?.component;
    if (numToSelectComponent) {
      if (numToSelectComponent.componentType === "integer" && numToSelectComponent.children?.length === 1 && typeof numToSelectComponent.children[0] === "string") {
        numToSelect = Number(numToSelectComponent.children[0]);
        if (!(Number.isInteger(numToSelect) && numToSelect >= 0)) {
          info(
            `cannot determine unique variants of selectFromSequence as numToSelect isn't a non-negative integer.`
          );
          return { success: false };
        }
      } else {
        info(
          `cannot determine unique variants of selectFromSequence as numToSelect isn't constant number.`
        );
        return { success: false };
      }
    }
    let coprimeCombinationsComponent = serializedComponent.attributes.coprime?.component;
    if (coprimeCombinationsComponent) {
      if (!(coprimeCombinationsComponent.componentType === "boolean" && coprimeCombinationsComponent.children?.length === 1 && typeof coprimeCombinationsComponent.children[0] === "string" && coprimeCombinationsComponent.children[0].trim().toLowerCase() === "false")) {
        info(
          `cannot determine unique variants of selectFromSequence as cannot determine coprime is always false.`
        );
        return { success: false };
      }
    }
    let withReplacementComponent = serializedComponent.attributes.withReplacement?.component;
    if (withReplacementComponent) {
      if (withReplacementComponent.componentType === "boolean") {
        if (withReplacementComponent.children?.length === 1 && typeof withReplacementComponent.children[0] === "string") {
          withReplacement = withReplacementComponent.children[0].toLowerCase() === "true";
        } else if ((withReplacementComponent.children === void 0 || withReplacementComponent.children.length === 0) && typeof withReplacementComponent.state?.value === "boolean") {
          withReplacement = withReplacementComponent.state.value;
        } else {
          info(
            `cannot determine unique variants of selectFromSequence as withReplacement isn't constant boolean.`
          );
          return { success: false };
        }
      } else {
        info(
          `cannot determine unique variants of selectFromSequence as withReplacement isn't constant boolean.`
        );
        return { success: false };
      }
    }
    let sequencePars = {
      from: null,
      to: null,
      step: null,
      length: null
    };
    let fromComponent = serializedComponent.attributes.from?.component;
    if (fromComponent) {
      let fromComponent2 = fromComponent.children[0];
      if (fromComponent2.children?.length === 1 && typeof fromComponent2.children[0] === "string") {
        let from;
        if (sequenceType === "number") {
          from = Number(fromComponent2.children[0]);
          if (!Number.isFinite(from)) {
            info(
              `cannot determine unique variants of selectFromSequence of number type as from isn't a number.`
            );
            return { success: false };
          }
        } else if (sequenceType === "letters") {
          from = lettersToNumber(fromComponent2.children[0]);
          if (!Number.isFinite(from)) {
            info(
              `cannot determine unique variants of selectFromSequence of letters type as from isn't a combination of letters.`
            );
            return { success: false };
          }
        } else {
          let fromText = textToMathFactory({
            functionSymbols: ["f", "g"]
          });
          try {
            from = fromText(fromComponent2.children[0]);
          } catch (e3) {
            info(
              `cannot determine unique variants of selectFromSequence of math type as from isn't a valid math expression.`
            );
            return { success: false };
          }
        }
        sequencePars.from = from;
      } else {
        info(
          `cannot determine unique variants of selectFromSequence as from isn't a constant.`
        );
        return { success: false };
      }
    }
    let toComponent = serializedComponent.attributes.to?.component;
    if (toComponent) {
      let toComponent2 = toComponent.children[0];
      if (toComponent2.children?.length === 1 && typeof toComponent2.children[0] === "string") {
        let to2;
        if (sequenceType === "number") {
          to2 = Number(toComponent2.children[0]);
          if (!Number.isFinite(to2)) {
            info(
              `cannot determine unique variants of selectFromSequence of number type as to isn't a number.`
            );
            return { success: false };
          }
        } else if (sequenceType === "letters") {
          to2 = lettersToNumber(toComponent2.children[0]);
          if (!Number.isFinite(to2)) {
            info(
              `cannot determine unique variants of selectFromSequence of letters type as to isn't a combination of letters.`
            );
            return { success: false };
          }
        } else {
          let fromText = textToMathFactory({
            functionSymbols: ["f", "g"]
          });
          try {
            to2 = fromText(toComponent2.children[0]);
          } catch (e3) {
            info(
              `cannot determine unique variants of selectFromSequence of math type as to isn't a valid math expression.`
            );
            return { success: false };
          }
        }
        sequencePars.to = to2;
      } else {
        info(
          `cannot determine unique variants of selectFromSequence as to isn't a constant.`
        );
        return { success: false };
      }
    }
    let stepComponent = serializedComponent.attributes.step?.component;
    if (stepComponent) {
      if (stepComponent.children?.length === 1 && typeof stepComponent.children[0] === "string") {
        let step;
        if (sequenceType === "number") {
          step = Number(stepComponent.children[0]);
          if (!Number.isFinite(step)) {
            info(
              `cannot determine unique variants of selectFromSequence of number type as step isn't a number.`
            );
            return { success: false };
          }
        } else if (sequenceType === "letters") {
          step = Number(stepComponent.children[0]);
          if (!Number.isInteger(step)) {
            info(
              `cannot determine unique variants of selectFromSequence of letters type as step isn't an integer.`
            );
            return { success: false };
          }
        } else {
          let fromText = textToMathFactory({
            functionSymbols: ["f", "g"]
          });
          try {
            step = fromText(stepComponent.children[0]);
          } catch (e3) {
            info(
              `cannot determine unique variants of selectFromSequence of math type as step isn't a valid math expression.`
            );
            return { success: false };
          }
        }
        sequencePars.step = step;
      } else {
        info(
          `cannot determine unique variants of selectFromSequence as step isn't a constant.`
        );
        return { success: false };
      }
    }
    let lengthComponent = serializedComponent.attributes.length?.component;
    if (lengthComponent) {
      if (lengthComponent.children?.length === 1 && typeof lengthComponent.children[0] === "string") {
        let length = Number(lengthComponent.children[0]);
        if (!Number.isInteger(length)) {
          info(
            `cannot determine unique variants of selectFromSequence as length isn't an integer.`
          );
          return { success: false };
        }
        sequencePars.length = length;
      } else {
        info(
          `cannot determine unique variants of selectFromSequence as length isn't a constant.`
        );
        return { success: false };
      }
    }
    if (serializedComponent.attributes.excludeCombinations) {
      info(
        "have not implemented unique variants of a selectFromSequence with excludeCombinations"
      );
      return { success: false };
    }
    let excludes = [];
    let excludeComponent = serializedComponent.attributes.exclude?.component;
    if (excludeComponent) {
      if (sequenceType === "math") {
        info(
          "have not implemented unique variants of a selectFromSequence of type math with exclude"
        );
        return { success: false };
      }
      if (!excludeComponent.children.every(
        (x2) => x2.children?.length === 1 && typeof x2.children[0] === "string"
      )) {
        info(
          "have not implemented unique variants of a selectFromSequence with non-constant exclude"
        );
        return { success: false };
      }
      if (sequenceType === "letters") {
        excludes = excludeComponent.children.map(
          (x2) => lettersToNumber(x2.children[0])
        );
      } else {
        excludes = excludeComponent.children.map(
          (x2) => Number(x2.children[0])
        );
      }
      if (!excludes.every(Number.isFinite)) {
        info(
          "have not implemented unique variants of a selectFromSequence with non-constant exclude"
        );
        return { success: false };
      }
    }
    let sortResult = extractConstantSortAttribute(
      serializedComponent,
      "selectFromSequence",
      numToSelect,
      infoDiagnostics
    );
    if (!sortResult.success) {
      return { success: false };
    }
    sequencePars = calculateSequenceParameters(sequencePars);
    let nOptions = sequencePars.length;
    let excludeIndices = [];
    if (excludes.length > 0) {
      if (sequenceType !== "math") {
        excludes.sort((a2, b2) => a2 - b2);
        excludes = excludes.filter((x2, ind, a2) => x2 != a2[ind - 1]);
        for (let item of excludes) {
          if (item < sequencePars.from) {
            continue;
          }
          let ind = (item - sequencePars.from) / sequencePars.step;
          if (ind > sequencePars.length - 1 + 1e-10) {
            break;
          }
          if (Math.abs(ind - Math.round(ind)) < 1e-10) {
            nOptions--;
            excludeIndices.push(ind);
          }
        }
      }
    }
    if (nOptions <= 0) {
      return { success: false };
    }
    let uniqueVariantData = {
      excludeIndices,
      nOptions,
      numToSelect,
      withReplacement
    };
    serializedComponent.variants.uniqueVariantData = uniqueVariantData;
    let numVariants;
    if (withReplacement || numToSelect === 1) {
      numVariants = Math.pow(nOptions, numToSelect);
    } else {
      numVariants = nOptions;
      for (let n2 = nOptions - 1; n2 > nOptions - numToSelect; n2--) {
        numVariants *= n2;
      }
    }
    if (!(numVariants > 0)) {
      return { success: false };
    }
    serializedComponent.variants.numVariants = numVariants;
    return {
      success: true,
      numVariants
    };
  }
  static getUniqueVariant({ serializedComponent, variantIndex }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let uniqueVariantData = serializedComponent.variants.uniqueVariantData;
    let excludeIndices = uniqueVariantData.excludeIndices;
    let nOptions = uniqueVariantData.nOptions;
    let numToSelect = uniqueVariantData.numToSelect;
    let withReplacement = uniqueVariantData.withReplacement;
    let getSingleIndex = function(num) {
      let ind = num;
      for (let excludeInd of excludeIndices) {
        if (ind >= excludeInd) {
          ind++;
        }
      }
      return ind;
    };
    if (numToSelect === 1) {
      return {
        success: true,
        desiredVariant: {
          indices: [getSingleIndex(variantIndex - 1) + 1]
        }
      };
    }
    let numbers = enumerateSelectionCombinations({
      numberOfIndices: numToSelect,
      numberOfOptions: nOptions,
      maxNumber: variantIndex,
      withReplacement
    })[variantIndex - 1];
    let indices = numbers.map(getSingleIndex).map((x2) => x2 + 1);
    return { success: true, desiredVariant: { indices } };
  }
}
function makeSelection$1({ dependencyValues }) {
  if (dependencyValues.numToSelect < 1) {
    return {
      setEssentialValue: {
        errorMessage: "",
        selectedValues: [],
        selectedIndices: []
      },
      setValue: {
        errorMessage: "",
        selectedValues: [],
        selectedIndices: []
      }
    };
  }
  let numUniqueRequired = 1;
  if (!dependencyValues.withReplacement) {
    numUniqueRequired = dependencyValues.numToSelect;
  }
  if (numUniqueRequired > dependencyValues.length) {
    let errorMessage = "Cannot select " + numUniqueRequired + " values from a sequence of length " + dependencyValues.length + ".";
    return {
      setEssentialValue: {
        errorMessage,
        selectedValues: null,
        selectedIndices: null
      },
      setValue: {
        errorMessage,
        selectedValues: null,
        selectedIndices: null
      }
    };
  }
  if (dependencyValues.variants && dependencyValues.variants.desiredVariant !== void 0) {
    let desiredIndices = dependencyValues.variants.desiredVariant.indices;
    if (desiredIndices !== void 0) {
      if (desiredIndices.length !== dependencyValues.numToSelect) {
        let errorMessage = "Number of indices specified for select must match number to select";
        return {
          setEssentialValue: {
            errorMessage,
            selectedValues: null,
            selectedIndices: null
          },
          setValue: {
            errorMessage,
            selectedValues: null,
            selectedIndices: null
          }
        };
      }
      desiredIndices = desiredIndices.map(Number);
      if (!desiredIndices.every(Number.isInteger)) {
        let errorMessage = "All indices specified for select must be integers";
        return {
          setEssentialValue: {
            errorMessage,
            selectedValues: null,
            selectedIndices: null
          },
          setValue: {
            errorMessage,
            selectedValues: null,
            selectedIndices: null
          }
        };
      }
      let n2 = dependencyValues.length;
      desiredIndices = desiredIndices.map(
        (x2) => ((x2 - 1) % n2 + n2) % n2 + 1
      );
      let selectedValues2 = [];
      for (let indexFrom1 of desiredIndices) {
        let componentValue = returnSequenceValueForIndex({
          index: indexFrom1 - 1,
          from: dependencyValues.from,
          step: dependencyValues.step,
          length: dependencyValues.length,
          exclude: dependencyValues.exclude,
          type: dependencyValues.type,
          lowercase: dependencyValues.lowercase
        });
        if (componentValue === null) {
          let errorMessage = "Specified index of selectfromsequence that was excluded";
          return {
            setEssentialValue: {
              errorMessage,
              selectedValues: null,
              selectedIndices: null
            },
            setValue: {
              errorMessage,
              selectedValues: null,
              selectedIndices: null
            }
          };
        }
        selectedValues2.push(componentValue);
      }
      if (checkForExcludedCombination({
        type: dependencyValues.type,
        excludedCombinations: dependencyValues.excludedCombinations,
        values: selectedValues2
      })) {
        let errorMessage = "Specified indices of selectfromsequence that was an excluded combination";
        return {
          setEssentialValue: {
            errorMessage,
            selectedValues: null,
            selectedIndices: null
          },
          setValue: {
            errorMessage,
            selectedValues: null,
            selectedIndices: null
          }
        };
      }
      return {
        setEssentialValue: {
          errorMessage: "",
          selectedValues: selectedValues2,
          selectedIndices: desiredIndices
        },
        setValue: {
          errorMessage: "",
          selectedValues: selectedValues2,
          selectedIndices: desiredIndices
        }
      };
    }
  }
  let numCombinationsExcluded = dependencyValues.excludedCombinations.length;
  let coprime = dependencyValues.coprime;
  if (dependencyValues.type === "number") {
    let errorMessage = null;
    numCombinationsExcluded = estimateNumberOfNumberCombinationsExcluded({
      excludedCombinations: dependencyValues.excludedCombinations,
      numValues: dependencyValues.length - dependencyValues.exclude.length,
      withReplacement: dependencyValues.withReplacement,
      numToSelect: dependencyValues.numToSelect
    });
    if (coprime) {
      const lastNumber = dependencyValues.from + (dependencyValues.length - 1) * dependencyValues.step;
      if (numCombinationsExcluded > 0) {
        coprime = false;
      } else if (!Number.isInteger(dependencyValues.from) || !Number.isInteger(dependencyValues.step) || dependencyValues.from <= 0 || lastNumber <= 0) {
        errorMessage = "Cannot select coprime combinations as not selecting positive integers.";
      } else if (gcd(dependencyValues.from, dependencyValues.step) !== 1) {
        errorMessage = `Cannot select coprime numbers. All possible values share a common factor. (Specified values of "from" or "to" must be coprime with "step".)`;
      } else if (dependencyValues.numToSelect === 1) {
        coprime = false;
      } else if (dependencyValues.length - dependencyValues.exclude.length <= 1) {
        if (dependencyValues.from > 1 || dependencyValues.exclude.includes(1)) {
          if (dependencyValues.length <= 1) {
            errorMessage = "Cannot select coprime combinations from a single number that is not 1.";
          } else {
            const possibleValues = returnSequenceValues(dependencyValues);
            if (possibleValues.length <= 1) {
              errorMessage = "Cannot select coprime combinations from a single number that is not 1.";
            }
          }
        }
      }
    }
    if (errorMessage) {
      return {
        setEssentialValue: {
          errorMessage,
          selectedValues: null,
          selectedIndices: null
        },
        setValue: {
          errorMessage,
          selectedValues: null,
          selectedIndices: null
        }
      };
    }
  } else {
    coprime = false;
  }
  let selectedValues, selectedIndices;
  if (numCombinationsExcluded === 0 && !coprime) {
    let selectedObj = selectValuesAndIndices({
      stateValues: dependencyValues,
      numUniqueRequired,
      numToSelect: dependencyValues.numToSelect,
      withReplacement: dependencyValues.withReplacement,
      rng: dependencyValues.variantRng
    });
    if (selectedObj.errorMessage) {
      return {
        setEssentialValue: {
          errorMessage: selectedObj.errorMessage,
          selectedValues: null,
          selectedIndices: null
        },
        setValue: {
          errorMessage: selectedObj.errorMessage,
          selectedValues: null,
          selectedIndices: null
        }
      };
    }
    selectedValues = selectedObj.selectedValues;
    selectedIndices = selectedObj.selectedIndices;
  } else {
    let numPossibilitiesLowerBound = dependencyValues.length - dependencyValues.exclude.length;
    if (dependencyValues.withReplacement) {
      numPossibilitiesLowerBound = Math.pow(
        numPossibilitiesLowerBound,
        dependencyValues.numToSelect
      );
    } else {
      let n2 = numPossibilitiesLowerBound;
      for (let i2 = 1; i2 < dependencyValues.numToSelect; i2++) {
        numPossibilitiesLowerBound *= n2 - i2;
      }
    }
    if (numCombinationsExcluded > 0.7 * numPossibilitiesLowerBound) {
      let numPossibilities = 0;
      for (let index2 = 0; index2 < dependencyValues.length; index2++) {
        if (returnSequenceValueForIndex({
          index: index2,
          from: dependencyValues.from,
          step: dependencyValues.step,
          length: dependencyValues.length,
          exclude: dependencyValues.exclude,
          type: dependencyValues.type
        }) !== null) {
          numPossibilities++;
        }
      }
      if (dependencyValues.withReplacement) {
        numPossibilities = Math.pow(
          numPossibilities,
          dependencyValues.numToSelect
        );
      } else {
        let n2 = numPossibilities;
        for (let i2 = 1; i2 < dependencyValues.numToSelect; i2++) {
          numPossibilities *= n2 - i2;
        }
      }
      if (numCombinationsExcluded > 0.7 * numPossibilities) {
        if (dependencyValues.type === "number" && dependencyValues.excludedCombinations.some(
          (x2) => x2.some(Number.isNaN)
        )) {
          let numDuplicated = estimateNumberOfDuplicateCombinations(
            dependencyValues.excludedCombinations,
            dependencyValues.length - dependencyValues.exclude.length,
            dependencyValues.withReplacement
          );
          numCombinationsExcluded -= numDuplicated;
          if (numCombinationsExcluded > 0.7 * numPossibilities) {
            let errorMessage = "Excluded over 70% of combinations in selectFromSequence";
            return {
              setEssentialValue: {
                errorMessage,
                selectedValues: null,
                selectedIndices: null
              },
              setValue: {
                errorMessage,
                selectedValues: null,
                selectedIndices: null
              }
            };
          }
        } else {
          let errorMessage = "Excluded over 70% of combinations in selectFromSequence";
          return {
            setEssentialValue: {
              errorMessage,
              selectedValues: null,
              selectedIndices: null
            },
            setValue: {
              errorMessage,
              selectedValues: null,
              selectedIndices: null
            }
          };
        }
      }
    }
    let foundValidCombination = false;
    for (let sampnum = 0; sampnum < 200; sampnum++) {
      let selectedObj = selectValuesAndIndices({
        stateValues: dependencyValues,
        numUniqueRequired,
        numToSelect: dependencyValues.numToSelect,
        withReplacement: dependencyValues.withReplacement,
        rng: dependencyValues.variantRng
      });
      if (selectedObj.errorMessage) {
        return {
          setEssentialValue: {
            errorMessage: selectedObj.errorMessage,
            selectedValues: null,
            selectedIndices: null
          },
          setValue: {
            errorMessage: selectedObj.errorMessage,
            selectedValues: null,
            selectedIndices: null
          }
        };
      }
      selectedValues = selectedObj.selectedValues;
      selectedIndices = selectedObj.selectedIndices;
      if (checkForExcludedCombination({
        type: dependencyValues.type,
        excludedCombinations: dependencyValues.excludedCombinations,
        values: selectedValues
      })) {
        continue;
      }
      if (coprime && gcd(...selectedValues) !== 1) {
        continue;
      }
      foundValidCombination = true;
      break;
    }
    if (!foundValidCombination) {
      let errorMessage;
      if (coprime) {
        errorMessage = "Could not select coprime numbers. All possible values share a common factor.";
      } else {
        errorMessage = "By extremely unlikely fluke, couldn't select combination of random values";
      }
      return {
        setEssentialValue: {
          errorMessage,
          selectedValues: null,
          selectedIndices: null
        },
        setValue: {
          errorMessage,
          selectedValues: null,
          selectedIndices: null
        }
      };
    }
  }
  if (dependencyValues.sort !== "unsorted") {
    let combinedList = [];
    for (let [ind, val] of selectedValues.entries()) {
      combinedList.push({ value: val, index: selectedIndices[ind] });
    }
    if (dependencyValues.type === "number") {
      if (dependencyValues.sort === "increasing") {
        combinedList.sort((a2, b2) => a2.value - b2.value);
      } else if (dependencyValues.sort === "decreasing") {
        combinedList.sort((a2, b2) => b2.value - a2.value);
      }
    } else if (dependencyValues.type === "letters") {
      if (dependencyValues.sort === "increasing") {
        combinedList.sort(
          (a2, b2) => lettersToNumber(a2.value) - lettersToNumber(b2.value)
        );
      } else if (dependencyValues.sort === "decreasing") {
        combinedList.sort(
          (a2, b2) => lettersToNumber(b2.value) - lettersToNumber(a2.value)
        );
      }
    }
    selectedValues = combinedList.map((x2) => x2.value);
    selectedIndices = combinedList.map((x2) => x2.index);
  }
  return {
    setEssentialValue: {
      errorMessage: "",
      selectedValues,
      selectedIndices
    },
    setValue: { errorMessage: "", selectedValues, selectedIndices }
  };
}
function selectValuesAndIndices({
  stateValues,
  numUniqueRequired = 1,
  numToSelect = 1,
  withReplacement = false,
  rng
}) {
  let selectedValues = [];
  let selectedIndices = [];
  if (stateValues.exclude.length + numUniqueRequired < 0.5 * stateValues.length) {
    for (let ind = 0; ind < numToSelect; ind++) {
      let foundValid = false;
      let componentValue;
      let selectedIndex;
      for (let sampnum = 0; sampnum < 100; sampnum++) {
        let rand = rng();
        selectedIndex = Math.floor(rand * stateValues.length) + 1;
        if (!withReplacement && selectedIndices.includes(selectedIndex)) {
          continue;
        }
        componentValue = returnSequenceValueForIndex({
          index: selectedIndex - 1,
          from: stateValues.from,
          step: stateValues.step,
          exclude: stateValues.exclude,
          type: stateValues.type,
          lowercase: stateValues.lowercase
        });
        if (componentValue === null) {
          continue;
        }
        foundValid = true;
        break;
      }
      if (!foundValid) {
        let errorMessage = "By extremely unlikely fluke, couldn't select random value";
        return {
          errorMessage,
          selectedValues: null,
          selectedIndices: null
        };
      }
      selectedValues.push(componentValue);
      selectedIndices.push(selectedIndex);
    }
    return { selectedValues, selectedIndices };
  }
  let possibleValuesAndIndices = returnSequenceValues(stateValues, true);
  let numPossibleValues = possibleValuesAndIndices.length;
  if (numUniqueRequired > numPossibleValues) {
    let errorMessage = "Cannot select " + numUniqueRequired + " unique values from sequence of length " + numPossibleValues;
    return {
      errorMessage,
      selectedValues: null,
      selectedIndices: null
    };
  }
  if (numUniqueRequired === 1) {
    for (let ind = 0; ind < numToSelect; ind++) {
      let rand = rng();
      let ind2 = Math.floor(rand * numPossibleValues);
      selectedIndices.push(
        possibleValuesAndIndices[ind2].originalIndex + 1
      );
      selectedValues.push(possibleValuesAndIndices[ind2].value);
    }
    return { selectedValues, selectedIndices };
  }
  for (let i2 = possibleValuesAndIndices.length - 1; i2 > 0; i2--) {
    const rand = rng();
    const j2 = Math.floor(rand * (i2 + 1));
    [possibleValuesAndIndices[i2], possibleValuesAndIndices[j2]] = [
      possibleValuesAndIndices[j2],
      possibleValuesAndIndices[i2]
    ];
  }
  let selectedValuesAndIndices = possibleValuesAndIndices.slice(
    0,
    numToSelect
  );
  selectedValues = selectedValuesAndIndices.map((x2) => x2.value);
  selectedIndices = selectedValuesAndIndices.map((x2) => x2.originalIndex + 1);
  return { selectedValues, selectedIndices };
}
class Select extends CompositeComponent {
  static componentType = "select";
  static componentDocs = {
    summary: "Randomly selects components from a list of children to create document variants"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static createsVariants = true;
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numToSelect = {
      description: "How many child components to select.",
      createComponentOfType: "integer",
      createStateVariable: "numToSelect",
      defaultValue: 1,
      public: true
    };
    attributes.withReplacement = {
      description: "Whether the same child can be selected more than once.",
      createComponentOfType: "boolean",
      createStateVariable: "withReplacement",
      defaultValue: false,
      public: true
    };
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "Component type to select from."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnChildGroups() {
    return [
      {
        group: "options",
        componentTypes: ["option"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.variants = {
      returnDependencies: () => ({
        variants: {
          dependencyType: "variants"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { variants: dependencyValues.variants } };
      }
    };
    stateVariableDefinitions.currentVariantName = {
      returnDependencies: ({ sharedParameters }) => ({
        variantName: {
          dependencyType: "value",
          value: sharedParameters.variantName
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { currentVariantName: dependencyValues.variantName }
      })
    };
    stateVariableDefinitions.allVariantNames = {
      returnDependencies: ({ sharedParameters }) => ({
        allVariantNames: {
          dependencyType: "value",
          value: sharedParameters.allVariantNames
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { allVariantNames: dependencyValues.allVariantNames }
      })
    };
    stateVariableDefinitions.nOptions = {
      additionalStateVariablesDefined: ["optionChildren"],
      returnDependencies: () => ({
        optionChildren: {
          dependencyType: "child",
          childGroups: ["options"],
          variableNames: ["selectForVariants", "selectWeight"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            optionChildren: dependencyValues.optionChildren,
            nOptions: dependencyValues.optionChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.availableVariants = {
      additionalStateVariablesDefined: ["errorMessageVariants"],
      returnDependencies: () => ({
        optionChildren: {
          dependencyType: "stateVariable",
          variableName: "optionChildren"
        },
        numToSelect: {
          dependencyType: "stateVariable",
          variableName: "numToSelect"
        },
        allVariantNames: {
          dependencyType: "stateVariable",
          variableName: "allVariantNames"
        }
      }),
      definition: function({ dependencyValues }) {
        let availableVariants = {};
        for (let [
          ind,
          optionChild
        ] of dependencyValues.optionChildren.entries()) {
          for (let variantName of optionChild.stateValues.selectForVariants) {
            let variantLower = variantName.toLowerCase();
            if (availableVariants[variantLower] === void 0) {
              availableVariants[variantLower] = [];
            }
            availableVariants[variantLower].push(ind + 1);
          }
        }
        for (let variantName in availableVariants) {
          if (availableVariants[variantName].length !== dependencyValues.numToSelect) {
            let errorMessageVariants = "Invalid variant name for select.  Variant name " + variantName + " appears in " + availableVariants[variantName].length + " options but number to select is " + dependencyValues.numToSelect + ".";
            return {
              setValue: {
                errorMessageVariants,
                availableVariants: []
              }
            };
          }
        }
        if (Object.keys(availableVariants).length > 0) {
          for (let variantName of dependencyValues.allVariantNames) {
            if (!(variantName in availableVariants)) {
              let errorMessageVariants = "Some variants are specified for select but no options are specified for possible variant name: " + variantName + ".";
              return {
                setValue: {
                  errorMessageVariants,
                  availableVariants: []
                }
              };
            }
          }
          for (let variantName in availableVariants) {
            if (!dependencyValues.allVariantNames.includes(
              variantName
            )) {
              let errorMessageVariants = "Variant name " + variantName + " that is specified for select is not a possible variant name.";
              return {
                setValue: {
                  errorMessageVariants,
                  availableVariants: []
                }
              };
            }
          }
        }
        return {
          setValue: { errorMessageVariants: "", availableVariants }
        };
      }
    };
    stateVariableDefinitions.selectedIndices = {
      immutable: true,
      hasEssential: true,
      shadowVariable: true,
      additionalStateVariablesDefined: [
        {
          variableName: "errorMessage",
          hasEssential: true,
          shadowVariable: true,
          immutable: true
        }
      ],
      returnDependencies: ({ sharedParameters }) => ({
        numToSelect: {
          dependencyType: "stateVariable",
          variableName: "numToSelect"
        },
        withReplacement: {
          dependencyType: "stateVariable",
          variableName: "withReplacement"
        },
        optionChildren: {
          dependencyType: "stateVariable",
          variableName: "optionChildren"
        },
        nOptions: {
          dependencyType: "stateVariable",
          variableName: "nOptions"
        },
        currentVariantName: {
          dependencyType: "stateVariable",
          variableName: "currentVariantName"
        },
        variants: {
          dependencyType: "stateVariable",
          variableName: "variants"
        },
        availableVariants: {
          dependencyType: "stateVariable",
          variableName: "availableVariants"
        },
        errorMessageVariants: {
          dependencyType: "stateVariable",
          variableName: "errorMessageVariants"
        },
        variantRng: {
          dependencyType: "value",
          value: sharedParameters.variantRng,
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.errorMessageVariants) {
          let errorMessage = dependencyValues.errorMessageVariants;
          return {
            setEssentialValue: {
              errorMessage,
              selectedIndices: []
            },
            setValue: { errorMessage, selectedIndices: [] }
          };
        }
        if (!(dependencyValues.numToSelect >= 1) || dependencyValues.nOptions === 0) {
          return {
            setEssentialValue: {
              errorMessage: "",
              selectedIndices: []
            },
            setValue: { errorMessage: "", selectedIndices: [] }
          };
        }
        if (dependencyValues.variants && dependencyValues.variants.desiredVariant !== void 0) {
          let desiredIndices = dependencyValues.variants.desiredVariant.indices;
          if (desiredIndices !== void 0) {
            if (desiredIndices.length !== dependencyValues.numToSelect) {
              throw Error(
                "Number of indices specified for select must match number to select"
              );
            }
            desiredIndices = desiredIndices.map(Number);
            if (!desiredIndices.every(Number.isInteger)) {
              throw Error(
                "All indices specified for select must be integers"
              );
            }
            let n2 = dependencyValues.nOptions;
            desiredIndices = desiredIndices.map(
              (x2) => ((x2 - 1) % n2 + n2) % n2 + 1
            );
            return {
              setEssentialValue: {
                errorMessage: "",
                selectedIndices: desiredIndices
              },
              setValue: {
                errorMessage: "",
                selectedIndices: desiredIndices
              }
            };
          }
        }
        let variantOptions = dependencyValues.availableVariants[dependencyValues.currentVariantName];
        if (variantOptions !== void 0) {
          if (dependencyValues.numToSelect > 1) {
            variantOptions = [...variantOptions];
            for (let i2 = dependencyValues.numToSelect - 1; i2 > 0; i2--) {
              const rand = dependencyValues.variantRng();
              const j2 = Math.floor(rand * (i2 + 1));
              [variantOptions[i2], variantOptions[j2]] = [
                variantOptions[j2],
                variantOptions[i2]
              ];
            }
          }
          return {
            setEssentialValue: {
              errorMessage: "",
              selectedIndices: variantOptions
            },
            setValue: {
              errorMessage: "",
              selectedIndices: variantOptions
            }
          };
        }
        let selectedIndices = [];
        let numUniqueRequired = 1;
        if (!dependencyValues.withReplacement) {
          numUniqueRequired = dependencyValues.numToSelect;
        }
        if (numUniqueRequired > dependencyValues.nOptions) {
          let errorMessage = "Cannot select " + numUniqueRequired + " components from only " + dependencyValues.nOptions + ".";
          return {
            setEssentialValue: {
              errorMessage,
              selectedIndices: []
            },
            setValue: { errorMessage, selectedIndices: [] }
          };
        }
        let selectWeightByChild = dependencyValues.optionChildren.map(
          (x2) => x2.stateValues.selectWeight
        );
        let totalWeight = selectWeightByChild.reduce((a2, c2) => a2 + c2);
        selectWeightByChild = selectWeightByChild.map(
          (x2) => x2 / totalWeight
        );
        let cumulativeWeights = selectWeightByChild.reduce(
          (a2, x2, i2) => [...a2, x2 + (a2[i2 - 1] || 0)],
          []
        );
        let indsRemaining = [
          ...Array(cumulativeWeights.length).keys()
        ].map((x2) => x2 + 1);
        for (let ind = 0; ind < dependencyValues.numToSelect; ind++) {
          let rand = dependencyValues.variantRng();
          let start = -1, end = cumulativeWeights.length - 1;
          while (start < end - 1) {
            let mid = Math.floor((start + end) / 2);
            if (cumulativeWeights[mid] > rand) {
              end = mid;
            } else {
              start = mid;
            }
          }
          let selectedInd = indsRemaining[end];
          selectedIndices.push(selectedInd);
          if (!dependencyValues.withReplacement && ind < dependencyValues.numToSelect - 1) {
            selectWeightByChild.splice(end, 1);
            indsRemaining.splice(end, 1);
            totalWeight = selectWeightByChild.reduce(
              (a2, c2) => a2 + c2
            );
            selectWeightByChild = selectWeightByChild.map(
              (x2) => x2 / totalWeight
            );
            cumulativeWeights = selectWeightByChild.reduce(
              (a2, x2, i2) => [...a2, x2 + (a2[i2 - 1] || 0)],
              []
            );
          }
        }
        return {
          setEssentialValue: { errorMessage: "", selectedIndices },
          setValue: { errorMessage: "", selectedIndices }
        };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      providePreviousValuesInDefinition: true,
      returnDependencies: ({ componentInfoObjects }) => ({
        selectedIndices: {
          dependencyType: "stateVariable",
          variableName: "selectedIndices"
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx, previousValues }) {
        let generatedVariantInfo = {
          indices: dependencyValues.selectedIndices,
          meta: { createdBy: componentIdx }
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        for (let [ind, subvar] of subvariants.entries()) {
          if (!subvar.subvariants && previousValues.generatedVariantInfo) {
            let previousSubvariants = previousValues.generatedVariantInfo.subvariants;
            if (previousSubvariants[ind].subvariants) {
              subvariants[ind] = Object.assign(
                {},
                subvariants[ind]
              );
              subvariants[ind].subvariants = previousSubvariants[ind].subvariants;
            }
          }
        }
        return { setValue: { generatedVariantInfo } };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        selectedIndices: {
          dependencyType: "stateVariable",
          variableName: "selectedIndices"
        }
      }),
      definition() {
        return {
          setValue: { readyToExpandWhenResolved: true }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let errorMessage = await component.stateValues.errorMessage;
    if (errorMessage) {
      return {
        replacements: [
          {
            type: "serialized",
            componentType: "_error",
            componentIdx: nComponents++,
            state: { message: errorMessage },
            attributes: {},
            doenetAttributes: {},
            children: []
          }
        ],
        diagnostics,
        nComponents
      };
    }
    let replacements = [];
    let optionChildren = await component.stateValues.optionChildren;
    for (let selectedIndex of await component.stateValues.selectedIndices) {
      let selectedChildComponentIdx = optionChildren[selectedIndex - 1].componentIdx;
      let selectedChild = components[selectedChildComponentIdx];
      let serializedGrandchildren = deepClone(
        await selectedChild.stateValues.serializedChildren
      );
      const idxResult = createNewComponentIndices(
        serializedGrandchildren,
        nComponents,
        stateIdInfo
      );
      serializedGrandchildren = idxResult.components;
      nComponents = idxResult.nComponents;
      let serializedChild = {
        type: "serialized",
        componentType: "group",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: {},
        doenetAttributes: Object.assign(
          {},
          selectedChild.doenetAttributes
        ),
        children: serializedGrandchildren,
        originalIdx: selectedChildComponentIdx
      };
      replacements.push(serializedChild);
    }
    let descendantVariantComponents = gatherVariantComponents({
      serializedComponents: replacements,
      componentInfoObjects
    });
    if (component.variants && component.variants.desiredVariant !== void 0) {
      let desiredVariant = component.variants.desiredVariant;
      if (desiredVariant !== void 0 && desiredVariant.subvariants !== void 0) {
        for (let ind in desiredVariant.subvariants) {
          let subvariant = desiredVariant.subvariants[ind];
          let variantComponent = descendantVariantComponents[ind];
          if (variantComponent === void 0) {
            break;
          }
          variantComponent.variants.desiredVariant = subvariant;
        }
      }
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return { replacements, diagnostics, nComponents };
  }
  static calculateReplacementChanges({ nComponents }) {
    return { replacementChanges: [], nComponents };
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants !== void 0) {
      return { success: true, numVariants };
    }
    let numToSelect = 1, withReplacement = false;
    let numToSelectComponent = serializedComponent.attributes.numToSelect?.component;
    if (numToSelectComponent) {
      if (numToSelectComponent.componentType === "integer" && numToSelectComponent.children?.length === 1 && typeof numToSelectComponent.children[0] === "string") {
        numToSelect = Number(numToSelectComponent.children[0]);
        if (!(Number.isInteger(numToSelect) && numToSelect >= 0)) {
          pushVariantInfo(
            infoDiagnostics,
            `cannot determine unique variants of select as numToSelect isn't a non-negative integer.`,
            serializedComponent
          );
          return { success: false };
        }
      } else {
        pushVariantInfo(
          infoDiagnostics,
          `cannot determine unique variants of select as numToSelect isn't constant number.`,
          serializedComponent
        );
        return { success: false };
      }
    }
    let withReplacementComponent = serializedComponent.attributes.withReplacement?.component;
    if (withReplacementComponent) {
      if (withReplacementComponent.componentType === "boolean") {
        if (withReplacementComponent.children?.length === 1 && typeof withReplacementComponent.children[0] === "string") {
          withReplacement = withReplacementComponent.children[0].toLowerCase() === "true";
        } else if ((withReplacementComponent.children === void 0 || withReplacementComponent.children.length === 0) && typeof withReplacementComponent.state?.value === "boolean") {
          withReplacement = withReplacementComponent.state.value;
        } else {
          pushVariantInfo(
            infoDiagnostics,
            `cannot determine unique variants of select as withReplacement isn't constant boolean.`,
            serializedComponent
          );
          return { success: false };
        }
      } else {
        pushVariantInfo(
          infoDiagnostics,
          `cannot determine unique variants of select as withReplacement isn't constant boolean.`,
          serializedComponent
        );
        return { success: false };
      }
    }
    for (let child of serializedComponent.children) {
      if (child.attributes?.selectWeight || child.attributes?.selectForVariants) {
        pushVariantInfo(
          infoDiagnostics,
          `Unique variants for select disabled if have an option with selectWeight or selectForVariants specified`,
          serializedComponent
        );
        return { success: false };
      }
    }
    if (serializedComponent.variants === void 0) {
      serializedComponent.variants = {};
    }
    let descendantVariantComponents = gatherVariantComponents({
      serializedComponents: serializedComponent.children,
      componentInfoObjects
    });
    serializedComponent.variants.descendantVariantComponents = descendantVariantComponents;
    let numVariantsByChild = [];
    for (let descendant of descendantVariantComponents) {
      let descendantClass = componentInfoObjects.allComponentClasses[descendant.componentType];
      let result = descendantClass.determineNumberOfUniqueVariants({
        serializedComponent: descendant,
        componentInfoObjects,
        infoDiagnostics
      });
      if (!result.success) {
        return { success: false };
      }
      numVariantsByChild.push(result.numVariants);
    }
    let uniqueVariantData = {
      numVariantsByChild,
      numToSelect,
      withReplacement
    };
    if (numVariantsByChild.length === 0) {
      numVariants = 1;
    } else if (withReplacement || numToSelect === 1) {
      let numberOfOptionsPerSelection = numVariantsByChild.reduce(
        (a2, c2) => a2 + c2
      );
      numVariants = Math.pow(numberOfOptionsPerSelection, numToSelect);
    } else {
      let numChildren = numVariantsByChild.length;
      if (numToSelect > numChildren) {
        return { success: false };
      }
      let firstNumber = numVariantsByChild[0];
      let allSameNumber = numVariantsByChild.slice(1).every((x2) => x2 === firstNumber);
      if (allSameNumber) {
        let numberOfPermutations = numChildren;
        for (let n2 = numChildren - 1; n2 > numChildren - numToSelect; n2--) {
          numberOfPermutations *= n2;
        }
        numVariants = numberOfPermutations * Math.pow(firstNumber, numToSelect);
      } else {
        numVariants = countOptions(numVariantsByChild, numToSelect);
      }
    }
    if (!(numVariants > 0)) {
      return { success: false };
    }
    serializedComponent.variants.numVariants = numVariants;
    serializedComponent.variants.uniqueVariants = true;
    serializedComponent.variants.uniqueVariantData = uniqueVariantData;
    return {
      success: true,
      numVariants
    };
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let uniqueVariantData = serializedComponent.variants.uniqueVariantData;
    let numVariantsByChild = uniqueVariantData.numVariantsByChild;
    let numToSelect = uniqueVariantData.numToSelect;
    let withReplacement = uniqueVariantData.withReplacement;
    let numChildren = serializedComponent.children.length;
    let childrenToSelect = serializedComponent.children;
    if (numVariantsByChild.length === 0) {
      return { success: true, desiredVariant: { indices: [] } };
    }
    let combinations;
    if (numToSelect === 1) {
      combinations = [...Array(numChildren).keys()].map((x2) => [x2]);
    } else {
      combinations = enumerateSelectionCombinations({
        numberOfIndices: numToSelect,
        numberOfOptions: numChildren,
        // maxNumber: variantIndex,
        withReplacement
      });
    }
    let numberOfCombinations = combinations.length;
    let combinationsAvailable = combinations.map((x2) => ({
      combination: x2,
      numberOfPossibilities: x2.reduce(
        (a2, c2) => a2 * numVariantsByChild[c2],
        1
      )
    }));
    let combinationsLeft = [...Array(numberOfCombinations).keys()];
    let possibilitiesUsed = 0;
    let nCombinationsLeft = combinationsLeft.length;
    let combinationIndexSelected, variantIndexOfSelected;
    let variantIndexLeft = variantIndex - 1;
    while (nCombinationsLeft > 0) {
      let minNumPos = combinationsLeft.map((ind) => combinationsAvailable[ind]).reduce(
        (a2, c2) => Math.min(a2, c2.numberOfPossibilities),
        Infinity
      );
      let chunksize = minNumPos - possibilitiesUsed;
      if (variantIndexLeft < chunksize * nCombinationsLeft) {
        combinationIndexSelected = combinationsLeft[variantIndexLeft % nCombinationsLeft];
        variantIndexOfSelected = possibilitiesUsed + Math.floor(variantIndexLeft / nCombinationsLeft) + 1;
        break;
      } else {
        possibilitiesUsed += chunksize;
        variantIndexLeft -= chunksize * nCombinationsLeft;
        combinationsLeft = combinationsLeft.filter(
          (ind) => combinationsAvailable[ind].numberOfPossibilities > possibilitiesUsed
        );
        nCombinationsLeft = combinationsLeft.length;
      }
    }
    let selectedCombination = combinations[combinationIndexSelected];
    let indicesForEachChild = enumerateCombinations({
      numberOfOptionsByIndex: selectedCombination.map(
        (x2) => numVariantsByChild[x2]
      ),
      maxNumber: variantIndexOfSelected
    })[variantIndexOfSelected - 1].map((x2) => x2 + 1);
    let subvariants = [];
    let haveNontrivialSubvariants = false;
    for (let [ind, childNum] of selectedCombination.entries()) {
      if (numVariantsByChild[childNum] > 1) {
        let child = childrenToSelect[childNum];
        let compClass = componentInfoObjects.allComponentClasses[child.componentType];
        let result = compClass.getUniqueVariant({
          serializedComponent: child,
          variantIndex: indicesForEachChild[ind],
          componentInfoObjects
        });
        if (!result.success) {
          return { success: false };
        }
        subvariants.push(result.desiredVariant);
        haveNontrivialSubvariants = true;
      } else {
        subvariants.push({});
      }
    }
    let desiredVariant = { indices: selectedCombination.map((x2) => x2 + 1) };
    if (haveNontrivialSubvariants) {
      desiredVariant.subvariants = subvariants;
    }
    return { success: true, desiredVariant };
  }
}
let countOptions = function(numOptionsByItem, numItems) {
  if (numItems === 0) {
    return 0;
  }
  if (numItems === 1) {
    return numOptionsByItem.reduce((a2, c2) => a2 + c2);
  }
  let numOptions = 0;
  for (let ind in numOptionsByItem) {
    let num = numOptionsByItem[ind];
    let rest = [...numOptionsByItem];
    rest.splice(ind, 1);
    numOptions += num * countOptions(rest, numItems - 1);
  }
  return numOptions;
};
class Group extends CompositeComponent {
  static componentType = "group";
  static componentDocs = {
    summary: "A logical grouping of components"
  };
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static treatAsComponentForRecursiveReplacements = true;
  static includeBlankStringChildren = true;
  static renderedDefault = true;
  static createsVariants = true;
  static serializeReplacementsForChildren = true;
  static replacementsAlreadyInResolver = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static useSerializedChildrenComponentIndices = true;
  static addExtraSerializedChildrenWhenShadowing = true;
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  // since don't have child groups, tell schema about children here
  static additionalSchemaChildren = ["_base"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.rendered = {
      description: "Whether the group's children are rendered.",
      createComponentOfType: "boolean",
      createStateVariable: "rendered",
      defaultValue: this.renderedDefault,
      public: true
    };
    attributes.isResponse = {
      leaveRaw: true,
      description: "Whether this group's children should be treated as responses for assessment."
    };
    attributes.isPotentialResponse = {
      leaveRaw: true,
      excludeFromSchema: true
    };
    attributes.createComponentOfType = {
      createPrimitiveOfType: "string",
      description: "Component type to wrap each child as when expanding the group."
    };
    attributes.numComponents = {
      createPrimitiveOfType: "number",
      description: "Number of components to create when expanding (used with createComponentOfType)."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: false,
      description: "Whether to render the group's children separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  // don't need child groups
  // as all children will remain serialized
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let componentClass = this;
    stateVariableDefinitions.serializedChildren = {
      returnDependencies: () => ({
        serializedChildren: {
          dependencyType: "serializedChildren",
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            serializedChildren: dependencyValues.serializedChildren
          }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        rendered: {
          dependencyType: "stateVariable",
          variableName: "rendered"
        }
      }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      },
      markStale() {
        return { updateReplacements: true };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    stateVariableDefinitions.numComponentsSpecified = {
      returnDependencies: () => ({
        numComponentsAttr: {
          dependencyType: "attributePrimitive",
          attributeName: "numComponents"
        },
        typeAttr: {
          dependencyType: "attributePrimitive",
          attributeName: "createComponentOfType"
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        let numComponentsSpecified;
        if (dependencyValues.typeAttr) {
          let componentType = componentInfoObjects.componentTypeLowerCaseMapping[dependencyValues.typeAttr.toLowerCase()];
          if (!(componentType in componentInfoObjects.allComponentClasses)) {
            throw Error(
              `Invalid componentType ${dependencyValues.typeAttr} of a ${componentClass.componentType}.`
            );
          }
          if (dependencyValues.numComponentsAttr !== null) {
            numComponentsSpecified = dependencyValues.numComponentsAttr;
          } else {
            numComponentsSpecified = 1;
          }
        } else if (dependencyValues.numComponentsAttr !== null) {
          throw Error(
            `You must specify createComponentOfType when specifying numComponents for a ${componentClass.componentType}.`
          );
        } else {
          numComponentsSpecified = null;
        }
        return { setValue: { numComponentsSpecified } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    nComponents,
    componentInfoObjects,
    workspace,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
  }) {
    let diagnostics = [];
    await component.stateValues.numComponentsSpecified;
    if (!await component.stateValues.rendered) {
      return { replacements: [], diagnostics, nComponents };
    } else {
      let replacements = deepClone(
        await component.state.serializedChildren.value
      );
      const stateIdInfo = {
        prefix: `${component.stateId}|`,
        num: workspace.replacementsCreated
      };
      if ("isResponse" in component.attributes || "isPotentialResponse" in component.attributes) {
        const attributes = {};
        if ("isResponse" in component.attributes) {
          attributes.isResponse = component.attributes.isResponse;
        }
        if ("isPotentialResponse" in component.attributes) {
          attributes.isPotentialResponse = component.attributes.isPotentialResponse;
        }
        for (let repl of replacements) {
          if (typeof repl !== "object") {
            continue;
          }
          const res = convertUnresolvedAttributesForComponentType({
            attributes,
            componentType: repl.componentType,
            componentInfoObjects,
            nComponents,
            stateIdInfo
          });
          nComponents = res.nComponents;
          const attributesFromComposite = res.attributes;
          if (!repl.attributes) {
            repl.attributes = {};
          }
          Object.assign(repl.attributes, attributesFromComposite);
        }
      }
      if (workspace.replacementsCreated === void 0) {
        workspace.replacementsCreated = 0;
      }
      let verificationResult = await verifyReplacementsMatchSpecifiedType(
        {
          component,
          replacements,
          componentInfoObjects,
          compositeAttributesObj: this.createAttributesObject(),
          components,
          publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
          nComponents,
          stateIdInfo
        }
      );
      nComponents = verificationResult.nComponents;
      diagnostics.push(...verificationResult.diagnostics);
      workspace.replacementsCreated = stateIdInfo.num;
      return {
        replacements: verificationResult.replacements,
        diagnostics,
        nComponents
      };
    }
  }
  static async calculateReplacementChanges({
    component,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    if (!await component.stateValues.rendered) {
      if (component.replacements.length > 0 && component.replacementsToWithhold === 0) {
        let replacementsToWithhold = component.replacements.length;
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold
        };
        return {
          replacementChanges: [replacementInstruction],
          diagnostics,
          nComponents
        };
      } else {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    } else {
      if (component.replacements.length > 0) {
        if (component.replacementsToWithhold > 0) {
          let replacementInstruction = {
            changeType: "changeReplacementsToWithhold",
            replacementsToWithhold: 0
          };
          return {
            replacementChanges: [replacementInstruction],
            diagnostics,
            nComponents
          };
        } else {
          return { replacementChanges: [], diagnostics, nComponents };
        }
      } else {
        let createResult = await this.createSerializedReplacements({
          component,
          componentInfoObjects,
          nComponents,
          workspace
        });
        let replacements = createResult.replacements;
        diagnostics.push(...createResult.diagnostics);
        nComponents = createResult.nComponents;
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: 0,
          numberReplacementsToReplace: 0,
          serializedReplacements: replacements,
          replacementsToWithhold: 0
        };
        return {
          replacementChanges: [replacementInstruction],
          diagnostics,
          nComponents
        };
      }
    }
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents,
      useSubpartVariantRng: true
    });
  }
  get allPotentialRendererTypes() {
    let allPotentialRendererTypes = super.allPotentialRendererTypes;
    let additionalRendererTypes = this.potentialRendererTypesFromSerializedComponents(
      this.serializedChildren
    );
    for (let rendererType of additionalRendererTypes) {
      if (!allPotentialRendererTypes.includes(rendererType)) {
        allPotentialRendererTypes.push(rendererType);
      }
    }
    return allPotentialRendererTypes;
  }
}
let urlAlphabet = "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict";
let nanoid = (size = 21) => {
  let id = "";
  let bytes = crypto.getRandomValues(new Uint8Array(size |= 0));
  while (size--) {
    id += urlAlphabet[bytes[size] & 63];
  }
  return id;
};
const { mod: mod$2 } = me$3.math;
class AnimateFromSequence extends BaseComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      changedAnimationOn: this.changedAnimationOn.bind(this),
      advanceAnimation: this.advanceAnimation.bind(this),
      startAnimation: this.startAnimation.bind(this),
      stopAnimation: this.stopAnimation.bind(this),
      toggleAnimation: this.toggleAnimation.bind(this)
    });
  }
  static componentType = "animateFromSequence";
  static componentDocs = {
    summary: "Animates a value through a sequence over time"
  };
  static rendererType = void 0;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    let sequenceAttributes = returnStandardSequenceAttributes();
    Object.assign(attributes, sequenceAttributes);
    attributes.target = {
      createReferences: true,
      description: "Reference to the state variable that will be animated."
    };
    attributes.animationOn = {
      createComponentOfType: "boolean",
      createStateVariable: "animationOn",
      defaultValue: false,
      public: true,
      triggerActionOnChange: "changedAnimationOn",
      description: "Whether the animation is currently playing."
    };
    attributes.animationMode = {
      createComponentOfType: "text",
      createStateVariable: "animationMode",
      defaultValue: "increase",
      validValues: [
        {
          value: "increase",
          description: "Step through values from first to last, looping back to the start."
        },
        {
          value: "decrease",
          description: "Step through values from last to first, looping back to the end."
        },
        {
          value: "increase once",
          description: "Step from first to last and stop without looping."
        },
        {
          value: "decrease once",
          description: "Step from last to first and stop without looping."
        },
        {
          value: "oscillate",
          description: "Step from first to last, then reverse direction at each end."
        }
      ],
      toLowerCase: true,
      public: true,
      description: "How the animation steps through the sequence."
    };
    attributes.animationInterval = {
      createComponentOfType: "number",
      createStateVariable: "animationInterval",
      defaultValue: 1e3,
      public: true,
      description: "Time in milliseconds between successive animation steps."
    };
    attributes.allowAdjustmentsWhileRunning = {
      createComponentOfType: "boolean",
      createStateVariable: "allowAdjustmentsWhileRunning",
      defaultValue: false,
      public: true,
      description: "Whether sequence parameters can be adjusted while the animation is running."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let sequenceDefs = returnStandardSequenceStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, sequenceDefs);
    stateVariableDefinitions.possibleValues = {
      additionalStateVariablesDefined: ["numValues"],
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        length: {
          dependencyType: "stateVariable",
          variableName: "length"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        },
        lowercase: {
          dependencyType: "stateVariable",
          variableName: "lowercase"
        }
      }),
      definition({ dependencyValues }) {
        let possibleValues = returnSequenceValues(dependencyValues);
        return {
          setValue: {
            possibleValues,
            numValues: possibleValues.length
          }
        };
      }
    };
    stateVariableDefinitions.selectedIndex = {
      description: "Index of the currently selected value in the sequence (1-based).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      defaultValue: 1,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition() {
        return {
          useEssentialOrDefaultValue: {
            selectedIndex: true
          }
        };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        stateValues
      }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "selectedIndex",
              value: mod$2(
                desiredStateVariableValues.selectedIndex - 1,
                await stateValues.numValues
              ) + 1
            }
          ]
        };
      }
    };
    stateVariableDefinitions.value = {
      description: "The currently selected value of the animation.",
      public: true,
      shadowingInstructions: {
        hasVariableComponentType: true
      },
      returnDependencies: () => ({
        possibleValues: {
          dependencyType: "stateVariable",
          variableName: "possibleValues"
        },
        selectedIndex: {
          dependencyType: "stateVariable",
          variableName: "selectedIndex"
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            value: dependencyValues.possibleValues[dependencyValues.selectedIndex - 1]
          },
          setCreateComponentOfType: { value: dependencyValues.type }
        };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (dependencyValues.type === "number") {
          let desiredValue = desiredStateVariableValues.value;
          if (desiredValue instanceof me$3.class) {
            desiredValue = desiredValue.evaluate_to_constant();
          }
          if (!Number.isFinite(desiredValue)) {
            return { success: false };
          }
          let start = -1, end = dependencyValues.possibleValues.length - 1;
          while (start < end - 1) {
            let mid = Math.floor((start + end) / 2);
            if (await stateValues.step * (dependencyValues.possibleValues[mid] - desiredValue) > 0) {
              end = mid;
            } else {
              start = mid;
            }
          }
          let closestInd = end;
          if (start !== -1) {
            if (Math.abs(
              desiredValue - dependencyValues.possibleValues[start]
            ) < Math.abs(
              desiredValue - dependencyValues.possibleValues[end]
            )) {
              closestInd = start;
            }
          }
          return {
            success: true,
            instructions: [
              {
                setDependency: "selectedIndex",
                desiredValue: closestInd + 1
              }
            ]
          };
        } else {
          let desiredValue = desiredStateVariableValues.value;
          let index2 = dependencyValues.possibleValues.indexOf(desiredValue);
          if (index2 === -1) {
            return { success: false };
          } else {
            return {
              success: true,
              instructions: [
                {
                  setDependency: "selectedIndex",
                  desiredValue: index2 + 1
                }
              ]
            };
          }
        }
      }
    };
    stateVariableDefinitions.currentAnimationDirection = {
      description: 'Current direction of the animation ("increase" or "decrease"; flips during oscillation).',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      hasEssential: true,
      returnDependencies: () => ({
        animationMode: {
          dependencyType: "stateVariable",
          variableName: "animationMode"
        }
      }),
      definition({ dependencyValues }) {
        return {
          useEssentialOrDefaultValue: {
            currentAnimationDirection: {
              get defaultValue() {
                if (dependencyValues.animationMode.substring(
                  0,
                  8
                ) === "decrease") {
                  return "decrease";
                } else {
                  return "increase";
                }
              }
            }
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let newDirection = desiredStateVariableValues.currentAnimationDirection.toLowerCase();
        if (!["increase", "decrease"].includes(newDirection)) {
          newDirection = "increase";
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "currentAnimationDirection",
              value: newDirection
            }
          ]
        };
      }
    };
    stateVariableDefinitions.targetComponentIdx = {
      additionalStateVariablesDefined: ["unresolvedPath"],
      returnDependencies: () => ({
        target: {
          dependencyType: "attributeRefResolutions",
          attributeName: "target"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.target?.length === 1) {
          const target = dependencyValues.target[0];
          return {
            setValue: {
              targetComponentIdx: target.componentIdx,
              unresolvedPath: target.unresolvedPath
            }
          };
        } else {
          return {
            setValue: {
              targetComponentIdx: null,
              unresolvedPath: null
            }
          };
        }
      }
    };
    stateVariableDefinitions.targetComponent = {
      stateVariablesDeterminingDependencies: ["targetComponentIdx"],
      returnDependencies({ stateValues }) {
        if (stateValues.targetComponentIdx != null) {
          return {
            targetComponent: {
              dependencyType: "componentIdentity",
              componentIdx: stateValues.targetComponentIdx
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        let targetComponent = null;
        if (dependencyValues.targetComponent) {
          targetComponent = dependencyValues.targetComponent;
        }
        return {
          setValue: { targetComponent }
        };
      }
    };
    stateVariableDefinitions.targetIdentities = {
      stateVariablesDeterminingDependencies: ["targetComponent"],
      returnDependencies: function({
        stateValues,
        componentInfoObjects
      }) {
        let dependencies = {};
        if (stateValues.targetComponent !== null) {
          if (componentInfoObjects.isCompositeComponent({
            componentType: stateValues.targetComponent.componentType,
            includeNonStandard: false
          })) {
            dependencies.targets = {
              dependencyType: "replacement",
              compositeIdx: stateValues.targetComponent.componentIdx,
              recursive: true
            };
          } else {
            dependencies.targets = {
              dependencyType: "stateVariable",
              variableName: "targetComponent"
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let targetIdentities = null;
        if (dependencyValues.targets) {
          targetIdentities = dependencyValues.targets;
          if (!Array.isArray(targetIdentities)) {
            targetIdentities = [targetIdentities];
          }
        }
        let diagnostics = [];
        if (targetIdentities === null || targetIdentities.length === 0) {
          diagnostics.push({
            message: "Invalid target for `<animateFromSequence>`: cannot find target.",
            type: "warning"
          });
        }
        return {
          setValue: { targetIdentities },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.targets = {
      stateVariablesDeterminingDependencies: [
        "targetIdentities",
        "unresolvedPath"
      ],
      returnDependencies: function({ stateValues }) {
        let dependencies = {
          targetIdentities: {
            dependencyType: "stateVariable",
            variableName: "targetIdentities"
          },
          unresolvedPath: {
            dependencyType: "stateVariable",
            variableName: "unresolvedPath"
          }
        };
        if (stateValues.targetIdentities !== null) {
          for (let [
            ind,
            target
          ] of stateValues.targetIdentities.entries()) {
            let thisTarget;
            if (stateValues.unresolvedPath) {
              let propIndex = stateValues.propIndex;
              if (propIndex) {
                propIndex = [...propIndex];
              }
              thisTarget = {
                dependencyType: "stateVariableFromUnresolvedPath",
                componentIdx: target.componentIdx,
                unresolvedPath: stateValues.unresolvedPath,
                returnAsComponentObject: true,
                variablesOptional: true,
                caseInsensitiveVariableMatch: true,
                publicStateVariablesOnly: true,
                useMappedVariableNames: true
              };
            } else {
              thisTarget = {
                dependencyType: "stateVariable",
                componentIdx: target.componentIdx,
                variableName: "value",
                returnAsComponentObject: true,
                variablesOptional: true
              };
            }
            dependencies["target" + ind] = thisTarget;
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let targets = null;
        let diagnostics = [];
        if (dependencyValues.targetIdentities !== null) {
          targets = [];
          for (let ind in dependencyValues.targetIdentities) {
            let target = dependencyValues["target" + ind];
            if (target == null) {
              let message = "Invalid target for `<animateFromSequence>`: cannot find target.";
              diagnostics.push({
                message,
                type: "warning"
              });
              continue;
            }
            targets.push(target);
            if (Object.keys(target.stateValues)[0] === void 0) {
              if (dependencyValues.unresolvedPath) {
                let prop = dependencyValues.unresolvedPath[0].name;
                if (dependencyValues.unresolvedPath[0].index.length > 0) {
                  for (const idx of dependencyValues.unresolvedPath[0].index) {
                    prop += `[idx]`;
                  }
                }
                let message = `Invalid target for \`<animateFromSequence>\`: cannot find a state variable named "${prop}" on a \`<${target.componentType}>\`.`;
                diagnostics.push({
                  message,
                  type: "warning"
                });
              } else {
                let message = `Invalid target for \`<animateFromSequence>\`: cannot find a state variable named "value" on a \`<${target.componentType}>\`.`;
                diagnostics.push({
                  message,
                  type: "warning"
                });
              }
            }
          }
        }
        return { setValue: { targets }, sendDiagnostics: diagnostics };
      }
    };
    stateVariableDefinitions.valueToIndex = {
      returnDependencies: () => ({
        possibleValues: {
          dependencyType: "stateVariable",
          variableName: "possibleValues"
        }
      }),
      definition: function({ dependencyValues }) {
        let valueToIndex = {};
        for (let [
          ind,
          item
        ] of dependencyValues.possibleValues.entries()) {
          valueToIndex[item] = ind;
        }
        return { setValue: { valueToIndex } };
      }
    };
    return stateVariableDefinitions;
  }
  async changedAnimationOn({
    stateValues,
    previousValues,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let updateInstructions = [];
    if (stateValues.animationOn) {
      if (!previousValues.animationOn) {
        let newDirection = await this.stateValues.currentAnimationDirection;
        let animationMode = await this.stateValues.animationMode;
        let numValues = await this.stateValues.numValues;
        let selectedIndex = await this.stateValues.selectedIndex;
        let startIndex = await this.findIndexFromTarget();
        if (newDirection === "increase") {
          if (startIndex === numValues) {
            if (animationMode === "increase once") {
              startIndex = 1;
            } else if (animationMode === "oscillate") {
              newDirection = "decrease";
              updateInstructions.push({
                updateType: "updateValue",
                componentIdx: this.componentIdx,
                stateVariable: "currentAnimationDirection",
                value: newDirection
              });
            }
          }
        } else if (newDirection === "decrease") {
          if (startIndex === 1) {
            if (animationMode === "decrease once") {
              startIndex = numValues;
            } else if (animationMode === "oscillate") {
              newDirection = "increase";
              updateInstructions.push({
                updateType: "updateValue",
                componentIdx: this.componentIdx,
                stateVariable: "currentAnimationDirection",
                value: newDirection
              });
            }
          }
        }
        if (startIndex !== selectedIndex) {
          updateInstructions.push({
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "selectedIndex",
            value: startIndex
          });
        }
        let additionalInstructions = await this.getUpdateInstructionsToSetTargetsToValue(
          (await this.stateValues.possibleValues)[mod$2(startIndex - 1, numValues)]
        );
        updateInstructions.push(...additionalInstructions);
        await this.coreFunctions.performUpdate({
          updateInstructions,
          actionId,
          sourceInformation,
          skipRendererUpdate: true,
          event: {
            verb: "played",
            object: {
              componentIdx: this.componentIdx,
              componentType: this.componentType
            },
            context: {
              startIndex,
              animationDirection: newDirection,
              animationMode
            }
          }
        });
        await this.coreFunctions.triggerChainedActions({
          componentIdx: this.componentIdx,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
        this.animationId = nanoid();
        await this.coreFunctions.requestAnimationFrame({
          action: {
            actionName: "advanceAnimation",
            componentIdx: this.componentIdx
          },
          delay: await this.stateValues.animationInterval,
          animationId: this.animationId,
          actionArgs: { previousAnimationId: this.animationId }
        });
      }
    } else {
      if (previousValues.animationOn) {
        await this.coreFunctions.cancelAnimationFrame(this.animationId);
        this.canceledAnimationId = this.animationId;
        await this.coreFunctions.triggerChainedActions({
          componentIdx: this.componentIdx,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
        this.coreFunctions.requestRecordEvent({
          verb: "paused",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          context: {
            endIndex: await this.stateValues.selectedIndex
          }
        });
      }
    }
  }
  async findIndexFromTarget() {
    let targets = await this.stateValues.targets;
    let selectedIndex = await this.stateValues.selectedIndex;
    if (targets === null) {
      return selectedIndex;
    }
    let target = targets[0];
    let stateVariable;
    if (target?.stateValues) {
      stateVariable = Object.keys(target.stateValues)[0];
    }
    if (!stateVariable) {
      return selectedIndex;
    }
    let value = target.stateValues[stateVariable];
    let type = await this.stateValues.type;
    if (type === "number" && value instanceof me$3.class) {
      value = value.evaluate_to_constant();
      if (!Number.isFinite(value)) {
        return selectedIndex;
      }
    }
    let matchedIndex = (await this.stateValues.valueToIndex)[value];
    if (matchedIndex !== void 0) {
      return matchedIndex + 1;
    }
    if (type !== "number") {
      return selectedIndex;
    }
    let items = await this.stateValues.possibleValues;
    let findNextLargerIndex = function(minIndex = 0, maxIndex = items.length - 1) {
      if (maxIndex <= minIndex + 1) {
        if (value > items[minIndex]) {
          return maxIndex;
        } else {
          return minIndex;
        }
      }
      let midIndex = Math.round((maxIndex + minIndex) / 2);
      if (value > items[midIndex]) {
        return findNextLargerIndex(midIndex, maxIndex);
      } else {
        return findNextLargerIndex(minIndex, midIndex);
      }
    };
    let closeIndex = findNextLargerIndex();
    if (closeIndex !== 0) {
      let leftValue = items[closeIndex - 1];
      let rightValue = items[closeIndex];
      let leftDist = Math.abs(value - leftValue);
      let rightDist = Math.abs(value - rightValue);
      if (leftDist < rightDist) {
        closeIndex--;
      }
    }
    return closeIndex + 1;
  }
  async advanceAnimation({
    previousAnimationId,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let animationOn = await this.stateValues.animationOn;
    if (previousAnimationId === this.canceledAnimationId || !animationOn) {
      return;
    }
    let newSelectedIndex;
    let continueAnimation = true;
    let newDirection;
    let animationMode = await this.stateValues.animationMode;
    let previousIndex;
    if (await this.stateValues.allowAdjustmentsWhileRunning) {
      previousIndex = await this.findIndexFromTarget();
    } else {
      previousIndex = await this.stateValues.selectedIndex;
    }
    if (await this.stateValues.currentAnimationDirection === "decrease") {
      newSelectedIndex = previousIndex - 1;
      if (newSelectedIndex <= 1) {
        if (animationMode === "decrease once") {
          continueAnimation = false;
        } else if (animationMode === "oscillate") {
          newDirection = "increase";
        }
      }
    } else {
      newSelectedIndex = previousIndex + 1;
      if (newSelectedIndex >= await this.stateValues.numValues) {
        if (animationMode === "increase once") {
          continueAnimation = false;
        } else if (animationMode === "oscillate") {
          newDirection = "decrease";
        }
      }
    }
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "selectedIndex",
        value: newSelectedIndex
      }
    ];
    let additionalInstructions = await this.getUpdateInstructionsToSetTargetsToValue(
      (await this.stateValues.possibleValues)[mod$2(newSelectedIndex - 1, await this.stateValues.numValues)]
    );
    updateInstructions.push(...additionalInstructions);
    if (!continueAnimation) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "animationOn",
        value: false
      });
    }
    if (newDirection) {
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "currentAnimationDirection",
        value: newDirection
      });
    }
    await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
    if (continueAnimation) {
      this.animationId = nanoid();
      await this.coreFunctions.requestAnimationFrame({
        action: {
          actionName: "advanceAnimation",
          componentIdx: this.componentIdx
        },
        delay: await this.stateValues.animationInterval,
        animationId: this.animationId,
        actionArgs: { previousAnimationId: this.animationId }
      });
    }
  }
  async startAnimation({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "animationOn",
          value: true
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async stopAnimation({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "animationOn",
          value: false
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async toggleAnimation({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "animationOn",
          value: !await this.stateValues.animationOn
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async getUpdateInstructionsToSetTargetsToValue(value) {
    let targets = await this.stateValues.targets;
    if (targets == null) {
      return [];
    }
    let updateInstructions = [];
    for (let target of targets) {
      let stateVariable = Object.keys(target.stateValues)[0];
      if (stateVariable === void 0) {
        continue;
      }
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: target.componentIdx,
        stateVariable,
        value
      });
    }
    return updateInstructions;
  }
}
class Evaluate extends MathComponent {
  static componentType = "evaluate";
  static componentDocs = {
    summary: "Evaluates a function at a math value"
  };
  static rendererType = "math";
  // remove variableForImplicitProp so that an evaluate copied into a function
  // behaves like an evaluate (not just the value property) and can be reevaluated
  static variableForImplicitProp = void 0;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.forceSymbolic = {
      description: "Whether to force symbolic evaluation.",
      createComponentOfType: "boolean",
      createStateVariable: "forceSymbolic",
      defaultValue: false,
      public: true
    };
    attributes.forceNumeric = {
      description: "Whether to force numeric evaluation.",
      createComponentOfType: "boolean",
      createStateVariable: "forceNumeric",
      defaultValue: false,
      public: true
    };
    attributes.function = {
      createComponentOfType: "function",
      description: "The function to evaluate."
    };
    attributes.input = {
      createComponentOfType: "mathList",
      description: "Input value(s) at which to evaluate the function."
    };
    attributes.unordered = {
      description: "Whether to treat the inputs as unordered.",
      createComponentOfType: "boolean",
      createStateVariable: "unordered",
      defaultValue: false,
      public: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.codePre;
    delete stateVariableDefinitions.expressionWithCodes;
    delete stateVariableDefinitions.mathChildrenFunctionSymbols;
    delete stateVariableDefinitions.codesAdjacentToStrings;
    delete stateVariableDefinitions.mathChildrenByVectorComponent;
    delete stateVariableDefinitions.mathChildrenWithCanBeModified;
    delete stateVariableDefinitions.unordered;
    stateVariableDefinitions.canBeModified = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { canBeModified: false } })
    };
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({
      additionalAttributeComponent: "function"
    });
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.inputMaths = {
      returnDependencies: () => ({
        inputAttr: {
          dependencyType: "attributeComponent",
          attributeName: "input",
          variableNames: ["maths"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.inputAttr) {
          return {
            setValue: {
              inputMaths: dependencyValues.inputAttr.stateValues.maths
            }
          };
        } else {
          return { setValue: { inputMaths: [] } };
        }
      }
    };
    stateVariableDefinitions.unnormalizedValue = {
      returnDependencies() {
        return {
          inputMaths: {
            dependencyType: "stateVariable",
            variableName: "inputMaths"
          },
          functionAttr: {
            dependencyType: "attributeComponent",
            attributeName: "function",
            variableNames: [
              "symbolicfs",
              "numericalfs",
              "symbolic",
              "numInputs"
            ]
          },
          forceSymbolic: {
            dependencyType: "stateVariable",
            variableName: "forceSymbolic"
          },
          forceNumeric: {
            dependencyType: "stateVariable",
            variableName: "forceNumeric"
          }
        };
      },
      definition({ dependencyValues }) {
        let functionComp = dependencyValues.functionAttr;
        if (!functionComp) {
          return {
            setValue: {
              unnormalizedValue: me$3.fromAst("＿")
            }
          };
        }
        let f2;
        if (!dependencyValues.forceNumeric && (functionComp.stateValues.symbolic || dependencyValues.forceSymbolic)) {
          f2 = returnSymbolicFunctionForEvaluate({
            numInputs: functionComp.stateValues.numInputs,
            symbolicfs: functionComp.stateValues.symbolicfs
          });
        } else {
          f2 = returnNumericFunctionForEvaluate({
            numInputs: functionComp.stateValues.numInputs,
            numericalfs: functionComp.stateValues.numericalfs
          });
        }
        let unnormalizedValue = f2(dependencyValues.inputMaths);
        return {
          setValue: { unnormalizedValue }
        };
      }
    };
    stateVariableDefinitions.formula = {
      description: "The function evaluated at the inputs as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies() {
        return {
          inputMaths: {
            dependencyType: "stateVariable",
            variableName: "inputMaths"
          },
          functionAttr: {
            dependencyType: "attributeComponent",
            attributeName: "function",
            variableNames: ["symbolicfs", "numInputs"]
          }
        };
      },
      definition({ dependencyValues }) {
        let functionComp = dependencyValues.functionAttr;
        if (!functionComp) {
          return {
            setValue: {
              formula: me$3.fromAst("＿")
            }
          };
        }
        let f2 = returnSymbolicFunctionForEvaluate({
          numInputs: functionComp.stateValues.numInputs,
          symbolicfs: functionComp.stateValues.symbolicfs
        });
        let formula = f2(dependencyValues.inputMaths);
        return {
          setValue: { formula }
        };
      }
    };
    stateVariableDefinitions.fReevaluate = {
      returnDependencies() {
        return {
          functionAttr: {
            dependencyType: "attributeComponent",
            attributeName: "function",
            variableNames: [
              "symbolicfs",
              "numericalfs",
              "symbolic",
              "numInputs"
            ]
          },
          forceSymbolic: {
            dependencyType: "stateVariable",
            variableName: "forceSymbolic"
          },
          forceNumeric: {
            dependencyType: "stateVariable",
            variableName: "forceNumeric"
          }
        };
      },
      definition({ dependencyValues }) {
        let functionComp = dependencyValues.functionAttr;
        if (!functionComp) {
          return {
            setValue: {
              fReevaluate: (_2) => me$3.fromAst("＿")
            }
          };
        }
        let fReevaluate;
        if (!dependencyValues.forceNumeric && (functionComp.stateValues.symbolic || dependencyValues.forceSymbolic)) {
          fReevaluate = returnSymbolicFunctionForEvaluate({
            numInputs: functionComp.stateValues.numInputs,
            symbolicfs: functionComp.stateValues.symbolicfs
          });
        } else {
          fReevaluate = returnNumericFunctionForEvaluate({
            numInputs: functionComp.stateValues.numInputs,
            numericalfs: functionComp.stateValues.numericalfs
          });
        }
        return { setValue: { fReevaluate } };
      }
    };
    stateVariableDefinitions.fReevaluateDefinition = {
      returnDependencies() {
        return {
          functionAttr: {
            dependencyType: "attributeComponent",
            attributeName: "function",
            variableNames: [
              "fDefinitions",
              "symbolic",
              "numInputs"
            ]
          },
          forceSymbolic: {
            dependencyType: "stateVariable",
            variableName: "forceSymbolic"
          },
          forceNumeric: {
            dependencyType: "stateVariable",
            variableName: "forceNumeric"
          }
        };
      },
      definition({ dependencyValues }) {
        let functionComp = dependencyValues.functionAttr;
        if (!functionComp) {
          return {
            setValue: {
              fReevaluateDefinition: {}
            }
          };
        }
        let fReevaluateDefinition;
        if (!dependencyValues.forceNumeric && (functionComp.stateValues.symbolic || dependencyValues.forceSymbolic)) {
          fReevaluateDefinition = {
            functionType: "numericForEvaluate",
            numInputs: functionComp.stateValues.numInputs,
            fDefinitions: functionComp.stateValues.fDefinitions
          };
        } else {
          fReevaluateDefinition = {
            functionType: "numericForEvaluate",
            numInputs: functionComp.stateValues.numInputs,
            fDefinitions: functionComp.stateValues.fDefinitions
          };
        }
        return { setValue: { fReevaluateDefinition } };
      }
    };
    return stateVariableDefinitions;
  }
}
function sampleFromRandomNumbers({
  type,
  numSamples,
  standardDeviation,
  mean: mean2,
  to: to2,
  from,
  step,
  exclude,
  numDiscreteValues,
  rng
}) {
  if (type === "gaussian") {
    if (!(standardDeviation >= 0) || !Number.isFinite(mean2)) {
      let message = "Invalid mean (" + mean2 + ") or standard deviation (" + standardDeviation + ") for a gaussian random variable.";
      console.warn(message);
      return Array(numSamples).fill(NaN);
    }
    let sampledValues = [];
    for (let i2 = 0; i2 < numSamples; i2++) {
      let u2 = 0, v2 = 0;
      while (u2 === 0) {
        u2 = rng();
      }
      while (v2 === 0) {
        v2 = rng();
      }
      let standardNormal = Math.sqrt(-2 * Math.log(u2)) * Math.cos(2 * Math.PI * v2);
      sampledValues.push(mean2 + standardDeviation * standardNormal);
    }
    return sampledValues;
  } else if (type === "uniform") {
    let sampledValues = [];
    let diff = to2 - from;
    for (let i2 = 0; i2 < numSamples; i2++) {
      sampledValues.push(from + rng() * diff);
    }
    return sampledValues;
  } else {
    let sampledValues = [];
    if (numDiscreteValues > 0) {
      let indexMap = [...Array(numDiscreteValues).keys()];
      if (exclude.length > 0) {
        indexMap = [];
        let numOrigValues = Math.round((to2 - from) / step + 1);
        for (let i2 = 0; i2 < numOrigValues; i2++) {
          let val = from + i2 * step;
          if (!exclude.includes(val)) {
            indexMap.push(i2);
          }
        }
      }
      for (let i2 = 0; i2 < numSamples; i2++) {
        let ind = Math.floor(rng() * numDiscreteValues);
        ind = indexMap[ind];
        sampledValues.push(from + step * ind);
      }
    }
    return sampledValues;
  }
}
function sampleFromNumberList({
  possibleValues,
  numUniqueRequired = 1,
  numSamples = 1,
  rng
}) {
  let numPossibleValues = possibleValues.length;
  if (numUniqueRequired === 1) {
    let sampledValues2 = [];
    for (let ind = 0; ind < numSamples; ind++) {
      let rand = rng();
      let ind2 = Math.floor(rand * numPossibleValues);
      sampledValues2.push(possibleValues[ind2]);
    }
    return sampledValues2;
  }
  let shuffledValues = [...possibleValues];
  for (let i2 = shuffledValues.length - 1; i2 > 0; i2--) {
    const rand = rng();
    const j2 = Math.floor(rand * (i2 + 1));
    [shuffledValues[i2], shuffledValues[j2]] = [
      shuffledValues[j2],
      shuffledValues[i2]
    ];
  }
  let sampledValues = shuffledValues.slice(0, numSamples);
  return sampledValues;
}
class SampleRandomNumbers extends CompositeComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      resample: this.resample.bind(this)
    });
  }
  static componentType = "sampleRandomNumbers";
  static componentDocs = {
    summary: "Samples random numbers from a distribution"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["number"];
  static createsVariants = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static processWhenJustUpdatedForNewComponent = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numSamples = {
      description: "Number of samples to draw.",
      createComponentOfType: "number",
      createStateVariable: "numSamples",
      defaultValue: 1,
      public: true
    };
    attributes.type = {
      description: "Distribution from which to sample.",
      createComponentOfType: "text",
      createStateVariable: "type",
      defaultValue: "uniform",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "uniform",
          description: "Continuous uniform distribution over `[from, to]`."
        },
        {
          value: "discreteUniform",
          description: "Discrete uniform distribution over integers in `[from, to]`."
        },
        {
          value: "gaussian",
          description: "Normal (Gaussian) distribution with the specified mean and standard deviation."
        }
      ]
    };
    attributes.mean = {
      createComponentOfType: "number",
      createStateVariable: "specifiedMean",
      defaultValue: 0,
      description: "Mean of the sampling distribution (Gaussian)."
    };
    attributes.standardDeviation = {
      createComponentOfType: "number",
      createStateVariable: "specifiedStandardDeviation",
      defaultValue: 1,
      description: "Standard deviation of the sampling distribution (Gaussian)."
    };
    attributes.variance = {
      createComponentOfType: "number",
      createStateVariable: "specifiedVariance",
      defaultValue: 1,
      description: "Variance of the sampling distribution (Gaussian)."
    };
    attributes.from = {
      createComponentOfType: "number",
      createStateVariable: "specifiedFrom",
      defaultValue: null,
      description: "Lower bound of the sampling range."
    };
    attributes.to = {
      createComponentOfType: "number",
      createStateVariable: "specifiedTo",
      defaultValue: null,
      description: "Upper bound of the sampling range."
    };
    attributes.step = {
      createComponentOfType: "number",
      createStateVariable: "specifiedStep",
      defaultValue: 1,
      description: "Step size between samples for the discrete-uniform distribution."
    };
    attributes.exclude = {
      createComponentOfType: "numberList",
      createStateVariable: "exclude",
      defaultValue: [],
      description: "Values to exclude from the sample space."
    };
    const numberDisplayAttrs = returnNumberDisplayAttributes();
    for (let attrName in numberDisplayAttrs) {
      attributes[attrName] = {
        leaveRaw: true,
        description: numberDisplayAttrs[attrName].description
      };
    }
    attributes.variantDeterminesSeed = {
      description: "Whether the document's variant index determines the random seed.",
      createPrimitiveOfType: "boolean",
      createStateVariable: "variantDeterminesSeed",
      defaultPrimitiveValue: false,
      public: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.step = {
      description: "Step size between sample values (for discrete distributions).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        specifiedStep: {
          dependencyType: "stateVariable",
          variableName: "specifiedStep"
        }
      }),
      definition({ dependencyValues }) {
        let step;
        if (dependencyValues.type === "discreteuniform") {
          step = dependencyValues.specifiedStep;
        } else {
          step = null;
        }
        return { setValue: { step } };
      }
    };
    stateVariableDefinitions.from = {
      description: "Lower bound of the sampling range.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      additionalStateVariablesDefined: [
        {
          variableName: "to",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "number"
          },
          description: "Upper bound of the sampling range."
        },
        {
          variableName: "numDiscreteValues"
        }
      ],
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        specifiedFrom: {
          dependencyType: "stateVariable",
          variableName: "specifiedFrom"
        },
        specifiedTo: {
          dependencyType: "stateVariable",
          variableName: "specifiedTo"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        }
      }),
      definition({ dependencyValues }) {
        if (!["discreteuniform", "uniform"].includes(
          dependencyValues.type
        )) {
          return {
            setValue: {
              from: null,
              to: null,
              numDiscreteValues: null
            }
          };
        }
        let step = dependencyValues.step;
        let exclude = dependencyValues.exclude;
        let from = dependencyValues.specifiedFrom;
        let to2 = dependencyValues.specifiedTo;
        let numDiscreteValues = null;
        if (to2 === null) {
          if (from === null) {
            from = 0;
          }
          if (dependencyValues.type === "uniform") {
            to2 = from + 1;
          } else {
            while (exclude.includes(from)) {
              from += step;
            }
            to2 = from + step;
            let i2 = 1;
            while (exclude.includes(to2)) {
              i2++;
              to2 = from + i2 * step;
            }
            numDiscreteValues = 2;
          }
        } else {
          if (from === null) {
            if (dependencyValues.type === "uniform") {
              from = 0;
            } else {
              let targetFrom = 0;
              numDiscreteValues = Math.floor(
                (to2 - targetFrom) / step + 1
              );
              if (numDiscreteValues < 1) {
                numDiscreteValues = 0;
                from = null;
              } else {
                from = to2 - (numDiscreteValues - 1) * step;
                let numExcluded = 0;
                for (let i2 = 0; i2 < numDiscreteValues; i2++) {
                  let val = from + i2 * step;
                  if (exclude.includes(val)) {
                    numExcluded++;
                  }
                }
                numDiscreteValues -= numExcluded;
              }
            }
          } else {
            if (dependencyValues.type === "discreteuniform") {
              numDiscreteValues = Math.floor(
                (to2 - from) / step + 1
              );
              if (numDiscreteValues < 1) {
                numDiscreteValues = 0;
              } else {
                to2 = from + (numDiscreteValues - 1) * step;
                let numExcluded = 0;
                for (let i2 = 0; i2 < numDiscreteValues; i2++) {
                  let val = from + i2 * step;
                  if (exclude.includes(val)) {
                    numExcluded++;
                  }
                }
                numDiscreteValues -= numExcluded;
              }
            }
          }
        }
        return { setValue: { from, to: to2, numDiscreteValues } };
      }
    };
    stateVariableDefinitions.mean = {
      description: "Mean of the sampling distribution.",
      stateVariablesDeterminingDependencies: ["type"],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies({ stateValues }) {
        let dependencies = {
          type: {
            dependencyType: "stateVariable",
            variableName: "type"
          }
        };
        if (stateValues.type === "gaussian") {
          dependencies.specifiedMean = {
            dependencyType: "stateVariable",
            variableName: "specifiedMean"
          };
        } else {
          dependencies.from = {
            dependencyType: "stateVariable",
            variableName: "from"
          };
          dependencies.to = {
            dependencyType: "stateVariable",
            variableName: "to"
          };
          if (stateValues.type === "discreteuniform") {
            dependencies.exclude = {
              dependencyType: "stateVariable",
              variableName: "exclude"
            };
            dependencies.step = {
              dependencyType: "stateVariable",
              variableName: "step"
            };
            dependencies.numDiscreteValues = {
              dependencyType: "stateVariable",
              variableName: "numDiscreteValues"
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let mean2;
        if (dependencyValues.type === "gaussian") {
          mean2 = dependencyValues.specifiedMean;
        } else if (dependencyValues.type === "discreteuniform" && dependencyValues.exclude.length > 0) {
          mean2 = 0;
          let numOrigValues = Math.round(
            (dependencyValues.to - dependencyValues.from) / dependencyValues.step + 1
          );
          for (let i2 = 0; i2 < numOrigValues; i2++) {
            let val = dependencyValues.from + i2 * dependencyValues.step;
            if (!dependencyValues.exclude.includes(val)) {
              mean2 += val;
            }
          }
          mean2 /= dependencyValues.numDiscreteValues;
        } else {
          mean2 = (dependencyValues.from + dependencyValues.to) / 2;
        }
        return { setValue: { mean: mean2 } };
      }
    };
    stateVariableDefinitions.variance = {
      description: "Variance of the sampling distribution.",
      stateVariablesDeterminingDependencies: ["type"],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies({ stateValues }) {
        let dependencies = {
          type: {
            dependencyType: "stateVariable",
            variableName: "type"
          }
        };
        if (stateValues.type === "gaussian") {
          dependencies.specifiedVariance = {
            dependencyType: "stateVariable",
            variableName: "specifiedVariance"
          };
          dependencies.specifiedStandardDeviation = {
            dependencyType: "stateVariable",
            variableName: "specifiedStandardDeviation"
          };
        } else {
          dependencies.from = {
            dependencyType: "stateVariable",
            variableName: "from"
          };
          dependencies.to = {
            dependencyType: "stateVariable",
            variableName: "to"
          };
          if (stateValues.type === "discreteuniform") {
            dependencies.exclude = {
              dependencyType: "stateVariable",
              variableName: "exclude"
            };
            dependencies.step = {
              dependencyType: "stateVariable",
              variableName: "step"
            };
            dependencies.numDiscreteValues = {
              dependencyType: "stateVariable",
              variableName: "numDiscreteValues"
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues, usedDefault }) {
        let variance2;
        if (dependencyValues.type === "gaussian") {
          if (usedDefault.specifiedVariance && !usedDefault.specifiedStandardDeviation) {
            variance2 = dependencyValues.specifiedStandardDeviation ** 2;
          } else {
            variance2 = dependencyValues.specifiedVariance;
          }
        } else if (dependencyValues.type === "discreteuniform") {
          if (dependencyValues.exclude.length > 0) {
            let sum2 = 0;
            variance2 = 0;
            let numOrigValues = Math.round(
              (dependencyValues.to - dependencyValues.from) / dependencyValues.step + 1
            );
            for (let i2 = 0; i2 < numOrigValues; i2++) {
              let val = dependencyValues.from + i2 * dependencyValues.step;
              if (!dependencyValues.exclude.includes(val)) {
                sum2 += val;
                variance2 += val * val;
              }
            }
            let N2 = dependencyValues.numDiscreteValues;
            variance2 -= sum2 * sum2 / N2;
            variance2 /= N2;
          } else {
            variance2 = (dependencyValues.numDiscreteValues ** 2 - 1) * dependencyValues.step ** 2 / 12;
          }
        } else {
          variance2 = (dependencyValues.to - dependencyValues.from) ** 2 / 12;
        }
        return { setValue: { variance: variance2 } };
      }
    };
    stateVariableDefinitions.standardDeviation = {
      description: "Standard deviation of the sampling distribution.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        variance: {
          dependencyType: "stateVariable",
          variableName: "variance"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          standardDeviation: Math.sqrt(dependencyValues.variance)
        }
      })
    };
    stateVariableDefinitions.sampledValues = {
      shadowVariable: true,
      hasEssential: true,
      stateVariablesDeterminingDependencies: ["variantDeterminesSeed"],
      returnDependencies({ stateValues, sharedParameters }) {
        let dependencies = {
          numSamples: {
            dependencyType: "stateVariable",
            variableName: "numSamples"
          },
          type: {
            dependencyType: "stateVariable",
            variableName: "type"
          },
          from: {
            dependencyType: "stateVariable",
            variableName: "from"
          },
          to: {
            dependencyType: "stateVariable",
            variableName: "to"
          },
          step: {
            dependencyType: "stateVariable",
            variableName: "step"
          },
          exclude: {
            dependencyType: "stateVariable",
            variableName: "exclude"
          },
          numDiscreteValues: {
            dependencyType: "stateVariable",
            variableName: "numDiscreteValues"
          },
          mean: {
            dependencyType: "stateVariable",
            variableName: "mean"
          },
          standardDeviation: {
            dependencyType: "stateVariable",
            variableName: "standardDeviation"
          }
        };
        if (stateValues.variantDeterminesSeed) {
          dependencies.rng = {
            dependencyType: "value",
            value: sharedParameters.variantRng,
            doNotProxy: true
          };
        } else {
          dependencies.rng = {
            dependencyType: "value",
            value: sharedParameters.rngWithDateSeed,
            doNotProxy: true
          };
        }
        return dependencies;
      },
      definition({
        dependencyValues,
        changes,
        justUpdatedForNewComponent
      }) {
        if (dependencyValues.numSamples < 1) {
          return {
            setEssentialValue: { sampledValues: [] },
            setValue: { sampledValues: [] }
          };
        }
        if (Object.keys(changes).length === 0 || justUpdatedForNewComponent) {
          return {
            useEssentialOrDefaultValue: { sampledValues: true }
          };
        }
        let sampledValues = sampleFromRandomNumbers(dependencyValues);
        return {
          setEssentialValue: { sampledValues },
          setValue: { sampledValues }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "sampledValues",
              value: desiredStateVariableValues.sampledValues
            }
          ]
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        sampledValues: {
          dependencyType: "stateVariable",
          variableName: "sampledValues"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({ sharedParameters }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    componentInfoObjects,
    startNum = 0,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let attributesToConvert = {};
    for (let attr of Object.keys(returnNumberDisplayAttributes())) {
      if (attr in component.attributes) {
        attributesToConvert[attr] = component.attributes[attr];
      }
    }
    let replacements = [];
    for (let value of (await component.stateValues.sampledValues).slice(
      startNum
    )) {
      let attributesFromComposite = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "number",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributesFromComposite = res.attributes;
        nComponents = res.nComponents;
      }
      replacements.push({
        type: "serialized",
        componentType: "number",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: attributesFromComposite,
        state: { value, fixed: true },
        doenetAttributes: {},
        children: []
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    let replacementChanges = [];
    let sampledValues = await component.stateValues.sampledValues;
    if (sampledValues.length < component.replacements.length) {
      let numberToWithhold = component.replacements.length - sampledValues.length;
      if (numberToWithhold !== component.replacementsToWithhold) {
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: numberToWithhold
        };
        replacementChanges.push(replacementInstruction);
      }
    } else {
      if (component.replacementsToWithhold > 0) {
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: 0
        };
        replacementChanges.push(replacementInstruction);
      }
      if (sampledValues.length > component.replacements.length) {
        let result = await this.createSerializedReplacements({
          component,
          componentInfoObjects,
          startNum: component.replacements.length,
          nComponents,
          workspace
        });
        diagnostics.push(...result.diagnostics);
        nComponents = result.nComponents;
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: component.replacements.length,
          numberReplacementsToReplace: 0,
          serializedReplacements: result.replacements
        };
        replacementChanges.push(replacementInstruction);
      }
    }
    let numUpdate = Math.min(
      component.replacements.length,
      sampledValues.length
    );
    for (let ind = 0; ind < numUpdate; ind++) {
      let replacementInstruction = {
        changeType: "updateStateVariables",
        component: component.replacements[ind],
        stateChanges: { value: sampledValues[ind] }
      };
      replacementChanges.push(replacementInstruction);
    }
    return { replacementChanges, diagnostics, nComponents };
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents
    });
    let seedForRandomNumbers = sharedParameters.variantRng().toString().slice(2, 8) + +/* @__PURE__ */ new Date();
    sharedParameters.rngWithDateSeed = new sharedParameters.rngClass(
      seedForRandomNumbers
    );
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let variantDeterminesSeed = serializedComponent.attributes.variantDeterminesSeed.primitive.value;
    if (variantDeterminesSeed) {
      return { success: false };
    } else {
      return super.determineNumberOfUniqueVariants({
        serializedComponent,
        componentInfoObjects,
        infoDiagnostics
      });
    }
  }
  async resample({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let sampledValues = sampleFromRandomNumbers({
      type: await this.stateValues.type,
      numSamples: await this.stateValues.numSamples,
      standardDeviation: await this.stateValues.standardDeviation,
      mean: await this.stateValues.mean,
      to: await this.stateValues.to,
      from: await this.stateValues.from,
      step: await this.stateValues.step,
      exclude: await this.stateValues.exclude,
      numDiscreteValues: await this.stateValues.numDiscreteValues,
      rng: await this.stateValues.variantDeterminesSeed ? this.sharedParameters.variantRng : this.sharedParameters.rngWithDateSeed
    });
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "sampledValues",
          value: sampledValues
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
class SelectRandomNumbers extends SampleRandomNumbers {
  static componentType = "selectRandomNumbers";
  static componentDocs = {
    summary: "Selects a fixed set of random numbers to create document variants"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["number"];
  static createsVariants = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.numSamples;
    delete attributes.variantDeterminesSeed;
    attributes.numToSelect = {
      description: "How many random numbers to select.",
      createComponentOfType: "integer",
      createStateVariable: "numToSelect",
      defaultValue: 1,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.variants = {
      returnDependencies: () => ({
        variants: {
          dependencyType: "variants"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { variants: dependencyValues.variants } };
      }
    };
    stateVariableDefinitions.step.immutable = true;
    stateVariableDefinitions.from.immutable = true;
    stateVariableDefinitions.from.additionalStateVariablesDefined[0].immutable = true;
    stateVariableDefinitions.from.additionalStateVariablesDefined[1].immutable = true;
    stateVariableDefinitions.mean.immutable = true;
    stateVariableDefinitions.variance.immutable = true;
    stateVariableDefinitions.standardDeviation.immutable = true;
    delete stateVariableDefinitions.sampledValues;
    stateVariableDefinitions.selectedValues = {
      immutable: true,
      hasEssential: true,
      shadowVariable: true,
      returnDependencies: ({ sharedParameters }) => ({
        numSamples: {
          dependencyType: "stateVariable",
          variableName: "numToSelect"
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        to: {
          dependencyType: "stateVariable",
          variableName: "to"
        },
        step: {
          dependencyType: "stateVariable",
          variableName: "step"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        },
        numDiscreteValues: {
          dependencyType: "stateVariable",
          variableName: "numDiscreteValues"
        },
        mean: {
          dependencyType: "stateVariable",
          variableName: "mean"
        },
        standardDeviation: {
          dependencyType: "stateVariable",
          variableName: "standardDeviation"
        },
        variants: {
          dependencyType: "stateVariable",
          variableName: "variants"
        },
        rng: {
          dependencyType: "value",
          value: sharedParameters.variantRng,
          doNotProxy: true
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.numSamples < 1) {
          return {
            setEssentialValue: { selectedValues: [] },
            setValue: { selectedValues: [] }
          };
        }
        if (dependencyValues.variants && dependencyValues.variants.desiredVariant) {
          let desiredValues = dependencyValues.variants.desiredVariant.values;
          if (desiredValues) {
            if (desiredValues.length !== dependencyValues.numSamples) {
              throw Error(
                "Number of values specified for selectRandomNumber must match number to select"
              );
            }
            return {
              setEssentialValue: {
                selectedValues: desiredValues
              },
              setValue: { selectedValues: desiredValues }
            };
          }
        }
        let selectedValues = sampleFromRandomNumbers(dependencyValues);
        return {
          setEssentialValue: { selectedValues },
          setValue: { selectedValues }
        };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: () => ({
        selectedValues: {
          dependencyType: "stateVariable",
          variableName: "selectedValues"
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          values: dependencyValues.selectedValues,
          meta: { createdBy: componentIdx }
        };
        return { setValue: { generatedVariantInfo } };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        selectedValues: {
          dependencyType: "stateVariable",
          variableName: "selectedValues"
        }
      }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let attributesToConvert = {};
    for (let attr of Object.keys(returnNumberDisplayAttributes())) {
      if (attr in component.attributes) {
        attributesToConvert[attr] = component.attributes[attr];
      }
    }
    let replacements = [];
    for (let value of await component.stateValues.selectedValues) {
      let attributesFromComposite = {};
      if (Object.keys(attributesToConvert).length > 0) {
        const res = convertUnresolvedAttributesForComponentType({
          attributes: attributesToConvert,
          componentType: "number",
          componentInfoObjects,
          nComponents,
          stateIdInfo
        });
        attributesFromComposite = res.attributes;
        nComponents = res.nComponents;
      }
      replacements.push({
        type: "serialized",
        componentType: "number",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: attributesFromComposite,
        state: { value, fixed: true },
        doenetAttributes: {},
        children: []
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static determineNumberOfUniqueVariants() {
    return { success: false };
  }
}
delete SelectRandomNumbers.stateVariableToEvaluateAfterReplacements;
delete SelectRandomNumbers.calculateReplacementChanges;
function createPrimesList({ from = 2, to: to2 = 100, exclude = [] }) {
  from = Math.max(from, 2);
  if (!(Number.isFinite(from) && Number.isFinite(to2) && to2 >= from)) {
    return [];
  }
  let valueList = [...Array(to2 + 1).keys()];
  let sqrtMax = Math.sqrt(to2);
  for (let i2 = 2; i2 <= sqrtMax; i2++) {
    if (valueList[i2]) {
      for (let j2 = i2 * i2; j2 <= to2; j2 += i2) {
        valueList[j2] = 0;
      }
    }
  }
  let primes = valueList.slice(from).filter((x2) => x2 && !exclude.includes(x2));
  return primes;
}
class SelectPrimeNumbers extends CompositeComponent {
  static componentType = "selectPrimeNumbers";
  static componentDocs = {
    summary: "Randomly selects prime numbers from a range to create document variants"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["integer"];
  static createsVariants = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.from = {
      description: "Lower bound (inclusive) of the prime range to select from.",
      createComponentOfType: "integer",
      createStateVariable: "from",
      defaultValue: 2,
      public: true
    };
    attributes.to = {
      description: "Upper bound (inclusive) of the prime range to select from.",
      createComponentOfType: "integer",
      createStateVariable: "to",
      defaultValue: 100,
      public: true
    };
    attributes.exclude = {
      description: "Primes to exclude from the selection.",
      createComponentOfType: "numberList",
      createStateVariable: "exclude",
      defaultValue: [],
      public: true
    };
    attributes.numToSelect = {
      description: "How many prime numbers to select.",
      createComponentOfType: "integer",
      createStateVariable: "numToSelect",
      defaultValue: 1,
      public: true
    };
    attributes.withReplacement = {
      description: "Whether the same prime can be selected more than once.",
      createComponentOfType: "boolean",
      createStateVariable: "withReplacement",
      defaultValue: false,
      public: true
    };
    attributes.sort = {
      description: "Sort order applied to the selected primes.",
      createComponentOfType: "text",
      createStateVariable: "sort",
      defaultValue: "unsorted",
      public: true,
      toLowerCase: true,
      valueForTrue: "increasing",
      valueForFalse: "unsorted",
      validValues: [
        {
          value: "unsorted",
          description: "Preserve the random selection order."
        },
        {
          value: "increasing",
          description: "Sort the selected primes in increasing order."
        },
        {
          value: "decreasing",
          description: "Sort the selected primes in decreasing order."
        }
      ]
    };
    attributes.excludeCombinations = {
      createComponentOfType: "_listOfNumberLists",
      description: "Combinations of values that should not be selected together."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.excludedCombinations = {
      returnDependencies: () => ({
        excludeCombinations: {
          dependencyType: "attributeComponent",
          attributeName: "excludeCombinations",
          variableNames: ["lists"]
        },
        numToSelect: {
          dependencyType: "stateVariable",
          variableName: "numToSelect"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.excludeCombinations !== null) {
          let excludedCombinations = dependencyValues.excludeCombinations.stateValues.lists.map(
            (x2) => x2.slice(0, dependencyValues.numToSelect)
          ).filter(
            (x2) => x2.length === dependencyValues.numToSelect
          );
          while (true) {
            let result = mergeContainingNumberCombinations(
              excludedCombinations
            );
            if (result.merged) {
              excludedCombinations = result.combinations;
            } else {
              break;
            }
          }
          return {
            setValue: { excludedCombinations }
          };
        } else {
          return { setValue: { excludedCombinations: [] } };
        }
      }
    };
    stateVariableDefinitions.variants = {
      returnDependencies: () => ({
        variants: {
          dependencyType: "variants"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { variants: dependencyValues.variants } };
      }
    };
    stateVariableDefinitions.possibleValues = {
      returnDependencies: () => ({
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        to: {
          dependencyType: "stateVariable",
          variableName: "to"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        }
      }),
      definition({ dependencyValues }) {
        let primes = createPrimesList({
          from: dependencyValues.from,
          to: dependencyValues.to,
          exclude: dependencyValues.exclude
        });
        return { setValue: { possibleValues: primes } };
      }
    };
    stateVariableDefinitions.selectedValues = {
      immutable: true,
      hasEssential: true,
      shadowVariable: true,
      additionalStateVariablesDefined: [
        {
          variableName: "errorMessage",
          hasEssential: true,
          shadowVariable: true,
          immutable: true
        }
      ],
      returnDependencies: ({ sharedParameters }) => ({
        numToSelect: {
          dependencyType: "stateVariable",
          variableName: "numToSelect"
        },
        withReplacement: {
          dependencyType: "stateVariable",
          variableName: "withReplacement"
        },
        possibleValues: {
          dependencyType: "stateVariable",
          variableName: "possibleValues"
        },
        excludedCombinations: {
          dependencyType: "stateVariable",
          variableName: "excludedCombinations"
        },
        sort: {
          dependencyType: "stateVariable",
          variableName: "sort"
        },
        variants: {
          dependencyType: "stateVariable",
          variableName: "variants"
        },
        variantRng: {
          dependencyType: "value",
          value: sharedParameters.variantRng,
          doNotProxy: true
        }
      }),
      definition: makeSelection
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: () => ({
        selectedValues: {
          dependencyType: "stateVariable",
          variableName: "selectedValues"
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          values: dependencyValues.selectedValues,
          meta: { createdBy: componentIdx }
        };
        return { setValue: { generatedVariantInfo } };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        selectedValues: {
          dependencyType: "stateVariable",
          variableName: "selectedValues"
        }
      }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let errorMessage = await component.stateValues.errorMessage;
    if (errorMessage) {
      return {
        replacements: [
          {
            type: "serialized",
            componentType: "_error",
            componentIdx: nComponents++,
            state: { message: errorMessage },
            attributes: {},
            doenetAttributes: {},
            children: []
          }
        ],
        diagnostics,
        nComponents
      };
    }
    let replacements = [];
    for (let value of await component.stateValues.selectedValues) {
      replacements.push({
        type: "serialized",
        componentType: "integer",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        state: { value, fixed: true },
        attributes: {},
        doenetAttributes: {},
        children: []
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static calculateReplacementChanges({ nComponents }) {
    return { replacementChanges: [], nComponents };
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    infoDiagnostics
  }) {
    const info = (message) => pushVariantInfo(infoDiagnostics, message, serializedComponent);
    let numToSelect = 1, withReplacement = false;
    let numToSelectComponent = serializedComponent.attributes.numToSelect?.component;
    if (numToSelectComponent) {
      if (numToSelectComponent.componentType === "integer" && numToSelectComponent.children?.length === 1 && typeof numToSelectComponent.children[0] === "string") {
        numToSelect = Number(numToSelectComponent.children[0]);
        if (!(Number.isInteger(numToSelect) && numToSelect >= 0)) {
          info(
            `cannot determine unique variants of selectPrimeNumbers as numToSelect isn't a non-negative integer.`
          );
          return { success: false };
        }
      } else {
        info(
          `cannot determine unique variants of selectPrimeNumbers as numToSelect isn't constant number.`
        );
        return { success: false };
      }
    }
    let withReplacementComponent = serializedComponent.attributes.withReplacement?.component;
    if (withReplacementComponent) {
      if (withReplacementComponent.componentType === "boolean") {
        if (withReplacementComponent.children?.length === 1 && typeof withReplacementComponent.children[0] === "string") {
          withReplacement = withReplacementComponent.children[0].toLowerCase() === "true";
        } else if ((withReplacementComponent.children === void 0 || withReplacementComponent.children.length === 0) && typeof withReplacementComponent.state?.value === "boolean") {
          withReplacement = withReplacementComponent.state.value;
        } else {
          info(
            `cannot determine unique variants of selectPrimeNumbers as withReplacement isn't constant boolean.`
          );
          return { success: false };
        }
      } else {
        info(
          `cannot determine unique variants of selectPrimeNumbers as withReplacement isn't constant boolean.`
        );
        return { success: false };
      }
    }
    let primePars = {};
    let fromComponent = serializedComponent.attributes.from?.component;
    if (fromComponent) {
      if (fromComponent.children?.length === 1 && typeof fromComponent.children[0] === "string") {
        let from = Number(fromComponent.children[0]);
        if (!Number.isFinite(from)) {
          info(
            `cannot determine unique variants of selectPrimeNumbers as from isn't a number.`
          );
          return { success: false };
        }
        primePars.from = from;
      } else {
        info(
          `cannot determine unique variants of selectPrimeNumbers as from isn't a constant.`
        );
        return { success: false };
      }
    }
    let toComponent = serializedComponent.attributes.to?.component;
    if (toComponent) {
      if (toComponent.children?.length === 1 && typeof toComponent.children[0] === "string") {
        let to2 = Number(toComponent.children[0]);
        if (!Number.isFinite(to2)) {
          info(
            `cannot determine unique variants of selectPrimeNumbers as to isn't a number.`
          );
          return { success: false };
        }
        primePars.to = to2;
      } else {
        info(
          `cannot determine unique variants of selectPrimeNumbers as to isn't a constant.`
        );
        return { success: false };
      }
    }
    if (serializedComponent.attributes.excludeCombinations) {
      info(
        "have not implemented unique variants of a selectPrimeNumbers with excludeCombinations"
      );
      return { success: false };
    }
    let excludeComponent = serializedComponent.attributes.exclude?.component;
    if (excludeComponent) {
      if (!excludeComponent.children.every(
        (x2) => x2.children?.length === 1 && typeof x2.children[0] === "string"
      )) {
        info(
          "have not implemented unique variants of a selectPrimeNumbers with non-constant exclude"
        );
        return { success: false };
      }
      let exclude = excludeComponent.children.map(
        (x2) => Number(x2.children[0])
      );
      if (!exclude.every(Number.isFinite)) {
        info(
          "have not implemented unique variants of a selectPrimeNumbers with non-constant exclude"
        );
        return { success: false };
      }
      primePars.exclude = exclude;
    }
    let sortResult = extractConstantSortAttribute(
      serializedComponent,
      "selectPrimeNumbers",
      numToSelect,
      infoDiagnostics
    );
    if (!sortResult.success) {
      return { success: false };
    }
    let primes = createPrimesList(primePars);
    if (primes.length <= 0) {
      return { success: false };
    }
    let uniqueVariantData = {
      primes,
      numToSelect,
      withReplacement
    };
    serializedComponent.variants.uniqueVariantData = uniqueVariantData;
    let numVariants;
    if (withReplacement || numToSelect === 1) {
      numVariants = Math.pow(primes.length, numToSelect);
    } else {
      numVariants = primes.length;
      for (let n2 = primes.length - 1; n2 > primes.length - numToSelect; n2--) {
        numVariants *= n2;
      }
    }
    if (!(numVariants > 0)) {
      return { success: false };
    }
    serializedComponent.variants.numVariants = numVariants;
    return {
      success: true,
      numVariants
    };
  }
  static getUniqueVariant({ serializedComponent, variantIndex }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let uniqueVariantData = serializedComponent.variants.uniqueVariantData;
    let primes = uniqueVariantData.primes;
    let numToSelect = uniqueVariantData.numToSelect;
    let withReplacement = uniqueVariantData.withReplacement;
    if (numToSelect === 1) {
      return {
        success: true,
        desiredVariant: { values: [primes[variantIndex - 1]] }
      };
    }
    let numbers = enumerateSelectionCombinations({
      numberOfIndices: numToSelect,
      numberOfOptions: primes.length,
      maxNumber: variantIndex,
      withReplacement
    })[variantIndex - 1];
    let values2 = numbers.map((x2) => primes[x2]);
    return { success: true, desiredVariant: { values: values2 } };
  }
}
function makeSelection({ dependencyValues }) {
  if (dependencyValues.numToSelect < 1) {
    return {
      setEssentialValue: {
        errorMessage: "",
        selectedValues: []
      },
      setValue: {
        errorMessage: "",
        selectedValues: []
      }
    };
  }
  let numUniqueRequired = 1;
  if (!dependencyValues.withReplacement) {
    numUniqueRequired = dependencyValues.numToSelect;
  }
  let possibleValues = dependencyValues.possibleValues;
  let numValues = possibleValues.length;
  if (numUniqueRequired > numValues) {
    let errorMessage = "Cannot select " + numUniqueRequired + " values from a list of primes of length " + numValues;
    return {
      setEssentialValue: {
        errorMessage,
        selectedValues: null
      },
      setValue: {
        errorMessage,
        selectedValues: null
      }
    };
  }
  if (dependencyValues.variants && dependencyValues.variants.desiredVariant !== void 0) {
    let desiredValues = dependencyValues.variants.desiredVariant.values;
    if (desiredValues !== void 0) {
      if (desiredValues.length !== dependencyValues.numToSelect) {
        let errorMessage = "Number of values specified for select must match number to select";
        return {
          setEssentialValue: {
            errorMessage,
            selectedValues: null
          },
          setValue: {
            errorMessage,
            selectedValues: null
          }
        };
      }
      desiredValues = desiredValues.map(Number);
      if (!desiredValues.every((x2) => possibleValues.includes(x2))) {
        let errorMessage = "All values specified for select prime number must be in the list of primes";
        return {
          setEssentialValue: {
            errorMessage,
            selectedValues: null
          },
          setValue: {
            errorMessage,
            selectedValues: null
          }
        };
      }
      if (checkForExcludedCombination({
        type: "number",
        excludedCombinations: dependencyValues.excludedCombinations,
        values: desiredValues
      })) {
        let errorMessage = "Specified values of selectPrimeNumbers was an excluded combination";
        return {
          setEssentialValue: {
            errorMessage,
            selectedValues: null
          },
          setValue: {
            errorMessage,
            selectedValues: null
          }
        };
      }
      return {
        setEssentialValue: {
          errorMessage: "",
          selectedValues: desiredValues
        },
        setValue: { errorMessage: "", selectedValues: desiredValues }
      };
    }
  }
  let numCombinationsExcluded = estimateNumberOfNumberCombinationsExcluded({
    excludedCombinations: dependencyValues.excludedCombinations,
    numValues,
    withReplacement: dependencyValues.withReplacement,
    numToSelect: dependencyValues.numToSelect
  });
  let selectedValues;
  if (numCombinationsExcluded === 0) {
    selectedValues = sampleFromNumberList({
      possibleValues,
      numUniqueRequired,
      numSamples: dependencyValues.numToSelect,
      rng: dependencyValues.variantRng
    });
  } else {
    let numPossibilities = numValues;
    if (dependencyValues.withReplacement) {
      numPossibilities = Math.pow(
        numPossibilities,
        dependencyValues.numToSelect
      );
    } else {
      let n2 = numPossibilities;
      for (let i2 = 1; i2 < dependencyValues.numToSelect; i2++) {
        numPossibilities *= n2 - i2;
      }
    }
    if (numCombinationsExcluded > 0.7 * numPossibilities) {
      if (dependencyValues.excludedCombinations.some(
        (x2) => x2.some(Number.isNaN)
      )) {
        let numberDuplicated = estimateNumberOfDuplicateCombinations(
          dependencyValues.excludedCombinations,
          numValues,
          dependencyValues.withReplacement
        );
        numCombinationsExcluded -= numberDuplicated;
        if (numCombinationsExcluded > 0.7 * numPossibilities) {
          let errorMessage = "Excluded over 70% of combinations in selectPrimeNumbers";
          return {
            setEssentialValue: {
              errorMessage,
              selectedValues: null
            },
            setValue: {
              errorMessage,
              selectedValues: null
            }
          };
        }
      } else {
        let errorMessage = "Excluded over 70% of combinations in selectPrimeNumbers";
        return {
          setEssentialValue: {
            errorMessage,
            selectedValues: null
          },
          setValue: {
            errorMessage,
            selectedValues: null
          }
        };
      }
    }
    let foundValidCombination = false;
    for (let sampnum = 0; sampnum < 200; sampnum++) {
      selectedValues = sampleFromNumberList({
        possibleValues,
        numUniqueRequired,
        numSamples: dependencyValues.numToSelect,
        rng: dependencyValues.variantRng
      });
      if (checkForExcludedCombination({
        type: "number",
        excludedCombinations: dependencyValues.excludedCombinations,
        values: selectedValues
      })) {
        continue;
      }
      foundValidCombination = true;
      break;
    }
    if (!foundValidCombination) {
      let errorMessage = "By extremely unlikely fluke, couldn't select combination of random values";
      return {
        setEssentialValue: {
          errorMessage,
          selectedValues: null
        },
        setValue: {
          errorMessage,
          selectedValues: null
        }
      };
    }
  }
  if (dependencyValues.sort === "increasing") {
    selectedValues.sort((a2, b2) => a2 - b2);
  } else if (dependencyValues.sort === "decreasing") {
    selectedValues.sort((a2, b2) => b2 - a2);
  }
  return {
    setEssentialValue: { errorMessage: "", selectedValues },
    setValue: { errorMessage: "", selectedValues }
  };
}
class SamplePrimeNumbers extends CompositeComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      resample: this.resample.bind(this)
    });
  }
  static componentType = "samplePrimeNumbers";
  static componentDocs = {
    summary: "Samples random prime numbers"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["integer"];
  static createsVariants = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static processWhenJustUpdatedForNewComponent = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numSamples = {
      description: "Number of prime samples to draw.",
      createComponentOfType: "number",
      createStateVariable: "numSamples",
      defaultValue: 1,
      public: true
    };
    attributes.from = {
      description: "Lower bound (inclusive) of the prime range to sample from.",
      createComponentOfType: "integer",
      createStateVariable: "from",
      defaultValue: 2,
      public: true
    };
    attributes.to = {
      description: "Upper bound (inclusive) of the prime range to sample from.",
      createComponentOfType: "integer",
      createStateVariable: "to",
      defaultValue: 100,
      public: true
    };
    attributes.exclude = {
      description: "Primes to exclude from the sample.",
      createComponentOfType: "numberList",
      createStateVariable: "exclude",
      defaultValue: [],
      public: true
    };
    attributes.variantDeterminesSeed = {
      description: "Whether the document's variant index determines the random seed.",
      createPrimitiveOfType: "boolean",
      createStateVariable: "variantDeterminesSeed",
      defaultPrimitiveValue: false,
      public: true
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.possibleValues = {
      returnDependencies: () => ({
        from: {
          dependencyType: "stateVariable",
          variableName: "from"
        },
        to: {
          dependencyType: "stateVariable",
          variableName: "to"
        },
        exclude: {
          dependencyType: "stateVariable",
          variableName: "exclude"
        }
      }),
      definition({ dependencyValues }) {
        let primes = createPrimesList({
          from: dependencyValues.from,
          to: dependencyValues.to,
          exclude: dependencyValues.exclude
        });
        return { setValue: { possibleValues: primes } };
      }
    };
    stateVariableDefinitions.sampledValues = {
      shadowVariable: true,
      hasEssential: true,
      stateVariablesDeterminingDependencies: ["variantDeterminesSeed"],
      returnDependencies({ stateValues, sharedParameters }) {
        let dependencies = {
          numSamples: {
            dependencyType: "stateVariable",
            variableName: "numSamples"
          },
          possibleValues: {
            dependencyType: "stateVariable",
            variableName: "possibleValues"
          }
        };
        if (stateValues.variantDeterminesSeed) {
          dependencies.rng = {
            dependencyType: "value",
            value: sharedParameters.variantRng,
            doNotProxy: true
          };
        } else {
          dependencies.rng = {
            dependencyType: "value",
            value: sharedParameters.rngWithDateSeed,
            doNotProxy: true
          };
        }
        return dependencies;
      },
      definition({
        dependencyValues,
        changes,
        justUpdatedForNewComponent
      }) {
        if (dependencyValues.numSamples < 1) {
          return {
            setEssentialValue: { sampledValues: [] },
            setValue: { sampledValues: [] }
          };
        }
        if (Object.keys(changes).length === 0 || justUpdatedForNewComponent) {
          return {
            useEssentialOrDefaultValue: { sampledValues: true }
          };
        }
        let sampledValues = sampleFromNumberList({
          possibleValues: dependencyValues.possibleValues,
          numSamples: dependencyValues.numSamples,
          rng: dependencyValues.rng
        });
        return {
          setEssentialValue: { sampledValues },
          setValue: { sampledValues }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "sampledValues",
              value: desiredStateVariableValues.sampledValues
            }
          ]
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        sampledValues: {
          dependencyType: "stateVariable",
          variableName: "sampledValues"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({ sharedParameters }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    workspace,
    startNum = 0,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let replacements = [];
    for (let value of (await component.stateValues.sampledValues).slice(
      startNum
    )) {
      replacements.push({
        type: "serialized",
        componentType: "integer",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        state: { value, fixed: true },
        attributes: {},
        doenetAttributes: {},
        children: []
      });
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    componentInfoObjects,
    flags,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    let replacementChanges = [];
    let sampledValues = await component.stateValues.sampledValues;
    if (sampledValues.length < component.replacements.length) {
      let numberToWithhold = component.replacements.length - sampledValues.length;
      if (numberToWithhold !== component.replacementsToWithhold) {
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: numberToWithhold
        };
        replacementChanges.push(replacementInstruction);
      }
    } else {
      if (component.replacementsToWithhold > 0) {
        let replacementInstruction = {
          changeType: "changeReplacementsToWithhold",
          replacementsToWithhold: 0
        };
        replacementChanges.push(replacementInstruction);
      }
      if (sampledValues.length > component.replacements.length) {
        let result = await this.createSerializedReplacements({
          component,
          componentInfoObjects,
          startNum: component.replacements.length,
          flags,
          nComponents,
          workspace
        });
        diagnostics.push(...result.diagnostics);
        nComponents = result.nComponents;
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: component.replacements.length,
          numberReplacementsToReplace: 0,
          serializedReplacements: result.replacements
        };
        replacementChanges.push(replacementInstruction);
      }
    }
    let numUpdate = Math.min(
      component.replacements.length,
      sampledValues.length
    );
    for (let ind = 0; ind < numUpdate; ind++) {
      let replacementInstruction = {
        changeType: "updateStateVariables",
        component: component.replacements[ind],
        stateChanges: { value: sampledValues[ind] }
      };
      replacementChanges.push(replacementInstruction);
    }
    return { replacementChanges, diagnostics, nComponents };
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents
    });
    let seedForRandomNumbers = sharedParameters.variantRng().toString().slice(2, 8) + +/* @__PURE__ */ new Date();
    sharedParameters.rngWithDateSeed = sharedParameters.rngClass(seedForRandomNumbers);
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let variantDeterminesSeed = serializedComponent.attributes.variantDeterminesSeed.primitive.value;
    if (variantDeterminesSeed) {
      return { success: false };
    } else {
      return super.determineNumberOfUniqueVariants({
        serializedComponent,
        componentInfoObjects,
        infoDiagnostics
      });
    }
  }
  async resample({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let sampledValues = sampleFromNumberList({
      possibleValues: await this.stateValues.possibleValues,
      numSamples: await this.stateValues.numSamples,
      rng: await this.stateValues.variantDeterminesSeed ? this.sharedParameters.variantRng : this.sharedParameters.rngWithDateSeed
    });
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "sampledValues",
          value: sampledValues
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
class Substitute extends CompositeComponent {
  static componentType = "substitute";
  static componentDocs = {
    summary: "Substitutes math expressions into another expression"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["math", "text"];
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      description: "Type of values being substituted.",
      createPrimitiveOfType: "string",
      createStateVariable: "type",
      defaultPrimitiveValue: "math",
      toLowerCase: true,
      validValues: [
        {
          value: "math",
          description: "Substitute within math expressions."
        },
        {
          value: "text",
          description: "Substitute within text values."
        }
      ],
      public: true
    };
    attributes.match = {
      createComponentOfType: "_componentWithSelectableType",
      createStateVariable: "match",
      defaultValue: null,
      description: "Pattern to match in the source value."
    };
    attributes.replacement = {
      createComponentOfType: "_componentWithSelectableType",
      createStateVariable: "replacement",
      defaultValue: null,
      description: "Value to substitute in place of each match."
    };
    attributes.simplify = {
      description: "Level of simplification applied after substitution.",
      createComponentOfType: "text",
      createStateVariable: "simplify",
      defaultValue: "none",
      public: true,
      toLowerCase: true,
      valueForTrue: "full",
      valueForFalse: "none",
      validValues: [
        {
          value: "none",
          description: "No simplification is applied."
        },
        {
          value: "full",
          description: "Fully simplify the resulting expression."
        },
        {
          value: "numbers",
          description: "Simplify numeric subexpressions only, leaving symbolic structure intact."
        },
        {
          value: "numbersPreserveOrder",
          description: "Like `numbers`, but does not reorder commutative operands."
        },
        {
          value: "normalizeOrder",
          description: "Reorder commutative operands into a canonical form without simplifying values."
        }
      ]
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    attributes.matchWholeWord = {
      description: "Whether matching is restricted to whole words (text mode).",
      createComponentOfType: "boolean",
      createStateVariable: "matchWholeWord",
      defaultValue: false,
      public: true
    };
    attributes.matchCase = {
      description: "Whether matching is case-sensitive (text mode).",
      createComponentOfType: "boolean",
      createStateVariable: "matchCase",
      defaultValue: false,
      public: true
    };
    attributes.preserveCase = {
      description: "Whether the case of the original is preserved when substituting.",
      createComponentOfType: "boolean",
      createStateVariable: "preserveCase",
      defaultValue: false,
      public: true
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = [];
    function addType({
      matchedChildren,
      componentAttributes,
      nComponents,
      stateIdInfo
    }) {
      let type = componentAttributes.type?.value;
      if (!["math", "text"].includes(type)) {
        type = "math";
      }
      return {
        success: true,
        newChildren: [
          {
            type: "serialized",
            componentType: type,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: matchedChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ],
        nComponents
      };
    }
    sugarInstructions.push({
      replacementFunction: addType
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({
      childGroupsIfSingleMatch: ["anything"]
    });
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.originalValue = {
      returnDependencies: () => ({
        child: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.child.length > 0) {
          return {
            setValue: {
              originalValue: dependencyValues.child[0].stateValues.value
            }
          };
        } else {
          return { setValue: { originalValue: null } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.child.length > 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "child",
                desiredValue: desiredStateVariableValues.originalValue,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.value = {
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        originalValue: {
          dependencyType: "stateVariable",
          variableName: "originalValue"
        },
        match: {
          dependencyType: "stateVariable",
          variableName: "match"
        },
        replacement: {
          dependencyType: "stateVariable",
          variableName: "replacement"
        },
        simplify: {
          dependencyType: "stateVariable",
          variableName: "simplify"
        },
        matchWholeWord: {
          dependencyType: "stateVariable",
          variableName: "matchWholeWord"
        },
        matchCase: {
          dependencyType: "stateVariable",
          variableName: "matchCase"
        },
        preserveCase: {
          dependencyType: "stateVariable",
          variableName: "preserveCase"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.originalValue === null) {
          return {
            setValue: { value: null }
          };
        }
        if (dependencyValues.match === null || dependencyValues.replacement === null) {
          return {
            setValue: { value: dependencyValues.originalValue }
          };
        }
        if (dependencyValues.type === "text") {
          let value = dependencyValues.originalValue;
          let re2;
          let flag = "g";
          if (!dependencyValues.matchCase) {
            flag = "gi";
          }
          if (dependencyValues.matchWholeWord) {
            re2 = new RegExp(
              "\\b" + escapeRegExp(dependencyValues.match) + "\\b",
              flag
            );
          } else {
            re2 = new RegExp(
              escapeRegExp(dependencyValues.match),
              flag
            );
          }
          let replacement = dependencyValues.replacement;
          if (dependencyValues.preserveCase) {
            let preserveCaseReplacer = returnPreserveCaseReplacer(replacement);
            value = value.replace(re2, preserveCaseReplacer);
          } else {
            value = value.replace(re2, replacement);
          }
          return {
            setValue: { value }
          };
        } else {
          let value = dependencyValues.originalValue.subscripts_to_strings();
          value = value.substitute({
            [dependencyValues.match.subscripts_to_strings().tree]: dependencyValues.replacement.subscripts_to_strings()
          });
          value = value.strings_to_subscripts();
          value = normalizeMathExpression({
            value,
            simplify: dependencyValues.simplify
          });
          return {
            setValue: { value }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.type === "text") {
          let desiredValue = desiredStateVariableValues.value;
          let reForMatch, reForReplacement;
          let flag = "g";
          if (!dependencyValues.matchCase) {
            flag = "gi";
          }
          let match = dependencyValues.match;
          let replacement = dependencyValues.replacement;
          if (dependencyValues.matchWholeWord) {
            reForMatch = new RegExp(
              "\\b" + escapeRegExp(match) + "\\b",
              flag
            );
            if (dependencyValues.matchCase && dependencyValues.preserveCase) {
              let preserveCaseReplacer = returnPreserveCaseReplacer(replacement);
              reForReplacement = new RegExp(
                "\\b" + escapeRegExp(preserveCaseReplacer(match)) + "\\b",
                flag
              );
            } else {
              reForReplacement = new RegExp(
                "\\b" + escapeRegExp(replacement) + "\\b",
                flag
              );
            }
          } else {
            reForMatch = new RegExp(escapeRegExp(match), flag);
            if (dependencyValues.matchCase && dependencyValues.preserveCase) {
              let preserveCaseReplacer = returnPreserveCaseReplacer(replacement);
              reForReplacement = new RegExp(
                escapeRegExp(preserveCaseReplacer(match)),
                flag
              );
            } else {
              reForReplacement = new RegExp(
                escapeRegExp(replacement),
                flag
              );
            }
          }
          if (dependencyValues.originalValue.search(
            reForReplacement
          ) !== -1 || desiredValue.search(reForMatch) !== -1) {
            return { success: false };
          }
          desiredValue = desiredValue.replace(
            reForReplacement,
            match
          );
          return {
            success: true,
            instructions: [
              {
                setDependency: "originalValue",
                desiredValue
              }
            ]
          };
        } else {
          let desiredValue = desiredStateVariableValues.value.subscripts_to_strings();
          let match = dependencyValues.match.subscripts_to_strings();
          let replacement = dependencyValues.replacement.subscripts_to_strings();
          if (!(typeof match.tree === "string" && typeof replacement.tree === "string")) {
            return { success: false };
          }
          let variablesOfOrig = dependencyValues.originalValue.variables(true);
          if (variablesOfOrig.includes(replacement.tree)) {
            return { success: false };
          }
          let variablesInDesired = desiredValue.variables();
          if (variablesInDesired.includes(match.tree)) {
            return { success: false };
          }
          desiredValue = desiredValue.substitute({
            [replacement.tree]: match
          });
          desiredValue = desiredValue.strings_to_subscripts();
          return {
            success: true,
            instructions: [
              {
                setDependency: "originalValue",
                desiredValue
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      // when this state variable is marked stale
      // it indicates we should update replacement
      // For this to work, must get value in replacement functions
      // so that the variable is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    let type = await component.stateValues.type;
    let serializedReplacement = {
      type: "serialized",
      componentType: type,
      componentIdx: nComponents++,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      state: { value: await component.stateValues.value },
      attributes: {},
      doenetAttributes: {},
      children: [],
      downstreamDependencies: {
        [component.componentIdx]: [
          {
            dependencyType: "referenceShadow",
            compositeIdx: component.componentIdx,
            propVariable: "value"
          }
        ]
      }
    };
    if (type === "math") {
      let attributes = {};
      let attributesComponentTypes = {};
      let roundingSVs = returnNumberDisplayStateVariableDefinitions();
      for (let attrName in roundingSVs) {
        attributesComponentTypes[attrName] = roundingSVs[attrName].shadowingInstructions.createComponentOfType;
      }
      for (let attr in attributesComponentTypes) {
        let shadowComponent = {
          type: "serialized",
          componentType: attributesComponentTypes[attr],
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          children: [],
          state: {},
          downstreamDependencies: {
            [component.componentIdx]: [
              {
                compositeIdx: component.componentIdx,
                dependencyType: "referenceShadow",
                propVariable: attr
              }
            ]
          }
        };
        attributes[attr] = {
          type: "component",
          component: shadowComponent
        };
      }
      serializedReplacement.attributes = attributes;
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements: [serializedReplacement],
      diagnostics,
      nComponents
    };
  }
}
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function returnPreserveCaseReplacer(replacement) {
  return function(match) {
    if (match.toUpperCase() === match) {
      return replacement.toUpperCase();
    } else if (match.toLowerCase() === match) {
      return replacement.toLowerCase();
    } else if (match[0].match(/[a-z]/)) {
      return replacement[0].toLowerCase() + replacement.substring(1);
    } else if (match[0].match(/[A-Z]/)) {
      return replacement[0].toUpperCase() + replacement.substring(1);
    } else {
      return replacement;
    }
  };
}
class PeriodicSet extends MathComponent {
  static componentType = "periodicSet";
  static componentDocs = {
    summary: "A periodic set of real numbers"
  };
  static rendererType = void 0;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.minIndex = {
      description: "Smallest period index considered when listing offsets.",
      createComponentOfType: "integer",
      createStateVariable: "minIndex",
      defaultValue: -Infinity,
      public: true
    };
    attributes.maxIndex = {
      description: "Largest period index considered when listing offsets.",
      createComponentOfType: "integer",
      createStateVariable: "maxIndex",
      defaultValue: Infinity,
      public: true
    };
    attributes.offsets = {
      description: "Offsets within one period.",
      createComponentOfType: "mathList",
      createStateVariable: "offsets",
      defaultValue: null,
      public: true
    };
    attributes.period = {
      description: "The period of the set.",
      createComponentOfType: "math",
      createStateVariable: "period",
      defaultValue: null,
      public: true
    };
    attributes.minIndexAsList = {
      description: "Smallest period index when rendered as a list.",
      createComponentOfType: "integer",
      createStateVariable: "minIndexAsList",
      defaultValue: -1,
      public: true
    };
    attributes.maxIndexAsList = {
      description: "Largest period index when rendered as a list.",
      createComponentOfType: "integer",
      createStateVariable: "maxIndexAsList",
      defaultValue: 1,
      public: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.codePre;
    delete stateVariableDefinitions.expressionWithCodes;
    delete stateVariableDefinitions.mathChildrenFunctionSymbols;
    delete stateVariableDefinitions.codesAdjacentToStrings;
    delete stateVariableDefinitions.mathChildrenByVectorComponent;
    delete stateVariableDefinitions.mathChildrenWithCanBeModified;
    delete stateVariableDefinitions.unordered;
    let roundingDefinitions = returnNumberDisplayStateVariableDefinitions({});
    Object.assign(stateVariableDefinitions, roundingDefinitions);
    stateVariableDefinitions.canBeModified = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { canBeModified: false } })
    };
    stateVariableDefinitions.numOffsets = {
      description: "The number of distinct offsets within one period.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        offsets: {
          dependencyType: "stateVariable",
          variableName: "offsets"
        }
      }),
      definition({ dependencyValues }) {
        let numOffsets = 0;
        if (dependencyValues.offsets !== null) {
          numOffsets = dependencyValues.offsets.length;
        }
        return { setValue: { numOffsets } };
      }
    };
    stateVariableDefinitions.unnormalizedValue = {
      returnDependencies: () => ({
        offsets: {
          dependencyType: "stateVariable",
          variableName: "offsets"
        },
        period: {
          dependencyType: "stateVariable",
          variableName: "period"
        },
        minIndex: {
          dependencyType: "stateVariable",
          variableName: "minIndex"
        },
        maxIndex: {
          dependencyType: "stateVariable",
          variableName: "maxIndex"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.offsets === null || dependencyValues.offsets.length === 0 || dependencyValues.offsets.length === 1 && dependencyValues.offsets[0].tree === "＿" || dependencyValues.period === null || dependencyValues.period.tree === "＿") {
          return {
            setValue: { unnormalizedValue: me$3.fromAst("＿") }
          };
        }
        let periodicInfo = [];
        let period = dependencyValues.period.tree;
        let minIndex = dependencyValues.minIndex;
        let maxIndex = dependencyValues.maxIndex;
        for (let offset of dependencyValues.offsets) {
          periodicInfo.push([
            "tuple",
            offset.tree,
            period,
            minIndex,
            maxIndex
          ]);
        }
        let unnormalizedValue = me$3.fromAst([
          "periodic_set",
          ...periodicInfo
        ]);
        return { setValue: { unnormalizedValue } };
      }
    };
    stateVariableDefinitions.redundantOffsets = {
      description: "Offsets that are redundant given the period.",
      additionalStateVariablesDefined: ["uniqueOffsets"],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        numOffsets: {
          dependencyType: "stateVariable",
          variableName: "numOffsets"
        },
        offsets: {
          dependencyType: "stateVariable",
          variableName: "offsets"
        },
        period: {
          dependencyType: "stateVariable",
          variableName: "period"
        }
      }),
      definition({ dependencyValues }) {
        let redundantOffsets = false;
        let uniqueOffsets = [];
        if (dependencyValues.period !== null) {
          let periodValue = dependencyValues.period.evaluate_to_constant();
          if (!Number.isNaN(periodValue)) {
            for (let ind1 = 0; ind1 < dependencyValues.numOffsets; ind1++) {
              let isUnique = true;
              for (let ind2 = 0; ind2 < ind1; ind2++) {
                let offsetDiff = dependencyValues.offsets[ind1].subtract(dependencyValues.offsets[ind2]).evaluate_to_constant();
                if (Math.abs(offsetDiff % periodValue) < 1e-10 * periodValue) {
                  redundantOffsets = true;
                  isUnique = false;
                  break;
                }
              }
              if (isUnique) {
                uniqueOffsets.push(
                  dependencyValues.offsets[ind1]
                );
              }
            }
          }
        }
        return { setValue: { redundantOffsets, uniqueOffsets } };
      }
    };
    stateVariableDefinitions.asList = {
      description: "The periodic set rendered as a list of explicit values.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "mathList",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        uniqueOffsets: {
          dependencyType: "stateVariable",
          variableName: "uniqueOffsets"
        },
        period: {
          dependencyType: "stateVariable",
          variableName: "period"
        },
        minIndexAsList: {
          dependencyType: "stateVariable",
          variableName: "minIndexAsList"
        },
        maxIndexAsList: {
          dependencyType: "stateVariable",
          variableName: "maxIndexAsList"
        }
      }),
      definition({ dependencyValues }) {
        let asList = [];
        if (dependencyValues.period !== null) {
          let periodValue = dependencyValues.period.evaluate_to_constant();
          if (!Number.isNaN(periodValue)) {
            let period = dependencyValues.period.simplify();
            let allFinite = true;
            let shiftedOffsetsWithNumeric = [];
            for (let offset of dependencyValues.uniqueOffsets) {
              let offsetValue = offset.evaluate_to_constant();
              if (Number.isNaN(offsetValue)) {
                allFinite = false;
                break;
              } else {
                let shiftedOffset = offset.subtract(
                  period.multiply(
                    Math.floor(
                      offsetValue / periodValue
                    )
                  )
                ).simplify();
                let shiftedOffsetValue = shiftedOffset.evaluate_to_constant();
                shiftedOffsetsWithNumeric.push({
                  num: shiftedOffsetValue,
                  offset: shiftedOffset
                });
              }
            }
            if (allFinite) {
              asList.push(me$3.fromAst(["ldots"]));
              shiftedOffsetsWithNumeric.sort(
                (a2, b2) => a2.num - b2.num
              );
              let minIndex = -1, maxIndex = 1;
              if (Number.isFinite(dependencyValues.minIndexAsList)) {
                minIndex = dependencyValues.minIndexAsList;
              }
              if (Number.isFinite(dependencyValues.maxIndexAsList)) {
                maxIndex = dependencyValues.maxIndexAsList;
              }
              for (let i2 = minIndex; i2 <= maxIndex; i2++) {
                for (let offsetObj of shiftedOffsetsWithNumeric) {
                  asList.push(
                    offsetObj.offset.add(period.multiply(i2)).simplify()
                  );
                }
              }
              asList.push(me$3.fromAst(["ldots"]));
            }
          }
        }
        return { setValue: { asList } };
      }
    };
    return stateVariableDefinitions;
  }
}
class Image extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      moveImage: this.moveImage.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this),
      imageClicked: this.imageClicked.bind(this),
      imageFocused: this.imageFocused.bind(this)
    });
  }
  static componentType = "image";
  static componentDocs = {
    summary: "Displays a static image"
  };
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      createComponentOfType: "componentSize",
      createStateVariable: "specifiedWidth",
      defaultValue: null,
      description: "Explicit width of the image (overrides size)."
    };
    attributes.size = {
      createComponentOfType: "text",
      createStateVariable: "specifiedSize",
      defaultValue: "medium",
      toLowerCase: true,
      validValues: [
        { value: "tiny", description: "About 1/12 the full width." },
        { value: "small", description: "About 30% of the full width." },
        { value: "medium", description: "About half the full width." },
        { value: "large", description: "About 70% of the full width." },
        { value: "full", description: "The full available width." }
      ],
      description: "Named size preset for the image."
    };
    attributes.aspectRatio = {
      createComponentOfType: "number",
      description: "Aspect ratio (width / height) for the image."
    };
    attributes.displayMode = {
      description: "How to size the image.",
      createComponentOfType: "text",
      createStateVariable: "displayMode",
      toLowerCase: true,
      validValues: [
        {
          value: "block",
          description: "Display as a block element on its own line."
        },
        {
          value: "inline",
          description: "Render inline with surrounding text."
        }
      ],
      defaultValue: "block",
      forRenderer: true,
      public: true
    };
    attributes.horizontalAlign = {
      description: "Horizontal alignment of the image within its container.",
      createComponentOfType: "text",
      createStateVariable: "horizontalAlign",
      toLowerCase: true,
      validValues: [
        {
          value: "center",
          description: "Center the image horizontally."
        },
        {
          value: "left",
          description: "Align the image to the left edge."
        },
        {
          value: "right",
          description: "Align the image to the right edge."
        }
      ],
      defaultValue: "center",
      forRenderer: true,
      public: true
    };
    attributes.decorative = {
      description: "Whether the image is purely decorative.",
      createPrimitiveOfType: "boolean",
      createStateVariable: "decorative",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    attributes.source = {
      description: "URL or path of the image source.",
      createComponentOfType: "text",
      createStateVariable: "source",
      defaultValue: "",
      public: true,
      forRenderer: true
    };
    attributes.authorName = {
      description: "Name of the author of the image.",
      createComponentOfType: "text",
      createStateVariable: "authorName",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.authorUrl = {
      description: "URL to link the author's name to in the attribution (e.g. the author's profile page).",
      createComponentOfType: "text",
      createStateVariable: "authorUrl",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.imageName = {
      description: "Name of the image, used in place of the generic word “Image” in the attribution.",
      createComponentOfType: "text",
      createStateVariable: "imageName",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.originalUrl = {
      description: "Original URL where the image can be found, used to link the image name in the attribution.",
      createComponentOfType: "text",
      createStateVariable: "originalUrl",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.licenseName = {
      description: "Name of the license for the image.",
      createComponentOfType: "text",
      createStateVariable: "licenseNamePreliminary",
      defaultValue: null
    };
    attributes.licenseUrl = {
      description: "URL for the license of the image.",
      createComponentOfType: "text",
      createStateVariable: "licenseUrlPreliminary",
      defaultValue: null
    };
    attributes.licenseCodes = {
      description: "License code(s) for the image. Specify two codes to indicate the image is dual licensed.",
      createComponentOfType: "textList",
      createStateVariable: "licenseCodes",
      defaultValue: null,
      toLowerCase: true,
      validValues: mediaLicenses.map((license) => ({
        value: license.code,
        description: license.description
      })),
      public: true,
      forRenderer: true
    };
    attributes.licenseVersion = {
      description: "Version of the Creative Commons license(s) given in `licenseCodes` (ignored by non-Creative-Commons licenses).",
      createComponentOfType: "text",
      createStateVariable: "licenseVersion",
      defaultValue: defaultCreativeCommonsVersion,
      toLowerCase: true,
      validValues: creativeCommonsVersions.map((version) => ({
        value: version,
        description: `Creative Commons version ${version}.`
      })),
      public: true,
      forRenderer: true
    };
    attributes.asFileName = {
      description: "File name to use when downloading the image.",
      createComponentOfType: "text",
      createStateVariable: "asFileName",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.mimeType = {
      description: "MIME type of the image.",
      createComponentOfType: "text",
      createStateVariable: "mimeType",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.draggable = {
      description: "Whether the image can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    attributes.layer = {
      description: "Z-order layer index when shown on a graph.",
      createComponentOfType: "number",
      createStateVariable: "layer",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    attributes.rotate = {
      description: "Rotation angle (radians) applied to the image.",
      createComponentOfType: "number",
      createStateVariable: "rotate",
      defaultValue: 0,
      public: true,
      forRenderer: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnListItemChildStateVariableDefinitions({
        listItemInlineAlignment: "flex-start"
      })
    );
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.shortDescription = {
      description: "A short accessibility description of the image.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        shortDescriptionChild: {
          dependencyType: "child",
          childGroups: ["shortDescriptions"],
          variableNames: ["text"]
        },
        decorative: {
          dependencyType: "stateVariable",
          variableName: "decorative"
        }
      }),
      definition({ dependencyValues }) {
        let shortDescription = "";
        const diagnostics = [];
        if (dependencyValues.shortDescriptionChild.length > 0) {
          const shortDescriptionChild = dependencyValues.shortDescriptionChild[dependencyValues.shortDescriptionChild.length - 1];
          shortDescription = shortDescriptionChild.stateValues.text.trim();
        }
        if (shortDescription === "" && !dependencyValues.decorative) {
          diagnostics.push({
            type: "accessibility",
            level: 1,
            message: "For accessibility, `<image>` must either have a short description or be specified as decorative."
          });
        }
        return {
          setValue: { shortDescription },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        const descriptionIdx = dependencyValues.allChildren.findLastIndex(
          (child) => child.componentType === "description"
        );
        const childIndicesToRender = descriptionIdx === -1 ? [] : [descriptionIdx];
        return { setValue: { childIndicesToRender } };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.size = {
      description: "The size of the image.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        specifiedSize: {
          dependencyType: "stateVariable",
          variableName: "specifiedSize"
        },
        specifiedWidth: {
          dependencyType: "stateVariable",
          variableName: "specifiedWidth"
        },
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["xScale"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        const defaultSize = "medium";
        if (!usedDefault.specifiedSize) {
          return {
            setValue: { size: dependencyValues.specifiedSize }
          };
        } else if (!usedDefault.specifiedWidth) {
          let componentSize = dependencyValues.specifiedWidth;
          if (componentSize === null) {
            return {
              setValue: { size: defaultSize }
            };
          }
          let { isAbsolute, size: widthSize } = componentSize;
          let size;
          if (isAbsolute) {
            let midpoints;
            if (dependencyValues.graphAncestor) {
              let xscale = dependencyValues.graphAncestor.stateValues.xScale;
              midpoints = orderedPercentWidthMidpoints.map(
                (x2) => x2 / 100 * xscale
              );
            } else {
              midpoints = orderedWidthMidpoints;
            }
            for (let [ind, pixels] of midpoints.entries()) {
              if (widthSize <= pixels) {
                size = sizePossibilities[ind];
                break;
              }
            }
            if (!size) {
              size = defaultSize;
            }
          } else {
            for (let [
              ind,
              percent
            ] of orderedPercentWidthMidpoints.entries()) {
              if (widthSize <= percent) {
                size = sizePossibilities[ind];
                break;
              }
            }
            if (!size) {
              size = defaultSize;
            }
          }
          return {
            setValue: { size }
          };
        } else {
          return {
            setValue: { size: defaultSize }
          };
        }
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "specifiedSize",
              desiredValue: desiredStateVariableValues.size
            }
          ]
        };
      }
    };
    stateVariableDefinitions.width = {
      description: "The display width of the image.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      returnDependencies: () => ({
        specifiedSize: {
          dependencyType: "stateVariable",
          variableName: "specifiedSize"
        },
        size: {
          dependencyType: "stateVariable",
          variableName: "size"
        },
        specifiedWidth: {
          dependencyType: "stateVariable",
          variableName: "specifiedWidth"
        },
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["xScale"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        const defaultSize = "medium";
        if (dependencyValues.graphAncestor) {
          dependencyValues.graphAncestor.stateValues.xScale;
          if (!usedDefault.specifiedSize) {
            return {
              setValue: {
                // width: { isAbsolute: true, size: percentWidthsBySize[dependencyValues.size] / 100 * xscale }
                width: {
                  isAbsolute: false,
                  size: percentWidthsBySize[dependencyValues.size]
                }
              }
            };
          } else if (!usedDefault.specifiedWidth) {
            let componentSize = dependencyValues.specifiedWidth;
            let width;
            if (componentSize) {
              width = componentSize;
            } else {
              width = {
                isAbsolute: false,
                size: percentWidthsBySize[defaultSize]
              };
            }
            return {
              setValue: { width }
            };
          } else {
            return {
              setValue: {
                // width: { isAbsolute: true, size: percentWidthsBySize[defaultSize] / 100 * xscale }
                width: {
                  isAbsolute: false,
                  size: percentWidthsBySize[defaultSize]
                }
              }
            };
          }
        } else {
          let width = {
            isAbsolute: true,
            size: widthsBySize[dependencyValues.size]
          };
          return {
            setValue: { width }
          };
        }
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "specifiedWidth",
              desiredValue: desiredStateVariableValues.width
            }
          ]
        };
      }
    };
    stateVariableDefinitions.widthForGraph = {
      forRenderer: true,
      returnDependencies: () => ({
        width: {
          dependencyType: "stateVariable",
          variableName: "width"
        },
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["xScale"]
        }
      }),
      definition({ dependencyValues }) {
        let widthForGraph = dependencyValues.width;
        if (dependencyValues.graphAncestor && !widthForGraph.isAbsolute) {
          widthForGraph = {
            isAbsolute: true,
            size: widthForGraph.size / 100 * dependencyValues.graphAncestor.stateValues.xScale
          };
        }
        return { setValue: { widthForGraph } };
      }
    };
    stateVariableDefinitions.aspectRatio = {
      description: "The aspect ratio (width / height) of the image.",
      public: true,
      forRenderer: true,
      hasEssential: true,
      defaultValue: null,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        aspectRatioAttr: {
          dependencyType: "attributeComponent",
          attributeName: "aspectRatio",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.aspectRatioAttr !== null) {
          let aspectRatio = dependencyValues.aspectRatioAttr.stateValues.value;
          if (!Number.isFinite(aspectRatio)) {
            aspectRatio = null;
          }
          return {
            setValue: { aspectRatio }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { aspectRatio: true }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.aspectRatioAttr !== null) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "aspectRatioAttr",
                desiredValue: desiredStateVariableValues.aspectRatio,
                variableIndex: 0
              }
            ]
          };
        } else {
          let aspectRatio = desiredStateVariableValues.aspectRatio;
          if (!(aspectRatio > 0)) {
            aspectRatio = null;
          }
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "aspectRatio",
                value: aspectRatio
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.licenseNames = {
      description: "The license name(s) for the image. Derived from `licenseCodes` when given; otherwise from the `licenseName` attribute.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "textList"
      },
      returnDependencies: () => ({
        licenseCodes: {
          dependencyType: "stateVariable",
          variableName: "licenseCodes"
        },
        licenseNamePreliminary: {
          dependencyType: "stateVariable",
          variableName: "licenseNamePreliminary"
        }
      }),
      definition({ dependencyValues }) {
        const { licenseCodes, licenseNamePreliminary } = dependencyValues;
        if (licenseCodes && licenseCodes.length > 0) {
          const licenseNames = [];
          for (const code of licenseCodes) {
            const info = getMediaLicenseInfo(code);
            if (info) {
              licenseNames.push(info.name);
            }
          }
          return { setValue: { licenseNames } };
        }
        if (licenseNamePreliminary !== null) {
          return {
            setValue: { licenseNames: [licenseNamePreliminary] }
          };
        }
        return { setValue: { licenseNames: [] } };
      }
    };
    stateVariableDefinitions.licenseUrls = {
      description: "The license URL(s) for the image. Derived from `licenseCodes` (and `licenseVersion`) when given; otherwise from the `licenseUrl` attribute.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "textList"
      },
      returnDependencies: () => ({
        licenseCodes: {
          dependencyType: "stateVariable",
          variableName: "licenseCodes"
        },
        licenseVersion: {
          dependencyType: "stateVariable",
          variableName: "licenseVersion"
        },
        licenseNamePreliminary: {
          dependencyType: "stateVariable",
          variableName: "licenseNamePreliminary"
        },
        licenseUrlPreliminary: {
          dependencyType: "stateVariable",
          variableName: "licenseUrlPreliminary"
        }
      }),
      definition({ dependencyValues }) {
        const {
          licenseCodes,
          licenseVersion,
          licenseNamePreliminary,
          licenseUrlPreliminary
        } = dependencyValues;
        if (licenseCodes && licenseCodes.length > 0) {
          const licenseUrls = [];
          for (const code of licenseCodes) {
            const info = getMediaLicenseInfo(code);
            if (info) {
              licenseUrls.push(info.url(licenseVersion));
            }
          }
          return { setValue: { licenseUrls } };
        }
        if (licenseNamePreliminary !== null) {
          return {
            setValue: {
              licenseUrls: [licenseUrlPreliminary ?? ""]
            }
          };
        }
        return { setValue: { licenseUrls: [] } };
      }
    };
    stateVariableDefinitions.imageId = {
      forRenderer: true,
      returnDependencies: () => ({
        source: {
          dependencyType: "stateVariable",
          variableName: "source"
        }
      }),
      definition: function({ dependencyValues }) {
        const result = dependencyValues.source.trim().match(/^doenet:([a-zA-Z0-9]+)$/i);
        return { setValue: { imageId: result ? result[1] : null } };
      }
    };
    return stateVariableDefinitions;
  }
  async moveImage({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
  async imageClicked({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "click",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async imageFocused({
    actionId,
    componentIdx,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (!await this.stateValues.fixed) {
      await this.coreFunctions.triggerChainedActions({
        triggeringAction: "focus",
        componentIdx,
        // use componentIdx rather than this.componentIdx to get original componentIdx if adapted
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
}
class Video extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVideoStarted: this.recordVideoStarted.bind(this),
      recordVideoWatched: this.recordVideoWatched.bind(this),
      recordVideoPaused: this.recordVideoPaused.bind(this),
      recordVideoSkipped: this.recordVideoSkipped.bind(this),
      recordVideoCompleted: this.recordVideoCompleted.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this),
      recordVideoReady: this.recordVideoReady.bind(this),
      playVideo: this.playVideo.bind(this),
      pauseVideo: this.pauseVideo.bind(this),
      setTime: this.setTime.bind(this)
    });
  }
  static componentType = "video";
  static componentDocs = {
    summary: "Embeds a video player"
  };
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      createComponentOfType: "componentSize",
      description: "Explicit width of the video player (overrides size)."
    };
    attributes.size = {
      createComponentOfType: "text",
      createStateVariable: "specifiedSize",
      defaultValue: "full",
      toLowerCase: true,
      validValues: [
        { value: "tiny", description: "About 1/12 the full width." },
        { value: "small", description: "About 30% of the full width." },
        { value: "medium", description: "About half the full width." },
        { value: "large", description: "About 70% of the full width." },
        { value: "full", description: "The full available width." }
      ],
      description: "Named size preset for the video player."
    };
    attributes.aspectRatio = {
      createComponentOfType: "number",
      description: "Aspect ratio (width / height) for the video player."
    };
    attributes.displayMode = {
      description: "How to size the video player.",
      createComponentOfType: "text",
      createStateVariable: "displayMode",
      toLowerCase: true,
      validValues: [
        {
          value: "block",
          description: "Display as a block element on its own line."
        },
        {
          value: "inline",
          description: "Render inline with surrounding text."
        }
      ],
      defaultValue: "block",
      forRenderer: true,
      public: true
    };
    attributes.horizontalAlign = {
      description: "Horizontal alignment of the video within its container.",
      createComponentOfType: "text",
      createStateVariable: "horizontalAlign",
      toLowerCase: true,
      validValues: [
        {
          value: "center",
          description: "Center the video horizontally."
        },
        {
          value: "left",
          description: "Align the video to the left edge."
        },
        {
          value: "right",
          description: "Align the video to the right edge."
        }
      ],
      defaultValue: "center",
      forRenderer: true,
      public: true
    };
    attributes.youtube = {
      description: "YouTube video ID to embed.",
      createComponentOfType: "text",
      createStateVariable: "youtube",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.source = {
      description: "URL of the video source.",
      createComponentOfType: "text",
      createStateVariable: "source",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "shortDescriptions",
        componentTypes: ["shortDescription"]
      },
      {
        group: "descriptions",
        componentTypes: ["description"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnListItemChildStateVariableDefinitions({
        listItemInlineAlignment: "flex-start"
      })
    );
    stateVariableDefinitions.shortDescription = {
      description: "A short accessibility description of the video.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        shortDescriptionChild: {
          dependencyType: "child",
          childGroups: ["shortDescriptions"],
          variableNames: ["text"]
        }
      }),
      definition({ dependencyValues }) {
        let shortDescription = "";
        const diagnostics = [];
        if (dependencyValues.shortDescriptionChild.length > 0) {
          const shortDescriptionChild = dependencyValues.shortDescriptionChild[dependencyValues.shortDescriptionChild.length - 1];
          shortDescription = shortDescriptionChild.stateValues.text.trim();
        }
        if (shortDescription === "") {
          diagnostics.push({
            type: "accessibility",
            level: 1,
            message: "For accessibility, `<video>` must have a short description."
          });
        }
        return {
          setValue: { shortDescription },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          includeAllChildren: true
        }
      }),
      definition({ dependencyValues }) {
        const descriptionIdx = dependencyValues.allChildren.findLastIndex(
          (child) => child.componentType === "description"
        );
        const childIndicesToRender = descriptionIdx === -1 ? [] : [descriptionIdx];
        return { setValue: { childIndicesToRender } };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.size = {
      description: "The size of the video player.",
      public: true,
      defaultValue: "full",
      hasEssential: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        specifiedSize: {
          dependencyType: "stateVariable",
          variableName: "specifiedSize"
        },
        widthAttr: {
          dependencyType: "attributeComponent",
          attributeName: "width",
          variableNames: ["componentSize"]
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        const defaultSize = "full";
        if (!usedDefault.specifiedSize) {
          return {
            setValue: { size: dependencyValues.specifiedSize }
          };
        } else if (dependencyValues.widthAttr) {
          let componentSize = dependencyValues.widthAttr.stateValues.componentSize;
          if (componentSize === null) {
            return {
              setValue: { size: defaultSize }
            };
          }
          let { isAbsolute, size: widthSize } = componentSize;
          let size;
          if (isAbsolute) {
            for (let [
              ind,
              pixels
            ] of orderedWidthMidpoints.entries()) {
              if (widthSize <= pixels) {
                size = sizePossibilities[ind];
                break;
              }
            }
            if (!size) {
              size = defaultSize;
            }
          } else {
            for (let [
              ind,
              percent
            ] of orderedPercentWidthMidpoints.entries()) {
              if (widthSize <= percent) {
                size = sizePossibilities[ind];
                break;
              }
            }
            if (!size) {
              size = defaultSize;
            }
          }
          return {
            setValue: { size }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { size: true }
          };
        }
      }
    };
    stateVariableDefinitions.width = {
      description: "The display width of the video player.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      returnDependencies: () => ({
        size: {
          dependencyType: "stateVariable",
          variableName: "size"
        }
      }),
      definition({ dependencyValues }) {
        let width = {
          isAbsolute: true,
          size: widthsBySize[dependencyValues.size]
        };
        return {
          setValue: { width }
        };
      }
    };
    stateVariableDefinitions.aspectRatio = {
      description: "The aspect ratio (width / height) of the video player.",
      public: true,
      forRenderer: true,
      defaultValue: "16 / 9",
      hasEssential: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        aspectRatioAttr: {
          dependencyType: "attributeComponent",
          attributeName: "aspectRatio",
          variableNames: ["value"]
        },
        width: {
          dependencyType: "stateVariable",
          variableName: "width"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.aspectRatioAttr !== null) {
          let aspectRatio = dependencyValues.aspectRatioAttr.stateValues.value;
          if (!Number.isFinite(aspectRatio)) {
            aspectRatio = 1;
          }
          return {
            setValue: { aspectRatio }
          };
        } else {
          return {
            useEssentialOrDefaultValue: { aspectRatio: true }
          };
        }
      }
    };
    stateVariableDefinitions.state = {
      description: "The video's current playback state (e.g. playing, paused).",
      hasEssential: true,
      defaultValue: "initializing",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      ignoreFixed: true,
      returnDependencies: () => ({}),
      definition() {
        return { useEssentialOrDefaultValue: { state: true } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let desiredState = desiredStateVariableValues.state.toLowerCase();
        let validValues = ["stopped", "playing"];
        if (!validValues.includes(desiredState)) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "state",
              value: desiredState
            }
          ]
        };
      }
    };
    stateVariableDefinitions.time = {
      description: "The current playback time in seconds.",
      hasEssential: true,
      defaultValue: 0,
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      ignoreFixed: true,
      returnDependencies: () => ({}),
      definition() {
        return { useEssentialOrDefaultValue: { time: true } };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        let desiredTime = desiredStateVariableValues.time;
        if (!(desiredTime >= 0)) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "time",
              value: desiredTime
            }
          ]
        };
      }
    };
    stateVariableDefinitions.duration = {
      description: "The total duration of the video in seconds.",
      hasEssential: true,
      defaultValue: null,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      ignoreFixed: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { duration: true }
      }),
      inverseDefinition: ({ desiredStateVariableValues }) => ({
        success: true,
        instructions: [
          {
            setEssentialValue: "duration",
            value: desiredStateVariableValues.duration
          }
        ]
      })
    };
    stateVariableDefinitions.segmentsWatched = {
      hasEssential: true,
      defaultValue: null,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: { segmentsWatched: true }
      }),
      inverseDefinition: ({ desiredStateVariableValues }) => ({
        success: true,
        instructions: [
          {
            setEssentialValue: "segmentsWatched",
            value: desiredStateVariableValues.segmentsWatched
          }
        ]
      })
    };
    stateVariableDefinitions.secondsWatched = {
      description: "Total seconds the viewer has watched.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        segmentsWatched: {
          dependencyType: "stateVariable",
          variableName: "segmentsWatched"
        }
      }),
      definition({ dependencyValues }) {
        let secondsWatched = 0;
        if (dependencyValues.segmentsWatched) {
          secondsWatched = dependencyValues.segmentsWatched.reduce(
            (a2, c2) => a2 + c2[1] - c2[0],
            0
          );
        }
        return { setValue: { secondsWatched } };
      }
    };
    stateVariableDefinitions.fractionWatched = {
      description: "Fraction of the video the viewer has watched (0 to 1).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        secondsWatched: {
          dependencyType: "stateVariable",
          variableName: "secondsWatched"
        },
        duration: {
          dependencyType: "stateVariable",
          variableName: "duration"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            fractionWatched: dependencyValues.secondsWatched / dependencyValues.duration
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async recordVideoStarted({
    beginTime,
    duration,
    rate,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    this.coreFunctions.requestRecordEvent({
      verb: "played",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType,
        duration
      },
      context: {
        startingPoint: beginTime,
        rate
      }
    });
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "state",
          value: "playing"
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
  async recordVideoWatched({
    beginTime,
    endTime,
    duration,
    rates,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    this.coreFunctions.requestRecordEvent({
      verb: "watched",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType,
        duration
      },
      context: {
        startingPoint: beginTime,
        endingPoint: endTime,
        rates
      }
    });
    let previousSegments = await this.stateValues.segmentsWatched;
    let segmentsWatched;
    if (beginTime < 1) {
      beginTime = 0;
    } else if (endTime > duration - 2) {
      endTime = duration;
    }
    if (!previousSegments) {
      segmentsWatched = [[beginTime, endTime]];
    } else {
      segmentsWatched = [];
      let addedNew = false;
      for (let [ind, seg] of previousSegments.entries()) {
        if (endTime < seg[0] - 1) {
          segmentsWatched.push([beginTime, endTime]);
          segmentsWatched.push(...previousSegments.slice(ind));
          addedNew = true;
          break;
        } else if (beginTime > seg[1] + 1) {
          segmentsWatched.push(seg);
          continue;
        }
        beginTime = Math.min(seg[0], beginTime);
        endTime = Math.max(seg[1], endTime);
      }
      if (!addedNew) {
        segmentsWatched.push([beginTime, endTime]);
      }
    }
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "segmentsWatched",
          value: segmentsWatched
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate: true,
      canSkipUpdatingRenderer: true
    });
    return await this.coreFunctions.triggerChainedActions({
      componentIdx: this.componentIdx,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async recordVideoPaused({
    endTime,
    duration,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    this.coreFunctions.requestRecordEvent({
      verb: "paused",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType,
        duration
      },
      context: {
        endingPoint: endTime
      }
    });
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "state",
          value: "stopped"
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
  recordVideoSkipped({ beginTime, endTime, duration }) {
    this.coreFunctions.requestRecordEvent({
      verb: "skipped",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType,
        duration
      },
      context: {
        startingPoint: beginTime,
        endingPoint: endTime
      }
    });
  }
  async recordVideoCompleted({
    duration,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    this.coreFunctions.requestRecordEvent({
      verb: "completed",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType,
        duration
      }
    });
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "state",
          value: "stopped"
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
  async recordVideoReady({
    duration,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "state",
          value: "stopped"
        },
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "duration",
          value: duration
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true,
      doNotSave: true
      // video actions don't count as changing doc state
    });
  }
  async playVideo({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "state",
          value: "playing"
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
  async pauseVideo({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "state",
          value: "stopped"
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
  async setTime({
    time,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "time",
          value: time
        }
      ],
      actionId,
      sourceInformation,
      skipRendererUpdate,
      overrideReadOnly: true
    });
  }
}
class Embed extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "embed";
  static componentDocs = {
    summary: "Embeds external content (e.g. an iframe) into the document."
  };
  static excludeFromSchema = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.width = {
      description: "Display width of the embedded content.",
      createComponentOfType: "componentSize",
      createStateVariable: "width",
      defaultValue: { size: 500, isAbsolute: true },
      public: true,
      forRenderer: true
    };
    attributes.height = {
      description: "Display height of the embedded content.",
      createComponentOfType: "componentSize",
      createStateVariable: "height",
      defaultValue: { size: 500, isAbsolute: true },
      public: true,
      forRenderer: true
    };
    attributes.geogebra = {
      description: "Identifier of a GeoGebra activity to embed.",
      createComponentOfType: "text",
      createStateVariable: "geogebra",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.encodedGeogebraContent = {
      description: "Base64-encoded GeoGebra worksheet content.",
      createComponentOfType: "text",
      createStateVariable: "encodedGeogebraContent",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    attributes.fromMathInsight = {
      description: "Identifier of a MathInsight resource to embed.",
      createComponentOfType: "text",
      createStateVariable: "fromMathInsight",
      defaultValue: null,
      public: true,
      forRenderer: true
    };
    return attributes;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Hint extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      revealHint: this.revealHint.bind(this),
      closeHint: this.closeHint.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "hint";
  static componentDocs = {
    summary: "Hint shown on demand to help with an exercise"
  };
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static includeBlankStringChildren = true;
  static returnChildGroups() {
    return [
      {
        group: "titles",
        componentTypes: ["title"]
      },
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.showHints = {
      forRenderer: true,
      returnDependencies: () => ({
        hide: {
          dependencyType: "stateVariable",
          variableName: "hide"
        },
        showHintsFlag: {
          dependencyType: "flag",
          flagName: "showHints"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            showHints: dependencyValues.showHintsFlag && !dependencyValues.hide
          }
        };
      }
    };
    stateVariableDefinitions.open = {
      description: "Whether the hint is currently open (revealed).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      defaultValue: false,
      hasEssential: true,
      doNotShadowEssential: true,
      returnDependencies: () => ({}),
      definition() {
        return {
          useEssentialOrDefaultValue: {
            open: true
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "open",
              value: desiredStateVariableValues.open
            }
          ]
        };
      }
    };
    stateVariableDefinitions.titleChildName = {
      forRenderer: true,
      returnDependencies: () => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["titles"]
        }
      }),
      definition({ dependencyValues }) {
        let titleChildName = null;
        if (dependencyValues.titleChild.length > 0) {
          titleChildName = dependencyValues.titleChild[dependencyValues.titleChild.length - 1].componentIdx;
        }
        return {
          setValue: { titleChildName }
        };
      }
    };
    stateVariableDefinitions.childIndicesToRender = {
      returnDependencies: () => ({
        titleChildren: {
          dependencyType: "child",
          childGroups: ["titles"]
        },
        allChildren: {
          dependencyType: "child",
          childGroups: ["anything", "titles"]
        },
        titleChildName: {
          dependencyType: "stateVariable",
          variableName: "titleChildName"
        }
      }),
      definition({ dependencyValues }) {
        let childIndicesToRender = [];
        let allTitleChildNames = dependencyValues.titleChildren.map(
          (x2) => x2.componentIdx
        );
        for (let [
          ind,
          child
        ] of dependencyValues.allChildren.entries()) {
          if (typeof child !== "object" || !allTitleChildNames.includes(child.componentIdx) || child.componentIdx === dependencyValues.titleChildName) {
            childIndicesToRender.push(ind);
          }
        }
        return { setValue: { childIndicesToRender } };
      },
      markStale: () => ({ updateRenderedChildren: true })
    };
    stateVariableDefinitions.title = {
      description: "The hint's title text.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        titleChild: {
          dependencyType: "child",
          childGroups: ["titles"],
          variableNames: ["text"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.titleChild.length === 0) {
          return { setValue: { title: "Hint" } };
        } else {
          return {
            setValue: {
              title: dependencyValues.titleChild[dependencyValues.titleChild.length - 1].stateValues.text
            }
          };
        }
      }
    };
    return stateVariableDefinitions;
  }
  async revealHint({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "open",
          value: true
        }
      ],
      overrideReadOnly: true,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "viewed",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        }
      }
    });
  }
  async closeHint({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await this.coreFunctions.performUpdate({
      updateInstructions: [
        {
          updateType: "updateValue",
          componentIdx: this.componentIdx,
          stateVariable: "open",
          value: false
        }
      ],
      overrideReadOnly: true,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "closed",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        }
      }
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class IntComma extends Text {
  static componentType = "intComma";
  static rendererType = "text";
  static componentDocs = {
    summary: "Renders a numeric value with thousands separators (e.g. 1,000,000)"
  };
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "value",
      newName: "originalValue"
    });
    stateVariableDefinitions.value = {
      description: "The numeric value with thousands separators.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: this.componentType
      },
      returnDependencies: () => ({
        originalValue: {
          dependencyType: "stateVariable",
          variableName: "originalValue"
        }
      }),
      definition: function({ dependencyValues }) {
        let value = dependencyValues.originalValue;
        let startAtLeastFourNumRegex = /^(-?\d+)(\d{3})/;
        let matchObj = value.match(startAtLeastFourNumRegex);
        while (matchObj !== null) {
          value = value.replace(startAtLeastFourNumRegex, `$1,$2`);
          matchObj = value.match(startAtLeastFourNumRegex);
        }
        return { setValue: { value } };
      }
    };
    stateVariableDefinitions.text = {
      description: "The formatted value rendered as a text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { text: dependencyValues.value }
      })
    };
    return stateVariableDefinitions;
  }
}
const e$1 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789".split("");
var t$1 = function(t2) {
  let r2 = (t2 = t2 || "_") + "-";
  for (let t3 = 0; t3 < 7; t3++) r2 += e$1[Math.floor(Math.random() * e$1.length)];
  return r2;
};
let r$1 = { "!": "¡", "?": "¿Ɂ", '"': '“”"❝❞', "'": "‘‛❛❜", "-": "—–", a: "ªÀÁÂÃÄÅàáâãäåĀāĂăĄąǍǎǞǟǠǡǺǻȀȁȂȃȦȧȺΆΑΔΛάαλАадѦѧӐӑӒӓƛɅæ", b: "ßþƀƁƂƃƄƅɃΒβϐϦБВЪЬвъьѢѣҌҍ", c: "¢©ÇçĆćĈĉĊċČčƆƇƈȻȼͻͼͽϲϹϽϾСсєҀҁҪҫ", d: "ÐĎďĐđƉƊȡƋƌǷ", e: "ÈÉÊËèéêëĒēĔĕĖėĘęĚěƎƏƐǝȄȅȆȇȨȩɆɇΈΕΞΣέεξϱϵ϶ЀЁЕЭеѐёҼҽҾҿӖӗӘәӚӛӬӭ", f: "ƑƒϜϝӺӻҒғſ", g: "ĜĝĞğĠġĢģƓǤǥǦǧǴǵ", h: "ĤĥĦħƕǶȞȟΉΗЂЊЋНнђћҢңҤҥҺһӉӊ", I: "ÌÍÎÏ", i: "ìíîïĨĩĪīĬĭĮįİıƖƗȈȉȊȋΊΐΪίιϊІЇії", j: "ĴĵǰȷɈɉϳЈј", k: "ĶķĸƘƙǨǩΚκЌЖКжкќҚқҜҝҞҟҠҡ", l: "ĹĺĻļĽľĿŀŁłƚƪǀǏǐȴȽΙӀӏ", m: "ΜϺϻМмӍӎ", n: "ÑñŃńŅņŇňŉŊŋƝƞǸǹȠȵΝΠήηϞЍИЙЛПийлпѝҊҋӅӆӢӣӤӥπ", o: "ÒÓÔÕÖØðòóôõöøŌōŎŏŐőƟƠơǑǒǪǫǬǭǾǿȌȍȎȏȪȫȬȭȮȯȰȱΌΘΟθοσόϕϘϙϬϭϴОФоѲѳӦӧӨөӪӫ", p: "ƤƿΡρϷϸϼРрҎҏÞ", q: "Ɋɋ", r: "ŔŕŖŗŘřƦȐȑȒȓɌɍЃГЯгяѓҐґ", s: "ŚśŜŝŞşŠšƧƨȘșȿЅѕ", t: "ŢţŤťŦŧƫƬƭƮȚțȶȾΓΤτϮТт", u: "µÙÚÛÜùúûüŨũŪūŬŭŮůŰűŲųƯưƱƲǓǔǕǖǗǘǙǚǛǜȔȕȖȗɄΰμυϋύ", v: "νѴѵѶѷ", w: "ŴŵƜωώϖϢϣШЩшщѡѿ", x: "×ΧχϗϰХхҲҳӼӽӾӿ", y: "ÝýÿŶŷŸƳƴȲȳɎɏΎΥΫγψϒϓϔЎУучўѰѱҮүҰұӮӯӰӱӲӳ", z: "ŹźŻżŽžƩƵƶȤȥɀΖζ" }, a$1 = {};
Object.keys(r$1).forEach((function(e3) {
  r$1[e3].split("").forEach((function(t2) {
    a$1[t2] = e3;
  }));
}));
var n$1 = (e3) => {
  let t2 = e3.split("");
  return t2.forEach((e4, r2) => {
    a$1[e4] && (t2[r2] = a$1[e4]);
  }), t2.join("");
};
const i$1 = /([A-Z]\.)+[A-Z]?,?$/, o$1 = /^[A-Z]\.,?$/, s$1 = /[A-Z]{2,}('s|,)?$/, l$1 = /([a-z]\.)+[a-z]\.?$/;
var u$1 = function(e3) {
  return true === i$1.test(e3) || (true === l$1.test(e3) || (true === o$1.test(e3) || true === s$1.test(e3)));
};
const c$1 = n$1, h$1 = u$1, d$1 = /[a-z\u00C0-\u00FF] ?\/ ?[a-z\u00C0-\u00FF]/;
const g$1 = function(e3) {
  let t2 = e3 = (e3 = (e3 = e3 || "").toLowerCase()).trim();
  return e3 = c$1(e3), true === d$1.test(e3) && (e3 = e3.replace(/\/.*/, "")), e3 = (e3 = (e3 = (e3 = (e3 = (e3 = (e3 = e3.replace(/^[#@]/, "")).replace(/[,;.!?]+$/, "")).replace(/[\u0027\u0060\u00B4\u2018\u2019\u201A\u201B\u2032\u2035\u2039\u203A]+/g, "'")).replace(/[\u0022\u00AB\u00BB\u201C\u201D\u201E\u201F\u2033\u2034\u2036\u2037\u2E42\u301D\u301E\u301F\uFF02]+/g, '"')).replace(/\u2026/g, "...")).replace(/\u2013/g, "-")).replace(/([aeiou][ktrp])in$/, "$1ing"), true === /^(re|un)-?[^aeiou]./.test(e3) && (e3 = e3.replace("-", "")), h$1(e3) && (e3 = e3.replace(/\./g, "")), false === /^[:;]/.test(e3) && (e3 = (e3 = (e3 = e3.replace(/\.{3,}$/g, "")).replace(/[",\.!:;\?\)]+$/g, "")).replace(/^['"\(]+/g, "")), "" === (e3 = (e3 = e3.replace(/[\u200B-\u200D\uFEFF]/g, "")).trim()) && (e3 = t2), e3 = e3.replace(/([0-9]),([0-9])/g, "$1$2");
}, p$1 = function(e3) {
  return e3 = (e3 = e3.replace(/['’]s$/, "")).replace(/s['’]$/, "s");
}, m$1 = /^[ \n\t\.\[\](){}⟨⟩:,،、‒–—―…!‹›«»‐\-?‘’;\/⁄·&*•^†‡°¡¿※№÷×ºª%‰+−=‱¶′″‴§~\|‖¦©℗®℠™¤₳฿\u0022\uFF02\u0027\u201C\u201F\u201B\u201E\u2E42\u201A\u2035\u2036\u2037\u301D\u0060\u301F]+/, f$1 = /[ \n\t\.'\[\](){}⟨⟩:,،、‒–—―…!‹›«»‐\-?‘’;\/⁄·&*@•^†‡°¡¿※#№÷×ºª‰+−=‱¶′″‴§~\|‖¦©℗®℠™¤₳฿\u0022\uFF02\u201D\u00B4\u301E]+$/, b$1 = /\//, y$1 = /['’]/, v$1 = /^[a-z]\.([a-z]\.)+/i, w$1 = /^[-+\.][0-9]/, k$1 = /^'[0-9]{2}/;
var A$1 = (e3) => {
  let t2 = e3, r2 = "", a2 = "";
  "" === (e3 = (e3 = e3.replace(m$1, (t3) => (r2 = t3, "-" !== r2 && "+" !== r2 && "." !== r2 || !w$1.test(e3) ? "'" === r2 && k$1.test(e3) ? (r2 = "", t3) : "" : (r2 = "", t3)))).replace(f$1, (n3) => (a2 = n3, y$1.test(n3) && /[sn]['’]$/.test(t2) && false === y$1.test(r2) ? (a2 = a2.replace(y$1, ""), "'") : true === v$1.test(e3) ? (a2 = a2.replace(/\./, ""), ".") : ""))) && (t2 = t2.replace(/ *$/, (e4) => (a2 = e4 || "", "")), e3 = t2, r2 = "", a2 = a2);
  let n2 = g$1(e3);
  const i2 = { text: e3, clean: n2, reduced: p$1(n2), pre: r2, post: a2 };
  return b$1.test(e3) && e3.split(b$1).forEach((e4) => {
    i2.alias = i2.alias || {}, i2.alias[e4.trim()] = true;
  }), i2;
}, D$1 = {};
!(function(e3) {
  const t2 = /^[A-Z][a-z'\u00C0-\u00FF]/, r2 = /^[A-Z]+s?$/;
  e3.toUpperCase = function() {
    return this.text = this.text.toUpperCase(), this;
  }, e3.toLowerCase = function() {
    return this.text = this.text.toLowerCase(), this;
  }, e3.toTitleCase = function() {
    return this.text = this.text.replace(/^ *[a-z\u00C0-\u00FF]/, (e4) => e4.toUpperCase()), this;
  }, e3.isUpperCase = function() {
    return r2.test(this.text);
  }, e3.isTitleCase = function() {
    return t2.test(this.text);
  }, e3.titleCase = e3.isTitleCase;
})(D$1);
var $$1 = {};
!(function(e3) {
  const t2 = /(\u0022|\uFF02|\u0027|\u201C|\u2018|\u201F|\u201B|\u201E|\u2E42|\u201A|\u00AB|\u2039|\u2035|\u2036|\u2037|\u301D|\u0060|\u301F)/, r2 = /(\u0022|\uFF02|\u0027|\u201D|\u2019|\u201D|\u2019|\u201D|\u201D|\u2019|\u00BB|\u203A|\u2032|\u2033|\u2034|\u301E|\u00B4|\u301E)/;
  e3.hasPost = function(e4) {
    return -1 !== this.post.indexOf(e4);
  }, e3.hasPre = function(e4) {
    return -1 !== this.pre.indexOf(e4);
  }, e3.hasQuote = function() {
    return t2.test(this.pre) || r2.test(this.post);
  }, e3.hasQuotation = e3.hasQuote, e3.hasComma = function() {
    return this.hasPost(",");
  }, e3.hasPeriod = function() {
    return true === this.hasPost(".") && false === this.hasPost("...");
  }, e3.hasExclamation = function() {
    return this.hasPost("!");
  }, e3.hasQuestionMark = function() {
    return this.hasPost("?") || this.hasPost("¿");
  }, e3.hasEllipses = function() {
    return this.hasPost("..") || this.hasPost("…") || this.hasPre("..") || this.hasPre("…");
  }, e3.hasSemicolon = function() {
    return this.hasPost(";");
  }, e3.hasSlash = function() {
    return /\//.test(this.text);
  }, e3.hasHyphen = function() {
    const e4 = /^(-|–|—)$/;
    return e4.test(this.post) || e4.test(this.pre);
  }, e3.hasDash = function() {
    const e4 = / (-|–|—) /;
    return e4.test(this.post) || e4.test(this.pre);
  }, e3.hasContraction = function() {
    return Boolean(this.implicit);
  }, e3.addPunctuation = function(e4) {
    return "," !== e4 && ";" !== e4 || (this.post = this.post.replace(e4, "")), this.post = e4 + this.post, this;
  };
})($$1);
var P$1 = {};
const E$1 = function(e3, t2, r2 = 3) {
  if (e3 === t2) return 1;
  if (e3.length < r2 || t2.length < r2) return 0;
  const a2 = (function(e4, t3) {
    let r3 = e4.length, a3 = t3.length;
    if (0 === r3) return a3;
    if (0 === a3) return r3;
    let n3 = (a3 > r3 ? a3 : r3) + 1;
    if (Math.abs(r3 - a3) > (n3 || 100)) return n3 || 100;
    let i2, o2, s2, l2, u2, c2, h2 = [];
    for (let e5 = 0; e5 < n3; e5++) h2[e5] = [e5], h2[e5].length = n3;
    for (let e5 = 0; e5 < n3; e5++) h2[0][e5] = e5;
    for (let n4 = 1; n4 <= r3; ++n4) for (o2 = e4[n4 - 1], i2 = 1; i2 <= a3; ++i2) {
      if (n4 === i2 && h2[n4][i2] > 4) return r3;
      s2 = t3[i2 - 1], l2 = o2 === s2 ? 0 : 1, u2 = h2[n4 - 1][i2] + 1, (c2 = h2[n4][i2 - 1] + 1) < u2 && (u2 = c2), (c2 = h2[n4 - 1][i2 - 1] + l2) < u2 && (u2 = c2);
      let a4 = n4 > 1 && i2 > 1 && o2 === t3[i2 - 2] && e4[n4 - 2] === s2 && (c2 = h2[n4 - 2][i2 - 2] + l2) < u2;
      h2[n4][i2] = a4 ? c2 : u2;
    }
    return h2[r3][a3];
  })(e3, t2);
  let n2 = Math.max(e3.length, t2.length);
  return 1 - (0 === n2 ? 0 : a2 / n2);
};
let H$1 = function() {
};
H$1 = function(e3, t2, r2, a2) {
  let n2 = (function(e4, t3, r3, a3) {
    if (t3.id === e4.id) return true;
    if (true === t3.anything) return true;
    if (true === t3.start && 0 !== r3) return false;
    if (true === t3.end && r3 !== a3 - 1) return false;
    if (void 0 !== t3.word) {
      if (null !== e4.implicit && e4.implicit === t3.word) return true;
      if (void 0 !== e4.alias && e4.alias.hasOwnProperty(t3.word)) return true;
      if (true === t3.soft && t3.word === e4.root) return true;
      if (void 0 !== t3.fuzzy) {
        let r4 = E$1(t3.word, e4.reduced);
        if (r4 > t3.fuzzy) return true;
        if (true === t3.soft && (r4 = E$1(t3.word, e4.root), r4 > t3.fuzzy)) return true;
      }
      return t3.word === e4.clean || t3.word === e4.text || t3.word === e4.reduced;
    }
    return void 0 !== t3.tag ? true === e4.tags[t3.tag] : void 0 !== t3.method ? "function" == typeof e4[t3.method] && true === e4[t3.method]() : void 0 !== t3.regex ? t3.regex.test(e4.clean) : void 0 !== t3.fastOr ? !(!e4.implicit || true !== t3.fastOr.hasOwnProperty(e4.implicit)) || t3.fastOr.hasOwnProperty(e4.reduced) || t3.fastOr.hasOwnProperty(e4.text) : void 0 !== t3.choices && ("and" === t3.operator ? t3.choices.every((t4) => H$1(e4, t4, r3, a3)) : t3.choices.some((t4) => H$1(e4, t4, r3, a3)));
  })(e3, t2, r2, a2);
  return true === t2.negative ? !n2 : n2;
};
const j$1 = H$1, N$1 = u$1, x$1 = {};
P$1.doesMatch = function(e3, t2, r2) {
  return j$1(this, e3, t2, r2);
}, P$1.isAcronym = function() {
  return N$1(this.text);
}, P$1.isImplicit = function() {
  return "" === this.text && Boolean(this.implicit);
}, P$1.isKnown = function() {
  return Object.keys(this.tags).some((e3) => true !== x$1[e3]);
}, P$1.setRoot = function(e3) {
  let t2 = e3.transforms, r2 = this.implicit || this.clean;
  if (this.tags.Plural && (r2 = t2.toSingular(r2, e3)), this.tags.Verb && !this.tags.Negative && !this.tags.Infinitive) {
    let a2 = null;
    this.tags.PastTense ? a2 = "PastTense" : this.tags.Gerund ? a2 = "Gerund" : this.tags.PresentTense ? a2 = "PresentTense" : this.tags.Participle ? a2 = "Participle" : this.tags.Actor && (a2 = "Actor"), r2 = t2.toInfinitive(r2, e3, a2);
  }
  this.root = r2;
};
var F$1 = {};
const C$1 = n$1, B$1 = /[\s-]/, G$1 = /^[A-Z-]+$/;
F$1.textOut = function(e3, t2, r2) {
  e3 = e3 || {};
  let a2 = this.text, n2 = this.pre, i2 = this.post;
  return true === e3.reduced && (a2 = this.reduced || ""), true === e3.root && (a2 = this.root || ""), true === e3.implicit && this.implicit && (a2 = this.implicit || ""), true === e3.normal && (a2 = this.clean || this.text || ""), true === e3.root && (a2 = this.root || this.reduced || ""), true === e3.unicode && (a2 = C$1(a2)), true === e3.titlecase && (this.tags.ProperNoun && !this.titleCase() || (this.tags.Acronym ? a2 = a2.toUpperCase() : G$1.test(a2) && !this.tags.Acronym && (a2 = a2.toLowerCase()))), true === e3.lowercase && (a2 = a2.toLowerCase()), true === e3.acronyms && this.tags.Acronym && (a2 = a2.replace(/\./g, "")), true !== e3.whitespace && true !== e3.root || (n2 = "", i2 = " ", false !== B$1.test(this.post) && !e3.last || this.implicit || (i2 = "")), true !== e3.punctuation || e3.root || (true === this.hasPost(".") ? i2 = "." + i2 : true === this.hasPost("?") ? i2 = "?" + i2 : true === this.hasPost("!") ? i2 = "!" + i2 : true === this.hasPost(",") ? i2 = "," + i2 : true === this.hasEllipses() && (i2 = "..." + i2)), true !== t2 && (n2 = ""), true !== r2 && (i2 = ""), true === e3.abbreviations && this.tags.Abbreviation && (i2 = i2.replace(/^\./, "")), n2 + a2 + i2;
};
var z$1 = {};
const I$1 = { Auxiliary: 1, Possessive: 1 };
const O$1 = function(e3, t2) {
  let r2 = Object.keys(e3.tags);
  const a2 = t2.tags;
  return r2 = r2.sort((e4, t3) => I$1[t3] || !a2[t3] ? -1 : a2[t3] ? a2[e4] ? a2[e4].lineage.length > a2[t3].lineage.length ? 1 : a2[e4].isA.length > a2[t3].isA.length ? -1 : 0 : 0 : 1), r2;
}, T$1 = { text: true, tags: true, implicit: true, whitespace: true, clean: false, id: false, index: false, offset: false, bestTag: false };
z$1.json = function(e3, t2) {
  e3 = e3 || {};
  let r2 = {};
  return (e3 = Object.assign({}, T$1, e3)).text && (r2.text = this.text), e3.normal && (r2.normal = this.clean), e3.tags && (r2.tags = Object.keys(this.tags)), e3.clean && (r2.clean = this.clean), (e3.id || e3.offset) && (r2.id = this.id), e3.implicit && null !== this.implicit && (r2.implicit = this.implicit), e3.whitespace && (r2.pre = this.pre, r2.post = this.post), e3.bestTag && (r2.bestTag = O$1(this, t2)[0]), r2;
};
var V$1 = Object.assign({}, D$1, $$1, P$1, F$1, z$1), M$1 = {}, J$1 = {};
function L$1() {
  return "undefined" != typeof window && window.document;
}
const S$1 = function(e3, t2) {
  for (e3 = e3.toString(); e3.length < t2; ) e3 += " ";
  return e3;
};
J$1.logTag = function(e3, t2, r2) {
  if (L$1()) return void console.log("%c" + S$1(e3.clean, 3) + "  + " + t2 + " ", "color: #6accb2;");
  let a2 = "\x1B[33m" + S$1(e3.clean, 15) + "\x1B[0m + \x1B[32m" + t2 + "\x1B[0m ";
  r2 && (a2 = S$1(a2, 35) + " " + r2), console.log(a2);
}, J$1.logUntag = function(e3, t2, r2) {
  if (L$1()) return void console.log("%c" + S$1(e3.clean, 3) + "  - " + t2 + " ", "color: #AB5850;");
  let a2 = "\x1B[33m" + S$1(e3.clean, 3) + " \x1B[31m - #" + t2 + "\x1B[0m ";
  r2 && (a2 = S$1(a2, 35) + " " + r2), console.log(a2);
}, J$1.isArray = function(e3) {
  return "[object Array]" === Object.prototype.toString.call(e3);
}, J$1.titleCase = (e3) => e3.charAt(0).toUpperCase() + e3.substr(1);
const _$1 = J$1, K$1 = function(e3, t2, r2, a2) {
  let n2 = a2.tags;
  if ("" === t2 || "." === t2 || "-" === t2) return;
  if ("#" === t2[0] && (t2 = t2.replace(/^#/, "")), t2 = _$1.titleCase(t2), true === e3.tags[t2]) return;
  const i2 = a2.isVerbose();
  true === i2 && _$1.logTag(e3, t2, r2), e3.tags[t2] = true, true === n2.hasOwnProperty(t2) && (n2[t2].isA.forEach((t3) => {
    e3.tags[t3] = true, true === i2 && _$1.logTag(e3, "→ " + t3);
  }), e3.unTag(n2[t2].notA, "←", a2));
};
const q$1 = J$1, W$1 = /^[a-z]/, R$1 = function(e3, t2, r2, a2) {
  const n2 = a2.isVerbose();
  if ("*" === t2) return e3.tags = {}, e3;
  var i2;
  t2 = t2.replace(/^#/, ""), true === W$1.test(t2) && (t2 = (i2 = t2).charAt(0).toUpperCase() + i2.substr(1)), true === e3.tags[t2] && (delete e3.tags[t2], true === n2 && q$1.logUntag(e3, t2, r2));
  const o2 = a2.tags;
  if (o2[t2]) {
    let r3 = o2[t2].lineage;
    for (let t3 = 0; t3 < r3.length; t3++) true === e3.tags[r3[t3]] && (delete e3.tags[r3[t3]], true === n2 && q$1.logUntag(e3, " - " + r3[t3]));
  }
  return e3;
};
const U$1 = function(e3, t2, r2) {
  const a2 = r2.tags;
  if ("#" === t2[0] && (t2 = t2.replace(/^#/, "")), void 0 === a2[t2]) return true;
  let n2 = a2[t2].notA || [];
  for (let t3 = 0; t3 < n2.length; t3++) if (true === e3.tags[n2[t3]]) return false;
  return void 0 === a2[t2].isA || U$1(e3, a2[t2].isA, r2);
};
const Q$1 = function(e3, t2, r2, a2) {
  if ("string" != typeof t2) for (let n2 = 0; n2 < t2.length; n2++) K$1(e3, t2[n2], r2, a2);
  else K$1(e3, t2, r2, a2);
}, Z$1 = function(e3, t2, r2, a2) {
  if ("string" != typeof t2 && t2) for (let n2 = 0; n2 < t2.length; n2++) R$1(e3, t2[n2], r2, a2);
  else R$1(e3, t2, r2, a2);
}, X$1 = U$1;
M$1.tag = function(e3, t2, r2) {
  return Q$1(this, e3, t2, r2), this;
}, M$1.tagSafe = function(e3, t2, r2) {
  return X$1(this, e3, r2) && Q$1(this, e3, t2, r2), this;
}, M$1.unTag = function(e3, t2, r2) {
  return Z$1(this, e3, t2, r2), this;
}, M$1.canBe = function(e3, t2) {
  return X$1(this, e3, t2);
};
const Y$1 = t$1, ee$1 = A$1, te$1 = V$1, re$1 = M$1;
let ae$1 = class ae {
  constructor(e3 = "") {
    e3 = String(e3);
    let t2 = ee$1(e3);
    this.text = t2.text || "", this.clean = t2.clean, this.reduced = t2.reduced, this.root = null, this.implicit = null, this.pre = t2.pre || "", this.post = t2.post || "", this.tags = {}, this.prev = null, this.next = null, this.id = Y$1(t2.clean), this.isA = "Term", t2.alias && (this.alias = t2.alias);
  }
  set(e3) {
    let t2 = ee$1(e3);
    return this.text = t2.text, this.clean = t2.clean, this.reduced = t2.reduced, this.root = null, this.implicit = null, this;
  }
};
ae$1.prototype.clone = function() {
  let e3 = new ae$1(this.text);
  return e3.pre = this.pre, e3.post = this.post, e3.clean = this.clean, e3.reduced = this.reduced, e3.root = this.root, e3.implicit = this.implicit, e3.tags = Object.assign({}, this.tags), e3;
}, Object.assign(ae$1.prototype, te$1), Object.assign(ae$1.prototype, re$1);
var ne$1 = ae$1, ie$1 = { terms: function(e3) {
  if (0 === this.length) return [];
  if (this.cache.terms) return void 0 !== e3 ? this.cache.terms[e3] : this.cache.terms;
  let t2 = [this.pool.get(this.start)];
  for (let r2 = 0; r2 < this.length - 1; r2 += 1) {
    let a2 = t2[t2.length - 1].next;
    if (null === a2) {
      console.error("Compromise error: Linked list broken in phrase '" + this.start + "'");
      break;
    }
    let n2 = this.pool.get(a2);
    if (t2.push(n2), void 0 !== e3 && e3 === r2) return t2[e3];
  }
  return void 0 === e3 && (this.cache.terms = t2), void 0 !== e3 ? t2[e3] : t2;
}, clone: function(e3) {
  if (e3) {
    let e4 = this.buildFrom(this.start, this.length);
    return e4.cache = this.cache, e4;
  }
  let t2 = this.terms().map((e4) => e4.clone());
  return t2.forEach((e4, r2) => {
    this.pool.add(e4), t2[r2 + 1] && (e4.next = t2[r2 + 1].id), t2[r2 - 1] && (e4.prev = t2[r2 - 1].id);
  }), this.buildFrom(t2[0].id, t2.length);
}, lastTerm: function() {
  let e3 = this.terms();
  return e3[e3.length - 1];
}, hasId: function(e3) {
  if (0 === this.length || !e3) return false;
  if (this.start === e3) return true;
  if (this.cache.terms) {
    let t3 = this.cache.terms;
    for (let r2 = 0; r2 < t3.length; r2++) if (t3[r2].id === e3) return true;
    return false;
  }
  let t2 = this.start;
  for (let r2 = 0; r2 < this.length - 1; r2 += 1) {
    let r3 = this.pool.get(t2);
    if (void 0 === r3) return console.error(`Compromise error: Linked list broken. Missing term '${t2}' in phrase '${this.start}'
`), false;
    if (r3.next === e3) return true;
    t2 = r3.next;
  }
  return false;
}, wordCount: function() {
  return this.terms().filter((e3) => "" !== e3.text).length;
}, fullSentence: function() {
  let e3 = this.terms(0);
  for (; e3.prev; ) e3 = this.pool.get(e3.prev);
  let t2 = e3.id, r2 = 1;
  for (; e3.next; ) e3 = this.pool.get(e3.next), r2 += 1;
  return this.buildFrom(t2, r2);
} }, oe$1 = {};
oe$1.text = function(e3 = {}, t2, r2) {
  "string" == typeof e3 && (e3 = "normal" === e3 ? { whitespace: true, unicode: true, lowercase: true, punctuation: true, acronyms: true, abbreviations: true, implicit: true, normal: true } : "clean" === e3 ? { titlecase: false, lowercase: true, punctuation: true, whitespace: true, unicode: true, implicit: true, normal: true } : "reduced" === e3 ? { punctuation: false, titlecase: false, lowercase: true, whitespace: true, unicode: true, implicit: true, reduced: true } : "implicit" === e3 ? { punctuation: true, implicit: true, whitespace: true, trim: true } : "root" === e3 ? { titlecase: false, lowercase: true, punctuation: true, whitespace: true, unicode: true, implicit: true, root: true } : {});
  let a2 = this.terms(), n2 = false;
  a2[0] && null === a2[0].prev && null === a2[a2.length - 1].next && (n2 = true);
  let i2 = a2.reduce((i3, o2, s2) => {
    if (0 === s2 && "" === o2.text && null !== o2.implicit && !e3.implicit) return i3;
    e3.last = r2 && s2 === a2.length - 1;
    let l2 = true, u2 = true;
    return false === n2 && (0 === s2 && t2 && (l2 = false), s2 === a2.length - 1 && r2 && (u2 = false)), i3 + o2.textOut(e3, l2, u2);
  }, "");
  return true === n2 && r2 && (i2 = i2.replace(/ +$/, "")), true === e3.trim && (i2 = i2.trim()), i2;
};
var se$1 = { trim: function() {
  let e3 = this.terms();
  if (e3.length > 0) {
    e3[0].pre = e3[0].pre.replace(/^\s+/, "");
    let t2 = e3[e3.length - 1];
    t2.post = t2.post.replace(/\s+$/, "");
  }
  return this;
} }, le$1 = {};
const ue$1 = /[.?!]\s*$/, ce$1 = function(e3, t2) {
  t2[0].pre = e3[0].pre;
  let r2 = e3[e3.length - 1], a2 = t2[t2.length - 1];
  a2.post = (function(e4, t3) {
    if (ue$1.test(t3)) return t3 + e4.match(/\s*$/);
    return e4;
  })(r2.post, a2.post), r2.post = "", "" === r2.post && (r2.post += " ");
};
const he$1 = / /;
const de$1 = function(e3, t2, r2) {
  let a2 = e3.terms(), n2 = t2.terms();
  ce$1(a2, n2), (function(e4, t3, r3) {
    let a3 = e4[e4.length - 1], n3 = t3[t3.length - 1], i3 = a3.next;
    a3.next = t3[0].id, n3.next = i3, i3 && (r3.get(i3).prev = n3.id);
    let o3 = e4[0].id;
    o3 && (t3[0].prev = o3);
  })(a2, n2, e3.pool);
  let i2 = [e3], o2 = e3.start, s2 = [r2];
  return s2 = s2.concat(r2.parents()), s2.forEach((e4) => {
    let t3 = e4.list.filter((e5) => e5.hasId(o2));
    i2 = i2.concat(t3);
  }), i2 = (function(e4) {
    return e4.filter((t3, r3) => e4.indexOf(t3) === r3);
  })(i2), i2.forEach((e4) => {
    e4.length += t2.length;
  }), e3.cache = {}, e3;
}, ge$1 = function(e3, t2, r2) {
  const a2 = e3.start;
  let n2 = t2.terms();
  !(function(e4) {
    let t3 = e4[e4.length - 1];
    false === he$1.test(t3.post) && (t3.post += " ");
  })(n2), (function(e4, t3, r3) {
    let a3 = r3[r3.length - 1];
    a3.next = e4.start;
    let n3 = e4.pool, i3 = n3.get(e4.start);
    i3.prev && (n3.get(i3.prev).next = t3.start);
    r3[0].prev = e4.terms(0).prev, e4.terms(0).prev = a3.id;
  })(e3, t2, n2);
  let i2 = [e3], o2 = [r2];
  return o2 = o2.concat(r2.parents()), o2.forEach((e4) => {
    let r3 = e4.list.filter((e5) => e5.hasId(a2) || e5.hasId(t2.start));
    i2 = i2.concat(r3);
  }), i2 = (function(e4) {
    return e4.filter((t3, r3) => e4.indexOf(t3) === r3);
  })(i2), i2.forEach((e4) => {
    e4.length += t2.length, e4.start === a2 && (e4.start = t2.start), e4.cache = {};
  }), e3;
}, pe$1 = function(e3, t2) {
  let r2 = t2.pool(), a2 = e3.terms(), n2 = r2.get(a2[0].prev) || {}, i2 = r2.get(a2[a2.length - 1].next) || {};
  a2[0].implicit && n2.implicit && (n2.set(n2.implicit), n2.post += " "), (function(e4, t3, r3, a3) {
    let n3 = e4.parents();
    n3.push(e4), n3.forEach((e5) => {
      let n4 = e5.list.find((e6) => e6.hasId(t3));
      n4 && (n4.length -= r3, n4.start === t3 && (n4.start = a3.id), n4.cache = {});
    }), e4.list = e4.list.filter((e5) => !(!e5.start || !e5.length));
  })(t2, e3.start, e3.length, i2), n2 && (n2.next = i2.id), i2 && (i2.prev = n2.id);
};
le$1.append = function(e3, t2) {
  return de$1(this, e3, t2), this;
}, le$1.prepend = function(e3, t2) {
  return ge$1(this, e3, t2), this;
}, le$1.delete = function(e3) {
  return pe$1(this, e3), this;
}, le$1.replace = function(e3, t2) {
  let r2 = this.length;
  de$1(this, e3, t2);
  let a2 = this.buildFrom(this.start, this.length);
  a2.length = r2, pe$1(a2, t2);
}, le$1.splitOn = function(e3) {
  let t2 = this.terms(), r2 = { before: null, match: null, after: null }, a2 = t2.findIndex((t3) => t3.id === e3.start);
  if (-1 === a2) return r2;
  let n2 = t2.slice(0, a2);
  n2.length > 0 && (r2.before = this.buildFrom(n2[0].id, n2.length));
  let i2 = t2.slice(a2, a2 + e3.length);
  i2.length > 0 && (r2.match = this.buildFrom(i2[0].id, i2.length));
  let o2 = t2.slice(a2 + e3.length, t2.length);
  return o2.length > 0 && (r2.after = this.buildFrom(o2[0].id, o2.length, this.pool)), r2;
};
var me$2 = { json: function(e3 = {}, t2) {
  let r2 = {};
  return e3.text && (r2.text = this.text()), e3.normal && (r2.normal = this.text("normal")), e3.clean && (r2.clean = this.text("clean")), e3.reduced && (r2.reduced = this.text("reduced")), e3.implicit && (r2.implicit = this.text("implicit")), e3.root && (r2.root = this.text("root")), e3.trim && (r2.text && (r2.text = r2.text.trim()), r2.normal && (r2.normal = r2.normal.trim()), r2.reduced && (r2.reduced = r2.reduced.trim())), e3.terms && (true === e3.terms && (e3.terms = {}), r2.terms = this.terms().map((r3) => r3.json(e3.terms, t2))), r2;
} }, fe$1 = { lookAhead: function(e3) {
  e3 || (e3 = ".*");
  let t2 = this.pool, r2 = [];
  const a2 = function(e4) {
    let n3 = t2.get(e4);
    n3 && (r2.push(n3), n3.prev && a2(n3.next));
  };
  let n2 = this.terms(), i2 = n2[n2.length - 1];
  return a2(i2.next), 0 === r2.length ? [] : this.buildFrom(r2[0].id, r2.length).match(e3);
}, lookBehind: function(e3) {
  e3 || (e3 = ".*");
  let t2 = this.pool, r2 = [];
  const a2 = function(e4) {
    let n3 = t2.get(e4);
    n3 && (r2.push(n3), n3.prev && a2(n3.prev));
  };
  let n2 = t2.get(this.start);
  return a2(n2.prev), 0 === r2.length ? [] : this.buildFrom(r2[r2.length - 1].id, r2.length).match(e3);
} }, be$1 = Object.assign({}, ie$1, oe$1, se$1, le$1, me$2, fe$1), ye$1 = {};
var ve$1, we$1 = function(e3, t2) {
  if (0 === t2.length) return true;
  for (let e4 = 0; e4 < t2.length; e4 += 1) {
    let r2 = t2[e4];
    if (true !== r2.optional && true !== r2.negative && true === r2.start && e4 > 0) return true;
    if (true === r2.anything && true === r2.negative) return true;
  }
  return false;
}, ke$1 = {};
(ve$1 = ke$1).getGreedy = function(e3, t2) {
  let r2 = Object.assign({}, e3.regs[e3.r], { start: false, end: false }), a2 = e3.t;
  for (; e3.t < e3.terms.length; e3.t += 1) {
    if (t2 && e3.terms[e3.t].doesMatch(t2, e3.start_i + e3.t, e3.phrase_length)) return e3.t;
    let n2 = e3.t - a2 + 1;
    if (void 0 !== r2.max && n2 === r2.max) return e3.t;
    if (false === e3.terms[e3.t].doesMatch(r2, e3.start_i + e3.t, e3.phrase_length)) return void 0 !== r2.min && n2 < r2.min ? null : e3.t;
  }
  return e3.t;
}, ve$1.greedyTo = function(e3, t2) {
  let r2 = e3.t;
  if (!t2) return e3.terms.length;
  for (; r2 < e3.terms.length; r2 += 1) if (true === e3.terms[r2].doesMatch(t2, e3.start_i + r2, e3.phrase_length)) return r2;
  return null;
}, ve$1.isEndGreedy = function(e3, t2) {
  if (true === e3.end && true === e3.greedy && t2.start_i + t2.t < t2.phrase_length - 1) {
    let r2 = Object.assign({}, e3, { end: false });
    if (true === t2.terms[t2.t].doesMatch(r2, t2.start_i + t2.t, t2.phrase_length)) return true;
  }
  return false;
}, ve$1.doOrBlock = function(e3, t2 = 0) {
  let r2 = e3.regs[e3.r], a2 = false;
  for (let n2 = 0; n2 < r2.choices.length; n2 += 1) {
    let i2 = r2.choices[n2];
    if (a2 = i2.every((r3, a3) => {
      let n3 = 0, i3 = e3.t + a3 + t2 + n3;
      if (void 0 === e3.terms[i3]) return false;
      let o2 = e3.terms[i3].doesMatch(r3, i3 + e3.start_i, e3.phrase_length);
      if (true === o2 && true === r3.greedy) for (let t3 = 1; t3 < e3.terms.length; t3 += 1) {
        let a4 = e3.terms[i3 + t3];
        if (a4) {
          if (true !== a4.doesMatch(r3, e3.start_i + t3, e3.phrase_length)) break;
          n3 += 1;
        }
      }
      return t2 += n3, o2;
    }), a2) {
      t2 += i2.length;
      break;
    }
  }
  return a2 && true === r2.greedy ? ve$1.doOrBlock(e3, t2) : t2;
}, ve$1.doAndBlock = function(e3) {
  let t2 = 0;
  return true === e3.regs[e3.r].choices.every((r2) => {
    let a2 = r2.every((t3, r3) => {
      let a3 = e3.t + r3;
      return void 0 !== e3.terms[a3] && e3.terms[a3].doesMatch(t3, a3, e3.phrase_length);
    });
    return true === a2 && r2.length > t2 && (t2 = r2.length), a2;
  }) && t2;
}, ve$1.getGroup = function(e3, t2, r2) {
  if (e3.groups[e3.groupId]) return e3.groups[e3.groupId];
  const a2 = e3.terms[t2].id;
  return e3.groups[e3.groupId] = { group: String(r2), start: a2, length: 0 }, e3.groups[e3.groupId];
};
const Ae$1 = t$1, De = ke$1;
var $e$1 = function(e3, t2, r2, a2) {
  let n2 = { t: 0, terms: e3, r: 0, regs: t2, groups: {}, start_i: r2, phrase_length: a2, hasGroup: false, groupId: null, previousGroup: null };
  for (; n2.r < t2.length; n2.r += 1) {
    let e4 = t2[n2.r];
    if (n2.hasGroup = "string" == typeof e4.named || "number" == typeof e4.named, true === n2.hasGroup) {
      const r4 = t2[n2.r - 1];
      r4 && r4.named === e4.named && n2.previousGroup ? n2.groupId = n2.previousGroup : (n2.groupId = Ae$1(e4.named), n2.previousGroup = n2.groupId);
    }
    if (!n2.terms[n2.t]) {
      if (false === t2.slice(n2.r).some((e5) => !e5.optional)) break;
      return null;
    }
    if (true === e4.anything && true === e4.greedy) {
      let r4 = De.greedyTo(n2, t2[n2.r + 1]);
      if (null === r4 || 0 === r4) return null;
      if (void 0 !== e4.min && r4 - n2.t < e4.min) return null;
      if (void 0 !== e4.max && r4 - n2.t > e4.max) {
        n2.t = n2.t + e4.max;
        continue;
      }
      if (true === n2.hasGroup) {
        De.getGroup(n2, n2.t, e4.named).length = r4 - n2.t;
      }
      n2.t = r4;
      continue;
    }
    if (void 0 !== e4.choices && "or" === e4.operator) {
      let t3 = De.doOrBlock(n2);
      if (t3) {
        if (true === e4.negative) return null;
        if (true === n2.hasGroup) {
          De.getGroup(n2, n2.t, e4.named).length += t3;
        }
        n2.t += t3;
        continue;
      }
      if (!e4.optional) return null;
    }
    if (void 0 !== e4.choices && "and" === e4.operator) {
      let t3 = De.doAndBlock(n2);
      if (t3) {
        if (true === e4.negative) return null;
        if (true === n2.hasGroup) {
          De.getGroup(n2, n2.t, e4.named).length += t3;
        }
        n2.t += t3;
        continue;
      }
      if (!e4.optional) return null;
    }
    let r3 = n2.terms[n2.t], i2 = r3.doesMatch(e4, n2.start_i + n2.t, n2.phrase_length);
    if (true === e4.anything || true === i2 || De.isEndGreedy(e4, n2)) {
      let i3 = n2.t;
      if (e4.optional && t2[n2.r + 1] && e4.negative) continue;
      if (e4.optional && t2[n2.r + 1]) {
        let a3 = r3.doesMatch(t2[n2.r + 1], n2.start_i + n2.t, n2.phrase_length);
        if (e4.negative || a3) {
          let e5 = n2.terms[n2.t + 1];
          e5 && e5.doesMatch(t2[n2.r + 1], n2.start_i + n2.t, n2.phrase_length) || (n2.r += 1);
        }
      }
      if (n2.t += 1, true === e4.end && n2.t !== n2.terms.length && true !== e4.greedy) return null;
      if (true === e4.greedy) {
        if (n2.t = De.getGreedy(n2, t2[n2.r + 1]), null === n2.t) return null;
        if (e4.min && e4.min > n2.t) return null;
        if (true === e4.end && n2.start_i + n2.t !== a2) return null;
      }
      if (true === n2.hasGroup) {
        const t3 = De.getGroup(n2, i3, e4.named);
        n2.t > 1 && e4.greedy ? t3.length += n2.t - i3 : t3.length++;
      }
    } else {
      if (e4.negative) {
        let t3 = Object.assign({}, e4);
        if (t3.negative = false, true === n2.terms[n2.t].doesMatch(t3, n2.start_i + n2.t, n2.phrase_length)) return null;
      }
      if (true !== e4.optional) {
        if (n2.terms[n2.t].isImplicit() && t2[n2.r - 1] && n2.terms[n2.t + 1]) {
          if (n2.terms[n2.t - 1] && n2.terms[n2.t - 1].implicit === t2[n2.r - 1].word) return null;
          if (n2.terms[n2.t + 1].doesMatch(e4, n2.start_i + n2.t, n2.phrase_length)) {
            n2.t += 2;
            continue;
          }
        }
        return null;
      }
    }
  }
  return { match: n2.terms.slice(0, n2.t), groups: n2.groups };
};
var Pe$1 = function(e3, t2, r2) {
  if (!r2 || 0 === r2.length) return r2;
  if (t2.some((e4) => e4.end)) {
    let t3 = e3[e3.length - 1];
    r2 = r2.filter(({ match: e4 }) => -1 !== e4.indexOf(t3));
  }
  return r2;
};
const Ee$1 = /(?:^|\s)([\!\[\^]*(?:<[^<]*>)?\/.*?[^\\\/]\/[\?\]\+\*\$~]*)(?:\s|$)/, He = /([\!\[\^]*(?:<[^<]*>)?\([^\)]+[^\\\)]\)[\?\]\+\*\$~]*)(?:\s|$)/, je$1 = / /g, Ne$1 = (e3) => /^[\!\[\^]*(<[^<]*>)?\//.test(e3) && /\/[\?\]\+\*\$~]*$/.test(e3), xe$1 = function(e3) {
  return e3 = (e3 = e3.map((e4) => e4.trim())).filter((e4) => e4);
};
var Fe$1 = function(e3) {
  let t2 = e3.split(Ee$1), r2 = [];
  t2.forEach((e4) => {
    Ne$1(e4) ? r2.push(e4) : r2 = r2.concat(e4.split(He));
  }), r2 = xe$1(r2);
  let a2 = [];
  return r2.forEach((e4) => {
    ((e5) => /^[\!\[\^]*(<[^<]*>)?\(/.test(e5) && /\)[\?\]\+\*\$~]*$/.test(e5))(e4) || Ne$1(e4) ? a2.push(e4) : a2 = a2.concat(e4.split(je$1));
  }), a2 = xe$1(a2), a2;
};
const Ce$1 = /\{([0-9]+,?[0-9]*)\}/, Be = /&&/, Ge = new RegExp(/^<\s*?(\S+)\s*?>/), ze$1 = function(e3) {
  return e3[e3.length - 1];
}, Ie = function(e3) {
  return e3[0];
}, Oe$1 = function(e3) {
  return e3.substr(1);
}, Te = function(e3) {
  return e3.substr(0, e3.length - 1);
}, Ve$1 = function(e3) {
  return e3 = Oe$1(e3), e3 = Te(e3);
}, Me$1 = function(e3) {
  let t2 = {};
  for (let r3 = 0; r3 < 2; r3 += 1) {
    if ("$" === ze$1(e3) && (t2.end = true, e3 = Te(e3)), "^" === Ie(e3) && (t2.start = true, e3 = Oe$1(e3)), ("[" === Ie(e3) || "]" === ze$1(e3)) && (t2.named = true, "[" === Ie(e3) ? t2.groupType = "]" === ze$1(e3) ? "single" : "start" : t2.groupType = "end", e3 = (e3 = e3.replace(/^\[/, "")).replace(/\]$/, ""), "<" === Ie(e3))) {
      const r4 = Ge.exec(e3);
      r4.length >= 2 && (t2.named = r4[1], e3 = e3.replace(r4[0], ""));
    }
    if ("+" === ze$1(e3) && (t2.greedy = true, e3 = Te(e3)), "*" !== e3 && "*" === ze$1(e3) && "\\*" !== e3 && (t2.greedy = true, e3 = Te(e3)), "?" === ze$1(e3) && (t2.optional = true, e3 = Te(e3)), "!" === Ie(e3) && (t2.negative = true, e3 = Oe$1(e3)), "(" === Ie(e3) && ")" === ze$1(e3)) {
      Be.test(e3) ? (t2.choices = e3.split(Be), t2.operator = "and") : (t2.choices = e3.split("|"), t2.operator = "or"), t2.choices[0] = Oe$1(t2.choices[0]);
      let r4 = t2.choices.length - 1;
      t2.choices[r4] = Te(t2.choices[r4]), t2.choices = t2.choices.map((e4) => e4.trim()), t2.choices = t2.choices.filter((e4) => e4), t2.choices = t2.choices.map((e4) => e4.split(/ /g).map(Me$1)), e3 = "";
    }
    if ("/" === Ie(e3) && "/" === ze$1(e3)) return e3 = Ve$1(e3), t2.regex = new RegExp(e3), t2;
    if ("~" === Ie(e3) && "~" === ze$1(e3)) return e3 = Ve$1(e3), t2.soft = true, t2.word = e3, t2;
  }
  return true === Ce$1.test(e3) && (e3 = e3.replace(Ce$1, (e4, r3) => {
    let a2 = r3.split(/,/g);
    return 1 === a2.length ? (t2.min = Number(a2[0]), t2.max = Number(a2[0])) : (t2.min = Number(a2[0]), t2.max = Number(a2[1] || 999)), t2.greedy = true, t2.optional = true, "";
  })), "#" === Ie(e3) ? (t2.tag = Oe$1(e3), t2.tag = (r2 = t2.tag).charAt(0).toUpperCase() + r2.substr(1), t2) : "@" === Ie(e3) ? (t2.method = Oe$1(e3), t2) : "." === e3 ? (t2.anything = true, t2) : "*" === e3 ? (t2.anything = true, t2.greedy = true, t2.optional = true, t2) : (e3 && (e3 = (e3 = e3.replace("\\*", "*")).replace("\\.", "."), t2.word = e3.toLowerCase()), t2);
  var r2;
};
const Je = Fe$1, Le = Me$1, Se = function(e3, t2 = {}) {
  return e3.filter((e4) => e4.groupType).length > 0 && (e3 = (function(e4) {
    let t3, r2 = false, a2 = -1;
    for (let n2 = 0; n2 < e4.length; n2++) {
      const i2 = e4[n2];
      "single" !== i2.groupType || true !== i2.named ? ("start" === i2.groupType && (r2 = true, "string" == typeof i2.named || "number" == typeof i2.named ? t3 = i2.named : (a2 += 1, t3 = a2)), r2 && (i2.named = t3), "end" === i2.groupType && (r2 = false)) : (a2 += 1, i2.named = a2);
    }
    return e4;
  })(e3)), t2.fuzzy || (e3 = (function(e4) {
    return e4.map((e5) => {
      if (void 0 !== e5.choices && true === e5.choices.every((e6) => {
        if (1 !== e6.length) return false;
        let t3 = e6[0];
        return void 0 !== t3.word && true !== t3.negative && true !== t3.optional && true !== t3.method;
      })) {
        let t3 = {};
        e5.choices.forEach((e6) => {
          t3[e6[0].word] = true;
        }), e5.fastOr = t3, delete e5.choices;
      }
      return e5;
    });
  })(e3)), e3;
};
var _e = function(e3, t2 = {}) {
  if (null == e3 || "" === e3) return [];
  if ("object" == typeof e3) {
    if ((function(e4) {
      return "[object Array]" === Object.prototype.toString.call(e4);
    })(e3)) {
      if (0 === e3.length || !e3[0]) return [];
      if ("object" == typeof e3[0]) return e3;
      if ("string" == typeof e3[0]) return (function(e4) {
        return [{ choices: e4.map((e5) => [{ word: e5 }]), operator: "or" }];
      })(e3);
    }
    return e3 && "Doc" === e3.isA ? (function(e4) {
      if (!e4 || !e4.list || !e4.list[0]) return [];
      let t3 = [];
      return e4.list.forEach((e5) => {
        let r3 = [];
        e5.terms().forEach((e6) => {
          r3.push(e6.id);
        }), t3.push(r3);
      }), [{ idBlocks: t3 }];
    })(e3) : [];
  }
  "number" == typeof e3 && (e3 = String(e3));
  let r2 = Je(e3);
  return r2 = r2.map((e4) => Le(e4)), r2 = Se(r2, t2), r2 = (function(e4, t3) {
    return true === t3.fuzzy && (t3.fuzzy = 0.85), "number" == typeof t3.fuzzy && (e4 = e4.map((e5) => (t3.fuzzy > 0 && e5.word && (e5.fuzzy = t3.fuzzy), e5.choices && e5.choices.forEach((e6) => {
      e6.forEach((e7) => {
        e7.fuzzy = t3.fuzzy;
      });
    }), e5))), e4;
  })(r2, t2), r2;
};
const Ke = we$1, qe = $e$1, We = Pe$1, Re = _e, Ue = function(e3, t2) {
  let r2 = [], a2 = t2[0].idBlocks;
  for (let t3 = 0; t3 < e3.length; t3 += 1) a2.forEach((a3) => {
    if (0 === a3.length) return;
    a3.every((r3, a4) => e3[t3 + a4].id === r3) && (r2.push({ match: e3.slice(t3, t3 + a3.length) }), t3 += a3.length - 1);
  });
  return r2;
};
var Qe = function(e3, t2, r2 = false) {
  if ("string" == typeof t2 && (t2 = Re(t2)), true === Ke(e3, t2)) return [];
  const a2 = t2.filter((e4) => true !== e4.optional && true !== e4.negative).length;
  let n2 = e3.terms(), i2 = [];
  if (t2[0].idBlocks) {
    let e4 = Ue(n2, t2);
    if (e4 && e4.length > 0) return We(n2, t2, e4);
  }
  if (true === t2[0].start) {
    let e4 = qe(n2, t2, 0, n2.length);
    return e4 && e4.match && e4.match.length > 0 && (e4.match = e4.match.filter((e5) => e5), i2.push(e4)), We(n2, t2, i2);
  }
  for (let e4 = 0; e4 < n2.length && !(e4 + a2 > n2.length); e4 += 1) {
    let a3 = qe(n2.slice(e4), t2, e4, n2.length);
    if (a3 && a3.match && a3.match.length > 0 && (e4 += a3.match.length - 1, a3.match = a3.match.filter((e5) => e5), i2.push(a3), true === r2)) return We(n2, t2, i2);
  }
  return We(n2, t2, i2);
};
const Ze = Qe;
const Xe = Qe, Ye = function(e3, t2) {
  let r2 = {};
  Ze(e3, t2).forEach(({ match: e4 }) => {
    e4.forEach((e5) => {
      r2[e5.id] = true;
    });
  });
  let a2 = e3.terms(), n2 = [], i2 = [];
  return a2.forEach((e4) => {
    true !== r2[e4.id] ? i2.push(e4) : i2.length > 0 && (n2.push(i2), i2 = []);
  }), i2.length > 0 && n2.push(i2), n2;
};
ye$1.match = function(e3, t2 = false) {
  let r2 = Xe(this, e3, t2);
  return r2 = r2.map(({ match: e4, groups: t3 }) => {
    let r3 = this.buildFrom(e4[0].id, e4.length, t3);
    return r3.cache.terms = e4, r3;
  }), r2;
}, ye$1.has = function(e3) {
  return Xe(this, e3, true).length > 0;
}, ye$1.not = function(e3) {
  let t2 = Ye(this, e3);
  return t2 = t2.map((e4) => this.buildFrom(e4[0].id, e4.length)), t2;
}, ye$1.canBe = function(e3, t2) {
  let r2 = [], a2 = this.terms(), n2 = false;
  for (let i2 = 0; i2 < a2.length; i2 += 1) {
    let o2 = a2[i2].canBe(e3, t2);
    true === o2 && (true === n2 ? r2[r2.length - 1].push(a2[i2]) : r2.push([a2[i2]]), n2 = o2);
  }
  return r2 = r2.filter((e4) => e4.length > 0).map((e4) => this.buildFrom(e4[0].id, e4.length)), r2;
};
const et = be$1, tt = ye$1;
class rt {
  constructor(e3, t2, r2) {
    this.start = e3, this.length = t2, this.isA = "Phrase", Object.defineProperty(this, "pool", { enumerable: false, writable: true, value: r2 }), Object.defineProperty(this, "cache", { enumerable: false, writable: true, value: {} }), Object.defineProperty(this, "groups", { enumerable: false, writable: true, value: {} });
  }
}
rt.prototype.buildFrom = function(e3, t2, r2) {
  let a2 = new rt(e3, t2, this.pool);
  return r2 && Object.keys(r2).length > 0 ? a2.groups = r2 : a2.groups = this.groups, a2;
}, Object.assign(rt.prototype, tt), Object.assign(rt.prototype, et);
const at = { term: "terms" };
Object.keys(at).forEach((e3) => rt.prototype[e3] = rt.prototype[at[e3]]);
var nt = rt;
class it {
  constructor(e3 = {}) {
    Object.defineProperty(this, "words", { enumerable: false, value: e3 });
  }
  add(e3) {
    return this.words[e3.id] = e3, this;
  }
  get(e3) {
    return this.words[e3];
  }
  remove(e3) {
    delete this.words[e3];
  }
  merge(e3) {
    return Object.assign(this.words, e3.words), this;
  }
  stats() {
    return { words: Object.keys(this.words).length };
  }
}
it.prototype.clone = function() {
  let e3 = Object.keys(this.words).reduce((e4, t2) => {
    let r2 = this.words[t2].clone();
    return e4[r2.id] = r2, e4;
  }, {});
  return new it(e3);
};
var ot = it;
var st = (e3) => {
  e3.forEach((t2, r2) => {
    r2 > 0 && (t2.prev = e3[r2 - 1].id), e3[r2 + 1] && (t2.next = e3[r2 + 1].id);
  });
};
const lt = /(\S.+?[.!?\u203D\u2E18\u203C\u2047-\u2049])(?=\s+|$)/g, ut = /\S/, ct = /[ .][A-Z]\.? *$/i, ht = /(?:\u2026|\.{2,}) *$/, dt = /((?:\r?\n|\r)+)/, gt = /[a-z0-9\u00C0-\u00FF\u00a9\u00ae\u2000-\u3300\ud000-\udfff]/i, pt = /^\s+/, mt = function(e3, t2, r2, a2) {
  if (a2.hasLetter = (function(e4, t3) {
    return t3 || gt.test(e4);
  })(t2, a2.hasLetter), !a2.hasLetter) return false;
  if ((function(e4, t3) {
    return -1 !== t3.indexOf(".") && ct.test(e4);
  })(e3, t2)) return false;
  if ((function(e4, t3) {
    return -1 !== t3.indexOf(".") && ht.test(e4);
  })(e3, t2)) return false;
  let n2 = e3.replace(/[.!?\u203D\u2E18\u203C\u2047-\u2049] *$/, "").split(" "), i2 = n2[n2.length - 1].toLowerCase();
  return !r2.hasOwnProperty(i2);
};
var ft = function(e3, t2) {
  let r2 = t2.cache.abbreviations;
  e3 = e3 || "";
  let a2 = [], n2 = [];
  if (!(e3 = String(e3)) || "string" != typeof e3 || false === ut.test(e3)) return a2;
  let i2 = (function(e4) {
    let t3 = [], r3 = e4.split(dt);
    for (let e5 = 0; e5 < r3.length; e5++) {
      let a3 = r3[e5].split(lt);
      for (let e6 = 0; e6 < a3.length; e6++) t3.push(a3[e6]);
    }
    return t3;
  })(e3 = e3.replace(" ", " "));
  for (let e4 = 0; e4 < i2.length; e4++) {
    let t3 = i2[e4];
    if (void 0 !== t3 && "" !== t3) {
      if (false === ut.test(t3)) {
        if (n2[n2.length - 1]) {
          n2[n2.length - 1] += t3;
          continue;
        }
        if (i2[e4 + 1]) {
          i2[e4 + 1] = t3 + i2[e4 + 1];
          continue;
        }
      }
      n2.push(t3);
    }
  }
  let o2 = n2[0] || "";
  const s2 = { hasLetter: false };
  for (let e4 = 0; e4 < n2.length; e4++) {
    let t3 = n2[e4];
    n2[e4 + 1] && false === mt(t3, o2, r2, s2) ? (o2 = n2[e4 + 1] || "", n2[e4 + 1] = t3 + o2) : t3 && t3.length > 0 && (a2.push(t3), o2 = n2[e4 + 1] || "", s2.hasLetter = false), n2[e4] = "";
  }
  if (0 === a2.length) return [e3];
  for (let e4 = 1; e4 < a2.length; e4 += 1) {
    let t3 = a2[e4].match(pt);
    null !== t3 && (a2[e4 - 1] += t3[0], a2[e4] = a2[e4].replace(pt, ""));
  }
  return a2;
};
const bt = /\S/, yt = /^[!?.]+$/, vt = /(\S+)/, wt = /[a-z] ?\/ ?[a-z]*$/;
let kt = [".", "?", "!", ":", ";", "-", "–", "—", "--", "...", "(", ")", "[", "]", '"', "'", "`"];
kt = kt.reduce((e3, t2) => (e3[t2] = true, e3), {});
const At = function(e3) {
  if (true === /^(re|un|micro|macro|trans|bi|mono|over)-?[^aeiou]./.test(e3)) return false;
  if (true === /^([a-z\u00C0-\u00FF/]+)(-|–|—)(like|ish|less|able)/i.test(e3)) return false;
  if (true === /^([a-z\u00C0-\u00FF`"'/]+)(-|–|—)([a-z0-9\u00C0-\u00FF].*)/i.test(e3)) return true;
  return true === /^([0-9]{1,4})(-|–|—)([a-z\u00C0-\u00FF`"'/-]+$)/i.test(e3);
}, Dt = function(e3) {
  let t2 = [];
  const r2 = e3.split(/[-–—]/);
  let a2 = "-", n2 = e3.match(/[-–—]/);
  n2 && n2[0] && (a2 = n2);
  for (let e4 = 0; e4 < r2.length; e4++) e4 === r2.length - 1 ? t2.push(r2[e4]) : t2.push(r2[e4] + a2);
  return t2;
};
const $t = ne$1, Pt = nt, Et = ot, Ht = st, jt = ft, Nt = function(e3) {
  let t2 = [], r2 = [];
  if ("number" == typeof (e3 = e3 || "") && (e3 = String(e3)), (function(e4) {
    return "[object Array]" === Object.prototype.toString.call(e4);
  })(e3)) return e3;
  const a2 = e3.split(vt);
  for (let e4 = 0; e4 < a2.length; e4++) true !== At(a2[e4]) ? r2.push(a2[e4]) : r2 = r2.concat(Dt(a2[e4]));
  let n2 = "";
  for (let e4 = 0; e4 < r2.length; e4++) {
    let a3 = r2[e4];
    true === bt.test(a3) && false === kt.hasOwnProperty(a3) && false === yt.test(a3) ? (t2.length > 0 ? (t2[t2.length - 1] += n2, t2.push(a3)) : t2.push(n2 + a3), n2 = "") : n2 += a3;
  }
  return n2 && (0 === t2.length && (t2[0] = ""), t2[t2.length - 1] += n2), t2 = (function(e4) {
    for (let t3 = 1; t3 < e4.length - 1; t3++) wt.test(e4[t3]) && (e4[t3 - 1] += e4[t3] + e4[t3 + 1], e4[t3] = null, e4[t3 + 1] = null);
    return e4;
  })(t2), t2 = (function(e4) {
    const t3 = /^[0-9]{1,4}(:[0-9][0-9])?([a-z]{1,2})? ?(-|–|—) ?$/, r3 = /^[0-9]{1,4}([a-z]{1,2})? ?$/;
    for (let a3 = 0; a3 < e4.length - 1; a3 += 1) e4[a3 + 1] && t3.test(e4[a3]) && r3.test(e4[a3 + 1]) && (e4[a3] = e4[a3] + e4[a3 + 1], e4[a3 + 1] = null);
    return e4;
  })(t2), t2 = t2.filter((e4) => e4), t2;
};
var xt = function(e3 = "", t2, r2) {
  let a2 = null;
  return "string" != typeof e3 && ("number" == typeof e3 ? e3 = String(e3) : (function(e4) {
    return "[object Array]" === Object.prototype.toString.call(e4);
  })(e3) && (a2 = e3)), a2 = a2 || jt(e3, t2), a2 = a2.map((e4) => Nt(e4)), r2 = r2 || new Et(), a2.map((e4) => {
    e4 = e4.map((e5) => {
      let t4 = new $t(e5);
      return r2.add(t4), t4;
    }), Ht(e4);
    let t3 = new Pt(e4[0].id, e4.length, r2);
    return t3.cache.terms = e4, t3;
  });
};
const Ft = ne$1, Ct = nt, Bt = ot, Gt = st;
var zt = function(e3, t2) {
  let r2 = new Bt();
  return e3.map((e4, a2) => {
    let n2 = e4.terms.map((n3, i2) => {
      let o2 = new Ft(n3.text);
      return o2.pre = void 0 !== n3.pre ? n3.pre : "", void 0 === n3.post && (n3.post = " ", i2 >= e4.terms.length - 1 && (n3.post = ". ", a2 >= e4.terms.length - 1 && (n3.post = "."))), o2.post = void 0 !== n3.post ? n3.post : " ", n3.tags && n3.tags.forEach((e5) => o2.tag(e5, "", t2)), r2.add(o2), o2;
    });
    return Gt(n2), new Ct(n2[0].id, n2.length, r2);
  });
};
const It = ["Person", "Place", "Organization"];
const Ot = ["Noun", "Verb", "Adjective", "Adverb", "Value", "QuestionWord"];
const Tt = { Noun: "blue", Verb: "green", Negative: "green", Date: "red", Value: "red", Adjective: "magenta", Preposition: "cyan", Conjunction: "cyan", Determiner: "cyan", Adverb: "cyan" };
const Vt = function(e3) {
  return Object.keys(e3).forEach((t2) => {
    e3[t2].color ? e3[t2].color = e3[t2].color : Tt[t2] ? e3[t2].color = Tt[t2] : e3[t2].isA.some((r2) => !!Tt[r2] && (e3[t2].color = Tt[r2], true));
  }), e3;
}, Mt = function(e3) {
  return Object.keys(e3).forEach((t2) => {
    let r2 = e3[t2], a2 = r2.isA.length;
    for (let t3 = 0; t3 < a2; t3++) {
      let a3 = r2.isA[t3];
      e3[a3] && (r2.isA = r2.isA.concat(e3[a3].isA));
    }
    r2.isA = (function(e4) {
      return e4.filter((e5, t3, r3) => r3.indexOf(e5) === t3);
    })(r2.isA);
  }), e3;
}, Jt = function(e3) {
  let t2 = Object.keys(e3);
  return t2.forEach((r2) => {
    let a2 = e3[r2];
    a2.notA = a2.notA || [], a2.isA.forEach((t3) => {
      if (e3[t3] && e3[t3].notA) {
        let r3 = "string" == typeof e3[t3].notA ? [e3[t3].isA] : e3[t3].notA || [];
        a2.notA = a2.notA.concat(r3);
      }
    });
    for (let n2 = 0; n2 < t2.length; n2++) {
      const i2 = t2[n2];
      -1 !== e3[i2].notA.indexOf(r2) && a2.notA.push(i2);
    }
    a2.notA = (function(e4) {
      return e4.filter((e5, t3, r3) => r3.indexOf(e5) === t3);
    })(a2.notA);
  }), e3;
}, Lt = function(e3) {
  let t2 = Object.keys(e3);
  return t2.forEach((r2) => {
    let a2 = e3[r2];
    a2.lineage = [];
    for (let n2 = 0; n2 < t2.length; n2++) -1 !== e3[t2[n2]].isA.indexOf(r2) && a2.lineage.push(t2[n2]);
  }), e3;
};
var St = function(e3) {
  return e3 = (function(e4) {
    return Object.keys(e4).forEach((t2) => {
      let r2 = e4[t2];
      r2.isA = r2.isA || [], "string" == typeof r2.isA && (r2.isA = [r2.isA]), r2.notA = r2.notA || [], "string" == typeof r2.notA && (r2.notA = [r2.notA]);
    }), e4;
  })(e3), e3 = Mt(e3), e3 = Jt(e3), e3 = Vt(e3), e3 = Lt(e3);
};
const _t = { Noun: { notA: ["Verb", "Adjective", "Adverb"] }, Singular: { isA: "Noun", notA: "Plural" }, ProperNoun: { isA: "Noun" }, Person: { isA: ["ProperNoun", "Singular"], notA: ["Place", "Organization", "Date"] }, FirstName: { isA: "Person" }, MaleName: { isA: "FirstName", notA: ["FemaleName", "LastName"] }, FemaleName: { isA: "FirstName", notA: ["MaleName", "LastName"] }, LastName: { isA: "Person", notA: ["FirstName"] }, NickName: { isA: "Person", notA: ["FirstName", "LastName"] }, Honorific: { isA: "Noun", notA: ["FirstName", "LastName", "Value"] }, Place: { isA: "Singular", notA: ["Person", "Organization"] }, Country: { isA: ["Place", "ProperNoun"], notA: ["City"] }, City: { isA: ["Place", "ProperNoun"], notA: ["Country"] }, Region: { isA: ["Place", "ProperNoun"] }, Address: { isA: "Place" }, Organization: { isA: ["Singular", "ProperNoun"], notA: ["Person", "Place"] }, SportsTeam: { isA: "Organization" }, School: { isA: "Organization" }, Company: { isA: "Organization" }, Plural: { isA: "Noun", notA: ["Singular"] }, Uncountable: { isA: "Noun" }, Pronoun: { isA: "Noun", notA: It }, Actor: { isA: "Noun", notA: It }, Activity: { isA: "Noun", notA: ["Person", "Place"] }, Unit: { isA: "Noun", notA: It }, Demonym: { isA: ["Noun", "ProperNoun"], notA: It }, Possessive: { isA: "Noun" } }, Kt = { Verb: { notA: ["Noun", "Adjective", "Adverb", "Value", "Expression"] }, PresentTense: { isA: "Verb", notA: ["PastTense", "FutureTense"] }, Infinitive: { isA: "PresentTense", notA: ["PastTense", "Gerund"] }, Imperative: { isA: "Infinitive" }, Gerund: { isA: "PresentTense", notA: ["PastTense", "Copula", "FutureTense"] }, PastTense: { isA: "Verb", notA: ["FutureTense"] }, FutureTense: { isA: "Verb" }, Copula: { isA: "Verb" }, Modal: { isA: "Verb", notA: ["Infinitive"] }, PerfectTense: { isA: "Verb", notA: "Gerund" }, Pluperfect: { isA: "Verb" }, Participle: { isA: "PastTense" }, PhrasalVerb: { isA: "Verb" }, Particle: { isA: "PhrasalVerb" }, Auxiliary: { notA: ["Noun", "Adjective", "Value"] } }, qt = { Value: { notA: ["Verb", "Adjective", "Adverb"] }, Ordinal: { isA: "Value", notA: ["Cardinal"] }, Cardinal: { isA: "Value", notA: ["Ordinal"] }, Fraction: { isA: "Value", notA: ["Noun"] }, RomanNumeral: { isA: "Cardinal", notA: ["Ordinal", "TextValue"] }, TextValue: { isA: "Value", notA: ["NumericValue"] }, NumericValue: { isA: "Value", notA: ["TextValue"] }, Money: { isA: "Cardinal" }, Percent: { isA: "Value" } }, Wt = { Adjective: { notA: ["Noun", "Verb", "Adverb", "Value"] }, Comparable: { isA: ["Adjective"] }, Comparative: { isA: ["Adjective"] }, Superlative: { isA: ["Adjective"], notA: ["Comparative"] }, NumberRange: {}, Adverb: { notA: ["Noun", "Verb", "Adjective", "Value"] }, Date: { notA: ["Verb", "Adverb", "Preposition", "Adjective"] }, Month: { isA: ["Date", "Singular"], notA: ["Year", "WeekDay", "Time"] }, WeekDay: { isA: ["Date", "Noun"] }, Timezone: { isA: ["Date", "Noun"], notA: ["Adjective", "ProperNoun"] }, Time: { isA: ["Date"], notA: ["AtMention"] }, Determiner: { notA: Ot }, Conjunction: { notA: Ot }, Preposition: { notA: Ot }, QuestionWord: { notA: ["Determiner"] }, Currency: { isA: ["Noun"] }, Expression: { notA: ["Noun", "Adjective", "Verb", "Adverb"] }, Abbreviation: {}, Url: { notA: ["HashTag", "PhoneNumber", "Verb", "Adjective", "Value", "AtMention", "Email"] }, PhoneNumber: { notA: ["HashTag", "Verb", "Adjective", "Value", "AtMention", "Email"] }, HashTag: {}, AtMention: { isA: ["Noun"], notA: ["HashTag", "Verb", "Adjective", "Value", "Email"] }, Emoji: { notA: ["HashTag", "Verb", "Adjective", "Value", "AtMention"] }, Emoticon: { notA: ["HashTag", "Verb", "Adjective", "Value", "AtMention"] }, Email: { notA: ["HashTag", "Verb", "Adjective", "Value", "AtMention"] }, Acronym: { notA: ["Plural", "RomanNumeral"] }, Negative: { notA: ["Noun", "Adjective", "Value"] }, Condition: { notA: ["Verb", "Adjective", "Noun", "Value"] } }, Rt = St, Ut = function(e3, t2) {
  Object.keys(e3).forEach((r2) => {
    t2[r2] = e3[r2];
  });
};
var Qt = (() => {
  let e3 = {};
  return Ut(_t, e3), Ut(Kt, e3), Ut(qt, e3), Ut(Wt, e3), e3 = Rt(e3), e3;
})();
const Zt = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ", Xt = Zt.split("").reduce((function(e3, t2, r2) {
  return e3[t2] = r2, e3;
}), {});
var Yt = function(e3) {
  if (void 0 !== Xt[e3]) return Xt[e3];
  let t2 = 0, r2 = 1, a2 = 36, n2 = 1;
  for (; r2 < e3.length; t2 += a2, r2++, a2 *= 36) ;
  for (let r3 = e3.length - 1; r3 >= 0; r3--, n2 *= 36) {
    let a3 = e3.charCodeAt(r3) - 48;
    a3 > 10 && (a3 -= 7), t2 += a3 * n2;
  }
  return t2;
};
const er = function(e3, t2, r2) {
  const a2 = Yt(t2);
  return a2 < e3.symCount ? e3.syms[a2] : r2 + a2 + 1 - e3.symCount;
};
var tr = function(e3) {
  const t2 = { nodes: e3.split(";"), syms: [], symCount: 0 };
  return e3.match(":") && (function(e4) {
    const t3 = new RegExp("([0-9A-Z]+):([0-9A-Z]+)");
    for (let r2 = 0; r2 < e4.nodes.length; r2++) {
      const a2 = t3.exec(e4.nodes[r2]);
      if (!a2) {
        e4.symCount = r2;
        break;
      }
      e4.syms[Yt(a2[1])] = Yt(a2[2]);
    }
    e4.nodes = e4.nodes.slice(e4.symCount, e4.nodes.length);
  })(t2), (function(e4) {
    const t3 = [], r2 = (a2, n2) => {
      let i2 = e4.nodes[a2];
      "!" === i2[0] && (t3.push(n2), i2 = i2.slice(1));
      const o2 = i2.split(/([A-Z0-9,]+)/g);
      for (let i3 = 0; i3 < o2.length; i3 += 2) {
        const s2 = o2[i3], l2 = o2[i3 + 1];
        if (!s2) continue;
        const u2 = n2 + s2;
        if ("," === l2 || void 0 === l2) {
          t3.push(u2);
          continue;
        }
        const c2 = er(e4, l2, a2);
        r2(c2, u2);
      }
    };
    return r2(0, ""), t3;
  })(t2);
};
const rr = { Comparative: "true¦better", Superlative: "true¦earlier", PresentTense: "true¦is,sounds", Value: "true¦a few", Noun: "true¦a5b4c2f1here,ie,lit,m0no doubt,pd,tce;a,d;t,y;a,ca,o0;l,rp;a,l;d,l,rc", Copula: "true¦a1is,w0;as,ere;m,re", PastTense: "true¦be3came,d2had,lied,meant,sa2taken,w0;as,e0;nt,re;id;en,gan", Condition: "true¦if,lest,unless", Preposition: "true¦'o,-,aLbIcHdGexcept,fFiDmidQnotwithstandiRoBpSqua,sAt6u3vi2w0;/o,hereNith0;!in,oR;a,s-a-vis;n1p0;!on;like,til;h0ill,owards;an,r0;ough0u;!oJ;ans,ince,o that;',f0n2ut;!f;f,n0;!to;or,rom;espite,own,u3;hez,irca;ar1e0oAy;sides,tween;ri6;',bo7cross,ft6lo5m3propos,round,s1t0;!op;! long 0;as;id0ong0;!st;ng;er;ut", Gerund: "true¦accord0be0develop0go0result0stain0;ing", Negative: "true¦n0;ever,o0;!n,t", QuestionWord: "true¦how3wh0;at,e1ich,o0y;!m,se;n,re; come,'s", Plural: "true¦records", Conjunction: "true¦&,aFbBcuz,how9in caEno8o7p5supposing,t2v1wh0yet;eth9ile;ers4s;h0o;eref9o0;!uC;l0rovided that;us;r,therwi6; matt1r;!ev0;er;e0ut;cau1f0;ore;se;lthou1nd,s 0;far as,if;gh", Abbreviation: "true¦a0Jb0Gc0Ad08e05f02g01h00iYjWkanVlTmNnKoJpFque,rDs8t6u5v2w0;is0r,y0B;!c;a,b,e1i0ol,s,t;tro,vo;r,t;niv,safa,t;ce,e0;l,mp,nn,x;ask,e2fc,gt,i1q,r,s,t,u0;pt,rg;r,tu;c,nJp0;!t;b,d,e0;pGs,v;a,d,ennNhd,l,p,r1s0vt;!eud;ef,o0;b,f,n;ct,kla,nt;e0ov;b0e;!r;a4d,essrs,i1lle,me,r7s0t;!tr;n1s0;c,ter;!n;!j,r,sc;at,it,lb,ng,t0;!d;!s;an,d,r,u0;l,n;a,da,e,n0;c,f;on,wy;a,en,ov;e1ig,l0m,r,t,y;!a;b,m;a,g,ng,s1tc,x0;!p;p,q,t;ak,e0ist,r;c,f,pt,t;a3ca,l,m2o0pl,res,yn;!l0m1nn,rp;!o;dr;!l0pt;!if;a,c,l1r0;ig,os;!dg,vd;d4l3p2r1ss0tty,ug,ve;n,t;c,iz;prox,r,t;!ta;!j,m,v", Pronoun: "true¦'em,elle,h4i3me,ourselves,she5th1us,we,you0;!rself;e0ou;m,y;!l,t;e0im;!'s", Singular: "true¦0:16;1:13;2:19;a16b0Tc0Kd0De0Af05g00hWiVjel0kitty,lTmPnOoNpHquestionGrEs9t6u4w3;ay,om03;nc10s 3;doll0Lst0N; rex,a4h3ic,ragedy,v show;ere,i2;l0x return;i6ky,omeoNt3uper bowl,yst15;ep4ri2u3;de0Yff;faTmoT;st1ze;al0i2o3;om,se;! mark;a7i1la6r4u3;dQrpoI;e3ie0Hobl0V;roga00ss releaG;te,y1;rt,te0N;bjWceJthers,verview;othi2umb1;a5ee08o3;del,m3nopo0rni2th1;!my;n,yf0;i3unch;ne;ci2nsect;ead start,o3uman right;l0me4u3;se;! run;adf0entlem6irl02laci1od,rand4u3;l0y; slam,fa3mo3;th1;an;a6ella,ly,ol0r4un3;di2;ee market,iWo3;nti1sP;mi0th1;conomy,gg,ner7ven4x3;ampTecu9;i2t;ad8e5inn1o3ragonf0ude;cumentGg3i0l0or;gy;ath,t3;ec3;tive;!dy;a9eili2h7i5o3redit card;ttage,u3;ri1sin;ty,vil w3;ar;andeli1ocol3;ate;n3rF;ary;aCel0lesJo8r5u3;n3tterf0;ti2;eakfa4o3;!th1;st;dy,tt5y3;!fri3;end;le;nki2r3;ri1;er;d5l0noma0u3;nt;ly; homin5verti3;si2;ng;em", FemaleName: "true¦0:J3;1:J7;2:IG;3:IF;4:IX;5:IK;6:JO;7:H0;8:JG;9:JK;A:HN;B:HY;C:IT;D:IP;E:JD;F:HC;G:I0;aGRbFLcDPdCYeBOfB4gADh9Ti9Gj8Gk7Gl60m49n3No3Jp37qu36r2Ds16t0Eu0Cv02wVxiTyOzH;aLeIineb,oHsof2;e3Uf2la,ra;h3iKlIna,ynH;ab,ep;da,ma;da,h3iHra;nab;aKeJi0Fol5BuIvH;etAonDO;i0na;le0sen2;el,gm3Jn,rGJs8W;aoHme0nyi;m62yAE;aMendDYhiDFiH;dele8lJnH;if48niHo0;e,f47;a,helmi0lHma;a,ow;ka0nB;aNeKiHusa5;cIktoriBMlAole7viH;anC3enJ0;kF9tor2;da,lA9nus,rHs0;a,nHoniH4;a,iFQ;leHnesH4;nIHrH;i1y;g8rHxH5;su5te;aYeUhRiNoLrIuHy3;i,la;acIZiHu0L;c2na,sH;hBPta;nHr0H;iBNya;aJffaEOnHs6;a,gtiH;ng;!nFQra;aIeHomasi0;a,l9Po8Ares1;l2ndolwethu;g9Go88rIssH;!a,ie;eHi,ri9;sa,za;bPlNmLnJrIs6tHwa0;ia0um;a63yn;iHya;a,ka,s6;arB6e3iHmEDra;!ka;a,iH;a,t6;at6it6;a0Fcarlet3We0BhXiTkye,neza0oRtNuIyH;bIBlvi1;e,ha,mayIEni7sIzH;an3MetAie,y;anHi9;!a,e,nH;aDe;aJeH;fHl5GphH;an4;cHZr5;b2fiA8m0OnHphi1;d3ia,ja,ya;er3lJmon1nIobh8PtH;a,i;dy;lEPv2;aMeIirHo0risF7y5;a,lDK;ba,e0i5lJrH;iHrDOyl;!d8Hfa;ia,lDX;hd,iMki3nJrIu0w0yH;la,ma,na;i,le8on,ron;aIda,ia,nHon;a,on;!ya;k6mH;!aa;lJrItaye81vH;da,inj;e0ife;en1i0ma;anA5bNd3Nh1RiBkMlLmJndIrHs6vannaD;aDi0;ra,y;aHi3;nt6ra;lDKma,ome;ee0in8Ru3;in1ri0;a05e00hYiVoIuH;by,thDH;bScRghQl2KnPsJwIxH;anAXie,y;an,e0;aIeHie,lE; merBLann9ll1marDBt7;!lHnn1;iHyn;e,nH;a,d9K;da,i,na;ayy8D;hel62io;bDKer7yn;a,cIkHmas,n9Fta,ya;ki,o;helGki;ea,iannGDoH;da,n1K;an0bJem9Agi0iInHta,y0;a88ee;han83na;a,eH;cEAkaD;bi0chIe,i0mo0nHquEKvCy0;di,ia;aEIelHiB;!e,le;een4ia0;aNeMhKipaluk,oJrHute66;iHudenCQ;scil3LyamvaB;lly,rt2;ilome0oebe,ylH;is,lis;arl,ggy,nelope,r5t3;ige,m0TnKo5rvaDGtIulH;a,etAin1;ricHsy,tBY;a,e,ia;do3i06;ctav2dIfCZis6lHphCZumC3yunbileg;a,ga,iv2;eHvAC;l2tA;aWeUiMoIurHy5;!ay,ul;a,eJor,rIuH;f,r;aDeCma;ll1mi;aNcLhariBOkKlaJna,sHta,vi;anHha;ur;!y;a,iDTki;hoGk9VolH;a,eDJ;!mh;hir,lHna,risFsreC;!a,lBT;asuLdKh2i6CnJomi9rgEPtHzanin zah3;aHhal4;li1s6;cy,etA;a,e8iEV;nngu30;a09ckenz4e01iMoJrignayani,uriDDyH;a,rH;a,lNna,tG;bi0i3llBInH;a,iH;ca,ka,qD3;a,cTkaSlNmi,nLrItzi,yH;ar;aIiam,lH;anEO;!l,nB;dy,eHh,n4;nhGrva;aKdJiCPlH;iHy;cent,e;red;!gros;!e5;ae5hH;ae5el3Z;ag5EgNi,lKrH;edi79iIjem,on,yH;em,l;em,sF;an4iHliF;nHsCE;a,da;!an,han;b0DcASd0Be,g09ha,i08ja,l06n04rLsoum60tKuIv82x9IyHz4;a,bell,ra,soB9;de,rH;a,eC;h8Fild1t4;a,cYgUiKjor4l7Sn4s6tJwa,yH;!aHbe6Wja8lAE;m,nBH;a,ha,in1;!aJbCBeIja,lEna,sHt64;!a,ol,sa;!l1H;! Jh,mInH;!a,e,n1;!awit,i;aliAHcJeduarBfernIjHlui5Y;o6Ful2;anB;ecil2la3;arJeIie,oHr44ueriA;!t;!ry;et42i37;el4Ui76y;dHon,ue5;akran7y;ak,en,iHk,lo3O;a,ka,nB;a,re,s4te;daHg4;!l3A;alEd4elHge,isDBon0;ei8in1yn;el,le;a0Ne0CiYoQuLyH;d2la,nH;!a,dIeBGnHsCL;!a,eBF;a,sCJ;aCWcJel0PiFlIna,pHz;e,i7;a,u,wa;iHy;a0Se,ja,l2JnB;is,l1SrJttIuHvel4;el5is1;e,ie;aKeIi9na,rH;a86i9;lHn1t7;ei;!in1;aSbb9CdRepa,lMnJsIv2zH;!a,be5LetAz4;a,etA;!a,dH;a,sHy;ay,ey,i,y;a,iJja,lHy;iHy;aA0e;!aH;!n5F;ia,ya;!nH;!a,ne;aPda,e0iNjYla,nMoKsJtHx4y5;iHt4;c2t2;e2LlCG;la,nHra;a,ie,o3;a,or1;a,gh,laH;!ni;!h,nH;a,d3e,n5P;cOdon97iNkes6mi9Ana,rMtJurIvHxmi,y5;ern1in2;a,e54ie,yn;as6iIoH;nya,ya;fa,s6;a,isF;a,la;ey,ie,y;a04eZhXiOlAKoNrJyH;lHra;a,ee,ie;istHy6D;a,en,iIyH;!na;!e,n59;nul,ri,urtnB0;aOerNlAZmJrHzzy;a,stH;en,in;!berlImernH;aq;eHi,y;e,y;a,stC;!na,ra;aHei3ongordzol;dij1w5;el7QiKjsi,lJnIrH;a,i,ri;d3na,za;ey,i,lBDs4y;ra,s6;bi7cAJdiat7IeB2iRlQmPnyakuma19rNss6KtKvi7yH;!e,lH;a,eH;e,i8L;a6DeIhHi4NlEri0y;ar6Ber6Bie,leCrB2y;!lyn8Gri0;a,en,iHl5Soli0yn;!ma,n3VsF;a5il1;ei8Ei,l4;a,tl6L;a07eYiVoNuH;anLdKliHst63;a8HeHsF;!n8tH;!a,te;e5Ji3Jy;a,i7;!anNcelEd6RelGhan7RlLni,sIva0yH;a,ce;eHie;fHlEph5U;a,in1;eHie;en,n1;!a,e,n41;lHng;!i1ClH;!i1B;anMle0nJrIsH;i8Csi8C;i,ri;!a,elGif2CnH;a,etAiHy;!e,f2A;a,e8EiInH;a,e8DiH;e,n1;cMd1mi,nIque4Xsmin3Ovie3y8zH;min9;a9eIiH;ce,e,n1s;!lHsFt0F;e,le;inIk4lEquelH;in1yn;da,ta;lRmPnOo0rNsIvaHzaro;!a0lu,na;aJiIlaHob84;!n9N;do3;!belHdo3;!a,e,l39;a77en1i0ma;a,di3es,gr6Yji;a8elBogH;en1;a,e8iHo0se;a0na;aSeOiJoHusFyacin2B;da,ll4rten23snH;a,i9Q;lImaH;ri;aIdHlaI;a,egard;ry;ath1CiJlInriet7rmi8sH;sa,t1B;en2Sga,mi;di;bi2Dil8IlNnMrJsItHwa,yl8Iz7H;i5St4;n5Yti;iHmo51ri52;etH;!te;aDnaD;a,ey,l4;a03eXiSlQoOrKunJwH;enHyne1Q;!dolE;ay,el;acIetHiselB;a,chC;e,ieH;!la;ld1AogooH;sh;adys,enHor2yn2H;a,da,na;aKgi,lIna,ov89selHta;a,e,le;da,liH;an;!n0;mLnJorgIrH;ald3Pi,m3Ctru8B;etAi4W;a,eHna;s26vieve;ma;bIil,le,mHrnet,yG;al5Ni5;i5FrielH;a,l1;aVeSiRloOoz2rH;anJeIiH;da,eB;da,ja;!cH;esIiHoi0O;n1s61;!ca;!rH;a,encH;e,ia;en,o0;lIn0rnH;!anB;ec2ic2;jr,n7rKtHy9;emIiHma,ouma7;ha,ma,n;eh;ah,iBrah,za0;cr4Nd0Ne0Mi0Lk7l04mWn4YrTsNtMuLvH;aJelIiH;!e,ta;in0Gyn;!ngel2S;geni1la,ni45;h5Sta;mLperanKtH;eIhHrel5;er;l30r9;za;a,eralB;iHma,nest2Jyn;cHka,n;a,ka;a,eMiJmH;aHie,y;!li8;lHn1;ee,iHy;a,e,ja;lHrald;da,y;aWeUiNlMma,no3oKsJvH;a,iH;na,ra;a,ie;iHuiH;se;a,en,ie,y;a0c2da,f,nMsJzaH;!betHve7;e,h;aHe,ka;!beH;th;!a,or;anor,nH;!a;!in1na;leCs6;vi;eIiHna,wi0;e,th;l,n;aYeMh2iLjeneKoHul30;lor5Tminiq4In3FrHtt4;a,eCis,la,othHthy;ea,y;ba;an0AnaDon8x4ya;anQbPde,eOiMja,lJmetr2nHsir5K;a,iH;ce,se;a,iIla,orHphi8;es,is;a,l6D;dHrdH;re;!d5Cna;!b2HoraDra;a,d3nH;!a,e;hl2i0l0HmNnLphn1rIvi1XyH;le,na;a,by,cIia,lH;a,en1;ey,ie;a,etAiH;!ca,el1Cka,z;arHia;is;a0Se0Oh05i03lVoKrIynH;di,th2;istHy05;al,i0;lPnMrIurH;tn1E;aJd2NiHn2Nri8;!nH;a,e,n1;!l1X;cepci59n4sH;tanHuelo;ce,za;eHleC;en,tA;aJeoIotH;il51;!pat3;ir9rJudH;etAiH;a,ne;a,e,iH;ce,sZ;a3er3ndH;i,y;aReNloe,rH;isJyH;stH;al;sy,tH;a1Ren,iHy;!an1e,n1;deJlseIrH;!i9yl;a,y;li8;nMrH;isKlImH;ai8;a,eHotA;n1tA;!sa;d3elGtH;al,elG;cIlH;esAi44;el2ilH;e,ia,y;itlZlYmilXndWrOsMtHy5;aKeJhHri0;erHleCrEy;in1;ri0;li0ri0;a33sH;a32ie;a,iNlLmeJolIrH;ie,ol;!e,in1yn;lHn;!a,la;a,eHie,o7y;ne,y;na,sF;a0Hi0H;a,e,l1;is7l4;in,yn;a0Ie02iZlXoUrH;andSeQiJoIyH;an0nn;nwEok9;an3DdgLg0XtH;n2XtH;!aInH;ey,i,y;ny;etH;!t9;an0e,nH;da,na;i9y;bbi9glarIlo05nH;i7n4;ka;ancHossom,ythe;a,he;an17lja0nHsm3I;i7tH;ou;aUcky,linTni7rPssOtJulaDvH;!erlH;ey,y;hJsy,tH;e,iHy9;e,na;!anH;ie,y;!ie;nHt6yl;adIiH;ce;etAi8;ay,da;!triH;ce,z;rbJyaH;rmH;aa;a3ie,o3ra;a2Sb2Md23g1Zi1Qj5l16m0Xn09oi,r04sUtTuPvOwa,yIzH;ra,u0;aKes6gJlIseH;!l;in;un;!nH;a,na;a,i2Ir2J;drJgus1RrIsteH;ja;el2;a,ey,i,y;aahua,he0;hIi2Gja,mi7s2DtrH;id;aMlIraqHt21;at;eIi9yH;!n;e,iHy;gh;!nH;ti;iJleIo6pi7;ta;en,n1tA;aHelG;!n1J;a00dje5eYgUiSjQnJohito,toHya;inetAnH;el5ia;!aKeIiHmJ;e,ka;!mHtA;ar4;!belIliFmU;sa;!le;a,eliH;ca;ka,sHta;a,sa;elHie;a,iH;a,ca,n1qH;ue;!tA;te;! JbImHstasiNya;ar2;el;cla3jul2pau5;aLberKeliJiHy;e,l2naH;!ta;a,ja;!ly;hGiIl2nB;da;a,ra;le;aWba,ePiMlKma,thJyH;a,c2sH;a,on,sa;ea;iHys0N;e,s0M;a,cIn1sHza;a,e,ha,on,sa;e,ia,ja;c2is6jaKksaKna,sJxH;aHia;!nd3;ia,saH;nd3;ra;ia;i0nIyH;ah,na;a,is,naDoud;la;c6da,leCmNnLsH;haDlH;inHyY;g,n;!h;a,o,slH;ey;ee;en;at6g4nIusH;ti0;es;ie;aWdiTelMrH;eJiH;anMenH;a,e,ne;an0;na;!aLeKiIyH;nn;a,n1;a,e;!ne;!iH;de;e,lEsH;on;yn;!lH;i8yn;ne;aKbIiHrL;!gaK;ey,i9y;!e;gaH;il;dKliyJradhIs6;ha;ya;ah;a,ya", Actor: "true¦aJbGcFdCengineIfAgardenIh9instructPjournalLlawyIm8nurse,opeOp5r3s1t0;echnCherapK;ailNcientJecretary,oldiGu0;pervKrgeon;e0oofE;ceptionGsearC;hotographClumbColi1r0sychologF;actitionBogrammB;cem6t5;echanic,inist9us4;airdress8ousekeep8;arm7ire0;fight6m2;eputy,iet0;ici0;an;arpent2lerk;ricklay1ut0;ch0;er;ccoun6d2ge7r0ssis6ttenda7;chitect,t0;ist;minist1v0;is1;rat0;or;ta0;nt", Honorific: "true¦a01bYcQdPeOfiJgIhon,jr,king,lHmCoffic00p7queen,r3s0taoiseach,vice6;e1fc,gt,ir,r,u0;ltRpt,rg;cond liInBrgeaJ;abbi,e0;ar1p9s,v0;!erend; admirX;astOhd,r0vt;esideDi1of0;!essM;me mini4nce0;!ss;a3essrs,i2lle,me,r1s0;!tr;!s;stK;gistrate,j,r6yF;i3lb,t;en,ov;eld mar3rst l0;ady,i0;eutena0;nt;shG;sq,xcellency;et,oct6r,utchess;apt6hance4mdr,o0pl;lonel,m2ngress0unci3;m0wom0;an;dr,mand5;ll0;or;!ain;ldg,rig0;!adi0;er;d0sst,tty,yatullah;j,m0v;!ir0;al", SportsTeam: "true¦0:1A;1:1H;2:1G;a1Eb16c0Td0Kfc dallas,g0Ihouston 0Hindiana0Gjacksonville jagua0k0El0Bm01newToQpJqueens parkIreal salt lake,sAt5utah jazz,vancouver whitecaps,w3yW;ashington 3est ham0Rh10;natio1Oredski2wizar0W;ampa bay 6e5o3;ronto 3ttenham hotspur;blue ja0Mrapto0;nnessee tita2xasC;buccanee0ra0K;a7eattle 5heffield0Kporting kansas0Wt3;. louis 3oke0V;c1Frams;marine0s3;eah15ounG;cramento Rn 3;antonio spu0diego 3francisco gJjose earthquak1;char08paA; ran07;a8h5ittsburgh 4ortland t3;imbe0rail blaze0;pirat1steele0;il3oenix su2;adelphia 3li1;eagl1philNunE;dr1;akland 3klahoma city thunder,rlando magic;athle0Mrai3;de0; 3castle01;england 7orleans 6york 3;city fc,g4je0FknXme0Fred bul0Yy3;anke1;ian0D;pelica2sain0C;patrio0Brevolut3;ion;anchester Be9i3ontreal impact;ami 7lwaukee b6nnesota 3;t4u0Fvi3;kings;imberwolv1wi2;rewe0uc0K;dolphi2heat,marli2;mphis grizz3ts;li1;cXu08;a4eicesterVos angeles 3;clippe0dodDla9; galaxy,ke0;ansas city 3nE;chiefs,roya0E; pace0polis colU;astr06dynamo,rockeTtexa2;olden state warrio0reen bay pac3;ke0;.c.Aallas 7e3i05od5;nver 5troit 3;lio2pisto2ti3;ge0;broncZnuggeM;cowbo4maver3;ic00;ys; uQ;arCelKh8incinnati 6leveland 5ol3;orado r3umbus crew sc;api5ocki1;brow2cavalie0india2;bengaWre3;ds;arlotte horAicago 3;b4cubs,fire,wh3;iteB;ea0ulR;diff3olina panthe0; c3;ity;altimore 9lackburn rove0oston 5rooklyn 3uffalo bilN;ne3;ts;cel4red3; sox;tics;rs;oriol1rave2;rizona Ast8tlanta 3;brav1falco2h4u3;nited;aw9;ns;es;on villa,r3;os;c5di3;amondbac3;ks;ardi3;na3;ls", Uncountable: "true¦0:1J;a1Qb1Ic19d16e0Zf0Tg0Mh0Hi0Dj0Cknowled1Pl07mXnWoVpRrMsBt6vi5w1;a3ea0Ai2oo1;d,l;ldlife,ne;rmth,t0;neg16ol0Btae;e4h3oothpaste,r1una;affSou1;ble,sers,t;ermod1Lund0;a,nnis;a9cene09eri0Wh8il7kittl0Wnow,o6p4t2u1;g0Znshi0P;ati1Ke1;am,el;ace1De1;ci0Red;ap,cc0;k,v0;eep,ingl0O;d0Cfe17l1nd,tish;m10t;a4e2ic1;e,ke0L;c1laxa0Hsearch;ogni0Grea0G;bi0Hin;aOe3hys17last8o1ress03;l1rk,w0;it15y9;a11trY;bstetr13il,xygen;ational securi0Vews;a8e6ilk,o3u1;mps,s1;ic;n1o0G;ey,o1;gamy;a1chan0V;sl03t;chine1il,themat0T; learn09ry;aught0e3i2ogi0Qu1;ck,g0G;ce,ghtn06ngui0OteratL;a1isK;th0;ewel8usti0J;ce,mp1nformaStself;a1ortan0H;ti1;en0F;a4isto3o1;ck1mework,n1spitali09;ey;ry;ir,libut,ppiB;ene4o2r1um,ymna0B;aAound;l1ssip;d,f; 1t08;editOpo1;ol;i5lour,o2urnit1;ure;od,rgive1uri0wl;ne1;ss;c7sh;conomZduca6lectr5n3quip4thZvery1;body,o1thF;ne;joy1tertain1;ment;iciNonU;tiG;ar2iabet1raugh2;es;ts;a8elcius,h4ivPl3o1urrency;al,ld w1nfusiBttB;ar;assMoth3;aos,e1;e2w1;ing;se;r5sh;a5eef,i2lood,owls,read,utt0;er;lliar2s1;on;ds;g1ss;ga1;ge;c6dvi5ero3ir2mnes1rt,thlet8;ty;craft;b5d1naut5;ynam4;ce;id,ou1;st1;ics", Infinitive: "true¦0:6S;1:76;2:5C;3:74;4:73;5:67;6:6F;7:6Y;8:6Q;9:72;A:70;B:5X;C:6X;D:6L;E:77;F:5B;a6Kb66c57d4De3Xf3Jg3Dh37i2Uj2Sk2Ql2Hm26n23o1Yp1Jr0Rs06tYuTvOwHyG;awn,e31ield;aJe1Zhist6iIoGre6D;nd0rG;k,ry;pe,sh,th0;lk,nHrGsh,tDve;n,raE;d0t;aJiHoG;te,w;eGsC;!w;l6Jry;nHpGr4se;gra4Pli41;dGi9lo5Zpub3Q;erGo;mi5Cw1I;aMeLhKig5SoJrHuGwi7;ne,rn;aGe0Mi5Uu7y;de,in,nsf0p,v5J;r2ZuD;ank,reatB;nd,st;ke pa53lk,rg1Qs9;aZcWeVhTi4Dkip,lSmRnee3Lo52pQtJuGwitD;bmCck,ff0gge7ppHrGspe5;ge,pri1rou4Zvi3;ly,o36;aLeKoJrHuG;dy,mb6;aFeGi3;ngthBss,tD;p,re;m,p;in,ke,r0Qy;la58oil,rink6;e1Zi6o3J;am,ip;a2iv0oG;ck,rtBut;arDem,le5n1r3tt6;aHo2rG;atDew;le,re;il,ve;a05eIisk,oHuG;in,le,sh;am,ll;a01cZdu8fYgXje5lUmTnt,pQquPsKtJvGwa5V;eGiew,o36;al,l,rG;se,t;aFi2u44;eJi7oItG;!o2rG;i5uc20;l3rt;mb6nt,r3;e7i2;air,eHlGo43r0K;a8y;at;aFemb0i3Zo3;aHeGi3y;a1nt;te,x;a5Dr0J;act1Yer,le5u1;a13ei3k5PoGyc6;gni2Cnci6rd;ch,li2Bs5N;i1nG;ge,k;aTerSiRlOoMrIuG;b21ll,mp,rGsh;cha1s4Q;ai1eIiEoG;cGdu8greAhibCmi1te7vi2W;eAlaim;di5pa2ss,veE;iEp,rtr46sGur;e,t;aHead,uG;g,n4;n,y;ck,le;fo34mCsi7;ck,iErt4Mss,u1;bJccur,ff0pera9utweIverGwe;co47lap,ta22u1wG;helm;igh;ser3taF;eHotG;e,i8;ed,gle5;aMeLiIoHuG;ltip3Grd0;nit13ve;nHrr12sreprG;eseE;d,g6us;asu2lt,n0Nr4;intaFna4rHtG;ch,t0;ch,kGry;et;aMeLiJoGu1C;aHck,oGve;k,sB;d,n;ft,g35ke,mCnk,st2YveG;!n;a2Fc0Et;b0Nck,uG;gh,nD;iGno34;ck,ll,ss;am,oFuG;d4mp;gno2mQnGss3H;cOdica9flu0MhNsKtIvG;eGol3;nt,st;erGrodu8;a5fe2;i7tG;aGru5;ll;abCibC;lu1Fr1D;agi24pG;lemeEo22ro3;aKeIi2oHuG;nt,rry;n02pe,st;aGlp;d,t;nd6ppBrm,te;aKloAove1PrIuG;arGeAi15;ant39d;aGip,ow,umb6;b,sp;in,th0ze;aReaQiOlMoJrHuncG;ti3J;acGeshB;tu2;cus,lHrG;ce,eca7m,s30;d,l24;a1ZoG;at,od,w;gu2lGni1Xt,x;e,l;r,tu2;il,stBvG;or;a15cho,le5mSnPstNvalua9xG;a0AcLerKi7pGte19;a18eHi2laFoGreA;rt,se;ct,riG;en8;ci1t;el,han4;abGima9;li1J;ab6couXdHfor8ga4han8j03riDsu2t0vG;isi2Vy;!u2;body,er4pG;hasiGow0;ze;a07eUiLoKrHuG;mp;aHeAiG;ft;g,in;d4ubt;ff0p,re5sHvG;iZor8;aKcHliGmiApl1Btingui14;ke;oGuA;uGv0;ra4;gr1YppG;ear,ro3;cOeNfLliv0ma0Fny,pKsHterG;mi0G;cribe,er3iHtrG;oy;gn,re;a0Be0Ai5osC;eGi0By;at,ct;m,pB;iIlHrG;ea1;a2i06;de;ma4n8rGte;e,kB;a0Ae09h06i9l04oJrG;aHeGoAu0Hy;a9dC;ck,ve;llZmSnHok,py,uGv0;gh,nt;cePdu5fMsKtIvG;eGin8;rt,y;aFin0VrG;a7ibu9ol;iGtitu9;d0st;iHoGroE;rm;gu2rm;rn;biLfoKmaJpG;a2laF;in;re;nd;rt;ne;ap1e5;aGip,o1;im,w;aHeG;at,ck,w;llen4n4r4se;a1nt0;ll,ncIrGt0u1;eGry;!en;el;aSePloOoMrIuG;lGry;ly;igHuG;sh;htB;en;a7mb,o7rrGth0un8;ow;ck;ar,lHnefCtrG;ay;ie3ong;ng,se;band0Jc0Bd06ffo05gr04id,l01mu1nYppTrQsKttGvoid,waC;acIeHra5;ct;m0Fnd;h,k;k,sG;eIiHocia9uG;me;gn,st;mb6rt;le;chHgGri3;ue;!i3;eaJlIroG;aDve;ch;aud,y;l,r;noun8sw0tG;icipa9;ce;lHt0;er;e4ow;ee;rd;aRdIju7mCoR;it;st;!reA;ss;cJhie3knowled4tiva9;te;ge;ve;eIouEu1;se;nt;pt;on", Unit: "true¦0:19;a14b12c0Od0Ne0Lf0Gg0Ch09in0Hjoule0k02l00mNnMoLpIqHsqCt7volts,w6y4z3°2µ1;g,s;c,f,n;b,e2;a0Nb,d0Dears old,o1;tt0H;att0b;able4b3d,e2on1sp;!ne0;a2r0D;!l,sp;spo04; ft,uare 1;c0Id0Hf3i0Fkilo0Jm1ya0E;e0Mil1;e0li0H;eet0o0D;t,uart0;ascals,e2i1ou0Pt;c0Mnt0;rcent,t02;hms,uYz;an0JewtT;/s,b,e9g,i3l,m2p1²,³;h,s;!²;!/h,cro5l1;e1li08;! pFs1²;! 1;anEpD;g06s0B;gQter1;! 2s1;! 1;per second;b,i00m,u1x;men0x0;b,elvin0g,ilo2m1nR;!/h,ph,²;byZgXmeter1;! p2s1;! p1;er1; hour;e1g,r0z;ct1rtz0;aXogQ;al2b,igAra1;in0m0;!l1;on0;a4emtPl2t1;²,³; oz,uid ou1;nce0;hrenheit0rad0;b,x1;abyH;eciCg,l,mA;arat0eAg,m9oulomb0u1;bic 1p0;c5d4fo3i2meAya1;rd0;nch0;ot0;eci2;enti1;me4;!²,³;lsius0nti1;g2li1me1;ter0;ram0;bl,y1;te0;c4tt1;os1;eco1;nd0;re0;!s", Organization: "true¦0:46;a3Ab2Qc2Ad21e1Xf1Tg1Lh1Gi1Dj19k17l13m0Sn0Go0Dp07qu06rZsStFuBv8w3y1;amaha,m0Xou1w0X;gov,tu2S;a3e1orld trade organizati41;lls fargo,st1;fie22inghou16;l1rner br3D;-m11gree31l street journ25m11;an halNeriz3Wisa,o1;dafo2Gl1;kswagLvo;bs,kip,n2ps,s1;a tod2Rps;es35i1;lev2Xted natio2Uv; mobi2Kaco bePd bMeAgi frida9h3im horto2Tmz,o1witt2W;shiba,y1;ota,s r Y;e 1in lizzy;b3carpen33daily ma2Xguess w2holli0rolling st1Ms1w2;mashing pumpki2Ouprem0;ho;ea1lack eyed pe3Fyrds;ch bo1tl0;ys;l2s1;co,la m12;efoni07us;a6e4ieme2Gnp,o2pice gir5ta1ubaru;rbucks,to2N;ny,undgard1;en;a2Rx pisto1;ls;few25insbu26msu1X;.e.m.,adiohead,b6e3oyal 1yan2X;b1dutch she4;ank;/max,aders dige1Ed 1vl32;bu1c1Uhot chili peppe2Klobst28;ll;c,s;ant2Vizno2F;an5bs,e3fiz24hilip morrBi2r1;emier27octer & gamb1Rudenti14;nk floyd,zza hut;psi28tro1uge08;br2Qchina,n2Q; 2ason1Xda2G;ld navy,pec,range juli2xf1;am;us;a9b8e5fl,h4i3o1sa,wa;kia,tre dame,vart1;is;ke,ntendo,ss0K;l,s;c,st1Etflix,w1; 1sweek;kids on the block,york08;a,c;nd1Us2t1;ional aca2Fo,we0Q;a,cYd0O;aAcdonald9e5i3lb,o1tv,yspace;b1Nnsanto,ody blu0t1;ley crue,or0O;crosoft,t1;as,subisO;dica3rcedes2talli1;ca;!-benz;id,re;'s,s;c's milk,tt13z1Y;'ore09a3e1g,ittle caesa1Ktd;novo,x1;is,mark; pres5-z-boy,bour party;atv,fc,kk,m1od1K;art;iffy lu0Lo3pmorgan1sa;! cha1;se;hnson & johns1Sy d1R;bm,hop,n1tv;c,g,te1;l,rpol; & m,asbro,ewlett-packaTi3o1sbc,yundai;me dep1n1J;ot;tac1zbollah;hi;eneral 6hq,l5mb,o2reen d0Iu1;cci,ns n ros0;ldman sachs,o1;dye1g0B;ar;axo smith kliZencore;electr0Im1;oto0V;a3bi,da,edex,i1leetwood mac,oGrito-l0A;at,nancial1restoV; tim0;cebook,nnie mae;b06sa,u3xxon1; m1m1;ob0H;!rosceptics;aiml0Ae5isney,o3u1;nkin donuts,po0Wran dur1;an;j,w j1;on0;a,f leppa3ll,p2r spiegZstiny's chi1;ld;eche mode,t;rd;aEbc,hBi9nn,o3r1;aigsli5eedence clearwater reviv1ossra05;al;!ca c5l4m1o0Ast05;ca2p1;aq;st;dplMgate;ola;a,sco1tigroup;! systems;ev2i1;ck fil-a,na daily;r0Hy;dbury,pital o1rl's jr;ne;aGbc,eCfAl6mw,ni,o2p,r1;exiteeWos;ei3mbardiJston 1;glo1pizza;be;ng;ack & deckFo2ue c1;roX;ckbuster video,omingda1;le; g1g1;oodriN;cht3e ge0n & jer2rkshire hathaw1;ay;ryH;el;nana republ3s1xt5y5;f,kin robbi1;ns;ic;bXcSdidRerosmith,ig,lLmFnheuser-busEol,ppleAr7s3t&t,v2y1;er;is,on;hland2s1;n,ociated F; o1;il;by4g2m1;co;os; compu2bee1;'s;te1;rs;ch;c,d,erican3t1;!r1;ak; ex1;pre1;ss; 4catel2t1;air;!-luce1;nt;jazeera,qae1;da;as;/dc,a3er,t1;ivisi1;on;demy of scienc0;es;ba,c", Demonym: "true¦0:16;1:13;a0Wb0Nc0Cd0Ae09f07g04h02iYjVkTlPmLnIomHpDqatari,rBs7t5u4v3wel0Rz2;am0Fimbabwe0;enezuel0ietnam0H;g9krai1;aiwThai,rinida0Iu2;ni0Qrkmen;a4cot0Ke3ingapoOlovak,oma0Tpa05udRw2y0X;edi0Kiss;negal0Br08;mo0uU;o6us0Lw2;and0;a3eru0Hhilipp0Po2;li0Ertugu06;kist3lesti1na2raguay0;ma1;ani;amiZi2orweP;caragu0geri2;an,en;a3ex0Mo2;ngo0Erocc0;cedo1la2;gasy,y08;a4eb9i2;b2thua1;e0Dy0;o,t02;azakh,eny0o2uwaiti;re0;a2orda1;ma0Bp2;anN;celandic,nd4r2sraeli,ta02vo06;a2iT;ni0qi;i0oneV;aiDin2ondur0unN;di;amDe2hanai0reek,uatemal0;or2rm0;gi0;i2ren7;lipino,n4;cuadoVgyp6ngliJsto1thiopi0urope0;a2ominXut4;niH;a9h6o4roa3ub0ze2;ch;ti0;lom2ngol5;bi0;a6i2;le0n2;ese;lifor1m2na3;bo2eroo1;di0;angladeshi,el8o6r3ul2;gaG;aziBi2;ti2;sh;li2s1;vi0;aru2gi0;si0;fAl7merBngol0r5si0us2;sie,tr2;a2i0;li0;gent2me1;ine;ba1ge2;ri0;ni0;gh0r2;ic0;an", Possessive: "true¦anyAh5its,m3noCo1sometBthe0yo1;ir1mselves;ur0;!s;i8y0;!se4;er1i0;mse2s;!s0;!e0;lf;o1t0;hing;ne", Currency: "true¦$,aud,bScQdLeurKfJgbp,hkd,iIjpy,kGlEp8r7s3usd,x2y1z0¢,£,¥,ден,лв,руб,฿,₡,₨,€,₭,﷼;lotySł;en,uanR;af,of;h0t5;e0il5;k0q0;elM;iel,oubleLp,upeeL;e2ound st0;er0;lingI;n0soH;ceGn0;ies,y;e0i8;i,mpi7;n,r0wanzaCyatC;!onaBw;ls,nr;ori7ranc9;!o8;en3i2kk,o0;b0ll2;ra5;me4n0rham4;ar3;ad,e0ny;nt1;aht,itcoin0;!s", City: "true¦0:73;1:61;2:6G;3:5U;4:5R;a68b54c4Id4Ae46f3Yg3Jh38i2Zj2Uk2Dl22m1Kn19o16p0Uq0Sr0Ls01tPuOvLwDxiBy9z5;a7h5i4Muri4O;a5e5ongsh0;ng3J;greb,nzib5G;ang2e5okoha3Uunfu;katerin3Jrev0;a5n0O;m5Hn;arsBeAi6roclBu5;h0xi,zh5P;c7n5;d5nipeg,terth4;hoek,s1K;hi5Zkl3C;l63xford;aw;a6ern2i5ladivost5Molgogr6K;en3lni6R;lenc6Dncouv2Yr3ughn;lan bat1Drumqi,trecht;aDbilisi,eCheBi9o8r7u5;l21n63r5;in,ku;ipoli,ondh62;kyo,m34ron1QulouS;an5jua3l2Zmisoa6Era3;j4Xshui; hag65ssaloni2L;gucigal28hr0l av1W;briz,i6llinn,mpe5Ang5rtu,shk2X;i2Msh0;an,chu1n0p2Iyu0;aEeDh8kopje,owe1It7u5ydney;ra5zh51;ba0Jt;aten is59ockholm,rasbou6Auttga31;an8e6i5;jiazhua1llo1m60y0;f54n5;ya1zh4L;gh3Ot4U;att4Ao1Yv49;cramen18int DlBn5o paulo,ppo3Wrajevo; 7aa,t5;a 5ia3Io domin3I;a3fe,m1O;antonCdie3Gfrancisco,j5ped3Ssalv8;o5u0;se;em,v5z2B;ad0I;lou59peters29;aAe9i7o5;me,sar5t5A;io;ga,o5yadh;! de janei3I;cife,ykjavik;b4Uip4lei2Mnc2Swalpindi;ingdao,u5;ez2i0Q;aEeDhCiBo8r7u6yong5;ya1;eb5Aya1;ag54etor53;rt5zn0; 5la4Fo;au prin0Nelizabe29sa05;ls3Srae5Ctts2B;iladelph4Ynom pe1Doenix;r26tah tik3I;ler00naji,r4Pt5;na,r36;ak47des0Lm1Rr6s5ttawa;a3Ylo;an,d07;a8ew6i5ovosibir1Oyc;ng2Hs; 5cast39;del27orlea46taip16york;g8iro4Xn5pl2Zshv36v0;ch6ji1t5;es,o1;a1o1;a6o5p4;ya;no,sa0Y;aFeCi9o6u5;mb2Cni28sc40;gadishu,nt6s5;c17ul;evideo,re31;ami,l6n18s5;kolc,sissauga;an,waukee;cca,d5lbour2Pmph41;an,ell5i3;in,ín;cau,drAkass2Tl9n8r5shh4A;aca6ib5rakesh,se2N;or;i1Ty;a4EchEdal12i47;mo;id;aCeiAi8o6u5vRy2;anLckn0Rdhia3;n5s angel28;d2g bea1O;brev2De3Kma5nz,sb2verpo2A;!ss29;c5pzig;est0C; p6g5ho2Yn0Gusan27;os;az,la35;aHharFiClaipeBo9rak0Hu7y5;iv,o5;to;ala lump4n5;mi1sh0;be,hi0Llka2Zpavog4si5wlo2;ce;da;ev,n5rkuk;gSsha5;sa;k5toum;iv;bIdu3llakuric0Tmpa3Gn6ohsiu1ra5un1Lwaguc0T;c0Sj;d5o,p4;ah1Vy;a7e6i5ohannesZ;l1Xn0;dd37rusalem;ip4k5;ar2J;bad0mph1QnBrkutYs8ta01z5̇zm7;m6tapala5;pa;ir;fah0l6tanb5;ul;am2Zi2I;che2d5;ianap2Lo21;aBe8o5yder2W; chi mi6ms,nolulu,u5;st2;nh;f6lsin5rakli2;ki;ei;ifa,lifax,m7n5rb1Dva3;gAnov5oi;er;bu2Wilt2;aFdanEenDhCiPlasgBo9raz,u5;a5jr21;dal6ng5yaquil;zh1H;aja2Lupe;ld coa18then5;bu2P;ow;ent;e0Toa;sk;lw7n5za;dhi5gt1C;nag0S;ay;aisal26es,o8r6ukuya5;ma;ankfu5esno;rt;rt5sh0; wor6ale5;za;th;d5indhov0Nl paso;in5mont2;bur5;gh;aAe8ha0Visp4o7resd0Ju5;b5esseldorf,rb0shanbe;ai,l0G;ha,nggu0rtmu11;hradRl5troit;hi;donghHe5k08li0masc1Xr es sala1HugavpiY;gu,je2;aKebu,hAo5raio03uriti1P;lo7n6penhag0Ar5;do1Nk;akLst0V;gVm5;bo;aBen8i6ongqi1ristchur5;ch;ang m7ca5ttago1;go;g6n5;ai;du,zho1;n5ttogr12;digarh,g5;ch8sha,zh06;i9lga8mayenJn6pe town,r5;acCdiff;ber18c5;un;ry;ro;aUeMhJirmingh0ToIr9u5;chareRdapeRenos air7r5s0tu0;g5sa;as;es;a9is6usse5;ls;ba6t5;ol;ne;sil0Mtisla7zzav5;il5;le;va;goZst2;op6ubaneshw5;ar;al;iBl9ng8r5;g6l5n;in;en;aluru,hazi;fa5grade,o horizonte;st;ji1rut;ghd0BkGnAot9r7s6yan n4;ur;el,r07;celo3ranquil09;na;ou;du1g6ja lu5;ka;alo6k5;ok;re;ng;ers5u;field;a04b01cc00ddis abaZgartaYhmedWizawl,lQmNnHqaZrEsBt7uck5;la5;nd;he7l5;an5;ta;ns;h5unci2;dod,gab5;at;li5;ngt2;on;a6chora5kaNtwerp;ge;h7p5;ol5;is;eim;aravati,m0s5;terd5;am; 8buquerq7e5giers,maty;ppo,xandr5;ia;ue;basrah al qadim5mawsil al jadid5;ah;ab5;ad;la;ba;ra;idj0u dha5;bi;an;lbo6rh5;us;rg", Country: "true¦0:39;1:2M;a2Xb2Ec22d1Ye1Sf1Mg1Ch1Ai14j12k0Zl0Um0Gn05om3DpZqat1KrXsKtCu6v4wal3yemTz2;a25imbabwe;es,lis and futu2Y;a2enezue32ietnam;nuatu,tican city;.5gTkraiZnited 3ruXs2zbeE;a,sr;arab emirat0Kkingdom,states2;! of am2Y;k.,s.2; 28a.;a7haBimor-les0Bo6rinidad4u2;nis0rk2valu;ey,me2Ys and caic1U; and 2-2;toba1K;go,kel0Znga;iw2Wji2nz2S;ki2U;aCcotl1eBi8lov7o5pa2Cri lanka,u4w2yr0;az2ed9itzerl1;il1;d2Rriname;lomon1Wmal0uth 2;afr2JkLsud2P;ak0en0;erra leoEn2;gapo1Xt maart2;en;negKrb0ychellY;int 2moa,n marino,udi arab0;hele25luc0mart20;epublic of ir0Dom2Duss0w2;an26;a3eHhilippinTitcairn1Lo2uerto riM;l1rtugE;ki2Cl3nama,pua new0Ura2;gu6;au,esti2;ne;aAe8i6or2;folk1Hth3w2;ay; k2ern mariana1C;or0N;caragua,ger2ue;!ia;p2ther19w zeal1;al;mib0u2;ru;a6exi5icro0Ao2yanm05;ldova,n2roc4zamb9;a3gol0t2;enegro,serrat;co;c9dagasc00l6r4urit3yot2;te;an0i15;shall0Wtin2;ique;a3div2i,ta;es;wi,ys0;ao,ed01;a5e4i2uxembourg;b2echtenste11thu1F;er0ya;ban0Hsotho;os,tv0;azakh1Ee3iriba03o2uwait,yrgyz1E;rWsovo;eling0Jnya;a2erF;ma15p1B;c6nd5r3s2taly,vory coast;le of m19rael;a2el1;n,q;ia,oI;el1;aiSon2ungary;dur0Mg kong;aAermany,ha0Pibralt9re7u2;a5ern4inea2ya0O;!-biss2;au;sey;deloupe,m,tema0P;e2na0M;ce,nl1;ar;bTmb0;a6i5r2;ance,ench 2;guia0Dpoly2;nes0;ji,nl1;lklandTroeT;ast tim6cu5gypt,l salv5ngl1quatorial3ritr4st2thiop0;on0; guin2;ea;ad2;or;enmark,jibou4ominica3r con2;go;!n B;ti;aAentral african 9h7o4roat0u3yprQzech2; 8ia;ba,racao;c3lo2morPngo-brazzaville,okFsta r03te d'ivoiK;mb0;osD;i2ristmasF;le,na;republic;m2naTpe verde,yman9;bod0ero2;on;aFeChut00o8r4u2;lgar0r2;kina faso,ma,undi;azil,itish 2unei;virgin2; is2;lands;liv0nai4snia and herzegoviGtswaGuvet2; isl1;and;re;l2n7rmuF;ar2gium,ize;us;h3ngladesh,rbad2;os;am3ra2;in;as;fghaFlCmAn5r3ustr2zerbaijH;al0ia;genti2men0uba;na;dorra,g4t2;arct6igua and barbu2;da;o2uil2;la;er2;ica;b2ger0;an0;ia;ni2;st2;an", Region: "true¦0:2M;1:2S;2:2J;a2Pb2Cc1Yd1Tes1Sf1Qg1Kh1Gi1Bj17k12l0Zm0On07o05pZqWrTsKtFuCv9w5y3zacatec2T;akut0o0Du3;cat2k07;a4est 3isconsin,yomi1L;bengal,vi6;rwick2Ashington3;! dc;er4i3;rgin0;acruz,mont;dmurt0t3;ah,tar3; 2Ka0W;a5e4laxca1Qripu1Wu3;scaDva;langa1nnessee,x2E;bas0Um3smNtar24;aulip2Cil nadu;a8i6o4taf10u3ylh1E;ffYrr03s19;me1Bno1Puth 3;cVdU;ber0c3kkim,naloa;hu2ily;n4skatchew2xo3;ny; luis potosi,ta catari1;a3hode9;j3ngp06;asth2shahi;ingh24u3;e3intana roo;bec,en5reta0Q;ara7e5rince edward3unjab; i3;sl0A;i,nnsylv3rnambu0A;an0;!na;axa0Xdisha,h3klaho1Zntar3reg6ss0Ax0F;io;aIeDo5u3;evo le3nav0V;on;r3tt16va scot0;f8mandy,th3; 3ampton15;c5d4yo3;rk13;ako1M;aroli1;olk;bras1Lva0Bw3; 4foundland3;! and labrador;brunswick,hamp0Wjers3mexiRyork state;ey;galOyarit;a9eghala0Mi5o3;nta1r3;dov0elos;ch5dlanCn4ss3zor11;issippi,ouri;as geraOneso18;ig2oac2;dhy12harasht0Gine,ni4r3ssachusetts;anhao,i el,ylF;p3toba;ur;anca0Ie3incoln0IouisH;e3iR;ds;a5e4h3omi;aka06ul1;ntucky,ra01;bardino,lmyk0ns0Qr3;achay,el0nata0X;alis5har3iangxi;kh3;and;co;daho,llino6n3owa;d4gush3;et0;ia1;is;a5ert4i3un2;dalFm0D;fordZ;mpYrya1waii;ansu,eorg0lou7oa,u3;an4erre3izhou,jarat;ro;ajuato,gdo3;ng;cesterS;lori3uji2;da;sex;ageTe6o4uran3;go;rs3;et;lawaLrbyK;aEeaDh8o3rimea ,umbr0;ahui6l5nnectic4rsi3ventry;ca;ut;i02orado;la;e4hattisgarh,i3uvash0;apQhuahua;chn4rke3;ss0;ya;ra;lFm3;bridge6peche;a8ihar,r7u3;ck3ryat0;ingham3;shi3;re;emen,itish columb0;h0ja cal7lk6s3v6;hkorto3que;st2;an;ar0;iforn0;ia;dygea,guascalientes,lAndhr8r4ss3;am;izo1kans4un3;achal 6;as;na;a 3;pradesh;a5ber4t3;ai;ta;ba4s3;ka;ma", Place: "true¦a0Eb0Bc04d03e02f00gVhUiRjfk,kOlMmJneGoFpBque,rd,s9t6u5v4w1y0;akutOyz;ake isFis1y0;!o;!c;a,ostok,t;laanbaatar,p02safa,t;ahiti,e1he 0;bronx,hamptons;nn,x;a0fo,oho,t,under7yd;khalNsk;a2e1h0itcairn;l,x;k,nnN;!cif04;kla,nt,rd;b1w eng0;land;!r;a1co,i0t,uc;dNnn;gadZlibu,nhattZ;a0gw,hr;s,x;an1osrae,rasnoyar0ul;sk;!s;a1cn,da,nd0st;ianRochina;!x;arlem,kg,nd,oHwy;a3re0;at 0enwich;brita0lakH;in;!y village;co,l0ra;!a;urope,vergladC;ak,en,fw,ist,own4xb;al5dg,gk,h2l1o0rA;lo,nn;!t;a1ina0uuk;town;morro,tham;!if;cn,e1kk,l0rooklyn;vd;l air,verly hills;frica,lta,m7n3r2sia,tl1ve,zor0;es;!ant2;ct1iz;adyr,tarct0;ic0; oce0;an;ericas,s", MaleName: "true¦0:E4;1:D5;2:DN;3:AX;4:D1;5:CF;6:B5;7:CV;8:C7;9:DJ;A:DK;B:A5;C:C1;aCNbBKcAId9Ge8Mf84g7Hh6Ti6Dj5Dk51l4Cm34n2So2Mp2Equ2Cr1Ls11t0Eu0Dv07wTxSyIzD;aDor0;cDh9Skaria,n5V;hEkD;!aCL;ar5VeCK;aLoFuD;sDu2JvBX;if,uf;nFsEusD;ouf,sD;ef;aDg;s,tD;an,h0;hli,nBLssX;avi3ho4;aMeKiFoDyaC1;jcie8Blfgang,odrow,utD;!er;lDnst1;bFey,frD0lD;aBCiD;am,e,s;e9Eur;i,nde6sD;!l8t1;de,lErrAyD;l1ne;lDt3;aA9y;aGiDladimir,ojte7Y;cEha0kt68nceDrgAIva0;!nt;e3Ut66;lentDnA4;in4X;ghBUlyss5Bnax,sm0;aXeShOiMoHrFuEyD;!l3ro7s1;n9r5B;avAVeDist0oy,um0;ntANv5Yy;bGdFmDny;!as,mDoharu;aCSie,y;!d;iBy;mDt5;!my,othy;adFeoEia8FomD;!as;!do8O;!de5;dGrD;en9KrD;an9JeDy;ll,n9I;!dy;dgh,ha,iDnn3req,tsu4S;cB4ka;aTcotRePhLiJoHpenc3tDur1Uylve9Jzym1;anFeDua8C;f0phBSvDwa8B;e61ie;!islaw,l8;lom1nBEuD;leyma7ta;dDlBm1yabonga;!dhart7An8;aFeD;lDrm0;d1t1;h7Tne,qu0Zun,wn,y7;aDbasti0k29l4Qrg4Nth,ymoAT;m5n;!tD;!ie,y;lEmDnti2Dq5Aul;!ke5LmCu4;ik,vato7W;aXeTheA9iPoHuEyD;an,ou;b7MdEf5pe7RssD;!elBY;ol3Ey;an,bJc66dIel,geHh0landBPmGnFry,sEyD;!ce;coe,s;!aAGnC;an,eo;l46r;e5Ng3n8olfo,ri79;bCeB7;cDl8;ar6Pc6OhEkDo;!ey,ie,y;a99ie;gEid,ubAx,yDza;an1InY;gA8iD;naA4s;ch70fa4lHmGndFpha4sEul,wi2HyD;an,mo82;h7Vm5;alBDol2Uy;iATon;f,ph;ent2inD;cy,t1;aIeGhilFier72ol,rD;aka16eD;m,st1;!ip,lip;dALrcy,tD;ar,e3Gr1X;b4Kdra7Ft4ZulD;!o17;ctav3Fi3liv3mAFndrej,rHsEtDum9wA;is,to;aEc9k9m0vD;al5Z;ma;i,l53vL;aLeJiFoDu3A;aDel,j5l0ma0r3K;h,m;cEg4i49kD;!au,h7Uola;holBkDolB;!olB;al,d,il,ls1vD;il8Y;hom,thD;anDy;!a4i4;aZeWiMoHuEyD;l2Jr1;hamEr6XstaD;fa,p5C;ed,mH;di0We,hamFis2FntEsDussa;es,he;e,y;ad,ed,mD;ad,ed;cIgu4hai,kGlFnEtchD;!e6;a8Aik;house,o0Bt1;ae5YeA4olD;aj;ah,hDk8;aEeD;al,l;el,l;hElv2rD;le,ri6v2;di,met;ay0ck,hTjd,ks2DlRmadWnQrKs1tFuricExD;!imilian9Nwe6;e,io;eGhEiBtDus,yB;!eo,hew,ia;eDis;us,w;j,o;cHio,kGlFqu7Dsha6tDv2;iDy;!m,n;in,on;!el,oPus;!el9IoOus;iGu4;achDcolm,ik;ai,y;amEdi,eDmoud;sh;adDm5T;ou;aXeQiOlo3EoKuEyD;le,nd1;cGiFkDth3uk;aDe;!s;gi,s,z;as,iaD;no;g0nn7SrenFuDv8Jwe6;!iD;e,s;!zo;am,oD;n4r;a8Cevi,la5JnIoGst3thaFvD;eDi;nte;bo;!nD;!a6Sel;!ny;mFnErDur5Hwr5H;ry,s;ce,d1;ar,o5A;aLeGhaled,iDrist5Iu4Vy6X;er0p,rD;by,k,ollD;os;en0iGnDrmit,v44;!dEnDt5Z;e1Ay;a6ri59;r,th;cp3j5m66na73rEsp9them,uD;ri;im,l;a02eUiSoGuD;an,lDst2;en,iD;an,en,o,us;aNeLhnKkubBnIrGsD;eEhDi8Bue;!ua;!ph;dDge;an,i,on;!aDny;h,s,th5I;!ath5Hie,nC;!l,sDy;ph;o,qu2;an,mD;!mC;d,ffIrFsD;sDus;!e;a6BemEmai7oDry;me,ni0Y;i7Ty;!e60rD;ey,y;cKdAkImHrFsEvi3yD;!dAs1;on,p3;ed,od,rDv56;e5Nod;al,es4Xis1;a,e,oDub;b,v;k,ob,quD;es;aWbQchiPgNkeMlija,nuLonut,rJsFtDv0;ai,suD;ki;aEha0i7DmaDsac;el,il;ac,iaD;h,s;a,vinDw2;!g;k,nngu5S;!r;nacDor;io;ka;ai,rahD;im;aPeJoIuDyd9;be2KgGmber4WsD;eyEsD;a2e2;in,n;h,o;m3ra3Gsse2wa4B;aHctGitGnrErD;be2Dm0;iDy;!q11;or;th;bMlLmza,nKo,rFsEyD;a4JdA;an,s0;lGo50rFuDv8;hi4Gki,tD;a,o;is1y;an,ey;k,s;!im;ib;aVeRiPlenOoLrHuD;ilEsD;!tavo;herme,lerD;mo;aFegDov3;!g,orD;io,y;dy,h5Wnt;nzaErD;an,d1;lo;!n;lbe5Ano,oD;rg3Hvan5A;ne,oFrD;aDry;ld,rd5H;ffr8rge;brElArDv2;la28r3Sth,y;e3EielD;!i5;aTePiNlLorr0NrD;anFedDitz;!dCeDri2B;ri2A;cFkD;!ie,lD;in,yn;esLisD;!co,z36;etch3oD;yd;d4lDnn,onn;ip;deriFliEng,rnD;an06;pe,x;co;bi0di,hd;ar04dZfrYit0lSmKnHo2rFsteb0th0uge7vDymAzra;an,eD;ns,re36;gi,i0DnDrol,v2w2;est4Pie;oEriqDzo;ue;ch;aJerIiEmD;aIe2Z;lErD;!h0;!iD;o,s;s1y;nu4;be0Cd1iGliFmEt1viDwood;n,s;er,o;ot1Ys;!as,j4NsD;ha;a2en;!dCg9mGoEuEwD;a2Din;arD;do;o0Wu0W;l,nD;est;a01eRiOoHrGuFwEylD;an,l0;ay7ight;a7dl8nc0st2;ag0ew;minGnEri0ugDvydBy2D;!lB;!a2MnDov0;e6ie,y;go,iDykB;cDk;!k;armuEeDll1on,rk;go;id;anKj0lbeJmetri5nHon,rGsFvEwDxt3;ay7ey;en,in;hawn,mo0B;ek,ri0I;is,nDv3;is,y;rt;!dD;re;an,lNmLnKrGvD;e,iD;! lucDd;as,ca;en,iFne6rDyl;eDin,yl;l3Bn;n,o,us;!e,i4ny;iDon;an,en,on;e,lB;as;a09e07hYiar0lNoIrGuEyrD;il,us;rtD;!is;aDistob0U;ig;dy,lGnErD;ey,neli5y;or,rD;ad;by,e,in,l2t1;aIeFiDyK;fDnt;fo0Ft1;meEt5velaD;nd;nt;rFuEyD;!t1;de;enD;ce;aIeGrisEuD;ck;!tD;i0oph3;st3;er;d,rDs;b4leD;s,y;cDdric,s9;il;lGmer1rD;ey,lEro6y;ll;!os,t1;eb,v2;a07eZiVlaUoRrEuDyr1;ddy,rtK;aLeGiFuEyD;an,ce,on;ce,no;an,ce;nEtD;!t;dEtD;!on;an,on;dEndD;en,on;!foDl8y;rd;bErDyd;is;!by;i7ke;bFlEshD;al;al,lC;ek;nHrDshoi;at,nEtD;!r1C;aDie;rd14;!edict,iEjam2nC;ie,y;to;kaMlazs,nHrD;n8rDt;eDy;tt;ey;dDeE;ar,iD;le;ar17b0Vd0Rf0Pgust2hm0Mi0Jja0Il04m00nSputsiRrIsaHuFveEyDziz;a0kh0;ry;gust5st2;us;hi;aKchJiIjun,maHnFon,tDy0;hDu09;ur;av,oD;ld;an,nd0H;!el,ki;ie;ta;aq;as,dIgel0CtD;hoGoD;i7nD;!i09y;ne;ny;er,reDy;!as,i,s,w;iFmaDos;nu4r;el;ne,r,t;an,bePdAeJfHi,lGonFphXt1vD;aNin;on;so,zo;an,en;onTrD;edU;c,jaGksandFssaGxD;!andD;er,ru;ar,er;ndD;ro;rtN;ni;dAm9;ar;en;ad,eD;d,t;in;onD;so;aEi,olfDri0vik;!o;mDn;!a;dHeGraEuD;!bakr,lfazl;hDm;am;!l;allIelFoulaye,ulD;!lDrF;ah,o;! rD;ahm0;an;ah;av,on", LastName: "true¦0:9F;1:9V;2:9H;3:9X;4:9N;5:8J;6:9K;7:A0;8:9E;9:88;A:6E;B:77;C:6J;a9Ub8Lc7Kd6Xe6Rf6Dg5Vh58i54j4Pk45l3Nm2Rn2Eo26p1Nquispe,r17s0Ft05vVwOxNyGzD;aytsADhD;aDou,u;ng,o;aGeun7ZiDoshiA9un;!lD;diDmaz;rim,z;maDng;da,guc97mo6UsDzaB;aBhiA7;iao,u;aHeGiEoDright,u;jc8Sng;lDmm0nkl0sniewsB;liA1s3;b0iss,lt0;a5Rgn0lDng,tanabe;k0sh;aHeGiEoDukA;lk5roby5;dAllalDnogr2Zr0Zss0val37;ba,obos;lasEsel7N;lGn dFrg8EsEzD;qu7;ily9Oqu7silj9O;en b35ijk,yk;enzue95verde;aLeix1JhHi4j6ka3IoGrFsui,uD;om4ZrD;c4n0un1;an,embl8TynisB;dor95lst31m2rr9th;at5Mi7LoD;mErD;are6Ylaci64;ps3s0Y;hirAkah8Dnaka;a00chWeThPiNmKoItFuEvDzabo;en8Aobod34;ar7bot2lliv4zuB;aEein0oD;i67j3Lyan8V;l6rm0;kol5lovy5re6Psa,to,uD;ng,sa;iDy5Z;rn5tD;!h;l5YmDngh,rbu;mo6Do6J;aFeDimizu;hu,vchD;en7Cuk;la,r17;gu8mDoh,pulve8Trra4R;jDyD;on5;evi6Filtz,miDneid0roed0ulz,warz;dEtD;!z;!t;ar42h6ito,lFnDr2saBto,v2;ch7d0AtDz;a4Pe,os;as,ihAm3Zo0Q;aOeNiKoGuEyD;a66oo,u;bio,iz,sD;so,u;bEc7Bdrigue57g03j73mDosevelt,ssi,ta7Nux,w3Z;a4Be0O;ertsDins3;!on;bei0LcEes,vDzzo;as,e8;ci,hards3;ag4es,it0ut0y9;dFmEnDsmu7Zv5F;tan1;ir7os;ic,u;aSeLhJiGoErDut6;asad,if5Zochazk1W;lishc24pDrti62u55we66;e2Tov48;cEe09nD;as,to;as60hl0;aDillips;k,m,n5K;de3AetIna,rGtD;ersErovDtersC;!a,ic;en,on;eDic,ry,ss3;i8ra,tz,z;ers;h71k,rk0tEvD;ic,l3T;el,t2O;bJconnor,g2ClGnei5PrEzD;demir,turk;ella3MtDwe5N;ega,iz;iDof6GsC;vDyn1F;ei8;aPri1;aLeJguy1iFoDune44ym4;rodahl,vDwak;ak3Uik5otn56;eEkolDlsCx3;ic,ov6X;ls1miD;!n1;ils3mD;co42ec;gy,kaEray4varD;ro;jiDmu8shiD;ma;aXcVeQiPoIuD;lGnFrDssoli5T;atDpUr68;i,ov2;oz,te4B;d0l0;h4lIo0HrEsDza0Z;er,s;aFeEiDoz5r3Ete4B;!n6F;au,i8no,t4M;!l9;i2Rl0;crac5Ohhail5kke3Qll0;hmeGij0j2ElFndErci0ssiDyer19;!er;e3Bo2Z;n0Io;dAti;cartDlaughl6;hy;dMe6Dgnu5Ei0jer34kLmJnci59rFtEyD;er,r;ei,ic,su1N;iEkAqu9roqu6tinD;ez,s;a54c,nD;!o;a52mD;ad5;e5Oin1;rig4Ns1;aSeMiIoGuEyD;!nch;k2nDo;d,gu;mbarDpe2Rvr2;di;!nDu,yana1R;coln,dD;bDholm;erg;bed5TfeGhtFitn0kaEn6rDw2G;oy;!j;in1on1;bvDvD;re;iDmmy,rsCu,voie;ne,t11;aTennedy,h4iSlQnez46oJrGuEvar4woD;k,n;cerDmar58znets5;a,o2G;aDem0i2Zyeziu;sni3PvD;ch3U;bay4Frh0Jsk0TvaFwalDzl5;czDsB;yk;cFlD;!cDen3Q;huk;!ev2ic,s;e6uiveD;rt;eff0l2mu8nnun1;hn,lloe,minsBrEstra31to,ur,yDzl5;a,s0;j0GlsC;aMenLha2Pim0QoEuD;ng,r2;e2JhFnErge2Ju2NvD;anA;es,ss3;anEnsD;en,on,t3;nesDsC;en,s1;ki26s1;cGkob3RnsDrv06;en,sD;enDon;!s;ks3obs1;brahimAglesi3Ake4Ll0CnoZoneFshikEto,vanoD;u,v4A;awa;scu;aPeIitchcock,jaltal6oFrist46uD;!aDb0gh9ynh;m4ng;a23dz2fEjga2Sk,rDx3B;ak0Yvat;er,fm3B;iGmingw3NnErD;nand7re8;dDriks1;ers3;kkiEnD;on1;la,n1;dz2g1lvoLmJnsCqIrr0SsFuEyD;as36es;g1ng;anEhiD;mo0Q;i,ov08;ue;alaD;in1;rs1;aMeorgLheorghe,iJjonIoGrEuDw3;o,staf2Utierr7zm4;ayDg2iffitUub0;li1G;lub3Rme0JnD;calv9zale0I;aj,i;l,mDordaL;en7;iev3B;gnJlGmaFnd2No,rDs2Nuthi0;cDza;ia;ge;eaElD;agh0i,o;no;e,on;ab0erMiIjeldsted,lor9oGrFuD;cDent9ji3F;hs;an1Wiedm4;ntaDrt6st0urni0;na;lipEsD;ch0;ovD;!ic;hatAnandeVrD;arDei8;a,i;ov2;dHinste6riksCsDva0D;cob2ZpDtra2X;inoDosiM;za;en,s3;er,is3wards;aUeMiKjurhuJoHrisco0YuEvorakD;!oQ;arte,boEmitru,rDt2U;and,ic;is;g4he0Hmingu7n2Ord19tD;to;us;aDmitr29ssanayake;s,z; GbnaFlEmirDrvis1Lvi,w4;!ov2;gado,ic;th;bo0groot,jo03lEsilDvri9;va;a cruz,e3uD;ca;hl,mcevsBnErw6t2EviD;d5es,s;ieDku1S;ls1;ki;a05e00hNiobMlarkLoFrD;ivDuz;elli;h1lGntFop0rDs26x;byn,reD;a,ia;i,rer0O;em4liD;ns;!e;anu;aLeIiu,oGriDuJwe;stD;eDiaD;ns1;i,ng,uFwDy;!dhury;!n,onEuD;ng;!g;kEnDtterjee,v7;!d,g;ma,raboD;rty;bGl09ng2rD;eghetEnD;a,y;ti;an,ota0M;cer9lder3mpbeIrFstDvadi08;iDro;llo;doEt0uDvalho;so;so,zo;ll;es;a09eXhUiSlNoGrFyD;rne,tyD;qi;ank5iem,ooks,yant;gdan5nFruya,su,uchEyHziD;c,n5;ard;darDik;enD;ko;ov;aEondD;al;nEzD;ev2;co;ancRshwD;as;a01oDuiy4;umDwmD;ik;ckNethov1gu,ktLnJrD;gGisFnD;ascoDds1;ni;ha;er,mD;ann;gtDit7nett;ss3;asD;hi;er,ham;b2ch,ez,hMiley,kk0nHrDu0;bEnDua;es,i0;ieDosa;ri;dDik;a8yopadhyD;ay;ra;er;k,ng;ic;cosZdYguilXkhtXlSnJrGsl4yD;aEd6;in;la;aEsl4;an;ujo,ya;dFgelD;ovD;!a;ersGov,reD;aDjL;ss1;en;en,on,s3;on;eksejGiyGmeiFvD;ar7es;ez;da;ev;ar;ams;ta", WeekDay: "true¦fri2mon2s1t0wednesd3;hurs1ues1;aturd1und1;!d0;ay0;!s", Month: "true¦aBdec9feb7j2mar,nov9oct1sep0;!t8;!o8;an3u0;l1n0;!e;!y;!u1;!ru0;ary;!em0;ber;pr1ug0;!ust;!il", Date: "true¦ago,t2week0yesterd4; e0e0;nd;mr2o0;d0morrow;ay;!w", FirstName: "true¦aLblair,cHdevGgabrieFhinaEjCk9l8m4nelly,quinn,re3s0;h0umit;ay,e0iloh;a,lby;g6ne;a1el0ina,org5;!okuh9;naia,r0;ion,lo;ashawn,uca;asCe1ir0rE;an;lsAnyat2rry;am0ess6ie,ude;ie,m5;ta;le;an,on;as2h0;arl0eyenne;ie;ey,sidy;lex2ndr1ubr0;ey;a,ea;is", Person: "true¦ashton kutchTbScNdLeJgastOhHinez,jFkEleDmCnettKoBp9r4s3t2v0;a0irgin maH;lentino rossi,n go3;aylor,heresa may,iger woods,yra banks;addam hussain,carlett johanssKlobodan milosevic,uC;ay romano,e3o1ush limbau0;gh;d stewart,nald0;inho,o;ese witherspoFilly;a0ipJ;lmIris hiltD;prah winfrFra;essiaen,itt romnEubarek;bron james,e;anye west,iefer sutherland,obe bryant;aime,effers8k rowli0;ng;alle ber0itlBulk hogan;ry;ff0meril lagasse,zekiel;ie;a0enzel washingt2ick wolf;lt1nte;ar1lint0;on;dinal wols1son0;! palm2;ey;arack obama,rock;er", Verb: "true¦awak9born,cannot,fr8g7h5k3le2m1s0wors9;e8h3;ake sure,sg;ngth6ss6;eep tabs,n0;own;as0e2;!t2;iv1onna;ight0;en", PhrasalVerb: "true¦0:7L;1:79;2:7X;3:7N;4:72;5:80;6:7P;7:6V;8:78;9:7J;A:6W;B:5Z;C:7S;D:7K;a81b6Lc5Rd5Me5Lf4Kg41h3Kiron0j3Gk3Bl2Vm2Jn2Ho2Fp1Wquiet7Ar1Js0CtSuQvacuum 1wHyammer9zE;eroBip FonE;e0k0;by,up;aLeHhGiForErit5G;d 1k33;mp0n2Vpe0r7s7;eel Dip 85;aFiEn2J;gh 09rd0;n Dr E;d2in,o5J;it 61k7lk6rm 6Csh 7Nt6Qv51;rge9sE;e AherB;aTeRhPiLoJrGuEype 69;ckBrn E;d2in,o3Sup;aFiEot0y 2I;ckle6Rp 7T;ck6Qde Y;ne6Pp Es4O;d2o73up;ck GdFe Egh6Bme0p o0Gre0;aw3ba4d2in,up;e 61y 1;by,o7D;ink Erow 6D;ba4ov8up;aEe 5Zll53;m 1r X;ck9ke Flk E;ov8u54;aEba4d2in,o3Cup;ba4ft8p59w3;a0Jc0Ie0Ch08i05l01m00nZoYpTquare StKuIwE;earGiE;ngFtch E;aw3ba4o77; by;ck Eit 1m 1ss0;in,up;aJe0WiIoGrEuc3G;aigh1WiE;ke 6Gn3A;p Erm1Z;by,in,o6T;n3Br 1tc3T;c3Amp0nd Er6Zve6y 1;ba4d2up;d2o6Pup;ar37eHiGlFrEur9;ing9uc7;a3Fit 5B;l13n 1;e5Sll0;be2Wrt0;ap 4Sow D;ash 5Foke0;eep FiEow A;c3Wp 1;in,oE;ff,v8;gn 4XngFt Ez7;d2o5up; al54le0;aGoEu4T;ot Eut0w 6D;aw3ba4f3Go67;c2PdeBk58ve6;e Ill1And HtE; Etl4H;d2in,o5upE;!on;aw3ba4d2in,o27up;o5Mto;al51out0rap51;il6v7;aPeMiLoHuE;b 4Ule0n Estl7;aEba4d2in5Jo3Ut39u3S;c26w3;ll Got FuE;g2Tnd6;a27f30o5;arCin,o5;ng 53p6;aEel6inBnt0;c5Dd E;o31u0I;c24t0;aSeRiPlNoLrIsyc2HuE;ll Gt E;aEba4d2in,o1Ot3Gup;p3Lw3;ap3Kd2in,o5t3Eup;attle9ess FiHoE;p 1;ah1Oon;iEp 5Hr3Yur4Jwer 5H;nt0;ay4DuE;gBmp A;ck Eg0le9n Ap4A;o2Yup;el 4KncilB;c42ir 3Un0ss GtFy E;ba4o54; d2c24;aw3ba4o18;pEw3X;e3Wt D;arrow46erd0oE;d6te45;aMeJiIoGuE;ddl7lE;l 3I;c1Dp 1uth6ve E;al3Nd2in,o5up;ss0x 1;asur7lFss E;a1Gup;t A;ke Fn ArEs1Px0;k Ary6;do,o48up;aRePiKoEuck0;aIc3Hg HoEse0;k Ese3F;aft8ba4d2forw2Jin46ov8uE;nd8p;in,o0M;d A;e HghtGnFsEv1V;ten 4M;e 1k 1; 1e37;arCd2;av1Jt 37velE; o3U;c7p 1sh Etch9ugh6y20;in3Uo5;eFick6nock E;d2o3Q;eEyB;l 2Pp E;aw3ba4d2fTin,o07to,up;aGoFuE;ic7mpB;ke31t35;c3Azz 1;aQeLiIoFuE;nker32rry 1s0W;lEneBrse2X;d Ee 1;ba4d2fast,o01up;de Ft E;ba4on,up;aw3o5;aElp0;d Gl 2Ar Et 1;fEof;rom;in,oTu1H;c02m 1nFve Ez25;it,to;d Eg 2FkerG;d2in,o5;aTeMive Kloss 22oGrFunE; f0N;in3How 2B; Eof 21;aFb1Dit,oErCt0Pu18;ff,n,v8;bo5ft8hKw3;aw3ba4d2in,oEup,w3;ff,n,ut;aJek0t E;aFb17d2oErCup;ff,n,ut,v8;cFhEl1XrCt,w3;ead;ross;r 1;d aFnE;g 1;bo5;a08e01iSlOoKrGuE;cEel 1;k 1;eFighten Eown9y 1;aw3o2S;eEshe1N; 1z7;lGol E;aEwi1G;bo5rC;d Alow 1;aFeEip0;sh0;g Ake0mErE;e 2R;gLlJnHrFsEzzle0;h 2P;e Em 1;aw3ba4up;d0isE;h 1;e El 19;aw3fJ;ht ba4ure0;eJnFsE;s 1;cGd E;fEo25;or;e D;dVl 1;cIll Erm0t0W;ap04bGd2in,oFtE;hrough;ff,ut,v8;a4ehi20;e 0L;at0dge0nd 0Ky7;oHrE;aFess Aop E;aw3bUin,o1E;g9w9; 0Dubl7;aXhUlean AoHrEut 10;ack9eep Eoss D;by,d2oEup;n,ut;me HoFuntE; o1Q;k 1l E;d2o1I;aKbJforHin,oGtFuE;nd8;ogeth8;ut,v8;th,wE;ard;a4y;pErCw3;art;eEipB;ck Der E;on,up;lKncel0rHsGtch FveB; in;o19up;h Dt6;ry FvE;e Y;aw3o15;l Em05;aEba4d2o13up;rCw3;a0Ke0Bl04oVrJuE;bblGcklWil02lk AndlWrn 08st FtEy 13zz6;t D;in,o5up;e E;ov8;anOeaMiFush E;o0Oup;ghIng E;aFba4d2forEin,o5up;th;bo5lErCw3;ong;teE;n 1;k E;d2in,o5up;ch0;arLgKil An7oHssGttlFunce Ex D;aw3ba4;e A; arC;k Dt 1;e 1;d2up; d2;d 1;aJeed0oEurt0;cGw E;aw3ba4d2o5up;ck;k E;in,oL;ck0nk0st6; oKaHef 1nd E;d2ov8up;er;up;r0t E;d2in,oEup;ff,ut;ff,nE;to;ck Kil0nGrgFsE;h D;ain9e D;g Dk9; on;in,o5; o5;aw3d2o5up;ay;cNdJsk Guction6; oE;ff;arCo5;ouE;nd;d E;d2oEup;ff,n;own;t E;o5up;ut", Modal: "true¦c5lets,m4ought3sh1w0;ill,o5;a0o4;ll,nt;! to,a;ay,ight,ust;an,o0;uld", Adjective: "true¦0:7P;1:84;2:83;3:8A;4:7W;5:5S;6:4N;7:4O;8:58;9:6I;A:81;a6Wb6Gc63d5Je54f4Hg49h3Wi39j37k36l2Vm2Ln2Bo1Wp1Dquack,r12s0Ft07uMvJwByear5;arp0eFholeEiDoB;man5oBu6P;d6Rzy;despr7Ls5S;!sa7;eClBste2A;co1Nl o4W;!k5;aCiBola4M;b89ce versa,ol5H;ca3gabo6Gnilla;ltUnHpCrb5Msu4tterB;!mo7G; Eb1SpDsBti1M;ca7etBide dKtairs;!ti2;er,i3U;f36to da1;aLbeco75convin29deIeHfair,ivers4knGprecedVrEsCwB;iel3Nritt6A;i1XuB;pervis0spec3Y;eBu5;cognHgul6Tl6T;own;ndi2v64xpect0;cid0rB;!grou5ZsB;iz0tood;b7pp0Dssu6UuthorB;iz0;i26ra;aGeEhDi6AoCrB;i1oubl0us3M;geth8p,rp6Vuc67;ough4Wril33;en60l32mpBrr2X;o6Ati2;boo,lBn;ent0;aWcVeThSiQmug,nobbi3LoOpNqueami3LtFuBymb6H;bDi gener5DpBrpri6D;erBre0N;! dup8b,i2C;du0seq52;anda77eGiFrBunni2y3F;aightCiB;ki2p0; fBfB;or5K;ll,r5S;aBreotyp0;dfa6Cmi2;a55ec2Gir1Hlend6Cot on; call0le,mb8phist1XrBu0Vvi48;d6Ary;gnifica3nB;ce51g7;am2Re8ocki2ut;cBda1em5lfi32ni1Wpa6Jre6;o1Er42;at5Gient28reec5G;cr0me;aJeEiCoB;bu60tt51uQy4;ghtBv4;!-2Bf9;ar,bel,condi1du6Dfres5AlEpublic42sCtard0vB;ea26;is4CoB;lu1na3;aQe1Cuc4A;b5TciBllyi2;al,st;aOeLicayu6lac5Ropuli5QrCuB;bl5Jmp0n51;eGiDoB;!b07fu5RmiBp8;ne3si2;mCor,sBva1;ti6;a53e;ci5MmB;a0EiB;er,um;ac20rBti1;feAma2XpleBv38;xi2;rBst;allelDtB;-tiBi4;me;!ed;bLffJkIld fashion0nHpGrg1Eth8utFvB;al,erB;!all,niCt,wB;eiBrouB;ght;do0Ter,g2Qsi4B;en,posi1; boa5Og2Oli6;!ay; gua5MbBli6;eat;eDsB;cBer0Eole1;e6u3O;d2Xse;aJeIiHoBua4X;nFrCtB;ab7;thB;!eB;rn;chala3descri58stop;ght5;arby,cessa44ighbor5xt;k0usia1A;aIeGiDoBultip7;bi7derBl0Vnth5ot,st;a1n;nBx0;dblo0RiaBor;tu37;ande3Qdi4NnaBre;ci2;cBgenta,in,j01keshift,le,mmoth,ny,sculi6;ab33ho;aKeFiCoBu15;uti14vi2;mCteraB;l,te;it0;ftEgBth4;al,eCitiB;ma1;nda3K;!-0C;ngu3Zst,tt8;ap1Xind5no0A;agg0uB;niMstifi0veni7;de4gno4Klleg4mQnEpso 20rB;a1rB;eleBita0J;va3; KaJbr0corIdGfluenQiQnFsEtCviB;go0Fti2;aAen3SoxB;ic3B;a6i2Vul0D;a1er,oce3;iCoB;or;reA;deq3Qppr33;fBsitu,vitro;ro3;mFpB;arDerfeAoBrop8;li1rtB;a3ed;ti4;eBi0S;d2Vn3C;aIeFiDoBumdr3I;ne36ok0rrBs08ur5;if2Z;ghfalut1QspB;an2X;aClB;liYpf9;li2;lEnDrB;d04roB;wi2;dy;f,low0;ainf9ener2Oiga24lHoGraDuB;ilBng ho;ty;cCtB;ef9is;ef9;ne,od;ea2Iob4;aTeNinMlKoFrB;a1VeDoz1MustB;raB;ti2;e2Gq10tf9;oDrB; keeps,eBm8tuna1;g03ign;liB;sh;aBue3;g31tte1P;al,i1;dFmCrB;ti7;a7ini6;ne;le; up;bl0i3l27r Cux,voB;ri1uri1;oBreac1E;ff;aLfficie3lKmHnFreAthere4veExB;aAcess,pe1QtraCuB;be2Nl0E;!va1E;n,ryday; BcouraEti0O;rou1sui1;erCiB;ne3;gi2;abo23dMe17i1;g8sB;t,ygB;oi2;er;aReJiDoBrea14ue;mina3ne,ubB;le,tf9;dact1Bfficu1OsCvB;er1K;creDeas0gruntl0hone1FordCtB;a3ressM;er5;et; HadpGfFgene1PliDrang0spe1PtCvoB;ut;ail0ermin0;be1Mca1ghB;tf9;ia3;an;facto;i5magBngeroUs0G;ed,i2;ly;ertaMhief,ivil,oDrB;aBowd0u0G;mp0vZz0;loImGnCrrBve0P;eAu1I;cre1fu0LgrDsCtB;empo0Dra0E;ta3;ue3;mer08pleB;te,x;ni4ss4;in;aNeIizarHlFoCrB;and new,isk,okN;gCna fiUttom,urgeoB;is;us;ank,indB;!i2;re;autif9hiDloCnBst,yoD;eUt;v0w;nd;ul;ckCnkru0WrrB;en;!wards; priori,b0Mc0Jd09fra08g04h03lYmWntiquVppSrMsIttracti06utheHvEwB;aCkB;wa0T;ke,re;ant garCerB;age;de;ntU;leep,piDsuDtonB;isB;hi2;ri2;ab,bitEroDtiB;fiB;ci4;ga3;raB;ry;are3etiNrB;oprB;ia1;at0;aJuB;si2;arEcohCeBiIl,oof;rt;olB;ic;mi2;ead;ainDgressiConiB;zi2;ve;st;id; IeGuFvB;aCerB;se;nc0;ed;lt;pt,qB;ua1;hoc,infinitB;um;cuCtu4u1;al;ra1;erLlKoIruHsCuB;nda3;e3oCtraA;ct;lu1rbi2;ng;te;pt;aBve;rd;aze,e;ra3;nt", Comparable: "true¦0:41;1:4I;2:45;3:2Y;4:4B;5:3X;a4Ob44c3Od3De35f2Rg2Fh24i1Vj1Uk1Rl1Jm1Dn17o15p0Vqu0Tr0KsTtMuIvFw7y6za13;ell27ou4;aBe9hi1Yi7r6;o4y;ck0Ode,l6n1ry,se;d,y;a6i4Mt;k,ry;n1Tr6sK;m,y;a7e6ulgar;nge5rda2xi4;g9in,st;g0n6pco3Mse5;like0t6;i1r6;ue;aAen9hi8i7ough,r6;anqu2Oen1ue;dy,g3Sme0ny,r09;ck,n,rs2P;d40se;ll,me,rt,s6wd45;te5;aVcarUeThRiQkin0FlMmKoHpGqua1FtAu7w6;eet,ift;b7dd13per0Gr6;e,re2H;sta2Ft3;aAe9iff,r7u6;pXr1;a6ict,o4;ig3Fn0U;a1ep,rn;le,rk;e22i3Fright0;ci28ft,l7o6re,ur;n,thi4;emn,id;a6el0ooth;ll,rt;e8i6ow,y;ck,g35m6;!y;ek,nd3D;ck,l0mp3;a6iTort,rill,y;dy,ll0Xrp;cu0Rve0Rxy;ce,ed,y;d,fe,int0l1Vv14;aBe9i8o6ude;mantic,o1Isy,u6;gh,nd;ch,pe,tzy;a6d,mo0H;dy,l;gg7ndom,p6re,w;id;ed;ai2i6;ck,et;aEhoDi1QlCoBr8u6;ny,r6;e,p3;egna2ic7o6;fouYud;ey,k0;li04or,te1B;ain,easa2;ny;in5le;dd,f6i0ld,ranQ;fi10;aAe8i7o6;b3isy,rm15sy;ce,mb3;a6w;r,t;ive,rr01;aAe8ild,o7u6;nda19te;ist,o1;a6ek,llX;n,s0ty;d,tuQ;aBeAi9o6ucky;f0Un7o1Du6ve0w17y0T;d,sy;e0g;g1Tke0tt3ve0;an,wd;me,r6te;ge;e7i6;nd;en;ol0ui1P;cy,ll,n6;sBt6;e6ima8;llege2r6;es7media6;te;ti4;ecu6ta2;re;aEeBiAo8u6;ge,m6ng1R;b3id;ll6me0t;ow;gh,l0;a6f04sita2;dy,v6;en0y;nd1Hppy,r6te5;d,sh;aGenFhDiClBoofy,r6;a9e8is0o6ue1E;o6ss;vy;at,en,y;nd,y;ad,ib,ooI;a2d1;a6o6;st0;t3uiY;u1y;aIeeb3iDlat,oAr8u6;ll,n6r14;!ny;aHe6iend0;e,sh;a7r6ul;get5mG;my;erce8n6rm;an6e;ciC;! ;le;ir,ke,n0Fr,st,t,ulA;aAerie,mp9sse7v6xtre0Q;il;nti6;al;ty;r7s6;tern,y;ly,th0;aFeCi9r7u6;ll,mb;u6y;nk;r7vi6;ne;e,ty;a6ep,nD;d6f,r;!ly;mp,pp03rk;aHhDlAo8r7u6;dd0r0te;isp,uel;ar6ld,mmon,ol,st0ward0zy;se;e6ou1;a6vW;n,r;ar8e6il0;ap,e6;sy;mi4;gey,lm8r6;e5i4;ful;!i4;aNiLlIoEr8u6;r0sy;ly;aAi7o6;ad,wn;ef,g7llia2;nt;ht;sh,ve;ld,r7un6;cy;ed,i4;ng;a7o6ue;nd,o1;ck,nd;g,tt6;er;d,ld,w1;dy;bsu9ng8we6;so6;me;ry;rd", TextOrdinal: "true¦bGeDf9hundredHmGnin7qu6s4t0zeroH;enGh1rFwe0;lfFn9;ir0ousandE;d,t4;e0ixt9;cond,ptAvent8xtA;adr9int9;et0th;e6ie8;i2o0;r0urt3;tie5;ft1rst;ight0lev1;e0h,ie2;en1;illion0;th", Cardinal: "true¦bHeEf8hundred,mHnineAone,qu6s4t0zero;en,h2rGw0;e0o;lve,n8;irt9ousandEree;e0ix5;pt1ven4xt1;adr0int0;illion;i3o0;r1ur0;!t2;ty;ft0ve;e2y;ight0lev1;!e0y;en;illion0;!s", Expression: "true¦a02b01dXeVfuck,gShLlImHnGoDpBshAtsk,u7voi04w3y0;a1eLu0;ck,p;!a,hoo,y;h1ow,t0;af,f;e0oa;e,w;gh,h0;! 0h,m;huh,oh;eesh,hh,it;ff,hew,l0sst;ease,z;h1o0w,y;h,o,ps;!h;ah,ope;eh,mm;m1ol0;!s;ao,fao;a4e2i,mm,oly1urr0;ah;! mo6;e,ll0y;!o;ha0i;!ha;ah,ee,o0rr;l0odbye;ly;e0h,t cetera,ww;k,p;'oh,a0uh;m0ng;mit,n0;!it;ah,oo,ye; 1h0rgh;!em;la", Adverb: "true¦a08by 06d02eYfShQinPjustOkinda,mMnJoEpCquite,r9s5t2up1very,well,ye0;p,s; to,wards5;h1iny bit,o0wiO;o,t6ward;en,us;eldom,o0uch;!me1rt0; of;hYtimes,w09;a1e0;alT;ndomSthN;ar excellDer0oint blank; Nhaps;f3n0;ce0ly;! 0;ag02moW; courIten;ewKo0; longEt 0;onIwithstanding;aybe,eanwhiAore0;!ovB;! aboU;deed,steV;en0;ce;or2u0;lArther0;!moJ; 0ev3;examp0good,suH;le;n1v0;er; mas0ough;se;e0irect1; 1finite0;ly;ju8trop;far,n0;ow; DbroCd nauseam,gBl6ny3part,s2t 0w4;be6l0mo6wor6;arge,ea5; soon,ide;mo1w0;ay;re;l 1mo0one,ready,so,ways;st;b1t0;hat;ut;ain;ad;lot,posteriori", Determiner: "true¦aBboth,d9e6few,l4mu8neiDown,plenty,s3th2various,wh0;at0ich0;evC;at,e4is,ose;everal,ome;a,e0;!ast,s;a1i6l0very;!se;ch;e0u;!s;!n0;!o0y;th0;er" }, ar = function(e3) {
  const t2 = e3.split("|").reduce((e4, t3) => {
    const r3 = t3.split("¦");
    return e4[r3[0]] = r3[1], e4;
  }, {}), r2 = {};
  return Object.keys(t2).forEach((function(e4) {
    const a2 = tr(t2[e4]);
    "true" === e4 && (e4 = true);
    for (let t3 = 0; t3 < a2.length; t3++) {
      const n2 = a2[t3];
      true === r2.hasOwnProperty(n2) ? false === Array.isArray(r2[n2]) ? r2[n2] = [r2[n2], e4] : r2[n2].push(e4) : r2[n2] = e4;
    }
  })), r2;
};
let nr = { "20th century fox": "Organization", "7 eleven": "Organization", "motel 6": "Organization", g8: "Organization", vh1: "Organization", q1: "Date", q2: "Date", q3: "Date", q4: "Date", her: ["Possessive", "Pronoun"], his: ["Possessive", "Pronoun"], their: ["Possessive", "Pronoun"], themselves: ["Possessive", "Pronoun"], your: ["Possessive", "Pronoun"], our: ["Possessive", "Pronoun"], my: ["Possessive", "Pronoun"], its: ["Possessive", "Pronoun"] };
const ir = { Unit: (e3, t2) => {
  e3[t2] = ["Abbreviation", "Unit"];
}, Cardinal: (e3, t2) => {
  e3[t2] = ["TextValue", "Cardinal"];
}, TextOrdinal: (e3, t2) => {
  e3[t2] = ["Ordinal", "TextValue"], e3[t2 + "s"] = ["TextValue", "Fraction"];
}, Singular: (e3, t2, r2) => {
  e3[t2] = "Singular";
  let a2 = r2.transforms.toPlural(t2, r2);
  e3[a2] = e3[a2] || "Plural";
}, Infinitive: (e3, t2, r2) => {
  e3[t2] = "Infinitive";
  let a2 = r2.transforms.conjugate(t2, r2), n2 = Object.keys(a2);
  for (let t3 = 0; t3 < n2.length; t3++) {
    let r3 = a2[n2[t3]];
    e3[r3] = e3[r3] || n2[t3];
  }
}, Comparable: (e3, t2, r2) => {
  e3[t2] = "Comparable";
  let a2 = r2.transforms.adjectives(t2), n2 = Object.keys(a2);
  for (let t3 = 0; t3 < n2.length; t3++) {
    let r3 = a2[n2[t3]];
    e3[r3] = e3[r3] || n2[t3];
  }
}, PhrasalVerb: (e3, t2, r2) => {
  e3[t2] = ["PhrasalVerb", "Infinitive"];
  let a2 = t2.split(" "), n2 = r2.transforms.conjugate(a2[0], r2), i2 = Object.keys(n2);
  for (let t3 = 0; t3 < i2.length; t3++) {
    let o2 = n2[i2[t3]] + " " + a2[1];
    e3[o2] = e3[o2] || ["PhrasalVerb", i2[t3]], r2.hasCompound[n2[i2[t3]]] = true;
  }
}, Demonym: (e3, t2, r2) => {
  e3[t2] = "Demonym";
  let a2 = r2.transforms.toPlural(t2, r2);
  e3[a2] = e3[a2] || ["Demonym", "Plural"];
} }, or = function(e3, t2, r2) {
  Object.keys(e3).forEach((a2) => {
    let n2 = e3[a2];
    "Abbreviation" !== n2 && "Unit" !== n2 || (r2.cache.abbreviations[a2] = true);
    let i2 = a2.split(" ");
    i2.length > 1 && (r2.hasCompound[i2[0]] = true), void 0 === ir[n2] ? void 0 !== t2[a2] ? ("string" == typeof t2[a2] && (t2[a2] = [t2[a2]]), "string" == typeof n2 ? t2[a2].push(n2) : t2[a2] = t2[a2].concat(n2)) : t2[a2] = n2 : ir[n2](t2, a2, r2);
  });
};
var sr = { buildOut: function(e3) {
  let t2 = Object.assign({}, nr);
  return Object.keys(rr).forEach((r2) => {
    let a2 = ar(rr[r2]);
    Object.keys(a2).forEach((e4) => {
      a2[e4] = r2;
    }), or(a2, t2, e3);
  }), t2;
}, addWords: or };
var lr = function(e3) {
  let t2 = e3.irregulars.nouns, r2 = Object.keys(t2);
  for (let a3 = 0; a3 < r2.length; a3++) {
    const n3 = r2[a3];
    e3.words[n3] = "Singular", e3.words[t2[n3]] = "Plural";
  }
  let a2 = e3.irregulars.verbs, n2 = Object.keys(a2);
  for (let t3 = 0; t3 < n2.length; t3++) {
    const r3 = n2[t3];
    e3.words[r3] = e3.words[r3] || "Infinitive";
    let i2 = e3.transforms.conjugate(r3, e3);
    i2 = Object.assign(i2, a2[r3]), Object.keys(i2).forEach((t4) => {
      e3.words[i2[t4]] = e3.words[i2[t4]] || t4, "Participle" === e3.words[i2[t4]] && (e3.words[i2[t4]] = t4);
    });
  }
};
const ur = { g: "Gerund", prt: "Participle", perf: "PerfectTense", pst: "PastTense", fut: "FuturePerfect", pres: "PresentTense", pluperf: "Pluperfect", a: "Actor" };
let cr = { act: { a: "_or" }, ache: { pst: "ached", g: "aching" }, age: { g: "ageing", pst: "aged", pres: "ages" }, aim: { a: "_er", g: "_ing", pst: "_ed" }, arise: { prt: "_n", pst: "arose" }, babysit: { a: "_ter", pst: "babysat" }, ban: { a: "", g: "_ning", pst: "_ned" }, be: { a: "", g: "am", prt: "been", pst: "was", pres: "is" }, beat: { a: "_er", g: "_ing", prt: "_en" }, become: { prt: "_" }, begin: { g: "_ning", prt: "begun", pst: "began" }, being: { g: "are", pst: "were", pres: "are" }, bend: { prt: "bent" }, bet: { a: "_ter", prt: "_" }, bind: { pst: "bound" }, bite: { g: "biting", prt: "bitten", pst: "bit" }, bleed: { pst: "bled", prt: "bled" }, blow: { prt: "_n", pst: "blew" }, boil: { a: "_er" }, brake: { prt: "broken" }, break: { pst: "broke" }, breed: { pst: "bred" }, bring: { pst: "brought", prt: "brought" }, broadcast: { pst: "_" }, budget: { pst: "_ed" }, build: { pst: "built", prt: "built" }, burn: { prt: "_ed" }, burst: { prt: "_" }, buy: { pst: "bought", prt: "bought" }, can: { a: "", fut: "_", g: "", pst: "could", perf: "could", pluperf: "could", pres: "_" }, catch: { pst: "caught" }, choose: { g: "choosing", prt: "chosen", pst: "chose" }, cling: { prt: "clung" }, come: { prt: "_", pst: "came", g: "coming" }, compete: { a: "competitor", g: "competing", pst: "_d" }, cost: { pst: "_" }, creep: { prt: "crept" }, cut: { prt: "_" }, deal: { pst: "_t", prt: "_t" }, develop: { a: "_er", g: "_ing", pst: "_ed" }, die: { g: "dying", pst: "_d" }, dig: { g: "_ging", pst: "dug", prt: "dug" }, dive: { prt: "_d" }, do: { pst: "did", pres: "_es" }, draw: { prt: "_n", pst: "drew" }, dream: { prt: "_t" }, drink: { prt: "drunk", pst: "drank" }, drive: { g: "driving", prt: "_n", pst: "drove" }, drop: { g: "_ping", pst: "_ped" }, eat: { a: "_er", g: "_ing", prt: "_en", pst: "ate" }, edit: { pst: "_ed", g: "_ing" }, egg: { pst: "_ed" }, fall: { prt: "_en", pst: "fell" }, feed: { prt: "fed", pst: "fed" }, feel: { a: "_er", pst: "felt" }, fight: { pst: "fought", prt: "fought" }, find: { pst: "found" }, flee: { g: "_ing", prt: "fled" }, fling: { prt: "flung" }, fly: { prt: "flown", pst: "flew" }, forbid: { pst: "forbade" }, forget: { g: "_ing", prt: "forgotten", pst: "forgot" }, forgive: { g: "forgiving", prt: "_n", pst: "forgave" }, free: { a: "", g: "_ing" }, freeze: { g: "freezing", prt: "frozen", pst: "froze" }, get: { pst: "got", prt: "gotten" }, give: { g: "giving", prt: "_n", pst: "gave" }, go: { prt: "_ne", pst: "went", pres: "goes" }, grow: { prt: "_n" }, guide: { pst: "_d" }, hang: { pst: "hung", prt: "hung" }, have: { g: "having", pst: "had", prt: "had", pres: "has" }, hear: { pst: "_d", prt: "_d" }, hide: { prt: "hidden", pst: "hid" }, hit: { prt: "_" }, hold: { pst: "held", prt: "held" }, hurt: { pst: "_", prt: "_" }, ice: { g: "icing", pst: "_d" }, imply: { pst: "implied", pres: "implies" }, is: { a: "", g: "being", pst: "was", pres: "_" }, keep: { prt: "kept" }, kneel: { prt: "knelt" }, know: { prt: "_n" }, lay: { pst: "laid", prt: "laid" }, lead: { pst: "led", prt: "led" }, leap: { prt: "_t" }, leave: { pst: "left", prt: "left" }, lend: { prt: "lent" }, lie: { g: "lying", pst: "lay" }, light: { pst: "lit", prt: "lit" }, log: { g: "_ging", pst: "_ged" }, loose: { prt: "lost" }, lose: { g: "losing", pst: "lost" }, make: { pst: "made", prt: "made" }, mean: { pst: "_t", prt: "_t" }, meet: { a: "_er", g: "_ing", pst: "met", prt: "met" }, miss: { pres: "_" }, name: { g: "naming" }, patrol: { g: "_ling", pst: "_led" }, pay: { pst: "paid", prt: "paid" }, prove: { prt: "_n" }, puke: { g: "puking" }, put: { prt: "_" }, quit: { prt: "_" }, read: { pst: "_", prt: "_" }, ride: { prt: "ridden" }, reside: { pst: "_d" }, ring: { pst: "rang", prt: "rung" }, rise: { fut: "will have _n", g: "rising", prt: "_n", pst: "rose", pluperf: "had _n" }, rub: { g: "_bing", pst: "_bed" }, run: { g: "_ning", prt: "_", pst: "ran" }, say: { pst: "said", prt: "said", pres: "_s" }, seat: { pst: "sat", prt: "sat" }, see: { g: "_ing", prt: "_n", pst: "saw" }, seek: { prt: "sought" }, sell: { pst: "sold", prt: "sold" }, send: { prt: "sent" }, set: { prt: "_" }, sew: { prt: "_n" }, shake: { prt: "_n" }, shave: { prt: "_d" }, shed: { g: "_ding", pst: "_", pres: "_s" }, shine: { pst: "shone", prt: "shone" }, shoot: { pst: "shot", prt: "shot" }, show: { pst: "_ed" }, shut: { prt: "_" }, sing: { prt: "sung", pst: "sang" }, sink: { pst: "sank", pluperf: "had sunk" }, sit: { pst: "sat" }, ski: { pst: "_ied" }, slay: { prt: "slain" }, sleep: { prt: "slept" }, slide: { pst: "slid", prt: "slid" }, smash: { pres: "_es" }, sneak: { prt: "snuck" }, speak: { fut: "will have spoken", prt: "spoken", pst: "spoke", perf: "have spoken", pluperf: "had spoken" }, speed: { prt: "sped" }, spend: { prt: "spent" }, spill: { prt: "_ed", pst: "spilt" }, spin: { g: "_ning", pst: "spun", prt: "spun" }, spit: { prt: "spat" }, split: { prt: "_" }, spread: { pst: "_" }, spring: { prt: "sprung" }, stand: { pst: "stood" }, steal: { a: "_er", pst: "stole" }, stick: { pst: "stuck" }, sting: { pst: "stung" }, stink: { pst: "stunk", prt: "stunk" }, stream: { a: "_er" }, strew: { prt: "_n" }, strike: { g: "striking", pst: "struck" }, suit: { a: "_er", g: "_ing", pst: "_ed" }, sware: { prt: "sworn" }, swear: { pst: "swore" }, sweep: { prt: "swept" }, swim: { g: "_ming", pst: "swam" }, swing: { pst: "swung" }, take: { fut: "will have _n", pst: "took", perf: "have _n", pluperf: "had _n" }, teach: { pst: "taught", pres: "_es" }, tear: { pst: "tore" }, tell: { pst: "told" }, think: { pst: "thought" }, thrive: { prt: "_d" }, tie: { g: "tying", pst: "_d" }, undergo: { prt: "_ne" }, understand: { pst: "understood" }, upset: { prt: "_" }, wait: { a: "_er", g: "_ing", pst: "_ed" }, wake: { pst: "woke" }, wear: { pst: "wore" }, weave: { prt: "woven" }, wed: { pst: "wed" }, weep: { prt: "wept" }, win: { g: "_ning", pst: "won" }, wind: { prt: "wound" }, withdraw: { pst: "withdrew" }, wring: { prt: "wrung" }, write: { g: "writing", prt: "written", pst: "wrote" } }, hr = Object.keys(cr);
for (let e3 = 0; e3 < hr.length; e3++) {
  const t2 = hr[e3];
  let r2 = {};
  Object.keys(cr[t2]).forEach((e4) => {
    let a2 = cr[t2][e4];
    a2 = a2.replace("_", t2), r2[ur[e4]] = a2;
  }), cr[t2] = r2;
}
var dr = cr;
const gr = { b: [{ reg: /([^aeiou][aeiou])b$/i, repl: { pr: "$1bs", pa: "$1bbed", gr: "$1bbing" } }], d: [{ reg: /(end)$/i, repl: { pr: "$1s", pa: "ent", gr: "$1ing", ar: "$1er" } }, { reg: /(eed)$/i, repl: { pr: "$1s", pa: "$1ed", gr: "$1ing", ar: "$1er" } }, { reg: /(ed)$/i, repl: { pr: "$1s", pa: "$1ded", ar: "$1der", gr: "$1ding" } }, { reg: /([^aeiou][ou])d$/i, repl: { pr: "$1ds", pa: "$1dded", gr: "$1dding" } }], e: [{ reg: /(eave)$/i, repl: { pr: "$1s", pa: "$1d", gr: "eaving", ar: "$1r" } }, { reg: /(ide)$/i, repl: { pr: "$1s", pa: "ode", gr: "iding", ar: "ider" } }, { reg: /(t|sh?)(ake)$/i, repl: { pr: "$1$2s", pa: "$1ook", gr: "$1aking", ar: "$1$2r" } }, { reg: /w(ake)$/i, repl: { pr: "w$1s", pa: "woke", gr: "waking", ar: "w$1r" } }, { reg: /m(ake)$/i, repl: { pr: "m$1s", pa: "made", gr: "making", ar: "m$1r" } }, { reg: /(a[tg]|i[zn]|ur|nc|gl|is)e$/i, repl: { pr: "$1es", pa: "$1ed", gr: "$1ing" } }, { reg: /([bd]l)e$/i, repl: { pr: "$1es", pa: "$1ed", gr: "$1ing" } }, { reg: /(om)e$/i, repl: { pr: "$1es", pa: "ame", gr: "$1ing" } }], g: [{ reg: /([^aeiou][aou])g$/i, repl: { pr: "$1gs", pa: "$1gged", gr: "$1gging" } }], h: [{ reg: /(..)([cs]h)$/i, repl: { pr: "$1$2es", pa: "$1$2ed", gr: "$1$2ing" } }], k: [{ reg: /(ink)$/i, repl: { pr: "$1s", pa: "unk", gr: "$1ing", ar: "$1er" } }], m: [{ reg: /([^aeiou][aeiou])m$/i, repl: { pr: "$1ms", pa: "$1mmed", gr: "$1mming" } }], n: [{ reg: /(en)$/i, repl: { pr: "$1s", pa: "$1ed", gr: "$1ing" } }], p: [{ reg: /(e)(ep)$/i, repl: { pr: "$1$2s", pa: "$1pt", gr: "$1$2ing", ar: "$1$2er" } }, { reg: /([^aeiou][aeiou])p$/i, repl: { pr: "$1ps", pa: "$1pped", gr: "$1pping" } }, { reg: /([aeiu])p$/i, repl: { pr: "$1ps", pa: "$1p", gr: "$1pping" } }], r: [{ reg: /([td]er)$/i, repl: { pr: "$1s", pa: "$1ed", gr: "$1ing" } }, { reg: /(er)$/i, repl: { pr: "$1s", pa: "$1ed", gr: "$1ing" } }], s: [{ reg: /(ish|tch|ess)$/i, repl: { pr: "$1es", pa: "$1ed", gr: "$1ing" } }], t: [{ reg: /(ion|end|e[nc]t)$/i, repl: { pr: "$1s", pa: "$1ed", gr: "$1ing" } }, { reg: /(.eat)$/i, repl: { pr: "$1s", pa: "$1ed", gr: "$1ing" } }, { reg: /([aeiu])t$/i, repl: { pr: "$1ts", pa: "$1t", gr: "$1tting" } }, { reg: /([^aeiou][aeiou])t$/i, repl: { pr: "$1ts", pa: "$1tted", gr: "$1tting" } }], w: [{ reg: /(.llow)$/i, repl: { pr: "$1s", pa: "$1ed" } }, { reg: /(..)(ow)$/i, repl: { pr: "$1$2s", pa: "$1ew", gr: "$1$2ing", prt: "$1$2n" } }], y: [{ reg: /(i|f|rr)y$/i, repl: { pr: "$1ies", pa: "$1ied", gr: "$1ying" } }], z: [{ reg: /([aeiou]zz)$/i, repl: { pr: "$1es", pa: "$1ed", gr: "$1ing" } }] }, pr = { pr: "PresentTense", pa: "PastTense", gr: "Gerund", prt: "Participle", ar: "Actor" }, mr = function(e3, t2) {
  let r2 = {}, a2 = Object.keys(t2.repl);
  for (let n2 = 0; n2 < a2.length; n2 += 1) {
    let i2 = a2[n2];
    r2[pr[i2]] = e3.replace(t2.reg, t2.repl[i2]);
  }
  return r2;
};
const fr = /[bcdfghjklmnpqrstvwxz]y$/;
const br = function(e3 = "") {
  let t2 = e3[e3.length - 1];
  if (true === gr.hasOwnProperty(t2)) for (let r2 = 0; r2 < gr[t2].length; r2 += 1) {
    if (true === gr[t2][r2].reg.test(e3)) return mr(e3, gr[t2][r2]);
  }
  return {};
}, yr = { Gerund: (e3) => "e" === e3.charAt(e3.length - 1) ? e3.replace(/e$/, "ing") : e3 + "ing", PresentTense: (e3) => "s" === e3.charAt(e3.length - 1) ? e3 + "es" : true === fr.test(e3) ? e3.slice(0, -1) + "ies" : e3 + "s", PastTense: (e3) => "e" === e3.charAt(e3.length - 1) ? e3 + "d" : "ed" === e3.substr(-2) ? e3 : true === fr.test(e3) ? e3.slice(0, -1) + "ied" : e3 + "ed" };
var vr = function(e3 = "", t2) {
  let r2 = {};
  return t2 && t2.irregulars && true === t2.irregulars.verbs.hasOwnProperty(e3) && (r2 = Object.assign({}, t2.irregulars.verbs[e3])), r2 = Object.assign({}, br(e3), r2), void 0 === r2.Gerund && (r2.Gerund = yr.Gerund(e3)), void 0 === r2.PastTense && (r2.PastTense = yr.PastTense(e3)), void 0 === r2.PresentTense && (r2.PresentTense = yr.PresentTense(e3)), r2;
};
const wr = [/ght$/, /nge$/, /ough$/, /ain$/, /uel$/, /[au]ll$/, /ow$/, /oud$/, /...p$/], kr = [/ary$/], Ar = { nice: "nicest", late: "latest", hard: "hardest", inner: "innermost", outer: "outermost", far: "furthest", worse: "worst", bad: "worst", good: "best", big: "biggest", large: "largest" }, Dr = [{ reg: /y$/i, repl: "iest" }, { reg: /([aeiou])t$/i, repl: "$1ttest" }, { reg: /([aeou])de$/i, repl: "$1dest" }, { reg: /nge$/i, repl: "ngest" }, { reg: /([aeiou])te$/i, repl: "$1test" }];
const $r = [/ght$/, /nge$/, /ough$/, /ain$/, /uel$/, /[au]ll$/, /ow$/, /old$/, /oud$/, /e[ae]p$/], Pr = [/ary$/, /ous$/], Er = { grey: "greyer", gray: "grayer", green: "greener", yellow: "yellower", red: "redder", good: "better", well: "better", bad: "worse", sad: "sadder", big: "bigger" }, Hr2 = [{ reg: /y$/i, repl: "ier" }, { reg: /([aeiou])t$/i, repl: "$1tter" }, { reg: /([aeou])de$/i, repl: "$1der" }, { reg: /nge$/i, repl: "nger" }];
const jr = { toSuperlative: function(e3) {
  if (Ar.hasOwnProperty(e3)) return Ar[e3];
  for (let t2 = 0; t2 < Dr.length; t2++) if (Dr[t2].reg.test(e3)) return e3.replace(Dr[t2].reg, Dr[t2].repl);
  for (let t2 = 0; t2 < kr.length; t2++) if (true === kr[t2].test(e3)) return null;
  for (let t2 = 0; t2 < wr.length; t2++) if (true === wr[t2].test(e3)) return "e" === e3.charAt(e3.length - 1) ? e3 + "st" : e3 + "est";
  return e3 + "est";
}, toComparative: function(e3) {
  if (Er.hasOwnProperty(e3)) return Er[e3];
  for (let t2 = 0; t2 < Hr2.length; t2++) if (true === Hr2[t2].reg.test(e3)) return e3.replace(Hr2[t2].reg, Hr2[t2].repl);
  for (let t2 = 0; t2 < Pr.length; t2++) if (true === Pr[t2].test(e3)) return null;
  for (let t2 = 0; t2 < $r.length; t2++) if (true === $r[t2].test(e3)) return e3 + "er";
  return true === /e$/.test(e3) ? e3 + "r" : e3 + "er";
} };
var Nr = function(e3) {
  let t2 = {}, r2 = jr.toSuperlative(e3);
  r2 && (t2.Superlative = r2);
  let a2 = jr.toComparative(e3);
  return a2 && (t2.Comparative = a2), t2;
};
const xr = { a: [[/(antenn|formul|nebul|vertebr|vit)a$/i, "$1ae"], [/([ti])a$/i, "$1a"]], e: [[/(kn|l|w)ife$/i, "$1ives"], [/(hive)$/i, "$1s"], [/([m|l])ouse$/i, "$1ice"], [/([m|l])ice$/i, "$1ice"]], f: [[/^(dwar|handkerchie|hoo|scar|whar)f$/i, "$1ves"], [/^((?:ca|e|ha|(?:our|them|your)?se|she|wo)l|lea|loa|shea|thie)f$/i, "$1ves"]], i: [[/(octop|vir)i$/i, "$1i"]], m: [[/([ti])um$/i, "$1a"]], n: [[/^(oxen)$/i, "$1"]], o: [[/(al|ad|at|er|et|ed|ad)o$/i, "$1oes"]], s: [[/(ax|test)is$/i, "$1es"], [/(alias|status)$/i, "$1es"], [/sis$/i, "ses"], [/(bu)s$/i, "$1ses"], [/(sis)$/i, "ses"], [/^(?!talis|.*hu)(.*)man$/i, "$1men"], [/(octop|vir|radi|nucle|fung|cact|stimul)us$/i, "$1i"]], x: [[/(matr|vert|ind|cort)(ix|ex)$/i, "$1ices"], [/^(ox)$/i, "$1en"]], y: [[/([^aeiouy]|qu)y$/i, "$1ies"]], z: [[/(quiz)$/i, "$1zes"]] }, Fr = /(x|ch|sh|s|z)$/;
var Cr = function(e3 = "", t2) {
  let r2 = t2.irregulars.nouns;
  if (r2.hasOwnProperty(e3)) return r2[e3];
  let a2 = (function(e4) {
    let t3 = e4[e4.length - 1];
    if (true === xr.hasOwnProperty(t3)) for (let r3 = 0; r3 < xr[t3].length; r3 += 1) {
      let a3 = xr[t3][r3][0];
      if (true === a3.test(e4)) return e4.replace(a3, xr[t3][r3][1]);
    }
    return null;
  })(e3);
  return null !== a2 ? a2 : Fr.test(e3) ? e3 + "es" : e3 + "s";
};
const Br2 = [[/([^v])ies$/i, "$1y"], [/ises$/i, "isis"], [/(kn|[^o]l|w)ives$/i, "$1ife"], [/^((?:ca|e|ha|(?:our|them|your)?se|she|wo)l|lea|loa|shea|thie)ves$/i, "$1f"], [/^(dwar|handkerchie|hoo|scar|whar)ves$/i, "$1f"], [/(antenn|formul|nebul|vertebr|vit)ae$/i, "$1a"], [/(octop|vir|radi|nucle|fung|cact|stimul)(i)$/i, "$1us"], [/(buffal|tomat|tornad)(oes)$/i, "$1o"], [/(eas)es$/i, "$1e"], [/(..[aeiou]s)es$/i, "$1"], [/(vert|ind|cort)(ices)$/i, "$1ex"], [/(matr|append)(ices)$/i, "$1ix"], [/(x|ch|ss|sh|z|o)es$/i, "$1"], [/men$/i, "man"], [/(n)ews$/i, "$1ews"], [/([ti])a$/i, "$1um"], [/([^aeiouy]|qu)ies$/i, "$1y"], [/(s)eries$/i, "$1eries"], [/(m)ovies$/i, "$1ovie"], [/([m|l])ice$/i, "$1ouse"], [/(cris|ax|test)es$/i, "$1is"], [/(alias|status)es$/i, "$1"], [/(ss)$/i, "$1"], [/(ics)$/i, "$1"], [/s$/i, ""]];
var Gr = function(e3, t2) {
  let r2 = t2.irregulars.nouns, a2 = (n2 = r2, Object.keys(n2).reduce((e4, t3) => (e4[n2[t3]] = t3, e4), {}));
  var n2;
  if (a2.hasOwnProperty(e3)) return a2[e3];
  for (let t3 = 0; t3 < Br2.length; t3++) if (true === Br2[t3][0].test(e3)) return e3 = e3.replace(Br2[t3][0], Br2[t3][1]);
  return e3;
};
var zr = { Participle: [{ reg: /own$/i, to: "ow" }, { reg: /(.)un([g|k])$/i, to: "$1in$2" }], Actor: [{ reg: /(er)er$/i, to: "$1" }], PresentTense: [{ reg: /(..)(ies)$/i, to: "$1y" }, { reg: /(tch|sh)es$/i, to: "$1" }, { reg: /(ss|zz)es$/i, to: "$1" }, { reg: /([tzlshicgrvdnkmu])es$/i, to: "$1e" }, { reg: /(n[dtk]|c[kt]|[eo]n|i[nl]|er|a[ytrl])s$/i, to: "$1" }, { reg: /(ow)s$/i, to: "$1" }, { reg: /(op)s$/i, to: "$1" }, { reg: /([eirs])ts$/i, to: "$1t" }, { reg: /(ll)s$/i, to: "$1" }, { reg: /(el)s$/i, to: "$1" }, { reg: /(ip)es$/i, to: "$1e" }, { reg: /ss$/i, to: "ss" }, { reg: /s$/i, to: "" }], Gerund: [{ reg: /(..)(p|d|t|g){2}ing$/i, to: "$1$2" }, { reg: /(ll|ss|zz)ing$/i, to: "$1" }, { reg: /([^aeiou])ying$/i, to: "$1y" }, { reg: /([^ae]i.)ing$/i, to: "$1e" }, { reg: /(ea[dklnrtv])ing$/i, to: "$1" }, { reg: /(ch|sh)ing$/i, to: "$1" }, { reg: /(z)ing$/i, to: "$1e" }, { reg: /(a[gdkvtc])ing$/i, to: "$1e" }, { reg: /(u[rtcbn])ing$/i, to: "$1e" }, { reg: /([^o]o[bdknprv])ing$/i, to: "$1e" }, { reg: /([tbckg]l)ing$/i, to: "$1e" }, { reg: /(c|s)ing$/i, to: "$1e" }, { reg: /(..)ing$/i, to: "$1" }], PastTense: [{ reg: /(ued)$/i, to: "ue" }, { reg: /ea(rn|l|m)ed$/i, to: "ea$1" }, { reg: /a([^aeiouy])ed$/i, to: "a$1e" }, { reg: /([aeiou]zz)ed$/i, to: "$1" }, { reg: /(e|i)lled$/i, to: "$1ll" }, { reg: /(.)(sh|ch)ed$/i, to: "$1$2" }, { reg: /(tl|gl)ed$/i, to: "$1e" }, { reg: /(um?pt?)ed$/i, to: "$1" }, { reg: /(ss)ed$/i, to: "$1" }, { reg: /pped$/i, to: "p" }, { reg: /tted$/i, to: "t" }, { reg: /(..)gged$/i, to: "$1g" }, { reg: /(..)lked$/i, to: "$1lk" }, { reg: /([^aeiouy][aeiou])ked$/i, to: "$1ke" }, { reg: /(.[aeiou])led$/i, to: "$1l" }, { reg: /(..)(h|ion|n[dt]|ai.|[cs]t|pp|all|ss|tt|int|ail|ld|en|oo.|er|k|pp|w|ou.|rt|ght|rm)ed$/i, to: "$1$2" }, { reg: /(.ut)ed$/i, to: "$1e" }, { reg: /(.pt)ed$/i, to: "$1" }, { reg: /(us)ed$/i, to: "$1e" }, { reg: /(dd)ed$/i, to: "$1" }, { reg: /(..[^aeiouy])ed$/i, to: "$1e" }, { reg: /(..)ied$/i, to: "$1y" }, { reg: /(.o)ed$/i, to: "$1o" }, { reg: /(..i)ed$/i, to: "$1" }, { reg: /(.a[^aeiou])ed$/i, to: "$1" }, { reg: /([aeiou][^aeiou])ed$/i, to: "$1e" }, { reg: /([rl])ew$/i, to: "$1ow" }, { reg: /([pl])t$/i, to: "$1t" }] };
let Ir = { Gerund: ["ing"], Actor: ["erer"], Infinitive: ["ate", "ize", "tion", "rify", "then", "ress", "ify", "age", "nce", "ect", "ise", "ine", "ish", "ace", "ash", "ure", "tch", "end", "ack", "and", "ute", "ade", "ock", "ite", "ase", "ose", "use", "ive", "int", "nge", "lay", "est", "ain", "ant", "ent", "eed", "er", "le", "own", "unk", "ung", "en"], PastTense: ["ed", "lt", "nt", "pt", "ew", "ld"], PresentTense: ["rks", "cks", "nks", "ngs", "mps", "tes", "zes", "ers", "les", "acks", "ends", "ands", "ocks", "lays", "eads", "lls", "els", "ils", "ows", "nds", "ays", "ams", "ars", "ops", "ffs", "als", "urs", "lds", "ews", "ips", "es", "ts", "ns"] };
Ir = Object.keys(Ir).reduce((e3, t2) => (Ir[t2].forEach((r2) => e3[r2] = t2), e3), {});
const Or2 = zr, Tr = Ir;
const Vr = Qt, Mr = sr, Jr = lr, Lr = St, Sr = { nouns: { addendum: "addenda", alga: "algae", alumna: "alumnae", alumnus: "alumni", analysis: "analyses", antenna: "antennae", appendix: "appendices", avocado: "avocados", axis: "axes", bacillus: "bacilli", barracks: "barracks", beau: "beaux", bus: "buses", cactus: "cacti", chateau: "chateaux", child: "children", circus: "circuses", clothes: "clothes", corpus: "corpora", criterion: "criteria", curriculum: "curricula", database: "databases", deer: "deer", diagnosis: "diagnoses", echo: "echoes", embargo: "embargoes", epoch: "epochs", foot: "feet", formula: "formulae", fungus: "fungi", genus: "genera", goose: "geese", halo: "halos", hippopotamus: "hippopotami", index: "indices", larva: "larvae", leaf: "leaves", libretto: "libretti", loaf: "loaves", man: "men", matrix: "matrices", memorandum: "memoranda", modulus: "moduli", mosquito: "mosquitoes", mouse: "mice", nebula: "nebulae", nucleus: "nuclei", octopus: "octopi", opus: "opera", ovum: "ova", ox: "oxen", parenthesis: "parentheses", person: "people", phenomenon: "phenomena", prognosis: "prognoses", quiz: "quizzes", radius: "radii", referendum: "referenda", rodeo: "rodeos", sex: "sexes", shoe: "shoes", sombrero: "sombreros", stimulus: "stimuli", stomach: "stomachs", syllabus: "syllabi", synopsis: "synopses", tableau: "tableaux", thesis: "theses", thief: "thieves", tooth: "teeth", tornado: "tornados", tuxedo: "tuxedos", vertebra: "vertebrae" }, verbs: dr }, _r = { conjugate: vr, adjectives: Nr, toPlural: Cr, toSingular: Gr, toInfinitive: function(e3, t2, r2) {
  if (!e3) return "";
  if (true === t2.words.hasOwnProperty(e3)) {
    let r3 = t2.irregulars.verbs, a2 = Object.keys(r3);
    for (let t3 = 0; t3 < a2.length; t3++) {
      let n2 = Object.keys(r3[a2[t3]]);
      for (let i2 = 0; i2 < n2.length; i2++) if (e3 === r3[a2[t3]][n2[i2]]) return a2[t3];
    }
  }
  if ((r2 = r2 || (function(e4) {
    let t3 = e4.substr(e4.length - 3);
    if (true === Tr.hasOwnProperty(t3)) return Tr[t3];
    let r3 = e4.substr(e4.length - 2);
    return true === Tr.hasOwnProperty(r3) ? Tr[r3] : "s" === e4.substr(e4.length - 1) ? "PresentTense" : null;
  })(e3)) && Or2[r2]) for (let t3 = 0; t3 < Or2[r2].length; t3++) {
    const a2 = Or2[r2][t3];
    if (true === a2.reg.test(e3)) return e3.replace(a2.reg, a2.to);
  }
  return e3;
} };
let Kr = false;
class qr {
  constructor() {
    Object.defineProperty(this, "words", { enumerable: false, value: {}, writable: true }), Object.defineProperty(this, "hasCompound", { enumerable: false, value: {}, writable: true }), Object.defineProperty(this, "irregulars", { enumerable: false, value: Sr, writable: true }), Object.defineProperty(this, "tags", { enumerable: false, value: Object.assign({}, Vr), writable: true }), Object.defineProperty(this, "transforms", { enumerable: false, value: _r, writable: true }), Object.defineProperty(this, "taggers", { enumerable: false, value: [], writable: true }), Object.defineProperty(this, "cache", { enumerable: false, value: { abbreviations: {} } }), this.words = Mr.buildOut(this), Jr(this);
  }
  verbose(e3) {
    return Kr = e3, this;
  }
  isVerbose() {
    return Kr;
  }
  addWords(e3) {
    let t2 = {};
    Object.keys(e3).forEach((r2) => {
      let a2 = e3[r2];
      r2 = r2.toLowerCase().trim(), t2[r2] = a2;
    }), Mr.addWords(t2, this.words, this);
  }
  addConjugations(e3) {
    return Object.assign(this.irregulars.verbs, e3), this;
  }
  addPlurals(e3) {
    return Object.assign(this.irregulars.nouns, e3), this;
  }
  addTags(e3) {
    return e3 = Object.assign({}, e3), this.tags = Object.assign(this.tags, e3), this.tags = Lr(this.tags), this;
  }
  postProcess(e3) {
    return this.taggers.push(e3), this;
  }
  stats() {
    return { words: Object.keys(this.words).length, plurals: Object.keys(this.irregulars.nouns).length, conjugations: Object.keys(this.irregulars.verbs).length, compounds: Object.keys(this.hasCompound).length, postProcessors: this.taggers.length };
  }
}
const Wr = function(e3) {
  return JSON.parse(JSON.stringify(e3));
};
qr.prototype.clone = function() {
  let e3 = new qr();
  return e3.words = Object.assign({}, this.words), e3.hasCompound = Object.assign({}, this.hasCompound), e3.irregulars = Wr(this.irregulars), e3.tags = Wr(this.tags), e3.transforms = this.transforms, e3.taggers = this.taggers, e3;
};
var Rr = qr, Ur = {};
!(function(e3) {
  e3.all = function() {
    return this.parents()[0] || this;
  }, e3.parent = function() {
    return this.from ? this.from : this;
  }, e3.parents = function(e4) {
    let t2 = [];
    const r2 = function(e5) {
      e5.from && (t2.push(e5.from), r2(e5.from));
    };
    return r2(this), t2 = t2.reverse(), "number" == typeof e4 ? t2[e4] : t2;
  }, e3.clone = function(e4) {
    let t2 = this.list.map((t3) => t3.clone(e4));
    return this.buildFrom(t2);
  }, e3.wordCount = function() {
    return this.list.reduce((e4, t2) => e4 += t2.wordCount(), 0);
  }, e3.wordcount = e3.wordCount;
})(Ur);
var Qr = {};
!(function(e3) {
  e3.first = function(e4) {
    return void 0 === e4 ? this.get(0) : this.slice(0, e4);
  }, e3.last = function(e4) {
    if (void 0 === e4) return this.get(this.list.length - 1);
    let t2 = this.list.length;
    return this.slice(t2 - e4, t2);
  }, e3.slice = function(e4, t2) {
    let r2 = this.list.slice(e4, t2);
    return this.buildFrom(r2);
  }, e3.eq = function(e4) {
    let t2 = this.list[e4];
    return void 0 === t2 ? this.buildFrom([]) : this.buildFrom([t2]);
  }, e3.get = e3.eq, e3.firstTerms = function() {
    return this.match("^.");
  }, e3.firstTerm = e3.firstTerms, e3.lastTerms = function() {
    return this.match(".$");
  }, e3.lastTerm = e3.lastTerms, e3.termList = function(e4) {
    let t2 = [];
    for (let r2 = 0; r2 < this.list.length; r2++) {
      let a2 = this.list[r2].terms();
      for (let r3 = 0; r3 < a2.length; r3++) if (t2.push(a2[r3]), void 0 !== e4 && void 0 !== t2[e4]) return t2[e4];
    }
    return t2;
  };
  e3.groups = function(e4) {
    return void 0 === e4 ? (function(e5) {
      let t2 = {};
      const r2 = {};
      for (let t3 = 0; t3 < e5.list.length; t3++) {
        const a3 = e5.list[t3], n2 = Object.keys(a3.groups).map((e6) => a3.groups[e6]);
        for (let e6 = 0; e6 < n2.length; e6++) {
          const { group: t4, start: i2, length: o2 } = n2[e6];
          r2[t4] || (r2[t4] = []), r2[t4].push(a3.buildFrom(i2, o2));
        }
      }
      const a2 = Object.keys(r2);
      for (let n2 = 0; n2 < a2.length; n2++) {
        const i2 = a2[n2];
        t2[i2] = e5.buildFrom(r2[i2]);
      }
      return t2;
    })(this) : ("number" == typeof e4 && (e4 = String(e4)), (function(e5, t2) {
      const r2 = [];
      for (let a2 = 0; a2 < e5.list.length; a2++) {
        const n2 = e5.list[a2];
        let i2 = Object.keys(n2.groups);
        i2 = i2.filter((e6) => n2.groups[e6].group === t2), i2.forEach((e6) => {
          r2.push(n2.buildFrom(n2.groups[e6].start, n2.groups[e6].length));
        });
      }
      return e5.buildFrom(r2);
    })(this, e4) || this.buildFrom([]));
  }, e3.group = e3.groups, e3.sentences = function(e4) {
    let t2 = [];
    return this.list.forEach((e5) => {
      t2.push(e5.fullSentence());
    }), "number" == typeof e4 ? this.buildFrom([t2[e4]]) : this.buildFrom(t2);
  }, e3.sentence = e3.sentences;
})(Qr);
var Zr = {};
var Xr = function(e3, t2) {
  if (e3._cache && true === e3._cache.set) {
    let { words: r2, tags: a2 } = (function(e4) {
      let t3 = [], r3 = [];
      return e4.forEach((e5) => {
        true !== e5.optional && true !== e5.negative && (void 0 !== e5.tag && t3.push(e5.tag), void 0 !== e5.word && r3.push(e5.word));
      }), { tags: t3, words: r3 };
    })(t2);
    for (let t3 = 0; t3 < r2.length; t3++) if (void 0 === e3._cache.words[r2[t3]]) return false;
    for (let t3 = 0; t3 < a2.length; t3++) if (void 0 === e3._cache.tags[a2[t3]]) return false;
  }
  return true;
};
!(function(e3) {
  const t2 = _e, r2 = Xr;
  e3.match = function(e4, a2 = {}) {
    "string" != typeof a2 && "number" != typeof a2 && null !== a2 || (a2 = { group: a2 });
    let n2 = t2(e4, a2);
    if (0 === n2.length) return this.buildFrom([]);
    if (false === r2(this, n2)) return this.buildFrom([]);
    let i2 = this.list.reduce((e5, t3) => e5.concat(t3.match(n2)), []);
    return void 0 !== a2.group && null !== a2.group && "" !== a2.group ? this.buildFrom(i2).groups(a2.group) : this.buildFrom(i2);
  }, e3.not = function(e4, a2 = {}) {
    let n2 = t2(e4, a2);
    if (0 === n2.length || false === r2(this, n2)) return this;
    let i2 = this.list.reduce((e5, t3) => e5.concat(t3.not(n2)), []);
    return this.buildFrom(i2);
  }, e3.matchOne = function(e4, a2 = {}) {
    let n2 = t2(e4, a2);
    if (false === r2(this, n2)) return this.buildFrom([]);
    for (let e5 = 0; e5 < this.list.length; e5++) {
      let t3 = this.list[e5].match(n2, true);
      return this.buildFrom(t3);
    }
    return this.buildFrom([]);
  }, e3.if = function(e4, a2 = {}) {
    let n2 = t2(e4, a2);
    if (false === r2(this, n2)) return this.buildFrom([]);
    let i2 = this.list.filter((e5) => true === e5.has(n2));
    return this.buildFrom(i2);
  }, e3.ifNo = function(e4, r3 = {}) {
    let a2 = t2(e4, r3), n2 = this.list.filter((e5) => false === e5.has(a2));
    return this.buildFrom(n2);
  }, e3.has = function(e4, a2 = {}) {
    let n2 = t2(e4, a2);
    return false !== r2(this, n2) && this.list.some((e5) => true === e5.has(n2));
  }, e3.lookAhead = function(e4, r3 = {}) {
    e4 || (e4 = ".*");
    let a2 = t2(e4, r3), n2 = [];
    return this.list.forEach((e5) => {
      n2 = n2.concat(e5.lookAhead(a2));
    }), n2 = n2.filter((e5) => e5), this.buildFrom(n2);
  }, e3.lookAfter = e3.lookAhead, e3.lookBehind = function(e4, r3 = {}) {
    e4 || (e4 = ".*");
    let a2 = t2(e4, r3), n2 = [];
    return this.list.forEach((e5) => {
      n2 = n2.concat(e5.lookBehind(a2));
    }), n2 = n2.filter((e5) => e5), this.buildFrom(n2);
  }, e3.lookBefore = e3.lookBehind, e3.before = function(e4, r3 = {}) {
    let a2 = t2(e4, r3), n2 = this.if(a2).list.map((e5) => {
      let t3 = e5.terms().map((e6) => e6.id), r4 = e5.match(a2)[0], n3 = t3.indexOf(r4.start);
      return 0 === n3 || -1 === n3 ? null : e5.buildFrom(e5.start, n3);
    });
    return n2 = n2.filter((e5) => null !== e5), this.buildFrom(n2);
  }, e3.after = function(e4, r3 = {}) {
    let a2 = t2(e4, r3), n2 = this.if(a2).list.map((e5) => {
      let t3 = e5.terms(), r4 = t3.map((e6) => e6.id), n3 = e5.match(a2)[0], i2 = r4.indexOf(n3.start);
      if (-1 === i2 || !t3[i2 + n3.length]) return null;
      let o2 = t3[i2 + n3.length].id, s2 = e5.length - i2 - n3.length;
      return e5.buildFrom(o2, s2);
    });
    return n2 = n2.filter((e5) => null !== e5), this.buildFrom(n2);
  }, e3.hasAfter = function(e4, t3 = {}) {
    return this.filter((r3) => r3.lookAfter(e4, t3).found);
  }, e3.hasBefore = function(e4, t3 = {}) {
    return this.filter((r3) => r3.lookBefore(e4, t3).found);
  };
})(Zr);
var Yr = {};
const ea = function(e3, t2, r2, a2) {
  let n2 = [];
  "string" == typeof e3 && (n2 = e3.split(" ")), t2.list.forEach((i2) => {
    let o2 = i2.terms();
    true === r2 && (o2 = o2.filter((r3) => r3.canBe(e3, t2.world))), o2.forEach((r3, i3) => {
      n2.length > 1 ? n2[i3] && "." !== n2[i3] && r3.tag(n2[i3], a2, t2.world) : r3.tag(e3, a2, t2.world);
    });
  });
};
Yr.tag = function(e3, t2) {
  return e3 ? (ea(e3, this, false, t2), this) : this;
}, Yr.tagSafe = function(e3, t2) {
  return e3 ? (ea(e3, this, true, t2), this) : this;
}, Yr.unTag = function(e3, t2) {
  return this.list.forEach((r2) => {
    r2.terms().forEach((r3) => r3.unTag(e3, t2, this.world));
  }), this;
}, Yr.canBe = function(e3) {
  if (!e3) return this;
  let t2 = this.world, r2 = this.list.reduce((r3, a2) => r3.concat(a2.canBe(e3, t2)), []);
  return this.buildFrom(r2);
};
var ta = { map: function(e3) {
  if (!e3) return this;
  let t2 = this.list.map((t3, r2) => {
    let a2 = this.buildFrom([t3]);
    a2.from = null;
    let n2 = e3(a2, r2);
    return n2 && n2.list && n2.list[0] ? n2.list[0] : n2;
  });
  return t2 = t2.filter((e4) => e4), 0 === t2.length ? this.buildFrom(t2) : "object" != typeof t2[0] || "Phrase" !== t2[0].isA ? t2 : this.buildFrom(t2);
}, forEach: function(e3, t2) {
  return e3 ? (this.list.forEach((r2, a2) => {
    let n2 = this.buildFrom([r2]);
    true === t2 && (n2.from = null), e3(n2, a2);
  }), this) : this;
}, filter: function(e3) {
  if (!e3) return this;
  let t2 = this.list.filter((t3, r2) => {
    let a2 = this.buildFrom([t3]);
    return a2.from = null, e3(a2, r2);
  });
  return this.buildFrom(t2);
}, find: function(e3) {
  if (!e3) return this;
  let t2 = this.list.find((t3, r2) => {
    let a2 = this.buildFrom([t3]);
    return a2.from = null, e3(a2, r2);
  });
  return t2 ? this.buildFrom([t2]) : void 0;
}, some: function(e3) {
  return e3 ? this.list.some((t2, r2) => {
    let a2 = this.buildFrom([t2]);
    return a2.from = null, e3(a2, r2);
  }) : this;
}, random: function(e3) {
  if (!this.found) return this;
  let t2 = Math.floor(Math.random() * this.list.length);
  if (void 0 === e3) {
    let e4 = [this.list[t2]];
    return this.buildFrom(e4);
  }
  return t2 + e3 > this.length && (t2 = this.length - e3, t2 = t2 < 0 ? 0 : t2), this.slice(t2, t2 + e3);
} }, ra = {};
var aa = function(e3, t2, r2) {
  let a2 = (function(e4, t3 = []) {
    let r3 = {};
    return e4.forEach((e5, a3) => {
      let n3 = true;
      void 0 !== t3[a3] && (n3 = t3[a3]);
      let i2 = (function(e6) {
        return e6.split(/[ -]/g);
      })(e5 = (e5 = (e5 || "").toLowerCase()).replace(/[,;.!?]+$/, "")).map((e6) => e6.trim());
      r3[i2[0]] = r3[i2[0]] || {}, 1 === i2.length ? r3[i2[0]].value = n3 : (r3[i2[0]].more = r3[i2[0]].more || [], r3[i2[0]].more.push({ rest: i2.slice(1), value: n3 }));
    }), r3;
  })(e3, t2), n2 = [];
  for (let e4 = 0; e4 < r2.list.length; e4++) {
    const t3 = r2.list[e4];
    let i2 = t3.terms().map((e5) => e5.reduced);
    for (let e5 = 0; e5 < i2.length; e5++) void 0 !== a2[i2[e5]] && (void 0 !== a2[i2[e5]].more && a2[i2[e5]].more.forEach((r3) => {
      if (void 0 === i2[e5 + r3.rest.length]) return;
      true === r3.rest.every((t4, r4) => t4 === i2[e5 + r4 + 1]) && n2.push({ id: t3.terms()[e5].id, value: r3.value, length: r3.rest.length + 1 });
    }), void 0 !== a2[i2[e5]].value && n2.push({ id: t3.terms()[e5].id, value: a2[i2[e5]].value, length: 1 }));
  }
  return n2;
};
!(function(e3) {
  const t2 = aa;
  e3.lookup = function(e4) {
    let r2 = [], a2 = (n2 = e4) && "[object Object]" === Object.prototype.toString.call(n2);
    var n2;
    true === a2 && (e4 = Object.keys(e4).map((t3) => (r2.push(e4[t3]), t3))), "string" == typeof e4 && (e4 = [e4]), true !== this._cache.set && this.cache();
    let i2 = t2(e4, r2, this), o2 = this.list[0];
    if (true === a2) {
      let e5 = {};
      return i2.forEach((t3) => {
        e5[t3.value] = e5[t3.value] || [], e5[t3.value].push(o2.buildFrom(t3.id, t3.length));
      }), Object.keys(e5).forEach((t3) => {
        e5[t3] = this.buildFrom(e5[t3]);
      }), e5;
    }
    return i2 = i2.map((e5) => o2.buildFrom(e5.id, e5.length)), this.buildFrom(i2);
  }, e3.lookUp = e3.lookup;
})(ra);
var na = { cache: function(e3) {
  e3 = e3 || {};
  let t2 = {}, r2 = {};
  return this._cache.words = t2, this._cache.tags = r2, this._cache.set = true, this.list.forEach((a2, n2) => {
    a2.cache = a2.cache || {}, a2.terms().forEach((a3) => {
      t2[a3.reduced] && !t2.hasOwnProperty(a3.reduced) || (t2[a3.reduced] = t2[a3.reduced] || [], t2[a3.reduced].push(n2), Object.keys(a3.tags).forEach((e4) => {
        r2[e4] = r2[e4] || [], r2[e4].push(n2);
      }), e3.root && (a3.setRoot(this.world), t2[a3.root] = [n2]));
    });
  }), this;
}, uncache: function() {
  return this._cache = {}, this.list.forEach((e3) => {
    e3.cache = {};
  }), this.parents().forEach((e3) => {
    e3._cache = {}, e3.list.forEach((e4) => {
      e4.cache = {};
    });
  }), this;
} }, ia = {};
const oa = xt;
ia.replaceWith = function(e3, t2 = {}) {
  return e3 ? (true === t2 && (t2 = { keepTags: true }), false === t2 && (t2 = { keepTags: false }), t2 = t2 || {}, this.uncache(), this.list.forEach((r2) => {
    let a2, n2 = e3;
    if ("function" == typeof e3 && (n2 = e3(r2)), n2 && "object" == typeof n2 && "Doc" === n2.isA) a2 = n2.list, this.pool().merge(n2.pool());
    else {
      if ("string" != typeof n2) return;
      {
        false !== t2.keepCase && r2.terms(0).isTitleCase() && (n2 = (i2 = n2).charAt(0).toUpperCase() + i2.substr(1)), a2 = oa(n2, this.world, this.pool());
        let e4 = this.buildFrom(a2);
        e4.tagger(), a2 = e4.list;
      }
    }
    var i2;
    if (true === t2.keepTags) {
      let e4 = r2.json({ terms: { tags: true } }).terms;
      a2[0].terms().forEach((t3, r3) => {
        e4[r3] && t3.tagSafe(e4[r3].tags, "keptTag", this.world);
      });
    }
    r2.replace(a2[0], this);
  }), this) : this.delete();
}, ia.replace = function(e3, t2, r2) {
  return void 0 === t2 ? this.replaceWith(e3, r2) : (this.match(e3).replaceWith(t2, r2), this);
};
var sa = {};
!(function(e3) {
  const t2 = xt, r2 = function(e4) {
    return e4 && "[object Object]" === Object.prototype.toString.call(e4);
  }, a2 = function(e4, r3) {
    let a3 = t2(e4, r3.world)[0], n2 = r3.buildFrom([a3]);
    return n2.tagger(), r3.list = n2.list, r3;
  };
  e3.append = function(e4 = "") {
    return e4 ? this.found ? (this.uncache(), this.list.forEach((a3) => {
      let n2;
      r2(e4) && "Doc" === e4.isA ? n2 = e4.list[0].clone() : "string" == typeof e4 && (n2 = t2(e4, this.world, this.pool())[0]), this.buildFrom([n2]).tagger(), a3.append(n2, this);
    }), this) : a2(e4, this) : this;
  }, e3.insertAfter = e3.append, e3.insertAt = e3.append, e3.prepend = function(e4) {
    return e4 ? this.found ? (this.uncache(), this.list.forEach((a3) => {
      let n2;
      r2(e4) && "Doc" === e4.isA ? n2 = e4.list[0].clone() : "string" == typeof e4 && (n2 = t2(e4, this.world, this.pool())[0]), this.buildFrom([n2]).tagger(), a3.prepend(n2, this);
    }), this) : a2(e4, this) : this;
  }, e3.insertBefore = e3.prepend, e3.concat = function() {
    this.uncache();
    let e4 = this.list.slice(0);
    for (let r3 = 0; r3 < arguments.length; r3++) {
      let a3 = arguments[r3];
      if ("string" == typeof a3) {
        let r4 = t2(a3, this.world);
        e4 = e4.concat(r4);
      } else "Doc" === a3.isA ? e4 = e4.concat(a3.list) : "Phrase" === a3.isA && e4.push(a3);
    }
    return this.buildFrom(e4);
  }, e3.delete = function(e4) {
    this.uncache();
    let t3 = this;
    return e4 && (t3 = this.match(e4)), t3.list.forEach((e5) => e5.delete(this)), this;
  }, e3.remove = e3.delete;
})(sa);
var la = {};
const ua = { clean: true, reduced: true, root: true };
la.text = function(e3) {
  e3 = e3 || {};
  let t2 = false;
  0 === this.parents().length && (t2 = true), ("root" === e3 || "object" == typeof e3 && e3.root) && this.list.forEach((e4) => {
    e4.terms().forEach((e5) => {
      null === e5.root && e5.setRoot(this.world);
    });
  });
  let r2 = this.list.reduce((r3, a2, n2) => {
    const i2 = !t2 && 0 === n2, o2 = !t2 && n2 === this.list.length - 1;
    return r3 + a2.text(e3, i2, o2);
  }, "");
  return true !== ua[e3] && true !== e3.reduced && true !== e3.clean && true !== e3.root || (r2 = r2.trim()), r2;
};
var ca = {};
var ha = function(e3, t2, r2) {
  let a2 = (function(e4) {
    let t3 = 0, r3 = 0, a3 = {};
    return e4.termList().forEach((e5) => {
      a3[e5.id] = { index: r3, start: t3 + e5.pre.length, length: e5.text.length }, t3 += e5.pre.length + e5.text.length + e5.post.length, r3 += 1;
    }), a3;
  })(e3.all());
  (r2.terms.index || r2.index) && t2.forEach((e4) => {
    e4.terms.forEach((e5) => {
      e5.index = a2[e5.id].index;
    }), e4.index = e4.terms[0].index;
  }), (r2.terms.offset || r2.offset) && t2.forEach((e4) => {
    e4.terms.forEach((e5) => {
      e5.offset = a2[e5.id] || {};
    }), e4.offset = { index: e4.terms[0].offset.index, start: e4.terms[0].offset.start - e4.text.indexOf(e4.terms[0].text), length: e4.text.length };
  });
};
!(function(e3) {
  const t2 = ha, r2 = { text: true, terms: true, trim: true };
  e3.json = function(e4 = {}) {
    if ("number" == typeof e4 && this.list[e4]) return this.list[e4].json(r2);
    true === (e4 = (function(e5) {
      return (e5 = Object.assign({}, r2, e5)).unique && (e5.reduced = true), e5.offset && (e5.text = true, e5.terms && true !== e5.terms || (e5.terms = {}), e5.terms.offset = true), (e5.index || e5.terms.index) && (e5.terms = true === e5.terms ? {} : e5.terms, e5.terms.id = true), e5;
    })(e4)).root && this.list.forEach((e5) => {
      e5.terms().forEach((e6) => {
        null === e6.root && e6.setRoot(this.world);
      });
    });
    let a2 = this.list.map((t3) => t3.json(e4, this.world));
    if ((e4.terms.offset || e4.offset || e4.terms.index || e4.index) && t2(this, a2, e4), e4.frequency || e4.freq || e4.count) {
      let e5 = {};
      this.list.forEach((t3) => {
        let r3 = t3.text("reduced");
        e5[r3] = e5[r3] || 0, e5[r3] += 1;
      }), this.list.forEach((t3, r3) => {
        a2[r3].count = e5[t3.text("reduced")];
      });
    }
    if (e4.unique) {
      let e5 = {};
      a2 = a2.filter((t3) => true !== e5[t3.reduced] && (e5[t3.reduced] = true, true));
    }
    return a2;
  }, e3.data = e3.json;
})(ca);
var da = {}, ga = { exports: {} };
!(function(e3) {
  const t2 = "\x1B[0m", r2 = function(e4, t3) {
    for (e4 = e4.toString(); e4.length < t3; ) e4 += " ";
    return e4;
  };
  const a2 = { green: "#7f9c6c", red: "#914045", blue: "#6699cc", magenta: "#6D5685", cyan: "#2D85A8", yellow: "#e6d7b3", black: "#303b50" }, n2 = { green: function(e4) {
    return "\x1B[32m" + e4 + t2;
  }, red: function(e4) {
    return "\x1B[31m" + e4 + t2;
  }, blue: function(e4) {
    return "\x1B[34m" + e4 + t2;
  }, magenta: function(e4) {
    return "\x1B[35m" + e4 + t2;
  }, cyan: function(e4) {
    return "\x1B[36m" + e4 + t2;
  }, yellow: function(e4) {
    return "\x1B[33m" + e4 + t2;
  }, black: function(e4) {
    return "\x1B[30m" + e4 + t2;
  } };
  ga.exports = function(e4) {
    return "undefined" != typeof window && window.document ? ((function(e5) {
      let t3 = e5.world.tags;
      e5.list.forEach((e6) => {
        console.log('\n%c"' + e6.text() + '"', "color: #e6d7b3;"), e6.terms().forEach((e7) => {
          let n3 = Object.keys(e7.tags), i2 = e7.text || "-";
          e7.implicit && (i2 = "[" + e7.implicit + "]");
          let o2 = "'" + i2 + "'";
          o2 = r2(o2, 8);
          let s2 = n3.find((e8) => t3[e8] && t3[e8].color), l2 = "steelblue";
          t3[s2] && (l2 = t3[s2].color, l2 = a2[l2]), console.log(`   ${o2}  -  %c${n3.join(", ")}`, `color: ${l2 || "steelblue"};`);
        });
      });
    })(e4), e4) : (console.log(n2.blue("=====")), e4.list.forEach((t3) => {
      console.log(n2.blue("  -----")), t3.terms().forEach((t4) => {
        let a3 = Object.keys(t4.tags), i2 = t4.text || "-";
        t4.implicit && (i2 = "[" + t4.implicit + "]"), i2 = n2.yellow(i2);
        let o2 = "'" + i2 + "'";
        o2 = r2(o2, 18);
        let s2 = n2.blue("  ｜ ") + o2 + "  - " + (function(e5, t5) {
          return (e5 = e5.map((e6) => {
            if (!t5.tags.hasOwnProperty(e6)) return e6;
            const r3 = t5.tags[e6].color || "blue";
            return n2[r3](e6);
          })).join(", ");
        })(a3, e4.world);
        console.log(s2);
      });
    }), console.log(""), e4);
  };
})();
const pa = ga.exports, ma = function(e3) {
  let t2 = e3.json({ text: false, terms: false, reduced: true }), r2 = {};
  t2.forEach((e4) => {
    r2[e4.reduced] || (e4.count = 0, r2[e4.reduced] = e4), r2[e4.reduced].count += 1;
  });
  let a2 = Object.keys(r2).map((e4) => r2[e4]);
  return a2.sort((e4, t3) => e4.count > t3.count ? -1 : e4.count < t3.count ? 1 : 0), a2;
};
da.debug = function() {
  return pa(this), this;
}, da.out = function(e3) {
  if ("text" === e3) return this.text();
  if ("normal" === e3) return this.text("normal");
  if ("json" === e3) return this.json();
  if ("offset" === e3 || "offsets" === e3) return this.json({ offset: true });
  if ("array" === e3) return this.json({ terms: false }).map((e4) => e4.text).filter((e4) => e4);
  if ("freq" === e3 || "frequency" === e3) return ma(this);
  if ("terms" === e3) {
    let e4 = [];
    return this.json({ text: false, terms: { text: true } }).forEach((t2) => {
      let r2 = t2.terms.map((e5) => e5.text);
      r2 = r2.filter((e5) => e5), e4 = e4.concat(r2);
    }), e4;
  }
  return "tags" === e3 ? this.list.map((e4) => e4.terms().reduce((e5, t2) => (e5[t2.clean || t2.implicit] = Object.keys(t2.tags), e5), {})) : "debug" === e3 ? (pa(this), this) : this.text();
};
var fa = {};
const ba = { alpha: (e3, t2) => {
  let r2 = e3.text("clean"), a2 = t2.text("clean");
  return r2 < a2 ? -1 : r2 > a2 ? 1 : 0;
}, length: (e3, t2) => {
  let r2 = e3.text().trim().length, a2 = t2.text().trim().length;
  return r2 < a2 ? 1 : r2 > a2 ? -1 : 0;
}, wordCount: (e3, t2) => {
  let r2 = e3.wordCount(), a2 = t2.wordCount();
  return r2 < a2 ? 1 : r2 > a2 ? -1 : 0;
} };
ba.alphabetical = ba.alpha, ba.wordcount = ba.wordCount;
const ya = { index: true, sequence: true, seq: true, sequential: true, chron: true, chronological: true };
fa.sort = function(e3) {
  return "freq" === (e3 = e3 || "alpha") || "frequency" === e3 || "topk" === e3 ? (function(e4) {
    let t2 = {};
    const r2 = { case: true, punctuation: false, whitespace: true, unicode: true };
    return e4.list.forEach((e5) => {
      let a2 = e5.text(r2);
      t2[a2] = t2[a2] || 0, t2[a2] += 1;
    }), e4.list.sort((e5, a2) => {
      let n2 = t2[e5.text(r2)], i2 = t2[a2.text(r2)];
      return n2 < i2 ? 1 : n2 > i2 ? -1 : 0;
    }), e4;
  })(this) : ya.hasOwnProperty(e3) ? (function(e4) {
    let t2 = {};
    return e4.json({ terms: { offset: true } }).forEach((e5) => {
      t2[e5.terms[0].id] = e5.terms[0].offset.start;
    }), e4.list = e4.list.sort((e5, r2) => t2[e5.start] > t2[r2.start] ? 1 : t2[e5.start] < t2[r2.start] ? -1 : 0), e4;
  })(this) : "function" == typeof (e3 = ba[e3] || e3) ? (this.list = this.list.sort(e3), this) : this;
}, fa.reverse = function() {
  let e3 = [].concat(this.list);
  return e3 = e3.reverse(), this.buildFrom(e3);
}, fa.unique = function() {
  let e3 = [].concat(this.list), t2 = {};
  return e3 = e3.filter((e4) => {
    let r2 = e4.text("reduced").trim() || e4.text("implicit").trim();
    return true !== t2.hasOwnProperty(r2) && (t2[r2] = true, true);
  }), this.buildFrom(e3);
};
var va = {};
const wa = n$1, ka = /[\[\]{}⟨⟩:,،、‒–—―…‹›«»‐\-;\/⁄·*\•^†‡°¡¿※№÷×ºª%‰=‱¶§~|‖¦©℗®℠™¤₳฿]/g, Aa = /['‘’“”"′″‴]+/g;
const Da = { whitespace: function(e3) {
  let t2 = e3.list.map((e4) => e4.terms());
  t2.forEach((e4, r2) => {
    e4.forEach((a2, n2) => {
      true !== a2.hasDash() ? (a2.pre = a2.pre.replace(/\s/g, ""), a2.post = a2.post.replace(/\s/g, ""), (e4.length - 1 !== n2 || t2[r2 + 1]) && (a2.implicit && true === Boolean(a2.text) || true !== a2.hasHyphen() && (a2.post += " "))) : a2.post = " - ";
    });
  });
}, punctuation: function(e3) {
  e3.forEach((e4) => {
    true === e4.hasHyphen() && (e4.post = " "), e4.pre = e4.pre.replace(ka, ""), e4.post = e4.post.replace(ka, ""), e4.post = e4.post.replace(/\.\.\./, ""), true === /!/.test(e4.post) && (e4.post = e4.post.replace(/!/g, ""), e4.post = "!" + e4.post), true === /\?/.test(e4.post) && (e4.post = e4.post.replace(/[\?!]*/, ""), e4.post = "?" + e4.post);
  });
}, unicode: function(e3) {
  e3.forEach((e4) => {
    true !== e4.isImplicit() && (e4.text = wa(e4.text));
  });
}, quotations: function(e3) {
  e3.forEach((e4) => {
    e4.post = e4.post.replace(Aa, ""), e4.pre = e4.pre.replace(Aa, "");
  });
}, adverbs: function(e3) {
  e3.match("#Adverb").not("(not|nary|seldom|never|barely|almost|basically|so)").remove();
}, abbreviations: function(e3) {
  e3.list.forEach((e4) => {
    let t2 = e4.terms();
    t2.forEach((e5, r2) => {
      true === e5.tags.Abbreviation && t2[r2 + 1] && (e5.post = e5.post.replace(/^\./, ""));
    });
  });
} }, $a = { whitespace: true, unicode: true, punctuation: true, emoji: true, acronyms: true, abbreviations: true, case: false, contractions: false, parentheses: false, quotations: false, adverbs: false, possessives: false, verbs: false, nouns: false, honorifics: false }, Pa = { light: {}, medium: { case: true, contractions: true, parentheses: true, quotations: true, adverbs: true } };
Pa.heavy = Object.assign({}, Pa.medium, { possessives: true, verbs: true, nouns: true, honorifics: true }), va.normalize = function(e3) {
  "string" == typeof (e3 = e3 || {}) && (e3 = Pa[e3] || {}), e3 = Object.assign({}, $a, e3), this.uncache();
  let t2 = this.termList();
  return e3.case && this.toLowerCase(), e3.whitespace && Da.whitespace(this), e3.unicode && Da.unicode(t2), e3.punctuation && Da.punctuation(t2), e3.emoji && this.remove("(#Emoji|#Emoticon)"), e3.acronyms && this.acronyms().strip(), e3.abbreviations && Da.abbreviations(this), (e3.contraction || e3.contractions) && this.contractions().expand(), e3.parentheses && this.parentheses().unwrap(), (e3.quotations || e3.quotes) && Da.quotations(t2), e3.adverbs && Da.adverbs(this), (e3.possessive || e3.possessives) && this.possessives().strip(), e3.verbs && this.verbs().toInfinitive(), (e3.nouns || e3.plurals) && this.nouns().toSingular(), e3.honorifics && this.remove("#Honorific"), this;
};
var Ea = {};
!(function(e3) {
  const t2 = _e;
  e3.splitOn = function(e4) {
    if (!e4) {
      return this.parent().splitOn(this);
    }
    let r2 = t2(e4), a2 = [];
    return this.list.forEach((e5) => {
      let t3 = e5.match(r2);
      if (0 === t3.length) return void a2.push(e5);
      let n2 = e5;
      t3.forEach((e6) => {
        let t4 = n2.splitOn(e6);
        t4.before && a2.push(t4.before), t4.match && a2.push(t4.match), n2 = t4.after;
      }), n2 && a2.push(n2);
    }), this.buildFrom(a2);
  }, e3.splitAfter = function(e4) {
    if (!e4) {
      return this.parent().splitAfter(this);
    }
    let r2 = t2(e4), a2 = [];
    return this.list.forEach((e5) => {
      let t3 = e5.match(r2);
      if (0 === t3.length) return void a2.push(e5);
      let n2 = e5;
      t3.forEach((e6) => {
        let t4 = n2.splitOn(e6);
        t4.before && t4.match ? (t4.before.length += t4.match.length, a2.push(t4.before)) : t4.match && a2.push(t4.match), n2 = t4.after;
      }), n2 && a2.push(n2);
    }), this.buildFrom(a2);
  }, e3.split = e3.splitAfter, e3.splitBefore = function(e4) {
    if (!e4) {
      return this.parent().splitBefore(this);
    }
    let r2 = t2(e4), a2 = [];
    return this.list.forEach((e5) => {
      let t3 = e5.match(r2);
      if (0 === t3.length) return void a2.push(e5);
      let n2 = e5;
      t3.forEach((e6) => {
        let t4 = n2.splitOn(e6);
        t4.before && a2.push(t4.before), t4.match && t4.after && (t4.match.length += t4.after.length), n2 = t4.match;
      }), n2 && a2.push(n2);
    }), this.buildFrom(a2);
  }, e3.segment = function(e4, t3) {
    e4 = e4 || {}, t3 = t3 || { text: true };
    let r2 = this, a2 = Object.keys(e4);
    return a2.forEach((e5) => {
      r2 = r2.splitOn(e5);
    }), r2.list.forEach((t4) => {
      for (let r3 = 0; r3 < a2.length; r3 += 1) if (t4.has(a2[r3])) return void (t4.segment = e4[a2[r3]]);
    }), r2.list.map((e5) => {
      let r3 = e5.json(t3);
      return r3.segment = e5.segment || null, r3;
    });
  };
})(Ea);
var Ha = {};
const ja = function(e3, t2) {
  let r2 = e3.world;
  return e3.list.forEach((e4) => {
    e4.terms().forEach((e5) => e5[t2](r2));
  }), e3;
};
Ha.toLowerCase = function() {
  return ja(this, "toLowerCase");
}, Ha.toUpperCase = function() {
  return ja(this, "toUpperCase");
}, Ha.toTitleCase = function() {
  return ja(this, "toTitleCase");
}, Ha.toCamelCase = function() {
  return this.list.forEach((e3) => {
    let t2 = e3.terms();
    t2.forEach((e4, r2) => {
      0 !== r2 && e4.toTitleCase(), r2 !== t2.length - 1 && (e4.post = "");
    });
  }), this;
};
var Na = {};
!(function(e3) {
  e3.pre = function(e4, t2) {
    return void 0 === e4 ? this.list[0].terms(0).pre : (this.list.forEach((r2) => {
      let a2 = r2.terms(0);
      true === t2 ? a2.pre += e4 : a2.pre = e4;
    }), this);
  }, e3.post = function(e4, t2) {
    return void 0 === e4 ? this.list.map((e5) => {
      let t3 = e5.terms();
      return t3[t3.length - 1].post;
    }) : (this.list.forEach((r2) => {
      let a2 = r2.terms(), n2 = a2[a2.length - 1];
      true === t2 ? n2.post += e4 : n2.post = e4;
    }), this);
  }, e3.trim = function() {
    return this.list = this.list.map((e4) => e4.trim()), this;
  }, e3.hyphenate = function() {
    return this.list.forEach((e4) => {
      let t2 = e4.terms();
      t2.forEach((e5, r2) => {
        0 !== r2 && (e5.pre = ""), t2[r2 + 1] && (e5.post = "-");
      });
    }), this;
  }, e3.dehyphenate = function() {
    const e4 = /(-|–|—)/;
    return this.list.forEach((t2) => {
      t2.terms().forEach((t3) => {
        e4.test(t3.post) && (t3.post = " ");
      });
    }), this;
  }, e3.deHyphenate = e3.dehyphenate, e3.toQuotations = function(e4, t2) {
    return e4 = e4 || '"', t2 = t2 || '"', this.list.forEach((r2) => {
      let a2 = r2.terms();
      a2[0].pre = e4 + a2[0].pre;
      let n2 = a2[a2.length - 1];
      n2.post = t2 + n2.post;
    }), this;
  }, e3.toQuotation = e3.toQuotations, e3.toParentheses = function(e4, t2) {
    return e4 = e4 || "(", t2 = t2 || ")", this.list.forEach((r2) => {
      let a2 = r2.terms();
      a2[0].pre = e4 + a2[0].pre;
      let n2 = a2[a2.length - 1];
      n2.post = t2 + n2.post;
    }), this;
  };
})(Na);
var xa = { join: function(e3) {
  this.uncache();
  let t2 = this.list[0], r2 = t2.length, a2 = {};
  for (let r3 = 1; r3 < this.list.length; r3++) {
    const n3 = this.list[r3];
    a2[n3.start] = true;
    let i2 = t2.lastTerm();
    e3 && (i2.post += e3), i2.next = n3.start, n3.terms(0).prev = i2.id, t2.length += n3.length, t2.cache = {};
  }
  let n2 = t2.length - r2;
  return this.parents().forEach((e4) => {
    e4.list.forEach((e5) => {
      let r3 = e5.terms();
      for (let a3 = 0; a3 < r3.length; a3++) if (r3[a3].id === t2.start) {
        e5.length += n2;
        break;
      }
      e5.cache = {};
    }), e4.list = e4.list.filter((e5) => true !== a2[e5.start]);
  }), this.buildFrom([t2]);
} }, Fa = {};
const Ca = /[,\)"';:\-–—\.…]/, Ba = function(e3, t2) {
  if (!e3.found) return;
  let r2 = e3.termList();
  for (let e4 = 0; e4 < r2.length - 1; e4++) {
    const t3 = r2[e4];
    if (Ca.test(t3.post)) return;
  }
  r2.forEach((e4) => {
    e4.implicit = e4.clean;
  }), r2[0].text += t2, r2.slice(1).forEach((e4) => {
    e4.text = "";
  });
  for (let e4 = 0; e4 < r2.length - 1; e4++) {
    const t3 = r2[e4];
    t3.post = t3.post.replace(/ /, "");
  }
};
Fa.contract = function() {
  let e3 = this.not("@hasContraction"), t2 = e3.match("(we|they|you) are");
  return Ba(t2, "'re"), t2 = e3.match("(he|she|they|it|we|you) will"), Ba(t2, "'ll"), t2 = e3.match("(he|she|they|it|we) is"), Ba(t2, "'s"), t2 = e3.match("#Person is"), Ba(t2, "'s"), t2 = e3.match("#Person would"), Ba(t2, "'d"), t2 = e3.match("(is|was|had|would|should|could|do|does|have|has|can) not"), Ba(t2, "n't"), t2 = e3.match("(i|we|they) have"), Ba(t2, "'ve"), t2 = e3.match("(would|should|could) have"), Ba(t2, "'ve"), t2 = e3.match("i am"), Ba(t2, "'m"), t2 = e3.match("going to"), this;
};
var Ga = Object.assign({}, Ur, Qr, Zr, Yr, ta, ra, na, ia, sa, la, ca, da, fa, va, Ea, Ha, Na, xa, Fa);
let za = {};
[["terms", "."], ["hyphenated", "@hasHyphen ."], ["adjectives", "#Adjective"], ["hashTags", "#HashTag"], ["emails", "#Email"], ["emoji", "#Emoji"], ["emoticons", "#Emoticon"], ["atMentions", "#AtMention"], ["urls", "#Url"], ["adverbs", "#Adverb"], ["pronouns", "#Pronoun"], ["conjunctions", "#Conjunction"], ["prepositions", "#Preposition"]].forEach((e3) => {
  za[e3[0]] = function(t2) {
    let r2 = this.match(e3[1]);
    return "number" == typeof t2 && (r2 = r2.get(t2)), r2;
  };
}), za.emojis = za.emoji, za.atmentions = za.atMentions, za.words = za.terms, za.phoneNumbers = function(e3) {
  let t2 = this.splitAfter("@hasComma");
  return t2 = t2.match("#PhoneNumber+"), "number" == typeof e3 && (t2 = t2.get(e3)), t2;
}, za.money = function(e3) {
  let t2 = this.match("#Money #Currency?");
  return "number" == typeof e3 && (t2 = t2.get(e3)), t2;
}, za.places = function(e3) {
  let t2 = this.match("(#City && @hasComma) (#Region|#Country)"), r2 = this.not(t2).splitAfter("@hasComma");
  return r2 = r2.concat(t2), r2.sort("index"), r2 = r2.match("#Place+"), "number" == typeof e3 && (r2 = r2.get(e3)), r2;
}, za.organizations = function(e3) {
  let t2 = this.clauses();
  return t2 = t2.match("#Organization+"), "number" == typeof e3 && (t2 = t2.get(e3)), t2;
}, za.entities = function(e3) {
  let t2 = this.clauses(), r2 = t2.people();
  r2 = r2.concat(t2.places()), r2 = r2.concat(t2.organizations());
  return r2 = r2.not(["someone", "man", "woman", "mother", "brother", "sister", "father"]), r2.sort("sequence"), "number" == typeof e3 && (r2 = r2.get(e3)), r2;
}, za.things = za.entities, za.topics = za.entities;
var Ia = za;
const Oa = /^(under|over)-?.{3}/, Ta = function(e3, t2, r2) {
  let a2 = r2.words, n2 = e3[t2].reduced + " " + e3[t2 + 1].reduced;
  return void 0 !== a2[n2] && true === a2.hasOwnProperty(n2) ? (e3[t2].tag(a2[n2], "lexicon-two", r2), e3[t2 + 1].tag(a2[n2], "lexicon-two", r2), 1) : t2 + 2 < e3.length && (n2 += " " + e3[t2 + 2].reduced, void 0 !== a2[n2] && true === a2.hasOwnProperty(n2)) ? (e3[t2].tag(a2[n2], "lexicon-three", r2), e3[t2 + 1].tag(a2[n2], "lexicon-three", r2), e3[t2 + 2].tag(a2[n2], "lexicon-three", r2), 2) : t2 + 3 < e3.length && (n2 += " " + e3[t2 + 3].reduced, void 0 !== a2[n2] && true === a2.hasOwnProperty(n2)) ? (e3[t2].tag(a2[n2], "lexicon-four", r2), e3[t2 + 1].tag(a2[n2], "lexicon-four", r2), e3[t2 + 2].tag(a2[n2], "lexicon-four", r2), e3[t2 + 3].tag(a2[n2], "lexicon-four", r2), 3) : 0;
};
var Va = function(e3, t2) {
  let r2 = t2.words, a2 = t2.hasCompound;
  for (let n2 = 0; n2 < e3.length; n2 += 1) {
    let i2 = e3[n2].clean;
    if (true === a2[i2] && n2 + 1 < e3.length) {
      let r3 = Ta(e3, n2, t2);
      if (r3 > 0) {
        n2 += r3;
        continue;
      }
    }
    if (void 0 === r2[i2] || true !== r2.hasOwnProperty(i2)) if (i2 === e3[n2].reduced || true !== r2.hasOwnProperty(e3[n2].reduced)) {
      if (true === Oa.test(i2)) {
        let a3 = i2.replace(/^(under|over)-?/, "");
        true === r2.hasOwnProperty(a3) && e3[n2].tag(r2[a3], "noprefix-lexicon", t2);
      }
    } else e3[n2].tag(r2[e3[n2].reduced], "lexicon", t2);
    else e3[n2].tag(r2[i2], "lexicon", t2);
  }
  return e3;
};
const Ma = /[\'‘’‛‵′`´]$/, Ja = /^(m|k|cm|km|m)\/(s|h|hr)$/;
const La = [[/^[\w\.]+@[\w\.]+\.[a-z]{2,3}$/, "Email"], [/^#[a-z0-9_\u00C0-\u00FF]{2,}$/, "HashTag"], [/^@1?[0-9](am|pm)$/i, "Time"], [/^@1?[0-9]:[0-9]{2}(am|pm)?$/i, "Time"], [/^@\w{2,}$/, "AtMention"], [/^(https?:\/\/|www\.)+\w+\.[a-z]{2,3}/, "Url"], [/^[a-z0-9./].+\.(com|net|gov|org|ly|edu|info|biz|dev|ru|jp|de|in|uk|br|io|ai)/, "Url"], [/^'[0-9]{2}$/, "Year"], [/^[012]?[0-9](:[0-5][0-9])(:[0-5][0-9])$/, "Time"], [/^[012]?[0-9](:[0-5][0-9])?(:[0-5][0-9])? ?(am|pm)$/i, "Time"], [/^[012]?[0-9](:[0-5][0-9])(:[0-5][0-9])? ?(am|pm)?$/i, "Time"], [/^[PMCE]ST$/, "Time"], [/^utc ?[+-]?[0-9]+?$/, "Time"], [/^[a-z0-9]*? o\'?clock$/, "Time"], [/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}/i, "Date"], [/^[0-9]{1,4}-[0-9]{1,2}-[0-9]{1,4}$/, "Date"], [/^[0-9]{1,4}\/[0-9]{1,2}\/[0-9]{1,4}$/, "Date"], [/^[0-9]{1,4}-[a-z]{2,9}-[0-9]{1,4}$/i, "Date"], [/^gmt[+-][0-9][0-9]?$/i, "Timezone"], [/^utc[+-][0-9][0-9]?$/i, "Timezone"], [/^ma?c\'.*/, "LastName"], [/^o\'[drlkn].*/, "LastName"], [/^ma?cd[aeiou]/, "LastName"], [/^(lol)+[sz]$/, "Expression"], [/^woo+a*?h?$/, "Expression"], [/^(un|de|re)\\-[a-z\u00C0-\u00FF]{2}/, "Verb"], [/^[0-9]{1,4}\.[0-9]{1,2}\.[0-9]{1,4}$/, "Date"], [/^[0-9]{3}-[0-9]{4}$/, "PhoneNumber"], [/^(\+?[0-9][ -])?[0-9]{3}[ -]?[0-9]{3}-[0-9]{4}$/, "PhoneNumber"], [/^[-+]?[\$\xA2-\xA5\u058F\u060B\u09F2\u09F3\u09FB\u0AF1\u0BF9\u0E3F\u17DB\u20A0-\u20BD\uA838\uFDFC\uFE69\uFF04\uFFE0\uFFE1\uFFE5\uFFE6][-+]?[0-9]+(,[0-9]{3})*(\.[0-9]+)?(k|m|b|bn)?\+?$/, ["Money", "Value"]], [/^[-+]?[0-9]+(,[0-9]{3})*(\.[0-9]+)?[\$\xA2-\xA5\u058F\u060B\u09F2\u09F3\u09FB\u0AF1\u0BF9\u0E3F\u17DB\u20A0-\u20BD\uA838\uFDFC\uFE69\uFF04\uFFE0\uFFE1\uFFE5\uFFE6]\+?$/, ["Money", "Value"]], [/^[-+]?[\$£]?[0-9]([0-9,.])+?(usd|eur|jpy|gbp|cad|aud|chf|cny|hkd|nzd|kr|rub)$/i, ["Money", "Value"]], [/^[-+]?[0-9]+(,[0-9]{3})*(\.[0-9]+)?\+?$/, ["Cardinal", "NumericValue"]], [/^[-+]?[0-9]+(,[0-9]{3})*(\.[0-9]+)?(st|nd|rd|r?th)$/, ["Ordinal", "NumericValue"]], [/^\.[0-9]+\+?$/, ["Cardinal", "NumericValue"]], [/^[-+]?[0-9]+(,[0-9]{3})*(\.[0-9]+)?%\+?$/, ["Percent", "Cardinal", "NumericValue"]], [/^\.[0-9]+%$/, ["Percent", "Cardinal", "NumericValue"]], [/^[0-9]{1,4}\/[0-9]{1,4}(st|nd|rd|th)?s?$/, ["Fraction", "NumericValue"]], [/^[0-9.]{1,3}[a-z]{0,2}[-–—][0-9]{1,3}[a-z]{0,2}$/, ["Value", "NumberRange"]], [/^[0-9][0-9]?(:[0-9][0-9])?(am|pm)? ?[-–—] ?[0-9][0-9]?(:[0-9][0-9])?(am|pm)?$/, ["Time", "NumberRange"]], [/^[0-9.]+([a-z]{1,4})$/, "Value"]], Sa = /^[IVXLCDM]{2,}$/, _a = /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;
const Ka = "Adjective", qa = "Infinitive", Wa = "PresentTense", Ra = "Singular", Ua = "PastTense", Qa = "Expression";
const Za = "Adjective", Xa = "Infinitive", Ya = "PresentTense", en = "Singular", tn = "PastTense", rn = "Adverb", an = "Plural", nn = "Verb", on = "LastName";
const sn = { a: [[/.[aeiou]na$/, "Noun"], [/.[oau][wvl]ska$/, "LastName"], [/.[^aeiou]ica$/, Ra], [/^([hyj]a)+$/, Qa]], c: [[/.[^aeiou]ic$/, Ka]], d: [[/[aeiou](pp|ll|ss|ff|gg|tt|rr|bb|nn|mm)ed$/, Ua], [/.[aeo]{2}[bdgmnprvz]ed$/, Ua], [/.[aeiou][sg]hed$/, Ua], [/.[aeiou]red$/, Ua], [/.[aeiou]r?ried$/, Ua], [/.[bcdgtr]led$/, Ua], [/.[aoui]f?led$/, Ua], [/.[iao]sed$/, Ua], [/[aeiou]n?[cs]ed$/, Ua], [/[aeiou][rl]?[mnf]ed$/, Ua], [/[aeiou][ns]?c?ked$/, Ua], [/[aeiou][nl]?ged$/, Ua], [/.[tdbwxz]ed$/, Ua], [/[^aeiou][aeiou][tvx]ed$/, Ua], [/.[cdlmnprstv]ied$/, Ua], [/[^aeiou]ard$/, Ra], [/[aeiou][^aeiou]id$/, Ka], [/.[vrl]id$/, Ka]], e: [[/.[lnr]ize$/, qa], [/.[^aeiou]ise$/, qa], [/.[aeiou]te$/, qa], [/.[^aeiou][ai]ble$/, Ka], [/.[^aeiou]eable$/, Ka], [/.[ts]ive$/, Ka], [/[a-z]-like$/, Ka]], h: [[/.[^aeiouf]ish$/, Ka], [/.v[iy]ch$/, "LastName"], [/^ug?h+$/, Qa], [/^uh[ -]?oh$/, Qa], [/[a-z]-ish$/, Ka]], i: [[/.[oau][wvl]ski$/, "LastName"]], k: [[/^(k){2}$/, Qa]], l: [[/.[gl]ial$/, Ka], [/.[^aeiou]ful$/, Ka], [/.[nrtumcd]al$/, Ka], [/.[^aeiou][ei]al$/, Ka]], m: [[/.[^aeiou]ium$/, Ra], [/[^aeiou]ism$/, Ra], [/^h*u*m+$/, Qa], [/^\d+ ?[ap]m$/, "Date"]], n: [[/.[lsrnpb]ian$/, Ka], [/[^aeiou]ician$/, "Actor"], [/[aeiou][ktrp]in$/, "Gerund"]], o: [[/^no+$/, Qa], [/^(yo)+$/, Qa], [/^woo+[pt]?$/, Qa]], r: [[/.[bdfklmst]ler$/, "Noun"], [/[aeiou][pns]er$/, Ra], [/[^i]fer$/, qa], [/.[^aeiou][ao]pher$/, "Actor"], [/.[lk]er$/, "Noun"], [/.ier$/, "Comparative"]], t: [[/.[di]est$/, "Superlative"], [/.[icldtgrv]ent$/, Ka], [/[aeiou].*ist$/, Ka], [/^[a-z]et$/, "Verb"]], s: [[/.[^aeiou]ises$/, Wa], [/.[rln]ates$/, Wa], [/.[^z]ens$/, "Verb"], [/.[lstrn]us$/, Ra], [/.[aeiou]sks$/, Wa], [/.[aeiou]kes$/, Wa], [/[aeiou][^aeiou]is$/, Ra], [/[a-z]\'s$/, "Noun"], [/^yes+$/, Qa]], v: [[/.[^aeiou][ai][kln]ov$/, "LastName"]], y: [[/.[cts]hy$/, Ka], [/.[st]ty$/, Ka], [/.[gk]y$/, Ka], [/.[tnl]ary$/, Ka], [/.[oe]ry$/, Ra], [/[rdntkbhs]ly$/, "Adverb"], [/...lly$/, "Adverb"], [/[bszmp]{2}y$/, Ka], [/.(gg|bb|zz)ly$/, Ka], [/.[ai]my$/, Ka], [/[ea]{2}zy$/, Ka], [/.[^aeiou]ity$/, Ra]] }, ln = [null, null, { ea: en, ia: "Noun", ic: Za, ly: rn, "'n": nn, "'t": nn }, { oed: tn, ued: tn, xed: tn, " so": rn, "'ll": "Modal", "'re": "Copula", azy: Za, eer: "Noun", end: nn, ped: tn, ffy: Za, ify: Xa, ing: "Gerund", ize: Xa, lar: Za, mum: Za, nes: Ya, nny: Za, oid: Za, ous: Za, que: Za, rol: en, sis: en, zes: Ya }, { amed: tn, aped: tn, ched: tn, lked: tn, nded: tn, cted: tn, dged: tn, akis: on, cede: Xa, chuk: on, czyk: on, ects: Ya, ends: nn, enko: on, ette: en, fies: Ya, fore: rn, gate: Xa, gone: Za, ices: an, ints: an, ines: an, ions: an, less: rn, llen: Za, made: Za, nsen: on, oses: Ya, ould: "Modal", some: Za, sson: on, tage: Xa, teen: "Value", tion: en, tive: Za, tors: "Noun", vice: en }, { tized: tn, urned: tn, eased: tn, ances: an, bound: Za, ettes: an, fully: rn, ishes: Ya, ities: an, marek: on, nssen: on, ology: "Noun", ports: an, rough: Za, tches: Ya, tieth: "Ordinal", tures: an, wards: rn, where: rn }, { auskas: on, keeper: "Actor", logist: "Actor", teenth: "Value" }, { opoulos: on, borough: "Place", sdottir: on }];
const un = /^(\u00a9|\u00ae|[\u2319-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/, cn = { ":(": true, ":)": true, ":P": true, ":p": true, ":O": true, ":3": true, ":|": true, ":/": true, ":\\": true, ":$": true, ":*": true, ":@": true, ":-(": true, ":-)": true, ":-P": true, ":-p": true, ":-O": true, ":-3": true, ":-|": true, ":-/": true, ":-\\": true, ":-$": true, ":-*": true, ":-@": true, ":^(": true, ":^)": true, ":^P": true, ":^p": true, ":^O": true, ":^3": true, ":^|": true, ":^/": true, ":^\\": true, ":^$": true, ":^*": true, ":^@": true, "):": true, "(:": true, "$:": true, "*:": true, ")-:": true, "(-:": true, "$-:": true, "*-:": true, ")^:": true, "(^:": true, "$^:": true, "*^:": true, "<3": true, "</3": true, "<\\3": true };
const hn = { lexicon: Va, punctuation: function(e3, t2, r2) {
  let a2 = e3[t2];
  if (Ma.test(a2.text) && !Ma.test(a2.pre) && !Ma.test(a2.post) && a2.clean.length > 2) {
    let e4 = a2.clean[a2.clean.length - 2];
    if ("s" === e4) return void a2.tag(["Possessive", "Noun"], "end-tick", r2);
    "n" === e4 && a2.tag(["Gerund"], "chillin", r2);
  }
  Ja.test(a2.text) && a2.tag("Unit", "per-sec", r2);
}, regex: function(e3, t2) {
  let r2 = e3.text;
  for (let a2 = 0; a2 < La.length; a2 += 1) if (true === La[a2][0].test(r2)) {
    e3.tagSafe(La[a2][1], "prefix #" + a2, t2);
    break;
  }
  e3.text.length >= 2 && Sa.test(r2) && _a.test(r2) && e3.tag("RomanNumeral", "xvii", t2);
}, suffix: function(e3, t2) {
  !(function(e4, t3) {
    const r2 = e4.clean.length;
    let a2 = 7;
    r2 <= a2 && (a2 = r2 - 1);
    for (let n2 = a2; n2 > 1; n2 -= 1) {
      let a3 = e4.clean.substr(r2 - n2, r2);
      if (true === ln[a3.length].hasOwnProperty(a3)) {
        let r3 = ln[a3.length][a3];
        e4.tagSafe(r3, "suffix -" + a3, t3);
        break;
      }
    }
  })(e3, t2), (function(e4, t3) {
    let r2 = e4.clean, a2 = r2[r2.length - 1];
    if (true === sn.hasOwnProperty(a2)) {
      let n2 = sn[a2];
      for (let i2 = 0; i2 < n2.length; i2 += 1) if (true === n2[i2][0].test(r2)) {
        e4.tagSafe(n2[i2][1], `endReg ${a2} #${i2}`, t3);
        break;
      }
    }
  })(e3, t2);
}, emoji: (e3, t2) => {
  let r2 = e3.pre + e3.text + e3.post;
  var a2;
  r2 = r2.trim(), r2 = r2.replace(/[.!?,]$/, ""), true === ((e4) => !(":" !== e4.charAt(0) || null === e4.match(/:.?$/) || e4.match(" ") || e4.length > 35))(r2) && (e3.tag("Emoji", "comma-emoji", t2), e3.text = r2, e3.pre = e3.pre.replace(":", ""), e3.post = e3.post.replace(":", "")), e3.text.match(un) && (e3.tag("Emoji", "unicode-emoji", t2), e3.text = r2), true === (a2 = (a2 = r2).replace(/^[:;]/, ":"), cn.hasOwnProperty(a2)) && (e3.tag("Emoticon", "emoticon-emoji", t2), e3.text = r2);
} };
var dn = function(e3, t2) {
  let r2 = e3.world;
  hn.lexicon(t2, r2);
  for (let e4 = 0; e4 < t2.length; e4 += 1) {
    let a2 = t2[e4];
    hn.punctuation(t2, e4, r2), hn.regex(a2, r2), hn.suffix(a2, r2), hn.emoji(a2, r2);
  }
  return e3;
};
const gn = { beforeThisWord: { there: "Verb", me: "Verb", man: "Adjective", only: "Verb", him: "Verb", were: "Noun", took: "Noun", himself: "Verb", went: "Noun", who: "Noun", jr: "Person" }, afterThisWord: { i: "Verb", first: "Noun", it: "Verb", there: "Verb", not: "Verb", because: "Noun", if: "Noun", but: "Noun", who: "Verb", this: "Noun", his: "Noun", when: "Noun", you: "Verb", very: "Adjective", old: "Noun", never: "Verb", before: "Noun" }, beforeThisPos: { Copula: "Noun", PastTense: "Noun", Conjunction: "Noun", Modal: "Noun", Pluperfect: "Noun", PerfectTense: "Verb" }, afterThisPos: { Adjective: "Noun", Possessive: "Noun", Determiner: "Noun", Adverb: "Verb", Pronoun: "Verb", Value: "Noun", Ordinal: "Noun", Modal: "Verb", Superlative: "Noun", Demonym: "Noun", Honorific: "Person" } }, pn = Object.keys(gn.afterThisPos), mn = Object.keys(gn.beforeThisPos);
var fn = function(e3, t2) {
  for (let r2 = 0; r2 < e3.length; r2 += 1) {
    let a2 = e3[r2];
    if (true === a2.isKnown()) continue;
    let n2 = e3[r2 - 1];
    if (n2) {
      if (true === gn.afterThisWord.hasOwnProperty(n2.clean)) {
        let e5 = gn.afterThisWord[n2.clean];
        a2.tag(e5, "after-" + n2.clean, t2);
        continue;
      }
      let e4 = pn.find((e5) => n2.tags[e5]);
      if (void 0 !== e4) {
        let r3 = gn.afterThisPos[e4];
        a2.tag(r3, "after-" + e4, t2);
        continue;
      }
    }
    let i2 = e3[r2 + 1];
    if (i2) {
      if (true === gn.beforeThisWord.hasOwnProperty(i2.clean)) {
        let e5 = gn.beforeThisWord[i2.clean];
        a2.tag(e5, "before-" + i2.clean, t2);
        continue;
      }
      let e4 = mn.find((e5) => i2.tags[e5]);
      if (void 0 !== e4) {
        let r3 = gn.beforeThisPos[e4];
        a2.tag(r3, "before-" + e4, t2);
        continue;
      }
    }
  }
};
const bn = /^[A-Z][a-z'\u00C0-\u00FF]/, yn = /[0-9]/;
var vn = function(e3) {
  let t2 = e3.world;
  e3.list.forEach((e4) => {
    let r2 = e4.terms();
    for (let e5 = 1; e5 < r2.length; e5++) {
      const a2 = r2[e5];
      true === bn.test(a2.text) && false === yn.test(a2.text) && void 0 === a2.tags.Date && a2.tag("ProperNoun", "titlecase-noun", t2);
    }
  });
};
const wn = /^(re|un)-?[a-z\u00C0-\u00FF]/, kn = /^(re|un)-?/;
var An = function(e3, t2) {
  let r2 = t2.words;
  e3.forEach((e4) => {
    if (true !== e4.isKnown() && true === wn.test(e4.clean)) {
      let a2 = e4.clean.replace(kn, "");
      a2 && a2.length > 3 && void 0 !== r2[a2] && true === r2.hasOwnProperty(a2) && e4.tag(r2[a2], "stem-" + a2, t2);
    }
  });
};
const Dn = ["Uncountable", "Pronoun", "Place", "Value", "Person", "Month", "WeekDay", "Holiday"], $n = { isSingular: [/(ax|test)is$/i, /(octop|vir|radi|nucle|fung|cact|stimul)us$/i, /(octop|vir)i$/i, /(rl)f$/i, /(alias|status)$/i, /(bu)s$/i, /(al|ad|at|er|et|ed|ad)o$/i, /(ti)um$/i, /(ti)a$/i, /sis$/i, /(?:(^f)fe|(lr)f)$/i, /hive$/i, /s[aeiou]+ns$/i, /(^aeiouy|qu)y$/i, /(x|ch|ss|sh|z)$/i, /(matr|vert|ind|cort)(ix|ex)$/i, /(m|l)ouse$/i, /(m|l)ice$/i, /(antenn|formul|nebul|vertebr|vit)a$/i, /.sis$/i, /^(?!talis|.*hu)(.*)man$/i], isPlural: [/(^v)ies$/i, /ises$/i, /ives$/i, /(antenn|formul|nebul|vertebr|vit)ae$/i, /(octop|vir|radi|nucle|fung|cact|stimul)i$/i, /(buffal|tomat|tornad)oes$/i, /(analy|ba|diagno|parenthe|progno|synop|the)ses$/i, /(vert|ind|cort)ices$/i, /(matr|append)ices$/i, /(x|ch|ss|sh|s|z|o)es$/i, /is$/i, /men$/i, /news$/i, /.tia$/i, /(^f)ves$/i, /(lr)ves$/i, /(^aeiouy|qu)ies$/i, /(m|l)ice$/i, /(cris|ax|test)es$/i, /(alias|status)es$/i, /ics$/i] }, Pn = [/ss$/, /sis$/, /[^aeiou][uo]s$/, /'s$/], En = [/i$/, /ae$/];
var Hn = function(e3, t2) {
  if (e3.tags.Noun && !e3.tags.Acronym) {
    let r2 = e3.clean;
    if (e3.tags.Singular || e3.tags.Plural) return;
    if (r2.length <= 3) return void e3.tag("Singular", "short-singular", t2);
    if (Dn.find((t3) => e3.tags[t3])) return;
    if ($n.isPlural.find((e4) => e4.test(r2))) return void e3.tag("Plural", "plural-rules", t2);
    if ($n.isSingular.find((e4) => e4.test(r2))) return void e3.tag("Singular", "singular-rules", t2);
    if (true === /s$/.test(r2)) {
      if (Pn.find((e4) => e4.test(r2))) return;
      return void e3.tag("Plural", "plural-fallback", t2);
    }
    if (En.find((e4) => e4.test(r2))) return;
    e3.tag("Singular", "singular-fallback", t2);
  }
};
let jn = ["academy", "administration", "agence", "agences", "agencies", "agency", "airlines", "airways", "army", "assoc", "associates", "association", "assurance", "authority", "autorite", "aviation", "bank", "banque", "board", "boys", "brands", "brewery", "brotherhood", "brothers", "building society", "bureau", "cafe", "caisse", "capital", "care", "cathedral", "center", "central bank", "centre", "chemicals", "choir", "chronicle", "church", "circus", "clinic", "clinique", "club", "co", "coalition", "coffee", "collective", "college", "commission", "committee", "communications", "community", "company", "comprehensive", "computers", "confederation", "conference", "conseil", "consulting", "containers", "corporation", "corps", "corp", "council", "crew", "daily news", "data", "departement", "department", "department store", "departments", "design", "development", "directorate", "division", "drilling", "education", "eglise", "electric", "electricity", "energy", "ensemble", "enterprise", "enterprises", "entertainment", "estate", "etat", "evening news", "faculty", "federation", "financial", "fm", "foundation", "fund", "gas", "gazette", "girls", "government", "group", "guild", "health authority", "herald", "holdings", "hospital", "hotel", "hotels", "inc", "industries", "institut", "institute", "institute of technology", "institutes", "insurance", "international", "interstate", "investment", "investments", "investors", "journal", "laboratory", "labs", "liberation army", "limited", "local authority", "local health authority", "machines", "magazine", "management", "marine", "marketing", "markets", "media", "memorial", "mercantile exchange", "ministere", "ministry", "military", "mobile", "motor", "motors", "musee", "museum", "news", "news service", "observatory", "office", "oil", "optical", "orchestra", "organization", "partners", "partnership", "people's party", "petrol", "petroleum", "pharmacare", "pharmaceutical", "pharmaceuticals", "pizza", "plc", "police", "polytechnic", "post", "power", "press", "productions", "quartet", "radio", "regional authority", "regional health authority", "reserve", "resources", "restaurant", "restaurants", "savings", "school", "securities", "service", "services", "social club", "societe", "society", "sons", "standard", "state police", "state university", "stock exchange", "subcommittee", "syndicat", "systems", "telecommunications", "telegraph", "television", "times", "tribunal", "tv", "union", "university", "utilities", "workers"].reduce((function(e3, t2) {
  return e3[t2] = "Noun", e3;
}), {});
const Nn = function(e3) {
  return !!e3.tags.Noun && (!(e3.tags.Pronoun || e3.tags.Comma || e3.tags.Possessive) && !!(e3.tags.Organization || e3.tags.Acronym || e3.tags.Place || e3.titleCase()));
};
const xn = /^[A-Z]('s|,)?$/, Fn = /([A-Z]\.){2}[A-Z]?/i, Cn = { I: true, A: true };
const Bn = { neighbours: fn, case: vn, stem: An, plural: Hn, organizations: function(e3, t2) {
  for (let r2 = 0; r2 < e3.length; r2 += 1) {
    let a2 = e3[r2];
    if (void 0 !== jn[a2.clean] && true === jn.hasOwnProperty(a2.clean)) {
      let n2 = e3[r2 - 1];
      if (void 0 !== n2 && true === Nn(n2)) {
        n2.tagSafe("Organization", "org-word-1", t2), a2.tagSafe("Organization", "org-word-2", t2);
        continue;
      }
      let i2 = e3[r2 + 1];
      if (void 0 !== i2 && "of" === i2.clean && e3[r2 + 2] && Nn(e3[r2 + 2])) {
        a2.tagSafe("Organization", "org-of-word-1", t2), i2.tagSafe("Organization", "org-of-word-2", t2), e3[r2 + 2].tagSafe("Organization", "org-of-word-3", t2);
        continue;
      }
    }
  }
}, acronyms: function(e3, t2) {
  e3.forEach((e4) => {
    true !== e4.tags.RomanNumeral && (true === Fn.test(e4.text) && e4.tag("Acronym", "period-acronym", t2), e4.isUpperCase() && (function(e5, t3) {
      let r2 = e5.reduced;
      return !!e5.tags.Acronym || !t3.words[r2] && !(r2.length > 5) && e5.isAcronym();
    })(e4, t2) ? (e4.tag("Acronym", "acronym-step", t2), e4.tag("Noun", "acronym-infer", t2)) : !Cn.hasOwnProperty(e4.text) && xn.test(e4.text) && (e4.tag("Acronym", "one-letter-acronym", t2), e4.tag("Noun", "one-letter-infer", t2)), e4.tags.Organization && e4.text.length <= 3 && e4.tag("Acronym", "acronym-org", t2), e4.tags.Organization && e4.isUpperCase() && e4.text.length <= 6 && e4.tag("Acronym", "acronym-org-case", t2));
  });
} };
var Gn = function(e3, t2) {
  let r2 = e3.world;
  return Bn.neighbours(t2, r2), Bn.case(e3), Bn.stem(t2, r2), t2.forEach((t3) => {
    false === t3.isKnown() && t3.tag("Noun", "noun-fallback", e3.world);
  }), Bn.organizations(t2, r2), Bn.acronyms(t2, r2), t2.forEach((t3) => {
    Bn.plural(t3, e3.world);
  }), e3;
};
const zn = /n't$/, In = { "won't": ["will", "not"], wont: ["will", "not"], "can't": ["can", "not"], cant: ["can", "not"], cannot: ["can", "not"], "shan't": ["should", "not"], dont: ["do", "not"], dun: ["do", "not"] };
const On = /([a-z\u00C0-\u00FF]+)[\u0027\u0060\u00B4\u2018\u2019\u201A\u201B\u2032\u2035\u2039\u203A]([a-z]{1,2})$/i, Tn = { ll: "will", ve: "have", re: "are", m: "am", "n't": "not" };
const Vn = { wanna: ["want", "to"], gonna: ["going", "to"], im: ["i", "am"], alot: ["a", "lot"], ive: ["i", "have"], imma: ["I", "will"], "where'd": ["where", "did"], whered: ["where", "did"], "when'd": ["when", "did"], whend: ["when", "did"], howd: ["how", "did"], whatd: ["what", "did"], dunno: ["do", "not", "know"], brb: ["be", "right", "back"], gtg: ["got", "to", "go"], irl: ["in", "real", "life"], tbh: ["to", "be", "honest"], imo: ["in", "my", "opinion"], til: ["today", "i", "learned"], rn: ["right", "now"], twas: ["it", "was"], "@": ["at"] };
const Mn = /([a-z\u00C0-\u00FF]+)[\u0027\u0060\u00B4\u2018\u2019\u201A\u201B\u2032\u2035\u2039\u203A]s$/i, Jn = { that: true, there: true }, Ln = { here: true, there: true, everywhere: true };
const Sn = /[a-z\u00C0-\u00FF]'d$/, _n = { how: true, what: true };
const Kn = /^([0-9.]{1,3}[a-z]{0,2}) ?[-–—] ?([0-9]{1,3}[a-z]{0,2})$/i, qn = /^([0-9][0-9]?(:[0-9][0-9])?(am|pm)?) ?[-–—] ?([0-9][0-9]?(:[0-9][0-9])?(am|pm)?)$/i;
const Wn = /^(l|c|d|j|m|n|qu|s|t)[\u0027\u0060\u00B4\u2018\u2019\u201A\u201B\u2032\u2035\u2039\u203A]([a-z\u00C0-\u00FF]+)$/i, Rn = { l: "le", c: "ce", d: "de", j: "je", m: "me", n: "ne", qu: "que", s: "se", t: "tu" };
const Un = Va, Qn = xt, Zn = function(e3, t2) {
  if (true === In.hasOwnProperty(e3.clean)) return In[e3.clean];
  if ("ain't" === e3.clean || "aint" === e3.clean) return (function(e4, t3) {
    let r2 = t3.terms(), a2 = r2.indexOf(e4), n2 = r2.slice(0, a2).find((e5) => e5.tags.Noun);
    return n2 && n2.tags.Plural ? ["are", "not"] : ["is", "not"];
  })(e3, t2);
  if (true === zn.test(e3.clean)) {
    return [e3.clean.replace(zn, ""), "not"];
  }
  return null;
}, Xn = function(e3) {
  let t2 = e3.text.match(On);
  return null === t2 ? null : Tn.hasOwnProperty(t2[2]) ? [t2[1], Tn[t2[2]]] : null;
}, Yn = function(e3) {
  return Vn.hasOwnProperty(e3.clean) ? Vn[e3.clean] : null;
}, ei = function(e3, t2, r2) {
  let a2 = e3.text.match(Mn);
  if (null !== a2) {
    if (true === ((e4, t3) => {
      if (e4.tags.Possessive) return true;
      if (e4.tags.Pronoun || e4.tags.QuestionWord) return false;
      if (Jn.hasOwnProperty(e4.reduced)) return false;
      let r3 = t3.get(e4.next);
      if (!r3) return true;
      if (r3.tags.Verb) return !!r3.tags.Infinitive || !!r3.tags.PresentTense;
      if (r3.tags.Noun) return true !== Ln.hasOwnProperty(r3.reduced);
      let a3 = t3.get(r3.next);
      return !(!a3 || !a3.tags.Noun || a3.tags.Pronoun) || (r3.tags.Adjective || r3.tags.Adverb || r3.tags.Verb, false);
    })(e3, t2.pool)) return e3.tag("#Possessive", "isPossessive", r2), null;
    if (null !== a2) return ((e4, t3) => {
      let r3 = t3.terms(), a3 = r3.indexOf(e4);
      return r3.slice(a3 + 1, a3 + 3).find((e5) => e5.tags.PastTense);
    })(e3, t2) ? [a2[1], "has"] : [a2[1], "is"];
  }
  return null;
}, ti = function(e3, t2) {
  if (Sn.test(e3.clean)) {
    let r2 = e3.clean.replace(/'d$/, ""), a2 = t2.terms(), n2 = a2.indexOf(e3), i2 = a2.slice(n2 + 1, n2 + 4);
    for (let e4 = 0; e4 < i2.length; e4++) {
      let t3 = i2[e4];
      if (t3.tags.Verb) return t3.tags.PastTense ? [r2, "had"] : true === _n[r2] ? [r2, "did"] : [r2, "would"];
    }
    return [r2, "would"];
  }
  return null;
}, ri = function(e3) {
  if (true === e3.tags.PhoneNumber) return null;
  let t2 = e3.text.match(Kn);
  return null !== t2 ? [t2[1], "to", t2[2]] : (t2 = e3.text.match(qn), null !== t2 ? [t2[1], "to", t2[4]] : null);
}, ai = function(e3) {
  let t2 = e3.text.match(Wn);
  if (null === t2 || false === Rn.hasOwnProperty(t2[1])) return null;
  let r2 = [Rn[t2[1]], t2[2]];
  return r2[0] && r2[1] ? r2 : null;
}, ni = /^[0-9]+$/, ii = /^[0-9]+(st|nd|rd|th)$/, oi = /^[0-9:]+(am|pm)?$/, si = function(e3, t2) {
  let r2 = Qn(e3.join(" "), t2.world, t2.pool())[0], a2 = r2.terms();
  Un(a2, t2.world);
  let n2 = a2[0];
  return ii.test(n2.text) && a2[2] ? (a2[0].tag("Ordinal", "ord-range", t2.world), a2[2].tag("Ordinal", "ord-range", t2.world)) : ni.test(n2.text) && a2[2] ? (a2[0].tag("Cardinal", "num-range", t2.world), a2[2].tag("Cardinal", "num-range", t2.world)) : oi.test(n2.text) && a2[1] && a2[2] && (a2[0].tag("Time", "time-range", t2.world), a2[1].tag("Date", "time-range", t2.world), a2[2].tag("Time", "time-range", t2.world)), a2.forEach((e4) => {
    e4.implicit = e4.text, e4.text = "", e4.clean = "", e4.pre = "", e4.post = "", 0 === Object.keys(e4.tags).length && (e4.tags.Noun = true);
  }), r2;
};
var li = function(e3) {
  let t2 = e3.world;
  return e3.list.forEach((r2) => {
    let a2 = r2.terms();
    for (let n2 = 0; n2 < a2.length; n2 += 1) {
      let i2 = a2[n2], o2 = Zn(i2, r2);
      if (o2 = o2 || Xn(i2), o2 = o2 || Yn(i2), o2 = o2 || ei(i2, r2, t2), o2 = o2 || ti(i2, r2), o2 = o2 || ri(i2), o2 = o2 || ai(i2), null !== o2) {
        let t3 = si(o2, e3);
        true === r2.has("#NumberRange") && e3.buildFrom([t3]).tag("NumberRange"), t3.terms(0).text = i2.text, r2.buildFrom(i2.id, 1, e3.pool()).replace(t3, e3, true);
      }
    }
  }), e3;
};
const ui = function(e3, t2) {
  let r2 = e3._cache.tags[t2] || [];
  return r2 = r2.map((t3) => e3.list[t3]), e3.buildFrom(r2);
};
var ci = function(e3) {
  let t2 = ui(e3, "Infinitive");
  return t2.found && (t2 = t2.ifNo("@hasQuestionMark"), t2 = t2.ifNo("(i|we|they)"), t2.not("will be").match("[#Infinitive] (#Determiner|#Possessive) #Noun").notIf("(our|their)").match("#Infinitive").tag("Imperative", "shut-the"), t2.match("^[#Infinitive] #Adverb?$", 0).tag("Imperative", "go-fast"), t2.match("[(do && #Infinitive)] not? #Verb", 0).tag("Imperative", "do-not"), t2.match("[#Infinitive] (it|some) (#Comparative|#Preposition|please|now|again)", 0).tag("Imperative", "do-it")), t2 = (function(e4, t3) {
    let r2 = e4._cache.words[t3] || [];
    return r2 = r2.map((t4) => e4.list[t4]), e4.buildFrom(r2);
  })(e3, "like"), t2.match("#Adverb like").notIf("(really|generally|typically|usually|sometimes|often|just) [like]").tag("Adverb", "adverb-like"), t2 = ui(e3, "Adjective"), t2.match("#Determiner #Adjective$").notIf("(#Comparative|#Superlative)").terms(1).tag("Noun", "the-adj-1"), t2 = ui(e3, "FirstName"), t2.match("#FirstName (#Noun|@titleCase)").ifNo("^#Possessive").ifNo("(#Pronoun|#Plural)").ifNo("@hasComma .").lastTerm().tag("#LastName", "firstname-noun"), t2 = ui(e3, "Value"), t2 = t2.match("#Value #PresentTense").ifNo("#Copula"), t2.found && (true === t2.has("(one|1)") ? t2.terms(1).tag("Singular", "one-presentTense") : t2.terms(1).tag("Plural", "value-presentTense")), e3.match("^(well|so|okay)").tag("Expression", "well-"), e3.match("#Value [of a second]", 0).unTag("Value", "of-a-second"), e3.match("#Value [seconds]", 0).unTag("Value", "30-seconds").tag(["Unit", "Plural"]), t2 = ui(e3, "Gerund"), t2.match("(be|been) (#Adverb|not)+? #Gerund").not("#Verb$").tag("Auxiliary", "be-walking"), e3.match("(try|use|attempt|build|make) #Verb").ifNo("(@hasComma|#Negative|#PhrasalVerb|#Copula|will|be)").lastTerm().tag("#Noun", "do-verb"), t2 = ui(e3, "Possessive"), t2 = t2.match("#Possessive [#Infinitive]", 0), t2.lookBehind("(let|made|make|force|ask)").found || t2.tag("Noun", "her-match"), e3;
};
var hi = function(e3) {
  let t2 = {};
  for (let r2 = 0; r2 < e3.length; r2++) t2[e3[r2]] = true;
  return Object.keys(t2);
};
var di = [{ match: "too much", tag: "Adverb Adjective", reason: "bit-4" }, { match: "u r", tag: "Pronoun Copula", reason: "u r" }, { match: "#Copula (pretty|dead|full|well|sure) (#Adjective|#Noun)", tag: "#Copula #Adverb #Adjective", reason: "sometimes-adverb" }, { match: "(#Pronoun|#Person) (had|#Adverb)? [better] #PresentTense", group: 0, tag: "Modal", reason: "i-better" }, { match: "[#Gerund] #Adverb? not? #Copula", group: 0, tag: "Activity", reason: "gerund-copula" }, { match: "[#Gerund] #Modal", group: 0, tag: "Activity", reason: "gerund-modal" }, { match: "holy (shit|fuck|hell)", tag: "Expression", reason: "swears-expression" }, { match: "#Noun #Actor", tag: "Actor", reason: "thing-doer" }, { match: "#Conjunction [u]", group: 0, tag: "Pronoun", reason: "u-pronoun-2" }, { match: "[u] #Verb", group: 0, tag: "Pronoun", reason: "u-pronoun-1" }, { match: "#Noun [(who|whom)]", group: 0, tag: "Determiner", reason: "captain-who" }, { match: "a bit much", tag: "Determiner Adverb Adjective", reason: "bit-3" }, { match: "#Verb #Adverb? #Noun [(that|which)]", group: 0, tag: "Preposition", reason: "that-prep" }, { match: "@hasComma [which] (#Pronoun|#Verb)", group: 0, tag: "Preposition", reason: "which-copula" }, { match: "#Copula just [like]", group: 0, tag: "Preposition", reason: "like-preposition" }, { match: "#Noun [like] #Noun", group: 0, tag: "Preposition", reason: "noun-like" }, { match: "[had] #Noun+ #PastTense", group: 0, tag: "Condition", reason: "had-he" }, { match: "[were] #Noun+ to #Infinitive", group: 0, tag: "Condition", reason: "were-he" }, { match: "^how", tag: "QuestionWord", reason: "how-question" }, { match: "[how] (#Determiner|#Copula|#Modal|#PastTense)", group: 0, tag: "QuestionWord", reason: "how-is" }, { match: "^which", tag: "QuestionWord", reason: "which-question" }, { match: "[so] #Noun", group: 0, tag: "Conjunction", reason: "so-conj" }, { match: "[(who|what|where|why|how|when)] #Noun #Copula #Adverb? (#Verb|#Adjective)", group: 0, tag: "Conjunction", reason: "how-he-is-x" }], gi = { adverbAdjective: ["dark", "bright", "flat", "light", "soft", "pale", "dead", "dim", "faux", "little", "wee", "sheer", "most", "near", "good", "extra", "all"], personDate: ["april", "june", "may", "jan", "august", "eve"], personMonth: ["january", "april", "may", "june", "jan", "sep"], personAdjective: ["misty", "rusty", "dusty", "rich", "randy", "young"], personVerb: ["pat", "wade", "ollie", "will", "rob", "buck", "bob", "mark", "jack"], personPlace: ["darwin", "hamilton", "paris", "alexandria", "houston", "kobe", "santiago", "salvador", "sydney", "victoria"], personNoun: ["art", "baker", "berg", "bill", "brown", "charity", "chin", "christian", "cliff", "daisy", "dawn", "dick", "dolly", "faith", "franco", "gene", "green", "hall", "hill", "holly", "hope", "jean", "jewel", "joy", "kelvin", "king", "kitty", "lane", "lily", "melody", "mercedes", "miles", "olive", "penny", "ray", "reed", "robin", "rod", "rose", "sky", "summer", "trinity", "van", "viola", "violet", "wang", "white"] };
const pi = `(${gi.personDate.join("|")})`;
var mi = [{ match: "#Holiday (day|eve)", tag: "Holiday", reason: "holiday-day" }, { match: "[sun] the #Ordinal", tag: "WeekDay", reason: "sun-the-5th" }, { match: "[sun] #Date", group: 0, tag: "WeekDay", reason: "sun-feb" }, { match: "#Date (on|this|next|last|during)? [sun]", group: 0, tag: "WeekDay", reason: "1pm-sun" }, { match: "(in|by|before|during|on|until|after|of|within|all) [sat]", group: 0, tag: "WeekDay", reason: "sat" }, { match: "(in|by|before|during|on|until|after|of|within|all) [wed]", group: 0, tag: "WeekDay", reason: "wed" }, { match: "(in|by|before|during|on|until|after|of|within|all) [march]", group: 0, tag: "Month", reason: "march" }, { match: "[sat] #Date", group: 0, tag: "WeekDay", reason: "sat-feb" }, { match: "#Preposition [(march|may)]", group: 0, tag: "Month", reason: "in-month" }, { match: "this [(march|may)]", group: 0, tag: "Month", reason: "this-month" }, { match: "next [(march|may)]", group: 0, tag: "Month", reason: "this-month" }, { match: "last [(march|may)]", group: 0, tag: "Month", reason: "this-month" }, { match: "[(march|may)] the? #Value", group: 0, tag: "Month", reason: "march-5th" }, { match: "#Value of? [(march|may)]", group: 0, tag: "Month", reason: "5th-of-march" }, { match: "[(march|may)] .? #Date", group: 0, tag: "Month", reason: "march-and-feb" }, { match: "#Date .? [(march|may)]", group: 0, tag: "Month", reason: "feb-and-march" }, { match: "#Adverb [(march|may)]", group: 0, tag: "Verb", reason: "quickly-march" }, { match: "[(march|may)] #Adverb", group: 0, tag: "Verb", reason: "march-quickly" }, { match: "#Value of #Month", tag: "Date", reason: "value-of-month" }, { match: "#Cardinal #Month", tag: "Date", reason: "cardinal-month" }, { match: "#Month #Value to #Value", tag: "Date", reason: "value-to-value" }, { match: "#Month the #Value", tag: "Date", reason: "month-the-value" }, { match: "(#WeekDay|#Month) #Value", tag: "Date", reason: "date-value" }, { match: "#Value (#WeekDay|#Month)", tag: "Date", reason: "value-date" }, { match: "(#TextValue && #Date) #TextValue", tag: "Date", reason: "textvalue-date" }, { match: `in [${pi}]`, group: 0, tag: "Date", reason: "in-june" }, { match: `during [${pi}]`, group: 0, tag: "Date", reason: "in-june" }, { match: `on [${pi}]`, group: 0, tag: "Date", reason: "in-june" }, { match: `by [${pi}]`, group: 0, tag: "Date", reason: "by-june" }, { match: `after [${pi}]`, group: 0, tag: "Date", reason: "after-june" }, { match: `#Date [${pi}]`, group: 0, tag: "Date", reason: "in-june" }, { match: pi + " #Value", tag: "Date", reason: "june-5th" }, { match: pi + " #Date", tag: "Date", reason: "june-5th" }, { match: pi + " #ProperNoun", tag: "Person", reason: "june-smith", safe: true }, { match: pi + " #Acronym? (#ProperNoun && !#Month)", tag: "Person", reason: "june-smith-jr" }, { match: "#Cardinal [second]", tag: "Unit", reason: "one-second" }, { match: "#Month #NumberRange", tag: "Date", reason: "aug 20-21" }, { match: "(#Place|#Demonmym|#Time) (standard|daylight|central|mountain)? time", tag: "Timezone", reason: "std-time" }, { match: "(eastern|mountain|pacific|central|atlantic) (standard|daylight|summer)? time", tag: "Timezone", reason: "eastern-time" }, { match: "#Time [(eastern|mountain|pacific|central|est|pst|gmt)]", group: 0, tag: "Timezone", reason: "5pm-central" }, { match: "(central|western|eastern) european time", tag: "Timezone", reason: "cet" }];
const fi = `(${gi.personAdjective.join("|")})`;
var bi = [{ match: "[all] #Determiner? #Noun", group: 0, tag: "Adjective", reason: "all-noun" }, { match: `#Adverb [${fi}]`, group: 0, tag: "Adjective", reason: "really-rich" }, { match: fi + " #Person", tag: "Person", reason: "randy-smith" }, { match: fi + " #Acronym? #ProperNoun", tag: "Person", reason: "rusty-smith" }, { match: "#Copula [(just|alone)]$", group: 0, tag: "Adjective", reason: "not-adverb" }, { match: "#Singular is #Adverb? [#PastTense$]", group: 0, tag: "Adjective", reason: "is-filled" }, { match: "[#PastTense] #Singular is", group: 0, tag: "Adjective", reason: "smoked-poutine" }, { match: "[#PastTense] #Plural are", group: 0, tag: "Adjective", reason: "baked-onions" }, { match: "well [#PastTense]", group: 0, tag: "Adjective", reason: "well-made" }, { match: "#Copula [fucked up?]", tag: "Adjective", reason: "swears-adjective" }, { match: "#Singular (seems|appears) #Adverb? [#PastTense$]", group: 0, tag: "Adjective", reason: "seems-filled" }, { match: "(a|an) [#Gerund]", group: 0, tag: "Adjective", reason: "a|an" }, { match: "as [#Gerund] as", group: 0, tag: "Adjective", reason: "as-gerund-as" }, { match: "more [#Gerund] than", group: 0, tag: "Adjective", reason: "more-gerund-than" }, { match: "(so|very|extremely) [#Gerund]", group: 0, tag: "Adjective", reason: "so-gerund" }, { match: "(it|he|she|everything|something) #Adverb? was #Adverb? [#Gerund]", group: 0, tag: "Adjective", reason: "it-was-gerund" }, { match: "(found|found) it #Adverb? [#Gerund]", group: 0, tag: "Adjective", reason: "found-it-gerund" }, { match: "a (little|bit|wee) bit? [#Gerund]", group: 0, tag: "Adjective", reason: "a-bit-gerund" }, { match: "#Copula #Adjective? [(out|in|through)]$", group: 0, tag: "Adjective", reason: "still-out" }, { match: "^[#Adjective] (the|your) #Noun", group: 0, tag: "Infinitive", reason: "shut-the" }, { match: "the [said] #Noun", group: 0, tag: "Adjective", reason: "the-said-card" }, { match: "#Noun (that|which|whose) [#PastTense && !#Copula] #Noun", group: 0, tag: "Adjective", reason: "that-past-noun" }], yi = [{ match: "there (are|were) #Adjective? [#PresentTense]", group: 0, tag: "Plural", reason: "there-are" }, { match: "#Determiner [sun]", group: 0, tag: "Singular", reason: "the-sun" }, { match: "#Verb (a|an) [#Value]", group: 0, tag: "Singular", reason: "did-a-value" }, { match: "the [(can|will|may)]", group: 0, tag: "Singular", reason: "the can" }, { match: "#FirstName #Acronym? (#Possessive && #LastName)", tag: "Possessive", reason: "name-poss" }, { match: "#Organization+ #Possessive", tag: "Possessive", reason: "org-possessive" }, { match: "#Place+ #Possessive", tag: "Possessive", reason: "place-possessive" }, { match: "(#Verb && !#Modal) (all|every|each|most|some|no) [#PresentTense]", group: 0, tag: "Noun", reason: "all-presentTense" }, { match: "#Determiner [#Adjective] #Copula", group: 0, tag: "Noun", reason: "the-adj-is" }, { match: "#Adjective [#Adjective] #Copula", group: 0, tag: "Noun", reason: "adj-adj-is" }, { match: "(had|have|#PastTense) #Adjective [#PresentTense]", group: 0, tag: "Noun", reason: "adj-presentTense" }, { match: "^#Adjective [#PresentTense]", group: 0, tag: "Noun", reason: "start adj-presentTense" }, { match: "#Value #Adjective [#PresentTense]", group: 0, tag: "Noun", reason: "one-big-reason" }, { match: "#PastTense #Adjective+ [#PresentTense]", group: 0, tag: "Noun", reason: "won-wide-support" }, { match: "(many|few|several|couple) [#PresentTense]", group: 0, tag: "Noun", reason: "many-poses" }, { match: "#Adverb #Adjective [#PresentTense]", group: 0, tag: "Noun", reason: "very-big-dream" }, { match: "#Adjective [#Infinitive] #Noun", group: 0, tag: "Noun", reason: "good-wait-staff" }, { match: "#Adjective #Adjective [#PresentTense]", group: 0, tag: "Noun", reason: "adorable-little-store" }, { match: "#Preposition #Adjective [#PresentTense]", group: 0, tag: "Noun", reason: "of-basic-training" }, { match: "#Adjective [#Gerund]", group: 0, tag: "Noun", reason: "early-warning" }, { match: "#Gerund #Adverb? #Comparative [#PresentTense]", group: 0, tag: "Noun", reason: "higher-costs" }, { match: "#Infinitive (this|that|the) [#Infinitive]", group: 0, tag: "Noun", reason: "do-this-dance" }, { match: "(his|her|its) [#Adjective]", group: 0, tag: "Noun", reason: "his-fine" }, { match: "some [#Verb] #Plural", group: 0, tag: "Noun", reason: "determiner6" }, { match: "more #Noun", tag: "Noun", reason: "more-noun" }, { match: "(#Noun && @hasComma) #Noun (and|or) [#PresentTense]", group: 0, tag: "Noun", reason: "noun-list" }, { match: "(right|rights) of .", tag: "Noun", reason: "right-of" }, { match: "a [bit]", group: 0, tag: "Noun", reason: "bit-2" }, { match: "#Possessive #Ordinal [#PastTense]", group: 0, tag: "Noun", reason: "first-thought" }, { match: "#Gerund #Determiner [#Infinitive]", group: 0, tag: "Noun", reason: "running-a-show" }, { match: "#Determiner #Adverb [#Infinitive]", group: 0, tag: "Noun", reason: "the-reason" }, { match: "(the|this|those|these) #Adjective [#Verb]", group: 0, tag: "Noun", reason: "the-adj-verb" }, { match: "(the|this|those|these) #Adverb #Adjective [#Verb]", group: 0, tag: "Noun", reason: "determiner4" }, { match: "#Determiner [#Adjective] (#Copula|#PastTense|#Auxiliary)", group: 0, tag: "Noun", reason: "the-adj-2" }, { match: "(the|this|a|an) [#Infinitive] #Adverb? #Verb", group: 0, tag: "Noun", reason: "determiner5" }, { match: "#Determiner [#Infinitive] #Noun", group: 0, tag: "Noun", reason: "determiner7" }, { match: "#Determiner #Adjective #Adjective? [#Infinitive]", group: 0, tag: "Noun", reason: "a-nice-inf" }, { match: "the [#Verb] #Preposition .", group: 0, tag: "Noun", reason: "determiner1" }, { match: "#Determiner [#Verb] of", group: 0, tag: "Noun", reason: "the-verb-of" }, { match: "#Adjective #Noun+ [#Infinitive] #Copula", group: 0, tag: "Noun", reason: "career-move" }, { match: "#Determiner #Noun of [#Verb]", group: 0, tag: "Noun", reason: "noun-of-noun" }, { match: "#Determiner [(western|eastern|northern|southern|central)] #Noun", group: 0, tag: "Noun", reason: "western-line" }, { match: "#Possessive [#Gerund]", group: 0, tag: "Noun", reason: "her-polling" }, { match: "(his|her|its) [#PresentTense]", group: 0, tag: "Noun", reason: "its-polling" }, { match: "(#Determiner|#Value) [(linear|binary|mobile|lexical|technical|computer|scientific|formal)] #Noun", group: 0, tag: "Noun", reason: "technical-noun" }, { match: "(the|those|these|a|an) [#Participle] #Noun", group: 0, tag: "Adjective", reason: "blown-motor" }, { match: "(the|those|these|a|an) #Adjective? [#Infinitive]", group: 0, tag: "Noun", reason: "det-inf" }, { match: "(the|those|these|a|an) #Adjective? [#PresentTense]", group: 0, tag: "Noun", reason: "det-pres" }, { match: "(the|those|these|a|an) #Adjective? [#PastTense]", group: 0, tag: "Noun", reason: "det-past" }, { match: "(this|that) [#Gerund]", group: 0, tag: "Noun", reason: "this-gerund" }, { match: "at some [#Infinitive]", group: 0, tag: "Noun", reason: "at-some-inf" }, { match: "(#Noun && @hasHyphen) #Verb", tag: "Noun", reason: "hyphen-verb" }, { match: "is no [#Verb]", group: 0, tag: "Noun", reason: "is-no-verb" }, { match: "[#Verb] than", group: 0, tag: "Noun", reason: "correction" }, { match: "(go|goes|went) to [#Infinitive]", group: 0, tag: "Noun", reason: "goes-to-verb" }, { match: "(a|an) #Noun [#Infinitive] (#Preposition|#Noun)", group: 0, tag: "Noun", reason: "a-noun-inf" }, { match: "(a|an) #Noun [#Infinitive]$", group: 0, tag: "Noun", reason: "a-noun-inf2" }, { match: "do [so]", group: 0, tag: "Noun", reason: "so-noun" }, { match: "#Copula [#Infinitive] #Noun", group: 0, tag: "Noun", reason: "is-pres-noun" }, { match: "#Determiner #Adverb? [close]", group: 0, tag: "Adjective", reason: "a-close" }, { match: "#Determiner [(shit|damn|hell)]", group: 0, tag: "Noun", reason: "swears-noun" }, { match: "(the|these) [#Singular] (were|are)", group: 0, tag: "Plural", reason: "singular-were" }, { match: "#Gerund #Adjective? for [#Infinitive]", group: 0, tag: "Noun", reason: "running-for" }, { match: "#Gerund #Adjective to [#Infinitive]", group: 0, tag: "Noun", reason: "running-to" }, { match: "(many|any|some|several) [#PresentTense] for", group: 0, tag: "Noun", reason: "any-verbs-for" }, { match: "(have|had) [#Adjective] #Preposition .", group: 0, tag: "Noun", reason: "have-fun" }, { match: "co #Noun", tag: "Actor", reason: "co-noun" }, { match: "to #PresentTense #Noun [#PresentTense] #Preposition", group: 0, tag: "Noun", reason: "gas-exchange" }, { match: "a #Noun+ or #Adverb+? [#Verb]", group: 0, tag: "Noun", reason: "noun-or-noun" }, { match: "[#Gerund] system", group: 0, tag: "Noun", reason: "operating-system" }, { match: "#PastTense (until|as|through|without) [#PresentTense]", group: 0, tag: "Noun", reason: "waited-until-release" }, { match: "#Gerund like #Adjective? [#PresentTense]", group: 0, tag: "Plural", reason: "like-hot-cakes" }, { match: "some #Adjective [#PresentTense]", group: 0, tag: "Noun", reason: "some-reason" }, { match: "for some [#PresentTense]", group: 0, tag: "Noun", reason: "for-some-reason" }, { match: "(same|some|the|that|a) kind of [#PresentTense]", group: 0, tag: "Noun", reason: "some-kind-of" }, { match: "(same|some|the|that|a) type of [#PresentTense]", group: 0, tag: "Noun", reason: "some-type-of" }, { match: "#Gerund #Adjective #Preposition [#PresentTense]", group: 0, tag: "Noun", reason: "doing-better-for-x" }, { match: "(get|got|have|had) #Comparative [#PresentTense]", group: 0, tag: "Noun", reason: "got-better-aim" }, { match: "#Pronoun #Infinitive [#Gerund] #PresentTense", group: 0, tag: "Noun", reason: "tipping-sucks" }];
var vi = [{ match: "[still] #Adjective", group: 0, tag: "Adverb", reason: "still-advb" }, { match: "[still] #Verb", group: 0, tag: "Adverb", reason: "still-verb" }, { match: "[so] #Adjective", group: 0, tag: "Adverb", reason: "so-adv" }, { match: "[way] #Comparative", group: 0, tag: "Adverb", reason: "way-adj" }, { match: "[way] #Adverb #Adjective", group: 0, tag: "Adverb", reason: "way-too-adj" }, { match: "[all] #Verb", group: 0, tag: "Adverb", reason: "all-verb" }, { match: "(#Verb && !#Modal) [like]", group: 0, tag: "Adverb", reason: "verb-like" }, { match: "(barely|hardly) even", tag: "Adverb", reason: "barely-even" }, { match: "[even] #Verb", group: 0, tag: "Adverb", reason: "even-walk" }, { match: "even left", tag: "#Adverb #Verb", reason: "even-left" }, { match: "(#PresentTense && !#Copula) [(hard|quick|long|bright|slow|fast|backwards|forwards)]", group: 0, tag: "Adverb", reason: "lazy-ly" }, { match: "[much] #Adjective", group: 0, tag: "Adverb", reason: "bit-1" }, { match: "#Copula [#Adverb]$", group: 0, tag: "Adjective", reason: "is-well" }, { match: "a [(little|bit|wee) bit?] #Adjective", group: 0, tag: "Adverb", reason: "a-bit-cold" }, { match: `[${`(${gi.adverbAdjective.join("|")})`}] #Adjective`, group: 0, tag: "Adverb", reason: "dark-green" }, { match: "#Adverb [#Adverb]$", group: 0, tag: "Adjective", reason: "kinda-sparkly" }, { match: "#Adverb [#Adverb] (and|or|then)", group: 0, tag: "Adjective", reason: "kinda-sparkly-and" }, { match: "[super] #Adjective #Noun", group: 0, tag: "Adverb", reason: "super-strong" }], wi = [{ match: "1 #Value #PhoneNumber", tag: "PhoneNumber", reason: "1-800-Value" }, { match: "#NumericValue #PhoneNumber", tag: "PhoneNumber", reason: "(800) PhoneNumber" }, { match: "#Demonym #Currency", tag: "Currency", reason: "demonym-currency" }, { match: "[second] #Noun", group: 0, tag: "Ordinal", reason: "second-noun" }, { match: "#Value+ [#Currency]", group: 0, tag: "Unit", reason: "5-yan" }, { match: "#Value [(foot|feet)]", group: 0, tag: "Unit", reason: "foot-unit" }, { match: "(minus|negative) #Value", tag: "Value", reason: "minus-value" }, { match: "#Value [#Abbreviation]", group: 0, tag: "Unit", reason: "value-abbr" }, { match: "#Value [k]", group: 0, tag: "Unit", reason: "value-k" }, { match: "#Unit an hour", tag: "Unit", reason: "unit-an-hour" }, { match: "#Value (point|decimal) #Value", tag: "Value", reason: "value-point-value" }, { match: "(#Value|a) [(buck|bucks|grand)]", group: 0, tag: "Currency", reason: "value-bucks" }, { match: "#Determiner [(half|quarter)] #Ordinal", group: 0, tag: "Value", reason: "half-ordinal" }, { match: "a #Value", tag: "Value", reason: "a-value" }, { match: "[#Value+] #Currency", group: 0, tag: "Money", reason: "15 usd" }, { match: "(hundred|thousand|million|billion|trillion|quadrillion)+ and #Value", tag: "Value", reason: "magnitude-and-value" }, { match: "!once [(a|an)] (#Duration|hundred|thousand|million|billion|trillion)", group: 0, tag: "Value", reason: "a-is-one" }];
const ki = `(${gi.personVerb.join("|")})`;
var Ai = [{ match: "[#Adjective] #Possessive #Noun", group: 0, tag: "Verb", reason: "gerund-his-noun" }, { match: "[#Adjective] (us|you)", group: 0, tag: "Gerund", reason: "loving-you" }, { match: "(slowly|quickly) [#Adjective]", group: 0, tag: "Gerund", reason: "slowly-adj" }, { match: "(#Modal|i|they|we|do) not? [like]", group: 0, tag: "PresentTense", reason: "modal-like" }, { match: "do (simply|just|really|not)+ [(#Adjective|like)]", group: 0, tag: "Verb", reason: "do-simply-like" }, { match: "does (#Adverb|not)? [#Adjective]", group: 0, tag: "PresentTense", reason: "does-mean" }, { match: "i (#Adverb|do)? not? [mean]", group: 0, tag: "PresentTense", reason: "i-mean" }, { match: "#Noun #Adverb? [left]", group: 0, tag: "PastTense", reason: "left-verb" }, { match: "(this|that) [#Plural]", group: 0, tag: "PresentTense", reason: "this-verbs" }, { match: "[#Copula (#Adverb|not)+?] (#Gerund|#PastTense)", group: 0, tag: "Auxiliary", reason: "copula-walking" }, { match: "[(has|had) (#Adverb|not)+?] #PastTense", group: 0, tag: "Auxiliary", reason: "had-walked" }, { match: "#Adverb+? [(#Modal|did)+ (#Adverb|not)+?] #Verb", group: 0, tag: "Auxiliary", reason: "modal-verb" }, { match: "[#Modal (#Adverb|not)+? have (#Adverb|not)+? had (#Adverb|not)+?] #Verb", group: 0, tag: "Auxiliary", reason: "would-have" }, { match: "[(has|had) (#Adverb|not)+?] #PastTense", group: 0, tag: "Auxiliary", reason: "had-walked" }, { match: "[(do|does|will|have|had)] (not|#Adverb)+? #Verb", group: 0, tag: "Auxiliary", reason: "have-had" }, { match: "[about to] #Adverb? #Verb", group: 0, tag: ["Auxiliary", "Verb"], reason: "about-to" }, { match: "#Modal (#Adverb|not)+? be (#Adverb|not)+? #Verb", group: 0, tag: "Auxiliary", reason: "would-be" }, { match: "(were|was) being [#PresentTense]", group: 0, tag: "PastTense", reason: "was-being" }, { match: "[#Modal (#Adverb|not)+? have (#Adverb|not)+? had (#Adverb|not)+?] #Verb", group: 0, tag: "Auxiliary", reason: "would-have" }, { match: "(#Modal|had|has) (#Adverb|not)+? been (#Adverb|not)+? #Verb", group: 0, tag: "Auxiliary", reason: "had-been" }, { match: "[(be|being|been)] #Participle", group: 0, tag: "Auxiliary", reason: "being-foo" }, { match: "(#Verb && @hasHyphen) up", tag: "PhrasalVerb", reason: "foo-up" }, { match: "(#Verb && @hasHyphen) off", tag: "PhrasalVerb", reason: "foo-off" }, { match: "(#Verb && @hasHyphen) over", tag: "PhrasalVerb", reason: "foo-over" }, { match: "(#Verb && @hasHyphen) out", tag: "PhrasalVerb", reason: "foo-out" }, { match: "#PhrasalVerb [#PhrasalVerb]", group: 0, tag: "Particle", reason: "phrasal-particle" }, { match: "(lived|went|crept|go) [on] for", group: 0, tag: "PhrasalVerb", reason: "went-on" }, { match: "#Verb (him|her|it|us|himself|herself|itself|everything|something) [(up|down)]", group: 0, tag: "Adverb", reason: "phrasal-pronoun-advb" }, { match: "[will #Adverb? not? #Adverb? be] #Gerund", group: 0, tag: "Copula", reason: "will-be-copula" }, { match: "will #Adverb? not? #Adverb? [be] #Adjective", group: 0, tag: "Copula", reason: "be-copula" }, { match: "[march] (up|down|back|to|toward)", group: 0, tag: "Infinitive", reason: "march-to" }, { match: "#Modal [march]", group: 0, tag: "Infinitive", reason: "must-march" }, { match: "(let|make|made) (him|her|it|#Person|#Place|#Organization)+ [#Singular] (a|an|the|it)", group: 0, tag: "Infinitive", reason: "let-him-glue" }, { match: "will [#Adjective]", group: 0, tag: "Verb", reason: "will-adj" }, { match: "#Pronoun [#Adjective] #Determiner #Adjective? #Noun", group: 0, tag: "Verb", reason: "he-adj-the" }, { match: "#Copula [#Adjective] to #Verb", group: 0, tag: "Verb", reason: "adj-to" }, { match: "[open] #Determiner", group: 0, tag: "Infinitive", reason: "open-the" }, { match: "[#PresentTense] (are|were|was) #Adjective", group: 0, tag: "Plural", reason: "compromises-are-possible" }, { match: `#Modal [${ki}]`, group: 0, tag: "Verb", reason: "would-mark" }, { match: `#Adverb [${ki}]`, group: 0, tag: "Verb", reason: "really-mark" }, { match: "(to|#Modal) [mark]", group: 0, tag: "PresentTense", reason: "to-mark" }, { match: "^[#Infinitive] (is|was)", group: 0, tag: "Noun", reason: "checkmate-is" }, { match: ki + " #Person", tag: "Person", reason: "rob-smith" }, { match: ki + " #Acronym #ProperNoun", tag: "Person", reason: "rob-a-smith" }, { match: "[shit] (#Determiner|#Possessive|them)", group: 0, tag: "Verb", reason: "swear1-verb" }, { match: "[damn] (#Determiner|#Possessive|them)", group: 0, tag: "Verb", reason: "swear2-verb" }, { match: "[fuck] (#Determiner|#Possessive|them)", group: 0, tag: "Verb", reason: "swear3-verb" }, { match: "(become|fall|grow) #Adverb? [#PastTense]", group: 0, tag: "Adjective", reason: "overly-weakened" }, { match: "(a|an) #Adverb [#Participle] #Noun", group: 0, tag: "Adjective", reason: "completely-beaten" }, { match: "whose [#PresentTense] #Copula", group: 0, tag: "Noun", reason: "whos-name-was" }, { match: "#PhrasalVerb #PhrasalVerb #Preposition [#PresentTense]", group: 0, tag: "Noun", reason: "given-up-on-x" }];
var Di = [{ match: "(west|north|south|east|western|northern|southern|eastern)+ #Place", tag: "Region", reason: "west-norfolk" }, { match: "#City [(al|ak|az|ar|ca|ct|dc|fl|ga|id|il|nv|nh|nj|ny|oh|pa|sc|tn|tx|ut|vt|pr)]", group: 0, tag: "Region", reason: "us-state" }, { match: "portland [or]", group: 0, tag: "Region", reason: "portland-or" }, { match: "#ProperNoun+ (district|region|province|county|prefecture|municipality|territory|burough|reservation)", tag: "Region", reason: "foo-district" }, { match: "(district|region|province|municipality|territory|burough|state) of #ProperNoun", tag: "Region", reason: "district-of-Foo" }, { match: "in [#ProperNoun] #Place", group: 0, tag: "Place", reason: "propernoun-place" }, { match: "#Value #Noun (st|street|rd|road|crescent|cr|way|tr|terrace|avenue|ave)", tag: "Address", reason: "address-st" }];
const $i = gi, Pi = `(${$i.personNoun.join("|")})`, Ei = `(${$i.personMonth.join("|")})`;
var Hi = [{ match: "[(1st|2nd|first|second)] #Honorific", group: 0, tag: "Honorific", reason: "ordinal-honorific" }, { match: "[(private|general|major|corporal|lord|lady|secretary|premier)] #Honorific? #Person", group: 0, tag: "Honorific", reason: "ambg-honorifics" }, { match: "#Copula [(#Noun|#PresentTense)] #LastName", group: 0, tag: "FirstName", reason: "copula-noun-lastname" }, { match: "(lady|queen|sister) #ProperNoun", tag: "FemaleName", reason: "lady-titlecase", safe: true }, { match: "(king|pope|father) #ProperNoun", tag: "MaleName", reason: "pope-titlecase", safe: true }, { match: "[(will|may|april|june|said|rob|wade|ray|rusty|drew|miles|jack|chuck|randy|jan|pat|cliff|bill)] #LastName", group: 0, tag: "FirstName", reason: "maybe-lastname" }, { match: "#FirstName [#Determiner #Noun] #LastName", group: 0, tag: "NickName", reason: "first-noun-last" }, { match: "#Possessive [#FirstName]", group: 0, tag: "Person", reason: "possessive-name" }, { match: "#ProperNoun (b|c|d|e|f|g|h|j|k|l|m|n|o|p|q|r|s|t|u|v|w|x|y|z) #ProperNoun", tag: "Person", reason: "titlecase-acronym-titlecase", safe: true }, { match: "#Acronym #LastName", tag: "Person", reason: "acronym-latname", safe: true }, { match: "#Person (jr|sr|md)", tag: "Person", reason: "person-honorific" }, { match: "#Person #Person the? #RomanNumeral", tag: "Person", reason: "roman-numeral" }, { match: "#FirstName [/^[^aiurck]$/]", group: 0, tag: ["Acronym", "Person"], reason: "john-e" }, { match: "#Honorific #Person", tag: "Person", reason: "honorific-person" }, { match: "#Honorific #Acronym", tag: "Person", reason: "Honorific-TitleCase" }, { match: "#Noun van der? #Noun", tag: "Person", reason: "van der noun", safe: true }, { match: "(king|queen|prince|saint|lady) of #Noun", tag: "Person", reason: "king-of-noun", safe: true }, { match: "(prince|lady) #Place", tag: "Person", reason: "lady-place" }, { match: "(king|queen|prince|saint) #ProperNoun", tag: "Person", reason: "saint-foo" }, { match: "[#ProperNoun] #Person", group: 0, tag: "Person", reason: "proper-person", safe: true }, { match: "al (#Person|#ProperNoun)", tag: "Person", reason: "al-borlen", safe: true }, { match: "#FirstName de #Noun", tag: "Person", reason: "bill-de-noun" }, { match: "#FirstName (bin|al) #Noun", tag: "Person", reason: "bill-al-noun" }, { match: "#FirstName #Acronym #ProperNoun", tag: "Person", reason: "bill-acronym-title" }, { match: "#FirstName #FirstName #ProperNoun", tag: "Person", reason: "bill-firstname-title" }, { match: "#Honorific #FirstName? #ProperNoun", tag: "Person", reason: "dr-john-Title" }, { match: "#FirstName the #Adjective", tag: "Person", reason: "name-the-great" }, { match: "#FirstName (green|white|brown|hall|young|king|hill|cook|gray|price)", tag: "Person", reason: "bill-green" }, { match: Pi + " #Person", tag: "Person", reason: "ray-smith", safe: true }, { match: Pi + " #Acronym? #ProperNoun", tag: "Person", reason: "ray-a-smith", safe: true }, { match: `#Infinitive #Determiner? #Adjective? #Noun? (to|for) [${Ei}]`, group: 0, tag: "Person", reason: "ambig-person" }, { match: `#Infinitive [${Ei}]`, group: 0, tag: "Person", reason: "infinitive-person" }, { match: `[${Ei}] #Modal`, group: 0, tag: "Person", reason: "ambig-modal" }, { match: "[may] be", group: 0, tag: "Verb", reason: "may-be" }, { match: `#Modal [${Ei}]`, group: 0, tag: "Person", reason: "modal-ambig" }, { match: `#Copula [${Ei}]`, group: 0, tag: "Person", reason: "is-may" }, { match: `[${Ei}] #Copula`, group: 0, tag: "Person", reason: "may-is" }, { match: `that [${Ei}]`, group: 0, tag: "Person", reason: "that-month" }, { match: `with [${Ei}]`, group: 0, tag: "Person", reason: "with-month" }, { match: `for [${Ei}]`, group: 0, tag: "Person", reason: "for-month" }, { match: `this [${Ei}]`, group: 0, tag: "Month", reason: "this-may" }, { match: `next [${Ei}]`, group: 0, tag: "Month", reason: "next-may" }, { match: `last [${Ei}]`, group: 0, tag: "Month", reason: "last-may" }, { match: `#Date [${Ei}]`, group: 0, tag: "Month", reason: "date-may" }, { match: `[${Ei}] the? #Value`, group: 0, tag: "Month", reason: "may-5th" }, { match: `#Value of [${Ei}]`, group: 0, tag: "Month", reason: "5th-of-may" }, { match: "#ProperNoun (van|al|bin) #ProperNoun", tag: "Person", reason: "title-van-title", safe: true }, { match: "#ProperNoun (de|du) la? #ProperNoun", tag: "Person", reason: "title-de-title", safe: true }, { match: "#Singular #Acronym #LastName", tag: "#Person", reason: "title-acro-noun", safe: true }, { match: "#FirstName (#Noun && #ProperNoun) #ProperNoun?", tag: "Person", reason: "firstname-titlecase" }, { match: "#FirstName #Acronym #Noun", tag: "Person", reason: "n-acro-noun", safe: true }, { match: "#FirstName [(de|di|du|van|von) #Person]", group: 0, tag: "LastName", reason: "de-firstname" }, { match: `[${`(${$i.personPlace.join("|")})`}] (#ProperNoun && !#Place)`, group: 0, tag: "FirstName", reason: "place-firstname" }];
const ji = _e, Ni = hi;
let xi = [];
xi = xi.concat(di), xi = xi.concat(mi), xi = xi.concat(bi), xi = xi.concat(yi), xi = xi.concat(vi), xi = xi.concat(wi), xi = xi.concat(Ai), xi = xi.concat(Di), xi = xi.concat([{ match: "#Noun (&|n) #Noun", tag: "Organization", reason: "Noun-&-Noun" }, { match: "#Organization of the? #ProperNoun", tag: "Organization", reason: "org-of-place", safe: true }, { match: "#Organization #Country", tag: "Organization", reason: "org-country" }, { match: "#ProperNoun #Organization", tag: "Organization", reason: "titlecase-org" }, { match: "#ProperNoun (ltd|co|inc|dept|assn|bros)", tag: "Organization", reason: "org-abbrv" }, { match: "the [#Acronym]", group: 0, tag: "Organization", reason: "the-acronym", safe: true }, { match: "(world|global|international|national|#Demonym) #Organization", tag: "Organization", reason: "global-org" }, { match: "#Noun+ (public|private) school", tag: "School", reason: "noun-public-school" }]), xi = xi.concat(Hi);
let Fi = [];
xi.forEach((e3) => {
  e3.reg = ji(e3.match);
  let t2 = (function(e4) {
    let t3 = [];
    if (1 === e4.reg.filter((e5) => void 0 !== e5.fastOr).length) {
      let r2 = e4.reg.findIndex((e5) => void 0 !== e5.fastOr);
      Object.keys(e4.reg[r2].fastOr).forEach((a2) => {
        let n2 = Object.assign({}, e4);
        n2.reg = n2.reg.slice(0), n2.reg[r2] = Object.assign({}, n2.reg[r2]), n2.reg[r2].word = a2, delete n2.reg[r2].operator, delete n2.reg[r2].fastOr, t3.push(n2);
      });
    }
    return t3;
  })(e3);
  t2.length > 0 ? Fi = Fi.concat(t2) : Fi.push(e3);
}), Fi.forEach((e3) => (e3.required = (function(e4) {
  let t2 = [], r2 = [];
  return e4.forEach((e5) => {
    true !== e5.optional && true !== e5.negative && (void 0 !== e5.tag && t2.push(e5.tag), void 0 !== e5.word && r2.push(e5.word));
  }), { tags: Ni(t2), words: Ni(r2) };
})(e3.reg), e3));
const Ci = Fi, Bi = hi;
const Gi = ci, zi = function(e3) {
  Ci.forEach((t2) => {
    let r2 = [];
    t2.required.words.forEach((t3) => {
      r2.push(e3._cache.words[t3] || []);
    }), t2.required.tags.forEach((t3) => {
      r2.push(e3._cache.tags[t3] || []);
    });
    let a2 = (function(e4) {
      if (0 === e4.length) return [];
      let t3 = {};
      e4.forEach((e5) => {
        e5 = Bi(e5);
        for (let r4 = 0; r4 < e5.length; r4++) t3[e5[r4]] = t3[e5[r4]] || 0, t3[e5[r4]] += 1;
      });
      let r3 = Object.keys(t3);
      return r3 = r3.filter((r4) => t3[r4] === e4.length), r3 = r3.map((e5) => Number(e5)), r3;
    })(r2);
    if (0 === a2.length) return;
    let n2 = a2.map((t3) => e3.list[t3]), i2 = e3.buildFrom(n2).match(t2.reg, t2.group);
    i2.found && (true === t2.safe ? i2.tagSafe(t2.tag, t2.reason) : i2.tag(t2.tag, t2.reason));
  });
};
const Ii = dn, Oi = Gn, Ti = li, Vi = function(e3) {
  return zi(e3), Gi(e3), e3;
};
var Mi = function(e3) {
  let t2 = e3.termList();
  return e3 = Ii(e3, t2), e3 = Oi(e3, t2), (e3 = Ti(e3)).cache(), (e3 = Vi(e3)).uncache(), e3.world.taggers.forEach((t3) => {
    t3(e3);
  }), e3;
};
var Ji = function(e3) {
  class t2 extends e3 {
    stripPeriods() {
      return this.termList().forEach((e4) => {
        true === e4.tags.Abbreviation && e4.next && (e4.post = e4.post.replace(/^\./, ""));
        let t3 = e4.text.replace(/\./, "");
        e4.set(t3);
      }), this;
    }
    addPeriods() {
      return this.termList().forEach((e4) => {
        e4.post = e4.post.replace(/^\./, ""), e4.post = "." + e4.post;
      }), this;
    }
  }
  return t2.prototype.unwrap = t2.prototype.stripPeriods, e3.prototype.abbreviations = function(e4) {
    let r2 = this.match("#Abbreviation");
    return "number" == typeof e4 && (r2 = r2.get(e4)), new t2(r2.list, this, this.world);
  }, e3;
};
const Li2 = /\./;
var Si = function(e3) {
  class t2 extends e3 {
    stripPeriods() {
      return this.termList().forEach((e4) => {
        let t3 = e4.text.replace(/\./g, "");
        e4.set(t3);
      }), this;
    }
    addPeriods() {
      return this.termList().forEach((e4) => {
        let t3 = e4.text.replace(/\./g, "");
        t3 = t3.split("").join("."), false === Li2.test(e4.post) && (t3 += "."), e4.set(t3);
      }), this;
    }
  }
  return t2.prototype.unwrap = t2.prototype.stripPeriods, t2.prototype.strip = t2.prototype.stripPeriods, e3.prototype.acronyms = function(e4) {
    let r2 = this.match("#Acronym");
    return "number" == typeof e4 && (r2 = r2.get(e4)), new t2(r2.list, this, this.world);
  }, e3;
};
var _i = function(e3) {
  return e3.prototype.clauses = function(t2) {
    let r2 = this.if("@hasComma").notIf("@hasComma @hasComma").notIf("@hasComma . .? (and|or) .").notIf("(#City && @hasComma) #Country").notIf("(#WeekDay && @hasComma) #Date").notIf("(#Date && @hasComma) #Year").notIf("@hasComma (too|also)$").match("@hasComma"), a2 = this.splitAfter(r2), n2 = a2.quotations();
    a2 = a2.splitOn(n2);
    let i2 = a2.parentheses();
    a2 = a2.splitOn(i2);
    let o2 = a2.if("#Copula #Adjective #Conjunction (#Pronoun|#Determiner) #Verb").match("#Conjunction");
    a2 = a2.splitBefore(o2);
    let s2 = a2.if("if .{2,9} then .").match("then");
    a2 = a2.splitBefore(s2), a2 = a2.splitBefore("as well as ."), a2 = a2.splitBefore("such as ."), a2 = a2.splitBefore("in addition to ."), a2 = a2.splitAfter("@hasSemicolon"), a2 = a2.splitAfter("@hasDash");
    let l2 = a2.filter((e4) => e4.wordCount() > 5 && e4.match("#Verb+").length >= 2);
    if (l2.found) {
      let e4 = l2.splitAfter("#Noun .* #Verb .* #Noun+");
      a2 = a2.splitOn(e4.eq(0));
    }
    return "number" == typeof t2 && (a2 = a2.get(t2)), new e3(a2.list, this, this.world);
  }, e3;
};
var Ki = function(e3) {
  class t2 extends e3 {
    constructor(e4, t3, r2) {
      super(e4, t3, r2), this.contracted = null;
    }
    expand() {
      return this.list.forEach((e4) => {
        let t3 = e4.terms(), r2 = t3[0].isTitleCase();
        t3.forEach((e5, r3) => {
          e5.set(e5.implicit || e5.text), e5.implicit = void 0, r3 < t3.length - 1 && "" === e5.post && (e5.post += " ");
        }), r2 && t3[0].toTitleCase();
      }), this;
    }
  }
  return e3.prototype.contractions = function(e4) {
    let r2 = this.match("@hasContraction+");
    return "number" == typeof e4 && (r2 = r2.get(e4)), new t2(r2.list, this, this.world);
  }, e3.prototype.expanded = e3.prototype.isExpanded, e3.prototype.contracted = e3.prototype.isContracted, e3;
};
var qi = function(e3) {
  const t2 = function(e4) {
    let t3 = e4.splitAfter("@hasComma").splitOn("(and|or) not?").not("(and|or) not?"), r3 = e4.match("[.] (and|or)", 0);
    return { things: t3, conjunction: e4.match("(and|or) not?"), beforeLast: r3, hasOxford: r3.has("@hasComma") };
  };
  class r2 extends e3 {
    conjunctions() {
      return this.match("(and|or)");
    }
    parts() {
      return this.splitAfter("@hasComma").splitOn("(and|or) not?");
    }
    items() {
      return t2(this).things;
    }
    add(e4) {
      return this.forEach((r3) => {
        let a2 = t2(r3).beforeLast;
        a2.append(e4), a2.termList(0).addPunctuation(",");
      }), this;
    }
    remove(e4) {
      return this.items().if(e4).remove();
    }
    hasOxfordComma() {
      return this.filter((e4) => t2(e4).hasOxford);
    }
    addOxfordComma() {
      let e4 = this.items(), t3 = e4.eq(e4.length - 2);
      return t3.found && false === t3.has("@hasComma") && t3.post(", "), this;
    }
    removeOxfordComma() {
      let e4 = this.items(), t3 = e4.eq(e4.length - 2);
      return t3.found && true === t3.has("@hasComma") && t3.post(" "), this;
    }
  }
  return r2.prototype.things = r2.prototype.items, e3.prototype.lists = function(e4) {
    let t3 = this.if("@hasComma+ .? (and|or) not? ."), a2 = t3.match("(#Noun|#Adjective|#Determiner|#Article)+ #Conjunction not? (#Article|#Determiner)? #Adjective? #Noun+").if("#Noun"), n2 = t3.match("(#Adjective|#Adverb)+ #Conjunction not? #Adverb? #Adjective+"), i2 = t3.match("(#Verb|#Adverb)+ #Conjunction not? #Adverb? #Verb+"), o2 = a2.concat(n2);
    return o2 = o2.concat(i2), o2 = o2.if("@hasComma"), "number" == typeof e4 && (o2 = t3.get(e4)), new r2(o2.list, this, this.world);
  }, e3;
};
const Wi = { hour: "an", heir: "an", heirloom: "an", honest: "an", honour: "an", honor: "an", uber: "an" }, Ri = { a: true, e: true, f: true, h: true, i: true, l: true, m: true, n: true, o: true, r: true, s: true, x: true }, Ui = [/^onc?e/i, /^u[bcfhjkqrstn][aeiou]/i, /^eul/i];
const Qi = { isSingular: [/(ax|test)is$/i, /(octop|vir|radi|nucle|fung|cact|stimul)us$/i, /(octop|vir)i$/i, /(rl)f$/i, /(alias|status)$/i, /(bu)s$/i, /(al|ad|at|er|et|ed|ad)o$/i, /(ti)um$/i, /(ti)a$/i, /sis$/i, /(?:(^f)fe|(lr)f)$/i, /hive$/i, /(^aeiouy|qu)y$/i, /(x|ch|ss|sh|z)$/i, /(matr|vert|ind|cort)(ix|ex)$/i, /(m|l)ouse$/i, /(m|l)ice$/i, /(antenn|formul|nebul|vertebr|vit)a$/i, /.sis$/i, /^(?!talis|.*hu)(.*)man$/i], isPlural: [/(antenn|formul|nebul|vertebr|vit)ae$/i, /(octop|vir|radi|nucle|fung|cact|stimul)i$/i, /men$/i, /.tia$/i, /(m|l)ice$/i] }, Zi = /s$/;
const Xi = { he: "his", she: "hers", they: "theirs", we: "ours", i: "mine", you: "yours", her: "hers", their: "theirs", our: "ours", my: "mine", your: "yours" };
const Yi = function(e3) {
  return true === e3.has("#Plural") || true !== e3.has("(#Pronoun|#Place|#Value|#Person|#Uncountable|#Month|#WeekDay|#Holiday|#Possessive)");
}, eo = function(e3) {
  if (e3.has("#Person") || e3.has("#Place")) return "";
  if (e3.has("#Plural")) return "the";
  let t2 = e3.text("normal").trim();
  if (Wi.hasOwnProperty(t2)) return Wi[t2];
  let r2 = t2.substr(0, 1);
  if (e3.has("^@isAcronym") && Ri.hasOwnProperty(r2)) return "an";
  for (let e4 = 0; e4 < Ui.length; e4++) if (Ui[e4].test(t2)) return "a";
  return /^[aeiou]/i.test(t2) ? "an" : "a";
}, to = function(e3) {
  return !Qi.isSingular.find((t2) => t2.test(e3)) && (true === Zi.test(e3) || (!!Qi.isPlural.find((t2) => t2.test(e3)) || null));
}, ro = function(e3) {
  let t2 = e3.text("text").trim();
  return Xi.hasOwnProperty(t2) ? (e3.replaceWith(Xi[t2], true), void e3.tag("Possessive", "toPossessive")) : /s$/.test(t2) ? (t2 += "'", e3.replaceWith(t2, true), void e3.tag("Possessive", "toPossessive")) : (t2 += "'s", e3.replaceWith(t2, true), void e3.tag("Possessive", "toPossessive"));
}, ao = function(e3) {
  let t2 = { main: e3 };
  if (e3.has("#Noun (of|by|for) .")) {
    let r2 = e3.splitAfter("[#Noun+]", 0);
    t2.main = r2.eq(0), t2.post = r2.eq(1);
  }
  return t2;
};
const no = { json: function(e3) {
  let t2 = null;
  "number" == typeof e3 && (t2 = e3, e3 = null), e3 = e3 || { text: true, normal: true, trim: true, terms: true };
  let r2 = [];
  return this.forEach((t3) => {
    let a2 = t3.json(e3)[0];
    a2.article = eo(t3), r2.push(a2);
  }), null !== t2 ? r2[t2] : r2;
}, adjectives: function() {
  let e3 = this.lookAhead("^(that|who|which)? (was|is|will)? be? #Adverb? #Adjective+");
  return e3 = e3.concat(this.lookBehind("#Adjective+ #Adverb?$")), e3 = e3.match("#Adjective"), e3.sort("index");
}, isPlural: function() {
  return this.if("#Plural");
}, hasPlural: function() {
  return this.filter((e3) => Yi(e3));
}, toPlural: function(e3) {
  let t2 = this.world.transforms.toPlural;
  return this.forEach((r2) => {
    if (r2.has("#Plural") || false === Yi(r2)) return;
    let a2 = ao(r2).main, n2 = a2.text("reduced");
    if ((a2.has("#Singular") || true !== to(n2)) && (n2 = t2(n2, this.world), a2.replace(n2).tag("#Plural"), e3)) {
      let e4 = a2.lookBefore("(an|a) #Adjective?$").not("#Adjective");
      true === e4.found && e4.remove();
    }
  }), this;
}, toSingular: function(e3) {
  let t2 = this.world.transforms.toSingular;
  return this.forEach((r2) => {
    if (r2.has("^#Singular+$") || false === Yi(r2)) return;
    let a2 = ao(r2).main, n2 = a2.text("reduced");
    if ((a2.has("#Plural") || true === to(n2)) && (n2 = t2(n2, this.world), a2.replace(n2).tag("#Singular"), e3)) {
      let e4 = r2, t3 = r2.lookBefore("#Adjective");
      t3.found && (e4 = t3);
      let a3 = eo(e4);
      e4.insertBefore(a3);
    }
  }), this;
}, toPossessive: function() {
  return this.forEach((e3) => {
    ro(e3);
  }), this;
} };
var io = function(e3) {
  class t2 extends e3 {
  }
  return Object.assign(t2.prototype, no), e3.prototype.nouns = function(e4, r2 = {}) {
    let a2 = this.match("(#City && @hasComma) (#Region|#Country)"), n2 = this.not(a2).splitAfter("@hasComma");
    n2 = n2.concat(a2);
    let i2 = n2.quotations();
    return i2.found && (n2 = n2.splitOn(i2.eq(0))), n2 = n2.match("#Noun+ (of|by)? the? #Noun+?"), true !== r2.keep_anaphora && (n2 = n2.not("#Pronoun"), n2 = n2.not("(there|these)"), n2 = n2.not("(#Month|#WeekDay)"), n2 = n2.not("(my|our|your|their|her|his)")), n2 = n2.not("(of|for|by|the)$"), "number" == typeof e4 && (n2 = n2.get(e4)), new t2(n2.list, this, this.world);
  }, e3;
};
const oo = /\(/, so = /\)/;
var lo = function(e3) {
  class t2 extends e3 {
    unwrap() {
      return this.list.forEach((e4) => {
        let t3 = e4.terms(0);
        t3.pre = t3.pre.replace(oo, "");
        let r2 = e4.lastTerm();
        r2.post = r2.post.replace(so, "");
      }), this;
    }
  }
  return e3.prototype.parentheses = function(e4) {
    let r2 = [];
    return this.list.forEach((e5) => {
      let t3 = e5.terms();
      for (let a2 = 0; a2 < t3.length; a2 += 1) {
        const n2 = t3[a2];
        if (oo.test(n2.pre)) {
          for (let i2 = a2; i2 < t3.length; i2 += 1) if (so.test(t3[i2].post)) {
            let t4 = i2 - a2 + 1;
            r2.push(e5.buildFrom(n2.id, t4)), a2 = i2;
            break;
          }
        }
      }
    }), "number" == typeof e4 ? (r2 = r2[e4] ? [r2[e4]] : [], new t2(r2, this, this.world)) : new t2(r2, this, this.world);
  }, e3;
};
var uo = function(e3) {
  class t2 extends e3 {
    constructor(e4, t3, r2) {
      super(e4, t3, r2), this.contracted = null;
    }
    strip() {
      return this.list.forEach((e4) => {
        e4.terms().forEach((e5) => {
          let t3 = e5.text.replace(/'s$/, "");
          e5.set(t3 || e5.text);
        });
      }), this;
    }
  }
  return e3.prototype.possessives = function(e4) {
    let r2 = this.match("#Noun+? #Possessive");
    return "number" == typeof e4 && (r2 = r2.get(e4)), new t2(r2.list, this, this.world);
  }, e3;
};
const co = { '"': '"', "＂": "＂", "'": "'", "“": "”", "‘": "’", "‟": "”", "‛": "’", "„": "”", "⹂": "”", "‚": "’", "«": "»", "‹": "›", "‵": "′", "‶": "″", "‷": "‴", "〝": "〞", "`": "´", "〟": "〞" }, ho = RegExp("(" + Object.keys(co).join("|") + ")");
var go = function(e3) {
  class t2 extends e3 {
    unwrap() {
      return this;
    }
  }
  return e3.prototype.quotations = function(e4) {
    let r2 = [];
    return this.list.forEach((e5) => {
      let t3 = e5.terms();
      for (let a2 = 0; a2 < t3.length; a2 += 1) {
        const n2 = t3[a2];
        if (ho.test(n2.pre)) {
          let i2 = (n2.pre.match(ho) || [])[0], o2 = co[i2];
          for (let i3 = a2; i3 < t3.length; i3 += 1) if (-1 !== t3[i3].post.indexOf(o2)) {
            let t4 = i3 - a2 + 1;
            r2.push(e5.buildFrom(n2.id, t4)), a2 = i3;
            break;
          }
        }
      }
    }), "number" == typeof e4 ? (r2 = r2[e4] ? [r2[e4]] : [], new t2(r2, this, this.world)) : new t2(r2, this, this.world);
  }, e3.prototype.quotes = e3.prototype.quotations, e3;
};
var po = function(e3, t2) {
  let r2 = e3.verb, a2 = r2.text("reduced");
  if (r2.has("#Infinitive")) return a2;
  let n2 = null;
  return r2.has("#PastTense") ? n2 = "PastTense" : r2.has("#Gerund") ? n2 = "Gerund" : r2.has("#PresentTense") ? n2 = "PresentTense" : r2.has("#Participle") ? n2 = "Participle" : r2.has("#Actor") && (n2 = "Actor"), t2.transforms.toInfinitive(a2, t2, n2);
};
var mo = function(e3) {
  let t2 = e3.verb;
  if (t2.has("(are|were|does)") || e3.auxiliary.has("(are|were|does)")) return true;
  let r2 = (function(e4) {
    return e4.lookBehind("#Noun+").last();
  })(t2);
  return !r2.has("(he|she|many|both)") && (!!r2.has("(we|they|you|i)") || !r2.has("#Person") && (!!r2.has("#Plural") || !r2.has("#Singular") && (!t2.has("(is|am|do|was)") && (!(e3.auxiliary.has("(is|am|do|was)") && !e3.negative.found) && null))));
};
const fo = po, bo = mo;
var yo = function(e3) {
  let t2 = e3.lookBehind(), r2 = t2.nouns(null, { keep_anaphora: true }).last();
  return r2.found || (r2 = t2.match("(that|this|each)").last(), r2 = r2.tag("#Noun").nouns()), r2;
};
const vo = yo;
const wo = mo;
const ko = po, Ao = (e3) => {
  let t2 = false, r2 = wo(e3), a2 = e3.negative.found;
  e3.verb.lookBehind("i (#Adverb|#Verb)?$").found && (t2 = true);
  let n2 = { PastTense: "was", PresentTense: "is", FutureTense: "will be", Infinitive: "is", Gerund: "being", Actor: "", PerfectTense: "been", Pluperfect: "been" };
  return r2 && (n2.PastTense = "were", n2.PresentTense = "are", n2.Infinitive = "are"), true === t2 && (n2.PastTense = "was", n2.PresentTense = "am", n2.Infinitive = "am"), a2 && (n2.PastTense += " not", n2.PresentTense += " not", n2.FutureTense = "will not be", n2.Infinitive += " not", n2.PerfectTense = "not " + n2.PerfectTense, n2.Pluperfect = "not " + n2.Pluperfect, n2.Gerund = "not " + n2.Gerund), n2;
}, Do = function(e3) {
  let t2 = e3.verb.text();
  return { PastTense: t2 + " have", PresentTense: t2, FutureTense: t2, Infinitive: t2 };
}, $o = mo;
var Po = function(e3, t2) {
  let r2 = e3.verb;
  if (r2.has("#Copula") || "be" === r2.out("normal") && e3.auxiliary.has("will")) return Ao(e3);
  if (e3.auxiliary.has("are") && r2.has("#Gerund")) {
    let r3 = e3.original.clone(), a3 = r3.clone().replace("are", "were"), n3 = r3.clone().replace("are", "will be"), i3 = ko(e3, t2);
    return { PastTense: a3.text(), PresentTense: r3.text(), FutureTense: n3.text(), Infinitive: i3 };
  }
  if (r2.has("#Modal")) return Do(e3);
  let a2 = ko(e3, t2);
  if (!a2) return {};
  let n2 = t2.transforms.conjugate(a2, t2);
  n2.Infinitive = a2;
  let i2 = $o(e3);
  true === i2 && (n2.PresentTense = n2.Infinitive);
  let o2 = e3.verb.termList(0).hasHyphen();
  if (e3.particle.found) {
    let t3 = e3.particle.text(), r3 = true === o2 ? "-" : " ";
    Object.keys(n2).forEach((e4) => n2[e4] += r3 + t3);
  }
  const s2 = e3.negative.found;
  return n2.FutureTense = n2.FutureTense || "will " + n2.Infinitive, s2 && (n2.PastTense = "did not " + n2.Infinitive, n2.FutureTense = "will not " + n2.Infinitive, i2 ? (n2.PresentTense = "do not " + n2.Infinitive, n2.Infinitive = "do not " + n2.Infinitive) : (n2.PresentTense = "does not " + n2.Infinitive, n2.Infinitive = "does not " + n2.Infinitive), n2.Gerund = "not " + n2.Gerund), n2;
};
const Eo = Po;
var Ho = { useParticiple: function(e3) {
  return !!e3.auxiliary.has("(could|should|would|may|can|must)") || (!!e3.auxiliary.has("am .+? being") || !!e3.auxiliary.has("had .+? been"));
}, toParticiple: function(e3, t2) {
  if (e3.auxiliary.has("(have|had)") && e3.verb.has("#Participle")) return;
  let r2 = Eo(e3, t2), a2 = r2.Participle || r2.PastTense;
  a2 && e3.verb.replaceWith(a2, false), e3.auxiliary.has("am .+? being") && (e3.auxiliary.remove("am"), e3.auxiliary.replace("being", "have been")), e3.auxiliary.has("have") || e3.auxiliary.append("have"), e3.verb.tag("Participle", "toParticiple"), e3.auxiliary.replace("can", "could"), e3.auxiliary.replace("be have", "have been"), e3.auxiliary.replace("not have", "have not"), e3.auxiliary.tag("Auxiliary");
} };
const jo = function(e3, t2) {
  let r2 = e3.verb;
  if (!e3.negative.found) {
    if (e3.auxiliary.found) return e3.auxiliary.eq(0).append("not"), void (e3.auxiliary.has("#Modal have not") && e3.auxiliary.replace("have not", "not have"));
    if (r2.has("(#Copula|will|has|had|do)")) r2.append("not");
    else {
      if (r2.has("#PastTense")) {
        let a2 = fo(e3, t2);
        return r2.replaceWith(a2, true), void r2.prepend("did not");
      }
      if (r2.has("#PresentTense")) {
        let a2 = fo(e3, t2);
        return r2.replaceWith(a2, true), void (bo(e3) ? r2.prepend("do not") : r2.prepend("does not"));
      }
      if (r2.has("#Gerund")) {
        let a2 = fo(e3, t2);
        return r2.replaceWith(a2, true), void r2.prepend("not");
      }
      bo(e3) ? r2.prepend("does not") : r2.prepend("do not");
    }
  }
}, No = function(e3) {
  let t2 = { adverb: e3.match("#Adverb+"), negative: e3.match("#Negative"), auxiliary: e3.match("#Auxiliary+").not("(#Negative|#Adverb)"), particle: e3.match("#Particle"), verb: e3.match("#Verb+").not("(#Adverb|#Negative|#Auxiliary|#Particle)"), original: e3, subject: vo(e3) };
  if (t2.verb.has("(#PresentTense|#PastTense|#Infinitive) #Gerund$") && (t2.verb = t2.verb.not("#Gerund$")), !t2.verb.found) return Object.keys(t2).forEach((e4) => {
    t2[e4] = t2[e4].not(".");
  }), t2.verb = e3, t2;
  if (t2.adverb && t2.adverb.found) {
    let r2 = t2.adverb.text("reduced") + "$";
    e3.has(r2) && (t2.adverbAfter = true);
  }
  return t2;
}, xo = mo, Fo = yo, Co = Po, { toParticiple: Bo, useParticiple: Go } = Ho, zo = function(e3) {
  return e3.auxiliary.remove("(will|are|am|being)"), e3.auxiliary.remove("(did|does)"), e3.auxiliary.remove("(had|has|have)"), e3.particle.remove(), e3.negative.remove(), e3;
};
const Io = { json: function(e3) {
  let t2 = null;
  "number" == typeof e3 && (t2 = e3, e3 = null), e3 = e3 || { text: true, normal: true, trim: true, terms: true };
  let r2 = [];
  return this.forEach((t3) => {
    let a2 = t3.json(e3)[0], n2 = No(t3);
    a2.parts = {}, Object.keys(n2).forEach((e4) => {
      n2[e4] && "Doc" === n2[e4].isA ? a2.parts[e4] = n2[e4].text("normal") : a2.parts[e4] = n2[e4];
    }), a2.isNegative = t3.has("#Negative"), a2.conjugations = Co(n2, this.world), r2.push(a2);
  }), null !== t2 ? r2[t2] : r2;
}, adverbs: function() {
  let e3 = [];
  this.forEach((t3) => {
    let r2 = No(t3).adverb;
    r2.found && (e3 = e3.concat(r2.list));
  });
  let t2 = this.lookBehind("#Adverb+$");
  return t2.found && (e3 = t2.list.concat(e3)), t2 = this.lookAhead("^#Adverb+"), t2.found && (e3 = e3.concat(t2.list)), this.buildFrom(e3);
}, isPlural: function() {
  let e3 = [];
  return this.forEach((t2) => {
    let r2 = No(t2);
    true === xo(r2, this.world) && e3.push(t2.list[0]);
  }), this.buildFrom(e3);
}, isSingular: function() {
  let e3 = [];
  return this.forEach((t2) => {
    let r2 = No(t2);
    false === xo(r2, this.world) && e3.push(t2.list[0]);
  }), this.buildFrom(e3);
}, conjugate: function() {
  let e3 = [];
  return this.forEach((t2) => {
    let r2 = No(t2), a2 = Co(r2, this.world);
    e3.push(a2);
  }), e3;
}, toPastTense: function() {
  return this.forEach((e3) => {
    let t2 = No(e3);
    if (Go(t2)) return void Bo(t2, this.world);
    if (e3.has("#Imperative")) return;
    if (e3.has("be") && e3.lookBehind("to$").found) return;
    if (t2.verb.has("#Gerund") && t2.auxiliary.has("(is|will|was)")) return void e3.replace("is", "was");
    let r2 = Co(t2, this.world).PastTense;
    r2 && (t2 = zo(t2), t2.verb.replaceWith(r2, false), t2.auxiliary.remove("(do|did|will)"));
  }), this;
}, toPresentTense: function() {
  return this.forEach((e3) => {
    if (e3.has("#Imperative")) return;
    let t2 = No(e3), r2 = Co(t2, this.world), a2 = r2.PresentTense;
    if (e3.lookBehind("(i|we) (#Adverb|#Verb)?$").found && (a2 = r2.Infinitive), a2) {
      if (t2.auxiliary.has("(have|had) been")) return t2.auxiliary.replace("(have|had) been", "am being"), void (r2.Particle && (a2 = r2.Particle || r2.PastTense));
      t2.verb.replaceWith(a2, false), t2.verb.tag("PresentTense"), t2 = zo(t2), t2.auxiliary.remove("#Modal"), t2.auxiliary.remove("(do|did|will)");
    }
  }), this;
}, toFutureTense: function() {
  return this.forEach((e3) => {
    let t2 = No(e3);
    if (Go(t2)) return;
    if (e3.has("#Imperative")) return;
    let r2 = Co(t2, this.world).FutureTense;
    r2 && (t2 = zo(t2), t2.auxiliary.remove("#Modal"), t2.verb.replaceWith(r2, false), t2.verb.tag("FutureTense"), t2.auxiliary.remove("(do|did|will)"));
  }), this;
}, toInfinitive: function() {
  return this.forEach((e3) => {
    let t2 = No(e3), r2 = Co(t2, this.world).Infinitive;
    r2 && (e3.replaceWith(r2, false), e3.tag("Infinitive"));
  }), this;
}, toGerund: function() {
  return this.forEach((e3) => {
    let t2 = No(e3), r2 = Co(t2, this.world).Gerund;
    r2 && (e3.replaceWith(r2, false), e3.tag("Gerund"));
  }), this;
}, toParticiple: function() {
  return this.forEach((e3) => {
    let t2 = No(e3), r2 = !t2.auxiliary.found;
    Bo(t2, this.world), r2 && (t2.verb.prepend(t2.auxiliary.text()), t2.auxiliary.remove());
  }), this;
}, isNegative: function() {
  return this.if("#Negative");
}, isPositive: function() {
  return this.ifNo("#Negative");
}, isImperative: function() {
  return this.if("#Imperative");
}, toNegative: function() {
  return this.list.forEach((e3) => {
    let t2 = this.buildFrom([e3]), r2 = No(t2);
    jo(r2, t2.world);
  }), this;
}, toPositive: function() {
  let e3 = this.match("do not #Verb");
  return e3.found && e3.remove("do not"), this.remove("#Negative");
}, subject: function() {
  let e3 = [];
  return this.forEach((t2) => {
    let r2 = Fo(t2);
    r2.list[0] && e3.push(r2.list[0]);
  }), this.buildFrom(e3);
} };
const Oo = Ia, To = [Ji, Si, _i, Ki, qi, io, lo, uo, go, function(e3) {
  class t2 extends e3 {
  }
  return Object.assign(t2.prototype, Io), t2.prototype.negate = t2.prototype.toNegative, e3.prototype.verbs = function(e4) {
    let r2 = this.match("(#Adverb|#Auxiliary|#Verb|#Negative|#Particle)+");
    r2 = r2.not("^#Adverb+"), r2 = r2.not("#Adverb+$");
    let a2 = r2.match("(#Adverb && @hasComma) #Adverb"), n2 = r2.not(a2).splitAfter("@hasComma");
    return n2 = n2.concat(a2), n2.sort("index"), n2 = n2.if("#Verb"), n2.has("(is|was)$") && (n2 = n2.splitBefore("(is|was)$")), n2.has("#PresentTense #Adverb #PresentTense") && (n2 = n2.splitBefore("#Adverb #PresentTense")), "number" == typeof e4 && (n2 = n2.get(e4)), new t2(n2.list, this, this.world);
  }, e3;
}, function(e3) {
  class t2 extends e3 {
  }
  return e3.prototype.people = function(e4) {
    let r2 = this.splitAfter("@hasComma");
    return r2 = r2.match("#Person+"), "number" == typeof e4 && (r2 = r2.get(e4)), new t2(r2.list, this, this.world);
  }, e3;
}];
const Vo = { misc: Ga, selections: Ia }, Mo = Mi, Jo = xt, Lo = function(e3) {
  return Object.keys(Oo).forEach((t2) => e3.prototype[t2] = Oo[t2]), To.forEach((t2) => t2(e3)), e3;
};
class So {
  constructor(e3, t2, r2) {
    this.list = e3, Object.defineProperty(this, "from", { enumerable: false, value: t2, writable: true }), void 0 === r2 && void 0 !== t2 && (r2 = t2.world), Object.defineProperty(this, "world", { enumerable: false, value: r2, writable: true }), Object.defineProperty(this, "_cache", { enumerable: false, writable: true, value: {} }), Object.defineProperty(this, "found", { get: () => this.list.length > 0 }), Object.defineProperty(this, "length", { get: () => this.list.length }), Object.defineProperty(this, "isA", { get: () => "Doc" });
  }
  tagger() {
    return Mo(this);
  }
  pool() {
    return this.list.length > 0 ? this.list[0].pool : this.all().list[0].pool;
  }
}
So.prototype.buildFrom = function(e3) {
  return e3 = e3.map((e4) => e4.clone(true)), new So(e3, this, this.world);
}, So.prototype.fromText = function(e3) {
  let t2 = Jo(e3, this.world, this.pool());
  return this.buildFrom(t2);
}, Object.assign(So.prototype, Vo.misc), Object.assign(So.prototype, Vo.selections), Lo(So);
const _o = { untag: "unTag", and: "match", notIf: "ifNo", only: "if", onlyIf: "if" };
Object.keys(_o).forEach((e3) => So.prototype[e3] = So.prototype[_o[e3]]);
const Ko = Va;
const qo = xt, Wo = zt, Ro = So, Uo = nt, Qo = ne$1, Zo = ot, Xo = function(e3) {
  let t2 = e3.termList();
  return Ko(t2, e3.world), e3.world.taggers.forEach((t3) => {
    t3(e3);
  }), e3;
}, Yo = _e;
var es = (function e(t2) {
  let r2 = t2;
  const a2 = function(e3 = "", t3) {
    t3 && r2.addWords(t3);
    let a3 = qo(e3, r2), n2 = new Ro(a3, null, r2);
    return n2.tagger(), n2;
  };
  return a2.tokenize = function(e3 = "", t3) {
    let a3 = r2;
    t3 && (a3 = a3.clone(), a3.words = {}, a3.addWords(t3));
    let n2 = qo(e3, a3), i2 = new Ro(n2, null, a3);
    return (t3 || i2.world.taggers.length > 0) && Xo(i2), i2;
  }, a2.extend = function(e3) {
    return e3(Ro, r2, this, Uo, Qo, Zo), this;
  }, a2.fromJSON = function(e3) {
    let t3 = Wo(e3, r2);
    return new Ro(t3, null, r2);
  }, a2.clone = function() {
    return e(r2.clone());
  }, a2.verbose = function(e3 = true) {
    return r2.verbose(e3), this;
  }, a2.world = function() {
    return r2;
  }, a2.parseMatch = function(e3, t3) {
    return Yo(e3, t3);
  }, a2.version = "13.11.4", a2.import = a2.load, a2.plugin = a2.extend, a2;
})(new Rr());
var e2 = function(e3, n2) {
  let t2 = e3.match("#Value+? #Money+ #Currency+ (and #Money+ #Currency+)+?");
  return e3.match("#Money").forEach(((e4) => {
    e4.lookAfter("#Currency").found || (t2 = t2.concat(e4));
  })), "number" == typeof n2 && (t2 = t2.get(n2)), t2;
};
const n = "twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|fourty";
var t = function(e3, t2) {
  let r2 = e3.match("#Value+");
  if (r2.has("#NumericValue #NumericValue") && (r2.has("#Value @hasComma #Value") ? r2.splitAfter("@hasComma") : r2.has("#NumericValue #Fraction") ? r2.splitAfter("#NumericValue #Fraction") : r2 = r2.splitAfter("#NumericValue")), r2.has("#Value #Value #Value") && !r2.has("#Multiple") && r2.has("(" + n + ") #Cardinal #Cardinal") && (r2 = r2.splitAfter("(" + n + ") #Cardinal")), r2.has("#Value #Value")) {
    r2.has("#NumericValue #NumericValue") && (r2 = r2.splitOn("#Year")), r2.has("(" + n + ") (eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen)") && (r2 = r2.splitAfter("(" + n + ")"));
    let e4 = r2.match("#Cardinal #Cardinal");
    if (e4.found && !r2.has("(point|decimal|#Fraction)") && !e4.has("#Cardinal (#Multiple|point|decimal)")) {
      let t3 = r2.has(`(one|two|three|four|five|six|seven|eight|nine) (${n})`), a2 = e4.has("(" + n + ") #Cardinal"), i2 = e4.has("#Multiple #Value");
      t3 || a2 || i2 || e4.terms().forEach(((e5) => {
        r2 = r2.splitOn(e5);
      }));
    }
    r2.match("#Ordinal #Ordinal").match("#TextValue").found && !r2.has("#Multiple") && (r2.has("(" + n + ") #Ordinal") || (r2 = r2.splitAfter("#Ordinal"))), r2.has("#Ordinal #Cardinal") && (r2 = r2.splitBefore("#Cardinal+")), r2.has("#TextValue #NumericValue") && !r2.has("(" + n + "|#Multiple)") && (r2 = r2.splitBefore("#NumericValue+"));
  }
  return r2.has("#NumberRange") && (r2 = r2.splitAfter("#NumberRange")), "number" == typeof t2 && (r2 = r2.get(t2)), r2;
};
var r = function(e3, n2) {
  let t2 = e3.match("#Fraction+");
  return t2 = t2.filter(((e4) => !e4.lookBehind("#Value and$").found)), "number" == typeof n2 && (t2 = t2.eq(n2)), t2;
};
var a = function(e3, n2) {
  let t2 = e3.match("#Percent+");
  return t2 = t2.concat(e3.match("[#Cardinal] percent", 0)), "number" == typeof n2 && (t2 = t2.eq(n2)), t2;
};
var i = { ones: { zeroth: 0, first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9 }, teens: { tenth: 10, eleventh: 11, twelfth: 12, thirteenth: 13, fourteenth: 14, fifteenth: 15, sixteenth: 16, seventeenth: 17, eighteenth: 18, nineteenth: 19, ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19 }, tens: { twentieth: 20, thirtieth: 30, fortieth: 40, fourtieth: 40, fiftieth: 50, sixtieth: 60, seventieth: 70, eightieth: 80, ninetieth: 90, twenty: 20, thirty: 30, forty: 40, fourty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 }, multiples: { hundredth: 100, thousandth: 1e3, millionth: 1e6, billionth: 1e9, trillionth: 1e12, quadrillionth: 1e15, quintillionth: 1e18, sextillionth: 1e21, septillionth: 1e24, hundred: 100, thousand: 1e3, million: 1e6, billion: 1e9, trillion: 1e12, quadrillion: 1e15, quintillion: 1e18, sextillion: 1e21, septillion: 1e24, grand: 1e3 } };
const s = i;
const o = i;
const u = (e3) => {
  const n2 = [{ reg: /^(minus|negative)[\s\-]/i, mult: -1 }, { reg: /^(a\s)?half[\s\-](of\s)?/i, mult: 0.5 }];
  for (let t2 = 0; t2 < n2.length; t2++) if (true === n2[t2].reg.test(e3)) return { amount: n2[t2].mult, str: e3.replace(n2[t2].reg, "") };
  return { amount: 1, str: e3 };
}, l = i, m = (e3, n2) => {
  if (s.ones.hasOwnProperty(e3)) {
    if (n2.ones || n2.teens) return false;
  } else if (s.teens.hasOwnProperty(e3)) {
    if (n2.ones || n2.teens || n2.tens) return false;
  } else if (s.tens.hasOwnProperty(e3) && (n2.ones || n2.teens || n2.tens)) return false;
  return true;
}, c = function(e3) {
  let n2 = "0.";
  for (let t2 = 0; t2 < e3.length; t2++) {
    let r2 = e3[t2];
    if (true === o.ones.hasOwnProperty(r2)) n2 += o.ones[r2];
    else if (true === o.teens.hasOwnProperty(r2)) n2 += o.teens[r2];
    else if (true === o.tens.hasOwnProperty(r2)) n2 += o.tens[r2];
    else {
      if (true !== /^[0-9]$/.test(r2)) return 0;
      n2 += r2;
    }
  }
  return parseFloat(n2);
}, h = (e3) => e3 = (e3 = (e3 = (e3 = (e3 = (e3 = (e3 = (e3 = e3.replace(/1st$/, "1")).replace(/2nd$/, "2")).replace(/3rd$/, "3")).replace(/([4567890])r?th$/, "$1")).replace(/^[$€¥£¢]/, "")).replace(/[%$€¥£¢]$/, "")).replace(/,/g, "")).replace(/([0-9])([a-z\u00C0-\u00FF]{1,2})$/, "$1"), f = /^([0-9,\. ]+)\/([0-9,\. ]+)$/, d = { "a few": 3, "a couple": 2, "a dozen": 12, "two dozen": 24, zero: 0 }, y = (e3) => Object.keys(e3).reduce(((n2, t2) => n2 += e3[t2]), 0);
var p = function(e3) {
  if (true === d.hasOwnProperty(e3)) return d[e3];
  if ("a" === e3 || "an" === e3) return 1;
  const n2 = u(e3);
  let t2 = null, r2 = {}, a2 = 0, i2 = false;
  const s2 = (e3 = n2.str).split(/[ -]/);
  for (let e4 = 0; e4 < s2.length; e4++) {
    let o2 = s2[e4];
    if (o2 = h(o2), !o2 || "and" === o2) continue;
    if ("-" === o2 || "negative" === o2) {
      i2 = true;
      continue;
    }
    if ("-" === o2.charAt(0) && (i2 = true, o2 = o2.substr(1)), "point" === o2) return a2 += y(r2), a2 += c(s2.slice(e4 + 1, s2.length)), a2 *= n2.amount, a2;
    const u2 = o2.match(f);
    if (u2) {
      const e5 = parseFloat(u2[1].replace(/[, ]/g, "")), n3 = parseFloat(u2[2].replace(/[, ]/g, ""));
      n3 && (a2 += e5 / n3 || 0);
    } else {
      if (l.tens.hasOwnProperty(o2) && r2.ones && 1 === Object.keys(r2).length && (a2 = 100 * r2.ones, r2 = {}), false === m(o2, r2)) return null;
      if (/^[0-9\.]+$/.test(o2)) r2.ones = parseFloat(o2);
      else if (true === l.ones.hasOwnProperty(o2)) r2.ones = l.ones[o2];
      else if (true === l.teens.hasOwnProperty(o2)) r2.teens = l.teens[o2];
      else if (true === l.tens.hasOwnProperty(o2)) r2.tens = l.tens[o2];
      else if (true === l.multiples.hasOwnProperty(o2)) {
        let n3 = l.multiples[o2];
        if (n3 === t2) return null;
        if (100 === n3 && void 0 !== s2[e4 + 1]) {
          const t3 = s2[e4 + 1];
          l.multiples[t3] && (n3 *= l.multiples[t3], e4 += 1);
        }
        null === t2 || n3 < t2 ? (a2 += (y(r2) || 1) * n3, t2 = n3, r2 = {}) : (a2 += y(r2), t2 = n3, a2 = (a2 || 1) * n3, r2 = {});
      }
    }
  }
  return a2 += y(r2), a2 *= n2.amount, a2 *= i2 ? -1 : 1, 0 === a2 && 0 === Object.keys(r2).length ? null : a2;
};
const b = /s$/, g = p, x = function(e3) {
  let n2 = e3.text("reduced");
  return g(n2);
};
let $ = { half: 2, halve: 2, quarter: 4 };
var v = function(e3) {
  let n2 = (function(e4) {
    let n3 = e4.text("reduced");
    return $.hasOwnProperty(n3) ? { numerator: 1, denominator: $[n3] } : null;
  })(e3 = e3.clone()) || (function(e4) {
    let n3 = e4.text("reduced").match(/^([-+]?[0-9]+)\/([-+]?[0-9]+)(st|nd|rd|th)?s?$/);
    return n3 && n3[1] && n3[0] ? { numerator: Number(n3[1]), denominator: Number(n3[2]) } : null;
  })(e3) || (function(e4) {
    let n3 = e4.match("[<num>#Value+] out of every? [<den>#Value+]");
    if (true !== n3.found) return null;
    let { num: t2, den: r2 } = n3.groups();
    return t2 && r2 ? (t2 = x(t2), r2 = x(r2), "number" == typeof t2 && "number" == typeof r2 ? { numerator: t2, denominator: r2 } : null) : null;
  })(e3) || (function(e4) {
    let n3 = e4.match("[<num>(#Cardinal|a)+] [<dem>#Fraction+]");
    if (true !== n3.found) return null;
    let { num: t2, dem: r2 } = n3.groups();
    t2 = t2.has("a") ? 1 : x(t2);
    let a2 = r2.text("reduced");
    return b.test(a2) && (a2 = a2.replace(b, ""), r2.replaceWith(a2)), r2 = $.hasOwnProperty(a2) ? $[a2] : x(r2), "number" == typeof t2 && "number" == typeof r2 ? { numerator: t2, denominator: r2 } : null;
  })(e3) || (function(e4) {
    let n3 = e4.match("^#Ordinal$");
    if (true !== n3.found) return null;
    if (e4.lookAhead("^of .")) return { numerator: 1, denominator: x(n3) };
    return null;
  })(e3) || null;
  return null !== n2 && n2.numerator && n2.denominator && (n2.decimal = n2.numerator / n2.denominator, n2.decimal = ((e4) => {
    let n3 = Math.round(1e3 * e4) / 1e3;
    return 0 === n3 && 0 !== e4 ? e4 : n3;
  })(n2.decimal)), n2;
};
const w = p, k = v;
var C2 = function(e3) {
  let n2 = e3.text("reduced"), t2 = /[0-9],[0-9]/.test(e3.text("text")), r2 = (function(e4, n3, t3) {
    let r3 = (e4 = e4.replace(/,/g, "")).split(/^([^0-9]*)([0-9.,]*)([^0-9]*)$/);
    if (r3 && r3[2] && n3.terms().length < 2) {
      let n4 = parseFloat(r3[2] || e4);
      "number" != typeof n4 && (n4 = null);
      let a3 = r3[3] || "";
      return "st" !== a3 && "nd" !== a3 && "rd" !== a3 && "th" !== a3 || (a3 = ""), "m" !== a3 && "M" !== a3 || (n4 *= 1e6, a3 = ""), "k" !== a3 && "k" !== a3 || (n4 *= 1e3, a3 = ""), n4 = n4, { prefix: r3[1] || "", num: n4, suffix: a3 };
    }
    return null;
  })(n2, e3);
  if (null !== r2) return r2.hasComma = t2, r2;
  let a2 = e3.match("#Fraction #Fraction+$");
  a2 = false === a2.found ? e3.match("^#Fraction$") : a2;
  let i2 = null;
  a2.found && (i2 = k(a2), n2 = (e3 = (e3 = e3.not(a2)).not("and$")).text("reduced"));
  let s2 = 0;
  return n2 && (s2 = w(n2) || 0), i2 && i2.decimal && (s2 += i2.decimal), { hasComma: t2, prefix: "", num: s2, suffix: "" };
};
var O = function(e3) {
  if (e3 < 1e6) return String(e3);
  let n2;
  return n2 = "number" == typeof e3 ? e3.toFixed(0) : e3, -1 === n2.indexOf("e+") ? n2 : n2.replace(".", "").split("e+").reduce((function(e4, n3) {
    return e4 + Array(n3 - e4.length + 2).join(0);
  }));
};
const V = O, F = [["ninety", 90], ["eighty", 80], ["seventy", 70], ["sixty", 60], ["fifty", 50], ["forty", 40], ["thirty", 30], ["twenty", 20]], M2 = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"], E = [[1e24, "septillion"], [1e20, "hundred sextillion"], [1e21, "sextillion"], [1e20, "hundred quintillion"], [1e18, "quintillion"], [1e17, "hundred quadrillion"], [1e15, "quadrillion"], [1e14, "hundred trillion"], [1e12, "trillion"], [1e11, "hundred billion"], [1e9, "billion"], [1e8, "hundred million"], [1e6, "million"], [1e5, "hundred thousand"], [1e3, "thousand"], [100, "hundred"], [1, "one"]], N = function(e3) {
  let n2 = [];
  if (e3 > 100) return n2;
  for (let t2 = 0; t2 < F.length; t2++) e3 >= F[t2][1] && (e3 -= F[t2][1], n2.push(F[t2][0]));
  return M2[e3] && n2.push(M2[e3]), n2;
};
var P2 = function(e3) {
  if (0 === e3 || "0" === e3) return "zero";
  e3 > 1e21 && (e3 = V(e3));
  let n2 = [];
  e3 < 0 && (n2.push("minus"), e3 = Math.abs(e3));
  let t2 = (function(e4) {
    let n3 = e4, t3 = [];
    return E.forEach(((r2) => {
      if (e4 >= r2[0]) {
        let e5 = Math.floor(n3 / r2[0]);
        n3 -= e5 * r2[0], e5 && t3.push({ unit: r2[1], count: e5 });
      }
    })), t3;
  })(e3);
  for (let e4 = 0; e4 < t2.length; e4++) {
    let r2 = t2[e4].unit;
    "one" === r2 && (r2 = "", n2.length > 1 && n2.push("and")), n2 = n2.concat(N(t2[e4].count)), n2.push(r2);
  }
  return n2 = n2.concat(((e4) => {
    const n3 = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
    let t3 = [], r2 = V(e4).match(/\.([0-9]+)/);
    if (!r2 || !r2[0]) return t3;
    t3.push("point");
    let a2 = r2[0].split("");
    for (let e5 = 0; e5 < a2.length; e5++) t3.push(n3[a2[e5]]);
    return t3;
  })(e3)), n2 = n2.filter(((e4) => e4)), 0 === n2.length && (n2[0] = ""), n2.join(" ");
};
const z = O;
const A = P2, j = { one: "first", two: "second", three: "third", five: "fifth", eight: "eighth", nine: "ninth", twelve: "twelfth", twenty: "twentieth", thirty: "thirtieth", forty: "fortieth", fourty: "fourtieth", fifty: "fiftieth", sixty: "sixtieth", seventy: "seventieth", eighty: "eightieth", ninety: "ninetieth" };
var q = (e3) => {
  let n2 = A(e3).split(" "), t2 = n2[n2.length - 1];
  return j.hasOwnProperty(t2) ? n2[n2.length - 1] = j[t2] : n2[n2.length - 1] = t2.replace(/y$/, "i") + "th", n2.join(" ");
};
var S = { prefixes: { "¢": "cents", $: "dollars", "£": "pounds", "¥": "yen", "€": "euros", "₡": "colón", "฿": "baht", "₭": "kip", "₩": "won", "₹": "rupees", "₽": "ruble", "₺": "liras" }, suffixes: { "%": "percent", s: "seconds", cm: "centimetres", km: "kilometres" } };
const T = O, R = P2, L = function(e3) {
  if (!e3 && 0 !== e3) return null;
  let n2 = e3 % 100;
  if (n2 > 10 && n2 < 20) return String(e3) + "th";
  const t2 = { 0: "th", 1: "st", 2: "nd", 3: "rd" };
  let r2 = z(e3), a2 = r2.slice(r2.length - 1, r2.length);
  return r2 += t2[a2] ? t2[a2] : "th", r2;
}, W = q, B = S.prefixes, D = S.suffixes, U = { usd: true, eur: true, jpy: true, gbp: true, cad: true, aud: true, chf: true, cny: true, hkd: true, nzd: true, kr: true, rub: true }, K = function(e3) {
  return B.hasOwnProperty(e3.prefix) && (e3.suffix += B[e3.prefix], e3.prefix = ""), D.hasOwnProperty(e3.suffix) && (e3.suffix = D[e3.suffix]), U.hasOwnProperty(e3.suffix) && (e3.suffix = e3.suffix.toUpperCase()), e3.suffix && (e3.suffix = " " + e3.suffix), e3;
};
var Y = function(e3, n2, t2) {
  let r2 = String(e3.num);
  return n2 ? (e3 = K(e3), t2 ? (r2 = W(r2), `${e3.prefix || ""}${r2}${e3.suffix || ""}`) : (r2 = R(r2), `${e3.prefix || ""}${r2}${e3.suffix || ""}`)) : t2 ? (r2 = L(r2), `${(e3 = K(e3)).prefix || ""}${r2}${e3.suffix || ""}`) : (true === e3.hasComma && (r2 = e3.num.toLocaleString()), r2 = T(r2), `${e3.prefix || ""}${r2}${e3.suffix || ""}`);
};
const G = C2, H2 = function(e3, n2, t2) {
  if (false === e3) return;
  let r2 = n2.lookAhead("^(#Unit|#Noun)");
  r2.has("(#Address|#Money|#Percent)") || n2.has("#Ordinal") || (1 === t2.num ? r2.nouns().toSingular() : r2.has("#Singular") && r2.nouns().toPlural());
}, I = Y, Q2 = p;
let Z = { json: function(e3) {
  let n2 = null;
  "number" == typeof e3 && (n2 = e3, e3 = null), e3 = e3 || { text: true, normal: true, trim: true, terms: true };
  let t2 = [];
  return this.forEach(((n3) => {
    let r2 = n3.json(e3)[0], a2 = G(n3);
    r2.prefix = a2.prefix, r2.number = a2.num, r2.suffix = a2.suffix, r2.cardinal = I(a2, false, false), r2.ordinal = I(a2, false, true), r2.textCardinal = I(a2, true, false), r2.textOrdinal = I(a2, true, true), t2.push(r2);
  })), null !== n2 ? t2[n2] : t2;
}, units: function() {
  let e3 = this.lookAhead("(#Unit|#Noun)+");
  return e3 = e3.splitAfter("@hasComma").first(), e3 = e3.not("#Pronoun"), e3.first();
}, isOrdinal: function() {
  return this.if("#Ordinal");
}, isCardinal: function() {
  return this.if("#Cardinal");
}, toNumber: function() {
  return this.forEach(((e3) => {
    let n2 = G(e3);
    if (null === n2.num) return;
    let t2 = I(n2, false, e3.has("#Ordinal"));
    e3.replaceWith(t2, true), e3.tag("NumericValue");
  })), this;
}, toLocaleString: function() {
  return this.forEach(((e3) => {
    let n2 = G(e3);
    if (null === n2.num) return;
    n2.num = n2.num.toLocaleString();
    let t2 = I(n2, false, e3.has("#Ordinal"));
    e3.replaceWith(t2, true);
  })), this;
}, toText: function() {
  return this.forEach(((e3) => {
    let n2 = G(e3);
    if (null === n2.num) return;
    let t2 = I(n2, true, e3.has("#Ordinal"));
    e3.replaceWith(t2, true), e3.tag("TextValue");
  })), this;
}, toCardinal: function(e3) {
  return this.if("#Ordinal").forEach(((n2) => {
    let t2 = G(n2);
    if (null === t2.num) return;
    let r2 = I(t2, n2.has("#TextValue"), false);
    if (n2.has("#NumberRange")) {
      let e4 = n2.termList()[0];
      e4.text && "" === e4.post && (e4.post = " ");
    }
    n2.replaceWith(r2, true), n2.tag("Cardinal"), H2(e3, n2, t2);
  })), this;
}, toOrdinal: function() {
  return this.if("#Cardinal").forEach(((e3) => {
    let n2 = G(e3);
    if (null === n2.num) return;
    let t2 = I(n2, e3.has("#TextValue"), true);
    if (e3.has("#NumberRange")) {
      let n3 = e3.termList()[0];
      n3.text && "" === n3.post && (n3.post = " ");
    }
    e3.replaceWith(t2, true), e3.tag("Ordinal");
    let r2 = this.lookAhead("^#Plural");
    r2.found && r2.nouns().toSingular();
  })), this;
}, isEqual: function(e3) {
  return this.filter(((n2) => G(n2).num === e3));
}, greaterThan: function(e3) {
  return this.filter(((n2) => G(n2).num > e3));
}, lessThan: function(e3) {
  return this.filter(((n2) => G(n2).num < e3));
}, between: function(e3, n2) {
  return this.filter(((t2) => {
    let r2 = G(t2).num;
    return r2 > e3 && r2 < n2;
  }));
}, set: function(e3, n2) {
  return void 0 === e3 || ("string" == typeof e3 && (e3 = Q2(e3)), this.forEach(((t2) => {
    let r2 = G(t2);
    if (r2.num = e3, null === r2.num) return;
    let a2 = I(r2, t2.has("#TextValue"), t2.has("#Ordinal"));
    (t2 = t2.not("#Currency")).replaceWith(a2, true), H2(n2, t2, r2);
  }))), this;
}, add: function(e3, n2) {
  return e3 ? ("string" == typeof e3 && (e3 = Q2(e3)), this.forEach(((t2) => {
    let r2 = G(t2);
    if (null === r2.num) return;
    r2.num += e3;
    let a2 = I(r2, t2.has("#TextValue"), t2.has("#Ordinal"));
    (t2 = t2.not("#Currency")).replaceWith(a2, true), H2(n2, t2, r2);
  })), this) : this;
}, subtract: function(e3, n2) {
  return this.add(-1 * e3, n2);
}, increment: function(e3) {
  return this.add(1, e3), this;
}, decrement: function(e3) {
  return this.add(-1, e3), this;
}, romanNumerals: function(e3) {
  let n2 = this.match("#RomanNumeral").numbers();
  return "number" == typeof e3 && (n2 = n2.get(e3)), n2;
}, normalize: function() {
  const e3 = { "%": true };
  return this.forEach(((n2) => {
    let t2 = G(n2);
    if (null !== t2.num && t2.suffix && true !== e3[t2.suffix]) {
      let e4 = t2.prefix || "";
      n2 = n2.replaceWith(e4 + t2.num + " " + t2.suffix);
    }
  })), this;
}, get: function(e3) {
  let n2 = [];
  return this.forEach(((e4) => {
    n2.push(G(e4).num);
  })), void 0 !== e3 ? n2[e3] || null : n2 || null;
} };
Z.toNice = Z.toLocaleString, Z.isBetween = Z.between, Z.minus = Z.subtract, Z.plus = Z.add, Z.equals = Z.isEqual;
var J = Z;
const X = C2, _ = function(e3) {
  let n2 = X(e3).num;
  return "number" == typeof n2 ? n2 / 100 : null;
};
var ee = { get: function(e3) {
  let n2 = [];
  return this.forEach(((e4) => {
    let t2 = _(e4);
    null !== t2 && n2.push(t2);
  })), void 0 !== e3 ? n2[e3] || null : n2 || null;
}, json: function(e3) {
  let n2 = null;
  "number" == typeof e3 && (n2 = e3, e3 = null), e3 = e3 || { text: true, normal: true, trim: true, terms: true };
  let t2 = [];
  return this.forEach(((n3) => {
    let r2 = n3.json(e3)[0], a2 = _(n3);
    if (r2.number = a2, null !== a2) {
      let e4 = 100 * a2;
      r2.textNumber = `${e4} percent`, r2.cardinal = `${e4}%`;
    }
    t2.push(r2);
  })), null !== n2 ? t2[n2] || {} : t2;
}, toFraction: function() {
  return this.forEach(((e3) => {
    let n2 = _(e3);
    if (null !== n2) {
      n2 *= 100, n2 = Math.round(100 * n2) / 100;
      let t2 = `${n2}/100`;
      this.replace(e3, t2);
    }
  })), this;
} }, ne = [{ dem: "american", name: "dollar", iso: "usd", sub: "cent", sym: ["$", "US$", "U$"] }, { name: "euro", iso: "eur", sub: "cent", sym: ["€"] }, { dem: "british", name: "pound", iso: "gbp", sub: "penny", alias: { sterling: true }, sym: ["£"] }, { name: "renminbi", iso: "cny", plural: "yuán", alias: { yuan: true }, sym: ["元"] }, { dem: "japanese", name: "yen", iso: "jpy", sub: "sen", sym: ["¥", "円", "圓"] }, { dem: "swedish", name: "krona", iso: "sek", sub: "öre", alias: { ore: true, kronor: true }, sym: ["kr"] }, { dem: "estonian", name: "kroon", iso: "eek", sub: "sent", sym: ["kr"] }, { dem: "norwegian", name: "krone", iso: "nok", sub: "øre", sym: ["kr"] }, { dem: "icelandic", name: "króna", iso: "isk", sym: ["kr"] }, { dem: "danish", name: "krone", iso: "dkk", sub: "øre", sym: ["kr."] }, { dem: "zambian", name: "kwacha", iso: "zmw", sub: "ngwee", sym: ["K"] }, { dem: "malawian", name: "kwacha", iso: "mwk", sub: "tambala", sym: ["K"] }, { dem: "greek", name: "drachma", iso: "grd", sub: "leptοn", sym: ["Δρχ.", "Δρ.", "₯"] }, { dem: "eastern caribbean", name: "dollar", iso: "xcd", sub: "cent", sym: ["$"] }, { dem: "finnish", name: "markka", iso: "fim", sub: "penni", sym: ["mk"] }, { dem: "polish", name: "złoty", iso: "pln", sub: "grosz", sym: ["zł"] }, { dem: "slovenian", name: "tolar", iso: "sit", sub: "stotin", sym: [] }, { dem: "australian", name: "dollar", iso: "aud", sub: "cent", sym: ["$", "A$", "AU$"] }, { dem: "deutsche", name: "mark", iso: "dem", sub: "pfennig", sym: ["DM"] }, { dem: "thai", name: "baht", iso: "thb", sub: "satang", sym: ["฿"] }, { dem: "canadian", name: "dollar", iso: "cad", sub: "cent", sym: ["$", "Can$", "C$", "CA$", "CAD"] }, { dem: "mexican", name: "peso", iso: "mxn", sub: "centavo", sym: ["$", "Mex$"] }, { dem: "spanish", name: "peseta", iso: "esp", sub: "céntimo", sym: ["Pta"] }, { dem: "new zealand", name: "dollar", iso: "nzd", sub: "cent", sym: ["$", "NZ$"] }, { dem: "chilean", name: "peso", iso: "clp", sub: "centavo", sym: ["Cifrão", "$"] }, { dem: "nigerian", name: "naira", iso: "ngn", sub: "kobo", sym: ["₦"] }, { dem: "austrian", name: "schilling", iso: "ats", sub: "groschen", sym: ["S", "öS"] }, { dem: "guatemalan", name: "quetzal", iso: "gtq", sub: "centavo", sym: ["Q"] }, { dem: "philippine", name: "peso", iso: "php", sub: "sentimo", sym: ["₱"] }, { dem: "hungarian", name: "forint", iso: "huf", sym: ["Ft"] }, { dem: "russian", name: "ruble", iso: "rub", sub: "kopeyka", sym: ["₽", "руб", "р."] }, { dem: "kuwaiti", name: "dinar", iso: "kwd", sub: "fils", sym: ["د.ك", "KD"] }, { dem: "israeli", name: "new shekel", iso: "ils", sub: "agora", sym: ["₪"] }, { dem: "latvian", name: "lats", iso: "lvl", sub: "santīms", sym: ["Ls"] }, { dem: "kazakhstani", name: "tenge", iso: "kzt", sub: "tıyn", sym: ["₸"] }, { dem: "iraqi", name: "dinar", iso: "iqd", sub: "fils", sym: ["د.ع"] }, { dem: "bahamian", name: "dollar", iso: "bsd", sub: "cent", sym: ["$", "B$"] }, { dem: "seychellois", name: "rupee", iso: "scr", sub: "cent", sym: ["SCR", "SR"] }, { dem: "albanian", name: "lek", iso: "all", sub: "qindarkë", sym: ["L"] }, { dem: "bulgarian", name: "lev", iso: "bgn", sub: "stotinka", sym: ["лв."] }, { dem: "irish", name: "pound", iso: "iep", sym: ["£", "IR£"] }, { name: "cfp franc", iso: "xpf", sym: ["f"] }, { dem: "south african", name: "rand", iso: "zar", sub: "cent", sym: ["R"] }, { dem: "south korean", name: "won", iso: "krw", sub: "jeon", plural: "won", sym: ["₩"] }, { dem: "north korean", name: "won", iso: "kpw", sub: "chon", plural: "won", sym: ["₩"] }, { dem: "portuguese", name: "escudo", iso: "pte", sub: "centavo", sym: [] }, { dem: "ghanaian", name: "cedi", iso: "ghs", sub: "pesewa", sym: ["GH₵"] }, { dem: "hong kong", name: "dollar", iso: "hkd", sub: "毫", sym: ["$"] }, { dem: "new taiwan", name: "dollar", iso: "twd", sub: "dime", sym: ["NT$"] }, { dem: "east german", name: "mark", iso: "ddm", sub: "pfennig", sym: ["M"] }, { dem: "namibian", name: "dollar", iso: "nad", sub: "cent", sym: ["$"] }, { dem: "malaysian", name: "ringgit", iso: "myr", sub: "sen", sym: ["RM"] }, { dem: "swiss", name: "franc", iso: "chf", sym: ["Rp."] }, { dem: "panamanian", name: "balboa", iso: "pab", sub: "centésimo", sym: ["B/."] }, { dem: "indonesian", name: "rupiah", iso: "idr", sub: "sen", sym: ["Rp"] }, { dem: "brunei", name: "dollar", iso: "bnd", sub: "sen", sym: ["$", "B$"] }, { dem: "venezuelan", name: "bolívar", iso: "vef", sub: "céntimo", sym: ["Bs.F", "Bs."] }, { dem: "macedonian", name: "denar", iso: "mkd", sub: "deni", sym: ["den"] }, { dem: "mauritanian", name: "ouguiya", iso: "mru", sub: "khoums", sym: ["UM"] }, { dem: "argentine", name: "peso", iso: "ars", sub: "centavo", sym: ["$"] }, { dem: "libyan", name: "dinar", iso: "lyd", sub: "dirham", sym: ["LD", "ل.د"] }, { dem: "jordanian", name: "dinar", iso: "jod", sub: "dirham", sym: ["د.أ"] }, { dem: "french", name: "franc", iso: "frf", sub: "centime", sym: ["F", "Fr", "FF", "₣"] }, { dem: "syrian", name: "pound", iso: "syp", sub: "piastre", sym: ["LS", "£S"] }, { dem: "belize", name: "dollar", iso: "bzd", sub: "cent", sym: ["$"] }, { dem: "saudi", name: "riyal", iso: "sar", sub: "halalah", sym: ["SAR", "ر.س", " ﷼"] }, { dem: "surinamese", name: "dollar", iso: "srd", sub: "cent", sym: ["$"] }, { dem: "singapore", name: "dollar", iso: "sgd", sub: "cent", sym: ["S$", "$"] }, { dem: "nepalese", name: "rupee", iso: "npr", sub: "paisa", sym: ["रु ₨", "Re"] }, { dem: "nicaraguan", name: "córdoba", iso: "nio", sub: "centavo", sym: ["C$"] }, { dem: "bangladeshi", name: "taka", iso: "bdt", sub: "poysha", sym: ["৳"] }, { dem: "indian", name: "rupee", iso: "inr", sub: "paisa", sym: ["₹"] }, { dem: "maldivian", name: "rufiyaa", iso: "mvr", sub: "laari", sym: ["Rf", "MRf", "MVR", ".ރ "] }, { dem: "sri lankan", name: "rupee", iso: "lkr", sub: "cents", sym: ["Rs", "රු", "ரூ"] }, { dem: "bhutanese", name: "ngultrum", iso: "btn", sub: "chhertum", sym: ["Nu."] }, { dem: "turkish", name: "lira", iso: "try", sub: "kuruş", sym: ["YTL"] }, { dem: "serbian", name: "dinar", iso: "rsd", sub: "para", sym: ["din", "дин"] }, { dem: "bosnia and herzegovina", name: "convertible mark", iso: "bam", sub: "fening", sym: ["KM"] }, { dem: "botswana", name: "pula", iso: "bwp", sub: "thebe", sym: ["p"] }, { dem: "swazi", name: "lilangeni", iso: "szl", sub: "cent", sym: ["L", "E"] }, { dem: "lithuanian", name: "litas", iso: "ltl", sub: "centas", sym: ["Lt", "ct"] }, { dem: "mauritian", name: "rupee", iso: "mur", sub: "cent", sym: ["₨"] }, { dem: "pakistani", name: "rupee", iso: "pkr", sub: "paisa", sym: ["₨"] }, { dem: "maltese", name: "lira", iso: "mtl", sub: "cent", sym: ["₤", "Lm"] }, { dem: "cypriot", name: "pound", iso: "cyp", sub: "cent", sym: ["£"] }, { dem: "moldovan", name: "leu", iso: "mdl", sym: ["l"] }, { dem: "croatian", name: "kuna", iso: "hrk", sub: "lipa", sym: ["kn"] }, { dem: "afghan", name: "afghani", iso: "afn", sub: "pul", sym: ["؋", "Af", "Afs"] }, { dem: "ecuadorian", name: "sucre", iso: "ecs", sub: "centavo", sym: ["S/."] }, { dem: "sierra leonean", name: "leone", iso: "sll", sub: "cent", sym: ["Le"] }];
const te = ne, re = C2, ae2 = {};
let ie = {};
te.forEach(((e3) => {
  e3.sym.forEach(((n2) => {
    ae2[n2] = ae2[n2] || e3.iso;
  })), ae2[e3.iso] = ae2[e3.iso] || e3.iso, e3.sub && (ie[e3.sub] = true);
}));
let se = `(${Object.keys(ie).join("|")})`;
const oe = Y, ue = function(e3) {
  let n2 = 0, t2 = e3.match(`and #Money+ ${se}`);
  if (t2.found) {
    e3 = e3.not(t2);
    let r3 = re(t2.match("#Value+"));
    r3 && r3.num && (n2 = r3.num / 100);
  }
  let r2 = re(e3), a2 = r2.num || 0;
  a2 += n2;
  let i2 = ((s2 = r2).suffix && ae2.hasOwnProperty(s2.suffix) ? te.find(((e4) => e4.iso === ae2[s2.suffix])) : s2.prefix && ae2.hasOwnProperty(s2.prefix) ? te.find(((e4) => e4.iso === ae2[s2.prefix])) : null) || (function(e4) {
    let n3 = e4.match("#Currency+");
    n3.nouns().toSingular();
    let t3 = n3.text("reduced");
    return te.find(((e5) => (t3 === `${e5.dem} ${e5.name}` || t3 === e5.iso || t3 === e5.sub || t3 === e5.name || !(!e5.alias || true !== e5.alias[t3])) && e5));
  })(e3) || {};
  var s2;
  let o2 = "";
  return i2 && i2.sym && (o2 = i2.sym[0], a2 && e3.has(`${se}`) && (a2 /= 100)), { num: a2, iso: i2.iso, demonym: i2.dem, currency: i2.name, plural: i2.plural, symbol: o2 };
}, le = function(e3 = "") {
  return e3.replace(/\w\S*/g, (function(e4) {
    return e4.charAt(0).toUpperCase() + e4.substr(1).toLowerCase();
  }));
};
var me$1 = { get: function(e3) {
  let n2 = [];
  return this.forEach(((e4) => {
    n2.push(ue(e4));
  })), void 0 !== e3 ? n2[e3] || null : n2 || null;
}, currency: function(e3) {
  let n2 = [];
  return this.forEach(((e4) => {
    let t2 = ue(e4);
    t2 && n2.push(t2);
  })), "number" == typeof e3 ? n2[e3] || null : n2 || null;
}, json: function(e3) {
  let n2 = null;
  "number" == typeof e3 && (n2 = e3, e3 = null), e3 = e3 || { text: true, normal: true, trim: true, terms: true };
  let t2 = [];
  return this.forEach(((n3) => {
    let r2 = n3.json(e3)[0], a2 = ue(n3);
    if (r2.number = a2.num, a2.iso && (r2.iso = a2.iso.toUpperCase(), r2.symbol = a2.symbol, r2.currency = le(a2.demonym) + " " + le(a2.currency)), r2.textFmt = oe(a2, true, false), a2.currency) {
      let e4 = a2.currency;
      1 !== a2.num && (e4 = a2.plural || e4 + "s"), r2.textFmt += " " + e4;
    }
    t2.push(r2);
  })), null !== n2 ? t2[n2] || {} : t2;
} }, ce = {};
const he = P2, fe = q;
ce.toText = function(e3) {
  if (!e3.numerator || !e3.denominator) return "";
  let n2 = he(e3.numerator), t2 = fe(e3.denominator);
  return 2 === e3.denominator && (t2 = "half"), n2 && t2 ? (1 !== e3.numerator && (t2 += "s"), `${n2} ${t2}`) : "";
}, ce.textCardinal = function(e3) {
  if (!e3.numerator || !e3.denominator) return "";
  return `${he(e3.numerator)} out of ${he(e3.denominator)}`;
}, ce.toDecimal = function(e3) {
  return e3.decimal;
};
const de = v, ye = ce, pe = { get: function(e3) {
  let n2 = [];
  return this.forEach(((e4) => {
    n2.push(de(e4));
  })), void 0 !== e3 ? n2[e3] || null : n2 || null;
}, toDecimal() {
  return this.forEach(((e3) => {
    let n2 = de(e3);
    if (n2) {
      let t2 = ye.toDecimal(n2);
      e3.replaceWith(String(t2), true), e3.tag("NumericValue"), e3.unTag("Fraction");
    }
  })), this;
}, json: function(e3) {
  let n2 = null;
  "number" == typeof e3 && (n2 = e3, e3 = null), e3 = e3 || { text: true, normal: true, trim: true, terms: true };
  let t2 = [];
  return this.forEach(((n3) => {
    let r2 = n3.json(e3)[0], a2 = de(n3) || {}, i2 = ye.toDecimal(a2);
    r2.numerator = a2.numerator, r2.denominator = a2.denominator, r2.number = i2, r2.textOrdinal = ye.toText(a2), r2.textCardinal = ye.textCardinal(a2), t2.push(r2);
  })), null !== n2 ? t2[n2] || {} : t2;
}, normalize: function() {
  return this.forEach(((e3) => {
    let n2 = de(e3);
    if (n2 && "number" == typeof n2.numerator && "number" == typeof n2.denominator) {
      let t2 = `${n2.numerator}/${n2.denominator}`;
      this.replace(e3, t2);
    }
  })), this;
}, toText: function(e3) {
  let n2 = [];
  return this.forEach(((e4) => {
    let n3 = de(e4) || {}, t2 = ye.toText(n3);
    e4.replaceWith(t2, true), e4.tag("Fraction");
  })), void 0 !== e3 ? n2[e3] : n2;
}, toPercentage: function() {
  return this.forEach(((e3) => {
    let n2 = de(e3);
    if (n2.decimal || 0 === n2.decimal) {
      let t2 = 100 * n2.decimal;
      t2 = Math.round(100 * t2) / 100, this.replace(e3, `${t2}%`);
    }
  })), this;
} };
pe.toNumber = pe.toDecimal;
var be = pe;
const ge = function(e3) {
  return e3.match("(hundred|thousand|million|billion|trillion|quadrillion|quintillion|sextillion|septillion)").tag("#Multiple", "fraction-tagger"), e3.match("[(half|quarter)] of? (a|an)", 0).tag("Fraction", "millionth"), e3.match("#Adverb [half]", 0).tag("Fraction", "nearly-half"), e3.match("[half] the", 0).tag("Fraction", "half-the"), e3.match("#Value (halves|halfs|quarters)").tag("Fraction", "two-halves"), e3.match("a #Ordinal").tag("Fraction", "a-quarter"), e3.match("(#Fraction && /s$/)").lookBefore("#Cardinal+$").tag("Fraction"), e3.match("[#Cardinal+ #Ordinal] of .", 0).tag("Fraction", "ordinal-of"), e3.match("[(#NumericValue && #Ordinal)] of .", 0).tag("Fraction", "num-ordinal-of"), e3.match("(a|one) #Cardinal?+ #Ordinal").tag("Fraction", "a-ordinal"), e3.match("#Cardinal+ out? of every? #Cardinal").tag("Fraction", "fraction-tagger"), e3;
}, xe = function(e3) {
  const n2 = "money-tagger";
  e3.match("#Money and #Money #Currency?").tag("Money", "money-and-money"), e3.match("#Money").not("#TextValue").match("/\\.[0-9]{3}$/").unTag("#Money", "three-decimal money"), e3.ifNo("#Value").match("#Currency #Verb").unTag("Currency", "no-currency"), e3.match("#Value #Currency [and] #Value (cents|ore|centavos|sens)", 0).tag("Money", n2);
  let t2 = e3.match("[<num>#Value] [<currency>(mark|rand|won|rub|ore)]");
  return t2.group("num").tag("Money", n2), t2.group("currency").tag("Currency", n2), e3;
};
var $e = function(e3) {
  (e3 = xe(e3)).match("the [/[0-9]+s$/]").tag("#Plural", "number-tag"), e3.match("half a? #Value").tag("Value", "half-a-value"), e3.match("#Value [and a (half|quarter)]", 0).tag(["TextValue", "#Fraction"], "value-and-a-half"), (e3 = ge(e3)).match("#Cardinal and #Fraction #Fraction").tag("Value", "number-tag");
};
const ve = { mark: true, sucre: true, leone: true, afghani: true, rand: true, try: true, mop: true, won: true, all: true, rub: true, eek: true, sit: true, bam: true, npr: true, leu: true };
let we = { kronor: "Currency" };
ne.forEach(((e3) => {
  e3.iso && !ve[e3.iso] && (we[e3.iso] = ["Acronym", "Currency"]);
  let n2 = e3.name;
  if (n2 && !ve[n2] && (we[n2] = "Currency", we[n2 + "s"] = "Currency"), e3.dem) {
    let t2 = e3.dem;
    we[`${t2} ${n2}`] = "Currency", we[`${t2} ${n2}s`] = "Currency";
  }
  e3.sub && (we[e3.sub] = "Currency");
}));
const ke = e2, Ce = t, Oe = r, Ve = a, Fe = J, Me2 = ee, Ee = me$1, Ne = be, Pe = $e, ze = { Fraction: { isA: ["Value"] }, Multiple: { isA: "Value" } }, Ae = we;
var je = function(e3, n2) {
  n2.addWords(Ae), n2.addTags(ze), n2.postProcess(Pe);
  class t2 extends e3 {
  }
  Object.assign(t2.prototype, Fe);
  class r2 extends t2 {
  }
  Object.assign(r2.prototype, Ee);
  class a2 extends t2 {
  }
  Object.assign(a2.prototype, Ne);
  class i2 extends t2 {
  }
  Object.assign(i2.prototype, Me2);
  const s2 = { numbers: function(e4) {
    let n3 = Ce(this, e4);
    return new t2(n3.list, this, this.world);
  }, percentages: function(e4) {
    let n3 = Ve(this, e4);
    return new i2(n3.list, this, this.world);
  }, fractions: function(e4) {
    let n3 = Oe(this, e4);
    return new a2(n3.list, this, this.world);
  }, money: function(e4) {
    let n3 = ke(this, e4);
    return new r2(n3.list, this, this.world);
  } };
  return s2.values = s2.numbers, s2.percents = s2.percentages, Object.assign(e3.prototype, s2), e3;
};
es.extend(je);
class Pluralize extends Text {
  static componentType = "pluralize";
  static componentDocs = {
    summary: "Renders a word in its singular or plural form based on a count"
  };
  static rendererType = "text";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.pluralForm = {
      description: "Custom plural form to use (overrides automatic pluralization).",
      createComponentOfType: "text",
      createStateVariable: "pluralForm",
      defaultValue: null,
      public: true
    };
    attributes.basedOnNumber = {
      description: "Number that determines whether to use the singular or plural form.",
      createComponentOfType: "number",
      createStateVariable: "basedOnNumber",
      defaultValue: null,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "value",
      newName: "valuePrePluralize"
    });
    stateVariableDefinitions.value = {
      description: "The selected (singular or plural) word.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        valuePrePluralize: {
          dependencyType: "stateVariable",
          variableName: "valuePrePluralize"
        },
        pluralForm: {
          dependencyType: "stateVariable",
          variableName: "pluralForm"
        },
        basedOnNumber: {
          dependencyType: "stateVariable",
          variableName: "basedOnNumber"
        }
      }),
      definition: function({ dependencyValues }) {
        let text = es(dependencyValues.valuePrePluralize);
        let allwords = text.values().toNumber().all().terms().json();
        if (allwords.length === 0) {
          return {
            setValue: { value: dependencyValues.valuePrePluralize }
          };
        }
        let makePlural;
        if (dependencyValues.basedOnNumber !== null) {
          makePlural = numberDesignatesPlural(
            dependencyValues.basedOnNumber
          );
        }
        if (allwords.length === 1) {
          if (makePlural === void 0) {
            makePlural = true;
          }
          if (!makePlural) {
            return {
              setValue: {
                value: dependencyValues.valuePrePluralize
              }
            };
          }
          if (dependencyValues.pluralForm !== null) {
            return {
              setValue: {
                value: dependencyValues.pluralForm
              }
            };
          } else {
            return {
              setValue: {
                value: text.nouns().toPlural().all().out("text")
              }
            };
          }
        }
        if (makePlural === void 0) {
          let nounIndices = [];
          let valueIndices = [];
          let nouns = text.nouns().json();
          for (let [ind, word] of allwords.entries()) {
            let nextNoun = nouns[nounIndices.length];
            if (word.terms[0].tags.includes("Value")) {
              valueIndices.push(ind);
            }
            if (nextNoun !== void 0 && nextNoun.text === word.text) {
              nounIndices.push(ind);
            }
          }
          makePlural = [];
          let numbers = text.values().numbers().json();
          let lastValueInd = -1;
          for (let ind of nounIndices) {
            while (Number(valueIndices[lastValueInd + 1]) < Number(ind)) {
              lastValueInd++;
            }
            if (numbers[lastValueInd] && numberDesignatesPlural(
              Number(numbers[lastValueInd].normal)
            )) {
              makePlural.push(true);
            } else {
              makePlural.push(false);
            }
          }
        }
        if (makePlural === false) {
          return {
            setValue: { value: dependencyValues.valuePrePluralize }
          };
        }
        let text2 = es(dependencyValues.valuePrePluralize);
        if (dependencyValues.pluralForm !== null) {
          for (let [ind, nounObj] of text.nouns().data().entries()) {
            if (makePlural === true || makePlural[ind] === true) {
              text2.replace(
                nounObj.text,
                dependencyValues.pluralForm
              );
            }
          }
        } else {
          let numNouns = text.nouns().json().length;
          for (let ind = 0; ind < numNouns; ind++) {
            if (makePlural === true || makePlural[ind] === true) {
              text2.nouns(ind).toPlural().all();
            }
          }
        }
        return {
          setValue: {
            value: text2.out("text")
          }
        };
      }
    };
    stateVariableDefinitions.text = {
      description: "The selected word as a text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { text: dependencyValues.value }
      })
    };
    return stateVariableDefinitions;
  }
}
function numberDesignatesPlural(num) {
  return num !== 1;
}
class Feedback extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateHide: this.updateHide.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "feedback";
  static componentDocs = {
    summary: "Targeted feedback shown when conditions are met"
  };
  static renderChildren = true;
  static primaryStateVariableForDefinition = "feedbackText";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.hide;
    attributes.condition = {
      createComponentOfType: "boolean",
      description: "Boolean expression; the feedback is shown when the condition is true."
    };
    attributes.updateWith = {
      createReferences: true,
      description: "References whose changes trigger re-evaluation of the feedback."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.updateWith = {
      returnDependencies: () => ({
        updateWith: {
          dependencyType: "attributeRefResolutions",
          attributeName: "updateWith"
        }
      }),
      definition({ dependencyValues }) {
        let updateWith = [];
        if (dependencyValues.updateWith !== null) {
          for (let refResolution of dependencyValues.updateWith) {
            if (refResolution.unresolvedPath === null) {
              updateWith.push(refResolution.componentIdx);
            }
          }
        }
        if (updateWith.length === 0) {
          updateWith = null;
        }
        return { setValue: { updateWith } };
      }
    };
    stateVariableDefinitions.updateWithComponentIds = {
      chainActionOnActionOfStateVariableTargets: {
        triggeredAction: "updateHide"
      },
      returnDependencies: () => ({
        updateWith: {
          dependencyType: "stateVariable",
          variableName: "updateWith"
        }
      }),
      definition({ dependencyValues }) {
        let updateWithComponentIds = [];
        if (dependencyValues.updateWith) {
          for (let cIdx of dependencyValues.updateWith) {
            if (!updateWithComponentIds.includes(cIdx)) {
              updateWithComponentIds.push(cIdx);
            }
          }
        }
        return { setValue: { updateWithComponentIds } };
      },
      markStale() {
        return { updateActionChaining: true };
      }
    };
    stateVariableDefinitions.hideWhenUpdated = {
      returnDependencies: () => ({
        condition: {
          dependencyType: "attributeComponent",
          attributeName: "condition",
          variableNames: ["value"]
        },
        showFeedback: {
          dependencyType: "flag",
          flagName: "showFeedback"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!dependencyValues.showFeedback) {
          return { setValue: { hideWhenUpdated: true } };
        }
        let hideWhenUpdated;
        if (dependencyValues.condition === null) {
          hideWhenUpdated = false;
        } else {
          hideWhenUpdated = !dependencyValues.condition.stateValues.value;
        }
        return { setValue: { hideWhenUpdated } };
      }
    };
    stateVariableDefinitions.hide = {
      forRenderer: true,
      defaultValue: true,
      hasEssential: true,
      returnDependencies: () => ({
        updateWith: {
          dependencyType: "stateVariable",
          variableName: "updateWith"
        },
        condition: {
          dependencyType: "attributeComponent",
          attributeName: "condition",
          variableNames: ["value"]
        },
        showFeedback: {
          dependencyType: "flag",
          flagName: "showFeedback"
        },
        shadowSource: {
          dependencyType: "shadowSource",
          givePropVariableValue: true
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.updateWith) {
          return {
            useEssentialOrDefaultValue: { hide: true }
          };
        }
        if (!dependencyValues.showFeedback) {
          return { setValue: { hide: true } };
        }
        if (dependencyValues.shadowSource && Object.values(
          dependencyValues.shadowSource.stateValues
        )[0] == void 0) {
          return { setValue: { hide: true } };
        }
        let hide;
        if (dependencyValues.condition === null) {
          hide = false;
        } else {
          hide = !dependencyValues.condition.stateValues.value;
        }
        return { setValue: { hide } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (!dependencyValues.updateWith) {
          return { success: false };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "hide",
                value: desiredStateVariableValues.hide
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.feedbackText = {
      forRenderer: true,
      defaultValue: null,
      hasEssential: true,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          feedbackText: true
        }
      })
    };
    return stateVariableDefinitions;
  }
  async updateHide({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let updateInstructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "hide",
        value: await this.stateValues.hideWhenUpdated
      }
    ];
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class CollaborateGroups extends BaseComponent {
  static componentType = "collaborateGroups";
  static componentDocs = {
    summary: "Defines collaboration groups for student input."
  };
  static rendererType = void 0;
  static excludeFromSchema = true;
  static stateVariableToBeShadowed = "collaborateGroups";
  static returnChildLogic(args) {
    let childLogic = super.returnChildLogic(args);
    let atLeastZeroPoints = childLogic.newLeaf({
      name: "atLeastZeroPoints",
      componentType: "point",
      comparison: "atLeast",
      number: 0
    });
    let breakIntoPointsByCommas = breakStringsAndOthersIntoComponentsByStringCommas();
    let exactlyOneString = childLogic.newLeaf({
      name: "exactlyOneString",
      componentType: "string",
      number: 1,
      isSugar: true,
      logicToWaitOnSugar: ["atLeastZeroPoints"],
      replacementFunction: breakIntoPointsByCommas
    });
    childLogic.newOperator({
      name: "pointsXorSugar",
      operator: "xor",
      propositions: [exactlyOneString, atLeastZeroPoints],
      setAsBase: true
    });
    return childLogic;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.collaborateGroups = {
      returnDependencies: () => ({
        pointChildren: {
          dependencyType: "child",
          childLogicName: "atLeastZeroPoints",
          variableNames: ["xs", "coords", "numDimensions"]
        }
      }),
      definition: function({ dependencyValues }) {
        let groups = {};
        for (let point of dependencyValues.pointChildren) {
          if (point.stateValues.numDimensions !== 2) {
            console.warn(
              `invalid collaborate group: ${point.stateValues.coords.toString()}`
            );
          } else {
            let numberOfGroups = point.stateValues.xs[0].evaluate_to_constant();
            let group = point.stateValues.xs[1].evaluate_to_constant();
            if (!(Number.isInteger(numberOfGroups) && numberOfGroups > 0 && Number.isInteger(group) && group > 0)) {
              console.warn(
                `invalid collaborate group: ${point.stateValues.coords.toString()}`
              );
            } else {
              if (groups[numberOfGroups] === void 0) {
                groups[numberOfGroups] = [];
              }
              groups[numberOfGroups].push(group);
            }
          }
        }
        let matchGroup = function({
          groupNumber,
          numberOfGroups
        } = {}) {
          if (numberOfGroups > 1) {
            let collaborationAssignment = groups[numberOfGroups];
            if (collaborationAssignment === void 0) {
              return false;
            } else {
              return collaborationAssignment.includes(
                groupNumber
              );
            }
          } else {
            return true;
          }
        };
        return {
          setValue: {
            collaborateGroups: {
              groups,
              matchGroup
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class CollaborateGroupSetup extends BaseComponent {
  static componentType = "collaborateGroupSetup";
  static componentDocs = {
    summary: "Per-group setup for collaboration groups."
  };
  static excludeFromSchema = true;
  static returnChildLogic(args) {
    let childLogic = super.returnChildLogic(args);
    childLogic.newLeaf({
      name: "atMostOnePossibleNumberOfGroups",
      componentType: "possibleNumberOfGroups",
      comparison: "atMost",
      number: 1,
      setAsBase: true
    });
    return childLogic;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.possibleNumberOfGroups = {
      returnDependencies: () => ({
        pngChild: {
          dependencyType: "child",
          childLogicName: "atMostOnePossibleNumberOfGroups",
          variableNames: ["numbers"]
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.pngChild.length === 0) {
          return { setValue: { possibleNumberOfGroups: [1] } };
        }
        return {
          setValue: {
            possibleNumberOfGroups: dependencyValues.pngChild[0].stateValues.numbers
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class ConsiderAsResponses extends BaseComponent {
  static componentType = "considerAsResponses";
  static rendererType = void 0;
  static componentDocs = {
    summary: "Marks its children as response components for an enclosing answer"
  };
  static inSchemaOnlyInheritAs = [];
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.childrenWithNValues = {
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["numValues"],
          variablesOptional: true
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { childrenWithNValues: dependencyValues.children }
      })
    };
    stateVariableDefinitions.childrenAsResponses = {
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["value", "values", "componentType"],
          variablesOptional: true
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { childrenAsResponses: dependencyValues.children }
      })
    };
    return stateVariableDefinitions;
  }
}
var dist = {};
var formats = {};
var hasRequiredFormats;
function requireFormats() {
  if (hasRequiredFormats) return formats;
  hasRequiredFormats = 1;
  Object.defineProperty(formats, "__esModule", {
    value: true
  });
  formats.FORMAT_PLAIN = formats.FORMAT_HTML = formats.FORMATS = void 0;
  var FORMAT_HTML = "html";
  formats.FORMAT_HTML = FORMAT_HTML;
  var FORMAT_PLAIN = "plain";
  formats.FORMAT_PLAIN = FORMAT_PLAIN;
  var FORMATS = [FORMAT_HTML, FORMAT_PLAIN];
  formats.FORMATS = FORMATS;
  return formats;
}
var units = {};
var hasRequiredUnits;
function requireUnits() {
  if (hasRequiredUnits) return units;
  hasRequiredUnits = 1;
  Object.defineProperty(units, "__esModule", {
    value: true
  });
  units.UNIT_WORDS = units.UNIT_WORD = units.UNIT_SENTENCES = units.UNIT_SENTENCE = units.UNIT_PARAGRAPHS = units.UNIT_PARAGRAPH = units.UNITS = void 0;
  var UNIT_WORDS = "words";
  units.UNIT_WORDS = UNIT_WORDS;
  var UNIT_WORD = "word";
  units.UNIT_WORD = UNIT_WORD;
  var UNIT_SENTENCES = "sentences";
  units.UNIT_SENTENCES = UNIT_SENTENCES;
  var UNIT_SENTENCE = "sentence";
  units.UNIT_SENTENCE = UNIT_SENTENCE;
  var UNIT_PARAGRAPHS = "paragraphs";
  units.UNIT_PARAGRAPHS = UNIT_PARAGRAPHS;
  var UNIT_PARAGRAPH = "paragraph";
  units.UNIT_PARAGRAPH = UNIT_PARAGRAPH;
  var UNITS = [UNIT_WORDS, UNIT_WORD, UNIT_SENTENCES, UNIT_SENTENCE, UNIT_PARAGRAPHS, UNIT_PARAGRAPH];
  units.UNITS = UNITS;
  return units;
}
var words = {};
var hasRequiredWords;
function requireWords() {
  if (hasRequiredWords) return words;
  hasRequiredWords = 1;
  Object.defineProperty(words, "__esModule", {
    value: true
  });
  words.WORDS = void 0;
  var WORDS = ["ad", "adipisicing", "aliqua", "aliquip", "amet", "anim", "aute", "cillum", "commodo", "consectetur", "consequat", "culpa", "cupidatat", "deserunt", "do", "dolor", "dolore", "duis", "ea", "eiusmod", "elit", "enim", "esse", "est", "et", "eu", "ex", "excepteur", "exercitation", "fugiat", "id", "in", "incididunt", "ipsum", "irure", "labore", "laboris", "laborum", "Lorem", "magna", "minim", "mollit", "nisi", "non", "nostrud", "nulla", "occaecat", "officia", "pariatur", "proident", "qui", "quis", "reprehenderit", "sint", "sit", "sunt", "tempor", "ullamco", "ut", "velit", "veniam", "voluptate"];
  words.WORDS = WORDS;
  return words;
}
var LoremIpsum = {};
var lineEndings = {};
var hasRequiredLineEndings;
function requireLineEndings() {
  if (hasRequiredLineEndings) return lineEndings;
  hasRequiredLineEndings = 1;
  Object.defineProperty(lineEndings, "__esModule", {
    value: true
  });
  lineEndings.LINE_ENDINGS = void 0;
  var LINE_ENDINGS = {
    POSIX: "\n",
    WIN32: "\r\n"
  };
  lineEndings.LINE_ENDINGS = LINE_ENDINGS;
  return lineEndings;
}
var generator = {};
var util = {};
var capitalize = {};
var hasRequiredCapitalize;
function requireCapitalize() {
  if (hasRequiredCapitalize) return capitalize;
  hasRequiredCapitalize = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var capitalize2 = function capitalize3(str) {
      var trimmed = str.trim();
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    };
    var _default = capitalize2;
    exports["default"] = _default;
  })(capitalize);
  return capitalize;
}
var isNode = { exports: {} };
var hasRequiredIsNode;
function requireIsNode() {
  if (hasRequiredIsNode) return isNode.exports;
  hasRequiredIsNode = 1;
  (function(module, exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var isNode2 = function isNode3() {
      return !!module.exports;
    };
    var _default = isNode2;
    exports["default"] = _default;
  })(isNode, isNode.exports);
  return isNode.exports;
}
var isReactNative = {};
var hasRequiredIsReactNative;
function requireIsReactNative() {
  if (hasRequiredIsReactNative) return isReactNative;
  hasRequiredIsReactNative = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var isReactNative2 = function isReactNative3() {
      var isReactNativeResult = false;
      try {
        isReactNativeResult = navigator.product === "ReactNative";
      } catch (e3) {
        isReactNativeResult = false;
      }
      return isReactNativeResult;
    };
    var _default = isReactNative2;
    exports["default"] = _default;
  })(isReactNative);
  return isReactNative;
}
var isWindows = {};
var platforms = {};
var hasRequiredPlatforms;
function requirePlatforms() {
  if (hasRequiredPlatforms) return platforms;
  hasRequiredPlatforms = 1;
  Object.defineProperty(platforms, "__esModule", {
    value: true
  });
  platforms.SUPPORTED_PLATFORMS = void 0;
  var SUPPORTED_PLATFORMS = {
    DARWIN: "darwin",
    LINUX: "linux",
    WIN32: "win32"
  };
  platforms.SUPPORTED_PLATFORMS = SUPPORTED_PLATFORMS;
  return platforms;
}
var hasRequiredIsWindows;
function requireIsWindows() {
  if (hasRequiredIsWindows) return isWindows;
  hasRequiredIsWindows = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var _platforms = requirePlatforms();
    var isWindows2 = function isWindows3() {
      var isWindowsResult = false;
      try {
        isWindowsResult = process.platform === _platforms.SUPPORTED_PLATFORMS.WIN32;
      } catch (e3) {
        isWindowsResult = false;
      }
      return isWindowsResult;
    };
    var _default = isWindows2;
    exports["default"] = _default;
  })(isWindows);
  return isWindows;
}
var makeArrayOfLength = {};
var hasRequiredMakeArrayOfLength;
function requireMakeArrayOfLength() {
  if (hasRequiredMakeArrayOfLength) return makeArrayOfLength;
  hasRequiredMakeArrayOfLength = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var makeArrayOfLength2 = function makeArrayOfLength3() {
      var length = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 0;
      return Array.apply(null, Array(length)).map(function(item, index2) {
        return index2;
      });
    };
    var _default = makeArrayOfLength2;
    exports["default"] = _default;
  })(makeArrayOfLength);
  return makeArrayOfLength;
}
var makeArrayOfStrings = {};
var hasRequiredMakeArrayOfStrings;
function requireMakeArrayOfStrings() {
  if (hasRequiredMakeArrayOfStrings) return makeArrayOfStrings;
  hasRequiredMakeArrayOfStrings = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var _makeArrayOfLength = _interopRequireDefault(requireMakeArrayOfLength());
    function _interopRequireDefault(obj) {
      return obj && obj.__esModule ? obj : { "default": obj };
    }
    var makeArrayOfStrings2 = function makeArrayOfStrings3(length, makeString) {
      var arr = (0, _makeArrayOfLength["default"])(length);
      return arr.map(function() {
        return makeString();
      });
    };
    var _default = makeArrayOfStrings2;
    exports["default"] = _default;
  })(makeArrayOfStrings);
  return makeArrayOfStrings;
}
var hasRequiredUtil;
function requireUtil() {
  if (hasRequiredUtil) return util;
  hasRequiredUtil = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    Object.defineProperty(exports, "capitalize", {
      enumerable: true,
      get: function get() {
        return _capitalize["default"];
      }
    });
    Object.defineProperty(exports, "isNode", {
      enumerable: true,
      get: function get() {
        return _isNode["default"];
      }
    });
    Object.defineProperty(exports, "isReactNative", {
      enumerable: true,
      get: function get() {
        return _isReactNative["default"];
      }
    });
    Object.defineProperty(exports, "isWindows", {
      enumerable: true,
      get: function get() {
        return _isWindows["default"];
      }
    });
    Object.defineProperty(exports, "makeArrayOfLength", {
      enumerable: true,
      get: function get() {
        return _makeArrayOfLength["default"];
      }
    });
    Object.defineProperty(exports, "makeArrayOfStrings", {
      enumerable: true,
      get: function get() {
        return _makeArrayOfStrings["default"];
      }
    });
    var _capitalize = _interopRequireDefault(requireCapitalize());
    var _isNode = _interopRequireDefault(requireIsNode());
    var _isReactNative = _interopRequireDefault(requireIsReactNative());
    var _isWindows = _interopRequireDefault(requireIsWindows());
    var _makeArrayOfLength = _interopRequireDefault(requireMakeArrayOfLength());
    var _makeArrayOfStrings = _interopRequireDefault(requireMakeArrayOfStrings());
    function _interopRequireDefault(obj) {
      return obj && obj.__esModule ? obj : { "default": obj };
    }
  })(util);
  return util;
}
var hasRequiredGenerator;
function requireGenerator() {
  if (hasRequiredGenerator) return generator;
  hasRequiredGenerator = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var _words = requireWords();
    var _util = requireUtil();
    function _classCallCheck(instance, Constructor) {
      if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
      }
    }
    function _defineProperties(target, props) {
      for (var i2 = 0; i2 < props.length; i2++) {
        var descriptor = props[i2];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
      }
    }
    function _createClass(Constructor, protoProps, staticProps) {
      if (protoProps) _defineProperties(Constructor.prototype, protoProps);
      Object.defineProperty(Constructor, "prototype", { writable: false });
      return Constructor;
    }
    function _defineProperty(obj, key, value) {
      if (key in obj) {
        Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
      } else {
        obj[key] = value;
      }
      return obj;
    }
    var Generator = /* @__PURE__ */ (function() {
      function Generator2() {
        var _ref = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, _ref$sentencesPerPara = _ref.sentencesPerParagraph, sentencesPerParagraph = _ref$sentencesPerPara === void 0 ? {
          max: 7,
          min: 3
        } : _ref$sentencesPerPara, _ref$wordsPerSentence = _ref.wordsPerSentence, wordsPerSentence = _ref$wordsPerSentence === void 0 ? {
          max: 15,
          min: 5
        } : _ref$wordsPerSentence, random = _ref.random;
        _ref.seed;
        var _ref$words = _ref.words, words2 = _ref$words === void 0 ? _words.WORDS : _ref$words;
        _classCallCheck(this, Generator2);
        _defineProperty(this, "sentencesPerParagraph", void 0);
        _defineProperty(this, "wordsPerSentence", void 0);
        _defineProperty(this, "random", void 0);
        _defineProperty(this, "words", void 0);
        if (sentencesPerParagraph.min > sentencesPerParagraph.max) {
          throw new Error("Minimum number of sentences per paragraph (".concat(sentencesPerParagraph.min, ") cannot exceed maximum (").concat(sentencesPerParagraph.max, ")."));
        }
        if (wordsPerSentence.min > wordsPerSentence.max) {
          throw new Error("Minimum number of words per sentence (".concat(wordsPerSentence.min, ") cannot exceed maximum (").concat(wordsPerSentence.max, ")."));
        }
        this.sentencesPerParagraph = sentencesPerParagraph;
        this.words = words2;
        this.wordsPerSentence = wordsPerSentence;
        this.random = random || Math.random;
      }
      _createClass(Generator2, [{
        key: "generateRandomInteger",
        value: function generateRandomInteger(min2, max2) {
          return Math.floor(this.random() * (max2 - min2 + 1) + min2);
        }
      }, {
        key: "generateRandomWords",
        value: function generateRandomWords(num) {
          var _this = this;
          var _this$wordsPerSentenc = this.wordsPerSentence, min2 = _this$wordsPerSentenc.min, max2 = _this$wordsPerSentenc.max;
          var length = num || this.generateRandomInteger(min2, max2);
          return (0, _util.makeArrayOfLength)(length).reduce(function(accumulator, index2) {
            return "".concat(_this.pluckRandomWord(), " ").concat(accumulator);
          }, "").trim();
        }
      }, {
        key: "generateRandomSentence",
        value: function generateRandomSentence(num) {
          return "".concat((0, _util.capitalize)(this.generateRandomWords(num)), ".");
        }
      }, {
        key: "generateRandomParagraph",
        value: function generateRandomParagraph(num) {
          var _this2 = this;
          var _this$sentencesPerPar = this.sentencesPerParagraph, min2 = _this$sentencesPerPar.min, max2 = _this$sentencesPerPar.max;
          var length = num || this.generateRandomInteger(min2, max2);
          return (0, _util.makeArrayOfLength)(length).reduce(function(accumulator, index2) {
            return "".concat(_this2.generateRandomSentence(), " ").concat(accumulator);
          }, "").trim();
        }
      }, {
        key: "pluckRandomWord",
        value: function pluckRandomWord() {
          var min2 = 0;
          var max2 = this.words.length - 1;
          var index2 = this.generateRandomInteger(min2, max2);
          return this.words[index2];
        }
      }]);
      return Generator2;
    })();
    var _default = Generator;
    exports["default"] = _default;
  })(generator);
  return generator;
}
var hasRequiredLoremIpsum;
function requireLoremIpsum() {
  if (hasRequiredLoremIpsum) return LoremIpsum;
  hasRequiredLoremIpsum = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    exports["default"] = void 0;
    var _formats = requireFormats();
    var _lineEndings = requireLineEndings();
    var _generator = _interopRequireDefault(requireGenerator());
    var _util = requireUtil();
    function _interopRequireDefault(obj) {
      return obj && obj.__esModule ? obj : { "default": obj };
    }
    function _classCallCheck(instance, Constructor) {
      if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
      }
    }
    function _defineProperties(target, props) {
      for (var i2 = 0; i2 < props.length; i2++) {
        var descriptor = props[i2];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
      }
    }
    function _createClass(Constructor, protoProps, staticProps) {
      if (protoProps) _defineProperties(Constructor.prototype, protoProps);
      Object.defineProperty(Constructor, "prototype", { writable: false });
      return Constructor;
    }
    function _defineProperty(obj, key, value) {
      if (key in obj) {
        Object.defineProperty(obj, key, { value, enumerable: true, configurable: true, writable: true });
      } else {
        obj[key] = value;
      }
      return obj;
    }
    var LoremIpsum2 = /* @__PURE__ */ (function() {
      function LoremIpsum3() {
        var options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        var format2 = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : _formats.FORMAT_PLAIN;
        var suffix = arguments.length > 2 ? arguments[2] : void 0;
        _classCallCheck(this, LoremIpsum3);
        this.format = format2;
        this.suffix = suffix;
        _defineProperty(this, "generator", void 0);
        if (_formats.FORMATS.indexOf(format2.toLowerCase()) === -1) {
          throw new Error("".concat(format2, " is an invalid format. Please use ").concat(_formats.FORMATS.join(" or "), "."));
        }
        this.generator = new _generator["default"](options);
      }
      _createClass(LoremIpsum3, [{
        key: "getLineEnding",
        value: function getLineEnding() {
          if (this.suffix) {
            return this.suffix;
          }
          if (!(0, _util.isReactNative)() && (0, _util.isNode)() && (0, _util.isWindows)()) {
            return _lineEndings.LINE_ENDINGS.WIN32;
          }
          return _lineEndings.LINE_ENDINGS.POSIX;
        }
      }, {
        key: "formatString",
        value: function formatString(str) {
          if (this.format === _formats.FORMAT_HTML) {
            return "<p>".concat(str, "</p>");
          }
          return str;
        }
      }, {
        key: "formatStrings",
        value: function formatStrings(strings) {
          var _this = this;
          return strings.map(function(str) {
            return _this.formatString(str);
          });
        }
      }, {
        key: "generateWords",
        value: function generateWords(num) {
          return this.formatString(this.generator.generateRandomWords(num));
        }
      }, {
        key: "generateSentences",
        value: function generateSentences(num) {
          return this.formatString(this.generator.generateRandomParagraph(num));
        }
      }, {
        key: "generateParagraphs",
        value: function generateParagraphs(num) {
          var makeString = this.generator.generateRandomParagraph.bind(this.generator);
          return this.formatStrings((0, _util.makeArrayOfStrings)(num, makeString)).join(this.getLineEnding());
        }
      }]);
      return LoremIpsum3;
    })();
    var _default = LoremIpsum2;
    exports["default"] = _default;
  })(LoremIpsum);
  return LoremIpsum;
}
var hasRequiredDist;
function requireDist() {
  if (hasRequiredDist) return dist;
  hasRequiredDist = 1;
  (function(exports) {
    Object.defineProperty(exports, "__esModule", {
      value: true
    });
    Object.defineProperty(exports, "LoremIpsum", {
      enumerable: true,
      get: function get() {
        return _LoremIpsum["default"];
      }
    });
    exports.loremIpsum = void 0;
    var _formats = requireFormats();
    var _units = requireUnits();
    var _words = requireWords();
    var _LoremIpsum = _interopRequireDefault(requireLoremIpsum());
    function _interopRequireDefault(obj) {
      return obj && obj.__esModule ? obj : { "default": obj };
    }
    var loremIpsum = function loremIpsum2() {
      var _ref = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, _ref$count = _ref.count, count = _ref$count === void 0 ? 1 : _ref$count, _ref$format = _ref.format, format2 = _ref$format === void 0 ? _formats.FORMAT_PLAIN : _ref$format, _ref$paragraphLowerBo = _ref.paragraphLowerBound, paragraphLowerBound = _ref$paragraphLowerBo === void 0 ? 3 : _ref$paragraphLowerBo, _ref$paragraphUpperBo = _ref.paragraphUpperBound, paragraphUpperBound = _ref$paragraphUpperBo === void 0 ? 7 : _ref$paragraphUpperBo, random = _ref.random, _ref$sentenceLowerBou = _ref.sentenceLowerBound, sentenceLowerBound = _ref$sentenceLowerBou === void 0 ? 5 : _ref$sentenceLowerBou, _ref$sentenceUpperBou = _ref.sentenceUpperBound, sentenceUpperBound = _ref$sentenceUpperBou === void 0 ? 15 : _ref$sentenceUpperBou, _ref$units = _ref.units, units2 = _ref$units === void 0 ? _units.UNIT_SENTENCES : _ref$units, _ref$words = _ref.words, words2 = _ref$words === void 0 ? _words.WORDS : _ref$words, _ref$suffix = _ref.suffix, suffix = _ref$suffix === void 0 ? "" : _ref$suffix;
      var options = {
        random,
        sentencesPerParagraph: {
          max: paragraphUpperBound,
          min: paragraphLowerBound
        },
        words: words2,
        wordsPerSentence: {
          max: sentenceUpperBound,
          min: sentenceLowerBound
        }
      };
      var lorem = new _LoremIpsum["default"](options, format2, suffix);
      switch (units2) {
        case _units.UNIT_PARAGRAPHS:
        case _units.UNIT_PARAGRAPH:
          return lorem.generateParagraphs(count);
        case _units.UNIT_SENTENCES:
        case _units.UNIT_SENTENCE:
          return lorem.generateSentences(count);
        case _units.UNIT_WORDS:
        case _units.UNIT_WORD:
          return lorem.generateWords(count);
        default:
          return "";
      }
    };
    exports.loremIpsum = loremIpsum;
  })(dist);
  return dist;
}
var distExports = requireDist();
class Lorem extends CompositeComponent {
  static componentType = "lorem";
  static componentDocs = {
    summary: "Generates lorem ipsum placeholder text"
  };
  static allowInSchemaAsComponent = ["text", "p"];
  static createsVariants = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.minSentencesPerParagraph = {
      description: "Minimum number of sentences per generated paragraph.",
      createComponentOfType: "number",
      createStateVariable: "minSentencesPerParagraph",
      defaultValue: 4,
      public: true
    };
    attributes.maxSentencesPerParagraph = {
      description: "Maximum number of sentences per generated paragraph.",
      createComponentOfType: "number",
      createStateVariable: "maxSentencesPerParagraph",
      defaultValue: 8,
      public: true
    };
    attributes.minWordsPerSentence = {
      description: "Minimum number of words per generated sentence.",
      createComponentOfType: "number",
      createStateVariable: "minWordsPerSentence",
      defaultValue: 4,
      public: true
    };
    attributes.maxWordsPerSentence = {
      description: "Maximum number of words per generated sentence.",
      createComponentOfType: "number",
      createStateVariable: "maxWordsPerSentence",
      defaultValue: 8,
      public: true
    };
    attributes.generateWords = {
      createComponentOfType: "number",
      createStateVariable: "generateWords",
      defaultValue: null,
      description: "Number of words of lorem-ipsum to generate."
    };
    attributes.generateSentences = {
      createComponentOfType: "number",
      createStateVariable: "generateSentences",
      defaultValue: null,
      description: "Number of sentences of lorem-ipsum to generate."
    };
    attributes.generateParagraphs = {
      createComponentOfType: "number",
      createStateVariable: "generateParagraphs",
      defaultValue: null,
      description: "Number of paragraphs of lorem-ipsum to generate."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        minSentencesPerParagraph: {
          dependencyType: "stateVariable",
          variableName: "minSentencesPerParagraph"
        },
        maxSentencesPerParagraph: {
          dependencyType: "stateVariable",
          variableName: "maxSentencesPerParagraph"
        },
        minWordsPerSentence: {
          dependencyType: "stateVariable",
          variableName: "minWordsPerSentence"
        },
        maxWordsPerSentence: {
          dependencyType: "stateVariable",
          variableName: "maxWordsPerSentence"
        },
        generateWords: {
          dependencyType: "stateVariable",
          variableName: "generateWords"
        },
        generateSentences: {
          dependencyType: "stateVariable",
          variableName: "generateSentences"
        },
        generateParagraphs: {
          dependencyType: "stateVariable",
          variableName: "generateParagraphs"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    stateVariableDefinitions.isVariantComponent = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isVariantComponent: true } })
    };
    stateVariableDefinitions.generatedVariantInfo = {
      returnDependencies: ({
        sharedParameters,
        componentInfoObjects
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: {
            createdBy: componentIdx
          }
        };
        return {
          setValue: {
            generatedVariantInfo
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    workspace,
    nComponents
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let diagnostics = [];
    const lorem = new distExports.LoremIpsum({
      sentencesPerParagraph: {
        max: await component.stateValues.maxSentencesPerParagraph,
        min: await component.stateValues.minSentencesPerParagraph
      },
      wordsPerSentence: {
        max: await component.stateValues.maxWordsPerSentence,
        min: await component.stateValues.minWordsPerSentence
      },
      random: component.sharedParameters.variantRng
    });
    let replacements = [];
    if (await component.stateValues.generateParagraphs !== null) {
      let numParagraphs = await component.stateValues.generateParagraphs;
      if (Number.isInteger(numParagraphs) && numParagraphs > 0) {
        let paragraphs = lorem.generateParagraphs(numParagraphs).split("\n");
        replacements = paragraphs.map((x2) => ({
          type: "serialized",
          componentType: "p",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          state: {},
          children: [x2]
        }));
      }
    } else if (await component.stateValues.generateSentences !== null) {
      let numSentences = await component.stateValues.generateSentences;
      if (Number.isInteger(numSentences) && numSentences > 0) {
        let sentences = lorem.generateSentences(numSentences).split(". ");
        for (let sent of sentences.slice(0, sentences.length - 1)) {
          replacements.push({
            type: "serialized",
            componentType: "text",
            componentIdx: nComponents++,
            stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
            attributes: {},
            doenetAttributes: {},
            state: {},
            children: [sent + "."]
          });
          replacements.push(" ");
        }
        replacements.push({
          type: "serialized",
          componentType: "text",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          state: {},
          children: [sentences[sentences.length - 1]]
        });
      }
    } else if (await component.stateValues.generateWords !== null) {
      let numWords = await component.stateValues.generateWords;
      if (Number.isInteger(numWords) && numWords > 0) {
        let words2 = lorem.generateWords(numWords).split(" ").map((w2) => ({
          type: "serialized",
          componentType: "text",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          state: {},
          children: [w2]
        }));
        replacements.push(words2[0]);
        for (let w2 of words2.slice(1)) {
          replacements.push(" ");
          replacements.push(w2);
        }
      }
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    let replacementResults = await this.createSerializedReplacements({
      component,
      componentInfoObjects,
      nComponents,
      workspace
    });
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementInstruction = {
      changeType: "add",
      changeTopLevelReplacements: true,
      firstReplacementInd: 0,
      numberReplacementsToReplace: component.replacements.length,
      serializedReplacements: replacementResults.replacements
    };
    return {
      replacementChanges: [replacementInstruction],
      diagnostics,
      nComponents
    };
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents
    });
  }
}
function returnStandardTriggeringAttributes(triggerActionOnChange) {
  return {
    triggerWhen: {
      createComponentOfType: "boolean",
      createStateVariable: "triggerWhen",
      defaultValue: false,
      triggerActionOnChange,
      groupName: "triggering",
      description: "Boolean expression that fires the action whenever it transitions to true."
    },
    triggerWith: {
      createReferences: true,
      groupName: "triggering",
      description: "References to components whose value changes should fire this action."
    },
    triggerWhenObjectsClicked: {
      createReferences: true,
      groupName: "triggering",
      description: "References to components whose click events should fire this action."
    },
    triggerWhenObjectsFocused: {
      createReferences: true,
      groupName: "triggering",
      description: "References to components whose focus events should fire this action."
    }
  };
}
function addStandardTriggeringStateVariableDefinitions(stateVariableDefinitions, triggeredAction) {
  stateVariableDefinitions.insideTriggerSet = {
    returnDependencies: () => ({
      parentTriggerSet: {
        dependencyType: "parentStateVariable",
        parentComponentType: "triggerSet",
        variableName: "updateValueAndActionsToTrigger"
      }
    }),
    definition({ dependencyValues }) {
      return {
        setValue: {
          insideTriggerSet: dependencyValues.parentTriggerSet !== null
        }
      };
    }
  };
  stateVariableDefinitions.triggerWith = {
    returnDependencies: () => ({
      triggerWith: {
        dependencyType: "attributeRefResolutions",
        attributeName: "triggerWith"
      },
      triggerWhenObjectsClicked: {
        dependencyType: "attributeRefResolutions",
        attributeName: "triggerWhenObjectsClicked"
      },
      triggerWhenObjectsFocused: {
        dependencyType: "attributeRefResolutions",
        attributeName: "triggerWhenObjectsFocused"
      },
      triggerWhen: {
        dependencyType: "attributeComponent",
        attributeName: "triggerWhen"
      },
      insideTriggerSet: {
        dependencyType: "stateVariable",
        variableName: "insideTriggerSet"
      }
    }),
    definition({ dependencyValues }) {
      if (dependencyValues.triggerWhen || dependencyValues.insideTriggerSet) {
        return { setValue: { triggerWith: null } };
      } else {
        let triggerWith = [];
        if (dependencyValues.triggerWith !== null) {
          for (let refResolution of dependencyValues.triggerWith) {
            if (refResolution.unresolvedPath === null) {
              triggerWith.push({
                target: refResolution.componentIdx
              });
            }
          }
        }
        if (dependencyValues.triggerWhenObjectsClicked !== null) {
          for (let refResolution of dependencyValues.triggerWhenObjectsClicked) {
            if (refResolution.unresolvedPath === null) {
              triggerWith.push({
                target: refResolution.componentIdx,
                triggeringAction: "click"
              });
            }
          }
        }
        if (dependencyValues.triggerWhenObjectsFocused !== null) {
          for (let refResolution of dependencyValues.triggerWhenObjectsFocused) {
            if (refResolution.unresolvedPath === null) {
              triggerWith.push({
                target: refResolution.componentIdx,
                triggeringAction: "focus"
              });
            }
          }
        }
        if (triggerWith.length === 0) {
          triggerWith = null;
        }
        return { setValue: { triggerWith } };
      }
    }
  };
  stateVariableDefinitions.triggerWithTargetIds = {
    chainActionOnActionOfStateVariableTargets: {
      triggeredAction
    },
    returnDependencies: () => ({
      triggerWith: {
        dependencyType: "stateVariable",
        variableName: "triggerWith"
      }
    }),
    definition({ dependencyValues }) {
      let triggerWithTargetIds = [];
      if (dependencyValues.triggerWith) {
        for (let targetObj of dependencyValues.triggerWith) {
          let id = targetObj.target;
          if (targetObj.triggeringAction) {
            id += "|" + targetObj.triggeringAction;
          }
          if (!triggerWithTargetIds.includes(id)) {
            triggerWithTargetIds.push(id);
          }
        }
      }
      return { setValue: { triggerWithTargetIds } };
    },
    markStale() {
      return { updateActionChaining: true };
    }
  };
  let originalHiddenReturnDependencies = stateVariableDefinitions.hidden.returnDependencies;
  let originalHiddenDefinition = stateVariableDefinitions.hidden.definition;
  stateVariableDefinitions.hidden.returnDependencies = function(args) {
    let dependencies = originalHiddenReturnDependencies(args);
    dependencies.triggerWhen = {
      dependencyType: "attributeComponent",
      attributeName: "triggerWhen"
    };
    dependencies.triggerWith = {
      dependencyType: "stateVariable",
      variableName: "triggerWith"
    };
    dependencies.insideTriggerSet = {
      dependencyType: "stateVariable",
      variableName: "insideTriggerSet"
    };
    return dependencies;
  };
  stateVariableDefinitions.hidden.definition = function(args) {
    if (args.dependencyValues.triggerWhen || args.dependencyValues.triggerWith || args.dependencyValues.insideTriggerSet) {
      return { setValue: { hidden: true } };
    } else {
      return originalHiddenDefinition(args);
    }
  };
}
class UpdateValue extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateValue: this.updateValue.bind(this),
      updateValueIfTriggerNewlyTrue: this.updateValueIfTriggerNewlyTrue.bind(this),
      moveButton: this.moveButton.bind(this)
    });
  }
  static componentType = "updateValue";
  static componentDocs = {
    summary: "Updates one or more state variables on a target component"
  };
  static rendererType = "button";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.target = {
      createReferences: true,
      description: "Reference to the state variable that will be updated."
    };
    attributes.type = {
      createPrimitiveOfType: "string",
      createStateVariable: "type",
      defaultPrimitiveValue: "math",
      toLowerCase: true,
      validValues: [
        {
          value: "math",
          description: "Treat the new value as a math expression."
        },
        {
          value: "number",
          description: "Treat the new value as a number."
        },
        {
          value: "boolean",
          description: "Treat the new value as a boolean."
        },
        {
          value: "text",
          description: "Treat the new value as text."
        }
      ],
      description: "Component type used to interpret the new value."
    };
    attributes.newValue = {
      createComponentOfType: "_componentWithSelectableType",
      description: "New value to assign to the target."
    };
    attributes.draggable = {
      description: "Whether the update button can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    Object.assign(attributes, returnLabelAttributes());
    let triggerAttributes = returnStandardTriggeringAttributes(
      "updateValueIfTriggerNewlyTrue"
    );
    Object.assign(attributes, triggerAttributes);
    attributes.simplify = {
      description: "Level of simplification applied to the new value.",
      createComponentOfType: "text",
      createStateVariable: "simplify",
      defaultValue: "none",
      public: true,
      toLowerCase: true,
      valueForTrue: "full",
      valueForFalse: "none",
      validValues: [
        {
          value: "none",
          description: "No simplification is applied."
        },
        {
          value: "full",
          description: "Fully simplify the new value."
        },
        {
          value: "numbers",
          description: "Simplify numeric subexpressions only, leaving symbolic structure intact."
        },
        {
          value: "numbersPreserveOrder",
          description: "Like `numbers`, but does not reorder commutative operands."
        },
        {
          value: "normalizeOrder",
          description: "Reorder commutative operands into a canonical form without simplifying values."
        }
      ]
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    addStandardTriggeringStateVariableDefinitions(
      stateVariableDefinitions,
      "updateValue"
    );
    let labelDefinitions = returnLabelStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, labelDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.clickAction = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { clickAction: "updateValue" } })
    };
    stateVariableDefinitions.targetComponentIdx = {
      additionalStateVariablesDefined: ["unresolvedPath"],
      returnDependencies: () => ({
        target: {
          dependencyType: "attributeRefResolutions",
          attributeName: "target"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.target?.length === 1) {
          const target = dependencyValues.target[0];
          return {
            setValue: {
              targetComponentIdx: target.componentIdx,
              unresolvedPath: target.unresolvedPath
            }
          };
        } else {
          return {
            setValue: {
              targetComponentIdx: null,
              unresolvedPath: null
            }
          };
        }
      }
    };
    stateVariableDefinitions.targetComponent = {
      stateVariablesDeterminingDependencies: ["targetComponentIdx"],
      returnDependencies({ stateValues }) {
        if (stateValues.targetComponentIdx != null) {
          return {
            targetComponent: {
              dependencyType: "componentIdentity",
              componentIdx: stateValues.targetComponentIdx
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        let targetComponent = null;
        if (dependencyValues.targetComponent) {
          targetComponent = dependencyValues.targetComponent;
        }
        return {
          setValue: { targetComponent }
        };
      }
    };
    stateVariableDefinitions.targetIdentities = {
      stateVariablesDeterminingDependencies: ["targetComponent"],
      returnDependencies: function({
        stateValues,
        componentInfoObjects
      }) {
        let dependencies = {};
        if (stateValues.targetComponent !== null) {
          if (componentInfoObjects.isCompositeComponent({
            componentType: stateValues.targetComponent.componentType,
            includeNonStandard: false
          })) {
            dependencies.targets = {
              dependencyType: "replacement",
              compositeIdx: stateValues.targetComponent.componentIdx,
              recursive: true
            };
          } else {
            dependencies.targets = {
              dependencyType: "stateVariable",
              variableName: "targetComponent"
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let targetIdentities = null;
        if (dependencyValues.targets) {
          targetIdentities = dependencyValues.targets;
          if (!Array.isArray(targetIdentities)) {
            targetIdentities = [targetIdentities];
          }
        }
        let diagnostics = [];
        if (targetIdentities === null || targetIdentities.length === 0) {
          diagnostics.push({
            message: "Invalid target for `<updateValue>`: cannot find target.",
            type: "warning"
          });
        }
        return {
          setValue: { targetIdentities },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.targets = {
      stateVariablesDeterminingDependencies: [
        "targetIdentities",
        "unresolvedPath"
      ],
      returnDependencies: function({ stateValues }) {
        let dependencies = {
          targetIdentities: {
            dependencyType: "stateVariable",
            variableName: "targetIdentities"
          },
          unresolvedPath: {
            dependencyType: "stateVariable",
            variableName: "unresolvedPath"
          }
        };
        if (stateValues.targetIdentities !== null) {
          for (let [
            ind,
            target
          ] of stateValues.targetIdentities.entries()) {
            let thisTarget;
            if (stateValues.unresolvedPath) {
              let propIndex = stateValues.propIndex;
              if (propIndex) {
                propIndex = [...propIndex];
              }
              thisTarget = {
                dependencyType: "stateVariableFromUnresolvedPath",
                componentIdx: target.componentIdx,
                unresolvedPath: stateValues.unresolvedPath,
                returnAsComponentObject: true,
                variablesOptional: true,
                caseInsensitiveVariableMatch: true,
                publicStateVariablesOnly: true,
                useMappedVariableNames: true
              };
            } else {
              thisTarget = {
                dependencyType: "stateVariable",
                componentIdx: target.componentIdx,
                variableName: "value",
                returnAsComponentObject: true,
                variablesOptional: true
              };
            }
            dependencies["target" + ind] = thisTarget;
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let targets = null;
        let diagnostics = [];
        if (dependencyValues.targetIdentities !== null) {
          targets = [];
          for (let ind in dependencyValues.targetIdentities) {
            let target = dependencyValues["target" + ind];
            if (target == null) {
              let message = "Invalid target for `<updateValue>`: cannot find target.";
              diagnostics.push({
                message,
                type: "warning"
              });
              continue;
            }
            targets.push(target);
            if (Object.keys(target.stateValues)[0] === void 0) {
              if (dependencyValues.unresolvedPath) {
                let prop = dependencyValues.unresolvedPath[0].name;
                if (dependencyValues.unresolvedPath[0].index.length > 0) {
                  for (const idx of dependencyValues.unresolvedPath[0].index) {
                    prop += `[idx]`;
                  }
                }
                let message = `Invalid target for \`<updateValue>\`: cannot find a state variable named "${prop}" on a \`<${target.componentType}>\`.`;
                diagnostics.push({
                  message,
                  type: "warning"
                });
              } else {
                let message = `Invalid target for \`<updateValue>\`: cannot find a state variable named "value" on a \`<${target.componentType}>\`.`;
                diagnostics.push({
                  message,
                  type: "warning"
                });
              }
            }
          }
        }
        return { setValue: { targets }, sendDiagnostics: diagnostics };
      }
    };
    stateVariableDefinitions.newValue = {
      returnDependencies: () => ({
        newValueAttr: {
          dependencyType: "attributeComponent",
          attributeName: "newValue",
          variableNames: ["value"]
        },
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        simplify: {
          dependencyType: "stateVariable",
          variableName: "simplify"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.newValueAttr === null) {
          return {
            setValue: {
              newValue: null
            }
          };
        }
        let newValue = dependencyValues.newValueAttr.stateValues.value;
        if (dependencyValues.type === "math") {
          newValue = normalizeMathExpression({
            value: newValue,
            simplify: dependencyValues.simplify
          });
        }
        return {
          setValue: { newValue }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async updateValue({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let targets = await this.stateValues.targets;
    let newValue = await this.stateValues.newValue;
    if (targets === null || newValue === null) {
      return;
    }
    let updateInstructions = [];
    for (let target of targets) {
      let stateVariable = Object.keys(target.stateValues)[0];
      if (stateVariable === void 0) {
        continue;
      }
      updateInstructions.push({
        updateType: "updateValue",
        componentIdx: target.componentIdx,
        stateVariable,
        value: newValue
      });
    }
    await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate: true,
      event: {
        verb: "selected",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          response: newValue,
          responseText: newValue.toString()
        }
      }
    });
    return await this.coreFunctions.triggerChainedActions({
      componentIdx: this.componentIdx,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async updateValueIfTriggerNewlyTrue({
    stateValues,
    previousValues,
    actionId
  }) {
    if (await stateValues.triggerWhen && previousValues.triggerWhen === false && !await this.stateValues.insideTriggerSet) {
      return await this.updateValue({
        actionId,
        skipRendererUpdate: true
      });
    }
  }
  async moveButton({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
}
class CallAction extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      callAction: this.callAction.bind(this),
      callActionIfTriggerNewlyTrue: this.callActionIfTriggerNewlyTrue.bind(this),
      moveButton: this.moveButton.bind(this)
    });
  }
  static componentType = "callAction";
  static componentDocs = {
    summary: "Triggers an action when clicked or in response to a specified user interaction"
  };
  static rendererType = "button";
  // The children of a `<callAction>` are kept serialized (see
  // `keepChildrenSerialized`) and passed to the invoked action — e.g. as the
  // components to insert for an `addChildren` action — rather than being matched
  // to child groups. Since those children can be any component, declare `_base`
  // as an additional schema child so the schema accepts arbitrary children.
  static additionalSchemaChildren = ["_base"];
  static keepChildrenSerialized({
    serializedComponent,
    componentInfoObjects
  }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      let keepSerializedInds = [];
      for (let [ind, child] of serializedComponent.children.entries()) {
        if (!componentInfoObjects.componentIsSpecifiedType(
          child,
          "label"
        )) {
          keepSerializedInds.push(ind);
        }
      }
      return keepSerializedInds;
    }
  }
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.target = {
      createReferences: true,
      description: "Reference to the component whose action will be invoked."
    };
    attributes.actionName = {
      createComponentOfType: "text",
      createStateVariable: "actionName",
      defaultValue: null,
      public: true,
      description: "Name of the action to invoke on the target component."
    };
    attributes.draggable = {
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true,
      description: "Whether the action button can be dragged on a graph."
    };
    Object.assign(attributes, returnLabelAttributes());
    Object.assign(attributes, returnAnchorAttributes());
    let triggerAttributes = returnStandardTriggeringAttributes(
      "callActionIfTriggerNewlyTrue"
    );
    Object.assign(attributes, triggerAttributes);
    attributes.numbers = {
      createComponentOfType: "numberList",
      description: "Numeric arguments passed to the invoked action."
    };
    attributes.number = {
      createComponentOfType: "number",
      description: "Numeric argument passed to the invoked action."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    addStandardTriggeringStateVariableDefinitions(
      stateVariableDefinitions,
      "callAction"
    );
    let labelDefinitions = returnLabelStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, labelDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.clickAction = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { clickAction: "callAction" } })
    };
    stateVariableDefinitions.targetComponentIdx = {
      additionalStateVariablesDefined: ["unresolvedPath"],
      returnDependencies: () => ({
        target: {
          dependencyType: "attributeRefResolutions",
          attributeName: "target"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.target?.length === 1) {
          const target = dependencyValues.target[0];
          if (target.unresolvedPath == null) {
            return {
              setValue: {
                targetComponentIdx: target.componentIdx,
                unresolvedPath: target.unresolvedPath
              }
            };
          }
        }
        return {
          setValue: {
            targetComponentIdx: null,
            unresolvedPath: null
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async callAction({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let targetIdx = await this.stateValues.targetComponentIdx;
    let actionName = await this.stateValues.actionName;
    if (targetIdx != null && actionName != null) {
      let args = { sourceInformation, skipRendererUpdate: true };
      if (this.serializedChildren.length > 0) {
        args.serializedComponents = deepClone(this.serializedChildren);
      }
      if (this.attributes.number) {
        args.number = await this.attributes.number.component.stateValues.value;
      }
      if (this.attributes.numbers) {
        args.numbers = await this.attributes.numbers.component.stateValues.numbers;
      }
      if (actionId) {
        args.actionId = actionId;
      }
      await this.coreFunctions.performAction({
        componentIdx: targetIdx,
        actionName,
        args,
        event: {
          verb: "selected",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          }
        },
        caseInsensitiveMatch: true
      });
      await this.coreFunctions.triggerChainedActions({
        componentIdx: this.componentIdx,
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    }
  }
  async callActionIfTriggerNewlyTrue({
    stateValues,
    previousValues,
    actionId
  }) {
    if (stateValues.triggerWhen && previousValues.triggerWhen === false && !await this.stateValues.insideTriggerSet) {
      return await this.callAction({ actionId });
    }
  }
  async moveButton({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
}
class triggerSet extends InlineComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      triggerActions: this.triggerActions.bind(this),
      triggerActionsIfTriggerNewlyTrue: this.triggerActionsIfTriggerNewlyTrue.bind(this),
      moveButton: this.moveButton.bind(this)
    });
  }
  static componentType = "triggerSet";
  static componentDocs = {
    summary: "Groups a set of `<updateValue>` or `<callAction>` components which share a single trigger condition"
  };
  static rendererType = "button";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.draggable = {
      description: "Whether the trigger set's button can be dragged on a graph.",
      createComponentOfType: "boolean",
      createStateVariable: "draggable",
      defaultValue: true,
      public: true,
      forRenderer: true
    };
    Object.assign(attributes, returnAnchorAttributes());
    Object.assign(attributes, returnLabelAttributes());
    let triggerAttributes = returnStandardTriggeringAttributes(
      "triggerActionsIfTriggerNewlyTrue"
    );
    Object.assign(attributes, triggerAttributes);
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "updateValuesCallActions",
        componentTypes: ["updateValue", "callAction"]
      },
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    addStandardTriggeringStateVariableDefinitions(
      stateVariableDefinitions,
      "triggerActions"
    );
    let labelDefinitions = returnLabelStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, labelDefinitions);
    let anchorDefinition = returnAnchorStateVariableDefinition();
    Object.assign(stateVariableDefinitions, anchorDefinition);
    stateVariableDefinitions.clickAction = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { clickAction: "triggerActions" } })
    };
    stateVariableDefinitions.updateValueAndActionsToTrigger = {
      returnDependencies: () => ({
        updateValueAndCallActionChildren: {
          dependencyType: "child",
          childGroups: ["updateValuesCallActions"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            updateValueAndActionsToTrigger: dependencyValues.updateValueAndCallActionChildren
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  async triggerActions({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    for (let child of await this.stateValues.updateValueAndActionsToTrigger) {
      if (this.componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: child.componentType,
        baseComponentType: "updateValue"
      })) {
        await this.coreFunctions.performAction({
          componentIdx: child.componentIdx,
          actionName: "updateValue",
          args: {
            actionId,
            sourceInformation,
            skipRendererUpdate: true
          }
        });
      } else if (this.componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: child.componentType,
        baseComponentType: "callAction"
      })) {
        await this.coreFunctions.performAction({
          componentIdx: child.componentIdx,
          actionName: "callAction",
          args: {
            actionId,
            sourceInformation,
            skipRendererUpdate: true
          }
        });
      }
    }
    return await this.coreFunctions.triggerChainedActions({
      componentIdx: this.componentIdx,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
  async triggerActionsIfTriggerNewlyTrue({
    stateValues,
    previousValues,
    actionId
  }) {
    if (stateValues.triggerWhen && previousValues.triggerWhen === false) {
      return await this.triggerActions({
        actionId,
        skipRendererUpdate: true
      });
    }
  }
  async moveButton({
    x: x2,
    y: y2,
    z: z2,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    return await moveGraphicalObjectWithAnchorAction({
      x: x2,
      y: y2,
      z: z2,
      transient,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      componentIdx: this.componentIdx,
      componentType: this.componentType,
      coreFunctions: this.coreFunctions
    });
  }
}
class FunctionIterates extends InlineComponent {
  static componentType = "functionIterates";
  static componentDocs = {
    summary: "List of values produced by iterating a function"
  };
  static rendererType = void 0;
  static variableForIndexAsProp = "iterates";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.numIterates = {
      description: "Number of iterations to compute.",
      createComponentOfType: "integer",
      createStateVariable: "numIterates",
      defaultValue: 0,
      public: true
    };
    attributes.forceSymbolic = {
      description: "Whether to force symbolic evaluation.",
      createComponentOfType: "boolean",
      createStateVariable: "forceSymbolic",
      defaultValue: false,
      public: true
    };
    attributes.forceNumeric = {
      description: "Whether to force numeric evaluation.",
      createComponentOfType: "boolean",
      createStateVariable: "forceNumeric",
      defaultValue: false,
      public: true
    };
    attributes.initialValue = {
      createComponentOfType: "math",
      createStateVariable: "initialValue",
      defaultValue: me$3.fromAst("＿"),
      description: "Starting value passed to the function on the first iteration."
    };
    attributes.function = {
      createComponentOfType: "function",
      description: "The function to iterate."
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions of each iterate.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        functionAttr: {
          dependencyType: "attributeComponent",
          attributeName: "function",
          variableNames: ["numInputs", "numOutputs"]
        }
      }),
      definition({ dependencyValues }) {
        if (!dependencyValues.functionAttr) {
          return { setValue: { numDimensions: 0 } };
        } else if (dependencyValues.functionAttr.stateValues.numInputs !== dependencyValues.functionAttr.stateValues.numOutputs) {
          let numInputs = dependencyValues.functionAttr.stateValues.numInputs;
          let numInputsPhrase = numInputs.toString() + (numInputs === 1 ? " input" : " inputs");
          let numOutputs = dependencyValues.functionAttr.stateValues.numOutputs;
          let numOutputsPhrase = numOutputs.toString() + (numOutputs === 1 ? " output" : " outputs");
          let warning = {
            type: "warning",
            message: `Function iterates are possible only if the number of inputs of the function is equal to the number of outputs. This function has ${numInputsPhrase} and ${numOutputsPhrase}.`
          };
          if (dependencyValues.functionAttr.position) {
            warning.position = dependencyValues.functionAttr.position;
          }
          return {
            setValue: { numDimensions: 0 },
            sendDiagnostics: [warning]
          };
        } else {
          return {
            setValue: {
              numDimensions: dependencyValues.functionAttr.stateValues.numInputs
            }
          };
        }
      }
    };
    stateVariableDefinitions.allIterates = {
      description: "All computed iterates of the function.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "mathList",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        functionAttr: {
          dependencyType: "attributeComponent",
          attributeName: "function",
          variableNames: ["symbolicfs", "numericalfs", "symbolic"]
        },
        forceSymbolic: {
          dependencyType: "stateVariable",
          variableName: "forceSymbolic"
        },
        forceNumeric: {
          dependencyType: "stateVariable",
          variableName: "forceNumeric"
        },
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        },
        initialValue: {
          dependencyType: "stateVariable",
          variableName: "initialValue"
        },
        numIterates: {
          dependencyType: "stateVariable",
          variableName: "numIterates"
        }
      }),
      definition({ dependencyValues }) {
        let allIterates = [];
        let functionComp = dependencyValues.functionAttr;
        let initialValue = dependencyValues.initialValue;
        let symbolic = !dependencyValues.forceNumeric && (functionComp.stateValues.symbolic || dependencyValues.forceSymbolic);
        let numIterates = dependencyValues.numIterates;
        if (!functionComp || dependencyValues.numDimensions === 0 || !(dependencyValues.numDimensions === 1 || Array.isArray(initialValue.tree) && vectorOperators.includes(initialValue.tree[0]) && initialValue.tree.length === dependencyValues.numDimensions + 1)) {
          allIterates = Array(numIterates).fill(me$3.fromAst("＿"));
          return { setValue: { allIterates } };
        }
        if (symbolic) {
          if (dependencyValues.numDimensions === 1) {
            let symbolicf = functionComp.stateValues.symbolicfs[0];
            let value = initialValue;
            for (let ind = 0; ind < numIterates; ind++) {
              value = symbolicf(value);
              allIterates.push(value);
            }
          } else {
            let symbolicfs = functionComp.stateValues.symbolicfs;
            let value = initialValue.tree.slice(1).map((v2) => me$3.fromAst(v2));
            for (let ind = 0; ind < numIterates; ind++) {
              let iterComps = [];
              for (let i2 = 0; i2 < dependencyValues.numDimensions; i2++) {
                iterComps.push(symbolicfs[i2](...value).tree);
              }
              allIterates.push(
                me$3.fromAst(["vector", ...iterComps])
              );
              value = iterComps.map((v2) => me$3.fromAst(v2));
            }
          }
        } else {
          if (dependencyValues.numDimensions === 1) {
            let numericalf = functionComp.stateValues.numericalfs[0];
            let value = initialValue.evaluate_to_constant();
            if (Number.isNaN(value)) {
              allIterates = Array(numIterates).fill(
                me$3.fromAst("＿")
              );
            } else {
              for (let ind = 0; ind < numIterates; ind++) {
                value = numericalf(value);
                allIterates.push(me$3.fromAst(value));
              }
            }
          } else {
            let numericalfs = functionComp.stateValues.numericalfs;
            let value = initialValue.tree.slice(1).map((x2) => me$3.fromAst(x2).evaluate_to_constant());
            for (let ind = 0; ind < numIterates; ind++) {
              let iterComps = [];
              for (let i2 = 0; i2 < dependencyValues.numDimensions; i2++) {
                iterComps.push(numericalfs[i2](...value));
              }
              allIterates.push(
                me$3.fromAst(["vector", ...iterComps])
              );
              value = iterComps;
            }
          }
        }
        return { setValue: { allIterates } };
      }
    };
    stateVariableDefinitions.allIteratesWithInitial = {
      description: "All iterates including the initial value.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "mathList",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        initialValue: {
          dependencyType: "stateVariable",
          variableName: "initialValue"
        },
        allIterates: {
          dependencyType: "stateVariable",
          variableName: "allIterates"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            allIteratesWithInitial: [
              dependencyValues.initialValue,
              ...dependencyValues.allIterates
            ]
          }
        };
      }
    };
    stateVariableDefinitions.iterates = {
      description: "The list of iterate values.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      entryPrefixes: ["iterate"],
      returnArraySizeDependencies: () => ({
        numIterates: {
          dependencyType: "stateVariable",
          variableName: "numIterates"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numIterates];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allIterates: {
            dependencyType: "stateVariable",
            variableName: "allIterates"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arraySize }) {
        let iterates = {};
        for (let ind = 0; ind < arraySize[0]; ind++) {
          iterates[ind] = globalDependencyValues.allIterates[ind];
        }
        return { setValue: { iterates } };
      }
    };
    stateVariableDefinitions.finalIterate = {
      description: "The last iterate value.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      stateVariablesDeterminingDependencies: ["numIterates"],
      returnDependencies({ stateValues }) {
        if (!Number.isFinite(stateValues.numIterates) || stateValues.numIterates < 0) {
          return {};
        }
        if (stateValues.numIterates > 0) {
          return {
            finalIterate: {
              dependencyType: "stateVariable",
              variableName: `iterate${stateValues.numIterates}`
            }
          };
        } else {
          return {
            finalIterate: {
              dependencyType: "stateVariable",
              variableName: "initialValue"
            }
          };
        }
      },
      definition({ dependencyValues }) {
        return {
          setValue: { finalIterate: dependencyValues.finalIterate }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Module extends Group {
  static componentType = "module";
  static componentDocs = {
    summary: "A reusable group with parameterized attributes; can be copied elsewhere with new parameter values"
  };
  static acceptAnyAttribute = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.styleNumber;
    return attributes;
  }
}
class ModuleAttributes extends CompositeComponent {
  static componentType = "moduleAttributes";
  static componentDocs = {
    summary: "Declares attributes accepted by an enclosing `<module>`"
  };
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  // since don't have child groups, tell schema about children here
  static additionalSchemaChildren = ["_base"];
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.moduleIdx = {
      returnDependencies: () => ({
        sourceCompositeIdentity: {
          dependencyType: "sourceCompositeIdentity"
        }
      }),
      definition({ dependencyValues }) {
        let moduleIdx = null;
        if (dependencyValues.sourceCompositeIdentity && dependencyValues.sourceCompositeIdentity.componentType === "module") {
          moduleIdx = dependencyValues.sourceCompositeIdentity.componentIdx;
        }
        return { setValue: { moduleIdx } };
      }
    };
    stateVariableDefinitions.serializedChildren = {
      returnDependencies: () => ({
        serializedChildren: {
          dependencyType: "serializedChildren",
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            serializedChildren: dependencyValues.serializedChildren
          }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        componentIdentity: {
          dependencyType: "stateVariable",
          variableName: "moduleIdx"
        }
      }),
      definition() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    const diagnostics = [];
    const moduleIdx = await component.stateValues.moduleIdx;
    if (moduleIdx === null) {
      return { replacements: [], diagnostics, nComponents };
    }
    const module = components[moduleIdx];
    const attributeLowerCaseMapping = {};
    for (let attrName in module.attributes) {
      attributeLowerCaseMapping[attrName.toLowerCase()] = attrName;
    }
    const existingModuleAttrNames = Object.keys(
      module.constructor.createAttributesObject()
    ).map((x2) => x2.toLowerCase());
    existingModuleAttrNames.push("name");
    const componentsForModuleAttributes = [];
    const children = deepClone(
      await component.state.serializedChildren.value
    );
    for (const child of children) {
      const childName = child.attributes.name?.primitive?.value;
      if (!childName) {
        componentsForModuleAttributes.push(child);
        diagnostics.push({
          type: "warning",
          message: `Since the component \`<${child.componentType}>\` does not have a name, it cannot be used for a module attribute`,
          position: child.position
        });
        continue;
      }
      let attributeName = attributeLowerCaseMapping[childName.toLowerCase()];
      let attributeFromModule = module.attributes[attributeName];
      if (attributeFromModule === void 0) {
        componentsForModuleAttributes.push(child);
        continue;
      }
      if (existingModuleAttrNames.includes(attributeName)) {
        diagnostics.push({
          type: "warning",
          message: `The component \`<${child.componentType} name="${childName}">\` cannot be used as an attribute for a module because the \`<module>\` component type already has a "${childName}" attribute defined.`,
          position: child.position
        });
        componentsForModuleAttributes.push(child);
        continue;
      }
      if (attributeFromModule.type !== "unresolved") {
        throw Error(
          "A module attribute that isn't an existing attribute must be unresolved!"
        );
      }
      const expandResult = expandUnflattenedToSerializedComponents({
        serializedComponents: attributeFromModule.children,
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      diagnostics.push(...expandResult.diagnostics);
      nComponents = expandResult.nComponents;
      child.children = expandResult.components;
      const sugarResult = applySugar({
        serializedComponents: [child],
        componentInfoObjects,
        nComponents,
        stateIdInfo
      });
      diagnostics.push(...sugarResult.diagnostics);
      nComponents = sugarResult.nComponents;
      componentsForModuleAttributes.push(sugarResult.components[0]);
    }
    const setup = {
      type: "serialized",
      componentType: "setup",
      componentIdx: nComponents++,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      children: componentsForModuleAttributes,
      attributes: {},
      doenetAttributes: {},
      state: {}
    };
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements: [setup],
      diagnostics,
      nComponents
    };
  }
}
class Footnote extends InlineComponent {
  static componentType = "footnote";
  static renderChildren = true;
  static componentDocs = {
    summary: "Inline footnote whose body is shown on demand and is auto-numbered in the document"
  };
  static includeBlankStringChildren = true;
  static returnChildGroups() {
    return [
      {
        group: "inlines",
        componentTypes: ["_inline"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.text = {
      description: "The footnote's body as plain text.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlines"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let text = textFromChildren(dependencyValues.inlineChildren);
        return { setValue: { text } };
      }
    };
    stateVariableDefinitions.footnoteTag = {
      description: "The auto-generated marker (number) used to reference this footnote.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      forRenderer: true,
      mustEvaluate: true,
      // must evaluate to make sure all counters are accounted for
      returnDependencies: () => ({
        footnoteCounter: {
          dependencyType: "counter",
          counterName: "footnote"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            footnoteTag: String(dependencyValues.footnoteCounter)
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Caption extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "caption";
  static rendererType = "containerInline";
  static canDisplayChildErrors = true;
  static componentDocs = {
    summary: "A caption attached to a figure or table"
  };
  static renderChildren = true;
  static includeBlankStringChildren = true;
  static returnChildGroups() {
    return [
      {
        group: "inlinesBlocks",
        componentTypes: ["_inline", "_block"]
      },
      {
        group: "errors",
        componentTypes: ["_error"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.text = {
      description: "The caption rendered as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlinesBlocks"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: function({ dependencyValues }) {
        let text = textFromChildren(dependencyValues.inlineChildren);
        return { setValue: { text } };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Endpoint extends Point {
  static componentType = "endpoint";
  static rendererType = "point";
  static componentDocs = {
    summary: "A point that marks the endpoint of an interval, rendered open or closed"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.markerStyle = {
      ...attributes.markerStyle,
      validValues: markerStyleValuesWithFillVariants
    };
    delete attributes.markerFilled;
    attributes.open = {
      createComponentOfType: "boolean",
      createStateVariable: "open",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether the endpoint is open (excluded) rather than closed (included)."
    };
    attributes.switchable = {
      createComponentOfType: "boolean",
      createStateVariable: "switchable",
      defaultValue: false,
      public: true,
      forRenderer: true,
      description: "Whether the user can toggle the endpoint between open and closed by clicking it."
    };
    return attributes;
  }
  async switchPoint({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (await this.stateValues.switchable) {
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "open",
            value: !await this.stateValues.open
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            open: !await this.stateValues.open
          }
        }
      });
    }
  }
}
class Sort extends CompositeComponent {
  static componentType = "sort";
  static componentDocs = {
    summary: "Sorts a list according to a comparison function"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.sortVectorsBy = {
      description: "Whether to sort vectors by component or by magnitude.",
      createComponentOfType: "text",
      createStateVariable: "sortVectorsBy",
      defaultValue: "displacement",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "displacement",
          description: "Sort vectors by their displacement components."
        },
        {
          value: "tail",
          description: "Sort vectors by the position of their tail point."
        }
      ]
    };
    attributes.sortByComponent = {
      description: "Index of the component to sort by (when sorting vectors).",
      createComponentOfType: "integer",
      createStateVariable: "sortByComponent",
      defaultValue: "1",
      public: true
    };
    attributes.sortByProp = {
      createPrimitiveOfType: "string",
      description: 'Name of a property to sort by (e.g. "x" for sorting points by x-coordinate).'
    };
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "Component type to sort children as."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    function breakStringsMacrosIntoTypeBySpaces({
      matchedChildren,
      componentAttributes,
      componentInfoObjects,
      nComponents
    }) {
      const diagnostics = [];
      if (!matchedChildren.every(
        (child) => typeof child === "string" || child.extending && "Ref" in child.extending
      )) {
        return { success: false };
      }
      let type;
      if (componentAttributes.type?.value) {
        type = componentAttributes.type.value;
      } else {
        if (matchedChildren.some((child) => typeof child === "string")) {
          diagnostics.push({
            type: "warning",
            message: `For \`<sort>\` to work with string children, a \`type\` attribute must be specified.`
          });
        }
        return { success: false, diagnostics };
      }
      if (!["math", "text", "number", "boolean"].includes(type)) {
        console.warn(`Invalid type ${type}`);
        diagnostics.push({
          type: "warning",
          message: `Invalid type ${type} for sort component. Must be one of math, text, number, or boolean. Defaulting to math.`
        });
        type = "math";
      }
      let groupIntoComponentTypesSeparatedBySpaces = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
        componentType: type,
        forceComponentType: true
      });
      let result = groupIntoComponentTypesSeparatedBySpaces({
        matchedChildren,
        componentInfoObjects,
        nComponents
      });
      if (result.success) {
        let newChildren = result.newChildren;
        return {
          success: true,
          newChildren,
          nComponents: result.nComponents,
          diagnostics
        };
      } else {
        return { success: false };
      }
    }
    sugarInstructions.push({
      replacementFunction: breakStringsMacrosIntoTypeBySpaces
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.propName = {
      returnDependencies: () => ({
        propName: {
          dependencyType: "attributePrimitive",
          attributeName: "sortByProp"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { propName: dependencyValues.propName } };
      }
    };
    stateVariableDefinitions.componentIndicesForValues = {
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["componentIndicesInList"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        let componentIndicesForValues = [];
        const diagnostics = [];
        for (let child of dependencyValues.children) {
          if (typeof child === "string") {
            diagnostics.push({
              type: "warning",
              message: `String "${child}" is not a valid component to sort. Ignoring.`
            });
            continue;
          }
          if (child.stateValues.componentIndicesInList) {
            componentIndicesForValues.push(
              ...child.stateValues.componentIndicesInList
            );
          } else {
            componentIndicesForValues.push(child.componentIdx);
          }
        }
        return {
          setValue: { componentIndicesForValues },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.sortedValues = {
      stateVariablesDeterminingDependencies: [
        "componentIndicesForValues",
        "sortByComponent",
        "propName"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          sortVectorsBy: {
            dependencyType: "stateVariable",
            variableName: "sortVectorsBy"
          },
          sortByComponent: {
            dependencyType: "stateVariable",
            variableName: "sortByComponent"
          },
          propName: {
            dependencyType: "stateVariable",
            variableName: "propName"
          }
        };
        if (stateValues.propName) {
          for (let [
            ind,
            cIdx
          ] of stateValues.componentIndicesForValues.entries()) {
            dependencies[`component${ind}`] = {
              dependencyType: "stateVariable",
              componentIdx: cIdx,
              variableName: stateValues.propName,
              variablesOptional: true,
              caseInsensitiveVariableMatch: true,
              publicStateVariablesOnly: true,
              returnAsComponentObject: true
            };
          }
        } else {
          for (let [
            ind,
            cIdx
          ] of stateValues.componentIndicesForValues.entries()) {
            dependencies[`component${ind}`] = {
              dependencyType: "multipleStateVariables",
              componentIdx: cIdx,
              variableNames: [
                "value",
                `x${stateValues.sortByComponent}`,
                `tailX${stateValues.sortByComponent}`
              ],
              variablesOptional: true
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        let allValues = [];
        let allAreNumeric = true;
        for (let depName in dependencyValues) {
          if (depName.substring(0, 9) !== "component") {
            continue;
          }
          let component = dependencyValues[depName];
          if (dependencyValues.propName) {
            let value = Object.values(component.stateValues)[0];
            allValues.push({
              componentIdx: component.componentIdx,
              numericalValue: Number(value),
              textValue: String(value)
            });
            if (!Number.isFinite(value)) {
              allAreNumeric = false;
            }
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: component.componentType,
            baseComponentType: "number"
          })) {
            allValues.push({
              componentIdx: component.componentIdx,
              numericalValue: component.stateValues.value,
              textValue: String(component.stateValues.value)
            });
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: component.componentType,
            baseComponentType: "text"
          })) {
            let numericalValue = NaN;
            let textValue = component.stateValues.value;
            allValues.push({
              componentIdx: component.componentIdx,
              numericalValue,
              textValue
            });
            allAreNumeric = false;
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: component.componentType,
            baseComponentType: "math"
          })) {
            let numericalValue = component.stateValues.value.evaluate_to_constant();
            if (Number.isNaN(numericalValue)) {
              allAreNumeric = false;
            }
            allValues.push({
              componentIdx: component.componentIdx,
              numericalValue,
              textValue: component.stateValues.value.toString()
            });
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: component.componentType,
            baseComponentType: "point"
          })) {
            let compValue = component.stateValues[`x${dependencyValues.sortByComponent}`];
            let numericalValue = NaN;
            let textValue = "";
            if (compValue) {
              numericalValue = compValue.evaluate_to_constant();
              if (Number.isNaN(numericalValue)) {
                allAreNumeric = false;
              }
              textValue = compValue.toString();
            }
            allValues.push({
              componentIdx: component.componentIdx,
              numericalValue,
              textValue
            });
          } else if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: component.componentType,
            baseComponentType: "vector"
          })) {
            let numericalValue = NaN;
            let textValue = "";
            let compValue = component.stateValues[`x${dependencyValues.sortByComponent}`];
            if (dependencyValues.sortVectorsBy === "displacement") {
              compValue = component.stateValues[`x${dependencyValues.sortByComponent}`];
            } else {
              compValue = component.stateValues[`tailX${dependencyValues.sortByComponent}`];
            }
            if (compValue) {
              numericalValue = compValue.evaluate_to_constant();
              if (Number.isNaN(numericalValue)) {
                allAreNumeric = false;
              }
              textValue = compValue.toString();
            }
            allValues.push({
              componentIdx: component.componentIdx,
              numericalValue,
              textValue
            });
          }
        }
        if (allAreNumeric) {
          allValues.sort(
            (a2, b2) => a2.numericalValue - b2.numericalValue
          );
        } else {
          allValues.sort(
            (a2, b2) => a2.textValue > b2.textValue ? 1 : a2.textValue < b2.textValue ? -1 : 0
          );
        }
        return {
          setValue: {
            sortedValues: allValues
          }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        sortedValues: {
          dependencyType: "stateVariable",
          variableName: "sortedValues"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    for (let valueObj of await component.stateValues.sortedValues) {
      let replacementSource;
      if (valueObj.listInd === void 0) {
        replacementSource = components[valueObj.componentIdx];
      } else {
        let listComponent = components[valueObj.componentIdx];
        replacementSource = listComponent.activeChildren[valueObj.listInd];
      }
      if (replacementSource) {
        componentsCopied.push(replacementSource.componentIdx);
        const serializedComponent = await replacementSource.serialize();
        const res = createNewComponentIndices(
          [serializedComponent],
          nComponents
        );
        nComponents = res.nComponents;
        replacements.push(res.components[0]);
      }
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let componentsToCopy = [];
    for (let valueObj of await component.stateValues.sortedValues) {
      let replacementSource;
      if (valueObj.listInd === void 0) {
        replacementSource = components[valueObj.componentIdx];
      } else {
        let listComponent = components[valueObj.componentIdx];
        replacementSource = listComponent.activeChildren[valueObj.listInd];
      }
      if (replacementSource) {
        componentsToCopy.push(replacementSource.componentIdx);
      }
    }
    if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
      (x2, i2) => x2 === componentsToCopy[i2]
    )) {
      return { replacementChanges: [], diagnostics, nComponents };
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
class Shuffle extends CompositeComponent {
  static componentType = "shuffle";
  static componentDocs = {
    summary: "Randomly shuffles its children"
  };
  static takesIndex = true;
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static createsVariants = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      createPrimitiveOfType: "string",
      description: "Component type to shuffle children as."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    function breakStringsMacrosIntoTypeBySpaces({
      matchedChildren,
      componentAttributes,
      componentInfoObjects,
      nComponents
    }) {
      const diagnostics = [];
      if (!matchedChildren.every(
        (child) => typeof child === "string" || child.extending && "Ref" in child.extending
      )) {
        return { success: false };
      }
      let type;
      if (componentAttributes.type?.value) {
        type = componentAttributes.type.value;
      } else {
        if (matchedChildren.some((child) => typeof child === "string")) {
          diagnostics.push({
            type: "warning",
            message: `For \`<shuffle>\` to work with string children, a \`type\` attribute must be specified.`
          });
        }
        return { success: false, diagnostics };
      }
      if (!["math", "text", "number", "boolean"].includes(type)) {
        console.warn(`Invalid type ${type}`);
        diagnostics.push({
          type: "warning",
          message: `Invalid type ${type} for shuffle component. Must be one of math, text, number, or boolean. Defaulting to math.`
        });
        type = "math";
      }
      let groupIntoComponentTypesSeparatedBySpaces = returnGroupIntoComponentTypeSeparatedBySpacesOutsideParens({
        componentType: type,
        forceComponentType: true
      });
      let result = groupIntoComponentTypesSeparatedBySpaces({
        matchedChildren,
        componentInfoObjects,
        nComponents
      });
      if (result.success) {
        let newChildren = result.newChildren;
        return {
          success: true,
          newChildren,
          nComponents: result.nComponents,
          diagnostics
        };
      } else {
        return { success: false };
      }
    }
    sugarInstructions.push({
      replacementFunction: breakStringsMacrosIntoTypeBySpaces
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.originalComponentIndices = {
      additionalStateVariablesDefined: ["numComponents"],
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["componentIndicesInList"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        let originalComponentIndices = [];
        const diagnostics = [];
        for (let child of dependencyValues.children) {
          if (typeof child === "string") {
            diagnostics.push({
              type: "warning",
              message: `String "${child}" is not a valid component to shuffle. Ignoring.`
            });
          }
          if (child.stateValues?.componentIndicesInList) {
            originalComponentIndices.push(
              ...child.stateValues.componentIndicesInList
            );
          } else {
            originalComponentIndices.push(child.componentIdx);
          }
        }
        return {
          setValue: {
            originalComponentIndices,
            numComponents: originalComponentIndices.length
          },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.componentOrder = {
      returnDependencies({ sharedParameters }) {
        let dependencies = {
          variantSeed: {
            dependencyType: "value",
            value: sharedParameters.variantSeed
          },
          rngClass: {
            dependencyType: "value",
            value: sharedParameters.rngClass,
            doNotProxy: true
          },
          numComponents: {
            dependencyType: "stateVariable",
            variableName: "numComponents"
          },
          variants: {
            dependencyType: "variants"
          }
        };
        return dependencies;
      },
      definition({ dependencyValues }) {
        let diagnostics = [];
        let numComponents = dependencyValues.numComponents;
        let desiredComponentOrder = dependencyValues.variants?.desiredVariant?.indices;
        if (desiredComponentOrder !== void 0) {
          if (desiredComponentOrder.length !== numComponents) {
            diagnostics.push({
              message: "Ignoring indices specified for shuffle as number of indices doesn't match number of components.",
              type: "info"
            });
          } else {
            desiredComponentOrder = desiredComponentOrder.map(Number);
            if (!desiredComponentOrder.every(Number.isInteger)) {
              throw Error(
                "All indices specified for shuffle must be integers"
              );
            }
            if (!desiredComponentOrder.every(
              (x2) => x2 >= 1 && x2 <= numComponents
            )) {
              diagnostics.push({
                message: "Ignoring indices specified for shuffle as some indices out of range.",
                type: "info"
              });
            } else {
              return {
                setValue: {
                  componentOrder: desiredComponentOrder.filter(
                    (x2) => x2 !== void 0
                  )
                }
              };
            }
          }
        }
        let variantRng = dependencyValues.rngClass(
          dependencyValues.variantSeed + "co"
        );
        let componentOrder = [...Array(numComponents).keys()].map(
          (x2) => x2 + 1
        );
        for (let i2 = numComponents - 1; i2 > 0; i2--) {
          const rand = variantRng();
          const j2 = Math.floor(rand * (i2 + 1));
          [componentOrder[i2], componentOrder[j2]] = [
            componentOrder[j2],
            componentOrder[i2]
          ];
        }
        return {
          setValue: {
            componentOrder
          },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        sortedValues: {
          dependencyType: "stateVariable",
          variableName: "componentOrder"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    stateVariableDefinitions.generatedVariantInfo = {
      additionalStateVariablesDefined: ["isVariantComponent"],
      returnDependencies: ({
        componentInfoObjects,
        sharedParameters
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        componentOrder: {
          dependencyType: "stateVariable",
          variableName: "componentOrder"
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: { createdBy: componentIdx },
          indices: dependencyValues.componentOrder
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo,
            isVariantComponent: true
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents
    });
  }
  static async createSerializedReplacements({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let replacements = [];
    let componentsCopied = [];
    let originalComponentIndices = await component.stateValues.originalComponentIndices;
    for (let ind of await component.stateValues.componentOrder) {
      let replacementSource = components[originalComponentIndices[ind - 1]];
      if (replacementSource) {
        componentsCopied.push(replacementSource.componentIdx);
        const serializedComponent = await replacementSource.serialize();
        const res = createNewComponentIndices(
          [serializedComponent],
          nComponents
        );
        nComponents = res.nComponents;
        replacements.push(...res.components);
      }
    }
    replacements = postProcessCopy({
      serializedComponents: replacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: true,
      markAsPrimaryShadow: true
    });
    workspace.componentsCopied = componentsCopied;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    componentInfoObjects,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    let componentsToCopy = [];
    let originalComponentIndices = await component.stateValues.originalComponentIndices;
    for (let ind of await component.stateValues.componentOrder) {
      let replacementSource = components[originalComponentIndices[ind - 1]];
      if (replacementSource) {
        componentsToCopy.push(replacementSource.componentIdx);
      }
    }
    if (componentsToCopy.length == workspace.componentsCopied.length && workspace.componentsCopied.every(
      (x2, i2) => x2 === componentsToCopy[i2]
    )) {
      return { replacementChanges: [], diagnostics, nComponents };
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      componentInfoObjects,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let numComponents = 0;
    for (let child of serializedComponent.children) {
      if (componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: child.componentType,
        baseComponentType: "_composite"
      })) {
        if (child.attributes.createComponentOfType?.primitive) {
          if (child.attributes.numComponents?.primitive !== void 0) {
            let newComponents = Number(
              child.attributes.numComponents?.primitive.value
            );
            if (Number.isInteger(newComponents) && newComponents >= 0) {
              numComponents += newComponents;
            } else {
              return { success: false };
            }
          } else {
            numComponents++;
          }
        } else {
          return { success: false };
        }
      } else {
        numComponents++;
      }
    }
    let numberOfPermutations = 1;
    for (let i2 = 2; i2 <= numComponents; i2++) {
      numberOfPermutations *= i2;
    }
    let result = super.determineNumberOfUniqueVariants({
      serializedComponent,
      componentInfoObjects,
      infoDiagnostics
    });
    if (!result.success) {
      return { success: false };
    }
    let numVariants = result.numVariants * numberOfPermutations;
    if (!(numVariants > 0)) {
      return { success: false };
    }
    serializedComponent.variants.numVariants = numVariants;
    serializedComponent.variants.uniqueVariantData = {
      numVariantsByDescendant: serializedComponent.variants.uniqueVariantData.numVariantsByDescendant,
      numberOfPermutations,
      numComponents
    };
    return { success: true, numVariants };
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let numVariantsByDescendant = serializedComponent.variants.uniqueVariantData.numVariantsByDescendant;
    let descendantVariantComponents = serializedComponent.variants.descendantVariantComponents;
    let numberOfPermutations = serializedComponent.variants.uniqueVariantData.numberOfPermutations;
    let numComponents = serializedComponent.variants.uniqueVariantData.numComponents;
    let numbersOfOptions = [...numVariantsByDescendant];
    numbersOfOptions.push(numberOfPermutations);
    let indicesForEachOption = enumerateCombinations({
      numberOfOptionsByIndex: numbersOfOptions,
      maxNumber: variantIndex
    })[variantIndex - 1].map((x2) => x2 + 1);
    let permutationsIndex = indicesForEachOption.pop();
    let indicesForEachDescendant = indicesForEachOption;
    let indicesToPermute = [...Array(numComponents).keys()].map(
      (x2) => x2 + 1
    );
    let permutedIndices = enumeratePermutations({
      values: indicesToPermute,
      maxNumber: permutationsIndex
    })[permutationsIndex - 1];
    let haveNontrivialSubvariants = false;
    let subvariants = [];
    for (let descendantNum = 0; descendantNum < numVariantsByDescendant.length; descendantNum++) {
      if (numVariantsByDescendant[descendantNum] > 1) {
        let descendant = descendantVariantComponents[descendantNum];
        let compClass = componentInfoObjects.allComponentClasses[descendant.componentType];
        let result = compClass.getUniqueVariant({
          serializedComponent: descendant,
          variantIndex: indicesForEachDescendant[descendantNum],
          componentInfoObjects
        });
        if (!result.success) {
          return { success: false };
        }
        subvariants.push(result.desiredVariant);
        haveNontrivialSubvariants = true;
      } else {
        subvariants.push({});
      }
    }
    let desiredVariant = { indices: permutedIndices };
    if (haveNontrivialSubvariants) {
      desiredVariant.subvariants = subvariants;
    }
    return { success: true, desiredVariant };
  }
}
class SolveEquations extends InlineComponent {
  static componentType = "solveEquations";
  static componentDocs = {
    summary: "Numerically solves a system of equations"
  };
  static rendererType = void 0;
  static variableForIndexAsProp = "solutions";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.variables = {
      createComponentOfType: "_variableNameList",
      description: "Names of the variables to solve for."
    };
    attributes.numDiscretizationPoints = {
      description: "Number of points used when searching for solutions numerically.",
      createComponentOfType: "number",
      createStateVariable: "numDiscretizationPoints",
      defaultValue: 100,
      public: true
    };
    attributes.minVar = {
      createComponentOfType: "number",
      createStateVariable: "minVar",
      defaultValue: null,
      description: "Lower bound of the variable's search range."
    };
    attributes.maxVar = {
      createComponentOfType: "number",
      createStateVariable: "maxVar",
      defaultValue: null,
      description: "Upper bound of the variable's search range."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let wrapStringsAndMacros = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      if (!matchedChildren.every(
        (child) => typeof child === "string" || child.extending && "Ref" in child.extending
      )) {
        return { success: false };
      }
      if (matchedChildren.length === 1 && typeof matchedChildren[0] !== "string") {
        return { success: false };
      }
      return {
        success: true,
        newChildren: [
          {
            type: "serialized",
            componentType: "math",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: matchedChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ],
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: wrapStringsAndMacros
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      },
      {
        group: "strings",
        componentTypes: ["string"]
      }
    ];
  }
  static returnStateVariableDefinitions(numerics) {
    let stateVariableDefinitions = super.returnStateVariableDefinitions(
      numerics
    );
    stateVariableDefinitions.variables = {
      description: "The variables of the equations.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      entryPrefixes: ["variable"],
      returnArraySizeDependencies: () => ({
        // numInputs: {
        //   dependencyType: "stateVariable",
        //   variableName: "numInputs",
        // },
      }),
      returnArraySize({ dependencyValues }) {
        return [1];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let globalDependencies = {
          variablesAttr: {
            dependencyType: "attributeComponent",
            attributeName: "variables",
            variableNames: ["variables"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        arraySize,
        arrayKeys,
        usedDefault
      }) {
        let variablesSpecified = [];
        if (globalDependencyValues.variablesAttr !== null) {
          variablesSpecified = globalDependencyValues.variablesAttr.stateValues.variables;
        }
        return {
          setValue: {
            variables: returnNVariables(
              arraySize[0],
              variablesSpecified
            )
          }
        };
      }
    };
    stateVariableDefinitions.variable = {
      isAlias: true,
      targetVariableName: "variable1",
      description: "The first variable being solved for."
    };
    stateVariableDefinitions.allSolutions = {
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        },
        variables: {
          dependencyType: "stateVariable",
          variableName: "variables"
        },
        minVar: {
          dependencyType: "stateVariable",
          variableName: "minVar"
        },
        maxVar: {
          dependencyType: "stateVariable",
          variableName: "maxVar"
        },
        numDiscretizationPoints: {
          dependencyType: "stateVariable",
          variableName: "numDiscretizationPoints"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.mathChild.length === 0) {
          return { setValue: { allSolutions: [] } };
        }
        let expression = dependencyValues.mathChild[0].stateValues.value;
        if (!(Array.isArray(expression.tree) && expression.tree.length === 3 && expression.tree[0] === "=")) {
          return { setValue: { allSolutions: [] } };
        }
        let minVar = dependencyValues.minVar;
        let maxVar = dependencyValues.maxVar;
        let numericalSolutions = [];
        let varName = dependencyValues.variables[0].subscripts_to_strings().tree;
        let formula = me$3.fromAst([
          "+",
          expression.tree[1],
          ["-", expression.tree[2]]
        ]).subscripts_to_strings();
        let f_base;
        try {
          f_base = formula.f();
        } catch (e3) {
          let message = "Cannot solve equation as could not evaluate equation: " + expression.toString();
          let diagnostics = [{ message, type: "warning" }];
          return {
            setValue: { allSolutions: [] },
            sendDiagnostics: diagnostics
          };
        }
        let f2 = (x2) => f_base({ [varName]: x2 });
        let f_symbolic = (x2) => normalizeMathExpression({
          value: formula.substitute({ [varName]: x2 }).strings_to_subscripts(),
          simplify: true,
          expand: true
        }).evaluate_to_constant();
        let f_or_nan = function(x2) {
          try {
            return f2(x2);
          } catch (e3) {
            return NaN;
          }
        };
        let f_or_symbolic = function(x2) {
          try {
            return f2(x2);
          } catch (e3) {
            let res = f_symbolic(x2);
            if (res !== null) {
              return res;
            } else {
              return NaN;
            }
          }
        };
        if (!Number.isFinite(minVar)) {
          minVar = -10;
        }
        if (!Number.isFinite(maxVar)) {
          maxVar = 10;
        }
        if (minVar > maxVar) {
          return { setValue: { allSolutions: [] } };
        }
        let boundaryPoints = [minVar, maxVar];
        let testPoints = [minVar];
        for (let ind = 0; ind < boundaryPoints.length - 1; ind++) {
          let bot = boundaryPoints[ind];
          let top = boundaryPoints[ind + 1];
          let Nsteps = dependencyValues.numDiscretizationPoints;
          let delta = (top - bot) / Nsteps;
          for (let step = 1; step < Nsteps; step++) {
            testPoints.push(bot + step * delta);
          }
          testPoints.push(top);
        }
        let newFirst = 2 * testPoints[0] - testPoints[1];
        let newLast = 2 * testPoints[testPoints.length - 1] - testPoints[testPoints.length - 2];
        testPoints = [newFirst, ...testPoints, newLast];
        let f_points = testPoints.map(f_or_symbolic);
        let lastX = testPoints[0];
        let lastF = f_points[0];
        for (let ind = 0; ind < testPoints.length; ind++) {
          let thisX = testPoints[ind];
          let thisF = f_points[ind];
          if (!Number.isFinite(thisF) || !Number.isFinite(lastF)) {
            let finiteX, nonFiniteX;
            if (Number.isFinite(thisF)) {
              finiteX = thisX;
              nonFiniteX = lastX;
            } else if (Number.isFinite(lastF)) {
              finiteX = lastX;
              nonFiniteX = thisX;
            }
            if (finiteX !== void 0) {
              while (true) {
                let midX = (finiteX + nonFiniteX) / 2;
                if (midX === finiteX || midX === nonFiniteX) {
                  break;
                }
                if (Number.isFinite(f_or_nan(midX))) {
                  finiteX = midX;
                } else {
                  nonFiniteX = midX;
                }
              }
              if (![thisX, lastX].includes(finiteX)) {
                testPoints.splice(ind, 0, finiteX);
                f_points.splice(ind, 0, f_or_nan(finiteX));
              }
            }
          }
          lastX = thisX;
          lastF = thisF;
        }
        numericalSolutions = [];
        lastX = testPoints[0];
        lastF = f_points[0];
        if (lastF === 0) {
          numericalSolutions.push(lastX);
        }
        for (let ind = 1; ind < testPoints.length; ind++) {
          let thisX = testPoints[ind];
          let thisF = f_points[ind];
          if (thisF === 0) {
            let addedExactZero = false;
            if (ind < testPoints.length - 1) {
              let nextX = testPoints[ind + 1];
              let nextF = f_points[ind + 1];
              let originalLastX = lastX;
              let originalLastF = lastF;
              if (lastF * nextF > 0) {
                for (let j2 = 0; j2 < 10; j2++) {
                  lastX = 0.1 * lastX + 0.9 * thisX;
                  nextX = 0.1 * nextX + 0.9 * thisX;
                  lastF = f_or_nan(lastX);
                  nextF = f_or_nan(nextX);
                  if (lastF * nextF < 0) {
                    if (lastF * originalLastF < 0) {
                      numericalSolutions.push(
                        numerics.fzero(f_or_nan, [
                          originalLastX,
                          lastX
                        ])
                      );
                      numericalSolutions.push(thisX);
                    } else {
                      if (!numericalSolutions.includes(
                        thisX
                      )) {
                        numericalSolutions.push(thisX);
                      }
                      thisX = nextX;
                      thisF = nextF;
                    }
                    addedExactZero = true;
                    break;
                  }
                }
              }
            }
            if (!addedExactZero && !numericalSolutions.includes(thisX)) {
              numericalSolutions.push(thisX);
            }
          } else if (thisF * lastF < 0) {
            numericalSolutions.push(
              numerics.fzero(f_or_nan, [lastX, thisX])
            );
          } else if (lastF !== 0) {
            if (ind < testPoints.length - 1) {
              let nextX = testPoints[ind + 1];
              let nextF = f_points[ind + 1];
              if ((lastF - thisF) * (nextF - thisF) > 0 && (thisF > 0 && lastF > thisF || thisF < 0 && lastF < thisF)) {
                let fFlip;
                if (lastF > thisF) {
                  fFlip = f_or_nan;
                } else {
                  fFlip = (x2) => -f_or_nan(x2);
                }
                let res = numerics.fminbr(
                  fFlip,
                  [lastX, nextX],
                  void 0,
                  1e-10
                );
                if (res.success) {
                  let extremumX = res.x;
                  let extremumF = f_or_nan(extremumX);
                  if (extremumF * lastF < 0) {
                    numericalSolutions.push(
                      numerics.fzero(f_or_nan, [
                        lastX,
                        extremumX
                      ])
                    );
                    thisX = extremumX;
                    thisF = extremumF;
                  } else if (extremumF === 0) {
                    numericalSolutions.push(extremumX);
                  }
                }
              }
            }
          }
          lastX = thisX;
          lastF = thisF;
        }
        numericalSolutions = numericalSolutions.filter(
          (x2) => x2 >= minVar && x2 <= maxVar
        );
        let allSolutions = numericalSolutions.map((x2) => me$3.fromAst(x2));
        return { setValue: { allSolutions } };
      }
    };
    stateVariableDefinitions.numSolutions = {
      description: "The number of numerical solutions found.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        allSolutions: {
          dependencyType: "stateVariable",
          variableName: "allSolutions"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numSolutions: dependencyValues.allSolutions.length
          }
        };
      }
    };
    stateVariableDefinitions.solutions = {
      description: "The numerical solutions of the equations.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      isArray: true,
      entryPrefixes: ["solution"],
      returnArraySizeDependencies: () => ({
        numSolutions: {
          dependencyType: "stateVariable",
          variableName: "numSolutions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numSolutions];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allSolutions: {
            dependencyType: "stateVariable",
            variableName: "allSolutions"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let solutions = {};
        for (let key = 0; key < globalDependencyValues.__array_size[0]; key++) {
          solutions[key] = globalDependencyValues.allSolutions[key];
        }
        return { setValue: { solutions } };
      }
    };
    return stateVariableDefinitions;
  }
}
class PostponeRenderContainer extends Group {
  static componentType = "_postponeRenderContainer";
  static componentDocs = {
    summary: "Internal container that postpones rendering of its children."
  };
  static inSchemaOnlyInheritAs = [];
  static allowInSchemaAsComponent = void 0;
  static renderedDefault = false;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.rendered;
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.rendered = {
      returnDependencies: () => ({
        parentRendered: {
          dependencyType: "parentStateVariable",
          variableName: "rendered"
        }
      }),
      markStale: () => ({ updateReplacements: true }),
      definition({ dependencyValues }) {
        let rendered = Boolean(dependencyValues.parentRendered);
        return { setValue: { rendered } };
      }
    };
    return stateVariableDefinitions;
  }
  static async calculateReplacementChanges({
    component,
    componentInfoObjects,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    if (await component.stateValues.rendered && component.replacements.length === 0) {
      let replacementResults = await this.createSerializedReplacements({
        component,
        componentInfoObjects,
        nComponents,
        workspace
      });
      let replacements = replacementResults.replacements;
      diagnostics.push(...replacementResults.diagnostics);
      nComponents = replacementResults.nComponents;
      if (replacements.length > 0) {
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: 0,
          numberReplacementsToReplace: 0,
          serializedReplacements: replacements
        };
        return {
          replacementChanges: [replacementInstruction],
          diagnostics,
          nComponents
        };
      } else {
        return { replacementChanges: [], diagnostics, nComponents };
      }
    } else {
      return { replacementChanges: [], diagnostics, nComponents };
    }
  }
}
class SubsetOfReals extends MathComponent {
  static componentType = "subsetOfReals";
  static componentDocs = {
    summary: "A subset of the real numbers, defined by intervals and points"
  };
  static rendererType = "math";
  // used when creating new component via adapter or copy prop
  static primaryStateVariableForDefinition = "subsetValue";
  static stateVariableToBeShadowed = "subsetValue";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.createIntervals.defaultValue = true;
    attributes.variable = {
      createComponentOfType: "_variableName",
      createStateVariable: "variable",
      defaultValue: me$3.fromAst("x"),
      description: "Name of the variable used when expressing the subset."
    };
    attributes.displayMode = {
      description: "How the subset is displayed.",
      createComponentOfType: "text",
      createStateVariable: "displayMode",
      defaultValue: "intervals",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "intervals",
          description: "Display the subset using interval notation (e.g., `(0, 1]`)."
        },
        {
          value: "inequalities",
          description: "Display the subset using inequalities (e.g., `0 < x ≤ 1`)."
        }
      ]
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "unnormalizedValue",
      newName: "unnormalizedValuePreliminary"
    });
    stateVariableDefinitions.value.shadowingInstructions.createComponentOfType = "math";
    stateVariableDefinitions.haveSingleSubsetChild = {
      returnDependencies: () => ({
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"]
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        let haveSingleSubsetChild = dependencyValues.mathChildren.length === 1 && dependencyValues.mathChildren.filter(
          (child) => componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "subsetOfReals"
          })
        ).length === 1;
        return { setValue: { haveSingleSubsetChild } };
      }
    };
    stateVariableDefinitions.subsetValue = {
      stateVariablesDeterminingDependencies: ["haveSingleSubsetChild"],
      returnDependencies({ stateValues }) {
        let dependencies = {
          haveSingleSubsetChild: {
            dependencyType: "stateVariable",
            variableName: "haveSingleSubsetChild"
          }
        };
        if (stateValues.haveSingleSubsetChild) {
          dependencies.subsetChild = {
            dependencyType: "child",
            childGroups: ["maths"],
            variableNames: ["subsetValue"]
          };
        } else {
          dependencies.unnormalizedValuePreliminary = {
            dependencyType: "stateVariable",
            variableName: "unnormalizedValuePreliminary"
          };
          dependencies.variable = {
            dependencyType: "stateVariable",
            variableName: "variable"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let subsetValue;
        if (dependencyValues.haveSingleSubsetChild) {
          subsetValue = dependencyValues.subsetChild[0].stateValues.subsetValue;
        } else {
          subsetValue = buildSubsetFromMathExpression(
            dependencyValues.unnormalizedValuePreliminary,
            dependencyValues.variable
          );
        }
        return { setValue: { subsetValue } };
      },
      async inverseDefinition({
        desiredStateVariableValues,
        dependencyValues,
        stateValues
      }) {
        if (dependencyValues.haveSingleSubsetChild) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "subsetChild",
                desiredValue: desiredStateVariableValues.subsetValue,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          let mathExpression = mathExpressionFromSubsetValue({
            subsetValue: desiredStateVariableValues.subsetValue,
            variable: dependencyValues.variable,
            displayMode: await stateValues.displayMode
          });
          return {
            success: true,
            instructions: [
              {
                setDependency: "unnormalizedValuePreliminary",
                desiredValue: mathExpression
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.unnormalizedValue = {
      returnDependencies: () => ({
        subsetValue: {
          dependencyType: "stateVariable",
          variableName: "subsetValue"
        },
        displayMode: {
          dependencyType: "stateVariable",
          variableName: "displayMode"
        },
        variable: {
          dependencyType: "stateVariable",
          variableName: "variable"
        }
      }),
      definition({ dependencyValues }) {
        let unnormalizedValue = mathExpressionFromSubsetValue(dependencyValues);
        return { setValue: { unnormalizedValue } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let subsetValue = buildSubsetFromMathExpression(
          desiredStateVariableValues.unnormalizedValue,
          dependencyValues.variable
        );
        return {
          success: true,
          instructions: [
            {
              setDependency: "subsetValue",
              desiredValue: subsetValue
            }
          ]
        };
      }
    };
    const pointAndIntervalDefinitions = returnPointsIntervalsFromSubsetStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, pointAndIntervalDefinitions);
    return stateVariableDefinitions;
  }
}
function returnPointsIntervalsFromSubsetStateVariableDefinitions() {
  let stateVariableDefinitions = {};
  stateVariableDefinitions.pointsFromSubset = {
    additionalStateVariablesDefined: ["intervalsFromSubset"],
    returnDependencies: () => ({
      subsetValue: {
        dependencyType: "stateVariable",
        variableName: "subsetValue"
      }
    }),
    definition({ dependencyValues }) {
      let { points, intervals } = determinePointAndIntervalsFromSubset(
        dependencyValues.subsetValue
      );
      return {
        setValue: {
          pointsFromSubset: points,
          intervalsFromSubset: intervals
        }
      };
    }
  };
  stateVariableDefinitions.points = {
    additionalStateVariablesDefined: [
      {
        variableName: "pointsClosed",
        public: true,
        isArray: true,
        shadowingInstructions: {
          createComponentOfType: "boolean"
        },
        description: "Whether each boundary point of the subset is a closed (included) endpoint."
      }
    ],
    description: "The boundary points that delimit the subset.",
    public: true,
    isArray: true,
    shadowingInstructions: {
      createComponentOfType: "math"
    },
    isArray: true,
    entryPrefixes: ["point"],
    returnArraySizeDependencies: () => ({
      pointsFromSubset: {
        dependencyType: "stateVariable",
        variableName: "pointsFromSubset"
      }
    }),
    returnArraySize({ dependencyValues }) {
      return [dependencyValues.pointsFromSubset.length];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        pointsFromSubset: {
          dependencyType: "stateVariable",
          variableName: "pointsFromSubset"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
      let points = {};
      let pointsClosed = {};
      for (let arrayKey of arrayKeys) {
        let point = globalDependencyValues.pointsFromSubset[arrayKey];
        points[arrayKey] = me$3.fromAst(point.value);
        pointsClosed[arrayKey] = point.inSubset;
      }
      return { setValue: { points, pointsClosed } };
    }
  };
  stateVariableDefinitions.allIntervals = {
    additionalStateVariablesDefined: ["allIsolatedPoints"],
    returnDependencies: () => ({
      subsetValue: {
        dependencyType: "stateVariable",
        variableName: "subsetValue"
      }
    }),
    definition({ dependencyValues }) {
      let allIntervals = [];
      let allIsolatedPoints = [];
      let pieces = mergeIntervals(dependencyValues.subsetValue);
      for (let subset2 of pieces) {
        if (subset2.type === "closedInterval") {
          allIntervals.push(
            me$3.fromAst([
              "interval",
              ["tuple", subset2.left, subset2.right],
              ["tuple", true, true]
            ])
          );
        } else if (subset2.type === "openClosedInterval") {
          allIntervals.push(
            me$3.fromAst([
              "interval",
              ["tuple", subset2.left, subset2.right],
              ["tuple", false, true]
            ])
          );
        } else if (subset2.type === "closedOpenInterval") {
          allIntervals.push(
            me$3.fromAst([
              "interval",
              ["tuple", subset2.left, subset2.right],
              ["tuple", true, false]
            ])
          );
        } else if (subset2.type === "openInterval") {
          allIntervals.push(
            me$3.fromAst([
              "interval",
              ["tuple", subset2.left, subset2.right],
              ["tuple", false, false]
            ])
          );
        } else if (subset2.type === "realLine") {
          allIntervals.push(
            me$3.fromAst([
              "interval",
              ["tuple", -Infinity, Infinity],
              ["tuple", false, false]
            ])
          );
        } else if (subset2.type === "singleton") {
          allIsolatedPoints.push(subset2.element);
        }
      }
      return { setValue: { allIntervals, allIsolatedPoints } };
    }
  };
  stateVariableDefinitions.intervals = {
    description: "The intervals (non-degenerate components) of the subset.",
    public: true,
    isArray: true,
    shadowingInstructions: {
      createComponentOfType: "math"
    },
    isArray: true,
    entryPrefixes: ["interval"],
    returnArraySizeDependencies: () => ({
      allIntervals: {
        dependencyType: "stateVariable",
        variableName: "allIntervals"
      }
    }),
    returnArraySize({ dependencyValues }) {
      return [dependencyValues.allIntervals.length];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        allIntervals: {
          dependencyType: "stateVariable",
          variableName: "allIntervals"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({ globalDependencyValues }) {
      return {
        setValue: { intervals: globalDependencyValues.allIntervals }
      };
    }
  };
  stateVariableDefinitions.isolatedPoints = {
    description: "The isolated (non-interval) points of the subset.",
    public: true,
    isArray: true,
    shadowingInstructions: {
      createComponentOfType: "math"
    },
    isArray: true,
    entryPrefixes: ["isolatedPoint"],
    returnArraySizeDependencies: () => ({
      allIsolatedPoints: {
        dependencyType: "stateVariable",
        variableName: "allIsolatedPoints"
      }
    }),
    returnArraySize({ dependencyValues }) {
      return [dependencyValues.allIsolatedPoints.length];
    },
    returnArrayDependenciesByKey() {
      let globalDependencies = {
        allIsolatedPoints: {
          dependencyType: "stateVariable",
          variableName: "allIsolatedPoints"
        }
      };
      return { globalDependencies };
    },
    arrayDefinitionByKey({ globalDependencyValues }) {
      return {
        setValue: {
          isolatedPoints: globalDependencyValues.allIsolatedPoints
        }
      };
    }
  };
  return stateVariableDefinitions;
}
function determinePointAndIntervalsFromSubset(subset2) {
  function mergePointsIntervals(result1, result2) {
    let points2 = [];
    let intervals2 = [];
    if (result1.points) {
      points2 = result1.points;
    }
    if (result1.intervals) {
      intervals2 = result1.intervals;
    }
    if (result2.points) {
      let valuesIn1 = points2.map((x2) => x2.value);
      for (let pt2 of result2.points) {
        let indIn1 = valuesIn1.indexOf(pt2.value);
        if (indIn1 === -1) {
          points2.push(pt2);
        } else {
          points2[indIn1].inSubset ||= pt2.inSubset;
        }
      }
    }
    if (result2.intervals) {
      intervals2.push(...result2.intervals);
    }
    return { points: points2, intervals: intervals2 };
  }
  function pointsIntervalsFromSubset(subset3) {
    if (subset3 === null || subset3.isEmpty()) {
      return {};
    }
    if (subset3.type === "union") {
      let points2 = [];
      let intervals2 = [];
      for (let sub2 of subset3.subsets) {
        let result = pointsIntervalsFromSubset(sub2);
        ({ points: points2, intervals: intervals2 } = mergePointsIntervals(
          { points: points2, intervals: intervals2 },
          result
        ));
      }
      return { points: points2, intervals: intervals2 };
    } else if (subset3.type === "realLine") {
      return { intervals: [[-Infinity, Infinity]] };
    } else if (subset3.type === "singleton") {
      return {
        points: [{ value: subset3.element, inSubset: true }]
      };
    } else if (subset3.type === "openInterval") {
      let intervals2 = [[subset3.left, subset3.right]];
      let points2 = [];
      if (Number.isFinite(subset3.left)) {
        points2.push({
          value: subset3.left,
          inSubset: false
        });
      }
      if (Number.isFinite(subset3.right)) {
        points2.push({
          value: subset3.right,
          inSubset: false
        });
      }
      return { intervals: intervals2, points: points2 };
    }
    return {};
  }
  let { points, intervals } = pointsIntervalsFromSubset(subset2);
  points ||= [];
  intervals ||= [];
  points.sort((a2, b2) => a2.value - b2.value);
  intervals.sort((a2, b2) => a2[0] - b2[0]);
  return { points, intervals };
}
class SubsetOfRealsInput extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      addPoint: this.addPoint.bind(this),
      deletePoint: this.deletePoint.bind(this),
      movePoint: this.movePoint.bind(this),
      togglePoint: this.togglePoint.bind(this),
      toggleInterval: this.toggleInterval.bind(this),
      clear: this.clear.bind(this),
      setToR: this.setToR.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "subsetOfRealsInput";
  static componentDocs = {
    summary: "An interactive number-line widget for entering a subset of real numbers (block-level; does not inherit from Input)"
  };
  static variableForImplicitProp = "subsetValue";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.xMin = {
      description: "Left endpoint of the visible number line.",
      createComponentOfType: "number",
      createStateVariable: "xMin",
      defaultValue: -10,
      public: true,
      forRenderer: true,
      excludeFromSchema: true
    };
    attributes.xMax = {
      description: "Right endpoint of the visible number line.",
      createComponentOfType: "number",
      createStateVariable: "xMax",
      defaultValue: 10,
      public: true,
      forRenderer: true,
      excludeFromSchema: true
    };
    attributes.width = {
      description: "Display width of the input.",
      createComponentOfType: "componentSize",
      createStateVariable: "width",
      defaultValue: 800,
      public: true,
      forRenderer: true,
      excludeFromSchema: true
    };
    attributes.height = {
      description: "Display height of the input.",
      createComponentOfType: "componentSize",
      createStateVariable: "height",
      defaultValue: 300,
      public: true,
      forRenderer: true,
      excludeFromSchema: true
    };
    attributes.xlabel = {
      description: "Label rendered for the x axis.",
      createComponentOfType: "text",
      createStateVariable: "xlabel",
      defaultValue: "",
      public: true,
      forRenderer: true,
      excludeFromSchema: true
    };
    attributes.dx = {
      description: "Tick spacing along the number line.",
      createComponentOfType: "number",
      createStateVariable: "dx",
      defaultValue: 0.5,
      public: true,
      excludeFromSchema: true
    };
    attributes.variable = {
      description: "Variable name displayed on the number line.",
      createComponentOfType: "_variableName",
      createStateVariable: "variable",
      defaultValue: me$3.fromAst("x"),
      public: true
    };
    attributes.format = {
      description: "Input format used for parsing entered subsets.",
      createComponentOfType: "text",
      createStateVariable: "format",
      defaultValue: "text",
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "text",
          description: "Parse entered subsets as plain-text notation."
        },
        {
          value: "latex",
          description: "Parse entered subsets as LaTeX notation."
        }
      ]
    };
    attributes.prefill = {
      description: "Initial subset displayed in the input.",
      createComponentOfType: "text",
      createStateVariable: "prefill",
      defaultValue: "",
      public: true
    };
    attributes.bindValueTo = {
      createComponentOfType: "subsetOfReals",
      description: "Two-way binding target for the input's value."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.subsetValue = {
      description: "The current subset of the reals entered by the user.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "subsetOfReals",
        addAttributeComponentsShadowingStateVariables: {
          variable: { stateVariableToShadow: "variable" }
        }
      },
      hasEssential: true,
      returnDependencies: () => ({
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["subsetValue"]
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        variable: {
          dependencyType: "stateVariable",
          variableName: "variable"
        },
        format: {
          dependencyType: "stateVariable",
          variableName: "format"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!dependencyValues.bindValueTo) {
          return {
            useEssentialOrDefaultValue: {
              subsetValue: {
                get defaultValue() {
                  return parseValueIntoSubset({
                    inputString: dependencyValues.prefill,
                    format: dependencyValues.format,
                    variable: dependencyValues.variable
                  });
                }
              }
            }
          };
        }
        return {
          setValue: {
            subsetValue: dependencyValues.bindValueTo.stateValues.subsetValue
          }
        };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.bindValueTo) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "bindValueTo",
                desiredValue: desiredStateVariableValues.subsetValue,
                variableIndex: 0
              }
            ]
          };
        }
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "subsetValue",
              value: desiredStateVariableValues.subsetValue
            }
          ]
        };
      }
    };
    stateVariableDefinitions.additionalPoints = {
      defaultValue: [],
      hasEssential: true,
      returnDependencies: () => ({}),
      definition() {
        return {
          useEssentialOrDefaultValue: {
            additionalPoints: true
          }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        if (desiredStateVariableValues.additionalPoints.every(
          Number.isFinite
        )) {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "additionalPoints",
                value: [
                  ...desiredStateVariableValues.additionalPoints
                ].sort((a2, b2) => a2 - b2)
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.pointsDisplayed = {
      additionalStateVariablesDefined: [
        { variableName: "intervalsDisplayed", forRenderer: true }
      ],
      forRenderer: true,
      returnDependencies: () => ({
        pointsFromSubset: {
          dependencyType: "stateVariable",
          variableName: "pointsFromSubset"
        },
        intervalsFromSubset: {
          dependencyType: "stateVariable",
          variableName: "intervalsFromSubset"
        },
        additionalPoints: {
          dependencyType: "stateVariable",
          variableName: "additionalPoints"
        }
      }),
      definition({ dependencyValues }) {
        let pointsSub = [...dependencyValues.pointsFromSubset];
        let intervalsSub = [...dependencyValues.intervalsFromSubset];
        let additionalPoints = [
          ...dependencyValues.additionalPoints
        ].sort((a2, b2) => a2 - b2);
        let pointsDisplayed = [];
        let intervalsDisplayed = [];
        let intervalInd = 0;
        let nextAdditionalPoint = Infinity;
        let additionalPointInd = 0;
        if (additionalPoints.length > 0) {
          nextAdditionalPoint = additionalPoints[0];
        }
        let nextInterval = intervalsSub[0];
        let lastIntervalEnd = -Infinity;
        pointsSub.push({ value: Infinity, inSubset: false });
        for (let point of pointsSub) {
          let inSubset = false;
          if (nextInterval && nextInterval[0] < point.value) {
            inSubset = true;
          }
          while (nextAdditionalPoint < point.value) {
            if (nextAdditionalPoint !== lastIntervalEnd) {
              pointsDisplayed.push({
                value: nextAdditionalPoint,
                inSubset,
                isAdditional: true,
                additionalPointInd
              });
              intervalsDisplayed.push({
                left: lastIntervalEnd,
                right: nextAdditionalPoint,
                inSubset
              });
              lastIntervalEnd = nextAdditionalPoint;
            }
            additionalPointInd++;
            nextAdditionalPoint = additionalPoints[additionalPointInd];
            if (nextAdditionalPoint === void 0) {
              nextAdditionalPoint = Infinity;
            }
          }
          pointsDisplayed.push(point);
          intervalsDisplayed.push({
            left: lastIntervalEnd,
            right: point.value,
            inSubset
          });
          lastIntervalEnd = point.value;
          if (inSubset) {
            intervalInd++;
            nextInterval = intervalsSub[intervalInd];
          }
        }
        pointsDisplayed = pointsDisplayed.slice(
          0,
          pointsDisplayed.length - 1
        );
        return {
          setValue: { pointsDisplayed, intervalsDisplayed }
        };
      }
    };
    const pointAndIntervalDefinitions = returnPointsIntervalsFromSubsetStateVariableDefinitions();
    Object.assign(stateVariableDefinitions, pointAndIntervalDefinitions);
    return stateVariableDefinitions;
  }
  async addPoint({
    value,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let dx = await this.stateValues.dx;
    let roundedValue = Math.round(
      Math.max(
        await this.stateValues.xMin,
        Math.min(await this.stateValues.xMax, value)
      ) / dx
    ) * dx;
    let pointsFromSubset = [...await this.stateValues.pointsFromSubset];
    let intervalsFromSubset = [
      ...await this.stateValues.intervalsFromSubset
    ];
    let additionalPoints = [...await this.stateValues.additionalPoints];
    if (!additionalPoints.includes(roundedValue)) {
      let subsetPointValues = pointsFromSubset.map((x2) => x2.value);
      if (!subsetPointValues.includes(roundedValue)) {
        let insideInterval = false;
        for (let interval of intervalsFromSubset) {
          if (interval[0] < roundedValue && interval[1] > roundedValue) {
            insideInterval = true;
            break;
          }
        }
        if (insideInterval) {
          additionalPoints.push(roundedValue);
          return await this.coreFunctions.performUpdate({
            updateInstructions: [
              {
                componentIdx: this.componentIdx,
                updateType: "updateValue",
                stateVariable: "additionalPoints",
                value: additionalPoints
              }
            ],
            actionId,
            sourceInformation,
            skipRendererUpdate,
            event: {
              verb: "interacted",
              object: {
                componentIdx: this.componentIdx,
                componentType: this.componentType
              },
              result: {
                addedPoint: roundedValue,
                intervalsFromSubset,
                pointsFromSubset,
                additionalPoints
              }
            }
          });
        } else {
          pointsFromSubset.push({
            value: roundedValue,
            inSubset: true
          });
          let updateInstructions = await this.createUpdateInstructions({
            intervalsFromSubset,
            pointsFromSubset,
            modifiedAdditionalPoints: false,
            additionalPoints
          });
          return await this.coreFunctions.performUpdate({
            updateInstructions,
            actionId,
            sourceInformation,
            skipRendererUpdate,
            event: {
              verb: "interacted",
              object: {
                componentIdx: this.componentIdx,
                componentType: this.componentType
              },
              result: {
                addedPoint: roundedValue,
                intervalsFromSubset,
                pointsFromSubset,
                additionalPoints
              }
            }
          });
        }
      }
    }
  }
  async deletePoint({
    pointInd,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let point = await this.stateValues.pointsDisplayed[pointInd];
    let additionalPoints = [...await this.stateValues.additionalPoints];
    let pointsFromSubset = [...await this.stateValues.pointsFromSubset];
    let intervalsFromSubset = [
      ...await this.stateValues.intervalsFromSubset
    ];
    if (point.isAdditional) {
      additionalPoints.splice(point.additionalPointInd, 1);
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            componentIdx: this.componentIdx,
            updateType: "updateValue",
            stateVariable: "additionalPoints",
            value: additionalPoints
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            deletedPoint: point.value,
            intervalsFromSubset,
            pointsFromSubset,
            additionalPoints
          }
        }
      });
    } else {
      let modifiedAdditionalPoints = false;
      let pointSubsetInd = pointsFromSubset.map((x2) => x2.value).indexOf(point.value);
      let leftIntervalInd = intervalsFromSubset.map((x2) => x2[1]).indexOf(point.value);
      let rightIntervalInd = intervalsFromSubset.map((x2) => x2[0]).indexOf(point.value);
      if (leftIntervalInd !== -1) {
        if (rightIntervalInd !== -1) {
          intervalsFromSubset[leftIntervalInd] = [
            intervalsFromSubset[leftIntervalInd][0],
            intervalsFromSubset[rightIntervalInd][1]
          ];
          intervalsFromSubset.splice(rightIntervalInd, 1);
          pointsFromSubset.splice(pointSubsetInd, 1);
        } else {
          let leftPoint = (await this.stateValues.pointsDisplayed)[pointInd - 1];
          if (leftPoint && leftPoint.isAdditional) {
            intervalsFromSubset[leftIntervalInd] = [
              intervalsFromSubset[leftIntervalInd][0],
              leftPoint.value
            ];
            additionalPoints.splice(
              leftPoint.additionalPointInd,
              1
            );
            modifiedAdditionalPoints = true;
            pointsFromSubset.splice(pointSubsetInd, 1, leftPoint);
          } else {
            intervalsFromSubset.splice(leftIntervalInd, 1);
            pointsFromSubset.splice(pointSubsetInd, 1);
            if (leftPoint && !leftPoint.inSubset) {
              let leftLeftIntervalInd = intervalsFromSubset.map((x2) => x2[1]).indexOf(leftPoint.value);
              if (leftLeftIntervalInd === -1) {
                additionalPoints.push(leftPoint.value);
                modifiedAdditionalPoints = true;
              }
            }
          }
        }
      } else {
        if (rightIntervalInd !== -1) {
          let rightPoint = (await this.stateValues.pointsDisplayed)[pointInd + 1];
          if (rightPoint && rightPoint.isAdditional) {
            intervalsFromSubset[rightIntervalInd] = [
              rightPoint.value,
              intervalsFromSubset[rightIntervalInd][1]
            ];
            additionalPoints.splice(
              rightPoint.additionalPointInd,
              1
            );
            modifiedAdditionalPoints = true;
            pointsFromSubset.splice(pointSubsetInd, 1, rightPoint);
          } else {
            intervalsFromSubset.splice(rightIntervalInd, 1);
            pointsFromSubset.splice(pointSubsetInd, 1);
            if (rightPoint && !rightPoint.inSubset) {
              let rightRightIntervalInd = intervalsFromSubset.map((x2) => x2[0]).indexOf(rightPoint.value);
              if (rightRightIntervalInd === -1) {
                additionalPoints.push(rightPoint.value);
                modifiedAdditionalPoints = true;
              }
            }
          }
        } else {
          pointsFromSubset.splice(pointSubsetInd, 1);
        }
      }
      let updateInstructions = await this.createUpdateInstructions({
        intervalsFromSubset,
        pointsFromSubset,
        modifiedAdditionalPoints,
        additionalPoints
      });
      return await this.coreFunctions.performUpdate({
        updateInstructions,
        actionId,
        sourceInformation,
        skipRendererUpdate,
        event: {
          verb: "interacted",
          object: {
            componentIdx: this.componentIdx,
            componentType: this.componentType
          },
          result: {
            deletedPoint: point.value,
            intervalsFromSubset,
            pointsFromSubset,
            additionalPoints
          }
        }
      });
    }
  }
  async createUpdateInstructions({
    intervalsFromSubset,
    pointsFromSubset,
    modifiedAdditionalPoints,
    additionalPoints
  }) {
    let dx = await this.stateValues.dx;
    let roundValue = (x2) => Math.round(x2 / dx) * dx;
    let pieces = [];
    for (let interval of intervalsFromSubset) {
      pieces.push(
        subsetOfReals.OpenInterval(
          roundValue(interval[0]),
          roundValue(interval[1])
        )
      );
    }
    for (let point of pointsFromSubset) {
      if (point.inSubset) {
        pieces.push(subsetOfReals.Singleton(roundValue(point.value)));
      }
    }
    let newSubset;
    if (pieces.length === 0) {
      newSubset = subsetOfReals.EmptySet();
    } else if (pieces.length === 1) {
      newSubset = pieces[0];
    } else {
      newSubset = subsetOfReals.Union(pieces);
    }
    let updateInstructions = [
      {
        componentIdx: this.componentIdx,
        updateType: "updateValue",
        stateVariable: "subsetValue",
        value: newSubset
      }
    ];
    if (modifiedAdditionalPoints) {
      updateInstructions.push({
        componentIdx: this.componentIdx,
        updateType: "updateValue",
        stateVariable: "additionalPoints",
        value: additionalPoints.map(roundValue)
      });
    }
    return updateInstructions;
  }
  async movePoint({
    pointInd,
    value,
    transient,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let dx = await this.stateValues.dx;
    let roundedValue = Math.round(
      Math.max(
        await this.stateValues.xMin,
        Math.min(await this.stateValues.xMax, value)
      ) / dx
    ) * dx;
    let pointsDisplayed = await this.stateValues.pointsDisplayed;
    let point = pointsDisplayed[pointInd];
    let leftPoint = pointsDisplayed[pointInd - 1];
    if (leftPoint) {
      roundedValue = Math.max(roundedValue, leftPoint.value + dx);
    }
    let rightPoint = pointsDisplayed[pointInd + 1];
    if (rightPoint) {
      roundedValue = Math.min(roundedValue, rightPoint.value - dx);
    }
    let additionalPoints = [...await this.stateValues.additionalPoints];
    let pointsFromSubset = [...await this.stateValues.pointsFromSubset];
    let intervalsFromSubset = [
      ...await this.stateValues.intervalsFromSubset
    ];
    if (point.isAdditional) {
      additionalPoints[point.additionalPointInd] = roundedValue;
      if (transient) {
        return await this.coreFunctions.performUpdate({
          updateInstructions: [
            {
              componentIdx: this.componentIdx,
              updateType: "updateValue",
              stateVariable: "additionalPoints",
              value: additionalPoints
            }
          ],
          transient: true,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      } else {
        return await this.coreFunctions.performUpdate({
          updateInstructions: [
            {
              componentIdx: this.componentIdx,
              updateType: "updateValue",
              stateVariable: "additionalPoints",
              value: additionalPoints
            }
          ],
          actionId,
          sourceInformation,
          skipRendererUpdate,
          event: {
            verb: "interacted",
            object: {
              componentIdx: this.componentIdx,
              componentType: this.componentType
            },
            result: {
              movedPoint: roundedValue,
              intervalsFromSubset,
              pointsFromSubset,
              additionalPoints
            }
          }
        });
      }
    } else {
      let pointSubsetInd = pointsFromSubset.map((x2) => x2.value).indexOf(point.value);
      let leftIntervalInd = intervalsFromSubset.map((x2) => x2[1]).indexOf(point.value);
      let rightIntervalInd = intervalsFromSubset.map((x2) => x2[0]).indexOf(point.value);
      pointsFromSubset[pointSubsetInd] = {
        value: roundedValue,
        inSubset: pointsFromSubset[pointSubsetInd].inSubset
      };
      if (leftIntervalInd !== -1) {
        intervalsFromSubset[leftIntervalInd] = [
          intervalsFromSubset[leftIntervalInd][0],
          roundedValue
        ];
      }
      if (rightIntervalInd !== -1) {
        intervalsFromSubset[rightIntervalInd] = [
          roundedValue,
          intervalsFromSubset[rightIntervalInd][1]
        ];
      }
      let updateInstructions = await this.createUpdateInstructions({
        intervalsFromSubset,
        pointsFromSubset,
        modifiedAdditionalPoints: false
      });
      if (transient) {
        return await this.coreFunctions.performUpdate({
          updateInstructions,
          transient: true,
          actionId,
          sourceInformation,
          skipRendererUpdate
        });
      } else {
        return await this.coreFunctions.performUpdate({
          updateInstructions,
          actionId,
          sourceInformation,
          skipRendererUpdate,
          event: {
            verb: "interacted",
            object: {
              componentIdx: this.componentIdx,
              componentType: this.componentType
            },
            result: {
              movedPoint: roundedValue,
              intervalsFromSubset,
              pointsFromSubset,
              additionalPoints
            }
          }
        });
      }
    }
  }
  async togglePoint({
    pointInd,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let point = (await this.stateValues.pointsDisplayed)[pointInd];
    let pointsFromSubset = [...await this.stateValues.pointsFromSubset];
    let intervalsFromSubset = [
      ...await this.stateValues.intervalsFromSubset
    ];
    let additionalPoints = [...await this.stateValues.additionalPoints];
    let modifiedAdditionalPoints = false;
    if (point.isAdditional) {
      additionalPoints.splice(point.additionalPointInd, 1);
      modifiedAdditionalPoints = true;
      if (point.inSubset) {
        for (let [ind, interval] of intervalsFromSubset.entries()) {
          if (interval[0] < point.value && interval[1] > point.value) {
            let newIntervals = [
              [interval[0], point.value],
              [point.value, interval[1]]
            ];
            intervalsFromSubset.splice(ind, 1, ...newIntervals);
            break;
          }
        }
      } else {
        pointsFromSubset.push({
          value: point.value,
          inSubset: true
        });
      }
    } else {
      let pointSubsetInd = pointsFromSubset.map((x2) => x2.value).indexOf(point.value);
      let leftIntervalInd = intervalsFromSubset.map((x2) => x2[1]).indexOf(point.value);
      let rightIntervalInd = intervalsFromSubset.map((x2) => x2[0]).indexOf(point.value);
      if (leftIntervalInd !== -1) {
        if (rightIntervalInd !== -1) {
          intervalsFromSubset[leftIntervalInd] = [
            intervalsFromSubset[leftIntervalInd][0],
            intervalsFromSubset[rightIntervalInd][1]
          ];
          intervalsFromSubset.splice(rightIntervalInd, 1);
          pointsFromSubset.splice(pointSubsetInd, 1);
          additionalPoints.push(point.value);
          modifiedAdditionalPoints = true;
        } else {
          pointsFromSubset[pointSubsetInd] = {
            value: point.value,
            inSubset: !point.inSubset
          };
        }
      } else {
        if (rightIntervalInd !== -1) {
          pointsFromSubset[pointSubsetInd] = {
            value: point.value,
            inSubset: !point.inSubset
          };
        } else {
          pointsFromSubset.splice(pointSubsetInd, 1);
          additionalPoints.push(point.value);
          modifiedAdditionalPoints = true;
        }
      }
    }
    let updateInstructions = await this.createUpdateInstructions({
      intervalsFromSubset,
      pointsFromSubset,
      modifiedAdditionalPoints,
      additionalPoints
    });
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          toggledPoint: point.value,
          intervalsFromSubset,
          pointsFromSubset,
          additionalPoints
        }
      }
    });
  }
  async toggleInterval({
    intervalInd,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let interval = (await this.stateValues.intervalsDisplayed)[intervalInd];
    let pointsDisplayed = await this.stateValues.pointsDisplayed;
    let leftPoint = pointsDisplayed[intervalInd - 1];
    let rightPoint = pointsDisplayed[intervalInd];
    let pointsFromSubset = [...await this.stateValues.pointsFromSubset];
    let intervalsFromSubset = [
      ...await this.stateValues.intervalsFromSubset
    ];
    let additionalPoints = [...await this.stateValues.additionalPoints];
    let modifiedAdditionalPoints = false;
    if (interval.inSubset) {
      let intervalSubsetInd, intervalSubset;
      for (let [ind, interval2] of intervalsFromSubset.entries()) {
        if (interval2[0] <= interval.left && interval2[1] >= interval.right) {
          intervalSubset = interval2;
          intervalSubsetInd = ind;
          break;
        }
      }
      if (interval.left === intervalSubset[0]) {
        if (interval.right === intervalSubset[1]) {
          intervalsFromSubset.splice(intervalSubsetInd, 1);
          if (leftPoint && !leftPoint.inSubset) {
            let leftLeftIntervalInd = intervalsFromSubset.map((x2) => x2[1]).indexOf(leftPoint.value);
            if (leftLeftIntervalInd === -1) {
              let leftPointSubsetInd = pointsFromSubset.map((x2) => x2.value).indexOf(leftPoint.value);
              pointsFromSubset.splice(leftPointSubsetInd, 1);
              additionalPoints.push(leftPoint.value);
              modifiedAdditionalPoints = true;
            }
          }
          if (rightPoint && !rightPoint.inSubset) {
            let rightRightIntervalInd = intervalsFromSubset.map((x2) => x2[0]).indexOf(rightPoint.value);
            if (rightRightIntervalInd === -1) {
              let rightPointSubsetInd = pointsFromSubset.map((x2) => x2.value).indexOf(rightPoint.value);
              pointsFromSubset.splice(rightPointSubsetInd, 1);
              additionalPoints.push(rightPoint.value);
              modifiedAdditionalPoints = true;
            }
          }
        } else {
          intervalsFromSubset[intervalSubsetInd] = [
            interval.right,
            intervalSubset[1]
          ];
          if (leftPoint && !leftPoint.inSubset) {
            let leftLeftIntervalInd = intervalsFromSubset.map((x2) => x2[1]).indexOf(leftPoint.value);
            if (leftLeftIntervalInd === -1) {
              let leftPointSubsetInd = pointsFromSubset.map((x2) => x2.value).indexOf(leftPoint.value);
              pointsFromSubset.splice(leftPointSubsetInd, 1);
              additionalPoints.push(leftPoint.value);
              modifiedAdditionalPoints = true;
            }
          }
          pointsFromSubset.push({
            value: rightPoint.value,
            inSubset: true
          });
          additionalPoints.splice(rightPoint.additionalPointInd, 1);
          modifiedAdditionalPoints = true;
        }
      } else {
        if (interval.right === intervalSubset[1]) {
          intervalsFromSubset[intervalSubsetInd] = [
            intervalSubset[0],
            interval.left
          ];
          if (rightPoint && !rightPoint.inSubset) {
            let rightRightIntervalInd = intervalsFromSubset.map((x2) => x2[0]).indexOf(rightPoint.value);
            if (rightRightIntervalInd === -1) {
              let rightPointSubsetInd = pointsFromSubset.map((x2) => x2.value).indexOf(rightPoint.value);
              pointsFromSubset.splice(rightPointSubsetInd, 1);
              additionalPoints.push(rightPoint.value);
              modifiedAdditionalPoints = true;
            }
          }
          pointsFromSubset.push({
            value: leftPoint.value,
            inSubset: true
          });
          additionalPoints.splice(leftPoint.additionalPointInd, 1);
          modifiedAdditionalPoints = true;
        } else {
          let newIntervals = [
            [intervalSubset[0], interval.left],
            [interval.right, intervalSubset[1]]
          ];
          intervalsFromSubset.splice(
            intervalSubsetInd,
            1,
            ...newIntervals
          );
          pointsFromSubset.push({
            value: leftPoint.value,
            inSubset: true
          });
          pointsFromSubset.push({
            value: rightPoint.value,
            inSubset: true
          });
          additionalPoints.splice(rightPoint.additionalPointInd, 1);
          additionalPoints.splice(leftPoint.additionalPointInd, 1);
          modifiedAdditionalPoints = true;
        }
      }
    } else {
      let leftIntervalInd = intervalsFromSubset.map((x2) => x2[1]).indexOf(interval.left);
      let rightIntervalInd = intervalsFromSubset.map((x2) => x2[0]).indexOf(interval.right);
      if (leftIntervalInd === -1) {
        if (rightIntervalInd === -1) {
          intervalsFromSubset.push([interval.left, interval.right]);
          if (rightPoint && rightPoint.isAdditional) {
            additionalPoints.splice(
              rightPoint.additionalPointInd,
              1
            );
            modifiedAdditionalPoints = true;
          }
          if (leftPoint && leftPoint.isAdditional) {
            additionalPoints.splice(
              leftPoint.additionalPointInd,
              1
            );
            modifiedAdditionalPoints = true;
          }
        } else {
          if (rightPoint.inSubset) {
            intervalsFromSubset[rightIntervalInd] = [
              interval.left,
              intervalsFromSubset[rightIntervalInd][1]
            ];
            additionalPoints.push(rightPoint.value);
            modifiedAdditionalPoints = true;
          } else {
            intervalsFromSubset.push([
              interval.left,
              interval.right
            ]);
          }
          if (leftPoint && leftPoint.isAdditional) {
            additionalPoints.splice(
              leftPoint.additionalPointInd,
              1
            );
            modifiedAdditionalPoints = true;
          }
        }
      } else {
        if (rightIntervalInd === -1) {
          if (leftPoint.inSubset) {
            intervalsFromSubset[leftIntervalInd] = [
              intervalsFromSubset[leftIntervalInd][0],
              interval.right
            ];
            additionalPoints.push(leftPoint.value);
            modifiedAdditionalPoints = true;
          } else {
            intervalsFromSubset.push([
              interval.left,
              interval.right
            ]);
          }
          if (rightPoint && rightPoint.isAdditional) {
            additionalPoints.splice(
              rightPoint.additionalPointInd,
              1
            );
            modifiedAdditionalPoints = true;
          }
        } else {
          if (leftPoint.inSubset) {
            if (rightPoint.inSubset) {
              let newInterval = [
                intervalsFromSubset[leftIntervalInd][0],
                intervalsFromSubset[rightIntervalInd][1]
              ];
              intervalsFromSubset.splice(
                leftIntervalInd,
                2,
                newInterval
              );
              additionalPoints.push(leftPoint.value);
              additionalPoints.push(rightPoint.value);
              modifiedAdditionalPoints = true;
            } else {
              intervalsFromSubset[leftIntervalInd] = [
                intervalsFromSubset[leftIntervalInd][0],
                interval.right
              ];
              additionalPoints.push(leftPoint.value);
              modifiedAdditionalPoints = true;
            }
          } else {
            if (rightPoint.inSubset) {
              intervalsFromSubset[rightIntervalInd] = [
                interval.left,
                intervalsFromSubset[rightIntervalInd][1]
              ];
              additionalPoints.push(rightPoint.value);
              modifiedAdditionalPoints = true;
            } else {
              intervalsFromSubset.push([
                interval.left,
                interval.right
              ]);
            }
          }
        }
      }
    }
    let updateInstructions = await this.createUpdateInstructions({
      intervalsFromSubset,
      pointsFromSubset,
      modifiedAdditionalPoints,
      additionalPoints
    });
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          toggledInterval: [interval.left, interval.right],
          intervalsFromSubset,
          pointsFromSubset,
          additionalPoints
        }
      }
    });
  }
  async clear({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let updateInstructions = await this.createUpdateInstructions({
      intervalsFromSubset: [],
      pointsFromSubset: [],
      modifiedAdditionalPoints: true,
      additionalPoints: []
    });
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          cleared: true,
          intervalsFromSubset: [],
          pointsFromSubset: [],
          additionalPoints: []
        }
      }
    });
  }
  async setToR({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    let updateInstructions = await this.createUpdateInstructions({
      intervalsFromSubset: [[-Infinity, Infinity]],
      pointsFromSubset: [],
      modifiedAdditionalPoints: true,
      additionalPoints: []
    });
    return await this.coreFunctions.performUpdate({
      updateInstructions,
      actionId,
      sourceInformation,
      skipRendererUpdate,
      event: {
        verb: "interacted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType
        },
        result: {
          setToR: true,
          intervalsFromSubset: [[-Infinity, Infinity]],
          pointsFromSubset: [],
          additionalPoints: []
        }
      }
    });
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
function parseValueIntoSubset({ inputString, format: format2, variable }) {
  if (!inputString) {
    return subsetOfReals.EmptySet();
  }
  let expression;
  if (format2 === "latex") {
    try {
      expression = me$3.fromLatex(inputString);
    } catch (e3) {
      return subsetOfReals.EmptySet();
    }
  } else if (format2 === "text") {
    try {
      expression = me$3.fromText(inputString);
    } catch (e3) {
      return subsetOfReals.EmptySet();
    }
  }
  return buildSubsetFromMathExpression(expression, variable);
}
class Split extends CompositeComponent {
  static componentType = "split";
  static componentDocs = {
    summary: "Splits a string or list into pieces"
  };
  static allowInSchemaAsComponent = ["_inline", "_block", "_graphical"];
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.type = {
      createPrimitiveOfType: "string",
      createStateVariable: "type",
      defaultPrimitiveValue: "text",
      toLowerCase: true,
      validValues: [
        {
          value: "text",
          description: "Split text values."
        }
      ],
      description: "Type of value being split."
    };
    attributes.splitBy = {
      createComponentOfType: "text",
      createStateVariable: "splitBy",
      defaultValue: "letter",
      toLowerCase: true,
      validValues: [
        {
          value: "letter",
          description: "Split into individual characters."
        },
        {
          value: "word",
          description: "Split into whitespace-separated words."
        },
        {
          value: "comma",
          description: "Split on comma boundaries."
        }
      ],
      description: "How to break the input string into pieces."
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asList",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = [];
    function addType({
      matchedChildren,
      componentAttributes,
      nComponents,
      stateIdInfo
    }) {
      let type = componentAttributes.type?.value;
      if (!["text"].includes(type)) {
        type = "text";
      }
      return {
        success: true,
        newChildren: [
          {
            type: "serialized",
            componentType: type,
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: matchedChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ],
        nComponents
      };
    }
    sugarInstructions.push({
      replacementFunction: addType
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.originalValue = {
      returnDependencies: () => ({
        child: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.child.length > 0) {
          return {
            setValue: {
              originalValue: dependencyValues.child[0].stateValues.value
            }
          };
        } else {
          return { setValue: { originalValue: null } };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.child.length > 0) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "child",
                desiredValue: desiredStateVariableValues.originalValue,
                childIndex: 0,
                variableIndex: 0
              }
            ]
          };
        } else {
          return { success: false };
        }
      }
    };
    stateVariableDefinitions.allSplitValues = {
      returnDependencies: () => ({
        type: {
          dependencyType: "stateVariable",
          variableName: "type"
        },
        originalValue: {
          dependencyType: "stateVariable",
          variableName: "originalValue"
        },
        splitBy: {
          dependencyType: "stateVariable",
          variableName: "splitBy"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.originalValue === null) {
          return {
            setValue: { allSplitValues: [] }
          };
        }
        let allSplitValues = [];
        if (dependencyValues.splitBy === "letter") {
          allSplitValues = [...dependencyValues.originalValue];
        } else if (dependencyValues.splitBy === "word") {
          allSplitValues = dependencyValues.originalValue.split(/\s+/);
        } else if (dependencyValues.splitBy === "comma") {
          allSplitValues = dependencyValues.originalValue.split(/\s*,\s*/);
        } else {
          allSplitValues = [dependencyValues.originalValue];
        }
        return {
          setValue: { allSplitValues }
        };
      }
    };
    stateVariableDefinitions.numValues = {
      returnDependencies: () => ({
        allSplitValues: {
          dependencyType: "stateVariable",
          variableName: "allSplitValues"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numValues: dependencyValues.allSplitValues.length
          }
        };
      }
    };
    stateVariableDefinitions.values = {
      isArray: true,
      entryPrefixes: ["value"],
      returnArraySizeDependencies: () => ({
        numValues: {
          dependencyType: "stateVariable",
          variableName: "numValues"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numValues];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allSplitValues: {
            dependencyType: "stateVariable",
            variableName: "allSplitValues"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let values2 = {};
        for (let arrayKey of arrayKeys) {
          values2[arrayKey] = globalDependencyValues.allSplitValues[arrayKey];
        }
        return { setValue: { values: values2 } };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        values: {
          dependencyType: "stateVariable",
          variableName: "values"
        }
      }),
      // when this state variable is marked stale
      // it indicates we should update replacement
      // For this to work, must get value in replacement functions
      // so that the variable is marked fresh
      markStale: () => ({ updateReplacements: true }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    const diagnostics = [];
    const values2 = await component.stateValues.values;
    const replacements = values2.map((value, i2) => ({
      type: "serialized",
      componentType: "text",
      componentIdx: nComponents++,
      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
      attributes: {},
      doenetAttributes: {},
      children: [],
      state: { value },
      downstreamDependencies: {
        [component.componentIdx]: [
          {
            dependencyType: "referenceShadow",
            compositeIdx: component.componentIdx,
            propVariable: `value${i2 + 1}`
          }
        ]
      }
    }));
    workspace.values = [...values2];
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    components,
    workspace,
    nComponents
  }) {
    let diagnostics = [];
    const values2 = await component.stateValues.values;
    if (values2.length === workspace.values.length && workspace.values.every((s2, i2) => s2 === values2[i2])) {
      return { replacementChanges: [], diagnostics, nComponents };
    }
    let replacementResults = await this.createSerializedReplacements({
      component,
      components,
      workspace,
      nComponents
    });
    let replacements = replacementResults.replacements;
    diagnostics.push(...replacementResults.diagnostics);
    nComponents = replacementResults.nComponents;
    let replacementChanges = [
      {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: 0,
        numberReplacementsToReplace: component.replacements.length,
        serializedReplacements: replacements
      }
    ];
    return { replacementChanges, diagnostics, nComponents };
  }
}
const { matrix, transpose, multiply, lusolve, subset, index } = me$3.math;
class BestFitLine extends Line {
  static componentType = "bestFitLine";
  static componentDocs = {
    summary: "The best-fit line for a set of points"
  };
  static rendererType = "line";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.draggable;
    delete attributes.equation;
    delete attributes.through;
    delete attributes.slope;
    attributes.data = {
      createComponentOfType: "pointList",
      description: "The data points to fit a line to."
    };
    return attributes;
  }
  // Include children that can be added due to sugar (delete those from line)
  static additionalSchemaChildren = [];
  static returnSugarInstructions() {
    return [];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.draggable = {
      description: "Whether the line can be dragged (always false for a best-fit line).",
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      public: true,
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { draggable: false } })
    };
    stateVariableDefinitions.numDimensions = {
      description: "Number of dimensions of the line (always 2 for a best-fit line).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { numDimensions: 2 } })
    };
    delete stateVariableDefinitions.numPointsPrescribed;
    delete stateVariableDefinitions.basedOnSlope;
    delete stateVariableDefinitions.basedOnParallel;
    delete stateVariableDefinitions.basedOnPerpendicular;
    delete stateVariableDefinitions.distForSecondPt;
    delete stateVariableDefinitions.essentialPoint1x;
    delete stateVariableDefinitions.essentialPoint1y;
    delete stateVariableDefinitions.equationIdentity;
    delete stateVariableDefinitions.equation.stateVariablesDeterminingDependencies;
    stateVariableDefinitions.equation.returnDependencies = () => ({
      data: {
        dependencyType: "attributeComponent",
        attributeName: "data",
        variableNames: ["points", "numDimensions"]
      },
      variables: {
        dependencyType: "stateVariable",
        variableName: "variables"
      }
    });
    stateVariableDefinitions.equation.definition = function({
      dependencyValues
    }) {
      if (!dependencyValues.data || dependencyValues.data.stateValues.numDimensions < 2 || dependencyValues.data.stateValues.points.length < 1) {
        let blankMath2 = me$3.fromAst("＿");
        return {
          setValue: {
            equation: blankMath2,
            coeff0: blankMath2,
            coeffvar1: blankMath2,
            coeffvar2: blankMath2
          }
        };
      }
      let X2 = [];
      let Y2 = [];
      for (let pt2 of dependencyValues.data.stateValues.points) {
        let numericalX = pt2.slice(0, 2).map((x2) => x2 && x2.evaluate_to_constant());
        if (numericalX.every(Number.isFinite)) {
          X2.push([1, numericalX[0]]);
          Y2.push([numericalX[1]]);
        }
      }
      if (X2.length === 0) {
        let blankMath2 = me$3.fromAst("＿");
        return {
          setValue: {
            equation: blankMath2,
            coeff0: blankMath2,
            coeffvar1: blankMath2,
            coeffvar2: blankMath2
          }
        };
      }
      X2 = matrix(X2);
      Y2 = matrix(Y2);
      let Xt2 = transpose(X2);
      let b2 = multiply(Xt2, Y2);
      let A2 = multiply(Xt2, X2);
      let s2 = lusolve(A2, b2);
      let coeff0 = me$3.fromAst(subset(s2, index(0, 0)));
      let coeffvar1 = me$3.fromAst(subset(s2, index(1, 0)));
      let coeffvar2 = me$3.fromAst(-1);
      let variables = dependencyValues.variables;
      let rhs = me$3.fromAst(["+", ["*", "a", "x"], "c"]).substitute({
        a: coeffvar1,
        c: coeff0,
        x: variables[0]
      }).simplify();
      let equation = me$3.fromAst(["=", variables[1].tree, "r"]).substitute({
        r: rhs
      });
      return {
        setValue: {
          equation,
          coeff0,
          coeffvar1,
          coeffvar2
        }
      };
    };
    delete stateVariableDefinitions.equation.inverseDefinition;
    delete stateVariableDefinitions.points.stateVariablesDeterminingDependencies;
    stateVariableDefinitions.points.returnArrayDependenciesByKey = function() {
      let globalDependencies = {
        coeff0: {
          dependencyType: "stateVariable",
          variableName: "coeff0"
        },
        coeffvar1: {
          dependencyType: "stateVariable",
          variableName: "coeffvar1"
        },
        coeffvar2: {
          dependencyType: "stateVariable",
          variableName: "coeffvar2"
        },
        variables: {
          dependencyType: "stateVariable",
          variableName: "variables"
        },
        lastPointsFromInverting: {
          dependencyType: "stateVariable",
          variableName: "lastPointsFromInverting"
        }
      };
      return { globalDependencies };
    };
    stateVariableDefinitions.data = {
      description: "The data points used to compute the best-fit line.",
      public: true,
      isLocation: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "datumX") {
            return [];
          } else {
            return [
              [
                "point",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      numDimensions: 2,
      entryPrefixes: ["datumX", "datum"],
      returnEntryDimensions: (prefix) => prefix === "datum" ? 1 : 0,
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "datumX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let datumInd = Number(varEnding) - 1;
          if (!(Number.isInteger(datumInd) && datumInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [datumInd + ",0"];
          }
          if (datumInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => datumInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "data") {
          if (propIndex.length === 1) {
            return "datum" + propIndex[0];
          } else {
            return `datumX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 5) === "datum") {
          let datumNum = Number(varName.slice(5));
          if (Number.isInteger(datumNum) && datumNum > 0) {
            return `datumX${datumNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      returnArraySizeDependencies: () => ({
        data: {
          dependencyType: "attributeComponent",
          attributeName: "data",
          variableNames: ["numPoints", "numDimensions"]
        }
      }),
      returnArraySize({ dependencyValues }) {
        let data = dependencyValues.data;
        if (!data) {
          return [0, 0];
        }
        return [
          data.stateValues.numPoints,
          data.stateValues.numDimensions
        ];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          data: {
            dependencyType: "attributeComponent",
            attributeName: "data",
            variableNames: ["points"]
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let points = globalDependencyValues.data.stateValues.points;
        let data = {};
        for (let pointInd in points) {
          let point = points[pointInd];
          for (let dim in points) {
            data[`${pointInd},${dim}`] = point[dim];
          }
        }
        return {
          setValue: { data }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class RegionBetweenCurveXAxis extends GraphicalComponent {
  static componentType = "regionBetweenCurveXAxis";
  static styleOverrideCategories = ["fill"];
  static componentDocs = {
    summary: "A region bounded between a curve and the x-axis"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.boundaryValues = {
      description: "Boundary values defining the region's left/right edges.",
      createComponentOfType: "numberList",
      createStateVariable: "boundaryValues",
      defaultValue: [0, 1],
      forRenderer: true,
      public: true
    };
    attributes.function = {
      createComponentOfType: "function",
      description: "The function whose graph bounds the region."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the region's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let fillColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          fillColorWord = dependencyValues.selectedStyle.fillColorWordDarkMode;
        } else {
          fillColorWord = dependencyValues.selectedStyle.fillColorWord;
        }
        return { setValue: { styleDescription: fillColorWord } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "region".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " region";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.function = {
      additionalStateVariablesDefined: [
        {
          variableName: "haveFunction",
          forRenderer: true
        },
        {
          variableName: "fDefinition",
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        functionAttr: {
          dependencyType: "attributeComponent",
          attributeName: "function",
          variableNames: [
            "numericalfs",
            "numInputs",
            "numOutputs",
            "fDefinition"
          ]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.functionAttr === null || dependencyValues.functionAttr.stateValues.numInputs !== 1 || dependencyValues.functionAttr.stateValues.numOutputs !== 1) {
          return {
            setValue: {
              function: () => NaN,
              haveFunction: false,
              fDefinition: {}
            }
          };
        }
        return {
          setValue: {
            function: dependencyValues.functionAttr.stateValues.numericalfs[0],
            haveFunction: true,
            fDefinition: dependencyValues.functionAttr.stateValues.fDefinition
          }
        };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        function: {
          dependencyType: "stateVariable",
          variableName: "function"
        },
        boundaryValues: {
          dependencyType: "stateVariable",
          variableName: "boundaryValues"
        },
        haveFunction: {
          dependencyType: "stateVariable",
          variableName: "haveFunction"
        }
      }),
      definition({ dependencyValues }) {
        if (!dependencyValues.haveFunction) {
          return { setValue: { nearestPoint: () => ({}) } };
        }
        const minx = Math.min(
          dependencyValues.boundaryValues[0],
          dependencyValues.boundaryValues[1]
        );
        const maxx = Math.max(
          dependencyValues.boundaryValues[0],
          dependencyValues.boundaryValues[1]
        );
        const f2 = dependencyValues.function;
        let nearestPoint = function({ variables }) {
          let x1 = variables.x1.evaluate_to_constant();
          let x2 = variables.x2.evaluate_to_constant();
          x1 = Math.max(minx, Math.min(maxx, x1));
          let [val1, val2] = [0, f2(x1)].sort((a2, b2) => a2 - b2);
          x2 = Math.max(val1, Math.min(val2, x2));
          return { x1, x2 };
        };
        return { setValue: { nearestPoint } };
      }
    };
    return stateVariableDefinitions;
  }
}
class RegionBetweenCurves extends GraphicalComponent {
  static componentType = "regionBetweenCurves";
  static styleOverrideCategories = ["fill"];
  static componentDocs = {
    summary: "A region bounded between two curves"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.boundaryValues = {
      description: "Boundary values defining the region's edges.",
      createComponentOfType: "numberList",
      createStateVariable: "boundaryValues",
      defaultValue: [0, 1],
      forRenderer: true,
      public: true
    };
    attributes.flipFunctions = {
      description: "Whether to swap the role of x and y for the input functions.",
      createComponentOfType: "boolean",
      createStateVariable: "flipFunctions",
      defaultValue: false,
      public: true,
      forRenderer: true
    };
    return attributes;
  }
  static returnChildGroups() {
    let groups = super.returnChildGroups();
    groups.push({
      group: "functions",
      componentTypes: ["function"]
    });
    return groups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.styleDescription = {
      description: "A textual description of the region's style.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        selectedStyle: {
          dependencyType: "stateVariable",
          variableName: "selectedStyle"
        },
        document: {
          dependencyType: "ancestor",
          componentType: "document",
          variableNames: ["theme"]
        }
      }),
      definition: function({ dependencyValues }) {
        let fillColorWord;
        if (dependencyValues.document?.stateValues.theme === "dark") {
          fillColorWord = dependencyValues.selectedStyle.fillColorWordDarkMode;
        } else {
          fillColorWord = dependencyValues.selectedStyle.fillColorWord;
        }
        return { setValue: { styleDescription: fillColorWord } };
      }
    };
    stateVariableDefinitions.styleDescriptionWithNoun = {
      description: 'Style description including the word "region".',
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        styleDescription: {
          dependencyType: "stateVariable",
          variableName: "styleDescription"
        }
      }),
      definition: function({ dependencyValues }) {
        let styleDescriptionWithNoun = dependencyValues.styleDescription + " region";
        return { setValue: { styleDescriptionWithNoun } };
      }
    };
    stateVariableDefinitions.functions = {
      additionalStateVariablesDefined: [
        {
          variableName: "haveFunctions",
          forRenderer: true
        },
        {
          variableName: "fDefinitions",
          forRenderer: true
        }
      ],
      returnDependencies: () => ({
        functions: {
          dependencyType: "child",
          childGroups: ["functions"],
          variableNames: [
            "numericalfs",
            "numInputs",
            "numOutputs",
            "fDefinition"
          ]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.functions.length < 2 || dependencyValues.functions[0].stateValues.numInputs !== 1 || dependencyValues.functions[0].stateValues.numOutputs !== 1 || dependencyValues.functions[1].stateValues.numInputs !== 1 || dependencyValues.functions[1].stateValues.numOutputs !== 1) {
          return {
            setValue: {
              functions: [() => NaN, () => NaN],
              haveFunctions: false,
              fDefinitions: [{}, {}]
            }
          };
        }
        return {
          setValue: {
            functions: [
              dependencyValues.functions[0].stateValues.numericalfs[0],
              dependencyValues.functions[1].stateValues.numericalfs[0]
            ],
            haveFunctions: true,
            fDefinitions: [
              dependencyValues.functions[0].stateValues.fDefinition,
              dependencyValues.functions[1].stateValues.fDefinition
            ]
          }
        };
      }
    };
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        functions: {
          dependencyType: "stateVariable",
          variableName: "functions"
        },
        boundaryValues: {
          dependencyType: "stateVariable",
          variableName: "boundaryValues"
        },
        flipFunctions: {
          dependencyType: "stateVariable",
          variableName: "flipFunctions"
        },
        haveFunctions: {
          dependencyType: "stateVariable",
          variableName: "haveFunctions"
        }
      }),
      definition({ dependencyValues }) {
        if (!dependencyValues.haveFunctions) {
          return { setValue: { nearestPoint: () => ({}) } };
        }
        const minx = Math.min(
          dependencyValues.boundaryValues[0],
          dependencyValues.boundaryValues[1]
        );
        const maxx = Math.max(
          dependencyValues.boundaryValues[0],
          dependencyValues.boundaryValues[1]
        );
        const f1 = dependencyValues.functions[0];
        const f2 = dependencyValues.functions[1];
        let nearestPoint = function({ variables }) {
          let x1 = variables.x1.evaluate_to_constant();
          let x2 = variables.x2.evaluate_to_constant();
          if (dependencyValues.flipFunctions) {
            [x1, x2] = [x2, x1];
          }
          x1 = Math.max(minx, Math.min(maxx, x1));
          let [val1, val2] = [f1(x1), f2(x1)].sort((a2, b2) => a2 - b2);
          x2 = Math.max(val1, Math.min(val2, x2));
          if (dependencyValues.flipFunctions) {
            [x1, x2] = [x2, x1];
          }
          return { x1, x2 };
        };
        return { setValue: { nearestPoint } };
      }
    };
    return stateVariableDefinitions;
  }
}
class RegionHalfPlane extends GraphicalComponent {
  static componentType = "regionHalfPlane";
  static componentDocs = {
    summary: "A half-plane region bounded by a line"
  };
  static rendererType = void 0;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.horizontal = {
      description: "Whether the half-plane extends horizontally, bounded by the vertical line x = boundaryValue. If false, the half-plane extends vertically, bounded by the horizontal line y = boundaryValue.",
      createComponentOfType: "boolean",
      createStateVariable: "horizontal",
      defaultValue: true,
      public: true
    };
    attributes.boundaryValue = {
      description: "Position of the bounding line: x = boundaryValue when horizontal is true, y = boundaryValue when horizontal is false.",
      createComponentOfType: "number",
      createStateVariable: "boundaryValue",
      defaultValue: 0,
      public: true
    };
    attributes.greaterThan = {
      description: "Whether the region is the side where the inequality is greater than the boundary.",
      createComponentOfType: "boolean",
      createStateVariable: "greaterThan",
      defaultValue: true,
      public: true
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.nearestPoint = {
      returnDependencies: () => ({
        horizontal: {
          dependencyType: "stateVariable",
          variableName: "horizontal"
        },
        boundaryValue: {
          dependencyType: "stateVariable",
          variableName: "boundaryValue"
        },
        greaterThan: {
          dependencyType: "stateVariable",
          variableName: "greaterThan"
        }
      }),
      definition({ dependencyValues }) {
        let value = dependencyValues.boundaryValue;
        return {
          setValue: {
            nearestPoint: function({ variables, scales }) {
              if (!Number.isFinite(value)) {
                return {};
              }
              if (dependencyValues.horizontal) {
                if (dependencyValues.greaterThan) {
                  if (variables.x1 < value) {
                    let result = Object.assign(
                      {},
                      variables
                    );
                    result.x1 = value;
                    return result;
                  } else {
                    return {};
                  }
                } else {
                  if (variables.x1 > value) {
                    let result = Object.assign(
                      {},
                      variables
                    );
                    result.x1 = value;
                    return result;
                  } else {
                    return {};
                  }
                }
              } else {
                if (dependencyValues.greaterThan) {
                  if (variables.x2 < value) {
                    let result = Object.assign(
                      {},
                      variables
                    );
                    result.x2 = value;
                    return result;
                  } else {
                    return {};
                  }
                } else {
                  if (variables.x2 > value) {
                    let result = Object.assign(
                      {},
                      variables
                    );
                    result.x2 = value;
                    return result;
                  } else {
                    return {};
                  }
                }
              }
            }
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class CodeEditor extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      updateImmediateValue: inputUpdateImmediateValue.bind(this),
      updateValue: inputUpdateValue.bind(this),
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "codeEditor";
  static componentDocs = {
    summary: "An interactive DoenetML code editor"
  };
  static variableForImplicitProp = "value";
  static renderChildren = true;
  static ignoreVariantsFromChildren = true;
  static processWhenJustUpdatedForNewComponent = true;
  static ignoreErrorsFromChildren = true;
  static get stateVariablesShadowedForReference() {
    return ["value"];
  }
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.prefill = {
      description: "Initial code shown in the editor before the user types.",
      createComponentOfType: "text",
      createStateVariable: "prefill",
      defaultValue: "",
      public: true
    };
    attributes.bindValueTo = {
      createComponentOfType: "text",
      description: "Two-way binding target for the input's value."
    };
    attributes.width = {
      description: 'Editor width (e.g. "100%" or "600px").',
      createComponentOfType: "componentSize",
      createStateVariable: "width",
      defaultValue: { size: `100`, isAbsolute: false },
      forRenderer: true,
      public: true
    };
    attributes.height = {
      description: 'Editor height (e.g. "500px").',
      createComponentOfType: "componentSize",
      createStateVariable: "height",
      defaultValue: { size: 400, isAbsolute: true },
      forRenderer: true,
      public: true
    };
    attributes.viewerRatio = {
      createComponentOfType: "number",
      description: "Fraction of the editor area allotted to the live viewer."
    };
    attributes.showResults = {
      description: "Whether to render the results panel alongside the editor.",
      createComponentOfType: "boolean",
      createStateVariable: "showResults",
      defaultValue: false,
      forRenderer: true,
      public: true
    };
    attributes.showFormatter = {
      description: "Whether to show a code formatter button.",
      createComponentOfType: "boolean",
      createStateVariable: "showFormatter",
      defaultValue: false,
      forRenderer: true,
      public: true
    };
    attributes.resultsLocation = {
      description: "Position of the results panel relative to the editor.",
      createComponentOfType: "text",
      createStateVariable: "resultsLocation",
      defaultValue: "bottom",
      forRenderer: true,
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "bottom",
          description: "Place the results panel below the editor."
        },
        {
          value: "left",
          description: "Place the results panel to the left of the editor."
        },
        {
          value: "right",
          description: "Place the results panel to the right of the editor."
        }
      ]
    };
    attributes.readOnly = {
      description: "Whether the editor is read-only.",
      createComponentOfType: "boolean",
      createStateVariable: "readOnly",
      defaultValue: false,
      forRenderer: true,
      public: true
    };
    attributes.initialOpenTab = {
      description: "Which diagnostics/responses tab to open initially.",
      createComponentOfType: "text",
      createStateVariable: "initialOpenTab",
      defaultValue: "first",
      forRenderer: true,
      public: true,
      toLowerCase: true,
      validValues: [
        {
          value: "none",
          description: "Panel starts closed."
        },
        {
          value: "first",
          description: "Open the first available tab."
        },
        {
          value: "errors",
          description: "Open the errors tab."
        },
        {
          value: "warnings",
          description: "Open the warnings tab."
        },
        {
          value: "info",
          description: "Open the info tab."
        },
        {
          value: "accessibility",
          description: "Open the accessibility tab."
        },
        {
          value: "responses",
          description: "Open the responses tab."
        },
        {
          value: "help",
          description: "Open the help tab."
        }
      ]
    };
    attributes.renderedName = {
      createPrimitiveOfType: "string",
      description: "Name to assign to the dynamically-rendered DoenetML output."
    };
    attributes.staticName = {
      createPrimitiveOfType: "string",
      description: "Name to assign to the static DoenetML source."
    };
    return attributes;
  }
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnInputValueChangedStateVariableDefinitions({
        valueChangedDescription: "Whether the value has been changed from its initial state.",
        immediateValueChangedDescription: "Whether the value, including in-progress edits, has been changed from its initial state."
      })
    );
    stateVariableDefinitions.prefillFromChildren = {
      returnDependencies: () => ({
        childrenDoenetML: {
          dependencyType: "doenetML",
          displayOnlyChildren: true
        }
      }),
      definition({ dependencyValues }) {
        let prefillFromChildren = dependencyValues.childrenDoenetML;
        if (prefillFromChildren) {
          prefillFromChildren += "\n";
        }
        return {
          setValue: { prefillFromChildren }
        };
      }
    };
    stateVariableDefinitions.value = {
      description: "The code value of the input.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      hasEssential: true,
      forRenderer: true,
      returnDependencies: () => ({
        bindValueTo: {
          dependencyType: "attributeComponent",
          attributeName: "bindValueTo",
          variableNames: ["value"]
        },
        prefill: {
          dependencyType: "stateVariable",
          variableName: "prefill"
        },
        prefillFromChildren: {
          dependencyType: "stateVariable",
          variableName: "prefillFromChildren"
        },
        valueChanged: {
          dependencyType: "stateVariable",
          variableName: "valueChanged",
          onlyToSetInInverseDefinition: true
        },
        immediateValueChanged: {
          dependencyType: "stateVariable",
          variableName: "immediateValueChanged",
          onlyToSetInInverseDefinition: true
        }
      }),
      definition: function({ dependencyValues, usedDefault }) {
        if (!dependencyValues.bindValueTo) {
          return {
            useEssentialOrDefaultValue: {
              value: {
                variablesToCheck: "value",
                get defaultValue() {
                  if (usedDefault.prefill) {
                    return dependencyValues.prefillFromChildren;
                  } else {
                    return dependencyValues.prefill;
                  }
                }
              }
            }
          };
        }
        return {
          setValue: {
            value: dependencyValues.bindValueTo.stateValues.value
          }
        };
      },
      inverseDefinition: function({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions = [
          {
            setDependency: "valueChanged",
            desiredValue: true
          },
          {
            setDependency: "immediateValueChanged",
            desiredValue: true
          }
        ];
        if (dependencyValues.bindValueTo) {
          instructions.push({
            setDependency: "bindValueTo",
            desiredValue: desiredStateVariableValues.value,
            variableIndex: 0
          });
        } else {
          instructions.push({
            setEssentialValue: "value",
            value: desiredStateVariableValues.value
          });
        }
        return { success: true, instructions };
      }
    };
    Object.assign(
      stateVariableDefinitions,
      returnImmediateValueStateVariableDefinition({
        description: "The code value reflecting the user's in-progress edits.",
        createComponentOfType: "text"
      })
    );
    stateVariableDefinitions.text = {
      description: "The current code as a plain text string.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        value: {
          dependencyType: "stateVariable",
          variableName: "value"
        }
      }),
      definition: function({ dependencyValues }) {
        return { setValue: { text: dependencyValues.value } };
      }
    };
    stateVariableDefinitions.componentType = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { componentType: "text" } })
    };
    stateVariableDefinitions.allChildren = {
      returnDependencies: () => ({
        allChildren: {
          dependencyType: "child",
          childGroups: ["anything"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: { allChildren: dependencyValues.allChildren }
        };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class HasSameFactoring extends BooleanComponent {
  static componentType = "hasSameFactoring";
  static componentDocs = {
    summary: "Boolean condition that tests for equivalent factoring"
  };
  static rendererType = "boolean";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.variable = {
      createComponentOfType: "_variableName",
      createStateVariable: "variable",
      defaultValue: me$3.fromAst("x"),
      description: "Variable used when comparing factored expressions."
    };
    attributes.restrictDivision = {
      createComponentOfType: "boolean",
      createStateVariable: "restrictDivision",
      defaultValue: false,
      description: "Whether to disallow dividing through to match factored forms."
    };
    attributes.monomialFactorMustMatch = {
      createComponentOfType: "boolean",
      createStateVariable: "monomialFactorMustMatch",
      defaultValue: false,
      description: "Whether monomial factors must match exactly (implies restrictDivision)."
    };
    attributes.allowOnlySignDifferences = {
      createComponentOfType: "boolean",
      createStateVariable: "allowOnlySignDifferences",
      defaultValue: false,
      description: "Whether to count factorizations as equivalent when they differ only by sign."
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.parsedExpression;
    delete stateVariableDefinitions.mathChildrenByCode;
    stateVariableDefinitions.value = {
      public: true,
      description: "Whether the input math expressions share the same factoring.",
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        },
        allowedErrorInNumbers: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorInNumbers"
        },
        includeErrorInNumberExponents: {
          dependencyType: "stateVariable",
          variableName: "includeErrorInNumberExponents"
        },
        allowedErrorIsAbsolute: {
          dependencyType: "stateVariable",
          variableName: "allowedErrorIsAbsolute"
        },
        numSignErrorsMatched: {
          dependencyType: "stateVariable",
          variableName: "numSignErrorsMatched"
        },
        variable: {
          dependencyType: "stateVariable",
          variableName: "variable"
        },
        restrictDivision: {
          dependencyType: "stateVariable",
          variableName: "restrictDivision"
        },
        monomialFactorMustMatch: {
          dependencyType: "stateVariable",
          variableName: "monomialFactorMustMatch"
        },
        allowOnlySignDifferences: {
          dependencyType: "stateVariable",
          variableName: "allowOnlySignDifferences"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.mathChildren.length !== 2) {
          return { setValue: { value: false } };
        }
        let expr1 = dependencyValues.mathChildren[0].stateValues.value;
        let expr2 = dependencyValues.mathChildren[1].stateValues.value;
        let result = checkEquality({
          object1: expr1,
          object2: expr2,
          isUnordered: false,
          partialMatches: false,
          symbolicEquality: false,
          allowedErrorInNumbers: dependencyValues.allowedErrorInNumbers,
          includeErrorInNumberExponents: dependencyValues.includeErrorInNumberExponents,
          allowedErrorIsAbsolute: dependencyValues.allowedErrorIsAbsolute,
          numSignErrorsMatched: dependencyValues.numSignErrorsMatched
        });
        if (result.fraction_equal !== 1) {
          return { setValue: { value: false } };
        }
        if (Array.isArray(expr1.tree) && expr1.tree[0] === "-") {
          expr1 = me$3.fromAst(expr1.tree[1]);
        }
        if (Array.isArray(expr2.tree) && expr2.tree[0] === "-") {
          expr2 = me$3.fromAst(expr2.tree[1]);
        }
        if (!dependencyValues.restrictDivision && !dependencyValues.monomialFactorMustMatch && !dependencyValues.allowOnlySignDifferences) {
          if (Array.isArray(expr1.tree) && expr1.tree[0] === "/" && me$3.fromAst(expr1.tree[2]).variables().length === 0) {
            expr1 = me$3.fromAst(expr1.tree[1]);
            if (Array.isArray(expr1.tree) && expr1.tree[0] === "-") {
              expr1 = me$3.fromAst(expr1.tree[1]);
            }
          }
          if (Array.isArray(expr2.tree) && expr2.tree[0] === "/" && me$3.fromAst(expr2.tree[2]).variables().length === 0) {
            expr2 = me$3.fromAst(expr2.tree[1]);
            if (Array.isArray(expr2.tree) && expr2.tree[0] === "-") {
              expr2 = me$3.fromAst(expr2.tree[1]);
            }
          }
        }
        expr1 = me$3.fromAst(expandPositiveIntegerPowers(expr1.tree));
        expr2 = me$3.fromAst(expandPositiveIntegerPowers(expr2.tree));
        if (!(Array.isArray(expr1.tree) && expr1.tree[0] === "*")) {
          if (!(Array.isArray(expr2.tree) && expr2.tree[0] === "*")) {
            return { setValue: { value: true } };
          } else {
            return { setValue: { value: false } };
          }
        } else if (!(Array.isArray(expr2.tree) && expr2.tree[0] === "*")) {
          return { setValue: { value: false } };
        }
        if (dependencyValues.monomialFactorMustMatch || dependencyValues.allowOnlySignDifferences) {
          let monomial1 = findMonomialFromFactors(
            expr1.tree.slice(1)
          );
          let monomial2 = findMonomialFromFactors(
            expr2.tree.slice(1)
          );
          if (!monomial1.equals(monomial2)) {
            let monomial2Opposite = me$3.fromAst([
              "-",
              monomial2.tree
            ]);
            if (!monomial1.equals(monomial2Opposite)) {
              return { setValue: { value: false } };
            }
          }
        }
        if (dependencyValues.allowOnlySignDifferences) {
          let expr1Normalized = normalizeFactorSigns(expr1);
          let expr2Normalized = normalizeFactorSigns(expr2);
          return {
            setValue: {
              value: expr1Normalized.equalsViaSyntax(
                expr2Normalized,
                {
                  allowed_error_in_numbers: dependencyValues.allowedErrorInNumbers,
                  include_error_in_number_exponents: dependencyValues.includeErrorInNumberExponents,
                  allowed_error_is_absolute: dependencyValues.allowedErrorIsAbsolute
                }
              )
            }
          };
        }
        let nonConstantFactors1 = expr1.tree.slice(1).filter((x2) => me$3.fromAst(x2).variables().length > 0);
        let nonConstantFactors2 = expr2.tree.slice(1).filter((x2) => me$3.fromAst(x2).variables().length > 0);
        if (nonConstantFactors1.length !== nonConstantFactors2.length) {
          return { setValue: { value: false } };
        }
        let numQuadraticFactors1 = 0, numLinearFactors1 = 0;
        let numQuadraticFactors2 = 0, numLinearFactors2 = 0;
        let v2 = dependencyValues.variable.subscripts_to_strings().tree;
        for (let factor of nonConstantFactors1) {
          let deriv2 = me$3.fromAst(factor).subscripts_to_strings().derivative(v2).derivative(v2).simplify();
          if (deriv2.tree === 0) {
            numLinearFactors1++;
          } else if (deriv2.variables().length === 0) {
            numQuadraticFactors1++;
          } else {
            return { setValue: { value: false } };
          }
        }
        for (let factor of nonConstantFactors2) {
          let deriv2 = me$3.fromAst(factor).subscripts_to_strings().derivative(v2).derivative(v2).simplify();
          if (deriv2.tree === 0) {
            numLinearFactors2++;
          } else if (deriv2.variables().length === 0) {
            numQuadraticFactors2++;
          } else {
            return { setValue: { value: false } };
          }
        }
        let value = numLinearFactors1 === numLinearFactors2 && numQuadraticFactors1 === numQuadraticFactors2;
        return {
          setValue: {
            value
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
function expandPositiveIntegerPowers(tree) {
  if (!Array.isArray(tree)) {
    return tree;
  }
  if (tree[0] === "^") {
    let base = tree[1];
    let exponent = tree[2];
    if (Number.isInteger(exponent) && exponent > 0) {
      tree = ["*", ...Array(exponent).fill(base)];
    }
  }
  if (tree[0] === "*") {
    let oldTree = tree;
    tree = ["*"];
    for (let factor of oldTree.slice(1)) {
      if (Array.isArray(factor) && factor[0] === "^") {
        let base = factor[1];
        let exponent = factor[2];
        if (Number.isInteger(exponent) && exponent > 0) {
          tree.push(...Array(exponent).fill(base));
        } else {
          tree.push(factor);
        }
      } else {
        tree.push(factor);
      }
    }
  }
  return tree;
}
function findMonomialFromFactors(factors) {
  let monomialFactors = [];
  let inMonomial = false;
  for (let factor of factors) {
    if (typeof factor === "string" || me$3.fromAst(factor).variables().length === 0) {
      if (!inMonomial) {
        if (monomialFactors.length > 0) {
          return me$3.fromAst("＿");
        } else {
          inMonomial = true;
        }
      }
      monomialFactors.push(factor);
    } else {
      inMonomial = false;
    }
  }
  let monomialTree;
  if (monomialFactors.length === 0) {
    monomialTree = 1;
  } else if (monomialFactors.length === 1) {
    monomialTree = monomialFactors[0];
  } else {
    monomialTree = ["*", ...monomialFactors];
  }
  return me$3.fromAst(monomialTree);
}
function normalizeFactorSigns(expr) {
  let exprTree = expr.simplify().tree;
  if (exprTree[0] === "-") {
    exprTree = exprTree[1];
  }
  if (exprTree[0] !== "*") {
    return me$3.fromAst(exprTree).simplify();
  }
  let newTree = ["*"];
  for (let factor of exprTree.slice(1)) {
    if (typeof factor === "number") {
      if (factor < 0) {
        newTree.push(-factor);
      } else {
        newTree.push(factor);
      }
    } else if (!Array.isArray(factor)) {
      newTree.push(factor);
    } else if (factor[0] === "-") {
      newTree.push(factor[1]);
    } else if (factor[0] === "+") {
      let firstTermHasNeg = false;
      let term = factor[1];
      if (typeof term === "number") {
        if (term < 0) {
          firstTermHasNeg = true;
        }
      } else if (Array.isArray(term)) {
        if (term[0] === "-") {
          firstTermHasNeg = true;
        } else if (term[0] === "*") {
          let firstFactorInTerm = term[1];
          if (typeof firstFactorInTerm === "number") {
            if (firstFactorInTerm < 0) {
              firstTermHasNeg = true;
            }
          } else if (Array.isArray(firstFactorInTerm) && firstFactorInTerm[0] === "-") {
            firstTermHasNeg = true;
          }
        }
      }
      if (firstTermHasNeg) {
        newTree.push(me$3.fromAst(["-", factor]).simplify().tree);
      } else {
        newTree.push(factor);
      }
    } else {
      newTree.push(factor);
    }
  }
  return me$3.fromAst(newTree).simplify();
}
class DataFrame extends BaseComponent {
  static componentType = "dataFrame";
  // Experimental — not surfaced via the schema/autocomplete/reference
  // docs until the data-source story is implemented.
  static excludeFromSchema = true;
  static componentDocs = {
    summary: "A 2D table of typed data values."
  };
  static rendererType = void 0;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.source = {
      description: "URL or identifier of the data source.",
      createComponentOfType: "text",
      createStateVariable: "source",
      required: true,
      // not implemented yet
      public: true,
      forRenderer: true
    };
    attributes.hasHeader = {
      description: "Whether the first row of data contains column headers.",
      createComponentOfType: "boolean",
      createStateVariable: "hasHeader",
      defaultValue: true,
      public: true
    };
    attributes.columnTypes = {
      createComponentOfType: "textList",
      createStateVariable: "columnTypesPrelim",
      defaultValue: [],
      description: "Override the inferred type of each column."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.cid = {
      forRenderer: true,
      returnDependencies: () => ({
        source: {
          dependencyType: "stateVariable",
          variableName: "source"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!dependencyValues.source || dependencyValues.source.substring(0, 7).toLowerCase() !== "doenet:") {
          return {
            setValue: { cid: null }
          };
        }
        let cid = null;
        let result = dependencyValues.source.match(/[:&]cid=([^&]+)/i);
        if (result) {
          cid = result[1];
        }
        return { setValue: { cid } };
      }
    };
    stateVariableDefinitions.dataFrame = {
      stateVariablesDeterminingDependencies: ["cid", "source"],
      additionalStateVariablesDefined: [
        {
          variableName: "numRows",
          public: true,
          description: "The number of rows in the data frame.",
          shadowingInstructions: {
            createComponentOfType: "integer"
          }
        },
        {
          variableName: "numColumns",
          public: true,
          description: "The number of columns in the data frame.",
          shadowingInstructions: {
            createComponentOfType: "integer"
          }
        },
        {
          variableName: "columnTypes",
          public: true,
          description: "The data type of each column in the data frame.",
          shadowingInstructions: {
            createComponentOfType: "textList"
          }
        },
        {
          variableName: "columnNames",
          public: true,
          description: "The name of each column in the data frame.",
          shadowingInstructions: {
            createComponentOfType: "textList"
          }
        }
      ],
      returnDependencies: ({ stateValues }) => ({
        fileContents: {
          dependencyType: "file",
          cid: stateValues.cid,
          uri: stateValues.source,
          fileType: "csv"
        },
        hasHeader: {
          dependencyType: "stateVariable",
          variableName: "hasHeader"
        },
        columnTypesPrelim: {
          dependencyType: "stateVariable",
          variableName: "columnTypesPrelim"
        }
      }),
      definition: function({ dependencyValues, componentIdx }) {
        let columnTypes = [], columnNames = [];
        let originalData = dependencyValues.fileContents.trim().split("\n").map(
          (x2) => x2.trim().split(",").map((y2) => y2.trim())
        );
        let numColumns = originalData[0]?.length;
        let foundInconsistentRow = false;
        for (let row of originalData.slice(1)) {
          if (row.length !== numColumns) {
            foundInconsistentRow = true;
            break;
          }
        }
        if (foundInconsistentRow) {
          let warning = {
            message: `Data has invalid shape.  Rows has inconsistent lengths. Found in componentIdx :${componentIdx}`,
            type: "warning"
          };
          return {
            setValue: {
              dataFrame: null,
              numRows,
              numColumns,
              columnTypes,
              columnNames
            },
            sendDiagnostics: [warning]
          };
        }
        let dataFrame = {};
        let data = [];
        if (dependencyValues.hasHeader) {
          dataFrame.columnNames = originalData[0].map((value) => {
            if ([`"`, `'`].includes(value[0]) && value[value.length - 1] === value[0]) {
              value = value.substring(1, value.length - 1);
            }
            return value;
          });
          data = originalData.slice(1);
        } else {
          dataFrame.columnNames = [...Array(numColumns).keys()].map(
            (x2) => `col${x2 + 1}`
          );
          data = originalData;
        }
        if ([...new Set(dataFrame.columnNames)].length < dataFrame.columnNames) {
          let warning = {
            message: `Data has duplicate column names.  Found in componentIdx :${componentIdx}`,
            type: "warning"
          };
          return {
            setValue: {
              dataFrame: null,
              numRows,
              numColumns,
              columnTypes,
              columnNames
            },
            sendDiagnostics: [warning]
          };
        }
        if (dataFrame.columnNames.includes("")) {
          let warning = {
            message: `Data is missing a column name.  Found in componentIdx :${componentIdx}`,
            type: "warning"
          };
          return {
            setValue: {
              dataFrame: null,
              numRows,
              numColumns,
              columnTypes,
              columnNames
            },
            sendDiagnostics: [warning]
          };
        }
        let numRows = data.length;
        dataFrame.shape = [numRows, numColumns];
        for (let colInd = 0; colInd < numColumns; colInd++) {
          let prescribedType = dependencyValues.columnTypesPrelim[colInd]?.toLowerCase();
          if (!["number", "string"].includes(prescribedType)) {
            prescribedType = "auto";
          }
          if (prescribedType === "auto") {
            let foundNonNumericValue = false;
            for (let rowInd = 0; rowInd < numRows; rowInd++) {
              let value = data[rowInd][colInd];
              if (value !== "") {
                let numVal = Number(value);
                if (!Number.isFinite(numVal)) {
                  foundNonNumericValue = true;
                  break;
                }
              }
            }
            if (foundNonNumericValue) {
              prescribedType = "string";
            } else {
              prescribedType = "number";
            }
          }
          if (prescribedType === "number") {
            for (let rowInd = 0; rowInd < numRows; rowInd++) {
              let value = data[rowInd][colInd];
              if (value === "") {
                data[rowInd][colInd] = null;
              } else {
                data[rowInd][colInd] = Number(
                  data[rowInd][colInd]
                );
              }
            }
          } else {
            for (let rowInd = 0; rowInd < numRows; rowInd++) {
              let value = data[rowInd][colInd];
              if ([`"`, `'`].includes(value[0]) && value[value.length - 1] === value[0]) {
                value = value.substring(1, value.length - 1);
              }
              data[rowInd][colInd] = value;
            }
          }
          columnTypes.push(prescribedType);
        }
        dataFrame.data = data;
        dataFrame.columnTypes = columnTypes;
        return {
          setValue: {
            dataFrame,
            numRows,
            numColumns,
            columnTypes,
            columnNames: dataFrame.columnNames
          }
        };
      }
    };
    stateVariableDefinitions.means = {
      description: "Mean of each numeric column in the data frame.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      isArray: true,
      entryPrefixes: ["mean"],
      returnArraySizeDependencies: () => ({
        numColumns: {
          dependencyType: "stateVariable",
          variableName: "numColumns"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numColumns];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          dataFrame: {
            dependencyType: "stateVariable",
            variableName: "dataFrame"
          },
          numColumns: {
            dependencyType: "stateVariable",
            variableName: "numColumns"
          },
          numRows: {
            dependencyType: "stateVariable",
            variableName: "numRows"
          },
          columnTypes: {
            dependencyType: "stateVariable",
            variableName: "columnTypes"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues, arrayKeys }) {
        let means = {};
        for (let arrayKey of arrayKeys) {
          let theMean = 0;
          let numValues = 0;
          if (globalDependencyValues.columnTypes[arrayKey] === "number") {
            for (let rowInd = 0; rowInd < globalDependencyValues.numRows; rowInd++) {
              let theValue = globalDependencyValues.dataFrame.data[rowInd][arrayKey];
              if (theValue !== null) {
                theMean += theValue;
                numValues++;
              }
            }
            theMean /= numValues;
          } else {
            theMean = NaN;
          }
          means[arrayKey] = theMean;
        }
        return { setValue: { means } };
      }
    };
    return stateVariableDefinitions;
  }
}
const { mean, std, variance, median, quantileSeq } = me$3.math;
class SummaryStatistics extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "summaryStatistics";
  // Experimental — not surfaced via the schema/autocomplete/reference
  // docs until the data-source story is implemented.
  static excludeFromSchema = true;
  static componentDocs = {
    summary: "Summary statistics (mean, median, etc.) for a list of numbers."
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.source = {
      createTargetComponentNames: true,
      description: "Reference to the data frame to summarize."
    };
    attributes.column = {
      createComponentOfType: "text",
      createStateVariable: "desiredColumn",
      defaultValue: null,
      description: "Name of the column from the source data frame to summarize."
    };
    attributes.statisticsToDisplay = {
      createComponentOfType: "textList",
      createStateVariable: "statisticsToDisplayPrelim",
      defaultValue: ["default"],
      description: 'Which summary statistics to display (or "default" / "all").'
    };
    attributes.byCategoryColumn = {
      description: "Column used to group data when computing per-category statistics.",
      createComponentOfType: "text",
      createStateVariable: "byCategoryColumn",
      defaultValue: null,
      public: true
    };
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.statisticsToDisplay = {
      description: "Which summary statistics to display.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "textList"
      },
      returnDependencies: () => ({
        statisticsToDisplayPrelim: {
          dependencyType: "stateVariable",
          variableName: "statisticsToDisplayPrelim"
        }
      }),
      definition: function({ dependencyValues }) {
        let options = [
          "mean",
          "stdev",
          "variance",
          "stderr",
          "count",
          "minimum",
          "quartile1",
          "median",
          "quartile3",
          "maximum",
          "range",
          "sum"
        ];
        let statisticsToDisplay = [];
        let desiredStats = dependencyValues.statisticsToDisplayPrelim.map(
          (x2) => x2.toLowerCase()
        );
        if (desiredStats.includes("default")) {
          statisticsToDisplay = [
            "mean",
            "stdev",
            "count",
            "minimum",
            "quartile1",
            "median",
            "quartile3",
            "maximum"
          ];
        } else if (desiredStats.includes("all")) {
          statisticsToDisplay = [...options];
        } else {
          for (let stat of options) {
            if (desiredStats.includes(stat)) {
              statisticsToDisplay.push(stat);
            }
          }
        }
        return { setValue: { statisticsToDisplay } };
      }
    };
    stateVariableDefinitions.sourceName = {
      returnDependencies: () => ({
        source: {
          dependencyType: "attributeTargetComponentNames",
          attributeName: "source"
        }
      }),
      definition({ dependencyValues }) {
        let sourceName;
        if (dependencyValues.source?.length === 1) {
          sourceName = dependencyValues.source[0].absoluteName;
        } else {
          sourceName = null;
        }
        return { setValue: { sourceName } };
      }
    };
    stateVariableDefinitions.dataColumn = {
      description: "The numeric column being summarized.",
      stateVariablesDeterminingDependencies: ["sourceName"],
      additionalStateVariablesDefined: [
        {
          variableName: "columnName",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "text"
          },
          forRenderer: true,
          description: "The name of the data column being summarized."
        }
      ],
      returnDependencies({ stateValues }) {
        return {
          dataFrame: {
            dependencyType: "stateVariable",
            componentIdx: stateValues.sourceName,
            variableName: "dataFrame",
            variableOptional: true
          },
          desiredColumn: {
            dependencyType: "stateVariable",
            variableName: "desiredColumn"
          }
        };
      },
      definition({ dependencyValues }) {
        let dataColumn = null, columnName = null;
        if (dependencyValues.dataFrame) {
          let dataFrame = dependencyValues.dataFrame;
          let colInd = dataFrame.columnNames.indexOf(
            dependencyValues.desiredColumn
          );
          if (colInd !== -1) {
            columnName = dependencyValues.desiredColumn;
            dataColumn = [];
            for (let row of dataFrame.data) {
              if (row[colInd] !== null) {
                dataColumn.push(row[colInd]);
              }
            }
          }
        }
        return {
          setValue: { dataColumn, columnName }
        };
      }
    };
    stateVariableDefinitions.count = {
      description: "The number of values in the data column.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let count = null;
        if (dependencyValues.dataColumn) {
          count = dependencyValues.dataColumn.length;
        }
        return { setValue: { count } };
      }
    };
    stateVariableDefinitions.sum = {
      description: "The sum of the values.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let sum2 = null;
        if (dependencyValues.dataColumn) {
          sum2 = dependencyValues.dataColumn.reduce((a2, c2) => a2 + c2);
        }
        return { setValue: { sum: sum2 } };
      }
    };
    stateVariableDefinitions.mean = {
      description: "The arithmetic mean of the values.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let computedMean = null;
        if (dependencyValues.dataColumn !== null) {
          computedMean = mean(dependencyValues.dataColumn);
        }
        return { setValue: { mean: computedMean } };
      }
    };
    stateVariableDefinitions.stdev = {
      description: "The sample standard deviation.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let computedStdev = null;
        if (dependencyValues.dataColumn) {
          computedStdev = std(dependencyValues.dataColumn);
        }
        return { setValue: { stdev: computedStdev } };
      }
    };
    stateVariableDefinitions.variance = {
      description: "The sample variance.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let computedVariance = null;
        if (dependencyValues.dataColumn) {
          computedVariance = variance(dependencyValues.dataColumn);
        }
        return { setValue: { variance: computedVariance } };
      }
    };
    stateVariableDefinitions.stderr = {
      description: "The standard error of the mean.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        stdev: {
          dependencyType: "stateVariable",
          variableName: "stdev"
        },
        count: {
          dependencyType: "stateVariable",
          variableName: "count"
        }
      }),
      definition({ dependencyValues }) {
        let stderr = null;
        if (dependencyValues.stdev !== null) {
          stderr = dependencyValues.stdev / Math.sqrt(dependencyValues.count);
        }
        return { setValue: { stderr } };
      }
    };
    stateVariableDefinitions.minimum = {
      description: "The minimum value.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let minimum = null;
        if (dependencyValues.dataColumn) {
          minimum = Math.min(...dependencyValues.dataColumn);
        }
        return { setValue: { minimum } };
      }
    };
    stateVariableDefinitions.maximum = {
      description: "The maximum value.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let maximum2 = null;
        if (dependencyValues.dataColumn) {
          maximum2 = Math.max(...dependencyValues.dataColumn);
        }
        return { setValue: { maximum: maximum2 } };
      }
    };
    stateVariableDefinitions.median = {
      description: "The median value.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let computedMedian = null;
        if (dependencyValues.dataColumn) {
          computedMedian = median(dependencyValues.dataColumn);
        }
        return { setValue: { median: computedMedian } };
      }
    };
    stateVariableDefinitions.quartile1 = {
      description: "The first quartile (25th percentile).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let quartile1 = null;
        if (dependencyValues.dataColumn) {
          quartile1 = quantileSeq(dependencyValues.dataColumn, 0.25);
        }
        return { setValue: { quartile1 } };
      }
    };
    stateVariableDefinitions.quartile3 = {
      description: "The third quartile (75th percentile).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        dataColumn: {
          dependencyType: "stateVariable",
          variableName: "dataColumn"
        }
      }),
      definition({ dependencyValues }) {
        let quartile3 = null;
        if (dependencyValues.dataColumn) {
          quartile3 = quantileSeq(dependencyValues.dataColumn, 0.75);
        }
        return { setValue: { quartile3 } };
      }
    };
    stateVariableDefinitions.range = {
      description: "The range (maximum − minimum).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      returnDependencies: () => ({
        minimum: {
          dependencyType: "stateVariable",
          variableName: "minimum"
        },
        maximum: {
          dependencyType: "stateVariable",
          variableName: "maximum"
        }
      }),
      definition({ dependencyValues }) {
        let range = null;
        if (dependencyValues.minimum !== null) {
          range = dependencyValues.maximum - dependencyValues.minimum;
        }
        return { setValue: { range } };
      }
    };
    stateVariableDefinitions.summaryStatistics = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["statisticsToDisplay"],
      returnDependencies({ stateValues }) {
        let dependencies = {
          displayDigits: {
            dependencyType: "stateVariable",
            variableName: "displayDigits"
          },
          displayDecimals: {
            dependencyType: "stateVariable",
            variableName: "displayDecimals"
          },
          displaySmallAsZero: {
            dependencyType: "stateVariable",
            variableName: "displaySmallAsZero"
          }
        };
        if (stateValues.statisticsToDisplay.includes("mean")) {
          dependencies.mean = {
            dependencyType: "stateVariable",
            variableName: "mean"
          };
        }
        if (stateValues.statisticsToDisplay.includes("stdev")) {
          dependencies.stdev = {
            dependencyType: "stateVariable",
            variableName: "stdev"
          };
        }
        if (stateValues.statisticsToDisplay.includes("variance")) {
          dependencies.variance = {
            dependencyType: "stateVariable",
            variableName: "variance"
          };
        }
        if (stateValues.statisticsToDisplay.includes("stderr")) {
          dependencies.stderr = {
            dependencyType: "stateVariable",
            variableName: "stderr"
          };
        }
        if (stateValues.statisticsToDisplay.includes("count")) {
          dependencies.count = {
            dependencyType: "stateVariable",
            variableName: "count"
          };
        }
        if (stateValues.statisticsToDisplay.includes("minimum")) {
          dependencies.minimum = {
            dependencyType: "stateVariable",
            variableName: "minimum"
          };
        }
        if (stateValues.statisticsToDisplay.includes("quartile1")) {
          dependencies.quartile1 = {
            dependencyType: "stateVariable",
            variableName: "quartile1"
          };
        }
        if (stateValues.statisticsToDisplay.includes("median")) {
          dependencies.median = {
            dependencyType: "stateVariable",
            variableName: "median"
          };
        }
        if (stateValues.statisticsToDisplay.includes("quartile3")) {
          dependencies.quartile3 = {
            dependencyType: "stateVariable",
            variableName: "quartile3"
          };
        }
        if (stateValues.statisticsToDisplay.includes("maximum")) {
          dependencies.maximum = {
            dependencyType: "stateVariable",
            variableName: "maximum"
          };
        }
        if (stateValues.statisticsToDisplay.includes("range")) {
          dependencies.range = {
            dependencyType: "stateVariable",
            variableName: "range"
          };
        }
        if (stateValues.statisticsToDisplay.includes("sum")) {
          dependencies.sum = {
            dependencyType: "stateVariable",
            variableName: "sum"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let summaryStatistics = {};
        if (dependencyValues.mean !== void 0) {
          summaryStatistics.mean = roundForDisplay({
            value: dependencyValues.mean,
            dependencyValues
          });
        }
        if (dependencyValues.stdev !== void 0) {
          summaryStatistics.stdev = roundForDisplay({
            value: dependencyValues.stdev,
            dependencyValues
          });
        }
        if (dependencyValues.variance !== void 0) {
          summaryStatistics.variance = roundForDisplay({
            value: dependencyValues.variance,
            dependencyValues
          });
        }
        if (dependencyValues.stderr !== void 0) {
          summaryStatistics.stderr = roundForDisplay({
            value: dependencyValues.stderr,
            dependencyValues
          });
        }
        if (dependencyValues.count !== void 0) {
          summaryStatistics.count = roundForDisplay({
            value: dependencyValues.count,
            dependencyValues
          });
        }
        if (dependencyValues.minimum !== void 0) {
          summaryStatistics.minimum = roundForDisplay({
            value: dependencyValues.minimum,
            dependencyValues
          });
        }
        if (dependencyValues.quartile1 !== void 0) {
          summaryStatistics.quartile1 = roundForDisplay({
            value: dependencyValues.quartile1,
            dependencyValues
          });
        }
        if (dependencyValues.median !== void 0) {
          summaryStatistics.median = roundForDisplay({
            value: dependencyValues.median,
            dependencyValues
          });
        }
        if (dependencyValues.quartile3 !== void 0) {
          summaryStatistics.quartile3 = roundForDisplay({
            value: dependencyValues.quartile3,
            dependencyValues
          });
        }
        if (dependencyValues.maximum !== void 0) {
          summaryStatistics.maximum = roundForDisplay({
            value: dependencyValues.maximum,
            dependencyValues
          });
        }
        if (dependencyValues.range !== void 0) {
          summaryStatistics.range = roundForDisplay({
            value: dependencyValues.range,
            dependencyValues
          });
        }
        if (dependencyValues.sum !== void 0) {
          summaryStatistics.sum = roundForDisplay({
            value: dependencyValues.sum,
            dependencyValues
          });
        }
        return { setValue: { summaryStatistics } };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Legend extends GraphicalComponent {
  static componentType = "legend";
  static componentDocs = {
    summary: "A legend describing the contents of a graph"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.position = {
      description: "Position of the legend on the graph.",
      createComponentOfType: "text",
      createStateVariable: "position",
      defaultValue: "upperRight",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "upperRight",
          description: "Place the legend in the upper-right corner."
        },
        {
          value: "upperLeft",
          description: "Place the legend in the upper-left corner."
        },
        {
          value: "lowerRight",
          description: "Place the legend in the lower-right corner."
        },
        {
          value: "lowerLeft",
          description: "Place the legend in the lower-left corner."
        }
      ]
    };
    attributes.displayClosedSwatches = {
      description: "Whether to render closed/filled swatches in the legend.",
      createComponentOfType: "boolean",
      createStateVariable: "displayClosedSwatches",
      defaultValue: false,
      public: true
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "labels",
        componentTypes: ["label"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.graphicalElementNames = {
      returnDependencies: () => ({
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["graphicalDescendants"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.graphAncestor) {
          return {
            setValue: {
              graphicalElementNames: dependencyValues.graphAncestor.stateValues.graphicalDescendants.map(
                (x2) => x2.componentIdx
              )
            }
          };
        } else {
          return { setValue: { graphicalElementNames: null } };
        }
      }
    };
    stateVariableDefinitions.legendElements = {
      forRenderer: true,
      stateVariablesDeterminingDependencies: ["graphicalElementNames"],
      returnDependencies: ({ stateValues }) => {
        let dependencies = {
          labelChildren: {
            dependencyType: "child",
            childGroups: ["labels"],
            variableNames: [
              "value",
              "hasLatex",
              "forObjectComponentIdx"
            ]
          },
          displayClosedSwatches: {
            dependencyType: "stateVariable",
            variableName: "displayClosedSwatches"
          }
        };
        if (stateValues.graphicalElementNames) {
          dependencies.numGraphicalElements = {
            dependencyType: "value",
            value: stateValues.graphicalElementNames.length
          };
          for (let [
            ind,
            cIdx
          ] of stateValues.graphicalElementNames.entries()) {
            dependencies[`graphicalElement${ind}`] = {
              dependencyType: "multipleStateVariables",
              componentIdx: cIdx,
              variableNames: [
                "selectedStyle",
                "styleNumber",
                "filled"
              ],
              variablesOptional: true
            };
            dependencies[`graphicalElement${ind}AdapterSource`] = {
              dependencyType: "adapterSource",
              componentIdx: cIdx
            };
            dependencies[`graphicalElement${ind}ShadowSource`] = {
              dependencyType: "shadowSource",
              componentIdx: cIdx
            };
          }
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        let legendElements = [];
        if (dependencyValues.numGraphicalElements > 0) {
          let pointStyleNumbersFound = [];
          let closedPathStyleNumbersFound = [];
          let otherStyleNumbersFound = [];
          let graphicalDescendantsLeft = [];
          for (let ind = 0; ind < dependencyValues.numGraphicalElements; ind++) {
            let graphicalElement = dependencyValues[`graphicalElement${ind}`];
            let adapter = dependencyValues[`graphicalElement${ind}AdapterSource`];
            if (adapter) {
              graphicalElement = { ...graphicalElement };
              graphicalElement.componentIdx = adapter.componentIdx;
            }
            let shadowSource = dependencyValues[`graphicalElement${ind}ShadowSource`];
            if (shadowSource) {
              const graphicalElement2 = JSON.parse(
                JSON.stringify(graphicalElement)
              );
              graphicalElement2.componentIdx = shadowSource.componentIdx;
              graphicalDescendantsLeft.push(graphicalElement2);
            }
            graphicalDescendantsLeft.push(graphicalElement);
          }
          let graphicalDescendantComponentIndicesLeft = graphicalDescendantsLeft.map((x2) => x2.componentIdx);
          let labelsInOrder = [];
          let labelsByComponentIdx = {};
          for (let labelChild of dependencyValues.labelChildren) {
            let labelInfo = {
              value: labelChild.stateValues.value,
              hasLatex: labelChild.stateValues.hasLatex
            };
            if (labelChild.stateValues.forObjectComponentIdx) {
              labelsByComponentIdx[labelChild.stateValues.forObjectComponentIdx] = labelInfo;
              let ind = graphicalDescendantComponentIndicesLeft.indexOf(
                labelChild.stateValues.forObjectComponentIdx
              );
              if (ind !== -1) {
                let comp = graphicalDescendantsLeft[ind];
                if (componentInfoObjects.isInheritedComponentType(
                  {
                    inheritedComponentType: comp.componentType,
                    baseComponentType: "point"
                  }
                )) {
                  pointStyleNumbersFound.push(
                    comp.stateValues.styleNumber
                  );
                } else if (dependencyValues.displayClosedSwatches && componentInfoObjects.allComponentClasses[comp.componentType].representsClosedPath) {
                  closedPathStyleNumbersFound.push(
                    comp.stateValues.styleNumber
                  );
                } else if (comp.componentType !== "legend") {
                  otherStyleNumbersFound.push(
                    comp.stateValues.styleNumber
                  );
                }
              }
            }
            labelsInOrder.push({
              labelInfo,
              forObject: labelChild.stateValues.forObjectComponentIdx
            });
          }
          for (let label of labelsInOrder) {
            let componentForLabel;
            if (label.forObject) {
              let ind = graphicalDescendantComponentIndicesLeft.indexOf(
                label.forObject
              );
              if (ind !== -1) {
                componentForLabel = graphicalDescendantsLeft[ind];
                graphicalDescendantsLeft.splice(ind, 1);
                graphicalDescendantComponentIndicesLeft.splice(
                  ind,
                  1
                );
              }
            } else {
              for (let ind = 0; ind < graphicalDescendantsLeft.length; ind++) {
                let comp = graphicalDescendantsLeft[ind];
                if (!(comp.componentIdx in labelsByComponentIdx)) {
                  if (componentInfoObjects.isInheritedComponentType(
                    {
                      inheritedComponentType: comp.componentType,
                      baseComponentType: "point"
                    }
                  )) {
                    if (!pointStyleNumbersFound.includes(
                      comp.stateValues.styleNumber
                    )) {
                      componentForLabel = comp;
                      break;
                    }
                  } else if (dependencyValues.displayClosedSwatches && componentInfoObjects.allComponentClasses[comp.componentType].representsClosedPath) {
                    if (!closedPathStyleNumbersFound.includes(
                      comp.stateValues.styleNumber
                    )) {
                      componentForLabel = comp;
                      break;
                    }
                  } else if (comp.componentType !== "legend") {
                    if (!otherStyleNumbersFound.includes(
                      comp.stateValues.styleNumber
                    )) {
                      componentForLabel = comp;
                      break;
                    }
                  }
                }
              }
            }
            if (componentForLabel) {
              let selectedStyle = componentForLabel.stateValues.selectedStyle;
              let styleNumber = componentForLabel.stateValues.styleNumber;
              if (componentInfoObjects.isInheritedComponentType({
                inheritedComponentType: componentForLabel.componentType,
                baseComponentType: "point"
              })) {
                pointStyleNumbersFound.push(styleNumber);
                legendElements.push({
                  swatchType: "marker",
                  markerStyle: selectedStyle.markerStyle,
                  markerColor: selectedStyle.markerColor,
                  markerSize: selectedStyle.markerSize,
                  lineOpacity: selectedStyle.lineOpacity,
                  label: label.labelInfo
                });
              } else if (dependencyValues.displayClosedSwatches && componentInfoObjects.allComponentClasses[componentForLabel.componentType].representsClosedPath) {
                closedPathStyleNumbersFound.push(styleNumber);
                legendElements.push({
                  swatchType: "rectangle",
                  lineStyle: selectedStyle.lineStyle,
                  lineColor: selectedStyle.lineColor,
                  lineWidth: selectedStyle.lineWidth,
                  lineOpacity: selectedStyle.lineOpacity,
                  fillColor: selectedStyle.fillColor,
                  filled: componentForLabel.stateValues.filled,
                  fillOpacity: selectedStyle.fillOpacity,
                  label: label.labelInfo
                });
              } else {
                otherStyleNumbersFound.push(styleNumber);
                legendElements.push({
                  swatchType: "line",
                  lineStyle: selectedStyle.lineStyle,
                  lineColor: selectedStyle.lineColor,
                  lineWidth: selectedStyle.lineWidth,
                  lineOpacity: selectedStyle.lineOpacity,
                  label: label.labelInfo
                });
              }
            }
          }
        }
        return { setValue: { legendElements } };
      }
    };
    stateVariableDefinitions.graphLimits = {
      forRenderer: true,
      returnDependencies: () => ({
        graphAncestor: {
          dependencyType: "ancestor",
          componentType: "graph",
          variableNames: ["xMin", "xMax", "yMin", "yMax"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.graphAncestor) {
          return {
            setValue: {
              graphLimits: dependencyValues.graphAncestor.stateValues
            }
          };
        } else {
          return { setValue: { graphLimits: null } };
        }
      }
    };
    return stateVariableDefinitions;
  }
}
class MatchesPattern extends BooleanComponent {
  static componentType = "matchesPattern";
  static componentDocs = {
    summary: "Boolean condition that tests whether input matches a math pattern"
  };
  static rendererType = "boolean";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.pattern = {
      createComponentOfType: "math",
      description: "Math expression pattern to match against."
    };
    attributes.allowImplicitIdentities = {
      description: "Whether to allow implicit identity transformations when matching.",
      createComponentOfType: "boolean",
      createStateVariable: "allowImplicitIdentities",
      defaultValue: false,
      public: true
    };
    attributes.allowPermutations = {
      description: "Whether to allow permutations of operands when matching.",
      createComponentOfType: "boolean",
      createStateVariable: "allowPermutations",
      defaultValue: true,
      public: true
    };
    attributes.requireNumericMatches = {
      description: "Whether numeric placeholders must match numbers.",
      createComponentOfType: "boolean",
      createStateVariable: "requireNumericMatches",
      defaultValue: false,
      public: true
    };
    attributes.requireVariableMatches = {
      description: "Whether variable placeholders must match variables.",
      createComponentOfType: "boolean",
      createStateVariable: "requireVariableMatches",
      defaultValue: false,
      public: true
    };
    attributes.excludeMatches = {
      description: "Patterns whose matches are excluded.",
      createComponentOfType: "mathList",
      createStateVariable: "excludeMatches",
      defaultValue: [],
      public: true
    };
    attributes.matchExpressionWithBlanks = {
      description: "Whether to allow expressions with blanks to match.",
      createComponentOfType: "boolean",
      createStateVariable: "matchExpressionWithBlanks",
      defaultValue: false,
      public: true
    };
    return attributes;
  }
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string"];
  static returnSugarInstructions() {
    let sugarInstructions = [];
    let wrapNonMathWithMath = function({
      matchedChildren,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    }) {
      if (matchedChildren.length === 0 || matchedChildren.length === 1 && componentInfoObjects.componentIsSpecifiedType(
        matchedChildren[0],
        "math"
      )) {
        return { success: false };
      }
      return {
        success: true,
        newChildren: [
          {
            type: "serialized",
            componentType: "math",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: matchedChildren,
            attributes: {},
            doenetAttributes: {},
            state: {}
          }
        ],
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: wrapNonMathWithMath
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.parsedExpression;
    delete stateVariableDefinitions.mathChildrenByCode;
    stateVariableDefinitions.pattern = {
      additionalStateVariablesDefined: ["patternVariables"],
      returnDependencies: () => ({
        patternAttr: {
          dependencyType: "attributeComponent",
          attributeName: "pattern",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        let patternVariables = [];
        if (!dependencyValues.patternAttr) {
          return {
            setValue: { pattern: "＿", patternVariables }
          };
        }
        let originalVariablesInPattern = dependencyValues.patternAttr.stateValues.value.variables();
        let ind = 26 * 27 + 1;
        function replacePatternVariables(tree) {
          if (tree === "＿") {
            let newVar = numberToLetters(ind);
            ind++;
            while (originalVariablesInPattern.includes(newVar)) {
              newVar = numberToLetters(ind);
              ind++;
            }
            patternVariables.push(newVar);
            return newVar;
          } else if (Array.isArray(tree)) {
            return [
              tree[0],
              ...tree.slice(1).map(replacePatternVariables)
            ];
          } else {
            return tree;
          }
        }
        let pattern = replacePatternVariables(
          dependencyValues.patternAttr.stateValues.value.tree
        );
        return {
          setValue: { pattern, patternVariables }
        };
      }
    };
    stateVariableDefinitions.value = {
      description: "Whether the input matches the pattern.",
      additionalStateVariablesDefined: [
        {
          variableName: "allPatternMatches"
        }
      ],
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      forRenderer: true,
      returnDependencies: () => ({
        pattern: {
          dependencyType: "stateVariable",
          variableName: "pattern"
        },
        patternVariables: {
          dependencyType: "stateVariable",
          variableName: "patternVariables"
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        },
        allowImplicitIdentities: {
          dependencyType: "stateVariable",
          variableName: "allowImplicitIdentities"
        },
        allowPermutations: {
          dependencyType: "stateVariable",
          variableName: "allowPermutations"
        },
        requireNumericMatches: {
          dependencyType: "stateVariable",
          variableName: "requireNumericMatches"
        },
        requireVariableMatches: {
          dependencyType: "stateVariable",
          variableName: "requireVariableMatches"
        },
        excludeMatches: {
          dependencyType: "stateVariable",
          variableName: "excludeMatches"
        },
        matchExpressionWithBlanks: {
          dependencyType: "stateVariable",
          variableName: "matchExpressionWithBlanks"
        }
      }),
      definition: function({ dependencyValues }) {
        if (dependencyValues.mathChildren.length === 0) {
          return {
            setValue: { value: false, allPatternMatches: [] }
          };
        }
        let mathValue = dependencyValues.mathChildren[0].stateValues.value;
        if (mathValue.variables().includes("＿") && !dependencyValues.matchExpressionWithBlanks) {
          return {
            setValue: { value: false, allPatternMatches: [] }
          };
        }
        let variables = {};
        if (dependencyValues.requireNumericMatches) {
          let isNumeric = (m2) => !Number.isNaN(me$3.fromAst(m2).evaluate_to_constant());
          dependencyValues.patternVariables.forEach(
            (v2) => variables[v2] = isNumeric
          );
        } else if (dependencyValues.requireVariableMatches) {
          let isString2 = (m2) => typeof m2 === "string";
          dependencyValues.patternVariables.forEach(
            (v2) => variables[v2] = isString2
          );
        } else {
          dependencyValues.patternVariables.forEach(
            (v2) => variables[v2] = true
          );
        }
        let params = {
          variables,
          allow_permutations: dependencyValues.allowPermutations
        };
        if (dependencyValues.allowImplicitIdentities) {
          params.allow_implicit_identities = dependencyValues.patternVariables;
        }
        let matchResult = mathValue.match(
          dependencyValues.pattern,
          params
        );
        let value = false, allPatternMatches = [];
        if (matchResult) {
          if (!Object.values(matchResult).map((x2) => me$3.fromAst(x2)).some(
            (m2) => dependencyValues.excludeMatches.some(
              (em) => m2.variables().some(
                (v2) => em.equalsViaSyntax(me$3.fromAst(v2))
              )
            )
          )) {
            value = true;
            allPatternMatches = dependencyValues.patternVariables.map(
              (v2) => me$3.fromAst(matchResult[v2])
            );
          }
        }
        return {
          setValue: {
            value,
            allPatternMatches
          }
        };
      }
    };
    stateVariableDefinitions.numMatches = {
      description: "The number of matches found.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        allPatternMatches: {
          dependencyType: "stateVariable",
          variableName: "allPatternMatches"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numMatches: dependencyValues.allPatternMatches.length
          },
          checkForActualChange: { numMatches: true }
        };
      }
    };
    stateVariableDefinitions.patternMatches = {
      description: "The list of matched sub-expressions.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math"
      },
      isArray: true,
      numDimensions: 1,
      entryPrefixes: ["patternMatch"],
      returnArraySizeDependencies: () => ({
        numMatches: {
          dependencyType: "stateVariable",
          variableName: "numMatches"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numMatches];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          allPatternMatches: {
            dependencyType: "stateVariable",
            variableName: "allPatternMatches"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let patternMatches = {};
        for (let ind = 0; ind < globalDependencyValues.__array_size[0]; ind++) {
          patternMatches[ind] = globalDependencyValues.allPatternMatches[ind];
        }
        return { setValue: { patternMatches } };
      }
    };
    return stateVariableDefinitions;
  }
}
class Matrix extends MathComponent {
  static componentType = "matrix";
  static componentDocs = {
    summary: "A matrix of math values",
    // `<row>` and `<column>` children are sugared to `<matrixRow>` and
    // `<matrixColumn>` at runtime. Editor help follows the same indirection
    // so authors see the matrix-aware description rather than the tabular
    // <row> / unrelated <column> ones.
    childAliases: { row: "matrixRow", column: "matrixColumn" }
  };
  static rendererType = "math";
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["row", "column"];
  static returnChildGroups() {
    return [
      {
        group: "rows",
        componentTypes: ["matrixRow"]
      },
      {
        group: "columns",
        componentTypes: ["matrixColumn"]
      },
      {
        group: "maths",
        componentTypes: ["math"],
        excludeFromSchema: true
      }
    ];
  }
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.defaultEntry = {
      createComponentOfType: "math",
      createStateVariable: "defaultEntry",
      defaultValue: me$3.fromAst(0),
      description: "Value used for entries that aren't explicitly specified."
    };
    attributes.numRows = {
      createComponentOfType: "integer",
      description: "Number of rows in the matrix."
    };
    attributes.numColumns = {
      createComponentOfType: "integer",
      description: "Number of columns in the matrix."
    };
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let replaceRowAndColumnChildren = function({
      matchedChildren,
      componentInfoObjects,
      nComponents,
      stateIdInfo
    }) {
      if (matchedChildren.length === 0) {
        return { success: false };
      }
      let newChildren = [];
      let foundRowsOrColumns = false;
      for (let child of matchedChildren) {
        if (typeof child === "string") {
          continue;
        }
        if (!child.doenetAttributes) {
          child.doenetAttributes = {};
        }
        if (child.componentType === "column") {
          child.doenetAttributes.createNameFromComponentType = "column";
          child.componentType = "matrixColumn";
          foundRowsOrColumns = true;
        } else if (child.componentType === "row") {
          child.doenetAttributes.createNameFromComponentType = "row";
          child.componentType = "matrixRow";
          foundRowsOrColumns = true;
        } else if (child.attributes?.createComponentOfType?.primitive.value === "column") {
          child.doenetAttributes.createNameFromComponentType = "column";
          child.attributes.createComponentOfType.primitive.value = "matrixColumn";
          foundRowsOrColumns = true;
        } else if (child.attributes?.createComponentOfType?.primitive.value === "row") {
          child.doenetAttributes.createNameFromComponentType = "row";
          child.attributes.createComponentOfType.primitive.value = "matrixRow";
          foundRowsOrColumns = true;
        }
        newChildren.push(child);
      }
      if (!foundRowsOrColumns) {
        if (matchedChildren.length > 1 || !componentInfoObjects.componentIsSpecifiedType(
          matchedChildren[0],
          "math"
        )) {
          newChildren = [
            {
              type: "serialized",
              componentType: "math",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: matchedChildren,
              attributes: {},
              doenetAttributes: {},
              state: {}
            }
          ];
        }
      }
      return {
        success: true,
        newChildren,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: replaceRowAndColumnChildren
    });
    return sugarInstructions;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.codePre;
    delete stateVariableDefinitions.mathChildrenFunctionSymbols;
    delete stateVariableDefinitions.expressionWithCodes;
    delete stateVariableDefinitions.mathChildrenWithCanBeModified;
    delete stateVariableDefinitions.codesAdjacentToStrings;
    delete stateVariableDefinitions.canBeModified;
    delete stateVariableDefinitions.mathChildrenByVectorComponent;
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.unordered = {
      description: "Whether matrix entries should be treated as unordered.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({}),
      definition() {
        return {
          setValue: { unordered: false }
        };
      }
    };
    stateVariableDefinitions.matrixSizePre = {
      returnDependencies: () => ({
        rowChildren: {
          dependencyType: "child",
          childGroups: ["rows"],
          variableNames: ["numComponents"]
        },
        colChildren: {
          dependencyType: "child",
          childGroups: ["columns"],
          variableNames: ["numComponents"]
        },
        mathChildren: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["matrixSize"]
        },
        numRowsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "numRows",
          variableNames: ["value"]
        },
        numColumnsAttr: {
          dependencyType: "attributeComponent",
          attributeName: "numColumns",
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        let numRows = null, numColumns = null;
        if (dependencyValues.numRowsAttr) {
          numRows = dependencyValues.numRowsAttr.stateValues.value;
          if (!(Number.isFinite(numRows) && numRows > 0)) {
            numRows = null;
          }
        }
        if (dependencyValues.numColumnsAttr) {
          numColumns = dependencyValues.numColumnsAttr.stateValues.value;
          if (!(Number.isFinite(numColumns) && numColumns > 0)) {
            numColumns = null;
          }
        }
        if (dependencyValues.rowChildren.length > 0) {
          if (numRows === null) {
            numRows = dependencyValues.rowChildren.length;
          }
          if (numColumns === null) {
            numColumns = Math.max(
              1,
              ...dependencyValues.rowChildren.map(
                (x2) => x2.stateValues.numComponents
              )
            );
          }
        } else if (dependencyValues.colChildren.length > 0) {
          if (numColumns === null) {
            numColumns = dependencyValues.colChildren.length;
          }
          if (numRows === null) {
            numRows = Math.max(
              1,
              ...dependencyValues.colChildren.map(
                (x2) => x2.stateValues.numComponents
              )
            );
          }
        } else if (dependencyValues.mathChildren.length === 1) {
          if (numRows === null) {
            numRows = dependencyValues.mathChildren[0].stateValues.matrixSize[0];
          }
          if (numColumns === null) {
            numColumns = dependencyValues.mathChildren[0].stateValues.matrixSize[1];
          }
        } else {
          if (numRows === null) {
            if (numColumns === null) {
              numRows = 0;
              numColumns = 0;
            } else {
              numRows = 1;
            }
          } else if (numColumns === null) {
            numColumns = 1;
          }
        }
        return { setValue: { matrixSizePre: [numRows, numColumns] } };
      }
    };
    stateVariableDefinitions.matrixPre = {
      isArray: true,
      numDimensions: 2,
      hasEssential: true,
      returnArraySizeDependencies: () => ({
        matrixSizePre: {
          dependencyType: "stateVariable",
          variableName: "matrixSizePre"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return dependencyValues.matrixSizePre;
      },
      returnArrayDependenciesByKey: ({ arrayKeys }) => {
        let globalDependencies = {
          defaultEntry: {
            dependencyType: "stateVariable",
            variableName: "defaultEntry"
          },
          rowChildIdentities: {
            dependencyType: "child",
            childGroups: ["rows"]
          },
          colChildIdentities: {
            dependencyType: "child",
            childGroups: ["columns"]
          }
        };
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          let [rowInd, colInd] = arrayKey.split(",");
          dependenciesByKey[arrayKey] = {
            rowChild: {
              dependencyType: "child",
              childGroups: ["rows"],
              childIndices: [rowInd],
              variableNames: ["math" + (Number(colInd) + 1)]
            },
            colChild: {
              dependencyType: "child",
              childGroups: ["columns"],
              childIndices: [colInd],
              variableNames: ["math" + (Number(rowInd) + 1)]
            },
            mathChild: {
              dependencyType: "child",
              childGroups: ["maths"],
              childIndices: [0],
              variableNames: [
                "matrixEntry" + (Number(rowInd) + 1) + "_" + (Number(colInd) + 1)
              ]
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        arraySize
      }) {
        let matrix2 = {};
        let essentialMatrix = {};
        let useRows = globalDependencyValues.rowChildIdentities.length > 0;
        let useColumns = globalDependencyValues.colChildIdentities.length > 0;
        for (let arrayKey of arrayKeys) {
          let [rowInd, colInd] = arrayKey.split(",");
          let entry;
          if (useRows) {
            entry = dependencyValuesByKey[arrayKey].rowChild[0]?.stateValues["math" + (Number(colInd) + 1)];
          } else if (useColumns) {
            entry = dependencyValuesByKey[arrayKey].colChild[0]?.stateValues["math" + (Number(rowInd) + 1)];
          } else {
            entry = dependencyValuesByKey[arrayKey].mathChild[0]?.stateValues["matrixEntry" + (Number(rowInd) + 1) + "_" + (Number(colInd) + 1)];
          }
          if (entry === void 0) {
            essentialMatrix[arrayKey] = {
              defaultValue: globalDependencyValues.defaultEntry
            };
          } else {
            matrix2[arrayKey] = entry;
          }
        }
        return {
          setValue: { matrixPre: matrix2 },
          useEssentialOrDefaultValue: { matrixPre: essentialMatrix }
        };
      },
      async inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey
      }) {
        let instructions = [];
        let newMatrixValues = {};
        let useRows = globalDependencyValues.rowChildIdentities.length > 0;
        let useColumns = globalDependencyValues.colChildIdentities.length > 0;
        for (let arrayKey in desiredStateVariableValues.matrixPre) {
          let [rowInd, colInd] = arrayKey.split(",");
          if (useRows) {
            let entry = dependencyValuesByKey[arrayKey].rowChild[0]?.stateValues["math" + (Number(colInd) + 1)];
            if (entry === void 0) {
              newMatrixValues[arrayKey] = desiredStateVariableValues.matrixPre[arrayKey];
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].rowChild,
                desiredValue: desiredStateVariableValues.matrixPre[arrayKey],
                childIndex: 0,
                variableIndex: 0
              });
            }
          } else if (useColumns) {
            let entry = dependencyValuesByKey[arrayKey].colChild[0]?.stateValues["math" + (Number(rowInd) + 1)];
            if (entry === void 0) {
              newMatrixValues[arrayKey] = desiredStateVariableValues.matrixPre[arrayKey];
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].colChild,
                desiredValue: desiredStateVariableValues.matrixPre[arrayKey],
                childIndex: 0,
                variableIndex: 0
              });
            }
          } else {
            let entry = dependencyValuesByKey[arrayKey].mathChild[0]?.stateValues["matrixEntry" + (Number(rowInd) + 1) + "_" + (Number(colInd) + 1)];
            if (entry === void 0) {
              newMatrixValues[arrayKey] = desiredStateVariableValues.matrixPre[arrayKey];
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].mathChild,
                desiredValue: desiredStateVariableValues.matrixPre[arrayKey],
                childIndex: 0,
                variableIndex: 0
              });
            }
          }
        }
        if (Object.keys(newMatrixValues).length > 0) {
          instructions.push({
            setEssentialValue: "matrixPre",
            value: newMatrixValues
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.unnormalizedValue = {
      returnDependencies: () => ({
        matrixPre: {
          dependencyType: "stateVariable",
          variableName: "matrixPre"
        },
        matrixSizePre: {
          dependencyType: "stateVariable",
          variableName: "matrixSizePre"
        }
      }),
      hasEssential: true,
      defaultValue: me$3.fromAst(["matrix", ["tuple", 0, 0], ["tuple"]]),
      // 0x0 matrix
      definition({ dependencyValues }) {
        let matrixValues = ["tuple"];
        for (let i2 = 0; i2 < dependencyValues.matrixSizePre[0]; i2++) {
          matrixValues.push([
            "tuple",
            ...dependencyValues.matrixPre[i2].map((x2) => x2.tree)
          ]);
        }
        let unnormalizedValue = me$3.fromAst([
          "matrix",
          ["tuple", ...dependencyValues.matrixSizePre],
          matrixValues
        ]);
        return { setValue: { unnormalizedValue } };
      },
      inverseDefinition({
        dependencyValues,
        desiredStateVariableValues
      }) {
        let desiredTree = desiredStateVariableValues.unnormalizedValue.tree;
        if (!(Array.isArray(desiredTree) && desiredTree[0] === "matrix" && desiredTree[1]?.[1] === dependencyValues.matrixSizePre[0] && desiredTree[1]?.[2] === dependencyValues.matrixSizePre[1])) {
          return { success: false };
        }
        let desiredMatrix = {};
        for (let i2 = 0; i2 < dependencyValues.matrixSizePre[0]; i2++) {
          for (let j2 = 0; j2 < dependencyValues.matrixSizePre[1]; j2++) {
            desiredMatrix[`${i2},${j2}`] = me$3.fromAst(
              desiredTree[2][i2 + 1][j2 + 1]
            );
          }
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "matrixPre",
              desiredValue: desiredMatrix
            }
          ]
        };
      }
    };
    stateVariableDefinitions.canBeModified = {
      returnDependencies: () => ({
        modifyIndirectly: {
          dependencyType: "stateVariable",
          variableName: "modifyIndirectly"
        },
        fixed: {
          dependencyType: "stateVariable",
          variableName: "fixed"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            canBeModified: dependencyValues.modifyIndirectly && !dependencyValues.fixed
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
const { eigs, square, abs, divide } = me$3.math;
class EigenDecomposition extends BaseComponent {
  static componentType = "eigenDecomposition";
  static rendererType = void 0;
  static componentDocs = {
    summary: "Computes the eigenvalues and eigenvectors of a square matrix"
  };
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "maths",
        componentTypes: ["math"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.decomposition = {
      description: "The eigendecomposition of the matrix as a list of eigenvalue/eigenvector pairs.",
      additionalStateVariablesDefined: [
        {
          variableName: "numEigenvectors",
          description: "The number of eigenvectors found in the decomposition.",
          public: true,
          shadowingInstructions: {
            createComponentOfType: "integer"
          }
        }
      ],
      returnDependencies: () => ({
        mathChild: {
          dependencyType: "child",
          childGroups: ["maths"],
          variableNames: ["value"]
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.mathChild.length === 0) {
          return {
            setValue: { decomposition: null, numEigenvectors: 0 }
          };
        }
        let mathTree = dependencyValues.mathChild[0].stateValues.value.tree;
        if (!Array.isArray(mathTree) || mathTree[0] !== "matrix") {
          return {
            setValue: { decomposition: null, numEigenvectors: 0 }
          };
        }
        let numRows = mathTree[1][1];
        let numColumns = mathTree[1][2];
        if (!(Number.isInteger(numRows) && numColumns === numRows)) {
          return {
            setValue: { decomposition: null, numEigenvectors: 0 }
          };
        }
        let matrixArray = [];
        for (let rowInd = 0; rowInd < numRows; rowInd++) {
          let row = [];
          let rowOperands = mathTree[2][rowInd + 1] || [];
          for (let colInd = 0; colInd < numColumns; colInd++) {
            let val = rowOperands[colInd + 1];
            if (val === void 0 || val === null) {
              return {
                setValue: {
                  decomposition: null,
                  numEigenvectors: 0
                }
              };
            }
            if (typeof val === "number" || typeof val?.re === "number" && typeof val?.im === "number") {
              row.push(val);
            } else {
              try {
                val = me$3.fromAst(val).evaluate_to_constant();
                if (typeof val === "number" || typeof val?.re === "number" && typeof val?.im === "number") {
                  row.push(val);
                } else {
                  row.push(NaN);
                }
              } catch (e3) {
                row.push(NaN);
              }
            }
          }
          matrixArray.push(row);
        }
        let result;
        try {
          result = eigs(matrixArray);
        } catch (e3) {
          console.error(
            "Could nt calculate eigenvalues of matrix",
            e3
          );
          let warning = {
            message: "Could not calculate eigenvalues of matrix",
            type: "warning"
          };
          return {
            setValue: { decomposition: null, numEigenvectors: 0 },
            sendDiagnostics: [warning]
          };
        }
        return {
          setValue: {
            decomposition: result,
            numEigenvectors: result.eigenvectors.length
          }
        };
      }
    };
    stateVariableDefinitions.eigenvalues = {
      description: "The eigenvalues of the matrix.",
      isArray: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing()
      },
      entryPrefixes: ["eigenvalue"],
      returnArraySizeDependencies: () => ({
        decomposition: {
          dependencyType: "stateVariable",
          variableName: "decomposition"
        }
      }),
      returnArraySize({ dependencyValues }) {
        let n2;
        if (dependencyValues.decomposition) {
          n2 = dependencyValues.decomposition.values.length;
        } else {
          n2 = 0;
        }
        return [n2];
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            decomposition: {
              dependencyType: "stateVariable",
              variableName: "decomposition"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues, arraySize }) {
        let eigenvalues = [];
        for (let i2 = 0; i2 < arraySize[0]; i2++) {
          eigenvalues.push(
            globalDependencyValues.decomposition.values[i2]
          );
        }
        return { setValue: { eigenvalues } };
      }
    };
    stateVariableDefinitions.eigenvectors = {
      description: "The eigenvectors of the matrix, as a 2D array (one column per eigenvalue).",
      isArray: true,
      public: true,
      numDimensions: 2,
      shadowingInstructions: {
        createComponentOfType: "number",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "eigenvectorX") {
            return [];
          } else {
            return [
              [
                "vector",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      getArrayKeysFromVarName({
        arrayEntryPrefix,
        varEnding,
        arraySize
      }) {
        if (arrayEntryPrefix === "eigenvectorX") {
          let indices = varEnding.split("_").map((x2) => Number(x2) - 1);
          if (indices.length === 2 && indices.every((x2, i2) => Number.isInteger(x2) && x2 >= 0)) {
            if (arraySize) {
              if (indices.every((x2, i2) => x2 < arraySize[i2])) {
                return [String(indices)];
              } else {
                return [];
              }
            } else {
              return [String(indices)];
            }
          } else {
            return [];
          }
        } else {
          let eigenvectorInd = Number(varEnding) - 1;
          if (!(Number.isInteger(eigenvectorInd) && eigenvectorInd >= 0)) {
            return [];
          }
          if (!arraySize) {
            return [eigenvectorInd + ",0"];
          }
          if (eigenvectorInd < arraySize[0]) {
            return Array.from(
              Array(arraySize[1]),
              (_2, i2) => eigenvectorInd + "," + i2
            );
          } else {
            return [];
          }
        }
      },
      arrayVarNameFromPropIndex(propIndex, varName) {
        if (varName === "eigenvectors") {
          if (propIndex.length === 1) {
            return "eigenvector" + propIndex[0];
          } else {
            return `eigenvectorX${propIndex[0]}_${propIndex[1]}`;
          }
        }
        if (varName.slice(0, 11) === "eigenvector") {
          let eigenvectorNum = Number(varName.slice(11));
          if (Number.isInteger(eigenvectorNum) && eigenvectorNum > 0) {
            return `eigenvectorX${eigenvectorNum}_${propIndex[0]}`;
          }
        }
        return null;
      },
      indexAliases: [[], ["x", "y", "z"]],
      entryPrefixes: ["eigenvectorX", "eigenvector"],
      returnEntryDimensions: (prefix) => prefix === "eigenvector" ? 1 : 0,
      returnArraySizeDependencies: () => ({
        decomposition: {
          dependencyType: "stateVariable",
          variableName: "decomposition"
        }
      }),
      returnArraySize({ dependencyValues }) {
        let m2, n2;
        if (dependencyValues.decomposition) {
          m2 = dependencyValues.decomposition.eigenvectors.length;
          n2 = dependencyValues.decomposition.eigenvectors[0].vector.length;
        } else {
          m2 = 0;
          n2 = 0;
        }
        return [m2, n2];
      },
      returnArrayDependenciesByKey() {
        return {
          globalDependencies: {
            decomposition: {
              dependencyType: "stateVariable",
              variableName: "decomposition"
            }
          }
        };
      },
      arrayDefinitionByKey({ globalDependencyValues, arraySize }) {
        let eigenvectors = {};
        for (let i2 = 0; i2 < arraySize[0]; i2++) {
          let vector = [];
          let vectorMag = 0;
          for (let j2 = 0; j2 < arraySize[1]; j2++) {
            let val = globalDependencyValues.decomposition.eigenvectors[i2].vector[j2];
            vector.push(val);
            vectorMag += square(abs(val));
          }
          vectorMag = Math.sqrt(vectorMag);
          vector = vector.map((x2) => divide(x2, vectorMag));
          for (let j2 = 0; j2 < arraySize[1]; j2++) {
            eigenvectors[`${i2},${j2}`] = vector[j2];
          }
        }
        return { setValue: { eigenvectors } };
      }
    };
    return stateVariableDefinitions;
  }
}
class Latex extends Text {
  static componentType = "latex";
  static componentDocs = {
    summary: "A snippet of LaTeX that can be rendered as math"
  };
  static rendererType = "text";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.isLatex.defaultValue = true;
    return attributes;
  }
  static returnChildGroups() {
    let childGroups = super.returnChildGroups();
    childGroups[0].componentTypes.push("math", "m", "md");
    return childGroups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.value.returnDependencies = () => ({
      mathTextLikeChildren: {
        dependencyType: "child",
        childGroups: ["textLike"],
        variableNames: ["text", "latex"],
        variablesOptional: true
      }
    });
    stateVariableDefinitions.value.definition = function({
      dependencyValues
    }) {
      if (dependencyValues.mathTextLikeChildren.length === 0) {
        return {
          useEssentialOrDefaultValue: {
            value: true
          }
        };
      }
      let pieces = [];
      for (let comp of dependencyValues.mathTextLikeChildren) {
        if (typeof comp === "string") {
          let comptrim = comp.trim();
          if (comptrim) {
            pieces.push(comptrim);
          }
        } else if (comp.stateValues.latex !== void 0) {
          let latex = comp.stateValues.latex.trim();
          if (latex) {
            pieces.push(latex);
          }
        } else {
          let text = comp.stateValues.text.trim();
          if (text) {
            pieces.push(text);
          }
        }
      }
      let value = pieces.join(" ");
      return { setValue: { value } };
    };
    stateVariableDefinitions.value.inverseDefinition = function({
      desiredStateVariableValues,
      dependencyValues
    }) {
      let numChildren = dependencyValues.mathTextLikeChildren.length;
      if (numChildren > 1) {
        return { success: false };
      }
      if (numChildren === 1) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "textLikeChildren",
              desiredValue: desiredStateVariableValues.value,
              childIndex: 0,
              variableIndex: dependencyValues.textLikeChildren[0].stateValues?.latex === void 0 ? 0 : 1
            }
          ]
        };
      }
      return {
        success: true,
        instructions: [
          {
            setEssentialValue: "value",
            value: desiredStateVariableValues.value === null ? "" : String(desiredStateVariableValues.value)
          }
        ]
      };
    };
    stateVariableDefinitions.latex = {
      isAlias: true,
      targetVariableName: "value",
      description: "The LaTeX content as a string."
    };
    return stateVariableDefinitions;
  }
}
class BlockQuote extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "blockQuote";
  static rendererType = "blockQuote";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static includeBlankStringChildren = true;
  static componentDocs = {
    summary: "A block-level quotation"
  };
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnPassThroughListItemChildStateVariableDefinitions()
    );
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
class Description extends BlockComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this)
    });
  }
  static componentType = "description";
  static componentDocs = {
    summary: "Extra information about an enclosing component, shown to all users in a popup or disclosure"
  };
  static rendererType = "containerBlock";
  static renderChildren = true;
  static canDisplayChildErrors = true;
  static includeBlankStringChildren = true;
  // `description` is only valid as an explicit child of the components that
  // declare a `description`/`descriptions` child group; it should not appear
  // as a generic block child everywhere.
  static inSchemaOnlyInheritAs = [];
  static returnChildGroups() {
    return [
      {
        group: "anything",
        componentTypes: ["_base"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnPassThroughListItemChildStateVariableDefinitions()
    );
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
}
const { mod: mod$1 } = me$3.math;
function findAttractedSegmentPointsSub({
  point1,
  point2,
  scales,
  threshold2,
  numericalNearestPointFunction,
  numericalNearestPointAsLineFunction,
  verifyNotOneSided = true
}) {
  let eps = 1e-6;
  let eps2 = eps ** 2;
  let nearestPoint1 = numericalNearestPointAsLineFunction(point1, scales);
  if (!nearestPoint1) {
    return { success: false };
  }
  let distance2 = nearestPoint1.reduce(
    (a2, c2, i2) => a2 + Math.pow(c2 - point1[i2], 2),
    0
  );
  if (distance2 >= threshold2) {
    return { success: false };
  }
  let nearestPoint2 = numericalNearestPointAsLineFunction(point2, scales);
  if (!nearestPoint2) {
    return { success: false };
  }
  distance2 = nearestPoint2.reduce(
    (a2, c2, i2) => a2 + Math.pow(c2 - point2[i2], 2),
    0
  );
  if (distance2 >= threshold2) {
    return { success: false };
  }
  if (verifyNotOneSided) {
    let nearestPoint1NotExtend = numericalNearestPointFunction(
      point1,
      scales
    );
    let nearestPoint1Unchanged = nearestPoint1NotExtend && nearestPoint1NotExtend.every(
      (v2, i2) => Math.abs(v2 - nearestPoint1[i2]) < eps
    );
    let nearestPoint2NotExtend = numericalNearestPointFunction(
      point2,
      scales
    );
    let nearestPoint2Unchanged = nearestPoint2NotExtend && nearestPoint2NotExtend.every(
      (v2, i2) => Math.abs(v2 - nearestPoint2[i2]) < eps
    );
    if (!(nearestPoint1Unchanged || nearestPoint2Unchanged)) {
      distance2 = nearestPoint1NotExtend.reduce(
        (a2, c2, i2) => a2 + Math.pow(c2 - nearestPoint2NotExtend[i2], 2),
        0
      );
      if (distance2 < eps2) {
        return { success: false };
      }
    }
  }
  return {
    success: true,
    nearestPoint1,
    nearestPoint2
  };
}
function findAttractedSegmentPoints({
  point1,
  point2,
  allowRotation,
  scales,
  threshold2,
  numericalNearestPointFunction,
  numericalNearestPointAsLineFunction
}) {
  let result = findAttractedSegmentPointsSub({
    point1,
    point2,
    scales,
    threshold2,
    numericalNearestPointFunction,
    numericalNearestPointAsLineFunction,
    verifyNotOneSided: true
  });
  if (!result.success) {
    return { success: false };
  }
  let eps = 1e-6;
  let eps2 = eps ** 2;
  let { nearestPoint1, nearestPoint2 } = result;
  if (!allowRotation) {
    let original_rel = [point2[0] - point1[0], point2[1] - point1[1]];
    let moved_rel = [
      nearestPoint2[0] - nearestPoint1[0],
      nearestPoint2[1] - nearestPoint1[1]
    ];
    let dTheta = Math.atan2(moved_rel[1], moved_rel[0]) - Math.atan2(original_rel[1], original_rel[0]);
    dTheta = mod$1(dTheta + Math.PI, 2 * Math.PI) - Math.PI;
    if (Math.abs(dTheta) > eps) {
      return { success: false };
    }
  }
  let originalDistance2 = point1.reduce(
    (a2, c2, i2) => a2 + Math.pow(c2 - point2[i2], 2),
    0
  );
  let newDistance2 = nearestPoint1.reduce(
    (a2, c2, i2) => a2 + Math.pow(c2 - nearestPoint2[i2], 2),
    0
  );
  if (Math.abs(originalDistance2 - newDistance2) < eps2) {
    let distance22 = nearestPoint1.reduce(
      (a2, c2, i2) => a2 + Math.pow(c2 - point1[i2], 2),
      0
    ) + nearestPoint2.reduce(
      (a2, c2, i2) => a2 + Math.pow(c2 - point2[i2], 2),
      0
    );
    if (distance22 < threshold2) {
      return {
        success: true,
        distance2: distance22,
        segment: [nearestPoint1, nearestPoint2]
      };
    } else {
      return { success: false };
    }
  }
  if (newDistance2 > originalDistance2) {
    return { success: false };
  }
  let expandFactor = Math.sqrt(originalDistance2 / newDistance2);
  let deviation1 = Math.sqrt(
    point1.reduce((a2, c2, i2) => a2 + Math.pow(c2 - nearestPoint1[i2], 2), 0)
  );
  let deviation2 = Math.sqrt(
    point2.reduce((a2, c2, i2) => a2 + Math.pow(c2 - nearestPoint2[i2], 2), 0)
  );
  let p1 = deviation1 / (deviation1 + deviation2);
  let p2 = 1 - p1;
  let expandFactor1 = (expandFactor - 1) * p1 + 1;
  let expandFactor2 = (expandFactor - 1) * p2 + 1;
  let extendedPoint1 = nearestPoint2.map(
    (v2, i2) => v2 + (nearestPoint1[i2] - v2) * expandFactor1
  );
  let extendedPoint2 = nearestPoint1.map(
    (v2, i2) => v2 + (nearestPoint2[i2] - v2) * expandFactor2
  );
  result = findAttractedSegmentPointsSub({
    point1: extendedPoint1,
    point2: extendedPoint2,
    scales,
    threshold2: eps2,
    numericalNearestPointFunction,
    numericalNearestPointAsLineFunction,
    verifyNotOneSided: false
  });
  if (!result.success) {
    return { success: false };
  }
  let distance2 = extendedPoint1.reduce((a2, c2, i2) => a2 + Math.pow(c2 - point1[i2], 2), 0) + extendedPoint2.reduce((a2, c2, i2) => a2 + Math.pow(c2 - point2[i2], 2), 0);
  if (distance2 < threshold2) {
    return {
      success: true,
      distance2,
      segment: [extendedPoint1, extendedPoint2]
    };
  } else {
    return { success: false };
  }
}
function attractSegment({
  segment,
  allowRotation,
  scales,
  threshold2,
  numericalNearestPointFunctions,
  numericalNearestPointAsLineFunctions
}) {
  let numericalPoint1 = segment[0];
  let numericalPoint2 = segment[1];
  if (numericalPoint1.length != numericalPoint2.length) {
    return {};
  }
  let closestSegment;
  let minDeviation2 = Infinity;
  let amplifiedThreshold2 = allowRotation ? threshold2 * 1e3 : threshold2;
  for (let [
    ind,
    numericalNearestPointFunction
  ] of numericalNearestPointFunctions.entries()) {
    let numericalNearestPointAsLineFunction = numericalNearestPointAsLineFunctions[ind];
    let result = findAttractedSegmentPoints({
      point1: numericalPoint1,
      point2: numericalPoint2,
      allowRotation,
      scales,
      threshold2: amplifiedThreshold2,
      numericalNearestPointFunction,
      numericalNearestPointAsLineFunction
    });
    if (!result.success) {
      continue;
    }
    if (result.distance2 < minDeviation2) {
      minDeviation2 = result.distance2;
      closestSegment = result.segment;
    }
  }
  if (minDeviation2 < threshold2) {
    return {
      constrained: true,
      segment: closestSegment
    };
  } else if (minDeviation2 < amplifiedThreshold2) {
    return {
      constrained: false,
      distance2: minDeviation2,
      segment: closestSegment
    };
  }
  return {};
}
function nearestPointForSegment({ point, segment, scales }) {
  let A1 = segment[0][0];
  let A2 = segment[0][1];
  let B1 = segment[1][0];
  let B2 = segment[1][1];
  let haveConstants = Number.isFinite(A1) && Number.isFinite(A2) && Number.isFinite(B1) && Number.isFinite(B2);
  if (segment[0].length !== 2 || !haveConstants || B1 === A1 && B2 === A2) {
    return null;
  }
  let xscale = scales[0];
  let yscale = scales[1];
  let BA1 = (B1 - A1) / xscale;
  let BA2 = (B2 - A2) / yscale;
  let denom = BA1 * BA1 + BA2 * BA2;
  let t2 = ((point[0] - A1) / xscale * BA1 + (point[1] - A2) / yscale * BA2) / denom;
  if (t2 <= 0) {
    return [A1, A2];
  } else if (t2 >= 1) {
    return [B1, B2];
  } else {
    return [A1 + t2 * BA1 * xscale, A2 + t2 * BA2 * yscale];
  }
}
function nearestPointForSegmentAsLine({ point, segment, scales }) {
  let A1 = segment[0][0];
  let A2 = segment[0][1];
  let B1 = segment[1][0];
  let B2 = segment[1][1];
  let haveConstants = Number.isFinite(A1) && Number.isFinite(A2) && Number.isFinite(B1) && Number.isFinite(B2);
  if (segment[0].length !== 2 || !haveConstants || B1 === A1 && B2 === A2) {
    return null;
  }
  let xscale = scales[0];
  let yscale = scales[1];
  let BA1 = (B1 - A1) / xscale;
  let BA2 = (B2 - A2) / yscale;
  let denom = BA1 * BA1 + BA2 * BA2;
  let t2 = ((point[0] - A1) / xscale * BA1 + (point[1] - A2) / yscale * BA2) / denom;
  return [A1 + t2 * BA1 * xscale, A2 + t2 * BA2 * yscale];
}
const { mod } = me$3.math;
class StickyGroup extends GraphicalComponent {
  static componentType = "stickyGroup";
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      addChildren: this.addChildren.bind(this),
      deleteChildren: this.deleteChildren.bind(this)
    });
  }
  static componentDocs = {
    summary: "Constrains group members to attract to each other when nearby"
  };
  static rendererType = "containerInline";
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.relativeToGraphScales = {
      description: "Whether the snapping threshold is interpreted relative to graph scales.",
      createComponentOfType: "boolean",
      createStateVariable: "relativeToGraphScales",
      defaultValue: false,
      public: true
    };
    attributes.threshold = {
      createComponentOfType: "number",
      description: "Distance threshold within which group members snap together."
    };
    attributes.angleThreshold = {
      description: "Angular distance within which group members snap together.",
      createComponentOfType: "number",
      createStateVariable: "angleThreshold",
      defaultValue: Math.PI * 0.03,
      public: true
    };
    return attributes;
  }
  static returnChildGroups() {
    let groups = super.returnChildGroups();
    groups.push({
      group: "graphical",
      componentTypes: ["_graphical"]
    });
    return groups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let graphInfoDefinitions = returnConstraintGraphInfoDefinitions();
    Object.assign(stateVariableDefinitions, graphInfoDefinitions);
    stateVariableDefinitions.numObjects = {
      returnDependencies: () => ({
        graphicalChildren: {
          dependencyType: "child",
          childGroups: ["graphical"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numObjects: dependencyValues.graphicalChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.numObjects = {
      returnDependencies: () => ({
        graphicalChildren: {
          dependencyType: "child",
          childGroups: ["graphical"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numObjects: dependencyValues.graphicalChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.pointsByObject = {
      returnDependencies: () => ({
        graphicalChildren: {
          dependencyType: "child",
          childGroups: ["graphical"],
          variableNames: [
            "numericalVertices",
            "numericalXs",
            "numericalEndpoints"
          ],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        let pointsByObject = [];
        for (let object of dependencyValues.graphicalChildren) {
          if (object.stateValues.numericalVertices) {
            pointsByObject.push(
              object.stateValues.numericalVertices
            );
          } else if (object.stateValues.numericalXs) {
            pointsByObject.push([object.stateValues.numericalXs]);
          } else if (object.stateValues.numericalEndpoints) {
            pointsByObject.push(
              object.stateValues.numericalEndpoints
            );
          } else {
            pointsByObject.push([]);
          }
        }
        return {
          setValue: {
            pointsByObject
          }
        };
      }
    };
    stateVariableDefinitions.getPointsForObject = {
      returnDependencies: () => ({
        pointsByObject: {
          dependencyType: "stateVariable",
          variableName: "pointsByObject"
        }
      }),
      definition({ dependencyValues }) {
        let getPointsForObject = function(objectInd) {
          return dependencyValues.pointsByObject.filter((v2, i2) => i2 !== objectInd).reduce((a2, c2) => {
            a2.push(...c2);
            return a2;
          }, []);
        };
        return {
          setValue: {
            getPointsForObject
          }
        };
      }
    };
    stateVariableDefinitions.segmentsByObject = {
      returnDependencies: () => ({
        graphicalChildren: {
          dependencyType: "child",
          childGroups: ["graphical"],
          variableNames: [
            "numericalVertices",
            "closed",
            "numericalEndpoints"
          ],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        let segmentsByObject = [];
        for (let object of dependencyValues.graphicalChildren) {
          if (object.stateValues.numericalVertices) {
            let vertices = object.stateValues.numericalVertices;
            let numVertices = vertices.length;
            let segments = [];
            for (let i2 = 1; i2 < numVertices; i2++) {
              segments.push([vertices[i2 - 1], vertices[i2]]);
            }
            if (object.stateValues.closed) {
              segments.push([
                vertices[numVertices - 1],
                vertices[0]
              ]);
            }
            segmentsByObject.push(segments);
          } else if (object.stateValues.numericalEndpoints) {
            let endpoints = object.stateValues.numericalEndpoints;
            let numEndpoints = endpoints.length;
            let segments = [];
            for (let i2 = 1; i2 < numEndpoints; i2++) {
              segments.push([endpoints[i2 - 1], endpoints[i2]]);
            }
            segmentsByObject.push(segments);
          } else {
            segmentsByObject.push([]);
          }
        }
        return {
          setValue: {
            segmentsByObject
          }
        };
      }
    };
    stateVariableDefinitions.getSegmentsForObject = {
      returnDependencies: () => ({
        segmentsByObject: {
          dependencyType: "stateVariable",
          variableName: "segmentsByObject"
        }
      }),
      definition({ dependencyValues }) {
        let getSegmentsForObject = function(objectInd) {
          return dependencyValues.segmentsByObject.filter((v2, i2) => i2 !== objectInd).reduce((a2, c2) => {
            a2.push(...c2);
            return a2;
          }, []);
        };
        return {
          setValue: {
            getSegmentsForObject
          }
        };
      }
    };
    stateVariableDefinitions.threshold = {
      description: "Distance within which group members attract each other.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      hasEssential: true,
      returnDependencies: () => ({
        thresholdAttr: {
          dependencyType: "attributeComponent",
          attributeName: "threshold",
          variableNames: ["value"]
        },
        graphXmin: {
          dependencyType: "stateVariable",
          variableName: "graphXmin"
        },
        relativeToGraphScales: {
          dependencyType: "stateVariable",
          variableName: "relativeToGraphScales"
        }
      }),
      definition({ dependencyValues }) {
        if (dependencyValues.thresholdAttr) {
          return {
            setValue: {
              threshold: dependencyValues.thresholdAttr.stateValues.value
            }
          };
        } else {
          return {
            useEssentialOrDefaultValue: {
              threshold: {
                get defaultValue() {
                  let useRelative = dependencyValues.relativeToGraphScales && dependencyValues.graphXmin !== null;
                  return useRelative ? 0.02 : 0.5;
                }
              }
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.thresholdAttr) {
          return {
            success: true,
            instructions: [
              {
                setDependency: "thresholdAttr",
                desiredValue: desiredStateVariableValues.threshold
              }
            ]
          };
        } else {
          return {
            success: true,
            instructions: [
              {
                setEssentialValue: "threshold",
                value: desiredStateVariableValues.threshold
              }
            ]
          };
        }
      }
    };
    stateVariableDefinitions.verticesConstraintFunction = {
      returnDependencies: () => ({
        getPointsForObject: {
          dependencyType: "stateVariable",
          variableName: "getPointsForObject"
        },
        getSegmentsForObject: {
          dependencyType: "stateVariable",
          variableName: "getSegmentsForObject"
        },
        threshold: {
          dependencyType: "stateVariable",
          variableName: "threshold"
        },
        angleThreshold: {
          dependencyType: "stateVariable",
          variableName: "angleThreshold"
        },
        relativeToGraphScales: {
          dependencyType: "stateVariable",
          variableName: "relativeToGraphScales"
        },
        scales: {
          dependencyType: "stateVariable",
          variableName: "scales"
        }
      }),
      definition({ dependencyValues }) {
        let scales;
        if (dependencyValues.relativeToGraphScales) {
          scales = dependencyValues.scales || [1, 1, 1];
        } else {
          scales = [1, 1, 1];
        }
        let angleThreshold = dependencyValues.angleThreshold;
        let edgeConstraintSub = function({
          numericalUnconstrainedEdges,
          allowRotation,
          enforceRigid,
          shrinkThreshold,
          rotationPoint
        }, { objectInd }) {
          let segmentsToAttract = dependencyValues.getSegmentsForObject(objectInd);
          let numericalConstrainedEdges = [];
          let constraintUsedForEdge = [];
          let foundConstraint = false;
          let almostConstrained = {
            distance2: Infinity
          };
          let threshold = dependencyValues.threshold;
          if (shrinkThreshold) {
            threshold *= 0.2;
          }
          let threshold2 = threshold * threshold;
          for (let [
            edgeInd,
            numericalUnconstrainedEdge
          ] of numericalUnconstrainedEdges.entries()) {
            let numericalNearestPointFunctions = [];
            let numericalNearestPointAsLineFunctions = [];
            for (let segment of segmentsToAttract) {
              numericalNearestPointFunctions.push(
                (point, scales2) => nearestPointForSegment({
                  point,
                  segment,
                  scales: scales2
                })
              );
              numericalNearestPointAsLineFunctions.push(
                (point, scales2) => nearestPointForSegmentAsLine({
                  point,
                  segment,
                  scales: scales2
                })
              );
            }
            let result = attractSegment({
              segment: numericalUnconstrainedEdge,
              allowRotation,
              scales,
              threshold2,
              numericalNearestPointFunctions,
              numericalNearestPointAsLineFunctions
            });
            if (result.constrained) {
              numericalConstrainedEdges.push(result.segment);
              constraintUsedForEdge.push(true);
              foundConstraint = true;
            } else {
              numericalConstrainedEdges.push(
                numericalUnconstrainedEdge
              );
              constraintUsedForEdge.push(false);
              if (!foundConstraint && result.distance2 < almostConstrained.distance2) {
                almostConstrained = result;
                almostConstrained.edgeInd = edgeInd;
              }
            }
          }
          if (allowRotation && enforceRigid && !foundConstraint && almostConstrained.distance2 < Infinity) {
            let attractedEdgeInd = almostConstrained.edgeInd;
            let rotateResult = rotateIfClose({
              attractedEdgeInd,
              attractingSegment: almostConstrained.segment,
              angleThreshold,
              rotationPoint,
              numericalUnconstrainedEdges
            });
            if (rotateResult) {
              numericalConstrainedEdges[attractedEdgeInd] = rotateResult;
              constraintUsedForEdge[attractedEdgeInd] = true;
              foundConstraint = true;
            }
          }
          if (allowRotation && enforceRigid && !foundConstraint) {
            let anglesToAttract = [0, Math.PI / 2];
            let rotateResult = rotateToAttractAngles({
              numericalUnconstrainedEdges,
              anglesToAttract,
              angleThreshold,
              rotationPoint
            });
            if (rotateResult) {
              let ind = rotateResult.rotatedEdgeInd;
              numericalConstrainedEdges[ind] = rotateResult.rotatedEdge;
              constraintUsedForEdge[ind] = true;
              foundConstraint = true;
            }
          }
          return {
            numericalConstrainedEdges,
            constraintUsedForEdge
          };
        };
        let vertexConstraintSub = function({
          numericalUnconstrainedVertices,
          closed,
          shrinkThreshold,
          onlyMoveVertexInd = null
        }, { objectInd }) {
          let pointsToAttract = dependencyValues.getPointsForObject(objectInd);
          let segmentsToAttract = dependencyValues.getSegmentsForObject(objectInd);
          let numericalConstrainedVertices = [];
          let constraintUsedForVertex = [];
          let threshold = dependencyValues.threshold;
          if (shrinkThreshold) {
            threshold *= 0.2;
          }
          let threshold2 = threshold * threshold;
          for (let [
            ind,
            unconstrainedVertex
          ] of numericalUnconstrainedVertices.entries()) {
            if (onlyMoveVertexInd !== null && ind !== onlyMoveVertexInd) {
              numericalConstrainedVertices.push(
                unconstrainedVertex
              );
              constraintUsedForVertex.push(false);
              continue;
            }
            let closestDistance2 = Infinity;
            let closestPoint = {};
            for (let point of pointsToAttract) {
              if (unconstrainedVertex.length !== point.length) {
                continue;
              }
              let distance2 = unconstrainedVertex.reduce(
                (a2, c2, i2) => a2 + Math.pow((c2 - point[i2]) / scales[i2], 2),
                0
              );
              if (distance2 < closestDistance2) {
                closestPoint = point;
                closestDistance2 = distance2;
              }
            }
            let constraintUsed = false;
            let constrainedVertex;
            if (closestDistance2 < threshold2) {
              constraintUsed = true;
              constrainedVertex = closestPoint;
            } else {
              closestDistance2 = Infinity;
              closestPoint = {};
              for (let segment of segmentsToAttract) {
                let point = nearestPointForSegment({
                  point: unconstrainedVertex,
                  segment,
                  scales
                });
                if (point) {
                  let distance2 = unconstrainedVertex.reduce(
                    (a2, c2, i2) => a2 + Math.pow(
                      (c2 - point[i2]) / scales[i2],
                      2
                    ),
                    0
                  );
                  if (distance2 < closestDistance2) {
                    closestPoint = point;
                    closestDistance2 = distance2;
                  }
                }
              }
              if (closestDistance2 < threshold2) {
                constraintUsed = true;
                constrainedVertex = closestPoint;
              } else {
                constrainedVertex = unconstrainedVertex;
              }
            }
            numericalConstrainedVertices.push(constrainedVertex);
            constraintUsedForVertex.push(constraintUsed);
          }
          let numVertices = numericalUnconstrainedVertices.length;
          let stopInd = closed ? numVertices : numVertices - 1;
          let potentialConstraintsForEdges = [];
          for (let vertexInd1 = 0; vertexInd1 < stopInd; vertexInd1++) {
            let vertexInd2 = (vertexInd1 + 1) % numVertices;
            if (onlyMoveVertexInd !== null && !(onlyMoveVertexInd === vertexInd1 || onlyMoveVertexInd === vertexInd2)) {
              continue;
            }
            if (!(constraintUsedForVertex[vertexInd1] || constraintUsedForVertex[vertexInd2])) {
              let numericalVertex1 = numericalUnconstrainedVertices[vertexInd1];
              let numericalVertex2 = numericalUnconstrainedVertices[vertexInd2];
              let closestSegment;
              let closestDistance2 = Infinity;
              for (let attractingPoint of pointsToAttract) {
                let closestPoint = nearestPointForSegment({
                  point: attractingPoint,
                  segment: [
                    numericalVertex1,
                    numericalVertex2
                  ],
                  scales
                });
                if (closestPoint) {
                  let distance2 = closestPoint.reduce(
                    (a2, c2, i2) => a2 + Math.pow(c2 - attractingPoint[i2], 2),
                    0
                  );
                  if (distance2 < closestDistance2) {
                    if (onlyMoveVertexInd === null) {
                      let newPoint1 = numericalVertex1.map(
                        (v2, i2) => v2 + attractingPoint[i2] - closestPoint[i2]
                      );
                      let newPoint2 = numericalVertex2.map(
                        (v2, i2) => v2 + attractingPoint[i2] - closestPoint[i2]
                      );
                      closestSegment = [
                        newPoint1,
                        newPoint2
                      ];
                      closestDistance2 = distance2;
                    } else {
                      let fixedVertex;
                      if (onlyMoveVertexInd === vertexInd1) {
                        fixedVertex = numericalVertex2;
                      } else {
                        fixedVertex = numericalVertex1;
                      }
                      let displacement = [
                        attractingPoint[0] - fixedVertex[0],
                        attractingPoint[1] - fixedVertex[1]
                      ];
                      let displacementLength = Math.sqrt(
                        Math.pow(displacement[0], 2) + Math.pow(
                          displacement[1],
                          2
                        )
                      );
                      let desiredLength = Math.sqrt(
                        Math.pow(
                          numericalVertex1[0] - numericalVertex2[0],
                          2
                        ) + Math.pow(
                          numericalVertex1[1] - numericalVertex2[1],
                          2
                        )
                      );
                      let ratio = desiredLength / displacementLength;
                      if (ratio < 1) {
                        continue;
                      }
                      let movedPoint = fixedVertex.map(
                        (v2, i2) => v2 + displacement[i2] * ratio
                      );
                      let potentialClosestSegment;
                      if (onlyMoveVertexInd === vertexInd1) {
                        potentialClosestSegment = [
                          movedPoint,
                          numericalVertex2
                        ];
                        distance2 = Math.pow(
                          numericalVertex1[0] - movedPoint[0],
                          2
                        ) + Math.pow(
                          numericalVertex1[1] - movedPoint[1],
                          2
                        );
                      } else {
                        potentialClosestSegment = [
                          numericalVertex1,
                          movedPoint
                        ];
                        distance2 = Math.pow(
                          numericalVertex2[0] - movedPoint[0],
                          2
                        ) + Math.pow(
                          numericalVertex2[1] - movedPoint[1],
                          2
                        );
                      }
                      if (distance2 < closestDistance2) {
                        closestDistance2 = distance2;
                        closestSegment = potentialClosestSegment;
                      }
                    }
                  }
                }
              }
              if (closestDistance2 < threshold2) {
                potentialConstraintsForEdges.push({
                  vertexInd1,
                  vertexInd2,
                  distance2: closestDistance2,
                  movedSegment: closestSegment
                });
              }
            }
          }
          potentialConstraintsForEdges.sort(
            (a2, b2) => a2.distance2 - b2.distance2
          );
          for (let {
            vertexInd1,
            vertexInd2,
            movedSegment
          } of potentialConstraintsForEdges) {
            if (!(constraintUsedForVertex[vertexInd1] || constraintUsedForVertex[vertexInd2])) {
              constraintUsedForVertex[vertexInd1] = true;
              constraintUsedForVertex[vertexInd2] = true;
              numericalConstrainedVertices[vertexInd1] = movedSegment[0];
              numericalConstrainedVertices[vertexInd2] = movedSegment[1];
            }
          }
          return {
            numericalConstrainedVertices,
            constraintUsedForVertex
          };
        };
        let edgeConstraintFunction = returnVertexConstraintFunctionFromEdges(edgeConstraintSub);
        let originalVerticesConstraintFunction = returnVertexConstraintFunction(vertexConstraintSub);
        let verticesConstraintFunction = function({
          unconstrainedVertices,
          closed,
          enforceRigid,
          allowRotation,
          rotationPoint,
          shrinkThreshold,
          vertexIndMoved
        }, { objectInd }) {
          let constrainedVertices = edgeConstraintFunction(
            {
              unconstrainedVertices,
              closed,
              enforceRigid,
              allowRotation,
              rotationPoint,
              shrinkThreshold,
              vertexIndMoved
            },
            { objectInd }
          );
          constrainedVertices = originalVerticesConstraintFunction(
            {
              unconstrainedVertices: constrainedVertices,
              closed,
              enforceRigid,
              shrinkThreshold,
              vertexIndMoved
            },
            { objectInd }
          );
          return constrainedVertices;
        };
        return { setValue: { verticesConstraintFunction } };
      }
    };
    stateVariableDefinitions.pointConstraintFunction = {
      returnDependencies: () => ({
        getPointsForObject: {
          dependencyType: "stateVariable",
          variableName: "getPointsForObject"
        },
        getSegmentsForObject: {
          dependencyType: "stateVariable",
          variableName: "getSegmentsForObject"
        },
        threshold: {
          dependencyType: "stateVariable",
          variableName: "threshold"
        },
        relativeToGraphScales: {
          dependencyType: "stateVariable",
          variableName: "relativeToGraphScales"
        },
        scales: {
          dependencyType: "stateVariable",
          variableName: "scales"
        }
      }),
      definition({ dependencyValues }) {
        let scales;
        if (dependencyValues.relativeToGraphScales) {
          scales = dependencyValues.scales || [1, 1, 1];
        } else {
          scales = [1, 1, 1];
        }
        let pointConstraintFunction = function(unconstrainedXs, objectInd) {
          let numericalXs = unconstrainedXs.map(
            (v2) => findFiniteNumericalValue(v2)
          );
          let pointsToAttract = dependencyValues.getPointsForObject(objectInd);
          let segmentsToAttract = dependencyValues.getSegmentsForObject(objectInd);
          let closestDistance2 = Infinity;
          let closestPoint = {};
          for (let point of pointsToAttract) {
            if (numericalXs.length !== point.length) {
              continue;
            }
            let distance2 = numericalXs.reduce(
              (a2, c2, i2) => a2 + Math.pow((c2 - point[i2]) / scales[i2], 2),
              0
            );
            if (distance2 < closestDistance2) {
              closestPoint = point;
              closestDistance2 = distance2;
            }
          }
          if (closestDistance2 < dependencyValues.threshold * dependencyValues.threshold) {
            return closestPoint.map((v2) => me$3.fromAst(v2));
          } else {
            closestDistance2 = Infinity;
            closestPoint = {};
            for (let segment of segmentsToAttract) {
              let point = nearestPointForSegment({
                point: numericalXs,
                segment,
                scales
              });
              if (point) {
                let distance2 = numericalXs.reduce(
                  (a2, c2, i2) => a2 + Math.pow((c2 - point[i2]) / scales[i2], 2),
                  0
                );
                if (distance2 < closestDistance2) {
                  closestPoint = point;
                  closestDistance2 = distance2;
                }
              }
            }
            if (closestDistance2 < dependencyValues.threshold * dependencyValues.threshold) {
              return closestPoint.map((v2) => me$3.fromAst(v2));
            } else {
              return unconstrainedXs;
            }
          }
        };
        return { setValue: { pointConstraintFunction } };
      }
    };
    return stateVariableDefinitions;
  }
  async addChildren(args) {
    return await addChildrenToDynamicChild(this, args);
  }
  async deleteChildren(args) {
    return await deleteChildrenFromDynamicChild(this, args);
  }
}
function rotateToAttractAngles({
  numericalUnconstrainedEdges,
  anglesToAttract,
  angleThreshold,
  rotationPoint
}) {
  let minDAngle = Infinity;
  let attractedEdgeInd;
  for (let [
    edgeInd,
    unconstrainedEdge
  ] of numericalUnconstrainedEdges.entries()) {
    let edgeAngle = Math.atan(
      (unconstrainedEdge[1][1] - unconstrainedEdge[0][1]) / (unconstrainedEdge[1][0] - unconstrainedEdge[0][0])
    );
    for (let attractingAngle of anglesToAttract) {
      let dAngle = mod(attractingAngle - edgeAngle + Math.PI / 2, Math.PI) - Math.PI / 2;
      if (Math.abs(dAngle) < Math.abs(minDAngle)) {
        minDAngle = dAngle;
        attractedEdgeInd = edgeInd;
      }
    }
  }
  if (!(Math.abs(minDAngle) < angleThreshold)) {
    return null;
  }
  let [cx, cy] = rotationPoint;
  let cos_theta = Math.cos(minDAngle);
  let sin_theta = Math.sin(minDAngle);
  let rotatedEdge = numericalUnconstrainedEdges[attractedEdgeInd].map(
    (point) => {
      let dx = point[0] - cx;
      let dy = point[1] - cy;
      return [
        dx * cos_theta - dy * sin_theta + cx,
        dx * sin_theta + dy * cos_theta + cy
      ];
    }
  );
  return { rotatedEdgeInd: attractedEdgeInd, rotatedEdge };
}
function rotateIfClose({
  attractedEdgeInd,
  attractingSegment,
  angleThreshold,
  rotationPoint,
  numericalUnconstrainedEdges
}) {
  let attractingAngle = Math.atan(
    (attractingSegment[1][1] - attractingSegment[0][1]) / (attractingSegment[1][0] - attractingSegment[0][0])
  );
  let edge = numericalUnconstrainedEdges[attractedEdgeInd];
  let edgeAngle = Math.atan(
    (edge[1][1] - edge[0][1]) / (edge[1][0] - edge[0][0])
  );
  let dAngle = mod(attractingAngle - edgeAngle + Math.PI / 2, Math.PI) - Math.PI / 2;
  if (!(Math.abs(dAngle) < angleThreshold)) {
    return null;
  }
  let [cx, cy] = rotationPoint;
  let cos_theta = Math.cos(dAngle);
  let sin_theta = Math.sin(dAngle);
  let rotatedEdge = numericalUnconstrainedEdges[attractedEdgeInd].map(
    (point) => {
      let dx = point[0] - cx;
      let dy = point[1] - cy;
      return [
        dx * cos_theta - dy * sin_theta + cx,
        dx * sin_theta + dy * cos_theta + cy
      ];
    }
  );
  return rotatedEdge;
}
class BlockScoredComponent extends BlockComponent {
  static componentType = "_blockScoredComponent";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    Object.assign(attributes, returnStandardAnswerAttributes());
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "disabled",
      newName: "disabledOriginal"
    });
    Object.assign(
      stateVariableDefinitions,
      returnStandardAnswerStateVariableDefinition()
    );
    return stateVariableDefinitions;
  }
}
function parseIntegerResponse(response) {
  const trimmedResponse = response.trim();
  if (trimmedResponse === "") {
    return null;
  }
  const numberResponse = Number(trimmedResponse);
  return Number.isInteger(numberResponse) ? numberResponse : null;
}
function isDistractorResponse(response) {
  return response.trim().toLowerCase() === "x";
}
function calculateResponseOffset({
  numericResponse,
  effectiveProblemNum,
  numEffectiveProblems
}) {
  return (numericResponse - effectiveProblemNum + numEffectiveProblems) % numEffectiveProblems;
}
function calculatePretzelCredit({
  problemOrder,
  currentResponses,
  distractors,
  mode
}) {
  const distractorSet = new Set(distractors);
  const numProblems = problemOrder.length;
  const numEffectiveProblems = numProblems - distractorSet.size;
  if (numEffectiveProblems === 0) {
    return currentResponses.every(isDistractorResponse) ? 1 : 0;
  }
  const problemNumToEffectiveProblemNum = Array(numProblems).fill(null);
  let effectiveProblemNum = 0;
  for (let problemNum = 0; problemNum < numProblems; problemNum++) {
    if (!distractorSet.has(problemNum)) {
      problemNumToEffectiveProblemNum[problemNum] = effectiveProblemNum;
      effectiveProblemNum++;
    }
  }
  const offsets = [];
  const expectedOffset = mode === "circuit" ? (
    // Circuit mode enforces a +1 step modulo the number of
    // effective problems; when there is exactly one effective
    // problem, +1 wraps to offset 0.
    numEffectiveProblems === 1 ? 0 : 1
  ) : null;
  for (let i2 = 0; i2 < numProblems; i2++) {
    const problemNum = problemOrder[i2];
    const response = currentResponses[i2] ?? "";
    if (distractorSet.has(problemNum)) {
      if (!isDistractorResponse(response)) {
        return 0;
      }
      continue;
    }
    const numericResponse = parseIntegerResponse(response);
    if (numericResponse === null) {
      return 0;
    }
    const effectiveProblemNum2 = problemNumToEffectiveProblemNum[problemNum];
    const offset = calculateResponseOffset({
      numericResponse,
      effectiveProblemNum: effectiveProblemNum2,
      numEffectiveProblems
    });
    if (mode === "circuit") {
      if (offset !== expectedOffset) {
        return 0;
      }
      continue;
    }
    offsets.push(offset);
  }
  if (mode === "circuit") {
    return 1;
  }
  const offset0 = offsets[0];
  const sameOffsets = offsets.every((offset) => offset === offset0);
  return sameOffsets ? 1 : 0;
}
class Pretzel extends BlockScoredComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      recordVisibilityChange: this.recordVisibilityChange.bind(this),
      submitAnswer: this.submitAnswer.bind(this)
    });
  }
  static componentType = "pretzel";
  static componentDocs = {
    summary: "A response-matching component where students enter a sequence to match statements and answers"
  };
  static renderChildren = true;
  static additionalSchemaChildren = ["problem"];
  static additionalSchemaChildrenDoNotInherit = true;
  static createAttributesObject() {
    const attributes = super.createAttributesObject();
    attributes.maxNumColumns = {
      description: "Maximum number of columns when arranging child problems.",
      createComponentOfType: "integer",
      createStateVariable: "maxNumColumns",
      defaultValue: 2,
      public: true,
      forRenderer: true,
      clamp: [1, 4]
    };
    attributes.mode = {
      description: "Arrangement mode for the child problems.",
      createPrimitiveOfType: "string",
      createStateVariable: "mode",
      defaultValue: "pretzel",
      public: true,
      forRenderer: true,
      toLowerCase: true,
      validValues: [
        {
          value: "pretzel",
          description: "Interleaved arrangement where each problem's answer feeds the next and one can start with any problem."
        },
        {
          value: "circuit",
          description: "A variant of the pretzel where one must start with the first problem."
        }
      ]
    };
    return attributes;
  }
  static returnChildGroups() {
    return [
      {
        group: "problems",
        componentTypes: ["statement"],
        excludeFromSchema: true
      },
      {
        group: "textInputs",
        componentTypes: ["textInput"],
        excludeFromSchema: true
      },
      {
        group: "givenAnswers",
        componentTypes: ["span"],
        excludeFromSchema: true
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.textInputs = {
      returnDependencies: () => ({
        textInputs: {
          dependencyType: "child",
          childGroups: ["textInputs"]
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            textInputs: dependencyValues.textInputs
          }
        };
      }
    };
    stateVariableDefinitions.problemOrder = {
      stateVariablesDeterminingDependencies: ["textInputs"],
      additionalStateVariablesDefined: ["distractors"],
      returnDependencies({ stateValues }) {
        if (stateValues.textInputs.length > 0) {
          return {
            problemOrder: {
              dependencyType: "sourceCompositeStateVariable",
              replacementIdx: stateValues.textInputs[0].componentIdx,
              variableName: "problemOrder"
            },
            distractors: {
              dependencyType: "sourceCompositeStateVariable",
              replacementIdx: stateValues.textInputs[0].componentIdx,
              variableName: "distractors"
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            problemOrder: dependencyValues.problemOrder ?? [],
            distractors: dependencyValues.distractors ?? []
          }
        };
      }
    };
    stateVariableDefinitions.numProblems = {
      description: "The number of child problems.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        problemChildren: {
          dependencyType: "child",
          childGroups: ["problems"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numProblems: dependencyValues.problemChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.currentResponses = {
      description: "The current responses across all child problems.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      isArray: true,
      entryPrefixes: ["currentResponse"],
      returnArraySizeDependencies: () => ({
        numProblems: {
          dependencyType: "stateVariable",
          variableName: "numProblems"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numProblems];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        const dependenciesByKey = {};
        for (const arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            textInput: {
              dependencyType: "child",
              childGroups: ["textInputs"],
              childIndices: [arrayKey],
              variableNames: ["value"]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey }) {
        const currentResponses = {};
        for (const arrayKey in dependencyValuesByKey) {
          currentResponses[arrayKey] = dependencyValuesByKey[arrayKey].textInput[0].stateValues.value;
        }
        return {
          setValue: { currentResponses }
        };
      }
    };
    stateVariableDefinitions.creditAchievedIfSubmit = {
      returnDependencies: () => ({
        problemOrder: {
          dependencyType: "stateVariable",
          variableName: "problemOrder"
        },
        currentResponses: {
          dependencyType: "stateVariable",
          variableName: "currentResponses"
        },
        distractors: {
          dependencyType: "stateVariable",
          variableName: "distractors"
        },
        mode: {
          dependencyType: "stateVariable",
          variableName: "mode"
        }
      }),
      definition({ dependencyValues }) {
        const problemOrder = dependencyValues.problemOrder.map(
          (p2) => p2 - 1
        );
        return {
          setValue: {
            creditAchievedIfSubmit: calculatePretzelCredit({
              problemOrder,
              currentResponses: dependencyValues.currentResponses,
              distractors: dependencyValues.distractors,
              mode: dependencyValues.mode
            })
          }
        };
      }
    };
    stateVariableDefinitions.numSubmittedResponses = {
      description: "The number of responses submitted.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      hasEssential: true,
      defaultValue: 0,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          numSubmittedResponses: true
        }
      }),
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "numSubmittedResponses",
              value: desiredStateVariableValues.numSubmittedResponses
            }
          ]
        };
      }
    };
    stateVariableDefinitions.submittedResponses = {
      description: "The most recently submitted responses.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      isArray: true,
      allowExtraArrayKeysInInverse: true,
      entryPrefixes: ["submittedResponse"],
      defaultValueByArrayKey: () => "＿",
      hasEssential: true,
      inverseShadowToSetEntireArray: true,
      doNotCombineInverseArrayInstructions: true,
      returnArraySizeDependencies: () => ({
        numSubmittedResponses: {
          dependencyType: "stateVariable",
          variableName: "numSubmittedResponses"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numSubmittedResponses];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          numSubmittedResponses: {
            dependencyType: "stateVariable",
            variableName: "numSubmittedResponses"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey({ globalDependencyValues }) {
        let componentType = [];
        if (globalDependencyValues.submittedResponsesComponentType) {
          componentType.push(
            ...globalDependencyValues.submittedResponsesComponentType.slice(
              0,
              globalDependencyValues.numSubmittedResponses
            )
          );
        }
        let essentialSubmittedResponses = {};
        for (let ind = 0; ind < globalDependencyValues.numSubmittedResponses; ind++) {
          essentialSubmittedResponses[ind] = true;
        }
        return {
          useEssentialOrDefaultValue: {
            submittedResponses: essentialSubmittedResponses
          }
        };
      },
      inverseArrayDefinitionByKey: function({
        desiredStateVariableValues,
        initialChange
      }) {
        if (!initialChange) {
          return { success: false };
        }
        return {
          success: true,
          instructions: [
            {
              setDependency: "numSubmittedResponses",
              desiredValue: desiredStateVariableValues.submittedResponses.length
            },
            {
              setEssentialValue: "submittedResponses",
              value: [
                ...desiredStateVariableValues.submittedResponses
              ]
            }
          ]
        };
      }
    };
    stateVariableDefinitions.showCheckWork = {
      forRenderer: true,
      returnDependencies: () => ({
        suppressCheckWork: {
          dependencyType: "stateVariable",
          variableName: "suppressCheckWork"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            showCheckWork: !dependencyValues.suppressCheckWork
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
  recordVisibilityChange({ isVisible }) {
    this.coreFunctions.requestRecordEvent({
      verb: "visibilityChanged",
      object: {
        componentIdx: this.componentIdx,
        componentType: this.componentType
      },
      result: { isVisible }
    });
  }
  async submitAnswer({
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    const numAttemptsLeft = await this.stateValues.numAttemptsLeft;
    if (numAttemptsLeft < 1) {
      return;
    }
    const disabled = await this.stateValues.disabled;
    if (disabled) {
      return;
    }
    const creditAchieved = await this.stateValues.handGraded ? 0 : await this.stateValues.creditAchievedIfSubmit;
    let instructions = [
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "creditAchieved",
        value: creditAchieved
      },
      {
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "responseHasBeenSubmitted",
        value: true
      }
    ];
    let currentResponses = await this.stateValues.currentResponses;
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "submittedResponses",
      value: currentResponses
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "justSubmitted",
      value: true
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "creditAchievedDependenciesAtSubmit",
      value: await this.stateValues.creditAchievedDependencies
    });
    instructions.push({
      updateType: "updateValue",
      componentIdx: this.componentIdx,
      stateVariable: "numSubmissions",
      value: await this.stateValues.numSubmissions + 1
    });
    if (creditAchieved < 1) {
      instructions.push({
        updateType: "updateValue",
        componentIdx: this.componentIdx,
        stateVariable: "numIncorrectSubmissions",
        value: await this.stateValues.numIncorrectSubmissions + 1
      });
    }
    const responseText = [];
    for (const response of currentResponses) {
      if (response.toString) {
        try {
          responseText.push(response.toString());
        } catch (e3) {
          responseText.push("＿");
        }
      } else {
        responseText.push(response);
      }
    }
    instructions.push({
      updateType: "recordItemSubmission",
      componentNumber: await this.stateValues.inComponentNumber,
      submittedComponent: this.componentIdx,
      response: currentResponses,
      responseText,
      creditAchieved
    });
    await this.coreFunctions.performUpdate({
      updateInstructions: instructions,
      actionId,
      sourceInformation,
      skipRendererUpdate: true,
      event: {
        verb: "submitted",
        object: {
          componentIdx: this.componentIdx,
          componentType: this.componentType,
          answerNumber: this.answerNumber,
          rootName: this.rootName
        },
        result: {
          response: currentResponses,
          responseText,
          componentTypes: Array(currentResponses.length).fill("text"),
          creditAchieved
        }
      }
    });
    return await this.coreFunctions.triggerChainedActions({
      componentIdx: this.componentIdx,
      actionId,
      sourceInformation,
      skipRendererUpdate
    });
  }
}
function getCorrespondingAnswerIdx({ problemIdx, numProblems, distractorSet }) {
  if (distractorSet.has(problemIdx)) {
    return problemIdx;
  }
  let answerIdx = (problemIdx - 1 + numProblems) % numProblems;
  let numIndicesChecked = 0;
  while (distractorSet.has(answerIdx) && numIndicesChecked < numProblems) {
    answerIdx = (answerIdx - 1 + numProblems) % numProblems;
    numIndicesChecked++;
  }
  return answerIdx;
}
function normalizePretzelMode(mode) {
  return mode?.toLowerCase() === "circuit" ? "circuit" : "pretzel";
}
function getProblemIndicesToPermute({ numProblems, mode }) {
  const start = mode === "circuit" ? 2 : 1;
  return [...Array(Math.max(numProblems - start + 1, 0)).keys()].map(
    (x2) => x2 + start
  );
}
function buildProblemOrderFromPermutedIndices({
  permutedIndices,
  numProblems,
  mode
}) {
  if (mode === "circuit") {
    return numProblems === 0 ? [] : [1, ...permutedIndices];
  }
  return permutedIndices;
}
function determineNumberOfPermutations({ numProblems, mode }) {
  const numProblemsToPermute = getProblemIndicesToPermute({
    numProblems,
    mode
  }).length;
  let numberOfPermutations = 1;
  for (let i2 = 2; i2 <= numProblemsToPermute; i2++) {
    numberOfPermutations *= i2;
  }
  return numberOfPermutations;
}
function shuffleProblemOrder({ numProblems, variantRng, mode }) {
  const problemIndices = getProblemIndicesToPermute({ numProblems, mode });
  for (let i2 = problemIndices.length - 1; i2 > 0; i2--) {
    const rand = variantRng();
    const j2 = Math.floor(rand * (i2 + 1));
    [problemIndices[i2], problemIndices[j2]] = [
      problemIndices[j2],
      problemIndices[i2]
    ];
  }
  return buildProblemOrderFromPermutedIndices({
    permutedIndices: problemIndices,
    numProblems,
    mode
  });
}
class PretzelArranger extends CompositeComponent {
  static componentType = "_pretzelArranger";
  static componentDocs = {
    summary: "Internal arranger that lays out subset visualizations within <pretzel>."
  };
  static createsVariants = true;
  static serializeReplacementsForChildren = true;
  static replacementsAlreadyInResolver = true;
  static useSerializedChildrenComponentIndices = true;
  static createAttributesObject() {
    const attributes = super.createAttributesObject();
    attributes.mode = {
      createPrimitiveOfType: "string",
      createStateVariable: "mode",
      defaultValue: "pretzel",
      toLowerCase: true,
      validValues: [
        {
          value: "pretzel",
          description: "Interleaved arrangement where each problem's answer feeds the next and one can start with any problem."
        },
        {
          value: "circuit",
          description: "A variant of the pretzel where one must start with the first problem."
        }
      ]
    };
    return attributes;
  }
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.serializedProblemChildren = {
      returnDependencies: () => ({
        serializedChildren: {
          dependencyType: "serializedChildren",
          doNotProxy: true
        }
      }),
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            serializedProblemChildren: dependencyValues.serializedChildren.filter(
              (child) => child.componentType === "problem"
            )
          }
        };
      }
    };
    stateVariableDefinitions.numProblems = {
      description: "The number of problems arranged.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        serializedProblemChildren: {
          dependencyType: "stateVariable",
          variableName: "serializedProblemChildren"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numProblems: dependencyValues.serializedProblemChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.problemOrder = {
      forRenderer: true,
      shadowVariable: true,
      returnDependencies: ({ sharedParameters }) => ({
        mode: {
          dependencyType: "stateVariable",
          variableName: "mode"
        },
        numProblems: {
          dependencyType: "stateVariable",
          variableName: "numProblems"
        },
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        rngClass: {
          dependencyType: "value",
          value: sharedParameters.rngClass,
          doNotProxy: true
        },
        variants: {
          dependencyType: "variants"
        }
      }),
      definition: function({ dependencyValues }) {
        let numProblems = dependencyValues.numProblems;
        let diagnostics = [];
        let problemOrder;
        const mode = dependencyValues.mode;
        let desiredProblemOrder = dependencyValues.variants?.desiredVariant?.indices;
        if (desiredProblemOrder !== void 0) {
          if (desiredProblemOrder.length !== numProblems) {
            diagnostics.push({
              message: "Ignoring indices specified for problem as number of indices doesn't match number of problem children.",
              type: "info"
            });
          } else {
            desiredProblemOrder = desiredProblemOrder.map(Number);
            if (!desiredProblemOrder.every(Number.isInteger)) {
              throw Error(
                "All indices specified for pretzel must be integers"
              );
            }
            if (!desiredProblemOrder.every(
              (x2) => x2 >= 1 && x2 <= numProblems
            )) {
              diagnostics.push({
                message: "Ignoring indices specified for pretzel as some indices out of range.",
                type: "info"
              });
            } else if (new Set(desiredProblemOrder).size !== numProblems) {
              diagnostics.push({
                message: "Ignoring indices specified for pretzel as some indices are repeated.",
                type: "info"
              });
            } else if (mode === "circuit" && numProblems > 0 && desiredProblemOrder[0] !== 1) {
              diagnostics.push({
                message: "Ignoring indices specified for pretzel in circuit mode as the first index must be 1.",
                type: "info"
              });
            } else {
              return {
                setValue: {
                  problemOrder: desiredProblemOrder
                }
              };
            }
          }
        }
        let variantRng = dependencyValues.rngClass(
          dependencyValues.variantSeed + "co"
        );
        problemOrder = shuffleProblemOrder({
          numProblems,
          variantRng,
          mode
        });
        return {
          setValue: { problemOrder },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.generatedVariantInfo = {
      additionalStateVariablesDefined: ["isVariantComponent"],
      returnDependencies: ({
        componentInfoObjects,
        sharedParameters
      }) => ({
        variantSeed: {
          dependencyType: "value",
          value: sharedParameters.variantSeed
        },
        problemOrder: {
          dependencyType: "stateVariable",
          variableName: "problemOrder"
        },
        variantDescendants: {
          dependencyType: "descendant",
          componentTypes: Object.keys(
            componentInfoObjects.componentTypesCreatingVariants
          ),
          variableNames: [
            "isVariantComponent",
            "generatedVariantInfo"
          ],
          useReplacementsForComposites: true,
          recurseToMatchedChildren: false,
          variablesOptional: true,
          includeNonActiveChildren: true,
          ignoreReplacementsOfEncounteredComposites: true
        }
      }),
      definition({ dependencyValues, componentIdx }) {
        let generatedVariantInfo = {
          seed: dependencyValues.variantSeed,
          meta: { createdBy: componentIdx },
          indices: dependencyValues.problemOrder
        };
        let subvariants = generatedVariantInfo.subvariants = [];
        for (let descendant of dependencyValues.variantDescendants) {
          if (descendant.stateValues.isVariantComponent) {
            subvariants.push(
              descendant.stateValues.generatedVariantInfo
            );
          } else if (descendant.stateValues.generatedVariantInfo) {
            subvariants.push(
              ...descendant.stateValues.generatedVariantInfo.subvariants
            );
          }
        }
        return {
          setValue: {
            generatedVariantInfo,
            isVariantComponent: true
          }
        };
      }
    };
    stateVariableDefinitions.statements = {
      additionalStateVariablesDefined: [
        "givenAnswers",
        "validProblems",
        "distractors"
      ],
      returnDependencies: () => ({
        serializedProblemChildren: {
          dependencyType: "stateVariable",
          variableName: "serializedProblemChildren"
        },
        mode: {
          dependencyType: "stateVariable",
          variableName: "mode"
        }
      }),
      definition({ dependencyValues }) {
        const statements = [];
        const givenAnswers = [];
        const distractors = [];
        let validProblems = true;
        const diagnostics = [];
        for (const [
          idx,
          problem
        ] of dependencyValues.serializedProblemChildren.entries()) {
          let lastStatement = null;
          let lastGivenAnswer = null;
          for (const child of problem.children) {
            if (child.componentType === "statement") {
              lastStatement = child;
            } else if (child.componentType === "givenAnswer") {
              const givenAnswerContent = child.children.find(
                (grandchild) => grandchild.componentType === "_postponeRenderContainer"
              );
              if (givenAnswerContent) {
                lastGivenAnswer = {
                  ...givenAnswerContent,
                  componentType: "span",
                  attributes: {
                    ...givenAnswerContent.attributes
                  },
                  children: (givenAnswerContent.children ?? []).filter(
                    (grandchild) => grandchild.componentType !== "label" && grandchild.componentType !== "shortDescription"
                  )
                };
                lastGivenAnswer.componentIdx = child.componentIdx;
                if (child.attributes.name) {
                  lastGivenAnswer.attributes.name = child.attributes.name;
                }
              }
            }
          }
          if (lastStatement === null || lastGivenAnswer === null) {
            validProblems = false;
          }
          statements.push(lastStatement);
          givenAnswers.push(lastGivenAnswer);
          if (problem.attributes.isDistractor?.primitive.value) {
            distractors.push(idx);
          }
        }
        if (!validProblems) {
          diagnostics.push({
            message: "Invalid pretzel: each `<problem>` must contain one `<statement>` and one `<answer>`.",
            type: "warning"
          });
        }
        if (dependencyValues.mode === "circuit" && distractors.includes(0)) {
          diagnostics.push({
            message: 'Invalid pretzel: in mode="circuit", the first `<problem>` cannot be a distractor.',
            type: "error"
          });
        }
        return {
          setValue: {
            statements,
            givenAnswers,
            validProblems,
            distractors
          },
          sendDiagnostics: diagnostics
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        problemOrder: {
          dependencyType: "stateVariable",
          variableName: "problemOrder"
        },
        statements: {
          dependencyType: "stateVariable",
          variableName: "statements"
        },
        givenAnswers: {
          dependencyType: "stateVariable",
          variableName: "givenAnswers"
        }
      }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    nComponents,
    workspace
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    const diagnostics = [];
    let replacements = [];
    const numProblems = await component.stateValues.numProblems;
    const problemOrder = await component.stateValues.problemOrder;
    const statements = await component.stateValues.statements;
    const givenAnswers = await component.stateValues.givenAnswers;
    const distractors = await component.stateValues.distractors;
    const mode = await component.stateValues.mode;
    const distractorSet = new Set(distractors);
    for (let i2 = 0; i2 < numProblems; i2++) {
      const problemIdx = problemOrder[i2] - 1;
      const thisStatement = statements[problemIdx];
      const answerIdx = getCorrespondingAnswerIdx({
        problemIdx,
        numProblems,
        distractorSet
      });
      const correspondingAnswer = givenAnswers[answerIdx];
      if (correspondingAnswer === null) {
        replacements.push({
          type: "serialized",
          componentType: "span",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          state: {},
          children: []
        });
      } else {
        replacements.push(correspondingAnswer);
      }
      replacements.push({
        type: "serialized",
        componentType: "textInput",
        componentIdx: nComponents++,
        stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
        attributes: {
          width: {
            type: "component",
            name: "width",
            component: {
              type: "serialized",
              componentType: "componentSize",
              componentIdx: nComponents++,
              stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
              state: {
                componentSize: { size: 15, isAbsolute: true }
              },
              children: [],
              attributes: {},
              doenetAttributes: {}
            }
          }
        },
        doenetAttributes: {},
        state: mode === "circuit" && i2 === 0 ? { value: "1", disabledPreliminary: true } : {},
        children: [
          {
            type: "serialized",
            componentType: "shortDescription",
            componentIdx: nComponents++,
            stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
            state: {},
            children: [
              {
                type: "serialized",
                componentType: "text",
                componentIdx: nComponents++,
                stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                state: {
                  value: "Enter number in sequence for this answer or X if it is a distractor"
                },
                children: [],
                attributes: {},
                doenetAttributes: {}
              }
            ],
            attributes: {},
            doenetAttributes: {}
          }
        ]
      });
      if (thisStatement === null) {
        replacements.push({
          type: "serialized",
          componentType: "statement",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: {},
          doenetAttributes: {},
          state: {},
          children: []
        });
      } else {
        replacements.push(thisStatement);
      }
    }
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static setUpVariant({
    serializedComponent,
    sharedParameters,
    descendantVariantComponents
  }) {
    setUpVariantSeedAndRng({
      serializedComponent,
      sharedParameters,
      descendantVariantComponents
    });
  }
  static determineNumberOfUniqueVariants({
    serializedComponent,
    componentInfoObjects,
    infoDiagnostics
  }) {
    let numProblems = 0;
    const mode = normalizePretzelMode(
      serializedComponent.attributes.mode?.primitive?.value
    );
    for (let child of serializedComponent.children) {
      if (child.componentType === "problem") {
        numProblems++;
      } else if (componentInfoObjects.isInheritedComponentType({
        inheritedComponentType: child.componentType,
        baseComponentType: "_composite"
      }) && child.attributes.createComponentOfType?.primitive.value === "problem") {
        if (child.attributes.numProblems?.primitive !== void 0) {
          let newProblems = Number(
            child.attributes.numProblems?.primitive.value
          );
          if (Number.isInteger(newProblems) && newProblems >= 0) {
            numProblems += newProblems;
          } else {
            return { success: false };
          }
        } else {
          numProblems++;
        }
      } else {
        return { success: false };
      }
    }
    let numberOfPermutations = determineNumberOfPermutations({
      numProblems,
      mode
    });
    let result = super.determineNumberOfUniqueVariants({
      serializedComponent,
      componentInfoObjects,
      infoDiagnostics
    });
    if (!result.success) {
      return { success: false };
    }
    let numVariants = result.numVariants * numberOfPermutations;
    serializedComponent.variants.numVariants = numVariants;
    serializedComponent.variants.uniqueVariantData = {
      numVariantsByDescendant: serializedComponent.variants.uniqueVariantData.numVariantsByDescendant,
      numberOfPermutations,
      numProblems,
      mode
    };
    return { success: true, numVariants };
  }
  static getUniqueVariant({
    serializedComponent,
    variantIndex,
    componentInfoObjects
  }) {
    let numVariants = serializedComponent.variants?.numVariants;
    if (numVariants === void 0) {
      return { success: false };
    }
    if (!Number.isInteger(variantIndex) || variantIndex < 1 || variantIndex > numVariants) {
      return { success: false };
    }
    let numVariantsByDescendant = serializedComponent.variants.uniqueVariantData.numVariantsByDescendant;
    let descendantVariantComponents = serializedComponent.variants.descendantVariantComponents;
    let numberOfPermutations = serializedComponent.variants.uniqueVariantData.numberOfPermutations;
    let numProblems = serializedComponent.variants.uniqueVariantData.numProblems;
    const mode = normalizePretzelMode(
      serializedComponent.variants.uniqueVariantData.mode
    );
    let numbersOfOptions = [...numVariantsByDescendant];
    numbersOfOptions.push(numberOfPermutations);
    let indicesForEachOption = enumerateCombinations({
      numberOfOptionsByIndex: numbersOfOptions,
      maxNumber: variantIndex
    })[variantIndex - 1].map((x2) => x2 + 1);
    let permutationsIndex = indicesForEachOption.pop();
    let indicesForEachDescendant = indicesForEachOption;
    const indicesToPermute = getProblemIndicesToPermute({
      numProblems,
      mode
    });
    const permutedIndices = buildProblemOrderFromPermutedIndices({
      permutedIndices: enumeratePermutations({
        values: indicesToPermute,
        maxNumber: permutationsIndex
      })[permutationsIndex - 1],
      numProblems,
      mode
    });
    let haveNontrivialSubvariants = false;
    let subvariants = [];
    for (let descendantNum = 0; descendantNum < numVariantsByDescendant.length; descendantNum++) {
      if (numVariantsByDescendant[descendantNum] > 1) {
        let descendant = descendantVariantComponents[descendantNum];
        let compClass = componentInfoObjects.allComponentClasses[descendant.componentType];
        let result = compClass.getUniqueVariant({
          serializedComponent: descendant,
          variantIndex: indicesForEachDescendant[descendantNum],
          componentInfoObjects
        });
        if (!result.success) {
          return { success: false };
        }
        subvariants.push(result.desiredVariant);
        haveNontrivialSubvariants = true;
      } else {
        subvariants.push({});
      }
    }
    let desiredVariant = { indices: permutedIndices };
    if (haveNontrivialSubvariants) {
      desiredVariant.subvariants = subvariants;
    }
    return { success: true, desiredVariant };
  }
}
class Cascade extends SectioningComponent {
  static componentType = "cascade";
  static componentDocs = {
    summary: "Sectional component that reveals its children step-by-step"
  };
  static rendererType = "section";
  static includeBlankStringChildren = false;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.hideFutureSections = {
      createComponentOfType: "boolean",
      createStateVariable: "hideFutureSections",
      defaultValue: false,
      public: true,
      description: "Whether to hide later cascade sections until previous ones are completed."
    };
    attributes.noAutoTitle.defaultValue = true;
    attributes.asList.createStateVariable = "asListPreliminary";
    attributes.asList.public = false;
    attributes.revealAll = {
      createComponentOfType: "boolean",
      createStateVariable: "revealAllPreliminary",
      defaultValue: false,
      description: "Whether all cascade entries should be revealed regardless of progress."
    };
    attributes.boxAll = {
      createComponentOfType: "boolean",
      createStateVariable: "boxAll",
      defaultValue: false,
      description: "Whether to draw boxes around all cascade entries regardless of progress."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.isListItem = {
      forRenderer: true,
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { isListItem: false } })
    };
    stateVariableDefinitions.asList = {
      description: "Whether to render this section's children as a list.",
      public: true,
      forRenderer: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        asListPreliminary: {
          dependencyType: "stateVariable",
          variableName: "asListPreliminary"
        },
        parentAsList: {
          dependencyType: "parentStateVariable",
          variableName: "asList"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let asList = dependencyValues.parentAsList;
        if (!usedDefault.asListPreliminary) {
          asList = dependencyValues.asListPreliminary;
        }
        return { setValue: { asList: Boolean(asList) } };
      }
    };
    stateVariableDefinitions.childrenAggregateScores = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { childrenAggregateScores: true } })
    };
    stateVariableDefinitions.childCreditAchieved = {
      returnDependencies: () => ({
        children: {
          dependencyType: "child",
          childGroups: ["anything"],
          variableNames: ["creditAchieved"],
          variablesOptional: true
        }
      }),
      definition({ dependencyValues }) {
        const childCreditAchieved = dependencyValues.children.map(
          (child) => child.stateValues?.creditAchieved ?? null
        );
        return { setValue: { childCreditAchieved } };
      }
    };
    stateVariableDefinitions.numCompleted = {
      description: "The number of cascade sections currently completed.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "integer"
      },
      returnDependencies: () => ({
        childCreditAchieved: {
          dependencyType: "stateVariable",
          variableName: "childCreditAchieved"
        }
      }),
      definition({ dependencyValues }) {
        let numCompleted = 0;
        for (const childCredit of dependencyValues.childCreditAchieved) {
          if (childCredit === 1 || childCredit === null) {
            numCompleted++;
          } else {
            break;
          }
        }
        return { setValue: { numCompleted } };
      }
    };
    stateVariableDefinitions.revealAll = {
      returnDependencies: () => ({
        cascadeAncestor: {
          dependencyType: "ancestor",
          componentType: "cascade",
          variableNames: ["revealAll"]
        },
        revealAllPreliminary: {
          dependencyType: "stateVariable",
          variableName: "revealAllPreliminary"
        }
      }),
      definition({ dependencyValues, usedDefault }) {
        let revealAll = false;
        if (!usedDefault.revealAllPreliminary) {
          revealAll = dependencyValues.revealAllPreliminary;
        } else if (dependencyValues.cascadeAncestor) {
          revealAll = dependencyValues.cascadeAncestor.stateValues.revealAll;
        } else {
          dependencyValues.revealAllPreliminary;
        }
        return { setValue: { revealAll } };
      }
    };
    stateVariableDefinitions.childrenToHide = {
      additionalStateVariablesDefined: ["childrenToHideChildren"],
      returnDependencies: () => ({
        hideFutureSections: {
          dependencyType: "stateVariable",
          variableName: "hideFutureSections"
        },
        numCompleted: {
          dependencyType: "stateVariable",
          variableName: "numCompleted"
        },
        children: {
          dependencyType: "child",
          childGroups: ["anything"]
        },
        childrenWithCascadeMessages: {
          dependencyType: "child",
          childGroups: ["anything", "cascadeMessages"]
        },
        revealAll: {
          dependencyType: "stateVariable",
          variableName: "revealAll"
        },
        hideChildren: {
          dependencyType: "stateVariable",
          variableName: "hideChildren"
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        const allContinuationComponentIndices = dependencyValues.childrenWithCascadeMessages.filter(
          (child) => child.componentType === "cascadeMessage"
        ).map((child) => child.componentIdx);
        if (dependencyValues.revealAll) {
          return {
            setValue: {
              childrenToHide: allContinuationComponentIndices,
              childrenToHideChildren: []
            }
          };
        }
        const childrenToHide = [];
        const childrenToHideChildren = [];
        for (const [
          idx,
          child
        ] of dependencyValues.children.entries()) {
          if (idx <= dependencyValues.numCompleted) {
            if (dependencyValues.hideChildren) {
              childrenToHide.push(child.componentIdx);
            }
          } else if (!dependencyValues.hideChildren && !dependencyValues.hideFutureSections && componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "_sectioningComponent"
          })) {
            childrenToHideChildren.push(child.componentIdx);
          } else {
            childrenToHide.push(child.componentIdx);
          }
        }
        if (dependencyValues.numCompleted <= dependencyValues.children.length - 2) {
          const lastShownChild = dependencyValues.children[dependencyValues.numCompleted].componentIdx;
          const lastShownChildIdx = dependencyValues.childrenWithCascadeMessages.findIndex(
            (child) => child.componentIdx === lastShownChild
          );
          const nextContinuation = dependencyValues.childrenWithCascadeMessages.slice(lastShownChildIdx + 1).find(
            (child) => child.componentType === "cascadeMessage"
          );
          if (nextContinuation) {
            childrenToHide.push(
              ...allContinuationComponentIndices.filter(
                (cIdx) => cIdx !== nextContinuation.componentIdx
              )
            );
          } else {
            childrenToHide.push(...allContinuationComponentIndices);
          }
        } else {
          childrenToHide.push(...allContinuationComponentIndices);
        }
        return {
          setValue: {
            childrenToHide,
            childrenToHideChildren
          }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class DynamicChildren extends CompositeComponent {
  constructor(args) {
    super(args);
    Object.assign(this.actions, {
      addChildren: this.addChildren.bind(this),
      deleteChildren: this.deleteChildren.bind(this)
    });
  }
  static componentType = "_dynamicChildren";
  static componentDocs = {
    summary: "Internal container for dynamically-generated children."
  };
  static treatAsComponentForRecursiveReplacements = true;
  static stateVariableToEvaluateAfterReplacements = "readyToExpandWhenResolved";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.deferUntilParentRendered = {
      createComponentOfType: "boolean",
      createStateVariable: "deferUntilParentRendered",
      defaultValue: false,
      description: "Whether to wait until the parent is rendered before creating dynamic children."
    };
    return attributes;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.dynamicChildren = {
      hasEssential: true,
      defaultValue: [],
      returnDependencies: () => ({}),
      definition: function() {
        return {
          useEssentialOrDefaultValue: { dynamicChildren: true }
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "dynamicChildren",
              value: desiredStateVariableValues.dynamicChildren
            }
          ]
        };
      }
    };
    stateVariableDefinitions.createReplacements = {
      stateVariablesDeterminingDependencies: ["deferUntilParentRendered"],
      returnDependencies: ({ stateValues }) => {
        const dependencies = {
          deferUntilParentRendered: {
            dependencyType: "stateVariable",
            variableName: "deferUntilParentRendered"
          }
        };
        if (stateValues.deferUntilParentRendered) {
          dependencies.parentRendered = {
            dependencyType: "parentStateVariable",
            variableName: "rendered"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        return {
          setValue: {
            createReplacements: !dependencyValues.deferUntilParentRendered || dependencyValues.parentRendered !== false
          }
        };
      },
      markStale() {
        return { updateReplacements: true };
      }
    };
    stateVariableDefinitions.nGroups = {
      returnDependencies: () => ({
        dynamicChildren: {
          dependencyType: "stateVariable",
          variableName: "dynamicChildren"
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            nGroups: dependencyValues.dynamicChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      returnDependencies: () => ({
        dynamicChildren: {
          dependencyType: "stateVariable",
          variableName: "dynamicChildren"
        },
        createReplacements: {
          dependencyType: "stateVariable",
          variableName: "createReplacements"
        }
      }),
      definition: function() {
        return { setValue: { readyToExpandWhenResolved: true } };
      },
      markStale() {
        return { updateReplacements: true };
      }
    };
    return stateVariableDefinitions;
  }
  async addChildren({
    serializedComponents,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    if (serializedComponents && serializedComponents.length > 0) {
      const dynamicChildren = [
        ...await this.stateValues.dynamicChildren
      ];
      dynamicChildren.push({
        type: "serialized",
        componentType: "group",
        componentIdx: 0,
        // will be replaced when actual components added
        attributes: {},
        doenetAttributes: {},
        state: {},
        children: deepClone(serializedComponents)
      });
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "dynamicChildren",
            value: dynamicChildren
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      return void 0;
    }
  }
  async deleteChildren({
    number,
    actionId,
    sourceInformation = {},
    skipRendererUpdate = false
  }) {
    const nGroups = await this.stateValues.nGroups;
    let numberToDelete = Math.min(number, nGroups);
    if (numberToDelete > 0) {
      const dynamicChildren = (await this.stateValues.dynamicChildren).slice(0, nGroups - numberToDelete);
      return await this.coreFunctions.performUpdate({
        updateInstructions: [
          {
            updateType: "updateValue",
            componentIdx: this.componentIdx,
            stateVariable: "dynamicChildren",
            value: dynamicChildren
          }
        ],
        actionId,
        sourceInformation,
        skipRendererUpdate
      });
    } else {
      return void 0;
    }
  }
  static async createSerializedReplacements({
    component,
    nComponents,
    workspace
  }) {
    let diagnostics = [];
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    workspace.replacementsCreatedThroughGroup = [];
    let replacements = [];
    if (!await component.stateValues.createReplacements) {
      workspace.nGroups = 0;
      return {
        replacements,
        diagnostics,
        nComponents
      };
    }
    const nGroups = await component.stateValues.nGroups;
    for (let groupNum = 0; groupNum < nGroups; groupNum++) {
      let groupReplacementResult = await this.replacementForGroup({
        component,
        groupNum,
        nComponents,
        workspace
      });
      let groupReplacements = groupReplacementResult.replacements;
      nComponents = groupReplacementResult.nComponents;
      replacements.push(...groupReplacements);
    }
    workspace.nGroups = nGroups;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async replacementForGroup({
    component,
    groupNum,
    nComponents,
    workspace
  }) {
    let replacements = [
      deepClone((await component.stateValues.dynamicChildren)[groupNum])
    ];
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    const idxResult = createNewComponentIndices(
      replacements,
      nComponents,
      stateIdInfo
    );
    replacements = idxResult.components;
    nComponents = idxResult.nComponents;
    workspace.replacementsCreated = stateIdInfo.num;
    workspace.replacementsCreatedThroughGroup = [
      ...workspace.replacementsCreatedThroughGroup,
      workspace.replacementsCreated
    ];
    return {
      replacements,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    nComponents,
    workspace
  }) {
    const createReplacements = await component.stateValues.createReplacements;
    const nGroups = createReplacements ? await component.stateValues.nGroups : 0;
    if (nGroups === workspace.nGroups) {
      return {
        replacementChanges: [],
        nComponents
      };
    } else if (nGroups < workspace.nGroups) {
      const replacementChanges = [
        {
          changeType: "delete",
          changeTopLevelReplacements: true,
          firstReplacementInd: nGroups,
          numberReplacementsToDelete: workspace.nGroups - nGroups
        }
      ];
      workspace.nGroups = nGroups;
      workspace.replacementsCreated = workspace.replacementsCreatedThroughGroup[nGroups - 1] || 0;
      workspace.replacementsCreatedThroughGroup = workspace.replacementsCreatedThroughGroup.slice(0, nGroups);
      return {
        replacementChanges,
        nComponents
      };
    }
    let newSerializedReplacements = [];
    for (let groupNum = workspace.nGroups; groupNum < nGroups; groupNum++) {
      let groupReplacementResult = await this.replacementForGroup({
        component,
        groupNum,
        nComponents,
        workspace
      });
      let groupReplacements = groupReplacementResult.replacements;
      nComponents = groupReplacementResult.nComponents;
      newSerializedReplacements.push(...groupReplacements);
    }
    let replacementInstruction = {
      changeType: "add",
      changeTopLevelReplacements: true,
      firstReplacementInd: workspace.nGroups,
      numberReplacementsToReplace: 0,
      serializedReplacements: newSerializedReplacements,
      replacementsToWithhold: 0
    };
    workspace.nGroups = nGroups;
    return {
      replacementChanges: [replacementInstruction],
      nComponents
    };
  }
}
class ShortDescription extends TextOrInline {
  static componentType = "shortDescription";
  static componentDocs = {
    summary: "A short accessibility description (e.g. for a `<graph>`) read by screen readers"
  };
  // A short description is never rendered visually — it is consumed only
  // by assistive technology, through its `text` state variable.
  static rendererType = void 0;
  static inSchemaOnlyInheritAs = [];
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.inlineChildren = {
      returnDependencies: () => ({
        inlineChildren: {
          dependencyType: "child",
          childGroups: ["inlines"],
          variableNames: ["text", "hidden"],
          variablesOptional: true
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: { inlineChildren: dependencyValues.inlineChildren }
      })
    };
    stateVariableDefinitions.value = {
      description: "The short description text.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: this.componentType
      },
      stateVariablesDeterminingDependencies: ["inlineChildren"],
      returnDependencies: ({ stateValues }) => {
        const dependencies = {
          inlineChildren: {
            dependencyType: "stateVariable",
            variableName: "inlineChildren"
          }
        };
        for (const [
          idx,
          child
        ] of stateValues.inlineChildren.entries()) {
          if (typeof child !== "object") {
            continue;
          }
          dependencies[`adapterSource${idx}`] = {
            dependencyType: "adapterSource",
            componentIdx: child.componentIdx
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        const value = textFromChildren(dependencyValues.inlineChildren);
        let foundMathType;
        for (let idx = 0; idx < dependencyValues.inlineChildren.length; idx++) {
          const child = dependencyValues.inlineChildren[idx];
          if (typeof child !== "object") {
            continue;
          }
          const effectiveType = dependencyValues[`adapterSource${idx}`]?.componentType ?? child.componentType;
          if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: effectiveType,
            baseComponentType: "math"
          }) || componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: effectiveType,
            baseComponentType: "m"
          })) {
            foundMathType = effectiveType;
            break;
          }
        }
        const diagnostics = [];
        if (foundMathType) {
          diagnostics.push({
            type: "accessibility",
            level: 2,
            message: `Short descriptions should not contain math components such as \`<${foundMathType}>\`. Spell out any math with words.`
          });
        }
        return { setValue: { value }, sendDiagnostics: diagnostics };
      }
    };
    return stateVariableDefinitions;
  }
}
const unitConversions = {
  "": 1,
  px: 1,
  pixel: 1,
  pixels: 1,
  "%": 1,
  em: 100,
  in: 96,
  inch: 96,
  inches: 96,
  pt: 1.333333333333,
  mm: 3.7795296,
  millimeter: 3.7795296,
  millimeters: 3.7795296,
  cm: 37.795296,
  centimeter: 37.795296,
  centimeters: 37.795296
};
class ComponentSize extends InlineComponent {
  static componentType = "componentSize";
  static rendererType = "text";
  static excludeFromSchema = true;
  // used when creating new component via adapter or copy prop
  static primaryStateVariableForDefinition = "componentSize";
  static stateVariableToBeShadowed = "componentSize";
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let addNumberAroundMultipleComponents = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let lastChild = matchedChildren[matchedChildren.length - 1];
      if (typeof lastChild === "string") {
        let lastWordRe = /([a-zA-z]+|%)$/;
        let lastString = lastChild.trim();
        let match = lastString.match(lastWordRe);
        if (match) {
          let unit = match[1];
          if (unit in unitConversions) {
            let rest = lastString.slice(0, match.index);
            let childrenForNumber = matchedChildren.slice(
              0,
              matchedChildren.length - 1
            );
            if (rest.length > 0) {
              childrenForNumber.push(rest);
            }
            let newChildren = [
              {
                type: "serialized",
                componentType: "number",
                componentIdx: nComponents++,
                stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
                children: childrenForNumber,
                attributes: {},
                doenetAttributes: {},
                state: {}
              },
              unit
            ];
            return {
              success: true,
              newChildren,
              nComponents
            };
          }
        }
      }
      return {
        success: true,
        newChildren: [
          {
            componentType: "number",
            children: matchedChildren
          }
        ]
      };
    };
    sugarInstructions.push({
      childrenRegex: /..+/,
      replacementFunction: addNumberAroundMultipleComponents
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "strings",
        componentTypes: ["string"]
      },
      {
        group: "numbers",
        componentTypes: ["number"]
      },
      {
        group: "componentSizes",
        componentTypes: ["componentSize"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    let selectedStyleDefinition = returnSelectedStyleStateVariableDefinition();
    Object.assign(stateVariableDefinitions, selectedStyleDefinition);
    let styleDescriptionDefinitions = returnTextStyleDescriptionDefinitions();
    Object.assign(stateVariableDefinitions, styleDescriptionDefinitions);
    stateVariableDefinitions.componentSize = {
      description: "The size value with its associated unit (absolute pixels or relative percent).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      hasEssential: true,
      returnDependencies: () => ({
        componentSizeChild: {
          dependencyType: "child",
          childGroups: ["componentSizes"],
          variableNames: ["componentSize"]
        },
        numberChild: {
          dependencyType: "child",
          childGroups: ["numbers"],
          variableNames: ["value"]
        },
        stringChild: {
          dependencyType: "child",
          childGroups: ["strings"],
          variableNames: ["value"]
        },
        parentDefaultAbsoluteSize: {
          dependencyType: "parentStateVariable",
          variableName: "defaultAbsoluteSize"
        }
      }),
      definition({ dependencyValues }) {
        let defaultIsAbsolute = dependencyValues.parentDefaultAbsoluteSize === void 0 ? false : dependencyValues.parentDefaultAbsoluteSize;
        if (dependencyValues.stringChild.length === 0) {
          if (dependencyValues.numberChild.length === 0) {
            if (dependencyValues.componentSizeChild.length === 0) {
              return {
                useEssentialOrDefaultValue: {
                  componentSize: {
                    defaultValue: {
                      size: 100,
                      isAbsolute: defaultIsAbsolute
                    }
                  }
                }
              };
            } else {
              return {
                setValue: {
                  componentSize: dependencyValues.componentSizeChild[0].stateValues.componentSize
                }
              };
            }
          } else {
            return {
              setValue: {
                componentSize: {
                  size: dependencyValues.numberChild[0].stateValues.value,
                  isAbsolute: true
                }
              }
            };
          }
        } else {
          let originalSize, originalUnit;
          if (dependencyValues.numberChild.length > 0) {
            originalSize = dependencyValues.numberChild[0].stateValues.value;
            originalUnit = dependencyValues.stringChild[0].trim();
          } else {
            let result = dependencyValues.stringChild[0].trim().match(/^(-?[\d.]+)\s*(.*)$/);
            if (result === null) {
              return { setValue: { componentSize: null } };
            }
            originalSize = result[1];
            originalUnit = result[2].trim();
          }
          originalSize = Number(originalSize);
          if (!Number.isFinite(originalSize)) {
            return { setValue: { componentSize: null } };
          }
          let isAbsolute = !(originalUnit === "%" || originalUnit === "em");
          let factor = unitConversions[originalUnit];
          if (factor === void 0) {
            factor = 1;
          }
          let size = factor * originalSize;
          return {
            setValue: {
              componentSize: {
                size,
                isAbsolute
              }
            }
          };
        }
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (dependencyValues.stringChild.length === 0) {
          if (dependencyValues.numberChild.length === 0) {
            if (dependencyValues.componentSizeChild.length === 0) {
              return {
                success: true,
                instructions: [
                  {
                    setEssentialValue: "componentSize",
                    value: desiredStateVariableValues.componentSize
                  }
                ]
              };
            } else {
              return {
                success: true,
                instructions: [
                  {
                    setDependency: "componentSizeChild",
                    desiredValue: desiredStateVariableValues.componentSize,
                    childIndex: 0,
                    variableIndex: 0
                  }
                ]
              };
            }
          } else {
            if (!desiredStateVariableValues.componentSize.isAbsolute) {
              return { success: false };
            }
            return {
              success: true,
              instructions: [
                {
                  setDependency: "numberChild",
                  desiredValue: desiredStateVariableValues.componentSize.size,
                  childIndex: 0,
                  variableIndex: 0
                }
              ]
            };
          }
        } else {
          if (dependencyValues.numberChild.length > 0) {
            let originalUnit = dependencyValues.stringChild[0].trim();
            let originalIsAbsolute = !(originalUnit === "%" || originalUnit === "em");
            if (desiredStateVariableValues.componentSize.isAbsolute !== originalIsAbsolute) {
              return { success: false };
            }
            let factor = unitConversions[originalUnit];
            if (factor === void 0) {
              factor = 1;
            }
            let desiredSize = desiredStateVariableValues.componentSize.size / factor;
            let instructions = [
              {
                setDependency: "numberChild",
                desiredValue: desiredSize,
                childIndex: 0,
                variableIndex: 0
              }
            ];
            return {
              success: true,
              instructions
            };
          } else {
            let desiredString = desiredStateVariableValues.componentSize.size;
            if (desiredStateVariableValues.componentSize.isAbsolute) {
              desiredString += "px";
            } else {
              desiredString += "%";
            }
            return {
              success: true,
              instructions: [
                {
                  setDependency: "stringChild",
                  desiredValue: desiredString,
                  childIndex: 0,
                  variableIndex: 0
                }
              ]
            };
          }
        }
      }
    };
    stateVariableDefinitions.number = {
      description: "The numeric portion of the size.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      returnDependencies: () => ({
        componentSize: {
          dependencyType: "stateVariable",
          variableName: "componentSize"
        }
      }),
      definition({ dependencyValues }) {
        let number = null;
        if (dependencyValues.componentSize) {
          number = dependencyValues.componentSize.size;
        }
        return { setValue: { number } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        if (!dependencyValues.componentSize) {
          return { success: false };
        }
        let desiredComponentSize = {
          size: desiredStateVariableValues.number,
          isAbsolute: dependencyValues.componentSize.isAbsolute
        };
        return {
          success: true,
          instructions: [
            {
              setDependency: "componentSize",
              desiredValue: desiredComponentSize
            }
          ]
        };
      }
    };
    stateVariableDefinitions.math = mathStateVariableFromNumberStateVariable({
      numberVariableName: "number",
      mathVariableName: "math",
      isPublic: true
    });
    stateVariableDefinitions.math.description = "The size as a math expression.";
    stateVariableDefinitions.isAbsolute = {
      description: "Whether the size uses absolute units (pixels) rather than relative units (percent).",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "boolean"
      },
      returnDependencies: () => ({
        componentSize: {
          dependencyType: "stateVariable",
          variableName: "componentSize"
        }
      }),
      definition({ dependencyValues }) {
        let isAbsolute = null;
        if (dependencyValues.componentSize) {
          isAbsolute = dependencyValues.componentSize.isAbsolute;
        }
        return { setValue: { isAbsolute } };
      }
    };
    stateVariableDefinitions.text = {
      forRenderer: true,
      returnDependencies: () => ({
        componentSize: {
          dependencyType: "stateVariable",
          variableName: "componentSize"
        }
      }),
      definition({ dependencyValues }) {
        let text = "";
        if (dependencyValues.componentSize) {
          text = dependencyValues.componentSize.size;
          if (dependencyValues.componentSize.isAbsolute) {
            text += "px";
          } else {
            text += "%";
          }
        }
        return {
          setValue: { text }
        };
      }
    };
    return stateVariableDefinitions;
  }
  static adapters = ["number", "math"];
}
class ComponentSizeList extends BaseComponent {
  static componentType = "_componentSizeList";
  static rendererType = "asList";
  static renderChildren = true;
  // when another component has a attribute that is a componentSizeList,
  // use the componentSizes state variable to populate that attribute
  static stateVariableToBeShadowed = "componentSizes";
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["string", "number"];
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let breakIntoComponentSizesByEmbeddedSpaces = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let newChildren = [];
      let piece = [];
      let createNewChildFromPiece = function() {
        if (piece.length > 0) {
          newChildren.push({
            type: "serialized",
            componentType: "componentSize",
            componentIdx: nComponents++,
            stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
            children: piece,
            attributes: {},
            doenetAttributes: {},
            state: {}
          });
          piece = [];
        }
      };
      for (let child of matchedChildren) {
        if (typeof child === "string") {
          let strParts = child.split(/\s+/);
          if (strParts[0].length === 0) {
            createNewChildFromPiece();
            strParts = strParts.slice(1);
          }
          for (let [i2, s2] of strParts.entries()) {
            if (s2.length > 0) {
              piece.push(s2);
              if (i2 < strParts.length - 1) {
                createNewChildFromPiece();
              }
            } else {
              createNewChildFromPiece();
            }
          }
        } else {
          piece.push(child);
        }
      }
      createNewChildFromPiece();
      return {
        success: true,
        newChildren,
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakIntoComponentSizesByEmbeddedSpaces
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "componentSizes",
        componentTypes: ["componentSize"]
      },
      {
        group: "componentSizeLists",
        componentTypes: ["_componentSizeList"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.overrideChildHide = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { overrideChildHide: true } })
    };
    stateVariableDefinitions.numComponents = {
      description: "The number of size values in the list.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "number"
      },
      additionalStateVariablesDefined: ["childIndexByArrayKey"],
      returnDependencies() {
        return {
          componentSizeListChildren: {
            dependencyType: "child",
            childGroups: ["componentSizeLists"],
            variableNames: ["numComponents"]
          },
          componentSizeAndComponentSizeListChildren: {
            dependencyType: "child",
            childGroups: ["componentSizes", "componentSizeLists"],
            skipComponentIndices: true
          }
        };
      },
      definition: function({ dependencyValues, componentInfoObjects }) {
        let numComponents = 0;
        let childIndexByArrayKey = [];
        let nComponentSizeLists = 0;
        for (let [
          childInd,
          child
        ] of dependencyValues.componentSizeAndComponentSizeListChildren.entries()) {
          if (componentInfoObjects.isInheritedComponentType({
            inheritedComponentType: child.componentType,
            baseComponentType: "_componentSizeList"
          })) {
            let componentSizeListChild = dependencyValues.componentSizeListChildren[nComponentSizeLists];
            nComponentSizeLists++;
            for (let i2 = 0; i2 < componentSizeListChild.stateValues.numComponents; i2++) {
              childIndexByArrayKey[numComponents + i2] = [
                childInd,
                i2
              ];
            }
            numComponents += componentSizeListChild.stateValues.numComponents;
          } else {
            childIndexByArrayKey[numComponents] = [childInd, 0];
            numComponents += 1;
          }
        }
        return {
          setValue: { numComponents, childIndexByArrayKey },
          checkForActualChange: { numComponents: true }
        };
      }
    };
    stateVariableDefinitions.componentSizes = {
      description: "The list of component sizes.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "componentSize"
      },
      isArray: true,
      entryPrefixes: ["componentSize"],
      stateVariablesDeterminingDependencies: ["childIndexByArrayKey"],
      returnArraySizeDependencies: () => ({
        numComponents: {
          dependencyType: "stateVariable",
          variableName: "numComponents"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numComponents];
      },
      returnArrayDependenciesByKey({ arrayKeys, stateValues }) {
        let dependenciesByKey = {};
        let globalDependencies = {
          childIndexByArrayKey: {
            dependencyType: "stateVariable",
            variableName: "childIndexByArrayKey"
          }
        };
        for (let arrayKey of arrayKeys) {
          let childIndices = [];
          let componentSizeIndex = "1";
          if (stateValues.childIndexByArrayKey[arrayKey]) {
            childIndices = [
              stateValues.childIndexByArrayKey[arrayKey][0]
            ];
            componentSizeIndex = stateValues.childIndexByArrayKey[arrayKey][1] + 1;
          }
          dependenciesByKey[arrayKey] = {
            componentSizeAndComponentSizeListChildren: {
              dependencyType: "child",
              childGroups: [
                "componentSizes",
                "componentSizeLists"
              ],
              variableNames: [
                "componentSize",
                "componentSize" + componentSizeIndex
              ],
              variablesOptional: true,
              childIndices
            }
          };
        }
        return { globalDependencies, dependenciesByKey };
      },
      arrayDefinitionByKey({
        globalDependencyValues,
        dependencyValuesByKey,
        arrayKeys,
        componentInfoObjects
      }) {
        let componentSizes = {};
        for (let arrayKey of arrayKeys) {
          let child = dependencyValuesByKey[arrayKey].componentSizeAndComponentSizeListChildren[0];
          if (child) {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "_componentSizeList"
            })) {
              let componentSizeIndex = globalDependencyValues.childIndexByArrayKey[arrayKey][1] + 1;
              componentSizes[arrayKey] = child.stateValues["componentSize" + componentSizeIndex];
            } else {
              componentSizes[arrayKey] = child.stateValues.componentSize;
            }
          }
        }
        return { setValue: { componentSizes } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        dependencyValuesByKey,
        dependencyNamesByKey,
        arraySize,
        componentInfoObjects
      }) {
        let instructions = [];
        for (let arrayKey in desiredStateVariableValues.componentSizes) {
          if (!dependencyValuesByKey[arrayKey]) {
            continue;
          }
          let child = dependencyValuesByKey[arrayKey].componentSizeAndComponentSizeListChildren[0];
          if (child) {
            if (componentInfoObjects.isInheritedComponentType({
              inheritedComponentType: child.componentType,
              baseComponentType: "_componentSizeList"
            })) {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].componentSizeAndComponentSizeListChildren,
                desiredValue: desiredStateVariableValues.componentSizes[arrayKey],
                childIndex: 0,
                variableIndex: 1
              });
            } else {
              instructions.push({
                setDependency: dependencyNamesByKey[arrayKey].componentSizeAndComponentSizeListChildren,
                desiredValue: desiredStateVariableValues.componentSizes[arrayKey],
                childIndex: 0,
                variableIndex: 0
              });
            }
          }
        }
        return {
          success: true,
          instructions
        };
      }
    };
    stateVariableDefinitions.numValues = {
      isAlias: true,
      targetVariableName: "numComponents",
      description: "The number of component sizes in the list."
    };
    stateVariableDefinitions.values = {
      isAlias: true,
      targetVariableName: "componentSizes",
      description: "The list's component sizes."
    };
    return stateVariableDefinitions;
  }
}
const ComponentSize$1 = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  ComponentSize,
  ComponentSizeList
}, Symbol.toStringTag, { value: "Module" }));
class Copy extends CompositeComponent {
  static componentType = "_copy";
  static excludeFromSchema = true;
  static acceptAnyAttribute = true;
  static includeBlankStringChildren = true;
  static stateVariableToEvaluateAfterReplacements = "needsReplacementsUpdatedWhenStale";
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    delete attributes.disabled;
    delete attributes.modifyIndirectly;
    delete attributes.fixed;
    delete attributes.styleNumber;
    delete attributes.isResponse;
    delete attributes.isPotentialResponse;
    delete attributes.hide;
    attributes.createComponentOfType = {
      createPrimitiveOfType: "string"
    };
    attributes.createComponentIdx = {
      createPrimitiveOfType: "integer"
    };
    attributes.createComponentName = {
      createPrimitiveOfType: "string"
    };
    attributes.numComponents = {
      createPrimitiveOfType: "number"
    };
    attributes.copyInChildren = {
      createPrimitiveOfType: "boolean"
    };
    attributes.uri = {
      createPrimitiveOfType: "string",
      createStateVariable: "uri",
      defaultValue: null,
      public: true
    };
    attributes.link = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "link",
      defaultValue: true
    };
    attributes.removeEmptyArrayEntries = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "removeEmptyArrayEntries",
      defaultValue: false
    };
    attributes.asList = {
      createPrimitiveOfType: "boolean",
      createStateVariable: "asListPreliminary",
      defaultValue: true,
      description: "Whether to render the items separated by commas (true) or with no separator (false)."
    };
    return attributes;
  }
  static keepChildrenSerialized({ serializedComponent }) {
    if (serializedComponent.children === void 0) {
      return [];
    } else {
      return Object.keys(serializedComponent.children);
    }
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.refResolutionIndexDependencies = {
      returnDependencies: () => ({
        refResolutionIndexDependencies: {
          dependencyType: "refResolutionIndexDependencies"
        }
      }),
      definition: ({ dependencyValues }) => ({
        setValue: {
          refResolutionIndexDependencies: dependencyValues.refResolutionIndexDependencies
        }
      })
    };
    stateVariableDefinitions.refResolutionIndexDependencyValues = {
      stateVariablesDeterminingDependencies: [
        "refResolutionIndexDependencies"
      ],
      returnDependencies: ({ stateValues }) => {
        const dependencies = {};
        for (const cIdx of stateValues.refResolutionIndexDependencies) {
          dependencies[cIdx] = {
            dependencyType: "stateVariable",
            componentIdx: cIdx,
            variableName: "value"
          };
        }
        return dependencies;
      },
      definition: ({ dependencyValues }) => ({
        setValue: {
          refResolutionIndexDependencyValues: dependencyValues
        }
      })
    };
    stateVariableDefinitions.extendIdx = {
      additionalStateVariablesDefined: ["unresolvedPath", "originalPath"],
      stateVariablesDeterminingDependencies: [
        "refResolutionIndexDependencyValues"
      ],
      returnDependencies: ({ stateValues }) => {
        return {
          refResolution: {
            dependencyType: "refResolution",
            indexDependencyValues: stateValues.refResolutionIndexDependencyValues
          }
        };
      },
      definition: ({ dependencyValues }) => {
        return {
          setValue: {
            extendIdx: dependencyValues.refResolution.extendIdx,
            unresolvedPath: dependencyValues.refResolution.unresolvedPath,
            originalPath: dependencyValues.refResolution.originalPath
          }
        };
      }
    };
    stateVariableDefinitions.extendedComponent = {
      shadowVariable: true,
      stateVariablesDeterminingDependencies: ["extendIdx"],
      // TODO: if it turns out we don't need `determineDependenciesImmediately`,
      // then remove the relevant code from `Dependencies`
      // determineDependenciesImmediately: true,
      returnDependencies({ stateValues }) {
        if (stateValues.extendIdx !== -1) {
          return {
            extendedComponent: {
              dependencyType: "componentIdentity",
              componentIdx: stateValues.extendIdx
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        let extendedComponent = null;
        if (dependencyValues.extendedComponent) {
          extendedComponent = dependencyValues.extendedComponent;
        }
        return {
          setValue: { extendedComponent }
        };
      }
    };
    stateVariableDefinitions.targetInactive = {
      stateVariablesDeterminingDependencies: ["extendedComponent"],
      returnDependencies({ stateValues }) {
        if (stateValues.extendedComponent) {
          return {
            targetIsInactiveCompositeReplacement: {
              dependencyType: "stateVariable",
              componentIdx: stateValues.extendedComponent.componentIdx,
              variableName: "isInactiveCompositeReplacement"
            }
          };
        } else {
          return {};
        }
      },
      definition: function({ dependencyValues }) {
        return {
          setValue: {
            targetInactive: Boolean(
              dependencyValues.targetIsInactiveCompositeReplacement
            )
          }
        };
      }
    };
    stateVariableDefinitions.cid = {
      additionalStateVariablesDefined: ["doenetId"],
      returnDependencies: () => ({
        uri: {
          dependencyType: "stateVariable",
          variableName: "uri"
        }
      }),
      definition: function({ dependencyValues }) {
        if (!dependencyValues.uri || dependencyValues.uri.substring(0, 7).toLowerCase() !== "doenet:") {
          return {
            setValue: { cid: null, doenetId: null }
          };
        }
        let cid = null, doenetId = null;
        let result = dependencyValues.uri.match(/[:&]cid=([^&]+)/i);
        if (result) {
          cid = result[1];
        }
        result = dependencyValues.uri.match(/[:&]doenetid=([^&]+)/i);
        if (result) {
          doenetId = result[1];
        }
        return { setValue: { cid, doenetId } };
      }
    };
    stateVariableDefinitions.createComponentOfType = {
      returnDependencies: () => ({
        typeAttr: {
          dependencyType: "attributePrimitive",
          attributeName: "createComponentOfType"
        }
      }),
      definition({ dependencyValues }) {
        let createComponentOfType = null;
        if (dependencyValues.typeAttr) {
          createComponentOfType = dependencyValues.typeAttr;
        }
        return { setValue: { createComponentOfType } };
      }
    };
    stateVariableDefinitions.replacementSourceIdentities = {
      stateVariablesDeterminingDependencies: [
        "extendedComponent",
        "unresolvedPath",
        "createComponentOfType"
      ],
      additionalStateVariablesDefined: ["usedReplacements"],
      returnDependencies: function({
        stateValues,
        componentInfoObjects
      }) {
        let dependencies = {
          numComponentsSpecified: {
            dependencyType: "stateVariable",
            variableName: "numComponentsSpecified"
          }
        };
        let useReplacements = false;
        if (stateValues.extendedComponent !== null) {
          if (stateValues.extendedComponent.componentType === "_copy" || componentInfoObjects.isCompositeComponent({
            componentType: stateValues.extendedComponent.componentType,
            includeNonStandard: true
          }) && stateValues.extendedComponent.componentType !== stateValues.createComponentOfType) {
            useReplacements = true;
            let firstPropNameFromPath = stateValues.unresolvedPath?.[0]?.name;
            if (firstPropNameFromPath === "") {
              firstPropNameFromPath = null;
            }
            dependencies.targets = {
              dependencyType: "replacement",
              compositeIdx: stateValues.extendedComponent.componentIdx,
              recursive: true,
              recurseNonStandardComposites: stateValues.unresolvedPath != null,
              includeWithheldReplacements: true,
              stopIfHaveProp: firstPropNameFromPath
            };
          }
          if (!useReplacements) {
            dependencies.targets = {
              dependencyType: "stateVariable",
              variableName: "extendedComponent"
            };
          }
          dependencies.usedReplacements = {
            dependencyType: "value",
            value: useReplacements
          };
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let replacementSourceIdentities = null;
        if (dependencyValues.targets) {
          replacementSourceIdentities = dependencyValues.targets;
          if (!Array.isArray(replacementSourceIdentities)) {
            replacementSourceIdentities = [
              replacementSourceIdentities
            ];
          }
          if (dependencyValues.numComponentsSpecified === 1 && replacementSourceIdentities.length > 1) {
            const firstNonBlankIdx = replacementSourceIdentities.findIndex(
              (repl) => typeof repl !== "string" || repl.trim() !== ""
            );
            const lastNonBlankIdx = replacementSourceIdentities.findLastIndex(
              (repl) => typeof repl !== "string" || repl.trim() !== ""
            );
            if (firstNonBlankIdx !== -1 && lastNonBlankIdx !== -1) {
              replacementSourceIdentities = replacementSourceIdentities.slice(
                firstNonBlankIdx,
                lastNonBlankIdx + 1
              );
            }
          }
        }
        return {
          setValue: {
            replacementSourceIdentities,
            usedReplacements: Boolean(
              dependencyValues.usedReplacements
            )
          }
        };
      }
    };
    stateVariableDefinitions.asList = {
      stateVariablesDeterminingDependencies: [
        "usedReplacements",
        "extendedComponent"
      ],
      returnDependencies({ stateValues }) {
        const dependencies = {
          asListPreliminary: {
            dependencyType: "stateVariable",
            variableName: "asListPreliminary"
          },
          usedReplacements: {
            dependencyType: "stateVariable",
            variableName: "usedReplacements"
          }
        };
        if (stateValues.usedReplacements) {
          dependencies.extendedComponentAsList = {
            dependencyType: "stateVariable",
            componentIdx: stateValues.extendedComponent.componentIdx,
            variableName: "asList",
            variablesOptional: true
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, usedDefault }) {
        if (usedDefault.asListPreliminary && dependencyValues.usedReplacements && typeof dependencyValues.extendedComponentAsList === "boolean") {
          return {
            setValue: {
              asList: dependencyValues.extendedComponentAsList
            }
          };
        } else {
          return {
            setValue: {
              asList: dependencyValues.asListPreliminary
            }
          };
        }
      }
    };
    stateVariableDefinitions.implicitProp = {
      stateVariablesDeterminingDependencies: [
        "replacementSourceIdentities",
        "unresolvedPath"
      ],
      returnDependencies({ stateValues }) {
        let dependencies = {
          unresolvedPath: {
            dependencyType: "stateVariable",
            variableName: "unresolvedPath"
          },
          usedReplacements: {
            dependencyType: "stateVariable",
            variableName: "usedReplacements"
          }
        };
        if (!stateValues.unresolvedPath) {
          dependencies.replacementSourceIdentities = {
            dependencyType: "stateVariable",
            variableName: "replacementSourceIdentities"
          };
          dependencies.typeAttr = {
            dependencyType: "attributePrimitive",
            attributeName: "createComponentOfType"
          };
        }
        return dependencies;
      },
      definition({ dependencyValues, componentInfoObjects }) {
        if (dependencyValues.unresolvedPath || dependencyValues.usedReplacements || !(dependencyValues.replacementSourceIdentities?.length > 0)) {
          return { setValue: { implicitProp: null } };
        }
        let source = dependencyValues.replacementSourceIdentities[0];
        let variableForImplicitProp = componentInfoObjects.allComponentClasses[source.componentType]?.variableForImplicitProp;
        if (!variableForImplicitProp) {
          return { setValue: { implicitProp: null } };
        }
        if (dependencyValues.typeAttr) {
          let componentTypeFromAttr = componentInfoObjects.componentTypeLowerCaseMapping[dependencyValues.typeAttr.toLowerCase()];
          if (componentTypeFromAttr === source.componentType) {
            return { setValue: { implicitProp: null } };
          }
          let varInfo = componentInfoObjects.publicStateVariableInfo[source.componentType].stateVariableDescriptions[variableForImplicitProp];
          if (varInfo.createComponentOfType !== void 0 && varInfo.createComponentOfType !== componentTypeFromAttr) {
            return { setValue: { implicitProp: null } };
          }
        }
        return { setValue: { implicitProp: variableForImplicitProp } };
      }
    };
    stateVariableDefinitions.replacementSources = {
      stateVariablesDeterminingDependencies: [
        "replacementSourceIdentities",
        "unresolvedPath",
        "implicitProp"
      ],
      additionalStateVariablesDefined: ["effectivePropNameBySource"],
      returnDependencies: function({ stateValues }) {
        let dependencies = {
          replacementSourceIdentities: {
            dependencyType: "stateVariable",
            variableName: "replacementSourceIdentities"
          }
        };
        if (stateValues.replacementSourceIdentities !== null) {
          for (let [
            ind,
            source
          ] of stateValues.replacementSourceIdentities.entries()) {
            let thisUnresolvedPath = stateValues.unresolvedPath;
            if (stateValues.implicitProp) {
              thisUnresolvedPath = [
                { name: stateValues.implicitProp, index: [] }
              ];
            }
            let thisTarget;
            if (thisUnresolvedPath) {
              thisUnresolvedPath = [...thisUnresolvedPath];
              dependencies["unresolvedPath" + ind] = {
                dependencyType: "value",
                value: thisUnresolvedPath
              };
              if (typeof source === "string") {
                continue;
              }
              thisTarget = {
                dependencyType: "stateVariableFromUnresolvedPath",
                componentIdx: source.componentIdx,
                unresolvedPath: thisUnresolvedPath,
                returnAsComponentObject: true,
                variablesOptional: true,
                caseInsensitiveVariableMatch: true,
                publicStateVariablesOnly: true,
                useMappedVariableNames: true
              };
            } else {
              if (typeof source === "string") {
                continue;
              }
              thisTarget = {
                dependencyType: "componentIdentity",
                componentIdx: source.componentIdx
              };
            }
            dependencies["target" + ind] = thisTarget;
          }
        }
        return dependencies;
      },
      definition({ dependencyValues }) {
        let replacementSources = null;
        let effectivePropNameBySource = null;
        if (dependencyValues.replacementSourceIdentities !== null) {
          replacementSources = [];
          effectivePropNameBySource = [];
          for (let ind in dependencyValues.replacementSourceIdentities) {
            let targetDep = dependencyValues["target" + ind];
            if (targetDep) {
              replacementSources.push(targetDep);
              let propName;
              if (targetDep.stateValues) {
                propName = Object.keys(
                  targetDep.stateValues
                )[0];
              }
              if (!propName && dependencyValues["unresolvedPath" + ind]) {
                propName = "__prop_name_not_found";
              }
              effectivePropNameBySource.push(propName);
            } else {
              const replSource = dependencyValues.replacementSourceIdentities[ind];
              if (typeof replSource === "string") {
                if (dependencyValues["unresolvedPath" + ind]) {
                  const propName = "__prop_name_not_found";
                  effectivePropNameBySource.push(propName);
                } else {
                  replacementSources.push(replSource);
                  effectivePropNameBySource.push(void 0);
                }
              } else {
                replacementSources.push(void 0);
                effectivePropNameBySource.push(
                  "__no_target_found"
                );
              }
            }
          }
        }
        return {
          setValue: { replacementSources, effectivePropNameBySource }
        };
      }
    };
    stateVariableDefinitions.numComponentsSpecified = {
      returnDependencies: () => ({
        numComponentsAttr: {
          dependencyType: "attributePrimitive",
          attributeName: "numComponents"
        },
        typeAttr: {
          dependencyType: "attributePrimitive",
          attributeName: "createComponentOfType"
        }
      }),
      definition({ dependencyValues, componentInfoObjects }) {
        let numComponentsSpecified;
        if (dependencyValues.typeAttr) {
          let componentType = componentInfoObjects.componentTypeLowerCaseMapping[dependencyValues.typeAttr.toLowerCase()];
          if (!(componentType in componentInfoObjects.allComponentClasses)) {
            return {
              setValue: { numComponentsSpecified: null },
              sendDiagnostics: [
                {
                  type: "warning",
                  message: `Cannot extend or copy an unrecognized component type: ${dependencyValues.typeAttr}.`
                }
              ]
            };
          }
          if (dependencyValues.numComponentsAttr !== null) {
            numComponentsSpecified = dependencyValues.numComponentsAttr;
          } else {
            numComponentsSpecified = 1;
          }
        } else if (dependencyValues.numComponentsAttr !== null) {
          throw Error(
            `You must specify createComponentOfType when specifying numComponents for a copy.`
          );
        } else {
          numComponentsSpecified = null;
        }
        return { setValue: { numComponentsSpecified } };
      }
    };
    stateVariableDefinitions.unlinkedDescendants = {
      stateVariablesDeterminingDependencies: ["extendIdx", "link"],
      returnDependencies({ stateValues }) {
        let dependencies = {};
        if (!stateValues.link) {
          dependencies.targetDescendants = {
            dependencyType: "descendant",
            ancestorIdx: stateValues.extendIdx,
            componentTypes: ["_composite"]
          };
        } else {
          dependencies.targetItself = {
            dependencyType: "componentIdentity",
            componentIdx: stateValues.extendIdx
          };
        }
        return dependencies;
      },
      definition() {
        return {
          setValue: {
            unlinkedDescendants: true
          }
        };
      }
    };
    stateVariableDefinitions.unlinkedTargetsExpanded = {
      stateVariablesDeterminingDependencies: [
        "extendedComponent",
        "link"
      ],
      returnDependencies({ stateValues, componentInfoObjects }) {
        let dependencies = {};
        if (!stateValues.link && stateValues.extendedComponent && componentInfoObjects.isInheritedComponentType({
          inheritedComponentType: stateValues.extendedComponent.componentType,
          baseComponentType: "_composite"
        })) {
          dependencies.targetReplacements = {
            dependencyType: "replacement",
            compositeIdx: stateValues.extendedComponent.componentIdx,
            recursive: true,
            recurseNonStandardComposites: true
          };
        }
        return dependencies;
      },
      definition() {
        return { setValue: { unlinkedTargetsExpanded: true } };
      }
    };
    stateVariableDefinitions.readyToExpandWhenResolved = {
      stateVariablesDeterminingDependencies: [
        "extendedComponent",
        "unresolvedPath",
        "link"
      ],
      returnDependencies({ stateValues, componentInfoObjects }) {
        let dependencies = {
          extendedComponent: {
            dependencyType: "stateVariable",
            variableName: "extendedComponent"
          },
          needsReplacementsUpdatedWhenStale: {
            dependencyType: "stateVariable",
            variableName: "needsReplacementsUpdatedWhenStale"
          },
          // replacementSources: {
          //   dependencyType: "stateVariable",
          //   variableName: "replacementSources",
          // },
          link: {
            dependencyType: "stateVariable",
            variableName: "link"
          },
          // propName: {
          //   dependencyType: "stateVariable",
          //   variableName: "propName",
          // },
          unlinkedTargetsExpanded: {
            dependencyType: "stateVariable",
            variableName: "unlinkedTargetsExpanded"
          },
          unlinkedDescendants: {
            dependencyType: "stateVariable",
            variableName: "unlinkedDescendants"
          }
        };
        if (stateValues.extendedComponent && componentInfoObjects.isCompositeComponent({
          componentType: stateValues.extendedComponent.componentType,
          includeNonStandard: false
        })) {
          dependencies.targetReadyToExpandWhenResolved = {
            dependencyType: "stateVariable",
            componentIdx: stateValues.extendedComponent.componentIdx,
            variableName: "readyToExpandWhenResolved"
          };
        }
        if (stateValues.extendedComponent && stateValues.unresolvedPath === null) {
          dependencies.extendedVariantInfo = {
            dependencyType: "stateVariable",
            componentIdx: stateValues.extendedComponent.componentIdx,
            variableName: "generatedVariantInfo",
            variablesOptional: true
          };
        }
        if (!stateValues.link) {
          dependencies.replacementSources = {
            dependencyType: "stateVariable",
            variableName: "replacementSources"
          };
        }
        return dependencies;
      },
      definition() {
        return { setValue: { readyToExpandWhenResolved: true } };
      }
    };
    stateVariableDefinitions.needsReplacementsUpdatedWhenStale = {
      stateVariablesDeterminingDependencies: [
        "extendedComponent",
        "replacementSourceIdentities",
        "effectivePropNameBySource",
        "unresolvedPath",
        "link",
        "removeEmptyArrayEntries"
      ],
      returnDependencies: function({
        stateValues,
        componentInfoObjects
      }) {
        if (!stateValues.link) {
          return {};
        }
        let dependencies = {
          extendedComponent: {
            dependencyType: "stateVariable",
            variableName: "extendedComponent"
          },
          targetInactive: {
            dependencyType: "stateVariable",
            variableName: "targetInactive"
          },
          replacementSourceIdentities: {
            dependencyType: "stateVariable",
            variableName: "replacementSourceIdentities"
          }
        };
        if (stateValues.effectivePropNameBySource !== null) {
          for (let [
            ind,
            propName
          ] of stateValues.effectivePropNameBySource.entries()) {
            if (propName) {
              let source = stateValues.replacementSourceIdentities[ind];
              dependencies["sourceArraySize" + ind] = {
                dependencyType: "stateVariableArraySize",
                componentIdx: source.componentIdx,
                variableName: propName,
                variablesOptional: true,
                caseInsensitiveVariableMatch: true
              };
              dependencies["sourceComponentType" + ind] = {
                dependencyType: "stateVariableComponentType",
                componentIdx: source.componentIdx,
                variableName: propName,
                variablesOptional: true,
                caseInsensitiveVariableMatch: true
              };
            }
          }
        }
        if (stateValues.extendedComponent !== null && componentInfoObjects.isCompositeComponent({
          componentType: stateValues.extendedComponent.componentType,
          includeNonStandard: false
        })) {
          dependencies.allReplacementIdentities = {
            dependencyType: "replacement",
            compositeIdx: stateValues.extendedComponent.componentIdx,
            recursive: true,
            variableNames: ["isInactiveCompositeReplacement"]
          };
        }
        if (stateValues.removeEmptyArrayEntries) {
          dependencies.replacementSources = {
            dependencyType: "stateVariable",
            variableName: "replacementSources"
          };
        }
        return dependencies;
      },
      // the whole point of this state variable is to return updateReplacements
      // on mark stale
      markStale() {
        return { updateReplacements: true };
      },
      definition: () => ({
        setValue: { needsReplacementsUpdatedWhenStale: true }
      })
    };
    return stateVariableDefinitions;
  }
  static async createSerializedReplacements({
    component,
    components,
    nComponents,
    workspace,
    allDoenetMLs,
    componentInfoObjects,
    resolveItem,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
  }) {
    if (workspace.replacementsCreated === void 0) {
      workspace.replacementsCreated = 0;
    }
    let diagnostics = [];
    await component.stateValues.numComponentsSpecified;
    workspace.numReplacementsBySource = [];
    workspace.numNonStringReplacementsBySource = [];
    workspace.propVariablesCopiedBySource = [];
    workspace.sourceIndices = [];
    let compositeAttributesObj = this.createAttributesObject();
    let replacementSourceIdentities = await component.stateValues.replacementSourceIdentities;
    if (!await component.stateValues.extendedComponent || !replacementSourceIdentities) {
      let replacements2 = [];
      const stateIdInfo2 = {
        prefix: `${component.stateId}|`,
        num: workspace.replacementsCreated
      };
      if (component.attributes.copyInChildren?.primitive.value) {
        let componentType = componentInfoObjects.componentTypeLowerCaseMapping[component.attributes.createComponentOfType.primitive.value.toLowerCase()];
        let componentClass = componentInfoObjects.allComponentClasses[componentType];
        const res = convertUnresolvedAttributesForComponentType({
          attributes: component.attributes,
          componentType,
          componentInfoObjects,
          compositeAttributesObj,
          nComponents,
          stateIdInfo: stateIdInfo2
        });
        const attributesFromComposite = res.attributes;
        nComponents = res.nComponents;
        let children = deepClone(component.serializedChildren);
        if (!componentClass.includeBlankStringChildren) {
          children = children.filter(
            (x2) => typeof x2 !== "string" || x2.trim() !== ""
          );
        }
        let attributes = attributesFromComposite;
        replacements2 = [
          {
            type: "serialized",
            componentType,
            componentIdx: nComponents++,
            stateId: `${stateIdInfo2.prefix}${stateIdInfo2.num++}`,
            attributes,
            doenetAttributes: {},
            state: {},
            children
          }
        ];
        workspace.numReplacementsBySource.push(replacements2.length);
        workspace.numNonStringReplacementsBySource.push(
          replacements2.filter((x2) => typeof x2 !== "string").length
        );
      }
      let verificationResult2 = await verifyReplacementsMatchSpecifiedType(
        {
          component,
          replacements: replacements2,
          workspace,
          componentInfoObjects,
          compositeAttributesObj,
          components,
          nComponents,
          stateIdInfo: stateIdInfo2,
          publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
        }
      );
      diagnostics.push(...verificationResult2.diagnostics);
      nComponents = verificationResult2.nComponents;
      addAttributesToSingleReplacement(
        verificationResult2.replacements,
        component
      );
      workspace.replacementsCreated = stateIdInfo2.num;
      return {
        replacements: verificationResult2.replacements,
        diagnostics,
        nComponents
      };
    }
    let resolveResult = await resolveItem({
      componentIdx: component.componentIdx,
      type: "determineDependencies",
      stateVariable: "replacementSources",
      dependency: "__determine_dependencies",
      expandComposites: false
    });
    if (!resolveResult.success) {
      throw Error(
        `Couldn't resolve determineDependencies of replacementSources of ${component.componentIdx}`
      );
    }
    let effectivePropNameBySource = await component.stateValues.effectivePropNameBySource;
    for (let ind in replacementSourceIdentities) {
      let thisUnresolvedPath = effectivePropNameBySource[ind];
      if (thisUnresolvedPath) {
        resolveResult = await resolveItem({
          componentIdx: component.componentIdx,
          type: "recalculateDownstreamComponents",
          stateVariable: "replacementSources",
          dependency: "target" + ind,
          expandComposites: false
        });
        if (!resolveResult.success) {
          throw Error(
            `Couldn't resolve recalculateDownstreamComponents for target${ind} of replacementSources of ${component.componentIdx}`
          );
        }
      }
    }
    let replacements = [];
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    let numReplacementsBySource = [];
    let numNonStringReplacementsBySource = [];
    let numReplacementsSoFar = 0;
    let numNonStringReplacementsSoFar = 0;
    for (let sourceNum in replacementSourceIdentities) {
      let numComponentsForSource;
      if (component.attributes.createComponentOfType?.primitive) {
        let numComponentsTotal = await component.stateValues.numComponentsSpecified;
        let numSources = replacementSourceIdentities.length;
        numComponentsForSource = Math.floor(
          numComponentsTotal / numSources
        );
        let nExtras = numComponentsTotal % numSources;
        if (sourceNum < nExtras) {
          numComponentsForSource++;
        }
      }
      let results = await this.createReplacementForSource({
        component,
        sourceNum,
        components,
        nComponents,
        stateIdInfo,
        allDoenetMLs,
        numReplacementsSoFar,
        numNonStringReplacementsSoFar,
        compositeAttributesObj,
        componentInfoObjects,
        numComponentsForSource,
        publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
        copyInChildren: Number(sourceNum) === 0 && component.attributes.copyInChildren?.primitive.value
      });
      diagnostics.push(...results.diagnostics);
      nComponents = results.nComponents;
      workspace.propVariablesCopiedBySource[sourceNum] = results.propVariablesCopiedByReplacement;
      let sourceReplacements = results.serializedReplacements;
      numReplacementsBySource[sourceNum] = sourceReplacements.length;
      numNonStringReplacementsBySource[sourceNum] = sourceReplacements.filter((x2) => typeof x2 !== "string").length;
      numReplacementsSoFar += numReplacementsBySource[sourceNum];
      numNonStringReplacementsSoFar += numNonStringReplacementsBySource[sourceNum];
      replacements.push(...sourceReplacements);
    }
    workspace.numReplacementsBySource = numReplacementsBySource;
    workspace.numNonStringReplacementsBySource = numNonStringReplacementsBySource;
    workspace.sourceIndices = replacementSourceIdentities.map(
      (x2) => x2.componentIdx
    );
    let verificationResult = await verifyReplacementsMatchSpecifiedType({
      component,
      replacements,
      workspace,
      componentInfoObjects,
      compositeAttributesObj,
      components,
      nComponents,
      stateIdInfo,
      publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
    });
    diagnostics.push(...verificationResult.diagnostics);
    nComponents = verificationResult.nComponents;
    replacements = verificationResult.replacements;
    addAttributesToSingleReplacement(
      replacements,
      component
    );
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacements,
      diagnostics,
      nComponents
    };
  }
  static async createReplacementForSource({
    component,
    sourceNum,
    components,
    nComponents,
    stateIdInfo,
    allDoenetMLs,
    numReplacementsSoFar,
    numNonStringReplacementsSoFar,
    compositeAttributesObj,
    componentInfoObjects,
    numComponentsForSource,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
    copyInChildren
  }) {
    let diagnostics = [];
    let replacementSource = (await component.stateValues.replacementSourceIdentities)[sourceNum];
    if (typeof replacementSource !== "object") {
      if (component.stateValues.effectivePropNameBySource[sourceNum]) {
        return {
          serializedReplacements: [],
          diagnostics,
          nComponents
        };
      } else {
        return {
          serializedReplacements: [replacementSource],
          diagnostics,
          nComponents
        };
      }
    }
    const replacementSourceComponent = components[replacementSource.componentIdx];
    if (await replacementSourceComponent.stateValues.isInactiveCompositeReplacement) {
      return {
        serializedReplacements: [],
        diagnostics,
        nComponents
      };
    }
    let propName = (await component.stateValues.effectivePropNameBySource)[sourceNum];
    let link = await component.stateValues.link;
    if (!link || propName && await component.stateValues.removeEmptyArrayEntries) {
      const replacementSourceWithProp = (await component.stateValues.replacementSources)[sourceNum];
      if (replacementSourceWithProp) {
        replacementSource = replacementSourceWithProp;
      }
    }
    if (propName) {
      let results = await replacementFromProp({
        component,
        components,
        nComponents,
        stateIdInfo,
        replacementSource,
        propName,
        allDoenetMLs,
        compositeAttributesObj,
        componentInfoObjects,
        numComponentsForSource,
        publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
      });
      diagnostics.push(...results.diagnostics);
      nComponents = results.nComponents;
      let serializedReplacements2 = results.serializedReplacements;
      let propVariablesCopiedByReplacement = results.propVariablesCopiedByReplacement;
      if (copyInChildren && component.serializedChildren.length > 0) {
        if (serializedReplacements2.length === 0) {
          let componentType = componentInfoObjects.componentTypeLowerCaseMapping[component.attributes.createComponentOfType.primitive.value.toLowerCase()];
          let componentClass = componentInfoObjects.allComponentClasses[componentType];
          const res = convertUnresolvedAttributesForComponentType({
            attributes: component.attributes,
            componentType,
            componentInfoObjects,
            compositeAttributesObj,
            nComponents,
            stateIdInfo
          });
          const attributesFromComposite = res.attributes;
          nComponents = res.nComponents;
          let children = deepClone(component.serializedChildren);
          if (!componentClass.includeBlankStringChildren) {
            children = children.filter(
              (x2) => typeof x2 !== "string" || x2.trim() !== ""
            );
          }
          let attributes = attributesFromComposite;
          serializedReplacements2 = [
            {
              type: "serialized",
              componentType,
              componentIdx: nComponents++,
              stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
              attributes,
              doenetAttributes: {},
              state: {},
              children
            }
          ];
          propVariablesCopiedByReplacement = [[]];
        } else if (serializedReplacements2.length === 1) {
          addChildrenFromComposite({
            replacements: serializedReplacements2,
            children: component.serializedChildren,
            componentInfoObjects
          });
        }
      }
      return {
        serializedReplacements: serializedReplacements2,
        propVariablesCopiedByReplacement,
        diagnostics,
        nComponents
      };
    }
    let serializedReplacements;
    try {
      serializedReplacements = [
        await replacementSourceComponent.serialize({
          copyAll: !link,
          componentSourceAttributesToIgnore: ["labelIsName"],
          copyVariants: !link,
          copyPrimaryEssentialIfShadow: !link,
          copyEssentialStateIfShadow: !link,
          errorIfEncounterComponent: [component.componentIdx]
        })
      ];
      let res = createNewComponentIndices(
        serializedReplacements,
        nComponents,
        stateIdInfo
      );
      serializedReplacements = res.components;
      nComponents = res.nComponents;
    } catch (e3) {
      if (e3.message?.startsWith(SERIALIZE_ENCOUNTERED_COMPONENT_PREFIX)) {
        const fallbackPropName = getSelfReferentialFallbackPropName({
          componentAncestors: component.ancestors,
          componentInfoObjects,
          replacementSourceComponentType: replacementSourceComponent.componentType
        });
        if (fallbackPropName) {
          try {
            let fallbackResults = await replacementFromProp({
              component,
              components,
              nComponents,
              stateIdInfo,
              replacementSource: replacementSourceComponent,
              propName: fallbackPropName,
              allDoenetMLs,
              compositeAttributesObj,
              componentInfoObjects,
              numComponentsForSource,
              publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
            });
            diagnostics.push(...fallbackResults.diagnostics);
            nComponents = fallbackResults.nComponents;
            return {
              serializedReplacements: fallbackResults.serializedReplacements,
              propVariablesCopiedByReplacement: fallbackResults.propVariablesCopiedByReplacement,
              diagnostics,
              nComponents
            };
          } catch (innerError) {
          }
        }
      }
      console.error("we're calling this circular", e3);
      let message = "Circular dependency detected";
      if (component.attributes.createComponentOfType?.primitive) {
        message += ` involving \`<${component.attributes.createComponentOfType.primitive.value}>\` component`;
      }
      message += ".";
      serializedReplacements = [
        {
          type: "serialized",
          componentType: "_error",
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          state: { message },
          attributes: {},
          doenetAttributes: {},
          children: []
        }
      ];
      diagnostics.push({
        message,
        type: "error"
      });
      return { serializedReplacements, diagnostics, nComponents };
    }
    if (!link && serializedReplacements[0].state) {
      delete serializedReplacements[0].state.fixed;
      delete serializedReplacements[0].state.fixedPreliminary;
      delete serializedReplacements[0].attributes.fixed;
    }
    serializedReplacements = postProcessCopy({
      serializedComponents: serializedReplacements,
      componentIdx: component.componentIdx,
      addShadowDependencies: link,
      unlinkExternalCopies: false
    });
    for (let repl of serializedReplacements) {
      if (typeof repl !== "object") {
        continue;
      }
      if (!repl.attributes) {
        repl.attributes = {};
      }
      const res = convertUnresolvedAttributesForComponentType({
        attributes: component.attributes,
        componentType: repl.componentType,
        componentInfoObjects,
        compositeAttributesObj,
        dontSkipAttributes: ["asList"],
        nComponents,
        stateIdInfo
      });
      const attributesFromComposite = res.attributes;
      nComponents = res.nComponents;
      if (attributesFromComposite.displayDigits || attributesFromComposite.displayDecimals) {
        delete repl.attributes.displayDigits;
        delete repl.attributes.displayDecimals;
      }
      Object.assign(repl.attributes, attributesFromComposite);
    }
    if (copyInChildren && serializedReplacements.length === 1 && component.serializedChildren.length > 0) {
      addChildrenFromComposite({
        replacements: serializedReplacements,
        children: component.serializedChildren,
        componentInfoObjects
      });
    }
    return {
      serializedReplacements,
      diagnostics,
      nComponents
    };
  }
  static async calculateReplacementChanges({
    component,
    nComponents,
    componentChanges,
    components,
    workspace,
    componentInfoObjects,
    resolveItem,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
  }) {
    let diagnostics = [];
    if (await component.stateValues.link === false) {
      return { replacementChanges: [], diagnostics, nComponents };
    }
    let compositeAttributesObj = this.createAttributesObject();
    let replacementSourceIdentities = await component.stateValues.replacementSourceIdentities;
    if (!await component.stateValues.extendedComponent || !replacementSourceIdentities) {
      let replacementChanges2 = [];
      const stateIdInfo2 = {
        prefix: `${component.stateId}|`,
        num: workspace.replacementsCreated
      };
      if (component.replacements.length > 0) {
        let replacementInstruction = {
          changeType: "delete",
          changeTopLevelReplacements: true,
          firstReplacementInd: 0,
          numberReplacementsToDelete: component.replacements.length
        };
        replacementChanges2.push(replacementInstruction);
      }
      let previousZeroSourceIndices2 = workspace.sourceIndices.length === 0;
      workspace.sourceIndices = [];
      workspace.numReplacementsBySource = [];
      workspace.numNonStringReplacementsBySource = [];
      workspace.propVariablesCopiedBySource = [];
      let verificationResult2 = await verifyReplacementsMatchSpecifiedType(
        {
          component,
          replacementChanges: replacementChanges2,
          workspace,
          componentInfoObjects,
          compositeAttributesObj,
          components,
          nComponents,
          stateIdInfo: stateIdInfo2,
          publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
        }
      );
      diagnostics.push(...verificationResult2.diagnostics);
      nComponents = verificationResult2.nComponents;
      if (previousZeroSourceIndices2) {
        return { replacementChanges: [], diagnostics, nComponents };
      }
      addAttributesToSingleReplacementChange(
        component,
        verificationResult2.replacementChanges
      );
      workspace.replacementsCreated = stateIdInfo2.num;
      return {
        replacementChanges: verificationResult2.replacementChanges,
        diagnostics,
        nComponents
      };
    }
    if (await component.stateValues.targetInactive) {
      let replacementChanges2 = [];
      const stateIdInfo2 = {
        prefix: `${component.stateId}|`,
        num: workspace.replacementsCreated
      };
      let nReplacements = component.replacements.length;
      if (nReplacements > 0) {
        if (component.replacementsToWithhold !== nReplacements) {
          let replacementInstruction = {
            changeType: "changeReplacementsToWithhold",
            replacementsToWithhold: nReplacements
          };
          replacementChanges2.push(replacementInstruction);
        }
        let verificationResult2 = await verifyReplacementsMatchSpecifiedType({
          component,
          replacementChanges: replacementChanges2,
          workspace,
          componentInfoObjects,
          compositeAttributesObj,
          components,
          nComponents,
          stateIdInfo: stateIdInfo2,
          publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
        });
        diagnostics.push(...verificationResult2.diagnostics);
        nComponents = verificationResult2.nComponents;
        replacementChanges2 = verificationResult2.replacementChanges;
      }
      addAttributesToSingleReplacementChange(
        component,
        replacementChanges2
      );
      workspace.replacementsCreated = stateIdInfo2.num;
      return { replacementChanges: replacementChanges2, diagnostics, nComponents };
    }
    let resolveResult = await resolveItem({
      componentIdx: component.componentIdx,
      type: "determineDependencies",
      stateVariable: "replacementSources",
      dependency: "__determine_dependencies",
      expandComposites: false
    });
    if (!resolveResult.success) {
      throw Error(
        `Couldn't resolve determineDependencies of replacementSources of ${component.componentIdx}`
      );
    }
    let effectivePropNameBySource = await component.stateValues.effectivePropNameBySource;
    for (let ind in replacementSourceIdentities) {
      let thisUnresolvedPath = effectivePropNameBySource[ind];
      if (thisUnresolvedPath) {
        resolveResult = await resolveItem({
          componentIdx: component.componentIdx,
          type: "recalculateDownstreamComponents",
          stateVariable: "replacementSources",
          dependency: "target" + ind,
          expandComposites: false
        });
        if (!resolveResult.success) {
          throw Error(
            `Couldn't resolve recalculateDownstreamComponents for target${ind} of replacementSources of ${component.componentIdx}`
          );
        }
      }
    }
    let replacementChanges = [];
    const stateIdInfo = {
      prefix: `${component.stateId}|`,
      num: workspace.replacementsCreated
    };
    if (component.replacementsToWithhold > 0) {
      let replacementInstruction = {
        changeType: "changeReplacementsToWithhold",
        replacementsToWithhold: 0
      };
      replacementChanges.push(replacementInstruction);
    }
    let numReplacementsSoFar = 0;
    let numNonStringReplacementsSoFar = 0;
    let numReplacementsBySource = [];
    let numNonStringReplacementsBySource = [];
    let propVariablesCopiedBySource = [];
    let maxSourceLength = Math.max(
      replacementSourceIdentities.length,
      workspace.numReplacementsBySource.length
    );
    let recreateRemaining = false;
    for (let sourceNum = 0; sourceNum < maxSourceLength; sourceNum++) {
      let numComponentsForSource;
      if (component.attributes.createComponentOfType?.primitive) {
        let numComponentsTotal = await component.stateValues.numComponentsSpecified;
        let numSources = replacementSourceIdentities.length;
        numComponentsForSource = Math.floor(
          numComponentsTotal / numSources
        );
        let nExtras = numComponentsTotal % numSources;
        if (sourceNum < nExtras) {
          numComponentsForSource++;
        }
      }
      let replacementSource = replacementSourceIdentities[sourceNum];
      if (!recreateRemaining && await components[replacementSource?.componentIdx]?.stateValues.isInactiveCompositeReplacement) {
        let allWithheld = true;
        for (let otherSourceNum = sourceNum + 1; otherSourceNum < maxSourceLength; otherSourceNum++) {
          const otherSource = replacementSourceIdentities[otherSourceNum];
          if (!await components[otherSource?.componentIdx]?.stateValues.isInactiveCompositeReplacement) {
            allWithheld = false;
            break;
          }
        }
        if (allWithheld) {
          let numberReplacementsLeft = workspace.numReplacementsBySource.slice(sourceNum).reduce((a2, c2) => a2 + c2, 0);
          if (numberReplacementsLeft > 0) {
            let replacementInstruction = {
              changeType: "changeReplacementsToWithhold",
              replacementsToWithhold: numberReplacementsLeft
            };
            replacementChanges.push(replacementInstruction);
          }
          for (let otherSourceNum = sourceNum; otherSourceNum < maxSourceLength; otherSourceNum++) {
            numReplacementsBySource[otherSourceNum] = workspace.numReplacementsBySource[otherSourceNum];
            numNonStringReplacementsBySource[otherSourceNum] = workspace.numNonStringReplacementsBySource[otherSourceNum];
            propVariablesCopiedBySource[otherSourceNum] = workspace.propVariablesCopiedBySource[otherSourceNum];
          }
          break;
        } else {
          replacementSource = void 0;
        }
      }
      if (replacementSource === void 0) {
        if (workspace.numReplacementsBySource[sourceNum] > 0) {
          if (!recreateRemaining) {
            let numberReplacementsLeft = workspace.numReplacementsBySource.slice(sourceNum).reduce((a2, c2) => a2 + c2, 0);
            if (numberReplacementsLeft > 0) {
              let replacementInstruction = {
                changeType: "delete",
                changeTopLevelReplacements: true,
                firstReplacementInd: numReplacementsSoFar,
                numberReplacementsToDelete: numberReplacementsLeft
              };
              replacementChanges.push(replacementInstruction);
            }
            recreateRemaining = true;
            workspace.numReplacementsBySource.slice(sourceNum).forEach(
              (v2, i2) => workspace.numReplacementsBySource[i2] = 0
            );
            workspace.numNonStringReplacementsBySource.slice(sourceNum).forEach(
              (v2, i2) => workspace.numNonStringReplacementsBySource[i2] = 0
            );
          }
        }
        numReplacementsBySource[sourceNum] = 0;
        numNonStringReplacementsBySource[sourceNum] = 0;
        propVariablesCopiedBySource.push([]);
        continue;
      }
      let prevSourceIdx = workspace.sourceIndices[sourceNum];
      let needToRecreate = prevSourceIdx === void 0 || replacementSource.componentIdx !== prevSourceIdx || recreateRemaining;
      if (!needToRecreate) {
        for (let ind = 0; ind < workspace.numReplacementsBySource[sourceNum]; ind++) {
          let currentReplacement = component.replacements[numReplacementsSoFar + ind];
          if (!currentReplacement) {
            needToRecreate = true;
            break;
          } else if (!effectivePropNameBySource[sourceNum] && currentReplacement.shadows?.componentIdx !== replacementSourceIdentities[sourceNum].componentIdx) {
            needToRecreate = true;
            break;
          }
        }
      }
      if (needToRecreate) {
        let prevNumReplacements = 0;
        if (sourceNum in workspace.numReplacementsBySource) {
          prevNumReplacements = workspace.numReplacementsBySource[sourceNum];
        }
        let numReplacementsToDelete = prevNumReplacements;
        if (recreateRemaining) {
          numReplacementsToDelete = 0;
        }
        let results2 = await this.recreateReplacements({
          component,
          sourceNum,
          numReplacementsSoFar,
          numNonStringReplacementsSoFar,
          numReplacementsToDelete,
          components,
          nComponents,
          stateIdInfo,
          compositeAttributesObj,
          componentInfoObjects,
          numComponentsForSource,
          publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
        });
        diagnostics.push(...results2.diagnostics);
        nComponents = results2.nComponents;
        numReplacementsSoFar += results2.numReplacements;
        numNonStringReplacementsSoFar += results2.numNonStringReplacements;
        numReplacementsBySource[sourceNum] = results2.numReplacements;
        numNonStringReplacementsBySource[sourceNum] = results2.numNonStringReplacements;
        propVariablesCopiedBySource[sourceNum] = results2.propVariablesCopiedByReplacement;
        let replacementInstruction = results2.replacementInstruction;
        if (!recreateRemaining) {
          if (results2.numReplacements !== prevNumReplacements) {
            let numberReplacementsLeft = workspace.numReplacementsBySource.slice(sourceNum).reduce((a2, c2) => a2 + c2, 0);
            replacementInstruction.numberReplacementsToReplace = numberReplacementsLeft;
            recreateRemaining = true;
            workspace.numReplacementsBySource.slice(sourceNum).forEach(
              (v2, i2) => workspace.numReplacementsBySource[i2] = 0
            );
            workspace.numNonStringReplacementsBySource.slice(sourceNum).forEach(
              (v2, i2) => workspace.numNonStringReplacementsBySource[i2] = 0
            );
          }
        }
        if (replacementInstruction) {
          replacementChanges.push(replacementInstruction);
        }
        continue;
      }
      if (!effectivePropNameBySource[sourceNum] && workspace.numReplacementsBySource[sourceNum] > 0) {
        numReplacementsSoFar += workspace.numReplacementsBySource[sourceNum];
        numNonStringReplacementsSoFar += workspace.numNonStringReplacementsBySource[sourceNum];
        numReplacementsBySource[sourceNum] = workspace.numReplacementsBySource[sourceNum];
        numNonStringReplacementsBySource[sourceNum] = workspace.numNonStringReplacementsBySource[sourceNum];
        continue;
      }
      const newStateIdInfo = {
        prefix: `${component.stateId}|`,
        num: stateIdInfo.num
      };
      let results = await this.createReplacementForSource({
        component,
        sourceNum,
        components,
        nComponents,
        stateIdInfo: newStateIdInfo,
        numReplacementsSoFar,
        numNonStringReplacementsSoFar,
        compositeAttributesObj,
        componentInfoObjects,
        numComponentsForSource,
        publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
        copyInChildren: Number(sourceNum) === 0 && component.attributes.copyInChildren?.primitive.value
      });
      diagnostics.push(...results.diagnostics);
      const nComponentsForNew = results.nComponents;
      let propVariablesCopiedByReplacement = results.propVariablesCopiedByReplacement;
      let newSerializedReplacements = results.serializedReplacements;
      let nNewReplacements = newSerializedReplacements.length;
      let nOldReplacements = workspace.numReplacementsBySource[sourceNum];
      if (nNewReplacements !== nOldReplacements) {
        let numberReplacementsLeft = workspace.numReplacementsBySource.slice(sourceNum).reduce((a2, c2) => a2 + c2, 0);
        let replacementInstruction = {
          changeType: "add",
          changeTopLevelReplacements: true,
          firstReplacementInd: numReplacementsSoFar,
          numberReplacementsToReplace: numberReplacementsLeft,
          serializedReplacements: newSerializedReplacements
        };
        replacementChanges.push(replacementInstruction);
        recreateRemaining = true;
        nComponents = nComponentsForNew;
        stateIdInfo.num = newStateIdInfo.num;
        workspace.numReplacementsBySource.slice(sourceNum).forEach(
          (v2, i2) => workspace.numReplacementsBySource[i2] = 0
        );
        workspace.numNonStringReplacementsBySource.slice(sourceNum).forEach(
          (v2, i2) => workspace.numNonStringReplacementsBySource[i2] = 0
        );
      } else {
        for (let ind = 0; ind < nNewReplacements; ind++) {
          let foundDifference = propVariablesCopiedByReplacement[ind].length !== workspace.propVariablesCopiedBySource[sourceNum][ind].length;
          let onlyDifferenceIsType = !foundDifference;
          if (!foundDifference) {
            if (workspace.propVariablesCopiedBySource[sourceNum][ind].some(
              (v2, i2) => v2 !== propVariablesCopiedByReplacement[ind][i2]
            )) {
              onlyDifferenceIsType = false;
              foundDifference = true;
            } else {
              let currentReplacement = component.replacements[numReplacementsSoFar + ind];
              if (currentReplacement.componentType !== newSerializedReplacements[ind].componentType) {
                foundDifference = true;
              }
            }
          }
          if (ind == 0 && foundDifference && onlyDifferenceIsType) {
            let requiredLength = await component.stateValues.numComponentsSpecified;
            let wrapExistingReplacements = requiredLength === 1 && nNewReplacements === 1 && !(component.replacementsToWithhold > 0) && workspace.sourceIndices.length === 1;
            if (wrapExistingReplacements) {
              foundDifference = false;
            }
          }
          if (foundDifference) {
            nComponents = nComponentsForNew;
            stateIdInfo.num = newStateIdInfo.num;
            let replacementInstruction = {
              changeType: "add",
              changeTopLevelReplacements: true,
              firstReplacementInd: numReplacementsSoFar + ind,
              numberReplacementsToReplace: 1,
              serializedReplacements: [
                newSerializedReplacements[ind]
              ]
            };
            replacementChanges.push(replacementInstruction);
          }
          if (typeof newSerializedReplacements[ind] !== "string") ;
        }
      }
      let nNewNonStrings = newSerializedReplacements.filter(
        (x2) => typeof x2 !== "string"
      ).length;
      numReplacementsSoFar += nNewReplacements;
      numNonStringReplacementsSoFar += nNewNonStrings;
      numReplacementsBySource[sourceNum] = nNewReplacements;
      numNonStringReplacementsBySource[sourceNum] = nNewNonStrings;
      propVariablesCopiedBySource[sourceNum] = propVariablesCopiedByReplacement;
    }
    let previousZeroSourceIndices = workspace.sourceIndices.length === 0;
    workspace.numReplacementsBySource = numReplacementsBySource;
    workspace.numNonStringReplacementsBySource = numNonStringReplacementsBySource;
    workspace.sourceIndices = replacementSourceIdentities.map(
      (x2) => x2.componentIdx
    );
    workspace.propVariablesCopiedBySource = propVariablesCopiedBySource;
    let verificationResult = await verifyReplacementsMatchSpecifiedType({
      component,
      replacementChanges,
      workspace,
      componentInfoObjects,
      compositeAttributesObj,
      components,
      nComponents,
      stateIdInfo,
      publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
    });
    diagnostics.push(...verificationResult.diagnostics);
    nComponents = verificationResult.nComponents;
    replacementChanges = verificationResult.replacementChanges;
    if (previousZeroSourceIndices && workspace.sourceIndices.length === 0) {
      return { replacementChanges: [], diagnostics, nComponents };
    }
    addAttributesToSingleReplacementChange(
      component,
      replacementChanges
    );
    workspace.replacementsCreated = stateIdInfo.num;
    return {
      replacementChanges,
      diagnostics,
      nComponents
    };
  }
  static async recreateReplacements({
    component,
    sourceNum,
    numReplacementsSoFar,
    numNonStringReplacementsSoFar,
    numReplacementsToDelete,
    components,
    nComponents,
    stateIdInfo,
    compositeAttributesObj,
    componentInfoObjects,
    numComponentsForSource,
    publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
  }) {
    let diagnostics = [];
    let results = await this.createReplacementForSource({
      component,
      sourceNum,
      numReplacementsSoFar,
      numNonStringReplacementsSoFar,
      components,
      nComponents,
      stateIdInfo,
      compositeAttributesObj,
      componentInfoObjects,
      numComponentsForSource,
      publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2,
      copyInChildren: Number(sourceNum) === 0 && component.attributes.copyInChildren?.primitive.value
    });
    diagnostics.push(...results.diagnostics);
    nComponents = results.nComponents;
    let propVariablesCopiedByReplacement = results.propVariablesCopiedByReplacement;
    let newSerializedChildren = results.serializedReplacements;
    if (newSerializedChildren.length > 0 || numReplacementsToDelete > 0) {
      let replacementInstruction = {
        changeType: "add",
        changeTopLevelReplacements: true,
        firstReplacementInd: numReplacementsSoFar,
        numberReplacementsToReplace: numReplacementsToDelete,
        serializedReplacements: newSerializedChildren
      };
      return {
        numReplacements: newSerializedChildren.length,
        numNonStringReplacements: newSerializedChildren.filter(
          (x2) => typeof x2 !== "string"
        ).length,
        propVariablesCopiedByReplacement,
        replacementInstruction,
        diagnostics,
        nComponents
      };
    } else {
      return {
        numReplacements: 0,
        numNonStringReplacements: 0,
        propVariablesCopiedByReplacement,
        replacementInstruction: null,
        diagnostics,
        nComponents
      };
    }
  }
}
async function replacementFromProp({
  component,
  components,
  nComponents,
  stateIdInfo,
  replacementSource,
  propName,
  allDoenetMLs,
  // numReplacementsSoFar,
  compositeAttributesObj,
  componentInfoObjects,
  numComponentsForSource,
  publicCaseInsensitiveAliasSubstitutions: publicCaseInsensitiveAliasSubstitutions2
}) {
  let diagnostics = [];
  let serializedReplacements = [];
  let propVariablesCopiedByReplacement = [];
  let replacementInd = -1;
  let target = void 0;
  let varName = void 0;
  if (replacementSource) {
    target = components[replacementSource.componentIdx];
    varName = publicCaseInsensitiveAliasSubstitutions2({
      stateVariables: [propName],
      componentClass: target.constructor
    })[0];
  }
  if (varName === void 0 || varName.slice(0, 12) === "__not_public") {
    if (propName === "__no_target_found") {
      const unresolvedPath = await component.stateValues.unresolvedPath;
      const startOffset = unresolvedPath[0].position?.start.offset;
      const endOffset = unresolvedPath[unresolvedPath.length - 1].position?.end.offset;
      let unresolvedPropName = "";
      if (startOffset != void 0 && endOffset != void 0) {
        const sourceDoc = unresolvedPath[0].sourceDoc ?? 0;
        unresolvedPropName = allDoenetMLs?.[sourceDoc]?.substring(
          startOffset,
          endOffset
        );
      }
      diagnostics.push({
        message: `Could not find prop ${unresolvedPropName} on a component of type ${replacementSource.componentType}`,
        type: "info"
      });
    } else if (propName !== "__prop_name_not_found") {
      diagnostics.push({
        message: `Could not find prop ${propName} on a component of type ${replacementSource.componentType}`,
        type: "info"
      });
    }
    return {
      serializedReplacements: [],
      propVariablesCopiedByReplacement: [],
      diagnostics,
      nComponents
    };
  }
  let stateVarObj = target.state[varName];
  let stateVarValue = await stateVarObj.value;
  let link = await component.stateValues.link;
  if (stateVarObj.isArray || stateVarObj.isArrayEntry) {
    let arrayStateVarObj, unflattenedArrayKeys, arraySize, arrayKeys;
    if (stateVarObj.isArray) {
      arrayStateVarObj = stateVarObj;
      arraySize = await stateVarObj.arraySize;
      unflattenedArrayKeys = stateVarObj.getAllArrayKeys(
        arraySize,
        false
      );
    } else {
      arrayStateVarObj = target.state[stateVarObj.arrayStateVariable];
      unflattenedArrayKeys = await stateVarObj.unflattenedArrayKeys;
      arrayKeys = await stateVarObj.arrayKeys;
    }
    if (arrayStateVarObj.shadowingInstructions?.hasVariableComponentType) {
      await component.stateValues.replacementSources;
      if (!arrayStateVarObj.shadowingInstructions.createComponentOfType) {
        return {
          serializedReplacements: [],
          propVariablesCopiedByReplacement: [],
          diagnostics,
          nComponents
        };
      }
    }
    let wrappingComponents = stateVarObj.wrappingComponents;
    let numWrappingComponents = wrappingComponents.length;
    let numReplacementsForSource = numComponentsForSource;
    if (stateVarObj.isArray) {
      if (arraySize.some((v2) => v2 === 0)) {
        numReplacementsForSource = 0;
      } else {
        numReplacementsForSource = arraySize.slice(0, arraySize.length - numWrappingComponents).reduce((a2, c2) => a2 * c2, 1);
      }
    } else {
      if (arrayKeys.length === 0) {
        numReplacementsForSource = 0;
      } else if (numWrappingComponents === 0) {
        numReplacementsForSource = arrayKeys.length;
      } else if (numWrappingComponents >= stateVarObj.numDimensions) {
        numReplacementsForSource = 1;
      } else if (numWrappingComponents === stateVarObj.numDimensions - 1) {
        numReplacementsForSource = unflattenedArrayKeys.length;
      } else {
        let nLevelsToFlatten = stateVarObj.numDimensions - numWrappingComponents - 1;
        numReplacementsForSource = flattenLevels(
          unflattenedArrayKeys,
          nLevelsToFlatten
        ).length;
      }
    }
    if (numWrappingComponents === 0) {
      let flattenedArrayKeys = flattenDeep(unflattenedArrayKeys);
      for (let ind = 0; ind < numReplacementsForSource; ind++) {
        let arrayKey = flattenedArrayKeys[ind];
        if (await component.stateValues.removeEmptyArrayEntries) {
          if (!arrayKey) {
            continue;
          }
          let arrayIndex = arrayStateVarObj.keyToIndex(arrayKey);
          if (!Array.isArray(arrayIndex)) {
            arrayIndex = [arrayIndex];
          }
          let propStateValue = await arrayStateVarObj.value;
          for (let ind2 of arrayIndex) {
            propStateValue = propStateValue[ind2];
          }
          if (propStateValue === void 0 || propStateValue === null) {
            continue;
          }
        }
        replacementInd++;
        let propVariablesCopied = propVariablesCopiedByReplacement[replacementInd] = [];
        let createComponentOfType = arrayStateVarObj.shadowingInstructions.createComponentOfType;
        if (Array.isArray(createComponentOfType)) {
          if (createComponentOfType[arrayStateVarObj.keyToIndex(arrayKey)]) {
            createComponentOfType = createComponentOfType[arrayStateVarObj.keyToIndex(arrayKey)];
          } else {
            createComponentOfType = createComponentOfType[0];
          }
        }
        if (arrayKey) {
          let propVariable = arrayStateVarObj.arrayVarNameFromArrayKey(arrayKey);
          propVariablesCopied.push(propVariable);
          const res = convertUnresolvedAttributesForComponentType({
            attributes: component.attributes,
            componentType: createComponentOfType,
            componentInfoObjects,
            compositeAttributesObj,
            nComponents,
            stateIdInfo
          });
          const attributesFromComposite = res.attributes;
          nComponents = res.nComponents;
          let attributeComponentsShadowingStateVariables;
          if (arrayStateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
            attributeComponentsShadowingStateVariables = {};
            for (let attrName in arrayStateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
              let stateVariableToShadow = arrayStateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
              let sObj = target.state[stateVariableToShadow];
              if (sObj.isArray) {
                stateVariableToShadow = sObj.arrayVarNameFromArrayKey(arrayKey);
              }
              attributeComponentsShadowingStateVariables[attrName] = {
                stateVariableToShadow
              };
            }
          }
          let stateVariablesShadowingStateVariables;
          if (arrayStateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables) {
            stateVariablesShadowingStateVariables = {};
            for (let attrName in arrayStateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables) {
              let stateVariableToShadow = arrayStateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables[attrName].stateVariableToShadow;
              let sObj = target.state[stateVariableToShadow];
              if (sObj.isArray) {
                stateVariableToShadow = sObj.arrayVarNameFromArrayKey(arrayKey);
              }
              stateVariablesShadowingStateVariables[attrName] = {
                stateVariableToShadow
              };
            }
          }
          if (link) {
            let attributesForReplacement = {};
            if (attributeComponentsShadowingStateVariables) {
              let classOfComponentToCreate = componentInfoObjects.allComponentClasses[createComponentOfType];
              let attrObj = classOfComponentToCreate.createAttributesObject();
              for (let attrName in attributeComponentsShadowingStateVariables) {
                let stateVariableToShadow = attributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
                let attributeComponentType = attrObj[attrName]?.createComponentOfType;
                if (attributeComponentType) {
                  let shadowComponent = {
                    type: "serialized",
                    componentType: attributeComponentType,
                    componentIdx: nComponents++,
                    stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                    attributes: {},
                    doenetAttributes: {},
                    state: {},
                    children: [],
                    downstreamDependencies: {
                      [target.componentIdx]: [
                        {
                          compositeIdx: component.componentIdx,
                          dependencyType: "referenceShadow",
                          propVariable: stateVariableToShadow
                        }
                      ]
                    }
                  };
                  attributesForReplacement[attrName] = {
                    component: shadowComponent
                  };
                }
              }
            }
            Object.assign(
              attributesForReplacement,
              attributesFromComposite
            );
            serializedReplacements.push({
              type: "serialized",
              componentType: createComponentOfType,
              componentIdx: nComponents++,
              stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
              attributes: attributesForReplacement,
              doenetAttributes: {},
              children: [],
              state: {},
              downstreamDependencies: {
                [replacementSource.componentIdx]: [
                  {
                    dependencyType: "referenceShadow",
                    compositeIdx: component.componentIdx,
                    propVariable,
                    additionalStateVariableShadowing: stateVariablesShadowingStateVariables
                  }
                ]
              }
            });
          } else {
            let attributesForReplacement = {};
            if (attributeComponentsShadowingStateVariables) {
              let classOfComponentToCreate = componentInfoObjects.allComponentClasses[createComponentOfType];
              let attrObj = classOfComponentToCreate.createAttributesObject();
              let additionalAttributes = {};
              for (let attrName in attributeComponentsShadowingStateVariables) {
                if (attrObj[attrName]?.createComponentOfType) {
                  let vName = attributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
                  let attributeStateVarObj = target.state[vName];
                  let attributeValue = await attributeStateVarObj.value;
                  if (attributeStateVarObj.isArray) {
                    attributeValue = attributeValue[attributeStateVarObj.keyToIndex[arrayKey]];
                  }
                  if (!target.state[vName].usedDefault) {
                    additionalAttributes[attrName] = attributeValue;
                  }
                }
              }
              const res2 = convertAttributeValuesForComponentType({
                attributeValues: additionalAttributes,
                componentType: createComponentOfType,
                componentInfoObjects,
                nComponents,
                stateIdInfo
              });
              const attributesFromComponent = res2.attributes;
              nComponents = res2.nComponents;
              if (stateVarObj.shadowingInstructions.attributesToShadow) {
                for (let attrName of stateVarObj.shadowingInstructions.attributesToShadow) {
                  if (target.attributes[attrName]?.component) {
                    const serializedComponent2 = await target.attributes[attrName].component.serialize({
                      copyAll: true,
                      copyVariants: true
                    });
                    let res3 = createNewComponentIndices(
                      [serializedComponent2],
                      nComponents,
                      stateIdInfo
                    );
                    nComponents = res3.nComponents;
                    attributesFromComponent[attrName] = {
                      component: res3.components[0]
                    };
                  } else if (target.attributes[attrName]?.primitive !== void 0) {
                    attributesFromComponent[attrName] = {
                      primitive: JSON.parse(
                        JSON.stringify(
                          target.attributes[attrName].primitive
                        )
                      )
                    };
                  }
                }
              }
              Object.assign(
                attributesForReplacement,
                attributesFromComponent
              );
            }
            Object.assign(
              attributesForReplacement,
              attributesFromComposite
            );
            let primaryEssentialStateVariable = "value";
            let componentClass = componentInfoObjects.allComponentClasses[createComponentOfType];
            if (componentClass.primaryEssentialStateVariable) {
              primaryEssentialStateVariable = componentClass.primaryEssentialStateVariable;
            } else if (componentClass.primaryStateVariableForDefinition) {
              primaryEssentialStateVariable = componentClass.primaryStateVariableForDefinition;
            }
            let arrayIndex = arrayStateVarObj.keyToIndex(arrayKey);
            if (!Array.isArray(arrayIndex)) {
              arrayIndex = [arrayIndex];
            }
            let propStateValue = await arrayStateVarObj.value;
            for (let ind2 of arrayIndex) {
              propStateValue = propStateValue[ind2];
            }
            let serializedComponent = {
              type: "serialized",
              componentType: createComponentOfType,
              componentIdx: nComponents++,
              stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
              attributes: attributesForReplacement,
              doenetAttributes: {},
              state: {
                [primaryEssentialStateVariable]: propStateValue
              },
              children: []
            };
            serializedReplacements.push(serializedComponent);
          }
        } else {
          serializedReplacements.push({
            type: "serialized",
            componentType: createComponentOfType,
            componentIdx: nComponents++,
            stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
            attributes: {},
            doenetAttributes: {},
            children: [],
            state: {}
          });
        }
      }
    } else {
      let createReplacementPiece = async function(subArrayKeys, numDimensionsLeft, init = false) {
        let pieces = [];
        let propVariablesCopiedByPiece = [];
        if (numDimensionsLeft > 1) {
          for (let subSubArrayKeys of subArrayKeys) {
            let result2 = await createReplacementPiece(
              subSubArrayKeys,
              numDimensionsLeft - 1
            );
            pieces.push(...result2.pieces);
            propVariablesCopiedByPiece.push(
              ...result2.propVariablesCopiedByPiece
            );
          }
        } else {
          for (let arrayKey of subArrayKeys) {
            let propVariable = arrayStateVarObj.arrayVarNameFromArrayKey(arrayKey);
            let propVariablesCopiedForThisPiece = [propVariable];
            let createComponentOfType = arrayStateVarObj.shadowingInstructions.createComponentOfType;
            if (Array.isArray(createComponentOfType)) {
              createComponentOfType = createComponentOfType[arrayStateVarObj.keyToIndex(arrayKey)];
            }
            let attributeComponentsShadowingStateVariables;
            if (arrayStateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
              attributeComponentsShadowingStateVariables = {};
              let attributeShadowingInfo = arrayStateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables;
              for (let attrName in attributeShadowingInfo) {
                if (!attributeShadowingInfo[attrName].addToOuterIfWrappedArray) {
                  let stateVariableToShadow = attributeShadowingInfo[attrName].stateVariableToShadow;
                  let sObj = target.state[stateVariableToShadow];
                  if (sObj.isArray) {
                    stateVariableToShadow = sObj.arrayVarNameFromArrayKey(
                      arrayKey
                    );
                  }
                  attributeComponentsShadowingStateVariables[attrName] = {
                    stateVariableToShadow
                  };
                }
              }
            }
            let stateVariablesShadowingStateVariables;
            if (arrayStateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables) {
              stateVariablesShadowingStateVariables = {};
              for (let attrName in arrayStateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables) {
                let stateVariableToShadow = arrayStateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables[attrName].stateVariableToShadow;
                let sObj = target.state[stateVariableToShadow];
                if (sObj.isArray) {
                  stateVariableToShadow = sObj.arrayVarNameFromArrayKey(arrayKey);
                }
                stateVariablesShadowingStateVariables[attrName] = {
                  stateVariableToShadow
                };
              }
            }
            if (link) {
              let attributesForReplacement = {};
              if (attributeComponentsShadowingStateVariables) {
                let classOfComponentToCreate = componentInfoObjects.allComponentClasses[createComponentOfType];
                let attrObj = classOfComponentToCreate.createAttributesObject();
                for (let attrName in attributeComponentsShadowingStateVariables) {
                  let stateVariableToShadow = attributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
                  let attributeComponentType = attrObj[attrName]?.createComponentOfType;
                  if (attributeComponentType) {
                    let shadowComponent = {
                      type: "serialized",
                      componentType: attributeComponentType,
                      componentIdx: nComponents++,
                      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                      attributes: {},
                      doenetAttributes: {},
                      state: {},
                      children: [],
                      downstreamDependencies: {
                        [target.componentIdx]: [
                          {
                            compositeIdx: component.componentIdx,
                            dependencyType: "referenceShadow",
                            propVariable: stateVariableToShadow
                          }
                        ]
                      }
                    };
                    attributesForReplacement[attrName] = {
                      component: shadowComponent
                    };
                  }
                }
              }
              pieces.push({
                type: "serialized",
                componentType: createComponentOfType,
                componentIdx: nComponents++,
                stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                attributes: attributesForReplacement,
                doenetAttributes: {},
                children: [],
                state: {},
                downstreamDependencies: {
                  [replacementSource.componentIdx]: [
                    {
                      dependencyType: "referenceShadow",
                      compositeIdx: component.componentIdx,
                      propVariable,
                      additionalStateVariableShadowing: stateVariablesShadowingStateVariables
                    }
                  ]
                }
              });
            } else {
              let attributesForReplacement = {};
              if (attributeComponentsShadowingStateVariables) {
                let classOfComponentToCreate = componentInfoObjects.allComponentClasses[createComponentOfType];
                let attrObj = classOfComponentToCreate.createAttributesObject();
                let additionalAttributes = {};
                for (let attrName in attributeComponentsShadowingStateVariables) {
                  if (attrObj[attrName]?.createComponentOfType) {
                    let vName = attributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
                    let attributeStateVarObj = target.state[vName];
                    let attributeValue = await attributeStateVarObj.value;
                    if (attributeStateVarObj.isArray) {
                      attributeValue = attributeValue[attributeStateVarObj.keyToIndex[arrayKey]];
                    }
                    if (!target.state[vName].usedDefault) {
                      additionalAttributes[attrName] = attributeValue;
                    }
                  }
                }
                const res = convertAttributeValuesForComponentType({
                  attributeValues: additionalAttributes,
                  componentType: createComponentOfType,
                  componentInfoObjects,
                  nComponents,
                  stateIdInfo
                });
                const attributesFromComponent = res.attributes;
                nComponents = res.nComponents;
                if (stateVarObj.shadowingInstructions.attributesToShadow) {
                  for (let attrName of stateVarObj.shadowingInstructions.attributesToShadow) {
                    if (target.attributes[attrName]?.component) {
                      let serializedComponent2 = await target.attributes[attrName].component.serialize({
                        copyAll: true,
                        copyVariants: true
                      });
                      let res2 = createNewComponentIndices(
                        [serializedComponent2],
                        nComponents,
                        stateIdInfo
                      );
                      nComponents = res2.nComponents;
                      attributesFromComponent[attrName] = {
                        component: res2.components[0]
                      };
                    } else if (target.attributes[attrName]?.primitive !== void 0) {
                      attributesFromComponent[attrName] = {
                        primitive: JSON.parse(
                          JSON.stringify(
                            target.attributes[attrName].primitive
                          )
                        )
                      };
                    }
                  }
                }
                Object.assign(
                  attributesForReplacement,
                  attributesFromComponent
                );
              }
              let primaryEssentialStateVariable = "value";
              let componentClass = componentInfoObjects.allComponentClasses[createComponentOfType];
              if (componentClass.primaryEssentialStateVariable) {
                primaryEssentialStateVariable = componentClass.primaryEssentialStateVariable;
              } else if (componentClass.primaryStateVariableForDefinition) {
                primaryEssentialStateVariable = componentClass.primaryStateVariableForDefinition;
              }
              let arrayIndex = arrayStateVarObj.keyToIndex(arrayKey);
              if (!Array.isArray(arrayIndex)) {
                arrayIndex = [arrayIndex];
              }
              let propStateValue = await arrayStateVarObj.value;
              for (let ind of arrayIndex) {
                propStateValue = propStateValue[ind];
              }
              let serializedComponent = {
                type: "serialized",
                componentType: createComponentOfType,
                componentIdx: nComponents++,
                stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                attributes: attributesForReplacement,
                doenetAttributes: {},
                children: [],
                state: {
                  [primaryEssentialStateVariable]: propStateValue
                }
              };
              pieces.push(serializedComponent);
            }
            propVariablesCopiedByPiece.push(
              propVariablesCopiedForThisPiece
            );
          }
        }
        let wrapCs = wrappingComponents[numDimensionsLeft - 1];
        if (pieces.length > 0 && wrapCs && wrapCs.length > 0) {
          for (let ind = wrapCs.length - 1; ind >= 0; ind--) {
            let wrapCT = typeof wrapCs[ind] === "object" ? wrapCs[ind].componentType : wrapCs[ind];
            let children = [];
            let attributes = {};
            for (let p2 of pieces) {
              if (p2.isAttributeNamed) {
                let attr = p2.isAttributeNamed;
                delete p2.isAttributeNamed;
                attributes[attr] = { component: p2 };
              } else {
                children.push(p2);
              }
            }
            pieces = [
              {
                type: "serialized",
                componentType: wrapCT,
                componentIdx: nComponents++,
                stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                children,
                attributes,
                doenetAttributes: {},
                state: {},
                skipSugar: true
              }
            ];
            if (typeof wrapCs[ind] === "object") {
              if (wrapCs[ind].isAttributeNamed) {
                pieces[0].isAttributeNamed = wrapCs[ind].isAttributeNamed;
              }
            }
          }
          propVariablesCopiedByPiece = [
            flattenDeep(propVariablesCopiedByPiece)
          ];
        }
        if (init && arrayStateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
          let attributeComponentsShadowingStateVariables = {};
          let attributeShadowingInfo = arrayStateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables;
          for (let attrName in attributeShadowingInfo) {
            if (attributeShadowingInfo[attrName].addToOuterIfWrappedArray) {
              let stateVariableToShadow = attributeShadowingInfo[attrName].stateVariableToShadow;
              attributeComponentsShadowingStateVariables[attrName] = {
                stateVariableToShadow
              };
            }
          }
          if (Object.keys(attributeComponentsShadowingStateVariables).length > 0) {
            for (let piece of pieces) {
              let attributesForReplacement = piece.attributes;
              if (!attributesForReplacement) {
                attributesForReplacement = piece.attributes = {};
              }
              let classOfComponentToCreate = componentInfoObjects.allComponentClasses[piece.componentType];
              let attrObj = classOfComponentToCreate.createAttributesObject();
              if (link) {
                for (let attrName in attributeComponentsShadowingStateVariables) {
                  let stateVariableToShadow = attributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
                  let attributeComponentType = attrObj[attrName]?.createComponentOfType;
                  if (attributeComponentType) {
                    let shadowComponent = {
                      type: "serialized",
                      componentType: attributeComponentType,
                      componentIdx: nComponents++,
                      stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                      attributes: {},
                      doenetAttributes: {},
                      state: {},
                      children: [],
                      downstreamDependencies: {
                        [target.componentIdx]: [
                          {
                            compositeIdx: component.componentIdx,
                            dependencyType: "referenceShadow",
                            propVariable: stateVariableToShadow
                          }
                        ]
                      }
                    };
                    attributesForReplacement[attrName] = {
                      component: shadowComponent
                    };
                  }
                }
              } else {
                let additionalAttributes = {};
                for (let attrName in attributeComponentsShadowingStateVariables) {
                  if (attrObj[attrName]?.createComponentOfType) {
                    let vName = attributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
                    let attributeStateVarObj = target.state[vName];
                    let attributeValue = await attributeStateVarObj.value;
                    if (!target.state[vName].usedDefault) {
                      additionalAttributes[attrName] = attributeValue;
                    }
                  }
                }
                if (Object.keys(additionalAttributes).length > 0) {
                  const res = convertAttributeValuesForComponentType({
                    attributeValues: additionalAttributes,
                    componentType: piece.componentType,
                    componentInfoObjects,
                    nComponents,
                    stateIdInfo
                  });
                  additionalAttributes = res.attributes;
                  nComponents = res.nComponents;
                  Object.assign(
                    attributesForReplacement,
                    additionalAttributes
                  );
                }
              }
            }
          }
        }
        return { pieces, propVariablesCopiedByPiece };
      };
      let result = await createReplacementPiece(
        unflattenedArrayKeys,
        stateVarObj.numDimensions,
        true
      );
      let newReplacements = result.pieces;
      propVariablesCopiedByReplacement = result.propVariablesCopiedByPiece;
      for (let replacement of newReplacements) {
        if (typeof replacement !== "object") {
          continue;
        }
        if (!replacement.attributes) {
          replacement.attributes = {};
        }
        const res = convertUnresolvedAttributesForComponentType({
          attributes: component.attributes,
          componentType: replacement.componentType,
          componentInfoObjects,
          compositeAttributesObj,
          nComponents,
          stateIdInfo
        });
        const attributesFromComposite = res.attributes;
        nComponents = res.nComponents;
        Object.assign(replacement.attributes, attributesFromComposite);
        if (link) {
          replacement.downstreamDependencies = {
            [replacementSource.componentIdx]: [
              {
                dependencyType: "referenceShadow",
                compositeIdx: component.componentIdx,
                propVariable: varName,
                ignorePrimaryStateVariable: true
              }
            ]
          };
        }
      }
      replacementInd += newReplacements.length;
      serializedReplacements.push(...newReplacements);
      if (newReplacements.length < numReplacementsForSource) {
        for (let ind = newReplacements.length; ind < numReplacementsForSource; ind++) {
          replacementInd++;
          propVariablesCopiedByReplacement[replacementInd] = [];
          let createComponentOfType;
          let wrapCs = wrappingComponents[0];
          let wrapDoenetAttributes;
          if (wrapCs && wrapCs.length > 0) {
            if (typeof wrapCs[0] === "object") {
              createComponentOfType = wrapCs[0].componentType;
              wrapDoenetAttributes = Object.assign(
                {},
                wrapCs[0].doenetAttributes
              );
            } else {
              createComponentOfType = wrapCs[0];
            }
          } else {
            createComponentOfType = arrayStateVarObj.shadowingInstructions.createComponentOfType;
            if (Array.isArray(createComponentOfType)) {
              if (stateVarObj.isArrayEntry) {
                createComponentOfType = createComponentOfType[arrayStateVarObj.keyToIndex(
                  arrayKeys[ind]
                )];
              } else {
                createComponentOfType = createComponentOfType[ind];
              }
            }
          }
          let newReplacement = {
            type: "serialized",
            componentType: createComponentOfType,
            componentIdx: nComponents++,
            stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
            attributes: {},
            doenetAttributes: {},
            state: {},
            children: []
          };
          if (wrapDoenetAttributes) {
            newReplacement.doenetAttributes = wrapDoenetAttributes;
          }
          serializedReplacements.push(newReplacement);
        }
      } else if (newReplacements > numReplacementsForSource) {
        throw Error(
          `Something went wrong when creating replacements for ${component.componentIdx} as we ended up with too many replacements`
        );
      }
    }
  } else {
    if (stateVarObj.shadowingInstructions?.hasVariableComponentType) {
      await stateVarObj.value;
    }
    if (!stateVarObj.shadowingInstructions?.createComponentOfType) {
      return {
        serializedReplacements: [],
        propVariablesCopiedByReplacement: [],
        diagnostics,
        nComponents
      };
    }
    replacementInd++;
    let propVariablesCopied = propVariablesCopiedByReplacement[replacementInd] = [];
    propVariablesCopied.push(varName);
    if (stateVarObj.shadowingInstructions.createComponentOfType === "string") {
      serializedReplacements.push(await stateVarObj.value);
    } else {
      const res = convertUnresolvedAttributesForComponentType({
        attributes: component.attributes,
        componentType: stateVarObj.shadowingInstructions.createComponentOfType,
        componentInfoObjects,
        compositeAttributesObj,
        nComponents,
        stateIdInfo
      });
      const attributesFromComposite = res.attributes;
      nComponents = res.nComponents;
      if (link) {
        let attributesForReplacement = {};
        if (stateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
          let classOfComponentToCreate = componentInfoObjects.allComponentClasses[stateVarObj.shadowingInstructions.createComponentOfType];
          let attrObj = classOfComponentToCreate.createAttributesObject();
          for (let attrName in stateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
            let stateVariableToShadow = stateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
            let attributeComponentType = attrObj[attrName]?.createComponentOfType;
            if (attributeComponentType) {
              let shadowComponent = {
                type: "serialized",
                componentType: attributeComponentType,
                componentIdx: nComponents++,
                stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
                attributes: {},
                doenetAttributes: {},
                state: {},
                children: [],
                downstreamDependencies: {
                  [target.componentIdx]: [
                    {
                      compositeIdx: component.componentIdx,
                      dependencyType: "referenceShadow",
                      propVariable: stateVariableToShadow
                    }
                  ]
                }
              };
              attributesForReplacement[attrName] = {
                component: shadowComponent
              };
            }
          }
        }
        Object.assign(
          attributesForReplacement,
          attributesFromComposite
        );
        serializedReplacements.push({
          type: "serialized",
          componentType: stateVarObj.shadowingInstructions.createComponentOfType,
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: attributesForReplacement,
          doenetAttributes: {},
          state: {},
          children: [],
          downstreamDependencies: {
            [target.componentIdx]: [
              {
                dependencyType: "referenceShadow",
                compositeIdx: component.componentIdx,
                propVariable: varName,
                additionalStateVariableShadowing: stateVarObj.shadowingInstructions.addStateVariablesShadowingStateVariables
              }
            ]
          }
        });
      } else {
        let attributesForReplacement = {};
        if (stateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
          let classOfComponentToCreate = componentInfoObjects.allComponentClasses[stateVarObj.shadowingInstructions.createComponentOfType];
          let attrObj = classOfComponentToCreate.createAttributesObject();
          let additionalAttributes = {};
          for (let attrName in stateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables) {
            if (attrObj[attrName]?.createComponentOfType) {
              if (attrName !== "fixed") {
                let vName = stateVarObj.shadowingInstructions.addAttributeComponentsShadowingStateVariables[attrName].stateVariableToShadow;
                let attributeValue = await target.state[vName].value;
                if (!target.state[vName].usedDefault) {
                  additionalAttributes[attrName] = {
                    type: "unresolved",
                    name: attrName,
                    children: [attributeValue.toString()]
                  };
                }
              }
            }
          }
          const res2 = convertUnresolvedAttributesForComponentType({
            attributes: additionalAttributes,
            componentType: stateVarObj.shadowingInstructions.createComponentOfType,
            componentInfoObjects,
            nComponents,
            stateIdInfo
          });
          const attributesFromComponent = res2.attributes;
          nComponents = res2.nComponents;
          if (stateVarObj.shadowingInstructions.attributesToShadow) {
            for (let attrName of stateVarObj.shadowingInstructions.attributesToShadow) {
              if (target.attributes[attrName]?.component) {
                let serializedComponent2 = await target.attributes[attrName].component.serialize({
                  copyAll: true,
                  copyVariants: true
                });
                let res3 = createNewComponentIndices(
                  [serializedComponent2],
                  nComponents,
                  stateIdInfo
                );
                nComponents = res3.nComponents;
                attributesFromComponent[attrName] = {
                  type: "component",
                  name: attrName,
                  component: res3.components[0]
                };
              } else if (target.attributes[attrName]?.primitive !== void 0) {
                attributesFromComponent[attrName] = {
                  type: "primitive",
                  name: attrName,
                  primitive: JSON.parse(
                    JSON.stringify(
                      target.attributes[attrName].primitive
                    )
                  )
                };
              }
            }
          }
          Object.assign(
            attributesForReplacement,
            attributesFromComponent
          );
        }
        Object.assign(
          attributesForReplacement,
          attributesFromComposite
        );
        let primaryEssentialStateVariable = "value";
        let componentClass = componentInfoObjects.allComponentClasses[stateVarObj.shadowingInstructions.createComponentOfType];
        if (componentClass.primaryEssentialStateVariable) {
          primaryEssentialStateVariable = componentClass.primaryEssentialStateVariable;
        } else if (componentClass.primaryStateVariableForDefinition) {
          primaryEssentialStateVariable = componentClass.primaryStateVariableForDefinition;
        }
        let serializedComponent = {
          type: "serialized",
          componentType: stateVarObj.shadowingInstructions.createComponentOfType,
          componentIdx: nComponents++,
          stateId: `${stateIdInfo.prefix}${stateIdInfo.num++}`,
          attributes: attributesForReplacement,
          doenetAttributes: {},
          children: [],
          state: {
            [primaryEssentialStateVariable]: stateVarValue
          }
        };
        serializedReplacements.push(serializedComponent);
      }
    }
  }
  if (await component.stateValues.implicitProp) {
    for (let repl of serializedReplacements) {
      if (!repl.doenetAttributes) {
        repl.doenetAttributes = {};
      }
      repl.doenetAttributes.fromImplicitProp = true;
      if (repl.downstreamDependencies?.[target.componentIdx]?.[0]) {
        repl.downstreamDependencies[target.componentIdx][0].fromImplicitProp = true;
      }
    }
  }
  return {
    serializedReplacements,
    propVariablesCopiedByReplacement,
    diagnostics,
    nComponents
  };
}
function addChildrenFromComposite({
  replacements,
  children,
  componentInfoObjects
}) {
  let repl = replacements[0];
  if (!repl.children) {
    repl.children = [];
  }
  let newChildren = deepClone(children);
  let componentClass = componentInfoObjects.allComponentClasses[repl.componentType];
  if (!componentClass.includeBlankStringChildren) {
    newChildren = newChildren.filter(
      (x2) => typeof x2 !== "string" || x2.trim() !== ""
    );
  }
  repl.children.push(...newChildren);
}
class SegmentConstraintComponent extends BaseComponent {
  static componentType = "_segmentConstraint";
  static rendererType = void 0;
  static inSchemaOnlyInheritAs = ["_segmentConstraint"];
}
class VariableNameList extends MathList {
  static componentType = "_variableNameList";
  // when another component has a attribute that is a _variableNameList,
  // use the variables state variable to populate that attribute
  static stateVariableToBeShadowed = "variables";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.variables = {
      description: "The list of variable names.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      isArray: true,
      entryPrefixes: ["var"],
      additionalStateVariablesDefined: ["validVariables"],
      returnArraySizeDependencies: () => ({
        numComponents: {
          dependencyType: "stateVariable",
          variableName: "numComponents"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numComponents];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            math: {
              dependencyType: "stateVariable",
              variableName: "math" + (Number(arrayKey) + 1)
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let variables = {};
        let validVariables = {};
        let diagnostics = [];
        for (let arrayKey of arrayKeys) {
          let variable = dependencyValuesByKey[arrayKey].math;
          let validVariable = isValidVariable(variable);
          if (!validVariable) {
            diagnostics.push({
              message: "Invalid value of a variable: `" + variable.toString() + "`",
              type: "warning"
            });
            validVariable = false;
          }
          variables[arrayKey] = variable;
          validVariables[arrayKey] = validVariable;
        }
        return {
          setValue: { variables, validVariables },
          sendDiagnostics: diagnostics
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class Variable extends MathComponent {
  static componentType = "_variableName";
  static rendererType = "math";
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    renameStateVariable({
      stateVariableDefinitions,
      oldName: "value",
      newName: "valuePreValidate"
    });
    stateVariableDefinitions.valuePreValidate.essentialVarName = "value";
    stateVariableDefinitions.value = {
      description: "The variable as a math expression.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "_variableName"
      },
      additionalStateVariablesDefined: ["validVariable"],
      returnDependencies: () => ({
        valuePreValidate: {
          dependencyType: "stateVariable",
          variableName: "valuePreValidate"
        }
      }),
      definition({ dependencyValues }) {
        let diagnostics = [];
        let validVariable = isValidVariable(
          dependencyValues.valuePreValidate
        );
        if (!validVariable) {
          diagnostics.push({
            message: "Invalid value of a variable: `" + dependencyValues.valuePreValidate.toString() + "`",
            type: "warning"
          });
        }
        return {
          setValue: {
            value: dependencyValues.valuePreValidate,
            validVariable
          },
          sendDiagnostics: diagnostics
        };
      },
      inverseDefinition({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setDependency: "valuePreValidate",
              desiredValue: desiredStateVariableValues.value
            }
          ]
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class ListOfNumberLists extends InlineComponent {
  static componentType = "_listOfNumberLists";
  static includeBlankStringChildren = true;
  static removeBlankStringChildrenPostSugar = true;
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "string"];
  static returnSugarInstructions() {
    let sugarInstructions = [];
    let breakIntoNumberListsByParens = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      let results = breakEmbeddedStringsIntoParensPieces({
        componentList: matchedChildren,
        removeParens: true
      });
      if (results.success !== true) {
        return { success: false };
      }
      return {
        success: true,
        newChildren: results.pieces.map((x2) => ({
          type: "serialized",
          componentType: "numberList",
          componentIdx: nComponents++,
          stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
          children: x2,
          attributes: {},
          doenetAttributes: {},
          state: {}
        })),
        nComponents
      };
    };
    sugarInstructions.push({
      replacementFunction: breakIntoNumberListsByParens
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    return [
      {
        group: "lists",
        componentTypes: ["numberList"]
      }
    ];
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    delete stateVariableDefinitions.value;
    stateVariableDefinitions.numLists = {
      returnDependencies: () => ({
        listChildren: {
          dependencyType: "child",
          childGroups: ["lists"]
        }
      }),
      definition({ dependencyValues }) {
        return {
          setValue: {
            numLists: dependencyValues.listChildren.length
          }
        };
      }
    };
    stateVariableDefinitions.lists = {
      isArray: true,
      entryPrefixes: ["list"],
      returnArraySizeDependencies: () => ({
        numLists: {
          dependencyType: "stateVariable",
          variableName: "numLists"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numLists];
      },
      returnArrayDependenciesByKey({ arrayKeys }) {
        let dependenciesByKey = {};
        for (let arrayKey of arrayKeys) {
          dependenciesByKey[arrayKey] = {
            listChildren: {
              dependencyType: "child",
              childGroups: ["lists"],
              variableNames: ["values"],
              childIndices: [arrayKey]
            }
          };
        }
        return { dependenciesByKey };
      },
      arrayDefinitionByKey({ dependencyValuesByKey, arrayKeys }) {
        let lists = {};
        for (let arrayKey of arrayKeys) {
          if (dependencyValuesByKey[arrayKey].listChildren && dependencyValuesByKey[arrayKey].listChildren.length === 1) {
            let listChild = dependencyValuesByKey[arrayKey].listChildren[0];
            lists[arrayKey] = listChild.stateValues.values;
          }
        }
        return {
          setValue: { lists }
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class DirectionComponent extends BaseComponent {
  static componentType = "_directionComponent";
  static rendererType = void 0;
  static primaryStateVariableForDefinition = "directionShadow";
  // Include children that can be added due to sugar
  static additionalSchemaChildren = ["math", "string"];
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    Object.assign(attributes, returnNumberDisplayAttributes());
    return attributes;
  }
  static returnSugarInstructions() {
    let sugarInstructions = super.returnSugarInstructions();
    let wrapNonMathInMath = function({
      matchedChildren,
      nComponents,
      stateIdInfo
    }) {
      if (matchedChildren.length === 0) {
        return { success: false };
      }
      if (matchedChildren.length > 1 || typeof matchedChildren[0] === "string") {
        return {
          success: true,
          newChildren: [
            {
              type: "serialized",
              componentType: "math",
              componentIdx: nComponents++,
              stateId: stateIdInfo ? `${stateIdInfo.prefix}${stateIdInfo.num++}` : void 0,
              children: matchedChildren,
              attributes: {},
              doenetAttributes: {},
              state: {}
            }
          ],
          nComponents
        };
      }
      return { success: false };
    };
    sugarInstructions.push({
      replacementFunction: wrapNonMathInMath
    });
    return sugarInstructions;
  }
  static returnChildGroups() {
    let childGroups = super.returnChildGroups();
    childGroups.push(
      ...[
        {
          group: "directions",
          componentTypes: ["_directionComponent"]
        }
      ]
    );
    return childGroups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    Object.assign(
      stateVariableDefinitions,
      returnNumberDisplayStateVariableDefinitions()
    );
    stateVariableDefinitions.directionShadow = {
      defaultValue: me$3.fromAst(["vector", 1, 0]),
      hasEssential: true,
      essentialVarName: "direction",
      set: convertValueToMathExpression,
      returnDependencies: () => ({}),
      definition: () => ({
        useEssentialOrDefaultValue: {
          directionShadow: true
        }
      }),
      inverseDefinition: function({ desiredStateVariableValues }) {
        return {
          success: true,
          instructions: [
            {
              setEssentialValue: "directionShadow",
              value: convertValueToMathExpression(
                desiredStateVariableValues.directionShadow
              )
            }
          ]
        };
      }
    };
    stateVariableDefinitions.unnormalizedDirection = {
      returnDependencies: () => ({
        directionChild: {
          dependencyType: "child",
          childGroups: ["directions"],
          variableNames: ["unnormalizedDirection"]
        },
        directionShadow: {
          dependencyType: "stateVariable",
          variableName: "directionShadow"
        }
      }),
      definition({ dependencyValues }) {
        let unnormalizedDirection;
        if (dependencyValues.directionChild.length > 0) {
          unnormalizedDirection = dependencyValues.directionChild[0].stateValues.unnormalizedDirection;
        } else {
          unnormalizedDirection = dependencyValues.directionShadow;
        }
        unnormalizedDirection = unnormalizedDirection.tuples_to_vectors().expand().simplify();
        return { setValue: { unnormalizedDirection } };
      },
      inverseDefinition({
        desiredStateVariableValues,
        dependencyValues
      }) {
        let instructions;
        if (dependencyValues.directionChild.length > 0) {
          instructions = [
            {
              setDependency: "directionChild",
              desiredValue: desiredStateVariableValues.unnormalizedDirection,
              childIndex: 0,
              variableIndex: 0
            }
          ];
        } else {
          instructions = [
            {
              setDependency: "directionShadow",
              desiredValue: desiredStateVariableValues.unnormalizedDirection
            }
          ];
        }
        return { success: true, instructions };
      }
    };
    stateVariableDefinitions.numDimensions = {
      returnDependencies: () => ({
        unnormalizedDirection: {
          dependencyType: "stateVariable",
          variableName: "unnormalizedDirection"
        }
      }),
      definition({ dependencyValues }) {
        let directionTree = dependencyValues.unnormalizedDirection.tree;
        let numDimensions;
        if (Array.isArray(directionTree) && vectorOperators.includes(directionTree[0])) {
          numDimensions = directionTree.length - 1;
        } else {
          numDimensions = 1;
        }
        return { setValue: { numDimensions } };
      }
    };
    stateVariableDefinitions.direction = {
      description: "The normalized direction as a unit vector.",
      public: true,
      shadowingInstructions: {
        createComponentOfType: "math",
        addAttributeComponentsShadowingStateVariables: returnNumberDisplayAttributeComponentShadowing(),
        returnWrappingComponents(prefix) {
          if (prefix === "x") {
            return [];
          } else {
            return [
              [
                "vector",
                {
                  componentType: "mathList",
                  isAttributeNamed: "xs"
                }
              ]
            ];
          }
        }
      },
      isArray: true,
      entryPrefixes: ["x"],
      hasEssential: true,
      essentialVarName: "direction2",
      // since "direction" used for directionShadow
      set: convertValueToMathExpression,
      returnArraySizeDependencies: () => ({
        numDimensions: {
          dependencyType: "stateVariable",
          variableName: "numDimensions"
        }
      }),
      returnArraySize({ dependencyValues }) {
        return [dependencyValues.numDimensions];
      },
      returnArrayDependenciesByKey() {
        let globalDependencies = {
          unnormalizedDirection: {
            dependencyType: "stateVariable",
            variableName: "unnormalizedDirection"
          }
        };
        return { globalDependencies };
      },
      arrayDefinitionByKey: function({
        globalDependencyValues,
        arraySize
      }) {
        let direction = {};
        let directionTree = globalDependencyValues.unnormalizedDirection.tree;
        if (Array.isArray(directionTree) && vectorOperators.includes(directionTree[0])) {
          let numericalDirection = [];
          let nDim = arraySize[0];
          let len2 = 0;
          let foundNumerical = true;
          for (let dim = 0; dim < nDim; dim++) {
            let val = globalDependencyValues.unnormalizedDirection.get_component(dim).evaluate_to_constant();
            if (Number.isFinite(val)) {
              numericalDirection.push(val);
              len2 += val * val;
            } else {
              foundNumerical = false;
              break;
            }
          }
          if (foundNumerical) {
            let len = Math.sqrt(len2);
            for (let dim = 0; dim < nDim; dim++) {
              direction[dim.toString()] = me$3.fromAst(
                numericalDirection[dim] / len
              );
            }
          } else {
            for (let dim = 0; dim < nDim; dim++) {
              direction[dim.toString()] = me$3.fromAst("＿");
            }
          }
        } else {
          direction["0"] = me$3.fromAst(1);
        }
        return { setValue: { direction } };
      },
      inverseArrayDefinitionByKey({
        desiredStateVariableValues,
        globalDependencyValues,
        arraySize
      }) {
        let instructions = [];
        if (arraySize[0] > 1) {
          let unnormalizedMagnitude = 0;
          for (let dim = 0; dim < arraySize[0]; dim++) {
            let comp = globalDependencyValues.unnormalizedDirection.get_component(dim).evaluate_to_constant();
            unnormalizedMagnitude += comp * comp;
          }
          if (Number.isFinite(unnormalizedMagnitude)) {
            unnormalizedMagnitude = Math.sqrt(
              unnormalizedMagnitude
            );
          } else {
            unnormalizedMagnitude = 1;
          }
          let desiredDirection = ["vector"];
          for (let arrayKey in desiredStateVariableValues.direction) {
            desiredDirection[Number(arrayKey) + 1] = desiredStateVariableValues.direction[arrayKey].tree * unnormalizedMagnitude;
          }
          desiredDirection.length = arraySize[0] + 1;
          instructions.push({
            setDependency: "unnormalizedDirection",
            desiredValue: me$3.fromAst(desiredDirection)
          });
        }
        return {
          success: true,
          instructions
        };
      }
    };
    return stateVariableDefinitions;
  }
}
class ErrorComponent extends BlockComponent {
  static componentType = "_error";
  static renderChildren = true;
  static createAttributesObject() {
    let attributes = super.createAttributesObject();
    attributes.message = {
      createComponentOfType: "text",
      createStateVariable: "message",
      defaultValue: "",
      public: true,
      forRenderer: true,
      description: "The error message describing what went wrong."
    };
    return attributes;
  }
  static returnChildGroups() {
    let childGroups = [
      {
        group: "errors",
        componentTypes: ["_error"]
      },
      {
        group: "any",
        componentTypes: ["_base"]
      }
    ];
    return childGroups;
  }
  static returnStateVariableDefinitions() {
    let stateVariableDefinitions = super.returnStateVariableDefinitions();
    stateVariableDefinitions.hidden = {
      returnDependencies: () => ({}),
      definition: () => ({ setValue: { hidden: false } })
    };
    stateVariableDefinitions.rangeMessage = {
      description: "A message describing where in the source document the error was found.",
      forRenderer: true,
      public: true,
      shadowingInstructions: {
        createComponentOfType: "text"
      },
      returnDependencies: () => ({
        position: {
          dependencyType: "position"
        }
      }),
      definition({ dependencyValues }) {
        let rangeMessage = "";
        let position = dependencyValues.position;
        if (position?.start.line !== void 0) {
          rangeMessage = "Found on " + printDoenetMLrange(position) + ".";
        }
        return { setValue: { rangeMessage } };
      }
    };
    stateVariableDefinitions.showMessage = {
      forRenderer: true,
      returnDependencies: () => ({
        childErrors: {
          dependencyType: "child",
          childGroups: ["errors"],
          variableNames: ["message"]
        },
        message: {
          dependencyType: "stateVariable",
          variableName: "message"
        }
      }),
      definition({ dependencyValues }) {
        let showMessage = !dependencyValues.childErrors.some(
          (child) => child.stateValues.message === dependencyValues.message
        );
        return { setValue: { showMessage } };
      }
    };
    return stateVariableDefinitions;
  }
}
class Comment extends BaseComponent {
  static componentType = "_comment";
  static rendererType = void 0;
  static returnChildGroups() {
    let childGroups = [
      {
        group: "any",
        componentTypes: ["_base"]
      }
    ];
    return childGroups;
  }
}
const componentTypeArray = [
  ...Object.values(Aliases),
  ...Object.values(MMeMen),
  ...Object.values(MdMdnMrow),
  ...Object.values(BooleanOperators),
  ...Object.values(BooleanOperatorsOfMath),
  ...Object.values(MathOperators),
  ...Object.values(FunctionOperators),
  ...Object.values(TextOperatorsOfMath),
  ...Object.values(Extrema$1),
  ...Object.values(ParagraphMarkup),
  ...Object.values(SingleCharacterComponents),
  ...Object.values(Sectioning),
  ...Object.values(Lists),
  ...Object.values(DynamicalSystems),
  ...Object.values(Chemistry),
  ...Object.values(FeedbackDefinitions$1),
  ...Object.values(StyleDefinitions$1),
  ...Object.values(SideBySide$1),
  ...Object.values(ComponentWithSelectableType$1),
  ...Object.values(Indexing),
  ...Object.values(Divisions),
  ...Object.values(Verbatim),
  ...Object.values(Paginator$1),
  ...Object.values(MatrixInput$1),
  ...Object.values(FractionInput$1),
  ...Object.values(Solution$1),
  Document,
  Text,
  TextList$1,
  TextList2,
  P$2,
  BooleanComponent,
  BooleanList,
  MathComponent,
  MathList,
  TupleList,
  NumberList,
  Copy,
  Collect,
  Ref,
  Point,
  Coords,
  Line,
  LineSegment,
  Ray,
  Polyline,
  Polygon,
  Triangle,
  Rectangle,
  RegularPolygon,
  Circle,
  Parabola,
  Curve,
  BezierControls,
  ControlVectors,
  Vector,
  Angle,
  Answer,
  Award,
  When,
  MathInput,
  Textinput,
  BooleanInput,
  Choiceinput,
  Choice,
  NumberComponent,
  Integer,
  Graph$1,
  Annotations,
  Annotation,
  Function$1,
  PiecewiseFunction,
  Interval,
  Option$1,
  Sequence,
  Slider,
  Spreadsheet,
  DiscreteSimulationResultList,
  DiscreteSimulationResultPolyline,
  Cell,
  Row,
  Column,
  CellBlock,
  Tabular,
  Table,
  Figure,
  Markers,
  Panel,
  Repeat,
  RepeatForSequence,
  Pegboard,
  Constraints,
  ConstrainToGrid,
  ConstrainToGraph,
  AttractToGrid,
  ConstrainTo,
  AttractTo,
  ConstraintUnion,
  AttractToConstraint,
  ConstrainToInterior,
  // AttractSegmentTo,
  Intersection,
  ConstrainToAngles,
  AttractToAngles,
  ConditionalContent,
  AsList,
  VariantControl,
  TextListFromString,
  SelectFromSequence,
  Select,
  Group,
  AnimateFromSequence,
  Evaluate,
  SelectRandomNumbers,
  SampleRandomNumbers,
  SelectPrimeNumbers,
  SamplePrimeNumbers,
  Substitute,
  PeriodicSet,
  Image,
  Video,
  Embed,
  Hint,
  IntComma,
  Pluralize,
  Feedback,
  CollaborateGroups,
  CollaborateGroupSetup,
  ConsiderAsResponses,
  Case,
  Lorem,
  UpdateValue,
  CallAction,
  triggerSet,
  FunctionIterates,
  Module,
  ModuleAttributes,
  Setup,
  Footnote,
  Caption,
  Endpoint,
  Sort,
  Shuffle,
  SolveEquations,
  PostponeRenderContainer,
  SubsetOfRealsInput,
  SubsetOfReals,
  Split,
  BestFitLine,
  RegionBetweenCurveXAxis,
  RegionBetweenCurves,
  RegionHalfPlane,
  CodeEditor,
  HasSameFactoring,
  DataFrame,
  SummaryStatistics,
  Legend,
  Label,
  MatchesPattern,
  Matrix,
  EigenDecomposition,
  Latex,
  BlockQuote,
  Description,
  // VertexConstraints,
  // EdgeConstraints,
  StickyGroup,
  Pretzel,
  PretzelArranger,
  Cascade,
  DynamicChildren,
  ShortDescription,
  BaseComponent,
  InlineComponent,
  BlockComponent,
  BlockScoredComponent,
  GraphicalComponent,
  ConstraintComponent,
  SegmentConstraintComponent,
  Input,
  CompositeComponent,
  PointList,
  IntervalList,
  LineListComponent,
  VectorListComponent,
  AngleListComponent,
  BooleanOperator,
  BooleanBaseOperatorOfMath,
  MathOperator,
  MathOperatorOneInput,
  FunctionOperator,
  ...Object.values(ComponentSize$1),
  ...Object.values(SectioningComponent$1),
  InlineRenderInlineChildren,
  TextOrInline,
  SingleCharacter,
  VariableNameList,
  Variable,
  ListOfNumberLists,
  DirectionComponent,
  ErrorComponent,
  Comment
];
function allComponentClasses() {
  const componentClasses = {};
  const lowerCaseComponentTypes = /* @__PURE__ */ new Set();
  for (let ct2 of componentTypeArray) {
    let newComponentType = ct2.componentType;
    if (newComponentType === void 0) {
      throw Error(
        "Cannot create component as componentType is undefined for class " + ct2
      );
    }
    let lowerCaseType = newComponentType.toLowerCase();
    if (lowerCaseComponentTypes.has(lowerCaseType)) {
      throw Error(
        "component type " + newComponentType + " defined in two classes"
      );
    }
    componentClasses[newComponentType] = ct2;
    lowerCaseComponentTypes.add(lowerCaseType);
  }
  return componentClasses;
}
function componentTypesCreatingVariants() {
  const componentClasses = {};
  const lowerCaseComponentTypes = /* @__PURE__ */ new Set();
  for (let ct2 of componentTypeArray) {
    if (ct2.createsVariants) {
      let newComponentType = ct2.componentType;
      if (newComponentType === void 0) {
        throw Error(
          "Cannot create component as componentType is undefined for class " + ct2
        );
      }
      let lowerCaseType = newComponentType.toLowerCase();
      if (lowerCaseComponentTypes.has(lowerCaseType)) {
        throw Error(
          "component type " + newComponentType + " defined in two classes"
        );
      }
      componentClasses[newComponentType] = ct2;
      lowerCaseComponentTypes.add(lowerCaseType);
    }
  }
  return componentClasses;
}
function createComponentInfoObjects() {
  const allComponentClasses$1 = allComponentClasses();
  const componentTypesCreatingVariants$1 = componentTypesCreatingVariants();
  const componentTypeLowerCaseMapping = {};
  for (const componentType in allComponentClasses$1) {
    componentTypeLowerCaseMapping[componentType.toLowerCase()] = componentType;
  }
  const stateVariableInfo = {};
  for (const componentType in allComponentClasses$1) {
    Object.defineProperty(stateVariableInfo, componentType, {
      get: (function() {
        const info = allComponentClasses$1[componentType].returnStateVariableInfo();
        delete stateVariableInfo[componentType];
        return stateVariableInfo[componentType] = info;
      }).bind(this),
      configurable: true
    });
  }
  const publicStateVariableInfo = {};
  for (const componentType in allComponentClasses$1) {
    Object.defineProperty(publicStateVariableInfo, componentType, {
      get: (function() {
        const info = allComponentClasses$1[componentType].returnStateVariableInfo({
          onlyPublic: true
        });
        delete publicStateVariableInfo[componentType];
        return publicStateVariableInfo[componentType] = info;
      }).bind(this),
      configurable: true
    });
  }
  function isInheritedComponentType({
    inheritedComponentType,
    baseComponentType
  }) {
    if (inheritedComponentType === baseComponentType) {
      return true;
    }
    if (inheritedComponentType === "string") {
      return baseComponentType === "_base" || baseComponentType === "_inline";
    } else if (baseComponentType === "string") {
      return false;
    }
    let baseClass = allComponentClasses$1[baseComponentType];
    if (!baseClass) {
      return false;
    }
    return baseClass.isPrototypeOf(
      allComponentClasses$1[inheritedComponentType]
    );
  }
  function isCompositeComponent({
    componentType,
    includeNonStandard = true
  }) {
    const componentClass = allComponentClasses$1[componentType];
    if (!componentClass) {
      return false;
    }
    const isComposite = isInheritedComponentType({
      inheritedComponentType: componentType,
      baseComponentType: "_composite"
    });
    return isComposite && (includeNonStandard || !componentClass.treatAsComponentForRecursiveReplacements);
  }
  const componentTypeIsSpecifiedType = (cType, specifiedCType) => isInheritedComponentType({
    inheritedComponentType: cType,
    baseComponentType: specifiedCType
  });
  const componentIsSpecifiedType = (comp, specifiedCType) => componentTypeIsSpecifiedType(comp.componentType, specifiedCType) || comp.attributes?.createComponentOfType?.primitive !== void 0 && componentTypeIsSpecifiedType(
    comp.attributes?.createComponentOfType?.primitive.value,
    specifiedCType
  );
  return {
    allComponentClasses: allComponentClasses$1,
    componentTypesCreatingVariants: componentTypesCreatingVariants$1,
    componentTypeLowerCaseMapping,
    isInheritedComponentType,
    isCompositeComponent,
    stateVariableInfo,
    publicStateVariableInfo,
    componentIsSpecifiedType
  };
}
async function returnAllPossibleVariants(serializedDocument, componentInfoObjects, infoDiagnostics) {
  let results = getNumVariants({
    serializedComponent: serializedDocument,
    componentInfoObjects,
    infoDiagnostics
  });
  let numVariants = results.numVariants;
  let allPossibleVariants;
  if (serializedDocument.variants?.allPossibleVariants) {
    allPossibleVariants = serializedDocument.variants.allPossibleVariants;
  } else {
    allPossibleVariants = [...Array(numVariants).keys()].map(
      (x2) => indexToLowercaseLetters(x2 + 1)
    );
  }
  return allPossibleVariants;
}
function indexToLowercaseLetters(index2) {
  return numberToLetters(index2, true);
}
class PublicDoenetMLCore {
  constructor() {
    this.core = null;
    this.initialized = false;
    this.doenetML = "";
    this.flags = {};
  }
  setSource(doenetML) {
    this.doenetML = doenetML;
  }
  setFlags(flags) {
    this.flags = flags;
  }
  async initializeWorker({
    activityId,
    docId,
    requestedVariantIndex,
    attemptNumber,
    normalizedRoot,
    addNodesToResolver,
    replaceIndexResolutionsInResolver,
    deleteNodesFromResolver,
    resolvePath,
    calculateRootNames
  }) {
    this.addNodesToResolver = addNodesToResolver;
    this.replaceIndexResolutionsInResolver = replaceIndexResolutionsInResolver;
    this.deleteNodesFromResolver = deleteNodesFromResolver;
    this.resolvePath = resolvePath;
    this.calculateRootNames = calculateRootNames;
    let componentInfoObjects = createComponentInfoObjects();
    const {
      document: root,
      nComponents: nComponentsInit,
      diagnostics,
      sources
    } = await normalizedDastToSerializedComponents(
      normalizedRoot,
      componentInfoObjects,
      addNodesToResolver
    );
    this.coreBaseArgs = {
      doenetML: this.doenetML,
      flags: this.flags,
      activityId,
      docId,
      requestedVariantIndex,
      attemptNumber,
      serializedDocument: root,
      nComponentsInit,
      allDoenetMLs: sources,
      preliminaryDiagnostics: diagnostics,
      componentInfoObjects
    };
    this.initialized = true;
    let allPossibleVariants = await returnAllPossibleVariants(
      this.coreBaseArgs.serializedDocument,
      this.coreBaseArgs.componentInfoObjects,
      diagnostics
    );
    const baseComponentCounts = this.coreBaseArgs.serializedDocument.children.reduce(
      (a2, c2) => {
        if (typeof c2 === "object") {
          a2[c2.componentType] = (a2[c2.componentType] ?? 0) + 1;
        }
        return a2;
      },
      {}
    );
    return {
      allPossibleVariants,
      baseComponentCounts
    };
  }
  /**
   * Create the Javascript core, create all the components and resolve all the state variables
   * need to generated the dast for the renderer.
   *
   * Note: the return type for this function is currently in flux as it is a work in progress.
   *
   * TODO: clean up this function
   */
  async createCoreGenerateDast(args, updateRenderersCallback, reportScoreAndStateCallback, requestAnimationFrame, cancelAnimationFrame, copyToClipboard, sendEvent, requestSolutionView) {
    if (this.initialized && !this.coreBaseArgs?.serializedDocument) {
      return {
        success: false,
        errMsg: "Cannot create the core a second time from the same worker; the serialized document was released after the first core was created."
      };
    }
    let coreArgs = {
      ...this.coreBaseArgs,
      ...args,
      addNodesToResolver: this.addNodesToResolver,
      replaceIndexResolutionsInResolver: this.replaceIndexResolutionsInResolver,
      deleteNodesFromResolver: this.deleteNodesFromResolver,
      resolvePath: this.resolvePath,
      calculateRootNames: this.calculateRootNames,
      updateRenderersCallback,
      reportScoreAndStateCallback,
      requestAnimationFrame,
      cancelAnimationFrame,
      copyToClipboard,
      sendEvent,
      requestSolutionView
    };
    if (this.initialized) {
      this.core = new Core(coreArgs);
      this.coreBaseArgs.serializedDocument = void 0;
      try {
        const result = await this.core.generateDast();
        return { success: true, ...result };
      } catch (e3) {
        console.error(e3);
        return {
          success: false,
          errMsg: typeof e3 === "object" && e3 && "message" in e3 && typeof e3.message === "string" ? e3.message : ""
        };
      }
    } else {
      let errMsg = "Internal error. Cannot create document. Core not initialized.";
      return {
        success: false,
        coreId: coreArgs.coreId,
        errMsg
      };
    }
  }
  /**
   * Add the action `actionName` of `componentIdx` to the queue to be executed.
   */
  async requestAction(actionArgs) {
    if (!this.core) {
      return {
        success: false,
        errMsg: "Cannot request action before core is created"
      };
    }
    try {
      return await this.core.requestAction(actionArgs);
    } catch (e3) {
      console.error(e3);
      return {
        success: false,
        errMsg: typeof e3 === "object" && e3 && "message" in e3 && typeof e3.message === "string" ? e3.message : "",
        actionId: actionArgs.args?.actionId
      };
    }
  }
  /**
   * A debugging function that returns a list of all components in the document,
   * all their states variables, and additional information about their children,
   * replacements, and shared parameters.
   *
   * If `consoleLogComponents` is `true`, then console.log the entire component tree,
   * which can be useful when debugging in the console.
   *
   * Unless `dontRemoveFunctionsMath` is set, clean state variables that can't be conveniently serialized,
   * i.e., remove functions and replace math-expressions with their tree.
   */
  async returnAllStateVariables(consoleLogComponents = false, dontRemoveFunctionsMath = false) {
    if (!this.core?.components) {
      return {};
    }
    const componentsObj = {};
    const components = this.core.components;
    if (consoleLogComponents) {
      console.log(components);
    }
    for (let componentIdxStr in components) {
      const componentIdx = Number(componentIdxStr);
      let component = components[componentIdx];
      if (!component) {
        continue;
      }
      componentsObj[componentIdx] = {
        componentIdx,
        componentType: component.componentType,
        stateValues: {},
        activeChildren: [],
        sharedParameters: null
      };
      let compObj = componentsObj[componentIdx];
      for (let vName in component.state) {
        if ([
          "replacements",
          "recursiveReplacements",
          "fullRecursiveReplacements"
        ].includes(vName) && this.core.componentInfoObjects.isCompositeComponent({
          componentType: component.componentType
        }) && !component.isExpanded) {
          continue;
        }
        const value = await component.state[vName].value;
        compObj.stateValues[vName] = dontRemoveFunctionsMath ? value : removeFunctionsMathExpressionClass(value);
      }
      compObj.activeChildren = component.activeChildren.map(
        (x2) => x2.componentIdx ? {
          componentIdx: x2.componentIdx,
          componentType: x2.componentType
        } : x2
      );
      if (component.replacements) {
        compObj.replacements = component.replacements.map(
          (x2) => x2.componentIdx ? {
            componentIdx: x2.componentIdx,
            componentType: x2.componentType
          } : x2
        );
        if (component.replacementsToWithhold !== void 0) {
          compObj.replacementsToWithhold = component.replacementsToWithhold;
        }
      }
      if (component.replacementOf) {
        compObj.replacementOf = component.replacementOf.componentIdx;
      }
      compObj.sharedParameters = dontRemoveFunctionsMath ? component.sharedParameters : removeFunctionsMathExpressionClass(
        component.sharedParameters
      );
    }
    return componentsObj;
  }
  /**
   * Submit any answers that are waiting to auto-submitted and save all state
   * so that the worker can be terminated without losing data.
   */
  async terminate() {
    if (this.core) {
      await this.core.terminate();
      this.core = null;
    }
  }
  // Turn on or off recording of the visibility of document components,
  // depending if the document itself is visible
  handleVisibilityChange(documentIsVisible) {
    this.core?.handleVisibilityChange(documentIsVisible);
  }
  // TODO: restore functionality that opens collapsible sections
  // when navigating to them or to items inside them
  navigatingToComponent(componentIdx, hash) {
    if (this.core) {
      handleNavigatingToComponent({
        core: this.core,
        componentIdx,
        hash
      }).catch(reportTimerError(TimerLabels.navigateToComponent));
    }
  }
  /**
   * Call submitAnswer on all answers in the document,
   * then immediately save all document state to the database
   */
  async submitAllAnswers() {
    await this.core?.requestAction({
      componentIdx: this.core.documentIdx,
      actionName: "submitAllAnswers",
      args: {}
    });
    await this.core?.saveImmediately();
  }
  /**
   * Immediately save all document state to the database,
   * ignoring any timeouts
   */
  async saveImmediately() {
    await this.core?.saveImmediately();
  }
  /**
   * Flush-state-on-demand (Doenet/DoenetML#1440): after letting in-flight
   * updates settle, push any pending state through the normal
   * `reportScoreAndState` pipeline so a persistence host saves it, letting a
   * host unmount this document losslessly. Returns whether the viewer held
   * any state (`false` when the core/document has not been created yet).
   */
  async flushState() {
    return await this.core?.flushState() ?? false;
  }
}
export {
  PublicDoenetMLCore as P,
  createComponentInfoObjects as c,
  expandCompositeComponent as e,
  getDefaultExportFromCjs as g
};
//# sourceMappingURL=index-B4TW0JWX.js.map
